/*
* XmlDigitalTeaching v0.0.1
* Copyright ©Wed Oct 23 2024 19:40:10 GMT+0800 (中国标准时间) smile
* Released under the ISC License.
*/
import Vue from 'vue';
import crypto from 'crypto';

let componentNamespace = 'xml';
// 组件前缀命名只能更新一次，防止重复执行修改组件注册出错
let isUpdated = false;
// scss 命名不能通过 js 更改，在 SCSS 编译已经打包好了
const cssNamespace = 'xml';
const statePrefix = 'is-';
function createNamespace$1(name, {
  prefix,
  isUnPrefix
}) {
  let useNamespace;
  if (!isUpdated) {
    isUpdated = true;
    useNamespace = prefix || componentNamespace; // 更改前缀
  }
  if (prefix) {
    if (name.indexOf(componentNamespace) === 0) {
      return name.replace(componentNamespace, prefix);
    }
    return useNamespace.charAt(0).toUpperCase() + useNamespace.substr(1) + name;
  }
  if (name.indexOf(componentNamespace) === 0 || name.indexOf(componentNamespace.charAt(0).toUpperCase()) === 0) {
    return name.charAt(0).toUpperCase() + name.substr(1);
  }
  return isUnPrefix ? name.charAt(0).toLowerCase() + name.substr(1) : componentNamespace + name.charAt(0).toUpperCase() + name.substr(1);
}

/**
 * 生成 bem
 * @param {} namespace 命名空间
 * @param {*} block 块
 * @param {*} blockSuffix 块多个单词
 * @param {*} element 元素
 * @param {*} modifier 修饰符
 * @returns
 */
const _bem = (namespace, block, blockSuffix, element, modifier) => {
  let cls = `${namespace}-${block}`;
  if (blockSuffix) {
    cls += `-${blockSuffix}`;
  }
  if (element) {
    cls += `__${element}`;
  }
  if (modifier) {
    cls += `--${modifier}`;
  }
  return cls;
};
const useNamespace = (block, namespace = cssNamespace) => {
  const b = (blockSuffix = '') => _bem(namespace, block, blockSuffix, '', '');
  const e = element => element ? _bem(namespace, block, '', element, '') : '';
  const m = modifier => modifier ? _bem(namespace, block, '', '', modifier) : '';
  const be = (blockSuffix, element) => blockSuffix && element ? _bem(namespace, block, blockSuffix, element, '') : '';
  const em = (element, modifier) => element && modifier ? _bem(namespace, block, '', element, modifier) : '';
  const bm = (blockSuffix, modifier) => blockSuffix && modifier ? _bem(namespace, block, blockSuffix, '', modifier) : '';
  const bem = (blockSuffix, element, modifier) => blockSuffix && element && modifier ? _bem(namespace, block, blockSuffix, element, modifier) : '';
  const is = (name, ...args) => {
    const state = args.length >= 1 ? args[0] : true;
    return name && state ? `${statePrefix}${name}` : '';
  };

  // for css var
  // --el-xxx: value;
  const cssVar = object => {
    const styles = {};
    for (const key in object) {
      styles[`--${namespace}-${key}`] = object[key];
    }
    return styles;
  };

  // with block
  const cssVarBlock = object => {
    const styles = {};
    for (const key in object) {
      styles[`--${namespace}-${block}-${key}`] = object[key];
    }
    return styles;
  };
  const cssVarName = name => `--${namespace}-${name}`;
  const cssVarBlockName = name => `--${namespace}-${block}-${name}`;
  return {
    cssNamespace,
    componentNamespace,
    b,
    e,
    m,
    be,
    em,
    bm,
    bem,
    is,
    // css
    cssVar,
    cssVarName,
    cssVarBlock,
    cssVarBlockName
  };
};

// 样式资源包
let chalk = '';
// 默认主题
let defaultTheme = '#409EFF';
const tintColor = (color, tint) => {
  color = color.replace('#', '');
  let red = parseInt(color.slice(0, 2), 16);
  let green = parseInt(color.slice(2, 4), 16);
  let blue = parseInt(color.slice(4, 6), 16);
  if (tint === 0) {
    // when primary color is in its rgb space
    return [red, green, blue].join(',');
  } else {
    red += Math.round(tint * (255 - red));
    green += Math.round(tint * (255 - green));
    blue += Math.round(tint * (255 - blue));
    red = red.toString(16);
    green = green.toString(16);
    blue = blue.toString(16);
    console.log('tintColor return', color, tint, `#${red}${green}${blue}`);
    return `#${red}${green}${blue}`;
  }
};
const shadeColor = (color, shade) => {
  let red = parseInt(color.slice(0, 2), 16);
  let green = parseInt(color.slice(2, 4), 16);
  let blue = parseInt(color.slice(4, 6), 16);
  red = Math.round((1 - shade) * red);
  green = Math.round((1 - shade) * green);
  blue = Math.round((1 - shade) * blue);
  red = red.toString(16);
  green = green.toString(16);
  blue = blue.toString(16);
  console.log('shadeColor return', color, shade, `#${red}${green}${blue}`);
  return `#${red}${green}${blue}`;
};
const getThemeCluster = function (theme) {
  const clusters = [theme];
  for (let i = 0; i <= 9; i++) {
    clusters.push(tintColor(theme, Number((i / 10).toFixed(2))));
  }
  clusters.push(shadeColor(theme, 0.1));
  console.log('getThemeCluster return', theme, clusters);
  return clusters;
};
const getCSSString = function (url) {
  return new Promise(resolve => {
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        chalk = xhr.responseText.replace(/@font-face{[^}]+}/, '');
        console.log('getCSSString: chalk', chalk);
        resolve();
      }
    };
    xhr.onerror = err => {
      console.error('样式下载失败', err);
    };
    xhr.open('GET', url);
    xhr.send();
  });
};
const updateStyle = function (style, oldCluster, newCluster) {
  let newStyle = style;
  oldCluster.forEach((color, index) => {
    newStyle = newStyle.replace(new RegExp(color, 'ig'), newCluster[index]);
  });
  console.log('updateStyle: newStyle', newStyle);
  return newStyle;
};
const updateElementTheme = async function (options = {}) {
  const {
    version = '2.15.8',
    oldTheme,
    primaryColor,
    appendDom,
    insertBefore,
    cssUrl,
    chalkStyle = 'chalk-style'
  } = options;
  if (typeof primaryColor !== 'string') return;
  const themeCluster = getThemeCluster(primaryColor.replace('#', ''));
  const originalCluster = getThemeCluster((oldTheme || defaultTheme).replace('#', ''));
  const chalkHandler = id => {
    const newStyle = updateStyle(chalk, originalCluster, themeCluster);
    // 覆盖原来的样式
    chalk = newStyle;
    let styleTag = document.querySelector(id);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.setAttribute('id', id);
      if (appendDom) {
        if (insertBefore) {
          appendDom.parentNode.insertBefore(styleTag, appendDom.nextSibling);
        } else {
          appendDom.appendChild(styleTag);
        }
      } else {
        document.head.appendChild(styleTag);
      }
    }
    styleTag.innerText = newStyle;
  };
  if (!chalk) {
    const url = cssUrl || `https://unpkg.com/element-ui@${version}/lib/theme-chalk/index.css`;
    await getCSSString(url);
  }
  chalkHandler(chalkStyle);
  defaultTheme = primaryColor;
};

const updateUITheme = async function (options) {
  const {
    oldTheme = '#409EFF',
    primaryColor,
    primarySecondColor,
    opacity = 0.7,
    elementUI,
    button = {
      disabledFontColorPrimary: '#fff',
      disabledBgColorPrimary: '#ccc',
      disabledFontColorPlain: '#999',
      disabledBgColorPlain: '#f5f5f5',
      disabledFontColorGhost: '#999',
      disabledBorderColorGhost: '#ccc'
    }
  } = options;
  if (!primaryColor) return;
  if (elementUI) {
    try {
      await updateElementTheme({
        oldTheme,
        primaryColor: primaryColor
      });
    } catch (err) {
      console.error(err);
    }
  }

  // const primaryOpc = colorPalette(primary, opacity)
  // 第二种主题透明

  const primarySecOpc = primarySecondColor ? tintColor(primarySecondColor, Number((1 - opacity).toFixed(2))) : '';
  // const primaryDis = colorPalette(primary, disOpacity)
  // const primarySecDis = colorPalette(primarySecondColor, disOpacity)
  const primaryLightColors = [];
  const el = document.documentElement;
  el.style.setProperty(`--${cssNamespace}-color-primary`, primaryColor);
  for (let i = 1; i <= 9; i++) {
    const color = tintColor(primaryColor, Number((0.1 * i).toFixed(2)));
    primaryLightColors.push(color);
    el.style.setProperty(`--${cssNamespace}-color-primary-light-${i}`, color);
  }

  // disabled primary
  el.style.setProperty(`--${cssNamespace}-button-disabled-font-color-primary`, button.disabledFontColorPrimary);
  el.style.setProperty(`--${cssNamespace}-button-disabled-bg-color-primary`, button.disabledBgColorPrimary);
  el.style.setProperty(`--${cssNamespace}-button-disabled-border-color-primary`, button.disabledBgColorPrimary);
  // plain
  el.style.setProperty(`--${cssNamespace}-button-disabled-font-color-primary-plain`, button.disabledFontColorPlain);
  el.style.setProperty(`--${cssNamespace}-button-disabled-bg-color-primary-plain`, button.disabledBgColorPlain);
  // ghost
  el.style.setProperty(`--${cssNamespace}-button-disabled-font-color-primary-ghost`, button.disabledFontColorGhost);
  el.style.setProperty(`--${cssNamespace}-button-disabled-border-color-primary-ghost`, button.disabledBorderColorGhost);
  const buttonTheme = {
    primary: {
      'button-font-color': primarySecondColor ? primaryColor : '#fff',
      'button-bg-color': primarySecondColor ? primarySecondColor : primaryColor,
      'button-hover-font-color': primarySecondColor ? primaryColor : '#fff',
      'button-hover-bg-color': primarySecondColor ? primarySecOpc : primaryLightColors[1],
      'button-active-font-color': primarySecondColor ? primaryColor : '#fff',
      'button-active-bg-color': primarySecondColor ? primarySecondColor : primaryColor
    },
    plain: {
      'button-font-color-primary': primarySecondColor ? primarySecondColor : primaryColor,
      'button-bg-color-primary': primarySecondColor ? primaryLightColors[3] : primaryLightColors[7],
      'button-hover-font-color-primary': primarySecondColor ? primarySecondColor : primaryColor,
      'button-hover-bg-color-primary': primarySecondColor ? primaryLightColors[4] : primaryLightColors[7],
      'button-active-font-color-primary': primarySecondColor ? primarySecondColor : primaryColor,
      'button-active-bg-color-primary': primarySecondColor ? primaryLightColors[3] : primaryLightColors[7]
    }
  };
  Object.keys(buttonTheme).forEach(type => {
    const item = buttonTheme[type];
    Object.keys(item).forEach(property => {
      el.style.setProperty(`--${cssNamespace}-${property}-${type}`, item[property]);
    });
  });
};

let _this$1 = {
  contentList: [],
  componentTotal: 0,
  chapterIndex: 0,
  chapterIndex: 0,
  componentList: [],
  isTrial: false,
  isTrialIndex: 10
};
const formatTextBook = data => {
  _this$1 = {
    ..._this$1,
    ...data
  };
  return new Promise(resolve => {
    const getItemByChild = list => {
      for (let i = 0; i < list.length; i++) {
        let it = list[i];
        if (_this$1.looseLeafData[it.id] && _this$1.looseLeafData[it.id].looseLeafInfo) {
          if (JSON.parse(_this$1.looseLeafData[it.id].looseLeafInfo).length > 0) {
            // 更新组件总数
            _this$1.componentTotal += JSON.parse(_this$1.looseLeafData[it.id].looseLeafInfo).length;
            _this$1.looseLeafData[it.id].label = it.label;
            _this$1.contentList.push(it);
            if (it.children) {
              getItemByChild(it.children);
            }
          }
        } else {
          list[i].xmlCompRef = new Date().getTime();
        }
      }
    };
    getItemByChild(_this$1.catalogList);
    // 处理数据格式

    _this$1.chapterIndex = 0;
    formattedData(_this$1.contentList[0].id, resolve); //@format
    // setTimeout(() => {
    //     resolve(textBookData)
    // }, 2000)
    // const xhr = new XMLHttpRequest()
    // xhr.onreadystatechange = () => {
    //   if (xhr.readyState === 4 && xhr.status === 200) {
    //     chalk = xhr.responseText.replace(/@font-face{[^}]+}/, '')
    //     console.log('getCSSString: chalk', chalk);
    //     resolve()
    //   }
    // }
    // xhr.onerror = (err) => {
    //   console.error('样式下载失败', err)
    // }
    // xhr.open('GET', url)
    // xhr.send()
  });
};
// 处理活页json数据信息 传入id获取数据解析为list
function formattedData(chapterId, resolve) {
  let list = JSON.parse(_this$1.looseLeafData[chapterId].looseLeafInfo);
  let readIndex = Number(_this$1.componentTotal * (_this$1.isTrialIndex / 100)).toFixed(0); //可以试读到的组件index
  // let startIndex = _this.componentIndex
  // list.map((element) => {
  //   let isTrialExternal = this.isTrial ? (this.componentIndex < readIndex ? false : true) : false
  //   // 向数据里添加位置信息，第一位：章节Id 、第二位：模块Id
  //   let location = `xml-digital-teaching/${chapterId}/${element.xmlCompRef}`
  //   this.$set(element, 'location', location)

  //   // 向数据里添加章节Id
  //   this.$set(element, 'chapterId', chapterId)
  //   this.$set(element, 'componentIndex', this.componentIndex)
  //   this.componentIndex++
  //   this.$set(element, 'label', this.looseLeafData[chapterId].label) //章节名字
  //   this.$set(element, 'firstLevelId', this.firstLevelChapterId) //章节所在一级标题的Id
  //   this.$set(element, 'firstLevelLabel', this.firstLevelLabel) //章节所在一级标题的名称
  //   this.$set(element, 'locationChapterId', this.locationChapterId) //当前内容阅读所在目录id
  //   this.$set(element, 'locationLabel', this.locationLabel) //当前内容阅读所在目录名称
  //   this.$set(element, 'chapterOrderNum', this.contentList[this.chapterIndex].chapterOrderNum) //当前内容阅读所在目录顺序
  //   this.$set(element, 'startIndex', startIndex) //章节开始位置
  //   this.$set(element, 'endIndex', startIndex + list.length) //章节开始位置
  // })

  // let data = {
  //   label: _this.looseLeafData[chapterId].label,
  //   chapterId: chapterId,
  //   list: list
  // }
  if (!_this$1.isTrial || _this$1.componentList.length < readIndex && _this$1.isTrial) {
    //判断是否非试读模式或当前内容在可试读内
    // 组件数据添加到componentList
    let compList = [];
    if (_this$1.componentList.length + list.length > readIndex && _this$1.isTrial) {
      //如果当前章节内容累加进去之后会超出试读区域就截取试读区域内的内容
      let i = readIndex - _this$1.componentList.length;
      compList = list.slice(0, i);
    } else {
      compList = list;
    }
    _this$1.componentList = _this$1.componentList.concat(compList);
  }
  if (_this$1.contentList.length - 1 > _this$1.chapterIndex) {
    _this$1.chapterIndex++;
    formattedData(_this$1.contentList[_this$1.chapterIndex].id, resolve);
  } else {
    const getItemByChild = list => {
      for (let i = 0; i < list.length; i++) {
        let isTrial = _this$1.componentList.findIndex(item => item.chapterId == list[i].id) == -1;
        if (isTrial) {
          list[i] = {
            ...list[i],
            notUsable: true
          };
        }
        if (list[i].children) {
          getItemByChild(list[i].children);
        }
        // let it = list[i]
        // if ( _this.looseLeafData[it.id] && _this.looseLeafData[it.id].looseLeafInfo) {
        //   if (JSON.parse(_this.looseLeafData[it.id].looseLeafInfo).length > 0) {
        //     // 更新组件总数
        //     _this.componentTotal += JSON.parse(_this.looseLeafData[it.id].looseLeafInfo).length

        //     _this.looseLeafData[it.id].label = it.label
        //     _this.contentList.push(it)
        // if (it.children) {
        //   getItemByChild(it.children)
        // }
        //   }
        // } else {
        //   list[i].xmlCompRef=new Date().getTime()
        // }
      }
    };
    getItemByChild(_this$1.catalogList);
    resolve({
      catalogList: _this$1.catalogList,
      componentList: _this$1.componentList
    });
  }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by rollup-plugin-commonjs');
}

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var vhCheck = createCommonjsModule(function (module, exports) {
(function (global, factory) {
  module.exports = factory() ;
})(commonjsGlobal, function () {

  /*! *****************************************************************************
  Copyright (c) Microsoft Corporation. All rights reserved.
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at http://www.apache.org/licenses/LICENSE-2.0
    THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
  KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
  WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
  MERCHANTABLITY OR NON-INFRINGEMENT.
    See the Apache Version 2.0 License for specific language governing permissions
  and limitations under the License.
  ***************************************************************************** */
  var __assign = function () {
    __assign = Object.assign || function __assign(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
    };
    return __assign.apply(this, arguments);
  };

  // don't know a better way to get the size of a CSS 100vh…
  function createTestElement() {
    var testElement = document.createElement('div');
    testElement.style.cssText = 'position: fixed; top: 0; height: 100vh; pointer-events: none;';
    document.documentElement.insertBefore(testElement, document.documentElement.firstChild);
    return testElement;
  }
  function removeTestElement(element) {
    document.documentElement.removeChild(element);
  }
  //  in some browsers this will be bigger than window.innerHeight
  function checkSizes() {
    var vhTest = createTestElement();
    var windowHeight = window.innerHeight;
    var vh = vhTest.offsetHeight;
    var offset = vh - windowHeight;
    removeTestElement(vhTest);
    return {
      vh: vh,
      windowHeight: windowHeight,
      offset: offset,
      isNeeded: offset !== 0,
      value: 0
    };
  }
  // export
  function noop() {}
  function computeDifference() {
    var sizes = checkSizes();
    sizes.value = sizes.offset;
    return sizes;
  }
  function redefineVhUnit() {
    var sizes = checkSizes();
    sizes.value = sizes.windowHeight * 0.01;
    return sizes;
  }
  var methods = /*#__PURE__*/Object.freeze({
    noop: noop,
    computeDifference: computeDifference,
    redefineVhUnit: redefineVhUnit
  });
  function isString(text) {
    return typeof text === "string" && text.length > 0;
  }
  function isFunction(f) {
    return typeof f === "function";
  }
  var defaultOptions = Object.freeze({
    cssVarName: 'vh-offset',
    redefineVh: false,
    method: computeDifference,
    force: false,
    bind: true,
    updateOnTouch: false,
    onUpdate: noop
  });
  function getOptions(options) {
    // old options handling: only redefine the CSS var name
    if (isString(options)) {
      return __assign({}, defaultOptions, {
        cssVarName: options
      });
    }
    // be sure to have a configuration object
    if (typeof options !== 'object') return defaultOptions;
    // make sure we have the right options to start with
    var finalOptions = {
      force: options.force === true,
      bind: options.bind !== false,
      updateOnTouch: options.updateOnTouch === true,
      onUpdate: isFunction(options.onUpdate) ? options.onUpdate : noop
    };
    // method change
    var redefineVh = options.redefineVh === true;
    finalOptions.method = methods[redefineVh ? 'redefineVhUnit' : 'computeDifference'];
    finalOptions.cssVarName = isString(options.cssVarName) ? options.cssVarName : redefineVh ?
    /*
    when redefining vh unit we follow this article name convention
    https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    */
    'vh' : defaultOptions.cssVarName;
    return finalOptions;
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
  var passiveSupported = false;
  var eventListeners = [];
  /* istanbul ignore next */
  try {
    var options = Object.defineProperty({}, "passive", {
      get: function () {
        passiveSupported = true;
      }
    });
    window.addEventListener("test", options, options);
    window.removeEventListener("test", options, options);
  } catch (err) {
    passiveSupported = false;
  }
  function addListener(eventName, callback) {
    eventListeners.push({
      eventName: eventName,
      callback: callback
    });
    window.addEventListener(eventName, callback, /* istanbul ignore next */
    passiveSupported ? {
      passive: true
    } : false);
  }
  function removeAll() {
    eventListeners.forEach(function (config) {
      window.removeEventListener(config.eventName, config.callback);
    });
    eventListeners = [];
  }
  function updateCssVar(cssVarName, result) {
    document.documentElement.style.setProperty("--" + cssVarName, result.value + "px");
  }
  function formatResult(sizes, options) {
    return __assign({}, sizes, {
      unbind: removeAll,
      recompute: options.method
    });
  }
  function vhCheck(options) {
    var config = Object.freeze(getOptions(options));
    var result = formatResult(config.method(), config);
    // usefulness check
    if (!result.isNeeded && !config.force) {
      return result;
    }
    updateCssVar(config.cssVarName, result);
    config.onUpdate(result);
    // enabled by default
    if (!config.bind) return result;
    function onWindowChange() {
      window.requestAnimationFrame(function () {
        var sizes = config.method();
        updateCssVar(config.cssVarName, sizes);
        config.onUpdate(formatResult(sizes, config));
      });
    }
    // be sure we don't duplicates events listeners
    result.unbind();
    // listen for orientation change
    // - this can't be configured
    // - because it's convenient and not a real performance bottleneck
    addListener('orientationchange', onWindowChange);
    // listen to touch move for scrolling
    // – disabled by default
    // - listening to scrolling can be expansive…
    if (config.updateOnTouch) {
      addListener('touchmove', onWindowChange);
    }
    return result;
  }
  return vhCheck;
});
});

function _extends$1() {
  return _extends$1 = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends$1.apply(null, arguments);
}

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (a) {
    for (var b, c = 1; c < arguments.length; c++) for (var d in b = arguments[c], b) Object.prototype.hasOwnProperty.call(b, d) && (a[d] = b[d]);
    return a;
  }, _extends.apply(this, arguments);
}
var normalMerge = ["attrs", "props", "domProps"],
  toArrayMerge = ["class", "style", "directives"],
  functionalMerge = ["on", "nativeOn"],
  mergeJsxProps = function (a) {
    return a.reduce(function (c, a) {
      for (var b in a) if (!c[b]) c[b] = a[b];else if (-1 !== normalMerge.indexOf(b)) c[b] = _extends({}, c[b], a[b]);else if (-1 !== toArrayMerge.indexOf(b)) {
        var d = c[b] instanceof Array ? c[b] : [c[b]],
          e = a[b] instanceof Array ? a[b] : [a[b]];
        c[b] = [].concat(d, e);
      } else if (-1 !== functionalMerge.indexOf(b)) {
        for (var f in a[b]) if (c[b][f]) {
          var g = c[b][f] instanceof Array ? c[b][f] : [c[b][f]],
            h = a[b][f] instanceof Array ? a[b][f] : [a[b][f]];
          c[b][f] = [].concat(g, h);
        } else c[b][f] = a[b][f];
      } else if ("hook" === b) for (var i in a[b]) c[b][i] = c[b][i] ? mergeFn(c[b][i], a[b][i]) : a[b][i];else c[b] = a[b];
      return c;
    }, {});
  },
  mergeFn = function (a, b) {
    return function () {
      a && a.apply(this, arguments), b && b.apply(this, arguments);
    };
  };
var helper = mergeJsxProps;

var _mergeJSXProps = helper;

/**
 * bem helper
 * b() // 'button'
 * b('text') // 'button__text'
 * b({ disabled }) // 'button button--disabled'
 * b('text', { disabled }) // 'button__text button__text--disabled'
 * b(['disabled', 'primary']) // 'button button--disabled button--primary'
 */
function gen(name, mods) {
  if (!mods) {
    return '';
  }
  if (typeof mods === 'string') {
    return " " + name + "--" + mods;
  }
  if (Array.isArray(mods)) {
    return mods.reduce(function (ret, item) {
      return ret + gen(name, item);
    }, '');
  }
  return Object.keys(mods).reduce(function (ret, key) {
    return ret + (mods[key] ? gen(name, key) : '');
  }, '');
}
function createBEM(name) {
  return function (el, mods) {
    if (el && typeof el !== 'string') {
      mods = el;
      el = '';
    }
    el = el ? name + "__" + el : name;
    return "" + el + gen(el, mods);
  };
}

var hasOwnProperty$3 = Object.prototype.hasOwnProperty;
function assignKey(to, from, key) {
  var val = from[key];
  if (!isDef(val)) {
    return;
  }
  if (!hasOwnProperty$3.call(to, key) || !isObject$2(val)) {
    to[key] = val;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    to[key] = deepAssign(Object(to[key]), from[key]);
  }
}
function deepAssign(to, from) {
  Object.keys(from).forEach(function (key) {
    assignKey(to, from, key);
  });
  return to;
}

var defaultMessages = {
  name: '姓名',
  tel: '电话',
  save: '保存',
  confirm: '确认',
  cancel: '取消',
  delete: '删除',
  complete: '完成',
  loading: '加载中...',
  telEmpty: '请填写电话',
  nameEmpty: '请填写姓名',
  nameInvalid: '请输入正确的姓名',
  confirmDelete: '确定要删除吗',
  telInvalid: '请输入正确的手机号',
  vanCalendar: {
    end: '结束',
    start: '开始',
    title: '日期选择',
    confirm: '确定',
    startEnd: '开始/结束',
    weekdays: ['日', '一', '二', '三', '四', '五', '六'],
    monthTitle: function monthTitle(year, month) {
      return year + "\u5E74" + month + "\u6708";
    },
    rangePrompt: function rangePrompt(maxRange) {
      return "\u9009\u62E9\u5929\u6570\u4E0D\u80FD\u8D85\u8FC7 " + maxRange + " \u5929";
    }
  },
  vanCascader: {
    select: '请选择'
  },
  vanContactCard: {
    addText: '添加联系人'
  },
  vanContactList: {
    addText: '新建联系人'
  },
  vanPagination: {
    prev: '上一页',
    next: '下一页'
  },
  vanPullRefresh: {
    pulling: '下拉即可刷新...',
    loosing: '释放即可刷新...'
  },
  vanSubmitBar: {
    label: '合计：'
  },
  vanCoupon: {
    unlimited: '无使用门槛',
    discount: function discount(_discount) {
      return _discount + "\u6298";
    },
    condition: function condition(_condition) {
      return "\u6EE1" + _condition + "\u5143\u53EF\u7528";
    }
  },
  vanCouponCell: {
    title: '优惠券',
    tips: '暂无可用',
    count: function count(_count) {
      return _count + "\u5F20\u53EF\u7528";
    }
  },
  vanCouponList: {
    empty: '暂无优惠券',
    exchange: '兑换',
    close: '不使用优惠券',
    enable: '可用',
    disabled: '不可用',
    placeholder: '请输入优惠码'
  },
  vanAddressEdit: {
    area: '地区',
    postal: '邮政编码',
    areaEmpty: '请选择地区',
    addressEmpty: '请填写详细地址',
    postalEmpty: '邮政编码格式不正确',
    defaultAddress: '设为默认收货地址',
    telPlaceholder: '收货人手机号',
    namePlaceholder: '收货人姓名',
    areaPlaceholder: '选择省 / 市 / 区'
  },
  vanAddressEditDetail: {
    label: '详细地址',
    placeholder: '街道门牌、楼层房间号等信息'
  },
  vanAddressList: {
    add: '新增地址'
  }
};

var proto$1 = Vue.prototype;
var defineReactive = Vue.util.defineReactive;
defineReactive(proto$1, '$vantLang', 'zh-CN');
defineReactive(proto$1, '$vantMessages', {
  'zh-CN': defaultMessages
});
var Locale$1 = {
  messages: function messages() {
    return proto$1.$vantMessages[proto$1.$vantLang];
  },
  use: function use(lang, messages) {
    var _this$add;
    proto$1.$vantLang = lang;
    this.add((_this$add = {}, _this$add[lang] = messages, _this$add));
  },
  add: function add(messages) {
    if (messages === void 0) {
      messages = {};
    }
    deepAssign(proto$1.$vantMessages, messages);
  }
};

var camelizeRE = /-(\w)/g;
function camelize(str) {
  return str.replace(camelizeRE, function (_, c) {
    return c.toUpperCase();
  });
}
function padZero(num, targetLength) {
  if (targetLength === void 0) {
    targetLength = 2;
  }
  var str = num + '';
  while (str.length < targetLength) {
    str = '0' + str;
  }
  return str;
}

/**
 * Use scopedSlots in Vue 2.6+
 * downgrade to slots in lower version
 */
var SlotsMixin = {
  methods: {
    slots: function slots(name, props) {
      if (name === void 0) {
        name = 'default';
      }
      var $slots = this.$slots,
        $scopedSlots = this.$scopedSlots;
      var scopedSlot = $scopedSlots[name];
      if (scopedSlot) {
        return scopedSlot(props);
      }
      return $slots[name];
    }
  }
};

/**
 * Create a basic component with common options
 */
function install$3(Vue) {
  var name = this.name;
  Vue.component(name, this);
  Vue.component(camelize("-" + name), this);
} // unify slots & scopedSlots

function unifySlots(context) {
  // use data.scopedSlots in lower Vue version
  var scopedSlots = context.scopedSlots || context.data.scopedSlots || {};
  var slots = context.slots();
  Object.keys(slots).forEach(function (key) {
    if (!scopedSlots[key]) {
      scopedSlots[key] = function () {
        return slots[key];
      };
    }
  });
  return scopedSlots;
} // should be removed after Vue 3

function transformFunctionComponent(pure) {
  return {
    functional: true,
    props: pure.props,
    model: pure.model,
    render: function render(h, context) {
      return pure(h, context.props, unifySlots(context), context);
    }
  };
}
function createComponent$1H(name) {
  return function (sfc) {
    if (isFunction$2(sfc)) {
      sfc = transformFunctionComponent(sfc);
    }
    if (!sfc.functional) {
      sfc.mixins = sfc.mixins || [];
      sfc.mixins.push(SlotsMixin);
    }
    sfc.name = name;
    sfc.install = install$3;
    return sfc;
  };
}

function createI18N(name) {
  var prefix = camelize(name) + '.';
  return function (path) {
    var messages = Locale$1.messages();
    var message = get$2(messages, prefix + path) || get$2(messages, path);
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return isFunction$2(message) ? message.apply(void 0, args) : message;
  };
}

function createNamespace(name) {
  name = 'van-' + name;
  return [createComponent$1H(name), createBEM(name), createI18N(name)];
}

function isNumeric(val) {
  return /^\d+(\.\d+)?$/.test(val);
}
function isNaN$1(val) {
  if (Number.isNaN) {
    return Number.isNaN(val);
  } // eslint-disable-next-line no-self-compare

  return val !== val;
}

function addUnit(value) {
  if (!isDef(value)) {
    return undefined;
  }
  value = String(value);
  return isNumeric(value) ? value + "px" : value;
} // cache

var rootFontSize;
function getRootFontSize() {
  if (!rootFontSize) {
    var doc = document.documentElement;
    var fontSize = doc.style.fontSize || window.getComputedStyle(doc).fontSize;
    rootFontSize = parseFloat(fontSize);
  }
  return rootFontSize;
}
function convertRem(value) {
  value = value.replace(/rem/g, '');
  return +value * getRootFontSize();
}
function convertVw(value) {
  value = value.replace(/vw/g, '');
  return +value * window.innerWidth / 100;
}
function convertVh(value) {
  value = value.replace(/vh/g, '');
  return +value * window.innerHeight / 100;
}
function unitToPx(value) {
  if (typeof value === 'number') {
    return value;
  }
  if (inBrowser$1) {
    if (value.indexOf('rem') !== -1) {
      return convertRem(value);
    }
    if (value.indexOf('vw') !== -1) {
      return convertVw(value);
    }
    if (value.indexOf('vh') !== -1) {
      return convertVh(value);
    }
  }
  return parseFloat(value);
}

var inBrowser$1 = typeof window !== 'undefined';
var isServer$1 = Vue.prototype.$isServer; // eslint-disable-next-line @typescript-eslint/no-empty-function

function noop$2() {}
function isDef(val) {
  return val !== undefined && val !== null;
}
function isFunction$2(val) {
  return typeof val === 'function';
}
function isObject$2(val) {
  return val !== null && typeof val === 'object';
}
function isPromise$1(val) {
  return isObject$2(val) && isFunction$2(val.then) && isFunction$2(val.catch);
}
function get$2(object, path) {
  var keys = path.split('.');
  var result = object;
  keys.forEach(function (key) {
    var _result$key;
    result = isObject$2(result) ? (_result$key = result[key]) != null ? _result$key : '' : '';
  });
  return result;
}
/**
 * Checks if `value` is an empty object, collection, map, or set.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Array-like values such as `arguments` objects, arrays, buffers, strings, or
 * jQuery-like collections are considered empty if they have a `length` of `0`.
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @function isEmpty
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 *
 * _.isEmpty(null);
 * // => true
 *
 * _.isEmpty(true);
 * // => true
 *
 * _.isEmpty(1);
 * // => true
 *
 * _.isEmpty([1, 2, 3]);
 * // => false
 *
 * _.isEmpty({ 'a': 1 });
 * // => false
 */

function isEmpty$2(value) {
  if (value == null) {
    return true;
  }
  if (typeof value !== 'object') {
    return true;
  }
  return Object.keys(value).length === 0;
}

var inheritKey = ['ref', 'key', 'style', 'class', 'attrs', 'refInFor', 'nativeOn', 'directives', 'staticClass', 'staticStyle'];
var mapInheritKey = {
  nativeOn: 'on'
}; // inherit partial context, map nativeOn to on

function inherit(context, inheritListeners) {
  var result = inheritKey.reduce(function (obj, key) {
    if (context.data[key]) {
      obj[mapInheritKey[key] || key] = context.data[key];
    }
    return obj;
  }, {});
  if (inheritListeners) {
    result.on = result.on || {};
    _extends$1(result.on, context.data.on);
  }
  return result;
} // emit event

function emit(context, eventName) {
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }
  var listeners = context.listeners[eventName];
  if (listeners) {
    if (Array.isArray(listeners)) {
      listeners.forEach(function (listener) {
        listener.apply(void 0, args);
      });
    } else {
      listeners.apply(void 0, args);
    }
  }
} // mount functional component

function mount(Component, data) {
  var instance = new Vue({
    el: document.createElement('div'),
    props: Component.props,
    render: function render(h) {
      return h(Component, _extends$1({
        props: this.$props
      }, data));
    }
  });
  document.body.appendChild(instance.$el);
  return instance;
}

var context$1 = {
  zIndex: 2000,
  lockCount: 0,
  stack: [],
  find: function find(vm) {
    return this.stack.filter(function (item) {
      return item.vm === vm;
    })[0];
  },
  remove: function remove(vm) {
    var item = this.find(vm);
    if (!item) return;
    item.vm = null;
    item.overlay = null;
    var index = this.stack.indexOf(item);
    this.stack.splice(index, 1);
  }
};

// eslint-disable-next-line import/no-mutable-exports
var supportsPassive = false;
if (!isServer$1) {
  try {
    var opts = {};
    Object.defineProperty(opts, 'passive', {
      // eslint-disable-next-line getter-return
      get: function get() {
        /* istanbul ignore next */
        supportsPassive = true;
      }
    });
    window.addEventListener('test-passive', null, opts); // eslint-disable-next-line no-empty
  } catch (e) {}
}
function on$2(target, event, handler, passive) {
  if (passive === void 0) {
    passive = false;
  }
  if (!isServer$1) {
    target.addEventListener(event, handler, supportsPassive ? {
      capture: false,
      passive: passive
    } : false);
  }
}
function off$2(target, event, handler) {
  if (!isServer$1) {
    target.removeEventListener(event, handler);
  }
}
function stopPropagation(event) {
  event.stopPropagation();
}
function preventDefault(event, isStopPropagation) {
  /* istanbul ignore else */
  if (typeof event.cancelable !== 'boolean' || event.cancelable) {
    event.preventDefault();
  }
  if (isStopPropagation) {
    stopPropagation(event);
  }
}

var _createNamespace$1C = createNamespace('overlay'),
  createComponent$1G = _createNamespace$1C[0],
  bem$1x = _createNamespace$1C[1];
function preventTouchMove(event) {
  preventDefault(event, true);
}
function Overlay(h, props, slots, ctx) {
  var style = _extends$1({
    zIndex: props.zIndex
  }, props.customStyle);
  if (isDef(props.duration)) {
    style.animationDuration = props.duration + "s";
  }
  return h("transition", {
    "attrs": {
      "name": "van-fade"
    }
  }, [h("div", _mergeJSXProps([{
    "directives": [{
      name: "show",
      value: props.show
    }],
    "style": style,
    "class": [bem$1x(), props.className],
    "on": {
      "touchmove": props.lockScroll ? preventTouchMove : noop$2
    }
  }, inherit(ctx, true)]), [slots.default == null ? void 0 : slots.default()])]);
}
Overlay.props = {
  show: Boolean,
  zIndex: [Number, String],
  duration: [Number, String],
  className: null,
  customStyle: Object,
  lockScroll: {
    type: Boolean,
    default: true
  }
};
var Overlay$1 = createComponent$1G(Overlay);

function removeNode(el) {
  var parent = el.parentNode;
  if (parent) {
    parent.removeChild(el);
  }
}

var defaultConfig$1 = {
  className: '',
  customStyle: {}
};
function mountOverlay(vm) {
  return mount(Overlay$1, {
    on: {
      // close popup when overlay clicked & closeOnClickOverlay is true
      click: function click() {
        vm.$emit('click-overlay');
        if (vm.closeOnClickOverlay) {
          if (vm.onClickOverlay) {
            vm.onClickOverlay();
          } else {
            vm.close();
          }
        }
      }
    }
  });
}
function updateOverlay(vm) {
  var item = context$1.find(vm);
  if (item) {
    var el = vm.$el;
    var config = item.config,
      overlay = item.overlay;
    if (el && el.parentNode) {
      el.parentNode.insertBefore(overlay.$el, el);
    }
    _extends$1(overlay, defaultConfig$1, config, {
      show: true
    });
  }
}
function openOverlay(vm, config) {
  var item = context$1.find(vm);
  if (item) {
    item.config = config;
  } else {
    var overlay = mountOverlay(vm);
    context$1.stack.push({
      vm: vm,
      config: config,
      overlay: overlay
    });
  }
  updateOverlay(vm);
}
function closeOverlay(vm) {
  var item = context$1.find(vm);
  if (item) {
    item.overlay.show = false;
  }
}
function removeOverlay(vm) {
  var item = context$1.find(vm);
  if (item) {
    removeNode(item.overlay.$el);
    context$1.remove(vm);
  }
}

function isWindow$1(val) {
  return val === window;
} // get nearest scroll element
// https://github.com/vant-ui/vant/issues/3823

var overflowScrollReg = /scroll|auto|overlay/i;
function getScroller(el, root) {
  if (root === void 0) {
    root = window;
  }
  var node = el;
  while (node && node.tagName !== 'HTML' && node.tagName !== 'BODY' && node.nodeType === 1 && node !== root) {
    var _window$getComputedSt = window.getComputedStyle(node),
      overflowY = _window$getComputedSt.overflowY;
    if (overflowScrollReg.test(overflowY)) {
      return node;
    }
    node = node.parentNode;
  }
  return root;
}
function getScrollTop(el) {
  var top = 'scrollTop' in el ? el.scrollTop : el.pageYOffset; // iOS scroll bounce cause minus scrollTop

  return Math.max(top, 0);
}
function setScrollTop(el, value) {
  if ('scrollTop' in el) {
    el.scrollTop = value;
  } else {
    el.scrollTo(el.scrollX, value);
  }
}
function getRootScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
}
function setRootScrollTop(value) {
  setScrollTop(window, value);
  setScrollTop(document.body, value);
} // get distance from element top to page top or scroller top

function getElementTop(el, scroller) {
  if (isWindow$1(el)) {
    return 0;
  }
  var scrollTop = scroller ? getScrollTop(scroller) : getRootScrollTop();
  return el.getBoundingClientRect().top + scrollTop;
}
function getVisibleHeight(el) {
  if (isWindow$1(el)) {
    return el.innerHeight;
  }
  return el.getBoundingClientRect().height;
}
function getVisibleTop(el) {
  if (isWindow$1(el)) {
    return 0;
  }
  return el.getBoundingClientRect().top;
}

function getDirection(x, y) {
  if (x > y) {
    return 'horizontal';
  }
  if (y > x) {
    return 'vertical';
  }
  return '';
}
var TouchMixin = {
  data: function data() {
    return {
      direction: ''
    };
  },
  methods: {
    touchStart: function touchStart(event) {
      this.resetTouchStatus();
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
    },
    touchMove: function touchMove(event) {
      var touch = event.touches[0]; // safari back will set clientX to negative number

      this.deltaX = touch.clientX < 0 ? 0 : touch.clientX - this.startX;
      this.deltaY = touch.clientY - this.startY;
      this.offsetX = Math.abs(this.deltaX);
      this.offsetY = Math.abs(this.deltaY); // lock direction when distance is greater than a certain value

      var LOCK_DIRECTION_DISTANCE = 10;
      if (!this.direction || this.offsetX < LOCK_DIRECTION_DISTANCE && this.offsetY < LOCK_DIRECTION_DISTANCE) {
        this.direction = getDirection(this.offsetX, this.offsetY);
      }
    },
    resetTouchStatus: function resetTouchStatus() {
      this.direction = '';
      this.deltaX = 0;
      this.deltaY = 0;
      this.offsetX = 0;
      this.offsetY = 0;
    },
    // avoid Vue 2.6 event bubble issues by manually binding events
    // https://github.com/vant-ui/vant/issues/3015
    bindTouchEvent: function bindTouchEvent(el) {
      var onTouchStart = this.onTouchStart,
        onTouchMove = this.onTouchMove,
        onTouchEnd = this.onTouchEnd;
      on$2(el, 'touchstart', onTouchStart);
      on$2(el, 'touchmove', onTouchMove);
      if (onTouchEnd) {
        on$2(el, 'touchend', onTouchEnd);
        on$2(el, 'touchcancel', onTouchEnd);
      }
    }
  }
};

function getElement(selector) {
  if (typeof selector === 'string') {
    return document.querySelector(selector);
  }
  return selector();
}
function PortalMixin(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    ref = _ref.ref,
    afterPortal = _ref.afterPortal;
  return {
    props: {
      getContainer: [String, Function]
    },
    watch: {
      getContainer: 'portal'
    },
    mounted: function mounted() {
      if (this.getContainer) {
        this.portal();
      }
    },
    methods: {
      portal: function portal() {
        var getContainer = this.getContainer;
        var el = ref ? this.$refs[ref] : this.$el;
        var container;
        if (getContainer) {
          container = getElement(getContainer);
        } else if (this.$parent) {
          container = this.$parent.$el;
        }
        if (container && container !== el.parentNode) {
          container.appendChild(el);
        }
        if (afterPortal) {
          afterPortal.call(this);
        }
      }
    }
  };
}

/**
 * Bind event when mounted or activated
 */
var uid$1 = 0;
function BindEventMixin(handler) {
  var key = "binded_" + uid$1++;
  function bind() {
    if (!this[key]) {
      handler.call(this, on$2, true);
      this[key] = true;
    }
  }
  function unbind() {
    if (this[key]) {
      handler.call(this, off$2, false);
      this[key] = false;
    }
  }
  return {
    mounted: bind,
    activated: bind,
    deactivated: unbind,
    beforeDestroy: unbind
  };
}

var CloseOnPopstateMixin = {
  mixins: [BindEventMixin(function (bind, isBind) {
    this.handlePopstate(isBind && this.closeOnPopstate);
  })],
  props: {
    closeOnPopstate: Boolean
  },
  data: function data() {
    return {
      bindStatus: false
    };
  },
  watch: {
    closeOnPopstate: function closeOnPopstate(val) {
      this.handlePopstate(val);
    }
  },
  methods: {
    onPopstate: function onPopstate() {
      this.close();
      this.shouldReopen = false;
    },
    handlePopstate: function handlePopstate(bind) {
      /* istanbul ignore if */
      if (this.$isServer) {
        return;
      }
      if (this.bindStatus !== bind) {
        this.bindStatus = bind;
        var action = bind ? on$2 : off$2;
        action(window, 'popstate', this.onPopstate);
      }
    }
  }
};

// Context
var popupMixinProps = {
  // Initial rendering animation
  transitionAppear: Boolean,
  // whether to show popup
  value: Boolean,
  // whether to show overlay
  overlay: Boolean,
  // overlay custom style
  overlayStyle: Object,
  // overlay custom class name
  overlayClass: String,
  // whether to close popup when overlay is clicked
  closeOnClickOverlay: Boolean,
  // z-index
  zIndex: [Number, String],
  // prevent body scroll
  lockScroll: {
    type: Boolean,
    default: true
  },
  // whether to lazy render
  lazyRender: {
    type: Boolean,
    default: true
  }
};
function PopupMixin(options) {
  if (options === void 0) {
    options = {};
  }
  return {
    mixins: [TouchMixin, CloseOnPopstateMixin, PortalMixin({
      afterPortal: function afterPortal() {
        if (this.overlay) {
          updateOverlay();
        }
      }
    })],
    provide: function provide() {
      return {
        vanPopup: this
      };
    },
    props: popupMixinProps,
    data: function data() {
      this.onReopenCallback = [];
      return {
        inited: this.value
      };
    },
    computed: {
      shouldRender: function shouldRender() {
        return this.inited || !this.lazyRender;
      }
    },
    watch: {
      value: function value(val) {
        var type = val ? 'open' : 'close';
        this.inited = this.inited || this.value;
        this[type]();
        if (!options.skipToggleEvent) {
          this.$emit(type);
        }
      },
      overlay: 'renderOverlay'
    },
    mounted: function mounted() {
      if (this.value) {
        this.open();
      }
    },
    /* istanbul ignore next */
    activated: function activated() {
      if (this.shouldReopen) {
        this.$emit('input', true);
        this.shouldReopen = false;
      }
    },
    beforeDestroy: function beforeDestroy() {
      removeOverlay(this);
      if (this.opened) {
        this.removeLock();
      }
      if (this.getContainer) {
        removeNode(this.$el);
      }
    },
    /* istanbul ignore next */
    deactivated: function deactivated() {
      if (this.value) {
        this.close();
        this.shouldReopen = true;
      }
    },
    methods: {
      open: function open() {
        /* istanbul ignore next */
        if (this.$isServer || this.opened) {
          return;
        } // cover default zIndex

        if (this.zIndex !== undefined) {
          context$1.zIndex = this.zIndex;
        }
        this.opened = true;
        this.renderOverlay();
        this.addLock();
        this.onReopenCallback.forEach(function (callback) {
          callback();
        });
      },
      addLock: function addLock() {
        if (this.lockScroll) {
          on$2(document, 'touchstart', this.touchStart);
          on$2(document, 'touchmove', this.onTouchMove);
          if (!context$1.lockCount) {
            document.body.classList.add('van-overflow-hidden');
          }
          context$1.lockCount++;
        }
      },
      removeLock: function removeLock() {
        if (this.lockScroll && context$1.lockCount) {
          context$1.lockCount--;
          off$2(document, 'touchstart', this.touchStart);
          off$2(document, 'touchmove', this.onTouchMove);
          if (!context$1.lockCount) {
            document.body.classList.remove('van-overflow-hidden');
          }
        }
      },
      close: function close() {
        if (!this.opened) {
          return;
        }
        closeOverlay(this);
        this.opened = false;
        this.removeLock();
        this.$emit('input', false);
      },
      onTouchMove: function onTouchMove(event) {
        this.touchMove(event);
        var direction = this.deltaY > 0 ? '10' : '01';
        var el = getScroller(event.target, this.$el);
        var scrollHeight = el.scrollHeight,
          offsetHeight = el.offsetHeight,
          scrollTop = el.scrollTop;
        var status = '11';
        /* istanbul ignore next */

        if (scrollTop === 0) {
          status = offsetHeight >= scrollHeight ? '00' : '01';
        } else if (scrollTop + offsetHeight >= scrollHeight) {
          status = '10';
        }
        /* istanbul ignore next */

        if (status !== '11' && this.direction === 'vertical' && !(parseInt(status, 2) & parseInt(direction, 2))) {
          preventDefault(event, true);
        }
      },
      renderOverlay: function renderOverlay() {
        var _this = this;
        if (this.$isServer || !this.value) {
          return;
        }
        this.$nextTick(function () {
          _this.updateZIndex(_this.overlay ? 1 : 0);
          if (_this.overlay) {
            openOverlay(_this, {
              zIndex: context$1.zIndex++,
              duration: _this.duration,
              className: _this.overlayClass,
              customStyle: _this.overlayStyle
            });
          } else {
            closeOverlay(_this);
          }
        });
      },
      updateZIndex: function updateZIndex(value) {
        if (value === void 0) {
          value = 0;
        }
        this.$el.style.zIndex = ++context$1.zIndex + value;
      },
      onReopen: function onReopen(callback) {
        this.onReopenCallback.push(callback);
      }
    }
  };
}

var _createNamespace$1B = createNamespace('info'),
  createComponent$1F = _createNamespace$1B[0],
  bem$1w = _createNamespace$1B[1];
function Info(h, props, slots, ctx) {
  var dot = props.dot,
    info = props.info;
  var showInfo = isDef(info) && info !== '';
  if (!dot && !showInfo) {
    return;
  }
  return h("div", _mergeJSXProps([{
    "class": bem$1w({
      dot: dot
    })
  }, inherit(ctx, true)]), [dot ? '' : props.info]);
}
Info.props = {
  dot: Boolean,
  info: [Number, String]
};
var Info$1 = createComponent$1F(Info);

var _createNamespace$1A = createNamespace('icon'),
  createComponent$1E = _createNamespace$1A[0],
  bem$1v = _createNamespace$1A[1];
function isImage(name) {
  return name ? name.indexOf('/') !== -1 : false;
} // compatible with legacy usage, should be removed in next major version

var LEGACY_MAP = {
  medel: 'medal',
  'medel-o': 'medal-o',
  'calender-o': 'calendar-o'
};
function correctName(name) {
  return name && LEGACY_MAP[name] || name;
}
function Icon$2(h, props, slots, ctx) {
  var _props$badge;
  var name = correctName(props.name);
  var imageIcon = isImage(name);
  return h(props.tag, _mergeJSXProps([{
    "class": [props.classPrefix, imageIcon ? '' : props.classPrefix + "-" + name],
    "style": {
      color: props.color,
      fontSize: addUnit(props.size)
    }
  }, inherit(ctx, true)]), [slots.default && slots.default(), imageIcon && h("img", {
    "class": bem$1v('image'),
    "attrs": {
      "src": name
    }
  }), h(Info$1, {
    "attrs": {
      "dot": props.dot,
      "info": (_props$badge = props.badge) != null ? _props$badge : props.info
    }
  })]);
}
Icon$2.props = {
  dot: Boolean,
  name: String,
  size: [Number, String],
  // @deprecated
  // should be removed in next major version
  info: [Number, String],
  badge: [Number, String],
  color: String,
  tag: {
    type: String,
    default: 'i'
  },
  classPrefix: {
    type: String,
    default: bem$1v()
  }
};
var Icon$3 = createComponent$1E(Icon$2);

var _createNamespace$1z = createNamespace('popup'),
  createComponent$1D = _createNamespace$1z[0],
  bem$1u = _createNamespace$1z[1];
var Popup = createComponent$1D({
  mixins: [PopupMixin()],
  props: {
    round: Boolean,
    duration: [Number, String],
    closeable: Boolean,
    transition: String,
    safeAreaInsetBottom: Boolean,
    closeIcon: {
      type: String,
      default: 'cross'
    },
    closeIconPosition: {
      type: String,
      default: 'top-right'
    },
    position: {
      type: String,
      default: 'center'
    },
    overlay: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    }
  },
  beforeCreate: function beforeCreate() {
    var _this = this;
    var createEmitter = function createEmitter(eventName) {
      return function (event) {
        return _this.$emit(eventName, event);
      };
    };
    this.onClick = createEmitter('click');
    this.onOpened = createEmitter('opened');
    this.onClosed = createEmitter('closed');
  },
  methods: {
    onClickCloseIcon: function onClickCloseIcon(event) {
      this.$emit('click-close-icon', event);
      this.close();
    }
  },
  render: function render() {
    var _bem;
    var h = arguments[0];
    if (!this.shouldRender) {
      return;
    }
    var round = this.round,
      position = this.position,
      duration = this.duration;
    var isCenter = position === 'center';
    var transitionName = this.transition || (isCenter ? 'van-fade' : "van-popup-slide-" + position);
    var style = {};
    if (isDef(duration)) {
      var key = isCenter ? 'animationDuration' : 'transitionDuration';
      style[key] = duration + "s";
    }
    return h("transition", {
      "attrs": {
        "appear": this.transitionAppear,
        "name": transitionName
      },
      "on": {
        "afterEnter": this.onOpened,
        "afterLeave": this.onClosed
      }
    }, [h("div", {
      "directives": [{
        name: "show",
        value: this.value
      }],
      "style": style,
      "class": bem$1u((_bem = {
        round: round
      }, _bem[position] = position, _bem['safe-area-inset-bottom'] = this.safeAreaInsetBottom, _bem)),
      "on": {
        "click": this.onClick
      }
    }, [this.slots(), this.closeable && h(Icon$3, {
      "attrs": {
        "role": "button",
        "tabindex": "0",
        "name": this.closeIcon
      },
      "class": bem$1u('close-icon', this.closeIconPosition),
      "on": {
        "click": this.onClickCloseIcon
      }
    })])]);
  }
});

var _createNamespace$1y = createNamespace('loading'),
  createComponent$1C = _createNamespace$1y[0],
  bem$1t = _createNamespace$1y[1];
function LoadingIcon(h, props) {
  if (props.type === 'spinner') {
    var Spin = [];
    for (var i = 0; i < 12; i++) {
      Spin.push(h("i"));
    }
    return Spin;
  }
  return h("svg", {
    "class": bem$1t('circular'),
    "attrs": {
      "viewBox": "25 25 50 50"
    }
  }, [h("circle", {
    "attrs": {
      "cx": "50",
      "cy": "50",
      "r": "20",
      "fill": "none"
    }
  })]);
}
function LoadingText(h, props, slots) {
  if (slots.default) {
    var _props$textColor;
    var style = {
      fontSize: addUnit(props.textSize),
      color: (_props$textColor = props.textColor) != null ? _props$textColor : props.color
    };
    return h("span", {
      "class": bem$1t('text'),
      "style": style
    }, [slots.default()]);
  }
}
function Loading(h, props, slots, ctx) {
  var color = props.color,
    size = props.size,
    type = props.type;
  var style = {
    color: color
  };
  if (size) {
    var iconSize = addUnit(size);
    style.width = iconSize;
    style.height = iconSize;
  }
  return h("div", _mergeJSXProps([{
    "class": bem$1t([type, {
      vertical: props.vertical
    }])
  }, inherit(ctx, true)]), [h("span", {
    "class": bem$1t('spinner', type),
    "style": style
  }, [LoadingIcon(h, props)]), LoadingText(h, props, slots)]);
}
Loading.props = {
  color: String,
  size: [Number, String],
  vertical: Boolean,
  textSize: [Number, String],
  textColor: String,
  type: {
    type: String,
    default: 'circular'
  }
};
var Loading$1 = createComponent$1C(Loading);

var _createNamespace$1x = createNamespace('action-sheet'),
  createComponent$1B = _createNamespace$1x[0],
  bem$1s = _createNamespace$1x[1];
function ActionSheet(h, props, slots, ctx) {
  var title = props.title,
    cancelText = props.cancelText,
    closeable = props.closeable;
  function onCancel() {
    emit(ctx, 'input', false);
    emit(ctx, 'cancel');
  }
  function Header() {
    if (title) {
      return h("div", {
        "class": bem$1s('header')
      }, [title, closeable && h(Icon$3, {
        "attrs": {
          "name": props.closeIcon
        },
        "class": bem$1s('close'),
        "on": {
          "click": onCancel
        }
      })]);
    }
  }
  function Option(item, index) {
    var disabled = item.disabled,
      loading = item.loading,
      callback = item.callback;
    function onClickOption(event) {
      event.stopPropagation();
      if (disabled || loading) {
        return;
      }
      if (callback) {
        callback(item);
      }
      if (props.closeOnClickAction) {
        emit(ctx, 'input', false);
      }
      Vue.nextTick(function () {
        emit(ctx, 'select', item, index);
      });
    }
    function OptionContent() {
      if (loading) {
        return h(Loading$1, {
          "class": bem$1s('loading-icon')
        });
      }
      return [h("span", {
        "class": bem$1s('name')
      }, [item.name]), item.subname && h("div", {
        "class": bem$1s('subname')
      }, [item.subname])];
    }
    return h("button", {
      "attrs": {
        "type": "button"
      },
      "class": [bem$1s('item', {
        disabled: disabled,
        loading: loading
      }), item.className],
      "style": {
        color: item.color
      },
      "on": {
        "click": onClickOption
      }
    }, [OptionContent()]);
  }
  function CancelText() {
    if (cancelText) {
      return [h("div", {
        "class": bem$1s('gap')
      }), h("button", {
        "attrs": {
          "type": "button"
        },
        "class": bem$1s('cancel'),
        "on": {
          "click": onCancel
        }
      }, [cancelText])];
    }
  }
  function Description() {
    var description = (slots.description == null ? void 0 : slots.description()) || props.description;
    if (description) {
      return h("div", {
        "class": bem$1s('description')
      }, [description]);
    }
  }
  return h(Popup, _mergeJSXProps([{
    "class": bem$1s(),
    "attrs": {
      "position": "bottom",
      "round": props.round,
      "value": props.value,
      "overlay": props.overlay,
      "duration": props.duration,
      "lazyRender": props.lazyRender,
      "lockScroll": props.lockScroll,
      "getContainer": props.getContainer,
      "closeOnPopstate": props.closeOnPopstate,
      "closeOnClickOverlay": props.closeOnClickOverlay,
      "safeAreaInsetBottom": props.safeAreaInsetBottom
    }
  }, inherit(ctx, true)]), [Header(), Description(), h("div", {
    "class": bem$1s('content')
  }, [props.actions && props.actions.map(Option), slots.default == null ? void 0 : slots.default()]), CancelText()]);
}
ActionSheet.props = _extends$1({}, popupMixinProps, {
  title: String,
  actions: Array,
  duration: [Number, String],
  cancelText: String,
  description: String,
  getContainer: [String, Function],
  closeOnPopstate: Boolean,
  closeOnClickAction: Boolean,
  round: {
    type: Boolean,
    default: true
  },
  closeable: {
    type: Boolean,
    default: true
  },
  closeIcon: {
    type: String,
    default: 'cross'
  },
  safeAreaInsetBottom: {
    type: Boolean,
    default: true
  },
  overlay: {
    type: Boolean,
    default: true
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true
  }
});
var ActionSheet$1 = createComponent$1B(ActionSheet);

function isMobile(value) {
  value = value.replace(/[^-|\d]/g, '');
  return /^((\+86)|(86))?(1)\d{10}$/.test(value) || /^0[0-9-]{10,13}$/.test(value);
}

var DEFAULT_ITEM_HEIGHT = 44;
var pickerProps = {
  title: String,
  loading: Boolean,
  readonly: Boolean,
  itemHeight: [Number, String],
  showToolbar: Boolean,
  cancelButtonText: String,
  confirmButtonText: String,
  allowHtml: {
    type: Boolean,
    default: true
  },
  visibleItemCount: {
    type: [Number, String],
    default: 6
  },
  swipeDuration: {
    type: [Number, String],
    default: 1000
  }
};

// color
var RED = '#ee0a24'; // border

var BORDER = 'van-hairline';
var BORDER_TOP = BORDER + "--top";
var BORDER_LEFT = BORDER + "--left";
var BORDER_BOTTOM = BORDER + "--bottom";
var BORDER_SURROUND = BORDER + "--surround";
var BORDER_TOP_BOTTOM = BORDER + "--top-bottom";
var BORDER_UNSET_TOP_BOTTOM = BORDER + "-unset--top-bottom";

function deepClone(obj) {
  if (!isDef(obj)) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(function (item) {
      return deepClone(item);
    });
  }
  if (typeof obj === 'object') {
    var to = {};
    Object.keys(obj).forEach(function (key) {
      to[key] = deepClone(obj[key]);
    });
    return to;
  }
  return obj;
}

function range$1(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
function trimExtraChar(value, _char, regExp) {
  var index = value.indexOf(_char);
  var prefix = '';
  if (index === -1) {
    return value;
  }
  if (_char === '-' && index !== 0) {
    return value.slice(0, index);
  }
  if (_char === '.' && value.match(/^(\.|-\.)/)) {
    prefix = index ? '-0' : '0';
  }
  return prefix + value.slice(0, index + 1) + value.slice(index).replace(regExp, '');
}
function formatNumber(value, allowDot, allowMinus) {
  if (allowDot === void 0) {
    allowDot = true;
  }
  if (allowMinus === void 0) {
    allowMinus = true;
  }
  if (allowDot) {
    value = trimExtraChar(value, '.', /\./g);
  } else {
    value = value.split('.')[0];
  }
  if (allowMinus) {
    value = trimExtraChar(value, '-', /-/g);
  } else {
    value = value.replace(/-/, '');
  }
  var regExp = allowDot ? /[^-0-9.]/g : /[^-0-9]/g;
  return value.replace(regExp, '');
} // add num and avoid float number

function addNumber(num1, num2) {
  var cardinal = Math.pow(10, 10);
  return Math.round((num1 + num2) * cardinal) / cardinal;
}

var DEFAULT_DURATION = 200; // 惯性滑动思路:
// 在手指离开屏幕时，如果和上一次 move 时的间隔小于 `MOMENTUM_LIMIT_TIME` 且 move
// 距离大于 `MOMENTUM_LIMIT_DISTANCE` 时，执行惯性滑动

var MOMENTUM_LIMIT_TIME = 300;
var MOMENTUM_LIMIT_DISTANCE = 15;
var _createNamespace$1w = createNamespace('picker-column'),
  createComponent$1A = _createNamespace$1w[0],
  bem$1r = _createNamespace$1w[1];
function getElementTranslateY(element) {
  var style = window.getComputedStyle(element);
  var transform = style.transform || style.webkitTransform;
  var translateY = transform.slice(7, transform.length - 1).split(', ')[5];
  return Number(translateY);
}
function isOptionDisabled(option) {
  return isObject$2(option) && option.disabled;
} // use standard WheelEvent:
// https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent

var supportMousewheel = inBrowser$1 && 'onwheel' in window;
var mousewheelTimer = null;
var PickerColumn = createComponent$1A({
  mixins: [TouchMixin],
  props: {
    valueKey: String,
    readonly: Boolean,
    allowHtml: Boolean,
    className: String,
    itemHeight: Number,
    defaultIndex: Number,
    swipeDuration: [Number, String],
    visibleItemCount: [Number, String],
    initialOptions: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  data: function data() {
    return {
      offset: 0,
      duration: 0,
      options: deepClone(this.initialOptions),
      currentIndex: this.defaultIndex
    };
  },
  created: function created() {
    if (this.$parent.children) {
      this.$parent.children.push(this);
    }
    this.setIndex(this.currentIndex);
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$el);
    if (supportMousewheel) {
      on$2(this.$el, 'wheel', this.onMouseWheel, false);
    }
  },
  destroyed: function destroyed() {
    var children = this.$parent.children;
    if (children) {
      children.splice(children.indexOf(this), 1);
    }
    if (supportMousewheel) {
      off$2(this.$el, 'wheel');
    }
  },
  watch: {
    initialOptions: 'setOptions',
    defaultIndex: function defaultIndex(val) {
      this.setIndex(val);
    }
  },
  computed: {
    count: function count() {
      return this.options.length;
    },
    baseOffset: function baseOffset() {
      return this.itemHeight * (this.visibleItemCount - 1) / 2;
    }
  },
  methods: {
    setOptions: function setOptions(options) {
      if (JSON.stringify(options) !== JSON.stringify(this.options)) {
        this.options = deepClone(options);
        this.setIndex(this.defaultIndex);
      }
    },
    onTouchStart: function onTouchStart(event) {
      if (this.readonly) {
        return;
      }
      this.touchStart(event);
      if (this.moving) {
        var translateY = getElementTranslateY(this.$refs.wrapper);
        this.offset = Math.min(0, translateY - this.baseOffset);
        this.startOffset = this.offset;
      } else {
        this.startOffset = this.offset;
      }
      this.duration = 0;
      this.transitionEndTrigger = null;
      this.touchStartTime = Date.now();
      this.momentumOffset = this.startOffset;
    },
    onTouchMove: function onTouchMove(event) {
      if (this.readonly) {
        return;
      }
      this.touchMove(event);
      if (this.direction === 'vertical') {
        this.moving = true;
        preventDefault(event, true);
      }
      this.offset = range$1(this.startOffset + this.deltaY, -(this.count * this.itemHeight), this.itemHeight);
      var now = Date.now();
      if (now - this.touchStartTime > MOMENTUM_LIMIT_TIME) {
        this.touchStartTime = now;
        this.momentumOffset = this.offset;
      }
    },
    onTouchEnd: function onTouchEnd() {
      var _this = this;
      if (this.readonly) {
        return;
      }
      var distance = this.offset - this.momentumOffset;
      var duration = Date.now() - this.touchStartTime;
      var allowMomentum = duration < MOMENTUM_LIMIT_TIME && Math.abs(distance) > MOMENTUM_LIMIT_DISTANCE;
      if (allowMomentum) {
        this.momentum(distance, duration);
        return;
      }
      var index = this.getIndexByOffset(this.offset);
      this.duration = DEFAULT_DURATION;
      this.setIndex(index, true); // compatible with desktop scenario
      // use setTimeout to skip the click event Emitted after touchstart

      setTimeout(function () {
        _this.moving = false;
      }, 0);
    },
    onMouseWheel: function onMouseWheel(event) {
      var _this2 = this;
      if (this.readonly) {
        return;
      }
      preventDefault(event, true); // simply combine touchstart and touchmove

      var translateY = getElementTranslateY(this.$refs.wrapper);
      this.startOffset = Math.min(0, translateY - this.baseOffset);
      this.momentumOffset = this.startOffset;
      this.transitionEndTrigger = null; // directly use deltaY, see https://caniuse.com/?search=deltaY
      // use deltaY to detect direction for not special setting device
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/wheel_event

      var deltaY = event.deltaY;
      if (this.startOffset === 0 && deltaY < 0) {
        return;
      } // Calculate the offset based on itemHeight

      var itemOffset = this.itemHeight * (deltaY > 0 ? -1 : 1);
      this.offset = range$1(this.startOffset + itemOffset, -(this.count * this.itemHeight), this.itemHeight);
      if (mousewheelTimer) {
        clearTimeout(mousewheelTimer);
      }
      mousewheelTimer = setTimeout(function () {
        _this2.onTouchEnd();
        _this2.touchStartTime = 0;
      }, MOMENTUM_LIMIT_TIME);
    },
    onTransitionEnd: function onTransitionEnd() {
      this.stopMomentum();
    },
    onClickItem: function onClickItem(index) {
      if (this.moving || this.readonly) {
        return;
      }
      this.transitionEndTrigger = null;
      this.duration = DEFAULT_DURATION;
      this.setIndex(index, true);
    },
    adjustIndex: function adjustIndex(index) {
      index = range$1(index, 0, this.count);
      for (var i = index; i < this.count; i++) {
        if (!isOptionDisabled(this.options[i])) return i;
      }
      for (var _i = index - 1; _i >= 0; _i--) {
        if (!isOptionDisabled(this.options[_i])) return _i;
      }
    },
    getOptionText: function getOptionText(option) {
      if (isObject$2(option) && this.valueKey in option) {
        return option[this.valueKey];
      }
      return option;
    },
    setIndex: function setIndex(index, emitChange) {
      var _this3 = this;
      index = this.adjustIndex(index) || 0;
      var offset = -index * this.itemHeight;
      var trigger = function trigger() {
        if (index !== _this3.currentIndex) {
          _this3.currentIndex = index;
          if (emitChange) {
            _this3.$emit('change', index);
          }
        }
      }; // trigger the change event after transitionend when moving

      if (this.moving && offset !== this.offset) {
        this.transitionEndTrigger = trigger;
      } else {
        trigger();
      }
      this.offset = offset;
    },
    setValue: function setValue(value) {
      var options = this.options;
      for (var i = 0; i < options.length; i++) {
        if (this.getOptionText(options[i]) === value) {
          return this.setIndex(i);
        }
      }
    },
    getValue: function getValue() {
      return this.options[this.currentIndex];
    },
    getIndexByOffset: function getIndexByOffset(offset) {
      return range$1(Math.round(-offset / this.itemHeight), 0, this.count - 1);
    },
    momentum: function momentum(distance, duration) {
      var speed = Math.abs(distance / duration);
      distance = this.offset + speed / 0.003 * (distance < 0 ? -1 : 1);
      var index = this.getIndexByOffset(distance);
      this.duration = +this.swipeDuration;
      this.setIndex(index, true);
    },
    stopMomentum: function stopMomentum() {
      this.moving = false;
      this.duration = 0;
      if (this.transitionEndTrigger) {
        this.transitionEndTrigger();
        this.transitionEndTrigger = null;
      }
    },
    genOptions: function genOptions() {
      var _this4 = this;
      var h = this.$createElement;
      var optionStyle = {
        height: this.itemHeight + "px"
      };
      return this.options.map(function (option, index) {
        var _domProps;
        var text = _this4.getOptionText(option);
        var disabled = isOptionDisabled(option);
        var data = {
          style: optionStyle,
          attrs: {
            role: 'button',
            tabindex: disabled ? -1 : 0
          },
          class: [bem$1r('item', {
            disabled: disabled,
            selected: index === _this4.currentIndex
          })],
          on: {
            click: function click() {
              _this4.onClickItem(index);
            }
          }
        };
        var childData = {
          class: 'van-ellipsis',
          domProps: (_domProps = {}, _domProps[_this4.allowHtml ? 'innerHTML' : 'textContent'] = text, _domProps)
        };
        return h("li", _mergeJSXProps([{}, data]), [_this4.slots('option', option) || h("div", _mergeJSXProps([{}, childData]))]);
      });
    }
  },
  render: function render() {
    var h = arguments[0];
    var wrapperStyle = {
      transform: "translate3d(0, " + (this.offset + this.baseOffset) + "px, 0)",
      transitionDuration: this.duration + "ms",
      transitionProperty: this.duration ? 'all' : 'none'
    };
    return h("div", {
      "class": [bem$1r(), this.className]
    }, [h("ul", {
      "ref": "wrapper",
      "style": wrapperStyle,
      "class": bem$1r('wrapper'),
      "on": {
        "transitionend": this.onTransitionEnd
      }
    }, [this.genOptions()])]);
  }
});

var _createNamespace$1v = createNamespace('picker'),
  createComponent$1z = _createNamespace$1v[0],
  bem$1q = _createNamespace$1v[1],
  t$r = _createNamespace$1v[2];
var Picker = createComponent$1z({
  props: _extends$1({}, pickerProps, {
    defaultIndex: {
      type: [Number, String],
      default: 0
    },
    columns: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    toolbarPosition: {
      type: String,
      default: 'top'
    },
    valueKey: {
      type: String,
      default: 'text'
    }
  }),
  data: function data() {
    return {
      children: [],
      formattedColumns: []
    };
  },
  computed: {
    itemPxHeight: function itemPxHeight() {
      return this.itemHeight ? unitToPx(this.itemHeight) : DEFAULT_ITEM_HEIGHT;
    },
    dataType: function dataType() {
      var columns = this.columns;
      var firstColumn = columns[0] || {};
      if (firstColumn.children) {
        return 'cascade';
      }
      if (firstColumn.values) {
        return 'object';
      }
      return 'text';
    }
  },
  watch: {
    columns: {
      handler: 'format',
      immediate: true
    }
  },
  methods: {
    format: function format() {
      var columns = this.columns,
        dataType = this.dataType;
      if (dataType === 'text') {
        this.formattedColumns = [{
          values: columns
        }];
      } else if (dataType === 'cascade') {
        this.formatCascade();
      } else {
        this.formattedColumns = columns;
      }
    },
    formatCascade: function formatCascade() {
      var formatted = [];
      var cursor = {
        children: this.columns
      };
      while (cursor && cursor.children) {
        var _cursor$defaultIndex;
        var _cursor = cursor,
          children = _cursor.children;
        var defaultIndex = (_cursor$defaultIndex = cursor.defaultIndex) != null ? _cursor$defaultIndex : +this.defaultIndex;
        while (children[defaultIndex] && children[defaultIndex].disabled) {
          if (defaultIndex < children.length - 1) {
            defaultIndex++;
          } else {
            defaultIndex = 0;
            break;
          }
        }
        formatted.push({
          values: cursor.children,
          className: cursor.className,
          defaultIndex: defaultIndex
        });
        cursor = children[defaultIndex];
      }
      this.formattedColumns = formatted;
    },
    emit: function emit(event) {
      var _this = this;
      if (this.dataType === 'text') {
        this.$emit(event, this.getColumnValue(0), this.getColumnIndex(0));
      } else {
        var values = this.getValues(); // compatible with old version of wrong parameters
        // should be removed in next major version
        // see: https://github.com/vant-ui/vant/issues/5905

        if (this.dataType === 'cascade') {
          values = values.map(function (item) {
            return item[_this.valueKey];
          });
        }
        this.$emit(event, values, this.getIndexes());
      }
    },
    onCascadeChange: function onCascadeChange(columnIndex) {
      var cursor = {
        children: this.columns
      };
      var indexes = this.getIndexes();
      for (var i = 0; i <= columnIndex; i++) {
        cursor = cursor.children[indexes[i]];
      }
      while (cursor && cursor.children) {
        columnIndex++;
        this.setColumnValues(columnIndex, cursor.children);
        cursor = cursor.children[cursor.defaultIndex || 0];
      }
    },
    onChange: function onChange(columnIndex) {
      var _this2 = this;
      if (this.dataType === 'cascade') {
        this.onCascadeChange(columnIndex);
      }
      if (this.dataType === 'text') {
        this.$emit('change', this, this.getColumnValue(0), this.getColumnIndex(0));
      } else {
        var values = this.getValues(); // compatible with old version of wrong parameters
        // should be removed in next major version
        // see: https://github.com/vant-ui/vant/issues/5905

        if (this.dataType === 'cascade') {
          values = values.map(function (item) {
            return item[_this2.valueKey];
          });
        }
        this.$emit('change', this, values, columnIndex);
      }
    },
    // get column instance by index
    getColumn: function getColumn(index) {
      return this.children[index];
    },
    // @exposed-api
    // get column value by index
    getColumnValue: function getColumnValue(index) {
      var column = this.getColumn(index);
      return column && column.getValue();
    },
    // @exposed-api
    // set column value by index
    setColumnValue: function setColumnValue(index, value) {
      var column = this.getColumn(index);
      if (column) {
        column.setValue(value);
        if (this.dataType === 'cascade') {
          this.onCascadeChange(index);
        }
      }
    },
    // @exposed-api
    // get column option index by column index
    getColumnIndex: function getColumnIndex(columnIndex) {
      return (this.getColumn(columnIndex) || {}).currentIndex;
    },
    // @exposed-api
    // set column option index by column index
    setColumnIndex: function setColumnIndex(columnIndex, optionIndex) {
      var column = this.getColumn(columnIndex);
      if (column) {
        column.setIndex(optionIndex);
        if (this.dataType === 'cascade') {
          this.onCascadeChange(columnIndex);
        }
      }
    },
    // @exposed-api
    // get options of column by index
    getColumnValues: function getColumnValues(index) {
      return (this.children[index] || {}).options;
    },
    // @exposed-api
    // set options of column by index
    setColumnValues: function setColumnValues(index, options) {
      var column = this.children[index];
      if (column) {
        column.setOptions(options);
      }
    },
    // @exposed-api
    // get values of all columns
    getValues: function getValues() {
      return this.children.map(function (child) {
        return child.getValue();
      });
    },
    // @exposed-api
    // set values of all columns
    setValues: function setValues(values) {
      var _this3 = this;
      values.forEach(function (value, index) {
        _this3.setColumnValue(index, value);
      });
    },
    // @exposed-api
    // get indexes of all columns
    getIndexes: function getIndexes() {
      return this.children.map(function (child) {
        return child.currentIndex;
      });
    },
    // @exposed-api
    // set indexes of all columns
    setIndexes: function setIndexes(indexes) {
      var _this4 = this;
      indexes.forEach(function (optionIndex, columnIndex) {
        _this4.setColumnIndex(columnIndex, optionIndex);
      });
    },
    // @exposed-api
    confirm: function confirm() {
      this.children.forEach(function (child) {
        return child.stopMomentum();
      });
      this.emit('confirm');
    },
    cancel: function cancel() {
      this.emit('cancel');
    },
    genTitle: function genTitle() {
      var h = this.$createElement;
      var titleSlot = this.slots('title');
      if (titleSlot) {
        return titleSlot;
      }
      if (this.title) {
        return h("div", {
          "class": ['van-ellipsis', bem$1q('title')]
        }, [this.title]);
      }
    },
    genCancel: function genCancel() {
      var h = this.$createElement;
      return h("button", {
        "attrs": {
          "type": "button"
        },
        "class": bem$1q('cancel'),
        "on": {
          "click": this.cancel
        }
      }, [this.slots('cancel') || this.cancelButtonText || t$r('cancel')]);
    },
    genConfirm: function genConfirm() {
      var h = this.$createElement;
      return h("button", {
        "attrs": {
          "type": "button"
        },
        "class": bem$1q('confirm'),
        "on": {
          "click": this.confirm
        }
      }, [this.slots('confirm') || this.confirmButtonText || t$r('confirm')]);
    },
    genToolbar: function genToolbar() {
      var h = this.$createElement;
      if (this.showToolbar) {
        return h("div", {
          "class": bem$1q('toolbar')
        }, [this.slots() || [this.genCancel(), this.genTitle(), this.genConfirm()]]);
      }
    },
    genColumns: function genColumns() {
      var h = this.$createElement;
      var itemPxHeight = this.itemPxHeight;
      var wrapHeight = itemPxHeight * this.visibleItemCount;
      var frameStyle = {
        height: itemPxHeight + "px"
      };
      var columnsStyle = {
        height: wrapHeight + "px"
      };
      var maskStyle = {
        backgroundSize: "100% " + (wrapHeight - itemPxHeight) / 2 + "px"
      };
      return h("div", {
        "class": bem$1q('columns'),
        "style": columnsStyle,
        "on": {
          "touchmove": preventDefault
        }
      }, [this.genColumnItems(), h("div", {
        "class": bem$1q('mask'),
        "style": maskStyle
      }), h("div", {
        "class": [BORDER_UNSET_TOP_BOTTOM, bem$1q('frame')],
        "style": frameStyle
      })]);
    },
    genColumnItems: function genColumnItems() {
      var _this5 = this;
      var h = this.$createElement;
      return this.formattedColumns.map(function (item, columnIndex) {
        var _item$defaultIndex;
        return h(PickerColumn, {
          "attrs": {
            "readonly": _this5.readonly,
            "valueKey": _this5.valueKey,
            "allowHtml": _this5.allowHtml,
            "className": item.className,
            "itemHeight": _this5.itemPxHeight,
            "defaultIndex": (_item$defaultIndex = item.defaultIndex) != null ? _item$defaultIndex : +_this5.defaultIndex,
            "swipeDuration": _this5.swipeDuration,
            "visibleItemCount": _this5.visibleItemCount,
            "initialOptions": item.values
          },
          "scopedSlots": {
            option: _this5.$scopedSlots.option
          },
          "on": {
            "change": function change() {
              _this5.onChange(columnIndex);
            }
          }
        });
      });
    }
  },
  render: function render(h) {
    return h("div", {
      "class": bem$1q()
    }, [this.toolbarPosition === 'top' ? this.genToolbar() : h(), this.loading ? h(Loading$1, {
      "class": bem$1q('loading')
    }) : h(), this.slots('columns-top'), this.genColumns(), this.slots('columns-bottom'), this.toolbarPosition === 'bottom' ? this.genToolbar() : h()]);
  }
});

var _createNamespace$1u = createNamespace('area'),
  createComponent$1y = _createNamespace$1u[0],
  bem$1p = _createNamespace$1u[1];
var PLACEHOLDER_CODE = '000000';
function isOverseaCode(code) {
  return code[0] === '9';
}
function pickSlots(instance, keys) {
  var $slots = instance.$slots,
    $scopedSlots = instance.$scopedSlots;
  var scopedSlots = {};
  keys.forEach(function (key) {
    if ($scopedSlots[key]) {
      scopedSlots[key] = $scopedSlots[key];
    } else if ($slots[key]) {
      scopedSlots[key] = function () {
        return $slots[key];
      };
    }
  });
  return scopedSlots;
}
var Area = createComponent$1y({
  props: _extends$1({}, pickerProps, {
    value: String,
    areaList: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    columnsNum: {
      type: [Number, String],
      default: 3
    },
    isOverseaCode: {
      type: Function,
      default: isOverseaCode
    },
    columnsPlaceholder: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  }),
  data: function data() {
    return {
      code: this.value,
      columns: [{
        values: []
      }, {
        values: []
      }, {
        values: []
      }]
    };
  },
  computed: {
    province: function province() {
      return this.areaList.province_list || {};
    },
    city: function city() {
      return this.areaList.city_list || {};
    },
    county: function county() {
      return this.areaList.county_list || {};
    },
    displayColumns: function displayColumns() {
      return this.columns.slice(0, +this.columnsNum);
    },
    placeholderMap: function placeholderMap() {
      return {
        province: this.columnsPlaceholder[0] || '',
        city: this.columnsPlaceholder[1] || '',
        county: this.columnsPlaceholder[2] || ''
      };
    }
  },
  watch: {
    value: function value(val) {
      this.code = val;
      this.setValues();
    },
    areaList: {
      deep: true,
      handler: 'setValues'
    },
    columnsNum: function columnsNum() {
      var _this = this;
      this.$nextTick(function () {
        _this.setValues();
      });
    }
  },
  mounted: function mounted() {
    this.setValues();
  },
  methods: {
    // get list by code
    getList: function getList(type, code) {
      var result = [];
      if (type !== 'province' && !code) {
        return result;
      }
      var list = this[type];
      result = Object.keys(list).map(function (listCode) {
        return {
          code: listCode,
          name: list[listCode]
        };
      });
      if (code) {
        // oversea code
        if (this.isOverseaCode(code) && type === 'city') {
          code = '9';
        }
        result = result.filter(function (item) {
          return item.code.indexOf(code) === 0;
        });
      }
      if (this.placeholderMap[type] && result.length) {
        // set columns placeholder
        var codeFill = '';
        if (type === 'city') {
          codeFill = PLACEHOLDER_CODE.slice(2, 4);
        } else if (type === 'county') {
          codeFill = PLACEHOLDER_CODE.slice(4, 6);
        }
        result.unshift({
          code: "" + code + codeFill,
          name: this.placeholderMap[type]
        });
      }
      return result;
    },
    // get index by code
    getIndex: function getIndex(type, code) {
      var compareNum = type === 'province' ? 2 : type === 'city' ? 4 : 6;
      var list = this.getList(type, code.slice(0, compareNum - 2)); // oversea code

      if (this.isOverseaCode(code) && type === 'province') {
        compareNum = 1;
      }
      code = code.slice(0, compareNum);
      for (var i = 0; i < list.length; i++) {
        if (list[i].code.slice(0, compareNum) === code) {
          return i;
        }
      }
      return 0;
    },
    // parse output columns data
    parseOutputValues: function parseOutputValues(values) {
      var _this2 = this;
      return values.map(function (value, index) {
        // save undefined value
        if (!value) return value;
        value = JSON.parse(JSON.stringify(value));
        if (!value.code || value.name === _this2.columnsPlaceholder[index]) {
          value.code = '';
          value.name = '';
        }
        return value;
      });
    },
    onChange: function onChange(picker, values, index) {
      this.code = values[index].code;
      this.setValues();
      var parsedValues = this.parseOutputValues(picker.getValues());
      this.$emit('change', picker, parsedValues, index);
    },
    onConfirm: function onConfirm(values, index) {
      values = this.parseOutputValues(values);
      this.setValues();
      this.$emit('confirm', values, index);
    },
    getDefaultCode: function getDefaultCode() {
      if (this.columnsPlaceholder.length) {
        return PLACEHOLDER_CODE;
      }
      var countyCodes = Object.keys(this.county);
      if (countyCodes[0]) {
        return countyCodes[0];
      }
      var cityCodes = Object.keys(this.city);
      if (cityCodes[0]) {
        return cityCodes[0];
      }
      return '';
    },
    setValues: function setValues() {
      var code = this.code;
      if (!code) {
        code = this.getDefaultCode();
      }
      var picker = this.$refs.picker;
      var province = this.getList('province');
      var city = this.getList('city', code.slice(0, 2));
      if (!picker) {
        return;
      }
      picker.setColumnValues(0, province);
      picker.setColumnValues(1, city);
      if (city.length && code.slice(2, 4) === '00' && !this.isOverseaCode(code)) {
        code = city[0].code;
      }
      picker.setColumnValues(2, this.getList('county', code.slice(0, 4)));
      picker.setIndexes([this.getIndex('province', code), this.getIndex('city', code), this.getIndex('county', code)]);
    },
    getValues: function getValues() {
      var picker = this.$refs.picker;
      var getValues = picker ? picker.getValues().filter(function (value) {
        return !!value;
      }) : [];
      getValues = this.parseOutputValues(getValues);
      return getValues;
    },
    getArea: function getArea() {
      var values = this.getValues();
      var area = {
        code: '',
        country: '',
        province: '',
        city: '',
        county: ''
      };
      if (!values.length) {
        return area;
      }
      var names = values.map(function (item) {
        return item.name;
      });
      var validValues = values.filter(function (value) {
        return !!value.code;
      });
      area.code = validValues.length ? validValues[validValues.length - 1].code : '';
      if (this.isOverseaCode(area.code)) {
        area.country = names[1] || '';
        area.province = names[2] || '';
      } else {
        area.province = names[0] || '';
        area.city = names[1] || '';
        area.county = names[2] || '';
      }
      return area;
    },
    // @exposed-api
    reset: function reset(code) {
      this.code = code || '';
      this.setValues();
    }
  },
  render: function render() {
    var h = arguments[0];
    var on = _extends$1({}, this.$listeners, {
      change: this.onChange,
      confirm: this.onConfirm
    });
    return h(Picker, {
      "ref": "picker",
      "class": bem$1p(),
      "attrs": {
        "showToolbar": true,
        "valueKey": "name",
        "title": this.title,
        "columns": this.displayColumns,
        "loading": this.loading,
        "readonly": this.readonly,
        "itemHeight": this.itemHeight,
        "swipeDuration": this.swipeDuration,
        "visibleItemCount": this.visibleItemCount,
        "cancelButtonText": this.cancelButtonText,
        "confirmButtonText": this.confirmButtonText
      },
      "scopedSlots": pickSlots(this, ['title', 'columns-top', 'columns-bottom']),
      "on": _extends$1({}, on)
    });
  }
});

/**
 * Vue Router support
 */
function isRedundantNavigation(err) {
  return err.name === 'NavigationDuplicated' ||
  // compatible with vue-router@3.3
  err.message && err.message.indexOf('redundant navigation') !== -1;
}
function route(router, config) {
  var to = config.to,
    url = config.url,
    replace = config.replace;
  if (to && router) {
    var promise = router[replace ? 'replace' : 'push'](to);
    /* istanbul ignore else */

    if (promise && promise.catch) {
      promise.catch(function (err) {
        if (err && !isRedundantNavigation(err)) {
          throw err;
        }
      });
    }
  } else if (url) {
    replace ? location.replace(url) : location.href = url;
  }
}
function functionalRoute(context) {
  route(context.parent && context.parent.$router, context.props);
}
var routeProps = {
  url: String,
  replace: Boolean,
  to: [String, Object]
};

var cellProps = {
  icon: String,
  size: String,
  center: Boolean,
  isLink: Boolean,
  required: Boolean,
  iconPrefix: String,
  titleStyle: null,
  titleClass: null,
  valueClass: null,
  labelClass: null,
  title: [Number, String],
  value: [Number, String],
  label: [Number, String],
  arrowDirection: String,
  border: {
    type: Boolean,
    default: true
  },
  clickable: {
    type: Boolean,
    default: null
  }
};

var _createNamespace$1t = createNamespace('cell'),
  createComponent$1x = _createNamespace$1t[0],
  bem$1o = _createNamespace$1t[1];
function Cell(h, props, slots, ctx) {
  var _props$clickable;
  var icon = props.icon,
    size = props.size,
    title = props.title,
    label = props.label,
    value = props.value,
    isLink = props.isLink;
  var showTitle = slots.title || isDef(title);
  function Label() {
    var showLabel = slots.label || isDef(label);
    if (showLabel) {
      return h("div", {
        "class": [bem$1o('label'), props.labelClass]
      }, [slots.label ? slots.label() : label]);
    }
  }
  function Title() {
    if (showTitle) {
      return h("div", {
        "class": [bem$1o('title'), props.titleClass],
        "style": props.titleStyle
      }, [slots.title ? slots.title() : h("span", [title]), Label()]);
    }
  }
  function Value() {
    var showValue = slots.default || isDef(value);
    if (showValue) {
      return h("div", {
        "class": [bem$1o('value', {
          alone: !showTitle
        }), props.valueClass]
      }, [slots.default ? slots.default() : h("span", [value])]);
    }
  }
  function LeftIcon() {
    if (slots.icon) {
      return slots.icon();
    }
    if (icon) {
      return h(Icon$3, {
        "class": bem$1o('left-icon'),
        "attrs": {
          "name": icon,
          "classPrefix": props.iconPrefix
        }
      });
    }
  }
  function RightIcon() {
    var rightIconSlot = slots['right-icon'];
    if (rightIconSlot) {
      return rightIconSlot();
    }
    if (isLink) {
      var arrowDirection = props.arrowDirection;
      return h(Icon$3, {
        "class": bem$1o('right-icon'),
        "attrs": {
          "name": arrowDirection ? "arrow-" + arrowDirection : 'arrow'
        }
      });
    }
  }
  function onClick(event) {
    emit(ctx, 'click', event);
    functionalRoute(ctx);
  }
  var clickable = (_props$clickable = props.clickable) != null ? _props$clickable : isLink;
  var classes = {
    clickable: clickable,
    center: props.center,
    required: props.required,
    borderless: !props.border
  };
  if (size) {
    classes[size] = size;
  }
  return h("div", _mergeJSXProps([{
    "class": bem$1o(classes),
    "attrs": {
      "role": clickable ? 'button' : null,
      "tabindex": clickable ? 0 : null
    },
    "on": {
      "click": onClick
    }
  }, inherit(ctx)]), [LeftIcon(), Title(), Value(), RightIcon(), slots.extra == null ? void 0 : slots.extra()]);
}
Cell.props = _extends$1({}, cellProps, routeProps);
var Cell$1 = createComponent$1x(Cell);

function isAndroid() {
  /* istanbul ignore next */
  return isServer$1 ? false : /android/.test(navigator.userAgent.toLowerCase());
}
function isIOS$1() {
  /* istanbul ignore next */
  return isServer$1 ? false : /ios|iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
}

/**
 * Hack for iOS12 page scroll
 * https://developers.weixin.qq.com/community/develop/doc/00044ae90742f8c82fb78fcae56800
 */
var isIOS = isIOS$1();
/* istanbul ignore next */

function resetScroll() {
  if (isIOS) {
    setRootScrollTop(getRootScrollTop());
  }
}

var _createNamespace$1s = createNamespace('field'),
  createComponent$1w = _createNamespace$1s[0],
  bem$1n = _createNamespace$1s[1];
var Field = createComponent$1w({
  inheritAttrs: false,
  provide: function provide() {
    return {
      vanField: this
    };
  },
  inject: {
    vanForm: {
      default: null
    }
  },
  props: _extends$1({}, cellProps, {
    name: String,
    rules: Array,
    disabled: {
      type: Boolean,
      default: null
    },
    readonly: {
      type: Boolean,
      default: null
    },
    autosize: [Boolean, Object],
    leftIcon: String,
    rightIcon: String,
    clearable: Boolean,
    formatter: Function,
    maxlength: [Number, String],
    labelWidth: [Number, String],
    labelClass: null,
    labelAlign: String,
    inputAlign: String,
    placeholder: String,
    errorMessage: String,
    errorMessageAlign: String,
    showWordLimit: Boolean,
    value: {
      type: [Number, String],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    error: {
      type: Boolean,
      default: null
    },
    colon: {
      type: Boolean,
      default: null
    },
    clearTrigger: {
      type: String,
      default: 'focus'
    },
    formatTrigger: {
      type: String,
      default: 'onChange'
    }
  }),
  data: function data() {
    return {
      focused: false,
      validateFailed: false,
      validateMessage: ''
    };
  },
  watch: {
    value: function value() {
      this.updateValue(this.value);
      this.resetValidation();
      this.validateWithTrigger('onChange');
      this.$nextTick(this.adjustSize);
    }
  },
  mounted: function mounted() {
    this.updateValue(this.value, this.formatTrigger);
    this.$nextTick(this.adjustSize);
    if (this.vanForm) {
      this.vanForm.addField(this);
    }
  },
  beforeDestroy: function beforeDestroy() {
    if (this.vanForm) {
      this.vanForm.removeField(this);
    }
  },
  computed: {
    showClear: function showClear() {
      var readonly = this.getProp('readonly');
      if (this.clearable && !readonly) {
        var hasValue = isDef(this.value) && this.value !== '';
        var trigger = this.clearTrigger === 'always' || this.clearTrigger === 'focus' && this.focused;
        return hasValue && trigger;
      }
    },
    showError: function showError() {
      if (this.error !== null) {
        return this.error;
      }
      if (this.vanForm && this.vanForm.showError && this.validateFailed) {
        return true;
      }
    },
    listeners: function listeners() {
      return _extends$1({}, this.$listeners, {
        blur: this.onBlur,
        focus: this.onFocus,
        input: this.onInput,
        click: this.onClickInput,
        keypress: this.onKeypress
      });
    },
    labelStyle: function labelStyle() {
      var labelWidth = this.getProp('labelWidth');
      if (labelWidth) {
        return {
          width: addUnit(labelWidth)
        };
      }
    },
    formValue: function formValue() {
      if (this.children && (this.$scopedSlots.input || this.$slots.input)) {
        return this.children.value;
      }
      return this.value;
    }
  },
  methods: {
    // @exposed-api
    focus: function focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    // @exposed-api
    blur: function blur() {
      if (this.$refs.input) {
        this.$refs.input.blur();
      }
    },
    runValidator: function runValidator(value, rule) {
      return new Promise(function (resolve) {
        var returnVal = rule.validator(value, rule);
        if (isPromise$1(returnVal)) {
          return returnVal.then(resolve);
        }
        resolve(returnVal);
      });
    },
    isEmptyValue: function isEmptyValue(value) {
      if (Array.isArray(value)) {
        return !value.length;
      }
      if (value === 0) {
        return false;
      }
      return !value;
    },
    runSyncRule: function runSyncRule(value, rule) {
      if (rule.required && this.isEmptyValue(value)) {
        return false;
      }
      if (rule.pattern && !rule.pattern.test(value)) {
        return false;
      }
      return true;
    },
    getRuleMessage: function getRuleMessage(value, rule) {
      var message = rule.message;
      if (isFunction$2(message)) {
        return message(value, rule);
      }
      return message;
    },
    runRules: function runRules(rules) {
      var _this = this;
      return rules.reduce(function (promise, rule) {
        return promise.then(function () {
          if (_this.validateFailed) {
            return;
          }
          var value = _this.formValue;
          if (rule.formatter) {
            value = rule.formatter(value, rule);
          }
          if (!_this.runSyncRule(value, rule)) {
            _this.validateFailed = true;
            _this.validateMessage = _this.getRuleMessage(value, rule);
            return;
          }
          if (rule.validator) {
            return _this.runValidator(value, rule).then(function (result) {
              if (result === false) {
                _this.validateFailed = true;
                _this.validateMessage = _this.getRuleMessage(value, rule);
              }
            });
          }
        });
      }, Promise.resolve());
    },
    validate: function validate(rules) {
      var _this2 = this;
      if (rules === void 0) {
        rules = this.rules;
      }
      return new Promise(function (resolve) {
        if (!rules) {
          resolve();
        }
        _this2.resetValidation();
        _this2.runRules(rules).then(function () {
          if (_this2.validateFailed) {
            resolve({
              name: _this2.name,
              message: _this2.validateMessage
            });
          } else {
            resolve();
          }
        });
      });
    },
    validateWithTrigger: function validateWithTrigger(trigger) {
      if (this.vanForm && this.rules) {
        var defaultTrigger = this.vanForm.validateTrigger === trigger;
        var rules = this.rules.filter(function (rule) {
          if (rule.trigger) {
            return rule.trigger === trigger;
          }
          return defaultTrigger;
        });
        if (rules.length) {
          this.validate(rules);
        }
      }
    },
    resetValidation: function resetValidation() {
      if (this.validateFailed) {
        this.validateFailed = false;
        this.validateMessage = '';
      }
    },
    updateValue: function updateValue(value, trigger) {
      if (trigger === void 0) {
        trigger = 'onChange';
      }
      value = isDef(value) ? String(value) : ''; // native maxlength have incorrect line-break counting
      // see: https://github.com/vant-ui/vant/issues/5033

      var maxlength = this.maxlength;
      if (isDef(maxlength) && value.length > maxlength) {
        if (this.value && this.value.length === +maxlength) {
          value = this.value;
        } else {
          value = value.slice(0, maxlength);
        }
      }
      if (this.type === 'number' || this.type === 'digit') {
        var isNumber = this.type === 'number';
        value = formatNumber(value, isNumber, isNumber);
      }
      if (this.formatter && trigger === this.formatTrigger) {
        value = this.formatter(value);
      }
      var input = this.$refs.input;
      if (input && value !== input.value) {
        input.value = value;
      }
      if (value !== this.value) {
        this.$emit('input', value);
      }
    },
    onInput: function onInput(event) {
      // not update v-model when composing
      if (event.target.composing) {
        return;
      }
      this.updateValue(event.target.value);
    },
    onFocus: function onFocus(event) {
      this.focused = true;
      this.$emit('focus', event); // https://github.com/vant-ui/vant/issues/9715

      this.$nextTick(this.adjustSize); // readonly not work in legacy mobile safari

      /* istanbul ignore if */

      if (this.getProp('readonly')) {
        this.blur();
      }
    },
    onBlur: function onBlur(event) {
      if (this.getProp('readonly')) {
        return;
      }
      this.focused = false;
      this.updateValue(this.value, 'onBlur');
      this.$emit('blur', event);
      this.validateWithTrigger('onBlur');
      this.$nextTick(this.adjustSize);
      resetScroll();
    },
    onClick: function onClick(event) {
      this.$emit('click', event);
    },
    onClickInput: function onClickInput(event) {
      this.$emit('click-input', event);
    },
    onClickLeftIcon: function onClickLeftIcon(event) {
      this.$emit('click-left-icon', event);
    },
    onClickRightIcon: function onClickRightIcon(event) {
      this.$emit('click-right-icon', event);
    },
    onClear: function onClear(event) {
      preventDefault(event);
      this.$emit('input', '');
      this.$emit('clear', event);
    },
    onKeypress: function onKeypress(event) {
      var ENTER_CODE = 13;
      if (event.keyCode === ENTER_CODE) {
        var submitOnEnter = this.getProp('submitOnEnter');
        if (!submitOnEnter && this.type !== 'textarea') {
          preventDefault(event);
        } // trigger blur after click keyboard search button

        if (this.type === 'search') {
          this.blur();
        }
      }
      this.$emit('keypress', event);
    },
    adjustSize: function adjustSize() {
      var input = this.$refs.input;
      if (!(this.type === 'textarea' && this.autosize) || !input) {
        return;
      }
      var scrollTop = getRootScrollTop();
      input.style.height = 'auto';
      var height = input.scrollHeight;
      if (isObject$2(this.autosize)) {
        var _this$autosize = this.autosize,
          maxHeight = _this$autosize.maxHeight,
          minHeight = _this$autosize.minHeight;
        if (maxHeight) {
          height = Math.min(height, maxHeight);
        }
        if (minHeight) {
          height = Math.max(height, minHeight);
        }
      }
      if (height) {
        input.style.height = height + 'px'; // https://github.com/vant-ui/vant/issues/9178

        setRootScrollTop(scrollTop);
      }
    },
    genInput: function genInput() {
      var h = this.$createElement;
      var type = this.type;
      var disabled = this.getProp('disabled');
      var readonly = this.getProp('readonly');
      var inputSlot = this.slots('input');
      var inputAlign = this.getProp('inputAlign');
      if (inputSlot) {
        return h("div", {
          "class": bem$1n('control', [inputAlign, 'custom']),
          "on": {
            "click": this.onClickInput
          }
        }, [inputSlot]);
      }
      var inputProps = {
        ref: 'input',
        class: bem$1n('control', inputAlign),
        domProps: {
          value: this.value
        },
        attrs: _extends$1({}, this.$attrs, {
          name: this.name,
          disabled: disabled,
          readonly: readonly,
          placeholder: this.placeholder
        }),
        on: this.listeners,
        // add model directive to skip IME composition
        directives: [{
          name: 'model',
          value: this.value
        }]
      };
      if (type === 'textarea') {
        return h("textarea", _mergeJSXProps([{}, inputProps]));
      }
      var inputType = type;
      var inputMode; // type="number" is weird in iOS, and can't prevent dot in Android
      // so use inputmode to set keyboard in modern browsers

      if (type === 'number') {
        inputType = 'text';
        inputMode = 'decimal';
      }
      if (type === 'digit') {
        inputType = 'tel';
        inputMode = 'numeric';
      }
      return h("input", _mergeJSXProps([{
        "attrs": {
          "type": inputType,
          "inputmode": inputMode
        }
      }, inputProps]));
    },
    genLeftIcon: function genLeftIcon() {
      var h = this.$createElement;
      var showLeftIcon = this.slots('left-icon') || this.leftIcon;
      if (showLeftIcon) {
        return h("div", {
          "class": bem$1n('left-icon'),
          "on": {
            "click": this.onClickLeftIcon
          }
        }, [this.slots('left-icon') || h(Icon$3, {
          "attrs": {
            "name": this.leftIcon,
            "classPrefix": this.iconPrefix
          }
        })]);
      }
    },
    genRightIcon: function genRightIcon() {
      var h = this.$createElement;
      var slots = this.slots;
      var showRightIcon = slots('right-icon') || this.rightIcon;
      if (showRightIcon) {
        return h("div", {
          "class": bem$1n('right-icon'),
          "on": {
            "click": this.onClickRightIcon
          }
        }, [slots('right-icon') || h(Icon$3, {
          "attrs": {
            "name": this.rightIcon,
            "classPrefix": this.iconPrefix
          }
        })]);
      }
    },
    genWordLimit: function genWordLimit() {
      var h = this.$createElement;
      if (this.showWordLimit && this.maxlength) {
        var count = (this.value || '').length;
        return h("div", {
          "class": bem$1n('word-limit')
        }, [h("span", {
          "class": bem$1n('word-num')
        }, [count]), "/", this.maxlength]);
      }
    },
    genMessage: function genMessage() {
      var h = this.$createElement;
      if (this.vanForm && this.vanForm.showErrorMessage === false) {
        return;
      }
      var message = this.errorMessage || this.validateMessage;
      if (message) {
        var errorMessageAlign = this.getProp('errorMessageAlign');
        return h("div", {
          "class": bem$1n('error-message', errorMessageAlign)
        }, [message]);
      }
    },
    getProp: function getProp(key) {
      if (isDef(this[key])) {
        return this[key];
      }
      if (this.vanForm && isDef(this.vanForm[key])) {
        return this.vanForm[key];
      }
    },
    genLabel: function genLabel() {
      var h = this.$createElement;
      var colon = this.getProp('colon') ? ':' : '';
      if (this.slots('label')) {
        return [this.slots('label'), colon];
      }
      if (this.label) {
        return h("span", [this.label + colon]);
      }
    }
  },
  render: function render() {
    var _bem;
    var h = arguments[0];
    var slots = this.slots;
    var disabled = this.getProp('disabled');
    var labelAlign = this.getProp('labelAlign');
    var scopedSlots = {
      icon: this.genLeftIcon
    };
    var Label = this.genLabel();
    if (Label) {
      scopedSlots.title = function () {
        return Label;
      };
    }
    var extra = this.slots('extra');
    if (extra) {
      scopedSlots.extra = function () {
        return extra;
      };
    }
    return h(Cell$1, {
      "attrs": {
        "icon": this.leftIcon,
        "size": this.size,
        "center": this.center,
        "border": this.border,
        "isLink": this.isLink,
        "required": this.required,
        "clickable": this.clickable,
        "titleStyle": this.labelStyle,
        "valueClass": bem$1n('value'),
        "titleClass": [bem$1n('label', labelAlign), this.labelClass],
        "arrowDirection": this.arrowDirection
      },
      "scopedSlots": scopedSlots,
      "class": bem$1n((_bem = {
        error: this.showError,
        disabled: disabled
      }, _bem["label-" + labelAlign] = labelAlign, _bem['min-height'] = this.type === 'textarea' && !this.autosize, _bem)),
      "on": {
        "click": this.onClick
      }
    }, [h("div", {
      "class": bem$1n('body')
    }, [this.genInput(), this.showClear && h(Icon$3, {
      "attrs": {
        "name": "clear"
      },
      "class": bem$1n('clear'),
      "on": {
        "touchstart": this.onClear
      }
    }), this.genRightIcon(), slots('button') && h("div", {
      "class": bem$1n('button')
    }, [slots('button')])]), this.genWordLimit(), this.genMessage()]);
  }
});

var lockCount = 0;
function lockClick(lock) {
  if (lock) {
    if (!lockCount) {
      document.body.classList.add('van-toast--unclickable');
    }
    lockCount++;
  } else {
    lockCount--;
    if (!lockCount) {
      document.body.classList.remove('van-toast--unclickable');
    }
  }
}

// Utils
var _createNamespace$1r = createNamespace('toast'),
  createComponent$1v = _createNamespace$1r[0],
  bem$1m = _createNamespace$1r[1];
var VueToast = createComponent$1v({
  mixins: [PopupMixin()],
  props: {
    icon: String,
    className: null,
    iconPrefix: String,
    loadingType: String,
    forbidClick: Boolean,
    closeOnClick: Boolean,
    message: [Number, String],
    type: {
      type: String,
      default: 'text'
    },
    position: {
      type: String,
      default: 'middle'
    },
    transition: {
      type: String,
      default: 'van-fade'
    },
    lockScroll: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      clickable: false
    };
  },
  mounted: function mounted() {
    this.toggleClickable();
  },
  destroyed: function destroyed() {
    this.toggleClickable();
  },
  watch: {
    value: 'toggleClickable',
    forbidClick: 'toggleClickable'
  },
  methods: {
    onClick: function onClick() {
      if (this.closeOnClick) {
        this.close();
      }
    },
    toggleClickable: function toggleClickable() {
      var clickable = this.value && this.forbidClick;
      if (this.clickable !== clickable) {
        this.clickable = clickable;
        lockClick(clickable);
      }
    },
    /* istanbul ignore next */
    onAfterEnter: function onAfterEnter() {
      this.$emit('opened');
      if (this.onOpened) {
        this.onOpened();
      }
    },
    onAfterLeave: function onAfterLeave() {
      this.$emit('closed');
    },
    genIcon: function genIcon() {
      var h = this.$createElement;
      var icon = this.icon,
        type = this.type,
        iconPrefix = this.iconPrefix,
        loadingType = this.loadingType;
      var hasIcon = icon || type === 'success' || type === 'fail';
      if (hasIcon) {
        return h(Icon$3, {
          "class": bem$1m('icon'),
          "attrs": {
            "classPrefix": iconPrefix,
            "name": icon || type
          }
        });
      }
      if (type === 'loading') {
        return h(Loading$1, {
          "class": bem$1m('loading'),
          "attrs": {
            "type": loadingType
          }
        });
      }
    },
    genMessage: function genMessage() {
      var h = this.$createElement;
      var type = this.type,
        message = this.message;
      if (!isDef(message) || message === '') {
        return;
      }
      if (type === 'html') {
        return h("div", {
          "class": bem$1m('text'),
          "domProps": {
            "innerHTML": message
          }
        });
      }
      return h("div", {
        "class": bem$1m('text')
      }, [message]);
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    return h("transition", {
      "attrs": {
        "name": this.transition
      },
      "on": {
        "afterEnter": this.onAfterEnter,
        "afterLeave": this.onAfterLeave
      }
    }, [h("div", {
      "directives": [{
        name: "show",
        value: this.value
      }],
      "class": [bem$1m([this.position, (_ref = {}, _ref[this.type] = !this.icon, _ref)]), this.className],
      "on": {
        "click": this.onClick
      }
    }, [this.genIcon(), this.genMessage()])]);
  }
});

var defaultOptions$1 = {
  icon: '',
  type: 'text',
  // @deprecated
  mask: false,
  value: true,
  message: '',
  className: '',
  overlay: false,
  onClose: null,
  onOpened: null,
  duration: 2000,
  iconPrefix: undefined,
  position: 'middle',
  transition: 'van-fade',
  forbidClick: false,
  loadingType: undefined,
  getContainer: 'body',
  overlayStyle: null,
  closeOnClick: false,
  closeOnClickOverlay: false
}; // default options of specific type

var defaultOptionsMap = {};
var queue = [];
var multiple = false;
var currentOptions = _extends$1({}, defaultOptions$1);
function parseOptions$2(message) {
  if (isObject$2(message)) {
    return message;
  }
  return {
    message: message
  };
}
function isInDocument$1(element) {
  return document.body.contains(element);
}
function createInstance() {
  /* istanbul ignore if */
  if (isServer$1) {
    return {};
  }
  queue = queue.filter(function (item) {
    return !item.$el.parentNode || isInDocument$1(item.$el);
  });
  if (!queue.length || multiple) {
    var toast = new (Vue.extend(VueToast))({
      el: document.createElement('div')
    });
    toast.$on('input', function (value) {
      toast.value = value;
    });
    queue.push(toast);
  }
  return queue[queue.length - 1];
} // transform toast options to popup props

function transformOptions(options) {
  return _extends$1({}, options, {
    overlay: options.mask || options.overlay,
    mask: undefined,
    duration: undefined
  });
}
function Toast(options) {
  if (options === void 0) {
    options = {};
  }
  var toast = createInstance(); // should add z-index if previous toast has not disappeared

  if (toast.value) {
    toast.updateZIndex();
  }
  options = parseOptions$2(options);
  options = _extends$1({}, currentOptions, defaultOptionsMap[options.type || currentOptions.type], options);
  options.clear = function () {
    toast.value = false;
    if (options.onClose) {
      options.onClose();
      options.onClose = null;
    }
    if (multiple && !isServer$1) {
      toast.$on('closed', function () {
        clearTimeout(toast.timer);
        queue = queue.filter(function (item) {
          return item !== toast;
        });
        removeNode(toast.$el);
        toast.$destroy();
      });
    }
  };
  _extends$1(toast, transformOptions(options));
  clearTimeout(toast.timer);
  if (options.duration > 0) {
    toast.timer = setTimeout(function () {
      toast.clear();
    }, options.duration);
  }
  return toast;
}
var createMethod = function createMethod(type) {
  return function (options) {
    return Toast(_extends$1({
      type: type
    }, parseOptions$2(options)));
  };
};
['loading', 'success', 'fail'].forEach(function (method) {
  Toast[method] = createMethod(method);
});
Toast.clear = function (all) {
  if (queue.length) {
    if (all) {
      queue.forEach(function (toast) {
        toast.clear();
      });
      queue = [];
    } else if (!multiple) {
      queue[0].clear();
    } else {
      queue.shift().clear();
    }
  }
};
Toast.setDefaultOptions = function (type, options) {
  if (typeof type === 'string') {
    defaultOptionsMap[type] = options;
  } else {
    _extends$1(currentOptions, type);
  }
};
Toast.resetDefaultOptions = function (type) {
  if (typeof type === 'string') {
    defaultOptionsMap[type] = null;
  } else {
    currentOptions = _extends$1({}, defaultOptions$1);
    defaultOptionsMap = {};
  }
};
Toast.allowMultiple = function (value) {
  if (value === void 0) {
    value = true;
  }
  multiple = value;
};
Toast.install = function () {
  Vue.use(VueToast);
};
Vue.prototype.$toast = Toast;

var _createNamespace$1q = createNamespace('button'),
  createComponent$1u = _createNamespace$1q[0],
  bem$1l = _createNamespace$1q[1];
function Button$1(h, props, slots, ctx) {
  var _ref;
  var tag = props.tag,
    icon = props.icon,
    type = props.type,
    color = props.color,
    plain = props.plain,
    disabled = props.disabled,
    loading = props.loading,
    hairline = props.hairline,
    loadingText = props.loadingText,
    iconPosition = props.iconPosition;
  var style = {};
  if (color) {
    style.color = plain ? color : 'white';
    if (!plain) {
      // Use background instead of backgroundColor to make linear-gradient work
      style.background = color;
    } // hide border when color is linear-gradient

    if (color.indexOf('gradient') !== -1) {
      style.border = 0;
    } else {
      style.borderColor = color;
    }
  }
  function onClick(event) {
    if (props.loading) {
      event.preventDefault();
    }
    if (!loading && !disabled) {
      emit(ctx, 'click', event);
      functionalRoute(ctx);
    }
  }
  function onTouchstart(event) {
    emit(ctx, 'touchstart', event);
  }
  var classes = [bem$1l([type, props.size, {
    plain: plain,
    loading: loading,
    disabled: disabled,
    hairline: hairline,
    block: props.block,
    round: props.round,
    square: props.square
  }]), (_ref = {}, _ref[BORDER_SURROUND] = hairline, _ref)];
  function renderIcon() {
    if (loading) {
      return slots.loading ? slots.loading() : h(Loading$1, {
        "class": bem$1l('loading'),
        "attrs": {
          "size": props.loadingSize,
          "type": props.loadingType,
          "color": "currentColor"
        }
      });
    }
    if (slots.icon) {
      return h("div", {
        "class": bem$1l('icon')
      }, [slots.icon()]);
    }
    if (icon) {
      return h(Icon$3, {
        "attrs": {
          "name": icon,
          "classPrefix": props.iconPrefix
        },
        "class": bem$1l('icon')
      });
    }
  }
  function renderContent() {
    var content = [];
    if (iconPosition === 'left') {
      content.push(renderIcon());
    }
    var text;
    if (loading) {
      text = loadingText;
    } else {
      text = slots.default ? slots.default() : props.text;
    }
    if (text) {
      content.push(h("span", {
        "class": bem$1l('text')
      }, [text]));
    }
    if (iconPosition === 'right') {
      content.push(renderIcon());
    }
    return content;
  }
  return h(tag, _mergeJSXProps([{
    "style": style,
    "class": classes,
    "attrs": {
      "type": props.nativeType,
      "disabled": disabled
    },
    "on": {
      "click": onClick,
      "touchstart": onTouchstart
    }
  }, inherit(ctx)]), [h("div", {
    "class": bem$1l('content')
  }, [renderContent()])]);
}
Button$1.props = _extends$1({}, routeProps, {
  text: String,
  icon: String,
  color: String,
  block: Boolean,
  plain: Boolean,
  round: Boolean,
  square: Boolean,
  loading: Boolean,
  hairline: Boolean,
  disabled: Boolean,
  iconPrefix: String,
  nativeType: String,
  loadingText: String,
  loadingType: String,
  tag: {
    type: String,
    default: 'button'
  },
  type: {
    type: String,
    default: 'default'
  },
  size: {
    type: String,
    default: 'normal'
  },
  loadingSize: {
    type: String,
    default: '20px'
  },
  iconPosition: {
    type: String,
    default: 'left'
  }
});
var Button$2 = createComponent$1u(Button$1);

function flattenVNodes(vnodes) {
  var result = [];
  function traverse(vnodes) {
    vnodes.forEach(function (vnode) {
      result.push(vnode);
      if (vnode.componentInstance) {
        traverse(vnode.componentInstance.$children.map(function (item) {
          return item.$vnode;
        }));
      }
      if (vnode.children) {
        traverse(vnode.children);
      }
    });
  }
  traverse(vnodes);
  return result;
} // sort children instances by vnodes order

function sortChildren(children, parent) {
  var componentOptions = parent.$vnode.componentOptions;
  if (!componentOptions || !componentOptions.children) {
    return;
  }
  var vnodes = flattenVNodes(componentOptions.children);
  children.sort(function (a, b) {
    return vnodes.indexOf(a.$vnode) - vnodes.indexOf(b.$vnode);
  });
}

function ChildrenMixin(_parent, options) {
  var _inject, _computed;
  if (options === void 0) {
    options = {};
  }
  var indexKey = options.indexKey || 'index';
  return {
    inject: (_inject = {}, _inject[_parent] = {
      default: null
    }, _inject),
    computed: (_computed = {
      parent: function parent() {
        if (this.disableBindRelation) {
          return null;
        }
        return this[_parent];
      }
    }, _computed[indexKey] = function () {
      this.bindRelation();
      if (this.parent) {
        return this.parent.children.indexOf(this);
      }
      return null;
    }, _computed),
    watch: {
      disableBindRelation: function disableBindRelation(val) {
        if (!val) {
          this.bindRelation();
        }
      }
    },
    mounted: function mounted() {
      this.bindRelation();
    },
    beforeDestroy: function beforeDestroy() {
      var _this = this;
      if (this.parent) {
        this.parent.children = this.parent.children.filter(function (item) {
          return item !== _this;
        });
      }
    },
    methods: {
      bindRelation: function bindRelation() {
        if (!this.parent || this.parent.children.indexOf(this) !== -1) {
          return;
        }
        var children = [].concat(this.parent.children, [this]);
        sortChildren(children, this.parent);
        this.parent.children = children;
      }
    }
  };
}
function ParentMixin(parent) {
  return {
    provide: function provide() {
      var _ref;
      return _ref = {}, _ref[parent] = this, _ref;
    },
    data: function data() {
      return {
        children: []
      };
    }
  };
}

var _createNamespace$1p = createNamespace('goods-action'),
  createComponent$1t = _createNamespace$1p[0],
  bem$1k = _createNamespace$1p[1];
var GoodsAction = createComponent$1t({
  mixins: [ParentMixin('vanGoodsAction')],
  props: {
    safeAreaInsetBottom: {
      type: Boolean,
      default: true
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$1k({
        unfit: !this.safeAreaInsetBottom
      })
    }, [this.slots()]);
  }
});

var _createNamespace$1o = createNamespace('goods-action-button'),
  createComponent$1s = _createNamespace$1o[0],
  bem$1j = _createNamespace$1o[1];
var GoodsActionButton = createComponent$1s({
  mixins: [ChildrenMixin('vanGoodsAction')],
  props: _extends$1({}, routeProps, {
    type: String,
    text: String,
    icon: String,
    color: String,
    loading: Boolean,
    disabled: Boolean
  }),
  computed: {
    isFirst: function isFirst() {
      var prev = this.parent && this.parent.children[this.index - 1];
      return !prev || prev.$options.name !== this.$options.name;
    },
    isLast: function isLast() {
      var next = this.parent && this.parent.children[this.index + 1];
      return !next || next.$options.name !== this.$options.name;
    }
  },
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
      route(this.$router, this);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h(Button$2, {
      "class": bem$1j([{
        first: this.isFirst,
        last: this.isLast
      }, this.type]),
      "attrs": {
        "size": "large",
        "type": this.type,
        "icon": this.icon,
        "color": this.color,
        "loading": this.loading,
        "disabled": this.disabled
      },
      "on": {
        "click": this.onClick
      }
    }, [this.slots() || this.text]);
  }
});

var _createNamespace$1n = createNamespace('dialog'),
  createComponent$1r = _createNamespace$1n[0],
  bem$1i = _createNamespace$1n[1],
  t$q = _createNamespace$1n[2];
var VanDialog = createComponent$1r({
  mixins: [PopupMixin()],
  props: {
    title: String,
    theme: String,
    width: [Number, String],
    message: String,
    className: null,
    callback: Function,
    beforeClose: Function,
    messageAlign: String,
    cancelButtonText: String,
    cancelButtonColor: String,
    confirmButtonText: String,
    confirmButtonColor: String,
    showCancelButton: Boolean,
    overlay: {
      type: Boolean,
      default: true
    },
    allowHtml: {
      type: Boolean,
      default: true
    },
    transition: {
      type: String,
      default: 'van-dialog-bounce'
    },
    showConfirmButton: {
      type: Boolean,
      default: true
    },
    closeOnPopstate: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      loading: {
        confirm: false,
        cancel: false
      }
    };
  },
  methods: {
    onClickOverlay: function onClickOverlay() {
      this.handleAction('overlay');
    },
    handleAction: function handleAction(action) {
      var _this = this;
      this.$emit(action); // show not trigger close event when hidden

      if (!this.value) {
        return;
      }
      if (this.beforeClose) {
        this.loading[action] = true;
        this.beforeClose(action, function (state) {
          if (state !== false && _this.loading[action]) {
            _this.onClose(action);
          }
          _this.loading.confirm = false;
          _this.loading.cancel = false;
        });
      } else {
        this.onClose(action);
      }
    },
    onClose: function onClose(action) {
      this.close();
      if (this.callback) {
        this.callback(action);
      }
    },
    onOpened: function onOpened() {
      var _this2 = this;
      this.$emit('opened');
      this.$nextTick(function () {
        var _this2$$refs$dialog;
        (_this2$$refs$dialog = _this2.$refs.dialog) == null ? void 0 : _this2$$refs$dialog.focus();
      });
    },
    onClosed: function onClosed() {
      this.$emit('closed');
    },
    onKeydown: function onKeydown(event) {
      var _this3 = this;
      if (event.key === 'Escape' || event.key === 'Enter') {
        // skip keyboard events of child elements
        if (event.target !== this.$refs.dialog) {
          return;
        }
        var onEventType = {
          Enter: this.showConfirmButton ? function () {
            return _this3.handleAction('confirm');
          } : noop$2,
          Escape: this.showCancelButton ? function () {
            return _this3.handleAction('cancel');
          } : noop$2
        };
        onEventType[event.key]();
        this.$emit('keydown', event);
      }
    },
    genRoundButtons: function genRoundButtons() {
      var _this4 = this;
      var h = this.$createElement;
      return h(GoodsAction, {
        "class": bem$1i('footer')
      }, [this.showCancelButton && h(GoodsActionButton, {
        "attrs": {
          "size": "large",
          "type": "warning",
          "text": this.cancelButtonText || t$q('cancel'),
          "color": this.cancelButtonColor,
          "loading": this.loading.cancel
        },
        "class": bem$1i('cancel'),
        "on": {
          "click": function click() {
            _this4.handleAction('cancel');
          }
        }
      }), this.showConfirmButton && h(GoodsActionButton, {
        "attrs": {
          "size": "large",
          "type": "danger",
          "text": this.confirmButtonText || t$q('confirm'),
          "color": this.confirmButtonColor,
          "loading": this.loading.confirm
        },
        "class": bem$1i('confirm'),
        "on": {
          "click": function click() {
            _this4.handleAction('confirm');
          }
        }
      })]);
    },
    genButtons: function genButtons() {
      var _this5 = this,
        _ref;
      var h = this.$createElement;
      var multiple = this.showCancelButton && this.showConfirmButton;
      return h("div", {
        "class": [BORDER_TOP, bem$1i('footer')]
      }, [this.showCancelButton && h(Button$2, {
        "attrs": {
          "size": "large",
          "loading": this.loading.cancel,
          "text": this.cancelButtonText || t$q('cancel'),
          "nativeType": "button"
        },
        "class": bem$1i('cancel'),
        "style": {
          color: this.cancelButtonColor
        },
        "on": {
          "click": function click() {
            _this5.handleAction('cancel');
          }
        }
      }), this.showConfirmButton && h(Button$2, {
        "attrs": {
          "size": "large",
          "loading": this.loading.confirm,
          "text": this.confirmButtonText || t$q('confirm'),
          "nativeType": "button"
        },
        "class": [bem$1i('confirm'), (_ref = {}, _ref[BORDER_LEFT] = multiple, _ref)],
        "style": {
          color: this.confirmButtonColor
        },
        "on": {
          "click": function click() {
            _this5.handleAction('confirm');
          }
        }
      })]);
    },
    genContent: function genContent(hasTitle, messageSlot) {
      var h = this.$createElement;
      if (messageSlot) {
        return h("div", {
          "class": bem$1i('content')
        }, [messageSlot]);
      }
      var message = this.message,
        messageAlign = this.messageAlign;
      if (message) {
        var _bem, _domProps;
        var data = {
          class: bem$1i('message', (_bem = {
            'has-title': hasTitle
          }, _bem[messageAlign] = messageAlign, _bem)),
          domProps: (_domProps = {}, _domProps[this.allowHtml ? 'innerHTML' : 'textContent'] = message, _domProps)
        };
        return h("div", {
          "class": bem$1i('content', {
            isolated: !hasTitle
          })
        }, [h("div", _mergeJSXProps([{}, data]))]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    if (!this.shouldRender) {
      return;
    }
    var message = this.message;
    var messageSlot = this.slots();
    var title = this.slots('title') || this.title;
    var Title = title && h("div", {
      "class": bem$1i('header', {
        isolated: !message && !messageSlot
      })
    }, [title]);
    return h("transition", {
      "attrs": {
        "name": this.transition
      },
      "on": {
        "afterEnter": this.onOpened,
        "afterLeave": this.onClosed
      }
    }, [h("div", {
      "directives": [{
        name: "show",
        value: this.value
      }],
      "attrs": {
        "role": "dialog",
        "aria-labelledby": this.title || message,
        "tabIndex": 0
      },
      "class": [bem$1i([this.theme]), this.className],
      "style": {
        width: addUnit(this.width)
      },
      "ref": "dialog",
      "on": {
        "keydown": this.onKeydown
      }
    }, [Title, this.genContent(title, messageSlot), this.theme === 'round-button' ? this.genRoundButtons() : this.genButtons()])]);
  }
});

var instance$4;
function isInDocument(element) {
  return document.body.contains(element);
}
function initInstance$1() {
  if (instance$4) {
    instance$4.$destroy();
  }
  instance$4 = new (Vue.extend(VanDialog))({
    el: document.createElement('div'),
    // avoid missing animation when first rendered
    propsData: {
      lazyRender: false
    }
  });
  instance$4.$on('input', function (value) {
    instance$4.value = value;
  });
}
function Dialog(options) {
  /* istanbul ignore if */
  if (isServer$1) {
    return Promise.resolve();
  }
  return new Promise(function (resolve, reject) {
    if (!instance$4 || !isInDocument(instance$4.$el)) {
      initInstance$1();
    }
    _extends$1(instance$4, Dialog.currentOptions, options, {
      resolve: resolve,
      reject: reject
    });
  });
}
Dialog.defaultOptions = {
  value: true,
  title: '',
  width: '',
  theme: null,
  message: '',
  overlay: true,
  className: '',
  allowHtml: true,
  lockScroll: true,
  transition: 'van-dialog-bounce',
  beforeClose: null,
  overlayClass: '',
  overlayStyle: null,
  messageAlign: '',
  getContainer: 'body',
  cancelButtonText: '',
  cancelButtonColor: null,
  confirmButtonText: '',
  confirmButtonColor: null,
  showConfirmButton: true,
  showCancelButton: false,
  closeOnPopstate: true,
  closeOnClickOverlay: false,
  callback: function callback(action) {
    instance$4[action === 'confirm' ? 'resolve' : 'reject'](action);
  }
};
Dialog.alert = Dialog;
Dialog.confirm = function (options) {
  return Dialog(_extends$1({
    showCancelButton: true
  }, options));
};
Dialog.close = function () {
  if (instance$4) {
    instance$4.value = false;
  }
};
Dialog.setDefaultOptions = function (options) {
  _extends$1(Dialog.currentOptions, options);
};
Dialog.resetDefaultOptions = function () {
  Dialog.currentOptions = _extends$1({}, Dialog.defaultOptions);
};
Dialog.resetDefaultOptions();
Dialog.install = function () {
  Vue.use(VanDialog);
};
Dialog.Component = VanDialog;
Vue.prototype.$dialog = Dialog;

var _createNamespace$1m = createNamespace('address-edit-detail'),
  createComponent$1q = _createNamespace$1m[0],
  bem$1h = _createNamespace$1m[1],
  t$p = _createNamespace$1m[2];
var android = isAndroid();
var Detail = createComponent$1q({
  props: {
    value: String,
    errorMessage: String,
    focused: Boolean,
    detailRows: [Number, String],
    searchResult: Array,
    detailMaxlength: [Number, String],
    showSearchResult: Boolean
  },
  computed: {
    shouldShowSearchResult: function shouldShowSearchResult() {
      return this.focused && this.searchResult && this.showSearchResult;
    }
  },
  methods: {
    onSelect: function onSelect(express) {
      this.$emit('select-search', express);
      this.$emit('input', ((express.address || '') + " " + (express.name || '')).trim());
    },
    onFinish: function onFinish() {
      this.$refs.field.blur();
    },
    genFinish: function genFinish() {
      var h = this.$createElement;
      var show = this.value && this.focused && android;
      if (show) {
        return h("div", {
          "class": bem$1h('finish'),
          "on": {
            "click": this.onFinish
          }
        }, [t$p('complete')]);
      }
    },
    genSearchResult: function genSearchResult() {
      var _this = this;
      var h = this.$createElement;
      var value = this.value,
        shouldShowSearchResult = this.shouldShowSearchResult,
        searchResult = this.searchResult;
      if (shouldShowSearchResult) {
        return searchResult.map(function (express) {
          return h(Cell$1, {
            "key": express.name + express.address,
            "attrs": {
              "clickable": true,
              "border": false,
              "icon": "location-o",
              "label": express.address
            },
            "class": bem$1h('search-item'),
            "on": {
              "click": function click() {
                _this.onSelect(express);
              }
            },
            "scopedSlots": {
              title: function title() {
                if (express.name) {
                  var text = express.name.replace(value, "<span class=" + bem$1h('keyword') + ">" + value + "</span>");
                  return h("div", {
                    "domProps": {
                      "innerHTML": text
                    }
                  });
                }
              }
            }
          });
        });
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h(Cell$1, {
      "class": bem$1h()
    }, [h(Field, {
      "attrs": {
        "autosize": true,
        "rows": this.detailRows,
        "clearable": !android,
        "type": "textarea",
        "value": this.value,
        "errorMessage": this.errorMessage,
        "border": !this.shouldShowSearchResult,
        "label": t$p('label'),
        "maxlength": this.detailMaxlength,
        "placeholder": t$p('placeholder')
      },
      "ref": "field",
      "scopedSlots": {
        icon: this.genFinish
      },
      "on": _extends$1({}, this.$listeners)
    }), this.genSearchResult()]);
  }
});

/**
 * Common Switch Props
 */
var switchProps = {
  size: [Number, String],
  value: null,
  loading: Boolean,
  disabled: Boolean,
  activeColor: String,
  inactiveColor: String,
  activeValue: {
    type: null,
    default: true
  },
  inactiveValue: {
    type: null,
    default: false
  }
};

var FieldMixin = {
  inject: {
    vanField: {
      default: null
    }
  },
  watch: {
    value: function value() {
      var field = this.vanField;
      if (field) {
        field.resetValidation();
        field.validateWithTrigger('onChange');
      }
    }
  },
  created: function created() {
    var field = this.vanField;
    if (field && !field.children) {
      field.children = this;
    }
  }
};

// Utils
var _createNamespace$1l = createNamespace('switch'),
  createComponent$1p = _createNamespace$1l[0],
  bem$1g = _createNamespace$1l[1];
var Switch = createComponent$1p({
  mixins: [FieldMixin],
  props: switchProps,
  computed: {
    checked: function checked() {
      return this.value === this.activeValue;
    },
    style: function style() {
      return {
        fontSize: addUnit(this.size),
        backgroundColor: this.checked ? this.activeColor : this.inactiveColor
      };
    }
  },
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
      if (!this.disabled && !this.loading) {
        var newValue = this.checked ? this.inactiveValue : this.activeValue;
        this.$emit('input', newValue);
        this.$emit('change', newValue);
      }
    },
    genLoading: function genLoading() {
      var h = this.$createElement;
      if (this.loading) {
        var color = this.checked ? this.activeColor : this.inactiveColor;
        return h(Loading$1, {
          "class": bem$1g('loading'),
          "attrs": {
            "color": color
          }
        });
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var checked = this.checked,
      loading = this.loading,
      disabled = this.disabled;
    return h("div", {
      "class": bem$1g({
        on: checked,
        loading: loading,
        disabled: disabled
      }),
      "attrs": {
        "role": "switch",
        "aria-checked": String(checked)
      },
      "style": this.style,
      "on": {
        "click": this.onClick
      }
    }, [h("div", {
      "class": bem$1g('node')
    }, [this.genLoading()])]);
  }
});

var _createNamespace$1k = createNamespace('address-edit'),
  createComponent$1o = _createNamespace$1k[0],
  bem$1f = _createNamespace$1k[1],
  t$o = _createNamespace$1k[2];
var defaultData = {
  name: '',
  tel: '',
  country: '',
  province: '',
  city: '',
  county: '',
  areaCode: '',
  postalCode: '',
  addressDetail: '',
  isDefault: false
};
function isPostal(value) {
  return /^\d{6}$/.test(value);
}
var AddressEdit = createComponent$1o({
  props: {
    areaList: Object,
    isSaving: Boolean,
    isDeleting: Boolean,
    validator: Function,
    showDelete: Boolean,
    showPostal: Boolean,
    searchResult: Array,
    telMaxlength: [Number, String],
    showSetDefault: Boolean,
    saveButtonText: String,
    areaPlaceholder: String,
    deleteButtonText: String,
    showSearchResult: Boolean,
    showArea: {
      type: Boolean,
      default: true
    },
    showDetail: {
      type: Boolean,
      default: true
    },
    disableArea: Boolean,
    detailRows: {
      type: [Number, String],
      default: 1
    },
    detailMaxlength: {
      type: [Number, String],
      default: 200
    },
    addressInfo: {
      type: Object,
      default: function _default() {
        return _extends$1({}, defaultData);
      }
    },
    telValidator: {
      type: Function,
      default: isMobile
    },
    postalValidator: {
      type: Function,
      default: isPostal
    },
    areaColumnsPlaceholder: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  data: function data() {
    return {
      data: {},
      showAreaPopup: false,
      detailFocused: false,
      errorInfo: {
        tel: '',
        name: '',
        areaCode: '',
        postalCode: '',
        addressDetail: ''
      }
    };
  },
  computed: {
    areaListLoaded: function areaListLoaded() {
      return isObject$2(this.areaList) && Object.keys(this.areaList).length;
    },
    areaText: function areaText() {
      var _this$data = this.data,
        country = _this$data.country,
        province = _this$data.province,
        city = _this$data.city,
        county = _this$data.county,
        areaCode = _this$data.areaCode;
      if (areaCode) {
        var arr = [country, province, city, county];
        if (province && province === city) {
          arr.splice(1, 1);
        }
        return arr.filter(function (text) {
          return text;
        }).join('/');
      }
      return '';
    },
    // hide bottom field when use search && detail get focused
    hideBottomFields: function hideBottomFields() {
      var searchResult = this.searchResult;
      return searchResult && searchResult.length && this.detailFocused;
    }
  },
  watch: {
    addressInfo: {
      handler: function handler(val) {
        this.data = _extends$1({}, defaultData, val);
        this.setAreaCode(val.areaCode);
      },
      deep: true,
      immediate: true
    },
    areaList: function areaList() {
      this.setAreaCode(this.data.areaCode);
    }
  },
  methods: {
    onFocus: function onFocus(key) {
      this.errorInfo[key] = '';
      this.detailFocused = key === 'addressDetail';
      this.$emit('focus', key);
    },
    onChangeDetail: function onChangeDetail(val) {
      this.data.addressDetail = val;
      this.$emit('change-detail', val);
    },
    onAreaConfirm: function onAreaConfirm(values) {
      values = values.filter(function (value) {
        return !!value;
      });
      if (values.some(function (value) {
        return !value.code;
      })) {
        Toast(t$o('areaEmpty'));
        return;
      }
      this.showAreaPopup = false;
      this.assignAreaValues();
      this.$emit('change-area', values);
    },
    assignAreaValues: function assignAreaValues() {
      var area = this.$refs.area;
      if (area) {
        var detail = area.getArea();
        detail.areaCode = detail.code;
        delete detail.code;
        _extends$1(this.data, detail);
      }
    },
    onSave: function onSave() {
      var _this = this;
      var items = ['name', 'tel'];
      if (this.showArea) {
        items.push('areaCode');
      }
      if (this.showDetail) {
        items.push('addressDetail');
      }
      if (this.showPostal) {
        items.push('postalCode');
      }
      var isValid = items.every(function (item) {
        var msg = _this.getErrorMessage(item);
        if (msg) {
          _this.errorInfo[item] = msg;
        }
        return !msg;
      });
      if (isValid && !this.isSaving) {
        this.$emit('save', this.data);
      }
    },
    getErrorMessage: function getErrorMessage(key) {
      var value = String(this.data[key] || '').trim();
      if (this.validator) {
        var message = this.validator(key, value);
        if (message) {
          return message;
        }
      }
      switch (key) {
        case 'name':
          return value ? '' : t$o('nameEmpty');
        case 'tel':
          return this.telValidator(value) ? '' : t$o('telInvalid');
        case 'areaCode':
          return value ? '' : t$o('areaEmpty');
        case 'addressDetail':
          return value ? '' : t$o('addressEmpty');
        case 'postalCode':
          return value && !this.postalValidator(value) ? t$o('postalEmpty') : '';
      }
    },
    onDelete: function onDelete() {
      var _this2 = this;
      Dialog.confirm({
        title: t$o('confirmDelete')
      }).then(function () {
        _this2.$emit('delete', _this2.data);
      }).catch(function () {
        _this2.$emit('cancel-delete', _this2.data);
      });
    },
    // get values of area component
    getArea: function getArea() {
      return this.$refs.area ? this.$refs.area.getValues() : [];
    },
    // set area code to area component
    setAreaCode: function setAreaCode(code) {
      this.data.areaCode = code || '';
      if (code) {
        this.$nextTick(this.assignAreaValues);
      }
    },
    // @exposed-api
    setAddressDetail: function setAddressDetail(value) {
      this.data.addressDetail = value;
    },
    onDetailBlur: function onDetailBlur() {
      var _this3 = this;

      // await for click search event
      setTimeout(function () {
        _this3.detailFocused = false;
      });
    },
    genSetDefaultCell: function genSetDefaultCell(h) {
      var _this4 = this;
      if (this.showSetDefault) {
        var slots = {
          'right-icon': function rightIcon() {
            return h(Switch, {
              "attrs": {
                "size": "24"
              },
              "on": {
                "change": function change(event) {
                  _this4.$emit('change-default', event);
                }
              },
              "model": {
                value: _this4.data.isDefault,
                callback: function callback($$v) {
                  _this4.$set(_this4.data, "isDefault", $$v);
                }
              }
            });
          }
        };
        return h(Cell$1, {
          "directives": [{
            name: "show",
            value: !this.hideBottomFields
          }],
          "attrs": {
            "center": true,
            "title": t$o('defaultAddress')
          },
          "class": bem$1f('default'),
          "scopedSlots": slots
        });
      }
      return h();
    }
  },
  render: function render(h) {
    var _this5 = this;
    var data = this.data,
      errorInfo = this.errorInfo,
      disableArea = this.disableArea,
      hideBottomFields = this.hideBottomFields;
    var onFocus = function onFocus(name) {
      return function () {
        return _this5.onFocus(name);
      };
    };
    return h("div", {
      "class": bem$1f()
    }, [h("div", {
      "class": bem$1f('fields')
    }, [h(Field, {
      "attrs": {
        "clearable": true,
        "label": t$o('name'),
        "placeholder": t$o('namePlaceholder'),
        "errorMessage": errorInfo.name
      },
      "on": {
        "focus": onFocus('name')
      },
      "model": {
        value: data.name,
        callback: function callback($$v) {
          _this5.$set(data, "name", $$v);
        }
      }
    }), h(Field, {
      "attrs": {
        "clearable": true,
        "type": "tel",
        "label": t$o('tel'),
        "maxlength": this.telMaxlength,
        "placeholder": t$o('telPlaceholder'),
        "errorMessage": errorInfo.tel
      },
      "on": {
        "focus": onFocus('tel')
      },
      "model": {
        value: data.tel,
        callback: function callback($$v) {
          _this5.$set(data, "tel", $$v);
        }
      }
    }), h(Field, {
      "directives": [{
        name: "show",
        value: this.showArea
      }],
      "attrs": {
        "readonly": true,
        "clickable": !disableArea,
        "label": t$o('area'),
        "placeholder": this.areaPlaceholder || t$o('areaPlaceholder'),
        "errorMessage": errorInfo.areaCode,
        "rightIcon": !disableArea ? 'arrow' : null,
        "value": this.areaText
      },
      "on": {
        "focus": onFocus('areaCode'),
        "click": function click() {
          _this5.$emit('click-area');
          _this5.showAreaPopup = !disableArea;
        }
      }
    }), h(Detail, {
      "directives": [{
        name: "show",
        value: this.showDetail
      }],
      "attrs": {
        "focused": this.detailFocused,
        "value": data.addressDetail,
        "errorMessage": errorInfo.addressDetail,
        "detailRows": this.detailRows,
        "detailMaxlength": this.detailMaxlength,
        "searchResult": this.searchResult,
        "showSearchResult": this.showSearchResult
      },
      "on": {
        "focus": onFocus('addressDetail'),
        "blur": this.onDetailBlur,
        "input": this.onChangeDetail,
        "select-search": function selectSearch(event) {
          _this5.$emit('select-search', event);
        }
      }
    }), this.showPostal && h(Field, {
      "directives": [{
        name: "show",
        value: !hideBottomFields
      }],
      "attrs": {
        "type": "tel",
        "maxlength": "6",
        "label": t$o('postal'),
        "placeholder": t$o('postal'),
        "errorMessage": errorInfo.postalCode
      },
      "on": {
        "focus": onFocus('postalCode')
      },
      "model": {
        value: data.postalCode,
        callback: function callback($$v) {
          _this5.$set(data, "postalCode", $$v);
        }
      }
    }), this.slots()]), this.genSetDefaultCell(h), h("div", {
      "directives": [{
        name: "show",
        value: !hideBottomFields
      }],
      "class": bem$1f('buttons')
    }, [h(Button$2, {
      "attrs": {
        "block": true,
        "round": true,
        "loading": this.isSaving,
        "type": "danger",
        "text": this.saveButtonText || t$o('save')
      },
      "on": {
        "click": this.onSave
      }
    }), this.showDelete && h(Button$2, {
      "attrs": {
        "block": true,
        "round": true,
        "loading": this.isDeleting,
        "text": this.deleteButtonText || t$o('delete')
      },
      "on": {
        "click": this.onDelete
      }
    })]), h(Popup, {
      "attrs": {
        "round": true,
        "position": "bottom",
        "lazyRender": false,
        "getContainer": "body"
      },
      "model": {
        value: _this5.showAreaPopup,
        callback: function callback($$v) {
          _this5.showAreaPopup = $$v;
        }
      }
    }, [h(Area, {
      "ref": "area",
      "attrs": {
        "value": data.areaCode,
        "loading": !this.areaListLoaded,
        "areaList": this.areaList,
        "columnsPlaceholder": this.areaColumnsPlaceholder
      },
      "on": {
        "confirm": this.onAreaConfirm,
        "cancel": function cancel() {
          _this5.showAreaPopup = false;
        }
      }
    })])]);
  }
});

var _createNamespace$1j = createNamespace('radio-group'),
  createComponent$1n = _createNamespace$1j[0],
  bem$1e = _createNamespace$1j[1];
var RadioGroup = createComponent$1n({
  mixins: [ParentMixin('vanRadio'), FieldMixin],
  props: {
    value: null,
    disabled: Boolean,
    direction: String,
    checkedColor: String,
    iconSize: [Number, String]
  },
  watch: {
    value: function value(_value) {
      this.$emit('change', _value);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$1e([this.direction]),
      "attrs": {
        "role": "radiogroup"
      }
    }, [this.slots()]);
  }
});

var _createNamespace$1i = createNamespace('tag'),
  createComponent$1m = _createNamespace$1i[0],
  bem$1d = _createNamespace$1i[1];
function Tag(h, props, slots, ctx) {
  var _style;
  var type = props.type,
    mark = props.mark,
    plain = props.plain,
    color = props.color,
    round = props.round,
    size = props.size,
    textColor = props.textColor;
  var key = plain ? 'color' : 'backgroundColor';
  var style = (_style = {}, _style[key] = color, _style);
  if (plain) {
    style.color = textColor || color;
    style.borderColor = color;
  } else {
    style.color = textColor;
    style.background = color;
  }
  var classes = {
    mark: mark,
    plain: plain,
    round: round
  };
  if (size) {
    classes[size] = size;
  }
  var CloseIcon = props.closeable && h(Icon$3, {
    "attrs": {
      "name": "cross"
    },
    "class": bem$1d('close'),
    "on": {
      "click": function click(event) {
        event.stopPropagation();
        emit(ctx, 'close');
      }
    }
  });
  return h("transition", {
    "attrs": {
      "name": props.closeable ? 'van-fade' : null
    }
  }, [h("span", _mergeJSXProps([{
    "key": "content",
    "style": style,
    "class": bem$1d([classes, type])
  }, inherit(ctx, true)]), [slots.default == null ? void 0 : slots.default(), CloseIcon])]);
}
Tag.props = {
  size: String,
  mark: Boolean,
  color: String,
  plain: Boolean,
  round: Boolean,
  textColor: String,
  closeable: Boolean,
  type: {
    type: String,
    default: 'default'
  }
};
var Tag$1 = createComponent$1m(Tag);

/**
 * Common part of Checkbox & Radio
 */
var CheckboxMixin = function CheckboxMixin(_ref) {
  var parent = _ref.parent,
    bem = _ref.bem,
    role = _ref.role;
  return {
    mixins: [ChildrenMixin(parent), FieldMixin],
    props: {
      name: null,
      value: null,
      disabled: Boolean,
      iconSize: [Number, String],
      checkedColor: String,
      labelPosition: String,
      labelDisabled: Boolean,
      shape: {
        type: String,
        default: 'round'
      },
      bindGroup: {
        type: Boolean,
        default: true
      }
    },
    computed: {
      disableBindRelation: function disableBindRelation() {
        return !this.bindGroup;
      },
      isDisabled: function isDisabled() {
        return this.parent && this.parent.disabled || this.disabled;
      },
      direction: function direction() {
        return this.parent && this.parent.direction || null;
      },
      iconStyle: function iconStyle() {
        var checkedColor = this.checkedColor || this.parent && this.parent.checkedColor;
        if (checkedColor && this.checked && !this.isDisabled) {
          return {
            borderColor: checkedColor,
            backgroundColor: checkedColor
          };
        }
      },
      tabindex: function tabindex() {
        if (this.isDisabled || role === 'radio' && !this.checked) {
          return -1;
        }
        return 0;
      }
    },
    methods: {
      onClick: function onClick(event) {
        var _this = this;
        var target = event.target;
        var icon = this.$refs.icon;
        var iconClicked = icon === target || (icon == null ? void 0 : icon.contains(target));
        if (!this.isDisabled && (iconClicked || !this.labelDisabled)) {
          this.toggle(); // wait for toggle method to complete
          // so we can get the changed value in the click event listener

          setTimeout(function () {
            _this.$emit('click', event);
          });
        } else {
          this.$emit('click', event);
        }
      },
      genIcon: function genIcon() {
        var h = this.$createElement;
        var checked = this.checked;
        var iconSize = this.iconSize || this.parent && this.parent.iconSize;
        return h("div", {
          "ref": "icon",
          "class": bem('icon', [this.shape, {
            disabled: this.isDisabled,
            checked: checked
          }]),
          "style": {
            fontSize: addUnit(iconSize)
          }
        }, [this.slots('icon', {
          checked: checked
        }) || h(Icon$3, {
          "attrs": {
            "name": "success"
          },
          "style": this.iconStyle
        })]);
      },
      genLabel: function genLabel() {
        var h = this.$createElement;
        var slot = this.slots();
        if (slot) {
          return h("span", {
            "class": bem('label', [this.labelPosition, {
              disabled: this.isDisabled
            }])
          }, [slot]);
        }
      }
    },
    render: function render() {
      var h = arguments[0];
      var Children = [this.genIcon()];
      if (this.labelPosition === 'left') {
        Children.unshift(this.genLabel());
      } else {
        Children.push(this.genLabel());
      }
      return h("div", {
        "attrs": {
          "role": role,
          "tabindex": this.tabindex,
          "aria-checked": String(this.checked)
        },
        "class": bem([{
          disabled: this.isDisabled,
          'label-disabled': this.labelDisabled
        }, this.direction]),
        "on": {
          "click": this.onClick
        }
      }, [Children]);
    }
  };
};

var _createNamespace$1h = createNamespace('radio'),
  createComponent$1l = _createNamespace$1h[0],
  bem$1c = _createNamespace$1h[1];
var Radio = createComponent$1l({
  mixins: [CheckboxMixin({
    bem: bem$1c,
    role: 'radio',
    parent: 'vanRadio'
  })],
  computed: {
    currentValue: {
      get: function get() {
        return this.parent ? this.parent.value : this.value;
      },
      set: function set(val) {
        (this.parent || this).$emit('input', val);
      }
    },
    checked: function checked() {
      return this.currentValue === this.name;
    }
  },
  methods: {
    toggle: function toggle() {
      this.currentValue = this.name;
    }
  }
});

var _createNamespace$1g = createNamespace('address-item'),
  createComponent$1k = _createNamespace$1g[0],
  bem$1b = _createNamespace$1g[1];
function AddressItem(h, props, slots, ctx) {
  var disabled = props.disabled,
    switchable = props.switchable;
  function onClick() {
    if (switchable) {
      emit(ctx, 'select');
    }
    emit(ctx, 'click');
  }
  var genRightIcon = function genRightIcon() {
    return h(Icon$3, {
      "attrs": {
        "name": "edit"
      },
      "class": bem$1b('edit'),
      "on": {
        "click": function click(event) {
          event.stopPropagation();
          emit(ctx, 'edit');
          emit(ctx, 'click');
        }
      }
    });
  };
  function genTag() {
    if (slots.tag) {
      return slots.tag(_extends$1({}, props.data));
    }
    if (props.data.isDefault && props.defaultTagText) {
      return h(Tag$1, {
        "attrs": {
          "type": "danger",
          "round": true
        },
        "class": bem$1b('tag')
      }, [props.defaultTagText]);
    }
  }
  function genContent() {
    var data = props.data;
    var Info = [h("div", {
      "class": bem$1b('name')
    }, [data.name + " " + data.tel, genTag()]), h("div", {
      "class": bem$1b('address')
    }, [data.address])];
    if (switchable && !disabled) {
      return h(Radio, {
        "attrs": {
          "name": data.id,
          "iconSize": 18
        }
      }, [Info]);
    }
    return Info;
  }
  return h("div", {
    "class": bem$1b({
      disabled: disabled
    }),
    "on": {
      "click": onClick
    }
  }, [h(Cell$1, _mergeJSXProps([{
    "attrs": {
      "border": false,
      "valueClass": bem$1b('value')
    },
    "scopedSlots": {
      default: genContent,
      'right-icon': genRightIcon
    }
  }, inherit(ctx)])), slots.bottom == null ? void 0 : slots.bottom(_extends$1({}, props.data, {
    disabled: disabled
  }))]);
}
AddressItem.props = {
  data: Object,
  disabled: Boolean,
  switchable: Boolean,
  defaultTagText: String
};
var AddressItem$1 = createComponent$1k(AddressItem);

var _createNamespace$1f = createNamespace('address-list'),
  createComponent$1j = _createNamespace$1f[0],
  bem$1a = _createNamespace$1f[1],
  t$n = _createNamespace$1f[2];
function AddressList(h, props, slots, ctx) {
  function genList(list, disabled) {
    if (!list) {
      return;
    }
    return list.map(function (item, index) {
      return h(AddressItem$1, {
        "attrs": {
          "data": item,
          "disabled": disabled,
          "switchable": props.switchable,
          "defaultTagText": props.defaultTagText
        },
        "key": item.id,
        "scopedSlots": {
          bottom: slots['item-bottom'],
          tag: slots.tag
        },
        "on": {
          "select": function select() {
            emit(ctx, disabled ? 'select-disabled' : 'select', item, index);
            if (!disabled) {
              emit(ctx, 'input', item.id);
            }
          },
          "edit": function edit() {
            emit(ctx, disabled ? 'edit-disabled' : 'edit', item, index);
          },
          "click": function click() {
            emit(ctx, 'click-item', item, index);
          }
        }
      });
    });
  }
  var List = genList(props.list);
  var DisabledList = genList(props.disabledList, true);
  return h("div", _mergeJSXProps([{
    "class": bem$1a()
  }, inherit(ctx)]), [slots.top == null ? void 0 : slots.top(), h(RadioGroup, {
    "attrs": {
      "value": props.value
    }
  }, [List]), props.disabledText && h("div", {
    "class": bem$1a('disabled-text')
  }, [props.disabledText]), DisabledList, slots.default == null ? void 0 : slots.default(), h("div", {
    "class": bem$1a('bottom')
  }, [h(Button$2, {
    "attrs": {
      "round": true,
      "block": true,
      "type": "danger",
      "text": props.addButtonText || t$n('add')
    },
    "class": bem$1a('add'),
    "on": {
      "click": function click() {
        emit(ctx, 'add');
      }
    }
  })])]);
}
AddressList.props = {
  list: Array,
  value: [Number, String],
  disabledList: Array,
  disabledText: String,
  addButtonText: String,
  defaultTagText: String,
  switchable: {
    type: Boolean,
    default: true
  }
};
var AddressList$1 = createComponent$1j(AddressList);

var _createNamespace$1e = createNamespace('badge'),
  createComponent$1i = _createNamespace$1e[0],
  bem$19 = _createNamespace$1e[1];
var Badge = createComponent$1i({
  props: {
    dot: Boolean,
    max: [Number, String],
    color: String,
    content: [Number, String],
    tag: {
      type: String,
      default: 'div'
    }
  },
  methods: {
    hasContent: function hasContent() {
      return !!(this.$scopedSlots.content || isDef(this.content) && this.content !== '');
    },
    renderContent: function renderContent() {
      var dot = this.dot,
        max = this.max,
        content = this.content;
      if (!dot && this.hasContent()) {
        if (this.$scopedSlots.content) {
          return this.$scopedSlots.content();
        }
        if (isDef(max) && isNumeric(content) && +content > max) {
          return max + "+";
        }
        return content;
      }
    },
    renderBadge: function renderBadge() {
      var h = this.$createElement;
      if (this.hasContent() || this.dot) {
        return h("div", {
          "class": bem$19({
            dot: this.dot,
            fixed: !!this.$scopedSlots.default
          }),
          "style": {
            background: this.color
          }
        }, [this.renderContent()]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    if (this.$scopedSlots.default) {
      var tag = this.tag;
      return h(tag, {
        "class": bem$19('wrapper')
      }, [this.$scopedSlots.default(), this.renderBadge()]);
    }
    return this.renderBadge();
  }
});

/**
 * requestAnimationFrame polyfill
 */
var prev = Date.now();
/* istanbul ignore next */

function fallback(fn) {
  var curr = Date.now();
  var ms = Math.max(0, 16 - (curr - prev));
  var id = setTimeout(fn, ms);
  prev = curr + ms;
  return id;
}
/* istanbul ignore next */

var root = isServer$1 ? global : window;
/* istanbul ignore next */

var iRaf = root.requestAnimationFrame || fallback;
/* istanbul ignore next */

var iCancel = root.cancelAnimationFrame || root.clearTimeout;
function raf(fn) {
  return iRaf.call(root, fn);
} // double raf for animation

function doubleRaf(fn) {
  raf(function () {
    raf(fn);
  });
}
function cancelRaf(id) {
  iCancel.call(root, id);
}

function isDate$1(val) {
  return Object.prototype.toString.call(val) === '[object Date]' && !isNaN$1(val.getTime());
}

var _createNamespace$1d = createNamespace('calendar'),
  createComponent$1h = _createNamespace$1d[0],
  bem$18 = _createNamespace$1d[1],
  t$m = _createNamespace$1d[2];
function formatMonthTitle(date) {
  return t$m('monthTitle', date.getFullYear(), date.getMonth() + 1);
}
function compareMonth(date1, date2) {
  var year1 = date1.getFullYear();
  var year2 = date2.getFullYear();
  var month1 = date1.getMonth();
  var month2 = date2.getMonth();
  if (year1 === year2) {
    return month1 === month2 ? 0 : month1 > month2 ? 1 : -1;
  }
  return year1 > year2 ? 1 : -1;
}
function compareDay(day1, day2) {
  var compareMonthResult = compareMonth(day1, day2);
  if (compareMonthResult === 0) {
    var date1 = day1.getDate();
    var date2 = day2.getDate();
    return date1 === date2 ? 0 : date1 > date2 ? 1 : -1;
  }
  return compareMonthResult;
}
function getDayByOffset(date, offset) {
  date = new Date(date);
  date.setDate(date.getDate() + offset);
  return date;
}
function getPrevDay(date) {
  return getDayByOffset(date, -1);
}
function getNextDay(date) {
  return getDayByOffset(date, 1);
}
function calcDateNum(date) {
  var day1 = date[0].getTime();
  var day2 = date[1].getTime();
  return (day2 - day1) / (1000 * 60 * 60 * 24) + 1;
}
function copyDate(dates) {
  return new Date(dates);
}
function copyDates(dates) {
  if (Array.isArray(dates)) {
    return dates.map(function (date) {
      if (date === null) {
        return date;
      }
      return copyDate(date);
    });
  }
  return copyDate(dates);
}

function times(n, iteratee) {
  if (n < 0) {
    return [];
  }
  var index = -1;
  var result = Array(n);
  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}
function getTrueValue(value) {
  if (!value) {
    return 0;
  }
  while (isNaN$1(parseInt(value, 10))) {
    if (value.length > 1) {
      value = value.slice(1);
    } else {
      return 0;
    }
  }
  return parseInt(value, 10);
}
function getMonthEndDay(year, month) {
  return 32 - new Date(year, month - 1, 32).getDate();
}

var _createNamespace$1c = createNamespace('calendar-month'),
  createComponent$1g = _createNamespace$1c[0];
var Month = createComponent$1g({
  props: {
    date: Date,
    type: String,
    color: String,
    minDate: Date,
    maxDate: Date,
    showMark: Boolean,
    rowHeight: [Number, String],
    formatter: Function,
    lazyRender: Boolean,
    currentDate: [Date, Array],
    allowSameDay: Boolean,
    showSubtitle: Boolean,
    showMonthTitle: Boolean,
    firstDayOfWeek: Number
  },
  data: function data() {
    return {
      visible: false
    };
  },
  computed: {
    title: function title() {
      return formatMonthTitle(this.date);
    },
    rowHeightWithUnit: function rowHeightWithUnit() {
      return addUnit(this.rowHeight);
    },
    offset: function offset() {
      var firstDayOfWeek = this.firstDayOfWeek;
      var realDay = this.date.getDay();
      if (!firstDayOfWeek) {
        return realDay;
      }
      return (realDay + 7 - this.firstDayOfWeek) % 7;
    },
    totalDay: function totalDay() {
      return getMonthEndDay(this.date.getFullYear(), this.date.getMonth() + 1);
    },
    shouldRender: function shouldRender() {
      return this.visible || !this.lazyRender;
    },
    placeholders: function placeholders() {
      var rows = [];
      var count = Math.ceil((this.totalDay + this.offset) / 7);
      for (var day = 1; day <= count; day++) {
        rows.push({
          type: 'placeholder'
        });
      }
      return rows;
    },
    days: function days() {
      var days = [];
      var year = this.date.getFullYear();
      var month = this.date.getMonth();
      for (var day = 1; day <= this.totalDay; day++) {
        var date = new Date(year, month, day);
        var type = this.getDayType(date);
        var config = {
          date: date,
          type: type,
          text: day,
          bottomInfo: this.getBottomInfo(type)
        };
        if (this.formatter) {
          config = this.formatter(config);
        }
        days.push(config);
      }
      return days;
    }
  },
  methods: {
    getHeight: function getHeight() {
      var _this$$el;
      return ((_this$$el = this.$el) == null ? void 0 : _this$$el.getBoundingClientRect().height) || 0;
    },
    scrollIntoView: function scrollIntoView(body) {
      var _this$$refs = this.$refs,
        days = _this$$refs.days,
        month = _this$$refs.month;
      var el = this.showSubtitle ? days : month;
      var scrollTop = el.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
      setScrollTop(body, scrollTop);
    },
    getMultipleDayType: function getMultipleDayType(day) {
      var _this = this;
      var isSelected = function isSelected(date) {
        return _this.currentDate.some(function (item) {
          return compareDay(item, date) === 0;
        });
      };
      if (isSelected(day)) {
        var prevDay = getPrevDay(day);
        var nextDay = getNextDay(day);
        var prevSelected = isSelected(prevDay);
        var nextSelected = isSelected(nextDay);
        if (prevSelected && nextSelected) {
          return 'multiple-middle';
        }
        if (prevSelected) {
          return 'end';
        }
        return nextSelected ? 'start' : 'multiple-selected';
      }
      return '';
    },
    getRangeDayType: function getRangeDayType(day) {
      var _this$currentDate = this.currentDate,
        startDay = _this$currentDate[0],
        endDay = _this$currentDate[1];
      if (!startDay) {
        return '';
      }
      var compareToStart = compareDay(day, startDay);
      if (!endDay) {
        return compareToStart === 0 ? 'start' : '';
      }
      var compareToEnd = compareDay(day, endDay);
      if (compareToStart === 0 && compareToEnd === 0 && this.allowSameDay) {
        return 'start-end';
      }
      if (compareToStart === 0) {
        return 'start';
      }
      if (compareToEnd === 0) {
        return 'end';
      }
      if (compareToStart > 0 && compareToEnd < 0) {
        return 'middle';
      }
    },
    getDayType: function getDayType(day) {
      var type = this.type,
        minDate = this.minDate,
        maxDate = this.maxDate,
        currentDate = this.currentDate;
      if (compareDay(day, minDate) < 0 || compareDay(day, maxDate) > 0) {
        return 'disabled';
      }
      if (currentDate === null) {
        return;
      }
      if (type === 'single') {
        return compareDay(day, currentDate) === 0 ? 'selected' : '';
      }
      if (type === 'multiple') {
        return this.getMultipleDayType(day);
      }
      /* istanbul ignore else */

      if (type === 'range') {
        return this.getRangeDayType(day);
      }
    },
    getBottomInfo: function getBottomInfo(type) {
      if (this.type === 'range') {
        if (type === 'start' || type === 'end') {
          return t$m(type);
        }
        if (type === 'start-end') {
          return t$m('startEnd');
        }
      }
    },
    getDayStyle: function getDayStyle(type, index) {
      var style = {
        height: this.rowHeightWithUnit
      };
      if (type === 'placeholder') {
        style.width = '100%';
        return style;
      }
      if (index === 0) {
        style.marginLeft = 100 * this.offset / 7 + "%";
      }
      if (this.color) {
        if (type === 'start' || type === 'end' || type === 'start-end' || type === 'multiple-selected' || type === 'multiple-middle') {
          style.background = this.color;
        } else if (type === 'middle') {
          style.color = this.color;
        }
      }
      return style;
    },
    genTitle: function genTitle() {
      var h = this.$createElement;
      if (this.showMonthTitle) {
        return h("div", {
          "class": bem$18('month-title')
        }, [this.title]);
      }
    },
    genMark: function genMark() {
      var h = this.$createElement;
      if (this.showMark && this.shouldRender) {
        return h("div", {
          "class": bem$18('month-mark')
        }, [this.date.getMonth() + 1]);
      }
    },
    genDays: function genDays() {
      var h = this.$createElement;
      var days = this.shouldRender ? this.days : this.placeholders;
      return h("div", {
        "ref": "days",
        "attrs": {
          "role": "grid"
        },
        "class": bem$18('days')
      }, [this.genMark(), days.map(this.genDay)]);
    },
    genTopInfo: function genTopInfo(item) {
      var h = this.$createElement;
      var slot = this.$scopedSlots['top-info'];
      if (item.topInfo || slot) {
        return h("div", {
          "class": bem$18('top-info')
        }, [slot ? slot(item) : item.topInfo]);
      }
    },
    genBottomInfo: function genBottomInfo(item) {
      var h = this.$createElement;
      var slot = this.$scopedSlots['bottom-info'];
      if (item.bottomInfo || slot) {
        return h("div", {
          "class": bem$18('bottom-info')
        }, [slot ? slot(item) : item.bottomInfo]);
      }
    },
    genDay: function genDay(item, index) {
      var _this2 = this;
      var h = this.$createElement;
      var type = item.type;
      var style = this.getDayStyle(type, index);
      var disabled = type === 'disabled';
      var onClick = function onClick() {
        if (!disabled) {
          _this2.$emit('click', item);
        }
      };
      if (type === 'selected') {
        return h("div", {
          "attrs": {
            "role": "gridcell",
            "tabindex": -1
          },
          "style": style,
          "class": [bem$18('day'), item.className],
          "on": {
            "click": onClick
          }
        }, [h("div", {
          "class": bem$18('selected-day'),
          "style": {
            width: this.rowHeightWithUnit,
            height: this.rowHeightWithUnit,
            background: this.color
          }
        }, [this.genTopInfo(item), item.text, this.genBottomInfo(item)])]);
      }
      return h("div", {
        "attrs": {
          "role": "gridcell",
          "tabindex": disabled ? null : -1
        },
        "style": style,
        "class": [bem$18('day', type), item.className],
        "on": {
          "click": onClick
        }
      }, [this.genTopInfo(item), item.text, this.genBottomInfo(item)]);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$18('month'),
      "ref": "month"
    }, [this.genTitle(), this.genDays()]);
  }
});

var _createNamespace$1b = createNamespace('calendar-header'),
  createComponent$1f = _createNamespace$1b[0];
var Header = createComponent$1f({
  props: {
    title: String,
    subtitle: String,
    showTitle: Boolean,
    showSubtitle: Boolean,
    firstDayOfWeek: Number
  },
  methods: {
    genTitle: function genTitle() {
      var h = this.$createElement;
      if (this.showTitle) {
        var title = this.slots('title') || this.title || t$m('title');
        return h("div", {
          "class": bem$18('header-title')
        }, [title]);
      }
    },
    genSubtitle: function genSubtitle() {
      var h = this.$createElement;
      if (this.showSubtitle) {
        return h("div", {
          "class": bem$18('header-subtitle')
        }, [this.subtitle]);
      }
    },
    genWeekDays: function genWeekDays() {
      var h = this.$createElement;
      var weekdays = t$m('weekdays');
      var firstDayOfWeek = this.firstDayOfWeek;
      var renderWeekDays = [].concat(weekdays.slice(firstDayOfWeek, 7), weekdays.slice(0, firstDayOfWeek));
      return h("div", {
        "class": bem$18('weekdays')
      }, [renderWeekDays.map(function (item) {
        return h("span", {
          "class": bem$18('weekday')
        }, [item]);
      })]);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$18('header')
    }, [this.genTitle(), this.genSubtitle(), this.genWeekDays()]);
  }
});

// Utils
var Calendar = createComponent$1h({
  props: {
    title: String,
    color: String,
    value: Boolean,
    readonly: Boolean,
    formatter: Function,
    rowHeight: [Number, String],
    confirmText: String,
    rangePrompt: String,
    defaultDate: [Date, Array],
    getContainer: [String, Function],
    allowSameDay: Boolean,
    confirmDisabledText: String,
    type: {
      type: String,
      default: 'single'
    },
    round: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'bottom'
    },
    poppable: {
      type: Boolean,
      default: true
    },
    maxRange: {
      type: [Number, String],
      default: null
    },
    lazyRender: {
      type: Boolean,
      default: true
    },
    showMark: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showConfirm: {
      type: Boolean,
      default: true
    },
    showSubtitle: {
      type: Boolean,
      default: true
    },
    closeOnPopstate: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: true
    },
    minDate: {
      type: Date,
      validator: isDate$1,
      default: function _default() {
        return new Date();
      }
    },
    maxDate: {
      type: Date,
      validator: isDate$1,
      default: function _default() {
        var now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
      }
    },
    firstDayOfWeek: {
      type: [Number, String],
      default: 0,
      validator: function validator(val) {
        return val >= 0 && val <= 6;
      }
    }
  },
  inject: {
    vanPopup: {
      default: null
    }
  },
  data: function data() {
    return {
      subtitle: '',
      currentDate: this.getInitialDate()
    };
  },
  computed: {
    months: function months() {
      var months = [];
      var cursor = new Date(this.minDate);
      cursor.setDate(1);
      do {
        months.push(new Date(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      } while (compareMonth(cursor, this.maxDate) !== 1);
      return months;
    },
    buttonDisabled: function buttonDisabled() {
      var type = this.type,
        currentDate = this.currentDate;
      if (currentDate) {
        if (type === 'range') {
          return !currentDate[0] || !currentDate[1];
        }
        if (type === 'multiple') {
          return !currentDate.length;
        }
      }
      return !currentDate;
    },
    dayOffset: function dayOffset() {
      return this.firstDayOfWeek ? this.firstDayOfWeek % 7 : 0;
    }
  },
  watch: {
    value: 'init',
    type: function type() {
      this.reset();
    },
    defaultDate: function defaultDate(val) {
      this.currentDate = val;
      this.scrollIntoView();
    }
  },
  mounted: function mounted() {
    this.init(); // https://github.com/vant-ui/vant/issues/9845

    if (!this.poppable) {
      var _this$vanPopup;
      (_this$vanPopup = this.vanPopup) == null ? void 0 : _this$vanPopup.$on('opened', this.onScroll);
    }
  },
  /* istanbul ignore next */
  activated: function activated() {
    this.init();
  },
  methods: {
    // @exposed-api
    reset: function reset(date) {
      if (date === void 0) {
        date = this.getInitialDate();
      }
      this.currentDate = date;
      this.scrollIntoView();
    },
    init: function init() {
      var _this = this;
      if (this.poppable && !this.value) {
        return;
      }
      this.$nextTick(function () {
        // add Math.floor to avoid decimal height issues
        // https://github.com/vant-ui/vant/issues/5640
        _this.bodyHeight = Math.floor(_this.$refs.body.getBoundingClientRect().height);
        _this.onScroll();
        _this.scrollIntoView();
      });
    },
    // @exposed-api
    scrollToDate: function scrollToDate(targetDate) {
      var _this2 = this;
      raf(function () {
        var displayed = _this2.value || !_this2.poppable;
        /* istanbul ignore if */

        if (!targetDate || !displayed) {
          return;
        }
        _this2.months.some(function (month, index) {
          if (compareMonth(month, targetDate) === 0) {
            var _this2$$refs = _this2.$refs,
              body = _this2$$refs.body,
              months = _this2$$refs.months;
            months[index].scrollIntoView(body);
            return true;
          }
          return false;
        });
        _this2.onScroll();
      });
    },
    // scroll to current month
    scrollIntoView: function scrollIntoView() {
      var currentDate = this.currentDate;
      if (currentDate) {
        var targetDate = this.type === 'single' ? currentDate : currentDate[0];
        this.scrollToDate(targetDate);
      }
    },
    getInitialDate: function getInitialDate() {
      var type = this.type,
        minDate = this.minDate,
        maxDate = this.maxDate,
        defaultDate = this.defaultDate;
      if (defaultDate === null) {
        return defaultDate;
      }
      var defaultVal = new Date();
      if (compareDay(defaultVal, minDate) === -1) {
        defaultVal = minDate;
      } else if (compareDay(defaultVal, maxDate) === 1) {
        defaultVal = maxDate;
      }
      if (type === 'range') {
        var _ref = defaultDate || [],
          startDay = _ref[0],
          endDay = _ref[1];
        return [startDay || defaultVal, endDay || getNextDay(defaultVal)];
      }
      if (type === 'multiple') {
        return defaultDate || [defaultVal];
      }
      return defaultDate || defaultVal;
    },
    // calculate the position of the elements
    // and find the elements that needs to be rendered
    onScroll: function onScroll() {
      var _this$$refs = this.$refs,
        body = _this$$refs.body,
        months = _this$$refs.months;
      var top = getScrollTop(body);
      var bottom = top + this.bodyHeight;
      var heights = months.map(function (item) {
        return item.getHeight();
      });
      var heightSum = heights.reduce(function (a, b) {
        return a + b;
      }, 0); // iOS scroll bounce may exceed the range

      if (bottom > heightSum && top > 0) {
        return;
      }
      var height = 0;
      var currentMonth;
      var visibleRange = [-1, -1];
      for (var i = 0; i < months.length; i++) {
        var visible = height <= bottom && height + heights[i] >= top;
        if (visible) {
          visibleRange[1] = i;
          if (!currentMonth) {
            currentMonth = months[i];
            visibleRange[0] = i;
          }
          if (!months[i].showed) {
            months[i].showed = true;
            this.$emit('month-show', {
              date: months[i].date,
              title: months[i].title
            });
          }
        }
        height += heights[i];
      }
      months.forEach(function (month, index) {
        month.visible = index >= visibleRange[0] - 1 && index <= visibleRange[1] + 1;
      });
      /* istanbul ignore else */

      if (currentMonth) {
        this.subtitle = currentMonth.title;
      }
    },
    onClickDay: function onClickDay(item) {
      if (this.readonly) {
        return;
      }
      var date = item.date;
      var type = this.type,
        currentDate = this.currentDate;
      if (type === 'range') {
        if (!currentDate) {
          this.select([date, null]);
          return;
        }
        var startDay = currentDate[0],
          endDay = currentDate[1];
        if (startDay && !endDay) {
          var compareToStart = compareDay(date, startDay);
          if (compareToStart === 1) {
            this.select([startDay, date], true);
          } else if (compareToStart === -1) {
            this.select([date, null]);
          } else if (this.allowSameDay) {
            this.select([date, date], true);
          }
        } else {
          this.select([date, null]);
        }
      } else if (type === 'multiple') {
        if (!currentDate) {
          this.select([date]);
          return;
        }
        var selectedIndex;
        var selected = this.currentDate.some(function (dateItem, index) {
          var equal = compareDay(dateItem, date) === 0;
          if (equal) {
            selectedIndex = index;
          }
          return equal;
        });
        if (selected) {
          var _currentDate$splice = currentDate.splice(selectedIndex, 1),
            unselectedDate = _currentDate$splice[0];
          this.$emit('unselect', copyDate(unselectedDate));
        } else if (this.maxRange && currentDate.length >= this.maxRange) {
          Toast(this.rangePrompt || t$m('rangePrompt', this.maxRange));
        } else {
          this.select([].concat(currentDate, [date]));
        }
      } else {
        this.select(date, true);
      }
    },
    togglePopup: function togglePopup(val) {
      this.$emit('input', val);
    },
    select: function select(date, complete) {
      var _this3 = this;
      var emit = function emit(date) {
        _this3.currentDate = date;
        _this3.$emit('select', copyDates(_this3.currentDate));
      };
      if (complete && this.type === 'range') {
        var valid = this.checkRange(date);
        if (!valid) {
          // auto selected to max range if showConfirm
          if (this.showConfirm) {
            emit([date[0], getDayByOffset(date[0], this.maxRange - 1)]);
          } else {
            emit(date);
          }
          return;
        }
      }
      emit(date);
      if (complete && !this.showConfirm) {
        this.onConfirm();
      }
    },
    checkRange: function checkRange(date) {
      var maxRange = this.maxRange,
        rangePrompt = this.rangePrompt;
      if (maxRange && calcDateNum(date) > maxRange) {
        Toast(rangePrompt || t$m('rangePrompt', maxRange));
        return false;
      }
      return true;
    },
    onConfirm: function onConfirm() {
      this.$emit('confirm', copyDates(this.currentDate));
    },
    genMonth: function genMonth(date, index) {
      var h = this.$createElement;
      var showMonthTitle = index !== 0 || !this.showSubtitle;
      return h(Month, {
        "ref": "months",
        "refInFor": true,
        "attrs": {
          "date": date,
          "type": this.type,
          "color": this.color,
          "minDate": this.minDate,
          "maxDate": this.maxDate,
          "showMark": this.showMark,
          "formatter": this.formatter,
          "rowHeight": this.rowHeight,
          "lazyRender": this.lazyRender,
          "currentDate": this.currentDate,
          "showSubtitle": this.showSubtitle,
          "allowSameDay": this.allowSameDay,
          "showMonthTitle": showMonthTitle,
          "firstDayOfWeek": this.dayOffset
        },
        "scopedSlots": {
          'top-info': this.$scopedSlots['top-info'],
          'bottom-info': this.$scopedSlots['bottom-info']
        },
        "on": {
          "click": this.onClickDay
        }
      });
    },
    genFooterContent: function genFooterContent() {
      var h = this.$createElement;
      var slot = this.slots('footer');
      if (slot) {
        return slot;
      }
      if (this.showConfirm) {
        var text = this.buttonDisabled ? this.confirmDisabledText : this.confirmText;
        return h(Button$2, {
          "attrs": {
            "round": true,
            "block": true,
            "type": "danger",
            "color": this.color,
            "disabled": this.buttonDisabled,
            "nativeType": "button"
          },
          "class": bem$18('confirm'),
          "on": {
            "click": this.onConfirm
          }
        }, [text || t$m('confirm')]);
      }
    },
    genFooter: function genFooter() {
      var h = this.$createElement;
      return h("div", {
        "class": bem$18('footer', {
          unfit: !this.safeAreaInsetBottom
        })
      }, [this.genFooterContent()]);
    },
    genCalendar: function genCalendar() {
      var _this4 = this;
      var h = this.$createElement;
      return h("div", {
        "class": bem$18()
      }, [h(Header, {
        "attrs": {
          "title": this.title,
          "showTitle": this.showTitle,
          "subtitle": this.subtitle,
          "showSubtitle": this.showSubtitle,
          "firstDayOfWeek": this.dayOffset
        },
        "scopedSlots": {
          title: function title() {
            return _this4.slots('title');
          }
        }
      }), h("div", {
        "ref": "body",
        "class": bem$18('body'),
        "on": {
          "scroll": this.onScroll
        }
      }, [this.months.map(this.genMonth)]), this.genFooter()]);
    }
  },
  render: function render() {
    var _this5 = this;
    var h = arguments[0];
    if (this.poppable) {
      var _attrs;
      var createListener = function createListener(name) {
        return function () {
          return _this5.$emit(name);
        };
      };
      return h(Popup, {
        "attrs": (_attrs = {
          "round": true,
          "value": this.value
        }, _attrs["round"] = this.round, _attrs["position"] = this.position, _attrs["closeable"] = this.showTitle || this.showSubtitle, _attrs["getContainer"] = this.getContainer, _attrs["closeOnPopstate"] = this.closeOnPopstate, _attrs["closeOnClickOverlay"] = this.closeOnClickOverlay, _attrs),
        "class": bem$18('popup'),
        "on": {
          "input": this.togglePopup,
          "open": createListener('open'),
          "opened": createListener('opened'),
          "close": createListener('close'),
          "closed": createListener('closed')
        }
      }, [this.genCalendar()]);
    }
    return this.genCalendar();
  }
});

var _createNamespace$1a = createNamespace('image'),
  createComponent$1e = _createNamespace$1a[0],
  bem$17 = _createNamespace$1a[1];
var Image$1 = createComponent$1e({
  props: {
    src: String,
    fit: String,
    alt: String,
    round: Boolean,
    width: [Number, String],
    height: [Number, String],
    radius: [Number, String],
    lazyLoad: Boolean,
    iconPrefix: String,
    showError: {
      type: Boolean,
      default: true
    },
    showLoading: {
      type: Boolean,
      default: true
    },
    errorIcon: {
      type: String,
      default: 'photo-fail'
    },
    loadingIcon: {
      type: String,
      default: 'photo'
    }
  },
  data: function data() {
    return {
      loading: true,
      error: false
    };
  },
  watch: {
    src: function src() {
      this.loading = true;
      this.error = false;
    }
  },
  computed: {
    style: function style() {
      var style = {};
      if (isDef(this.width)) {
        style.width = addUnit(this.width);
      }
      if (isDef(this.height)) {
        style.height = addUnit(this.height);
      }
      if (isDef(this.radius)) {
        style.overflow = 'hidden';
        style.borderRadius = addUnit(this.radius);
      }
      return style;
    }
  },
  created: function created() {
    var $Lazyload = this.$Lazyload;
    if ($Lazyload && inBrowser$1) {
      $Lazyload.$on('loaded', this.onLazyLoaded);
      $Lazyload.$on('error', this.onLazyLoadError);
    }
  },
  beforeDestroy: function beforeDestroy() {
    var $Lazyload = this.$Lazyload;
    if ($Lazyload) {
      $Lazyload.$off('loaded', this.onLazyLoaded);
      $Lazyload.$off('error', this.onLazyLoadError);
    }
  },
  methods: {
    onLoad: function onLoad(event) {
      this.loading = false;
      this.$emit('load', event);
    },
    onLazyLoaded: function onLazyLoaded(_ref) {
      var el = _ref.el;
      if (el === this.$refs.image && this.loading) {
        this.onLoad();
      }
    },
    onLazyLoadError: function onLazyLoadError(_ref2) {
      var el = _ref2.el;
      if (el === this.$refs.image && !this.error) {
        this.onError();
      }
    },
    onError: function onError(event) {
      this.error = true;
      this.loading = false;
      this.$emit('error', event);
    },
    onClick: function onClick(event) {
      this.$emit('click', event);
    },
    genPlaceholder: function genPlaceholder() {
      var h = this.$createElement;
      if (this.loading && this.showLoading) {
        return h("div", {
          "class": bem$17('loading')
        }, [this.slots('loading') || h(Icon$3, {
          "attrs": {
            "name": this.loadingIcon,
            "classPrefix": this.iconPrefix
          },
          "class": bem$17('loading-icon')
        })]);
      }
      if (this.error && this.showError) {
        return h("div", {
          "class": bem$17('error')
        }, [this.slots('error') || h(Icon$3, {
          "attrs": {
            "name": this.errorIcon,
            "classPrefix": this.iconPrefix
          },
          "class": bem$17('error-icon')
        })]);
      }
    },
    genImage: function genImage() {
      var h = this.$createElement;
      var imgData = {
        class: bem$17('img'),
        attrs: {
          alt: this.alt
        },
        style: {
          objectFit: this.fit
        }
      };
      if (this.error) {
        return;
      }
      if (this.lazyLoad) {
        return h("img", _mergeJSXProps([{
          "ref": "image",
          "directives": [{
            name: "lazy",
            value: this.src
          }]
        }, imgData]));
      }
      return h("img", _mergeJSXProps([{
        "attrs": {
          "src": this.src
        },
        "on": {
          "load": this.onLoad,
          "error": this.onError
        }
      }, imgData]));
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$17({
        round: this.round
      }),
      "style": this.style,
      "on": {
        "click": this.onClick
      }
    }, [this.genImage(), this.genPlaceholder(), this.slots()]);
  }
});

var _createNamespace$19 = createNamespace('card'),
  createComponent$1d = _createNamespace$19[0],
  bem$16 = _createNamespace$19[1];
function Card(h, props, slots, ctx) {
  var _slots$priceTop;
  var thumb = props.thumb;
  var showNum = slots.num || isDef(props.num);
  var showPrice = slots.price || isDef(props.price);
  var showOriginPrice = slots['origin-price'] || isDef(props.originPrice);
  var showBottom = showNum || showPrice || showOriginPrice || slots.bottom;
  function onThumbClick(event) {
    emit(ctx, 'click-thumb', event);
  }
  function ThumbTag() {
    if (slots.tag || props.tag) {
      return h("div", {
        "class": bem$16('tag')
      }, [slots.tag ? slots.tag() : h(Tag$1, {
        "attrs": {
          "mark": true,
          "type": "danger"
        }
      }, [props.tag])]);
    }
  }
  function Thumb() {
    if (slots.thumb || thumb) {
      return h("a", {
        "attrs": {
          "href": props.thumbLink
        },
        "class": bem$16('thumb'),
        "on": {
          "click": onThumbClick
        }
      }, [slots.thumb ? slots.thumb() : h(Image$1, {
        "attrs": {
          "src": thumb,
          "width": "100%",
          "height": "100%",
          "fit": "cover",
          "lazy-load": props.lazyLoad
        }
      }), ThumbTag()]);
    }
  }
  function Title() {
    if (slots.title) {
      return slots.title();
    }
    if (props.title) {
      return h("div", {
        "class": [bem$16('title'), 'van-multi-ellipsis--l2']
      }, [props.title]);
    }
  }
  function Desc() {
    if (slots.desc) {
      return slots.desc();
    }
    if (props.desc) {
      return h("div", {
        "class": [bem$16('desc'), 'van-ellipsis']
      }, [props.desc]);
    }
  }
  function PriceContent() {
    var priceArr = props.price.toString().split('.');
    return h("div", [h("span", {
      "class": bem$16('price-currency')
    }, [props.currency]), h("span", {
      "class": bem$16('price-integer')
    }, [priceArr[0]]), ".", h("span", {
      "class": bem$16('price-decimal')
    }, [priceArr[1]])]);
  }
  function Price() {
    if (showPrice) {
      return h("div", {
        "class": bem$16('price')
      }, [slots.price ? slots.price() : PriceContent()]);
    }
  }
  function OriginPrice() {
    if (showOriginPrice) {
      var slot = slots['origin-price'];
      return h("div", {
        "class": bem$16('origin-price')
      }, [slot ? slot() : props.currency + " " + props.originPrice]);
    }
  }
  function Num() {
    if (showNum) {
      return h("div", {
        "class": bem$16('num')
      }, [slots.num ? slots.num() : "x" + props.num]);
    }
  }
  function Footer() {
    if (slots.footer) {
      return h("div", {
        "class": bem$16('footer')
      }, [slots.footer()]);
    }
  }
  return h("div", _mergeJSXProps([{
    "class": bem$16()
  }, inherit(ctx, true)]), [h("div", {
    "class": bem$16('header')
  }, [Thumb(), h("div", {
    "class": bem$16('content', {
      centered: props.centered
    })
  }, [h("div", [Title(), Desc(), slots.tags == null ? void 0 : slots.tags()]), showBottom && h("div", {
    "class": "van-card__bottom"
  }, [(_slots$priceTop = slots['price-top']) == null ? void 0 : _slots$priceTop.call(slots), Price(), OriginPrice(), Num(), slots.bottom == null ? void 0 : slots.bottom()])])]), Footer()]);
}
Card.props = {
  tag: String,
  desc: String,
  thumb: String,
  title: String,
  centered: Boolean,
  lazyLoad: Boolean,
  thumbLink: String,
  num: [Number, String],
  price: [Number, String],
  originPrice: [Number, String],
  currency: {
    type: String,
    default: '¥'
  }
};
var Card$1 = createComponent$1d(Card);

var _createNamespace$18 = createNamespace('tab'),
  createComponent$1c = _createNamespace$18[0],
  bem$15 = _createNamespace$18[1];
var Tab = createComponent$1c({
  mixins: [ChildrenMixin('vanTabs')],
  props: _extends$1({}, routeProps, {
    dot: Boolean,
    name: [Number, String],
    // @deprecated
    info: [Number, String],
    badge: [Number, String],
    title: String,
    titleStyle: null,
    titleClass: null,
    disabled: Boolean
  }),
  data: function data() {
    return {
      inited: false
    };
  },
  computed: {
    computedName: function computedName() {
      var _this$name;
      return (_this$name = this.name) != null ? _this$name : this.index;
    },
    isActive: function isActive() {
      var active = this.computedName === this.parent.currentName;
      if (active) {
        this.inited = true;
      }
      return active;
    }
  },
  watch: {
    title: function title() {
      this.parent.setLine();
      this.parent.scrollIntoView();
    },
    inited: function inited(val) {
      var _this = this;
      if (this.parent.lazyRender && val) {
        this.$nextTick(function () {
          _this.parent.$emit('rendered', _this.computedName, _this.title);
        });
      }
    }
  },
  render: function render(h) {
    var slots = this.slots,
      parent = this.parent,
      isActive = this.isActive;
    var slotContent = slots();
    if (!slotContent && !parent.animated) {
      return;
    }
    var show = parent.scrollspy || isActive;
    var shouldRender = this.inited || parent.scrollspy || !parent.lazyRender;
    var Content = shouldRender ? slotContent : h();
    if (parent.animated) {
      return h("div", {
        "attrs": {
          "role": "tabpanel",
          "aria-hidden": !isActive
        },
        "class": bem$15('pane-wrapper', {
          inactive: !isActive
        })
      }, [h("div", {
        "class": bem$15('pane')
      }, [Content])]);
    }
    return h("div", {
      "directives": [{
        name: "show",
        value: show
      }],
      "attrs": {
        "role": "tabpanel"
      },
      "class": bem$15('pane')
    }, [Content]);
  }
});

function scrollLeftTo(scroller, to, duration) {
  var count = 0;
  var from = scroller.scrollLeft;
  var frames = duration === 0 ? 1 : Math.round(duration * 1000 / 16);
  function animate() {
    scroller.scrollLeft += (to - from) / frames;
    if (++count < frames) {
      raf(animate);
    }
  }
  animate();
}
function scrollTopTo(scroller, to, duration, callback) {
  var current = getScrollTop(scroller);
  var isDown = current < to;
  var frames = duration === 0 ? 1 : Math.round(duration * 1000 / 16);
  var step = (to - current) / frames;
  function animate() {
    current += step;
    if (isDown && current > to || !isDown && current < to) {
      current = to;
    }
    setScrollTop(scroller, current);
    if (isDown && current < to || !isDown && current > to) {
      raf(animate);
    } else if (callback) {
      raf(callback);
    }
  }
  animate();
}

function isHidden(el) {
  var style = window.getComputedStyle(el);
  var hidden = style.display === 'none'; // offsetParent returns null in the following situations:
  // 1. The element or its parent element has the display property set to none.
  // 2. The element has the position property set to fixed

  var parentHidden = el.offsetParent === null && style.position !== 'fixed';
  return hidden || parentHidden;
}

function callInterceptor(options) {
  var interceptor = options.interceptor,
    args = options.args,
    done = options.done;
  if (interceptor) {
    var returnVal = interceptor.apply(void 0, args);
    if (isPromise$1(returnVal)) {
      returnVal.then(function (value) {
        if (value) {
          done();
        }
      }).catch(noop$2);
    } else if (returnVal) {
      done();
    }
  } else {
    done();
  }
}

var _createNamespace$17 = createNamespace('tab'),
  createComponent$1b = _createNamespace$17[0],
  bem$14 = _createNamespace$17[1];
var Title = createComponent$1b({
  props: {
    dot: Boolean,
    type: String,
    info: [Number, String],
    color: String,
    title: String,
    isActive: Boolean,
    disabled: Boolean,
    scrollable: Boolean,
    activeColor: String,
    inactiveColor: String
  },
  computed: {
    style: function style() {
      var style = {};
      var color = this.color,
        isActive = this.isActive;
      var isCard = this.type === 'card'; // card theme color

      if (color && isCard) {
        style.borderColor = color;
        if (!this.disabled) {
          if (isActive) {
            style.backgroundColor = color;
          } else {
            style.color = color;
          }
        }
      }
      var titleColor = isActive ? this.activeColor : this.inactiveColor;
      if (titleColor) {
        style.color = titleColor;
      }
      return style;
    }
  },
  methods: {
    onClick: function onClick() {
      this.$emit('click');
    },
    genText: function genText() {
      var h = this.$createElement;
      var Text = h("span", {
        "class": bem$14('text', {
          ellipsis: !this.scrollable
        })
      }, [this.slots() || this.title]);
      if (this.dot || isDef(this.info) && this.info !== '') {
        return h("span", {
          "class": bem$14('text-wrapper')
        }, [Text, h(Info$1, {
          "attrs": {
            "dot": this.dot,
            "info": this.info
          }
        })]);
      }
      return Text;
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "attrs": {
        "role": "tab",
        "aria-selected": this.isActive
      },
      "class": [bem$14({
        active: this.isActive,
        disabled: this.disabled
      })],
      "style": this.style,
      "on": {
        "click": this.onClick
      }
    }, [this.genText()]);
  }
});

var _createNamespace$16 = createNamespace('sticky'),
  createComponent$1a = _createNamespace$16[0],
  bem$13 = _createNamespace$16[1];
var Sticky = createComponent$1a({
  mixins: [BindEventMixin(function (bind, isBind) {
    if (!this.scroller) {
      this.scroller = getScroller(this.$el);
    }
    if (this.observer) {
      var method = isBind ? 'observe' : 'unobserve';
      this.observer[method](this.$el);
    }
    bind(this.scroller, 'scroll', this.onScroll, true);
    this.onScroll();
  })],
  props: {
    zIndex: [Number, String],
    container: null,
    offsetTop: {
      type: [Number, String],
      default: 0
    }
  },
  data: function data() {
    return {
      fixed: false,
      height: 0,
      transform: 0
    };
  },
  computed: {
    offsetTopPx: function offsetTopPx() {
      return unitToPx(this.offsetTop);
    },
    style: function style() {
      if (!this.fixed) {
        return;
      }
      var style = {};
      if (isDef(this.zIndex)) {
        style.zIndex = this.zIndex;
      }
      if (this.offsetTopPx && this.fixed) {
        style.top = this.offsetTopPx + "px";
      }
      if (this.transform) {
        style.transform = "translate3d(0, " + this.transform + "px, 0)";
      }
      return style;
    }
  },
  watch: {
    fixed: function fixed(isFixed) {
      this.$emit('change', isFixed);
    }
  },
  created: function created() {
    var _this = this;

    // compatibility: https://caniuse.com/#feat=intersectionobserver
    if (!isServer$1 && window.IntersectionObserver) {
      this.observer = new IntersectionObserver(function (entries) {
        // trigger scroll when visibility changed
        if (entries[0].intersectionRatio > 0) {
          _this.onScroll();
        }
      }, {
        root: document.body
      });
    }
  },
  methods: {
    onScroll: function onScroll() {
      var _this2 = this;
      if (isHidden(this.$el)) {
        return;
      }
      this.height = this.$el.offsetHeight;
      var container = this.container,
        offsetTopPx = this.offsetTopPx;
      var scrollTop = getScrollTop(window);
      var topToPageTop = getElementTop(this.$el);
      var emitScrollEvent = function emitScrollEvent() {
        _this2.$emit('scroll', {
          scrollTop: scrollTop,
          isFixed: _this2.fixed
        });
      }; // The sticky component should be kept inside the container element

      if (container) {
        var bottomToPageTop = topToPageTop + container.offsetHeight;
        if (scrollTop + offsetTopPx + this.height > bottomToPageTop) {
          var distanceToBottom = this.height + scrollTop - bottomToPageTop;
          if (distanceToBottom < this.height) {
            this.fixed = true;
            this.transform = -(distanceToBottom + offsetTopPx);
          } else {
            this.fixed = false;
          }
          emitScrollEvent();
          return;
        }
      }
      if (scrollTop + offsetTopPx > topToPageTop) {
        this.fixed = true;
        this.transform = 0;
      } else {
        this.fixed = false;
      }
      emitScrollEvent();
    }
  },
  render: function render() {
    var h = arguments[0];
    var fixed = this.fixed;
    var style = {
      height: fixed ? this.height + "px" : null
    };
    return h("div", {
      "style": style
    }, [h("div", {
      "class": bem$13({
        fixed: fixed
      }),
      "style": this.style
    }, [this.slots()])]);
  }
});

var _createNamespace$15 = createNamespace('tabs'),
  createComponent$19 = _createNamespace$15[0],
  bem$12 = _createNamespace$15[1];
var MIN_SWIPE_DISTANCE = 50;
var Content = createComponent$19({
  mixins: [TouchMixin],
  props: {
    count: Number,
    duration: [Number, String],
    animated: Boolean,
    swipeable: Boolean,
    currentIndex: Number
  },
  computed: {
    style: function style() {
      if (this.animated) {
        return {
          transform: "translate3d(" + -1 * this.currentIndex * 100 + "%, 0, 0)",
          transitionDuration: this.duration + "s"
        };
      }
    },
    listeners: function listeners() {
      if (this.swipeable) {
        return {
          touchstart: this.touchStart,
          touchmove: this.touchMove,
          touchend: this.onTouchEnd,
          touchcancel: this.onTouchEnd
        };
      }
    }
  },
  methods: {
    // watch swipe touch end
    onTouchEnd: function onTouchEnd() {
      var direction = this.direction,
        deltaX = this.deltaX,
        currentIndex = this.currentIndex;
      /* istanbul ignore else */

      if (direction === 'horizontal' && this.offsetX >= MIN_SWIPE_DISTANCE) {
        /* istanbul ignore else */
        if (deltaX > 0 && currentIndex !== 0) {
          this.$emit('change', currentIndex - 1);
        } else if (deltaX < 0 && currentIndex !== this.count - 1) {
          this.$emit('change', currentIndex + 1);
        }
      }
    },
    genChildren: function genChildren() {
      var h = this.$createElement;
      if (this.animated) {
        return h("div", {
          "class": bem$12('track'),
          "style": this.style
        }, [this.slots()]);
      }
      return this.slots();
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$12('content', {
        animated: this.animated
      }),
      "on": _extends$1({}, this.listeners)
    }, [this.genChildren()]);
  }
});

// Utils
var _createNamespace$14 = createNamespace('tabs'),
  createComponent$18 = _createNamespace$14[0],
  bem$11 = _createNamespace$14[1];
var Tabs = createComponent$18({
  mixins: [ParentMixin('vanTabs'), BindEventMixin(function (bind) {
    if (!this.scroller) {
      this.scroller = getScroller(this.$el);
    }
    bind(window, 'resize', this.resize, true);
    if (this.scrollspy) {
      bind(this.scroller, 'scroll', this.onScroll, true);
    }
  })],
  inject: {
    vanPopup: {
      default: null
    }
  },
  model: {
    prop: 'active'
  },
  props: {
    color: String,
    border: Boolean,
    sticky: Boolean,
    animated: Boolean,
    swipeable: Boolean,
    scrollspy: Boolean,
    background: String,
    lineWidth: [Number, String],
    lineHeight: [Number, String],
    beforeChange: Function,
    titleActiveColor: String,
    titleInactiveColor: String,
    type: {
      type: String,
      default: 'line'
    },
    active: {
      type: [Number, String],
      default: 0
    },
    ellipsis: {
      type: Boolean,
      default: true
    },
    duration: {
      type: [Number, String],
      default: 0.3
    },
    offsetTop: {
      type: [Number, String],
      default: 0
    },
    lazyRender: {
      type: Boolean,
      default: true
    },
    swipeThreshold: {
      type: [Number, String],
      default: 5
    }
  },
  data: function data() {
    return {
      position: '',
      currentIndex: null,
      lineStyle: {
        backgroundColor: this.color
      }
    };
  },
  computed: {
    // whether the nav is scrollable
    scrollable: function scrollable() {
      return this.children.length > this.swipeThreshold || !this.ellipsis;
    },
    navStyle: function navStyle() {
      return {
        borderColor: this.color,
        background: this.background
      };
    },
    currentName: function currentName() {
      var activeTab = this.children[this.currentIndex];
      if (activeTab) {
        return activeTab.computedName;
      }
    },
    offsetTopPx: function offsetTopPx() {
      return unitToPx(this.offsetTop);
    },
    scrollOffset: function scrollOffset() {
      if (this.sticky) {
        return this.offsetTopPx + this.tabHeight;
      }
      return 0;
    }
  },
  watch: {
    color: 'setLine',
    active: function active(name) {
      if (name !== this.currentName) {
        this.setCurrentIndexByName(name);
      }
    },
    children: function children() {
      var _this = this;
      this.setCurrentIndexByName(this.active);
      this.setLine();
      this.$nextTick(function () {
        _this.scrollIntoView(true);
      });
    },
    currentIndex: function currentIndex() {
      this.scrollIntoView();
      this.setLine(); // scroll to correct position

      if (this.stickyFixed && !this.scrollspy) {
        setRootScrollTop(Math.ceil(getElementTop(this.$el) - this.offsetTopPx));
      }
    },
    scrollspy: function scrollspy(val) {
      if (val) {
        on$2(this.scroller, 'scroll', this.onScroll, true);
      } else {
        off$2(this.scroller, 'scroll', this.onScroll);
      }
    }
  },
  mounted: function mounted() {
    var _this2 = this;
    this.init(); // https://github.com/vant-ui/vant/issues/7959

    if (this.vanPopup) {
      this.vanPopup.onReopen(function () {
        _this2.setLine();
      });
    }
  },
  activated: function activated() {
    this.init();
    this.setLine();
  },
  methods: {
    // @exposed-api
    resize: function resize() {
      this.setLine();
    },
    init: function init() {
      var _this3 = this;
      this.$nextTick(function () {
        _this3.inited = true;
        _this3.tabHeight = getVisibleHeight(_this3.$refs.wrap);
        _this3.scrollIntoView(true);
      });
    },
    // update nav bar style
    setLine: function setLine() {
      var _this4 = this;
      var shouldAnimate = this.inited;
      this.$nextTick(function () {
        var titles = _this4.$refs.titles;
        if (!titles || !titles[_this4.currentIndex] || _this4.type !== 'line' || isHidden(_this4.$el)) {
          return;
        }
        var title = titles[_this4.currentIndex].$el;
        var lineWidth = _this4.lineWidth,
          lineHeight = _this4.lineHeight;
        var left = title.offsetLeft + title.offsetWidth / 2;
        var lineStyle = {
          width: addUnit(lineWidth),
          backgroundColor: _this4.color,
          transform: "translateX(" + left + "px) translateX(-50%)"
        };
        if (shouldAnimate) {
          lineStyle.transitionDuration = _this4.duration + "s";
        }
        if (isDef(lineHeight)) {
          var height = addUnit(lineHeight);
          lineStyle.height = height;
          lineStyle.borderRadius = height;
        }
        _this4.lineStyle = lineStyle;
      });
    },
    // correct the index of active tab
    setCurrentIndexByName: function setCurrentIndexByName(name) {
      var matched = this.children.filter(function (tab) {
        return tab.computedName === name;
      });
      var defaultIndex = (this.children[0] || {}).index || 0;
      this.setCurrentIndex(matched.length ? matched[0].index : defaultIndex);
    },
    setCurrentIndex: function setCurrentIndex(currentIndex) {
      var newIndex = this.findAvailableTab(currentIndex);
      if (!isDef(newIndex)) {
        return;
      }
      var newTab = this.children[newIndex];
      var newName = newTab.computedName;
      var shouldEmitChange = this.currentIndex !== null;
      this.currentIndex = newIndex;
      if (newName !== this.active) {
        this.$emit('input', newName);
        if (shouldEmitChange) {
          this.$emit('change', newName, newTab.title);
        }
      }
    },
    findAvailableTab: function findAvailableTab(index) {
      var diff = index < this.currentIndex ? -1 : 1;
      while (index >= 0 && index < this.children.length) {
        if (!this.children[index].disabled) {
          return index;
        }
        index += diff;
      }
    },
    // emit event when clicked
    onClick: function onClick(item, index) {
      var _this5 = this;
      var _this$children$index = this.children[index],
        title = _this$children$index.title,
        disabled = _this$children$index.disabled,
        computedName = _this$children$index.computedName;
      if (disabled) {
        this.$emit('disabled', computedName, title);
      } else {
        callInterceptor({
          interceptor: this.beforeChange,
          args: [computedName],
          done: function done() {
            _this5.setCurrentIndex(index);
            _this5.scrollToCurrentContent();
          }
        });
        this.$emit('click', computedName, title);
        route(item.$router, item);
      }
    },
    // scroll active tab into view
    scrollIntoView: function scrollIntoView(immediate) {
      var titles = this.$refs.titles;
      if (!this.scrollable || !titles || !titles[this.currentIndex]) {
        return;
      }
      var nav = this.$refs.nav;
      var title = titles[this.currentIndex].$el;
      var to = title.offsetLeft - (nav.offsetWidth - title.offsetWidth) / 2;
      scrollLeftTo(nav, to, immediate ? 0 : +this.duration);
    },
    onSticktScroll: function onSticktScroll(params) {
      this.stickyFixed = params.isFixed;
      this.$emit('scroll', params);
    },
    // @exposed-api
    scrollTo: function scrollTo(name) {
      var _this6 = this;
      this.$nextTick(function () {
        _this6.setCurrentIndexByName(name);
        _this6.scrollToCurrentContent(true);
      });
    },
    scrollToCurrentContent: function scrollToCurrentContent(immediate) {
      var _this7 = this;
      if (immediate === void 0) {
        immediate = false;
      }
      if (this.scrollspy) {
        var target = this.children[this.currentIndex];
        var el = target == null ? void 0 : target.$el;
        if (el) {
          var to = getElementTop(el, this.scroller) - this.scrollOffset;
          this.lockScroll = true;
          scrollTopTo(this.scroller, to, immediate ? 0 : +this.duration, function () {
            _this7.lockScroll = false;
          });
        }
      }
    },
    onScroll: function onScroll() {
      if (this.scrollspy && !this.lockScroll) {
        var index = this.getCurrentIndexOnScroll();
        this.setCurrentIndex(index);
      }
    },
    getCurrentIndexOnScroll: function getCurrentIndexOnScroll() {
      var children = this.children;
      for (var index = 0; index < children.length; index++) {
        var top = getVisibleTop(children[index].$el);
        if (top > this.scrollOffset) {
          return index === 0 ? 0 : index - 1;
        }
      }
      return children.length - 1;
    }
  },
  render: function render() {
    var _this8 = this,
      _ref;
    var h = arguments[0];
    var type = this.type,
      animated = this.animated,
      scrollable = this.scrollable;
    var Nav = this.children.map(function (item, index) {
      var _item$badge;
      return h(Title, {
        "ref": "titles",
        "refInFor": true,
        "attrs": {
          "type": type,
          "dot": item.dot,
          "info": (_item$badge = item.badge) != null ? _item$badge : item.info,
          "title": item.title,
          "color": _this8.color,
          "isActive": index === _this8.currentIndex,
          "disabled": item.disabled,
          "scrollable": scrollable,
          "activeColor": _this8.titleActiveColor,
          "inactiveColor": _this8.titleInactiveColor
        },
        "style": item.titleStyle,
        "class": item.titleClass,
        "scopedSlots": {
          default: function _default() {
            return item.slots('title');
          }
        },
        "on": {
          "click": function click() {
            _this8.onClick(item, index);
          }
        }
      });
    });
    var Wrap = h("div", {
      "ref": "wrap",
      "class": [bem$11('wrap', {
        scrollable: scrollable
      }), (_ref = {}, _ref[BORDER_TOP_BOTTOM] = type === 'line' && this.border, _ref)]
    }, [h("div", {
      "ref": "nav",
      "attrs": {
        "role": "tablist"
      },
      "class": bem$11('nav', [type, {
        complete: this.scrollable
      }]),
      "style": this.navStyle
    }, [this.slots('nav-left'), Nav, type === 'line' && h("div", {
      "class": bem$11('line'),
      "style": this.lineStyle
    }), this.slots('nav-right')])]);
    return h("div", {
      "class": bem$11([type])
    }, [this.sticky ? h(Sticky, {
      "attrs": {
        "container": this.$el,
        "offsetTop": this.offsetTop
      },
      "on": {
        "scroll": this.onSticktScroll
      }
    }, [Wrap]) : Wrap, h(Content, {
      "attrs": {
        "count": this.children.length,
        "animated": animated,
        "duration": this.duration,
        "swipeable": this.swipeable,
        "currentIndex": this.currentIndex
      },
      "on": {
        "change": this.setCurrentIndex
      }
    }, [this.slots()])]);
  }
});

var _createNamespace$13 = createNamespace('cascader'),
  createComponent$17 = _createNamespace$13[0],
  bem$10 = _createNamespace$13[1],
  t$l = _createNamespace$13[2];
var Cascader = createComponent$17({
  props: {
    title: String,
    value: [Number, String],
    fieldNames: Object,
    placeholder: String,
    activeColor: String,
    options: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    closeable: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      tabs: [],
      activeTab: 0
    };
  },
  computed: {
    textKey: function textKey() {
      var _this$fieldNames;
      return ((_this$fieldNames = this.fieldNames) == null ? void 0 : _this$fieldNames.text) || 'text';
    },
    valueKey: function valueKey() {
      var _this$fieldNames2;
      return ((_this$fieldNames2 = this.fieldNames) == null ? void 0 : _this$fieldNames2.value) || 'value';
    },
    childrenKey: function childrenKey() {
      var _this$fieldNames3;
      return ((_this$fieldNames3 = this.fieldNames) == null ? void 0 : _this$fieldNames3.children) || 'children';
    }
  },
  watch: {
    options: {
      deep: true,
      handler: 'updateTabs'
    },
    value: function value(_value) {
      var _this = this;
      if (_value || _value === 0) {
        var values = this.tabs.map(function (tab) {
          var _tab$selectedOption;
          return (_tab$selectedOption = tab.selectedOption) == null ? void 0 : _tab$selectedOption[_this.valueKey];
        });
        if (values.indexOf(_value) !== -1) {
          return;
        }
      }
      this.updateTabs();
    }
  },
  created: function created() {
    this.updateTabs();
  },
  methods: {
    getSelectedOptionsByValue: function getSelectedOptionsByValue(options, value) {
      for (var i = 0; i < options.length; i++) {
        var option = options[i];
        if (option[this.valueKey] === value) {
          return [option];
        }
        if (option[this.childrenKey]) {
          var selectedOptions = this.getSelectedOptionsByValue(option[this.childrenKey], value);
          if (selectedOptions) {
            return [option].concat(selectedOptions);
          }
        }
      }
    },
    updateTabs: function updateTabs() {
      var _this2 = this;
      if (this.value || this.value === 0) {
        var selectedOptions = this.getSelectedOptionsByValue(this.options, this.value);
        if (selectedOptions) {
          var optionsCursor = this.options;
          this.tabs = selectedOptions.map(function (option) {
            var tab = {
              options: optionsCursor,
              selectedOption: option
            };
            var next = optionsCursor.filter(function (item) {
              return item[_this2.valueKey] === option[_this2.valueKey];
            });
            if (next.length) {
              optionsCursor = next[0][_this2.childrenKey];
            }
            return tab;
          });
          if (optionsCursor) {
            this.tabs.push({
              options: optionsCursor,
              selectedOption: null
            });
          }
          this.$nextTick(function () {
            _this2.activeTab = _this2.tabs.length - 1;
          });
          return;
        }
      }
      this.tabs = [{
        options: this.options,
        selectedOption: null
      }];
    },
    onSelect: function onSelect(option, tabIndex) {
      var _this3 = this;
      this.tabs[tabIndex].selectedOption = option;
      if (this.tabs.length > tabIndex + 1) {
        this.tabs = this.tabs.slice(0, tabIndex + 1);
      }
      if (option[this.childrenKey]) {
        var nextTab = {
          options: option[this.childrenKey],
          selectedOption: null
        };
        if (this.tabs[tabIndex + 1]) {
          this.$set(this.tabs, tabIndex + 1, nextTab);
        } else {
          this.tabs.push(nextTab);
        }
        this.$nextTick(function () {
          _this3.activeTab++;
        });
      }
      var selectedOptions = this.tabs.map(function (tab) {
        return tab.selectedOption;
      }).filter(function (item) {
        return !!item;
      });
      var eventParams = {
        value: option[this.valueKey],
        tabIndex: tabIndex,
        selectedOptions: selectedOptions
      };
      this.$emit('input', option[this.valueKey]);
      this.$emit('change', eventParams);
      if (!option[this.childrenKey]) {
        this.$emit('finish', eventParams);
      }
    },
    onClose: function onClose() {
      this.$emit('close');
    },
    renderHeader: function renderHeader() {
      var h = this.$createElement;
      if (this.showHeader) {
        return h("div", {
          "class": bem$10('header')
        }, [h("h2", {
          "class": bem$10('title')
        }, [this.slots('title') || this.title]), this.closeable ? h(Icon$3, {
          "attrs": {
            "name": "cross"
          },
          "class": bem$10('close-icon'),
          "on": {
            "click": this.onClose
          }
        }) : null]);
      }
    },
    renderOptions: function renderOptions(options, selectedOption, tabIndex) {
      var _this4 = this;
      var h = this.$createElement;
      var renderOption = function renderOption(option) {
        var isSelected = selectedOption && option[_this4.valueKey] === selectedOption[_this4.valueKey];
        var Text = _this4.slots('option', {
          option: option,
          selected: isSelected
        }) || h("span", [option[_this4.textKey]]);
        return h("li", {
          "class": bem$10('option', {
            selected: isSelected
          }),
          "style": {
            color: isSelected ? _this4.activeColor : null
          },
          "on": {
            "click": function click() {
              _this4.onSelect(option, tabIndex);
            }
          }
        }, [Text, isSelected ? h(Icon$3, {
          "attrs": {
            "name": "success"
          },
          "class": bem$10('selected-icon')
        }) : null]);
      };
      return h("ul", {
        "class": bem$10('options')
      }, [options.map(renderOption)]);
    },
    renderTab: function renderTab(item, tabIndex) {
      var h = this.$createElement;
      var options = item.options,
        selectedOption = item.selectedOption;
      var title = selectedOption ? selectedOption[this.textKey] : this.placeholder || t$l('select');
      return h(Tab, {
        "attrs": {
          "title": title,
          "titleClass": bem$10('tab', {
            unselected: !selectedOption
          })
        }
      }, [this.renderOptions(options, selectedOption, tabIndex)]);
    },
    renderTabs: function renderTabs() {
      var _this5 = this;
      var h = this.$createElement;
      return h(Tabs, {
        "attrs": {
          "animated": true,
          "swipeable": true,
          "swipeThreshold": 0,
          "color": this.activeColor
        },
        "class": bem$10('tabs'),
        "model": {
          value: _this5.activeTab,
          callback: function callback($$v) {
            _this5.activeTab = $$v;
          }
        }
      }, [this.tabs.map(this.renderTab)]);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$10()
    }, [this.renderHeader(), this.renderTabs()]);
  }
});

var _createNamespace$12 = createNamespace('cell-group'),
  createComponent$16 = _createNamespace$12[0],
  bem$$ = _createNamespace$12[1];
function CellGroup(h, props, slots, ctx) {
  var _ref;
  var Group = h("div", _mergeJSXProps([{
    "class": [bem$$({
      inset: props.inset
    }), (_ref = {}, _ref[BORDER_TOP_BOTTOM] = props.border, _ref)]
  }, inherit(ctx, true)]), [slots.default == null ? void 0 : slots.default()]);
  if (props.title || slots.title) {
    return h("div", {
      "key": ctx.data.key
    }, [h("div", {
      "class": bem$$('title', {
        inset: props.inset
      })
    }, [slots.title ? slots.title() : props.title]), Group]);
  }
  return Group;
}
CellGroup.props = {
  title: String,
  inset: Boolean,
  border: {
    type: Boolean,
    default: true
  }
};
var CellGroup$1 = createComponent$16(CellGroup);

var _createNamespace$11 = createNamespace('checkbox'),
  createComponent$15 = _createNamespace$11[0],
  bem$_ = _createNamespace$11[1];
var Checkbox = createComponent$15({
  mixins: [CheckboxMixin({
    bem: bem$_,
    role: 'checkbox',
    parent: 'vanCheckbox'
  })],
  computed: {
    checked: {
      get: function get() {
        if (this.parent) {
          return this.parent.value.indexOf(this.name) !== -1;
        }
        return this.value;
      },
      set: function set(val) {
        if (this.parent) {
          this.setParentValue(val);
        } else {
          this.$emit('input', val);
        }
      }
    }
  },
  watch: {
    value: function value(val) {
      this.$emit('change', val);
    }
  },
  methods: {
    // @exposed-api
    toggle: function toggle(checked) {
      var _this = this;
      if (checked === void 0) {
        checked = !this.checked;
      }

      // When toggle method is called multiple times at the same time,
      // only the last call is valid.
      // This is a hack for usage inside Cell.
      clearTimeout(this.toggleTask);
      this.toggleTask = setTimeout(function () {
        _this.checked = checked;
      });
    },
    setParentValue: function setParentValue(val) {
      var parent = this.parent;
      var value = parent.value.slice();
      if (val) {
        if (parent.max && value.length >= parent.max) {
          return;
        }
        /* istanbul ignore else */

        if (value.indexOf(this.name) === -1) {
          value.push(this.name);
          parent.$emit('input', value);
        }
      } else {
        var index = value.indexOf(this.name);
        /* istanbul ignore else */

        if (index !== -1) {
          value.splice(index, 1);
          parent.$emit('input', value);
        }
      }
    }
  }
});

var _createNamespace$10 = createNamespace('checkbox-group'),
  createComponent$14 = _createNamespace$10[0],
  bem$Z = _createNamespace$10[1];
var CheckboxGroup = createComponent$14({
  mixins: [ParentMixin('vanCheckbox'), FieldMixin],
  props: {
    max: [Number, String],
    disabled: Boolean,
    direction: String,
    iconSize: [Number, String],
    checkedColor: String,
    value: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  watch: {
    value: function value(val) {
      this.$emit('change', val);
    }
  },
  methods: {
    // @exposed-api
    toggleAll: function toggleAll(options) {
      if (options === void 0) {
        options = {};
      }
      if (typeof options === 'boolean') {
        options = {
          checked: options
        };
      }
      var _options = options,
        checked = _options.checked,
        skipDisabled = _options.skipDisabled;
      var children = this.children.filter(function (item) {
        if (item.disabled && skipDisabled) {
          return item.checked;
        }
        return checked != null ? checked : !item.checked;
      });
      var names = children.map(function (item) {
        return item.name;
      });
      this.$emit('input', names);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$Z([this.direction])
    }, [this.slots()]);
  }
});

var _createNamespace$$ = createNamespace('circle'),
  createComponent$13 = _createNamespace$$[0],
  bem$Y = _createNamespace$$[1];
var PERIMETER = 3140;
var uid = 0;
function format$2(rate) {
  return Math.min(Math.max(rate, 0), 100);
}
function getPath(clockwise, viewBoxSize) {
  var sweepFlag = clockwise ? 1 : 0;
  return "M " + viewBoxSize / 2 + " " + viewBoxSize / 2 + " m 0, -500 a 500, 500 0 1, " + sweepFlag + " 0, 1000 a 500, 500 0 1, " + sweepFlag + " 0, -1000";
}
var Circle = createComponent$13({
  props: {
    text: String,
    size: [Number, String],
    color: [String, Object],
    layerColor: String,
    strokeLinecap: String,
    value: {
      type: Number,
      default: 0
    },
    speed: {
      type: [Number, String],
      default: 0
    },
    fill: {
      type: String,
      default: 'none'
    },
    rate: {
      type: [Number, String],
      default: 100
    },
    strokeWidth: {
      type: [Number, String],
      default: 40
    },
    clockwise: {
      type: Boolean,
      default: true
    }
  },
  beforeCreate: function beforeCreate() {
    this.uid = "van-circle-gradient-" + uid++;
  },
  computed: {
    style: function style() {
      var size = addUnit(this.size);
      return {
        width: size,
        height: size
      };
    },
    path: function path() {
      return getPath(this.clockwise, this.viewBoxSize);
    },
    viewBoxSize: function viewBoxSize() {
      return +this.strokeWidth + 1000;
    },
    layerStyle: function layerStyle() {
      return {
        fill: "" + this.fill,
        stroke: "" + this.layerColor,
        strokeWidth: this.strokeWidth + "px"
      };
    },
    hoverStyle: function hoverStyle() {
      var offset = PERIMETER * this.value / 100;
      return {
        stroke: "" + (this.gradient ? "url(#" + this.uid + ")" : this.color),
        strokeWidth: +this.strokeWidth + 1 + "px",
        strokeLinecap: this.strokeLinecap,
        strokeDasharray: offset + "px " + PERIMETER + "px"
      };
    },
    gradient: function gradient() {
      return isObject$2(this.color);
    },
    LinearGradient: function LinearGradient() {
      var _this = this;
      var h = this.$createElement;
      if (!this.gradient) {
        return;
      }
      var Stops = Object.keys(this.color).sort(function (a, b) {
        return parseFloat(a) - parseFloat(b);
      }).map(function (key, index) {
        return h("stop", {
          "key": index,
          "attrs": {
            "offset": key,
            "stop-color": _this.color[key]
          }
        });
      });
      return h("defs", [h("linearGradient", {
        "attrs": {
          "id": this.uid,
          "x1": "100%",
          "y1": "0%",
          "x2": "0%",
          "y2": "0%"
        }
      }, [Stops])]);
    }
  },
  watch: {
    rate: {
      handler: function handler(rate) {
        this.startTime = Date.now();
        this.startRate = this.value;
        this.endRate = format$2(rate);
        this.increase = this.endRate > this.startRate;
        this.duration = Math.abs((this.startRate - this.endRate) * 1000 / this.speed);
        if (this.speed) {
          cancelRaf(this.rafId);
          this.rafId = raf(this.animate);
        } else {
          this.$emit('input', this.endRate);
        }
      },
      immediate: true
    }
  },
  methods: {
    animate: function animate() {
      var now = Date.now();
      var progress = Math.min((now - this.startTime) / this.duration, 1);
      var rate = progress * (this.endRate - this.startRate) + this.startRate;
      this.$emit('input', format$2(parseFloat(rate.toFixed(1))));
      if (this.increase ? rate < this.endRate : rate > this.endRate) {
        this.rafId = raf(this.animate);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$Y(),
      "style": this.style
    }, [h("svg", {
      "attrs": {
        "viewBox": "0 0 " + this.viewBoxSize + " " + this.viewBoxSize
      }
    }, [this.LinearGradient, h("path", {
      "class": bem$Y('layer'),
      "style": this.layerStyle,
      "attrs": {
        "d": this.path
      }
    }), h("path", {
      "attrs": {
        "d": this.path
      },
      "class": bem$Y('hover'),
      "style": this.hoverStyle
    })]), this.slots() || this.text && h("div", {
      "class": bem$Y('text')
    }, [this.text])]);
  }
});

var _createNamespace$_ = createNamespace('col'),
  createComponent$12 = _createNamespace$_[0],
  bem$X = _createNamespace$_[1];
var Col = createComponent$12({
  mixins: [ChildrenMixin('vanRow')],
  props: {
    span: [Number, String],
    offset: [Number, String],
    tag: {
      type: String,
      default: 'div'
    }
  },
  computed: {
    style: function style() {
      var index = this.index;
      var _ref = this.parent || {},
        spaces = _ref.spaces;
      if (spaces && spaces[index]) {
        var _spaces$index = spaces[index],
          left = _spaces$index.left,
          right = _spaces$index.right;
        return {
          paddingLeft: left ? left + "px" : null,
          paddingRight: right ? right + "px" : null
        };
      }
    }
  },
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
    }
  },
  render: function render() {
    var _bem;
    var h = arguments[0];
    var span = this.span,
      offset = this.offset;
    return h(this.tag, {
      "style": this.style,
      "class": bem$X((_bem = {}, _bem[span] = span, _bem["offset-" + offset] = offset, _bem)),
      "on": {
        "click": this.onClick
      }
    }, [this.slots()]);
  }
});

var _createNamespace$Z = createNamespace('collapse'),
  createComponent$11 = _createNamespace$Z[0],
  bem$W = _createNamespace$Z[1];
var Collapse = createComponent$11({
  mixins: [ParentMixin('vanCollapse')],
  props: {
    accordion: Boolean,
    value: [String, Number, Array],
    border: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    switch: function _switch(name, expanded) {
      if (!this.accordion) {
        name = expanded ? this.value.concat(name) : this.value.filter(function (activeName) {
          return activeName !== name;
        });
      }
      this.$emit('change', name);
      this.$emit('input', name);
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    return h("div", {
      "class": [bem$W(), (_ref = {}, _ref[BORDER_TOP_BOTTOM] = this.border, _ref)]
    }, [this.slots()]);
  }
});

var _createNamespace$Y = createNamespace('collapse-item'),
  createComponent$10 = _createNamespace$Y[0],
  bem$V = _createNamespace$Y[1];
var CELL_SLOTS = ['title', 'icon', 'right-icon'];
var CollapseItem = createComponent$10({
  mixins: [ChildrenMixin('vanCollapse')],
  props: _extends$1({}, cellProps, {
    name: [Number, String],
    disabled: Boolean,
    lazyRender: {
      type: Boolean,
      default: true
    },
    isLink: {
      type: Boolean,
      default: true
    }
  }),
  data: function data() {
    return {
      show: null,
      inited: null
    };
  },
  computed: {
    currentName: function currentName() {
      var _this$name;
      return (_this$name = this.name) != null ? _this$name : this.index;
    },
    expanded: function expanded() {
      var _this = this;
      if (!this.parent) {
        return null;
      }
      var _this$parent = this.parent,
        value = _this$parent.value,
        accordion = _this$parent.accordion;
      return accordion ? value === this.currentName : value.some(function (name) {
        return name === _this.currentName;
      });
    }
  },
  created: function created() {
    this.show = this.expanded;
    this.inited = this.expanded;
  },
  watch: {
    expanded: function expanded(_expanded, prev) {
      var _this2 = this;
      if (prev === null) {
        return;
      }
      if (_expanded) {
        this.show = true;
        this.inited = true;
      } // Use raf: flick when opened in safari
      // Use nextTick: closing animation failed when set `user-select: none`

      var nextTick = _expanded ? this.$nextTick : raf;
      nextTick(function () {
        var _this2$$refs = _this2.$refs,
          content = _this2$$refs.content,
          wrapper = _this2$$refs.wrapper;
        if (!content || !wrapper) {
          return;
        }
        var offsetHeight = content.offsetHeight;
        if (offsetHeight) {
          var contentHeight = offsetHeight + "px";
          wrapper.style.height = _expanded ? 0 : contentHeight; // use double raf to ensure animation can start

          doubleRaf(function () {
            wrapper.style.height = _expanded ? contentHeight : 0;
          });
        } else {
          _this2.onTransitionEnd();
        }
      });
    }
  },
  methods: {
    onClick: function onClick() {
      if (!this.disabled) {
        this.toggle();
      }
    },
    // @exposed-api
    toggle: function toggle(expanded) {
      if (expanded === void 0) {
        expanded = !this.expanded;
      }
      var parent = this.parent,
        currentName = this.currentName;
      var close = parent.accordion && currentName === parent.value;
      var name = close ? '' : currentName;
      this.parent.switch(name, expanded);
    },
    onTransitionEnd: function onTransitionEnd() {
      if (!this.expanded) {
        this.show = false;
      } else {
        this.$refs.wrapper.style.height = '';
      }
    },
    genTitle: function genTitle() {
      var _this3 = this;
      var h = this.$createElement;
      var border = this.border,
        disabled = this.disabled,
        expanded = this.expanded;
      var titleSlots = CELL_SLOTS.reduce(function (slots, name) {
        if (_this3.slots(name)) {
          slots[name] = function () {
            return _this3.slots(name);
          };
        }
        return slots;
      }, {});
      if (this.slots('value')) {
        titleSlots.default = function () {
          return _this3.slots('value');
        };
      }
      return h(Cell$1, {
        "attrs": {
          "role": "button",
          "tabindex": disabled ? -1 : 0,
          "aria-expanded": String(expanded)
        },
        "class": bem$V('title', {
          disabled: disabled,
          expanded: expanded,
          borderless: !border
        }),
        "on": {
          "click": this.onClick
        },
        "scopedSlots": titleSlots,
        "props": _extends$1({}, this.$props)
      });
    },
    genContent: function genContent() {
      var h = this.$createElement;
      if (this.inited || !this.lazyRender) {
        return h("div", {
          "directives": [{
            name: "show",
            value: this.show
          }],
          "ref": "wrapper",
          "class": bem$V('wrapper'),
          "on": {
            "transitionend": this.onTransitionEnd
          }
        }, [h("div", {
          "ref": "content",
          "class": bem$V('content')
        }, [this.slots()])]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": [bem$V({
        border: this.index && this.border
      })]
    }, [this.genTitle(), this.genContent()]);
  }
});

var _createNamespace$X = createNamespace('contact-card'),
  createComponent$$ = _createNamespace$X[0],
  bem$U = _createNamespace$X[1],
  t$k = _createNamespace$X[2];
function ContactCard(h, props, slots, ctx) {
  var type = props.type,
    editable = props.editable;
  function onClick(event) {
    if (editable) {
      emit(ctx, 'click', event);
    }
  }
  function Content() {
    if (type === 'add') {
      return props.addText || t$k('addText');
    }
    return [h("div", [t$k('name') + "\uFF1A" + props.name]), h("div", [t$k('tel') + "\uFF1A" + props.tel])];
  }
  return h(Cell$1, _mergeJSXProps([{
    "attrs": {
      "center": true,
      "border": false,
      "isLink": editable,
      "valueClass": bem$U('value'),
      "icon": type === 'edit' ? 'contact' : 'add-square'
    },
    "class": bem$U([type]),
    "on": {
      "click": onClick
    }
  }, inherit(ctx)]), [Content()]);
}
ContactCard.props = {
  tel: String,
  name: String,
  addText: String,
  editable: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'add'
  }
};
var ContactCard$1 = createComponent$$(ContactCard);

var _createNamespace$W = createNamespace('contact-edit'),
  createComponent$_ = _createNamespace$W[0],
  bem$T = _createNamespace$W[1],
  t$j = _createNamespace$W[2];
var defaultContact = {
  tel: '',
  name: ''
};
var ContactEdit = createComponent$_({
  props: {
    isEdit: Boolean,
    isSaving: Boolean,
    isDeleting: Boolean,
    showSetDefault: Boolean,
    setDefaultLabel: String,
    contactInfo: {
      type: Object,
      default: function _default() {
        return _extends$1({}, defaultContact);
      }
    },
    telValidator: {
      type: Function,
      default: isMobile
    }
  },
  data: function data() {
    return {
      data: _extends$1({}, defaultContact, this.contactInfo),
      errorInfo: {
        name: '',
        tel: ''
      }
    };
  },
  watch: {
    contactInfo: function contactInfo(val) {
      this.data = _extends$1({}, defaultContact, val);
    }
  },
  methods: {
    onFocus: function onFocus(key) {
      this.errorInfo[key] = '';
    },
    getErrorMessageByKey: function getErrorMessageByKey(key) {
      var value = this.data[key].trim();
      switch (key) {
        case 'name':
          return value ? '' : t$j('nameInvalid');
        case 'tel':
          return this.telValidator(value) ? '' : t$j('telInvalid');
      }
    },
    onSave: function onSave() {
      var _this = this;
      var isValid = ['name', 'tel'].every(function (item) {
        var msg = _this.getErrorMessageByKey(item);
        if (msg) {
          _this.errorInfo[item] = msg;
        }
        return !msg;
      });
      if (isValid && !this.isSaving) {
        this.$emit('save', this.data);
      }
    },
    onDelete: function onDelete() {
      var _this2 = this;
      Dialog.confirm({
        title: t$j('confirmDelete')
      }).then(function () {
        _this2.$emit('delete', _this2.data);
      });
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    var data = this.data,
      errorInfo = this.errorInfo;
    var onFocus = function onFocus(name) {
      return function () {
        return _this3.onFocus(name);
      };
    };
    return h("div", {
      "class": bem$T()
    }, [h("div", {
      "class": bem$T('fields')
    }, [h(Field, {
      "attrs": {
        "clearable": true,
        "maxlength": "30",
        "label": t$j('name'),
        "placeholder": t$j('nameEmpty'),
        "errorMessage": errorInfo.name
      },
      "on": {
        "focus": onFocus('name')
      },
      "model": {
        value: data.name,
        callback: function callback($$v) {
          _this3.$set(data, "name", $$v);
        }
      }
    }), h(Field, {
      "attrs": {
        "clearable": true,
        "type": "tel",
        "label": t$j('tel'),
        "placeholder": t$j('telEmpty'),
        "errorMessage": errorInfo.tel
      },
      "on": {
        "focus": onFocus('tel')
      },
      "model": {
        value: data.tel,
        callback: function callback($$v) {
          _this3.$set(data, "tel", $$v);
        }
      }
    })]), this.showSetDefault && h(Cell$1, {
      "attrs": {
        "title": this.setDefaultLabel,
        "border": false
      },
      "class": bem$T('switch-cell')
    }, [h(Switch, {
      "attrs": {
        "size": 24
      },
      "slot": "right-icon",
      "on": {
        "change": function change(event) {
          _this3.$emit('change-default', event);
        }
      },
      "model": {
        value: data.isDefault,
        callback: function callback($$v) {
          _this3.$set(data, "isDefault", $$v);
        }
      }
    })]), h("div", {
      "class": bem$T('buttons')
    }, [h(Button$2, {
      "attrs": {
        "block": true,
        "round": true,
        "type": "danger",
        "text": t$j('save'),
        "loading": this.isSaving
      },
      "on": {
        "click": this.onSave
      }
    }), this.isEdit && h(Button$2, {
      "attrs": {
        "block": true,
        "round": true,
        "text": t$j('delete'),
        "loading": this.isDeleting
      },
      "on": {
        "click": this.onDelete
      }
    })])]);
  }
});

var _createNamespace$V = createNamespace('contact-list'),
  createComponent$Z = _createNamespace$V[0],
  bem$S = _createNamespace$V[1],
  t$i = _createNamespace$V[2];
function ContactList(h, props, slots, ctx) {
  var List = props.list && props.list.map(function (item, index) {
    function onClick() {
      emit(ctx, 'input', item.id);
      emit(ctx, 'select', item, index);
    }
    function RightIcon() {
      return h(Radio, {
        "attrs": {
          "name": item.id,
          "iconSize": 16,
          "checkedColor": RED
        },
        "on": {
          "click": onClick
        }
      });
    }
    function LeftIcon() {
      return h(Icon$3, {
        "attrs": {
          "name": "edit"
        },
        "class": bem$S('edit'),
        "on": {
          "click": function click(event) {
            event.stopPropagation();
            emit(ctx, 'edit', item, index);
          }
        }
      });
    }
    function Content() {
      var nodes = [item.name + "\uFF0C" + item.tel];
      if (item.isDefault && props.defaultTagText) {
        nodes.push(h(Tag$1, {
          "attrs": {
            "type": "danger",
            "round": true
          },
          "class": bem$S('item-tag')
        }, [props.defaultTagText]));
      }
      return nodes;
    }
    return h(Cell$1, {
      "key": item.id,
      "attrs": {
        "isLink": true,
        "center": true,
        "valueClass": bem$S('item-value')
      },
      "class": bem$S('item'),
      "scopedSlots": {
        icon: LeftIcon,
        default: Content,
        'right-icon': RightIcon
      },
      "on": {
        "click": onClick
      }
    });
  });
  return h("div", _mergeJSXProps([{
    "class": bem$S()
  }, inherit(ctx)]), [h(RadioGroup, {
    "attrs": {
      "value": props.value
    },
    "class": bem$S('group')
  }, [List]), h("div", {
    "class": bem$S('bottom')
  }, [h(Button$2, {
    "attrs": {
      "round": true,
      "block": true,
      "type": "danger",
      "text": props.addText || t$i('addText')
    },
    "class": bem$S('add'),
    "on": {
      "click": function click() {
        emit(ctx, 'add');
      }
    }
  })])]);
}
ContactList.props = {
  value: null,
  list: Array,
  addText: String,
  defaultTagText: String
};
var ContactList$1 = createComponent$Z(ContactList);

var SECOND = 1000;
var MINUTE = 60 * SECOND;
var HOUR = 60 * MINUTE;
var DAY = 24 * HOUR;
function parseTimeData(time) {
  var days = Math.floor(time / DAY);
  var hours = Math.floor(time % DAY / HOUR);
  var minutes = Math.floor(time % HOUR / MINUTE);
  var seconds = Math.floor(time % MINUTE / SECOND);
  var milliseconds = Math.floor(time % SECOND);
  return {
    days: days,
    hours: hours,
    minutes: minutes,
    seconds: seconds,
    milliseconds: milliseconds
  };
}
function parseFormat(format, timeData) {
  var days = timeData.days;
  var hours = timeData.hours,
    minutes = timeData.minutes,
    seconds = timeData.seconds,
    milliseconds = timeData.milliseconds;
  if (format.indexOf('DD') === -1) {
    hours += days * 24;
  } else {
    format = format.replace('DD', padZero(days));
  }
  if (format.indexOf('HH') === -1) {
    minutes += hours * 60;
  } else {
    format = format.replace('HH', padZero(hours));
  }
  if (format.indexOf('mm') === -1) {
    seconds += minutes * 60;
  } else {
    format = format.replace('mm', padZero(minutes));
  }
  if (format.indexOf('ss') === -1) {
    milliseconds += seconds * 1000;
  } else {
    format = format.replace('ss', padZero(seconds));
  }
  if (format.indexOf('S') !== -1) {
    var ms = padZero(milliseconds, 3);
    if (format.indexOf('SSS') !== -1) {
      format = format.replace('SSS', ms);
    } else if (format.indexOf('SS') !== -1) {
      format = format.replace('SS', ms.slice(0, 2));
    } else {
      format = format.replace('S', ms.charAt(0));
    }
  }
  return format;
}
function isSameSecond(time1, time2) {
  return Math.floor(time1 / 1000) === Math.floor(time2 / 1000);
}

var _createNamespace$U = createNamespace('count-down'),
  createComponent$Y = _createNamespace$U[0],
  bem$R = _createNamespace$U[1];
var CountDown = createComponent$Y({
  props: {
    millisecond: Boolean,
    time: {
      type: [Number, String],
      default: 0
    },
    format: {
      type: String,
      default: 'HH:mm:ss'
    },
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      remain: 0
    };
  },
  computed: {
    timeData: function timeData() {
      return parseTimeData(this.remain);
    },
    formattedTime: function formattedTime() {
      return parseFormat(this.format, this.timeData);
    }
  },
  watch: {
    time: {
      immediate: true,
      handler: 'reset'
    }
  },
  activated: function activated() {
    if (this.keepAlivePaused) {
      this.counting = true;
      this.keepAlivePaused = false;
      this.tick();
    }
  },
  deactivated: function deactivated() {
    if (this.counting) {
      this.pause();
      this.keepAlivePaused = true;
    }
  },
  beforeDestroy: function beforeDestroy() {
    this.pause();
  },
  methods: {
    // @exposed-api
    start: function start() {
      if (this.counting) {
        return;
      }
      this.counting = true;
      this.endTime = Date.now() + this.remain;
      this.tick();
    },
    // @exposed-api
    pause: function pause() {
      this.counting = false;
      cancelRaf(this.rafId);
    },
    // @exposed-api
    reset: function reset() {
      this.pause();
      this.remain = +this.time;
      if (this.autoStart) {
        this.start();
      }
    },
    tick: function tick() {
      // should not start counting in server
      // see: https://github.com/vant-ui/vant/issues/7807
      if (!inBrowser$1) {
        return;
      }
      if (this.millisecond) {
        this.microTick();
      } else {
        this.macroTick();
      }
    },
    microTick: function microTick() {
      var _this = this;
      this.rafId = raf(function () {
        /* istanbul ignore if */
        // in case of call reset immediately after finish
        if (!_this.counting) {
          return;
        }
        _this.setRemain(_this.getRemain());
        if (_this.remain > 0) {
          _this.microTick();
        }
      });
    },
    macroTick: function macroTick() {
      var _this2 = this;
      this.rafId = raf(function () {
        /* istanbul ignore if */
        // in case of call reset immediately after finish
        if (!_this2.counting) {
          return;
        }
        var remain = _this2.getRemain();
        if (!isSameSecond(remain, _this2.remain) || remain === 0) {
          _this2.setRemain(remain);
        }
        if (_this2.remain > 0) {
          _this2.macroTick();
        }
      });
    },
    getRemain: function getRemain() {
      return Math.max(this.endTime - Date.now(), 0);
    },
    setRemain: function setRemain(remain) {
      this.remain = remain;
      this.$emit('change', this.timeData);
      if (remain === 0) {
        this.pause();
        this.$emit('finish');
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$R()
    }, [this.slots('default', this.timeData) || this.formattedTime]);
  }
});

var _createNamespace$T = createNamespace('coupon'),
  createComponent$X = _createNamespace$T[0],
  bem$Q = _createNamespace$T[1],
  t$h = _createNamespace$T[2];
function formatTimeStamp(timeStamp) {
  // compatible when the timestamp is seconds
  if (timeStamp < Math.pow(10, 12)) {
    return timeStamp * 1000;
  }
  return +timeStamp;
}
function getDate(timeStamp) {
  var date = new Date(formatTimeStamp(timeStamp));
  return date.getFullYear() + "." + padZero(date.getMonth() + 1) + "." + padZero(date.getDate());
}
function formatDiscount(discount) {
  return (discount / 10).toFixed(discount % 10 === 0 ? 0 : 1);
}
function formatAmount(amount) {
  return (amount / 100).toFixed(amount % 100 === 0 ? 0 : amount % 10 === 0 ? 1 : 2);
}
var Coupon = createComponent$X({
  props: {
    coupon: Object,
    chosen: Boolean,
    disabled: Boolean,
    currency: {
      type: String,
      default: '¥'
    }
  },
  computed: {
    validPeriod: function validPeriod() {
      var _this$coupon = this.coupon,
        startAt = _this$coupon.startAt,
        endAt = _this$coupon.endAt,
        customValidPeriod = _this$coupon.customValidPeriod;
      return customValidPeriod || getDate(startAt) + " - " + getDate(endAt);
    },
    faceAmount: function faceAmount() {
      var coupon = this.coupon;
      if (coupon.valueDesc) {
        return coupon.valueDesc + "<span>" + (coupon.unitDesc || '') + "</span>";
      }
      if (coupon.denominations) {
        var denominations = formatAmount(coupon.denominations);
        return "<span>" + this.currency + "</span> " + denominations;
      }
      if (coupon.discount) {
        return t$h('discount', formatDiscount(coupon.discount));
      }
      return '';
    },
    conditionMessage: function conditionMessage() {
      var condition = formatAmount(this.coupon.originCondition);
      return condition === '0' ? t$h('unlimited') : t$h('condition', condition);
    }
  },
  render: function render() {
    var h = arguments[0];
    var coupon = this.coupon,
      disabled = this.disabled;
    var description = disabled && coupon.reason || coupon.description;
    return h("div", {
      "class": bem$Q({
        disabled: disabled
      })
    }, [h("div", {
      "class": bem$Q('content')
    }, [h("div", {
      "class": bem$Q('head')
    }, [h("h2", {
      "class": bem$Q('amount'),
      "domProps": {
        "innerHTML": this.faceAmount
      }
    }), h("p", {
      "class": bem$Q('condition')
    }, [this.coupon.condition || this.conditionMessage])]), h("div", {
      "class": bem$Q('body')
    }, [h("p", {
      "class": bem$Q('name')
    }, [coupon.name]), h("p", {
      "class": bem$Q('valid')
    }, [this.validPeriod]), !this.disabled && h(Checkbox, {
      "attrs": {
        "size": 18,
        "value": this.chosen,
        "checkedColor": RED
      },
      "class": bem$Q('corner')
    })])]), description && h("p", {
      "class": bem$Q('description')
    }, [description])]);
  }
});

var _createNamespace$S = createNamespace('coupon-cell'),
  createComponent$W = _createNamespace$S[0],
  bem$P = _createNamespace$S[1],
  t$g = _createNamespace$S[2];
function formatValue(props) {
  var coupons = props.coupons,
    chosenCoupon = props.chosenCoupon,
    currency = props.currency;
  var coupon = coupons[+chosenCoupon];
  if (coupon) {
    var value = 0;
    if (isDef(coupon.value)) {
      value = coupon.value;
    } else if (isDef(coupon.denominations)) {
      value = coupon.denominations;
    }
    return "-" + currency + " " + (value / 100).toFixed(2);
  }
  return coupons.length === 0 ? t$g('tips') : t$g('count', coupons.length);
}
function CouponCell(h, props, slots, ctx) {
  var selected = props.coupons[+props.chosenCoupon];
  var value = formatValue(props);
  return h(Cell$1, _mergeJSXProps([{
    "class": bem$P(),
    "attrs": {
      "value": value,
      "title": props.title || t$g('title'),
      "border": props.border,
      "isLink": props.editable,
      "valueClass": bem$P('value', {
        selected: selected
      })
    }
  }, inherit(ctx, true)]));
}
CouponCell.model = {
  prop: 'chosenCoupon'
};
CouponCell.props = {
  title: String,
  coupons: {
    type: Array,
    default: function _default() {
      return [];
    }
  },
  currency: {
    type: String,
    default: '¥'
  },
  border: {
    type: Boolean,
    default: true
  },
  editable: {
    type: Boolean,
    default: true
  },
  chosenCoupon: {
    type: [Number, String],
    default: -1
  }
};
var CouponCell$1 = createComponent$W(CouponCell);

// Utils
var _createNamespace$R = createNamespace('coupon-list'),
  createComponent$V = _createNamespace$R[0],
  bem$O = _createNamespace$R[1],
  t$f = _createNamespace$R[2];
var EMPTY_IMAGE = 'https://img01.yzcdn.cn/vant/coupon-empty.png';
var CouponList = createComponent$V({
  model: {
    prop: 'code'
  },
  props: {
    code: String,
    closeButtonText: String,
    inputPlaceholder: String,
    enabledTitle: String,
    disabledTitle: String,
    exchangeButtonText: String,
    exchangeButtonLoading: Boolean,
    exchangeButtonDisabled: Boolean,
    exchangeMinLength: {
      type: Number,
      default: 1
    },
    chosenCoupon: {
      type: Number,
      default: -1
    },
    coupons: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    disabledCoupons: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    displayedCouponIndex: {
      type: Number,
      default: -1
    },
    showExchangeBar: {
      type: Boolean,
      default: true
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    showCount: {
      type: Boolean,
      default: true
    },
    currency: {
      type: String,
      default: '¥'
    },
    emptyImage: {
      type: String,
      default: EMPTY_IMAGE
    }
  },
  data: function data() {
    return {
      tab: 0,
      winHeight: window.innerHeight,
      currentCode: this.code || ''
    };
  },
  computed: {
    buttonDisabled: function buttonDisabled() {
      return !this.exchangeButtonLoading && (this.exchangeButtonDisabled || !this.currentCode || this.currentCode.length < this.exchangeMinLength);
    },
    listStyle: function listStyle() {
      return {
        height: this.winHeight - (this.showExchangeBar ? 140 : 94) + 'px'
      };
    }
  },
  watch: {
    code: function code(_code) {
      this.currentCode = _code;
    },
    currentCode: function currentCode(code) {
      this.$emit('input', code);
    },
    displayedCouponIndex: 'scrollToShowCoupon'
  },
  mounted: function mounted() {
    this.scrollToShowCoupon(this.displayedCouponIndex);
  },
  methods: {
    onClickExchangeButton: function onClickExchangeButton() {
      this.$emit('exchange', this.currentCode); // auto clear currentCode when not use vModel

      if (!this.code) {
        this.currentCode = '';
      }
    },
    // scroll to show specific coupon
    scrollToShowCoupon: function scrollToShowCoupon(index) {
      var _this = this;
      if (index === -1) {
        return;
      }
      this.$nextTick(function () {
        var _this$$refs = _this.$refs,
          card = _this$$refs.card,
          list = _this$$refs.list;
        /* istanbul ignore next */

        if (list && card && card[index]) {
          list.scrollTop = card[index].$el.offsetTop - 100;
        }
      });
    },
    genEmpty: function genEmpty() {
      var h = this.$createElement;
      return h("div", {
        "class": bem$O('empty')
      }, [h("img", {
        "attrs": {
          "src": this.emptyImage
        }
      }), h("p", [t$f('empty')])]);
    },
    genExchangeButton: function genExchangeButton() {
      var h = this.$createElement;
      return h(Button$2, {
        "attrs": {
          "plain": true,
          "type": "danger",
          "text": this.exchangeButtonText || t$f('exchange'),
          "loading": this.exchangeButtonLoading,
          "disabled": this.buttonDisabled
        },
        "class": bem$O('exchange'),
        "on": {
          "click": this.onClickExchangeButton
        }
      });
    }
  },
  render: function render() {
    var _this2 = this;
    var h = arguments[0];
    var coupons = this.coupons,
      disabledCoupons = this.disabledCoupons;
    var count = this.showCount ? " (" + coupons.length + ")" : '';
    var title = (this.enabledTitle || t$f('enable')) + count;
    var disabledCount = this.showCount ? " (" + disabledCoupons.length + ")" : '';
    var disabledTitle = (this.disabledTitle || t$f('disabled')) + disabledCount;
    var ExchangeBar = this.showExchangeBar && h("div", {
      "class": bem$O('exchange-bar')
    }, [h(Field, {
      "attrs": {
        "clearable": true,
        "border": false,
        "placeholder": this.inputPlaceholder || t$f('placeholder'),
        "maxlength": "20"
      },
      "class": bem$O('field'),
      "model": {
        value: _this2.currentCode,
        callback: function callback($$v) {
          _this2.currentCode = $$v;
        }
      }
    }), this.genExchangeButton()]);
    var onChange = function onChange(index) {
      return function () {
        return _this2.$emit('change', index);
      };
    };
    var CouponTab = h(Tab, {
      "attrs": {
        "title": title
      }
    }, [h("div", {
      "class": bem$O('list', {
        'with-bottom': this.showCloseButton
      }),
      "style": this.listStyle
    }, [coupons.map(function (coupon, index) {
      return h(Coupon, {
        "ref": "card",
        "key": coupon.id,
        "attrs": {
          "coupon": coupon,
          "currency": _this2.currency,
          "chosen": index === _this2.chosenCoupon
        },
        "nativeOn": {
          "click": onChange(index)
        }
      });
    }), !coupons.length && this.genEmpty(), this.slots('list-footer')])]);
    var DisabledCouponTab = h(Tab, {
      "attrs": {
        "title": disabledTitle
      }
    }, [h("div", {
      "class": bem$O('list', {
        'with-bottom': this.showCloseButton
      }),
      "style": this.listStyle
    }, [disabledCoupons.map(function (coupon) {
      return h(Coupon, {
        "attrs": {
          "disabled": true,
          "coupon": coupon,
          "currency": _this2.currency
        },
        "key": coupon.id
      });
    }), !disabledCoupons.length && this.genEmpty(), this.slots('disabled-list-footer')])]);
    return h("div", {
      "class": bem$O()
    }, [ExchangeBar, h(Tabs, {
      "class": bem$O('tab'),
      "attrs": {
        "border": false
      },
      "model": {
        value: _this2.tab,
        callback: function callback($$v) {
          _this2.tab = $$v;
        }
      }
    }, [CouponTab, DisabledCouponTab]), h("div", {
      "class": bem$O('bottom')
    }, [h(Button$2, {
      "directives": [{
        name: "show",
        value: this.showCloseButton
      }],
      "attrs": {
        "round": true,
        "type": "danger",
        "block": true,
        "text": this.closeButtonText || t$f('close')
      },
      "class": bem$O('close'),
      "on": {
        "click": onChange(-1)
      }
    })])]);
  }
});

var sharedProps = _extends$1({}, pickerProps, {
  value: null,
  filter: Function,
  columnsOrder: Array,
  showToolbar: {
    type: Boolean,
    default: true
  },
  formatter: {
    type: Function,
    default: function _default(type, value) {
      return value;
    }
  }
});
var TimePickerMixin = {
  data: function data() {
    return {
      innerValue: this.formatValue(this.value)
    };
  },
  computed: {
    originColumns: function originColumns() {
      var _this = this;
      return this.ranges.map(function (_ref) {
        var type = _ref.type,
          rangeArr = _ref.range;
        var values = times(rangeArr[1] - rangeArr[0] + 1, function (index) {
          var value = padZero(rangeArr[0] + index);
          return value;
        });
        if (_this.filter) {
          values = _this.filter(type, values);
        }
        return {
          type: type,
          values: values
        };
      });
    },
    columns: function columns() {
      var _this2 = this;
      return this.originColumns.map(function (column) {
        return {
          values: column.values.map(function (value) {
            return _this2.formatter(column.type, value);
          })
        };
      });
    }
  },
  watch: {
    columns: 'updateColumnValue',
    innerValue: function innerValue(val, oldVal) {
      if (!oldVal) {
        this.$emit('input', null);
      } else {
        this.$emit('input', val);
      }
    }
  },
  mounted: function mounted() {
    var _this3 = this;
    this.updateColumnValue();
    this.$nextTick(function () {
      _this3.updateInnerValue();
    });
  },
  methods: {
    getPicker: function getPicker() {
      return this.$refs.picker;
    },
    // https://github.com/vant-ui/vant/issues/10013
    getProxiedPicker: function getProxiedPicker() {
      var _this4 = this;
      var picker = this.$refs.picker;
      if (picker) {
        var proxy = function proxy(fn) {
          return function () {
            picker[fn].apply(picker, arguments);
            _this4.updateInnerValue();
          };
        };
        return _extends$1({}, picker, {
          setValues: proxy('setValues'),
          setIndexes: proxy('setIndexes'),
          setColumnIndex: proxy('setColumnIndex'),
          setColumnValue: proxy('setColumnValue')
        });
      }
    },
    onConfirm: function onConfirm() {
      this.$emit('input', this.innerValue);
      this.$emit('confirm', this.innerValue);
    },
    onCancel: function onCancel() {
      this.$emit('cancel');
    }
  },
  render: function render() {
    var _this5 = this;
    var h = arguments[0];
    var props = {};
    Object.keys(pickerProps).forEach(function (key) {
      props[key] = _this5[key];
    });
    return h(Picker, {
      "ref": "picker",
      "attrs": {
        "columns": this.columns,
        "readonly": this.readonly
      },
      "scopedSlots": this.$scopedSlots,
      "on": {
        "change": this.onChange,
        "confirm": this.onConfirm,
        "cancel": this.onCancel
      },
      "props": _extends$1({}, props)
    });
  }
};

var _createNamespace$Q = createNamespace('time-picker'),
  createComponent$U = _createNamespace$Q[0];
var TimePicker = createComponent$U({
  mixins: [TimePickerMixin],
  props: _extends$1({}, sharedProps, {
    minHour: {
      type: [Number, String],
      default: 0
    },
    maxHour: {
      type: [Number, String],
      default: 23
    },
    minMinute: {
      type: [Number, String],
      default: 0
    },
    maxMinute: {
      type: [Number, String],
      default: 59
    }
  }),
  computed: {
    ranges: function ranges() {
      return [{
        type: 'hour',
        range: [+this.minHour, +this.maxHour]
      }, {
        type: 'minute',
        range: [+this.minMinute, +this.maxMinute]
      }];
    }
  },
  watch: {
    filter: 'updateInnerValue',
    minHour: function minHour() {
      var _this = this;
      this.$nextTick(function () {
        _this.updateInnerValue();
      });
    },
    maxHour: function maxHour(value) {
      var _this$innerValue$spli = this.innerValue.split(':'),
        hour = _this$innerValue$spli[0],
        minute = _this$innerValue$spli[1];
      if (hour >= value) {
        this.innerValue = this.formatValue(value + ":" + minute);
        this.updateColumnValue();
      } else {
        this.updateInnerValue();
      }
    },
    minMinute: 'updateInnerValue',
    maxMinute: function maxMinute(value) {
      var _this$innerValue$spli2 = this.innerValue.split(':'),
        hour = _this$innerValue$spli2[0],
        minute = _this$innerValue$spli2[1];
      if (minute >= value) {
        this.innerValue = this.formatValue(hour + ":" + value);
        this.updateColumnValue();
      } else {
        this.updateInnerValue();
      }
    },
    value: function value(val) {
      val = this.formatValue(val);
      if (val !== this.innerValue) {
        this.innerValue = val;
        this.updateColumnValue();
      }
    }
  },
  methods: {
    formatValue: function formatValue(value) {
      if (!value) {
        value = padZero(this.minHour) + ":" + padZero(this.minMinute);
      }
      var _value$split = value.split(':'),
        hour = _value$split[0],
        minute = _value$split[1];
      hour = padZero(range$1(hour, this.minHour, this.maxHour));
      minute = padZero(range$1(minute, this.minMinute, this.maxMinute));
      return hour + ":" + minute;
    },
    updateInnerValue: function updateInnerValue() {
      var _this$getPicker$getIn = this.getPicker().getIndexes(),
        hourIndex = _this$getPicker$getIn[0],
        minuteIndex = _this$getPicker$getIn[1];
      var _this$originColumns = this.originColumns,
        hourColumn = _this$originColumns[0],
        minuteColumn = _this$originColumns[1];
      var hour = hourColumn.values[hourIndex] || hourColumn.values[0];
      var minute = minuteColumn.values[minuteIndex] || minuteColumn.values[0];
      this.innerValue = this.formatValue(hour + ":" + minute);
      this.updateColumnValue();
    },
    onChange: function onChange(picker) {
      var _this2 = this;
      this.updateInnerValue();
      this.$nextTick(function () {
        _this2.$nextTick(function () {
          // https://github.com/vant-ui/vant/issues/9775
          _this2.updateInnerValue();
          _this2.$emit('change', picker);
        });
      });
    },
    updateColumnValue: function updateColumnValue() {
      var _this3 = this;
      var formatter = this.formatter;
      var pair = this.innerValue.split(':');
      var values = [formatter('hour', pair[0]), formatter('minute', pair[1])];
      this.$nextTick(function () {
        _this3.getPicker().setValues(values);
      });
    }
  }
});

function _isNativeReflectConstruct() {
  try {
    var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
  } catch (t) {}
  return (_isNativeReflectConstruct = function _isNativeReflectConstruct() {
    return !!t;
  })();
}

function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
    return t.__proto__ = e, t;
  }, _setPrototypeOf(t, e);
}

function _construct(t, e, r) {
  if (_isNativeReflectConstruct()) return Reflect.construct.apply(null, arguments);
  var o = [null];
  o.push.apply(o, e);
  var p = new (t.bind.apply(t, o))();
  return r && _setPrototypeOf(p, r.prototype), p;
}

var currentYear = new Date().getFullYear();
var _createNamespace$P = createNamespace('date-picker'),
  createComponent$T = _createNamespace$P[0];
var DatePicker = createComponent$T({
  mixins: [TimePickerMixin],
  props: _extends$1({}, sharedProps, {
    type: {
      type: String,
      default: 'datetime'
    },
    minDate: {
      type: Date,
      default: function _default() {
        return new Date(currentYear - 10, 0, 1);
      },
      validator: isDate$1
    },
    maxDate: {
      type: Date,
      default: function _default() {
        return new Date(currentYear + 10, 11, 31);
      },
      validator: isDate$1
    }
  }),
  watch: {
    filter: 'updateInnerValue',
    minDate: function minDate() {
      var _this = this;
      this.$nextTick(function () {
        _this.updateInnerValue();
      });
    },
    maxDate: function maxDate(value) {
      if (this.innerValue.valueOf() >= value.valueOf()) {
        this.innerValue = value;
      } else {
        this.updateInnerValue();
      }
    },
    value: function value(val) {
      val = this.formatValue(val);
      if (val && val.valueOf() !== this.innerValue.valueOf()) {
        this.innerValue = val;
      }
    }
  },
  computed: {
    ranges: function ranges() {
      var _this$getBoundary = this.getBoundary('max', this.innerValue ? this.innerValue : this.minDate),
        maxYear = _this$getBoundary.maxYear,
        maxDate = _this$getBoundary.maxDate,
        maxMonth = _this$getBoundary.maxMonth,
        maxHour = _this$getBoundary.maxHour,
        maxMinute = _this$getBoundary.maxMinute;
      var _this$getBoundary2 = this.getBoundary('min', this.innerValue ? this.innerValue : this.minDate),
        minYear = _this$getBoundary2.minYear,
        minDate = _this$getBoundary2.minDate,
        minMonth = _this$getBoundary2.minMonth,
        minHour = _this$getBoundary2.minHour,
        minMinute = _this$getBoundary2.minMinute;
      var result = [{
        type: 'year',
        range: [minYear, maxYear]
      }, {
        type: 'month',
        range: [minMonth, maxMonth]
      }, {
        type: 'day',
        range: [minDate, maxDate]
      }, {
        type: 'hour',
        range: [minHour, maxHour]
      }, {
        type: 'minute',
        range: [minMinute, maxMinute]
      }];
      switch (this.type) {
        case 'date':
          result = result.slice(0, 3);
          break;
        case 'year-month':
          result = result.slice(0, 2);
          break;
        case 'month-day':
          result = result.slice(1, 3);
          break;
        case 'datehour':
          result = result.slice(0, 4);
          break;
      }
      if (this.columnsOrder) {
        var columnsOrder = this.columnsOrder.concat(result.map(function (column) {
          return column.type;
        }));
        result.sort(function (a, b) {
          return columnsOrder.indexOf(a.type) - columnsOrder.indexOf(b.type);
        });
      }
      return result;
    }
  },
  methods: {
    formatValue: function formatValue(value) {
      var _this2 = this;
      if (!isDate$1(value)) {
        return null;
      }
      var minDate = new Date(this.minDate);
      var maxDate = new Date(this.maxDate);
      var dateMethods = {
        year: 'getFullYear',
        month: 'getMonth',
        day: 'getDate',
        hour: 'getHours',
        minute: 'getMinutes'
      };
      if (this.originColumns) {
        var dateColumns = this.originColumns.map(function (_ref, index) {
          var type = _ref.type,
            values = _ref.values;
          var range = _this2.ranges[index].range;
          var minDateVal = minDate[dateMethods[type]]();
          var maxDateVal = maxDate[dateMethods[type]]();
          var min = type === 'month' ? +values[0] - 1 : +values[0];
          var max = type === 'month' ? +values[values.length - 1] - 1 : +values[values.length - 1];
          return {
            type: type,
            values: [minDateVal < range[0] ? Math.max(minDateVal, min) : min || minDateVal, maxDateVal > range[1] ? Math.min(maxDateVal, max) : max || maxDateVal]
          };
        });
        if (this.type === 'month-day') {
          var year = (this.innerValue || this.minDate).getFullYear();
          dateColumns.unshift({
            type: 'year',
            values: [year, year]
          });
        }
        var dates = Object.keys(dateMethods).map(function (type) {
          var _dateColumns$filter$;
          return (_dateColumns$filter$ = dateColumns.filter(function (item) {
            return item.type === type;
          })[0]) == null ? void 0 : _dateColumns$filter$.values;
        }).filter(function (item) {
          return item;
        });
        minDate = _construct(Date, dates.map(function (val) {
          return getTrueValue(val[0]);
        }));
        maxDate = _construct(Date, dates.map(function (val) {
          return getTrueValue(val[1]);
        }));
      }
      value = Math.max(value, minDate.getTime());
      value = Math.min(value, maxDate.getTime());
      return new Date(value);
    },
    getBoundary: function getBoundary(type, value) {
      var _ref2;
      var boundary = this[type + "Date"];
      var year = boundary.getFullYear();
      var month = 1;
      var date = 1;
      var hour = 0;
      var minute = 0;
      if (type === 'max') {
        month = 12;
        date = getMonthEndDay(value.getFullYear(), value.getMonth() + 1);
        hour = 23;
        minute = 59;
      }
      if (value.getFullYear() === year) {
        month = boundary.getMonth() + 1;
        if (value.getMonth() + 1 === month) {
          date = boundary.getDate();
          if (value.getDate() === date) {
            hour = boundary.getHours();
            if (value.getHours() === hour) {
              minute = boundary.getMinutes();
            }
          }
        }
      }
      return _ref2 = {}, _ref2[type + "Year"] = year, _ref2[type + "Month"] = month, _ref2[type + "Date"] = date, _ref2[type + "Hour"] = hour, _ref2[type + "Minute"] = minute, _ref2;
    },
    updateInnerValue: function updateInnerValue() {
      var _this3 = this;
      var type = this.type;
      var indexes = this.getPicker().getIndexes();
      var getValue = function getValue(type) {
        var index = 0;
        _this3.originColumns.forEach(function (column, columnIndex) {
          if (type === column.type) {
            index = columnIndex;
          }
        });
        var values = _this3.originColumns[index].values;
        return getTrueValue(values[indexes[index]]);
      };
      var year;
      var month;
      var day;
      if (type === 'month-day') {
        year = (this.innerValue || this.minDate).getFullYear();
        month = getValue('month');
        day = getValue('day');
      } else {
        year = getValue('year');
        month = getValue('month');
        day = type === 'year-month' ? 1 : getValue('day');
      }
      var maxDay = getMonthEndDay(year, month);
      day = day > maxDay ? maxDay : day;
      var hour = 0;
      var minute = 0;
      if (type === 'datehour') {
        hour = getValue('hour');
      }
      if (type === 'datetime') {
        hour = getValue('hour');
        minute = getValue('minute');
      }
      var value = new Date(year, month - 1, day, hour, minute);
      this.innerValue = this.formatValue(value);
    },
    onChange: function onChange(picker) {
      var _this4 = this;
      this.updateInnerValue();
      this.$nextTick(function () {
        _this4.$nextTick(function () {
          // https://github.com/vant-ui/vant/issues/9775
          _this4.updateInnerValue();
          _this4.$emit('change', picker);
        });
      });
    },
    updateColumnValue: function updateColumnValue() {
      var _this5 = this;
      var value = this.innerValue ? this.innerValue : this.minDate;
      var formatter = this.formatter;
      var values = this.originColumns.map(function (column) {
        switch (column.type) {
          case 'year':
            return formatter('year', "" + value.getFullYear());
          case 'month':
            return formatter('month', padZero(value.getMonth() + 1));
          case 'day':
            return formatter('day', padZero(value.getDate()));
          case 'hour':
            return formatter('hour', padZero(value.getHours()));
          case 'minute':
            return formatter('minute', padZero(value.getMinutes()));
          default:
            // no default
            return null;
        }
      });
      this.$nextTick(function () {
        _this5.getPicker().setValues(values);
      });
    }
  }
});

var _createNamespace$O = createNamespace('datetime-picker'),
  createComponent$S = _createNamespace$O[0],
  bem$N = _createNamespace$O[1];
var DatetimePicker = createComponent$S({
  props: _extends$1({}, TimePicker.props, DatePicker.props),
  methods: {
    // @exposed-api
    getPicker: function getPicker() {
      return this.$refs.root.getProxiedPicker();
    }
  },
  render: function render() {
    var h = arguments[0];
    var Component = this.type === 'time' ? TimePicker : DatePicker;
    return h(Component, {
      "ref": "root",
      "class": bem$N(),
      "scopedSlots": this.$scopedSlots,
      "props": _extends$1({}, this.$props),
      "on": _extends$1({}, this.$listeners)
    });
  }
});

var _createNamespace$N = createNamespace('divider'),
  createComponent$R = _createNamespace$N[0],
  bem$M = _createNamespace$N[1];
function Divider(h, props, slots, ctx) {
  var _bem;
  return h("div", _mergeJSXProps([{
    "attrs": {
      "role": "separator"
    },
    "style": {
      borderColor: props.borderColor
    },
    "class": bem$M((_bem = {
      dashed: props.dashed,
      hairline: props.hairline
    }, _bem["content-" + props.contentPosition] = slots.default, _bem))
  }, inherit(ctx, true)]), [slots.default && slots.default()]);
}
Divider.props = {
  dashed: Boolean,
  hairline: {
    type: Boolean,
    default: true
  },
  contentPosition: {
    type: String,
    default: 'center'
  }
};
var Divider$1 = createComponent$R(Divider);

// Utils
var _createNamespace$M = createNamespace('dropdown-item'),
  createComponent$Q = _createNamespace$M[0],
  bem$L = _createNamespace$M[1];
var DropdownItem = createComponent$Q({
  mixins: [PortalMixin({
    ref: 'wrapper'
  }), ChildrenMixin('vanDropdownMenu')],
  props: {
    value: null,
    title: String,
    disabled: Boolean,
    titleClass: String,
    options: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    lazyRender: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      transition: true,
      showPopup: false,
      showWrapper: false
    };
  },
  computed: {
    displayTitle: function displayTitle() {
      var _this = this;
      if (this.title) {
        return this.title;
      }
      var match = this.options.filter(function (option) {
        return option.value === _this.value;
      });
      return match.length ? match[0].text : '';
    }
  },
  watch: {
    showPopup: function showPopup(val) {
      this.bindScroll(val);
    }
  },
  beforeCreate: function beforeCreate() {
    var _this2 = this;
    var createEmitter = function createEmitter(eventName) {
      return function () {
        return _this2.$emit(eventName);
      };
    };
    this.onOpen = createEmitter('open');
    this.onClose = createEmitter('close');
    this.onOpened = createEmitter('opened');
  },
  methods: {
    // @exposed-api
    toggle: function toggle(show, options) {
      if (show === void 0) {
        show = !this.showPopup;
      }
      if (options === void 0) {
        options = {};
      }
      if (show === this.showPopup) {
        return;
      }
      this.transition = !options.immediate;
      this.showPopup = show;
      if (show) {
        this.parent.updateOffset();
        this.showWrapper = true;
      }
    },
    bindScroll: function bindScroll(bind) {
      var scroller = this.parent.scroller;
      var action = bind ? on$2 : off$2;
      action(scroller, 'scroll', this.onScroll, true);
    },
    onScroll: function onScroll() {
      this.parent.updateOffset();
    },
    onClickWrapper: function onClickWrapper(event) {
      // prevent being identified as clicking outside and closed when use get-contaienr
      if (this.getContainer) {
        event.stopPropagation();
      }
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    var _this$parent = this.parent,
      zIndex = _this$parent.zIndex,
      offset = _this$parent.offset,
      overlay = _this$parent.overlay,
      duration = _this$parent.duration,
      direction = _this$parent.direction,
      activeColor = _this$parent.activeColor,
      closeOnClickOverlay = _this$parent.closeOnClickOverlay;
    var Options = this.options.map(function (option) {
      var active = option.value === _this3.value;
      return h(Cell$1, {
        "attrs": {
          "clickable": true,
          "icon": option.icon,
          "title": option.text
        },
        "key": option.value,
        "class": bem$L('option', {
          active: active
        }),
        "style": {
          color: active ? activeColor : ''
        },
        "on": {
          "click": function click() {
            _this3.showPopup = false;
            if (option.value !== _this3.value) {
              _this3.$emit('input', option.value);
              _this3.$emit('change', option.value);
            }
          }
        }
      }, [active && h(Icon$3, {
        "class": bem$L('icon'),
        "attrs": {
          "color": activeColor,
          "name": "success"
        }
      })]);
    });
    var style = {
      zIndex: zIndex
    };
    if (direction === 'down') {
      style.top = offset + "px";
    } else {
      style.bottom = offset + "px";
    }
    return h("div", [h("div", {
      "directives": [{
        name: "show",
        value: this.showWrapper
      }],
      "ref": "wrapper",
      "style": style,
      "class": bem$L([direction]),
      "on": {
        "click": this.onClickWrapper
      }
    }, [h(Popup, {
      "attrs": {
        "overlay": overlay,
        "position": direction === 'down' ? 'top' : 'bottom',
        "duration": this.transition ? duration : 0,
        "lazyRender": this.lazyRender,
        "overlayStyle": {
          position: 'absolute'
        },
        "closeOnClickOverlay": closeOnClickOverlay
      },
      "class": bem$L('content'),
      "on": {
        "open": this.onOpen,
        "close": this.onClose,
        "opened": this.onOpened,
        "closed": function closed() {
          _this3.showWrapper = false;
          _this3.$emit('closed');
        }
      },
      "model": {
        value: _this3.showPopup,
        callback: function callback($$v) {
          _this3.showPopup = $$v;
        }
      }
    }, [Options, this.slots('default')])])]);
  }
});

/**
 * Listen to click outside event
 */
var ClickOutsideMixin = function ClickOutsideMixin(config) {
  return {
    props: {
      closeOnClickOutside: {
        type: Boolean,
        default: true
      }
    },
    data: function data() {
      var _this = this;
      var clickOutsideHandler = function clickOutsideHandler(event) {
        if (_this.closeOnClickOutside && !_this.$el.contains(event.target)) {
          _this[config.method]();
        }
      };
      return {
        clickOutsideHandler: clickOutsideHandler
      };
    },
    mounted: function mounted() {
      on$2(document, config.event, this.clickOutsideHandler);
    },
    beforeDestroy: function beforeDestroy() {
      off$2(document, config.event, this.clickOutsideHandler);
    }
  };
};

// Utils
var _createNamespace$L = createNamespace('dropdown-menu'),
  createComponent$P = _createNamespace$L[0],
  bem$K = _createNamespace$L[1];
var DropdownMenu = createComponent$P({
  mixins: [ParentMixin('vanDropdownMenu'), ClickOutsideMixin({
    event: 'click',
    method: 'onClickOutside'
  })],
  props: {
    zIndex: [Number, String],
    activeColor: String,
    overlay: {
      type: Boolean,
      default: true
    },
    duration: {
      type: [Number, String],
      default: 0.2
    },
    direction: {
      type: String,
      default: 'down'
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      offset: 0
    };
  },
  computed: {
    scroller: function scroller() {
      return getScroller(this.$el);
    },
    opened: function opened() {
      return this.children.some(function (item) {
        return item.showWrapper;
      });
    },
    barStyle: function barStyle() {
      if (this.opened && isDef(this.zIndex)) {
        return {
          zIndex: 1 + this.zIndex
        };
      }
    }
  },
  methods: {
    updateOffset: function updateOffset() {
      if (!this.$refs.bar) {
        return;
      }
      var rect = this.$refs.bar.getBoundingClientRect();
      if (this.direction === 'down') {
        this.offset = rect.bottom;
      } else {
        this.offset = window.innerHeight - rect.top;
      }
    },
    toggleItem: function toggleItem(active) {
      this.children.forEach(function (item, index) {
        if (index === active) {
          item.toggle();
        } else if (item.showPopup) {
          item.toggle(false, {
            immediate: true
          });
        }
      });
    },
    onClickOutside: function onClickOutside() {
      this.children.forEach(function (item) {
        item.toggle(false);
      });
    }
  },
  render: function render() {
    var _this = this;
    var h = arguments[0];
    var Titles = this.children.map(function (item, index) {
      return h("div", {
        "attrs": {
          "role": "button",
          "tabindex": item.disabled ? -1 : 0
        },
        "class": bem$K('item', {
          disabled: item.disabled
        }),
        "on": {
          "click": function click() {
            if (!item.disabled) {
              _this.toggleItem(index);
            }
          }
        }
      }, [h("span", {
        "class": [bem$K('title', {
          active: item.showPopup,
          down: item.showPopup === (_this.direction === 'down')
        }), item.titleClass],
        "style": {
          color: item.showPopup ? _this.activeColor : ''
        }
      }, [h("div", {
        "class": "van-ellipsis"
      }, [item.slots('title') || item.displayTitle])])]);
    });
    return h("div", {
      "class": bem$K()
    }, [h("div", {
      "ref": "bar",
      "style": this.barStyle,
      "class": bem$K('bar', {
        opened: this.opened
      })
    }, [Titles]), this.slots('default')]);
  }
});

var prefix = 'van-empty-network-';
var Network = {
  render: function render() {
    var h = arguments[0];
    var genStop = function genStop(color, offset, opacity) {
      return h("stop", {
        "attrs": {
          "stop-color": color,
          "offset": offset + "%",
          "stop-opacity": opacity
        }
      });
    };
    return h("svg", {
      "attrs": {
        "viewBox": "0 0 160 160",
        "xmlns": "http://www.w3.org/2000/svg"
      }
    }, [h("defs", [h("linearGradient", {
      "attrs": {
        "id": prefix + "1",
        "x1": "64.022%",
        "y1": "100%",
        "x2": "64.022%",
        "y2": "0%"
      }
    }, [genStop('#FFF', 0, 0.5), genStop('#F2F3F5', 100)]), h("linearGradient", {
      "attrs": {
        "id": prefix + "2",
        "x1": "50%",
        "y1": "0%",
        "x2": "50%",
        "y2": "84.459%"
      }
    }, [genStop('#EBEDF0', 0), genStop('#DCDEE0', 100, 0)]), h("linearGradient", {
      "attrs": {
        "id": prefix + "3",
        "x1": "100%",
        "y1": "0%",
        "x2": "100%",
        "y2": "100%"
      }
    }, [genStop('#EAEDF0', 0), genStop('#DCDEE0', 100)]), h("linearGradient", {
      "attrs": {
        "id": prefix + "4",
        "x1": "100%",
        "y1": "100%",
        "x2": "100%",
        "y2": "0%"
      }
    }, [genStop('#EAEDF0', 0), genStop('#DCDEE0', 100)]), h("linearGradient", {
      "attrs": {
        "id": prefix + "5",
        "x1": "0%",
        "y1": "43.982%",
        "x2": "100%",
        "y2": "54.703%"
      }
    }, [genStop('#EAEDF0', 0), genStop('#DCDEE0', 100)]), h("linearGradient", {
      "attrs": {
        "id": prefix + "6",
        "x1": "94.535%",
        "y1": "43.837%",
        "x2": "5.465%",
        "y2": "54.948%"
      }
    }, [genStop('#EAEDF0', 0), genStop('#DCDEE0', 100)]), h("radialGradient", {
      "attrs": {
        "id": prefix + "7",
        "cx": "50%",
        "cy": "0%",
        "fx": "50%",
        "fy": "0%",
        "r": "100%",
        "gradientTransform": "matrix(0 1 -.54835 0 .5 -.5)"
      }
    }, [genStop('#EBEDF0', 0), genStop('#FFF', 100, 0)])]), h("g", {
      "attrs": {
        "fill": "none",
        "fill-rule": "evenodd"
      }
    }, [h("g", {
      "attrs": {
        "opacity": ".8"
      }
    }, [h("path", {
      "attrs": {
        "d": "M0 124V46h20v20h14v58H0z",
        "fill": "url(#" + prefix + "1)",
        "transform": "matrix(-1 0 0 1 36 7)"
      }
    }), h("path", {
      "attrs": {
        "d": "M121 8h22.231v14H152v77.37h-31V8z",
        "fill": "url(#" + prefix + "1)",
        "transform": "translate(2 7)"
      }
    })]), h("path", {
      "attrs": {
        "fill": "url(#" + prefix + "7)",
        "d": "M0 139h160v21H0z"
      }
    }), h("path", {
      "attrs": {
        "d": "M37 18a7 7 0 013 13.326v26.742c0 1.23-.997 2.227-2.227 2.227h-1.546A2.227 2.227 0 0134 58.068V31.326A7 7 0 0137 18z",
        "fill": "url(#" + prefix + "2)",
        "fill-rule": "nonzero",
        "transform": "translate(43 36)"
      }
    }), h("g", {
      "attrs": {
        "opacity": ".6",
        "stroke-linecap": "round",
        "stroke-width": "7"
      }
    }, [h("path", {
      "attrs": {
        "d": "M20.875 11.136a18.868 18.868 0 00-5.284 13.121c0 5.094 2.012 9.718 5.284 13.12",
        "stroke": "url(#" + prefix + "3)",
        "transform": "translate(43 36)"
      }
    }), h("path", {
      "attrs": {
        "d": "M9.849 0C3.756 6.225 0 14.747 0 24.146c0 9.398 3.756 17.92 9.849 24.145",
        "stroke": "url(#" + prefix + "3)",
        "transform": "translate(43 36)"
      }
    }), h("path", {
      "attrs": {
        "d": "M57.625 11.136a18.868 18.868 0 00-5.284 13.121c0 5.094 2.012 9.718 5.284 13.12",
        "stroke": "url(#" + prefix + "4)",
        "transform": "rotate(-180 76.483 42.257)"
      }
    }), h("path", {
      "attrs": {
        "d": "M73.216 0c-6.093 6.225-9.849 14.747-9.849 24.146 0 9.398 3.756 17.92 9.849 24.145",
        "stroke": "url(#" + prefix + "4)",
        "transform": "rotate(-180 89.791 42.146)"
      }
    })]), h("g", {
      "attrs": {
        "transform": "translate(31 105)",
        "fill-rule": "nonzero"
      }
    }, [h("rect", {
      "attrs": {
        "fill": "url(#" + prefix + "5)",
        "width": "98",
        "height": "34",
        "rx": "2"
      }
    }), h("rect", {
      "attrs": {
        "fill": "#FFF",
        "x": "9",
        "y": "8",
        "width": "80",
        "height": "18",
        "rx": "1.114"
      }
    }), h("rect", {
      "attrs": {
        "fill": "url(#" + prefix + "6)",
        "x": "15",
        "y": "12",
        "width": "18",
        "height": "6",
        "rx": "1.114"
      }
    })])])]);
  }
};

var _createNamespace$K = createNamespace('empty'),
  createComponent$O = _createNamespace$K[0],
  bem$J = _createNamespace$K[1];
var PRESETS = ['error', 'search', 'default'];
var Empty = createComponent$O({
  props: {
    imageSize: [Number, String],
    description: String,
    image: {
      type: String,
      default: 'default'
    }
  },
  methods: {
    genImageContent: function genImageContent() {
      var h = this.$createElement;
      var slots = this.slots('image');
      if (slots) {
        return slots;
      }
      if (this.image === 'network') {
        return h(Network);
      }
      var image = this.image;
      if (PRESETS.indexOf(image) !== -1) {
        image = "https://img01.yzcdn.cn/vant/empty-image-" + image + ".png";
      }
      return h("img", {
        "attrs": {
          "src": image
        }
      });
    },
    genImage: function genImage() {
      var h = this.$createElement;
      var imageStyle = {
        width: addUnit(this.imageSize),
        height: addUnit(this.imageSize)
      };
      return h("div", {
        "class": bem$J('image'),
        "style": imageStyle
      }, [this.genImageContent()]);
    },
    genDescription: function genDescription() {
      var h = this.$createElement;
      var description = this.slots('description') || this.description;
      if (description) {
        return h("p", {
          "class": bem$J('description')
        }, [description]);
      }
    },
    genBottom: function genBottom() {
      var h = this.$createElement;
      var slot = this.slots();
      if (slot) {
        return h("div", {
          "class": bem$J('bottom')
        }, [slot]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$J()
    }, [this.genImage(), this.genDescription(), this.genBottom()]);
  }
});

var _createNamespace$J = createNamespace('form'),
  createComponent$N = _createNamespace$J[0],
  bem$I = _createNamespace$J[1];
var Form = createComponent$N({
  props: {
    colon: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    labelWidth: [Number, String],
    labelAlign: String,
    inputAlign: String,
    scrollToError: Boolean,
    validateFirst: Boolean,
    errorMessageAlign: String,
    submitOnEnter: {
      type: Boolean,
      default: true
    },
    validateTrigger: {
      type: String,
      default: 'onBlur'
    },
    showError: {
      type: Boolean,
      default: true
    },
    showErrorMessage: {
      type: Boolean,
      default: true
    }
  },
  provide: function provide() {
    return {
      vanForm: this
    };
  },
  data: function data() {
    return {
      fields: []
    };
  },
  methods: {
    getFieldsByNames: function getFieldsByNames(names) {
      if (names) {
        return this.fields.filter(function (field) {
          return names.indexOf(field.name) !== -1;
        });
      }
      return this.fields;
    },
    validateSeq: function validateSeq(names) {
      var _this = this;
      return new Promise(function (resolve, reject) {
        var errors = [];
        var fields = _this.getFieldsByNames(names);
        fields.reduce(function (promise, field) {
          return promise.then(function () {
            if (!errors.length) {
              return field.validate().then(function (error) {
                if (error) {
                  errors.push(error);
                }
              });
            }
          });
        }, Promise.resolve()).then(function () {
          if (errors.length) {
            reject(errors);
          } else {
            resolve();
          }
        });
      });
    },
    validateFields: function validateFields(names) {
      var _this2 = this;
      return new Promise(function (resolve, reject) {
        var fields = _this2.getFieldsByNames(names);
        Promise.all(fields.map(function (item) {
          return item.validate();
        })).then(function (errors) {
          errors = errors.filter(function (item) {
            return item;
          });
          if (errors.length) {
            reject(errors);
          } else {
            resolve();
          }
        });
      });
    },
    // @exposed-api
    validate: function validate(name) {
      if (name && !Array.isArray(name)) {
        return this.validateField(name);
      }
      return this.validateFirst ? this.validateSeq(name) : this.validateFields(name);
    },
    validateField: function validateField(name) {
      var matched = this.fields.filter(function (item) {
        return item.name === name;
      });
      if (matched.length) {
        return new Promise(function (resolve, reject) {
          matched[0].validate().then(function (error) {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          });
        });
      }
      return Promise.reject();
    },
    // @exposed-api
    resetValidation: function resetValidation(name) {
      if (name && !Array.isArray(name)) {
        name = [name];
      }
      var fields = this.getFieldsByNames(name);
      fields.forEach(function (item) {
        item.resetValidation();
      });
    },
    // @exposed-api
    scrollToField: function scrollToField(name, options) {
      this.fields.some(function (item) {
        if (item.name === name) {
          item.$el.scrollIntoView(options);
          return true;
        }
        return false;
      });
    },
    addField: function addField(field) {
      this.fields.push(field);
      sortChildren(this.fields, this);
    },
    removeField: function removeField(field) {
      this.fields = this.fields.filter(function (item) {
        return item !== field;
      });
    },
    getValues: function getValues() {
      return this.fields.reduce(function (form, field) {
        form[field.name] = field.formValue;
        return form;
      }, {});
    },
    onSubmit: function onSubmit(event) {
      event.preventDefault();
      this.submit();
    },
    // @exposed-api
    submit: function submit() {
      var _this3 = this;
      var values = this.getValues();
      this.validate().then(function () {
        _this3.$emit('submit', values);
      }).catch(function (errors) {
        _this3.$emit('failed', {
          values: values,
          errors: errors
        });
        if (_this3.scrollToError) {
          _this3.scrollToField(errors[0].name);
        }
      });
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("form", {
      "class": bem$I(),
      "on": {
        "submit": this.onSubmit
      }
    }, [this.slots()]);
  }
});

var _createNamespace$I = createNamespace('goods-action-icon'),
  createComponent$M = _createNamespace$I[0],
  bem$H = _createNamespace$I[1];
var GoodsActionIcon = createComponent$M({
  mixins: [ChildrenMixin('vanGoodsAction')],
  props: _extends$1({}, routeProps, {
    dot: Boolean,
    text: String,
    icon: String,
    color: String,
    // @deprecated
    info: [Number, String],
    badge: [Number, String],
    iconClass: null
  }),
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
      route(this.$router, this);
    },
    genIcon: function genIcon() {
      var _this$badge;
      var h = this.$createElement;
      var slot = this.slots('icon');
      var info = (_this$badge = this.badge) != null ? _this$badge : this.info;
      if (slot) {
        return h("div", {
          "class": bem$H('icon')
        }, [slot, h(Info$1, {
          "attrs": {
            "dot": this.dot,
            "info": info
          }
        })]);
      }
      return h(Icon$3, {
        "class": [bem$H('icon'), this.iconClass],
        "attrs": {
          "tag": "div",
          "dot": this.dot,
          "name": this.icon,
          "badge": info,
          "color": this.color
        }
      });
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "attrs": {
        "role": "button",
        "tabindex": "0"
      },
      "class": bem$H(),
      "on": {
        "click": this.onClick
      }
    }, [this.genIcon(), this.slots() || this.text]);
  }
});

var _createNamespace$H = createNamespace('grid'),
  createComponent$L = _createNamespace$H[0],
  bem$G = _createNamespace$H[1];
var Grid = createComponent$L({
  mixins: [ParentMixin('vanGrid')],
  props: {
    square: Boolean,
    gutter: [Number, String],
    iconSize: [Number, String],
    direction: String,
    clickable: Boolean,
    columnNum: {
      type: [Number, String],
      default: 4
    },
    center: {
      type: Boolean,
      default: true
    },
    border: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    style: function style() {
      var gutter = this.gutter;
      if (gutter) {
        return {
          paddingLeft: addUnit(gutter)
        };
      }
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    return h("div", {
      "style": this.style,
      "class": [bem$G(), (_ref = {}, _ref[BORDER_TOP] = this.border && !this.gutter, _ref)]
    }, [this.slots()]);
  }
});

var _createNamespace$G = createNamespace('grid-item'),
  createComponent$K = _createNamespace$G[0],
  bem$F = _createNamespace$G[1];
var GridItem = createComponent$K({
  mixins: [ChildrenMixin('vanGrid')],
  props: _extends$1({}, routeProps, {
    dot: Boolean,
    text: String,
    icon: String,
    iconPrefix: String,
    // @deprecated
    info: [Number, String],
    badge: [Number, String]
  }),
  computed: {
    style: function style() {
      var _this$parent = this.parent,
        square = _this$parent.square,
        gutter = _this$parent.gutter,
        columnNum = _this$parent.columnNum;
      var percent = 100 / columnNum + "%";
      var style = {
        flexBasis: percent
      };
      if (square) {
        style.paddingTop = percent;
      } else if (gutter) {
        var gutterValue = addUnit(gutter);
        style.paddingRight = gutterValue;
        if (this.index >= columnNum) {
          style.marginTop = gutterValue;
        }
      }
      return style;
    },
    contentStyle: function contentStyle() {
      var _this$parent2 = this.parent,
        square = _this$parent2.square,
        gutter = _this$parent2.gutter;
      if (square && gutter) {
        var gutterValue = addUnit(gutter);
        return {
          right: gutterValue,
          bottom: gutterValue,
          height: 'auto'
        };
      }
    }
  },
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
      route(this.$router, this);
    },
    genIcon: function genIcon() {
      var _this$badge;
      var h = this.$createElement;
      var iconSlot = this.slots('icon');
      var info = (_this$badge = this.badge) != null ? _this$badge : this.info;
      if (iconSlot) {
        return h("div", {
          "class": bem$F('icon-wrapper')
        }, [iconSlot, h(Info$1, {
          "attrs": {
            "dot": this.dot,
            "info": info
          }
        })]);
      }
      if (this.icon) {
        return h(Icon$3, {
          "attrs": {
            "name": this.icon,
            "dot": this.dot,
            "badge": info,
            "size": this.parent.iconSize,
            "classPrefix": this.iconPrefix
          },
          "class": bem$F('icon')
        });
      }
    },
    getText: function getText() {
      var h = this.$createElement;
      var textSlot = this.slots('text');
      if (textSlot) {
        return textSlot;
      }
      if (this.text) {
        return h("span", {
          "class": bem$F('text')
        }, [this.text]);
      }
    },
    genContent: function genContent() {
      var slot = this.slots();
      if (slot) {
        return slot;
      }
      return [this.genIcon(), this.getText()];
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    var _this$parent3 = this.parent,
      center = _this$parent3.center,
      border = _this$parent3.border,
      square = _this$parent3.square,
      gutter = _this$parent3.gutter,
      direction = _this$parent3.direction,
      clickable = _this$parent3.clickable;
    return h("div", {
      "class": [bem$F({
        square: square
      })],
      "style": this.style
    }, [h("div", {
      "style": this.contentStyle,
      "attrs": {
        "role": clickable ? 'button' : null,
        "tabindex": clickable ? 0 : null
      },
      "class": [bem$F('content', [direction, {
        center: center,
        square: square,
        clickable: clickable,
        surround: border && gutter
      }]), (_ref = {}, _ref[BORDER] = border, _ref)],
      "on": {
        "click": this.onClick
      }
    }, [this.genContent()])]);
  }
});

var _createNamespace$F = createNamespace('image-preview'),
  createComponent$J = _createNamespace$F[0],
  bem$E = _createNamespace$F[1];

// Utils
var _createNamespace$E = createNamespace('swipe'),
  createComponent$I = _createNamespace$E[0],
  bem$D = _createNamespace$E[1];
var Swipe = createComponent$I({
  mixins: [TouchMixin, ParentMixin('vanSwipe'), BindEventMixin(function (bind, isBind) {
    bind(window, 'resize', this.resize, true);
    bind(window, 'orientationchange', this.resize, true);
    bind(window, 'visibilitychange', this.onVisibilityChange);
    if (isBind) {
      this.initialize();
    } else {
      this.clear();
    }
  })],
  props: {
    width: [Number, String],
    height: [Number, String],
    autoplay: [Number, String],
    vertical: Boolean,
    lazyRender: Boolean,
    indicatorColor: String,
    loop: {
      type: Boolean,
      default: true
    },
    duration: {
      type: [Number, String],
      default: 500
    },
    touchable: {
      type: Boolean,
      default: true
    },
    initialSwipe: {
      type: [Number, String],
      default: 0
    },
    showIndicators: {
      type: Boolean,
      default: true
    },
    stopPropagation: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      rect: null,
      offset: 0,
      active: 0,
      deltaX: 0,
      deltaY: 0,
      swiping: false,
      computedWidth: 0,
      computedHeight: 0
    };
  },
  watch: {
    children: function children() {
      this.initialize();
    },
    initialSwipe: function initialSwipe() {
      this.initialize();
    },
    autoplay: function autoplay(_autoplay) {
      if (_autoplay > 0) {
        this.autoPlay();
      } else {
        this.clear();
      }
    }
  },
  computed: {
    count: function count() {
      return this.children.length;
    },
    maxCount: function maxCount() {
      return Math.ceil(Math.abs(this.minOffset) / this.size);
    },
    delta: function delta() {
      return this.vertical ? this.deltaY : this.deltaX;
    },
    size: function size() {
      return this[this.vertical ? 'computedHeight' : 'computedWidth'];
    },
    trackSize: function trackSize() {
      return this.count * this.size;
    },
    activeIndicator: function activeIndicator() {
      return (this.active + this.count) % this.count;
    },
    isCorrectDirection: function isCorrectDirection() {
      var expect = this.vertical ? 'vertical' : 'horizontal';
      return this.direction === expect;
    },
    trackStyle: function trackStyle() {
      var style = {
        transitionDuration: (this.swiping ? 0 : this.duration) + "ms",
        transform: "translate" + (this.vertical ? 'Y' : 'X') + "(" + this.offset + "px)"
      };
      if (this.size) {
        var mainAxis = this.vertical ? 'height' : 'width';
        var crossAxis = this.vertical ? 'width' : 'height';
        style[mainAxis] = this.trackSize + "px";
        style[crossAxis] = this[crossAxis] ? this[crossAxis] + "px" : '';
      }
      return style;
    },
    indicatorStyle: function indicatorStyle() {
      return {
        backgroundColor: this.indicatorColor
      };
    },
    minOffset: function minOffset() {
      return (this.vertical ? this.rect.height : this.rect.width) - this.size * this.count;
    }
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$refs.track);
  },
  methods: {
    // initialize swipe position
    initialize: function initialize(active) {
      if (active === void 0) {
        active = +this.initialSwipe;
      }
      if (!this.$el || isHidden(this.$el)) {
        return;
      }
      clearTimeout(this.timer);
      var rect = {
        width: this.$el.offsetWidth,
        height: this.$el.offsetHeight
      };
      this.rect = rect;
      this.swiping = true;
      this.active = active;
      this.computedWidth = +this.width || rect.width;
      this.computedHeight = +this.height || rect.height;
      this.offset = this.getTargetOffset(active);
      this.children.forEach(function (swipe) {
        swipe.offset = 0;
      });
      this.autoPlay();
    },
    // @exposed-api
    resize: function resize() {
      this.initialize(this.activeIndicator);
    },
    onVisibilityChange: function onVisibilityChange() {
      if (document.hidden) {
        this.clear();
      } else {
        this.autoPlay();
      }
    },
    onTouchStart: function onTouchStart(event) {
      if (!this.touchable) return;
      this.clear();
      this.touchStartTime = Date.now();
      this.touchStart(event);
      this.correctPosition();
    },
    onTouchMove: function onTouchMove(event) {
      if (!this.touchable || !this.swiping) return;
      this.touchMove(event);
      if (this.isCorrectDirection) {
        preventDefault(event, this.stopPropagation);
        this.move({
          offset: this.delta
        });
      }
    },
    onTouchEnd: function onTouchEnd() {
      if (!this.touchable || !this.swiping) return;
      var size = this.size,
        delta = this.delta;
      var duration = Date.now() - this.touchStartTime;
      var speed = delta / duration;
      var shouldSwipe = Math.abs(speed) > 0.25 || Math.abs(delta) > size / 2;
      if (shouldSwipe && this.isCorrectDirection) {
        var offset = this.vertical ? this.offsetY : this.offsetX;
        var pace = 0;
        if (this.loop) {
          pace = offset > 0 ? delta > 0 ? -1 : 1 : 0;
        } else {
          pace = -Math[delta > 0 ? 'ceil' : 'floor'](delta / size);
        }
        this.move({
          pace: pace,
          emitChange: true
        });
      } else if (delta) {
        this.move({
          pace: 0
        });
      }
      this.swiping = false;
      this.autoPlay();
    },
    getTargetActive: function getTargetActive(pace) {
      var active = this.active,
        count = this.count,
        maxCount = this.maxCount;
      if (pace) {
        if (this.loop) {
          return range$1(active + pace, -1, count);
        }
        return range$1(active + pace, 0, maxCount);
      }
      return active;
    },
    getTargetOffset: function getTargetOffset(targetActive, offset) {
      if (offset === void 0) {
        offset = 0;
      }
      var currentPosition = targetActive * this.size;
      if (!this.loop) {
        currentPosition = Math.min(currentPosition, -this.minOffset);
      }
      var targetOffset = offset - currentPosition;
      if (!this.loop) {
        targetOffset = range$1(targetOffset, this.minOffset, 0);
      }
      return targetOffset;
    },
    move: function move(_ref) {
      var _ref$pace = _ref.pace,
        pace = _ref$pace === void 0 ? 0 : _ref$pace,
        _ref$offset = _ref.offset,
        offset = _ref$offset === void 0 ? 0 : _ref$offset,
        emitChange = _ref.emitChange;
      var loop = this.loop,
        count = this.count,
        active = this.active,
        children = this.children,
        trackSize = this.trackSize,
        minOffset = this.minOffset;
      if (count <= 1) {
        return;
      }
      var targetActive = this.getTargetActive(pace);
      var targetOffset = this.getTargetOffset(targetActive, offset); // auto move first and last swipe in loop mode

      if (loop) {
        if (children[0] && targetOffset !== minOffset) {
          var outRightBound = targetOffset < minOffset;
          children[0].offset = outRightBound ? trackSize : 0;
        }
        if (children[count - 1] && targetOffset !== 0) {
          var outLeftBound = targetOffset > 0;
          children[count - 1].offset = outLeftBound ? -trackSize : 0;
        }
      }
      this.active = targetActive;
      this.offset = targetOffset;
      if (emitChange && targetActive !== active) {
        this.$emit('change', this.activeIndicator);
      }
    },
    // @exposed-api
    prev: function prev() {
      var _this = this;
      this.correctPosition();
      this.resetTouchStatus();
      doubleRaf(function () {
        _this.swiping = false;
        _this.move({
          pace: -1,
          emitChange: true
        });
      });
    },
    // @exposed-api
    next: function next() {
      var _this2 = this;
      this.correctPosition();
      this.resetTouchStatus();
      doubleRaf(function () {
        _this2.swiping = false;
        _this2.move({
          pace: 1,
          emitChange: true
        });
      });
    },
    // @exposed-api
    swipeTo: function swipeTo(index, options) {
      var _this3 = this;
      if (options === void 0) {
        options = {};
      }
      this.correctPosition();
      this.resetTouchStatus();
      doubleRaf(function () {
        var targetIndex;
        if (_this3.loop && index === _this3.count) {
          targetIndex = _this3.active === 0 ? 0 : index;
        } else {
          targetIndex = index % _this3.count;
        }
        if (options.immediate) {
          doubleRaf(function () {
            _this3.swiping = false;
          });
        } else {
          _this3.swiping = false;
        }
        _this3.move({
          pace: targetIndex - _this3.active,
          emitChange: true
        });
      });
    },
    correctPosition: function correctPosition() {
      this.swiping = true;
      if (this.active <= -1) {
        this.move({
          pace: this.count
        });
      }
      if (this.active >= this.count) {
        this.move({
          pace: -this.count
        });
      }
    },
    clear: function clear() {
      clearTimeout(this.timer);
    },
    autoPlay: function autoPlay() {
      var _this4 = this;
      var autoplay = this.autoplay;
      if (autoplay > 0 && this.count > 1) {
        this.clear();
        this.timer = setTimeout(function () {
          _this4.next();
          _this4.autoPlay();
        }, autoplay);
      }
    },
    genIndicator: function genIndicator() {
      var _this5 = this;
      var h = this.$createElement;
      var count = this.count,
        activeIndicator = this.activeIndicator;
      var slot = this.slots('indicator');
      if (slot) {
        return slot;
      }
      if (this.showIndicators && count > 1) {
        return h("div", {
          "class": bem$D('indicators', {
            vertical: this.vertical
          })
        }, [Array.apply(void 0, Array(count)).map(function (empty, index) {
          return h("i", {
            "class": bem$D('indicator', {
              active: index === activeIndicator
            }),
            "style": index === activeIndicator ? _this5.indicatorStyle : null
          });
        })]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$D()
    }, [h("div", {
      "ref": "track",
      "style": this.trackStyle,
      "class": bem$D('track', {
        vertical: this.vertical
      })
    }, [this.slots()]), this.genIndicator()]);
  }
});

var _createNamespace$D = createNamespace('swipe-item'),
  createComponent$H = _createNamespace$D[0],
  bem$C = _createNamespace$D[1];
var SwipeItem = createComponent$H({
  mixins: [ChildrenMixin('vanSwipe')],
  data: function data() {
    return {
      offset: 0,
      inited: false,
      mounted: false
    };
  },
  mounted: function mounted() {
    var _this = this;
    this.$nextTick(function () {
      _this.mounted = true;
    });
  },
  computed: {
    style: function style() {
      var style = {};
      var _this$parent = this.parent,
        size = _this$parent.size,
        vertical = _this$parent.vertical;
      if (size) {
        style[vertical ? 'height' : 'width'] = size + "px";
      }
      if (this.offset) {
        style.transform = "translate" + (vertical ? 'Y' : 'X') + "(" + this.offset + "px)";
      }
      return style;
    },
    shouldRender: function shouldRender() {
      var index = this.index,
        inited = this.inited,
        parent = this.parent,
        mounted = this.mounted;
      if (!parent.lazyRender || inited) {
        return true;
      } // wait for all item to mount, so we can get the exact count

      if (!mounted) {
        return false;
      }
      var active = parent.activeIndicator;
      var maxActive = parent.count - 1;
      var prevActive = active === 0 && parent.loop ? maxActive : active - 1;
      var nextActive = active === maxActive && parent.loop ? 0 : active + 1;
      var shouldRender = index === active || index === prevActive || index === nextActive;
      if (shouldRender) {
        this.inited = true;
      }
      return shouldRender;
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$C(),
      "style": this.style,
      "on": _extends$1({}, this.$listeners)
    }, [this.shouldRender && this.slots()]);
  }
});

// Utils
function getDistance(touches) {
  return Math.sqrt(Math.pow(touches[0].clientX - touches[1].clientX, 2) + Math.pow(touches[0].clientY - touches[1].clientY, 2));
}
var ImagePreviewItem = {
  mixins: [TouchMixin],
  props: {
    src: String,
    show: Boolean,
    active: Number,
    minZoom: [Number, String],
    maxZoom: [Number, String],
    rootWidth: Number,
    rootHeight: Number
  },
  data: function data() {
    return {
      scale: 1,
      moveX: 0,
      moveY: 0,
      moving: false,
      zooming: false,
      imageRatio: 0,
      displayWidth: 0,
      displayHeight: 0
    };
  },
  computed: {
    vertical: function vertical() {
      var rootWidth = this.rootWidth,
        rootHeight = this.rootHeight;
      var rootRatio = rootHeight / rootWidth;
      return this.imageRatio > rootRatio;
    },
    imageStyle: function imageStyle() {
      var scale = this.scale;
      var style = {
        transitionDuration: this.zooming || this.moving ? '0s' : '.3s'
      };
      if (scale !== 1) {
        var offsetX = this.moveX / scale;
        var offsetY = this.moveY / scale;
        style.transform = "scale(" + scale + ", " + scale + ") translate(" + offsetX + "px, " + offsetY + "px)";
      }
      return style;
    },
    maxMoveX: function maxMoveX() {
      if (this.imageRatio) {
        var displayWidth = this.vertical ? this.rootHeight / this.imageRatio : this.rootWidth;
        return Math.max(0, (this.scale * displayWidth - this.rootWidth) / 2);
      }
      return 0;
    },
    maxMoveY: function maxMoveY() {
      if (this.imageRatio) {
        var displayHeight = this.vertical ? this.rootHeight : this.rootWidth * this.imageRatio;
        return Math.max(0, (this.scale * displayHeight - this.rootHeight) / 2);
      }
      return 0;
    }
  },
  watch: {
    active: 'resetScale',
    show: function show(val) {
      if (!val) {
        this.resetScale();
      }
    }
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$el);
  },
  methods: {
    resetScale: function resetScale() {
      this.setScale(1);
      this.moveX = 0;
      this.moveY = 0;
    },
    setScale: function setScale(scale) {
      scale = range$1(scale, +this.minZoom, +this.maxZoom);
      if (scale !== this.scale) {
        this.scale = scale;
        this.$emit('scale', {
          scale: this.scale,
          index: this.active
        });
      }
    },
    toggleScale: function toggleScale() {
      var scale = this.scale > 1 ? 1 : 2;
      this.setScale(scale);
      this.moveX = 0;
      this.moveY = 0;
    },
    onTouchStart: function onTouchStart(event) {
      var touches = event.touches;
      var _this$offsetX = this.offsetX,
        offsetX = _this$offsetX === void 0 ? 0 : _this$offsetX;
      this.touchStart(event);
      this.touchStartTime = new Date();
      this.fingerNum = touches.length;
      this.startMoveX = this.moveX;
      this.startMoveY = this.moveY;
      this.moving = this.fingerNum === 1 && this.scale !== 1;
      this.zooming = this.fingerNum === 2 && !offsetX;
      if (this.zooming) {
        this.startScale = this.scale;
        this.startDistance = getDistance(event.touches);
      }
    },
    onTouchMove: function onTouchMove(event) {
      var touches = event.touches;
      this.touchMove(event);
      if (this.moving || this.zooming) {
        preventDefault(event, true);
      }
      if (this.moving) {
        var moveX = this.deltaX + this.startMoveX;
        var moveY = this.deltaY + this.startMoveY;
        this.moveX = range$1(moveX, -this.maxMoveX, this.maxMoveX);
        this.moveY = range$1(moveY, -this.maxMoveY, this.maxMoveY);
      }
      if (this.zooming && touches.length === 2) {
        var distance = getDistance(touches);
        var scale = this.startScale * distance / this.startDistance;
        this.setScale(scale);
      }
    },
    onTouchEnd: function onTouchEnd(event) {
      var stopPropagation = false;
      /* istanbul ignore else */

      if (this.moving || this.zooming) {
        stopPropagation = true;
        if (this.moving && this.startMoveX === this.moveX && this.startMoveY === this.moveY) {
          stopPropagation = false;
        }
        if (!event.touches.length) {
          if (this.zooming) {
            this.moveX = range$1(this.moveX, -this.maxMoveX, this.maxMoveX);
            this.moveY = range$1(this.moveY, -this.maxMoveY, this.maxMoveY);
            this.zooming = false;
          }
          this.moving = false;
          this.startMoveX = 0;
          this.startMoveY = 0;
          this.startScale = 1;
          if (this.scale < 1) {
            this.resetScale();
          }
        }
      } // eliminate tap delay on safari

      preventDefault(event, stopPropagation);
      this.checkTap();
      this.resetTouchStatus();
    },
    checkTap: function checkTap() {
      var _this = this;
      if (this.fingerNum > 1) {
        return;
      }
      var _this$offsetX2 = this.offsetX,
        offsetX = _this$offsetX2 === void 0 ? 0 : _this$offsetX2,
        _this$offsetY = this.offsetY,
        offsetY = _this$offsetY === void 0 ? 0 : _this$offsetY;
      var deltaTime = new Date() - this.touchStartTime;
      var TAP_TIME = 250;
      var TAP_OFFSET = 5;
      if (offsetX < TAP_OFFSET && offsetY < TAP_OFFSET && deltaTime < TAP_TIME) {
        if (this.doubleTapTimer) {
          clearTimeout(this.doubleTapTimer);
          this.doubleTapTimer = null;
          this.toggleScale();
        } else {
          this.doubleTapTimer = setTimeout(function () {
            _this.$emit('close');
            _this.doubleTapTimer = null;
          }, TAP_TIME);
        }
      }
    },
    onLoad: function onLoad(event) {
      var _event$target = event.target,
        naturalWidth = _event$target.naturalWidth,
        naturalHeight = _event$target.naturalHeight;
      this.imageRatio = naturalHeight / naturalWidth;
    }
  },
  render: function render() {
    var h = arguments[0];
    var imageSlots = {
      loading: function loading() {
        return h(Loading$1, {
          "attrs": {
            "type": "spinner"
          }
        });
      }
    };
    return h(SwipeItem, {
      "class": bem$E('swipe-item')
    }, [h(Image$1, {
      "attrs": {
        "src": this.src,
        "fit": "contain"
      },
      "class": bem$E('image', {
        vertical: this.vertical
      }),
      "style": this.imageStyle,
      "scopedSlots": imageSlots,
      "on": {
        "load": this.onLoad
      }
    })]);
  }
};

// Utils
var VueImagePreview = createComponent$J({
  mixins: [TouchMixin, PopupMixin({
    skipToggleEvent: true
  }), BindEventMixin(function (bind) {
    bind(window, 'resize', this.resize, true);
    bind(window, 'orientationchange', this.resize, true);
  })],
  props: {
    className: null,
    closeable: Boolean,
    asyncClose: Boolean,
    overlayStyle: Object,
    showIndicators: Boolean,
    images: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    loop: {
      type: Boolean,
      default: true
    },
    overlay: {
      type: Boolean,
      default: true
    },
    minZoom: {
      type: [Number, String],
      default: 1 / 3
    },
    maxZoom: {
      type: [Number, String],
      default: 3
    },
    transition: {
      type: String,
      default: 'van-fade'
    },
    showIndex: {
      type: Boolean,
      default: true
    },
    swipeDuration: {
      type: [Number, String],
      default: 300
    },
    startPosition: {
      type: [Number, String],
      default: 0
    },
    overlayClass: {
      type: String,
      default: bem$E('overlay')
    },
    closeIcon: {
      type: String,
      default: 'clear'
    },
    closeOnPopstate: {
      type: Boolean,
      default: true
    },
    closeIconPosition: {
      type: String,
      default: 'top-right'
    }
  },
  data: function data() {
    return {
      active: 0,
      rootWidth: 0,
      rootHeight: 0,
      doubleClickTimer: null
    };
  },
  mounted: function mounted() {
    this.resize();
  },
  watch: {
    startPosition: 'setActive',
    value: function value(val) {
      var _this = this;
      if (val) {
        this.setActive(+this.startPosition);
        this.$nextTick(function () {
          _this.resize();
          _this.$refs.swipe.swipeTo(+_this.startPosition, {
            immediate: true
          });
        });
      } else {
        this.$emit('close', {
          index: this.active,
          url: this.images[this.active]
        });
      }
    }
  },
  methods: {
    resize: function resize() {
      if (this.$el && this.$el.getBoundingClientRect) {
        var rect = this.$el.getBoundingClientRect();
        this.rootWidth = rect.width;
        this.rootHeight = rect.height;
      }
    },
    emitClose: function emitClose() {
      if (!this.asyncClose) {
        this.$emit('input', false);
      }
    },
    emitScale: function emitScale(args) {
      this.$emit('scale', args);
    },
    setActive: function setActive(active) {
      if (active !== this.active) {
        this.active = active;
        this.$emit('change', active);
      }
    },
    genIndex: function genIndex() {
      var h = this.$createElement;
      if (this.showIndex) {
        return h("div", {
          "class": bem$E('index')
        }, [this.slots('index', {
          index: this.active
        }) || this.active + 1 + " / " + this.images.length]);
      }
    },
    genCover: function genCover() {
      var h = this.$createElement;
      var cover = this.slots('cover');
      if (cover) {
        return h("div", {
          "class": bem$E('cover')
        }, [cover]);
      }
    },
    genImages: function genImages() {
      var _this2 = this;
      var h = this.$createElement;
      return h(Swipe, {
        "ref": "swipe",
        "attrs": {
          "lazyRender": true,
          "loop": this.loop,
          "duration": this.swipeDuration,
          "initialSwipe": this.startPosition,
          "showIndicators": this.showIndicators,
          "indicatorColor": "white"
        },
        "class": bem$E('swipe'),
        "on": {
          "change": this.setActive
        }
      }, [this.images.map(function (image) {
        return h(ImagePreviewItem, {
          "attrs": {
            "src": image,
            "show": _this2.value,
            "active": _this2.active,
            "maxZoom": _this2.maxZoom,
            "minZoom": _this2.minZoom,
            "rootWidth": _this2.rootWidth,
            "rootHeight": _this2.rootHeight
          },
          "on": {
            "scale": _this2.emitScale,
            "close": _this2.emitClose
          }
        });
      })]);
    },
    genClose: function genClose() {
      var h = this.$createElement;
      if (this.closeable) {
        return h(Icon$3, {
          "attrs": {
            "role": "button",
            "name": this.closeIcon
          },
          "class": bem$E('close-icon', this.closeIconPosition),
          "on": {
            "click": this.emitClose
          }
        });
      }
    },
    onClosed: function onClosed() {
      this.$emit('closed');
    },
    // @exposed-api
    swipeTo: function swipeTo(index, options) {
      if (this.$refs.swipe) {
        this.$refs.swipe.swipeTo(index, options);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("transition", {
      "attrs": {
        "name": this.transition
      },
      "on": {
        "afterLeave": this.onClosed
      }
    }, [this.shouldRender ? h("div", {
      "directives": [{
        name: "show",
        value: this.value
      }],
      "class": [bem$E(), this.className]
    }, [this.genClose(), this.genImages(), this.genIndex(), this.genCover()]) : null]);
  }
});

var instance$3;
var defaultConfig = {
  loop: true,
  value: true,
  images: [],
  maxZoom: 3,
  minZoom: 1 / 3,
  onClose: null,
  onChange: null,
  className: '',
  showIndex: true,
  closeable: false,
  closeIcon: 'clear',
  asyncClose: false,
  transition: 'van-fade',
  getContainer: 'body',
  overlayStyle: null,
  startPosition: 0,
  swipeDuration: 300,
  showIndicators: false,
  closeOnPopstate: true,
  closeIconPosition: 'top-right'
};
var initInstance = function initInstance() {
  instance$3 = new (Vue.extend(VueImagePreview))({
    el: document.createElement('div')
  });
  document.body.appendChild(instance$3.$el);
  instance$3.$on('change', function (index) {
    if (instance$3.onChange) {
      instance$3.onChange(index);
    }
  });
  instance$3.$on('scale', function (data) {
    if (instance$3.onScale) {
      instance$3.onScale(data);
    }
  });
};
var ImagePreview = function ImagePreview(images, startPosition) {
  if (startPosition === void 0) {
    startPosition = 0;
  }

  /* istanbul ignore if */
  if (isServer$1) {
    return;
  }
  if (!instance$3) {
    initInstance();
  }
  var options = Array.isArray(images) ? {
    images: images,
    startPosition: startPosition
  } : images;
  _extends$1(instance$3, defaultConfig, options);
  instance$3.$once('input', function (show) {
    instance$3.value = show;
  });
  instance$3.$once('closed', function () {
    instance$3.images = [];
  });
  if (options.onClose) {
    instance$3.$off('close');
    instance$3.$once('close', options.onClose);
  }
  return instance$3;
};
ImagePreview.Component = VueImagePreview;
ImagePreview.install = function () {
  Vue.use(VueImagePreview);
};
var ImagePreview$1 = ImagePreview;

var _createNamespace$C = createNamespace('index-anchor'),
  createComponent$G = _createNamespace$C[0],
  bem$B = _createNamespace$C[1];
var IndexAnchor = createComponent$G({
  mixins: [ChildrenMixin('vanIndexBar', {
    indexKey: 'childrenIndex'
  })],
  props: {
    index: [Number, String]
  },
  data: function data() {
    return {
      top: 0,
      left: null,
      rect: {
        top: 0,
        height: 0
      },
      width: null,
      active: false
    };
  },
  computed: {
    sticky: function sticky() {
      return this.active && this.parent.sticky;
    },
    anchorStyle: function anchorStyle() {
      if (this.sticky) {
        return {
          zIndex: "" + this.parent.zIndex,
          left: this.left ? this.left + "px" : null,
          width: this.width ? this.width + "px" : null,
          transform: "translate3d(0, " + this.top + "px, 0)",
          color: this.parent.highlightColor
        };
      }
    }
  },
  mounted: function mounted() {
    var rect = this.$el.getBoundingClientRect();
    this.rect.height = rect.height;
  },
  methods: {
    scrollIntoView: function scrollIntoView() {
      this.$el.scrollIntoView();
    },
    getRect: function getRect(scroller, scrollerRect) {
      var el = this.$el;
      var elRect = el.getBoundingClientRect();
      this.rect.height = elRect.height;
      if (scroller === window || scroller === document.body) {
        this.rect.top = elRect.top + getRootScrollTop();
      } else {
        this.rect.top = elRect.top + getScrollTop(scroller) - scrollerRect.top;
      }
      return this.rect;
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    var sticky = this.sticky;
    return h("div", {
      "style": {
        height: sticky ? this.rect.height + "px" : null
      }
    }, [h("div", {
      "style": this.anchorStyle,
      "class": [bem$B({
        sticky: sticky
      }), (_ref = {}, _ref[BORDER_BOTTOM] = sticky, _ref)]
    }, [this.slots('default') || this.index])]);
  }
});

// Utils
function genAlphabet() {
  var indexList = [];
  var charCodeOfA = 'A'.charCodeAt(0);
  for (var i = 0; i < 26; i++) {
    indexList.push(String.fromCharCode(charCodeOfA + i));
  }
  return indexList;
}
var _createNamespace$B = createNamespace('index-bar'),
  createComponent$F = _createNamespace$B[0],
  bem$A = _createNamespace$B[1];
var IndexBar = createComponent$F({
  mixins: [TouchMixin, ParentMixin('vanIndexBar'), BindEventMixin(function (bind) {
    if (!this.scroller) {
      this.scroller = getScroller(this.$el);
    }
    bind(this.scroller, 'scroll', this.onScroll);
  })],
  props: {
    zIndex: [Number, String],
    highlightColor: String,
    sticky: {
      type: Boolean,
      default: true
    },
    stickyOffsetTop: {
      type: Number,
      default: 0
    },
    indexList: {
      type: Array,
      default: genAlphabet
    }
  },
  data: function data() {
    return {
      activeAnchorIndex: null
    };
  },
  computed: {
    sidebarStyle: function sidebarStyle() {
      if (isDef(this.zIndex)) {
        return {
          zIndex: this.zIndex + 1
        };
      }
    },
    highlightStyle: function highlightStyle() {
      var highlightColor = this.highlightColor;
      if (highlightColor) {
        return {
          color: highlightColor
        };
      }
    }
  },
  watch: {
    indexList: function indexList() {
      this.$nextTick(this.onScroll);
    },
    activeAnchorIndex: function activeAnchorIndex(value) {
      if (value) {
        this.$emit('change', value);
      }
    }
  },
  methods: {
    onScroll: function onScroll() {
      var _this = this;
      if (isHidden(this.$el)) {
        return;
      }
      var scrollTop = getScrollTop(this.scroller);
      var scrollerRect = this.getScrollerRect();
      var rects = this.children.map(function (item) {
        return item.getRect(_this.scroller, scrollerRect);
      });
      var active = this.getActiveAnchorIndex(scrollTop, rects);
      this.activeAnchorIndex = this.indexList[active];
      if (this.sticky) {
        this.children.forEach(function (item, index) {
          if (index === active || index === active - 1) {
            var rect = item.$el.getBoundingClientRect();
            item.left = rect.left;
            item.width = rect.width;
          } else {
            item.left = null;
            item.width = null;
          }
          if (index === active) {
            item.active = true;
            item.top = Math.max(_this.stickyOffsetTop, rects[index].top - scrollTop) + scrollerRect.top;
          } else if (index === active - 1) {
            var activeItemTop = rects[active].top - scrollTop;
            item.active = activeItemTop > 0;
            item.top = activeItemTop + scrollerRect.top - rects[index].height;
          } else {
            item.active = false;
          }
        });
      }
    },
    getScrollerRect: function getScrollerRect() {
      if (this.scroller.getBoundingClientRect) {
        return this.scroller.getBoundingClientRect();
      }
      return {
        top: 0,
        left: 0
      };
    },
    getActiveAnchorIndex: function getActiveAnchorIndex(scrollTop, rects) {
      for (var i = this.children.length - 1; i >= 0; i--) {
        var prevHeight = i > 0 ? rects[i - 1].height : 0;
        var reachTop = this.sticky ? prevHeight + this.stickyOffsetTop : 0;
        if (scrollTop + reachTop >= rects[i].top) {
          return i;
        }
      }
      return -1;
    },
    onClick: function onClick(event) {
      this.scrollToElement(event.target);
    },
    onTouchMove: function onTouchMove(event) {
      this.touchMove(event);
      if (this.direction === 'vertical') {
        preventDefault(event);
        var _event$touches$ = event.touches[0],
          clientX = _event$touches$.clientX,
          clientY = _event$touches$.clientY;
        var target = document.elementFromPoint(clientX, clientY);
        if (target) {
          var index = target.dataset.index;
          /* istanbul ignore else */

          if (this.touchActiveIndex !== index) {
            this.touchActiveIndex = index;
            this.scrollToElement(target);
          }
        }
      }
    },
    scrollTo: function scrollTo(index) {
      var match = this.children.filter(function (item) {
        return String(item.index) === index;
      });
      if (match[0]) {
        match[0].scrollIntoView();
        if (this.sticky && this.stickyOffsetTop) {
          setRootScrollTop(getRootScrollTop() - this.stickyOffsetTop);
        }
        this.$emit('select', match[0].index);
      }
    },
    scrollToElement: function scrollToElement(element) {
      var index = element.dataset.index;
      this.scrollTo(index);
    },
    onTouchEnd: function onTouchEnd() {
      this.active = null;
    }
  },
  render: function render() {
    var _this2 = this;
    var h = arguments[0];
    var Indexes = this.indexList.map(function (index) {
      var active = index === _this2.activeAnchorIndex;
      return h("span", {
        "class": bem$A('index', {
          active: active
        }),
        "style": active ? _this2.highlightStyle : null,
        "attrs": {
          "data-index": index
        }
      }, [index]);
    });
    return h("div", {
      "class": bem$A()
    }, [h("div", {
      "class": bem$A('sidebar'),
      "style": this.sidebarStyle,
      "on": {
        "click": this.onClick,
        "touchstart": this.touchStart,
        "touchmove": this.onTouchMove,
        "touchend": this.onTouchEnd,
        "touchcancel": this.onTouchEnd
      }
    }, [Indexes]), this.slots('default')]);
  }
});

// Utils
var _createNamespace$A = createNamespace('list'),
  createComponent$E = _createNamespace$A[0],
  bem$z = _createNamespace$A[1],
  t$e = _createNamespace$A[2];
var List = createComponent$E({
  mixins: [BindEventMixin(function (bind) {
    if (!this.scroller) {
      this.scroller = getScroller(this.$el);
    }
    bind(this.scroller, 'scroll', this.check);
  })],
  model: {
    prop: 'loading'
  },
  props: {
    error: Boolean,
    loading: Boolean,
    finished: Boolean,
    errorText: String,
    loadingText: String,
    finishedText: String,
    immediateCheck: {
      type: Boolean,
      default: true
    },
    offset: {
      type: [Number, String],
      default: 300
    },
    direction: {
      type: String,
      default: 'down'
    }
  },
  data: function data() {
    return {
      // use sync innerLoading state to avoid repeated loading in some edge cases
      innerLoading: this.loading
    };
  },
  updated: function updated() {
    this.innerLoading = this.loading;
  },
  mounted: function mounted() {
    if (this.immediateCheck) {
      this.check();
    }
  },
  watch: {
    loading: 'check',
    finished: 'check'
  },
  methods: {
    // @exposed-api
    check: function check() {
      var _this = this;
      this.$nextTick(function () {
        if (_this.innerLoading || _this.finished || _this.error) {
          return;
        }
        var el = _this.$el,
          scroller = _this.scroller,
          offset = _this.offset,
          direction = _this.direction;
        var scrollerRect;
        if (scroller.getBoundingClientRect) {
          scrollerRect = scroller.getBoundingClientRect();
        } else {
          scrollerRect = {
            top: 0,
            bottom: scroller.innerHeight
          };
        }
        var scrollerHeight = scrollerRect.bottom - scrollerRect.top;
        /* istanbul ignore next */

        if (!scrollerHeight || isHidden(el)) {
          return false;
        }
        var isReachEdge = false;
        var placeholderRect = _this.$refs.placeholder.getBoundingClientRect();
        if (direction === 'up') {
          isReachEdge = scrollerRect.top - placeholderRect.top <= offset;
        } else {
          isReachEdge = placeholderRect.bottom - scrollerRect.bottom <= offset;
        }
        if (isReachEdge) {
          _this.innerLoading = true;
          _this.$emit('input', true);
          _this.$emit('load');
        }
      });
    },
    clickErrorText: function clickErrorText() {
      this.$emit('update:error', false);
      this.check();
    },
    genLoading: function genLoading() {
      var h = this.$createElement;
      if (this.innerLoading && !this.finished) {
        return h("div", {
          "key": "loading",
          "class": bem$z('loading')
        }, [this.slots('loading') || h(Loading$1, {
          "attrs": {
            "size": "16"
          }
        }, [this.loadingText || t$e('loading')])]);
      }
    },
    genFinishedText: function genFinishedText() {
      var h = this.$createElement;
      if (this.finished) {
        var text = this.slots('finished') || this.finishedText;
        if (text) {
          return h("div", {
            "class": bem$z('finished-text')
          }, [text]);
        }
      }
    },
    genErrorText: function genErrorText() {
      var h = this.$createElement;
      if (this.error) {
        var text = this.slots('error') || this.errorText;
        if (text) {
          return h("div", {
            "on": {
              "click": this.clickErrorText
            },
            "class": bem$z('error-text')
          }, [text]);
        }
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var Placeholder = h("div", {
      "ref": "placeholder",
      "key": "placeholder",
      "class": bem$z('placeholder')
    });
    return h("div", {
      "class": bem$z(),
      "attrs": {
        "role": "feed",
        "aria-busy": this.innerLoading
      }
    }, [this.direction === 'down' ? this.slots() : Placeholder, this.genLoading(), this.genFinishedText(), this.genErrorText(), this.direction === 'up' ? this.slots() : Placeholder]);
  }
});

// Utils
var _createNamespace$z = createNamespace('nav-bar'),
  createComponent$D = _createNamespace$z[0],
  bem$y = _createNamespace$z[1];
var NavBar = createComponent$D({
  props: {
    title: String,
    fixed: Boolean,
    zIndex: [Number, String],
    leftText: String,
    rightText: String,
    leftArrow: Boolean,
    placeholder: Boolean,
    safeAreaInsetTop: Boolean,
    border: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      height: null
    };
  },
  mounted: function mounted() {
    var _this = this;
    if (this.placeholder && this.fixed) {
      var setHeight = function setHeight() {
        _this.height = _this.$refs.navBar.getBoundingClientRect().height;
      };
      setHeight(); // https://github.com/vant-ui/vant/issues/10131

      setTimeout(setHeight, 100);
    }
  },
  methods: {
    genLeft: function genLeft() {
      var h = this.$createElement;
      var leftSlot = this.slots('left');
      if (leftSlot) {
        return leftSlot;
      }
      return [this.leftArrow && h(Icon$3, {
        "class": bem$y('arrow'),
        "attrs": {
          "name": "arrow-left"
        }
      }), this.leftText && h("span", {
        "class": bem$y('text')
      }, [this.leftText])];
    },
    genRight: function genRight() {
      var h = this.$createElement;
      var rightSlot = this.slots('right');
      if (rightSlot) {
        return rightSlot;
      }
      if (this.rightText) {
        return h("span", {
          "class": bem$y('text')
        }, [this.rightText]);
      }
    },
    genNavBar: function genNavBar() {
      var _ref;
      var h = this.$createElement;
      return h("div", {
        "ref": "navBar",
        "style": {
          zIndex: this.zIndex
        },
        "class": [bem$y({
          fixed: this.fixed,
          'safe-area-inset-top': this.safeAreaInsetTop
        }), (_ref = {}, _ref[BORDER_BOTTOM] = this.border, _ref)]
      }, [h("div", {
        "class": bem$y('content')
      }, [this.hasLeft() && h("div", {
        "class": bem$y('left'),
        "on": {
          "click": this.onClickLeft
        }
      }, [this.genLeft()]), h("div", {
        "class": [bem$y('title'), 'van-ellipsis']
      }, [this.slots('title') || this.title]), this.hasRight() && h("div", {
        "class": bem$y('right'),
        "on": {
          "click": this.onClickRight
        }
      }, [this.genRight()])])]);
    },
    hasLeft: function hasLeft() {
      return this.leftArrow || this.leftText || this.slots('left');
    },
    hasRight: function hasRight() {
      return this.rightText || this.slots('right');
    },
    onClickLeft: function onClickLeft(event) {
      this.$emit('click-left', event);
    },
    onClickRight: function onClickRight(event) {
      this.$emit('click-right', event);
    }
  },
  render: function render() {
    var h = arguments[0];
    if (this.placeholder && this.fixed) {
      return h("div", {
        "class": bem$y('placeholder'),
        "style": {
          height: this.height + "px"
        }
      }, [this.genNavBar()]);
    }
    return this.genNavBar();
  }
});

var _createNamespace$y = createNamespace('notice-bar'),
  createComponent$C = _createNamespace$y[0],
  bem$x = _createNamespace$y[1];
var NoticeBar = createComponent$C({
  mixins: [BindEventMixin(function (bind) {
    // fix cache issues with forwards and back history in safari
    // see: https://guwii.com/cache-issues-with-forwards-and-back-history-in-safari/
    bind(window, 'pageshow', this.reset);
  })],
  inject: {
    vanPopup: {
      default: null
    }
  },
  props: {
    text: String,
    mode: String,
    color: String,
    leftIcon: String,
    wrapable: Boolean,
    background: String,
    scrollable: {
      type: Boolean,
      default: null
    },
    delay: {
      type: [Number, String],
      default: 1
    },
    speed: {
      type: [Number, String],
      default: 60
    }
  },
  data: function data() {
    return {
      show: true,
      offset: 0,
      duration: 0,
      wrapWidth: 0,
      contentWidth: 0
    };
  },
  watch: {
    scrollable: 'reset',
    text: {
      handler: 'reset',
      immediate: true
    }
  },
  created: function created() {
    // https://github.com/vant-ui/vant/issues/8634
    if (this.vanPopup) {
      this.vanPopup.onReopen(this.reset);
    }
  },
  activated: function activated() {
    this.reset();
  },
  methods: {
    onClickIcon: function onClickIcon(event) {
      if (this.mode === 'closeable') {
        this.show = false;
        this.$emit('close', event);
      }
    },
    onTransitionEnd: function onTransitionEnd() {
      var _this = this;
      this.offset = this.wrapWidth;
      this.duration = 0; // wait for Vue to render offset
      // using nextTick won't work in iOS14

      raf(function () {
        // use double raf to ensure animation can start
        doubleRaf(function () {
          _this.offset = -_this.contentWidth;
          _this.duration = (_this.contentWidth + _this.wrapWidth) / _this.speed;
          _this.$emit('replay');
        });
      });
    },
    // not an exposed-api, but may used by some users
    start: function start() {
      this.reset();
    },
    // @exposed-api
    reset: function reset() {
      var _this2 = this;
      var delay = isDef(this.delay) ? this.delay * 1000 : 0;
      this.offset = 0;
      this.duration = 0;
      this.wrapWidth = 0;
      this.contentWidth = 0;
      clearTimeout(this.startTimer);
      this.startTimer = setTimeout(function () {
        var _this2$$refs = _this2.$refs,
          wrap = _this2$$refs.wrap,
          content = _this2$$refs.content;
        if (!wrap || !content || _this2.scrollable === false) {
          return;
        }
        var wrapWidth = wrap.getBoundingClientRect().width;
        var contentWidth = content.getBoundingClientRect().width;
        if (_this2.scrollable || contentWidth > wrapWidth) {
          doubleRaf(function () {
            _this2.offset = -contentWidth;
            _this2.duration = contentWidth / _this2.speed;
            _this2.wrapWidth = wrapWidth;
            _this2.contentWidth = contentWidth;
          });
        }
      }, delay);
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    var slots = this.slots,
      mode = this.mode,
      leftIcon = this.leftIcon,
      onClickIcon = this.onClickIcon;
    var barStyle = {
      color: this.color,
      background: this.background
    };
    var contentStyle = {
      transform: this.offset ? "translateX(" + this.offset + "px)" : '',
      transitionDuration: this.duration + 's'
    };
    function LeftIcon() {
      var slot = slots('left-icon');
      if (slot) {
        return slot;
      }
      if (leftIcon) {
        return h(Icon$3, {
          "class": bem$x('left-icon'),
          "attrs": {
            "name": leftIcon
          }
        });
      }
    }
    function RightIcon() {
      var slot = slots('right-icon');
      if (slot) {
        return slot;
      }
      var iconName;
      if (mode === 'closeable') {
        iconName = 'cross';
      } else if (mode === 'link') {
        iconName = 'arrow';
      }
      if (iconName) {
        return h(Icon$3, {
          "class": bem$x('right-icon'),
          "attrs": {
            "name": iconName
          },
          "on": {
            "click": onClickIcon
          }
        });
      }
    }
    return h("div", {
      "attrs": {
        "role": "alert"
      },
      "directives": [{
        name: "show",
        value: this.show
      }],
      "class": bem$x({
        wrapable: this.wrapable
      }),
      "style": barStyle,
      "on": {
        "click": function click(event) {
          _this3.$emit('click', event);
        }
      }
    }, [LeftIcon(), h("div", {
      "ref": "wrap",
      "class": bem$x('wrap'),
      "attrs": {
        "role": "marquee"
      }
    }, [h("div", {
      "ref": "content",
      "class": [bem$x('content'), {
        'van-ellipsis': this.scrollable === false && !this.wrapable
      }],
      "style": contentStyle,
      "on": {
        "transitionend": this.onTransitionEnd
      }
    }, [this.slots() || this.text])]), RightIcon()]);
  }
});

var _createNamespace$x = createNamespace('notify'),
  createComponent$B = _createNamespace$x[0],
  bem$w = _createNamespace$x[1];
function Notify$1(h, props, slots, ctx) {
  var style = {
    color: props.color,
    background: props.background
  };
  return h(Popup, _mergeJSXProps([{
    "attrs": {
      "value": props.value,
      "position": "top",
      "overlay": false,
      "duration": 0.2,
      "lockScroll": false
    },
    "style": style,
    "class": [bem$w([props.type]), props.className]
  }, inherit(ctx, true)]), [(slots.default == null ? void 0 : slots.default()) || props.message]);
}
Notify$1.props = _extends$1({}, popupMixinProps, {
  color: String,
  message: [Number, String],
  duration: [Number, String],
  className: null,
  background: String,
  getContainer: [String, Function],
  type: {
    type: String,
    default: 'danger'
  }
});
var VanNotify = createComponent$B(Notify$1);

var timer;
var instance$2;
function parseOptions$1(message) {
  return isObject$2(message) ? message : {
    message: message
  };
}
function Notify(options) {
  /* istanbul ignore if */
  if (isServer$1) {
    return;
  }
  if (!instance$2) {
    instance$2 = mount(VanNotify, {
      on: {
        click: function click(event) {
          if (instance$2.onClick) {
            instance$2.onClick(event);
          }
        },
        close: function close() {
          if (instance$2.onClose) {
            instance$2.onClose();
          }
        },
        opened: function opened() {
          if (instance$2.onOpened) {
            instance$2.onOpened();
          }
        }
      }
    });
  }
  options = _extends$1({}, Notify.currentOptions, parseOptions$1(options));
  _extends$1(instance$2, options);
  clearTimeout(timer);
  if (options.duration && options.duration > 0) {
    timer = setTimeout(Notify.clear, options.duration);
  }
  return instance$2;
}
function defaultOptions() {
  return {
    type: 'danger',
    value: true,
    message: '',
    color: undefined,
    background: undefined,
    duration: 3000,
    className: '',
    onClose: null,
    onClick: null,
    onOpened: null
  };
}
Notify.clear = function () {
  if (instance$2) {
    instance$2.value = false;
  }
};
Notify.currentOptions = defaultOptions();
Notify.setDefaultOptions = function (options) {
  _extends$1(Notify.currentOptions, options);
};
Notify.resetDefaultOptions = function () {
  Notify.currentOptions = defaultOptions();
};
Notify.install = function () {
  Vue.use(VanNotify);
};
Notify.Component = VanNotify;
Vue.prototype.$notify = Notify;

var DeleteIcon = {
  render: function render() {
    var h = arguments[0];
    return h("svg", {
      "attrs": {
        "viewBox": "0 0 32 22",
        "xmlns": "http://www.w3.org/2000/svg"
      }
    }, [h("path", {
      "attrs": {
        "d": "M28.016 0A3.991 3.991 0 0132 3.987v14.026c0 2.2-1.787 3.987-3.98 3.987H10.382c-.509 0-.996-.206-1.374-.585L.89 13.09C.33 12.62 0 11.84 0 11.006c0-.86.325-1.62.887-2.08L9.01.585A1.936 1.936 0 0110.383 0zm0 1.947H10.368L2.24 10.28c-.224.226-.312.432-.312.73 0 .287.094.51.312.729l8.128 8.333h17.648a2.041 2.041 0 002.037-2.04V3.987c0-1.127-.915-2.04-2.037-2.04zM23.028 6a.96.96 0 01.678.292.95.95 0 01-.003 1.377l-3.342 3.348 3.326 3.333c.189.188.292.43.292.679 0 .248-.103.49-.292.679a.96.96 0 01-.678.292.959.959 0 01-.677-.292L18.99 12.36l-3.343 3.345a.96.96 0 01-.677.292.96.96 0 01-.678-.292.962.962 0 01-.292-.68c0-.248.104-.49.292-.679l3.342-3.348-3.342-3.348A.963.963 0 0114 6.971c0-.248.104-.49.292-.679A.96.96 0 0114.97 6a.96.96 0 01.677.292l3.358 3.348 3.345-3.348A.96.96 0 0123.028 6z",
        "fill": "currentColor"
      }
    })]);
  }
};

var CollapseIcon = {
  render: function render() {
    var h = arguments[0];
    return h("svg", {
      "attrs": {
        "viewBox": "0 0 30 24",
        "xmlns": "http://www.w3.org/2000/svg"
      }
    }, [h("path", {
      "attrs": {
        "d": "M25.877 12.843h-1.502c-.188 0-.188 0-.188.19v1.512c0 .188 0 .188.188.188h1.5c.187 0 .187 0 .187-.188v-1.511c0-.19 0-.191-.185-.191zM17.999 10.2c0 .188 0 .188.188.188h1.687c.188 0 .188 0 .188-.188V8.688c0-.187.004-.187-.186-.19h-1.69c-.187 0-.187 0-.187.19V10.2zm2.25-3.967h1.5c.188 0 .188 0 .188-.188v-1.7c0-.19 0-.19-.188-.19h-1.5c-.189 0-.189 0-.189.19v1.7c0 .188 0 .188.19.188zm2.063 4.157h3.563c.187 0 .187 0 .187-.189V4.346c0-.19.004-.19-.185-.19h-1.69c-.187 0-.187 0-.187.188v4.155h-1.688c-.187 0-.187 0-.187.189v1.514c0 .19 0 .19.187.19zM14.812 24l2.812-3.4H12l2.813 3.4zm-9-11.157H4.31c-.188 0-.188 0-.188.19v1.512c0 .188 0 .188.188.188h1.502c.187 0 .187 0 .187-.188v-1.511c0-.19.01-.191-.189-.191zm15.937 0H8.25c-.188 0-.188 0-.188.19v1.512c0 .188 0 .188.188.188h13.5c.188 0 .188 0 .188-.188v-1.511c0-.19 0-.191-.188-.191zm-11.438-2.454h1.5c.188 0 .188 0 .188-.188V8.688c0-.187 0-.187-.188-.189h-1.5c-.187 0-.187 0-.187.189V10.2c0 .188 0 .188.187.188zM27.94 0c.563 0 .917.21 1.313.567.518.466.748.757.748 1.51v14.92c0 .567-.188 1.134-.562 1.512-.376.378-.938.566-1.313.566H2.063c-.563 0-.938-.188-1.313-.566-.562-.378-.75-.945-.75-1.511V2.078C0 1.51.188.944.562.567.938.189 1.5 0 1.875 0zm-.062 2H2v14.92h25.877V2zM5.81 4.157c.19 0 .19 0 .19.189v1.762c-.003.126-.024.126-.188.126H4.249c-.126-.003-.126-.023-.126-.188v-1.7c-.187-.19 0-.19.188-.19zm10.5 2.077h1.503c.187 0 .187 0 .187-.188v-1.7c0-.19 0-.19-.187-.19h-1.502c-.188 0-.188.001-.188.19v1.7c0 .188 0 .188.188.188zM7.875 8.5c.187 0 .187.002.187.189V10.2c0 .188 0 .188-.187.188H4.249c-.126-.002-.126-.023-.126-.188V8.625c.003-.126.024-.126.188-.126zm7.875 0c.19.002.19.002.19.189v1.575c-.003.126-.024.126-.19.126h-1.563c-.126-.002-.126-.023-.126-.188V8.625c.002-.126.023-.126.189-.126zm-6-4.342c.187 0 .187 0 .187.189v1.7c0 .188 0 .188-.187.188H8.187c-.126-.003-.126-.023-.126-.188V4.283c.003-.126.024-.126.188-.126zm3.94 0c.185 0 .372 0 .372.189v1.762c-.002.126-.023.126-.187.126h-1.75C12 6.231 12 6.211 12 6.046v-1.7c0-.19.187-.19.187-.19z",
        "fill": "currentColor"
      }
    })]);
  }
};

var _createNamespace$w = createNamespace('key'),
  createComponent$A = _createNamespace$w[0],
  bem$v = _createNamespace$w[1];
var Key = createComponent$A({
  mixins: [TouchMixin],
  props: {
    type: String,
    text: [Number, String],
    color: String,
    wider: Boolean,
    large: Boolean,
    loading: Boolean
  },
  data: function data() {
    return {
      active: false
    };
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$el);
  },
  methods: {
    onTouchStart: function onTouchStart(event) {
      // compatible with Vue 2.6 event bubble bug
      event.stopPropagation();
      this.touchStart(event);
      this.active = true;
    },
    onTouchMove: function onTouchMove(event) {
      this.touchMove(event);
      if (this.direction) {
        this.active = false;
      }
    },
    onTouchEnd: function onTouchEnd(event) {
      if (this.active) {
        // eliminate tap delay on safari
        // see: https://github.com/vant-ui/vant/issues/6836
        if (!this.slots('default')) {
          event.preventDefault();
        }
        this.active = false;
        this.$emit('press', this.text, this.type);
      }
    },
    genContent: function genContent() {
      var h = this.$createElement;
      var isExtra = this.type === 'extra';
      var isDelete = this.type === 'delete';
      var text = this.slots('default') || this.text;
      if (this.loading) {
        return h(Loading$1, {
          "class": bem$v('loading-icon')
        });
      }
      if (isDelete) {
        return text || h(DeleteIcon, {
          "class": bem$v('delete-icon')
        });
      }
      if (isExtra) {
        return text || h(CollapseIcon, {
          "class": bem$v('collapse-icon')
        });
      }
      return text;
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$v('wrapper', {
        wider: this.wider
      })
    }, [h("div", {
      "attrs": {
        "role": "button",
        "tabindex": "0"
      },
      "class": bem$v([this.color, {
        large: this.large,
        active: this.active,
        delete: this.type === 'delete'
      }])
    }, [this.genContent()])]);
  }
});

var _createNamespace$v = createNamespace('number-keyboard'),
  createComponent$z = _createNamespace$v[0],
  bem$u = _createNamespace$v[1];
var NumberKeyboard = createComponent$z({
  mixins: [PortalMixin(), BindEventMixin(function (bind) {
    if (this.hideOnClickOutside) {
      bind(document.body, 'touchstart', this.onBlur);
    }
  })],
  model: {
    event: 'update:value'
  },
  props: {
    show: Boolean,
    title: String,
    zIndex: [Number, String],
    randomKeyOrder: Boolean,
    closeButtonText: String,
    deleteButtonText: String,
    closeButtonLoading: Boolean,
    theme: {
      type: String,
      default: 'default'
    },
    value: {
      type: String,
      default: ''
    },
    extraKey: {
      type: [String, Array],
      default: ''
    },
    maxlength: {
      type: [Number, String],
      default: Number.MAX_VALUE
    },
    transition: {
      type: Boolean,
      default: true
    },
    showDeleteKey: {
      type: Boolean,
      default: true
    },
    hideOnClickOutside: {
      type: Boolean,
      default: true
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    show: function show(val) {
      if (!this.transition) {
        this.$emit(val ? 'show' : 'hide');
      }
    }
  },
  computed: {
    keys: function keys() {
      if (this.theme === 'custom') {
        return this.genCustomKeys();
      }
      return this.genDefaultKeys();
    }
  },
  methods: {
    genBasicKeys: function genBasicKeys() {
      var keys = [];
      for (var i = 1; i <= 9; i++) {
        keys.push({
          text: i
        });
      }
      if (this.randomKeyOrder) {
        keys.sort(function () {
          return Math.random() > 0.5 ? 1 : -1;
        });
      }
      return keys;
    },
    genDefaultKeys: function genDefaultKeys() {
      return [].concat(this.genBasicKeys(), [{
        text: this.extraKey,
        type: 'extra'
      }, {
        text: 0
      }, {
        text: this.showDeleteKey ? this.deleteButtonText : '',
        type: this.showDeleteKey ? 'delete' : ''
      }]);
    },
    genCustomKeys: function genCustomKeys() {
      var keys = this.genBasicKeys();
      var extraKey = this.extraKey;
      var extraKeys = Array.isArray(extraKey) ? extraKey : [extraKey];
      if (extraKeys.length === 1) {
        keys.push({
          text: 0,
          wider: true
        }, {
          text: extraKeys[0],
          type: 'extra'
        });
      } else if (extraKeys.length === 2) {
        keys.push({
          text: extraKeys[0],
          type: 'extra'
        }, {
          text: 0
        }, {
          text: extraKeys[1],
          type: 'extra'
        });
      }
      return keys;
    },
    onBlur: function onBlur() {
      this.show && this.$emit('blur');
    },
    onClose: function onClose() {
      this.$emit('close');
      this.onBlur();
    },
    onAnimationEnd: function onAnimationEnd() {
      this.$emit(this.show ? 'show' : 'hide');
    },
    onPress: function onPress(text, type) {
      if (text === '') {
        if (type === 'extra') {
          this.onBlur();
        }
        return;
      }
      var value = this.value;
      if (type === 'delete') {
        this.$emit('delete');
        this.$emit('update:value', value.slice(0, value.length - 1));
      } else if (type === 'close') {
        this.onClose();
      } else if (value.length < this.maxlength) {
        this.$emit('input', text);
        this.$emit('update:value', value + text);
      }
    },
    genTitle: function genTitle() {
      var h = this.$createElement;
      var title = this.title,
        theme = this.theme,
        closeButtonText = this.closeButtonText;
      var titleLeft = this.slots('title-left');
      var showClose = closeButtonText && theme === 'default';
      var showTitle = title || showClose || titleLeft;
      if (!showTitle) {
        return;
      }
      return h("div", {
        "class": bem$u('header')
      }, [titleLeft && h("span", {
        "class": bem$u('title-left')
      }, [titleLeft]), title && h("h2", {
        "class": bem$u('title')
      }, [title]), showClose && h("button", {
        "attrs": {
          "type": "button"
        },
        "class": bem$u('close'),
        "on": {
          "click": this.onClose
        }
      }, [closeButtonText])]);
    },
    genKeys: function genKeys() {
      var _this = this;
      var h = this.$createElement;
      return this.keys.map(function (key) {
        return h(Key, {
          "key": key.text,
          "attrs": {
            "text": key.text,
            "type": key.type,
            "wider": key.wider,
            "color": key.color
          },
          "on": {
            "press": _this.onPress
          }
        }, [key.type === 'delete' && _this.slots('delete'), key.type === 'extra' && _this.slots('extra-key')]);
      });
    },
    genSidebar: function genSidebar() {
      var h = this.$createElement;
      if (this.theme === 'custom') {
        return h("div", {
          "class": bem$u('sidebar')
        }, [this.showDeleteKey && h(Key, {
          "attrs": {
            "large": true,
            "text": this.deleteButtonText,
            "type": "delete"
          },
          "on": {
            "press": this.onPress
          }
        }, [this.slots('delete')]), h(Key, {
          "attrs": {
            "large": true,
            "text": this.closeButtonText,
            "type": "close",
            "color": "blue",
            "loading": this.closeButtonLoading
          },
          "on": {
            "press": this.onPress
          }
        })]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var Title = this.genTitle();
    return h("transition", {
      "attrs": {
        "name": this.transition ? 'van-slide-up' : ''
      }
    }, [h("div", {
      "directives": [{
        name: "show",
        value: this.show
      }],
      "style": {
        zIndex: this.zIndex
      },
      "class": bem$u({
        unfit: !this.safeAreaInsetBottom,
        'with-title': Title
      }),
      "on": {
        "touchstart": stopPropagation,
        "animationend": this.onAnimationEnd,
        "webkitAnimationEnd": this.onAnimationEnd
      }
    }, [Title, h("div", {
      "class": bem$u('body')
    }, [h("div", {
      "class": bem$u('keys')
    }, [this.genKeys()]), this.genSidebar()])])]);
  }
});

var _createNamespace$u = createNamespace('pagination'),
  createComponent$y = _createNamespace$u[0],
  bem$t = _createNamespace$u[1],
  t$d = _createNamespace$u[2];
function makePage(number, text, active) {
  return {
    number: number,
    text: text,
    active: active
  };
}
var Pagination = createComponent$y({
  props: {
    prevText: String,
    nextText: String,
    forceEllipses: Boolean,
    mode: {
      type: String,
      default: 'multi'
    },
    value: {
      type: Number,
      default: 0
    },
    pageCount: {
      type: [Number, String],
      default: 0
    },
    totalItems: {
      type: [Number, String],
      default: 0
    },
    itemsPerPage: {
      type: [Number, String],
      default: 10
    },
    showPageSize: {
      type: [Number, String],
      default: 5
    }
  },
  computed: {
    count: function count() {
      var count = this.pageCount || Math.ceil(this.totalItems / this.itemsPerPage);
      return Math.max(1, count);
    },
    pages: function pages() {
      var pages = [];
      var pageCount = this.count;
      var showPageSize = +this.showPageSize;
      if (this.mode !== 'multi') {
        return pages;
      } // Default page limits

      var startPage = 1;
      var endPage = pageCount;
      var isMaxSized = showPageSize < pageCount; // recompute if showPageSize

      if (isMaxSized) {
        // Current page is displayed in the middle of the visible ones
        startPage = Math.max(this.value - Math.floor(showPageSize / 2), 1);
        endPage = startPage + showPageSize - 1; // Adjust if limit is exceeded

        if (endPage > pageCount) {
          endPage = pageCount;
          startPage = endPage - showPageSize + 1;
        }
      } // Add page number links

      for (var number = startPage; number <= endPage; number++) {
        var page = makePage(number, number, number === this.value);
        pages.push(page);
      } // Add links to move between page sets

      if (isMaxSized && showPageSize > 0 && this.forceEllipses) {
        if (startPage > 1) {
          var previousPageSet = makePage(startPage - 1, '...', false);
          pages.unshift(previousPageSet);
        }
        if (endPage < pageCount) {
          var nextPageSet = makePage(endPage + 1, '...', false);
          pages.push(nextPageSet);
        }
      }
      return pages;
    }
  },
  watch: {
    value: {
      handler: function handler(page) {
        this.select(page || this.value);
      },
      immediate: true
    }
  },
  methods: {
    select: function select(page, emitChange) {
      page = Math.min(this.count, Math.max(1, page));
      if (this.value !== page) {
        this.$emit('input', page);
        if (emitChange) {
          this.$emit('change', page);
        }
      }
    }
  },
  render: function render() {
    var _this = this,
      _this$slots,
      _this$slots3;
    var h = arguments[0];
    var value = this.value;
    var simple = this.mode !== 'multi';
    var onSelect = function onSelect(value) {
      return function () {
        _this.select(value, true);
      };
    };
    return h("ul", {
      "class": bem$t({
        simple: simple
      })
    }, [h("li", {
      "class": [bem$t('item', {
        disabled: value === 1
      }), bem$t('prev'), BORDER],
      "on": {
        "click": onSelect(value - 1)
      }
    }, [((_this$slots = this.slots('prev-text')) != null ? _this$slots : this.prevText) || t$d('prev')]), this.pages.map(function (page) {
      var _this$slots2;
      return h("li", {
        "class": [bem$t('item', {
          active: page.active
        }), bem$t('page'), BORDER],
        "on": {
          "click": onSelect(page.number)
        }
      }, [(_this$slots2 = _this.slots('page', page)) != null ? _this$slots2 : page.text]);
    }), simple && h("li", {
      "class": bem$t('page-desc')
    }, [this.slots('pageDesc') || value + "/" + this.count]), h("li", {
      "class": [bem$t('item', {
        disabled: value === this.count
      }), bem$t('next'), BORDER],
      "on": {
        "click": onSelect(value + 1)
      }
    }, [((_this$slots3 = this.slots('next-text')) != null ? _this$slots3 : this.nextText) || t$d('next')])]);
  }
});

var _createNamespace$t = createNamespace('panel'),
  createComponent$x = _createNamespace$t[0],
  bem$s = _createNamespace$t[1];
function Panel(h, props, slots, ctx) {
  var Content = function Content() {
    return [slots.header ? slots.header() : h(Cell$1, {
      "attrs": {
        "icon": props.icon,
        "label": props.desc,
        "title": props.title,
        "value": props.status,
        "valueClass": bem$s('header-value')
      },
      "class": bem$s('header')
    }), h("div", {
      "class": bem$s('content')
    }, [slots.default && slots.default()]), slots.footer && h("div", {
      "class": [bem$s('footer'), BORDER_TOP]
    }, [slots.footer()])];
  };
  return h(CellGroup$1, _mergeJSXProps([{
    "class": bem$s(),
    "scopedSlots": {
      default: Content
    }
  }, inherit(ctx, true)]));
}
Panel.props = {
  icon: String,
  desc: String,
  title: String,
  status: String
};
var Panel$1 = createComponent$x(Panel);

var _createNamespace$s = createNamespace('password-input'),
  createComponent$w = _createNamespace$s[0],
  bem$r = _createNamespace$s[1];
function PasswordInput(h, props, slots, ctx) {
  var _ref2;
  var mask = props.mask,
    value = props.value,
    length = props.length,
    gutter = props.gutter,
    focused = props.focused,
    errorInfo = props.errorInfo;
  var info = errorInfo || props.info;
  var Points = [];
  for (var i = 0; i < length; i++) {
    var _ref;
    var _char = value[i];
    var showBorder = i !== 0 && !gutter;
    var showCursor = focused && i === value.length;
    var style = void 0;
    if (i !== 0 && gutter) {
      style = {
        marginLeft: addUnit(gutter)
      };
    }
    Points.push(h("li", {
      "class": [(_ref = {}, _ref[BORDER_LEFT] = showBorder, _ref), bem$r('item', {
        focus: showCursor
      })],
      "style": style
    }, [mask ? h("i", {
      "style": {
        visibility: _char ? 'visible' : 'hidden'
      }
    }) : _char, showCursor && h("div", {
      "class": bem$r('cursor')
    })]));
  }
  return h("div", {
    "class": bem$r()
  }, [h("ul", _mergeJSXProps([{
    "class": [bem$r('security'), (_ref2 = {}, _ref2[BORDER_SURROUND] = !gutter, _ref2)],
    "on": {
      "touchstart": function touchstart(event) {
        event.stopPropagation();
        emit(ctx, 'focus', event);
      }
    }
  }, inherit(ctx, true)]), [Points]), info && h("div", {
    "class": bem$r(errorInfo ? 'error-info' : 'info')
  }, [info])]);
}
PasswordInput.props = {
  info: String,
  gutter: [Number, String],
  focused: Boolean,
  errorInfo: String,
  mask: {
    type: Boolean,
    default: true
  },
  value: {
    type: String,
    default: ''
  },
  length: {
    type: [Number, String],
    default: 6
  }
};
var PasswordInput$1 = createComponent$w(PasswordInput);

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getWindow.js
function getWindow$1(node) {
  if (node == null) {
    return window;
  }
  if (node.toString() !== "[object Window]") {
    var ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }
  return node;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/instanceOf.js
function isElement$3(node) {
  var OwnElement = getWindow$1(node).Element;
  return node instanceof OwnElement || node instanceof Element;
}
function isHTMLElement$2(node) {
  var OwnElement = getWindow$1(node).HTMLElement;
  return node instanceof OwnElement || node instanceof HTMLElement;
}
function isShadowRoot$1(node) {
  if (typeof ShadowRoot === "undefined") {
    return false;
  }
  var OwnElement = getWindow$1(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/math.js
var round$3 = Math.round;

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/userAgent.js
function getUAString$1() {
  var uaData = navigator.userAgentData;
  if (uaData != null && uaData.brands) {
    return uaData.brands.map(function (item) {
      return item.brand + "/" + item.version;
    }).join(" ");
  }
  return navigator.userAgent;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/isLayoutViewport.js
function isLayoutViewport$1() {
  return !/^((?!chrome|android).)*safari/i.test(getUAString$1());
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getBoundingClientRect.js
function getBoundingClientRect$2(element, includeScale, isFixedStrategy) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  var clientRect = element.getBoundingClientRect();
  var scaleX = 1;
  var scaleY = 1;
  if (includeScale && isHTMLElement$2(element)) {
    scaleX = element.offsetWidth > 0 ? round$3(clientRect.width) / element.offsetWidth || 1 : 1;
    scaleY = element.offsetHeight > 0 ? round$3(clientRect.height) / element.offsetHeight || 1 : 1;
  }
  var _ref = isElement$3(element) ? getWindow$1(element) : window,
    visualViewport = _ref.visualViewport;
  var addVisualOffsets = !isLayoutViewport$1() && isFixedStrategy;
  var x = (clientRect.left + (addVisualOffsets && visualViewport ? visualViewport.offsetLeft : 0)) / scaleX;
  var y = (clientRect.top + (addVisualOffsets && visualViewport ? visualViewport.offsetTop : 0)) / scaleY;
  var width = clientRect.width / scaleX;
  var height = clientRect.height / scaleY;
  return {
    width,
    height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x,
    x,
    y
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getWindowScroll.js
function getWindowScroll$1(node) {
  var win = getWindow$1(node);
  var scrollLeft = win.pageXOffset;
  var scrollTop = win.pageYOffset;
  return {
    scrollLeft,
    scrollTop
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getHTMLElementScroll.js
function getHTMLElementScroll$1(element) {
  return {
    scrollLeft: element.scrollLeft,
    scrollTop: element.scrollTop
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getNodeScroll.js
function getNodeScroll$1(node) {
  if (node === getWindow$1(node) || !isHTMLElement$2(node)) {
    return getWindowScroll$1(node);
  } else {
    return getHTMLElementScroll$1(node);
  }
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getNodeName.js
function getNodeName$1(element) {
  return element ? (element.nodeName || "").toLowerCase() : null;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getDocumentElement.js
function getDocumentElement$1(element) {
  return ((isElement$3(element) ? element.ownerDocument : element.document) || window.document).documentElement;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getWindowScrollBarX.js
function getWindowScrollBarX$1(element) {
  return getBoundingClientRect$2(getDocumentElement$1(element)).left + getWindowScroll$1(element).scrollLeft;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getComputedStyle.js
function getComputedStyle$2(element) {
  return getWindow$1(element).getComputedStyle(element);
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/isScrollParent.js
function isScrollParent$1(element) {
  var _getComputedStyle = getComputedStyle$2(element),
    overflow = _getComputedStyle.overflow,
    overflowX = _getComputedStyle.overflowX,
    overflowY = _getComputedStyle.overflowY;
  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getCompositeRect.js
function isElementScaled$1(element) {
  var rect = element.getBoundingClientRect();
  var scaleX = round$3(rect.width) / element.offsetWidth || 1;
  var scaleY = round$3(rect.height) / element.offsetHeight || 1;
  return scaleX !== 1 || scaleY !== 1;
}
function getCompositeRect$1(elementOrVirtualElement, offsetParent, isFixed) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  var isOffsetParentAnElement = isHTMLElement$2(offsetParent);
  var offsetParentIsScaled = isHTMLElement$2(offsetParent) && isElementScaled$1(offsetParent);
  var documentElement = getDocumentElement$1(offsetParent);
  var rect = getBoundingClientRect$2(elementOrVirtualElement, offsetParentIsScaled, isFixed);
  var scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  var offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName$1(offsetParent) !== "body" || isScrollParent$1(documentElement)) {
      scroll = getNodeScroll$1(offsetParent);
    }
    if (isHTMLElement$2(offsetParent)) {
      offsets = getBoundingClientRect$2(offsetParent, true);
      offsets.x += offsetParent.clientLeft;
      offsets.y += offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX$1(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getLayoutRect.js
function getLayoutRect$1(element) {
  var clientRect = getBoundingClientRect$2(element);
  var width = element.offsetWidth;
  var height = element.offsetHeight;
  if (Math.abs(clientRect.width - width) <= 1) {
    width = clientRect.width;
  }
  if (Math.abs(clientRect.height - height) <= 1) {
    height = clientRect.height;
  }
  return {
    x: element.offsetLeft,
    y: element.offsetTop,
    width,
    height
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getParentNode.js
function getParentNode$1(element) {
  if (getNodeName$1(element) === "html") {
    return element;
  }
  return element.assignedSlot || element.parentNode || (isShadowRoot$1(element) ? element.host : null) || getDocumentElement$1(element);
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getScrollParent.js
function getScrollParent$1(node) {
  if (["html", "body", "#document"].indexOf(getNodeName$1(node)) >= 0) {
    return node.ownerDocument.body;
  }
  if (isHTMLElement$2(node) && isScrollParent$1(node)) {
    return node;
  }
  return getScrollParent$1(getParentNode$1(node));
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/listScrollParents.js
function listScrollParents$1(element, list) {
  var _element$ownerDocumen;
  if (list === void 0) {
    list = [];
  }
  var scrollParent = getScrollParent$1(element);
  var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
  var win = getWindow$1(scrollParent);
  var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent$1(scrollParent) ? scrollParent : []) : scrollParent;
  var updatedList = list.concat(target);
  return isBody ? updatedList : updatedList.concat(listScrollParents$1(getParentNode$1(target)));
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/isTableElement.js
function isTableElement$1(element) {
  return ["table", "td", "th"].indexOf(getNodeName$1(element)) >= 0;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/dom-utils/getOffsetParent.js
function getTrueOffsetParent$1(element) {
  if (!isHTMLElement$2(element) || getComputedStyle$2(element).position === "fixed") {
    return null;
  }
  return element.offsetParent;
}
function getContainingBlock$1(element) {
  var isFirefox = /firefox/i.test(getUAString$1());
  var isIE = /Trident/i.test(getUAString$1());
  if (isIE && isHTMLElement$2(element)) {
    var elementCss = getComputedStyle$2(element);
    if (elementCss.position === "fixed") {
      return null;
    }
  }
  var currentNode = getParentNode$1(element);
  if (isShadowRoot$1(currentNode)) {
    currentNode = currentNode.host;
  }
  while (isHTMLElement$2(currentNode) && ["html", "body"].indexOf(getNodeName$1(currentNode)) < 0) {
    var css = getComputedStyle$2(currentNode);
    if (css.transform !== "none" || css.perspective !== "none" || css.contain === "paint" || ["transform", "perspective"].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === "filter" || isFirefox && css.filter && css.filter !== "none") {
      return currentNode;
    } else {
      currentNode = currentNode.parentNode;
    }
  }
  return null;
}
function getOffsetParent$1(element) {
  var window2 = getWindow$1(element);
  var offsetParent = getTrueOffsetParent$1(element);
  while (offsetParent && isTableElement$1(offsetParent) && getComputedStyle$2(offsetParent).position === "static") {
    offsetParent = getTrueOffsetParent$1(offsetParent);
  }
  if (offsetParent && (getNodeName$1(offsetParent) === "html" || getNodeName$1(offsetParent) === "body" && getComputedStyle$2(offsetParent).position === "static")) {
    return window2;
  }
  return offsetParent || getContainingBlock$1(element) || window2;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/enums.js
var top$1 = "top";
var bottom$1 = "bottom";
var right$1 = "right";
var left$1 = "left";
var auto$1 = "auto";
var basePlacements$1 = [top$1, bottom$1, right$1, left$1];
var start$1 = "start";
var end$1 = "end";
var placements$1 = /* @__PURE__ */[].concat(basePlacements$1, [auto$1]).reduce(function (acc, placement) {
  return acc.concat([placement, placement + "-" + start$1, placement + "-" + end$1]);
}, []);
var beforeRead$1 = "beforeRead";
var read$1 = "read";
var afterRead$1 = "afterRead";
var beforeMain$1 = "beforeMain";
var main$1 = "main";
var afterMain$1 = "afterMain";
var beforeWrite$1 = "beforeWrite";
var write$1 = "write";
var afterWrite$1 = "afterWrite";
var modifierPhases$1 = [beforeRead$1, read$1, afterRead$1, beforeMain$1, main$1, afterMain$1, beforeWrite$1, write$1, afterWrite$1];

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/orderModifiers.js
function order$1(modifiers) {
  var map = /* @__PURE__ */new Map();
  var visited = /* @__PURE__ */new Set();
  var result = [];
  modifiers.forEach(function (modifier) {
    map.set(modifier.name, modifier);
  });
  function sort(modifier) {
    visited.add(modifier.name);
    var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
    requires.forEach(function (dep) {
      if (!visited.has(dep)) {
        var depModifier = map.get(dep);
        if (depModifier) {
          sort(depModifier);
        }
      }
    });
    result.push(modifier);
  }
  modifiers.forEach(function (modifier) {
    if (!visited.has(modifier.name)) {
      sort(modifier);
    }
  });
  return result;
}
function orderModifiers$1(modifiers) {
  var orderedModifiers = order$1(modifiers);
  return modifierPhases$1.reduce(function (acc, phase) {
    return acc.concat(orderedModifiers.filter(function (modifier) {
      return modifier.phase === phase;
    }));
  }, []);
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/debounce.js
function debounce$4(fn2) {
  var pending;
  return function () {
    if (!pending) {
      pending = new Promise(function (resolve) {
        Promise.resolve().then(function () {
          pending = void 0;
          resolve(fn2());
        });
      });
    }
    return pending;
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/format.js
function format$1(str) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  return [].concat(args).reduce(function (p, c) {
    return p.replace(/%s/, c);
  }, str);
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/validateModifiers.js
var INVALID_MODIFIER_ERROR = 'Popper: modifier "%s" provided an invalid %s property, expected %s but got %s';
var MISSING_DEPENDENCY_ERROR = 'Popper: modifier "%s" requires "%s", but "%s" modifier is not available';
var VALID_PROPERTIES = ["name", "enabled", "phase", "fn", "effect", "requires", "options"];
function validateModifiers(modifiers) {
  modifiers.forEach(function (modifier) {
    [].concat(Object.keys(modifier), VALID_PROPERTIES).filter(function (value, index, self) {
      return self.indexOf(value) === index;
    }).forEach(function (key) {
      switch (key) {
        case "name":
          if (typeof modifier.name !== "string") {
            console.error(format$1(INVALID_MODIFIER_ERROR, String(modifier.name), '"name"', '"string"', '"' + String(modifier.name) + '"'));
          }
          break;
        case "enabled":
          if (typeof modifier.enabled !== "boolean") {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"enabled"', '"boolean"', '"' + String(modifier.enabled) + '"'));
          }
          break;
        case "phase":
          if (modifierPhases$1.indexOf(modifier.phase) < 0) {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"phase"', "either " + modifierPhases$1.join(", "), '"' + String(modifier.phase) + '"'));
          }
          break;
        case "fn":
          if (typeof modifier.fn !== "function") {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"fn"', '"function"', '"' + String(modifier.fn) + '"'));
          }
          break;
        case "effect":
          if (modifier.effect != null && typeof modifier.effect !== "function") {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"effect"', '"function"', '"' + String(modifier.fn) + '"'));
          }
          break;
        case "requires":
          if (modifier.requires != null && !Array.isArray(modifier.requires)) {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"requires"', '"array"', '"' + String(modifier.requires) + '"'));
          }
          break;
        case "requiresIfExists":
          if (!Array.isArray(modifier.requiresIfExists)) {
            console.error(format$1(INVALID_MODIFIER_ERROR, modifier.name, '"requiresIfExists"', '"array"', '"' + String(modifier.requiresIfExists) + '"'));
          }
          break;
        case "options":
        case "data":
          break;
        default:
          console.error('PopperJS: an invalid property has been provided to the "' + modifier.name + '" modifier, valid properties are ' + VALID_PROPERTIES.map(function (s) {
            return '"' + s + '"';
          }).join(", ") + '; but "' + key + '" was provided.');
      }
      modifier.requires && modifier.requires.forEach(function (requirement) {
        if (modifiers.find(function (mod) {
          return mod.name === requirement;
        }) == null) {
          console.error(format$1(MISSING_DEPENDENCY_ERROR, String(modifier.name), requirement, requirement));
        }
      });
    });
  });
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/uniqueBy.js
function uniqueBy(arr, fn2) {
  var identifiers = /* @__PURE__ */new Set();
  return arr.filter(function (item) {
    var identifier = fn2(item);
    if (!identifiers.has(identifier)) {
      identifiers.add(identifier);
      return true;
    }
  });
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/getBasePlacement.js
function getBasePlacement$2(placement) {
  return placement.split("-")[0];
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/mergeByName.js
function mergeByName$1(modifiers) {
  var merged = modifiers.reduce(function (merged2, current) {
    var existing = merged2[current.name];
    merged2[current.name] = existing ? Object.assign({}, existing, current, {
      options: Object.assign({}, existing.options, current.options),
      data: Object.assign({}, existing.data, current.data)
    }) : current;
    return merged2;
  }, {});
  return Object.keys(merged).map(function (key) {
    return merged[key];
  });
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/getVariation.js
function getVariation$1(placement) {
  return placement.split("-")[1];
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/getMainAxisFromPlacement.js
function getMainAxisFromPlacement$1(placement) {
  return ["top", "bottom"].indexOf(placement) >= 0 ? "x" : "y";
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/utils/computeOffsets.js
function computeOffsets$1(_ref) {
  var reference = _ref.reference,
    element = _ref.element,
    placement = _ref.placement;
  var basePlacement = placement ? getBasePlacement$2(placement) : null;
  var variation = placement ? getVariation$1(placement) : null;
  var commonX = reference.x + reference.width / 2 - element.width / 2;
  var commonY = reference.y + reference.height / 2 - element.height / 2;
  var offsets;
  switch (basePlacement) {
    case top$1:
      offsets = {
        x: commonX,
        y: reference.y - element.height
      };
      break;
    case bottom$1:
      offsets = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case right$1:
      offsets = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case left$1:
      offsets = {
        x: reference.x - element.width,
        y: commonY
      };
      break;
    default:
      offsets = {
        x: reference.x,
        y: reference.y
      };
  }
  var mainAxis = basePlacement ? getMainAxisFromPlacement$1(basePlacement) : null;
  if (mainAxis != null) {
    var len = mainAxis === "y" ? "height" : "width";
    switch (variation) {
      case start$1:
        offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
        break;
      case end$1:
        offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
        break;
    }
  }
  return offsets;
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/createPopper.js
var INVALID_ELEMENT_ERROR = "Popper: Invalid reference or popper argument provided. They must be either a DOM element or virtual element.";
var INFINITE_LOOP_ERROR = "Popper: An infinite loop in the modifiers cycle has been detected! The cycle has been interrupted to prevent a browser crash.";
var DEFAULT_OPTIONS$1 = {
  placement: "bottom",
  modifiers: [],
  strategy: "absolute"
};
function areValidElements$1() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  return !args.some(function (element) {
    return !(element && typeof element.getBoundingClientRect === "function");
  });
}
function popperGenerator$1(generatorOptions) {
  if (generatorOptions === void 0) {
    generatorOptions = {};
  }
  var _generatorOptions = generatorOptions,
    _generatorOptions$def = _generatorOptions.defaultModifiers,
    defaultModifiers2 = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
    _generatorOptions$def2 = _generatorOptions.defaultOptions,
    defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS$1 : _generatorOptions$def2;
  return function createPopper2(reference, popper, options) {
    if (options === void 0) {
      options = defaultOptions;
    }
    var state = {
      placement: "bottom",
      orderedModifiers: [],
      options: Object.assign({}, DEFAULT_OPTIONS$1, defaultOptions),
      modifiersData: {},
      elements: {
        reference,
        popper
      },
      attributes: {},
      styles: {}
    };
    var effectCleanupFns = [];
    var isDestroyed = false;
    var instance = {
      state,
      setOptions: function setOptions(setOptionsAction) {
        var options2 = typeof setOptionsAction === "function" ? setOptionsAction(state.options) : setOptionsAction;
        cleanupModifierEffects();
        state.options = Object.assign({}, defaultOptions, state.options, options2);
        state.scrollParents = {
          reference: isElement$3(reference) ? listScrollParents$1(reference) : reference.contextElement ? listScrollParents$1(reference.contextElement) : [],
          popper: listScrollParents$1(popper)
        };
        var orderedModifiers = orderModifiers$1(mergeByName$1([].concat(defaultModifiers2, state.options.modifiers)));
        state.orderedModifiers = orderedModifiers.filter(function (m) {
          return m.enabled;
        });
        {
          var modifiers = uniqueBy([].concat(orderedModifiers, state.options.modifiers), function (_ref) {
            var name = _ref.name;
            return name;
          });
          validateModifiers(modifiers);
          if (getBasePlacement$2(state.options.placement) === auto$1) {
            var flipModifier = state.orderedModifiers.find(function (_ref2) {
              var name = _ref2.name;
              return name === "flip";
            });
            if (!flipModifier) {
              console.error(['Popper: "auto" placements require the "flip" modifier be', "present and enabled to work."].join(" "));
            }
          }
          var _getComputedStyle = getComputedStyle$2(popper),
            marginTop = _getComputedStyle.marginTop,
            marginRight = _getComputedStyle.marginRight,
            marginBottom = _getComputedStyle.marginBottom,
            marginLeft = _getComputedStyle.marginLeft;
          if ([marginTop, marginRight, marginBottom, marginLeft].some(function (margin) {
            return parseFloat(margin);
          })) {
            console.warn(['Popper: CSS "margin" styles cannot be used to apply padding', "between the popper and its reference element or boundary.", "To replicate margin, use the `offset` modifier, as well as", "the `padding` option in the `preventOverflow` and `flip`", "modifiers."].join(" "));
          }
        }
        runModifierEffects();
        return instance.update();
      },
      forceUpdate: function forceUpdate() {
        if (isDestroyed) {
          return;
        }
        var _state$elements = state.elements,
          reference2 = _state$elements.reference,
          popper2 = _state$elements.popper;
        if (!areValidElements$1(reference2, popper2)) {
          {
            console.error(INVALID_ELEMENT_ERROR);
          }
          return;
        }
        state.rects = {
          reference: getCompositeRect$1(reference2, getOffsetParent$1(popper2), state.options.strategy === "fixed"),
          popper: getLayoutRect$1(popper2)
        };
        state.reset = false;
        state.placement = state.options.placement;
        state.orderedModifiers.forEach(function (modifier) {
          return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
        });
        var __debug_loops__ = 0;
        for (var index = 0; index < state.orderedModifiers.length; index++) {
          {
            __debug_loops__ += 1;
            if (__debug_loops__ > 100) {
              console.error(INFINITE_LOOP_ERROR);
              break;
            }
          }
          if (state.reset === true) {
            state.reset = false;
            index = -1;
            continue;
          }
          var _state$orderedModifie = state.orderedModifiers[index],
            fn2 = _state$orderedModifie.fn,
            _state$orderedModifie2 = _state$orderedModifie.options,
            _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
            name = _state$orderedModifie.name;
          if (typeof fn2 === "function") {
            state = fn2({
              state,
              options: _options,
              name,
              instance
            }) || state;
          }
        }
      },
      update: debounce$4(function () {
        return new Promise(function (resolve) {
          instance.forceUpdate();
          resolve(state);
        });
      }),
      destroy: function destroy() {
        cleanupModifierEffects();
        isDestroyed = true;
      }
    };
    if (!areValidElements$1(reference, popper)) {
      {
        console.error(INVALID_ELEMENT_ERROR);
      }
      return instance;
    }
    instance.setOptions(options).then(function (state2) {
      if (!isDestroyed && options.onFirstUpdate) {
        options.onFirstUpdate(state2);
      }
    });
    function runModifierEffects() {
      state.orderedModifiers.forEach(function (_ref3) {
        var name = _ref3.name,
          _ref3$options = _ref3.options,
          options2 = _ref3$options === void 0 ? {} : _ref3$options,
          effect3 = _ref3.effect;
        if (typeof effect3 === "function") {
          var cleanupFn = effect3({
            state,
            name,
            instance,
            options: options2
          });
          var noopFn = function noopFn2() {};
          effectCleanupFns.push(cleanupFn || noopFn);
        }
      });
    }
    function cleanupModifierEffects() {
      effectCleanupFns.forEach(function (fn2) {
        return fn2();
      });
      effectCleanupFns = [];
    }
    return instance;
  };
}

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/modifiers/eventListeners.js
var passive$1 = {
  passive: true
};
function effect$3(_ref) {
  var state = _ref.state,
    instance = _ref.instance,
    options = _ref.options;
  var _options$scroll = options.scroll,
    scroll = _options$scroll === void 0 ? true : _options$scroll,
    _options$resize = options.resize,
    resize = _options$resize === void 0 ? true : _options$resize;
  var window2 = getWindow$1(state.elements.popper);
  var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);
  if (scroll) {
    scrollParents.forEach(function (scrollParent) {
      scrollParent.addEventListener("scroll", instance.update, passive$1);
    });
  }
  if (resize) {
    window2.addEventListener("resize", instance.update, passive$1);
  }
  return function () {
    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.removeEventListener("scroll", instance.update, passive$1);
      });
    }
    if (resize) {
      window2.removeEventListener("resize", instance.update, passive$1);
    }
  };
}
var eventListeners_default = {
  name: "eventListeners",
  enabled: true,
  phase: "write",
  fn: function fn() {},
  effect: effect$3,
  data: {}
};

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/modifiers/popperOffsets.js
function popperOffsets$2(_ref) {
  var state = _ref.state,
    name = _ref.name;
  state.modifiersData[name] = computeOffsets$1({
    reference: state.rects.reference,
    element: state.rects.popper,
    strategy: "absolute",
    placement: state.placement
  });
}
var popperOffsets_default = {
  name: "popperOffsets",
  enabled: true,
  phase: "read",
  fn: popperOffsets$2,
  data: {}
};

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/modifiers/computeStyles.js
var unsetSides$1 = {
  top: "auto",
  right: "auto",
  bottom: "auto",
  left: "auto"
};
function roundOffsetsByDPR$1(_ref) {
  var x = _ref.x,
    y = _ref.y;
  var win = window;
  var dpr = win.devicePixelRatio || 1;
  return {
    x: round$3(x * dpr) / dpr || 0,
    y: round$3(y * dpr) / dpr || 0
  };
}
function mapToStyles$1(_ref2) {
  var _Object$assign2;
  var popper = _ref2.popper,
    popperRect = _ref2.popperRect,
    placement = _ref2.placement,
    variation = _ref2.variation,
    offsets = _ref2.offsets,
    position = _ref2.position,
    gpuAcceleration = _ref2.gpuAcceleration,
    adaptive = _ref2.adaptive,
    roundOffsets = _ref2.roundOffsets,
    isFixed = _ref2.isFixed;
  var _offsets$x = offsets.x,
    x = _offsets$x === void 0 ? 0 : _offsets$x,
    _offsets$y = offsets.y,
    y = _offsets$y === void 0 ? 0 : _offsets$y;
  var _ref3 = typeof roundOffsets === "function" ? roundOffsets({
    x,
    y
  }) : {
    x,
    y
  };
  x = _ref3.x;
  y = _ref3.y;
  var hasX = offsets.hasOwnProperty("x");
  var hasY = offsets.hasOwnProperty("y");
  var sideX = left$1;
  var sideY = top$1;
  var win = window;
  if (adaptive) {
    var offsetParent = getOffsetParent$1(popper);
    var heightProp = "clientHeight";
    var widthProp = "clientWidth";
    if (offsetParent === getWindow$1(popper)) {
      offsetParent = getDocumentElement$1(popper);
      if (getComputedStyle$2(offsetParent).position !== "static" && position === "absolute") {
        heightProp = "scrollHeight";
        widthProp = "scrollWidth";
      }
    }
    offsetParent = offsetParent;
    if (placement === top$1 || (placement === left$1 || placement === right$1) && variation === end$1) {
      sideY = bottom$1;
      var offsetY = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.height : offsetParent[heightProp];
      y -= offsetY - popperRect.height;
      y *= gpuAcceleration ? 1 : -1;
    }
    if (placement === left$1 || (placement === top$1 || placement === bottom$1) && variation === end$1) {
      sideX = right$1;
      var offsetX = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.width : offsetParent[widthProp];
      x -= offsetX - popperRect.width;
      x *= gpuAcceleration ? 1 : -1;
    }
  }
  var commonStyles = Object.assign({
    position
  }, adaptive && unsetSides$1);
  var _ref4 = roundOffsets === true ? roundOffsetsByDPR$1({
    x,
    y
  }) : {
    x,
    y
  };
  x = _ref4.x;
  y = _ref4.y;
  if (gpuAcceleration) {
    var _Object$assign;
    return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? "0" : "", _Object$assign[sideX] = hasX ? "0" : "", _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
  }
  return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : "", _Object$assign2[sideX] = hasX ? x + "px" : "", _Object$assign2.transform = "", _Object$assign2));
}
function computeStyles$2(_ref5) {
  var state = _ref5.state,
    options = _ref5.options;
  var _options$gpuAccelerat = options.gpuAcceleration,
    gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
    _options$adaptive = options.adaptive,
    adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
    _options$roundOffsets = options.roundOffsets,
    roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;
  {
    var transitionProperty = getComputedStyle$2(state.elements.popper).transitionProperty || "";
    if (adaptive && ["transform", "top", "right", "bottom", "left"].some(function (property) {
      return transitionProperty.indexOf(property) >= 0;
    })) {
      console.warn(["Popper: Detected CSS transitions on at least one of the following", 'CSS properties: "transform", "top", "right", "bottom", "left".', "\n\n", 'Disable the "computeStyles" modifier\'s `adaptive` option to allow', "for smooth transitions, or remove these properties from the CSS", "transition declaration on the popper element if only transitioning", "opacity or background-color for example.", "\n\n", "We recommend using the popper element as a wrapper around an inner", "element that can have any CSS property transitioned for animations."].join(" "));
    }
  }
  var commonStyles = {
    placement: getBasePlacement$2(state.placement),
    variation: getVariation$1(state.placement),
    popper: state.elements.popper,
    popperRect: state.rects.popper,
    gpuAcceleration,
    isFixed: state.options.strategy === "fixed"
  };
  if (state.modifiersData.popperOffsets != null) {
    state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles$1(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.popperOffsets,
      position: state.options.strategy,
      adaptive,
      roundOffsets
    })));
  }
  if (state.modifiersData.arrow != null) {
    state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles$1(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.arrow,
      position: "absolute",
      adaptive: false,
      roundOffsets
    })));
  }
  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    "data-popper-placement": state.placement
  });
}
var computeStyles_default = {
  name: "computeStyles",
  enabled: true,
  phase: "beforeWrite",
  fn: computeStyles$2,
  data: {}
};

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/modifiers/applyStyles.js
function applyStyles$2(_ref) {
  var state = _ref.state;
  Object.keys(state.elements).forEach(function (name) {
    var style = state.styles[name] || {};
    var attributes = state.attributes[name] || {};
    var element = state.elements[name];
    if (!isHTMLElement$2(element) || !getNodeName$1(element)) {
      return;
    }
    Object.assign(element.style, style);
    Object.keys(attributes).forEach(function (name2) {
      var value = attributes[name2];
      if (value === false) {
        element.removeAttribute(name2);
      } else {
        element.setAttribute(name2, value === true ? "" : value);
      }
    });
  });
}
function effect2(_ref2) {
  var state = _ref2.state;
  var initialStyles = {
    popper: {
      position: state.options.strategy,
      left: "0",
      top: "0",
      margin: "0"
    },
    arrow: {
      position: "absolute"
    },
    reference: {}
  };
  Object.assign(state.elements.popper.style, initialStyles.popper);
  state.styles = initialStyles;
  if (state.elements.arrow) {
    Object.assign(state.elements.arrow.style, initialStyles.arrow);
  }
  return function () {
    Object.keys(state.elements).forEach(function (name) {
      var element = state.elements[name];
      var attributes = state.attributes[name] || {};
      var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]);
      var style = styleProperties.reduce(function (style2, property) {
        style2[property] = "";
        return style2;
      }, {});
      if (!isHTMLElement$2(element) || !getNodeName$1(element)) {
        return;
      }
      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (attribute) {
        element.removeAttribute(attribute);
      });
    });
  };
}
var applyStyles_default = {
  name: "applyStyles",
  enabled: true,
  phase: "write",
  fn: applyStyles$2,
  effect: effect2,
  requires: ["computeStyles"]
};

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/popper-lite.js
var defaultModifiers$1 = [eventListeners_default, popperOffsets_default, computeStyles_default, applyStyles_default];
var createPopper$1 = /* @__PURE__ */popperGenerator$1({
  defaultModifiers: defaultModifiers$1
});

// ../../node_modules/.pnpm/@popperjs+core@2.11.6/node_modules/@popperjs/core/lib/modifiers/offset.js
function distanceAndSkiddingToXY$1(placement, rects, offset2) {
  var basePlacement = getBasePlacement$2(placement);
  var invertDistance = [left$1, top$1].indexOf(basePlacement) >= 0 ? -1 : 1;
  var _ref = typeof offset2 === "function" ? offset2(Object.assign({}, rects, {
      placement
    })) : offset2,
    skidding = _ref[0],
    distance = _ref[1];
  skidding = skidding || 0;
  distance = (distance || 0) * invertDistance;
  return [left$1, right$1].indexOf(basePlacement) >= 0 ? {
    x: distance,
    y: skidding
  } : {
    x: skidding,
    y: distance
  };
}
function offset$2(_ref2) {
  var state = _ref2.state,
    options = _ref2.options,
    name = _ref2.name;
  var _options$offset = options.offset,
    offset2 = _options$offset === void 0 ? [0, 0] : _options$offset;
  var data = placements$1.reduce(function (acc, placement) {
    acc[placement] = distanceAndSkiddingToXY$1(placement, state.rects, offset2);
    return acc;
  }, {});
  var _data$state$placement = data[state.placement],
    x = _data$state$placement.x,
    y = _data$state$placement.y;
  if (state.modifiersData.popperOffsets != null) {
    state.modifiersData.popperOffsets.x += x;
    state.modifiersData.popperOffsets.y += y;
  }
  state.modifiersData[name] = data;
}
var offset_default = {
  name: "offset",
  enabled: true,
  phase: "main",
  requires: ["popperOffsets"],
  fn: offset$2
};

var _createNamespace$r = createNamespace('popover'),
  createComponent$v = _createNamespace$r[0],
  bem$q = _createNamespace$r[1];
var Popover = createComponent$v({
  mixins: [ClickOutsideMixin({
    event: 'touchstart',
    method: 'onClickOutside'
  })],
  props: {
    value: Boolean,
    trigger: String,
    overlay: Boolean,
    offset: {
      type: Array,
      default: function _default() {
        return [0, 8];
      }
    },
    theme: {
      type: String,
      default: 'light'
    },
    actions: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    placement: {
      type: String,
      default: 'bottom'
    },
    getContainer: {
      type: [String, Function],
      default: 'body'
    },
    closeOnClickAction: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    value: 'updateLocation',
    placement: 'updateLocation'
  },
  mounted: function mounted() {
    this.updateLocation();
  },
  beforeDestroy: function beforeDestroy() {
    if (this.popper) {
      if (!isServer$1) {
        window.removeEventListener('animationend', this.updateLocation);
        window.removeEventListener('transitionend', this.updateLocation);
      }
      this.popper.destroy();
      this.popper = null;
    }
  },
  methods: {
    createPopper: function createPopper() {
      var popper = createPopper$1(this.$refs.wrapper, this.$refs.popover.$el, {
        placement: this.placement,
        modifiers: [{
          name: 'computeStyles',
          options: {
            adaptive: false,
            gpuAcceleration: false
          }
        }, _extends$1({}, offset_default, {
          options: {
            offset: this.offset
          }
        })]
      });
      if (!isServer$1) {
        window.addEventListener('animationend', this.updateLocation);
        window.addEventListener('transitionend', this.updateLocation);
      }
      return popper;
    },
    updateLocation: function updateLocation() {
      var _this = this;
      this.$nextTick(function () {
        if (!_this.value) {
          return;
        }
        if (!_this.popper) {
          _this.popper = _this.createPopper();
        } else {
          _this.popper.setOptions({
            placement: _this.placement
          });
        }
      });
    },
    renderAction: function renderAction(action, index) {
      var _this2 = this;
      var h = this.$createElement;
      var icon = action.icon,
        text = action.text,
        disabled = action.disabled,
        className = action.className;
      return h("div", {
        "attrs": {
          "role": "menuitem"
        },
        "class": [bem$q('action', {
          disabled: disabled,
          'with-icon': icon
        }), className],
        "on": {
          "click": function click() {
            return _this2.onClickAction(action, index);
          }
        }
      }, [icon && h(Icon$3, {
        "attrs": {
          "name": icon
        },
        "class": bem$q('action-icon')
      }), h("div", {
        "class": [bem$q('action-text'), BORDER_BOTTOM]
      }, [text])]);
    },
    onToggle: function onToggle(value) {
      this.$emit('input', value);
    },
    onClickWrapper: function onClickWrapper() {
      if (this.trigger === 'click') {
        this.onToggle(!this.value);
      }
    },
    onTouchstart: function onTouchstart(event) {
      event.stopPropagation();
      this.$emit('touchstart', event);
    },
    onClickAction: function onClickAction(action, index) {
      if (action.disabled) {
        return;
      }
      this.$emit('select', action, index);
      if (this.closeOnClickAction) {
        this.$emit('input', false);
      }
    },
    onClickOutside: function onClickOutside() {
      this.$emit('input', false);
    },
    onOpen: function onOpen() {
      this.$emit('open');
    },
    /* istanbul ignore next */
    onOpened: function onOpened() {
      this.$emit('opened');
    },
    onClose: function onClose() {
      this.$emit('close');
    },
    /* istanbul ignore next */
    onClosed: function onClosed() {
      this.$emit('closed');
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("span", {
      "ref": "wrapper",
      "class": bem$q('wrapper'),
      "on": {
        "click": this.onClickWrapper
      }
    }, [h(Popup, {
      "ref": "popover",
      "attrs": {
        "value": this.value,
        "overlay": this.overlay,
        "position": null,
        "transition": "van-popover-zoom",
        "lockScroll": false,
        "getContainer": this.getContainer
      },
      "class": bem$q([this.theme]),
      "on": {
        "open": this.onOpen,
        "close": this.onClose,
        "input": this.onToggle,
        "opened": this.onOpened,
        "closed": this.onClosed
      },
      "nativeOn": {
        "touchstart": this.onTouchstart
      }
    }, [h("div", {
      "class": bem$q('arrow')
    }), h("div", {
      "class": bem$q('content'),
      "attrs": {
        "role": "menu"
      }
    }, [this.slots('default') || this.actions.map(this.renderAction)])]), this.slots('reference')]);
  }
});

var _createNamespace$q = createNamespace('progress'),
  createComponent$u = _createNamespace$q[0],
  bem$p = _createNamespace$q[1];
var Progress = createComponent$u({
  mixins: [BindEventMixin(function (bind) {
    bind(window, 'resize', this.resize, true);
    bind(window, 'orientationchange', this.resize, true);
  })],
  props: {
    color: String,
    inactive: Boolean,
    pivotText: String,
    textColor: String,
    pivotColor: String,
    trackColor: String,
    strokeWidth: [Number, String],
    percentage: {
      type: [Number, String],
      required: true,
      validator: function validator(value) {
        return value >= 0 && value <= 100;
      }
    },
    showPivot: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      pivotWidth: 0,
      progressWidth: 0
    };
  },
  mounted: function mounted() {
    this.resize();
  },
  watch: {
    showPivot: 'resize',
    pivotText: 'resize'
  },
  methods: {
    // @exposed-api
    resize: function resize() {
      var _this = this;
      this.$nextTick(function () {
        _this.progressWidth = _this.$el.offsetWidth;
        _this.pivotWidth = _this.$refs.pivot ? _this.$refs.pivot.offsetWidth : 0;
      });
    }
  },
  render: function render() {
    var h = arguments[0];
    var pivotText = this.pivotText,
      percentage = this.percentage;
    var text = pivotText != null ? pivotText : percentage + '%';
    var showPivot = this.showPivot && text;
    var background = this.inactive ? '#cacaca' : this.color;
    var pivotStyle = {
      color: this.textColor,
      left: (this.progressWidth - this.pivotWidth) * percentage / 100 + "px",
      background: this.pivotColor || background
    };
    var portionStyle = {
      background: background,
      width: this.progressWidth * percentage / 100 + 'px'
    };
    var wrapperStyle = {
      background: this.trackColor,
      height: addUnit(this.strokeWidth)
    };
    return h("div", {
      "class": bem$p(),
      "style": wrapperStyle
    }, [h("span", {
      "class": bem$p('portion'),
      "style": portionStyle
    }, [showPivot && h("span", {
      "ref": "pivot",
      "style": pivotStyle,
      "class": bem$p('pivot')
    }, [text])])]);
  }
});

// Utils
var _createNamespace$p = createNamespace('pull-refresh'),
  createComponent$t = _createNamespace$p[0],
  bem$o = _createNamespace$p[1],
  t$c = _createNamespace$p[2];
var DEFAULT_HEAD_HEIGHT = 50;
var TEXT_STATUS = ['pulling', 'loosing', 'success'];
var PullRefresh = createComponent$t({
  mixins: [TouchMixin],
  props: {
    disabled: Boolean,
    successText: String,
    pullingText: String,
    loosingText: String,
    loadingText: String,
    pullDistance: [Number, String],
    value: {
      type: Boolean,
      required: true
    },
    successDuration: {
      type: [Number, String],
      default: 500
    },
    animationDuration: {
      type: [Number, String],
      default: 300
    },
    headHeight: {
      type: [Number, String],
      default: DEFAULT_HEAD_HEIGHT
    }
  },
  data: function data() {
    return {
      status: 'normal',
      distance: 0,
      duration: 0
    };
  },
  computed: {
    touchable: function touchable() {
      return this.status !== 'loading' && this.status !== 'success' && !this.disabled;
    },
    headStyle: function headStyle() {
      if (this.headHeight !== DEFAULT_HEAD_HEIGHT) {
        return {
          height: this.headHeight + "px"
        };
      }
    }
  },
  watch: {
    value: function value(loading) {
      this.duration = this.animationDuration;
      if (loading) {
        this.setStatus(+this.headHeight, true);
      } else if (this.slots('success') || this.successText) {
        this.showSuccessTip();
      } else {
        this.setStatus(0, false);
      }
    }
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$refs.track);
    this.scrollEl = getScroller(this.$el);
  },
  methods: {
    checkPullStart: function checkPullStart(event) {
      this.ceiling = getScrollTop(this.scrollEl) === 0;
      if (this.ceiling) {
        this.duration = 0;
        this.touchStart(event);
      }
    },
    onTouchStart: function onTouchStart(event) {
      if (this.touchable) {
        this.checkPullStart(event);
      }
    },
    onTouchMove: function onTouchMove(event) {
      if (!this.touchable) {
        return;
      }
      if (!this.ceiling) {
        this.checkPullStart(event);
      }
      this.touchMove(event);
      if (this.ceiling && this.deltaY >= 0 && this.direction === 'vertical') {
        preventDefault(event);
        this.setStatus(this.ease(this.deltaY));
      }
    },
    onTouchEnd: function onTouchEnd() {
      var _this = this;
      if (this.touchable && this.ceiling && this.deltaY) {
        this.duration = this.animationDuration;
        if (this.status === 'loosing') {
          this.setStatus(+this.headHeight, true);
          this.$emit('input', true); // ensure value change can be watched

          this.$nextTick(function () {
            _this.$emit('refresh');
          });
        } else {
          this.setStatus(0);
        }
      }
    },
    ease: function ease(distance) {
      var pullDistance = +(this.pullDistance || this.headHeight);
      if (distance > pullDistance) {
        if (distance < pullDistance * 2) {
          distance = pullDistance + (distance - pullDistance) / 2;
        } else {
          distance = pullDistance * 1.5 + (distance - pullDistance * 2) / 4;
        }
      }
      return Math.round(distance);
    },
    setStatus: function setStatus(distance, isLoading) {
      var status;
      if (isLoading) {
        status = 'loading';
      } else if (distance === 0) {
        status = 'normal';
      } else {
        status = distance < (this.pullDistance || this.headHeight) ? 'pulling' : 'loosing';
      }
      this.distance = distance;
      if (status !== this.status) {
        this.status = status;
      }
    },
    genStatus: function genStatus() {
      var h = this.$createElement;
      var status = this.status,
        distance = this.distance;
      var slot = this.slots(status, {
        distance: distance
      });
      if (slot) {
        return slot;
      }
      var nodes = [];
      var text = this[status + "Text"] || t$c(status);
      if (TEXT_STATUS.indexOf(status) !== -1) {
        nodes.push(h("div", {
          "class": bem$o('text')
        }, [text]));
      }
      if (status === 'loading') {
        nodes.push(h(Loading$1, {
          "attrs": {
            "size": "16"
          }
        }, [text]));
      }
      return nodes;
    },
    showSuccessTip: function showSuccessTip() {
      var _this2 = this;
      this.status = 'success';
      setTimeout(function () {
        _this2.setStatus(0);
      }, this.successDuration);
    }
  },
  render: function render() {
    var h = arguments[0];
    var trackStyle = {
      transitionDuration: this.duration + "ms",
      transform: this.distance ? "translate3d(0," + this.distance + "px, 0)" : ''
    };
    return h("div", {
      "class": bem$o()
    }, [h("div", {
      "ref": "track",
      "class": bem$o('track'),
      "style": trackStyle
    }, [h("div", {
      "class": bem$o('head'),
      "style": this.headStyle
    }, [this.genStatus()]), this.slots()])]);
  }
});

// Utils
var _createNamespace$o = createNamespace('rate'),
  createComponent$s = _createNamespace$o[0],
  bem$n = _createNamespace$o[1];
function getRateStatus(value, index, allowHalf) {
  if (value >= index) {
    return 'full';
  }
  if (value + 0.5 >= index && allowHalf) {
    return 'half';
  }
  return 'void';
}
var Rate = createComponent$s({
  mixins: [TouchMixin, FieldMixin],
  props: {
    size: [Number, String],
    color: String,
    gutter: [Number, String],
    readonly: Boolean,
    disabled: Boolean,
    allowHalf: Boolean,
    voidColor: String,
    iconPrefix: String,
    disabledColor: String,
    value: {
      type: Number,
      default: 0
    },
    icon: {
      type: String,
      default: 'star'
    },
    voidIcon: {
      type: String,
      default: 'star-o'
    },
    count: {
      type: [Number, String],
      default: 5
    },
    touchable: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    list: function list() {
      var list = [];
      for (var i = 1; i <= this.count; i++) {
        list.push(getRateStatus(this.value, i, this.allowHalf));
      }
      return list;
    },
    sizeWithUnit: function sizeWithUnit() {
      return addUnit(this.size);
    },
    gutterWithUnit: function gutterWithUnit() {
      return addUnit(this.gutter);
    }
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$el);
  },
  methods: {
    select: function select(index) {
      if (!this.disabled && !this.readonly && index !== this.value) {
        this.$emit('input', index);
        this.$emit('change', index);
      }
    },
    onTouchStart: function onTouchStart(event) {
      var _this = this;
      if (this.readonly || this.disabled || !this.touchable) {
        return;
      }
      this.touchStart(event);
      var rects = this.$refs.items.map(function (item) {
        return item.getBoundingClientRect();
      });
      var ranges = [];
      rects.forEach(function (rect, index) {
        if (_this.allowHalf) {
          ranges.push({
            score: index + 0.5,
            left: rect.left
          }, {
            score: index + 1,
            left: rect.left + rect.width / 2
          });
        } else {
          ranges.push({
            score: index + 1,
            left: rect.left
          });
        }
      });
      this.ranges = ranges;
    },
    onTouchMove: function onTouchMove(event) {
      if (this.readonly || this.disabled || !this.touchable) {
        return;
      }
      this.touchMove(event);
      if (this.direction === 'horizontal') {
        preventDefault(event);
        var clientX = event.touches[0].clientX;
        this.select(this.getScoreByPosition(clientX));
      }
    },
    getScoreByPosition: function getScoreByPosition(x) {
      for (var i = this.ranges.length - 1; i > 0; i--) {
        if (x > this.ranges[i].left) {
          return this.ranges[i].score;
        }
      }
      return this.allowHalf ? 0.5 : 1;
    },
    genStar: function genStar(status, index) {
      var _this2 = this;
      var h = this.$createElement;
      var icon = this.icon,
        color = this.color,
        count = this.count,
        voidIcon = this.voidIcon,
        disabled = this.disabled,
        voidColor = this.voidColor,
        disabledColor = this.disabledColor;
      var score = index + 1;
      var isFull = status === 'full';
      var isVoid = status === 'void';
      var style;
      if (this.gutterWithUnit && score !== +count) {
        style = {
          paddingRight: this.gutterWithUnit
        };
      }
      return h("div", {
        "ref": "items",
        "refInFor": true,
        "key": index,
        "attrs": {
          "role": "radio",
          "tabindex": "0",
          "aria-setsize": count,
          "aria-posinset": score,
          "aria-checked": String(!isVoid)
        },
        "style": style,
        "class": bem$n('item')
      }, [h(Icon$3, {
        "attrs": {
          "size": this.sizeWithUnit,
          "name": isFull ? icon : voidIcon,
          "color": disabled ? disabledColor : isFull ? color : voidColor,
          "classPrefix": this.iconPrefix,
          "data-score": score
        },
        "class": bem$n('icon', {
          disabled: disabled,
          full: isFull
        }),
        "on": {
          "click": function click() {
            _this2.select(score);
          }
        }
      }), this.allowHalf && h(Icon$3, {
        "attrs": {
          "size": this.sizeWithUnit,
          "name": isVoid ? voidIcon : icon,
          "color": disabled ? disabledColor : isVoid ? voidColor : color,
          "classPrefix": this.iconPrefix,
          "data-score": score - 0.5
        },
        "class": bem$n('icon', ['half', {
          disabled: disabled,
          full: !isVoid
        }]),
        "on": {
          "click": function click() {
            _this2.select(score - 0.5);
          }
        }
      })]);
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    return h("div", {
      "class": bem$n({
        readonly: this.readonly,
        disabled: this.disabled
      }),
      "attrs": {
        "tabindex": "0",
        "role": "radiogroup"
      }
    }, [this.list.map(function (status, index) {
      return _this3.genStar(status, index);
    })]);
  }
});

var _createNamespace$n = createNamespace('row'),
  createComponent$r = _createNamespace$n[0],
  bem$m = _createNamespace$n[1];
var Row = createComponent$r({
  mixins: [ParentMixin('vanRow')],
  props: {
    type: String,
    align: String,
    justify: String,
    tag: {
      type: String,
      default: 'div'
    },
    gutter: {
      type: [Number, String],
      default: 0
    }
  },
  computed: {
    spaces: function spaces() {
      var gutter = Number(this.gutter);
      if (!gutter) {
        return;
      }
      var spaces = [];
      var groups = [[]];
      var totalSpan = 0;
      this.children.forEach(function (item, index) {
        totalSpan += Number(item.span);
        if (totalSpan > 24) {
          groups.push([index]);
          totalSpan -= 24;
        } else {
          groups[groups.length - 1].push(index);
        }
      });
      groups.forEach(function (group) {
        var averagePadding = gutter * (group.length - 1) / group.length;
        group.forEach(function (item, index) {
          if (index === 0) {
            spaces.push({
              right: averagePadding
            });
          } else {
            var left = gutter - spaces[item - 1].right;
            var right = averagePadding - left;
            spaces.push({
              left: left,
              right: right
            });
          }
        });
      });
      return spaces;
    }
  },
  methods: {
    onClick: function onClick(event) {
      this.$emit('click', event);
    }
  },
  render: function render() {
    var _bem;
    var h = arguments[0];
    var align = this.align,
      justify = this.justify;
    var flex = this.type === 'flex';
    return h(this.tag, {
      "class": bem$m((_bem = {
        flex: flex
      }, _bem["align-" + align] = flex && align, _bem["justify-" + justify] = flex && justify, _bem)),
      "on": {
        "click": this.onClick
      }
    }, [this.slots()]);
  }
});

var _createNamespace$m = createNamespace('search'),
  createComponent$q = _createNamespace$m[0],
  bem$l = _createNamespace$m[1],
  t$b = _createNamespace$m[2];
function Search(h, props, slots, ctx) {
  function Label() {
    if (slots.label || props.label) {
      return h("div", {
        "class": bem$l('label')
      }, [slots.label ? slots.label() : props.label]);
    }
  }
  function Action() {
    if (!props.showAction) {
      return;
    }
    function onCancel() {
      if (slots.action) {
        return;
      }
      emit(ctx, 'input', '');
      emit(ctx, 'cancel');
    }
    return h("div", {
      "class": bem$l('action'),
      "attrs": {
        "role": "button",
        "tabindex": "0"
      },
      "on": {
        "click": onCancel
      }
    }, [slots.action ? slots.action() : props.actionText || t$b('cancel')]);
  }
  var fieldData = {
    attrs: ctx.data.attrs,
    on: _extends$1({}, ctx.listeners, {
      keypress: function keypress(event) {
        // press enter
        if (event.keyCode === 13) {
          preventDefault(event);
          emit(ctx, 'search', props.value);
        }
        emit(ctx, 'keypress', event);
      }
    })
  };
  var inheritData = inherit(ctx);
  inheritData.attrs = undefined;
  return h("div", _mergeJSXProps([{
    "class": bem$l({
      'show-action': props.showAction
    }),
    "style": {
      background: props.background
    }
  }, inheritData]), [slots.left == null ? void 0 : slots.left(), h("div", {
    "class": bem$l('content', props.shape)
  }, [Label(), h(Field, _mergeJSXProps([{
    "attrs": {
      "type": "search",
      "border": false,
      "value": props.value,
      "leftIcon": props.leftIcon,
      "rightIcon": props.rightIcon,
      "clearable": props.clearable,
      "clearTrigger": props.clearTrigger
    },
    "scopedSlots": {
      'left-icon': slots['left-icon'],
      'right-icon': slots['right-icon']
    }
  }, fieldData]))]), Action()]);
}
Search.props = {
  value: String,
  label: String,
  rightIcon: String,
  actionText: String,
  background: String,
  showAction: Boolean,
  clearTrigger: String,
  shape: {
    type: String,
    default: 'square'
  },
  clearable: {
    type: Boolean,
    default: true
  },
  leftIcon: {
    type: String,
    default: 'search'
  }
};
var Search$1 = createComponent$q(Search);

var PRESET_ICONS = ['qq', 'link', 'weibo', 'wechat', 'poster', 'qrcode', 'weapp-qrcode', 'wechat-moments'];
var _createNamespace$l = createNamespace('share-sheet'),
  createComponent$p = _createNamespace$l[0],
  bem$k = _createNamespace$l[1],
  t$a = _createNamespace$l[2];
var ShareSheet = createComponent$p({
  props: _extends$1({}, popupMixinProps, {
    title: String,
    duration: String,
    cancelText: String,
    description: String,
    getContainer: [String, Function],
    options: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    overlay: {
      type: Boolean,
      default: true
    },
    closeOnPopstate: {
      type: Boolean,
      default: true
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    }
  }),
  methods: {
    onCancel: function onCancel() {
      this.toggle(false);
      this.$emit('cancel');
    },
    onSelect: function onSelect(option, index) {
      this.$emit('select', option, index);
    },
    toggle: function toggle(val) {
      this.$emit('input', val);
    },
    getIconURL: function getIconURL(icon) {
      if (PRESET_ICONS.indexOf(icon) !== -1) {
        return "https://img01.yzcdn.cn/vant/share-sheet-" + icon + ".png";
      }
      return icon;
    },
    genHeader: function genHeader() {
      var h = this.$createElement;
      var title = this.slots('title') || this.title;
      var description = this.slots('description') || this.description;
      if (!title && !description) {
        return;
      }
      return h("div", {
        "class": bem$k('header')
      }, [title && h("h2", {
        "class": bem$k('title')
      }, [title]), description && h("span", {
        "class": bem$k('description')
      }, [description])]);
    },
    genOptions: function genOptions(options, showBorder) {
      var _this = this;
      var h = this.$createElement;
      return h("div", {
        "class": bem$k('options', {
          border: showBorder
        })
      }, [options.map(function (option, index) {
        return h("div", {
          "attrs": {
            "role": "button",
            "tabindex": "0"
          },
          "class": [bem$k('option'), option.className],
          "on": {
            "click": function click() {
              _this.onSelect(option, index);
            }
          }
        }, [h("img", {
          "attrs": {
            "src": _this.getIconURL(option.icon)
          },
          "class": bem$k('icon')
        }), option.name && h("span", {
          "class": bem$k('name')
        }, [option.name]), option.description && h("span", {
          "class": bem$k('option-description')
        }, [option.description])]);
      })]);
    },
    genRows: function genRows() {
      var _this2 = this;
      var options = this.options;
      if (Array.isArray(options[0])) {
        return options.map(function (item, index) {
          return _this2.genOptions(item, index !== 0);
        });
      }
      return this.genOptions(options);
    },
    genCancelText: function genCancelText() {
      var _this$cancelText;
      var h = this.$createElement;
      var cancelText = (_this$cancelText = this.cancelText) != null ? _this$cancelText : t$a('cancel');
      if (cancelText) {
        return h("button", {
          "attrs": {
            "type": "button"
          },
          "class": bem$k('cancel'),
          "on": {
            "click": this.onCancel
          }
        }, [cancelText]);
      }
    },
    onClickOverlay: function onClickOverlay() {
      this.$emit('click-overlay');
    }
  },
  render: function render() {
    var h = arguments[0];
    return h(Popup, {
      "attrs": {
        "round": true,
        "value": this.value,
        "position": "bottom",
        "overlay": this.overlay,
        "duration": this.duration,
        "lazyRender": this.lazyRender,
        "lockScroll": this.lockScroll,
        "getContainer": this.getContainer,
        "closeOnPopstate": this.closeOnPopstate,
        "closeOnClickOverlay": this.closeOnClickOverlay,
        "safeAreaInsetBottom": this.safeAreaInsetBottom
      },
      "class": bem$k(),
      "on": {
        "input": this.toggle,
        "click-overlay": this.onClickOverlay
      }
    }, [this.genHeader(), this.genRows(), this.genCancelText()]);
  }
});

var _createNamespace$k = createNamespace('sidebar'),
  createComponent$o = _createNamespace$k[0],
  bem$j = _createNamespace$k[1];
var Sidebar = createComponent$o({
  mixins: [ParentMixin('vanSidebar')],
  model: {
    prop: 'activeKey'
  },
  props: {
    activeKey: {
      type: [Number, String],
      default: 0
    }
  },
  data: function data() {
    return {
      index: +this.activeKey
    };
  },
  watch: {
    activeKey: function activeKey() {
      this.setIndex(+this.activeKey);
    }
  },
  methods: {
    setIndex: function setIndex(index) {
      if (index !== this.index) {
        this.index = index;
        this.$emit('change', index);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$j()
    }, [this.slots()]);
  }
});

var _createNamespace$j = createNamespace('sidebar-item'),
  createComponent$n = _createNamespace$j[0],
  bem$i = _createNamespace$j[1];
var SidebarItem = createComponent$n({
  mixins: [ChildrenMixin('vanSidebar')],
  props: _extends$1({}, routeProps, {
    dot: Boolean,
    // @deprecated
    info: [Number, String],
    badge: [Number, String],
    title: String,
    disabled: Boolean
  }),
  computed: {
    select: function select() {
      return this.index === +this.parent.activeKey;
    }
  },
  methods: {
    onClick: function onClick() {
      if (this.disabled) {
        return;
      }
      this.$emit('click', this.index);
      this.parent.$emit('input', this.index);
      this.parent.setIndex(this.index);
      route(this.$router, this);
    }
  },
  render: function render() {
    var _this$slots, _this$badge;
    var h = arguments[0];
    return h("a", {
      "class": bem$i({
        select: this.select,
        disabled: this.disabled
      }),
      "on": {
        "click": this.onClick
      }
    }, [h("div", {
      "class": bem$i('text')
    }, [(_this$slots = this.slots('title')) != null ? _this$slots : this.title, h(Info$1, {
      "attrs": {
        "dot": this.dot,
        "info": (_this$badge = this.badge) != null ? _this$badge : this.info
      },
      "class": bem$i('info')
    })])]);
  }
});

var _createNamespace$i = createNamespace('skeleton'),
  createComponent$m = _createNamespace$i[0],
  bem$h = _createNamespace$i[1];
var DEFAULT_ROW_WIDTH = '100%';
var DEFAULT_LAST_ROW_WIDTH = '60%';
function Skeleton$1(h, props, slots, ctx) {
  if (!props.loading) {
    return slots.default && slots.default();
  }
  function Title() {
    if (props.title) {
      return h("h3", {
        "class": bem$h('title'),
        "style": {
          width: addUnit(props.titleWidth)
        }
      });
    }
  }
  function Rows() {
    var Rows = [];
    var rowWidth = props.rowWidth;
    function getRowWidth(index) {
      if (rowWidth === DEFAULT_ROW_WIDTH && index === +props.row - 1) {
        return DEFAULT_LAST_ROW_WIDTH;
      }
      if (Array.isArray(rowWidth)) {
        return rowWidth[index];
      }
      return rowWidth;
    }
    for (var i = 0; i < props.row; i++) {
      Rows.push(h("div", {
        "class": bem$h('row'),
        "style": {
          width: addUnit(getRowWidth(i))
        }
      }));
    }
    return Rows;
  }
  function Avatar() {
    if (props.avatar) {
      var size = addUnit(props.avatarSize);
      return h("div", {
        "class": bem$h('avatar', props.avatarShape),
        "style": {
          width: size,
          height: size
        }
      });
    }
  }
  return h("div", _mergeJSXProps([{
    "class": bem$h({
      animate: props.animate,
      round: props.round
    })
  }, inherit(ctx)]), [Avatar(), h("div", {
    "class": bem$h('content')
  }, [Title(), Rows()])]);
}
Skeleton$1.props = {
  title: Boolean,
  round: Boolean,
  avatar: Boolean,
  titleWidth: [Number, String],
  avatarSize: [Number, String],
  row: {
    type: [Number, String],
    default: 0
  },
  loading: {
    type: Boolean,
    default: true
  },
  animate: {
    type: Boolean,
    default: true
  },
  avatarShape: {
    type: String,
    default: 'round'
  },
  rowWidth: {
    type: [Number, String, Array],
    default: DEFAULT_ROW_WIDTH
  }
};
var Skeleton$2 = createComponent$m(Skeleton$1);

/**
 * Sku only provide zh-CN lang by default
 */
var lang$1 = {
  'zh-CN': {
    vanSku: {
      select: '请选择',
      selected: '已选',
      selectSku: '请先选择商品规格',
      soldout: '库存不足',
      originPrice: '原价',
      minusTip: '至少选择一件',
      minusStartTip: function minusStartTip(start) {
        return start + "\u4EF6\u8D77\u552E";
      },
      unavailable: '商品已经无法购买啦',
      stock: '剩余',
      stockUnit: '件',
      quotaTip: function quotaTip(quota) {
        return "\u6BCF\u4EBA\u9650\u8D2D" + quota + "\u4EF6";
      },
      quotaUsedTip: function quotaUsedTip(quota, count) {
        return "\u6BCF\u4EBA\u9650\u8D2D" + quota + "\u4EF6\uFF0C\u4F60\u5DF2\u8D2D\u4E70" + count + "\u4EF6";
      }
    },
    vanSkuActions: {
      buy: '立即购买',
      addCart: '加入购物车'
    },
    vanSkuImgUploader: {
      oversize: function oversize(maxSize) {
        return "\u6700\u5927\u53EF\u4E0A\u4F20\u56FE\u7247\u4E3A" + maxSize + "MB\uFF0C\u8BF7\u5C1D\u8BD5\u538B\u7F29\u56FE\u7247\u5C3A\u5BF8";
      },
      fail: '上传失败',
      uploading: '上传中...'
    },
    vanSkuStepper: {
      quotaLimit: function quotaLimit(quota) {
        return "\u9650\u8D2D" + quota + "\u4EF6";
      },
      quotaStart: function quotaStart(start) {
        return start + "\u4EF6\u8D77\u552E";
      },
      comma: '，',
      num: '购买数量'
    },
    vanSkuMessages: {
      fill: '请填写',
      upload: '请上传',
      imageLabel: '仅限一张',
      invalid: {
        tel: '请填写正确的数字格式留言',
        mobile: '手机号长度为6-20位数字',
        email: '请填写正确的邮箱',
        id_no: '请填写正确的身份证号码'
      },
      placeholder: {
        id_no: '请填写身份证号',
        text: '请填写留言',
        tel: '请填写数字',
        email: '请填写邮箱',
        date: '请选择日期',
        time: '请选择时间',
        textarea: '请填写留言',
        mobile: '请填写手机号'
      }
    },
    vanSkuRow: {
      multiple: '可多选'
    },
    vanSkuDatetimeField: {
      title: {
        date: '选择年月日',
        time: '选择时间',
        datetime: '选择日期时间'
      },
      format: {
        year: '年',
        month: '月',
        day: '日',
        hour: '时',
        minute: '分'
      }
    }
  }
};

var LIMIT_TYPE = {
  QUOTA_LIMIT: 0,
  STOCK_LIMIT: 1
};
var UNSELECTED_SKU_VALUE_ID = '';
var constants = {
  LIMIT_TYPE: LIMIT_TYPE,
  UNSELECTED_SKU_VALUE_ID: UNSELECTED_SKU_VALUE_ID
};

/*
  normalize sku tree

  [
    {
      count: 2,
      k: "品种", // 规格名称 skuKeyName
      k_id: "1200", // skuKeyId
      k_s: "s1" // skuKeyStr
      v: [ // skuValues
        { // skuValue
          id: "1201", // skuValueId
          name: "萌" // 具体的规格值 skuValueName
        }, {
          id: "973",
          name: "帅"
        }
      ]
    },
    ...
  ]
                |
                v
  {
    s1: [{
      id: "1201",
      name: "萌"
    }, {
      id: "973",
      name: "帅"
    }],
    ...
  }
 */

var normalizeSkuTree = function normalizeSkuTree(skuTree) {
  var normalizedTree = {};
  skuTree.forEach(function (treeItem) {
    normalizedTree[treeItem.k_s] = treeItem.v;
  });
  return normalizedTree;
};
var normalizePropList = function normalizePropList(propList) {
  var normalizedProp = {};
  propList.forEach(function (item) {
    var itemObj = {};
    item.v.forEach(function (it) {
      itemObj[it.id] = it;
    });
    normalizedProp[item.k_id] = itemObj;
  });
  return normalizedProp;
}; // 判断是否所有的sku都已经选中

var isAllSelected = function isAllSelected(skuTree, selectedSku) {
  // 筛选selectedSku对象中key值不为空的值
  var selected = Object.keys(selectedSku).filter(function (skuKeyStr) {
    return selectedSku[skuKeyStr] !== UNSELECTED_SKU_VALUE_ID;
  });
  return skuTree.length === selected.length;
}; // 根据已选择的 sku 获取 skuComb

var getSkuComb = function getSkuComb(skuList, selectedSku) {
  var skuComb = skuList.filter(function (item) {
    return Object.keys(selectedSku).every(function (skuKeyStr) {
      return String(item[skuKeyStr]) === String(selectedSku[skuKeyStr]);
    });
  });
  return skuComb[0];
}; // 获取已选择的sku名称

var getSelectedSkuValues = function getSelectedSkuValues(skuTree, selectedSku) {
  var normalizedTree = normalizeSkuTree(skuTree);
  return Object.keys(selectedSku).reduce(function (selectedValues, skuKeyStr) {
    var skuValues = normalizedTree[skuKeyStr] || [];
    var skuValueId = selectedSku[skuKeyStr];
    if (skuValueId !== UNSELECTED_SKU_VALUE_ID && skuValues.length > 0) {
      var skuValue = skuValues.filter(function (value) {
        return value.id === skuValueId;
      })[0];
      skuValue && selectedValues.push(skuValue);
    }
    return selectedValues;
  }, []);
}; // 判断sku是否可选

var isSkuChoosable = function isSkuChoosable(skuList, selectedSku, skuToChoose) {
  var _extends2;
  var key = skuToChoose.key,
    valueId = skuToChoose.valueId; // 先假设sku已选中，拼入已选中sku对象中

  var matchedSku = _extends$1({}, selectedSku, (_extends2 = {}, _extends2[key] = valueId, _extends2)); // 再判断剩余sku是否全部不可选，若不可选则当前sku不可选中

  var skusToCheck = Object.keys(matchedSku).filter(function (skuKey) {
    return matchedSku[skuKey] !== UNSELECTED_SKU_VALUE_ID;
  });
  var filteredSku = skuList.filter(function (sku) {
    return skusToCheck.every(function (skuKey) {
      return String(matchedSku[skuKey]) === String(sku[skuKey]);
    });
  });
  var stock = filteredSku.reduce(function (total, sku) {
    total += sku.stock_num;
    return total;
  }, 0);
  return stock > 0;
};
var getSelectedPropValues = function getSelectedPropValues(propList, selectedProp) {
  var normalizeProp = normalizePropList(propList);
  return Object.keys(selectedProp).reduce(function (acc, cur) {
    selectedProp[cur].forEach(function (it) {
      acc.push(_extends$1({}, normalizeProp[cur][it]));
    });
    return acc;
  }, []);
};
var getSelectedProperties = function getSelectedProperties(propList, selectedProp) {
  var list = [];
  (propList || []).forEach(function (prop) {
    if (selectedProp[prop.k_id] && selectedProp[prop.k_id].length > 0) {
      var v = [];
      prop.v.forEach(function (it) {
        if (selectedProp[prop.k_id].indexOf(it.id) > -1) {
          v.push(_extends$1({}, it));
        }
      });
      list.push(_extends$1({}, prop, {
        v: v
      }));
    }
  });
  return list;
};
var skuHelper = {
  normalizeSkuTree: normalizeSkuTree,
  getSkuComb: getSkuComb,
  getSelectedSkuValues: getSelectedSkuValues,
  isAllSelected: isAllSelected,
  isSkuChoosable: isSkuChoosable,
  getSelectedPropValues: getSelectedPropValues,
  getSelectedProperties: getSelectedProperties
};

var _createNamespace$h = createNamespace('sku-header'),
  createComponent$l = _createNamespace$h[0],
  bem$g = _createNamespace$h[1];
function getSkuImgValue(sku, selectedSku) {
  var imgValue;
  sku.tree.some(function (item) {
    var id = selectedSku[item.k_s];
    if (id && item.v) {
      var matchedSku = item.v.filter(function (skuValue) {
        return skuValue.id === id;
      })[0] || {};
      var img = matchedSku.previewImgUrl || matchedSku.imgUrl || matchedSku.img_url;
      if (img) {
        imgValue = _extends$1({}, matchedSku, {
          ks: item.k_s,
          imgUrl: img
        });
        return true;
      }
    }
    return false;
  });
  return imgValue;
}
function SkuHeader$1(h, props, slots, ctx) {
  var _slots$skuHeaderIma;
  var sku = props.sku,
    goods = props.goods,
    skuEventBus = props.skuEventBus,
    selectedSku = props.selectedSku,
    _props$showHeaderImag = props.showHeaderImage,
    showHeaderImage = _props$showHeaderImag === void 0 ? true : _props$showHeaderImag;
  var selectedValue = getSkuImgValue(sku, selectedSku);
  var imgUrl = selectedValue ? selectedValue.imgUrl : goods.picture;
  var previewImage = function previewImage() {
    skuEventBus.$emit('sku:previewImage', selectedValue);
  };
  return h("div", _mergeJSXProps([{
    "class": [bem$g(), BORDER_BOTTOM]
  }, inherit(ctx)]), [showHeaderImage && h(Image$1, {
    "attrs": {
      "fit": "cover",
      "src": imgUrl
    },
    "class": bem$g('img-wrap'),
    "on": {
      "click": previewImage
    }
  }, [(_slots$skuHeaderIma = slots['sku-header-image-extra']) == null ? void 0 : _slots$skuHeaderIma.call(slots)]), h("div", {
    "class": bem$g('goods-info')
  }, [slots.default == null ? void 0 : slots.default()])]);
}
SkuHeader$1.props = {
  sku: Object,
  goods: Object,
  skuEventBus: Object,
  selectedSku: Object,
  showHeaderImage: Boolean
};
var SkuHeader$2 = createComponent$l(SkuHeader$1);

var _createNamespace$g = createNamespace('sku-header-item'),
  createComponent$k = _createNamespace$g[0],
  bem$f = _createNamespace$g[1];
function SkuHeader(h, props, slots, ctx) {
  return h("div", _mergeJSXProps([{
    "class": bem$f()
  }, inherit(ctx)]), [slots.default && slots.default()]);
}
var SkuHeaderItem = createComponent$k(SkuHeader);

// Utils
var _createNamespace$f = createNamespace('sku-row'),
  createComponent$j = _createNamespace$f[0],
  bem$e = _createNamespace$f[1],
  t$9 = _createNamespace$f[2];
var SkuRow = createComponent$j({
  mixins: [ParentMixin('vanSkuRows'), BindEventMixin(function (bind) {
    if (this.scrollable && this.$refs.scroller) {
      bind(this.$refs.scroller, 'scroll', this.onScroll);
    }
  })],
  props: {
    skuRow: Object
  },
  data: function data() {
    return {
      progress: 0
    };
  },
  computed: {
    scrollable: function scrollable() {
      return this.skuRow.largeImageMode && this.skuRow.v.length > 6;
    }
  },
  methods: {
    onScroll: function onScroll() {
      var _this$$refs = this.$refs,
        scroller = _this$$refs.scroller,
        row = _this$$refs.row;
      var distance = row.offsetWidth - scroller.offsetWidth;
      this.progress = scroller.scrollLeft / distance;
    },
    genTitle: function genTitle() {
      var h = this.$createElement;
      return h("div", {
        "class": bem$e('title')
      }, [this.skuRow.k, this.skuRow.is_multiple && h("span", {
        "class": bem$e('title-multiple')
      }, ["\uFF08", t$9('multiple'), "\uFF09"])]);
    },
    genIndicator: function genIndicator() {
      var h = this.$createElement;
      if (this.scrollable) {
        var style = {
          transform: "translate3d(" + this.progress * 20 + "px, 0, 0)"
        };
        return h("div", {
          "class": bem$e('indicator-wrapper')
        }, [h("div", {
          "class": bem$e('indicator')
        }, [h("div", {
          "class": bem$e('indicator-slider'),
          "style": style
        })])]);
      }
    },
    genContent: function genContent() {
      var h = this.$createElement;
      var nodes = this.slots();
      if (this.skuRow.largeImageMode) {
        var top = [];
        var bottom = [];
        nodes.forEach(function (node, index) {
          var group = Math.floor(index / 3) % 2 === 0 ? top : bottom;
          group.push(node);
        });
        return h("div", {
          "class": bem$e('scroller'),
          "ref": "scroller"
        }, [h("div", {
          "class": bem$e('row'),
          "ref": "row"
        }, [top]), bottom.length ? h("div", {
          "class": bem$e('row')
        }, [bottom]) : null]);
      }
      return nodes;
    },
    centerItem: function centerItem(selectSkuId) {
      if (!this.skuRow.largeImageMode || !selectSkuId) {
        return;
      }
      var _this$children = this.children,
        children = _this$children === void 0 ? [] : _this$children;
      var _this$$refs2 = this.$refs,
        scroller = _this$$refs2.scroller,
        row = _this$$refs2.row;
      var child = children.find(function (it) {
        return +it.skuValue.id === +selectSkuId;
      });
      if (scroller && row && child && child.$el) {
        var target = child.$el;
        var to = target.offsetLeft - (scroller.offsetWidth - target.offsetWidth) / 2;
        scroller.scrollLeft = to;
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": [bem$e(), BORDER_BOTTOM]
    }, [this.genTitle(), this.genContent(), this.genIndicator()]);
  }
});

var _createNamespace$e = createNamespace('sku-row-item'),
  createComponent$i = _createNamespace$e[0];
var SkuRowItem = createComponent$i({
  mixins: [ChildrenMixin('vanSkuRows')],
  props: {
    lazyLoad: Boolean,
    skuValue: Object,
    skuKeyStr: String,
    skuEventBus: Object,
    selectedSku: Object,
    largeImageMode: Boolean,
    disableSoldoutSku: Boolean,
    skuList: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  computed: {
    imgUrl: function imgUrl() {
      var url = this.skuValue.imgUrl || this.skuValue.img_url;
      return this.largeImageMode ? url || 'https://img01.yzcdn.cn/upload_files/2020/06/24/FmKWDg0bN9rMcTp9ne8MXiQWGtLn.png' : url;
    },
    choosable: function choosable() {
      if (!this.disableSoldoutSku) {
        return true;
      }
      return isSkuChoosable(this.skuList, this.selectedSku, {
        key: this.skuKeyStr,
        valueId: this.skuValue.id
      });
    }
  },
  methods: {
    onSelect: function onSelect() {
      if (this.choosable) {
        this.skuEventBus.$emit('sku:select', _extends$1({}, this.skuValue, {
          skuKeyStr: this.skuKeyStr
        }));
      }
    },
    onPreviewImg: function onPreviewImg(event) {
      event.stopPropagation();
      var skuValue = this.skuValue,
        skuKeyStr = this.skuKeyStr;
      this.skuEventBus.$emit('sku:previewImage', _extends$1({}, skuValue, {
        ks: skuKeyStr,
        imgUrl: skuValue.imgUrl || skuValue.img_url
      }));
    },
    genImage: function genImage(classPrefix) {
      var h = this.$createElement;
      if (this.imgUrl) {
        return h(Image$1, {
          "attrs": {
            "fit": "cover",
            "src": this.imgUrl,
            "lazyLoad": this.lazyLoad
          },
          "class": classPrefix + "-img"
        });
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var choosed = this.skuValue.id === this.selectedSku[this.skuKeyStr];
    var classPrefix = this.largeImageMode ? bem$e('image-item') : bem$e('item');
    return h("span", {
      "class": [classPrefix, choosed ? classPrefix + "--active" : '', !this.choosable ? classPrefix + "--disabled" : ''],
      "on": {
        "click": this.onSelect
      }
    }, [this.genImage(classPrefix), h("div", {
      "class": classPrefix + "-name"
    }, [this.largeImageMode ? h("span", {
      "class": {
        'van-multi-ellipsis--l2': this.largeImageMode
      }
    }, [this.skuValue.name]) : this.skuValue.name]), this.largeImageMode && h(Icon$3, {
      "attrs": {
        "name": "enlarge"
      },
      "class": classPrefix + "-img-icon",
      "on": {
        "click": this.onPreviewImg
      }
    })]);
  }
});

var _createNamespace$d = createNamespace('sku-row-prop-item'),
  createComponent$h = _createNamespace$d[0];
var SkuRowPropItem = createComponent$h({
  props: {
    skuValue: Object,
    skuKeyStr: String,
    skuEventBus: Object,
    selectedProp: Object,
    multiple: Boolean,
    disabled: Boolean
  },
  computed: {
    choosed: function choosed() {
      var selectedProp = this.selectedProp,
        skuKeyStr = this.skuKeyStr,
        skuValue = this.skuValue;
      if (selectedProp && selectedProp[skuKeyStr]) {
        return selectedProp[skuKeyStr].indexOf(skuValue.id) > -1;
      }
      return false;
    }
  },
  methods: {
    onSelect: function onSelect() {
      if (this.disabled) return;
      this.skuEventBus.$emit('sku:propSelect', _extends$1({}, this.skuValue, {
        skuKeyStr: this.skuKeyStr,
        multiple: this.multiple
      }));
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("span", {
      "class": ['van-sku-row__item', {
        'van-sku-row__item--active': this.choosed
      }, {
        'van-sku-row__item--disabled': this.disabled
      }],
      "on": {
        "click": this.onSelect
      }
    }, [h("span", {
      "class": "van-sku-row__item-name"
    }, [this.skuValue.name])]);
  }
});

var _createNamespace$c = createNamespace('stepper'),
  createComponent$g = _createNamespace$c[0],
  bem$d = _createNamespace$c[1];
var LONG_PRESS_START_TIME = 600;
var LONG_PRESS_INTERVAL = 200;
function equal$1(value1, value2) {
  return String(value1) === String(value2);
}
var Stepper = createComponent$g({
  mixins: [FieldMixin],
  props: {
    value: null,
    theme: String,
    integer: Boolean,
    disabled: Boolean,
    allowEmpty: Boolean,
    inputWidth: [Number, String],
    buttonSize: [Number, String],
    asyncChange: Boolean,
    placeholder: String,
    disablePlus: Boolean,
    disableMinus: Boolean,
    disableInput: Boolean,
    decimalLength: [Number, String],
    name: {
      type: [Number, String],
      default: ''
    },
    min: {
      type: [Number, String],
      default: 1
    },
    max: {
      type: [Number, String],
      default: Infinity
    },
    step: {
      type: [Number, String],
      default: 1
    },
    defaultValue: {
      type: [Number, String],
      default: 1
    },
    showPlus: {
      type: Boolean,
      default: true
    },
    showMinus: {
      type: Boolean,
      default: true
    },
    showInput: {
      type: Boolean,
      default: true
    },
    longPress: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    var _this$value;
    var defaultValue = (_this$value = this.value) != null ? _this$value : this.defaultValue;
    var value = this.format(defaultValue);
    if (!equal$1(value, this.value)) {
      this.$emit('input', value);
    }
    return {
      currentValue: value
    };
  },
  computed: {
    minusDisabled: function minusDisabled() {
      return this.disabled || this.disableMinus || this.currentValue <= +this.min;
    },
    plusDisabled: function plusDisabled() {
      return this.disabled || this.disablePlus || this.currentValue >= +this.max;
    },
    inputStyle: function inputStyle() {
      var style = {};
      if (this.inputWidth) {
        style.width = addUnit(this.inputWidth);
      }
      if (this.buttonSize) {
        style.height = addUnit(this.buttonSize);
      }
      return style;
    },
    buttonStyle: function buttonStyle() {
      if (this.buttonSize) {
        var size = addUnit(this.buttonSize);
        return {
          width: size,
          height: size
        };
      }
    }
  },
  watch: {
    max: 'check',
    min: 'check',
    integer: 'check',
    decimalLength: 'check',
    value: function value(val) {
      if (!equal$1(val, this.currentValue)) {
        this.currentValue = this.format(val);
      }
    },
    currentValue: function currentValue(val) {
      this.$emit('input', val);
      this.$emit('change', val, {
        name: this.name
      });
    }
  },
  methods: {
    check: function check() {
      var val = this.format(this.currentValue);
      if (!equal$1(val, this.currentValue)) {
        this.currentValue = val;
      }
    },
    // formatNumber illegal characters
    formatNumber: function formatNumber$1(value) {
      return formatNumber(String(value), !this.integer);
    },
    format: function format(value) {
      if (this.allowEmpty && value === '') {
        return value;
      }
      value = this.formatNumber(value); // format range

      value = value === '' ? 0 : +value;
      value = isNaN$1(value) ? this.min : value;
      value = Math.max(Math.min(this.max, value), this.min); // format decimal

      if (isDef(this.decimalLength)) {
        value = value.toFixed(this.decimalLength);
      }
      return value;
    },
    onInput: function onInput(event) {
      var value = event.target.value;
      var formatted = this.formatNumber(value); // limit max decimal length

      if (isDef(this.decimalLength) && formatted.indexOf('.') !== -1) {
        var pair = formatted.split('.');
        formatted = pair[0] + "." + pair[1].slice(0, this.decimalLength);
      }
      if (!equal$1(value, formatted)) {
        event.target.value = formatted;
      } // prefer number type

      if (formatted === String(+formatted)) {
        formatted = +formatted;
      }
      this.emitChange(formatted);
    },
    emitChange: function emitChange(value) {
      if (this.asyncChange) {
        this.$emit('input', value);
        this.$emit('change', value, {
          name: this.name
        });
      } else {
        this.currentValue = value;
      }
    },
    onChange: function onChange() {
      var type = this.type;
      if (this[type + "Disabled"]) {
        this.$emit('overlimit', type);
        return;
      }
      var diff = type === 'minus' ? -this.step : +this.step;
      var value = this.format(addNumber(+this.currentValue, diff));
      this.emitChange(value);
      this.$emit(type);
    },
    onFocus: function onFocus(event) {
      // readonly not work in legacy mobile safari
      if (this.disableInput && this.$refs.input) {
        this.$refs.input.blur();
      } else {
        this.$emit('focus', event);
      }
    },
    onBlur: function onBlur(event) {
      var value = this.format(event.target.value);
      event.target.value = value;
      this.emitChange(value);
      this.$emit('blur', event);
      resetScroll();
    },
    longPressStep: function longPressStep() {
      var _this = this;
      this.longPressTimer = setTimeout(function () {
        _this.onChange();
        _this.longPressStep(_this.type);
      }, LONG_PRESS_INTERVAL);
    },
    onTouchStart: function onTouchStart() {
      var _this2 = this;
      if (!this.longPress) {
        return;
      }
      clearTimeout(this.longPressTimer);
      this.isLongPress = false;
      this.longPressTimer = setTimeout(function () {
        _this2.isLongPress = true;
        _this2.onChange();
        _this2.longPressStep();
      }, LONG_PRESS_START_TIME);
    },
    onTouchEnd: function onTouchEnd(event) {
      if (!this.longPress) {
        return;
      }
      clearTimeout(this.longPressTimer);
      if (this.isLongPress) {
        preventDefault(event);
      }
    },
    onMousedown: function onMousedown(event) {
      // fix mobile safari page scroll down issue
      // see: https://github.com/vant-ui/vant/issues/7690
      if (this.disableInput) {
        event.preventDefault();
      }
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    var createListeners = function createListeners(type) {
      return {
        on: {
          click: function click(e) {
            // disable double tap scrolling on mobile safari
            e.preventDefault();
            _this3.type = type;
            _this3.onChange();
          },
          touchstart: function touchstart() {
            _this3.type = type;
            _this3.onTouchStart();
          },
          touchend: _this3.onTouchEnd,
          touchcancel: _this3.onTouchEnd
        }
      };
    };
    return h("div", {
      "class": bem$d([this.theme])
    }, [h("button", _mergeJSXProps([{
      "directives": [{
        name: "show",
        value: this.showMinus
      }],
      "attrs": {
        "type": "button"
      },
      "style": this.buttonStyle,
      "class": bem$d('minus', {
        disabled: this.minusDisabled
      })
    }, createListeners('minus')])), h("input", {
      "directives": [{
        name: "show",
        value: this.showInput
      }],
      "ref": "input",
      "attrs": {
        "type": this.integer ? 'tel' : 'text',
        "role": "spinbutton",
        "disabled": this.disabled,
        "readonly": this.disableInput,
        "inputmode": this.integer ? 'numeric' : 'decimal',
        "placeholder": this.placeholder,
        "aria-valuemax": this.max,
        "aria-valuemin": this.min,
        "aria-valuenow": this.currentValue
      },
      "class": bem$d('input'),
      "domProps": {
        "value": this.currentValue
      },
      "style": this.inputStyle,
      "on": {
        "input": this.onInput,
        "focus": this.onFocus,
        "blur": this.onBlur,
        "mousedown": this.onMousedown
      }
    }), h("button", _mergeJSXProps([{
      "directives": [{
        name: "show",
        value: this.showPlus
      }],
      "attrs": {
        "type": "button"
      },
      "style": this.buttonStyle,
      "class": bem$d('plus', {
        disabled: this.plusDisabled
      })
    }, createListeners('plus')]))]);
  }
});

var namespace$3 = createNamespace('sku-stepper');
var createComponent$f = namespace$3[0];
var t$8 = namespace$3[2];
var QUOTA_LIMIT$1 = LIMIT_TYPE.QUOTA_LIMIT,
  STOCK_LIMIT = LIMIT_TYPE.STOCK_LIMIT;
var SkuStepper = createComponent$f({
  props: {
    stock: Number,
    skuEventBus: Object,
    skuStockNum: Number,
    selectedNum: Number,
    stepperTitle: String,
    disableStepperInput: Boolean,
    customStepperConfig: Object,
    hideQuotaText: Boolean,
    quota: {
      type: Number,
      default: 0
    },
    quotaUsed: {
      type: Number,
      default: 0
    },
    startSaleNum: {
      type: Number,
      default: 1
    }
  },
  data: function data() {
    return {
      currentNum: this.selectedNum,
      // 购买限制类型: 限购/库存
      limitType: STOCK_LIMIT
    };
  },
  watch: {
    currentNum: function currentNum(num) {
      var intValue = parseInt(num, 10);
      if (intValue >= this.stepperMinLimit && intValue <= this.stepperLimit) {
        this.skuEventBus.$emit('sku:numChange', intValue);
      }
    },
    stepperLimit: function stepperLimit(limit) {
      if (limit < this.currentNum && this.stepperMinLimit <= limit) {
        this.currentNum = limit;
      }
      this.checkState(this.stepperMinLimit, limit);
    },
    stepperMinLimit: function stepperMinLimit(start) {
      if (start > this.currentNum || start > this.stepperLimit) {
        this.currentNum = start;
      }
      this.checkState(start, this.stepperLimit);
    }
  },
  computed: {
    stepperLimit: function stepperLimit() {
      var quotaLimit = this.quota - this.quotaUsed;
      var limit; // 无限购时直接取库存，有限购时取限购数和库存数中小的那个

      if (this.quota > 0 && quotaLimit <= this.stock) {
        // 修正负的limit
        limit = quotaLimit < 0 ? 0 : quotaLimit;
        this.limitType = QUOTA_LIMIT$1;
      } else {
        limit = this.stock;
        this.limitType = STOCK_LIMIT;
      }
      return limit;
    },
    stepperMinLimit: function stepperMinLimit() {
      return this.startSaleNum < 1 ? 1 : this.startSaleNum;
    },
    quotaText: function quotaText() {
      var _this$customStepperCo = this.customStepperConfig,
        quotaText = _this$customStepperCo.quotaText,
        hideQuotaText = _this$customStepperCo.hideQuotaText;
      if (hideQuotaText) return '';
      var text = '';
      if (quotaText) {
        text = quotaText;
      } else {
        var textArr = [];
        if (this.startSaleNum > 1) {
          textArr.push(t$8('quotaStart', this.startSaleNum));
        }
        if (this.quota > 0) {
          textArr.push(t$8('quotaLimit', this.quota));
        }
        text = textArr.join(t$8('comma'));
      }
      return text;
    }
  },
  created: function created() {
    this.checkState(this.stepperMinLimit, this.stepperLimit);
  },
  methods: {
    setCurrentNum: function setCurrentNum(num) {
      this.currentNum = num;
      this.checkState(this.stepperMinLimit, this.stepperLimit);
    },
    onOverLimit: function onOverLimit(action) {
      this.skuEventBus.$emit('sku:overLimit', {
        action: action,
        limitType: this.limitType,
        quota: this.quota,
        quotaUsed: this.quotaUsed,
        startSaleNum: this.startSaleNum
      });
    },
    onChange: function onChange(currentValue) {
      var intValue = parseInt(currentValue, 10);
      var handleStepperChange = this.customStepperConfig.handleStepperChange;
      handleStepperChange && handleStepperChange(intValue);
      this.$emit('change', intValue);
    },
    checkState: function checkState(min, max) {
      // 如果选择小于起售，则强制变为起售
      if (this.currentNum < min || min > max) {
        this.currentNum = min;
      } else if (this.currentNum > max) {
        // 当前选择数量大于最大可选时，需要重置已选数量
        this.currentNum = max;
      }
      this.skuEventBus.$emit('sku:stepperState', {
        valid: min <= max,
        min: min,
        max: max,
        limitType: this.limitType,
        quota: this.quota,
        quotaUsed: this.quotaUsed,
        startSaleNum: this.startSaleNum
      });
    }
  },
  render: function render() {
    var _this = this;
    var h = arguments[0];
    return h("div", {
      "class": "van-sku-stepper-stock"
    }, [h("div", {
      "class": "van-sku__stepper-title"
    }, [this.stepperTitle || t$8('num')]), h(Stepper, {
      "attrs": {
        "integer": true,
        "min": this.stepperMinLimit,
        "max": this.stepperLimit,
        "disableInput": this.disableStepperInput
      },
      "class": "van-sku__stepper",
      "on": {
        "overlimit": this.onOverLimit,
        "change": this.onChange
      },
      "model": {
        value: _this.currentNum,
        callback: function callback($$v) {
          _this.currentNum = $$v;
        }
      }
    }), !this.hideQuotaText && this.quotaText && h("span", {
      "class": "van-sku__stepper-quota"
    }, ["(", this.quotaText, ")"])]);
  }
});

/* eslint-disable */
function isEmail(value) {
  var reg = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  return reg.test(value.trim());
}

function toArray$1(item) {
  if (Array.isArray(item)) {
    return item;
  }
  return [item];
}
function readFile(file, resultType) {
  return new Promise(function (resolve) {
    if (resultType === 'file') {
      resolve(null);
      return;
    }
    var reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target.result);
    };
    if (resultType === 'dataUrl') {
      reader.readAsDataURL(file);
    } else if (resultType === 'text') {
      reader.readAsText(file);
    }
  });
}
function isOversize(files, maxSize) {
  return toArray$1(files).some(function (file) {
    if (file) {
      if (isFunction$2(maxSize)) {
        return maxSize(file);
      }
      return file.size > maxSize;
    }
    return false;
  });
}
var IMAGE_REGEXP = /\.(jpeg|jpg|gif|png|svg|webp|jfif|bmp|dpg)/i;
function isImageUrl(url) {
  return IMAGE_REGEXP.test(url);
}
function isImageFile(item) {
  // some special urls cannot be recognized
  // user can add `isImage` flag to mark it as an image url
  if (item.isImage) {
    return true;
  }
  if (item.file && item.file.type) {
    return item.file.type.indexOf('image') === 0;
  }
  if (item.url) {
    return isImageUrl(item.url);
  }
  if (item.content) {
    return item.content.indexOf('data:image') === 0;
  }
  return false;
}

var _createNamespace$b = createNamespace('uploader'),
  createComponent$e = _createNamespace$b[0],
  bem$c = _createNamespace$b[1];
var Uploader = createComponent$e({
  inheritAttrs: false,
  mixins: [FieldMixin],
  model: {
    prop: 'fileList'
  },
  props: {
    disabled: Boolean,
    readonly: Boolean,
    lazyLoad: Boolean,
    uploadText: String,
    afterRead: Function,
    beforeRead: Function,
    beforeDelete: Function,
    previewSize: [Number, String],
    previewOptions: Object,
    name: {
      type: [Number, String],
      default: ''
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    fileList: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    maxSize: {
      type: [Number, String, Function],
      default: Number.MAX_VALUE
    },
    maxCount: {
      type: [Number, String],
      default: Number.MAX_VALUE
    },
    deletable: {
      type: Boolean,
      default: true
    },
    showUpload: {
      type: Boolean,
      default: true
    },
    previewImage: {
      type: Boolean,
      default: true
    },
    previewFullImage: {
      type: Boolean,
      default: true
    },
    imageFit: {
      type: String,
      default: 'cover'
    },
    resultType: {
      type: String,
      default: 'dataUrl'
    },
    uploadIcon: {
      type: String,
      default: 'photograph'
    }
  },
  computed: {
    previewSizeWithUnit: function previewSizeWithUnit() {
      return addUnit(this.previewSize);
    },
    // for form
    value: function value() {
      return this.fileList;
    }
  },
  created: function created() {
    this.urls = [];
  },
  beforeDestroy: function beforeDestroy() {
    this.urls.forEach(function (url) {
      return URL.revokeObjectURL(url);
    });
  },
  methods: {
    getDetail: function getDetail(index) {
      if (index === void 0) {
        index = this.fileList.length;
      }
      return {
        name: this.name,
        index: index
      };
    },
    onChange: function onChange(event) {
      var _this = this;
      var files = event.target.files;
      if (this.disabled || !files.length) {
        return;
      }
      files = files.length === 1 ? files[0] : [].slice.call(files);
      if (this.beforeRead) {
        var response = this.beforeRead(files, this.getDetail());
        if (!response) {
          this.resetInput();
          return;
        }
        if (isPromise$1(response)) {
          response.then(function (data) {
            if (data) {
              _this.readFile(data);
            } else {
              _this.readFile(files);
            }
          }).catch(this.resetInput);
          return;
        }
      }
      this.readFile(files);
    },
    readFile: function readFile$1(files) {
      var _this2 = this;
      var oversize = isOversize(files, this.maxSize);
      if (Array.isArray(files)) {
        var maxCount = this.maxCount - this.fileList.length;
        if (files.length > maxCount) {
          files = files.slice(0, maxCount);
        }
        Promise.all(files.map(function (file) {
          return readFile(file, _this2.resultType);
        })).then(function (contents) {
          var fileList = files.map(function (file, index) {
            var result = {
              file: file,
              status: '',
              message: ''
            };
            if (contents[index]) {
              result.content = contents[index];
            }
            return result;
          });
          _this2.onAfterRead(fileList, oversize);
        });
      } else {
        readFile(files, this.resultType).then(function (content) {
          var result = {
            file: files,
            status: '',
            message: ''
          };
          if (content) {
            result.content = content;
          }
          _this2.onAfterRead(result, oversize);
        });
      }
    },
    onAfterRead: function onAfterRead(files, oversize) {
      var _this3 = this;
      this.resetInput();
      var validFiles = files;
      if (oversize) {
        var oversizeFiles = files;
        if (Array.isArray(files)) {
          oversizeFiles = [];
          validFiles = [];
          files.forEach(function (item) {
            if (item.file) {
              if (isOversize(item.file, _this3.maxSize)) {
                oversizeFiles.push(item);
              } else {
                validFiles.push(item);
              }
            }
          });
        } else {
          validFiles = null;
        }
        this.$emit('oversize', oversizeFiles, this.getDetail());
      }
      var isValidFiles = Array.isArray(validFiles) ? Boolean(validFiles.length) : Boolean(validFiles);
      if (isValidFiles) {
        this.$emit('input', [].concat(this.fileList, toArray$1(validFiles)));
        if (this.afterRead) {
          this.afterRead(validFiles, this.getDetail());
        }
      }
    },
    onDelete: function onDelete(file, index) {
      var _file$beforeDelete,
        _this4 = this;
      var beforeDelete = (_file$beforeDelete = file.beforeDelete) != null ? _file$beforeDelete : this.beforeDelete;
      if (beforeDelete) {
        var response = beforeDelete(file, this.getDetail(index));
        if (!response) {
          return;
        }
        if (isPromise$1(response)) {
          response.then(function () {
            _this4.deleteFile(file, index);
          }).catch(noop$2);
          return;
        }
      }
      this.deleteFile(file, index);
    },
    deleteFile: function deleteFile(file, index) {
      var fileList = this.fileList.slice(0);
      fileList.splice(index, 1);
      this.$emit('input', fileList);
      this.$emit('delete', file, this.getDetail(index));
    },
    resetInput: function resetInput() {
      /* istanbul ignore else */
      if (this.$refs.input) {
        this.$refs.input.value = '';
      }
    },
    onClickUpload: function onClickUpload(event) {
      this.$emit('click-upload', event);
    },
    onPreviewImage: function onPreviewImage(item) {
      var _this5 = this;
      if (!this.previewFullImage) {
        return;
      }
      var imageFiles = this.fileList.filter(function (item) {
        return isImageFile(item);
      });
      var imageContents = imageFiles.map(function (item) {
        if (item.file && !item.url && item.status !== 'failed') {
          item.url = URL.createObjectURL(item.file);
          _this5.urls.push(item.url);
        }
        return item.url;
      });
      this.imagePreview = ImagePreview$1(_extends$1({
        images: imageContents,
        startPosition: imageFiles.indexOf(item),
        onClose: function onClose() {
          _this5.$emit('close-preview');
        }
      }, this.previewOptions));
    },
    // @exposed-api
    closeImagePreview: function closeImagePreview() {
      if (this.imagePreview) {
        this.imagePreview.close();
      }
    },
    // @exposed-api
    chooseFile: function chooseFile() {
      if (this.disabled) {
        return;
      }
      /* istanbul ignore else */

      if (this.$refs.input) {
        this.$refs.input.click();
      }
    },
    genPreviewMask: function genPreviewMask(item) {
      var h = this.$createElement;
      var status = item.status,
        message = item.message;
      if (status === 'uploading' || status === 'failed') {
        var MaskIcon = status === 'failed' ? h(Icon$3, {
          "attrs": {
            "name": "close"
          },
          "class": bem$c('mask-icon')
        }) : h(Loading$1, {
          "class": bem$c('loading')
        });
        var showMessage = isDef(message) && message !== '';
        return h("div", {
          "class": bem$c('mask')
        }, [MaskIcon, showMessage && h("div", {
          "class": bem$c('mask-message')
        }, [message])]);
      }
    },
    genPreviewItem: function genPreviewItem(item, index) {
      var _item$deletable,
        _this6 = this,
        _item$previewSize,
        _item$imageFit;
      var h = this.$createElement;
      var deleteAble = (_item$deletable = item.deletable) != null ? _item$deletable : this.deletable;
      var showDelete = item.status !== 'uploading' && deleteAble;
      var DeleteIcon = showDelete && h("div", {
        "class": bem$c('preview-delete'),
        "on": {
          "click": function click(event) {
            event.stopPropagation();
            _this6.onDelete(item, index);
          }
        }
      }, [h(Icon$3, {
        "attrs": {
          "name": "cross"
        },
        "class": bem$c('preview-delete-icon')
      })]);
      var PreviewCoverContent = this.slots('preview-cover', _extends$1({
        index: index
      }, item));
      var PreviewCover = PreviewCoverContent && h("div", {
        "class": bem$c('preview-cover')
      }, [PreviewCoverContent]);
      var previewSize = (_item$previewSize = item.previewSize) != null ? _item$previewSize : this.previewSize;
      var imageFit = (_item$imageFit = item.imageFit) != null ? _item$imageFit : this.imageFit;
      var Preview = isImageFile(item) ? h(Image$1, {
        "attrs": {
          "fit": imageFit,
          "src": item.content || item.url,
          "width": previewSize,
          "height": previewSize,
          "lazyLoad": this.lazyLoad
        },
        "class": bem$c('preview-image'),
        "on": {
          "click": function click() {
            _this6.onPreviewImage(item);
          }
        }
      }, [PreviewCover]) : h("div", {
        "class": bem$c('file'),
        "style": {
          width: this.previewSizeWithUnit,
          height: this.previewSizeWithUnit
        }
      }, [h(Icon$3, {
        "class": bem$c('file-icon'),
        "attrs": {
          "name": "description"
        }
      }), h("div", {
        "class": [bem$c('file-name'), 'van-ellipsis']
      }, [item.file ? item.file.name : item.url]), PreviewCover]);
      return h("div", {
        "class": bem$c('preview'),
        "on": {
          "click": function click() {
            _this6.$emit('click-preview', item, _this6.getDetail(index));
          }
        }
      }, [Preview, this.genPreviewMask(item), DeleteIcon]);
    },
    genPreviewList: function genPreviewList() {
      if (this.previewImage) {
        return this.fileList.map(this.genPreviewItem);
      }
    },
    genUpload: function genUpload() {
      var h = this.$createElement;
      if (this.fileList.length >= this.maxCount) {
        return;
      }
      var slot = this.slots();
      var Input = this.readonly ? null : h("input", {
        "attrs": _extends$1({}, this.$attrs, {
          "type": "file",
          "accept": this.accept,
          "disabled": this.disabled
        }),
        "ref": "input",
        "class": bem$c('input'),
        "on": {
          "change": this.onChange
        }
      });
      if (slot) {
        return h("div", {
          "class": bem$c('input-wrapper'),
          "key": "input-wrapper",
          "on": {
            "click": this.onClickUpload
          }
        }, [slot, Input]);
      }
      var style;
      if (this.previewSize) {
        var size = this.previewSizeWithUnit;
        style = {
          width: size,
          height: size
        };
      }
      return h("div", {
        "directives": [{
          name: "show",
          value: this.showUpload
        }],
        "class": bem$c('upload', {
          readonly: this.readonly
        }),
        "style": style,
        "on": {
          "click": this.onClickUpload
        }
      }, [h(Icon$3, {
        "attrs": {
          "name": this.uploadIcon
        },
        "class": bem$c('upload-icon')
      }), this.uploadText && h("span", {
        "class": bem$c('upload-text')
      }, [this.uploadText]), Input]);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$c()
    }, [h("div", {
      "class": bem$c('wrapper', {
        disabled: this.disabled
      })
    }, [this.genPreviewList(), this.genUpload()])]);
  }
});

// Utils
var namespace$2 = createNamespace('sku-img-uploader');
var createComponent$d = namespace$2[0];
var t$7 = namespace$2[2];
var SkuImgUploader = createComponent$d({
  props: {
    value: String,
    uploadImg: Function,
    customUpload: Function,
    maxSize: {
      type: Number,
      default: 6
    }
  },
  data: function data() {
    return {
      fileList: []
    };
  },
  watch: {
    value: function value(val) {
      if (val) {
        this.fileList = [{
          url: val,
          isImage: true
        }];
      } else {
        this.fileList = [];
      }
    }
  },
  methods: {
    afterReadFile: function afterReadFile(file) {
      var _this = this;
      file.status = 'uploading';
      file.message = t$7('uploading');
      this.uploadImg(file.file, file.content).then(function (img) {
        file.status = 'done';
        _this.$emit('input', img);
      }).catch(function () {
        file.status = 'failed';
        file.message = t$7('fail');
      });
    },
    onOversize: function onOversize() {
      this.$toast(t$7('oversize', this.maxSize));
    },
    onDelete: function onDelete() {
      this.$emit('input', '');
    },
    onClickUpload: function onClickUpload() {
      var _this2 = this;
      if (this.customUpload) {
        this.customUpload().then(function (url) {
          _this2.fileList.push({
            url: url
          });
          _this2.$emit('input', url);
        });
      }
    }
  },
  render: function render() {
    var _this3 = this;
    var h = arguments[0];
    return h(Uploader, {
      "attrs": {
        "maxCount": 1,
        "readonly": !!this.customUpload,
        "maxSize": this.maxSize * 1024 * 1024,
        "afterRead": this.afterReadFile
      },
      "on": {
        "oversize": this.onOversize,
        "delete": this.onDelete,
        "click-upload": this.onClickUpload
      },
      "model": {
        value: _this3.fileList,
        callback: function callback($$v) {
          _this3.fileList = $$v;
        }
      }
    });
  }
});

// 只处理 YYYY-MM-DD 或者 YYYY-MM-DD HH:MM 格式

function stringToDate(timeString) {
  if (!timeString) {
    return null;
  }
  return new Date(timeString.replace(/-/g, '/'));
} // Date 转字符串
// type: date or datetime

function dateToString(date, type) {
  if (type === void 0) {
    type = 'date';
  }
  if (!date) {
    return '';
  }
  var year = date.getFullYear();
  var month = date.getMonth() + 1;
  var day = date.getDate();
  var timeString = year + "-" + padZero(month) + "-" + padZero(day);
  if (type === 'datetime') {
    var hours = date.getHours();
    var minute = date.getMinutes();
    timeString += " " + padZero(hours) + ":" + padZero(minute);
  }
  return timeString;
}

// Utils
var namespace$1 = createNamespace('sku-datetime-field');
var createComponent$c = namespace$1[0];
var t$6 = namespace$1[2];
var SkuDateTimeField = createComponent$c({
  props: {
    value: String,
    label: String,
    required: Boolean,
    placeholder: String,
    type: {
      type: String,
      default: 'date'
    }
  },
  data: function data() {
    return {
      showDatePicker: false,
      currentDate: this.type === 'time' ? '' : new Date(),
      minDate: new Date(new Date().getFullYear() - 60, 0, 1)
    };
  },
  watch: {
    value: function value(val) {
      switch (this.type) {
        case 'time':
          this.currentDate = val;
          break;
        case 'date':
        case 'datetime':
          this.currentDate = stringToDate(val) || new Date();
          break;
      }
    }
  },
  computed: {
    title: function title() {
      return t$6("title." + this.type);
    }
  },
  methods: {
    onClick: function onClick() {
      this.showDatePicker = true;
    },
    onConfirm: function onConfirm(val) {
      var data = val;
      if (this.type !== 'time') {
        data = dateToString(val, this.type);
      }
      this.$emit('input', data);
      this.showDatePicker = false;
    },
    onCancel: function onCancel() {
      this.showDatePicker = false;
    },
    formatter: function formatter(type, val) {
      var word = t$6("format." + type);
      return "" + val + word;
    }
  },
  render: function render() {
    var _this = this;
    var h = arguments[0];
    return h(Field, {
      "attrs": {
        "readonly": true,
        "is-link": true,
        "center": true,
        "value": this.value,
        "label": this.label,
        "required": this.required,
        "placeholder": this.placeholder
      },
      "on": {
        "click": this.onClick
      }
    }, [h(Popup, {
      "attrs": {
        "round": true,
        "position": "bottom",
        "getContainer": "body"
      },
      "slot": "extra",
      "model": {
        value: _this.showDatePicker,
        callback: function callback($$v) {
          _this.showDatePicker = $$v;
        }
      }
    }, [h(DatetimePicker, {
      "attrs": {
        "type": this.type,
        "title": this.title,
        "value": this.currentDate,
        "minDate": this.minDate,
        "formatter": this.formatter
      },
      "on": {
        "cancel": this.onCancel,
        "confirm": this.onConfirm
      }
    })])]);
  }
});

// Utils
var _createNamespace$a = createNamespace('sku-messages'),
  createComponent$b = _createNamespace$a[0],
  bem$b = _createNamespace$a[1],
  t$5 = _createNamespace$a[2];
var SkuMessages = createComponent$b({
  props: {
    messageConfig: Object,
    goodsId: [Number, String],
    messages: {
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  data: function data() {
    return {
      messageValues: this.resetMessageValues(this.messages)
    };
  },
  watch: {
    messages: function messages(val) {
      this.messageValues = this.resetMessageValues(val);
    }
  },
  methods: {
    resetMessageValues: function resetMessageValues(messages) {
      var messageConfig = this.messageConfig;
      var _messageConfig$initia = messageConfig.initialMessages,
        initialMessages = _messageConfig$initia === void 0 ? {} : _messageConfig$initia;
      return (messages || []).map(function (message) {
        return {
          value: initialMessages[message.name] || ''
        };
      });
    },
    getType: function getType(message) {
      if (+message.multiple === 1) {
        return 'textarea';
      }
      if (message.type === 'id_no') {
        return 'text';
      }
      return message.datetime > 0 ? 'datetime' : message.type;
    },
    getMessages: function getMessages() {
      var messages = {};
      this.messageValues.forEach(function (item, index) {
        messages["message_" + index] = item.value;
      });
      return messages;
    },
    getCartMessages: function getCartMessages() {
      var _this = this;
      var messages = {};
      this.messageValues.forEach(function (item, index) {
        var message = _this.messages[index];
        messages[message.name] = item.value;
      });
      return messages;
    },
    getPlaceholder: function getPlaceholder(message) {
      var type = +message.multiple === 1 ? 'textarea' : message.type;
      var map = this.messageConfig.placeholderMap || {};
      return message.placeholder || map[type] || t$5("placeholder." + type);
    },
    validateMessages: function validateMessages() {
      var values = this.messageValues;
      for (var i = 0; i < values.length; i++) {
        var value = values[i].value;
        var message = this.messages[i];
        if (value === '') {
          // 必填字段的校验
          if (String(message.required) === '1') {
            var textType = t$5(message.type === 'image' ? 'upload' : 'fill');
            return textType + message.name;
          }
        } else {
          if (message.type === 'tel' && !isNumeric(value)) {
            return t$5('invalid.tel');
          }
          if (message.type === 'mobile' && !/^\d{6,20}$/.test(value)) {
            return t$5('invalid.mobile');
          }
          if (message.type === 'email' && !isEmail(value)) {
            return t$5('invalid.email');
          }
          if (message.type === 'id_no' && (value.length < 15 || value.length > 18)) {
            return t$5('invalid.id_no');
          }
        }
      }
    },
    /**
     * The phone number copied from IOS mobile phone address book
     * will add spaces and invisible Unicode characters
     * which cannot pass the /^\d+$/ verification
     * so keep numbers and dots
     */
    getFormatter: function getFormatter(message) {
      return function formatter(value) {
        if (message.type === 'mobile' || message.type === 'tel') {
          return value.replace(/[^\d.]/g, '');
        }
        return value;
      };
    },
    getExtraDesc: function getExtraDesc(message) {
      var h = this.$createElement;
      var extraDesc = message.extraDesc;
      if (extraDesc) {
        return h("div", {
          "class": bem$b('extra-message')
        }, [extraDesc]);
      }
    },
    genMessage: function genMessage(message, index) {
      var _this2 = this;
      var h = this.$createElement;
      if (message.type === 'image') {
        return h(Cell$1, {
          "key": this.goodsId + "-" + index,
          "attrs": {
            "title": message.name,
            "required": String(message.required) === '1',
            "valueClass": bem$b('image-cell-value')
          },
          "class": bem$b('image-cell')
        }, [h(SkuImgUploader, {
          "attrs": {
            "maxSize": this.messageConfig.uploadMaxSize,
            "uploadImg": this.messageConfig.uploadImg,
            "customUpload": this.messageConfig.customUpload
          },
          "model": {
            value: _this2.messageValues[index].value,
            callback: function callback($$v) {
              _this2.$set(_this2.messageValues[index], "value", $$v);
            }
          }
        }), h("div", {
          "class": bem$b('image-cell-label')
        }, [t$5('imageLabel')])]);
      } // 时间和日期使用的vant选择器

      var isDateOrTime = ['date', 'time'].indexOf(message.type) > -1;
      if (isDateOrTime) {
        return h(SkuDateTimeField, {
          "attrs": {
            "label": message.name,
            "required": String(message.required) === '1',
            "placeholder": this.getPlaceholder(message),
            "type": this.getType(message)
          },
          "key": this.goodsId + "-" + index,
          "model": {
            value: _this2.messageValues[index].value,
            callback: function callback($$v) {
              _this2.$set(_this2.messageValues[index], "value", $$v);
            }
          }
        });
      }
      return h("div", {
        "class": bem$b('cell-block')
      }, [h(Field, {
        "attrs": {
          "maxlength": "200",
          "center": !message.multiple,
          "label": message.name,
          "required": String(message.required) === '1',
          "placeholder": this.getPlaceholder(message),
          "type": this.getType(message),
          "formatter": this.getFormatter(message),
          "border": false
        },
        "key": this.goodsId + "-" + index,
        "model": {
          value: _this2.messageValues[index].value,
          callback: function callback($$v) {
            _this2.$set(_this2.messageValues[index], "value", $$v);
          }
        }
      }), this.getExtraDesc(message)]);
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$b()
    }, [this.messages.map(this.genMessage)]);
  }
});

var _createNamespace$9 = createNamespace('sku-actions'),
  createComponent$a = _createNamespace$9[0],
  bem$a = _createNamespace$9[1],
  t$4 = _createNamespace$9[2];
function SkuActions(h, props, slots, ctx) {
  var createEmitter = function createEmitter(name) {
    return function () {
      props.skuEventBus.$emit(name);
    };
  };
  return h("div", _mergeJSXProps([{
    "class": bem$a()
  }, inherit(ctx)]), [props.showAddCartBtn && h(Button$2, {
    "attrs": {
      "size": "large",
      "type": "warning",
      "text": props.addCartText || t$4('addCart')
    },
    "on": {
      "click": createEmitter('sku:addCart')
    }
  }), h(Button$2, {
    "attrs": {
      "size": "large",
      "type": "danger",
      "text": props.buyText || t$4('buy')
    },
    "on": {
      "click": createEmitter('sku:buy')
    }
  })]);
}
SkuActions.props = {
  buyText: String,
  addCartText: String,
  skuEventBus: Object,
  showAddCartBtn: Boolean
};
var SkuActions$1 = createComponent$a(SkuActions);

var namespace = createNamespace('sku');
var createComponent$9 = namespace[0],
  bem$9 = namespace[1],
  t$3 = namespace[2];
var QUOTA_LIMIT = LIMIT_TYPE.QUOTA_LIMIT;
var Sku = createComponent$9({
  props: {
    sku: Object,
    goods: Object,
    value: Boolean,
    buyText: String,
    goodsId: [Number, String],
    priceTag: String,
    lazyLoad: Boolean,
    hideStock: Boolean,
    properties: Array,
    addCartText: String,
    stepperTitle: String,
    getContainer: [String, Function],
    hideQuotaText: Boolean,
    hideSelectedText: Boolean,
    resetStepperOnHide: Boolean,
    customSkuValidator: Function,
    disableStepperInput: Boolean,
    resetSelectedSkuOnHide: Boolean,
    quota: {
      type: Number,
      default: 0
    },
    quotaUsed: {
      type: Number,
      default: 0
    },
    startSaleNum: {
      type: Number,
      default: 1
    },
    initialSku: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    stockThreshold: {
      type: Number,
      default: 50
    },
    showSoldoutSku: {
      type: Boolean,
      default: true
    },
    showAddCartBtn: {
      type: Boolean,
      default: true
    },
    disableSoldoutSku: {
      type: Boolean,
      default: true
    },
    customStepperConfig: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    showHeaderImage: {
      type: Boolean,
      default: true
    },
    previewOnClickImage: {
      type: Boolean,
      default: true
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    },
    bodyOffsetTop: {
      type: Number,
      default: 200
    },
    messageConfig: {
      type: Object,
      default: function _default() {
        return {
          initialMessages: {},
          placeholderMap: {},
          uploadImg: function uploadImg() {
            return Promise.resolve();
          },
          uploadMaxSize: 5
        };
      }
    }
  },
  data: function data() {
    return {
      selectedSku: {},
      selectedProp: {},
      selectedNum: 1,
      show: this.value
    };
  },
  watch: {
    show: function show(val) {
      this.$emit('input', val);
      if (!val) {
        this.$emit('sku-close', {
          selectedSkuValues: this.selectedSkuValues,
          selectedNum: this.selectedNum,
          selectedSkuComb: this.selectedSkuComb
        });
        if (this.resetStepperOnHide) {
          this.resetStepper();
        }
        if (this.resetSelectedSkuOnHide) {
          this.resetSelectedSku();
        }
      }
    },
    value: function value(val) {
      this.show = val;
    },
    skuTree: 'resetSelectedSku',
    initialSku: function initialSku() {
      this.resetStepper();
      this.resetSelectedSku();
    }
  },
  computed: {
    skuGroupClass: function skuGroupClass() {
      return ['van-sku-group-container', {
        'van-sku-group-container--hide-soldout': !this.showSoldoutSku
      }];
    },
    bodyStyle: function bodyStyle() {
      if (this.$isServer) {
        return;
      }
      var maxHeight = window.innerHeight - this.bodyOffsetTop;
      return {
        maxHeight: maxHeight + 'px'
      };
    },
    isSkuCombSelected: function isSkuCombSelected() {
      var _this = this;

      // SKU 未选完
      if (this.hasSku && !isAllSelected(this.skuTree, this.selectedSku)) {
        return false;
      } // 属性未全选

      return !this.propList.filter(function (i) {
        return i.is_necessary !== false;
      }).some(function (i) {
        return (_this.selectedProp[i.k_id] || []).length === 0;
      });
    },
    isSkuEmpty: function isSkuEmpty() {
      return Object.keys(this.sku).length === 0;
    },
    hasSku: function hasSku() {
      return !this.sku.none_sku;
    },
    hasSkuOrAttr: function hasSkuOrAttr() {
      return this.hasSku || this.propList.length > 0;
    },
    selectedSkuComb: function selectedSkuComb() {
      var skuComb = null;
      if (this.isSkuCombSelected) {
        if (this.hasSku) {
          skuComb = getSkuComb(this.skuList, this.selectedSku);
        } else {
          skuComb = {
            id: this.sku.collection_id,
            price: Math.round(this.sku.price * 100),
            stock_num: this.sku.stock_num
          };
        }
        if (skuComb) {
          skuComb.properties = getSelectedProperties(this.propList, this.selectedProp);
          skuComb.property_price = this.selectedPropValues.reduce(function (acc, cur) {
            return acc + (cur.price || 0);
          }, 0);
        }
      }
      return skuComb;
    },
    selectedSkuValues: function selectedSkuValues() {
      return getSelectedSkuValues(this.skuTree, this.selectedSku);
    },
    selectedPropValues: function selectedPropValues() {
      return getSelectedPropValues(this.propList, this.selectedProp);
    },
    price: function price() {
      if (this.selectedSkuComb) {
        return ((this.selectedSkuComb.price + this.selectedSkuComb.property_price) / 100).toFixed(2);
      } // sku.price是一个格式化好的价格区间

      return this.sku.price;
    },
    originPrice: function originPrice() {
      if (this.selectedSkuComb && this.selectedSkuComb.origin_price) {
        return ((this.selectedSkuComb.origin_price + this.selectedSkuComb.property_price) / 100).toFixed(2);
      }
      return this.sku.origin_price;
    },
    skuTree: function skuTree() {
      return this.sku.tree || [];
    },
    skuList: function skuList() {
      return this.sku.list || [];
    },
    propList: function propList() {
      return this.properties || [];
    },
    imageList: function imageList() {
      var imageList = [this.goods.picture];
      if (this.skuTree.length > 0) {
        this.skuTree.forEach(function (treeItem) {
          if (!treeItem.v) {
            return;
          }
          treeItem.v.forEach(function (vItem) {
            var imgUrl = vItem.previewImgUrl || vItem.imgUrl || vItem.img_url;
            if (imgUrl && imageList.indexOf(imgUrl) === -1) {
              imageList.push(imgUrl);
            }
          });
        });
      }
      return imageList;
    },
    stock: function stock() {
      var stockNum = this.customStepperConfig.stockNum;
      if (stockNum !== undefined) {
        return stockNum;
      }
      if (this.selectedSkuComb) {
        return this.selectedSkuComb.stock_num;
      }
      return this.sku.stock_num;
    },
    stockText: function stockText() {
      var h = this.$createElement;
      var stockFormatter = this.customStepperConfig.stockFormatter;
      if (stockFormatter) {
        return stockFormatter(this.stock);
      }
      return [t$3('stock') + " ", h("span", {
        "class": bem$9('stock-num', {
          highlight: this.stock < this.stockThreshold
        })
      }, [this.stock]), " " + t$3('stockUnit')];
    },
    selectedText: function selectedText() {
      var _this2 = this;
      if (this.selectedSkuComb) {
        var values = this.selectedSkuValues.concat(this.selectedPropValues);
        return t$3('selected') + " " + values.map(function (item) {
          return item.name;
        }).join(' ');
      }
      var unselectedSku = this.skuTree.filter(function (item) {
        return _this2.selectedSku[item.k_s] === UNSELECTED_SKU_VALUE_ID;
      }).map(function (item) {
        return item.k;
      });
      var unselectedProp = this.propList.filter(function (item) {
        return (_this2.selectedProp[item.k_id] || []).length < 1;
      }).map(function (item) {
        return item.k;
      });
      return t$3('select') + " " + unselectedSku.concat(unselectedProp).join(' ');
    }
  },
  created: function created() {
    var skuEventBus = new Vue();
    this.skuEventBus = skuEventBus;
    skuEventBus.$on('sku:select', this.onSelect);
    skuEventBus.$on('sku:propSelect', this.onPropSelect);
    skuEventBus.$on('sku:numChange', this.onNumChange);
    skuEventBus.$on('sku:previewImage', this.onPreviewImage);
    skuEventBus.$on('sku:overLimit', this.onOverLimit);
    skuEventBus.$on('sku:stepperState', this.onStepperState);
    skuEventBus.$on('sku:addCart', this.onAddCart);
    skuEventBus.$on('sku:buy', this.onBuy);
    this.resetStepper();
    this.resetSelectedSku(); // 组件初始化后的钩子，抛出skuEventBus

    this.$emit('after-sku-create', skuEventBus);
  },
  methods: {
    resetStepper: function resetStepper() {
      var skuStepper = this.$refs.skuStepper;
      var selectedNum = this.initialSku.selectedNum;
      var num = selectedNum != null ? selectedNum : this.startSaleNum; // 用来缓存不合法的情况

      this.stepperError = null;
      if (skuStepper) {
        skuStepper.setCurrentNum(num);
      } else {
        // 当首次加载（skuStepper 为空）时，传入数量如果不合法，可能会存在问题
        this.selectedNum = num;
      }
    },
    // @exposed-api
    resetSelectedSku: function resetSelectedSku() {
      var _this3 = this;
      this.selectedSku = {}; // 重置 selectedSku

      this.skuTree.forEach(function (item) {
        _this3.selectedSku[item.k_s] = UNSELECTED_SKU_VALUE_ID;
      });
      this.skuTree.forEach(function (item) {
        var key = item.k_s; // 规格值只有1个时，优先判断

        var valueId = item.v.length === 1 ? item.v[0].id : _this3.initialSku[key];
        if (valueId && isSkuChoosable(_this3.skuList, _this3.selectedSku, {
          key: key,
          valueId: valueId
        })) {
          _this3.selectedSku[key] = valueId;
        }
      });
      var skuValues = this.selectedSkuValues;
      if (skuValues.length > 0) {
        this.$nextTick(function () {
          _this3.$emit('sku-selected', {
            skuValue: skuValues[skuValues.length - 1],
            selectedSku: _this3.selectedSku,
            selectedSkuComb: _this3.selectedSkuComb
          });
        });
      } // 重置商品属性

      this.selectedProp = {};
      var _this$initialSku$sele = this.initialSku.selectedProp,
        selectedProp = _this$initialSku$sele === void 0 ? {} : _this$initialSku$sele; // 选中外部传入信息

      this.propList.forEach(function (item) {
        if (selectedProp[item.k_id]) {
          _this3.selectedProp[item.k_id] = selectedProp[item.k_id];
        }
      });
      if (isEmpty$2(this.selectedProp)) {
        this.propList.forEach(function (item) {
          var _item$v;

          // 没有加价的属性，默认选中第一个
          if ((item == null ? void 0 : (_item$v = item.v) == null ? void 0 : _item$v.length) > 0) {
            var v = item.v,
              k_id = item.k_id;
            var isHasConfigPrice = v.some(function (i) {
              return +i.price !== 0;
            }); // 没有加价属性

            if (!isHasConfigPrice) {
              // 找到第一个不被禁用的属性
              // 历史如果没有 text_status 字段的，就相当于沿用直接原来的逻辑取第一个属性
              var firstEnableProp = v.find(function (prop) {
                return prop.text_status !== 0;
              });
              if (firstEnableProp) {
                _this3.selectedProp[k_id] = [firstEnableProp.id];
              }
            }
          }
        });
      }
      var propValues = this.selectedPropValues;
      if (propValues.length > 0) {
        this.$emit('sku-prop-selected', {
          propValue: propValues[propValues.length - 1],
          selectedProp: this.selectedProp,
          selectedSkuComb: this.selectedSkuComb
        });
      } // 抛出重置事件

      this.$emit('sku-reset', {
        selectedSku: this.selectedSku,
        selectedProp: this.selectedProp,
        selectedSkuComb: this.selectedSkuComb
      });
      this.centerInitialSku();
    },
    getSkuMessages: function getSkuMessages() {
      return this.$refs.skuMessages ? this.$refs.skuMessages.getMessages() : {};
    },
    getSkuCartMessages: function getSkuCartMessages() {
      return this.$refs.skuMessages ? this.$refs.skuMessages.getCartMessages() : {};
    },
    validateSkuMessages: function validateSkuMessages() {
      return this.$refs.skuMessages ? this.$refs.skuMessages.validateMessages() : '';
    },
    validateSku: function validateSku() {
      if (this.selectedNum === 0) {
        return t$3('unavailable');
      }
      if (this.isSkuCombSelected) {
        return this.validateSkuMessages();
      } // 自定义sku校验

      if (this.customSkuValidator) {
        var err = this.customSkuValidator(this);
        if (err) return err;
      }
      return t$3('selectSku');
    },
    onSelect: function onSelect(skuValue) {
      var _extends2, _extends3;

      // 点击已选中的sku时则取消选中
      this.selectedSku = this.selectedSku[skuValue.skuKeyStr] === skuValue.id ? _extends$1({}, this.selectedSku, (_extends2 = {}, _extends2[skuValue.skuKeyStr] = UNSELECTED_SKU_VALUE_ID, _extends2)) : _extends$1({}, this.selectedSku, (_extends3 = {}, _extends3[skuValue.skuKeyStr] = skuValue.id, _extends3));
      this.$emit('sku-selected', {
        skuValue: skuValue,
        selectedSku: this.selectedSku,
        selectedSkuComb: this.selectedSkuComb
      });
    },
    onPropSelect: function onPropSelect(propValue) {
      var _extends4;
      var arr = this.selectedProp[propValue.skuKeyStr] || [];
      var pos = arr.indexOf(propValue.id);
      if (pos > -1) {
        arr.splice(pos, 1);
      } else if (propValue.multiple) {
        arr.push(propValue.id);
      } else {
        arr.splice(0, 1, propValue.id);
      }
      this.selectedProp = _extends$1({}, this.selectedProp, (_extends4 = {}, _extends4[propValue.skuKeyStr] = arr, _extends4));
      this.$emit('sku-prop-selected', {
        propValue: propValue,
        selectedProp: this.selectedProp,
        selectedSkuComb: this.selectedSkuComb
      });
    },
    onNumChange: function onNumChange(num) {
      this.selectedNum = num;
    },
    onPreviewImage: function onPreviewImage(selectedValue) {
      var _this4 = this;
      var imageList = this.imageList;
      var index = 0;
      var indexImage = imageList[0];
      if (selectedValue && selectedValue.imgUrl) {
        this.imageList.some(function (image, pos) {
          if (image === selectedValue.imgUrl) {
            index = pos;
            return true;
          }
          return false;
        });
        indexImage = selectedValue.imgUrl;
      }
      var params = _extends$1({}, selectedValue, {
        index: index,
        imageList: this.imageList,
        indexImage: indexImage
      });
      this.$emit('open-preview', params);
      if (!this.previewOnClickImage) {
        return;
      }
      ImagePreview$1({
        images: this.imageList,
        startPosition: index,
        onClose: function onClose() {
          _this4.$emit('close-preview', params);
        }
      });
    },
    onOverLimit: function onOverLimit(data) {
      var action = data.action,
        limitType = data.limitType,
        quota = data.quota,
        quotaUsed = data.quotaUsed;
      var handleOverLimit = this.customStepperConfig.handleOverLimit;
      if (handleOverLimit) {
        handleOverLimit(data);
        return;
      }
      if (action === 'minus') {
        if (this.startSaleNum > 1) {
          Toast(t$3('minusStartTip', this.startSaleNum));
        } else {
          Toast(t$3('minusTip'));
        }
      } else if (action === 'plus') {
        if (limitType === QUOTA_LIMIT) {
          if (quotaUsed > 0) {
            Toast(t$3('quotaUsedTip', quota, quotaUsed));
          } else {
            Toast(t$3('quotaTip', quota));
          }
        } else {
          Toast(t$3('soldout'));
        }
      }
    },
    onStepperState: function onStepperState(data) {
      this.stepperError = data.valid ? null : _extends$1({}, data, {
        action: 'plus'
      });
    },
    onAddCart: function onAddCart() {
      this.onBuyOrAddCart('add-cart');
    },
    onBuy: function onBuy() {
      this.onBuyOrAddCart('buy-clicked');
    },
    onBuyOrAddCart: function onBuyOrAddCart(type) {
      // sku 不符合购买条件
      if (this.stepperError) {
        return this.onOverLimit(this.stepperError);
      }
      var error = this.validateSku();
      if (error) {
        Toast(error);
      } else {
        this.$emit(type, this.getSkuData());
      }
    },
    // @exposed-api
    getSkuData: function getSkuData() {
      return {
        goodsId: this.goodsId,
        messages: this.getSkuMessages(),
        selectedNum: this.selectedNum,
        cartMessages: this.getSkuCartMessages(),
        selectedSkuComb: this.selectedSkuComb
      };
    },
    // 当 popup 完全打开后执行
    onOpened: function onOpened() {
      this.centerInitialSku();
    },
    centerInitialSku: function centerInitialSku() {
      var _this5 = this;
      (this.$refs.skuRows || []).forEach(function (it) {
        var _ref = it.skuRow || {},
          k_s = _ref.k_s;
        it.centerItem(_this5.initialSku[k_s]);
      });
    }
  },
  render: function render() {
    var _this6 = this;
    var h = arguments[0];
    if (this.isSkuEmpty) {
      return;
    }
    var sku = this.sku,
      skuList = this.skuList,
      goods = this.goods,
      price = this.price,
      lazyLoad = this.lazyLoad,
      originPrice = this.originPrice,
      skuEventBus = this.skuEventBus,
      selectedSku = this.selectedSku,
      selectedProp = this.selectedProp,
      selectedNum = this.selectedNum,
      stepperTitle = this.stepperTitle,
      selectedSkuComb = this.selectedSkuComb,
      showHeaderImage = this.showHeaderImage,
      disableSoldoutSku = this.disableSoldoutSku;
    var slotsProps = {
      price: price,
      originPrice: originPrice,
      selectedNum: selectedNum,
      skuEventBus: skuEventBus,
      selectedSku: selectedSku,
      selectedSkuComb: selectedSkuComb
    };
    var slots = function slots(name) {
      return _this6.slots(name, slotsProps);
    };
    var Header = slots('sku-header') || h(SkuHeader$2, {
      "attrs": {
        "sku": sku,
        "goods": goods,
        "skuEventBus": skuEventBus,
        "selectedSku": selectedSku,
        "showHeaderImage": showHeaderImage
      }
    }, [h("template", {
      "slot": "sku-header-image-extra"
    }, [slots('sku-header-image-extra')]), slots('sku-header-price') || h("div", {
      "class": "van-sku__goods-price"
    }, [h("span", {
      "class": "van-sku__price-symbol"
    }, ["\uFFE5"]), h("span", {
      "class": "van-sku__price-num"
    }, [price]), this.priceTag && h("span", {
      "class": "van-sku__price-tag"
    }, [this.priceTag])]), slots('sku-header-origin-price') || originPrice && h(SkuHeaderItem, [t$3('originPrice'), " \uFFE5", originPrice]), !this.hideStock && h(SkuHeaderItem, [h("span", {
      "class": "van-sku__stock"
    }, [this.stockText])]), this.hasSkuOrAttr && !this.hideSelectedText && h(SkuHeaderItem, [this.selectedText]), slots('sku-header-extra')]);
    var Group = slots('sku-group') || this.hasSkuOrAttr && h("div", {
      "class": this.skuGroupClass
    }, [this.skuTree.map(function (skuTreeItem) {
      return h(SkuRow, {
        "attrs": {
          "skuRow": skuTreeItem
        },
        "ref": "skuRows",
        "refInFor": true
      }, [skuTreeItem.v.map(function (skuValue) {
        return h(SkuRowItem, {
          "attrs": {
            "skuList": skuList,
            "lazyLoad": lazyLoad,
            "skuValue": skuValue,
            "skuKeyStr": skuTreeItem.k_s,
            "selectedSku": selectedSku,
            "skuEventBus": skuEventBus,
            "disableSoldoutSku": disableSoldoutSku,
            "largeImageMode": skuTreeItem.largeImageMode
          }
        });
      })]);
    }), this.propList.map(function (skuTreeItem) {
      return h(SkuRow, {
        "attrs": {
          "skuRow": skuTreeItem
        }
      }, [skuTreeItem.v.map(function (skuValue) {
        return h(SkuRowPropItem, {
          "attrs": {
            "skuValue": skuValue,
            "skuKeyStr": skuTreeItem.k_id + '',
            "selectedProp": selectedProp,
            "skuEventBus": skuEventBus,
            "multiple": skuTreeItem.is_multiple,
            "disabled": skuValue.text_status === 0
          }
        });
      })]);
    })]);
    var Stepper = slots('sku-stepper') || h(SkuStepper, {
      "ref": "skuStepper",
      "attrs": {
        "stock": this.stock,
        "quota": this.quota,
        "quotaUsed": this.quotaUsed,
        "startSaleNum": this.startSaleNum,
        "skuEventBus": skuEventBus,
        "selectedNum": selectedNum,
        "stepperTitle": stepperTitle,
        "skuStockNum": sku.stock_num,
        "disableStepperInput": this.disableStepperInput,
        "customStepperConfig": this.customStepperConfig,
        "hideQuotaText": this.hideQuotaText
      },
      "on": {
        "change": function change(event) {
          _this6.$emit('stepper-change', event);
        }
      }
    });
    var Messages = slots('sku-messages') || h(SkuMessages, {
      "ref": "skuMessages",
      "attrs": {
        "goodsId": this.goodsId,
        "messageConfig": this.messageConfig,
        "messages": sku.messages
      }
    });
    var Actions = slots('sku-actions') || h(SkuActions$1, {
      "attrs": {
        "buyText": this.buyText,
        "skuEventBus": skuEventBus,
        "addCartText": this.addCartText,
        "showAddCartBtn": this.showAddCartBtn
      }
    });
    return h(Popup, {
      "attrs": {
        "round": true,
        "closeable": true,
        "position": "bottom",
        "getContainer": this.getContainer,
        "closeOnClickOverlay": this.closeOnClickOverlay,
        "safeAreaInsetBottom": this.safeAreaInsetBottom
      },
      "class": "van-sku-container",
      "on": {
        "opened": this.onOpened
      },
      "model": {
        value: _this6.show,
        callback: function callback($$v) {
          _this6.show = $$v;
        }
      }
    }, [Header, h("div", {
      "class": "van-sku-body",
      "style": this.bodyStyle
    }, [slots('sku-body-top'), Group, slots('extra-sku-group'), Stepper, slots('before-sku-messages'), Messages, slots('after-sku-messages')]), slots('sku-actions-top'), Actions]);
  }
});

// Utils
Locale$1.add(lang$1);
Sku.SkuActions = SkuActions$1;
Sku.SkuHeader = SkuHeader$2;
Sku.SkuHeaderItem = SkuHeaderItem;
Sku.SkuMessages = SkuMessages;
Sku.SkuStepper = SkuStepper;
Sku.SkuRow = SkuRow;
Sku.SkuRowItem = SkuRowItem;
Sku.SkuRowPropItem = SkuRowPropItem;
Sku.skuHelper = skuHelper;
Sku.skuConstants = constants;

var _createNamespace$8 = createNamespace('slider'),
  createComponent$8 = _createNamespace$8[0],
  bem$8 = _createNamespace$8[1];
var isSameValue = function isSameValue(newValue, oldValue) {
  return JSON.stringify(newValue) === JSON.stringify(oldValue);
};
var Slider$1 = createComponent$8({
  mixins: [TouchMixin, FieldMixin],
  props: {
    disabled: Boolean,
    vertical: Boolean,
    range: Boolean,
    barHeight: [Number, String],
    buttonSize: [Number, String],
    activeColor: String,
    inactiveColor: String,
    min: {
      type: [Number, String],
      default: 0
    },
    max: {
      type: [Number, String],
      default: 100
    },
    step: {
      type: [Number, String],
      default: 1
    },
    value: {
      type: [Number, Array],
      default: 0
    }
  },
  data: function data() {
    return {
      dragStatus: ''
    };
  },
  computed: {
    scope: function scope() {
      return this.max - this.min;
    },
    buttonStyle: function buttonStyle() {
      if (this.buttonSize) {
        var size = addUnit(this.buttonSize);
        return {
          width: size,
          height: size
        };
      }
    }
  },
  created: function created() {
    // format initial value
    this.updateValue(this.value);
  },
  mounted: function mounted() {
    if (this.range) {
      this.bindTouchEvent(this.$refs.wrapper0);
      this.bindTouchEvent(this.$refs.wrapper1);
    } else {
      this.bindTouchEvent(this.$refs.wrapper);
    }
  },
  methods: {
    onTouchStart: function onTouchStart(event) {
      if (this.disabled) {
        return;
      }
      this.touchStart(event);
      this.currentValue = this.value;
      if (this.range) {
        this.startValue = this.value.map(this.format);
      } else {
        this.startValue = this.format(this.value);
      }
      this.dragStatus = 'start';
    },
    onTouchMove: function onTouchMove(event) {
      if (this.disabled) {
        return;
      }
      if (this.dragStatus === 'start') {
        this.$emit('drag-start');
      }
      preventDefault(event, true);
      this.touchMove(event);
      this.dragStatus = 'draging';
      var rect = this.$el.getBoundingClientRect();
      var delta = this.vertical ? this.deltaY : this.deltaX;
      var total = this.vertical ? rect.height : rect.width;
      var diff = delta / total * this.scope;
      if (this.range) {
        this.currentValue[this.index] = this.startValue[this.index] + diff;
      } else {
        this.currentValue = this.startValue + diff;
      }
      this.updateValue(this.currentValue);
    },
    onTouchEnd: function onTouchEnd() {
      if (this.disabled) {
        return;
      }
      if (this.dragStatus === 'draging') {
        this.updateValue(this.currentValue, true);
        this.$emit('drag-end');
      }
      this.dragStatus = '';
    },
    onClick: function onClick(event) {
      event.stopPropagation();
      if (this.disabled) return;
      var rect = this.$el.getBoundingClientRect();
      var delta = this.vertical ? event.clientY - rect.top : event.clientX - rect.left;
      var total = this.vertical ? rect.height : rect.width;
      var value = +this.min + delta / total * this.scope;
      if (this.range) {
        var _this$value = this.value,
          left = _this$value[0],
          right = _this$value[1];
        var middle = (left + right) / 2;
        if (value <= middle) {
          left = value;
        } else {
          right = value;
        }
        value = [left, right];
      }
      this.startValue = this.value;
      this.updateValue(value, true);
    },
    // 处理两个滑块重叠之后的情况
    handleOverlap: function handleOverlap(value) {
      if (value[0] > value[1]) {
        value = deepClone(value);
        return value.reverse();
      }
      return value;
    },
    updateValue: function updateValue(value, end) {
      if (this.range) {
        value = this.handleOverlap(value).map(this.format);
      } else {
        value = this.format(value);
      }
      if (!isSameValue(value, this.value)) {
        this.$emit('input', value);
      }
      if (end && !isSameValue(value, this.startValue)) {
        this.$emit('change', value);
      }
    },
    format: function format(value) {
      var min = +this.min;
      var max = +this.max;
      var step = +this.step;
      value = range$1(value, min, max);
      var diff = Math.round((value - min) / step) * step;
      return addNumber(min, diff);
    }
  },
  render: function render() {
    var _wrapperStyle,
      _this = this,
      _barStyle;
    var h = arguments[0];
    var vertical = this.vertical;
    var mainAxis = vertical ? 'height' : 'width';
    var crossAxis = vertical ? 'width' : 'height';
    var wrapperStyle = (_wrapperStyle = {
      background: this.inactiveColor
    }, _wrapperStyle[crossAxis] = addUnit(this.barHeight), _wrapperStyle); // 计算选中条的长度百分比

    var calcMainAxis = function calcMainAxis() {
      var value = _this.value,
        min = _this.min,
        range = _this.range,
        scope = _this.scope;
      if (range) {
        return (value[1] - value[0]) * 100 / scope + "%";
      }
      return (value - min) * 100 / scope + "%";
    }; // 计算选中条的开始位置的偏移量

    var calcOffset = function calcOffset() {
      var value = _this.value,
        min = _this.min,
        range = _this.range,
        scope = _this.scope;
      if (range) {
        return (value[0] - min) * 100 / scope + "%";
      }
      return null;
    };
    var barStyle = (_barStyle = {}, _barStyle[mainAxis] = calcMainAxis(), _barStyle.left = this.vertical ? null : calcOffset(), _barStyle.top = this.vertical ? calcOffset() : null, _barStyle.background = this.activeColor, _barStyle);
    if (this.dragStatus) {
      barStyle.transition = 'none';
    }
    var renderButton = function renderButton(i) {
      var map = ['left', 'right'];
      var isNumber = typeof i === 'number';
      var current = isNumber ? _this.value[i] : _this.value;
      var getClassName = function getClassName() {
        if (isNumber) {
          return "button-wrapper-" + map[i];
        }
        return "button-wrapper";
      };
      var getRefName = function getRefName() {
        if (isNumber) {
          return "wrapper" + i;
        }
        return "wrapper";
      };
      var renderButtonContent = function renderButtonContent() {
        if (isNumber) {
          var slot = _this.slots(i === 0 ? 'left-button' : 'right-button', {
            value: current
          });
          if (slot) {
            return slot;
          }
        }
        if (_this.slots('button')) {
          return _this.slots('button');
        }
        return h("div", {
          "class": bem$8('button'),
          "style": _this.buttonStyle
        });
      };
      return h("div", {
        "ref": getRefName(),
        "attrs": {
          "role": "slider",
          "tabindex": _this.disabled ? -1 : 0,
          "aria-valuemin": _this.min,
          "aria-valuenow": _this.value,
          "aria-valuemax": _this.max,
          "aria-orientation": _this.vertical ? 'vertical' : 'horizontal'
        },
        "class": bem$8(getClassName()),
        "on": {
          "touchstart": function touchstart() {
            if (isNumber) {
              // 保存当前按钮的索引
              _this.index = i;
            }
          },
          "click": function click(e) {
            return e.stopPropagation();
          }
        }
      }, [renderButtonContent()]);
    };
    return h("div", {
      "style": wrapperStyle,
      "class": bem$8({
        disabled: this.disabled,
        vertical: vertical
      }),
      "on": {
        "click": this.onClick
      }
    }, [h("div", {
      "class": bem$8('bar'),
      "style": barStyle
    }, [this.range ? [renderButton(0), renderButton(1)] : renderButton()])]);
  }
});

var _createNamespace$7 = createNamespace('step'),
  createComponent$7 = _createNamespace$7[0],
  bem$7 = _createNamespace$7[1];
var Step = createComponent$7({
  mixins: [ChildrenMixin('vanSteps')],
  computed: {
    status: function status() {
      if (this.index < this.parent.active) {
        return 'finish';
      }
      if (this.index === +this.parent.active) {
        return 'process';
      }
    },
    active: function active() {
      return this.status === 'process';
    },
    lineStyle: function lineStyle() {
      var _this$parent = this.parent,
        activeColor = _this$parent.activeColor,
        inactiveColor = _this$parent.inactiveColor,
        center = _this$parent.center,
        direction = _this$parent.direction;
      var style = {
        background: this.status === 'finish' ? activeColor : inactiveColor
      };
      if (center && direction === 'vertical') {
        style.top = '50%';
      }
      return style;
    },
    circleContainerStyle: function circleContainerStyle() {
      if (this.parent.center && this.parent.direction === 'vertical') {
        return {
          top: '50%'
        };
      }
    },
    titleStyle: function titleStyle() {
      if (this.active) {
        return {
          color: this.parent.activeColor
        };
      }
      if (!this.status) {
        return {
          color: this.parent.inactiveColor
        };
      }
    }
  },
  methods: {
    genCircle: function genCircle() {
      var h = this.$createElement;
      var _this$parent2 = this.parent,
        activeIcon = _this$parent2.activeIcon,
        iconPrefix = _this$parent2.iconPrefix,
        activeColor = _this$parent2.activeColor,
        finishIcon = _this$parent2.finishIcon,
        inactiveIcon = _this$parent2.inactiveIcon;
      if (this.active) {
        return this.slots('active-icon') || h(Icon$3, {
          "class": bem$7('icon', 'active'),
          "attrs": {
            "name": activeIcon,
            "color": activeColor,
            "classPrefix": iconPrefix
          }
        });
      }
      var finishIconSlot = this.slots('finish-icon');
      if (this.status === 'finish' && (finishIcon || finishIconSlot)) {
        return finishIconSlot || h(Icon$3, {
          "class": bem$7('icon', 'finish'),
          "attrs": {
            "name": finishIcon,
            "color": activeColor,
            "classPrefix": iconPrefix
          }
        });
      }
      var inactiveIconSlot = this.slots('inactive-icon');
      if (inactiveIcon || inactiveIconSlot) {
        return inactiveIconSlot || h(Icon$3, {
          "class": bem$7('icon'),
          "attrs": {
            "name": inactiveIcon,
            "classPrefix": iconPrefix
          }
        });
      }
      return h("i", {
        "class": bem$7('circle'),
        "style": this.lineStyle
      });
    },
    onClickStep: function onClickStep() {
      this.parent.$emit('click-step', this.index);
    }
  },
  render: function render() {
    var _ref;
    var h = arguments[0];
    var status = this.status,
      active = this.active;
    var direction = this.parent.direction;
    return h("div", {
      "class": [BORDER, bem$7([direction, (_ref = {}, _ref[status] = status, _ref)])]
    }, [h("div", {
      "class": bem$7('title', {
        active: active
      }),
      "style": this.titleStyle,
      "on": {
        "click": this.onClickStep
      }
    }, [this.slots()]), h("div", {
      "class": bem$7('circle-container'),
      "on": {
        "click": this.onClickStep
      },
      "style": this.circleContainerStyle
    }, [this.genCircle()]), h("div", {
      "class": bem$7('line'),
      "style": this.lineStyle
    })]);
  }
});

var _createNamespace$6 = createNamespace('steps'),
  createComponent$6 = _createNamespace$6[0],
  bem$6 = _createNamespace$6[1];
var Steps = createComponent$6({
  mixins: [ParentMixin('vanSteps')],
  props: {
    center: Boolean,
    iconPrefix: String,
    finishIcon: String,
    activeColor: String,
    inactiveIcon: String,
    inactiveColor: String,
    active: {
      type: [Number, String],
      default: 0
    },
    direction: {
      type: String,
      default: 'horizontal'
    },
    activeIcon: {
      type: String,
      default: 'checked'
    }
  },
  render: function render() {
    var h = arguments[0];
    return h("div", {
      "class": bem$6([this.direction])
    }, [h("div", {
      "class": bem$6('items')
    }, [this.slots()])]);
  }
});

var _createNamespace$5 = createNamespace('submit-bar'),
  createComponent$5 = _createNamespace$5[0],
  bem$5 = _createNamespace$5[1],
  t$2 = _createNamespace$5[2];
function SubmitBar(h, props, slots, ctx) {
  var tip = props.tip,
    price = props.price,
    tipIcon = props.tipIcon;
  function Text() {
    if (typeof price === 'number') {
      var priceArr = (price / 100).toFixed(props.decimalLength).split('.');
      var decimalStr = props.decimalLength ? "." + priceArr[1] : '';
      return h("div", {
        "style": {
          textAlign: props.textAlign ? props.textAlign : ''
        },
        "class": bem$5('text')
      }, [h("span", [props.label || t$2('label')]), h("span", {
        "class": bem$5('price')
      }, [props.currency, h("span", {
        "class": bem$5('price', 'integer')
      }, [priceArr[0]]), decimalStr]), props.suffixLabel && h("span", {
        "class": bem$5('suffix-label')
      }, [props.suffixLabel])]);
    }
  }
  function Tip() {
    if (slots.tip || tip) {
      return h("div", {
        "class": bem$5('tip')
      }, [tipIcon && h(Icon$3, {
        "class": bem$5('tip-icon'),
        "attrs": {
          "name": tipIcon
        }
      }), tip && h("span", {
        "class": bem$5('tip-text')
      }, [tip]), slots.tip && slots.tip()]);
    }
  }
  return h("div", _mergeJSXProps([{
    "class": bem$5({
      unfit: !props.safeAreaInsetBottom
    })
  }, inherit(ctx)]), [slots.top && slots.top(), Tip(), h("div", {
    "class": bem$5('bar')
  }, [slots.default && slots.default(), Text(), slots.button ? slots.button() : h(Button$2, {
    "attrs": {
      "round": true,
      "type": props.buttonType,
      "text": props.loading ? '' : props.buttonText,
      "color": props.buttonColor,
      "loading": props.loading,
      "disabled": props.disabled
    },
    "class": bem$5('button', props.buttonType),
    "on": {
      "click": function click() {
        emit(ctx, 'submit');
      }
    }
  })])]);
}
SubmitBar.props = {
  tip: String,
  label: String,
  price: Number,
  tipIcon: String,
  loading: Boolean,
  disabled: Boolean,
  textAlign: String,
  buttonText: String,
  buttonColor: String,
  suffixLabel: String,
  safeAreaInsetBottom: {
    type: Boolean,
    default: true
  },
  decimalLength: {
    type: [Number, String],
    default: 2
  },
  currency: {
    type: String,
    default: '¥'
  },
  buttonType: {
    type: String,
    default: 'danger'
  }
};
var SubmitBar$1 = createComponent$5(SubmitBar);

// Utils
var _createNamespace$4 = createNamespace('swipe-cell'),
  createComponent$4 = _createNamespace$4[0],
  bem$4 = _createNamespace$4[1];
var THRESHOLD = 0.15;
var SwipeCell = createComponent$4({
  mixins: [TouchMixin, ClickOutsideMixin({
    event: 'touchstart',
    method: 'onClick'
  })],
  props: {
    // @deprecated
    // should be removed in next major version, use beforeClose instead
    onClose: Function,
    disabled: Boolean,
    leftWidth: [Number, String],
    rightWidth: [Number, String],
    beforeClose: Function,
    stopPropagation: Boolean,
    name: {
      type: [Number, String],
      default: ''
    }
  },
  data: function data() {
    return {
      offset: 0,
      dragging: false
    };
  },
  computed: {
    computedLeftWidth: function computedLeftWidth() {
      return +this.leftWidth || this.getWidthByRef('left');
    },
    computedRightWidth: function computedRightWidth() {
      return +this.rightWidth || this.getWidthByRef('right');
    }
  },
  mounted: function mounted() {
    this.bindTouchEvent(this.$el);
  },
  methods: {
    getWidthByRef: function getWidthByRef(ref) {
      if (this.$refs[ref]) {
        var rect = this.$refs[ref].getBoundingClientRect();
        return rect.width;
      }
      return 0;
    },
    // @exposed-api
    open: function open(position) {
      var offset = position === 'left' ? this.computedLeftWidth : -this.computedRightWidth;
      this.opened = true;
      this.offset = offset;
      this.$emit('open', {
        position: position,
        name: this.name,
        // @deprecated
        // should be removed in next major version
        detail: this.name
      });
    },
    // @exposed-api
    close: function close(position) {
      this.offset = 0;
      if (this.opened) {
        this.opened = false;
        this.$emit('close', {
          position: position,
          name: this.name
        });
      }
    },
    onTouchStart: function onTouchStart(event) {
      if (this.disabled) {
        return;
      }
      this.startOffset = this.offset;
      this.touchStart(event);
    },
    onTouchMove: function onTouchMove(event) {
      if (this.disabled) {
        return;
      }
      this.touchMove(event);
      if (this.direction === 'horizontal') {
        this.dragging = true;
        this.lockClick = true;
        var isPrevent = !this.opened || this.deltaX * this.startOffset < 0;
        if (isPrevent) {
          preventDefault(event, this.stopPropagation);
        }
        this.offset = range$1(this.deltaX + this.startOffset, -this.computedRightWidth, this.computedLeftWidth);
      }
    },
    onTouchEnd: function onTouchEnd() {
      var _this = this;
      if (this.disabled) {
        return;
      }
      if (this.dragging) {
        this.toggle(this.offset > 0 ? 'left' : 'right');
        this.dragging = false; // compatible with desktop scenario

        setTimeout(function () {
          _this.lockClick = false;
        }, 0);
      }
    },
    toggle: function toggle(direction) {
      var offset = Math.abs(this.offset);
      var threshold = this.opened ? 1 - THRESHOLD : THRESHOLD;
      var computedLeftWidth = this.computedLeftWidth,
        computedRightWidth = this.computedRightWidth;
      if (computedRightWidth && direction === 'right' && offset > computedRightWidth * threshold) {
        this.open('right');
      } else if (computedLeftWidth && direction === 'left' && offset > computedLeftWidth * threshold) {
        this.open('left');
      } else {
        this.close();
      }
    },
    onClick: function onClick(position) {
      if (position === void 0) {
        position = 'outside';
      }
      this.$emit('click', position);
      if (this.opened && !this.lockClick) {
        if (this.beforeClose) {
          this.beforeClose({
            position: position,
            name: this.name,
            instance: this
          });
        } else if (this.onClose) {
          this.onClose(position, this, {
            name: this.name
          });
        } else {
          this.close(position);
        }
      }
    },
    getClickHandler: function getClickHandler(position, stop) {
      var _this2 = this;
      return function (event) {
        if (stop) {
          event.stopPropagation();
        }
        _this2.onClick(position);
      };
    },
    genLeftPart: function genLeftPart() {
      var h = this.$createElement;
      var content = this.slots('left');
      if (content) {
        return h("div", {
          "ref": "left",
          "class": bem$4('left'),
          "on": {
            "click": this.getClickHandler('left', true)
          }
        }, [content]);
      }
    },
    genRightPart: function genRightPart() {
      var h = this.$createElement;
      var content = this.slots('right');
      if (content) {
        return h("div", {
          "ref": "right",
          "class": bem$4('right'),
          "on": {
            "click": this.getClickHandler('right', true)
          }
        }, [content]);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var wrapperStyle = {
      transform: "translate3d(" + this.offset + "px, 0, 0)",
      transitionDuration: this.dragging ? '0s' : '.6s'
    };
    return h("div", {
      "class": bem$4(),
      "on": {
        "click": this.getClickHandler('cell')
      }
    }, [h("div", {
      "class": bem$4('wrapper'),
      "style": wrapperStyle
    }, [this.genLeftPart(), this.slots(), this.genRightPart()])]);
  }
});

var _createNamespace$3 = createNamespace('switch-cell'),
  createComponent$3 = _createNamespace$3[0],
  bem$3 = _createNamespace$3[1];
function SwitchCell(h, props, slots, ctx) {
  return h(Cell$1, _mergeJSXProps([{
    "attrs": {
      "center": true,
      "size": props.cellSize,
      "title": props.title,
      "border": props.border
    },
    "class": bem$3([props.cellSize])
  }, inherit(ctx)]), [h(Switch, {
    "props": _extends$1({}, props),
    "on": _extends$1({}, ctx.listeners)
  })]);
}
SwitchCell.props = _extends$1({}, switchProps, {
  title: String,
  cellSize: String,
  border: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: '24px'
  }
});
var SwitchCell$1 = createComponent$3(SwitchCell);

var _createNamespace$2 = createNamespace('tabbar'),
  createComponent$2 = _createNamespace$2[0],
  bem$2 = _createNamespace$2[1];
var Tabbar = createComponent$2({
  mixins: [ParentMixin('vanTabbar')],
  props: {
    route: Boolean,
    zIndex: [Number, String],
    placeholder: Boolean,
    activeColor: String,
    beforeChange: Function,
    inactiveColor: String,
    value: {
      type: [Number, String],
      default: 0
    },
    border: {
      type: Boolean,
      default: true
    },
    fixed: {
      type: Boolean,
      default: true
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: null
    }
  },
  data: function data() {
    return {
      height: null
    };
  },
  computed: {
    fit: function fit() {
      if (this.safeAreaInsetBottom !== null) {
        return this.safeAreaInsetBottom;
      } // enable safe-area-inset-bottom by default when fixed

      return this.fixed;
    }
  },
  watch: {
    value: 'setActiveItem',
    children: 'setActiveItem'
  },
  mounted: function mounted() {
    var _this = this;
    if (this.placeholder && this.fixed) {
      var setHeight = function setHeight() {
        _this.height = _this.$refs.tabbar.getBoundingClientRect().height;
      };
      setHeight(); // https://github.com/vant-ui/vant/issues/10131

      setTimeout(setHeight, 100);
    }
  },
  methods: {
    setActiveItem: function setActiveItem() {
      var _this2 = this;
      this.children.forEach(function (item, index) {
        item.nameMatched = item.name === _this2.value || index === _this2.value;
      });
    },
    triggerChange: function triggerChange(active, afterChange) {
      var _this3 = this;
      callInterceptor({
        interceptor: this.beforeChange,
        args: [active],
        done: function done() {
          _this3.$emit('input', active);
          _this3.$emit('change', active);
          afterChange();
        }
      });
    },
    genTabbar: function genTabbar() {
      var _ref;
      var h = this.$createElement;
      return h("div", {
        "ref": "tabbar",
        "style": {
          zIndex: this.zIndex
        },
        "class": [(_ref = {}, _ref[BORDER_TOP_BOTTOM] = this.border, _ref), bem$2({
          unfit: !this.fit,
          fixed: this.fixed
        })]
      }, [this.slots()]);
    }
  },
  render: function render() {
    var h = arguments[0];
    if (this.placeholder && this.fixed) {
      return h("div", {
        "class": bem$2('placeholder'),
        "style": {
          height: this.height + "px"
        }
      }, [this.genTabbar()]);
    }
    return this.genTabbar();
  }
});

var _createNamespace$1 = createNamespace('tabbar-item'),
  createComponent$1 = _createNamespace$1[0],
  bem$1 = _createNamespace$1[1];
var TabbarItem = createComponent$1({
  mixins: [ChildrenMixin('vanTabbar')],
  props: _extends$1({}, routeProps, {
    dot: Boolean,
    icon: String,
    name: [Number, String],
    // @deprecated
    info: [Number, String],
    badge: [Number, String],
    iconPrefix: String
  }),
  data: function data() {
    return {
      nameMatched: false
    };
  },
  computed: {
    active: function active() {
      var routeMode = this.parent.route;
      if (routeMode && '$route' in this) {
        var to = this.to,
          $route = this.$route;
        var config = isObject$2(to) ? to : {
          path: to
        };
        return !!$route.matched.find(function (r) {
          // vue-router 3.x $route.matched[0].path is empty in / and its children paths
          var path = r.path === '' ? '/' : r.path;
          var pathMatched = config.path === path;
          var nameMatched = isDef(config.name) && config.name === r.name;
          return pathMatched || nameMatched;
        });
      }
      return this.nameMatched;
    }
  },
  methods: {
    onClick: function onClick(event) {
      var _this = this;
      if (!this.active) {
        this.parent.triggerChange(this.name || this.index, function () {
          route(_this.$router, _this);
        });
      }
      this.$emit('click', event);
    },
    genIcon: function genIcon() {
      var h = this.$createElement;
      var slot = this.slots('icon', {
        active: this.active
      });
      if (slot) {
        return slot;
      }
      if (this.icon) {
        return h(Icon$3, {
          "attrs": {
            "name": this.icon,
            "classPrefix": this.iconPrefix
          }
        });
      }
    }
  },
  render: function render() {
    var _this$badge;
    var h = arguments[0];
    var active = this.active;
    var color = this.parent[active ? 'activeColor' : 'inactiveColor'];
    return h("div", {
      "class": bem$1({
        active: active
      }),
      "style": {
        color: color
      },
      "on": {
        "click": this.onClick
      }
    }, [h("div", {
      "class": bem$1('icon')
    }, [this.genIcon(), h(Info$1, {
      "attrs": {
        "dot": this.dot,
        "info": (_this$badge = this.badge) != null ? _this$badge : this.info
      }
    })]), h("div", {
      "class": bem$1('text')
    }, [this.slots('default', {
      active: active
    })])]);
  }
});

var _createNamespace = createNamespace('tree-select'),
  createComponent = _createNamespace[0],
  bem = _createNamespace[1];
function TreeSelect(h, props, slots, ctx) {
  var items = props.items,
    height = props.height,
    activeId = props.activeId,
    selectedIcon = props.selectedIcon,
    mainActiveIndex = props.mainActiveIndex;
  var selectedItem = items[+mainActiveIndex] || {};
  var subItems = selectedItem.children || [];
  var isMultiple = Array.isArray(activeId);
  function isActiveItem(id) {
    return isMultiple ? activeId.indexOf(id) !== -1 : activeId === id;
  }
  var Navs = items.map(function (item) {
    var _item$badge;
    return h(SidebarItem, {
      "attrs": {
        "dot": item.dot,
        "info": (_item$badge = item.badge) != null ? _item$badge : item.info,
        "title": item.text,
        "disabled": item.disabled
      },
      "class": [bem('nav-item'), item.className]
    });
  });
  function Content() {
    if (slots.content) {
      return slots.content();
    }
    return subItems.map(function (item) {
      return h("div", {
        "key": item.id,
        "class": ['van-ellipsis', bem('item', {
          active: isActiveItem(item.id),
          disabled: item.disabled
        })],
        "on": {
          "click": function click() {
            if (!item.disabled) {
              var newActiveId = item.id;
              if (isMultiple) {
                newActiveId = activeId.slice();
                var index = newActiveId.indexOf(item.id);
                if (index !== -1) {
                  newActiveId.splice(index, 1);
                } else if (newActiveId.length < props.max) {
                  newActiveId.push(item.id);
                }
              }
              emit(ctx, 'update:active-id', newActiveId);
              emit(ctx, 'click-item', item); // compatible with legacy usage, should be removed in next major version

              emit(ctx, 'itemclick', item);
            }
          }
        }
      }, [item.text, isActiveItem(item.id) && h(Icon$3, {
        "attrs": {
          "name": selectedIcon
        },
        "class": bem('selected')
      })]);
    });
  }
  return h("div", _mergeJSXProps([{
    "class": bem(),
    "style": {
      height: addUnit(height)
    }
  }, inherit(ctx)]), [h(Sidebar, {
    "class": bem('nav'),
    "attrs": {
      "activeKey": mainActiveIndex
    },
    "on": {
      "change": function change(index) {
        emit(ctx, 'update:main-active-index', index);
        emit(ctx, 'click-nav', index); // compatible with legacy usage, should be removed in next major version

        emit(ctx, 'navclick', index);
      }
    }
  }, [Navs]), h("div", {
    "class": bem('content')
  }, [Content()])]);
}
TreeSelect.props = {
  max: {
    type: [Number, String],
    default: Infinity
  },
  items: {
    type: Array,
    default: function _default() {
      return [];
    }
  },
  height: {
    type: [Number, String],
    default: 300
  },
  activeId: {
    type: [Number, String, Array],
    default: 0
  },
  selectedIcon: {
    type: String,
    default: 'success'
  },
  mainActiveIndex: {
    type: [Number, String],
    default: 0
  }
};
var TreeSelect$1 = createComponent(TreeSelect);

function install$2(Vue) {
  var components = [ActionSheet$1, AddressEdit, AddressList$1, Area, Badge, Button$2, Calendar, Card$1, Cascader, Cell$1, CellGroup$1, Checkbox, CheckboxGroup, Circle, Col, Collapse, CollapseItem, ContactCard$1, ContactEdit, ContactList$1, CountDown, Coupon, CouponCell$1, CouponList, DatetimePicker, Dialog, Divider$1, DropdownItem, DropdownMenu, Empty, Field, Form, GoodsAction, GoodsActionButton, GoodsActionIcon, Grid, GridItem, Icon$3, Image$1, ImagePreview$1, IndexAnchor, IndexBar, Info$1, List, Loading$1, Locale$1, NavBar, NoticeBar, Notify, NumberKeyboard, Overlay$1, Pagination, Panel$1, PasswordInput$1, Picker, Popover, Popup, Progress, PullRefresh, Radio, RadioGroup, Rate, Row, Search$1, ShareSheet, Sidebar, SidebarItem, Skeleton$2, Sku, Slider$1, Step, Stepper, Steps, Sticky, SubmitBar$1, Swipe, SwipeCell, SwipeItem, Switch, SwitchCell$1, Tab, Tabbar, TabbarItem, Tabs, Tag$1, Toast, TreeSelect$1, Uploader];
  components.forEach(function (item) {
    if (item.install) {
      Vue.use(item);
    } else if (item.name) {
      Vue.component(item.name, item);
    }
  });
}
if (typeof window !== 'undefined' && window.Vue) {
  install$2(window.Vue);
}

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$5I = {
  //预览教材目录
  name: 'PreviewCatalogRender',
  props: {
    textBookId: String,
    catalogList: Array,
    checkedId: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    },
    isShow: {
      type: Boolean,
      default: false
    },
    title: String
  },
  data() {
    return {
      scrollTop: 0,
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      selectId: ''
    };
  },
  watch: {
    checkedId: {
      handler(newValue) {
        console.log('=============================newValue=======', newValue);
        if (this.$refs.tree) {
          this.$refs.tree.setCurrentKey(newValue);
        }
      },
      immediate: true,
      deep: true
    },
    isShow: {
      handler(newValue) {
        console.log(newValue);
        let dom = document.getElementById('preview-tree');
        if (dom) {
          dom.animate({
            scrollTop: this.scrollTop
          }, 200);
        }
      },
      immediate: true
    }
  },
  created() {},
  methods: {
    nodeClick(data) {
      console.log(data);
      this.$emit('onNodeClick', {
        id: data.id,
        label: data.label
      });
      this.selectId = data.id;
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  }
  // Vue.extend constructor export interop.
  const options = typeof script === 'function' ? script.options : script;
  // render functions
  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true;
    // functional template
    if (isFunctionalTemplate) {
      options.functional = true;
    }
  }
  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }
  let hook;
  if (moduleIdentifier) {
    // server build
    hook = function (context) {
      // 2.3 injection
      context = context ||
      // cached call
      this.$vnode && this.$vnode.ssrContext ||
      // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (style) {
        style.call(this, createInjectorSSR(context));
      }
      // register component module identifier for async chunk inference
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }
  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      const originalRender = options.render;
      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      const existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }
  return script;
}

/* script */
const __vue_script__$5I = script$5I;
/* template */
var __vue_render__$5I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _vm.pageType !== "pc"
        ? _c("div", { staticClass: "preview-container-title" }, [
            _c("span", { staticClass: "container-text" }, [
              _vm._v(_vm._s(_vm.title)),
            ]),
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("el-tree", {
        ref: "tree",
        class: "preview-tree-box-" + _vm.pageType,
        staticStyle: { "margin-top": "50px" },
        attrs: {
          id: "preview-tree",
          draggable: true,
          "default-expand-all": true,
          "highlight-current": "",
          data: _vm.catalogList,
          "node-key": "id",
          props: _vm.defaultProps,
          "expand-on-click-node": false,
        },
        on: { "node-click": _vm.nodeClick },
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$5I = [];
__vue_render__$5I._withStripped = true;

  /* style */
  const __vue_inject_styles__$5I = undefined;
  /* scoped */
  const __vue_scope_id__$5I = "data-v-2d83ed86";
  /* module identifier */
  const __vue_module_identifier__$5I = undefined;
  /* functional template */
  const __vue_is_functional_template__$5I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5I, staticRenderFns: __vue_staticRenderFns__$5I },
    __vue_inject_styles__$5I,
    __vue_script__$5I,
    __vue_scope_id__$5I,
    __vue_is_functional_template__$5I,
    __vue_module_identifier__$5I,
    false,
    undefined,
    undefined,
    undefined
  );

var jquery = createCommonjsModule(function (module) {
/*!
 * jQuery JavaScript Library v3.7.1
 * https://jquery.com/
 *
 * Copyright OpenJS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2023-08-28T13:37Z
 */
(function (global, factory) {

  {
    // For CommonJS and CommonJS-like environments where a proper `window`
    // is present, execute the factory and get jQuery.
    // For environments that do not have a `window` with a `document`
    // (such as Node.js), expose a factory as module.exports.
    // This accentuates the need for the creation of a real `window`.
    // e.g. var jQuery = require("jquery")(window);
    // See ticket trac-14549 for more info.
    module.exports = global.document ? factory(global, true) : function (w) {
      if (!w.document) {
        throw new Error("jQuery requires a window with a document");
      }
      return factory(w);
    };
  }

  // Pass this if window is not defined yet
})(typeof window !== "undefined" ? window : commonjsGlobal, function (window, noGlobal) {

  var arr = [];
  var getProto = Object.getPrototypeOf;
  var slice = arr.slice;
  var flat = arr.flat ? function (array) {
    return arr.flat.call(array);
  } : function (array) {
    return arr.concat.apply([], array);
  };
  var push = arr.push;
  var indexOf = arr.indexOf;
  var class2type = {};
  var toString = class2type.toString;
  var hasOwn = class2type.hasOwnProperty;
  var fnToString = hasOwn.toString;
  var ObjectFunctionString = fnToString.call(Object);
  var support = {};
  var isFunction = function isFunction(obj) {
    // Support: Chrome <=57, Firefox <=52
    // In some browsers, typeof returns "function" for HTML <object> elements
    // (i.e., `typeof document.createElement( "object" ) === "function"`).
    // We don't want to classify *any* DOM node as a function.
    // Support: QtWeb <=3.8.5, WebKit <=534.34, wkhtmltopdf tool <=0.12.5
    // Plus for old WebKit, typeof returns "function" for HTML collections
    // (e.g., `typeof document.getElementsByTagName("div") === "function"`). (gh-4756)
    return typeof obj === "function" && typeof obj.nodeType !== "number" && typeof obj.item !== "function";
  };
  var isWindow = function isWindow(obj) {
    return obj != null && obj === obj.window;
  };
  var document = window.document;
  var preservedScriptAttributes = {
    type: true,
    src: true,
    nonce: true,
    noModule: true
  };
  function DOMEval(code, node, doc) {
    doc = doc || document;
    var i,
      val,
      script = doc.createElement("script");
    script.text = code;
    if (node) {
      for (i in preservedScriptAttributes) {
        // Support: Firefox 64+, Edge 18+
        // Some browsers don't support the "nonce" property on scripts.
        // On the other hand, just using `getAttribute` is not enough as
        // the `nonce` attribute is reset to an empty string whenever it
        // becomes browsing-context connected.
        // See https://github.com/whatwg/html/issues/2369
        // See https://html.spec.whatwg.org/#nonce-attributes
        // The `node.getAttribute` check was added for the sake of
        // `jQuery.globalEval` so that it can fake a nonce-containing node
        // via an object.
        val = node[i] || node.getAttribute && node.getAttribute(i);
        if (val) {
          script.setAttribute(i, val);
        }
      }
    }
    doc.head.appendChild(script).parentNode.removeChild(script);
  }
  function toType(obj) {
    if (obj == null) {
      return obj + "";
    }

    // Support: Android <=2.3 only (functionish RegExp)
    return typeof obj === "object" || typeof obj === "function" ? class2type[toString.call(obj)] || "object" : typeof obj;
  }
  /* global Symbol */
  // Defining this global in .eslintrc.json would create a danger of using the global
  // unguarded in another place, it seems safer to define global only for this module

  var version = "3.7.1",
    rhtmlSuffix = /HTML$/i,
    // Define a local copy of jQuery
    jQuery = function (selector, context) {
      // The jQuery object is actually just the init constructor 'enhanced'
      // Need init if jQuery is called (just allow error to be thrown if not included)
      return new jQuery.fn.init(selector, context);
    };
  jQuery.fn = jQuery.prototype = {
    // The current version of jQuery being used
    jquery: version,
    constructor: jQuery,
    // The default length of a jQuery object is 0
    length: 0,
    toArray: function () {
      return slice.call(this);
    },
    // Get the Nth element in the matched element set OR
    // Get the whole matched element set as a clean array
    get: function (num) {
      // Return all the elements in a clean array
      if (num == null) {
        return slice.call(this);
      }

      // Return just the one element from the set
      return num < 0 ? this[num + this.length] : this[num];
    },
    // Take an array of elements and push it onto the stack
    // (returning the new matched element set)
    pushStack: function (elems) {
      // Build a new jQuery matched element set
      var ret = jQuery.merge(this.constructor(), elems);

      // Add the old object onto the stack (as a reference)
      ret.prevObject = this;

      // Return the newly-formed element set
      return ret;
    },
    // Execute a callback for every element in the matched set.
    each: function (callback) {
      return jQuery.each(this, callback);
    },
    map: function (callback) {
      return this.pushStack(jQuery.map(this, function (elem, i) {
        return callback.call(elem, i, elem);
      }));
    },
    slice: function () {
      return this.pushStack(slice.apply(this, arguments));
    },
    first: function () {
      return this.eq(0);
    },
    last: function () {
      return this.eq(-1);
    },
    even: function () {
      return this.pushStack(jQuery.grep(this, function (_elem, i) {
        return (i + 1) % 2;
      }));
    },
    odd: function () {
      return this.pushStack(jQuery.grep(this, function (_elem, i) {
        return i % 2;
      }));
    },
    eq: function (i) {
      var len = this.length,
        j = +i + (i < 0 ? len : 0);
      return this.pushStack(j >= 0 && j < len ? [this[j]] : []);
    },
    end: function () {
      return this.prevObject || this.constructor();
    },
    // For internal use only.
    // Behaves like an Array's method, not like a jQuery method.
    push: push,
    sort: arr.sort,
    splice: arr.splice
  };
  jQuery.extend = jQuery.fn.extend = function () {
    var options,
      name,
      src,
      copy,
      copyIsArray,
      clone,
      target = arguments[0] || {},
      i = 1,
      length = arguments.length,
      deep = false;

    // Handle a deep copy situation
    if (typeof target === "boolean") {
      deep = target;

      // Skip the boolean and the target
      target = arguments[i] || {};
      i++;
    }

    // Handle case when target is a string or something (possible in deep copy)
    if (typeof target !== "object" && !isFunction(target)) {
      target = {};
    }

    // Extend jQuery itself if only one argument is passed
    if (i === length) {
      target = this;
      i--;
    }
    for (; i < length; i++) {
      // Only deal with non-null/undefined values
      if ((options = arguments[i]) != null) {
        // Extend the base object
        for (name in options) {
          copy = options[name];

          // Prevent Object.prototype pollution
          // Prevent never-ending loop
          if (name === "__proto__" || target === copy) {
            continue;
          }

          // Recurse if we're merging plain objects or arrays
          if (deep && copy && (jQuery.isPlainObject(copy) || (copyIsArray = Array.isArray(copy)))) {
            src = target[name];

            // Ensure proper type for the source value
            if (copyIsArray && !Array.isArray(src)) {
              clone = [];
            } else if (!copyIsArray && !jQuery.isPlainObject(src)) {
              clone = {};
            } else {
              clone = src;
            }
            copyIsArray = false;

            // Never move original objects, clone them
            target[name] = jQuery.extend(deep, clone, copy);

            // Don't bring in undefined values
          } else if (copy !== undefined) {
            target[name] = copy;
          }
        }
      }
    }

    // Return the modified object
    return target;
  };
  jQuery.extend({
    // Unique for each copy of jQuery on the page
    expando: "jQuery" + (version + Math.random()).replace(/\D/g, ""),
    // Assume jQuery is ready without the ready module
    isReady: true,
    error: function (msg) {
      throw new Error(msg);
    },
    noop: function () {},
    isPlainObject: function (obj) {
      var proto, Ctor;

      // Detect obvious negatives
      // Use toString instead of jQuery.type to catch host objects
      if (!obj || toString.call(obj) !== "[object Object]") {
        return false;
      }
      proto = getProto(obj);

      // Objects with no prototype (e.g., `Object.create( null )`) are plain
      if (!proto) {
        return true;
      }

      // Objects with prototype are plain iff they were constructed by a global Object function
      Ctor = hasOwn.call(proto, "constructor") && proto.constructor;
      return typeof Ctor === "function" && fnToString.call(Ctor) === ObjectFunctionString;
    },
    isEmptyObject: function (obj) {
      var name;
      for (name in obj) {
        return false;
      }
      return true;
    },
    // Evaluates a script in a provided context; falls back to the global one
    // if not specified.
    globalEval: function (code, options, doc) {
      DOMEval(code, {
        nonce: options && options.nonce
      }, doc);
    },
    each: function (obj, callback) {
      var length,
        i = 0;
      if (isArrayLike(obj)) {
        length = obj.length;
        for (; i < length; i++) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      } else {
        for (i in obj) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      }
      return obj;
    },
    // Retrieve the text value of an array of DOM nodes
    text: function (elem) {
      var node,
        ret = "",
        i = 0,
        nodeType = elem.nodeType;
      if (!nodeType) {
        // If no nodeType, this is expected to be an array
        while (node = elem[i++]) {
          // Do not traverse comment nodes
          ret += jQuery.text(node);
        }
      }
      if (nodeType === 1 || nodeType === 11) {
        return elem.textContent;
      }
      if (nodeType === 9) {
        return elem.documentElement.textContent;
      }
      if (nodeType === 3 || nodeType === 4) {
        return elem.nodeValue;
      }

      // Do not include comment or processing instruction nodes

      return ret;
    },
    // results is for internal usage only
    makeArray: function (arr, results) {
      var ret = results || [];
      if (arr != null) {
        if (isArrayLike(Object(arr))) {
          jQuery.merge(ret, typeof arr === "string" ? [arr] : arr);
        } else {
          push.call(ret, arr);
        }
      }
      return ret;
    },
    inArray: function (elem, arr, i) {
      return arr == null ? -1 : indexOf.call(arr, elem, i);
    },
    isXMLDoc: function (elem) {
      var namespace = elem && elem.namespaceURI,
        docElem = elem && (elem.ownerDocument || elem).documentElement;

      // Assume HTML when documentElement doesn't yet exist, such as inside
      // document fragments.
      return !rhtmlSuffix.test(namespace || docElem && docElem.nodeName || "HTML");
    },
    // Support: Android <=4.0 only, PhantomJS 1 only
    // push.apply(_, arraylike) throws on ancient WebKit
    merge: function (first, second) {
      var len = +second.length,
        j = 0,
        i = first.length;
      for (; j < len; j++) {
        first[i++] = second[j];
      }
      first.length = i;
      return first;
    },
    grep: function (elems, callback, invert) {
      var callbackInverse,
        matches = [],
        i = 0,
        length = elems.length,
        callbackExpect = !invert;

      // Go through the array, only saving the items
      // that pass the validator function
      for (; i < length; i++) {
        callbackInverse = !callback(elems[i], i);
        if (callbackInverse !== callbackExpect) {
          matches.push(elems[i]);
        }
      }
      return matches;
    },
    // arg is for internal usage only
    map: function (elems, callback, arg) {
      var length,
        value,
        i = 0,
        ret = [];

      // Go through the array, translating each of the items to their new values
      if (isArrayLike(elems)) {
        length = elems.length;
        for (; i < length; i++) {
          value = callback(elems[i], i, arg);
          if (value != null) {
            ret.push(value);
          }
        }

        // Go through every key on the object,
      } else {
        for (i in elems) {
          value = callback(elems[i], i, arg);
          if (value != null) {
            ret.push(value);
          }
        }
      }

      // Flatten any nested arrays
      return flat(ret);
    },
    // A global GUID counter for objects
    guid: 1,
    // jQuery.support is not used in Core but other projects attach their
    // properties to it so it needs to exist.
    support: support
  });
  if (typeof Symbol === "function") {
    jQuery.fn[Symbol.iterator] = arr[Symbol.iterator];
  }

  // Populate the class2type map
  jQuery.each("Boolean Number String Function Array Date RegExp Object Error Symbol".split(" "), function (_i, name) {
    class2type["[object " + name + "]"] = name.toLowerCase();
  });
  function isArrayLike(obj) {
    // Support: real iOS 8.2 only (not reproducible in simulator)
    // `in` check used to prevent JIT error (gh-2145)
    // hasOwn isn't used here due to false negatives
    // regarding Nodelist length in IE
    var length = !!obj && "length" in obj && obj.length,
      type = toType(obj);
    if (isFunction(obj) || isWindow(obj)) {
      return false;
    }
    return type === "array" || length === 0 || typeof length === "number" && length > 0 && length - 1 in obj;
  }
  function nodeName(elem, name) {
    return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();
  }
  var pop = arr.pop;
  var sort = arr.sort;
  var splice = arr.splice;
  var whitespace = "[\\x20\\t\\r\\n\\f]";
  var rtrimCSS = new RegExp("^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$", "g");

  // Note: an element does not contain itself
  jQuery.contains = function (a, b) {
    var bup = b && b.parentNode;
    return a === bup || !!(bup && bup.nodeType === 1 && (
    // Support: IE 9 - 11+
    // IE doesn't have `contains` on SVG.
    a.contains ? a.contains(bup) : a.compareDocumentPosition && a.compareDocumentPosition(bup) & 16));
  };

  // CSS string/identifier serialization
  // https://drafts.csswg.org/cssom/#common-serializing-idioms
  var rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\x80-\uFFFF\w-]/g;
  function fcssescape(ch, asCodePoint) {
    if (asCodePoint) {
      // U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
      if (ch === "\0") {
        return "\uFFFD";
      }

      // Control characters and (dependent upon position) numbers get escaped as code points
      return ch.slice(0, -1) + "\\" + ch.charCodeAt(ch.length - 1).toString(16) + " ";
    }

    // Other potentially-special ASCII characters get backslash-escaped
    return "\\" + ch;
  }
  jQuery.escapeSelector = function (sel) {
    return (sel + "").replace(rcssescape, fcssescape);
  };
  var preferredDoc = document,
    pushNative = push;
  (function () {
    var i,
      Expr,
      outermostContext,
      sortInput,
      hasDuplicate,
      push = pushNative,
      // Local document vars
      document,
      documentElement,
      documentIsHTML,
      rbuggyQSA,
      matches,
      // Instance-specific data
      expando = jQuery.expando,
      dirruns = 0,
      done = 0,
      classCache = createCache(),
      tokenCache = createCache(),
      compilerCache = createCache(),
      nonnativeSelectorCache = createCache(),
      sortOrder = function (a, b) {
        if (a === b) {
          hasDuplicate = true;
        }
        return 0;
      },
      booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|" + "loop|multiple|open|readonly|required|scoped",
      // Regular expressions

      // https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
      identifier = "(?:\\\\[\\da-fA-F]{1,6}" + whitespace + "?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+",
      // Attribute selectors: https://www.w3.org/TR/selectors/#attribute-selectors
      attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +
      // Operator (capture 2)
      "*([*^$|!~]?=)" + whitespace +
      // "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
      "*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" + whitespace + "*\\]",
      pseudos = ":(" + identifier + ")(?:\\((" +
      // To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
      // 1. quoted (capture 3; capture 4 or capture 5)
      "('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +
      // 2. simple (capture 6)
      "((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +
      // 3. anything else (capture 2)
      ".*" + ")\\)|)",
      // Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
      rwhitespace = new RegExp(whitespace + "+", "g"),
      rcomma = new RegExp("^" + whitespace + "*," + whitespace + "*"),
      rleadingCombinator = new RegExp("^" + whitespace + "*([>+~]|" + whitespace + ")" + whitespace + "*"),
      rdescend = new RegExp(whitespace + "|>"),
      rpseudo = new RegExp(pseudos),
      ridentifier = new RegExp("^" + identifier + "$"),
      matchExpr = {
        ID: new RegExp("^#(" + identifier + ")"),
        CLASS: new RegExp("^\\.(" + identifier + ")"),
        TAG: new RegExp("^(" + identifier + "|[*])"),
        ATTR: new RegExp("^" + attributes),
        PSEUDO: new RegExp("^" + pseudos),
        CHILD: new RegExp("^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" + whitespace + "*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" + whitespace + "*(\\d+)|))" + whitespace + "*\\)|)", "i"),
        bool: new RegExp("^(?:" + booleans + ")$", "i"),
        // For use in libraries implementing .is()
        // We use this for POS matching in `select`
        needsContext: new RegExp("^" + whitespace + "*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" + whitespace + "*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i")
      },
      rinputs = /^(?:input|select|textarea|button)$/i,
      rheader = /^h\d$/i,
      // Easily-parseable/retrievable ID or TAG or CLASS selectors
      rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,
      rsibling = /[+~]/,
      // CSS escapes
      // https://www.w3.org/TR/CSS21/syndata.html#escaped-characters
      runescape = new RegExp("\\\\[\\da-fA-F]{1,6}" + whitespace + "?|\\\\([^\\r\\n\\f])", "g"),
      funescape = function (escape, nonHex) {
        var high = "0x" + escape.slice(1) - 0x10000;
        if (nonHex) {
          // Strip the backslash prefix from a non-hex escape sequence
          return nonHex;
        }

        // Replace a hexadecimal escape sequence with the encoded Unicode code point
        // Support: IE <=11+
        // For values outside the Basic Multilingual Plane (BMP), manually construct a
        // surrogate pair
        return high < 0 ? String.fromCharCode(high + 0x10000) : String.fromCharCode(high >> 10 | 0xD800, high & 0x3FF | 0xDC00);
      },
      // Used for iframes; see `setDocument`.
      // Support: IE 9 - 11+, Edge 12 - 18+
      // Removing the function wrapper causes a "Permission Denied"
      // error in IE/Edge.
      unloadHandler = function () {
        setDocument();
      },
      inDisabledFieldset = addCombinator(function (elem) {
        return elem.disabled === true && nodeName(elem, "fieldset");
      }, {
        dir: "parentNode",
        next: "legend"
      });

    // Support: IE <=9 only
    // Accessing document.activeElement can throw unexpectedly
    // https://bugs.jquery.com/ticket/13393
    function safeActiveElement() {
      try {
        return document.activeElement;
      } catch (err) {}
    }

    // Optimize for push.apply( _, NodeList )
    try {
      push.apply(arr = slice.call(preferredDoc.childNodes), preferredDoc.childNodes);

      // Support: Android <=4.0
      // Detect silently failing push.apply
      // eslint-disable-next-line no-unused-expressions
      arr[preferredDoc.childNodes.length].nodeType;
    } catch (e) {
      push = {
        apply: function (target, els) {
          pushNative.apply(target, slice.call(els));
        },
        call: function (target) {
          pushNative.apply(target, slice.call(arguments, 1));
        }
      };
    }
    function find(selector, context, results, seed) {
      var m,
        i,
        elem,
        nid,
        match,
        groups,
        newSelector,
        newContext = context && context.ownerDocument,
        // nodeType defaults to 9, since context defaults to document
        nodeType = context ? context.nodeType : 9;
      results = results || [];

      // Return early from calls with invalid selector or context
      if (typeof selector !== "string" || !selector || nodeType !== 1 && nodeType !== 9 && nodeType !== 11) {
        return results;
      }

      // Try to shortcut find operations (as opposed to filters) in HTML documents
      if (!seed) {
        setDocument(context);
        context = context || document;
        if (documentIsHTML) {
          // If the selector is sufficiently simple, try using a "get*By*" DOM method
          // (excepting DocumentFragment context, where the methods don't exist)
          if (nodeType !== 11 && (match = rquickExpr.exec(selector))) {
            // ID selector
            if (m = match[1]) {
              // Document context
              if (nodeType === 9) {
                if (elem = context.getElementById(m)) {
                  // Support: IE 9 only
                  // getElementById can match elements by name instead of ID
                  if (elem.id === m) {
                    push.call(results, elem);
                    return results;
                  }
                } else {
                  return results;
                }

                // Element context
              } else {
                // Support: IE 9 only
                // getElementById can match elements by name instead of ID
                if (newContext && (elem = newContext.getElementById(m)) && find.contains(context, elem) && elem.id === m) {
                  push.call(results, elem);
                  return results;
                }
              }

              // Type selector
            } else if (match[2]) {
              push.apply(results, context.getElementsByTagName(selector));
              return results;

              // Class selector
            } else if ((m = match[3]) && context.getElementsByClassName) {
              push.apply(results, context.getElementsByClassName(m));
              return results;
            }
          }

          // Take advantage of querySelectorAll
          if (!nonnativeSelectorCache[selector + " "] && (!rbuggyQSA || !rbuggyQSA.test(selector))) {
            newSelector = selector;
            newContext = context;

            // qSA considers elements outside a scoping root when evaluating child or
            // descendant combinators, which is not what we want.
            // In such cases, we work around the behavior by prefixing every selector in the
            // list with an ID selector referencing the scope context.
            // The technique has to be used as well when a leading combinator is used
            // as such selectors are not recognized by querySelectorAll.
            // Thanks to Andrew Dupont for this technique.
            if (nodeType === 1 && (rdescend.test(selector) || rleadingCombinator.test(selector))) {
              // Expand context for sibling selectors
              newContext = rsibling.test(selector) && testContext(context.parentNode) || context;

              // We can use :scope instead of the ID hack if the browser
              // supports it & if we're not changing the context.
              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when
              // strict-comparing two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              if (newContext != context || !support.scope) {
                // Capture the context ID, setting it first if necessary
                if (nid = context.getAttribute("id")) {
                  nid = jQuery.escapeSelector(nid);
                } else {
                  context.setAttribute("id", nid = expando);
                }
              }

              // Prefix every selector in the list
              groups = tokenize(selector);
              i = groups.length;
              while (i--) {
                groups[i] = (nid ? "#" + nid : ":scope") + " " + toSelector(groups[i]);
              }
              newSelector = groups.join(",");
            }
            try {
              push.apply(results, newContext.querySelectorAll(newSelector));
              return results;
            } catch (qsaError) {
              nonnativeSelectorCache(selector, true);
            } finally {
              if (nid === expando) {
                context.removeAttribute("id");
              }
            }
          }
        }
      }

      // All others
      return select(selector.replace(rtrimCSS, "$1"), context, results, seed);
    }

    /**
     * Create key-value caches of limited size
     * @returns {function(string, object)} Returns the Object data after storing it on itself with
     *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
     *	deleting the oldest entry
     */
    function createCache() {
      var keys = [];
      function cache(key, value) {
        // Use (key + " ") to avoid collision with native prototype properties
        // (see https://github.com/jquery/sizzle/issues/157)
        if (keys.push(key + " ") > Expr.cacheLength) {
          // Only keep the most recent entries
          delete cache[keys.shift()];
        }
        return cache[key + " "] = value;
      }
      return cache;
    }

    /**
     * Mark a function for special use by jQuery selector module
     * @param {Function} fn The function to mark
     */
    function markFunction(fn) {
      fn[expando] = true;
      return fn;
    }

    /**
     * Support testing using an element
     * @param {Function} fn Passed the created element and returns a boolean result
     */
    function assert(fn) {
      var el = document.createElement("fieldset");
      try {
        return !!fn(el);
      } catch (e) {
        return false;
      } finally {
        // Remove from its parent by default
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }

        // release memory in IE
        el = null;
      }
    }

    /**
     * Returns a function to use in pseudos for input types
     * @param {String} type
     */
    function createInputPseudo(type) {
      return function (elem) {
        return nodeName(elem, "input") && elem.type === type;
      };
    }

    /**
     * Returns a function to use in pseudos for buttons
     * @param {String} type
     */
    function createButtonPseudo(type) {
      return function (elem) {
        return (nodeName(elem, "input") || nodeName(elem, "button")) && elem.type === type;
      };
    }

    /**
     * Returns a function to use in pseudos for :enabled/:disabled
     * @param {Boolean} disabled true for :disabled; false for :enabled
     */
    function createDisabledPseudo(disabled) {
      // Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
      return function (elem) {
        // Only certain elements can match :enabled or :disabled
        // https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
        // https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
        if ("form" in elem) {
          // Check for inherited disabledness on relevant non-disabled elements:
          // * listed form-associated elements in a disabled fieldset
          //   https://html.spec.whatwg.org/multipage/forms.html#category-listed
          //   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
          // * option elements in a disabled optgroup
          //   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
          // All such elements have a "form" property.
          if (elem.parentNode && elem.disabled === false) {
            // Option elements defer to a parent optgroup if present
            if ("label" in elem) {
              if ("label" in elem.parentNode) {
                return elem.parentNode.disabled === disabled;
              } else {
                return elem.disabled === disabled;
              }
            }

            // Support: IE 6 - 11+
            // Use the isDisabled shortcut property to check for disabled fieldset ancestors
            return elem.isDisabled === disabled ||
            // Where there is no isDisabled, check manually
            elem.isDisabled !== !disabled && inDisabledFieldset(elem) === disabled;
          }
          return elem.disabled === disabled;

          // Try to winnow out elements that can't be disabled before trusting the disabled property.
          // Some victims get caught in our net (label, legend, menu, track), but it shouldn't
          // even exist on them, let alone have a boolean value.
        } else if ("label" in elem) {
          return elem.disabled === disabled;
        }

        // Remaining elements are neither :enabled nor :disabled
        return false;
      };
    }

    /**
     * Returns a function to use in pseudos for positionals
     * @param {Function} fn
     */
    function createPositionalPseudo(fn) {
      return markFunction(function (argument) {
        argument = +argument;
        return markFunction(function (seed, matches) {
          var j,
            matchIndexes = fn([], seed.length, argument),
            i = matchIndexes.length;

          // Match elements found at the specified indexes
          while (i--) {
            if (seed[j = matchIndexes[i]]) {
              seed[j] = !(matches[j] = seed[j]);
            }
          }
        });
      });
    }

    /**
     * Checks a node for validity as a jQuery selector context
     * @param {Element|Object=} context
     * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
     */
    function testContext(context) {
      return context && typeof context.getElementsByTagName !== "undefined" && context;
    }

    /**
     * Sets document-related variables once based on the current document
     * @param {Element|Object} [node] An element or document object to use to set the document
     * @returns {Object} Returns the current document
     */
    function setDocument(node) {
      var subWindow,
        doc = node ? node.ownerDocument || node : preferredDoc;

      // Return early if doc is invalid or already selected
      // Support: IE 11+, Edge 17 - 18+
      // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
      // two documents; shallow comparisons work.
      // eslint-disable-next-line eqeqeq
      if (doc == document || doc.nodeType !== 9 || !doc.documentElement) {
        return document;
      }

      // Update global variables
      document = doc;
      documentElement = document.documentElement;
      documentIsHTML = !jQuery.isXMLDoc(document);

      // Support: iOS 7 only, IE 9 - 11+
      // Older browsers didn't support unprefixed `matches`.
      matches = documentElement.matches || documentElement.webkitMatchesSelector || documentElement.msMatchesSelector;

      // Support: IE 9 - 11+, Edge 12 - 18+
      // Accessing iframe documents after unload throws "permission denied" errors
      // (see trac-13936).
      // Limit the fix to IE & Edge Legacy; despite Edge 15+ implementing `matches`,
      // all IE 9+ and Edge Legacy versions implement `msMatchesSelector` as well.
      if (documentElement.msMatchesSelector &&
      // Support: IE 11+, Edge 17 - 18+
      // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
      // two documents; shallow comparisons work.
      // eslint-disable-next-line eqeqeq
      preferredDoc != document && (subWindow = document.defaultView) && subWindow.top !== subWindow) {
        // Support: IE 9 - 11+, Edge 12 - 18+
        subWindow.addEventListener("unload", unloadHandler);
      }

      // Support: IE <10
      // Check if getElementById returns elements by name
      // The broken getElementById methods don't pick up programmatically-set names,
      // so use a roundabout getElementsByName test
      support.getById = assert(function (el) {
        documentElement.appendChild(el).id = jQuery.expando;
        return !document.getElementsByName || !document.getElementsByName(jQuery.expando).length;
      });

      // Support: IE 9 only
      // Check to see if it's possible to do matchesSelector
      // on a disconnected node.
      support.disconnectedMatch = assert(function (el) {
        return matches.call(el, "*");
      });

      // Support: IE 9 - 11+, Edge 12 - 18+
      // IE/Edge don't support the :scope pseudo-class.
      support.scope = assert(function () {
        return document.querySelectorAll(":scope");
      });

      // Support: Chrome 105 - 111 only, Safari 15.4 - 16.3 only
      // Make sure the `:has()` argument is parsed unforgivingly.
      // We include `*` in the test to detect buggy implementations that are
      // _selectively_ forgiving (specifically when the list includes at least
      // one valid selector).
      // Note that we treat complete lack of support for `:has()` as if it were
      // spec-compliant support, which is fine because use of `:has()` in such
      // environments will fail in the qSA path and fall back to jQuery traversal
      // anyway.
      support.cssHas = assert(function () {
        try {
          document.querySelector(":has(*,:jqfake)");
          return false;
        } catch (e) {
          return true;
        }
      });

      // ID filter and find
      if (support.getById) {
        Expr.filter.ID = function (id) {
          var attrId = id.replace(runescape, funescape);
          return function (elem) {
            return elem.getAttribute("id") === attrId;
          };
        };
        Expr.find.ID = function (id, context) {
          if (typeof context.getElementById !== "undefined" && documentIsHTML) {
            var elem = context.getElementById(id);
            return elem ? [elem] : [];
          }
        };
      } else {
        Expr.filter.ID = function (id) {
          var attrId = id.replace(runescape, funescape);
          return function (elem) {
            var node = typeof elem.getAttributeNode !== "undefined" && elem.getAttributeNode("id");
            return node && node.value === attrId;
          };
        };

        // Support: IE 6 - 7 only
        // getElementById is not reliable as a find shortcut
        Expr.find.ID = function (id, context) {
          if (typeof context.getElementById !== "undefined" && documentIsHTML) {
            var node,
              i,
              elems,
              elem = context.getElementById(id);
            if (elem) {
              // Verify the id attribute
              node = elem.getAttributeNode("id");
              if (node && node.value === id) {
                return [elem];
              }

              // Fall back on getElementsByName
              elems = context.getElementsByName(id);
              i = 0;
              while (elem = elems[i++]) {
                node = elem.getAttributeNode("id");
                if (node && node.value === id) {
                  return [elem];
                }
              }
            }
            return [];
          }
        };
      }

      // Tag
      Expr.find.TAG = function (tag, context) {
        if (typeof context.getElementsByTagName !== "undefined") {
          return context.getElementsByTagName(tag);

          // DocumentFragment nodes don't have gEBTN
        } else {
          return context.querySelectorAll(tag);
        }
      };

      // Class
      Expr.find.CLASS = function (className, context) {
        if (typeof context.getElementsByClassName !== "undefined" && documentIsHTML) {
          return context.getElementsByClassName(className);
        }
      };

      /* QSA/matchesSelector
      ---------------------------------------------------------------------- */

      // QSA and matchesSelector support

      rbuggyQSA = [];

      // Build QSA regex
      // Regex strategy adopted from Diego Perini
      assert(function (el) {
        var input;
        documentElement.appendChild(el).innerHTML = "<a id='" + expando + "' href='' disabled='disabled'></a>" + "<select id='" + expando + "-\r\\' disabled='disabled'>" + "<option selected=''></option></select>";

        // Support: iOS <=7 - 8 only
        // Boolean attributes and "value" are not treated correctly in some XML documents
        if (!el.querySelectorAll("[selected]").length) {
          rbuggyQSA.push("\\[" + whitespace + "*(?:value|" + booleans + ")");
        }

        // Support: iOS <=7 - 8 only
        if (!el.querySelectorAll("[id~=" + expando + "-]").length) {
          rbuggyQSA.push("~=");
        }

        // Support: iOS 8 only
        // https://bugs.webkit.org/show_bug.cgi?id=136851
        // In-page `selector#id sibling-combinator selector` fails
        if (!el.querySelectorAll("a#" + expando + "+*").length) {
          rbuggyQSA.push(".#.+[+~]");
        }

        // Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
        // In some of the document kinds, these selectors wouldn't work natively.
        // This is probably OK but for backwards compatibility we want to maintain
        // handling them through jQuery traversal in jQuery 3.x.
        if (!el.querySelectorAll(":checked").length) {
          rbuggyQSA.push(":checked");
        }

        // Support: Windows 8 Native Apps
        // The type and name attributes are restricted during .innerHTML assignment
        input = document.createElement("input");
        input.setAttribute("type", "hidden");
        el.appendChild(input).setAttribute("name", "D");

        // Support: IE 9 - 11+
        // IE's :disabled selector does not pick up the children of disabled fieldsets
        // Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
        // In some of the document kinds, these selectors wouldn't work natively.
        // This is probably OK but for backwards compatibility we want to maintain
        // handling them through jQuery traversal in jQuery 3.x.
        documentElement.appendChild(el).disabled = true;
        if (el.querySelectorAll(":disabled").length !== 2) {
          rbuggyQSA.push(":enabled", ":disabled");
        }

        // Support: IE 11+, Edge 15 - 18+
        // IE 11/Edge don't find elements on a `[name='']` query in some cases.
        // Adding a temporary attribute to the document before the selection works
        // around the issue.
        // Interestingly, IE 10 & older don't seem to have the issue.
        input = document.createElement("input");
        input.setAttribute("name", "");
        el.appendChild(input);
        if (!el.querySelectorAll("[name='']").length) {
          rbuggyQSA.push("\\[" + whitespace + "*name" + whitespace + "*=" + whitespace + "*(?:''|\"\")");
        }
      });
      if (!support.cssHas) {
        // Support: Chrome 105 - 110+, Safari 15.4 - 16.3+
        // Our regular `try-catch` mechanism fails to detect natively-unsupported
        // pseudo-classes inside `:has()` (such as `:has(:contains("Foo"))`)
        // in browsers that parse the `:has()` argument as a forgiving selector list.
        // https://drafts.csswg.org/selectors/#relational now requires the argument
        // to be parsed unforgivingly, but browsers have not yet fully adjusted.
        rbuggyQSA.push(":has");
      }
      rbuggyQSA = rbuggyQSA.length && new RegExp(rbuggyQSA.join("|"));

      /* Sorting
      ---------------------------------------------------------------------- */

      // Document order sorting
      sortOrder = function (a, b) {
        // Flag for duplicate removal
        if (a === b) {
          hasDuplicate = true;
          return 0;
        }

        // Sort on method existence if only one input has compareDocumentPosition
        var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
        if (compare) {
          return compare;
        }

        // Calculate position if both inputs belong to the same document
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        compare = (a.ownerDocument || a) == (b.ownerDocument || b) ? a.compareDocumentPosition(b) :
        // Otherwise we know they are disconnected
        1;

        // Disconnected nodes
        if (compare & 1 || !support.sortDetached && b.compareDocumentPosition(a) === compare) {
          // Choose the first element that is related to our preferred document
          // Support: IE 11+, Edge 17 - 18+
          // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
          // two documents; shallow comparisons work.
          // eslint-disable-next-line eqeqeq
          if (a === document || a.ownerDocument == preferredDoc && find.contains(preferredDoc, a)) {
            return -1;
          }

          // Support: IE 11+, Edge 17 - 18+
          // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
          // two documents; shallow comparisons work.
          // eslint-disable-next-line eqeqeq
          if (b === document || b.ownerDocument == preferredDoc && find.contains(preferredDoc, b)) {
            return 1;
          }

          // Maintain original order
          return sortInput ? indexOf.call(sortInput, a) - indexOf.call(sortInput, b) : 0;
        }
        return compare & 4 ? -1 : 1;
      };
      return document;
    }
    find.matches = function (expr, elements) {
      return find(expr, null, null, elements);
    };
    find.matchesSelector = function (elem, expr) {
      setDocument(elem);
      if (documentIsHTML && !nonnativeSelectorCache[expr + " "] && (!rbuggyQSA || !rbuggyQSA.test(expr))) {
        try {
          var ret = matches.call(elem, expr);

          // IE 9's matchesSelector returns false on disconnected nodes
          if (ret || support.disconnectedMatch ||
          // As well, disconnected nodes are said to be in a document
          // fragment in IE 9
          elem.document && elem.document.nodeType !== 11) {
            return ret;
          }
        } catch (e) {
          nonnativeSelectorCache(expr, true);
        }
      }
      return find(expr, document, null, [elem]).length > 0;
    };
    find.contains = function (context, elem) {
      // Set document vars if needed
      // Support: IE 11+, Edge 17 - 18+
      // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
      // two documents; shallow comparisons work.
      // eslint-disable-next-line eqeqeq
      if ((context.ownerDocument || context) != document) {
        setDocument(context);
      }
      return jQuery.contains(context, elem);
    };
    find.attr = function (elem, name) {
      // Set document vars if needed
      // Support: IE 11+, Edge 17 - 18+
      // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
      // two documents; shallow comparisons work.
      // eslint-disable-next-line eqeqeq
      if ((elem.ownerDocument || elem) != document) {
        setDocument(elem);
      }
      var fn = Expr.attrHandle[name.toLowerCase()],
        // Don't get fooled by Object.prototype properties (see trac-13807)
        val = fn && hasOwn.call(Expr.attrHandle, name.toLowerCase()) ? fn(elem, name, !documentIsHTML) : undefined;
      if (val !== undefined) {
        return val;
      }
      return elem.getAttribute(name);
    };
    find.error = function (msg) {
      throw new Error("Syntax error, unrecognized expression: " + msg);
    };

    /**
     * Document sorting and removing duplicates
     * @param {ArrayLike} results
     */
    jQuery.uniqueSort = function (results) {
      var elem,
        duplicates = [],
        j = 0,
        i = 0;

      // Unless we *know* we can detect duplicates, assume their presence
      //
      // Support: Android <=4.0+
      // Testing for detecting duplicates is unpredictable so instead assume we can't
      // depend on duplicate detection in all browsers without a stable sort.
      hasDuplicate = !support.sortStable;
      sortInput = !support.sortStable && slice.call(results, 0);
      sort.call(results, sortOrder);
      if (hasDuplicate) {
        while (elem = results[i++]) {
          if (elem === results[i]) {
            j = duplicates.push(i);
          }
        }
        while (j--) {
          splice.call(results, duplicates[j], 1);
        }
      }

      // Clear input after sorting to release objects
      // See https://github.com/jquery/sizzle/pull/225
      sortInput = null;
      return results;
    };
    jQuery.fn.uniqueSort = function () {
      return this.pushStack(jQuery.uniqueSort(slice.apply(this)));
    };
    Expr = jQuery.expr = {
      // Can be adjusted by the user
      cacheLength: 50,
      createPseudo: markFunction,
      match: matchExpr,
      attrHandle: {},
      find: {},
      relative: {
        ">": {
          dir: "parentNode",
          first: true
        },
        " ": {
          dir: "parentNode"
        },
        "+": {
          dir: "previousSibling",
          first: true
        },
        "~": {
          dir: "previousSibling"
        }
      },
      preFilter: {
        ATTR: function (match) {
          match[1] = match[1].replace(runescape, funescape);

          // Move the given value to match[3] whether quoted or unquoted
          match[3] = (match[3] || match[4] || match[5] || "").replace(runescape, funescape);
          if (match[2] === "~=") {
            match[3] = " " + match[3] + " ";
          }
          return match.slice(0, 4);
        },
        CHILD: function (match) {
          /* matches from matchExpr["CHILD"]
          	1 type (only|nth|...)
          	2 what (child|of-type)
          	3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
          	4 xn-component of xn+y argument ([+-]?\d*n|)
          	5 sign of xn-component
          	6 x of xn-component
          	7 sign of y-component
          	8 y of y-component
          */
          match[1] = match[1].toLowerCase();
          if (match[1].slice(0, 3) === "nth") {
            // nth-* requires argument
            if (!match[3]) {
              find.error(match[0]);
            }

            // numeric x and y parameters for Expr.filter.CHILD
            // remember that false/true cast respectively to 0/1
            match[4] = +(match[4] ? match[5] + (match[6] || 1) : 2 * (match[3] === "even" || match[3] === "odd"));
            match[5] = +(match[7] + match[8] || match[3] === "odd");

            // other types prohibit arguments
          } else if (match[3]) {
            find.error(match[0]);
          }
          return match;
        },
        PSEUDO: function (match) {
          var excess,
            unquoted = !match[6] && match[2];
          if (matchExpr.CHILD.test(match[0])) {
            return null;
          }

          // Accept quoted arguments as-is
          if (match[3]) {
            match[2] = match[4] || match[5] || "";

            // Strip excess characters from unquoted arguments
          } else if (unquoted && rpseudo.test(unquoted) && (
          // Get excess from tokenize (recursively)
          excess = tokenize(unquoted, true)) && (
          // advance to the next closing parenthesis
          excess = unquoted.indexOf(")", unquoted.length - excess) - unquoted.length)) {
            // excess is a negative index
            match[0] = match[0].slice(0, excess);
            match[2] = unquoted.slice(0, excess);
          }

          // Return only captures needed by the pseudo filter method (type and argument)
          return match.slice(0, 3);
        }
      },
      filter: {
        TAG: function (nodeNameSelector) {
          var expectedNodeName = nodeNameSelector.replace(runescape, funescape).toLowerCase();
          return nodeNameSelector === "*" ? function () {
            return true;
          } : function (elem) {
            return nodeName(elem, expectedNodeName);
          };
        },
        CLASS: function (className) {
          var pattern = classCache[className + " "];
          return pattern || (pattern = new RegExp("(^|" + whitespace + ")" + className + "(" + whitespace + "|$)")) && classCache(className, function (elem) {
            return pattern.test(typeof elem.className === "string" && elem.className || typeof elem.getAttribute !== "undefined" && elem.getAttribute("class") || "");
          });
        },
        ATTR: function (name, operator, check) {
          return function (elem) {
            var result = find.attr(elem, name);
            if (result == null) {
              return operator === "!=";
            }
            if (!operator) {
              return true;
            }
            result += "";
            if (operator === "=") {
              return result === check;
            }
            if (operator === "!=") {
              return result !== check;
            }
            if (operator === "^=") {
              return check && result.indexOf(check) === 0;
            }
            if (operator === "*=") {
              return check && result.indexOf(check) > -1;
            }
            if (operator === "$=") {
              return check && result.slice(-check.length) === check;
            }
            if (operator === "~=") {
              return (" " + result.replace(rwhitespace, " ") + " ").indexOf(check) > -1;
            }
            if (operator === "|=") {
              return result === check || result.slice(0, check.length + 1) === check + "-";
            }
            return false;
          };
        },
        CHILD: function (type, what, _argument, first, last) {
          var simple = type.slice(0, 3) !== "nth",
            forward = type.slice(-4) !== "last",
            ofType = what === "of-type";
          return first === 1 && last === 0 ?
          // Shortcut for :nth-*(n)
          function (elem) {
            return !!elem.parentNode;
          } : function (elem, _context, xml) {
            var cache,
              outerCache,
              node,
              nodeIndex,
              start,
              dir = simple !== forward ? "nextSibling" : "previousSibling",
              parent = elem.parentNode,
              name = ofType && elem.nodeName.toLowerCase(),
              useCache = !xml && !ofType,
              diff = false;
            if (parent) {
              // :(first|last|only)-(child|of-type)
              if (simple) {
                while (dir) {
                  node = elem;
                  while (node = node[dir]) {
                    if (ofType ? nodeName(node, name) : node.nodeType === 1) {
                      return false;
                    }
                  }

                  // Reverse direction for :only-* (if we haven't yet done so)
                  start = dir = type === "only" && !start && "nextSibling";
                }
                return true;
              }
              start = [forward ? parent.firstChild : parent.lastChild];

              // non-xml :nth-child(...) stores cache data on `parent`
              if (forward && useCache) {
                // Seek `elem` from a previously-cached index
                outerCache = parent[expando] || (parent[expando] = {});
                cache = outerCache[type] || [];
                nodeIndex = cache[0] === dirruns && cache[1];
                diff = nodeIndex && cache[2];
                node = nodeIndex && parent.childNodes[nodeIndex];
                while (node = ++nodeIndex && node && node[dir] || (
                // Fallback to seeking `elem` from the start
                diff = nodeIndex = 0) || start.pop()) {
                  // When found, cache indexes on `parent` and break
                  if (node.nodeType === 1 && ++diff && node === elem) {
                    outerCache[type] = [dirruns, nodeIndex, diff];
                    break;
                  }
                }
              } else {
                // Use previously-cached element index if available
                if (useCache) {
                  outerCache = elem[expando] || (elem[expando] = {});
                  cache = outerCache[type] || [];
                  nodeIndex = cache[0] === dirruns && cache[1];
                  diff = nodeIndex;
                }

                // xml :nth-child(...)
                // or :nth-last-child(...) or :nth(-last)?-of-type(...)
                if (diff === false) {
                  // Use the same loop as above to seek `elem` from the start
                  while (node = ++nodeIndex && node && node[dir] || (diff = nodeIndex = 0) || start.pop()) {
                    if ((ofType ? nodeName(node, name) : node.nodeType === 1) && ++diff) {
                      // Cache the index of each encountered element
                      if (useCache) {
                        outerCache = node[expando] || (node[expando] = {});
                        outerCache[type] = [dirruns, diff];
                      }
                      if (node === elem) {
                        break;
                      }
                    }
                  }
                }
              }

              // Incorporate the offset, then check against cycle size
              diff -= last;
              return diff === first || diff % first === 0 && diff / first >= 0;
            }
          };
        },
        PSEUDO: function (pseudo, argument) {
          // pseudo-class names are case-insensitive
          // https://www.w3.org/TR/selectors/#pseudo-classes
          // Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
          // Remember that setFilters inherits from pseudos
          var args,
            fn = Expr.pseudos[pseudo] || Expr.setFilters[pseudo.toLowerCase()] || find.error("unsupported pseudo: " + pseudo);

          // The user may use createPseudo to indicate that
          // arguments are needed to create the filter function
          // just as jQuery does
          if (fn[expando]) {
            return fn(argument);
          }

          // But maintain support for old signatures
          if (fn.length > 1) {
            args = [pseudo, pseudo, "", argument];
            return Expr.setFilters.hasOwnProperty(pseudo.toLowerCase()) ? markFunction(function (seed, matches) {
              var idx,
                matched = fn(seed, argument),
                i = matched.length;
              while (i--) {
                idx = indexOf.call(seed, matched[i]);
                seed[idx] = !(matches[idx] = matched[i]);
              }
            }) : function (elem) {
              return fn(elem, 0, args);
            };
          }
          return fn;
        }
      },
      pseudos: {
        // Potentially complex pseudos
        not: markFunction(function (selector) {
          // Trim the selector passed to compile
          // to avoid treating leading and trailing
          // spaces as combinators
          var input = [],
            results = [],
            matcher = compile(selector.replace(rtrimCSS, "$1"));
          return matcher[expando] ? markFunction(function (seed, matches, _context, xml) {
            var elem,
              unmatched = matcher(seed, null, xml, []),
              i = seed.length;

            // Match elements unmatched by `matcher`
            while (i--) {
              if (elem = unmatched[i]) {
                seed[i] = !(matches[i] = elem);
              }
            }
          }) : function (elem, _context, xml) {
            input[0] = elem;
            matcher(input, null, xml, results);

            // Don't keep the element
            // (see https://github.com/jquery/sizzle/issues/299)
            input[0] = null;
            return !results.pop();
          };
        }),
        has: markFunction(function (selector) {
          return function (elem) {
            return find(selector, elem).length > 0;
          };
        }),
        contains: markFunction(function (text) {
          text = text.replace(runescape, funescape);
          return function (elem) {
            return (elem.textContent || jQuery.text(elem)).indexOf(text) > -1;
          };
        }),
        // "Whether an element is represented by a :lang() selector
        // is based solely on the element's language value
        // being equal to the identifier C,
        // or beginning with the identifier C immediately followed by "-".
        // The matching of C against the element's language value is performed case-insensitively.
        // The identifier C does not have to be a valid language name."
        // https://www.w3.org/TR/selectors/#lang-pseudo
        lang: markFunction(function (lang) {
          // lang value must be a valid identifier
          if (!ridentifier.test(lang || "")) {
            find.error("unsupported lang: " + lang);
          }
          lang = lang.replace(runescape, funescape).toLowerCase();
          return function (elem) {
            var elemLang;
            do {
              if (elemLang = documentIsHTML ? elem.lang : elem.getAttribute("xml:lang") || elem.getAttribute("lang")) {
                elemLang = elemLang.toLowerCase();
                return elemLang === lang || elemLang.indexOf(lang + "-") === 0;
              }
            } while ((elem = elem.parentNode) && elem.nodeType === 1);
            return false;
          };
        }),
        // Miscellaneous
        target: function (elem) {
          var hash = window.location && window.location.hash;
          return hash && hash.slice(1) === elem.id;
        },
        root: function (elem) {
          return elem === documentElement;
        },
        focus: function (elem) {
          return elem === safeActiveElement() && document.hasFocus() && !!(elem.type || elem.href || ~elem.tabIndex);
        },
        // Boolean properties
        enabled: createDisabledPseudo(false),
        disabled: createDisabledPseudo(true),
        checked: function (elem) {
          // In CSS3, :checked should return both checked and selected elements
          // https://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
          return nodeName(elem, "input") && !!elem.checked || nodeName(elem, "option") && !!elem.selected;
        },
        selected: function (elem) {
          // Support: IE <=11+
          // Accessing the selectedIndex property
          // forces the browser to treat the default option as
          // selected when in an optgroup.
          if (elem.parentNode) {
            // eslint-disable-next-line no-unused-expressions
            elem.parentNode.selectedIndex;
          }
          return elem.selected === true;
        },
        // Contents
        empty: function (elem) {
          // https://www.w3.org/TR/selectors/#empty-pseudo
          // :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
          //   but not by others (comment: 8; processing instruction: 7; etc.)
          // nodeType < 6 works because attributes (2) do not appear as children
          for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
            if (elem.nodeType < 6) {
              return false;
            }
          }
          return true;
        },
        parent: function (elem) {
          return !Expr.pseudos.empty(elem);
        },
        // Element/input types
        header: function (elem) {
          return rheader.test(elem.nodeName);
        },
        input: function (elem) {
          return rinputs.test(elem.nodeName);
        },
        button: function (elem) {
          return nodeName(elem, "input") && elem.type === "button" || nodeName(elem, "button");
        },
        text: function (elem) {
          var attr;
          return nodeName(elem, "input") && elem.type === "text" && (
          // Support: IE <10 only
          // New HTML5 attribute values (e.g., "search") appear
          // with elem.type === "text"
          (attr = elem.getAttribute("type")) == null || attr.toLowerCase() === "text");
        },
        // Position-in-collection
        first: createPositionalPseudo(function () {
          return [0];
        }),
        last: createPositionalPseudo(function (_matchIndexes, length) {
          return [length - 1];
        }),
        eq: createPositionalPseudo(function (_matchIndexes, length, argument) {
          return [argument < 0 ? argument + length : argument];
        }),
        even: createPositionalPseudo(function (matchIndexes, length) {
          var i = 0;
          for (; i < length; i += 2) {
            matchIndexes.push(i);
          }
          return matchIndexes;
        }),
        odd: createPositionalPseudo(function (matchIndexes, length) {
          var i = 1;
          for (; i < length; i += 2) {
            matchIndexes.push(i);
          }
          return matchIndexes;
        }),
        lt: createPositionalPseudo(function (matchIndexes, length, argument) {
          var i;
          if (argument < 0) {
            i = argument + length;
          } else if (argument > length) {
            i = length;
          } else {
            i = argument;
          }
          for (; --i >= 0;) {
            matchIndexes.push(i);
          }
          return matchIndexes;
        }),
        gt: createPositionalPseudo(function (matchIndexes, length, argument) {
          var i = argument < 0 ? argument + length : argument;
          for (; ++i < length;) {
            matchIndexes.push(i);
          }
          return matchIndexes;
        })
      }
    };
    Expr.pseudos.nth = Expr.pseudos.eq;

    // Add button/input type pseudos
    for (i in {
      radio: true,
      checkbox: true,
      file: true,
      password: true,
      image: true
    }) {
      Expr.pseudos[i] = createInputPseudo(i);
    }
    for (i in {
      submit: true,
      reset: true
    }) {
      Expr.pseudos[i] = createButtonPseudo(i);
    }

    // Easy API for creating new setFilters
    function setFilters() {}
    setFilters.prototype = Expr.filters = Expr.pseudos;
    Expr.setFilters = new setFilters();
    function tokenize(selector, parseOnly) {
      var matched,
        match,
        tokens,
        type,
        soFar,
        groups,
        preFilters,
        cached = tokenCache[selector + " "];
      if (cached) {
        return parseOnly ? 0 : cached.slice(0);
      }
      soFar = selector;
      groups = [];
      preFilters = Expr.preFilter;
      while (soFar) {
        // Comma and first run
        if (!matched || (match = rcomma.exec(soFar))) {
          if (match) {
            // Don't consume trailing commas as valid
            soFar = soFar.slice(match[0].length) || soFar;
          }
          groups.push(tokens = []);
        }
        matched = false;

        // Combinators
        if (match = rleadingCombinator.exec(soFar)) {
          matched = match.shift();
          tokens.push({
            value: matched,
            // Cast descendant combinators to space
            type: match[0].replace(rtrimCSS, " ")
          });
          soFar = soFar.slice(matched.length);
        }

        // Filters
        for (type in Expr.filter) {
          if ((match = matchExpr[type].exec(soFar)) && (!preFilters[type] || (match = preFilters[type](match)))) {
            matched = match.shift();
            tokens.push({
              value: matched,
              type: type,
              matches: match
            });
            soFar = soFar.slice(matched.length);
          }
        }
        if (!matched) {
          break;
        }
      }

      // Return the length of the invalid excess
      // if we're just parsing
      // Otherwise, throw an error or return tokens
      if (parseOnly) {
        return soFar.length;
      }
      return soFar ? find.error(selector) :
      // Cache the tokens
      tokenCache(selector, groups).slice(0);
    }
    function toSelector(tokens) {
      var i = 0,
        len = tokens.length,
        selector = "";
      for (; i < len; i++) {
        selector += tokens[i].value;
      }
      return selector;
    }
    function addCombinator(matcher, combinator, base) {
      var dir = combinator.dir,
        skip = combinator.next,
        key = skip || dir,
        checkNonElements = base && key === "parentNode",
        doneName = done++;
      return combinator.first ?
      // Check against closest ancestor/preceding element
      function (elem, context, xml) {
        while (elem = elem[dir]) {
          if (elem.nodeType === 1 || checkNonElements) {
            return matcher(elem, context, xml);
          }
        }
        return false;
      } :
      // Check against all ancestor/preceding elements
      function (elem, context, xml) {
        var oldCache,
          outerCache,
          newCache = [dirruns, doneName];

        // We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
        if (xml) {
          while (elem = elem[dir]) {
            if (elem.nodeType === 1 || checkNonElements) {
              if (matcher(elem, context, xml)) {
                return true;
              }
            }
          }
        } else {
          while (elem = elem[dir]) {
            if (elem.nodeType === 1 || checkNonElements) {
              outerCache = elem[expando] || (elem[expando] = {});
              if (skip && nodeName(elem, skip)) {
                elem = elem[dir] || elem;
              } else if ((oldCache = outerCache[key]) && oldCache[0] === dirruns && oldCache[1] === doneName) {
                // Assign to newCache so results back-propagate to previous elements
                return newCache[2] = oldCache[2];
              } else {
                // Reuse newcache so results back-propagate to previous elements
                outerCache[key] = newCache;

                // A match means we're done; a fail means we have to keep checking
                if (newCache[2] = matcher(elem, context, xml)) {
                  return true;
                }
              }
            }
          }
        }
        return false;
      };
    }
    function elementMatcher(matchers) {
      return matchers.length > 1 ? function (elem, context, xml) {
        var i = matchers.length;
        while (i--) {
          if (!matchers[i](elem, context, xml)) {
            return false;
          }
        }
        return true;
      } : matchers[0];
    }
    function multipleContexts(selector, contexts, results) {
      var i = 0,
        len = contexts.length;
      for (; i < len; i++) {
        find(selector, contexts[i], results);
      }
      return results;
    }
    function condense(unmatched, map, filter, context, xml) {
      var elem,
        newUnmatched = [],
        i = 0,
        len = unmatched.length,
        mapped = map != null;
      for (; i < len; i++) {
        if (elem = unmatched[i]) {
          if (!filter || filter(elem, context, xml)) {
            newUnmatched.push(elem);
            if (mapped) {
              map.push(i);
            }
          }
        }
      }
      return newUnmatched;
    }
    function setMatcher(preFilter, selector, matcher, postFilter, postFinder, postSelector) {
      if (postFilter && !postFilter[expando]) {
        postFilter = setMatcher(postFilter);
      }
      if (postFinder && !postFinder[expando]) {
        postFinder = setMatcher(postFinder, postSelector);
      }
      return markFunction(function (seed, results, context, xml) {
        var temp,
          i,
          elem,
          matcherOut,
          preMap = [],
          postMap = [],
          preexisting = results.length,
          // Get initial elements from seed or context
          elems = seed || multipleContexts(selector || "*", context.nodeType ? [context] : context, []),
          // Prefilter to get matcher input, preserving a map for seed-results synchronization
          matcherIn = preFilter && (seed || !selector) ? condense(elems, preMap, preFilter, context, xml) : elems;
        if (matcher) {
          // If we have a postFinder, or filtered seed, or non-seed postFilter
          // or preexisting results,
          matcherOut = postFinder || (seed ? preFilter : preexisting || postFilter) ?
          // ...intermediate processing is necessary
          [] :
          // ...otherwise use results directly
          results;

          // Find primary matches
          matcher(matcherIn, matcherOut, context, xml);
        } else {
          matcherOut = matcherIn;
        }

        // Apply postFilter
        if (postFilter) {
          temp = condense(matcherOut, postMap);
          postFilter(temp, [], context, xml);

          // Un-match failing elements by moving them back to matcherIn
          i = temp.length;
          while (i--) {
            if (elem = temp[i]) {
              matcherOut[postMap[i]] = !(matcherIn[postMap[i]] = elem);
            }
          }
        }
        if (seed) {
          if (postFinder || preFilter) {
            if (postFinder) {
              // Get the final matcherOut by condensing this intermediate into postFinder contexts
              temp = [];
              i = matcherOut.length;
              while (i--) {
                if (elem = matcherOut[i]) {
                  // Restore matcherIn since elem is not yet a final match
                  temp.push(matcherIn[i] = elem);
                }
              }
              postFinder(null, matcherOut = [], temp, xml);
            }

            // Move matched elements from seed to results to keep them synchronized
            i = matcherOut.length;
            while (i--) {
              if ((elem = matcherOut[i]) && (temp = postFinder ? indexOf.call(seed, elem) : preMap[i]) > -1) {
                seed[temp] = !(results[temp] = elem);
              }
            }
          }

          // Add elements to results, through postFinder if defined
        } else {
          matcherOut = condense(matcherOut === results ? matcherOut.splice(preexisting, matcherOut.length) : matcherOut);
          if (postFinder) {
            postFinder(null, results, matcherOut, xml);
          } else {
            push.apply(results, matcherOut);
          }
        }
      });
    }
    function matcherFromTokens(tokens) {
      var checkContext,
        matcher,
        j,
        len = tokens.length,
        leadingRelative = Expr.relative[tokens[0].type],
        implicitRelative = leadingRelative || Expr.relative[" "],
        i = leadingRelative ? 1 : 0,
        // The foundational matcher ensures that elements are reachable from top-level context(s)
        matchContext = addCombinator(function (elem) {
          return elem === checkContext;
        }, implicitRelative, true),
        matchAnyContext = addCombinator(function (elem) {
          return indexOf.call(checkContext, elem) > -1;
        }, implicitRelative, true),
        matchers = [function (elem, context, xml) {
          // Support: IE 11+, Edge 17 - 18+
          // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
          // two documents; shallow comparisons work.
          // eslint-disable-next-line eqeqeq
          var ret = !leadingRelative && (xml || context != outermostContext) || ((checkContext = context).nodeType ? matchContext(elem, context, xml) : matchAnyContext(elem, context, xml));

          // Avoid hanging onto element
          // (see https://github.com/jquery/sizzle/issues/299)
          checkContext = null;
          return ret;
        }];
      for (; i < len; i++) {
        if (matcher = Expr.relative[tokens[i].type]) {
          matchers = [addCombinator(elementMatcher(matchers), matcher)];
        } else {
          matcher = Expr.filter[tokens[i].type].apply(null, tokens[i].matches);

          // Return special upon seeing a positional matcher
          if (matcher[expando]) {
            // Find the next relative operator (if any) for proper handling
            j = ++i;
            for (; j < len; j++) {
              if (Expr.relative[tokens[j].type]) {
                break;
              }
            }
            return setMatcher(i > 1 && elementMatcher(matchers), i > 1 && toSelector(
            // If the preceding token was a descendant combinator, insert an implicit any-element `*`
            tokens.slice(0, i - 1).concat({
              value: tokens[i - 2].type === " " ? "*" : ""
            })).replace(rtrimCSS, "$1"), matcher, i < j && matcherFromTokens(tokens.slice(i, j)), j < len && matcherFromTokens(tokens = tokens.slice(j)), j < len && toSelector(tokens));
          }
          matchers.push(matcher);
        }
      }
      return elementMatcher(matchers);
    }
    function matcherFromGroupMatchers(elementMatchers, setMatchers) {
      var bySet = setMatchers.length > 0,
        byElement = elementMatchers.length > 0,
        superMatcher = function (seed, context, xml, results, outermost) {
          var elem,
            j,
            matcher,
            matchedCount = 0,
            i = "0",
            unmatched = seed && [],
            setMatched = [],
            contextBackup = outermostContext,
            // We must always have either seed elements or outermost context
            elems = seed || byElement && Expr.find.TAG("*", outermost),
            // Use integer dirruns iff this is the outermost matcher
            dirrunsUnique = dirruns += contextBackup == null ? 1 : Math.random() || 0.1,
            len = elems.length;
          if (outermost) {
            // Support: IE 11+, Edge 17 - 18+
            // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
            // two documents; shallow comparisons work.
            // eslint-disable-next-line eqeqeq
            outermostContext = context == document || context || outermost;
          }

          // Add elements passing elementMatchers directly to results
          // Support: iOS <=7 - 9 only
          // Tolerate NodeList properties (IE: "length"; Safari: <number>) matching
          // elements by id. (see trac-14142)
          for (; i !== len && (elem = elems[i]) != null; i++) {
            if (byElement && elem) {
              j = 0;

              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
              // two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              if (!context && elem.ownerDocument != document) {
                setDocument(elem);
                xml = !documentIsHTML;
              }
              while (matcher = elementMatchers[j++]) {
                if (matcher(elem, context || document, xml)) {
                  push.call(results, elem);
                  break;
                }
              }
              if (outermost) {
                dirruns = dirrunsUnique;
              }
            }

            // Track unmatched elements for set filters
            if (bySet) {
              // They will have gone through all possible matchers
              if (elem = !matcher && elem) {
                matchedCount--;
              }

              // Lengthen the array for every element, matched or not
              if (seed) {
                unmatched.push(elem);
              }
            }
          }

          // `i` is now the count of elements visited above, and adding it to `matchedCount`
          // makes the latter nonnegative.
          matchedCount += i;

          // Apply set filters to unmatched elements
          // NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
          // equals `i`), unless we didn't visit _any_ elements in the above loop because we have
          // no element matchers and no seed.
          // Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
          // case, which will result in a "00" `matchedCount` that differs from `i` but is also
          // numerically zero.
          if (bySet && i !== matchedCount) {
            j = 0;
            while (matcher = setMatchers[j++]) {
              matcher(unmatched, setMatched, context, xml);
            }
            if (seed) {
              // Reintegrate element matches to eliminate the need for sorting
              if (matchedCount > 0) {
                while (i--) {
                  if (!(unmatched[i] || setMatched[i])) {
                    setMatched[i] = pop.call(results);
                  }
                }
              }

              // Discard index placeholder values to get only actual matches
              setMatched = condense(setMatched);
            }

            // Add matches to results
            push.apply(results, setMatched);

            // Seedless set matches succeeding multiple successful matchers stipulate sorting
            if (outermost && !seed && setMatched.length > 0 && matchedCount + setMatchers.length > 1) {
              jQuery.uniqueSort(results);
            }
          }

          // Override manipulation of globals by nested matchers
          if (outermost) {
            dirruns = dirrunsUnique;
            outermostContext = contextBackup;
          }
          return unmatched;
        };
      return bySet ? markFunction(superMatcher) : superMatcher;
    }
    function compile(selector, match /* Internal Use Only */) {
      var i,
        setMatchers = [],
        elementMatchers = [],
        cached = compilerCache[selector + " "];
      if (!cached) {
        // Generate a function of recursive functions that can be used to check each element
        if (!match) {
          match = tokenize(selector);
        }
        i = match.length;
        while (i--) {
          cached = matcherFromTokens(match[i]);
          if (cached[expando]) {
            setMatchers.push(cached);
          } else {
            elementMatchers.push(cached);
          }
        }

        // Cache the compiled function
        cached = compilerCache(selector, matcherFromGroupMatchers(elementMatchers, setMatchers));

        // Save selector and tokenization
        cached.selector = selector;
      }
      return cached;
    }

    /**
     * A low-level selection function that works with jQuery's compiled
     *  selector functions
     * @param {String|Function} selector A selector or a pre-compiled
     *  selector function built with jQuery selector compile
     * @param {Element} context
     * @param {Array} [results]
     * @param {Array} [seed] A set of elements to match against
     */
    function select(selector, context, results, seed) {
      var i,
        tokens,
        token,
        type,
        find,
        compiled = typeof selector === "function" && selector,
        match = !seed && tokenize(selector = compiled.selector || selector);
      results = results || [];

      // Try to minimize operations if there is only one selector in the list and no seed
      // (the latter of which guarantees us context)
      if (match.length === 1) {
        // Reduce context if the leading compound selector is an ID
        tokens = match[0] = match[0].slice(0);
        if (tokens.length > 2 && (token = tokens[0]).type === "ID" && context.nodeType === 9 && documentIsHTML && Expr.relative[tokens[1].type]) {
          context = (Expr.find.ID(token.matches[0].replace(runescape, funescape), context) || [])[0];
          if (!context) {
            return results;

            // Precompiled matchers will still verify ancestry, so step up a level
          } else if (compiled) {
            context = context.parentNode;
          }
          selector = selector.slice(tokens.shift().value.length);
        }

        // Fetch a seed set for right-to-left matching
        i = matchExpr.needsContext.test(selector) ? 0 : tokens.length;
        while (i--) {
          token = tokens[i];

          // Abort if we hit a combinator
          if (Expr.relative[type = token.type]) {
            break;
          }
          if (find = Expr.find[type]) {
            // Search, expanding context for leading sibling combinators
            if (seed = find(token.matches[0].replace(runescape, funescape), rsibling.test(tokens[0].type) && testContext(context.parentNode) || context)) {
              // If seed is empty or no tokens remain, we can return early
              tokens.splice(i, 1);
              selector = seed.length && toSelector(tokens);
              if (!selector) {
                push.apply(results, seed);
                return results;
              }
              break;
            }
          }
        }
      }

      // Compile and execute a filtering function if one is not provided
      // Provide `match` to avoid retokenization if we modified the selector above
      (compiled || compile(selector, match))(seed, context, !documentIsHTML, results, !context || rsibling.test(selector) && testContext(context.parentNode) || context);
      return results;
    }

    // One-time assignments

    // Support: Android <=4.0 - 4.1+
    // Sort stability
    support.sortStable = expando.split("").sort(sortOrder).join("") === expando;

    // Initialize against the default document
    setDocument();

    // Support: Android <=4.0 - 4.1+
    // Detached nodes confoundingly follow *each other*
    support.sortDetached = assert(function (el) {
      // Should return 1, but returns 4 (following)
      return el.compareDocumentPosition(document.createElement("fieldset")) & 1;
    });
    jQuery.find = find;

    // Deprecated
    jQuery.expr[":"] = jQuery.expr.pseudos;
    jQuery.unique = jQuery.uniqueSort;

    // These have always been private, but they used to be documented as part of
    // Sizzle so let's maintain them for now for backwards compatibility purposes.
    find.compile = compile;
    find.select = select;
    find.setDocument = setDocument;
    find.tokenize = tokenize;
    find.escape = jQuery.escapeSelector;
    find.getText = jQuery.text;
    find.isXML = jQuery.isXMLDoc;
    find.selectors = jQuery.expr;
    find.support = jQuery.support;
    find.uniqueSort = jQuery.uniqueSort;

    /* eslint-enable */
  })();
  var dir = function (elem, dir, until) {
    var matched = [],
      truncate = until !== undefined;
    while ((elem = elem[dir]) && elem.nodeType !== 9) {
      if (elem.nodeType === 1) {
        if (truncate && jQuery(elem).is(until)) {
          break;
        }
        matched.push(elem);
      }
    }
    return matched;
  };
  var siblings = function (n, elem) {
    var matched = [];
    for (; n; n = n.nextSibling) {
      if (n.nodeType === 1 && n !== elem) {
        matched.push(n);
      }
    }
    return matched;
  };
  var rneedsContext = jQuery.expr.match.needsContext;
  var rsingleTag = /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i;

  // Implement the identical functionality for filter and not
  function winnow(elements, qualifier, not) {
    if (isFunction(qualifier)) {
      return jQuery.grep(elements, function (elem, i) {
        return !!qualifier.call(elem, i, elem) !== not;
      });
    }

    // Single element
    if (qualifier.nodeType) {
      return jQuery.grep(elements, function (elem) {
        return elem === qualifier !== not;
      });
    }

    // Arraylike of elements (jQuery, arguments, Array)
    if (typeof qualifier !== "string") {
      return jQuery.grep(elements, function (elem) {
        return indexOf.call(qualifier, elem) > -1 !== not;
      });
    }

    // Filtered directly for both simple and complex selectors
    return jQuery.filter(qualifier, elements, not);
  }
  jQuery.filter = function (expr, elems, not) {
    var elem = elems[0];
    if (not) {
      expr = ":not(" + expr + ")";
    }
    if (elems.length === 1 && elem.nodeType === 1) {
      return jQuery.find.matchesSelector(elem, expr) ? [elem] : [];
    }
    return jQuery.find.matches(expr, jQuery.grep(elems, function (elem) {
      return elem.nodeType === 1;
    }));
  };
  jQuery.fn.extend({
    find: function (selector) {
      var i,
        ret,
        len = this.length,
        self = this;
      if (typeof selector !== "string") {
        return this.pushStack(jQuery(selector).filter(function () {
          for (i = 0; i < len; i++) {
            if (jQuery.contains(self[i], this)) {
              return true;
            }
          }
        }));
      }
      ret = this.pushStack([]);
      for (i = 0; i < len; i++) {
        jQuery.find(selector, self[i], ret);
      }
      return len > 1 ? jQuery.uniqueSort(ret) : ret;
    },
    filter: function (selector) {
      return this.pushStack(winnow(this, selector || [], false));
    },
    not: function (selector) {
      return this.pushStack(winnow(this, selector || [], true));
    },
    is: function (selector) {
      return !!winnow(this,
      // If this is a positional/relative selector, check membership in the returned set
      // so $("p:first").is("p:last") won't return true for a doc with two "p".
      typeof selector === "string" && rneedsContext.test(selector) ? jQuery(selector) : selector || [], false).length;
    }
  });

  // Initialize a jQuery object

  // A central reference to the root jQuery(document)
  var rootjQuery,
    // A simple way to check for HTML strings
    // Prioritize #id over <tag> to avoid XSS via location.hash (trac-9521)
    // Strict HTML recognition (trac-11290: must start with <)
    // Shortcut simple #id case for speed
    rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,
    init = jQuery.fn.init = function (selector, context, root) {
      var match, elem;

      // HANDLE: $(""), $(null), $(undefined), $(false)
      if (!selector) {
        return this;
      }

      // Method init() accepts an alternate rootjQuery
      // so migrate can support jQuery.sub (gh-2101)
      root = root || rootjQuery;

      // Handle HTML strings
      if (typeof selector === "string") {
        if (selector[0] === "<" && selector[selector.length - 1] === ">" && selector.length >= 3) {
          // Assume that strings that start and end with <> are HTML and skip the regex check
          match = [null, selector, null];
        } else {
          match = rquickExpr.exec(selector);
        }

        // Match html or make sure no context is specified for #id
        if (match && (match[1] || !context)) {
          // HANDLE: $(html) -> $(array)
          if (match[1]) {
            context = context instanceof jQuery ? context[0] : context;

            // Option to run scripts is true for back-compat
            // Intentionally let the error be thrown if parseHTML is not present
            jQuery.merge(this, jQuery.parseHTML(match[1], context && context.nodeType ? context.ownerDocument || context : document, true));

            // HANDLE: $(html, props)
            if (rsingleTag.test(match[1]) && jQuery.isPlainObject(context)) {
              for (match in context) {
                // Properties of context are called as methods if possible
                if (isFunction(this[match])) {
                  this[match](context[match]);

                  // ...and otherwise set as attributes
                } else {
                  this.attr(match, context[match]);
                }
              }
            }
            return this;

            // HANDLE: $(#id)
          } else {
            elem = document.getElementById(match[2]);
            if (elem) {
              // Inject the element directly into the jQuery object
              this[0] = elem;
              this.length = 1;
            }
            return this;
          }

          // HANDLE: $(expr, $(...))
        } else if (!context || context.jquery) {
          return (context || root).find(selector);

          // HANDLE: $(expr, context)
          // (which is just equivalent to: $(context).find(expr)
        } else {
          return this.constructor(context).find(selector);
        }

        // HANDLE: $(DOMElement)
      } else if (selector.nodeType) {
        this[0] = selector;
        this.length = 1;
        return this;

        // HANDLE: $(function)
        // Shortcut for document ready
      } else if (isFunction(selector)) {
        return root.ready !== undefined ? root.ready(selector) :
        // Execute immediately if ready is not present
        selector(jQuery);
      }
      return jQuery.makeArray(selector, this);
    };

  // Give the init function the jQuery prototype for later instantiation
  init.prototype = jQuery.fn;

  // Initialize central reference
  rootjQuery = jQuery(document);
  var rparentsprev = /^(?:parents|prev(?:Until|All))/,
    // Methods guaranteed to produce a unique set when starting from a unique set
    guaranteedUnique = {
      children: true,
      contents: true,
      next: true,
      prev: true
    };
  jQuery.fn.extend({
    has: function (target) {
      var targets = jQuery(target, this),
        l = targets.length;
      return this.filter(function () {
        var i = 0;
        for (; i < l; i++) {
          if (jQuery.contains(this, targets[i])) {
            return true;
          }
        }
      });
    },
    closest: function (selectors, context) {
      var cur,
        i = 0,
        l = this.length,
        matched = [],
        targets = typeof selectors !== "string" && jQuery(selectors);

      // Positional selectors never match, since there's no _selection_ context
      if (!rneedsContext.test(selectors)) {
        for (; i < l; i++) {
          for (cur = this[i]; cur && cur !== context; cur = cur.parentNode) {
            // Always skip document fragments
            if (cur.nodeType < 11 && (targets ? targets.index(cur) > -1 :
            // Don't pass non-elements to jQuery#find
            cur.nodeType === 1 && jQuery.find.matchesSelector(cur, selectors))) {
              matched.push(cur);
              break;
            }
          }
        }
      }
      return this.pushStack(matched.length > 1 ? jQuery.uniqueSort(matched) : matched);
    },
    // Determine the position of an element within the set
    index: function (elem) {
      // No argument, return index in parent
      if (!elem) {
        return this[0] && this[0].parentNode ? this.first().prevAll().length : -1;
      }

      // Index in selector
      if (typeof elem === "string") {
        return indexOf.call(jQuery(elem), this[0]);
      }

      // Locate the position of the desired element
      return indexOf.call(this,
      // If it receives a jQuery object, the first element is used
      elem.jquery ? elem[0] : elem);
    },
    add: function (selector, context) {
      return this.pushStack(jQuery.uniqueSort(jQuery.merge(this.get(), jQuery(selector, context))));
    },
    addBack: function (selector) {
      return this.add(selector == null ? this.prevObject : this.prevObject.filter(selector));
    }
  });
  function sibling(cur, dir) {
    while ((cur = cur[dir]) && cur.nodeType !== 1) {}
    return cur;
  }
  jQuery.each({
    parent: function (elem) {
      var parent = elem.parentNode;
      return parent && parent.nodeType !== 11 ? parent : null;
    },
    parents: function (elem) {
      return dir(elem, "parentNode");
    },
    parentsUntil: function (elem, _i, until) {
      return dir(elem, "parentNode", until);
    },
    next: function (elem) {
      return sibling(elem, "nextSibling");
    },
    prev: function (elem) {
      return sibling(elem, "previousSibling");
    },
    nextAll: function (elem) {
      return dir(elem, "nextSibling");
    },
    prevAll: function (elem) {
      return dir(elem, "previousSibling");
    },
    nextUntil: function (elem, _i, until) {
      return dir(elem, "nextSibling", until);
    },
    prevUntil: function (elem, _i, until) {
      return dir(elem, "previousSibling", until);
    },
    siblings: function (elem) {
      return siblings((elem.parentNode || {}).firstChild, elem);
    },
    children: function (elem) {
      return siblings(elem.firstChild);
    },
    contents: function (elem) {
      if (elem.contentDocument != null &&
      // Support: IE 11+
      // <object> elements with no `data` attribute has an object
      // `contentDocument` with a `null` prototype.
      getProto(elem.contentDocument)) {
        return elem.contentDocument;
      }

      // Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
      // Treat the template element as a regular one in browsers that
      // don't support it.
      if (nodeName(elem, "template")) {
        elem = elem.content || elem;
      }
      return jQuery.merge([], elem.childNodes);
    }
  }, function (name, fn) {
    jQuery.fn[name] = function (until, selector) {
      var matched = jQuery.map(this, fn, until);
      if (name.slice(-5) !== "Until") {
        selector = until;
      }
      if (selector && typeof selector === "string") {
        matched = jQuery.filter(selector, matched);
      }
      if (this.length > 1) {
        // Remove duplicates
        if (!guaranteedUnique[name]) {
          jQuery.uniqueSort(matched);
        }

        // Reverse order for parents* and prev-derivatives
        if (rparentsprev.test(name)) {
          matched.reverse();
        }
      }
      return this.pushStack(matched);
    };
  });
  var rnothtmlwhite = /[^\x20\t\r\n\f]+/g;

  // Convert String-formatted options into Object-formatted ones
  function createOptions(options) {
    var object = {};
    jQuery.each(options.match(rnothtmlwhite) || [], function (_, flag) {
      object[flag] = true;
    });
    return object;
  }

  /*
   * Create a callback list using the following parameters:
   *
   *	options: an optional list of space-separated options that will change how
   *			the callback list behaves or a more traditional option object
   *
   * By default a callback list will act like an event callback list and can be
   * "fired" multiple times.
   *
   * Possible options:
   *
   *	once:			will ensure the callback list can only be fired once (like a Deferred)
   *
   *	memory:			will keep track of previous values and will call any callback added
   *					after the list has been fired right away with the latest "memorized"
   *					values (like a Deferred)
   *
   *	unique:			will ensure a callback can only be added once (no duplicate in the list)
   *
   *	stopOnFalse:	interrupt callings when a callback returns false
   *
   */
  jQuery.Callbacks = function (options) {
    // Convert options from String-formatted to Object-formatted if needed
    // (we check in cache first)
    options = typeof options === "string" ? createOptions(options) : jQuery.extend({}, options);
    var
      // Flag to know if list is currently firing
      firing,
      // Last fire value for non-forgettable lists
      memory,
      // Flag to know if list was already fired
      fired,
      // Flag to prevent firing
      locked,
      // Actual callback list
      list = [],
      // Queue of execution data for repeatable lists
      queue = [],
      // Index of currently firing callback (modified by add/remove as needed)
      firingIndex = -1,
      // Fire callbacks
      fire = function () {
        // Enforce single-firing
        locked = locked || options.once;

        // Execute callbacks for all pending executions,
        // respecting firingIndex overrides and runtime changes
        fired = firing = true;
        for (; queue.length; firingIndex = -1) {
          memory = queue.shift();
          while (++firingIndex < list.length) {
            // Run callback and check for early termination
            if (list[firingIndex].apply(memory[0], memory[1]) === false && options.stopOnFalse) {
              // Jump to end and forget the data so .add doesn't re-fire
              firingIndex = list.length;
              memory = false;
            }
          }
        }

        // Forget the data if we're done with it
        if (!options.memory) {
          memory = false;
        }
        firing = false;

        // Clean up if we're done firing for good
        if (locked) {
          // Keep an empty list if we have data for future add calls
          if (memory) {
            list = [];

            // Otherwise, this object is spent
          } else {
            list = "";
          }
        }
      },
      // Actual Callbacks object
      self = {
        // Add a callback or a collection of callbacks to the list
        add: function () {
          if (list) {
            // If we have memory from a past run, we should fire after adding
            if (memory && !firing) {
              firingIndex = list.length - 1;
              queue.push(memory);
            }
            (function add(args) {
              jQuery.each(args, function (_, arg) {
                if (isFunction(arg)) {
                  if (!options.unique || !self.has(arg)) {
                    list.push(arg);
                  }
                } else if (arg && arg.length && toType(arg) !== "string") {
                  // Inspect recursively
                  add(arg);
                }
              });
            })(arguments);
            if (memory && !firing) {
              fire();
            }
          }
          return this;
        },
        // Remove a callback from the list
        remove: function () {
          jQuery.each(arguments, function (_, arg) {
            var index;
            while ((index = jQuery.inArray(arg, list, index)) > -1) {
              list.splice(index, 1);

              // Handle firing indexes
              if (index <= firingIndex) {
                firingIndex--;
              }
            }
          });
          return this;
        },
        // Check if a given callback is in the list.
        // If no argument is given, return whether or not list has callbacks attached.
        has: function (fn) {
          return fn ? jQuery.inArray(fn, list) > -1 : list.length > 0;
        },
        // Remove all callbacks from the list
        empty: function () {
          if (list) {
            list = [];
          }
          return this;
        },
        // Disable .fire and .add
        // Abort any current/pending executions
        // Clear all callbacks and values
        disable: function () {
          locked = queue = [];
          list = memory = "";
          return this;
        },
        disabled: function () {
          return !list;
        },
        // Disable .fire
        // Also disable .add unless we have memory (since it would have no effect)
        // Abort any pending executions
        lock: function () {
          locked = queue = [];
          if (!memory && !firing) {
            list = memory = "";
          }
          return this;
        },
        locked: function () {
          return !!locked;
        },
        // Call all callbacks with the given context and arguments
        fireWith: function (context, args) {
          if (!locked) {
            args = args || [];
            args = [context, args.slice ? args.slice() : args];
            queue.push(args);
            if (!firing) {
              fire();
            }
          }
          return this;
        },
        // Call all the callbacks with the given arguments
        fire: function () {
          self.fireWith(this, arguments);
          return this;
        },
        // To know if the callbacks have already been called at least once
        fired: function () {
          return !!fired;
        }
      };
    return self;
  };
  function Identity(v) {
    return v;
  }
  function Thrower(ex) {
    throw ex;
  }
  function adoptValue(value, resolve, reject, noValue) {
    var method;
    try {
      // Check for promise aspect first to privilege synchronous behavior
      if (value && isFunction(method = value.promise)) {
        method.call(value).done(resolve).fail(reject);

        // Other thenables
      } else if (value && isFunction(method = value.then)) {
        method.call(value, resolve, reject);

        // Other non-thenables
      } else {
        // Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
        // * false: [ value ].slice( 0 ) => resolve( value )
        // * true: [ value ].slice( 1 ) => resolve()
        resolve.apply(undefined, [value].slice(noValue));
      }

      // For Promises/A+, convert exceptions into rejections
      // Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
      // Deferred#then to conditionally suppress rejection.
    } catch (value) {
      // Support: Android 4.0 only
      // Strict mode functions invoked without .call/.apply get global-object context
      reject.apply(undefined, [value]);
    }
  }
  jQuery.extend({
    Deferred: function (func) {
      var tuples = [
        // action, add listener, callbacks,
        // ... .then handlers, argument index, [final state]
        ["notify", "progress", jQuery.Callbacks("memory"), jQuery.Callbacks("memory"), 2], ["resolve", "done", jQuery.Callbacks("once memory"), jQuery.Callbacks("once memory"), 0, "resolved"], ["reject", "fail", jQuery.Callbacks("once memory"), jQuery.Callbacks("once memory"), 1, "rejected"]],
        state = "pending",
        promise = {
          state: function () {
            return state;
          },
          always: function () {
            deferred.done(arguments).fail(arguments);
            return this;
          },
          "catch": function (fn) {
            return promise.then(null, fn);
          },
          // Keep pipe for back-compat
          pipe: function /* fnDone, fnFail, fnProgress */
          () {
            var fns = arguments;
            return jQuery.Deferred(function (newDefer) {
              jQuery.each(tuples, function (_i, tuple) {
                // Map tuples (progress, done, fail) to arguments (done, fail, progress)
                var fn = isFunction(fns[tuple[4]]) && fns[tuple[4]];

                // deferred.progress(function() { bind to newDefer or newDefer.notify })
                // deferred.done(function() { bind to newDefer or newDefer.resolve })
                // deferred.fail(function() { bind to newDefer or newDefer.reject })
                deferred[tuple[1]](function () {
                  var returned = fn && fn.apply(this, arguments);
                  if (returned && isFunction(returned.promise)) {
                    returned.promise().progress(newDefer.notify).done(newDefer.resolve).fail(newDefer.reject);
                  } else {
                    newDefer[tuple[0] + "With"](this, fn ? [returned] : arguments);
                  }
                });
              });
              fns = null;
            }).promise();
          },
          then: function (onFulfilled, onRejected, onProgress) {
            var maxDepth = 0;
            function resolve(depth, deferred, handler, special) {
              return function () {
                var that = this,
                  args = arguments,
                  mightThrow = function () {
                    var returned, then;

                    // Support: Promises/A+ section 2.3.3.3.3
                    // https://promisesaplus.com/#point-59
                    // Ignore double-resolution attempts
                    if (depth < maxDepth) {
                      return;
                    }
                    returned = handler.apply(that, args);

                    // Support: Promises/A+ section 2.3.1
                    // https://promisesaplus.com/#point-48
                    if (returned === deferred.promise()) {
                      throw new TypeError("Thenable self-resolution");
                    }

                    // Support: Promises/A+ sections 2.3.3.1, 3.5
                    // https://promisesaplus.com/#point-54
                    // https://promisesaplus.com/#point-75
                    // Retrieve `then` only once
                    then = returned && (
                    // Support: Promises/A+ section 2.3.4
                    // https://promisesaplus.com/#point-64
                    // Only check objects and functions for thenability
                    typeof returned === "object" || typeof returned === "function") && returned.then;

                    // Handle a returned thenable
                    if (isFunction(then)) {
                      // Special processors (notify) just wait for resolution
                      if (special) {
                        then.call(returned, resolve(maxDepth, deferred, Identity, special), resolve(maxDepth, deferred, Thrower, special));

                        // Normal processors (resolve) also hook into progress
                      } else {
                        // ...and disregard older resolution values
                        maxDepth++;
                        then.call(returned, resolve(maxDepth, deferred, Identity, special), resolve(maxDepth, deferred, Thrower, special), resolve(maxDepth, deferred, Identity, deferred.notifyWith));
                      }

                      // Handle all other returned values
                    } else {
                      // Only substitute handlers pass on context
                      // and multiple values (non-spec behavior)
                      if (handler !== Identity) {
                        that = undefined;
                        args = [returned];
                      }

                      // Process the value(s)
                      // Default process is resolve
                      (special || deferred.resolveWith)(that, args);
                    }
                  },
                  // Only normal processors (resolve) catch and reject exceptions
                  process = special ? mightThrow : function () {
                    try {
                      mightThrow();
                    } catch (e) {
                      if (jQuery.Deferred.exceptionHook) {
                        jQuery.Deferred.exceptionHook(e, process.error);
                      }

                      // Support: Promises/A+ section 2.3.3.3.4.1
                      // https://promisesaplus.com/#point-61
                      // Ignore post-resolution exceptions
                      if (depth + 1 >= maxDepth) {
                        // Only substitute handlers pass on context
                        // and multiple values (non-spec behavior)
                        if (handler !== Thrower) {
                          that = undefined;
                          args = [e];
                        }
                        deferred.rejectWith(that, args);
                      }
                    }
                  };

                // Support: Promises/A+ section 2.3.3.3.1
                // https://promisesaplus.com/#point-57
                // Re-resolve promises immediately to dodge false rejection from
                // subsequent errors
                if (depth) {
                  process();
                } else {
                  // Call an optional hook to record the error, in case of exception
                  // since it's otherwise lost when execution goes async
                  if (jQuery.Deferred.getErrorHook) {
                    process.error = jQuery.Deferred.getErrorHook();

                    // The deprecated alias of the above. While the name suggests
                    // returning the stack, not an error instance, jQuery just passes
                    // it directly to `console.warn` so both will work; an instance
                    // just better cooperates with source maps.
                  } else if (jQuery.Deferred.getStackHook) {
                    process.error = jQuery.Deferred.getStackHook();
                  }
                  window.setTimeout(process);
                }
              };
            }
            return jQuery.Deferred(function (newDefer) {
              // progress_handlers.add( ... )
              tuples[0][3].add(resolve(0, newDefer, isFunction(onProgress) ? onProgress : Identity, newDefer.notifyWith));

              // fulfilled_handlers.add( ... )
              tuples[1][3].add(resolve(0, newDefer, isFunction(onFulfilled) ? onFulfilled : Identity));

              // rejected_handlers.add( ... )
              tuples[2][3].add(resolve(0, newDefer, isFunction(onRejected) ? onRejected : Thrower));
            }).promise();
          },
          // Get a promise for this deferred
          // If obj is provided, the promise aspect is added to the object
          promise: function (obj) {
            return obj != null ? jQuery.extend(obj, promise) : promise;
          }
        },
        deferred = {};

      // Add list-specific methods
      jQuery.each(tuples, function (i, tuple) {
        var list = tuple[2],
          stateString = tuple[5];

        // promise.progress = list.add
        // promise.done = list.add
        // promise.fail = list.add
        promise[tuple[1]] = list.add;

        // Handle state
        if (stateString) {
          list.add(function () {
            // state = "resolved" (i.e., fulfilled)
            // state = "rejected"
            state = stateString;
          },
          // rejected_callbacks.disable
          // fulfilled_callbacks.disable
          tuples[3 - i][2].disable,
          // rejected_handlers.disable
          // fulfilled_handlers.disable
          tuples[3 - i][3].disable,
          // progress_callbacks.lock
          tuples[0][2].lock,
          // progress_handlers.lock
          tuples[0][3].lock);
        }

        // progress_handlers.fire
        // fulfilled_handlers.fire
        // rejected_handlers.fire
        list.add(tuple[3].fire);

        // deferred.notify = function() { deferred.notifyWith(...) }
        // deferred.resolve = function() { deferred.resolveWith(...) }
        // deferred.reject = function() { deferred.rejectWith(...) }
        deferred[tuple[0]] = function () {
          deferred[tuple[0] + "With"](this === deferred ? undefined : this, arguments);
          return this;
        };

        // deferred.notifyWith = list.fireWith
        // deferred.resolveWith = list.fireWith
        // deferred.rejectWith = list.fireWith
        deferred[tuple[0] + "With"] = list.fireWith;
      });

      // Make the deferred a promise
      promise.promise(deferred);

      // Call given func if any
      if (func) {
        func.call(deferred, deferred);
      }

      // All done!
      return deferred;
    },
    // Deferred helper
    when: function (singleValue) {
      var
        // count of uncompleted subordinates
        remaining = arguments.length,
        // count of unprocessed arguments
        i = remaining,
        // subordinate fulfillment data
        resolveContexts = Array(i),
        resolveValues = slice.call(arguments),
        // the primary Deferred
        primary = jQuery.Deferred(),
        // subordinate callback factory
        updateFunc = function (i) {
          return function (value) {
            resolveContexts[i] = this;
            resolveValues[i] = arguments.length > 1 ? slice.call(arguments) : value;
            if (! --remaining) {
              primary.resolveWith(resolveContexts, resolveValues);
            }
          };
        };

      // Single- and empty arguments are adopted like Promise.resolve
      if (remaining <= 1) {
        adoptValue(singleValue, primary.done(updateFunc(i)).resolve, primary.reject, !remaining);

        // Use .then() to unwrap secondary thenables (cf. gh-3000)
        if (primary.state() === "pending" || isFunction(resolveValues[i] && resolveValues[i].then)) {
          return primary.then();
        }
      }

      // Multiple arguments are aggregated like Promise.all array elements
      while (i--) {
        adoptValue(resolveValues[i], updateFunc(i), primary.reject);
      }
      return primary.promise();
    }
  });

  // These usually indicate a programmer mistake during development,
  // warn about them ASAP rather than swallowing them by default.
  var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

  // If `jQuery.Deferred.getErrorHook` is defined, `asyncError` is an error
  // captured before the async barrier to get the original error cause
  // which may otherwise be hidden.
  jQuery.Deferred.exceptionHook = function (error, asyncError) {
    // Support: IE 8 - 9 only
    // Console exists when dev tools are open, which can happen at any time
    if (window.console && window.console.warn && error && rerrorNames.test(error.name)) {
      window.console.warn("jQuery.Deferred exception: " + error.message, error.stack, asyncError);
    }
  };
  jQuery.readyException = function (error) {
    window.setTimeout(function () {
      throw error;
    });
  };

  // The deferred used on DOM ready
  var readyList = jQuery.Deferred();
  jQuery.fn.ready = function (fn) {
    readyList.then(fn)

    // Wrap jQuery.readyException in a function so that the lookup
    // happens at the time of error handling instead of callback
    // registration.
    .catch(function (error) {
      jQuery.readyException(error);
    });
    return this;
  };
  jQuery.extend({
    // Is the DOM ready to be used? Set to true once it occurs.
    isReady: false,
    // A counter to track how many items to wait for before
    // the ready event fires. See trac-6781
    readyWait: 1,
    // Handle when the DOM is ready
    ready: function (wait) {
      // Abort if there are pending holds or we're already ready
      if (wait === true ? --jQuery.readyWait : jQuery.isReady) {
        return;
      }

      // Remember that the DOM is ready
      jQuery.isReady = true;

      // If a normal DOM Ready event fired, decrement, and wait if need be
      if (wait !== true && --jQuery.readyWait > 0) {
        return;
      }

      // If there are functions bound, to execute
      readyList.resolveWith(document, [jQuery]);
    }
  });
  jQuery.ready.then = readyList.then;

  // The ready event handler and self cleanup method
  function completed() {
    document.removeEventListener("DOMContentLoaded", completed);
    window.removeEventListener("load", completed);
    jQuery.ready();
  }

  // Catch cases where $(document).ready() is called
  // after the browser event has already occurred.
  // Support: IE <=9 - 10 only
  // Older IE sometimes signals "interactive" too soon
  if (document.readyState === "complete" || document.readyState !== "loading" && !document.documentElement.doScroll) {
    // Handle it asynchronously to allow scripts the opportunity to delay ready
    window.setTimeout(jQuery.ready);
  } else {
    // Use the handy event callback
    document.addEventListener("DOMContentLoaded", completed);

    // A fallback to window.onload, that will always work
    window.addEventListener("load", completed);
  }

  // Multifunctional method to get and set values of a collection
  // The value/s can optionally be executed if it's a function
  var access = function (elems, fn, key, value, chainable, emptyGet, raw) {
    var i = 0,
      len = elems.length,
      bulk = key == null;

    // Sets many values
    if (toType(key) === "object") {
      chainable = true;
      for (i in key) {
        access(elems, fn, i, key[i], true, emptyGet, raw);
      }

      // Sets one value
    } else if (value !== undefined) {
      chainable = true;
      if (!isFunction(value)) {
        raw = true;
      }
      if (bulk) {
        // Bulk operations run against the entire set
        if (raw) {
          fn.call(elems, value);
          fn = null;

          // ...except when executing function values
        } else {
          bulk = fn;
          fn = function (elem, _key, value) {
            return bulk.call(jQuery(elem), value);
          };
        }
      }
      if (fn) {
        for (; i < len; i++) {
          fn(elems[i], key, raw ? value : value.call(elems[i], i, fn(elems[i], key)));
        }
      }
    }
    if (chainable) {
      return elems;
    }

    // Gets
    if (bulk) {
      return fn.call(elems);
    }
    return len ? fn(elems[0], key) : emptyGet;
  };

  // Matches dashed string for camelizing
  var rmsPrefix = /^-ms-/,
    rdashAlpha = /-([a-z])/g;

  // Used by camelCase as callback to replace()
  function fcamelCase(_all, letter) {
    return letter.toUpperCase();
  }

  // Convert dashed to camelCase; used by the css and data modules
  // Support: IE <=9 - 11, Edge 12 - 15
  // Microsoft forgot to hump their vendor prefix (trac-9572)
  function camelCase(string) {
    return string.replace(rmsPrefix, "ms-").replace(rdashAlpha, fcamelCase);
  }
  var acceptData = function (owner) {
    // Accepts only:
    //  - Node
    //    - Node.ELEMENT_NODE
    //    - Node.DOCUMENT_NODE
    //  - Object
    //    - Any
    return owner.nodeType === 1 || owner.nodeType === 9 || !+owner.nodeType;
  };
  function Data() {
    this.expando = jQuery.expando + Data.uid++;
  }
  Data.uid = 1;
  Data.prototype = {
    cache: function (owner) {
      // Check if the owner object already has a cache
      var value = owner[this.expando];

      // If not, create one
      if (!value) {
        value = {};

        // We can accept data for non-element nodes in modern browsers,
        // but we should not, see trac-8335.
        // Always return an empty object.
        if (acceptData(owner)) {
          // If it is a node unlikely to be stringify-ed or looped over
          // use plain assignment
          if (owner.nodeType) {
            owner[this.expando] = value;

            // Otherwise secure it in a non-enumerable property
            // configurable must be true to allow the property to be
            // deleted when data is removed
          } else {
            Object.defineProperty(owner, this.expando, {
              value: value,
              configurable: true
            });
          }
        }
      }
      return value;
    },
    set: function (owner, data, value) {
      var prop,
        cache = this.cache(owner);

      // Handle: [ owner, key, value ] args
      // Always use camelCase key (gh-2257)
      if (typeof data === "string") {
        cache[camelCase(data)] = value;

        // Handle: [ owner, { properties } ] args
      } else {
        // Copy the properties one-by-one to the cache object
        for (prop in data) {
          cache[camelCase(prop)] = data[prop];
        }
      }
      return cache;
    },
    get: function (owner, key) {
      return key === undefined ? this.cache(owner) :
      // Always use camelCase key (gh-2257)
      owner[this.expando] && owner[this.expando][camelCase(key)];
    },
    access: function (owner, key, value) {
      // In cases where either:
      //
      //   1. No key was specified
      //   2. A string key was specified, but no value provided
      //
      // Take the "read" path and allow the get method to determine
      // which value to return, respectively either:
      //
      //   1. The entire cache object
      //   2. The data stored at the key
      //
      if (key === undefined || key && typeof key === "string" && value === undefined) {
        return this.get(owner, key);
      }

      // When the key is not a string, or both a key and value
      // are specified, set or extend (existing objects) with either:
      //
      //   1. An object of properties
      //   2. A key and value
      //
      this.set(owner, key, value);

      // Since the "set" path can have two possible entry points
      // return the expected data based on which path was taken[*]
      return value !== undefined ? value : key;
    },
    remove: function (owner, key) {
      var i,
        cache = owner[this.expando];
      if (cache === undefined) {
        return;
      }
      if (key !== undefined) {
        // Support array or space separated string of keys
        if (Array.isArray(key)) {
          // If key is an array of keys...
          // We always set camelCase keys, so remove that.
          key = key.map(camelCase);
        } else {
          key = camelCase(key);

          // If a key with the spaces exists, use it.
          // Otherwise, create an array by matching non-whitespace
          key = key in cache ? [key] : key.match(rnothtmlwhite) || [];
        }
        i = key.length;
        while (i--) {
          delete cache[key[i]];
        }
      }

      // Remove the expando if there's no more data
      if (key === undefined || jQuery.isEmptyObject(cache)) {
        // Support: Chrome <=35 - 45
        // Webkit & Blink performance suffers when deleting properties
        // from DOM nodes, so set to undefined instead
        // https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
        if (owner.nodeType) {
          owner[this.expando] = undefined;
        } else {
          delete owner[this.expando];
        }
      }
    },
    hasData: function (owner) {
      var cache = owner[this.expando];
      return cache !== undefined && !jQuery.isEmptyObject(cache);
    }
  };
  var dataPriv = new Data();
  var dataUser = new Data();

  //	Implementation Summary
  //
  //	1. Enforce API surface and semantic compatibility with 1.9.x branch
  //	2. Improve the module's maintainability by reducing the storage
  //		paths to a single mechanism.
  //	3. Use the same single mechanism to support "private" and "user" data.
  //	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
  //	5. Avoid exposing implementation details on user objects (eg. expando properties)
  //	6. Provide a clear path for implementation upgrade to WeakMap in 2014

  var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
    rmultiDash = /[A-Z]/g;
  function getData(data) {
    if (data === "true") {
      return true;
    }
    if (data === "false") {
      return false;
    }
    if (data === "null") {
      return null;
    }

    // Only convert to a number if it doesn't change the string
    if (data === +data + "") {
      return +data;
    }
    if (rbrace.test(data)) {
      return JSON.parse(data);
    }
    return data;
  }
  function dataAttr(elem, key, data) {
    var name;

    // If nothing was found internally, try to fetch any
    // data from the HTML5 data-* attribute
    if (data === undefined && elem.nodeType === 1) {
      name = "data-" + key.replace(rmultiDash, "-$&").toLowerCase();
      data = elem.getAttribute(name);
      if (typeof data === "string") {
        try {
          data = getData(data);
        } catch (e) {}

        // Make sure we set the data so it isn't changed later
        dataUser.set(elem, key, data);
      } else {
        data = undefined;
      }
    }
    return data;
  }
  jQuery.extend({
    hasData: function (elem) {
      return dataUser.hasData(elem) || dataPriv.hasData(elem);
    },
    data: function (elem, name, data) {
      return dataUser.access(elem, name, data);
    },
    removeData: function (elem, name) {
      dataUser.remove(elem, name);
    },
    // TODO: Now that all calls to _data and _removeData have been replaced
    // with direct calls to dataPriv methods, these can be deprecated.
    _data: function (elem, name, data) {
      return dataPriv.access(elem, name, data);
    },
    _removeData: function (elem, name) {
      dataPriv.remove(elem, name);
    }
  });
  jQuery.fn.extend({
    data: function (key, value) {
      var i,
        name,
        data,
        elem = this[0],
        attrs = elem && elem.attributes;

      // Gets all values
      if (key === undefined) {
        if (this.length) {
          data = dataUser.get(elem);
          if (elem.nodeType === 1 && !dataPriv.get(elem, "hasDataAttrs")) {
            i = attrs.length;
            while (i--) {
              // Support: IE 11 only
              // The attrs elements can be null (trac-14894)
              if (attrs[i]) {
                name = attrs[i].name;
                if (name.indexOf("data-") === 0) {
                  name = camelCase(name.slice(5));
                  dataAttr(elem, name, data[name]);
                }
              }
            }
            dataPriv.set(elem, "hasDataAttrs", true);
          }
        }
        return data;
      }

      // Sets multiple values
      if (typeof key === "object") {
        return this.each(function () {
          dataUser.set(this, key);
        });
      }
      return access(this, function (value) {
        var data;

        // The calling jQuery object (element matches) is not empty
        // (and therefore has an element appears at this[ 0 ]) and the
        // `value` parameter was not undefined. An empty jQuery object
        // will result in `undefined` for elem = this[ 0 ] which will
        // throw an exception if an attempt to read a data cache is made.
        if (elem && value === undefined) {
          // Attempt to get data from the cache
          // The key will always be camelCased in Data
          data = dataUser.get(elem, key);
          if (data !== undefined) {
            return data;
          }

          // Attempt to "discover" the data in
          // HTML5 custom data-* attrs
          data = dataAttr(elem, key);
          if (data !== undefined) {
            return data;
          }

          // We tried really hard, but the data doesn't exist.
          return;
        }

        // Set the data...
        this.each(function () {
          // We always store the camelCased key
          dataUser.set(this, key, value);
        });
      }, null, value, arguments.length > 1, null, true);
    },
    removeData: function (key) {
      return this.each(function () {
        dataUser.remove(this, key);
      });
    }
  });
  jQuery.extend({
    queue: function (elem, type, data) {
      var queue;
      if (elem) {
        type = (type || "fx") + "queue";
        queue = dataPriv.get(elem, type);

        // Speed up dequeue by getting out quickly if this is just a lookup
        if (data) {
          if (!queue || Array.isArray(data)) {
            queue = dataPriv.access(elem, type, jQuery.makeArray(data));
          } else {
            queue.push(data);
          }
        }
        return queue || [];
      }
    },
    dequeue: function (elem, type) {
      type = type || "fx";
      var queue = jQuery.queue(elem, type),
        startLength = queue.length,
        fn = queue.shift(),
        hooks = jQuery._queueHooks(elem, type),
        next = function () {
          jQuery.dequeue(elem, type);
        };

      // If the fx queue is dequeued, always remove the progress sentinel
      if (fn === "inprogress") {
        fn = queue.shift();
        startLength--;
      }
      if (fn) {
        // Add a progress sentinel to prevent the fx queue from being
        // automatically dequeued
        if (type === "fx") {
          queue.unshift("inprogress");
        }

        // Clear up the last queue stop function
        delete hooks.stop;
        fn.call(elem, next, hooks);
      }
      if (!startLength && hooks) {
        hooks.empty.fire();
      }
    },
    // Not public - generate a queueHooks object, or return the current one
    _queueHooks: function (elem, type) {
      var key = type + "queueHooks";
      return dataPriv.get(elem, key) || dataPriv.access(elem, key, {
        empty: jQuery.Callbacks("once memory").add(function () {
          dataPriv.remove(elem, [type + "queue", key]);
        })
      });
    }
  });
  jQuery.fn.extend({
    queue: function (type, data) {
      var setter = 2;
      if (typeof type !== "string") {
        data = type;
        type = "fx";
        setter--;
      }
      if (arguments.length < setter) {
        return jQuery.queue(this[0], type);
      }
      return data === undefined ? this : this.each(function () {
        var queue = jQuery.queue(this, type, data);

        // Ensure a hooks for this queue
        jQuery._queueHooks(this, type);
        if (type === "fx" && queue[0] !== "inprogress") {
          jQuery.dequeue(this, type);
        }
      });
    },
    dequeue: function (type) {
      return this.each(function () {
        jQuery.dequeue(this, type);
      });
    },
    clearQueue: function (type) {
      return this.queue(type || "fx", []);
    },
    // Get a promise resolved when queues of a certain type
    // are emptied (fx is the type by default)
    promise: function (type, obj) {
      var tmp,
        count = 1,
        defer = jQuery.Deferred(),
        elements = this,
        i = this.length,
        resolve = function () {
          if (! --count) {
            defer.resolveWith(elements, [elements]);
          }
        };
      if (typeof type !== "string") {
        obj = type;
        type = undefined;
      }
      type = type || "fx";
      while (i--) {
        tmp = dataPriv.get(elements[i], type + "queueHooks");
        if (tmp && tmp.empty) {
          count++;
          tmp.empty.add(resolve);
        }
      }
      resolve();
      return defer.promise(obj);
    }
  });
  var pnum = /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/.source;
  var rcssNum = new RegExp("^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i");
  var cssExpand = ["Top", "Right", "Bottom", "Left"];
  var documentElement = document.documentElement;
  var isAttached = function (elem) {
      return jQuery.contains(elem.ownerDocument, elem);
    },
    composed = {
      composed: true
    };

  // Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
  // Check attachment across shadow DOM boundaries when possible (gh-3504)
  // Support: iOS 10.0-10.2 only
  // Early iOS 10 versions support `attachShadow` but not `getRootNode`,
  // leading to errors. We need to check for `getRootNode`.
  if (documentElement.getRootNode) {
    isAttached = function (elem) {
      return jQuery.contains(elem.ownerDocument, elem) || elem.getRootNode(composed) === elem.ownerDocument;
    };
  }
  var isHiddenWithinTree = function (elem, el) {
    // isHiddenWithinTree might be called from jQuery#filter function;
    // in that case, element will be second argument
    elem = el || elem;

    // Inline style trumps all
    return elem.style.display === "none" || elem.style.display === "" &&
    // Otherwise, check computed style
    // Support: Firefox <=43 - 45
    // Disconnected elements can have computed display: none, so first confirm that elem is
    // in the document.
    isAttached(elem) && jQuery.css(elem, "display") === "none";
  };
  function adjustCSS(elem, prop, valueParts, tween) {
    var adjusted,
      scale,
      maxIterations = 20,
      currentValue = tween ? function () {
        return tween.cur();
      } : function () {
        return jQuery.css(elem, prop, "");
      },
      initial = currentValue(),
      unit = valueParts && valueParts[3] || (jQuery.cssNumber[prop] ? "" : "px"),
      // Starting value computation is required for potential unit mismatches
      initialInUnit = elem.nodeType && (jQuery.cssNumber[prop] || unit !== "px" && +initial) && rcssNum.exec(jQuery.css(elem, prop));
    if (initialInUnit && initialInUnit[3] !== unit) {
      // Support: Firefox <=54
      // Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
      initial = initial / 2;

      // Trust units reported by jQuery.css
      unit = unit || initialInUnit[3];

      // Iteratively approximate from a nonzero starting point
      initialInUnit = +initial || 1;
      while (maxIterations--) {
        // Evaluate and update our best guess (doubling guesses that zero out).
        // Finish if the scale equals or crosses 1 (making the old*new product non-positive).
        jQuery.style(elem, prop, initialInUnit + unit);
        if ((1 - scale) * (1 - (scale = currentValue() / initial || 0.5)) <= 0) {
          maxIterations = 0;
        }
        initialInUnit = initialInUnit / scale;
      }
      initialInUnit = initialInUnit * 2;
      jQuery.style(elem, prop, initialInUnit + unit);

      // Make sure we update the tween properties later on
      valueParts = valueParts || [];
    }
    if (valueParts) {
      initialInUnit = +initialInUnit || +initial || 0;

      // Apply relative offset (+=/-=) if specified
      adjusted = valueParts[1] ? initialInUnit + (valueParts[1] + 1) * valueParts[2] : +valueParts[2];
      if (tween) {
        tween.unit = unit;
        tween.start = initialInUnit;
        tween.end = adjusted;
      }
    }
    return adjusted;
  }
  var defaultDisplayMap = {};
  function getDefaultDisplay(elem) {
    var temp,
      doc = elem.ownerDocument,
      nodeName = elem.nodeName,
      display = defaultDisplayMap[nodeName];
    if (display) {
      return display;
    }
    temp = doc.body.appendChild(doc.createElement(nodeName));
    display = jQuery.css(temp, "display");
    temp.parentNode.removeChild(temp);
    if (display === "none") {
      display = "block";
    }
    defaultDisplayMap[nodeName] = display;
    return display;
  }
  function showHide(elements, show) {
    var display,
      elem,
      values = [],
      index = 0,
      length = elements.length;

    // Determine new display value for elements that need to change
    for (; index < length; index++) {
      elem = elements[index];
      if (!elem.style) {
        continue;
      }
      display = elem.style.display;
      if (show) {
        // Since we force visibility upon cascade-hidden elements, an immediate (and slow)
        // check is required in this first loop unless we have a nonempty display value (either
        // inline or about-to-be-restored)
        if (display === "none") {
          values[index] = dataPriv.get(elem, "display") || null;
          if (!values[index]) {
            elem.style.display = "";
          }
        }
        if (elem.style.display === "" && isHiddenWithinTree(elem)) {
          values[index] = getDefaultDisplay(elem);
        }
      } else {
        if (display !== "none") {
          values[index] = "none";

          // Remember what we're overwriting
          dataPriv.set(elem, "display", display);
        }
      }
    }

    // Set the display of the elements in a second loop to avoid constant reflow
    for (index = 0; index < length; index++) {
      if (values[index] != null) {
        elements[index].style.display = values[index];
      }
    }
    return elements;
  }
  jQuery.fn.extend({
    show: function () {
      return showHide(this, true);
    },
    hide: function () {
      return showHide(this);
    },
    toggle: function (state) {
      if (typeof state === "boolean") {
        return state ? this.show() : this.hide();
      }
      return this.each(function () {
        if (isHiddenWithinTree(this)) {
          jQuery(this).show();
        } else {
          jQuery(this).hide();
        }
      });
    }
  });
  var rcheckableType = /^(?:checkbox|radio)$/i;
  var rtagName = /<([a-z][^\/\0>\x20\t\r\n\f]*)/i;
  var rscriptType = /^$|^module$|\/(?:java|ecma)script/i;
  (function () {
    var fragment = document.createDocumentFragment(),
      div = fragment.appendChild(document.createElement("div")),
      input = document.createElement("input");

    // Support: Android 4.0 - 4.3 only
    // Check state lost if the name is set (trac-11217)
    // Support: Windows Web Apps (WWA)
    // `name` and `type` must use .setAttribute for WWA (trac-14901)
    input.setAttribute("type", "radio");
    input.setAttribute("checked", "checked");
    input.setAttribute("name", "t");
    div.appendChild(input);

    // Support: Android <=4.1 only
    // Older WebKit doesn't clone checked state correctly in fragments
    support.checkClone = div.cloneNode(true).cloneNode(true).lastChild.checked;

    // Support: IE <=11 only
    // Make sure textarea (and checkbox) defaultValue is properly cloned
    div.innerHTML = "<textarea>x</textarea>";
    support.noCloneChecked = !!div.cloneNode(true).lastChild.defaultValue;

    // Support: IE <=9 only
    // IE <=9 replaces <option> tags with their contents when inserted outside of
    // the select element.
    div.innerHTML = "<option></option>";
    support.option = !!div.lastChild;
  })();

  // We have to close these tags to support XHTML (trac-13200)
  var wrapMap = {
    // XHTML parsers do not magically insert elements in the
    // same way that tag soup parsers do. So we cannot shorten
    // this by omitting <tbody> or other required elements.
    thead: [1, "<table>", "</table>"],
    col: [2, "<table><colgroup>", "</colgroup></table>"],
    tr: [2, "<table><tbody>", "</tbody></table>"],
    td: [3, "<table><tbody><tr>", "</tr></tbody></table>"],
    _default: [0, "", ""]
  };
  wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
  wrapMap.th = wrapMap.td;

  // Support: IE <=9 only
  if (!support.option) {
    wrapMap.optgroup = wrapMap.option = [1, "<select multiple='multiple'>", "</select>"];
  }
  function getAll(context, tag) {
    // Support: IE <=9 - 11 only
    // Use typeof to avoid zero-argument method invocation on host objects (trac-15151)
    var ret;
    if (typeof context.getElementsByTagName !== "undefined") {
      ret = context.getElementsByTagName(tag || "*");
    } else if (typeof context.querySelectorAll !== "undefined") {
      ret = context.querySelectorAll(tag || "*");
    } else {
      ret = [];
    }
    if (tag === undefined || tag && nodeName(context, tag)) {
      return jQuery.merge([context], ret);
    }
    return ret;
  }

  // Mark scripts as having already been evaluated
  function setGlobalEval(elems, refElements) {
    var i = 0,
      l = elems.length;
    for (; i < l; i++) {
      dataPriv.set(elems[i], "globalEval", !refElements || dataPriv.get(refElements[i], "globalEval"));
    }
  }
  var rhtml = /<|&#?\w+;/;
  function buildFragment(elems, context, scripts, selection, ignored) {
    var elem,
      tmp,
      tag,
      wrap,
      attached,
      j,
      fragment = context.createDocumentFragment(),
      nodes = [],
      i = 0,
      l = elems.length;
    for (; i < l; i++) {
      elem = elems[i];
      if (elem || elem === 0) {
        // Add nodes directly
        if (toType(elem) === "object") {
          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, elem.nodeType ? [elem] : elem);

          // Convert non-html into a text node
        } else if (!rhtml.test(elem)) {
          nodes.push(context.createTextNode(elem));

          // Convert html into DOM nodes
        } else {
          tmp = tmp || fragment.appendChild(context.createElement("div"));

          // Deserialize a standard representation
          tag = (rtagName.exec(elem) || ["", ""])[1].toLowerCase();
          wrap = wrapMap[tag] || wrapMap._default;
          tmp.innerHTML = wrap[1] + jQuery.htmlPrefilter(elem) + wrap[2];

          // Descend through wrappers to the right content
          j = wrap[0];
          while (j--) {
            tmp = tmp.lastChild;
          }

          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, tmp.childNodes);

          // Remember the top-level container
          tmp = fragment.firstChild;

          // Ensure the created nodes are orphaned (trac-12392)
          tmp.textContent = "";
        }
      }
    }

    // Remove wrapper from fragment
    fragment.textContent = "";
    i = 0;
    while (elem = nodes[i++]) {
      // Skip elements already in the context collection (trac-4087)
      if (selection && jQuery.inArray(elem, selection) > -1) {
        if (ignored) {
          ignored.push(elem);
        }
        continue;
      }
      attached = isAttached(elem);

      // Append to fragment
      tmp = getAll(fragment.appendChild(elem), "script");

      // Preserve script evaluation history
      if (attached) {
        setGlobalEval(tmp);
      }

      // Capture executables
      if (scripts) {
        j = 0;
        while (elem = tmp[j++]) {
          if (rscriptType.test(elem.type || "")) {
            scripts.push(elem);
          }
        }
      }
    }
    return fragment;
  }
  var rtypenamespace = /^([^.]*)(?:\.(.+)|)/;
  function returnTrue() {
    return true;
  }
  function returnFalse() {
    return false;
  }
  function on(elem, types, selector, data, fn, one) {
    var origFn, type;

    // Types can be a map of types/handlers
    if (typeof types === "object") {
      // ( types-Object, selector, data )
      if (typeof selector !== "string") {
        // ( types-Object, data )
        data = data || selector;
        selector = undefined;
      }
      for (type in types) {
        on(elem, type, selector, data, types[type], one);
      }
      return elem;
    }
    if (data == null && fn == null) {
      // ( types, fn )
      fn = selector;
      data = selector = undefined;
    } else if (fn == null) {
      if (typeof selector === "string") {
        // ( types, selector, fn )
        fn = data;
        data = undefined;
      } else {
        // ( types, data, fn )
        fn = data;
        data = selector;
        selector = undefined;
      }
    }
    if (fn === false) {
      fn = returnFalse;
    } else if (!fn) {
      return elem;
    }
    if (one === 1) {
      origFn = fn;
      fn = function (event) {
        // Can use an empty set, since event contains the info
        jQuery().off(event);
        return origFn.apply(this, arguments);
      };

      // Use same guid so caller can remove using origFn
      fn.guid = origFn.guid || (origFn.guid = jQuery.guid++);
    }
    return elem.each(function () {
      jQuery.event.add(this, types, fn, data, selector);
    });
  }

  /*
   * Helper functions for managing events -- not part of the public interface.
   * Props to Dean Edwards' addEvent library for many of the ideas.
   */
  jQuery.event = {
    global: {},
    add: function (elem, types, handler, data, selector) {
      var handleObjIn,
        eventHandle,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.get(elem);

      // Only attach events to objects that accept data
      if (!acceptData(elem)) {
        return;
      }

      // Caller can pass in an object of custom data in lieu of the handler
      if (handler.handler) {
        handleObjIn = handler;
        handler = handleObjIn.handler;
        selector = handleObjIn.selector;
      }

      // Ensure that invalid selectors throw exceptions at attach time
      // Evaluate against documentElement in case elem is a non-element node (e.g., document)
      if (selector) {
        jQuery.find.matchesSelector(documentElement, selector);
      }

      // Make sure that the handler has a unique ID, used to find/remove it later
      if (!handler.guid) {
        handler.guid = jQuery.guid++;
      }

      // Init the element's event structure and main handler, if this is the first
      if (!(events = elemData.events)) {
        events = elemData.events = Object.create(null);
      }
      if (!(eventHandle = elemData.handle)) {
        eventHandle = elemData.handle = function (e) {
          // Discard the second event of a jQuery.event.trigger() and
          // when an event is called after a page has unloaded
          return typeof jQuery !== "undefined" && jQuery.event.triggered !== e.type ? jQuery.event.dispatch.apply(elem, arguments) : undefined;
        };
      }

      // Handle multiple events separated by a space
      types = (types || "").match(rnothtmlwhite) || [""];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || "").split(".").sort();

        // There *must* be a type, no attaching namespace-only handlers
        if (!type) {
          continue;
        }

        // If event changes its type, use the special event handlers for the changed type
        special = jQuery.event.special[type] || {};

        // If selector defined, determine special event api type, otherwise given type
        type = (selector ? special.delegateType : special.bindType) || type;

        // Update special based on newly reset type
        special = jQuery.event.special[type] || {};

        // handleObj is passed to all event handlers
        handleObj = jQuery.extend({
          type: type,
          origType: origType,
          data: data,
          handler: handler,
          guid: handler.guid,
          selector: selector,
          needsContext: selector && jQuery.expr.match.needsContext.test(selector),
          namespace: namespaces.join(".")
        }, handleObjIn);

        // Init the event handler queue if we're the first
        if (!(handlers = events[type])) {
          handlers = events[type] = [];
          handlers.delegateCount = 0;

          // Only use addEventListener if the special events handler returns false
          if (!special.setup || special.setup.call(elem, data, namespaces, eventHandle) === false) {
            if (elem.addEventListener) {
              elem.addEventListener(type, eventHandle);
            }
          }
        }
        if (special.add) {
          special.add.call(elem, handleObj);
          if (!handleObj.handler.guid) {
            handleObj.handler.guid = handler.guid;
          }
        }

        // Add to the element's handler list, delegates in front
        if (selector) {
          handlers.splice(handlers.delegateCount++, 0, handleObj);
        } else {
          handlers.push(handleObj);
        }

        // Keep track of which events have ever been used, for event optimization
        jQuery.event.global[type] = true;
      }
    },
    // Detach an event or set of events from an element
    remove: function (elem, types, handler, selector, mappedTypes) {
      var j,
        origCount,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.hasData(elem) && dataPriv.get(elem);
      if (!elemData || !(events = elemData.events)) {
        return;
      }

      // Once for each type.namespace in types; type may be omitted
      types = (types || "").match(rnothtmlwhite) || [""];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || "").split(".").sort();

        // Unbind all events (on this namespace, if provided) for the element
        if (!type) {
          for (type in events) {
            jQuery.event.remove(elem, type + types[t], handler, selector, true);
          }
          continue;
        }
        special = jQuery.event.special[type] || {};
        type = (selector ? special.delegateType : special.bindType) || type;
        handlers = events[type] || [];
        tmp = tmp[2] && new RegExp("(^|\\.)" + namespaces.join("\\.(?:.*\\.|)") + "(\\.|$)");

        // Remove matching events
        origCount = j = handlers.length;
        while (j--) {
          handleObj = handlers[j];
          if ((mappedTypes || origType === handleObj.origType) && (!handler || handler.guid === handleObj.guid) && (!tmp || tmp.test(handleObj.namespace)) && (!selector || selector === handleObj.selector || selector === "**" && handleObj.selector)) {
            handlers.splice(j, 1);
            if (handleObj.selector) {
              handlers.delegateCount--;
            }
            if (special.remove) {
              special.remove.call(elem, handleObj);
            }
          }
        }

        // Remove generic event handler if we removed something and no more handlers exist
        // (avoids potential for endless recursion during removal of special event handlers)
        if (origCount && !handlers.length) {
          if (!special.teardown || special.teardown.call(elem, namespaces, elemData.handle) === false) {
            jQuery.removeEvent(elem, type, elemData.handle);
          }
          delete events[type];
        }
      }

      // Remove data and the expando if it's no longer used
      if (jQuery.isEmptyObject(events)) {
        dataPriv.remove(elem, "handle events");
      }
    },
    dispatch: function (nativeEvent) {
      var i,
        j,
        ret,
        matched,
        handleObj,
        handlerQueue,
        args = new Array(arguments.length),
        // Make a writable jQuery.Event from the native event object
        event = jQuery.event.fix(nativeEvent),
        handlers = (dataPriv.get(this, "events") || Object.create(null))[event.type] || [],
        special = jQuery.event.special[event.type] || {};

      // Use the fix-ed jQuery.Event rather than the (read-only) native event
      args[0] = event;
      for (i = 1; i < arguments.length; i++) {
        args[i] = arguments[i];
      }
      event.delegateTarget = this;

      // Call the preDispatch hook for the mapped type, and let it bail if desired
      if (special.preDispatch && special.preDispatch.call(this, event) === false) {
        return;
      }

      // Determine handlers
      handlerQueue = jQuery.event.handlers.call(this, event, handlers);

      // Run delegates first; they may want to stop propagation beneath us
      i = 0;
      while ((matched = handlerQueue[i++]) && !event.isPropagationStopped()) {
        event.currentTarget = matched.elem;
        j = 0;
        while ((handleObj = matched.handlers[j++]) && !event.isImmediatePropagationStopped()) {
          // If the event is namespaced, then each handler is only invoked if it is
          // specially universal or its namespaces are a superset of the event's.
          if (!event.rnamespace || handleObj.namespace === false || event.rnamespace.test(handleObj.namespace)) {
            event.handleObj = handleObj;
            event.data = handleObj.data;
            ret = ((jQuery.event.special[handleObj.origType] || {}).handle || handleObj.handler).apply(matched.elem, args);
            if (ret !== undefined) {
              if ((event.result = ret) === false) {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          }
        }
      }

      // Call the postDispatch hook for the mapped type
      if (special.postDispatch) {
        special.postDispatch.call(this, event);
      }
      return event.result;
    },
    handlers: function (event, handlers) {
      var i,
        handleObj,
        sel,
        matchedHandlers,
        matchedSelectors,
        handlerQueue = [],
        delegateCount = handlers.delegateCount,
        cur = event.target;

      // Find delegate handlers
      if (delegateCount &&
      // Support: IE <=9
      // Black-hole SVG <use> instance trees (trac-13180)
      cur.nodeType &&
      // Support: Firefox <=42
      // Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
      // https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
      // Support: IE 11 only
      // ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
      !(event.type === "click" && event.button >= 1)) {
        for (; cur !== this; cur = cur.parentNode || this) {
          // Don't check non-elements (trac-13208)
          // Don't process clicks on disabled elements (trac-6911, trac-8165, trac-11382, trac-11764)
          if (cur.nodeType === 1 && !(event.type === "click" && cur.disabled === true)) {
            matchedHandlers = [];
            matchedSelectors = {};
            for (i = 0; i < delegateCount; i++) {
              handleObj = handlers[i];

              // Don't conflict with Object.prototype properties (trac-13203)
              sel = handleObj.selector + " ";
              if (matchedSelectors[sel] === undefined) {
                matchedSelectors[sel] = handleObj.needsContext ? jQuery(sel, this).index(cur) > -1 : jQuery.find(sel, this, null, [cur]).length;
              }
              if (matchedSelectors[sel]) {
                matchedHandlers.push(handleObj);
              }
            }
            if (matchedHandlers.length) {
              handlerQueue.push({
                elem: cur,
                handlers: matchedHandlers
              });
            }
          }
        }
      }

      // Add the remaining (directly-bound) handlers
      cur = this;
      if (delegateCount < handlers.length) {
        handlerQueue.push({
          elem: cur,
          handlers: handlers.slice(delegateCount)
        });
      }
      return handlerQueue;
    },
    addProp: function (name, hook) {
      Object.defineProperty(jQuery.Event.prototype, name, {
        enumerable: true,
        configurable: true,
        get: isFunction(hook) ? function () {
          if (this.originalEvent) {
            return hook(this.originalEvent);
          }
        } : function () {
          if (this.originalEvent) {
            return this.originalEvent[name];
          }
        },
        set: function (value) {
          Object.defineProperty(this, name, {
            enumerable: true,
            configurable: true,
            writable: true,
            value: value
          });
        }
      });
    },
    fix: function (originalEvent) {
      return originalEvent[jQuery.expando] ? originalEvent : new jQuery.Event(originalEvent);
    },
    special: {
      load: {
        // Prevent triggered image.load events from bubbling to window.load
        noBubble: true
      },
      click: {
        // Utilize native event to ensure correct state for checkable inputs
        setup: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Claim the first handler
          if (rcheckableType.test(el.type) && el.click && nodeName(el, "input")) {
            // dataPriv.set( el, "click", ... )
            leverageNative(el, "click", true);
          }

          // Return false to allow normal processing in the caller
          return false;
        },
        trigger: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Force setup before triggering a click
          if (rcheckableType.test(el.type) && el.click && nodeName(el, "input")) {
            leverageNative(el, "click");
          }

          // Return non-false to allow normal event-path propagation
          return true;
        },
        // For cross-browser consistency, suppress native .click() on links
        // Also prevent it if we're currently inside a leveraged native-event stack
        _default: function (event) {
          var target = event.target;
          return rcheckableType.test(target.type) && target.click && nodeName(target, "input") && dataPriv.get(target, "click") || nodeName(target, "a");
        }
      },
      beforeunload: {
        postDispatch: function (event) {
          // Support: Firefox 20+
          // Firefox doesn't alert if the returnValue field is not set.
          if (event.result !== undefined && event.originalEvent) {
            event.originalEvent.returnValue = event.result;
          }
        }
      }
    }
  };

  // Ensure the presence of an event listener that handles manually-triggered
  // synthetic events by interrupting progress until reinvoked in response to
  // *native* events that it fires directly, ensuring that state changes have
  // already occurred before other listeners are invoked.
  function leverageNative(el, type, isSetup) {
    // Missing `isSetup` indicates a trigger call, which must force setup through jQuery.event.add
    if (!isSetup) {
      if (dataPriv.get(el, type) === undefined) {
        jQuery.event.add(el, type, returnTrue);
      }
      return;
    }

    // Register the controller as a special universal handler for all event namespaces
    dataPriv.set(el, type, false);
    jQuery.event.add(el, type, {
      namespace: false,
      handler: function (event) {
        var result,
          saved = dataPriv.get(this, type);
        if (event.isTrigger & 1 && this[type]) {
          // Interrupt processing of the outer synthetic .trigger()ed event
          if (!saved) {
            // Store arguments for use when handling the inner native event
            // There will always be at least one argument (an event object), so this array
            // will not be confused with a leftover capture object.
            saved = slice.call(arguments);
            dataPriv.set(this, type, saved);

            // Trigger the native event and capture its result
            this[type]();
            result = dataPriv.get(this, type);
            dataPriv.set(this, type, false);
            if (saved !== result) {
              // Cancel the outer synthetic event
              event.stopImmediatePropagation();
              event.preventDefault();
              return result;
            }

            // If this is an inner synthetic event for an event with a bubbling surrogate
            // (focus or blur), assume that the surrogate already propagated from triggering
            // the native event and prevent that from happening again here.
            // This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
            // bubbling surrogate propagates *after* the non-bubbling base), but that seems
            // less bad than duplication.
          } else if ((jQuery.event.special[type] || {}).delegateType) {
            event.stopPropagation();
          }

          // If this is a native event triggered above, everything is now in order
          // Fire an inner synthetic event with the original arguments
        } else if (saved) {
          // ...and capture the result
          dataPriv.set(this, type, jQuery.event.trigger(saved[0], saved.slice(1), this));

          // Abort handling of the native event by all jQuery handlers while allowing
          // native handlers on the same element to run. On target, this is achieved
          // by stopping immediate propagation just on the jQuery event. However,
          // the native event is re-wrapped by a jQuery one on each level of the
          // propagation so the only way to stop it for jQuery is to stop it for
          // everyone via native `stopPropagation()`. This is not a problem for
          // focus/blur which don't bubble, but it does also stop click on checkboxes
          // and radios. We accept this limitation.
          event.stopPropagation();
          event.isImmediatePropagationStopped = returnTrue;
        }
      }
    });
  }
  jQuery.removeEvent = function (elem, type, handle) {
    // This "if" is needed for plain objects
    if (elem.removeEventListener) {
      elem.removeEventListener(type, handle);
    }
  };
  jQuery.Event = function (src, props) {
    // Allow instantiation without the 'new' keyword
    if (!(this instanceof jQuery.Event)) {
      return new jQuery.Event(src, props);
    }

    // Event object
    if (src && src.type) {
      this.originalEvent = src;
      this.type = src.type;

      // Events bubbling up the document may have been marked as prevented
      // by a handler lower down the tree; reflect the correct value.
      this.isDefaultPrevented = src.defaultPrevented || src.defaultPrevented === undefined &&
      // Support: Android <=2.3 only
      src.returnValue === false ? returnTrue : returnFalse;

      // Create target properties
      // Support: Safari <=6 - 7 only
      // Target should not be a text node (trac-504, trac-13143)
      this.target = src.target && src.target.nodeType === 3 ? src.target.parentNode : src.target;
      this.currentTarget = src.currentTarget;
      this.relatedTarget = src.relatedTarget;

      // Event type
    } else {
      this.type = src;
    }

    // Put explicitly provided properties onto the event object
    if (props) {
      jQuery.extend(this, props);
    }

    // Create a timestamp if incoming event doesn't have one
    this.timeStamp = src && src.timeStamp || Date.now();

    // Mark it as fixed
    this[jQuery.expando] = true;
  };

  // jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
  // https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
  jQuery.Event.prototype = {
    constructor: jQuery.Event,
    isDefaultPrevented: returnFalse,
    isPropagationStopped: returnFalse,
    isImmediatePropagationStopped: returnFalse,
    isSimulated: false,
    preventDefault: function () {
      var e = this.originalEvent;
      this.isDefaultPrevented = returnTrue;
      if (e && !this.isSimulated) {
        e.preventDefault();
      }
    },
    stopPropagation: function () {
      var e = this.originalEvent;
      this.isPropagationStopped = returnTrue;
      if (e && !this.isSimulated) {
        e.stopPropagation();
      }
    },
    stopImmediatePropagation: function () {
      var e = this.originalEvent;
      this.isImmediatePropagationStopped = returnTrue;
      if (e && !this.isSimulated) {
        e.stopImmediatePropagation();
      }
      this.stopPropagation();
    }
  };

  // Includes all common event props including KeyEvent and MouseEvent specific props
  jQuery.each({
    altKey: true,
    bubbles: true,
    cancelable: true,
    changedTouches: true,
    ctrlKey: true,
    detail: true,
    eventPhase: true,
    metaKey: true,
    pageX: true,
    pageY: true,
    shiftKey: true,
    view: true,
    "char": true,
    code: true,
    charCode: true,
    key: true,
    keyCode: true,
    button: true,
    buttons: true,
    clientX: true,
    clientY: true,
    offsetX: true,
    offsetY: true,
    pointerId: true,
    pointerType: true,
    screenX: true,
    screenY: true,
    targetTouches: true,
    toElement: true,
    touches: true,
    which: true
  }, jQuery.event.addProp);
  jQuery.each({
    focus: "focusin",
    blur: "focusout"
  }, function (type, delegateType) {
    function focusMappedHandler(nativeEvent) {
      if (document.documentMode) {
        // Support: IE 11+
        // Attach a single focusin/focusout handler on the document while someone wants
        // focus/blur. This is because the former are synchronous in IE while the latter
        // are async. In other browsers, all those handlers are invoked synchronously.

        // `handle` from private data would already wrap the event, but we need
        // to change the `type` here.
        var handle = dataPriv.get(this, "handle"),
          event = jQuery.event.fix(nativeEvent);
        event.type = nativeEvent.type === "focusin" ? "focus" : "blur";
        event.isSimulated = true;

        // First, handle focusin/focusout
        handle(nativeEvent);

        // ...then, handle focus/blur
        //
        // focus/blur don't bubble while focusin/focusout do; simulate the former by only
        // invoking the handler at the lower level.
        if (event.target === event.currentTarget) {
          // The setup part calls `leverageNative`, which, in turn, calls
          // `jQuery.event.add`, so event handle will already have been set
          // by this point.
          handle(event);
        }
      } else {
        // For non-IE browsers, attach a single capturing handler on the document
        // while someone wants focusin/focusout.
        jQuery.event.simulate(delegateType, nativeEvent.target, jQuery.event.fix(nativeEvent));
      }
    }
    jQuery.event.special[type] = {
      // Utilize native event if possible so blur/focus sequence is correct
      setup: function () {
        var attaches;

        // Claim the first handler
        // dataPriv.set( this, "focus", ... )
        // dataPriv.set( this, "blur", ... )
        leverageNative(this, type, true);
        if (document.documentMode) {
          // Support: IE 9 - 11+
          // We use the same native handler for focusin & focus (and focusout & blur)
          // so we need to coordinate setup & teardown parts between those events.
          // Use `delegateType` as the key as `type` is already used by `leverageNative`.
          attaches = dataPriv.get(this, delegateType);
          if (!attaches) {
            this.addEventListener(delegateType, focusMappedHandler);
          }
          dataPriv.set(this, delegateType, (attaches || 0) + 1);
        } else {
          // Return false to allow normal processing in the caller
          return false;
        }
      },
      trigger: function () {
        // Force setup before trigger
        leverageNative(this, type);

        // Return non-false to allow normal event-path propagation
        return true;
      },
      teardown: function () {
        var attaches;
        if (document.documentMode) {
          attaches = dataPriv.get(this, delegateType) - 1;
          if (!attaches) {
            this.removeEventListener(delegateType, focusMappedHandler);
            dataPriv.remove(this, delegateType);
          } else {
            dataPriv.set(this, delegateType, attaches);
          }
        } else {
          // Return false to indicate standard teardown should be applied
          return false;
        }
      },
      // Suppress native focus or blur if we're currently inside
      // a leveraged native-event stack
      _default: function (event) {
        return dataPriv.get(event.target, type);
      },
      delegateType: delegateType
    };

    // Support: Firefox <=44
    // Firefox doesn't have focus(in | out) events
    // Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
    //
    // Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
    // focus(in | out) events fire after focus & blur events,
    // which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
    // Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
    //
    // Support: IE 9 - 11+
    // To preserve relative focusin/focus & focusout/blur event order guaranteed on the 3.x branch,
    // attach a single handler for both events in IE.
    jQuery.event.special[delegateType] = {
      setup: function () {
        // Handle: regular nodes (via `this.ownerDocument`), window
        // (via `this.document`) & document (via `this`).
        var doc = this.ownerDocument || this.document || this,
          dataHolder = document.documentMode ? this : doc,
          attaches = dataPriv.get(dataHolder, delegateType);

        // Support: IE 9 - 11+
        // We use the same native handler for focusin & focus (and focusout & blur)
        // so we need to coordinate setup & teardown parts between those events.
        // Use `delegateType` as the key as `type` is already used by `leverageNative`.
        if (!attaches) {
          if (document.documentMode) {
            this.addEventListener(delegateType, focusMappedHandler);
          } else {
            doc.addEventListener(type, focusMappedHandler, true);
          }
        }
        dataPriv.set(dataHolder, delegateType, (attaches || 0) + 1);
      },
      teardown: function () {
        var doc = this.ownerDocument || this.document || this,
          dataHolder = document.documentMode ? this : doc,
          attaches = dataPriv.get(dataHolder, delegateType) - 1;
        if (!attaches) {
          if (document.documentMode) {
            this.removeEventListener(delegateType, focusMappedHandler);
          } else {
            doc.removeEventListener(type, focusMappedHandler, true);
          }
          dataPriv.remove(dataHolder, delegateType);
        } else {
          dataPriv.set(dataHolder, delegateType, attaches);
        }
      }
    };
  });

  // Create mouseenter/leave events using mouseover/out and event-time checks
  // so that event delegation works in jQuery.
  // Do the same for pointerenter/pointerleave and pointerover/pointerout
  //
  // Support: Safari 7 only
  // Safari sends mouseenter too often; see:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=470258
  // for the description of the bug (it existed in older Chrome versions as well).
  jQuery.each({
    mouseenter: "mouseover",
    mouseleave: "mouseout",
    pointerenter: "pointerover",
    pointerleave: "pointerout"
  }, function (orig, fix) {
    jQuery.event.special[orig] = {
      delegateType: fix,
      bindType: fix,
      handle: function (event) {
        var ret,
          target = this,
          related = event.relatedTarget,
          handleObj = event.handleObj;

        // For mouseenter/leave call the handler if related is outside the target.
        // NB: No relatedTarget if the mouse left/entered the browser window
        if (!related || related !== target && !jQuery.contains(target, related)) {
          event.type = handleObj.origType;
          ret = handleObj.handler.apply(this, arguments);
          event.type = fix;
        }
        return ret;
      }
    };
  });
  jQuery.fn.extend({
    on: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn);
    },
    one: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn, 1);
    },
    off: function (types, selector, fn) {
      var handleObj, type;
      if (types && types.preventDefault && types.handleObj) {
        // ( event )  dispatched jQuery.Event
        handleObj = types.handleObj;
        jQuery(types.delegateTarget).off(handleObj.namespace ? handleObj.origType + "." + handleObj.namespace : handleObj.origType, handleObj.selector, handleObj.handler);
        return this;
      }
      if (typeof types === "object") {
        // ( types-object [, selector] )
        for (type in types) {
          this.off(type, selector, types[type]);
        }
        return this;
      }
      if (selector === false || typeof selector === "function") {
        // ( types [, fn] )
        fn = selector;
        selector = undefined;
      }
      if (fn === false) {
        fn = returnFalse;
      }
      return this.each(function () {
        jQuery.event.remove(this, types, fn, selector);
      });
    }
  });
  var
    // Support: IE <=10 - 11, Edge 12 - 13 only
    // In IE/Edge using regex groups here causes severe slowdowns.
    // See https://connect.microsoft.com/IE/feedback/details/1736512/
    rnoInnerhtml = /<script|<style|<link/i,
    // checked="checked" or checked
    rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
    rcleanScript = /^\s*<!\[CDATA\[|\]\]>\s*$/g;

  // Prefer a tbody over its parent table for containing new rows
  function manipulationTarget(elem, content) {
    if (nodeName(elem, "table") && nodeName(content.nodeType !== 11 ? content : content.firstChild, "tr")) {
      return jQuery(elem).children("tbody")[0] || elem;
    }
    return elem;
  }

  // Replace/restore the type attribute of script elements for safe DOM manipulation
  function disableScript(elem) {
    elem.type = (elem.getAttribute("type") !== null) + "/" + elem.type;
    return elem;
  }
  function restoreScript(elem) {
    if ((elem.type || "").slice(0, 5) === "true/") {
      elem.type = elem.type.slice(5);
    } else {
      elem.removeAttribute("type");
    }
    return elem;
  }
  function cloneCopyEvent(src, dest) {
    var i, l, type, pdataOld, udataOld, udataCur, events;
    if (dest.nodeType !== 1) {
      return;
    }

    // 1. Copy private data: events, handlers, etc.
    if (dataPriv.hasData(src)) {
      pdataOld = dataPriv.get(src);
      events = pdataOld.events;
      if (events) {
        dataPriv.remove(dest, "handle events");
        for (type in events) {
          for (i = 0, l = events[type].length; i < l; i++) {
            jQuery.event.add(dest, type, events[type][i]);
          }
        }
      }
    }

    // 2. Copy user data
    if (dataUser.hasData(src)) {
      udataOld = dataUser.access(src);
      udataCur = jQuery.extend({}, udataOld);
      dataUser.set(dest, udataCur);
    }
  }

  // Fix IE bugs, see support tests
  function fixInput(src, dest) {
    var nodeName = dest.nodeName.toLowerCase();

    // Fails to persist the checked state of a cloned checkbox or radio button.
    if (nodeName === "input" && rcheckableType.test(src.type)) {
      dest.checked = src.checked;

      // Fails to return the selected option to the default selected state when cloning options
    } else if (nodeName === "input" || nodeName === "textarea") {
      dest.defaultValue = src.defaultValue;
    }
  }
  function domManip(collection, args, callback, ignored) {
    // Flatten any nested arrays
    args = flat(args);
    var fragment,
      first,
      scripts,
      hasScripts,
      node,
      doc,
      i = 0,
      l = collection.length,
      iNoClone = l - 1,
      value = args[0],
      valueIsFunction = isFunction(value);

    // We can't cloneNode fragments that contain checked, in WebKit
    if (valueIsFunction || l > 1 && typeof value === "string" && !support.checkClone && rchecked.test(value)) {
      return collection.each(function (index) {
        var self = collection.eq(index);
        if (valueIsFunction) {
          args[0] = value.call(this, index, self.html());
        }
        domManip(self, args, callback, ignored);
      });
    }
    if (l) {
      fragment = buildFragment(args, collection[0].ownerDocument, false, collection, ignored);
      first = fragment.firstChild;
      if (fragment.childNodes.length === 1) {
        fragment = first;
      }

      // Require either new content or an interest in ignored elements to invoke the callback
      if (first || ignored) {
        scripts = jQuery.map(getAll(fragment, "script"), disableScript);
        hasScripts = scripts.length;

        // Use the original fragment for the last item
        // instead of the first because it can end up
        // being emptied incorrectly in certain situations (trac-8070).
        for (; i < l; i++) {
          node = fragment;
          if (i !== iNoClone) {
            node = jQuery.clone(node, true, true);

            // Keep references to cloned scripts for later restoration
            if (hasScripts) {
              // Support: Android <=4.0 only, PhantomJS 1 only
              // push.apply(_, arraylike) throws on ancient WebKit
              jQuery.merge(scripts, getAll(node, "script"));
            }
          }
          callback.call(collection[i], node, i);
        }
        if (hasScripts) {
          doc = scripts[scripts.length - 1].ownerDocument;

          // Re-enable scripts
          jQuery.map(scripts, restoreScript);

          // Evaluate executable scripts on first document insertion
          for (i = 0; i < hasScripts; i++) {
            node = scripts[i];
            if (rscriptType.test(node.type || "") && !dataPriv.access(node, "globalEval") && jQuery.contains(doc, node)) {
              if (node.src && (node.type || "").toLowerCase() !== "module") {
                // Optional AJAX dependency, but won't run scripts if not present
                if (jQuery._evalUrl && !node.noModule) {
                  jQuery._evalUrl(node.src, {
                    nonce: node.nonce || node.getAttribute("nonce")
                  }, doc);
                }
              } else {
                // Unwrap a CDATA section containing script contents. This shouldn't be
                // needed as in XML documents they're already not visible when
                // inspecting element contents and in HTML documents they have no
                // meaning but we're preserving that logic for backwards compatibility.
                // This will be removed completely in 4.0. See gh-4904.
                DOMEval(node.textContent.replace(rcleanScript, ""), node, doc);
              }
            }
          }
        }
      }
    }
    return collection;
  }
  function remove(elem, selector, keepData) {
    var node,
      nodes = selector ? jQuery.filter(selector, elem) : elem,
      i = 0;
    for (; (node = nodes[i]) != null; i++) {
      if (!keepData && node.nodeType === 1) {
        jQuery.cleanData(getAll(node));
      }
      if (node.parentNode) {
        if (keepData && isAttached(node)) {
          setGlobalEval(getAll(node, "script"));
        }
        node.parentNode.removeChild(node);
      }
    }
    return elem;
  }
  jQuery.extend({
    htmlPrefilter: function (html) {
      return html;
    },
    clone: function (elem, dataAndEvents, deepDataAndEvents) {
      var i,
        l,
        srcElements,
        destElements,
        clone = elem.cloneNode(true),
        inPage = isAttached(elem);

      // Fix IE cloning issues
      if (!support.noCloneChecked && (elem.nodeType === 1 || elem.nodeType === 11) && !jQuery.isXMLDoc(elem)) {
        // We eschew jQuery#find here for performance reasons:
        // https://jsperf.com/getall-vs-sizzle/2
        destElements = getAll(clone);
        srcElements = getAll(elem);
        for (i = 0, l = srcElements.length; i < l; i++) {
          fixInput(srcElements[i], destElements[i]);
        }
      }

      // Copy the events from the original to the clone
      if (dataAndEvents) {
        if (deepDataAndEvents) {
          srcElements = srcElements || getAll(elem);
          destElements = destElements || getAll(clone);
          for (i = 0, l = srcElements.length; i < l; i++) {
            cloneCopyEvent(srcElements[i], destElements[i]);
          }
        } else {
          cloneCopyEvent(elem, clone);
        }
      }

      // Preserve script evaluation history
      destElements = getAll(clone, "script");
      if (destElements.length > 0) {
        setGlobalEval(destElements, !inPage && getAll(elem, "script"));
      }

      // Return the cloned set
      return clone;
    },
    cleanData: function (elems) {
      var data,
        elem,
        type,
        special = jQuery.event.special,
        i = 0;
      for (; (elem = elems[i]) !== undefined; i++) {
        if (acceptData(elem)) {
          if (data = elem[dataPriv.expando]) {
            if (data.events) {
              for (type in data.events) {
                if (special[type]) {
                  jQuery.event.remove(elem, type);

                  // This is a shortcut to avoid jQuery.event.remove's overhead
                } else {
                  jQuery.removeEvent(elem, type, data.handle);
                }
              }
            }

            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataPriv.expando] = undefined;
          }
          if (elem[dataUser.expando]) {
            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataUser.expando] = undefined;
          }
        }
      }
    }
  });
  jQuery.fn.extend({
    detach: function (selector) {
      return remove(this, selector, true);
    },
    remove: function (selector) {
      return remove(this, selector);
    },
    text: function (value) {
      return access(this, function (value) {
        return value === undefined ? jQuery.text(this) : this.empty().each(function () {
          if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
            this.textContent = value;
          }
        });
      }, null, value, arguments.length);
    },
    append: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.appendChild(elem);
        }
      });
    },
    prepend: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.insertBefore(elem, target.firstChild);
        }
      });
    },
    before: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this);
        }
      });
    },
    after: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this.nextSibling);
        }
      });
    },
    empty: function () {
      var elem,
        i = 0;
      for (; (elem = this[i]) != null; i++) {
        if (elem.nodeType === 1) {
          // Prevent memory leaks
          jQuery.cleanData(getAll(elem, false));

          // Remove any remaining nodes
          elem.textContent = "";
        }
      }
      return this;
    },
    clone: function (dataAndEvents, deepDataAndEvents) {
      dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
      deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;
      return this.map(function () {
        return jQuery.clone(this, dataAndEvents, deepDataAndEvents);
      });
    },
    html: function (value) {
      return access(this, function (value) {
        var elem = this[0] || {},
          i = 0,
          l = this.length;
        if (value === undefined && elem.nodeType === 1) {
          return elem.innerHTML;
        }

        // See if we can take a shortcut and just use innerHTML
        if (typeof value === "string" && !rnoInnerhtml.test(value) && !wrapMap[(rtagName.exec(value) || ["", ""])[1].toLowerCase()]) {
          value = jQuery.htmlPrefilter(value);
          try {
            for (; i < l; i++) {
              elem = this[i] || {};

              // Remove element nodes and prevent memory leaks
              if (elem.nodeType === 1) {
                jQuery.cleanData(getAll(elem, false));
                elem.innerHTML = value;
              }
            }
            elem = 0;

            // If using innerHTML throws an exception, use the fallback method
          } catch (e) {}
        }
        if (elem) {
          this.empty().append(value);
        }
      }, null, value, arguments.length);
    },
    replaceWith: function () {
      var ignored = [];

      // Make the changes, replacing each non-ignored context element with the new content
      return domManip(this, arguments, function (elem) {
        var parent = this.parentNode;
        if (jQuery.inArray(this, ignored) < 0) {
          jQuery.cleanData(getAll(this));
          if (parent) {
            parent.replaceChild(elem, this);
          }
        }

        // Force callback invocation
      }, ignored);
    }
  });
  jQuery.each({
    appendTo: "append",
    prependTo: "prepend",
    insertBefore: "before",
    insertAfter: "after",
    replaceAll: "replaceWith"
  }, function (name, original) {
    jQuery.fn[name] = function (selector) {
      var elems,
        ret = [],
        insert = jQuery(selector),
        last = insert.length - 1,
        i = 0;
      for (; i <= last; i++) {
        elems = i === last ? this : this.clone(true);
        jQuery(insert[i])[original](elems);

        // Support: Android <=4.0 only, PhantomJS 1 only
        // .get() because push.apply(_, arraylike) throws on ancient WebKit
        push.apply(ret, elems.get());
      }
      return this.pushStack(ret);
    };
  });
  var rnumnonpx = new RegExp("^(" + pnum + ")(?!px)[a-z%]+$", "i");
  var rcustomProp = /^--/;
  var getStyles = function (elem) {
    // Support: IE <=11 only, Firefox <=30 (trac-15098, trac-14150)
    // IE throws on elements created in popups
    // FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
    var view = elem.ownerDocument.defaultView;
    if (!view || !view.opener) {
      view = window;
    }
    return view.getComputedStyle(elem);
  };
  var swap = function (elem, options, callback) {
    var ret,
      name,
      old = {};

    // Remember the old values, and insert the new ones
    for (name in options) {
      old[name] = elem.style[name];
      elem.style[name] = options[name];
    }
    ret = callback.call(elem);

    // Revert the old values
    for (name in options) {
      elem.style[name] = old[name];
    }
    return ret;
  };
  var rboxStyle = new RegExp(cssExpand.join("|"), "i");
  (function () {
    // Executing both pixelPosition & boxSizingReliable tests require only one layout
    // so they're executed at the same time to save the second computation.
    function computeStyleTests() {
      // This is a singleton, we need to execute it only once
      if (!div) {
        return;
      }
      container.style.cssText = "position:absolute;left:-11111px;width:60px;" + "margin-top:1px;padding:0;border:0";
      div.style.cssText = "position:relative;display:block;box-sizing:border-box;overflow:scroll;" + "margin:auto;border:1px;padding:1px;" + "width:60%;top:1%";
      documentElement.appendChild(container).appendChild(div);
      var divStyle = window.getComputedStyle(div);
      pixelPositionVal = divStyle.top !== "1%";

      // Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
      reliableMarginLeftVal = roundPixelMeasures(divStyle.marginLeft) === 12;

      // Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
      // Some styles come back with percentage values, even though they shouldn't
      div.style.right = "60%";
      pixelBoxStylesVal = roundPixelMeasures(divStyle.right) === 36;

      // Support: IE 9 - 11 only
      // Detect misreporting of content dimensions for box-sizing:border-box elements
      boxSizingReliableVal = roundPixelMeasures(divStyle.width) === 36;

      // Support: IE 9 only
      // Detect overflow:scroll screwiness (gh-3699)
      // Support: Chrome <=64
      // Don't get tricked when zoom affects offsetWidth (gh-4029)
      div.style.position = "absolute";
      scrollboxSizeVal = roundPixelMeasures(div.offsetWidth / 3) === 12;
      documentElement.removeChild(container);

      // Nullify the div so it wouldn't be stored in the memory and
      // it will also be a sign that checks already performed
      div = null;
    }
    function roundPixelMeasures(measure) {
      return Math.round(parseFloat(measure));
    }
    var pixelPositionVal,
      boxSizingReliableVal,
      scrollboxSizeVal,
      pixelBoxStylesVal,
      reliableTrDimensionsVal,
      reliableMarginLeftVal,
      container = document.createElement("div"),
      div = document.createElement("div");

    // Finish early in limited (non-browser) environments
    if (!div.style) {
      return;
    }

    // Support: IE <=9 - 11 only
    // Style of cloned element affects source element cloned (trac-8908)
    div.style.backgroundClip = "content-box";
    div.cloneNode(true).style.backgroundClip = "";
    support.clearCloneStyle = div.style.backgroundClip === "content-box";
    jQuery.extend(support, {
      boxSizingReliable: function () {
        computeStyleTests();
        return boxSizingReliableVal;
      },
      pixelBoxStyles: function () {
        computeStyleTests();
        return pixelBoxStylesVal;
      },
      pixelPosition: function () {
        computeStyleTests();
        return pixelPositionVal;
      },
      reliableMarginLeft: function () {
        computeStyleTests();
        return reliableMarginLeftVal;
      },
      scrollboxSize: function () {
        computeStyleTests();
        return scrollboxSizeVal;
      },
      // Support: IE 9 - 11+, Edge 15 - 18+
      // IE/Edge misreport `getComputedStyle` of table rows with width/height
      // set in CSS while `offset*` properties report correct values.
      // Behavior in IE 9 is more subtle than in newer versions & it passes
      // some versions of this test; make sure not to make it pass there!
      //
      // Support: Firefox 70+
      // Only Firefox includes border widths
      // in computed dimensions. (gh-4529)
      reliableTrDimensions: function () {
        var table, tr, trChild, trStyle;
        if (reliableTrDimensionsVal == null) {
          table = document.createElement("table");
          tr = document.createElement("tr");
          trChild = document.createElement("div");
          table.style.cssText = "position:absolute;left:-11111px;border-collapse:separate";
          tr.style.cssText = "box-sizing:content-box;border:1px solid";

          // Support: Chrome 86+
          // Height set through cssText does not get applied.
          // Computed height then comes back as 0.
          tr.style.height = "1px";
          trChild.style.height = "9px";

          // Support: Android 8 Chrome 86+
          // In our bodyBackground.html iframe,
          // display for all div elements is set to "inline",
          // which causes a problem only in Android 8 Chrome 86.
          // Ensuring the div is `display: block`
          // gets around this issue.
          trChild.style.display = "block";
          documentElement.appendChild(table).appendChild(tr).appendChild(trChild);
          trStyle = window.getComputedStyle(tr);
          reliableTrDimensionsVal = parseInt(trStyle.height, 10) + parseInt(trStyle.borderTopWidth, 10) + parseInt(trStyle.borderBottomWidth, 10) === tr.offsetHeight;
          documentElement.removeChild(table);
        }
        return reliableTrDimensionsVal;
      }
    });
  })();
  function curCSS(elem, name, computed) {
    var width,
      minWidth,
      maxWidth,
      ret,
      isCustomProp = rcustomProp.test(name),
      // Support: Firefox 51+
      // Retrieving style before computed somehow
      // fixes an issue with getting wrong values
      // on detached elements
      style = elem.style;
    computed = computed || getStyles(elem);

    // getPropertyValue is needed for:
    //   .css('filter') (IE 9 only, trac-12537)
    //   .css('--customProperty) (gh-3144)
    if (computed) {
      // Support: IE <=9 - 11+
      // IE only supports `"float"` in `getPropertyValue`; in computed styles
      // it's only available as `"cssFloat"`. We no longer modify properties
      // sent to `.css()` apart from camelCasing, so we need to check both.
      // Normally, this would create difference in behavior: if
      // `getPropertyValue` returns an empty string, the value returned
      // by `.css()` would be `undefined`. This is usually the case for
      // disconnected elements. However, in IE even disconnected elements
      // with no styles return `"none"` for `getPropertyValue( "float" )`
      ret = computed.getPropertyValue(name) || computed[name];
      if (isCustomProp && ret) {
        // Support: Firefox 105+, Chrome <=105+
        // Spec requires trimming whitespace for custom properties (gh-4926).
        // Firefox only trims leading whitespace. Chrome just collapses
        // both leading & trailing whitespace to a single space.
        //
        // Fall back to `undefined` if empty string returned.
        // This collapses a missing definition with property defined
        // and set to an empty string but there's no standard API
        // allowing us to differentiate them without a performance penalty
        // and returning `undefined` aligns with older jQuery.
        //
        // rtrimCSS treats U+000D CARRIAGE RETURN and U+000C FORM FEED
        // as whitespace while CSS does not, but this is not a problem
        // because CSS preprocessing replaces them with U+000A LINE FEED
        // (which *is* CSS whitespace)
        // https://www.w3.org/TR/css-syntax-3/#input-preprocessing
        ret = ret.replace(rtrimCSS, "$1") || undefined;
      }
      if (ret === "" && !isAttached(elem)) {
        ret = jQuery.style(elem, name);
      }

      // A tribute to the "awesome hack by Dean Edwards"
      // Android Browser returns percentage for some values,
      // but width seems to be reliably pixels.
      // This is against the CSSOM draft spec:
      // https://drafts.csswg.org/cssom/#resolved-values
      if (!support.pixelBoxStyles() && rnumnonpx.test(ret) && rboxStyle.test(name)) {
        // Remember the original values
        width = style.width;
        minWidth = style.minWidth;
        maxWidth = style.maxWidth;

        // Put in the new values to get a computed value out
        style.minWidth = style.maxWidth = style.width = ret;
        ret = computed.width;

        // Revert the changed values
        style.width = width;
        style.minWidth = minWidth;
        style.maxWidth = maxWidth;
      }
    }
    return ret !== undefined ?
    // Support: IE <=9 - 11 only
    // IE returns zIndex value as an integer.
    ret + "" : ret;
  }
  function addGetHookIf(conditionFn, hookFn) {
    // Define the hook, we'll check on the first run if it's really needed.
    return {
      get: function () {
        if (conditionFn()) {
          // Hook not needed (or it's not possible to use it due
          // to missing dependency), remove it.
          delete this.get;
          return;
        }

        // Hook needed; redefine it so that the support test is not executed again.
        return (this.get = hookFn).apply(this, arguments);
      }
    };
  }
  var cssPrefixes = ["Webkit", "Moz", "ms"],
    emptyStyle = document.createElement("div").style,
    vendorProps = {};

  // Return a vendor-prefixed property or undefined
  function vendorPropName(name) {
    // Check for vendor prefixed names
    var capName = name[0].toUpperCase() + name.slice(1),
      i = cssPrefixes.length;
    while (i--) {
      name = cssPrefixes[i] + capName;
      if (name in emptyStyle) {
        return name;
      }
    }
  }

  // Return a potentially-mapped jQuery.cssProps or vendor prefixed property
  function finalPropName(name) {
    var final = jQuery.cssProps[name] || vendorProps[name];
    if (final) {
      return final;
    }
    if (name in emptyStyle) {
      return name;
    }
    return vendorProps[name] = vendorPropName(name) || name;
  }
  var
    // Swappable if display is none or starts with table
    // except "table", "table-cell", or "table-caption"
    // See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
    rdisplayswap = /^(none|table(?!-c[ea]).+)/,
    cssShow = {
      position: "absolute",
      visibility: "hidden",
      display: "block"
    },
    cssNormalTransform = {
      letterSpacing: "0",
      fontWeight: "400"
    };
  function setPositiveNumber(_elem, value, subtract) {
    // Any relative (+/-) values have already been
    // normalized at this point
    var matches = rcssNum.exec(value);
    return matches ?
    // Guard against undefined "subtract", e.g., when used as in cssHooks
    Math.max(0, matches[2] - (subtract || 0)) + (matches[3] || "px") : value;
  }
  function boxModelAdjustment(elem, dimension, box, isBorderBox, styles, computedVal) {
    var i = dimension === "width" ? 1 : 0,
      extra = 0,
      delta = 0,
      marginDelta = 0;

    // Adjustment may not be necessary
    if (box === (isBorderBox ? "border" : "content")) {
      return 0;
    }
    for (; i < 4; i += 2) {
      // Both box models exclude margin
      // Count margin delta separately to only add it after scroll gutter adjustment.
      // This is needed to make negative margins work with `outerHeight( true )` (gh-3982).
      if (box === "margin") {
        marginDelta += jQuery.css(elem, box + cssExpand[i], true, styles);
      }

      // If we get here with a content-box, we're seeking "padding" or "border" or "margin"
      if (!isBorderBox) {
        // Add padding
        delta += jQuery.css(elem, "padding" + cssExpand[i], true, styles);

        // For "border" or "margin", add border
        if (box !== "padding") {
          delta += jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);

          // But still keep track of it otherwise
        } else {
          extra += jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);
        }

        // If we get here with a border-box (content + padding + border), we're seeking "content" or
        // "padding" or "margin"
      } else {
        // For "content", subtract padding
        if (box === "content") {
          delta -= jQuery.css(elem, "padding" + cssExpand[i], true, styles);
        }

        // For "content" or "padding", subtract border
        if (box !== "margin") {
          delta -= jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);
        }
      }
    }

    // Account for positive content-box scroll gutter when requested by providing computedVal
    if (!isBorderBox && computedVal >= 0) {
      // offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
      // Assuming integer scroll gutter, subtract the rest and round down
      delta += Math.max(0, Math.ceil(elem["offset" + dimension[0].toUpperCase() + dimension.slice(1)] - computedVal - delta - extra - 0.5

      // If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
      // Use an explicit zero to avoid NaN (gh-3964)
      )) || 0;
    }
    return delta + marginDelta;
  }
  function getWidthOrHeight(elem, dimension, extra) {
    // Start with computed style
    var styles = getStyles(elem),
      // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
      // Fake content-box until we know it's needed to know the true value.
      boxSizingNeeded = !support.boxSizingReliable() || extra,
      isBorderBox = boxSizingNeeded && jQuery.css(elem, "boxSizing", false, styles) === "border-box",
      valueIsBorderBox = isBorderBox,
      val = curCSS(elem, dimension, styles),
      offsetProp = "offset" + dimension[0].toUpperCase() + dimension.slice(1);

    // Support: Firefox <=54
    // Return a confounding non-pixel value or feign ignorance, as appropriate.
    if (rnumnonpx.test(val)) {
      if (!extra) {
        return val;
      }
      val = "auto";
    }

    // Support: IE 9 - 11 only
    // Use offsetWidth/offsetHeight for when box sizing is unreliable.
    // In those cases, the computed value can be trusted to be border-box.
    if ((!support.boxSizingReliable() && isBorderBox ||
    // Support: IE 10 - 11+, Edge 15 - 18+
    // IE/Edge misreport `getComputedStyle` of table rows with width/height
    // set in CSS while `offset*` properties report correct values.
    // Interestingly, in some cases IE 9 doesn't suffer from this issue.
    !support.reliableTrDimensions() && nodeName(elem, "tr") ||
    // Fall back to offsetWidth/offsetHeight when value is "auto"
    // This happens for inline elements with no explicit setting (gh-3571)
    val === "auto" ||
    // Support: Android <=4.1 - 4.3 only
    // Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
    !parseFloat(val) && jQuery.css(elem, "display", false, styles) === "inline") &&
    // Make sure the element is visible & connected
    elem.getClientRects().length) {
      isBorderBox = jQuery.css(elem, "boxSizing", false, styles) === "border-box";

      // Where available, offsetWidth/offsetHeight approximate border box dimensions.
      // Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
      // retrieved value as a content box dimension.
      valueIsBorderBox = offsetProp in elem;
      if (valueIsBorderBox) {
        val = elem[offsetProp];
      }
    }

    // Normalize "" and auto
    val = parseFloat(val) || 0;

    // Adjust for the element's box model
    return val + boxModelAdjustment(elem, dimension, extra || (isBorderBox ? "border" : "content"), valueIsBorderBox, styles,
    // Provide the current computed size to request scroll gutter calculation (gh-3589)
    val) + "px";
  }
  jQuery.extend({
    // Add in style property hooks for overriding the default
    // behavior of getting and setting a style property
    cssHooks: {
      opacity: {
        get: function (elem, computed) {
          if (computed) {
            // We should always get a number back from opacity
            var ret = curCSS(elem, "opacity");
            return ret === "" ? "1" : ret;
          }
        }
      }
    },
    // Don't automatically add "px" to these possibly-unitless properties
    cssNumber: {
      animationIterationCount: true,
      aspectRatio: true,
      borderImageSlice: true,
      columnCount: true,
      flexGrow: true,
      flexShrink: true,
      fontWeight: true,
      gridArea: true,
      gridColumn: true,
      gridColumnEnd: true,
      gridColumnStart: true,
      gridRow: true,
      gridRowEnd: true,
      gridRowStart: true,
      lineHeight: true,
      opacity: true,
      order: true,
      orphans: true,
      scale: true,
      widows: true,
      zIndex: true,
      zoom: true,
      // SVG-related
      fillOpacity: true,
      floodOpacity: true,
      stopOpacity: true,
      strokeMiterlimit: true,
      strokeOpacity: true
    },
    // Add in properties whose names you wish to fix before
    // setting or getting the value
    cssProps: {},
    // Get and set the style property on a DOM Node
    style: function (elem, name, value, extra) {
      // Don't set styles on text and comment nodes
      if (!elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style) {
        return;
      }

      // Make sure that we're working with the right name
      var ret,
        type,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name),
        style = elem.style;

      // Make sure that we're working with the right name. We don't
      // want to query the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Gets hook for the prefixed version, then unprefixed version
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // Check if we're setting a value
      if (value !== undefined) {
        type = typeof value;

        // Convert "+=" or "-=" to relative numbers (trac-7345)
        if (type === "string" && (ret = rcssNum.exec(value)) && ret[1]) {
          value = adjustCSS(elem, name, ret);

          // Fixes bug trac-9237
          type = "number";
        }

        // Make sure that null and NaN values aren't set (trac-7116)
        if (value == null || value !== value) {
          return;
        }

        // If a number was passed in, add the unit (except for certain CSS properties)
        // The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
        // "px" to a few hardcoded values.
        if (type === "number" && !isCustomProp) {
          value += ret && ret[3] || (jQuery.cssNumber[origName] ? "" : "px");
        }

        // background-* props affect original clone's values
        if (!support.clearCloneStyle && value === "" && name.indexOf("background") === 0) {
          style[name] = "inherit";
        }

        // If a hook was provided, use that value, otherwise just set the specified value
        if (!hooks || !("set" in hooks) || (value = hooks.set(elem, value, extra)) !== undefined) {
          if (isCustomProp) {
            style.setProperty(name, value);
          } else {
            style[name] = value;
          }
        }
      } else {
        // If a hook was provided get the non-computed value from there
        if (hooks && "get" in hooks && (ret = hooks.get(elem, false, extra)) !== undefined) {
          return ret;
        }

        // Otherwise just get the value from the style object
        return style[name];
      }
    },
    css: function (elem, name, extra, styles) {
      var val,
        num,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name);

      // Make sure that we're working with the right name. We don't
      // want to modify the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Try prefixed name followed by the unprefixed name
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // If a hook was provided get the computed value from there
      if (hooks && "get" in hooks) {
        val = hooks.get(elem, true, extra);
      }

      // Otherwise, if a way to get the computed value exists, use that
      if (val === undefined) {
        val = curCSS(elem, name, styles);
      }

      // Convert "normal" to computed value
      if (val === "normal" && name in cssNormalTransform) {
        val = cssNormalTransform[name];
      }

      // Make numeric if forced or a qualifier was provided and val looks numeric
      if (extra === "" || extra) {
        num = parseFloat(val);
        return extra === true || isFinite(num) ? num || 0 : val;
      }
      return val;
    }
  });
  jQuery.each(["height", "width"], function (_i, dimension) {
    jQuery.cssHooks[dimension] = {
      get: function (elem, computed, extra) {
        if (computed) {
          // Certain elements can have dimension info if we invisibly show them
          // but it must have a current display style that would benefit
          return rdisplayswap.test(jQuery.css(elem, "display")) && (
          // Support: Safari 8+
          // Table columns in Safari have non-zero offsetWidth & zero
          // getBoundingClientRect().width unless display is changed.
          // Support: IE <=11 only
          // Running getBoundingClientRect on a disconnected node
          // in IE throws an error.
          !elem.getClientRects().length || !elem.getBoundingClientRect().width) ? swap(elem, cssShow, function () {
            return getWidthOrHeight(elem, dimension, extra);
          }) : getWidthOrHeight(elem, dimension, extra);
        }
      },
      set: function (elem, value, extra) {
        var matches,
          styles = getStyles(elem),
          // Only read styles.position if the test has a chance to fail
          // to avoid forcing a reflow.
          scrollboxSizeBuggy = !support.scrollboxSize() && styles.position === "absolute",
          // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
          boxSizingNeeded = scrollboxSizeBuggy || extra,
          isBorderBox = boxSizingNeeded && jQuery.css(elem, "boxSizing", false, styles) === "border-box",
          subtract = extra ? boxModelAdjustment(elem, dimension, extra, isBorderBox, styles) : 0;

        // Account for unreliable border-box dimensions by comparing offset* to computed and
        // faking a content-box to get border and padding (gh-3699)
        if (isBorderBox && scrollboxSizeBuggy) {
          subtract -= Math.ceil(elem["offset" + dimension[0].toUpperCase() + dimension.slice(1)] - parseFloat(styles[dimension]) - boxModelAdjustment(elem, dimension, "border", false, styles) - 0.5);
        }

        // Convert to pixels if value adjustment is needed
        if (subtract && (matches = rcssNum.exec(value)) && (matches[3] || "px") !== "px") {
          elem.style[dimension] = value;
          value = jQuery.css(elem, dimension);
        }
        return setPositiveNumber(elem, value, subtract);
      }
    };
  });
  jQuery.cssHooks.marginLeft = addGetHookIf(support.reliableMarginLeft, function (elem, computed) {
    if (computed) {
      return (parseFloat(curCSS(elem, "marginLeft")) || elem.getBoundingClientRect().left - swap(elem, {
        marginLeft: 0
      }, function () {
        return elem.getBoundingClientRect().left;
      })) + "px";
    }
  });

  // These hooks are used by animate to expand properties
  jQuery.each({
    margin: "",
    padding: "",
    border: "Width"
  }, function (prefix, suffix) {
    jQuery.cssHooks[prefix + suffix] = {
      expand: function (value) {
        var i = 0,
          expanded = {},
          // Assumes a single number if not a string
          parts = typeof value === "string" ? value.split(" ") : [value];
        for (; i < 4; i++) {
          expanded[prefix + cssExpand[i] + suffix] = parts[i] || parts[i - 2] || parts[0];
        }
        return expanded;
      }
    };
    if (prefix !== "margin") {
      jQuery.cssHooks[prefix + suffix].set = setPositiveNumber;
    }
  });
  jQuery.fn.extend({
    css: function (name, value) {
      return access(this, function (elem, name, value) {
        var styles,
          len,
          map = {},
          i = 0;
        if (Array.isArray(name)) {
          styles = getStyles(elem);
          len = name.length;
          for (; i < len; i++) {
            map[name[i]] = jQuery.css(elem, name[i], false, styles);
          }
          return map;
        }
        return value !== undefined ? jQuery.style(elem, name, value) : jQuery.css(elem, name);
      }, name, value, arguments.length > 1);
    }
  });
  function Tween(elem, options, prop, end, easing) {
    return new Tween.prototype.init(elem, options, prop, end, easing);
  }
  jQuery.Tween = Tween;
  Tween.prototype = {
    constructor: Tween,
    init: function (elem, options, prop, end, easing, unit) {
      this.elem = elem;
      this.prop = prop;
      this.easing = easing || jQuery.easing._default;
      this.options = options;
      this.start = this.now = this.cur();
      this.end = end;
      this.unit = unit || (jQuery.cssNumber[prop] ? "" : "px");
    },
    cur: function () {
      var hooks = Tween.propHooks[this.prop];
      return hooks && hooks.get ? hooks.get(this) : Tween.propHooks._default.get(this);
    },
    run: function (percent) {
      var eased,
        hooks = Tween.propHooks[this.prop];
      if (this.options.duration) {
        this.pos = eased = jQuery.easing[this.easing](percent, this.options.duration * percent, 0, 1, this.options.duration);
      } else {
        this.pos = eased = percent;
      }
      this.now = (this.end - this.start) * eased + this.start;
      if (this.options.step) {
        this.options.step.call(this.elem, this.now, this);
      }
      if (hooks && hooks.set) {
        hooks.set(this);
      } else {
        Tween.propHooks._default.set(this);
      }
      return this;
    }
  };
  Tween.prototype.init.prototype = Tween.prototype;
  Tween.propHooks = {
    _default: {
      get: function (tween) {
        var result;

        // Use a property on the element directly when it is not a DOM element,
        // or when there is no matching style property that exists.
        if (tween.elem.nodeType !== 1 || tween.elem[tween.prop] != null && tween.elem.style[tween.prop] == null) {
          return tween.elem[tween.prop];
        }

        // Passing an empty string as a 3rd parameter to .css will automatically
        // attempt a parseFloat and fallback to a string if the parse fails.
        // Simple values such as "10px" are parsed to Float;
        // complex values such as "rotate(1rad)" are returned as-is.
        result = jQuery.css(tween.elem, tween.prop, "");

        // Empty strings, null, undefined and "auto" are converted to 0.
        return !result || result === "auto" ? 0 : result;
      },
      set: function (tween) {
        // Use step hook for back compat.
        // Use cssHook if its there.
        // Use .style if available and use plain properties where available.
        if (jQuery.fx.step[tween.prop]) {
          jQuery.fx.step[tween.prop](tween);
        } else if (tween.elem.nodeType === 1 && (jQuery.cssHooks[tween.prop] || tween.elem.style[finalPropName(tween.prop)] != null)) {
          jQuery.style(tween.elem, tween.prop, tween.now + tween.unit);
        } else {
          tween.elem[tween.prop] = tween.now;
        }
      }
    }
  };

  // Support: IE <=9 only
  // Panic based approach to setting things on disconnected nodes
  Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
    set: function (tween) {
      if (tween.elem.nodeType && tween.elem.parentNode) {
        tween.elem[tween.prop] = tween.now;
      }
    }
  };
  jQuery.easing = {
    linear: function (p) {
      return p;
    },
    swing: function (p) {
      return 0.5 - Math.cos(p * Math.PI) / 2;
    },
    _default: "swing"
  };
  jQuery.fx = Tween.prototype.init;

  // Back compat <1.8 extension point
  jQuery.fx.step = {};
  var fxNow,
    inProgress,
    rfxtypes = /^(?:toggle|show|hide)$/,
    rrun = /queueHooks$/;
  function schedule() {
    if (inProgress) {
      if (document.hidden === false && window.requestAnimationFrame) {
        window.requestAnimationFrame(schedule);
      } else {
        window.setTimeout(schedule, jQuery.fx.interval);
      }
      jQuery.fx.tick();
    }
  }

  // Animations created synchronously will run synchronously
  function createFxNow() {
    window.setTimeout(function () {
      fxNow = undefined;
    });
    return fxNow = Date.now();
  }

  // Generate parameters to create a standard animation
  function genFx(type, includeWidth) {
    var which,
      i = 0,
      attrs = {
        height: type
      };

    // If we include width, step value is 1 to do all cssExpand values,
    // otherwise step value is 2 to skip over Left and Right
    includeWidth = includeWidth ? 1 : 0;
    for (; i < 4; i += 2 - includeWidth) {
      which = cssExpand[i];
      attrs["margin" + which] = attrs["padding" + which] = type;
    }
    if (includeWidth) {
      attrs.opacity = attrs.width = type;
    }
    return attrs;
  }
  function createTween(value, prop, animation) {
    var tween,
      collection = (Animation.tweeners[prop] || []).concat(Animation.tweeners["*"]),
      index = 0,
      length = collection.length;
    for (; index < length; index++) {
      if (tween = collection[index].call(animation, prop, value)) {
        // We're done with this property
        return tween;
      }
    }
  }
  function defaultPrefilter(elem, props, opts) {
    var prop,
      value,
      toggle,
      hooks,
      oldfire,
      propTween,
      restoreDisplay,
      display,
      isBox = "width" in props || "height" in props,
      anim = this,
      orig = {},
      style = elem.style,
      hidden = elem.nodeType && isHiddenWithinTree(elem),
      dataShow = dataPriv.get(elem, "fxshow");

    // Queue-skipping animations hijack the fx hooks
    if (!opts.queue) {
      hooks = jQuery._queueHooks(elem, "fx");
      if (hooks.unqueued == null) {
        hooks.unqueued = 0;
        oldfire = hooks.empty.fire;
        hooks.empty.fire = function () {
          if (!hooks.unqueued) {
            oldfire();
          }
        };
      }
      hooks.unqueued++;
      anim.always(function () {
        // Ensure the complete handler is called before this completes
        anim.always(function () {
          hooks.unqueued--;
          if (!jQuery.queue(elem, "fx").length) {
            hooks.empty.fire();
          }
        });
      });
    }

    // Detect show/hide animations
    for (prop in props) {
      value = props[prop];
      if (rfxtypes.test(value)) {
        delete props[prop];
        toggle = toggle || value === "toggle";
        if (value === (hidden ? "hide" : "show")) {
          // Pretend to be hidden if this is a "show" and
          // there is still data from a stopped show/hide
          if (value === "show" && dataShow && dataShow[prop] !== undefined) {
            hidden = true;

            // Ignore all other no-op show/hide data
          } else {
            continue;
          }
        }
        orig[prop] = dataShow && dataShow[prop] || jQuery.style(elem, prop);
      }
    }

    // Bail out if this is a no-op like .hide().hide()
    propTween = !jQuery.isEmptyObject(props);
    if (!propTween && jQuery.isEmptyObject(orig)) {
      return;
    }

    // Restrict "overflow" and "display" styles during box animations
    if (isBox && elem.nodeType === 1) {
      // Support: IE <=9 - 11, Edge 12 - 15
      // Record all 3 overflow attributes because IE does not infer the shorthand
      // from identically-valued overflowX and overflowY and Edge just mirrors
      // the overflowX value there.
      opts.overflow = [style.overflow, style.overflowX, style.overflowY];

      // Identify a display type, preferring old show/hide data over the CSS cascade
      restoreDisplay = dataShow && dataShow.display;
      if (restoreDisplay == null) {
        restoreDisplay = dataPriv.get(elem, "display");
      }
      display = jQuery.css(elem, "display");
      if (display === "none") {
        if (restoreDisplay) {
          display = restoreDisplay;
        } else {
          // Get nonempty value(s) by temporarily forcing visibility
          showHide([elem], true);
          restoreDisplay = elem.style.display || restoreDisplay;
          display = jQuery.css(elem, "display");
          showHide([elem]);
        }
      }

      // Animate inline elements as inline-block
      if (display === "inline" || display === "inline-block" && restoreDisplay != null) {
        if (jQuery.css(elem, "float") === "none") {
          // Restore the original display value at the end of pure show/hide animations
          if (!propTween) {
            anim.done(function () {
              style.display = restoreDisplay;
            });
            if (restoreDisplay == null) {
              display = style.display;
              restoreDisplay = display === "none" ? "" : display;
            }
          }
          style.display = "inline-block";
        }
      }
    }
    if (opts.overflow) {
      style.overflow = "hidden";
      anim.always(function () {
        style.overflow = opts.overflow[0];
        style.overflowX = opts.overflow[1];
        style.overflowY = opts.overflow[2];
      });
    }

    // Implement show/hide animations
    propTween = false;
    for (prop in orig) {
      // General show/hide setup for this element animation
      if (!propTween) {
        if (dataShow) {
          if ("hidden" in dataShow) {
            hidden = dataShow.hidden;
          }
        } else {
          dataShow = dataPriv.access(elem, "fxshow", {
            display: restoreDisplay
          });
        }

        // Store hidden/visible for toggle so `.stop().toggle()` "reverses"
        if (toggle) {
          dataShow.hidden = !hidden;
        }

        // Show elements before animating them
        if (hidden) {
          showHide([elem], true);
        }

        /* eslint-disable no-loop-func */

        anim.done(function () {
          /* eslint-enable no-loop-func */

          // The final step of a "hide" animation is actually hiding the element
          if (!hidden) {
            showHide([elem]);
          }
          dataPriv.remove(elem, "fxshow");
          for (prop in orig) {
            jQuery.style(elem, prop, orig[prop]);
          }
        });
      }

      // Per-property setup
      propTween = createTween(hidden ? dataShow[prop] : 0, prop, anim);
      if (!(prop in dataShow)) {
        dataShow[prop] = propTween.start;
        if (hidden) {
          propTween.end = propTween.start;
          propTween.start = 0;
        }
      }
    }
  }
  function propFilter(props, specialEasing) {
    var index, name, easing, value, hooks;

    // camelCase, specialEasing and expand cssHook pass
    for (index in props) {
      name = camelCase(index);
      easing = specialEasing[name];
      value = props[index];
      if (Array.isArray(value)) {
        easing = value[1];
        value = props[index] = value[0];
      }
      if (index !== name) {
        props[name] = value;
        delete props[index];
      }
      hooks = jQuery.cssHooks[name];
      if (hooks && "expand" in hooks) {
        value = hooks.expand(value);
        delete props[name];

        // Not quite $.extend, this won't overwrite existing keys.
        // Reusing 'index' because we have the correct "name"
        for (index in value) {
          if (!(index in props)) {
            props[index] = value[index];
            specialEasing[index] = easing;
          }
        }
      } else {
        specialEasing[name] = easing;
      }
    }
  }
  function Animation(elem, properties, options) {
    var result,
      stopped,
      index = 0,
      length = Animation.prefilters.length,
      deferred = jQuery.Deferred().always(function () {
        // Don't match elem in the :animated selector
        delete tick.elem;
      }),
      tick = function () {
        if (stopped) {
          return false;
        }
        var currentTime = fxNow || createFxNow(),
          remaining = Math.max(0, animation.startTime + animation.duration - currentTime),
          // Support: Android 2.3 only
          // Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (trac-12497)
          temp = remaining / animation.duration || 0,
          percent = 1 - temp,
          index = 0,
          length = animation.tweens.length;
        for (; index < length; index++) {
          animation.tweens[index].run(percent);
        }
        deferred.notifyWith(elem, [animation, percent, remaining]);

        // If there's more to do, yield
        if (percent < 1 && length) {
          return remaining;
        }

        // If this was an empty animation, synthesize a final progress notification
        if (!length) {
          deferred.notifyWith(elem, [animation, 1, 0]);
        }

        // Resolve the animation and report its conclusion
        deferred.resolveWith(elem, [animation]);
        return false;
      },
      animation = deferred.promise({
        elem: elem,
        props: jQuery.extend({}, properties),
        opts: jQuery.extend(true, {
          specialEasing: {},
          easing: jQuery.easing._default
        }, options),
        originalProperties: properties,
        originalOptions: options,
        startTime: fxNow || createFxNow(),
        duration: options.duration,
        tweens: [],
        createTween: function (prop, end) {
          var tween = jQuery.Tween(elem, animation.opts, prop, end, animation.opts.specialEasing[prop] || animation.opts.easing);
          animation.tweens.push(tween);
          return tween;
        },
        stop: function (gotoEnd) {
          var index = 0,
            // If we are going to the end, we want to run all the tweens
            // otherwise we skip this part
            length = gotoEnd ? animation.tweens.length : 0;
          if (stopped) {
            return this;
          }
          stopped = true;
          for (; index < length; index++) {
            animation.tweens[index].run(1);
          }

          // Resolve when we played the last frame; otherwise, reject
          if (gotoEnd) {
            deferred.notifyWith(elem, [animation, 1, 0]);
            deferred.resolveWith(elem, [animation, gotoEnd]);
          } else {
            deferred.rejectWith(elem, [animation, gotoEnd]);
          }
          return this;
        }
      }),
      props = animation.props;
    propFilter(props, animation.opts.specialEasing);
    for (; index < length; index++) {
      result = Animation.prefilters[index].call(animation, elem, props, animation.opts);
      if (result) {
        if (isFunction(result.stop)) {
          jQuery._queueHooks(animation.elem, animation.opts.queue).stop = result.stop.bind(result);
        }
        return result;
      }
    }
    jQuery.map(props, createTween, animation);
    if (isFunction(animation.opts.start)) {
      animation.opts.start.call(elem, animation);
    }

    // Attach callbacks from options
    animation.progress(animation.opts.progress).done(animation.opts.done, animation.opts.complete).fail(animation.opts.fail).always(animation.opts.always);
    jQuery.fx.timer(jQuery.extend(tick, {
      elem: elem,
      anim: animation,
      queue: animation.opts.queue
    }));
    return animation;
  }
  jQuery.Animation = jQuery.extend(Animation, {
    tweeners: {
      "*": [function (prop, value) {
        var tween = this.createTween(prop, value);
        adjustCSS(tween.elem, prop, rcssNum.exec(value), tween);
        return tween;
      }]
    },
    tweener: function (props, callback) {
      if (isFunction(props)) {
        callback = props;
        props = ["*"];
      } else {
        props = props.match(rnothtmlwhite);
      }
      var prop,
        index = 0,
        length = props.length;
      for (; index < length; index++) {
        prop = props[index];
        Animation.tweeners[prop] = Animation.tweeners[prop] || [];
        Animation.tweeners[prop].unshift(callback);
      }
    },
    prefilters: [defaultPrefilter],
    prefilter: function (callback, prepend) {
      if (prepend) {
        Animation.prefilters.unshift(callback);
      } else {
        Animation.prefilters.push(callback);
      }
    }
  });
  jQuery.speed = function (speed, easing, fn) {
    var opt = speed && typeof speed === "object" ? jQuery.extend({}, speed) : {
      complete: fn || !fn && easing || isFunction(speed) && speed,
      duration: speed,
      easing: fn && easing || easing && !isFunction(easing) && easing
    };

    // Go to the end state if fx are off
    if (jQuery.fx.off) {
      opt.duration = 0;
    } else {
      if (typeof opt.duration !== "number") {
        if (opt.duration in jQuery.fx.speeds) {
          opt.duration = jQuery.fx.speeds[opt.duration];
        } else {
          opt.duration = jQuery.fx.speeds._default;
        }
      }
    }

    // Normalize opt.queue - true/undefined/null -> "fx"
    if (opt.queue == null || opt.queue === true) {
      opt.queue = "fx";
    }

    // Queueing
    opt.old = opt.complete;
    opt.complete = function () {
      if (isFunction(opt.old)) {
        opt.old.call(this);
      }
      if (opt.queue) {
        jQuery.dequeue(this, opt.queue);
      }
    };
    return opt;
  };
  jQuery.fn.extend({
    fadeTo: function (speed, to, easing, callback) {
      // Show any hidden elements after setting opacity to 0
      return this.filter(isHiddenWithinTree).css("opacity", 0).show()

      // Animate to the value specified
      .end().animate({
        opacity: to
      }, speed, easing, callback);
    },
    animate: function (prop, speed, easing, callback) {
      var empty = jQuery.isEmptyObject(prop),
        optall = jQuery.speed(speed, easing, callback),
        doAnimation = function () {
          // Operate on a copy of prop so per-property easing won't be lost
          var anim = Animation(this, jQuery.extend({}, prop), optall);

          // Empty animations, or finishing resolves immediately
          if (empty || dataPriv.get(this, "finish")) {
            anim.stop(true);
          }
        };
      doAnimation.finish = doAnimation;
      return empty || optall.queue === false ? this.each(doAnimation) : this.queue(optall.queue, doAnimation);
    },
    stop: function (type, clearQueue, gotoEnd) {
      var stopQueue = function (hooks) {
        var stop = hooks.stop;
        delete hooks.stop;
        stop(gotoEnd);
      };
      if (typeof type !== "string") {
        gotoEnd = clearQueue;
        clearQueue = type;
        type = undefined;
      }
      if (clearQueue) {
        this.queue(type || "fx", []);
      }
      return this.each(function () {
        var dequeue = true,
          index = type != null && type + "queueHooks",
          timers = jQuery.timers,
          data = dataPriv.get(this);
        if (index) {
          if (data[index] && data[index].stop) {
            stopQueue(data[index]);
          }
        } else {
          for (index in data) {
            if (data[index] && data[index].stop && rrun.test(index)) {
              stopQueue(data[index]);
            }
          }
        }
        for (index = timers.length; index--;) {
          if (timers[index].elem === this && (type == null || timers[index].queue === type)) {
            timers[index].anim.stop(gotoEnd);
            dequeue = false;
            timers.splice(index, 1);
          }
        }

        // Start the next in the queue if the last step wasn't forced.
        // Timers currently will call their complete callbacks, which
        // will dequeue but only if they were gotoEnd.
        if (dequeue || !gotoEnd) {
          jQuery.dequeue(this, type);
        }
      });
    },
    finish: function (type) {
      if (type !== false) {
        type = type || "fx";
      }
      return this.each(function () {
        var index,
          data = dataPriv.get(this),
          queue = data[type + "queue"],
          hooks = data[type + "queueHooks"],
          timers = jQuery.timers,
          length = queue ? queue.length : 0;

        // Enable finishing flag on private data
        data.finish = true;

        // Empty the queue first
        jQuery.queue(this, type, []);
        if (hooks && hooks.stop) {
          hooks.stop.call(this, true);
        }

        // Look for any active animations, and finish them
        for (index = timers.length; index--;) {
          if (timers[index].elem === this && timers[index].queue === type) {
            timers[index].anim.stop(true);
            timers.splice(index, 1);
          }
        }

        // Look for any animations in the old queue and finish them
        for (index = 0; index < length; index++) {
          if (queue[index] && queue[index].finish) {
            queue[index].finish.call(this);
          }
        }

        // Turn off finishing flag
        delete data.finish;
      });
    }
  });
  jQuery.each(["toggle", "show", "hide"], function (_i, name) {
    var cssFn = jQuery.fn[name];
    jQuery.fn[name] = function (speed, easing, callback) {
      return speed == null || typeof speed === "boolean" ? cssFn.apply(this, arguments) : this.animate(genFx(name, true), speed, easing, callback);
    };
  });

  // Generate shortcuts for custom animations
  jQuery.each({
    slideDown: genFx("show"),
    slideUp: genFx("hide"),
    slideToggle: genFx("toggle"),
    fadeIn: {
      opacity: "show"
    },
    fadeOut: {
      opacity: "hide"
    },
    fadeToggle: {
      opacity: "toggle"
    }
  }, function (name, props) {
    jQuery.fn[name] = function (speed, easing, callback) {
      return this.animate(props, speed, easing, callback);
    };
  });
  jQuery.timers = [];
  jQuery.fx.tick = function () {
    var timer,
      i = 0,
      timers = jQuery.timers;
    fxNow = Date.now();
    for (; i < timers.length; i++) {
      timer = timers[i];

      // Run the timer and safely remove it when done (allowing for external removal)
      if (!timer() && timers[i] === timer) {
        timers.splice(i--, 1);
      }
    }
    if (!timers.length) {
      jQuery.fx.stop();
    }
    fxNow = undefined;
  };
  jQuery.fx.timer = function (timer) {
    jQuery.timers.push(timer);
    jQuery.fx.start();
  };
  jQuery.fx.interval = 13;
  jQuery.fx.start = function () {
    if (inProgress) {
      return;
    }
    inProgress = true;
    schedule();
  };
  jQuery.fx.stop = function () {
    inProgress = null;
  };
  jQuery.fx.speeds = {
    slow: 600,
    fast: 200,
    // Default speed
    _default: 400
  };

  // Based off of the plugin by Clint Helfers, with permission.
  jQuery.fn.delay = function (time, type) {
    time = jQuery.fx ? jQuery.fx.speeds[time] || time : time;
    type = type || "fx";
    return this.queue(type, function (next, hooks) {
      var timeout = window.setTimeout(next, time);
      hooks.stop = function () {
        window.clearTimeout(timeout);
      };
    });
  };
  (function () {
    var input = document.createElement("input"),
      select = document.createElement("select"),
      opt = select.appendChild(document.createElement("option"));
    input.type = "checkbox";

    // Support: Android <=4.3 only
    // Default value for a checkbox should be "on"
    support.checkOn = input.value !== "";

    // Support: IE <=11 only
    // Must access selectedIndex to make default options select
    support.optSelected = opt.selected;

    // Support: IE <=11 only
    // An input loses its value after becoming a radio
    input = document.createElement("input");
    input.value = "t";
    input.type = "radio";
    support.radioValue = input.value === "t";
  })();
  var boolHook,
    attrHandle = jQuery.expr.attrHandle;
  jQuery.fn.extend({
    attr: function (name, value) {
      return access(this, jQuery.attr, name, value, arguments.length > 1);
    },
    removeAttr: function (name) {
      return this.each(function () {
        jQuery.removeAttr(this, name);
      });
    }
  });
  jQuery.extend({
    attr: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set attributes on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }

      // Fallback to prop when attributes are not supported
      if (typeof elem.getAttribute === "undefined") {
        return jQuery.prop(elem, name, value);
      }

      // Attribute hooks are determined by the lowercase version
      // Grab necessary hook if one is defined
      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        hooks = jQuery.attrHooks[name.toLowerCase()] || (jQuery.expr.match.bool.test(name) ? boolHook : undefined);
      }
      if (value !== undefined) {
        if (value === null) {
          jQuery.removeAttr(elem, name);
          return;
        }
        if (hooks && "set" in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }
        elem.setAttribute(name, value + "");
        return value;
      }
      if (hooks && "get" in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }
      ret = jQuery.find.attr(elem, name);

      // Non-existent attributes return null, we normalize to undefined
      return ret == null ? undefined : ret;
    },
    attrHooks: {
      type: {
        set: function (elem, value) {
          if (!support.radioValue && value === "radio" && nodeName(elem, "input")) {
            var val = elem.value;
            elem.setAttribute("type", value);
            if (val) {
              elem.value = val;
            }
            return value;
          }
        }
      }
    },
    removeAttr: function (elem, value) {
      var name,
        i = 0,
        // Attribute names can contain non-HTML whitespace characters
        // https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
        attrNames = value && value.match(rnothtmlwhite);
      if (attrNames && elem.nodeType === 1) {
        while (name = attrNames[i++]) {
          elem.removeAttribute(name);
        }
      }
    }
  });

  // Hooks for boolean attributes
  boolHook = {
    set: function (elem, value, name) {
      if (value === false) {
        // Remove boolean attributes when set to false
        jQuery.removeAttr(elem, name);
      } else {
        elem.setAttribute(name, name);
      }
      return name;
    }
  };
  jQuery.each(jQuery.expr.match.bool.source.match(/\w+/g), function (_i, name) {
    var getter = attrHandle[name] || jQuery.find.attr;
    attrHandle[name] = function (elem, name, isXML) {
      var ret,
        handle,
        lowercaseName = name.toLowerCase();
      if (!isXML) {
        // Avoid an infinite loop by temporarily removing this function from the getter
        handle = attrHandle[lowercaseName];
        attrHandle[lowercaseName] = ret;
        ret = getter(elem, name, isXML) != null ? lowercaseName : null;
        attrHandle[lowercaseName] = handle;
      }
      return ret;
    };
  });
  var rfocusable = /^(?:input|select|textarea|button)$/i,
    rclickable = /^(?:a|area)$/i;
  jQuery.fn.extend({
    prop: function (name, value) {
      return access(this, jQuery.prop, name, value, arguments.length > 1);
    },
    removeProp: function (name) {
      return this.each(function () {
        delete this[jQuery.propFix[name] || name];
      });
    }
  });
  jQuery.extend({
    prop: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set properties on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }
      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        // Fix name and attach hooks
        name = jQuery.propFix[name] || name;
        hooks = jQuery.propHooks[name];
      }
      if (value !== undefined) {
        if (hooks && "set" in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }
        return elem[name] = value;
      }
      if (hooks && "get" in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }
      return elem[name];
    },
    propHooks: {
      tabIndex: {
        get: function (elem) {
          // Support: IE <=9 - 11 only
          // elem.tabIndex doesn't always return the
          // correct value when it hasn't been explicitly set
          // Use proper attribute retrieval (trac-12072)
          var tabindex = jQuery.find.attr(elem, "tabindex");
          if (tabindex) {
            return parseInt(tabindex, 10);
          }
          if (rfocusable.test(elem.nodeName) || rclickable.test(elem.nodeName) && elem.href) {
            return 0;
          }
          return -1;
        }
      }
    },
    propFix: {
      "for": "htmlFor",
      "class": "className"
    }
  });

  // Support: IE <=11 only
  // Accessing the selectedIndex property
  // forces the browser to respect setting selected
  // on the option
  // The getter ensures a default option is selected
  // when in an optgroup
  // eslint rule "no-unused-expressions" is disabled for this code
  // since it considers such accessions noop
  if (!support.optSelected) {
    jQuery.propHooks.selected = {
      get: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent && parent.parentNode) {
          parent.parentNode.selectedIndex;
        }
        return null;
      },
      set: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent) {
          parent.selectedIndex;
          if (parent.parentNode) {
            parent.parentNode.selectedIndex;
          }
        }
      }
    };
  }
  jQuery.each(["tabIndex", "readOnly", "maxLength", "cellSpacing", "cellPadding", "rowSpan", "colSpan", "useMap", "frameBorder", "contentEditable"], function () {
    jQuery.propFix[this.toLowerCase()] = this;
  });

  // Strip and collapse whitespace according to HTML spec
  // https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
  function stripAndCollapse(value) {
    var tokens = value.match(rnothtmlwhite) || [];
    return tokens.join(" ");
  }
  function getClass(elem) {
    return elem.getAttribute && elem.getAttribute("class") || "";
  }
  function classesToArray(value) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === "string") {
      return value.match(rnothtmlwhite) || [];
    }
    return [];
  }
  jQuery.fn.extend({
    addClass: function (value) {
      var classNames, cur, curValue, className, i, finalValue;
      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).addClass(value.call(this, j, getClass(this)));
        });
      }
      classNames = classesToArray(value);
      if (classNames.length) {
        return this.each(function () {
          curValue = getClass(this);
          cur = this.nodeType === 1 && " " + stripAndCollapse(curValue) + " ";
          if (cur) {
            for (i = 0; i < classNames.length; i++) {
              className = classNames[i];
              if (cur.indexOf(" " + className + " ") < 0) {
                cur += className + " ";
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              this.setAttribute("class", finalValue);
            }
          }
        });
      }
      return this;
    },
    removeClass: function (value) {
      var classNames, cur, curValue, className, i, finalValue;
      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).removeClass(value.call(this, j, getClass(this)));
        });
      }
      if (!arguments.length) {
        return this.attr("class", "");
      }
      classNames = classesToArray(value);
      if (classNames.length) {
        return this.each(function () {
          curValue = getClass(this);

          // This expression is here for better compressibility (see addClass)
          cur = this.nodeType === 1 && " " + stripAndCollapse(curValue) + " ";
          if (cur) {
            for (i = 0; i < classNames.length; i++) {
              className = classNames[i];

              // Remove *all* instances
              while (cur.indexOf(" " + className + " ") > -1) {
                cur = cur.replace(" " + className + " ", " ");
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              this.setAttribute("class", finalValue);
            }
          }
        });
      }
      return this;
    },
    toggleClass: function (value, stateVal) {
      var classNames,
        className,
        i,
        self,
        type = typeof value,
        isValidValue = type === "string" || Array.isArray(value);
      if (isFunction(value)) {
        return this.each(function (i) {
          jQuery(this).toggleClass(value.call(this, i, getClass(this), stateVal), stateVal);
        });
      }
      if (typeof stateVal === "boolean" && isValidValue) {
        return stateVal ? this.addClass(value) : this.removeClass(value);
      }
      classNames = classesToArray(value);
      return this.each(function () {
        if (isValidValue) {
          // Toggle individual class names
          self = jQuery(this);
          for (i = 0; i < classNames.length; i++) {
            className = classNames[i];

            // Check each className given, space separated list
            if (self.hasClass(className)) {
              self.removeClass(className);
            } else {
              self.addClass(className);
            }
          }

          // Toggle whole class name
        } else if (value === undefined || type === "boolean") {
          className = getClass(this);
          if (className) {
            // Store className if set
            dataPriv.set(this, "__className__", className);
          }

          // If the element has a class name or if we're passed `false`,
          // then remove the whole classname (if there was one, the above saved it).
          // Otherwise bring back whatever was previously saved (if anything),
          // falling back to the empty string if nothing was stored.
          if (this.setAttribute) {
            this.setAttribute("class", className || value === false ? "" : dataPriv.get(this, "__className__") || "");
          }
        }
      });
    },
    hasClass: function (selector) {
      var className,
        elem,
        i = 0;
      className = " " + selector + " ";
      while (elem = this[i++]) {
        if (elem.nodeType === 1 && (" " + stripAndCollapse(getClass(elem)) + " ").indexOf(className) > -1) {
          return true;
        }
      }
      return false;
    }
  });
  var rreturn = /\r/g;
  jQuery.fn.extend({
    val: function (value) {
      var hooks,
        ret,
        valueIsFunction,
        elem = this[0];
      if (!arguments.length) {
        if (elem) {
          hooks = jQuery.valHooks[elem.type] || jQuery.valHooks[elem.nodeName.toLowerCase()];
          if (hooks && "get" in hooks && (ret = hooks.get(elem, "value")) !== undefined) {
            return ret;
          }
          ret = elem.value;

          // Handle most common string cases
          if (typeof ret === "string") {
            return ret.replace(rreturn, "");
          }

          // Handle cases where value is null/undef or number
          return ret == null ? "" : ret;
        }
        return;
      }
      valueIsFunction = isFunction(value);
      return this.each(function (i) {
        var val;
        if (this.nodeType !== 1) {
          return;
        }
        if (valueIsFunction) {
          val = value.call(this, i, jQuery(this).val());
        } else {
          val = value;
        }

        // Treat null/undefined as ""; convert numbers to string
        if (val == null) {
          val = "";
        } else if (typeof val === "number") {
          val += "";
        } else if (Array.isArray(val)) {
          val = jQuery.map(val, function (value) {
            return value == null ? "" : value + "";
          });
        }
        hooks = jQuery.valHooks[this.type] || jQuery.valHooks[this.nodeName.toLowerCase()];

        // If set returns undefined, fall back to normal setting
        if (!hooks || !("set" in hooks) || hooks.set(this, val, "value") === undefined) {
          this.value = val;
        }
      });
    }
  });
  jQuery.extend({
    valHooks: {
      option: {
        get: function (elem) {
          var val = jQuery.find.attr(elem, "value");
          return val != null ? val :
          // Support: IE <=10 - 11 only
          // option.text throws exceptions (trac-14686, trac-14858)
          // Strip and collapse whitespace
          // https://html.spec.whatwg.org/#strip-and-collapse-whitespace
          stripAndCollapse(jQuery.text(elem));
        }
      },
      select: {
        get: function (elem) {
          var value,
            option,
            i,
            options = elem.options,
            index = elem.selectedIndex,
            one = elem.type === "select-one",
            values = one ? null : [],
            max = one ? index + 1 : options.length;
          if (index < 0) {
            i = max;
          } else {
            i = one ? index : 0;
          }

          // Loop through all the selected options
          for (; i < max; i++) {
            option = options[i];

            // Support: IE <=9 only
            // IE8-9 doesn't update selected after form reset (trac-2551)
            if ((option.selected || i === index) &&
            // Don't return options that are disabled or in a disabled optgroup
            !option.disabled && (!option.parentNode.disabled || !nodeName(option.parentNode, "optgroup"))) {
              // Get the specific value for the option
              value = jQuery(option).val();

              // We don't need an array for one selects
              if (one) {
                return value;
              }

              // Multi-Selects return an array
              values.push(value);
            }
          }
          return values;
        },
        set: function (elem, value) {
          var optionSet,
            option,
            options = elem.options,
            values = jQuery.makeArray(value),
            i = options.length;
          while (i--) {
            option = options[i];

            /* eslint-disable no-cond-assign */

            if (option.selected = jQuery.inArray(jQuery.valHooks.option.get(option), values) > -1) {
              optionSet = true;
            }

            /* eslint-enable no-cond-assign */
          }

          // Force browsers to behave consistently when non-matching value is set
          if (!optionSet) {
            elem.selectedIndex = -1;
          }
          return values;
        }
      }
    }
  });

  // Radios and checkboxes getter/setter
  jQuery.each(["radio", "checkbox"], function () {
    jQuery.valHooks[this] = {
      set: function (elem, value) {
        if (Array.isArray(value)) {
          return elem.checked = jQuery.inArray(jQuery(elem).val(), value) > -1;
        }
      }
    };
    if (!support.checkOn) {
      jQuery.valHooks[this].get = function (elem) {
        return elem.getAttribute("value") === null ? "on" : elem.value;
      };
    }
  });

  // Return jQuery for attributes-only inclusion
  var location = window.location;
  var nonce = {
    guid: Date.now()
  };
  var rquery = /\?/;

  // Cross-browser xml parsing
  jQuery.parseXML = function (data) {
    var xml, parserErrorElem;
    if (!data || typeof data !== "string") {
      return null;
    }

    // Support: IE 9 - 11 only
    // IE throws on parseFromString with invalid input.
    try {
      xml = new window.DOMParser().parseFromString(data, "text/xml");
    } catch (e) {}
    parserErrorElem = xml && xml.getElementsByTagName("parsererror")[0];
    if (!xml || parserErrorElem) {
      jQuery.error("Invalid XML: " + (parserErrorElem ? jQuery.map(parserErrorElem.childNodes, function (el) {
        return el.textContent;
      }).join("\n") : data));
    }
    return xml;
  };
  var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
    stopPropagationCallback = function (e) {
      e.stopPropagation();
    };
  jQuery.extend(jQuery.event, {
    trigger: function (event, data, elem, onlyHandlers) {
      var i,
        cur,
        tmp,
        bubbleType,
        ontype,
        handle,
        special,
        lastElement,
        eventPath = [elem || document],
        type = hasOwn.call(event, "type") ? event.type : event,
        namespaces = hasOwn.call(event, "namespace") ? event.namespace.split(".") : [];
      cur = lastElement = tmp = elem = elem || document;

      // Don't do events on text and comment nodes
      if (elem.nodeType === 3 || elem.nodeType === 8) {
        return;
      }

      // focus/blur morphs to focusin/out; ensure we're not firing them right now
      if (rfocusMorph.test(type + jQuery.event.triggered)) {
        return;
      }
      if (type.indexOf(".") > -1) {
        // Namespaced trigger; create a regexp to match event type in handle()
        namespaces = type.split(".");
        type = namespaces.shift();
        namespaces.sort();
      }
      ontype = type.indexOf(":") < 0 && "on" + type;

      // Caller can pass in a jQuery.Event object, Object, or just an event type string
      event = event[jQuery.expando] ? event : new jQuery.Event(type, typeof event === "object" && event);

      // Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
      event.isTrigger = onlyHandlers ? 2 : 3;
      event.namespace = namespaces.join(".");
      event.rnamespace = event.namespace ? new RegExp("(^|\\.)" + namespaces.join("\\.(?:.*\\.|)") + "(\\.|$)") : null;

      // Clean up the event in case it is being reused
      event.result = undefined;
      if (!event.target) {
        event.target = elem;
      }

      // Clone any incoming data and prepend the event, creating the handler arg list
      data = data == null ? [event] : jQuery.makeArray(data, [event]);

      // Allow special events to draw outside the lines
      special = jQuery.event.special[type] || {};
      if (!onlyHandlers && special.trigger && special.trigger.apply(elem, data) === false) {
        return;
      }

      // Determine event propagation path in advance, per W3C events spec (trac-9951)
      // Bubble up to document, then to window; watch for a global ownerDocument var (trac-9724)
      if (!onlyHandlers && !special.noBubble && !isWindow(elem)) {
        bubbleType = special.delegateType || type;
        if (!rfocusMorph.test(bubbleType + type)) {
          cur = cur.parentNode;
        }
        for (; cur; cur = cur.parentNode) {
          eventPath.push(cur);
          tmp = cur;
        }

        // Only add window if we got to document (e.g., not plain obj or detached DOM)
        if (tmp === (elem.ownerDocument || document)) {
          eventPath.push(tmp.defaultView || tmp.parentWindow || window);
        }
      }

      // Fire handlers on the event path
      i = 0;
      while ((cur = eventPath[i++]) && !event.isPropagationStopped()) {
        lastElement = cur;
        event.type = i > 1 ? bubbleType : special.bindType || type;

        // jQuery handler
        handle = (dataPriv.get(cur, "events") || Object.create(null))[event.type] && dataPriv.get(cur, "handle");
        if (handle) {
          handle.apply(cur, data);
        }

        // Native handler
        handle = ontype && cur[ontype];
        if (handle && handle.apply && acceptData(cur)) {
          event.result = handle.apply(cur, data);
          if (event.result === false) {
            event.preventDefault();
          }
        }
      }
      event.type = type;

      // If nobody prevented the default action, do it now
      if (!onlyHandlers && !event.isDefaultPrevented()) {
        if ((!special._default || special._default.apply(eventPath.pop(), data) === false) && acceptData(elem)) {
          // Call a native DOM method on the target with the same name as the event.
          // Don't do default actions on window, that's where global variables be (trac-6170)
          if (ontype && isFunction(elem[type]) && !isWindow(elem)) {
            // Don't re-trigger an onFOO event when we call its FOO() method
            tmp = elem[ontype];
            if (tmp) {
              elem[ontype] = null;
            }

            // Prevent re-triggering of the same event, since we already bubbled it above
            jQuery.event.triggered = type;
            if (event.isPropagationStopped()) {
              lastElement.addEventListener(type, stopPropagationCallback);
            }
            elem[type]();
            if (event.isPropagationStopped()) {
              lastElement.removeEventListener(type, stopPropagationCallback);
            }
            jQuery.event.triggered = undefined;
            if (tmp) {
              elem[ontype] = tmp;
            }
          }
        }
      }
      return event.result;
    },
    // Piggyback on a donor event to simulate a different one
    // Used only for `focus(in | out)` events
    simulate: function (type, elem, event) {
      var e = jQuery.extend(new jQuery.Event(), event, {
        type: type,
        isSimulated: true
      });
      jQuery.event.trigger(e, null, elem);
    }
  });
  jQuery.fn.extend({
    trigger: function (type, data) {
      return this.each(function () {
        jQuery.event.trigger(type, data, this);
      });
    },
    triggerHandler: function (type, data) {
      var elem = this[0];
      if (elem) {
        return jQuery.event.trigger(type, data, elem, true);
      }
    }
  });
  var rbracket = /\[\]$/,
    rCRLF = /\r?\n/g,
    rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
    rsubmittable = /^(?:input|select|textarea|keygen)/i;
  function buildParams(prefix, obj, traditional, add) {
    var name;
    if (Array.isArray(obj)) {
      // Serialize array item.
      jQuery.each(obj, function (i, v) {
        if (traditional || rbracket.test(prefix)) {
          // Treat each array item as a scalar.
          add(prefix, v);
        } else {
          // Item is non-scalar (array or object), encode its numeric index.
          buildParams(prefix + "[" + (typeof v === "object" && v != null ? i : "") + "]", v, traditional, add);
        }
      });
    } else if (!traditional && toType(obj) === "object") {
      // Serialize object item.
      for (name in obj) {
        buildParams(prefix + "[" + name + "]", obj[name], traditional, add);
      }
    } else {
      // Serialize scalar item.
      add(prefix, obj);
    }
  }

  // Serialize an array of form elements or a set of
  // key/values into a query string
  jQuery.param = function (a, traditional) {
    var prefix,
      s = [],
      add = function (key, valueOrFunction) {
        // If value is a function, invoke it and use its return value
        var value = isFunction(valueOrFunction) ? valueOrFunction() : valueOrFunction;
        s[s.length] = encodeURIComponent(key) + "=" + encodeURIComponent(value == null ? "" : value);
      };
    if (a == null) {
      return "";
    }

    // If an array was passed in, assume that it is an array of form elements.
    if (Array.isArray(a) || a.jquery && !jQuery.isPlainObject(a)) {
      // Serialize the form elements
      jQuery.each(a, function () {
        add(this.name, this.value);
      });
    } else {
      // If traditional, encode the "old" way (the way 1.3.2 or older
      // did it), otherwise encode params recursively.
      for (prefix in a) {
        buildParams(prefix, a[prefix], traditional, add);
      }
    }

    // Return the resulting serialization
    return s.join("&");
  };
  jQuery.fn.extend({
    serialize: function () {
      return jQuery.param(this.serializeArray());
    },
    serializeArray: function () {
      return this.map(function () {
        // Can add propHook for "elements" to filter or add form elements
        var elements = jQuery.prop(this, "elements");
        return elements ? jQuery.makeArray(elements) : this;
      }).filter(function () {
        var type = this.type;

        // Use .is( ":disabled" ) so that fieldset[disabled] works
        return this.name && !jQuery(this).is(":disabled") && rsubmittable.test(this.nodeName) && !rsubmitterTypes.test(type) && (this.checked || !rcheckableType.test(type));
      }).map(function (_i, elem) {
        var val = jQuery(this).val();
        if (val == null) {
          return null;
        }
        if (Array.isArray(val)) {
          return jQuery.map(val, function (val) {
            return {
              name: elem.name,
              value: val.replace(rCRLF, "\r\n")
            };
          });
        }
        return {
          name: elem.name,
          value: val.replace(rCRLF, "\r\n")
        };
      }).get();
    }
  });
  var r20 = /%20/g,
    rhash = /#.*$/,
    rantiCache = /([?&])_=[^&]*/,
    rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg,
    // trac-7653, trac-8125, trac-8152: local protocol detection
    rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
    rnoContent = /^(?:GET|HEAD)$/,
    rprotocol = /^\/\//,
    /* Prefilters
     * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
     * 2) These are called:
     *    - BEFORE asking for a transport
     *    - AFTER param serialization (s.data is a string if s.processData is true)
     * 3) key is the dataType
     * 4) the catchall symbol "*" can be used
     * 5) execution will start with transport dataType and THEN continue down to "*" if needed
     */
    prefilters = {},
    /* Transports bindings
     * 1) key is the dataType
     * 2) the catchall symbol "*" can be used
     * 3) selection will start with transport dataType and THEN go to "*" if needed
     */
    transports = {},
    // Avoid comment-prolog char sequence (trac-10098); must appease lint and evade compression
    allTypes = "*/".concat("*"),
    // Anchor tag for parsing the document origin
    originAnchor = document.createElement("a");
  originAnchor.href = location.href;

  // Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
  function addToPrefiltersOrTransports(structure) {
    // dataTypeExpression is optional and defaults to "*"
    return function (dataTypeExpression, func) {
      if (typeof dataTypeExpression !== "string") {
        func = dataTypeExpression;
        dataTypeExpression = "*";
      }
      var dataType,
        i = 0,
        dataTypes = dataTypeExpression.toLowerCase().match(rnothtmlwhite) || [];
      if (isFunction(func)) {
        // For each dataType in the dataTypeExpression
        while (dataType = dataTypes[i++]) {
          // Prepend if requested
          if (dataType[0] === "+") {
            dataType = dataType.slice(1) || "*";
            (structure[dataType] = structure[dataType] || []).unshift(func);

            // Otherwise append
          } else {
            (structure[dataType] = structure[dataType] || []).push(func);
          }
        }
      }
    };
  }

  // Base inspection function for prefilters and transports
  function inspectPrefiltersOrTransports(structure, options, originalOptions, jqXHR) {
    var inspected = {},
      seekingTransport = structure === transports;
    function inspect(dataType) {
      var selected;
      inspected[dataType] = true;
      jQuery.each(structure[dataType] || [], function (_, prefilterOrFactory) {
        var dataTypeOrTransport = prefilterOrFactory(options, originalOptions, jqXHR);
        if (typeof dataTypeOrTransport === "string" && !seekingTransport && !inspected[dataTypeOrTransport]) {
          options.dataTypes.unshift(dataTypeOrTransport);
          inspect(dataTypeOrTransport);
          return false;
        } else if (seekingTransport) {
          return !(selected = dataTypeOrTransport);
        }
      });
      return selected;
    }
    return inspect(options.dataTypes[0]) || !inspected["*"] && inspect("*");
  }

  // A special extend for ajax options
  // that takes "flat" options (not to be deep extended)
  // Fixes trac-9887
  function ajaxExtend(target, src) {
    var key,
      deep,
      flatOptions = jQuery.ajaxSettings.flatOptions || {};
    for (key in src) {
      if (src[key] !== undefined) {
        (flatOptions[key] ? target : deep || (deep = {}))[key] = src[key];
      }
    }
    if (deep) {
      jQuery.extend(true, target, deep);
    }
    return target;
  }

  /* Handles responses to an ajax request:
   * - finds the right dataType (mediates between content-type and expected dataType)
   * - returns the corresponding response
   */
  function ajaxHandleResponses(s, jqXHR, responses) {
    var ct,
      type,
      finalDataType,
      firstDataType,
      contents = s.contents,
      dataTypes = s.dataTypes;

    // Remove auto dataType and get content-type in the process
    while (dataTypes[0] === "*") {
      dataTypes.shift();
      if (ct === undefined) {
        ct = s.mimeType || jqXHR.getResponseHeader("Content-Type");
      }
    }

    // Check if we're dealing with a known content-type
    if (ct) {
      for (type in contents) {
        if (contents[type] && contents[type].test(ct)) {
          dataTypes.unshift(type);
          break;
        }
      }
    }

    // Check to see if we have a response for the expected dataType
    if (dataTypes[0] in responses) {
      finalDataType = dataTypes[0];
    } else {
      // Try convertible dataTypes
      for (type in responses) {
        if (!dataTypes[0] || s.converters[type + " " + dataTypes[0]]) {
          finalDataType = type;
          break;
        }
        if (!firstDataType) {
          firstDataType = type;
        }
      }

      // Or just use first one
      finalDataType = finalDataType || firstDataType;
    }

    // If we found a dataType
    // We add the dataType to the list if needed
    // and return the corresponding response
    if (finalDataType) {
      if (finalDataType !== dataTypes[0]) {
        dataTypes.unshift(finalDataType);
      }
      return responses[finalDataType];
    }
  }

  /* Chain conversions given the request and the original response
   * Also sets the responseXXX fields on the jqXHR instance
   */
  function ajaxConvert(s, response, jqXHR, isSuccess) {
    var conv2,
      current,
      conv,
      tmp,
      prev,
      converters = {},
      // Work with a copy of dataTypes in case we need to modify it for conversion
      dataTypes = s.dataTypes.slice();

    // Create converters map with lowercased keys
    if (dataTypes[1]) {
      for (conv in s.converters) {
        converters[conv.toLowerCase()] = s.converters[conv];
      }
    }
    current = dataTypes.shift();

    // Convert to each sequential dataType
    while (current) {
      if (s.responseFields[current]) {
        jqXHR[s.responseFields[current]] = response;
      }

      // Apply the dataFilter if provided
      if (!prev && isSuccess && s.dataFilter) {
        response = s.dataFilter(response, s.dataType);
      }
      prev = current;
      current = dataTypes.shift();
      if (current) {
        // There's only work to do if current dataType is non-auto
        if (current === "*") {
          current = prev;

          // Convert response if prev dataType is non-auto and differs from current
        } else if (prev !== "*" && prev !== current) {
          // Seek a direct converter
          conv = converters[prev + " " + current] || converters["* " + current];

          // If none found, seek a pair
          if (!conv) {
            for (conv2 in converters) {
              // If conv2 outputs current
              tmp = conv2.split(" ");
              if (tmp[1] === current) {
                // If prev can be converted to accepted input
                conv = converters[prev + " " + tmp[0]] || converters["* " + tmp[0]];
                if (conv) {
                  // Condense equivalence converters
                  if (conv === true) {
                    conv = converters[conv2];

                    // Otherwise, insert the intermediate dataType
                  } else if (converters[conv2] !== true) {
                    current = tmp[0];
                    dataTypes.unshift(tmp[1]);
                  }
                  break;
                }
              }
            }
          }

          // Apply converter (if not an equivalence)
          if (conv !== true) {
            // Unless errors are allowed to bubble, catch and return them
            if (conv && s.throws) {
              response = conv(response);
            } else {
              try {
                response = conv(response);
              } catch (e) {
                return {
                  state: "parsererror",
                  error: conv ? e : "No conversion from " + prev + " to " + current
                };
              }
            }
          }
        }
      }
    }
    return {
      state: "success",
      data: response
    };
  }
  jQuery.extend({
    // Counter for holding the number of active queries
    active: 0,
    // Last-Modified header cache for next request
    lastModified: {},
    etag: {},
    ajaxSettings: {
      url: location.href,
      type: "GET",
      isLocal: rlocalProtocol.test(location.protocol),
      global: true,
      processData: true,
      async: true,
      contentType: "application/x-www-form-urlencoded; charset=UTF-8",
      /*
      timeout: 0,
      data: null,
      dataType: null,
      username: null,
      password: null,
      cache: null,
      throws: false,
      traditional: false,
      headers: {},
      */

      accepts: {
        "*": allTypes,
        text: "text/plain",
        html: "text/html",
        xml: "application/xml, text/xml",
        json: "application/json, text/javascript"
      },
      contents: {
        xml: /\bxml\b/,
        html: /\bhtml/,
        json: /\bjson\b/
      },
      responseFields: {
        xml: "responseXML",
        text: "responseText",
        json: "responseJSON"
      },
      // Data converters
      // Keys separate source (or catchall "*") and destination types with a single space
      converters: {
        // Convert anything to text
        "* text": String,
        // Text to html (true = no transformation)
        "text html": true,
        // Evaluate text as a json expression
        "text json": JSON.parse,
        // Parse text as xml
        "text xml": jQuery.parseXML
      },
      // For options that shouldn't be deep extended:
      // you can add your own custom options here if
      // and when you create one that shouldn't be
      // deep extended (see ajaxExtend)
      flatOptions: {
        url: true,
        context: true
      }
    },
    // Creates a full fledged settings object into target
    // with both ajaxSettings and settings fields.
    // If target is omitted, writes into ajaxSettings.
    ajaxSetup: function (target, settings) {
      return settings ?
      // Building a settings object
      ajaxExtend(ajaxExtend(target, jQuery.ajaxSettings), settings) :
      // Extending ajaxSettings
      ajaxExtend(jQuery.ajaxSettings, target);
    },
    ajaxPrefilter: addToPrefiltersOrTransports(prefilters),
    ajaxTransport: addToPrefiltersOrTransports(transports),
    // Main method
    ajax: function (url, options) {
      // If url is an object, simulate pre-1.5 signature
      if (typeof url === "object") {
        options = url;
        url = undefined;
      }

      // Force options to be an object
      options = options || {};
      var transport,
        // URL without anti-cache param
        cacheURL,
        // Response headers
        responseHeadersString,
        responseHeaders,
        // timeout handle
        timeoutTimer,
        // Url cleanup var
        urlAnchor,
        // Request state (becomes false upon send and true upon completion)
        completed,
        // To know if global events are to be dispatched
        fireGlobals,
        // Loop variable
        i,
        // uncached part of the url
        uncached,
        // Create the final options object
        s = jQuery.ajaxSetup({}, options),
        // Callbacks context
        callbackContext = s.context || s,
        // Context for global events is callbackContext if it is a DOM node or jQuery collection
        globalEventContext = s.context && (callbackContext.nodeType || callbackContext.jquery) ? jQuery(callbackContext) : jQuery.event,
        // Deferreds
        deferred = jQuery.Deferred(),
        completeDeferred = jQuery.Callbacks("once memory"),
        // Status-dependent callbacks
        statusCode = s.statusCode || {},
        // Headers (they are sent all at once)
        requestHeaders = {},
        requestHeadersNames = {},
        // Default abort message
        strAbort = "canceled",
        // Fake xhr
        jqXHR = {
          readyState: 0,
          // Builds headers hashtable if needed
          getResponseHeader: function (key) {
            var match;
            if (completed) {
              if (!responseHeaders) {
                responseHeaders = {};
                while (match = rheaders.exec(responseHeadersString)) {
                  responseHeaders[match[1].toLowerCase() + " "] = (responseHeaders[match[1].toLowerCase() + " "] || []).concat(match[2]);
                }
              }
              match = responseHeaders[key.toLowerCase() + " "];
            }
            return match == null ? null : match.join(", ");
          },
          // Raw string
          getAllResponseHeaders: function () {
            return completed ? responseHeadersString : null;
          },
          // Caches the header
          setRequestHeader: function (name, value) {
            if (completed == null) {
              name = requestHeadersNames[name.toLowerCase()] = requestHeadersNames[name.toLowerCase()] || name;
              requestHeaders[name] = value;
            }
            return this;
          },
          // Overrides response content-type header
          overrideMimeType: function (type) {
            if (completed == null) {
              s.mimeType = type;
            }
            return this;
          },
          // Status-dependent callbacks
          statusCode: function (map) {
            var code;
            if (map) {
              if (completed) {
                // Execute the appropriate callbacks
                jqXHR.always(map[jqXHR.status]);
              } else {
                // Lazy-add the new callbacks in a way that preserves old ones
                for (code in map) {
                  statusCode[code] = [statusCode[code], map[code]];
                }
              }
            }
            return this;
          },
          // Cancel the request
          abort: function (statusText) {
            var finalText = statusText || strAbort;
            if (transport) {
              transport.abort(finalText);
            }
            done(0, finalText);
            return this;
          }
        };

      // Attach deferreds
      deferred.promise(jqXHR);

      // Add protocol if not provided (prefilters might expect it)
      // Handle falsy url in the settings object (trac-10093: consistency with old signature)
      // We also use the url parameter if available
      s.url = ((url || s.url || location.href) + "").replace(rprotocol, location.protocol + "//");

      // Alias method option to type as per ticket trac-12004
      s.type = options.method || options.type || s.method || s.type;

      // Extract dataTypes list
      s.dataTypes = (s.dataType || "*").toLowerCase().match(rnothtmlwhite) || [""];

      // A cross-domain request is in order when the origin doesn't match the current origin.
      if (s.crossDomain == null) {
        urlAnchor = document.createElement("a");

        // Support: IE <=8 - 11, Edge 12 - 15
        // IE throws exception on accessing the href property if url is malformed,
        // e.g. http://example.com:80x/
        try {
          urlAnchor.href = s.url;

          // Support: IE <=8 - 11 only
          // Anchor's host property isn't correctly set when s.url is relative
          urlAnchor.href = urlAnchor.href;
          s.crossDomain = originAnchor.protocol + "//" + originAnchor.host !== urlAnchor.protocol + "//" + urlAnchor.host;
        } catch (e) {
          // If there is an error parsing the URL, assume it is crossDomain,
          // it can be rejected by the transport if it is invalid
          s.crossDomain = true;
        }
      }

      // Convert data if not already a string
      if (s.data && s.processData && typeof s.data !== "string") {
        s.data = jQuery.param(s.data, s.traditional);
      }

      // Apply prefilters
      inspectPrefiltersOrTransports(prefilters, s, options, jqXHR);

      // If request was aborted inside a prefilter, stop there
      if (completed) {
        return jqXHR;
      }

      // We can fire global events as of now if asked to
      // Don't fire events if jQuery.event is undefined in an AMD-usage scenario (trac-15118)
      fireGlobals = jQuery.event && s.global;

      // Watch for a new set of requests
      if (fireGlobals && jQuery.active++ === 0) {
        jQuery.event.trigger("ajaxStart");
      }

      // Uppercase the type
      s.type = s.type.toUpperCase();

      // Determine if request has content
      s.hasContent = !rnoContent.test(s.type);

      // Save the URL in case we're toying with the If-Modified-Since
      // and/or If-None-Match header later on
      // Remove hash to simplify url manipulation
      cacheURL = s.url.replace(rhash, "");

      // More options handling for requests with no content
      if (!s.hasContent) {
        // Remember the hash so we can put it back
        uncached = s.url.slice(cacheURL.length);

        // If data is available and should be processed, append data to url
        if (s.data && (s.processData || typeof s.data === "string")) {
          cacheURL += (rquery.test(cacheURL) ? "&" : "?") + s.data;

          // trac-9682: remove data so that it's not used in an eventual retry
          delete s.data;
        }

        // Add or update anti-cache param if needed
        if (s.cache === false) {
          cacheURL = cacheURL.replace(rantiCache, "$1");
          uncached = (rquery.test(cacheURL) ? "&" : "?") + "_=" + nonce.guid++ + uncached;
        }

        // Put hash and anti-cache on the URL that will be requested (gh-1732)
        s.url = cacheURL + uncached;

        // Change '%20' to '+' if this is encoded form body content (gh-2658)
      } else if (s.data && s.processData && (s.contentType || "").indexOf("application/x-www-form-urlencoded") === 0) {
        s.data = s.data.replace(r20, "+");
      }

      // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
      if (s.ifModified) {
        if (jQuery.lastModified[cacheURL]) {
          jqXHR.setRequestHeader("If-Modified-Since", jQuery.lastModified[cacheURL]);
        }
        if (jQuery.etag[cacheURL]) {
          jqXHR.setRequestHeader("If-None-Match", jQuery.etag[cacheURL]);
        }
      }

      // Set the correct header, if data is being sent
      if (s.data && s.hasContent && s.contentType !== false || options.contentType) {
        jqXHR.setRequestHeader("Content-Type", s.contentType);
      }

      // Set the Accepts header for the server, depending on the dataType
      jqXHR.setRequestHeader("Accept", s.dataTypes[0] && s.accepts[s.dataTypes[0]] ? s.accepts[s.dataTypes[0]] + (s.dataTypes[0] !== "*" ? ", " + allTypes + "; q=0.01" : "") : s.accepts["*"]);

      // Check for headers option
      for (i in s.headers) {
        jqXHR.setRequestHeader(i, s.headers[i]);
      }

      // Allow custom headers/mimetypes and early abort
      if (s.beforeSend && (s.beforeSend.call(callbackContext, jqXHR, s) === false || completed)) {
        // Abort if not done already and return
        return jqXHR.abort();
      }

      // Aborting is no longer a cancellation
      strAbort = "abort";

      // Install callbacks on deferreds
      completeDeferred.add(s.complete);
      jqXHR.done(s.success);
      jqXHR.fail(s.error);

      // Get transport
      transport = inspectPrefiltersOrTransports(transports, s, options, jqXHR);

      // If no transport, we auto-abort
      if (!transport) {
        done(-1, "No Transport");
      } else {
        jqXHR.readyState = 1;

        // Send global event
        if (fireGlobals) {
          globalEventContext.trigger("ajaxSend", [jqXHR, s]);
        }

        // If request was aborted inside ajaxSend, stop there
        if (completed) {
          return jqXHR;
        }

        // Timeout
        if (s.async && s.timeout > 0) {
          timeoutTimer = window.setTimeout(function () {
            jqXHR.abort("timeout");
          }, s.timeout);
        }
        try {
          completed = false;
          transport.send(requestHeaders, done);
        } catch (e) {
          // Rethrow post-completion exceptions
          if (completed) {
            throw e;
          }

          // Propagate others as results
          done(-1, e);
        }
      }

      // Callback for when everything is done
      function done(status, nativeStatusText, responses, headers) {
        var isSuccess,
          success,
          error,
          response,
          modified,
          statusText = nativeStatusText;

        // Ignore repeat invocations
        if (completed) {
          return;
        }
        completed = true;

        // Clear timeout if it exists
        if (timeoutTimer) {
          window.clearTimeout(timeoutTimer);
        }

        // Dereference transport for early garbage collection
        // (no matter how long the jqXHR object will be used)
        transport = undefined;

        // Cache response headers
        responseHeadersString = headers || "";

        // Set readyState
        jqXHR.readyState = status > 0 ? 4 : 0;

        // Determine if successful
        isSuccess = status >= 200 && status < 300 || status === 304;

        // Get response data
        if (responses) {
          response = ajaxHandleResponses(s, jqXHR, responses);
        }

        // Use a noop converter for missing script but not if jsonp
        if (!isSuccess && jQuery.inArray("script", s.dataTypes) > -1 && jQuery.inArray("json", s.dataTypes) < 0) {
          s.converters["text script"] = function () {};
        }

        // Convert no matter what (that way responseXXX fields are always set)
        response = ajaxConvert(s, response, jqXHR, isSuccess);

        // If successful, handle type chaining
        if (isSuccess) {
          // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
          if (s.ifModified) {
            modified = jqXHR.getResponseHeader("Last-Modified");
            if (modified) {
              jQuery.lastModified[cacheURL] = modified;
            }
            modified = jqXHR.getResponseHeader("etag");
            if (modified) {
              jQuery.etag[cacheURL] = modified;
            }
          }

          // if no content
          if (status === 204 || s.type === "HEAD") {
            statusText = "nocontent";

            // if not modified
          } else if (status === 304) {
            statusText = "notmodified";

            // If we have data, let's convert it
          } else {
            statusText = response.state;
            success = response.data;
            error = response.error;
            isSuccess = !error;
          }
        } else {
          // Extract error from statusText and normalize for non-aborts
          error = statusText;
          if (status || !statusText) {
            statusText = "error";
            if (status < 0) {
              status = 0;
            }
          }
        }

        // Set data for the fake xhr object
        jqXHR.status = status;
        jqXHR.statusText = (nativeStatusText || statusText) + "";

        // Success/Error
        if (isSuccess) {
          deferred.resolveWith(callbackContext, [success, statusText, jqXHR]);
        } else {
          deferred.rejectWith(callbackContext, [jqXHR, statusText, error]);
        }

        // Status-dependent callbacks
        jqXHR.statusCode(statusCode);
        statusCode = undefined;
        if (fireGlobals) {
          globalEventContext.trigger(isSuccess ? "ajaxSuccess" : "ajaxError", [jqXHR, s, isSuccess ? success : error]);
        }

        // Complete
        completeDeferred.fireWith(callbackContext, [jqXHR, statusText]);
        if (fireGlobals) {
          globalEventContext.trigger("ajaxComplete", [jqXHR, s]);

          // Handle the global AJAX counter
          if (! --jQuery.active) {
            jQuery.event.trigger("ajaxStop");
          }
        }
      }
      return jqXHR;
    },
    getJSON: function (url, data, callback) {
      return jQuery.get(url, data, callback, "json");
    },
    getScript: function (url, callback) {
      return jQuery.get(url, undefined, callback, "script");
    }
  });
  jQuery.each(["get", "post"], function (_i, method) {
    jQuery[method] = function (url, data, callback, type) {
      // Shift arguments if data argument was omitted
      if (isFunction(data)) {
        type = type || callback;
        callback = data;
        data = undefined;
      }

      // The url can be an options object (which then must have .url)
      return jQuery.ajax(jQuery.extend({
        url: url,
        type: method,
        dataType: type,
        data: data,
        success: callback
      }, jQuery.isPlainObject(url) && url));
    };
  });
  jQuery.ajaxPrefilter(function (s) {
    var i;
    for (i in s.headers) {
      if (i.toLowerCase() === "content-type") {
        s.contentType = s.headers[i] || "";
      }
    }
  });
  jQuery._evalUrl = function (url, options, doc) {
    return jQuery.ajax({
      url: url,
      // Make this explicit, since user can override this through ajaxSetup (trac-11264)
      type: "GET",
      dataType: "script",
      cache: true,
      async: false,
      global: false,
      // Only evaluate the response if it is successful (gh-4126)
      // dataFilter is not invoked for failure responses, so using it instead
      // of the default converter is kludgy but it works.
      converters: {
        "text script": function () {}
      },
      dataFilter: function (response) {
        jQuery.globalEval(response, options, doc);
      }
    });
  };
  jQuery.fn.extend({
    wrapAll: function (html) {
      var wrap;
      if (this[0]) {
        if (isFunction(html)) {
          html = html.call(this[0]);
        }

        // The elements to wrap the target around
        wrap = jQuery(html, this[0].ownerDocument).eq(0).clone(true);
        if (this[0].parentNode) {
          wrap.insertBefore(this[0]);
        }
        wrap.map(function () {
          var elem = this;
          while (elem.firstElementChild) {
            elem = elem.firstElementChild;
          }
          return elem;
        }).append(this);
      }
      return this;
    },
    wrapInner: function (html) {
      if (isFunction(html)) {
        return this.each(function (i) {
          jQuery(this).wrapInner(html.call(this, i));
        });
      }
      return this.each(function () {
        var self = jQuery(this),
          contents = self.contents();
        if (contents.length) {
          contents.wrapAll(html);
        } else {
          self.append(html);
        }
      });
    },
    wrap: function (html) {
      var htmlIsFunction = isFunction(html);
      return this.each(function (i) {
        jQuery(this).wrapAll(htmlIsFunction ? html.call(this, i) : html);
      });
    },
    unwrap: function (selector) {
      this.parent(selector).not("body").each(function () {
        jQuery(this).replaceWith(this.childNodes);
      });
      return this;
    }
  });
  jQuery.expr.pseudos.hidden = function (elem) {
    return !jQuery.expr.pseudos.visible(elem);
  };
  jQuery.expr.pseudos.visible = function (elem) {
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
  };
  jQuery.ajaxSettings.xhr = function () {
    try {
      return new window.XMLHttpRequest();
    } catch (e) {}
  };
  var xhrSuccessStatus = {
      // File protocol always yields status code 0, assume 200
      0: 200,
      // Support: IE <=9 only
      // trac-1450: sometimes IE returns 1223 when it should be 204
      1223: 204
    },
    xhrSupported = jQuery.ajaxSettings.xhr();
  support.cors = !!xhrSupported && "withCredentials" in xhrSupported;
  support.ajax = xhrSupported = !!xhrSupported;
  jQuery.ajaxTransport(function (options) {
    var callback, errorCallback;

    // Cross domain only allowed if supported through XMLHttpRequest
    if (support.cors || xhrSupported && !options.crossDomain) {
      return {
        send: function (headers, complete) {
          var i,
            xhr = options.xhr();
          xhr.open(options.type, options.url, options.async, options.username, options.password);

          // Apply custom fields if provided
          if (options.xhrFields) {
            for (i in options.xhrFields) {
              xhr[i] = options.xhrFields[i];
            }
          }

          // Override mime type if needed
          if (options.mimeType && xhr.overrideMimeType) {
            xhr.overrideMimeType(options.mimeType);
          }

          // X-Requested-With header
          // For cross-domain requests, seeing as conditions for a preflight are
          // akin to a jigsaw puzzle, we simply never set it to be sure.
          // (it can always be set on a per-request basis or even using ajaxSetup)
          // For same-domain requests, won't change header if already provided.
          if (!options.crossDomain && !headers["X-Requested-With"]) {
            headers["X-Requested-With"] = "XMLHttpRequest";
          }

          // Set headers
          for (i in headers) {
            xhr.setRequestHeader(i, headers[i]);
          }

          // Callback
          callback = function (type) {
            return function () {
              if (callback) {
                callback = errorCallback = xhr.onload = xhr.onerror = xhr.onabort = xhr.ontimeout = xhr.onreadystatechange = null;
                if (type === "abort") {
                  xhr.abort();
                } else if (type === "error") {
                  // Support: IE <=9 only
                  // On a manual native abort, IE9 throws
                  // errors on any property access that is not readyState
                  if (typeof xhr.status !== "number") {
                    complete(0, "error");
                  } else {
                    complete(
                    // File: protocol always yields status 0; see trac-8605, trac-14207
                    xhr.status, xhr.statusText);
                  }
                } else {
                  complete(xhrSuccessStatus[xhr.status] || xhr.status, xhr.statusText,
                  // Support: IE <=9 only
                  // IE9 has no XHR2 but throws on binary (trac-11426)
                  // For XHR2 non-text, let the caller handle it (gh-2498)
                  (xhr.responseType || "text") !== "text" || typeof xhr.responseText !== "string" ? {
                    binary: xhr.response
                  } : {
                    text: xhr.responseText
                  }, xhr.getAllResponseHeaders());
                }
              }
            };
          };

          // Listen to events
          xhr.onload = callback();
          errorCallback = xhr.onerror = xhr.ontimeout = callback("error");

          // Support: IE 9 only
          // Use onreadystatechange to replace onabort
          // to handle uncaught aborts
          if (xhr.onabort !== undefined) {
            xhr.onabort = errorCallback;
          } else {
            xhr.onreadystatechange = function () {
              // Check readyState before timeout as it changes
              if (xhr.readyState === 4) {
                // Allow onerror to be called first,
                // but that will not handle a native abort
                // Also, save errorCallback to a variable
                // as xhr.onerror cannot be accessed
                window.setTimeout(function () {
                  if (callback) {
                    errorCallback();
                  }
                });
              }
            };
          }

          // Create the abort callback
          callback = callback("abort");
          try {
            // Do send the request (this may raise an exception)
            xhr.send(options.hasContent && options.data || null);
          } catch (e) {
            // trac-14683: Only rethrow if this hasn't been notified as an error yet
            if (callback) {
              throw e;
            }
          }
        },
        abort: function () {
          if (callback) {
            callback();
          }
        }
      };
    }
  });

  // Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
  jQuery.ajaxPrefilter(function (s) {
    if (s.crossDomain) {
      s.contents.script = false;
    }
  });

  // Install script dataType
  jQuery.ajaxSetup({
    accepts: {
      script: "text/javascript, application/javascript, " + "application/ecmascript, application/x-ecmascript"
    },
    contents: {
      script: /\b(?:java|ecma)script\b/
    },
    converters: {
      "text script": function (text) {
        jQuery.globalEval(text);
        return text;
      }
    }
  });

  // Handle cache's special case and crossDomain
  jQuery.ajaxPrefilter("script", function (s) {
    if (s.cache === undefined) {
      s.cache = false;
    }
    if (s.crossDomain) {
      s.type = "GET";
    }
  });

  // Bind script tag hack transport
  jQuery.ajaxTransport("script", function (s) {
    // This transport only deals with cross domain or forced-by-attrs requests
    if (s.crossDomain || s.scriptAttrs) {
      var script, callback;
      return {
        send: function (_, complete) {
          script = jQuery("<script>").attr(s.scriptAttrs || {}).prop({
            charset: s.scriptCharset,
            src: s.url
          }).on("load error", callback = function (evt) {
            script.remove();
            callback = null;
            if (evt) {
              complete(evt.type === "error" ? 404 : 200, evt.type);
            }
          });

          // Use native DOM manipulation to avoid our domManip AJAX trickery
          document.head.appendChild(script[0]);
        },
        abort: function () {
          if (callback) {
            callback();
          }
        }
      };
    }
  });
  var oldCallbacks = [],
    rjsonp = /(=)\?(?=&|$)|\?\?/;

  // Default jsonp settings
  jQuery.ajaxSetup({
    jsonp: "callback",
    jsonpCallback: function () {
      var callback = oldCallbacks.pop() || jQuery.expando + "_" + nonce.guid++;
      this[callback] = true;
      return callback;
    }
  });

  // Detect, normalize options and install callbacks for jsonp requests
  jQuery.ajaxPrefilter("json jsonp", function (s, originalSettings, jqXHR) {
    var callbackName,
      overwritten,
      responseContainer,
      jsonProp = s.jsonp !== false && (rjsonp.test(s.url) ? "url" : typeof s.data === "string" && (s.contentType || "").indexOf("application/x-www-form-urlencoded") === 0 && rjsonp.test(s.data) && "data");

    // Handle iff the expected data type is "jsonp" or we have a parameter to set
    if (jsonProp || s.dataTypes[0] === "jsonp") {
      // Get callback name, remembering preexisting value associated with it
      callbackName = s.jsonpCallback = isFunction(s.jsonpCallback) ? s.jsonpCallback() : s.jsonpCallback;

      // Insert callback into url or form data
      if (jsonProp) {
        s[jsonProp] = s[jsonProp].replace(rjsonp, "$1" + callbackName);
      } else if (s.jsonp !== false) {
        s.url += (rquery.test(s.url) ? "&" : "?") + s.jsonp + "=" + callbackName;
      }

      // Use data converter to retrieve json after script execution
      s.converters["script json"] = function () {
        if (!responseContainer) {
          jQuery.error(callbackName + " was not called");
        }
        return responseContainer[0];
      };

      // Force json dataType
      s.dataTypes[0] = "json";

      // Install callback
      overwritten = window[callbackName];
      window[callbackName] = function () {
        responseContainer = arguments;
      };

      // Clean-up function (fires after converters)
      jqXHR.always(function () {
        // If previous value didn't exist - remove it
        if (overwritten === undefined) {
          jQuery(window).removeProp(callbackName);

          // Otherwise restore preexisting value
        } else {
          window[callbackName] = overwritten;
        }

        // Save back as free
        if (s[callbackName]) {
          // Make sure that re-using the options doesn't screw things around
          s.jsonpCallback = originalSettings.jsonpCallback;

          // Save the callback name for future use
          oldCallbacks.push(callbackName);
        }

        // Call if it was a function and we have a response
        if (responseContainer && isFunction(overwritten)) {
          overwritten(responseContainer[0]);
        }
        responseContainer = overwritten = undefined;
      });

      // Delegate to script
      return "script";
    }
  });

  // Support: Safari 8 only
  // In Safari 8 documents created via document.implementation.createHTMLDocument
  // collapse sibling forms: the second one becomes a child of the first one.
  // Because of that, this security measure has to be disabled in Safari 8.
  // https://bugs.webkit.org/show_bug.cgi?id=137337
  support.createHTMLDocument = function () {
    var body = document.implementation.createHTMLDocument("").body;
    body.innerHTML = "<form></form><form></form>";
    return body.childNodes.length === 2;
  }();

  // Argument "data" should be string of html
  // context (optional): If specified, the fragment will be created in this context,
  // defaults to document
  // keepScripts (optional): If true, will include scripts passed in the html string
  jQuery.parseHTML = function (data, context, keepScripts) {
    if (typeof data !== "string") {
      return [];
    }
    if (typeof context === "boolean") {
      keepScripts = context;
      context = false;
    }
    var base, parsed, scripts;
    if (!context) {
      // Stop scripts or inline event handlers from being executed immediately
      // by using document.implementation
      if (support.createHTMLDocument) {
        context = document.implementation.createHTMLDocument("");

        // Set the base href for the created document
        // so any parsed elements with URLs
        // are based on the document's URL (gh-2965)
        base = context.createElement("base");
        base.href = document.location.href;
        context.head.appendChild(base);
      } else {
        context = document;
      }
    }
    parsed = rsingleTag.exec(data);
    scripts = !keepScripts && [];

    // Single tag
    if (parsed) {
      return [context.createElement(parsed[1])];
    }
    parsed = buildFragment([data], context, scripts);
    if (scripts && scripts.length) {
      jQuery(scripts).remove();
    }
    return jQuery.merge([], parsed.childNodes);
  };

  /**
   * Load a url into a page
   */
  jQuery.fn.load = function (url, params, callback) {
    var selector,
      type,
      response,
      self = this,
      off = url.indexOf(" ");
    if (off > -1) {
      selector = stripAndCollapse(url.slice(off));
      url = url.slice(0, off);
    }

    // If it's a function
    if (isFunction(params)) {
      // We assume that it's the callback
      callback = params;
      params = undefined;

      // Otherwise, build a param string
    } else if (params && typeof params === "object") {
      type = "POST";
    }

    // If we have elements to modify, make the request
    if (self.length > 0) {
      jQuery.ajax({
        url: url,
        // If "type" variable is undefined, then "GET" method will be used.
        // Make value of this field explicit since
        // user can override it through ajaxSetup method
        type: type || "GET",
        dataType: "html",
        data: params
      }).done(function (responseText) {
        // Save response for use in complete callback
        response = arguments;
        self.html(selector ?
        // If a selector was specified, locate the right elements in a dummy div
        // Exclude scripts to avoid IE 'Permission Denied' errors
        jQuery("<div>").append(jQuery.parseHTML(responseText)).find(selector) :
        // Otherwise use the full result
        responseText);

        // If the request succeeds, this function gets "data", "status", "jqXHR"
        // but they are ignored because response was set above.
        // If it fails, this function gets "jqXHR", "status", "error"
      }).always(callback && function (jqXHR, status) {
        self.each(function () {
          callback.apply(this, response || [jqXHR.responseText, status, jqXHR]);
        });
      });
    }
    return this;
  };
  jQuery.expr.pseudos.animated = function (elem) {
    return jQuery.grep(jQuery.timers, function (fn) {
      return elem === fn.elem;
    }).length;
  };
  jQuery.offset = {
    setOffset: function (elem, options, i) {
      var curPosition,
        curLeft,
        curCSSTop,
        curTop,
        curOffset,
        curCSSLeft,
        calculatePosition,
        position = jQuery.css(elem, "position"),
        curElem = jQuery(elem),
        props = {};

      // Set position first, in-case top/left are set even on static elem
      if (position === "static") {
        elem.style.position = "relative";
      }
      curOffset = curElem.offset();
      curCSSTop = jQuery.css(elem, "top");
      curCSSLeft = jQuery.css(elem, "left");
      calculatePosition = (position === "absolute" || position === "fixed") && (curCSSTop + curCSSLeft).indexOf("auto") > -1;

      // Need to be able to calculate position if either
      // top or left is auto and position is either absolute or fixed
      if (calculatePosition) {
        curPosition = curElem.position();
        curTop = curPosition.top;
        curLeft = curPosition.left;
      } else {
        curTop = parseFloat(curCSSTop) || 0;
        curLeft = parseFloat(curCSSLeft) || 0;
      }
      if (isFunction(options)) {
        // Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
        options = options.call(elem, i, jQuery.extend({}, curOffset));
      }
      if (options.top != null) {
        props.top = options.top - curOffset.top + curTop;
      }
      if (options.left != null) {
        props.left = options.left - curOffset.left + curLeft;
      }
      if ("using" in options) {
        options.using.call(elem, props);
      } else {
        curElem.css(props);
      }
    }
  };
  jQuery.fn.extend({
    // offset() relates an element's border box to the document origin
    offset: function (options) {
      // Preserve chaining for setter
      if (arguments.length) {
        return options === undefined ? this : this.each(function (i) {
          jQuery.offset.setOffset(this, options, i);
        });
      }
      var rect,
        win,
        elem = this[0];
      if (!elem) {
        return;
      }

      // Return zeros for disconnected and hidden (display: none) elements (gh-2310)
      // Support: IE <=11 only
      // Running getBoundingClientRect on a
      // disconnected node in IE throws an error
      if (!elem.getClientRects().length) {
        return {
          top: 0,
          left: 0
        };
      }

      // Get document-relative position by adding viewport scroll to viewport-relative gBCR
      rect = elem.getBoundingClientRect();
      win = elem.ownerDocument.defaultView;
      return {
        top: rect.top + win.pageYOffset,
        left: rect.left + win.pageXOffset
      };
    },
    // position() relates an element's margin box to its offset parent's padding box
    // This corresponds to the behavior of CSS absolute positioning
    position: function () {
      if (!this[0]) {
        return;
      }
      var offsetParent,
        offset,
        doc,
        elem = this[0],
        parentOffset = {
          top: 0,
          left: 0
        };

      // position:fixed elements are offset from the viewport, which itself always has zero offset
      if (jQuery.css(elem, "position") === "fixed") {
        // Assume position:fixed implies availability of getBoundingClientRect
        offset = elem.getBoundingClientRect();
      } else {
        offset = this.offset();

        // Account for the *real* offset parent, which can be the document or its root element
        // when a statically positioned element is identified
        doc = elem.ownerDocument;
        offsetParent = elem.offsetParent || doc.documentElement;
        while (offsetParent && (offsetParent === doc.body || offsetParent === doc.documentElement) && jQuery.css(offsetParent, "position") === "static") {
          offsetParent = offsetParent.parentNode;
        }
        if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
          // Incorporate borders into its offset, since they are outside its content origin
          parentOffset = jQuery(offsetParent).offset();
          parentOffset.top += jQuery.css(offsetParent, "borderTopWidth", true);
          parentOffset.left += jQuery.css(offsetParent, "borderLeftWidth", true);
        }
      }

      // Subtract parent offsets and element margins
      return {
        top: offset.top - parentOffset.top - jQuery.css(elem, "marginTop", true),
        left: offset.left - parentOffset.left - jQuery.css(elem, "marginLeft", true)
      };
    },
    // This method will return documentElement in the following cases:
    // 1) For the element inside the iframe without offsetParent, this method will return
    //    documentElement of the parent window
    // 2) For the hidden or detached element
    // 3) For body or html element, i.e. in case of the html node - it will return itself
    //
    // but those exceptions were never presented as a real life use-cases
    // and might be considered as more preferable results.
    //
    // This logic, however, is not guaranteed and can change at any point in the future
    offsetParent: function () {
      return this.map(function () {
        var offsetParent = this.offsetParent;
        while (offsetParent && jQuery.css(offsetParent, "position") === "static") {
          offsetParent = offsetParent.offsetParent;
        }
        return offsetParent || documentElement;
      });
    }
  });

  // Create scrollLeft and scrollTop methods
  jQuery.each({
    scrollLeft: "pageXOffset",
    scrollTop: "pageYOffset"
  }, function (method, prop) {
    var top = "pageYOffset" === prop;
    jQuery.fn[method] = function (val) {
      return access(this, function (elem, method, val) {
        // Coalesce documents and windows
        var win;
        if (isWindow(elem)) {
          win = elem;
        } else if (elem.nodeType === 9) {
          win = elem.defaultView;
        }
        if (val === undefined) {
          return win ? win[prop] : elem[method];
        }
        if (win) {
          win.scrollTo(!top ? val : win.pageXOffset, top ? val : win.pageYOffset);
        } else {
          elem[method] = val;
        }
      }, method, val, arguments.length);
    };
  });

  // Support: Safari <=7 - 9.1, Chrome <=37 - 49
  // Add the top/left cssHooks using jQuery.fn.position
  // Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
  // Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
  // getComputedStyle returns percent when specified for top/left/bottom/right;
  // rather than make the css module depend on the offset module, just check for it here
  jQuery.each(["top", "left"], function (_i, prop) {
    jQuery.cssHooks[prop] = addGetHookIf(support.pixelPosition, function (elem, computed) {
      if (computed) {
        computed = curCSS(elem, prop);

        // If curCSS returns percentage, fallback to offset
        return rnumnonpx.test(computed) ? jQuery(elem).position()[prop] + "px" : computed;
      }
    });
  });

  // Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
  jQuery.each({
    Height: "height",
    Width: "width"
  }, function (name, type) {
    jQuery.each({
      padding: "inner" + name,
      content: type,
      "": "outer" + name
    }, function (defaultExtra, funcName) {
      // Margin is only for outerHeight, outerWidth
      jQuery.fn[funcName] = function (margin, value) {
        var chainable = arguments.length && (defaultExtra || typeof margin !== "boolean"),
          extra = defaultExtra || (margin === true || value === true ? "margin" : "border");
        return access(this, function (elem, type, value) {
          var doc;
          if (isWindow(elem)) {
            // $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
            return funcName.indexOf("outer") === 0 ? elem["inner" + name] : elem.document.documentElement["client" + name];
          }

          // Get document width or height
          if (elem.nodeType === 9) {
            doc = elem.documentElement;

            // Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
            // whichever is greatest
            return Math.max(elem.body["scroll" + name], doc["scroll" + name], elem.body["offset" + name], doc["offset" + name], doc["client" + name]);
          }
          return value === undefined ?
          // Get width or height on the element, requesting but not forcing parseFloat
          jQuery.css(elem, type, extra) :
          // Set width or height on the element
          jQuery.style(elem, type, value, extra);
        }, type, chainable ? margin : undefined, chainable);
      };
    });
  });
  jQuery.each(["ajaxStart", "ajaxStop", "ajaxComplete", "ajaxError", "ajaxSuccess", "ajaxSend"], function (_i, type) {
    jQuery.fn[type] = function (fn) {
      return this.on(type, fn);
    };
  });
  jQuery.fn.extend({
    bind: function (types, data, fn) {
      return this.on(types, null, data, fn);
    },
    unbind: function (types, fn) {
      return this.off(types, null, fn);
    },
    delegate: function (selector, types, data, fn) {
      return this.on(types, selector, data, fn);
    },
    undelegate: function (selector, types, fn) {
      // ( namespace ) or ( selector, types [, fn] )
      return arguments.length === 1 ? this.off(selector, "**") : this.off(types, selector || "**", fn);
    },
    hover: function (fnOver, fnOut) {
      return this.on("mouseenter", fnOver).on("mouseleave", fnOut || fnOver);
    }
  });
  jQuery.each(("blur focus focusin focusout resize scroll click dblclick " + "mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " + "change select submit keydown keypress keyup contextmenu").split(" "), function (_i, name) {
    // Handle event binding
    jQuery.fn[name] = function (data, fn) {
      return arguments.length > 0 ? this.on(name, null, data, fn) : this.trigger(name);
    };
  });

  // Support: Android <=4.0 only
  // Make sure we trim BOM and NBSP
  // Require that the "whitespace run" starts from a non-whitespace
  // to avoid O(N^2) behavior when the engine would try matching "\s+$" at each space position.
  var rtrim = /^[\s\uFEFF\xA0]+|([^\s\uFEFF\xA0])[\s\uFEFF\xA0]+$/g;

  // Bind a function to a context, optionally partially applying any
  // arguments.
  // jQuery.proxy is deprecated to promote standards (specifically Function#bind)
  // However, it is not slated for removal any time soon
  jQuery.proxy = function (fn, context) {
    var tmp, args, proxy;
    if (typeof context === "string") {
      tmp = fn[context];
      context = fn;
      fn = tmp;
    }

    // Quick check to determine if target is callable, in the spec
    // this throws a TypeError, but we will just return undefined.
    if (!isFunction(fn)) {
      return undefined;
    }

    // Simulated bind
    args = slice.call(arguments, 2);
    proxy = function () {
      return fn.apply(context || this, args.concat(slice.call(arguments)));
    };

    // Set the guid of unique handler to the same of original handler, so it can be removed
    proxy.guid = fn.guid = fn.guid || jQuery.guid++;
    return proxy;
  };
  jQuery.holdReady = function (hold) {
    if (hold) {
      jQuery.readyWait++;
    } else {
      jQuery.ready(true);
    }
  };
  jQuery.isArray = Array.isArray;
  jQuery.parseJSON = JSON.parse;
  jQuery.nodeName = nodeName;
  jQuery.isFunction = isFunction;
  jQuery.isWindow = isWindow;
  jQuery.camelCase = camelCase;
  jQuery.type = toType;
  jQuery.now = Date.now;
  jQuery.isNumeric = function (obj) {
    // As of jQuery 3.0, isNumeric is limited to
    // strings and numbers (primitives or objects)
    // that can be coerced to finite numbers (gh-2662)
    var type = jQuery.type(obj);
    return (type === "number" || type === "string") &&
    // parseFloat NaNs numeric-cast false positives ("")
    // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
    // subtraction forces infinities to NaN
    !isNaN(obj - parseFloat(obj));
  };
  jQuery.trim = function (text) {
    return text == null ? "" : (text + "").replace(rtrim, "$1");
  };
  var
    // Map over jQuery in case of overwrite
    _jQuery = window.jQuery,
    // Map over the $ in case of overwrite
    _$ = window.$;
  jQuery.noConflict = function (deep) {
    if (window.$ === jQuery) {
      window.$ = _$;
    }
    if (deep && window.jQuery === jQuery) {
      window.jQuery = _jQuery;
    }
    return jQuery;
  };

  // Expose jQuery and $ identifiers, even in AMD
  // (trac-7102#comment:10, https://github.com/jquery/jquery/pull/557)
  // and CommonJS for browser emulators (trac-13566)
  if (typeof noGlobal === "undefined") {
    window.jQuery = window.$ = jQuery;
  }
  return jQuery;
});
});

//
var script$5H = {
  name: 'PreviewLooseLeafRender',
  props: {
    businessType: String,
    //业务类型：looseLeaf，textBook
    renderMode: {
      type: String,
      default: ''
    },
    renderData: {
      type: Object,
      default: () => {}
    },
    renderLooseLeaf: {
      type: Object,
      default: () => {}
    },
    looseLeafId: {
      type: String,
      default: ''
    },
    pTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      xmlCompList: [],
      pageType: 'h5',
      // 页面类型：取值h5、pc、pad
      looseLeafData: {},
      title: '',
      showEmpty: false,
      inlineWordsModal: {
        title: '',
        visible: false,
        content: ''
      }
    };
  },
  watch: {
    looseLeafId: {
      handler(newValue) {
        if (newValue) {
          this.queryData();
        }
      },
      immediate: true,
      deep: true
    },
    pTitle: {
      handler(newValue) {
        // if (newValue) {
        this.title = newValue;
        // }
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    this.$nextTick(() => {
      setTimeout(() => {
        jquery('a').attr('target', '_self');
      }, 2000);
      window.inlineWordsShow = uuid => {
        let ele = document.getElementById(`words__${uuid}`);
        let {
          title = '',
          content = ''
        } = ele.dataset || {};
        this.inlineWordsModal = {
          title,
          content,
          visible: true
        };
      };
    });
  },
  methods: {
    queryData() {
      if (this.renderMode === 'localview') {
        setTimeout(() => {
          let dataDict = {
            looseLeaf: this.renderData,
            textBook: this.renderData
          };
          let data = dataDict[this.businessType] || {};
          console.log('当前为本地预览模式，加载resource路径资源文件数据', this.renderData, data);
          this.looseLeafData = data;
          if (this.looseLeafData.looseLeafInfo) {
            this.xmlCompList = JSON.parse(this.looseLeafData.looseLeafInfo);
          }
          if (this.businessType === 'looseLeaf') {
            this.title = this.looseLeafData.looseLeafName;
            this.$emit('onLoadLeafData', this.looseLeafData);
          } else {
            this.title = this.looseLeafData.looseLeafName;
            this.$emit('onLoadLeafData', this.looseLeafData);
          }
        }, 100);
      } else {
        console.log('调用接口获取活页数据');
        // getLeaf(this.looseLeafId).then((response) => {
        //   response.data.fileList = [
        //     {
        //       name: '',
        //       url: response.data.looseLeafFileUrl
        //     }
        //   ]
        //   this.looseLeafData = response.data
        //   if (this.looseLeafData.looseLeafInfo) {
        //     this.xmlCompList = JSON.parse(this.looseLeafData.looseLeafInfo)
        //   }
        //   if (this.businessType === 'looseLeaf') {
        //     this.title = this.looseLeafData.looseLeafName
        //     this.$emit('onLoadLeafData', this.looseLeafData)
        //   }
        // })
      }
    },
    goBack() {
      this.$emit('onGoBack', this.looseLeafId);
    }
  }
};

/* script */
const __vue_script__$5H = script$5H;
/* template */
var __vue_render__$5H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      { staticClass: "preview-container" },
      [
        _c(
          "div",
          {
            staticClass: "preview-container-title",
            staticStyle: { display: "none" },
          },
          [
            _vm.businessType === "textBook"
              ? _c("i", {
                  staticClass: "el-icon-back container-icon",
                  on: { click: _vm.goBack },
                })
              : _vm._e(),
            _vm._v(" "),
            _c("span", { staticClass: "container-text" }, [
              _vm._v(_vm._s(_vm.title)),
            ]),
          ]
        ),
        _vm._v(" "),
        _c(
          "div",
          {
            class: "preview-container-" + _vm.pageType,
            attrs: { id: _vm.looseLeafId },
          },
          [
            _vm._l(_vm.xmlCompList, function (item) {
              return _c(item.componentsName + "-render", {
                key: item.xmlCompRef,
                ref: item.xmlCompRef,
                refInFor: true,
                tag: "component",
                attrs: {
                  mode: "preview",
                  pageType: _vm.pageType,
                  pGroupData: item.data.pGroupData,
                  currentRule: item,
                  "extend-params": item.data.extendParams,
                },
              })
            }),
            _vm._v(" "),
            _vm.xmlCompList.length == 0
              ? _c("el-empty", {
                  staticClass: "empty",
                  attrs: {
                    image:
                      "data:image/png;base64,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",
                  },
                })
              : _vm._e(),
          ],
          2
        ),
        _vm._v(" "),
        _c(
          "el-dialog",
          {
            staticClass: "preview-box-dialog",
            attrs: {
              "show-close": false,
              visible: _vm.inlineWordsModal.visible,
              "modal-append-to-body": false,
              width: "80%",
              center: "",
            },
            on: {
              "update:visible": function ($event) {
                return _vm.$set(_vm.inlineWordsModal, "visible", $event)
              },
            },
          },
          [_c("div", [_vm._v(_vm._s(_vm.inlineWordsModal.content))])]
        ),
      ],
      1
    ),
  ])
};
var __vue_staticRenderFns__$5H = [];
__vue_render__$5H._withStripped = true;

  /* style */
  const __vue_inject_styles__$5H = undefined;
  /* scoped */
  const __vue_scope_id__$5H = "data-v-7a006dee";
  /* module identifier */
  const __vue_module_identifier__$5H = undefined;
  /* functional template */
  const __vue_is_functional_template__$5H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5H, staticRenderFns: __vue_staticRenderFns__$5H },
    __vue_inject_styles__$5H,
    __vue_script__$5H,
    __vue_scope_id__$5H,
    __vue_is_functional_template__$5H,
    __vue_module_identifier__$5H,
    false,
    undefined,
    undefined,
    undefined
  );

//
let target$1;
Array.prototype.Remove = function (item, all) {
  let result,
    isType = Object.prototype.toString,
    i,
    len,
    start,
    hasLast = arguments[2];
  start = 0, len = this.length;
  for (i = start; i < len;) {
    var isPass = true,
      inx;
    if (!hasLast) {
      inx = i;
    } else {
      inx = len - 1;
    }
    if (isType.call(item) == '[object Array]') {
      for (let ii = 0, iimax = item.length; ii < iimax; ii++) {
        if (this[inx] === item[ii]) {
          isPass = false;
          break;
        }
      }
    } else if (this[inx] === item) {
      isPass = false;
    }
    if (!isPass) {
      result = true;
      this.splice(inx, 1);
      if (all) {
        break;
      }
    } else if (!hasLast) {
      len = this.length;
      i++;
    } else {
      len--;
    }
  }
  return result ? this : void 0;
};
var script$5G = {
  name: 'XmlRender',
  components: {
    PreviewLooseLeafRender: __vue_component__$5H,
    PreviewCatalogRender: __vue_component__$5I
  },
  props: {
    uuid: {
      type: String,
      default: ''
    },
    renderType: {
      type: String,
      default: ''
    },
    renderMode: {
      type: String,
      default: ''
    },
    renderData: {
      type: Object,
      default: () => {}
    },
    renderLooseLeaf: {
      type: Object,
      default: () => {}
    },
    renderCatalog: {
      type: Array,
      default: () => []
    },
    pageType: {
      type: String,
      default: 'h5'
    },
    styleType: {
      type: String,
      default: 'A'
    },
    projectId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      pageTitle: '',
      imageTitle: '',
      showViewer: false,
      previewSrc: '',
      businessType: String,
      //业务类型：looseLeaf，textBook
      looseLeafId: '',
      textBookId: '',
      xmlCompList: [],
      // pageType: 'h5', // 页面类型：取值h5、pc、pad
      looseLeafData: {},
      leafTitle: '',
      showType: 'leaf',
      //显示的类型：leaf 活页，catalog
      catalogList: [],
      imageShow: false,
      previewList: [],
      showPageIndex: 0,
      isStop: false,
      contentList: [],
      //内容list，只有3个
      initialIndex: 0,
      selectId: '',
      textBookData: {
        textBooksName: ''
      },
      uuidState: false
    };
  },
  computed: {
    // 公告滚动自定义
    cityOption() {
      return {
        step: 1,
        // 数值越大速度滚动越快
        limitMoveNum: 4,
        // 开始无缝滚动的数据量 this.cityData.length
        hoverStop: true,
        // 是否开启鼠标悬停stop
        direction: 1,
        // 0向下 1向上 2向左 3向右
        openWatch: true,
        // 开启数据实时监控刷新dom
        singleHeight: 0,
        // 单步运动停止的高度(默认值0是无缝不停止的滚动) direction => 0/1
        singleWidth: 0,
        // 单步运动停止的宽度(默认值0是无缝不停止的滚动) direction => 2/3
        waitTime: 1000 // 单步运动停止的时间(默认值1000ms)
      };
    }
  },
  watch: {},
  created() {
    this.uuidState = true;
    // if (this.$route.query.pageType) {
    //   this.pageType = this.$route.query.pageType
    // } else {
    //   this.deviceType()
    // }
    //TODO http://${url}/previewLooseLeaf?businessType=looseLeaf&looseLeafId=1556533500887220224
    switch (this.renderType) {
      case 'looseLeaf':
        this.looseLeafId = this.uuid;
        break;
      case 'textBook':
        this.textBookId = this.uuid;
        break;
    }
    // if (this.$route.query.looseLeafId) {
    //   this.looseLeafId = this.$route.query.looseLeafId
    // }
    // if (this.$route.query.textBookId) {
    //   this.textBookId = this.$route.query.textBookId
    // }
    this.businessType = this.renderType;
    console.log('渲染数据源', this.renderData);
    // this.businessType = this.$route.query.businessType
    this.queryData();
    this.$nextTick(() => {
      window.showImageBYDate = () => {
        target$1 = window.event.target;
        if (target$1.isShow == true) return;
        target$1.isShow = true;
        jquery(target$1).find('img').click();
        setTimeout(() => {
          target$1.isShow = false;
        }, 1000);
        return false;
      };
      window.inlineAudioPlay = uuid => {
        target$1 = window.event.target;
        if (target$1.isShow == true) return;
        target$1.isShow = true;
        let audio = document.getElementById(`audio${uuid}`);
        if (audio.paused) {
          audio.play();
          document.getElementById(`icon${uuid}`).className = 'wifi-symbol playing';
        } else {
          audio.pause();
          audio.currentTime = 0; //音乐从头播放
          document.getElementById(`icon${uuid}`).className = 'wifi-symbol';
        }
        setTimeout(() => {
          target$1.isShow = false;
        }, 1000);
        return false;
      };
    });
  },
  methods: {
    showTitle(leafData) {
      if (this.businessType === 'looseLeaf') {
        this.pageTitle = leafData.looseLeafName;
      }
    },
    srcollChangeData() {
      let {
        scrollTop
      } = this.$refs.rightContent;
      console.log('===scrollTop', scrollTop);
      let id = '';
      this.contentList.forEach((it, index) => {
        let top = jquery(`#page_${it.id}`).get(0).offsetTop;
        if (top <= scrollTop) {
          id = this.contentList[index + 1] ? this.contentList[index + 1].id : it.id;
        }
      });
      this.selectId = id;
      console.log(this.selectId);
    },
    closeViewer() {
      this.showViewer = false;
    },
    debounce: (func, delay) => {
      let timer = null;
      return function () {
        let context = this;
        let args = arguments;
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(function () {
          func.apply(context, args);
          timer = null;
        }, delay);
      };
    },
    getHeightByIndex(h) {
      let height = 0;
      h = h || this.showPageIndex;
      for (let i = 0; i <= h; i++) {
        height += jquery(jquery('.preview-list').get(i)).height();
      }
      return height;
    },
    //新的滚动方式
    scrollIndexPage(e) {
      this.isRunAnm = this.isRunAnm || false;
      let direction = e.deltaY > 0 ? 'down' : 'up'; //deltaY为正则滚轮向下，为负滚轮向上
      //console.log("==========================",this.initialIndex,this.showPageIndex)
      if (this.initialIndex != this.showPageIndex) return;
      if (direction == 'down' && e.deltaY >= 100) {
        //125为用户一次滚动鼠标的wheelDelta的值
        //   if ( this.isRunAnm == true ) return;
        console.log(this.initialIndex, jquery('.preview-list').length - 1);
        if (this.initialIndex >= jquery('.preview-list').length - 1) {
          this.initialIndex = jquery('.preview-list').length - 1;
        } else {
          let dom = jquery(`.preview-list:eq(${this.initialIndex})  > div > div `);
          // console.log(dom.height(),dom.parent().parent().get(0).scrollTop);
          if (dom.height() > 896 && dom.parent().parent().get(0).scrollTop + 896 < dom.height()) {
            this.isRunAnm = false;
          } else {
            this.isRunAnm = true;
            this.initialIndex = this.showPageIndex + 1;
            this.setActiveItem(this.initialIndex, direction);
            //  this.showPageIndex =  this.initialIndex;
          }
        }
      }
      if (direction == 'up' && e.deltaY <= -100) {
        //    if ( this.isRunAnm == true ) return;
        // console.log( this.initialIndex,"====================",$(".preview-list").length - 1);

        if (this.initialIndex <= 0) {
          this.initialIndex = 0;
          this.setActiveItem(0);
        } else {
          if (this.initialIndex >= jquery('.preview-list').length - 1) {
            this.initialIndex = jquery('.preview-list').length - 1;
          }
          let dom = jquery(`.preview-list:eq(${this.initialIndex})  > div > div `);
          //console.log(dom.parent().parent().get(0).scrollTop);

          if (dom.height() > 896 && dom.parent().parent().get(0).scrollTop > 0) {
            this.isRunAnm = false;
          } else {
            this.isRunAnm = true;
            this.initialIndex -= 1;
            this.setActiveItem(this.initialIndex, direction);
          }
        }
      }
    },
    /**
     *  滚轮的滚动结果
     * @param {页索引} index
     * @param {方向} direction
     */
    setActiveItem(index, direction) {
      //  this.isRunAnm = true;
      if (direction == 'down') {
        if (index < jquery('.preview-list').length) {
          jquery('.preview-list').each(function (i) {
            jquery(this).css('top', (index - i) * -896);
            //     console.log(i,index, $(this).css("top"));
          });
        }
      } else {
        // console.log($(".preview-list:eq(0)").position().top <= -896 );
        if (index >= 0 && jquery('.preview-list:eq(0)').position().top <= -896) {
          jquery('.preview-list').each(function (i) {
            jquery(this).css('top', (i - index) * 896);
            //   console.log(i,index, $(this).css("top"));
          });
        }
      }
      setTimeout(() => {
        this.isRunAnm = false;
        this.showPageIndex = this.initialIndex;
      }, 600);
    },
    deviceType() {
      if (/pad|iPad|phone|pod|iPhone|iPod|ios|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone/i.test(navigator.userAgent)) {
        if (/pad|iPad/i.test(navigator.userAgent)) {
          this.pageType = 'pad';
        } else {
          this.pageType = 'h5';
        }
      } else {
        this.pageType = 'pc';
      }
    },
    queryData() {
      if (this.businessType === 'textBook') {
        if (this.renderMode === 'localview') {
          // eslint-disable-next-line no-undef
          let textBookData = this.renderData || {};
          this.textBookData = textBookData;
          this.pageTitle = textBookData.textBooksName;
          // eslint-disable-next-line no-undef
          let catalogList = this.renderCatalog || [];
          this.catalogList = catalogList;
          if (this.catalogList.length > 0) {
            this.looseLeafId = this.catalogList[0].id;
            this.leafTitle = this.catalogList[0].label;
          }
          this.contentList = [];
          const getItemByChild = list => {
            for (let i = 0; i < list.length; i++) {
              let it = list[i];
              this.contentList.push(it);
              if (it.children) {
                getItemByChild(it.children);
              }
            }
          };
          getItemByChild(this.catalogList);
          this.initialIndex = 0;
          this.showPageIndex = 0;
          if (this.contentList[0] && this.contentList[1] && this.contentList[0].id == this.contentList[1].id) {
            this.contentList.splice(0, 1);
          }
        } else {
          console.log('开始查询教材数据');
          // getBooks(this.textBookId).then((response) => {
          //   console.log('教材数据：', response.data)
          //   this.textBookData = response.data
          //   this.pageTitle = this.textBookData.textBooksName
          // })
          // listCatalog({ textBookId: this.textBookId }).then((res) => {
          //   console.log('目录列表：', res.data)
          //   this.catalogList = res.data

          //   if (this.catalogList.length > 0) {
          //     this.looseLeafId = this.catalogList[0].id
          //     this.leafTitle = this.catalogList[0].label
          //   }
          //   this.contentList = []
          //   //  if ( this.catalogList.length > 3) {
          //   const getItemByChild = (list) => {
          //     for (let i = 0; i < list.length; i++) {
          //       let it = list[i]
          //       this.contentList.push(it)

          //       if (it.children) {
          //         getItemByChild(it.children)
          //       }
          //     }
          //   }

          //   getItemByChild(this.catalogList)
          //   this.initialIndex = 0
          //   this.showPageIndex = 0
          //   if (this.contentList[0] && this.contentList[1] && this.contentList[0].id == this.contentList[1].id) {
          //     this.contentList.splice(0, 1)
          //   }
          //   console.log('============================', this.contentList)
          //   //}
          // })
        }
      } else {
        this.contentList.push({
          id: this.looseLeafId
        });
      }
    },
    showCatalog() {
      this.showType = 'catalog';
    },
    catalogClick(obj) {
      this.showType = 'leaf';
      this.looseLeafId = obj.id;
      this.selectId = obj.id;
      this.leafTitle = obj.label;
      this.contentList.forEach((it, index) => {
        if (it.id == obj.id) {
          this.initialIndex = index;
          this.setActiveItem(index, 'down');
        }
      });
      this.$forceUpdate();
      this.$nextTick(() => {
        setTimeout(() => {
          // alert($("#mo2g").attr("href"));
          // alert(1);
          console.log(this.selectId, '=========', jquery(`#${this.selectId}`).length);
          let isPc = 50;
          if (this.pageType == 'pc') isPc = 0;
          let top = jquery(`#page_${this.selectId}`).get(0).offsetTop - isPc;
          // alert(top);
          document.body.scrollTop = top;
          this.$refs.rightContent.scrollTop = top;
        }, 300);
      });
    }
  }
};

/* script */
const __vue_script__$5G = script$5G;
/* template */
var __vue_render__$5G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "ouchn-container preview-mode" }, [
    _vm.uuidState
      ? _c("div", [
          _c(
            "div",
            { class: "phone-preview preview-for-" + _vm.pageType },
            [
              _vm.pageType === "pc"
                ? [
                    _c(
                      "div",
                      { staticClass: "pc-container" },
                      [
                        _vm.businessType === "textBook"
                          ? _c("preview-catalog-render", {
                              staticClass: "pc-catalog pc-catalog--render",
                              attrs: {
                                "page-type": _vm.pageType,
                                "text-book-id": _vm.textBookId,
                                "style-type": _vm.styleType,
                                "catalog-list": _vm.catalogList,
                                checkedId: _vm.selectId,
                              },
                              on: { onNodeClick: _vm.catalogClick },
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            ref: "rightContent",
                            staticClass: "preview-list-counter",
                            on: {
                              scroll: function ($event) {
                                return _vm.srcollChangeData($event)
                              },
                            },
                          },
                          _vm._l(_vm.contentList, function (item, index) {
                            return _c(
                              "div",
                              {
                                key: "page" + index,
                                ref: "page" + index,
                                refInFor: true,
                                staticClass: "preview-list",
                                attrs: { id: "page_" + item.id },
                              },
                              [
                                _c("preview-loose-leaf-render", {
                                  class: "preview-screen-" + _vm.pageType,
                                  attrs: {
                                    "business-type": _vm.businessType,
                                    "loose-leaf-id": item.id,
                                    "p-title": item.label,
                                    renderMode: _vm.renderMode,
                                    renderData: _vm.renderData,
                                    renderLooseLeaf: _vm.renderLooseLeaf,
                                  },
                                  on: { onGoBack: _vm.showCatalog },
                                }),
                              ],
                              1
                            )
                          }),
                          0
                        ),
                      ],
                      1
                    ),
                  ]
                : [
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: _vm.showType === "leaf",
                            expression: "showType === 'leaf'",
                          },
                        ],
                        ref: "rightContent",
                        staticClass: "preview-list-counter",
                        on: {
                          scroll: function ($event) {
                            return _vm.srcollChangeData($event)
                          },
                        },
                      },
                      [
                        _vm.pageType !== "pc"
                          ? _c(
                              "div",
                              { staticClass: "preview-container-title" },
                              [
                                _vm.businessType === "textBook"
                                  ? _c("i", {
                                      staticClass:
                                        "el-icon-s-fold container-icon",
                                      on: { click: _vm.showCatalog },
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                _c("span", { staticClass: "container-text" }, [
                                  _vm._v(_vm._s(_vm.pageTitle)),
                                ]),
                              ]
                            )
                          : _vm._e(),
                        _vm._v(" "),
                        _vm._l(_vm.contentList, function (item, index) {
                          return _c(
                            "div",
                            {
                              key: "page" + index,
                              ref: "page" + index,
                              refInFor: true,
                              staticClass: "preview-list",
                              style: { top: index * 896 + "px" },
                              attrs: { id: "page_" + item.id },
                            },
                            [
                              _c("preview-loose-leaf-render", {
                                key: index,
                                class: "preview-screen-" + _vm.pageType,
                                attrs: {
                                  "business-type": _vm.businessType,
                                  "loose-leaf-id": item.id,
                                  "p-title": item.label,
                                  renderMode: _vm.renderMode,
                                  renderData: _vm.renderData,
                                  renderLooseLeaf: _vm.renderLooseLeaf,
                                },
                                on: {
                                  onGoBack: _vm.showCatalog,
                                  onLoadLeafData: _vm.showTitle,
                                },
                              }),
                            ],
                            1
                          )
                        }),
                      ],
                      2
                    ),
                    _vm._v(" "),
                    _vm.businessType === "textBook"
                      ? _c("preview-catalog-render", {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.showType === "catalog",
                              expression: "showType === 'catalog'",
                            },
                          ],
                          attrs: {
                            "page-type": _vm.pageType,
                            "text-book-id": _vm.textBookId,
                            "style-type": _vm.styleType,
                            "catalog-list": _vm.catalogList,
                            "is-show": _vm.showType === "catalog",
                            checkedId: _vm.selectId,
                          },
                          on: { onNodeClick: _vm.catalogClick },
                        })
                      : _vm._e(),
                  ],
            ],
            2
          ),
          _vm._v(" "),
          _c("a", { attrs: { id: "mo2g", href: "#" + _vm.selectId } }),
        ])
      : _c("div", { attrs: { id: "out" } }, [_vm._v("预览地址已失效")]),
  ])
};
var __vue_staticRenderFns__$5G = [];
__vue_render__$5G._withStripped = true;

  /* style */
  const __vue_inject_styles__$5G = undefined;
  /* scoped */
  const __vue_scope_id__$5G = undefined;
  /* module identifier */
  const __vue_module_identifier__$5G = undefined;
  /* functional template */
  const __vue_is_functional_template__$5G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5G, staticRenderFns: __vue_staticRenderFns__$5G },
    __vue_inject_styles__$5G,
    __vue_script__$5G,
    __vue_scope_id__$5G,
    __vue_is_functional_template__$5G,
    __vue_module_identifier__$5G,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5G.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('render', {
    prefix: options.prefix
  }), __vue_component__$5G);
};

const rnds8Pool = new Uint8Array(256); // # of random values to pre-allocate

let poolPtr = rnds8Pool.length;
function rng() {
  if (poolPtr > rnds8Pool.length - 16) {
    crypto.randomFillSync(rnds8Pool);
    poolPtr = 0;
  }
  return rnds8Pool.slice(poolPtr, poolPtr += 16);
}

var REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).substr(1));
}
function stringify$1(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  const uuid = (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase(); // Consistency check for valid UUID.  If this throws, it's likely due to one
  // of the following:
  // - One or more input array values don't map to a hex octet (leading to
  // "undefined" in the uuid)
  // - Invalid input values for the RFC `version` or `variant` fields

  if (!validate(uuid)) {
    throw TypeError('Stringified UUID is invalid');
  }
  return uuid;
}

function v4(options, buf, offset) {
  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;
    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }
    return buf;
  }
  return stringify$1(rnds);
}

var defaultLang = {
  el: {
    colorpicker: {
      confirm: '确定',
      clear: '清空'
    },
    datepicker: {
      now: '此刻',
      today: '今天',
      cancel: '取消',
      clear: '清空',
      confirm: '确定',
      selectDate: '选择日期',
      selectTime: '选择时间',
      startDate: '开始日期',
      startTime: '开始时间',
      endDate: '结束日期',
      endTime: '结束时间',
      prevYear: '前一年',
      nextYear: '后一年',
      prevMonth: '上个月',
      nextMonth: '下个月',
      year: '年',
      month1: '1 月',
      month2: '2 月',
      month3: '3 月',
      month4: '4 月',
      month5: '5 月',
      month6: '6 月',
      month7: '7 月',
      month8: '8 月',
      month9: '9 月',
      month10: '10 月',
      month11: '11 月',
      month12: '12 月',
      // week: '周次',
      weeks: {
        sun: '日',
        mon: '一',
        tue: '二',
        wed: '三',
        thu: '四',
        fri: '五',
        sat: '六'
      },
      months: {
        jan: '一月',
        feb: '二月',
        mar: '三月',
        apr: '四月',
        may: '五月',
        jun: '六月',
        jul: '七月',
        aug: '八月',
        sep: '九月',
        oct: '十月',
        nov: '十一月',
        dec: '十二月'
      }
    },
    select: {
      loading: '加载中',
      noMatch: '无匹配数据',
      noData: '无数据',
      placeholder: '请选择'
    },
    cascader: {
      noMatch: '无匹配数据',
      loading: '加载中',
      placeholder: '请选择',
      noData: '暂无数据'
    },
    pagination: {
      goto: '前往',
      pagesize: '条/页',
      total: '共 {total} 条',
      pageClassifier: '页'
    },
    messagebox: {
      title: '提示',
      confirm: '确定',
      cancel: '取消',
      error: '输入的数据不合法!'
    },
    upload: {
      deleteTip: '按 delete 键可删除',
      delete: '删除',
      preview: '查看图片',
      continue: '继续上传'
    },
    table: {
      emptyText: '暂无数据',
      confirmFilter: '筛选',
      resetFilter: '重置',
      clearFilter: '全部',
      sumText: '合计'
    },
    tree: {
      emptyText: '暂无数据'
    },
    transfer: {
      noMatch: '无匹配数据',
      noData: '无数据',
      titles: ['列表 1', '列表 2'],
      filterPlaceholder: '请输入搜索内容',
      noCheckedFormat: '共 {total} 项',
      hasCheckedFormat: '已选 {checked}/{total} 项'
    },
    image: {
      error: '加载失败'
    },
    pageHeader: {
      title: '返回'
    },
    popconfirm: {
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    },
    empty: {
      description: '暂无数据'
    }
  }
};

var isMergeableObject = function isMergeableObject(value) {
  return isNonNullObject(value) && !isSpecial(value);
};
function isNonNullObject(value) {
  return !!value && typeof value === 'object';
}
function isSpecial(value) {
  var stringValue = Object.prototype.toString.call(value);
  return stringValue === '[object RegExp]' || stringValue === '[object Date]' || isReactElement(value);
}

// see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;
function isReactElement(value) {
  return value.$$typeof === REACT_ELEMENT_TYPE;
}
function emptyTarget(val) {
  return Array.isArray(val) ? [] : {};
}
function cloneIfNecessary(value, optionsArgument) {
  var clone = optionsArgument && optionsArgument.clone === true;
  return clone && isMergeableObject(value) ? deepmerge(emptyTarget(value), value, optionsArgument) : value;
}
function defaultArrayMerge(target, source, optionsArgument) {
  var destination = target.slice();
  source.forEach(function (e, i) {
    if (typeof destination[i] === 'undefined') {
      destination[i] = cloneIfNecessary(e, optionsArgument);
    } else if (isMergeableObject(e)) {
      destination[i] = deepmerge(target[i], e, optionsArgument);
    } else if (target.indexOf(e) === -1) {
      destination.push(cloneIfNecessary(e, optionsArgument));
    }
  });
  return destination;
}
function mergeObject(target, source, optionsArgument) {
  var destination = {};
  if (isMergeableObject(target)) {
    Object.keys(target).forEach(function (key) {
      destination[key] = cloneIfNecessary(target[key], optionsArgument);
    });
  }
  Object.keys(source).forEach(function (key) {
    if (!isMergeableObject(source[key]) || !target[key]) {
      destination[key] = cloneIfNecessary(source[key], optionsArgument);
    } else {
      destination[key] = deepmerge(target[key], source[key], optionsArgument);
    }
  });
  return destination;
}
function deepmerge(target, source, optionsArgument) {
  var sourceIsArray = Array.isArray(source);
  var targetIsArray = Array.isArray(target);
  var options = optionsArgument || {
    arrayMerge: defaultArrayMerge
  };
  var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;
  if (!sourceAndTargetTypesMatch) {
    return cloneIfNecessary(source, optionsArgument);
  } else if (sourceIsArray) {
    var arrayMerge = options.arrayMerge || defaultArrayMerge;
    return arrayMerge(target, source, optionsArgument);
  } else {
    return mergeObject(target, source, optionsArgument);
  }
}
deepmerge.all = function deepmergeAll(array, optionsArgument) {
  if (!Array.isArray(array) || array.length < 2) {
    throw new Error('first argument should be an array with at least two elements');
  }

  // we are sure there are at least 2 values, so it is safe to have no initial value
  return array.reduce(function (prev, next) {
    return deepmerge(prev, next, optionsArgument);
  });
};
var deepmerge_1 = deepmerge;

function isString$1(obj) {
  return Object.prototype.toString.call(obj) === '[object String]';
}
function isHtmlElement(node) {
  return node && node.nodeType === Node.ELEMENT_NODE;
}
if (typeof /./ !== 'function' && typeof Int8Array !== 'object' && (Vue.prototype.$isServer || typeof document.childNodes !== 'function')) ;

const hasOwnProperty$2 = Object.prototype.hasOwnProperty;
function hasOwn(obj, key) {
  return hasOwnProperty$2.call(obj, key);
}
const isFirefox = function () {
  return !Vue.prototype.$isServer && !!window.navigator.userAgent.match(/firefox/i);
};
function rafThrottle(fn) {
  let locked = false;
  return function (...args) {
    if (locked) return;
    locked = true;
    window.requestAnimationFrame(_ => {
      fn.apply(this, args);
      locked = false;
    });
  };
}

const RE_NARGS = /(%|)\{([0-9a-zA-Z_]+)\}/g;
/**
 *  String format template
 *  - Inspired:
 *    https://github.com/Matt-Esch/string-template/index.js
 */
function Format (Vue) {
  /**
   * template
   *
   * @param {String} string
   * @param {Array} ...args
   * @return {String}
   */

  function template(string, ...args) {
    if (args.length === 1 && typeof args[0] === 'object') {
      args = args[0];
    }
    if (!args || !args.hasOwnProperty) {
      args = {};
    }
    return string.replace(RE_NARGS, (match, prefix, i, index) => {
      let result;
      if (string[index - 1] === '{' && string[index + match.length] === '}') {
        return i;
      } else {
        result = hasOwn(args, i) ? args[i] : null;
        if (result === null || result === undefined) {
          return '';
        }
        return result;
      }
    });
  }
  return template;
}

const format = Format();
let lang = defaultLang;
let merged = false;
let i18nHandler = function () {
  const vuei18n = Object.getPrototypeOf(this || Vue).$t;
  if (typeof vuei18n === 'function' && !!Vue.locale) {
    if (!merged) {
      merged = true;
      Vue.locale(Vue.config.lang, deepmerge_1(lang, Vue.locale(Vue.config.lang) || {}, {
        clone: true
      }));
    }
    return vuei18n.apply(this, arguments);
  }
};
const t$1 = function (path, options) {
  let value = i18nHandler.apply(this, arguments);
  if (value !== null && value !== undefined) return value;
  const array = path.split('.');
  let current = lang;
  for (let i = 0, j = array.length; i < j; i++) {
    const property = array[i];
    value = current[property];
    if (i === j - 1) return format(value, options);
    if (!value) return '';
    current = value;
  }
  return '';
};

var Locale = {
  methods: {
    t(...args) {
      return t$1.apply(this, args);
    }
  }
};

/* istanbul ignore next */
const isServer = Vue.prototype.$isServer;
const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
const MOZ_HACK_REGEXP = /^moz([A-Z])/;
const ieVersion = isServer ? 0 : Number(document.documentMode);

/* istanbul ignore next */
const trim$2 = function (string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
const camelCase$1 = function (name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
const on$1 = function () {
  if (!isServer && document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
const off$1 = function () {
  if (!isServer && document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
function hasClass$1(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) throw new Error('className should not contain space.');
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}

/* istanbul ignore next */
function addClass$1(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');
  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;
    if (el.classList) {
      el.classList.add(clsName);
    } else if (!hasClass$1(el, clsName)) {
      curClass += ' ' + clsName;
    }
  }
  if (!el.classList) {
    el.setAttribute('class', curClass);
  }
}

/* istanbul ignore next */
function removeClass$1(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';
  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;
    if (el.classList) {
      el.classList.remove(clsName);
    } else if (hasClass$1(el, clsName)) {
      curClass = curClass.replace(' ' + clsName + ' ', ' ');
    }
  }
  if (!el.classList) {
    el.setAttribute('class', trim$2(curClass));
  }
}

/* istanbul ignore next */
const getStyle = ieVersion < 9 ? function (element, styleName) {
  if (isServer) return;
  if (!element || !styleName) return null;
  styleName = camelCase$1(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (isServer) return;
  if (!element || !styleName) return null;
  styleName = camelCase$1(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};
const isScroll = (el, vertical) => {
  if (isServer) return;
  const determinedDirection = vertical !== null && vertical !== undefined;
  const overflow = determinedDirection ? vertical ? getStyle(el, 'overflow-y') : getStyle(el, 'overflow-x') : getStyle(el, 'overflow');
  return overflow.match(/(scroll|auto|overlay)/);
};
const getScrollContainer = (el, vertical) => {
  if (isServer) return;
  let parent = el;
  while (parent) {
    if ([window, document, document.documentElement].includes(parent)) {
      return window;
    }
    if (isScroll(parent, vertical)) {
      return parent;
    }
    parent = parent.parentNode;
  }
  return parent;
};
const isInContainer = (el, container) => {
  if (isServer || !el || !container) return false;
  const elRect = el.getBoundingClientRect();
  let containerRect;
  if ([window, document, document.documentElement, null, undefined].includes(container)) {
    containerRect = {
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0
    };
  } else {
    containerRect = container.getBoundingClientRect();
  }
  return elRect.top < containerRect.bottom && elRect.bottom > containerRect.top && elRect.right > containerRect.left && elRect.left < containerRect.right;
};

/* eslint-disable no-undefined,no-param-reassign,no-shadow */

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  {Number}    delay          A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 * @param  {Boolean}   [noTrailing]   Optional, defaults to false. If noTrailing is true, callback will only execute every `delay` milliseconds while the
 *                                    throttled-function is being called. If noTrailing is false or unspecified, callback will be executed one final time
 *                                    after the last throttled-function call. (After the throttled-function has not been called for `delay` milliseconds,
 *                                    the internal counter is reset)
 * @param  {Function}  callback       A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                    to `callback` when the throttled-function is executed.
 * @param  {Boolean}   [debounceMode] If `debounceMode` is true (at begin), schedule `clear` to execute after `delay` ms. If `debounceMode` is false (at end),
 *                                    schedule `callback` to execute after `delay` ms.
 *
 * @return {Function}  A new, throttled, function.
 */
var throttle$2 = function (delay, noTrailing, callback, debounceMode) {
  // After wrapper has stopped being called, this timeout ensures that
  // `callback` is executed at the proper times in `throttle` and `end`
  // debounce modes.
  var timeoutID;

  // Keep track of the last time `callback` was executed.
  var lastExec = 0;

  // `noTrailing` defaults to falsy.
  if (typeof noTrailing !== 'boolean') {
    debounceMode = callback;
    callback = noTrailing;
    noTrailing = undefined;
  }

  // The `wrapper` function encapsulates all of the throttling / debouncing
  // functionality and when executed will limit the rate at which `callback`
  // is executed.
  function wrapper() {
    var self = this;
    var elapsed = Number(new Date()) - lastExec;
    var args = arguments;

    // Execute `callback` and update the `lastExec` timestamp.
    function exec() {
      lastExec = Number(new Date());
      callback.apply(self, args);
    }

    // If `debounceMode` is true (at begin) this is used to clear the flag
    // to allow future `callback` executions.
    function clear() {
      timeoutID = undefined;
    }
    if (debounceMode && !timeoutID) {
      // Since `wrapper` is being called for the first time and
      // `debounceMode` is true (at begin), execute `callback`.
      exec();
    }

    // Clear any existing timeout.
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    if (debounceMode === undefined && elapsed > delay) {
      // In throttle mode, if `delay` time has been exceeded, execute
      // `callback`.
      exec();
    } else if (noTrailing !== true) {
      // In trailing throttle mode, since `delay` time has not been
      // exceeded, schedule `callback` to execute `delay` ms after most
      // recent execution.
      //
      // If `debounceMode` is true (at begin), schedule `clear` to execute
      // after `delay` ms.
      //
      // If `debounceMode` is false (at end), schedule `callback` to
      // execute after `delay` ms.
      timeoutID = setTimeout(debounceMode ? clear : exec, debounceMode === undefined ? delay - elapsed : delay);
    }
  }

  // Return the wrapper function.
  return wrapper;
};

//
const isSupportObjectFit = () => document.documentElement.style.objectFit !== undefined;
const ObjectFit = {
  NONE: 'none',
  CONTAIN: 'contain',
  COVER: 'cover',
  FILL: 'fill',
  SCALE_DOWN: 'scale-down'
};
var script$5F = {
  name: 'DesignPreviewImage',
  mixins: [Locale],
  inheritAttrs: false,
  props: {
    src: String,
    fit: String,
    lazy: Boolean,
    mode: String,
    scrollContainer: {},
    previewSrcList: {
      type: Array,
      default: () => []
    },
    zIndex: {
      type: Number,
      default: 2000
    },
    currentIndex: {
      type: Number,
      default: -1
    },
    current: {
      type: Number,
      default: 0
    },
    showTitle: {
      type: Boolean,
      default: false
    },
    title: {
      type: Array,
      default: () => []
    },
    contents: {
      type: Array,
      default: () => []
    },
    self: {
      type: Object,
      default: () => {
      }
    },
    titlePosition: {
      type: String,
      default: 'under'
    },
    titleAlign: {
      type: String,
      default: 'left'
    },
    clickEffect: {
      type: String,
      default: 'dian-ji-fang-da'
    },
    width: {
      type: Number,
      default: 100
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      show: !this.lazy,
      imageWidth: 0,
      imageHeight: 0,
      showViewer: false,
      clientWidth: 0
    };
  },
  computed: {
    placeholderStyle() {
      // console.log(this.clientWidth)
      let offsetWidth = this.self?.offsetWidth || 400;
      let offsetHeight = this.self?.offsetHeight || 600;
      let width = this.width / 100 * this.clientWidth;
      let height = width / offsetWidth * offsetHeight;
      return {
        width: width + 'px',
        height: height + 'px'
      };
    },
    imageStyle() {
      const {
        fit
      } = this;
      if (!this.$isServer && fit) {
        return isSupportObjectFit() ? {
          'object-fit': 'contain',
          width: this.width + '% !important'
        } : this.getImageStyle(fit);
      }
      return {
        width: this.width + '% !important'
      };
    },
    alignCenter() {
      return !this.$isServer && !isSupportObjectFit() && this.fit !== ObjectFit.FILL;
    },
    preview() {
      const {
        previewSrcList
      } = this;
      return Array.isArray(previewSrcList) && previewSrcList.length > 0 && this.mode === 'preview' && (this.clickEffect === 'dian-ji-fang-da' || this.clickEffect === 'dian-ji-tiao-zhuan');
    },
    imageIndex() {
      if (this.currentIndex !== -1) {
        return this.currentIndex;
      }
      let previewIndex = 0;
      const srcIndex = this.previewSrcList.indexOf(this.src);
      if (srcIndex >= 0) {
        previewIndex = srcIndex;
      }
      return previewIndex;
    }
  },
  watch: {
    src(val) {
      this.show && this.loadImage();
    },
    show(val) {
      val && this.loadImage();
    },
    title: {
      handler(n, o) {
        // console.log(n, o)
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.clientWidth = this.$refs.placeholder?.clientWidth;
    if (this.lazy) {
      this.addLazyLoadListener();
    } else {
      this.loadImage();
    }
  },
  beforeDestroy() {
    this.lazy && this.removeLazyLoadListener();
  },
  methods: {
    loadImage() {
      if (this.$isServer) return;

      // reset status
      this.loading = true;
      this.error = false;
      const img = new Image();
      img.onload = e => this.handleLoad(e, img);
      img.onerror = this.handleError.bind(this);

      // bind html attrs
      // so it can behave consistently
      Object.keys(this.$attrs).forEach(key => {
        const value = this.$attrs[key];
        img.setAttribute(key, value);
      });
      img.src = this.src;
    },
    handleLoad(e, img) {
      this.imageWidth = img.width;
      this.imageHeight = img.height;
      this.loading = false;
      this.error = false;
    },
    handleError(e) {
      this.loading = false;
      this.error = true;
      this.$emit('error', e);
    },
    handleLazyLoad() {
      if (isInContainer(this.$el, this._scrollContainer)) {
        this.show = true;
        this.removeLazyLoadListener();
      }
    },
    addLazyLoadListener() {
      if (this.$isServer) return;
      const {
        scrollContainer
      } = this;
      let _scrollContainer = null;
      if (isHtmlElement(scrollContainer)) {
        _scrollContainer = scrollContainer;
      } else if (isString$1(scrollContainer)) {
        _scrollContainer = document.querySelector(scrollContainer);
      } else {
        _scrollContainer = getScrollContainer(this.$el);
      }
      if (_scrollContainer) {
        this._scrollContainer = _scrollContainer;
        this._lazyLoadHandler = throttle$2(200, this.handleLazyLoad);
        on$1(_scrollContainer, 'scroll', this._lazyLoadHandler);
        this.handleLazyLoad();
      }
    },
    removeLazyLoadListener() {
      const {
        _scrollContainer,
        _lazyLoadHandler
      } = this;
      if (this.$isServer || !_scrollContainer || !_lazyLoadHandler) return;
      off$1(_scrollContainer, 'scroll', _lazyLoadHandler);
      this._scrollContainer = null;
      this._lazyLoadHandler = null;
    },
    /**
     * simulate object-fit behavior to compatible with IE11 and other browsers which not support object-fit
     */
    getImageStyle(fit) {
      const {
        imageWidth,
        imageHeight
      } = this;
      const {
        clientWidth: containerWidth,
        clientHeight: containerHeight
      } = this.$el;
      if (!imageWidth || !imageHeight || !containerWidth || !containerHeight) return {};
      const imageAspectRatio = imageWidth / imageHeight;
      const containerAspectRatio = containerWidth / containerHeight;
      if (fit === ObjectFit.SCALE_DOWN) {
        const isSmaller = imageWidth < containerWidth && imageHeight < containerHeight;
        fit = isSmaller ? ObjectFit.NONE : ObjectFit.CONTAIN;
      }
      switch (fit) {
        case ObjectFit.NONE:
          return {
            width: this.width + '% !important',
            height: 'auto'
          };
        case ObjectFit.CONTAIN:
          return imageAspectRatio < containerAspectRatio ? {
            width: this.width + '% !important'
          } : {
            height: 'auto',
            width: this.width + '% !important'
          };
        case ObjectFit.COVER:
          return imageAspectRatio < containerAspectRatio ? {
            width: this.width + '% !important'
          } : {
            width: 'auto',
            width: this.width + '% !important'
          };
        default:
          return {
            width: this.width + '% !important'
          };
      }
    },
    clickHandler(isByTitle) {
      // don't show viewer when preview is false
      if (!this.preview) {
        return;
      }
      if (this.clickEffect === 'dian-ji-fang-da') {
        let imgList = [];
        this.previewSrcList.map((item, index) => {
          imgList.push({
            url: item,
            title: this.title[index]
          });
        });
        this.$EventBus.$emit('learningStatistics', {
          type: 'image',
          info: null
        });
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: this.current,
          imgList
        });
      }
      if (isByTitle) {
        this.$emit('onBottomTitleClick');
      }
    }
  }
};

/* script */
const __vue_script__$5F = script$5F;
/* template */
var __vue_render__$5F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "placeholder",
      staticClass: "el-image",
      staticStyle: { "{\n      width": "100px" },
    },
    [
      _vm.self && _vm.self.title && _vm.showTitle && _vm.titlePosition === "on"
        ? _c("div", {
            staticClass: "xml-image-title image-title--lay image-title--on",
            style: { textAlign: _vm.titleAlign || "left" },
            domProps: { innerHTML: _vm._s(_vm.self.title) },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.loading
        ? _vm._t("placeholder", function () {
            return [
              _c(
                "div",
                {
                  staticStyle: {
                    width: "100%",
                    display: "flex",
                    "justify-content": "center",
                  },
                },
                [
                  _c(
                    "div",
                    {
                      staticClass: "el-image__placeholder",
                      style: _vm.placeholderStyle,
                    },
                    [_c("i", { staticClass: "el-icon-loading" })]
                  ),
                ]
              ),
            ]
          })
        : _c(
            "img",
            _vm._g(
              _vm._b(
                {
                  staticClass: "el-image__inner",
                  class: {
                    "el-image__inner--center": _vm.alignCenter,
                    "el-image__preview": _vm.preview,
                  },
                  style: _vm.imageStyle,
                  attrs: { src: _vm.src },
                  on: { click: _vm.clickHandler },
                },
                "img",
                _vm.$attrs,
                false
              ),
              _vm.$listeners
            )
          ),
      _vm._v(" "),
      _vm.self &&
      _vm.self.title &&
      _vm.showTitle &&
      _vm.titlePosition === "bottom"
        ? _c("div", {
            staticClass:
              "xml-image-title image-title--fixed image-title--bottom",
            style: { textAlign: _vm.titleAlign || "left" },
            domProps: { innerHTML: _vm._s(_vm.self.title) },
            on: {
              click: function ($event) {
                return _vm.clickHandler(1)
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.self && _vm.self.title && _vm.showTitle && _vm.titlePosition === "top"
        ? _c(
            "div",
            {
              staticClass:
                "xml-image-title image-title--fixed image-title--top",
              style: { textAlign: _vm.titleAlign || "left" },
              on: {
                click: function ($event) {
                  return _vm.clickHandler(1)
                },
              },
            },
            [_vm._v("\n    " + _vm._s(_vm.self.title) + "\n  ")]
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.self &&
      _vm.self.title &&
      _vm.showTitle &&
      _vm.titlePosition === "under"
        ? _c("div", {
            staticClass: "xml-image-title image-title--lay image-title--under",
            style: { textAlign: _vm.titleAlign || "left" },
            domProps: { innerHTML: _vm._s(_vm.self.title) },
          })
        : _vm._e(),
    ],
    2
  )
};
var __vue_staticRenderFns__$5F = [];
__vue_render__$5F._withStripped = true;

  /* style */
  const __vue_inject_styles__$5F = undefined;
  /* scoped */
  const __vue_scope_id__$5F = "data-v-7c7f955d";
  /* module identifier */
  const __vue_module_identifier__$5F = undefined;
  /* functional template */
  const __vue_is_functional_template__$5F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5F, staticRenderFns: __vue_staticRenderFns__$5F },
    __vue_inject_styles__$5F,
    __vue_script__$5F,
    __vue_scope_id__$5F,
    __vue_is_functional_template__$5F,
    __vue_module_identifier__$5F,
    false,
    undefined,
    undefined,
    undefined
  );

let hasModal = false;
let hasInitZIndex = false;
let zIndex$1;
const getModal = function () {
  if (Vue.prototype.$isServer) return;
  let modalDom = PopupManager.modalDom;
  if (modalDom) {
    hasModal = true;
  } else {
    hasModal = false;
    modalDom = document.createElement('div');
    PopupManager.modalDom = modalDom;
    modalDom.addEventListener('touchmove', function (event) {
      event.preventDefault();
      event.stopPropagation();
    });
    modalDom.addEventListener('click', function () {
      PopupManager.doOnModalClick && PopupManager.doOnModalClick();
    });
  }
  return modalDom;
};
const instances = {};
const PopupManager = {
  modalFade: true,
  getInstance: function (id) {
    return instances[id];
  },
  register: function (id, instance) {
    if (id && instance) {
      instances[id] = instance;
    }
  },
  deregister: function (id) {
    if (id) {
      instances[id] = null;
      delete instances[id];
    }
  },
  nextZIndex: function () {
    return PopupManager.zIndex++;
  },
  modalStack: [],
  doOnModalClick: function () {
    const topItem = PopupManager.modalStack[PopupManager.modalStack.length - 1];
    if (!topItem) return;
    const instance = PopupManager.getInstance(topItem.id);
    if (instance && instance.closeOnClickModal) {
      instance.close();
    }
  },
  openModal: function (id, zIndex, dom, modalClass, modalFade) {
    if (Vue.prototype.$isServer) return;
    if (!id || zIndex === undefined) return;
    this.modalFade = modalFade;
    const modalStack = this.modalStack;
    for (let i = 0, j = modalStack.length; i < j; i++) {
      const item = modalStack[i];
      if (item.id === id) {
        return;
      }
    }
    const modalDom = getModal();
    addClass$1(modalDom, 'v-modal');
    if (this.modalFade && !hasModal) {
      addClass$1(modalDom, 'v-modal-enter');
    }
    if (modalClass) {
      let classArr = modalClass.trim().split(/\s+/);
      classArr.forEach(item => addClass$1(modalDom, item));
    }
    setTimeout(() => {
      removeClass$1(modalDom, 'v-modal-enter');
    }, 200);
    if (dom && dom.parentNode && dom.parentNode.nodeType !== 11) {
      dom.parentNode.appendChild(modalDom);
    } else {
      document.body.appendChild(modalDom);
    }
    if (zIndex) {
      modalDom.style.zIndex = zIndex;
    }
    modalDom.tabIndex = 0;
    modalDom.style.display = '';
    this.modalStack.push({
      id: id,
      zIndex: zIndex,
      modalClass: modalClass
    });
  },
  closeModal: function (id) {
    const modalStack = this.modalStack;
    const modalDom = getModal();
    if (modalStack.length > 0) {
      const topItem = modalStack[modalStack.length - 1];
      if (topItem.id === id) {
        if (topItem.modalClass) {
          let classArr = topItem.modalClass.trim().split(/\s+/);
          classArr.forEach(item => removeClass$1(modalDom, item));
        }
        modalStack.pop();
        if (modalStack.length > 0) {
          modalDom.style.zIndex = modalStack[modalStack.length - 1].zIndex;
        }
      } else {
        for (let i = modalStack.length - 1; i >= 0; i--) {
          if (modalStack[i].id === id) {
            modalStack.splice(i, 1);
            break;
          }
        }
      }
    }
    if (modalStack.length === 0) {
      if (this.modalFade) {
        addClass$1(modalDom, 'v-modal-leave');
      }
      setTimeout(() => {
        if (modalStack.length === 0) {
          if (modalDom.parentNode) modalDom.parentNode.removeChild(modalDom);
          modalDom.style.display = 'none';
          PopupManager.modalDom = undefined;
        }
        removeClass$1(modalDom, 'v-modal-leave');
      }, 200);
    }
  }
};
Object.defineProperty(PopupManager, 'zIndex', {
  configurable: true,
  get() {
    if (!hasInitZIndex) {
      zIndex$1 = zIndex$1 || (Vue.prototype.$ELEMENT || {}).zIndex || 2000;
      hasInitZIndex = true;
    }
    return zIndex$1;
  },
  set(value) {
    zIndex$1 = value;
  }
});
const getTopPopup = function () {
  if (Vue.prototype.$isServer) return;
  if (PopupManager.modalStack.length > 0) {
    const topPopup = PopupManager.modalStack[PopupManager.modalStack.length - 1];
    if (!topPopup) return;
    const instance = PopupManager.getInstance(topPopup.id);
    return instance;
  }
};
if (!Vue.prototype.$isServer) {
  // handle `esc` key when the popup is shown
  window.addEventListener('keydown', function (event) {
    if (event.keyCode === 27) {
      const topPopup = getTopPopup();
      if (topPopup && topPopup.closeOnPressEscape) {
        topPopup.handleClose ? topPopup.handleClose() : topPopup.handleAction ? topPopup.handleAction('cancel') : topPopup.close();
      }
    }
  });
}

//
const Mode = {
  CONTAIN: {
    name: 'contain',
    icon: 'el-icon-full-screen'
  },
  ORIGINAL: {
    name: 'original',
    icon: 'el-icon-c-scale-to-original'
  }
};
const mousewheelEventName = isFirefox() ? 'DOMMouseScroll' : 'mousewheel';
var script$5E = {
  name: 'XmlImageViewer',
  props: {
    urlList: {
      type: Array,
      default: () => []
    },
    zIndex: {
      type: Number,
      default: 2000
    },
    onSwitch: {
      type: Function,
      default: () => {}
    },
    onClose: {
      type: Function,
      default: () => {}
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    title: {
      type: Array,
      default: () => []
    },
    contents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      index: this.initialIndex,
      isShow: false,
      infinite: true,
      loading: false,
      mode: Mode.CONTAIN,
      transform: {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false
      },
      contentExpand: false,
      ellipsisReady: true,
      showEllipsisButton: false,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      slide: false
    };
  },
  computed: {
    isSingle() {
      return this.urlList.length <= 1;
    },
    isFirst() {
      return this.index === 0;
    },
    isLast() {
      return this.index === this.urlList.length - 1;
    },
    currentImg() {
      return this.urlList[this.index];
    },
    imgStyle() {
      const {
        scale,
        deg,
        offsetX,
        offsetY,
        enableTransition
      } = this.transform;
      const style = {
        transform: `scale(${scale}) rotate(${deg}deg)`,
        transition: enableTransition ? 'transform .3s' : '',
        'margin-left': `${offsetX}px`,
        'margin-top': `${offsetY}px`
      };
      if (this.mode === Mode.CONTAIN) {
        style.maxWidth = style.maxHeight = '100%';
      }
      return style;
    },
    viewerZIndex() {
      const nextZIndex = PopupManager.nextZIndex();
      return this.zIndex > nextZIndex ? this.zIndex : nextZIndex;
    }
  },
  watch: {
    index: {
      handler: function (val) {
        this.reset();
        this.onSwitch(val);
        this.contentExpand = false;
        this.ellipsisReady = true;
        this.setEllipsisButton();
      }
    },
    currentImg(val) {
      this.$nextTick(_ => {
        const $img = this.$refs.img[0];
        if (!$img.complete) {
          this.loading = true;
        }
      });
    }
  },
  methods: {
    setEllipsisButton() {
      this.$nextTick(() => {
        let wrapper = this.$refs['el-image-viewer__wrapper'] ? this.$refs['el-image-viewer__wrapper'].getBoundingClientRect() : {};
        if (!wrapper.width) {
          this.showEllipsisButton = false;
          return;
        }
        let content = this.contents.length ? this.contents[this.index] : '';
        let vDom = document.createElement('div');
        vDom.style.width = wrapper.width + 'px';
        vDom.style.padding = '0 15px';
        vDom.style.fontSize = '14px';
        vDom.style.lineHeight = '22px';
        vDom.style.position = 'absolute';
        vDom.style.top = '-9999px';
        vDom.innerHTML = content;
        document.documentElement.appendChild(vDom);
        let vRect = vDom.getBoundingClientRect();
        if (vRect.height && vRect.height > 66) {
          this.showEllipsisButton = true;
          return;
        }
        this.showEllipsisButton = false;
      });
    },
    toggleEllipsis() {
      this.contentExpand = !this.contentExpand;
      if (this.contentExpand) {
        this.ellipsisReady = false;
        setTimeout(() => {
          this.ellipsisReady = true;
        }, 80);
      }
    },
    hide() {
      this.deviceSupportUninstall();
      this.onClose();
    },
    deviceSupportInstall() {
      this._keyDownHandler = e => {
        e.stopPropagation();
        const keyCode = e.keyCode;
        switch (keyCode) {
          // ESC
          case 27:
            this.hide();
            break;
          // SPACE
          case 32:
            this.toggleMode();
            break;
          // LEFT_ARROW
          case 37:
            this.prev();
            break;
          // UP_ARROW
          case 38:
            this.handleActions('zoomIn');
            break;
          // RIGHT_ARROW
          case 39:
            this.next();
            break;
          // DOWN_ARROW
          case 40:
            this.handleActions('zoomOut');
            break;
        }
      };
      this._mouseWheelHandler = rafThrottle(e => {
        const delta = e.wheelDelta ? e.wheelDelta : -e.detail;
        if (delta > 0) {
          this.handleActions('zoomIn', {
            zoomRate: 0.015,
            enableTransition: false
          });
        } else {
          this.handleActions('zoomOut', {
            zoomRate: 0.015,
            enableTransition: false
          });
        }
      });
      on$1(document, 'keydown', this._keyDownHandler);
      on$1(document, mousewheelEventName, this._mouseWheelHandler);
    },
    deviceSupportUninstall() {
      off$1(document, 'keydown', this._keyDownHandler);
      off$1(document, mousewheelEventName, this._mouseWheelHandler);
      this._keyDownHandler = null;
      this._mouseWheelHandler = null;
    },
    handleImgLoad(e) {
      this.loading = false;
    },
    handleImgError(e) {
      this.loading = false;
      e.target.alt = '加载失败';
    },
    // 移动端触摸开始
    touchstart(event) {
      let ev = event.touches[0];
      this.startX = ev.pageX;
      this.startY = ev.pageY;
      this.offsetY = this.transform.offsetY;
      this.offsetX = this.transform.offsetX;
      if (this.loading || ev.button !== 0) return;
    },
    // 移动端触摸结束
    touchmove(event) {
      let ev = event.touches[0];
      if (!this.slide) {
        this.slide = true;
        this.transform.offsetX = this.offsetX + ev.pageX - this.startX;
        this.transform.offsetY = this.offsetY + ev.pageY - this.startY;
        setTimeout(() => {
          this.slide = false;
        }, 100);
      }
      event.preventDefault();
    },
    handleMouseDown(e) {
      if (this.loading || e.button !== 0) return;
      const {
        offsetX,
        offsetY
      } = this.transform;
      const startX = e.pageX;
      const startY = e.pageY;
      this._dragHandler = rafThrottle(ev => {
        this.transform.offsetX = offsetX + ev.pageX - startX;
        this.transform.offsetY = offsetY + ev.pageY - startY;
      });
      on$1(document, 'mousemove', this._dragHandler);
      on$1(document, 'mouseup', ev => {
        off$1(document, 'mousemove', this._dragHandler);
      });
      e.preventDefault();
    },
    handleMaskClick() {
      if (this.maskClosable) {
        this.hide();
      }
    },
    reset() {
      this.transform = {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false
      };
    },
    toggleMode() {
      if (this.loading) return;
      const modeNames = Object.keys(Mode);
      const modeValues = Object.values(Mode);
      const index = modeValues.indexOf(this.mode);
      const nextIndex = (index + 1) % modeNames.length;
      this.mode = Mode[modeNames[nextIndex]];
      this.reset();
    },
    prev() {
      if (this.isFirst && !this.infinite) return;
      const len = this.urlList.length;
      this.index = (this.index - 1 + len) % len;
    },
    next() {
      if (this.isLast && !this.infinite) return;
      const len = this.urlList.length;
      this.index = (this.index + 1) % len;
    },
    handleActions(action, options = {}) {
      if (this.loading) return;
      const {
        zoomRate,
        rotateDeg,
        enableTransition
      } = {
        zoomRate: 0.2,
        rotateDeg: 90,
        enableTransition: true,
        ...options
      };
      const {
        transform
      } = this;
      switch (action) {
        case 'zoomOut':
          if (transform.scale > 0.2) {
            transform.scale = parseFloat((transform.scale - zoomRate).toFixed(3));
          }
          break;
        case 'zoomIn':
          transform.scale = parseFloat((transform.scale + zoomRate).toFixed(3));
          break;
        case 'clocelise':
          transform.deg += rotateDeg;
          break;
        case 'anticlocelise':
          transform.deg -= rotateDeg;
          break;
      }
      transform.enableTransition = enableTransition;
    }
  },
  mounted() {
    this.deviceSupportInstall();
    if (this.appendToBody) {
      document.body.appendChild(this.$el);
    }
    // add tabindex then wrapper can be focusable via Javascript
    // focus wrapper so arrow key can't cause inner scroll behavior underneath
    this.$refs['el-image-viewer__wrapper'].focus();
    this.setEllipsisButton();
  },
  destroyed() {
    // if appendToBody is true, remove DOM node after destroy
    if (this.appendToBody && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }
};

/* script */
const __vue_script__$5E = script$5E;

/* template */
var __vue_render__$5E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("transition", { attrs: { name: "viewer-fade" } }, [
    _c(
      "div",
      {
        ref: "el-image-viewer__wrapper",
        staticClass: "el-image-viewer__wrapper",
        style: { "z-index": _vm.viewerZIndex },
        attrs: { tabindex: "-1" },
      },
      [
        _c("div", {
          staticClass: "el-image-viewer__mask",
          on: {
            click: function ($event) {
              if ($event.target !== $event.currentTarget) {
                return null
              }
              return _vm.handleMaskClick.apply(null, arguments)
            },
          },
        }),
        _vm._v(" "),
        _c(
          "span",
          {
            staticClass: "el-image-viewer__btn el-image-viewer__close",
            on: { click: _vm.hide },
          },
          [_c("i", { staticClass: "el-icon-close" })]
        ),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "el-image-viewer__btn el-image-viewer__actions" },
          [
            _c("div", { staticClass: "el-image-viewer__actions__inner" }, [
              !_vm.isSingle
                ? _c("i", {
                    class: [
                      "el-icon-arrow-left",
                      !_vm.infinite && _vm.isFirst ? "is-disabled" : "",
                    ],
                    on: { click: _vm.prev },
                  })
                : _vm._e(),
              _vm._v(" "),
              _c("i", {
                staticClass: "el-icon-zoom-out",
                on: {
                  click: function ($event) {
                    return _vm.handleActions("zoomOut")
                  },
                },
              }),
              _vm._v(" "),
              _c("i", {
                staticClass: "el-icon-zoom-in",
                on: {
                  click: function ($event) {
                    return _vm.handleActions("zoomIn")
                  },
                },
              }),
              _vm._v(" "),
              _c("i", { staticClass: "el-image-viewer__actions__divider" }),
              _vm._v(" "),
              _c("i", { class: _vm.mode.icon, on: { click: _vm.toggleMode } }),
              _vm._v(" "),
              _c("i", { staticClass: "el-image-viewer__actions__divider" }),
              _vm._v(" "),
              _c("i", {
                staticClass: "el-icon-refresh-left",
                on: {
                  click: function ($event) {
                    return _vm.handleActions("anticlocelise")
                  },
                },
              }),
              _vm._v(" "),
              _c("i", {
                staticClass: "el-icon-refresh-right",
                on: {
                  click: function ($event) {
                    return _vm.handleActions("clocelise")
                  },
                },
              }),
              _vm._v(" "),
              !_vm.isSingle
                ? _c("i", {
                    class: [
                      "el-icon-arrow-right",
                      !_vm.infinite && _vm.isLast ? "is-disabled" : "",
                    ],
                    on: { click: _vm.next },
                  })
                : _vm._e(),
            ]),
          ]
        ),
        _vm._v(" "),
        _vm.title.length && _vm.title[_vm.index]
          ? _c(
              "div",
              { staticClass: "el-image-viewer__btn el-image-viewer__title" },
              [
                _vm._v(
                  "\n      " +
                    _vm._s(_vm.title.length ? _vm.title[_vm.index] : "") +
                    "\n    "
                ),
              ]
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.contents.length && _vm.contents[_vm.index]
          ? _c(
              "div",
              { staticClass: "el-image-viewer__btn el-image-viewer__content" },
              [
                _c(
                  "el-collapse-transition",
                  { attrs: { name: "el-fade-in" } },
                  [
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: _vm.ellipsisReady,
                            expression: "ellipsisReady",
                          },
                        ],
                        class: [
                          "ellipsis-box",
                          _vm.contentExpand ? "" : "text-ellipsis-3",
                        ],
                      },
                      [
                        _vm._v(
                          "\n          " +
                            _vm._s(
                              _vm.contents.length ? _vm.contents[_vm.index] : ""
                            ) +
                            "\n        "
                        ),
                      ]
                    ),
                  ]
                ),
                _vm._v(" "),
                _c(
                  "el-button",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.showEllipsisButton,
                        expression: "showEllipsisButton",
                      },
                    ],
                    attrs: { type: "text", size: "mini" },
                    on: { click: _vm.toggleEllipsis },
                  },
                  [
                    _vm._v(_vm._s(_vm.contentExpand ? "收起" : "展开")),
                    _c("i", {
                      class: _vm.contentExpand
                        ? "el-icon-arrow-up"
                        : "el-icon-arrow-down",
                    }),
                  ]
                ),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "el-image-viewer__canvas" },
          _vm._l(_vm.urlList, function (url, i) {
            return i === _vm.index
              ? _c("img", {
                  key: url,
                  ref: "img",
                  refInFor: true,
                  staticClass: "el-image-viewer__img",
                  style: _vm.imgStyle,
                  attrs: { src: _vm.currentImg },
                  on: {
                    load: _vm.handleImgLoad,
                    error: _vm.handleImgError,
                    mousedown: _vm.handleMouseDown,
                    touchstart: _vm.touchstart,
                    touchmove: _vm.touchmove,
                  },
                })
              : _vm._e()
          }),
          0
        ),
      ]
    ),
  ])
};
var __vue_staticRenderFns__$5E = [];
__vue_render__$5E._withStripped = true;

  /* style */
  const __vue_inject_styles__$5E = undefined;
  /* scoped */
  const __vue_scope_id__$5E = undefined;
  /* module identifier */
  const __vue_module_identifier__$5E = undefined;
  /* functional template */
  const __vue_is_functional_template__$5E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5E, staticRenderFns: __vue_staticRenderFns__$5E },
    __vue_inject_styles__$5E,
    __vue_script__$5E,
    __vue_scope_id__$5E,
    __vue_is_functional_template__$5E,
    __vue_module_identifier__$5E,
    false,
    undefined,
    undefined,
    undefined
  );

/* istanbul ignore next */
__vue_component__$5F.install = function (Vue) {
  Vue.component(__vue_component__$5F.name, __vue_component__$5F);
};

//
/***
 * 文本块
 */
var script$5D = {
  name: 'XmlImageRender',
  components: {
    previewImageRender: __vue_component__$5F
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    pBlockData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    showTitle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // image,
      // noImage,
      previewKey: Math.random(),
      blockId: `blockId${Math.random()}`,
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        imageMode: 'dan-tu',
        showEffect: 'hua-lang',
        clickEffect: 'wu',
        titlePosition: 'under',
        // width: 100,
        titleAlign: 'left',
        imageList: [{
          source: 'upload',
          //来源：上传，素材
          uploadFileUrl: '',
          title: '',
          content: '',
          href: '' //跳转链接
        }]
      },
      carouselActiveIndex: 0,
      //属性组件名
      propertyComponentsName: 'xml-image-property',
      currentPreviewList: [],
      carouselHeight: '',
      currentIndex: -1
    };
  },
  computed: {
    blockPreviewList() {
      let images = this.blockData.imageList || [],
        list = [];
      images.map(item => {
        let src = this.resourceBasisPath ? this.resourceBasisPath + item.uploadFileUrl.split('./')[1] : item.uploadFileUrl;
        list.push(src);
      });
      return this.blockData.imageMode == 'dan-tu' ? [list[0]] : list;
    },
    blockPreviewTitleList() {
      let images = this.blockData.imageList || [],
        list = [];
      images.map(item => {
        list.push(item.title);
      });
      return this.blockData.imageMode == 'dan-tu' ? [list[0]] : list;
    },
    blockPreviewContentList() {
      let images = this.blockData.imageList || [],
        list = [];
      images.map(item => {
        list.push(item.content || '');
      });
      return this.blockData.imageMode == 'dan-tu' ? [list[0]] : list;
    },
    indicatorText() {
      let allNum = this.blockData.imageList.length;
      return `${this.carouselActiveIndex + 1} / ${allNum}`;
    },
    carouselActiveTitle() {
      return this.blockData.imageList[this.carouselActiveIndex].title || '';
    },
    carouselActiveItem() {
      return this.blockData.imageList[this.carouselActiveIndex] || {};
    },
    allHaveNoTitle() {
      let has = false;
      this.blockData.imageList.forEach(item => {
        if (item.title) {
          has = true;
        }
      });
      return !has;
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    },
    'blockData.showEffect': {
      handler(newValue) {
        if (newValue === 'lun-bo') {
          this.setCarouselHeight();
        }
      },
      deep: true,
      immediate: true
    }
  },
  updated() {},
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    imageClick({
      source,
      uploadFileUrl,
      title,
      link,
      content,
      href,
      businessType,
      linkId,
      cIndex
    }, imgList) {
      if (this.mode === 'editor') return;
      console.log('??????????????????', linkId, cIndex);
      if (cIndex !== undefined) {
        this.currentIndex = cIndex;
      }
      if (this.blockData.clickEffect === 'dian-ji-fang-da') ; else if (this.blockData.clickEffect === 'dian-ji-tiao-zhuan') {
        //点击跳转
        // window.href = href
        if (linkId) {
          // window.open(link, '_blank')
          window.open(process.env.VUE_APP_TEXTBOOK_WEB + 'previewLooseLeaf?pageType=' + this.pageType + '&businessType=looseLeaf' + '&looseLeafId=' + linkId + '&uuid=' + v4() + '&styleType=' + 'B', '_blank');
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }
      }
    },
    setImage(image) {
      let imageEle = image.path[0];
      let {
        naturalWidth,
        naturalHeight
      } = imageEle;
      let renderWidth = naturalWidth / (naturalHeight / 200);
      image.path[0].style.width = `${renderWidth}px`;
    },
    setActiveImageIndex(current) {
      this.carouselActiveIndex = current;
    },
    setCarouselHeight() {
      // 获取容器宽度以16:9计算高度并设置
      let containerRect = this.$refs.carouselContainer ? this.$refs.carouselContainer.getBoundingClientRect() : {
        width: 568
      };
      this.carouselHeight = parseInt(containerRect.width / 16 * 9) + 'px';
    },
    clickImg() {
      this.$emit('clickImg');
    }
  },
  mounted() {
    this.setCarouselHeight();
  }
};

/* script */
const __vue_script__$5D = script$5D;
/* template */
var __vue_render__$5D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "carouselContainer",
      class:
        "xml-image-container-" +
        _vm.pageType +
        " image-container " +
        (_vm.blockData.showEffect === "hua-lang"
          ? "hua-lang-image-container"
          : ""),
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _vm.blockData.imageList.length > 0
        ? [
            _vm.blockData.imageMode == "dan-tu"
              ? _c(
                  "div",
                  { staticClass: "xml-image-dan-tu" },
                  [
                    _c("preview-image-render", {
                      class: "xml-image-dan-tu-" + _vm.pageType,
                      attrs: {
                        src: _vm.blockData.imageList[0].uploadFileUrl
                          ? _vm.blockData.imageList[0].uploadFileUrl.indexOf(
                              "./"
                            ) != -1
                            ? _vm.resourceBasisPath +
                              _vm.blockData.imageList[0].uploadFileUrl.split(
                                "./"
                              )[1]
                            : _vm.blockData.imageList[0].uploadFileUrl
                          : "",
                        mode: _vm.mode,
                        showTitle: _vm.showTitle,
                        pageType: _vm.pageType,
                        "preview-text": _vm.blockPreviewTitleList,
                        contents: _vm.blockPreviewContentList,
                        current: 0,
                        previewSrcList: _vm.blockPreviewList,
                        clickEffect: _vm.blockData.clickEffect,
                        width: _vm.blockData.width,
                      },
                      on: {
                        click: function ($event) {
                          return _vm.imageClick(_vm.blockData.imageList[0])
                        },
                      },
                    }),
                  ],
                  1
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.blockData.imageMode == "duo-tu"
              ? _c(
                  "div",
                  {
                    staticClass: "xml-image-duo-tu",
                    class: "xml-image-duo-tu " + _vm.blockData.showEffect,
                  },
                  [
                    _vm.blockData.showEffect === "hua-lang"
                      ? _c(
                          "div",
                          {
                            class: [
                              "xml-image-hualang",
                              _vm.allHaveNoTitle ? "no-title" : "",
                            ],
                            on: {
                              touchstart: _vm.clickImg,
                              mousedown: _vm.clickImg,
                            },
                          },
                          _vm._l(
                            _vm.blockData.imageList,
                            function (item, index) {
                              return _c("preview-image-render", {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value:
                                      _vm.blockData.showEffect === "hua-lang",
                                    expression:
                                      "blockData.showEffect === 'hua-lang'",
                                  },
                                ],
                                key: index,
                                class: [
                                  "xml-image-" +
                                    _vm.blockData.showEffect +
                                    "-" +
                                    _vm.pageType,
                                  "xml-image-hua-lang  xml-group-item",
                                ],
                                style: {
                                  width: _vm.blockData.width
                                    ? (290 / 100) *
                                        Number(_vm.blockData.width) +
                                      "px"
                                    : "290px",
                                  height:
                                    (_vm.blockData.galleryHeight || 290) + "px",
                                },
                                attrs: {
                                  src: item.uploadFileUrl
                                    ? _vm.blockData.imageList[0].uploadFileUrl.indexOf(
                                        "./"
                                      ) != -1
                                      ? _vm.resourceBasisPath +
                                        item.uploadFileUrl.split("./")[1]
                                      : item.uploadFileUrl
                                    : "",
                                  "preview-text": _vm.blockPreviewTitleList,
                                  previewSrcList: _vm.blockPreviewList,
                                  showTitle: _vm.showTitle,
                                  pageType: _vm.pageType,
                                  current: index,
                                  title: _vm.blockPreviewTitleList,
                                  contents: _vm.blockPreviewContentList,
                                  currentIndex: _vm.currentIndex,
                                  titlePosition: _vm.blockData.titlePosition,
                                  titleAlign: _vm.blockData.titleAlign,
                                  mode: _vm.mode,
                                  self: item,
                                  clickEffect: _vm.blockData.clickEffect,
                                  width: _vm.blockData.width,
                                },
                                on: {
                                  click: function ($event) {
                                    return _vm.imageClick(
                                      Object.assign({}, item, {
                                        cIndex: index,
                                      }),
                                      _vm.blockData
                                    )
                                  },
                                  onBottomTitleClick: function ($event) {
                                    return _vm.imageClick(item, _vm.blockData)
                                  },
                                },
                              })
                            }
                          ),
                          1
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.blockData.showEffect === "ping-pu"
                      ? _c(
                          "div",
                          { staticClass: "xml-image-ping-pu" },
                          _vm._l(
                            _vm.blockData.imageList,
                            function (item, index) {
                              return _c("preview-image-render", {
                                key: index,
                                staticClass: "xml-group-item",
                                class:
                                  "xml-image-" +
                                  _vm.blockData.showEffect +
                                  "-" +
                                  _vm.pageType,
                                attrs: {
                                  src: item.uploadFileUrl
                                    ? _vm.blockData.imageList[0].uploadFileUrl.indexOf(
                                        "./"
                                      ) != -1
                                      ? _vm.resourceBasisPath +
                                        item.uploadFileUrl.split("./")[1]
                                      : item.uploadFileUrl
                                    : "",
                                  fit: "cover",
                                  title: _vm.blockPreviewTitleList,
                                  pageType: _vm.pageType,
                                  contents: _vm.blockPreviewContentList,
                                  currentIndex: _vm.currentIndex,
                                  current: index,
                                  showTitle: _vm.showTitle,
                                  titlePosition: _vm.blockData.titlePosition,
                                  titleAlign: _vm.blockData.titleAlign,
                                  self: item,
                                  mode: _vm.mode,
                                  "preview-text": _vm.blockPreviewTitleList,
                                  previewSrcList: _vm.blockPreviewList,
                                  clickEffect: _vm.blockData.clickEffect,
                                  width: _vm.blockData.width,
                                },
                                on: {
                                  click: function ($event) {
                                    return _vm.imageClick(
                                      Object.assign({}, item, { cIndex: index })
                                    )
                                  },
                                  onBottomTitleClick: function ($event) {
                                    return _vm.imageClick(item, _vm.blockData)
                                  },
                                },
                              })
                            }
                          ),
                          1
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.blockData.showEffect === "lun-bo"
                      ? _c(
                          "div",
                          { staticClass: "xml-image-lun-bo xml-group-item" },
                          [
                            _vm.showTitle &&
                            _vm.blockData.titlePosition === "on" &&
                            !_vm.allHaveNoTitle
                              ? _c("div", {
                                  staticClass:
                                    "image-title--lay image-title--on",
                                  style: {
                                    textAlign:
                                      _vm.blockData.titleAlign || "left",
                                  },
                                  domProps: {
                                    innerHTML: _vm._s(_vm.carouselActiveTitle),
                                  },
                                })
                              : _vm._e(),
                            _vm._v(" "),
                            _c(
                              "el-carousel",
                              {
                                ref: "imageCarousel",
                                attrs: {
                                  height: _vm.carouselHeight,
                                  arrow: "always",
                                  loop: "",
                                  "indicator-position": "none",
                                },
                                on: { change: _vm.setActiveImageIndex },
                              },
                              [
                                _vm._l(
                                  _vm.blockData.imageList,
                                  function (item, index) {
                                    return _c(
                                      "el-carousel-item",
                                      { key: index },
                                      [
                                        _c("img", {
                                          style: {
                                            width: _vm.blockData.width + "%",
                                          },
                                          attrs: {
                                            src: item.uploadFileUrl
                                              ? _vm.blockData.imageList[0].uploadFileUrl.indexOf(
                                                  "./"
                                                ) != -1
                                                ? _vm.resourceBasisPath +
                                                  item.uploadFileUrl.split(
                                                    "./"
                                                  )[1]
                                                : item.uploadFileUrl
                                              : "",
                                          },
                                          on: {
                                            click: function ($event) {
                                              return _vm.imageClick(item)
                                            },
                                          },
                                        }),
                                      ]
                                    )
                                  }
                                ),
                                _vm._v(" "),
                                _c(
                                  "div",
                                  {
                                    class: [
                                      "custom-indicator",
                                      _vm.blockData.titlePosition === "bottom"
                                        ? "withBottomTitle"
                                        : _vm.blockData.titlePosition === "top"
                                        ? "withTopTitle"
                                        : "",
                                    ],
                                  },
                                  [
                                    _vm._v(
                                      "\n            " +
                                        _vm._s(_vm.indicatorText) +
                                        "\n          "
                                    ),
                                  ]
                                ),
                                _vm._v(" "),
                                _vm.showTitle &&
                                _vm.blockData.titlePosition === "bottom" &&
                                _vm.carouselActiveTitle
                                  ? _c("div", {
                                      staticClass:
                                        "image-title--fixed image-title--bottom",
                                      style: {
                                        textAlign:
                                          _vm.blockData.titleAlign || "left",
                                      },
                                      domProps: {
                                        innerHTML: _vm._s(
                                          _vm.carouselActiveTitle
                                        ),
                                      },
                                      on: {
                                        click: function ($event) {
                                          return _vm.imageClick(
                                            _vm.carouselActiveItem
                                          )
                                        },
                                      },
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                _vm.showTitle &&
                                _vm.blockData.titlePosition === "top" &&
                                _vm.carouselActiveTitle
                                  ? _c(
                                      "div",
                                      {
                                        staticClass:
                                          "image-title--fixed image-title--top",
                                        style: {
                                          textAlign:
                                            _vm.blockData.titleAlign || "left",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.imageClick(
                                              _vm.carouselActiveItem
                                            )
                                          },
                                        },
                                      },
                                      [
                                        _vm._v(
                                          "\n            " +
                                            _vm._s(_vm.carouselActiveTitle) +
                                            "\n          "
                                        ),
                                      ]
                                    )
                                  : _vm._e(),
                              ],
                              2
                            ),
                            _vm._v(" "),
                            _vm.showTitle &&
                            _vm.blockData.titlePosition === "under" &&
                            !_vm.allHaveNoTitle
                              ? _c("div", {
                                  staticClass:
                                    "xml-image-title image-title--lay image-title--under",
                                  style: {
                                    textAlign:
                                      _vm.blockData.titleAlign || "left",
                                  },
                                  domProps: {
                                    innerHTML: _vm._s(_vm.carouselActiveTitle),
                                  },
                                })
                              : _vm._e(),
                          ],
                          1
                        )
                      : _vm._e(),
                  ]
                )
              : _vm._e(),
          ]
        : [
            _c(
              "div",
              { staticClass: "xml-noimage-container" },
              [
                _c("el-image", {
                  staticClass: "xml-noimage",
                  class: "xml-image-dan-tu-" + _vm.pageType,
                  attrs: {
                    src: "../../../../static/images/defaultCover/no-image.png",
                    fit: "cover",
                  },
                }),
              ],
              1
            ),
          ],
    ],
    2
  )
};
var __vue_staticRenderFns__$5D = [];
__vue_render__$5D._withStripped = true;

  /* style */
  const __vue_inject_styles__$5D = undefined;
  /* scoped */
  const __vue_scope_id__$5D = undefined;
  /* module identifier */
  const __vue_module_identifier__$5D = undefined;
  /* functional template */
  const __vue_is_functional_template__$5D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5D, staticRenderFns: __vue_staticRenderFns__$5D },
    __vue_inject_styles__$5D,
    __vue_script__$5D,
    __vue_scope_id__$5D,
    __vue_is_functional_template__$5D,
    __vue_module_identifier__$5D,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5D.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('image-render', {
    prefix: options.prefix
  }), __vue_component__$5D);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// import "../block.scss"
var script$5C = {
  name: 'xmlHtmlRender',
  components: {},
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'preview';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isCopyUrl: {
      // 是否显示复制链接
      type: Boolean,
      default: false
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        // console.log('XmlHtml组件(ruleIndex:' + this.ruleIndex + ')，watch触发，变量pBlockData：', newValue)
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);

          // ios扫描二维码微信端不支持srcdoc，改为获取dom写入
          this.$refs.iframe;
          // console.log(this.blockData.code);
          // iframe.contentWindow.document.write(this.blockData.code) // 动态写入返回页面到iframe
          // iframe.contentWindow.document.close()
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-html-property',
      src: 'data:image/png;base64,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'
      // ebookSetList:[]
    };
  },
  mounted() {
    // ios扫描二维码微信端不支持srcdoc，改为获取dom写入
    var iframe = this.$refs.iframe;
    // console.log(this.blockData.code);
    iframe.contentWindow.document.write(this.pBlockData.code); // 动态写入返回页面到iframe
    iframe.contentWindow.document.close();
  },
  methods: {
    catchtouchmove() {},
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    goRead(info) {
      // console.log('🚀 ~ goRead ~ info:', info,this.mode,this.pageType)

      if (this.mode === 'preview') {
        let url = '';
        if (info.htmlType == 'url') {
          url = info.link;
        } else {
          url = info.file.indexOf('./') !== -1 ? this.resourceBasisPath + info.file.split('./')[1] : info.file;
        }
        if (!this.isReader) {
          window.open(info.file);
        } else {
          this.$emit('openHtml', {
            previewUrl: url,
            title: info.materialName
          });
        }
      }
    }
  },
  created() {
    // this.blockData = Object.assign( this.blockData,this.pBlockData)
  }
};

var __$_require_static_images_leaflet_resource_enter_html_cover_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$5C = script$5C;
var __vue_render__$5C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-group-item xml-single-html-group-render",
      class: "xml-ebook-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _vm.mode === "editor"
        ? _c("div", {
            staticClass: "xml-html-mask",
            attrs: { catchtouchmove: "catchtouchmove" },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.blockData.htmlType === "url"
        ? _c("div", { staticStyle: { width: "100%", overflow: "auto" } }, [
            _vm.isCopyUrl
              ? _c(
                  "div",
                  {
                    staticClass: "web-file-box",
                    on: {
                      click: function ($event) {
                        return _vm.$emit("copyHtmlUrl", _vm.blockData)
                      },
                    },
                  },
                  [
                    _c("div", { staticClass: "file-cover" }, [
                      _c("img", {
                        attrs: {
                          src: _vm.blockData.fileCover
                            ? _vm.blockData.fileCover.indexOf("http") !== -1
                              ? _vm.blockData.fileCover
                              : _vm.resourceBasisPath
                              ? _vm.resourceBasisPath +
                                _vm.blockData.fileCover.split("./")[1]
                              : _vm.blockData.file
                            : __$_require_static_images_leaflet_resource_enter_html_cover_png__,
                        },
                      }),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "file-title" }, [
                      _vm._v(
                        "\n        " +
                          _vm._s(
                            _vm.blockData.fileNames
                              ? _vm.blockData.fileNames
                              : "--"
                          ) +
                          "\n      "
                      ),
                    ]),
                  ]
                )
              : !_vm.isCopyUrl && _vm.blockData.styleModel == "A"
              ? _c(
                  "div",
                  {
                    staticClass: "web-file-box",
                    on: {
                      click: function ($event) {
                        return _vm.goRead(_vm.blockData)
                      },
                    },
                  },
                  [
                    _c("div", { staticClass: "file-cover" }, [
                      _c("img", {
                        attrs: {
                          src: _vm.blockData.fileCover
                            ? _vm.blockData.fileCover.indexOf("http") !== -1
                              ? _vm.blockData.fileCover
                              : _vm.resourceBasisPath
                              ? _vm.resourceBasisPath +
                                _vm.blockData.fileCover.split("./")[1]
                              : _vm.blockData.file
                            : __$_require_static_images_leaflet_resource_enter_html_cover_png__,
                        },
                      }),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "file-title" }, [
                      _vm._v(
                        "\n        " +
                          _vm._s(
                            _vm.blockData.fileNames
                              ? _vm.blockData.fileNames
                              : "--"
                          ) +
                          "\n      "
                      ),
                    ]),
                  ]
                )
              : _c("iframe", {
                  style: {
                    width: "100%",
                    border: "none",
                    height: _vm.blockData.htmlHeight + "px",
                    "padding-top": Number(_vm.blockData.htmlTopMargin) + "px",
                    "padding-bottom": _vm.blockData.htmlBottomMargin + "px",
                  },
                  attrs: { src: _vm.blockData.link, loading: "lazy" },
                }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: _vm.blockData.htmlType === "code",
              expression: "blockData.htmlType === 'code'",
            },
          ],
        },
        [
          _c("iframe", {
            ref: "iframe",
            style: {
              width: "100%",
              height: _vm.blockData.htmlHeight + "px",
              border: "none",
              "padding-top": Number(_vm.blockData.htmlTopMargin) + "px",
              "padding-bottom": _vm.blockData.htmlBottomMargin + "px",
            },
            attrs: { loading: "lazy" },
          }),
        ]
      ),
      _vm._v(" "),
      _vm.blockData.htmlType === "file"
        ? _c("div", [
            _vm.blockData.styleModel == "A"
              ? _c(
                  "div",
                  {
                    staticClass: "web-file-box",
                    on: {
                      click: function ($event) {
                        return _vm.goRead(_vm.blockData)
                      },
                    },
                  },
                  [
                    _c("div", { staticClass: "file-cover" }, [
                      _c("img", {
                        attrs: {
                          src: _vm.blockData.fileCover
                            ? _vm.blockData.fileCover.indexOf("http") !== -1
                              ? _vm.blockData.fileCover
                              : _vm.resourceBasisPath
                              ? _vm.resourceBasisPath +
                                _vm.blockData.fileCover.split("./")[1]
                              : _vm.blockData.file
                            : __$_require_static_images_leaflet_resource_enter_html_cover_png__,
                        },
                      }),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "file-title" }, [
                      _vm._v(
                        "\n        " +
                          _vm._s(
                            _vm.blockData.fileNames
                              ? _vm.blockData.fileNames
                              : "--"
                          ) +
                          "\n      "
                      ),
                    ]),
                  ]
                )
              : _c("div", [
                  _c("iframe", {
                    style: {
                      width: "100%",
                      height: _vm.blockData.htmlHeight + "px",
                      border: "none",
                      "padding-top": Number(_vm.blockData.htmlTopMargin) + "px",
                      "padding-bottom": _vm.blockData.htmlBottomMargin + "px",
                    },
                    attrs: {
                      src: _vm.blockData.file
                        ? _vm.blockData.file.indexOf("http") !== -1
                          ? _vm.blockData.file
                          : _vm.resourceBasisPath
                          ? _vm.resourceBasisPath +
                            _vm.blockData.file.split("./")[1]
                          : _vm.blockData.file
                        : "",
                      loading: "lazy",
                    },
                  }),
                ]),
          ])
        : _vm._e(),
    ]
  )
};
var __vue_staticRenderFns__$5C = [];
__vue_render__$5C._withStripped = true;

  /* style */
  const __vue_inject_styles__$5C = undefined;
  /* scoped */
  const __vue_scope_id__$5C = undefined;
  /* module identifier */
  const __vue_module_identifier__$5C = undefined;
  /* functional template */
  const __vue_is_functional_template__$5C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5C, staticRenderFns: __vue_staticRenderFns__$5C },
    __vue_inject_styles__$5C,
    __vue_script__$5C,
    __vue_scope_id__$5C,
    __vue_is_functional_template__$5C,
    __vue_module_identifier__$5C,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5C.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('html-render', {
    prefix: options.prefix
  }), __vue_component__$5C);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// import '../block.scss'
var script$5B = {
  name: 'XmlCatalogRender',
  components: {},
  inject: ['getCatalogList'],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'preview';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      blockId: `blockId${Math.random()}`,
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-catalog-property',
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      filteredTree: []
    };
  },
  computed: {
    catalogList() {
      return this.getCatalogList().data;
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
          this.$forceUpdate();
          // this.ebookSetList=Object.assign({},this.blockData.ebookSetList);
        });
      },
      deep: true,
      immediate: true
    },
    catalogList: {
      handler(newValue) {
        // this.$nextTick(() => {
        //   this.$refs.tree.filter('unexpected')
        // })
        let origin = newValue || [];
        const filterMenu = menuList => {
          return menuList.filter(item => {
            return item.hiddenFlag !== '1';
          }).map(item => {
            item = Object.assign({}, item);
            if (item.children) {
              item.children = filterMenu(item.children);
            }
            return item;
          });
        };
        this.filteredTree = filterMenu(origin);
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    // this.blockData = Object.assign( this.blockData,this.pBlockData)
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    nodeClick(data, node) {
      this.$EventBus.$emit('treeNodeClick', {
        data,
        node
      });
    },
    filterNode(value, data) {
      // if (!value) return true;
      return data.hiddenFlag !== '1';
    }
  }
};

/* script */
const __vue_script__$5B = script$5B;
/* template */
var __vue_render__$5B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-show-catalog xml-group-item",
      class: "xml-catalog-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _c("el-tree", {
        ref: "tree",
        class: "preview-tree-box-" + _vm.pageType,
        staticStyle: { "margin-top": "0px" },
        attrs: {
          id: "built-in-tree",
          draggable: _vm.blockData.draggable,
          "default-expand-all": true,
          "highlight-current": "",
          data: _vm.filteredTree,
          "node-key": "id",
          props: _vm.defaultProps,
          "filter-node-method": _vm.filterNode,
          "expand-on-click-node": false,
        },
        on: { "node-click": _vm.nodeClick },
        scopedSlots: _vm._u([
          {
            key: "default",
            fn: function (ref) {
              var node = ref.node;
              var data = ref.data;
              return _c("div", { staticClass: "catalog-node-label" }, [
                data.uploadFileUrl
                  ? _c("img", {
                      staticStyle: { width: "15px", height: "15px" },
                      attrs: { src: data.uploadFileUrl, alt: "" },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    class: ["node-label", node.expanded ? "expanded-node" : ""],
                  },
                  [_vm._v(_vm._s(node.label))]
                ),
              ])
            },
          },
        ]),
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$5B = [];
__vue_render__$5B._withStripped = true;

  /* style */
  const __vue_inject_styles__$5B = undefined;
  /* scoped */
  const __vue_scope_id__$5B = "data-v-179d841f";
  /* module identifier */
  const __vue_module_identifier__$5B = undefined;
  /* functional template */
  const __vue_is_functional_template__$5B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5B, staticRenderFns: __vue_staticRenderFns__$5B },
    __vue_inject_styles__$5B,
    __vue_script__$5B,
    __vue_scope_id__$5B,
    __vue_is_functional_template__$5B,
    __vue_module_identifier__$5B,
    false,
    undefined,
    undefined,
    undefined
  );

// 提交
__vue_component__$5B.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('catalog-render', {
    prefix: options.prefix
  }), __vue_component__$5B);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$5A = {
  name: 'XmlTextDialogRender',
  props: {
    content: {
      type: String,
      default: ''
    },
    textstyle: {
      type: String,
      default: 'A'
    },
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    projectId: {
      type: String,
      default: ''
    }
  },
  watch: {
    content() {
      this.show = false;
      this.$nextTick(() => {
        this.show = true;
      });
    }
  },
  data() {
    return {
      show: true,
      source: ''
    };
  },
  created() {
    this.source = this.$route.query.pageType;
  },
  methods: {
    formatImg(str) {
      if (str) {
        let data = str.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/gi, (match, p1) => {
          if (match.indexOf('style') !== -1) {
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image.split('style="')[0] + 'style="max-width:100% !important;' + image.split('style="')[1];
          } else {
            return `<img src='${p1.indexOf('http') > -1 ? p1 : this.resourceBasisPath + p1.split('./')[1]}' style="max-width:100%;" />`;
          }
        });
        return data;
      }
    }
  }
};

var __$_require_static_images_leaflet_icons_scroll_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$5A = script$5A;
var __vue_render__$5A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _vm.textstyle == "D"
      ? _c("div", [
          _c("div", { staticClass: "style-body-boxA" }, [
            _c("div", { staticClass: "style-wrapper" }, [
              _c("div", { staticClass: "top-line" }),
              _vm._v(" "),
              _c("div", {
                staticClass: "native-html-wrapper",
                domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
              }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom-margin" }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom-triangle" }),
            ]),
          ]),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.textstyle == "A"
      ? _c("div", [
          _c("div", { staticClass: "style-body-boxA" }, [
            _c("div", { staticClass: "top-bg" }),
            _vm._v(" "),
            _c("div", {
              staticClass: "style-content-box",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "paper-clip-icon" }),
            _vm._v(" "),
            _c("div", { staticClass: "style-content-bg" }),
          ]),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.textstyle == "B"
      ? _c("div", [
          _c("div", { staticClass: "style-body-boxB" }, [
            _c("div", { staticClass: "top-bg" }),
            _vm._v(" "),
            _c("div", {
              staticClass: "style-content-box",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "style-content-bg" }),
            _vm._v(" "),
            _vm._m(0),
          ]),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.textstyle == "C"
      ? _c("div", [
          _c("div", { staticClass: "style-body-boxC" }, [
            _c("div", { staticClass: "top-bg" }),
            _vm._v(" "),
            _c("div", {
              staticClass: "style-content-box",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "style-content-bg" }),
            _vm._v(" "),
            _vm._m(1),
          ]),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.textstyle == "E"
      ? _c("div", [
          _c("div", { staticClass: "style-body-boxE" }, [
            _c("div", { staticClass: "scroll-top" }, [
              _c("img", {
                style: _vm.pageType == "pc" ? "height:20px" : "",
                attrs: {
                  src: __$_require_static_images_leaflet_icons_scroll_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", {
              staticClass: "style-content-box",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "scroll-bottom" }, [
              _c("img", {
                style: _vm.pageType == "pc" ? "height:20px" : "",
                attrs: {
                  src: __$_require_static_images_leaflet_icons_scroll_png__,
                  alt: "",
                },
              }),
            ]),
          ]),
        ])
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$5A = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "top-style-box" }, [
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
    ])
  },
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "top-style-box" }, [
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
      _vm._v(" "),
      _c("div"),
    ])
  },
];
__vue_render__$5A._withStripped = true;

  /* style */
  const __vue_inject_styles__$5A = undefined;
  /* scoped */
  const __vue_scope_id__$5A = "data-v-5546d780";
  /* module identifier */
  const __vue_module_identifier__$5A = undefined;
  /* functional template */
  const __vue_is_functional_template__$5A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5A, staticRenderFns: __vue_staticRenderFns__$5A },
    __vue_inject_styles__$5A,
    __vue_script__$5A,
    __vue_scope_id__$5A,
    __vue_is_functional_template__$5A,
    __vue_module_identifier__$5A,
    false,
    undefined,
    undefined,
    undefined
  );

const reHtml = str => {
  return str ? str.replace(/&((g|l|quo|ldquo|rdquo)t|amp|#39|nbsp);/g, function (m) {
    return {
      '&lt;': '<',
      '&amp;': '&',
      '&quot;': '"',
      '&ldquo;': '“',
      '&rdquo;': '”',
      '&gt;': '>',
      '&#39;': "'",
      '&nbsp;': ' '
    }[m];
  }) : '';
};
class MagicLink extends HTMLElement {
  static get observedAttributes() {
    return ['data-plaintext', 'data-type', 'data-title', 'data-content', 'data-isplaying'];
  }
  constructor() {
    super();
    this.createShadow();
    setTimeout(() => {
      this.insertContent();
    });
  }
  createShadow() {
    this.shadow = this.attachShadow({
      mode: 'closed'
    });
  }
  insertContent() {
    const type = this.getAttribute('data-type');
    const title = this.getAttribute('data-title');
    const wrapper = document.createElement('span');
    wrapper.setAttribute('class', `magic-link magic-link--${type}`);
    wrapper.setAttribute('contenteditable', 'false');
    wrapper.innerHTML = reHtml(title);
    const style = document.createElement('style');
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'WCE内部样式连接',style.isConnected)

    style.textContent = `
      p {
        margin: 0;
      }
      .magic-link {
        display: inline;
        font-size:16px;
        font-weight: normal;
        font-style: normal;
        text-align: left;
        text-indent: initial;
        // color: #6883FA;
        color: #00F;
        transition: all 240ms ease-in-out;
        cursor: pointer;
      }
      .magic-link span:hover {
        text-decoration: underline;
      }
      .wifi-symbol {
        display: inline-block;
        width: 21px;
        height: 21px;
        box-sizing: border-box;
        overflow: hidden;
        vertical-align: text-bottom;
      }
      .wifi-symbol svg {
        display:block;
      }
      .wifi-symbol.playing .play-path {
        animation: fade-in 900ms cubic-bezier(0.43, 0.2, 0.56, 0.94) infinite;
      }
      @keyframes fadeInOut {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    `;
    this.shadow.appendChild(style);
    this.shadow.appendChild(wrapper);
    if (type === 'audio') {
      let audioExtra = document.createElement('div');
      audioExtra.setAttribute('class', 'wifi-symbol');
      audioExtra.innerHTML = `
      <svg width="21" height="21" viewBox="-3 -5 50 64">
        <defs>
          <style>
            .play-path {fill:#3fa9f5;}
            @keyframes fade-in{
              to {opacity: 0;}
            }
            .play-path:nth-of-type(2){
              animation-delay: 0.25s !important;
            }
            .play-path:nth-of-type(3){
              animation-delay: 0.5s !important;
            }
          </style>
        </defs>
        <path class="play-path" d="M12.69,39.57a12,12,0,0,0-1.3-21.73L1.18,28.06Z" />
        <path class="play-path" d="M24,29a17,17,0,0,1-7.67,14.2l3.59,3.6A22,22,0,0,0,18.8,10.44l-3.65,3.64A17,17,0,0,1,24,29Z" />
        <path class="play-path" d="M26.4,2.56,22.83,6.13a27,27,0,0,1,.2,43.6l3.56,3.56A32,32,0,0,0,26.4,2.56Z" />
      </svg>
      `;
      this.shadow.appendChild(audioExtra);
    }
  }
  updateContent({
    key,
    oldValue,
    newValue
  }) {
    let _this = this;
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'wce更新逻辑',[this],this.shadow)
    const type = this.getAttribute('data-type');
    this.getAttribute('data-title');
    const content = this.getAttribute('data-content');
    if (oldValue === null && newValue) {
      // 首次加载
      if (type === 'leaflet' && key === 'data-title') {
        setTimeout(() => {
          const links = this.shadow.querySelectorAll('.magic-link a');
          this.shadow.querySelector('.magic-link.magic-link--leaflet');
          // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'wcce leflet', links, this.shadow)
          links.forEach((elem, index) => {
            elem.addEventListener('click', e => {
              // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'link内点击',e,elem)
              _this.shadow.dispatchEvent(new CustomEvent('openTarget', {
                detail: {
                  aid: elem.id,
                  aindex: index,
                  elem,
                  targets: content
                }
              }));
            });
          });
        }, 50);
      }
    }
    if (oldValue !== null && newValue) {
      // 后续更新
      if (type === 'audio') {
        if (key === 'data-isplaying') {
          let target = this.shadow.querySelector('.wifi-symbol');
          let classNames = newValue === 'true' ? 'wifi-symbol playing' : 'wifi-symbol';
          target.setAttribute('class', classNames);
          // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF", 'target', [target])
        }
      }
    }
  }

  //生命周期 - 首次被插入文档 DOM 时
  connectedCallback() {
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'WCE嵌入',)
    // updateContent(this);
  }

  //生命周期 - 从文档 DOM 中删除时
  disconnectedCallback() {
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'WCE销毁',)
  }

  //生命周期 - 被移动到新的文档时
  adoptedCallback() {
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'WCE迁移',)
  }

  //生命周期 - 增加、删除、修改自身属性时
  attributeChangedCallback(key, oldValue, newValue) {
    // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'WCE更新',{key, oldValue, newValue})
    this.updateContent({
      key,
      oldValue,
      newValue
    });
  }
}

var recorder_mp3_min = createCommonjsModule(function (module) {
/*
录音
https://github.com/xiangyuecn/Recorder
src: recorder-core.js,engine/mp3.js,engine/mp3-engine.js
*/
!function (e) {
  var t = "object" == typeof window && !!window.document,
    a = t ? window : Object;
  !function (e, w) {

    var S = function () {},
      f = function (e) {
        return "number" == typeof e;
      },
      N = function (e) {
        return new l(e);
      },
      M = N.LM = "2024-04-09 19:15",
      y = "https://github.com/xiangyuecn/Recorder",
      R = "Recorder",
      A = "getUserMedia",
      V = "srcSampleRate",
      D = "sampleRate",
      i = "bitRate",
      x = "catch",
      t = e[R];
    if (t && t.LM == M) return t.CLog(t.i18n.$T("K8zP::重复导入{1}", 0, R), 3);
    N.IsOpen = function () {
      var e = N.Stream;
      if (e) {
        var t = e.getTracks && e.getTracks() || e.audioTracks || [],
          a = t[0];
        if (a) {
          var n = a.readyState;
          return "live" == n || n == a.LIVE;
        }
      }
      return !1;
    }, N.BufferSize = 4096, N.Destroy = function () {
      for (var e in O(R + " Destroy"), L(), a) a[e]();
    };
    var a = {};
    N.BindDestroy = function (e, t) {
      a[e] = t;
    }, N.Support = function () {
      if (!w) return !1;
      var e = navigator.mediaDevices || {};
      return e[A] || (e = navigator)[A] || (e[A] = e.webkitGetUserMedia || e.mozGetUserMedia || e.msGetUserMedia), !!e[A] && (N.Scope = e, !!N.GetContext());
    }, N.GetContext = function (e) {
      if (!w) return null;
      var t = window.AudioContext;
      if (t || (t = window.webkitAudioContext), !t) return null;
      var a = N.Ctx;
      if (a && "closed" != a.state || (a = N.Ctx = new t(), N.NewCtxs = N.NewCtxs || [], N.BindDestroy("Ctx", function () {
        var e = N.Ctx;
        e && e.close && (s(e), N.Ctx = 0);
        var t = N.NewCtxs;
        N.NewCtxs = [];
        for (var a = 0; a < t.length; a++) s(t[a]);
      })), e && a.close) try {
        a = new t(), N.NewCtxs.push(a);
      } catch (e) {
        O("GetContext tryNew Error", 1, e);
      }
      return a;
    }, N.CloseNewCtx = function (e) {
      if (e && e != N.Ctx) {
        s(e);
        for (var t = N.NewCtxs || [], a = t.length, n = 0; n < t.length; n++) if (t[n] == e) {
          t.splice(n, 1);
          break;
        }
        O(z("mSxV::剩{1}个GetContext未close", 0, a + "-1=" + t.length), t.length ? 3 : 0);
      }
    };
    var s = function (e) {
        if (e && e.close) {
          e._isC = 1;
          try {
            e.close();
          } catch (e) {
            O("ctx close err", 1, e);
          }
        }
      },
      B = N.ResumeCtx = function (a, n, s, r) {
        var i = 0,
          o = 0,
          _ = 0,
          l = 0,
          f = "EventListener",
          c = "ResumeCtx ",
          u = function (e, t) {
            o && h(), i || (i = 1, e && r(e, l), t && s(l)), t && (!a._LsSC && a["add" + f] && a["add" + f]("statechange", b), a._LsSC = 1, _ = 1);
          },
          h = function (e) {
            if (!e || !o) {
              o = e ? 1 : 0;
              for (var t = ["focus", "mousedown", "mouseup", "touchstart", "touchend"], a = 0; a < t.length; a++) window[(e ? "add" : "remove") + f](t[a], b, !0);
            }
          },
          b = function () {
            var e = a.state,
              t = p(e);
            if (!i && !n(t ? ++l : l)) return u();
            t ? (_ && O(c + "sc " + e, 3), h(1), a.resume().then(function () {
              _ && O(c + "sc " + a.state), u(0, 1);
            })[x](function (e) {
              O(c + "error", 1, e), p(a.state) || u(e.message || "error");
            })) : "closed" == e ? (_ && !a._isC && O(c + "sc " + e, 1), u("ctx closed")) : u(0, 1);
          };
        b();
      },
      p = N.CtxSpEnd = function (e) {
        return "suspended" == e || "interrupted" == e;
      },
      k = function (e) {
        var t = e.state,
          a = "ctx.state=" + t;
        return p(t) && (a += z("nMIy::（注意：ctx不是running状态，rec.open和start至少要有一个在用户操作(触摸、点击等)时进行调用，否则将在rec.start时尝试进行ctx.resume，可能会产生兼容性问题(仅iOS)，请参阅文档中runningContext配置）")), a;
      },
      T = "ConnectEnableWebM";
    N[T] = !0;
    var E = "ConnectEnableWorklet";
    N[E] = !1;
    var C = function (e, _) {
        var l = e.BufferSize || N.BufferSize,
          f = e.Stream,
          c = f._RC || f._c || N.GetContext(!0);
        f._c = c;
        var u,
          a,
          h,
          i = function (e) {
            var t = f._m = c.createMediaStreamSource(f),
              a = c.destination,
              n = "createMediaStreamDestination";
            c[n] && (a = f._d = c[n]()), t.connect(e), e.connect(a);
          },
          b = "",
          p = f._call,
          m = function (e) {
            for (var t in p) {
              for (var a = e.length, n = new Int16Array(a), s = 0, r = 0; r < a; r++) {
                var i = Math.max(-1, Math.min(1, e[r]));
                i = i < 0 ? 32768 * i : 32767 * i, n[r] = i, s += Math.abs(i);
              }
              for (var o in p) p[o](n, s);
              return;
            }
          },
          v = "ScriptProcessor",
          d = "audioWorklet",
          o = R + " " + d,
          g = "RecProc",
          w = "MediaRecorder",
          S = w + ".WebM.PCM",
          M = c.createScriptProcessor || c.createJavaScriptNode,
          y = z("ZGlf::。由于{1}内部1秒375次回调，在移动端可能会有性能问题导致回调丢失录音变短，PC端无影响，暂不建议开启{1}。", 0, d),
          A = function () {
            a = f.isWorklet = !1, n(f), O(z("7TU0::Connect采用老的{1}，", 0, v) + Y.get(z(N[E] ? "JwCL::但已设置{1}尝试启用{2}" : "VGjB::可设置{1}尝试启用{2}", 2), [R + "." + E + "=true", d]) + b + y, 3);
            var e = f._p = M.call(c, l, 1, 1);
            i(e), e.onaudioprocess = function (e) {
              var t = e.inputBuffer.getChannelData(0);
              m(t);
            };
          },
          k = function () {
            u = f.isWebM = !1, I(f), a = f.isWorklet = !M || N[E];
            var t = window.AudioWorkletNode;
            if (a && c[d] && t) {
              var n = function () {
                  return a && f._na;
                },
                s = f._na = function () {
                  "" !== h && (clearTimeout(h), h = setTimeout(function () {
                    h = 0, n() && (O(z("MxX1::{1}未返回任何音频，恢复使用{2}", 0, d, v), 3), M && A());
                  }, 500));
                },
                r = function () {
                  if (n()) {
                    var e = f._n = new t(c, g, {
                      processorOptions: {
                        bufferSize: l
                      }
                    });
                    i(e), e.port.onmessage = function (e) {
                      h && (clearTimeout(h), h = ""), n() ? m(e.data.val) : a || O(z("XUap::{1}多余回调", 0, d), 3);
                    }, O(z("yOta::Connect采用{1}，设置{2}可恢复老式{3}", 0, d, R + "." + E + "=false", v) + b + y, 3);
                  }
                },
                e = function () {
                  if (n()) if (c[g]) r();else {
                    var e,
                      t,
                      a = (t = "class " + g + " extends AudioWorkletProcessor{", t += "constructor " + (e = function (e) {
                        return e.toString().replace(/^function|DEL_/g, "").replace(/\$RA/g, o);
                      })(function (e) {
                        DEL_super(e);
                        var t = this,
                          a = e.processorOptions.bufferSize;
                        t.bufferSize = a, t.buffer = new Float32Array(2 * a), t.pos = 0, t.port.onmessage = function (e) {
                          e.data.kill && (t.kill = !0, $C.log("$RA kill call"));
                        }, $C.log("$RA .ctor call", e);
                      }), t += "process " + e(function (e, t, a) {
                        var n = this,
                          s = n.bufferSize,
                          r = n.buffer,
                          i = n.pos;
                        if ((e = (e[0] || [])[0] || []).length) {
                          r.set(e, i);
                          var o = ~~((i += e.length) / s) * s;
                          if (o) {
                            this.port.postMessage({
                              val: r.slice(0, o)
                            });
                            var _ = r.subarray(o, i);
                            (r = new Float32Array(2 * s)).set(_), i = _.length, n.buffer = r;
                          }
                          n.pos = i;
                        }
                        return !n.kill;
                      }), t = (t += '}try{registerProcessor("' + g + '", ' + g + ')}catch(e){$C.error("' + o + ' Reg Error",e)}').replace(/\$C\./g, "console."), "data:text/javascript;base64," + btoa(unescape(encodeURIComponent(t))));
                    c[d].addModule(a).then(function (e) {
                      n() && (c[g] = 1, r(), h && s());
                    })[x](function (e) {
                      O(d + ".addModule Error", 1, e), n() && A();
                    });
                  }
                };
              B(c, function () {
                return n();
              }, e, e);
            } else A();
          };
        !function () {
          var e = window[w],
            t = "ondataavailable",
            a = "audio/webm; codecs=pcm";
          u = f.isWebM = N[T];
          var n = e && t in e.prototype && e.isTypeSupported(a);
          if (b = n ? "" : z("VwPd::（此浏览器不支持{1}）", 0, S), !_ || !u || !n) return k();
          var s = function () {
              return u && f._ra;
            },
            r = (f._ra = function () {
              "" !== h && (clearTimeout(h), h = setTimeout(function () {
                s() && (O(z("vHnb::{1}未返回任何音频，降级使用{2}", 0, w, d), 3), k());
              }, 500));
            }, Object.assign({
              mimeType: a
            }, N.ConnectWebMOptions)),
            i = f._r = new e(f, r),
            o = f._rd = {
              sampleRate: c[D]
            };
          i[t] = function (e) {
            var t = new FileReader();
            t.onloadend = function () {
              if (s()) {
                var e = P(new Uint8Array(t.result), o);
                if (!e) return;
                if (-1 == e) return void k();
                h && (clearTimeout(h), h = ""), m(e);
              } else u || O(z("O9P7::{1}多余回调", 0, w), 3);
            }, t.readAsArrayBuffer(e.data);
          }, i.start(~~(l / 48)), O(z("LMEm::Connect采用{1}，设置{2}可恢复使用{3}或老式{4}", 0, S, R + "." + T + "=false", d, v));
        }();
      },
      r = function (e) {
        e._na && e._na(), e._ra && e._ra();
      },
      n = function (e) {
        e._na = null, e._n && (e._n.port.postMessage({
          kill: !0
        }), e._n.disconnect(), e._n = null);
      },
      I = function (e) {
        if (e._ra = null, e._r) {
          try {
            e._r.stop();
          } catch (e) {
            O("mr stop err", 1, e);
          }
          e._r = null;
        }
      },
      L = function (e) {
        var t = (e = e || N) == N,
          a = e.Stream;
        a && (a._m && (a._m.disconnect(), a._m = null), !a._RC && a._c && N.CloseNewCtx(a._c), a._RC = null, a._c = null, a._d && (o(a._d.stream), a._d = null), a._p && (a._p.disconnect(), a._p.onaudioprocess = a._p = null), n(a), I(a), t && o(a)), e.Stream = 0;
      },
      o = N.StopS_ = function (e) {
        for (var t = e.getTracks && e.getTracks() || e.audioTracks || [], a = 0; a < t.length; a++) {
          var n = t[a];
          n.stop && n.stop();
        }
        e.stop && e.stop();
      };
    N.SampleData = function (e, t, a, n, s) {
      var r = "SampleData";
      n || (n = {});
      var i = n.index || 0,
        o = n.offset || 0,
        _ = n.filter;
      if (_ && _.fn && _.sr != t && (_ = null, O(z("d48C::{1}的filter采样率变了，重设滤波", 0, r), 3)), !_) {
        var l = 3 * t / 4 < a ? 0 : a / 2 * 3 / 4;
        _ = {
          fn: l ? N.IIRFilter(!0, t, l) : 0
        };
      }
      _.sr = t;
      var f = _.fn,
        c = n.frameNext || [];
      s || (s = {});
      var u = s.frameSize || 1;
      s.frameType && (u = "mp3" == s.frameType ? 1152 : 1);
      var h = e.length;
      h + 1 < i && O(z("tlbC::{1}似乎传入了未重置chunk {2}", 0, r, i + ">" + h), 3);
      for (var b = 0, p = i; p < h; p++) b += e[p].length;
      b = Math.max(0, b - Math.floor(o));
      var m = t / a;
      1 < m ? b = Math.floor(b / m) : (m = 1, a = t), b += c.length;
      for (var v = new Int16Array(b), d = 0, p = 0; p < c.length; p++) v[d] = c[p], d++;
      for (; i < h; i++) {
        for (var g = e[i], p = o, w = g.length, S = f && f.Embed, M = 0, y = 0, A = 0, k = 0, R = 0, x = 0; R < w; R++, x++) if (x < w && (S ? (A = g[x], k = S.b0 * A + S.b1 * S.x1 + S.b0 * S.x2 - S.a1 * S.y1 - S.a2 * S.y2, S.x2 = S.x1, S.x1 = A, S.y2 = S.y1, S.y1 = k) : k = f ? f(g[x]) : g[x]), M = y, y = k, 0 != x) {
          var B = Math.floor(p);
          if (R == B) {
            var T = Math.ceil(p),
              E = p - B,
              C = M,
              I = T < w ? y : C,
              L = C + (I - C) * E;
            32767 < L ? L = 32767 : L < -32768 && (L = -32768), v[d] = L, d++, p += m;
          }
        } else R--;
        o = Math.max(0, p - w);
      }
      c = null;
      var P = v.length % u;
      if (0 < P) {
        var H = 2 * (v.length - P);
        c = new Int16Array(v.buffer.slice(H)), v = new Int16Array(v.buffer.slice(0, H));
      }
      return {
        index: i,
        offset: o,
        filter: _,
        frameNext: c,
        sampleRate: a,
        data: v
      };
    }, N.IIRFilter = function (e, t, a) {
      var n = 2 * Math.PI * a / t,
        s = Math.sin(n),
        r = Math.cos(n),
        i = s / 2,
        o = 1 + i,
        _ = -2 * r / o,
        l = (1 - i) / o;
      if (e) var f = (1 - r) / 2 / o,
        c = (1 - r) / o;else var f = (1 + r) / 2 / o,
        c = -(1 + r) / o;
      var u = 0,
        h = 0,
        b = 0,
        p = 0,
        m = 0,
        v = function (e) {
          return b = f * e + c * u + f * h - _ * p - l * m, h = u, u = e, m = p, p = b;
        };
      return v.Embed = {
        x1: 0,
        x2: 0,
        y1: 0,
        y2: 0,
        b0: f,
        b1: c,
        a1: _,
        a2: l
      }, v;
    }, N.PowerLevel = function (e, t) {
      var a = e / t || 0;
      return a < 1251 ? Math.round(a / 1250 * 10) : Math.round(Math.min(100, Math.max(0, 100 * (1 + Math.log(a / 1e4) / Math.log(10)))));
    }, N.PowerDBFS = function (e) {
      var t = Math.max(.1, e || 0);
      return t = Math.min(t, 32767), t = 20 * Math.log(t / 32767) / Math.log(10), Math.max(-100, Math.round(t));
    }, N.CLog = function (e, t) {
      if ("object" == typeof console) {
        var a = new Date(),
          n = ("0" + a.getMinutes()).substr(-2) + ":" + ("0" + a.getSeconds()).substr(-2) + "." + ("00" + a.getMilliseconds()).substr(-3),
          s = this && this.envIn && this.envCheck && this.id,
          r = ["[" + n + " " + R + (s ? ":" + s : "") + "]" + e],
          i = arguments,
          o = N.CLog,
          _ = 2,
          l = o.log || console.log;
        for (f(t) ? l = 1 == t ? o.error || console.error : 3 == t ? o.warn || console.warn : l : _ = 1; _ < i.length; _++) r.push(i[_]);
        c ? l && l("[IsLoser]" + r[0], 1 < r.length ? r : "") : l.apply(console, r);
      }
    };
    var O = function () {
        N.CLog.apply(this, arguments);
      },
      c = !0;
    try {
      c = !console.log.apply;
    } catch (e) {}
    var _ = 0;
    function l(e) {
      var t = this;
      t.id = ++_, u();
      var a = {
        type: "mp3",
        onProcess: S
      };
      for (var n in e) a[n] = e[n];
      var s = (t.set = a)[i],
        r = a[D];
      (s && !f(s) || r && !f(r)) && t.CLog(z.G("IllegalArgs-1", [z("VtS4::{1}和{2}必须是数值", 0, D, i)]), 1, e), a[i] = +s || 16, a[D] = +r || 16e3, t.state = 0, t._S = 9, t.Sync = {
        O: 9,
        C: 9
      };
    }
    N.Sync = {
      O: 9,
      C: 9
    }, N.prototype = l.prototype = {
      CLog: O,
      _streamStore: function () {
        return this.set.sourceStream ? this : N;
      },
      _streamCtx: function () {
        var e = this._streamStore().Stream;
        return e && e._c;
      },
      open: function (e, a) {
        var n = this,
          s = n.set,
          r = n._streamStore(),
          i = 0;
        e = e || S;
        var o = function (e, t) {
          t = !!t, n.CLog(z("5tWi::录音open失败：") + e + ",isUserNotAllow:" + t, 1), i && N.CloseNewCtx(i), a && a(e, t);
        };
        n._streamTag = A;
        var _ = function () {
            n.CLog("open ok, id:" + n.id + " stream:" + n._streamTag), e(), n._SO = 0;
          },
          l = r.Sync,
          f = ++l.O,
          c = l.C;
        n._O = n._O_ = f, n._SO = n._S;
        if (w) {
          var t = n.envCheck({
            envName: "H5",
            canProcess: !0
          });
          if (t) o(z("A5bm::不能录音：") + t);else if (s.sourceStream) {
            if (n._streamTag = "set.sourceStream", !N.GetContext()) return void o(z("1iU7::不支持此浏览器从流中获取录音"));
            L(r);
            var u = n.Stream = s.sourceStream;
            u._RC = s.runningContext, u._call = {};
            try {
              C(r);
            } catch (e) {
              return L(r), void o(z("BTW2::从流中打开录音失败：") + e.message);
            }
            _();
          } else {
            var h = function (e, t) {
              try {
                window.top.a;
              } catch (e) {
                return void o(z("Nclz::无权录音(跨域，请尝试给iframe添加麦克风访问策略，如{1})", 0, 'allow="camera;microphone"'));
              }
              /Permission|Allow/i.test(e) ? o(z("gyO5::用户拒绝了录音权限"), !0) : !1 === window.isSecureContext ? o(z("oWNo::浏览器禁止不安全页面录音，可开启https解决")) : /Found/i.test(e) ? o(t + z("jBa9::，无可用麦克风")) : o(t);
            };
            if (N.IsOpen()) _();else if (N.Support()) {
              var b = s.runningContext;
              b || (b = i = N.GetContext(!0));
              var p = function (t) {
                  setTimeout(function () {
                    t._call = {};
                    var e = N.Stream;
                    e && (L(), t._call = e._call), (N.Stream = t)._c = b, t._RC = s.runningContext, function () {
                      if (c != l.C || !n._O) {
                        var e = z("dFm8::open被取消");
                        return f == l.O ? n.close() : e = z("VtJO::open被中断"), o(e), !0;
                      }
                    }() || (N.IsOpen() ? (e && n.CLog(z("upb8::发现同时多次调用open"), 1), C(r, 1), _()) : o(z("Q1GA::录音功能无效：无音频流")));
                  }, 100);
                },
                m = function (e) {
                  var t = e.name || e.message || e.code + ":" + e;
                  n.CLog(z("xEQR::请求录音权限错误"), 1, e), h(t, z("bDOG::无法录音：") + t);
                },
                v = s.audioTrackSet || {};
              v[D] = b[D];
              var d = {
                audio: v
              };
              try {
                var g = N.Scope[A](d, p, m);
              } catch (e) {
                n.CLog(A, 3, e), d = {
                  audio: !0
                }, g = N.Scope[A](d, p, m);
              }
              n.CLog(A + "(" + JSON.stringify(d) + ") " + k(b) + z("RiWe::，未配置noiseSuppression和echoCancellation时浏览器可能会自动打开降噪和回声消除，移动端可能会降低系统播放音量（关闭录音后可恢复），请参阅文档中audioTrackSet配置") + "(" + y + ") LM:" + M + " UA:" + navigator.userAgent), g && g.then && g.then(p)[x](m);
            } else h("", z("COxc::此浏览器不支持录音"));
          }
        } else o(z.G("NonBrowser-1", ["open"]) + z("EMJq::，可尝试使用RecordApp解决方案") + "(" + y + "/tree/master/app-support-sample)");
      },
      close: function (e) {
        e = e || S;
        var t = this,
          a = t._streamStore();
        t._stop();
        var n = " stream:" + t._streamTag,
          s = a.Sync;
        if (t._O = 0, t._O_ != s.O) return t.CLog(z("hWVz::close被忽略（因为同时open了多个rec，只有最后一个会真正close）") + n, 3), void e();
        s.C++, L(a), t.CLog("close," + n), e();
      },
      mock: function (e, t) {
        var a = this;
        return a._stop(), a.isMock = 1, a.mockEnvInfo = null, a.buffers = [e], a.recSize = e.length, a._setSrcSR(t), a._streamTag = "mock", a;
      },
      _setSrcSR: function (e) {
        var t = this.set,
          a = t[D];
        e < a ? t[D] = e : a = 0, this[V] = e, this.CLog(V + ": " + e + " set." + D + ": " + t[D] + (a ? " " + z("UHvm::忽略") + ": " + a : ""), a ? 3 : 0);
      },
      envCheck: function (e) {
        var t,
          a = this.set,
          n = "CPU_BE";
        if (t || N[n] || "function" != typeof Int8Array || new Int8Array(new Int32Array([1]).buffer)[0] || (u(n), t = z("Essp::不支持{1}架构", 0, n)), !t) {
          var s = a.type,
            r = this[s + "_envCheck"];
          a.takeoffEncodeChunk && (r ? e.canProcess || (t = z("7uMV::{1}环境不支持实时处理", 0, e.envName)) : t = z("2XBl::{1}类型不支持设置takeoffEncodeChunk", 0, s) + (this[s] ? "" : z("LG7e::(未加载编码器)"))), !t && r && (t = this[s + "_envCheck"](e, a));
        }
        return t || "";
      },
      envStart: function (e, t) {
        var a = this,
          n = a.set;
        if (a.isMock = e ? 1 : 0, a.mockEnvInfo = e, a.buffers = [], a.recSize = 0, e && (a._streamTag = "env$" + e.envName), a.state = 1, a.envInLast = 0, a.envInFirst = 0, a.envInFix = 0, a.envInFixTs = [], a._setSrcSR(t), a.engineCtx = 0, a[n.type + "_start"]) {
          var s = a.engineCtx = a[n.type + "_start"](n);
          s && (s.pcmDatas = [], s.pcmSize = 0);
        }
      },
      envResume: function () {
        this.envInFixTs = [];
      },
      envIn: function (e, t) {
        var s = this,
          r = s.set,
          i = s.engineCtx;
        if (1 == s.state) {
          var a = s[V],
            n = e.length,
            o = N.PowerLevel(t, n),
            _ = s.buffers,
            l = _.length;
          _.push(e);
          var f = _,
            c = l,
            u = Date.now(),
            h = Math.round(n / a * 1e3);
          s.envInLast = u, 1 == s.buffers.length && (s.envInFirst = u - h);
          var b = s.envInFixTs;
          b.splice(0, 0, {
            t: u,
            d: h
          });
          for (var p = u, m = 0, v = 0; v < b.length; v++) {
            var d = b[v];
            if (3e3 < u - d.t) {
              b.length = v;
              break;
            }
            p = d.t, m += d.d;
          }
          var g = b[1],
            w = u - p,
            S = w - m;
          if (w / 3 < S && (g && 1e3 < w || 6 <= b.length)) {
            var M = u - g.t - h;
            if (h / 5 < M) {
              var y = !r.disableEnvInFix;
              if (s.CLog("[" + u + "]" + Y.get(z(y ? "4Kfd::补偿{1}ms" : "bM5i::未补偿{1}ms", 1), [M]), 3), s.envInFix += M, y) {
                var A = new Int16Array(M * a / 1e3);
                n += A.length, _.push(A);
              }
            }
          }
          var k = s.recSize,
            R = n,
            x = k + R;
          if (s.recSize = x, i) {
            var B = N.SampleData(_, a, r[D], i.chunkInfo);
            i.chunkInfo = B, k = i.pcmSize, R = B.data.length, x = k + R, i.pcmSize = x, _ = i.pcmDatas, l = _.length, _.push(B.data), a = B[D];
          }
          var T = Math.round(x / a * 1e3),
            E = _.length,
            C = f.length,
            I = function () {
              for (var e = L ? 0 : -R, t = null == _[0], a = l; a < E; a++) {
                var n = _[a];
                null == n ? t = 1 : (e += n.length, i && n.length && s[r.type + "_encode"](i, n));
              }
              if (t && i) {
                var a = c;
                for (f[0] && (a = 0); a < C; a++) f[a] = null;
              }
              t && (e = L ? R : 0, _[0] = null), i ? i.pcmSize += e : s.recSize += e;
            },
            L = 0,
            P = "rec.set.onProcess";
          try {
            L = r.onProcess(_, o, T, a, l, I);
          } catch (e) {
            console.error(P + z("gFUF::回调出错是不允许的，需保证不会抛异常"), e);
          }
          var H = Date.now() - u;
          if (10 < H && 1e3 < s.envInFirst - u && s.CLog(P + z("2ghS::低性能，耗时{1}ms", 0, H), 3), !0 === L) {
            for (var O = 0, v = l; v < E; v++) null == _[v] ? O = 1 : _[v] = new Int16Array(0);
            O ? s.CLog(z("ufqH::未进入异步前不能清除buffers"), 3) : i ? i.pcmSize -= R : s.recSize -= R;
          } else I();
        } else s.state || s.CLog("envIn at state=0", 3);
      },
      start: function () {
        var t = this,
          e = 1;
        if (t.set.sourceStream ? t.Stream || (e = 0) : N.IsOpen() || (e = 0), e) {
          var a = t._streamCtx();
          if (t.CLog(z("kLDN::start 开始录音，") + k(a) + " stream:" + t._streamTag), t._stop(), t.envStart(null, a[D]), t.state = 3, t._SO && t._SO + 1 != t._S) t.CLog(z("Bp2y::start被中断"), 3);else {
            t._SO = 0;
            var n = function () {
                3 == t.state && (t.state = 1, t.resume());
              },
              s = "AudioContext resume: ";
            B(a, function (e) {
              return e && t.CLog(s + "wait..."), 3 == t.state;
            }, function (e) {
              e && t.CLog(s + a.state), n();
            }, function (e) {
              t.CLog(s + a.state + z("upkE::，可能无法录音：") + e, 1), n();
            });
          }
        } else t.CLog(z("6WmN::start失败：未open"), 1);
      },
      pause: function () {
        var e = this,
          t = e._streamStore().Stream;
        e.state && (e.state = 2, e.CLog("pause"), t && delete t._call[e.id]);
      },
      resume: function () {
        var a = this,
          t = a._streamStore().Stream,
          n = "resume(wait ctx)";
        if (3 == a.state) a.CLog(n);else if (a.state) {
          a.state = 1, a.CLog("resume"), a.envResume(), t && (t._call[a.id] = function (e, t) {
            1 == a.state && a.envIn(e, t);
          }, r(t));
          var s = a._streamCtx();
          s && B(s, function (e) {
            return e && a.CLog(n + "..."), 1 == a.state;
          }, function (e) {
            e && a.CLog(n + s.state), r(t);
          }, function (e) {
            a.CLog(n + s.state + "[err]" + e, 1);
          });
        }
      },
      _stop: function (e) {
        var t = this,
          a = t.set;
        t.isMock || t._S++, t.state && (t.pause(), t.state = 0), !e && t[a.type + "_stop"] && (t[a.type + "_stop"](t.engineCtx), t.engineCtx = 0);
      },
      stop: function (c, t, e) {
        var u,
          h = this,
          b = h.set,
          a = h.envInLast - h.envInFirst,
          n = a && h.buffers.length;
        h.CLog(z("Xq4s::stop 和start时差:") + (a ? a + "ms " + z("3CQP::补偿:") + h.envInFix + "ms envIn:" + n + " fps:" + (n / a * 1e3).toFixed(1) : "-") + " stream:" + h._streamTag + " (" + y + ") LM:" + M);
        var p = function () {
            h._stop(), e && h.close();
          },
          m = function (e) {
            h.CLog(z("u8JG::结束录音失败：") + e, 1), t && t(e), p();
          },
          s = function (e, t, a) {
            var n = "arraybuffer",
              s = "dataType",
              r = "DefaultDataType",
              i = h[s] || N[r] || "blob",
              o = s + "=" + i,
              _ = e instanceof ArrayBuffer,
              l = 0,
              f = _ ? e.byteLength : e.size;
            if (i == n ? _ || (l = 1) : "blob" == i ? "function" != typeof Blob ? l = z.G("NonBrowser-1", [o]) + z("1skY::，请设置{1}", 0, R + "." + r + '="' + n + '"') : (_ && (e = new Blob([e], {
              type: t
            })), e instanceof Blob || (l = 1), t = e.type || t) : l = z.G("NotSupport-1", [o]), h.CLog(z("Wv7l::结束录音 编码花{1}ms 音频时长{2}ms 文件大小{3}b", 0, Date.now() - u, a, f) + " " + o + "," + t), l) m(1 != l ? l : z("Vkbd::{1}编码器返回的不是{2}", 0, b.type, i) + ", " + o);else {
              if (b.takeoffEncodeChunk) h.CLog(z("QWnr::启用takeoffEncodeChunk后stop返回的blob长度为0不提供音频数据"), 3);else if (f < Math.max(50, a / 5)) return void m(z("Sz2H::生成的{1}无效", 0, b.type));
              c && c(e, a, t), p();
            }
          };
        if (!h.isMock) {
          var r = 3 == h.state;
          if (!h.state || r) return void m(z("wf9t::未开始录音") + (r ? z("Dl2c::，开始录音前无用户交互导致AudioContext未运行") : ""));
        }
        h._stop(!0);
        var i = h.recSize;
        if (i) {
          if (h[b.type]) {
            if (h.isMock) {
              var o = h.envCheck(h.mockEnvInfo || {
                envName: "mock",
                canProcess: !1
              });
              if (o) return void m(z("AxOH::录音错误：") + o);
            }
            var _ = h.engineCtx;
            if (h[b.type + "_complete"] && _) {
              var l = Math.round(_.pcmSize / b[D] * 1e3);
              return u = Date.now(), void h[b.type + "_complete"](_, function (e, t) {
                s(e, t, l);
              }, m);
            }
            if (u = Date.now(), h.buffers[0]) {
              var f = N.SampleData(h.buffers, h[V], b[D]);
              b[D] = f[D];
              var v = f.data,
                l = Math.round(v.length / b[D] * 1e3);
              h.CLog(z("CxeT::采样:{1} 花:{2}ms", 0, i + "->" + v.length, Date.now() - u)), setTimeout(function () {
                u = Date.now(), h[b.type](v, function (e, t) {
                  s(e, t, l);
                }, function (e) {
                  m(e);
                });
              });
            } else m(z("xkKd::音频buffers被释放"));
          } else m(z("xGuI::未加载{1}编码器，请尝试到{2}的src/engine内找到{1}的编码器并加载", 0, b.type, R));
        } else m(z("Ltz3::未采集到录音"));
      }
    };
    var P = function (e, t) {
        t.pos || (t.pos = [0], t.tracks = {}, t.bytes = []);
        var a = t.tracks,
          n = [t.pos[0]],
          s = function () {
            t.pos[0] = n[0];
          },
          r = t.bytes.length,
          i = new Uint8Array(r + e.length);
        if (i.set(t.bytes), i.set(e, r), t.bytes = i, !t._ht) {
          if (j(i, n), X(i, n), !H(j(i, n), [24, 83, 128, 103])) return;
          for (j(i, n); n[0] < i.length;) {
            var o = j(i, n),
              _ = X(i, n),
              l = [0],
              f = 0;
            if (!_) return;
            if (H(o, [22, 84, 174, 107])) {
              for (; l[0] < _.length;) {
                var c = j(_, l),
                  u = X(_, l),
                  h = [0],
                  b = {
                    channels: 0,
                    sampleRate: 0
                  };
                if (H(c, [174])) for (; h[0] < u.length;) {
                  var p = j(u, h),
                    m = X(u, h),
                    v = [0];
                  if (H(p, [215])) {
                    var d = F(m);
                    b.number = d, a[d] = b;
                  } else if (H(p, [131])) {
                    var d = F(m);
                    1 == d ? b.type = "video" : 2 == d ? (b.type = "audio", f || (t.track0 = b), b.idx = f++) : b.type = "Type-" + d;
                  } else if (H(p, [134])) {
                    for (var g = "", w = 0; w < m.length; w++) g += String.fromCharCode(m[w]);
                    b.codec = g;
                  } else if (H(p, [225])) for (; v[0] < m.length;) {
                    var S = j(m, v),
                      M = X(m, v);
                    if (H(S, [181])) {
                      var d = 0,
                        y = new Uint8Array(M.reverse()).buffer;
                      4 == M.length ? d = new Float32Array(y)[0] : 8 == M.length ? d = new Float64Array(y)[0] : O("WebM Track !Float", 1, M), b[D] = Math.round(d);
                    } else H(S, [98, 100]) ? b.bitDepth = F(M) : H(S, [159]) && (b.channels = F(M));
                  }
                }
              }
              t._ht = 1, O("WebM Tracks", a), s();
              break;
            }
          }
        }
        var A = t.track0;
        if (A) {
          if (16 == A.bitDepth && /FLOAT/i.test(A.codec) && (A.bitDepth = 32, O("WebM 16->32 bit", 3)), A[D] != t[D] || 32 != A.bitDepth || A.channels < 1 || !/(\b|_)PCM\b/i.test(A.codec)) return t.bytes = [], t.bad || O("WebM Track Unexpected", 3, t), -(t.bad = 1);
          for (var k = [], R = 0; n[0] < i.length;) {
            var c = j(i, n),
              u = X(i, n);
            if (!u) break;
            if (H(c, [163])) {
              var x = 15 & u[0],
                b = a[x];
              if (b) {
                if (0 === b.idx) {
                  for (var B = new Uint8Array(u.length - 4), w = 4; w < u.length; w++) B[w - 4] = u[w];
                  k.push(B), R += B.length;
                }
              } else O("WebM !Track" + x, 1, a);
            }
            s();
          }
          if (R) {
            var T = new Uint8Array(i.length - t.pos[0]);
            T.set(i.subarray(t.pos[0])), t.bytes = T, t.pos[0] = 0;
            for (var B = new Uint8Array(R), w = 0, E = 0; w < k.length; w++) B.set(k[w], E), E += k[w].length;
            var y = new Float32Array(B.buffer);
            if (1 < A.channels) {
              for (var C = [], w = 0; w < y.length;) C.push(y[w]), w += A.channels;
              y = new Float32Array(C);
            }
            return y;
          }
        }
      },
      H = function (e, t) {
        if (!e || e.length != t.length) return !1;
        if (1 == e.length) return e[0] == t[0];
        for (var a = 0; a < e.length; a++) if (e[a] != t[a]) return !1;
        return !0;
      },
      F = function (e) {
        for (var t = "", a = 0; a < e.length; a++) {
          var n = e[a];
          t += (n < 16 ? "0" : "") + n.toString(16);
        }
        return parseInt(t, 16) || 0;
      },
      j = function (e, t, a) {
        var n = t[0];
        if (!(n >= e.length)) {
          var s = e[n],
            r = ("0000000" + s.toString(2)).substr(-8),
            i = /^(0*1)(\d*)$/.exec(r);
          if (i) {
            var o = i[1].length,
              _ = [];
            if (!(n + o > e.length)) {
              for (var l = 0; l < o; l++) _[l] = e[n], n++;
              return a && (_[0] = parseInt(i[2] || "0", 2)), t[0] = n, _;
            }
          }
        }
      },
      X = function (e, t) {
        var a = j(e, t, 1);
        if (a) {
          var n = F(a),
            s = t[0],
            r = [];
          if (n < 2147483647) {
            if (s + n > e.length) return;
            for (var i = 0; i < n; i++) r[i] = e[s], s++;
          }
          return t[0] = s, r;
        }
      },
      Y = N.i18n = {
        lang: "zh-CN",
        alias: {
          "zh-CN": "zh",
          "en-US": "en"
        },
        locales: {},
        data: {},
        put: function (e, t) {
          var a = R + ".i18n.put: ",
            n = e.overwrite;
          n = null == n || n;
          var s = e.lang;
          if (!(s = Y.alias[s] || s)) throw new Error(a + "set.lang?");
          var r = Y.locales[s];
          r || (r = {}, Y.locales[s] = r);
          for (var i, o = /^([\w\-]+):/, _ = 0; _ < t.length; _++) {
            var l = t[_];
            if (i = o.exec(l)) {
              var f = i[1],
                l = l.substr(f.length + 1);
              !n && r[f] || (r[f] = l);
            } else O(a + "'key:'? " + l, 3, e);
          }
        },
        get: function () {
          return Y.v_G.apply(null, arguments);
        },
        v_G: function (n, s, e) {
          s = s || [], e = e || Y.lang, e = Y.alias[e] || e;
          var t = Y.locales[e],
            r = t && t[n] || "";
          return r || "zh" == e ? (Y.lastLang = e, "=Empty" == r ? "" : r.replace(/\{(\d+)(\!?)\}/g, function (e, t, a) {
            return e = s[(t = +t || 0) - 1], (t < 1 || t > s.length) && (e = "{?}", O("i18n[" + n + "] no {" + t + "}: " + r, 3)), a ? "" : e;
          })) : "en" == e ? Y.v_G(n, s, "zh") : Y.v_G(n, s, "en");
        },
        $T: function () {
          return Y.v_T.apply(null, arguments);
        },
        v_T: function () {
          for (var e, t = arguments, a = "", n = [], s = 0, r = R + ".i18n.$T:", i = /^([\w\-]*):/, o = 0; o < t.length; o++) {
            var _ = t[o];
            if (0 == o) {
              if (e = i.exec(_), !(a = e && e[1])) throw new Error(r + "0 'key:'?");
              _ = _.substr(a.length + 1);
            }
            if (-1 === s) n.push(_);else {
              if (s) throw new Error(r + " bad args");
              if (0 === _) s = -1;else if (f(_)) {
                if (_ < 1) throw new Error(r + " bad args");
                s = _;
              } else {
                var l = 1 == o ? "en" : o ? "" : "zh";
                if ((e = i.exec(_)) && (l = e[1] || l, _ = _.substr(e[1].length + 1)), !e || !l) throw new Error(r + o + " 'lang:'?");
                Y.put({
                  lang: l,
                  overwrite: !1
                }, [a + ":" + _]);
              }
            }
          }
          return a ? 0 < s ? a : Y.v_G(a, n) : "";
        }
      },
      z = Y.$T;
    z.G = Y.get, z("NonBrowser-1::非浏览器环境，不支持{1}", 1), z("IllegalArgs-1::参数错误：{1}", 1), z("NeedImport-2::调用{1}需要先导入{2}", 2), z("NotSupport-1::不支持：{1}", 1), N.TrafficImgUrl = "//ia.51.la/go1?id=20469973&pvFlag=1";
    var u = N.Traffic = function (e) {
      if (w) {
        e = e ? "/" + R + "/Report/" + e : "";
        var t = N.TrafficImgUrl;
        if (t) {
          var a = N.Traffic,
            n = /^(https?:..[^\/#]*\/?)[^#]*/i.exec(location.href) || [],
            s = n[1] || "http://file/",
            r = (n[0] || s) + e;
          if (0 == t.indexOf("//") && (t = /^https:/i.test(r) ? "https:" + t : "http:" + t), e && (t = t + "&cu=" + encodeURIComponent(s + e)), !a[r]) {
            a[r] = 1;
            var i = new Image();
            i.src = t, O("Traffic Analysis Image: " + (e || R + ".TrafficImgUrl=" + N.TrafficImgUrl));
          }
        }
      }
    };
    t && (O(z("8HO5::覆盖导入{1}", 0, R), 1), t.Destroy());
    e[R] = N;
  }(a, t), module.exports && (module.exports = a.Recorder);
}(), function (e) {
  var t = "object" == typeof window && !!window.document,
    a = (t ? window : Object).Recorder,
    n = a.i18n;
  !function (m, e, u, t) {

    var o = "48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000",
      _ = "8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, 192, 224, 256, 320";
    m.prototype.enc_mp3 = {
      stable: !0,
      takeEC: "full",
      getTestMsg: function () {
        return u("Zm7L::采样率范围：{1}；比特率范围：{2}（不同比特率支持的采样率范围不同，小于32kbps时采样率需小于32000）", 0, o, _);
      }
    };
    var h,
      v = function (e) {
        var t = e.bitRate,
          a = e.sampleRate,
          n = a;
        if (-1 == (" " + _ + ",").indexOf(" " + t + ",") && m.CLog(u("eGB9::{1}不在mp3支持的取值范围：{2}", 0, "bitRate=" + t, _), 3), -1 == (" " + o + ",").indexOf(" " + a + ",")) {
          for (var s = o.split(", "), r = [], i = 0; i < s.length; i++) r.push({
            v: +s[i],
            s: Math.abs(s[i] - a)
          });
          r.sort(function (e, t) {
            return e.s - t.s;
          }), n = r[0].v, e.sampleRate = n, m.CLog(u("zLTa::sampleRate已更新为{1}，因为{2}不在mp3支持的取值范围：{3}", 0, n, a, o), 3);
        }
      },
      n = function () {
        return u.G("NeedImport-2", ["mp3.js", "src/engine/mp3-engine.js"]);
      },
      d = t && "function" == typeof Worker;
    m.prototype.mp3 = function (r, i, o) {
      var e = this,
        _ = e.set,
        l = r.length;
      if (m.lamejs) {
        if (d) {
          var t = e.mp3_start(_);
          if (t) {
            if (t.isW) return e.mp3_encode(t, r), void e.mp3_complete(t, i, o, 1);
            e.mp3_stop(t);
          }
        }
        v(_);
        var f = new m.lamejs.Mp3Encoder(1, _.sampleRate, _.bitRate),
          c = new Int8Array(5e5),
          u = 0,
          h = 0,
          b = 0,
          p = function () {
            try {
              if (h < l) var e = f.encodeBuffer(r.subarray(h, h + 57600));else {
                b = 1;
                var e = f.flush();
              }
            } catch (e) {
              if (console.error(e), !b) try {
                f.flush();
              } catch (e) {
                console.error(e);
              }
              return void o("MP3 Encoder: " + e.message);
            }
            var t = e.length;
            if (0 < t) {
              if (u + t > c.length) {
                var a = new Int8Array(c.length + Math.max(5e5, t));
                a.set(c.subarray(0, u)), c = a;
              }
              c.set(e, u), u += t;
            }
            if (h < l) h += 57600, setTimeout(p);else {
              var n = [c.buffer.slice(0, u)],
                s = g.fn(n, u, l, _.sampleRate);
              w(s, _), i(n[0] || new ArrayBuffer(0), "audio/mp3");
            }
          };
        p();
      } else o(n());
    }, m.BindDestroy("mp3Worker", function () {
      h && (m.CLog("mp3Worker Destroy"), h.terminate(), h = null);
    }), m.prototype.mp3_envCheck = function (e, t) {
      var a = "";
      return t.takeoffEncodeChunk && (p() || (a = u("yhUs::当前浏览器版本太低，无法实时处理"))), a || m.lamejs || (a = n()), a;
    }, m.prototype.mp3_start = function (e) {
      return p(e);
    };
    var b = {
        id: 0
      },
      p = function (t, e) {
        var f,
          a = function (e) {
            var t = e.data,
              a = f.wkScope.wk_ctxs,
              n = f.wkScope.wk_lame,
              s = f.wkScope.wk_mp3TrimFix,
              r = a[t.id];
            if ("init" == t.action) a[t.id] = {
              sampleRate: t.sampleRate,
              bitRate: t.bitRate,
              takeoff: t.takeoff,
              pcmSize: 0,
              memory: new Int8Array(5e5),
              mOffset: 0,
              encObj: new n.Mp3Encoder(1, t.sampleRate, t.bitRate)
            };else if (!r) return;
            var i = function (e) {
              var t = e.length;
              if (r.mOffset + t > r.memory.length) {
                var a = new Int8Array(r.memory.length + Math.max(5e5, t));
                a.set(r.memory.subarray(0, r.mOffset)), r.memory = a;
              }
              r.memory.set(e, r.mOffset), r.mOffset += t;
            };
            switch (t.action) {
              case "stop":
                if (!r.isCp) try {
                  r.encObj.flush();
                } catch (e) {
                  console.error(e);
                }
                r.encObj = null, delete a[t.id];
                break;
              case "encode":
                if (r.isCp) break;
                r.pcmSize += t.pcm.length;
                try {
                  var o = r.encObj.encodeBuffer(t.pcm);
                } catch (e) {
                  r.err = e, console.error(e);
                }
                o && 0 < o.length && (r.takeoff ? c.onmessage({
                  action: "takeoff",
                  id: t.id,
                  chunk: o
                }) : i(o));
                break;
              case "complete":
                r.isCp = 1;
                try {
                  var o = r.encObj.flush();
                } catch (e) {
                  r.err = e, console.error(e);
                }
                if (o && 0 < o.length && (r.takeoff ? c.onmessage({
                  action: "takeoff",
                  id: t.id,
                  chunk: o
                }) : i(o)), r.err) {
                  c.onmessage({
                    action: t.action,
                    id: t.id,
                    err: "MP3 Encoder: " + r.err.message
                  });
                  break;
                }
                var _ = [r.memory.buffer.slice(0, r.mOffset)],
                  l = s.fn(_, r.mOffset, r.pcmSize, r.sampleRate);
                c.onmessage({
                  action: t.action,
                  id: t.id,
                  blob: _[0] || new ArrayBuffer(0),
                  meta: l
                });
            }
          },
          n = function (n) {
            c.onmessage = function (e) {
              var t = e;
              n && (t = e.data);
              var a = b[t.id];
              a && ("takeoff" == t.action ? a.set.takeoffEncodeChunk(new Uint8Array(t.chunk.buffer)) : (a.call && a.call(t), a.call = null));
            };
          },
          s = function () {
            var e = {
              worker: c,
              set: t
            };
            return t ? (e.id = ++b.id, b[e.id] = e, v(t), c.postMessage({
              action: "init",
              id: e.id,
              sampleRate: t.sampleRate,
              bitRate: t.bitRate,
              takeoff: !!t.takeoffEncodeChunk,
              x: new Int16Array(5)
            })) : c.postMessage({
              x: new Int16Array(5)
            }), e;
          },
          c = h;
        if (e || !d) return m.CLog(u("k9PT::当前环境不支持Web Worker，mp3实时编码器运行在主线程中"), 3), c = {
          postMessage: function (e) {
            a({
              data: e
            });
          }
        }, f = {
          wkScope: {
            wk_ctxs: {},
            wk_lame: m.lamejs,
            wk_mp3TrimFix: g
          }
        }, n(), s();
        try {
          if (!c) {
            var r = (a + "").replace(/[\w\$]+\.onmessage/g, "self.postMessage"),
              i = ");wk_lame();self.onmessage=" + (r = r.replace(/[\w\$]+\.wkScope/g, "wkScope"));
            i += ";var wkScope={ wk_ctxs:{},wk_lame:wk_lame", i += ",wk_mp3TrimFix:{rm:" + g.rm + ",fn:" + g.fn + "} }";
            var o = m.lamejs.toString(),
              _ = (window.URL || webkitURL).createObjectURL(new Blob(["var wk_lame=(", o, i], {
                type: "text/javascript"
              }));
            c = new Worker(_), setTimeout(function () {
              (window.URL || webkitURL).revokeObjectURL(_);
            }, 1e4), n(1);
          }
          var l = s();
          return l.isW = 1, h = c, l;
        } catch (e) {
          return c && c.terminate(), console.error(e), p(t, 1);
        }
      };
    m.prototype.mp3_stop = function (e) {
      if (e && e.worker) {
        e.worker.postMessage({
          action: "stop",
          id: e.id
        }), e.worker = null, delete b[e.id];
        var t = -1;
        for (var a in b) t++;
        t && m.CLog(u("fT6M::mp3 worker剩{1}个未stop", 0, t), 3);
      }
    }, m.prototype.mp3_encode = function (e, t) {
      e && e.worker && e.worker.postMessage({
        action: "encode",
        id: e.id,
        pcm: t
      });
    }, m.prototype.mp3_complete = function (t, a, n, s) {
      var r = this;
      t && t.worker ? (t.call = function (e) {
        s && r.mp3_stop(t), e.err ? n(e.err) : (w(e.meta, t.set), a(e.blob, "audio/mp3"));
      }, t.worker.postMessage({
        action: "complete",
        id: t.id
      })) : n(u("mPxH::mp3编码器未start"));
    }, m.mp3ReadMeta = function (e, t) {
      var a = "undefined" != typeof window && window.parseInt || "undefined" != typeof self && self.parseInt || parseInt,
        n = new Uint8Array(e[0] || []);
      if (n.length < 4) return null;
      var s = function (e, t) {
          return ("0000000" + ((t || n)[e] || 0).toString(2)).substr(-8);
        },
        r = s(0) + s(1),
        i = s(2) + s(3);
      if (!/^1{11}/.test(r)) return null;
      var o = {
          "00": 2.5,
          10: 2,
          11: 1
        }[r.substr(11, 2)],
        _ = {
          "01": 3
        }[r.substr(13, 2)],
        l = {
          1: [44100, 48e3, 32e3],
          2: [22050, 24e3, 16e3],
          2.5: [11025, 12e3, 8e3]
        }[o];
      l && (l = l[a(i.substr(4, 2), 2)]);
      var f = [[0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160], [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320]][1 == o ? 1 : 0][a(i.substr(0, 4), 2)];
      if (!(o && _ && f && l)) return null;
      for (var c = Math.round(8 * t / f), u = 1 == _ ? 384 : 2 == _ ? 1152 : 1 == o ? 1152 : 576, h = u / l * 1e3, b = Math.floor(u * f / 8 / l * 1e3), p = 0, m = 0, v = 0; v < e.length; v++) {
        var d = e[v];
        if (m += d.byteLength, b + 3 <= m) {
          var g = new Uint8Array(d),
            w = d.byteLength - (m - (b + 3) + 1),
            S = s(w, g);
          p = "1" == S.charAt(6);
          break;
        }
      }
      return p && b++, {
        version: o,
        layer: _,
        sampleRate: l,
        bitRate: f,
        duration: c,
        size: t,
        hasPadding: p,
        frameSize: b,
        frameDurationFloat: h
      };
    };
    var g = {
        rm: m.mp3ReadMeta,
        fn: function (e, t, a, n) {
          var s = this.rm(e, t);
          if (!s) return {
            err: "mp3 unknown format"
          };
          var r = Math.round(a / n * 1e3),
            i = Math.floor((s.duration - r) / s.frameDurationFloat);
          if (0 < i) {
            var o = i * s.frameSize - (s.hasPadding ? 1 : 0);
            t -= o;
            for (var _ = 0, l = [], f = 0; f < e.length; f++) {
              var c = e[f];
              if (o <= 0) break;
              o >= c.byteLength ? (o -= c.byteLength, l.push(c), e.splice(f, 1), f--) : (e[f] = c.slice(o), _ = c, o = 0);
            }
            var u = this.rm(e, t);
            if (!u) {
              _ && (e[0] = _);
              for (var f = 0; f < l.length; f++) e.splice(f, 0, l[f]);
              s.err = "mp3 fix error: 已还原，错误原因不明";
            }
            var h = s.trimFix = {};
            h.remove = i, h.removeDuration = Math.round(i * s.frameDurationFloat), h.duration = Math.round(8 * t / s.bitRate);
          }
          return s;
        }
      },
      w = function (e, t) {
        var a = "MP3 Info: ";
        (e.sampleRate && e.sampleRate != t.sampleRate || e.bitRate && e.bitRate != t.bitRate) && (m.CLog(a + u("uY9i::和设置的不匹配{1}，已更新成{2}", 0, "set:" + t.bitRate + "kbps " + t.sampleRate + "hz", "set:" + e.bitRate + "kbps " + e.sampleRate + "hz"), 3, t), t.sampleRate = e.sampleRate, t.bitRate = e.bitRate);
        var n = e.trimFix;
        n ? (a += u("iMSm::Fix移除{1}帧", 0, n.remove) + " " + n.removeDuration + "ms -> " + n.duration + "ms", 2 < n.remove && (e.err = (e.err ? e.err + ", " : "") + u("b9zm::移除帧数过多"))) : a += (e.duration || "-") + "ms", e.err ? m.CLog(a, 1, e.err, e) : m.CLog(a, e);
      };
  }(a, 0, n.$T, t);
}(), function (e) {

  function t() {
    var d = function (e) {
        return Math.log(e) / Math.log(10);
      },
      me = function (e) {
        throw new Error("abort(" + e + ")");
      };
    function S(e) {
      return new Int8Array(e);
    }
    function s(e) {
      return new Int16Array(e);
    }
    function ve(e) {
      return new Int32Array(e);
    }
    function de(e) {
      return new Float32Array(e);
    }
    function n(e) {
      return new Float64Array(e);
    }
    function ge(e) {
      if (1 == e.length) return de(e[0]);
      var t = e[0];
      e = e.slice(1);
      for (var a = [], n = 0; n < t; n++) a.push(ge(e));
      return a;
    }
    function w(e) {
      if (1 == e.length) return ve(e[0]);
      var t = e[0];
      e = e.slice(1);
      for (var a = [], n = 0; n < t; n++) a.push(w(e));
      return a;
    }
    function M(e) {
      if (1 == e.length) return s(e[0]);
      var t = e[0];
      e = e.slice(1);
      for (var a = [], n = 0; n < t; n++) a.push(M(e));
      return a;
    }
    function E(e) {
      if (1 == e.length) return new Array(e[0]);
      var t = e[0];
      e = e.slice(1);
      for (var a = [], n = 0; n < t; n++) a.push(E(e));
      return a;
    }
    var we = {
        fill: function (e, t, a, n) {
          if (2 == arguments.length) for (var s = 0; s < e.length; s++) e[s] = t;else for (var s = t; s < a; s++) e[s] = n;
        }
      },
      I = {
        arraycopy: function (e, t, a, n, s) {
          for (var r = t + s; t < r;) a[n++] = e[t++];
        }
      },
      V = {};
    function Se(e) {
      this.ordinal = e;
    }
    V.SQRT2 = 1.4142135623730951, V.FAST_LOG10 = function (e) {
      return d(e);
    }, V.FAST_LOG10_X = function (e, t) {
      return d(e) * t;
    }, Se.short_block_allowed = new Se(0), Se.short_block_coupled = new Se(1), Se.short_block_dispensed = new Se(2), Se.short_block_forced = new Se(3);
    var D = {};
    function Me(e) {
      this.ordinal = e;
    }
    function ye(e) {
      var t = e;
      this.ordinal = function () {
        return t;
      };
    }
    function A() {
      var S = null;
      function v(e) {
        this.bits = 0 | e;
      }
      this.qupvt = null, this.setModules = function (e) {
        this.qupvt = e, S = e;
      };
      var s = [[0, 0], [0, 0], [0, 0], [0, 0], [0, 0], [0, 1], [1, 1], [1, 1], [1, 2], [2, 2], [2, 3], [2, 3], [3, 4], [3, 4], [3, 4], [4, 5], [4, 5], [4, 6], [5, 6], [5, 6], [5, 7], [6, 7], [6, 7]];
      function M(e, t, a, n, s, r) {
        var i = .5946 / t;
        for (e >>= 1; 0 != e--;) s[r++] = i > a[n++] ? 0 : 1, s[r++] = i > a[n++] ? 0 : 1;
      }
      function y(e, t, a, n, s, r) {
        var i = (e >>= 1) % 2;
        for (e >>= 1; 0 != e--;) {
          var o, _, l, f, c, u, h, b;
          o = a[n++] * t, _ = a[n++] * t, c = 0 | o, l = a[n++] * t, u = 0 | _, f = a[n++] * t, h = 0 | l, o += S.adj43[c], b = 0 | f, _ += S.adj43[u], s[r++] = 0 | o, l += S.adj43[h], s[r++] = 0 | _, f += S.adj43[b], s[r++] = 0 | l, s[r++] = 0 | f;
        }
        0 != i && (o = a[n++] * t, _ = a[n++] * t, c = 0 | o, u = 0 | _, o += S.adj43[c], _ += S.adj43[u], s[r++] = 0 | o, s[r++] = 0 | _);
      }
      var o = [1, 2, 5, 7, 7, 10, 10, 13, 13, 13, 13, 13, 13, 13, 13];
      function d(e, t, a, n) {
        var s = function (e, t, a) {
          var n = 0,
            s = 0;
          do {
            var r = e[t++],
              i = e[t++];
            n < r && (n = r), s < i && (s = i);
          } while (t < a);
          return n < s && (n = s), n;
        }(e, t, a);
        switch (s) {
          case 0:
            return s;
          case 1:
            return function (e, t, a, n) {
              var s = 0,
                r = k.ht[1].hlen;
              do {
                var i = 2 * e[t + 0] + e[t + 1];
                t += 2, s += r[i];
              } while (t < a);
              return n.bits += s, 1;
            }(e, t, a, n);
          case 2:
          case 3:
            return function (e, t, a, n, s) {
              var r,
                i,
                o = 0,
                _ = k.ht[n].xlen;
              i = 2 == n ? k.table23 : k.table56;
              do {
                var l = e[t + 0] * _ + e[t + 1];
                t += 2, o += i[l];
              } while (t < a);
              return (r = 65535 & o) < (o >>= 16) && (o = r, n++), s.bits += o, n;
            }(e, t, a, o[s - 1], n);
          case 4:
          case 5:
          case 6:
          case 7:
          case 8:
          case 9:
          case 10:
          case 11:
          case 12:
          case 13:
          case 14:
          case 15:
            return function (e, t, a, n, s) {
              var r = 0,
                i = 0,
                o = 0,
                _ = k.ht[n].xlen,
                l = k.ht[n].hlen,
                f = k.ht[n + 1].hlen,
                c = k.ht[n + 2].hlen;
              do {
                var u = e[t + 0] * _ + e[t + 1];
                t += 2, r += l[u], i += f[u], o += c[u];
              } while (t < a);
              var h = n;
              return i < r && (r = i, h++), o < r && (r = o, h = n + 2), s.bits += r, h;
            }(e, t, a, o[s - 1], n);
          default:
            var r, i;
            for (T.IXMAX_VAL < s && me(), s -= 15, r = 24; r < 32 && !(k.ht[r].linmax >= s); r++);
            for (i = r - 8; i < 24 && !(k.ht[i].linmax >= s); i++);
            return function (e, t, a, n, s, r) {
              var i,
                o = 65536 * k.ht[n].xlen + k.ht[s].xlen,
                _ = 0;
              do {
                var l = e[t++],
                  f = e[t++];
                0 != l && (14 < l && (l = 15, _ += o), l *= 16), 0 != f && (14 < f && (f = 15, _ += o), l += f), _ += k.largetbl[l];
              } while (t < a);
              return (i = 65535 & _) < (_ >>= 16) && (_ = i, n = s), r.bits += _, n;
            }(e, t, a, i, r, n);
        }
      }
      function h(e, t, a, n, s, r, i, o) {
        for (var _ = t.big_values, l = 2; l < Ae.SBMAX_l + 1; l++) {
          var f = e.scalefac_band.l[l];
          if (_ <= f) break;
          var c = s[l - 2] + t.count1bits;
          if (a.part2_3_length <= c) break;
          var u = new v(c),
            h = d(n, f, _, u);
          c = u.bits, a.part2_3_length <= c || (a.assign(t), a.part2_3_length = c, a.region0_count = r[l - 2], a.region1_count = l - 2 - r[l - 2], a.table_select[0] = i[l - 2], a.table_select[1] = o[l - 2], a.table_select[2] = h);
        }
      }
      this.noquant_count_bits = function (e, t, a) {
        var n = t.l3_enc,
          s = Math.min(576, t.max_nonzero_coeff + 2 >> 1 << 1);
        for (null != a && (a.sfb_count1 = 0); 1 < s && 0 == (n[s - 1] | n[s - 2]); s -= 2);
        t.count1 = s;
        for (var r = 0, i = 0; 3 < s; s -= 4) {
          var o;
          if (1 < (2147483647 & (n[s - 1] | n[s - 2] | n[s - 3] | n[s - 4]))) break;
          o = 2 * (2 * (2 * n[s - 4] + n[s - 3]) + n[s - 2]) + n[s - 1], r += k.t32l[o], i += k.t33l[o];
        }
        var _ = r;
        if (t.count1table_select = 0, i < r && (_ = i, t.count1table_select = 1), t.count1bits = _, 0 == (t.big_values = s)) return _;
        if (t.block_type == Ae.SHORT_TYPE) (r = 3 * e.scalefac_band.s[3]) > t.big_values && (r = t.big_values), i = t.big_values;else if (t.block_type == Ae.NORM_TYPE) {
          if (r = t.region0_count = e.bv_scf[s - 2], i = t.region1_count = e.bv_scf[s - 1], i = e.scalefac_band.l[r + i + 2], r = e.scalefac_band.l[r + 1], i < s) {
            var l = new v(_);
            t.table_select[2] = d(n, i, s, l), _ = l.bits;
          }
        } else t.region0_count = 7, t.region1_count = Ae.SBMAX_l - 1 - 7 - 1, r = e.scalefac_band.l[8], (i = s) < r && (r = i);
        if (r = Math.min(r, s), i = Math.min(i, s), 0 < r) {
          var l = new v(_);
          t.table_select[0] = d(n, 0, r, l), _ = l.bits;
        }
        if (r < i) {
          var l = new v(_);
          t.table_select[1] = d(n, r, i, l), _ = l.bits;
        }
        if (2 == e.use_best_huffman && me(), null != a && t.block_type == Ae.NORM_TYPE) {
          for (var f = 0; e.scalefac_band.l[f] < t.big_values;) f++;
          a.sfb_count1 = f;
        }
        return _;
      }, this.count_bits = function (e, t, a, n) {
        var s = a.l3_enc,
          r = T.IXMAX_VAL / S.IPOW20(a.global_gain);
        return a.xrpow_max > r ? T.LARGE_BITS : (function (e, t, a, n, s) {
          var r,
            i,
            o,
            _ = 0,
            l = 0,
            f = 0,
            c = 0,
            u = t,
            h = 0,
            b = u,
            p = 0,
            m = e,
            v = 0;
          for (o = null != s && n.global_gain == s.global_gain, i = n.block_type == Ae.SHORT_TYPE ? 38 : 21, r = 0; r <= i; r++) {
            var d = -1;
            if ((o || n.block_type == Ae.NORM_TYPE) && (d = n.global_gain - (n.scalefac[r] + (0 != n.preflag ? S.pretab[r] : 0) << n.scalefac_scale + 1) - 8 * n.subblock_gain[n.window[r]]), o && s.step[r] == d) 0 != l && (y(l, a, m, v, b, p), l = 0), 0 != f && me();else {
              var g,
                w = n.width[r];
              if (_ + n.width[r] > n.max_nonzero_coeff && (g = n.max_nonzero_coeff - _ + 1, we.fill(t, n.max_nonzero_coeff, 576, 0), (w = g) < 0 && (w = 0), r = i + 1), 0 == l && 0 == f && (b = u, p = h, m = e, v = c), null != s && 0 < s.sfb_count1 && r >= s.sfb_count1 && 0 < s.step[r] && d >= s.step[r] ? (0 != l && (y(l, a, m, v, b, p), l = 0, b = u, p = h, m = e, v = c), f += w) : (0 != f && (M(f, a, m, v, b, p), f = 0, b = u, p = h, m = e, v = c), l += w), w <= 0) {
                0 != f && me(), 0 != l && me();
                break;
              }
            }
            r <= i && (h += n.width[r], c += n.width[r], _ += n.width[r]);
          }
          0 != l && (y(l, a, m, v, b, p), l = 0), 0 != f && me();
        }(t, s, S.IPOW20(a.global_gain), a, n), 0 != (2 & e.substep_shaping) && me(), this.noquant_count_bits(e, a, n));
      }, this.best_huffman_divide = function (e, t) {
        var a = new B(),
          n = t.l3_enc,
          s = ve(23),
          r = ve(23),
          i = ve(23),
          o = ve(23);
        if (t.block_type != Ae.SHORT_TYPE || 1 != e.mode_gr) {
          a.assign(t), t.block_type == Ae.NORM_TYPE && (function (e, t, a, n, s, r, i) {
            for (var o = t.big_values, _ = 0; _ <= 22; _++) n[_] = T.LARGE_BITS;
            for (var _ = 0; _ < 16; _++) {
              var l = e.scalefac_band.l[_ + 1];
              if (o <= l) break;
              var f = 0,
                c = new v(f),
                u = d(a, 0, l, c);
              f = c.bits;
              for (var h = 0; h < 8; h++) {
                var b = e.scalefac_band.l[_ + h + 2];
                if (o <= b) break;
                var p = f;
                c = new v(p);
                var m = d(a, l, b, c);
                p = c.bits, n[_ + h] > p && (n[_ + h] = p, s[_ + h] = _, r[_ + h] = u, i[_ + h] = m);
              }
            }
          }(e, t, n, s, r, i, o), h(e, a, t, n, s, r, i, o));
          var _ = a.big_values;
          if (!(0 == _ || 1 < (n[_ - 2] | n[_ - 1]) || 576 < (_ = t.count1 + 2))) {
            a.assign(t), a.count1 = _;
            for (var l = 0, f = 0; _ > a.big_values; _ -= 4) {
              var c = 2 * (2 * (2 * n[_ - 4] + n[_ - 3]) + n[_ - 2]) + n[_ - 1];
              l += k.t32l[c], f += k.t33l[c];
            }
            if (a.big_values = _, a.count1table_select = 0, f < l && (l = f, a.count1table_select = 1), a.count1bits = l, a.block_type == Ae.NORM_TYPE) h(e, a, t, n, s, r, i, o);else {
              if (a.part2_3_length = l, l = e.scalefac_band.l[8], _ < l && (l = _), 0 < l) {
                var u = new v(a.part2_3_length);
                a.table_select[0] = d(n, 0, l, u), a.part2_3_length = u.bits;
              }
              if (l < _) {
                var u = new v(a.part2_3_length);
                a.table_select[1] = d(n, l, _, u), a.part2_3_length = u.bits;
              }
              t.part2_3_length > a.part2_3_length && t.assign(a);
            }
          }
        }
      };
      var u = [1, 1, 1, 1, 8, 2, 2, 2, 4, 4, 4, 8, 8, 8, 16, 16],
        b = [1, 2, 4, 8, 1, 2, 4, 8, 2, 4, 8, 2, 4, 8, 4, 8],
        p = [0, 0, 0, 0, 3, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4],
        m = [0, 1, 2, 3, 0, 1, 2, 3, 1, 2, 3, 1, 2, 3, 2, 3];
      A.slen1_tab = p, A.slen2_tab = m, this.best_scalefac_store = function (e, t, a, n) {
        var s,
          r,
          i,
          o,
          _ = n.tt[t][a],
          l = 0;
        for (s = i = 0; s < _.sfbmax; s++) {
          var f = _.width[s];
          for (i += f, o = -f; o < 0 && 0 == _.l3_enc[o + i]; o++);
          0 == o && (_.scalefac[s] = l = -2);
        }
        if (0 == _.scalefac_scale && 0 == _.preflag) {
          var c = 0;
          for (s = 0; s < _.sfbmax; s++) 0 < _.scalefac[s] && (c |= _.scalefac[s]);
          if (0 == (1 & c) && 0 != c) {
            for (s = 0; s < _.sfbmax; s++) 0 < _.scalefac[s] && (_.scalefac[s] >>= 1);
            _.scalefac_scale = l = 1;
          }
        }
        if (0 == _.preflag && _.block_type != Ae.SHORT_TYPE && 2 == e.mode_gr) {
          for (s = 11; s < Ae.SBPSY_l && !(_.scalefac[s] < S.pretab[s] && -2 != _.scalefac[s]); s++);
          if (s == Ae.SBPSY_l) {
            for (s = 11; s < Ae.SBPSY_l; s++) 0 < _.scalefac[s] && (_.scalefac[s] -= S.pretab[s]);
            _.preflag = l = 1;
          }
        }
        for (r = 0; r < 4; r++) n.scfsi[a][r] = 0;
        for (2 == e.mode_gr && 1 == t && n.tt[0][a].block_type != Ae.SHORT_TYPE && n.tt[1][a].block_type != Ae.SHORT_TYPE && (function (e, t) {
          for (var a, n = t.tt[1][e], s = t.tt[0][e], r = 0; r < k.scfsi_band.length - 1; r++) {
            for (a = k.scfsi_band[r]; a < k.scfsi_band[r + 1] && !(s.scalefac[a] != n.scalefac[a] && 0 <= n.scalefac[a]); a++);
            if (a == k.scfsi_band[r + 1]) {
              for (a = k.scfsi_band[r]; a < k.scfsi_band[r + 1]; a++) n.scalefac[a] = -1;
              t.scfsi[e][r] = 1;
            }
          }
          var i = 0,
            o = 0;
          for (a = 0; a < 11; a++) -1 != n.scalefac[a] && (o++, i < n.scalefac[a] && (i = n.scalefac[a]));
          for (var _ = 0, l = 0; a < Ae.SBPSY_l; a++) -1 != n.scalefac[a] && (l++, _ < n.scalefac[a] && (_ = n.scalefac[a]));
          for (var r = 0; r < 16; r++) if (i < u[r] && _ < b[r]) {
            var f = p[r] * o + m[r] * l;
            n.part2_length > f && (n.part2_length = f, n.scalefac_compress = r);
          }
        }(a, n), l = 0), s = 0; s < _.sfbmax; s++) -2 == _.scalefac[s] && (_.scalefac[s] = 0);
        0 != l && (2 == e.mode_gr ? this.scale_bitcount(_) : this.scale_bitcount_lsf(e, _));
      };
      var _ = [0, 18, 36, 54, 54, 36, 54, 72, 54, 72, 90, 72, 90, 108, 108, 126],
        l = [0, 18, 36, 54, 51, 35, 53, 71, 52, 70, 88, 69, 87, 105, 104, 122],
        f = [0, 10, 20, 30, 33, 21, 31, 41, 32, 42, 52, 43, 53, 63, 64, 74];
      this.scale_bitcount = function (e) {
        var t,
          a,
          n,
          s = 0,
          r = 0,
          i = e.scalefac;
        if (e.block_type == Ae.SHORT_TYPE) n = _, 0 != e.mixed_block_flag && (n = l);else if (n = f, 0 == e.preflag) {
          for (a = 11; a < Ae.SBPSY_l && !(i[a] < S.pretab[a]); a++);
          if (a == Ae.SBPSY_l) for (e.preflag = 1, a = 11; a < Ae.SBPSY_l; a++) i[a] -= S.pretab[a];
        }
        for (a = 0; a < e.sfbdivide; a++) s < i[a] && (s = i[a]);
        for (; a < e.sfbmax; a++) r < i[a] && (r = i[a]);
        for (e.part2_length = T.LARGE_BITS, t = 0; t < 16; t++) s < u[t] && r < b[t] && e.part2_length > n[t] && (e.part2_length = n[t], e.scalefac_compress = t);
        return e.part2_length == T.LARGE_BITS;
      };
      var g = [[15, 15, 7, 7], [15, 15, 7, 0], [7, 3, 0, 0], [15, 31, 31, 0], [7, 7, 7, 0], [3, 3, 0, 0]];
      this.scale_bitcount_lsf = function (e, t) {
        var a,
          n,
          s,
          r,
          i,
          o,
          _,
          l,
          f = ve(4),
          c = t.scalefac;
        for (a = 0 != t.preflag ? 2 : 0, _ = 0; _ < 4; _++) f[_] = 0;
        if (t.block_type == Ae.SHORT_TYPE) {
          n = 1;
          var u = S.nr_of_sfb_block[a][n];
          for (s = l = 0; s < 4; s++) for (r = u[s] / 3, _ = 0; _ < r; _++, l++) for (i = 0; i < 3; i++) c[3 * l + i] > f[s] && (f[s] = c[3 * l + i]);
        } else {
          n = 0;
          var u = S.nr_of_sfb_block[a][n];
          for (s = l = 0; s < 4; s++) for (r = u[s], _ = 0; _ < r; _++, l++) c[l] > f[s] && (f[s] = c[l]);
        }
        for (o = !1, s = 0; s < 4; s++) f[s] > g[a][s] && (o = !0);
        if (!o) {
          var h, b, p, m;
          for (t.sfb_partition_table = S.nr_of_sfb_block[a][n], s = 0; s < 4; s++) t.slen[s] = w[f[s]];
          switch (h = t.slen[0], b = t.slen[1], p = t.slen[2], m = t.slen[3], a) {
            case 0:
              t.scalefac_compress = (5 * h + b << 4) + (p << 2) + m;
              break;
            case 1:
              t.scalefac_compress = 400 + (5 * h + b << 2) + p;
              break;
            case 2:
              t.scalefac_compress = 500 + 3 * h + b;
          }
        }
        if (!o) for (t.part2_length = 0, s = 0; s < 4; s++) t.part2_length += t.slen[s] * t.sfb_partition_table[s];
        return o;
      };
      var w = [0, 1, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4];
      this.huffman_init = function (e) {
        for (var t = 2; t <= 576; t += 2) {
          for (var a, n = 0; e.scalefac_band.l[++n] < t;);
          for (a = s[n][0]; e.scalefac_band.l[a + 1] > t;) a--;
          for (a < 0 && (a = s[n][0]), e.bv_scf[t - 2] = a, a = s[n][1]; e.scalefac_band.l[a + e.bv_scf[t - 2] + 2] > t;) a--;
          a < 0 && (a = s[n][1]), e.bv_scf[t - 1] = a;
        }
      };
    }
    function O() {}
    function y() {
      this.setModules = function (e, t, a) {};
      var o = [0, 49345, 49537, 320, 49921, 960, 640, 49729, 50689, 1728, 1920, 51009, 1280, 50625, 50305, 1088, 52225, 3264, 3456, 52545, 3840, 53185, 52865, 3648, 2560, 51905, 52097, 2880, 51457, 2496, 2176, 51265, 55297, 6336, 6528, 55617, 6912, 56257, 55937, 6720, 7680, 57025, 57217, 8e3, 56577, 7616, 7296, 56385, 5120, 54465, 54657, 5440, 55041, 6080, 5760, 54849, 53761, 4800, 4992, 54081, 4352, 53697, 53377, 4160, 61441, 12480, 12672, 61761, 13056, 62401, 62081, 12864, 13824, 63169, 63361, 14144, 62721, 13760, 13440, 62529, 15360, 64705, 64897, 15680, 65281, 16320, 16e3, 65089, 64001, 15040, 15232, 64321, 14592, 63937, 63617, 14400, 10240, 59585, 59777, 10560, 60161, 11200, 10880, 59969, 60929, 11968, 12160, 61249, 11520, 60865, 60545, 11328, 58369, 9408, 9600, 58689, 9984, 59329, 59009, 9792, 8704, 58049, 58241, 9024, 57601, 8640, 8320, 57409, 40961, 24768, 24960, 41281, 25344, 41921, 41601, 25152, 26112, 42689, 42881, 26432, 42241, 26048, 25728, 42049, 27648, 44225, 44417, 27968, 44801, 28608, 28288, 44609, 43521, 27328, 27520, 43841, 26880, 43457, 43137, 26688, 30720, 47297, 47489, 31040, 47873, 31680, 31360, 47681, 48641, 32448, 32640, 48961, 32e3, 48577, 48257, 31808, 46081, 29888, 30080, 46401, 30464, 47041, 46721, 30272, 29184, 45761, 45953, 29504, 45313, 29120, 28800, 45121, 20480, 37057, 37249, 20800, 37633, 21440, 21120, 37441, 38401, 22208, 22400, 38721, 21760, 38337, 38017, 21568, 39937, 23744, 23936, 40257, 24320, 40897, 40577, 24128, 23040, 39617, 39809, 23360, 39169, 22976, 22656, 38977, 34817, 18624, 18816, 35137, 19200, 35777, 35457, 19008, 19968, 36545, 36737, 20288, 36097, 19904, 19584, 35905, 17408, 33985, 34177, 17728, 34561, 18368, 18048, 34369, 33281, 17088, 17280, 33601, 16640, 33217, 32897, 16448];
      this.updateMusicCRC = function (e, t, a, n) {
        for (var s = 0; s < n; ++s) e[0] = (r = t[a + s], i = (i = e[0]) >> 8 ^ o[255 & (i ^ r)]);
        var r, i;
      };
    }
    function N() {
      var i = this,
        r = null,
        o = null;
      this.setModules = function (e, t, a, n) {
        r = a, o = n;
      };
      var _ = null,
        l = 0,
        f = 0,
        c = 0;
      function v(e, t, a) {
        for (; 0 < a;) {
          var n;
          0 == c && (c = 8, f++, e.header[e.w_ptr].write_timing == l && (s = e, I.arraycopy(s.header[s.w_ptr].buf, 0, _, f, s.sideinfo_len), f += s.sideinfo_len, l += 8 * s.sideinfo_len, s.w_ptr = s.w_ptr + 1 & j.MAX_HEADER_BUF - 1), _[f] = 0), n = Math.min(a, c), a -= n, c -= n, _[f] |= t >> a << c, l += n;
        }
        var s;
      }
      function u(e, t) {
        var a,
          n = e.internal_flags;
        if (8 <= t && (v(n, 76, 8), t -= 8), 8 <= t && (v(n, 65, 8), t -= 8), 8 <= t && (v(n, 77, 8), t -= 8), 8 <= t && (v(n, 69, 8), t -= 8), 32 <= t) {
          var s = r.getLameShortVersion();
          if (32 <= t) for (a = 0; a < s.length && 8 <= t; ++a) t -= 8, v(n, s.charCodeAt(a), 8);
        }
        for (; 1 <= t; t -= 1) v(n, n.ancillary_flag, 1), n.ancillary_flag ^= e.disable_reservoir ? 0 : 1;
      }
      function h(e, t, a) {
        for (var n = e.header[e.h_ptr].ptr; 0 < a;) {
          var s = Math.min(a, 8 - (7 & n));
          a -= s, e.header[e.h_ptr].buf[n >> 3] |= t >> a << 8 - (7 & n) - s, n += s;
        }
        e.header[e.h_ptr].ptr = n;
      }
      function m(e, t) {
        var a,
          n = k.ht[t.count1table_select + 32],
          s = 0,
          r = t.big_values,
          i = t.big_values;
        for (a = (t.count1 - t.big_values) / 4; 0 < a; --a) {
          var o = 0,
            _ = 0;
          0 != t.l3_enc[r + 0] && (_ += 8, t.xr[i + 0] < 0 && o++), 0 != t.l3_enc[r + 1] && (_ += 4, o *= 2, t.xr[i + 1] < 0 && o++), 0 != t.l3_enc[r + 2] && (_ += 2, o *= 2, t.xr[i + 2] < 0 && o++), 0 != t.l3_enc[r + 3] && (_++, o *= 2, t.xr[i + 3] < 0 && o++), r += 4, i += 4, v(e, o + n.table[_], n.hlen[_]), s += n.hlen[_];
        }
        return s;
      }
      function b(e, t, a, n, s) {
        var r = k.ht[t],
          i = 0;
        if (0 == t) return i;
        for (var o = a; o < n; o += 2) {
          var _ = 0,
            l = 0,
            f = r.xlen,
            c = r.xlen,
            u = 0,
            h = s.l3_enc[o],
            b = s.l3_enc[o + 1];
          if (0 != h && (s.xr[o] < 0 && u++, _--), 15 < t) {
            if (14 < h) {
              var p = h - 15;
              u |= p << 1, l = f, h = 15;
            }
            if (14 < b) {
              var m = b - 15;
              u <<= f, u |= m, l += f, b = 15;
            }
            c = 16;
          }
          0 != b && (u <<= 1, s.xr[o + 1] < 0 && u++, _--), h = h * c + b, l -= _, _ += r.hlen[h], v(e, r.table[h], _), v(e, u, l), i += _ + l;
        }
        return i;
      }
      function d(e, t) {
        var a = 3 * e.scalefac_band.s[3];
        a > t.big_values && (a = t.big_values);
        var n = b(e, t.table_select[0], 0, a, t);
        return n += b(e, t.table_select[1], a, t.big_values, t);
      }
      function g(e, t) {
        var a, n, s, r;
        a = t.big_values;
        var i = t.region0_count + 1;
        return s = e.scalefac_band.l[i], i += t.region1_count + 1, r = e.scalefac_band.l[i], a < s && (s = a), a < r && (r = a), n = b(e, t.table_select[0], 0, s, t), n += b(e, t.table_select[1], s, r, t), n += b(e, t.table_select[2], r, a, t);
      }
      function p() {
        this.total = 0;
      }
      function w(e, t) {
        var a,
          n,
          s,
          r = e.internal_flags;
        return r.w_ptr, -1 == (s = r.h_ptr - 1) && (s = j.MAX_HEADER_BUF - 1), a = r.header[s].write_timing - l, 0 <= (t.total = a) && me(), n = i.getframebits(e), a += n, t.total += n, t.total % 8 != 0 ? t.total = 1 + t.total / 8 : t.total = t.total / 8, t.total += f + 1, a;
      }
      this.getframebits = function (e) {
        var t,
          a = e.internal_flags;
        t = 0 != a.bitrate_index ? k.bitrate_table[e.version][a.bitrate_index] : e.brate;
        var n = 0 | 72e3 * (e.version + 1) * t / e.out_samplerate + a.padding;
        return 8 * n;
      }, this.flush_bitstream = function (e) {
        var t,
          a,
          n = e.internal_flags;
          n.h_ptr - 1;
        t = n.l3_side, (a = w(e, new p())) < 0 || (u(e, a), n.ResvSize = 0, t.main_data_begin = 0, n.findReplayGain && me(), n.findPeakSample && me());
      }, this.format_bitstream = function (e) {
        var t,
          a = e.internal_flags;
        t = a.l3_side;
        var n = this.getframebits(e);
        u(e, t.resvDrain_pre), function (e, t) {
          var a,
            n,
            s,
            r = e.internal_flags;
          if (a = r.l3_side, r.header[r.h_ptr].ptr = 0, we.fill(r.header[r.h_ptr].buf, 0, r.sideinfo_len, 0), e.out_samplerate < 16e3 ? h(r, 4094, 12) : h(r, 4095, 12), h(r, e.version, 1), h(r, 1, 2), h(r, e.error_protection ? 0 : 1, 1), h(r, r.bitrate_index, 4), h(r, r.samplerate_index, 2), h(r, r.padding, 1), h(r, e.extension, 1), h(r, e.mode.ordinal(), 2), h(r, r.mode_ext, 2), h(r, e.copyright, 1), h(r, e.original, 1), h(r, e.emphasis, 2), e.error_protection && h(r, 0, 16), 1 == e.version) {
            for (h(r, a.main_data_begin, 9), 2 == r.channels_out ? h(r, a.private_bits, 3) : h(r, a.private_bits, 5), s = 0; s < r.channels_out; s++) {
              var i;
              for (i = 0; i < 4; i++) h(r, a.scfsi[s][i], 1);
            }
            for (n = 0; n < 2; n++) for (s = 0; s < r.channels_out; s++) {
              var o = a.tt[n][s];
              h(r, o.part2_3_length + o.part2_length, 12), h(r, o.big_values / 2, 9), h(r, o.global_gain, 8), h(r, o.scalefac_compress, 4), o.block_type != Ae.NORM_TYPE ? (h(r, 1, 1), h(r, o.block_type, 2), h(r, o.mixed_block_flag, 1), 14 == o.table_select[0] && (o.table_select[0] = 16), h(r, o.table_select[0], 5), 14 == o.table_select[1] && (o.table_select[1] = 16), h(r, o.table_select[1], 5), h(r, o.subblock_gain[0], 3), h(r, o.subblock_gain[1], 3), h(r, o.subblock_gain[2], 3)) : (h(r, 0, 1), 14 == o.table_select[0] && (o.table_select[0] = 16), h(r, o.table_select[0], 5), 14 == o.table_select[1] && (o.table_select[1] = 16), h(r, o.table_select[1], 5), 14 == o.table_select[2] && (o.table_select[2] = 16), h(r, o.table_select[2], 5), h(r, o.region0_count, 4), h(r, o.region1_count, 3)), h(r, o.preflag, 1), h(r, o.scalefac_scale, 1), h(r, o.count1table_select, 1);
            }
          } else for (h(r, a.main_data_begin, 8), h(r, a.private_bits, r.channels_out), s = n = 0; s < r.channels_out; s++) {
            var o = a.tt[n][s];
            h(r, o.part2_3_length + o.part2_length, 12), h(r, o.big_values / 2, 9), h(r, o.global_gain, 8), h(r, o.scalefac_compress, 9), o.block_type != Ae.NORM_TYPE ? (h(r, 1, 1), h(r, o.block_type, 2), h(r, o.mixed_block_flag, 1), 14 == o.table_select[0] && (o.table_select[0] = 16), h(r, o.table_select[0], 5), 14 == o.table_select[1] && (o.table_select[1] = 16), h(r, o.table_select[1], 5), h(r, o.subblock_gain[0], 3), h(r, o.subblock_gain[1], 3), h(r, o.subblock_gain[2], 3)) : (h(r, 0, 1), 14 == o.table_select[0] && (o.table_select[0] = 16), h(r, o.table_select[0], 5), 14 == o.table_select[1] && (o.table_select[1] = 16), h(r, o.table_select[1], 5), 14 == o.table_select[2] && (o.table_select[2] = 16), h(r, o.table_select[2], 5), h(r, o.region0_count, 4), h(r, o.region1_count, 3)), h(r, o.scalefac_scale, 1), h(r, o.count1table_select, 1);
          }
          e.error_protection && me();
          var _ = r.h_ptr;
          r.h_ptr = _ + 1 & j.MAX_HEADER_BUF - 1, r.header[r.h_ptr].write_timing = r.header[_].write_timing + t, r.h_ptr, r.w_ptr;
        }(e, n);
        var s = 8 * a.sideinfo_len;
        if (s += function (e) {
          var t,
            a,
            n,
            s,
            r = 0,
            i = e.internal_flags,
            o = i.l3_side;
          if (1 == e.version) for (t = 0; t < 2; t++) for (a = 0; a < i.channels_out; a++) {
            var _ = o.tt[t][a],
              l = A.slen1_tab[_.scalefac_compress],
              f = A.slen2_tab[_.scalefac_compress];
            for (n = s = 0; n < _.sfbdivide; n++) -1 != _.scalefac[n] && (v(i, _.scalefac[n], l), s += l);
            for (; n < _.sfbmax; n++) -1 != _.scalefac[n] && (v(i, _.scalefac[n], f), s += f);
            _.block_type == Ae.SHORT_TYPE ? s += d(i, _) : s += g(i, _), s += m(i, _), r += s;
          } else for (a = t = 0; a < i.channels_out; a++) {
            var c,
              u,
              _ = o.tt[t][a],
              h = 0;
            if (u = n = s = 0, _.block_type == Ae.SHORT_TYPE) {
              for (; u < 4; u++) {
                var b = _.sfb_partition_table[u] / 3,
                  p = _.slen[u];
                for (c = 0; c < b; c++, n++) v(i, Math.max(_.scalefac[3 * n + 0], 0), p), v(i, Math.max(_.scalefac[3 * n + 1], 0), p), v(i, Math.max(_.scalefac[3 * n + 2], 0), p), h += 3 * p;
              }
              s += d(i, _);
            } else {
              for (; u < 4; u++) {
                var b = _.sfb_partition_table[u],
                  p = _.slen[u];
                for (c = 0; c < b; c++, n++) v(i, Math.max(_.scalefac[n], 0), p), h += p;
              }
              s += g(i, _);
            }
            s += m(i, _), r += h + s;
          }
          return r;
        }(e), u(e, t.resvDrain_post), s += t.resvDrain_post, t.main_data_begin += (n - s) / 8, w(e, new p()), a.ResvSize, 8 * t.main_data_begin != a.ResvSize && (a.ResvSize = 8 * t.main_data_begin), 1e9 < l) {
          var r;
          for (r = 0; r < j.MAX_HEADER_BUF; ++r) a.header[r].write_timing -= l;
          l = 0;
        }
        return 0;
      }, this.copy_buffer = function (e, t, a, n, s) {
        var r = f + 1;
        if (r <= 0) return 0;
        if (0 != n && n < r) return -1;
        if (I.arraycopy(_, 0, t, a, r), f = -1, (c = 0) != s) {
          var i = ve(1);
          i[0] = e.nMusicCRC, o.updateMusicCRC(i, t, a, r), e.nMusicCRC = i[0], 0 < r && (e.VBR_seek_table.nBytesWritten += r), e.decode_on_the_fly && me();
        }
        return r;
      }, this.init_bit_stream_w = function (e) {
        _ = S(Y.LAME_MAXMP3BUFFER), e.h_ptr = e.w_ptr = 0, e.header[e.h_ptr].write_timing = 0, f = -1, l = c = 0;
      };
    }
    function e(e, t, a, n) {
      this.xlen = e, this.linmax = t, this.table = a, this.hlen = n;
    }
    D.MAX_VALUE = 3.4028235e38, Me.vbr_off = new Me(0), Me.vbr_mt = new Me(1), Me.vbr_rh = new Me(2), Me.vbr_abr = new Me(3), Me.vbr_mtrh = new Me(4), Me.vbr_default = Me.vbr_mtrh, ye.STEREO = new ye(0), ye.JOINT_STEREO = new ye(1), ye.DUAL_CHANNEL = new ye(2), ye.MONO = new ye(3), ye.NOT_SET = new ye(4), O.STEPS_per_dB = 100, O.MAX_dB = 120, O.GAIN_NOT_ENOUGH_SAMPLES = -24601, O.GAIN_ANALYSIS_ERROR = 0, O.GAIN_ANALYSIS_OK = 1, O.INIT_GAIN_ANALYSIS_ERROR = 0, O.INIT_GAIN_ANALYSIS_OK = 1, O.MAX_ORDER = O.YULE_ORDER = 10, O.MAX_SAMPLES_PER_WINDOW = (O.MAX_SAMP_FREQ = 48e3) * (O.RMS_WINDOW_TIME_NUMERATOR = 1) / (O.RMS_WINDOW_TIME_DENOMINATOR = 20) + 1, y.NUMTOCENTRIES = 100, y.MAXFRAMESIZE = 2880, N.EQ = function (e, t) {
      return Math.abs(e) > Math.abs(t) ? Math.abs(e - t) <= 1e-6 * Math.abs(e) : Math.abs(e - t) <= 1e-6 * Math.abs(t);
    }, N.NEQ = function (e, t) {
      return !N.EQ(e, t);
    };
    var k = {};
    function R(e) {
      this.bits = e;
    }
    function x() {
      this.over_noise = 0, this.tot_noise = 0, this.max_noise = 0, this.over_count = 0, this.over_SSD = 0, this.bits = 0;
    }
    function r(e, t, a, n) {
      this.l = ve(1 + Ae.SBMAX_l), this.s = ve(1 + Ae.SBMAX_s), this.psfb21 = ve(1 + Ae.PSFB21), this.psfb12 = ve(1 + Ae.PSFB12);
      var s = this.l,
        r = this.s;
      4 == arguments.length && (this.arrL = e, this.arrS = t, this.arr21 = a, this.arr12 = n, I.arraycopy(this.arrL, 0, s, 0, Math.min(this.arrL.length, this.l.length)), I.arraycopy(this.arrS, 0, r, 0, Math.min(this.arrS.length, this.s.length)), I.arraycopy(this.arr21, 0, this.psfb21, 0, Math.min(this.arr21.length, this.psfb21.length)), I.arraycopy(this.arr12, 0, this.psfb12, 0, Math.min(this.arr12.length, this.psfb12.length)));
    }
    function T() {
      var l = null,
        b = null,
        n = null;
      this.setModules = function (e, t, a) {
        l = e, b = t, n = a;
      }, this.IPOW20 = function (e) {
        return h[e];
      };
      var B = 2.220446049250313e-16,
        e = T.IXMAX_VAL,
        f = e + 2,
        c = T.Q_MAX,
        u = T.Q_MAX2,
        s = 100;
      this.nr_of_sfb_block = [[[6, 5, 5, 5], [9, 9, 9, 9], [6, 9, 9, 9]], [[6, 5, 7, 3], [9, 9, 12, 6], [6, 9, 12, 6]], [[11, 10, 0, 0], [18, 18, 0, 0], [15, 18, 0, 0]], [[7, 7, 7, 0], [12, 12, 12, 0], [6, 15, 12, 0]], [[6, 6, 6, 3], [12, 9, 9, 6], [6, 12, 9, 6]], [[8, 8, 5, 0], [15, 12, 9, 0], [6, 18, 9, 0]]];
      var M = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 3, 3, 3, 2, 0];
      this.pretab = M, this.sfBandIndex = [new r([0, 6, 12, 18, 24, 30, 36, 44, 54, 66, 80, 96, 116, 140, 168, 200, 238, 284, 336, 396, 464, 522, 576], [0, 4, 8, 12, 18, 24, 32, 42, 56, 74, 100, 132, 174, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 6, 12, 18, 24, 30, 36, 44, 54, 66, 80, 96, 114, 136, 162, 194, 232, 278, 332, 394, 464, 540, 576], [0, 4, 8, 12, 18, 26, 36, 48, 62, 80, 104, 136, 180, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 6, 12, 18, 24, 30, 36, 44, 54, 66, 80, 96, 116, 140, 168, 200, 238, 284, 336, 396, 464, 522, 576], [0, 4, 8, 12, 18, 26, 36, 48, 62, 80, 104, 134, 174, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 4, 8, 12, 16, 20, 24, 30, 36, 44, 52, 62, 74, 90, 110, 134, 162, 196, 238, 288, 342, 418, 576], [0, 4, 8, 12, 16, 22, 30, 40, 52, 66, 84, 106, 136, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 4, 8, 12, 16, 20, 24, 30, 36, 42, 50, 60, 72, 88, 106, 128, 156, 190, 230, 276, 330, 384, 576], [0, 4, 8, 12, 16, 22, 28, 38, 50, 64, 80, 100, 126, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 4, 8, 12, 16, 20, 24, 30, 36, 44, 54, 66, 82, 102, 126, 156, 194, 240, 296, 364, 448, 550, 576], [0, 4, 8, 12, 16, 22, 30, 42, 58, 78, 104, 138, 180, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 6, 12, 18, 24, 30, 36, 44, 54, 66, 80, 96, 116, 140, 168, 200, 238, 284, 336, 396, 464, 522, 576], [0, 4, 8, 12, 18, 26, 36, 48, 62, 80, 104, 134, 174, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 6, 12, 18, 24, 30, 36, 44, 54, 66, 80, 96, 116, 140, 168, 200, 238, 284, 336, 396, 464, 522, 576], [0, 4, 8, 12, 18, 26, 36, 48, 62, 80, 104, 134, 174, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]), new r([0, 12, 24, 36, 48, 60, 72, 88, 108, 132, 160, 192, 232, 280, 336, 400, 476, 566, 568, 570, 572, 574, 576], [0, 8, 16, 24, 36, 52, 72, 96, 124, 160, 162, 164, 166, 192], [0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0])];
      var y = de(c + u + 1),
        h = de(c),
        p = de(f),
        m = de(f);
      function v(e, t) {
        var a = n.ATHformula(t, e);
        return a -= s, a = Math.pow(10, a / 10 + e.ATHlower);
      }
      function A(e) {
        this.s = e;
      }
      this.adj43 = m, this.iteration_init = function (e) {
        var t,
          a = e.internal_flags,
          n = a.l3_side;
        if (0 == a.iteration_init_init) {
          for (a.iteration_init_init = 1, n.main_data_begin = 0, function (e) {
            for (var t = e.internal_flags.ATH.l, a = e.internal_flags.ATH.psfb21, n = e.internal_flags.ATH.s, s = e.internal_flags.ATH.psfb12, r = e.internal_flags, i = e.out_samplerate, o = 0; o < Ae.SBMAX_l; o++) {
              var _ = r.scalefac_band.l[o],
                l = r.scalefac_band.l[o + 1];
              t[o] = D.MAX_VALUE;
              for (var f = _; f < l; f++) {
                var c = f * i / 1152,
                  u = v(e, c);
                t[o] = Math.min(t[o], u);
              }
            }
            for (var o = 0; o < Ae.PSFB21; o++) {
              var _ = r.scalefac_band.psfb21[o],
                l = r.scalefac_band.psfb21[o + 1];
              a[o] = D.MAX_VALUE;
              for (var f = _; f < l; f++) {
                var c = f * i / 1152,
                  u = v(e, c);
                a[o] = Math.min(a[o], u);
              }
            }
            for (var o = 0; o < Ae.SBMAX_s; o++) {
              var _ = r.scalefac_band.s[o],
                l = r.scalefac_band.s[o + 1];
              n[o] = D.MAX_VALUE;
              for (var f = _; f < l; f++) {
                var c = f * i / 384,
                  u = v(e, c);
                n[o] = Math.min(n[o], u);
              }
              n[o] *= r.scalefac_band.s[o + 1] - r.scalefac_band.s[o];
            }
            for (var o = 0; o < Ae.PSFB12; o++) {
              var _ = r.scalefac_band.psfb12[o],
                l = r.scalefac_band.psfb12[o + 1];
              s[o] = D.MAX_VALUE;
              for (var f = _; f < l; f++) {
                var c = f * i / 384,
                  u = v(e, c);
                s[o] = Math.min(s[o], u);
              }
              s[o] *= r.scalefac_band.s[13] - r.scalefac_band.s[12];
            }
            e.noATH && me(), r.ATH.floor = 10 * d(v(e, -1));
          }(e), p[0] = 0, t = 1; t < f; t++) p[t] = Math.pow(t, 4 / 3);
          for (t = 0; t < f - 1; t++) m[t] = t + 1 - Math.pow(.5 * (p[t] + p[t + 1]), .75);
          for (m[t] = .5, t = 0; t < c; t++) h[t] = Math.pow(2, -.1875 * (t - 210));
          for (t = 0; t <= c + u; t++) y[t] = Math.pow(2, .25 * (t - 210 - u));
          var s, r, i, o;
          for (l.huffman_init(a), 32 <= (t = e.exp_nspsytune >> 2 & 63) && (t -= 64), s = Math.pow(10, t / 4 / 10), 32 <= (t = e.exp_nspsytune >> 8 & 63) && (t -= 64), r = Math.pow(10, t / 4 / 10), 32 <= (t = e.exp_nspsytune >> 14 & 63) && (t -= 64), i = Math.pow(10, t / 4 / 10), 32 <= (t = e.exp_nspsytune >> 20 & 63) && (t -= 64), o = i * Math.pow(10, t / 4 / 10), t = 0; t < Ae.SBMAX_l; t++) _ = t <= 6 ? s : t <= 13 ? r : t <= 20 ? i : o, a.nsPsy.longfact[t] = _;
          for (t = 0; t < Ae.SBMAX_s; t++) {
            var _;
            _ = t <= 5 ? s : t <= 10 ? r : t <= 11 ? i : o, a.nsPsy.shortfact[t] = _;
          }
        }
      }, this.on_pe = function (e, t, a, n, s, r) {
        var i,
          o,
          _ = e.internal_flags,
          l = 0,
          f = ve(2),
          c = new R(l),
          u = b.ResvMaxBits(e, n, c, r),
          h = (l = c.bits) + u;
        for (j.MAX_BITS_PER_GRANULE < h && (h = j.MAX_BITS_PER_GRANULE), o = i = 0; o < _.channels_out; ++o) a[o] = Math.min(j.MAX_BITS_PER_CHANNEL, l / _.channels_out), f[o] = 0 | a[o] * t[s][o] / 700 - a[o], f[o] > 3 * n / 4 && (f[o] = 3 * n / 4), f[o] < 0 && (f[o] = 0), f[o] + a[o] > j.MAX_BITS_PER_CHANNEL && (f[o] = Math.max(0, j.MAX_BITS_PER_CHANNEL - a[o])), i += f[o];
        if (u < i) for (o = 0; o < _.channels_out; ++o) f[o] = u * f[o] / i;
        for (o = 0; o < _.channels_out; ++o) a[o] += f[o], u -= f[o];
        for (o = i = 0; o < _.channels_out; ++o) i += a[o];
        return j.MAX_BITS_PER_GRANULE < i && me(), h;
      }, this.athAdjust = function (e, t, a) {
        var n = 90.30873362,
          s = V.FAST_LOG10_X(t, 10),
          r = e * e,
          i = 0;
        return s -= a, 1e-20 < r && (i = 1 + V.FAST_LOG10_X(r, 10 / n)), i < 0 && (i = 0), s *= i, s += a + n - 94.82444863, Math.pow(10, .1 * s);
      }, this.calc_xmin = function (e, t, a, n) {
        var s,
          r = 0,
          i = e.internal_flags,
          o = 0,
          _ = 0,
          l = i.ATH,
          f = a.xr,
          c = e.VBR == Me.vbr_mtrh ? 1 : 0,
          u = i.masking_lower;
        for (e.VBR != Me.vbr_mtrh && e.VBR != Me.vbr_mt || (u = 1), s = 0; s < a.psy_lmax; s++) {
          for (w = e.VBR == Me.vbr_rh || e.VBR == Me.vbr_mtrh ? athAdjust(l.adjust, l.l[s], l.floor) : l.adjust * l.l[s], v = a.width[s], S = w / v, M = B, A = v >> 1, y = 0; k = f[o] * f[o], y += k, M += k < S ? k : S, R = f[++o] * f[o], y += R, M += R < S ? R : S, o++, 0 < --A;);
          if (w < y && _++, s == Ae.SBPSY_l && me(), 0 != c && (w = M), !e.ATHonly) {
            var h = t.en.l[s];
            0 < h && (x = y * t.thm.l[s] * u / h, 0 != c && (x *= i.nsPsy.longfact[s]), w < x && (w = x));
          }
          n[r++] = 0 != c ? w : w * i.nsPsy.longfact[s];
        }
        var b = 575;
        if (a.block_type != Ae.SHORT_TYPE) for (var p = 576; 0 != p-- && N.EQ(f[p], 0);) b = p;
        a.max_nonzero_coeff = b;
        for (var m = a.sfb_smin; s < a.psymax; m++, s += 3) {
          var v, d, g;
          for (g = e.VBR == Me.vbr_rh || e.VBR == Me.vbr_mtrh ? athAdjust(l.adjust, l.s[m], l.floor) : l.adjust * l.s[m], v = a.width[s], d = 0; d < 3; d++) {
            var w,
              S,
              M,
              y = 0,
              A = v >> 1;
            S = g / v, M = B;
            do {
              var k, R;
              k = f[o] * f[o], y += k, M += k < S ? k : S, R = f[++o] * f[o], y += R, M += R < S ? R : S, o++;
            } while (0 < --A);
            if (g < y && _++, m == Ae.SBPSY_s && me(), w = 0 != c ? M : g, !e.ATHonly && !e.ATHshort) {
              var x,
                h = t.en.s[m][d];
              0 < h && (x = y * t.thm.s[m][d] * u / h, 0 != c && (x *= i.nsPsy.shortfact[m]), w < x && (w = x));
            }
            n[r++] = 0 != c ? w : w * i.nsPsy.shortfact[m];
          }
          e.useTemporal && (n[r - 3] > n[r - 3 + 1] && (n[r - 3 + 1] += (n[r - 3] - n[r - 3 + 1]) * i.decay), n[r - 3 + 1] > n[r - 3 + 2] && (n[r - 3 + 2] += (n[r - 3 + 1] - n[r - 3 + 2]) * i.decay));
        }
        return _;
      }, this.calc_noise_core = function (e, t, a, n) {
        var s = 0,
          r = t.s,
          i = e.l3_enc;
        if (r > e.count1) for (; 0 != a--;) _ = e.xr[r], r++, s += _ * _, _ = e.xr[r], r++, s += _ * _;else if (r > e.big_values) {
          var o = de(2);
          for (o[0] = 0, o[1] = n; 0 != a--;) _ = Math.abs(e.xr[r]) - o[i[r]], r++, s += _ * _, _ = Math.abs(e.xr[r]) - o[i[r]], r++, s += _ * _;
        } else for (; 0 != a--;) {
          var _;
          _ = Math.abs(e.xr[r]) - p[i[r]] * n, r++, s += _ * _, _ = Math.abs(e.xr[r]) - p[i[r]] * n, r++, s += _ * _;
        }
        return t.s = r, s;
      }, this.calc_noise = function (e, t, a, n, s) {
        var r,
          i,
          o = 0,
          _ = 0,
          l = 0,
          f = 0,
          c = 0,
          u = -20,
          h = 0,
          b = e.scalefac,
          p = 0;
        for (n.over_SSD = 0, r = 0; r < e.psymax; r++) {
          var m,
            v = e.global_gain - (b[p++] + (0 != e.preflag ? M[r] : 0) << e.scalefac_scale + 1) - 8 * e.subblock_gain[e.window[r]],
            d = 0;
          if (null != s && s.step[r] == v) d = s.noise[r], h += e.width[r], a[o++] = d / t[_++], d = s.noise_log[r];else {
            var g,
              w = y[v + T.Q_MAX2];
            i = e.width[r] >> 1, h + e.width[r] > e.max_nonzero_coeff && (g = e.max_nonzero_coeff - h + 1, i = 0 < g ? g >> 1 : 0);
            var S = new A(h);
            d = this.calc_noise_core(e, S, i, w), h = S.s, null != s && (s.step[r] = v, s.noise[r] = d), d = a[o++] = d / t[_++], d = V.FAST_LOG10(Math.max(d, 1e-20)), null != s && (s.noise_log[r] = d);
          }
          null != s && (s.global_gain = e.global_gain), c += d, 0 < d && (m = Math.max(0 | 10 * d + .5, 1), n.over_SSD += m * m, l++, f += d), u = Math.max(u, d);
        }
        return n.over_count = l, n.tot_noise = c, n.over_noise = f, n.max_noise = u, l;
      };
    }
    function B() {
      this.xr = de(576), this.l3_enc = ve(576), this.scalefac = ve(F.SFBMAX), this.xrpow_max = 0, this.part2_3_length = 0, this.big_values = 0, this.count1 = 0, this.global_gain = 0, this.scalefac_compress = 0, this.block_type = 0, this.mixed_block_flag = 0, this.table_select = ve(3), this.subblock_gain = ve(4), this.region0_count = 0, this.region1_count = 0, this.preflag = 0, this.scalefac_scale = 0, this.count1table_select = 0, this.part2_length = 0, this.sfb_lmax = 0, this.sfb_smin = 0, this.psy_lmax = 0, this.sfbmax = 0, this.psymax = 0, this.sfbdivide = 0, this.width = ve(F.SFBMAX), this.window = ve(F.SFBMAX), this.count1bits = 0, this.sfb_partition_table = null, this.slen = ve(4), this.max_nonzero_coeff = 0;
      var a = this;
      function n(e) {
        return new Int32Array(e);
      }
      this.assign = function (e) {
        var t;
        a.xr = (t = e.xr, new Float32Array(t)), a.l3_enc = n(e.l3_enc), a.scalefac = n(e.scalefac), a.xrpow_max = e.xrpow_max, a.part2_3_length = e.part2_3_length, a.big_values = e.big_values, a.count1 = e.count1, a.global_gain = e.global_gain, a.scalefac_compress = e.scalefac_compress, a.block_type = e.block_type, a.mixed_block_flag = e.mixed_block_flag, a.table_select = n(e.table_select), a.subblock_gain = n(e.subblock_gain), a.region0_count = e.region0_count, a.region1_count = e.region1_count, a.preflag = e.preflag, a.scalefac_scale = e.scalefac_scale, a.count1table_select = e.count1table_select, a.part2_length = e.part2_length, a.sfb_lmax = e.sfb_lmax, a.sfb_smin = e.sfb_smin, a.psy_lmax = e.psy_lmax, a.sfbmax = e.sfbmax, a.psymax = e.psymax, a.sfbdivide = e.sfbdivide, a.width = n(e.width), a.window = n(e.window), a.count1bits = e.count1bits, a.sfb_partition_table = e.sfb_partition_table.slice(0), a.slen = n(e.slen), a.max_nonzero_coeff = e.max_nonzero_coeff;
      };
    }
    k.t1HB = [1, 1, 1, 0], k.t2HB = [1, 2, 1, 3, 1, 1, 3, 2, 0], k.t3HB = [3, 2, 1, 1, 1, 1, 3, 2, 0], k.t5HB = [1, 2, 6, 5, 3, 1, 4, 4, 7, 5, 7, 1, 6, 1, 1, 0], k.t6HB = [7, 3, 5, 1, 6, 2, 3, 2, 5, 4, 4, 1, 3, 3, 2, 0], k.t7HB = [1, 2, 10, 19, 16, 10, 3, 3, 7, 10, 5, 3, 11, 4, 13, 17, 8, 4, 12, 11, 18, 15, 11, 2, 7, 6, 9, 14, 3, 1, 6, 4, 5, 3, 2, 0], k.t8HB = [3, 4, 6, 18, 12, 5, 5, 1, 2, 16, 9, 3, 7, 3, 5, 14, 7, 3, 19, 17, 15, 13, 10, 4, 13, 5, 8, 11, 5, 1, 12, 4, 4, 1, 1, 0], k.t9HB = [7, 5, 9, 14, 15, 7, 6, 4, 5, 5, 6, 7, 7, 6, 8, 8, 8, 5, 15, 6, 9, 10, 5, 1, 11, 7, 9, 6, 4, 1, 14, 4, 6, 2, 6, 0], k.t10HB = [1, 2, 10, 23, 35, 30, 12, 17, 3, 3, 8, 12, 18, 21, 12, 7, 11, 9, 15, 21, 32, 40, 19, 6, 14, 13, 22, 34, 46, 23, 18, 7, 20, 19, 33, 47, 27, 22, 9, 3, 31, 22, 41, 26, 21, 20, 5, 3, 14, 13, 10, 11, 16, 6, 5, 1, 9, 8, 7, 8, 4, 4, 2, 0], k.t11HB = [3, 4, 10, 24, 34, 33, 21, 15, 5, 3, 4, 10, 32, 17, 11, 10, 11, 7, 13, 18, 30, 31, 20, 5, 25, 11, 19, 59, 27, 18, 12, 5, 35, 33, 31, 58, 30, 16, 7, 5, 28, 26, 32, 19, 17, 15, 8, 14, 14, 12, 9, 13, 14, 9, 4, 1, 11, 4, 6, 6, 6, 3, 2, 0], k.t12HB = [9, 6, 16, 33, 41, 39, 38, 26, 7, 5, 6, 9, 23, 16, 26, 11, 17, 7, 11, 14, 21, 30, 10, 7, 17, 10, 15, 12, 18, 28, 14, 5, 32, 13, 22, 19, 18, 16, 9, 5, 40, 17, 31, 29, 17, 13, 4, 2, 27, 12, 11, 15, 10, 7, 4, 1, 27, 12, 8, 12, 6, 3, 1, 0], k.t13HB = [1, 5, 14, 21, 34, 51, 46, 71, 42, 52, 68, 52, 67, 44, 43, 19, 3, 4, 12, 19, 31, 26, 44, 33, 31, 24, 32, 24, 31, 35, 22, 14, 15, 13, 23, 36, 59, 49, 77, 65, 29, 40, 30, 40, 27, 33, 42, 16, 22, 20, 37, 61, 56, 79, 73, 64, 43, 76, 56, 37, 26, 31, 25, 14, 35, 16, 60, 57, 97, 75, 114, 91, 54, 73, 55, 41, 48, 53, 23, 24, 58, 27, 50, 96, 76, 70, 93, 84, 77, 58, 79, 29, 74, 49, 41, 17, 47, 45, 78, 74, 115, 94, 90, 79, 69, 83, 71, 50, 59, 38, 36, 15, 72, 34, 56, 95, 92, 85, 91, 90, 86, 73, 77, 65, 51, 44, 43, 42, 43, 20, 30, 44, 55, 78, 72, 87, 78, 61, 46, 54, 37, 30, 20, 16, 53, 25, 41, 37, 44, 59, 54, 81, 66, 76, 57, 54, 37, 18, 39, 11, 35, 33, 31, 57, 42, 82, 72, 80, 47, 58, 55, 21, 22, 26, 38, 22, 53, 25, 23, 38, 70, 60, 51, 36, 55, 26, 34, 23, 27, 14, 9, 7, 34, 32, 28, 39, 49, 75, 30, 52, 48, 40, 52, 28, 18, 17, 9, 5, 45, 21, 34, 64, 56, 50, 49, 45, 31, 19, 12, 15, 10, 7, 6, 3, 48, 23, 20, 39, 36, 35, 53, 21, 16, 23, 13, 10, 6, 1, 4, 2, 16, 15, 17, 27, 25, 20, 29, 11, 17, 12, 16, 8, 1, 1, 0, 1], k.t15HB = [7, 12, 18, 53, 47, 76, 124, 108, 89, 123, 108, 119, 107, 81, 122, 63, 13, 5, 16, 27, 46, 36, 61, 51, 42, 70, 52, 83, 65, 41, 59, 36, 19, 17, 15, 24, 41, 34, 59, 48, 40, 64, 50, 78, 62, 80, 56, 33, 29, 28, 25, 43, 39, 63, 55, 93, 76, 59, 93, 72, 54, 75, 50, 29, 52, 22, 42, 40, 67, 57, 95, 79, 72, 57, 89, 69, 49, 66, 46, 27, 77, 37, 35, 66, 58, 52, 91, 74, 62, 48, 79, 63, 90, 62, 40, 38, 125, 32, 60, 56, 50, 92, 78, 65, 55, 87, 71, 51, 73, 51, 70, 30, 109, 53, 49, 94, 88, 75, 66, 122, 91, 73, 56, 42, 64, 44, 21, 25, 90, 43, 41, 77, 73, 63, 56, 92, 77, 66, 47, 67, 48, 53, 36, 20, 71, 34, 67, 60, 58, 49, 88, 76, 67, 106, 71, 54, 38, 39, 23, 15, 109, 53, 51, 47, 90, 82, 58, 57, 48, 72, 57, 41, 23, 27, 62, 9, 86, 42, 40, 37, 70, 64, 52, 43, 70, 55, 42, 25, 29, 18, 11, 11, 118, 68, 30, 55, 50, 46, 74, 65, 49, 39, 24, 16, 22, 13, 14, 7, 91, 44, 39, 38, 34, 63, 52, 45, 31, 52, 28, 19, 14, 8, 9, 3, 123, 60, 58, 53, 47, 43, 32, 22, 37, 24, 17, 12, 15, 10, 2, 1, 71, 37, 34, 30, 28, 20, 17, 26, 21, 16, 10, 6, 8, 6, 2, 0], k.t16HB = [1, 5, 14, 44, 74, 63, 110, 93, 172, 149, 138, 242, 225, 195, 376, 17, 3, 4, 12, 20, 35, 62, 53, 47, 83, 75, 68, 119, 201, 107, 207, 9, 15, 13, 23, 38, 67, 58, 103, 90, 161, 72, 127, 117, 110, 209, 206, 16, 45, 21, 39, 69, 64, 114, 99, 87, 158, 140, 252, 212, 199, 387, 365, 26, 75, 36, 68, 65, 115, 101, 179, 164, 155, 264, 246, 226, 395, 382, 362, 9, 66, 30, 59, 56, 102, 185, 173, 265, 142, 253, 232, 400, 388, 378, 445, 16, 111, 54, 52, 100, 184, 178, 160, 133, 257, 244, 228, 217, 385, 366, 715, 10, 98, 48, 91, 88, 165, 157, 148, 261, 248, 407, 397, 372, 380, 889, 884, 8, 85, 84, 81, 159, 156, 143, 260, 249, 427, 401, 392, 383, 727, 713, 708, 7, 154, 76, 73, 141, 131, 256, 245, 426, 406, 394, 384, 735, 359, 710, 352, 11, 139, 129, 67, 125, 247, 233, 229, 219, 393, 743, 737, 720, 885, 882, 439, 4, 243, 120, 118, 115, 227, 223, 396, 746, 742, 736, 721, 712, 706, 223, 436, 6, 202, 224, 222, 218, 216, 389, 386, 381, 364, 888, 443, 707, 440, 437, 1728, 4, 747, 211, 210, 208, 370, 379, 734, 723, 714, 1735, 883, 877, 876, 3459, 865, 2, 377, 369, 102, 187, 726, 722, 358, 711, 709, 866, 1734, 871, 3458, 870, 434, 0, 12, 10, 7, 11, 10, 17, 11, 9, 13, 12, 10, 7, 5, 3, 1, 3], k.t24HB = [15, 13, 46, 80, 146, 262, 248, 434, 426, 669, 653, 649, 621, 517, 1032, 88, 14, 12, 21, 38, 71, 130, 122, 216, 209, 198, 327, 345, 319, 297, 279, 42, 47, 22, 41, 74, 68, 128, 120, 221, 207, 194, 182, 340, 315, 295, 541, 18, 81, 39, 75, 70, 134, 125, 116, 220, 204, 190, 178, 325, 311, 293, 271, 16, 147, 72, 69, 135, 127, 118, 112, 210, 200, 188, 352, 323, 306, 285, 540, 14, 263, 66, 129, 126, 119, 114, 214, 202, 192, 180, 341, 317, 301, 281, 262, 12, 249, 123, 121, 117, 113, 215, 206, 195, 185, 347, 330, 308, 291, 272, 520, 10, 435, 115, 111, 109, 211, 203, 196, 187, 353, 332, 313, 298, 283, 531, 381, 17, 427, 212, 208, 205, 201, 193, 186, 177, 169, 320, 303, 286, 268, 514, 377, 16, 335, 199, 197, 191, 189, 181, 174, 333, 321, 305, 289, 275, 521, 379, 371, 11, 668, 184, 183, 179, 175, 344, 331, 314, 304, 290, 277, 530, 383, 373, 366, 10, 652, 346, 171, 168, 164, 318, 309, 299, 287, 276, 263, 513, 375, 368, 362, 6, 648, 322, 316, 312, 307, 302, 292, 284, 269, 261, 512, 376, 370, 364, 359, 4, 620, 300, 296, 294, 288, 282, 273, 266, 515, 380, 374, 369, 365, 361, 357, 2, 1033, 280, 278, 274, 267, 264, 259, 382, 378, 372, 367, 363, 360, 358, 356, 0, 43, 20, 19, 17, 15, 13, 11, 9, 7, 6, 4, 7, 5, 3, 1, 3], k.t32HB = [1, 10, 8, 20, 12, 20, 16, 32, 14, 12, 24, 0, 28, 16, 24, 16], k.t33HB = [15, 28, 26, 48, 22, 40, 36, 64, 14, 24, 20, 32, 12, 16, 8, 0], k.t1l = [1, 4, 3, 5], k.t2l = [1, 4, 7, 4, 5, 7, 6, 7, 8], k.t3l = [2, 3, 7, 4, 4, 7, 6, 7, 8], k.t5l = [1, 4, 7, 8, 4, 5, 8, 9, 7, 8, 9, 10, 8, 8, 9, 10], k.t6l = [3, 4, 6, 8, 4, 4, 6, 7, 5, 6, 7, 8, 7, 7, 8, 9], k.t7l = [1, 4, 7, 9, 9, 10, 4, 6, 8, 9, 9, 10, 7, 7, 9, 10, 10, 11, 8, 9, 10, 11, 11, 11, 8, 9, 10, 11, 11, 12, 9, 10, 11, 12, 12, 12], k.t8l = [2, 4, 7, 9, 9, 10, 4, 4, 6, 10, 10, 10, 7, 6, 8, 10, 10, 11, 9, 10, 10, 11, 11, 12, 9, 9, 10, 11, 12, 12, 10, 10, 11, 11, 13, 13], k.t9l = [3, 4, 6, 7, 9, 10, 4, 5, 6, 7, 8, 10, 5, 6, 7, 8, 9, 10, 7, 7, 8, 9, 9, 10, 8, 8, 9, 9, 10, 11, 9, 9, 10, 10, 11, 11], k.t10l = [1, 4, 7, 9, 10, 10, 10, 11, 4, 6, 8, 9, 10, 11, 10, 10, 7, 8, 9, 10, 11, 12, 11, 11, 8, 9, 10, 11, 12, 12, 11, 12, 9, 10, 11, 12, 12, 12, 12, 12, 10, 11, 12, 12, 13, 13, 12, 13, 9, 10, 11, 12, 12, 12, 13, 13, 10, 10, 11, 12, 12, 13, 13, 13], k.t11l = [2, 4, 6, 8, 9, 10, 9, 10, 4, 5, 6, 8, 10, 10, 9, 10, 6, 7, 8, 9, 10, 11, 10, 10, 8, 8, 9, 11, 10, 12, 10, 11, 9, 10, 10, 11, 11, 12, 11, 12, 9, 10, 11, 12, 12, 13, 12, 13, 9, 9, 9, 10, 11, 12, 12, 12, 9, 9, 10, 11, 12, 12, 12, 12], k.t12l = [4, 4, 6, 8, 9, 10, 10, 10, 4, 5, 6, 7, 9, 9, 10, 10, 6, 6, 7, 8, 9, 10, 9, 10, 7, 7, 8, 8, 9, 10, 10, 10, 8, 8, 9, 9, 10, 10, 10, 11, 9, 9, 10, 10, 10, 11, 10, 11, 9, 9, 9, 10, 10, 11, 11, 12, 10, 10, 10, 11, 11, 11, 11, 12], k.t13l = [1, 5, 7, 8, 9, 10, 10, 11, 10, 11, 12, 12, 13, 13, 14, 14, 4, 6, 8, 9, 10, 10, 11, 11, 11, 11, 12, 12, 13, 14, 14, 14, 7, 8, 9, 10, 11, 11, 12, 12, 11, 12, 12, 13, 13, 14, 15, 15, 8, 9, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 15, 15, 9, 9, 11, 11, 12, 12, 13, 13, 12, 13, 13, 14, 14, 15, 15, 16, 10, 10, 11, 12, 12, 12, 13, 13, 13, 13, 14, 13, 15, 15, 16, 16, 10, 11, 12, 12, 13, 13, 13, 13, 13, 14, 14, 14, 15, 15, 16, 16, 11, 11, 12, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 16, 18, 18, 10, 10, 11, 12, 12, 13, 13, 14, 14, 14, 14, 15, 15, 16, 17, 17, 11, 11, 12, 12, 13, 13, 13, 15, 14, 15, 15, 16, 16, 16, 18, 17, 11, 12, 12, 13, 13, 14, 14, 15, 14, 15, 16, 15, 16, 17, 18, 19, 12, 12, 12, 13, 14, 14, 14, 14, 15, 15, 15, 16, 17, 17, 17, 18, 12, 13, 13, 14, 14, 15, 14, 15, 16, 16, 17, 17, 17, 18, 18, 18, 13, 13, 14, 15, 15, 15, 16, 16, 16, 16, 16, 17, 18, 17, 18, 18, 14, 14, 14, 15, 15, 15, 17, 16, 16, 19, 17, 17, 17, 19, 18, 18, 13, 14, 15, 16, 16, 16, 17, 16, 17, 17, 18, 18, 21, 20, 21, 18], k.t15l = [3, 5, 6, 8, 8, 9, 10, 10, 10, 11, 11, 12, 12, 12, 13, 14, 5, 5, 7, 8, 9, 9, 10, 10, 10, 11, 11, 12, 12, 12, 13, 13, 6, 7, 7, 8, 9, 9, 10, 10, 10, 11, 11, 12, 12, 13, 13, 13, 7, 8, 8, 9, 9, 10, 10, 11, 11, 11, 12, 12, 12, 13, 13, 13, 8, 8, 9, 9, 10, 10, 11, 11, 11, 11, 12, 12, 12, 13, 13, 13, 9, 9, 9, 10, 10, 10, 11, 11, 11, 11, 12, 12, 13, 13, 13, 14, 10, 9, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 13, 13, 14, 14, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 12, 13, 13, 13, 14, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 13, 13, 14, 14, 14, 10, 10, 11, 11, 11, 11, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 11, 11, 11, 11, 12, 12, 12, 12, 12, 13, 13, 13, 13, 14, 15, 14, 11, 11, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 15, 12, 12, 11, 12, 12, 12, 13, 13, 13, 13, 13, 13, 14, 14, 15, 15, 12, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 14, 15, 15, 13, 13, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 14, 15, 13, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 14, 15, 15, 15, 15], k.t16_5l = [1, 5, 7, 9, 10, 10, 11, 11, 12, 12, 12, 13, 13, 13, 14, 11, 4, 6, 8, 9, 10, 11, 11, 11, 12, 12, 12, 13, 14, 13, 14, 11, 7, 8, 9, 10, 11, 11, 12, 12, 13, 12, 13, 13, 13, 14, 14, 12, 9, 9, 10, 11, 11, 12, 12, 12, 13, 13, 14, 14, 14, 15, 15, 13, 10, 10, 11, 11, 12, 12, 13, 13, 13, 14, 14, 14, 15, 15, 15, 12, 10, 10, 11, 11, 12, 13, 13, 14, 13, 14, 14, 15, 15, 15, 16, 13, 11, 11, 11, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 16, 13, 11, 11, 12, 12, 13, 13, 13, 14, 14, 15, 15, 15, 15, 17, 17, 13, 11, 12, 12, 13, 13, 13, 14, 14, 15, 15, 15, 15, 16, 16, 16, 13, 12, 12, 12, 13, 13, 14, 14, 15, 15, 15, 15, 16, 15, 16, 15, 14, 12, 13, 12, 13, 14, 14, 14, 14, 15, 16, 16, 16, 17, 17, 16, 13, 13, 13, 13, 13, 14, 14, 15, 16, 16, 16, 16, 16, 16, 15, 16, 14, 13, 14, 14, 14, 14, 15, 15, 15, 15, 17, 16, 16, 16, 16, 18, 14, 15, 14, 14, 14, 15, 15, 16, 16, 16, 18, 17, 17, 17, 19, 17, 14, 14, 15, 13, 14, 16, 16, 15, 16, 16, 17, 18, 17, 19, 17, 16, 14, 11, 11, 11, 12, 12, 13, 13, 13, 14, 14, 14, 14, 14, 14, 14, 12], k.t16l = [1, 5, 7, 9, 10, 10, 11, 11, 12, 12, 12, 13, 13, 13, 14, 10, 4, 6, 8, 9, 10, 11, 11, 11, 12, 12, 12, 13, 14, 13, 14, 10, 7, 8, 9, 10, 11, 11, 12, 12, 13, 12, 13, 13, 13, 14, 14, 11, 9, 9, 10, 11, 11, 12, 12, 12, 13, 13, 14, 14, 14, 15, 15, 12, 10, 10, 11, 11, 12, 12, 13, 13, 13, 14, 14, 14, 15, 15, 15, 11, 10, 10, 11, 11, 12, 13, 13, 14, 13, 14, 14, 15, 15, 15, 16, 12, 11, 11, 11, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 16, 12, 11, 11, 12, 12, 13, 13, 13, 14, 14, 15, 15, 15, 15, 17, 17, 12, 11, 12, 12, 13, 13, 13, 14, 14, 15, 15, 15, 15, 16, 16, 16, 12, 12, 12, 12, 13, 13, 14, 14, 15, 15, 15, 15, 16, 15, 16, 15, 13, 12, 13, 12, 13, 14, 14, 14, 14, 15, 16, 16, 16, 17, 17, 16, 12, 13, 13, 13, 13, 14, 14, 15, 16, 16, 16, 16, 16, 16, 15, 16, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15, 17, 16, 16, 16, 16, 18, 13, 15, 14, 14, 14, 15, 15, 16, 16, 16, 18, 17, 17, 17, 19, 17, 13, 14, 15, 13, 14, 16, 16, 15, 16, 16, 17, 18, 17, 19, 17, 16, 13, 10, 10, 10, 11, 11, 12, 12, 12, 13, 13, 13, 13, 13, 13, 13, 10], k.t24l = [4, 5, 7, 8, 9, 10, 10, 11, 11, 12, 12, 12, 12, 12, 13, 10, 5, 6, 7, 8, 9, 10, 10, 11, 11, 11, 12, 12, 12, 12, 12, 10, 7, 7, 8, 9, 9, 10, 10, 11, 11, 11, 11, 12, 12, 12, 13, 9, 8, 8, 9, 9, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 9, 9, 9, 9, 10, 10, 10, 10, 11, 11, 11, 12, 12, 12, 12, 13, 9, 10, 9, 10, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 12, 9, 10, 10, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 12, 13, 9, 11, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 12, 13, 13, 10, 11, 11, 11, 11, 11, 11, 11, 11, 11, 12, 12, 12, 12, 13, 13, 10, 11, 11, 11, 11, 11, 11, 11, 12, 12, 12, 12, 12, 13, 13, 13, 10, 12, 11, 11, 11, 11, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 10, 12, 12, 11, 11, 11, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 10, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 10, 12, 12, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 13, 13, 10, 13, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 13, 13, 13, 10, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 10, 10, 10, 10, 6], k.t32l = [1, 5, 5, 7, 5, 8, 7, 9, 5, 7, 7, 9, 7, 9, 9, 10], k.t33l = [4, 5, 5, 6, 5, 6, 6, 7, 5, 6, 6, 7, 6, 7, 7, 8], k.ht = [new e(0, 0, null, null), new e(2, 0, k.t1HB, k.t1l), new e(3, 0, k.t2HB, k.t2l), new e(3, 0, k.t3HB, k.t3l), new e(0, 0, null, null), new e(4, 0, k.t5HB, k.t5l), new e(4, 0, k.t6HB, k.t6l), new e(6, 0, k.t7HB, k.t7l), new e(6, 0, k.t8HB, k.t8l), new e(6, 0, k.t9HB, k.t9l), new e(8, 0, k.t10HB, k.t10l), new e(8, 0, k.t11HB, k.t11l), new e(8, 0, k.t12HB, k.t12l), new e(16, 0, k.t13HB, k.t13l), new e(0, 0, null, k.t16_5l), new e(16, 0, k.t15HB, k.t15l), new e(1, 1, k.t16HB, k.t16l), new e(2, 3, k.t16HB, k.t16l), new e(3, 7, k.t16HB, k.t16l), new e(4, 15, k.t16HB, k.t16l), new e(6, 63, k.t16HB, k.t16l), new e(8, 255, k.t16HB, k.t16l), new e(10, 1023, k.t16HB, k.t16l), new e(13, 8191, k.t16HB, k.t16l), new e(4, 15, k.t24HB, k.t24l), new e(5, 31, k.t24HB, k.t24l), new e(6, 63, k.t24HB, k.t24l), new e(7, 127, k.t24HB, k.t24l), new e(8, 255, k.t24HB, k.t24l), new e(9, 511, k.t24HB, k.t24l), new e(11, 2047, k.t24HB, k.t24l), new e(13, 8191, k.t24HB, k.t24l), new e(0, 0, k.t32HB, k.t32l), new e(0, 0, k.t33HB, k.t33l)], k.largetbl = [65540, 327685, 458759, 589832, 655369, 655370, 720906, 720907, 786443, 786444, 786444, 851980, 851980, 851980, 917517, 655370, 262149, 393222, 524295, 589832, 655369, 720906, 720906, 720907, 786443, 786443, 786444, 851980, 917516, 851980, 917516, 655370, 458759, 524295, 589832, 655369, 720905, 720906, 786442, 786443, 851979, 786443, 851979, 851980, 851980, 917516, 917517, 720905, 589832, 589832, 655369, 720905, 720906, 786442, 786442, 786443, 851979, 851979, 917515, 917516, 917516, 983052, 983052, 786441, 655369, 655369, 720905, 720906, 786442, 786442, 851978, 851979, 851979, 917515, 917516, 917516, 983052, 983052, 983053, 720905, 655370, 655369, 720906, 720906, 786442, 851978, 851979, 917515, 851979, 917515, 917516, 983052, 983052, 983052, 1048588, 786441, 720906, 720906, 720906, 786442, 851978, 851979, 851979, 851979, 917515, 917516, 917516, 917516, 983052, 983052, 1048589, 786441, 720907, 720906, 786442, 786442, 851979, 851979, 851979, 917515, 917516, 983052, 983052, 983052, 983052, 1114125, 1114125, 786442, 720907, 786443, 786443, 851979, 851979, 851979, 917515, 917515, 983051, 983052, 983052, 983052, 1048588, 1048589, 1048589, 786442, 786443, 786443, 786443, 851979, 851979, 917515, 917515, 983052, 983052, 983052, 983052, 1048588, 983053, 1048589, 983053, 851978, 786444, 851979, 786443, 851979, 917515, 917516, 917516, 917516, 983052, 1048588, 1048588, 1048589, 1114125, 1114125, 1048589, 786442, 851980, 851980, 851979, 851979, 917515, 917516, 983052, 1048588, 1048588, 1048588, 1048588, 1048589, 1048589, 983053, 1048589, 851978, 851980, 917516, 917516, 917516, 917516, 983052, 983052, 983052, 983052, 1114124, 1048589, 1048589, 1048589, 1048589, 1179661, 851978, 983052, 917516, 917516, 917516, 983052, 983052, 1048588, 1048588, 1048589, 1179661, 1114125, 1114125, 1114125, 1245197, 1114125, 851978, 917517, 983052, 851980, 917516, 1048588, 1048588, 983052, 1048589, 1048589, 1114125, 1179661, 1114125, 1245197, 1114125, 1048589, 851978, 655369, 655369, 655369, 720905, 720905, 786441, 786441, 786441, 851977, 851977, 851977, 851978, 851978, 851978, 851978, 655366], k.table23 = [65538, 262147, 458759, 262148, 327684, 458759, 393222, 458759, 524296], k.table56 = [65539, 262148, 458758, 524296, 262148, 327684, 524294, 589831, 458757, 524294, 589831, 655368, 524295, 524295, 589832, 655369], k.bitrate_table = [[0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, -1], [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, -1], [0, 8, 16, 24, 32, 40, 48, 56, 64, -1, -1, -1, -1, -1, -1, -1]], k.samplerate_table = [[22050, 24e3, 16e3, -1], [44100, 48e3, 32e3, -1], [11025, 12e3, 8e3, -1]], k.scfsi_band = [0, 6, 11, 16, 21], T.Q_MAX = 257, T.Q_MAX2 = 116, T.LARGE_BITS = 1e5, T.IXMAX_VAL = 8206;
    var F = {};
    function C() {
      var r, g;
      this.rv = null, this.qupvt = null;
      var w,
        s = new function () {
          this.setModules = function (e, t) {};
        }();
      function S(e) {
        this.ordinal = e;
      }
      function o(e) {
        for (var t = 0; t < e.sfbmax; t++) if (e.scalefac[t] + e.subblock_gain[e.window[t]] == 0) return !1;
        return !0;
      }
      function M(e, t, a, n, s) {
        var r;
        switch (e) {
          default:
          case 9:
            0 < t.over_count ? (r = a.over_SSD <= t.over_SSD, a.over_SSD == t.over_SSD && (r = a.bits < t.bits)) : r = a.max_noise < 0 && 10 * a.max_noise + a.bits <= 10 * t.max_noise + t.bits;
            break;
          case 0:
            r = a.over_count < t.over_count || a.over_count == t.over_count && a.over_noise < t.over_noise || a.over_count == t.over_count && N.EQ(a.over_noise, t.over_noise) && a.tot_noise < t.tot_noise;
            break;
          case 8:
            me();
          case 1:
            r = a.max_noise < t.max_noise;
            break;
          case 2:
            r = a.tot_noise < t.tot_noise;
            break;
          case 3:
            r = a.tot_noise < t.tot_noise && a.max_noise < t.max_noise;
            break;
          case 4:
            r = a.max_noise <= 0 && .2 < t.max_noise || a.max_noise <= 0 && t.max_noise < 0 && t.max_noise > a.max_noise - .2 && a.tot_noise < t.tot_noise || a.max_noise <= 0 && 0 < t.max_noise && t.max_noise > a.max_noise - .2 && a.tot_noise < t.tot_noise + t.over_noise || 0 < a.max_noise && -.05 < t.max_noise && t.max_noise > a.max_noise - .1 && a.tot_noise + a.over_noise < t.tot_noise + t.over_noise || 0 < a.max_noise && -.1 < t.max_noise && t.max_noise > a.max_noise - .15 && a.tot_noise + a.over_noise + a.over_noise < t.tot_noise + t.over_noise + t.over_noise;
            break;
          case 5:
            r = a.over_noise < t.over_noise || N.EQ(a.over_noise, t.over_noise) && a.tot_noise < t.tot_noise;
            break;
          case 6:
            r = a.over_noise < t.over_noise || N.EQ(a.over_noise, t.over_noise) && (a.max_noise < t.max_noise || N.EQ(a.max_noise, t.max_noise) && a.tot_noise <= t.tot_noise);
            break;
          case 7:
            r = a.over_count < t.over_count || a.over_noise < t.over_noise;
        }
        return 0 == t.over_count && (r = r && a.bits < t.bits), r;
      }
      function y(e, t, a, n, s) {
        var r = e.internal_flags;
        !function (e, t, a, n, s) {
          var r,
            i = e.internal_flags;
          r = 0 == t.scalefac_scale ? 1.2968395546510096 : 1.6817928305074292;
          for (var o = 0, _ = 0; _ < t.sfbmax; _++) o < a[_] && (o = a[_]);
          var l = i.noise_shaping_amp;
          switch (3 == l && me(), l) {
            case 2:
              break;
            case 1:
              1 < o ? o = Math.pow(o, .5) : o *= .95;
              break;
            case 0:
            default:
              1 < o ? o = 1 : o *= .95;
          }
          for (var f = 0, _ = 0; _ < t.sfbmax; _++) {
            var c,
              u = t.width[_];
            if (f += u, !(a[_] < o)) {
              for (0 != (2 & i.substep_shaping) && me(), t.scalefac[_]++, c = -u; c < 0; c++) n[f + c] *= r, n[f + c] > t.xrpow_max && (t.xrpow_max = n[f + c]);
              if (2 == i.noise_shaping_amp) return;
            }
          }
        }(e, t, a, n);
        var i = o(t);
        return !(i || (i = 2 == r.mode_gr ? w.scale_bitcount(t) : w.scale_bitcount_lsf(r, t)) && (1 < r.noise_shaping && (we.fill(r.pseudohalf, 0), 0 == t.scalefac_scale ? (function (e, t) {
          for (var a = 0, n = 0; n < e.sfbmax; n++) {
            var s = e.width[n],
              r = e.scalefac[n];
            if (0 != e.preflag && (r += g.pretab[n]), a += s, 0 != (1 & r)) {
              r++;
              for (var i = -s; i < 0; i++) t[a + i] *= 1.2968395546510096, t[a + i] > e.xrpow_max && (e.xrpow_max = t[a + i]);
            }
            e.scalefac[n] = r >> 1;
          }
          e.preflag = 0, e.scalefac_scale = 1;
        }(t, n), i = !1) : t.block_type == Ae.SHORT_TYPE && 0 < r.subblock_gain && (i = function (e, t, a) {
          var n,
            s = t.scalefac;
          for (n = 0; n < t.sfb_lmax; n++) if (16 <= s[n]) return !0;
          for (var r = 0; r < 3; r++) {
            var i = 0,
              o = 0;
            for (n = t.sfb_lmax + r; n < t.sfbdivide; n += 3) i < s[n] && (i = s[n]);
            for (; n < t.sfbmax; n += 3) o < s[n] && (o = s[n]);
            if (!(i < 16 && o < 8)) {
              if (7 <= t.subblock_gain[r]) return !0;
              t.subblock_gain[r]++;
              var _ = e.scalefac_band.l[t.sfb_lmax];
              for (n = t.sfb_lmax + r; n < t.sfbmax; n += 3) {
                var l = t.width[n],
                  f = s[n];
                if (0 <= (f -= 4 >> t.scalefac_scale)) s[n] = f, _ += 3 * l;else {
                  s[n] = 0;
                  var c = 210 + (f << t.scalefac_scale + 1);
                  h = g.IPOW20(c), _ += l * (r + 1);
                  for (var u = -l; u < 0; u++) a[_ + u] *= h, a[_ + u] > t.xrpow_max && (t.xrpow_max = a[_ + u]);
                  _ += l * (3 - r - 1);
                }
              }
              var h = g.IPOW20(202);
              _ += t.width[n] * (r + 1);
              for (var u = -t.width[n]; u < 0; u++) a[_ + u] *= h, a[_ + u] > t.xrpow_max && (t.xrpow_max = a[_ + u]);
            }
          }
          return !1;
        }(r, t, n) || o(t))), i || (i = 2 == r.mode_gr ? w.scale_bitcount(t) : w.scale_bitcount_lsf(r, t)), i));
      }
      this.setModules = function (e, t, a, n) {
        r = t, this.rv = t, g = a, this.qupvt = a, w = n, s.setModules(g, w);
      }, this.init_xrpow = function (e, t, a) {
        var n = 0,
          s = 0 | t.max_nonzero_coeff;
        if (t.xrpow_max = 0, we.fill(a, s, 576, 0), 1e-20 < (n = function (e, t, a, n) {
          for (var s = n = 0; s <= a; ++s) {
            var r = Math.abs(e.xr[s]);
            n += r, t[s] = Math.sqrt(r * Math.sqrt(r)), t[s] > e.xrpow_max && (e.xrpow_max = t[s]);
          }
          return n;
        }(t, a, s, n))) {
          var r = 0;
          0 != (2 & e.substep_shaping) && (r = 1);
          for (var i = 0; i < t.psymax; i++) e.pseudohalf[i] = r;
          return !0;
        }
        return we.fill(t.l3_enc, 0, 576, 0), !1;
      }, this.init_outer_loop = function (e, t) {
        t.part2_3_length = 0, t.big_values = 0, t.count1 = 0, t.global_gain = 210, t.scalefac_compress = 0, t.table_select[0] = 0, t.table_select[1] = 0, t.table_select[2] = 0, t.subblock_gain[0] = 0, t.subblock_gain[1] = 0, t.subblock_gain[2] = 0, t.subblock_gain[3] = 0, t.region0_count = 0, t.region1_count = 0, t.preflag = 0, t.scalefac_scale = 0, t.count1table_select = 0, t.part2_length = 0, t.sfb_lmax = Ae.SBPSY_l, t.sfb_smin = Ae.SBPSY_s, t.psy_lmax = e.sfb21_extra ? Ae.SBMAX_l : Ae.SBPSY_l, t.psymax = t.psy_lmax, t.sfbmax = t.sfb_lmax, t.sfbdivide = 11;
        for (var a = 0; a < Ae.SBMAX_l; a++) t.width[a] = e.scalefac_band.l[a + 1] - e.scalefac_band.l[a], t.window[a] = 3;
        if (t.block_type == Ae.SHORT_TYPE) {
          var n = de(576);
          t.sfb_smin = 0, (t.sfb_lmax = 0) != t.mixed_block_flag && me(), t.psymax = t.sfb_lmax + 3 * ((e.sfb21_extra ? Ae.SBMAX_s : Ae.SBPSY_s) - t.sfb_smin), t.sfbmax = t.sfb_lmax + 3 * (Ae.SBPSY_s - t.sfb_smin), t.sfbdivide = t.sfbmax - 18, t.psy_lmax = t.sfb_lmax;
          var s = e.scalefac_band.l[t.sfb_lmax];
          I.arraycopy(t.xr, 0, n, 0, 576);
          for (var a = t.sfb_smin; a < Ae.SBMAX_s; a++) for (var r = e.scalefac_band.s[a], i = e.scalefac_band.s[a + 1], o = 0; o < 3; o++) for (var _ = r; _ < i; _++) t.xr[s++] = n[3 * _ + o];
          for (var l = t.sfb_lmax, a = t.sfb_smin; a < Ae.SBMAX_s; a++) t.width[l] = t.width[l + 1] = t.width[l + 2] = e.scalefac_band.s[a + 1] - e.scalefac_band.s[a], t.window[l] = 0, t.window[l + 1] = 1, t.window[l + 2] = 2, l += 3;
        }
        t.count1bits = 0, t.sfb_partition_table = g.nr_of_sfb_block[0][0], t.slen[0] = 0, t.slen[1] = 0, t.slen[2] = 0, t.slen[3] = 0, t.max_nonzero_coeff = 575, we.fill(t.scalefac, 0), function (e, t) {
          var a = e.ATH,
            n = t.xr;
          if (t.block_type != Ae.SHORT_TYPE) for (var s = !1, r = Ae.PSFB21 - 1; 0 <= r && !s; r--) {
            var i = e.scalefac_band.psfb21[r],
              o = e.scalefac_band.psfb21[r + 1],
              _ = g.athAdjust(a.adjust, a.psfb21[r], a.floor);
            1e-12 < e.nsPsy.longfact[21] && (_ *= e.nsPsy.longfact[21]);
            for (var l = o - 1; i <= l; l--) {
              if (!(Math.abs(n[l]) < _)) {
                s = !0;
                break;
              }
              n[l] = 0;
            }
          } else for (var f = 0; f < 3; f++) for (var s = !1, r = Ae.PSFB12 - 1; 0 <= r && !s; r--) {
            var i = 3 * e.scalefac_band.s[12] + (e.scalefac_band.s[13] - e.scalefac_band.s[12]) * f + (e.scalefac_band.psfb12[r] - e.scalefac_band.psfb12[0]),
              o = i + (e.scalefac_band.psfb12[r + 1] - e.scalefac_band.psfb12[r]),
              c = g.athAdjust(a.adjust, a.psfb12[r], a.floor);
            1e-12 < e.nsPsy.shortfact[12] && (c *= e.nsPsy.shortfact[12]);
            for (var l = o - 1; i <= l; l--) {
              if (!(Math.abs(n[l]) < c)) {
                s = !0;
                break;
              }
              n[l] = 0;
            }
          }
        }(e, t);
      }, S.BINSEARCH_NONE = new S(0), S.BINSEARCH_UP = new S(1), S.BINSEARCH_DOWN = new S(2), this.outer_loop = function (e, t, a, n, s, r) {
        var i = e.internal_flags,
          o = new B(),
          _ = de(576),
          l = de(F.SFBMAX),
          f = new x(),
          c = new function () {
            this.global_gain = 0, this.sfb_count1 = 0, this.step = ve(39), this.noise = de(39), this.noise_log = de(39);
          }(),
          u = 9999999,
          h = !1;
        if (function (e, t, a, n, s) {
          var r,
            i = e.CurrentStep[n],
            o = !1,
            _ = e.OldValue[n],
            l = S.BINSEARCH_NONE;
          for (t.global_gain = _, a -= t.part2_length;;) {
            var f;
            if (r = w.count_bits(e, s, t, null), 1 == i || r == a) break;
            a < r ? (l == S.BINSEARCH_DOWN && (o = !0), o && (i /= 2), l = S.BINSEARCH_UP, f = i) : (l == S.BINSEARCH_UP && (o = !0), o && (i /= 2), l = S.BINSEARCH_DOWN, f = -i), t.global_gain += f, t.global_gain < 0 && me(), 255 < t.global_gain && me();
          }
          for (; a < r && t.global_gain < 255;) t.global_gain++, r = w.count_bits(e, s, t, null);
          e.CurrentStep[n] = 4 <= _ - t.global_gain ? 4 : 2, e.OldValue[n] = t.global_gain, t.part2_3_length = r;
        }(i, t, r, s, n), 0 == i.noise_shaping) return 100;
        g.calc_noise(t, a, l, f, c), f.bits = t.part2_3_length, o.assign(t);
        var b = 0;
        for (I.arraycopy(n, 0, _, 0, 576); !h;) {
          do {
            var p,
              m = new x(),
              v = 255;
            if (p = 0 != (2 & i.substep_shaping) ? 20 : 3, i.sfb21_extra && me(), !y(e, o, l, n)) break;
            0 != o.scalefac_scale && (v = 254);
            var d = r - o.part2_length;
            if (d <= 0) break;
            for (; (o.part2_3_length = w.count_bits(i, n, o, c)) > d && o.global_gain <= v;) o.global_gain++;
            if (o.global_gain > v) break;
            if (0 == f.over_count) {
              for (; (o.part2_3_length = w.count_bits(i, n, o, c)) > u && o.global_gain <= v;) o.global_gain++;
              if (o.global_gain > v) break;
            }
            if (g.calc_noise(o, a, l, m, c), m.bits = o.part2_3_length, 0 != (M(t.block_type != Ae.SHORT_TYPE ? e.quant_comp : e.quant_comp_short, f, m) ? 1 : 0)) u = t.part2_3_length, f = m, t.assign(o), b = 0, I.arraycopy(n, 0, _, 0, 576);else if (0 == i.full_outer_loop) {
              if (++b > p && 0 == f.over_count) break;
              i.noise_shaping_amp, i.noise_shaping_amp;
            }
          } while (o.global_gain + o.scalefac_scale < 255);
          3 == i.noise_shaping_amp ? me() : h = !0;
        }
        return e.VBR == Me.vbr_rh || e.VBR == Me.vbr_mtrh ? I.arraycopy(_, 0, n, 0, 576) : 0 != (1 & i.substep_shaping) && me(), f.over_count;
      }, this.iteration_finish_one = function (e, t, a) {
        var n = e.l3_side,
          s = n.tt[t][a];
        w.best_scalefac_store(e, t, a, n), 1 == e.use_best_huffman && w.best_huffman_divide(e, s), r.ResvAdjust(e, s);
      };
    }
    function L() {
      this.thm = new i(), this.en = new i();
    }
    function Ae() {
      var k = Ae.MPG_MD_MS_LR,
        R = null,
        x = this.psy = null,
        B = null;
      this.setModules = function (e, t, a, n) {
        R = e, this.psy = t, x = t, B = n;
      };
      var T = new function () {
        var u = [-.1482523854003001, 32.308141959636465, 296.40344946382766, 883.1344870032432, 11113.947376231741, 1057.2713659324597, 305.7402417275812, 30.825928907280012, 3.8533188138216365, 59.42900443849514, 709.5899960123345, 5281.91112291017, -5829.66483675846, -817.6293103748613, -76.91656988279972, -4.594269939176596, .9063471690191471, .1960342806591213, -.15466694054279598, 34.324387823855965, 301.8067566458425, 817.599602898885, 11573.795901679885, 1181.2520595540152, 321.59731579894424, 31.232021761053772, 3.7107095756221318, 53.650946155329365, 684.167428119626, 5224.56624370173, -6366.391851890084, -908.9766368219582, -89.83068876699639, -5.411397422890401, .8206787908286602, .3901806440322567, -.16070888947830023, 36.147034243915876, 304.11815768187864, 732.7429163887613, 11989.60988270091, 1300.012278487897, 335.28490093152146, 31.48816102859945, 3.373875931311736, 47.232241542899175, 652.7371796173471, 5132.414255594984, -6909.087078780055, -1001.9990371107289, -103.62185754286375, -6.104916304710272, .7416505462720353, .5805693545089249, -.16636367662261495, 37.751650073343995, 303.01103387567713, 627.9747488785183, 12358.763425278165, 1412.2779918482834, 346.7496836825721, 31.598286663170416, 3.1598635433980946, 40.57878626349686, 616.1671130880391, 5007.833007176154, -7454.040671756168, -1095.7960341867115, -118.24411666465777, -6.818469345853504, .6681786379192989, .7653668647301797, -.1716176790982088, 39.11551877123304, 298.3413246578966, 503.5259106886539, 12679.589408408976, 1516.5821921214542, 355.9850766329023, 31.395241710249053, 2.9164211881972335, 33.79716964664243, 574.8943997801362, 4853.234992253242, -7997.57021486075, -1189.7624067269965, -133.6444792601766, -7.7202770609839915, .5993769336819237, .9427934736519954, -.17645823955292173, 40.21879108166477, 289.9982036694474, 359.3226160751053, 12950.259102786438, 1612.1013903507662, 362.85067106591504, 31.045922092242872, 2.822222032597987, 26.988862316190684, 529.8996541764288, 4671.371946949588, -8535.899136645805, -1282.5898586244496, -149.58553632943463, -8.643494270763135, .5345111359507916, 1.111140466039205, -.36174739330527045, 41.04429910497807, 277.5463268268618, 195.6386023135583, 13169.43812144731, 1697.6433561479398, 367.40983966190305, 30.557037410382826, 2.531473372857427, 20.070154905927314, 481.50208566532336, 4464.970341588308, -9065.36882077239, -1373.62841526722, -166.1660487028118, -9.58289321133207, .4729647758913199, 1.268786568327291, -.36970682634889585, 41.393213350082036, 261.2935935556502, 12.935476055240873, 13336.131683328815, 1772.508612059496, 369.76534388639965, 29.751323653701338, 2.4023193045459172, 13.304795348228817, 430.5615775526625, 4237.0568611071185, -9581.931701634761, -1461.6913552409758, -183.12733958476446, -10.718010163869403, .41421356237309503, 1.414213562373095, -.37677560326535325, 41.619486213528496, 241.05423794991074, -187.94665032361226, 13450.063605744153, 1836.153896465782, 369.4908799925761, 29.001847876923147, 2.0714759319987186, 6.779591200894186, 377.7767837205709, 3990.386575512536, -10081.709459700915, -1545.947424837898, -200.3762958015653, -11.864482073055006, .3578057213145241, 1.546020906725474, -.3829366947518991, 41.1516456456653, 216.47684307105183, -406.1569483347166, 13511.136535077321, 1887.8076599260432, 367.3025214564151, 28.136213436723654, 1.913880671464418, .3829366947518991, 323.85365704338597, 3728.1472257487526, -10561.233882199509, -1625.2025997821418, -217.62525175416, -13.015432208941645, .3033466836073424, 1.66293922460509, -.5822628872992417, 40.35639251440489, 188.20071124269245, -640.2706748618148, 13519.21490106562, 1927.6022433578062, 362.8197642637487, 26.968821921868447, 1.7463817695935329, -5.62650678237171, 269.3016715297017, 3453.386536448852, -11016.145278780888, -1698.6569643425091, -234.7658734267683, -14.16351421663124, .2504869601913055, 1.76384252869671, -.5887180101749253, 39.23429103868072, 155.76096234403798, -889.2492977967378, 13475.470561874661, 1955.0535223723712, 356.4450994756727, 25.894952980042156, 1.5695032905781554, -11.181939564328772, 214.80884394039484, 3169.1640829158237, -11443.321309975563, -1765.1588461316153, -251.68908574481912, -15.49755935939164, .198912367379658, 1.847759065022573, -.7912582233652842, 37.39369355329111, 119.699486012458, -1151.0956593239027, 13380.446257078214, 1970.3952110853447, 348.01959814116185, 24.731487364283044, 1.3850130831637748, -16.421408865300393, 161.05030052864092, 2878.3322807850063, -11838.991423510031, -1823.985884688674, -268.2854986386903, -16.81724543849939, .1483359875383474, 1.913880671464418, -.7960642926861912, 35.2322109610459, 80.01928065061526, -1424.0212633405113, 13235.794061869668, 1973.804052543835, 337.9908651258184, 23.289159354463873, 1.3934255946442087, -21.099669467133474, 108.48348407242611, 2583.700758091299, -12199.726194855148, -1874.2780658979746, -284.2467154529415, -18.11369784385905, .09849140335716425, 1.961570560806461, -.998795456205172, 32.56307803611191, 36.958364584370486, -1706.075448829146, 13043.287458812016, 1965.3831106103316, 326.43182772364605, 22.175018750622293, 1.198638339011324, -25.371248002043963, 57.53505923036915, 2288.41886619975, -12522.674544337233, -1914.8400385312243, -299.26241273417224, -19.37805630698734, .04912684976946725, 1.990369453344394, .035780907 * V.SQRT2 * .5 / 2384e-9, .017876148 * V.SQRT2 * .5 / 2384e-9, .003134727 * V.SQRT2 * .5 / 2384e-9, .002457142 * V.SQRT2 * .5 / 2384e-9, 971317e-9 * V.SQRT2 * .5 / 2384e-9, 218868e-9 * V.SQRT2 * .5 / 2384e-9, 101566e-9 * V.SQRT2 * .5 / 2384e-9, 13828e-9 * V.SQRT2 * .5 / 2384e-9, 12804.797818791945, 1945.5515939597317, 313.4244966442953, 49591e-9 / 2384e-9, 1995.1556208053692, 21458e-9 / 2384e-9, -69618e-9 / 2384e-9],
          A = [[2.382191739347913e-13, 6.423305872147834e-13, 9.400849094049688e-13, 1.122435026096556e-12, 1.183840321267481e-12, 1.122435026096556e-12, 9.40084909404969e-13, 6.423305872147839e-13, 2.382191739347918e-13, 5.456116108943412e-12, 4.878985199565852e-12, 4.240448995017367e-12, 3.559909094758252e-12, 2.858043359288075e-12, 2.156177623817898e-12, 1.475637723558783e-12, 8.371015190102974e-13, 2.599706096327376e-13, -5.456116108943412e-12, -4.878985199565852e-12, -4.240448995017367e-12, -3.559909094758252e-12, -2.858043359288076e-12, -2.156177623817898e-12, -1.475637723558783e-12, -8.371015190102975e-13, -2.599706096327376e-13, -2.382191739347923e-13, -6.423305872147843e-13, -9.400849094049696e-13, -1.122435026096556e-12, -1.183840321267481e-12, -1.122435026096556e-12, -9.400849094049694e-13, -6.42330587214784e-13, -2.382191739347918e-13], [2.382191739347913e-13, 6.423305872147834e-13, 9.400849094049688e-13, 1.122435026096556e-12, 1.183840321267481e-12, 1.122435026096556e-12, 9.400849094049688e-13, 6.423305872147841e-13, 2.382191739347918e-13, 5.456116108943413e-12, 4.878985199565852e-12, 4.240448995017367e-12, 3.559909094758253e-12, 2.858043359288075e-12, 2.156177623817898e-12, 1.475637723558782e-12, 8.371015190102975e-13, 2.599706096327376e-13, -5.461314069809755e-12, -4.921085770524055e-12, -4.343405037091838e-12, -3.732668368707687e-12, -3.093523840190885e-12, -2.430835727329465e-12, -1.734679010007751e-12, -9.74825365660928e-13, -2.797435120168326e-13, 0, 0, 0, 0, 0, 0, -2.283748241799531e-13, -4.037858874020686e-13, -2.146547464825323e-13], [.1316524975873958, .414213562373095, .7673269879789602, 1.091308501069271, 1.303225372841206, 1.56968557711749, 1.920982126971166, 2.414213562373094, 3.171594802363212, 4.510708503662055, 7.595754112725146, 22.90376554843115, .984807753012208, .6427876096865394, .3420201433256688, .9396926207859084, -.1736481776669303, -.7660444431189779, .8660254037844387, .5, -.5144957554275265, -.4717319685649723, -.3133774542039019, -.1819131996109812, -.09457419252642064, -.04096558288530405, -.01419856857247115, -.003699974673760037, .8574929257125442, .8817419973177052, .9496286491027329, .9833145924917901, .9955178160675857, .9991605581781475, .999899195244447, .9999931550702802], [0, 0, 0, 0, 0, 0, 2.283748241799531e-13, 4.037858874020686e-13, 2.146547464825323e-13, 5.461314069809755e-12, 4.921085770524055e-12, 4.343405037091838e-12, 3.732668368707687e-12, 3.093523840190885e-12, 2.430835727329466e-12, 1.734679010007751e-12, 9.74825365660928e-13, 2.797435120168326e-13, -5.456116108943413e-12, -4.878985199565852e-12, -4.240448995017367e-12, -3.559909094758253e-12, -2.858043359288075e-12, -2.156177623817898e-12, -1.475637723558782e-12, -8.371015190102975e-13, -2.599706096327376e-13, -2.382191739347913e-13, -6.423305872147834e-13, -9.400849094049688e-13, -1.122435026096556e-12, -1.183840321267481e-12, -1.122435026096556e-12, -9.400849094049688e-13, -6.423305872147841e-13, -2.382191739347918e-13]],
          k = A[Ae.SHORT_TYPE],
          S = A[Ae.SHORT_TYPE],
          R = A[Ae.SHORT_TYPE],
          x = A[Ae.SHORT_TYPE],
          B = [0, 1, 16, 17, 8, 9, 24, 25, 4, 5, 20, 21, 12, 13, 28, 29, 2, 3, 18, 19, 10, 11, 26, 27, 6, 7, 22, 23, 14, 15, 30, 31];
        function T(e, t, a) {
          for (var n, s, r, i = 10, o = t + 238 - 14 - 286, _ = -15; _ < 0; _++) {
            var l, f, c;
            l = u[i + -10], f = e[o + -224] * l, c = e[t + 224] * l, l = u[i + -9], f += e[o + -160] * l, c += e[t + 160] * l, l = u[i + -8], f += e[o + -96] * l, c += e[t + 96] * l, l = u[i + -7], f += e[o + -32] * l, c += e[t + 32] * l, l = u[i + -6], f += e[o + 32] * l, c += e[t + -32] * l, l = u[i + -5], f += e[o + 96] * l, c += e[t + -96] * l, l = u[i + -4], f += e[o + 160] * l, c += e[t + -160] * l, l = u[i + -3], f += e[o + 224] * l, c += e[t + -224] * l, l = u[i + -2], f += e[t + -256] * l, c -= e[o + 256] * l, l = u[i + -1], f += e[t + -192] * l, c -= e[o + 192] * l, l = u[i + 0], f += e[t + -128] * l, c -= e[o + 128] * l, l = u[i + 1], f += e[t + -64] * l, c -= e[o + 64] * l, l = u[i + 2], f += e[t + 0] * l, c -= e[o + 0] * l, l = u[i + 3], f += e[t + 64] * l, c -= e[o + -64] * l, l = u[i + 4], f += e[t + 128] * l, c -= e[o + -128] * l, l = u[i + 5], f += e[t + 192] * l, c -= e[o + -192] * l, f *= u[i + 6], l = c - f, a[30 + 2 * _] = c + f, a[31 + 2 * _] = u[i + 7] * l, i += 18, t--, o++;
          }
          c = e[t + -16] * u[i + -10], f = e[t + -32] * u[i + -2], c += (e[t + -48] - e[t + 16]) * u[i + -9], f += e[t + -96] * u[i + -1], c += (e[t + -80] + e[t + 48]) * u[i + -8], f += e[t + -160] * u[i + 0], c += (e[t + -112] - e[t + 80]) * u[i + -7], f += e[t + -224] * u[i + 1], c += (e[t + -144] + e[t + 112]) * u[i + -6], f -= e[t + 32] * u[i + 2], c += (e[t + -176] - e[t + 144]) * u[i + -5], f -= e[t + 96] * u[i + 3], c += (e[t + -208] + e[t + 176]) * u[i + -4], f -= e[t + 160] * u[i + 4], c += (e[t + -240] - e[t + 208]) * u[i + -3], f -= e[t + 224], n = f - c, s = f + c, c = a[14], f = a[15] - c, a[31] = s + c, a[30] = n + f, a[15] = n - f, a[14] = s - c, r = a[28] - a[0], a[0] += a[28], a[28] = r * u[i + -36 + 7], r = a[29] - a[1], a[1] += a[29], a[29] = r * u[i + -36 + 7], r = a[26] - a[2], a[2] += a[26], a[26] = r * u[i + -72 + 7], r = a[27] - a[3], a[3] += a[27], a[27] = r * u[i + -72 + 7], r = a[24] - a[4], a[4] += a[24], a[24] = r * u[i + -108 + 7], r = a[25] - a[5], a[5] += a[25], a[25] = r * u[i + -108 + 7], r = a[22] - a[6], a[6] += a[22], a[22] = r * V.SQRT2, r = a[23] - a[7], a[7] += a[23], a[23] = r * V.SQRT2 - a[7], a[7] -= a[6], a[22] -= a[7], a[23] -= a[22], r = a[6], a[6] = a[31] - r, a[31] = a[31] + r, r = a[7], a[7] = a[30] - r, a[30] = a[30] + r, r = a[22], a[22] = a[15] - r, a[15] = a[15] + r, r = a[23], a[23] = a[14] - r, a[14] = a[14] + r, r = a[20] - a[8], a[8] += a[20], a[20] = r * u[i + -180 + 7], r = a[21] - a[9], a[9] += a[21], a[21] = r * u[i + -180 + 7], r = a[18] - a[10], a[10] += a[18], a[18] = r * u[i + -216 + 7], r = a[19] - a[11], a[11] += a[19], a[19] = r * u[i + -216 + 7], r = a[16] - a[12], a[12] += a[16], a[16] = r * u[i + -252 + 7], r = a[17] - a[13], a[13] += a[17], a[17] = r * u[i + -252 + 7], r = -a[20] + a[24], a[20] += a[24], a[24] = r * u[i + -216 + 7], r = -a[21] + a[25], a[21] += a[25], a[25] = r * u[i + -216 + 7], r = a[4] - a[8], a[4] += a[8], a[8] = r * u[i + -216 + 7], r = a[5] - a[9], a[5] += a[9], a[9] = r * u[i + -216 + 7], r = a[0] - a[12], a[0] += a[12], a[12] = r * u[i + -72 + 7], r = a[1] - a[13], a[1] += a[13], a[13] = r * u[i + -72 + 7], r = a[16] - a[28], a[16] += a[28], a[28] = r * u[i + -72 + 7], r = -a[17] + a[29], a[17] += a[29], a[29] = r * u[i + -72 + 7], r = V.SQRT2 * (a[2] - a[10]), a[2] += a[10], a[10] = r, r = V.SQRT2 * (a[3] - a[11]), a[3] += a[11], a[11] = r, r = V.SQRT2 * (-a[18] + a[26]), a[18] += a[26], a[26] = r - a[18], r = V.SQRT2 * (-a[19] + a[27]), a[19] += a[27], a[27] = r - a[19], r = a[2], a[19] -= a[3], a[3] -= r, a[2] = a[31] - r, a[31] += r, r = a[3], a[11] -= a[19], a[18] -= r, a[3] = a[30] - r, a[30] += r, r = a[18], a[27] -= a[11], a[19] -= r, a[18] = a[15] - r, a[15] += r, r = a[19], a[10] -= r, a[19] = a[14] - r, a[14] += r, r = a[10], a[11] -= r, a[10] = a[23] - r, a[23] += r, r = a[11], a[26] -= r, a[11] = a[22] - r, a[22] += r, r = a[26], a[27] -= r, a[26] = a[7] - r, a[7] += r, r = a[27], a[27] = a[6] - r, a[6] += r, r = V.SQRT2 * (a[0] - a[4]), a[0] += a[4], a[4] = r, r = V.SQRT2 * (a[1] - a[5]), a[1] += a[5], a[5] = r, r = V.SQRT2 * (a[16] - a[20]), a[16] += a[20], a[20] = r, r = V.SQRT2 * (a[17] - a[21]), a[17] += a[21], a[21] = r, r = -V.SQRT2 * (a[8] - a[12]), a[8] += a[12], a[12] = r - a[8], r = -V.SQRT2 * (a[9] - a[13]), a[9] += a[13], a[13] = r - a[9], r = -V.SQRT2 * (a[25] - a[29]), a[25] += a[29], a[29] = r - a[25], r = -V.SQRT2 * (a[24] + a[28]), a[24] -= a[28], a[28] = r - a[24], r = a[24] - a[16], a[24] = r, r = a[20] - r, a[20] = r, r = a[28] - r, a[28] = r, r = a[25] - a[17], a[25] = r, r = a[21] - r, a[21] = r, r = a[29] - r, a[29] = r, r = a[17] - a[1], a[17] = r, r = a[9] - r, a[9] = r, r = a[25] - r, a[25] = r, r = a[5] - r, a[5] = r, r = a[21] - r, a[21] = r, r = a[13] - r, a[13] = r, r = a[29] - r, a[29] = r, r = a[1] - a[0], a[1] = r, r = a[16] - r, a[16] = r, r = a[17] - r, a[17] = r, r = a[8] - r, a[8] = r, r = a[9] - r, a[9] = r, r = a[24] - r, a[24] = r, r = a[25] - r, a[25] = r, r = a[4] - r, a[4] = r, r = a[5] - r, a[5] = r, r = a[20] - r, a[20] = r, r = a[21] - r, a[21] = r, r = a[12] - r, a[12] = r, r = a[13] - r, a[13] = r, r = a[28] - r, a[28] = r, r = a[29] - r, a[29] = r, r = a[0], a[0] += a[31], a[31] -= r, r = a[1], a[1] += a[30], a[30] -= r, r = a[16], a[16] += a[15], a[15] -= r, r = a[17], a[17] += a[14], a[14] -= r, r = a[8], a[8] += a[23], a[23] -= r, r = a[9], a[9] += a[22], a[22] -= r, r = a[24], a[24] += a[7], a[7] -= r, r = a[25], a[25] += a[6], a[6] -= r, r = a[4], a[4] += a[27], a[27] -= r, r = a[5], a[5] += a[26], a[26] -= r, r = a[20], a[20] += a[11], a[11] -= r, r = a[21], a[21] += a[10], a[10] -= r, r = a[12], a[12] += a[19], a[19] -= r, r = a[13], a[13] += a[18], a[18] -= r, r = a[28], a[28] += a[3], a[3] -= r, r = a[29], a[29] += a[2], a[2] -= r;
        }
        function E(e, t) {
          for (var a = 0; a < 3; a++) {
            var n, s, r, i, o, _;
            i = e[t + 6] * A[Ae.SHORT_TYPE][0] - e[t + 15], n = e[t + 0] * A[Ae.SHORT_TYPE][2] - e[t + 9], s = i + n, r = i - n, i = e[t + 15] * A[Ae.SHORT_TYPE][0] + e[t + 6], n = e[t + 9] * A[Ae.SHORT_TYPE][2] + e[t + 0], o = i + n, _ = -i + n, n = 2.069978111953089e-11 * (e[t + 3] * A[Ae.SHORT_TYPE][1] - e[t + 12]), i = 2.069978111953089e-11 * (e[t + 12] * A[Ae.SHORT_TYPE][1] + e[t + 3]), e[t + 0] = 1.90752519173728e-11 * s + n, e[t + 15] = 1.90752519173728e-11 * -o + i, r = .8660254037844387 * r * 1.907525191737281e-11, o = .5 * o * 1.907525191737281e-11 + i, e[t + 3] = r - o, e[t + 6] = r + o, s = .5 * s * 1.907525191737281e-11 - n, _ = .8660254037844387 * _ * 1.907525191737281e-11, e[t + 9] = s + _, e[t + 12] = s - _, t++;
          }
        }
        function C(e, t, a) {
          var n, s, r, i, o, _, l, f, c, u, h, b, p, m, v, d, g, w;
          r = a[17] - a[9], o = a[15] - a[11], _ = a[14] - a[12], l = a[0] + a[8], f = a[1] + a[7], c = a[2] + a[6], u = a[3] + a[5], e[t + 17] = l + c - u - (f - a[4]), s = (l + c - u) * S[19] + (f - a[4]), n = (r - o - _) * S[18], e[t + 5] = n + s, e[t + 6] = n - s, i = (a[16] - a[10]) * S[18], f = f * S[19] + a[4], n = r * S[12] + i + o * S[13] + _ * S[14], s = -l * S[16] + f - c * S[17] + u * S[15], e[t + 1] = n + s, e[t + 2] = n - s, n = r * S[13] - i - o * S[14] + _ * S[12], s = -l * S[17] + f - c * S[15] + u * S[16], e[t + 9] = n + s, e[t + 10] = n - s, n = r * S[14] - i + o * S[12] - _ * S[13], s = l * S[15] - f + c * S[16] - u * S[17], e[t + 13] = n + s, e[t + 14] = n - s, h = a[8] - a[0], p = a[6] - a[2], m = a[5] - a[3], v = a[17] + a[9], d = a[16] + a[10], g = a[15] + a[11], w = a[14] + a[12], e[t + 0] = v + g + w + (d + a[13]), n = (v + g + w) * S[19] - (d + a[13]), s = (h - p + m) * S[18], e[t + 11] = n + s, e[t + 12] = n - s, b = (a[7] - a[1]) * S[18], d = a[13] - d * S[19], n = v * S[15] - d + g * S[16] + w * S[17], s = h * S[14] + b + p * S[12] + m * S[13], e[t + 3] = n + s, e[t + 4] = n - s, n = -v * S[17] + d - g * S[15] - w * S[16], s = h * S[13] + b - p * S[14] - m * S[12], e[t + 7] = n + s, e[t + 8] = n - s, n = -v * S[16] + d - g * S[17] - w * S[15], s = h * S[12] - b + p * S[13] - m * S[14], e[t + 15] = n + s, e[t + 16] = n - s;
        }
        this.mdct_sub48 = function (e, t, a) {
          for (var n = t, s = 286, r = 0; r < e.channels_out; r++) {
            for (var i = 0; i < e.mode_gr; i++) {
              for (var o, _ = e.l3_side.tt[i][r], l = _.xr, f = 0, c = e.sb_sample[r][1 - i], u = 0, h = 0; h < 9; h++) for (T(n, s, c[u]), T(n, s + 32, c[u + 1]), u += 2, s += 64, o = 1; o < 32; o += 2) c[u - 1][o] *= -1;
              for (o = 0; o < 32; o++, f += 18) {
                var b = _.block_type,
                  p = e.sb_sample[r][i],
                  m = e.sb_sample[r][1 - i];
                if (0 != _.mixed_block_flag && o < 2 && (b = 0), e.amp_filter[o] < 1e-12) we.fill(l, f + 0, f + 18, 0);else if (e.amp_filter[o] < 1 && me(), b == Ae.SHORT_TYPE) {
                  for (var h = -3; h < 0; h++) {
                    var v = A[Ae.SHORT_TYPE][h + 3];
                    l[f + 3 * h + 9] = p[9 + h][B[o]] * v - p[8 - h][B[o]], l[f + 3 * h + 18] = p[14 - h][B[o]] * v + p[15 + h][B[o]], l[f + 3 * h + 10] = p[15 + h][B[o]] * v - p[14 - h][B[o]], l[f + 3 * h + 19] = m[2 - h][B[o]] * v + m[3 + h][B[o]], l[f + 3 * h + 11] = m[3 + h][B[o]] * v - m[2 - h][B[o]], l[f + 3 * h + 20] = m[8 - h][B[o]] * v + m[9 + h][B[o]];
                  }
                  E(l, f);
                } else {
                  for (var d = de(18), h = -9; h < 0; h++) {
                    var g, w;
                    g = A[b][h + 27] * m[h + 9][B[o]] + A[b][h + 36] * m[8 - h][B[o]], w = A[b][h + 9] * p[h + 9][B[o]] - A[b][h + 18] * p[8 - h][B[o]], d[h + 9] = g - w * k[3 + h + 9], d[h + 18] = g * k[3 + h + 9] + w;
                  }
                  C(l, f, d);
                }
                if (b != Ae.SHORT_TYPE && 0 != o) for (var h = 7; 0 <= h; --h) {
                  var S, M;
                  S = l[f + h] * R[20 + h] + l[f + -1 - h] * x[28 + h], M = l[f + h] * x[28 + h] - l[f + -1 - h] * R[20 + h], l[f + -1 - h] = S, l[f + h] = M;
                }
              }
            }
            if (n = a, s = 286, 1 == e.mode_gr) for (var y = 0; y < 18; y++) I.arraycopy(e.sb_sample[r][1][y], 0, e.sb_sample[r][0][y], 0, 32);
          }
        };
      }();
      this.lame_encode_mp3_frame = function (e, t, a, n, s, r) {
        var i,
          o = E([2, 2]);
        o[0][0] = new L(), o[0][1] = new L(), o[1][0] = new L(), o[1][1] = new L();
        var _,
          l = E([2, 2]);
        l[0][0] = new L(), l[0][1] = new L(), l[1][0] = new L(), l[1][1] = new L();
        var f,
          c,
          u,
          h = [null, null],
          b = e.internal_flags,
          p = ge([2, 4]),
          m = [[0, 0], [0, 0]],
          v = [[0, 0], [0, 0]];
        if (h[0] = t, h[1] = a, 0 == b.lame_encode_frame_init && function (e, t) {
          var a,
            n,
            s = e.internal_flags;
          if (0 == s.lame_encode_frame_init) {
            var r,
              i,
              o = de(2014),
              _ = de(2014);
            for (s.lame_encode_frame_init = 1, i = r = 0; r < 286 + 576 * (1 + s.mode_gr); ++r) r < 576 * s.mode_gr ? (o[r] = 0, 2 == s.channels_out && (_[r] = 0)) : (o[r] = t[0][i], 2 == s.channels_out && (_[r] = t[1][i]), ++i);
            for (n = 0; n < s.mode_gr; n++) for (a = 0; a < s.channels_out; a++) s.l3_side.tt[n][a].block_type = Ae.SHORT_TYPE;
            T.mdct_sub48(s, o, _);
          }
        }(e, h), b.padding = 0, (b.slot_lag -= b.frac_SpF) < 0 && (b.slot_lag += e.out_samplerate, b.padding = 1), 0 != b.psymodel) {
          var d,
            g = [null, null],
            w = 0,
            S = ve(2);
          for (u = 0; u < b.mode_gr; u++) {
            for (c = 0; c < b.channels_out; c++) g[c] = h[c], w = 576 + 576 * u - Ae.FFTOFFSET;
            if (e.VBR == Me.vbr_mtrh || e.VBR == Me.vbr_mt ? me() : d = x.L3psycho_anal_ns(e, g, w, u, o, l, m[u], v[u], p[u], S), 0 != d) return -4;
            for (e.mode == ye.JOINT_STEREO && me(), c = 0; c < b.channels_out; c++) {
              var M = b.l3_side.tt[u][c];
              M.block_type = S[c], M.mixed_block_flag = 0;
            }
          }
        } else me();
        if (function (e) {
          var t, a;
          if (0 == e.ATH.useAdjust) return e.ATH.adjust = 1;
          if (a = e.loudness_sq[0][0], t = e.loudness_sq[1][0], 2 == e.channels_out ? me() : (a += a, t += t), 2 == e.mode_gr && (a = Math.max(a, t)), a *= .5, .03125 < (a *= e.ATH.aaSensitivityP)) 1 <= e.ATH.adjust ? e.ATH.adjust = 1 : e.ATH.adjust < e.ATH.adjustLimit && (e.ATH.adjust = e.ATH.adjustLimit), e.ATH.adjustLimit = 1;else {
            var n = 31.98 * a + 625e-6;
            e.ATH.adjust >= n ? (e.ATH.adjust *= .075 * n + .925, e.ATH.adjust < n && (e.ATH.adjust = n)) : e.ATH.adjustLimit >= n ? e.ATH.adjust = n : e.ATH.adjust < e.ATH.adjustLimit && (e.ATH.adjust = e.ATH.adjustLimit), e.ATH.adjustLimit = n;
          }
        }(b), T.mdct_sub48(b, h[0], h[1]), b.mode_ext = Ae.MPG_MD_LR_LR, e.force_ms ? b.mode_ext = Ae.MPG_MD_MS_LR : e.mode == ye.JOINT_STEREO && me(), b.mode_ext == k ? (_ = l, f = v) : (_ = o, f = m), e.analysis && null != b.pinfo && me(), e.VBR == Me.vbr_off || e.VBR == Me.vbr_abr) {
          var y, A;
          for (y = 0; y < 18; y++) b.nsPsy.pefirbuf[y] = b.nsPsy.pefirbuf[y + 1];
          for (u = A = 0; u < b.mode_gr; u++) for (c = 0; c < b.channels_out; c++) A += f[u][c];
          for (b.nsPsy.pefirbuf[18] = A, A = b.nsPsy.pefirbuf[9], y = 0; y < 9; y++) A += (b.nsPsy.pefirbuf[y] + b.nsPsy.pefirbuf[18 - y]) * Ae.fircoef[y];
          for (A = 3350 * b.mode_gr * b.channels_out / A, u = 0; u < b.mode_gr; u++) for (c = 0; c < b.channels_out; c++) f[u][c] *= A;
        }
        return b.iteration_loop.iteration_loop(e, f, [.5, .5], _), R.format_bitstream(e), i = R.copy_buffer(b, n, s, r, 1), e.bWriteVbrTag && B.addVbrFrame(e), e.analysis && null != b.pinfo && me(), function (e) {
          var t, a;
          for (e.bitrate_stereoMode_Hist[e.bitrate_index][4]++, e.bitrate_stereoMode_Hist[15][4]++, 2 == e.channels_out && me(), t = 0; t < e.mode_gr; ++t) for (a = 0; a < e.channels_out; ++a) {
            var n = 0 | e.l3_side.tt[t][a].block_type;
            0 != e.l3_side.tt[t][a].mixed_block_flag && (n = 4), e.bitrate_blockType_Hist[e.bitrate_index][n]++, e.bitrate_blockType_Hist[e.bitrate_index][5]++, e.bitrate_blockType_Hist[15][n]++, e.bitrate_blockType_Hist[15][5]++;
          }
        }(b), i;
      };
    }
    function i() {
      this.l = de(Ae.SBMAX_l), this.s = ge([Ae.SBMAX_s, 3]);
      var n = this;
      this.assign = function (e) {
        I.arraycopy(e.l, 0, n.l, 0, Ae.SBMAX_l);
        for (var t = 0; t < Ae.SBMAX_s; t++) for (var a = 0; a < 3; a++) n.s[t][a] = e.s[t][a];
      };
    }
    function j() {
      var e = 40;
      function t() {
        this.write_timing = 0, this.ptr = 0, this.buf = S(e);
      }
      this.Class_ID = 0, this.lame_encode_frame_init = 0, this.iteration_init_init = 0, this.fill_buffer_resample_init = 0, this.mfbuf = ge([2, j.MFSIZE]), this.mode_gr = 0, this.channels_in = 0, this.channels_out = 0, this.resample_ratio = 0, this.mf_samples_to_encode = 0, this.mf_size = 0, this.VBR_min_bitrate = 0, this.VBR_max_bitrate = 0, this.bitrate_index = 0, this.samplerate_index = 0, this.mode_ext = 0, this.lowpass1 = 0, this.lowpass2 = 0, this.highpass1 = 0, this.highpass2 = 0, this.noise_shaping = 0, this.noise_shaping_amp = 0, this.substep_shaping = 0, this.psymodel = 0, this.noise_shaping_stop = 0, this.subblock_gain = 0, this.use_best_huffman = 0, this.full_outer_loop = 0, this.l3_side = new function () {
        this.tt = [[null, null], [null, null]], this.main_data_begin = 0, this.private_bits = 0, this.resvDrain_pre = 0, this.resvDrain_post = 0, this.scfsi = [ve(4), ve(4)];
        for (var e = 0; e < 2; e++) for (var t = 0; t < 2; t++) this.tt[e][t] = new B();
      }(), this.ms_ratio = de(2), this.padding = 0, this.frac_SpF = 0, this.slot_lag = 0, this.tag_spec = null, this.nMusicCRC = 0, this.OldValue = ve(2), this.CurrentStep = ve(2), this.masking_lower = 0, this.bv_scf = ve(576), this.pseudohalf = ve(F.SFBMAX), this.sfb21_extra = !1, this.inbuf_old = new Array(2), this.blackfilt = new Array(2 * j.BPC + 1), this.itime = n(2), this.sideinfo_len = 0, this.sb_sample = ge([2, 2, 18, Ae.SBLIMIT]), this.amp_filter = de(32), this.header = new Array(j.MAX_HEADER_BUF), this.h_ptr = 0, this.w_ptr = 0, this.ancillary_flag = 0, this.ResvSize = 0, this.ResvMax = 0, this.scalefac_band = new r(), this.minval_l = de(Ae.CBANDS), this.minval_s = de(Ae.CBANDS), this.nb_1 = ge([4, Ae.CBANDS]), this.nb_2 = ge([4, Ae.CBANDS]), this.nb_s1 = ge([4, Ae.CBANDS]), this.nb_s2 = ge([4, Ae.CBANDS]), this.s3_ss = null, this.s3_ll = null, this.decay = 0, this.thm = new Array(4), this.en = new Array(4), this.tot_ener = de(4), this.loudness_sq = ge([2, 2]), this.loudness_sq_save = de(2), this.mld_l = de(Ae.SBMAX_l), this.mld_s = de(Ae.SBMAX_s), this.bm_l = ve(Ae.SBMAX_l), this.bo_l = ve(Ae.SBMAX_l), this.bm_s = ve(Ae.SBMAX_s), this.bo_s = ve(Ae.SBMAX_s), this.npart_l = 0, this.npart_s = 0, this.s3ind = w([Ae.CBANDS, 2]), this.s3ind_s = w([Ae.CBANDS, 2]), this.numlines_s = ve(Ae.CBANDS), this.numlines_l = ve(Ae.CBANDS), this.rnumlines_l = de(Ae.CBANDS), this.mld_cb_l = de(Ae.CBANDS), this.mld_cb_s = de(Ae.CBANDS), this.numlines_s_num1 = 0, this.numlines_l_num1 = 0, this.pe = de(4), this.ms_ratio_s_old = 0, this.ms_ratio_l_old = 0, this.ms_ener_ratio_old = 0, this.blocktype_old = ve(2), this.nsPsy = new function () {
        this.last_en_subshort = ge([4, 9]), this.lastAttacks = ve(4), this.pefirbuf = de(19), this.longfact = de(Ae.SBMAX_l), this.shortfact = de(Ae.SBMAX_s), this.attackthre = 0, this.attackthre_s = 0;
      }(), this.VBR_seek_table = new function () {
        this.sum = 0, this.seen = 0, this.want = 0, this.pos = 0, this.size = 0, this.bag = null, this.nVbrNumFrames = 0, this.nBytesWritten = 0, this.TotalFrameSize = 0;
      }(), this.ATH = null, this.PSY = null, this.nogap_total = 0, this.nogap_current = 0, this.decode_on_the_fly = !0, this.findReplayGain = !0, this.findPeakSample = !0, this.PeakSample = 0, this.RadioGain = 0, this.AudiophileGain = 0, this.rgdata = null, this.noclipGainChange = 0, this.noclipScale = 0, this.bitrate_stereoMode_Hist = w([16, 5]), this.bitrate_blockType_Hist = w([16, 6]), this.pinfo = null, this.hip = null, this.in_buffer_nsamples = 0, this.in_buffer_0 = null, this.in_buffer_1 = null, this.iteration_loop = null;
      for (var a = 0; a < this.en.length; a++) this.en[a] = new i();
      for (var a = 0; a < this.thm.length; a++) this.thm[a] = new i();
      for (var a = 0; a < this.header.length; a++) this.header[a] = new t();
    }
    function X() {
      var k = new function () {
          var h = de(Ae.BLKSIZE),
            p = de(Ae.BLKSIZE_s / 2),
            x = [.9238795325112867, .3826834323650898, .9951847266721969, .0980171403295606, .9996988186962042, .02454122852291229, .9999811752826011, .006135884649154475];
          function m(e, t, a) {
            var n,
              s,
              r,
              i = 0,
              o = t + (a <<= 1);
            n = 4;
            do {
              var _, l, f, c, u, h, b;
              for (b = n >> 1, h = (u = (c = n) << 1) + c, n = u << 1, r = (s = t) + b; S = e[s + 0] - e[s + c], w = e[s + 0] + e[s + c], k = e[s + u] - e[s + h], y = e[s + u] + e[s + h], e[s + u] = w - y, e[s + 0] = w + y, e[s + h] = S - k, e[s + c] = S + k, S = e[r + 0] - e[r + c], w = e[r + 0] + e[r + c], k = V.SQRT2 * e[r + h], y = V.SQRT2 * e[r + u], e[r + u] = w - y, e[r + 0] = w + y, e[r + h] = S - k, e[r + c] = S + k, r += n, (s += n) < o;);
              for (l = x[i + 0], _ = x[i + 1], f = 1; f < b; f++) {
                var p, m;
                p = 1 - 2 * _ * _, m = 2 * _ * l, s = t + f, r = t + c - f;
                do {
                  var v, d, g, w, S, M, y, A, k, R;
                  d = m * e[s + c] - p * e[r + c], v = p * e[s + c] + m * e[r + c], S = e[s + 0] - v, w = e[s + 0] + v, M = e[r + 0] - d, g = e[r + 0] + d, d = m * e[s + h] - p * e[r + h], v = p * e[s + h] + m * e[r + h], k = e[s + u] - v, y = e[s + u] + v, R = e[r + u] - d, A = e[r + u] + d, d = _ * y - l * R, v = l * y + _ * R, e[s + u] = w - v, e[s + 0] = w + v, e[r + h] = M - d, e[r + c] = M + d, d = l * A - _ * k, v = _ * A + l * k, e[r + u] = g - v, e[r + 0] = g + v, e[s + h] = S - d, e[s + c] = S + d, r += n, s += n;
                } while (s < o);
                l = (p = l) * x[i + 0] - _ * x[i + 1], _ = p * x[i + 1] + _ * x[i + 0];
              }
              i += 2;
            } while (n < a);
          }
          var v = [0, 128, 64, 192, 32, 160, 96, 224, 16, 144, 80, 208, 48, 176, 112, 240, 8, 136, 72, 200, 40, 168, 104, 232, 24, 152, 88, 216, 56, 184, 120, 248, 4, 132, 68, 196, 36, 164, 100, 228, 20, 148, 84, 212, 52, 180, 116, 244, 12, 140, 76, 204, 44, 172, 108, 236, 28, 156, 92, 220, 60, 188, 124, 252, 2, 130, 66, 194, 34, 162, 98, 226, 18, 146, 82, 210, 50, 178, 114, 242, 10, 138, 74, 202, 42, 170, 106, 234, 26, 154, 90, 218, 58, 186, 122, 250, 6, 134, 70, 198, 38, 166, 102, 230, 22, 150, 86, 214, 54, 182, 118, 246, 14, 142, 78, 206, 46, 174, 110, 238, 30, 158, 94, 222, 62, 190, 126, 254];
          this.fft_short = function (e, t, a, n, s) {
            for (var r = 0; r < 3; r++) {
              var i = Ae.BLKSIZE_s / 2,
                o = 65535 & 192 * (r + 1),
                _ = Ae.BLKSIZE_s / 8 - 1;
              do {
                var l,
                  f,
                  c,
                  u,
                  h,
                  b = 255 & v[_ << 2];
                l = p[b] * n[a][s + b + o], h = p[127 - b] * n[a][s + b + o + 128], f = l - h, l += h, c = p[b + 64] * n[a][s + b + o + 64], h = p[63 - b] * n[a][s + b + o + 192], u = c - h, c += h, i -= 4, t[r][i + 0] = l + c, t[r][i + 2] = l - c, t[r][i + 1] = f + u, t[r][i + 3] = f - u, l = p[b + 1] * n[a][s + b + o + 1], h = p[126 - b] * n[a][s + b + o + 129], f = l - h, l += h, c = p[b + 65] * n[a][s + b + o + 65], h = p[62 - b] * n[a][s + b + o + 193], u = c - h, c += h, t[r][i + Ae.BLKSIZE_s / 2 + 0] = l + c, t[r][i + Ae.BLKSIZE_s / 2 + 2] = l - c, t[r][i + Ae.BLKSIZE_s / 2 + 1] = f + u, t[r][i + Ae.BLKSIZE_s / 2 + 3] = f - u;
              } while (0 <= --_);
              m(t[r], i, Ae.BLKSIZE_s / 2);
            }
          }, this.fft_long = function (e, t, a, n, s) {
            var r = Ae.BLKSIZE / 8 - 1,
              i = Ae.BLKSIZE / 2;
            do {
              var o,
                _,
                l,
                f,
                c,
                u = 255 & v[r];
              o = h[u] * n[a][s + u], c = h[u + 512] * n[a][s + u + 512], _ = o - c, o += c, l = h[u + 256] * n[a][s + u + 256], c = h[u + 768] * n[a][s + u + 768], f = l - c, l += c, t[0 + (i -= 4)] = o + l, t[i + 2] = o - l, t[i + 1] = _ + f, t[i + 3] = _ - f, o = h[u + 1] * n[a][s + u + 1], c = h[u + 513] * n[a][s + u + 513], _ = o - c, o += c, l = h[u + 257] * n[a][s + u + 257], c = h[u + 769] * n[a][s + u + 769], f = l - c, l += c, t[i + Ae.BLKSIZE / 2 + 0] = o + l, t[i + Ae.BLKSIZE / 2 + 2] = o - l, t[i + Ae.BLKSIZE / 2 + 1] = _ + f, t[i + Ae.BLKSIZE / 2 + 3] = _ - f;
            } while (0 <= --r);
            m(t, i, Ae.BLKSIZE / 2);
          }, this.init_fft = function (e) {
            for (var t = 0; t < Ae.BLKSIZE; t++) h[t] = .42 - .5 * Math.cos(2 * Math.PI * (t + .5) / Ae.BLKSIZE) + .08 * Math.cos(4 * Math.PI * (t + .5) / Ae.BLKSIZE);
            for (var t = 0; t < Ae.BLKSIZE_s / 2; t++) p[t] = .5 * (1 - Math.cos(2 * Math.PI * (t + .5) / Ae.BLKSIZE_s));
          };
        }(),
        R = 2.302585092994046,
        d = 2,
        g = 16,
        E = .34,
        v = 1 / 217621504 / (Ae.BLKSIZE / 2),
        w = .2302585093;
      function se(e, t, a, n, s, r, i, o, _, l, f) {
        var c = e.internal_flags;
        _ < 2 ? (k.fft_long(c, n[s], _, l, f), k.fft_short(c, r[i], _, l, f)) : 2 == _ && me(), t[0] = n[s + 0][0], t[0] *= t[0];
        for (var u = Ae.BLKSIZE / 2 - 1; 0 <= u; --u) {
          var h = n[s + 0][Ae.BLKSIZE / 2 - u],
            b = n[s + 0][Ae.BLKSIZE / 2 + u];
          t[Ae.BLKSIZE / 2 - u] = .5 * (h * h + b * b);
        }
        for (var p = 2; 0 <= p; --p) {
          a[p][0] = r[i + 0][p][0], a[p][0] *= a[p][0];
          for (var u = Ae.BLKSIZE_s / 2 - 1; 0 <= u; --u) {
            var h = r[i + 0][p][Ae.BLKSIZE_s / 2 - u],
              b = r[i + 0][p][Ae.BLKSIZE_s / 2 + u];
            a[p][Ae.BLKSIZE_s / 2 - u] = .5 * (h * h + b * b);
          }
        }
        for (var m = 0, u = 11; u < Ae.HBLKSIZE; u++) m += t[u];
        c.tot_ener[_] = m, e.analysis && me(), 2 == e.athaa_loudapprox && _ < 2 && (c.loudness_sq[o][_] = c.loudness_sq_save[_], c.loudness_sq_save[_] = function (e, t) {
          for (var a = 0, n = 0; n < Ae.BLKSIZE / 2; ++n) a += e[n] * t.ATH.eql_w[n];
          return a *= v;
        }(t, c));
      }
      var x,
        B,
        T,
        C = 8,
        I = 23,
        L = 15,
        re = [1, .79433, .63096, .63096, .63096, .63096, .63096, .25119, .11749],
        f = [3.3246 * 3.3246, 3.23837 * 3.23837, 9.9500500969, 9.0247369744, 8.1854926609, 7.0440875649, 2.46209 * 2.46209, 2.284 * 2.284, 4.4892710641, 1.96552 * 1.96552, 1.82335 * 1.82335, 1.69146 * 1.69146, 2.4621061921, 2.1508568964, 1.37074 * 1.37074, 1.31036 * 1.31036, 1.5691069696, 1.4555939904, 1.16203 * 1.16203, 1.2715945225, 1.09428 * 1.09428, 1.0659 * 1.0659, 1.0779838276, 1.0382591025, 1],
        c = [1.7782755904, 1.35879 * 1.35879, 1.38454 * 1.38454, 1.39497 * 1.39497, 1.40548 * 1.40548, 1.3537 * 1.3537, 1.6999465924, 1.22321 * 1.22321, 1.3169398564, 1],
        u = [5.5396212496, 2.29259 * 2.29259, 4.9868695969, 2.12675 * 2.12675, 2.02545 * 2.02545, 1.87894 * 1.87894, 1.74303 * 1.74303, 1.61695 * 1.61695, 2.2499700001, 1.39148 * 1.39148, 1.29083 * 1.29083, 1.19746 * 1.19746, 1.2339655056, 1.0779838276];
      function ie(e, t, a, n, s, r) {
        var i;
        if (e < t) {
          if (!(t < e * B)) return e + t;
          i = t / e;
        } else {
          if (t * B <= e) return e + t;
          i = e / t;
        }
        if (e += t, n + 3 <= 6) {
          if (x <= i) return e;
          var o = 0 | V.FAST_LOG10_X(i, 16);
          return e * c[o];
        }
        var _,
          l,
          o = 0 | V.FAST_LOG10_X(i, 16);
        return t = 0 != r ? s.ATH.cb_s[a] * s.ATH.adjust : s.ATH.cb_l[a] * s.ATH.adjust, e < T * t ? t < e ? (_ = 1, o <= 13 && (_ = u[o]), l = V.FAST_LOG10_X(e / t, 10 / 15), e * ((f[o] - _) * l + _)) : 13 < o ? e : e * u[o] : e * f[o];
      }
      function oe(e, t, a, n, s) {
        var r,
          i,
          o = 0,
          _ = 0;
        for (r = i = 0; r < Ae.SBMAX_s; ++i, ++r) {
          for (var l = e.bo_s[r], f = e.npart_s, c = l < f ? l : f; i < c;) o += t[i], _ += a[i], i++;
          if (e.en[n].s[r][s] = o, e.thm[n].s[r][s] = _, f <= i) {
            ++r;
            break;
          }
          var u = e.PSY.bo_s_weight[r],
            h = 1 - u;
          o = u * t[i], _ = u * a[i], e.en[n].s[r][s] += o, e.thm[n].s[r][s] += _, o = h * t[i], _ = h * a[i];
        }
        for (; r < Ae.SBMAX_s; ++r) e.en[n].s[r][s] = 0, e.thm[n].s[r][s] = 0;
      }
      function _e(e, t, a, n) {
        var s,
          r,
          i = 0,
          o = 0;
        for (s = r = 0; s < Ae.SBMAX_l; ++r, ++s) {
          for (var _ = e.bo_l[s], l = e.npart_l, f = _ < l ? _ : l; r < f;) i += t[r], o += a[r], r++;
          if (e.en[n].l[s] = i, e.thm[n].l[s] = o, l <= r) {
            ++s;
            break;
          }
          var c = e.PSY.bo_l_weight[s],
            u = 1 - c;
          i = c * t[r], o = c * a[r], e.en[n].l[s] += i, e.thm[n].l[s] += o, i = u * t[r], o = u * a[r];
        }
        for (; s < Ae.SBMAX_l; ++s) e.en[n].l[s] = 0, e.thm[n].l[s] = 0;
      }
      function le(e, t, a, n, s, r) {
        var i,
          o,
          _ = e.internal_flags;
        for (o = i = 0; o < _.npart_s; ++o) {
          for (var l = 0, f = 0, c = _.numlines_s[o], u = 0; u < c; ++u, ++i) {
            var h = t[r][i];
            l += h, f < h && (f = h);
          }
          a[o] = l;
        }
        for (i = o = 0; o < _.npart_s; o++) {
          var b = _.s3ind_s[o][0],
            p = _.s3_ss[i++] * a[b];
          for (++b; b <= _.s3ind_s[o][1];) p += _.s3_ss[i] * a[b], ++i, ++b;
          var m = d * _.nb_s1[s][o];
          if (n[o] = Math.min(p, m), _.blocktype_old[1 & s] == Ae.SHORT_TYPE) {
            var m = g * _.nb_s2[s][o],
              v = n[o];
            n[o] = Math.min(m, v);
          }
          _.nb_s2[s][o] = _.nb_s1[s][o], _.nb_s1[s][o] = p;
        }
        for (; o <= Ae.CBANDS; ++o) a[o] = 0, n[o] = 0;
      }
      function fe(e, t, a) {
        return 1 <= a ? e : a <= 0 ? t : 0 < t ? Math.pow(e / t, a) * t : 0;
      }
      var _ = [11.8, 13.6, 17.2, 32, 46.5, 51.3, 57.5, 67.1, 71.5, 84.6, 97.6, 130];
      function ce(e, t) {
        for (var a = 309.07, n = 0; n < Ae.SBMAX_s - 1; n++) for (var s = 0; s < 3; s++) {
          var r = e.thm.s[n][s];
          if (0 < r) {
            var i = r * t,
              o = e.en.s[n][s];
            i < o && (a += 1e10 * i < o ? _[n] * (10 * R) : _[n] * V.FAST_LOG10(o / i));
          }
        }
        return a;
      }
      var o = [6.8, 5.8, 5.8, 6.4, 6.5, 9.9, 12.1, 14.4, 15, 18.9, 21.6, 26.9, 34.2, 40.2, 46.8, 56.5, 60.7, 73.9, 85.7, 93.4, 126.1];
      function ue(e, t) {
        for (var a = 281.0575, n = 0; n < Ae.SBMAX_l - 1; n++) {
          var s = e.thm.l[n];
          if (0 < s) {
            var r = s * t,
              i = e.en.l[n];
            r < i && (a += 1e10 * r < i ? o[n] * (10 * R) : o[n] * V.FAST_LOG10(i / r));
          }
        }
        return a;
      }
      function he(e, t, a, n, s) {
        var r, i;
        for (r = i = 0; r < e.npart_l; ++r) {
          var o,
            _ = 0,
            l = 0;
          for (o = 0; o < e.numlines_l[r]; ++o, ++i) {
            var f = t[i];
            _ += f, l < f && (l = f);
          }
          a[r] = _, n[r] = l, s[r] = _ * e.rnumlines_l[r];
        }
      }
      function be(e, t, a, n) {
        var s = re.length - 1,
          r = 0,
          i = a[r] + a[r + 1];
        if (0 < i) {
          var o = t[r];
          o < t[r + 1] && (o = t[r + 1]);
          var _ = 0 | (i = 20 * (2 * o - i) / (i * (e.numlines_l[r] + e.numlines_l[r + 1] - 1)));
          s < _ && (_ = s), n[r] = _;
        } else n[r] = 0;
        for (r = 1; r < e.npart_l - 1; r++) if (0 < (i = a[r - 1] + a[r] + a[r + 1])) {
          var o = t[r - 1];
          o < t[r] && (o = t[r]), o < t[r + 1] && (o = t[r + 1]);
          var _ = 0 | (i = 20 * (3 * o - i) / (i * (e.numlines_l[r - 1] + e.numlines_l[r] + e.numlines_l[r + 1] - 1)));
          s < _ && (_ = s), n[r] = _;
        } else n[r] = 0;
        if (0 < (i = a[r - 1] + a[r])) {
          var o = t[r - 1];
          o < t[r] && (o = t[r]);
          var _ = 0 | (i = 20 * (2 * o - i) / (i * (e.numlines_l[r - 1] + e.numlines_l[r] - 1)));
          s < _ && (_ = s), n[r] = _;
        } else n[r] = 0;
      }
      var pe = [-1.730326e-17, -.01703172, -1.349528e-17, .0418072, -6.73278e-17, -.0876324, -3.0835e-17, .1863476, -1.104424e-16, -.627638];
      function P(e) {
        return e < 0 && (e = 0), e *= .001, 13 * Math.atan(.76 * e) + 3.5 * Math.atan(e * e / 56.25);
      }
      function H(e, t, a, n, s, r, i, o, _, l, f, c) {
        var u,
          h = de(Ae.CBANDS + 1),
          b = o / (15 < c ? 1152 : 384),
          p = ve(Ae.HBLKSIZE);
        o /= _;
        var m = 0,
          v = 0;
        for (u = 0; u < Ae.CBANDS; u++) {
          var d;
          for (x = P(o * m), h[u] = o * m, d = m; P(o * d) - x < E && d <= _ / 2; d++);
          for (e[u] = d - m, v = u + 1; m < d;) p[m++] = u;
          if (_ / 2 < m) {
            m = _ / 2, ++u;
            break;
          }
        }
        h[u] = o * m;
        for (var g = 0; g < c; g++) {
          var w, S, M, y, A;
          M = l[g], y = l[g + 1], (w = 0 | Math.floor(.5 + f * (M - .5))) < 0 && (w = 0), S = 0 | Math.floor(.5 + f * (y - .5)), _ / 2 < S && (S = _ / 2), a[g] = (p[w] + p[S]) / 2, t[g] = p[S];
          var k = b * y;
          i[g] = (k - h[t[g]]) / (h[t[g] + 1] - h[t[g]]), i[g] < 0 ? i[g] = 0 : 1 < i[g] && (i[g] = 1), A = P(o * l[g] * f), A = Math.min(A, 15.5) / 15.5, r[g] = Math.pow(10, 1.25 * (1 - Math.cos(Math.PI * A)) - 2.5);
        }
        for (var R = m = 0; R < v; R++) {
          var x,
            B,
            T = e[R];
          x = P(o * m), B = P(o * (m + T - 1)), n[R] = .5 * (x + B), x = P(o * (m - .5)), B = P(o * (m + T - .5)), s[R] = B - x, m += T;
        }
        return v;
      }
      function O(e, t, a, n, s, r) {
        var i,
          o,
          _,
          l,
          f,
          c,
          u = ge([Ae.CBANDS, Ae.CBANDS]),
          h = 0;
        if (r) for (var b = 0; b < t; b++) for (i = 0; i < t; i++) {
          var p = (o = a[b] - a[i], c = f = l = _ = void 0, _ = o, l = .5 <= (_ *= 0 <= _ ? 3 : 1.5) && _ <= 2.5 ? 8 * ((c = _ - .5) * c - 2 * c) : 0, ((f = 15.811389 + 7.5 * (_ += .474) - 17.5 * Math.sqrt(1 + _ * _)) <= -60 ? 0 : (_ = Math.exp((l + f) * w), _ /= .6609193)) * n[i]);
          u[b][i] = p * s[b];
        } else me();
        for (var b = 0; b < t; b++) {
          for (i = 0; i < t && !(0 < u[b][i]); i++);
          for (e[b][0] = i, i = t - 1; 0 < i && !(0 < u[b][i]); i--);
          e[b][1] = i, h += e[b][1] - e[b][0] + 1;
        }
        for (var m = de(h), v = 0, b = 0; b < t; b++) for (i = e[b][0]; i <= e[b][1]; i++) m[v++] = u[b][i];
        return m;
      }
      function N(e) {
        var t = P(e);
        return t = Math.min(t, 15.5) / 15.5, Math.pow(10, 1.25 * (1 - Math.cos(Math.PI * t)) - 2.5);
      }
      function n(e, t) {
        e < -.3 && (e = 3410), e /= 1e3, e = Math.max(.1, e);
        var a = 3.64 * Math.pow(e, -.8) - 6.8 * Math.exp(-.6 * Math.pow(e - 3.4, 2)) + 6 * Math.exp(-.15 * Math.pow(e - 8.7, 2)) + .001 * (.6 + .04 * t) * Math.pow(e, 4);
        return a;
      }
      this.L3psycho_anal_ns = function (e, t, a, n, s, r, i, o, _, l) {
        var f,
          c,
          u,
          h,
          b,
          p,
          m,
          v,
          d,
          g = e.internal_flags,
          w = ge([2, Ae.BLKSIZE]),
          S = ge([2, 3, Ae.BLKSIZE_s]),
          M = de(Ae.CBANDS + 1),
          y = de(Ae.CBANDS + 1),
          A = de(Ae.CBANDS + 2),
          k = ve(2),
          R = ve(2),
          x = ge([2, 576]),
          B = ve(Ae.CBANDS + 2),
          T = ve(Ae.CBANDS + 2);
        for (we.fill(T, 0), f = g.channels_out, e.mode == ye.JOINT_STEREO && (f = 4), d = e.VBR == Me.vbr_off ? 0 == g.ResvMax ? 0 : g.ResvSize / g.ResvMax * .5 : e.VBR == Me.vbr_rh || e.VBR == Me.vbr_mtrh || e.VBR == Me.vbr_mt ? .6 : 1, c = 0; c < g.channels_out; c++) {
          var E = t[c],
            C = a + 576 - 350 - 21 + 192;
          for (h = 0; h < 576; h++) {
            var I, L;
            for (I = E[C + h + 10], b = L = 0; b < 9; b += 2) I += pe[b] * (E[C + h + b] + E[C + h + 21 - b]), L += pe[b + 1] * (E[C + h + b + 1] + E[C + h + 21 - b - 1]);
            x[c][h] = I + L;
          }
          s[n][c].en.assign(g.en[c]), s[n][c].thm.assign(g.thm[c]), 2 < f && me();
        }
        for (c = 0; c < f; c++) {
          var P,
            H = de(12),
            O = [0, 0, 0, 0],
            N = de(12),
            V = 1,
            D = de(Ae.CBANDS),
            F = de(Ae.CBANDS),
            j = [0, 0, 0, 0],
            X = de(Ae.HBLKSIZE),
            Y = ge([3, Ae.HBLKSIZE_s]);
          for (h = 0; h < 3; h++) H[h] = g.nsPsy.last_en_subshort[c][h + 6], N[h] = H[h] / g.nsPsy.last_en_subshort[c][h + 4], O[0] += H[h];
          2 == c && me();
          var z = x[1 & c],
            q = 0;
          for (h = 0; h < 9; h++) {
            for (var G = q + 64, U = 1; q < G; q++) U < Math.abs(z[q]) && (U = Math.abs(z[q]));
            g.nsPsy.last_en_subshort[c][h] = H[h + 3] = U, O[1 + h / 3] += U, U > H[h + 3 - 2] ? U /= H[h + 3 - 2] : U = H[h + 3 - 2] > 10 * U ? H[h + 3 - 2] / (10 * U) : 0, N[h + 3] = U;
          }
          for (e.analysis && me(), P = 3 == c ? g.nsPsy.attackthre_s : g.nsPsy.attackthre, h = 0; h < 12; h++) 0 == j[h / 3] && N[h] > P && (j[h / 3] = h % 3 + 1);
          for (h = 1; h < 4; h++) (O[h - 1] > O[h] ? O[h - 1] / O[h] : O[h] / O[h - 1]) < 1.7 && (j[h] = 0, 1 == h && (j[0] = 0));
          for (0 != j[0] && 0 != g.nsPsy.lastAttacks[c] && (j[0] = 0), 3 != g.nsPsy.lastAttacks[c] && j[0] + j[1] + j[2] + j[3] == 0 || ((V = 0) != j[1] && 0 != j[0] && (j[1] = 0), 0 != j[2] && 0 != j[1] && (j[2] = 0), 0 != j[3] && 0 != j[2] && (j[3] = 0)), c < 2 ? R[c] = V : me(), _[c] = g.tot_ener[c], se(e, X, Y, w, 1 & c, S, 1 & c, n, c, t, a), he(g, X, M, D, F), be(g, D, F, B), v = 0; v < 3; v++) {
            var K, Z;
            for (le(e, Y, y, A, c, v), oe(g, y, A, c, v), m = 0; m < Ae.SBMAX_s; m++) {
              if (Z = g.thm[c].s[m][v], Z *= .8, 2 <= j[v] || 1 == j[v + 1]) {
                var W = 0 != v ? v - 1 : 2,
                  U = fe(g.thm[c].s[m][W], Z, .6 * d);
                Z = Math.min(Z, U);
              }
              if (1 == j[v]) {
                var W = 0 != v ? v - 1 : 2,
                  U = fe(g.thm[c].s[m][W], Z, .3 * d);
                Z = Math.min(Z, U);
              } else if (0 != v && 3 == j[v - 1] || 0 == v && 3 == g.nsPsy.lastAttacks[c]) {
                var W = 2 != v ? v + 1 : 0,
                  U = fe(g.thm[c].s[m][W], Z, .3 * d);
                Z = Math.min(Z, U);
              }
              K = H[3 * v + 3] + H[3 * v + 4] + H[3 * v + 5], 6 * H[3 * v + 5] < K && (Z *= .5, 6 * H[3 * v + 4] < K && (Z *= .5)), g.thm[c].s[m][v] = Z;
            }
          }
          for (g.nsPsy.lastAttacks[c] = j[2], u = p = 0; u < g.npart_l; u++) {
            for (var Q = g.s3ind[u][0], $ = M[Q] * re[B[Q]], J = g.s3_ll[p++] * $; ++Q <= g.s3ind[u][1];) $ = M[Q] * re[B[Q]], J = ie(J, g.s3_ll[p++] * $, Q, Q - u, g, 0);
            J *= .158489319246111, g.blocktype_old[1 & c] == Ae.SHORT_TYPE ? A[u] = J : A[u] = fe(Math.min(J, Math.min(2 * g.nb_1[c][u], 16 * g.nb_2[c][u])), J, d), g.nb_2[c][u] = g.nb_1[c][u], g.nb_1[c][u] = J;
          }
          for (; u <= Ae.CBANDS; ++u) M[u] = 0, A[u] = 0;
          _e(g, M, A, c);
        }
        for (e.mode != ye.STEREO && e.mode != ye.JOINT_STEREO || me(), e.mode == ye.JOINT_STEREO && me(), function (e, t, a, n) {
          var s = e.internal_flags;
          e.short_blocks != Se.short_block_coupled || 0 != t[0] && 0 != t[1] || (t[0] = t[1] = 0);
          for (var r = 0; r < s.channels_out; r++) n[r] = Ae.NORM_TYPE, e.short_blocks == Se.short_block_dispensed && (t[r] = 1), e.short_blocks == Se.short_block_forced && (t[r] = 0), 0 != t[r] ? s.blocktype_old[r] == Ae.SHORT_TYPE && (n[r] = Ae.STOP_TYPE) : (n[r] = Ae.SHORT_TYPE, s.blocktype_old[r] == Ae.NORM_TYPE && (s.blocktype_old[r] = Ae.START_TYPE), s.blocktype_old[r] == Ae.STOP_TYPE && (s.blocktype_old[r] = Ae.SHORT_TYPE)), a[r] = s.blocktype_old[r], s.blocktype_old[r] = n[r];
        }(e, R, l, k), c = 0; c < f; c++) {
          var ee,
            te,
            ae,
            ne = 0;
          1 < c ? me() : (ee = i, ne = 0, te = l[c], ae = s[n][c]), ee[ne + c] = te == Ae.SHORT_TYPE ? ce(ae, g.masking_lower) : ue(ae, g.masking_lower), e.analysis && (g.pinfo.pe[n][c] = ee[ne + c]);
        }
        return 0;
      }, this.psymodel_init = function (e) {
        var t,
          a = e.internal_flags,
          n = !0,
          s = 13,
          r = 0,
          i = 0,
          o = -8.25,
          _ = -4.5,
          l = de(Ae.CBANDS),
          f = de(Ae.CBANDS),
          c = de(Ae.CBANDS),
          u = e.out_samplerate;
        switch (e.experimentalZ) {
          default:
          case 0:
            n = !0;
            break;
          case 1:
            n = e.VBR != Me.vbr_mtrh && e.VBR != Me.vbr_mt;
            break;
          case 2:
            n = !1;
            break;
          case 3:
            s = 8, r = -1.75, i = -.0125, o = -8.25, _ = -2.25;
        }
        for (a.ms_ener_ratio_old = .25, a.blocktype_old[0] = a.blocktype_old[1] = Ae.NORM_TYPE, t = 0; t < 4; ++t) {
          for (var h = 0; h < Ae.CBANDS; ++h) a.nb_1[t][h] = 1e20, a.nb_2[t][h] = 1e20, a.nb_s1[t][h] = a.nb_s2[t][h] = 1;
          for (var b = 0; b < Ae.SBMAX_l; b++) a.en[t].l[b] = 1e20, a.thm[t].l[b] = 1e20;
          for (var h = 0; h < 3; ++h) {
            for (var b = 0; b < Ae.SBMAX_s; b++) a.en[t].s[b][h] = 1e20, a.thm[t].s[b][h] = 1e20;
            a.nsPsy.lastAttacks[t] = 0;
          }
          for (var h = 0; h < 9; h++) a.nsPsy.last_en_subshort[t][h] = 10;
        }
        for (a.loudness_sq_save[0] = a.loudness_sq_save[1] = 0, a.npart_l = H(a.numlines_l, a.bo_l, a.bm_l, l, f, a.mld_l, a.PSY.bo_l_weight, u, Ae.BLKSIZE, a.scalefac_band.l, Ae.BLKSIZE / 1152, Ae.SBMAX_l), t = 0; t < a.npart_l; t++) {
          var p = r;
          l[t] >= s && (p = i * (l[t] - s) / (24 - s) + r * (24 - l[t]) / (24 - s)), c[t] = Math.pow(10, p / 10), 0 < a.numlines_l[t] ? a.rnumlines_l[t] = 1 / a.numlines_l[t] : a.rnumlines_l[t] = 0;
        }
        a.s3_ll = O(a.s3ind, a.npart_l, l, f, c, n);
        var m,
          h = 0;
        for (t = 0; t < a.npart_l; t++) {
          g = D.MAX_VALUE;
          for (var v = 0; v < a.numlines_l[t]; v++, h++) {
            var d = u * h / (1e3 * Ae.BLKSIZE);
            w = this.ATHformula(1e3 * d, e) - 20, w = Math.pow(10, .1 * w), (w *= a.numlines_l[t]) < g && (g = w);
          }
          a.ATH.cb_l[t] = g, 6 < (g = 20 * l[t] / 10 - 20) && (g = 100), g < -15 && (g = -15), g -= 8, a.minval_l[t] = Math.pow(10, g / 10) * a.numlines_l[t];
        }
        for (a.npart_s = H(a.numlines_s, a.bo_s, a.bm_s, l, f, a.mld_s, a.PSY.bo_s_weight, u, Ae.BLKSIZE_s, a.scalefac_band.s, Ae.BLKSIZE_s / 384, Ae.SBMAX_s), t = h = 0; t < a.npart_s; t++) {
          var g,
            p = o;
          l[t] >= s && (p = _ * (l[t] - s) / (24 - s) + o * (24 - l[t]) / (24 - s)), c[t] = Math.pow(10, p / 10), g = D.MAX_VALUE;
          for (var v = 0; v < a.numlines_s[t]; v++, h++) {
            var w,
              d = u * h / (1e3 * Ae.BLKSIZE_s);
            w = this.ATHformula(1e3 * d, e) - 20, w = Math.pow(10, .1 * w), (w *= a.numlines_s[t]) < g && (g = w);
          }
          a.ATH.cb_s[t] = g, g = 7 * l[t] / 12 - 7, 12 < l[t] && (g *= 1 + 3.1 * Math.log(1 + g)), l[t] < 12 && (g *= 1 + 2.3 * Math.log(1 - g)), g < -15 && (g = -15), g -= 8, a.minval_s[t] = Math.pow(10, g / 10) * a.numlines_s[t];
        }
        a.s3_ss = O(a.s3ind_s, a.npart_s, l, f, c, n), x = Math.pow(10, (C + 1) / 16), B = Math.pow(10, (I + 1) / 16), T = Math.pow(10, L / 10), k.init_fft(a), a.decay = Math.exp(-1 * R / (.01 * u / 192)), m = 3.5, 0 != (2 & e.exp_nspsytune) && (m = 1), 0 < Math.abs(e.msfix) && (m = e.msfix), e.msfix = m;
        for (var S = 0; S < a.npart_l; S++) a.s3ind[S][1] > a.npart_l - 1 && (a.s3ind[S][1] = a.npart_l - 1);
        var M = 576 * a.mode_gr / u;
        if (a.ATH.decay = Math.pow(10, -1.2 * M), a.ATH.adjust = .01, -(a.ATH.adjustLimit = 1) != e.ATHtype) {
          var y = e.out_samplerate / Ae.BLKSIZE,
            A = 0;
          for (t = d = 0; t < Ae.BLKSIZE / 2; ++t) d += y, a.ATH.eql_w[t] = 1 / Math.pow(10, this.ATHformula(d, e) / 10), A += a.ATH.eql_w[t];
          for (A = 1 / A, t = Ae.BLKSIZE / 2; 0 <= --t;) a.ATH.eql_w[t] *= A;
        }
        for (var S = h = 0; S < a.npart_s; ++S) for (t = 0; t < a.numlines_s[S]; ++t) ++h;
        for (var S = h = 0; S < a.npart_l; ++S) for (t = 0; t < a.numlines_l[S]; ++t) ++h;
        for (t = h = 0; t < a.npart_l; t++) {
          var d = u * (h + a.numlines_l[t] / 2) / (1 * Ae.BLKSIZE);
          a.mld_cb_l[t] = N(d), h += a.numlines_l[t];
        }
        for (; t < Ae.CBANDS; ++t) a.mld_cb_l[t] = 1;
        for (t = h = 0; t < a.npart_s; t++) {
          var d = u * (h + a.numlines_s[t] / 2) / (1 * Ae.BLKSIZE_s);
          a.mld_cb_s[t] = N(d), h += a.numlines_s[t];
        }
        for (; t < Ae.CBANDS; ++t) a.mld_cb_s[t] = 1;
        return 0;
      }, this.ATHformula = function (e, t) {
        var a;
        switch (t.ATHtype) {
          case 0:
            a = n(e, 9);
            break;
          case 1:
            a = n(e, -1);
            break;
          case 2:
            a = n(e, 0);
            break;
          case 3:
            a = n(e, 1) + 6;
            break;
          case 4:
            a = n(e, t.ATHcurve);
            break;
          default:
            a = n(e, 0);
        }
        return a;
      };
    }
    function Y() {
      var T,
        E,
        u,
        h,
        b,
        C = this;
      Y.V9 = 410, Y.V8 = 420, Y.V7 = 430, Y.V6 = 440, Y.V5 = 450, Y.V4 = 460, Y.V3 = 470, Y.V2 = 480, Y.V1 = 490, Y.V0 = 500, Y.R3MIX = 1e3, Y.STANDARD = 1001, Y.EXTREME = 1002, Y.INSANE = 1003, Y.STANDARD_FAST = 1004, Y.EXTREME_FAST = 1005, Y.MEDIUM = 1006, Y.MEDIUM_FAST = 1007, Y.LAME_MAXMP3BUFFER = 147456;
      var p,
        m,
        v = new X();
      function d() {
        this.lowerlimit = 0;
      }
      function s(e, t) {
        this.lowpass = t;
      }
      this.enc = new Ae(), this.setModules = function (e, t, a, n, s, r, i, o, _) {
        T = e, E = t, u = a, h = n, b = s, p = r, m = o, this.enc.setModules(E, v, h, p);
      };
      var I = 4294479419;
      function g(e, t) {
        var a = [new s(8, 2e3), new s(16, 3700), new s(24, 3900), new s(32, 5500), new s(40, 7e3), new s(48, 7500), new s(56, 1e4), new s(64, 11e3), new s(80, 13500), new s(96, 15100), new s(112, 15600), new s(128, 17e3), new s(160, 17500), new s(192, 18600), new s(224, 19400), new s(256, 19700), new s(320, 20500)],
          n = C.nearestBitrateFullIndex(t);
        e.lowerlimit = a[n].lowpass;
      }
      function L(e) {
        var t = Ae.BLKSIZE + e.framesize - Ae.FFTOFFSET;
        return t = Math.max(t, 512 + e.framesize - 32);
      }
      function P() {
        this.n_in = 0, this.n_out = 0;
      }
      function H(e, t, a, n, s, r) {
        var i = e.internal_flags;
        if (i.resample_ratio < .9999 || 1.0001 < i.resample_ratio) me();else {
          r.n_out = Math.min(e.framesize, s), r.n_in = r.n_out;
          for (var o = 0; o < r.n_out; ++o) t[0][i.mf_size + o] = a[0][n + o], 2 == i.channels_out && (t[1][i.mf_size + o] = a[1][n + o]);
        }
      }
      this.lame_init = function () {
        var e,
          t,
          a = new function () {
            this.class_id = 0, this.num_samples = 0, this.num_channels = 0, this.in_samplerate = 0, this.out_samplerate = 0, this.scale = 0, this.scale_left = 0, this.scale_right = 0, this.analysis = !1, this.bWriteVbrTag = !1, this.decode_only = !1, this.quality = 0, this.mode = ye.STEREO, this.force_ms = !1, this.free_format = !1, this.findReplayGain = !1, this.decode_on_the_fly = !1, this.write_id3tag_automatic = !1, this.brate = 0, this.compression_ratio = 0, this.copyright = 0, this.original = 0, this.extension = 0, this.emphasis = 0, this.error_protection = 0, this.strict_ISO = !1, this.disable_reservoir = !1, this.quant_comp = 0, this.quant_comp_short = 0, this.experimentalY = !1, this.experimentalZ = 0, this.exp_nspsytune = 0, this.preset = 0, this.VBR = null, this.VBR_q_frac = 0, this.VBR_q = 0, this.VBR_mean_bitrate_kbps = 0, this.VBR_min_bitrate_kbps = 0, this.VBR_max_bitrate_kbps = 0, this.VBR_hard_min = 0, this.lowpassfreq = 0, this.highpassfreq = 0, this.lowpasswidth = 0, this.highpasswidth = 0, this.maskingadjust = 0, this.maskingadjust_short = 0, this.ATHonly = !1, this.ATHshort = !1, this.noATH = !1, this.ATHtype = 0, this.ATHcurve = 0, this.ATHlower = 0, this.athaa_type = 0, this.athaa_loudapprox = 0, this.athaa_sensitivity = 0, this.short_blocks = null, this.useTemporal = !1, this.interChRatio = 0, this.msfix = 0, this.tune = !1, this.tune_value_a = 0, this.version = 0, this.encoder_delay = 0, this.encoder_padding = 0, this.framesize = 0, this.frameNum = 0, this.lame_allocated_gfp = 0, this.internal_flags = null;
          }(),
          n = ((e = a).class_id = I, t = e.internal_flags = new j(), e.mode = ye.NOT_SET, e.original = 1, e.in_samplerate = 44100, e.num_channels = 2, e.num_samples = -1, e.bWriteVbrTag = !0, e.quality = -1, e.short_blocks = null, t.subblock_gain = -1, e.lowpassfreq = 0, e.highpassfreq = 0, e.lowpasswidth = -1, e.highpasswidth = -1, e.VBR = Me.vbr_off, e.VBR_q = 4, e.ATHcurve = -1, e.VBR_mean_bitrate_kbps = 128, e.VBR_min_bitrate_kbps = 0, e.VBR_max_bitrate_kbps = 0, e.VBR_hard_min = 0, t.VBR_min_bitrate = 1, t.VBR_max_bitrate = 13, e.quant_comp = -1, e.quant_comp_short = -1, e.msfix = -1, t.resample_ratio = 1, t.OldValue[0] = 180, t.OldValue[1] = 180, t.CurrentStep[0] = 4, t.CurrentStep[1] = 4, t.masking_lower = 1, t.nsPsy.attackthre = -1, t.nsPsy.attackthre_s = -1, e.scale = -1, e.athaa_type = -1, e.ATHtype = -1, e.athaa_loudapprox = -1, e.athaa_sensitivity = 0, e.useTemporal = null, e.interChRatio = -1, t.mf_samples_to_encode = Ae.ENCDELAY + Ae.POSTDELAY, e.encoder_padding = 0, t.mf_size = Ae.ENCDELAY - Ae.MDCTDELAY, e.findReplayGain = !1, e.decode_on_the_fly = !1, t.decode_on_the_fly = !1, t.findReplayGain = !1, t.findPeakSample = !1, t.RadioGain = 0, t.AudiophileGain = 0, t.noclipGainChange = 0, t.noclipScale = -1, e.preset = 0, e.write_id3tag_automatic = !0, 0);
        return 0 != n ? null : (a.lame_allocated_gfp = 1, a);
      }, this.nearestBitrateFullIndex = function (e) {
        var t = [8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
          a = 0,
          n = 0,
          s = 0,
          r = 0;
        r = t[16], n = t[s = 16], a = 16;
        for (var i = 0; i < 16; i++) if (Math.max(e, t[i + 1]) != e) {
          r = t[i + 1], s = i + 1, n = t[i], a = i;
          break;
        }
        return e - n < r - e ? a : s;
      }, this.lame_init_params = function (e) {
        var t,
          a,
          n = e.internal_flags;
        if (n.Class_ID = 0, null == n.ATH && (n.ATH = new function () {
          this.useAdjust = 0, this.aaSensitivityP = 0, this.adjust = 0, this.adjustLimit = 0, this.decay = 0, this.floor = 0, this.l = de(Ae.SBMAX_l), this.s = de(Ae.SBMAX_s), this.psfb21 = de(Ae.PSFB21), this.psfb12 = de(Ae.PSFB12), this.cb_l = de(Ae.CBANDS), this.cb_s = de(Ae.CBANDS), this.eql_w = de(Ae.BLKSIZE / 2);
        }()), null == n.PSY && (n.PSY = new function () {
          this.mask_adjust = 0, this.mask_adjust_short = 0, this.bo_l_weight = de(Ae.SBMAX_l), this.bo_s_weight = de(Ae.SBMAX_s);
        }()), null == n.rgdata && (n.rgdata = new function () {}()), n.channels_in = e.num_channels, 1 == n.channels_in && (e.mode = ye.MONO), n.channels_out = e.mode == ye.MONO ? 1 : 2, n.mode_ext = Ae.MPG_MD_MS_LR, e.mode == ye.MONO && (e.force_ms = !1), e.VBR == Me.vbr_off && 128 != e.VBR_mean_bitrate_kbps && 0 == e.brate && (e.brate = e.VBR_mean_bitrate_kbps), e.VBR == Me.vbr_off || e.VBR == Me.vbr_mtrh || e.VBR == Me.vbr_mt || (e.free_format = !1), e.VBR == Me.vbr_off && 0 == e.brate && me(), e.VBR == Me.vbr_off && 0 < e.compression_ratio && me(), 0 != e.out_samplerate && (e.out_samplerate < 16e3 ? (e.VBR_mean_bitrate_kbps = Math.max(e.VBR_mean_bitrate_kbps, 8), e.VBR_mean_bitrate_kbps = Math.min(e.VBR_mean_bitrate_kbps, 64)) : e.out_samplerate < 32e3 ? (e.VBR_mean_bitrate_kbps = Math.max(e.VBR_mean_bitrate_kbps, 8), e.VBR_mean_bitrate_kbps = Math.min(e.VBR_mean_bitrate_kbps, 160)) : (e.VBR_mean_bitrate_kbps = Math.max(e.VBR_mean_bitrate_kbps, 32), e.VBR_mean_bitrate_kbps = Math.min(e.VBR_mean_bitrate_kbps, 320))), 0 == e.lowpassfreq) {
          var s = 16e3;
          switch (e.VBR) {
            case Me.vbr_off:
              var r = new d();
              g(r, e.brate), s = r.lowerlimit;
              break;
            case Me.vbr_abr:
              var r = new d();
              g(r, e.VBR_mean_bitrate_kbps), s = r.lowerlimit;
              break;
            case Me.vbr_rh:
              me();
            default:
              me();
          }
          e.mode != ye.MONO || e.VBR != Me.vbr_off && e.VBR != Me.vbr_abr || (s *= 1.5), e.lowpassfreq = 0 | s;
        }
        switch (0 == e.out_samplerate && me(), e.lowpassfreq = Math.min(20500, e.lowpassfreq), e.lowpassfreq = Math.min(e.out_samplerate / 2, e.lowpassfreq), e.VBR == Me.vbr_off && (e.compression_ratio = 16 * e.out_samplerate * n.channels_out / (1e3 * e.brate)), e.VBR == Me.vbr_abr && me(), e.bWriteVbrTag || (e.findReplayGain = !1, e.decode_on_the_fly = !1, n.findPeakSample = !1), n.findReplayGain = e.findReplayGain, n.decode_on_the_fly = e.decode_on_the_fly, n.decode_on_the_fly && (n.findPeakSample = !0), n.findReplayGain && me(), n.decode_on_the_fly && !e.decode_only && me(), n.mode_gr = e.out_samplerate <= 24e3 ? 1 : 2, e.framesize = 576 * n.mode_gr, e.encoder_delay = Ae.ENCDELAY, n.resample_ratio = e.in_samplerate / e.out_samplerate, e.VBR) {
          case Me.vbr_mt:
          case Me.vbr_rh:
          case Me.vbr_mtrh:
            e.compression_ratio = [5.7, 6.5, 7.3, 8.2, 10, 11.9, 13, 14, 15, 16.5][e.VBR_q];
            break;
          case Me.vbr_abr:
            e.compression_ratio = 16 * e.out_samplerate * n.channels_out / (1e3 * e.VBR_mean_bitrate_kbps);
            break;
          default:
            e.compression_ratio = 16 * e.out_samplerate * n.channels_out / (1e3 * e.brate);
        }
        e.mode == ye.NOT_SET && (e.mode = ye.JOINT_STEREO), 0 < e.highpassfreq ? me() : (n.highpass1 = 0, n.highpass2 = 0), 0 < e.lowpassfreq ? (n.lowpass2 = 2 * e.lowpassfreq, 0 <= e.lowpasswidth ? me() : n.lowpass1 = 2 * e.lowpassfreq, n.lowpass1 /= e.out_samplerate, n.lowpass2 /= e.out_samplerate) : me(), function (e) {
          var t,
            a = e.internal_flags,
            n = 32;
          if (0 < a.lowpass1) {
            for (var s = 999, r = 0; r <= 31; r++) {
              var i = r / 31;
              i >= a.lowpass2 && (n = Math.min(n, r)), a.lowpass1 < i && i < a.lowpass2 && (s = Math.min(s, r));
            }
            a.lowpass1 = 999 == s ? (n - .75) / 31 : (s - .75) / 31, a.lowpass2 = n / 31;
          }
          0 < a.highpass2 && me(), 0 < a.highpass2 && me();
          for (var r = 0; r < 32; r++) {
            var o,
              _,
              i = r / 31;
            a.highpass2 > a.highpass1 ? me() : o = 1, _ = a.lowpass2 > a.lowpass1 ? 1 < (t = (i - a.lowpass1) / (a.lowpass2 - a.lowpass1 + 1e-20)) ? 0 : t <= 0 ? 1 : Math.cos(Math.PI / 2 * t) : 1, a.amp_filter[r] = o * _;
          }
        }(e), n.samplerate_index = function (e, t) {
          switch (e) {
            case 44100:
              return t.version = 1, 0;
            case 48e3:
              return t.version = 1;
            case 32e3:
              return t.version = 1, 2;
            case 22050:
              return t.version = 0;
            case 24e3:
              return t.version = 0, 1;
            case 16e3:
              return t.version = 0, 2;
            case 11025:
              return t.version = 0;
            case 12e3:
              return t.version = 0, 1;
            case 8e3:
              return t.version = 0, 2;
            default:
              return t.version = 0, -1;
          }
        }(e.out_samplerate, e), n.samplerate_index < 0 && me(), e.VBR == Me.vbr_off ? e.free_format ? n.bitrate_index = 0 : (e.brate = function (e, t, a) {
          a < 16e3 && (t = 2);
          for (var n = k.bitrate_table[t][1], s = 2; s <= 14; s++) 0 < k.bitrate_table[t][s] && Math.abs(k.bitrate_table[t][s] - e) < Math.abs(n - e) && (n = k.bitrate_table[t][s]);
          return n;
        }(e.brate, e.version, e.out_samplerate), n.bitrate_index = function (e, t, a) {
          a < 16e3 && (t = 2);
          for (var n = 0; n <= 14; n++) if (0 < k.bitrate_table[t][n] && k.bitrate_table[t][n] == e) return n;
          return -1;
        }(e.brate, e.version, e.out_samplerate), n.bitrate_index <= 0 && me()) : n.bitrate_index = 1, e.analysis && (e.bWriteVbrTag = !1), null != n.pinfo && (e.bWriteVbrTag = !1), E.init_bit_stream_w(n);
        for (var i, o = n.samplerate_index + 3 * e.version + 6 * (e.out_samplerate < 16e3 ? 1 : 0), _ = 0; _ < Ae.SBMAX_l + 1; _++) n.scalefac_band.l[_] = h.sfBandIndex[o].l[_];
        for (var _ = 0; _ < Ae.PSFB21 + 1; _++) {
          var l = (n.scalefac_band.l[22] - n.scalefac_band.l[21]) / Ae.PSFB21,
            f = n.scalefac_band.l[21] + _ * l;
          n.scalefac_band.psfb21[_] = f;
        }
        n.scalefac_band.psfb21[Ae.PSFB21] = 576;
        for (var _ = 0; _ < Ae.SBMAX_s + 1; _++) n.scalefac_band.s[_] = h.sfBandIndex[o].s[_];
        for (var _ = 0; _ < Ae.PSFB12 + 1; _++) {
          var l = (n.scalefac_band.s[13] - n.scalefac_band.s[12]) / Ae.PSFB12,
            f = n.scalefac_band.s[12] + _ * l;
          n.scalefac_band.psfb12[_] = f;
        }
        for (n.scalefac_band.psfb12[Ae.PSFB12] = 192, 1 == e.version ? n.sideinfo_len = 1 == n.channels_out ? 21 : 36 : n.sideinfo_len = 1 == n.channels_out ? 13 : 21, e.error_protection && (n.sideinfo_len += 2), a = void 0, a = (t = e).internal_flags, t.frameNum = 0, t.write_id3tag_automatic && m.id3tag_write_v2(t), a.bitrate_stereoMode_Hist = w([16, 5]), a.bitrate_blockType_Hist = w([16, 6]), a.PeakSample = 0, t.bWriteVbrTag && p.InitVbrTag(t), n.Class_ID = I, i = 0; i < 19; i++) n.nsPsy.pefirbuf[i] = 700 * n.mode_gr * n.channels_out;
        switch (-1 == e.ATHtype && (e.ATHtype = 4), e.VBR) {
          case Me.vbr_mt:
            e.VBR = Me.vbr_mtrh;
          case Me.vbr_mtrh:
            null == e.useTemporal && (e.useTemporal = !1), u.apply_preset(e, 500 - 10 * e.VBR_q, 0), e.quality < 0 && (e.quality = LAME_DEFAULT_QUALITY), e.quality < 5 && (e.quality = 0), 5 < e.quality && (e.quality = 5), n.PSY.mask_adjust = e.maskingadjust, n.PSY.mask_adjust_short = e.maskingadjust_short, e.experimentalY ? n.sfb21_extra = !1 : n.sfb21_extra = 44e3 < e.out_samplerate, n.iteration_loop = new VBRNewIterationLoop(b);
            break;
          case Me.vbr_rh:
            u.apply_preset(e, 500 - 10 * e.VBR_q, 0), n.PSY.mask_adjust = e.maskingadjust, n.PSY.mask_adjust_short = e.maskingadjust_short, e.experimentalY ? n.sfb21_extra = !1 : n.sfb21_extra = 44e3 < e.out_samplerate, 6 < e.quality && (e.quality = 6), e.quality < 0 && (e.quality = LAME_DEFAULT_QUALITY), n.iteration_loop = new VBROldIterationLoop(b);
            break;
          default:
            var c;
            n.sfb21_extra = !1, e.quality < 0 && (e.quality = LAME_DEFAULT_QUALITY), (c = e.VBR) == Me.vbr_off && (e.VBR_mean_bitrate_kbps = e.brate), u.apply_preset(e, e.VBR_mean_bitrate_kbps, 0), e.VBR = c, n.PSY.mask_adjust = e.maskingadjust, n.PSY.mask_adjust_short = e.maskingadjust_short, c == Me.vbr_off ? n.iteration_loop = new function (e) {
              var t = e;
              this.quantize = t, this.iteration_loop = function (e, t, a, n) {
                var s = e.internal_flags,
                  r = de(F.SFBMAX),
                  i = de(576),
                  o = ve(2),
                  _ = 0,
                  l = s.l3_side,
                  f = new R(_);
                this.quantize.rv.ResvFrameBegin(e, f), _ = f.bits;
                for (var c = 0; c < s.mode_gr; c++) {
                  this.quantize.qupvt.on_pe(e, t, o, _, c, c), s.mode_ext == Ae.MPG_MD_MS_LR && me();
                  for (var u = 0; u < s.channels_out; u++) {
                    var h,
                      b,
                      p = l.tt[c][u];
                    p.block_type != Ae.SHORT_TYPE ? (h = 0, b = s.PSY.mask_adjust - h) : (h = 0, b = s.PSY.mask_adjust_short - h), s.masking_lower = Math.pow(10, .1 * b), this.quantize.init_outer_loop(s, p), this.quantize.init_xrpow(s, p, i) && (this.quantize.qupvt.calc_xmin(e, n[c][u], p, r), this.quantize.outer_loop(e, p, r, i, u, o[u])), this.quantize.iteration_finish_one(s, c, u);
                  }
                }
                this.quantize.rv.ResvFrameEnd(s, _);
              };
            }(b) : me();
        }
        return e.VBR != Me.vbr_off && me(), e.tune && me(), function (e) {
          var t = e.internal_flags;
          switch (e.quality) {
            default:
            case 9:
              t.psymodel = 0, t.noise_shaping = 0, t.noise_shaping_amp = 0, t.noise_shaping_stop = 0, t.use_best_huffman = 0, t.full_outer_loop = 0;
              break;
            case 8:
              e.quality = 7;
            case 7:
              t.psymodel = 1, t.noise_shaping = 0, t.noise_shaping_amp = 0, t.noise_shaping_stop = 0, t.use_best_huffman = 0, t.full_outer_loop = 0;
              break;
            case 6:
            case 5:
              t.psymodel = 1, 0 == t.noise_shaping && (t.noise_shaping = 1), t.noise_shaping_amp = 0, t.noise_shaping_stop = 0, -1 == t.subblock_gain && (t.subblock_gain = 1), t.use_best_huffman = 0, t.full_outer_loop = 0;
              break;
            case 4:
              t.psymodel = 1, 0 == t.noise_shaping && (t.noise_shaping = 1), t.noise_shaping_amp = 0, t.noise_shaping_stop = 0, -1 == t.subblock_gain && (t.subblock_gain = 1), t.use_best_huffman = 1, t.full_outer_loop = 0;
              break;
            case 3:
              t.psymodel = 1, 0 == t.noise_shaping && (t.noise_shaping = 1), t.noise_shaping_amp = 1, -(t.noise_shaping_stop = 1) == t.subblock_gain && (t.subblock_gain = 1), t.use_best_huffman = 1, t.full_outer_loop = 0;
              break;
            case 2:
              t.psymodel = 1, 0 == t.noise_shaping && (t.noise_shaping = 1), 0 == t.substep_shaping && (t.substep_shaping = 2), t.noise_shaping_amp = 1, -(t.noise_shaping_stop = 1) == t.subblock_gain && (t.subblock_gain = 1), t.use_best_huffman = 1, t.full_outer_loop = 0;
              break;
            case 1:
            case 0:
              t.psymodel = 1, 0 == t.noise_shaping && (t.noise_shaping = 1), 0 == t.substep_shaping && (t.substep_shaping = 2), t.noise_shaping_amp = 2, -(t.noise_shaping_stop = 1) == t.subblock_gain && (t.subblock_gain = 1), t.use_best_huffman = 1, t.full_outer_loop = 0;
          }
        }(e), e.athaa_type < 0 ? n.ATH.useAdjust = 3 : n.ATH.useAdjust = e.athaa_type, n.ATH.aaSensitivityP = Math.pow(10, e.athaa_sensitivity / -10), null == e.short_blocks && (e.short_blocks = Se.short_block_allowed), e.short_blocks != Se.short_block_allowed || e.mode != ye.JOINT_STEREO && e.mode != ye.STEREO || (e.short_blocks = Se.short_block_coupled), e.quant_comp < 0 && (e.quant_comp = 1), e.quant_comp_short < 0 && (e.quant_comp_short = 0), e.msfix < 0 && (e.msfix = 0), e.exp_nspsytune = 1 | e.exp_nspsytune, e.internal_flags.nsPsy.attackthre < 0 && (e.internal_flags.nsPsy.attackthre = X.NSATTACKTHRE), e.internal_flags.nsPsy.attackthre_s < 0 && (e.internal_flags.nsPsy.attackthre_s = X.NSATTACKTHRE_S), e.scale < 0 && (e.scale = 1), e.ATHtype < 0 && (e.ATHtype = 4), e.ATHcurve < 0 && (e.ATHcurve = 4), e.athaa_loudapprox < 0 && (e.athaa_loudapprox = 2), e.interChRatio < 0 && (e.interChRatio = 0), null == e.useTemporal && (e.useTemporal = !0), n.slot_lag = n.frac_SpF = 0, e.VBR == Me.vbr_off && (n.slot_lag = n.frac_SpF = 72e3 * (e.version + 1) * e.brate % e.out_samplerate | 0), h.iteration_init(e), v.psymodel_init(e), 0;
      }, this.lame_encode_flush = function (e, t, a, n) {
        var s,
          r,
          i,
          o,
          _ = e.internal_flags,
          l = M([2, 1152]),
          f = 0,
          c = _.mf_samples_to_encode - Ae.POSTDELAY,
          u = L(e);
        if (_.mf_samples_to_encode < 1) return 0;
        for (s = 0, e.in_samplerate != e.out_samplerate && me(), (i = e.framesize - c % e.framesize) < 576 && (i += e.framesize), e.encoder_padding = i, o = (c + i) / e.framesize; 0 < o && 0 <= f;) {
          var h = u - _.mf_size,
            b = e.frameNum;
          h *= e.in_samplerate, 1152 < (h /= e.out_samplerate) && (h = 1152), h < 1 && (h = 1), r = n - s, 0 == n && (r = 0), f = this.lame_encode_buffer(e, l[0], l[1], h, t, a, r), a += f, s += f, o -= b != e.frameNum ? 1 : 0;
        }
        return _.mf_samples_to_encode = 0, f < 0 ? f : (r = n - s, 0 == n && (r = 0), E.flush_bitstream(e), (f = E.copy_buffer(_, t, a, r, 1)) < 0 ? f : (a += f, r = n - (s += f), 0 == n && (r = 0), e.write_id3tag_automatic && me(), s));
      }, this.lame_encode_buffer = function (e, t, a, n, s, r, i) {
        var o,
          _,
          l = e.internal_flags,
          f = [null, null];
        if (l.Class_ID != I) return -3;
        if (0 == n) return 0;
        _ = n, (null == (o = l).in_buffer_0 || o.in_buffer_nsamples < _) && (o.in_buffer_0 = de(_), o.in_buffer_1 = de(_), o.in_buffer_nsamples = _), f[0] = l.in_buffer_0, f[1] = l.in_buffer_1;
        for (var c = 0; c < n; c++) f[0][c] = t[c], 1 < l.channels_in && (f[1][c] = a[c]);
        return function (e, t, a, n, s, r, i) {
          var o,
            _,
            l,
            f,
            c,
            u = e.internal_flags,
            h = 0,
            b = [null, null],
            p = [null, null];
          if (u.Class_ID != I) return -3;
          if (0 == n) return 0;
          if ((c = E.copy_buffer(u, s, r, i, 0)) < 0) return c;
          if (r += c, h += c, p[0] = t, p[1] = a, N.NEQ(e.scale, 0) && N.NEQ(e.scale, 1)) for (_ = 0; _ < n; ++_) p[0][_] *= e.scale, 2 == u.channels_out && (p[1][_] *= e.scale);
          if (N.NEQ(e.scale_left, 0) && N.NEQ(e.scale_left, 1)) for (_ = 0; _ < n; ++_) p[0][_] *= e.scale_left;
          if (N.NEQ(e.scale_right, 0) && N.NEQ(e.scale_right, 1)) for (_ = 0; _ < n; ++_) p[1][_] *= e.scale_right;
          2 == e.num_channels && 1 == u.channels_out && me(), f = L(e), b[0] = u.mfbuf[0], b[1] = u.mfbuf[1];
          for (var m, v, d, g, w, S, M, y = 0; 0 < n;) {
            var A = [null, null],
              k = 0,
              R = 0;
            A[0] = p[0], A[1] = p[1];
            var x = new P();
            if (H(e, b, A, y, n, x), k = x.n_in, R = x.n_out, u.findReplayGain && !u.decode_on_the_fly && T.AnalyzeSamples(u.rgdata, b[0], u.mf_size, b[1], u.mf_size, R, u.channels_out) == O.GAIN_ANALYSIS_ERROR) return -6;
            if (n -= k, y += k, u.channels_out, u.mf_size += R, u.mf_samples_to_encode < 1 && me(), u.mf_samples_to_encode += R, u.mf_size >= f) {
              var B = i - h;
              if (0 == i && (B = 0), m = e, v = b[0], d = b[1], g = s, w = r, S = B, M = C.enc.lame_encode_mp3_frame(m, v, d, g, w, S), m.frameNum++, (o = M) < 0) return o;
              for (r += o, h += o, u.mf_size -= e.framesize, u.mf_samples_to_encode -= e.framesize, l = 0; l < u.channels_out; l++) for (_ = 0; _ < u.mf_size; _++) b[l][_] = b[l][_ + e.framesize];
            }
          }
          return h;
        }(e, f[0], f[1], n, s, r, i);
      };
    }
    F.SFBMAX = 3 * Ae.SBMAX_s, Ae.ENCDELAY = 576, Ae.POSTDELAY = 1152, Ae.FFTOFFSET = 224 + (Ae.MDCTDELAY = 48), Ae.DECDELAY = 528, Ae.SBLIMIT = 32, Ae.CBANDS = 64, Ae.SBPSY_l = 21, Ae.SBPSY_s = 12, Ae.SBMAX_l = 22, Ae.SBMAX_s = 13, Ae.PSFB21 = 6, Ae.PSFB12 = 6, Ae.HBLKSIZE = (Ae.BLKSIZE = 1024) / 2 + 1, Ae.HBLKSIZE_s = (Ae.BLKSIZE_s = 256) / 2 + 1, Ae.NORM_TYPE = 0, Ae.START_TYPE = 1, Ae.SHORT_TYPE = 2, Ae.STOP_TYPE = 3, Ae.MPG_MD_LR_LR = 0, Ae.MPG_MD_LR_I = 1, Ae.MPG_MD_MS_LR = 2, Ae.MPG_MD_MS_I = 3, Ae.fircoef = [-.1039435, -.1892065, 5 * -.0432472, -.155915, 3.898045e-17, .0467745 * 5, .50455, .756825, .187098 * 5], j.MFSIZE = 3456 + Ae.ENCDELAY - Ae.MDCTDELAY, j.MAX_HEADER_BUF = 256, j.MAX_BITS_PER_CHANNEL = 4095, j.MAX_BITS_PER_GRANULE = 7680, j.BPC = 320, F.SFBMAX = 3 * Ae.SBMAX_s, t.Mp3Encoder = function (n, e, t) {
      1 != n && me("fix cc: only supports mono");
      var s = new Y(),
        a = new function () {
          this.setModules = function (e, t) {};
        }(),
        r = new O(),
        i = new N(),
        o = new function () {
          function e(e, t, a, n, s, r, i, o, _, l, f, c, u, h) {
            this.quant_comp = t, this.quant_comp_s = a, this.safejoint = n, this.nsmsfix = s, this.st_lrm = r, this.st_s = i, this.nsbass = o, this.scale = _, this.masking_adj = l, this.ath_lower = f, this.ath_curve = c, this.interch = u, this.sfscale = h;
          }
          var i;
          function n(e, t, a) {
            me();
          }
          this.setModules = function (e) {
            i = e;
          };
          var o = [new e(8, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -30, 11, .0012, 1), new e(16, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -25, 11, .001, 1), new e(24, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -20, 11, .001, 1), new e(32, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -15, 11, .001, 1), new e(40, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -10, 11, 9e-4, 1), new e(48, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -10, 11, 9e-4, 1), new e(56, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -6, 11, 8e-4, 1), new e(64, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, -2, 11, 8e-4, 1), new e(80, 9, 9, 0, 0, 6.6, 145, 0, .95, 0, 0, 8, 7e-4, 1), new e(96, 9, 9, 0, 2.5, 6.6, 145, 0, .95, 0, 1, 5.5, 6e-4, 1), new e(112, 9, 9, 0, 2.25, 6.6, 145, 0, .95, 0, 2, 4.5, 5e-4, 1), new e(128, 9, 9, 0, 1.95, 6.4, 140, 0, .95, 0, 3, 4, 2e-4, 1), new e(160, 9, 9, 1, 1.79, 6, 135, 0, .95, -2, 5, 3.5, 0, 1), new e(192, 9, 9, 1, 1.49, 5.6, 125, 0, .97, -4, 7, 3, 0, 0), new e(224, 9, 9, 1, 1.25, 5.2, 125, 0, .98, -6, 9, 2, 0, 0), new e(256, 9, 9, 1, .97, 5.2, 125, 0, 1, -8, 10, 1, 0, 0), new e(320, 9, 9, 1, .9, 5.2, 125, 0, 1, -10, 12, 0, 0, 0)];
          function s(e, t, a) {
            var n = t,
              s = i.nearestBitrateFullIndex(t);
            if (e.VBR = Me.vbr_abr, e.VBR_mean_bitrate_kbps = n, e.VBR_mean_bitrate_kbps = Math.min(e.VBR_mean_bitrate_kbps, 320), e.VBR_mean_bitrate_kbps = Math.max(e.VBR_mean_bitrate_kbps, 8), e.brate = e.VBR_mean_bitrate_kbps, 320 < e.VBR_mean_bitrate_kbps && (e.disable_reservoir = !0), 0 < o[s].safejoint && (e.exp_nspsytune = 2 | e.exp_nspsytune), 0 < o[s].sfscale && (e.internal_flags.noise_shaping = 2), 0 < Math.abs(o[s].nsbass)) {
              var r = int(4 * o[s].nsbass);
              r < 0 && (r += 64), e.exp_nspsytune = e.exp_nspsytune | r << 2;
            }
            return 0 != a ? e.quant_comp = o[s].quant_comp : 0 < Math.abs(e.quant_comp - -1) || (e.quant_comp = o[s].quant_comp), 0 != a ? e.quant_comp_short = o[s].quant_comp_s : 0 < Math.abs(e.quant_comp_short - -1) || (e.quant_comp_short = o[s].quant_comp_s), 0 != a ? e.msfix = o[s].nsmsfix : 0 < Math.abs(e.msfix - -1) || (e.msfix = o[s].nsmsfix), 0 != a ? e.internal_flags.nsPsy.attackthre = o[s].st_lrm : 0 < Math.abs(e.internal_flags.nsPsy.attackthre - -1) || (e.internal_flags.nsPsy.attackthre = o[s].st_lrm), 0 != a ? e.internal_flags.nsPsy.attackthre_s = o[s].st_s : 0 < Math.abs(e.internal_flags.nsPsy.attackthre_s - -1) || (e.internal_flags.nsPsy.attackthre_s = o[s].st_s), 0 != a ? e.scale = o[s].scale : 0 < Math.abs(e.scale - -1) || (e.scale = o[s].scale), 0 != a ? e.maskingadjust = o[s].masking_adj : 0 < Math.abs(e.maskingadjust - 0) || (e.maskingadjust = o[s].masking_adj), 0 < o[s].masking_adj ? 0 != a ? e.maskingadjust_short = .9 * o[s].masking_adj : 0 < Math.abs(e.maskingadjust_short - 0) || (e.maskingadjust_short = .9 * o[s].masking_adj) : 0 != a ? e.maskingadjust_short = 1.1 * o[s].masking_adj : 0 < Math.abs(e.maskingadjust_short - 0) || (e.maskingadjust_short = 1.1 * o[s].masking_adj), 0 != a ? e.ATHlower = -o[s].ath_lower / 10 : 0 < Math.abs(10 * -e.ATHlower - 0) || (e.ATHlower = -o[s].ath_lower / 10), 0 != a ? e.ATHcurve = o[s].ath_curve : 0 < Math.abs(e.ATHcurve - -1) || (e.ATHcurve = o[s].ath_curve), 0 != a ? e.interChRatio = o[s].interch : 0 < Math.abs(e.interChRatio - -1) || (e.interChRatio = o[s].interch), t;
          }
          this.apply_preset = function (e, t, a) {
            switch (t) {
              case Y.R3MIX:
                t = Y.V3, e.VBR = Me.vbr_mtrh;
                break;
              case Y.MEDIUM:
                t = Y.V4, e.VBR = Me.vbr_rh;
                break;
              case Y.MEDIUM_FAST:
                t = Y.V4, e.VBR = Me.vbr_mtrh;
                break;
              case Y.STANDARD:
                t = Y.V2, e.VBR = Me.vbr_rh;
                break;
              case Y.STANDARD_FAST:
                t = Y.V2, e.VBR = Me.vbr_mtrh;
                break;
              case Y.EXTREME:
                t = Y.V0, e.VBR = Me.vbr_rh;
                break;
              case Y.EXTREME_FAST:
                t = Y.V0, e.VBR = Me.vbr_mtrh;
                break;
              case Y.INSANE:
                return t = 320, e.preset = t, s(e, t, a), e.VBR = Me.vbr_off, t;
            }
            switch (e.preset = t) {
              case Y.V9:
                return n(), t;
              case Y.V8:
                return n(), t;
              case Y.V7:
                return n(), t;
              case Y.V6:
                return n(), t;
              case Y.V5:
                return n(), t;
              case Y.V4:
                return n(), t;
              case Y.V3:
                return n(), t;
              case Y.V2:
                return n(), t;
              case Y.V1:
                return n(), t;
              case Y.V0:
                return n(), t;
            }
            return 8 <= t && t <= 320 ? s(e, t, a) : (e.preset = 0, t);
          };
        }(),
        _ = new T(),
        l = new C(),
        f = new y(),
        c = new function () {
          this.getLameShortVersion = function () {
            return "3.98.4";
          };
        }(),
        u = new function () {
          this.setModules = function (e, t) {};
        }(),
        h = new function () {
          var _;
          this.setModules = function (e) {
            _ = e;
          }, this.ResvFrameBegin = function (e, t) {
            var a,
              n = e.internal_flags,
              s = n.l3_side,
              r = _.getframebits(e);
            t.bits = (r - 8 * n.sideinfo_len) / n.mode_gr;
            var i = 2048 * n.mode_gr - 8;
            320 < e.brate ? me() : (a = 11520, e.strict_ISO && me()), n.ResvMax = a - r, n.ResvMax > i && (n.ResvMax = i), (n.ResvMax < 0 || e.disable_reservoir) && (n.ResvMax = 0);
            var o = t.bits * n.mode_gr + Math.min(n.ResvSize, n.ResvMax);
            return a < o && (o = a), s.resvDrain_pre = 0, null != n.pinfo && me(), o;
          }, this.ResvMaxBits = function (e, t, a, n) {
            var s,
              r = e.internal_flags,
              i = r.ResvSize,
              o = r.ResvMax;
            0 != n && (i += t), 0 != (1 & r.substep_shaping) && (o *= .9), a.bits = t, 9 * o < 10 * i ? (s = i - 9 * o / 10, a.bits += s, r.substep_shaping |= 128) : (s = 0, r.substep_shaping &= 127, e.disable_reservoir || 0 != (1 & r.substep_shaping) || (a.bits -= .1 * t));
            var _ = i < 6 * r.ResvMax / 10 ? i : 6 * r.ResvMax / 10;
            return (_ -= s) < 0 && (_ = 0), _;
          }, this.ResvAdjust = function (e, t) {
            e.ResvSize -= t.part2_3_length + t.part2_length;
          }, this.ResvFrameEnd = function (e, t) {
            var a,
              n = e.l3_side;
            e.ResvSize += t * e.mode_gr;
            var s = 0;
            n.resvDrain_post = 0, (n.resvDrain_pre = 0) != (a = e.ResvSize % 8) && (s += a), 0 < (a = e.ResvSize - s - e.ResvMax) && (s += a);
            var r = Math.min(8 * n.main_data_begin, s) / 8;
            n.resvDrain_pre += 8 * r, s -= 8 * r, e.ResvSize -= 8 * r, n.main_data_begin -= r, n.resvDrain_post += s, e.ResvSize -= s;
          };
        }(),
        b = new A(),
        p = new function () {
          this.setModules = function (e, t, a) {};
        }(),
        m = new function () {}();
      s.setModules(r, i, o, _, l, f, c, u, m), i.setModules(r, m, c, f), u.setModules(i, c), o.setModules(s), l.setModules(i, h, _, b), _.setModules(b, h, s.enc.psy), h.setModules(i), b.setModules(_), f.setModules(s, i, c), a.setModules(p, m), p.setModules(c, u, o);
      var v = s.lame_init();
      v.num_channels = n, v.in_samplerate = e, v.out_samplerate = e, v.brate = t, v.mode = ye.STEREO, v.quality = 3, v.bWriteVbrTag = !1, v.disable_reservoir = !0, v.write_id3tag_automatic = !1, s.lame_init_params(v);
      var d = 1152,
        g = 0 | 1.25 * d + 7200,
        w = S(g);
      this.encodeBuffer = function (e, t) {
        1 == n && (t = e), e.length > d && (d = e.length, w = S(g = 0 | 1.25 * d + 7200));
        var a = s.lame_encode_buffer(v, e, t, e.length, w, 0, g);
        return new Int8Array(w.subarray(0, a));
      }, this.flush = function () {
        var e = s.lame_encode_flush(v, w, 0, g);
        return new Int8Array(w.subarray(0, e));
      };
    };
  }
  t(), e.lamejs = t;
}(("object" == typeof window && window.document ? window : Object).Recorder);
});

/*
录音 Recorder扩展，动态波形显示
https://github.com/xiangyuecn/Recorder
*/
(function (factory) {
  var browser = typeof window == "object" && !!window.document;
  var win = browser ? window : Object; //非浏览器环境，Recorder挂载在Object下面
  var rec = win.Recorder,
    ni = rec.i18n;
  factory(rec, ni, ni.$T, browser);
})(function (Recorder, i18n, $T, isBrowser) {

  var WaveView = function (set) {
    return new fn(set);
  };
  var ViewTxt = "WaveView";
  var fn = function (set) {
    var This = this;
    var o = {
      /*
      elem:"css selector" //自动显示到dom，并以此dom大小为显示大小
      	//或者配置显示大小，手动把waveviewObj.elem显示到别的地方
      ,width:0 //显示宽度
      ,height:0 //显示高度
      
      H5环境以上配置二选一
      
      compatibleCanvas: CanvasObject //提供一个兼容H5的canvas对象，需支持getContext("2d")，支持设置width、height，支持drawImage(canvas,...)
      ,width:0 //canvas显示宽度
      ,height:0 //canvas显示高度
      非H5环境使用以上配置
      */

      scale: 2 //缩放系数，应为正整数，使用2(3? no!)倍宽高进行绘制，避免移动端绘制模糊
      ,
      speed: 9 //移动速度系数，越大越快
      ,
      phase: 21.8 //相位，调整了速度后，调整这个值得到一个看起来舒服的波形
      ,

      fps: 20 //绘制帧率，调整后也需调整phase值
      ,
      keep: true //当停止了input输入时，是否保持波形，设为false停止后将变成一条线
      ,

      lineWidth: 3 //线条基础粗细

      //渐变色配置：[位置，css颜色，...] 位置: 取值0.0-1.0之间
      ,
      linear1: [0, "rgba(150,96,238,1)", 0.2, "rgba(170,79,249,1)", 1, "rgba(53,199,253,1)"] //线条渐变色1，从左到右
      ,
      linear2: [0, "rgba(209,130,255,0.6)", 1, "rgba(53,199,255,0.6)"] //线条渐变色2，从左到右
      ,
      linearBg: [0, "rgba(255,255,255,0.2)", 1, "rgba(54,197,252,0.2)"] //背景渐变色，从上到下
    };
    for (var k in set) {
      o[k] = set[k];
    }
    This.set = set = o;
    var cCanvas = "compatibleCanvas";
    if (set[cCanvas]) {
      var canvas = This.canvas = set[cCanvas];
    } else {
      if (!isBrowser) throw new Error($T.G("NonBrowser-1", [ViewTxt]));
      var elem = set.elem;
      if (elem) {
        if (typeof elem == "string") {
          elem = document.querySelector(elem);
        } else if (elem.length) {
          elem = elem[0];
        }
      }
      if (elem) {
        set.width = elem.offsetWidth;
        set.height = elem.offsetHeight;
      }
      var thisElem = This.elem = document.createElement("div");
      thisElem.style.fontSize = 0;
      thisElem.innerHTML = '<canvas style="width:100%;height:100%;"/>';
      var canvas = This.canvas = thisElem.querySelector("canvas");
      if (elem) {
        elem.innerHTML = "";
        elem.appendChild(thisElem);
      }
    }
    var scale = set.scale;
    var width = set.width * scale;
    var height = set.height * scale;
    if (!width || !height) {
      throw new Error($T.G("IllegalArgs-1", [ViewTxt + " width=0 height=0"]));
    }
    canvas.width = width;
    canvas.height = height;
    var ctx = This.ctx = canvas.getContext("2d");
    This.linear1 = This.genLinear(ctx, width, set.linear1);
    This.linear2 = This.genLinear(ctx, width, set.linear2);
    This.linearBg = This.genLinear(ctx, height, set.linearBg, true);
    This._phase = 0;
  };
  fn.prototype = WaveView.prototype = {
    genLinear: function (ctx, size, colors, top) {
      var rtv = ctx.createLinearGradient(0, 0, top ? 0 : size, top ? size : 0);
      for (var i = 0; i < colors.length;) {
        rtv.addColorStop(colors[i++], colors[i++]);
      }
      return rtv;
    },
    genPath: function (frequency, amplitude, phase) {
      //曲线生成算法参考 https://github.com/HaloMartin/MCVoiceWave/blob/f6dc28975fbe0f7fc6cc4dbc2e61b0aa5574e9bc/MCVoiceWave/MCVoiceWaveView.m#L268
      var rtv = [];
      var This = this,
        set = This.set;
      var scale = set.scale;
      var width = set.width * scale;
      var maxAmplitude = set.height * scale / 2;
      for (var x = 0; x <= width; x += scale) {
        var scaling = (1 + Math.cos(Math.PI + x / width * 2 * Math.PI)) / 2;
        var y = scaling * maxAmplitude * amplitude * Math.sin(2 * Math.PI * (x / width) * frequency + phase) + maxAmplitude;
        rtv.push(y);
      }
      return rtv;
    },
    input: function (pcmData, powerLevel, sampleRate) {
      var This = this;
      This.sampleRate = sampleRate;
      This.pcmData = pcmData;
      This.pcmPos = 0;
      This.inputTime = Date.now();
      This.schedule();
    },
    schedule: function () {
      var This = this,
        set = This.set;
      var interval = Math.floor(1000 / set.fps);
      if (!This.timer) {
        This.timer = setInterval(function () {
          This.schedule();
        }, interval);
      }
      var now = Date.now();
      var drawTime = This.drawTime || 0;
      if (now - drawTime < interval) {
        //没到间隔时间，不绘制
        return;
      }
      This.drawTime = now;

      //切分当前需要的绘制数据
      var bufferSize = This.sampleRate / set.fps;
      var pcm = This.pcmData;
      var pos = This.pcmPos;
      var len = Math.max(0, Math.min(bufferSize, pcm.length - pos));
      var sum = 0;
      for (var i = 0; i < len; i++, pos++) {
        sum += Math.abs(pcm[pos]);
      }
      This.pcmPos = pos;

      //推入绘制
      if (len || !set.keep) {
        This.draw(Recorder.PowerLevel(sum, len));
      }
      if (!len && now - This.inputTime > 1300) {
        //超时没有输入，干掉定时器
        clearInterval(This.timer);
        This.timer = 0;
      }
    },
    draw: function (powerLevel) {
      var This = this,
        set = This.set;
      var ctx = This.ctx;
      var scale = set.scale;
      var width = set.width * scale;
      var height = set.height * scale;
      var speedx = set.speed / set.fps;
      var phase = This._phase -= speedx; //位移速度
      var phase2 = phase + speedx * set.phase;
      var amplitude = powerLevel / 100;
      var path1 = This.genPath(2, amplitude, phase);
      var path2 = This.genPath(1.8, amplitude, phase2);

      //开始绘制图形
      ctx.clearRect(0, 0, width, height);

      //绘制包围背景
      ctx.beginPath();
      for (var i = 0, x = 0; x <= width; i++, x += scale) {
        if (x == 0) {
          ctx.moveTo(x, path1[i]);
        } else {
          ctx.lineTo(x, path1[i]);
        }
      }
      i--;
      for (var x = width - 1; x >= 0; i--, x -= scale) {
        ctx.lineTo(x, path2[i]);
      }
      ctx.closePath();
      ctx.fillStyle = This.linearBg;
      ctx.fill();

      //绘制线
      This.drawPath(path2, This.linear2);
      This.drawPath(path1, This.linear1);
    },
    drawPath: function (path, linear) {
      var This = this,
        set = This.set;
      var ctx = This.ctx;
      var scale = set.scale;
      var width = set.width * scale;
      ctx.beginPath();
      for (var i = 0, x = 0; x <= width; i++, x += scale) {
        if (x == 0) {
          ctx.moveTo(x, path[i]);
        } else {
          ctx.lineTo(x, path[i]);
        }
      }
      ctx.lineWidth = set.lineWidth * scale;
      ctx.strokeStyle = linear;
      ctx.stroke();
    }
  };
  Recorder[ViewTxt] = WaveView;
});

/*
录音 Recorder扩展，频率直方图显示
使用本扩展需要引入src/extensions/lib.fft.js支持，直方图特意优化主要显示0-5khz语音部分（线性），其他高频显示区域较小，不适合用来展示音乐频谱，可通过配置fullFreq来恢复成完整的线性频谱，或自行修改源码修改成倍频程频谱（伯德图、对数频谱）；本可视化插件可以移植到其他语言环境，如需定制可联系作者

https://github.com/xiangyuecn/Recorder

本扩展核心算法主要参考了Java开源库jmp123 版本0.3 的代码：
https://www.iteye.com/topic/851459
https://sourceforge.net/projects/jmp123/files/
*/
(function (factory) {
  var browser = typeof window == "object" && !!window.document;
  var win = browser ? window : Object; //非浏览器环境，Recorder挂载在Object下面
  var rec = win.Recorder,
    ni = rec.i18n;
  factory(rec, ni, ni.$T, browser);
})(function (Recorder, i18n, $T, isBrowser) {

  var FrequencyHistogramView = function (set) {
    return new fn(set);
  };
  var ViewTxt = "FrequencyHistogramView";
  var fn = function (set) {
    var This = this;
    var o = {
      /*
      elem:"css selector" //自动显示到dom，并以此dom大小为显示大小
      	//或者配置显示大小，手动把frequencyObj.elem显示到别的地方
      ,width:0 //显示宽度
      ,height:0 //显示高度
      
      H5环境以上配置二选一
      
      compatibleCanvas: CanvasObject //提供一个兼容H5的canvas对象，需支持getContext("2d")，支持设置width、height，支持drawImage(canvas,...)
      ,width:0 //canvas显示宽度
      ,height:0 //canvas显示高度
      非H5环境使用以上配置
      */

      scale: 2 //缩放系数，应为正整数，使用2(3? no!)倍宽高进行绘制，避免移动端绘制模糊
      ,

      fps: 20 //绘制帧率，不可过高
      ,

      lineCount: 30 //直方图柱子数量，数量的多少对性能影响不大，密集运算集中在FFT算法中
      ,
      widthRatio: 0.6 //柱子线条宽度占比，为所有柱子占用整个视图宽度的比例，剩下的空白区域均匀插入柱子中间；默认值也基本相当于一根柱子占0.6，一根空白占0.4；设为1不留空白，当视图不足容下所有柱子时也不留空白
      ,
      spaceWidth: 0 //柱子间空白固定基础宽度，柱子宽度自适应，当不为0时widthRatio无效，当视图不足容下所有柱子时将不会留空白，允许为负数，让柱子发生重叠
      ,
      minHeight: 0 //柱子保留基础高度，position不为±1时应该保留点高度
      ,
      position: -1 //绘制位置，取值-1到1，-1为最底下，0为中间，1为最顶上，小数为百分比
      ,
      mirrorEnable: false //是否启用镜像，如果启用，视图宽度会分成左右两块，右边这块进行绘制，左边这块进行镜像（以中间这根柱子的中心进行镜像）
      ,

      stripeEnable: true //是否启用柱子顶上的峰值小横条，position不是-1时应当关闭，否则会很丑
      ,
      stripeHeight: 3 //峰值小横条基础高度
      ,
      stripeMargin: 6 //峰值小横条和柱子保持的基础距离
      ,

      fallDuration: 1000 //柱子从最顶上下降到最底部最长时间ms
      ,
      stripeFallDuration: 3500 //峰值小横条从最顶上下降到底部最长时间ms

      //柱子颜色配置：[位置，css颜色，...] 位置: 取值0.0-1.0之间
      ,
      linear: [0, "rgba(0,187,17,1)", 0.5, "rgba(255,215,0,1)", 1, "rgba(255,102,0,1)"]
      //峰值小横条渐变颜色配置，取值格式和linear一致，留空为柱子的渐变颜色
      ,
      stripeLinear: null,
      shadowBlur: 0 //柱子阴影基础大小，设为0不显示阴影，如果柱子数量太多时请勿开启，非常影响性能
      ,
      shadowColor: "#bbb" //柱子阴影颜色
      ,
      stripeShadowBlur: -1 //峰值小横条阴影基础大小，设为0不显示阴影，-1为柱子的大小，如果柱子数量太多时请勿开启，非常影响性能
      ,
      stripeShadowColor: "" //峰值小横条阴影颜色，留空为柱子的阴影颜色
      ,

      fullFreq: false //是否要绘制所有频率；默认false主要绘制5khz以下的频率，高频部分占比很少，此时不同的采样率对频谱显示几乎没有影响；设为true后不同采样率下显示的频谱是不一样的，因为 最大频率=采样率/2 会有差异
      //当发生绘制时会回调此方法，参数为当前绘制的频率数据和采样率，可实现多个直方图同时绘制，只消耗一个input输入和计算时间
      ,
      onDraw: function (frequencyData, sampleRate) {}
    };
    for (var k in set) {
      o[k] = set[k];
    }
    This.set = set = o;
    var cCanvas = "compatibleCanvas";
    if (set[cCanvas]) {
      var canvas = This.canvas = set[cCanvas];
    } else {
      if (!isBrowser) throw new Error($T.G("NonBrowser-1", [ViewTxt]));
      var elem = set.elem;
      if (elem) {
        if (typeof elem == "string") {
          elem = document.querySelector(elem);
        } else if (elem.length) {
          elem = elem[0];
        }
      }
      if (elem) {
        set.width = elem.offsetWidth;
        set.height = elem.offsetHeight;
      }
      var thisElem = This.elem = document.createElement("div");
      thisElem.style.fontSize = 0;
      thisElem.innerHTML = '<canvas style="width:100%;height:100%;"/>';
      var canvas = This.canvas = thisElem.querySelector("canvas");
      if (elem) {
        elem.innerHTML = "";
        elem.appendChild(thisElem);
      }
    }
    var scale = set.scale;
    var width = set.width * scale;
    var height = set.height * scale;
    if (!width || !height) {
      throw new Error($T.G("IllegalArgs-1", [ViewTxt + " width=0 height=0"]));
    }
    canvas.width = width;
    canvas.height = height;
    This.ctx = canvas.getContext("2d");
    if (!Recorder.LibFFT) {
      throw new Error($T.G("NeedImport-2", [ViewTxt, "src/extensions/lib.fft.js"]));
    }
    This.fft = Recorder.LibFFT(1024);

    //柱子所在高度
    This.lastH = [];
    //峰值小横条所在高度
    This.stripesH = [];
  };
  fn.prototype = FrequencyHistogramView.prototype = {
    genLinear: function (ctx, colors, from, to) {
      var rtv = ctx.createLinearGradient(0, from, 0, to);
      for (var i = 0; i < colors.length;) {
        rtv.addColorStop(colors[i++], colors[i++]);
      }
      return rtv;
    },
    input: function (pcmData, powerLevel, sampleRate) {
      var This = this;
      This.sampleRate = sampleRate;
      This.pcmData = pcmData;
      This.pcmPos = 0;
      This.inputTime = Date.now();
      This.schedule();
    },
    schedule: function () {
      var This = this,
        set = This.set;
      var interval = Math.floor(1000 / set.fps);
      if (!This.timer) {
        This.timer = setInterval(function () {
          This.schedule();
        }, interval);
      }
      var now = Date.now();
      var drawTime = This.drawTime || 0;
      if (now - This.inputTime > set.stripeFallDuration * 1.3) {
        //超时没有输入，顶部横条已全部落下，干掉定时器
        clearInterval(This.timer);
        This.timer = 0;
        This.lastH = []; //重置高度再绘制一次，避免定时不准没到底就停了
        This.stripesH = [];
        This.draw(null, This.sampleRate);
        return;
      }
      if (now - drawTime < interval) {
        //没到间隔时间，不绘制
        return;
      }
      This.drawTime = now;

      //调用FFT计算频率数据
      var bufferSize = This.fft.bufferSize;
      var pcm = This.pcmData;
      var pos = This.pcmPos;
      var arr = new Int16Array(bufferSize);
      for (var i = 0; i < bufferSize && pos < pcm.length; i++, pos++) {
        arr[i] = pcm[pos];
      }
      This.pcmPos = pos;
      var frequencyData = This.fft.transform(arr);

      //推入绘制
      This.draw(frequencyData, This.sampleRate);
    },
    draw: function (frequencyData, sampleRate) {
      var This = this,
        set = This.set;
      var ctx = This.ctx;
      var scale = set.scale;
      var width = set.width * scale;
      var height = set.height * scale;
      var lineCount = set.lineCount;
      var bufferSize = This.fft.bufferSize;

      //计算高度位置
      var position = set.position;
      var posAbs = Math.abs(set.position);
      var originY = position == 1 ? 0 : height; //y轴原点
      var heightY = height; //最高的一边高度
      if (posAbs < 1) {
        heightY = heightY / 2;
        originY = heightY;
        heightY = Math.floor(heightY * (1 + posAbs));
        originY = Math.floor(position > 0 ? originY * (1 - posAbs) : originY * (1 + posAbs));
      }
      var lastH = This.lastH;
      var stripesH = This.stripesH;
      var speed = Math.ceil(heightY / (set.fallDuration / (1000 / set.fps)));
      var stripeSpeed = Math.ceil(heightY / (set.stripeFallDuration / (1000 / set.fps)));
      var stripeMargin = set.stripeMargin * scale;
      var Y0 = 1 << (Math.round(Math.log(bufferSize) / Math.log(2) + 3) << 1);
      var logY0 = Math.log(Y0) / Math.log(10);
      var dBmax = 20 * Math.log(0x7fff) / Math.log(10);
      var fftSize = bufferSize / 2,
        fftSize5k = fftSize;
      if (!set.fullFreq) {
        //非绘制所有频率时，计算5khz所在位置，8000采样率及以下最高只有4khz
        fftSize5k = Math.min(fftSize, Math.floor(fftSize * 5000 / (sampleRate / 2)));
      }
      var isFullFreq = fftSize5k == fftSize;
      var line80 = isFullFreq ? lineCount : Math.round(lineCount * 0.8); //80%的柱子位置
      var fftSizeStep1 = fftSize5k / line80;
      var fftSizeStep2 = isFullFreq ? 0 : (fftSize - fftSize5k) / (lineCount - line80);
      var fftIdx = 0;
      for (var i = 0; i < lineCount; i++) {
        // !fullFreq 时不采用jmp123的非线性划分频段，录音语音并不适用于音乐的频率，应当弱化高频部分
        //80%关注0-5khz主要人声部分 20%关注剩下的高频，这样不管什么采样率都能做到大部分频率显示一致。
        var start = Math.ceil(fftIdx);
        if (i < line80) {
          //5khz以下
          fftIdx += fftSizeStep1;
        } else {
          //5khz以上
          fftIdx += fftSizeStep2;
        }
        var end = Math.ceil(fftIdx);
        if (end == start) end++;
        end = Math.min(end, fftSize);

        //参考AudioGUI.java .drawHistogram方法

        //查找当前频段的最大"幅值"
        var maxAmp = 0;
        if (frequencyData) {
          for (var j = start; j < end; j++) {
            maxAmp = Math.max(maxAmp, Math.abs(frequencyData[j]));
          }
        }

        //计算音量
        var dB = maxAmp > Y0 ? Math.floor((Math.log(maxAmp) / Math.log(10) - logY0) * 17) : 0;
        var h = heightY * Math.min(dB / dBmax, 1);

        //使柱子匀速下降
        lastH[i] = (lastH[i] || 0) - speed;
        if (h < lastH[i]) {
          h = lastH[i];
        }
        if (h < 0) {
          h = 0;
        }
        lastH[i] = h;
        var shi = stripesH[i] || 0;
        if (h && h + stripeMargin > shi) {
          stripesH[i] = h + stripeMargin;
        } else {
          //使峰值小横条匀速度下落
          var sh = shi - stripeSpeed;
          if (sh < 0) {
            sh = 0;
          }
          stripesH[i] = sh;
        }
      }

      //开始绘制图形
      ctx.clearRect(0, 0, width, height);
      var linear1 = This.genLinear(ctx, set.linear, originY, originY - heightY); //上半部分的填充
      var stripeLinear1 = set.stripeLinear && This.genLinear(ctx, set.stripeLinear, originY, originY - heightY) || linear1; //上半部分的峰值小横条填充

      var linear2 = This.genLinear(ctx, set.linear, originY, originY + heightY); //下半部分的填充
      var stripeLinear2 = set.stripeLinear && This.genLinear(ctx, set.stripeLinear, originY, originY + heightY) || linear2; //上半部分的峰值小横条填充

      //计算柱子间距
      var mirrorEnable = set.mirrorEnable;
      var mirrorCount = mirrorEnable ? lineCount * 2 - 1 : lineCount; //镜像柱子数量翻一倍-1根

      var widthRatio = set.widthRatio;
      var spaceWidth = set.spaceWidth * scale;
      if (spaceWidth != 0) {
        widthRatio = (width - spaceWidth * (mirrorCount + 1)) / width;
      }
      for (var i = 0; i < 2; i++) {
        var lineFloat = Math.max(1 * scale, width * widthRatio / mirrorCount); //柱子宽度至少1个单位
        var lineWN = Math.floor(lineFloat),
          lineWF = lineFloat - lineWN; //提取出小数部分
        var spaceFloat = (width - mirrorCount * lineFloat) / (mirrorCount + 1); //均匀间隔，首尾都留空，可能为负数，柱子将发生重叠
        if (spaceFloat > 0 && spaceFloat < 1) {
          widthRatio = 1;
          spaceFloat = 0; //不够一个像素，丢弃不绘制间隔，重新计算
        } else break;
      }

      //绘制
      var minHeight = set.minHeight * scale;
      var XFloat = mirrorEnable ? (width - lineWN) / 2 - spaceFloat : 0; //镜像时，中间柱子位于正中心
      for (var iMirror = 0; iMirror < 2; iMirror++) {
        if (iMirror) {
          ctx.save();
          ctx.scale(-1, 1);
        }
        var xMirror = iMirror ? width : 0; //绘制镜像部分，不用drawImage(canvas)进行镜像绘制，提升兼容性（iOS微信小程序bug https://developers.weixin.qq.com/community/develop/doc/000aaca2148dc8a235a0fb8c66b000）

        //绘制柱子
        ctx.shadowBlur = set.shadowBlur * scale;
        ctx.shadowColor = set.shadowColor;
        for (var i = 0, xFloat = XFloat, wFloat = 0, x, y, w, h; i < lineCount; i++) {
          xFloat += spaceFloat;
          x = Math.floor(xFloat) - xMirror;
          w = lineWN;
          wFloat += lineWF;
          if (wFloat >= 1) {
            w++;
            wFloat--;
          } //小数凑够1像素
          h = Math.max(lastH[i], minHeight);

          //绘制上半部分
          if (originY != 0) {
            y = originY - h;
            ctx.fillStyle = linear1;
            ctx.fillRect(x, y, w, h);
          }
          //绘制下半部分
          if (originY != height) {
            ctx.fillStyle = linear2;
            ctx.fillRect(x, originY, w, h);
          }
          xFloat += w;
        }

        //绘制柱子顶上峰值小横条
        if (set.stripeEnable) {
          var stripeShadowBlur = set.stripeShadowBlur;
          ctx.shadowBlur = (stripeShadowBlur == -1 ? set.shadowBlur : stripeShadowBlur) * scale;
          ctx.shadowColor = set.stripeShadowColor || set.shadowColor;
          var stripeHeight = set.stripeHeight * scale;
          for (var i = 0, xFloat = XFloat, wFloat = 0, x, y, w, h; i < lineCount; i++) {
            xFloat += spaceFloat;
            x = Math.floor(xFloat) - xMirror;
            w = lineWN;
            wFloat += lineWF;
            if (wFloat >= 1) {
              w++;
              wFloat--;
            } //小数凑够1像素
            h = stripesH[i];

            //绘制上半部分
            if (originY != 0) {
              y = originY - h - stripeHeight;
              if (y < 0) {
                y = 0;
              }
              ctx.fillStyle = stripeLinear1;
              ctx.fillRect(x, y, w, stripeHeight);
            }
            //绘制下半部分
            if (originY != height) {
              y = originY + h;
              if (y + stripeHeight > height) {
                y = height - stripeHeight;
              }
              ctx.fillStyle = stripeLinear2;
              ctx.fillRect(x, y, w, stripeHeight);
            }
            xFloat += w;
          }
        }
        if (iMirror) {
          ctx.restore();
        }
        if (!mirrorEnable) break;
      }
      if (frequencyData) {
        set.onDraw(frequencyData, sampleRate);
      }
    }
  };
  Recorder[ViewTxt] = FrequencyHistogramView;
});

/*
时域转频域，快速傅里叶变换(FFT)
https://github.com/xiangyuecn/Recorder

var fft=Recorder.LibFFT(bufferSize)
	bufferSize取值2的n次方

fft.bufferSize 实际采用的bufferSize
fft.transform(inBuffer)
	inBuffer:[Int16,...] 数组长度必须是bufferSize
	返回[Float64(Long),...]，长度为bufferSize/2
*/
(function (factory) {
  var browser = typeof window == "object" && !!window.document;
  var win = browser ? window : Object; //非浏览器环境，Recorder挂载在Object下面
  var rec = win.Recorder,
    ni = rec.i18n;
  factory(rec, ni, ni.$T, browser);
})(function (Recorder, i18n, $T, isBrowser) {

  /*
  从FFT.java 移植，Java开源库：jmp123 版本0.3
  https://www.iteye.com/topic/851459
  https://sourceforge.net/projects/jmp123/files/
  */
  Recorder.LibFFT = function (bufferSize) {
    var FFT_N_LOG, FFT_N, MINY;
    var real, imag, sintable, costable;
    var bitReverse;
    var FFT_Fn = function (bufferSize) {
      //bufferSize只能取值2的n次方
      FFT_N_LOG = Math.round(Math.log(bufferSize) / Math.log(2));
      FFT_N = 1 << FFT_N_LOG;
      MINY = (FFT_N << 2) * Math.sqrt(2);
      real = [];
      imag = [];
      sintable = [0];
      costable = [0];
      bitReverse = [];
      var i, j, k, reve;
      for (i = 0; i < FFT_N; i++) {
        k = i;
        for (j = 0, reve = 0; j != FFT_N_LOG; j++) {
          reve <<= 1;
          reve |= k & 1;
          k >>>= 1;
        }
        bitReverse[i] = reve;
      }
      var theta,
        dt = 2 * Math.PI / FFT_N;
      for (i = (FFT_N >> 1) - 1; i > 0; i--) {
        theta = i * dt;
        costable[i] = Math.cos(theta);
        sintable[i] = Math.sin(theta);
      }
    };

    /*
    用于频谱显示的快速傅里叶变换 
       inBuffer 输入FFT_N个实数，返回 FFT_N/2个输出值(复数模的平方)。 
    */
    var getModulus = function (inBuffer) {
      var i,
        j,
        k,
        ir,
        j0 = 1,
        idx = FFT_N_LOG - 1;
      var cosv, sinv, tmpr, tmpi;
      for (i = 0; i != FFT_N; i++) {
        real[i] = inBuffer[bitReverse[i]];
        imag[i] = 0;
      }
      for (i = FFT_N_LOG; i != 0; i--) {
        for (j = 0; j != j0; j++) {
          cosv = costable[j << idx];
          sinv = sintable[j << idx];
          for (k = j; k < FFT_N; k += j0 << 1) {
            ir = k + j0;
            tmpr = cosv * real[ir] - sinv * imag[ir];
            tmpi = cosv * imag[ir] + sinv * real[ir];
            real[ir] = real[k] - tmpr;
            imag[ir] = imag[k] - tmpi;
            real[k] += tmpr;
            imag[k] += tmpi;
          }
        }
        j0 <<= 1;
        idx--;
      }
      j = FFT_N >> 1;
      var outBuffer = new Float64Array(j);
      /*
       * 输出模的平方:
       * for(i = 1; i <= j; i++)
       * 	inBuffer[i-1] = real[i] * real[i] +  imag[i] * imag[i];
       * 
       * 如果FFT只用于频谱显示,可以"淘汰"幅值较小的而减少浮点乘法运算. MINY的值
       * 和Spectrum.Y0,Spectrum.logY0对应.
       */
      sinv = MINY;
      cosv = -MINY;
      for (i = j; i != 0; i--) {
        tmpr = real[i];
        tmpi = imag[i];
        if (tmpr > cosv && tmpr < sinv && tmpi > cosv && tmpi < sinv) outBuffer[i - 1] = 0;else outBuffer[i - 1] = Math.round(tmpr * tmpr + tmpi * tmpi);
      }
      return outBuffer;
    };
    FFT_Fn(bufferSize);
    return {
      transform: getModulus,
      bufferSize: FFT_N
    };
  };
});

var playIcon$6 = "data:image/png;base64,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";

var pauseIcon$5 = "data:image/png;base64,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";

var audioIcon$4 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAF4AAABsCAIAAABsApKfAAAAAXNSR0IArs4c6QAADPRJREFUeNrtnelfU8kShs9/eMcZF2RHkEVFBEFAr4IbKJsDiCi44Qw6Oo4rggQQBAHFFS4I7iACbghqTp/u5st9O4kg6zkh3QnG4dcfkpCQnIfq6uqqtyvahF/+UIN//cI/jfI3g/xxN7vTxm7UsQYb7+6csNst/g3Nf3AYBv/8mX94PzHQz3t72L07tKmB1deKUXfdNUCnr3eCMX9HwzknujCNoSH+/Bnv6mQd7bSxfgrEnKPtFrdmONqPh0PX+fv3vL+f93Szex2srYWZ4piB5ssX/0IDKB8/sCdPaMdt1twofId1HN+Pdj9DAy5vR1hrC5zFIon4Kxo4V9ba7CkU/0PDCWGdD+Vw8TerGR50z9H+LGgY432PpXHxKzREZ50P/kUz14/dzu7f/RfNXD+6/jOiEXHthw98ZIi/HuAvn/MnvWLgBu7iQfwK26KvX9kjP55QnIsdnUH4u7fs4X1qqzbOVRpHikleNtm9nexMJ9u3kK2byZYEMXADd/EgfpWXZZQfojVV07aIfoIG/3ZE9/c76D9nScE+kp6ob4zWo0P08FV66ArzEfybHhNGTx6VEAQvHTTID9BXz41L50lmur4uQl8TaImFf6MRZnKnzSgv0RPj9IiAxRDxPzR8eAjZI6Ngnx4T6hERf0KD7R+cK9mzQ18bIgeKH6DB24jps3+XcK4SoahDg1TWV8VoOKXs1QvjcLG+bo18KMrQ8I52hBEK0QA8srBk22ary7BqNHgCducLDlpXi/QgH3gl4ixFaOBujdMVenSoQihuoamvpefPGEcOGqVFsOIZgx47TGEsbc2s+39IKlvk4j4aJCKf9uk5uz1dld1DU26C5ka98XuOeHLAf/TVv8wcawKN40f4h3fuWoDmHpe+x2RHqjegTKEJpSfKzNEU5enhAXrI8jn/jj18FRYK/uKZdZNxBw1SSj1dJC3Be1ycaKJD6XGgqfMEjaATugK7M97dZbE+ZxkNY+zBXZK8watcptAc9hyNc+AS2MN78tCAy70OPWm9t7k40awNgR+VhUbQSU1gT3ploGGM3r+rb97gAy6TaMpLTQpy7qDBMDLT2ZtBz9DA7z7u1tMSfcPFhSaYlh+Si0YPW6UX5vH37xaPBi82cvf4jMsUmhLJaDAiVtGKo3x8fDFokHmklSf1iNU+RhMVTMsUoMHAVK2tnqDUTTRwMTcbEG75kgtGyG96ZBAiWiVo4JJT4hGpuYeGvXxOtqf4mMskmtIiRWj0sJVIy87ndLQ58wxG2UG1+0araJbrkYEK0WBEBiJvDYGbBTScI8utb4j0PZcpNIUK0WCkxLPHPbP3ENrsVQk2tiS4ONFgc3jod6Vo7GErjYP5EL4thAbZKXrDhkVhKaFZbZQcUGs1oBMdgmWHMzovGvrunbF3h7cvHis0RtCveuAyMXDD+Qh+hRERQA8qRyNC5L0ZuPx50DBGWm8hxPIeEYDAjdhwkhBDtmwk/00RAzcSYvCg+BWyLbD24gIvoMGF4/K/35dPoeHjY4ZqL+M0BECJDCQpG419uxDp0soKeu40vfg3q7qEgRviLh4sKyEw4ZR441ChN9DAcLCQj4/NQsO52EaqS4BPWkpkEMlIo6eO08sX2PVrThG4GE7ts3M4H7lRh2o3vXSeAtnCiWFJaHD59H7H5FKlTWo1RL0xbKXC4C18Far9IlMLIs7rNy0ACFI2cy2ALDRIBh4rhUx7Ghr+8gVJWqdwKxQRYOzfza5emqmMd3PQuhoMVWgc2Rw+/GYaGnrlAkxdCResOFFBpGC/MBaPy0mq0eixYbSpfgoNCvhGVqYqLrHhKINgjytRIzMTkEQ0IvwrmCDEhUbsDOLXKsrRGUdLqWeTyKtoMKfSNvH+ly40xtWL8vMy4IIYvzhfptZs5qhVgUZUvm7eEGi4QcjhIhUmA+EErb0m117UrlCTAc6fxycY1fjQG6jm5IcwYH/mlDe4KEBDsjJwWkSD/F/o6+SiCVxGsne64pcfEA2AAIvGmpvs4QGSvUzcGnq6wktcFKABEGDReHWVfJPZuZVWX7WKxoZ13UZtNbT6Cl4lbiACxoNWV6vrVLrVhK4AFg3/XsleBqEBUpYWz6LYalhLE3/2FHUPccYW4/M4w0HKlkY2O7RbyGryRcZWHhpg0bCvlRzjxUfTv/60EvjCGaEECG3xzOQj7kJw3N3pM1+DRepQoUYglpE7m7CxrjWfDljXBZcFdAvQZjx7Iny5OZo6h9XIRAMsmsyV25F2ENvIxgZTB8FuNkzYv5qd+CG8vdUSmpIDSJXKRLN7u4Z/skw02NcX5sLCTS6m6hLvt6Sp44OvLeRr6mhpsdgeI/UhC01GmkbSk2QmZaKCqGmxscGGHBX2tFYVydeumKmy6nDKQ0iWg+WhSU/SdImCIqdSyFRfBjR3b+PEjyU0OCp2p90cDWqKUUES0QCLRpLjpVbvg5CCMLUa1vVogTr8tB+ok7semaBprDfy97lmtCyrSY7XyNYkyb6mKNccDdSqs0pic1vN6CiCQJEGXRhNzl5XTl4Wmq1Jst0wVqicPSbxHmS+Vy9CfGzJDY8MU2fadCE0DUZ2ph60TCYa4Yb3bJcb1wg5mGlQA0NouzWhm3UEoQZUiSJiNgsFxKkrVLXkXQiwaJJ1V0G/koRYFJJMo2HQgUWYmMybQfPoEU793Gl9U6wIxOVdCLBoBOcv5O6h4G6wh7KyUWis58PD83lf/uqFyHKamQzeSOx1pG6ghNUcLtbo2UrZO+9fyK5ttMbCzhuXjTZEvT3848cpvwMVy8gwu93Kqi6yevNCFaIe4S6lziaxvTxbqcFi5WeF10fRv89YLK0IiHDb7W2ioxVGe6vIilvMaSCb8ccJIayTajICTW21RttaRLAkOwFKcve6UWABRDwZUe+1y+JGg83aC2tpdZXYA0qM9JwDMX1bi8agDJaumBaGEwkhmJs1OTdrMg11UA3I3R98C4XjWW+PJmRY+3aqUERA5yBOr9epSYM6giNxzEb2VHIsT3sRampYC1CiV6KLwFJVnE/rbSrQYLaKiqvUCHiygEnPVGA1ECU6OMJFHlE3322GGMeOLL7l1wI7A+eCLX0qYcRFoCnXt8Lug7v6RgWFXVd1IYKeKLfsWS35bONkuViVVHBxpCP4QP83OcDoqCoJn9PgITfKy6JVlz1VSuDl1VdFGhhLqgIX45IDlJc4swKaS5J17YpC4adDs4htjliznIIi952u8C//nDOcOz419uIQkYSjwjFdejTQr6eqPDzoXLMS44RjhlTNKUmzaCnYJV2+gBfi5SKRqMheXLmIzfzt8HTBmmEYJ44oF99jB4hjAemJUEmLLSiiO3joets0qZZL0efQ8l2vNs5W4smiuwvEdFI3kHML1irKRfFnhswR3brUyhy/Fzsi+x8fDYeH7ByWMKH3hPATpUsM3KiswINGbhbBKTUIf/DkYLXGMilzRIelWTJHhziWFOV6T0kNE8CAnSKcxXKD/wooYOAG7uJBYSYOhbUXoDhnU1HuXOJYpzNubfaSpHpOtfnkmFSbe/OT4Lhea/P3iUdtRmWDoKfIEjmg4N2BCxdtnOc92YJNQ1O97w/PeX/EheP4xowix6xDP58+kQM5eujyn4gLwryyEv7ls4WjYjjwvinmJ5pKkFHPdQJTm7OJrfHHMXEY+mdAg5NQNVcmGLV6LBUpa0NifWopT6WiPD760Z3DzJhWD+/pm9f7+VTakYq+mu53B6DUQB5nbbDfotkUi8arC2ifFmycMDZGoUBQdxLId8MeFYwU/ZxnmK12IkH9kOTs8Tc6EQEELaI+j3vWpAVO59lTkpHuP1zCA0hxvugnJqG1D+h0PSKpCf7ietPY636ZvbLQJ5j8+Ms52ZaMC5HfYQ1KZ6Mg+0f1O0ihZWWy+fuOeNqXjw8NksI8LzXlk5pwQFNs9AVX1pfPaTtvRwgq8Osjf5hJlBgHeS0f+6SyZeF3qkzachPzdulPIj0jVcR1FiWVcjrHYtl62kcKcyU3GJYY1OHYJ443utnBUVq/YciihY4uNcG+pKA48gyiJeo8+0ZvoHHpep/24VguTkgvCTSxYfgw+EiLm0QKGnh/+UxvNZH9u1Hh9s3qjjeNi8AHwMeYna/zJRrXGZ2xT+gcDkm1nhDtPUB4o4RooeNG03IsQ5a7n3r9ywLQANLxVSLIEwotL9rQKJw74XgLvBHeDm+6aHfr9W/fgBENDaKyQ0+WiR2GpLyPPXQ5mjcJZcHJMvxxvIVEM/HuF5OI4146HxxAcxzj/F8kO5OgOAmZE+JpUV83nXQrxdPw5MhAPSEGJ60gyuedD/EHJ4iu+oN78Zt+8HU2b0fQ/xsH44yqi5APQXRDtiaLU0cbo+2YerCstcHiBs6dJ2/Ar0h+Nj11FGlt3nEHPabFESqP1x3rP/8HzQUQ5ICOGvoAAAAASUVORK5CYII=";

var coverLocal$3 = "data:image/png;base64,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";

var playRepeat$3 = "data:image/png;base64,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";

var playOnce$3 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAgCAMAAAA2a+hwAAAAAXNSR0IArs4c6QAAAlJQTFRFAAAAgID/QID/QID/OY7jRovoO4nrSZLtRIjuPIfwQIzyQJnySZLzRpfzQ4X0QIrqQo7sPo3tEN73RI/wPI3yQZDrPo/sQ4ztQ4zzO4jtQY7tPo7uPYvuQI/vOY3vPo3vPIzrQZHwNqL1QI7wRYTrK4b2QY/uP47uR47yQIvvP43rQI/wP5DwSXz0QozuPYvxQY/uQI3uQY3vQI/yQZDzQpDwQ472QI3uPIn3P4vqQo7vQo3tQI3uQJDuQI7uQI3uQI7sQo7sQpHxQY3sPo7vQI7tQI/xQY7tQY3tQY3vPo7vQo/rP5HuQY3uQJDxQorsQo7sQo7vOI3xQovrQo3rQY7tQY3tQI7tQI3sPY/wN5vvQo7vNKPyQY7tQ43tQY/uQorsQI7sQY3uPJfwQI7wPpPuP4/vQY7tTnTsRJPxP47vQYzsPZDvP43uQo7sO5jvQY3uPZDwQ4ftQI7tQo3uQY7uQo7uQY7tQY7tQ43wQY7tQI3tQI7uQI7tQI/wP4zuQ5DtQY7tRJHsQI7tQY7tQI7tQY7uQI7uQY3uQo7tPpHtQI7tQY/sQY7sQY/tQY7tQY7tQY3sQY/uQI7tQY7tQo7uQo/uSY7tQY/tTXnqQY3tQY7tQY7tQY7tQY7tQY7uQY7tPZXtQY3tQY7tQY7tQY7tQo/tP5LqQI/rQI/sQY3sQY7rQY7sQY7tQY/rQY/sQY/tQZDtQozsQo3rQo3sQo3tQo7qQo7rQo7sQo7tQo/tQ4vsQ4zrQ47qQ47sRIfrRInsRI/rRI/sRYnsR4TqR4fqvnKd/wAAAKd0Uk5TAAIECAkLDQ4PERQUFRYXGBsdHyImJykqKisrLS4wMTEzMzQ0NDs7PT1AQURFRklLS0xOUFNVWFxdYWFlZ2doa2xsbW5vb3BxcnJzdHl5fHx8fH+AgIGCg4iJjIyOjpGSkpOUlZeZm56goaKio6OjpKaqqq6vsLO0t7i5vL7GxsfHyMjLy8zN0tTW2trc4ubn6erq7Ozs7Ozu7vHz9Pb3+Pn6+/v9/v6utNnsAAABnklEQVQYGY3BhVsTcQDH4c8QRbCwu7tbVFQsPLu7u3t2YRfYXRhg7r4GMnYzMPm/nLf7nTjkeXxf/lv7I3OoVP+IllCpvg80l39rNiTrkrQymQpqTD4rV7QriabkRx3tnXdP9tQqkDGAP1IPKXw6qy7ZciYCqbLnY6RdkDKBUVI2MUlL39jr8eQV3elATKdTI4kb+6FsMa7NZY/rkWDw8589ielS8qU3FWwquRoAcu0N+Opg3AxNgFalr2rhSd/3qBuece8vwszvOXjGFEiz8dQOC46GRuNK3iMVaDzG+TBct3vgSpGGrpOFse0rPH3blLhGrdkqC2P5OyhUQ3y7ZWEs/AG31RlfUBbGmiLIdQbiC8rC2P8SNkaW4QvKwrgfhkGhu/h2aRKedsVhqGnb/TAOazqend8OAit0BWP4ycbEdXxR2hJIeRJZS6LArc87+K2Xo2n8Len46xsBXAs+fVxFeU3OhJ61wTNL0XPdMarOkPOwLb7M/GIdGNE8rVr1+n1WX3OcYw0oJ32LnKhcjvKGkaDFohOFirm8PQPPL6EvjEOwGD5qAAAAAElFTkSuQmCC";

var recStartIcon$1 = "data:image/png;base64,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";

var recStopIcon$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGgAAABoCAYAAAAdHLWhAAAAAXNSR0IArs4c6QAAGa9JREFUeF7tXQl4lNXVfs/3zUz2SWZJCItsAlGhIm7VypIJoAVx6eLS1rZu/aWuZCKotda0WhVIJmjVulSttlZLfWr9rRYQMmFxF7dfEGVVEbLMkn2bme/+PXcyIcskmSQzIch8zxMSZr67nfee5Z577rmEI+SZe0+tRSS2HgtNHCuIxkNoo0lRRgjACkHZIGEBKJEgyoXAL50F1g1HyNB67SYN10HkrvRMA4kziLQzCHSyIMoBkNzeXyJAiO7d5xEJNDnzzSkgCvPCoSIM+oZfGd3DlQbcr2EDUGGhUJyJ5eeQXnc2kXI2gBMk4YIEj/Bpf7nSmW/O7g0gW1HVcujUGyDwjtLou2TD7SMqImxkSF877ADlLnePUXTapYKUG0A0Vo4+HGf0TJZ6AI0A6gkiIEA1CuHGDfmWN3sqYltRuRB69ZVgW/Lfp5x2y5VDSvkIGztsAM1ZcWCWohoWg+hiEOn6AKVBCGwnwnaAtgkSe1Xga8BfQS16t6nZ1PSPQmqNcMyYs8p1mqLROx3etzvtlpKeys8ucR2vGLSvyq7L4skwpM+QA2Qrcn1f6JTrSRM29KRH5LymjQJii6phk07RbV1rT/dEkzK2Ys9CoYjLFYjNpfnWP4Sr+6LVQnV/6fmrUOlSCK1KgB71t6iOLbdleKPZl97qGjKAbPcdnAq9/iEQzemxQwKfAOJxVVX+tX6J+cuhIkJP7eQWVc4kRd3c6XuCFwHtVufNmY8NRf9iDtCCB9zGZp+4B4pyXQ/6pZUgVoN0T5XmZ5QOxaAjbWNuSfVETQR2d3s/yPlvwO9f5lw24vVI6xvIezEFKHdF5SWkU+8H0YgwOsYtCH/Qt2qPv3ZL5oGBdH4oyuSVeBYIIRwAjuvMSSEzXysRtfW/Kiuc0ByL/sQEoPMKDyTXpRqeJ1U5LwzXCBDuVFvogfW3mmtiMahY1JnncF0vQHcByOgi8lhj7qeA+HnpUmvUJUDUAZpdXDlDJd1qECZ15xp6CUrgFueSzM9iQcRY15m7ojKbVKUIRD8J1xYJcW1pgfWP0exHVAHKK6q8Qai6B7pzDe0CxO1Ou2V1NDt/uOrKc3jOEwL3gMS0btykiWdrUy1XbL2GfNHoX9QAsjncT0KhK6B1WfYT/dE62nTDPy6mQDQ6PJzqyHO4iwRQ0BUkAj71QZu7OT/z4GD7GxWAbEVVf4Oq/qirSBNQlpTZTfcPtpPDuXyuw30VAY93cptJK0/b7ydx+mBBGhRAFxUKg8voeQWEeV38ZXsUhb63YYn54+FM3Gj1zbaqKgeauhoQJ3ap06UoYuGGJdZ3B9rWgAFicDzp3rc1IU7q3LjYmJrSuvDla0axf+zoeYSgPIf3n4LEhV0HTaSeUpqf8f5AiDFggGzFno1QMLuLWHvVabecO5COfFPK2Ipdf+1m5Qk0GUg3ZiDuqgEBZCuuehaK+uNO4BA958w3//ibQujBjCPX4S0iaJ2NB+BztZVO7+/ar98A2RzuP0NRfg5NOzQGor87882XDmZQ37SyuSWelSTEzZ3GJcQOZ4H1+P6MtV8A5d138JciKelhBA5ZzALkLLOb8/rT6NHybl6J+ykhcHnH8RLwfKnd8qNIaRAxQLblX+fAkLRD7gS0L3XoQ6fdPCPSxo7G92wO7wuA9oOOYxegH5XZzc9HQo+IAMot3JtI6ek7AYzpoHeqW33a2NdvyayLpKGj+R2bw/0hgOmdQQpMLrNn7eqLLhEBZCuqeg063bxOekdVT3XelLG1rwbi3wMzH6426ZsDVQDUdnoQDjjzLaP7ok+fANnuK78AiQn/6giOAO4us1vu6Kvy+PeHKJC7yn0Jaegq1u502i2/641OvQJ0UeE2g8uY7QFRSrtoU7DGucSyIE78/lMgnGUXEBi7qcDyVU+19QqQbWXVI9DrrunAPa36gM+8bml2Q/+7Fy/BFLA5PLsBMVFSQ7rsxGtlBVYOMwv79AjQ7OKvj1GVpC87LkZJoRtKl5gfjJN64BTIW+WdLTRt4yFdRAho/pM3FWR9EK7WHgGyFVW9B1U9pR0gErud+dZJA+9avGSIAjaH+wUAHU3vr5355mPCBVqGBWju8or5mkG/rosrZ6Yz3xzTAImjBUK5bDEaebvfEBJ1pIXfjQ0LkK3Y9S4U5dRD3EMvOfPN3by00SLo3N9XjBAJlCP0ih7+aNU68HoUCI18vt3rbx0Vs9AvW7HbDkJxey8FPnHazSd25aJuAOWuqDyJdLoPOgZEqwqNi0WcWu7y/WMUfWKJAC0CkDhwksakJAcgr1ED/ls3LB0Rk30tm8Pd1HHctSlmQ9et8m4A5RW7XhQKXdjuziH805lv6eSqiAY5Zv2+aqQuWdkGkKmfsdjRaD6yOtoiX5UATd+wNPqbj7ZV7nOggfVRCgGOUruls3O16+mGs5ZXpRn0am1H7gkI5eRNBaawFkZkowz/Vp7D9bIgZdGwBSfU7SBInzvtFj7+EvVn7oO1Fl+LL7mntVAnDsp1VBUQqUUhoilEn23IN3cO2ItCF2X4kk4ddEBFFLoScRWxmqh9daATQLYStwcCJi6kKoC7Tlz7wR3RjfPiuucWuedrCtb11bnh9D0J/E9pgYWDQ4b0aQfIVuyaC0VZz9zD4ByoDlRtL8zKikVv8hzuswWwNhZ1x6rO4QAQx1DfyIh5GjVMGaHfe+vZ6cuyjNg5xkQfRXPgcjIQrY9mnTGvi3CVM9/yZMzb6dLAIQ5yuPeAMEFogKs+gF8vMiM3R8VnFYAi8CkR1giBZ2dNokFvMcQBihxmCZBteVUO9MoO/ruhVcCaouC2hSakJACNXc6tEbBBaPjLzEn0dOTNdH4zDlB4ytmK3VcSNLW0ILNd10mAcouqriZVfZz1T0VtALOnJOKmeWnwNIQ5LsolglveH2sBrCyfhOcupv6F9cYB6g5Q3oqqm4VBXRmkrXjUmW9ZzH8FOajY/QQUXMlfHqwJ4PLvpOHi0xJxoDoiHvkSGv5EwKNnTaLKSErEAepOpel3VZWbUpQRChGEELCOMes4nj0IkMP9CYCpHPfe0KLBPj8Dp4zXoSrSaIMgVzWQwNOawJN96ak4QN0Buugx7+XbD/j+KAiJSQa1eOttQa8CySOKfnAyB12zTyA1QcGyBRnITCXUt0TCD93fIYHnND+Wz8oJb/3Nv9811x+IW3FMufe/ECdYjbi0ugnT3tzTeqzzs6arnr8q470QVSnP4TpdgN7mD6obNeRk61FwTrrcRPUN9MDIIT21Xgvg0dmTif1N7c+031bMz07XrQt0iH0c2FQYwlJRNrM3fi6O1ym4TSj4KZMhUQeYU6E11OMn08dSe+wC5RW5fioUeoaHWlkXQN5xSbjWlhreQOgvPQ4BtcMv8Ny4ZDwzYSTt+9a9FRONqrrboOszZqW/Lcbu/SgB9NpukZ4gcLei4nrZ2Q7HqfhPvQLoWpB5ag65pIjLLXbdTgrdHTIQLj4tFT87MwnlMTg9yiGP40y4f/XWlk+ffqP2EUuKSuyLPCKeKAC0aaf4oaLiIRCyektvoyj4/nfG04sSIJvD9RBA1zJ6VXUBXHmWERfMSIgVQMg2Ah/t9+PeV6pFaoJCinJEwMP27oA9Ca/vEtNAuFco4H2vXnMP8YQN+LBs9hRa2QZQcH+cLbiaRg3X5hmRO8WAykgtuH7S15oKvL3XhwfW10iD5JsMkHObyNYnoQCE4D5PJEmheHcjgAdmTaKbggAVuzeCMJsVdmOrwJJ5Rpw6Xg9XjLLSHC0AbdkjOCDxZhCSIgKmbaIzDsYk/HnGaLoixEHs4snxBQT4y/z5GZg2WpVGQiyebzJA27YJgzcRVwqBG0nF8f0Bhmnt14ARRkCvYvUkC10S0kGVAGW2+oUUN7xInZylopp3y2PwfBMB2rxbjIXAz4iwGApG9xeYEJnZ91nbrOHJLQ21b+xsfCcrTb2HbA43a5vUFr+AXiXY56djYqaKmjhA7dNTVQhfePxX7vxd1lMd5+ymz8UPVBWXCcL5ICgDBYbrVAiwpALFa+vx0kf1OGGkgZOBNTJArG1S4gD1LC4aWgSsacp5r15v/venB0VuVSMWKIoMPDw2YuXfhzTiTdIkA1C0phYf7W+BNVUehBAMEC+ILHERF56CHHvFC/ib5qZ/tOgkQ/KuCkxmYkb7kQDpgfvX12LrFy2wBAGqJpvDsxMQk9hIYPfOkvkZmDpahTduJASZQwCehgAWzzFi3tQEVNZGG5pgfTo16EUoWV+L/9vfCnOKnAXlbGZvBmEmW3BNPg03zcvAqeN0cTO7DYcQQFfPMuK734odQAYdZBYdx7oa7Kr0IT1JAUjsprxiz4ucfIG/rG3ScJ0tHbOn6OML1SEGiMVbQyuwcm01ymv8chEP0PtkK/E8AiGu4UWui109M404/6TYuHp4zEeamT1UHJSWCBysFli5zovGFoFEPbFR+B+yOby/AbTfMvF4N/XS01JxWYycpXGAetZf5hRg24EAHOuqoWOPtiq9yE9QnsN9hQBkOFFlbQDzTkjCL3NT4Q4XjxAF/RjnoPBEzDICGz/z4aHSGmQkK8GEyIRCFnFnQYgtXMzbqMkFUsE5RvgDg9iw6wXIOEDhiTMyHfj7u834y5t1yE4PHgbnfAo0895qkz4hwGshpcknYExUsOy7GbAOYsu7N0aLA9SdOizMrGnAw84GrNveiGxj22l94T8pFDTCOUSnhExt9sfNGBsbUzsOUHeA9GowFr5obQ0+K/dJEQegqRmNljaAPM8CQmaqKq8J4IqZRvzwlIRIw676pZniAHUnlzEJ2O8RWL7GC39AQIYCELY48y2zgoGLJa7rSJA8vc2Bi3OmJOHGeanSm9A1BWm/0Ajzchyg7kRh/fPa9lY8WFqDzLT2ZCQrnXbLMgnQ3FWeEzVNyAD5+haBrDQFty40IdkANEV8ZUVk0MUB6k6nURnAY5sa8eL79RiVoQstkS902q0vdQye5wNV2bwwY2tu6XczcOZEnRRz0QzsiAPUGSB28bD+ueeVGux1tbl4gIDaShZO/tcRIL4s4hf8wYGaAOafkIwb8lJQVd/f63x656Q4QIfow8yQnQ68tceP4nXV0v/G+0KC8FpZvkVmH2kHKHeVZxFp4mX+kCNMeSX7m/NMGGEk1EZx844BemtPMGgkLXH4B42EXD1XzTRiwYnRd5YyQKteq8e67U0YY1KDp08V5SbnEpNMEN8OUDDFsrsWoATJRdUBLM414sIZCfjKEz0xl5kGvLE7CBDPmGiKz8i0YP/f4v2gX8wy4tzpCaiIYrwg+9/YY3P3vz3y2E+yPgiHn7RRoXzbncIG80o8dwshbucP+RDX1NEJuG2hUZ4RYs9CNJ70JGCvS5MmJc+WhGEeXRry8tvPzsBp46O3NuSxH2MG/rG1BY9vqsEYU9slZISnnfmW9jSanQDiI+Faq0+GnHLHvA0abjvXhNPGqzgYJWOBA1PMycC9r9Zh884mTMzUyWii4fiw8t7vDch49cLzM9DK7q8oZULhxSlvMdz9Sg12HPTBmtoW0qBqpzpvymw/xdgt8DbX4SklCBuLngPeAM6elowl81JwgFk7ksC7PijNM4f10F5XAL95qRrVjQGMzNBJD27/7haMHaQ8dt5drqgLSBF853kmnDxOJ8VbNEQyT/4xJuD1XX6sWOOFOUWVxgFv0HVNWNUNIJkuS2gbGQzeBm9qFSi8wIycbEWGA8uKBvlwB3lx9ll5AM+8WY/dlT40c9iXXEAf3ofnIPfPoBLGmnW49NupUrQdZHCi1DXmTN5euPfVWry9twWj0lXZpiDlirJ80587NhO2TVuRawcUymGCMYvbjkvC0nNSUVEXnFmDfZgIzC1sMLBu++SAX2518GfRmACD6V+obxy0MW20DikGDhoJ9jca3BMQwDgzsGUXx6d7YU1r4x6g2mm3yBwVfQKU6/BcSiSeYy5i/cBB9ayLZk3WSYsuWkTkQbMsZl+UQQ1K0ChI0cHgI7mEf3wahwAArf7oAMOd4rEl6wFenBb+bw0+r/BJr410pxH9yplvvjcigPglW7F7LwjjGQyWxZOyDLjrgnSpKNmqixa7D4qaR1hhBmKCFXjunRY8WlaDcdb262PralPMlnCXQvVIZ1uxdzpI43zPkrW/cPtx9ax0XHZGAva5ojerjjAaD7i7DA4bRxW1Are/6IUvoMmFujSMBJ3rLDC/Gq7yXhkhlLqRAaprFuBt8vt+aMaodJJKMxYBfAOmwDAuyBiwlTraBKxc04D/fNKAcRad1OcE8UGp3XpyT93vFaBzHDXmVvjlbfGhNcHpExJxx6I01DWz5zt6+mgY03fQXQuJtvXb/Vi51iujRhkwfgIkTtiUb/10QABxodwS99Uk5BVg8vnS48dFp6Zi8Zxk6emO9n7RoKkxzCpgI4u3E3ZXafjNv7wICIGMpKBhIIiKyvLNS3vrckS63uZwc0K/k1jUtfgEquo13LHIhNwcHfa64lzUE4EZBHZtsdV2yws12FHeItdWQc+JqHLarX1mE4sIoFklVSN1QuHLNVLYquP9oiSDgnu+Z8IYE2G/N66PuoLEyj9RHzyQVbK+Af/+qAHHmNutNig6TN1wo2V7XwwfEUBS1K3yzCRNyIvHWR997Q1gvFWHuy4MJl3ioPK40RAkN4PDwfDsznn2rWY8saUOI9MV6BQZLcqi7bKyfPOzfYHD30cMUFAfee8koRXy3yEvw+QRetzzvQy54OQV99EOEoPDNBhnAV7Y2oIHS2ulIzTZQG36mv7itJt/Fgk4/QaozWhYRwLzZWECvvL48e2JSdKyC3odjl6QQuCMNQPrtrfKMCoOgk9LDIGDbU67pfPtxX0g1S8OCtVlc7jKAJrDhZllv/L6cebERNy60CjXShVHobiTDlYdcIwJWPNJK4rX1Uiu4Rg3nrh8O7GWEDi97Lqsfp2fHxBADFQoQ1YIpK+r/cgZYcCvF6UjMy1oOETLZxepODhc7zE4HAHF8dUvvNeMP22u6wQOZxlN1GHCf2609Pv414AByi2qtaqK72MNGCkroeAa6dhMA24/14jxVkU6VuVqecCtHC6SR94ucwdvHbDD95k3mvD0m3UwJ6swJvGtJrKeVkXDjA03922xhWt1UKRj81svlA0CkFdPSuuuOgBTsorr89Iwc1IwIQZ7Hb5pxkNoc5EXoew8fmRjPdZ+0ogso4okfZvOIRyApix0FpgGnJR3UAAxKBetFqprv4dzYMurOmW+7XoNfGr8/JOScfl3UiQHsV7i34NuMPLJHbM3WaQl6IK+tff2+fHIxjrsqfRjZIYqxx8Ej7YrBt3sDdcbpatsoE/U6GVzuDkPtozlYt3DoVvMTWdNSsTiOalS5DFIPNuOVN0U4hqZDUQHvPJxi9Q3PBl5V5S/D+5n0Yf6QOvMaNxUFjWAgoaDZwUgpG9J6h0RDIK0pKi4YEYSFkxLQmoCB+gHN8SOJKBCbhtTCvB/+wP45/uN2LKzGelJBGNS0FJre55w2i1XD5RjupaLKkBceW6xZxGR+CuA9JDI40yO3kaB6ccY8P0ZyThjoh689ctrpmhtJUeLIF3rYWA4+oa55mCNwMsfN+HVj5tQ36JhZHrQKx1yGBOUK0vtpk7ZSAbbr6gDxB3Kc7j4ftCXBUjeUhziJg4A5Jk2a0oizp+ejKmjVMlJnOuE028OF44KbbtzPAKnZ6luADbvbMFLHzZij4u3qdUOnoFgNE5AUy+KxS0xMQEoNGtsDs9SQNzHaimkmxgIBopX2LMmJ0gdddxInRR93kbOOhyU4zHtWA/TOuRDM/03bo+NAOaYrV+0onRHE7Yd8CE1gWBKDu6ChkAUirgvc5Tl15xCebDcEq58zOkw+/6ayaoW4Ctvzg91IGREuOo1OROnjjbgjAkJmDbGgNEZwS5VNwItUQoS7Itw3B+eIKmJwQmyq9KPd/e1SHD2ufzySDxn/mi776mtOrGRgGWldus7fdU/mO9jDlCoc3OLvTaNtIcBtN9HxAPmsCtOtcK50jh4PCfbgDMmJshozozkoNne3MpZUIJicLBRqNwm18kcwiKMvc58Bqq8VsPWfa14/8sW7K7yy4BKjh3nuIHOwKCcIK4ttVtlTtFYP0MGUGggeSXeG4TQruMkgqHPQu4izvjIBkWSgeRWxniLTv4ea9YjM1WRq3N2qfCMZ6DY0GCAWUnLHcoOMVv8Dv8wGKEf/j+DzFzCN7wcrA5gn9svA2JYt/D/WemzGNPrOAN8J/J/JQhPGPy+omiYz5ECO+QAddBPPxYQi/97Wcesjp2VSVU1oL5ZkxH/TFSeyeyy5+OBvFLnUNnQ7GYRyWc6ORKU4755QAwW18EZvDhilbPpc5qb6iZNZlNhHci/WcTypOCtEuYUFmWhyXJo8ogPBNEjosb8ZFkhDZHQPUSRwwZQO0c5XKdDUS8WmnYNJxYMN7M4BJkXg7z4ZY5hEJmofDIiQU8yESFvhjGnhOKqg9x1qBzXESrLgCa2le3BchQgPEak/K10iWlTpLM9Fu8ddoBCgzp7ZXmWT6fn2yfZGzEXQEZvA2bxE+QU0U3EhVxKIX3DGRN5oH04bRsEwGCsBSkvleWb9sWC4P2tc9gA1LHjfJ9ES4DOEpo4E4Rvc8AKgD4DLPo1eAIfUPqYCG8JgTeFZni97GajPHoznJ5hCVBXAi14YGdCg98yRSVtstCUyaSICUKIUQRi0DjgPAWEZAiEjkjzmoQPbnI+Vt6D4WtzviZFfCk0+pxI26UZxOf93Tw7HMD9P7lW8X2jq8nBAAAAAElFTkSuQmCC";

var recDelete$1 = "data:image/png;base64,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";

var recMockWave$1 = "data:image/png;base64,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";

//
const uuid$1 = prefix => {
  const s = [];
  const hexDigits = '0123456789abcdef';
  for (let i = 0; i < 36; i += 1) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4';
  s[19] = hexDigits.substr(s[19] & 0x3 | 0x8, 1);
  const id = s.join('');
  return prefix ? prefix + '__' + id : id;
};
var script$5z = {
  name: 'RecordPlayer',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    targerLink: {
      type: String,
      default: ''
    },
    playerIndex: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      playIcon: playIcon$6,
      pauseIcon: pauseIcon$5,
      audioIcon: audioIcon$4,
      coverLocal: coverLocal$3,
      recStartIcon: recStartIcon$1,
      recStopIcon: recStopIcon$1,
      recDelete: recDelete$1,
      recMockWave: recMockWave$1,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      recordAudio: null,
      permissionDialogVisible: false,
      Rec: recorder_mp3_min,
      type: 'mp3',
      bitRate: 16,
      sampleRate: 16000,
      rec: null,
      recDuration: 0,
      powerLevel: 0,
      logs: [],
      recBolb: null,
      isRecing: false,
      hasInitSuccess: false,
      waveStore: {
        selected: 'Histogram2',
        WaveView: {},
        Histogram2: {},
        Histogram3: {}
      },
      recordDataUrl: '',
      lastAudioStamp: null,
      lastPlayStamp: null,
      waveBoxKey: uuid$1('wave'),
      recTimestamp: null
    };
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat$3,
        'dan-ci': playOnce$3
      };
      return srcMap[this.playMode];
    },
    controlStatusText() {
      let text = '';
      if (this.recBolb) {
        text = this.isPlay ? '' : '';
      } else {
        text = this.isRecing ? '结束录音' : '开始录音';
      }
      return text;
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        console.log('浏览器 UA', ua);
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        // isWeixin && isIOS && newVal && this.$message({
        //   message: `当前UA为${ua}`,
        //   duration: 3000
        // })
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.recordAudio.muted = true;
              const playPromise = _this.$refs.recordAudio.play();
              _this.$refs.recordAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.recordAudio.pause();
                }).catch(error => {
                  _this.$refs.recordAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.recordAudio.pause();
      }
    }
  },
  mounted() {
    // 加载完毕后，先获取<video>标签DOM对象
    this.recordAudio = this.$refs.recordAudio;
    // let _this = this;
    // if(!this.recBolb && !this.isRecing) {
    //   let rec = this.rec = Recorder({
    //       type:_this.type,
    //       bitRate:_this.bitRate,
    //       sampleRate:_this.sampleRate,
    //       onProcess:function(buffers,powerLevel,duration,sampleRate){
    //           _this.recDuration = duration;
    //           _this.powerLevel = powerLevel;
    //           // _this.wave.input(buffers[buffers.length-1],powerLevel,sampleRate);
    //           //可视化图形绘制
    //           $(".recwave").html("").append(_this.waveStore[_this.waveStore.selected].elem);
    //           _this.waveStore[_this.waveStore.selected].input(buffers[buffers.length-1],powerLevel,sampleRate);
    //       }
    //   });

    //   _this.dialogInt=setTimeout(function(){//定时8秒后打开弹窗，用于监测浏览器没有发起权限请求的情况
    //       _this.showDialog();
    //   },8000);

    //   rec.open(function(){
    //       _this.dialogCancel();
    //       // _this.reclog("已打开:"+_this.type+" "+_this.sampleRate+"hz "+_this.bitRate+"kbps",2);

    //       _this.initWaveStore(".recwave");
    //       // rec.start();
    //       _this.hasInitSuccess = true
    //       // _this.isRecing = true
    //   },function(msg,isUserNotAllow){
    //       _this.dialogCancel();
    //       isUserNotAllow && _this.$message.error('启动录音失败，用户拒绝了授权')
    //       // _this.reclog((isUserNotAllow?"UserNotAllow，":"")+"打开失败："+msg,1);
    //   });

    //   _this.waitDialogClickFn=function(){
    //       _this.dialogCancel();
    //       _this.$message.error('启动录音失败，用户忽略了权限请求')
    //       // _this.reclog("打开失败：权限请求被忽略，用户主动点击的弹窗",1);
    //   };
    // }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.recordAudio.duration);
        this.totalDuration = this.$refs.recordAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    resetRecorder() {
      let audio = this.$refs.recordAudio;
      if (!audio.paused) {
        audio.pause();
      }
      this.recBolb = null;
      this.isPlay = false;
      this.isRecing = false;
      this.playTime = 0;
      this.playDuration = '00:00';
      this.playCurrentTime = '00:00';
      this.totalDuration = 0;
      this.recDuration = 0;
      this.recordDataUrl = '';
      this.lastAudioStamp = null;
      this.lastPlayStamp = null;
    },
    setRecedDuration(ms) {
      this.playDuration = this.timeFormat(ms / 1000);
      this.totalDuration = ms / 1000;
    },
    initWaveStore(elem) {
      this.waveStore.WaveView = recorder_mp3_min.WaveView({
        elem: elem,
        width: 300,
        height: 100
      });
      this.waveStore.Histogram2 = recorder_mp3_min.FrequencyHistogramView({
        elem: elem,
        lineCount: 30,
        position: 0,
        minHeight: 12,
        mirrorEnable: true,
        stripeEnable: false,
        linear: [0, '#8FC1FD', 1, '#8FC1FD'],
        width: 300,
        height: 100
      });
      this.waveStore.Histogram3 = recorder_mp3_min.FrequencyHistogramView({
        elem: elem,
        lineCount: 20,
        position: 0,
        minHeight: 1,
        fallDuration: 400,
        stripeEnable: false,
        mirrorEnable: true,
        linear: [0, '#0ac', 1, '#0ac'],
        width: 300,
        height: 100
      });
    },
    toggleRec() {
      let _this = this;
      // if(!this.hasInitSuccess) {
      //   this.$message.warning('当前浏览器不支持或用户未进行授权，组件功能暂无法使用')
      //   return
      // }
      let waveBoxSelector = '#' + _this.waveBoxKey;
      if (!this.recBolb && !this.isRecing) {
        // _this.rec.start();
        // _this.isRecing = true
        if (recorder_mp3_min.IsOpen()) {
          _this.$message.warning('当前存在进行中的录制任务');
          return;
        }
        let rec = this.rec = recorder_mp3_min({
          type: _this.type,
          bitRate: _this.bitRate,
          sampleRate: _this.sampleRate,
          onProcess: function (buffers, powerLevel, duration, sampleRate) {
            _this.recDuration = duration;
            _this.powerLevel = powerLevel;
            // _this.wave.input(buffers[buffers.length-1],powerLevel,sampleRate);
            //可视化图形绘制
            jquery(waveBoxSelector).html('').append(_this.waveStore[_this.waveStore.selected].elem);
            _this.waveStore[_this.waveStore.selected].input(buffers[buffers.length - 1], powerLevel, sampleRate);
          }
        });
        _this.dialogInt = setTimeout(function () {
          //定时8秒后打开弹窗，用于监测浏览器没有发起权限请求的情况
          _this.showDialog();
        }, 15000);
        rec.open(function () {
          _this.dialogCancel();
          // _this.reclog("已打开:"+_this.type+" "+_this.sampleRate+"hz "+_this.bitRate+"kbps",2);
          console.log(waveBoxSelector);
          // _this.wave=Recorder.WaveView({elem:".ctrlProcessWave"});
          _this.initWaveStore(waveBoxSelector);
          rec.start();
          _this.isRecing = true;
          _this.recTimestamp = new Date().getTime();
        }, function (msg, isUserNotAllow) {
          _this.dialogCancel();
          console.log('启动录音错误信息', msg);
          let errMsg = '启动录音失败，当前浏览器不支持';
          if (isUserNotAllow) {
            errMsg = '启动录音失败，用户拒绝了授权';
          }
          if (msg.includes('麦克风')) {
            errMsg = '启动录音失败，无可用 MIC 设备';
          }
          _this.$message.error(errMsg);
          // _this.reclog((isUserNotAllow?"UserNotAllow，":"")+"打开失败："+msg,1);
        });
        _this.waitDialogClickFn = function () {
          _this.dialogCancel();
          _this.$message.error('启动录音失败，用户忽略了权限请求');
          // _this.reclog("打开失败：权限请求被忽略，用户主动点击的弹窗",1);
        };
        // _this.$nextTick(() => {
        //   if(!Recorder.IsOpen()){
        //     _this.$message.error('未获取录音权限')
        //     // this.reclog("未打开录音",1);
        //     return;
        //   }
        //   console.log('当前录音实例配置',_this.rec.set);
        //   rec.start();
        // })
      } else {
        if (!(_this.rec && recorder_mp3_min.IsOpen())) {
          _this.$message.error('录音未启动');
          return;
        }
        let doneTimestamp = new Date().getTime();
        if (!_this.recTimestamp || doneTimestamp - _this.recTimestamp < 5000) {
          _this.$message.warning('至少录制 5s 方可进行保存，请稍候');
          return;
        }
        let rec = _this.rec;
        rec.stop(function (blob, duration) {
          _this.$message.success('录制完成');
          _this.setAudioPath(blob, duration);
          jquery(waveBoxSelector).html('');
          _this.$emit('resData', {
            blob: blob,
            duration: duration
          });
        }, function (msg) {
          _this.recTimestamp = null;
          _this.$message.error(`录音失败：${msg}`);
        }, true); //录音结束时自动调用rec.close方法丢弃所有数据
      }
    },
    // 销毁录音实例
    destruction() {
      this.rec.close();
      this.isRecing = false;
      let waveBoxSelector = '#' + this.waveBoxKey;
      jquery(waveBoxSelector).html('');
    },
    setAudioPath(blob, duration) {
      this.recDuration = duration;
      this.setRecedDuration(duration);
      this.$nextTick(() => {
        this.recBolb = blob;
        this.isRecing = false;
        this.lastAudioStamp = new Date().getTime();
        this.recTimestamp = null;
      });
    },
    play() {
      if (!this.recBolb) {
        this.toggleRec();
      } else {
        // if (this.playStyle === 'yuan-wen') {
        //   this.doPlay()
        // }
        // if (this.playStyle === 'tan-chuang') {
        //   this.$emit('showDialog', { url: this.url, title: this.title, coverFileUrl: this.coverImageUrl, content: this.content, playerIndex: this.playerIndex })
        // }
        this.togglePlay();
      }
    },
    togglePlay() {
      let _this = this;
      let audios = document.querySelectorAll('audio');
      audios.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      let audio = this.$refs.recordAudio;
      if (this.isPlay) {
        !(audio.ended || audio.paused) && audio.pause();
      } else {
        if (!this.lastPlayStamp || this.lastPlayStamp !== this.lastAudioStamp) {
          audio.onerror = function (e) {
            _this.$message.error(audio.error.message);
          };
          audio.currentTime = 0;
          audio.src = typeof _this.recBolb == 'object' ? (window.URL || webkitURL).createObjectURL(_this.recBolb) : _this.recBolb;
          audio.play();
          this.lastPlayStamp = this.lastAudioStamp;
        } else {
          audio.paused && audio.play();
        }
      }
      this.isPlay = !this.isPlay;
    },
    doPlay() {
      let _this = this;
      let rec = this.rec;
      let audio = this.$refs.LogAudioPlayer;
      audio.controls = true;
      if (!(audio.ended || audio.paused)) {
        audio.pause();
      }
      audio.onerror = function (e) {
        _this.$message.error(audio.error.message);
      };
      audio.src = (window.URL || webkitURL).createObjectURL(rec.blob);
      audio.play();
      // console.log('暂停')
      // let audio = document.querySelectorAll('audio')
      // audio.forEach((i) => i.pause())
      // let video = document.querySelectorAll('video')
      // if (video && video.length) {
      //   video.forEach((i) => i.pause())
      // }

      // this.isPlay ? this.$refs.recordAudio.pause() : this.$refs.recordAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.recordAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.recordAudio.currentTime);
      if (!this.loop && this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      // this.$emit('timeUpdate', {event:e, time: this.$refs.recordAudio.currentTime })
    },
    changeTime(val) {
      this.$refs.recordAudio.currentTime = val;
    },
    showDialog: function () {
      //选择性的弹一个对话框：为了防止移动端浏览器存在第三种情况：用户忽略，并且（或者国产系统UC系）浏览器没有任何回调
      if (!/mobile/i.test(navigator.userAgent)) {
        return; //只在移动端开启没有权限请求的检测
      }
      this.permissionDialogVisible = true;
    },
    dialogCancel: function () {
      clearTimeout(this.dialogInt);
      this.permissionDialogVisible = false;
    },
    waitDialogClick: function () {
      this.dialogCancel();
      this.waitDialogClickFn();
    }
  }
};

/* script */
const __vue_script__$5z = script$5z;

/* template */
var __vue_render__$5z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: ["xml-audio-player", _vm.mode] },
    [
      _vm.recBolb
        ? _c("div", { staticClass: "play-mode-wrap" }, [
            _c("img", {
              staticClass: "play-control-image",
              attrs: { src: _vm.playModeIconSrc },
              on: {
                click: function ($event) {
                  _vm.$refs.recordAudio.currentTime = 0;
                },
              },
            }),
            _vm._v(" "),
            _c("img", {
              staticClass: "play-control-image",
              attrs: { src: _vm.recDelete },
              on: { click: _vm.resetRecorder },
            }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.styleMode !== "C"
        ? _c("div", { staticClass: "audio-cover" }, [
            _vm.styleMode === "A"
              ? _c("div", { staticClass: "play-button style-a" }, [
                  _c("img", {
                    staticClass: "button-image",
                    attrs: {
                      src: _vm.recBolb
                        ? _vm.isPlay
                          ? _vm.pauseIcon
                          : _vm.playIcon
                        : _vm.isRecing
                        ? _vm.recStopIcon
                        : _vm.recStartIcon,
                    },
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.play.apply(null, arguments)
                      },
                    },
                  }),
                ])
              : _vm._e(),
            _vm._v(" "),
            _vm.controlStatusText
              ? _c("div", { staticClass: "play-text" }, [
                  _vm._v(_vm._s(_vm.controlStatusText)),
                ])
              : _vm._e(),
          ])
        : _vm._e(),
      _vm._v(" "),
      !_vm.recBolb
        ? _c("div", { staticClass: "record-player-control" }, [
            !_vm.recBolb
              ? _c("div", {
                  staticClass: "recwave",
                  staticStyle: { height: "80px", width: "100%" },
                  attrs: { id: _vm.waveBoxKey },
                })
              : _vm._e(),
            _vm._v(" "),
            !_vm.isRecing
              ? _c("div", { staticClass: "mock-wave" }, [
                  _c("img", {
                    staticClass: "button-image",
                    attrs: { src: _vm.recMockWave },
                  }),
                ])
              : _vm._e(),
          ])
        : _c("div", { staticClass: "audio-player-control" }, [
            _c("div", { staticClass: "audio-controls" }, [
              _vm.styleMode !== "A"
                ? _c(
                    "div",
                    { staticClass: "audio-controls__play-button-wrapper" },
                    [
                      _c("div", { staticClass: "play-button" }, [
                        _c("img", {
                          staticClass: "button-image",
                          attrs: {
                            src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                          },
                          on: {
                            click: function ($event) {
                              $event.stopPropagation();
                              return _vm.play.apply(null, arguments)
                            },
                          },
                        }),
                      ]),
                    ]
                  )
                : _vm._e(),
              _vm._v(" "),
              _c("div", { staticClass: "audio-controls__handler" }, [
                _c(
                  "div",
                  { staticClass: "progress" },
                  [
                    _c("el-slider", {
                      attrs: {
                        max: _vm.totalDuration,
                        disabled: _vm.scheduleControl !== "yes",
                        "show-tooltip": false,
                      },
                      on: { change: _vm.changeTime },
                      model: {
                        value: _vm.playTime,
                        callback: function ($$v) {
                          _vm.playTime = $$v;
                        },
                        expression: "playTime",
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "play-times" }, [
                  _c("div", { staticClass: "current-time" }, [
                    _vm._v(_vm._s(_vm.playCurrentTime)),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "total-time" }, [
                    _vm._v(_vm._s(_vm.playDuration)),
                  ]),
                ]),
              ]),
            ]),
          ]),
      _vm._v(" "),
      _c("audio", {
        ref: "recordAudio",
        staticClass: "audio-component",
        attrs: {
          controls: "",
          preload: "auto",
          src: _vm.recordDataUrl,
          loop: _vm.loop,
          link: _vm.link,
        },
        on: {
          canplay: _vm.changeDuration,
          timeupdate: _vm.updateTime,
          pause: function ($event) {
            _vm.isPlay = false;
          },
        },
      }),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          attrs: {
            title: "权限请求提醒",
            width: "320px",
            visible: _vm.permissionDialogVisible,
            "custom-class": "info-dialog",
            "append-to-body": "",
          },
          on: {
            "update:visible": function ($event) {
              _vm.permissionDialogVisible = $event;
            },
          },
        },
        [
          _c("div", [
            _c("p", { staticClass: "version-number" }, [
              _vm._v(
                "录音功能需要麦克风权限，请允许；如果未看到任何请求，请点击忽略"
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticStyle: { "text-align": "right" } },
              [
                _c(
                  "el-button",
                  { attrs: { plain: "" }, on: { click: _vm.waitDialogClick } },
                  [_vm._v("忽略")]
                ),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$5z = [];
__vue_render__$5z._withStripped = true;

  /* style */
  const __vue_inject_styles__$5z = undefined;
  /* scoped */
  const __vue_scope_id__$5z = undefined;
  /* module identifier */
  const __vue_module_identifier__$5z = undefined;
  /* functional template */
  const __vue_is_functional_template__$5z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5z, staticRenderFns: __vue_staticRenderFns__$5z },
    __vue_inject_styles__$5z,
    __vue_script__$5z,
    __vue_scope_id__$5z,
    __vue_is_functional_template__$5z,
    __vue_module_identifier__$5z,
    false,
    undefined,
    undefined,
    undefined
  );

//
const _image = '.jpg,.png,.jpeg,.gif,';
const _audio = '.mp3,';
const _video = '.mp4,.MP4';
const _resources = 'application/x-zip-compressed,.zip,';
const _office = '.ppt,.xlsx,.xls,.doc,.docx,.pptx';
var script$5y = {
  components: {
    RecordPlayer: __vue_component__$5z
  },
  name: 'addFile',
  props: {
    actionUrl: {
      type: String,
      default: ''
    },
    headers: {
      type: Object,
      default: () => {}
    },
    data: {
      // 上传文件附带的额外参数
      type: Object,
      default: () => {}
    },
    fileSize: {
      // 最大上传文件大小
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      Accept: _image + _audio + _video + _resources + _office,
      fileRules: {
        resourcesName: [{
          required: true,
          message: '请输入资源名称',
          trigger: 'blur'
        }, {
          min: 2,
          max: 50,
          message: '长度在 2 到 50 个字符',
          trigger: 'blur'
        }],
        isDownload: [{
          required: true,
          message: '请选择是否允许下载',
          trigger: 'change'
        }],
        fileUrl: [{
          required: true,
          message: '请上传资源文件',
          trigger: 'change'
        }],
        isStudentShow: [{
          required: true,
          message: '请选择是否对学生可见',
          trigger: 'change'
        }]
      },
      recordedRules: {
        isStudentShow: [{
          required: true,
          message: '请选择是否对学生可见',
          trigger: 'change'
        }]
      },
      noteRules: {
        noteContent: [{
          required: true,
          message: '请输入笔记内容',
          trigger: 'blur'
        }, {
          min: 2,
          max: 1000,
          message: '长度在 2 到 1000 个字符',
          trigger: 'blur'
        }],
        isStudentShow: [{
          required: true,
          message: '请选择是否对学生可见',
          trigger: 'change'
        }]
      },
      fileList: [
        // {
        //   name: 'food.jpeg',
        //   url: 'https://fuss10.elemecdn.com/3/63/4e7f3a15429bfda99bce42a18cdd1jpeg.jpeg?imageMogr2/thumbnail/360x360/format/webp/quality/100'
        // },
        // {
        //   name: 'food2.jpeg',
        //   // url: 'https://fuss10.elemecdn.com/3/63/4e7f3a15429bfda99bce42a18cdd1jpeg.jpeg?imageMogr2/thumbnail/360x360/format/webp/quality/100'
        //   url:'../../../static/images/defaultCover/audio.png'
        // }
      ],
      fileData: {
        resourcesName: '',
        noteContent: '',
        isDownload: [0],
        fileUrl: '',
        id: '',
        resourcesType: '',
        fileExt: '',
        creationTime: '',
        textBookId: '',
        chapterId: '',
        orderNum: '',
        firstLevelId: '',
        firstLevelLabel: '',
        locationChapterId: '',
        isStudentShow: [0],
        location: '',
        duration: 0
      },
      recordedData: {
        resourcesName: '',
        noteContent: '',
        isDownload: [0],
        fileUrl: '',
        id: '',
        resourcesType: '',
        fileExt: '',
        creationTime: '',
        textBookId: '',
        chapterId: '',
        orderNum: '',
        firstLevelId: '',
        firstLevelLabel: '',
        locationChapterId: '',
        isStudentShow: [0],
        location: '',
        duration: 0
      },
      noteData: {
        resourcesName: '',
        noteContent: '',
        isDownload: [0],
        fileUrl: '',
        id: '',
        resourcesType: '',
        fileExt: '',
        creationTime: '',
        textBookId: '',
        chapterId: '',
        orderNum: '',
        firstLevelId: '',
        firstLevelLabel: '',
        locationChapterId: '',
        isStudentShow: [0],
        location: '',
        duration: 0
      },
      dialogImageUrl: '',
      dialogVisible: false,
      disabled: false,
      show: false,
      isEdit: false,
      chapterOrderNum: 1,
      isSwhoUpload: false,
      activeName: 'resource',
      recordedBlob: null,
      loadingInstance: null,
      limit: 1
    };
  },
  methods: {
    open(e) {
      console.log('e', e);
      this.show = true;
      this.recordedBlob = null;
      let fileData = e;
      // fileData.textBookId = e.textBookId
      // fileData.creationTime = e.creationTime
      // fileData.resourcesName = e.resourcesName
      // fileData.noteContent = e.noteContent
      fileData.isDownload = [e.isDownload];
      fileData.isStudentShow = [e.isStudentShow];
      // fileData.id = e.id
      // fileData.resourcesType = e.resourcesType
      // fileData.fileExt = e.fileExt
      // fileData.fileUrl = e.fileUrl
      // fileData.location = e.location
      // fileData.chapterId = e.chapterId
      // fileData.orderNum = e.orderNum
      // fileData.firstLevelId = e.firstLevelId
      // fileData.firstLevelLabel = e.firstLevelLabel
      // fileData.chapterOrderNum = e.chapterOrderNum
      // fileData.locationChapterId = e.locationChapterId
      // fileData.duration = e.duration
      this.isEdit = e.isEdit ? true : false;
      if (!this.isEdit) {
        fileData.id = new Date().getTime();
        fileData.creationTime = new Date();
        fileData.duration = 0;
        fileData.noteContent = '';
      } else {
        if (e.resourcesType == 'recorded') {
          this.activeName = 'recorded';
          this.$nextTick(() => {
            this.$refs.xmlRecordPlayer.setAudioPath(fileData.fileUrl, fileData.duration);
          });
        } else if (e.resourcesType == 'notes') {
          this.activeName = 'notes';
        } else {
          this.activeName = 'resource';
        }
        this.getFileList();
      }
      this.fileData = JSON.parse(JSON.stringify(fileData));
      this.recordedData = JSON.parse(JSON.stringify(fileData));
      this.noteData = JSON.parse(JSON.stringify(fileData));
    },
    setSingleCheckedValue(e, item, value) {
      item.isDownload = [value];
    },
    setSingleCheckedShowValue(e, item, value) {
      item.isStudentShow = [value];
      if (value == 1) {
        // 不显示添加资源
        console.log('不显示添加资源');
      }
    },
    //
    handleBeforeUpload(file) {
      const isPass = file.size / 1024 / 1024 > this.fileSize;
      if (isPass) {
        this.$message.error(`上传文件大小不能超过 ${this.fileSize}MB!`);
        return false;
      }
      let fileType = '';
      let fileExt = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
      console.log(fileExt);
      if (this.Accept.indexOf('.' + fileExt) === -1) {
        this.$message.error('请上传指定类型文件');
        return false;
      }
      this.loadingInstance = this.$loading({
        lock: true,
        text: 'Loading'
      });
      this.fileData.resourcesName = file.name;
      if (_image.indexOf(fileExt) !== -1) {
        fileType = 'image';
      } else if (_audio.indexOf(fileExt) !== -1) {
        fileType = 'audio';
      } else if (_video.indexOf(fileExt) !== -1) {
        fileType = 'video';
      } else if (_resources.indexOf(fileExt) !== -1) {
        fileType = 'resources';
      } else if (_office.indexOf(fileExt) !== -1) {
        fileType = 'office';
      }
      this.fileData.resourcesType = fileType;
      this.fileData.fileExt = fileExt;
      this.fileData.resourcesName = this.fileData.resourcesName.length > 50 ? this.fileData.resourcesName.slice(0, 50) : this.fileData.resourcesName;
    },
    // 上传成功
    handleSuccess(file, item) {
      item.fileUrl = file.data;
      this.getFileList();
      this.loadingInstance.close();
      console.log(item, 'item'); //有值
      console.log(this.fileData, 'this.fileData1111'); //有值
    },
    getFileList() {
      console.log(this.fileData, 'fileData'); //有值 再次编辑就无值，因为触发了close
      this.fileList = [{
        name: this.fileData.resourcesName,
        url: this.fileData.resourcesType == 'image' ? this.fileData.fileUrl : 'data:image/png;base64,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'
      }];
      this.fileList.length > 0 ? this.isSwhoUpload = true : this.isSwhoUpload = false;
    },
    handleRemove(file) {
      this.fileData.fileUrl = '';
      this.fileList = [];
      this.fileList.length > 0 ? this.isSwhoUpload = true : this.isSwhoUpload = false;
      console.log(file);
    },
    handlePictureCardPreview(file) {
      this.fileData.fileUrl = file.url;
      this.dialogVisible = true;
    },
    handleDownload(file) {
      console.log(file);
    },
    submit(type) {
      // if(this.activeName == 'resource' && !this.fileData.fileUrl){
      //   this.$message.error('请上传文件')
      //   return
      // }
      this.$refs[this.activeName + 'RuleForm'].validate(valid => {
        if (valid) {
          if (this.activeName == 'resource') {
            if (!this.fileData.fileUrl) {
              this.$message.warning("请上传资源文件");
            }
            // 添加资源文件
            let fileData = JSON.parse(JSON.stringify(this.fileData)) || {};
            let data = {
              chapterOrderNum: this.chapterOrderNum,
              ...fileData
            };
            data.isDownload = this.fileData.isDownload[0];
            data.isStudentShow = this.fileData.isStudentShow[0];
            // 发送数据
            if (!this.isEdit) {
              this.$emit('addResources', data);
            } else {
              this.$emit('editResources', data);
            }
            setTimeout(() => {
              this.close();
              this.show = false;
            }, 200);
          } else if (this.activeName == 'recorded') {
            // 录音
            if (this.recordedData.duration < 100) {
              this.$message.error('请先录制音频');
              return;
            }
            if (this.recordedBlob) {
              this.addRecorder();
            } else {
              let recordedData = JSON.parse(JSON.stringify(this.recordedData)) || {};
              let data = {
                chapterOrderNum: this.chapterOrderNum,
                ...recordedData
              };
              data.isDownload = this.recordedData.isDownload[0];
              data.isStudentShow = this.recordedData.isStudentShow[0];
              data.resourcesType = 'recorded';
              // 发送数据
              if (!this.isEdit) {
                this.$emit('addResources', data);
              } else {
                this.$emit('editResources', data);
              }
              setTimeout(() => {
                this.close();
                this.show = false;
              }, 800);
            }
          } else if (this.activeName == 'notes') {
            // 教师笔记
            let noteData = JSON.parse(JSON.stringify(this.noteData)) || {};
            let data = {
              chapterOrderNum: this.chapterOrderNum,
              ...noteData
            };
            data.isDownload = this.noteData.isDownload[0];
            data.isStudentShow = this.noteData.isStudentShow[0];
            data.resourcesType = 'notes';
            // 发送数据
            if (!this.isEdit) {
              this.$emit('addTeacherNote', data);
            } else {
              this.$emit('editResources', data);
            }
            setTimeout(() => {
              this.close();
              this.show = false;
            }, 200);
          }

          // if (type === 'continue') {
          //   // 若是点击“保存并继续添加”
          //   // 提示保存成功
          //   this.$message({
          //     message: '保存成功',
          //     type: 'success',
          //     duration: 1500
          //   })

          //   // 清空表单
          //   this.$refs[this.activeName + 'RuleForm'].resetFields()
          //   this.fileData.fileUrl = ''
          //   this.fileList = []
          //   this.isSwhoUpload = false

          //   // 不关闭弹窗
          //   this.show = true
          // } else {
          //   setTimeout(() => {
          //     this.close()
          //     this.show = false
          //   }, 200)
          // }
        }
      });
    },
    close() {
      this.fileData.resourcesName = '';
      this.fileData.isDownload = [0];
      if (this.fileData.resourcesType !== 'image') {
        this.fileData.fileUrl = '';
      }
      this.fileList = [];
      this.show = false;
      // 等待200毫秒，显示添加文件按钮
      setTimeout(() => this.isSwhoUpload = false, 200);
      if (this.activeName == 'recorded') {
        this.$refs.xmlRecordPlayer.destruction();
        this.$refs.xmlRecordPlayer.resetRecorder();
      }
    },
    getresdata({
      blob,
      duration
    }) {
      this.recordedData.duration = duration;
      this.recordedBlob = blob;
    },
    addRecorder() {
      const loading = this.$loading({
        lock: true,
        text: 'Loading'
      });
      let formData = new FormData();
      formData.append('file', this.recordedBlob, new Date().getTime() + '.mp3');
      for (let key in this.data) {
        formData.append(key, this.data[key]);
      }
      fetch(this.actionUrl, {
        method: 'post',
        body: formData,
        headers: this.headers
      }).then(data => {
        return data.text();
      }).then(res => {
        let urlData = JSON.parse(res);
        this.recordedData.fileUrl = urlData.data;
        let recordedData = JSON.parse(JSON.stringify(this.recordedData)) || {};
        let data = {
          chapterOrderNum: this.chapterOrderNum,
          ...recordedData
        };
        data.isDownload = this.recordedData.isDownload[0];
        data.isStudentShow = this.recordedData.isStudentShow[0];
        data.resourcesType = 'recorded';
        // 发送数据
        if (!this.isEdit) {
          this.$emit('addResources', data);
        } else {
          this.$emit('editResources', data);
        }
        setTimeout(() => {
          loading.close();
          this.close();
          this.show = false;
        }, 800);
      }).catch(() => {
        loading.close();
        this.close();
        this.show = false;
      });
    },
    carriageReturn(event) {
      event.preventDefault(); // 更推荐使用preventDefault来阻止默认行为，适用于现代浏览器
      if (event.keyCode === 13 || event.key === 'Enter' || event.code === 'Enter') {
        this.noteData.noteContent += '\n';
      }
    }
  },
  computed: {
    currentLength() {
      return this.noteData.noteContent.length;
    },
    maxLength() {
      return 1000;
    }
  },
  created() {},
  mounted() {}
};

/* script */
const __vue_script__$5y = script$5y;
/* template */
var __vue_render__$5y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      staticClass: "add-teacher-resources-dialog",
      attrs: { "append-to-body": "", visible: _vm.show, width: "665px" },
      on: {
        "update:visible": function ($event) {
          _vm.show = $event;
        },
        close: _vm.close,
      },
    },
    [
      _c("div", { attrs: { slot: "title" }, slot: "title" }, [
        _vm._v(_vm._s(_vm.isEdit ? "编辑" : "添加") + "授课资源"),
      ]),
      _vm._v(" "),
      _c(
        "div",
        [
          _c(
            "el-tabs",
            {
              model: {
                value: _vm.activeName,
                callback: function ($$v) {
                  _vm.activeName = $$v;
                },
                expression: "activeName",
              },
            },
            [
              _c(
                "el-tab-pane",
                {
                  attrs: {
                    disabled: _vm.isEdit,
                    label: "资源上传",
                    name: "resource",
                  },
                },
                [
                  _c(
                    "div",
                    { staticClass: "add-teacher-container" },
                    [
                      _c(
                        "el-form",
                        {
                          ref: "resourceRuleForm",
                          attrs: {
                            "label-position": "left",
                            model: _vm.fileData,
                            rules: _vm.fileRules,
                          },
                        },
                        [
                          _c(
                            "el-form-item",
                            {
                              staticClass: "resouceName addOne",
                              attrs: {
                                "label-width": "96px",
                                label: "资源名称：",
                                prop: "resourcesName",
                              },
                            },
                            [
                              _c("el-input", {
                                staticClass: "inputresource",
                                attrs: { maxlength: "50" },
                                model: {
                                  value: _vm.fileData.resourcesName,
                                  callback: function ($$v) {
                                    _vm.$set(
                                      _vm.fileData,
                                      "resourcesName",
                                      typeof $$v === "string" ? $$v.trim() : $$v
                                    );
                                  },
                                  expression: "fileData.resourcesName",
                                },
                              }),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _c(
                            "el-form-item",
                            {
                              attrs: {
                                "label-width": "96px",
                                label: "下载设置：",
                                prop: "isDownload",
                              },
                            },
                            [
                              _c(
                                "el-checkbox-group",
                                {
                                  model: {
                                    value: _vm.fileData.isDownload,
                                    callback: function ($$v) {
                                      _vm.$set(_vm.fileData, "isDownload", $$v);
                                    },
                                    expression: "fileData.isDownload",
                                  },
                                },
                                [
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 1 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedValue(
                                            v,
                                            _vm.fileData,
                                            1
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("允许学生下载")]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 0 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedValue(
                                            v,
                                            _vm.fileData,
                                            0
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("不允许学生下载")]
                                  ),
                                ],
                                1
                              ),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _c(
                            "el-form-item",
                            {
                              attrs: {
                                "label-width": "96px",
                                label: "显示设置：",
                                prop: "isStudentShow",
                              },
                            },
                            [
                              _c(
                                "el-checkbox-group",
                                {
                                  model: {
                                    value: _vm.fileData.isStudentShow,
                                    callback: function ($$v) {
                                      _vm.$set(
                                        _vm.fileData,
                                        "isStudentShow",
                                        $$v
                                      );
                                    },
                                    expression: "fileData.isStudentShow",
                                  },
                                },
                                [
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 0 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.fileData,
                                            0
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生可见")]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 1 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.fileData,
                                            1
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生不可见")]
                                  ),
                                ],
                                1
                              ),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _c(
                            "el-form-item",
                            { attrs: { label: "文件上传：", prop: "fileUrl" } },
                            [
                              _c(
                                "el-upload",
                                {
                                  staticClass: "upload-demo",
                                  class: _vm.isSwhoUpload ? "disUoloadSty" : "",
                                  attrs: {
                                    action: _vm.actionUrl,
                                    headers: _vm.headers,
                                    data: _vm.data,
                                    "list-type": "picture-card",
                                    multiple: false,
                                    limit: _vm.limit,
                                    "file-list": _vm.fileList,
                                    playMode: "xun-huan",
                                    scheduleControl: "yes",
                                    accept: _vm.Accept,
                                    "on-success": function (e) {
                                      return _vm.handleSuccess(e, _vm.fileData)
                                    },
                                    "before-upload": _vm.handleBeforeUpload,
                                    "on-remove": _vm.handleRemove,
                                  },
                                },
                                [
                                  _c("i", {
                                    staticClass: "el-icon-plus",
                                    attrs: { slot: "default" },
                                    slot: "default",
                                  }),
                                ]
                              ),
                            ],
                            1
                          ),
                        ],
                        1
                      ),
                    ],
                    1
                  ),
                ]
              ),
              _vm._v(" "),
              _c(
                "el-tab-pane",
                {
                  attrs: {
                    disabled: _vm.isEdit,
                    label: "录音",
                    name: "recorded",
                  },
                },
                [
                  _c(
                    "div",
                    { staticClass: "add-teacher-container" },
                    [
                      _c(
                        "el-form",
                        {
                          ref: "recordedRuleForm",
                          attrs: {
                            "label-position": "left",
                            model: _vm.recordedData,
                            rules: _vm.recordedRules,
                          },
                        },
                        [
                          _c("el-form-item", [
                            _c(
                              "div",
                              {
                                staticClass:
                                  "parent-group xml-single-recorder-group",
                              },
                              [
                                _c(
                                  "div",
                                  { staticClass: "xml-recorder-container" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        staticClass: "xml-recorder-player-box",
                                      },
                                      [
                                        _c("record-player", {
                                          ref: "xmlRecordPlayer",
                                          attrs: {
                                            mode: "preview",
                                            loop: false,
                                            "play-mode": "xun-huan",
                                            "play-style": "A",
                                            scheduleControl: "yes",
                                          },
                                          on: { resData: _vm.getresdata },
                                        }),
                                      ],
                                      1
                                    ),
                                  ]
                                ),
                              ]
                            ),
                          ]),
                          _vm._v(" "),
                          _c(
                            "el-form-item",
                            {
                              attrs: {
                                "label-width": "96px",
                                label: "显示设置：",
                                prop: "isStudentShow",
                              },
                            },
                            [
                              _c(
                                "el-checkbox-group",
                                {
                                  model: {
                                    value: _vm.recordedData.isStudentShow,
                                    callback: function ($$v) {
                                      _vm.$set(
                                        _vm.recordedData,
                                        "isStudentShow",
                                        $$v
                                      );
                                    },
                                    expression: "recordedData.isStudentShow",
                                  },
                                },
                                [
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 0 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.recordedData,
                                            0
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生可见")]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 1 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.recordedData,
                                            1
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生不可见")]
                                  ),
                                ],
                                1
                              ),
                            ],
                            1
                          ),
                        ],
                        1
                      ),
                    ],
                    1
                  ),
                ]
              ),
              _vm._v(" "),
              _c(
                "el-tab-pane",
                {
                  attrs: {
                    disabled: _vm.isEdit,
                    label: "教师笔记",
                    name: "notes",
                  },
                },
                [
                  _c(
                    "div",
                    { staticClass: "add-teacher-container" },
                    [
                      _c(
                        "el-form",
                        {
                          ref: "notesRuleForm",
                          attrs: {
                            "label-position": "left",
                            model: _vm.noteData,
                            rules: _vm.noteRules,
                          },
                        },
                        [
                          _c(
                            "el-form-item",
                            {
                              attrs: { label: "笔记内容", prop: "noteContent" },
                            },
                            [
                              _c("el-input", {
                                attrs: {
                                  type: "textarea",
                                  rows: 6,
                                  placeholder: "请输入内容",
                                  maxlength: "1000",
                                },
                                nativeOn: {
                                  keydown: function ($event) {
                                    if (
                                      !$event.type.indexOf("key") &&
                                      _vm._k(
                                        $event.keyCode,
                                        "enter",
                                        13,
                                        $event.key,
                                        "Enter"
                                      )
                                    ) {
                                      return null
                                    }
                                    return _vm.carriageReturn($event)
                                  },
                                },
                                model: {
                                  value: _vm.noteData.noteContent,
                                  callback: function ($$v) {
                                    _vm.$set(
                                      _vm.noteData,
                                      "noteContent",
                                      typeof $$v === "string" ? $$v.trim() : $$v
                                    );
                                  },
                                  expression: "noteData.noteContent",
                                },
                              }),
                              _vm._v(" "),
                              _c("span", { staticClass: "character-count" }, [
                                _vm._v(
                                  _vm._s(_vm.currentLength) +
                                    " / " +
                                    _vm._s(_vm.maxLength)
                                ),
                              ]),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _c(
                            "el-form-item",
                            {
                              attrs: {
                                "label-width": "96px",
                                label: "显示设置",
                                prop: "isStudentShow",
                              },
                            },
                            [
                              _c(
                                "el-checkbox-group",
                                {
                                  model: {
                                    value: _vm.noteData.isStudentShow,
                                    callback: function ($$v) {
                                      _vm.$set(
                                        _vm.noteData,
                                        "isStudentShow",
                                        $$v
                                      );
                                    },
                                    expression: "noteData.isStudentShow",
                                  },
                                },
                                [
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 0 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.noteData,
                                            0
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生可见")]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "el-checkbox",
                                    {
                                      attrs: { label: 1 },
                                      on: {
                                        change: function (v) {
                                          return _vm.setSingleCheckedShowValue(
                                            v,
                                            _vm.noteData,
                                            1
                                          )
                                        },
                                      },
                                    },
                                    [_vm._v("学生不可见")]
                                  ),
                                ],
                                1
                              ),
                            ],
                            1
                          ),
                        ],
                        1
                      ),
                    ],
                    1
                  ),
                ]
              ),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "bottom" },
            [
              _c(
                "el-button",
                {
                  attrs: { size: "small" },
                  on: {
                    click: function ($event) {
                      return _vm.close()
                    },
                  },
                },
                [_vm._v("取消")]
              ),
              _vm._v(" "),
              _c(
                "el-button",
                {
                  attrs: { size: "small", type: "primary" },
                  on: {
                    click: function ($event) {
                      return _vm.submit("")
                    },
                  },
                },
                [_vm._v("确认")]
              ),
            ],
            1
          ),
        ],
        1
      ),
    ]
  )
};
var __vue_staticRenderFns__$5y = [];
__vue_render__$5y._withStripped = true;

  /* style */
  const __vue_inject_styles__$5y = undefined;
  /* scoped */
  const __vue_scope_id__$5y = undefined;
  /* module identifier */
  const __vue_module_identifier__$5y = undefined;
  /* functional template */
  const __vue_is_functional_template__$5y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5y, staticRenderFns: __vue_staticRenderFns__$5y },
    __vue_inject_styles__$5y,
    __vue_script__$5y,
    __vue_scope_id__$5y,
    __vue_is_functional_template__$5y,
    __vue_module_identifier__$5y,
    false,
    undefined,
    undefined,
    undefined
  );

var setupDefaults = {
  cookies: {
    path: '/'
  },
  treeOptions: {
    parentKey: 'parentId',
    key: 'id',
    children: 'children'
  },
  parseDateFormat: 'yyyy-MM-dd HH:mm:ss',
  firstDayOfWeek: 1
};
var setupDefaults_1 = setupDefaults;

function arrayEach(list, iterate, context) {
  if (list) {
    if (list.forEach) {
      list.forEach(iterate, context);
    } else {
      for (var index = 0, len = list.length; index < len; index++) {
        iterate.call(context, list[index], index, list);
      }
    }
  }
}
var arrayEach_1 = arrayEach;

var objectToString = Object.prototype.toString;
var staticObjectToString = objectToString;

function helperCreateInInObjectString(type) {
  return function (obj) {
    return '[object ' + type + ']' === staticObjectToString.call(obj);
  };
}
var helperCreateInInObjectString_1 = helperCreateInInObjectString;

/**
  * 判断是否数组
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isArray$1 = Array.isArray || helperCreateInInObjectString_1('Array');
var isArray_1 = isArray$1;

/**
  * 判断对象自身属性中是否具有指定的属性
  *
  * @param {Object} obj 对象
  * @param {String/Number} key 键值
  * @return {Boolean}
  */
function hasOwnProp(obj, key) {
  return obj && obj.hasOwnProperty ? obj.hasOwnProperty(key) : false;
}
var hasOwnProp_1 = hasOwnProp;

function objectEach(obj, iterate, context) {
  if (obj) {
    for (var key in obj) {
      if (hasOwnProp_1(obj, key)) {
        iterate.call(context, obj[key], key, obj);
      }
    }
  }
}
var objectEach_1 = objectEach;

/**
  * 迭代器
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
function each$1(obj, iterate, context) {
  if (obj) {
    return (isArray_1(obj) ? arrayEach_1 : objectEach_1)(obj, iterate, context);
  }
  return obj;
}
var each_1 = each$1;

/* eslint-disable valid-typeof */
function helperCreateInTypeof(type) {
  return function (obj) {
    return typeof obj === type;
  };
}
var helperCreateInTypeof_1 = helperCreateInTypeof;

/**
  * 判断是否方法
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isFunction$1 = helperCreateInTypeof_1('function');
var isFunction_1$1 = isFunction$1;

function helperCreateGetObjects(name, getIndex) {
  var proMethod = Object[name];
  return function (obj) {
    var result = [];
    if (obj) {
      if (proMethod) {
        return proMethod(obj);
      }
      each_1(obj, getIndex > 1 ? function (key) {
        result.push(['' + key, obj[key]]);
      } : function () {
        result.push(arguments[getIndex]);
      });
    }
    return result;
  };
}
var helperCreateGetObjects_1 = helperCreateGetObjects;

/**
  * 获取对象所有属性
  *
  * @param {Object} obj 对象/数组
  * @return {Array}
  */
var keys$1 = helperCreateGetObjects_1('keys', 1);
var keys_1 = keys$1;

function getCativeCtor(val, args) {
  var Ctor = val.__proto__.constructor;
  return args ? new Ctor(args) : new Ctor();
}
function handleValueClone(item, isDeep) {
  return isDeep ? copyValue(item, isDeep) : item;
}
function copyValue(val, isDeep) {
  if (val) {
    switch (staticObjectToString.call(val)) {
      case "[object Object]":
        {
          var restObj = Object.create(Object.getPrototypeOf(val));
          objectEach_1(val, function (item, key) {
            restObj[key] = handleValueClone(item, isDeep);
          });
          return restObj;
        }
      case "[object Date]":
      case "[object RegExp]":
        {
          return getCativeCtor(val, val.valueOf());
        }
      case "[object Array]":
      case "[object Arguments]":
        {
          var restArr = [];
          arrayEach_1(val, function (item) {
            restArr.push(handleValueClone(item, isDeep));
          });
          return restArr;
        }
      case "[object Set]":
        {
          var restSet = getCativeCtor(val);
          restSet.forEach(function (item) {
            restSet.add(handleValueClone(item, isDeep));
          });
          return restSet;
        }
      case "[object Map]":
        {
          var restMap = getCativeCtor(val);
          restMap.forEach(function (item, key) {
            restMap.set(key, handleValueClone(item, isDeep));
          });
          return restMap;
        }
    }
  }
  return val;
}

/**
  * 浅拷贝/深拷贝
  *
  * @param {Object} obj 对象/数组
  * @param {Boolean} deep 是否深拷贝
  * @return {Object}
  */
function clone(obj, deep) {
  if (obj) {
    return copyValue(obj, deep);
  }
  return obj;
}
var clone_1 = clone;

var objectAssignFns = Object.assign;
function handleAssign(destination, args, isClone) {
  var len = args.length;
  for (var source, index = 1; index < len; index++) {
    source = args[index];
    arrayEach_1(keys_1(args[index]), isClone ? function (key) {
      destination[key] = clone_1(source[key], isClone);
    } : function (key) {
      destination[key] = source[key];
    });
  }
  return destination;
}

/**
  * 将一个或多个源对象复制到目标对象中
  *
  * @param {Object} target 目标对象
  * @param {...Object}
  * @return {Boolean}
  */
var assign$2 = function (target) {
  if (target) {
    var args = arguments;
    if (target === true) {
      if (args.length > 1) {
        target = isArray_1(target[1]) ? [] : {};
        return handleAssign(target, args, true);
      }
    } else {
      return objectAssignFns ? objectAssignFns.apply(Object, args) : handleAssign(target, args);
    }
  }
  return target;
};
var assign_1 = assign$2;

var XEUtils = function () {};
function mixin() {
  arrayEach_1(arguments, function (methods) {
    each_1(methods, function (fn, name) {
      XEUtils[name] = isFunction_1$1(fn) ? function () {
        var result = fn.apply(XEUtils.$context, arguments);
        XEUtils.$context = null;
        return result;
      } : fn;
    });
  });
}
function setup(options) {
  return assign_1(setupDefaults_1, options);
}
XEUtils.VERSION = '3.5.29';
XEUtils.mixin = mixin;
XEUtils.setup = setup;
var ctor = XEUtils;

function lastArrayEach(obj, iterate, context) {
  for (var len = obj.length - 1; len >= 0; len--) {
    iterate.call(context, obj[len], len, obj);
  }
}
var lastArrayEach_1 = lastArrayEach;

function lastObjectEach(obj, iterate, context) {
  lastArrayEach_1(keys_1(obj), function (key) {
    iterate.call(context, obj[key], key, obj);
  });
}
var lastObjectEach_1 = lastObjectEach;

/**
  * 判断是否为Null
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isNull(obj) {
  return obj === null;
}
var isNull_1 = isNull;

/**
 * 返回一个获取对象属性的函数
 *
 * @param {String} name 属性名
 * @param {Object} defs 空值
 */
function property$1(name, defs) {
  return function (obj) {
    return isNull_1(obj) ? defs : obj[name];
  };
}
var property_1 = property$1;

/**
  * 指定方法后的返回值组成的新对象
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
function objectMap(obj, iterate, context) {
  var result = {};
  if (obj) {
    if (iterate) {
      if (!isFunction_1$1(iterate)) {
        iterate = property_1(iterate);
      }
      each_1(obj, function (val, index) {
        result[index] = iterate.call(context, val, index, obj);
      });
    } else {
      return obj;
    }
  }
  return result;
}
var objectMap_1 = objectMap;

/**
  * 判断是否对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isPlainObject(obj) {
  return obj ? obj.constructor === Object : false;
}
var isPlainObject_1 = isPlainObject;

function handleMerge(target, source) {
  if (isPlainObject_1(target) && isPlainObject_1(source) || isArray_1(target) && isArray_1(source)) {
    each_1(source, function (obj, key) {
      target[key] = handleMerge(target[key], obj);
    });
    return target;
  }
  return source;
}

/**
  * 将一个或多个源对象合并到目标对象中
  *
  * @param {Object} target 目标对象
  * @param {...Object}
  * @return {Boolean}
  */
var merge = function (target) {
  if (!target) {
    target = {};
  }
  var args = arguments;
  var len = args.length;
  for (var source, index = 1; index < len; index++) {
    source = args[index];
    if (source) {
      handleMerge(target, source);
    }
  }
  return target;
};
var merge_1 = merge;

/**
  * 指定方法后的返回值组成的新数组
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Array}
  */
function map(obj, iterate, context) {
  var result = [];
  if (obj && arguments.length > 1) {
    if (obj.map) {
      return obj.map(iterate, context);
    } else {
      each_1(obj, function () {
        result.push(iterate.apply(context, arguments));
      });
    }
  }
  return result;
}
var map_1 = map;

function helperCreateIterateHandle(prop, useArray, restIndex, matchValue, defaultValue) {
  return function (obj, iterate, context) {
    if (obj && iterate) {
      if (prop && obj[prop]) {
        return obj[prop](iterate, context);
      } else {
        if (useArray && isArray_1(obj)) {
          for (var index = 0, len = obj.length; index < len; index++) {
            if (!!iterate.call(context, obj[index], index, obj) === matchValue) {
              return [true, false, index, obj[index]][restIndex];
            }
          }
        } else {
          for (var key in obj) {
            if (hasOwnProp_1(obj, key)) {
              if (!!iterate.call(context, obj[key], key, obj) === matchValue) {
                return [true, false, key, obj[key]][restIndex];
              }
            }
          }
        }
      }
    }
    return defaultValue;
  };
}
var helperCreateIterateHandle_1 = helperCreateIterateHandle;

/**
  * 对象中的值中的每一项运行给定函数,如果函数对任一项返回true,则返回true,否则返回false
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Boolean}
  */
var some = helperCreateIterateHandle_1('some', 1, 0, true, false);
var some_1 = some;

/**
  * 对象中的值中的每一项运行给定函数,如果该函数对每一项都返回true,则返回true,否则返回false
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Boolean}
  */
var every = helperCreateIterateHandle_1('every', 1, 1, false, true);
var every_1 = every;

/**
  * 判断对象是否包含该值,成功返回true否则false
  *
  * @param {Object} obj 对象
  * @param {Object} val 值
  * @return {Boolean}
  */
function includes(obj, val) {
  if (obj) {
    if (obj.includes) {
      return obj.includes(val);
    }
    for (var key in obj) {
      if (hasOwnProp_1(obj, key)) {
        if (val === obj[key]) {
          return true;
        }
      }
    }
  }
  return false;
}
var includes_1 = includes;

/**
  * 判断数组是否包含另一数组
  *
  * @param {Array} array1 数组
  * @param {Array} array2 被包含数组
  * @return {Boolean}
  */
function includeArrays(array1, array2) {
  var len;
  var index = 0;
  if (isArray_1(array1) && isArray_1(array2)) {
    for (len = array2.length; index < len; index++) {
      if (!includes_1(array1, array2[index])) {
        return false;
      }
    }
    return true;
  }
  return includes_1(array1, array2);
}
var includeArrays_1 = includeArrays;

/**
 * 数组去重
 * 
 * @param {*} array  数组
 * @param {*} iterate 字段或回调
 * @param {*} context 
 * @returns 
 */
function uniq(array, iterate, context) {
  var result = [];
  if (iterate) {
    if (!isFunction_1$1(iterate)) {
      iterate = property_1(iterate);
    }
    var val,
      valMap = {};
    each_1(array, function (item, key) {
      val = iterate.call(context, item, key, array);
      if (!valMap[val]) {
        valMap[val] = 1;
        result.push(item);
      }
    });
  } else {
    each_1(array, function (value) {
      if (!includes_1(result, value)) {
        result.push(value);
      }
    });
  }
  return result;
}
var uniq_1 = uniq;

/**
 * 将对象或者伪数组转为新数组
 *
 * @param {Array} list 数组
 * @return {Array}
 */
function toArray(list) {
  return map_1(list, function (item) {
    return item;
  });
}
var toArray_1 = toArray;

/**
  * 将多个数的值返回唯一的并集数组
  *
  * @param {...Array} 数组
  * @return {Array}
  */
function union() {
  var args = arguments;
  var result = [];
  var index = 0;
  var len = args.length;
  for (; index < len; index++) {
    result = result.concat(toArray_1(args[index]));
  }
  return uniq_1(result);
}
var union_1 = union;

var staticStrUndefined = 'undefined';
var staticStrUndefined_1 = staticStrUndefined;

/**
  * 判断是否Undefined
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isUndefined = helperCreateInTypeof_1(staticStrUndefined_1);
var isUndefined_1 = isUndefined;

/**
 * 判断是否 undefined 和 null
 * @param {Object} obj 对象
 * @return {Boolean}
 */
function eqNull(obj) {
  return isNull_1(obj) || isUndefined_1(obj);
}
var eqNull_1 = eqNull;

var staticHGKeyRE = /(.+)?\[(\d+)\]$/;
var staticHGKeyRE_1 = staticHGKeyRE;

function helperGetHGSKeys(property) {
  // 以最快的方式判断数组，可忽略准确性
  return property ? property.splice && property.join ? property : ('' + property).replace(/(\[\d+\])\.?/g, '$1.').replace(/\.$/, '').split('.') : [];
}
var helperGetHGSKeys_1 = helperGetHGSKeys;

/**
 * 获取对象的属性的值，如果值为 undefined，则返回默认值
 * @param {Object/Array} obj 对象
 * @param {String/Function} property 键、路径
 * @param {Object} defaultValue 默认值
 * @return {Object}
 */
function get(obj, property, defaultValue) {
  if (eqNull_1(obj)) {
    return defaultValue;
  }
  var result = getValueByPath(obj, property);
  return isUndefined_1(result) ? defaultValue : result;
}
function getDeepProps(obj, key) {
  var matchs = key ? key.match(staticHGKeyRE_1) : '';
  return matchs ? matchs[1] ? obj[matchs[1]] ? obj[matchs[1]][matchs[2]] : undefined : obj[matchs[2]] : obj[key];
}
function getValueByPath(obj, property) {
  if (obj) {
    var rest, props, len;
    var index = 0;
    if (obj[property] || hasOwnProp_1(obj, property)) {
      return obj[property];
    } else {
      props = helperGetHGSKeys_1(property);
      len = props.length;
      if (len) {
        for (rest = obj; index < len; index++) {
          rest = getDeepProps(rest, props[index]);
          if (eqNull_1(rest)) {
            if (index === len - 1) {
              return rest;
            }
            return;
          }
        }
      }
      return rest;
    }
  }
}
var get_1 = get;

var ORDER_PROP_ASC = 'asc';
var ORDER_PROP_DESC = 'desc';

// function handleSort (v1, v2) {
//   return v1 > v2 ? 1 : -1
// }

// '' < 数字 < 字符 < null < undefined
function handleSort(v1, v2) {
  if (isUndefined_1(v1)) {
    return 1;
  }
  if (isNull_1(v1)) {
    return isUndefined_1(v2) ? -1 : 1;
  }
  return v1 && v1.localeCompare ? v1.localeCompare(v2) : v1 > v2 ? 1 : -1;
}
function buildMultiOrders(name, confs, compares) {
  return function (item1, item2) {
    var v1 = item1[name];
    var v2 = item2[name];
    if (v1 === v2) {
      return compares ? compares(item1, item2) : 0;
    }
    return confs.order === ORDER_PROP_DESC ? handleSort(v2, v1) : handleSort(v1, v2);
  };
}
function getSortConfs(arr, list, fieldConfs, context) {
  var sortConfs = [];
  fieldConfs = isArray_1(fieldConfs) ? fieldConfs : [fieldConfs];
  arrayEach_1(fieldConfs, function (handle, index) {
    if (handle) {
      var field = handle;
      var order;
      if (isArray_1(handle)) {
        field = handle[0];
        order = handle[1];
      } else if (isPlainObject_1(handle)) {
        field = handle.field;
        order = handle.order;
      }
      sortConfs.push({
        field: field,
        order: order || ORDER_PROP_ASC
      });
      arrayEach_1(list, isFunction_1$1(field) ? function (item, key) {
        item[index] = field.call(context, item.data, key, arr);
      } : function (item) {
        item[index] = field ? get_1(item.data, field) : item.data;
      });
    }
  });
  return sortConfs;
}

/**
  * 将数组进行排序
  *
  * @param {Array} arr 数组
  * @param {Function/String/Array} fieldConfs 方法或属性
  * @param {Object} context 上下文
  * @return {Array}
  */
function orderBy(arr, fieldConfs, context) {
  if (arr) {
    if (eqNull_1(fieldConfs)) {
      return toArray_1(arr).sort(handleSort);
    }
    var compares;
    var list = map_1(arr, function (item) {
      return {
        data: item
      };
    });
    var sortConfs = getSortConfs(arr, list, fieldConfs, context);
    var len = sortConfs.length - 1;
    while (len >= 0) {
      compares = buildMultiOrders(len, sortConfs[len], compares);
      len--;
    }
    if (compares) {
      list = list.sort(compares);
    }
    return map_1(list, property_1('data'));
  }
  return [];
}
var orderBy_1 = orderBy;

var sortBy = orderBy_1;
var sortBy_1 = sortBy;

/**
  * 获取一个指定范围内随机数
  *
  * @param {Number} minVal 最小值
  * @param {Number} maxVal 最大值
  * @return {Number}
  */
function random(minVal, maxVal) {
  return minVal >= maxVal ? minVal : (minVal = minVal >> 0) + Math.round(Math.random() * ((maxVal || 9) - minVal));
}
var random_1 = random;

/**
  * 获取对象所有值
  *
  * @param {Object} obj 对象/数组
  * @return {Array}
  */
var values = helperCreateGetObjects_1('values', 0);
var values_1 = values;

/**
  * 将一个数组随机打乱，返回一个新的数组
  *
  * @param {Array} array 数组
  * @return {Array}
  */
function shuffle(array) {
  var index;
  var result = [];
  var list = values_1(array);
  var len = list.length - 1;
  for (; len >= 0; len--) {
    index = len > 0 ? random_1(0, len) : 0;
    result.push(list[index]);
    list.splice(index, 1);
  }
  return result;
}
var shuffle_1 = shuffle;

/**
  * 从一个数组中随机返回几个元素
  *
  * @param {Array} array 数组
  * @param {Number} number 个数
  * @return {Array}
  */
function sample(array, number) {
  var result = shuffle_1(array);
  if (arguments.length <= 1) {
    return result[0];
  }
  if (number < result.length) {
    result.length = number || 0;
  }
  return result;
}
var sample_1 = sample;

function helperCreateToNumber(handle) {
  return function (str) {
    if (str) {
      var num = handle(str && str.replace ? str.replace(/,/g, '') : str);
      if (!isNaN(num)) {
        return num;
      }
    }
    return 0;
  };
}
var helperCreateToNumber_1 = helperCreateToNumber;

/**
 * 转数值
 * @param { String/Number } str 数值
 *
 * @return {Number}
 */
var toNumber = helperCreateToNumber_1(parseFloat);
var toNumber_1 = toNumber;

/**
 * 裁剪 Arguments 或数组 array，从 start 位置开始到 end 结束，但不包括 end 本身的位置
 * @param {Array/Arguments} array 数组或Arguments
 * @param {Number} startIndex 开始索引
 * @param {Number} endIndex 结束索引
 */
function slice$1(array, startIndex, endIndex) {
  var result = [];
  var argsSize = arguments.length;
  if (array) {
    startIndex = argsSize >= 2 ? toNumber_1(startIndex) : 0;
    endIndex = argsSize >= 3 ? toNumber_1(endIndex) : array.length;
    if (array.slice) {
      return array.slice(startIndex, endIndex);
    }
    for (; startIndex < endIndex; startIndex++) {
      result.push(array[startIndex]);
    }
  }
  return result;
}
var slice_1 = slice$1;

/**
  * 根据回调过滤数据
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
function filter(obj, iterate, context) {
  var result = [];
  if (obj && iterate) {
    if (obj.filter) {
      return obj.filter(iterate, context);
    }
    each_1(obj, function (val, key) {
      if (iterate.call(context, val, key, obj)) {
        result.push(val);
      }
    });
  }
  return result;
}
var filter_1 = filter;

/**
  * 查找匹配第一条数据的键
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
var findKey = helperCreateIterateHandle_1('', 0, 2, true);
var findKey_1 = findKey;

/**
  * 从左至右遍历，匹配最近的一条数据
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
var find = helperCreateIterateHandle_1('find', 1, 3, true);
var find_1 = find;

/**
  * 从右至左遍历，匹配最近的一条数据
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
function findLast(obj, iterate, context) {
  if (obj) {
    if (!isArray_1(obj)) {
      obj = values_1(obj);
    }
    for (var len = obj.length - 1; len >= 0; len--) {
      if (iterate.call(context, obj[len], len, obj)) {
        return obj[len];
      }
    }
  }
}
var findLast_1 = findLast;

/**
  * 接收一个函数作为累加器，数组中的每个值（从左到右）开始合并，最终为一个值。
  *
  * @param {Array} array 数组
  * @param {Function} callback 方法
  * @param {Object} initialValue 初始值
  * @return {Number}
  */
function reduce$1(array, callback, initialValue) {
  if (array) {
    var len, reduceMethod;
    var index = 0;
    var context = null;
    var previous = initialValue;
    var isInitialVal = arguments.length > 2;
    var keyList = keys_1(array);
    if (array.length && array.reduce) {
      reduceMethod = function () {
        return callback.apply(context, arguments);
      };
      if (isInitialVal) {
        return array.reduce(reduceMethod, previous);
      }
      return array.reduce(reduceMethod);
    }
    if (isInitialVal) {
      index = 1;
      previous = array[keyList[0]];
    }
    for (len = keyList.length; index < len; index++) {
      previous = callback.call(context, previous, array[keyList[index]], index, array);
    }
    return previous;
  }
}
var reduce_1 = reduce$1;

/**
  * 浅复制数组的一部分到同一数组中的另一个位置,数组大小不变
  *
  * @param {Array} array 数组
  * @param {Number} target 从该位置开始替换数据
  * @param {Number} start 从该位置开始读取数据，默认为 0 。如果为负值，表示倒数
  * @param {Number} end 到该位置前停止读取数据，默认等于数组长度。如果为负值，表示倒数
  * @return {Array}
  */
function copyWithin(array, target, start, end) {
  if (isArray_1(array) && array.copyWithin) {
    return array.copyWithin(target, start, end);
  }
  var replaceIndex, replaceArray;
  var targetIndex = target >> 0;
  var startIndex = start >> 0;
  var len = array.length;
  var endIndex = arguments.length > 3 ? end >> 0 : len;
  if (targetIndex < len) {
    targetIndex = targetIndex >= 0 ? targetIndex : len + targetIndex;
    if (targetIndex >= 0) {
      startIndex = startIndex >= 0 ? startIndex : len + startIndex;
      endIndex = endIndex >= 0 ? endIndex : len + endIndex;
      if (startIndex < endIndex) {
        for (replaceIndex = 0, replaceArray = array.slice(startIndex, endIndex); targetIndex < len; targetIndex++) {
          if (replaceArray.length <= replaceIndex) {
            break;
          }
          array[targetIndex] = replaceArray[replaceIndex++];
        }
      }
    }
  }
  return array;
}
var copyWithin_1 = copyWithin;

/**
  * 将一个数组分割成大小的组。如果数组不能被平均分配，那么最后一块将是剩下的元素
  *
  * @param {Array} array 数组
  * @param {Number} size 每组大小
  * @return {Array}
  */
function chunk(array, size) {
  var index;
  var result = [];
  var arrLen = size >> 0 || 1;
  if (isArray_1(array)) {
    if (arrLen >= 0 && array.length > arrLen) {
      index = 0;
      while (index < array.length) {
        result.push(array.slice(index, index + arrLen));
        index += arrLen;
      }
    } else {
      result = array.length ? [array] : array;
    }
  }
  return result;
}
var chunk_1 = chunk;

/**
  * 获取数组对象中某属性值，返回一个数组
  *
  * @param {Array} array 数组
  * @param {String} key 属性值
  * @return {Array}
  */
function pluck(obj, key) {
  return map_1(obj, property_1(key));
}
var pluck_1 = pluck;

function helperCreateMinMax(handle) {
  return function (arr, iterate) {
    if (arr && arr.length) {
      var rest, itemIndex;
      arrayEach_1(arr, function (itemVal, index) {
        if (iterate) {
          itemVal = isFunction_1$1(iterate) ? iterate(itemVal, index, arr) : get_1(itemVal, iterate);
        }
        if (!eqNull_1(itemVal) && (eqNull_1(rest) || handle(rest, itemVal))) {
          itemIndex = index;
          rest = itemVal;
        }
      });
      return arr[itemIndex];
    }
    return rest;
  };
}
var helperCreateMinMax_1 = helperCreateMinMax;

/**
  * 获取最大值
  *
  * @param {Array} arr 数组
  * @param {Function} iterate(item, index, obj) 回调
  * @return {Number}
  */
var max$3 = helperCreateMinMax_1(function (rest, itemVal) {
  return rest < itemVal;
});
var max_1 = max$3;

/**
 * 与 zip 相反
 *
 * @param {Array} arrays 数组集合
 */
function unzip(arrays) {
  var index, maxItem, len;
  var result = [];
  if (arrays && arrays.length) {
    index = 0;
    maxItem = max_1(arrays, function (item) {
      return item ? item.length : 0;
    });
    for (len = maxItem ? maxItem.length : 0; index < len; index++) {
      result.push(pluck_1(arrays, index));
    }
  }
  return result;
}
var unzip_1 = unzip;

/**
 * 将每个数组中相应位置的值合并在一起
 *
 * @param {Array*} array 数组
 */
function zip() {
  return unzip_1(arguments);
}
var zip_1 = zip;

/**
 * 根据键数组、值数组对转换为对象
 *
 * @param {Array} props 键数组
 * @param {Number} arr 值数组
 * @return {Object}
 */
function zipObject(props, arr) {
  var result = {};
  arr = arr || [];
  each_1(values_1(props), function (val, key) {
    result[val] = arr[key];
  });
  return result;
}
var zipObject_1 = zipObject;

function flattenDeep(array, deep) {
  var result = [];
  arrayEach_1(array, function (vals) {
    result = result.concat(isArray_1(vals) ? deep ? flattenDeep(vals, deep) : vals : [vals]);
  });
  return result;
}

/**
  * 将一个多维数组铺平
  * @param {Array} array 数组
  * @param {Boolean} deep 是否深层
  * @return {Array}
  */
function flatten$1(array, deep) {
  if (isArray_1(array)) {
    return flattenDeep(array, deep);
  }
  return [];
}
var flatten_1 = flatten$1;

function deepGetObj(obj, path) {
  var index = 0;
  var len = path.length;
  while (obj && index < len) {
    obj = obj[path[index++]];
  }
  return len && obj ? obj : 0;
}

/**
 * 在list的每个元素上执行方法,任何传递的额外参数都会在调用方法的时候传递给它
 *
 * @param {Array} list
 * @param {Array/String/Function} path
 * @param {...Object} arguments
 * @return {Array}
 */
function invoke(list, path) {
  var func;
  var args = arguments;
  var params = [];
  var paths = [];
  var index = 2;
  var len = args.length;
  for (; index < len; index++) {
    params.push(args[index]);
  }
  if (isArray_1(path)) {
    len = path.length - 1;
    for (index = 0; index < len; index++) {
      paths.push(path[index]);
    }
    path = path[len];
  }
  return map_1(list, function (context) {
    if (paths.length) {
      context = deepGetObj(context, paths);
    }
    func = context[path] || path;
    if (func && func.apply) {
      return func.apply(context, params);
    }
  });
}
var invoke_1 = invoke;

function helperDeleteProperty(obj, property) {
  try {
    delete obj[property];
  } catch (e) {
    obj[property] = undefined;
  }
}
var helperDeleteProperty_1 = helperDeleteProperty;

/**
  * 迭代器,从最后开始迭代
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
function lastEach(obj, iterate, context) {
  if (obj) {
    return (isArray_1(obj) ? lastArrayEach_1 : lastObjectEach_1)(obj, iterate, context);
  }
  return obj;
}
var lastEach_1 = lastEach;

/**
  * 判断是否Object对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isObject$1 = helperCreateInTypeof_1('object');
var isObject_1 = isObject$1;

/**
  * 清空对象
  *
  * @param {Object} obj 对象
  * @param {*} defs 默认值,如果不传（清空所有属性）、如果传对象（清空并继承)、如果传值(给所有赋值)
  * @param {Object/Array} assigns 默认值
  * @return {Object}
  */
function clear(obj, defs, assigns) {
  if (obj) {
    var len;
    var isDefs = arguments.length > 1 && (isNull_1(defs) || !isObject_1(defs));
    var extds = isDefs ? assigns : defs;
    if (isPlainObject_1(obj)) {
      objectEach_1(obj, isDefs ? function (val, key) {
        obj[key] = defs;
      } : function (val, key) {
        helperDeleteProperty_1(obj, key);
      });
      if (extds) {
        assign_1(obj, extds);
      }
    } else if (isArray_1(obj)) {
      if (isDefs) {
        len = obj.length;
        while (len > 0) {
          len--;
          obj[len] = defs;
        }
      } else {
        obj.length = 0;
      }
      if (extds) {
        obj.push.apply(obj, extds);
      }
    }
  }
  return obj;
}
var clear_1 = clear;

function pluckProperty(name) {
  return function (obj, key) {
    return key === name;
  };
}

/**
  * 移除对象属性
  *
  * @param {Object/Array} obj 对象/数组
  * @param {Function/String} iterate 方法或属性
  * @param {Object} context 上下文
  * @return {Object/Array}
  */
function remove(obj, iterate, context) {
  if (obj) {
    if (!eqNull_1(iterate)) {
      var removeKeys = [];
      var rest = [];
      if (!isFunction_1$1(iterate)) {
        iterate = pluckProperty(iterate);
      }
      each_1(obj, function (item, index, rest) {
        if (iterate.call(context, item, index, rest)) {
          removeKeys.push(index);
        }
      });
      if (isArray_1(obj)) {
        lastEach_1(removeKeys, function (item, key) {
          rest.push(obj[item]);
          obj.splice(item, 1);
        });
      } else {
        rest = {};
        arrayEach_1(removeKeys, function (key) {
          rest[key] = obj[key];
          helperDeleteProperty_1(obj, key);
        });
      }
      return rest;
    }
    return clear_1(obj);
  }
  return obj;
}
var remove_1 = remove;

function strictTree(array, optChildren) {
  each_1(array, function (item) {
    if (item[optChildren] && !item[optChildren].length) {
      remove_1(item, optChildren);
    }
  });
}

/**
  * 将一个带层级的数据列表转成树结构
  *
  * @param {Array} array 数组
  * @param {Object} options {strict: false, parentKey: 'parentId', key: 'id', children: 'children', mapChildren: 'children', data: 'data'}
  * @return {Array}
  */
function toArrayTree(array, options) {
  var opts = assign_1({}, setupDefaults_1.treeOptions, options);
  var optStrict = opts.strict;
  var optKey = opts.key;
  var optParentKey = opts.parentKey;
  var optChildren = opts.children;
  var optMapChildren = opts.mapChildren;
  var optSortKey = opts.sortKey;
  var optReverse = opts.reverse;
  var optData = opts.data;
  var result = [];
  var treeMap = {};
  var idsMap = {};
  var id, treeData, parentId;
  if (optSortKey) {
    array = orderBy_1(clone_1(array), optSortKey);
    if (optReverse) {
      array = array.reverse();
    }
  }
  each_1(array, function (item) {
    id = item[optKey];
    idsMap[id] = true;
  });
  each_1(array, function (item) {
    id = item[optKey];
    if (optData) {
      treeData = {};
      treeData[optData] = item;
    } else {
      treeData = item;
    }
    parentId = item[optParentKey];
    treeMap[id] = treeMap[id] || [];
    treeMap[parentId] = treeMap[parentId] || [];
    treeMap[parentId].push(treeData);
    treeData[optKey] = id;
    treeData[optParentKey] = parentId;
    treeData[optChildren] = treeMap[id];
    if (optMapChildren) {
      treeData[optMapChildren] = treeMap[id];
    }
    if (!optStrict || optStrict && eqNull_1(parentId)) {
      if (!idsMap[parentId]) {
        result.push(treeData);
      }
    }
  });
  if (optStrict) {
    strictTree(array, optChildren);
  }
  return result;
}
var toArrayTree_1 = toArrayTree;

function unTreeList(result, array, opts) {
  var optChildren = opts.children;
  var optData = opts.data;
  var optClear = opts.clear;
  each_1(array, function (item) {
    var children = item[optChildren];
    if (optData) {
      item = item[optData];
    }
    result.push(item);
    if (children && children.length) {
      unTreeList(result, children, opts);
    }
    if (optClear) {
      delete item[optChildren];
    }
  });
  return result;
}

/**
  * 将一个树结构转成数组列表
  *
  * @param {Array} array 数组
  * @param {Object} options { children: 'children', data: 'data', clear: false }
  * @return {Array}
  */
function toTreeArray(array, options) {
  return unTreeList([], array, assign_1({}, setupDefaults_1.treeOptions, options));
}
var toTreeArray_1 = toTreeArray;

function helperCreateTreeFunc(handle) {
  return function (obj, iterate, options, context) {
    var opts = options || {};
    var optChildren = opts.children || 'children';
    return handle(null, obj, iterate, context, [], [], optChildren, opts);
  };
}
var helperCreateTreeFunc_1 = helperCreateTreeFunc;

function findTreeItem(parent, obj, iterate, context, path, node, parseChildren, opts) {
  if (obj) {
    var item, index, len, paths, nodes, match;
    for (index = 0, len = obj.length; index < len; index++) {
      item = obj[index];
      paths = path.concat(['' + index]);
      nodes = node.concat([item]);
      if (iterate.call(context, item, index, obj, paths, parent, nodes)) {
        return {
          index: index,
          item: item,
          path: paths,
          items: obj,
          parent: parent,
          nodes: nodes
        };
      }
      if (parseChildren && item) {
        match = findTreeItem(item, item[parseChildren], iterate, context, paths.concat([parseChildren]), nodes, parseChildren);
        if (match) {
          return match;
        }
      }
    }
  }
}

/**
  * 从树结构中查找匹配第一条数据的键、值、路径
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, items, path, parent, nodes) 回调
  * @param {Object} options {children: 'children'}
  * @param {Object} context 上下文
  * @return {Object} { item, index, items, path, parent, nodes }
  */
var findTree = helperCreateTreeFunc_1(findTreeItem);
var findTree_1 = findTree;

function eachTreeItem(parent, obj, iterate, context, path, node, parseChildren, opts) {
  var paths, nodes;
  each_1(obj, function (item, index) {
    paths = path.concat(['' + index]);
    nodes = node.concat([item]);
    iterate.call(context, item, index, obj, paths, parent, nodes);
    if (item && parseChildren) {
      paths.push(parseChildren);
      eachTreeItem(item, item[parseChildren], iterate, context, paths, nodes, parseChildren);
    }
  });
}

/**
  * 从树结构中遍历数据的键、值、路径
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, items, path, parent, nodes) 回调
  * @param {Object} options {children: 'children', mapChildren: 'children}
  * @param {Object} context 上下文
  */
var eachTree = helperCreateTreeFunc_1(eachTreeItem);
var eachTree_1 = eachTree;

function mapTreeItem(parent, obj, iterate, context, path, node, parseChildren, opts) {
  var paths, nodes, rest;
  var mapChildren = opts.mapChildren || parseChildren;
  return map_1(obj, function (item, index) {
    paths = path.concat(['' + index]);
    nodes = node.concat([item]);
    rest = iterate.call(context, item, index, obj, paths, parent, nodes);
    if (rest && item && parseChildren && item[parseChildren]) {
      rest[mapChildren] = mapTreeItem(item, item[parseChildren], iterate, context, paths, nodes, parseChildren, opts);
    }
    return rest;
  });
}

/**
  * 从树结构中指定方法后的返回值组成的新数组
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, items, path, parent, nodes) 回调
  * @param {Object} options {children: 'children'}
  * @param {Object} context 上下文
  * @return {Object/Array}
  */
var mapTree = helperCreateTreeFunc_1(mapTreeItem);
var mapTree_1 = mapTree;

/**
  * 从树结构中根据回调过滤数据
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, items, path, parent) 回调
  * @param {Object} options {children: 'children'}
  * @param {Object} context 上下文
  * @return {Array}
  */
function filterTree(obj, iterate, options, context) {
  var result = [];
  if (obj && iterate) {
    eachTree_1(obj, function (item, index, items, path, parent, nodes) {
      if (iterate.call(context, item, index, items, path, parent, nodes)) {
        result.push(item);
      }
    }, options);
  }
  return result;
}
var filterTree_1 = filterTree;

function searchTreeItem(matchParent, parent, obj, iterate, context, path, node, parseChildren, opts) {
  var paths, nodes, rest, isMatch, hasChild;
  var rests = [];
  var hasOriginal = opts.original;
  var sourceData = opts.data;
  var mapChildren = opts.mapChildren || parseChildren;
  var isEvery = opts.isEvery;
  arrayEach_1(obj, function (item, index) {
    paths = path.concat(['' + index]);
    nodes = node.concat([item]);
    isMatch = matchParent && !isEvery || iterate.call(context, item, index, obj, paths, parent, nodes);
    hasChild = parseChildren && item[parseChildren];
    if (isMatch || hasChild) {
      if (hasOriginal) {
        rest = item;
      } else {
        rest = assign_1({}, item);
        if (sourceData) {
          rest[sourceData] = item;
        }
      }
      rest[mapChildren] = searchTreeItem(isMatch, item, item[parseChildren], iterate, context, paths, nodes, parseChildren, opts);
      if (isMatch || rest[mapChildren].length) {
        rests.push(rest);
      }
    } else if (isMatch) {
      rests.push(rest);
    }
  });
  return rests;
}

/**
  * 从树结构中根据回调查找数据
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, items, path, parent, nodes) 回调
  * @param {Object} options {children: 'children'}
  * @param {Object} context 上下文
  * @return {Array}
  */
var searchTree = helperCreateTreeFunc_1(function (parent, obj, iterate, context, path, nodes, parseChildren, opts) {
  return searchTreeItem(0, parent, obj, iterate, context, path, nodes, parseChildren, opts);
});
var searchTree_1 = searchTree;

function arrayIndexOf(list, val) {
  if (list.indexOf) {
    return list.indexOf(val);
  }
  for (var index = 0, len = list.length; index < len; index++) {
    if (val === list[index]) {
      return index;
    }
  }
}
var arrayIndexOf_1 = arrayIndexOf;

function arrayLastIndexOf(list, val) {
  if (list.lastIndexOf) {
    return list.lastIndexOf(val);
  }
  for (var len = list.length - 1; len >= 0; len--) {
    if (val === list[len]) {
      return len;
    }
  }
  return -1;
}
var arrayLastIndexOf_1 = arrayLastIndexOf;

/**
  * 判断是否Number对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isNumber = helperCreateInTypeof_1('number');
var isNumber_1 = isNumber;

/* eslint-disable eqeqeq */
function isNumberNaN(obj) {
  return isNumber_1(obj) && isNaN(obj);
}
var _isNaN = isNumberNaN;

/**
  * 判断是否String对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isString = helperCreateInTypeof_1('string');
var isString_1 = isString;

/**
  * 判断是否Date对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isDate = helperCreateInInObjectString_1('Date');
var isDate_1 = isDate;

var staticParseInt = parseInt;
var staticParseInt_1 = staticParseInt;

function helperGetUTCDateTime(resMaps) {
  return Date.UTC(resMaps.y, resMaps.M || 0, resMaps.d || 1, resMaps.H || 0, resMaps.m || 0, resMaps.s || 0, resMaps.S || 0);
}
var helperGetUTCDateTime_1 = helperGetUTCDateTime;

function helperGetDateTime(date) {
  return date.getTime();
}
var helperGetDateTime_1 = helperGetDateTime;

function getParseRule(txt) {
  return '(\\d{' + txt + '})';
}
function toParseMs(num) {
  if (num < 10) {
    return num * 100;
  } else if (num < 100) {
    return num * 10;
  }
  return num;
}
function toParseNum(num) {
  return isNaN(num) ? num : staticParseInt_1(num);
}
var d2 = getParseRule(2);
var d1or2 = getParseRule('1,2');
var d1or7 = getParseRule('1,7');
var d3or4 = getParseRule('3,4');
var place = '.{1}';
var d1Or2RE = place + d1or2;
var dzZ = '(([zZ])|([-+]\\d{2}:?\\d{2}))';
var defaulParseStrs = [d3or4, d1Or2RE, d1Or2RE, d1Or2RE, d1Or2RE, d1Or2RE, place + d1or7, dzZ];
var defaulParseREs = [];
for (var len = defaulParseStrs.length - 1; len >= 0; len--) {
  var rule = '';
  for (var i$5 = 0; i$5 < len + 1; i$5++) {
    rule += defaulParseStrs[i$5];
  }
  defaulParseREs.push(new RegExp('^' + rule + '$'));
}

/**
 * 解析默认格式
 */
function parseDefaultRules(str) {
  var matchRest,
    resMaps = {};
  for (var i = 0, dfrLen = defaulParseREs.length; i < dfrLen; i++) {
    matchRest = str.match(defaulParseREs[i]);
    if (matchRest) {
      resMaps.y = matchRest[1];
      resMaps.M = matchRest[2];
      resMaps.d = matchRest[3];
      resMaps.H = matchRest[4];
      resMaps.m = matchRest[5];
      resMaps.s = matchRest[6];
      resMaps.S = matchRest[7];
      resMaps.Z = matchRest[8];
      break;
    }
  }
  return resMaps;
}
var customParseStrs = [['yyyy', d3or4], ['yy', d2], ['MM', d2], ['M', d1or2], ['dd', d2], ['d', d1or2], ['HH', d2], ['H', d1or2], ['mm', d2], ['m', d1or2], ['ss', d2], ['s', d1or2], ['SSS', getParseRule(3)], ['S', d1or7], ['Z', dzZ]];
var parseRuleMaps = {};
var parseRuleKeys = ['\\[([^\\]]+)\\]'];
for (var i$5 = 0; i$5 < customParseStrs.length; i$5++) {
  var itemRule = customParseStrs[i$5];
  parseRuleMaps[itemRule[0]] = itemRule[1] + '?';
  parseRuleKeys.push(itemRule[0]);
}
var customParseRes = new RegExp(parseRuleKeys.join('|'), 'g');
var cacheFormatMaps = {};

/**
 * 解析自定义格式
 */
function parseCustomRules(str, format) {
  var cacheItem = cacheFormatMaps[format];
  if (!cacheItem) {
    var posIndexs = [];
    var re = format.replace(/([$(){}*+.?\\^|])/g, "\\$1").replace(customParseRes, function (text, val) {
      var firstChar = text.charAt(0);
      // 如果为转义符号:[关键字]
      if (firstChar === '[') {
        return val;
      }
      posIndexs.push(firstChar);
      return parseRuleMaps[text];
    });
    cacheItem = cacheFormatMaps[format] = {
      _i: posIndexs,
      _r: new RegExp(re)
    };
  }
  var resMaps = {};
  var matchRest = str.match(cacheItem._r);
  if (matchRest) {
    var _i = cacheItem._i;
    for (var i = 1, len = matchRest.length; i < len; i++) {
      resMaps[_i[i - 1]] = matchRest[i];
    }
    return resMaps;
  }
  return resMaps;
}

/**
 * 解析时区
 */
function parseTimeZone(resMaps) {
  // 如果为UTC 时间
  if (/^[zZ]/.test(resMaps.Z)) {
    return new Date(helperGetUTCDateTime_1(resMaps));
  } else {
    // 如果指定时区，时区转换
    var matchRest = resMaps.Z.match(/([-+])(\d{2}):?(\d{2})/);
    if (matchRest) {
      return new Date(helperGetUTCDateTime_1(resMaps) - (matchRest[1] === '-' ? -1 : 1) * staticParseInt_1(matchRest[2]) * 3600000 + staticParseInt_1(matchRest[3]) * 60000);
    }
  }
  return new Date('');
}

/**
  * 字符串转为日期
  *
  * @param {String/Number/Date} str 日期或数字
  * @param {String} format 解析日期格式(yyyy年份、MM月份、dd天、hh(12)HH(24)小时、mm分钟、ss秒、SSS毫秒、Z时区)
  * @return {Date}
  */
function toStringDate(str, format) {
  if (str) {
    var isDType = isDate_1(str);
    if (isDType || !format && /^[0-9]{11,15}$/.test(str)) {
      return new Date(isDType ? helperGetDateTime_1(str) : staticParseInt_1(str));
    }
    if (isString_1(str)) {
      var resMaps = format ? parseCustomRules(str, format) : parseDefaultRules(str);
      if (resMaps.y) {
        if (resMaps.M) {
          resMaps.M = toParseNum(resMaps.M) - 1;
        }
        if (resMaps.S) {
          // 如果7位则是微秒，只精确到3位毫秒
          resMaps.S = toParseMs(toParseNum(resMaps.S.substring(0, 3)));
        }
        if (resMaps.Z) {
          return parseTimeZone(resMaps);
        } else {
          return new Date(resMaps.y, resMaps.M || 0, resMaps.d || 1, resMaps.H || 0, resMaps.m || 0, resMaps.s || 0, resMaps.S || 0);
        }
      }
    }
  }
  return new Date('');
}
var toStringDate_1 = toStringDate;

function helperNewDate() {
  return new Date();
}
var helperNewDate_1 = helperNewDate;

/**
  * 判断是否闰年
  *
  * @param {Date} date 日期或数字
  * @return {Boolean}
  */
function isLeapYear(date) {
  var year;
  var currentDate = date ? toStringDate_1(date) : helperNewDate_1();
  if (isDate_1(currentDate)) {
    year = currentDate.getFullYear();
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  }
  return false;
}
var isLeapYear_1 = isLeapYear;

/**
  * 已废弃，被 some, every 替换
  * @deprecated
  */
function forOf(obj, iterate, context) {
  if (obj) {
    if (isArray_1(obj)) {
      for (var index = 0, len = obj.length; index < len; index++) {
        if (iterate.call(context, obj[index], index, obj) === false) {
          break;
        }
      }
    } else {
      for (var key in obj) {
        if (hasOwnProp_1(obj, key)) {
          if (iterate.call(context, obj[key], key, obj) === false) {
            break;
          }
        }
      }
    }
  }
}
var forOf_1 = forOf;

/**
  * 已废弃
  * @deprecated
  */
function lastForOf(obj, iterate, context) {
  if (obj) {
    var len, list;
    if (isArray_1(obj)) {
      for (len = obj.length - 1; len >= 0; len--) {
        if (iterate.call(context, obj[len], len, obj) === false) {
          break;
        }
      }
    } else {
      list = hasOwnProp_1(obj);
      for (len = list.length - 1; len >= 0; len--) {
        if (iterate.call(context, obj[list[len]], list[len], obj) === false) {
          break;
        }
      }
    }
  }
}
var lastForOf_1 = lastForOf;

function helperCreateIndexOf(name, callback) {
  return function (obj, val) {
    if (obj) {
      if (obj[name]) {
        return obj[name](val);
      }
      if (isString_1(obj) || isArray_1(obj)) {
        return callback(obj, val);
      }
      for (var key in obj) {
        if (hasOwnProp_1(obj, key)) {
          if (val === obj[key]) {
            return key;
          }
        }
      }
    }
    return -1;
  };
}
var helperCreateIndexOf_1 = helperCreateIndexOf;

/**
  * 返回对象第一个索引值
  *
  * @param {Object} obj 对象
  * @param {Object} val 值
  * @return {Number}
  */
var indexOf = helperCreateIndexOf_1('indexOf', arrayIndexOf_1);
var indexOf_1 = indexOf;

/**
  * 从最后开始的索引值,返回对象第一个索引值
  *
  * @param {Object} array 对象
  * @param {Object} val 值
  * @return {Number}
  */
var lastIndexOf = helperCreateIndexOf_1('lastIndexOf', arrayLastIndexOf_1);
var lastIndexOf_1 = lastIndexOf;

/**
  * 返回对象的长度
  *
  * @param {Object} obj 对象
  * @return {Number}
  */
function getSize(obj) {
  var len = 0;
  if (isString_1(obj) || isArray_1(obj)) {
    return obj.length;
  }
  each_1(obj, function () {
    len++;
  });
  return len;
}
var getSize_1 = getSize;

function isNumberFinite(obj) {
  return isNumber_1(obj) && isFinite(obj);
}
var _isFinite = isNumberFinite;

/**
  * 判断是否整数
  *
  * @param {Number, String} number 数值
  * @return {Boolean}
  */
var isInteger = function (obj) {
  return !isNull_1(obj) && !isNaN(obj) && !isArray_1(obj) && obj % 1 === 0;
};
var isInteger_1 = isInteger;

/**
  * 判断是否小数
  *
  * @param {Number} obj 数值
  * @return {Boolean}
  */
function isFloat(obj) {
  return !isNull_1(obj) && !isNaN(obj) && !isArray_1(obj) && !isInteger_1(obj);
}
var isFloat_1 = isFloat;

/**
  * 判断是否Boolean对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isBoolean = helperCreateInTypeof_1('boolean');
var isBoolean_1 = isBoolean;

/**
  * 判断是否RegExp对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isRegExp = helperCreateInInObjectString_1('RegExp');
var isRegExp_1 = isRegExp;

/**
  * 判断是否Error对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isError = helperCreateInInObjectString_1('Error');
var isError_1 = isError;

/**
  * 判断是否TypeError对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isTypeError(obj) {
  return obj ? obj.constructor === TypeError : false;
}
var isTypeError_1 = isTypeError;

/**
  * 判断是否为空对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isEmpty$1(obj) {
  for (var key in obj) {
    return false;
  }
  return true;
}
var isEmpty_1 = isEmpty$1;

/* eslint-disable valid-typeof */


/**
  * 判断是否Symbol对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var supportSymbol = typeof Symbol !== staticStrUndefined_1;
function isSymbol(obj) {
  return supportSymbol && Symbol.isSymbol ? Symbol.isSymbol(obj) : typeof obj === 'symbol';
}
var isSymbol_1 = isSymbol;

/**
  * 判断是否Arguments对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var isArguments = helperCreateInInObjectString_1('Arguments');
var isArguments_1 = isArguments;

/**
  * 判断是否Element对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isElement$2(obj) {
  return !!(obj && isString_1(obj.nodeName) && isNumber_1(obj.nodeType));
}
var isElement_1 = isElement$2;

/* eslint-disable valid-typeof */
var staticDocument = typeof document === staticStrUndefined_1 ? 0 : document;
var staticDocument_1 = staticDocument;

/**
  * 判断是否Document对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isDocument(obj) {
  return !!(obj && staticDocument_1 && obj.nodeType === 9);
}
var isDocument_1 = isDocument;

/* eslint-disable valid-typeof */
var staticWindow = typeof window === staticStrUndefined_1 ? 0 : window;
var staticWindow_1 = staticWindow;

/**
  * 判断是否Window对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
function isWindow(obj) {
  return !!(staticWindow_1 && !!(obj && obj === obj.window));
}
var isWindow_1 = isWindow;

/* eslint-disable valid-typeof */


/**
  * 判断是否FormData对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
  */
var supportFormData = typeof FormData !== staticStrUndefined_1;
function isFormData(obj) {
  return supportFormData && obj instanceof FormData;
}
var isFormData_1 = isFormData;

/* eslint-disable valid-typeof */


/**
  * 判断是否Map对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
 */
var supportMap = typeof Map !== staticStrUndefined_1;
function isMap(obj) {
  return supportMap && obj instanceof Map;
}
var isMap_1 = isMap;

/* eslint-disable valid-typeof */


/**
  * 判断是否WeakMap对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
 */
var supportWeakMap = typeof WeakMap !== staticStrUndefined_1;
function isWeakMap(obj) {
  return supportWeakMap && obj instanceof WeakMap;
}
var isWeakMap_1 = isWeakMap;

/* eslint-disable valid-typeof */


/**
  * 判断是否Set对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
 */
var supportSet = typeof Set !== staticStrUndefined_1;
function isSet(obj) {
  return supportSet && obj instanceof Set;
}
var isSet_1 = isSet;

/* eslint-disable valid-typeof */


/**
  * 判断是否WeakSet对象
  *
  * @param {Object} obj 对象
  * @return {Boolean}
 */
var supportWeakSet = typeof WeakSet !== staticStrUndefined_1;
function isWeakSet(obj) {
  return supportWeakSet && obj instanceof WeakSet;
}
var isWeakSet_1 = isWeakSet;

function helperCreateiterateIndexOf(callback) {
  return function (obj, iterate, context) {
    if (obj && isFunction_1$1(iterate)) {
      if (isArray_1(obj) || isString_1(obj)) {
        return callback(obj, iterate, context);
      }
      for (var key in obj) {
        if (hasOwnProp_1(obj, key)) {
          if (iterate.call(context, obj[key], key, obj)) {
            return key;
          }
        }
      }
    }
    return -1;
  };
}
var helperCreateiterateIndexOf_1 = helperCreateiterateIndexOf;

/**
  * 返回对象第一个索引值
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
var findIndexOf = helperCreateiterateIndexOf_1(function (obj, iterate, context) {
  for (var index = 0, len = obj.length; index < len; index++) {
    if (iterate.call(context, obj[index], index, obj)) {
      return index;
    }
  }
  return -1;
});
var findIndexOf_1 = findIndexOf;

function helperEqualCompare(val1, val2, compare, func, key, obj1, obj2) {
  if (val1 === val2) {
    return true;
  }
  if (val1 && val2 && !isNumber_1(val1) && !isNumber_1(val2) && !isString_1(val1) && !isString_1(val2)) {
    if (isRegExp_1(val1)) {
      return compare('' + val1, '' + val2, key, obj1, obj2);
    }
    if (isDate_1(val1) || isBoolean_1(val1)) {
      return compare(+val1, +val2, key, obj1, obj2);
    } else {
      var result, val1Keys, val2Keys;
      var isObj1Arr = isArray_1(val1);
      var isObj2Arr = isArray_1(val2);
      if (isObj1Arr || isObj2Arr ? isObj1Arr && isObj2Arr : val1.constructor === val2.constructor) {
        val1Keys = keys_1(val1);
        val2Keys = keys_1(val2);
        if (func) {
          result = func(val1, val2, key);
        }
        if (val1Keys.length === val2Keys.length) {
          return isUndefined_1(result) ? every_1(val1Keys, function (key, index) {
            return key === val2Keys[index] && helperEqualCompare(val1[key], val2[val2Keys[index]], compare, func, isObj1Arr || isObj2Arr ? index : key, val1, val2);
          }) : !!result;
        }
        return false;
      }
    }
  }
  return compare(val1, val2, key, obj1, obj2);
}
var helperEqualCompare_1 = helperEqualCompare;

function helperDefaultCompare(v1, v2) {
  return v1 === v2;
}
var helperDefaultCompare_1 = helperDefaultCompare;

/**
 * 深度比较两个对象之间的值是否相等
 *
 * @param {Object} obj1 值1
 * @param {Object} obj2 值2
 * @return {Boolean}
 */
function isEqual(obj1, obj2) {
  return helperEqualCompare_1(obj1, obj2, helperDefaultCompare_1);
}
var isEqual_1 = isEqual;

/**
 * 判断属性中的键和值是否包含在对象中
 *
 * @param {Object/Array} obj 对象
 * @param {Object} source 值
 * @return {Boolean}
 */
function isMatch(obj, source) {
  var objKeys = keys_1(obj);
  var sourceKeys = keys_1(source);
  if (sourceKeys.length) {
    if (includeArrays_1(objKeys, sourceKeys)) {
      return some_1(sourceKeys, function (key2) {
        return findIndexOf_1(objKeys, function (key1) {
          return key1 === key2 && isEqual_1(obj[key1], source[key2]);
        }) > -1;
      });
    }
  } else {
    return true;
  }
  return isEqual_1(obj, source);
}
var isMatch_1 = isMatch;

/**
 * 深度比较两个对象之间的值是否相等，使用自定义比较函数
 *
 * @param {Object} obj1 值1
 * @param {Object} obj2 值2
 * @param {Function} func 自定义函数
 * @return {Boolean}
 */
function isEqualWith(obj1, obj2, func) {
  if (isFunction_1$1(func)) {
    return helperEqualCompare_1(obj1, obj2, function (v1, v2, key, obj1, obj2) {
      var result = func(v1, v2, key, obj1, obj2);
      return isUndefined_1(result) ? helperDefaultCompare_1(v1, v2) : !!result;
    }, func);
  }
  return helperEqualCompare_1(obj1, obj2, helperDefaultCompare_1);
}
var isEqualWith_1 = isEqualWith;

/**
  * 获取对象类型
  *
  * @param {Object} obj 对象
  * @return {String}
  */
function getType(obj) {
  if (isNull_1(obj)) {
    return 'null';
  }
  if (isSymbol_1(obj)) {
    return 'symbol';
  }
  if (isDate_1(obj)) {
    return 'date';
  }
  if (isArray_1(obj)) {
    return 'array';
  }
  if (isRegExp_1(obj)) {
    return 'regexp';
  }
  if (isError_1(obj)) {
    return 'error';
  }
  return typeof obj;
}
var getType_1 = getType;

/**
  * 获取一个全局唯一标识
  *
  * @param {String} prefix 前缀
  * @return {Number}
  */
var __uniqueId = 0;
function uniqueId(prefix) {
  return [prefix, ++__uniqueId].join('');
}
var uniqueId_1 = uniqueId;

/**
  * 从最后开始的索引值,返回对象第一个索引值
  *
  * @param {Object} obj 对象/数组
  * @param {Function} iterate(item, index, obj) 回调
  * @param {Object} context 上下文
  * @return {Object}
  */
var findLastIndexOf = helperCreateiterateIndexOf_1(function (obj, iterate, context) {
  for (var len = obj.length - 1; len >= 0; len--) {
    if (iterate.call(context, obj[len], len, obj)) {
      return len;
    }
  }
  return -1;
});
var findLastIndexOf_1 = findLastIndexOf;

/**
  * 字符串转JSON
  *
  * @param {String} str 字符串
  * @return {Object} 返回转换后对象
  */
function toStringJSON(str) {
  if (isPlainObject_1(str)) {
    return str;
  } else if (isString_1(str)) {
    try {
      return JSON.parse(str);
    } catch (e) {}
  }
  return {};
}
var toStringJSON_1 = toStringJSON;

/**
  * JSON转字符串
  *
  * @param {Object} obj 对象
  * @return {String} 返回字符串
  */
function toJSONString(obj) {
  return eqNull_1(obj) ? '' : JSON.stringify(obj);
}
var toJSONString_1 = toJSONString;

/**
  * 获取对象所有属性、值
  *
  * @param {Object} obj 对象/数组
  * @return {Array}
  */
var entries = helperCreateGetObjects_1('entries', 2);
var entries_1 = entries;

function helperCreatePickOmit(case1, case2) {
  return function (obj, callback) {
    var item, index;
    var rest = {};
    var result = [];
    var context = this;
    var args = arguments;
    var len = args.length;
    if (!isFunction_1$1(callback)) {
      for (index = 1; index < len; index++) {
        item = args[index];
        result.push.apply(result, isArray_1(item) ? item : [item]);
      }
      callback = 0;
    }
    each_1(obj, function (val, key) {
      if ((callback ? callback.call(context, val, key, obj) : findIndexOf_1(result, function (name) {
        return name === key;
      }) > -1) ? case1 : case2) {
        rest[key] = val;
      }
    });
    return rest;
  };
}
var helperCreatePickOmit_1 = helperCreatePickOmit;

/**
 * 根据 key 过滤指定的属性值，返回一个新的对象
 *
 * @param {Object} obj 对象
 * @param {String/Array} key 键数组
 * @return {Object}
 */
var pick$1 = helperCreatePickOmit_1(1, 0);
var pick_1 = pick$1;

/**
 * 根据 key 排除指定的属性值，返回一个新的对象
 *
 * @param {Object} obj 对象
 * @param {String/Array} key 键数组
 * @return {Object}
 */
var omit = helperCreatePickOmit_1(0, 1);
var omit_1 = omit;

/**
  * 获取对象第一个值
  *
  * @param {Object} obj 对象/数组
  * @return {Object}
  */
function first(obj) {
  return values_1(obj)[0];
}
var first_1 = first;

/**
  * 获取对象最后一个值
  *
  * @param {Object} obj 对象/数组
  * @return {Object}
  */
function last(obj) {
  var list = values_1(obj);
  return list[list.length - 1];
}
var last_1 = last;

/**
 * 检查键、路径是否是该对象的属性
 *
 * @param {Object/Array} data 对象
 * @param {String/Function} property 键、路径
 * @return {Boolean}
 */
function has(obj, property) {
  if (obj) {
    if (hasOwnProp_1(obj, property)) {
      return true;
    } else {
      var prop, arrIndex, objProp, matchs, rest, isHas;
      var props = helperGetHGSKeys_1(property);
      var index = 0;
      var len = props.length;
      for (rest = obj; index < len; index++) {
        isHas = false;
        prop = props[index];
        matchs = prop ? prop.match(staticHGKeyRE_1) : '';
        if (matchs) {
          arrIndex = matchs[1];
          objProp = matchs[2];
          if (arrIndex) {
            if (rest[arrIndex]) {
              if (hasOwnProp_1(rest[arrIndex], objProp)) {
                isHas = true;
                rest = rest[arrIndex][objProp];
              }
            }
          } else {
            if (hasOwnProp_1(rest, objProp)) {
              isHas = true;
              rest = rest[objProp];
            }
          }
        } else {
          if (hasOwnProp_1(rest, prop)) {
            isHas = true;
            rest = rest[prop];
          }
        }
        if (isHas) {
          if (index === len - 1) {
            return true;
          }
        } else {
          break;
        }
      }
    }
  }
  return false;
}
var has_1 = has;

var sKeyRE = /(.+)?\[(\d+)\]$/;
function setDeepProps(obj, key, isEnd, nextKey, value) {
  if (obj[key]) {
    if (isEnd) {
      obj[key] = value;
    }
  } else {
    var index;
    var rest;
    var currMatchs = key ? key.match(sKeyRE) : null;
    if (isEnd) {
      rest = value;
    } else {
      var nextMatchs = nextKey ? nextKey.match(sKeyRE) : null;
      if (nextMatchs && !nextMatchs[1]) {
        // 如果下一个属性为数组类型
        rest = new Array(staticParseInt_1(nextMatchs[2]) + 1);
      } else {
        rest = {};
      }
    }
    if (currMatchs) {
      if (currMatchs[1]) {
        // 如果为对象中数组
        index = staticParseInt_1(currMatchs[2]);
        if (obj[currMatchs[1]]) {
          if (isEnd) {
            obj[currMatchs[1]][index] = rest;
          } else {
            if (obj[currMatchs[1]][index]) {
              rest = obj[currMatchs[1]][index];
            } else {
              obj[currMatchs[1]][index] = rest;
            }
          }
        } else {
          obj[currMatchs[1]] = new Array(index + 1);
          obj[currMatchs[1]][index] = rest;
        }
      } else {
        // 如果为数组
        obj[currMatchs[2]] = rest;
      }
    } else {
      // 如果为对象
      obj[key] = rest;
    }
    return rest;
  }
  return obj[key];
}

/**
 * 设置对象属性上的值。如果属性不存在则创建它
 * @param {Object/Array} obj 对象
 * @param {String/Function} property 键、路径
 * @param {Object} value 值
 */
function set(obj, property, value) {
  if (obj) {
    if ((obj[property] || hasOwnProp_1(obj, property)) && !isPrototypePolluted(property)) {
      obj[property] = value;
    } else {
      var rest = obj;
      var props = helperGetHGSKeys_1(property);
      var len = props.length;
      for (var index = 0; index < len; index++) {
        if (isPrototypePolluted(props[index])) {
          continue;
        }
        var isEnd = index === len - 1;
        rest = setDeepProps(rest, props[index], isEnd, isEnd ? null : props[index + 1], value);
      }
    }
  }
  return obj;
}

/**
 * Blacklist certain keys to prevent Prototype Pollution
 * @param {string} key
 */
function isPrototypePolluted(key) {
  return key === '__proto__' || key === 'constructor' || key === 'prototype';
}
var set_1 = set;

function createiterateEmpty(iterate) {
  return function () {
    return isEmpty_1(iterate);
  };
}

/**
  * 集合分组,默认使用键值分组,如果有iterate则使用结果进行分组
  *
  * @param {Array} obj 对象
  * @param {Function} iterate 回调/对象属性
  * @param {Object} context 上下文
  * @return {Object}
  */
function groupBy(obj, iterate, context) {
  var groupKey;
  var result = {};
  if (obj) {
    if (iterate && isObject_1(iterate)) {
      iterate = createiterateEmpty(iterate);
    } else if (!isFunction_1$1(iterate)) {
      iterate = property_1(iterate);
    }
    each_1(obj, function (val, key) {
      groupKey = iterate ? iterate.call(context, val, key, obj) : val;
      if (result[groupKey]) {
        result[groupKey].push(val);
      } else {
        result[groupKey] = [val];
      }
    });
  }
  return result;
}
var groupBy_1 = groupBy;

/**
  * 集合分组统计,返回各组中对象的数量统计
  *
  * @param {Array} obj 对象
  * @param {Function} iterate 回调/对象属性
  * @param {Object} context 上下文
  * @return {Object}
  */
function countBy(obj, iterate, context) {
  var result = groupBy_1(obj, iterate, context || this);
  objectEach_1(result, function (item, key) {
    result[key] = item.length;
  });
  return result;
}
var countBy_1 = countBy;

/**
  * 序号列表生成函数
  *
  * @param {Number} start 起始值
  * @param {Number} stop 结束值
  * @param {Number} step 自增值
  * @return {Array}
  */
function range(start, stop, step) {
  var index, len;
  var result = [];
  var args = arguments;
  if (args.length < 2) {
    stop = args[0];
    start = 0;
  }
  index = start >> 0;
  len = stop >> 0;
  if (index < stop) {
    step = step >> 0 || 1;
    for (; index < len; index += step) {
      result.push(index);
    }
  }
  return result;
}
var range_1 = range;

/**
  * 将一个或者多个对象值解构到目标对象
  *
  * @param {Object} destination 目标对象
  * @param {...Object}
  * @return {Boolean}
  */
function destructuring(destination, sources) {
  if (destination && sources) {
    var rest = assign_1.apply(this, [{}].concat(slice_1(arguments, 1)));
    var restKeys = keys_1(rest);
    arrayEach_1(keys_1(destination), function (key) {
      if (includes_1(restKeys, key)) {
        destination[key] = rest[key];
      }
    });
  }
  return destination;
}
var destructuring_1 = destructuring;

/**
  * 获取最小值
  *
  * @param {Array} arr 数组
  * @param {Function} iterate(item, index, obj) 回调
  * @return {Number}
  */
var min$3 = helperCreateMinMax_1(function (rest, itemVal) {
  return rest > itemVal;
});
var min_1 = min$3;

function helperNumberDecimal(numStr) {
  return (numStr.split('.')[1] || '').length;
}
var helperNumberDecimal_1 = helperNumberDecimal;

function helperStringRepeat(str, count) {
  if (str.repeat) {
    return str.repeat(count);
  }
  var list = isNaN(count) ? [] : new Array(staticParseInt_1(count));
  return list.join(str) + (list.length > 0 ? str : '');
}
var helperStringRepeat_1 = helperStringRepeat;

function helperNumberOffsetPoint(str, offsetIndex) {
  return str.substring(0, offsetIndex) + '.' + str.substring(offsetIndex, str.length);
}
var helperNumberOffsetPoint_1 = helperNumberOffsetPoint;

/**
 * 数值转字符串，科学计数转字符串
 * @param { Number } num 数值
 *
 * @return {Number}
 */
function toNumberString(num) {
  var rest = '' + num;
  var scienceMatchs = rest.match(/^([-+]?)((\d+)|((\d+)?[.](\d+)?))e([-+]{1})([0-9]+)$/);
  if (scienceMatchs) {
    var isNegative = num < 0;
    var absFlag = isNegative ? '-' : '';
    var intNumStr = scienceMatchs[3] || '';
    var dIntNumStr = scienceMatchs[5] || '';
    var dFloatNumStr = scienceMatchs[6] || '';
    var sciencFlag = scienceMatchs[7];
    var scienceNumStr = scienceMatchs[8];
    var floatOffsetIndex = scienceNumStr - dFloatNumStr.length;
    var intOffsetIndex = scienceNumStr - intNumStr.length;
    var dIntOffsetIndex = scienceNumStr - dIntNumStr.length;
    if (sciencFlag === '+') {
      if (intNumStr) {
        return absFlag + intNumStr + helperStringRepeat_1('0', scienceNumStr);
      }
      if (floatOffsetIndex > 0) {
        return absFlag + dIntNumStr + dFloatNumStr + helperStringRepeat_1('0', floatOffsetIndex);
      }
      return absFlag + dIntNumStr + helperNumberOffsetPoint_1(dFloatNumStr, scienceNumStr);
    }
    if (intNumStr) {
      if (intOffsetIndex > 0) {
        return absFlag + '0.' + helperStringRepeat_1('0', Math.abs(intOffsetIndex)) + intNumStr;
      }
      return absFlag + helperNumberOffsetPoint_1(intNumStr, intOffsetIndex);
    }
    if (dIntOffsetIndex > 0) {
      return absFlag + '0.' + helperStringRepeat_1('0', Math.abs(dIntOffsetIndex)) + dIntNumStr + dFloatNumStr;
    }
    return absFlag + helperNumberOffsetPoint_1(dIntNumStr, dIntOffsetIndex) + dFloatNumStr;
  }
  return rest;
}
var toNumberString_1 = toNumberString;

function helperMultiply(multiplier, multiplicand) {
  var str1 = toNumberString_1(multiplier);
  var str2 = toNumberString_1(multiplicand);
  return parseInt(str1.replace('.', '')) * parseInt(str2.replace('.', '')) / Math.pow(10, helperNumberDecimal_1(str1) + helperNumberDecimal_1(str2));
}
var helperMultiply_1 = helperMultiply;

function helperCreateMathNumber(name) {
  return function (num, digits) {
    var numRest = toNumber_1(num);
    var rest = numRest;
    if (numRest) {
      digits = digits >> 0;
      var numStr = toNumberString_1(numRest);
      var nums = numStr.split('.');
      var intStr = nums[0];
      var floatStr = nums[1] || '';
      var fStr = floatStr.substring(0, digits + 1);
      var subRest = intStr + (fStr ? '.' + fStr : '');
      if (digits >= floatStr.length) {
        return toNumber_1(subRest);
      }
      subRest = numRest;
      if (digits > 0) {
        var ratio = Math.pow(10, digits);
        rest = Math[name](helperMultiply_1(subRest, ratio)) / ratio;
      } else {
        rest = Math[name](subRest);
      }
    }
    return rest;
  };
}
var helperCreateMathNumber_1 = helperCreateMathNumber;

/**
 * 将数值四舍五入
 *
 * @param {string|number} num 数值
 * @param {number} digits 小数保留位数
 * @return {number}
 */
var round$2 = helperCreateMathNumber_1('round');
var round_1 = round$2;

/**
 * 将数值向上舍入
 *
 * @param {string|number} num 数值
 * @param {number} digits 小数保留位数
 * @return {number}
 */
var ceil$1 = helperCreateMathNumber_1('ceil');
var ceil_1 = ceil$1;

/**
 * 将数值向下舍入
 *
 * @param {string|number} num 数值
 * @param {number} digits 小数保留位数
 * @return {number}
 */
var floor$1 = helperCreateMathNumber_1('floor');
var floor_1 = floor$1;

function toValueString(obj) {
  if (isNumber_1(obj)) {
    return toNumberString_1(obj);
  }
  return '' + (eqNull_1(obj) ? '' : obj);
}
var toValueString_1 = toValueString;

/**
  * 将数值四舍五入并格式化为固定小数位的字符串
  *
 * @param {string|number} num 数值
 * @param {number} digits 小数保留位数
  * @return {String}
  */
function toFixed(num, digits) {
  digits = digits >> 0;
  var str = toValueString_1(round_1(num, digits));
  var nums = str.split('.');
  var intStr = nums[0];
  var floatStr = nums[1] || '';
  var digitOffsetIndex = digits - floatStr.length;
  if (digits) {
    if (digitOffsetIndex > 0) {
      return intStr + '.' + floatStr + helperStringRepeat_1('0', digitOffsetIndex);
    }
    return intStr + helperNumberOffsetPoint_1(floatStr, Math.abs(digitOffsetIndex));
  }
  return intStr;
}
var toFixed_1 = toFixed;

/**
  * 千分位分隔符、小数点
  *
  * @param {String/Number} num 数值
  * @param {CommafyOptions} options 参数
  * @return {String}
 */
function commafy(num, options) {
  var opts = assign_1({}, setupDefaults_1.commafyOptions, options);
  var optDigits = opts.digits;
  var isNum = isNumber_1(num);
  var rest, result, isNegative, intStr, floatStr;
  if (isNum) {
    rest = (opts.ceil ? ceil_1 : opts.floor ? floor_1 : round_1)(num, optDigits);
    result = toNumberString_1(optDigits ? toFixed_1(rest, optDigits) : rest).split('.');
    intStr = result[0];
    floatStr = result[1];
    isNegative = intStr && rest < 0;
    if (isNegative) {
      intStr = intStr.substring(1, intStr.length);
    }
  } else {
    rest = toValueString_1(num).replace(/,/g, '');
    result = rest ? [rest] : [];
    intStr = result[0];
  }
  if (result.length) {
    return (isNegative ? '-' : '') + intStr.replace(new RegExp('(?=(?!(\\b))(.{' + (opts.spaceNumber || 3) + '})+$)', 'g'), opts.separator || ',') + (floatStr ? '.' + floatStr : '');
  }
  return rest;
}
var commafy_1 = commafy;

/**
 * 转整数
 * @param { String/Number } str 数值
 *
 * @return {Number}
 */
var toInteger = helperCreateToNumber_1(staticParseInt_1);
var toInteger_1 = toInteger;

/**
 * 乘法运算
 *
 * @param { Number } num1 数值1
 * @param { Number } num2 数值2
 * @return {Number}
 */
function multiply(num1, num2) {
  var multiplier = toNumber_1(num1);
  var multiplicand = toNumber_1(num2);
  return helperMultiply_1(multiplier, multiplicand);
}
var multiply_1 = multiply;

function helperNumberAdd(addend, augend) {
  var str1 = toNumberString_1(addend);
  var str2 = toNumberString_1(augend);
  var ratio = Math.pow(10, Math.max(helperNumberDecimal_1(str1), helperNumberDecimal_1(str2)));
  return (multiply_1(addend, ratio) + multiply_1(augend, ratio)) / ratio;
}
var helperNumberAdd_1 = helperNumberAdd;

/**
 * 加法运算
 *
 * @param { Number } num1 被加数
 * @param { Number } num2 加数
 * @return {Number}
 */
function add$1(num1, num2) {
  return helperNumberAdd_1(toNumber_1(num1), toNumber_1(num2));
}
var add_1 = add$1;

/**
 * 减法运算
 *
 * @param { Number } num1 被减数
 * @param { Number } num2 减数
 * @return {Number}
 */
function subtract(num1, num2) {
  var subtrahend = toNumber_1(num1);
  var minuend = toNumber_1(num2);
  var str1 = toNumberString_1(subtrahend);
  var str2 = toNumberString_1(minuend);
  var digit1 = helperNumberDecimal_1(str1);
  var digit2 = helperNumberDecimal_1(str2);
  var ratio = Math.pow(10, Math.max(digit1, digit2));
  var precision = digit1 >= digit2 ? digit1 : digit2;
  return parseFloat(toFixed_1((subtrahend * ratio - minuend * ratio) / ratio, precision));
}
var subtract_1 = subtract;

function helperNumberDivide(divisor, dividend) {
  var str1 = toNumberString_1(divisor);
  var str2 = toNumberString_1(dividend);
  var divisorDecimal = helperNumberDecimal_1(str1);
  var dividendDecimal = helperNumberDecimal_1(str2);
  var powY = dividendDecimal - divisorDecimal;
  var isMinus = powY < 0;
  var multiplicand = Math.pow(10, isMinus ? Math.abs(powY) : powY);
  return multiply_1(str1.replace('.', '') / str2.replace('.', ''), isMinus ? 1 / multiplicand : multiplicand);
}
var helperNumberDivide_1 = helperNumberDivide;

/**
 * 除法运算
 *
 * @param { Number } num1 数值1
 * @param { Number } num2 数值2
 * @return {Number}
 */
function divide(num1, num2) {
  return helperNumberDivide_1(toNumber_1(num1), toNumber_1(num2));
}
var divide_1 = divide;

/**
  * 求和函数，将数值相加
  *
  * @param {Array} array 数组
  * @param {Function/String} iterate 方法或属性
  * @param {Object} context 上下文
  * @return {Number}
  */
function sum(array, iterate, context) {
  var result = 0;
  each_1(array, iterate ? isFunction_1$1(iterate) ? function () {
    result = helperNumberAdd_1(result, iterate.apply(context, arguments));
  } : function (val) {
    result = helperNumberAdd_1(result, get_1(val, iterate));
  } : function (val) {
    result = helperNumberAdd_1(result, val);
  });
  return result;
}
var sum_1 = sum;

/**
  * 求平均值函数
  *
  * @param {Array} array 数组
  * @param {Function/String} iterate 方法或属性
  * @param {Object} context 上下文
  * @return {Number}
  */
function mean(array, iterate, context) {
  return helperNumberDivide_1(sum_1(array, iterate, context), getSize_1(array));
}
var mean_1 = mean;

var staticStrFirst = 'first';
var staticStrFirst_1 = staticStrFirst;

var staticStrLast = 'last';
var staticStrLast_1 = staticStrLast;

function helperGetDateFullYear(date) {
  return date.getFullYear();
}
var helperGetDateFullYear_1 = helperGetDateFullYear;

var staticDayTime = 86400000;
var staticDayTime_1 = staticDayTime;

function helperGetDateMonth(date) {
  return date.getMonth();
}
var helperGetDateMonth_1 = helperGetDateMonth;

/**
  * 判断是否有效的Date对象
  *
  * @param {any} val 对象
  * @return {boolean}
  */
function isValidDate(val) {
  return isDate_1(val) && !isNaN(helperGetDateTime_1(val));
}
var isValidDate_1 = isValidDate;

/**
  * 返回前几月或后几月的日期
  *
  * @param {Date} date 日期或数字
  * @param {Number} offsetMonth 月(默认当前月)、前几个月、后几个月
  * @param {Number/String} offsetDay 获取哪天：月初(first)、月末(last)、指定天数(数值)，如果为空，但超过指定月份的天数时，则默认单月最后一天
  * @return {Date}
  */
function getWhatMonth(date, offsetMonth, offsetDay) {
  var monthNum = offsetMonth && !isNaN(offsetMonth) ? offsetMonth : 0;
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    if (offsetDay === staticStrFirst_1) {
      return new Date(helperGetDateFullYear_1(date), helperGetDateMonth_1(date) + monthNum, 1);
    } else if (offsetDay === staticStrLast_1) {
      return new Date(helperGetDateTime_1(getWhatMonth(date, monthNum + 1, staticStrFirst_1)) - 1);
    } else if (isNumber_1(offsetDay)) {
      date.setDate(offsetDay);
    }
    if (monthNum) {
      var currDate = date.getDate();
      date.setMonth(helperGetDateMonth_1(date) + monthNum);
      if (currDate !== date.getDate()) {
        // 当为指定天数，且被跨月了，则默认单月最后一天
        date.setDate(1);
        return new Date(helperGetDateTime_1(date) - staticDayTime_1);
      }
    }
  }
  return date;
}
var getWhatMonth_1 = getWhatMonth;

/**
  * 返回前几年或后几年的日期
  *
  * @param {Date} date 日期或数字
  * @param {Number} offset 年(默认当前年)、前几个年(数值)、后几个年(数值)
  * @param {Number/String} month 获取哪月(null默认当前年)、年初(first)、年末(last)、指定月份（0-11）
  * @return {Date}
  */
function getWhatYear(date, offset, month) {
  var number;
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    if (offset) {
      number = offset && !isNaN(offset) ? offset : 0;
      date.setFullYear(helperGetDateFullYear_1(date) + number);
    }
    if (month || !isNaN(month)) {
      if (month === staticStrFirst_1) {
        return new Date(helperGetDateFullYear_1(date), 0, 1);
      } else if (month === staticStrLast_1) {
        date.setMonth(11);
        return getWhatMonth_1(date, 0, staticStrLast_1);
      } else {
        date.setMonth(month);
      }
    }
  }
  return date;
}
var getWhatYear_1 = getWhatYear;

function getQuarterNumber(date) {
  var month = date.getMonth();
  if (month < 3) {
    return 1;
  } else if (month < 6) {
    return 2;
  } else if (month < 9) {
    return 3;
  }
  return 4;
}

/**
  * 返回前几季度或后几季度的日期
  *
  * @param {Date} date 日期
  * @param {Number} offset 季度(默认当前季度)、前几季度、后几季度
  * @param {Number} day 获取哪天：月初(first)、月末(last)、指定天数(数值)，如果为空，但超过指定月份的天数时，则默认单月最后一天
  * @return {Date}
  */
function getWhatQuarter(date, offset, day) {
  var currMonth,
    monthOffset = offset && !isNaN(offset) ? offset * 3 : 0;
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    currMonth = (getQuarterNumber(date) - 1) * 3;
    date.setMonth(currMonth);
    return getWhatMonth_1(date, monthOffset, day);
  }
  return date;
}
var getWhatQuarter_1 = getWhatQuarter;

/**
  * 返回前几天或后几天的日期
  *
  * @param {Date} date 日期或数字
  * @param {Number} offset 天(默认当天)、前几天、后几天
  * @param {String} mode 获取时分秒(null默认当前时分秒)、日初(first)、日末(last)
  * @return {Date}
  */
function getWhatDay(date, offset, mode) {
  date = toStringDate_1(date);
  if (isValidDate_1(date) && !isNaN(offset)) {
    date.setDate(date.getDate() + staticParseInt_1(offset));
    if (mode === staticStrFirst_1) {
      return new Date(helperGetDateFullYear_1(date), helperGetDateMonth_1(date), date.getDate());
    } else if (mode === staticStrLast_1) {
      return new Date(helperGetDateTime_1(getWhatDay(date, 1, staticStrFirst_1)) - 1);
    }
  }
  return date;
}
var getWhatDay_1 = getWhatDay;

function helperStringUpperCase(str) {
  return str.toUpperCase();
}
var helperStringUpperCase_1 = helperStringUpperCase;

var staticWeekTime = staticDayTime_1 * 7;
var staticWeekTime_1 = staticWeekTime;

/**
  * 返回前几周或后几周的星期几
  *
  * @param {Date} date 日期
  * @param {Number} offsetWeek 周(默认当前周)、前几周、后几周
  * @param {Number} offsetDay 星期天(默认0)、星期一(1)、星期二(2)、星期三(3)、星期四(4)、星期五(5)、星期六(6)
  * @param {Number} firstDay 周视图的起始天，默认星期一
  * @return {Date}
  */
function getWhatWeek(date, offsetWeek, offsetDay, firstDay) {
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    var hasCustomDay = isNumber_1(offsetDay);
    var hasStartDay = isNumber_1(firstDay);
    var whatDayTime = helperGetDateTime_1(date);
    // 如果指定了天或周视图起始天
    if (hasCustomDay || hasStartDay) {
      var viewStartDay = hasStartDay ? firstDay : setupDefaults_1.firstDayOfWeek;
      var currentDay = date.getDay();
      var customDay = hasCustomDay ? offsetDay : currentDay;
      if (currentDay !== customDay) {
        var offsetNum = 0;
        if (viewStartDay > currentDay) {
          offsetNum = -(7 - viewStartDay + currentDay);
        } else if (viewStartDay < currentDay) {
          offsetNum = viewStartDay - currentDay;
        }
        if (customDay > viewStartDay) {
          whatDayTime += ((customDay === 0 ? 7 : customDay) - viewStartDay + offsetNum) * staticDayTime_1;
        } else if (customDay < viewStartDay) {
          whatDayTime += (7 - viewStartDay + customDay + offsetNum) * staticDayTime_1;
        } else {
          whatDayTime += offsetNum * staticDayTime_1;
        }
      }
    }
    if (offsetWeek && !isNaN(offsetWeek)) {
      whatDayTime += offsetWeek * staticWeekTime_1;
    }
    return new Date(whatDayTime);
  }
  return date;
}
var getWhatWeek_1 = getWhatWeek;

function helperCreateGetDateWeek(getStartDate) {
  return function (date, firstDay) {
    var viewStartDay = isNumber_1(firstDay) ? firstDay : setupDefaults_1.firstDayOfWeek;
    var targetDate = getWhatWeek_1(date, 0, viewStartDay, viewStartDay);
    if (isValidDate_1(targetDate)) {
      var targetOffsetDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      var targerStartDate = getStartDate(targetDate);
      var targetFirstDay = targerStartDate.getDay();
      if (targetFirstDay > viewStartDay) {
        targerStartDate.setDate(7 - targetFirstDay + viewStartDay + 1);
      }
      if (targetFirstDay < viewStartDay) {
        targerStartDate.setDate(viewStartDay - targetFirstDay + 1);
      }
      return Math.floor((helperGetDateTime_1(targetOffsetDate) - helperGetDateTime_1(targerStartDate)) / staticWeekTime_1 + 1);
    }
    return NaN;
  };
}
var helperCreateGetDateWeek_1 = helperCreateGetDateWeek;

/**
  * 返回某个年份的第几周
  *
  * @param {Date} date 日期或数字
  * @param {Number} firstDay 从年初的星期几为起始开始周开始算，默认星期一
  * @return {Number}
  */
var getYearWeek = helperCreateGetDateWeek_1(function (targetDate) {
  return new Date(targetDate.getFullYear(), 0, 1);
});
var getYearWeek_1 = getYearWeek;

function helperGetYMD(date) {
  return new Date(helperGetDateFullYear_1(date), helperGetDateMonth_1(date), date.getDate());
}
var helperGetYMD_1 = helperGetYMD;

function helperGetYMDTime(date) {
  return helperGetDateTime_1(helperGetYMD_1(date));
}
var helperGetYMDTime_1 = helperGetYMDTime;

/**
  * 返回某个年份的第几天
  *
  * @param {Date} date 日期或数字
  * @return {Number}
  */
function getYearDay(date) {
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    return Math.floor((helperGetYMDTime_1(date) - helperGetYMDTime_1(getWhatYear_1(date, 0, staticStrFirst_1))) / staticDayTime_1) + 1;
  }
  return NaN;
}
var getYearDay_1 = getYearDay;

/**
  * 用指定字符从前面开始补全字符串
  *
  * @param {String} str 字符串
  * @param {Number} targetLength 结果长度
  * @param {Number} padString 补全字符
  * @return {String}
  */
function padStart(str, targetLength, padString) {
  var rest = toValueString_1(str);
  targetLength = targetLength >> 0;
  padString = isUndefined_1(padString) ? ' ' : '' + padString;
  if (rest.padStart) {
    return rest.padStart(targetLength, padString);
  }
  if (targetLength > rest.length) {
    targetLength -= rest.length;
    if (targetLength > padString.length) {
      padString += helperStringRepeat_1(padString, targetLength / padString.length);
    }
    return padString.slice(0, targetLength) + rest;
  }
  return rest;
}
var padStart_1 = padStart;

function handleCustomTemplate(date, formats, match, value) {
  var format = formats[match];
  if (format) {
    if (isFunction_1$1(format)) {
      return format(value, match, date);
    } else {
      return format[value];
    }
  }
  return value;
}
var dateFormatRE = /\[([^\]]+)]|y{2,4}|M{1,2}|d{1,2}|H{1,2}|h{1,2}|m{1,2}|s{1,2}|S{1,3}|Z{1,2}|W{1,2}|D{1,3}|[aAeEq]/g;

/**
  * 日期格式化为字符串，转义符号 []
  *
  * @param {Date} date 日期或数字
  * @param {String} format 输出日期格式(年份(yy|yyyy)、月份(M|MM自动补0)、天(d|dd自动补0)、12小时制(h|hh自动补0)、24小时制(H|HH自动补0)、分钟(m|mm自动补0)、秒(s|ss自动补0)、毫秒(S|SSS自动补0)、D当年的第几天、a/A上午下午、e/E星期几、w当年的第几周、W当月的第几周、q当年第几个季度、Z时区)
  * @param {Object} options {formats: {q: ['日', '一', '二', '三', '四', '五', '六'], E: function (value, match, date) {return '三'}, }} 自定义格式化模板
  * @return {String}
  */
function toDateString(date, format, options) {
  if (date) {
    date = toStringDate_1(date);
    if (isValidDate_1(date)) {
      var result = format || setupDefaults_1.parseDateFormat || setupDefaults_1.formatString;
      var hours = date.getHours();
      var apm = hours < 12 ? 'am' : 'pm';
      var formats = assign_1({}, setupDefaults_1.parseDateRules || setupDefaults_1.formatStringMatchs, options ? options.formats : null);
      var fy = function (match, length) {
        return ('' + helperGetDateFullYear_1(date)).substr(4 - length);
      };
      var fM = function (match, length) {
        return padStart_1(helperGetDateMonth_1(date) + 1, length, '0');
      };
      var fd = function (match, length) {
        return padStart_1(date.getDate(), length, '0');
      };
      var fH = function (match, length) {
        return padStart_1(hours, length, '0');
      };
      var fh = function (match, length) {
        return padStart_1(hours <= 12 ? hours : hours - 12, length, '0');
      };
      var fm = function (match, length) {
        return padStart_1(date.getMinutes(), length, '0');
      };
      var fs = function (match, length) {
        return padStart_1(date.getSeconds(), length, '0');
      };
      var fS = function (match, length) {
        return padStart_1(date.getMilliseconds(), length, '0');
      };
      var fZ = function (match, length) {
        var zoneHours = date.getTimezoneOffset() / 60 * -1;
        return handleCustomTemplate(date, formats, match, (zoneHours >= 0 ? '+' : '-') + padStart_1(zoneHours, 2, '0') + (length === 1 ? ':' : '') + '00');
      };
      var fW = function (match, length) {
        return padStart_1(handleCustomTemplate(date, formats, match, getYearWeek_1(date, (options ? options.firstDay : null) || setupDefaults_1.firstDayOfWeek)), length, '0');
      };
      var fD = function (match, length) {
        return padStart_1(handleCustomTemplate(date, formats, match, getYearDay_1(date)), length, '0');
      };
      var parseDates = {
        yyyy: fy,
        yy: fy,
        MM: fM,
        M: fM,
        dd: fd,
        d: fd,
        HH: fH,
        H: fH,
        hh: fh,
        h: fh,
        mm: fm,
        m: fm,
        ss: fs,
        s: fs,
        SSS: fS,
        S: fS,
        ZZ: fZ,
        Z: fZ,
        WW: fW,
        W: fW,
        DDD: fD,
        D: fD,
        a: function (match) {
          return handleCustomTemplate(date, formats, match, apm);
        },
        A: function (match) {
          return handleCustomTemplate(date, formats, match, helperStringUpperCase_1(apm));
        },
        e: function (match) {
          return handleCustomTemplate(date, formats, match, date.getDay());
        },
        E: function (match) {
          return handleCustomTemplate(date, formats, match, date.getDay());
        },
        q: function (match) {
          return handleCustomTemplate(date, formats, match, Math.floor((helperGetDateMonth_1(date) + 3) / 3));
        }
      };
      return result.replace(dateFormatRE, function (match, skip) {
        return skip || (parseDates[match] ? parseDates[match](match, match.length) : match);
      });
    }
    return 'Invalid Date';
  }
  return '';
}
var toDateString_1 = toDateString;

/**
 * 返回当前时间戳
 *
 * @returns Number
 */
var now = Date.now || function () {
  return helperGetDateTime_1(helperNewDate_1());
};
var now_1 = now;

/**
 * 将日期格式化为时间戳
 *
  * @param {String/Number/Date} str 日期或数字
  * @param {String} format 解析日期格式
 * @returns Number
 */
var timestamp = function (str, format) {
  if (str) {
    var date = toStringDate_1(str, format);
    return isDate_1(date) ? helperGetDateTime_1(date) : date;
  }
  return now_1();
};
var timestamp_1 = timestamp;

/**
 * 比较两个日期
 *
 * @param {Number/String/Date} date1 日期
 * @param {Number/String/Date} date2 日期
 * @param {String} format 对比格式
 */
function isDateSame(date1, date2, format) {
  if (date1 && date2) {
    date1 = toDateString_1(date1, format);
    return date1 !== 'Invalid Date' && date1 === toDateString_1(date2, format);
  }
  return false;
}
var isDateSame_1 = isDateSame;

/**
  * 返回某个月的第几周
  *
  * @param {Date} date 日期或数字
  * @param {Number} firstDay 周视图的起始天，默认星期一
  * @return {Number}
  */
var getMonthWeek = helperCreateGetDateWeek_1(function (targetDate) {
  return new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
});
var getMonthWeek_1 = getMonthWeek;

/**
  * 返回某个年份的天数
  *
  * @param {Date} date 日期或数字
  * @param {Number} offset 年(默认当年)、前几个年、后几个年
  * @return {Number}
  */
function getDayOfYear(date, year) {
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    return isLeapYear_1(getWhatYear_1(date, year)) ? 366 : 365;
  }
  return NaN;
}
var getDayOfYear_1 = getDayOfYear;

/**
  * 返回某个月份的天数
  *
  * @param {Date} date 日期或数字
  * @param {Number} offset 月(默认当月)、前几个月、后几个月
  * @return {Number}
  */
function getDayOfMonth(date, month) {
  date = toStringDate_1(date);
  if (isValidDate_1(date)) {
    return Math.floor((helperGetDateTime_1(getWhatMonth_1(date, month, staticStrLast_1)) - helperGetDateTime_1(getWhatMonth_1(date, month, staticStrFirst_1))) / staticDayTime_1) + 1;
  }
  return NaN;
}
var getDayOfMonth_1 = getDayOfMonth;

var dateDiffRules = [['yyyy', 31536000000], ['MM', 2592000000], ['dd', 86400000], ['HH', 3600000], ['mm', 60000], ['ss', 1000], ['S', 0]];

/**
  * 返回两个日期之间差距,如果结束日期小于开始日期done为fasle
  *
  * @param {Date} startDate 开始日期
  * @param {Date} endDate 结束日期或当期日期
  * @return {Object}
  */
function getDateDiff(startDate, endDate) {
  var startTime, endTime, item, diffTime, len, index;
  var result = {
    done: false,
    time: 0
  };
  startDate = toStringDate_1(startDate);
  endDate = endDate ? toStringDate_1(endDate) : helperNewDate_1();
  if (isValidDate_1(startDate) && isValidDate_1(endDate)) {
    startTime = helperGetDateTime_1(startDate);
    endTime = helperGetDateTime_1(endDate);
    if (startTime < endTime) {
      diffTime = result.time = endTime - startTime;
      result.done = true;
      for (index = 0, len = dateDiffRules.length; index < len; index++) {
        item = dateDiffRules[index];
        if (diffTime >= item[1]) {
          if (index === len - 1) {
            result[item[0]] = diffTime || 0;
          } else {
            result[item[0]] = Math.floor(diffTime / item[1]);
            diffTime -= result[item[0]] * item[1];
          }
        } else {
          result[item[0]] = 0;
        }
      }
    }
  }
  return result;
}
var getDateDiff_1 = getDateDiff;

/**
  * 用指定字符从后面开始补全字符串
  *
  * @param {String} str 字符串
  * @param {Number} targetLength 结果长度
  * @param {Number} padString 补全字符
  * @return {String}
  */
function padEnd(str, targetLength, padString) {
  var rest = toValueString_1(str);
  targetLength = targetLength >> 0;
  padString = isUndefined_1(padString) ? ' ' : '' + padString;
  if (rest.padEnd) {
    return rest.padEnd(targetLength, padString);
  }
  if (targetLength > rest.length) {
    targetLength -= rest.length;
    if (targetLength > padString.length) {
      padString += helperStringRepeat_1(padString, targetLength / padString.length);
    }
    return rest + padString.slice(0, targetLength);
  }
  return rest;
}
var padEnd_1 = padEnd;

/**
  * 将字符串重复 n 次
  *
  * @param {String} str 字符串
  * @param {Number} count 次数
  * @return {String}
  */
function repeat(str, count) {
  return helperStringRepeat_1(toValueString_1(str), count);
}
var repeat_1 = repeat;

/**
  * 去除字符串右边的空格
  *
  * @param {String} str 字符串
  * @return {String}
  */
function trimRight$1(str) {
  return str && str.trimRight ? str.trimRight() : toValueString_1(str).replace(/[\s\uFEFF\xA0]+$/g, '');
}
var trimRight_1 = trimRight$1;

/**
  * 去除字符串左边的空格
  *
  * @param {String} str 字符串
  * @return {String}
  */
function trimLeft$1(str) {
  return str && str.trimLeft ? str.trimLeft() : toValueString_1(str).replace(/^[\s\uFEFF\xA0]+/g, '');
}
var trimLeft_1 = trimLeft$1;

/**
  * 去除字符串左右两边的空格
  *
  * @param {String} str 字符串
  * @return {String}
  */
function trim$1(str) {
  return str && str.trim ? str.trim() : trimRight_1(trimLeft_1(str));
}
var trim_1 = trim$1;

var staticEscapeMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;'
};
var staticEscapeMap_1 = staticEscapeMap;

function helperFormatEscaper(dataMap) {
  var replaceRegexp = new RegExp('(?:' + keys_1(dataMap).join('|') + ')', 'g');
  return function (str) {
    return toValueString_1(str).replace(replaceRegexp, function (match) {
      return dataMap[match];
    });
  };
}
var helperFormatEscaper_1 = helperFormatEscaper;

/**
  * 转义HTML字符串，替换&, <, >, ", ', `字符
  *
  * @param {String} str 字符串
  * @return {String}
  */
var escape$1 = helperFormatEscaper_1(staticEscapeMap_1);
var _escape = escape$1;

var unescapeMap = {};
each_1(staticEscapeMap_1, function (item, key) {
  unescapeMap[staticEscapeMap_1[key]] = key;
});

/**
  * 反转escape
  *
  * @param {String} str 字符串
  * @return {String}
  */
var unescape$1 = helperFormatEscaper_1(unescapeMap);
var _unescape = unescape$1;

function helperStringSubstring(str, start, end) {
  return str.substring(start, end);
}
var helperStringSubstring_1 = helperStringSubstring;

function helperStringLowerCase(str) {
  return str.toLowerCase();
}
var helperStringLowerCase_1 = helperStringLowerCase;

var camelCacheMaps = {};

/**
  * 将带字符串转成驼峰字符串,例如： project-name 转为 projectName
  *
  * @param {String} str 字符串
  * @return {String}
  */
function camelCase(str) {
  str = toValueString_1(str);
  if (camelCacheMaps[str]) {
    return camelCacheMaps[str];
  }
  var strLen = str.length;
  var rest = str.replace(/([-]+)/g, function (text, flag, index) {
    return index && index + flag.length < strLen ? '-' : '';
  });
  strLen = rest.length;
  rest = rest.replace(/([A-Z]+)/g, function (text, upper, index) {
    var upperLen = upper.length;
    upper = helperStringLowerCase_1(upper);
    if (index) {
      if (upperLen > 2 && index + upperLen < strLen) {
        return helperStringUpperCase_1(helperStringSubstring_1(upper, 0, 1)) + helperStringSubstring_1(upper, 1, upperLen - 1) + helperStringUpperCase_1(helperStringSubstring_1(upper, upperLen - 1, upperLen));
      }
      return helperStringUpperCase_1(helperStringSubstring_1(upper, 0, 1)) + helperStringSubstring_1(upper, 1, upperLen);
    } else {
      if (upperLen > 1 && index + upperLen < strLen) {
        return helperStringSubstring_1(upper, 0, upperLen - 1) + helperStringUpperCase_1(helperStringSubstring_1(upper, upperLen - 1, upperLen));
      }
    }
    return upper;
  }).replace(/(-[a-zA-Z])/g, function (text, upper) {
    return helperStringUpperCase_1(helperStringSubstring_1(upper, 1, upper.length));
  });
  camelCacheMaps[str] = rest;
  return rest;
}
var camelCase_1 = camelCase;

var kebabCacheMaps = {};

/**
  * 将带驼峰字符串转成字符串,例如： projectName 转为 project-name
  *
  * @param {String} str 字符串
  * @return {String}
  */
function kebabCase(str) {
  str = toValueString_1(str);
  if (kebabCacheMaps[str]) {
    return kebabCacheMaps[str];
  }
  if (/^[A-Z]+$/.test(str)) {
    return helperStringLowerCase_1(str);
  }
  var rest = str.replace(/^([a-z])([A-Z]+)([a-z]+)$/, function (text, prevLower, upper, nextLower) {
    var upperLen = upper.length;
    if (upperLen > 1) {
      return prevLower + '-' + helperStringLowerCase_1(helperStringSubstring_1(upper, 0, upperLen - 1)) + '-' + helperStringLowerCase_1(helperStringSubstring_1(upper, upperLen - 1, upperLen)) + nextLower;
    }
    return helperStringLowerCase_1(prevLower + '-' + upper + nextLower);
  }).replace(/^([A-Z]+)([a-z]+)?$/, function (text, upper, nextLower) {
    var upperLen = upper.length;
    return helperStringLowerCase_1(helperStringSubstring_1(upper, 0, upperLen - 1) + '-' + helperStringSubstring_1(upper, upperLen - 1, upperLen) + (nextLower || ''));
  }).replace(/([a-z]?)([A-Z]+)([a-z]?)/g, function (text, prevLower, upper, nextLower, index) {
    var upperLen = upper.length;
    if (upperLen > 1) {
      if (prevLower) {
        prevLower += '-';
      }
      if (nextLower) {
        return (prevLower || '') + helperStringLowerCase_1(helperStringSubstring_1(upper, 0, upperLen - 1)) + '-' + helperStringLowerCase_1(helperStringSubstring_1(upper, upperLen - 1, upperLen)) + nextLower;
      }
    }
    return (prevLower || '') + (index ? '-' : '') + helperStringLowerCase_1(upper) + (nextLower || '');
  });
  rest = rest.replace(/([-]+)/g, function (text, flag, index) {
    return index && index + flag.length < rest.length ? '-' : '';
  });
  kebabCacheMaps[str] = rest;
  return rest;
}
var kebabCase_1 = kebabCase;

/**
  * 判断字符串是否在源字符串的头部
  *
  * @param {String} str 字符串
  * @param {String/Number} val 值
  * @param {Number} startIndex 开始索引
  * @return {String}
  */
function startsWith(str, val, startIndex) {
  var rest = toValueString_1(str);
  return (arguments.length === 1 ? rest : rest.substring(startIndex)).indexOf(val) === 0;
}
var startsWith_1 = startsWith;

/**
  * 判断字符串是否在源字符串的尾部
  *
  * @param {String} str 字符串
  * @param {String/Number} val 值
  * @param {Number} startIndex 开始索引
  * @return {String}
  */
function endsWith(str, val, startIndex) {
  var rest = toValueString_1(str);
  var argsLen = arguments.length;
  return argsLen > 1 && (argsLen > 2 ? rest.substring(0, startIndex).indexOf(val) === startIndex - 1 : rest.indexOf(val) === rest.length - 1);
}
var endsWith_1 = endsWith;

/**
 * 解析动态字符串模板
 * @param {atring} str 字符串模板
 * @param {any | any[]} args 对象
 * @param {any} options 
 */
function template(str, args, options) {
  return toValueString_1(str).replace((options || setupDefaults_1).tmplRE || /\{{2}([.\w[\]\s]+)\}{2}/g, function (match, key) {
    return get_1(args, trim_1(key));
  });
}
var template_1 = template;

/**
 * 字符串格式化占位符
 * @param { string } str 
 * @param { object | any[] } obj 
 */
function toFormatString(str, obj) {
  return template_1(str, obj, {
    tmplRE: /\{([.\w[\]\s]+)\}/g
  });
}
var toFormatString_1 = toFormatString;

/**
 * 一个空的方法，始终返回 undefined，可用于初始化值
 */
function noop$1() {}
var noop_1 = noop$1;

/**
  * 创建一个绑定上下文的函数
  *
  * @param {Function} callback 函数
  * @param {Object} context 上下文
  * @param {*} args 额外的参数
  * @return {Object}
  */
function bind$1(callback, context) {
  var args = slice_1(arguments, 2);
  return function () {
    return callback.apply(context, slice_1(arguments).concat(args));
  };
}
var bind_1 = bind$1;

/**
  * 创建一个只能调用一次的函数,只会返回第一次执行后的结果
  *
  * @param {Function} callback 函数
  * @param {Object} context 上下文
  * @param {*} args 额外的参数
  * @return {Object}
  */
function once(callback, context) {
  var done = false;
  var rest = null;
  var args = slice_1(arguments, 2);
  return function () {
    if (done) {
      return rest;
    }
    rest = callback.apply(context, slice_1(arguments).concat(args));
    done = true;
    return rest;
  };
}
var once_1 = once;

/**
  * 创建一个函数, 调用次数超过 count 次之后执行回调并将所有结果记住后返回
  *
  * @param {Number} count 调用次数
  * @param {Function} callback 完成回调
  * @return {Object}
  */
function after(count, callback, context) {
  var runCount = 0;
  var rests = [];
  return function () {
    var args = arguments;
    runCount++;
    if (runCount <= count) {
      rests.push(args[0]);
    }
    if (runCount >= count) {
      callback.apply(context, [rests].concat(slice_1(args)));
    }
  };
}
var after_1 = after;

/**
  * 创建一个函数, 调用次数不超过 count 次之前执行回调并将所有结果记住后返回
  *
  * @param {Number} count 调用次数
  * @param {Function} callback 完成回调
  * @return {Object}
  */
function before(count, callback, context) {
  var runCount = 0;
  var rests = [];
  context = context || this;
  return function () {
    var args = arguments;
    runCount++;
    if (runCount < count) {
      rests.push(args[0]);
      callback.apply(context, [rests].concat(slice_1(args)));
    }
  };
}
var before_1 = before;

/**
  * 节流函数；当被调用 n 毫秒后才会执行，如果在这时间内又被调用则至少每隔 n 秒毫秒调用一次该函数
  *
  * @param {Function} callback 回调
  * @param {Number} wait 多少秒毫
  * @param {Object} options 参数{leading: 是否在之前执行, trailing: 是否在之后执行}
  * @return {Function}
  */
function throttle$1(callback, wait, options) {
  var args = null;
  var context = null;
  var opts = options || {};
  var runFlag = false;
  var timeout = null;
  var optLeading = 'leading' in opts ? opts.leading : true;
  var optTrailing = 'trailing' in opts ? opts.trailing : false;
  var gcFn = function () {
    args = null;
    context = null;
  };
  var runFn = function () {
    runFlag = true;
    callback.apply(context, args);
    timeout = setTimeout(endFn, wait);
    gcFn();
  };
  var endFn = function () {
    timeout = null;
    if (!runFlag && optTrailing === true) {
      runFn();
    }
  };
  var cancelFn = function () {
    var rest = timeout !== null;
    if (rest) {
      clearTimeout(timeout);
    }
    gcFn();
    timeout = null;
    runFlag = false;
    return rest;
  };
  var throttled = function () {
    args = arguments;
    context = this;
    runFlag = false;
    if (timeout === null) {
      if (optLeading === true) {
        runFn();
      } else if (optTrailing === true) {
        timeout = setTimeout(endFn, wait);
      }
    }
  };
  throttled.cancel = cancelFn;
  return throttled;
}
var throttle_1 = throttle$1;

/**
  * 函数去抖；当被调用 n 毫秒后才会执行，如果在这时间内又被调用则将重新计算执行时间
  *
  * @param {Function} callback 回调
  * @param {Number} wait 多少秒毫
  * @param {Object} options 参数{leading: 是否在之前执行, trailing: 是否在之后执行}
  * @return {Function}
  */
function debounce$3(callback, wait, options) {
  var args = null;
  var context = null;
  var opts = options || {};
  var runFlag = false;
  var timeout = null;
  var isLeading = typeof options === 'boolean';
  var optLeading = 'leading' in opts ? opts.leading : isLeading;
  var optTrailing = 'trailing' in opts ? opts.trailing : !isLeading;
  var gcFn = function () {
    args = null;
    context = null;
  };
  var runFn = function () {
    runFlag = true;
    callback.apply(context, args);
    gcFn();
  };
  var endFn = function () {
    if (optLeading === true) {
      timeout = null;
    }
    if (!runFlag && optTrailing === true) {
      runFn();
    }
  };
  var cancelFn = function () {
    var rest = timeout !== null;
    if (rest) {
      clearTimeout(timeout);
    }
    gcFn();
    timeout = null;
    runFlag = false;
    return rest;
  };
  var debounced = function () {
    runFlag = false;
    args = arguments;
    context = this;
    if (timeout === null) {
      if (optLeading === true) {
        runFn();
      }
    } else {
      clearTimeout(timeout);
    }
    timeout = setTimeout(endFn, wait);
  };
  debounced.cancel = cancelFn;
  return debounced;
}
var debounce_1 = debounce$3;

/**
  * 该方法和 setTimeout 一样的效果，区别就是支持上下文和额外参数
  *
  * @param {Function} callback 函数
  * @param {Number} wait 延迟毫秒
  * @param {*} args 额外的参数
  * @return {Number}
 */
function delay(callback, wait) {
  var args = slice_1(arguments, 2);
  var context = this;
  return setTimeout(function () {
    callback.apply(context, args);
  }, wait);
}
var delay_1 = delay;

var staticDecodeURIComponent = decodeURIComponent;
var staticDecodeURIComponent_1 = staticDecodeURIComponent;

/**
 * 反序列化查询参数
 * @param {String} query 字符串
 */
function unserialize(str) {
  var items;
  var result = {};
  if (str && isString_1(str)) {
    arrayEach_1(str.split('&'), function (param) {
      items = param.split('=');
      result[staticDecodeURIComponent_1(items[0])] = staticDecodeURIComponent_1(items[1] || '');
    });
  }
  return result;
}
var unserialize_1 = unserialize;

var staticEncodeURIComponent = encodeURIComponent;
var staticEncodeURIComponent_1 = staticEncodeURIComponent;

function stringifyParams(resultVal, resultKey, isArr) {
  var _arr;
  var result = [];
  each_1(resultVal, function (item, key) {
    _arr = isArray_1(item);
    if (isPlainObject_1(item) || _arr) {
      result = result.concat(stringifyParams(item, resultKey + '[' + key + ']', _arr));
    } else {
      result.push(staticEncodeURIComponent_1(resultKey + '[' + (isArr ? '' : key) + ']') + '=' + staticEncodeURIComponent_1(isNull_1(item) ? '' : item));
    }
  });
  return result;
}

/**
 * 序列化查询参数
 *
 * @param {Object} query 查询参数
 */
function serialize$1(query) {
  var _arr;
  var params = [];
  each_1(query, function (item, key) {
    if (!isUndefined_1(item)) {
      _arr = isArray_1(item);
      if (isPlainObject_1(item) || _arr) {
        params = params.concat(stringifyParams(item, key, _arr));
      } else {
        params.push(staticEncodeURIComponent_1(key) + '=' + staticEncodeURIComponent_1(isNull_1(item) ? '' : item));
      }
    }
  });
  return params.join('&').replace(/%20/g, '+');
}
var serialize_1 = serialize$1;

/* eslint-disable valid-typeof */
var staticLocation = typeof location === staticStrUndefined_1 ? 0 : location;
var staticLocation_1 = staticLocation;

function helperGetLocatOrigin() {
  return staticLocation_1 ? staticLocation_1.origin || staticLocation_1.protocol + '//' + staticLocation_1.host : '';
}
var helperGetLocatOrigin_1 = helperGetLocatOrigin;

function parseURLQuery(uri) {
  return unserialize_1(uri.split('?')[1] || '');
}
function parseUrl$1(url) {
  var hashs, portText, searchs, parsed;
  var href = '' + url;
  if (href.indexOf('//') === 0) {
    href = (staticLocation_1 ? staticLocation_1.protocol : '') + href;
  } else if (href.indexOf('/') === 0) {
    href = helperGetLocatOrigin_1() + href;
  }
  searchs = href.replace(/#.*/, '').match(/(\?.*)/);
  parsed = {
    href: href,
    hash: '',
    host: '',
    hostname: '',
    protocol: '',
    port: '',
    search: searchs && searchs[1] && searchs[1].length > 1 ? searchs[1] : ''
  };
  parsed.path = href.replace(/^([a-z0-9.+-]*:)\/\//, function (text, protocol) {
    parsed.protocol = protocol;
    return '';
  }).replace(/^([a-z0-9.+-]*)(:\d+)?\/?/, function (text, hostname, port) {
    portText = port || '';
    parsed.port = portText.replace(':', '');
    parsed.hostname = hostname;
    parsed.host = hostname + portText;
    return '/';
  }).replace(/(#.*)/, function (text, hash) {
    parsed.hash = hash.length > 1 ? hash : '';
    return '';
  });
  hashs = parsed.hash.match(/#((.*)\?|(.*))/);
  parsed.pathname = parsed.path.replace(/(\?|#.*).*/, '');
  parsed.origin = parsed.protocol + '//' + parsed.host;
  parsed.hashKey = hashs ? hashs[2] || hashs[1] || '' : '';
  parsed.hashQuery = parseURLQuery(parsed.hash);
  parsed.searchQuery = parseURLQuery(parsed.search);
  return parsed;
}
var parseUrl_1 = parseUrl$1;

function getBaseURL() {
  if (staticLocation_1) {
    var pathname = staticLocation_1.pathname;
    var lastIndex = lastIndexOf_1(pathname, '/') + 1;
    return helperGetLocatOrigin_1() + (lastIndex === pathname.length ? pathname : pathname.substring(0, lastIndex));
  }
  return '';
}
var getBaseURL_1 = getBaseURL;

/**
  * 获取地址栏信息
  *
  * @return Object
  */
function locat() {
  return staticLocation_1 ? parseUrl_1(staticLocation_1.href) : {};
}
var locat_1 = locat;

function toCookieUnitTime(unit, expires) {
  var num = parseFloat(expires);
  var nowdate = helperNewDate_1();
  var time = helperGetDateTime_1(nowdate);
  switch (unit) {
    case 'y':
      return helperGetDateTime_1(getWhatYear_1(nowdate, num));
    case 'M':
      return helperGetDateTime_1(getWhatMonth_1(nowdate, num));
    case 'd':
      return helperGetDateTime_1(getWhatDay_1(nowdate, num));
    case 'h':
    case 'H':
      return time + num * 60 * 60 * 1000;
    case 'm':
      return time + num * 60 * 1000;
    case 's':
      return time + num * 1000;
  }
  return time;
}
function toCookieUTCString(date) {
  return (isDate_1(date) ? date : new Date(date)).toUTCString();
}

/**
  * cookie操作函数
  * @param {String/Array/Object} name 键/数组/对象
  * @param {String} value 值
  * @param {Object} options 参数
  *   @param {String} name: 键
  *   @param {Object} value: 值
  *   @param {String} path: 路径
  *   @param {String} domain: 作用域
  *   @param {Boolean} secure: 设置为安全的,只能用https协议
  *   @param {Number} expires: 过期时间,可以指定日期或者字符串，默认天
  */
function cookie(name, value, options) {
  if (staticDocument_1) {
    var opts, expires, values, result, cookies, keyIndex;
    var inserts = [];
    var args = arguments;
    if (isArray_1(name)) {
      inserts = name;
    } else if (args.length > 1) {
      inserts = [assign_1({
        name: name,
        value: value
      }, options)];
    } else if (isObject_1(name)) {
      inserts = [name];
    }
    if (inserts.length > 0) {
      arrayEach_1(inserts, function (obj) {
        opts = assign_1({}, setupDefaults_1.cookies, obj);
        values = [];
        if (opts.name) {
          expires = opts.expires;
          values.push(staticEncodeURIComponent_1(opts.name) + '=' + staticEncodeURIComponent_1(isObject_1(opts.value) ? JSON.stringify(opts.value) : opts.value));
          if (expires) {
            if (isNaN(expires)) {
              // UTCString || Unit
              expires = expires.replace(/^([0-9]+)(y|M|d|H|h|m|s)$/, function (text, num, unit) {
                return toCookieUTCString(toCookieUnitTime(unit, num));
              });
            } else if (/^[0-9]{11,13}$/.test(expires) || isDate_1(expires)) {
              // Date || now
              expires = toCookieUTCString(expires);
            } else {
              // day
              expires = toCookieUTCString(toCookieUnitTime('d', expires));
            }
            opts.expires = expires;
          }
          arrayEach_1(['expires', 'path', 'domain', 'secure'], function (key) {
            if (!isUndefined_1(opts[key])) {
              values.push(opts[key] && key === 'secure' ? key : key + '=' + opts[key]);
            }
          });
        }
        staticDocument_1.cookie = values.join('; ');
      });
      return true;
    } else {
      result = {};
      cookies = staticDocument_1.cookie;
      if (cookies) {
        arrayEach_1(cookies.split('; '), function (val) {
          keyIndex = val.indexOf('=');
          result[staticDecodeURIComponent_1(val.substring(0, keyIndex))] = staticDecodeURIComponent_1(val.substring(keyIndex + 1) || '');
        });
      }
      return args.length === 1 ? result[name] : result;
    }
  }
  return false;
}
function hasCookieItem(value) {
  return includes_1(cookieKeys(), value);
}
function getCookieItem(name) {
  return cookie(name);
}
function setCookieItem(name, value, options) {
  cookie(name, value, options);
  return cookie;
}
function removeCookieItem(name, options) {
  cookie(name, '', assign_1({
    expires: -1
  }, setupDefaults_1.cookies, options));
}
function cookieKeys() {
  return keys_1(cookie());
}
function cookieJson() {
  return cookie();
}
assign_1(cookie, {
  has: hasCookieItem,
  set: setCookieItem,
  setItem: setCookieItem,
  get: getCookieItem,
  getItem: getCookieItem,
  remove: removeCookieItem,
  removeItem: removeCookieItem,
  keys: cookieKeys,
  getJSON: cookieJson
});
var cookie_1 = cookie;

/* eslint-disable valid-typeof */
function isBrowseStorage(storage) {
  try {
    var testKey = '__xe_t';
    storage.setItem(testKey, 1);
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}
function isBrowseType(type) {
  return navigator.userAgent.indexOf(type) > -1;
}

/**
  * 获取浏览器内核
  * @return Object
  */
function browse() {
  var $body, isChrome, isEdge;
  var isMobile = false;
  var isLocalStorage = false;
  var isSessionStorage = false;
  var result = {
    isNode: false,
    isMobile: isMobile,
    isPC: false,
    isDoc: !!staticDocument_1
  };
  if (!staticWindow_1 && typeof process !== staticStrUndefined_1) {
    result.isNode = true;
  } else {
    isEdge = isBrowseType('Edge');
    isChrome = isBrowseType('Chrome');
    isMobile = /(Android|webOS|iPhone|iPad|iPod|SymbianOS|BlackBerry|Windows Phone)/.test(navigator.userAgent);
    if (result.isDoc) {
      $body = staticDocument_1.body || staticDocument_1.documentElement;
      arrayEach_1(['webkit', 'khtml', 'moz', 'ms', 'o'], function (core) {
        result['-' + core] = !!$body[core + 'MatchesSelector'];
      });
    }
    try {
      isLocalStorage = isBrowseStorage(staticWindow_1.localStorage);
    } catch (e) {}
    try {
      isSessionStorage = isBrowseStorage(staticWindow_1.sessionStorage);
    } catch (e) {}
    assign_1(result, {
      edge: isEdge,
      firefox: isBrowseType('Firefox'),
      msie: !isEdge && result['-ms'],
      safari: !isChrome && !isEdge && isBrowseType('Safari'),
      isMobile: isMobile,
      isPC: !isMobile,
      isLocalStorage: isLocalStorage,
      isSessionStorage: isSessionStorage
    });
  }
  return result;
}
var browse_1 = browse;

// 核心


// 对象相关的方法






// 数组相关的方法






































// 基础方法

































































// 数值相关方法


















// 日期相关的方法


















// 字符串相关的方法
















// 函数相关的方法










// 地址相关的方法




// 浏览器相关的方法




assign_1(ctor, {
  // object
  assign: assign_1,
  objectEach: objectEach_1,
  lastObjectEach: lastObjectEach_1,
  objectMap: objectMap_1,
  merge: merge_1,
  // array
  uniq: uniq_1,
  union: union_1,
  sortBy: sortBy_1,
  orderBy: orderBy_1,
  shuffle: shuffle_1,
  sample: sample_1,
  some: some_1,
  every: every_1,
  slice: slice_1,
  filter: filter_1,
  find: find_1,
  findLast: findLast_1,
  findKey: findKey_1,
  includes: includes_1,
  arrayIndexOf: arrayIndexOf_1,
  arrayLastIndexOf: arrayLastIndexOf_1,
  map: map_1,
  reduce: reduce_1,
  copyWithin: copyWithin_1,
  chunk: chunk_1,
  zip: zip_1,
  unzip: unzip_1,
  zipObject: zipObject_1,
  flatten: flatten_1,
  toArray: toArray_1,
  includeArrays: includeArrays_1,
  pluck: pluck_1,
  invoke: invoke_1,
  arrayEach: arrayEach_1,
  lastArrayEach: lastArrayEach_1,
  toArrayTree: toArrayTree_1,
  toTreeArray: toTreeArray_1,
  findTree: findTree_1,
  eachTree: eachTree_1,
  mapTree: mapTree_1,
  filterTree: filterTree_1,
  searchTree: searchTree_1,
  // base
  hasOwnProp: hasOwnProp_1,
  eqNull: eqNull_1,
  isNaN: _isNaN,
  isFinite: _isFinite,
  isUndefined: isUndefined_1,
  isArray: isArray_1,
  isFloat: isFloat_1,
  isInteger: isInteger_1,
  isFunction: isFunction_1$1,
  isBoolean: isBoolean_1,
  isString: isString_1,
  isNumber: isNumber_1,
  isRegExp: isRegExp_1,
  isObject: isObject_1,
  isPlainObject: isPlainObject_1,
  isDate: isDate_1,
  isError: isError_1,
  isTypeError: isTypeError_1,
  isEmpty: isEmpty_1,
  isNull: isNull_1,
  isSymbol: isSymbol_1,
  isArguments: isArguments_1,
  isElement: isElement_1,
  isDocument: isDocument_1,
  isWindow: isWindow_1,
  isFormData: isFormData_1,
  isMap: isMap_1,
  isWeakMap: isWeakMap_1,
  isSet: isSet_1,
  isWeakSet: isWeakSet_1,
  isLeapYear: isLeapYear_1,
  isMatch: isMatch_1,
  isEqual: isEqual_1,
  isEqualWith: isEqualWith_1,
  getType: getType_1,
  uniqueId: uniqueId_1,
  getSize: getSize_1,
  indexOf: indexOf_1,
  lastIndexOf: lastIndexOf_1,
  findIndexOf: findIndexOf_1,
  findLastIndexOf: findLastIndexOf_1,
  toStringJSON: toStringJSON_1,
  toJSONString: toJSONString_1,
  keys: keys_1,
  values: values_1,
  entries: entries_1,
  pick: pick_1,
  omit: omit_1,
  first: first_1,
  last: last_1,
  each: each_1,
  forOf: forOf_1,
  lastForOf: lastForOf_1,
  lastEach: lastEach_1,
  has: has_1,
  get: get_1,
  set: set_1,
  groupBy: groupBy_1,
  countBy: countBy_1,
  clone: clone_1,
  clear: clear_1,
  remove: remove_1,
  range: range_1,
  destructuring: destructuring_1,
  // number
  random: random_1,
  min: min_1,
  max: max_1,
  commafy: commafy_1,
  round: round_1,
  ceil: ceil_1,
  floor: floor_1,
  toFixed: toFixed_1,
  toNumber: toNumber_1,
  toNumberString: toNumberString_1,
  toInteger: toInteger_1,
  add: add_1,
  subtract: subtract_1,
  multiply: multiply_1,
  divide: divide_1,
  sum: sum_1,
  mean: mean_1,
  // date
  now: now_1,
  timestamp: timestamp_1,
  isValidDate: isValidDate_1,
  isDateSame: isDateSame_1,
  toStringDate: toStringDate_1,
  toDateString: toDateString_1,
  getWhatYear: getWhatYear_1,
  getWhatQuarter: getWhatQuarter_1,
  getWhatMonth: getWhatMonth_1,
  getWhatWeek: getWhatWeek_1,
  getWhatDay: getWhatDay_1,
  getYearDay: getYearDay_1,
  getYearWeek: getYearWeek_1,
  getMonthWeek: getMonthWeek_1,
  getDayOfYear: getDayOfYear_1,
  getDayOfMonth: getDayOfMonth_1,
  getDateDiff: getDateDiff_1,
  // string
  trim: trim_1,
  trimLeft: trimLeft_1,
  trimRight: trimRight_1,
  escape: _escape,
  unescape: _unescape,
  camelCase: camelCase_1,
  kebabCase: kebabCase_1,
  repeat: repeat_1,
  padStart: padStart_1,
  padEnd: padEnd_1,
  startsWith: startsWith_1,
  endsWith: endsWith_1,
  template: template_1,
  toFormatString: toFormatString_1,
  toString: toValueString_1,
  toValueString: toValueString_1,
  // function
  noop: noop_1,
  property: property_1,
  bind: bind_1,
  once: once_1,
  after: after_1,
  before: before_1,
  throttle: throttle_1,
  debounce: debounce_1,
  delay: delay_1,
  // url
  unserialize: unserialize_1,
  serialize: serialize_1,
  parseUrl: parseUrl_1,
  // web
  getBaseURL: getBaseURL_1,
  locat: locat_1,
  browse: browse_1,
  cookie: cookie_1
});
var xeUtils = ctor;

var prism = createCommonjsModule(function (module) {
/* **********************************************
     Begin prism-core.js
********************************************** */

/// <reference lib="WebWorker"/>

var _self = typeof window !== 'undefined' ? window // if in browser
: typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope ? self // if in worker
: {} // if in node js
;

/**
 * Prism: Lightweight, robust, elegant syntax highlighting
 *
 * @license MIT <https://opensource.org/licenses/MIT>
 * @author Lea Verou <https://lea.verou.me>
 * @namespace
 * @public
 */
var Prism = function (_self) {
  // Private helper vars
  var lang = /(?:^|\s)lang(?:uage)?-([\w-]+)(?=\s|$)/i;
  var uniqueId = 0;

  // The grammar object for plaintext
  var plainTextGrammar = {};
  var _ = {
    /**
     * By default, Prism will attempt to highlight all code elements (by calling {@link Prism.highlightAll}) on the
     * current page after the page finished loading. This might be a problem if e.g. you wanted to asynchronously load
     * additional languages or plugins yourself.
     *
     * By setting this value to `true`, Prism will not automatically highlight all code elements on the page.
     *
     * You obviously have to change this value before the automatic highlighting started. To do this, you can add an
     * empty Prism object into the global scope before loading the Prism script like this:
     *
     * ```js
     * window.Prism = window.Prism || {};
     * Prism.manual = true;
     * // add a new <script> to load Prism's script
     * ```
     *
     * @default false
     * @type {boolean}
     * @memberof Prism
     * @public
     */
    manual: _self.Prism && _self.Prism.manual,
    /**
     * By default, if Prism is in a web worker, it assumes that it is in a worker it created itself, so it uses
     * `addEventListener` to communicate with its parent instance. However, if you're using Prism manually in your
     * own worker, you don't want it to do this.
     *
     * By setting this value to `true`, Prism will not add its own listeners to the worker.
     *
     * You obviously have to change this value before Prism executes. To do this, you can add an
     * empty Prism object into the global scope before loading the Prism script like this:
     *
     * ```js
     * window.Prism = window.Prism || {};
     * Prism.disableWorkerMessageHandler = true;
     * // Load Prism's script
     * ```
     *
     * @default false
     * @type {boolean}
     * @memberof Prism
     * @public
     */
    disableWorkerMessageHandler: _self.Prism && _self.Prism.disableWorkerMessageHandler,
    /**
     * A namespace for utility methods.
     *
     * All function in this namespace that are not explicitly marked as _public_ are for __internal use only__ and may
     * change or disappear at any time.
     *
     * @namespace
     * @memberof Prism
     */
    util: {
      encode: function encode(tokens) {
        if (tokens instanceof Token) {
          return new Token(tokens.type, encode(tokens.content), tokens.alias);
        } else if (Array.isArray(tokens)) {
          return tokens.map(encode);
        } else {
          return tokens.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\u00a0/g, ' ');
        }
      },
      /**
       * Returns the name of the type of the given value.
       *
       * @param {any} o
       * @returns {string}
       * @example
       * type(null)      === 'Null'
       * type(undefined) === 'Undefined'
       * type(123)       === 'Number'
       * type('foo')     === 'String'
       * type(true)      === 'Boolean'
       * type([1, 2])    === 'Array'
       * type({})        === 'Object'
       * type(String)    === 'Function'
       * type(/abc+/)    === 'RegExp'
       */
      type: function (o) {
        return Object.prototype.toString.call(o).slice(8, -1);
      },
      /**
       * Returns a unique number for the given object. Later calls will still return the same number.
       *
       * @param {Object} obj
       * @returns {number}
       */
      objId: function (obj) {
        if (!obj['__id']) {
          Object.defineProperty(obj, '__id', {
            value: ++uniqueId
          });
        }
        return obj['__id'];
      },
      /**
       * Creates a deep clone of the given object.
       *
       * The main intended use of this function is to clone language definitions.
       *
       * @param {T} o
       * @param {Record<number, any>} [visited]
       * @returns {T}
       * @template T
       */
      clone: function deepClone(o, visited) {
        visited = visited || {};
        var clone;
        var id;
        switch (_.util.type(o)) {
          case 'Object':
            id = _.util.objId(o);
            if (visited[id]) {
              return visited[id];
            }
            clone = /** @type {Record<string, any>} */{};
            visited[id] = clone;
            for (var key in o) {
              if (o.hasOwnProperty(key)) {
                clone[key] = deepClone(o[key], visited);
              }
            }
            return /** @type {any} */clone;
          case 'Array':
            id = _.util.objId(o);
            if (visited[id]) {
              return visited[id];
            }
            clone = [];
            visited[id] = clone;
            ( /** @type {Array} */ /** @type {any} */o).forEach(function (v, i) {
              clone[i] = deepClone(v, visited);
            });
            return /** @type {any} */clone;
          default:
            return o;
        }
      },
      /**
       * Returns the Prism language of the given element set by a `language-xxxx` or `lang-xxxx` class.
       *
       * If no language is set for the element or the element is `null` or `undefined`, `none` will be returned.
       *
       * @param {Element} element
       * @returns {string}
       */
      getLanguage: function (element) {
        while (element) {
          var m = lang.exec(element.className);
          if (m) {
            return m[1].toLowerCase();
          }
          element = element.parentElement;
        }
        return 'none';
      },
      /**
       * Sets the Prism `language-xxxx` class of the given element.
       *
       * @param {Element} element
       * @param {string} language
       * @returns {void}
       */
      setLanguage: function (element, language) {
        // remove all `language-xxxx` classes
        // (this might leave behind a leading space)
        element.className = element.className.replace(RegExp(lang, 'gi'), '');

        // add the new `language-xxxx` class
        // (using `classList` will automatically clean up spaces for us)
        element.classList.add('language-' + language);
      },
      /**
       * Returns the script element that is currently executing.
       *
       * This does __not__ work for line script element.
       *
       * @returns {HTMLScriptElement | null}
       */
      currentScript: function () {
        if (typeof document === 'undefined') {
          return null;
        }
        if ('currentScript' in document && 1 < 2 /* hack to trip TS' flow analysis */) {
          return /** @type {any} */document.currentScript;
        }

        // IE11 workaround
        // we'll get the src of the current script by parsing IE11's error stack trace
        // this will not work for inline scripts

        try {
          throw new Error();
        } catch (err) {
          // Get file src url from stack. Specifically works with the format of stack traces in IE.
          // A stack will look like this:
          //
          // Error
          //    at _.util.currentScript (http://localhost/components/prism-core.js:119:5)
          //    at Global code (http://localhost/components/prism-core.js:606:1)

          var src = (/at [^(\r\n]*\((.*):[^:]+:[^:]+\)$/i.exec(err.stack) || [])[1];
          if (src) {
            var scripts = document.getElementsByTagName('script');
            for (var i in scripts) {
              if (scripts[i].src == src) {
                return scripts[i];
              }
            }
          }
          return null;
        }
      },
      /**
       * Returns whether a given class is active for `element`.
       *
       * The class can be activated if `element` or one of its ancestors has the given class and it can be deactivated
       * if `element` or one of its ancestors has the negated version of the given class. The _negated version_ of the
       * given class is just the given class with a `no-` prefix.
       *
       * Whether the class is active is determined by the closest ancestor of `element` (where `element` itself is
       * closest ancestor) that has the given class or the negated version of it. If neither `element` nor any of its
       * ancestors have the given class or the negated version of it, then the default activation will be returned.
       *
       * In the paradoxical situation where the closest ancestor contains __both__ the given class and the negated
       * version of it, the class is considered active.
       *
       * @param {Element} element
       * @param {string} className
       * @param {boolean} [defaultActivation=false]
       * @returns {boolean}
       */
      isActive: function (element, className, defaultActivation) {
        var no = 'no-' + className;
        while (element) {
          var classList = element.classList;
          if (classList.contains(className)) {
            return true;
          }
          if (classList.contains(no)) {
            return false;
          }
          element = element.parentElement;
        }
        return !!defaultActivation;
      }
    },
    /**
     * This namespace contains all currently loaded languages and the some helper functions to create and modify languages.
     *
     * @namespace
     * @memberof Prism
     * @public
     */
    languages: {
      /**
       * The grammar for plain, unformatted text.
       */
      plain: plainTextGrammar,
      plaintext: plainTextGrammar,
      text: plainTextGrammar,
      txt: plainTextGrammar,
      /**
       * Creates a deep copy of the language with the given id and appends the given tokens.
       *
       * If a token in `redef` also appears in the copied language, then the existing token in the copied language
       * will be overwritten at its original position.
       *
       * ## Best practices
       *
       * Since the position of overwriting tokens (token in `redef` that overwrite tokens in the copied language)
       * doesn't matter, they can technically be in any order. However, this can be confusing to others that trying to
       * understand the language definition because, normally, the order of tokens matters in Prism grammars.
       *
       * Therefore, it is encouraged to order overwriting tokens according to the positions of the overwritten tokens.
       * Furthermore, all non-overwriting tokens should be placed after the overwriting ones.
       *
       * @param {string} id The id of the language to extend. This has to be a key in `Prism.languages`.
       * @param {Grammar} redef The new tokens to append.
       * @returns {Grammar} The new language created.
       * @public
       * @example
       * Prism.languages['css-with-colors'] = Prism.languages.extend('css', {
       *     // Prism.languages.css already has a 'comment' token, so this token will overwrite CSS' 'comment' token
       *     // at its original position
       *     'comment': { ... },
       *     // CSS doesn't have a 'color' token, so this token will be appended
       *     'color': /\b(?:red|green|blue)\b/
       * });
       */
      extend: function (id, redef) {
        var lang = _.util.clone(_.languages[id]);
        for (var key in redef) {
          lang[key] = redef[key];
        }
        return lang;
      },
      /**
       * Inserts tokens _before_ another token in a language definition or any other grammar.
       *
       * ## Usage
       *
       * This helper method makes it easy to modify existing languages. For example, the CSS language definition
       * not only defines CSS highlighting for CSS documents, but also needs to define highlighting for CSS embedded
       * in HTML through `<style>` elements. To do this, it needs to modify `Prism.languages.markup` and add the
       * appropriate tokens. However, `Prism.languages.markup` is a regular JavaScript object literal, so if you do
       * this:
       *
       * ```js
       * Prism.languages.markup.style = {
       *     // token
       * };
       * ```
       *
       * then the `style` token will be added (and processed) at the end. `insertBefore` allows you to insert tokens
       * before existing tokens. For the CSS example above, you would use it like this:
       *
       * ```js
       * Prism.languages.insertBefore('markup', 'cdata', {
       *     'style': {
       *         // token
       *     }
       * });
       * ```
       *
       * ## Special cases
       *
       * If the grammars of `inside` and `insert` have tokens with the same name, the tokens in `inside`'s grammar
       * will be ignored.
       *
       * This behavior can be used to insert tokens after `before`:
       *
       * ```js
       * Prism.languages.insertBefore('markup', 'comment', {
       *     'comment': Prism.languages.markup.comment,
       *     // tokens after 'comment'
       * });
       * ```
       *
       * ## Limitations
       *
       * The main problem `insertBefore` has to solve is iteration order. Since ES2015, the iteration order for object
       * properties is guaranteed to be the insertion order (except for integer keys) but some browsers behave
       * differently when keys are deleted and re-inserted. So `insertBefore` can't be implemented by temporarily
       * deleting properties which is necessary to insert at arbitrary positions.
       *
       * To solve this problem, `insertBefore` doesn't actually insert the given tokens into the target object.
       * Instead, it will create a new object and replace all references to the target object with the new one. This
       * can be done without temporarily deleting properties, so the iteration order is well-defined.
       *
       * However, only references that can be reached from `Prism.languages` or `insert` will be replaced. I.e. if
       * you hold the target object in a variable, then the value of the variable will not change.
       *
       * ```js
       * var oldMarkup = Prism.languages.markup;
       * var newMarkup = Prism.languages.insertBefore('markup', 'comment', { ... });
       *
       * assert(oldMarkup !== Prism.languages.markup);
       * assert(newMarkup === Prism.languages.markup);
       * ```
       *
       * @param {string} inside The property of `root` (e.g. a language id in `Prism.languages`) that contains the
       * object to be modified.
       * @param {string} before The key to insert before.
       * @param {Grammar} insert An object containing the key-value pairs to be inserted.
       * @param {Object<string, any>} [root] The object containing `inside`, i.e. the object that contains the
       * object to be modified.
       *
       * Defaults to `Prism.languages`.
       * @returns {Grammar} The new grammar object.
       * @public
       */
      insertBefore: function (inside, before, insert, root) {
        root = root || ( /** @type {any} */_.languages);
        var grammar = root[inside];
        /** @type {Grammar} */
        var ret = {};
        for (var token in grammar) {
          if (grammar.hasOwnProperty(token)) {
            if (token == before) {
              for (var newToken in insert) {
                if (insert.hasOwnProperty(newToken)) {
                  ret[newToken] = insert[newToken];
                }
              }
            }

            // Do not insert token which also occur in insert. See #1525
            if (!insert.hasOwnProperty(token)) {
              ret[token] = grammar[token];
            }
          }
        }
        var old = root[inside];
        root[inside] = ret;

        // Update references in other language definitions
        _.languages.DFS(_.languages, function (key, value) {
          if (value === old && key != inside) {
            this[key] = ret;
          }
        });
        return ret;
      },
      // Traverse a language definition with Depth First Search
      DFS: function DFS(o, callback, type, visited) {
        visited = visited || {};
        var objId = _.util.objId;
        for (var i in o) {
          if (o.hasOwnProperty(i)) {
            callback.call(o, i, o[i], type || i);
            var property = o[i];
            var propertyType = _.util.type(property);
            if (propertyType === 'Object' && !visited[objId(property)]) {
              visited[objId(property)] = true;
              DFS(property, callback, null, visited);
            } else if (propertyType === 'Array' && !visited[objId(property)]) {
              visited[objId(property)] = true;
              DFS(property, callback, i, visited);
            }
          }
        }
      }
    },
    plugins: {},
    /**
     * This is the most high-level function in Prism’s API.
     * It fetches all the elements that have a `.language-xxxx` class and then calls {@link Prism.highlightElement} on
     * each one of them.
     *
     * This is equivalent to `Prism.highlightAllUnder(document, async, callback)`.
     *
     * @param {boolean} [async=false] Same as in {@link Prism.highlightAllUnder}.
     * @param {HighlightCallback} [callback] Same as in {@link Prism.highlightAllUnder}.
     * @memberof Prism
     * @public
     */
    highlightAll: function (async, callback) {
      _.highlightAllUnder(document, async, callback);
    },
    /**
     * Fetches all the descendants of `container` that have a `.language-xxxx` class and then calls
     * {@link Prism.highlightElement} on each one of them.
     *
     * The following hooks will be run:
     * 1. `before-highlightall`
     * 2. `before-all-elements-highlight`
     * 3. All hooks of {@link Prism.highlightElement} for each element.
     *
     * @param {ParentNode} container The root element, whose descendants that have a `.language-xxxx` class will be highlighted.
     * @param {boolean} [async=false] Whether each element is to be highlighted asynchronously using Web Workers.
     * @param {HighlightCallback} [callback] An optional callback to be invoked on each element after its highlighting is done.
     * @memberof Prism
     * @public
     */
    highlightAllUnder: function (container, async, callback) {
      var env = {
        callback: callback,
        container: container,
        selector: 'code[class*="language-"], [class*="language-"] code, code[class*="lang-"], [class*="lang-"] code'
      };
      _.hooks.run('before-highlightall', env);
      env.elements = Array.prototype.slice.apply(env.container.querySelectorAll(env.selector));
      _.hooks.run('before-all-elements-highlight', env);
      for (var i = 0, element; element = env.elements[i++];) {
        _.highlightElement(element, async === true, env.callback);
      }
    },
    /**
     * Highlights the code inside a single element.
     *
     * The following hooks will be run:
     * 1. `before-sanity-check`
     * 2. `before-highlight`
     * 3. All hooks of {@link Prism.highlight}. These hooks will be run by an asynchronous worker if `async` is `true`.
     * 4. `before-insert`
     * 5. `after-highlight`
     * 6. `complete`
     *
     * Some the above hooks will be skipped if the element doesn't contain any text or there is no grammar loaded for
     * the element's language.
     *
     * @param {Element} element The element containing the code.
     * It must have a class of `language-xxxx` to be processed, where `xxxx` is a valid language identifier.
     * @param {boolean} [async=false] Whether the element is to be highlighted asynchronously using Web Workers
     * to improve performance and avoid blocking the UI when highlighting very large chunks of code. This option is
     * [disabled by default](https://prismjs.com/faq.html#why-is-asynchronous-highlighting-disabled-by-default).
     *
     * Note: All language definitions required to highlight the code must be included in the main `prism.js` file for
     * asynchronous highlighting to work. You can build your own bundle on the
     * [Download page](https://prismjs.com/download.html).
     * @param {HighlightCallback} [callback] An optional callback to be invoked after the highlighting is done.
     * Mostly useful when `async` is `true`, since in that case, the highlighting is done asynchronously.
     * @memberof Prism
     * @public
     */
    highlightElement: function (element, async, callback) {
      // Find language
      var language = _.util.getLanguage(element);
      var grammar = _.languages[language];

      // Set language on the element, if not present
      _.util.setLanguage(element, language);

      // Set language on the parent, for styling
      var parent = element.parentElement;
      if (parent && parent.nodeName.toLowerCase() === 'pre') {
        _.util.setLanguage(parent, language);
      }
      var code = element.textContent;
      var env = {
        element: element,
        language: language,
        grammar: grammar,
        code: code
      };
      function insertHighlightedCode(highlightedCode) {
        env.highlightedCode = highlightedCode;
        _.hooks.run('before-insert', env);
        env.element.innerHTML = env.highlightedCode;
        _.hooks.run('after-highlight', env);
        _.hooks.run('complete', env);
        callback && callback.call(env.element);
      }
      _.hooks.run('before-sanity-check', env);

      // plugins may change/add the parent/element
      parent = env.element.parentElement;
      if (parent && parent.nodeName.toLowerCase() === 'pre' && !parent.hasAttribute('tabindex')) {
        parent.setAttribute('tabindex', '0');
      }
      if (!env.code) {
        _.hooks.run('complete', env);
        callback && callback.call(env.element);
        return;
      }
      _.hooks.run('before-highlight', env);
      if (!env.grammar) {
        insertHighlightedCode(_.util.encode(env.code));
        return;
      }
      if (async && _self.Worker) {
        var worker = new Worker(_.filename);
        worker.onmessage = function (evt) {
          insertHighlightedCode(evt.data);
        };
        worker.postMessage(JSON.stringify({
          language: env.language,
          code: env.code,
          immediateClose: true
        }));
      } else {
        insertHighlightedCode(_.highlight(env.code, env.grammar, env.language));
      }
    },
    /**
     * Low-level function, only use if you know what you’re doing. It accepts a string of text as input
     * and the language definitions to use, and returns a string with the HTML produced.
     *
     * The following hooks will be run:
     * 1. `before-tokenize`
     * 2. `after-tokenize`
     * 3. `wrap`: On each {@link Token}.
     *
     * @param {string} text A string with the code to be highlighted.
     * @param {Grammar} grammar An object containing the tokens to use.
     *
     * Usually a language definition like `Prism.languages.markup`.
     * @param {string} language The name of the language definition passed to `grammar`.
     * @returns {string} The highlighted HTML.
     * @memberof Prism
     * @public
     * @example
     * Prism.highlight('var foo = true;', Prism.languages.javascript, 'javascript');
     */
    highlight: function (text, grammar, language) {
      var env = {
        code: text,
        grammar: grammar,
        language: language
      };
      _.hooks.run('before-tokenize', env);
      if (!env.grammar) {
        throw new Error('The language "' + env.language + '" has no grammar.');
      }
      env.tokens = _.tokenize(env.code, env.grammar);
      _.hooks.run('after-tokenize', env);
      return Token.stringify(_.util.encode(env.tokens), env.language);
    },
    /**
     * This is the heart of Prism, and the most low-level function you can use. It accepts a string of text as input
     * and the language definitions to use, and returns an array with the tokenized code.
     *
     * When the language definition includes nested tokens, the function is called recursively on each of these tokens.
     *
     * This method could be useful in other contexts as well, as a very crude parser.
     *
     * @param {string} text A string with the code to be highlighted.
     * @param {Grammar} grammar An object containing the tokens to use.
     *
     * Usually a language definition like `Prism.languages.markup`.
     * @returns {TokenStream} An array of strings and tokens, a token stream.
     * @memberof Prism
     * @public
     * @example
     * let code = `var foo = 0;`;
     * let tokens = Prism.tokenize(code, Prism.languages.javascript);
     * tokens.forEach(token => {
     *     if (token instanceof Prism.Token && token.type === 'number') {
     *         console.log(`Found numeric literal: ${token.content}`);
     *     }
     * });
     */
    tokenize: function (text, grammar) {
      var rest = grammar.rest;
      if (rest) {
        for (var token in rest) {
          grammar[token] = rest[token];
        }
        delete grammar.rest;
      }
      var tokenList = new LinkedList();
      addAfter(tokenList, tokenList.head, text);
      matchGrammar(text, tokenList, grammar, tokenList.head, 0);
      return toArray(tokenList);
    },
    /**
     * @namespace
     * @memberof Prism
     * @public
     */
    hooks: {
      all: {},
      /**
       * Adds the given callback to the list of callbacks for the given hook.
       *
       * The callback will be invoked when the hook it is registered for is run.
       * Hooks are usually directly run by a highlight function but you can also run hooks yourself.
       *
       * One callback function can be registered to multiple hooks and the same hook multiple times.
       *
       * @param {string} name The name of the hook.
       * @param {HookCallback} callback The callback function which is given environment variables.
       * @public
       */
      add: function (name, callback) {
        var hooks = _.hooks.all;
        hooks[name] = hooks[name] || [];
        hooks[name].push(callback);
      },
      /**
       * Runs a hook invoking all registered callbacks with the given environment variables.
       *
       * Callbacks will be invoked synchronously and in the order in which they were registered.
       *
       * @param {string} name The name of the hook.
       * @param {Object<string, any>} env The environment variables of the hook passed to all callbacks registered.
       * @public
       */
      run: function (name, env) {
        var callbacks = _.hooks.all[name];
        if (!callbacks || !callbacks.length) {
          return;
        }
        for (var i = 0, callback; callback = callbacks[i++];) {
          callback(env);
        }
      }
    },
    Token: Token
  };
  _self.Prism = _;

  // Typescript note:
  // The following can be used to import the Token type in JSDoc:
  //
  //   @typedef {InstanceType<import("./prism-core")["Token"]>} Token

  /**
   * Creates a new token.
   *
   * @param {string} type See {@link Token#type type}
   * @param {string | TokenStream} content See {@link Token#content content}
   * @param {string|string[]} [alias] The alias(es) of the token.
   * @param {string} [matchedStr=""] A copy of the full string this token was created from.
   * @class
   * @global
   * @public
   */
  function Token(type, content, alias, matchedStr) {
    /**
     * The type of the token.
     *
     * This is usually the key of a pattern in a {@link Grammar}.
     *
     * @type {string}
     * @see GrammarToken
     * @public
     */
    this.type = type;
    /**
     * The strings or tokens contained by this token.
     *
     * This will be a token stream if the pattern matched also defined an `inside` grammar.
     *
     * @type {string | TokenStream}
     * @public
     */
    this.content = content;
    /**
     * The alias(es) of the token.
     *
     * @type {string|string[]}
     * @see GrammarToken
     * @public
     */
    this.alias = alias;
    // Copy of the full string this token was created from
    this.length = (matchedStr || '').length | 0;
  }

  /**
   * A token stream is an array of strings and {@link Token Token} objects.
   *
   * Token streams have to fulfill a few properties that are assumed by most functions (mostly internal ones) that process
   * them.
   *
   * 1. No adjacent strings.
   * 2. No empty strings.
   *
   *    The only exception here is the token stream that only contains the empty string and nothing else.
   *
   * @typedef {Array<string | Token>} TokenStream
   * @global
   * @public
   */

  /**
   * Converts the given token or token stream to an HTML representation.
   *
   * The following hooks will be run:
   * 1. `wrap`: On each {@link Token}.
   *
   * @param {string | Token | TokenStream} o The token or token stream to be converted.
   * @param {string} language The name of current language.
   * @returns {string} The HTML representation of the token or token stream.
   * @memberof Token
   * @static
   */
  Token.stringify = function stringify(o, language) {
    if (typeof o == 'string') {
      return o;
    }
    if (Array.isArray(o)) {
      var s = '';
      o.forEach(function (e) {
        s += stringify(e, language);
      });
      return s;
    }
    var env = {
      type: o.type,
      content: stringify(o.content, language),
      tag: 'span',
      classes: ['token', o.type],
      attributes: {},
      language: language
    };
    var aliases = o.alias;
    if (aliases) {
      if (Array.isArray(aliases)) {
        Array.prototype.push.apply(env.classes, aliases);
      } else {
        env.classes.push(aliases);
      }
    }
    _.hooks.run('wrap', env);
    var attributes = '';
    for (var name in env.attributes) {
      attributes += ' ' + name + '="' + (env.attributes[name] || '').replace(/"/g, '&quot;') + '"';
    }
    return '<' + env.tag + ' class="' + env.classes.join(' ') + '"' + attributes + '>' + env.content + '</' + env.tag + '>';
  };

  /**
   * @param {RegExp} pattern
   * @param {number} pos
   * @param {string} text
   * @param {boolean} lookbehind
   * @returns {RegExpExecArray | null}
   */
  function matchPattern(pattern, pos, text, lookbehind) {
    pattern.lastIndex = pos;
    var match = pattern.exec(text);
    if (match && lookbehind && match[1]) {
      // change the match to remove the text matched by the Prism lookbehind group
      var lookbehindLength = match[1].length;
      match.index += lookbehindLength;
      match[0] = match[0].slice(lookbehindLength);
    }
    return match;
  }

  /**
   * @param {string} text
   * @param {LinkedList<string | Token>} tokenList
   * @param {any} grammar
   * @param {LinkedListNode<string | Token>} startNode
   * @param {number} startPos
   * @param {RematchOptions} [rematch]
   * @returns {void}
   * @private
   *
   * @typedef RematchOptions
   * @property {string} cause
   * @property {number} reach
   */
  function matchGrammar(text, tokenList, grammar, startNode, startPos, rematch) {
    for (var token in grammar) {
      if (!grammar.hasOwnProperty(token) || !grammar[token]) {
        continue;
      }
      var patterns = grammar[token];
      patterns = Array.isArray(patterns) ? patterns : [patterns];
      for (var j = 0; j < patterns.length; ++j) {
        if (rematch && rematch.cause == token + ',' + j) {
          return;
        }
        var patternObj = patterns[j];
        var inside = patternObj.inside;
        var lookbehind = !!patternObj.lookbehind;
        var greedy = !!patternObj.greedy;
        var alias = patternObj.alias;
        if (greedy && !patternObj.pattern.global) {
          // Without the global flag, lastIndex won't work
          var flags = patternObj.pattern.toString().match(/[imsuy]*$/)[0];
          patternObj.pattern = RegExp(patternObj.pattern.source, flags + 'g');
        }

        /** @type {RegExp} */
        var pattern = patternObj.pattern || patternObj;
        for (
        // iterate the token list and keep track of the current token/string position
        var currentNode = startNode.next, pos = startPos; currentNode !== tokenList.tail; pos += currentNode.value.length, currentNode = currentNode.next) {
          if (rematch && pos >= rematch.reach) {
            break;
          }
          var str = currentNode.value;
          if (tokenList.length > text.length) {
            // Something went terribly wrong, ABORT, ABORT!
            return;
          }
          if (str instanceof Token) {
            continue;
          }
          var removeCount = 1; // this is the to parameter of removeBetween
          var match;
          if (greedy) {
            match = matchPattern(pattern, pos, text, lookbehind);
            if (!match || match.index >= text.length) {
              break;
            }
            var from = match.index;
            var to = match.index + match[0].length;
            var p = pos;

            // find the node that contains the match
            p += currentNode.value.length;
            while (from >= p) {
              currentNode = currentNode.next;
              p += currentNode.value.length;
            }
            // adjust pos (and p)
            p -= currentNode.value.length;
            pos = p;

            // the current node is a Token, then the match starts inside another Token, which is invalid
            if (currentNode.value instanceof Token) {
              continue;
            }

            // find the last node which is affected by this match
            for (var k = currentNode; k !== tokenList.tail && (p < to || typeof k.value === 'string'); k = k.next) {
              removeCount++;
              p += k.value.length;
            }
            removeCount--;

            // replace with the new match
            str = text.slice(pos, p);
            match.index -= pos;
          } else {
            match = matchPattern(pattern, 0, str, lookbehind);
            if (!match) {
              continue;
            }
          }

          // eslint-disable-next-line no-redeclare
          var from = match.index;
          var matchStr = match[0];
          var before = str.slice(0, from);
          var after = str.slice(from + matchStr.length);
          var reach = pos + str.length;
          if (rematch && reach > rematch.reach) {
            rematch.reach = reach;
          }
          var removeFrom = currentNode.prev;
          if (before) {
            removeFrom = addAfter(tokenList, removeFrom, before);
            pos += before.length;
          }
          removeRange(tokenList, removeFrom, removeCount);
          var wrapped = new Token(token, inside ? _.tokenize(matchStr, inside) : matchStr, alias, matchStr);
          currentNode = addAfter(tokenList, removeFrom, wrapped);
          if (after) {
            addAfter(tokenList, currentNode, after);
          }
          if (removeCount > 1) {
            // at least one Token object was removed, so we have to do some rematching
            // this can only happen if the current pattern is greedy

            /** @type {RematchOptions} */
            var nestedRematch = {
              cause: token + ',' + j,
              reach: reach
            };
            matchGrammar(text, tokenList, grammar, currentNode.prev, pos, nestedRematch);

            // the reach might have been extended because of the rematching
            if (rematch && nestedRematch.reach > rematch.reach) {
              rematch.reach = nestedRematch.reach;
            }
          }
        }
      }
    }
  }

  /**
   * @typedef LinkedListNode
   * @property {T} value
   * @property {LinkedListNode<T> | null} prev The previous node.
   * @property {LinkedListNode<T> | null} next The next node.
   * @template T
   * @private
   */

  /**
   * @template T
   * @private
   */
  function LinkedList() {
    /** @type {LinkedListNode<T>} */
    var head = {
      value: null,
      prev: null,
      next: null
    };
    /** @type {LinkedListNode<T>} */
    var tail = {
      value: null,
      prev: head,
      next: null
    };
    head.next = tail;

    /** @type {LinkedListNode<T>} */
    this.head = head;
    /** @type {LinkedListNode<T>} */
    this.tail = tail;
    this.length = 0;
  }

  /**
   * Adds a new node with the given value to the list.
   *
   * @param {LinkedList<T>} list
   * @param {LinkedListNode<T>} node
   * @param {T} value
   * @returns {LinkedListNode<T>} The added node.
   * @template T
   */
  function addAfter(list, node, value) {
    // assumes that node != list.tail && values.length >= 0
    var next = node.next;
    var newNode = {
      value: value,
      prev: node,
      next: next
    };
    node.next = newNode;
    next.prev = newNode;
    list.length++;
    return newNode;
  }
  /**
   * Removes `count` nodes after the given node. The given node will not be removed.
   *
   * @param {LinkedList<T>} list
   * @param {LinkedListNode<T>} node
   * @param {number} count
   * @template T
   */
  function removeRange(list, node, count) {
    var next = node.next;
    for (var i = 0; i < count && next !== list.tail; i++) {
      next = next.next;
    }
    node.next = next;
    next.prev = node;
    list.length -= i;
  }
  /**
   * @param {LinkedList<T>} list
   * @returns {T[]}
   * @template T
   */
  function toArray(list) {
    var array = [];
    var node = list.head.next;
    while (node !== list.tail) {
      array.push(node.value);
      node = node.next;
    }
    return array;
  }
  if (!_self.document) {
    if (!_self.addEventListener) {
      // in Node.js
      return _;
    }
    if (!_.disableWorkerMessageHandler) {
      // In worker
      _self.addEventListener('message', function (evt) {
        var message = JSON.parse(evt.data);
        var lang = message.language;
        var code = message.code;
        var immediateClose = message.immediateClose;
        _self.postMessage(_.highlight(code, _.languages[lang], lang));
        if (immediateClose) {
          _self.close();
        }
      }, false);
    }
    return _;
  }

  // Get current script and highlight
  var script = _.util.currentScript();
  if (script) {
    _.filename = script.src;
    if (script.hasAttribute('data-manual')) {
      _.manual = true;
    }
  }
  function highlightAutomaticallyCallback() {
    if (!_.manual) {
      _.highlightAll();
    }
  }
  if (!_.manual) {
    // If the document state is "loading", then we'll use DOMContentLoaded.
    // If the document state is "interactive" and the prism.js script is deferred, then we'll also use the
    // DOMContentLoaded event because there might be some plugins or languages which have also been deferred and they
    // might take longer one animation frame to execute which can create a race condition where only some plugins have
    // been loaded when Prism.highlightAll() is executed, depending on how fast resources are loaded.
    // See https://github.com/PrismJS/prism/issues/2102
    var readyState = document.readyState;
    if (readyState === 'loading' || readyState === 'interactive' && script && script.defer) {
      document.addEventListener('DOMContentLoaded', highlightAutomaticallyCallback);
    } else {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(highlightAutomaticallyCallback);
      } else {
        window.setTimeout(highlightAutomaticallyCallback, 16);
      }
    }
  }
  return _;
}(_self);
if (module.exports) {
  module.exports = Prism;
}

// hack for components to work correctly in node.js
if (typeof commonjsGlobal !== 'undefined') {
  commonjsGlobal.Prism = Prism;
}

// some additional documentation/types

/**
 * The expansion of a simple `RegExp` literal to support additional properties.
 *
 * @typedef GrammarToken
 * @property {RegExp} pattern The regular expression of the token.
 * @property {boolean} [lookbehind=false] If `true`, then the first capturing group of `pattern` will (effectively)
 * behave as a lookbehind group meaning that the captured text will not be part of the matched text of the new token.
 * @property {boolean} [greedy=false] Whether the token is greedy.
 * @property {string|string[]} [alias] An optional alias or list of aliases.
 * @property {Grammar} [inside] The nested grammar of this token.
 *
 * The `inside` grammar will be used to tokenize the text value of each token of this kind.
 *
 * This can be used to make nested and even recursive language definitions.
 *
 * Note: This can cause infinite recursion. Be careful when you embed different languages or even the same language into
 * each another.
 * @global
 * @public
 */

/**
 * @typedef Grammar
 * @type {Object<string, RegExp | GrammarToken | Array<RegExp | GrammarToken>>}
 * @property {Grammar} [rest] An optional grammar object that will be appended to this grammar.
 * @global
 * @public
 */

/**
 * A function which will invoked after an element was successfully highlighted.
 *
 * @callback HighlightCallback
 * @param {Element} element The element successfully highlighted.
 * @returns {void}
 * @global
 * @public
 */

/**
 * @callback HookCallback
 * @param {Object<string, any>} env The environment variables of the hook.
 * @returns {void}
 * @global
 * @public
 */

/* **********************************************
     Begin prism-markup.js
********************************************** */

Prism.languages.markup = {
  'comment': {
    pattern: /<!--(?:(?!<!--)[\s\S])*?-->/,
    greedy: true
  },
  'prolog': {
    pattern: /<\?[\s\S]+?\?>/,
    greedy: true
  },
  'doctype': {
    // https://www.w3.org/TR/xml/#NT-doctypedecl
    pattern: /<!DOCTYPE(?:[^>"'[\]]|"[^"]*"|'[^']*')+(?:\[(?:[^<"'\]]|"[^"]*"|'[^']*'|<(?!!--)|<!--(?:[^-]|-(?!->))*-->)*\]\s*)?>/i,
    greedy: true,
    inside: {
      'internal-subset': {
        pattern: /(^[^\[]*\[)[\s\S]+(?=\]>$)/,
        lookbehind: true,
        greedy: true,
        inside: null // see below
      },
      'string': {
        pattern: /"[^"]*"|'[^']*'/,
        greedy: true
      },
      'punctuation': /^<!|>$|[[\]]/,
      'doctype-tag': /^DOCTYPE/i,
      'name': /[^\s<>'"]+/
    }
  },
  'cdata': {
    pattern: /<!\[CDATA\[[\s\S]*?\]\]>/i,
    greedy: true
  },
  'tag': {
    pattern: /<\/?(?!\d)[^\s>\/=$<%]+(?:\s(?:\s*[^\s>\/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+(?=[\s>]))|(?=[\s/>])))+)?\s*\/?>/,
    greedy: true,
    inside: {
      'tag': {
        pattern: /^<\/?[^\s>\/]+/,
        inside: {
          'punctuation': /^<\/?/,
          'namespace': /^[^\s>\/:]+:/
        }
      },
      'special-attr': [],
      'attr-value': {
        pattern: /=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+)/,
        inside: {
          'punctuation': [{
            pattern: /^=/,
            alias: 'attr-equals'
          }, {
            pattern: /^(\s*)["']|["']$/,
            lookbehind: true
          }]
        }
      },
      'punctuation': /\/?>/,
      'attr-name': {
        pattern: /[^\s>\/]+/,
        inside: {
          'namespace': /^[^\s>\/:]+:/
        }
      }
    }
  },
  'entity': [{
    pattern: /&[\da-z]{1,8};/i,
    alias: 'named-entity'
  }, /&#x?[\da-f]{1,8};/i]
};
Prism.languages.markup['tag'].inside['attr-value'].inside['entity'] = Prism.languages.markup['entity'];
Prism.languages.markup['doctype'].inside['internal-subset'].inside = Prism.languages.markup;

// Plugin to make entity title show the real entity, idea by Roman Komarov
Prism.hooks.add('wrap', function (env) {
  if (env.type === 'entity') {
    env.attributes['title'] = env.content.replace(/&amp;/, '&');
  }
});
Object.defineProperty(Prism.languages.markup.tag, 'addInlined', {
  /**
   * Adds an inlined language to markup.
   *
   * An example of an inlined language is CSS with `<style>` tags.
   *
   * @param {string} tagName The name of the tag that contains the inlined language. This name will be treated as
   * case insensitive.
   * @param {string} lang The language key.
   * @example
   * addInlined('style', 'css');
   */
  value: function addInlined(tagName, lang) {
    var includedCdataInside = {};
    includedCdataInside['language-' + lang] = {
      pattern: /(^<!\[CDATA\[)[\s\S]+?(?=\]\]>$)/i,
      lookbehind: true,
      inside: Prism.languages[lang]
    };
    includedCdataInside['cdata'] = /^<!\[CDATA\[|\]\]>$/i;
    var inside = {
      'included-cdata': {
        pattern: /<!\[CDATA\[[\s\S]*?\]\]>/i,
        inside: includedCdataInside
      }
    };
    inside['language-' + lang] = {
      pattern: /[\s\S]+/,
      inside: Prism.languages[lang]
    };
    var def = {};
    def[tagName] = {
      pattern: RegExp(/(<__[^>]*>)(?:<!\[CDATA\[(?:[^\]]|\](?!\]>))*\]\]>|(?!<!\[CDATA\[)[\s\S])*?(?=<\/__>)/.source.replace(/__/g, function () {
        return tagName;
      }), 'i'),
      lookbehind: true,
      greedy: true,
      inside: inside
    };
    Prism.languages.insertBefore('markup', 'cdata', def);
  }
});
Object.defineProperty(Prism.languages.markup.tag, 'addAttribute', {
  /**
   * Adds an pattern to highlight languages embedded in HTML attributes.
   *
   * An example of an inlined language is CSS with `style` attributes.
   *
   * @param {string} attrName The name of the tag that contains the inlined language. This name will be treated as
   * case insensitive.
   * @param {string} lang The language key.
   * @example
   * addAttribute('style', 'css');
   */
  value: function (attrName, lang) {
    Prism.languages.markup.tag.inside['special-attr'].push({
      pattern: RegExp(/(^|["'\s])/.source + '(?:' + attrName + ')' + /\s*=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+(?=[\s>]))/.source, 'i'),
      lookbehind: true,
      inside: {
        'attr-name': /^[^\s=]+/,
        'attr-value': {
          pattern: /=[\s\S]+/,
          inside: {
            'value': {
              pattern: /(^=\s*(["']|(?!["'])))\S[\s\S]*(?=\2$)/,
              lookbehind: true,
              alias: [lang, 'language-' + lang],
              inside: Prism.languages[lang]
            },
            'punctuation': [{
              pattern: /^=/,
              alias: 'attr-equals'
            }, /"|'/]
          }
        }
      }
    });
  }
});
Prism.languages.html = Prism.languages.markup;
Prism.languages.mathml = Prism.languages.markup;
Prism.languages.svg = Prism.languages.markup;
Prism.languages.xml = Prism.languages.extend('markup', {});
Prism.languages.ssml = Prism.languages.xml;
Prism.languages.atom = Prism.languages.xml;
Prism.languages.rss = Prism.languages.xml;

/* **********************************************
     Begin prism-css.js
********************************************** */

(function (Prism) {
  var string = /(?:"(?:\\(?:\r\n|[\s\S])|[^"\\\r\n])*"|'(?:\\(?:\r\n|[\s\S])|[^'\\\r\n])*')/;
  Prism.languages.css = {
    'comment': /\/\*[\s\S]*?\*\//,
    'atrule': {
      pattern: RegExp('@[\\w-](?:' + /[^;{\s"']|\s+(?!\s)/.source + '|' + string.source + ')*?' + /(?:;|(?=\s*\{))/.source),
      inside: {
        'rule': /^@[\w-]+/,
        'selector-function-argument': {
          pattern: /(\bselector\s*\(\s*(?![\s)]))(?:[^()\s]|\s+(?![\s)])|\((?:[^()]|\([^()]*\))*\))+(?=\s*\))/,
          lookbehind: true,
          alias: 'selector'
        },
        'keyword': {
          pattern: /(^|[^\w-])(?:and|not|only|or)(?![\w-])/,
          lookbehind: true
        }
        // See rest below
      }
    },
    'url': {
      // https://drafts.csswg.org/css-values-3/#urls
      pattern: RegExp('\\burl\\((?:' + string.source + '|' + /(?:[^\\\r\n()"']|\\[\s\S])*/.source + ')\\)', 'i'),
      greedy: true,
      inside: {
        'function': /^url/i,
        'punctuation': /^\(|\)$/,
        'string': {
          pattern: RegExp('^' + string.source + '$'),
          alias: 'url'
        }
      }
    },
    'selector': {
      pattern: RegExp('(^|[{}\\s])[^{}\\s](?:[^{};"\'\\s]|\\s+(?![\\s{])|' + string.source + ')*(?=\\s*\\{)'),
      lookbehind: true
    },
    'string': {
      pattern: string,
      greedy: true
    },
    'property': {
      pattern: /(^|[^-\w\xA0-\uFFFF])(?!\s)[-_a-z\xA0-\uFFFF](?:(?!\s)[-\w\xA0-\uFFFF])*(?=\s*:)/i,
      lookbehind: true
    },
    'important': /!important\b/i,
    'function': {
      pattern: /(^|[^-a-z0-9])[-a-z0-9]+(?=\()/i,
      lookbehind: true
    },
    'punctuation': /[(){};:,]/
  };
  Prism.languages.css['atrule'].inside.rest = Prism.languages.css;
  var markup = Prism.languages.markup;
  if (markup) {
    markup.tag.addInlined('style', 'css');
    markup.tag.addAttribute('style', 'css');
  }
})(Prism);

/* **********************************************
     Begin prism-clike.js
********************************************** */

Prism.languages.clike = {
  'comment': [{
    pattern: /(^|[^\\])\/\*[\s\S]*?(?:\*\/|$)/,
    lookbehind: true,
    greedy: true
  }, {
    pattern: /(^|[^\\:])\/\/.*/,
    lookbehind: true,
    greedy: true
  }],
  'string': {
    pattern: /(["'])(?:\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
    greedy: true
  },
  'class-name': {
    pattern: /(\b(?:class|extends|implements|instanceof|interface|new|trait)\s+|\bcatch\s+\()[\w.\\]+/i,
    lookbehind: true,
    inside: {
      'punctuation': /[.\\]/
    }
  },
  'keyword': /\b(?:break|catch|continue|do|else|finally|for|function|if|in|instanceof|new|null|return|throw|try|while)\b/,
  'boolean': /\b(?:false|true)\b/,
  'function': /\b\w+(?=\()/,
  'number': /\b0x[\da-f]+\b|(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:e[+-]?\d+)?/i,
  'operator': /[<>]=?|[!=]=?=?|--?|\+\+?|&&?|\|\|?|[?*/~^%]/,
  'punctuation': /[{}[\];(),.:]/
};

/* **********************************************
     Begin prism-javascript.js
********************************************** */

Prism.languages.javascript = Prism.languages.extend('clike', {
  'class-name': [Prism.languages.clike['class-name'], {
    pattern: /(^|[^$\w\xA0-\uFFFF])(?!\s)[_$A-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\.(?:constructor|prototype))/,
    lookbehind: true
  }],
  'keyword': [{
    pattern: /((?:^|\})\s*)catch\b/,
    lookbehind: true
  }, {
    pattern: /(^|[^.]|\.\.\.\s*)\b(?:as|assert(?=\s*\{)|async(?=\s*(?:function\b|\(|[$\w\xA0-\uFFFF]|$))|await|break|case|class|const|continue|debugger|default|delete|do|else|enum|export|extends|finally(?=\s*(?:\{|$))|for|from(?=\s*(?:['"]|$))|function|(?:get|set)(?=\s*(?:[#\[$\w\xA0-\uFFFF]|$))|if|implements|import|in|instanceof|interface|let|new|null|of|package|private|protected|public|return|static|super|switch|this|throw|try|typeof|undefined|var|void|while|with|yield)\b/,
    lookbehind: true
  }],
  // Allow for all non-ASCII characters (See http://stackoverflow.com/a/2008444)
  'function': /#?(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*(?:\.\s*(?:apply|bind|call)\s*)?\()/,
  'number': {
    pattern: RegExp(/(^|[^\w$])/.source + '(?:' + (
    // constant
    /NaN|Infinity/.source + '|' +
    // binary integer
    /0[bB][01]+(?:_[01]+)*n?/.source + '|' +
    // octal integer
    /0[oO][0-7]+(?:_[0-7]+)*n?/.source + '|' +
    // hexadecimal integer
    /0[xX][\dA-Fa-f]+(?:_[\dA-Fa-f]+)*n?/.source + '|' +
    // decimal bigint
    /\d+(?:_\d+)*n/.source + '|' +
    // decimal number (integer or float) but no bigint
    /(?:\d+(?:_\d+)*(?:\.(?:\d+(?:_\d+)*)?)?|\.\d+(?:_\d+)*)(?:[Ee][+-]?\d+(?:_\d+)*)?/.source) + ')' + /(?![\w$])/.source),
    lookbehind: true
  },
  'operator': /--|\+\+|\*\*=?|=>|&&=?|\|\|=?|[!=]==|<<=?|>>>?=?|[-+*/%&|^!=<>]=?|\.{3}|\?\?=?|\?\.?|[~:]/
});
Prism.languages.javascript['class-name'][0].pattern = /(\b(?:class|extends|implements|instanceof|interface|new)\s+)[\w.\\]+/;
Prism.languages.insertBefore('javascript', 'keyword', {
  'regex': {
    pattern: RegExp(
    // lookbehind
    // eslint-disable-next-line regexp/no-dupe-characters-character-class
    /((?:^|[^$\w\xA0-\uFFFF."'\])\s]|\b(?:return|yield))\s*)/.source +
    // Regex pattern:
    // There are 2 regex patterns here. The RegExp set notation proposal added support for nested character
    // classes if the `v` flag is present. Unfortunately, nested CCs are both context-free and incompatible
    // with the only syntax, so we have to define 2 different regex patterns.
    /\//.source + '(?:' + /(?:\[(?:[^\]\\\r\n]|\\.)*\]|\\.|[^/\\\[\r\n])+\/[dgimyus]{0,7}/.source + '|' +
    // `v` flag syntax. This supports 3 levels of nested character classes.
    /(?:\[(?:[^[\]\\\r\n]|\\.|\[(?:[^[\]\\\r\n]|\\.|\[(?:[^[\]\\\r\n]|\\.)*\])*\])*\]|\\.|[^/\\\[\r\n])+\/[dgimyus]{0,7}v[dgimyus]{0,7}/.source + ')' +
    // lookahead
    /(?=(?:\s|\/\*(?:[^*]|\*(?!\/))*\*\/)*(?:$|[\r\n,.;:})\]]|\/\/))/.source),
    lookbehind: true,
    greedy: true,
    inside: {
      'regex-source': {
        pattern: /^(\/)[\s\S]+(?=\/[a-z]*$)/,
        lookbehind: true,
        alias: 'language-regex',
        inside: Prism.languages.regex
      },
      'regex-delimiter': /^\/|\/$/,
      'regex-flags': /^[a-z]+$/
    }
  },
  // This must be declared before keyword because we use "function" inside the look-forward
  'function-variable': {
    pattern: /#?(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*[=:]\s*(?:async\s*)?(?:\bfunction\b|(?:\((?:[^()]|\([^()]*\))*\)|(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*)\s*=>))/,
    alias: 'function'
  },
  'parameter': [{
    pattern: /(function(?:\s+(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*)?\s*\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\))/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /(^|[^$\w\xA0-\uFFFF])(?!\s)[_$a-z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*=>)/i,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /(\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\)\s*=>)/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /((?:\b|\s|^)(?!(?:as|async|await|break|case|catch|class|const|continue|debugger|default|delete|do|else|enum|export|extends|finally|for|from|function|get|if|implements|import|in|instanceof|interface|let|new|null|of|package|private|protected|public|return|set|static|super|switch|this|throw|try|typeof|undefined|var|void|while|with|yield)(?![$\w\xA0-\uFFFF]))(?:(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*\s*)\(\s*|\]\s*\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\)\s*\{)/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }],
  'constant': /\b[A-Z](?:[A-Z_]|\dx?)*\b/
});
Prism.languages.insertBefore('javascript', 'string', {
  'hashbang': {
    pattern: /^#!.*/,
    greedy: true,
    alias: 'comment'
  },
  'template-string': {
    pattern: /`(?:\\[\s\S]|\$\{(?:[^{}]|\{(?:[^{}]|\{[^}]*\})*\})+\}|(?!\$\{)[^\\`])*`/,
    greedy: true,
    inside: {
      'template-punctuation': {
        pattern: /^`|`$/,
        alias: 'string'
      },
      'interpolation': {
        pattern: /((?:^|[^\\])(?:\\{2})*)\$\{(?:[^{}]|\{(?:[^{}]|\{[^}]*\})*\})+\}/,
        lookbehind: true,
        inside: {
          'interpolation-punctuation': {
            pattern: /^\$\{|\}$/,
            alias: 'punctuation'
          },
          rest: Prism.languages.javascript
        }
      },
      'string': /[\s\S]+/
    }
  },
  'string-property': {
    pattern: /((?:^|[,{])[ \t]*)(["'])(?:\\(?:\r\n|[\s\S])|(?!\2)[^\\\r\n])*\2(?=\s*:)/m,
    lookbehind: true,
    greedy: true,
    alias: 'property'
  }
});
Prism.languages.insertBefore('javascript', 'operator', {
  'literal-property': {
    pattern: /((?:^|[,{])[ \t]*)(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*:)/m,
    lookbehind: true,
    alias: 'property'
  }
});
if (Prism.languages.markup) {
  Prism.languages.markup.tag.addInlined('script', 'javascript');

  // add attribute support for all DOM events.
  // https://developer.mozilla.org/en-US/docs/Web/Events#Standard_events
  Prism.languages.markup.tag.addAttribute(/on(?:abort|blur|change|click|composition(?:end|start|update)|dblclick|error|focus(?:in|out)?|key(?:down|up)|load|mouse(?:down|enter|leave|move|out|over|up)|reset|resize|scroll|select|slotchange|submit|unload|wheel)/.source, 'javascript');
}
Prism.languages.js = Prism.languages.javascript;

/* **********************************************
     Begin prism-file-highlight.js
********************************************** */

(function () {
  if (typeof Prism === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/Element/matches#Polyfill
  if (!Element.prototype.matches) {
    Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
  }
  var LOADING_MESSAGE = 'Loading…';
  var FAILURE_MESSAGE = function (status, message) {
    return '✖ Error ' + status + ' while fetching file: ' + message;
  };
  var FAILURE_EMPTY_MESSAGE = '✖ Error: File does not exist or is empty';
  var EXTENSIONS = {
    'js': 'javascript',
    'py': 'python',
    'rb': 'ruby',
    'ps1': 'powershell',
    'psm1': 'powershell',
    'sh': 'bash',
    'bat': 'batch',
    'h': 'c',
    'tex': 'latex'
  };
  var STATUS_ATTR = 'data-src-status';
  var STATUS_LOADING = 'loading';
  var STATUS_LOADED = 'loaded';
  var STATUS_FAILED = 'failed';
  var SELECTOR = 'pre[data-src]:not([' + STATUS_ATTR + '="' + STATUS_LOADED + '"])' + ':not([' + STATUS_ATTR + '="' + STATUS_LOADING + '"])';

  /**
   * Loads the given file.
   *
   * @param {string} src The URL or path of the source file to load.
   * @param {(result: string) => void} success
   * @param {(reason: string) => void} error
   */
  function loadFile(src, success, error) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', src, true);
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4) {
        if (xhr.status < 400 && xhr.responseText) {
          success(xhr.responseText);
        } else {
          if (xhr.status >= 400) {
            error(FAILURE_MESSAGE(xhr.status, xhr.statusText));
          } else {
            error(FAILURE_EMPTY_MESSAGE);
          }
        }
      }
    };
    xhr.send(null);
  }

  /**
   * Parses the given range.
   *
   * This returns a range with inclusive ends.
   *
   * @param {string | null | undefined} range
   * @returns {[number, number | undefined] | undefined}
   */
  function parseRange(range) {
    var m = /^\s*(\d+)\s*(?:(,)\s*(?:(\d+)\s*)?)?$/.exec(range || '');
    if (m) {
      var start = Number(m[1]);
      var comma = m[2];
      var end = m[3];
      if (!comma) {
        return [start, start];
      }
      if (!end) {
        return [start, undefined];
      }
      return [start, Number(end)];
    }
    return undefined;
  }
  Prism.hooks.add('before-highlightall', function (env) {
    env.selector += ', ' + SELECTOR;
  });
  Prism.hooks.add('before-sanity-check', function (env) {
    var pre = /** @type {HTMLPreElement} */env.element;
    if (pre.matches(SELECTOR)) {
      env.code = ''; // fast-path the whole thing and go to complete

      pre.setAttribute(STATUS_ATTR, STATUS_LOADING); // mark as loading

      // add code element with loading message
      var code = pre.appendChild(document.createElement('CODE'));
      code.textContent = LOADING_MESSAGE;
      var src = pre.getAttribute('data-src');
      var language = env.language;
      if (language === 'none') {
        // the language might be 'none' because there is no language set;
        // in this case, we want to use the extension as the language
        var extension = (/\.(\w+)$/.exec(src) || [, 'none'])[1];
        language = EXTENSIONS[extension] || extension;
      }

      // set language classes
      Prism.util.setLanguage(code, language);
      Prism.util.setLanguage(pre, language);

      // preload the language
      var autoloader = Prism.plugins.autoloader;
      if (autoloader) {
        autoloader.loadLanguages(language);
      }

      // load file
      loadFile(src, function (text) {
        // mark as loaded
        pre.setAttribute(STATUS_ATTR, STATUS_LOADED);

        // handle data-range
        var range = parseRange(pre.getAttribute('data-range'));
        if (range) {
          var lines = text.split(/\r\n?|\n/g);

          // the range is one-based and inclusive on both ends
          var start = range[0];
          var end = range[1] == null ? lines.length : range[1];
          if (start < 0) {
            start += lines.length;
          }
          start = Math.max(0, Math.min(start - 1, lines.length));
          if (end < 0) {
            end += lines.length;
          }
          end = Math.max(0, Math.min(end, lines.length));
          text = lines.slice(start, end).join('\n');

          // add data-start for line numbers
          if (!pre.hasAttribute('data-start')) {
            pre.setAttribute('data-start', String(start + 1));
          }
        }

        // highlight code
        code.textContent = text;
        Prism.highlightElement(code);
      }, function (error) {
        // mark as failed
        pre.setAttribute(STATUS_ATTR, STATUS_FAILED);
        code.textContent = error;
      });
    }
  });
  Prism.plugins.fileHighlight = {
    /**
     * Executes the File Highlight plugin for all matching `pre` elements under the given container.
     *
     * Note: Elements which are already loaded or currently loading will not be touched by this method.
     *
     * @param {ParentNode} [container=document]
     */
    highlight: function highlight(container) {
      var elements = (container || document).querySelectorAll(SELECTOR);
      for (var i = 0, element; element = elements[i++];) {
        Prism.highlightElement(element);
      }
    }
  };
  var logged = false;
  /** @deprecated Use `Prism.plugins.fileHighlight.highlight` instead. */
  Prism.fileHighlight = function () {
    if (!logged) {
      console.warn('Prism.fileHighlight is deprecated. Use `Prism.plugins.fileHighlight.highlight` instead.');
      logged = true;
    }
    Prism.plugins.fileHighlight.highlight.apply(this, arguments);
  };
})();
});

Prism.languages.clike = {
  'comment': [{
    pattern: /(^|[^\\])\/\*[\s\S]*?(?:\*\/|$)/,
    lookbehind: true,
    greedy: true
  }, {
    pattern: /(^|[^\\:])\/\/.*/,
    lookbehind: true,
    greedy: true
  }],
  'string': {
    pattern: /(["'])(?:\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
    greedy: true
  },
  'class-name': {
    pattern: /(\b(?:class|extends|implements|instanceof|interface|new|trait)\s+|\bcatch\s+\()[\w.\\]+/i,
    lookbehind: true,
    inside: {
      'punctuation': /[.\\]/
    }
  },
  'keyword': /\b(?:break|catch|continue|do|else|finally|for|function|if|in|instanceof|new|null|return|throw|try|while)\b/,
  'boolean': /\b(?:false|true)\b/,
  'function': /\b\w+(?=\()/,
  'number': /\b0x[\da-f]+\b|(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:e[+-]?\d+)?/i,
  'operator': /[<>]=?|[!=]=?=?|--?|\+\+?|&&?|\|\|?|[?*/~^%]/,
  'punctuation': /[{}[\];(),.:]/
};

Prism.languages.markup = {
  'comment': {
    pattern: /<!--(?:(?!<!--)[\s\S])*?-->/,
    greedy: true
  },
  'prolog': {
    pattern: /<\?[\s\S]+?\?>/,
    greedy: true
  },
  'doctype': {
    // https://www.w3.org/TR/xml/#NT-doctypedecl
    pattern: /<!DOCTYPE(?:[^>"'[\]]|"[^"]*"|'[^']*')+(?:\[(?:[^<"'\]]|"[^"]*"|'[^']*'|<(?!!--)|<!--(?:[^-]|-(?!->))*-->)*\]\s*)?>/i,
    greedy: true,
    inside: {
      'internal-subset': {
        pattern: /(^[^\[]*\[)[\s\S]+(?=\]>$)/,
        lookbehind: true,
        greedy: true,
        inside: null // see below
      },
      'string': {
        pattern: /"[^"]*"|'[^']*'/,
        greedy: true
      },
      'punctuation': /^<!|>$|[[\]]/,
      'doctype-tag': /^DOCTYPE/i,
      'name': /[^\s<>'"]+/
    }
  },
  'cdata': {
    pattern: /<!\[CDATA\[[\s\S]*?\]\]>/i,
    greedy: true
  },
  'tag': {
    pattern: /<\/?(?!\d)[^\s>\/=$<%]+(?:\s(?:\s*[^\s>\/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+(?=[\s>]))|(?=[\s/>])))+)?\s*\/?>/,
    greedy: true,
    inside: {
      'tag': {
        pattern: /^<\/?[^\s>\/]+/,
        inside: {
          'punctuation': /^<\/?/,
          'namespace': /^[^\s>\/:]+:/
        }
      },
      'special-attr': [],
      'attr-value': {
        pattern: /=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+)/,
        inside: {
          'punctuation': [{
            pattern: /^=/,
            alias: 'attr-equals'
          }, {
            pattern: /^(\s*)["']|["']$/,
            lookbehind: true
          }]
        }
      },
      'punctuation': /\/?>/,
      'attr-name': {
        pattern: /[^\s>\/]+/,
        inside: {
          'namespace': /^[^\s>\/:]+:/
        }
      }
    }
  },
  'entity': [{
    pattern: /&[\da-z]{1,8};/i,
    alias: 'named-entity'
  }, /&#x?[\da-f]{1,8};/i]
};
Prism.languages.markup['tag'].inside['attr-value'].inside['entity'] = Prism.languages.markup['entity'];
Prism.languages.markup['doctype'].inside['internal-subset'].inside = Prism.languages.markup;

// Plugin to make entity title show the real entity, idea by Roman Komarov
Prism.hooks.add('wrap', function (env) {
  if (env.type === 'entity') {
    env.attributes['title'] = env.content.replace(/&amp;/, '&');
  }
});
Object.defineProperty(Prism.languages.markup.tag, 'addInlined', {
  /**
   * Adds an inlined language to markup.
   *
   * An example of an inlined language is CSS with `<style>` tags.
   *
   * @param {string} tagName The name of the tag that contains the inlined language. This name will be treated as
   * case insensitive.
   * @param {string} lang The language key.
   * @example
   * addInlined('style', 'css');
   */
  value: function addInlined(tagName, lang) {
    var includedCdataInside = {};
    includedCdataInside['language-' + lang] = {
      pattern: /(^<!\[CDATA\[)[\s\S]+?(?=\]\]>$)/i,
      lookbehind: true,
      inside: Prism.languages[lang]
    };
    includedCdataInside['cdata'] = /^<!\[CDATA\[|\]\]>$/i;
    var inside = {
      'included-cdata': {
        pattern: /<!\[CDATA\[[\s\S]*?\]\]>/i,
        inside: includedCdataInside
      }
    };
    inside['language-' + lang] = {
      pattern: /[\s\S]+/,
      inside: Prism.languages[lang]
    };
    var def = {};
    def[tagName] = {
      pattern: RegExp(/(<__[^>]*>)(?:<!\[CDATA\[(?:[^\]]|\](?!\]>))*\]\]>|(?!<!\[CDATA\[)[\s\S])*?(?=<\/__>)/.source.replace(/__/g, function () {
        return tagName;
      }), 'i'),
      lookbehind: true,
      greedy: true,
      inside: inside
    };
    Prism.languages.insertBefore('markup', 'cdata', def);
  }
});
Object.defineProperty(Prism.languages.markup.tag, 'addAttribute', {
  /**
   * Adds an pattern to highlight languages embedded in HTML attributes.
   *
   * An example of an inlined language is CSS with `style` attributes.
   *
   * @param {string} attrName The name of the tag that contains the inlined language. This name will be treated as
   * case insensitive.
   * @param {string} lang The language key.
   * @example
   * addAttribute('style', 'css');
   */
  value: function (attrName, lang) {
    Prism.languages.markup.tag.inside['special-attr'].push({
      pattern: RegExp(/(^|["'\s])/.source + '(?:' + attrName + ')' + /\s*=\s*(?:"[^"]*"|'[^']*'|[^\s'">=]+(?=[\s>]))/.source, 'i'),
      lookbehind: true,
      inside: {
        'attr-name': /^[^\s=]+/,
        'attr-value': {
          pattern: /=[\s\S]+/,
          inside: {
            'value': {
              pattern: /(^=\s*(["']|(?!["'])))\S[\s\S]*(?=\2$)/,
              lookbehind: true,
              alias: [lang, 'language-' + lang],
              inside: Prism.languages[lang]
            },
            'punctuation': [{
              pattern: /^=/,
              alias: 'attr-equals'
            }, /"|'/]
          }
        }
      }
    });
  }
});
Prism.languages.html = Prism.languages.markup;
Prism.languages.mathml = Prism.languages.markup;
Prism.languages.svg = Prism.languages.markup;
Prism.languages.xml = Prism.languages.extend('markup', {});
Prism.languages.ssml = Prism.languages.xml;
Prism.languages.atom = Prism.languages.xml;
Prism.languages.rss = Prism.languages.xml;

Prism.languages.javascript = Prism.languages.extend('clike', {
  'class-name': [Prism.languages.clike['class-name'], {
    pattern: /(^|[^$\w\xA0-\uFFFF])(?!\s)[_$A-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\.(?:constructor|prototype))/,
    lookbehind: true
  }],
  'keyword': [{
    pattern: /((?:^|\})\s*)catch\b/,
    lookbehind: true
  }, {
    pattern: /(^|[^.]|\.\.\.\s*)\b(?:as|assert(?=\s*\{)|async(?=\s*(?:function\b|\(|[$\w\xA0-\uFFFF]|$))|await|break|case|class|const|continue|debugger|default|delete|do|else|enum|export|extends|finally(?=\s*(?:\{|$))|for|from(?=\s*(?:['"]|$))|function|(?:get|set)(?=\s*(?:[#\[$\w\xA0-\uFFFF]|$))|if|implements|import|in|instanceof|interface|let|new|null|of|package|private|protected|public|return|static|super|switch|this|throw|try|typeof|undefined|var|void|while|with|yield)\b/,
    lookbehind: true
  }],
  // Allow for all non-ASCII characters (See http://stackoverflow.com/a/2008444)
  'function': /#?(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*(?:\.\s*(?:apply|bind|call)\s*)?\()/,
  'number': {
    pattern: RegExp(/(^|[^\w$])/.source + '(?:' + (
    // constant
    /NaN|Infinity/.source + '|' +
    // binary integer
    /0[bB][01]+(?:_[01]+)*n?/.source + '|' +
    // octal integer
    /0[oO][0-7]+(?:_[0-7]+)*n?/.source + '|' +
    // hexadecimal integer
    /0[xX][\dA-Fa-f]+(?:_[\dA-Fa-f]+)*n?/.source + '|' +
    // decimal bigint
    /\d+(?:_\d+)*n/.source + '|' +
    // decimal number (integer or float) but no bigint
    /(?:\d+(?:_\d+)*(?:\.(?:\d+(?:_\d+)*)?)?|\.\d+(?:_\d+)*)(?:[Ee][+-]?\d+(?:_\d+)*)?/.source) + ')' + /(?![\w$])/.source),
    lookbehind: true
  },
  'operator': /--|\+\+|\*\*=?|=>|&&=?|\|\|=?|[!=]==|<<=?|>>>?=?|[-+*/%&|^!=<>]=?|\.{3}|\?\?=?|\?\.?|[~:]/
});
Prism.languages.javascript['class-name'][0].pattern = /(\b(?:class|extends|implements|instanceof|interface|new)\s+)[\w.\\]+/;
Prism.languages.insertBefore('javascript', 'keyword', {
  'regex': {
    pattern: RegExp(
    // lookbehind
    // eslint-disable-next-line regexp/no-dupe-characters-character-class
    /((?:^|[^$\w\xA0-\uFFFF."'\])\s]|\b(?:return|yield))\s*)/.source +
    // Regex pattern:
    // There are 2 regex patterns here. The RegExp set notation proposal added support for nested character
    // classes if the `v` flag is present. Unfortunately, nested CCs are both context-free and incompatible
    // with the only syntax, so we have to define 2 different regex patterns.
    /\//.source + '(?:' + /(?:\[(?:[^\]\\\r\n]|\\.)*\]|\\.|[^/\\\[\r\n])+\/[dgimyus]{0,7}/.source + '|' +
    // `v` flag syntax. This supports 3 levels of nested character classes.
    /(?:\[(?:[^[\]\\\r\n]|\\.|\[(?:[^[\]\\\r\n]|\\.|\[(?:[^[\]\\\r\n]|\\.)*\])*\])*\]|\\.|[^/\\\[\r\n])+\/[dgimyus]{0,7}v[dgimyus]{0,7}/.source + ')' +
    // lookahead
    /(?=(?:\s|\/\*(?:[^*]|\*(?!\/))*\*\/)*(?:$|[\r\n,.;:})\]]|\/\/))/.source),
    lookbehind: true,
    greedy: true,
    inside: {
      'regex-source': {
        pattern: /^(\/)[\s\S]+(?=\/[a-z]*$)/,
        lookbehind: true,
        alias: 'language-regex',
        inside: Prism.languages.regex
      },
      'regex-delimiter': /^\/|\/$/,
      'regex-flags': /^[a-z]+$/
    }
  },
  // This must be declared before keyword because we use "function" inside the look-forward
  'function-variable': {
    pattern: /#?(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*[=:]\s*(?:async\s*)?(?:\bfunction\b|(?:\((?:[^()]|\([^()]*\))*\)|(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*)\s*=>))/,
    alias: 'function'
  },
  'parameter': [{
    pattern: /(function(?:\s+(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*)?\s*\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\))/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /(^|[^$\w\xA0-\uFFFF])(?!\s)[_$a-z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*=>)/i,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /(\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\)\s*=>)/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }, {
    pattern: /((?:\b|\s|^)(?!(?:as|async|await|break|case|catch|class|const|continue|debugger|default|delete|do|else|enum|export|extends|finally|for|from|function|get|if|implements|import|in|instanceof|interface|let|new|null|of|package|private|protected|public|return|set|static|super|switch|this|throw|try|typeof|undefined|var|void|while|with|yield)(?![$\w\xA0-\uFFFF]))(?:(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*\s*)\(\s*|\]\s*\(\s*)(?!\s)(?:[^()\s]|\s+(?![\s)])|\([^()]*\))+(?=\s*\)\s*\{)/,
    lookbehind: true,
    inside: Prism.languages.javascript
  }],
  'constant': /\b[A-Z](?:[A-Z_]|\dx?)*\b/
});
Prism.languages.insertBefore('javascript', 'string', {
  'hashbang': {
    pattern: /^#!.*/,
    greedy: true,
    alias: 'comment'
  },
  'template-string': {
    pattern: /`(?:\\[\s\S]|\$\{(?:[^{}]|\{(?:[^{}]|\{[^}]*\})*\})+\}|(?!\$\{)[^\\`])*`/,
    greedy: true,
    inside: {
      'template-punctuation': {
        pattern: /^`|`$/,
        alias: 'string'
      },
      'interpolation': {
        pattern: /((?:^|[^\\])(?:\\{2})*)\$\{(?:[^{}]|\{(?:[^{}]|\{[^}]*\})*\})+\}/,
        lookbehind: true,
        inside: {
          'interpolation-punctuation': {
            pattern: /^\$\{|\}$/,
            alias: 'punctuation'
          },
          rest: Prism.languages.javascript
        }
      },
      'string': /[\s\S]+/
    }
  },
  'string-property': {
    pattern: /((?:^|[,{])[ \t]*)(["'])(?:\\(?:\r\n|[\s\S])|(?!\2)[^\\\r\n])*\2(?=\s*:)/m,
    lookbehind: true,
    greedy: true,
    alias: 'property'
  }
});
Prism.languages.insertBefore('javascript', 'operator', {
  'literal-property': {
    pattern: /((?:^|[,{])[ \t]*)(?!\s)[_$a-zA-Z\xA0-\uFFFF](?:(?!\s)[$\w\xA0-\uFFFF])*(?=\s*:)/m,
    lookbehind: true,
    alias: 'property'
  }
});
if (Prism.languages.markup) {
  Prism.languages.markup.tag.addInlined('script', 'javascript');

  // add attribute support for all DOM events.
  // https://developer.mozilla.org/en-US/docs/Web/Events#Standard_events
  Prism.languages.markup.tag.addAttribute(/on(?:abort|blur|change|click|composition(?:end|start|update)|dblclick|error|focus(?:in|out)?|key(?:down|up)|load|mouse(?:down|enter|leave|move|out|over|up)|reset|resize|scroll|select|slotchange|submit|unload|wheel)/.source, 'javascript');
}
Prism.languages.js = Prism.languages.javascript;

(function (Prism) {
  var string = /(?:"(?:\\(?:\r\n|[\s\S])|[^"\\\r\n])*"|'(?:\\(?:\r\n|[\s\S])|[^'\\\r\n])*')/;
  Prism.languages.css = {
    'comment': /\/\*[\s\S]*?\*\//,
    'atrule': {
      pattern: RegExp('@[\\w-](?:' + /[^;{\s"']|\s+(?!\s)/.source + '|' + string.source + ')*?' + /(?:;|(?=\s*\{))/.source),
      inside: {
        'rule': /^@[\w-]+/,
        'selector-function-argument': {
          pattern: /(\bselector\s*\(\s*(?![\s)]))(?:[^()\s]|\s+(?![\s)])|\((?:[^()]|\([^()]*\))*\))+(?=\s*\))/,
          lookbehind: true,
          alias: 'selector'
        },
        'keyword': {
          pattern: /(^|[^\w-])(?:and|not|only|or)(?![\w-])/,
          lookbehind: true
        }
        // See rest below
      }
    },
    'url': {
      // https://drafts.csswg.org/css-values-3/#urls
      pattern: RegExp('\\burl\\((?:' + string.source + '|' + /(?:[^\\\r\n()"']|\\[\s\S])*/.source + ')\\)', 'i'),
      greedy: true,
      inside: {
        'function': /^url/i,
        'punctuation': /^\(|\)$/,
        'string': {
          pattern: RegExp('^' + string.source + '$'),
          alias: 'url'
        }
      }
    },
    'selector': {
      pattern: RegExp('(^|[{}\\s])[^{}\\s](?:[^{};"\'\\s]|\\s+(?![\\s{])|' + string.source + ')*(?=\\s*\\{)'),
      lookbehind: true
    },
    'string': {
      pattern: string,
      greedy: true
    },
    'property': {
      pattern: /(^|[^-\w\xA0-\uFFFF])(?!\s)[-_a-z\xA0-\uFFFF](?:(?!\s)[-\w\xA0-\uFFFF])*(?=\s*:)/i,
      lookbehind: true
    },
    'important': /!important\b/i,
    'function': {
      pattern: /(^|[^-a-z0-9])[-a-z0-9]+(?=\()/i,
      lookbehind: true
    },
    'punctuation': /[(){};:,]/
  };
  Prism.languages.css['atrule'].inside.rest = Prism.languages.css;
  var markup = Prism.languages.markup;
  if (markup) {
    markup.tag.addInlined('style', 'css');
    markup.tag.addAttribute('style', 'css');
  }
})(Prism);

(function (Prism) {
  // $ set | grep '^[A-Z][^[:space:]]*=' | cut -d= -f1 | tr '\n' '|'
  // + LC_ALL, RANDOM, REPLY, SECONDS.
  // + make sure PS1..4 are here as they are not always set,
  // - some useless things.
  var envVars = '\\b(?:BASH|BASHOPTS|BASH_ALIASES|BASH_ARGC|BASH_ARGV|BASH_CMDS|BASH_COMPLETION_COMPAT_DIR|BASH_LINENO|BASH_REMATCH|BASH_SOURCE|BASH_VERSINFO|BASH_VERSION|COLORTERM|COLUMNS|COMP_WORDBREAKS|DBUS_SESSION_BUS_ADDRESS|DEFAULTS_PATH|DESKTOP_SESSION|DIRSTACK|DISPLAY|EUID|GDMSESSION|GDM_LANG|GNOME_KEYRING_CONTROL|GNOME_KEYRING_PID|GPG_AGENT_INFO|GROUPS|HISTCONTROL|HISTFILE|HISTFILESIZE|HISTSIZE|HOME|HOSTNAME|HOSTTYPE|IFS|INSTANCE|JOB|LANG|LANGUAGE|LC_ADDRESS|LC_ALL|LC_IDENTIFICATION|LC_MEASUREMENT|LC_MONETARY|LC_NAME|LC_NUMERIC|LC_PAPER|LC_TELEPHONE|LC_TIME|LESSCLOSE|LESSOPEN|LINES|LOGNAME|LS_COLORS|MACHTYPE|MAILCHECK|MANDATORY_PATH|NO_AT_BRIDGE|OLDPWD|OPTERR|OPTIND|ORBIT_SOCKETDIR|OSTYPE|PAPERSIZE|PATH|PIPESTATUS|PPID|PS1|PS2|PS3|PS4|PWD|RANDOM|REPLY|SECONDS|SELINUX_INIT|SESSION|SESSIONTYPE|SESSION_MANAGER|SHELL|SHELLOPTS|SHLVL|SSH_AUTH_SOCK|TERM|UID|UPSTART_EVENTS|UPSTART_INSTANCE|UPSTART_JOB|UPSTART_SESSION|USER|WINDOWID|XAUTHORITY|XDG_CONFIG_DIRS|XDG_CURRENT_DESKTOP|XDG_DATA_DIRS|XDG_GREETER_DATA_DIR|XDG_MENU_PREFIX|XDG_RUNTIME_DIR|XDG_SEAT|XDG_SEAT_PATH|XDG_SESSION_DESKTOP|XDG_SESSION_ID|XDG_SESSION_PATH|XDG_SESSION_TYPE|XDG_VTNR|XMODIFIERS)\\b';
  var commandAfterHeredoc = {
    pattern: /(^(["']?)\w+\2)[ \t]+\S.*/,
    lookbehind: true,
    alias: 'punctuation',
    // this looks reasonably well in all themes
    inside: null // see below
  };
  var insideString = {
    'bash': commandAfterHeredoc,
    'environment': {
      pattern: RegExp('\\$' + envVars),
      alias: 'constant'
    },
    'variable': [
    // [0]: Arithmetic Environment
    {
      pattern: /\$?\(\([\s\S]+?\)\)/,
      greedy: true,
      inside: {
        // If there is a $ sign at the beginning highlight $(( and )) as variable
        'variable': [{
          pattern: /(^\$\(\([\s\S]+)\)\)/,
          lookbehind: true
        }, /^\$\(\(/],
        'number': /\b0x[\dA-Fa-f]+\b|(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:[Ee]-?\d+)?/,
        // Operators according to https://www.gnu.org/software/bash/manual/bashref.html#Shell-Arithmetic
        'operator': /--|\+\+|\*\*=?|<<=?|>>=?|&&|\|\||[=!+\-*/%<>^&|]=?|[?~:]/,
        // If there is no $ sign at the beginning highlight (( and )) as punctuation
        'punctuation': /\(\(?|\)\)?|,|;/
      }
    },
    // [1]: Command Substitution
    {
      pattern: /\$\((?:\([^)]+\)|[^()])+\)|`[^`]+`/,
      greedy: true,
      inside: {
        'variable': /^\$\(|^`|\)$|`$/
      }
    },
    // [2]: Brace expansion
    {
      pattern: /\$\{[^}]+\}/,
      greedy: true,
      inside: {
        'operator': /:[-=?+]?|[!\/]|##?|%%?|\^\^?|,,?/,
        'punctuation': /[\[\]]/,
        'environment': {
          pattern: RegExp('(\\{)' + envVars),
          lookbehind: true,
          alias: 'constant'
        }
      }
    }, /\$(?:\w+|[#?*!@$])/],
    // Escape sequences from echo and printf's manuals, and escaped quotes.
    'entity': /\\(?:[abceEfnrtv\\"]|O?[0-7]{1,3}|U[0-9a-fA-F]{8}|u[0-9a-fA-F]{4}|x[0-9a-fA-F]{1,2})/
  };
  Prism.languages.bash = {
    'shebang': {
      pattern: /^#!\s*\/.*/,
      alias: 'important'
    },
    'comment': {
      pattern: /(^|[^"{\\$])#.*/,
      lookbehind: true
    },
    'function-name': [
    // a) function foo {
    // b) foo() {
    // c) function foo() {
    // but not “foo {”
    {
      // a) and c)
      pattern: /(\bfunction\s+)[\w-]+(?=(?:\s*\(?:\s*\))?\s*\{)/,
      lookbehind: true,
      alias: 'function'
    }, {
      // b)
      pattern: /\b[\w-]+(?=\s*\(\s*\)\s*\{)/,
      alias: 'function'
    }],
    // Highlight variable names as variables in for and select beginnings.
    'for-or-select': {
      pattern: /(\b(?:for|select)\s+)\w+(?=\s+in\s)/,
      alias: 'variable',
      lookbehind: true
    },
    // Highlight variable names as variables in the left-hand part
    // of assignments (“=” and “+=”).
    'assign-left': {
      pattern: /(^|[\s;|&]|[<>]\()\w+(?:\.\w+)*(?=\+?=)/,
      inside: {
        'environment': {
          pattern: RegExp('(^|[\\s;|&]|[<>]\\()' + envVars),
          lookbehind: true,
          alias: 'constant'
        }
      },
      alias: 'variable',
      lookbehind: true
    },
    // Highlight parameter names as variables
    'parameter': {
      pattern: /(^|\s)-{1,2}(?:\w+:[+-]?)?\w+(?:\.\w+)*(?=[=\s]|$)/,
      alias: 'variable',
      lookbehind: true
    },
    'string': [
    // Support for Here-documents https://en.wikipedia.org/wiki/Here_document
    {
      pattern: /((?:^|[^<])<<-?\s*)(\w+)\s[\s\S]*?(?:\r?\n|\r)\2/,
      lookbehind: true,
      greedy: true,
      inside: insideString
    },
    // Here-document with quotes around the tag
    // → No expansion (so no “inside”).
    {
      pattern: /((?:^|[^<])<<-?\s*)(["'])(\w+)\2\s[\s\S]*?(?:\r?\n|\r)\3/,
      lookbehind: true,
      greedy: true,
      inside: {
        'bash': commandAfterHeredoc
      }
    },
    // “Normal” string
    {
      // https://www.gnu.org/software/bash/manual/html_node/Double-Quotes.html
      pattern: /(^|[^\\](?:\\\\)*)"(?:\\[\s\S]|\$\([^)]+\)|\$(?!\()|`[^`]+`|[^"\\`$])*"/,
      lookbehind: true,
      greedy: true,
      inside: insideString
    }, {
      // https://www.gnu.org/software/bash/manual/html_node/Single-Quotes.html
      pattern: /(^|[^$\\])'[^']*'/,
      lookbehind: true,
      greedy: true
    }, {
      // https://www.gnu.org/software/bash/manual/html_node/ANSI_002dC-Quoting.html
      pattern: /\$'(?:[^'\\]|\\[\s\S])*'/,
      greedy: true,
      inside: {
        'entity': insideString.entity
      }
    }],
    'environment': {
      pattern: RegExp('\\$?' + envVars),
      alias: 'constant'
    },
    'variable': insideString.variable,
    'function': {
      pattern: /(^|[\s;|&]|[<>]\()(?:add|apropos|apt|apt-cache|apt-get|aptitude|aspell|automysqlbackup|awk|basename|bash|bc|bconsole|bg|bzip2|cal|cargo|cat|cfdisk|chgrp|chkconfig|chmod|chown|chroot|cksum|clear|cmp|column|comm|composer|cp|cron|crontab|csplit|curl|cut|date|dc|dd|ddrescue|debootstrap|df|diff|diff3|dig|dir|dircolors|dirname|dirs|dmesg|docker|docker-compose|du|egrep|eject|env|ethtool|expand|expect|expr|fdformat|fdisk|fg|fgrep|file|find|fmt|fold|format|free|fsck|ftp|fuser|gawk|git|gparted|grep|groupadd|groupdel|groupmod|groups|grub-mkconfig|gzip|halt|head|hg|history|host|hostname|htop|iconv|id|ifconfig|ifdown|ifup|import|install|ip|java|jobs|join|kill|killall|less|link|ln|locate|logname|logrotate|look|lpc|lpr|lprint|lprintd|lprintq|lprm|ls|lsof|lynx|make|man|mc|mdadm|mkconfig|mkdir|mke2fs|mkfifo|mkfs|mkisofs|mknod|mkswap|mmv|more|most|mount|mtools|mtr|mutt|mv|nano|nc|netstat|nice|nl|node|nohup|notify-send|npm|nslookup|op|open|parted|passwd|paste|pathchk|ping|pkill|pnpm|podman|podman-compose|popd|pr|printcap|printenv|ps|pushd|pv|quota|quotacheck|quotactl|ram|rar|rcp|reboot|remsync|rename|renice|rev|rm|rmdir|rpm|rsync|scp|screen|sdiff|sed|sendmail|seq|service|sftp|sh|shellcheck|shuf|shutdown|sleep|slocate|sort|split|ssh|stat|strace|su|sudo|sum|suspend|swapon|sync|sysctl|tac|tail|tar|tee|time|timeout|top|touch|tr|traceroute|tsort|tty|umount|uname|unexpand|uniq|units|unrar|unshar|unzip|update-grub|uptime|useradd|userdel|usermod|users|uudecode|uuencode|v|vcpkg|vdir|vi|vim|virsh|vmstat|wait|watch|wc|wget|whereis|which|who|whoami|write|xargs|xdg-open|yarn|yes|zenity|zip|zsh|zypper)(?=$|[)\s;|&])/,
      lookbehind: true
    },
    'keyword': {
      pattern: /(^|[\s;|&]|[<>]\()(?:case|do|done|elif|else|esac|fi|for|function|if|in|select|then|until|while)(?=$|[)\s;|&])/,
      lookbehind: true
    },
    // https://www.gnu.org/software/bash/manual/html_node/Shell-Builtin-Commands.html
    'builtin': {
      pattern: /(^|[\s;|&]|[<>]\()(?:\.|:|alias|bind|break|builtin|caller|cd|command|continue|declare|echo|enable|eval|exec|exit|export|getopts|hash|help|let|local|logout|mapfile|printf|pwd|read|readarray|readonly|return|set|shift|shopt|source|test|times|trap|type|typeset|ulimit|umask|unalias|unset)(?=$|[)\s;|&])/,
      lookbehind: true,
      // Alias added to make those easier to distinguish from strings.
      alias: 'class-name'
    },
    'boolean': {
      pattern: /(^|[\s;|&]|[<>]\()(?:false|true)(?=$|[)\s;|&])/,
      lookbehind: true
    },
    'file-descriptor': {
      pattern: /\B&\d\b/,
      alias: 'important'
    },
    'operator': {
      // Lots of redirections here, but not just that.
      pattern: /\d?<>|>\||\+=|=[=~]?|!=?|<<[<-]?|[&\d]?>>|\d[<>]&?|[<>][&=]?|&[>&]?|\|[&|]?/,
      inside: {
        'file-descriptor': {
          pattern: /^\d/,
          alias: 'important'
        }
      }
    },
    'punctuation': /\$?\(\(?|\)\)?|\.\.|[{}[\];\\]/,
    'number': {
      pattern: /(^|\s)(?:[1-9]\d*|0)(?:[.,]\d+)?\b/,
      lookbehind: true
    }
  };
  commandAfterHeredoc.inside = Prism.languages.bash;

  /* Patterns in command substitution. */
  var toBeCopied = ['comment', 'function-name', 'for-or-select', 'assign-left', 'parameter', 'string', 'environment', 'function', 'keyword', 'builtin', 'boolean', 'file-descriptor', 'operator', 'punctuation', 'number'];
  var inside = insideString.variable[1].inside;
  for (var i = 0; i < toBeCopied.length; i++) {
    inside[toBeCopied[i]] = Prism.languages.bash[toBeCopied[i]];
  }
  Prism.languages.sh = Prism.languages.bash;
  Prism.languages.shell = Prism.languages.bash;
})(Prism);

(function (Prism) {
  var keywords = [/\b(?:async|sync|yield)\*/, /\b(?:abstract|assert|async|await|break|case|catch|class|const|continue|covariant|default|deferred|do|dynamic|else|enum|export|extends|extension|external|factory|final|finally|for|get|hide|if|implements|import|in|interface|library|mixin|new|null|on|operator|part|rethrow|return|set|show|static|super|switch|sync|this|throw|try|typedef|var|void|while|with|yield)\b/];

  // Handles named imports, such as http.Client
  var packagePrefix = /(^|[^\w.])(?:[a-z]\w*\s*\.\s*)*(?:[A-Z]\w*\s*\.\s*)*/.source;

  // based on the dart naming conventions
  var className = {
    pattern: RegExp(packagePrefix + /[A-Z](?:[\d_A-Z]*[a-z]\w*)?\b/.source),
    lookbehind: true,
    inside: {
      'namespace': {
        pattern: /^[a-z]\w*(?:\s*\.\s*[a-z]\w*)*(?:\s*\.)?/,
        inside: {
          'punctuation': /\./
        }
      }
    }
  };
  Prism.languages.dart = Prism.languages.extend('clike', {
    'class-name': [className, {
      // variables and parameters
      // this to support class names (or generic parameters) which do not contain a lower case letter (also works for methods)
      pattern: RegExp(packagePrefix + /[A-Z]\w*(?=\s+\w+\s*[;,=()])/.source),
      lookbehind: true,
      inside: className.inside
    }],
    'keyword': keywords,
    'operator': /\bis!|\b(?:as|is)\b|\+\+|--|&&|\|\||<<=?|>>=?|~(?:\/=?)?|[+\-*\/%&^|=!<>]=?|\?/
  });
  Prism.languages.insertBefore('dart', 'string', {
    'string-literal': {
      pattern: /r?(?:("""|''')[\s\S]*?\1|(["'])(?:\\.|(?!\2)[^\\\r\n])*\2(?!\2))/,
      greedy: true,
      inside: {
        'interpolation': {
          pattern: /((?:^|[^\\])(?:\\{2})*)\$(?:\w+|\{(?:[^{}]|\{[^{}]*\})*\})/,
          lookbehind: true,
          inside: {
            'punctuation': /^\$\{?|\}$/,
            'expression': {
              pattern: /[\s\S]+/,
              inside: Prism.languages.dart
            }
          }
        },
        'string': /[\s\S]+/
      }
    },
    'string': undefined
  });
  Prism.languages.insertBefore('dart', 'class-name', {
    'metadata': {
      pattern: /@\w+/,
      alias: 'function'
    }
  });
  Prism.languages.insertBefore('dart', 'class-name', {
    'generics': {
      pattern: /<(?:[\w\s,.&?]|<(?:[\w\s,.&?]|<(?:[\w\s,.&?]|<[\w\s,.&?]*>)*>)*>)*>/,
      inside: {
        'class-name': className,
        'keyword': keywords,
        'punctuation': /[<>(),.:]/,
        'operator': /[?&|]/
      }
    }
  });
})(Prism);

(function (Prism) {
  Prism.languages.diff = {
    'coord': [
    // Match all kinds of coord lines (prefixed by "+++", "---" or "***").
    /^(?:\*{3}|-{3}|\+{3}).*$/m,
    // Match "@@ ... @@" coord lines in unified diff.
    /^@@.*@@$/m,
    // Match coord lines in normal diff (starts with a number).
    /^\d.*$/m]

    // deleted, inserted, unchanged, diff
  };

  /**
   * A map from the name of a block to its line prefix.
   *
   * @type {Object<string, string>}
   */
  var PREFIXES = {
    'deleted-sign': '-',
    'deleted-arrow': '<',
    'inserted-sign': '+',
    'inserted-arrow': '>',
    'unchanged': ' ',
    'diff': '!'
  };

  // add a token for each prefix
  Object.keys(PREFIXES).forEach(function (name) {
    var prefix = PREFIXES[name];
    var alias = [];
    if (!/^\w+$/.test(name)) {
      // "deleted-sign" -> "deleted"
      alias.push(/\w+/.exec(name)[0]);
    }
    if (name === 'diff') {
      alias.push('bold');
    }
    Prism.languages.diff[name] = {
      pattern: RegExp('^(?:[' + prefix + '].*(?:\r\n?|\n|(?![\\s\\S])))+', 'm'),
      alias: alias,
      inside: {
        'line': {
          pattern: /(.)(?=[\s\S]).*(?:\r\n?|\n)?/,
          lookbehind: true
        },
        'prefix': {
          pattern: /[\s\S]/,
          alias: /\w+/.exec(name)[0]
        }
      }
    };
  });

  // make prefixes available to Diff plugin
  Object.defineProperty(Prism.languages.diff, 'PREFIXES', {
    value: PREFIXES
  });
})(Prism);

Prism.languages.erlang = {
  'comment': /%.+/,
  'string': {
    pattern: /"(?:\\.|[^\\"\r\n])*"/,
    greedy: true
  },
  'quoted-function': {
    pattern: /'(?:\\.|[^\\'\r\n])+'(?=\()/,
    alias: 'function'
  },
  'quoted-atom': {
    pattern: /'(?:\\.|[^\\'\r\n])+'/,
    alias: 'atom'
  },
  'boolean': /\b(?:false|true)\b/,
  'keyword': /\b(?:after|begin|case|catch|end|fun|if|of|receive|try|when)\b/,
  'number': [/\$\\?./, /\b\d+#[a-z0-9]+/i, /(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:e[+-]?\d+)?/i],
  'function': /\b[a-z][\w@]*(?=\()/,
  'variable': {
    // Look-behind is used to prevent wrong highlighting of atoms containing "@"
    pattern: /(^|[^@])(?:\b|\?)[A-Z_][\w@]*/,
    lookbehind: true
  },
  'operator': [/[=\/<>:]=|=[:\/]=|\+\+?|--?|[=*\/!]|\b(?:and|andalso|band|bnot|bor|bsl|bsr|bxor|div|not|or|orelse|rem|xor)\b/, {
    // We don't want to match <<
    pattern: /(^|[^<])<(?!<)/,
    lookbehind: true
  }, {
    // We don't want to match >>
    pattern: /(^|[^>])>(?!>)/,
    lookbehind: true
  }],
  'atom': /\b[a-z][\w@]*/,
  'punctuation': /[()[\]{}:;,.#|]|<<|>>/
};

Prism.languages.go = Prism.languages.extend('clike', {
  'string': {
    pattern: /(^|[^\\])"(?:\\.|[^"\\\r\n])*"|`[^`]*`/,
    lookbehind: true,
    greedy: true
  },
  'keyword': /\b(?:break|case|chan|const|continue|default|defer|else|fallthrough|for|func|go(?:to)?|if|import|interface|map|package|range|return|select|struct|switch|type|var)\b/,
  'boolean': /\b(?:_|false|iota|nil|true)\b/,
  'number': [
  // binary and octal integers
  /\b0(?:b[01_]+|o[0-7_]+)i?\b/i,
  // hexadecimal integers and floats
  /\b0x(?:[a-f\d_]+(?:\.[a-f\d_]*)?|\.[a-f\d_]+)(?:p[+-]?\d+(?:_\d+)*)?i?(?!\w)/i,
  // decimal integers and floats
  /(?:\b\d[\d_]*(?:\.[\d_]*)?|\B\.\d[\d_]*)(?:e[+-]?[\d_]+)?i?(?!\w)/i],
  'operator': /[*\/%^!=]=?|\+[=+]?|-[=-]?|\|[=|]?|&(?:=|&|\^=?)?|>(?:>=?|=)?|<(?:<=?|=|-)?|:=|\.\.\./,
  'builtin': /\b(?:append|bool|byte|cap|close|complex|complex(?:64|128)|copy|delete|error|float(?:32|64)|u?int(?:8|16|32|64)?|imag|len|make|new|panic|print(?:ln)?|real|recover|rune|string|uintptr)\b/
});
Prism.languages.insertBefore('go', 'string', {
  'char': {
    pattern: /'(?:\\.|[^'\\\r\n]){0,10}'/,
    greedy: true
  }
});
delete Prism.languages.go['class-name'];

(function (Prism) {
  var interpolation = {
    pattern: /((?:^|[^\\$])(?:\\{2})*)\$(?:\w+|\{[^{}]*\})/,
    lookbehind: true,
    inside: {
      'interpolation-punctuation': {
        pattern: /^\$\{?|\}$/,
        alias: 'punctuation'
      },
      'expression': {
        pattern: /[\s\S]+/,
        inside: null // see below
      }
    }
  };
  Prism.languages.groovy = Prism.languages.extend('clike', {
    'string': {
      // https://groovy-lang.org/syntax.html#_dollar_slashy_string
      pattern: /'''(?:[^\\]|\\[\s\S])*?'''|'(?:\\.|[^\\'\r\n])*'/,
      greedy: true
    },
    'keyword': /\b(?:abstract|as|assert|boolean|break|byte|case|catch|char|class|const|continue|def|default|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|in|instanceof|int|interface|long|native|new|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|trait|transient|try|void|volatile|while)\b/,
    'number': /\b(?:0b[01_]+|0x[\da-f_]+(?:\.[\da-f_p\-]+)?|[\d_]+(?:\.[\d_]+)?(?:e[+-]?\d+)?)[glidf]?\b/i,
    'operator': {
      pattern: /(^|[^.])(?:~|==?~?|\?[.:]?|\*(?:[.=]|\*=?)?|\.[@&]|\.\.<|\.\.(?!\.)|-[-=>]?|\+[+=]?|!=?|<(?:<=?|=>?)?|>(?:>>?=?|=)?|&[&=]?|\|[|=]?|\/=?|\^=?|%=?)/,
      lookbehind: true
    },
    'punctuation': /\.+|[{}[\];(),:$]/
  });
  Prism.languages.insertBefore('groovy', 'string', {
    'shebang': {
      pattern: /#!.+/,
      alias: 'comment',
      greedy: true
    },
    'interpolation-string': {
      // TODO: Slash strings (e.g. /foo/) can contain line breaks but this will cause a lot of trouble with
      // simple division (see JS regex), so find a fix maybe?
      pattern: /"""(?:[^\\]|\\[\s\S])*?"""|(["/])(?:\\.|(?!\1)[^\\\r\n])*\1|\$\/(?:[^/$]|\$(?:[/$]|(?![/$]))|\/(?!\$))*\/\$/,
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'string': /[\s\S]+/
      }
    }
  });
  Prism.languages.insertBefore('groovy', 'punctuation', {
    'spock-block': /\b(?:and|cleanup|expect|given|setup|then|when|where):/
  });
  Prism.languages.insertBefore('groovy', 'function', {
    'annotation': {
      pattern: /(^|[^.])@\w+/,
      lookbehind: true,
      alias: 'punctuation'
    }
  });
  interpolation.inside.expression.inside = Prism.languages.groovy;
})(Prism);

(function (Prism) {
  var keywords = /\b(?:abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|default|do|double|else|enum|exports|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|module|native|new|non-sealed|null|open|opens|package|permits|private|protected|provides|public|record(?!\s*[(){}[\]<>=%~.:,;?+\-*/&|^])|requires|return|sealed|short|static|strictfp|super|switch|synchronized|this|throw|throws|to|transient|transitive|try|uses|var|void|volatile|while|with|yield)\b/;

  // full package (optional) + parent classes (optional)
  var classNamePrefix = /(?:[a-z]\w*\s*\.\s*)*(?:[A-Z]\w*\s*\.\s*)*/.source;

  // based on the java naming conventions
  var className = {
    pattern: RegExp(/(^|[^\w.])/.source + classNamePrefix + /[A-Z](?:[\d_A-Z]*[a-z]\w*)?\b/.source),
    lookbehind: true,
    inside: {
      'namespace': {
        pattern: /^[a-z]\w*(?:\s*\.\s*[a-z]\w*)*(?:\s*\.)?/,
        inside: {
          'punctuation': /\./
        }
      },
      'punctuation': /\./
    }
  };
  Prism.languages.java = Prism.languages.extend('clike', {
    'string': {
      pattern: /(^|[^\\])"(?:\\.|[^"\\\r\n])*"/,
      lookbehind: true,
      greedy: true
    },
    'class-name': [className, {
      // variables, parameters, and constructor references
      // this to support class names (or generic parameters) which do not contain a lower case letter (also works for methods)
      pattern: RegExp(/(^|[^\w.])/.source + classNamePrefix + /[A-Z]\w*(?=\s+\w+\s*[;,=()]|\s*(?:\[[\s,]*\]\s*)?::\s*new\b)/.source),
      lookbehind: true,
      inside: className.inside
    }, {
      // class names based on keyword
      // this to support class names (or generic parameters) which do not contain a lower case letter (also works for methods)
      pattern: RegExp(/(\b(?:class|enum|extends|implements|instanceof|interface|new|record|throws)\s+)/.source + classNamePrefix + /[A-Z]\w*\b/.source),
      lookbehind: true,
      inside: className.inside
    }],
    'keyword': keywords,
    'function': [Prism.languages.clike.function, {
      pattern: /(::\s*)[a-z_]\w*/,
      lookbehind: true
    }],
    'number': /\b0b[01][01_]*L?\b|\b0x(?:\.[\da-f_p+-]+|[\da-f_]+(?:\.[\da-f_p+-]+)?)\b|(?:\b\d[\d_]*(?:\.[\d_]*)?|\B\.\d[\d_]*)(?:e[+-]?\d[\d_]*)?[dfl]?/i,
    'operator': {
      pattern: /(^|[^.])(?:<<=?|>>>?=?|->|--|\+\+|&&|\|\||::|[?:~]|[-+*/%&|^!=<>]=?)/m,
      lookbehind: true
    },
    'constant': /\b[A-Z][A-Z_\d]+\b/
  });
  Prism.languages.insertBefore('java', 'string', {
    'triple-quoted-string': {
      // http://openjdk.java.net/jeps/355#Description
      pattern: /"""[ \t]*[\r\n](?:(?:"|"")?(?:\\.|[^"\\]))*"""/,
      greedy: true,
      alias: 'string'
    },
    'char': {
      pattern: /'(?:\\.|[^'\\\r\n]){1,6}'/,
      greedy: true
    }
  });
  Prism.languages.insertBefore('java', 'class-name', {
    'annotation': {
      pattern: /(^|[^.])@\w+(?:\s*\.\s*\w+)*/,
      lookbehind: true,
      alias: 'punctuation'
    },
    'generics': {
      pattern: /<(?:[\w\s,.?]|&(?!&)|<(?:[\w\s,.?]|&(?!&)|<(?:[\w\s,.?]|&(?!&)|<(?:[\w\s,.?]|&(?!&))*>)*>)*>)*>/,
      inside: {
        'class-name': className,
        'keyword': keywords,
        'punctuation': /[<>(),.:]/,
        'operator': /[?&|]/
      }
    },
    'import': [{
      pattern: RegExp(/(\bimport\s+)/.source + classNamePrefix + /(?:[A-Z]\w*|\*)(?=\s*;)/.source),
      lookbehind: true,
      inside: {
        'namespace': className.inside.namespace,
        'punctuation': /\./,
        'operator': /\*/,
        'class-name': /\w+/
      }
    }, {
      pattern: RegExp(/(\bimport\s+static\s+)/.source + classNamePrefix + /(?:\w+|\*)(?=\s*;)/.source),
      lookbehind: true,
      alias: 'static',
      inside: {
        'namespace': className.inside.namespace,
        'static': /\b\w+$/,
        'punctuation': /\./,
        'operator': /\*/,
        'class-name': /\w+/
      }
    }],
    'namespace': {
      pattern: RegExp(/(\b(?:exports|import(?:\s+static)?|module|open|opens|package|provides|requires|to|transitive|uses|with)\s+)(?!<keyword>)[a-z]\w*(?:\.[a-z]\w*)*\.?/.source.replace(/<keyword>/g, function () {
        return keywords.source;
      })),
      lookbehind: true,
      inside: {
        'punctuation': /\./
      }
    }
  });
})(Prism);

// https://www.json.org/json-en.html
Prism.languages.json = {
  'property': {
    pattern: /(^|[^\\])"(?:\\.|[^\\"\r\n])*"(?=\s*:)/,
    lookbehind: true,
    greedy: true
  },
  'string': {
    pattern: /(^|[^\\])"(?:\\.|[^\\"\r\n])*"(?!\s*:)/,
    lookbehind: true,
    greedy: true
  },
  'comment': {
    pattern: /\/\/.*|\/\*[\s\S]*?(?:\*\/|$)/,
    greedy: true
  },
  'number': /-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/i,
  'punctuation': /[{}[\],]/,
  'operator': /:/,
  'boolean': /\b(?:false|true)\b/,
  'null': {
    pattern: /\bnull\b/,
    alias: 'keyword'
  }
};
Prism.languages.webmanifest = Prism.languages.json;

(function (Prism) {
  Prism.languages.kotlin = Prism.languages.extend('clike', {
    'keyword': {
      // The lookbehind prevents wrong highlighting of e.g. kotlin.properties.get
      pattern: /(^|[^.])\b(?:abstract|actual|annotation|as|break|by|catch|class|companion|const|constructor|continue|crossinline|data|do|dynamic|else|enum|expect|external|final|finally|for|fun|get|if|import|in|infix|init|inline|inner|interface|internal|is|lateinit|noinline|null|object|open|operator|out|override|package|private|protected|public|reified|return|sealed|set|super|suspend|tailrec|this|throw|to|try|typealias|val|var|vararg|when|where|while)\b/,
      lookbehind: true
    },
    'function': [{
      pattern: /(?:`[^\r\n`]+`|\b\w+)(?=\s*\()/,
      greedy: true
    }, {
      pattern: /(\.)(?:`[^\r\n`]+`|\w+)(?=\s*\{)/,
      lookbehind: true,
      greedy: true
    }],
    'number': /\b(?:0[xX][\da-fA-F]+(?:_[\da-fA-F]+)*|0[bB][01]+(?:_[01]+)*|\d+(?:_\d+)*(?:\.\d+(?:_\d+)*)?(?:[eE][+-]?\d+(?:_\d+)*)?[fFL]?)\b/,
    'operator': /\+[+=]?|-[-=>]?|==?=?|!(?:!|==?)?|[\/*%<>]=?|[?:]:?|\.\.|&&|\|\||\b(?:and|inv|or|shl|shr|ushr|xor)\b/
  });
  delete Prism.languages.kotlin['class-name'];
  var interpolationInside = {
    'interpolation-punctuation': {
      pattern: /^\$\{?|\}$/,
      alias: 'punctuation'
    },
    'expression': {
      pattern: /[\s\S]+/,
      inside: Prism.languages.kotlin
    }
  };
  Prism.languages.insertBefore('kotlin', 'string', {
    // https://kotlinlang.org/spec/expressions.html#string-interpolation-expressions
    'string-literal': [{
      pattern: /"""(?:[^$]|\$(?:(?!\{)|\{[^{}]*\}))*?"""/,
      alias: 'multiline',
      inside: {
        'interpolation': {
          pattern: /\$(?:[a-z_]\w*|\{[^{}]*\})/i,
          inside: interpolationInside
        },
        'string': /[\s\S]+/
      }
    }, {
      pattern: /"(?:[^"\\\r\n$]|\\.|\$(?:(?!\{)|\{[^{}]*\}))*"/,
      alias: 'singleline',
      inside: {
        'interpolation': {
          pattern: /((?:^|[^\\])(?:\\{2})*)\$(?:[a-z_]\w*|\{[^{}]*\})/i,
          lookbehind: true,
          inside: interpolationInside
        },
        'string': /[\s\S]+/
      }
    }],
    'char': {
      // https://kotlinlang.org/spec/expressions.html#character-literals
      pattern: /'(?:[^'\\\r\n]|\\(?:.|u[a-fA-F0-9]{0,4}))'/,
      greedy: true
    }
  });
  delete Prism.languages.kotlin['string'];
  Prism.languages.insertBefore('kotlin', 'keyword', {
    'annotation': {
      pattern: /\B@(?:\w+:)?(?:[A-Z]\w*|\[[^\]]+\])/,
      alias: 'builtin'
    }
  });
  Prism.languages.insertBefore('kotlin', 'function', {
    'label': {
      pattern: /\b\w+@|@\w+\b/,
      alias: 'symbol'
    }
  });
  Prism.languages.kt = Prism.languages.kotlin;
  Prism.languages.kts = Prism.languages.kotlin;
})(Prism);

(function (Prism) {
  var brackets = /(?:\((?:[^()\\]|\\[\s\S])*\)|\{(?:[^{}\\]|\\[\s\S])*\}|\[(?:[^[\]\\]|\\[\s\S])*\]|<(?:[^<>\\]|\\[\s\S])*>)/.source;
  Prism.languages.perl = {
    'comment': [{
      // POD
      pattern: /(^\s*)=\w[\s\S]*?=cut.*/m,
      lookbehind: true,
      greedy: true
    }, {
      pattern: /(^|[^\\$])#.*/,
      lookbehind: true,
      greedy: true
    }],
    // TODO Could be nice to handle Heredoc too.
    'string': [{
      pattern: RegExp(/\b(?:q|qq|qw|qx)(?![a-zA-Z0-9])\s*/.source + '(?:' + [
      // q/.../
      /([^a-zA-Z0-9\s{(\[<])(?:(?!\1)[^\\]|\\[\s\S])*\1/.source,
      // q a...a
      // eslint-disable-next-line regexp/strict
      /([a-zA-Z0-9])(?:(?!\2)[^\\]|\\[\s\S])*\2/.source,
      // q(...)
      // q{...}
      // q[...]
      // q<...>
      brackets].join('|') + ')'),
      greedy: true
    },
    // "...", `...`
    {
      pattern: /("|`)(?:(?!\1)[^\\]|\\[\s\S])*\1/,
      greedy: true
    },
    // '...'
    // FIXME Multi-line single-quoted strings are not supported as they would break variables containing '
    {
      pattern: /'(?:[^'\\\r\n]|\\.)*'/,
      greedy: true
    }],
    'regex': [{
      pattern: RegExp(/\b(?:m|qr)(?![a-zA-Z0-9])\s*/.source + '(?:' + [
      // m/.../
      /([^a-zA-Z0-9\s{(\[<])(?:(?!\1)[^\\]|\\[\s\S])*\1/.source,
      // m a...a
      // eslint-disable-next-line regexp/strict
      /([a-zA-Z0-9])(?:(?!\2)[^\\]|\\[\s\S])*\2/.source,
      // m(...)
      // m{...}
      // m[...]
      // m<...>
      brackets].join('|') + ')' + /[msixpodualngc]*/.source),
      greedy: true
    },
    // The lookbehinds prevent -s from breaking
    {
      pattern: RegExp(/(^|[^-])\b(?:s|tr|y)(?![a-zA-Z0-9])\s*/.source + '(?:' + [
      // s/.../.../
      // eslint-disable-next-line regexp/strict
      /([^a-zA-Z0-9\s{(\[<])(?:(?!\2)[^\\]|\\[\s\S])*\2(?:(?!\2)[^\\]|\\[\s\S])*\2/.source,
      // s a...a...a
      // eslint-disable-next-line regexp/strict
      /([a-zA-Z0-9])(?:(?!\3)[^\\]|\\[\s\S])*\3(?:(?!\3)[^\\]|\\[\s\S])*\3/.source,
      // s(...)(...)
      // s{...}{...}
      // s[...][...]
      // s<...><...>
      // s(...)[...]
      brackets + /\s*/.source + brackets].join('|') + ')' + /[msixpodualngcer]*/.source),
      lookbehind: true,
      greedy: true
    },
    // /.../
    // The look-ahead tries to prevent two divisions on
    // the same line from being highlighted as regex.
    // This does not support multi-line regex.
    {
      pattern: /\/(?:[^\/\\\r\n]|\\.)*\/[msixpodualngc]*(?=\s*(?:$|[\r\n,.;})&|\-+*~<>!?^]|(?:and|cmp|eq|ge|gt|le|lt|ne|not|or|x|xor)\b))/,
      greedy: true
    }],
    // FIXME Not sure about the handling of ::, ', and #
    'variable': [
    // ${^POSTMATCH}
    /[&*$@%]\{\^[A-Z]+\}/,
    // $^V
    /[&*$@%]\^[A-Z_]/,
    // ${...}
    /[&*$@%]#?(?=\{)/,
    // $foo
    /[&*$@%]#?(?:(?:::)*'?(?!\d)[\w$]+(?![\w$]))+(?:::)*/,
    // $1
    /[&*$@%]\d+/,
    // $_, @_, %!
    // The negative lookahead prevents from breaking the %= operator
    /(?!%=)[$@%][!"#$%&'()*+,\-.\/:;<=>?@[\\\]^_`{|}~]/],
    'filehandle': {
      // <>, <FOO>, _
      pattern: /<(?![<=])\S*?>|\b_\b/,
      alias: 'symbol'
    },
    'v-string': {
      // v1.2, 1.2.3
      pattern: /v\d+(?:\.\d+)*|\d+(?:\.\d+){2,}/,
      alias: 'string'
    },
    'function': {
      pattern: /(\bsub[ \t]+)\w+/,
      lookbehind: true
    },
    'keyword': /\b(?:any|break|continue|default|delete|die|do|else|elsif|eval|for|foreach|given|goto|if|last|local|my|next|our|package|print|redo|require|return|say|state|sub|switch|undef|unless|until|use|when|while)\b/,
    'number': /\b(?:0x[\dA-Fa-f](?:_?[\dA-Fa-f])*|0b[01](?:_?[01])*|(?:(?:\d(?:_?\d)*)?\.)?\d(?:_?\d)*(?:[Ee][+-]?\d+)?)\b/,
    'operator': /-[rwxoRWXOezsfdlpSbctugkTBMAC]\b|\+[+=]?|-[-=>]?|\*\*?=?|\/\/?=?|=[=~>]?|~[~=]?|\|\|?=?|&&?=?|<(?:=>?|<=?)?|>>?=?|![~=]?|[%^]=?|\.(?:=|\.\.?)?|[\\?]|\bx(?:=|\b)|\b(?:and|cmp|eq|ge|gt|le|lt|ne|not|or|xor)\b/,
    'punctuation': /[{}[\];(),:]/
  };
})(Prism);

(function (Prism) {
  /**
   * Returns the placeholder for the given language id and index.
   *
   * @param {string} language
   * @param {string|number} index
   * @returns {string}
   */
  function getPlaceholder(language, index) {
    return '___' + language.toUpperCase() + index + '___';
  }
  Object.defineProperties(Prism.languages['markup-templating'] = {}, {
    buildPlaceholders: {
      /**
       * Tokenize all inline templating expressions matching `placeholderPattern`.
       *
       * If `replaceFilter` is provided, only matches of `placeholderPattern` for which `replaceFilter` returns
       * `true` will be replaced.
       *
       * @param {object} env The environment of the `before-tokenize` hook.
       * @param {string} language The language id.
       * @param {RegExp} placeholderPattern The matches of this pattern will be replaced by placeholders.
       * @param {(match: string) => boolean} [replaceFilter]
       */
      value: function (env, language, placeholderPattern, replaceFilter) {
        if (env.language !== language) {
          return;
        }
        var tokenStack = env.tokenStack = [];
        env.code = env.code.replace(placeholderPattern, function (match) {
          if (typeof replaceFilter === 'function' && !replaceFilter(match)) {
            return match;
          }
          var i = tokenStack.length;
          var placeholder;

          // Check for existing strings
          while (env.code.indexOf(placeholder = getPlaceholder(language, i)) !== -1) {
            ++i;
          }

          // Create a sparse array
          tokenStack[i] = match;
          return placeholder;
        });

        // Switch the grammar to markup
        env.grammar = Prism.languages.markup;
      }
    },
    tokenizePlaceholders: {
      /**
       * Replace placeholders with proper tokens after tokenizing.
       *
       * @param {object} env The environment of the `after-tokenize` hook.
       * @param {string} language The language id.
       */
      value: function (env, language) {
        if (env.language !== language || !env.tokenStack) {
          return;
        }

        // Switch the grammar back
        env.grammar = Prism.languages[language];
        var j = 0;
        var keys = Object.keys(env.tokenStack);
        function walkTokens(tokens) {
          for (var i = 0; i < tokens.length; i++) {
            // all placeholders are replaced already
            if (j >= keys.length) {
              break;
            }
            var token = tokens[i];
            if (typeof token === 'string' || token.content && typeof token.content === 'string') {
              var k = keys[j];
              var t = env.tokenStack[k];
              var s = typeof token === 'string' ? token : token.content;
              var placeholder = getPlaceholder(language, k);
              var index = s.indexOf(placeholder);
              if (index > -1) {
                ++j;
                var before = s.substring(0, index);
                var middle = new Prism.Token(language, Prism.tokenize(t, env.grammar), 'language-' + language, t);
                var after = s.substring(index + placeholder.length);
                var replacement = [];
                if (before) {
                  replacement.push.apply(replacement, walkTokens([before]));
                }
                replacement.push(middle);
                if (after) {
                  replacement.push.apply(replacement, walkTokens([after]));
                }
                if (typeof token === 'string') {
                  tokens.splice.apply(tokens, [i, 1].concat(replacement));
                } else {
                  token.content = replacement;
                }
              }
            } else if (token.content /* && typeof token.content !== 'string' */) {
              walkTokens(token.content);
            }
          }
          return tokens;
        }
        walkTokens(env.tokens);
      }
    }
  });
})(Prism);

/**
 * Original by Aaron Harun: http://aahacreative.com/2012/07/31/php-syntax-highlighting-prism/
 * Modified by Miles Johnson: http://milesj.me
 * Rewritten by Tom Pavelec
 *
 * Supports PHP 5.3 - 8.0
 */
(function (Prism) {
  var comment = /\/\*[\s\S]*?\*\/|\/\/.*|#(?!\[).*/;
  var constant = [{
    pattern: /\b(?:false|true)\b/i,
    alias: 'boolean'
  }, {
    pattern: /(::\s*)\b[a-z_]\w*\b(?!\s*\()/i,
    greedy: true,
    lookbehind: true
  }, {
    pattern: /(\b(?:case|const)\s+)\b[a-z_]\w*(?=\s*[;=])/i,
    greedy: true,
    lookbehind: true
  }, /\b(?:null)\b/i, /\b[A-Z_][A-Z0-9_]*\b(?!\s*\()/];
  var number = /\b0b[01]+(?:_[01]+)*\b|\b0o[0-7]+(?:_[0-7]+)*\b|\b0x[\da-f]+(?:_[\da-f]+)*\b|(?:\b\d+(?:_\d+)*\.?(?:\d+(?:_\d+)*)?|\B\.\d+)(?:e[+-]?\d+)?/i;
  var operator = /<?=>|\?\?=?|\.{3}|\??->|[!=]=?=?|::|\*\*=?|--|\+\+|&&|\|\||<<|>>|[?~]|[/^|%*&<>.+-]=?/;
  var punctuation = /[{}\[\](),:;]/;
  Prism.languages.php = {
    'delimiter': {
      pattern: /\?>$|^<\?(?:php(?=\s)|=)?/i,
      alias: 'important'
    },
    'comment': comment,
    'variable': /\$+(?:\w+\b|(?=\{))/,
    'package': {
      pattern: /(namespace\s+|use\s+(?:function\s+)?)(?:\\?\b[a-z_]\w*)+\b(?!\\)/i,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    },
    'class-name-definition': {
      pattern: /(\b(?:class|enum|interface|trait)\s+)\b[a-z_]\w*(?!\\)\b/i,
      lookbehind: true,
      alias: 'class-name'
    },
    'function-definition': {
      pattern: /(\bfunction\s+)[a-z_]\w*(?=\s*\()/i,
      lookbehind: true,
      alias: 'function'
    },
    'keyword': [{
      pattern: /(\(\s*)\b(?:array|bool|boolean|float|int|integer|object|string)\b(?=\s*\))/i,
      alias: 'type-casting',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /([(,?]\s*)\b(?:array(?!\s*\()|bool|callable|(?:false|null)(?=\s*\|)|float|int|iterable|mixed|object|self|static|string)\b(?=\s*\$)/i,
      alias: 'type-hint',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /(\)\s*:\s*(?:\?\s*)?)\b(?:array(?!\s*\()|bool|callable|(?:false|null)(?=\s*\|)|float|int|iterable|mixed|never|object|self|static|string|void)\b/i,
      alias: 'return-type',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /\b(?:array(?!\s*\()|bool|float|int|iterable|mixed|object|string|void)\b/i,
      alias: 'type-declaration',
      greedy: true
    }, {
      pattern: /(\|\s*)(?:false|null)\b|\b(?:false|null)(?=\s*\|)/i,
      alias: 'type-declaration',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /\b(?:parent|self|static)(?=\s*::)/i,
      alias: 'static-context',
      greedy: true
    }, {
      // yield from
      pattern: /(\byield\s+)from\b/i,
      lookbehind: true
    },
    // `class` is always a keyword unlike other keywords
    /\bclass\b/i, {
      // https://www.php.net/manual/en/reserved.keywords.php
      //
      // keywords cannot be preceded by "->"
      // the complex lookbehind means `(?<!(?:->|::)\s*)`
      pattern: /((?:^|[^\s>:]|(?:^|[^-])>|(?:^|[^:]):)\s*)\b(?:abstract|and|array|as|break|callable|case|catch|clone|const|continue|declare|default|die|do|echo|else|elseif|empty|enddeclare|endfor|endforeach|endif|endswitch|endwhile|enum|eval|exit|extends|final|finally|fn|for|foreach|function|global|goto|if|implements|include|include_once|instanceof|insteadof|interface|isset|list|match|namespace|never|new|or|parent|print|private|protected|public|readonly|require|require_once|return|self|static|switch|throw|trait|try|unset|use|var|while|xor|yield|__halt_compiler)\b/i,
      lookbehind: true
    }],
    'argument-name': {
      pattern: /([(,]\s*)\b[a-z_]\w*(?=\s*:(?!:))/i,
      lookbehind: true
    },
    'class-name': [{
      pattern: /(\b(?:extends|implements|instanceof|new(?!\s+self|\s+static))\s+|\bcatch\s*\()\b[a-z_]\w*(?!\\)\b/i,
      greedy: true,
      lookbehind: true
    }, {
      pattern: /(\|\s*)\b[a-z_]\w*(?!\\)\b/i,
      greedy: true,
      lookbehind: true
    }, {
      pattern: /\b[a-z_]\w*(?!\\)\b(?=\s*\|)/i,
      greedy: true
    }, {
      pattern: /(\|\s*)(?:\\?\b[a-z_]\w*)+\b/i,
      alias: 'class-name-fully-qualified',
      greedy: true,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /(?:\\?\b[a-z_]\w*)+\b(?=\s*\|)/i,
      alias: 'class-name-fully-qualified',
      greedy: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /(\b(?:extends|implements|instanceof|new(?!\s+self\b|\s+static\b))\s+|\bcatch\s*\()(?:\\?\b[a-z_]\w*)+\b(?!\\)/i,
      alias: 'class-name-fully-qualified',
      greedy: true,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /\b[a-z_]\w*(?=\s*\$)/i,
      alias: 'type-declaration',
      greedy: true
    }, {
      pattern: /(?:\\?\b[a-z_]\w*)+(?=\s*\$)/i,
      alias: ['class-name-fully-qualified', 'type-declaration'],
      greedy: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /\b[a-z_]\w*(?=\s*::)/i,
      alias: 'static-context',
      greedy: true
    }, {
      pattern: /(?:\\?\b[a-z_]\w*)+(?=\s*::)/i,
      alias: ['class-name-fully-qualified', 'static-context'],
      greedy: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /([(,?]\s*)[a-z_]\w*(?=\s*\$)/i,
      alias: 'type-hint',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /([(,?]\s*)(?:\\?\b[a-z_]\w*)+(?=\s*\$)/i,
      alias: ['class-name-fully-qualified', 'type-hint'],
      greedy: true,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    }, {
      pattern: /(\)\s*:\s*(?:\?\s*)?)\b[a-z_]\w*(?!\\)\b/i,
      alias: 'return-type',
      greedy: true,
      lookbehind: true
    }, {
      pattern: /(\)\s*:\s*(?:\?\s*)?)(?:\\?\b[a-z_]\w*)+\b(?!\\)/i,
      alias: ['class-name-fully-qualified', 'return-type'],
      greedy: true,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    }],
    'constant': constant,
    'function': {
      pattern: /(^|[^\\\w])\\?[a-z_](?:[\w\\]*\w)?(?=\s*\()/i,
      lookbehind: true,
      inside: {
        'punctuation': /\\/
      }
    },
    'property': {
      pattern: /(->\s*)\w+/,
      lookbehind: true
    },
    'number': number,
    'operator': operator,
    'punctuation': punctuation
  };
  var string_interpolation = {
    pattern: /\{\$(?:\{(?:\{[^{}]+\}|[^{}]+)\}|[^{}])+\}|(^|[^\\{])\$+(?:\w+(?:\[[^\r\n\[\]]+\]|->\w+)?)/,
    lookbehind: true,
    inside: Prism.languages.php
  };
  var string = [{
    pattern: /<<<'([^']+)'[\r\n](?:.*[\r\n])*?\1;/,
    alias: 'nowdoc-string',
    greedy: true,
    inside: {
      'delimiter': {
        pattern: /^<<<'[^']+'|[a-z_]\w*;$/i,
        alias: 'symbol',
        inside: {
          'punctuation': /^<<<'?|[';]$/
        }
      }
    }
  }, {
    pattern: /<<<(?:"([^"]+)"[\r\n](?:.*[\r\n])*?\1;|([a-z_]\w*)[\r\n](?:.*[\r\n])*?\2;)/i,
    alias: 'heredoc-string',
    greedy: true,
    inside: {
      'delimiter': {
        pattern: /^<<<(?:"[^"]+"|[a-z_]\w*)|[a-z_]\w*;$/i,
        alias: 'symbol',
        inside: {
          'punctuation': /^<<<"?|[";]$/
        }
      },
      'interpolation': string_interpolation
    }
  }, {
    pattern: /`(?:\\[\s\S]|[^\\`])*`/,
    alias: 'backtick-quoted-string',
    greedy: true
  }, {
    pattern: /'(?:\\[\s\S]|[^\\'])*'/,
    alias: 'single-quoted-string',
    greedy: true
  }, {
    pattern: /"(?:\\[\s\S]|[^\\"])*"/,
    alias: 'double-quoted-string',
    greedy: true,
    inside: {
      'interpolation': string_interpolation
    }
  }];
  Prism.languages.insertBefore('php', 'variable', {
    'string': string,
    'attribute': {
      pattern: /#\[(?:[^"'\/#]|\/(?![*/])|\/\/.*$|#(?!\[).*$|\/\*(?:[^*]|\*(?!\/))*\*\/|"(?:\\[\s\S]|[^\\"])*"|'(?:\\[\s\S]|[^\\'])*')+\](?=\s*[a-z$#])/im,
      greedy: true,
      inside: {
        'attribute-content': {
          pattern: /^(#\[)[\s\S]+(?=\]$)/,
          lookbehind: true,
          // inside can appear subset of php
          inside: {
            'comment': comment,
            'string': string,
            'attribute-class-name': [{
              pattern: /([^:]|^)\b[a-z_]\w*(?!\\)\b/i,
              alias: 'class-name',
              greedy: true,
              lookbehind: true
            }, {
              pattern: /([^:]|^)(?:\\?\b[a-z_]\w*)+/i,
              alias: ['class-name', 'class-name-fully-qualified'],
              greedy: true,
              lookbehind: true,
              inside: {
                'punctuation': /\\/
              }
            }],
            'constant': constant,
            'number': number,
            'operator': operator,
            'punctuation': punctuation
          }
        },
        'delimiter': {
          pattern: /^#\[|\]$/,
          alias: 'punctuation'
        }
      }
    }
  });
  Prism.hooks.add('before-tokenize', function (env) {
    if (!/<\?/.test(env.code)) {
      return;
    }
    var phpPattern = /<\?(?:[^"'/#]|\/(?![*/])|("|')(?:\\[\s\S]|(?!\1)[^\\])*\1|(?:\/\/|#(?!\[))(?:[^?\n\r]|\?(?!>))*(?=$|\?>|[\r\n])|#\[|\/\*(?:[^*]|\*(?!\/))*(?:\*\/|$))*?(?:\?>|$)/g;
    Prism.languages['markup-templating'].buildPlaceholders(env, 'php', phpPattern);
  });
  Prism.hooks.add('after-tokenize', function (env) {
    Prism.languages['markup-templating'].tokenizePlaceholders(env, 'php');
  });
})(Prism);

(function (Prism) {
  var powershell = Prism.languages.powershell = {
    'comment': [{
      pattern: /(^|[^`])<#[\s\S]*?#>/,
      lookbehind: true
    }, {
      pattern: /(^|[^`])#.*/,
      lookbehind: true
    }],
    'string': [{
      pattern: /"(?:`[\s\S]|[^`"])*"/,
      greedy: true,
      inside: null // see below
    }, {
      pattern: /'(?:[^']|'')*'/,
      greedy: true
    }],
    // Matches name spaces as well as casts, attribute decorators. Force starting with letter to avoid matching array indices
    // Supports two levels of nested brackets (e.g. `[OutputType([System.Collections.Generic.List[int]])]`)
    'namespace': /\[[a-z](?:\[(?:\[[^\]]*\]|[^\[\]])*\]|[^\[\]])*\]/i,
    'boolean': /\$(?:false|true)\b/i,
    'variable': /\$\w+\b/,
    // Cmdlets and aliases. Aliases should come last, otherwise "write" gets preferred over "write-host" for example
    // Get-Command | ?{ $_.ModuleName -match "Microsoft.PowerShell.(Util|Core|Management)" }
    // Get-Alias | ?{ $_.ReferencedCommand.Module.Name -match "Microsoft.PowerShell.(Util|Core|Management)" }
    'function': [/\b(?:Add|Approve|Assert|Backup|Block|Checkpoint|Clear|Close|Compare|Complete|Compress|Confirm|Connect|Convert|ConvertFrom|ConvertTo|Copy|Debug|Deny|Disable|Disconnect|Dismount|Edit|Enable|Enter|Exit|Expand|Export|Find|ForEach|Format|Get|Grant|Group|Hide|Import|Initialize|Install|Invoke|Join|Limit|Lock|Measure|Merge|Move|New|Open|Optimize|Out|Ping|Pop|Protect|Publish|Push|Read|Receive|Redo|Register|Remove|Rename|Repair|Request|Reset|Resize|Resolve|Restart|Restore|Resume|Revoke|Save|Search|Select|Send|Set|Show|Skip|Sort|Split|Start|Step|Stop|Submit|Suspend|Switch|Sync|Tee|Test|Trace|Unblock|Undo|Uninstall|Unlock|Unprotect|Unpublish|Unregister|Update|Use|Wait|Watch|Where|Write)-[a-z]+\b/i, /\b(?:ac|cat|chdir|clc|cli|clp|clv|compare|copy|cp|cpi|cpp|cvpa|dbp|del|diff|dir|ebp|echo|epal|epcsv|epsn|erase|fc|fl|ft|fw|gal|gbp|gc|gci|gcs|gdr|gi|gl|gm|gp|gps|group|gsv|gu|gv|gwmi|iex|ii|ipal|ipcsv|ipsn|irm|iwmi|iwr|kill|lp|ls|measure|mi|mount|move|mp|mv|nal|ndr|ni|nv|ogv|popd|ps|pushd|pwd|rbp|rd|rdr|ren|ri|rm|rmdir|rni|rnp|rp|rv|rvpa|rwmi|sal|saps|sasv|sbp|sc|select|set|shcm|si|sl|sleep|sls|sort|sp|spps|spsv|start|sv|swmi|tee|trcm|type|write)\b/i],
    // per http://technet.microsoft.com/en-us/library/hh847744.aspx
    'keyword': /\b(?:Begin|Break|Catch|Class|Continue|Data|Define|Do|DynamicParam|Else|ElseIf|End|Exit|Filter|Finally|For|ForEach|From|Function|If|InlineScript|Parallel|Param|Process|Return|Sequence|Switch|Throw|Trap|Try|Until|Using|Var|While|Workflow)\b/i,
    'operator': {
      pattern: /(^|\W)(?:!|-(?:b?(?:and|x?or)|as|(?:Not)?(?:Contains|In|Like|Match)|eq|ge|gt|is(?:Not)?|Join|le|lt|ne|not|Replace|sh[lr])\b|-[-=]?|\+[+=]?|[*\/%]=?)/i,
      lookbehind: true
    },
    'punctuation': /[|{}[\];(),.]/
  };

  // Variable interpolation inside strings, and nested expressions
  powershell.string[0].inside = {
    'function': {
      // Allow for one level of nesting
      pattern: /(^|[^`])\$\((?:\$\([^\r\n()]*\)|(?!\$\()[^\r\n)])*\)/,
      lookbehind: true,
      inside: powershell
    },
    'boolean': powershell.boolean,
    'variable': powershell.variable
  };
})(Prism);

Prism.languages.python = {
  'comment': {
    pattern: /(^|[^\\])#.*/,
    lookbehind: true,
    greedy: true
  },
  'string-interpolation': {
    pattern: /(?:f|fr|rf)(?:("""|''')[\s\S]*?\1|("|')(?:\\.|(?!\2)[^\\\r\n])*\2)/i,
    greedy: true,
    inside: {
      'interpolation': {
        // "{" <expression> <optional "!s", "!r", or "!a"> <optional ":" format specifier> "}"
        pattern: /((?:^|[^{])(?:\{\{)*)\{(?!\{)(?:[^{}]|\{(?!\{)(?:[^{}]|\{(?!\{)(?:[^{}])+\})+\})+\}/,
        lookbehind: true,
        inside: {
          'format-spec': {
            pattern: /(:)[^:(){}]+(?=\}$)/,
            lookbehind: true
          },
          'conversion-option': {
            pattern: /![sra](?=[:}]$)/,
            alias: 'punctuation'
          },
          rest: null
        }
      },
      'string': /[\s\S]+/
    }
  },
  'triple-quoted-string': {
    pattern: /(?:[rub]|br|rb)?("""|''')[\s\S]*?\1/i,
    greedy: true,
    alias: 'string'
  },
  'string': {
    pattern: /(?:[rub]|br|rb)?("|')(?:\\.|(?!\1)[^\\\r\n])*\1/i,
    greedy: true
  },
  'function': {
    pattern: /((?:^|\s)def[ \t]+)[a-zA-Z_]\w*(?=\s*\()/g,
    lookbehind: true
  },
  'class-name': {
    pattern: /(\bclass\s+)\w+/i,
    lookbehind: true
  },
  'decorator': {
    pattern: /(^[\t ]*)@\w+(?:\.\w+)*/m,
    lookbehind: true,
    alias: ['annotation', 'punctuation'],
    inside: {
      'punctuation': /\./
    }
  },
  'keyword': /\b(?:_(?=\s*:)|and|as|assert|async|await|break|case|class|continue|def|del|elif|else|except|exec|finally|for|from|global|if|import|in|is|lambda|match|nonlocal|not|or|pass|print|raise|return|try|while|with|yield)\b/,
  'builtin': /\b(?:__import__|abs|all|any|apply|ascii|basestring|bin|bool|buffer|bytearray|bytes|callable|chr|classmethod|cmp|coerce|compile|complex|delattr|dict|dir|divmod|enumerate|eval|execfile|file|filter|float|format|frozenset|getattr|globals|hasattr|hash|help|hex|id|input|int|intern|isinstance|issubclass|iter|len|list|locals|long|map|max|memoryview|min|next|object|oct|open|ord|pow|property|range|raw_input|reduce|reload|repr|reversed|round|set|setattr|slice|sorted|staticmethod|str|sum|super|tuple|type|unichr|unicode|vars|xrange|zip)\b/,
  'boolean': /\b(?:False|None|True)\b/,
  'number': /\b0(?:b(?:_?[01])+|o(?:_?[0-7])+|x(?:_?[a-f0-9])+)\b|(?:\b\d+(?:_\d+)*(?:\.(?:\d+(?:_\d+)*)?)?|\B\.\d+(?:_\d+)*)(?:e[+-]?\d+(?:_\d+)*)?j?(?!\w)/i,
  'operator': /[-+%=]=?|!=|:=|\*\*?=?|\/\/?=?|<[<=>]?|>[=>]?|[&|^~]/,
  'punctuation': /[{}[\];(),.:]/
};
Prism.languages.python['string-interpolation'].inside['interpolation'].inside.rest = Prism.languages.python;
Prism.languages.py = Prism.languages.python;

/**
 * Original by Samuel Flores
 *
 * Adds the following new token classes:
 *     constant, builtin, variable, symbol, regex
 */
(function (Prism) {
  Prism.languages.ruby = Prism.languages.extend('clike', {
    'comment': {
      pattern: /#.*|^=begin\s[\s\S]*?^=end/m,
      greedy: true
    },
    'class-name': {
      pattern: /(\b(?:class|module)\s+|\bcatch\s+\()[\w.\\]+|\b[A-Z_]\w*(?=\s*\.\s*new\b)/,
      lookbehind: true,
      inside: {
        'punctuation': /[.\\]/
      }
    },
    'keyword': /\b(?:BEGIN|END|alias|and|begin|break|case|class|def|define_method|defined|do|each|else|elsif|end|ensure|extend|for|if|in|include|module|new|next|nil|not|or|prepend|private|protected|public|raise|redo|require|rescue|retry|return|self|super|then|throw|undef|unless|until|when|while|yield)\b/,
    'operator': /\.{2,3}|&\.|===|<?=>|[!=]?~|(?:&&|\|\||<<|>>|\*\*|[+\-*/%<>!^&|=])=?|[?:]/,
    'punctuation': /[(){}[\].,;]/
  });
  Prism.languages.insertBefore('ruby', 'operator', {
    'double-colon': {
      pattern: /::/,
      alias: 'punctuation'
    }
  });
  var interpolation = {
    pattern: /((?:^|[^\\])(?:\\{2})*)#\{(?:[^{}]|\{[^{}]*\})*\}/,
    lookbehind: true,
    inside: {
      'content': {
        pattern: /^(#\{)[\s\S]+(?=\}$)/,
        lookbehind: true,
        inside: Prism.languages.ruby
      },
      'delimiter': {
        pattern: /^#\{|\}$/,
        alias: 'punctuation'
      }
    }
  };
  delete Prism.languages.ruby.function;
  var percentExpression = '(?:' + [/([^a-zA-Z0-9\s{(\[<=])(?:(?!\1)[^\\]|\\[\s\S])*\1/.source, /\((?:[^()\\]|\\[\s\S]|\((?:[^()\\]|\\[\s\S])*\))*\)/.source, /\{(?:[^{}\\]|\\[\s\S]|\{(?:[^{}\\]|\\[\s\S])*\})*\}/.source, /\[(?:[^\[\]\\]|\\[\s\S]|\[(?:[^\[\]\\]|\\[\s\S])*\])*\]/.source, /<(?:[^<>\\]|\\[\s\S]|<(?:[^<>\\]|\\[\s\S])*>)*>/.source].join('|') + ')';
  var symbolName = /(?:"(?:\\.|[^"\\\r\n])*"|(?:\b[a-zA-Z_]\w*|[^\s\0-\x7F]+)[?!]?|\$.)/.source;
  Prism.languages.insertBefore('ruby', 'keyword', {
    'regex-literal': [{
      pattern: RegExp(/%r/.source + percentExpression + /[egimnosux]{0,6}/.source),
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'regex': /[\s\S]+/
      }
    }, {
      pattern: /(^|[^/])\/(?!\/)(?:\[[^\r\n\]]+\]|\\.|[^[/\\\r\n])+\/[egimnosux]{0,6}(?=\s*(?:$|[\r\n,.;})#]))/,
      lookbehind: true,
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'regex': /[\s\S]+/
      }
    }],
    'variable': /[@$]+[a-zA-Z_]\w*(?:[?!]|\b)/,
    'symbol': [{
      pattern: RegExp(/(^|[^:]):/.source + symbolName),
      lookbehind: true,
      greedy: true
    }, {
      pattern: RegExp(/([\r\n{(,][ \t]*)/.source + symbolName + /(?=:(?!:))/.source),
      lookbehind: true,
      greedy: true
    }],
    'method-definition': {
      pattern: /(\bdef\s+)\w+(?:\s*\.\s*\w+)?/,
      lookbehind: true,
      inside: {
        'function': /\b\w+$/,
        'keyword': /^self\b/,
        'class-name': /^\w+/,
        'punctuation': /\./
      }
    }
  });
  Prism.languages.insertBefore('ruby', 'string', {
    'string-literal': [{
      pattern: RegExp(/%[qQiIwWs]?/.source + percentExpression),
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'string': /[\s\S]+/
      }
    }, {
      pattern: /("|')(?:#\{[^}]+\}|#(?!\{)|\\(?:\r\n|[\s\S])|(?!\1)[^\\#\r\n])*\1/,
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'string': /[\s\S]+/
      }
    }, {
      pattern: /<<[-~]?([a-z_]\w*)[\r\n](?:.*[\r\n])*?[\t ]*\1/i,
      alias: 'heredoc-string',
      greedy: true,
      inside: {
        'delimiter': {
          pattern: /^<<[-~]?[a-z_]\w*|\b[a-z_]\w*$/i,
          inside: {
            'symbol': /\b\w+/,
            'punctuation': /^<<[-~]?/
          }
        },
        'interpolation': interpolation,
        'string': /[\s\S]+/
      }
    }, {
      pattern: /<<[-~]?'([a-z_]\w*)'[\r\n](?:.*[\r\n])*?[\t ]*\1/i,
      alias: 'heredoc-string',
      greedy: true,
      inside: {
        'delimiter': {
          pattern: /^<<[-~]?'[a-z_]\w*'|\b[a-z_]\w*$/i,
          inside: {
            'symbol': /\b\w+/,
            'punctuation': /^<<[-~]?'|'$/
          }
        },
        'string': /[\s\S]+/
      }
    }],
    'command-literal': [{
      pattern: RegExp(/%x/.source + percentExpression),
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'command': {
          pattern: /[\s\S]+/,
          alias: 'string'
        }
      }
    }, {
      pattern: /`(?:#\{[^}]+\}|#(?!\{)|\\(?:\r\n|[\s\S])|[^\\`#\r\n])*`/,
      greedy: true,
      inside: {
        'interpolation': interpolation,
        'command': {
          pattern: /[\s\S]+/,
          alias: 'string'
        }
      }
    }]
  });
  delete Prism.languages.ruby.string;
  Prism.languages.insertBefore('ruby', 'number', {
    'builtin': /\b(?:Array|Bignum|Binding|Class|Continuation|Dir|Exception|FalseClass|File|Fixnum|Float|Hash|IO|Integer|MatchData|Method|Module|NilClass|Numeric|Object|Proc|Range|Regexp|Stat|String|Struct|Symbol|TMS|Thread|ThreadGroup|Time|TrueClass)\b/,
    'constant': /\b[A-Z][A-Z0-9_]*(?:[?!]|\b)/
  });
  Prism.languages.rb = Prism.languages.ruby;
})(Prism);

Prism.languages.scala = Prism.languages.extend('java', {
  'triple-quoted-string': {
    pattern: /"""[\s\S]*?"""/,
    greedy: true,
    alias: 'string'
  },
  'string': {
    pattern: /("|')(?:\\.|(?!\1)[^\\\r\n])*\1/,
    greedy: true
  },
  'keyword': /<-|=>|\b(?:abstract|case|catch|class|def|derives|do|else|enum|extends|extension|final|finally|for|forSome|given|if|implicit|import|infix|inline|lazy|match|new|null|object|opaque|open|override|package|private|protected|return|sealed|self|super|this|throw|trait|transparent|try|type|using|val|var|while|with|yield)\b/,
  'number': /\b0x(?:[\da-f]*\.)?[\da-f]+|(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:e\d+)?[dfl]?/i,
  'builtin': /\b(?:Any|AnyRef|AnyVal|Boolean|Byte|Char|Double|Float|Int|Long|Nothing|Short|String|Unit)\b/,
  'symbol': /'[^\d\s\\]\w*/
});
Prism.languages.insertBefore('scala', 'triple-quoted-string', {
  'string-interpolation': {
    pattern: /\b[a-z]\w*(?:"""(?:[^$]|\$(?:[^{]|\{(?:[^{}]|\{[^{}]*\})*\}))*?"""|"(?:[^$"\r\n]|\$(?:[^{]|\{(?:[^{}]|\{[^{}]*\})*\}))*")/i,
    greedy: true,
    inside: {
      'id': {
        pattern: /^\w+/,
        greedy: true,
        alias: 'function'
      },
      'escape': {
        pattern: /\\\$"|\$[$"]/,
        greedy: true,
        alias: 'symbol'
      },
      'interpolation': {
        pattern: /\$(?:\w+|\{(?:[^{}]|\{[^{}]*\})*\})/,
        greedy: true,
        inside: {
          'punctuation': /^\$\{?|\}$/,
          'expression': {
            pattern: /[\s\S]+/,
            inside: Prism.languages.scala
          }
        }
      },
      'string': /[\s\S]+/
    }
  }
});
delete Prism.languages.scala['class-name'];
delete Prism.languages.scala['function'];
delete Prism.languages.scala['constant'];

Prism.languages.sql = {
  'comment': {
    pattern: /(^|[^\\])(?:\/\*[\s\S]*?\*\/|(?:--|\/\/|#).*)/,
    lookbehind: true
  },
  'variable': [{
    pattern: /@(["'`])(?:\\[\s\S]|(?!\1)[^\\])+\1/,
    greedy: true
  }, /@[\w.$]+/],
  'string': {
    pattern: /(^|[^@\\])("|')(?:\\[\s\S]|(?!\2)[^\\]|\2\2)*\2/,
    greedy: true,
    lookbehind: true
  },
  'identifier': {
    pattern: /(^|[^@\\])`(?:\\[\s\S]|[^`\\]|``)*`/,
    greedy: true,
    lookbehind: true,
    inside: {
      'punctuation': /^`|`$/
    }
  },
  'function': /\b(?:AVG|COUNT|FIRST|FORMAT|LAST|LCASE|LEN|MAX|MID|MIN|MOD|NOW|ROUND|SUM|UCASE)(?=\s*\()/i,
  // Should we highlight user defined functions too?
  'keyword': /\b(?:ACTION|ADD|AFTER|ALGORITHM|ALL|ALTER|ANALYZE|ANY|APPLY|AS|ASC|AUTHORIZATION|AUTO_INCREMENT|BACKUP|BDB|BEGIN|BERKELEYDB|BIGINT|BINARY|BIT|BLOB|BOOL|BOOLEAN|BREAK|BROWSE|BTREE|BULK|BY|CALL|CASCADED?|CASE|CHAIN|CHAR(?:ACTER|SET)?|CHECK(?:POINT)?|CLOSE|CLUSTERED|COALESCE|COLLATE|COLUMNS?|COMMENT|COMMIT(?:TED)?|COMPUTE|CONNECT|CONSISTENT|CONSTRAINT|CONTAINS(?:TABLE)?|CONTINUE|CONVERT|CREATE|CROSS|CURRENT(?:_DATE|_TIME|_TIMESTAMP|_USER)?|CURSOR|CYCLE|DATA(?:BASES?)?|DATE(?:TIME)?|DAY|DBCC|DEALLOCATE|DEC|DECIMAL|DECLARE|DEFAULT|DEFINER|DELAYED|DELETE|DELIMITERS?|DENY|DESC|DESCRIBE|DETERMINISTIC|DISABLE|DISCARD|DISK|DISTINCT|DISTINCTROW|DISTRIBUTED|DO|DOUBLE|DROP|DUMMY|DUMP(?:FILE)?|DUPLICATE|ELSE(?:IF)?|ENABLE|ENCLOSED|END|ENGINE|ENUM|ERRLVL|ERRORS|ESCAPED?|EXCEPT|EXEC(?:UTE)?|EXISTS|EXIT|EXPLAIN|EXTENDED|FETCH|FIELDS|FILE|FILLFACTOR|FIRST|FIXED|FLOAT|FOLLOWING|FOR(?: EACH ROW)?|FORCE|FOREIGN|FREETEXT(?:TABLE)?|FROM|FULL|FUNCTION|GEOMETRY(?:COLLECTION)?|GLOBAL|GOTO|GRANT|GROUP|HANDLER|HASH|HAVING|HOLDLOCK|HOUR|IDENTITY(?:COL|_INSERT)?|IF|IGNORE|IMPORT|INDEX|INFILE|INNER|INNODB|INOUT|INSERT|INT|INTEGER|INTERSECT|INTERVAL|INTO|INVOKER|ISOLATION|ITERATE|JOIN|KEYS?|KILL|LANGUAGE|LAST|LEAVE|LEFT|LEVEL|LIMIT|LINENO|LINES|LINESTRING|LOAD|LOCAL|LOCK|LONG(?:BLOB|TEXT)|LOOP|MATCH(?:ED)?|MEDIUM(?:BLOB|INT|TEXT)|MERGE|MIDDLEINT|MINUTE|MODE|MODIFIES|MODIFY|MONTH|MULTI(?:LINESTRING|POINT|POLYGON)|NATIONAL|NATURAL|NCHAR|NEXT|NO|NONCLUSTERED|NULLIF|NUMERIC|OFF?|OFFSETS?|ON|OPEN(?:DATASOURCE|QUERY|ROWSET)?|OPTIMIZE|OPTION(?:ALLY)?|ORDER|OUT(?:ER|FILE)?|OVER|PARTIAL|PARTITION|PERCENT|PIVOT|PLAN|POINT|POLYGON|PRECEDING|PRECISION|PREPARE|PREV|PRIMARY|PRINT|PRIVILEGES|PROC(?:EDURE)?|PUBLIC|PURGE|QUICK|RAISERROR|READS?|REAL|RECONFIGURE|REFERENCES|RELEASE|RENAME|REPEAT(?:ABLE)?|REPLACE|REPLICATION|REQUIRE|RESIGNAL|RESTORE|RESTRICT|RETURN(?:ING|S)?|REVOKE|RIGHT|ROLLBACK|ROUTINE|ROW(?:COUNT|GUIDCOL|S)?|RTREE|RULE|SAVE(?:POINT)?|SCHEMA|SECOND|SELECT|SERIAL(?:IZABLE)?|SESSION(?:_USER)?|SET(?:USER)?|SHARE|SHOW|SHUTDOWN|SIMPLE|SMALLINT|SNAPSHOT|SOME|SONAME|SQL|START(?:ING)?|STATISTICS|STATUS|STRIPED|SYSTEM_USER|TABLES?|TABLESPACE|TEMP(?:ORARY|TABLE)?|TERMINATED|TEXT(?:SIZE)?|THEN|TIME(?:STAMP)?|TINY(?:BLOB|INT|TEXT)|TOP?|TRAN(?:SACTIONS?)?|TRIGGER|TRUNCATE|TSEQUAL|TYPES?|UNBOUNDED|UNCOMMITTED|UNDEFINED|UNION|UNIQUE|UNLOCK|UNPIVOT|UNSIGNED|UPDATE(?:TEXT)?|USAGE|USE|USER|USING|VALUES?|VAR(?:BINARY|CHAR|CHARACTER|YING)|VIEW|WAITFOR|WARNINGS|WHEN|WHERE|WHILE|WITH(?: ROLLUP|IN)?|WORK|WRITE(?:TEXT)?|YEAR)\b/i,
  'boolean': /\b(?:FALSE|NULL|TRUE)\b/i,
  'number': /\b0x[\da-f]+\b|\b\d+(?:\.\d*)?|\B\.\d+\b/i,
  'operator': /[-+*\/=%^~]|&&?|\|\|?|!=?|<(?:=>?|<|>)?|>[>=]?|\b(?:AND|BETWEEN|DIV|ILIKE|IN|IS|LIKE|NOT|OR|REGEXP|RLIKE|SOUNDS LIKE|XOR)\b/i,
  'punctuation': /[;[\]()`,.]/
};

Prism.languages['visual-basic'] = {
  'comment': {
    pattern: /(?:['‘’]|REM\b)(?:[^\r\n_]|_(?:\r\n?|\n)?)*/i,
    inside: {
      'keyword': /^REM/i
    }
  },
  'directive': {
    pattern: /#(?:Const|Else|ElseIf|End|ExternalChecksum|ExternalSource|If|Region)(?:\b_[ \t]*(?:\r\n?|\n)|.)+/i,
    alias: 'property',
    greedy: true
  },
  'string': {
    pattern: /\$?["“”](?:["“”]{2}|[^"“”])*["“”]C?/i,
    greedy: true
  },
  'date': {
    pattern: /#[ \t]*(?:\d+([/-])\d+\1\d+(?:[ \t]+(?:\d+[ \t]*(?:AM|PM)|\d+:\d+(?::\d+)?(?:[ \t]*(?:AM|PM))?))?|\d+[ \t]*(?:AM|PM)|\d+:\d+(?::\d+)?(?:[ \t]*(?:AM|PM))?)[ \t]*#/i,
    alias: 'number'
  },
  'number': /(?:(?:\b\d+(?:\.\d+)?|\.\d+)(?:E[+-]?\d+)?|&[HO][\dA-F]+)(?:[FRD]|U?[ILS])?/i,
  'boolean': /\b(?:False|Nothing|True)\b/i,
  'keyword': /\b(?:AddHandler|AddressOf|Alias|And(?:Also)?|As|Boolean|ByRef|Byte|ByVal|Call|Case|Catch|C(?:Bool|Byte|Char|Date|Dbl|Dec|Int|Lng|Obj|SByte|Short|Sng|Str|Type|UInt|ULng|UShort)|Char|Class|Const|Continue|Currency|Date|Decimal|Declare|Default|Delegate|Dim|DirectCast|Do|Double|Each|Else(?:If)?|End(?:If)?|Enum|Erase|Error|Event|Exit|Finally|For|Friend|Function|Get(?:Type|XMLNamespace)?|Global|GoSub|GoTo|Handles|If|Implements|Imports|In|Inherits|Integer|Interface|Is|IsNot|Let|Lib|Like|Long|Loop|Me|Mod|Module|Must(?:Inherit|Override)|My(?:Base|Class)|Namespace|Narrowing|New|Next|Not(?:Inheritable|Overridable)?|Object|Of|On|Operator|Option(?:al)?|Or(?:Else)?|Out|Overloads|Overridable|Overrides|ParamArray|Partial|Private|Property|Protected|Public|RaiseEvent|ReadOnly|ReDim|RemoveHandler|Resume|Return|SByte|Select|Set|Shadows|Shared|short|Single|Static|Step|Stop|String|Structure|Sub|SyncLock|Then|Throw|To|Try|TryCast|Type|TypeOf|U(?:Integer|Long|Short)|Until|Using|Variant|Wend|When|While|Widening|With(?:Events)?|WriteOnly|Xor)\b/i,
  'operator': /[+\-*/\\^<=>&#@$%!]|\b_(?=[ \t]*[\r\n])/,
  'punctuation': /[{}().,:?]/
};
Prism.languages.vb = Prism.languages['visual-basic'];
Prism.languages.vba = Prism.languages['visual-basic'];

(function (Prism) {
  /**
   * If the given language is present, it will insert the given doc comment grammar token into it.
   *
   * @param {string} lang
   * @param {any} docComment
   */
  function insertDocComment(lang, docComment) {
    if (Prism.languages[lang]) {
      Prism.languages.insertBefore(lang, 'comment', {
        'doc-comment': docComment
      });
    }
  }
  var tag = Prism.languages.markup.tag;
  var slashDocComment = {
    pattern: /\/\/\/.*/,
    greedy: true,
    alias: 'comment',
    inside: {
      'tag': tag
    }
  };
  var tickDocComment = {
    pattern: /'''.*/,
    greedy: true,
    alias: 'comment',
    inside: {
      'tag': tag
    }
  };
  insertDocComment('csharp', slashDocComment);
  insertDocComment('fsharp', slashDocComment);
  insertDocComment('vbnet', tickDocComment);
})(Prism);

(function (Prism) {
  // https://yaml.org/spec/1.2/spec.html#c-ns-anchor-property
  // https://yaml.org/spec/1.2/spec.html#c-ns-alias-node
  var anchorOrAlias = /[*&][^\s[\]{},]+/;
  // https://yaml.org/spec/1.2/spec.html#c-ns-tag-property
  var tag = /!(?:<[\w\-%#;/?:@&=+$,.!~*'()[\]]+>|(?:[a-zA-Z\d-]*!)?[\w\-%#;/?:@&=+$.~*'()]+)?/;
  // https://yaml.org/spec/1.2/spec.html#c-ns-properties(n,c)
  var properties = '(?:' + tag.source + '(?:[ \t]+' + anchorOrAlias.source + ')?|' + anchorOrAlias.source + '(?:[ \t]+' + tag.source + ')?)';
  // https://yaml.org/spec/1.2/spec.html#ns-plain(n,c)
  // This is a simplified version that doesn't support "#" and multiline keys
  // All these long scarry character classes are simplified versions of YAML's characters
  var plainKey = /(?:[^\s\x00-\x08\x0e-\x1f!"#%&'*,\-:>?@[\]`{|}\x7f-\x84\x86-\x9f\ud800-\udfff\ufffe\uffff]|[?:-]<PLAIN>)(?:[ \t]*(?:(?![#:])<PLAIN>|:<PLAIN>))*/.source.replace(/<PLAIN>/g, function () {
    return /[^\s\x00-\x08\x0e-\x1f,[\]{}\x7f-\x84\x86-\x9f\ud800-\udfff\ufffe\uffff]/.source;
  });
  var string = /"(?:[^"\\\r\n]|\\.)*"|'(?:[^'\\\r\n]|\\.)*'/.source;

  /**
   *
   * @param {string} value
   * @param {string} [flags]
   * @returns {RegExp}
   */
  function createValuePattern(value, flags) {
    flags = (flags || '').replace(/m/g, '') + 'm'; // add m flag
    var pattern = /([:\-,[{]\s*(?:\s<<prop>>[ \t]+)?)(?:<<value>>)(?=[ \t]*(?:$|,|\]|\}|(?:[\r\n]\s*)?#))/.source.replace(/<<prop>>/g, function () {
      return properties;
    }).replace(/<<value>>/g, function () {
      return value;
    });
    return RegExp(pattern, flags);
  }
  Prism.languages.yaml = {
    'scalar': {
      pattern: RegExp(/([\-:]\s*(?:\s<<prop>>[ \t]+)?[|>])[ \t]*(?:((?:\r?\n|\r)[ \t]+)\S[^\r\n]*(?:\2[^\r\n]+)*)/.source.replace(/<<prop>>/g, function () {
        return properties;
      })),
      lookbehind: true,
      alias: 'string'
    },
    'comment': /#.*/,
    'key': {
      pattern: RegExp(/((?:^|[:\-,[{\r\n?])[ \t]*(?:<<prop>>[ \t]+)?)<<key>>(?=\s*:\s)/.source.replace(/<<prop>>/g, function () {
        return properties;
      }).replace(/<<key>>/g, function () {
        return '(?:' + plainKey + '|' + string + ')';
      })),
      lookbehind: true,
      greedy: true,
      alias: 'atrule'
    },
    'directive': {
      pattern: /(^[ \t]*)%.+/m,
      lookbehind: true,
      alias: 'important'
    },
    'datetime': {
      pattern: createValuePattern(/\d{4}-\d\d?-\d\d?(?:[tT]|[ \t]+)\d\d?:\d{2}:\d{2}(?:\.\d*)?(?:[ \t]*(?:Z|[-+]\d\d?(?::\d{2})?))?|\d{4}-\d{2}-\d{2}|\d\d?:\d{2}(?::\d{2}(?:\.\d*)?)?/.source),
      lookbehind: true,
      alias: 'number'
    },
    'boolean': {
      pattern: createValuePattern(/false|true/.source, 'i'),
      lookbehind: true,
      alias: 'important'
    },
    'null': {
      pattern: createValuePattern(/null|~/.source, 'i'),
      lookbehind: true,
      alias: 'important'
    },
    'string': {
      pattern: createValuePattern(string),
      lookbehind: true,
      greedy: true
    },
    'number': {
      pattern: createValuePattern(/[+-]?(?:0x[\da-f]+|0o[0-7]+|(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?|\.inf|\.nan)/.source, 'i'),
      lookbehind: true
    },
    'tag': tag,
    'important': anchorOrAlias,
    'punctuation': /---|[:[\]{}\-,|>?]|\.\.\./
  };
  Prism.languages.yml = Prism.languages.yaml;
})(Prism);

(function () {
  if (typeof Prism === 'undefined' || typeof document === 'undefined') {
    return;
  }

  /**
   * Plugin name which is used as a class name for <pre> which is activating the plugin
   *
   * @type {string}
   */
  var PLUGIN_NAME = 'line-numbers';

  /**
   * Regular expression used for determining line breaks
   *
   * @type {RegExp}
   */
  var NEW_LINE_EXP = /\n(?!$)/g;

  /**
   * Global exports
   */
  var config = Prism.plugins.lineNumbers = {
    /**
     * Get node for provided line number
     *
     * @param {Element} element pre element
     * @param {number} number line number
     * @returns {Element|undefined}
     */
    getLine: function (element, number) {
      if (element.tagName !== 'PRE' || !element.classList.contains(PLUGIN_NAME)) {
        return;
      }
      var lineNumberRows = element.querySelector('.line-numbers-rows');
      if (!lineNumberRows) {
        return;
      }
      var lineNumberStart = parseInt(element.getAttribute('data-start'), 10) || 1;
      var lineNumberEnd = lineNumberStart + (lineNumberRows.children.length - 1);
      if (number < lineNumberStart) {
        number = lineNumberStart;
      }
      if (number > lineNumberEnd) {
        number = lineNumberEnd;
      }
      var lineIndex = number - lineNumberStart;
      return lineNumberRows.children[lineIndex];
    },
    /**
     * Resizes the line numbers of the given element.
     *
     * This function will not add line numbers. It will only resize existing ones.
     *
     * @param {HTMLElement} element A `<pre>` element with line numbers.
     * @returns {void}
     */
    resize: function (element) {
      resizeElements([element]);
    },
    /**
     * Whether the plugin can assume that the units font sizes and margins are not depended on the size of
     * the current viewport.
     *
     * Setting this to `true` will allow the plugin to do certain optimizations for better performance.
     *
     * Set this to `false` if you use any of the following CSS units: `vh`, `vw`, `vmin`, `vmax`.
     *
     * @type {boolean}
     */
    assumeViewportIndependence: true
  };

  /**
   * Resizes the given elements.
   *
   * @param {HTMLElement[]} elements
   */
  function resizeElements(elements) {
    elements = elements.filter(function (e) {
      var codeStyles = getStyles(e);
      var whiteSpace = codeStyles['white-space'];
      return whiteSpace === 'pre-wrap' || whiteSpace === 'pre-line';
    });
    if (elements.length == 0) {
      return;
    }
    var infos = elements.map(function (element) {
      var codeElement = element.querySelector('code');
      var lineNumbersWrapper = element.querySelector('.line-numbers-rows');
      if (!codeElement || !lineNumbersWrapper) {
        return undefined;
      }

      /** @type {HTMLElement} */
      var lineNumberSizer = element.querySelector('.line-numbers-sizer');
      var codeLines = codeElement.textContent.split(NEW_LINE_EXP);
      if (!lineNumberSizer) {
        lineNumberSizer = document.createElement('span');
        lineNumberSizer.className = 'line-numbers-sizer';
        codeElement.appendChild(lineNumberSizer);
      }
      lineNumberSizer.innerHTML = '0';
      lineNumberSizer.style.display = 'block';
      var oneLinerHeight = lineNumberSizer.getBoundingClientRect().height;
      lineNumberSizer.innerHTML = '';
      return {
        element: element,
        lines: codeLines,
        lineHeights: [],
        oneLinerHeight: oneLinerHeight,
        sizer: lineNumberSizer
      };
    }).filter(Boolean);
    infos.forEach(function (info) {
      var lineNumberSizer = info.sizer;
      var lines = info.lines;
      var lineHeights = info.lineHeights;
      var oneLinerHeight = info.oneLinerHeight;
      lineHeights[lines.length - 1] = undefined;
      lines.forEach(function (line, index) {
        if (line && line.length > 1) {
          var e = lineNumberSizer.appendChild(document.createElement('span'));
          e.style.display = 'block';
          e.textContent = line;
        } else {
          lineHeights[index] = oneLinerHeight;
        }
      });
    });
    infos.forEach(function (info) {
      var lineNumberSizer = info.sizer;
      var lineHeights = info.lineHeights;
      var childIndex = 0;
      for (var i = 0; i < lineHeights.length; i++) {
        if (lineHeights[i] === undefined) {
          lineHeights[i] = lineNumberSizer.children[childIndex++].getBoundingClientRect().height;
        }
      }
    });
    infos.forEach(function (info) {
      var lineNumberSizer = info.sizer;
      var wrapper = info.element.querySelector('.line-numbers-rows');
      lineNumberSizer.style.display = 'none';
      lineNumberSizer.innerHTML = '';
      info.lineHeights.forEach(function (height, lineNumber) {
        wrapper.children[lineNumber].style.height = height + 'px';
      });
    });
  }

  /**
   * Returns style declarations for the element
   *
   * @param {Element} element
   */
  function getStyles(element) {
    if (!element) {
      return null;
    }
    return window.getComputedStyle ? getComputedStyle(element) : element.currentStyle || null;
  }
  var lastWidth = undefined;
  window.addEventListener('resize', function () {
    if (config.assumeViewportIndependence && lastWidth === window.innerWidth) {
      return;
    }
    lastWidth = window.innerWidth;
    resizeElements(Array.prototype.slice.call(document.querySelectorAll('pre.' + PLUGIN_NAME)));
  });
  Prism.hooks.add('complete', function (env) {
    if (!env.code) {
      return;
    }
    var code = /** @type {Element} */env.element;
    var pre = /** @type {HTMLElement} */code.parentNode;

    // works only for <code> wrapped inside <pre> (not inline)
    if (!pre || !/pre/i.test(pre.nodeName)) {
      return;
    }

    // Abort if line numbers already exists
    if (code.querySelector('.line-numbers-rows')) {
      return;
    }

    // only add line numbers if <code> or one of its ancestors has the `line-numbers` class
    if (!Prism.util.isActive(code, PLUGIN_NAME)) {
      return;
    }

    // Remove the class 'line-numbers' from the <code>
    code.classList.remove(PLUGIN_NAME);
    // Add the class 'line-numbers' to the <pre>
    pre.classList.add(PLUGIN_NAME);
    var match = env.code.match(NEW_LINE_EXP);
    var linesNum = match ? match.length + 1 : 1;
    var lineNumbersWrapper;
    var lines = new Array(linesNum + 1).join('<span></span>');
    lineNumbersWrapper = document.createElement('span');
    lineNumbersWrapper.setAttribute('aria-hidden', 'true');
    lineNumbersWrapper.className = 'line-numbers-rows';
    lineNumbersWrapper.innerHTML = lines;
    if (pre.hasAttribute('data-start')) {
      pre.style.counterReset = 'linenumber ' + (parseInt(pre.getAttribute('data-start'), 10) - 1);
    }
    env.element.appendChild(lineNumbersWrapper);
    resizeElements([pre]);
    Prism.hooks.run('line-numbers', env);
  });
  Prism.hooks.add('line-numbers', function (env) {
    env.plugins = env.plugins || {};
    env.plugins.lineNumbers = true;
  });
})();

(function () {
  if (typeof Prism === 'undefined' || typeof document === 'undefined') {
    return;
  }
  var callbacks = [];
  var map = {};
  var noop = function () {};
  Prism.plugins.toolbar = {};

  /**
   * @typedef ButtonOptions
   * @property {string} text The text displayed.
   * @property {string} [url] The URL of the link which will be created.
   * @property {Function} [onClick] The event listener for the `click` event of the created button.
   * @property {string} [className] The class attribute to include with element.
   */

  /**
   * Register a button callback with the toolbar.
   *
   * @param {string} key
   * @param {ButtonOptions|Function} opts
   */
  var registerButton = Prism.plugins.toolbar.registerButton = function (key, opts) {
    var callback;
    if (typeof opts === 'function') {
      callback = opts;
    } else {
      callback = function (env) {
        var element;
        if (typeof opts.onClick === 'function') {
          element = document.createElement('button');
          element.type = 'button';
          element.addEventListener('click', function () {
            opts.onClick.call(this, env);
          });
        } else if (typeof opts.url === 'string') {
          element = document.createElement('a');
          element.href = opts.url;
        } else {
          element = document.createElement('span');
        }
        if (opts.className) {
          element.classList.add(opts.className);
        }
        element.textContent = opts.text;
        return element;
      };
    }
    if (key in map) {
      console.warn('There is a button with the key "' + key + '" registered already.');
      return;
    }
    callbacks.push(map[key] = callback);
  };

  /**
   * Returns the callback order of the given element.
   *
   * @param {HTMLElement} element
   * @returns {string[] | undefined}
   */
  function getOrder(element) {
    while (element) {
      var order = element.getAttribute('data-toolbar-order');
      if (order != null) {
        order = order.trim();
        if (order.length) {
          return order.split(/\s*,\s*/g);
        } else {
          return [];
        }
      }
      element = element.parentElement;
    }
  }

  /**
   * Post-highlight Prism hook callback.
   *
   * @param env
   */
  var hook = Prism.plugins.toolbar.hook = function (env) {
    // Check if inline or actual code block (credit to line-numbers plugin)
    var pre = env.element.parentNode;
    if (!pre || !/pre/i.test(pre.nodeName)) {
      return;
    }

    // Autoloader rehighlights, so only do this once.
    if (pre.parentNode.classList.contains('code-toolbar')) {
      return;
    }

    // Create wrapper for <pre> to prevent scrolling toolbar with content
    var wrapper = document.createElement('div');
    wrapper.classList.add('code-toolbar');
    pre.parentNode.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    // Setup the toolbar
    var toolbar = document.createElement('div');
    toolbar.classList.add('toolbar');

    // order callbacks
    var elementCallbacks = callbacks;
    var order = getOrder(env.element);
    if (order) {
      elementCallbacks = order.map(function (key) {
        return map[key] || noop;
      });
    }
    elementCallbacks.forEach(function (callback) {
      var element = callback(env);
      if (!element) {
        return;
      }
      var item = document.createElement('div');
      item.classList.add('toolbar-item');
      item.appendChild(element);
      toolbar.appendChild(item);
    });

    // Add our toolbar to the currently created wrapper of <pre> tag
    wrapper.appendChild(toolbar);
  };
  registerButton('label', function (env) {
    var pre = env.element.parentNode;
    if (!pre || !/pre/i.test(pre.nodeName)) {
      return;
    }
    if (!pre.hasAttribute('data-label')) {
      return;
    }
    var element;
    var template;
    var text = pre.getAttribute('data-label');
    try {
      // Any normal text will blow up this selector.
      template = document.querySelector('template#' + text);
    } catch (e) {/* noop */}
    if (template) {
      element = template.content;
    } else {
      if (pre.hasAttribute('data-url')) {
        element = document.createElement('a');
        element.href = pre.getAttribute('data-url');
      } else {
        element = document.createElement('span');
      }
      element.textContent = text;
    }
    return element;
  });

  /**
   * Register the toolbar with Prism.
   */
  Prism.hooks.add('complete', hook);
})();

(function () {
  if (typeof Prism === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (!Prism.plugins.toolbar) {
    console.warn('Show Languages plugin loaded before Toolbar plugin.');
    return;
  }

  /* eslint-disable */

  // The languages map is built automatically with gulp
  var Languages = /*languages_placeholder[*/{
    "none": "Plain text",
    "plain": "Plain text",
    "plaintext": "Plain text",
    "text": "Plain text",
    "txt": "Plain text",
    "html": "HTML",
    "xml": "XML",
    "svg": "SVG",
    "mathml": "MathML",
    "ssml": "SSML",
    "rss": "RSS",
    "css": "CSS",
    "clike": "C-like",
    "js": "JavaScript",
    "abap": "ABAP",
    "abnf": "ABNF",
    "al": "AL",
    "antlr4": "ANTLR4",
    "g4": "ANTLR4",
    "apacheconf": "Apache Configuration",
    "apl": "APL",
    "aql": "AQL",
    "ino": "Arduino",
    "arff": "ARFF",
    "armasm": "ARM Assembly",
    "arm-asm": "ARM Assembly",
    "art": "Arturo",
    "asciidoc": "AsciiDoc",
    "adoc": "AsciiDoc",
    "aspnet": "ASP.NET (C#)",
    "asm6502": "6502 Assembly",
    "asmatmel": "Atmel AVR Assembly",
    "autohotkey": "AutoHotkey",
    "autoit": "AutoIt",
    "avisynth": "AviSynth",
    "avs": "AviSynth",
    "avro-idl": "Avro IDL",
    "avdl": "Avro IDL",
    "awk": "AWK",
    "gawk": "GAWK",
    "sh": "Shell",
    "basic": "BASIC",
    "bbcode": "BBcode",
    "bbj": "BBj",
    "bnf": "BNF",
    "rbnf": "RBNF",
    "bqn": "BQN",
    "bsl": "BSL (1C:Enterprise)",
    "oscript": "OneScript",
    "csharp": "C#",
    "cs": "C#",
    "dotnet": "C#",
    "cpp": "C++",
    "cfscript": "CFScript",
    "cfc": "CFScript",
    "cil": "CIL",
    "cilkc": "Cilk/C",
    "cilk-c": "Cilk/C",
    "cilkcpp": "Cilk/C++",
    "cilk-cpp": "Cilk/C++",
    "cilk": "Cilk/C++",
    "cmake": "CMake",
    "cobol": "COBOL",
    "coffee": "CoffeeScript",
    "conc": "Concurnas",
    "csp": "Content-Security-Policy",
    "css-extras": "CSS Extras",
    "csv": "CSV",
    "cue": "CUE",
    "dataweave": "DataWeave",
    "dax": "DAX",
    "django": "Django/Jinja2",
    "jinja2": "Django/Jinja2",
    "dns-zone-file": "DNS zone file",
    "dns-zone": "DNS zone file",
    "dockerfile": "Docker",
    "dot": "DOT (Graphviz)",
    "gv": "DOT (Graphviz)",
    "ebnf": "EBNF",
    "editorconfig": "EditorConfig",
    "ejs": "EJS",
    "etlua": "Embedded Lua templating",
    "erb": "ERB",
    "excel-formula": "Excel Formula",
    "xlsx": "Excel Formula",
    "xls": "Excel Formula",
    "fsharp": "F#",
    "firestore-security-rules": "Firestore security rules",
    "ftl": "FreeMarker Template Language",
    "gml": "GameMaker Language",
    "gamemakerlanguage": "GameMaker Language",
    "gap": "GAP (CAS)",
    "gcode": "G-code",
    "gdscript": "GDScript",
    "gedcom": "GEDCOM",
    "gettext": "gettext",
    "po": "gettext",
    "glsl": "GLSL",
    "gn": "GN",
    "gni": "GN",
    "linker-script": "GNU Linker Script",
    "ld": "GNU Linker Script",
    "go-module": "Go module",
    "go-mod": "Go module",
    "graphql": "GraphQL",
    "hbs": "Handlebars",
    "hs": "Haskell",
    "hcl": "HCL",
    "hlsl": "HLSL",
    "http": "HTTP",
    "hpkp": "HTTP Public-Key-Pins",
    "hsts": "HTTP Strict-Transport-Security",
    "ichigojam": "IchigoJam",
    "icu-message-format": "ICU Message Format",
    "idr": "Idris",
    "ignore": ".ignore",
    "gitignore": ".gitignore",
    "hgignore": ".hgignore",
    "npmignore": ".npmignore",
    "inform7": "Inform 7",
    "javadoc": "JavaDoc",
    "javadoclike": "JavaDoc-like",
    "javastacktrace": "Java stack trace",
    "jq": "JQ",
    "jsdoc": "JSDoc",
    "js-extras": "JS Extras",
    "json": "JSON",
    "webmanifest": "Web App Manifest",
    "json5": "JSON5",
    "jsonp": "JSONP",
    "jsstacktrace": "JS stack trace",
    "js-templates": "JS Templates",
    "keepalived": "Keepalived Configure",
    "kts": "Kotlin Script",
    "kt": "Kotlin",
    "kumir": "KuMir (КуМир)",
    "kum": "KuMir (КуМир)",
    "latex": "LaTeX",
    "tex": "TeX",
    "context": "ConTeXt",
    "lilypond": "LilyPond",
    "ly": "LilyPond",
    "emacs": "Lisp",
    "elisp": "Lisp",
    "emacs-lisp": "Lisp",
    "llvm": "LLVM IR",
    "log": "Log file",
    "lolcode": "LOLCODE",
    "magma": "Magma (CAS)",
    "md": "Markdown",
    "markup-templating": "Markup templating",
    "matlab": "MATLAB",
    "maxscript": "MAXScript",
    "mel": "MEL",
    "metafont": "METAFONT",
    "mongodb": "MongoDB",
    "moon": "MoonScript",
    "n1ql": "N1QL",
    "n4js": "N4JS",
    "n4jsd": "N4JS",
    "nand2tetris-hdl": "Nand To Tetris HDL",
    "naniscript": "Naninovel Script",
    "nani": "Naninovel Script",
    "nasm": "NASM",
    "neon": "NEON",
    "nginx": "nginx",
    "nsis": "NSIS",
    "objectivec": "Objective-C",
    "objc": "Objective-C",
    "ocaml": "OCaml",
    "opencl": "OpenCL",
    "openqasm": "OpenQasm",
    "qasm": "OpenQasm",
    "parigp": "PARI/GP",
    "objectpascal": "Object Pascal",
    "psl": "PATROL Scripting Language",
    "pcaxis": "PC-Axis",
    "px": "PC-Axis",
    "peoplecode": "PeopleCode",
    "pcode": "PeopleCode",
    "php": "PHP",
    "phpdoc": "PHPDoc",
    "php-extras": "PHP Extras",
    "plant-uml": "PlantUML",
    "plantuml": "PlantUML",
    "plsql": "PL/SQL",
    "powerquery": "PowerQuery",
    "pq": "PowerQuery",
    "mscript": "PowerQuery",
    "powershell": "PowerShell",
    "promql": "PromQL",
    "properties": ".properties",
    "protobuf": "Protocol Buffers",
    "purebasic": "PureBasic",
    "pbfasm": "PureBasic",
    "purs": "PureScript",
    "py": "Python",
    "qsharp": "Q#",
    "qs": "Q#",
    "q": "Q (kdb+ database)",
    "qml": "QML",
    "rkt": "Racket",
    "cshtml": "Razor C#",
    "razor": "Razor C#",
    "jsx": "React JSX",
    "tsx": "React TSX",
    "renpy": "Ren'py",
    "rpy": "Ren'py",
    "res": "ReScript",
    "rest": "reST (reStructuredText)",
    "robotframework": "Robot Framework",
    "robot": "Robot Framework",
    "rb": "Ruby",
    "sas": "SAS",
    "sass": "Sass (Sass)",
    "scss": "Sass (SCSS)",
    "shell-session": "Shell session",
    "sh-session": "Shell session",
    "shellsession": "Shell session",
    "sml": "SML",
    "smlnj": "SML/NJ",
    "solidity": "Solidity (Ethereum)",
    "sol": "Solidity (Ethereum)",
    "solution-file": "Solution file",
    "sln": "Solution file",
    "soy": "Soy (Closure Template)",
    "sparql": "SPARQL",
    "rq": "SPARQL",
    "splunk-spl": "Splunk SPL",
    "sqf": "SQF: Status Quo Function (Arma 3)",
    "sql": "SQL",
    "stata": "Stata Ado",
    "iecst": "Structured Text (IEC 61131-3)",
    "supercollider": "SuperCollider",
    "sclang": "SuperCollider",
    "systemd": "Systemd configuration file",
    "t4-templating": "T4 templating",
    "t4-cs": "T4 Text Templates (C#)",
    "t4": "T4 Text Templates (C#)",
    "t4-vb": "T4 Text Templates (VB)",
    "tap": "TAP",
    "tt2": "Template Toolkit 2",
    "toml": "TOML",
    "trickle": "trickle",
    "troy": "troy",
    "trig": "TriG",
    "ts": "TypeScript",
    "tsconfig": "TSConfig",
    "uscript": "UnrealScript",
    "uc": "UnrealScript",
    "uorazor": "UO Razor Script",
    "uri": "URI",
    "url": "URL",
    "vbnet": "VB.Net",
    "vhdl": "VHDL",
    "vim": "vim",
    "visual-basic": "Visual Basic",
    "vba": "VBA",
    "vb": "Visual Basic",
    "wasm": "WebAssembly",
    "web-idl": "Web IDL",
    "webidl": "Web IDL",
    "wgsl": "WGSL",
    "wiki": "Wiki markup",
    "wolfram": "Wolfram language",
    "nb": "Mathematica Notebook",
    "wl": "Wolfram language",
    "xeoracube": "XeoraCube",
    "xml-doc": "XML doc (.net)",
    "xojo": "Xojo (REALbasic)",
    "xquery": "XQuery",
    "yaml": "YAML",
    "yml": "YAML",
    "yang": "YANG"
  } /*]*/;

  /* eslint-enable */

  Prism.plugins.toolbar.registerButton('show-language', function (env) {
    var pre = env.element.parentNode;
    if (!pre || !/pre/i.test(pre.nodeName)) {
      return;
    }

    /**
     * Tries to guess the name of a language given its id.
     *
     * @param {string} id The language id.
     * @returns {string}
     */
    function guessTitle(id) {
      if (!id) {
        return id;
      }
      return (id.substring(0, 1).toUpperCase() + id.substring(1)).replace(/s(?=cript)/, 'S');
    }
    var language = pre.getAttribute('data-language') || Languages[env.language] || guessTitle(env.language);
    if (!language) {
      return;
    }
    var element = document.createElement('span');
    element.textContent = language;
    return element;
  });
})();

(function () {
  if (typeof Prism === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (!Prism.plugins.toolbar) {
    console.warn('Copy to Clipboard plugin loaded before Toolbar plugin.');
    return;
  }

  /**
   * When the given elements is clicked by the user, the given text will be copied to clipboard.
   *
   * @param {HTMLElement} element
   * @param {CopyInfo} copyInfo
   *
   * @typedef CopyInfo
   * @property {() => string} getText
   * @property {() => void} success
   * @property {(reason: unknown) => void} error
   */
  function registerClipboard(element, copyInfo) {
    element.addEventListener('click', function () {
      copyTextToClipboard(copyInfo);
    });
  }

  // https://stackoverflow.com/a/30810322/7595472

  /** @param {CopyInfo} copyInfo */
  function fallbackCopyTextToClipboard(copyInfo) {
    var textArea = document.createElement('textarea');
    textArea.value = copyInfo.getText();

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      var successful = document.execCommand('copy');
      setTimeout(function () {
        if (successful) {
          copyInfo.success();
        } else {
          copyInfo.error();
        }
      }, 1);
    } catch (err) {
      setTimeout(function () {
        copyInfo.error(err);
      }, 1);
    }
    document.body.removeChild(textArea);
  }
  /** @param {CopyInfo} copyInfo */
  function copyTextToClipboard(copyInfo) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(copyInfo.getText()).then(copyInfo.success, function () {
        // try the fallback in case `writeText` didn't work
        fallbackCopyTextToClipboard(copyInfo);
      });
    } else {
      fallbackCopyTextToClipboard(copyInfo);
    }
  }

  /**
   * Selects the text content of the given element.
   *
   * @param {Element} element
   */
  function selectElementText(element) {
    // https://stackoverflow.com/a/20079910/7595472
    window.getSelection().selectAllChildren(element);
  }

  /**
   * Traverses up the DOM tree to find data attributes that override the default plugin settings.
   *
   * @param {Element} startElement An element to start from.
   * @returns {Settings} The plugin settings.
   * @typedef {Record<"copy" | "copy-error" | "copy-success" | "copy-timeout", string | number>} Settings
   */
  function getSettings(startElement) {
    /** @type {Settings} */
    var settings = {
      'copy': 'Copy',
      'copy-error': 'Press Ctrl+C to copy',
      'copy-success': 'Copied!',
      'copy-timeout': 5000
    };
    var prefix = 'data-prismjs-';
    for (var key in settings) {
      var attr = prefix + key;
      var element = startElement;
      while (element && !element.hasAttribute(attr)) {
        element = element.parentElement;
      }
      if (element) {
        settings[key] = element.getAttribute(attr);
      }
    }
    return settings;
  }
  Prism.plugins.toolbar.registerButton('copy-to-clipboard', function (env) {
    var element = env.element;
    var settings = getSettings(element);
    var linkCopy = document.createElement('button');
    linkCopy.className = 'copy-to-clipboard-button';
    linkCopy.setAttribute('type', 'button');
    var linkSpan = document.createElement('span');
    linkCopy.appendChild(linkSpan);
    setState('copy');
    registerClipboard(linkCopy, {
      getText: function () {
        return element.textContent;
      },
      success: function () {
        setState('copy-success');
        resetText();
      },
      error: function () {
        setState('copy-error');
        setTimeout(function () {
          selectElementText(element);
        }, 1);
        resetText();
      }
    });
    return linkCopy;
    function resetText() {
      setTimeout(function () {
        setState('copy');
      }, settings['copy-timeout']);
    }

    /** @param {"copy" | "copy-error" | "copy-success"} state */
    function setState(state) {
      linkSpan.textContent = settings[state];
      linkCopy.setAttribute('data-copy-state', state);
    }
  });
})();

var top = 'top';
var bottom = 'bottom';
var right = 'right';
var left = 'left';
var auto = 'auto';
var basePlacements = [top, bottom, right, left];
var start = 'start';
var end = 'end';
var clippingParents = 'clippingParents';
var viewport = 'viewport';
var popper = 'popper';
var reference$1 = 'reference';
var variationPlacements = /*#__PURE__*/basePlacements.reduce(function (acc, placement) {
  return acc.concat([placement + "-" + start, placement + "-" + end]);
}, []);
var placements = /*#__PURE__*/[].concat(basePlacements, [auto]).reduce(function (acc, placement) {
  return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
}, []); // modifiers that need to read the DOM

var beforeRead = 'beforeRead';
var read = 'read';
var afterRead = 'afterRead'; // pure-logic modifiers

var beforeMain = 'beforeMain';
var main = 'main';
var afterMain = 'afterMain'; // modifier with the purpose to write to the DOM (or write into a framework state)

var beforeWrite = 'beforeWrite';
var write = 'write';
var afterWrite = 'afterWrite';
var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

function getNodeName(element) {
  return element ? (element.nodeName || '').toLowerCase() : null;
}

function getWindow(node) {
  if (node == null) {
    return window;
  }
  if (node.toString() !== '[object Window]') {
    var ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }
  return node;
}

function isElement$1(node) {
  var OwnElement = getWindow(node).Element;
  return node instanceof OwnElement || node instanceof Element;
}
function isHTMLElement$1(node) {
  var OwnElement = getWindow(node).HTMLElement;
  return node instanceof OwnElement || node instanceof HTMLElement;
}
function isShadowRoot(node) {
  // IE 11 has no ShadowRoot
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  var OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

// and applies them to the HTMLElements such as popper and arrow

function applyStyles(_ref) {
  var state = _ref.state;
  Object.keys(state.elements).forEach(function (name) {
    var style = state.styles[name] || {};
    var attributes = state.attributes[name] || {};
    var element = state.elements[name]; // arrow is optional + virtual elements

    if (!isHTMLElement$1(element) || !getNodeName(element)) {
      return;
    } // Flow doesn't support to extend this property, but it's the most
    // effective way to apply styles to an HTMLElement
    // $FlowFixMe[cannot-write]

    Object.assign(element.style, style);
    Object.keys(attributes).forEach(function (name) {
      var value = attributes[name];
      if (value === false) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value === true ? '' : value);
      }
    });
  });
}
function effect$2(_ref2) {
  var state = _ref2.state;
  var initialStyles = {
    popper: {
      position: state.options.strategy,
      left: '0',
      top: '0',
      margin: '0'
    },
    arrow: {
      position: 'absolute'
    },
    reference: {}
  };
  Object.assign(state.elements.popper.style, initialStyles.popper);
  state.styles = initialStyles;
  if (state.elements.arrow) {
    Object.assign(state.elements.arrow.style, initialStyles.arrow);
  }
  return function () {
    Object.keys(state.elements).forEach(function (name) {
      var element = state.elements[name];
      var attributes = state.attributes[name] || {};
      var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]); // Set all values to an empty string to unset them

      var style = styleProperties.reduce(function (style, property) {
        style[property] = '';
        return style;
      }, {}); // arrow is optional + virtual elements

      if (!isHTMLElement$1(element) || !getNodeName(element)) {
        return;
      }
      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (attribute) {
        element.removeAttribute(attribute);
      });
    });
  };
} // eslint-disable-next-line import/no-unused-modules

var applyStyles$1 = {
  name: 'applyStyles',
  enabled: true,
  phase: 'write',
  fn: applyStyles,
  effect: effect$2,
  requires: ['computeStyles']
};

function getBasePlacement$1(placement) {
  return placement.split('-')[0];
}

var max$2 = Math.max;
var min$2 = Math.min;
var round$1 = Math.round;

function getUAString() {
  var uaData = navigator.userAgentData;
  if (uaData != null && uaData.brands && Array.isArray(uaData.brands)) {
    return uaData.brands.map(function (item) {
      return item.brand + "/" + item.version;
    }).join(' ');
  }
  return navigator.userAgent;
}

function isLayoutViewport() {
  return !/^((?!chrome|android).)*safari/i.test(getUAString());
}

function getBoundingClientRect$1(element, includeScale, isFixedStrategy) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  var clientRect = element.getBoundingClientRect();
  var scaleX = 1;
  var scaleY = 1;
  if (includeScale && isHTMLElement$1(element)) {
    scaleX = element.offsetWidth > 0 ? round$1(clientRect.width) / element.offsetWidth || 1 : 1;
    scaleY = element.offsetHeight > 0 ? round$1(clientRect.height) / element.offsetHeight || 1 : 1;
  }
  var _ref = isElement$1(element) ? getWindow(element) : window,
    visualViewport = _ref.visualViewport;
  var addVisualOffsets = !isLayoutViewport() && isFixedStrategy;
  var x = (clientRect.left + (addVisualOffsets && visualViewport ? visualViewport.offsetLeft : 0)) / scaleX;
  var y = (clientRect.top + (addVisualOffsets && visualViewport ? visualViewport.offsetTop : 0)) / scaleY;
  var width = clientRect.width / scaleX;
  var height = clientRect.height / scaleY;
  return {
    width: width,
    height: height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x,
    x: x,
    y: y
  };
}

// means it doesn't take into account transforms.

function getLayoutRect(element) {
  var clientRect = getBoundingClientRect$1(element); // Use the clientRect sizes if it's not been transformed.
  // Fixes https://github.com/popperjs/popper-core/issues/1223

  var width = element.offsetWidth;
  var height = element.offsetHeight;
  if (Math.abs(clientRect.width - width) <= 1) {
    width = clientRect.width;
  }
  if (Math.abs(clientRect.height - height) <= 1) {
    height = clientRect.height;
  }
  return {
    x: element.offsetLeft,
    y: element.offsetTop,
    width: width,
    height: height
  };
}

function contains$1(parent, child) {
  var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

  if (parent.contains(child)) {
    return true;
  } // then fallback to custom implementation with Shadow DOM support
  else if (rootNode && isShadowRoot(rootNode)) {
    var next = child;
    do {
      if (next && parent.isSameNode(next)) {
        return true;
      } // $FlowFixMe[prop-missing]: need a better way to handle this...

      next = next.parentNode || next.host;
    } while (next);
  } // Give up, the result is false

  return false;
}

function getComputedStyle$1(element) {
  return getWindow(element).getComputedStyle(element);
}

function isTableElement(element) {
  return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
}

function getDocumentElement(element) {
  // $FlowFixMe[incompatible-return]: assume body is always available
  return ((isElement$1(element) ? element.ownerDocument :
  // $FlowFixMe[prop-missing]
  element.document) || window.document).documentElement;
}

function getParentNode(element) {
  if (getNodeName(element) === 'html') {
    return element;
  }
  return (
    // this is a quicker (but less type safe) way to save quite some bytes from the bundle
    // $FlowFixMe[incompatible-return]
    // $FlowFixMe[prop-missing]
    element.assignedSlot ||
    // step into the shadow DOM of the parent of a slotted node
    element.parentNode || (
    // DOM Element detected
    isShadowRoot(element) ? element.host : null) ||
    // ShadowRoot detected
    // $FlowFixMe[incompatible-call]: HTMLElement is a Node
    getDocumentElement(element) // fallback
  );
}

function getTrueOffsetParent(element) {
  if (!isHTMLElement$1(element) ||
  // https://github.com/popperjs/popper-core/issues/837
  getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  return element.offsetParent;
} // `.offsetParent` reports `null` for fixed elements, while absolute elements
// return the containing block

function getContainingBlock(element) {
  var isFirefox = /firefox/i.test(getUAString());
  var isIE = /Trident/i.test(getUAString());
  if (isIE && isHTMLElement$1(element)) {
    // In IE 9, 10 and 11 fixed elements containing block is always established by the viewport
    var elementCss = getComputedStyle$1(element);
    if (elementCss.position === 'fixed') {
      return null;
    }
  }
  var currentNode = getParentNode(element);
  if (isShadowRoot(currentNode)) {
    currentNode = currentNode.host;
  }
  while (isHTMLElement$1(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
    var css = getComputedStyle$1(currentNode); // This is non-exhaustive but covers the most common CSS properties that
    // create a containing block.
    // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block

    if (css.transform !== 'none' || css.perspective !== 'none' || css.contain === 'paint' || ['transform', 'perspective'].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === 'filter' || isFirefox && css.filter && css.filter !== 'none') {
      return currentNode;
    } else {
      currentNode = currentNode.parentNode;
    }
  }
  return null;
} // Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.

function getOffsetParent(element) {
  var window = getWindow(element);
  var offsetParent = getTrueOffsetParent(element);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent);
  }
  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static')) {
    return window;
  }
  return offsetParent || getContainingBlock(element) || window;
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
}

function within(min, value, max) {
  return max$2(min, min$2(value, max));
}
function withinMaxClamp(min, value, max) {
  var v = within(min, value, max);
  return v > max ? max : v;
}

function getFreshSideObject() {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };
}

function mergePaddingObject(paddingObject) {
  return Object.assign({}, getFreshSideObject(), paddingObject);
}

function expandToHashMap(value, keys) {
  return keys.reduce(function (hashMap, key) {
    hashMap[key] = value;
    return hashMap;
  }, {});
}

var toPaddingObject = function toPaddingObject(padding, state) {
  padding = typeof padding === 'function' ? padding(Object.assign({}, state.rects, {
    placement: state.placement
  })) : padding;
  return mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
};
function arrow(_ref) {
  var _state$modifiersData$;
  var state = _ref.state,
    name = _ref.name,
    options = _ref.options;
  var arrowElement = state.elements.arrow;
  var popperOffsets = state.modifiersData.popperOffsets;
  var basePlacement = getBasePlacement$1(state.placement);
  var axis = getMainAxisFromPlacement(basePlacement);
  var isVertical = [left, right].indexOf(basePlacement) >= 0;
  var len = isVertical ? 'height' : 'width';
  if (!arrowElement || !popperOffsets) {
    return;
  }
  var paddingObject = toPaddingObject(options.padding, state);
  var arrowRect = getLayoutRect(arrowElement);
  var minProp = axis === 'y' ? top : left;
  var maxProp = axis === 'y' ? bottom : right;
  var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
  var startDiff = popperOffsets[axis] - state.rects.reference[axis];
  var arrowOffsetParent = getOffsetParent(arrowElement);
  var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
  var centerToReference = endDiff / 2 - startDiff / 2; // Make sure the arrow doesn't overflow the popper if the center point is
  // outside of the popper bounds

  var min = paddingObject[minProp];
  var max = clientSize - arrowRect[len] - paddingObject[maxProp];
  var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
  var offset = within(min, center, max); // Prevents breaking syntax highlighting...

  var axisProp = axis;
  state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
}
function effect$1(_ref2) {
  var state = _ref2.state,
    options = _ref2.options;
  var _options$element = options.element,
    arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element;
  if (arrowElement == null) {
    return;
  } // CSS selector

  if (typeof arrowElement === 'string') {
    arrowElement = state.elements.popper.querySelector(arrowElement);
    if (!arrowElement) {
      return;
    }
  }
  if (!contains$1(state.elements.popper, arrowElement)) {
    return;
  }
  state.elements.arrow = arrowElement;
} // eslint-disable-next-line import/no-unused-modules

var arrow$1 = {
  name: 'arrow',
  enabled: true,
  phase: 'main',
  fn: arrow,
  effect: effect$1,
  requires: ['popperOffsets'],
  requiresIfExists: ['preventOverflow']
};

function getVariation(placement) {
  return placement.split('-')[1];
}

var unsetSides = {
  top: 'auto',
  right: 'auto',
  bottom: 'auto',
  left: 'auto'
}; // Round the offsets to the nearest suitable subpixel based on the DPR.
// Zooming can change the DPR, but it seems to report a value that will
// cleanly divide the values into the appropriate subpixels.

function roundOffsetsByDPR(_ref, win) {
  var x = _ref.x,
    y = _ref.y;
  var dpr = win.devicePixelRatio || 1;
  return {
    x: round$1(x * dpr) / dpr || 0,
    y: round$1(y * dpr) / dpr || 0
  };
}
function mapToStyles(_ref2) {
  var _Object$assign2;
  var popper = _ref2.popper,
    popperRect = _ref2.popperRect,
    placement = _ref2.placement,
    variation = _ref2.variation,
    offsets = _ref2.offsets,
    position = _ref2.position,
    gpuAcceleration = _ref2.gpuAcceleration,
    adaptive = _ref2.adaptive,
    roundOffsets = _ref2.roundOffsets,
    isFixed = _ref2.isFixed;
  var _offsets$x = offsets.x,
    x = _offsets$x === void 0 ? 0 : _offsets$x,
    _offsets$y = offsets.y,
    y = _offsets$y === void 0 ? 0 : _offsets$y;
  var _ref3 = typeof roundOffsets === 'function' ? roundOffsets({
    x: x,
    y: y
  }) : {
    x: x,
    y: y
  };
  x = _ref3.x;
  y = _ref3.y;
  var hasX = offsets.hasOwnProperty('x');
  var hasY = offsets.hasOwnProperty('y');
  var sideX = left;
  var sideY = top;
  var win = window;
  if (adaptive) {
    var offsetParent = getOffsetParent(popper);
    var heightProp = 'clientHeight';
    var widthProp = 'clientWidth';
    if (offsetParent === getWindow(popper)) {
      offsetParent = getDocumentElement(popper);
      if (getComputedStyle$1(offsetParent).position !== 'static' && position === 'absolute') {
        heightProp = 'scrollHeight';
        widthProp = 'scrollWidth';
      }
    } // $FlowFixMe[incompatible-cast]: force type refinement, we compare offsetParent with window above, but Flow doesn't detect it

    offsetParent = offsetParent;
    if (placement === top || (placement === left || placement === right) && variation === end) {
      sideY = bottom;
      var offsetY = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.height :
      // $FlowFixMe[prop-missing]
      offsetParent[heightProp];
      y -= offsetY - popperRect.height;
      y *= gpuAcceleration ? 1 : -1;
    }
    if (placement === left || (placement === top || placement === bottom) && variation === end) {
      sideX = right;
      var offsetX = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.width :
      // $FlowFixMe[prop-missing]
      offsetParent[widthProp];
      x -= offsetX - popperRect.width;
      x *= gpuAcceleration ? 1 : -1;
    }
  }
  var commonStyles = Object.assign({
    position: position
  }, adaptive && unsetSides);
  var _ref4 = roundOffsets === true ? roundOffsetsByDPR({
    x: x,
    y: y
  }, getWindow(popper)) : {
    x: x,
    y: y
  };
  x = _ref4.x;
  y = _ref4.y;
  if (gpuAcceleration) {
    var _Object$assign;
    return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
  }
  return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
}
function computeStyles(_ref5) {
  var state = _ref5.state,
    options = _ref5.options;
  var _options$gpuAccelerat = options.gpuAcceleration,
    gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
    _options$adaptive = options.adaptive,
    adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
    _options$roundOffsets = options.roundOffsets,
    roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;
  var commonStyles = {
    placement: getBasePlacement$1(state.placement),
    variation: getVariation(state.placement),
    popper: state.elements.popper,
    popperRect: state.rects.popper,
    gpuAcceleration: gpuAcceleration,
    isFixed: state.options.strategy === 'fixed'
  };
  if (state.modifiersData.popperOffsets != null) {
    state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.popperOffsets,
      position: state.options.strategy,
      adaptive: adaptive,
      roundOffsets: roundOffsets
    })));
  }
  if (state.modifiersData.arrow != null) {
    state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.arrow,
      position: 'absolute',
      adaptive: false,
      roundOffsets: roundOffsets
    })));
  }
  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-placement': state.placement
  });
} // eslint-disable-next-line import/no-unused-modules

var computeStyles$1 = {
  name: 'computeStyles',
  enabled: true,
  phase: 'beforeWrite',
  fn: computeStyles,
  data: {}
};

var passive = {
  passive: true
};
function effect(_ref) {
  var state = _ref.state,
    instance = _ref.instance,
    options = _ref.options;
  var _options$scroll = options.scroll,
    scroll = _options$scroll === void 0 ? true : _options$scroll,
    _options$resize = options.resize,
    resize = _options$resize === void 0 ? true : _options$resize;
  var window = getWindow(state.elements.popper);
  var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);
  if (scroll) {
    scrollParents.forEach(function (scrollParent) {
      scrollParent.addEventListener('scroll', instance.update, passive);
    });
  }
  if (resize) {
    window.addEventListener('resize', instance.update, passive);
  }
  return function () {
    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.removeEventListener('scroll', instance.update, passive);
      });
    }
    if (resize) {
      window.removeEventListener('resize', instance.update, passive);
    }
  };
} // eslint-disable-next-line import/no-unused-modules

var eventListeners = {
  name: 'eventListeners',
  enabled: true,
  phase: 'write',
  fn: function fn() {},
  effect: effect,
  data: {}
};

var hash$1 = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash$1[matched];
  });
}

var hash = {
  start: 'end',
  end: 'start'
};
function getOppositeVariationPlacement(placement) {
  return placement.replace(/start|end/g, function (matched) {
    return hash[matched];
  });
}

function getWindowScroll(node) {
  var win = getWindow(node);
  var scrollLeft = win.pageXOffset;
  var scrollTop = win.pageYOffset;
  return {
    scrollLeft: scrollLeft,
    scrollTop: scrollTop
  };
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  // Popper 1 is broken in this case and never had a bug report so let's assume
  // it's not an issue. I don't think anyone ever specifies width on <html>
  // anyway.
  // Browsers where the left scrollbar doesn't cause an issue report `0` for
  // this (e.g. Edge 2019, IE11, Safari)
  return getBoundingClientRect$1(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
}

function getViewportRect(element, strategy) {
  var win = getWindow(element);
  var html = getDocumentElement(element);
  var visualViewport = win.visualViewport;
  var width = html.clientWidth;
  var height = html.clientHeight;
  var x = 0;
  var y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    var layoutViewport = isLayoutViewport();
    if (layoutViewport || !layoutViewport && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width: width,
    height: height,
    x: x + getWindowScrollBarX(element),
    y: y
  };
}

// of the `<html>` and `<body>` rect bounds if horizontally scrollable

function getDocumentRect(element) {
  var _element$ownerDocumen;
  var html = getDocumentElement(element);
  var winScroll = getWindowScroll(element);
  var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
  var width = max$2(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  var height = max$2(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
  var y = -winScroll.scrollTop;
  if (getComputedStyle$1(body || html).direction === 'rtl') {
    x += max$2(html.clientWidth, body ? body.clientWidth : 0) - width;
  }
  return {
    width: width,
    height: height,
    x: x,
    y: y
  };
}

function isScrollParent(element) {
  // Firefox wants us to check `-x` and `-y` variations as well
  var _getComputedStyle = getComputedStyle$1(element),
    overflow = _getComputedStyle.overflow,
    overflowX = _getComputedStyle.overflowX,
    overflowY = _getComputedStyle.overflowY;
  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

function getScrollParent(node) {
  if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
    // $FlowFixMe[incompatible-return]: assume body is always available
    return node.ownerDocument.body;
  }
  if (isHTMLElement$1(node) && isScrollParent(node)) {
    return node;
  }
  return getScrollParent(getParentNode(node));
}

/*
given a DOM element, return the list of all scroll parents, up the list of ancesors
until we get to the top window object. This list is what we attach scroll listeners
to, because if any of these parent elements scroll, we'll need to re-calculate the
reference element's position.
*/

function listScrollParents(element, list) {
  var _element$ownerDocumen;
  if (list === void 0) {
    list = [];
  }
  var scrollParent = getScrollParent(element);
  var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
  var win = getWindow(scrollParent);
  var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
  var updatedList = list.concat(target);
  return isBody ? updatedList :
  // $FlowFixMe[incompatible-call]: isBody tells us target will be an HTMLElement here
  updatedList.concat(listScrollParents(getParentNode(target)));
}

function rectToClientRect(rect) {
  return Object.assign({}, rect, {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  });
}

function getInnerBoundingClientRect(element, strategy) {
  var rect = getBoundingClientRect$1(element, false, strategy === 'fixed');
  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;
  return rect;
}
function getClientRectFromMixedType(element, clippingParent, strategy) {
  return clippingParent === viewport ? rectToClientRect(getViewportRect(element, strategy)) : isElement$1(clippingParent) ? getInnerBoundingClientRect(clippingParent, strategy) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
} // A "clipping parent" is an overflowable container with the characteristic of
// clipping (or hiding) overflowing elements with a position different from
// `initial`

function getClippingParents(element) {
  var clippingParents = listScrollParents(getParentNode(element));
  var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle$1(element).position) >= 0;
  var clipperElement = canEscapeClipping && isHTMLElement$1(element) ? getOffsetParent(element) : element;
  if (!isElement$1(clipperElement)) {
    return [];
  } // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414

  return clippingParents.filter(function (clippingParent) {
    return isElement$1(clippingParent) && contains$1(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
  });
} // Gets the maximum area that the element is visible in due to any number of
// clipping parents

function getClippingRect(element, boundary, rootBoundary, strategy) {
  var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
  var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
  var firstClippingParent = clippingParents[0];
  var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
    var rect = getClientRectFromMixedType(element, clippingParent, strategy);
    accRect.top = max$2(rect.top, accRect.top);
    accRect.right = min$2(rect.right, accRect.right);
    accRect.bottom = min$2(rect.bottom, accRect.bottom);
    accRect.left = max$2(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromMixedType(element, firstClippingParent, strategy));
  clippingRect.width = clippingRect.right - clippingRect.left;
  clippingRect.height = clippingRect.bottom - clippingRect.top;
  clippingRect.x = clippingRect.left;
  clippingRect.y = clippingRect.top;
  return clippingRect;
}

function computeOffsets(_ref) {
  var reference = _ref.reference,
    element = _ref.element,
    placement = _ref.placement;
  var basePlacement = placement ? getBasePlacement$1(placement) : null;
  var variation = placement ? getVariation(placement) : null;
  var commonX = reference.x + reference.width / 2 - element.width / 2;
  var commonY = reference.y + reference.height / 2 - element.height / 2;
  var offsets;
  switch (basePlacement) {
    case top:
      offsets = {
        x: commonX,
        y: reference.y - element.height
      };
      break;
    case bottom:
      offsets = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case right:
      offsets = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case left:
      offsets = {
        x: reference.x - element.width,
        y: commonY
      };
      break;
    default:
      offsets = {
        x: reference.x,
        y: reference.y
      };
  }
  var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;
  if (mainAxis != null) {
    var len = mainAxis === 'y' ? 'height' : 'width';
    switch (variation) {
      case start:
        offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
        break;
      case end:
        offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
        break;
    }
  }
  return offsets;
}

function detectOverflow(state, options) {
  if (options === void 0) {
    options = {};
  }
  var _options = options,
    _options$placement = _options.placement,
    placement = _options$placement === void 0 ? state.placement : _options$placement,
    _options$strategy = _options.strategy,
    strategy = _options$strategy === void 0 ? state.strategy : _options$strategy,
    _options$boundary = _options.boundary,
    boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
    _options$rootBoundary = _options.rootBoundary,
    rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
    _options$elementConte = _options.elementContext,
    elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
    _options$altBoundary = _options.altBoundary,
    altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
    _options$padding = _options.padding,
    padding = _options$padding === void 0 ? 0 : _options$padding;
  var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  var altContext = elementContext === popper ? reference$1 : popper;
  var popperRect = state.rects.popper;
  var element = state.elements[altBoundary ? altContext : elementContext];
  var clippingClientRect = getClippingRect(isElement$1(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary, strategy);
  var referenceClientRect = getBoundingClientRect$1(state.elements.reference);
  var popperOffsets = computeOffsets({
    reference: referenceClientRect,
    element: popperRect,
    strategy: 'absolute',
    placement: placement
  });
  var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
  var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
  // 0 or negative = within the clipping rect

  var overflowOffsets = {
    top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
    bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
    left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
    right: elementClientRect.right - clippingClientRect.right + paddingObject.right
  };
  var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

  if (elementContext === popper && offsetData) {
    var offset = offsetData[placement];
    Object.keys(overflowOffsets).forEach(function (key) {
      var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
      var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
      overflowOffsets[key] += offset[axis] * multiply;
    });
  }
  return overflowOffsets;
}

function computeAutoPlacement(state, options) {
  if (options === void 0) {
    options = {};
  }
  var _options = options,
    placement = _options.placement,
    boundary = _options.boundary,
    rootBoundary = _options.rootBoundary,
    padding = _options.padding,
    flipVariations = _options.flipVariations,
    _options$allowedAutoP = _options.allowedAutoPlacements,
    allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
  var variation = getVariation(placement);
  var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
    return getVariation(placement) === variation;
  }) : basePlacements;
  var allowedPlacements = placements$1.filter(function (placement) {
    return allowedAutoPlacements.indexOf(placement) >= 0;
  });
  if (allowedPlacements.length === 0) {
    allowedPlacements = placements$1;
  } // $FlowFixMe[incompatible-type]: Flow seems to have problems with two array unions...

  var overflows = allowedPlacements.reduce(function (acc, placement) {
    acc[placement] = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding
    })[getBasePlacement$1(placement)];
    return acc;
  }, {});
  return Object.keys(overflows).sort(function (a, b) {
    return overflows[a] - overflows[b];
  });
}

function getExpandedFallbackPlacements(placement) {
  if (getBasePlacement$1(placement) === auto) {
    return [];
  }
  var oppositePlacement = getOppositePlacement(placement);
  return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
}
function flip$1(_ref) {
  var state = _ref.state,
    options = _ref.options,
    name = _ref.name;
  if (state.modifiersData[name]._skip) {
    return;
  }
  var _options$mainAxis = options.mainAxis,
    checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
    _options$altAxis = options.altAxis,
    checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
    specifiedFallbackPlacements = options.fallbackPlacements,
    padding = options.padding,
    boundary = options.boundary,
    rootBoundary = options.rootBoundary,
    altBoundary = options.altBoundary,
    _options$flipVariatio = options.flipVariations,
    flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
    allowedAutoPlacements = options.allowedAutoPlacements;
  var preferredPlacement = state.options.placement;
  var basePlacement = getBasePlacement$1(preferredPlacement);
  var isBasePlacement = basePlacement === preferredPlacement;
  var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
  var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
    return acc.concat(getBasePlacement$1(placement) === auto ? computeAutoPlacement(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      flipVariations: flipVariations,
      allowedAutoPlacements: allowedAutoPlacements
    }) : placement);
  }, []);
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var checksMap = new Map();
  var makeFallbackChecks = true;
  var firstFittingPlacement = placements[0];
  for (var i = 0; i < placements.length; i++) {
    var placement = placements[i];
    var _basePlacement = getBasePlacement$1(placement);
    var isStartVariation = getVariation(placement) === start;
    var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
    var len = isVertical ? 'width' : 'height';
    var overflow = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      altBoundary: altBoundary,
      padding: padding
    });
    var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;
    if (referenceRect[len] > popperRect[len]) {
      mainVariationSide = getOppositePlacement(mainVariationSide);
    }
    var altVariationSide = getOppositePlacement(mainVariationSide);
    var checks = [];
    if (checkMainAxis) {
      checks.push(overflow[_basePlacement] <= 0);
    }
    if (checkAltAxis) {
      checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
    }
    if (checks.every(function (check) {
      return check;
    })) {
      firstFittingPlacement = placement;
      makeFallbackChecks = false;
      break;
    }
    checksMap.set(placement, checks);
  }
  if (makeFallbackChecks) {
    // `2` may be desired in some cases – research later
    var numberOfChecks = flipVariations ? 3 : 1;
    var _loop = function _loop(_i) {
      var fittingPlacement = placements.find(function (placement) {
        var checks = checksMap.get(placement);
        if (checks) {
          return checks.slice(0, _i).every(function (check) {
            return check;
          });
        }
      });
      if (fittingPlacement) {
        firstFittingPlacement = fittingPlacement;
        return "break";
      }
    };
    for (var _i = numberOfChecks; _i > 0; _i--) {
      var _ret = _loop(_i);
      if (_ret === "break") break;
    }
  }
  if (state.placement !== firstFittingPlacement) {
    state.modifiersData[name]._skip = true;
    state.placement = firstFittingPlacement;
    state.reset = true;
  }
} // eslint-disable-next-line import/no-unused-modules

var flip$2 = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  fn: flip$1,
  requiresIfExists: ['offset'],
  data: {
    _skip: false
  }
};

function getSideOffsets(overflow, rect, preventedOffsets) {
  if (preventedOffsets === void 0) {
    preventedOffsets = {
      x: 0,
      y: 0
    };
  }
  return {
    top: overflow.top - rect.height - preventedOffsets.y,
    right: overflow.right - rect.width + preventedOffsets.x,
    bottom: overflow.bottom - rect.height + preventedOffsets.y,
    left: overflow.left - rect.width - preventedOffsets.x
  };
}
function isAnySideFullyClipped(overflow) {
  return [top, right, bottom, left].some(function (side) {
    return overflow[side] >= 0;
  });
}
function hide(_ref) {
  var state = _ref.state,
    name = _ref.name;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var preventedOffsets = state.modifiersData.preventOverflow;
  var referenceOverflow = detectOverflow(state, {
    elementContext: 'reference'
  });
  var popperAltOverflow = detectOverflow(state, {
    altBoundary: true
  });
  var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
  var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
  var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
  var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
  state.modifiersData[name] = {
    referenceClippingOffsets: referenceClippingOffsets,
    popperEscapeOffsets: popperEscapeOffsets,
    isReferenceHidden: isReferenceHidden,
    hasPopperEscaped: hasPopperEscaped
  };
  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-reference-hidden': isReferenceHidden,
    'data-popper-escaped': hasPopperEscaped
  });
} // eslint-disable-next-line import/no-unused-modules

var hide$1 = {
  name: 'hide',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['preventOverflow'],
  fn: hide
};

function distanceAndSkiddingToXY(placement, rects, offset) {
  var basePlacement = getBasePlacement$1(placement);
  var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;
  var _ref = typeof offset === 'function' ? offset(Object.assign({}, rects, {
      placement: placement
    })) : offset,
    skidding = _ref[0],
    distance = _ref[1];
  skidding = skidding || 0;
  distance = (distance || 0) * invertDistance;
  return [left, right].indexOf(basePlacement) >= 0 ? {
    x: distance,
    y: skidding
  } : {
    x: skidding,
    y: distance
  };
}
function offset(_ref2) {
  var state = _ref2.state,
    options = _ref2.options,
    name = _ref2.name;
  var _options$offset = options.offset,
    offset = _options$offset === void 0 ? [0, 0] : _options$offset;
  var data = placements.reduce(function (acc, placement) {
    acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
    return acc;
  }, {});
  var _data$state$placement = data[state.placement],
    x = _data$state$placement.x,
    y = _data$state$placement.y;
  if (state.modifiersData.popperOffsets != null) {
    state.modifiersData.popperOffsets.x += x;
    state.modifiersData.popperOffsets.y += y;
  }
  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules

var offset$1 = {
  name: 'offset',
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets'],
  fn: offset
};

function popperOffsets(_ref) {
  var state = _ref.state,
    name = _ref.name;
  // Offsets are the actual position the popper needs to have to be
  // properly positioned near its reference element
  // This is the most basic placement, and will be adjusted by
  // the modifiers in the next step
  state.modifiersData[name] = computeOffsets({
    reference: state.rects.reference,
    element: state.rects.popper,
    strategy: 'absolute',
    placement: state.placement
  });
} // eslint-disable-next-line import/no-unused-modules

var popperOffsets$1 = {
  name: 'popperOffsets',
  enabled: true,
  phase: 'read',
  fn: popperOffsets,
  data: {}
};

function getAltAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}

function preventOverflow(_ref) {
  var state = _ref.state,
    options = _ref.options,
    name = _ref.name;
  var _options$mainAxis = options.mainAxis,
    checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
    _options$altAxis = options.altAxis,
    checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
    boundary = options.boundary,
    rootBoundary = options.rootBoundary,
    altBoundary = options.altBoundary,
    padding = options.padding,
    _options$tether = options.tether,
    tether = _options$tether === void 0 ? true : _options$tether,
    _options$tetherOffset = options.tetherOffset,
    tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
  var overflow = detectOverflow(state, {
    boundary: boundary,
    rootBoundary: rootBoundary,
    padding: padding,
    altBoundary: altBoundary
  });
  var basePlacement = getBasePlacement$1(state.placement);
  var variation = getVariation(state.placement);
  var isBasePlacement = !variation;
  var mainAxis = getMainAxisFromPlacement(basePlacement);
  var altAxis = getAltAxis(mainAxis);
  var popperOffsets = state.modifiersData.popperOffsets;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign({}, state.rects, {
    placement: state.placement
  })) : tetherOffset;
  var normalizedTetherOffsetValue = typeof tetherOffsetValue === 'number' ? {
    mainAxis: tetherOffsetValue,
    altAxis: tetherOffsetValue
  } : Object.assign({
    mainAxis: 0,
    altAxis: 0
  }, tetherOffsetValue);
  var offsetModifierState = state.modifiersData.offset ? state.modifiersData.offset[state.placement] : null;
  var data = {
    x: 0,
    y: 0
  };
  if (!popperOffsets) {
    return;
  }
  if (checkMainAxis) {
    var _offsetModifierState$;
    var mainSide = mainAxis === 'y' ? top : left;
    var altSide = mainAxis === 'y' ? bottom : right;
    var len = mainAxis === 'y' ? 'height' : 'width';
    var offset = popperOffsets[mainAxis];
    var min = offset + overflow[mainSide];
    var max = offset - overflow[altSide];
    var additive = tether ? -popperRect[len] / 2 : 0;
    var minLen = variation === start ? referenceRect[len] : popperRect[len];
    var maxLen = variation === start ? -popperRect[len] : -referenceRect[len]; // We need to include the arrow in the calculation so the arrow doesn't go
    // outside the reference bounds

    var arrowElement = state.elements.arrow;
    var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
      width: 0,
      height: 0
    };
    var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
    var arrowPaddingMin = arrowPaddingObject[mainSide];
    var arrowPaddingMax = arrowPaddingObject[altSide]; // If the reference length is smaller than the arrow length, we don't want
    // to include its full size in the calculation. If the reference is small
    // and near the edge of a boundary, the popper can overflow even if the
    // reference is not overflowing as well (e.g. virtual elements with no
    // width or height)

    var arrowLen = within(0, referenceRect[len], arrowRect[len]);
    var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis : minLen - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis;
    var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis : maxLen + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis;
    var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
    var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
    var offsetModifierValue = (_offsetModifierState$ = offsetModifierState == null ? void 0 : offsetModifierState[mainAxis]) != null ? _offsetModifierState$ : 0;
    var tetherMin = offset + minOffset - offsetModifierValue - clientOffset;
    var tetherMax = offset + maxOffset - offsetModifierValue;
    var preventedOffset = within(tether ? min$2(min, tetherMin) : min, offset, tether ? max$2(max, tetherMax) : max);
    popperOffsets[mainAxis] = preventedOffset;
    data[mainAxis] = preventedOffset - offset;
  }
  if (checkAltAxis) {
    var _offsetModifierState$2;
    var _mainSide = mainAxis === 'x' ? top : left;
    var _altSide = mainAxis === 'x' ? bottom : right;
    var _offset = popperOffsets[altAxis];
    var _len = altAxis === 'y' ? 'height' : 'width';
    var _min = _offset + overflow[_mainSide];
    var _max = _offset - overflow[_altSide];
    var isOriginSide = [top, left].indexOf(basePlacement) !== -1;
    var _offsetModifierValue = (_offsetModifierState$2 = offsetModifierState == null ? void 0 : offsetModifierState[altAxis]) != null ? _offsetModifierState$2 : 0;
    var _tetherMin = isOriginSide ? _min : _offset - referenceRect[_len] - popperRect[_len] - _offsetModifierValue + normalizedTetherOffsetValue.altAxis;
    var _tetherMax = isOriginSide ? _offset + referenceRect[_len] + popperRect[_len] - _offsetModifierValue - normalizedTetherOffsetValue.altAxis : _max;
    var _preventedOffset = tether && isOriginSide ? withinMaxClamp(_tetherMin, _offset, _tetherMax) : within(tether ? _tetherMin : _min, _offset, tether ? _tetherMax : _max);
    popperOffsets[altAxis] = _preventedOffset;
    data[altAxis] = _preventedOffset - _offset;
  }
  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules

var preventOverflow$1 = {
  name: 'preventOverflow',
  enabled: true,
  phase: 'main',
  fn: preventOverflow,
  requiresIfExists: ['offset']
};

function getHTMLElementScroll(element) {
  return {
    scrollLeft: element.scrollLeft,
    scrollTop: element.scrollTop
  };
}

function getNodeScroll(node) {
  if (node === getWindow(node) || !isHTMLElement$1(node)) {
    return getWindowScroll(node);
  } else {
    return getHTMLElementScroll(node);
  }
}

function isElementScaled(element) {
  var rect = element.getBoundingClientRect();
  var scaleX = round$1(rect.width) / element.offsetWidth || 1;
  var scaleY = round$1(rect.height) / element.offsetHeight || 1;
  return scaleX !== 1 || scaleY !== 1;
} // Returns the composite rect of an element relative to its offsetParent.
// Composite means it takes into account transforms as well as layout.

function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  var isOffsetParentAnElement = isHTMLElement$1(offsetParent);
  var offsetParentIsScaled = isHTMLElement$1(offsetParent) && isElementScaled(offsetParent);
  var documentElement = getDocumentElement(offsetParent);
  var rect = getBoundingClientRect$1(elementOrVirtualElement, offsetParentIsScaled, isFixed);
  var scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  var offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' ||
    // https://github.com/popperjs/popper-core/issues/1078
    isScrollParent(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement$1(offsetParent)) {
      offsets = getBoundingClientRect$1(offsetParent, true);
      offsets.x += offsetParent.clientLeft;
      offsets.y += offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function order(modifiers) {
  var map = new Map();
  var visited = new Set();
  var result = [];
  modifiers.forEach(function (modifier) {
    map.set(modifier.name, modifier);
  }); // On visiting object, check for its dependencies and visit them recursively

  function sort(modifier) {
    visited.add(modifier.name);
    var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
    requires.forEach(function (dep) {
      if (!visited.has(dep)) {
        var depModifier = map.get(dep);
        if (depModifier) {
          sort(depModifier);
        }
      }
    });
    result.push(modifier);
  }
  modifiers.forEach(function (modifier) {
    if (!visited.has(modifier.name)) {
      // check for visited object
      sort(modifier);
    }
  });
  return result;
}
function orderModifiers(modifiers) {
  // order based on dependencies
  var orderedModifiers = order(modifiers); // order based on phase

  return modifierPhases.reduce(function (acc, phase) {
    return acc.concat(orderedModifiers.filter(function (modifier) {
      return modifier.phase === phase;
    }));
  }, []);
}

function debounce$2(fn) {
  var pending;
  return function () {
    if (!pending) {
      pending = new Promise(function (resolve) {
        Promise.resolve().then(function () {
          pending = undefined;
          resolve(fn());
        });
      });
    }
    return pending;
  };
}

function mergeByName(modifiers) {
  var merged = modifiers.reduce(function (merged, current) {
    var existing = merged[current.name];
    merged[current.name] = existing ? Object.assign({}, existing, current, {
      options: Object.assign({}, existing.options, current.options),
      data: Object.assign({}, existing.data, current.data)
    }) : current;
    return merged;
  }, {}); // IE11 does not support Object.values

  return Object.keys(merged).map(function (key) {
    return merged[key];
  });
}

var DEFAULT_OPTIONS = {
  placement: 'bottom',
  modifiers: [],
  strategy: 'absolute'
};
function areValidElements() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  return !args.some(function (element) {
    return !(element && typeof element.getBoundingClientRect === 'function');
  });
}
function popperGenerator(generatorOptions) {
  if (generatorOptions === void 0) {
    generatorOptions = {};
  }
  var _generatorOptions = generatorOptions,
    _generatorOptions$def = _generatorOptions.defaultModifiers,
    defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
    _generatorOptions$def2 = _generatorOptions.defaultOptions,
    defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
  return function createPopper(reference, popper, options) {
    if (options === void 0) {
      options = defaultOptions;
    }
    var state = {
      placement: 'bottom',
      orderedModifiers: [],
      options: Object.assign({}, DEFAULT_OPTIONS, defaultOptions),
      modifiersData: {},
      elements: {
        reference: reference,
        popper: popper
      },
      attributes: {},
      styles: {}
    };
    var effectCleanupFns = [];
    var isDestroyed = false;
    var instance = {
      state: state,
      setOptions: function setOptions(setOptionsAction) {
        var options = typeof setOptionsAction === 'function' ? setOptionsAction(state.options) : setOptionsAction;
        cleanupModifierEffects();
        state.options = Object.assign({}, defaultOptions, state.options, options);
        state.scrollParents = {
          reference: isElement$1(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
          popper: listScrollParents(popper)
        }; // Orders the modifiers based on their dependencies and `phase`
        // properties

        var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers))); // Strip out disabled modifiers

        state.orderedModifiers = orderedModifiers.filter(function (m) {
          return m.enabled;
        });
        runModifierEffects();
        return instance.update();
      },
      // Sync update – it will always be executed, even if not necessary. This
      // is useful for low frequency updates where sync behavior simplifies the
      // logic.
      // For high frequency updates (e.g. `resize` and `scroll` events), always
      // prefer the async Popper#update method
      forceUpdate: function forceUpdate() {
        if (isDestroyed) {
          return;
        }
        var _state$elements = state.elements,
          reference = _state$elements.reference,
          popper = _state$elements.popper; // Don't proceed if `reference` or `popper` are not valid elements
        // anymore

        if (!areValidElements(reference, popper)) {
          return;
        } // Store the reference and popper rects to be read by modifiers

        state.rects = {
          reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
          popper: getLayoutRect(popper)
        }; // Modifiers have the ability to reset the current update cycle. The
        // most common use case for this is the `flip` modifier changing the
        // placement, which then needs to re-run all the modifiers, because the
        // logic was previously ran for the previous placement and is therefore
        // stale/incorrect

        state.reset = false;
        state.placement = state.options.placement; // On each update cycle, the `modifiersData` property for each modifier
        // is filled with the initial data specified by the modifier. This means
        // it doesn't persist and is fresh on each update.
        // To ensure persistent data, use `${name}#persistent`

        state.orderedModifiers.forEach(function (modifier) {
          return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
        });
        for (var index = 0; index < state.orderedModifiers.length; index++) {
          if (state.reset === true) {
            state.reset = false;
            index = -1;
            continue;
          }
          var _state$orderedModifie = state.orderedModifiers[index],
            fn = _state$orderedModifie.fn,
            _state$orderedModifie2 = _state$orderedModifie.options,
            _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
            name = _state$orderedModifie.name;
          if (typeof fn === 'function') {
            state = fn({
              state: state,
              options: _options,
              name: name,
              instance: instance
            }) || state;
          }
        }
      },
      // Async and optimistically optimized update – it will not be executed if
      // not necessary (debounced to run at most once-per-tick)
      update: debounce$2(function () {
        return new Promise(function (resolve) {
          instance.forceUpdate();
          resolve(state);
        });
      }),
      destroy: function destroy() {
        cleanupModifierEffects();
        isDestroyed = true;
      }
    };
    if (!areValidElements(reference, popper)) {
      return instance;
    }
    instance.setOptions(options).then(function (state) {
      if (!isDestroyed && options.onFirstUpdate) {
        options.onFirstUpdate(state);
      }
    }); // Modifiers have the ability to execute arbitrary code before the first
    // update cycle runs. They will be executed in the same order as the update
    // cycle. This is useful when a modifier adds some persistent data that
    // other modifiers need to use, but the modifier is run after the dependent
    // one.

    function runModifierEffects() {
      state.orderedModifiers.forEach(function (_ref) {
        var name = _ref.name,
          _ref$options = _ref.options,
          options = _ref$options === void 0 ? {} : _ref$options,
          effect = _ref.effect;
        if (typeof effect === 'function') {
          var cleanupFn = effect({
            state: state,
            name: name,
            instance: instance,
            options: options
          });
          var noopFn = function noopFn() {};
          effectCleanupFns.push(cleanupFn || noopFn);
        }
      });
    }
    function cleanupModifierEffects() {
      effectCleanupFns.forEach(function (fn) {
        return fn();
      });
      effectCleanupFns = [];
    }
    return instance;
  };
}

var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$2, preventOverflow$1, arrow$1, hide$1];
var createPopper = /*#__PURE__*/popperGenerator({
  defaultModifiers: defaultModifiers
}); // eslint-disable-next-line import/no-unused-modules

/**!
* tippy.js v6.3.7
* (c) 2017-2021 atomiks
* MIT License
*/
var BOX_CLASS = "tippy-box";
var CONTENT_CLASS = "tippy-content";
var BACKDROP_CLASS = "tippy-backdrop";
var ARROW_CLASS = "tippy-arrow";
var SVG_ARROW_CLASS = "tippy-svg-arrow";
var TOUCH_OPTIONS = {
  passive: true,
  capture: true
};
var TIPPY_DEFAULT_APPEND_TO = function TIPPY_DEFAULT_APPEND_TO() {
  return document.body;
};
function hasOwnProperty$1(obj, key) {
  return {}.hasOwnProperty.call(obj, key);
}
function getValueAtIndexOrReturn(value, index, defaultValue) {
  if (Array.isArray(value)) {
    var v = value[index];
    return v == null ? Array.isArray(defaultValue) ? defaultValue[index] : defaultValue : v;
  }
  return value;
}
function isType(value, type) {
  var str = {}.toString.call(value);
  return str.indexOf('[object') === 0 && str.indexOf(type + "]") > -1;
}
function invokeWithArgsOrReturn(value, args) {
  return typeof value === 'function' ? value.apply(void 0, args) : value;
}
function debounce$1(fn, ms) {
  // Avoid wrapping in `setTimeout` if ms is 0 anyway
  if (ms === 0) {
    return fn;
  }
  var timeout;
  return function (arg) {
    clearTimeout(timeout);
    timeout = setTimeout(function () {
      fn(arg);
    }, ms);
  };
}
function removeProperties(obj, keys) {
  var clone = Object.assign({}, obj);
  keys.forEach(function (key) {
    delete clone[key];
  });
  return clone;
}
function splitBySpaces(value) {
  return value.split(/\s+/).filter(Boolean);
}
function normalizeToArray(value) {
  return [].concat(value);
}
function pushIfUnique(arr, value) {
  if (arr.indexOf(value) === -1) {
    arr.push(value);
  }
}
function unique(arr) {
  return arr.filter(function (item, index) {
    return arr.indexOf(item) === index;
  });
}
function getBasePlacement(placement) {
  return placement.split('-')[0];
}
function arrayFrom(value) {
  return [].slice.call(value);
}
function removeUndefinedProps(obj) {
  return Object.keys(obj).reduce(function (acc, key) {
    if (obj[key] !== undefined) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}
function div$1() {
  return document.createElement('div');
}
function isElement(value) {
  return ['Element', 'Fragment'].some(function (type) {
    return isType(value, type);
  });
}
function isNodeList(value) {
  return isType(value, 'NodeList');
}
function isMouseEvent(value) {
  return isType(value, 'MouseEvent');
}
function isReferenceElement(value) {
  return !!(value && value._tippy && value._tippy.reference === value);
}
function getArrayOfElements(value) {
  if (isElement(value)) {
    return [value];
  }
  if (isNodeList(value)) {
    return arrayFrom(value);
  }
  if (Array.isArray(value)) {
    return value;
  }
  return arrayFrom(document.querySelectorAll(value));
}
function setTransitionDuration(els, value) {
  els.forEach(function (el) {
    if (el) {
      el.style.transitionDuration = value + "ms";
    }
  });
}
function setVisibilityState(els, state) {
  els.forEach(function (el) {
    if (el) {
      el.setAttribute('data-state', state);
    }
  });
}
function getOwnerDocument(elementOrElements) {
  var _element$ownerDocumen;
  var _normalizeToArray = normalizeToArray(elementOrElements),
    element = _normalizeToArray[0]; // Elements created via a <template> have an ownerDocument with no reference to the body

  return element != null && (_element$ownerDocumen = element.ownerDocument) != null && _element$ownerDocumen.body ? element.ownerDocument : document;
}
function isCursorOutsideInteractiveBorder(popperTreeData, event) {
  var clientX = event.clientX,
    clientY = event.clientY;
  return popperTreeData.every(function (_ref) {
    var popperRect = _ref.popperRect,
      popperState = _ref.popperState,
      props = _ref.props;
    var interactiveBorder = props.interactiveBorder;
    var basePlacement = getBasePlacement(popperState.placement);
    var offsetData = popperState.modifiersData.offset;
    if (!offsetData) {
      return true;
    }
    var topDistance = basePlacement === 'bottom' ? offsetData.top.y : 0;
    var bottomDistance = basePlacement === 'top' ? offsetData.bottom.y : 0;
    var leftDistance = basePlacement === 'right' ? offsetData.left.x : 0;
    var rightDistance = basePlacement === 'left' ? offsetData.right.x : 0;
    var exceedsTop = popperRect.top - clientY + topDistance > interactiveBorder;
    var exceedsBottom = clientY - popperRect.bottom - bottomDistance > interactiveBorder;
    var exceedsLeft = popperRect.left - clientX + leftDistance > interactiveBorder;
    var exceedsRight = clientX - popperRect.right - rightDistance > interactiveBorder;
    return exceedsTop || exceedsBottom || exceedsLeft || exceedsRight;
  });
}
function updateTransitionEndListener(box, action, listener) {
  var method = action + "EventListener"; // some browsers apparently support `transition` (unprefixed) but only fire
  // `webkitTransitionEnd`...

  ['transitionend', 'webkitTransitionEnd'].forEach(function (event) {
    box[method](event, listener);
  });
}
/**
 * Compared to xxx.contains, this function works for dom structures with shadow
 * dom
 */

function actualContains(parent, child) {
  var target = child;
  while (target) {
    var _target$getRootNode;
    if (parent.contains(target)) {
      return true;
    }
    target = target.getRootNode == null ? void 0 : (_target$getRootNode = target.getRootNode()) == null ? void 0 : _target$getRootNode.host;
  }
  return false;
}
var currentInput = {
  isTouch: false
};
var lastMouseMoveTime = 0;
/**
 * When a `touchstart` event is fired, it's assumed the user is using touch
 * input. We'll bind a `mousemove` event listener to listen for mouse input in
 * the future. This way, the `isTouch` property is fully dynamic and will handle
 * hybrid devices that use a mix of touch + mouse input.
 */

function onDocumentTouchStart() {
  if (currentInput.isTouch) {
    return;
  }
  currentInput.isTouch = true;
  if (window.performance) {
    document.addEventListener('mousemove', onDocumentMouseMove);
  }
}
/**
 * When two `mousemove` event are fired consecutively within 20ms, it's assumed
 * the user is using mouse input again. `mousemove` can fire on touch devices as
 * well, but very rarely that quickly.
 */

function onDocumentMouseMove() {
  var now = performance.now();
  if (now - lastMouseMoveTime < 20) {
    currentInput.isTouch = false;
    document.removeEventListener('mousemove', onDocumentMouseMove);
  }
  lastMouseMoveTime = now;
}
/**
 * When an element is in focus and has a tippy, leaving the tab/window and
 * returning causes it to show again. For mouse users this is unexpected, but
 * for keyboard use it makes sense.
 * TODO: find a better technique to solve this problem
 */

function onWindowBlur() {
  var activeElement = document.activeElement;
  if (isReferenceElement(activeElement)) {
    var instance = activeElement._tippy;
    if (activeElement.blur && !instance.state.isVisible) {
      activeElement.blur();
    }
  }
}
function bindGlobalEventListeners() {
  document.addEventListener('touchstart', onDocumentTouchStart, TOUCH_OPTIONS);
  window.addEventListener('blur', onWindowBlur);
}
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
var isIE11 = isBrowser ?
// @ts-ignore
!!window.msCrypto : false;
function createMemoryLeakWarning(method) {
  var txt = method === 'destroy' ? 'n already-' : ' ';
  return [method + "() was called on a" + txt + "destroyed instance. This is a no-op but", 'indicates a potential memory leak.'].join(' ');
}
function clean$1(value) {
  var spacesAndTabs = /[ \t]{2,}/g;
  var lineStartWithSpaces = /^[ \t]*/gm;
  return value.replace(spacesAndTabs, ' ').replace(lineStartWithSpaces, '').trim();
}
function getDevMessage(message) {
  return clean$1("\n  %ctippy.js\n\n  %c" + clean$1(message) + "\n\n  %c\uD83D\uDC77\u200D This is a development-only message. It will be removed in production.\n  ");
}
function getFormattedMessage(message) {
  return [getDevMessage(message),
  // title
  'color: #00C584; font-size: 1.3em; font-weight: bold;',
  // message
  'line-height: 1.5',
  // footer
  'color: #a6a095;'];
} // Assume warnings and errors never have the same message

var visitedMessages;
{
  resetVisitedMessages();
}
function resetVisitedMessages() {
  visitedMessages = new Set();
}
function warnWhen(condition, message) {
  if (condition && !visitedMessages.has(message)) {
    var _console;
    visitedMessages.add(message);
    (_console = console).warn.apply(_console, getFormattedMessage(message));
  }
}
function errorWhen(condition, message) {
  if (condition && !visitedMessages.has(message)) {
    var _console2;
    visitedMessages.add(message);
    (_console2 = console).error.apply(_console2, getFormattedMessage(message));
  }
}
function validateTargets(targets) {
  var didPassFalsyValue = !targets;
  var didPassPlainObject = Object.prototype.toString.call(targets) === '[object Object]' && !targets.addEventListener;
  errorWhen(didPassFalsyValue, ['tippy() was passed', '`' + String(targets) + '`', 'as its targets (first) argument. Valid types are: String, Element,', 'Element[], or NodeList.'].join(' '));
  errorWhen(didPassPlainObject, ['tippy() was passed a plain object which is not supported as an argument', 'for virtual positioning. Use props.getReferenceClientRect instead.'].join(' '));
}
var pluginProps = {
  animateFill: false,
  followCursor: false,
  inlinePositioning: false,
  sticky: false
};
var renderProps = {
  allowHTML: false,
  animation: 'fade',
  arrow: true,
  content: '',
  inertia: false,
  maxWidth: 350,
  role: 'tooltip',
  theme: '',
  zIndex: 9999
};
var defaultProps = Object.assign({
  appendTo: TIPPY_DEFAULT_APPEND_TO,
  aria: {
    content: 'auto',
    expanded: 'auto'
  },
  delay: 0,
  duration: [300, 250],
  getReferenceClientRect: null,
  hideOnClick: true,
  ignoreAttributes: false,
  interactive: false,
  interactiveBorder: 2,
  interactiveDebounce: 0,
  moveTransition: '',
  offset: [0, 10],
  onAfterUpdate: function onAfterUpdate() {},
  onBeforeUpdate: function onBeforeUpdate() {},
  onCreate: function onCreate() {},
  onDestroy: function onDestroy() {},
  onHidden: function onHidden() {},
  onHide: function onHide() {},
  onMount: function onMount() {},
  onShow: function onShow() {},
  onShown: function onShown() {},
  onTrigger: function onTrigger() {},
  onUntrigger: function onUntrigger() {},
  onClickOutside: function onClickOutside() {},
  placement: 'top',
  plugins: [],
  popperOptions: {},
  render: null,
  showOnCreate: false,
  touch: true,
  trigger: 'mouseenter focus',
  triggerTarget: null
}, pluginProps, renderProps);
var defaultKeys = Object.keys(defaultProps);
var setDefaultProps = function setDefaultProps(partialProps) {
  /* istanbul ignore else */
  {
    validateProps(partialProps, []);
  }
  var keys = Object.keys(partialProps);
  keys.forEach(function (key) {
    defaultProps[key] = partialProps[key];
  });
};
function getExtendedPassedProps(passedProps) {
  var plugins = passedProps.plugins || [];
  var pluginProps = plugins.reduce(function (acc, plugin) {
    var name = plugin.name,
      defaultValue = plugin.defaultValue;
    if (name) {
      var _name;
      acc[name] = passedProps[name] !== undefined ? passedProps[name] : (_name = defaultProps[name]) != null ? _name : defaultValue;
    }
    return acc;
  }, {});
  return Object.assign({}, passedProps, pluginProps);
}
function getDataAttributeProps(reference, plugins) {
  var propKeys = plugins ? Object.keys(getExtendedPassedProps(Object.assign({}, defaultProps, {
    plugins: plugins
  }))) : defaultKeys;
  var props = propKeys.reduce(function (acc, key) {
    var valueAsString = (reference.getAttribute("data-tippy-" + key) || '').trim();
    if (!valueAsString) {
      return acc;
    }
    if (key === 'content') {
      acc[key] = valueAsString;
    } else {
      try {
        acc[key] = JSON.parse(valueAsString);
      } catch (e) {
        acc[key] = valueAsString;
      }
    }
    return acc;
  }, {});
  return props;
}
function evaluateProps(reference, props) {
  var out = Object.assign({}, props, {
    content: invokeWithArgsOrReturn(props.content, [reference])
  }, props.ignoreAttributes ? {} : getDataAttributeProps(reference, props.plugins));
  out.aria = Object.assign({}, defaultProps.aria, out.aria);
  out.aria = {
    expanded: out.aria.expanded === 'auto' ? props.interactive : out.aria.expanded,
    content: out.aria.content === 'auto' ? props.interactive ? null : 'describedby' : out.aria.content
  };
  return out;
}
function validateProps(partialProps, plugins) {
  if (partialProps === void 0) {
    partialProps = {};
  }
  if (plugins === void 0) {
    plugins = [];
  }
  var keys = Object.keys(partialProps);
  keys.forEach(function (prop) {
    var nonPluginProps = removeProperties(defaultProps, Object.keys(pluginProps));
    var didPassUnknownProp = !hasOwnProperty$1(nonPluginProps, prop); // Check if the prop exists in `plugins`

    if (didPassUnknownProp) {
      didPassUnknownProp = plugins.filter(function (plugin) {
        return plugin.name === prop;
      }).length === 0;
    }
    warnWhen(didPassUnknownProp, ["`" + prop + "`", "is not a valid prop. You may have spelled it incorrectly, or if it's", 'a plugin, forgot to pass it in an array as props.plugins.', '\n\n', 'All props: https://atomiks.github.io/tippyjs/v6/all-props/\n', 'Plugins: https://atomiks.github.io/tippyjs/v6/plugins/'].join(' '));
  });
}
var innerHTML = function innerHTML() {
  return 'innerHTML';
};
function dangerouslySetInnerHTML(element, html) {
  element[innerHTML()] = html;
}
function createArrowElement(value) {
  var arrow = div$1();
  if (value === true) {
    arrow.className = ARROW_CLASS;
  } else {
    arrow.className = SVG_ARROW_CLASS;
    if (isElement(value)) {
      arrow.appendChild(value);
    } else {
      dangerouslySetInnerHTML(arrow, value);
    }
  }
  return arrow;
}
function setContent(content, props) {
  if (isElement(props.content)) {
    dangerouslySetInnerHTML(content, '');
    content.appendChild(props.content);
  } else if (typeof props.content !== 'function') {
    if (props.allowHTML) {
      dangerouslySetInnerHTML(content, props.content);
    } else {
      content.textContent = props.content;
    }
  }
}
function getChildren(popper) {
  var box = popper.firstElementChild;
  var boxChildren = arrayFrom(box.children);
  return {
    box: box,
    content: boxChildren.find(function (node) {
      return node.classList.contains(CONTENT_CLASS);
    }),
    arrow: boxChildren.find(function (node) {
      return node.classList.contains(ARROW_CLASS) || node.classList.contains(SVG_ARROW_CLASS);
    }),
    backdrop: boxChildren.find(function (node) {
      return node.classList.contains(BACKDROP_CLASS);
    })
  };
}
function render(instance) {
  var popper = div$1();
  var box = div$1();
  box.className = BOX_CLASS;
  box.setAttribute('data-state', 'hidden');
  box.setAttribute('tabindex', '-1');
  var content = div$1();
  content.className = CONTENT_CLASS;
  content.setAttribute('data-state', 'hidden');
  setContent(content, instance.props);
  popper.appendChild(box);
  box.appendChild(content);
  onUpdate(instance.props, instance.props);
  function onUpdate(prevProps, nextProps) {
    var _getChildren = getChildren(popper),
      box = _getChildren.box,
      content = _getChildren.content,
      arrow = _getChildren.arrow;
    if (nextProps.theme) {
      box.setAttribute('data-theme', nextProps.theme);
    } else {
      box.removeAttribute('data-theme');
    }
    if (typeof nextProps.animation === 'string') {
      box.setAttribute('data-animation', nextProps.animation);
    } else {
      box.removeAttribute('data-animation');
    }
    if (nextProps.inertia) {
      box.setAttribute('data-inertia', '');
    } else {
      box.removeAttribute('data-inertia');
    }
    box.style.maxWidth = typeof nextProps.maxWidth === 'number' ? nextProps.maxWidth + "px" : nextProps.maxWidth;
    if (nextProps.role) {
      box.setAttribute('role', nextProps.role);
    } else {
      box.removeAttribute('role');
    }
    if (prevProps.content !== nextProps.content || prevProps.allowHTML !== nextProps.allowHTML) {
      setContent(content, instance.props);
    }
    if (nextProps.arrow) {
      if (!arrow) {
        box.appendChild(createArrowElement(nextProps.arrow));
      } else if (prevProps.arrow !== nextProps.arrow) {
        box.removeChild(arrow);
        box.appendChild(createArrowElement(nextProps.arrow));
      }
    } else if (arrow) {
      box.removeChild(arrow);
    }
  }
  return {
    popper: popper,
    onUpdate: onUpdate
  };
} // Runtime check to identify if the render function is the default one; this
// way we can apply default CSS transitions logic and it can be tree-shaken away

render.$$tippy = true;
var idCounter = 1;
var mouseMoveListeners = []; // Used by `hideAll()`

var mountedInstances = [];
function createTippy(reference, passedProps) {
  var props = evaluateProps(reference, Object.assign({}, defaultProps, getExtendedPassedProps(removeUndefinedProps(passedProps)))); // ===========================================================================
  // 🔒 Private members
  // ===========================================================================

  var showTimeout;
  var hideTimeout;
  var scheduleHideAnimationFrame;
  var isVisibleFromClick = false;
  var didHideDueToDocumentMouseDown = false;
  var didTouchMove = false;
  var ignoreOnFirstUpdate = false;
  var lastTriggerEvent;
  var currentTransitionEndListener;
  var onFirstUpdate;
  var listeners = [];
  var debouncedOnMouseMove = debounce$1(onMouseMove, props.interactiveDebounce);
  var currentTarget; // ===========================================================================
  // 🔑 Public members
  // ===========================================================================

  var id = idCounter++;
  var popperInstance = null;
  var plugins = unique(props.plugins);
  var state = {
    // Is the instance currently enabled?
    isEnabled: true,
    // Is the tippy currently showing and not transitioning out?
    isVisible: false,
    // Has the instance been destroyed?
    isDestroyed: false,
    // Is the tippy currently mounted to the DOM?
    isMounted: false,
    // Has the tippy finished transitioning in?
    isShown: false
  };
  var instance = {
    // properties
    id: id,
    reference: reference,
    popper: div$1(),
    popperInstance: popperInstance,
    props: props,
    state: state,
    plugins: plugins,
    // methods
    clearDelayTimeouts: clearDelayTimeouts,
    setProps: setProps,
    setContent: setContent,
    show: show,
    hide: hide,
    hideWithInteractivity: hideWithInteractivity,
    enable: enable,
    disable: disable,
    unmount: unmount,
    destroy: destroy
  }; // TODO: Investigate why this early return causes a TDZ error in the tests —
  // it doesn't seem to happen in the browser

  /* istanbul ignore if */

  if (!props.render) {
    {
      errorWhen(true, 'render() function has not been supplied.');
    }
    return instance;
  } // ===========================================================================
  // Initial mutations
  // ===========================================================================

  var _props$render = props.render(instance),
    popper = _props$render.popper,
    onUpdate = _props$render.onUpdate;
  popper.setAttribute('data-tippy-root', '');
  popper.id = "tippy-" + instance.id;
  instance.popper = popper;
  reference._tippy = instance;
  popper._tippy = instance;
  var pluginsHooks = plugins.map(function (plugin) {
    return plugin.fn(instance);
  });
  var hasAriaExpanded = reference.hasAttribute('aria-expanded');
  addListeners();
  handleAriaExpandedAttribute();
  handleStyles();
  invokeHook('onCreate', [instance]);
  if (props.showOnCreate) {
    scheduleShow();
  } // Prevent a tippy with a delay from hiding if the cursor left then returned
  // before it started hiding

  popper.addEventListener('mouseenter', function () {
    if (instance.props.interactive && instance.state.isVisible) {
      instance.clearDelayTimeouts();
    }
  });
  popper.addEventListener('mouseleave', function () {
    if (instance.props.interactive && instance.props.trigger.indexOf('mouseenter') >= 0) {
      getDocument().addEventListener('mousemove', debouncedOnMouseMove);
    }
  });
  return instance; // ===========================================================================
  // 🔒 Private methods
  // ===========================================================================

  function getNormalizedTouchSettings() {
    var touch = instance.props.touch;
    return Array.isArray(touch) ? touch : [touch, 0];
  }
  function getIsCustomTouchBehavior() {
    return getNormalizedTouchSettings()[0] === 'hold';
  }
  function getIsDefaultRenderFn() {
    var _instance$props$rende;

    // @ts-ignore
    return !!((_instance$props$rende = instance.props.render) != null && _instance$props$rende.$$tippy);
  }
  function getCurrentTarget() {
    return currentTarget || reference;
  }
  function getDocument() {
    var parent = getCurrentTarget().parentNode;
    return parent ? getOwnerDocument(parent) : document;
  }
  function getDefaultTemplateChildren() {
    return getChildren(popper);
  }
  function getDelay(isShow) {
    // For touch or keyboard input, force `0` delay for UX reasons
    // Also if the instance is mounted but not visible (transitioning out),
    // ignore delay
    if (instance.state.isMounted && !instance.state.isVisible || currentInput.isTouch || lastTriggerEvent && lastTriggerEvent.type === 'focus') {
      return 0;
    }
    return getValueAtIndexOrReturn(instance.props.delay, isShow ? 0 : 1, defaultProps.delay);
  }
  function handleStyles(fromHide) {
    if (fromHide === void 0) {
      fromHide = false;
    }
    popper.style.pointerEvents = instance.props.interactive && !fromHide ? '' : 'none';
    popper.style.zIndex = "" + instance.props.zIndex;
  }
  function invokeHook(hook, args, shouldInvokePropsHook) {
    if (shouldInvokePropsHook === void 0) {
      shouldInvokePropsHook = true;
    }
    pluginsHooks.forEach(function (pluginHooks) {
      if (pluginHooks[hook]) {
        pluginHooks[hook].apply(pluginHooks, args);
      }
    });
    if (shouldInvokePropsHook) {
      var _instance$props;
      (_instance$props = instance.props)[hook].apply(_instance$props, args);
    }
  }
  function handleAriaContentAttribute() {
    var aria = instance.props.aria;
    if (!aria.content) {
      return;
    }
    var attr = "aria-" + aria.content;
    var id = popper.id;
    var nodes = normalizeToArray(instance.props.triggerTarget || reference);
    nodes.forEach(function (node) {
      var currentValue = node.getAttribute(attr);
      if (instance.state.isVisible) {
        node.setAttribute(attr, currentValue ? currentValue + " " + id : id);
      } else {
        var nextValue = currentValue && currentValue.replace(id, '').trim();
        if (nextValue) {
          node.setAttribute(attr, nextValue);
        } else {
          node.removeAttribute(attr);
        }
      }
    });
  }
  function handleAriaExpandedAttribute() {
    if (hasAriaExpanded || !instance.props.aria.expanded) {
      return;
    }
    var nodes = normalizeToArray(instance.props.triggerTarget || reference);
    nodes.forEach(function (node) {
      if (instance.props.interactive) {
        node.setAttribute('aria-expanded', instance.state.isVisible && node === getCurrentTarget() ? 'true' : 'false');
      } else {
        node.removeAttribute('aria-expanded');
      }
    });
  }
  function cleanupInteractiveMouseListeners() {
    getDocument().removeEventListener('mousemove', debouncedOnMouseMove);
    mouseMoveListeners = mouseMoveListeners.filter(function (listener) {
      return listener !== debouncedOnMouseMove;
    });
  }
  function onDocumentPress(event) {
    // Moved finger to scroll instead of an intentional tap outside
    if (currentInput.isTouch) {
      if (didTouchMove || event.type === 'mousedown') {
        return;
      }
    }
    var actualTarget = event.composedPath && event.composedPath()[0] || event.target; // Clicked on interactive popper

    if (instance.props.interactive && actualContains(popper, actualTarget)) {
      return;
    } // Clicked on the event listeners target

    if (normalizeToArray(instance.props.triggerTarget || reference).some(function (el) {
      return actualContains(el, actualTarget);
    })) {
      if (currentInput.isTouch) {
        return;
      }
      if (instance.state.isVisible && instance.props.trigger.indexOf('click') >= 0) {
        return;
      }
    } else {
      invokeHook('onClickOutside', [instance, event]);
    }
    if (instance.props.hideOnClick === true) {
      instance.clearDelayTimeouts();
      instance.hide(); // `mousedown` event is fired right before `focus` if pressing the
      // currentTarget. This lets a tippy with `focus` trigger know that it
      // should not show

      didHideDueToDocumentMouseDown = true;
      setTimeout(function () {
        didHideDueToDocumentMouseDown = false;
      }); // The listener gets added in `scheduleShow()`, but this may be hiding it
      // before it shows, and hide()'s early bail-out behavior can prevent it
      // from being cleaned up

      if (!instance.state.isMounted) {
        removeDocumentPress();
      }
    }
  }
  function onTouchMove() {
    didTouchMove = true;
  }
  function onTouchStart() {
    didTouchMove = false;
  }
  function addDocumentPress() {
    var doc = getDocument();
    doc.addEventListener('mousedown', onDocumentPress, true);
    doc.addEventListener('touchend', onDocumentPress, TOUCH_OPTIONS);
    doc.addEventListener('touchstart', onTouchStart, TOUCH_OPTIONS);
    doc.addEventListener('touchmove', onTouchMove, TOUCH_OPTIONS);
  }
  function removeDocumentPress() {
    var doc = getDocument();
    doc.removeEventListener('mousedown', onDocumentPress, true);
    doc.removeEventListener('touchend', onDocumentPress, TOUCH_OPTIONS);
    doc.removeEventListener('touchstart', onTouchStart, TOUCH_OPTIONS);
    doc.removeEventListener('touchmove', onTouchMove, TOUCH_OPTIONS);
  }
  function onTransitionedOut(duration, callback) {
    onTransitionEnd(duration, function () {
      if (!instance.state.isVisible && popper.parentNode && popper.parentNode.contains(popper)) {
        callback();
      }
    });
  }
  function onTransitionedIn(duration, callback) {
    onTransitionEnd(duration, callback);
  }
  function onTransitionEnd(duration, callback) {
    var box = getDefaultTemplateChildren().box;
    function listener(event) {
      if (event.target === box) {
        updateTransitionEndListener(box, 'remove', listener);
        callback();
      }
    } // Make callback synchronous if duration is 0
    // `transitionend` won't fire otherwise

    if (duration === 0) {
      return callback();
    }
    updateTransitionEndListener(box, 'remove', currentTransitionEndListener);
    updateTransitionEndListener(box, 'add', listener);
    currentTransitionEndListener = listener;
  }
  function on(eventType, handler, options) {
    if (options === void 0) {
      options = false;
    }
    var nodes = normalizeToArray(instance.props.triggerTarget || reference);
    nodes.forEach(function (node) {
      node.addEventListener(eventType, handler, options);
      listeners.push({
        node: node,
        eventType: eventType,
        handler: handler,
        options: options
      });
    });
  }
  function addListeners() {
    if (getIsCustomTouchBehavior()) {
      on('touchstart', onTrigger, {
        passive: true
      });
      on('touchend', onMouseLeave, {
        passive: true
      });
    }
    splitBySpaces(instance.props.trigger).forEach(function (eventType) {
      if (eventType === 'manual') {
        return;
      }
      on(eventType, onTrigger);
      switch (eventType) {
        case 'mouseenter':
          on('mouseleave', onMouseLeave);
          break;
        case 'focus':
          on(isIE11 ? 'focusout' : 'blur', onBlurOrFocusOut);
          break;
        case 'focusin':
          on('focusout', onBlurOrFocusOut);
          break;
      }
    });
  }
  function removeListeners() {
    listeners.forEach(function (_ref) {
      var node = _ref.node,
        eventType = _ref.eventType,
        handler = _ref.handler,
        options = _ref.options;
      node.removeEventListener(eventType, handler, options);
    });
    listeners = [];
  }
  function onTrigger(event) {
    var _lastTriggerEvent;
    var shouldScheduleClickHide = false;
    if (!instance.state.isEnabled || isEventListenerStopped(event) || didHideDueToDocumentMouseDown) {
      return;
    }
    var wasFocused = ((_lastTriggerEvent = lastTriggerEvent) == null ? void 0 : _lastTriggerEvent.type) === 'focus';
    lastTriggerEvent = event;
    currentTarget = event.currentTarget;
    handleAriaExpandedAttribute();
    if (!instance.state.isVisible && isMouseEvent(event)) {
      // If scrolling, `mouseenter` events can be fired if the cursor lands
      // over a new target, but `mousemove` events don't get fired. This
      // causes interactive tooltips to get stuck open until the cursor is
      // moved
      mouseMoveListeners.forEach(function (listener) {
        return listener(event);
      });
    } // Toggle show/hide when clicking click-triggered tooltips

    if (event.type === 'click' && (instance.props.trigger.indexOf('mouseenter') < 0 || isVisibleFromClick) && instance.props.hideOnClick !== false && instance.state.isVisible) {
      shouldScheduleClickHide = true;
    } else {
      scheduleShow(event);
    }
    if (event.type === 'click') {
      isVisibleFromClick = !shouldScheduleClickHide;
    }
    if (shouldScheduleClickHide && !wasFocused) {
      scheduleHide(event);
    }
  }
  function onMouseMove(event) {
    var target = event.target;
    var isCursorOverReferenceOrPopper = getCurrentTarget().contains(target) || popper.contains(target);
    if (event.type === 'mousemove' && isCursorOverReferenceOrPopper) {
      return;
    }
    var popperTreeData = getNestedPopperTree().concat(popper).map(function (popper) {
      var _instance$popperInsta;
      var instance = popper._tippy;
      var state = (_instance$popperInsta = instance.popperInstance) == null ? void 0 : _instance$popperInsta.state;
      if (state) {
        return {
          popperRect: popper.getBoundingClientRect(),
          popperState: state,
          props: props
        };
      }
      return null;
    }).filter(Boolean);
    if (isCursorOutsideInteractiveBorder(popperTreeData, event)) {
      cleanupInteractiveMouseListeners();
      scheduleHide(event);
    }
  }
  function onMouseLeave(event) {
    var shouldBail = isEventListenerStopped(event) || instance.props.trigger.indexOf('click') >= 0 && isVisibleFromClick;
    if (shouldBail) {
      return;
    }
    if (instance.props.interactive) {
      instance.hideWithInteractivity(event);
      return;
    }
    scheduleHide(event);
  }
  function onBlurOrFocusOut(event) {
    if (instance.props.trigger.indexOf('focusin') < 0 && event.target !== getCurrentTarget()) {
      return;
    } // If focus was moved to within the popper

    if (instance.props.interactive && event.relatedTarget && popper.contains(event.relatedTarget)) {
      return;
    }
    scheduleHide(event);
  }
  function isEventListenerStopped(event) {
    return currentInput.isTouch ? getIsCustomTouchBehavior() !== event.type.indexOf('touch') >= 0 : false;
  }
  function createPopperInstance() {
    destroyPopperInstance();
    var _instance$props2 = instance.props,
      popperOptions = _instance$props2.popperOptions,
      placement = _instance$props2.placement,
      offset = _instance$props2.offset,
      getReferenceClientRect = _instance$props2.getReferenceClientRect,
      moveTransition = _instance$props2.moveTransition;
    var arrow = getIsDefaultRenderFn() ? getChildren(popper).arrow : null;
    var computedReference = getReferenceClientRect ? {
      getBoundingClientRect: getReferenceClientRect,
      contextElement: getReferenceClientRect.contextElement || getCurrentTarget()
    } : reference;
    var tippyModifier = {
      name: '$$tippy',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: function fn(_ref2) {
        var state = _ref2.state;
        if (getIsDefaultRenderFn()) {
          var _getDefaultTemplateCh = getDefaultTemplateChildren(),
            box = _getDefaultTemplateCh.box;
          ['placement', 'reference-hidden', 'escaped'].forEach(function (attr) {
            if (attr === 'placement') {
              box.setAttribute('data-placement', state.placement);
            } else {
              if (state.attributes.popper["data-popper-" + attr]) {
                box.setAttribute("data-" + attr, '');
              } else {
                box.removeAttribute("data-" + attr);
              }
            }
          });
          state.attributes.popper = {};
        }
      }
    };
    var modifiers = [{
      name: 'offset',
      options: {
        offset: offset
      }
    }, {
      name: 'preventOverflow',
      options: {
        padding: {
          top: 2,
          bottom: 2,
          left: 5,
          right: 5
        }
      }
    }, {
      name: 'flip',
      options: {
        padding: 5
      }
    }, {
      name: 'computeStyles',
      options: {
        adaptive: !moveTransition
      }
    }, tippyModifier];
    if (getIsDefaultRenderFn() && arrow) {
      modifiers.push({
        name: 'arrow',
        options: {
          element: arrow,
          padding: 3
        }
      });
    }
    modifiers.push.apply(modifiers, (popperOptions == null ? void 0 : popperOptions.modifiers) || []);
    instance.popperInstance = createPopper(computedReference, popper, Object.assign({}, popperOptions, {
      placement: placement,
      onFirstUpdate: onFirstUpdate,
      modifiers: modifiers
    }));
  }
  function destroyPopperInstance() {
    if (instance.popperInstance) {
      instance.popperInstance.destroy();
      instance.popperInstance = null;
    }
  }
  function mount() {
    var appendTo = instance.props.appendTo;
    var parentNode; // By default, we'll append the popper to the triggerTargets's parentNode so
    // it's directly after the reference element so the elements inside the
    // tippy can be tabbed to
    // If there are clipping issues, the user can specify a different appendTo
    // and ensure focus management is handled correctly manually

    var node = getCurrentTarget();
    if (instance.props.interactive && appendTo === TIPPY_DEFAULT_APPEND_TO || appendTo === 'parent') {
      parentNode = node.parentNode;
    } else {
      parentNode = invokeWithArgsOrReturn(appendTo, [node]);
    } // The popper element needs to exist on the DOM before its position can be
    // updated as Popper needs to read its dimensions

    if (!parentNode.contains(popper)) {
      parentNode.appendChild(popper);
    }
    instance.state.isMounted = true;
    createPopperInstance();
    /* istanbul ignore else */

    {
      // Accessibility check
      warnWhen(instance.props.interactive && appendTo === defaultProps.appendTo && node.nextElementSibling !== popper, ['Interactive tippy element may not be accessible via keyboard', 'navigation because it is not directly after the reference element', 'in the DOM source order.', '\n\n', 'Using a wrapper <div> or <span> tag around the reference element', 'solves this by creating a new parentNode context.', '\n\n', 'Specifying `appendTo: document.body` silences this warning, but it', 'assumes you are using a focus management solution to handle', 'keyboard navigation.', '\n\n', 'See: https://atomiks.github.io/tippyjs/v6/accessibility/#interactivity'].join(' '));
    }
  }
  function getNestedPopperTree() {
    return arrayFrom(popper.querySelectorAll('[data-tippy-root]'));
  }
  function scheduleShow(event) {
    instance.clearDelayTimeouts();
    if (event) {
      invokeHook('onTrigger', [instance, event]);
    }
    addDocumentPress();
    var delay = getDelay(true);
    var _getNormalizedTouchSe = getNormalizedTouchSettings(),
      touchValue = _getNormalizedTouchSe[0],
      touchDelay = _getNormalizedTouchSe[1];
    if (currentInput.isTouch && touchValue === 'hold' && touchDelay) {
      delay = touchDelay;
    }
    if (delay) {
      showTimeout = setTimeout(function () {
        instance.show();
      }, delay);
    } else {
      instance.show();
    }
  }
  function scheduleHide(event) {
    instance.clearDelayTimeouts();
    invokeHook('onUntrigger', [instance, event]);
    if (!instance.state.isVisible) {
      removeDocumentPress();
      return;
    } // For interactive tippies, scheduleHide is added to a document.body handler
    // from onMouseLeave so must intercept scheduled hides from mousemove/leave
    // events when trigger contains mouseenter and click, and the tip is
    // currently shown as a result of a click.

    if (instance.props.trigger.indexOf('mouseenter') >= 0 && instance.props.trigger.indexOf('click') >= 0 && ['mouseleave', 'mousemove'].indexOf(event.type) >= 0 && isVisibleFromClick) {
      return;
    }
    var delay = getDelay(false);
    if (delay) {
      hideTimeout = setTimeout(function () {
        if (instance.state.isVisible) {
          instance.hide();
        }
      }, delay);
    } else {
      // Fixes a `transitionend` problem when it fires 1 frame too
      // late sometimes, we don't want hide() to be called.
      scheduleHideAnimationFrame = requestAnimationFrame(function () {
        instance.hide();
      });
    }
  } // ===========================================================================
  // 🔑 Public methods
  // ===========================================================================

  function enable() {
    instance.state.isEnabled = true;
  }
  function disable() {
    // Disabling the instance should also hide it
    // https://github.com/atomiks/tippy.js-react/issues/106
    instance.hide();
    instance.state.isEnabled = false;
  }
  function clearDelayTimeouts() {
    clearTimeout(showTimeout);
    clearTimeout(hideTimeout);
    cancelAnimationFrame(scheduleHideAnimationFrame);
  }
  function setProps(partialProps) {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('setProps'));
    }
    if (instance.state.isDestroyed) {
      return;
    }
    invokeHook('onBeforeUpdate', [instance, partialProps]);
    removeListeners();
    var prevProps = instance.props;
    var nextProps = evaluateProps(reference, Object.assign({}, prevProps, removeUndefinedProps(partialProps), {
      ignoreAttributes: true
    }));
    instance.props = nextProps;
    addListeners();
    if (prevProps.interactiveDebounce !== nextProps.interactiveDebounce) {
      cleanupInteractiveMouseListeners();
      debouncedOnMouseMove = debounce$1(onMouseMove, nextProps.interactiveDebounce);
    } // Ensure stale aria-expanded attributes are removed

    if (prevProps.triggerTarget && !nextProps.triggerTarget) {
      normalizeToArray(prevProps.triggerTarget).forEach(function (node) {
        node.removeAttribute('aria-expanded');
      });
    } else if (nextProps.triggerTarget) {
      reference.removeAttribute('aria-expanded');
    }
    handleAriaExpandedAttribute();
    handleStyles();
    if (onUpdate) {
      onUpdate(prevProps, nextProps);
    }
    if (instance.popperInstance) {
      createPopperInstance(); // Fixes an issue with nested tippies if they are all getting re-rendered,
      // and the nested ones get re-rendered first.
      // https://github.com/atomiks/tippyjs-react/issues/177
      // TODO: find a cleaner / more efficient solution(!)

      getNestedPopperTree().forEach(function (nestedPopper) {
        // React (and other UI libs likely) requires a rAF wrapper as it flushes
        // its work in one
        requestAnimationFrame(nestedPopper._tippy.popperInstance.forceUpdate);
      });
    }
    invokeHook('onAfterUpdate', [instance, partialProps]);
  }
  function setContent(content) {
    instance.setProps({
      content: content
    });
  }
  function show() {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('show'));
    } // Early bail-out

    var isAlreadyVisible = instance.state.isVisible;
    var isDestroyed = instance.state.isDestroyed;
    var isDisabled = !instance.state.isEnabled;
    var isTouchAndTouchDisabled = currentInput.isTouch && !instance.props.touch;
    var duration = getValueAtIndexOrReturn(instance.props.duration, 0, defaultProps.duration);
    if (isAlreadyVisible || isDestroyed || isDisabled || isTouchAndTouchDisabled) {
      return;
    } // Normalize `disabled` behavior across browsers.
    // Firefox allows events on disabled elements, but Chrome doesn't.
    // Using a wrapper element (i.e. <span>) is recommended.

    if (getCurrentTarget().hasAttribute('disabled')) {
      return;
    }
    invokeHook('onShow', [instance], false);
    if (instance.props.onShow(instance) === false) {
      return;
    }
    instance.state.isVisible = true;
    if (getIsDefaultRenderFn()) {
      popper.style.visibility = 'visible';
    }
    handleStyles();
    addDocumentPress();
    if (!instance.state.isMounted) {
      popper.style.transition = 'none';
    } // If flipping to the opposite side after hiding at least once, the
    // animation will use the wrong placement without resetting the duration

    if (getIsDefaultRenderFn()) {
      var _getDefaultTemplateCh2 = getDefaultTemplateChildren(),
        box = _getDefaultTemplateCh2.box,
        content = _getDefaultTemplateCh2.content;
      setTransitionDuration([box, content], 0);
    }
    onFirstUpdate = function onFirstUpdate() {
      var _instance$popperInsta2;
      if (!instance.state.isVisible || ignoreOnFirstUpdate) {
        return;
      }
      ignoreOnFirstUpdate = true; // reflow

      void popper.offsetHeight;
      popper.style.transition = instance.props.moveTransition;
      if (getIsDefaultRenderFn() && instance.props.animation) {
        var _getDefaultTemplateCh3 = getDefaultTemplateChildren(),
          _box = _getDefaultTemplateCh3.box,
          _content = _getDefaultTemplateCh3.content;
        setTransitionDuration([_box, _content], duration);
        setVisibilityState([_box, _content], 'visible');
      }
      handleAriaContentAttribute();
      handleAriaExpandedAttribute();
      pushIfUnique(mountedInstances, instance); // certain modifiers (e.g. `maxSize`) require a second update after the
      // popper has been positioned for the first time

      (_instance$popperInsta2 = instance.popperInstance) == null ? void 0 : _instance$popperInsta2.forceUpdate();
      invokeHook('onMount', [instance]);
      if (instance.props.animation && getIsDefaultRenderFn()) {
        onTransitionedIn(duration, function () {
          instance.state.isShown = true;
          invokeHook('onShown', [instance]);
        });
      }
    };
    mount();
  }
  function hide() {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('hide'));
    } // Early bail-out

    var isAlreadyHidden = !instance.state.isVisible;
    var isDestroyed = instance.state.isDestroyed;
    var isDisabled = !instance.state.isEnabled;
    var duration = getValueAtIndexOrReturn(instance.props.duration, 1, defaultProps.duration);
    if (isAlreadyHidden || isDestroyed || isDisabled) {
      return;
    }
    invokeHook('onHide', [instance], false);
    if (instance.props.onHide(instance) === false) {
      return;
    }
    instance.state.isVisible = false;
    instance.state.isShown = false;
    ignoreOnFirstUpdate = false;
    isVisibleFromClick = false;
    if (getIsDefaultRenderFn()) {
      popper.style.visibility = 'hidden';
    }
    cleanupInteractiveMouseListeners();
    removeDocumentPress();
    handleStyles(true);
    if (getIsDefaultRenderFn()) {
      var _getDefaultTemplateCh4 = getDefaultTemplateChildren(),
        box = _getDefaultTemplateCh4.box,
        content = _getDefaultTemplateCh4.content;
      if (instance.props.animation) {
        setTransitionDuration([box, content], duration);
        setVisibilityState([box, content], 'hidden');
      }
    }
    handleAriaContentAttribute();
    handleAriaExpandedAttribute();
    if (instance.props.animation) {
      if (getIsDefaultRenderFn()) {
        onTransitionedOut(duration, instance.unmount);
      }
    } else {
      instance.unmount();
    }
  }
  function hideWithInteractivity(event) {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('hideWithInteractivity'));
    }
    getDocument().addEventListener('mousemove', debouncedOnMouseMove);
    pushIfUnique(mouseMoveListeners, debouncedOnMouseMove);
    debouncedOnMouseMove(event);
  }
  function unmount() {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('unmount'));
    }
    if (instance.state.isVisible) {
      instance.hide();
    }
    if (!instance.state.isMounted) {
      return;
    }
    destroyPopperInstance(); // If a popper is not interactive, it will be appended outside the popper
    // tree by default. This seems mainly for interactive tippies, but we should
    // find a workaround if possible

    getNestedPopperTree().forEach(function (nestedPopper) {
      nestedPopper._tippy.unmount();
    });
    if (popper.parentNode) {
      popper.parentNode.removeChild(popper);
    }
    mountedInstances = mountedInstances.filter(function (i) {
      return i !== instance;
    });
    instance.state.isMounted = false;
    invokeHook('onHidden', [instance]);
  }
  function destroy() {
    /* istanbul ignore else */
    {
      warnWhen(instance.state.isDestroyed, createMemoryLeakWarning('destroy'));
    }
    if (instance.state.isDestroyed) {
      return;
    }
    instance.clearDelayTimeouts();
    instance.unmount();
    removeListeners();
    delete reference._tippy;
    instance.state.isDestroyed = true;
    invokeHook('onDestroy', [instance]);
  }
}
function tippy(targets, optionalProps) {
  if (optionalProps === void 0) {
    optionalProps = {};
  }
  var plugins = defaultProps.plugins.concat(optionalProps.plugins || []);
  /* istanbul ignore else */

  {
    validateTargets(targets);
    validateProps(optionalProps, plugins);
  }
  bindGlobalEventListeners();
  var passedProps = Object.assign({}, optionalProps, {
    plugins: plugins
  });
  var elements = getArrayOfElements(targets);
  /* istanbul ignore else */

  {
    var isSingleContentElement = isElement(passedProps.content);
    var isMoreThanOneReferenceElement = elements.length > 1;
    warnWhen(isSingleContentElement && isMoreThanOneReferenceElement, ['tippy() was passed an Element as the `content` prop, but more than', 'one tippy instance was created by this invocation. This means the', 'content element will only be appended to the last tippy instance.', '\n\n', 'Instead, pass the .innerHTML of the element, or use a function that', 'returns a cloned version of the element instead.', '\n\n', '1) content: element.innerHTML\n', '2) content: () => element.cloneNode(true)'].join(' '));
  }
  var instances = elements.reduce(function (acc, reference) {
    var instance = reference && createTippy(reference, passedProps);
    if (instance) {
      acc.push(instance);
    }
    return acc;
  }, []);
  return isElement(targets) ? instances[0] : instances;
}
tippy.defaultProps = defaultProps;
tippy.setDefaultProps = setDefaultProps;
tippy.currentInput = currentInput;

// every time the popper is destroyed (i.e. a new target), removing the styles
// and causing transitions to break for singletons when the console is open, but
// most notably for non-transform styles being used, `gpuAcceleration: false`.

Object.assign({}, applyStyles$1, {
  effect: function effect(_ref) {
    var state = _ref.state;
    var initialStyles = {
      popper: {
        position: state.options.strategy,
        left: '0',
        top: '0',
        margin: '0'
      },
      arrow: {
        position: 'absolute'
      },
      reference: {}
    };
    Object.assign(state.elements.popper.style, initialStyles.popper);
    state.styles = initialStyles;
    if (state.elements.arrow) {
      Object.assign(state.elements.arrow.style, initialStyles.arrow);
    } // intentionally return no cleanup function
    // return () => { ... }
  }
});
var mouseCoords = {
  clientX: 0,
  clientY: 0
};
var activeInstances = [];
function storeMouseCoords(_ref) {
  var clientX = _ref.clientX,
    clientY = _ref.clientY;
  mouseCoords = {
    clientX: clientX,
    clientY: clientY
  };
}
function addMouseCoordsListener(doc) {
  doc.addEventListener('mousemove', storeMouseCoords);
}
function removeMouseCoordsListener(doc) {
  doc.removeEventListener('mousemove', storeMouseCoords);
}
var followCursor = {
  name: 'followCursor',
  defaultValue: false,
  fn: function fn(instance) {
    var reference = instance.reference;
    var doc = getOwnerDocument(instance.props.triggerTarget || reference);
    var isInternalUpdate = false;
    var wasFocusEvent = false;
    var isUnmounted = true;
    var prevProps = instance.props;
    function getIsInitialBehavior() {
      return instance.props.followCursor === 'initial' && instance.state.isVisible;
    }
    function addListener() {
      doc.addEventListener('mousemove', onMouseMove);
    }
    function removeListener() {
      doc.removeEventListener('mousemove', onMouseMove);
    }
    function unsetGetReferenceClientRect() {
      isInternalUpdate = true;
      instance.setProps({
        getReferenceClientRect: null
      });
      isInternalUpdate = false;
    }
    function onMouseMove(event) {
      // If the instance is interactive, avoid updating the position unless it's
      // over the reference element
      var isCursorOverReference = event.target ? reference.contains(event.target) : true;
      var followCursor = instance.props.followCursor;
      var clientX = event.clientX,
        clientY = event.clientY;
      var rect = reference.getBoundingClientRect();
      var relativeX = clientX - rect.left;
      var relativeY = clientY - rect.top;
      if (isCursorOverReference || !instance.props.interactive) {
        instance.setProps({
          // @ts-ignore - unneeded DOMRect properties
          getReferenceClientRect: function getReferenceClientRect() {
            var rect = reference.getBoundingClientRect();
            var x = clientX;
            var y = clientY;
            if (followCursor === 'initial') {
              x = rect.left + relativeX;
              y = rect.top + relativeY;
            }
            var top = followCursor === 'horizontal' ? rect.top : y;
            var right = followCursor === 'vertical' ? rect.right : x;
            var bottom = followCursor === 'horizontal' ? rect.bottom : y;
            var left = followCursor === 'vertical' ? rect.left : x;
            return {
              width: right - left,
              height: bottom - top,
              top: top,
              right: right,
              bottom: bottom,
              left: left
            };
          }
        });
      }
    }
    function create() {
      if (instance.props.followCursor) {
        activeInstances.push({
          instance: instance,
          doc: doc
        });
        addMouseCoordsListener(doc);
      }
    }
    function destroy() {
      activeInstances = activeInstances.filter(function (data) {
        return data.instance !== instance;
      });
      if (activeInstances.filter(function (data) {
        return data.doc === doc;
      }).length === 0) {
        removeMouseCoordsListener(doc);
      }
    }
    return {
      onCreate: create,
      onDestroy: destroy,
      onBeforeUpdate: function onBeforeUpdate() {
        prevProps = instance.props;
      },
      onAfterUpdate: function onAfterUpdate(_, _ref2) {
        var followCursor = _ref2.followCursor;
        if (isInternalUpdate) {
          return;
        }
        if (followCursor !== undefined && prevProps.followCursor !== followCursor) {
          destroy();
          if (followCursor) {
            create();
            if (instance.state.isMounted && !wasFocusEvent && !getIsInitialBehavior()) {
              addListener();
            }
          } else {
            removeListener();
            unsetGetReferenceClientRect();
          }
        }
      },
      onMount: function onMount() {
        if (instance.props.followCursor && !wasFocusEvent) {
          if (isUnmounted) {
            onMouseMove(mouseCoords);
            isUnmounted = false;
          }
          if (!getIsInitialBehavior()) {
            addListener();
          }
        }
      },
      onTrigger: function onTrigger(_, event) {
        if (isMouseEvent(event)) {
          mouseCoords = {
            clientX: event.clientX,
            clientY: event.clientY
          };
        }
        wasFocusEvent = event.type === 'focus';
      },
      onHidden: function onHidden() {
        if (instance.props.followCursor) {
          unsetGetReferenceClientRect();
          removeListener();
          isUnmounted = true;
        }
      }
    };
  }
};
tippy.setDefaultProps({
  render: render
});

var t = function () {
  return t = Object.assign || function (t) {
    for (var e, n = 1, i = arguments.length; n < i; n++) for (var r in e = arguments[n]) Object.prototype.hasOwnProperty.call(e, r) && (t[r] = e[r]);
    return t;
  }, t.apply(this, arguments);
};
"function" == typeof SuppressedError && SuppressedError;
var e$1;
!function (t) {
  t[t.render = 0] = "render", t[t.selected = 1] = "selected";
}(e$1 || (e$1 = {}));
var n = function () {
  function e(e, n) {
    void 0 === n && (n = {}), this._initStatus = !1, this.onArr = [], this.prefix = "mark", this._selectionchangeTimeOut = 0, this.markRange = [], this.sectionChangeDelay = 100, this.lineColor = "#F17F53", this.lineWidth = 2, this.eventController = new AbortController(), this.options = {
      idPrefix: "mark-svg"
    };
    var i = null;
    if ("string" == typeof e ? i = document.getElementById(e) : e.nodeType == Node.ELEMENT_NODE && (i = e), !i) throw new Error("target错误 或 未找到对应元素");
    this.targetElement = i, this.onArr = [], this.options = t(t({}, this.options), n);
    var r = n.sectionChangeDelay,
      o = n.lineColor,
      a = n.lineWidth;
    r && (this.sectionChangeDelay = r), o && (this.lineColor = o), a && (this.lineWidth = a);
  }
  return e.prototype.render = function () {
    var t = this;
    if (!this._initStatus) {
      var e = this.targetElement.parentNode,
        n = document.createElement("div");
      n.className = "mark-view", n.style.position = "relative", n.style.width = "fit-content", n.style.height = "fit-content", n.style.width = "100%";
      var i = document.createElement("style");
      i.innerHTML = "\n              @keyframes blink {\n                0%, 100% { opacity: 0; }\n                50% { opacity: 1; }\n              }\n              .blink {\n                animation: blink 1s linear 0;\n                animation-iteration-count: 3;\n              }\n            ", n.appendChild(i);
      var r = this.targetElement.cloneNode(!0);
      this.targetNode = r, n.appendChild(r);
      var o = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      this.svgDom = o, o.id = "".concat(this.options.idPrefix || "mark-svg").concat(this.options.id ? "_#_" + this.options.id : ""), o.style.position = "absolute", o.style.left = "0px", o.style.top = "0px", o.style.width = "100%", o.style.height = "100%", o.style.mixBlendMode = "multiply", o.setAttribute("width", "100%"), o.setAttribute("height", "100%"), o.setAttribute("pointer-events", "none"), n.appendChild(o), null == e || e.replaceChild(n, this.targetElement), this.rootElement = n, this.targetNodeRect = this.rootElement.getBoundingClientRect(), this.targetNode.addEventListener("click", function (e) {
        if (t.rootElement) {
          var n = t.rootElement.getBoundingClientRect(),
            i = e,
            r = i.pageX - n.x,
            o = i.pageY - n.y,
            a = t.markRange.filter(function (t) {
              return t.position.filter(function (t) {
                return r > t.x1 && r < t.x2 && o > t.y1 && o < t.y2;
              }).length > 0;
            });
          a.length > 0 && a.forEach(function (t) {
            var e = new Event("click");
            t.node.dispatchEvent(e);
          });
        }
      }, {
        signal: this.eventController.signal
      }), this._handletListener(), this._update(), this._handleOn("render", this.rootElement);
    }
    this._initStatus = !0;
  }, e.prototype.update = function () {
    var t = this;
    this.markRange.forEach(function (e) {
      var n = t.svgDom.querySelector("[data-id='".concat(e.markStr, "']"));
      if (n && (n.innerHTML = "", !t._handleEfiToRange(e.markStr).collapsed)) {
        var i = e.range.getClientRects(),
          r = [],
          o = document.createDocumentFragment(),
          a = t._detachRangeRects(i),
          l = a.textRunRects;
        a.rowRects;
        for (var s = 0; s < l.length; s++) {
          var h = l[s],
            c = {
              line: "underline" === e.type ? e.styleScheme : "",
              rect: "highlight" === e.type ? e.styleScheme : ""
            },
            d = t._handleCreateSvgG(h, o, e.type, c);
          d.group;
          var u = d.position;
          r.push(u);
        }
        n.appendChild(o), e.position = r;
      }
    });
  }, e.prototype.add = function (t) {
    var e,
      n,
      i = t.type,
      r = null !== (e = t.click) && void 0 !== e ? e : null,
      o = this._handleEfiToRange(t.markStr);
    if (!o.collapsed) {
      var a = o.getClientRects(),
        l = document.createElementNS("http://www.w3.org/2000/svg", "g"),
        s = "";
      t.className && (s = t.className.split("__")[1], l.classList.add(t.className)), this.options.id && l.setAttribute("id", "".concat(this.options.id, "_#_").concat(t.markStr)), l.setAttribute("data-id", t.markStr), l.setAttribute("data-styleScheme", s), l.setAttribute("data-type", t.type), l.setAttribute("opacity", "0.5");
      var h = [],
        c = document.createDocumentFragment(),
        d = this._detachRangeRects(a),
        u = d.textRunRects;
      d.rowRects;
      for (var p = 0; p < u.length; p++) {
        var g = u[p],
          f = {
            line: "underline" === t.type ? s : "",
            rect: "highlight" === t.type ? s : ""
          },
          m = this._handleCreateSvgG(g, c, i, f);
        m.group;
        var v = m.position;
        h.push(v);
      }
      l.appendChild(c), this.markRange.push({
        markStr: t.markStr,
        range: o,
        type: i,
        styleScheme: s,
        position: h,
        node: l,
        data: null !== (n = t.data) && void 0 !== n ? n : {}
      }), r && "function" == typeof r && l.addEventListener("click", function (t) {
        var e = this.getAttribute("data-id");
        r.apply(this, [t, e]);
      }, {
        signal: this.eventController.signal
      }), this.svgDom.appendChild(l);
    }
  }, e.prototype.remove = function (t, e) {
    var n;
    if (!t) throw new Error("未传入MarkEfiCode");
    for (var i = this.markRange.map(function (t, e) {
        return {
          item: t,
          index: e
        };
      }).filter(function (n) {
        return n.item.markStr == t && (!e || n.item.type == e);
      }), r = 0, o = this.svgDom, a = 0; a < i.length; a++) {
      var l = i[a].index - r,
        s = i[a].item;
      null === (n = o.querySelector("[data-id='".concat(s.markStr, "']"))) || void 0 === n || n.remove(), this.markRange.splice(l, 1), r++;
    }
  }, e.prototype.animate = function (t, e) {
    if (void 0 === e && (e = {
      type: "blink"
    }), !t) throw new Error("未传入动画目标target");
    var n = this.svgDom.querySelectorAll('g[data-id="'.concat(t, '"] > rect'));
    Array.from(n).forEach(function (t, n) {
      t.classList.add(e.type), t.addEventListener("animationend", function () {
        t.classList.contains("blink") && t.classList.remove("blink");
      });
    });
  }, e.prototype.highlight = function (t, e, n, i) {
    if (void 0 === i && (i = {}), !t) throw new Error("未传入MarkEfiCode");
    this.add({
      type: "highlight",
      markStr: t,
      className: e,
      click: n,
      data: i
    });
  }, e.prototype.underline = function (t, e, n, i) {
    if (void 0 === i && (i = {}), !t) throw new Error("未传入MarkEfiCode");
    this.add({
      type: "underline",
      markStr: t,
      className: e,
      click: n,
      data: i
    });
  }, e.prototype.show = function (t) {
    var e, n;
    t ? null === (e = this.svgDom) || void 0 === e || e.querySelectorAll('g[data-type="'.concat(t, '"]')).forEach(function (t) {
      t.setAttribute("display", "auto");
    }) : null === (n = this.svgDom) || void 0 === n || n.querySelectorAll("g").forEach(function (t) {
      t.setAttribute("display", "auto");
    });
  }, e.prototype.hide = function (t) {
    var e, n;
    t ? null === (e = this.svgDom) || void 0 === e || e.querySelectorAll('g[data-type="'.concat(t, '"]')).forEach(function (t) {
      t.setAttribute("display", "none");
    }) : null === (n = this.svgDom) || void 0 === n || n.querySelectorAll("g").forEach(function (t) {
      t.setAttribute("display", "none");
    });
  }, e.prototype.clear = function (t) {
    for (var e, n = this.markRange.map(function (t, e) {
        return {
          item: t,
          index: e
        };
      }).filter(function (e) {
        return !t || e.item.type == t;
      }), i = 0, r = this.svgDom, o = 0; o < n.length; o++) {
      var a = n[o].index - i,
        l = n[o].item;
      null === (e = r.querySelector("[data-id='".concat(l.markStr, "']"))) || void 0 === e || e.remove(), this.markRange.splice(a, 1), i++;
    }
  }, e.prototype.on = function (t, e) {
    this.onArr.push({
      key: t,
      func: e
    });
  }, e.prototype.destroy = function () {
    this.eventController.abort(), this.rootElement = void 0, this.targetNode = void 0, this.svgDom = void 0, this.markRange = [], this.onArr = [], this._initStatus = !1;
  }, e.prototype.getMarks = function () {
    return this.markRange.map(function (t) {
      return {
        markStr: t.markStr,
        type: t.type,
        data: t.data
      };
    });
  }, e.prototype._update = function () {
    var t = this;
    window.addEventListener("resize", function () {
      requestAnimationFrame(function () {
        t.rootElement && (t.targetNodeRect = t.rootElement.getBoundingClientRect(), t.update());
      });
    }, {
      signal: this.eventController.signal
    });
  }, e.prototype._handletListener = function () {
    var t = this,
      e = this;
    document.addEventListener("selectionchange", function () {
      for (var n = [], i = 0; i < arguments.length; i++) n[i] = arguments[i];
      clearTimeout(t._selectionchangeTimeOut), t._selectionchangeTimeOut = setTimeout(function () {
        t._handleSelected.apply(t, n);
      }, e.sectionChangeDelay || 250);
    }, {
      signal: e.eventController.signal
    });
  }, e.prototype._handleSelected = function () {
    var t,
      e,
      n,
      i = window.getSelection();
    if (i && !i.isCollapsed) {
      var r = i.getRangeAt(0),
        o = this._handleSelectRangePostion(r),
        a = r.getClientRects(),
        l = a[0],
        s = a[a.length - 1],
        h = (null === (t = this.rootElement) || void 0 === t ? void 0 : t.getBoundingClientRect()) || this.targetNodeRect || {},
        c = null !== (e = null == h ? void 0 : h.top) && void 0 !== e ? e : 0,
        d = null !== (n = null == h ? void 0 : h.left) && void 0 !== n ? n : 0,
        u = {
          top_left: [l.left - d, l.top - c],
          top_center: [l.left + l.width / 2 - d, l.top - c],
          top_right: [l.right - d, l.top - c],
          center_left: [s.left - d, l.top + (s.bottom - l.top) / 2 - c],
          center: [s.left + (l.right - s.left) / 2 - d, l.top + (s.bottom - l.top) / 2 - c],
          center_right: [l.right - d, l.top + (s.bottom - l.top) / 2 - c],
          bottom_left: [s.left - d, s.bottom - c],
          bottom_center: [s.left + s.width / 2 - d, s.top + s.height - c],
          bottom_right: [s.right - d, s.bottom - c]
        };
      this._handleOn("selected", {
        markStr: o,
        range: r,
        position: u
      });
    }
  }, e.prototype._handleSelectRangePostion = function (t) {
    var e = this._handleSelectNodePostion(t.startContainer),
      n = t.startOffset,
      i = this._handleSelectNodePostion(t.endContainer),
      r = t.endOffset;
    return "".concat(this.prefix, "(").concat(e, ":").concat(n, ",").concat(i, ":").concat(r, ")");
  }, e.prototype._handleSelectNodePostion = function (t) {
    for (var e = [], n = t; n != this.targetNode;) {
      for (var i = 0; n.previousSibling;) i++, n = n.previousSibling;
      if (e.push(i), null == n.parentNode) break;
      n = n.parentNode;
    }
    return e.reverse().join("/");
  }, e.prototype._handleEfiToRange = function (t) {
    var e = document.createRange(),
      n = this._handleCheckEfi(t);
    if (!n) return e;
    for (var i = this.targetNode, r = null, o = 0; o < n.start_arr.length; o++) {
      var a = n.start_arr[o];
      i = r = i.childNodes[a];
    }
    n.start_node = r, i = this.targetNode;
    for (o = 0; o < n.end_arr.length; o++) {
      a = n.end_arr[o];
      i = r = i.childNodes[a];
    }
    return n.end_node = r, e.setStart(n.start_node, n.start_offset), e.setEnd(n.end_node, n.end_offset), e;
  }, e.prototype._handleOn = function (t, e) {
    void 0 === e && (e = {}), this.onArr.filter(function (e) {
      return e.key == t;
    }).forEach(function (t) {
      null == t || t.func(e);
    });
  }, e.prototype._handleCheckEfi = function (t) {
    var e = new RegExp("".concat(this.prefix, "(.*,.*)")),
      n = t.match(e);
    if (!n || n.length < 2) return null;
    var i = n[1],
      r = (i = i.replace("(", "").replace(")", "")).split(",");
    return {
      start_arr: r[0].split(":")[0].split("/").map(function (t) {
        return Number(t);
      }),
      start_offset: Number(r[0].split(":")[1]),
      end_arr: r[1].split(":")[0].split("/").map(function (t) {
        return Number(t);
      }),
      end_offset: Number(r[1].split(":")[1]),
      start_node: null,
      end_node: null
    };
  }, e.prototype._detachRangeRects = function (t) {
    for (var e = [], n = [], i = [], r = 0, o = t.length; r < o; r++) {
      var a = !0,
        l = t[r],
        s = r ? t[r - 1] : null;
      s && s.x === l.x && l.y < s.y + s.height && (a = !1), a || (i[r - 1] = !1), i.push(!0);
    }
    return i.forEach(function (i, r) {
      i ? e.push(t[r]) : n.push(t[r]);
    }), {
      textRunRects: e,
      rowRects: n
    };
  }, e.prototype._handleCreateRect = function (t, e) {
    var n;
    void 0 === e && (e = {
      opcity: !1
    });
    var i = document.createElementNS("http://www.w3.org/2000/svg", "rect"),
      r = (null === (n = this.rootElement) || void 0 === n ? void 0 : n.getBoundingClientRect()) || this.targetNodeRect || {},
      o = t.x - r.x,
      a = t.y - r.y;
    return i.setAttribute("x", o.toString()), i.setAttribute("y", a.toString()), i.setAttribute("width", t.width.toString()), i.setAttribute("height", t.height.toString()), i.setAttribute("fill", e.isPlaceholder ? this.options.animationColor || "#D6D6D6" : "#FFFF00"), i.setAttribute("mix-blend-mode", "multiply"), e.opcity && i.setAttribute("opacity", "0"), i;
  }, e.prototype._handleCreateLine = function (t, e) {
    var n,
      i = document.createElementNS("http://www.w3.org/2000/svg", "line"),
      r = (null === (n = this.rootElement) || void 0 === n ? void 0 : n.getBoundingClientRect()) || this.targetNodeRect || {},
      o = this.lineColor,
      a = t.x - r.x,
      l = t.y - r.y + t.height;
    "dashed" === (null == e ? void 0 : e.styleScheme) && (l += 3, o = "#25B774");
    var s = a + t.width,
      h = l;
    return i.setAttribute("x1", a.toString()), i.setAttribute("y1", l.toString()), i.setAttribute("x2", s.toString()), i.setAttribute("y2", h.toString()), i.setAttribute("stroke-width", "".concat(this.lineWidth) || "2"), i.setAttribute("stroke", o || "#03a9f4"), i.setAttribute("mix-blend-mode", "multiply"), "dashed" === (null == e ? void 0 : e.styleScheme) && i.setAttribute("stroke-dasharray", "6,3"), i;
  }, e.prototype._handleCreateSvgG = function (t, e, n, i) {
    var r,
      o = (null === (r = this.rootElement) || void 0 === r ? void 0 : r.getBoundingClientRect()) || this.targetNodeRect || {},
      a = document.createDocumentFragment();
    if ("highlight" == n) {
      var l = this._handleCreateRect(t, {
        styleScheme: i.rect
      });
      e.appendChild(l);
    }
    if ("underline" == n) {
      l = this._handleCreateRect(t, {
        opcity: !0,
        isPlaceholder: !0
      });
      var s = this._handleCreateLine(t, {
        styleScheme: i.line
      });
      e.appendChild(l), e.appendChild(s);
    }
    var h = t.x - o.x,
      c = t.y - o.y;
    return {
      group: a,
      position: {
        x1: h,
        y1: c,
        x2: h + t.width,
        y2: c + t.height
      }
    };
  }, e;
}();

//
var script$5x = {
  name: 'XmlTextRender',
  components: {
    addFile: __vue_component__$5y,
    XmlTextDialogRender: __vue_component__$5A,
    [ImagePreview$1.Component.name]: ImagePreview$1.Component
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    pBlockData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    dataIndex: Number,
    //数据下标
    containerClass: String,
    extendParams: {
      type: Object,
      default: function () {
        return {
          //默认开启编辑器
          defaultOpenEditor: false
        };
      }
    },
    headers: {
      // 上传文件头部
      type: Object,
      default: () => {}
    },
    action: {
      // 上传文件地址
      type: String,
      default: ''
    },
    data: {
      // 上传文件附带的额外参数
      type: Object,
      default: () => {}
    },
    textBookData: Object //数字教材信息
  },
  data() {
    return {
      blockId: `blockId${v4()}`,
      propertyComponentsName: 'XmlTextProperty',
      editorId: `editor-container${`${Math.random()}`.replaceAll('0.', '')}`,
      blockData: {
        html: ''
      },
      imageViewerTitles: [],
      imageViewerSrcs: [],
      inlineWordsModal: {
        visible: false,
        title: '',
        content: '',
        stylemode: ''
      },
      markRootId: `mark-view__${v4()}`,
      markEfiStr: '',
      markInstance: null,
      activeTippy: null,
      tippyTplBarId: `tipbar-tpl__${v4()}`,
      tippyTplNoteId: `tipnote-tpl__${v4()}`,
      actionCopyId: `act-copy__${v4()}`,
      actionNoteId: `act-note__${v4()}`,
      actionHighlightId: `act-light__${v4()}`,
      actionLinkId: `act-light__${v4()}`,
      actionEncyclopediasId: `act-light__${v4()}`,
      actionDictionaryId: `act-light__${v4()}`,
      addTeacherResourcesId: `add-teacher-resources__${v4()}`,
      noteInputId: `note_input__${v4()}`,
      actNoteCancelId: `act-cancel__${v4()}`,
      actNoteConfirmId: `act-confirm__${v4()}`,
      editNoteTplId: `editnote-tpl__${v4()}`,
      actNoteDeleteId: `act-delete__${v4()}`,
      actNoteEditId: `act-edit__${v4()}`,
      noteOperateMode: '',
      tempMarkModel: {},
      noteData: {
        selectionText: '',
        content: ''
      },
      noteEditData: {
        selectionText: '',
        content: ''
      },
      tempMarkEfiStr: '',
      isMobile: false,
      notePopupVisible: false
    };
  },
  inject: {
    getProviderUserType: {
      from: 'getProviderUserType',
      default: () => () => ''
    },
    getProviderInsideIndex: {
      from: 'getProviderInsideIndex',
      default: () => () => -1
    },
    getSingleGroupLocation: {
      from: 'getSingleGroupLocation',
      default: () => () => ''
    },
    getToolbarVisible: {
      from: 'getToolbarVisible',
      default: () => () => false
    },
    getReaderBoxId: {
      from: 'getReaderBoxId',
      default: () => () => ''
    },
    getTeacherNoteList: {
      from: 'getTeacherNoteList',
      default: () => () => []
    },
    getStudentNoteList: {
      from: 'getStudentNoteList',
      default: () => () => []
    },
    getHighlightList: {
      from: 'getHighlightList',
      default: () => () => []
    },
    getKeyboardShow: {
      from: 'getKeyboardShow',
      default: () => () => false
    },
    getInitialLoad: {
      from: 'getInitialLoad',
      default: () => () => false
    },
    getResourceBasisPath: {
      from: 'getResourceBasisPath',
      default: () => () => ''
    },
    getSysEvn: {
      from: 'getSysEvn',
      default: () => () => {}
    }
  },
  computed: {
    feedback() {
      return {
        ruleIndex: this.ruleIndex,
        dataName: this.blockDataName,
        data: this.pBlockData,
        dataIndex: this.dataIndex,
        blockId: this.blockId
      };
    },
    userType() {
      return this.getProviderUserType();
    },
    insideIndex() {
      return this.getProviderInsideIndex();
    },
    groupLocation() {
      return this.getSingleGroupLocation();
    },
    toolbarVisible() {
      return this.getToolbarVisible();
    },
    readerBoxId() {
      return this.getReaderBoxId();
    },
    keyboardShow() {
      return this.getKeyboardShow();
    },
    evn() {
      return this.getSysEvn() || {
        tippy: {}
      };
    },
    mergeMarkList() {
      let teacherNoteList = this.getTeacherNoteList() || [],
        studentNoteList = this.getStudentNoteList() || [],
        highlightList = this.getHighlightList() || [];
      return [...teacherNoteList.filter(item => item.location === this.groupLocation), ...studentNoteList.filter(item => item.location === this.groupLocation), ...highlightList.filter(item => item.location === this.groupLocation)];
    },
    resourceBasisPath() {
      return this.getResourceBasisPath();
    }
  },
  watch: {
    mergeMarkList: {
      handler(newValue) {
        // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'教师笔记回显0',newValue,this.markInstance)
        // this.$nextTick(() => {
        //   if (newValue?.length && this.markInstance) {
        //     this.updateMarkView()
        //   }
        // })
      },
      immediate: true,
      deep: true
    }
  },
  created() {},
  mounted() {
    // 兼容一期文本弹窗=====
    this.$nextTick(() => {
      setTimeout(() => {
        jquery('a').attr('target', '_self');
      }, 2000);
      window.inlineWordsShow = uuid => {
        const ele = document.getElementById(`words__${uuid}`);
        const {
          title = '',
          content = ''
        } = ele.dataset || {};
        this.inlineWordsModal = {
          title,
          content,
          visible: true
        };
      };
    });
    // ==================
    let _this = this;
    this.blockData = this.pBlockData;
    if (!this.blockData.html) {
      this.blockData.html = '<span>暂无内容</span>';
    }
    `#${this.blockId} .richtext-reset-wrapper--view`;
    let mceMagicLinkRegistered = window.customElements?.get('magic-link');
    if (!mceMagicLinkRegistered) {
      window.customElements.whenDefined('magic-link').then(res => {
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '富文本组件MCE注册完成', res);
      });
      window.customElements.define('magic-link', MagicLink);
    }
    let containerSelector = `#${this.blockId}.richtext-reset-container`;
    this.$nextTick(() => {
      jquery(containerSelector).on('click', 'img', function (e) {
        let target = jquery(e.target);
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '事件对象', e, target, target.index('img[preview]'));
        let title = target.attr('alt');
        let src = target.attr('src');
        let notallowgallery = target.attr('notallowgallery');
        if (notallowgallery) {
          return;
        }
        _this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: src,
            title: title
          }]
        });
      });
      jquery(containerSelector).on('click', 'magic-link', function (e) {
        let {
          plaintext,
          title,
          content,
          type,
          isPlaying,
          stylemode
        } = e.target.dataset;
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'magic-link点击', e, e.target.dataset);
        if (_this.mode === 'editor') {
          _this.$message.info('当前内容仅支持在预览模式下查看');
          return;
        }
        if (type === 'image') {
          // 魔链图片

          _this.$xmlImgPreview({
            multiple: true,
            nowImgIndex: 0,
            imgList: [{
              url: content,
              title: plaintext
            }]
          });
        }
        if (type === 'audio') {
          // 魔链音频
          _this.$EventBus.$emit('handleMagicLinkAudioPlay', {
            plaintext,
            title,
            content,
            type,
            isPlaying,
            targetId: e.target.id,
            callback: audio => {
              e.target.setAttribute('data-isplaying', audio.paused ? 'false' : 'true');
            }
          });
        }
        if (type === 'richtext') {
          content = content.replace(/text\-align:\s?justify/g, 'text-align:left');
          // 魔链富文本
          _this.inlineWordsModal = {
            title,
            content,
            stylemode,
            visible: true
          };
        }
        if (type === 'sheet') {
          // 插入活页
          window.open(process.env.VUE_APP_TEXTBOOK_WEB + 'previewLooseLeaf?pageType=' + _this.pageType + '&businessType=looseLeaf' + '&looseLeafId=' + content + '&isTrial=' + true + '&uuid=' + v4());
        }
      });
      jquery(containerSelector).on('openTarget', 'magic-link', function (e) {
        // let {plaintext,title,content,type,isPlaying,stylemode} = e.target.dataset
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'leaflet target点击', e, e.detail);
      });
      jquery(`#${this.blockId} .inline-link-wrap`).each(function (i) {
        let parent = jquery(this).parent();
        // let indent = parent ? parent.css("text-indent") : '' // 此处使用css()方法获取属性值有内部bug
        let elem = parent.length ? parent[0] : null;
        if (elem) {
          let indent = getComputedStyle(elem, null)['textIndent'];
          let hackStyle = jquery(this).attr('style') + `max-width:calc(100% - ${indent})`;
          jquery(this).attr('style', hackStyle);
        }
      });
      jquery(`#${this.blockId} :not(.inline-link-wrap) img`).each(function (i) {
        let parent = jquery(this).parent();
        let elem = parent.length ? parent[0] : null;
        if (elem) {
          let indent = getComputedStyle(elem, null)['textIndent'];
          let hackStyle = jquery(this).attr('style') + `max-width:calc(100% - ${indent}) !important`;
          jquery(this).attr('style', hackStyle);
        }
      });
      if ('ontouchstart' in window || 'onmsgesturechange' in window) {
        this.isMobile = true;
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '当前环境:移动端');
      } else {
        this.isMobile = false;
        console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '当前环境:PC端');
      }

      // @pos 初始化tippy
      tippy.setDefaultProps({
        plugins: [followCursor]
      });
      _this.activeTippy = tippy(`#${_this.blockId}`, {
        content: '',
        placement: 'top',
        trigger: 'manual',
        theme: 'light',
        animation: 'shift-away',
        interactive: true,
        allowHTML: true,
        // followCursor: this.isMobile ? true : 'initial',
        followCursor: 'initial',
        onShow(instance) {
          _this.$EventBus.$emit('tippyChange', {
            state: true
          });
        },
        onHide(instance) {
          _this.$EventBus.$emit('tippyChange', {
            state: false
          });
        }
      });
      function findParentByClassName(node, className) {
        if (node && !(node.nodeType == 1 && node.tagName.toLowerCase() === 'body')) {
          while (node) {
            let isBody = node && node.nodeType == 1 && node.tagName.toLowerCase() == 'body';
            let hasClass = node && node.nodeType == 1 && node.classList.contains(className);
            if (hasClass || isBody) {
              return !hasClass ? null : node;
            }
            node = node.parentNode;
          }
        }
        return null;
      }
      if (this.userType && this.evn.isAddMark && this.pBlockData.html.indexOf('<pre') === -1) {
        this.markInstance = new n(this.markRootId, {
          id: this.currentRule.xmlCompRef
        });
        let timer = null;
        this.markInstance.on('selected', data => {
          if (this.insideIndex === this.ruleIndex) {
            let {
              startContainer,
              endContainer
            } = data.range;
            let startParent = startContainer && findParentByClassName(startContainer, 'richtext-reset-wrapper--view');
            let endParent = endContainer && findParentByClassName(endContainer, 'richtext-reset-wrapper--view');
            if (startParent && endParent && startParent !== endParent) {
              // _this.activeTippy[0].setContent('暂不支持跨组件标注')
              _this.activeTippy[0].setContent(`<span style="color:#EE3751;">暂不支持跨组件标注</span>`);
              _this.activeTippy[0].show();
              let preOperateTippy = document.getElementById(`tippy-${_this.activeTippy[0].id - 1}`);
              preOperateTippy && preOperateTippy.remove();
              setTimeout(handler => {
                _this.hideTippy();
              }, 1500);
              return;
            }
            _this.markEfiStr = data.markStr;
            if (_this.tempMarkEfiStr) {
              this.markInstance.remove(_this.tempMarkEfiStr, 'underline');
            }
            if (timer) {
              clearTimeout(timer);
            }
            if (this.isMobile) {
              _this.activeTippy[0] && _this.hideTippy();
              timer = setTimeout(() => {
                _this.handleMouseUp();
              }, 200);
            }
          }
        });
        this.markInstance.render();
        this.$nextTick(() => {
          // console.log('绑定监听',_this.groupLocation);
          this.$EventBus.$on(`previewRepaint-${_this.groupLocation}`, () => {
            setTimeout(() => {
              _this.markInstance.update();
            }, 10);
          });
          this.$EventBus.$on(`markJumpIn-${_this.groupLocation}`, target => {
            setTimeout(() => {
              if (target.location === _this.groupLocation && target.blockIndex === _this.ruleIndex) {
                _this.markInstance.animate(target.markEfiCode);
              }
            }, 10);
          });
          this.$EventBus.$on(`clickToolbarTip-${_this.tippyTplBarId}`, function (data) {
            if (data.type == 'copy' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.actionCopy();
            } else if (data.type == 'note' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.actionAddNote();
            } else if (data.type == 'highlight' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.actionAddHighlight();
            } else if (data.type == 'link' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.actionAddLink();
            } else if (data.type == 'dictionary' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.openDictionary();
            } else if (data.type == 'encyclopedias' && data.tippyTplBarId == _this.tippyTplBarId) {
              _this.openEncyclopedias();
            }
            _this.$EventBus.$emit('selectionTextChange', {
              state: false,
              tippyTplBarId: _this.tippyTplBarId
            });
            _this.$EventBus.$emit('tippyChange', {
              state: false
            });
          });
          this.$EventBus.$on(`underlineSign-${_this.groupLocation}`, function (data) {
            // this.noteOperateMode = 'add'
            // let defaultUnderlineStyle = 'mk-underline__default'
            if (data.operationType == 'add' && _this.groupLocation == data.location) {
              _this.markInstance.underline(data.markEfiCode, data.styleType, e => {
              });
              _this.tempMarkEfiStr = '';
            } else if (data.operationType == 'remove' && _this.groupLocation == data.location) {
              _this.markInstance.remove(data.markEfiCode, data.signType);
            }
          });
          this.$once('hook:beforeDestroy', () => {
            // console.log('销毁监听',_this.groupLocation);
            _this.$EventBus.$off(`clickToolbarTip-${_this.tippyTplBarId}`);
            _this.$EventBus.$off(`previewRepaint-${_this.groupLocation}`);
            _this.$EventBus.$off(`markJumpIn-${_this.groupLocation}`);
            _this.$EventBus.$off(`underlineSign-${_this.groupLocation}`);
          });
          this.updateCodeBlock();
          setTimeout(() => {
            this.updateMarkView();
          }, this.getInitialLoad() ? 1000 : 0);
        });
      } else {
        this.updateCodeBlock();
      }
    });
  },
  beforeDestroy() {
    if (this.markInstance) {
      this.markInstance.destroy();
    }
    if (this.activeTippy && this.activeTippy[0]) {
      this.activeTippy[0].destroy();
    }
  },
  methods: {
    // // 添加教师资源
    // addTeacherResources() {
    //   let markId = `mark__${uuidv4()}`
    //   // let styleType = 'mk-addTeacherResources__default'
    //   let model = {
    //     selectionText: this.noteData.selectionText,
    //     markEfiCode: this.markEfiStr,
    //     type: `note__${this.userType}`,
    //     // styleType,
    //     blockIndex: this.ruleIndex,
    //     markId
    //   }
    //   this.emitRollbackHandler('addTeacherResources', model)
    //     .then((id) => {
    //       id === markId && _this.markInstance.remove(rollbackMark, 'addTeacherResources')
    //       console.log(222)
    //     })
    //     .catch((err) => {
    //       console.log(err)
    //     })
    // },
    // 打开添加教师资源弹窗
    addTeacherResources(e) {
      let markId = `mark__${v4()}`;
      // let styleType = 'mk-addTeacherResources__default'
      let model = {
        selectionText: this.noteData.selectionText,
        markEfiCode: this.markEfiStr,
        type: `note__${this.userType}`,
        styleType: this.evn.resourcesStyle,
        blockIndex: this.ruleIndex,
        markId
      };
      let _this = this;
      // this.noteOperateMode = 'add'
      // let defaultUnderlineStyle = 'mk-underline__default'
      _this.tempMarkEfiStr = _this.markEfiStr;
      _this.noteData.content = '';
      // _this.markInstance.underline(_this.markEfiStr, defaultUnderlineStyle, (e) => {
      //   try {
      //     // 下划线点击事件, 比如打开编辑框
      //     // let markEfiStr = e.target.getAttribute('data-id')
      //     // _this.noteClick(e, markEfiStr)
      //   } catch (error) {
      //     console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'underline error', error)
      //   }
      // })
      this.emitRollbackHandler('addTeacherResources', model).then(id => {
        id === markId && _this.markInstance.remove(rollbackMark, 'underline');
      }).catch(err => {
        console.log(err);
      });
      this.hideTippy();
    },
    // 添加数据
    addData(type, item) {
      this.data[type].list.push(item);
      let list = JSON.stringify(this.data);
      localStorage.setItem('simulated-data_' + this.textBookData.id, list);
      this.getData(type, item);
    },
    // 确认添加教师资源
    addResources(e) {
      this.$emit('addResources', e);
    },
    // 编辑教师资源
    editTeachingResource(e) {
      this.$refs.addTeacherResources.open({
        isEdit: true,
        ...e
      });
    },
    // 确认编辑教师资源
    editResources(e) {
      this.$emit('editResources', e);
    },
    updateMarkView() {
      // @pos 硬刷新Mark视图
      let _this = this;
      this.markInstance.clear();
      this.mergeMarkList.forEach(item => {
        console.log("🚀 ~ this.mergeMarkList.forEach ~ item:", item);
        if (item.type === 'note__teacher' && item.resourcesType) {
          this.markInstance.underline(item.markEfiCode, item.styleType, e => {
            try {
              // 下划线点击事件, 比如打开编辑框
              let markEfiStr = e.target.getAttribute('data-id');
              console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'Mark Click', e, markEfiStr);
              console.log('下划线点击事件, 比如打开编辑框', markEfiStr);
              this.emitRollbackHandler('openTeacherResources', {
                ...e,
                markEfiStr
              });
              // _this.noteClick(e, markEfiStr)
            } catch (error) {
              console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'MarkClick Error', error);
            }
          });
        } else if (item.type === 'note__teacher') {
          this.markInstance.underline(item.markEfiCode, item.styleType, e => {
            try {
              // 下划线点击事件, 比如打开编辑框
              // if (!_this.markEfiStr) {
              let markEfiStr = e.target.getAttribute('data-id');
              _this.noteClick(e, markEfiStr);
              // }
            } catch (error) {
              console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'MarkClick Error', error);
            }
          });
        }
        if (item.type === 'note__student') {
          this.markInstance.underline(item.markEfiCode, item.styleType, e => {
            try {
              // 下划线点击事件, 比如打开编辑框
              // if (!_this.markEfiStr) {
              let markEfiStr = e.target.getAttribute('data-id');
              _this.noteClick(e, markEfiStr);
              // }
            } catch (error) {
              console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'MarkClick Error', error);
            }
          });
        }
        if (item.type === 'highlight__student') {
          this.markInstance.highlight(item.markEfiCode, item.styleType, e => {
            try {
              // 下划线点击事件, 比如打开编辑框
              let markEfiStr = e.target.getAttribute('data-id');
              _this.highlightClick(e, markEfiStr);
            } catch (error) {
              console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'HighlightClick Error', error);
            }
          });
        }
        if (item.type === 'highlight__teacher') {
          this.markInstance.highlight(item.markEfiCode, item.styleType, e => {
            try {
              // 下划线点击事件, 比如打开编辑框
              let markEfiStr = e.target.getAttribute('data-id');
              _this.highlightClick(e, markEfiStr);
            } catch (error) {
              console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'HighlightClick Error', error);
            }
          });
        }
      });
    },
    handleMouseUp() {
      // @pos MouseUp
      let _this = this;
      let u = navigator.userAgent;
      if (this.userType && this.insideIndex === this.ruleIndex && this.evn.isAddMark) {
        setTimeout(() => {
          // console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'MouseUp', _this.markEfiStr)
          this.$EventBus.$emit('selectionTextChange', {
            state: false,
            tippyTplBarId: _this.tippyTplBarId
          });
          _this.$EventBus.$emit('tippyChange', {
            state: false
          });
          const text = window?.getSelection()?.toString() || '';
          if (text.length && text.length <= 200 && _this.markEfiStr) {
            let checkHasSaved = _this.mergeMarkList.find(item => item.markEfiCode === _this.markEfiStr);
            let checkout = true;
            if (checkHasSaved) {
              let msg = '当前位置已存在标记',
                resourceType = checkHasSaved.resourcesType || checkHasSaved.type?.split('__')[0];
              if (checkHasSaved.resourcesName) {
                resourceType = 'resource';
              }
              switch (resourceType) {
                case 'notes':
                  msg = '当前位置已存在笔记';
                  break;
                case 'recorded':
                  msg = '当前位置已存在录音';
                  break;
                case 'underline':
                  msg = '当前位置已存在笔记';
                  break;
                case 'highlight':
                  msg = '当前位置已存在高亮';
                  break;
                case 'resource':
                  msg = '当前位置已存在资源';
                  break;
              }
              checkout = false;
              if (_this.pageType == 'pc' || !u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)) {
                _this.activeTippy[0].setContent(`<span style="color:#EE3751;">${msg}</span>`);
                _this.activeTippy[0].show();
              } else {
                _this.$EventBus.$emit('showToastTippy', {
                  msg: msg
                });
              }
              let preOperateTippy = document.getElementById(`tippy-${_this.activeTippy[0].id - 1}`);
              preOperateTippy && preOperateTippy.remove();
              _this.markEfiStr = '';
              setTimeout(handler => {
                _this.hideTippy();
                checkout = true;
              }, 1500);
              return;
            }
            if (!checkout) {
              return;
            }
            _this.noteData.selectionText = text;
            const templateBar = document.getElementById(_this.tippyTplBarId);
            // let HTMLContentClone = $J(`#${_this.tippyTplBarId}`)?.clone(true,true)?.get(0)
            let content = templateBar?.innerHTML || '<span style="color:"#EE3751;"">选区解析错误</span>';
            let markId = `mark__${v4()}`;
            let noteModel = {
              selectionText: this.noteData.selectionText,
              noteContent: '',
              markEfiCode: this.markEfiStr,
              type: `note__${this.userType}`,
              styleType: this.evn.noteStyle,
              blockIndex: this.ruleIndex,
              markId
            };
            this.$EventBus.$emit('selectionTextChange', {
              state: false,
              tippyTplBarId: _this.tippyTplBarId,
              noteModel
            });
            _this.$EventBus.$emit('tippyChange', {
              state: false
            });
            if (_this.pageType == 'pc' || !u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)) {
              _this.activeTippy[0].setProps({
                content,
                onShown: instance => {
                  // @pos 显示tippybar
                  let actCopy = instance.popper.children[0]?.querySelector(`#${_this.actionCopyId}`),
                    actNote = instance.popper.children[0]?.querySelector(`#${_this.actionNoteId}`),
                    actHighlight = instance.popper.children[0]?.querySelector(`#${_this.actionHighlightId}`),
                    actLink = instance.popper.children[0]?.querySelector(`#${_this.actionLinkId}`),
                    addTeacherResources = instance.popper.children[0]?.querySelector(`#${_this.addTeacherResourcesId}`),
                    actionDictionary = instance.popper.children[0]?.querySelector(`#${_this.actionDictionaryId}`),
                    //词典
                    actionEncyclopedias = instance.popper.children[0]?.querySelector(`#${_this.actionEncyclopediasId}`); //百科

                  if (actCopy) {
                    actCopy.addEventListener('click', _this.actionCopy, {
                      once: true
                    });
                  }
                  if (actNote) {
                    actNote.addEventListener('click', _this.actionAddNote, {
                      once: true
                    });
                  }
                  if (actHighlight) {
                    actHighlight.addEventListener('click', _this.actionAddHighlight, {
                      once: true
                    });
                  }
                  if (actLink) {
                    actLink.addEventListener('click', _this.actionAddLink, {
                      once: true
                    });
                  }
                  if (addTeacherResources) {
                    addTeacherResources.addEventListener('click', _this.addTeacherResources, {
                      once: true
                    });
                  }
                  if (actionDictionary) {
                    actionDictionary.addEventListener('click', _this.openDictionary, {
                      once: true
                    });
                  }
                  if (actionEncyclopedias) {
                    actionEncyclopedias.addEventListener('click', _this.openEncyclopedias, {
                      once: true
                    });
                  }
                }
              });
              // _this.activeTippy = tippy(e.target, {
              //   content: templateBar,
              //   placement: 'top',
              //   trigger: 'manual',
              //   theme: 'light',
              //   animation: 'shift-away',
              //   interactive: true,
              //   onClickOutside(instance, event) {
              //     instance.hide()
              //   },
              // })
              _this.activeTippy[0].show();
              let preTipTippy = document.getElementById(`tippy-${_this.activeTippy[0].id - 1}`);
              preTipTippy && preTipTippy.remove();
            } else {
              this.$EventBus.$emit('selectionTextChange', {
                state: true,
                tippyTplBarId: _this.tippyTplBarId
              });
              _this.$EventBus.$emit('tippyChange', {
                state: true
              });
            }
          } else if (text.length > 200) {
            _this.activeTippy[0].setContent(`<div style="color:#666;">可选文本最多为200字</div>`);
            _this.activeTippy[0].show();
            setTimeout(handler => {
              _this.activeTippy[0].hide();
            }, 2000);
          }
        }, 110);
      }
    },
    resetTempNote() {
      if (this.tempMarkEfiStr) {
        this.noteData.selectionText = '';
        this.markInstance.remove(this.tempMarkEfiStr, 'underline');
      }
    },
    actionCopy() {
      if (!this.noteData.selectionText) {
        this.$message.error('未选中任何内容');
        return;
      }
      document.execCommand('copy');
      this.$message.success('复制成功');
      this.hideTippy();
      this.markEfiStr = '';
      this.noteData = {
        selectionText: '',
        content: ''
      };
      window.getSelection()?.removeAllRanges();
    },
    cancelEditNote(instance) {
      this.noteEditData = {
        selectionText: '',
        content: ''
      };
      this.noteOperateMode = '';
      this.tempMarkModel = {};
      instance.hide();
      instance.destroy();
    },
    deleteNote(instance) {
      let _this = this;
      let noteModel = {
        ...this.tempMarkModel
      };
      let markId = noteModel.markId;
      if (this.userType && this.insideIndex === this.ruleIndex) {
        let rollbackMark = noteModel.markEfiCode;
        this.emitRollbackHandler('deleteNote', noteModel).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'underline');
        });
      }
      this.cancelEditNote(instance);
    },
    confirmEditNote(noteInput, instance) {
      let value = noteInput?.value || '';
      if (!value) {
        this.$message.error('笔记内容不能为空');
        return;
      }
      let noteModel = {
        ...this.tempMarkModel,
        noteContent: value
      };
      if (this.userType && this.insideIndex === this.ruleIndex) {
        this.$EventBus.$emit('editNote', noteModel);
      }
      this.cancelEditNote(instance);
    },
    showEditNoteTippy() {
      let _this = this;
      try {
        const templateNote = document.getElementById(_this.editNoteTplId);
        let content = templateNote?.innerHTML || '笔记面板加载错误';
        // @pos 解决tippy不弹出问题
        let newTippy = tippy(`#${_this.blockId}`, {
          content,
          placement: 'top',
          trigger: 'manual',
          theme: 'light',
          animation: 'shift-away',
          interactive: true,
          allowHTML: true,
          followCursor: 'initial',
          onShown: instance => {
            // @pos 弹出编辑笔记tippy
            let actNoteCancel = instance.popper.children[0]?.querySelector(`#${_this.actNoteCancelId}`),
              actNoteDelete = instance.popper.children[0]?.querySelector(`#${_this.actNoteDeleteId}`),
              actNoteEditSave = instance.popper.children[0]?.querySelector(`#${_this.actNoteEditId}`),
              noteInput = instance.popper.children[0]?.querySelector(`#${_this.noteInputId}`);
            console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '获取不到事件?', {
              actNoteCancel,
              actNoteDelete,
              actNoteEditSave,
              noteInput
            });
            if (noteInput) {
              noteInput.value = _this.noteEditData.content;
              noteInput.addEventListener('input', e => {
                let value = e.target.value;
                if (value === _this.noteEditData.content) {
                  actNoteEditSave && actNoteEditSave.parentElement.classList.add('disabled');
                } else {
                  actNoteEditSave && actNoteEditSave.parentElement.classList.remove('disabled');
                }
              });
            }
            if (actNoteCancel) {
              actNoteCancel.addEventListener('click', () => _this.cancelEditNote(instance), {
                once: true
              });
            }
            if (actNoteDelete) {
              actNoteDelete.addEventListener('click', () => _this.deleteNote(instance), {
                once: true
              });
            }
            if (actNoteEditSave && noteInput) {
              actNoteEditSave.addEventListener('click', () => _this.confirmEditNote(noteInput, instance), {
                once: true
              });
            }
          }
        });
        newTippy[0].show();
      } catch (error) {
        console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'error', error);
      }
    },
    // @pos 学生笔记编辑
    noteClick(e, markEfi) {
      let _this = this;
      let styleScheme = e.target.getAttribute('data-styleScheme');
      console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', '下划线样式', styleScheme);
      if (this.userType === 'student' && styleScheme === 'default' || this.userType === 'teacher' && styleScheme === 'dashed') {
        // 学生和教师分别仅可触发各自对应的笔记下划线点击事件
        return;
      }
      let model = this.mergeMarkList.find(item => item.markEfiCode === markEfi);
      if (!model) {
        _this.activeTippy[0].setContent('查找笔记出错');
        _this.activeTippy[0].show();
        setTimeout(handler => {
          _this.hideTippy();
        }, 1500);
        return;
      }
      this.noteOperateMode = 'edit';
      this.tempMarkModel = model;
      if (this.userType && this.isMobile) {
        this.notePopupVisible = true;
        this.noteData = {
          selectionText: model.selectionText,
          content: model.noteContent
        };
      } else {
        this.noteEditData = {
          selectionText: model.selectionText,
          content: model.noteContent
        };
        this.$nextTick(() => {
          this.showEditNoteTippy(e);
        });
      }
    },
    actionAddNote(e) {
      // @pos 添加笔记
      // e?.stopPropagation && e.stopPropagation()
      let _this = this;
      this.noteOperateMode = 'add';
      let defaultUnderlineStyle = this.evn.noteStyle;
      _this.markInstance.underline(_this.markEfiStr, defaultUnderlineStyle, e => {
        try {
          // 下划线点击事件, 比如打开编辑框
          let markEfiStr = e.target.getAttribute('data-id');
          console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'UnderlineClick', e, markEfiStr);
          _this.noteClick(e, markEfiStr);
        } catch (error) {
          console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'underline error', error);
        }
      });
      _this.tempMarkEfiStr = _this.markEfiStr;
      _this.noteData.content = '';
      console.log('this.isMobile', this.isMobile);
      if (!this.isMobile) {
        const templateNote = document.getElementById(_this.tippyTplNoteId);
        let content = templateNote?.innerHTML || '笔记面板加载错误';
        _this.activeTippy[0].setProps({
          content,
          onAfterUpdate: instance => {
            // @pos 更新tippy
            _this.noteEventController = new AbortController();
            let actNoteCancel = instance.popper.children[0]?.querySelector(`#${_this.actNoteCancelId}`),
              actNoteConfirm = instance.popper.children[0]?.querySelector(`#${_this.actNoteConfirmId}`),
              noteInput = instance.popper.children[0]?.querySelector(`#${_this.noteInputId}`);
            if (actNoteCancel && noteInput) {
              actNoteCancel.addEventListener('click', _this.cancelAddNote, {
                once: true
              });
            }
            if (actNoteConfirm) {
              actNoteConfirm.addEventListener('click', () => _this.confirmAddNote(noteInput), {
                signal: _this.noteEventController.signal
              });
            }
          },
          onUntrigger: instance => {
            console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'Untrigger');
          }
        });
      } else {
        this.hideTippy();
        this.notePopupVisible = true;
      }
      window.getSelection()?.removeAllRanges();
    },
    confirmAddStudentNote() {
      let noteContent = this.noteData.content;
      console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', '内容', noteContent);
      if (!noteContent) {
        this.$message.error('请输入笔记内容');
        return;
      }
      if (this.noteOperateMode === 'edit') {
        this.confirmEditStudentNote(noteContent);
        return;
      }
      let markId = `mark__${v4()}`;
      let noteModel = {
        selectionText: this.noteData.selectionText,
        noteContent,
        markEfiCode: this.markEfiStr,
        type: `note__${this.userType}`,
        styleType: this.evn.noteStyle,
        blockIndex: this.ruleIndex,
        markId
      };
      this.tempMarkEfiStr = '';
      this.noteData = {
        selectionText: '',
        content: ''
      };
      if (this.userType && this.insideIndex === this.ruleIndex) {
        let rollbackMark = this.markEfiStr;
        this.emitRollbackHandler('createNote', noteModel).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'underline');
        });
      }
      this.notePopupVisible = false;
    },
    confirmEditStudentNote(noteContent) {
      let noteModel = {
        ...this.tempMarkModel,
        noteContent
      };
      this.tempMarkEfiStr = '';
      this.noteData = {
        selectionText: '',
        content: ''
      };
      if (this.userType && this.insideIndex === this.ruleIndex) {
        this.$EventBus.$emit('editNote', noteModel);
      }
      this.notePopupVisible = false;
      this.noteOperateMode = '';
      this.tempMarkModel = {};
    },
    deleteStudentNote() {
      let _this = this;
      let noteModel = {
        ...this.tempMarkModel
      };
      let markId = noteModel.markId;
      if (this.userType && this.insideIndex === this.ruleIndex) {
        let rollbackMark = noteModel.markEfiCode;
        this.emitRollbackHandler('deleteNote', noteModel).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'underline');
        });
      }
      this.notePopupVisible = false;
      this.noteOperateMode = '';
      this.tempMarkModel = {};
    },
    actionAddHighlight() {
      let _this = this;
      let styleType = this.evn.highlight;
      _this.markInstance.highlight(_this.markEfiStr, styleType, e => {
        try {
          let markEfiStr = e.target.getAttribute('data-id');
          _this.highlightClick(e, markEfiStr);
        } catch (error) {
          console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'underline error', error);
        }
      });
      this.hideTippy();
      let markId = `mark__${v4()}`;
      let model = {
        selectionText: this.noteData.selectionText,
        markEfiCode: this.markEfiStr,
        type: `highlight__${this.userType}`,
        styleType,
        blockIndex: this.ruleIndex,
        markId
      };
      this.noteData.selectionText = '';
      if (this.userType && this.insideIndex === this.ruleIndex) {
        // this.$EventBus.$emit('createHighlight', model)
        let rollbackMark = this.markEfiStr;
        this.emitRollbackHandler('createHighlight', model).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'highlight');
        });
      }
      window.getSelection()?.removeAllRanges();
    },
    actionAddLink() {
      let _this = this;
      let styleType = this.evn.linkStyle;
      _this.markInstance.underline(_this.markEfiStr, styleType, e => {
        try {
          let markEfiStr = e.target.getAttribute('data-id');
          _this.highlightClick(e, markEfiStr);
        } catch (error) {
          console.log('%c%s', 'font-size:2em;background: #DD5644;color: #FFF', 'underline error', error);
        }
      });
      this.hideTippy();
      let markId = `mark__${v4()}`;
      let model = {
        selectionText: this.noteData.selectionText,
        markEfiCode: this.markEfiStr,
        type: `link__${this.userType}`,
        styleType,
        blockIndex: this.ruleIndex,
        markId
      };
      this.noteData.selectionText = '';
      if (this.userType && this.insideIndex === this.ruleIndex) {
        // this.$EventBus.$emit('createHighlight', model)
        let rollbackMark = this.markEfiStr;
        this.emitRollbackHandler('createLink', model).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'underline');
        });
      }
      window.getSelection()?.removeAllRanges();
    },
    highlightClick(e, efi) {
      console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', 'HighLight Click', e, this.markEfiStr);
    },
    emitRollbackHandler(eventName, eventData) {
      console.log('emitRollbackHandler', eventName, eventData);
      return new Promise((resolve, reject) => {
        this.$EventBus.$emit(eventName, eventData, resolve);
      });
    },
    confirmAddNote(noteInput) {
      let value = noteInput?.value || '';
      if (!value) {
        this.$message.error('请输入笔记内容');
        return;
      }
      if (!value.trim()) {
        this.$message.error('笔记内容不能为空！');
        return;
      }
      this.hideTippy();
      let markId = `mark__${v4()}`;
      let noteModel = {
        selectionText: this.noteData.selectionText,
        noteContent: value,
        markEfiCode: this.markEfiStr,
        type: `note__${this.userType}`,
        styleType: this.evn.noteStyle,
        blockIndex: this.ruleIndex,
        markId
      };
      this.tempMarkEfiStr = '';
      this.noteData = {
        selectionText: '',
        content: ''
      };
      if (this.userType && this.insideIndex === this.ruleIndex) {
        let rollbackMark = this.markEfiStr;
        this.emitRollbackHandler('createNote', noteModel).then(id => {
          id === markId && _this.markInstance.remove(rollbackMark, 'underline');
        });
      }
      this.noteEventController.abort();
    },
    cancelAddNote() {
      this.hideTippy();
      this.markEfiStr && this.markInstance.remove(this.markEfiStr, 'underline');
      this.markEfiStr = '';
      this.noteData = {
        selectionText: '',
        content: ''
      };
    },
    hideTippy() {
      this.activeTippy[0].hide();
      this.activeTippy[0].setContent('');
    },
    formatImg(str) {
      if (str) {
        let data = str.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/gi, (match, p1) => {
          if (match.indexOf('style') !== -1) {
            // if (match.indexOf('http') !== -1) {
            //   return match
            // } else {
            //   let src = this.resourceBasisPath + p1.split('./')[1]
            //   let image = match.replace(p1, src)
            //   return image.split('style="')[0] + 'style="max-width:100% !important;' + image.split('style="')[1]
            // }
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image.split('style="')[0] + 'style="max-width:100% !important;' + image.split('style="')[1];
          } else {
            return `<img src='${p1.indexOf('http') > -1 ? p1 : this.resourceBasisPath + p1.split('./')[1]}' style="max-width:100%;" />`;
          }
        });
        return data;
      }
    },
    updateCodeBlock() {
      jquery(`#${this.blockId} .richtext-reset-wrapper--view pre`).each(function (i) {
        let classStrs = jquery(this).attr('class');
        let classArr = classStrs.split(';');
        classArr = classArr[0].split(':');
        let lang = 'language-' + classArr[1];
        let codeElemStr = '<code class="line-numbers ' + lang + '" data-prismjs-copy="复制代码" data-prismjs-copy-success="复制完成">' + jquery(this).html() + '</code>';
        jquery(this).html(codeElemStr);
        jquery(this).attr('class', 'line-numbers');
        let codeElem = jquery(this).children('code').get(0);
        // Prism.highlightAll();
        prism.highlightElement(codeElem);
      });
    },
    groupDataChange() {
      this.$emit('groupDataChange', {
        ruleIndex: this.ruleIndex,
        dataName: this.blockDataName,
        data: this.blockData,
        dataIndex: this.dataIndex
      });
    },
    //编辑器是否存在
    getIsEditorContainerExist() {
      const container = document.getElementById(this.editorId);
      if (!container) return false;
      const isWeExist = container.getAttribute('data-w-e-textarea');
      return isWeExist;
    },
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      // this.markEfiStr = ''
      this.hideTippy();
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex,
        dataIndex: this.dataIndex
      });
    },
    openDictionary() {
      if (!this.noteData.selectionText) {
        this.$message.error('未选中任何内容');
        return;
      }
      // let url = `https://fanyi.baidu.com/mtpe-individual/multimodal?query=${this.noteData.selectionText}&lang=zh2en`
      let url = `https://dict.baidu.com/s?wd=${this.noteData.selectionText}&device=pc&from=home`;
      this.openLink(url);
    },
    openEncyclopedias() {
      if (!this.noteData.selectionText) {
        this.$message.error('未选中任何内容');
        return;
      }
      let url = 'https://baike.baidu.com/item/' + this.noteData.selectionText;
      this.openLink(url);
    },
    openLink(url) {
      if (this.pageType == 'pc') {
        window.open(url);
      } else {
        this.$EventBus.$emit('xmlOpenLink', {
          url
        });
      }
      this.hideTippy();
    }
  }
};

/* script */
const __vue_script__$5x = script$5x;
/* template */
var __vue_render__$5x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        "xml-text-" + _vm.pageType,
        "" + _vm.containerClass,
        "" +
          (_vm.currentRule.data.pGroupData.className
            ? _vm.currentRule.data.pGroupData.className
            : ""),
        "richtext-reset-container",
      ],
      attrs: { id: _vm.blockId },
      on: { mousedown: _vm.blockClick, mouseup: _vm.handleMouseUp },
    },
    [
      _vm.mode === "editor"
        ? _vm._t("editor", null, { feedback: _vm.feedback })
        : _c("div", {
            staticClass: "richtext-reset-wrapper--view",
            attrs: { id: _vm.markRootId },
            domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.pBlockData.html)) },
          }),
      _vm._v(" "),
      _vm._t("mater-lib-dialog"),
      _vm._v(" "),
      _vm._t("mater-lib-dialog2"),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          staticClass: "xml-text-preview-box-dialog",
          attrs: {
            visible: _vm.inlineWordsModal.visible,
            "append-to-body": true,
            width: _vm.pageType == "pc" ? "40%" : "80%",
            center: "",
            "show-close": false,
          },
          on: {
            "update:visible": function ($event) {
              return _vm.$set(_vm.inlineWordsModal, "visible", $event)
            },
          },
        },
        [
          _c("xml-text-dialog-render", {
            attrs: {
              content: _vm.inlineWordsModal.content,
              textstyle: _vm.inlineWordsModal.stylemode
                ? _vm.inlineWordsModal.stylemode
                : "D",
              "page-type": _vm.pageType,
              resourceBasisPath: _vm.resourceBasisPath,
            },
          }),
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticStyle: { display: "none" }, attrs: { id: _vm.tippyTplBarId } },
        [
          _c(
            "div",
            {
              staticClass: "tipbar tipbar-tpl",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _vm.isMobile && _vm.evn.tippy.maxWordCount
                ? _c("div", { staticClass: "tipbar-remark--mobile" }, [
                    _c("span", { staticClass: "tipbar-remark" }, [
                      _vm._v("均<=200字"),
                    ]),
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.copy
                ? [
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionCopyId },
                        on: {
                          click: function ($event) {
                            $event.stopPropagation();
                            return _vm.actionCopy.apply(null, arguments)
                          },
                        },
                      },
                      [
                        _vm.evn.tippy.copyIcon
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.copyIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("复制")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.note
                ? [
                    _vm.evn.tippy.noteLine
                      ? _c("span", { staticClass: "tipbar-separator" }, [
                          _vm._v("|"),
                        ])
                      : _vm._e(),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionNoteId },
                        on: {
                          click: function ($event) {
                            $event.stopPropagation();
                            return _vm.actionAddNote.apply(null, arguments)
                          },
                        },
                      },
                      [
                        _vm.evn.tippy.noteIcon
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.noteIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("笔记")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _c("span", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.isMobile,
                    expression: "isMobile",
                  },
                ],
                staticClass: "tipbar-separator--mobile",
              }),
              _vm._v(" "),
              _vm.evn.tippy.resources
                ? _c(
                    "div",
                    {
                      staticClass: "tipbar-action",
                      attrs: { id: _vm.addTeacherResourcesId },
                      on: {
                        click: function ($event) {
                          return _vm.addTeacherResources()
                        },
                      },
                    },
                    [
                      _c(
                        "span",
                        {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.evn.tippy.resourcesLine,
                              expression: "evn.tippy.resourcesLine",
                            },
                          ],
                          staticClass: "tipbar-separator",
                        },
                        [_vm._v("|")]
                      ),
                      _vm._v(" "),
                      _vm.evn.tippy.resourcesIcon
                        ? _c("i", {
                            staticClass: "tipbar-icon",
                            class: _vm.evn.tippy.resourcesIcon,
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _c("span", [_vm._v("资源")]),
                    ]
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.highlight
                ? [
                    _c(
                      "span",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: _vm.evn.tippy.highlightLine,
                            expression: "evn.tippy.highlightLine",
                          },
                        ],
                        staticClass: "tipbar-separator",
                      },
                      [_vm._v("|")]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionHighlightId },
                      },
                      [
                        _vm.evn.tippy.highlight
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.highlightIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("高亮")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.link
                ? [
                    _c(
                      "span",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: _vm.evn.tippy.linkLine,
                            expression: "evn.tippy.linkLine",
                          },
                        ],
                        staticClass: "tipbar-separator",
                      },
                      [_vm._v("|")]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionLinkId },
                      },
                      [
                        _vm.evn.tippy.linkIcon
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.linkIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("划线")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.dictionary
                ? [
                    _vm.evn.tippy.dictionaryLine
                      ? _c("span", { staticClass: "tipbar-separator" }, [
                          _vm._v("|"),
                        ])
                      : _vm._e(),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionDictionaryId },
                      },
                      [
                        _vm.evn.tippy.dictionaryIcon
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.dictionaryIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("词典")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _vm.evn.tippy.encyclopedias
                ? [
                    _vm.evn.tippy.encyclopediasLine
                      ? _c("span", { staticClass: "tipbar-separator" }, [
                          _vm._v("|"),
                        ])
                      : _vm._e(),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "tipbar-action",
                        attrs: { id: _vm.actionEncyclopediasId },
                      },
                      [
                        _vm.evn.tippy.encyclopediasIcon
                          ? _c("i", {
                              staticClass: "tipbar-icon",
                              class: _vm.evn.tippy.encyclopediasIcon,
                            })
                          : _vm._e(),
                        _vm._v(" "),
                        _c("span", [_vm._v("百科")]),
                      ]
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              !_vm.isMobile && _vm.evn.tippy.maxWordCount
                ? _c("span", { staticClass: "tipbar-remark" }, [
                    _vm._v("均<=200字"),
                  ])
                : _vm._e(),
            ],
            2
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticStyle: { display: "none" }, attrs: { id: _vm.tippyTplNoteId } },
        [
          _c("div", { staticClass: "tipnote tipnote-tpl" }, [
            _c("div", { staticClass: "tipnote-title" }, [
              _vm._v(_vm._s(_vm.noteData.selectionText)),
            ]),
            _vm._v(" "),
            _c("textarea", {
              directives: [
                {
                  name: "model",
                  rawName: "v-model.trim",
                  value: _vm.noteData.content,
                  expression: "noteData.content",
                  modifiers: { trim: true },
                },
              ],
              ref: "tippyTextarea",
              staticClass: "tipnote-textarea",
              attrs: {
                id: _vm.noteInputId,
                placeholder: "请输入笔记内容",
                maxlength: "200",
                rows: "5",
              },
              domProps: { value: _vm.noteData.content },
              on: {
                input: function ($event) {
                  if ($event.target.composing) {
                    return
                  }
                  _vm.$set(_vm.noteData, "content", $event.target.value.trim());
                },
                blur: function ($event) {
                  return _vm.$forceUpdate()
                },
              },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "tipnote-footer" }, [
              _c(
                "div",
                {
                  staticClass: "tipnote-btn cancel",
                  attrs: { id: _vm.actNoteCancelId },
                  on: { click: _vm.cancelAddNote },
                },
                [_c("span", [_vm._v("取消")])]
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticClass: "tipnote-btn confirm",
                  attrs: { id: _vm.actNoteConfirmId },
                  on: { click: _vm.confirmAddNote },
                },
                [_c("span", [_vm._v("确认")])]
              ),
            ]),
          ]),
        ]
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticStyle: { display: "none" }, attrs: { id: _vm.editNoteTplId } },
        [
          _c("div", { staticClass: "tipnote tipnote-tpl" }, [
            _c("div", { staticClass: "tipnote-title" }, [
              _vm._v(_vm._s(_vm.noteEditData.selectionText)),
            ]),
            _vm._v(" "),
            _c("textarea", {
              ref: "tippyTextarea",
              staticClass: "tipnote-textarea",
              attrs: {
                id: _vm.noteInputId,
                placeholder: "请输入笔记内容",
                maxlength: "200",
                rows: "5",
              },
              domProps: { value: _vm.noteEditData.content },
            }),
            _vm._v(" "),
            _c("div", { staticClass: "tipnote-footer" }, [
              _c(
                "div",
                {
                  staticClass: "tipnote-btn cancel",
                  attrs: { id: _vm.actNoteCancelId },
                },
                [_c("span", [_vm._v("取消")])]
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticClass: "tipnote-btn delete",
                  attrs: { id: _vm.actNoteDeleteId },
                },
                [_c("span", [_vm._v("删除")])]
              ),
              _vm._v(" "),
              _c("div", { staticClass: "tipnote-btn btn-wrap disabled" }, [
                _c(
                  "div",
                  { staticClass: "btn-edit", attrs: { id: _vm.actNoteEditId } },
                  [_c("span", [_vm._v("保存修改")])]
                ),
              ]),
            ]),
          ]),
        ]
      ),
      _vm._v(" "),
      _vm.isMobile
        ? _c(
            "van-popup",
            {
              style: { backgroundColor: "transparent" },
              attrs: { "get-container": "body", position: "bottom" },
              on: { "click-overlay": _vm.resetTempNote },
              model: {
                value: _vm.notePopupVisible,
                callback: function ($$v) {
                  _vm.notePopupVisible = $$v;
                },
                expression: "notePopupVisible",
              },
            },
            [
              _c(
                "div",
                {
                  staticClass: "xml-reader-phone-mark-note-popup",
                  class: [
                    _vm.toolbarVisible &&
                      !_vm.keyboardShow &&
                      "is-toolbar-shown",
                    _vm.keyboardShow && "keyboard-show",
                    _vm.toolbarVisible && "toolbar-show",
                  ],
                  on: {
                    click: function ($event) {
                      $event.stopPropagation();
                    },
                  },
                },
                [
                  _c("div", { staticClass: "mark-note__selection" }, [
                    _c("div", [_vm._v(_vm._s(_vm.noteData.selectionText))]),
                  ]),
                  _vm._v(" "),
                  _c("section", { staticClass: "mark-note__wrap" }, [
                    _c("textarea", {
                      directives: [
                        {
                          name: "model",
                          rawName: "v-model.trim",
                          value: _vm.noteData.content,
                          expression: "noteData.content",
                          modifiers: { trim: true },
                        },
                      ],
                      ref: "popupTextarea",
                      staticClass: "mark-note__textarea",
                      attrs: {
                        placeholder: "添加笔记...",
                        maxlength: "200",
                        rows: "5",
                      },
                      domProps: { value: _vm.noteData.content },
                      on: {
                        input: function ($event) {
                          if ($event.target.composing) {
                            return
                          }
                          _vm.$set(
                            _vm.noteData,
                            "content",
                            $event.target.value.trim()
                          );
                        },
                        blur: function ($event) {
                          return _vm.$forceUpdate()
                        },
                      },
                    }),
                    _vm._v(" "),
                    _c("div", { staticClass: "mark-note__footer" }, [
                      _c("div", { staticClass: "mark-note__count" }, [
                        _vm._v(_vm._s(_vm.noteData.content.length) + "/200"),
                      ]),
                      _vm._v(" "),
                      _vm.noteOperateMode === "edit"
                        ? _c(
                            "div",
                            {
                              staticClass: "mark-note__delete",
                              on: {
                                click: function ($event) {
                                  if ($event.target !== $event.currentTarget) {
                                    return null
                                  }
                                  return _vm.deleteStudentNote.apply(
                                    null,
                                    arguments
                                  )
                                },
                              },
                            },
                            [_vm._v("删除")]
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _c(
                        "div",
                        {
                          staticClass: "mark-note__confirm",
                          class: _vm.noteData.content.length && "enable",
                          on: {
                            click: function ($event) {
                              if ($event.target !== $event.currentTarget) {
                                return null
                              }
                              return _vm.confirmAddStudentNote.apply(
                                null,
                                arguments
                              )
                            },
                          },
                        },
                        [_vm._v("\n            保存\n          ")]
                      ),
                    ]),
                  ]),
                ]
              ),
            ]
          )
        : _vm._e(),
    ],
    2
  )
};
var __vue_staticRenderFns__$5x = [];
__vue_render__$5x._withStripped = true;

  /* style */
  const __vue_inject_styles__$5x = undefined;
  /* scoped */
  const __vue_scope_id__$5x = "data-v-ecedc702";
  /* module identifier */
  const __vue_module_identifier__$5x = undefined;
  /* functional template */
  const __vue_is_functional_template__$5x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5x, staticRenderFns: __vue_staticRenderFns__$5x },
    __vue_inject_styles__$5x,
    __vue_script__$5x,
    __vue_scope_id__$5x,
    __vue_is_functional_template__$5x,
    __vue_module_identifier__$5x,
    false,
    undefined,
    undefined,
    undefined
  );

const TextComponents = [__vue_component__$5x, __vue_component__$5A];
var XmlText = {
  ...TextComponents
};

//
//
//
//
//
//
//
//
//
//
//

var script$5w = {
  name: 'XmlGroupRender',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    ruleIndex: Number,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    field: String,
    typeText: {
      type: String,
      default: ''
    },
    showLine: {
      type: Boolean,
      default: true
    },
    pBlockData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    BookName: {
      type: String,
      default: ''
    },
    //块儿数据名
    blockDataName: String
  },
  data() {
    return {
      isShowUp: false,
      isShowDown: false,
      //当前所处组的规则
      currentRule: {},
      mouseHover: false,
      showBtn: false
    };
  },
  created() {
    const _this = this;
    this.$EventBus.$on('blockClick', function ({
      blockId,
      ruleIndex
    }) {
      if (_this.ruleIndex === ruleIndex) {
        _this.showBtn = true;
      } else {
        _this.showBtn = false;
      }
    });
  },
  mounted() {
    this.blockData = this.pBlockData;
  },
  methods: {
    hover() {
      this.mouseHover = true;
    },
    leave() {
      this.mouseHover = false;
    }
  }
};

/* script */
const __vue_script__$5w = script$5w;
/* template */
var __vue_render__$5w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: _vm.showLine ? "xml-group-box" : "",
      on: { mousemove: _vm.hover, mouseleave: _vm.leave },
    },
    [
      _vm.mode === "editor"
        ? _c("span", { staticClass: "xml-group-type-text" }, [
            _vm._v(_vm._s(_vm.typeText)),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.showBtn && _vm.mode === "editor"
        ? _vm._t("action-buttons")
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        {
          class: [
            "xml-group-" + _vm.pageType,
            _vm.mode === "editor" ? "xml-group--editor" : "",
            _vm.mouseHover && _vm.mode === "editor" ? "hovered" : "",
          ],
        },
        [_vm._t("default")],
        2
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$5w = [];
__vue_render__$5w._withStripped = true;

  /* style */
  const __vue_inject_styles__$5w = undefined;
  /* scoped */
  const __vue_scope_id__$5w = "data-v-b8742ca0";
  /* module identifier */
  const __vue_module_identifier__$5w = undefined;
  /* functional template */
  const __vue_is_functional_template__$5w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5w, staticRenderFns: __vue_staticRenderFns__$5w },
    __vue_inject_styles__$5w,
    __vue_script__$5w,
    __vue_scope_id__$5w,
    __vue_is_functional_template__$5w,
    __vue_module_identifier__$5w,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5w.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('group-render', {
    prefix: options.prefix
  }), __vue_component__$5w);
};

var playIcon$5 = "data:image/png;base64,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";

var pauseIcon$4 = "data:image/png;base64,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";

var audioIcon$3 = "data:image/png;base64,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";

var coverLocal$2 = "5d9cf0a5d487a818.png";

var playRepeat$2 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAaCAMAAADhRa4NAAAAAXNSR0IArs4c6QAAAi5QTFRFAAAAAID/QID/M5n/JJL/SZL/TYD/O4nrQJ/vR47xQIz/OovoQ5DpPYXrPYX1QJLtPo3tRJDuOI//QI/vOpL4QpLwOZzxQI7qRYr4Po/sPo/zPYztPZL5QIvuQJHuOZL1RI3vPYrwQYzwOY7xQo7sQo3tQpHtPY7vQY7zI4XzPI/wQI/sQZDtSpHtP4/uPo3nPo3xQYvuRoDuQY3vQIn1QI/tOpfxP4zvQY3sKrv1MYv1QJDtQI/tPpLwQJDxQo/sQI7tQYzuPI7vQZDvQY3xQI7sM6nyQY3sQJLvPpLtQI/tQI/vRIntQI7uQI7vQY7uQY7tQY3tQo7tPYzxP47wQpDuRJDxQo7tQo7vQI3tQ5PtRI3uQJXxQYvxQZDxQJDuQ5DsQI7uPozsOpPtQY7tQI7tQI7uQI/uQ43sQY7tQI/tQY7tQ43vQ4/tQY7tQovsQI/tQY7uQI/wQo7uQo7tQI/uQYztQ43vQYvtQY7tQI7qQI7vQY3uQo/tQI/rQY7uQY7tQo7uP43vQI3vQY7tQY7rQpDvQY7tQY/tQY7tQY7vQY7sQo7tQ5DrQY7sQY7tQo7tQY7tQI3sQI7qQI7sQI/qQI/tQI/uQY3rQY3sQY3tQY7rQY7sQY7tQY/sQY/tQozsQo3pQo3qQo3sQo3tQo7rQo7sQo7tQo7uQo/qQo/sQo/tQ4nrQ4vuQ4zsQ43sQ47qQ47rQ47sQ4/qQ4/sRIvrRobsSI/qS3/qS5DpVRNwpwAAAJJ0Uk5TAAIEBQcHCg0QEhQWFxkZHB0eICAjIyQkJSkpKiosLDExMjM2Njo6Pz9BRERHSElKSktMTlBUXV1eYmNjZGdsbXN2fH6Ch4iJjI+Pj4+Tk5WZmpqgpaWmq6usrq+wsLGzs7a4ubm8vsLHyMvLy8vMzc/V1tbZ3N3e4ePm5ubp6+vs7e7u8PHy8/T29vj5+vz8/P6b5lOEAAABY0lEQVQYGY3BhztVYRwH8C+XsiIjM3tlFZGSPcsoM5SVUXZ2ocgOmSUj76+Lm3OsZEb/HR5d93iPp8fnAyn3+JzyqlfpoddxoeC6n0QkkJJYtgNkHCqVIpU9CguMTG2gLywFHL+Z3wuPLXHqdpmgKlVAyuuv0GgFjYjpjRpIXJlgFbqQunv49Qk08nbfgxOy+OsW1DzWlzzBK9ivh5pzfyFkDH4o7fFfr/8k4J/7VyFz7V7sQr7eA5x4xrpNwHsx16V820o49lIg6jIHJ+ibSMQ+AcjcpkkS+0zAubNGRC4A3k2VjH8cITvw/FfGXHDM2MZU6HF1gpy+OU4pBma9IeemgFrGchNkbrAOqFnQwUPwauff4MzT5VEznBcn7DlCo32r1xZSUcSiIWE9vPrZF2e0k1SbuTjnZqdIxQFaOGEU1bYznQiO4XNSfR+qTkvOaiaRPoRDzqdokIgYI2qJ0cGlHQE0EGWmZ0WkvQAAAABJRU5ErkJggg==";

var playOnce$2 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAgCAMAAAA2a+hwAAAAAXNSR0IArs4c6QAAAlJQTFRFAAAAgID/QID/QID/OY7jRovoO4nrSZLtRIjuPIfwQIzyQJnySZLzRpfzQ4X0QIrqQo7sPo3tEN73RI/wPI3yQZDrPo/sQ4ztQ4zzO4jtQY7tPo7uPYvuQI/vOY3vPo3vPIzrQZHwNqL1QI7wRYTrK4b2QY/uP47uR47yQIvvP43rQI/wP5DwSXz0QozuPYvxQY/uQI3uQY3vQI/yQZDzQpDwQ472QI3uPIn3P4vqQo7vQo3tQI3uQJDuQI7uQI3uQI7sQo7sQpHxQY3sPo7vQI7tQI/xQY7tQY3tQY3vPo7vQo/rP5HuQY3uQJDxQorsQo7sQo7vOI3xQovrQo3rQY7tQY3tQI7tQI3sPY/wN5vvQo7vNKPyQY7tQ43tQY/uQorsQI7sQY3uPJfwQI7wPpPuP4/vQY7tTnTsRJPxP47vQYzsPZDvP43uQo7sO5jvQY3uPZDwQ4ftQI7tQo3uQY7uQo7uQY7tQY7tQ43wQY7tQI3tQI7uQI7tQI/wP4zuQ5DtQY7tRJHsQI7tQY7tQI7tQY7uQI7uQY3uQo7tPpHtQI7tQY/sQY7sQY/tQY7tQY7tQY3sQY/uQI7tQY7tQo7uQo/uSY7tQY/tTXnqQY3tQY7tQY7tQY7tQY7tQY7uQY7tPZXtQY3tQY7tQY7tQY7tQo/tP5LqQI/rQI/sQY3sQY7rQY7sQY7tQY/rQY/sQY/tQZDtQozsQo3rQo3sQo3tQo7qQo7rQo7sQo7tQo/tQ4vsQ4zrQ47qQ47sRIfrRInsRI/rRI/sRYnsR4TqR4fqvnKd/wAAAKd0Uk5TAAIECAkLDQ4PERQUFRYXGBsdHyImJykqKisrLS4wMTEzMzQ0NDs7PT1AQURFRklLS0xOUFNVWFxdYWFlZ2doa2xsbW5vb3BxcnJzdHl5fHx8fH+AgIGCg4iJjIyOjpGSkpOUlZeZm56goaKio6OjpKaqqq6vsLO0t7i5vL7GxsfHyMjLy8zN0tTW2trc4ubn6erq7Ozs7Ozu7vHz9Pb3+Pn6+/v9/v6utNnsAAABnklEQVQYGY3BhVsTcQDH4c8QRbCwu7tbVFQsPLu7u3t2YRfYXRhg7r4GMnYzMPm/nLf7nTjkeXxf/lv7I3OoVP+IllCpvg80l39rNiTrkrQymQpqTD4rV7QriabkRx3tnXdP9tQqkDGAP1IPKXw6qy7ZciYCqbLnY6RdkDKBUVI2MUlL39jr8eQV3elATKdTI4kb+6FsMa7NZY/rkWDw8589ielS8qU3FWwquRoAcu0N+Opg3AxNgFalr2rhSd/3qBuece8vwszvOXjGFEiz8dQOC46GRuNK3iMVaDzG+TBct3vgSpGGrpOFse0rPH3blLhGrdkqC2P5OyhUQ3y7ZWEs/AG31RlfUBbGmiLIdQbiC8rC2P8SNkaW4QvKwrgfhkGhu/h2aRKedsVhqGnb/TAOazqend8OAit0BWP4ycbEdXxR2hJIeRJZS6LArc87+K2Xo2n8Len46xsBXAs+fVxFeU3OhJ61wTNL0XPdMarOkPOwLb7M/GIdGNE8rVr1+n1WX3OcYw0oJ32LnKhcjvKGkaDFohOFirm8PQPPL6EvjEOwGD5qAAAAAElFTkSuQmCC";

function parseLrc(lrc_s) {
  if (lrc_s) {
    lrc_s = lrc_s.replace(/([^\]^\n])\[/g, (match, p1) => p1 + '\n[');
    const lyric = lrc_s.split('\n');
    const lrc = [];
    const lyricLen = lyric.length;
    for (let i = 0; i < lyricLen; i++) {
      // match lrc time
      const lrcTimes = lyric[i].match(/\[(\d{2}):(\d{2})(\.(\d{2,3}))?]/g);
      // match lrc text
      const lrcText = lyric[i].replace(/.*\[(\d{2}):(\d{2})(\.(\d{2,3}))?]/g, '').replace(/<(\d{2}):(\d{2})(\.(\d{2,3}))?>/g, '').replace(/^\s+|\s+$/g, '');
      if (lrcTimes) {
        // handle multiple time tag
        const timeLen = lrcTimes.length;
        for (let j = 0; j < timeLen; j++) {
          const oneTime = /\[(\d{2}):(\d{2})(\.(\d{2,3}))?]/.exec(lrcTimes[j]);
          const min2sec = oneTime[1] * 60;
          const sec2sec = parseInt(oneTime[2]);
          const msec2sec = oneTime[4] ? parseInt(oneTime[4]) / ((oneTime[4] + '').length === 2 ? 100 : 1000) : 0;
          const lrcTime = min2sec + sec2sec + msec2sec;
          lrc.push([lrcTime, lrcText]);
        }
      }
    }
    // sort by time
    lrc.sort((a, b) => a[0] - b[0]);
    return lrc;
  } else {
    return [];
  }
}

//
var script$5v = {
  props: {
    currentMusic: {
      type: Object,
      required: true
    },
    playStat: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      displayLrc: '',
      currentLineIndex: 0
    };
  },
  computed: {
    lrcLines() {
      return parseLrc(this.displayLrc);
    },
    currentLine() {
      if (this.currentLineIndex > this.lrcLines.length - 1) {
        return null;
      }
      return this.lrcLines[this.currentLineIndex];
    },
    transformStyle() {
      // transform: translateY(0); -webkit-transform: translateY(0);
      return {
        transform: `translateY(${-this.currentLineIndex * 16}px)`,
        webkitTransform: `translateY(${-this.currentLineIndex * 16}px)`
      };
    }
  },
  methods: {
    applyLrc(lrc) {
      // if (/^https?:\/\//.test(lrc)) {
      //   this.fetchLrc(lrc)
      // } else {
      //   this.displayLrc = lrc
      // }
      this.fetchLrc(lrc);
    },
    fetchLrc(src) {
      fetch(src).then(response => response.text()).then(lrc => {
        this.displayLrc = lrc;
      });
    },
    hideLrc() {
      this.displayLrc = '';
    }
  },
  watch: {
    currentMusic: {
      deep: true,
      immediate: true,
      handler(music) {
        this.currentLineIndex = 0;
        if (music.lrc) {
          this.applyLrc(music.lrc);
        } else {
          this.hideLrc();
        }
      }
    },
    'playStat.playedTime'(playedTime) {
      for (let i = 0; i < this.lrcLines.length; i++) {
        const line = this.lrcLines[i];
        const nextLine = this.lrcLines[i + 1];
        if (playedTime >= line[0] && (!nextLine || playedTime < nextLine[0])) {
          this.currentLineIndex = i;
        }
      }
    }
  }
};

/* script */
const __vue_script__$5v = script$5v;
/* template */
var __vue_render__$5v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "aplayer-lrc" }, [
    _c(
      "div",
      { staticClass: "aplayer-lrc-contents", style: _vm.transformStyle },
      _vm._l(_vm.lrcLines, function (line, index) {
        return _c(
          "p",
          {
            key: index,
            class: { "aplayer-lrc-current": index === _vm.currentLineIndex },
          },
          [_vm._v("\n      " + _vm._s(line[1]) + "\n    ")]
        )
      }),
      0
    ),
  ])
};
var __vue_staticRenderFns__$5v = [];
__vue_render__$5v._withStripped = true;

  /* style */
  const __vue_inject_styles__$5v = undefined;
  /* scoped */
  const __vue_scope_id__$5v = undefined;
  /* module identifier */
  const __vue_module_identifier__$5v = undefined;
  /* functional template */
  const __vue_is_functional_template__$5v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5v, staticRenderFns: __vue_staticRenderFns__$5v },
    __vue_inject_styles__$5v,
    __vue_script__$5v,
    __vue_scope_id__$5v,
    __vue_is_functional_template__$5v,
    __vue_module_identifier__$5v,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5u = {
  name: 'XmlAudioModalPlayer',
  components: {
    lyrics: __vue_component__$5v
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    targerLink: {
      type: String,
      default: ''
    },
    subTitle: {
      type: Boolean,
      default: false
    },
    subTitleSrc: {
      type: String,
      default: ''
    },
    subTitleSrcName: {
      type: String,
      default: ''
    }
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat$2,
        'dan-ci': playOnce$2
      };
      return srcMap[this.playMode];
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.musicAudio.muted = true;
              const playPromise = _this.$refs.musicAudio.play();
              _this.$refs.musicAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.musicAudio.pause();
                }).catch(error => {
                  _this.$refs.musicAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    subTitleSrc: {
      handler(newVal) {
        this.audio.title = this.title;
        this.audio.src = this.url;
        this.audio.pic = this.coverImageUrl || coverLocal$2;
        this.audio.lrc = newVal;
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.musicAudio.pause();
      }
    }
  },
  data() {
    return {
      playIcon: playIcon$5,
      pauseIcon: pauseIcon$4,
      audioIcon: audioIcon$3,
      coverLocal: coverLocal$2,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      musicAudio: null,
      manualPlay: false,
      audio: {
        title: '',
        artist: '',
        src: '',
        pic: '',
        lrc: ''
      },
      playStat: {
        duration: 0,
        loadedTime: 0,
        playedTime: 0
      }
    };
  },
  mounted() {
    // 加载完毕后，先获取<video>标签DOM对象
    this.musicAudio = this.$refs.musicAudio;
    // this.videoContainer = this.$refs.videoContainer
    if (this.mode === 'preview' && this.playStyle === 'yuan-wen') {
      this.$emit('resetTimeUpdate');
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    play() {
      // if (this.mode==='editor') {
      //   this.$message.warning('音频仅支持在预览模式下播放')
      //   return
      // }
      if (this.playStyle === 'yuan-wen') {
        this.doPlay();
      }
      if (this.playStyle === 'tiao-zhuan') {
        if (this.targerLink) {
          // window.location.href=this.targerLink
          window.open(this.targerLink, '_blank');
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }
      }
      if (this.playStyle === 'tan-chuang') {
        this.$emit('showDialog', {
          url: this.url,
          title: this.title,
          coverFileUrl: this.coverImageUrl
        });
      }
    },
    doPlay() {
      console.log('暂停');
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      if (this.isPlay) {
        this.$refs.musicAudio.pause();
        this.isPlay = !this.isPlay;
      } else {
        this.manualPlay = true;
        this.$refs.musicAudio.play().then(() => {
          this.isPlay = !this.isPlay;
          let timeStamp = new Date().getTime();
          this.lastOperateTimeStamp = timeStamp;
          let playStamp = `${this.url}__playTimeStamp__${timeStamp}`;
          sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
        }).catch(error => {
          this.$message.warning('无效音频资源，请检查资源地址');
          console.log('音频播放catch', error);
        });
      }
      // this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playStat.playedTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      this.$emit('timeUpdate', {
        event: e,
        time: this.$refs.musicAudio.currentTime
      });
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    },
    onPlay() {
      if (!this.manualPlay) {
        this.$refs.musicAudio.pause();
      }
    }
  }
};

/* script */
const __vue_script__$5u = script$5u;

/* template */
var __vue_render__$5u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-audio-player" }, [
    _c("div", { staticClass: "audio-cover" }, [
      _c(
        "div",
        { staticClass: "square-wrapper" },
        [
          _c("el-image", {
            attrs: {
              src: _vm.coverImageUrl ? _vm.coverImageUrl : _vm.coverLocal,
              alt: "",
            },
          }),
          _vm._v(" "),
          _c("div", { staticClass: "play-button style-a" }, [
            _c("img", {
              staticClass: "button-image",
              attrs: { src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.play.apply(null, arguments)
                },
              },
            }),
          ]),
        ],
        1
      ),
    ]),
    _vm._v(" "),
    _c("div", { staticClass: "audio-player-control" }, [
      _c(
        "div",
        {
          class: [
            "audio-player-title",
            "audio-player-title--modal",
            _vm.mode == "preview" && _vm.title.length >= 16 ? "preview" : "",
          ],
        },
        [
          _c(
            "div",
            {
              staticClass: "scroll-text",
              style:
                _vm.mode == "preview" && _vm.title.length >= 16
                  ? {
                      animation:
                        "fadeNum " +
                        _vm.title.length / 3 +
                        "s linear infinite backwards",
                    }
                  : "",
            },
            [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]
          ),
          _vm._v(" "),
          _vm.subTitle
            ? _c("lyrics", {
                staticStyle: { "margin-top": "15px" },
                attrs: {
                  "current-music": _vm.audio,
                  "play-stat": _vm.playStat,
                },
              })
            : _vm._e(),
        ],
        1
      ),
      _vm._v(" "),
      _c("div", { staticClass: "audio-controls" }, [
        _c("div", { staticClass: "audio-controls__handler" }, [
          _c("div", { staticClass: "play-times" }, [
            _c("div", { staticClass: "current-time" }, [
              _vm._v(_vm._s(_vm.playCurrentTime)),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "progress" },
              [
                _c("el-slider", {
                  attrs: {
                    max: _vm.totalDuration,
                    disabled: _vm.scheduleControl !== "yes",
                    "show-tooltip": false,
                  },
                  on: { change: _vm.changeTime },
                  model: {
                    value: _vm.playTime,
                    callback: function ($$v) {
                      _vm.playTime = $$v;
                    },
                    expression: "playTime",
                  },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "total-time" }, [
              _vm._v(_vm._s(_vm.playDuration)),
            ]),
          ]),
        ]),
      ]),
    ]),
    _vm._v(" "),
    _c("audio", {
      ref: "musicAudio",
      staticClass: "audio-component",
      attrs: {
        controls: "",
        preload: "auto",
        src: _vm.url,
        loop: _vm.loop,
        link: _vm.link,
      },
      on: {
        canplay: _vm.changeDuration,
        timeupdate: _vm.updateTime,
        pause: function ($event) {
          _vm.isPlay = false;
        },
        play: _vm.onPlay,
      },
    }),
  ])
};
var __vue_staticRenderFns__$5u = [];
__vue_render__$5u._withStripped = true;

  /* style */
  const __vue_inject_styles__$5u = undefined;
  /* scoped */
  const __vue_scope_id__$5u = undefined;
  /* module identifier */
  const __vue_module_identifier__$5u = undefined;
  /* functional template */
  const __vue_is_functional_template__$5u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5u, staticRenderFns: __vue_staticRenderFns__$5u },
    __vue_inject_styles__$5u,
    __vue_script__$5u,
    __vue_scope_id__$5u,
    __vue_is_functional_template__$5u,
    __vue_module_identifier__$5u,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5t = {
  name: 'XmlMusicRender',
  components: {
    XmlAudioModalPlayer: __vue_component__$5u
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('新data', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        playMode: 'xun-huan',
        scheduleControl: 'yes',
        playStyle: 'yuan-wen',
        styleMode: 'A'
      },
      //属性组件名
      propertyComponentsName: 'xml-music-property',
      dialogShow: false,
      curMusicUrl: '',
      curSubTitle: '',
      curSubTitleSrcName: '',
      curSubTitleSrc: '',
      curMusicTitle: '',
      curCoverFileUrl: '',
      curMusicContent: '',
      currentPlayer: {},
      modalPlayerKey: 'mpk' + new Date().getTime()
    };
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog(param) {
      this.curMusicUrl = param.url;
      this.curSubTitle = param.subTitle;
      this.curSubTitleSrc = param.subTitleSrc;
      this.curSubTitleSrcName = param.subTitleSrcName;
      this.curMusicTitle = param.title;
      this.curCoverFileUrl = param.coverFileUrl;
      this.curMusicContent = param.content;
      this.currentPlayer = this.blockData.musicSetList[param.playerIndex];
      this.modalPlayerKey = 'mpk' + new Date().getTime();
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    timeUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('audioTimeUpdate', {
        event,
        time,
        data
      });
    },
    resetTimeUpdate(data) {
      this.$EventBus.$emit('resetTimeUpdate', data);
    }
  },
  created() {},
  computed: {
    isEmpty() {
      return !(this.blockData.musicSetList && this.blockData.musicSetList.length);
    }
  }
};

/* script */
const __vue_script__$5t = script$5t;

/* template */
var __vue_render__$5t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-music-container",
      class: [
        "xml-music-container-" + _vm.pageType,
        "xml-music-style-mode-" + _vm.blockData.styleMode,
      ],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _vm.blockData.musicSetList && _vm.blockData.musicSetList.length
        ? _vm._l(_vm.blockData.musicSetList, function (item, index) {
            return _c(
              "div",
              {
                key: index,
                staticClass: "fixed-ratio-container xml-group-item",
              },
              [
                _c(
                  "div",
                  {
                    class: [
                      "xml-music__wrapper",
                      "style-mode--" + _vm.blockData.styleMode,
                      _vm.mode,
                    ],
                  },
                  [
                    _c(
                      "div",
                      { staticClass: "xml-music-player-box" },
                      [
                        _c("xml-audio-play", {
                          attrs: {
                            "targer-link": item.link,
                            mode: _vm.mode,
                            styleMode: _vm.blockData.styleMode,
                            "play-style": _vm.blockData.playStyle,
                            "play-mode": _vm.blockData.playMode,
                            url: item.uploadFileUrl
                              ? _vm.resourceBasisPath
                                ? _vm.resourceBasisPath +
                                  item.uploadFileUrl.split("./")[1]
                                : item.uploadFileUrl
                              : "",
                            loop: _vm.blockData.playMode === "xun-huan",
                            title: item.title,
                            playerIndex: index,
                            content: item.content,
                            scheduleControl: _vm.blockData.scheduleControl,
                            subTitle: _vm.blockData.subTitle == "showSubtitle",
                            subTitleSrcName: item.subTitleSrcName,
                            subTitleSrc: item.subTitleSrc,
                            "cover-image-url": item.coverFileUrl
                              ? _vm.resourceBasisPath
                                ? _vm.resourceBasisPath +
                                  item.coverFileUrl.split("./")[1]
                                : item.coverFileUrl
                              : "",
                          },
                          on: {
                            showDialog: _vm.showDialog,
                            timeUpdate: function (e) {
                              return _vm.timeUpdate(e, item)
                            },
                            resetTimeUpdate: function ($event) {
                              return _vm.resetTimeUpdate(item)
                            },
                          },
                        }),
                      ],
                      1
                    ),
                  ]
                ),
              ]
            )
          })
        : [
            _c("div", { staticClass: "fixed-ratio-container" }, [
              _c(
                "div",
                {
                  class: [
                    "xml-music__wrapper",
                    "style-mode--" + _vm.blockData.styleMode,
                    _vm.mode,
                  ],
                },
                [
                  _c(
                    "div",
                    { staticClass: "xml-music-player-box" },
                    [
                      _c("xml-audio-play", {
                        attrs: {
                          mode: _vm.mode,
                          styleMode: _vm.blockData.styleMode,
                          "play-mode": _vm.blockData.playMode,
                          title: "这是音频",
                        },
                      }),
                    ],
                    1
                  ),
                ]
              ),
            ]),
          ],
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          staticClass: "resource-preview player-dialog",
          attrs: {
            title: "",
            visible: _vm.dialogShow,
            "destroy-on-close": true,
            width: "100%",
            modal: false,
            "show-close": false,
            "append-to-body": "",
            "custom-class": "preview-modal-music",
            "close-on-click-modal": true,
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogShow = $event;
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "xml-modal-player__wrapper" },
            [
              _c("xml-audio-modal-player", {
                key: _vm.modalPlayerKey,
                attrs: {
                  mode: _vm.mode,
                  styleMode: _vm.blockData.styleMode,
                  "play-style": "yuan-wen",
                  "play-mode": _vm.blockData.playMode,
                  url: _vm.curMusicUrl,
                  loop: _vm.blockData.playMode === "xun-huan",
                  title: _vm.curMusicTitle,
                  content: _vm.curMusicContent,
                  scheduleControl: _vm.blockData.scheduleControl,
                  "cover-image-url": _vm.curCoverFileUrl,
                  subTitle: _vm.curSubTitle,
                  subTitleSrcName: _vm.curSubTitleSrcName,
                  subTitleSrc: _vm.curSubTitleSrc,
                },
                on: {
                  timeUpdate: function (e) {
                    return _vm.timeUpdate(e, _vm.currentPlayer)
                  },
                  resetTimeUpdate: function ($event) {
                    return _vm.resetTimeUpdate(_vm.currentPlayer)
                  },
                },
              }),
            ],
            1
          ),
        ]
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$5t = [];
__vue_render__$5t._withStripped = true;

  /* style */
  const __vue_inject_styles__$5t = undefined;
  /* scoped */
  const __vue_scope_id__$5t = undefined;
  /* module identifier */
  const __vue_module_identifier__$5t = undefined;
  /* functional template */
  const __vue_is_functional_template__$5t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5t, staticRenderFns: __vue_staticRenderFns__$5t },
    __vue_inject_styles__$5t,
    __vue_script__$5t,
    __vue_scope_id__$5t,
    __vue_is_functional_template__$5t,
    __vue_module_identifier__$5t,
    false,
    undefined,
    undefined,
    undefined
  );

var playIcon2 = "data:image/png;base64,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";

var pauseIcon2 = "data:image/png;base64,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";

var recordIcon = "a6071719aecd4e53.png";

//
var script$5s = {
  name: 'AudioPlay',
  components: {
    lyrics: __vue_component__$5v
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    targerLink: {
      type: String,
      default: ''
    },
    playerIndex: {
      type: Number,
      default: -1
    },
    subTitle: {
      type: Boolean,
      default: false
    },
    subTitleSrc: {
      type: String,
      default: ''
    },
    subTitleSrcName: {
      type: String,
      default: ''
    }
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat$2,
        'dan-ci': playOnce$2
      };
      return srcMap[this.playMode];
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        console.log('浏览器 UA', ua);
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        // isWeixin && isIOS && newVal && this.$message({
        //   message: `当前UA为${ua}`,
        //   duration: 3000
        // })
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.musicAudio.muted = true;
              const playPromise = _this.$refs.musicAudio.play();
              _this.$refs.musicAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.musicAudio.pause();
                }).catch(error => {
                  _this.$refs.musicAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    subTitleSrc: {
      handler(newVal) {
        this.audio.title = this.title;
        this.audio.src = this.url;
        this.audio.pic = this.coverImageUrl || coverLocal$2;
        this.audio.lrc = newVal;
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.musicAudio.pause();
      }
    }
  },
  data() {
    return {
      playIcon: playIcon$5,
      playIcon2,
      pauseIcon: pauseIcon$4,
      pauseIcon2,
      audioIcon: audioIcon$3,
      coverLocal: coverLocal$2,
      recordIcon,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      musicAudio: null,
      manualPlay: false,
      Volume: 100,
      //音量
      doubleSpeed: 1,
      //倍速
      audio: {
        title: '',
        artist: '',
        src: '',
        pic: '',
        lrc: ''
      },
      playStat: {
        duration: 0,
        loadedTime: 0,
        playedTime: 0
      }
    };
  },
  mounted() {
    // 加载完毕后，先获取<video>标签DOM对象
    this.musicAudio = this.$refs.musicAudio;
    // this.videoContainer = this.$refs.videoContainer
    if (this.mode === 'preview' && this.playStyle === 'yuan-wen') {
      this.$emit('resetTimeUpdate');
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    play() {
      // if (this.mode==='editor') {
      //   this.$message.warning('音频仅支持在预览模式下播放')
      //   return
      // }
      if (this.playStyle === 'yuan-wen') {
        this.doPlay();
      }
      if (this.playStyle === 'tiao-zhuan') {
        if (this.targerLink) {
          // window.location.href=this.targerLink
          window.open(this.targerLink, '_blank');
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }
      }
      if (this.playStyle === 'tan-chuang') {
        this.$emit('showDialog', {
          url: this.url,
          title: this.title,
          coverFileUrl: this.coverImageUrl,
          content: this.content,
          playerIndex: this.playerIndex,
          subTitle: this.subTitle,
          subTitleSrc: this.subTitleSrc,
          subTitleSrcName: this.subTitleSrcName
        });
      }
    },
    doPlay() {
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      if (this.isPlay) {
        this.$refs.musicAudio.pause();
        this.isPlay = !this.isPlay;
      } else {
        this.manualPlay = true;
        this.$refs.musicAudio.play().then(() => {
          this.isPlay = !this.isPlay;
          let timeStamp = new Date().getTime();
          this.lastOperateTimeStamp = timeStamp;
          let playStamp = `${this.url}__playTimeStamp__${timeStamp}`;
          sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
        }).catch(error => {
          this.$message.warning('无效音频资源，请检查资源地址');
          console.log('音频播放catch', error);
        });
      }
      // this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playStat.playedTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      this.$emit('timeUpdate', {
        event: e,
        time: this.$refs.musicAudio.currentTime
      });
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    },
    onPlay() {
      if (!this.manualPlay) {
        this.$refs.musicAudio.pause();
      }
    },
    changeVolume(val) {
      this.$refs.musicAudio.volume = val / 100;
    },
    changeDoubleSpeed() {
      if (this.doubleSpeed == 1) {
        this.doubleSpeed = 1.25;
      } else if (this.doubleSpeed == 1.25) {
        this.doubleSpeed = 1.5;
      } else if (this.doubleSpeed == 1.5) {
        this.doubleSpeed = 2;
      } else if (this.doubleSpeed == 2) {
        this.doubleSpeed = 0.5;
      } else if (this.doubleSpeed == 0.5) {
        this.doubleSpeed = 1;
      }
      this.$refs.musicAudio.playbackRate = this.doubleSpeed;
    }
  }
};

/* script */
const __vue_script__$5s = script$5s;

/* template */
var __vue_render__$5s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: ["xml-audio-player", _vm.mode] },
    [
      _vm.styleMode == "A" || _vm.styleMode == "B" || _vm.styleMode == "C"
        ? _c("div", { staticClass: "play-mode-wrap" }, [
            _c("img", {
              staticClass: "play-control-image",
              attrs: { src: _vm.playModeIconSrc },
            }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.styleMode == "A" || _vm.styleMode == "B" || _vm.styleMode == "C"
        ? _c("div", { staticStyle: { width: "100%", display: "flex" } }, [
            _vm.styleMode !== "C"
              ? _c(
                  "div",
                  { staticClass: "audio-cover" },
                  [
                    _c("el-image", {
                      staticClass: "xml-round-6",
                      attrs: {
                        src: _vm.coverImageUrl
                          ? _vm.coverImageUrl
                          : _vm.coverLocal,
                        alt: "",
                      },
                    }),
                    _vm._v(" "),
                    _vm.styleMode === "A"
                      ? _c("div", { staticClass: "play-button style-a" }, [
                          _c("img", {
                            staticClass: "button-image",
                            attrs: {
                              src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                            },
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.play.apply(null, arguments)
                              },
                            },
                          }),
                        ])
                      : _vm._e(),
                  ],
                  1
                )
              : _vm._e(),
            _vm._v(" "),
            _c("div", { staticClass: "audio-player-control" }, [
              _c(
                "div",
                {
                  class: [
                    "audio-player-title",
                    _vm.mode == "preview" && _vm.title.length >= 16
                      ? "preview"
                      : "",
                  ],
                },
                [
                  _c(
                    "div",
                    {
                      staticClass: "xml-audio-title scroll-text",
                      style:
                        _vm.mode == "preview" && _vm.title.length >= 16
                          ? {
                              animation:
                                "fadeNum " +
                                _vm.title.length / 3 +
                                "s linear infinite backwards",
                            }
                          : "",
                    },
                    [
                      _vm._v(
                        "\n            " + _vm._s(_vm.title) + "\n          "
                      ),
                    ]
                  ),
                ]
              ),
              _vm._v(" "),
              _c("div", { staticClass: "audio-controls" }, [
                _vm.styleMode !== "A"
                  ? _c(
                      "div",
                      { staticClass: "audio-controls__play-button-wrapper" },
                      [
                        _c("div", { staticClass: "play-button" }, [
                          _c("img", {
                            staticClass: "button-image",
                            attrs: {
                              src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                            },
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.play.apply(null, arguments)
                              },
                            },
                          }),
                        ]),
                      ]
                    )
                  : _vm._e(),
                _vm._v(" "),
                _c("div", { staticClass: "audio-controls__handler" }, [
                  _c(
                    "div",
                    { staticClass: "progress" },
                    [
                      _vm.subTitle
                        ? _c("lyrics", {
                            staticStyle: { "margin-top": "-25px" },
                            attrs: {
                              "current-music": _vm.audio,
                              "play-stat": _vm.playStat,
                            },
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _c("el-slider", {
                        attrs: {
                          max: _vm.totalDuration,
                          disabled: _vm.scheduleControl !== "yes",
                          "show-tooltip": false,
                        },
                        on: { change: _vm.changeTime },
                        model: {
                          value: _vm.playTime,
                          callback: function ($$v) {
                            _vm.playTime = $$v;
                          },
                          expression: "playTime",
                        },
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "play-times" }, [
                    _c("div", { staticClass: "current-time" }, [
                      _vm._v(_vm._s(_vm.playCurrentTime)),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "total-time" }, [
                      _vm._v(_vm._s(_vm.playDuration)),
                    ]),
                  ]),
                ]),
              ]),
            ]),
          ])
        : _vm.styleMode == "D"
        ? _c("div", { staticClass: "audio-play-box" }, [
            _c(
              "div",
              { staticClass: "audio-cover" },
              [
                _c("div", { staticClass: "video-pause-btn" }, [
                  _c(
                    "div",
                    { staticClass: "audio-controls__play-button-wrapper" },
                    [
                      _c("div", { staticClass: "play-button" }, [
                        _c("img", {
                          staticClass: "button-image",
                          attrs: {
                            src: _vm.isPlay ? _vm.pauseIcon2 : _vm.playIcon2,
                          },
                          on: {
                            click: function ($event) {
                              $event.stopPropagation();
                              return _vm.play.apply(null, arguments)
                            },
                          },
                        }),
                      ]),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("el-image", {
                  staticClass: "xml-round-6",
                  attrs: { src: _vm.recordIcon, alt: "" },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "audio-player-control" }, [
              _c(
                "div",
                {
                  class: [
                    "audio-player-title",
                    _vm.mode == "preview" && _vm.title.length >= 16
                      ? "preview"
                      : "",
                  ],
                },
                [
                  _c(
                    "div",
                    {
                      staticClass: "xml-audio-title scroll-text",
                      style:
                        _vm.mode == "preview" && _vm.title.length >= 16
                          ? {
                              animation:
                                "fadeNum " +
                                _vm.title.length / 3 +
                                "s linear infinite backwards",
                            }
                          : "",
                    },
                    [_vm._v("\n          " + _vm._s(_vm.title) + "\n        ")]
                  ),
                ]
              ),
              _vm._v(" "),
              _c("div", { staticClass: "audio-controls" }, [
                _c("div", { staticClass: "audio-controls__handler" }, [
                  _c(
                    "div",
                    { staticClass: "progress" },
                    [
                      _vm.subTitle
                        ? _c("lyrics", {
                            staticStyle: { "margin-top": "-25px" },
                            attrs: {
                              "current-music": _vm.audio,
                              "play-stat": _vm.playStat,
                            },
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _c("el-slider", {
                        attrs: {
                          max: _vm.totalDuration,
                          disabled: _vm.scheduleControl !== "yes",
                          "show-tooltip": false,
                          "tooltip-class": "silder-bg",
                        },
                        on: { change: _vm.changeTime },
                        model: {
                          value: _vm.playTime,
                          callback: function ($$v) {
                            _vm.playTime = $$v;
                          },
                          expression: "playTime",
                        },
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "play-times" }, [
                    _c("div", { staticClass: "current-time" }, [
                      _vm._v(_vm._s(_vm.playCurrentTime)),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "total-time" }, [
                      _vm._v(_vm._s(_vm.playDuration)),
                    ]),
                  ]),
                ]),
              ]),
            ]),
          ])
        : _vm.styleMode == "E"
        ? _c("div", { staticClass: "styleModeE_content" }, [
            _c("div", { staticClass: "styleModeE_title" }, [
              _vm._v(_vm._s(_vm.title)),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "styleModeE_row" }, [
              _c(
                "div",
                {
                  staticStyle: { cursor: "pointer" },
                  on: {
                    click: function ($event) {
                      $event.stopPropagation();
                      return _vm.play.apply(null, arguments)
                    },
                  },
                },
                [
                  _vm.isPlay
                    ? _c("i", {
                        staticClass: "el-icon-video-pause",
                        staticStyle: { "font-size": "28px" },
                      })
                    : _c("i", {
                        staticClass: "el-icon-caret-right",
                        staticStyle: { "font-size": "28px" },
                      }),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "styleModeE_progress" },
                [
                  _vm.subTitle
                    ? _c("lyrics", {
                        staticStyle: { "margin-top": "-25px" },
                        attrs: {
                          "current-music": _vm.audio,
                          "play-stat": _vm.playStat,
                        },
                      })
                    : _vm._e(),
                  _vm._v(" "),
                  _c("el-slider", {
                    attrs: {
                      max: _vm.totalDuration,
                      disabled: _vm.scheduleControl !== "yes",
                      "show-tooltip": false,
                    },
                    on: { change: _vm.changeTime },
                    model: {
                      value: _vm.playTime,
                      callback: function ($$v) {
                        _vm.playTime = $$v;
                      },
                      expression: "playTime",
                    },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticStyle: {
                    display: "flex",
                    "flex-direction": "row",
                    "align-items": "center",
                  },
                },
                [
                  _c("div", [
                    _vm._v(
                      _vm._s(_vm.playCurrentTime) +
                        "/ " +
                        _vm._s(_vm.playDuration)
                    ),
                  ]),
                  _vm._v(" "),
                  _c(
                    "el-popover",
                    {
                      attrs: {
                        placement: "bottom",
                        width: "200",
                        trigger: "click",
                      },
                    },
                    [
                      _c(
                        "div",
                        [
                          _c("el-slider", {
                            attrs: { "show-tooltip": false },
                            on: { change: _vm.changeVolume },
                            model: {
                              value: _vm.Volume,
                              callback: function ($$v) {
                                _vm.Volume = $$v;
                              },
                              expression: "Volume",
                            },
                          }),
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("i", {
                        staticClass: "el-icon-s-tools",
                        staticStyle: {
                          "font-size": "18px",
                          "margin-left": "15px",
                          cursor: "pointer",
                        },
                        attrs: { slot: "reference" },
                        slot: "reference",
                      }),
                    ]
                  ),
                ],
                1
              ),
            ]),
          ])
        : _vm.styleMode == "F"
        ? [
            _c("div", { staticClass: "styleModeF_row styleModeF_content" }, [
              _c("div", { staticStyle: { position: "relative" } }, [
                _c("div", { staticClass: "styleModeF_IMG" }, [
                  _c("img", {
                    attrs: {
                      src: _vm.coverImageUrl
                        ? _vm.coverImageUrl
                        : _vm.coverLocal,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _vm.isPlay
                  ? _c("i", {
                      staticClass: "el-icon-video-pause styleModeF_I",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.play.apply(null, arguments)
                        },
                      },
                    })
                  : _c("i", {
                      staticClass: "el-icon-video-play styleModeF_I",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.play.apply(null, arguments)
                        },
                      },
                    }),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticStyle: { "margin-left": "20px", width: "100%" } },
                [
                  _c("div", { staticClass: "styleModeF_row" }, [
                    _c(
                      "div",
                      {
                        staticStyle: { cursor: "pointer" },
                        on: {
                          click: function ($event) {
                            $event.stopPropagation();
                            return _vm.play.apply(null, arguments)
                          },
                        },
                      },
                      [
                        _vm.isPlay
                          ? _c("i", {
                              staticClass: "el-icon-video-pause",
                              staticStyle: { "font-size": "28px" },
                            })
                          : _c("i", {
                              staticClass: "el-icon-caret-right",
                              staticStyle: { "font-size": "28px" },
                            }),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "styleModeF_progress" },
                      [
                        _c("el-slider", {
                          attrs: {
                            max: _vm.totalDuration,
                            disabled: _vm.scheduleControl !== "yes",
                            "show-tooltip": false,
                          },
                          on: { change: _vm.changeTime },
                          model: {
                            value: _vm.playTime,
                            callback: function ($$v) {
                              _vm.playTime = $$v;
                            },
                            expression: "playTime",
                          },
                        }),
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticStyle: {
                          display: "flex",
                          "flex-direction": "row",
                          "align-items": "center",
                        },
                      },
                      [
                        _c("div", [
                          _vm._v(
                            _vm._s(_vm.playCurrentTime) +
                              " / " +
                              _vm._s(_vm.playDuration)
                          ),
                        ]),
                        _vm._v(" "),
                        _c(
                          "el-popover",
                          {
                            attrs: {
                              placement: "bottom",
                              width: "200",
                              trigger: "click",
                            },
                          },
                          [
                            _c(
                              "div",
                              [
                                _c("el-slider", {
                                  attrs: { "show-tooltip": false },
                                  on: { change: _vm.changeVolume },
                                  model: {
                                    value: _vm.Volume,
                                    callback: function ($$v) {
                                      _vm.Volume = $$v;
                                    },
                                    expression: "Volume",
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c("i", {
                              staticClass: "el-icon-s-tools",
                              staticStyle: {
                                "font-size": "18px",
                                "margin-left": "15px",
                                cursor: "pointer",
                              },
                              attrs: { slot: "reference" },
                              slot: "reference",
                            }),
                          ]
                        ),
                      ],
                      1
                    ),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "styleModeF_title" }, [
                    _vm._v("当前倍速："),
                    _c(
                      "span",
                      {
                        staticStyle: { cursor: "pointer" },
                        on: { click: _vm.changeDoubleSpeed },
                      },
                      [_vm._v("X" + _vm._s(_vm.doubleSpeed))]
                    ),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "styleModeF_title" }, [
                    _vm._v("\n          " + _vm._s(_vm.title) + "\n        "),
                  ]),
                  _vm._v(" "),
                  _vm.subTitle
                    ? _c("lyrics", {
                        staticStyle: { "margin-top": "10px" },
                        attrs: {
                          "current-music": _vm.audio,
                          "play-stat": _vm.playStat,
                        },
                      })
                    : _vm._e(),
                ],
                1
              ),
            ]),
          ]
        : _vm._e(),
      _vm._v(" "),
      _c("audio", {
        ref: "musicAudio",
        staticClass: "audio-component",
        attrs: {
          controls: "",
          preload: "auto",
          src: _vm.url,
          loop: _vm.loop,
          link: _vm.link,
        },
        on: {
          canplay: _vm.changeDuration,
          timeupdate: _vm.updateTime,
          pause: function ($event) {
            _vm.isPlay = false;
          },
          play: _vm.onPlay,
        },
      }),
    ],
    2
  )
};
var __vue_staticRenderFns__$5s = [];
__vue_render__$5s._withStripped = true;

  /* style */
  const __vue_inject_styles__$5s = undefined;
  /* scoped */
  const __vue_scope_id__$5s = undefined;
  /* module identifier */
  const __vue_module_identifier__$5s = undefined;
  /* functional template */
  const __vue_is_functional_template__$5s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5s, staticRenderFns: __vue_staticRenderFns__$5s },
    __vue_inject_styles__$5s,
    __vue_script__$5s,
    __vue_scope_id__$5s,
    __vue_is_functional_template__$5s,
    __vue_module_identifier__$5s,
    false,
    undefined,
    undefined,
    undefined
  );

const MusicComponents = [__vue_component__$5t, __vue_component__$5s];
var XmlMusic = {
  ...MusicComponents
};

const radioType$1 = ['1', '4'];
const checkType$1 = ['2', '5'];
const showOptionsType$1 = ['1', '2', '4', '5'];
const showJudgeAnswerType$1 = ['3'];
const showSortType$1 = ['7'];
const showRichTextAnswerType$1 = ['10', '13'];
const isclozeType$1 = ['10', '11', '12'];
const isRadio$1 = type => {
  return radioType$1.includes(type);
};
const isCheck$1 = type => checkType$1.includes(type);
const showOptions$1 = type => showOptionsType$1.includes(type);
const showJudgeAnswer$1 = type => showJudgeAnswerType$1.includes(type);
const showSort$1 = type => showSortType$1.includes(type);
const showRichTextAnswer$1 = type => showRichTextAnswerType$1.includes(type);
const isCloze$1 = type => isclozeType$1.includes(type);

var playIcon$4 = "data:image/png;base64,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";

var pauseIcon$3 = "data:image/png;base64,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";

var audioIcon$2 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAAAXNSR0IArs4c6QAAAzxJREFUWEftmE1rU1EQhp+5N2msQVCogtWCC0EwG4OCG3+ClGpxobhQ8GMjKFjbFBfWVBQpght3LvzoxoVYwT8gLkQQNyKKigEXpXXhB9LEmOSMTK7R1sTkRpOmggMhJ+TcmefOmXPeuVeYZzo6uhPnDgIJRLrn/9eG8WfgCdHoFRkff1TxLzbQoaE4kchNYFcbAtd36XmObDYtly+ftYkBUCp1F+hfdJhKQFXw/SE5f/6S6PDwAJ53p2MwP6FyqG4UHRm5jcjujgOVl0pPiKZSL4BNSwIIbhjQW6BviQDdaj2QFahZqQSFghUrdHWFvd8WADkXBDZbtgxWrYLeXujrC77fvYOpqQCssf0BUD4PkQgsXw5r1sC6dUFwG69eDbFYVVg9dw6Zm2uMA00C2V3u2RMArFwJnhcmCDo+jmSzYeY2AeQcum0bYkBN2pIAcrlcWQaku3txM6SquOlp3OwsxUwGMhl0ZgZvdpaukyfxtmxpP1Dh6VOKjx/jMhm8Dx/w8nk8Efx4HM92mghYER8+DIlE+4Hc1avIy5dINGrHffD51ayIjxxZHCAmJ+HZs/ql/R/on8mQSUmlrg4c6HANmaAmEtDfH2ia/fb9Nuyy7/qlySSydy81i9qyEo/DyEiVurf2pDYRHRiADRvQfB7p6akNVEdaNJ1GcrkwgtNAy6ytOHasDLPAam17VUpr1+IfP75gqrMDMp3GCyfEdYBsCVasQE+fDh5N5plOTiI1dpn78oXi4CBdO3aUZ2upRP76dWLPnyPWsjS2+hlyIsjYGDKv47MzWScm8N6/r3YvQuHjRwq9veWl1TdviBUK+CYn4aw+kBaLfE0mie3b98Pd1/v3iUxNBZr1OxMpZ0esf6olK7+/snE/VJqbI9/Tg5dM4l69Ivr6NVFrzpoLFC4/zXSMznZYLFZVT2EjhZzXOEMhHbVqWhnIZHtzqzz+lR/VawZkbz32/5WjVl3s3CHR0dHtOPcQsTavg6Y6Qza7qfI6ZgzVM+XWszNWpFTaJRMT934QaCp1CjgDxBeNyY4OkWlEjsqFC/cs7oKU6PDwekQGEdmKc9WPoK0i9X1FJIdzD1C9LRcvfqq4/gYOF90Ot1xnCQAAAABJRU5ErkJggg==";

//
var script$5r = {
  name: 'audio-play',
  props: {
    url: {
      type: String,
      default: '',
      require: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    }
  },
  data() {
    return {
      playIcon: playIcon$4,
      pauseIcon: pauseIcon$3,
      audioIcon: audioIcon$2,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      isShowPlay: false
    };
  },
  watch: {
    url: {
      handler() {
        this.isPlay = false;
      }
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : "0" + minute;
      second = second >= 10 ? second : "0" + second;
      return minute + ":" + second;
    },
    play() {
      // console.log()
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play();
      this.isPlay = !this.isPlay;
      this.$emit('play');
    },
    updateTime() {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    }
  }
};

/* script */
const __vue_script__$5r = script$5r;
/* template */
var __vue_render__$5r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticStyle: { width: "100%" } }, [
    !_vm.mobile && !_vm.isLineFile
      ? _c("div", { staticClass: "audio", attrs: { flex: "" } }, [
          _c("div", { staticClass: "audio-icon" }, [
            _c("img", { attrs: { src: _vm.audioIcon, alt: "" } }),
          ]),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "audio-controls",
              attrs: { flex: "dir:top main:justify" },
            },
            [
              _c(
                "div",
                {
                  staticClass: "audio-controls--handler",
                  attrs: { flex: "main:justify cross:center" },
                },
                [
                  _c("div", { staticClass: "current-time" }, [
                    _vm._v(_vm._s(_vm.playCurrentTime)),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "play" }, [
                    _c("img", {
                      staticClass: "play-handler",
                      attrs: { src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon },
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.play.apply(null, arguments)
                        },
                      },
                    }),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "total-time" }, [
                    _vm._v(_vm._s(_vm.playDuration)),
                  ]),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "audio-controls--progress" },
                [
                  _c("el-slider", {
                    attrs: {
                      disabled: _vm.playbackControl === "1",
                      max: _vm.totalDuration,
                      "show-tooltip": false,
                    },
                    on: { change: _vm.changeTime },
                    model: {
                      value: _vm.playTime,
                      callback: function ($$v) {
                        _vm.playTime = $$v;
                      },
                      expression: "playTime",
                    },
                  }),
                ],
                1
              ),
            ]
          ),
        ])
      : _c(
          "div",
          [
            _c("img", {
              staticClass: "play-handler mobile",
              attrs: { src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  _vm.isShowPlay = true;
                },
              },
            }),
            _vm._v(" "),
            _c(
              "el-dialog",
              {
                attrs: {
                  width: "100%",
                  top: "60px",
                  "custom-class": "video-play--dialog",
                  "destroy-on-close": "",
                  "append-to-body": "",
                  visible: _vm.isShowPlay,
                },
                on: {
                  "update:visible": function ($event) {
                    _vm.isShowPlay = $event;
                  },
                },
              },
              [
                _c("div", { staticClass: "audio", attrs: { flex: "" } }, [
                  _c("div", { staticClass: "audio-icon" }, [
                    _c("img", { attrs: { src: _vm.audioIcon, alt: "" } }),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "audio-controls",
                      attrs: { flex: "dir:top main:justify" },
                    },
                    [
                      _c(
                        "div",
                        {
                          staticClass: "audio-controls--handler",
                          attrs: { flex: "main:justify cross:center" },
                        },
                        [
                          _c("div", { staticClass: "current-time" }, [
                            _vm._v(_vm._s(_vm.playCurrentTime)),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "play" }, [
                            _c("img", {
                              staticClass: "play-handler",
                              attrs: {
                                src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                              },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.play.apply(null, arguments)
                                },
                              },
                            }),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "total-time" }, [
                            _vm._v(_vm._s(_vm.playDuration)),
                          ]),
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "div",
                        { staticClass: "audio-controls--progress" },
                        [
                          _c("el-slider", {
                            attrs: {
                              disabled: _vm.playbackControl === "1",
                              max: _vm.totalDuration,
                              "show-tooltip": false,
                            },
                            on: { change: _vm.changeTime },
                            model: {
                              value: _vm.playTime,
                              callback: function ($$v) {
                                _vm.playTime = $$v;
                              },
                              expression: "playTime",
                            },
                          }),
                        ],
                        1
                      ),
                    ]
                  ),
                ]),
              ]
            ),
          ],
          1
        ),
    _vm._v(" "),
    _c("audio", {
      ref: "musicAudio",
      staticClass: "audio-component",
      attrs: { controls: "", preload: "auto", src: _vm.url },
      on: {
        canplay: _vm.changeDuration,
        timeupdate: _vm.updateTime,
        pause: function ($event) {
          _vm.isPlay = false;
        },
      },
    }),
  ])
};
var __vue_staticRenderFns__$5r = [];
__vue_render__$5r._withStripped = true;

  /* style */
  const __vue_inject_styles__$5r = undefined;
  /* scoped */
  const __vue_scope_id__$5r = "data-v-15593d96";
  /* module identifier */
  const __vue_module_identifier__$5r = undefined;
  /* functional template */
  const __vue_is_functional_template__$5r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5r, staticRenderFns: __vue_staticRenderFns__$5r },
    __vue_inject_styles__$5r,
    __vue_script__$5r,
    __vue_scope_id__$5r,
    __vue_is_functional_template__$5r,
    __vue_module_identifier__$5r,
    false,
    undefined,
    undefined,
    undefined
  );

var videoIcon$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAaRJREFUSEu91surTXEUwPHP8iiSyAQTpRRTUkqu3CHFkETdy8BcKFNGir/AwKM8YuRRjOTGnfEHGJgwwUQMjDyWftr7dhxn733O0blruNfju9dvPX6/0CKZuQYHcQBbsL4y/4S3eILHEfG1KUwMUmTmOpzHKRRIm5TgV3EpIj73G/4DyMy9uIcNHYH71R9xJCJe9Cr+AmTmMdzE0hGD1+Y/MRMRt+sPC4DM3IPnWDZm8NrtB6YjYr58+APIzLV401PE/2QoTbAtIr7UgMs42xC1pP0AO7B5BPKViDgXmbkaH7CqwXk+IqYys+gv4nSdeQfsGzYWwCyutxjPRcR0rc/M3VUjlLnokhMFcAdHhwVUNVuJCziDJS2+dwvgFXaOAujJZleVzdYG/9cF8A6bxgFU2azAM5Sj65f3iwKY+BFNvMgzuDFsDUZs09nJD1rVCV2r4iG2j7UqFmXZVZApzHVMZtdqKPpf2BcRLxfWdc9kHsc1LB8m0gCb7zgZEbdq3aArs2Ryf8wr83D9542A6rgmd+n3pl09Ww5hf8Oz5SketT1bfgPyir14bBOMeQAAAABJRU5ErkJggg==";

var win;
if (typeof window !== "undefined") {
  win = window;
} else if (typeof commonjsGlobal !== "undefined") {
  win = commonjsGlobal;
} else if (typeof self !== "undefined") {
  win = self;
} else {
  win = {};
}
var window_1 = win;

var slice = Array.prototype.slice;
var domWalk = iterativelyWalk;
function iterativelyWalk(nodes, cb) {
  if (!('length' in nodes)) {
    nodes = [nodes];
  }
  nodes = slice.call(nodes);
  while (nodes.length) {
    var node = nodes.shift(),
      ret = cb(node);
    if (ret) {
      return ret;
    }
    if (node.childNodes && node.childNodes.length) {
      nodes = slice.call(node.childNodes).concat(nodes);
    }
  }
}

var domComment = Comment;
function Comment(data, owner) {
  if (!(this instanceof Comment)) {
    return new Comment(data, owner);
  }
  this.data = data;
  this.nodeValue = data;
  this.length = data.length;
  this.ownerDocument = owner || null;
}
Comment.prototype.nodeType = 8;
Comment.prototype.nodeName = "#comment";
Comment.prototype.toString = function _Comment_toString() {
  return "[object Comment]";
};

var domText = DOMText;
function DOMText(value, owner) {
  if (!(this instanceof DOMText)) {
    return new DOMText(value);
  }
  this.data = value || "";
  this.length = this.data.length;
  this.ownerDocument = owner || null;
}
DOMText.prototype.type = "DOMTextNode";
DOMText.prototype.nodeType = 3;
DOMText.prototype.nodeName = "#text";
DOMText.prototype.toString = function _Text_toString() {
  return this.data;
};
DOMText.prototype.replaceData = function replaceData(index, length, value) {
  var current = this.data;
  var left = current.substring(0, index);
  var right = current.substring(index + length, current.length);
  this.data = left + value + right;
  this.length = this.data.length;
};

var dispatchEvent_1 = dispatchEvent;
function dispatchEvent(ev) {
  var elem = this;
  var type = ev.type;
  if (!ev.target) {
    ev.target = elem;
  }
  if (!elem.listeners) {
    elem.listeners = {};
  }
  var listeners = elem.listeners[type];
  if (listeners) {
    return listeners.forEach(function (listener) {
      ev.currentTarget = elem;
      if (typeof listener === 'function') {
        listener(ev);
      } else {
        listener.handleEvent(ev);
      }
    });
  }
  if (elem.parentNode) {
    elem.parentNode.dispatchEvent(ev);
  }
}

var addEventListener_1 = addEventListener;
function addEventListener(type, listener) {
  var elem = this;
  if (!elem.listeners) {
    elem.listeners = {};
  }
  if (!elem.listeners[type]) {
    elem.listeners[type] = [];
  }
  if (elem.listeners[type].indexOf(listener) === -1) {
    elem.listeners[type].push(listener);
  }
}

var removeEventListener_1 = removeEventListener;
function removeEventListener(type, listener) {
  var elem = this;
  if (!elem.listeners) {
    return;
  }
  if (!elem.listeners[type]) {
    return;
  }
  var list = elem.listeners[type];
  var index = list.indexOf(listener);
  if (index !== -1) {
    list.splice(index, 1);
  }
}

var serialize = serializeNode;
var voidElements = ["area", "base", "br", "col", "embed", "hr", "img", "input", "keygen", "link", "menuitem", "meta", "param", "source", "track", "wbr"];
function serializeNode(node) {
  switch (node.nodeType) {
    case 3:
      return escapeText(node.data);
    case 8:
      return "<!--" + node.data + "-->";
    default:
      return serializeElement(node);
  }
}
function serializeElement(elem) {
  var strings = [];
  var tagname = elem.tagName;
  if (elem.namespaceURI === "http://www.w3.org/1999/xhtml") {
    tagname = tagname.toLowerCase();
  }
  strings.push("<" + tagname + properties(elem) + datasetify(elem));
  if (voidElements.indexOf(tagname) > -1) {
    strings.push(" />");
  } else {
    strings.push(">");
    if (elem.childNodes.length) {
      strings.push.apply(strings, elem.childNodes.map(serializeNode));
    } else if (elem.textContent || elem.innerText) {
      strings.push(escapeText(elem.textContent || elem.innerText));
    } else if (elem.innerHTML) {
      strings.push(elem.innerHTML);
    }
    strings.push("</" + tagname + ">");
  }
  return strings.join("");
}
function isProperty(elem, key) {
  var type = typeof elem[key];
  if (key === "style" && Object.keys(elem.style).length > 0) {
    return true;
  }
  return elem.hasOwnProperty(key) && (type === "string" || type === "boolean" || type === "number") && key !== "nodeName" && key !== "className" && key !== "tagName" && key !== "textContent" && key !== "innerText" && key !== "namespaceURI" && key !== "innerHTML";
}
function stylify(styles) {
  if (typeof styles === 'string') return styles;
  var attr = "";
  Object.keys(styles).forEach(function (key) {
    var value = styles[key];
    key = key.replace(/[A-Z]/g, function (c) {
      return "-" + c.toLowerCase();
    });
    attr += key + ":" + value + ";";
  });
  return attr;
}
function datasetify(elem) {
  var ds = elem.dataset;
  var props = [];
  for (var key in ds) {
    props.push({
      name: "data-" + key,
      value: ds[key]
    });
  }
  return props.length ? stringify(props) : "";
}
function stringify(list) {
  var attributes = [];
  list.forEach(function (tuple) {
    var name = tuple.name;
    var value = tuple.value;
    if (name === "style") {
      value = stylify(value);
    }
    attributes.push(name + "=" + "\"" + escapeAttributeValue(value) + "\"");
  });
  return attributes.length ? " " + attributes.join(" ") : "";
}
function properties(elem) {
  var props = [];
  for (var key in elem) {
    if (isProperty(elem, key)) {
      props.push({
        name: key,
        value: elem[key]
      });
    }
  }
  for (var ns in elem._attributes) {
    for (var attribute in elem._attributes[ns]) {
      var prop = elem._attributes[ns][attribute];
      var name = (prop.prefix ? prop.prefix + ":" : "") + attribute;
      props.push({
        name: name,
        value: prop.value
      });
    }
  }
  if (elem.className) {
    props.push({
      name: "class",
      value: elem.className
    });
  }
  return props.length ? stringify(props) : "";
}
function escapeText(s) {
  var str = '';
  if (typeof s === 'string') {
    str = s;
  } else if (s) {
    str = s.toString();
  }
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttributeValue(str) {
  return escapeText(str).replace(/"/g, "&quot;");
}

var htmlns = "http://www.w3.org/1999/xhtml";
var domElement = DOMElement;
function DOMElement(tagName, owner, namespace) {
  if (!(this instanceof DOMElement)) {
    return new DOMElement(tagName);
  }
  var ns = namespace === undefined ? htmlns : namespace || null;
  this.tagName = ns === htmlns ? String(tagName).toUpperCase() : tagName;
  this.nodeName = this.tagName;
  this.className = "";
  this.dataset = {};
  this.childNodes = [];
  this.parentNode = null;
  this.style = {};
  this.ownerDocument = owner || null;
  this.namespaceURI = ns;
  this._attributes = {};
  if (this.tagName === 'INPUT') {
    this.type = 'text';
  }
}
DOMElement.prototype.type = "DOMElement";
DOMElement.prototype.nodeType = 1;
DOMElement.prototype.appendChild = function _Element_appendChild(child) {
  if (child.parentNode) {
    child.parentNode.removeChild(child);
  }
  this.childNodes.push(child);
  child.parentNode = this;
  return child;
};
DOMElement.prototype.replaceChild = function _Element_replaceChild(elem, needle) {
  // TODO: Throw NotFoundError if needle.parentNode !== this

  if (elem.parentNode) {
    elem.parentNode.removeChild(elem);
  }
  var index = this.childNodes.indexOf(needle);
  needle.parentNode = null;
  this.childNodes[index] = elem;
  elem.parentNode = this;
  return needle;
};
DOMElement.prototype.removeChild = function _Element_removeChild(elem) {
  // TODO: Throw NotFoundError if elem.parentNode !== this

  var index = this.childNodes.indexOf(elem);
  this.childNodes.splice(index, 1);
  elem.parentNode = null;
  return elem;
};
DOMElement.prototype.insertBefore = function _Element_insertBefore(elem, needle) {
  // TODO: Throw NotFoundError if referenceElement is a dom node
  // and parentNode !== this

  if (elem.parentNode) {
    elem.parentNode.removeChild(elem);
  }
  var index = needle === null || needle === undefined ? -1 : this.childNodes.indexOf(needle);
  if (index > -1) {
    this.childNodes.splice(index, 0, elem);
  } else {
    this.childNodes.push(elem);
  }
  elem.parentNode = this;
  return elem;
};
DOMElement.prototype.setAttributeNS = function _Element_setAttributeNS(namespace, name, value) {
  var prefix = null;
  var localName = name;
  var colonPosition = name.indexOf(":");
  if (colonPosition > -1) {
    prefix = name.substr(0, colonPosition);
    localName = name.substr(colonPosition + 1);
  }
  if (this.tagName === 'INPUT' && name === 'type') {
    this.type = value;
  } else {
    var attributes = this._attributes[namespace] || (this._attributes[namespace] = {});
    attributes[localName] = {
      value: value,
      prefix: prefix
    };
  }
};
DOMElement.prototype.getAttributeNS = function _Element_getAttributeNS(namespace, name) {
  var attributes = this._attributes[namespace];
  var value = attributes && attributes[name] && attributes[name].value;
  if (this.tagName === 'INPUT' && name === 'type') {
    return this.type;
  }
  if (typeof value !== "string") {
    return null;
  }
  return value;
};
DOMElement.prototype.removeAttributeNS = function _Element_removeAttributeNS(namespace, name) {
  var attributes = this._attributes[namespace];
  if (attributes) {
    delete attributes[name];
  }
};
DOMElement.prototype.hasAttributeNS = function _Element_hasAttributeNS(namespace, name) {
  var attributes = this._attributes[namespace];
  return !!attributes && name in attributes;
};
DOMElement.prototype.setAttribute = function _Element_setAttribute(name, value) {
  return this.setAttributeNS(null, name, value);
};
DOMElement.prototype.getAttribute = function _Element_getAttribute(name) {
  return this.getAttributeNS(null, name);
};
DOMElement.prototype.removeAttribute = function _Element_removeAttribute(name) {
  return this.removeAttributeNS(null, name);
};
DOMElement.prototype.hasAttribute = function _Element_hasAttribute(name) {
  return this.hasAttributeNS(null, name);
};
DOMElement.prototype.removeEventListener = removeEventListener_1;
DOMElement.prototype.addEventListener = addEventListener_1;
DOMElement.prototype.dispatchEvent = dispatchEvent_1;

// Un-implemented
DOMElement.prototype.focus = function _Element_focus() {
  return void 0;
};
DOMElement.prototype.toString = function _Element_toString() {
  return serialize(this);
};
DOMElement.prototype.getElementsByClassName = function _Element_getElementsByClassName(classNames) {
  var classes = classNames.split(" ");
  var elems = [];
  domWalk(this, function (node) {
    if (node.nodeType === 1) {
      var nodeClassName = node.className || "";
      var nodeClasses = nodeClassName.split(" ");
      if (classes.every(function (item) {
        return nodeClasses.indexOf(item) !== -1;
      })) {
        elems.push(node);
      }
    }
  });
  return elems;
};
DOMElement.prototype.getElementsByTagName = function _Element_getElementsByTagName(tagName) {
  tagName = tagName.toLowerCase();
  var elems = [];
  domWalk(this.childNodes, function (node) {
    if (node.nodeType === 1 && (tagName === '*' || node.tagName.toLowerCase() === tagName)) {
      elems.push(node);
    }
  });
  return elems;
};
DOMElement.prototype.contains = function _Element_contains(element) {
  return domWalk(this, function (node) {
    return element === node;
  }) || false;
};

var domFragment = DocumentFragment;
function DocumentFragment(owner) {
  if (!(this instanceof DocumentFragment)) {
    return new DocumentFragment();
  }
  this.childNodes = [];
  this.parentNode = null;
  this.ownerDocument = owner || null;
}
DocumentFragment.prototype.type = "DocumentFragment";
DocumentFragment.prototype.nodeType = 11;
DocumentFragment.prototype.nodeName = "#document-fragment";
DocumentFragment.prototype.appendChild = domElement.prototype.appendChild;
DocumentFragment.prototype.replaceChild = domElement.prototype.replaceChild;
DocumentFragment.prototype.removeChild = domElement.prototype.removeChild;
DocumentFragment.prototype.toString = function _DocumentFragment_toString() {
  return this.childNodes.map(function (node) {
    return String(node);
  }).join("");
};

var event$1 = Event$1;
function Event$1(family) {}
Event$1.prototype.initEvent = function _Event_initEvent(type, bubbles, cancelable) {
  this.type = type;
  this.bubbles = bubbles;
  this.cancelable = cancelable;
};
Event$1.prototype.preventDefault = function _Event_preventDefault() {};

var document$2 = Document;
function Document() {
  if (!(this instanceof Document)) {
    return new Document();
  }
  this.head = this.createElement("head");
  this.body = this.createElement("body");
  this.documentElement = this.createElement("html");
  this.documentElement.appendChild(this.head);
  this.documentElement.appendChild(this.body);
  this.childNodes = [this.documentElement];
  this.nodeType = 9;
}
var proto = Document.prototype;
proto.createTextNode = function createTextNode(value) {
  return new domText(value, this);
};
proto.createElementNS = function createElementNS(namespace, tagName) {
  var ns = namespace === null ? null : String(namespace);
  return new domElement(tagName, this, ns);
};
proto.createElement = function createElement(tagName) {
  return new domElement(tagName, this);
};
proto.createDocumentFragment = function createDocumentFragment() {
  return new domFragment(this);
};
proto.createEvent = function createEvent(family) {
  return new event$1();
};
proto.createComment = function createComment(data) {
  return new domComment(data, this);
};
proto.getElementById = function getElementById(id) {
  id = String(id);
  var result = domWalk(this.childNodes, function (node) {
    if (String(node.id) === id) {
      return node;
    }
  });
  return result || null;
};
proto.getElementsByClassName = domElement.prototype.getElementsByClassName;
proto.getElementsByTagName = domElement.prototype.getElementsByTagName;
proto.contains = domElement.prototype.contains;
proto.removeEventListener = removeEventListener_1;
proto.addEventListener = addEventListener_1;
proto.dispatchEvent = dispatchEvent_1;

var minDocument = new document$2();

var topLevel = typeof commonjsGlobal !== 'undefined' ? commonjsGlobal : typeof window !== 'undefined' ? window : {};

var doccy;
if (typeof document !== 'undefined') {
  doccy = document;
} else {
  doccy = topLevel['__GLOBAL_DOCUMENT_CACHE@4'];
  if (!doccy) {
    doccy = topLevel['__GLOBAL_DOCUMENT_CACHE@4'] = minDocument;
  }
}
var document_1 = doccy;

function clean(s) {
  return s.replace(/\n\r?\s*/g, '');
}
var tsml$1 = function tsml(sa) {
  var s = '',
    i = 0;
  for (; i < arguments.length; i++) s += clean(sa[i]) + (arguments[i + 1] || '');
  return s;
};

var tuple = SafeParseTuple;
function SafeParseTuple(obj, reviver) {
  var json;
  var error = null;
  try {
    json = JSON.parse(obj, reviver);
  } catch (err) {
    error = err;
  }
  return [error, json];
}

var isFunction_1 = isFunction;
var toString$1 = Object.prototype.toString;
function isFunction(fn) {
  if (!fn) {
    return false;
  }
  var string = toString$1.call(fn);
  return string === '[object Function]' || typeof fn === 'function' && string !== '[object RegExp]' || typeof window !== 'undefined' && (
  // IE8 and below
  fn === window.setTimeout || fn === window.alert || fn === window.confirm || fn === window.prompt);
}

var trim = function (string) {
    return string.replace(/^\s+|\s+$/g, '');
  },
  isArray = function (arg) {
    return Object.prototype.toString.call(arg) === '[object Array]';
  };
var parseHeaders = function (headers) {
  if (!headers) return {};
  var result = {};
  var headersArr = trim(headers).split('\n');
  for (var i = 0; i < headersArr.length; i++) {
    var row = headersArr[i];
    var index = row.indexOf(':'),
      key = trim(row.slice(0, index)).toLowerCase(),
      value = trim(row.slice(index + 1));
    if (typeof result[key] === 'undefined') {
      result[key] = value;
    } else if (isArray(result[key])) {
      result[key].push(value);
    } else {
      result[key] = [result[key], value];
    }
  }
  return result;
};

var immutable = extend$1;
var hasOwnProperty = Object.prototype.hasOwnProperty;
function extend$1() {
  var target = {};
  for (var i = 0; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
}

var xhr$1 = createXHR;
createXHR.XMLHttpRequest = window_1.XMLHttpRequest || noop;
createXHR.XDomainRequest = "withCredentials" in new createXHR.XMLHttpRequest() ? createXHR.XMLHttpRequest : window_1.XDomainRequest;
forEachArray(["get", "put", "post", "patch", "head", "delete"], function (method) {
  createXHR[method === "delete" ? "del" : method] = function (uri, options, callback) {
    options = initParams(uri, options, callback);
    options.method = method.toUpperCase();
    return _createXHR(options);
  };
});
function forEachArray(array, iterator) {
  for (var i = 0; i < array.length; i++) {
    iterator(array[i]);
  }
}
function isEmpty(obj) {
  for (var i in obj) {
    if (obj.hasOwnProperty(i)) return false;
  }
  return true;
}
function initParams(uri, options, callback) {
  var params = uri;
  if (isFunction_1(options)) {
    callback = options;
    if (typeof uri === "string") {
      params = {
        uri: uri
      };
    }
  } else {
    params = immutable(options, {
      uri: uri
    });
  }
  params.callback = callback;
  return params;
}
function createXHR(uri, options, callback) {
  options = initParams(uri, options, callback);
  return _createXHR(options);
}
function _createXHR(options) {
  if (typeof options.callback === "undefined") {
    throw new Error("callback argument missing");
  }
  var called = false;
  var callback = function cbOnce(err, response, body) {
    if (!called) {
      called = true;
      options.callback(err, response, body);
    }
  };
  function readystatechange() {
    if (xhr.readyState === 4) {
      setTimeout(loadFunc, 0);
    }
  }
  function getBody() {
    // Chrome with requestType=blob throws errors arround when even testing access to responseText
    var body = undefined;
    if (xhr.response) {
      body = xhr.response;
    } else {
      body = xhr.responseText || getXml(xhr);
    }
    if (isJson) {
      try {
        body = JSON.parse(body);
      } catch (e) {}
    }
    return body;
  }
  function errorFunc(evt) {
    clearTimeout(timeoutTimer);
    if (!(evt instanceof Error)) {
      evt = new Error("" + (evt || "Unknown XMLHttpRequest Error"));
    }
    evt.statusCode = 0;
    return callback(evt, failureResponse);
  }

  // will load the data & process the response in a special response object
  function loadFunc() {
    if (aborted) return;
    var status;
    clearTimeout(timeoutTimer);
    if (options.useXDR && xhr.status === undefined) {
      //IE8 CORS GET successful response doesn't have a status field, but body is fine
      status = 200;
    } else {
      status = xhr.status === 1223 ? 204 : xhr.status;
    }
    var response = failureResponse;
    var err = null;
    if (status !== 0) {
      response = {
        body: getBody(),
        statusCode: status,
        method: method,
        headers: {},
        url: uri,
        rawRequest: xhr
      };
      if (xhr.getAllResponseHeaders) {
        //remember xhr can in fact be XDR for CORS in IE
        response.headers = parseHeaders(xhr.getAllResponseHeaders());
      }
    } else {
      err = new Error("Internal XMLHttpRequest Error");
    }
    return callback(err, response, response.body);
  }
  var xhr = options.xhr || null;
  if (!xhr) {
    if (options.cors || options.useXDR) {
      xhr = new createXHR.XDomainRequest();
    } else {
      xhr = new createXHR.XMLHttpRequest();
    }
  }
  var key;
  var aborted;
  var uri = xhr.url = options.uri || options.url;
  var method = xhr.method = options.method || "GET";
  var body = options.body || options.data;
  var headers = xhr.headers = options.headers || {};
  var sync = !!options.sync;
  var isJson = false;
  var timeoutTimer;
  var failureResponse = {
    body: undefined,
    headers: {},
    statusCode: 0,
    method: method,
    url: uri,
    rawRequest: xhr
  };
  if ("json" in options && options.json !== false) {
    isJson = true;
    headers["accept"] || headers["Accept"] || (headers["Accept"] = "application/json"); //Don't override existing accept header declared by user
    if (method !== "GET" && method !== "HEAD") {
      headers["content-type"] || headers["Content-Type"] || (headers["Content-Type"] = "application/json"); //Don't override existing accept header declared by user
      body = JSON.stringify(options.json === true ? body : options.json);
    }
  }
  xhr.onreadystatechange = readystatechange;
  xhr.onload = loadFunc;
  xhr.onerror = errorFunc;
  // IE9 must have onprogress be set to a unique function.
  xhr.onprogress = function () {
    // IE must die
  };
  xhr.onabort = function () {
    aborted = true;
  };
  xhr.ontimeout = errorFunc;
  xhr.open(method, uri, !sync, options.username, options.password);
  //has to be after open
  if (!sync) {
    xhr.withCredentials = !!options.withCredentials;
  }
  // Cannot set timeout with sync request
  // not setting timeout on the xhr object, because of old webkits etc. not handling that correctly
  // both npm's request and jquery 1.x use this kind of timeout, so this is being consistent
  if (!sync && options.timeout > 0) {
    timeoutTimer = setTimeout(function () {
      if (aborted) return;
      aborted = true; //IE9 may still call readystatechange
      xhr.abort("timeout");
      var e = new Error("XMLHttpRequest timeout");
      e.code = "ETIMEDOUT";
      errorFunc(e);
    }, options.timeout);
  }
  if (xhr.setRequestHeader) {
    for (key in headers) {
      if (headers.hasOwnProperty(key)) {
        xhr.setRequestHeader(key, headers[key]);
      }
    }
  } else if (options.headers && !isEmpty(options.headers)) {
    throw new Error("Headers cannot be set on an XDomainRequest object");
  }
  if ("responseType" in options) {
    xhr.responseType = options.responseType;
  }
  if ("beforeSend" in options && typeof options.beforeSend === "function") {
    options.beforeSend(xhr);
  }

  // Microsoft Edge browser sends "undefined" when send is called with undefined value.
  // XMLHttpRequest spec says to pass null as body to indicate no body
  // See https://github.com/naugtur/xhr/issues/100.
  xhr.send(body || null);
  return xhr;
}
function getXml(xhr) {
  if (xhr.responseType === "document") {
    return xhr.responseXML;
  }
  var firefoxBugTakenEffect = xhr.responseXML && xhr.responseXML.documentElement.nodeName === "parsererror";
  if (xhr.responseType === "" && !firefoxBugTakenEffect) {
    return xhr.responseXML;
  }
  return null;
}
function noop() {}

/**
 * Copyright 2013 vtt.js Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* -*- Mode: Java; tab-width: 2; indent-tabs-mode: nil; c-basic-offset: 2 -*- */
/* vim: set shiftwidth=2 tabstop=2 autoindent cindent expandtab: */
var _objCreate = Object.create || function () {
  function F() {}
  return function (o) {
    if (arguments.length !== 1) {
      throw new Error('Object.create shim only accepts one parameter.');
    }
    F.prototype = o;
    return new F();
  };
}();

// Creates a new ParserError object from an errorData object. The errorData
// object should have default code and message properties. The default message
// property can be overriden by passing in a message parameter.
// See ParsingError.Errors below for acceptable errors.
function ParsingError(errorData, message) {
  this.name = "ParsingError";
  this.code = errorData.code;
  this.message = message || errorData.message;
}
ParsingError.prototype = _objCreate(Error.prototype);
ParsingError.prototype.constructor = ParsingError;

// ParsingError metadata for acceptable ParsingErrors.
ParsingError.Errors = {
  BadSignature: {
    code: 0,
    message: "Malformed WebVTT signature."
  },
  BadTimeStamp: {
    code: 1,
    message: "Malformed time stamp."
  }
};

// Try to parse input as a time stamp.
function parseTimeStamp(input) {
  function computeSeconds(h, m, s, f) {
    return (h | 0) * 3600 + (m | 0) * 60 + (s | 0) + (f | 0) / 1000;
  }
  var m = input.match(/^(\d+):(\d{2})(:\d{2})?\.(\d{3})/);
  if (!m) {
    return null;
  }
  if (m[3]) {
    // Timestamp takes the form of [hours]:[minutes]:[seconds].[milliseconds]
    return computeSeconds(m[1], m[2], m[3].replace(":", ""), m[4]);
  } else if (m[1] > 59) {
    // Timestamp takes the form of [hours]:[minutes].[milliseconds]
    // First position is hours as it's over 59.
    return computeSeconds(m[1], m[2], 0, m[4]);
  } else {
    // Timestamp takes the form of [minutes]:[seconds].[milliseconds]
    return computeSeconds(0, m[1], m[2], m[4]);
  }
}

// A settings object holds key/value pairs and will ignore anything but the first
// assignment to a specific key.
function Settings() {
  this.values = _objCreate(null);
}
Settings.prototype = {
  // Only accept the first assignment to any key.
  set: function (k, v) {
    if (!this.get(k) && v !== "") {
      this.values[k] = v;
    }
  },
  // Return the value for a key, or a default value.
  // If 'defaultKey' is passed then 'dflt' is assumed to be an object with
  // a number of possible default values as properties where 'defaultKey' is
  // the key of the property that will be chosen; otherwise it's assumed to be
  // a single value.
  get: function (k, dflt, defaultKey) {
    if (defaultKey) {
      return this.has(k) ? this.values[k] : dflt[defaultKey];
    }
    return this.has(k) ? this.values[k] : dflt;
  },
  // Check whether we have a value for a key.
  has: function (k) {
    return k in this.values;
  },
  // Accept a setting if its one of the given alternatives.
  alt: function (k, v, a) {
    for (var n = 0; n < a.length; ++n) {
      if (v === a[n]) {
        this.set(k, v);
        break;
      }
    }
  },
  // Accept a setting if its a valid (signed) integer.
  integer: function (k, v) {
    if (/^-?\d+$/.test(v)) {
      // integer
      this.set(k, parseInt(v, 10));
    }
  },
  // Accept a setting if its a valid percentage.
  percent: function (k, v) {
    if (v.match(/^([\d]{1,3})(\.[\d]*)?%$/)) {
      v = parseFloat(v);
      if (v >= 0 && v <= 100) {
        this.set(k, v);
        return true;
      }
    }
    return false;
  }
};

// Helper function to parse input into groups separated by 'groupDelim', and
// interprete each group as a key/value pair separated by 'keyValueDelim'.
function parseOptions(input, callback, keyValueDelim, groupDelim) {
  var groups = groupDelim ? input.split(groupDelim) : [input];
  for (var i in groups) {
    if (typeof groups[i] !== "string") {
      continue;
    }
    var kv = groups[i].split(keyValueDelim);
    if (kv.length !== 2) {
      continue;
    }
    var k = kv[0];
    var v = kv[1];
    callback(k, v);
  }
}
function parseCue(input, cue, regionList) {
  // Remember the original input if we need to throw an error.
  var oInput = input;
  // 4.1 WebVTT timestamp
  function consumeTimeStamp() {
    var ts = parseTimeStamp(input);
    if (ts === null) {
      throw new ParsingError(ParsingError.Errors.BadTimeStamp, "Malformed timestamp: " + oInput);
    }
    // Remove time stamp from input.
    input = input.replace(/^[^\sa-zA-Z-]+/, "");
    return ts;
  }

  // 4.4.2 WebVTT cue settings
  function consumeCueSettings(input, cue) {
    var settings = new Settings();
    parseOptions(input, function (k, v) {
      switch (k) {
        case "region":
          // Find the last region we parsed with the same region id.
          for (var i = regionList.length - 1; i >= 0; i--) {
            if (regionList[i].id === v) {
              settings.set(k, regionList[i].region);
              break;
            }
          }
          break;
        case "vertical":
          settings.alt(k, v, ["rl", "lr"]);
          break;
        case "line":
          var vals = v.split(","),
            vals0 = vals[0];
          settings.integer(k, vals0);
          settings.percent(k, vals0) ? settings.set("snapToLines", false) : null;
          settings.alt(k, vals0, ["auto"]);
          if (vals.length === 2) {
            settings.alt("lineAlign", vals[1], ["start", "middle", "end"]);
          }
          break;
        case "position":
          vals = v.split(",");
          settings.percent(k, vals[0]);
          if (vals.length === 2) {
            settings.alt("positionAlign", vals[1], ["start", "middle", "end"]);
          }
          break;
        case "size":
          settings.percent(k, v);
          break;
        case "align":
          settings.alt(k, v, ["start", "middle", "end", "left", "right"]);
          break;
      }
    }, /:/, /\s/);

    // Apply default values for any missing fields.
    cue.region = settings.get("region", null);
    cue.vertical = settings.get("vertical", "");
    cue.line = settings.get("line", "auto");
    cue.lineAlign = settings.get("lineAlign", "start");
    cue.snapToLines = settings.get("snapToLines", true);
    cue.size = settings.get("size", 100);
    cue.align = settings.get("align", "middle");
    cue.position = settings.get("position", {
      start: 0,
      left: 0,
      middle: 50,
      end: 100,
      right: 100
    }, cue.align);
    cue.positionAlign = settings.get("positionAlign", {
      start: "start",
      left: "start",
      middle: "middle",
      end: "end",
      right: "end"
    }, cue.align);
  }
  function skipWhitespace() {
    input = input.replace(/^\s+/, "");
  }

  // 4.1 WebVTT cue timings.
  skipWhitespace();
  cue.startTime = consumeTimeStamp(); // (1) collect cue start time
  skipWhitespace();
  if (input.substr(0, 3) !== "-->") {
    // (3) next characters must match "-->"
    throw new ParsingError(ParsingError.Errors.BadTimeStamp, "Malformed time stamp (time stamps must be separated by '-->'): " + oInput);
  }
  input = input.substr(3);
  skipWhitespace();
  cue.endTime = consumeTimeStamp(); // (5) collect cue end time

  // 4.1 WebVTT cue settings list.
  skipWhitespace();
  consumeCueSettings(input, cue);
}
var ESCAPE = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&lrm;": "\u200e",
  "&rlm;": "\u200f",
  "&nbsp;": "\u00a0"
};
var TAG_NAME = {
  c: "span",
  i: "i",
  b: "b",
  u: "u",
  ruby: "ruby",
  rt: "rt",
  v: "span",
  lang: "span"
};
var TAG_ANNOTATION = {
  v: "title",
  lang: "lang"
};
var NEEDS_PARENT = {
  rt: "ruby"
};

// Parse content into a document fragment.
function parseContent(window, input) {
  function nextToken() {
    // Check for end-of-string.
    if (!input) {
      return null;
    }

    // Consume 'n' characters from the input.
    function consume(result) {
      input = input.substr(result.length);
      return result;
    }
    var m = input.match(/^([^<]*)(<[^>]*>?)?/);
    // If there is some text before the next tag, return it, otherwise return
    // the tag.
    return consume(m[1] ? m[1] : m[2]);
  }

  // Unescape a string 's'.
  function unescape1(e) {
    return ESCAPE[e];
  }
  function unescape(s) {
    while (m = s.match(/&(amp|lt|gt|lrm|rlm|nbsp);/)) {
      s = s.replace(m[0], unescape1);
    }
    return s;
  }
  function shouldAdd(current, element) {
    return !NEEDS_PARENT[element.localName] || NEEDS_PARENT[element.localName] === current.localName;
  }

  // Create an element for this tag.
  function createElement(type, annotation) {
    var tagName = TAG_NAME[type];
    if (!tagName) {
      return null;
    }
    var element = window.document.createElement(tagName);
    element.localName = tagName;
    var name = TAG_ANNOTATION[type];
    if (name && annotation) {
      element[name] = annotation.trim();
    }
    return element;
  }
  var rootDiv = window.document.createElement("div"),
    current = rootDiv,
    t,
    tagStack = [];
  while ((t = nextToken()) !== null) {
    if (t[0] === '<') {
      if (t[1] === "/") {
        // If the closing tag matches, move back up to the parent node.
        if (tagStack.length && tagStack[tagStack.length - 1] === t.substr(2).replace(">", "")) {
          tagStack.pop();
          current = current.parentNode;
        }
        // Otherwise just ignore the end tag.
        continue;
      }
      var ts = parseTimeStamp(t.substr(1, t.length - 2));
      var node;
      if (ts) {
        // Timestamps are lead nodes as well.
        node = window.document.createProcessingInstruction("timestamp", ts);
        current.appendChild(node);
        continue;
      }
      var m = t.match(/^<([^.\s/0-9>]+)(\.[^\s\\>]+)?([^>\\]+)?(\\?)>?$/);
      // If we can't parse the tag, skip to the next tag.
      if (!m) {
        continue;
      }
      // Try to construct an element, and ignore the tag if we couldn't.
      node = createElement(m[1], m[3]);
      if (!node) {
        continue;
      }
      // Determine if the tag should be added based on the context of where it
      // is placed in the cuetext.
      if (!shouldAdd(current, node)) {
        continue;
      }
      // Set the class list (as a list of classes, separated by space).
      if (m[2]) {
        node.className = m[2].substr(1).replace('.', ' ');
      }
      // Append the node to the current node, and enter the scope of the new
      // node.
      tagStack.push(m[1]);
      current.appendChild(node);
      current = node;
      continue;
    }

    // Text nodes are leaf nodes.
    current.appendChild(window.document.createTextNode(unescape(t)));
  }
  return rootDiv;
}

// This is a list of all the Unicode characters that have a strong
// right-to-left category. What this means is that these characters are
// written right-to-left for sure. It was generated by pulling all the strong
// right-to-left characters out of the Unicode data table. That table can
// found at: http://www.unicode.org/Public/UNIDATA/UnicodeData.txt
var strongRTLRanges = [[0x5be, 0x5be], [0x5c0, 0x5c0], [0x5c3, 0x5c3], [0x5c6, 0x5c6], [0x5d0, 0x5ea], [0x5f0, 0x5f4], [0x608, 0x608], [0x60b, 0x60b], [0x60d, 0x60d], [0x61b, 0x61b], [0x61e, 0x64a], [0x66d, 0x66f], [0x671, 0x6d5], [0x6e5, 0x6e6], [0x6ee, 0x6ef], [0x6fa, 0x70d], [0x70f, 0x710], [0x712, 0x72f], [0x74d, 0x7a5], [0x7b1, 0x7b1], [0x7c0, 0x7ea], [0x7f4, 0x7f5], [0x7fa, 0x7fa], [0x800, 0x815], [0x81a, 0x81a], [0x824, 0x824], [0x828, 0x828], [0x830, 0x83e], [0x840, 0x858], [0x85e, 0x85e], [0x8a0, 0x8a0], [0x8a2, 0x8ac], [0x200f, 0x200f], [0xfb1d, 0xfb1d], [0xfb1f, 0xfb28], [0xfb2a, 0xfb36], [0xfb38, 0xfb3c], [0xfb3e, 0xfb3e], [0xfb40, 0xfb41], [0xfb43, 0xfb44], [0xfb46, 0xfbc1], [0xfbd3, 0xfd3d], [0xfd50, 0xfd8f], [0xfd92, 0xfdc7], [0xfdf0, 0xfdfc], [0xfe70, 0xfe74], [0xfe76, 0xfefc], [0x10800, 0x10805], [0x10808, 0x10808], [0x1080a, 0x10835], [0x10837, 0x10838], [0x1083c, 0x1083c], [0x1083f, 0x10855], [0x10857, 0x1085f], [0x10900, 0x1091b], [0x10920, 0x10939], [0x1093f, 0x1093f], [0x10980, 0x109b7], [0x109be, 0x109bf], [0x10a00, 0x10a00], [0x10a10, 0x10a13], [0x10a15, 0x10a17], [0x10a19, 0x10a33], [0x10a40, 0x10a47], [0x10a50, 0x10a58], [0x10a60, 0x10a7f], [0x10b00, 0x10b35], [0x10b40, 0x10b55], [0x10b58, 0x10b72], [0x10b78, 0x10b7f], [0x10c00, 0x10c48], [0x1ee00, 0x1ee03], [0x1ee05, 0x1ee1f], [0x1ee21, 0x1ee22], [0x1ee24, 0x1ee24], [0x1ee27, 0x1ee27], [0x1ee29, 0x1ee32], [0x1ee34, 0x1ee37], [0x1ee39, 0x1ee39], [0x1ee3b, 0x1ee3b], [0x1ee42, 0x1ee42], [0x1ee47, 0x1ee47], [0x1ee49, 0x1ee49], [0x1ee4b, 0x1ee4b], [0x1ee4d, 0x1ee4f], [0x1ee51, 0x1ee52], [0x1ee54, 0x1ee54], [0x1ee57, 0x1ee57], [0x1ee59, 0x1ee59], [0x1ee5b, 0x1ee5b], [0x1ee5d, 0x1ee5d], [0x1ee5f, 0x1ee5f], [0x1ee61, 0x1ee62], [0x1ee64, 0x1ee64], [0x1ee67, 0x1ee6a], [0x1ee6c, 0x1ee72], [0x1ee74, 0x1ee77], [0x1ee79, 0x1ee7c], [0x1ee7e, 0x1ee7e], [0x1ee80, 0x1ee89], [0x1ee8b, 0x1ee9b], [0x1eea1, 0x1eea3], [0x1eea5, 0x1eea9], [0x1eeab, 0x1eebb], [0x10fffd, 0x10fffd]];
function isStrongRTLChar(charCode) {
  for (var i = 0; i < strongRTLRanges.length; i++) {
    var currentRange = strongRTLRanges[i];
    if (charCode >= currentRange[0] && charCode <= currentRange[1]) {
      return true;
    }
  }
  return false;
}
function determineBidi(cueDiv) {
  var nodeStack = [],
    text = "",
    charCode;
  if (!cueDiv || !cueDiv.childNodes) {
    return "ltr";
  }
  function pushNodes(nodeStack, node) {
    for (var i = node.childNodes.length - 1; i >= 0; i--) {
      nodeStack.push(node.childNodes[i]);
    }
  }
  function nextTextNode(nodeStack) {
    if (!nodeStack || !nodeStack.length) {
      return null;
    }
    var node = nodeStack.pop(),
      text = node.textContent || node.innerText;
    if (text) {
      // TODO: This should match all unicode type B characters (paragraph
      // separator characters). See issue #115.
      var m = text.match(/^.*(\n|\r)/);
      if (m) {
        nodeStack.length = 0;
        return m[0];
      }
      return text;
    }
    if (node.tagName === "ruby") {
      return nextTextNode(nodeStack);
    }
    if (node.childNodes) {
      pushNodes(nodeStack, node);
      return nextTextNode(nodeStack);
    }
  }
  pushNodes(nodeStack, cueDiv);
  while (text = nextTextNode(nodeStack)) {
    for (var i = 0; i < text.length; i++) {
      charCode = text.charCodeAt(i);
      if (isStrongRTLChar(charCode)) {
        return "rtl";
      }
    }
  }
  return "ltr";
}
function computeLinePos(cue) {
  if (typeof cue.line === "number" && (cue.snapToLines || cue.line >= 0 && cue.line <= 100)) {
    return cue.line;
  }
  if (!cue.track || !cue.track.textTrackList || !cue.track.textTrackList.mediaElement) {
    return -1;
  }
  var track = cue.track,
    trackList = track.textTrackList,
    count = 0;
  for (var i = 0; i < trackList.length && trackList[i] !== track; i++) {
    if (trackList[i].mode === "showing") {
      count++;
    }
  }
  return ++count * -1;
}
function StyleBox() {}

// Apply styles to a div. If there is no div passed then it defaults to the
// div on 'this'.
StyleBox.prototype.applyStyles = function (styles, div) {
  div = div || this.div;
  for (var prop in styles) {
    if (styles.hasOwnProperty(prop)) {
      div.style[prop] = styles[prop];
    }
  }
};
StyleBox.prototype.formatStyle = function (val, unit) {
  return val === 0 ? 0 : val + unit;
};

// Constructs the computed display state of the cue (a div). Places the div
// into the overlay which should be a block level element (usually a div).
function CueStyleBox(window, cue, styleOptions) {
  var isIE8 = /MSIE\s8\.0/.test(navigator.userAgent);
  var color = "rgba(255, 255, 255, 1)";
  var backgroundColor = "rgba(0, 0, 0, 0.8)";
  if (isIE8) {
    color = "rgb(255, 255, 255)";
    backgroundColor = "rgb(0, 0, 0)";
  }
  StyleBox.call(this);
  this.cue = cue;

  // Parse our cue's text into a DOM tree rooted at 'cueDiv'. This div will
  // have inline positioning and will function as the cue background box.
  this.cueDiv = parseContent(window, cue.text);
  var styles = {
    color: color,
    backgroundColor: backgroundColor,
    position: "relative",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: "inline"
  };
  if (!isIE8) {
    styles.writingMode = cue.vertical === "" ? "horizontal-tb" : cue.vertical === "lr" ? "vertical-lr" : "vertical-rl";
    styles.unicodeBidi = "plaintext";
  }
  this.applyStyles(styles, this.cueDiv);

  // Create an absolutely positioned div that will be used to position the cue
  // div. Note, all WebVTT cue-setting alignments are equivalent to the CSS
  // mirrors of them except "middle" which is "center" in CSS.
  this.div = window.document.createElement("div");
  styles = {
    textAlign: cue.align === "middle" ? "center" : cue.align,
    font: styleOptions.font,
    whiteSpace: "pre-line",
    position: "absolute"
  };
  if (!isIE8) {
    styles.direction = determineBidi(this.cueDiv);
    styles.writingMode = cue.vertical === "" ? "horizontal-tb" : cue.vertical === "lr" ? "vertical-lr" : "vertical-rl".stylesunicodeBidi = "plaintext";
  }
  this.applyStyles(styles);
  this.div.appendChild(this.cueDiv);

  // Calculate the distance from the reference edge of the viewport to the text
  // position of the cue box. The reference edge will be resolved later when
  // the box orientation styles are applied.
  var textPos = 0;
  switch (cue.positionAlign) {
    case "start":
      textPos = cue.position;
      break;
    case "middle":
      textPos = cue.position - cue.size / 2;
      break;
    case "end":
      textPos = cue.position - cue.size;
      break;
  }

  // Horizontal box orientation; textPos is the distance from the left edge of the
  // area to the left edge of the box and cue.size is the distance extending to
  // the right from there.
  if (cue.vertical === "") {
    this.applyStyles({
      left: this.formatStyle(textPos, "%"),
      width: this.formatStyle(cue.size, "%")
    });
    // Vertical box orientation; textPos is the distance from the top edge of the
    // area to the top edge of the box and cue.size is the height extending
    // downwards from there.
  } else {
    this.applyStyles({
      top: this.formatStyle(textPos, "%"),
      height: this.formatStyle(cue.size, "%")
    });
  }
  this.move = function (box) {
    this.applyStyles({
      top: this.formatStyle(box.top, "px"),
      bottom: this.formatStyle(box.bottom, "px"),
      left: this.formatStyle(box.left, "px"),
      right: this.formatStyle(box.right, "px"),
      height: this.formatStyle(box.height, "px"),
      width: this.formatStyle(box.width, "px")
    });
  };
}
CueStyleBox.prototype = _objCreate(StyleBox.prototype);
CueStyleBox.prototype.constructor = CueStyleBox;

// Represents the co-ordinates of an Element in a way that we can easily
// compute things with such as if it overlaps or intersects with another Element.
// Can initialize it with either a StyleBox or another BoxPosition.
function BoxPosition(obj) {
  var isIE8 = /MSIE\s8\.0/.test(navigator.userAgent);

  // Either a BoxPosition was passed in and we need to copy it, or a StyleBox
  // was passed in and we need to copy the results of 'getBoundingClientRect'
  // as the object returned is readonly. All co-ordinate values are in reference
  // to the viewport origin (top left).
  var lh, height, width, top;
  if (obj.div) {
    height = obj.div.offsetHeight;
    width = obj.div.offsetWidth;
    top = obj.div.offsetTop;
    var rects = (rects = obj.div.childNodes) && (rects = rects[0]) && rects.getClientRects && rects.getClientRects();
    obj = obj.div.getBoundingClientRect();
    // In certain cases the outter div will be slightly larger then the sum of
    // the inner div's lines. This could be due to bold text, etc, on some platforms.
    // In this case we should get the average line height and use that. This will
    // result in the desired behaviour.
    lh = rects ? Math.max(rects[0] && rects[0].height || 0, obj.height / rects.length) : 0;
  }
  this.left = obj.left;
  this.right = obj.right;
  this.top = obj.top || top;
  this.height = obj.height || height;
  this.bottom = obj.bottom || top + (obj.height || height);
  this.width = obj.width || width;
  this.lineHeight = lh !== undefined ? lh : obj.lineHeight;
  if (isIE8 && !this.lineHeight) {
    this.lineHeight = 13;
  }
}

// Move the box along a particular axis. Optionally pass in an amount to move
// the box. If no amount is passed then the default is the line height of the
// box.
BoxPosition.prototype.move = function (axis, toMove) {
  toMove = toMove !== undefined ? toMove : this.lineHeight;
  switch (axis) {
    case "+x":
      this.left += toMove;
      this.right += toMove;
      break;
    case "-x":
      this.left -= toMove;
      this.right -= toMove;
      break;
    case "+y":
      this.top += toMove;
      this.bottom += toMove;
      break;
    case "-y":
      this.top -= toMove;
      this.bottom -= toMove;
      break;
  }
};

// Check if this box overlaps another box, b2.
BoxPosition.prototype.overlaps = function (b2) {
  return this.left < b2.right && this.right > b2.left && this.top < b2.bottom && this.bottom > b2.top;
};

// Check if this box overlaps any other boxes in boxes.
BoxPosition.prototype.overlapsAny = function (boxes) {
  for (var i = 0; i < boxes.length; i++) {
    if (this.overlaps(boxes[i])) {
      return true;
    }
  }
  return false;
};

// Check if this box is within another box.
BoxPosition.prototype.within = function (container) {
  return this.top >= container.top && this.bottom <= container.bottom && this.left >= container.left && this.right <= container.right;
};

// Check if this box is entirely within the container or it is overlapping
// on the edge opposite of the axis direction passed. For example, if "+x" is
// passed and the box is overlapping on the left edge of the container, then
// return true.
BoxPosition.prototype.overlapsOppositeAxis = function (container, axis) {
  switch (axis) {
    case "+x":
      return this.left < container.left;
    case "-x":
      return this.right > container.right;
    case "+y":
      return this.top < container.top;
    case "-y":
      return this.bottom > container.bottom;
  }
};

// Find the percentage of the area that this box is overlapping with another
// box.
BoxPosition.prototype.intersectPercentage = function (b2) {
  var x = Math.max(0, Math.min(this.right, b2.right) - Math.max(this.left, b2.left)),
    y = Math.max(0, Math.min(this.bottom, b2.bottom) - Math.max(this.top, b2.top)),
    intersectArea = x * y;
  return intersectArea / (this.height * this.width);
};

// Convert the positions from this box to CSS compatible positions using
// the reference container's positions. This has to be done because this
// box's positions are in reference to the viewport origin, whereas, CSS
// values are in referecne to their respective edges.
BoxPosition.prototype.toCSSCompatValues = function (reference) {
  return {
    top: this.top - reference.top,
    bottom: reference.bottom - this.bottom,
    left: this.left - reference.left,
    right: reference.right - this.right,
    height: this.height,
    width: this.width
  };
};

// Get an object that represents the box's position without anything extra.
// Can pass a StyleBox, HTMLElement, or another BoxPositon.
BoxPosition.getSimpleBoxPosition = function (obj) {
  var height = obj.div ? obj.div.offsetHeight : obj.tagName ? obj.offsetHeight : 0;
  var width = obj.div ? obj.div.offsetWidth : obj.tagName ? obj.offsetWidth : 0;
  var top = obj.div ? obj.div.offsetTop : obj.tagName ? obj.offsetTop : 0;
  obj = obj.div ? obj.div.getBoundingClientRect() : obj.tagName ? obj.getBoundingClientRect() : obj;
  var ret = {
    left: obj.left,
    right: obj.right,
    top: obj.top || top,
    height: obj.height || height,
    bottom: obj.bottom || top + (obj.height || height),
    width: obj.width || width
  };
  return ret;
};

// Move a StyleBox to its specified, or next best, position. The containerBox
// is the box that contains the StyleBox, such as a div. boxPositions are
// a list of other boxes that the styleBox can't overlap with.
function moveBoxToLinePosition(window, styleBox, containerBox, boxPositions) {
  // Find the best position for a cue box, b, on the video. The axis parameter
  // is a list of axis, the order of which, it will move the box along. For example:
  // Passing ["+x", "-x"] will move the box first along the x axis in the positive
  // direction. If it doesn't find a good position for it there it will then move
  // it along the x axis in the negative direction.
  function findBestPosition(b, axis) {
    var bestPosition,
      specifiedPosition = new BoxPosition(b),
      percentage = 1; // Highest possible so the first thing we get is better.

    for (var i = 0; i < axis.length; i++) {
      while (b.overlapsOppositeAxis(containerBox, axis[i]) || b.within(containerBox) && b.overlapsAny(boxPositions)) {
        b.move(axis[i]);
      }
      // We found a spot where we aren't overlapping anything. This is our
      // best position.
      if (b.within(containerBox)) {
        return b;
      }
      var p = b.intersectPercentage(containerBox);
      // If we're outside the container box less then we were on our last try
      // then remember this position as the best position.
      if (percentage > p) {
        bestPosition = new BoxPosition(b);
        percentage = p;
      }
      // Reset the box position to the specified position.
      b = new BoxPosition(specifiedPosition);
    }
    return bestPosition || specifiedPosition;
  }
  var boxPosition = new BoxPosition(styleBox),
    cue = styleBox.cue,
    linePos = computeLinePos(cue),
    axis = [];

  // If we have a line number to align the cue to.
  if (cue.snapToLines) {
    var size;
    switch (cue.vertical) {
      case "":
        axis = ["+y", "-y"];
        size = "height";
        break;
      case "rl":
        axis = ["+x", "-x"];
        size = "width";
        break;
      case "lr":
        axis = ["-x", "+x"];
        size = "width";
        break;
    }
    var step = boxPosition.lineHeight,
      position = step * Math.round(linePos),
      maxPosition = containerBox[size] + step,
      initialAxis = axis[0];

    // If the specified intial position is greater then the max position then
    // clamp the box to the amount of steps it would take for the box to
    // reach the max position.
    if (Math.abs(position) > maxPosition) {
      position = position < 0 ? -1 : 1;
      position *= Math.ceil(maxPosition / step) * step;
    }

    // If computed line position returns negative then line numbers are
    // relative to the bottom of the video instead of the top. Therefore, we
    // need to increase our initial position by the length or width of the
    // video, depending on the writing direction, and reverse our axis directions.
    if (linePos < 0) {
      position += cue.vertical === "" ? containerBox.height : containerBox.width;
      axis = axis.reverse();
    }

    // Move the box to the specified position. This may not be its best
    // position.
    boxPosition.move(initialAxis, position);
  } else {
    // If we have a percentage line value for the cue.
    var calculatedPercentage = boxPosition.lineHeight / containerBox.height * 100;
    switch (cue.lineAlign) {
      case "middle":
        linePos -= calculatedPercentage / 2;
        break;
      case "end":
        linePos -= calculatedPercentage;
        break;
    }

    // Apply initial line position to the cue box.
    switch (cue.vertical) {
      case "":
        styleBox.applyStyles({
          top: styleBox.formatStyle(linePos, "%")
        });
        break;
      case "rl":
        styleBox.applyStyles({
          left: styleBox.formatStyle(linePos, "%")
        });
        break;
      case "lr":
        styleBox.applyStyles({
          right: styleBox.formatStyle(linePos, "%")
        });
        break;
    }
    axis = ["+y", "-x", "+x", "-y"];

    // Get the box position again after we've applied the specified positioning
    // to it.
    boxPosition = new BoxPosition(styleBox);
  }
  var bestPosition = findBestPosition(boxPosition, axis);
  styleBox.move(bestPosition.toCSSCompatValues(containerBox));
}
function WebVTT$1() {
  // Nothing
}

// Helper to allow strings to be decoded instead of the default binary utf8 data.
WebVTT$1.StringDecoder = function () {
  return {
    decode: function (data) {
      if (!data) {
        return "";
      }
      if (typeof data !== "string") {
        throw new Error("Error - expected string data.");
      }
      return decodeURIComponent(encodeURIComponent(data));
    }
  };
};
WebVTT$1.convertCueToDOMTree = function (window, cuetext) {
  if (!window || !cuetext) {
    return null;
  }
  return parseContent(window, cuetext);
};
var FONT_SIZE_PERCENT = 0.05;
var FONT_STYLE = "sans-serif";
var CUE_BACKGROUND_PADDING = "1.5%";

// Runs the processing model over the cues and regions passed to it.
// @param overlay A block level element (usually a div) that the computed cues
//                and regions will be placed into.
WebVTT$1.processCues = function (window, cues, overlay) {
  if (!window || !cues || !overlay) {
    return null;
  }

  // Remove all previous children.
  while (overlay.firstChild) {
    overlay.removeChild(overlay.firstChild);
  }
  var paddedOverlay = window.document.createElement("div");
  paddedOverlay.style.position = "absolute";
  paddedOverlay.style.left = "0";
  paddedOverlay.style.right = "0";
  paddedOverlay.style.top = "0";
  paddedOverlay.style.bottom = "0";
  paddedOverlay.style.margin = CUE_BACKGROUND_PADDING;
  overlay.appendChild(paddedOverlay);

  // Determine if we need to compute the display states of the cues. This could
  // be the case if a cue's state has been changed since the last computation or
  // if it has not been computed yet.
  function shouldCompute(cues) {
    for (var i = 0; i < cues.length; i++) {
      if (cues[i].hasBeenReset || !cues[i].displayState) {
        return true;
      }
    }
    return false;
  }

  // We don't need to recompute the cues' display states. Just reuse them.
  if (!shouldCompute(cues)) {
    for (var i = 0; i < cues.length; i++) {
      paddedOverlay.appendChild(cues[i].displayState);
    }
    return;
  }
  var boxPositions = [],
    containerBox = BoxPosition.getSimpleBoxPosition(paddedOverlay),
    fontSize = Math.round(containerBox.height * FONT_SIZE_PERCENT * 100) / 100;
  var styleOptions = {
    font: fontSize + "px " + FONT_STYLE
  };
  (function () {
    var styleBox, cue;
    for (var i = 0; i < cues.length; i++) {
      cue = cues[i];

      // Compute the intial position and styles of the cue div.
      styleBox = new CueStyleBox(window, cue, styleOptions);
      paddedOverlay.appendChild(styleBox.div);

      // Move the cue div to it's correct line position.
      moveBoxToLinePosition(window, styleBox, containerBox, boxPositions);

      // Remember the computed div so that we don't have to recompute it later
      // if we don't have too.
      cue.displayState = styleBox.div;
      boxPositions.push(BoxPosition.getSimpleBoxPosition(styleBox));
    }
  })();
};
WebVTT$1.Parser = function (window, vttjs, decoder) {
  if (!decoder) {
    decoder = vttjs;
    vttjs = {};
  }
  if (!vttjs) {
    vttjs = {};
  }
  this.window = window;
  this.vttjs = vttjs;
  this.state = "INITIAL";
  this.buffer = "";
  this.decoder = decoder || new TextDecoder("utf8");
  this.regionList = [];
};
WebVTT$1.Parser.prototype = {
  // If the error is a ParsingError then report it to the consumer if
  // possible. If it's not a ParsingError then throw it like normal.
  reportOrThrowError: function (e) {
    if (e instanceof ParsingError) {
      this.onparsingerror && this.onparsingerror(e);
    } else {
      throw e;
    }
  },
  parse: function (data) {
    var self = this;

    // If there is no data then we won't decode it, but will just try to parse
    // whatever is in buffer already. This may occur in circumstances, for
    // example when flush() is called.
    if (data) {
      // Try to decode the data that we received.
      self.buffer += self.decoder.decode(data, {
        stream: true
      });
    }
    function collectNextLine() {
      var buffer = self.buffer;
      var pos = 0;
      while (pos < buffer.length && buffer[pos] !== '\r' && buffer[pos] !== '\n') {
        ++pos;
      }
      var line = buffer.substr(0, pos);
      // Advance the buffer early in case we fail below.
      if (buffer[pos] === '\r') {
        ++pos;
      }
      if (buffer[pos] === '\n') {
        ++pos;
      }
      self.buffer = buffer.substr(pos);
      return line;
    }

    // 3.4 WebVTT region and WebVTT region settings syntax
    function parseRegion(input) {
      var settings = new Settings();
      parseOptions(input, function (k, v) {
        switch (k) {
          case "id":
            settings.set(k, v);
            break;
          case "width":
            settings.percent(k, v);
            break;
          case "lines":
            settings.integer(k, v);
            break;
          case "regionanchor":
          case "viewportanchor":
            var xy = v.split(',');
            if (xy.length !== 2) {
              break;
            }
            // We have to make sure both x and y parse, so use a temporary
            // settings object here.
            var anchor = new Settings();
            anchor.percent("x", xy[0]);
            anchor.percent("y", xy[1]);
            if (!anchor.has("x") || !anchor.has("y")) {
              break;
            }
            settings.set(k + "X", anchor.get("x"));
            settings.set(k + "Y", anchor.get("y"));
            break;
          case "scroll":
            settings.alt(k, v, ["up"]);
            break;
        }
      }, /=/, /\s/);

      // Create the region, using default values for any values that were not
      // specified.
      if (settings.has("id")) {
        var region = new (self.vttjs.VTTRegion || self.window.VTTRegion)();
        region.width = settings.get("width", 100);
        region.lines = settings.get("lines", 3);
        region.regionAnchorX = settings.get("regionanchorX", 0);
        region.regionAnchorY = settings.get("regionanchorY", 100);
        region.viewportAnchorX = settings.get("viewportanchorX", 0);
        region.viewportAnchorY = settings.get("viewportanchorY", 100);
        region.scroll = settings.get("scroll", "");
        // Register the region.
        self.onregion && self.onregion(region);
        // Remember the VTTRegion for later in case we parse any VTTCues that
        // reference it.
        self.regionList.push({
          id: settings.get("id"),
          region: region
        });
      }
    }

    // draft-pantos-http-live-streaming-20
    // https://tools.ietf.org/html/draft-pantos-http-live-streaming-20#section-3.5
    // 3.5 WebVTT
    function parseTimestampMap(input) {
      var settings = new Settings();
      parseOptions(input, function (k, v) {
        switch (k) {
          case "MPEGT":
            settings.integer(k + 'S', v);
            break;
          case "LOCA":
            settings.set(k + 'L', parseTimeStamp(v));
            break;
        }
      }, /[^\d]:/, /,/);
      self.ontimestampmap && self.ontimestampmap({
        "MPEGTS": settings.get("MPEGTS"),
        "LOCAL": settings.get("LOCAL")
      });
    }

    // 3.2 WebVTT metadata header syntax
    function parseHeader(input) {
      if (input.match(/X-TIMESTAMP-MAP/)) {
        // This line contains HLS X-TIMESTAMP-MAP metadata
        parseOptions(input, function (k, v) {
          switch (k) {
            case "X-TIMESTAMP-MAP":
              parseTimestampMap(v);
              break;
          }
        }, /=/);
      } else {
        parseOptions(input, function (k, v) {
          switch (k) {
            case "Region":
              // 3.3 WebVTT region metadata header syntax
              parseRegion(v);
              break;
          }
        }, /:/);
      }
    }

    // 5.1 WebVTT file parsing.
    try {
      var line;
      if (self.state === "INITIAL") {
        // We can't start parsing until we have the first line.
        if (!/\r\n|\n/.test(self.buffer)) {
          return this;
        }
        line = collectNextLine();
        var m = line.match(/^WEBVTT([ \t].*)?$/);
        if (!m || !m[0]) {
          throw new ParsingError(ParsingError.Errors.BadSignature);
        }
        self.state = "HEADER";
      }
      var alreadyCollectedLine = false;
      while (self.buffer) {
        // We can't parse a line until we have the full line.
        if (!/\r\n|\n/.test(self.buffer)) {
          return this;
        }
        if (!alreadyCollectedLine) {
          line = collectNextLine();
        } else {
          alreadyCollectedLine = false;
        }
        switch (self.state) {
          case "HEADER":
            // 13-18 - Allow a header (metadata) under the WEBVTT line.
            if (/:/.test(line)) {
              parseHeader(line);
            } else if (!line) {
              // An empty line terminates the header and starts the body (cues).
              self.state = "ID";
            }
            continue;
          case "NOTE":
            // Ignore NOTE blocks.
            if (!line) {
              self.state = "ID";
            }
            continue;
          case "ID":
            // Check for the start of NOTE blocks.
            if (/^NOTE($|[ \t])/.test(line)) {
              self.state = "NOTE";
              break;
            }
            // 19-29 - Allow any number of line terminators, then initialize new cue values.
            if (!line) {
              continue;
            }
            self.cue = new (self.vttjs.VTTCue || self.window.VTTCue)(0, 0, "");
            self.state = "CUE";
            // 30-39 - Check if self line contains an optional identifier or timing data.
            if (line.indexOf("-->") === -1) {
              self.cue.id = line;
              continue;
            }
          // Process line as start of a cue.
          /*falls through*/
          case "CUE":
            // 40 - Collect cue timings and settings.
            try {
              parseCue(line, self.cue, self.regionList);
            } catch (e) {
              self.reportOrThrowError(e);
              // In case of an error ignore rest of the cue.
              self.cue = null;
              self.state = "BADCUE";
              continue;
            }
            self.state = "CUETEXT";
            continue;
          case "CUETEXT":
            var hasSubstring = line.indexOf("-->") !== -1;
            // 34 - If we have an empty line then report the cue.
            // 35 - If we have the special substring '-->' then report the cue,
            // but do not collect the line as we need to process the current
            // one as a new cue.
            if (!line || hasSubstring && (alreadyCollectedLine = true)) {
              // We are done parsing self cue.
              self.oncue && self.oncue(self.cue);
              self.cue = null;
              self.state = "ID";
              continue;
            }
            if (self.cue.text) {
              self.cue.text += "\n";
            }
            self.cue.text += line;
            continue;
          case "BADCUE":
            // BADCUE
            // 54-62 - Collect and discard the remaining cue.
            if (!line) {
              self.state = "ID";
            }
            continue;
        }
      }
    } catch (e) {
      self.reportOrThrowError(e);

      // If we are currently parsing a cue, report what we have.
      if (self.state === "CUETEXT" && self.cue && self.oncue) {
        self.oncue(self.cue);
      }
      self.cue = null;
      // Enter BADWEBVTT state if header was not parsed correctly otherwise
      // another exception occurred so enter BADCUE state.
      self.state = self.state === "INITIAL" ? "BADWEBVTT" : "BADCUE";
    }
    return this;
  },
  flush: function () {
    var self = this;
    try {
      // Finish decoding the stream.
      self.buffer += self.decoder.decode();
      // Synthesize the end of the current cue or region.
      if (self.cue || self.state === "HEADER") {
        self.buffer += "\n\n";
        self.parse();
      }
      // If we've flushed, parsed, and we're still on the INITIAL state then
      // that means we don't have enough of the stream to parse the first
      // line.
      if (self.state === "INITIAL") {
        throw new ParsingError(ParsingError.Errors.BadSignature);
      }
    } catch (e) {
      self.reportOrThrowError(e);
    }
    self.onflush && self.onflush();
    return this;
  }
};
var vtt$1 = WebVTT$1;

/**
 * Copyright 2013 vtt.js Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var autoKeyword = "auto";
var directionSetting = {
  "": true,
  "lr": true,
  "rl": true
};
var alignSetting = {
  "start": true,
  "middle": true,
  "end": true,
  "left": true,
  "right": true
};
function findDirectionSetting(value) {
  if (typeof value !== "string") {
    return false;
  }
  var dir = directionSetting[value.toLowerCase()];
  return dir ? value.toLowerCase() : false;
}
function findAlignSetting(value) {
  if (typeof value !== "string") {
    return false;
  }
  var align = alignSetting[value.toLowerCase()];
  return align ? value.toLowerCase() : false;
}
function extend(obj) {
  var i = 1;
  for (; i < arguments.length; i++) {
    var cobj = arguments[i];
    for (var p in cobj) {
      obj[p] = cobj[p];
    }
  }
  return obj;
}
function VTTCue(startTime, endTime, text) {
  var cue = this;
  var isIE8 = /MSIE\s8\.0/.test(navigator.userAgent);
  var baseObj = {};
  if (isIE8) {
    cue = document.createElement('custom');
  } else {
    baseObj.enumerable = true;
  }

  /**
   * Shim implementation specific properties. These properties are not in
   * the spec.
   */

  // Lets us know when the VTTCue's data has changed in such a way that we need
  // to recompute its display state. This lets us compute its display state
  // lazily.
  cue.hasBeenReset = false;

  /**
   * VTTCue and TextTrackCue properties
   * http://dev.w3.org/html5/webvtt/#vttcue-interface
   */

  var _id = "";
  var _pauseOnExit = false;
  var _startTime = startTime;
  var _endTime = endTime;
  var _text = text;
  var _region = null;
  var _vertical = "";
  var _snapToLines = true;
  var _line = "auto";
  var _lineAlign = "start";
  var _position = 50;
  var _positionAlign = "middle";
  var _size = 50;
  var _align = "middle";
  Object.defineProperty(cue, "id", extend({}, baseObj, {
    get: function () {
      return _id;
    },
    set: function (value) {
      _id = "" + value;
    }
  }));
  Object.defineProperty(cue, "pauseOnExit", extend({}, baseObj, {
    get: function () {
      return _pauseOnExit;
    },
    set: function (value) {
      _pauseOnExit = !!value;
    }
  }));
  Object.defineProperty(cue, "startTime", extend({}, baseObj, {
    get: function () {
      return _startTime;
    },
    set: function (value) {
      if (typeof value !== "number") {
        throw new TypeError("Start time must be set to a number.");
      }
      _startTime = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "endTime", extend({}, baseObj, {
    get: function () {
      return _endTime;
    },
    set: function (value) {
      if (typeof value !== "number") {
        throw new TypeError("End time must be set to a number.");
      }
      _endTime = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "text", extend({}, baseObj, {
    get: function () {
      return _text;
    },
    set: function (value) {
      _text = "" + value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "region", extend({}, baseObj, {
    get: function () {
      return _region;
    },
    set: function (value) {
      _region = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "vertical", extend({}, baseObj, {
    get: function () {
      return _vertical;
    },
    set: function (value) {
      var setting = findDirectionSetting(value);
      // Have to check for false because the setting an be an empty string.
      if (setting === false) {
        throw new SyntaxError("An invalid or illegal string was specified.");
      }
      _vertical = setting;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "snapToLines", extend({}, baseObj, {
    get: function () {
      return _snapToLines;
    },
    set: function (value) {
      _snapToLines = !!value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "line", extend({}, baseObj, {
    get: function () {
      return _line;
    },
    set: function (value) {
      if (typeof value !== "number" && value !== autoKeyword) {
        throw new SyntaxError("An invalid number or illegal string was specified.");
      }
      _line = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "lineAlign", extend({}, baseObj, {
    get: function () {
      return _lineAlign;
    },
    set: function (value) {
      var setting = findAlignSetting(value);
      if (!setting) {
        throw new SyntaxError("An invalid or illegal string was specified.");
      }
      _lineAlign = setting;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "position", extend({}, baseObj, {
    get: function () {
      return _position;
    },
    set: function (value) {
      if (value < 0 || value > 100) {
        throw new Error("Position must be between 0 and 100.");
      }
      _position = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "positionAlign", extend({}, baseObj, {
    get: function () {
      return _positionAlign;
    },
    set: function (value) {
      var setting = findAlignSetting(value);
      if (!setting) {
        throw new SyntaxError("An invalid or illegal string was specified.");
      }
      _positionAlign = setting;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "size", extend({}, baseObj, {
    get: function () {
      return _size;
    },
    set: function (value) {
      if (value < 0 || value > 100) {
        throw new Error("Size must be between 0 and 100.");
      }
      _size = value;
      this.hasBeenReset = true;
    }
  }));
  Object.defineProperty(cue, "align", extend({}, baseObj, {
    get: function () {
      return _align;
    },
    set: function (value) {
      var setting = findAlignSetting(value);
      if (!setting) {
        throw new SyntaxError("An invalid or illegal string was specified.");
      }
      _align = setting;
      this.hasBeenReset = true;
    }
  }));

  /**
   * Other <track> spec defined properties
   */

  // http://www.whatwg.org/specs/web-apps/current-work/multipage/the-video-element.html#text-track-cue-display-state
  cue.displayState = undefined;
  if (isIE8) {
    return cue;
  }
}

/**
 * VTTCue methods
 */

VTTCue.prototype.getCueAsHTML = function () {
  // Assume WebVTT.convertCueToDOMTree is on the global.
  return WebVTT.convertCueToDOMTree(window, this.text);
};
var vttcue = VTTCue;

/**
 * Copyright 2013 vtt.js Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var scrollSetting = {
  "": true,
  "up": true
};
function findScrollSetting(value) {
  if (typeof value !== "string") {
    return false;
  }
  var scroll = scrollSetting[value.toLowerCase()];
  return scroll ? value.toLowerCase() : false;
}
function isValidPercentValue(value) {
  return typeof value === "number" && value >= 0 && value <= 100;
}

// VTTRegion shim http://dev.w3.org/html5/webvtt/#vttregion-interface
function VTTRegion() {
  var _width = 100;
  var _lines = 3;
  var _regionAnchorX = 0;
  var _regionAnchorY = 100;
  var _viewportAnchorX = 0;
  var _viewportAnchorY = 100;
  var _scroll = "";
  Object.defineProperties(this, {
    "width": {
      enumerable: true,
      get: function () {
        return _width;
      },
      set: function (value) {
        if (!isValidPercentValue(value)) {
          throw new Error("Width must be between 0 and 100.");
        }
        _width = value;
      }
    },
    "lines": {
      enumerable: true,
      get: function () {
        return _lines;
      },
      set: function (value) {
        if (typeof value !== "number") {
          throw new TypeError("Lines must be set to a number.");
        }
        _lines = value;
      }
    },
    "regionAnchorY": {
      enumerable: true,
      get: function () {
        return _regionAnchorY;
      },
      set: function (value) {
        if (!isValidPercentValue(value)) {
          throw new Error("RegionAnchorX must be between 0 and 100.");
        }
        _regionAnchorY = value;
      }
    },
    "regionAnchorX": {
      enumerable: true,
      get: function () {
        return _regionAnchorX;
      },
      set: function (value) {
        if (!isValidPercentValue(value)) {
          throw new Error("RegionAnchorY must be between 0 and 100.");
        }
        _regionAnchorX = value;
      }
    },
    "viewportAnchorY": {
      enumerable: true,
      get: function () {
        return _viewportAnchorY;
      },
      set: function (value) {
        if (!isValidPercentValue(value)) {
          throw new Error("ViewportAnchorY must be between 0 and 100.");
        }
        _viewportAnchorY = value;
      }
    },
    "viewportAnchorX": {
      enumerable: true,
      get: function () {
        return _viewportAnchorX;
      },
      set: function (value) {
        if (!isValidPercentValue(value)) {
          throw new Error("ViewportAnchorX must be between 0 and 100.");
        }
        _viewportAnchorX = value;
      }
    },
    "scroll": {
      enumerable: true,
      get: function () {
        return _scroll;
      },
      set: function (value) {
        var setting = findScrollSetting(value);
        // Have to check for false as an empty string is a legal value.
        if (setting === false) {
          throw new SyntaxError("An invalid or illegal string was specified.");
        }
        _scroll = setting;
      }
    }
  });
}
var vttregion = VTTRegion;

var browserIndex = createCommonjsModule(function (module) {
/**
 * Copyright 2013 vtt.js Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Default exports for Node. Export the extended versions of VTTCue and
// VTTRegion in Node since we likely want the capability to convert back and
// forth between JSON. If we don't then it's not that big of a deal since we're
// off browser.


var vttjs = module.exports = {
  WebVTT: vtt$1,
  VTTCue: vttcue,
  VTTRegion: vttregion
};
window_1.vttjs = vttjs;
window_1.WebVTT = vttjs.WebVTT;
var cueShim = vttjs.VTTCue;
var regionShim = vttjs.VTTRegion;
var nativeVTTCue = window_1.VTTCue;
var nativeVTTRegion = window_1.VTTRegion;
vttjs.shim = function () {
  window_1.VTTCue = cueShim;
  window_1.VTTRegion = regionShim;
};
vttjs.restore = function () {
  window_1.VTTCue = nativeVTTCue;
  window_1.VTTRegion = nativeVTTRegion;
};
if (!window_1.VTTCue) {
  vttjs.shim();
}
});
browserIndex.WebVTT;
browserIndex.VTTCue;
browserIndex.VTTRegion;

/**
 * @license
 * Video.js 6.13.0 <http://videojs.com/>
 * Copyright Brightcove, Inc. <https://www.brightcove.com/>
 * Available under Apache License Version 2.0
 * <https://github.com/videojs/video.js/blob/master/LICENSE>
 *
 * Includes vtt.js <https://github.com/mozilla/vtt.js>
 * Available under Apache License Version 2.0
 * <https://github.com/mozilla/vtt.js/blob/master/LICENSE>
 */function _interopDefault(ex){return ex&&typeof ex==='object'&&'default'in ex?ex['default']:ex;}var window$1=_interopDefault(window_1);var document$1=_interopDefault(document_1);var tsml=_interopDefault(tsml$1);var safeParseTuple=_interopDefault(tuple);var xhr=_interopDefault(xhr$1);var vtt=_interopDefault(browserIndex);var version="6.13.0";/**
 * @file browser.js
 * @module browser
 */var USER_AGENT=window$1.navigator&&window$1.navigator.userAgent||'';var webkitVersionMap=/AppleWebKit\/([\d.]+)/i.exec(USER_AGENT);var appleWebkitVersion=webkitVersionMap?parseFloat(webkitVersionMap.pop()):null;/*
 * Device is an iPhone
 *
 * @type {Boolean}
 * @constant
 * @private
 */var IS_IPAD=/iPad/i.test(USER_AGENT);// The Facebook app's UIWebView identifies as both an iPhone and iPad, so
// to identify iPhones, we need to exclude iPads.
// http://artsy.github.io/blog/2012/10/18/the-perils-of-ios-user-agent-sniffing/
var IS_IPHONE=/iPhone/i.test(USER_AGENT)&&!IS_IPAD;var IS_IPOD=/iPod/i.test(USER_AGENT);var IS_IOS=IS_IPHONE||IS_IPAD||IS_IPOD;var IOS_VERSION=function(){var match=USER_AGENT.match(/OS (\d+)_/i);if(match&&match[1]){return match[1];}return null;}();var IS_ANDROID=/Android/i.test(USER_AGENT);var ANDROID_VERSION=function(){// This matches Android Major.Minor.Patch versions
// ANDROID_VERSION is Major.Minor as a Number, if Minor isn't available, then only Major is returned
var match=USER_AGENT.match(/Android (\d+)(?:\.(\d+))?(?:\.(\d+))*/i);if(!match){return null;}var major=match[1]&&parseFloat(match[1]);var minor=match[2]&&parseFloat(match[2]);if(major&&minor){return parseFloat(match[1]+'.'+match[2]);}else if(major){return major;}return null;}();// Old Android is defined as Version older than 2.3, and requiring a webkit version of the android browser
var IS_OLD_ANDROID=IS_ANDROID&&/webkit/i.test(USER_AGENT)&&ANDROID_VERSION<2.3;var IS_NATIVE_ANDROID=IS_ANDROID&&ANDROID_VERSION<5&&appleWebkitVersion<537;var IS_FIREFOX=/Firefox/i.test(USER_AGENT);var IS_EDGE=/Edge/i.test(USER_AGENT);var IS_CHROME=!IS_EDGE&&(/Chrome/i.test(USER_AGENT)||/CriOS/i.test(USER_AGENT));var CHROME_VERSION=function(){var match=USER_AGENT.match(/(Chrome|CriOS)\/(\d+)/);if(match&&match[2]){return parseFloat(match[2]);}return null;}();var IS_IE8=/MSIE\s8\.0/.test(USER_AGENT);var IE_VERSION=function(){var result=/MSIE\s(\d+)\.\d/.exec(USER_AGENT);var version=result&&parseFloat(result[1]);if(!version&&/Trident\/7.0/i.test(USER_AGENT)&&/rv:11.0/.test(USER_AGENT)){// IE 11 has a different user agent string than other IE versions
version=11.0;}return version;}();var IS_SAFARI=/Safari/i.test(USER_AGENT)&&!IS_CHROME&&!IS_ANDROID&&!IS_EDGE;var IS_ANY_SAFARI=(IS_SAFARI||IS_IOS)&&!IS_CHROME;var TOUCH_ENABLED=isReal()&&('ontouchstart'in window$1||window$1.navigator.maxTouchPoints||window$1.DocumentTouch&&window$1.document instanceof window$1.DocumentTouch);var BACKGROUND_SIZE_SUPPORTED=isReal()&&'backgroundSize'in window$1.document.createElement('video').style;var browser=(Object.freeze||Object)({IS_IPAD:IS_IPAD,IS_IPHONE:IS_IPHONE,IS_IPOD:IS_IPOD,IS_IOS:IS_IOS,IOS_VERSION:IOS_VERSION,IS_ANDROID:IS_ANDROID,ANDROID_VERSION:ANDROID_VERSION,IS_OLD_ANDROID:IS_OLD_ANDROID,IS_NATIVE_ANDROID:IS_NATIVE_ANDROID,IS_FIREFOX:IS_FIREFOX,IS_EDGE:IS_EDGE,IS_CHROME:IS_CHROME,CHROME_VERSION:CHROME_VERSION,IS_IE8:IS_IE8,IE_VERSION:IE_VERSION,IS_SAFARI:IS_SAFARI,IS_ANY_SAFARI:IS_ANY_SAFARI,TOUCH_ENABLED:TOUCH_ENABLED,BACKGROUND_SIZE_SUPPORTED:BACKGROUND_SIZE_SUPPORTED});var _typeof$2=typeof Symbol==="function"&&typeof Symbol.iterator==="symbol"?function(obj){return typeof obj;}:function(obj){return obj&&typeof Symbol==="function"&&obj.constructor===Symbol&&obj!==Symbol.prototype?"symbol":typeof obj;};var classCallCheck=function(instance,Constructor){if(!(instance instanceof Constructor)){throw new TypeError("Cannot call a class as a function");}};var inherits=function(subClass,superClass){if(typeof superClass!=="function"&&superClass!==null){throw new TypeError("Super expression must either be null or a function, not "+typeof superClass);}subClass.prototype=Object.create(superClass&&superClass.prototype,{constructor:{value:subClass,enumerable:false,writable:true,configurable:true}});if(superClass)Object.setPrototypeOf?Object.setPrototypeOf(subClass,superClass):subClass.__proto__=superClass;};var possibleConstructorReturn=function(self,call){if(!self){throw new ReferenceError("this hasn't been initialised - super() hasn't been called");}return call&&(typeof call==="object"||typeof call==="function")?call:self;};var taggedTemplateLiteralLoose=function(strings,raw){strings.raw=raw;return strings;};/**
 * @file obj.js
 * @module obj
 */ /**
 * @callback obj:EachCallback
 *
 * @param {Mixed} value
 *        The current key for the object that is being iterated over.
 *
 * @param {string} key
 *        The current key-value for object that is being iterated over
 */ /**
 * @callback obj:ReduceCallback
 *
 * @param {Mixed} accum
 *        The value that is accumulating over the reduce loop.
 *
 * @param {Mixed} value
 *        The current key for the object that is being iterated over.
 *
 * @param {string} key
 *        The current key-value for object that is being iterated over
 *
 * @return {Mixed}
 *         The new accumulated value.
 */var toString=Object.prototype.toString;/**
 * Get the keys of an Object
 *
 * @param {Object}
 *        The Object to get the keys from
 *
 * @return {string[]}
 *         An array of the keys from the object. Returns an empty array if the
 *         object passed in was invalid or had no keys.
 *
 * @private
 */var keys=function keys(object){return isObject(object)?Object.keys(object):[];};/**
 * Array-like iteration for objects.
 *
 * @param {Object} object
 *        The object to iterate over
 *
 * @param {obj:EachCallback} fn
 *        The callback function which is called for each key in the object.
 */function each(object,fn){keys(object).forEach(function(key){return fn(object[key],key);});}/**
 * Array-like reduce for objects.
 *
 * @param {Object} object
 *        The Object that you want to reduce.
 *
 * @param {Function} fn
 *         A callback function which is called for each key in the object. It
 *         receives the accumulated value and the per-iteration value and key
 *         as arguments.
 *
 * @param {Mixed} [initial = 0]
 *        Starting value
 *
 * @return {Mixed}
 *         The final accumulated value.
 */function reduce(object,fn){var initial=arguments.length>2&&arguments[2]!==undefined?arguments[2]:0;return keys(object).reduce(function(accum,key){return fn(accum,object[key],key);},initial);}/**
 * Object.assign-style object shallow merge/extend.
 *
 * @param  {Object} target
 * @param  {Object} ...sources
 * @return {Object}
 */function assign$1(target){for(var _len=arguments.length,sources=Array(_len>1?_len-1:0),_key=1;_key<_len;_key++){sources[_key-1]=arguments[_key];}if(Object.assign){return Object.assign.apply(Object,[target].concat(sources));}sources.forEach(function(source){if(!source){return;}each(source,function(value,key){target[key]=value;});});return target;}/**
 * Returns whether a value is an object of any kind - including DOM nodes,
 * arrays, regular expressions, etc. Not functions, though.
 *
 * This avoids the gotcha where using `typeof` on a `null` value
 * results in `'object'`.
 *
 * @param  {Object} value
 * @return {Boolean}
 */function isObject(value){return !!value&&(typeof value==='undefined'?'undefined':_typeof$2(value))==='object';}/**
 * Returns whether an object appears to be a "plain" object - that is, a
 * direct instance of `Object`.
 *
 * @param  {Object} value
 * @return {Boolean}
 */function isPlain(value){return isObject(value)&&toString.call(value)==='[object Object]'&&value.constructor===Object;}/**
 * @file create-logger.js
 * @module create-logger
 */ // This is the private tracking variable for the logging history.
var history=[];/**
 * Log messages to the console and history based on the type of message
 *
 * @private
 * @param  {string} type
 *         The name of the console method to use.
 *
 * @param  {Array} args
 *         The arguments to be passed to the matching console method.
 */var LogByTypeFactory=function LogByTypeFactory(name,log){return function(type,level,args,stringify){var lvl=log.levels[level];var lvlRegExp=new RegExp('^('+lvl+')$');if(type!=='log'){// Add the type to the front of the message when it's not "log".
args.unshift(type.toUpperCase()+':');}// Add console prefix after adding to history.
args.unshift(name+':');// Add a clone of the args at this point to history.
if(history){history.push([].concat(args));}// If there's no console then don't try to output messages, but they will
// still be stored in history.
if(!window$1.console){return;}// Was setting these once outside of this function, but containing them
// in the function makes it easier to test cases where console doesn't exist
// when the module is executed.
var fn=window$1.console[type];if(!fn&&type==='debug'){// Certain browsers don't have support for console.debug. For those, we
// should default to the closest comparable log.
fn=window$1.console.info||window$1.console.log;}// Bail out if there's no console or if this type is not allowed by the
// current logging level.
if(!fn||!lvl||!lvlRegExp.test(type)){return;}// IEs previous to 11 log objects uselessly as "[object Object]"; so, JSONify
// objects and arrays for those less-capable browsers.
if(stringify){args=args.map(function(a){if(isObject(a)||Array.isArray(a)){try{return JSON.stringify(a);}catch(x){return String(a);}}// Cast to string before joining, so we get null and undefined explicitly
// included in output (as we would in a modern console).
return String(a);}).join(' ');}// Old IE versions do not allow .apply() for console methods (they are
// reported as objects rather than functions).
if(!fn.apply){fn(args);}else {fn[Array.isArray(args)?'apply':'call'](window$1.console,args);}};};function createLogger$1(name){// This is the private tracking variable for logging level.
var level='info';// the curried logByType bound to the specific log and history
var logByType=void 0;/**
   * Logs plain debug messages. Similar to `console.log`.
   *
   * Due to [limitations](https://github.com/jsdoc3/jsdoc/issues/955#issuecomment-313829149)
   * of our JSDoc template, we cannot properly document this as both a function
   * and a namespace, so its function signature is documented here.
   *
   * #### Arguments
   * ##### *args
   * Mixed[]
   *
   * Any combination of values that could be passed to `console.log()`.
   *
   * #### Return Value
   *
   * `undefined`
   *
   * @namespace
   * @param    {Mixed[]} args
   *           One or more messages or objects that should be logged.
   */var log=function log(){var stringify=log.stringify||IE_VERSION&&IE_VERSION<11;for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}logByType('log',level,args,stringify);};// This is the logByType helper that the logging methods below use
logByType=LogByTypeFactory(name,log);/**
   * Create a new sublogger which chains the old name to the new name.
   *
   * For example, doing `videojs.log.createLogger('player')` and then using that logger will log the following:
   * ```js
   *  mylogger('foo');
   *  // > VIDEOJS: player: foo
   * ```
   *
   * @param {string} name
   *        The name to add call the new logger
   * @return {Object}
   */log.createLogger=function(subname){return createLogger$1(name+': '+subname);};/**
   * Enumeration of available logging levels, where the keys are the level names
   * and the values are `|`-separated strings containing logging methods allowed
   * in that logging level. These strings are used to create a regular expression
   * matching the function name being called.
   *
   * Levels provided by Video.js are:
   *
   * - `off`: Matches no calls. Any value that can be cast to `false` will have
   *   this effect. The most restrictive.
   * - `all`: Matches only Video.js-provided functions (`debug`, `log`,
   *   `log.warn`, and `log.error`).
   * - `debug`: Matches `log.debug`, `log`, `log.warn`, and `log.error` calls.
   * - `info` (default): Matches `log`, `log.warn`, and `log.error` calls.
   * - `warn`: Matches `log.warn` and `log.error` calls.
   * - `error`: Matches only `log.error` calls.
   *
   * @type {Object}
   */log.levels={all:'debug|log|warn|error',off:'',debug:'debug|log|warn|error',info:'log|warn|error',warn:'warn|error',error:'error',DEFAULT:level};/**
   * Get or set the current logging level.
   *
   * If a string matching a key from {@link module:log.levels} is provided, acts
   * as a setter.
   *
   * @param  {string} [lvl]
   *         Pass a valid level to set a new logging level.
   *
   * @return {string}
   *         The current logging level.
   */log.level=function(lvl){if(typeof lvl==='string'){if(!log.levels.hasOwnProperty(lvl)){throw new Error('"'+lvl+'" in not a valid log level');}level=lvl;}return level;};/**
   * Returns an array containing everything that has been logged to the history.
   *
   * This array is a shallow clone of the internal history record. However, its
   * contents are _not_ cloned; so, mutating objects inside this array will
   * mutate them in history.
   *
   * @return {Array}
   */log.history=function(){return history?[].concat(history):[];};/**
   * Allows you to filter the history by the given logger name
   *
   * @param {string} fname
   *        The name to filter by
   *
   * @return {Array}
   *         The filtered list to return
   */log.history.filter=function(fname){return (history||[]).filter(function(historyItem){// if the first item in each historyItem includes `fname`, then it's a match
return new RegExp('.*'+fname+'.*').test(historyItem[0]);});};/**
   * Clears the internal history tracking, but does not prevent further history
   * tracking.
   */log.history.clear=function(){if(history){history.length=0;}};/**
   * Disable history tracking if it is currently enabled.
   */log.history.disable=function(){if(history!==null){history.length=0;history=null;}};/**
   * Enable history tracking if it is currently disabled.
   */log.history.enable=function(){if(history===null){history=[];}};/**
   * Logs error messages. Similar to `console.error`.
   *
   * @param {Mixed[]} args
   *        One or more messages or objects that should be logged as an error
   */log.error=function(){for(var _len2=arguments.length,args=Array(_len2),_key2=0;_key2<_len2;_key2++){args[_key2]=arguments[_key2];}return logByType('error',level,args);};/**
   * Logs warning messages. Similar to `console.warn`.
   *
   * @param {Mixed[]} args
   *        One or more messages or objects that should be logged as a warning.
   */log.warn=function(){for(var _len3=arguments.length,args=Array(_len3),_key3=0;_key3<_len3;_key3++){args[_key3]=arguments[_key3];}return logByType('warn',level,args);};/**
   * Logs debug messages. Similar to `console.debug`, but may also act as a comparable
   * log if `console.debug` is not available
   *
   * @param {Mixed[]} args
   *        One or more messages or objects that should be logged as debug.
   */log.debug=function(){for(var _len4=arguments.length,args=Array(_len4),_key4=0;_key4<_len4;_key4++){args[_key4]=arguments[_key4];}return logByType('debug',level,args);};return log;}/**
 * @file log.js
 * @module log
 */var log$1=createLogger$1('VIDEOJS');var createLogger=log$1.createLogger;/**
 * @file computed-style.js
 * @module computed-style
 */ /**
 * A safe getComputedStyle with an IE8 fallback.
 *
 * This is needed because in Firefox, if the player is loaded in an iframe with
 * `display:none`, then `getComputedStyle` returns `null`, so, we do a null-check to
 * make sure  that the player doesn't break in these cases.
 *
 * @param {Element} el
 *        The element you want the computed style of
 *
 * @param {string} prop
 *        The property name you want
 *
 * @see https://bugzilla.mozilla.org/show_bug.cgi?id=548397
 *
 * @static
 * @const
 */function computedStyle(el,prop){if(!el||!prop){return '';}if(typeof window$1.getComputedStyle==='function'){var cs=window$1.getComputedStyle(el);return cs?cs[prop]:'';}return el.currentStyle[prop]||'';}var _templateObject=taggedTemplateLiteralLoose(['Setting attributes in the second argument of createEl()\n                has been deprecated. Use the third argument instead.\n                createEl(type, properties, attributes). Attempting to set ',' to ','.'],['Setting attributes in the second argument of createEl()\n                has been deprecated. Use the third argument instead.\n                createEl(type, properties, attributes). Attempting to set ',' to ','.']);/**
 * @file dom.js
 * @module dom
 */ /**
 * Detect if a value is a string with any non-whitespace characters.
 *
 * @param {string} str
 *        The string to check
 *
 * @return {boolean}
 *         - True if the string is non-blank
 *         - False otherwise
 *
 */function isNonBlankString(str){return typeof str==='string'&&/\S/.test(str);}/**
 * Throws an error if the passed string has whitespace. This is used by
 * class methods to be relatively consistent with the classList API.
 *
 * @param {string} str
 *         The string to check for whitespace.
 *
 * @throws {Error}
 *         Throws an error if there is whitespace in the string.
 *
 */function throwIfWhitespace(str){if(/\s/.test(str)){throw new Error('class has illegal whitespace characters');}}/**
 * Produce a regular expression for matching a className within an elements className.
 *
 * @param {string} className
 *         The className to generate the RegExp for.
 *
 * @return {RegExp}
 *         The RegExp that will check for a specific `className` in an elements
 *         className.
 */function classRegExp(className){return new RegExp('(^|\\s)'+className+'($|\\s)');}/**
 * Whether the current DOM interface appears to be real.
 *
 * @return {Boolean}
 */function isReal(){return(// Both document and window will never be undefined thanks to `global`.
document$1===window$1.document&&// In IE < 9, DOM methods return "object" as their type, so all we can
// confidently check is that it exists.
typeof document$1.createElement!=='undefined');}/**
 * Determines, via duck typing, whether or not a value is a DOM element.
 *
 * @param {Mixed} value
 *        The thing to check
 *
 * @return {boolean}
 *         - True if it is a DOM element
 *         - False otherwise
 */function isEl(value){return isObject(value)&&value.nodeType===1;}/**
 * Determines if the current DOM is embedded in an iframe.
 *
 * @return {boolean}
 *
 */function isInFrame(){// We need a try/catch here because Safari will throw errors when attempting
// to get either `parent` or `self`
try{return window$1.parent!==window$1.self;}catch(x){return true;}}/**
 * Creates functions to query the DOM using a given method.
 *
 * @param {string} method
 *         The method to create the query with.
 *
 * @return {Function}
 *         The query method
 */function createQuerier(method){return function(selector,context){if(!isNonBlankString(selector)){return document$1[method](null);}if(isNonBlankString(context)){context=document$1.querySelector(context);}var ctx=isEl(context)?context:document$1;return ctx[method]&&ctx[method](selector);};}/**
 * Creates an element and applies properties.
 *
 * @param {string} [tagName='div']
 *         Name of tag to be created.
 *
 * @param {Object} [properties={}]
 *         Element properties to be applied.
 *
 * @param {Object} [attributes={}]
 *         Element attributes to be applied.
 *
 * @param {String|Element|TextNode|Array|Function} [content]
 *         Contents for the element (see: {@link dom:normalizeContent})
 *
 * @return {Element}
 *         The element that was created.
 */function createEl(){var tagName=arguments.length>0&&arguments[0]!==undefined?arguments[0]:'div';var properties=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var attributes=arguments.length>2&&arguments[2]!==undefined?arguments[2]:{};var content=arguments[3];var el=document$1.createElement(tagName);Object.getOwnPropertyNames(properties).forEach(function(propName){var val=properties[propName];// See #2176
// We originally were accepting both properties and attributes in the
// same object, but that doesn't work so well.
if(propName.indexOf('aria-')!==-1||propName==='role'||propName==='type'){log$1.warn(tsml(_templateObject,propName,val));el.setAttribute(propName,val);// Handle textContent since it's not supported everywhere and we have a
// method for it.
}else if(propName==='textContent'){textContent(el,val);}else {el[propName]=val;}});Object.getOwnPropertyNames(attributes).forEach(function(attrName){el.setAttribute(attrName,attributes[attrName]);});if(content){appendContent(el,content);}return el;}/**
 * Injects text into an element, replacing any existing contents entirely.
 *
 * @param {Element} el
 *        The element to add text content into
 *
 * @param {string} text
 *        The text content to add.
 *
 * @return {Element}
 *         The element with added text content.
 */function textContent(el,text){if(typeof el.textContent==='undefined'){el.innerText=text;}else {el.textContent=text;}return el;}/**
 * Insert an element as the first child node of another
 *
 * @param {Element} child
 *        Element to insert
 *
 * @param {Element} parent
 *        Element to insert child into
 */function prependTo(child,parent){if(parent.firstChild){parent.insertBefore(child,parent.firstChild);}else {parent.appendChild(child);}}/**
 * Check if an element has a CSS class
 *
 * @param {Element} element
 *        Element to check
 *
 * @param {string} classToCheck
 *        Class name to check for
 *
 * @return {boolean}
 *         - True if the element had the class
 *         - False otherwise.
 *
 * @throws {Error}
 *         Throws an error if `classToCheck` has white space.
 */function hasClass(element,classToCheck){throwIfWhitespace(classToCheck);if(element.classList){return element.classList.contains(classToCheck);}return classRegExp(classToCheck).test(element.className);}/**
 * Add a CSS class name to an element
 *
 * @param {Element} element
 *        Element to add class name to.
 *
 * @param {string} classToAdd
 *        Class name to add.
 *
 * @return {Element}
 *         The dom element with the added class name.
 */function addClass(element,classToAdd){if(element.classList){element.classList.add(classToAdd);// Don't need to `throwIfWhitespace` here because `hasElClass` will do it
// in the case of classList not being supported.
}else if(!hasClass(element,classToAdd)){element.className=(element.className+' '+classToAdd).trim();}return element;}/**
 * Remove a CSS class name from an element
 *
 * @param {Element} element
 *        Element to remove a class name from.
 *
 * @param {string} classToRemove
 *        Class name to remove
 *
 * @return {Element}
 *         The dom element with class name removed.
 */function removeClass(element,classToRemove){if(element.classList){element.classList.remove(classToRemove);}else {throwIfWhitespace(classToRemove);element.className=element.className.split(/\s+/).filter(function(c){return c!==classToRemove;}).join(' ');}return element;}/**
 * The callback definition for toggleElClass.
 *
 * @callback Dom~PredicateCallback
 * @param {Element} element
 *        The DOM element of the Component.
 *
 * @param {string} classToToggle
 *        The `className` that wants to be toggled
 *
 * @return {boolean|undefined}
 *         - If true the `classToToggle` will get added to `element`.
 *         - If false the `classToToggle` will get removed from `element`.
 *         - If undefined this callback will be ignored
 */ /**
 * Adds or removes a CSS class name on an element depending on an optional
 * condition or the presence/absence of the class name.
 *
 * @param {Element} element
 *        The element to toggle a class name on.
 *
 * @param {string} classToToggle
 *        The class that should be toggled
 *
 * @param {boolean|PredicateCallback} [predicate]
 *        See the return value for {@link Dom~PredicateCallback}
 *
 * @return {Element}
 *         The element with a class that has been toggled.
 */function toggleClass(element,classToToggle,predicate){// This CANNOT use `classList` internally because IE does not support the
// second parameter to the `classList.toggle()` method! Which is fine because
// `classList` will be used by the add/remove functions.
var has=hasClass(element,classToToggle);if(typeof predicate==='function'){predicate=predicate(element,classToToggle);}if(typeof predicate!=='boolean'){predicate=!has;}// If the necessary class operation matches the current state of the
// element, no action is required.
if(predicate===has){return;}if(predicate){addClass(element,classToToggle);}else {removeClass(element,classToToggle);}return element;}/**
 * Apply attributes to an HTML element.
 *
 * @param {Element} el
 *        Element to add attributes to.
 *
 * @param {Object} [attributes]
 *        Attributes to be applied.
 */function setAttributes(el,attributes){Object.getOwnPropertyNames(attributes).forEach(function(attrName){var attrValue=attributes[attrName];if(attrValue===null||typeof attrValue==='undefined'||attrValue===false){el.removeAttribute(attrName);}else {el.setAttribute(attrName,attrValue===true?'':attrValue);}});}/**
 * Get an element's attribute values, as defined on the HTML tag
 * Attributes are not the same as properties. They're defined on the tag
 * or with setAttribute (which shouldn't be used with HTML)
 * This will return true or false for boolean attributes.
 *
 * @param {Element} tag
 *        Element from which to get tag attributes.
 *
 * @return {Object}
 *         All attributes of the element.
 */function getAttributes(tag){var obj={};// known boolean attributes
// we can check for matching boolean properties, but older browsers
// won't know about HTML5 boolean attributes that we still read from
var knownBooleans=','+'autoplay,controls,playsinline,loop,muted,default,defaultMuted'+',';if(tag&&tag.attributes&&tag.attributes.length>0){var attrs=tag.attributes;for(var i=attrs.length-1;i>=0;i--){var attrName=attrs[i].name;var attrVal=attrs[i].value;// check for known booleans
// the matching element property will return a value for typeof
if(typeof tag[attrName]==='boolean'||knownBooleans.indexOf(','+attrName+',')!==-1){// the value of an included boolean attribute is typically an empty
// string ('') which would equal false if we just check for a false value.
// we also don't want support bad code like autoplay='false'
attrVal=attrVal!==null?true:false;}obj[attrName]=attrVal;}}return obj;}/**
 * Get the value of an element's attribute
 *
 * @param {Element} el
 *        A DOM element
 *
 * @param {string} attribute
 *        Attribute to get the value of
 *
 * @return {string}
 *         value of the attribute
 */function getAttribute(el,attribute){return el.getAttribute(attribute);}/**
 * Set the value of an element's attribute
 *
 * @param {Element} el
 *        A DOM element
 *
 * @param {string} attribute
 *        Attribute to set
 *
 * @param {string} value
 *        Value to set the attribute to
 */function setAttribute(el,attribute,value){el.setAttribute(attribute,value);}/**
 * Remove an element's attribute
 *
 * @param {Element} el
 *        A DOM element
 *
 * @param {string} attribute
 *        Attribute to remove
 */function removeAttribute(el,attribute){el.removeAttribute(attribute);}/**
 * Attempt to block the ability to select text while dragging controls
 */function blockTextSelection(){document$1.body.focus();document$1.onselectstart=function(){return false;};}/**
 * Turn off text selection blocking
 */function unblockTextSelection(){document$1.onselectstart=function(){return true;};}/**
 * Identical to the native `getBoundingClientRect` function, but ensures that
 * the method is supported at all (it is in all browsers we claim to support)
 * and that the element is in the DOM before continuing.
 *
 * This wrapper function also shims properties which are not provided by some
 * older browsers (namely, IE8).
 *
 * Additionally, some browsers do not support adding properties to a
 * `ClientRect`/`DOMRect` object; so, we shallow-copy it with the standard
 * properties (except `x` and `y` which are not widely supported). This helps
 * avoid implementations where keys are non-enumerable.
 *
 * @param  {Element} el
 *         Element whose `ClientRect` we want to calculate.
 *
 * @return {Object|undefined}
 *         Always returns a plain
 */function getBoundingClientRect(el){if(el&&el.getBoundingClientRect&&el.parentNode){var rect=el.getBoundingClientRect();var result={};['bottom','height','left','right','top','width'].forEach(function(k){if(rect[k]!==undefined){result[k]=rect[k];}});if(!result.height){result.height=parseFloat(computedStyle(el,'height'));}if(!result.width){result.width=parseFloat(computedStyle(el,'width'));}return result;}}/**
 * The postion of a DOM element on the page.
 *
 * @typedef {Object} module:dom~Position
 *
 * @property {number} left
 *           Pixels to the left
 *
 * @property {number} top
 *           Pixels on top
 */ /**
 * Offset Left.
 * getBoundingClientRect technique from
 * John Resig
 *
 * @see http://ejohn.org/blog/getboundingclientrect-is-awesome/
 *
 * @param {Element} el
 *        Element from which to get offset
 *
 * @return {module:dom~Position}
 *         The position of the element that was passed in.
 */function findPosition(el){var box=void 0;if(el.getBoundingClientRect&&el.parentNode){box=el.getBoundingClientRect();}if(!box){return {left:0,top:0};}var docEl=document$1.documentElement;var body=document$1.body;var clientLeft=docEl.clientLeft||body.clientLeft||0;var scrollLeft=window$1.pageXOffset||body.scrollLeft;var left=box.left+scrollLeft-clientLeft;var clientTop=docEl.clientTop||body.clientTop||0;var scrollTop=window$1.pageYOffset||body.scrollTop;var top=box.top+scrollTop-clientTop;// Android sometimes returns slightly off decimal values, so need to round
return {left:Math.round(left),top:Math.round(top)};}/**
 * x and y coordinates for a dom element or mouse pointer
 *
 * @typedef {Object} Dom~Coordinates
 *
 * @property {number} x
 *           x coordinate in pixels
 *
 * @property {number} y
 *           y coordinate in pixels
 */ /**
 * Get pointer position in element
 * Returns an object with x and y coordinates.
 * The base on the coordinates are the bottom left of the element.
 *
 * @param {Element} el
 *        Element on which to get the pointer position on
 *
 * @param {EventTarget~Event} event
 *        Event object
 *
 * @return {Dom~Coordinates}
 *         A Coordinates object corresponding to the mouse position.
 *
 */function getPointerPosition(el,event){var position={};var box=findPosition(el);var boxW=el.offsetWidth;var boxH=el.offsetHeight;var boxY=box.top;var boxX=box.left;var pageY=event.pageY;var pageX=event.pageX;if(event.changedTouches){pageX=event.changedTouches[0].pageX;pageY=event.changedTouches[0].pageY;}position.y=Math.max(0,Math.min(1,(boxY-pageY+boxH)/boxH));position.x=Math.max(0,Math.min(1,(pageX-boxX)/boxW));return position;}/**
 * Determines, via duck typing, whether or not a value is a text node.
 *
 * @param {Mixed} value
 *        Check if this value is a text node.
 *
 * @return {boolean}
 *         - True if it is a text node
 *         - False otherwise
 */function isTextNode$1(value){return isObject(value)&&value.nodeType===3;}/**
 * Empties the contents of an element.
 *
 * @param {Element} el
 *        The element to empty children from
 *
 * @return {Element}
 *         The element with no children
 */function emptyEl(el){while(el.firstChild){el.removeChild(el.firstChild);}return el;}/**
 * Normalizes content for eventual insertion into the DOM.
 *
 * This allows a wide range of content definition methods, but protects
 * from falling into the trap of simply writing to `innerHTML`, which is
 * an XSS concern.
 *
 * The content for an element can be passed in multiple types and
 * combinations, whose behavior is as follows:
 *
 * @param {String|Element|TextNode|Array|Function} content
 *        - String: Normalized into a text node.
 *        - Element/TextNode: Passed through.
 *        - Array: A one-dimensional array of strings, elements, nodes, or functions
 *          (which return single strings, elements, or nodes).
 *        - Function: If the sole argument, is expected to produce a string, element,
 *          node, or array as defined above.
 *
 * @return {Array}
 *         All of the content that was passed in normalized.
 */function normalizeContent(content){// First, invoke content if it is a function. If it produces an array,
// that needs to happen before normalization.
if(typeof content==='function'){content=content();}// Next up, normalize to an array, so one or many items can be normalized,
// filtered, and returned.
return (Array.isArray(content)?content:[content]).map(function(value){// First, invoke value if it is a function to produce a new value,
// which will be subsequently normalized to a Node of some kind.
if(typeof value==='function'){value=value();}if(isEl(value)||isTextNode$1(value)){return value;}if(typeof value==='string'&&/\S/.test(value)){return document$1.createTextNode(value);}}).filter(function(value){return value;});}/**
 * Normalizes and appends content to an element.
 *
 * @param {Element} el
 *        Element to append normalized content to.
 *
 *
 * @param {String|Element|TextNode|Array|Function} content
 *        See the `content` argument of {@link dom:normalizeContent}
 *
 * @return {Element}
 *         The element with appended normalized content.
 */function appendContent(el,content){normalizeContent(content).forEach(function(node){return el.appendChild(node);});return el;}/**
 * Normalizes and inserts content into an element; this is identical to
 * `appendContent()`, except it empties the element first.
 *
 * @param {Element} el
 *        Element to insert normalized content into.
 *
 * @param {String|Element|TextNode|Array|Function} content
 *        See the `content` argument of {@link dom:normalizeContent}
 *
 * @return {Element}
 *         The element with inserted normalized content.
 *
 */function insertContent(el,content){return appendContent(emptyEl(el),content);}/**
 * Check if event was a single left click
 *
 * @param {EventTarget~Event} event
 *        Event object
 *
 * @return {boolean}
 *         - True if a left click
 *         - False if not a left click
 */function isSingleLeftClick(event){// Note: if you create something draggable, be sure to
// call it on both `mousedown` and `mousemove` event,
// otherwise `mousedown` should be enough for a button
if(event.button===undefined&&event.buttons===undefined){// Why do we need `buttons` ?
// Because, middle mouse sometimes have this:
// e.button === 0 and e.buttons === 4
// Furthermore, we want to prevent combination click, something like
// HOLD middlemouse then left click, that would be
// e.button === 0, e.buttons === 5
// just `button` is not gonna work
// Alright, then what this block does ?
// this is for chrome `simulate mobile devices`
// I want to support this as well
return true;}if(event.button===0&&event.buttons===undefined){// Touch screen, sometimes on some specific device, `buttons`
// doesn't have anything (safari on ios, blackberry...)
return true;}if(IE_VERSION===9){// Ignore IE9
return true;}if(event.button!==0||event.buttons!==1){// This is the reason we have those if else block above
// if any special case we can catch and let it slide
// we do it above, when get to here, this definitely
// is-not-left-click
return false;}return true;}/**
 * Finds a single DOM element matching `selector` within the optional
 * `context` of another DOM element (defaulting to `document`).
 *
 * @param {string} selector
 *        A valid CSS selector, which will be passed to `querySelector`.
 *
 * @param {Element|String} [context=document]
 *        A DOM element within which to query. Can also be a selector
 *        string in which case the first matching element will be used
 *        as context. If missing (or no element matches selector), falls
 *        back to `document`.
 *
 * @return {Element|null}
 *         The element that was found or null.
 */var $$1=createQuerier('querySelector');/**
 * Finds a all DOM elements matching `selector` within the optional
 * `context` of another DOM element (defaulting to `document`).
 *
 * @param {string} selector
 *           A valid CSS selector, which will be passed to `querySelectorAll`.
 *
 * @param {Element|String} [context=document]
 *           A DOM element within which to query. Can also be a selector
 *           string in which case the first matching element will be used
 *           as context. If missing (or no element matches selector), falls
 *           back to `document`.
 *
 * @return {NodeList}
 *         A element list of elements that were found. Will be empty if none were found.
 *
 */var $$=createQuerier('querySelectorAll');var Dom=(Object.freeze||Object)({isReal:isReal,isEl:isEl,isInFrame:isInFrame,createEl:createEl,textContent:textContent,prependTo:prependTo,hasClass:hasClass,addClass:addClass,removeClass:removeClass,toggleClass:toggleClass,setAttributes:setAttributes,getAttributes:getAttributes,getAttribute:getAttribute,setAttribute:setAttribute,removeAttribute:removeAttribute,blockTextSelection:blockTextSelection,unblockTextSelection:unblockTextSelection,getBoundingClientRect:getBoundingClientRect,findPosition:findPosition,getPointerPosition:getPointerPosition,isTextNode:isTextNode$1,emptyEl:emptyEl,normalizeContent:normalizeContent,appendContent:appendContent,insertContent:insertContent,isSingleLeftClick:isSingleLeftClick,$:$$1,$$:$$});/**
 * @file guid.js
 * @module guid
 */ /**
 * Unique ID for an element or function
 * @type {Number}
 */var _guid=1;/**
 * Get a unique auto-incrementing ID by number that has not been returned before.
 *
 * @return {number}
 *         A new unique ID.
 */function newGUID(){return _guid++;}/**
 * @file dom-data.js
 * @module dom-data
 */ /**
 * Element Data Store.
 *
 * Allows for binding data to an element without putting it directly on the
 * element. Ex. Event listeners are stored here.
 * (also from jsninja.com, slightly modified and updated for closure compiler)
 *
 * @type {Object}
 * @private
 */var elData={};/*
 * Unique attribute name to store an element's guid in
 *
 * @type {String}
 * @constant
 * @private
 */var elIdAttr='vdata'+new Date().getTime();/**
 * Returns the cache object where data for an element is stored
 *
 * @param {Element} el
 *        Element to store data for.
 *
 * @return {Object}
 *         The cache object for that el that was passed in.
 */function getData(el){var id=el[elIdAttr];if(!id){id=el[elIdAttr]=newGUID();}if(!elData[id]){elData[id]={};}return elData[id];}/**
 * Returns whether or not an element has cached data
 *
 * @param {Element} el
 *        Check if this element has cached data.
 *
 * @return {boolean}
 *         - True if the DOM element has cached data.
 *         - False otherwise.
 */function hasData(el){var id=el[elIdAttr];if(!id){return false;}return !!Object.getOwnPropertyNames(elData[id]).length;}/**
 * Delete data for the element from the cache and the guid attr from getElementById
 *
 * @param {Element} el
 *        Remove cached data for this element.
 */function removeData(el){var id=el[elIdAttr];if(!id){return;}// Remove all stored data
delete elData[id];// Remove the elIdAttr property from the DOM node
try{delete el[elIdAttr];}catch(e){if(el.removeAttribute){el.removeAttribute(elIdAttr);}else {// IE doesn't appear to support removeAttribute on the document element
el[elIdAttr]=null;}}}/**
 * @file events.js. An Event System (John Resig - Secrets of a JS Ninja http://jsninja.com/)
 * (Original book version wasn't completely usable, so fixed some things and made Closure Compiler compatible)
 * This should work very similarly to jQuery's events, however it's based off the book version which isn't as
 * robust as jquery's, so there's probably some differences.
 *
 * @module events
 */ /**
 * Clean up the listener cache and dispatchers
 *
 * @param {Element|Object} elem
 *        Element to clean up
 *
 * @param {string} type
 *        Type of event to clean up
 */function _cleanUpEvents(elem,type){var data=getData(elem);// Remove the events of a particular type if there are none left
if(data.handlers[type].length===0){delete data.handlers[type];// data.handlers[type] = null;
// Setting to null was causing an error with data.handlers
// Remove the meta-handler from the element
if(elem.removeEventListener){elem.removeEventListener(type,data.dispatcher,false);}else if(elem.detachEvent){elem.detachEvent('on'+type,data.dispatcher);}}// Remove the events object if there are no types left
if(Object.getOwnPropertyNames(data.handlers).length<=0){delete data.handlers;delete data.dispatcher;delete data.disabled;}// Finally remove the element data if there is no data left
if(Object.getOwnPropertyNames(data).length===0){removeData(elem);}}/**
 * Loops through an array of event types and calls the requested method for each type.
 *
 * @param {Function} fn
 *        The event method we want to use.
 *
 * @param {Element|Object} elem
 *        Element or object to bind listeners to
 *
 * @param {string} type
 *        Type of event to bind to.
 *
 * @param {EventTarget~EventListener} callback
 *        Event listener.
 */function _handleMultipleEvents(fn,elem,types,callback){types.forEach(function(type){// Call the event method for each one of the types
fn(elem,type,callback);});}/**
 * Fix a native event to have standard property values
 *
 * @param {Object} event
 *        Event object to fix.
 *
 * @return {Object}
 *         Fixed event object.
 */function fixEvent(event){function returnTrue(){return true;}function returnFalse(){return false;}// Test if fixing up is needed
// Used to check if !event.stopPropagation instead of isPropagationStopped
// But native events return true for stopPropagation, but don't have
// other expected methods like isPropagationStopped. Seems to be a problem
// with the Javascript Ninja code. So we're just overriding all events now.
if(!event||!event.isPropagationStopped){var old=event||window$1.event;event={};// Clone the old object so that we can modify the values event = {};
// IE8 Doesn't like when you mess with native event properties
// Firefox returns false for event.hasOwnProperty('type') and other props
//  which makes copying more difficult.
// TODO: Probably best to create a whitelist of event props
for(var key in old){// Safari 6.0.3 warns you if you try to copy deprecated layerX/Y
// Chrome warns you if you try to copy deprecated keyboardEvent.keyLocation
// and webkitMovementX/Y
if(key!=='layerX'&&key!=='layerY'&&key!=='keyLocation'&&key!=='webkitMovementX'&&key!=='webkitMovementY'){// Chrome 32+ warns if you try to copy deprecated returnValue, but
// we still want to if preventDefault isn't supported (IE8).
if(!(key==='returnValue'&&old.preventDefault)){event[key]=old[key];}}}// The event occurred on this element
if(!event.target){event.target=event.srcElement||document$1;}// Handle which other element the event is related to
if(!event.relatedTarget){event.relatedTarget=event.fromElement===event.target?event.toElement:event.fromElement;}// Stop the default browser action
event.preventDefault=function(){if(old.preventDefault){old.preventDefault();}event.returnValue=false;old.returnValue=false;event.defaultPrevented=true;};event.defaultPrevented=false;// Stop the event from bubbling
event.stopPropagation=function(){if(old.stopPropagation){old.stopPropagation();}event.cancelBubble=true;old.cancelBubble=true;event.isPropagationStopped=returnTrue;};event.isPropagationStopped=returnFalse;// Stop the event from bubbling and executing other handlers
event.stopImmediatePropagation=function(){if(old.stopImmediatePropagation){old.stopImmediatePropagation();}event.isImmediatePropagationStopped=returnTrue;event.stopPropagation();};event.isImmediatePropagationStopped=returnFalse;// Handle mouse position
if(event.clientX!==null&&event.clientX!==undefined){var doc=document$1.documentElement;var body=document$1.body;event.pageX=event.clientX+(doc&&doc.scrollLeft||body&&body.scrollLeft||0)-(doc&&doc.clientLeft||body&&body.clientLeft||0);event.pageY=event.clientY+(doc&&doc.scrollTop||body&&body.scrollTop||0)-(doc&&doc.clientTop||body&&body.clientTop||0);}// Handle key presses
event.which=event.charCode||event.keyCode;// Fix button for mouse clicks:
// 0 == left; 1 == middle; 2 == right
if(event.button!==null&&event.button!==undefined){// The following is disabled because it does not pass videojs-standard
// and... yikes.
/* eslint-disable */event.button=event.button&1?0:event.button&4?1:event.button&2?2:0;/* eslint-enable */}}// Returns fixed-up instance
return event;}/**
 * Whether passive event listeners are supported
 */var _supportsPassive=false;(function(){try{var opts=Object.defineProperty({},'passive',{get:function get(){_supportsPassive=true;}});window$1.addEventListener('test',null,opts);window$1.removeEventListener('test',null,opts);}catch(e){// disregard
}})();/**
 * Touch events Chrome expects to be passive
 */var passiveEvents=['touchstart','touchmove'];/**
 * Add an event listener to element
 * It stores the handler function in a separate cache object
 * and adds a generic handler to the element's event,
 * along with a unique id (guid) to the element.
 *
 * @param {Element|Object} elem
 *        Element or object to bind listeners to
 *
 * @param {string|string[]} type
 *        Type of event to bind to.
 *
 * @param {EventTarget~EventListener} fn
 *        Event listener.
 */function on(elem,type,fn){if(Array.isArray(type)){return _handleMultipleEvents(on,elem,type,fn);}var data=getData(elem);// We need a place to store all our handler data
if(!data.handlers){data.handlers={};}if(!data.handlers[type]){data.handlers[type]=[];}if(!fn.guid){fn.guid=newGUID();}data.handlers[type].push(fn);if(!data.dispatcher){data.disabled=false;data.dispatcher=function(event,hash){if(data.disabled){return;}event=fixEvent(event);var handlers=data.handlers[event.type];if(handlers){// Copy handlers so if handlers are added/removed during the process it doesn't throw everything off.
var handlersCopy=handlers.slice(0);for(var m=0,n=handlersCopy.length;m<n;m++){if(event.isImmediatePropagationStopped()){break;}else {try{handlersCopy[m].call(elem,event,hash);}catch(e){log$1.error(e);}}}}};}if(data.handlers[type].length===1){if(elem.addEventListener){var options=false;if(_supportsPassive&&passiveEvents.indexOf(type)>-1){options={passive:true};}elem.addEventListener(type,data.dispatcher,options);}else if(elem.attachEvent){elem.attachEvent('on'+type,data.dispatcher);}}}/**
 * Removes event listeners from an element
 *
 * @param {Element|Object} elem
 *        Object to remove listeners from.
 *
 * @param {string|string[]} [type]
 *        Type of listener to remove. Don't include to remove all events from element.
 *
 * @param {EventTarget~EventListener} [fn]
 *        Specific listener to remove. Don't include to remove listeners for an event
 *        type.
 */function off(elem,type,fn){// Don't want to add a cache object through getElData if not needed
if(!hasData(elem)){return;}var data=getData(elem);// If no events exist, nothing to unbind
if(!data.handlers){return;}if(Array.isArray(type)){return _handleMultipleEvents(off,elem,type,fn);}// Utility function
var removeType=function removeType(el,t){data.handlers[t]=[];_cleanUpEvents(el,t);};// Are we removing all bound events?
if(type===undefined){for(var t in data.handlers){if(Object.prototype.hasOwnProperty.call(data.handlers||{},t)){removeType(elem,t);}}return;}var handlers=data.handlers[type];// If no handlers exist, nothing to unbind
if(!handlers){return;}// If no listener was provided, remove all listeners for type
if(!fn){removeType(elem,type);return;}// We're only removing a single handler
if(fn.guid){for(var n=0;n<handlers.length;n++){if(handlers[n].guid===fn.guid){handlers.splice(n--,1);}}}_cleanUpEvents(elem,type);}/**
 * Trigger an event for an element
 *
 * @param {Element|Object} elem
 *        Element to trigger an event on
 *
 * @param {EventTarget~Event|string} event
 *        A string (the type) or an event object with a type attribute
 *
 * @param {Object} [hash]
 *        data hash to pass along with the event
 *
 * @return {boolean|undefined}
 *         - Returns the opposite of `defaultPrevented` if default was prevented
 *         - Otherwise returns undefined
 */function trigger(elem,event,hash){// Fetches element data and a reference to the parent (for bubbling).
// Don't want to add a data object to cache for every parent,
// so checking hasElData first.
var elemData=hasData(elem)?getData(elem):{};var parent=elem.parentNode||elem.ownerDocument;// type = event.type || event,
// handler;
// If an event name was passed as a string, creates an event out of it
if(typeof event==='string'){event={type:event,target:elem};}else if(!event.target){event.target=elem;}// Normalizes the event properties.
event=fixEvent(event);// If the passed element has a dispatcher, executes the established handlers.
if(elemData.dispatcher){elemData.dispatcher.call(elem,event,hash);}// Unless explicitly stopped or the event does not bubble (e.g. media events)
// recursively calls this function to bubble the event up the DOM.
if(parent&&!event.isPropagationStopped()&&event.bubbles===true){trigger.call(null,parent,event,hash);// If at the top of the DOM, triggers the default action unless disabled.
}else if(!parent&&!event.defaultPrevented){var targetData=getData(event.target);// Checks if the target has a default action for this event.
if(event.target[event.type]){// Temporarily disables event dispatching on the target as we have already executed the handler.
targetData.disabled=true;// Executes the default action.
if(typeof event.target[event.type]==='function'){event.target[event.type]();}// Re-enables event dispatching.
targetData.disabled=false;}}// Inform the triggerer if the default was prevented by returning false
return !event.defaultPrevented;}/**
 * Trigger a listener only once for an event
 *
 * @param {Element|Object} elem
 *        Element or object to bind to.
 *
 * @param {string|string[]} type
 *        Name/type of event
 *
 * @param {Event~EventListener} fn
 *        Event Listener function
 */function one(elem,type,fn){if(Array.isArray(type)){return _handleMultipleEvents(one,elem,type,fn);}var func=function func(){off(elem,type,func);fn.apply(this,arguments);};// copy the guid to the new function so it can removed using the original function's ID
func.guid=fn.guid=fn.guid||newGUID();on(elem,type,func);}var Events=(Object.freeze||Object)({fixEvent:fixEvent,on:on,off:off,trigger:trigger,one:one});/**
 * @file setup.js - Functions for setting up a player without
 * user interaction based on the data-setup `attribute` of the video tag.
 *
 * @module setup
 */var _windowLoaded=false;var videojs$2=void 0;/**
 * Set up any tags that have a data-setup `attribute` when the player is started.
 */var autoSetup=function autoSetup(){// Protect against breakage in non-browser environments and check global autoSetup option.
if(!isReal()||videojs$2.options.autoSetup===false){return;}// One day, when we stop supporting IE8, go back to this, but in the meantime...*hack hack hack*
// var vids = Array.prototype.slice.call(document.getElementsByTagName('video'));
// var audios = Array.prototype.slice.call(document.getElementsByTagName('audio'));
// var mediaEls = vids.concat(audios);
// Because IE8 doesn't support calling slice on a node list, we need to loop
// through each list of elements to build up a new, combined list of elements.
var vids=document$1.getElementsByTagName('video');var audios=document$1.getElementsByTagName('audio');var divs=document$1.getElementsByTagName('video-js');var mediaEls=[];if(vids&&vids.length>0){for(var i=0,e=vids.length;i<e;i++){mediaEls.push(vids[i]);}}if(audios&&audios.length>0){for(var _i=0,_e=audios.length;_i<_e;_i++){mediaEls.push(audios[_i]);}}if(divs&&divs.length>0){for(var _i2=0,_e2=divs.length;_i2<_e2;_i2++){mediaEls.push(divs[_i2]);}}// Check if any media elements exist
if(mediaEls&&mediaEls.length>0){for(var _i3=0,_e3=mediaEls.length;_i3<_e3;_i3++){var mediaEl=mediaEls[_i3];// Check if element exists, has getAttribute func.
// IE seems to consider typeof el.getAttribute == 'object' instead of
// 'function' like expected, at least when loading the player immediately.
if(mediaEl&&mediaEl.getAttribute){// Make sure this player hasn't already been set up.
if(mediaEl.player===undefined){var options=mediaEl.getAttribute('data-setup');// Check if data-setup attr exists.
// We only auto-setup if they've added the data-setup attr.
if(options!==null){// Create new video.js instance.
videojs$2(mediaEl);}}// If getAttribute isn't defined, we need to wait for the DOM.
}else {autoSetupTimeout(1);break;}}// No videos were found, so keep looping unless page is finished loading.
}else if(!_windowLoaded){autoSetupTimeout(1);}};/**
 * Wait until the page is loaded before running autoSetup. This will be called in
 * autoSetup if `hasLoaded` returns false.
 *
 * @param {number} wait
 *        How long to wait in ms
 *
 * @param {module:videojs} [vjs]
 *        The videojs library function
 */function autoSetupTimeout(wait,vjs){if(vjs){videojs$2=vjs;}window$1.setTimeout(autoSetup,wait);}if(isReal()&&document$1.readyState==='complete'){_windowLoaded=true;}else {/**
   * Listen for the load event on window, and set _windowLoaded to true.
   *
   * @listens load
   */one(window$1,'load',function(){_windowLoaded=true;});}/**
 * @file stylesheet.js
 * @module stylesheet
 */ /**
 * Create a DOM syle element given a className for it.
 *
 * @param {string} className
 *        The className to add to the created style element.
 *
 * @return {Element}
 *         The element that was created.
 */var createStyleElement=function createStyleElement(className){var style=document$1.createElement('style');style.className=className;return style;};/**
 * Add text to a DOM element.
 *
 * @param {Element} el
 *        The Element to add text content to.
 *
 * @param {string} content
 *        The text to add to the element.
 */var setTextContent=function setTextContent(el,content){if(el.styleSheet){el.styleSheet.cssText=content;}else {el.textContent=content;}};/**
 * @file fn.js
 * @module fn
 */ /**
 * Bind (a.k.a proxy or Context). A simple method for changing the context of a function
 * It also stores a unique id on the function so it can be easily removed from events.
 *
 * @param {Mixed} context
 *        The object to bind as scope.
 *
 * @param {Function} fn
 *        The function to be bound to a scope.
 *
 * @param {number} [uid]
 *        An optional unique ID for the function to be set
 *
 * @return {Function}
 *         The new function that will be bound into the context given
 */var bind=function bind(context,fn,uid){// Make sure the function has a unique ID
if(!fn.guid){fn.guid=newGUID();}// Create the new function that changes the context
var bound=function bound(){return fn.apply(context,arguments);};// Allow for the ability to individualize this function
// Needed in the case where multiple objects might share the same prototype
// IF both items add an event listener with the same function, then you try to remove just one
// it will remove both because they both have the same guid.
// when using this, you need to use the bind method when you remove the listener as well.
// currently used in text tracks
bound.guid=uid?uid+'_'+fn.guid:fn.guid;return bound;};/**
 * Wraps the given function, `fn`, with a new function that only invokes `fn`
 * at most once per every `wait` milliseconds.
 *
 * @param  {Function} fn
 *         The function to be throttled.
 *
 * @param  {Number}   wait
 *         The number of milliseconds by which to throttle.
 *
 * @return {Function}
 */var throttle=function throttle(fn,wait){var last=Date.now();var throttled=function throttled(){var now=Date.now();if(now-last>=wait){fn.apply(undefined,arguments);last=now;}};return throttled;};/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked.
 *
 * Inspired by lodash and underscore implementations.
 *
 * @param  {Function} func
 *         The function to wrap with debounce behavior.
 *
 * @param  {number} wait
 *         The number of milliseconds to wait after the last invocation.
 *
 * @param  {boolean} [immediate]
 *         Whether or not to invoke the function immediately upon creation.
 *
 * @param  {Object} [context=window]
 *         The "context" in which the debounced function should debounce. For
 *         example, if this function should be tied to a Video.js player,
 *         the player can be passed here. Alternatively, defaults to the
 *         global `window` object.
 *
 * @return {Function}
 *         A debounced function.
 */var debounce=function debounce(func,wait,immediate){var context=arguments.length>3&&arguments[3]!==undefined?arguments[3]:window$1;var timeout=void 0;var cancel=function cancel(){context.clearTimeout(timeout);timeout=null;};/* eslint-disable consistent-this */var debounced=function debounced(){var self=this;var args=arguments;var _later=function later(){timeout=null;_later=null;if(!immediate){func.apply(self,args);}};if(!timeout&&immediate){func.apply(self,args);}context.clearTimeout(timeout);timeout=context.setTimeout(_later,wait);};/* eslint-enable consistent-this */debounced.cancel=cancel;return debounced;};/**
 * @file src/js/event-target.js
 */ /**
 * `EventTarget` is a class that can have the same API as the DOM `EventTarget`. It
 * adds shorthand functions that wrap around lengthy functions. For example:
 * the `on` function is a wrapper around `addEventListener`.
 *
 * @see [EventTarget Spec]{@link https://www.w3.org/TR/DOM-Level-2-Events/events.html#Events-EventTarget}
 * @class EventTarget
 */var EventTarget=function EventTarget(){};/**
 * A Custom DOM event.
 *
 * @typedef {Object} EventTarget~Event
 * @see [Properties]{@link https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent}
 */ /**
 * All event listeners should follow the following format.
 *
 * @callback EventTarget~EventListener
 * @this {EventTarget}
 *
 * @param {EventTarget~Event} event
 *        the event that triggered this function
 *
 * @param {Object} [hash]
 *        hash of data sent during the event
 */ /**
 * An object containing event names as keys and booleans as values.
 *
 * > NOTE: If an event name is set to a true value here {@link EventTarget#trigger}
 *         will have extra functionality. See that function for more information.
 *
 * @property EventTarget.prototype.allowedEvents_
 * @private
 */EventTarget.prototype.allowedEvents_={};/**
 * Adds an `event listener` to an instance of an `EventTarget`. An `event listener` is a
 * function that will get called when an event with a certain name gets triggered.
 *
 * @param {string|string[]} type
 *        An event name or an array of event names.
 *
 * @param {EventTarget~EventListener} fn
 *        The function to call with `EventTarget`s
 */EventTarget.prototype.on=function(type,fn){// Remove the addEventListener alias before calling Events.on
// so we don't get into an infinite type loop
var ael=this.addEventListener;this.addEventListener=function(){};on(this,type,fn);this.addEventListener=ael;};/**
 * An alias of {@link EventTarget#on}. Allows `EventTarget` to mimic
 * the standard DOM API.
 *
 * @function
 * @see {@link EventTarget#on}
 */EventTarget.prototype.addEventListener=EventTarget.prototype.on;/**
 * Removes an `event listener` for a specific event from an instance of `EventTarget`.
 * This makes it so that the `event listener` will no longer get called when the
 * named event happens.
 *
 * @param {string|string[]} type
 *        An event name or an array of event names.
 *
 * @param {EventTarget~EventListener} fn
 *        The function to remove.
 */EventTarget.prototype.off=function(type,fn){off(this,type,fn);};/**
 * An alias of {@link EventTarget#off}. Allows `EventTarget` to mimic
 * the standard DOM API.
 *
 * @function
 * @see {@link EventTarget#off}
 */EventTarget.prototype.removeEventListener=EventTarget.prototype.off;/**
 * This function will add an `event listener` that gets triggered only once. After the
 * first trigger it will get removed. This is like adding an `event listener`
 * with {@link EventTarget#on} that calls {@link EventTarget#off} on itself.
 *
 * @param {string|string[]} type
 *        An event name or an array of event names.
 *
 * @param {EventTarget~EventListener} fn
 *        The function to be called once for each event name.
 */EventTarget.prototype.one=function(type,fn){// Remove the addEventListener alialing Events.on
// so we don't get into an infinite type loop
var ael=this.addEventListener;this.addEventListener=function(){};one(this,type,fn);this.addEventListener=ael;};/**
 * This function causes an event to happen. This will then cause any `event listeners`
 * that are waiting for that event, to get called. If there are no `event listeners`
 * for an event then nothing will happen.
 *
 * If the name of the `Event` that is being triggered is in `EventTarget.allowedEvents_`.
 * Trigger will also call the `on` + `uppercaseEventName` function.
 *
 * Example:
 * 'click' is in `EventTarget.allowedEvents_`, so, trigger will attempt to call
 * `onClick` if it exists.
 *
 * @param {string|EventTarget~Event|Object} event
 *        The name of the event, an `Event`, or an object with a key of type set to
 *        an event name.
 */EventTarget.prototype.trigger=function(event){var type=event.type||event;if(typeof event==='string'){event={type:type};}event=fixEvent(event);if(this.allowedEvents_[type]&&this['on'+type]){this['on'+type](event);}trigger(this,event);};/**
 * An alias of {@link EventTarget#trigger}. Allows `EventTarget` to mimic
 * the standard DOM API.
 *
 * @function
 * @see {@link EventTarget#trigger}
 */EventTarget.prototype.dispatchEvent=EventTarget.prototype.trigger;/**
 * @file mixins/evented.js
 * @module evented
 */ /**
 * Returns whether or not an object has had the evented mixin applied.
 *
 * @param  {Object} object
 *         An object to test.
 *
 * @return {boolean}
 *         Whether or not the object appears to be evented.
 */var isEvented=function isEvented(object){return object instanceof EventTarget||!!object.eventBusEl_&&['on','one','off','trigger'].every(function(k){return typeof object[k]==='function';});};/**
 * Whether a value is a valid event type - non-empty string or array.
 *
 * @private
 * @param  {string|Array} type
 *         The type value to test.
 *
 * @return {boolean}
 *         Whether or not the type is a valid event type.
 */var isValidEventType=function isValidEventType(type){return(// The regex here verifies that the `type` contains at least one non-
// whitespace character.
typeof type==='string'&&/\S/.test(type)||Array.isArray(type)&&!!type.length);};/**
 * Validates a value to determine if it is a valid event target. Throws if not.
 *
 * @private
 * @throws {Error}
 *         If the target does not appear to be a valid event target.
 *
 * @param  {Object} target
 *         The object to test.
 */var validateTarget=function validateTarget(target){if(!target.nodeName&&!isEvented(target)){throw new Error('Invalid target; must be a DOM node or evented object.');}};/**
 * Validates a value to determine if it is a valid event target. Throws if not.
 *
 * @private
 * @throws {Error}
 *         If the type does not appear to be a valid event type.
 *
 * @param  {string|Array} type
 *         The type to test.
 */var validateEventType=function validateEventType(type){if(!isValidEventType(type)){throw new Error('Invalid event type; must be a non-empty string or array.');}};/**
 * Validates a value to determine if it is a valid listener. Throws if not.
 *
 * @private
 * @throws {Error}
 *         If the listener is not a function.
 *
 * @param  {Function} listener
 *         The listener to test.
 */var validateListener=function validateListener(listener){if(typeof listener!=='function'){throw new Error('Invalid listener; must be a function.');}};/**
 * Takes an array of arguments given to `on()` or `one()`, validates them, and
 * normalizes them into an object.
 *
 * @private
 * @param  {Object} self
 *         The evented object on which `on()` or `one()` was called. This
 *         object will be bound as the `this` value for the listener.
 *
 * @param  {Array} args
 *         An array of arguments passed to `on()` or `one()`.
 *
 * @return {Object}
 *         An object containing useful values for `on()` or `one()` calls.
 */var normalizeListenArgs=function normalizeListenArgs(self,args){// If the number of arguments is less than 3, the target is always the
// evented object itself.
var isTargetingSelf=args.length<3||args[0]===self||args[0]===self.eventBusEl_;var target=void 0;var type=void 0;var listener=void 0;if(isTargetingSelf){target=self.eventBusEl_;// Deal with cases where we got 3 arguments, but we are still listening to
// the evented object itself.
if(args.length>=3){args.shift();}type=args[0];listener=args[1];}else {target=args[0];type=args[1];listener=args[2];}validateTarget(target);validateEventType(type);validateListener(listener);listener=bind(self,listener);return {isTargetingSelf:isTargetingSelf,target:target,type:type,listener:listener};};/**
 * Adds the listener to the event type(s) on the target, normalizing for
 * the type of target.
 *
 * @private
 * @param  {Element|Object} target
 *         A DOM node or evented object.
 *
 * @param  {string} method
 *         The event binding method to use ("on" or "one").
 *
 * @param  {string|Array} type
 *         One or more event type(s).
 *
 * @param  {Function} listener
 *         A listener function.
 */var listen=function listen(target,method,type,listener){validateTarget(target);if(target.nodeName){Events[method](target,type,listener);}else {target[method](type,listener);}};/**
 * Contains methods that provide event capabilites to an object which is passed
 * to {@link module:evented|evented}.
 *
 * @mixin EventedMixin
 */var EventedMixin={/**
   * Add a listener to an event (or events) on this object or another evented
   * object.
   *
   * @param  {string|Array|Element|Object} targetOrType
   *         If this is a string or array, it represents the event type(s)
   *         that will trigger the listener.
   *
   *         Another evented object can be passed here instead, which will
   *         cause the listener to listen for events on _that_ object.
   *
   *         In either case, the listener's `this` value will be bound to
   *         this object.
   *
   * @param  {string|Array|Function} typeOrListener
   *         If the first argument was a string or array, this should be the
   *         listener function. Otherwise, this is a string or array of event
   *         type(s).
   *
   * @param  {Function} [listener]
   *         If the first argument was another evented object, this will be
   *         the listener function.
   */on:function on$$1(){var _this=this;for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}var _normalizeListenArgs=normalizeListenArgs(this,args),isTargetingSelf=_normalizeListenArgs.isTargetingSelf,target=_normalizeListenArgs.target,type=_normalizeListenArgs.type,listener=_normalizeListenArgs.listener;listen(target,'on',type,listener);// If this object is listening to another evented object.
if(!isTargetingSelf){// If this object is disposed, remove the listener.
var removeListenerOnDispose=function removeListenerOnDispose(){return _this.off(target,type,listener);};// Use the same function ID as the listener so we can remove it later it
// using the ID of the original listener.
removeListenerOnDispose.guid=listener.guid;// Add a listener to the target's dispose event as well. This ensures
// that if the target is disposed BEFORE this object, we remove the
// removal listener that was just added. Otherwise, we create a memory leak.
var removeRemoverOnTargetDispose=function removeRemoverOnTargetDispose(){return _this.off('dispose',removeListenerOnDispose);};// Use the same function ID as the listener so we can remove it later
// it using the ID of the original listener.
removeRemoverOnTargetDispose.guid=listener.guid;listen(this,'on','dispose',removeListenerOnDispose);listen(target,'on','dispose',removeRemoverOnTargetDispose);}},/**
   * Add a listener to an event (or events) on this object or another evented
   * object. The listener will only be called once and then removed.
   *
   * @param  {string|Array|Element|Object} targetOrType
   *         If this is a string or array, it represents the event type(s)
   *         that will trigger the listener.
   *
   *         Another evented object can be passed here instead, which will
   *         cause the listener to listen for events on _that_ object.
   *
   *         In either case, the listener's `this` value will be bound to
   *         this object.
   *
   * @param  {string|Array|Function} typeOrListener
   *         If the first argument was a string or array, this should be the
   *         listener function. Otherwise, this is a string or array of event
   *         type(s).
   *
   * @param  {Function} [listener]
   *         If the first argument was another evented object, this will be
   *         the listener function.
   */one:function one$$1(){var _this2=this;for(var _len2=arguments.length,args=Array(_len2),_key2=0;_key2<_len2;_key2++){args[_key2]=arguments[_key2];}var _normalizeListenArgs2=normalizeListenArgs(this,args),isTargetingSelf=_normalizeListenArgs2.isTargetingSelf,target=_normalizeListenArgs2.target,type=_normalizeListenArgs2.type,listener=_normalizeListenArgs2.listener;// Targeting this evented object.
if(isTargetingSelf){listen(target,'one',type,listener);// Targeting another evented object.
}else {var wrapper=function wrapper(){for(var _len3=arguments.length,largs=Array(_len3),_key3=0;_key3<_len3;_key3++){largs[_key3]=arguments[_key3];}_this2.off(target,type,wrapper);listener.apply(null,largs);};// Use the same function ID as the listener so we can remove it later
// it using the ID of the original listener.
wrapper.guid=listener.guid;listen(target,'one',type,wrapper);}},/**
   * Removes listener(s) from event(s) on an evented object.
   *
   * @param  {string|Array|Element|Object} [targetOrType]
   *         If this is a string or array, it represents the event type(s).
   *
   *         Another evented object can be passed here instead, in which case
   *         ALL 3 arguments are _required_.
   *
   * @param  {string|Array|Function} [typeOrListener]
   *         If the first argument was a string or array, this may be the
   *         listener function. Otherwise, this is a string or array of event
   *         type(s).
   *
   * @param  {Function} [listener]
   *         If the first argument was another evented object, this will be
   *         the listener function; otherwise, _all_ listeners bound to the
   *         event type(s) will be removed.
   */off:function off$$1(targetOrType,typeOrListener,listener){// Targeting this evented object.
if(!targetOrType||isValidEventType(targetOrType)){off(this.eventBusEl_,targetOrType,typeOrListener);// Targeting another evented object.
}else {var target=targetOrType;var type=typeOrListener;// Fail fast and in a meaningful way!
validateTarget(target);validateEventType(type);validateListener(listener);// Ensure there's at least a guid, even if the function hasn't been used
listener=bind(this,listener);// Remove the dispose listener on this evented object, which was given
// the same guid as the event listener in on().
this.off('dispose',listener);if(target.nodeName){off(target,type,listener);off(target,'dispose',listener);}else if(isEvented(target)){target.off(type,listener);target.off('dispose',listener);}}},/**
   * Fire an event on this evented object, causing its listeners to be called.
   *
   * @param   {string|Object} event
   *          An event type or an object with a type property.
   *
   * @param   {Object} [hash]
   *          An additional object to pass along to listeners.
   *
   * @returns {boolean}
   *          Whether or not the default behavior was prevented.
   */trigger:function trigger$$1(event,hash){return trigger(this.eventBusEl_,event,hash);}};/**
 * Applies {@link module:evented~EventedMixin|EventedMixin} to a target object.
 *
 * @param  {Object} target
 *         The object to which to add event methods.
 *
 * @param  {Object} [options={}]
 *         Options for customizing the mixin behavior.
 *
 * @param  {String} [options.eventBusKey]
 *         By default, adds a `eventBusEl_` DOM element to the target object,
 *         which is used as an event bus. If the target object already has a
 *         DOM element that should be used, pass its key here.
 *
 * @return {Object}
 *         The target object.
 */function evented(target){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var eventBusKey=options.eventBusKey;// Set or create the eventBusEl_.
if(eventBusKey){if(!target[eventBusKey].nodeName){throw new Error('The eventBusKey "'+eventBusKey+'" does not refer to an element.');}target.eventBusEl_=target[eventBusKey];}else {target.eventBusEl_=createEl('span',{className:'vjs-event-bus'});}assign$1(target,EventedMixin);// When any evented object is disposed, it removes all its listeners.
target.on('dispose',function(){target.off();window$1.setTimeout(function(){target.eventBusEl_=null;},0);});return target;}/**
 * @file mixins/stateful.js
 * @module stateful
 */ /**
 * Contains methods that provide statefulness to an object which is passed
 * to {@link module:stateful}.
 *
 * @mixin StatefulMixin
 */var StatefulMixin={/**
   * A hash containing arbitrary keys and values representing the state of
   * the object.
   *
   * @type {Object}
   */state:{},/**
   * Set the state of an object by mutating its
   * {@link module:stateful~StatefulMixin.state|state} object in place.
   *
   * @fires   module:stateful~StatefulMixin#statechanged
   * @param   {Object|Function} stateUpdates
   *          A new set of properties to shallow-merge into the plugin state.
   *          Can be a plain object or a function returning a plain object.
   *
   * @returns {Object|undefined}
   *          An object containing changes that occurred. If no changes
   *          occurred, returns `undefined`.
   */setState:function setState(stateUpdates){var _this=this;// Support providing the `stateUpdates` state as a function.
if(typeof stateUpdates==='function'){stateUpdates=stateUpdates();}var changes=void 0;each(stateUpdates,function(value,key){// Record the change if the value is different from what's in the
// current state.
if(_this.state[key]!==value){changes=changes||{};changes[key]={from:_this.state[key],to:value};}_this.state[key]=value;});// Only trigger "statechange" if there were changes AND we have a trigger
// function. This allows us to not require that the target object be an
// evented object.
if(changes&&isEvented(this)){/**
       * An event triggered on an object that is both
       * {@link module:stateful|stateful} and {@link module:evented|evented}
       * indicating that its state has changed.
       *
       * @event    module:stateful~StatefulMixin#statechanged
       * @type     {Object}
       * @property {Object} changes
       *           A hash containing the properties that were changed and
       *           the values they were changed `from` and `to`.
       */this.trigger({changes:changes,type:'statechanged'});}return changes;}};/**
 * Applies {@link module:stateful~StatefulMixin|StatefulMixin} to a target
 * object.
 *
 * If the target object is {@link module:evented|evented} and has a
 * `handleStateChanged` method, that method will be automatically bound to the
 * `statechanged` event on itself.
 *
 * @param   {Object} target
 *          The object to be made stateful.
 *
 * @param   {Object} [defaultState]
 *          A default set of properties to populate the newly-stateful object's
 *          `state` property.
 *
 * @returns {Object}
 *          Returns the `target`.
 */function stateful(target,defaultState){assign$1(target,StatefulMixin);// This happens after the mixing-in because we need to replace the `state`
// added in that step.
target.state=assign$1({},target.state,defaultState);// Auto-bind the `handleStateChanged` method of the target object if it exists.
if(typeof target.handleStateChanged==='function'&&isEvented(target)){target.on('statechanged',target.handleStateChanged);}return target;}/**
 * @file to-title-case.js
 * @module to-title-case
 */ /**
 * Uppercase the first letter of a string.
 *
 * @param {string} string
 *        String to be uppercased
 *
 * @return {string}
 *         The string with an uppercased first letter
 */function toTitleCase(string){if(typeof string!=='string'){return string;}return string.charAt(0).toUpperCase()+string.slice(1);}/**
 * Compares the TitleCase versions of the two strings for equality.
 *
 * @param {string} str1
 *        The first string to compare
 *
 * @param {string} str2
 *        The second string to compare
 *
 * @return {boolean}
 *         Whether the TitleCase versions of the strings are equal
 */function titleCaseEquals(str1,str2){return toTitleCase(str1)===toTitleCase(str2);}/**
 * @file merge-options.js
 * @module merge-options
 */ /**
 * Deep-merge one or more options objects, recursively merging **only** plain
 * object properties.
 *
 * @param   {Object[]} sources
 *          One or more objects to merge into a new object.
 *
 * @returns {Object}
 *          A new object that is the merged result of all sources.
 */function mergeOptions(){var result={};for(var _len=arguments.length,sources=Array(_len),_key=0;_key<_len;_key++){sources[_key]=arguments[_key];}sources.forEach(function(source){if(!source){return;}each(source,function(value,key){if(!isPlain(value)){result[key]=value;return;}if(!isPlain(result[key])){result[key]={};}result[key]=mergeOptions(result[key],value);});});return result;}/**
 * Player Component - Base class for all UI objects
 *
 * @file component.js
 */ /**
 * Base class for all UI Components.
 * Components are UI objects which represent both a javascript object and an element
 * in the DOM. They can be children of other components, and can have
 * children themselves.
 *
 * Components can also use methods from {@link EventTarget}
 */var Component=function(){/**
   * A callback that is called when a component is ready. Does not have any
   * paramters and any callback value will be ignored.
   *
   * @callback Component~ReadyCallback
   * @this Component
   */ /**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Object[]} [options.children]
   *        An array of children objects to intialize this component with. Children objects have
   *        a name property that will be used if more than one component of the same type needs to be
   *        added.
   *
   * @param {Component~ReadyCallback} [ready]
   *        Function that gets called when the `Component` is ready.
   */function Component(player,options,ready){classCallCheck(this,Component);// The component might be the player itself and we can't pass `this` to super
if(!player&&this.play){this.player_=player=this;// eslint-disable-line
}else {this.player_=player;}// Make a copy of prototype.options_ to protect against overriding defaults
this.options_=mergeOptions({},this.options_);// Updated options with supplied options
options=this.options_=mergeOptions(this.options_,options);// Get ID from options or options element if one is supplied
this.id_=options.id||options.el&&options.el.id;// If there was no ID from the options, generate one
if(!this.id_){// Don't require the player ID function in the case of mock players
var id=player&&player.id&&player.id()||'no_player';this.id_=id+'_component_'+newGUID();}this.name_=options.name||null;// Create element if one wasn't provided in options
if(options.el){this.el_=options.el;}else if(options.createEl!==false){this.el_=this.createEl();}// if evented is anything except false, we want to mixin in evented
if(options.evented!==false){// Make this an evented object and use `el_`, if available, as its event bus
evented(this,{eventBusKey:this.el_?'el_':null});}stateful(this,this.constructor.defaultState);this.children_=[];this.childIndex_={};this.childNameIndex_={};// Add any child components in options
if(options.initChildren!==false){this.initChildren();}this.ready(ready);// Don't want to trigger ready here or it will before init is actually
// finished for all children that run this constructor
if(options.reportTouchActivity!==false){this.enableTouchActivity();}}/**
   * Dispose of the `Component` and all child components.
   *
   * @fires Component#dispose
   */Component.prototype.dispose=function dispose(){/**
     * Triggered when a `Component` is disposed.
     *
     * @event Component#dispose
     * @type {EventTarget~Event}
     *
     * @property {boolean} [bubbles=false]
     *           set to false so that the close event does not
     *           bubble up
     */this.trigger({type:'dispose',bubbles:false});// Dispose all children.
if(this.children_){for(var i=this.children_.length-1;i>=0;i--){if(this.children_[i].dispose){this.children_[i].dispose();}}}// Delete child references
this.children_=null;this.childIndex_=null;this.childNameIndex_=null;if(this.el_){// Remove element from DOM
if(this.el_.parentNode){this.el_.parentNode.removeChild(this.el_);}removeData(this.el_);this.el_=null;}// remove reference to the player after disposing of the element
this.player_=null;};/**
   * Return the {@link Player} that the `Component` has attached to.
   *
   * @return {Player}
   *         The player that this `Component` has attached to.
   */Component.prototype.player=function player(){return this.player_;};/**
   * Deep merge of options objects with new options.
   * > Note: When both `obj` and `options` contain properties whose values are objects.
   *         The two properties get merged using {@link module:mergeOptions}
   *
   * @param {Object} obj
   *        The object that contains new options.
   *
   * @return {Object}
   *         A new object of `this.options_` and `obj` merged together.
   *
   * @deprecated since version 5
   */Component.prototype.options=function options(obj){log$1.warn('this.options() has been deprecated and will be moved to the constructor in 6.0');if(!obj){return this.options_;}this.options_=mergeOptions(this.options_,obj);return this.options_;};/**
   * Get the `Component`s DOM element
   *
   * @return {Element}
   *         The DOM element for this `Component`.
   */Component.prototype.el=function el(){return this.el_;};/**
   * Create the `Component`s DOM element.
   *
   * @param {string} [tagName]
   *        Element's DOM node type. e.g. 'div'
   *
   * @param {Object} [properties]
   *        An object of properties that should be set.
   *
   * @param {Object} [attributes]
   *        An object of attributes that should be set.
   *
   * @return {Element}
   *         The element that gets created.
   */Component.prototype.createEl=function createEl$$1(tagName,properties,attributes){return createEl(tagName,properties,attributes);};/**
   * Localize a string given the string in english.
   *
   * If tokens are provided, it'll try and run a simple token replacement on the provided string.
   * The tokens it looks for look like `{1}` with the index being 1-indexed into the tokens array.
   *
   * If a `defaultValue` is provided, it'll use that over `string`,
   * if a value isn't found in provided language files.
   * This is useful if you want to have a descriptive key for token replacement
   * but have a succinct localized string and not require `en.json` to be included.
   *
   * Currently, it is used for the progress bar timing.
   * ```js
   * {
   *   "progress bar timing: currentTime={1} duration={2}": "{1} of {2}"
   * }
   * ```
   * It is then used like so:
   * ```js
   * this.localize('progress bar timing: currentTime={1} duration{2}',
   *               [this.player_.currentTime(), this.player_.duration()],
   *               '{1} of {2}');
   * ```
   *
   * Which outputs something like: `01:23 of 24:56`.
   *
   *
   * @param {string} string
   *        The string to localize and the key to lookup in the language files.
   * @param {string[]} [tokens]
   *        If the current item has token replacements, provide the tokens here.
   * @param {string} [defaultValue]
   *        Defaults to `string`. Can be a default value to use for token replacement
   *        if the lookup key is needed to be separate.
   *
   * @return {string}
   *         The localized string or if no localization exists the english string.
   */Component.prototype.localize=function localize(string,tokens){var defaultValue=arguments.length>2&&arguments[2]!==undefined?arguments[2]:string;var code=this.player_.language&&this.player_.language();var languages=this.player_.languages&&this.player_.languages();var language=languages&&languages[code];var primaryCode=code&&code.split('-')[0];var primaryLang=languages&&languages[primaryCode];var localizedString=defaultValue;if(language&&language[string]){localizedString=language[string];}else if(primaryLang&&primaryLang[string]){localizedString=primaryLang[string];}if(tokens){localizedString=localizedString.replace(/\{(\d+)\}/g,function(match,index){var value=tokens[index-1];var ret=value;if(typeof value==='undefined'){ret=match;}return ret;});}return localizedString;};/**
   * Return the `Component`s DOM element. This is where children get inserted.
   * This will usually be the the same as the element returned in {@link Component#el}.
   *
   * @return {Element}
   *         The content element for this `Component`.
   */Component.prototype.contentEl=function contentEl(){return this.contentEl_||this.el_;};/**
   * Get this `Component`s ID
   *
   * @return {string}
   *         The id of this `Component`
   */Component.prototype.id=function id(){return this.id_;};/**
   * Get the `Component`s name. The name gets used to reference the `Component`
   * and is set during registration.
   *
   * @return {string}
   *         The name of this `Component`.
   */Component.prototype.name=function name(){return this.name_;};/**
   * Get an array of all child components
   *
   * @return {Array}
   *         The children
   */Component.prototype.children=function children(){return this.children_;};/**
   * Returns the child `Component` with the given `id`.
   *
   * @param {string} id
   *        The id of the child `Component` to get.
   *
   * @return {Component|undefined}
   *         The child `Component` with the given `id` or undefined.
   */Component.prototype.getChildById=function getChildById(id){return this.childIndex_[id];};/**
   * Returns the child `Component` with the given `name`.
   *
   * @param {string} name
   *        The name of the child `Component` to get.
   *
   * @return {Component|undefined}
   *         The child `Component` with the given `name` or undefined.
   */Component.prototype.getChild=function getChild(name){if(!name){return;}name=toTitleCase(name);return this.childNameIndex_[name];};/**
   * Add a child `Component` inside the current `Component`.
   *
   *
   * @param {string|Component} child
   *        The name or instance of a child to add.
   *
   * @param {Object} [options={}]
   *        The key/value store of options that will get passed to children of
   *        the child.
   *
   * @param {number} [index=this.children_.length]
   *        The index to attempt to add a child into.
   *
   * @return {Component}
   *         The `Component` that gets added as a child. When using a string the
   *         `Component` will get created by this process.
   */Component.prototype.addChild=function addChild(child){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var index=arguments.length>2&&arguments[2]!==undefined?arguments[2]:this.children_.length;var component=void 0;var componentName=void 0;// If child is a string, create component with options
if(typeof child==='string'){componentName=toTitleCase(child);var componentClassName=options.componentClass||componentName;// Set name through options
options.name=componentName;// Create a new object & element for this controls set
// If there's no .player_, this is a player
var ComponentClass=Component.getComponent(componentClassName);if(!ComponentClass){throw new Error('Component '+componentClassName+' does not exist');}// data stored directly on the videojs object may be
// misidentified as a component to retain
// backwards-compatibility with 4.x. check to make sure the
// component class can be instantiated.
if(typeof ComponentClass!=='function'){return null;}component=new ComponentClass(this.player_||this,options);// child is a component instance
}else {component=child;}this.children_.splice(index,0,component);if(typeof component.id==='function'){this.childIndex_[component.id()]=component;}// If a name wasn't used to create the component, check if we can use the
// name function of the component
componentName=componentName||component.name&&toTitleCase(component.name());if(componentName){this.childNameIndex_[componentName]=component;}// Add the UI object's element to the container div (box)
// Having an element is not required
if(typeof component.el==='function'&&component.el()){var childNodes=this.contentEl().children;var refNode=childNodes[index]||null;this.contentEl().insertBefore(component.el(),refNode);}// Return so it can stored on parent object if desired.
return component;};/**
   * Remove a child `Component` from this `Component`s list of children. Also removes
   * the child `Component`s element from this `Component`s element.
   *
   * @param {Component} component
   *        The child `Component` to remove.
   */Component.prototype.removeChild=function removeChild(component){if(typeof component==='string'){component=this.getChild(component);}if(!component||!this.children_){return;}var childFound=false;for(var i=this.children_.length-1;i>=0;i--){if(this.children_[i]===component){childFound=true;this.children_.splice(i,1);break;}}if(!childFound){return;}this.childIndex_[component.id()]=null;this.childNameIndex_[component.name()]=null;var compEl=component.el();if(compEl&&compEl.parentNode===this.contentEl()){this.contentEl().removeChild(component.el());}};/**
   * Add and initialize default child `Component`s based upon options.
   */Component.prototype.initChildren=function initChildren(){var _this=this;var children=this.options_.children;if(children){// `this` is `parent`
var parentOptions=this.options_;var handleAdd=function handleAdd(child){var name=child.name;var opts=child.opts;// Allow options for children to be set at the parent options
// e.g. videojs(id, { controlBar: false });
// instead of videojs(id, { children: { controlBar: false });
if(parentOptions[name]!==undefined){opts=parentOptions[name];}// Allow for disabling default components
// e.g. options['children']['posterImage'] = false
if(opts===false){return;}// Allow options to be passed as a simple boolean if no configuration
// is necessary.
if(opts===true){opts={};}// We also want to pass the original player options
// to each component as well so they don't need to
// reach back into the player for options later.
opts.playerOptions=_this.options_.playerOptions;// Create and add the child component.
// Add a direct reference to the child by name on the parent instance.
// If two of the same component are used, different names should be supplied
// for each
var newChild=_this.addChild(name,opts);if(newChild){_this[name]=newChild;}};// Allow for an array of children details to passed in the options
var workingChildren=void 0;var Tech=Component.getComponent('Tech');if(Array.isArray(children)){workingChildren=children;}else {workingChildren=Object.keys(children);}workingChildren// children that are in this.options_ but also in workingChildren  would
// give us extra children we do not want. So, we want to filter them out.
.concat(Object.keys(this.options_).filter(function(child){return !workingChildren.some(function(wchild){if(typeof wchild==='string'){return child===wchild;}return child===wchild.name;});})).map(function(child){var name=void 0;var opts=void 0;if(typeof child==='string'){name=child;opts=children[name]||_this.options_[name]||{};}else {name=child.name;opts=child;}return {name:name,opts:opts};}).filter(function(child){// we have to make sure that child.name isn't in the techOrder since
// techs are registerd as Components but can't aren't compatible
// See https://github.com/videojs/video.js/issues/2772
var c=Component.getComponent(child.opts.componentClass||toTitleCase(child.name));return c&&!Tech.isTech(c);}).forEach(handleAdd);}};/**
   * Builds the default DOM class name. Should be overriden by sub-components.
   *
   * @return {string}
   *         The DOM class name for this object.
   *
   * @abstract
   */Component.prototype.buildCSSClass=function buildCSSClass(){// Child classes can include a function that does:
// return 'CLASS NAME' + this._super();
return '';};/**
   * Bind a listener to the component's ready state.
   * Different from event listeners in that if the ready event has already happened
   * it will trigger the function immediately.
   *
   * @return {Component}
   *         Returns itself; method can be chained.
   */Component.prototype.ready=function ready(fn){var sync=arguments.length>1&&arguments[1]!==undefined?arguments[1]:false;if(!fn){return;}if(!this.isReady_){this.readyQueue_=this.readyQueue_||[];this.readyQueue_.push(fn);return;}if(sync){fn.call(this);}else {// Call the function asynchronously by default for consistency
this.setTimeout(fn,1);}};/**
   * Trigger all the ready listeners for this `Component`.
   *
   * @fires Component#ready
   */Component.prototype.triggerReady=function triggerReady(){this.isReady_=true;// Ensure ready is triggered asynchronously
this.setTimeout(function(){var readyQueue=this.readyQueue_;// Reset Ready Queue
this.readyQueue_=[];if(readyQueue&&readyQueue.length>0){readyQueue.forEach(function(fn){fn.call(this);},this);}// Allow for using event listeners also
/**
       * Triggered when a `Component` is ready.
       *
       * @event Component#ready
       * @type {EventTarget~Event}
       */this.trigger('ready');},1);};/**
   * Find a single DOM element matching a `selector`. This can be within the `Component`s
   * `contentEl()` or another custom context.
   *
   * @param {string} selector
   *        A valid CSS selector, which will be passed to `querySelector`.
   *
   * @param {Element|string} [context=this.contentEl()]
   *        A DOM element within which to query. Can also be a selector string in
   *        which case the first matching element will get used as context. If
   *        missing `this.contentEl()` gets used. If  `this.contentEl()` returns
   *        nothing it falls back to `document`.
   *
   * @return {Element|null}
   *         the dom element that was found, or null
   *
   * @see [Information on CSS Selectors](https://developer.mozilla.org/en-US/docs/Web/Guide/CSS/Getting_Started/Selectors)
   */Component.prototype.$=function $$$1(selector,context){return $$1(selector,context||this.contentEl());};/**
   * Finds all DOM element matching a `selector`. This can be within the `Component`s
   * `contentEl()` or another custom context.
   *
   * @param {string} selector
   *        A valid CSS selector, which will be passed to `querySelectorAll`.
   *
   * @param {Element|string} [context=this.contentEl()]
   *        A DOM element within which to query. Can also be a selector string in
   *        which case the first matching element will get used as context. If
   *        missing `this.contentEl()` gets used. If  `this.contentEl()` returns
   *        nothing it falls back to `document`.
   *
   * @return {NodeList}
   *         a list of dom elements that were found
   *
   * @see [Information on CSS Selectors](https://developer.mozilla.org/en-US/docs/Web/Guide/CSS/Getting_Started/Selectors)
   */Component.prototype.$$=function $$$$1(selector,context){return $$(selector,context||this.contentEl());};/**
   * Check if a component's element has a CSS class name.
   *
   * @param {string} classToCheck
   *        CSS class name to check.
   *
   * @return {boolean}
   *         - True if the `Component` has the class.
   *         - False if the `Component` does not have the class`
   */Component.prototype.hasClass=function hasClass$$1(classToCheck){return hasClass(this.el_,classToCheck);};/**
   * Add a CSS class name to the `Component`s element.
   *
   * @param {string} classToAdd
   *        CSS class name to add
   */Component.prototype.addClass=function addClass$$1(classToAdd){addClass(this.el_,classToAdd);};/**
   * Remove a CSS class name from the `Component`s element.
   *
   * @param {string} classToRemove
   *        CSS class name to remove
   */Component.prototype.removeClass=function removeClass$$1(classToRemove){removeClass(this.el_,classToRemove);};/**
   * Add or remove a CSS class name from the component's element.
   * - `classToToggle` gets added when {@link Component#hasClass} would return false.
   * - `classToToggle` gets removed when {@link Component#hasClass} would return true.
   *
   * @param  {string} classToToggle
   *         The class to add or remove based on (@link Component#hasClass}
   *
   * @param  {boolean|Dom~predicate} [predicate]
   *         An {@link Dom~predicate} function or a boolean
   */Component.prototype.toggleClass=function toggleClass$$1(classToToggle,predicate){toggleClass(this.el_,classToToggle,predicate);};/**
   * Show the `Component`s element if it is hidden by removing the
   * 'vjs-hidden' class name from it.
   */Component.prototype.show=function show(){this.removeClass('vjs-hidden');};/**
   * Hide the `Component`s element if it is currently showing by adding the
   * 'vjs-hidden` class name to it.
   */Component.prototype.hide=function hide(){this.addClass('vjs-hidden');};/**
   * Lock a `Component`s element in its visible state by adding the 'vjs-lock-showing'
   * class name to it. Used during fadeIn/fadeOut.
   *
   * @private
   */Component.prototype.lockShowing=function lockShowing(){this.addClass('vjs-lock-showing');};/**
   * Unlock a `Component`s element from its visible state by removing the 'vjs-lock-showing'
   * class name from it. Used during fadeIn/fadeOut.
   *
   * @private
   */Component.prototype.unlockShowing=function unlockShowing(){this.removeClass('vjs-lock-showing');};/**
   * Get the value of an attribute on the `Component`s element.
   *
   * @param {string} attribute
   *        Name of the attribute to get the value from.
   *
   * @return {string|null}
   *         - The value of the attribute that was asked for.
   *         - Can be an empty string on some browsers if the attribute does not exist
   *           or has no value
   *         - Most browsers will return null if the attibute does not exist or has
   *           no value.
   *
   * @see [DOM API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Element/getAttribute}
   */Component.prototype.getAttribute=function getAttribute$$1(attribute){return getAttribute(this.el_,attribute);};/**
   * Set the value of an attribute on the `Component`'s element
   *
   * @param {string} attribute
   *        Name of the attribute to set.
   *
   * @param {string} value
   *        Value to set the attribute to.
   *
   * @see [DOM API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Element/setAttribute}
   */Component.prototype.setAttribute=function setAttribute$$1(attribute,value){setAttribute(this.el_,attribute,value);};/**
   * Remove an attribute from the `Component`s element.
   *
   * @param {string} attribute
   *        Name of the attribute to remove.
   *
   * @see [DOM API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Element/removeAttribute}
   */Component.prototype.removeAttribute=function removeAttribute$$1(attribute){removeAttribute(this.el_,attribute);};/**
   * Get or set the width of the component based upon the CSS styles.
   * See {@link Component#dimension} for more detailed information.
   *
   * @param {number|string} [num]
   *        The width that you want to set postfixed with '%', 'px' or nothing.
   *
   * @param {boolean} [skipListeners]
   *        Skip the componentresize event trigger
   *
   * @return {number|string}
   *         The width when getting, zero if there is no width. Can be a string
   *           postpixed with '%' or 'px'.
   */Component.prototype.width=function width(num,skipListeners){return this.dimension('width',num,skipListeners);};/**
   * Get or set the height of the component based upon the CSS styles.
   * See {@link Component#dimension} for more detailed information.
   *
   * @param {number|string} [num]
   *        The height that you want to set postfixed with '%', 'px' or nothing.
   *
   * @param {boolean} [skipListeners]
   *        Skip the componentresize event trigger
   *
   * @return {number|string}
   *         The width when getting, zero if there is no width. Can be a string
   *         postpixed with '%' or 'px'.
   */Component.prototype.height=function height(num,skipListeners){return this.dimension('height',num,skipListeners);};/**
   * Set both the width and height of the `Component` element at the same time.
   *
   * @param  {number|string} width
   *         Width to set the `Component`s element to.
   *
   * @param  {number|string} height
   *         Height to set the `Component`s element to.
   */Component.prototype.dimensions=function dimensions(width,height){// Skip componentresize listeners on width for optimization
this.width(width,true);this.height(height);};/**
   * Get or set width or height of the `Component` element. This is the shared code
   * for the {@link Component#width} and {@link Component#height}.
   *
   * Things to know:
   * - If the width or height in an number this will return the number postfixed with 'px'.
   * - If the width/height is a percent this will return the percent postfixed with '%'
   * - Hidden elements have a width of 0 with `window.getComputedStyle`. This function
   *   defaults to the `Component`s `style.width` and falls back to `window.getComputedStyle`.
   *   See [this]{@link http://www.foliotek.com/devblog/getting-the-width-of-a-hidden-element-with-jquery-using-width/}
   *   for more information
   * - If you want the computed style of the component, use {@link Component#currentWidth}
   *   and {@link {Component#currentHeight}
   *
   * @fires Component#componentresize
   *
   * @param {string} widthOrHeight
   8        'width' or 'height'
   *
   * @param  {number|string} [num]
   8         New dimension
   *
   * @param  {boolean} [skipListeners]
   *         Skip componentresize event trigger
   *
   * @return {number}
   *         The dimension when getting or 0 if unset
   */Component.prototype.dimension=function dimension(widthOrHeight,num,skipListeners){if(num!==undefined){// Set to zero if null or literally NaN (NaN !== NaN)
if(num===null||num!==num){num=0;}// Check if using css width/height (% or px) and adjust
if((''+num).indexOf('%')!==-1||(''+num).indexOf('px')!==-1){this.el_.style[widthOrHeight]=num;}else if(num==='auto'){this.el_.style[widthOrHeight]='';}else {this.el_.style[widthOrHeight]=num+'px';}// skipListeners allows us to avoid triggering the resize event when setting both width and height
if(!skipListeners){/**
         * Triggered when a component is resized.
         *
         * @event Component#componentresize
         * @type {EventTarget~Event}
         */this.trigger('componentresize');}return;}// Not setting a value, so getting it
// Make sure element exists
if(!this.el_){return 0;}// Get dimension value from style
var val=this.el_.style[widthOrHeight];var pxIndex=val.indexOf('px');if(pxIndex!==-1){// Return the pixel value with no 'px'
return parseInt(val.slice(0,pxIndex),10);}// No px so using % or no style was set, so falling back to offsetWidth/height
// If component has display:none, offset will return 0
// TODO: handle display:none and no dimension style using px
return parseInt(this.el_['offset'+toTitleCase(widthOrHeight)],10);};/**
   * Get the computed width or the height of the component's element.
   *
   * Uses `window.getComputedStyle`.
   *
   * @param {string} widthOrHeight
   *        A string containing 'width' or 'height'. Whichever one you want to get.
   *
   * @return {number}
   *         The dimension that gets asked for or 0 if nothing was set
   *         for that dimension.
   */Component.prototype.currentDimension=function currentDimension(widthOrHeight){var computedWidthOrHeight=0;if(widthOrHeight!=='width'&&widthOrHeight!=='height'){throw new Error('currentDimension only accepts width or height value');}if(typeof window$1.getComputedStyle==='function'){var computedStyle=window$1.getComputedStyle(this.el_);computedWidthOrHeight=computedStyle.getPropertyValue(widthOrHeight)||computedStyle[widthOrHeight];}// remove 'px' from variable and parse as integer
computedWidthOrHeight=parseFloat(computedWidthOrHeight);// if the computed value is still 0, it's possible that the browser is lying
// and we want to check the offset values.
// This code also runs on IE8 and wherever getComputedStyle doesn't exist.
if(computedWidthOrHeight===0){var rule='offset'+toTitleCase(widthOrHeight);computedWidthOrHeight=this.el_[rule];}return computedWidthOrHeight;};/**
   * An object that contains width and height values of the `Component`s
   * computed style. Uses `window.getComputedStyle`.
   *
   * @typedef {Object} Component~DimensionObject
   *
   * @property {number} width
   *           The width of the `Component`s computed style.
   *
   * @property {number} height
   *           The height of the `Component`s computed style.
   */ /**
   * Get an object that contains computed width and height values of the
   * component's element.
   *
   * Uses `window.getComputedStyle`.
   *
   * @return {Component~DimensionObject}
   *         The computed dimensions of the component's element.
   */Component.prototype.currentDimensions=function currentDimensions(){return {width:this.currentDimension('width'),height:this.currentDimension('height')};};/**
   * Get the computed width of the component's element.
   *
   * Uses `window.getComputedStyle`.
   *
   * @return {number}
   *         The computed width of the component's element.
   */Component.prototype.currentWidth=function currentWidth(){return this.currentDimension('width');};/**
   * Get the computed height of the component's element.
   *
   * Uses `window.getComputedStyle`.
   *
   * @return {number}
   *         The computed height of the component's element.
   */Component.prototype.currentHeight=function currentHeight(){return this.currentDimension('height');};/**
   * Set the focus to this component
   */Component.prototype.focus=function focus(){this.el_.focus();};/**
   * Remove the focus from this component
   */Component.prototype.blur=function blur(){this.el_.blur();};/**
   * Emit a 'tap' events when touch event support gets detected. This gets used to
   * support toggling the controls through a tap on the video. They get enabled
   * because every sub-component would have extra overhead otherwise.
   *
   * @private
   * @fires Component#tap
   * @listens Component#touchstart
   * @listens Component#touchmove
   * @listens Component#touchleave
   * @listens Component#touchcancel
   * @listens Component#touchend
    */Component.prototype.emitTapEvents=function emitTapEvents(){// Track the start time so we can determine how long the touch lasted
var touchStart=0;var firstTouch=null;// Maximum movement allowed during a touch event to still be considered a tap
// Other popular libs use anywhere from 2 (hammer.js) to 15,
// so 10 seems like a nice, round number.
var tapMovementThreshold=10;// The maximum length a touch can be while still being considered a tap
var touchTimeThreshold=200;var couldBeTap=void 0;this.on('touchstart',function(event){// If more than one finger, don't consider treating this as a click
if(event.touches.length===1){// Copy pageX/pageY from the object
firstTouch={pageX:event.touches[0].pageX,pageY:event.touches[0].pageY};// Record start time so we can detect a tap vs. "touch and hold"
touchStart=new Date().getTime();// Reset couldBeTap tracking
couldBeTap=true;}});this.on('touchmove',function(event){// If more than one finger, don't consider treating this as a click
if(event.touches.length>1){couldBeTap=false;}else if(firstTouch){// Some devices will throw touchmoves for all but the slightest of taps.
// So, if we moved only a small distance, this could still be a tap
var xdiff=event.touches[0].pageX-firstTouch.pageX;var ydiff=event.touches[0].pageY-firstTouch.pageY;var touchDistance=Math.sqrt(xdiff*xdiff+ydiff*ydiff);if(touchDistance>tapMovementThreshold){couldBeTap=false;}}});var noTap=function noTap(){couldBeTap=false;};// TODO: Listen to the original target. http://youtu.be/DujfpXOKUp8?t=13m8s
this.on('touchleave',noTap);this.on('touchcancel',noTap);// When the touch ends, measure how long it took and trigger the appropriate
// event
this.on('touchend',function(event){firstTouch=null;// Proceed only if the touchmove/leave/cancel event didn't happen
if(couldBeTap===true){// Measure how long the touch lasted
var touchTime=new Date().getTime()-touchStart;// Make sure the touch was less than the threshold to be considered a tap
if(touchTime<touchTimeThreshold){// Don't let browser turn this into a click
event.preventDefault();/**
           * Triggered when a `Component` is tapped.
           *
           * @event Component#tap
           * @type {EventTarget~Event}
           */this.trigger('tap');// It may be good to copy the touchend event object and change the
// type to tap, if the other event properties aren't exact after
// Events.fixEvent runs (e.g. event.target)
}}});};/**
   * This function reports user activity whenever touch events happen. This can get
   * turned off by any sub-components that wants touch events to act another way.
   *
   * Report user touch activity when touch events occur. User activity gets used to
   * determine when controls should show/hide. It is simple when it comes to mouse
   * events, because any mouse event should show the controls. So we capture mouse
   * events that bubble up to the player and report activity when that happens.
   * With touch events it isn't as easy as `touchstart` and `touchend` toggle player
   * controls. So touch events can't help us at the player level either.
   *
   * User activity gets checked asynchronously. So what could happen is a tap event
   * on the video turns the controls off. Then the `touchend` event bubbles up to
   * the player. Which, if it reported user activity, would turn the controls right
   * back on. We also don't want to completely block touch events from bubbling up.
   * Furthermore a `touchmove` event and anything other than a tap, should not turn
   * controls back on.
   *
   * @listens Component#touchstart
   * @listens Component#touchmove
   * @listens Component#touchend
   * @listens Component#touchcancel
   */Component.prototype.enableTouchActivity=function enableTouchActivity(){// Don't continue if the root player doesn't support reporting user activity
if(!this.player()||!this.player().reportUserActivity){return;}// listener for reporting that the user is active
var report=bind(this.player(),this.player().reportUserActivity);var touchHolding=void 0;this.on('touchstart',function(){report();// For as long as the they are touching the device or have their mouse down,
// we consider them active even if they're not moving their finger or mouse.
// So we want to continue to update that they are active
this.clearInterval(touchHolding);// report at the same interval as activityCheck
touchHolding=this.setInterval(report,250);});var touchEnd=function touchEnd(event){report();// stop the interval that maintains activity if the touch is holding
this.clearInterval(touchHolding);};this.on('touchmove',report);this.on('touchend',touchEnd);this.on('touchcancel',touchEnd);};/**
   * A callback that has no parameters and is bound into `Component`s context.
   *
   * @callback Component~GenericCallback
   * @this Component
   */ /**
   * Creates a function that runs after an `x` millisecond timeout. This function is a
   * wrapper around `window.setTimeout`. There are a few reasons to use this one
   * instead though:
   * 1. It gets cleared via  {@link Component#clearTimeout} when
   *    {@link Component#dispose} gets called.
   * 2. The function callback will gets turned into a {@link Component~GenericCallback}
   *
   * > Note: You can't use `window.clearTimeout` on the id returned by this function. This
   *         will cause its dispose listener not to get cleaned up! Please use
   *         {@link Component#clearTimeout} or {@link Component#dispose} instead.
   *
   * @param {Component~GenericCallback} fn
   *        The function that will be run after `timeout`.
   *
   * @param {number} timeout
   *        Timeout in milliseconds to delay before executing the specified function.
   *
   * @return {number}
   *         Returns a timeout ID that gets used to identify the timeout. It can also
   *         get used in {@link Component#clearTimeout} to clear the timeout that
   *         was set.
   *
   * @listens Component#dispose
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/WindowTimers/setTimeout}
   */Component.prototype.setTimeout=function setTimeout(fn,timeout){var _this2=this;// declare as variables so they are properly available in timeout function
// eslint-disable-next-line
var timeoutId,disposeFn;fn=bind(this,fn);timeoutId=window$1.setTimeout(function(){_this2.off('dispose',disposeFn);fn();},timeout);disposeFn=function disposeFn(){return _this2.clearTimeout(timeoutId);};disposeFn.guid='vjs-timeout-'+timeoutId;this.on('dispose',disposeFn);return timeoutId;};/**
   * Clears a timeout that gets created via `window.setTimeout` or
   * {@link Component#setTimeout}. If you set a timeout via {@link Component#setTimeout}
   * use this function instead of `window.clearTimout`. If you don't your dispose
   * listener will not get cleaned up until {@link Component#dispose}!
   *
   * @param {number} timeoutId
   *        The id of the timeout to clear. The return value of
   *        {@link Component#setTimeout} or `window.setTimeout`.
   *
   * @return {number}
   *         Returns the timeout id that was cleared.
   *
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/WindowTimers/clearTimeout}
   */Component.prototype.clearTimeout=function clearTimeout(timeoutId){window$1.clearTimeout(timeoutId);var disposeFn=function disposeFn(){};disposeFn.guid='vjs-timeout-'+timeoutId;this.off('dispose',disposeFn);return timeoutId;};/**
   * Creates a function that gets run every `x` milliseconds. This function is a wrapper
   * around `window.setInterval`. There are a few reasons to use this one instead though.
   * 1. It gets cleared via  {@link Component#clearInterval} when
   *    {@link Component#dispose} gets called.
   * 2. The function callback will be a {@link Component~GenericCallback}
   *
   * @param {Component~GenericCallback} fn
   *        The function to run every `x` seconds.
   *
   * @param {number} interval
   *        Execute the specified function every `x` milliseconds.
   *
   * @return {number}
   *         Returns an id that can be used to identify the interval. It can also be be used in
   *         {@link Component#clearInterval} to clear the interval.
   *
   * @listens Component#dispose
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/WindowTimers/setInterval}
   */Component.prototype.setInterval=function setInterval(fn,interval){var _this3=this;fn=bind(this,fn);var intervalId=window$1.setInterval(fn,interval);var disposeFn=function disposeFn(){return _this3.clearInterval(intervalId);};disposeFn.guid='vjs-interval-'+intervalId;this.on('dispose',disposeFn);return intervalId;};/**
   * Clears an interval that gets created via `window.setInterval` or
   * {@link Component#setInterval}. If you set an inteval via {@link Component#setInterval}
   * use this function instead of `window.clearInterval`. If you don't your dispose
   * listener will not get cleaned up until {@link Component#dispose}!
   *
   * @param {number} intervalId
   *        The id of the interval to clear. The return value of
   *        {@link Component#setInterval} or `window.setInterval`.
   *
   * @return {number}
   *         Returns the interval id that was cleared.
   *
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/WindowTimers/clearInterval}
   */Component.prototype.clearInterval=function clearInterval(intervalId){window$1.clearInterval(intervalId);var disposeFn=function disposeFn(){};disposeFn.guid='vjs-interval-'+intervalId;this.off('dispose',disposeFn);return intervalId;};/**
   * Queues up a callback to be passed to requestAnimationFrame (rAF), but
   * with a few extra bonuses:
   *
   * - Supports browsers that do not support rAF by falling back to
   *   {@link Component#setTimeout}.
   *
   * - The callback is turned into a {@link Component~GenericCallback} (i.e.
   *   bound to the component).
   *
   * - Automatic cancellation of the rAF callback is handled if the component
   *   is disposed before it is called.
   *
   * @param  {Component~GenericCallback} fn
   *         A function that will be bound to this component and executed just
   *         before the browser's next repaint.
   *
   * @return {number}
   *         Returns an rAF ID that gets used to identify the timeout. It can
   *         also be used in {@link Component#cancelAnimationFrame} to cancel
   *         the animation frame callback.
   *
   * @listens Component#dispose
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame}
   */Component.prototype.requestAnimationFrame=function requestAnimationFrame(fn){var _this4=this;// declare as variables so they are properly available in rAF function
// eslint-disable-next-line
var id,disposeFn;if(this.supportsRaf_){fn=bind(this,fn);id=window$1.requestAnimationFrame(function(){_this4.off('dispose',disposeFn);fn();});disposeFn=function disposeFn(){return _this4.cancelAnimationFrame(id);};disposeFn.guid='vjs-raf-'+id;this.on('dispose',disposeFn);return id;}// Fall back to using a timer.
return this.setTimeout(fn,1000/60);};/**
   * Cancels a queued callback passed to {@link Component#requestAnimationFrame}
   * (rAF).
   *
   * If you queue an rAF callback via {@link Component#requestAnimationFrame},
   * use this function instead of `window.cancelAnimationFrame`. If you don't,
   * your dispose listener will not get cleaned up until {@link Component#dispose}!
   *
   * @param {number} id
   *        The rAF ID to clear. The return value of {@link Component#requestAnimationFrame}.
   *
   * @return {number}
   *         Returns the rAF ID that was cleared.
   *
   * @see [Similar to]{@link https://developer.mozilla.org/en-US/docs/Web/API/window/cancelAnimationFrame}
   */Component.prototype.cancelAnimationFrame=function cancelAnimationFrame(id){if(this.supportsRaf_){window$1.cancelAnimationFrame(id);var disposeFn=function disposeFn(){};disposeFn.guid='vjs-raf-'+id;this.off('dispose',disposeFn);return id;}// Fall back to using a timer.
return this.clearTimeout(id);};/**
   * Register a `Component` with `videojs` given the name and the component.
   *
   * > NOTE: {@link Tech}s should not be registered as a `Component`. {@link Tech}s
   *         should be registered using {@link Tech.registerTech} or
   *         {@link videojs:videojs.registerTech}.
   *
   * > NOTE: This function can also be seen on videojs as
   *         {@link videojs:videojs.registerComponent}.
   *
   * @param {string} name
   *        The name of the `Component` to register.
   *
   * @param {Component} ComponentToRegister
   *        The `Component` class to register.
   *
   * @return {Component}
   *         The `Component` that was registered.
   */Component.registerComponent=function registerComponent(name,ComponentToRegister){if(typeof name!=='string'||!name){throw new Error('Illegal component name, "'+name+'"; must be a non-empty string.');}var Tech=Component.getComponent('Tech');// We need to make sure this check is only done if Tech has been registered.
var isTech=Tech&&Tech.isTech(ComponentToRegister);var isComp=Component===ComponentToRegister||Component.prototype.isPrototypeOf(ComponentToRegister.prototype);if(isTech||!isComp){var reason=void 0;if(isTech){reason='techs must be registered using Tech.registerTech()';}else {reason='must be a Component subclass';}throw new Error('Illegal component, "'+name+'"; '+reason+'.');}name=toTitleCase(name);if(!Component.components_){Component.components_={};}var Player=Component.getComponent('Player');if(name==='Player'&&Player&&Player.players){var players=Player.players;var playerNames=Object.keys(players);// If we have players that were disposed, then their name will still be
// in Players.players. So, we must loop through and verify that the value
// for each item is not null. This allows registration of the Player component
// after all players have been disposed or before any were created.
if(players&&playerNames.length>0&&playerNames.map(function(pname){return players[pname];}).every(Boolean)){throw new Error('Can not register Player component after player has been created.');}}Component.components_[name]=ComponentToRegister;return ComponentToRegister;};/**
   * Get a `Component` based on the name it was registered with.
   *
   * @param {string} name
   *        The Name of the component to get.
   *
   * @return {Component}
   *         The `Component` that got registered under the given name.
   *
   * @deprecated In `videojs` 6 this will not return `Component`s that were not
   *             registered using {@link Component.registerComponent}. Currently we
   *             check the global `videojs` object for a `Component` name and
   *             return that if it exists.
   */Component.getComponent=function getComponent(name){if(!name){return;}name=toTitleCase(name);if(Component.components_&&Component.components_[name]){return Component.components_[name];}};return Component;}();/**
 * Whether or not this component supports `requestAnimationFrame`.
 *
 * This is exposed primarily for testing purposes.
 *
 * @private
 * @type {Boolean}
 */Component.prototype.supportsRaf_=typeof window$1.requestAnimationFrame==='function'&&typeof window$1.cancelAnimationFrame==='function';Component.registerComponent('Component',Component);/**
 * @file time-ranges.js
 * @module time-ranges
 */ /**
 * Returns the time for the specified index at the start or end
 * of a TimeRange object.
 *
 * @function time-ranges:indexFunction
 *
 * @param {number} [index=0]
 *        The range number to return the time for.
 *
 * @return {number}
 *         The time that offset at the specified index.
 *
 * @depricated index must be set to a value, in the future this will throw an error.
 */ /**
 * An object that contains ranges of time for various reasons.
 *
 * @typedef {Object} TimeRange
 *
 * @property {number} length
 *           The number of time ranges represented by this Object
 *
 * @property {time-ranges:indexFunction} start
 *           Returns the time offset at which a specified time range begins.
 *
 * @property {time-ranges:indexFunction} end
 *           Returns the time offset at which a specified time range ends.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/TimeRanges
 */ /**
 * Check if any of the time ranges are over the maximum index.
 *
 * @param {string} fnName
 *        The function name to use for logging
 *
 * @param {number} index
 *        The index to check
 *
 * @param {number} maxIndex
 *        The maximum possible index
 *
 * @throws {Error} if the timeRanges provided are over the maxIndex
 */function rangeCheck(fnName,index,maxIndex){if(typeof index!=='number'||index<0||index>maxIndex){throw new Error('Failed to execute \''+fnName+'\' on \'TimeRanges\': The index provided ('+index+') is non-numeric or out of bounds (0-'+maxIndex+').');}}/**
 * Get the time for the specified index at the start or end
 * of a TimeRange object.
 *
 * @param {string} fnName
 *        The function name to use for logging
 *
 * @param {string} valueIndex
 *        The proprety that should be used to get the time. should be 'start' or 'end'
 *
 * @param {Array} ranges
 *        An array of time ranges
 *
 * @param {Array} [rangeIndex=0]
 *        The index to start the search at
 *
 * @return {number}
 *         The time that offset at the specified index.
 *
 *
 * @depricated rangeIndex must be set to a value, in the future this will throw an error.
 * @throws {Error} if rangeIndex is more than the length of ranges
 */function getRange(fnName,valueIndex,ranges,rangeIndex){rangeCheck(fnName,rangeIndex,ranges.length-1);return ranges[rangeIndex][valueIndex];}/**
 * Create a time range object given ranges of time.
 *
 * @param {Array} [ranges]
 *        An array of time ranges.
 */function createTimeRangesObj(ranges){if(ranges===undefined||ranges.length===0){return {length:0,start:function start(){throw new Error('This TimeRanges object is empty');},end:function end(){throw new Error('This TimeRanges object is empty');}};}return {length:ranges.length,start:getRange.bind(null,'start',0,ranges),end:getRange.bind(null,'end',1,ranges)};}/**
 * Should create a fake `TimeRange` object which mimics an HTML5 time range instance.
 *
 * @param {number|Array} start
 *        The start of a single range or an array of ranges
 *
 * @param {number} end
 *        The end of a single range.
 *
 * @private
 */function createTimeRanges(start,end){if(Array.isArray(start)){return createTimeRangesObj(start);}else if(start===undefined||end===undefined){return createTimeRangesObj();}return createTimeRangesObj([[start,end]]);}/**
 * @file buffer.js
 * @module buffer
 */ /**
 * Compute the percentage of the media that has been buffered.
 *
 * @param {TimeRange} buffered
 *        The current `TimeRange` object representing buffered time ranges
 *
 * @param {number} duration
 *        Total duration of the media
 *
 * @return {number}
 *         Percent buffered of the total duration in decimal form.
 */function bufferedPercent(buffered,duration){var bufferedDuration=0;var start=void 0;var end=void 0;if(!duration){return 0;}if(!buffered||!buffered.length){buffered=createTimeRanges(0,0);}for(var i=0;i<buffered.length;i++){start=buffered.start(i);end=buffered.end(i);// buffered end can be bigger than duration by a very small fraction
if(end>duration){end=duration;}bufferedDuration+=end-start;}return bufferedDuration/duration;}/**
 * @file fullscreen-api.js
 * @module fullscreen-api
 * @private
 */ /**
 * Store the browser-specific methods for the fullscreen API.
 *
 * @type {Object}
 * @see [Specification]{@link https://fullscreen.spec.whatwg.org}
 * @see [Map Approach From Screenfull.js]{@link https://github.com/sindresorhus/screenfull.js}
 */var FullscreenApi={};// browser API methods
var apiMap=[['requestFullscreen','exitFullscreen','fullscreenElement','fullscreenEnabled','fullscreenchange','fullscreenerror'],// WebKit
['webkitRequestFullscreen','webkitExitFullscreen','webkitFullscreenElement','webkitFullscreenEnabled','webkitfullscreenchange','webkitfullscreenerror'],// Old WebKit (Safari 5.1)
['webkitRequestFullScreen','webkitCancelFullScreen','webkitCurrentFullScreenElement','webkitCancelFullScreen','webkitfullscreenchange','webkitfullscreenerror'],// Mozilla
['mozRequestFullScreen','mozCancelFullScreen','mozFullScreenElement','mozFullScreenEnabled','mozfullscreenchange','mozfullscreenerror'],// Microsoft
['msRequestFullscreen','msExitFullscreen','msFullscreenElement','msFullscreenEnabled','MSFullscreenChange','MSFullscreenError']];var specApi=apiMap[0];var browserApi=void 0;// determine the supported set of functions
for(var i$4=0;i$4<apiMap.length;i$4++){// check for exitFullscreen function
if(apiMap[i$4][1]in document$1){browserApi=apiMap[i$4];break;}}// map the browser API names to the spec API names
if(browserApi){for(var _i=0;_i<browserApi.length;_i++){FullscreenApi[specApi[_i]]=browserApi[_i];}}/**
 * @file media-error.js
 */ /**
 * A Custom `MediaError` class which mimics the standard HTML5 `MediaError` class.
 *
 * @param {number|string|Object|MediaError} value
 *        This can be of multiple types:
 *        - number: should be a standard error code
 *        - string: an error message (the code will be 0)
 *        - Object: arbitrary properties
 *        - `MediaError` (native): used to populate a video.js `MediaError` object
 *        - `MediaError` (video.js): will return itself if it's already a
 *          video.js `MediaError` object.
 *
 * @see [MediaError Spec]{@link https://dev.w3.org/html5/spec-author-view/video.html#mediaerror}
 * @see [Encrypted MediaError Spec]{@link https://www.w3.org/TR/2013/WD-encrypted-media-20130510/#error-codes}
 *
 * @class MediaError
 */function MediaError(value){// Allow redundant calls to this constructor to avoid having `instanceof`
// checks peppered around the code.
if(value instanceof MediaError){return value;}if(typeof value==='number'){this.code=value;}else if(typeof value==='string'){// default code is zero, so this is a custom error
this.message=value;}else if(isObject(value)){// We assign the `code` property manually because native `MediaError` objects
// do not expose it as an own/enumerable property of the object.
if(typeof value.code==='number'){this.code=value.code;}assign$1(this,value);}if(!this.message){this.message=MediaError.defaultMessages[this.code]||'';}}/**
 * The error code that refers two one of the defined `MediaError` types
 *
 * @type {Number}
 */MediaError.prototype.code=0;/**
 * An optional message that to show with the error. Message is not part of the HTML5
 * video spec but allows for more informative custom errors.
 *
 * @type {String}
 */MediaError.prototype.message='';/**
 * An optional status code that can be set by plugins to allow even more detail about
 * the error. For example a plugin might provide a specific HTTP status code and an
 * error message for that code. Then when the plugin gets that error this class will
 * know how to display an error message for it. This allows a custom message to show
 * up on the `Player` error overlay.
 *
 * @type {Array}
 */MediaError.prototype.status=null;/**
 * Errors indexed by the W3C standard. The order **CANNOT CHANGE**! See the
 * specification listed under {@link MediaError} for more information.
 *
 * @enum {array}
 * @readonly
 * @property {string} 0 - MEDIA_ERR_CUSTOM
 * @property {string} 1 - MEDIA_ERR_CUSTOM
 * @property {string} 2 - MEDIA_ERR_ABORTED
 * @property {string} 3 - MEDIA_ERR_NETWORK
 * @property {string} 4 - MEDIA_ERR_SRC_NOT_SUPPORTED
 * @property {string} 5 - MEDIA_ERR_ENCRYPTED
 */MediaError.errorTypes=['MEDIA_ERR_CUSTOM','MEDIA_ERR_ABORTED','MEDIA_ERR_NETWORK','MEDIA_ERR_DECODE','MEDIA_ERR_SRC_NOT_SUPPORTED','MEDIA_ERR_ENCRYPTED'];/**
 * The default `MediaError` messages based on the {@link MediaError.errorTypes}.
 *
 * @type {Array}
 * @constant
 */MediaError.defaultMessages={1:'You aborted the media playback',2:'A network error caused the media download to fail part-way.',3:'The media playback was aborted due to a corruption problem or because the media used features your browser did not support.',4:'The media could not be loaded, either because the server or network failed or because the format is not supported.',5:'The media is encrypted and we do not have the keys to decrypt it.'};// Add types as properties on MediaError
// e.g. MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED = 4;
for(var errNum=0;errNum<MediaError.errorTypes.length;errNum++){MediaError[MediaError.errorTypes[errNum]]=errNum;// values should be accessible on both the class and instance
MediaError.prototype[MediaError.errorTypes[errNum]]=errNum;}/**
 * Returns whether an object is `Promise`-like (i.e. has a `then` method).
 *
 * @param  {Object}  value
 *         An object that may or may not be `Promise`-like.
 *
 * @return {Boolean}
 *         Whether or not the object is `Promise`-like.
 */function isPromise(value){return value!==undefined&&value!==null&&typeof value.then==='function';}/**
 * Silence a Promise-like object.
 *
 * This is useful for avoiding non-harmful, but potentially confusing "uncaught
 * play promise" rejection error messages.
 *
 * @param  {Object} value
 *         An object that may or may not be `Promise`-like.
 */function silencePromise(value){if(isPromise(value)){value.then(null,function(e){});}}/**
 * @file text-track-list-converter.js Utilities for capturing text track state and
 * re-creating tracks based on a capture.
 *
 * @module text-track-list-converter
 */ /**
 * Examine a single {@link TextTrack} and return a JSON-compatible javascript object that
 * represents the {@link TextTrack}'s state.
 *
 * @param {TextTrack} track
 *        The text track to query.
 *
 * @return {Object}
 *         A serializable javascript representation of the TextTrack.
 * @private
 */var trackToJson_=function trackToJson_(track){var ret=['kind','label','language','id','inBandMetadataTrackDispatchType','mode','src'].reduce(function(acc,prop,i){if(track[prop]){acc[prop]=track[prop];}return acc;},{cues:track.cues&&Array.prototype.map.call(track.cues,function(cue){return {startTime:cue.startTime,endTime:cue.endTime,text:cue.text,id:cue.id};})});return ret;};/**
 * Examine a {@link Tech} and return a JSON-compatible javascript array that represents the
 * state of all {@link TextTrack}s currently configured. The return array is compatible with
 * {@link text-track-list-converter:jsonToTextTracks}.
 *
 * @param {Tech} tech
 *        The tech object to query
 *
 * @return {Array}
 *         A serializable javascript representation of the {@link Tech}s
 *         {@link TextTrackList}.
 */var textTracksToJson=function textTracksToJson(tech){var trackEls=tech.$$('track');var trackObjs=Array.prototype.map.call(trackEls,function(t){return t.track;});var tracks=Array.prototype.map.call(trackEls,function(trackEl){var json=trackToJson_(trackEl.track);if(trackEl.src){json.src=trackEl.src;}return json;});return tracks.concat(Array.prototype.filter.call(tech.textTracks(),function(track){return trackObjs.indexOf(track)===-1;}).map(trackToJson_));};/**
 * Create a set of remote {@link TextTrack}s on a {@link Tech} based on an array of javascript
 * object {@link TextTrack} representations.
 *
 * @param {Array} json
 *        An array of `TextTrack` representation objects, like those that would be
 *        produced by `textTracksToJson`.
 *
 * @param {Tech} tech
 *        The `Tech` to create the `TextTrack`s on.
 */var jsonToTextTracks=function jsonToTextTracks(json,tech){json.forEach(function(track){var addedTrack=tech.addRemoteTextTrack(track).track;if(!track.src&&track.cues){track.cues.forEach(function(cue){return addedTrack.addCue(cue);});}});return tech.textTracks();};var textTrackConverter={textTracksToJson:textTracksToJson,jsonToTextTracks:jsonToTextTracks,trackToJson_:trackToJson_};/**
 * @file modal-dialog.js
 */var MODAL_CLASS_NAME='vjs-modal-dialog';var ESC=27;/**
 * The `ModalDialog` displays over the video and its controls, which blocks
 * interaction with the player until it is closed.
 *
 * Modal dialogs include a "Close" button and will close when that button
 * is activated - or when ESC is pressed anywhere.
 *
 * @extends Component
 */var ModalDialog=function(_Component){inherits(ModalDialog,_Component);/**
   * Create an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Mixed} [options.content=undefined]
   *        Provide customized content for this modal.
   *
   * @param {string} [options.description]
   *        A text description for the modal, primarily for accessibility.
   *
   * @param {boolean} [options.fillAlways=false]
   *        Normally, modals are automatically filled only the first time
   *        they open. This tells the modal to refresh its content
   *        every time it opens.
   *
   * @param {string} [options.label]
   *        A text label for the modal, primarily for accessibility.
   *
   * @param {boolean} [options.temporary=true]
   *        If `true`, the modal can only be opened once; it will be
   *        disposed as soon as it's closed.
   *
   * @param {boolean} [options.uncloseable=false]
   *        If `true`, the user will not be able to close the modal
   *        through the UI in the normal ways. Programmatic closing is
   *        still possible.
   */function ModalDialog(player,options){classCallCheck(this,ModalDialog);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.opened_=_this.hasBeenOpened_=_this.hasBeenFilled_=false;_this.closeable(!_this.options_.uncloseable);_this.content(_this.options_.content);// Make sure the contentEl is defined AFTER any children are initialized
// because we only want the contents of the modal in the contentEl
// (not the UI elements like the close button).
_this.contentEl_=createEl('div',{className:MODAL_CLASS_NAME+'-content'},{role:'document'});_this.descEl_=createEl('p',{className:MODAL_CLASS_NAME+'-description vjs-control-text',id:_this.el().getAttribute('aria-describedby')});textContent(_this.descEl_,_this.description());_this.el_.appendChild(_this.descEl_);_this.el_.appendChild(_this.contentEl_);return _this;}/**
   * Create the `ModalDialog`'s DOM element
   *
   * @return {Element}
   *         The DOM element that gets created.
   */ModalDialog.prototype.createEl=function createEl$$1(){return _Component.prototype.createEl.call(this,'div',{className:this.buildCSSClass(),tabIndex:-1},{'aria-describedby':this.id()+'_description','aria-hidden':'true','aria-label':this.label(),'role':'dialog'});};ModalDialog.prototype.dispose=function dispose(){this.contentEl_=null;this.descEl_=null;this.previouslyActiveEl_=null;_Component.prototype.dispose.call(this);};/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */ModalDialog.prototype.buildCSSClass=function buildCSSClass(){return MODAL_CLASS_NAME+' vjs-hidden '+_Component.prototype.buildCSSClass.call(this);};/**
   * Handles `keydown` events on the document, looking for ESC, which closes
   * the modal.
   *
   * @param {EventTarget~Event} e
   *        The keypress that triggered this event.
   *
   * @listens keydown
   */ModalDialog.prototype.handleKeyPress=function handleKeyPress(e){if(e.which===ESC&&this.closeable()){this.close();}};/**
   * Returns the label string for this modal. Primarily used for accessibility.
   *
   * @return {string}
   *         the localized or raw label of this modal.
   */ModalDialog.prototype.label=function label(){return this.localize(this.options_.label||'Modal Window');};/**
   * Returns the description string for this modal. Primarily used for
   * accessibility.
   *
   * @return {string}
   *         The localized or raw description of this modal.
   */ModalDialog.prototype.description=function description(){var desc=this.options_.description||this.localize('This is a modal window.');// Append a universal closeability message if the modal is closeable.
if(this.closeable()){desc+=' '+this.localize('This modal can be closed by pressing the Escape key or activating the close button.');}return desc;};/**
   * Opens the modal.
   *
   * @fires ModalDialog#beforemodalopen
   * @fires ModalDialog#modalopen
   */ModalDialog.prototype.open=function open(){if(!this.opened_){var player=this.player();/**
        * Fired just before a `ModalDialog` is opened.
        *
        * @event ModalDialog#beforemodalopen
        * @type {EventTarget~Event}
        */this.trigger('beforemodalopen');this.opened_=true;// Fill content if the modal has never opened before and
// never been filled.
if(this.options_.fillAlways||!this.hasBeenOpened_&&!this.hasBeenFilled_){this.fill();}// If the player was playing, pause it and take note of its previously
// playing state.
this.wasPlaying_=!player.paused();if(this.options_.pauseOnOpen&&this.wasPlaying_){player.pause();}if(this.closeable()){this.on(this.el_.ownerDocument,'keydown',bind(this,this.handleKeyPress));}// Hide controls and note if they were enabled.
this.hadControls_=player.controls();player.controls(false);this.show();this.conditionalFocus_();this.el().setAttribute('aria-hidden','false');/**
        * Fired just after a `ModalDialog` is opened.
        *
        * @event ModalDialog#modalopen
        * @type {EventTarget~Event}
        */this.trigger('modalopen');this.hasBeenOpened_=true;}};/**
   * If the `ModalDialog` is currently open or closed.
   *
   * @param  {boolean} [value]
   *         If given, it will open (`true`) or close (`false`) the modal.
   *
   * @return {boolean}
   *         the current open state of the modaldialog
   */ModalDialog.prototype.opened=function opened(value){if(typeof value==='boolean'){this[value?'open':'close']();}return this.opened_;};/**
   * Closes the modal, does nothing if the `ModalDialog` is
   * not open.
   *
   * @fires ModalDialog#beforemodalclose
   * @fires ModalDialog#modalclose
   */ModalDialog.prototype.close=function close(){if(!this.opened_){return;}var player=this.player();/**
      * Fired just before a `ModalDialog` is closed.
      *
      * @event ModalDialog#beforemodalclose
      * @type {EventTarget~Event}
      */this.trigger('beforemodalclose');this.opened_=false;if(this.wasPlaying_&&this.options_.pauseOnOpen){player.play();}if(this.closeable()){this.off(this.el_.ownerDocument,'keydown',bind(this,this.handleKeyPress));}if(this.hadControls_){player.controls(true);}this.hide();this.el().setAttribute('aria-hidden','true');/**
      * Fired just after a `ModalDialog` is closed.
      *
      * @event ModalDialog#modalclose
      * @type {EventTarget~Event}
      */this.trigger('modalclose');this.conditionalBlur_();if(this.options_.temporary){this.dispose();}};/**
   * Check to see if the `ModalDialog` is closeable via the UI.
   *
   * @param  {boolean} [value]
   *         If given as a boolean, it will set the `closeable` option.
   *
   * @return {boolean}
   *         Returns the final value of the closable option.
   */ModalDialog.prototype.closeable=function closeable(value){if(typeof value==='boolean'){var closeable=this.closeable_=!!value;var close=this.getChild('closeButton');// If this is being made closeable and has no close button, add one.
if(closeable&&!close){// The close button should be a child of the modal - not its
// content element, so temporarily change the content element.
var temp=this.contentEl_;this.contentEl_=this.el_;close=this.addChild('closeButton',{controlText:'Close Modal Dialog'});this.contentEl_=temp;this.on(close,'close',this.close);}// If this is being made uncloseable and has a close button, remove it.
if(!closeable&&close){this.off(close,'close',this.close);this.removeChild(close);close.dispose();}}return this.closeable_;};/**
   * Fill the modal's content element with the modal's "content" option.
   * The content element will be emptied before this change takes place.
   */ModalDialog.prototype.fill=function fill(){this.fillWith(this.content());};/**
   * Fill the modal's content element with arbitrary content.
   * The content element will be emptied before this change takes place.
   *
   * @fires ModalDialog#beforemodalfill
   * @fires ModalDialog#modalfill
   *
   * @param {Mixed} [content]
   *        The same rules apply to this as apply to the `content` option.
   */ModalDialog.prototype.fillWith=function fillWith(content){var contentEl=this.contentEl();var parentEl=contentEl.parentNode;var nextSiblingEl=contentEl.nextSibling;/**
     * Fired just before a `ModalDialog` is filled with content.
     *
     * @event ModalDialog#beforemodalfill
     * @type {EventTarget~Event}
     */this.trigger('beforemodalfill');this.hasBeenFilled_=true;// Detach the content element from the DOM before performing
// manipulation to avoid modifying the live DOM multiple times.
parentEl.removeChild(contentEl);this.empty();insertContent(contentEl,content);/**
     * Fired just after a `ModalDialog` is filled with content.
     *
     * @event ModalDialog#modalfill
     * @type {EventTarget~Event}
     */this.trigger('modalfill');// Re-inject the re-filled content element.
if(nextSiblingEl){parentEl.insertBefore(contentEl,nextSiblingEl);}else {parentEl.appendChild(contentEl);}// make sure that the close button is last in the dialog DOM
var closeButton=this.getChild('closeButton');if(closeButton){parentEl.appendChild(closeButton.el_);}};/**
   * Empties the content element. This happens anytime the modal is filled.
   *
   * @fires ModalDialog#beforemodalempty
   * @fires ModalDialog#modalempty
   */ModalDialog.prototype.empty=function empty(){/**
     * Fired just before a `ModalDialog` is emptied.
     *
     * @event ModalDialog#beforemodalempty
     * @type {EventTarget~Event}
     */this.trigger('beforemodalempty');emptyEl(this.contentEl());/**
     * Fired just after a `ModalDialog` is emptied.
     *
     * @event ModalDialog#modalempty
     * @type {EventTarget~Event}
     */this.trigger('modalempty');};/**
   * Gets or sets the modal content, which gets normalized before being
   * rendered into the DOM.
   *
   * This does not update the DOM or fill the modal, but it is called during
   * that process.
   *
   * @param  {Mixed} [value]
   *         If defined, sets the internal content value to be used on the
   *         next call(s) to `fill`. This value is normalized before being
   *         inserted. To "clear" the internal content value, pass `null`.
   *
   * @return {Mixed}
   *         The current content of the modal dialog
   */ModalDialog.prototype.content=function content(value){if(typeof value!=='undefined'){this.content_=value;}return this.content_;};/**
   * conditionally focus the modal dialog if focus was previously on the player.
   *
   * @private
   */ModalDialog.prototype.conditionalFocus_=function conditionalFocus_(){var activeEl=document$1.activeElement;var playerEl=this.player_.el_;this.previouslyActiveEl_=null;if(playerEl.contains(activeEl)||playerEl===activeEl){this.previouslyActiveEl_=activeEl;this.focus();this.on(document$1,'keydown',this.handleKeyDown);}};/**
   * conditionally blur the element and refocus the last focused element
   *
   * @private
   */ModalDialog.prototype.conditionalBlur_=function conditionalBlur_(){if(this.previouslyActiveEl_){this.previouslyActiveEl_.focus();this.previouslyActiveEl_=null;}this.off(document$1,'keydown',this.handleKeyDown);};/**
   * Keydown handler. Attached when modal is focused.
   *
   * @listens keydown
   */ModalDialog.prototype.handleKeyDown=function handleKeyDown(event){// exit early if it isn't a tab key
if(event.which!==9){return;}var focusableEls=this.focusableEls_();var activeEl=this.el_.querySelector(':focus');var focusIndex=void 0;for(var i=0;i<focusableEls.length;i++){if(activeEl===focusableEls[i]){focusIndex=i;break;}}if(document$1.activeElement===this.el_){focusIndex=0;}if(event.shiftKey&&focusIndex===0){focusableEls[focusableEls.length-1].focus();event.preventDefault();}else if(!event.shiftKey&&focusIndex===focusableEls.length-1){focusableEls[0].focus();event.preventDefault();}};/**
   * get all focusable elements
   *
   * @private
   */ModalDialog.prototype.focusableEls_=function focusableEls_(){var allChildren=this.el_.querySelectorAll('*');return Array.prototype.filter.call(allChildren,function(child){return (child instanceof window$1.HTMLAnchorElement||child instanceof window$1.HTMLAreaElement)&&child.hasAttribute('href')||(child instanceof window$1.HTMLInputElement||child instanceof window$1.HTMLSelectElement||child instanceof window$1.HTMLTextAreaElement||child instanceof window$1.HTMLButtonElement)&&!child.hasAttribute('disabled')||child instanceof window$1.HTMLIFrameElement||child instanceof window$1.HTMLObjectElement||child instanceof window$1.HTMLEmbedElement||child.hasAttribute('tabindex')&&child.getAttribute('tabindex')!==-1||child.hasAttribute('contenteditable');});};return ModalDialog;}(Component);/**
 * Default options for `ModalDialog` default options.
 *
 * @type {Object}
 * @private
 */ModalDialog.prototype.options_={pauseOnOpen:true,temporary:true};Component.registerComponent('ModalDialog',ModalDialog);/**
 * @file track-list.js
 */ /**
 * Common functionaliy between {@link TextTrackList}, {@link AudioTrackList}, and
 * {@link VideoTrackList}
 *
 * @extends EventTarget
 */var TrackList=function(_EventTarget){inherits(TrackList,_EventTarget);/**
   * Create an instance of this class
   *
   * @param {Track[]} tracks
   *        A list of tracks to initialize the list with.
   *
   * @param {Object} [list]
   *        The child object with inheritance done manually for ie8.
   *
   * @abstract
   */function TrackList(){var tracks=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];var _ret;var list=arguments.length>1&&arguments[1]!==undefined?arguments[1]:null;classCallCheck(this,TrackList);var _this=possibleConstructorReturn(this,_EventTarget.call(this));if(!list){list=_this;// eslint-disable-line
if(IS_IE8){list=document$1.createElement('custom');for(var prop in TrackList.prototype){if(prop!=='constructor'){list[prop]=TrackList.prototype[prop];}}}}list.tracks_=[];/**
     * @memberof TrackList
     * @member {number} length
     *         The current number of `Track`s in the this Trackist.
     * @instance
     */Object.defineProperty(list,'length',{get:function get$$1(){return this.tracks_.length;}});for(var i=0;i<tracks.length;i++){list.addTrack(tracks[i]);}// must return the object, as for ie8 it will not be this
// but a reference to a document object
return _ret=list,possibleConstructorReturn(_this,_ret);}/**
   * Add a {@link Track} to the `TrackList`
   *
   * @param {Track} track
   *        The audio, video, or text track to add to the list.
   *
   * @fires TrackList#addtrack
   */TrackList.prototype.addTrack=function addTrack(track){var index=this.tracks_.length;if(!(''+index in this)){Object.defineProperty(this,index,{get:function get$$1(){return this.tracks_[index];}});}// Do not add duplicate tracks
if(this.tracks_.indexOf(track)===-1){this.tracks_.push(track);/**
       * Triggered when a track is added to a track list.
       *
       * @event TrackList#addtrack
       * @type {EventTarget~Event}
       * @property {Track} track
       *           A reference to track that was added.
       */this.trigger({track:track,type:'addtrack'});}};/**
   * Remove a {@link Track} from the `TrackList`
   *
   * @param {Track} rtrack
   *        The audio, video, or text track to remove from the list.
   *
   * @fires TrackList#removetrack
   */TrackList.prototype.removeTrack=function removeTrack(rtrack){var track=void 0;for(var i=0,l=this.length;i<l;i++){if(this[i]===rtrack){track=this[i];if(track.off){track.off();}this.tracks_.splice(i,1);break;}}if(!track){return;}/**
     * Triggered when a track is removed from track list.
     *
     * @event TrackList#removetrack
     * @type {EventTarget~Event}
     * @property {Track} track
     *           A reference to track that was removed.
     */this.trigger({track:track,type:'removetrack'});};/**
   * Get a Track from the TrackList by a tracks id
   *
   * @param {String} id - the id of the track to get
   * @method getTrackById
   * @return {Track}
   * @private
   */TrackList.prototype.getTrackById=function getTrackById(id){var result=null;for(var i=0,l=this.length;i<l;i++){var track=this[i];if(track.id===id){result=track;break;}}return result;};return TrackList;}(EventTarget);/**
 * Triggered when a different track is selected/enabled.
 *
 * @event TrackList#change
 * @type {EventTarget~Event}
 */ /**
 * Events that can be called with on + eventName. See {@link EventHandler}.
 *
 * @property {Object} TrackList#allowedEvents_
 * @private
 */TrackList.prototype.allowedEvents_={change:'change',addtrack:'addtrack',removetrack:'removetrack'};// emulate attribute EventHandler support to allow for feature detection
for(var event in TrackList.prototype.allowedEvents_){TrackList.prototype['on'+event]=null;}/**
 * @file audio-track-list.js
 */ /**
 * Anywhere we call this function we diverge from the spec
 * as we only support one enabled audiotrack at a time
 *
 * @param {AudioTrackList} list
 *        list to work on
 *
 * @param {AudioTrack} track
 *        The track to skip
 *
 * @private
 */var disableOthers=function disableOthers(list,track){for(var i=0;i<list.length;i++){if(!Object.keys(list[i]).length||track.id===list[i].id){continue;}// another audio track is enabled, disable it
list[i].enabled=false;}};/**
 * The current list of {@link AudioTrack} for a media file.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#audiotracklist}
 * @extends TrackList
 */var AudioTrackList=function(_TrackList){inherits(AudioTrackList,_TrackList);/**
   * Create an instance of this class.
   *
   * @param {AudioTrack[]} [tracks=[]]
   *        A list of `AudioTrack` to instantiate the list with.
   */function AudioTrackList(){var _this,_ret;var tracks=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];classCallCheck(this,AudioTrackList);var list=void 0;// make sure only 1 track is enabled
// sorted from last index to first index
for(var i=tracks.length-1;i>=0;i--){if(tracks[i].enabled){disableOthers(tracks,tracks[i]);break;}}// IE8 forces us to implement inheritance ourselves
// as it does not support Object.defineProperty properly
if(IS_IE8){list=document$1.createElement('custom');for(var prop in TrackList.prototype){if(prop!=='constructor'){list[prop]=TrackList.prototype[prop];}}for(var _prop in AudioTrackList.prototype){if(_prop!=='constructor'){list[_prop]=AudioTrackList.prototype[_prop];}}}list=(_this=possibleConstructorReturn(this,_TrackList.call(this,tracks,list)),_this);list.changing_=false;return _ret=list,possibleConstructorReturn(_this,_ret);}/**
   * Add an {@link AudioTrack} to the `AudioTrackList`.
   *
   * @param {AudioTrack} track
   *        The AudioTrack to add to the list
   *
   * @fires TrackList#addtrack
   */AudioTrackList.prototype.addTrack=function addTrack(track){var _this2=this;if(track.enabled){disableOthers(this,track);}_TrackList.prototype.addTrack.call(this,track);// native tracks don't have this
if(!track.addEventListener){return;}/**
     * @listens AudioTrack#enabledchange
     * @fires TrackList#change
     */track.addEventListener('enabledchange',function(){// when we are disabling other tracks (since we don't support
// more than one track at a time) we will set changing_
// to true so that we don't trigger additional change events
if(_this2.changing_){return;}_this2.changing_=true;disableOthers(_this2,track);_this2.changing_=false;_this2.trigger('change');});};return AudioTrackList;}(TrackList);/**
 * @file video-track-list.js
 */ /**
 * Un-select all other {@link VideoTrack}s that are selected.
 *
 * @param {VideoTrackList} list
 *        list to work on
 *
 * @param {VideoTrack} track
 *        The track to skip
 *
 * @private
 */var disableOthers$1=function disableOthers(list,track){for(var i=0;i<list.length;i++){if(!Object.keys(list[i]).length||track.id===list[i].id){continue;}// another video track is enabled, disable it
list[i].selected=false;}};/**
 * The current list of {@link VideoTrack} for a video.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#videotracklist}
 * @extends TrackList
 */var VideoTrackList=function(_TrackList){inherits(VideoTrackList,_TrackList);/**
   * Create an instance of this class.
   *
   * @param {VideoTrack[]} [tracks=[]]
   *        A list of `VideoTrack` to instantiate the list with.
   */function VideoTrackList(){var _this,_ret;var tracks=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];classCallCheck(this,VideoTrackList);var list=void 0;// make sure only 1 track is enabled
// sorted from last index to first index
for(var i=tracks.length-1;i>=0;i--){if(tracks[i].selected){disableOthers$1(tracks,tracks[i]);break;}}// IE8 forces us to implement inheritance ourselves
// as it does not support Object.defineProperty properly
if(IS_IE8){list=document$1.createElement('custom');for(var prop in TrackList.prototype){if(prop!=='constructor'){list[prop]=TrackList.prototype[prop];}}for(var _prop in VideoTrackList.prototype){if(_prop!=='constructor'){list[_prop]=VideoTrackList.prototype[_prop];}}}list=(_this=possibleConstructorReturn(this,_TrackList.call(this,tracks,list)),_this);list.changing_=false;/**
     * @member {number} VideoTrackList#selectedIndex
     *         The current index of the selected {@link VideoTrack`}.
     */Object.defineProperty(list,'selectedIndex',{get:function get$$1(){for(var _i=0;_i<this.length;_i++){if(this[_i].selected){return _i;}}return -1;},set:function set$$1(){}});return _ret=list,possibleConstructorReturn(_this,_ret);}/**
   * Add a {@link VideoTrack} to the `VideoTrackList`.
   *
   * @param {VideoTrack} track
   *        The VideoTrack to add to the list
   *
   * @fires TrackList#addtrack
   */VideoTrackList.prototype.addTrack=function addTrack(track){var _this2=this;if(track.selected){disableOthers$1(this,track);}_TrackList.prototype.addTrack.call(this,track);// native tracks don't have this
if(!track.addEventListener){return;}/**
     * @listens VideoTrack#selectedchange
     * @fires TrackList#change
     */track.addEventListener('selectedchange',function(){if(_this2.changing_){return;}_this2.changing_=true;disableOthers$1(_this2,track);_this2.changing_=false;_this2.trigger('change');});};return VideoTrackList;}(TrackList);/**
 * @file text-track-list.js
 */ /**
 * The current list of {@link TextTrack} for a media file.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#texttracklist}
 * @extends TrackList
 */var TextTrackList=function(_TrackList){inherits(TextTrackList,_TrackList);/**
   * Create an instance of this class.
   *
   * @param {TextTrack[]} [tracks=[]]
   *        A list of `TextTrack` to instantiate the list with.
   */function TextTrackList(){var _this,_ret;var tracks=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];classCallCheck(this,TextTrackList);var list=void 0;// IE8 forces us to implement inheritance ourselves
// as it does not support Object.defineProperty properly
if(IS_IE8){list=document$1.createElement('custom');for(var prop in TrackList.prototype){if(prop!=='constructor'){list[prop]=TrackList.prototype[prop];}}for(var _prop in TextTrackList.prototype){if(_prop!=='constructor'){list[_prop]=TextTrackList.prototype[_prop];}}}list=(_this=possibleConstructorReturn(this,_TrackList.call(this,tracks,list)),_this);return _ret=list,possibleConstructorReturn(_this,_ret);}/**
   * Add a {@link TextTrack} to the `TextTrackList`
   *
   * @param {TextTrack} track
   *        The text track to add to the list.
   *
   * @fires TrackList#addtrack
   */TextTrackList.prototype.addTrack=function addTrack(track){_TrackList.prototype.addTrack.call(this,track);/**
     * @listens TextTrack#modechange
     * @fires TrackList#change
     */track.addEventListener('modechange',bind(this,function(){this.trigger('change');}));var nonLanguageTextTrackKind=['metadata','chapters'];if(nonLanguageTextTrackKind.indexOf(track.kind)===-1){track.addEventListener('modechange',bind(this,function(){this.trigger('selectedlanguagechange');}));}};return TextTrackList;}(TrackList);/**
 * @file html-track-element-list.js
 */ /**
 * The current list of {@link HtmlTrackElement}s.
 */var HtmlTrackElementList=function(){/**
   * Create an instance of this class.
   *
   * @param {HtmlTrackElement[]} [tracks=[]]
   *        A list of `HtmlTrackElement` to instantiate the list with.
   */function HtmlTrackElementList(){var trackElements=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];classCallCheck(this,HtmlTrackElementList);var list=this;// eslint-disable-line
if(IS_IE8){list=document$1.createElement('custom');for(var prop in HtmlTrackElementList.prototype){if(prop!=='constructor'){list[prop]=HtmlTrackElementList.prototype[prop];}}}list.trackElements_=[];/**
     * @memberof HtmlTrackElementList
     * @member {number} length
     *         The current number of `Track`s in the this Trackist.
     * @instance
     */Object.defineProperty(list,'length',{get:function get$$1(){return this.trackElements_.length;}});for(var i=0,length=trackElements.length;i<length;i++){list.addTrackElement_(trackElements[i]);}if(IS_IE8){return list;}}/**
   * Add an {@link HtmlTrackElement} to the `HtmlTrackElementList`
   *
   * @param {HtmlTrackElement} trackElement
   *        The track element to add to the list.
   *
   * @private
   */HtmlTrackElementList.prototype.addTrackElement_=function addTrackElement_(trackElement){var index=this.trackElements_.length;if(!(''+index in this)){Object.defineProperty(this,index,{get:function get$$1(){return this.trackElements_[index];}});}// Do not add duplicate elements
if(this.trackElements_.indexOf(trackElement)===-1){this.trackElements_.push(trackElement);}};/**
   * Get an {@link HtmlTrackElement} from the `HtmlTrackElementList` given an
   * {@link TextTrack}.
   *
   * @param {TextTrack} track
   *        The track associated with a track element.
   *
   * @return {HtmlTrackElement|undefined}
   *         The track element that was found or undefined.
   *
   * @private
   */HtmlTrackElementList.prototype.getTrackElementByTrack_=function getTrackElementByTrack_(track){var trackElement_=void 0;for(var i=0,length=this.trackElements_.length;i<length;i++){if(track===this.trackElements_[i].track){trackElement_=this.trackElements_[i];break;}}return trackElement_;};/**
   * Remove a {@link HtmlTrackElement} from the `HtmlTrackElementList`
   *
   * @param {HtmlTrackElement} trackElement
   *        The track element to remove from the list.
   *
   * @private
   */HtmlTrackElementList.prototype.removeTrackElement_=function removeTrackElement_(trackElement){for(var i=0,length=this.trackElements_.length;i<length;i++){if(trackElement===this.trackElements_[i]){this.trackElements_.splice(i,1);break;}}};return HtmlTrackElementList;}();/**
 * @file text-track-cue-list.js
 */ /**
 * @typedef {Object} TextTrackCueList~TextTrackCue
 *
 * @property {string} id
 *           The unique id for this text track cue
 *
 * @property {number} startTime
 *           The start time for this text track cue
 *
 * @property {number} endTime
 *           The end time for this text track cue
 *
 * @property {boolean} pauseOnExit
 *           Pause when the end time is reached if true.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#texttrackcue}
 */ /**
 * A List of TextTrackCues.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#texttrackcuelist}
 */var TextTrackCueList=function(){/**
   * Create an instance of this class..
   *
   * @param {Array} cues
   *        A list of cues to be initialized with
   */function TextTrackCueList(cues){classCallCheck(this,TextTrackCueList);var list=this;// eslint-disable-line
if(IS_IE8){list=document$1.createElement('custom');for(var prop in TextTrackCueList.prototype){if(prop!=='constructor'){list[prop]=TextTrackCueList.prototype[prop];}}}TextTrackCueList.prototype.setCues_.call(list,cues);/**
     * @memberof TextTrackCueList
     * @member {number} length
     *         The current number of `TextTrackCue`s in the TextTrackCueList.
     * @instance
     */Object.defineProperty(list,'length',{get:function get$$1(){return this.length_;}});if(IS_IE8){return list;}}/**
   * A setter for cues in this list. Creates getters
   * an an index for the cues.
   *
   * @param {Array} cues
   *        An array of cues to set
   *
   * @private
   */TextTrackCueList.prototype.setCues_=function setCues_(cues){var oldLength=this.length||0;var i=0;var l=cues.length;this.cues_=cues;this.length_=cues.length;var defineProp=function defineProp(index){if(!(''+index in this)){Object.defineProperty(this,''+index,{get:function get$$1(){return this.cues_[index];}});}};if(oldLength<l){i=oldLength;for(;i<l;i++){defineProp.call(this,i);}}};/**
   * Get a `TextTrackCue` that is currently in the `TextTrackCueList` by id.
   *
   * @param {string} id
   *        The id of the cue that should be searched for.
   *
   * @return {TextTrackCueList~TextTrackCue|null}
   *         A single cue or null if none was found.
   */TextTrackCueList.prototype.getCueById=function getCueById(id){var result=null;for(var i=0,l=this.length;i<l;i++){var cue=this[i];if(cue.id===id){result=cue;break;}}return result;};return TextTrackCueList;}();/**
 * @file track-kinds.js
 */ /**
 * All possible `VideoTrackKind`s
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-videotrack-kind
 * @typedef VideoTrack~Kind
 * @enum
 */var VideoTrackKind={alternative:'alternative',captions:'captions',main:'main',sign:'sign',subtitles:'subtitles',commentary:'commentary'};/**
 * All possible `AudioTrackKind`s
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-audiotrack-kind
 * @typedef AudioTrack~Kind
 * @enum
 */var AudioTrackKind={'alternative':'alternative','descriptions':'descriptions','main':'main','main-desc':'main-desc','translation':'translation','commentary':'commentary'};/**
 * All possible `TextTrackKind`s
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-texttrack-kind
 * @typedef TextTrack~Kind
 * @enum
 */var TextTrackKind={subtitles:'subtitles',captions:'captions',descriptions:'descriptions',chapters:'chapters',metadata:'metadata'};/**
 * All possible `TextTrackMode`s
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#texttrackmode
 * @typedef TextTrack~Mode
 * @enum
 */var TextTrackMode={disabled:'disabled',hidden:'hidden',showing:'showing'};/**
 * @file track.js
 */ /**
 * A Track class that contains all of the common functionality for {@link AudioTrack},
 * {@link VideoTrack}, and {@link TextTrack}.
 *
 * > Note: This class should not be used directly
 *
 * @see {@link https://html.spec.whatwg.org/multipage/embedded-content.html}
 * @extends EventTarget
 * @abstract
 */var Track=function(_EventTarget){inherits(Track,_EventTarget);/**
   * Create an instance of this class.
   *
   * @param {Object} [options={}]
   *        Object of option names and values
   *
   * @param {string} [options.kind='']
   *        A valid kind for the track type you are creating.
   *
   * @param {string} [options.id='vjs_track_' + Guid.newGUID()]
   *        A unique id for this AudioTrack.
   *
   * @param {string} [options.label='']
   *        The menu label for this track.
   *
   * @param {string} [options.language='']
   *        A valid two character language code.
   *
   * @abstract
   */function Track(){var _ret;var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};classCallCheck(this,Track);var _this=possibleConstructorReturn(this,_EventTarget.call(this));var track=_this;// eslint-disable-line
if(IS_IE8){track=document$1.createElement('custom');for(var prop in Track.prototype){if(prop!=='constructor'){track[prop]=Track.prototype[prop];}}}var trackProps={id:options.id||'vjs_track_'+newGUID(),kind:options.kind||'',label:options.label||'',language:options.language||''};/**
     * @memberof Track
     * @member {string} id
     *         The id of this track. Cannot be changed after creation.
     * @instance
     *
     * @readonly
     */ /**
     * @memberof Track
     * @member {string} kind
     *         The kind of track that this is. Cannot be changed after creation.
     * @instance
     *
     * @readonly
     */ /**
     * @memberof Track
     * @member {string} label
     *         The label of this track. Cannot be changed after creation.
     * @instance
     *
     * @readonly
     */ /**
     * @memberof Track
     * @member {string} language
     *         The two letter language code for this track. Cannot be changed after
     *         creation.
     * @instance
     *
     * @readonly
     */var _loop=function _loop(key){Object.defineProperty(track,key,{get:function get$$1(){return trackProps[key];},set:function set$$1(){}});};for(var key in trackProps){_loop(key);}return _ret=track,possibleConstructorReturn(_this,_ret);}return Track;}(EventTarget);/**
 * @file url.js
 * @module url
 */ /**
 * @typedef {Object} url:URLObject
 *
 * @property {string} protocol
 *           The protocol of the url that was parsed.
 *
 * @property {string} hostname
 *           The hostname of the url that was parsed.
 *
 * @property {string} port
 *           The port of the url that was parsed.
 *
 * @property {string} pathname
 *           The pathname of the url that was parsed.
 *
 * @property {string} search
 *           The search query of the url that was parsed.
 *
 * @property {string} hash
 *           The hash of the url that was parsed.
 *
 * @property {string} host
 *           The host of the url that was parsed.
 */ /**
 * Resolve and parse the elements of a URL.
 *
 * @param  {String} url
 *         The url to parse
 *
 * @return {url:URLObject}
 *         An object of url details
 */var parseUrl=function parseUrl(url){var props=['protocol','hostname','port','pathname','search','hash','host'];// add the url to an anchor and let the browser parse the URL
var a=document$1.createElement('a');a.href=url;// IE8 (and 9?) Fix
// ie8 doesn't parse the URL correctly until the anchor is actually
// added to the body, and an innerHTML is needed to trigger the parsing
var addToBody=a.host===''&&a.protocol!=='file:';var div=void 0;if(addToBody){div=document$1.createElement('div');div.innerHTML='<a href="'+url+'"></a>';a=div.firstChild;// prevent the div from affecting layout
div.setAttribute('style','display:none; position:absolute;');document$1.body.appendChild(div);}// Copy the specific URL properties to a new object
// This is also needed for IE8 because the anchor loses its
// properties when it's removed from the dom
var details={};for(var i=0;i<props.length;i++){details[props[i]]=a[props[i]];}// IE9 adds the port to the host property unlike everyone else. If
// a port identifier is added for standard ports, strip it.
if(details.protocol==='http:'){details.host=details.host.replace(/:80$/,'');}if(details.protocol==='https:'){details.host=details.host.replace(/:443$/,'');}if(!details.protocol){details.protocol=window$1.location.protocol;}if(addToBody){document$1.body.removeChild(div);}return details;};/**
 * Get absolute version of relative URL. Used to tell flash correct URL.
 *
 *
 * @param  {string} url
 *         URL to make absolute
 *
 * @return {string}
 *         Absolute URL
 *
 * @see http://stackoverflow.com/questions/470832/getting-an-absolute-url-from-a-relative-one-ie6-issue
 */var getAbsoluteURL=function getAbsoluteURL(url){// Check if absolute URL
if(!url.match(/^https?:\/\//)){// Convert to absolute URL. Flash hosted off-site needs an absolute URL.
var div=document$1.createElement('div');div.innerHTML='<a href="'+url+'">x</a>';url=div.firstChild.href;}return url;};/**
 * Returns the extension of the passed file name. It will return an empty string
 * if passed an invalid path.
 *
 * @param {string} path
 *        The fileName path like '/path/to/file.mp4'
 *
 * @returns {string}
 *          The extension in lower case or an empty string if no
 *          extension could be found.
 */var getFileExtension=function getFileExtension(path){if(typeof path==='string'){var splitPathRe=/^(\/?)([\s\S]*?)((?:\.{1,2}|[^\/]+?)(\.([^\.\/\?]+)))(?:[\/]*|[\?].*)$/i;var pathParts=splitPathRe.exec(path);if(pathParts){return pathParts.pop().toLowerCase();}}return '';};/**
 * Returns whether the url passed is a cross domain request or not.
 *
 * @param {string} url
 *        The url to check.
 *
 * @return {boolean}
 *         Whether it is a cross domain request or not.
 */var isCrossOrigin=function isCrossOrigin(url){var winLoc=window$1.location;var urlInfo=parseUrl(url);// IE8 protocol relative urls will return ':' for protocol
var srcProtocol=urlInfo.protocol===':'?winLoc.protocol:urlInfo.protocol;// Check if url is for another domain/origin
// IE8 doesn't know location.origin, so we won't rely on it here
var crossOrigin=srcProtocol+urlInfo.host!==winLoc.protocol+winLoc.host;return crossOrigin;};var Url=(Object.freeze||Object)({parseUrl:parseUrl,getAbsoluteURL:getAbsoluteURL,getFileExtension:getFileExtension,isCrossOrigin:isCrossOrigin});/**
 * @file text-track.js
 */ /**
 * Takes a webvtt file contents and parses it into cues
 *
 * @param {string} srcContent
 *        webVTT file contents
 *
 * @param {TextTrack} track
 *        TextTrack to add cues to. Cues come from the srcContent.
 *
 * @private
 */var parseCues=function parseCues(srcContent,track){var parser=new window$1.WebVTT.Parser(window$1,window$1.vttjs,window$1.WebVTT.StringDecoder());var errors=[];parser.oncue=function(cue){track.addCue(cue);};parser.onparsingerror=function(error){errors.push(error);};parser.onflush=function(){track.trigger({type:'loadeddata',target:track});};parser.parse(srcContent);if(errors.length>0){if(window$1.console&&window$1.console.groupCollapsed){window$1.console.groupCollapsed('Text Track parsing errors for '+track.src);}errors.forEach(function(error){return log$1.error(error);});if(window$1.console&&window$1.console.groupEnd){window$1.console.groupEnd();}}parser.flush();};/**
 * Load a `TextTrack` from a specifed url.
 *
 * @param {string} src
 *        Url to load track from.
 *
 * @param {TextTrack} track
 *        Track to add cues to. Comes from the content at the end of `url`.
 *
 * @private
 */var loadTrack=function loadTrack(src,track){var opts={uri:src};var crossOrigin=isCrossOrigin(src);if(crossOrigin){opts.cors=crossOrigin;}xhr(opts,bind(this,function(err,response,responseBody){if(err){return log$1.error(err,response);}track.loaded_=true;// Make sure that vttjs has loaded, otherwise, wait till it finished loading
// NOTE: this is only used for the alt/video.novtt.js build
if(typeof window$1.WebVTT!=='function'){if(track.tech_){var loadHandler=function loadHandler(){return parseCues(responseBody,track);};track.tech_.on('vttjsloaded',loadHandler);track.tech_.on('vttjserror',function(){log$1.error('vttjs failed to load, stopping trying to process '+track.src);track.tech_.off('vttjsloaded',loadHandler);});}}else {parseCues(responseBody,track);}}));};/**
 * A representation of a single `TextTrack`.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#texttrack}
 * @extends Track
 */var TextTrack=function(_Track){inherits(TextTrack,_Track);/**
   * Create an instance of this class.
   *
   * @param {Object} options={}
   *        Object of option names and values
   *
   * @param {Tech} options.tech
   *        A reference to the tech that owns this TextTrack.
   *
   * @param {TextTrack~Kind} [options.kind='subtitles']
   *        A valid text track kind.
   *
   * @param {TextTrack~Mode} [options.mode='disabled']
   *        A valid text track mode.
   *
   * @param {string} [options.id='vjs_track_' + Guid.newGUID()]
   *        A unique id for this TextTrack.
   *
   * @param {string} [options.label='']
   *        The menu label for this track.
   *
   * @param {string} [options.language='']
   *        A valid two character language code.
   *
   * @param {string} [options.srclang='']
   *        A valid two character language code. An alternative, but deprioritized
   *        vesion of `options.language`
   *
   * @param {string} [options.src]
   *        A url to TextTrack cues.
   *
   * @param {boolean} [options.default]
   *        If this track should default to on or off.
   */function TextTrack(){var _this,_ret;var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};classCallCheck(this,TextTrack);if(!options.tech){throw new Error('A tech was not provided.');}var settings=mergeOptions(options,{kind:TextTrackKind[options.kind]||'subtitles',language:options.language||options.srclang||''});var mode=TextTrackMode[settings.mode]||'disabled';var default_=settings['default'];if(settings.kind==='metadata'||settings.kind==='chapters'){mode='hidden';}// on IE8 this will be a document element
// for every other browser this will be a normal object
var tt=(_this=possibleConstructorReturn(this,_Track.call(this,settings)),_this);tt.tech_=settings.tech;if(IS_IE8){for(var prop in TextTrack.prototype){if(prop!=='constructor'){tt[prop]=TextTrack.prototype[prop];}}}tt.cues_=[];tt.activeCues_=[];var cues=new TextTrackCueList(tt.cues_);var activeCues=new TextTrackCueList(tt.activeCues_);var changed=false;var timeupdateHandler=bind(tt,function(){// Accessing this.activeCues for the side-effects of updating itself
// due to it's nature as a getter function. Do not remove or cues will
// stop updating!
// Use the setter to prevent deletion from uglify (pure_getters rule)
this.activeCues=this.activeCues;if(changed){this.trigger('cuechange');changed=false;}});if(mode!=='disabled'){tt.tech_.ready(function(){tt.tech_.on('timeupdate',timeupdateHandler);},true);}/**
     * @memberof TextTrack
     * @member {boolean} default
     *         If this track was set to be on or off by default. Cannot be changed after
     *         creation.
     * @instance
     *
     * @readonly
     */Object.defineProperty(tt,'default',{get:function get$$1(){return default_;},set:function set$$1(){}});/**
     * @memberof TextTrack
     * @member {string} mode
     *         Set the mode of this TextTrack to a valid {@link TextTrack~Mode}. Will
     *         not be set if setting to an invalid mode.
     * @instance
     *
     * @fires TextTrack#modechange
     */Object.defineProperty(tt,'mode',{get:function get$$1(){return mode;},set:function set$$1(newMode){var _this2=this;if(!TextTrackMode[newMode]){return;}mode=newMode;if(mode!=='disabled'){this.tech_.ready(function(){_this2.tech_.on('timeupdate',timeupdateHandler);},true);}else {this.tech_.off('timeupdate',timeupdateHandler);}/**
         * An event that fires when mode changes on this track. This allows
         * the TextTrackList that holds this track to act accordingly.
         *
         * > Note: This is not part of the spec!
         *
         * @event TextTrack#modechange
         * @type {EventTarget~Event}
         */this.trigger('modechange');}});/**
     * @memberof TextTrack
     * @member {TextTrackCueList} cues
     *         The text track cue list for this TextTrack.
     * @instance
     */Object.defineProperty(tt,'cues',{get:function get$$1(){if(!this.loaded_){return null;}return cues;},set:function set$$1(){}});/**
     * @memberof TextTrack
     * @member {TextTrackCueList} activeCues
     *         The list text track cues that are currently active for this TextTrack.
     * @instance
     */Object.defineProperty(tt,'activeCues',{get:function get$$1(){if(!this.loaded_){return null;}// nothing to do
if(this.cues.length===0){return activeCues;}var ct=this.tech_.currentTime();var active=[];for(var i=0,l=this.cues.length;i<l;i++){var cue=this.cues[i];if(cue.startTime<=ct&&cue.endTime>=ct){active.push(cue);}else if(cue.startTime===cue.endTime&&cue.startTime<=ct&&cue.startTime+0.5>=ct){active.push(cue);}}changed=false;if(active.length!==this.activeCues_.length){changed=true;}else {for(var _i=0;_i<active.length;_i++){if(this.activeCues_.indexOf(active[_i])===-1){changed=true;}}}this.activeCues_=active;activeCues.setCues_(this.activeCues_);return activeCues;},// /!\ Keep this setter empty (see the timeupdate handler above)
set:function set$$1(){}});if(settings.src){tt.src=settings.src;loadTrack(settings.src,tt);}else {tt.loaded_=true;}return _ret=tt,possibleConstructorReturn(_this,_ret);}/**
   * Add a cue to the internal list of cues.
   *
   * @param {TextTrack~Cue} cue
   *        The cue to add to our internal list
   */TextTrack.prototype.addCue=function addCue(originalCue){var cue=originalCue;if(window$1.vttjs&&!(originalCue instanceof window$1.vttjs.VTTCue)){cue=new window$1.vttjs.VTTCue(originalCue.startTime,originalCue.endTime,originalCue.text);for(var prop in originalCue){if(!(prop in cue)){cue[prop]=originalCue[prop];}}// make sure that `id` is copied over
cue.id=originalCue.id;cue.originalCue_=originalCue;}var tracks=this.tech_.textTracks();for(var i=0;i<tracks.length;i++){if(tracks[i]!==this){tracks[i].removeCue(cue);}}this.cues_.push(cue);this.cues.setCues_(this.cues_);};/**
   * Remove a cue from our internal list
   *
   * @param {TextTrack~Cue} removeCue
   *        The cue to remove from our internal list
   */TextTrack.prototype.removeCue=function removeCue(_removeCue){var i=this.cues_.length;while(i--){var cue=this.cues_[i];if(cue===_removeCue||cue.originalCue_&&cue.originalCue_===_removeCue){this.cues_.splice(i,1);this.cues.setCues_(this.cues_);break;}}};return TextTrack;}(Track);/**
 * cuechange - One or more cues in the track have become active or stopped being active.
 */TextTrack.prototype.allowedEvents_={cuechange:'cuechange'};/**
 * A representation of a single `AudioTrack`. If it is part of an {@link AudioTrackList}
 * only one `AudioTrack` in the list will be enabled at a time.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#audiotrack}
 * @extends Track
 */var AudioTrack=function(_Track){inherits(AudioTrack,_Track);/**
   * Create an instance of this class.
   *
   * @param {Object} [options={}]
   *        Object of option names and values
   *
   * @param {AudioTrack~Kind} [options.kind='']
   *        A valid audio track kind
   *
   * @param {string} [options.id='vjs_track_' + Guid.newGUID()]
   *        A unique id for this AudioTrack.
   *
   * @param {string} [options.label='']
   *        The menu label for this track.
   *
   * @param {string} [options.language='']
   *        A valid two character language code.
   *
   * @param {boolean} [options.enabled]
   *        If this track is the one that is currently playing. If this track is part of
   *        an {@link AudioTrackList}, only one {@link AudioTrack} will be enabled.
   */function AudioTrack(){var _this,_ret;var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};classCallCheck(this,AudioTrack);var settings=mergeOptions(options,{kind:AudioTrackKind[options.kind]||''});// on IE8 this will be a document element
// for every other browser this will be a normal object
var track=(_this=possibleConstructorReturn(this,_Track.call(this,settings)),_this);var enabled=false;if(IS_IE8){for(var prop in AudioTrack.prototype){if(prop!=='constructor'){track[prop]=AudioTrack.prototype[prop];}}}/**
     * @memberof AudioTrack
     * @member {boolean} enabled
     *         If this `AudioTrack` is enabled or not. When setting this will
     *         fire {@link AudioTrack#enabledchange} if the state of enabled is changed.
     * @instance
     *
     * @fires VideoTrack#selectedchange
     */Object.defineProperty(track,'enabled',{get:function get$$1(){return enabled;},set:function set$$1(newEnabled){// an invalid or unchanged value
if(typeof newEnabled!=='boolean'||newEnabled===enabled){return;}enabled=newEnabled;/**
         * An event that fires when enabled changes on this track. This allows
         * the AudioTrackList that holds this track to act accordingly.
         *
         * > Note: This is not part of the spec! Native tracks will do
         *         this internally without an event.
         *
         * @event AudioTrack#enabledchange
         * @type {EventTarget~Event}
         */this.trigger('enabledchange');}});// if the user sets this track to selected then
// set selected to that true value otherwise
// we keep it false
if(settings.enabled){track.enabled=settings.enabled;}track.loaded_=true;return _ret=track,possibleConstructorReturn(_this,_ret);}return AudioTrack;}(Track);/**
 * A representation of a single `VideoTrack`.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#videotrack}
 * @extends Track
 */var VideoTrack=function(_Track){inherits(VideoTrack,_Track);/**
   * Create an instance of this class.
   *
   * @param {Object} [options={}]
   *        Object of option names and values
   *
   * @param {string} [options.kind='']
   *        A valid {@link VideoTrack~Kind}
   *
   * @param {string} [options.id='vjs_track_' + Guid.newGUID()]
   *        A unique id for this AudioTrack.
   *
   * @param {string} [options.label='']
   *        The menu label for this track.
   *
   * @param {string} [options.language='']
   *        A valid two character language code.
   *
   * @param {boolean} [options.selected]
   *        If this track is the one that is currently playing.
   */function VideoTrack(){var _this,_ret;var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};classCallCheck(this,VideoTrack);var settings=mergeOptions(options,{kind:VideoTrackKind[options.kind]||''});// on IE8 this will be a document element
// for every other browser this will be a normal object
var track=(_this=possibleConstructorReturn(this,_Track.call(this,settings)),_this);var selected=false;if(IS_IE8){for(var prop in VideoTrack.prototype){if(prop!=='constructor'){track[prop]=VideoTrack.prototype[prop];}}}/**
     * @memberof VideoTrack
     * @member {boolean} selected
     *         If this `VideoTrack` is selected or not. When setting this will
     *         fire {@link VideoTrack#selectedchange} if the state of selected changed.
     * @instance
     *
     * @fires VideoTrack#selectedchange
     */Object.defineProperty(track,'selected',{get:function get$$1(){return selected;},set:function set$$1(newSelected){// an invalid or unchanged value
if(typeof newSelected!=='boolean'||newSelected===selected){return;}selected=newSelected;/**
         * An event that fires when selected changes on this track. This allows
         * the VideoTrackList that holds this track to act accordingly.
         *
         * > Note: This is not part of the spec! Native tracks will do
         *         this internally without an event.
         *
         * @event VideoTrack#selectedchange
         * @type {EventTarget~Event}
         */this.trigger('selectedchange');}});// if the user sets this track to selected then
// set selected to that true value otherwise
// we keep it false
if(settings.selected){track.selected=settings.selected;}return _ret=track,possibleConstructorReturn(_this,_ret);}return VideoTrack;}(Track);/**
 * @file html-track-element.js
 */ /**
 * @memberof HTMLTrackElement
 * @typedef {HTMLTrackElement~ReadyState}
 * @enum {number}
 */var NONE=0;var LOADING=1;var LOADED=2;var ERROR=3;/**
 * A single track represented in the DOM.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#htmltrackelement}
 * @extends EventTarget
 */var HTMLTrackElement=function(_EventTarget){inherits(HTMLTrackElement,_EventTarget);/**
   * Create an instance of this class.
   *
   * @param {Object} options={}
   *        Object of option names and values
   *
   * @param {Tech} options.tech
   *        A reference to the tech that owns this HTMLTrackElement.
   *
   * @param {TextTrack~Kind} [options.kind='subtitles']
   *        A valid text track kind.
   *
   * @param {TextTrack~Mode} [options.mode='disabled']
   *        A valid text track mode.
   *
   * @param {string} [options.id='vjs_track_' + Guid.newGUID()]
   *        A unique id for this TextTrack.
   *
   * @param {string} [options.label='']
   *        The menu label for this track.
   *
   * @param {string} [options.language='']
   *        A valid two character language code.
   *
   * @param {string} [options.srclang='']
   *        A valid two character language code. An alternative, but deprioritized
   *        vesion of `options.language`
   *
   * @param {string} [options.src]
   *        A url to TextTrack cues.
   *
   * @param {boolean} [options.default]
   *        If this track should default to on or off.
   */function HTMLTrackElement(){var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};classCallCheck(this,HTMLTrackElement);var _this=possibleConstructorReturn(this,_EventTarget.call(this));var readyState=void 0;var trackElement=_this;// eslint-disable-line
if(IS_IE8){trackElement=document$1.createElement('custom');for(var prop in HTMLTrackElement.prototype){if(prop!=='constructor'){trackElement[prop]=HTMLTrackElement.prototype[prop];}}}var track=new TextTrack(options);trackElement.kind=track.kind;trackElement.src=track.src;trackElement.srclang=track.language;trackElement.label=track.label;trackElement['default']=track['default'];/**
     * @memberof HTMLTrackElement
     * @member {HTMLTrackElement~ReadyState} readyState
     *         The current ready state of the track element.
     * @instance
     */Object.defineProperty(trackElement,'readyState',{get:function get$$1(){return readyState;}});/**
     * @memberof HTMLTrackElement
     * @member {TextTrack} track
     *         The underlying TextTrack object.
     * @instance
     *
     */Object.defineProperty(trackElement,'track',{get:function get$$1(){return track;}});readyState=NONE;/**
     * @listens TextTrack#loadeddata
     * @fires HTMLTrackElement#load
     */track.addEventListener('loadeddata',function(){readyState=LOADED;trackElement.trigger({type:'load',target:trackElement});});if(IS_IE8){var _ret;return _ret=trackElement,possibleConstructorReturn(_this,_ret);}return _this;}return HTMLTrackElement;}(EventTarget);HTMLTrackElement.prototype.allowedEvents_={load:'load'};HTMLTrackElement.NONE=NONE;HTMLTrackElement.LOADING=LOADING;HTMLTrackElement.LOADED=LOADED;HTMLTrackElement.ERROR=ERROR;/*
 * This file contains all track properties that are used in
 * player.js, tech.js, html5.js and possibly other techs in the future.
 */var NORMAL={audio:{ListClass:AudioTrackList,TrackClass:AudioTrack,capitalName:'Audio'},video:{ListClass:VideoTrackList,TrackClass:VideoTrack,capitalName:'Video'},text:{ListClass:TextTrackList,TrackClass:TextTrack,capitalName:'Text'}};Object.keys(NORMAL).forEach(function(type){NORMAL[type].getterName=type+'Tracks';NORMAL[type].privateName=type+'Tracks_';});var REMOTE={remoteText:{ListClass:TextTrackList,TrackClass:TextTrack,capitalName:'RemoteText',getterName:'remoteTextTracks',privateName:'remoteTextTracks_'},remoteTextEl:{ListClass:HtmlTrackElementList,TrackClass:HTMLTrackElement,capitalName:'RemoteTextTrackEls',getterName:'remoteTextTrackEls',privateName:'remoteTextTrackEls_'}};var ALL=mergeOptions(NORMAL,REMOTE);REMOTE.names=Object.keys(REMOTE);NORMAL.names=Object.keys(NORMAL);ALL.names=[].concat(REMOTE.names).concat(NORMAL.names);/**
 * @file tech.js
 */ /**
 * An Object containing a structure like: `{src: 'url', type: 'mimetype'}` or string
 * that just contains the src url alone.
 * * `var SourceObject = {src: 'http://ex.com/video.mp4', type: 'video/mp4'};`
   * `var SourceString = 'http://example.com/some-video.mp4';`
 *
 * @typedef {Object|string} Tech~SourceObject
 *
 * @property {string} src
 *           The url to the source
 *
 * @property {string} type
 *           The mime type of the source
 */ /**
 * A function used by {@link Tech} to create a new {@link TextTrack}.
 *
 * @private
 *
 * @param {Tech} self
 *        An instance of the Tech class.
 *
 * @param {string} kind
 *        `TextTrack` kind (subtitles, captions, descriptions, chapters, or metadata)
 *
 * @param {string} [label]
 *        Label to identify the text track
 *
 * @param {string} [language]
 *        Two letter language abbreviation
 *
 * @param {Object} [options={}]
 *        An object with additional text track options
 *
 * @return {TextTrack}
 *          The text track that was created.
 */function createTrackHelper(self,kind,label,language){var options=arguments.length>4&&arguments[4]!==undefined?arguments[4]:{};var tracks=self.textTracks();options.kind=kind;if(label){options.label=label;}if(language){options.language=language;}options.tech=self;var track=new ALL.text.TrackClass(options);tracks.addTrack(track);return track;}/**
 * This is the base class for media playback technology controllers, such as
 * {@link Flash} and {@link HTML5}
 *
 * @extends Component
 */var Tech=function(_Component){inherits(Tech,_Component);/**
   * Create an instance of this Tech.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} ready
   *        Callback function to call when the `HTML5` Tech is ready.
   */function Tech(){var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};var ready=arguments.length>1&&arguments[1]!==undefined?arguments[1]:function(){};classCallCheck(this,Tech);// we don't want the tech to report user activity automatically.
// This is done manually in addControlsListeners
options.reportTouchActivity=false;// keep track of whether the current source has played at all to
// implement a very limited played()
var _this=possibleConstructorReturn(this,_Component.call(this,null,options,ready));_this.hasStarted_=false;_this.on('playing',function(){this.hasStarted_=true;});_this.on('loadstart',function(){this.hasStarted_=false;});ALL.names.forEach(function(name){var props=ALL[name];if(options&&options[props.getterName]){_this[props.privateName]=options[props.getterName];}});// Manually track progress in cases where the browser/flash player doesn't report it.
if(!_this.featuresProgressEvents){_this.manualProgressOn();}// Manually track timeupdates in cases where the browser/flash player doesn't report it.
if(!_this.featuresTimeupdateEvents){_this.manualTimeUpdatesOn();}['Text','Audio','Video'].forEach(function(track){if(options['native'+track+'Tracks']===false){_this['featuresNative'+track+'Tracks']=false;}});if(options.nativeCaptions===false||options.nativeTextTracks===false){_this.featuresNativeTextTracks=false;}else if(options.nativeCaptions===true||options.nativeTextTracks===true){_this.featuresNativeTextTracks=true;}if(!_this.featuresNativeTextTracks){_this.emulateTextTracks();}_this.autoRemoteTextTracks_=new ALL.text.ListClass();_this.initTrackListeners();// Turn on component tap events only if not using native controls
if(!options.nativeControlsForTouch){_this.emitTapEvents();}if(_this.constructor){_this.name_=_this.constructor.name||'Unknown Tech';}return _this;}/**
   * A special function to trigger source set in a way that will allow player
   * to re-trigger if the player or tech are not ready yet.
   *
   * @fires Tech#sourceset
   * @param {string} src The source string at the time of the source changing.
   */Tech.prototype.triggerSourceset=function triggerSourceset(src){var _this2=this;if(!this.isReady_){// on initial ready we have to trigger source set
// 1ms after ready so that player can watch for it.
this.one('ready',function(){return _this2.setTimeout(function(){return _this2.triggerSourceset(src);},1);});}/**
     * Fired when the source is set on the tech causing the media element
     * to reload.
     *
     * @see {@link Player#event:sourceset}
     * @event Tech#sourceset
     * @type {EventTarget~Event}
     */this.trigger({src:src,type:'sourceset'});};/* Fallbacks for unsupported event types
  ================================================================================ */ /**
   * Polyfill the `progress` event for browsers that don't support it natively.
   *
   * @see {@link Tech#trackProgress}
   */Tech.prototype.manualProgressOn=function manualProgressOn(){this.on('durationchange',this.onDurationChange);this.manualProgress=true;// Trigger progress watching when a source begins loading
this.one('ready',this.trackProgress);};/**
   * Turn off the polyfill for `progress` events that was created in
   * {@link Tech#manualProgressOn}
   */Tech.prototype.manualProgressOff=function manualProgressOff(){this.manualProgress=false;this.stopTrackingProgress();this.off('durationchange',this.onDurationChange);};/**
   * This is used to trigger a `progress` event when the buffered percent changes. It
   * sets an interval function that will be called every 500 milliseconds to check if the
   * buffer end percent has changed.
   *
   * > This function is called by {@link Tech#manualProgressOn}
   *
   * @param {EventTarget~Event} event
   *        The `ready` event that caused this to run.
   *
   * @listens Tech#ready
   * @fires Tech#progress
   */Tech.prototype.trackProgress=function trackProgress(event){this.stopTrackingProgress();this.progressInterval=this.setInterval(bind(this,function(){// Don't trigger unless buffered amount is greater than last time
var numBufferedPercent=this.bufferedPercent();if(this.bufferedPercent_!==numBufferedPercent){/**
         * See {@link Player#progress}
         *
         * @event Tech#progress
         * @type {EventTarget~Event}
         */this.trigger('progress');}this.bufferedPercent_=numBufferedPercent;if(numBufferedPercent===1){this.stopTrackingProgress();}}),500);};/**
   * Update our internal duration on a `durationchange` event by calling
   * {@link Tech#duration}.
   *
   * @param {EventTarget~Event} event
   *        The `durationchange` event that caused this to run.
   *
   * @listens Tech#durationchange
   */Tech.prototype.onDurationChange=function onDurationChange(event){this.duration_=this.duration();};/**
   * Get and create a `TimeRange` object for buffering.
   *
   * @return {TimeRange}
   *         The time range object that was created.
   */Tech.prototype.buffered=function buffered(){return createTimeRanges(0,0);};/**
   * Get the percentage of the current video that is currently buffered.
   *
   * @return {number}
   *         A number from 0 to 1 that represents the decimal percentage of the
   *         video that is buffered.
   *
   */Tech.prototype.bufferedPercent=function bufferedPercent$$1(){return bufferedPercent(this.buffered(),this.duration_);};/**
   * Turn off the polyfill for `progress` events that was created in
   * {@link Tech#manualProgressOn}
   * Stop manually tracking progress events by clearing the interval that was set in
   * {@link Tech#trackProgress}.
   */Tech.prototype.stopTrackingProgress=function stopTrackingProgress(){this.clearInterval(this.progressInterval);};/**
   * Polyfill the `timeupdate` event for browsers that don't support it.
   *
   * @see {@link Tech#trackCurrentTime}
   */Tech.prototype.manualTimeUpdatesOn=function manualTimeUpdatesOn(){this.manualTimeUpdates=true;this.on('play',this.trackCurrentTime);this.on('pause',this.stopTrackingCurrentTime);};/**
   * Turn off the polyfill for `timeupdate` events that was created in
   * {@link Tech#manualTimeUpdatesOn}
   */Tech.prototype.manualTimeUpdatesOff=function manualTimeUpdatesOff(){this.manualTimeUpdates=false;this.stopTrackingCurrentTime();this.off('play',this.trackCurrentTime);this.off('pause',this.stopTrackingCurrentTime);};/**
   * Sets up an interval function to track current time and trigger `timeupdate` every
   * 250 milliseconds.
   *
   * @listens Tech#play
   * @triggers Tech#timeupdate
   */Tech.prototype.trackCurrentTime=function trackCurrentTime(){if(this.currentTimeInterval){this.stopTrackingCurrentTime();}this.currentTimeInterval=this.setInterval(function(){/**
       * Triggered at an interval of 250ms to indicated that time is passing in the video.
       *
       * @event Tech#timeupdate
       * @type {EventTarget~Event}
       */this.trigger({type:'timeupdate',target:this,manuallyTriggered:true});// 42 = 24 fps // 250 is what Webkit uses // FF uses 15
},250);};/**
   * Stop the interval function created in {@link Tech#trackCurrentTime} so that the
   * `timeupdate` event is no longer triggered.
   *
   * @listens {Tech#pause}
   */Tech.prototype.stopTrackingCurrentTime=function stopTrackingCurrentTime(){this.clearInterval(this.currentTimeInterval);// #1002 - if the video ends right before the next timeupdate would happen,
// the progress bar won't make it all the way to the end
this.trigger({type:'timeupdate',target:this,manuallyTriggered:true});};/**
   * Turn off all event polyfills, clear the `Tech`s {@link AudioTrackList},
   * {@link VideoTrackList}, and {@link TextTrackList}, and dispose of this Tech.
   *
   * @fires Component#dispose
   */Tech.prototype.dispose=function dispose(){// clear out all tracks because we can't reuse them between techs
this.clearTracks(NORMAL.names);// Turn off any manual progress or timeupdate tracking
if(this.manualProgress){this.manualProgressOff();}if(this.manualTimeUpdates){this.manualTimeUpdatesOff();}_Component.prototype.dispose.call(this);};/**
   * Clear out a single `TrackList` or an array of `TrackLists` given their names.
   *
   * > Note: Techs without source handlers should call this between sources for `video`
   *         & `audio` tracks. You don't want to use them between tracks!
   *
   * @param {string[]|string} types
   *        TrackList names to clear, valid names are `video`, `audio`, and
   *        `text`.
   */Tech.prototype.clearTracks=function clearTracks(types){var _this3=this;types=[].concat(types);// clear out all tracks because we can't reuse them between techs
types.forEach(function(type){var list=_this3[type+'Tracks']()||[];var i=list.length;while(i--){var track=list[i];if(type==='text'){_this3.removeRemoteTextTrack(track);}list.removeTrack(track);}});};/**
   * Remove any TextTracks added via addRemoteTextTrack that are
   * flagged for automatic garbage collection
   */Tech.prototype.cleanupAutoTextTracks=function cleanupAutoTextTracks(){var list=this.autoRemoteTextTracks_||[];var i=list.length;while(i--){var track=list[i];this.removeRemoteTextTrack(track);}};/**
   * Reset the tech, which will removes all sources and reset the internal readyState.
   *
   * @abstract
   */Tech.prototype.reset=function reset(){};/**
   * Get or set an error on the Tech.
   *
   * @param {MediaError} [err]
   *        Error to set on the Tech
   *
   * @return {MediaError|null}
   *         The current error object on the tech, or null if there isn't one.
   */Tech.prototype.error=function error(err){if(err!==undefined){this.error_=new MediaError(err);this.trigger('error');}return this.error_;};/**
   * Returns the `TimeRange`s that have been played through for the current source.
   *
   * > NOTE: This implementation is incomplete. It does not track the played `TimeRange`.
   *         It only checks wether the source has played at all or not.
   *
   * @return {TimeRange}
   *         - A single time range if this video has played
   *         - An empty set of ranges if not.
   */Tech.prototype.played=function played(){if(this.hasStarted_){return createTimeRanges(0,0);}return createTimeRanges();};/**
   * Causes a manual time update to occur if {@link Tech#manualTimeUpdatesOn} was
   * previously called.
   *
   * @fires Tech#timeupdate
   */Tech.prototype.setCurrentTime=function setCurrentTime(){// improve the accuracy of manual timeupdates
if(this.manualTimeUpdates){/**
       * A manual `timeupdate` event.
       *
       * @event Tech#timeupdate
       * @type {EventTarget~Event}
       */this.trigger({type:'timeupdate',target:this,manuallyTriggered:true});}};/**
   * Turn on listeners for {@link VideoTrackList}, {@link {AudioTrackList}, and
   * {@link TextTrackList} events.
   *
   * This adds {@link EventTarget~EventListeners} for `addtrack`, and  `removetrack`.
   *
   * @fires Tech#audiotrackchange
   * @fires Tech#videotrackchange
   * @fires Tech#texttrackchange
   */Tech.prototype.initTrackListeners=function initTrackListeners(){var _this4=this;/**
     * Triggered when tracks are added or removed on the Tech {@link AudioTrackList}
     *
     * @event Tech#audiotrackchange
     * @type {EventTarget~Event}
     */ /**
     * Triggered when tracks are added or removed on the Tech {@link VideoTrackList}
     *
     * @event Tech#videotrackchange
     * @type {EventTarget~Event}
     */ /**
     * Triggered when tracks are added or removed on the Tech {@link TextTrackList}
     *
     * @event Tech#texttrackchange
     * @type {EventTarget~Event}
     */NORMAL.names.forEach(function(name){var props=NORMAL[name];var trackListChanges=function trackListChanges(){_this4.trigger(name+'trackchange');};var tracks=_this4[props.getterName]();tracks.addEventListener('removetrack',trackListChanges);tracks.addEventListener('addtrack',trackListChanges);_this4.on('dispose',function(){tracks.removeEventListener('removetrack',trackListChanges);tracks.removeEventListener('addtrack',trackListChanges);});});};/**
   * Emulate TextTracks using vtt.js if necessary
   *
   * @fires Tech#vttjsloaded
   * @fires Tech#vttjserror
   */Tech.prototype.addWebVttScript_=function addWebVttScript_(){var _this5=this;if(window$1.WebVTT){return;}// Initially, Tech.el_ is a child of a dummy-div wait until the Component system
// signals that the Tech is ready at which point Tech.el_ is part of the DOM
// before inserting the WebVTT script
if(document$1.body.contains(this.el())){// load via require if available and vtt.js script location was not passed in
// as an option. novtt builds will turn the above require call into an empty object
// which will cause this if check to always fail.
if(!this.options_['vtt.js']&&isPlain(vtt)&&Object.keys(vtt).length>0){this.trigger('vttjsloaded');return;}// load vtt.js via the script location option or the cdn of no location was
// passed in
var script=document$1.createElement('script');script.src=this.options_['vtt.js']||'https://vjs.zencdn.net/vttjs/0.12.4/vtt.min.js';script.onload=function(){/**
         * Fired when vtt.js is loaded.
         *
         * @event Tech#vttjsloaded
         * @type {EventTarget~Event}
         */_this5.trigger('vttjsloaded');};script.onerror=function(){/**
         * Fired when vtt.js was not loaded due to an error
         *
         * @event Tech#vttjsloaded
         * @type {EventTarget~Event}
         */_this5.trigger('vttjserror');};this.on('dispose',function(){script.onload=null;script.onerror=null;});// but have not loaded yet and we set it to true before the inject so that
// we don't overwrite the injected window.WebVTT if it loads right away
window$1.WebVTT=true;this.el().parentNode.appendChild(script);}else {this.ready(this.addWebVttScript_);}};/**
   * Emulate texttracks
   *
   */Tech.prototype.emulateTextTracks=function emulateTextTracks(){var _this6=this;var tracks=this.textTracks();var remoteTracks=this.remoteTextTracks();var handleAddTrack=function handleAddTrack(e){return tracks.addTrack(e.track);};var handleRemoveTrack=function handleRemoveTrack(e){return tracks.removeTrack(e.track);};remoteTracks.on('addtrack',handleAddTrack);remoteTracks.on('removetrack',handleRemoveTrack);this.addWebVttScript_();var updateDisplay=function updateDisplay(){return _this6.trigger('texttrackchange');};var textTracksChanges=function textTracksChanges(){updateDisplay();for(var i=0;i<tracks.length;i++){var track=tracks[i];track.removeEventListener('cuechange',updateDisplay);if(track.mode==='showing'){track.addEventListener('cuechange',updateDisplay);}}};textTracksChanges();tracks.addEventListener('change',textTracksChanges);tracks.addEventListener('addtrack',textTracksChanges);tracks.addEventListener('removetrack',textTracksChanges);this.on('dispose',function(){remoteTracks.off('addtrack',handleAddTrack);remoteTracks.off('removetrack',handleRemoveTrack);tracks.removeEventListener('change',textTracksChanges);tracks.removeEventListener('addtrack',textTracksChanges);tracks.removeEventListener('removetrack',textTracksChanges);for(var i=0;i<tracks.length;i++){var track=tracks[i];track.removeEventListener('cuechange',updateDisplay);}});};/**
   * Create and returns a remote {@link TextTrack} object.
   *
   * @param {string} kind
   *        `TextTrack` kind (subtitles, captions, descriptions, chapters, or metadata)
   *
   * @param {string} [label]
   *        Label to identify the text track
   *
   * @param {string} [language]
   *        Two letter language abbreviation
   *
   * @return {TextTrack}
   *         The TextTrack that gets created.
   */Tech.prototype.addTextTrack=function addTextTrack(kind,label,language){if(!kind){throw new Error('TextTrack kind is required but was not provided');}return createTrackHelper(this,kind,label,language);};/**
   * Create an emulated TextTrack for use by addRemoteTextTrack
   *
   * This is intended to be overridden by classes that inherit from
   * Tech in order to create native or custom TextTracks.
   *
   * @param {Object} options
   *        The object should contain the options to initialize the TextTrack with.
   *
   * @param {string} [options.kind]
   *        `TextTrack` kind (subtitles, captions, descriptions, chapters, or metadata).
   *
   * @param {string} [options.label].
   *        Label to identify the text track
   *
   * @param {string} [options.language]
   *        Two letter language abbreviation.
   *
   * @return {HTMLTrackElement}
   *         The track element that gets created.
   */Tech.prototype.createRemoteTextTrack=function createRemoteTextTrack(options){var track=mergeOptions(options,{tech:this});return new REMOTE.remoteTextEl.TrackClass(track);};/**
   * Creates a remote text track object and returns an html track element.
   *
   * > Note: This can be an emulated {@link HTMLTrackElement} or a native one.
   *
   * @param {Object} options
   *        See {@link Tech#createRemoteTextTrack} for more detailed properties.
   *
   * @param {boolean} [manualCleanup=true]
   *        - When false: the TextTrack will be automatically removed from the video
   *          element whenever the source changes
   *        - When True: The TextTrack will have to be cleaned up manually
   *
   * @return {HTMLTrackElement}
   *         An Html Track Element.
   *
   * @deprecated The default functionality for this function will be equivalent
   *             to "manualCleanup=false" in the future. The manualCleanup parameter will
   *             also be removed.
   */Tech.prototype.addRemoteTextTrack=function addRemoteTextTrack(){var _this7=this;var options=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};var manualCleanup=arguments[1];var htmlTrackElement=this.createRemoteTextTrack(options);if(manualCleanup!==true&&manualCleanup!==false){// deprecation warning
log$1.warn('Calling addRemoteTextTrack without explicitly setting the "manualCleanup" parameter to `true` is deprecated and default to `false` in future version of video.js');manualCleanup=true;}// store HTMLTrackElement and TextTrack to remote list
this.remoteTextTrackEls().addTrackElement_(htmlTrackElement);this.remoteTextTracks().addTrack(htmlTrackElement.track);if(manualCleanup!==true){// create the TextTrackList if it doesn't exist
this.ready(function(){return _this7.autoRemoteTextTracks_.addTrack(htmlTrackElement.track);});}return htmlTrackElement;};/**
   * Remove a remote text track from the remote `TextTrackList`.
   *
   * @param {TextTrack} track
   *        `TextTrack` to remove from the `TextTrackList`
   */Tech.prototype.removeRemoteTextTrack=function removeRemoteTextTrack(track){var trackElement=this.remoteTextTrackEls().getTrackElementByTrack_(track);// remove HTMLTrackElement and TextTrack from remote list
this.remoteTextTrackEls().removeTrackElement_(trackElement);this.remoteTextTracks().removeTrack(track);this.autoRemoteTextTracks_.removeTrack(track);};/**
   * Gets available media playback quality metrics as specified by the W3C's Media
   * Playback Quality API.
   *
   * @see [Spec]{@link https://wicg.github.io/media-playback-quality}
   *
   * @return {Object}
   *         An object with supported media playback quality metrics
   *
   * @abstract
   */Tech.prototype.getVideoPlaybackQuality=function getVideoPlaybackQuality(){return {};};/**
   * A method to set a poster from a `Tech`.
   *
   * @abstract
   */Tech.prototype.setPoster=function setPoster(){};/**
   * A method to check for the presence of the 'playsinine' <video> attribute.
   *
   * @abstract
   */Tech.prototype.playsinline=function playsinline(){};/**
   * A method to set or unset the 'playsinine' <video> attribute.
   *
   * @abstract
   */Tech.prototype.setPlaysinline=function setPlaysinline(){};/*
   * Check if the tech can support the given mime-type.
   *
   * The base tech does not support any type, but source handlers might
   * overwrite this.
   *
   * @param  {string} type
   *         The mimetype to check for support
   *
   * @return {string}
   *         'probably', 'maybe', or empty string
   *
   * @see [Spec]{@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canPlayType}
   *
   * @abstract
   */Tech.prototype.canPlayType=function canPlayType(){return '';};/**
   * Check if the type is supported by this tech.
   *
   * The base tech does not support any type, but source handlers might
   * overwrite this.
   *
   * @param {string} type
   *        The media type to check
   * @return {string} Returns the native video element's response
   */Tech.canPlayType=function canPlayType(){return '';};/**
   * Check if the tech can support the given source
   * @param {Object} srcObj
   *        The source object
   * @param {Object} options
   *        The options passed to the tech
   * @return {string} 'probably', 'maybe', or '' (empty string)
   */Tech.canPlaySource=function canPlaySource(srcObj,options){return Tech.canPlayType(srcObj.type);};/*
   * Return whether the argument is a Tech or not.
   * Can be passed either a Class like `Html5` or a instance like `player.tech_`
   *
   * @param {Object} component
   *        The item to check
   *
   * @return {boolean}
   *         Whether it is a tech or not
   *         - True if it is a tech
   *         - False if it is not
   */Tech.isTech=function isTech(component){return component.prototype instanceof Tech||component instanceof Tech||component===Tech;};/**
   * Registers a `Tech` into a shared list for videojs.
   *
   * @param {string} name
   *        Name of the `Tech` to register.
   *
   * @param {Object} tech
   *        The `Tech` class to register.
   */Tech.registerTech=function registerTech(name,tech){if(!Tech.techs_){Tech.techs_={};}if(!Tech.isTech(tech)){throw new Error('Tech '+name+' must be a Tech');}if(!Tech.canPlayType){throw new Error('Techs must have a static canPlayType method on them');}if(!Tech.canPlaySource){throw new Error('Techs must have a static canPlaySource method on them');}name=toTitleCase(name);Tech.techs_[name]=tech;if(name!=='Tech'){// camel case the techName for use in techOrder
Tech.defaultTechOrder_.push(name);}return tech;};/**
   * Get a `Tech` from the shared list by name.
   *
   * @param {string} name
   *        `camelCase` or `TitleCase` name of the Tech to get
   *
   * @return {Tech|undefined}
   *         The `Tech` or undefined if there was no tech with the name requsted.
   */Tech.getTech=function getTech(name){if(!name){return;}name=toTitleCase(name);if(Tech.techs_&&Tech.techs_[name]){return Tech.techs_[name];}if(window$1&&window$1.videojs&&window$1.videojs[name]){log$1.warn('The '+name+' tech was added to the videojs object when it should be registered using videojs.registerTech(name, tech)');return window$1.videojs[name];}};return Tech;}(Component);/**
 * Get the {@link VideoTrackList}
 *
 * @returns {VideoTrackList}
 * @method Tech.prototype.videoTracks
 */ /**
 * Get the {@link AudioTrackList}
 *
 * @returns {AudioTrackList}
 * @method Tech.prototype.audioTracks
 */ /**
 * Get the {@link TextTrackList}
 *
 * @returns {TextTrackList}
 * @method Tech.prototype.textTracks
 */ /**
 * Get the remote element {@link TextTrackList}
 *
 * @returns {TextTrackList}
 * @method Tech.prototype.remoteTextTracks
 */ /**
 * Get the remote element {@link HtmlTrackElementList}
 *
 * @returns {HtmlTrackElementList}
 * @method Tech.prototype.remoteTextTrackEls
 */ALL.names.forEach(function(name){var props=ALL[name];Tech.prototype[props.getterName]=function(){this[props.privateName]=this[props.privateName]||new props.ListClass();return this[props.privateName];};});/**
 * List of associated text tracks
 *
 * @type {TextTrackList}
 * @private
 * @property Tech#textTracks_
 */ /**
 * List of associated audio tracks.
 *
 * @type {AudioTrackList}
 * @private
 * @property Tech#audioTracks_
 */ /**
 * List of associated video tracks.
 *
 * @type {VideoTrackList}
 * @private
 * @property Tech#videoTracks_
 */ /**
 * Boolean indicating wether the `Tech` supports volume control.
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresVolumeControl=true;/**
 * Boolean indicating whether the `Tech` supports muting volume.
 *
 * @type {bolean}
 * @default
 */Tech.prototype.featuresMuteControl=true;/**
 * Boolean indicating whether the `Tech` supports fullscreen resize control.
 * Resizing plugins using request fullscreen reloads the plugin
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresFullscreenResize=false;/**
 * Boolean indicating wether the `Tech` supports changing the speed at which the video
 * plays. Examples:
 *   - Set player to play 2x (twice) as fast
 *   - Set player to play 0.5x (half) as fast
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresPlaybackRate=false;/**
 * Boolean indicating wether the `Tech` supports the `progress` event. This is currently
 * not triggered by video-js-swf. This will be used to determine if
 * {@link Tech#manualProgressOn} should be called.
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresProgressEvents=false;/**
 * Boolean indicating wether the `Tech` supports the `sourceset` event.
 *
 * A tech should set this to `true` and then use {@link Tech#triggerSourceset}
 * to trigger a {@link Tech#event:sourceset} at the earliest time after getting
 * a new source.
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresSourceset=false;/**
 * Boolean indicating wether the `Tech` supports the `timeupdate` event. This is currently
 * not triggered by video-js-swf. This will be used to determine if
 * {@link Tech#manualTimeUpdates} should be called.
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresTimeupdateEvents=false;/**
 * Boolean indicating wether the `Tech` supports the native `TextTrack`s.
 * This will help us integrate with native `TextTrack`s if the browser supports them.
 *
 * @type {boolean}
 * @default
 */Tech.prototype.featuresNativeTextTracks=false;/**
 * A functional mixin for techs that want to use the Source Handler pattern.
 * Source handlers are scripts for handling specific formats.
 * The source handler pattern is used for adaptive formats (HLS, DASH) that
 * manually load video data and feed it into a Source Buffer (Media Source Extensions)
 * Example: `Tech.withSourceHandlers.call(MyTech);`
 *
 * @param {Tech} _Tech
 *        The tech to add source handler functions to.
 *
 * @mixes Tech~SourceHandlerAdditions
 */Tech.withSourceHandlers=function(_Tech){/**
   * Register a source handler
   *
   * @param {Function} handler
   *        The source handler class
   *
   * @param {number} [index]
   *        Register it at the following index
   */_Tech.registerSourceHandler=function(handler,index){var handlers=_Tech.sourceHandlers;if(!handlers){handlers=_Tech.sourceHandlers=[];}if(index===undefined){// add to the end of the list
index=handlers.length;}handlers.splice(index,0,handler);};/**
   * Check if the tech can support the given type. Also checks the
   * Techs sourceHandlers.
   *
   * @param {string} type
   *         The mimetype to check.
   *
   * @return {string}
   *         'probably', 'maybe', or '' (empty string)
   */_Tech.canPlayType=function(type){var handlers=_Tech.sourceHandlers||[];var can=void 0;for(var i=0;i<handlers.length;i++){can=handlers[i].canPlayType(type);if(can){return can;}}return '';};/**
   * Returns the first source handler that supports the source.
   *
   * TODO: Answer question: should 'probably' be prioritized over 'maybe'
   *
   * @param {Tech~SourceObject} source
   *        The source object
   *
   * @param {Object} options
   *        The options passed to the tech
   *
   * @return {SourceHandler|null}
   *          The first source handler that supports the source or null if
   *          no SourceHandler supports the source
   */_Tech.selectSourceHandler=function(source,options){var handlers=_Tech.sourceHandlers||[];var can=void 0;for(var i=0;i<handlers.length;i++){can=handlers[i].canHandleSource(source,options);if(can){return handlers[i];}}return null;};/**
   * Check if the tech can support the given source.
   *
   * @param {Tech~SourceObject} srcObj
   *        The source object
   *
   * @param {Object} options
   *        The options passed to the tech
   *
   * @return {string}
   *         'probably', 'maybe', or '' (empty string)
   */_Tech.canPlaySource=function(srcObj,options){var sh=_Tech.selectSourceHandler(srcObj,options);if(sh){return sh.canHandleSource(srcObj,options);}return '';};/**
   * When using a source handler, prefer its implementation of
   * any function normally provided by the tech.
   */var deferrable=['seekable','seeking','duration'];/**
   * A wrapper around {@link Tech#seekable} that will call a `SourceHandler`s seekable
   * function if it exists, with a fallback to the Techs seekable function.
   *
   * @method _Tech.seekable
   */ /**
   * A wrapper around {@link Tech#duration} that will call a `SourceHandler`s duration
   * function if it exists, otherwise it will fallback to the techs duration function.
   *
   * @method _Tech.duration
   */deferrable.forEach(function(fnName){var originalFn=this[fnName];if(typeof originalFn!=='function'){return;}this[fnName]=function(){if(this.sourceHandler_&&this.sourceHandler_[fnName]){return this.sourceHandler_[fnName].apply(this.sourceHandler_,arguments);}return originalFn.apply(this,arguments);};},_Tech.prototype);/**
   * Create a function for setting the source using a source object
   * and source handlers.
   * Should never be called unless a source handler was found.
   *
   * @param {Tech~SourceObject} source
   *        A source object with src and type keys
   */_Tech.prototype.setSource=function(source){var sh=_Tech.selectSourceHandler(source,this.options_);if(!sh){// Fall back to a native source hander when unsupported sources are
// deliberately set
if(_Tech.nativeSourceHandler){sh=_Tech.nativeSourceHandler;}else {log$1.error('No source hander found for the current source.');}}// Dispose any existing source handler
this.disposeSourceHandler();this.off('dispose',this.disposeSourceHandler);if(sh!==_Tech.nativeSourceHandler){this.currentSource_=source;}this.sourceHandler_=sh.handleSource(source,this,this.options_);this.on('dispose',this.disposeSourceHandler);};/**
   * Clean up any existing SourceHandlers and listeners when the Tech is disposed.
   *
   * @listens Tech#dispose
   */_Tech.prototype.disposeSourceHandler=function(){// if we have a source and get another one
// then we are loading something new
// than clear all of our current tracks
if(this.currentSource_){this.clearTracks(['audio','video']);this.currentSource_=null;}// always clean up auto-text tracks
this.cleanupAutoTextTracks();if(this.sourceHandler_){if(this.sourceHandler_.dispose){this.sourceHandler_.dispose();}this.sourceHandler_=null;}};};// The base Tech class needs to be registered as a Component. It is the only
// Tech that can be registered as a Component.
Component.registerComponent('Tech',Tech);Tech.registerTech('Tech',Tech);/**
 * A list of techs that should be added to techOrder on Players
 *
 * @private
 */Tech.defaultTechOrder_=[];var middlewares={};var middlewareInstances={};var TERMINATOR={};function use(type,middleware){middlewares[type]=middlewares[type]||[];middlewares[type].push(middleware);}function setSource(player,src,next){player.setTimeout(function(){return setSourceHelper(src,middlewares[src.type],next,player);},1);}function setTech(middleware,tech){middleware.forEach(function(mw){return mw.setTech&&mw.setTech(tech);});}/**
 * Calls a getter on the tech first, through each middleware
 * from right to left to the player.
 */function get$1(middleware,tech,method){return middleware.reduceRight(middlewareIterator(method),tech[method]());}/**
 * Takes the argument given to the player and calls the setter method on each
 * middlware from left to right to the tech.
 */function set$1(middleware,tech,method,arg){return tech[method](middleware.reduce(middlewareIterator(method),arg));}/**
 * Takes the argument given to the player and calls the `call` version of the method
 * on each middleware from left to right.
 * Then, call the passed in method on the tech and return the result unchanged
 * back to the player, through middleware, this time from right to left.
 */function mediate(middleware,tech,method){var arg=arguments.length>3&&arguments[3]!==undefined?arguments[3]:null;var callMethod='call'+toTitleCase(method);var middlewareValue=middleware.reduce(middlewareIterator(callMethod),arg);var terminated=middlewareValue===TERMINATOR;var returnValue=terminated?null:tech[method](middlewareValue);executeRight(middleware,method,returnValue,terminated);return returnValue;}var allowedGetters={buffered:1,currentTime:1,duration:1,seekable:1,played:1,paused:1};var allowedSetters={setCurrentTime:1};var allowedMediators={play:1,pause:1};function middlewareIterator(method){return function(value,mw){// if the previous middleware terminated, pass along the termination
if(value===TERMINATOR){return TERMINATOR;}if(mw[method]){return mw[method](value);}return value;};}function executeRight(mws,method,value,terminated){for(var i=mws.length-1;i>=0;i--){var mw=mws[i];if(mw[method]){mw[method](terminated,value);}}}function clearCacheForPlayer(player){middlewareInstances[player.id()]=null;}/**
 * {
 *  [playerId]: [[mwFactory, mwInstance], ...]
 * }
 */function getOrCreateFactory(player,mwFactory){var mws=middlewareInstances[player.id()];var mw=null;if(mws===undefined||mws===null){mw=mwFactory(player);middlewareInstances[player.id()]=[[mwFactory,mw]];return mw;}for(var i=0;i<mws.length;i++){var _mws$i=mws[i],mwf=_mws$i[0],mwi=_mws$i[1];if(mwf!==mwFactory){continue;}mw=mwi;}if(mw===null){mw=mwFactory(player);mws.push([mwFactory,mw]);}return mw;}function setSourceHelper(){var src=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};var middleware=arguments.length>1&&arguments[1]!==undefined?arguments[1]:[];var next=arguments[2];var player=arguments[3];var acc=arguments.length>4&&arguments[4]!==undefined?arguments[4]:[];var lastRun=arguments.length>5&&arguments[5]!==undefined?arguments[5]:false;var mwFactory=middleware[0],mwrest=middleware.slice(1);// if mwFactory is a string, then we're at a fork in the road
if(typeof mwFactory==='string'){setSourceHelper(src,middlewares[mwFactory],next,player,acc,lastRun);// if we have an mwFactory, call it with the player to get the mw,
// then call the mw's setSource method
}else if(mwFactory){var mw=getOrCreateFactory(player,mwFactory);// if setSource isn't present, implicitly select this middleware
if(!mw.setSource){acc.push(mw);return setSourceHelper(src,mwrest,next,player,acc,lastRun);}mw.setSource(assign$1({},src),function(err,_src){// something happened, try the next middleware on the current level
// make sure to use the old src
if(err){return setSourceHelper(src,mwrest,next,player,acc,lastRun);}// we've succeeded, now we need to go deeper
acc.push(mw);// if it's the same type, continue down the current chain
// otherwise, we want to go down the new chain
setSourceHelper(_src,src.type===_src.type?mwrest:middlewares[_src.type],next,player,acc,lastRun);});}else if(mwrest.length){setSourceHelper(src,mwrest,next,player,acc,lastRun);}else if(lastRun){next(src,acc);}else {setSourceHelper(src,middlewares['*'],next,player,acc,true);}}/**
 * Mimetypes
 *
 * @see http://hul.harvard.edu/ois/////systems/wax/wax-public-help/mimetypes.htm
 * @typedef Mimetypes~Kind
 * @enum
 */var MimetypesKind={opus:'video/ogg',ogv:'video/ogg',mp4:'video/mp4',mov:'video/mp4',m4v:'video/mp4',mkv:'video/x-matroska',mp3:'audio/mpeg',aac:'audio/aac',oga:'audio/ogg',m3u8:'application/x-mpegURL'};/**
 * Get the mimetype of a given src url if possible
 *
 * @param {string} src
 *        The url to the src
 *
 * @return {string}
 *         return the mimetype if it was known or empty string otherwise
 */var getMimetype=function getMimetype(){var src=arguments.length>0&&arguments[0]!==undefined?arguments[0]:'';var ext=getFileExtension(src);var mimetype=MimetypesKind[ext.toLowerCase()];return mimetype||'';};/**
 * Find the mime type of a given source string if possible. Uses the player
 * source cache.
 *
 * @param {Player} player
 *        The player object
 *
 * @param {string} src
 *        The source string
 *
 * @return {string}
 *         The type that was found
 */var findMimetype=function findMimetype(player,src){if(!src){return '';}// 1. check for the type in the `source` cache
if(player.cache_.source.src===src&&player.cache_.source.type){return player.cache_.source.type;}// 2. see if we have this source in our `currentSources` cache
var matchingSources=player.cache_.sources.filter(function(s){return s.src===src;});if(matchingSources.length){return matchingSources[0].type;}// 3. look for the src url in source elements and use the type there
var sources=player.$$('source');for(var i=0;i<sources.length;i++){var s=sources[i];if(s.type&&s.src&&s.src===src){return s.type;}}// 4. finally fallback to our list of mime types based on src url extension
return getMimetype(src);};/**
 * @module filter-source
 */ /**
 * Filter out single bad source objects or multiple source objects in an
 * array. Also flattens nested source object arrays into a 1 dimensional
 * array of source objects.
 *
 * @param {Tech~SourceObject|Tech~SourceObject[]} src
 *        The src object to filter
 *
 * @return {Tech~SourceObject[]}
 *         An array of sourceobjects containing only valid sources
 *
 * @private
 */var filterSource=function filterSource(src){// traverse array
if(Array.isArray(src)){var newsrc=[];src.forEach(function(srcobj){srcobj=filterSource(srcobj);if(Array.isArray(srcobj)){newsrc=newsrc.concat(srcobj);}else if(isObject(srcobj)){newsrc.push(srcobj);}});src=newsrc;}else if(typeof src==='string'&&src.trim()){// convert string into object
src=[fixSource({src:src})];}else if(isObject(src)&&typeof src.src==='string'&&src.src&&src.src.trim()){// src is already valid
src=[fixSource(src)];}else {// invalid source, turn it into an empty array
src=[];}return src;};/**
 * Checks src mimetype, adding it when possible
 *
 * @param {Tech~SourceObject} src
 *        The src object to check
 * @return {Tech~SourceObject}
 *        src Object with known type
 */function fixSource(src){var mimetype=getMimetype(src.src);if(!src.type&&mimetype){src.type=mimetype;}return src;}/**
 * @file loader.js
 */ /**
 * The `MediaLoader` is the `Component` that decides which playback technology to load
 * when a player is initialized.
 *
 * @extends Component
 */var MediaLoader=function(_Component){inherits(MediaLoader,_Component);/**
   * Create an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should attach to.
   *
   * @param {Object} [options]
   *        The key/value stroe of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function that is run when this component is ready.
   */function MediaLoader(player,options,ready){classCallCheck(this,MediaLoader);// MediaLoader has no element
var options_=mergeOptions({createEl:false},options);// If there are no sources when the player is initialized,
// load the first supported playback technology.
var _this=possibleConstructorReturn(this,_Component.call(this,player,options_,ready));if(!options.playerOptions.sources||options.playerOptions.sources.length===0){for(var i=0,j=options.playerOptions.techOrder;i<j.length;i++){var techName=toTitleCase(j[i]);var tech=Tech.getTech(techName);// Support old behavior of techs being registered as components.
// Remove once that deprecated behavior is removed.
if(!techName){tech=Component.getComponent(techName);}// Check if the browser supports this technology
if(tech&&tech.isSupported()){player.loadTech_(techName);break;}}}else {// Loop through playback technologies (HTML5, Flash) and check for support.
// Then load the best source.
// A few assumptions here:
//   All playback technologies respect preload false.
player.src(options.playerOptions.sources);}return _this;}return MediaLoader;}(Component);Component.registerComponent('MediaLoader',MediaLoader);/**
 * @file button.js
 */ /**
 * Clickable Component which is clickable or keyboard actionable,
 * but is not a native HTML button.
 *
 * @extends Component
 */var ClickableComponent=function(_Component){inherits(ClickableComponent,_Component);/**
   * Creates an instance of this class.
   *
   * @param  {Player} player
   *         The `Player` that this class should be attached to.
   *
   * @param  {Object} [options]
   *         The key/value store of player options.
   */function ClickableComponent(player,options){classCallCheck(this,ClickableComponent);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.emitTapEvents();_this.enable();return _this;}/**
   * Create the `Component`s DOM element.
   *
   * @param {string} [tag=div]
   *        The element's node type.
   *
   * @param {Object} [props={}]
   *        An object of properties that should be set on the element.
   *
   * @param {Object} [attributes={}]
   *        An object of attributes that should be set on the element.
   *
   * @return {Element}
   *         The element that gets created.
   */ClickableComponent.prototype.createEl=function createEl$$1(){var tag=arguments.length>0&&arguments[0]!==undefined?arguments[0]:'div';var props=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var attributes=arguments.length>2&&arguments[2]!==undefined?arguments[2]:{};props=assign$1({innerHTML:'<span aria-hidden="true" class="vjs-icon-placeholder"></span>',className:this.buildCSSClass(),tabIndex:0},props);if(tag==='button'){log$1.error('Creating a ClickableComponent with an HTML element of '+tag+' is not supported; use a Button instead.');}// Add ARIA attributes for clickable element which is not a native HTML button
attributes=assign$1({role:'button'},attributes);this.tabIndex_=props.tabIndex;var el=_Component.prototype.createEl.call(this,tag,props,attributes);this.createControlTextEl(el);return el;};ClickableComponent.prototype.dispose=function dispose(){// remove controlTextEl_ on dipose
this.controlTextEl_=null;_Component.prototype.dispose.call(this);};/**
   * Create a control text element on this `Component`
   *
   * @param {Element} [el]
   *        Parent element for the control text.
   *
   * @return {Element}
   *         The control text element that gets created.
   */ClickableComponent.prototype.createControlTextEl=function createControlTextEl(el){this.controlTextEl_=createEl('span',{className:'vjs-control-text'},{// let the screen reader user know that the text of the element may change
'aria-live':'polite'});if(el){el.appendChild(this.controlTextEl_);}this.controlText(this.controlText_,el);return this.controlTextEl_;};/**
   * Get or set the localize text to use for the controls on the `Component`.
   *
   * @param {string} [text]
   *        Control text for element.
   *
   * @param {Element} [el=this.el()]
   *        Element to set the title on.
   *
   * @return {string}
   *         - The control text when getting
   */ClickableComponent.prototype.controlText=function controlText(text){var el=arguments.length>1&&arguments[1]!==undefined?arguments[1]:this.el();if(text===undefined){return this.controlText_||'Need Text';}var localizedText=this.localize(text);this.controlText_=text;textContent(this.controlTextEl_,localizedText);if(!this.nonIconControl){// Set title attribute if only an icon is shown
el.setAttribute('title',localizedText);}};/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */ClickableComponent.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-control vjs-button '+_Component.prototype.buildCSSClass.call(this);};/**
   * Enable this `Component`s element.
   */ClickableComponent.prototype.enable=function enable(){if(!this.enabled_){this.enabled_=true;this.removeClass('vjs-disabled');this.el_.setAttribute('aria-disabled','false');if(typeof this.tabIndex_!=='undefined'){this.el_.setAttribute('tabIndex',this.tabIndex_);}this.on(['tap','click'],this.handleClick);this.on('focus',this.handleFocus);this.on('blur',this.handleBlur);}};/**
   * Disable this `Component`s element.
   */ClickableComponent.prototype.disable=function disable(){this.enabled_=false;this.addClass('vjs-disabled');this.el_.setAttribute('aria-disabled','true');if(typeof this.tabIndex_!=='undefined'){this.el_.removeAttribute('tabIndex');}this.off(['tap','click'],this.handleClick);this.off('focus',this.handleFocus);this.off('blur',this.handleBlur);};/**
   * This gets called when a `ClickableComponent` gets:
   * - Clicked (via the `click` event, listening starts in the constructor)
   * - Tapped (via the `tap` event, listening starts in the constructor)
   * - The following things happen in order:
   *   1. {@link ClickableComponent#handleFocus} is called via a `focus` event on the
   *      `ClickableComponent`.
   *   2. {@link ClickableComponent#handleFocus} adds a listener for `keydown` on using
   *      {@link ClickableComponent#handleKeyPress}.
   *   3. `ClickableComponent` has not had a `blur` event (`blur` means that focus was lost). The user presses
   *      the space or enter key.
   *   4. {@link ClickableComponent#handleKeyPress} calls this function with the `keydown`
   *      event as a parameter.
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   * @abstract
   */ClickableComponent.prototype.handleClick=function handleClick(event){};/**
   * This gets called when a `ClickableComponent` gains focus via a `focus` event.
   * Turns on listening for `keydown` events. When they happen it
   * calls `this.handleKeyPress`.
   *
   * @param {EventTarget~Event} event
   *        The `focus` event that caused this function to be called.
   *
   * @listens focus
   */ClickableComponent.prototype.handleFocus=function handleFocus(event){on(document$1,'keydown',bind(this,this.handleKeyPress));};/**
   * Called when this ClickableComponent has focus and a key gets pressed down. By
   * default it will call `this.handleClick` when the key is space or enter.
   *
   * @param {EventTarget~Event} event
   *        The `keydown` event that caused this function to be called.
   *
   * @listens keydown
   */ClickableComponent.prototype.handleKeyPress=function handleKeyPress(event){// Support Space (32) or Enter (13) key operation to fire a click event
if(event.which===32||event.which===13){event.preventDefault();this.trigger('click');}else if(_Component.prototype.handleKeyPress){// Pass keypress handling up for unsupported keys
_Component.prototype.handleKeyPress.call(this,event);}};/**
   * Called when a `ClickableComponent` loses focus. Turns off the listener for
   * `keydown` events. Which Stops `this.handleKeyPress` from getting called.
   *
   * @param {EventTarget~Event} event
   *        The `blur` event that caused this function to be called.
   *
   * @listens blur
   */ClickableComponent.prototype.handleBlur=function handleBlur(event){off(document$1,'keydown',bind(this,this.handleKeyPress));};return ClickableComponent;}(Component);Component.registerComponent('ClickableComponent',ClickableComponent);/**
 * @file poster-image.js
 */ /**
 * A `ClickableComponent` that handles showing the poster image for the player.
 *
 * @extends ClickableComponent
 */var PosterImage=function(_ClickableComponent){inherits(PosterImage,_ClickableComponent);/**
   * Create an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should attach to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function PosterImage(player,options){classCallCheck(this,PosterImage);var _this=possibleConstructorReturn(this,_ClickableComponent.call(this,player,options));_this.update();player.on('posterchange',bind(_this,_this.update));return _this;}/**
   * Clean up and dispose of the `PosterImage`.
   */PosterImage.prototype.dispose=function dispose(){this.player().off('posterchange',this.update);_ClickableComponent.prototype.dispose.call(this);};/**
   * Create the `PosterImage`s DOM element.
   *
   * @return {Element}
   *         The element that gets created.
   */PosterImage.prototype.createEl=function createEl$$1(){var el=createEl('div',{className:'vjs-poster',// Don't want poster to be tabbable.
tabIndex:-1});// To ensure the poster image resizes while maintaining its original aspect
// ratio, use a div with `background-size` when available. For browsers that
// do not support `background-size` (e.g. IE8), fall back on using a regular
// img element.
if(!BACKGROUND_SIZE_SUPPORTED){this.fallbackImg_=createEl('img');el.appendChild(this.fallbackImg_);}return el;};/**
   * An {@link EventTarget~EventListener} for {@link Player#posterchange} events.
   *
   * @listens Player#posterchange
   *
   * @param {EventTarget~Event} [event]
   *        The `Player#posterchange` event that triggered this function.
   */PosterImage.prototype.update=function update(event){var url=this.player().poster();this.setSrc(url);// If there's no poster source we should display:none on this component
// so it's not still clickable or right-clickable
if(url){this.show();}else {this.hide();}};/**
   * Set the source of the `PosterImage` depending on the display method.
   *
   * @param {string} url
   *        The URL to the source for the `PosterImage`.
   */PosterImage.prototype.setSrc=function setSrc(url){if(this.fallbackImg_){this.fallbackImg_.src=url;}else {var backgroundImage='';// Any falsey values should stay as an empty string, otherwise
// this will throw an extra error
if(url){backgroundImage='url("'+url+'")';}this.el_.style.backgroundImage=backgroundImage;}};/**
   * An {@link EventTarget~EventListener} for clicks on the `PosterImage`. See
   * {@link ClickableComponent#handleClick} for instances where this will be triggered.
   *
   * @listens tap
   * @listens click
   * @listens keydown
   *
   * @param {EventTarget~Event} event
   +        The `click`, `tap` or `keydown` event that caused this function to be called.
   */PosterImage.prototype.handleClick=function handleClick(event){// We don't want a click to trigger playback when controls are disabled
if(!this.player_.controls()){return;}if(this.player_.paused()){silencePromise(this.player_.play());}else {this.player_.pause();}};return PosterImage;}(ClickableComponent);Component.registerComponent('PosterImage',PosterImage);/**
 * @file text-track-display.js
 */var darkGray='#222';var lightGray='#ccc';var fontMap={monospace:'monospace',sansSerif:'sans-serif',serif:'serif',monospaceSansSerif:'"Andale Mono", "Lucida Console", monospace',monospaceSerif:'"Courier New", monospace',proportionalSansSerif:'sans-serif',proportionalSerif:'serif',casual:'"Comic Sans MS", Impact, fantasy',script:'"Monotype Corsiva", cursive',smallcaps:'"Andale Mono", "Lucida Console", monospace, sans-serif'};/**
 * Construct an rgba color from a given hex color code.
 *
 * @param {number} color
 *        Hex number for color, like #f0e or #f604e2.
 *
 * @param {number} opacity
 *        Value for opacity, 0.0 - 1.0.
 *
 * @return {string}
 *         The rgba color that was created, like 'rgba(255, 0, 0, 0.3)'.
 */function constructColor(color,opacity){var hex=void 0;if(color.length===4){// color looks like "#f0e"
hex=color[1]+color[1]+color[2]+color[2]+color[3]+color[3];}else if(color.length===7){// color looks like "#f604e2"
hex=color.slice(1);}else {throw new Error('Invalid color code provided, '+color+'; must be formatted as e.g. #f0e or #f604e2.');}return 'rgba('+parseInt(hex.slice(0,2),16)+','+parseInt(hex.slice(2,4),16)+','+parseInt(hex.slice(4,6),16)+','+opacity+')';}/**
 * Try to update the style of a DOM element. Some style changes will throw an error,
 * particularly in IE8. Those should be noops.
 *
 * @param {Element} el
 *        The DOM element to be styled.
 *
 * @param {string} style
 *        The CSS property on the element that should be styled.
 *
 * @param {string} rule
 *        The style rule that should be applied to the property.
 *
 * @private
 */function tryUpdateStyle(el,style,rule){try{el.style[style]=rule;}catch(e){// Satisfies linter.
return;}}/**
 * The component for displaying text track cues.
 *
 * @extends Component
 */var TextTrackDisplay=function(_Component){inherits(TextTrackDisplay,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function to call when `TextTrackDisplay` is ready.
   */function TextTrackDisplay(player,options,ready){classCallCheck(this,TextTrackDisplay);var _this=possibleConstructorReturn(this,_Component.call(this,player,options,ready));var updateDisplayHandler=bind(_this,_this.updateDisplay);player.on('loadstart',bind(_this,_this.toggleDisplay));player.on('texttrackchange',updateDisplayHandler);player.on('loadstart',bind(_this,_this.preselectTrack));// This used to be called during player init, but was causing an error
// if a track should show by default and the display hadn't loaded yet.
// Should probably be moved to an external track loader when we support
// tracks that don't need a display.
player.ready(bind(_this,function(){if(player.tech_&&player.tech_.featuresNativeTextTracks){this.hide();return;}player.on('fullscreenchange',updateDisplayHandler);player.on('playerresize',updateDisplayHandler);if(window$1.addEventListener){window$1.addEventListener('orientationchange',updateDisplayHandler);}player.on('dispose',function(){return window$1.removeEventListener('orientationchange',updateDisplayHandler);});var tracks=this.options_.playerOptions.tracks||[];for(var i=0;i<tracks.length;i++){this.player_.addRemoteTextTrack(tracks[i],true);}this.preselectTrack();}));return _this;}/**
  * Preselect a track following this precedence:
  * - matches the previously selected {@link TextTrack}'s language and kind
  * - matches the previously selected {@link TextTrack}'s language only
  * - is the first default captions track
  * - is the first default descriptions track
  *
  * @listens Player#loadstart
  */TextTrackDisplay.prototype.preselectTrack=function preselectTrack(){var modes={captions:1,subtitles:1};var trackList=this.player_.textTracks();var userPref=this.player_.cache_.selectedLanguage;var firstDesc=void 0;var firstCaptions=void 0;var preferredTrack=void 0;for(var i=0;i<trackList.length;i++){var track=trackList[i];if(userPref&&userPref.enabled&&userPref.language===track.language){// Always choose the track that matches both language and kind
if(track.kind===userPref.kind){preferredTrack=track;// or choose the first track that matches language
}else if(!preferredTrack){preferredTrack=track;}// clear everything if offTextTrackMenuItem was clicked
}else if(userPref&&!userPref.enabled){preferredTrack=null;firstDesc=null;firstCaptions=null;}else if(track['default']){if(track.kind==='descriptions'&&!firstDesc){firstDesc=track;}else if(track.kind in modes&&!firstCaptions){firstCaptions=track;}}}// The preferredTrack matches the user preference and takes
// precendence over all the other tracks.
// So, display the preferredTrack before the first default track
// and the subtitles/captions track before the descriptions track
if(preferredTrack){preferredTrack.mode='showing';}else if(firstCaptions){firstCaptions.mode='showing';}else if(firstDesc){firstDesc.mode='showing';}};/**
   * Turn display of {@link TextTrack}'s from the current state into the other state.
   * There are only two states:
   * - 'shown'
   * - 'hidden'
   *
   * @listens Player#loadstart
   */TextTrackDisplay.prototype.toggleDisplay=function toggleDisplay(){if(this.player_.tech_&&this.player_.tech_.featuresNativeTextTracks){this.hide();}else {this.show();}};/**
   * Create the {@link Component}'s DOM element.
   *
   * @return {Element}
   *         The element that was created.
   */TextTrackDisplay.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-text-track-display'},{'aria-live':'off','aria-atomic':'true'});};/**
   * Clear all displayed {@link TextTrack}s.
   */TextTrackDisplay.prototype.clearDisplay=function clearDisplay(){if(typeof window$1.WebVTT==='function'){window$1.WebVTT.processCues(window$1,[],this.el_);}};/**
   * Update the displayed TextTrack when a either a {@link Player#texttrackchange} or
   * a {@link Player#fullscreenchange} is fired.
   *
   * @listens Player#texttrackchange
   * @listens Player#fullscreenchange
   */TextTrackDisplay.prototype.updateDisplay=function updateDisplay(){var tracks=this.player_.textTracks();this.clearDisplay();// Track display prioritization model: if multiple tracks are 'showing',
//  display the first 'subtitles' or 'captions' track which is 'showing',
//  otherwise display the first 'descriptions' track which is 'showing'
var descriptionsTrack=null;var captionsSubtitlesTrack=null;var i=tracks.length;while(i--){var track=tracks[i];if(track.mode==='showing'){if(track.kind==='descriptions'){descriptionsTrack=track;}else {captionsSubtitlesTrack=track;}}}if(captionsSubtitlesTrack){if(this.getAttribute('aria-live')!=='off'){this.setAttribute('aria-live','off');}this.updateForTrack(captionsSubtitlesTrack);}else if(descriptionsTrack){if(this.getAttribute('aria-live')!=='assertive'){this.setAttribute('aria-live','assertive');}this.updateForTrack(descriptionsTrack);}};/**
   * Add an {@link Texttrack} to to the {@link Tech}s {@link TextTrackList}.
   *
   * @param {TextTrack} track
   *        Text track object to be added to the list.
   */TextTrackDisplay.prototype.updateForTrack=function updateForTrack(track){if(typeof window$1.WebVTT!=='function'||!track.activeCues){return;}var cues=[];for(var _i=0;_i<track.activeCues.length;_i++){cues.push(track.activeCues[_i]);}window$1.WebVTT.processCues(window$1,cues,this.el_);if(!this.player_.textTrackSettings){return;}var overrides=this.player_.textTrackSettings.getValues();var i=cues.length;while(i--){var cue=cues[i];if(!cue){continue;}var cueDiv=cue.displayState;if(overrides.color){cueDiv.firstChild.style.color=overrides.color;}if(overrides.textOpacity){tryUpdateStyle(cueDiv.firstChild,'color',constructColor(overrides.color||'#fff',overrides.textOpacity));}if(overrides.backgroundColor){cueDiv.firstChild.style.backgroundColor=overrides.backgroundColor;}if(overrides.backgroundOpacity){tryUpdateStyle(cueDiv.firstChild,'backgroundColor',constructColor(overrides.backgroundColor||'#000',overrides.backgroundOpacity));}if(overrides.windowColor){if(overrides.windowOpacity){tryUpdateStyle(cueDiv,'backgroundColor',constructColor(overrides.windowColor,overrides.windowOpacity));}else {cueDiv.style.backgroundColor=overrides.windowColor;}}if(overrides.edgeStyle){if(overrides.edgeStyle==='dropshadow'){cueDiv.firstChild.style.textShadow='2px 2px 3px '+darkGray+', 2px 2px 4px '+darkGray+', 2px 2px 5px '+darkGray;}else if(overrides.edgeStyle==='raised'){cueDiv.firstChild.style.textShadow='1px 1px '+darkGray+', 2px 2px '+darkGray+', 3px 3px '+darkGray;}else if(overrides.edgeStyle==='depressed'){cueDiv.firstChild.style.textShadow='1px 1px '+lightGray+', 0 1px '+lightGray+', -1px -1px '+darkGray+', 0 -1px '+darkGray;}else if(overrides.edgeStyle==='uniform'){cueDiv.firstChild.style.textShadow='0 0 4px '+darkGray+', 0 0 4px '+darkGray+', 0 0 4px '+darkGray+', 0 0 4px '+darkGray;}}if(overrides.fontPercent&&overrides.fontPercent!==1){var fontSize=window$1.parseFloat(cueDiv.style.fontSize);cueDiv.style.fontSize=fontSize*overrides.fontPercent+'px';cueDiv.style.height='auto';cueDiv.style.top='auto';cueDiv.style.bottom='2px';}if(overrides.fontFamily&&overrides.fontFamily!=='default'){if(overrides.fontFamily==='small-caps'){cueDiv.firstChild.style.fontVariant='small-caps';}else {cueDiv.firstChild.style.fontFamily=fontMap[overrides.fontFamily];}}}};return TextTrackDisplay;}(Component);Component.registerComponent('TextTrackDisplay',TextTrackDisplay);/**
 * @file loading-spinner.js
 */ /**
 * A loading spinner for use during waiting/loading events.
 *
 * @extends Component
 */var LoadingSpinner=function(_Component){inherits(LoadingSpinner,_Component);function LoadingSpinner(){classCallCheck(this,LoadingSpinner);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the `LoadingSpinner`s DOM element.
   *
   * @return {Element}
   *         The dom element that gets created.
   */LoadingSpinner.prototype.createEl=function createEl$$1(){var isAudio=this.player_.isAudio();var playerType=this.localize(isAudio?'Audio Player':'Video Player');var controlText=createEl('span',{className:'vjs-control-text',innerHTML:this.localize('{1} is loading.',[playerType])});var el=_Component.prototype.createEl.call(this,'div',{className:'vjs-loading-spinner',dir:'ltr'});el.appendChild(controlText);return el;};return LoadingSpinner;}(Component);Component.registerComponent('LoadingSpinner',LoadingSpinner);/**
 * @file button.js
 */ /**
 * Base class for all buttons.
 *
 * @extends ClickableComponent
 */var Button=function(_ClickableComponent){inherits(Button,_ClickableComponent);function Button(){classCallCheck(this,Button);return possibleConstructorReturn(this,_ClickableComponent.apply(this,arguments));}/**
   * Create the `Button`s DOM element.
   *
   * @param {string} [tag="button"]
   *        The element's node type. This argument is IGNORED: no matter what
   *        is passed, it will always create a `button` element.
   *
   * @param {Object} [props={}]
   *        An object of properties that should be set on the element.
   *
   * @param {Object} [attributes={}]
   *        An object of attributes that should be set on the element.
   *
   * @return {Element}
   *         The element that gets created.
   */Button.prototype.createEl=function createEl(tag){var props=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var attributes=arguments.length>2&&arguments[2]!==undefined?arguments[2]:{};tag='button';props=assign$1({innerHTML:'<span aria-hidden="true" class="vjs-icon-placeholder"></span>',className:this.buildCSSClass()},props);// Add attributes for button element
attributes=assign$1({// Necessary since the default button type is "submit"
type:'button'},attributes);var el=Component.prototype.createEl.call(this,tag,props,attributes);this.createControlTextEl(el);return el;};/**
   * Add a child `Component` inside of this `Button`.
   *
   * @param {string|Component} child
   *        The name or instance of a child to add.
   *
   * @param {Object} [options={}]
   *        The key/value store of options that will get passed to children of
   *        the child.
   *
   * @return {Component}
   *         The `Component` that gets added as a child. When using a string the
   *         `Component` will get created by this process.
   *
   * @deprecated since version 5
   */Button.prototype.addChild=function addChild(child){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var className=this.constructor.name;log$1.warn('Adding an actionable (user controllable) child to a Button ('+className+') is not supported; use a ClickableComponent instead.');// Avoid the error message generated by ClickableComponent's addChild method
return Component.prototype.addChild.call(this,child,options);};/**
   * Enable the `Button` element so that it can be activated or clicked. Use this with
   * {@link Button#disable}.
   */Button.prototype.enable=function enable(){_ClickableComponent.prototype.enable.call(this);this.el_.removeAttribute('disabled');};/**
   * Disable the `Button` element so that it cannot be activated or clicked. Use this with
   * {@link Button#enable}.
   */Button.prototype.disable=function disable(){_ClickableComponent.prototype.disable.call(this);this.el_.setAttribute('disabled','disabled');};/**
   * This gets called when a `Button` has focus and `keydown` is triggered via a key
   * press.
   *
   * @param {EventTarget~Event} event
   *        The event that caused this function to get called.
   *
   * @listens keydown
   */Button.prototype.handleKeyPress=function handleKeyPress(event){// Ignore Space (32) or Enter (13) key operation, which is handled by the browser for a button.
if(event.which===32||event.which===13){return;}// Pass keypress handling up for unsupported keys
_ClickableComponent.prototype.handleKeyPress.call(this,event);};return Button;}(ClickableComponent);Component.registerComponent('Button',Button);/**
 * @file big-play-button.js
 */ /**
 * The initial play button that shows before the video has played. The hiding of the
 * `BigPlayButton` get done via CSS and `Player` states.
 *
 * @extends Button
 */var BigPlayButton=function(_Button){inherits(BigPlayButton,_Button);function BigPlayButton(player,options){classCallCheck(this,BigPlayButton);var _this=possibleConstructorReturn(this,_Button.call(this,player,options));_this.mouseused_=false;_this.on('mousedown',_this.handleMouseDown);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object. Always returns 'vjs-big-play-button'.
   */BigPlayButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-big-play-button';};/**
   * This gets called when a `BigPlayButton` "clicked". See {@link ClickableComponent}
   * for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */BigPlayButton.prototype.handleClick=function handleClick(event){var playPromise=this.player_.play();// exit early if clicked via the mouse
if(this.mouseused_&&event.clientX&&event.clientY){silencePromise(playPromise);return;}var cb=this.player_.getChild('controlBar');var playToggle=cb&&cb.getChild('playToggle');if(!playToggle){this.player_.focus();return;}var playFocus=function playFocus(){return playToggle.focus();};if(isPromise(playPromise)){playPromise.then(playFocus,function(){});}else {this.setTimeout(playFocus,1);}};BigPlayButton.prototype.handleKeyPress=function handleKeyPress(event){this.mouseused_=false;_Button.prototype.handleKeyPress.call(this,event);};BigPlayButton.prototype.handleMouseDown=function handleMouseDown(event){this.mouseused_=true;};return BigPlayButton;}(Button);/**
 * The text that should display over the `BigPlayButton`s controls. Added to for localization.
 *
 * @type {string}
 * @private
 */BigPlayButton.prototype.controlText_='Play Video';Component.registerComponent('BigPlayButton',BigPlayButton);/**
 * @file close-button.js
 */ /**
 * The `CloseButton` is a `{@link Button}` that fires a `close` event when
 * it gets clicked.
 *
 * @extends Button
 */var CloseButton=function(_Button){inherits(CloseButton,_Button);/**
   * Creates an instance of the this class.
   *
   * @param  {Player} player
   *         The `Player` that this class should be attached to.
   *
   * @param  {Object} [options]
   *         The key/value store of player options.
   */function CloseButton(player,options){classCallCheck(this,CloseButton);var _this=possibleConstructorReturn(this,_Button.call(this,player,options));_this.controlText(options&&options.controlText||_this.localize('Close'));return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */CloseButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-close-button '+_Button.prototype.buildCSSClass.call(this);};/**
   * This gets called when a `CloseButton` gets clicked. See
   * {@link ClickableComponent#handleClick} for more information on when this will be
   * triggered
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   * @fires CloseButton#close
   */CloseButton.prototype.handleClick=function handleClick(event){/**
     * Triggered when the a `CloseButton` is clicked.
     *
     * @event CloseButton#close
     * @type {EventTarget~Event}
     *
     * @property {boolean} [bubbles=false]
     *           set to false so that the close event does not
     *           bubble up to parents if there is no listener
     */this.trigger({type:'close',bubbles:false});};return CloseButton;}(Button);Component.registerComponent('CloseButton',CloseButton);/**
 * @file play-toggle.js
 */ /**
 * Button to toggle between play and pause.
 *
 * @extends Button
 */var PlayToggle=function(_Button){inherits(PlayToggle,_Button);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function PlayToggle(player,options){classCallCheck(this,PlayToggle);var _this=possibleConstructorReturn(this,_Button.call(this,player,options));_this.on(player,'play',_this.handlePlay);_this.on(player,'pause',_this.handlePause);_this.on(player,'ended',_this.handleEnded);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */PlayToggle.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-play-control '+_Button.prototype.buildCSSClass.call(this);};/**
   * This gets called when an `PlayToggle` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */PlayToggle.prototype.handleClick=function handleClick(event){if(this.player_.paused()){this.player_.play();}else {this.player_.pause();}};/**
   * This gets called once after the video has ended and the user seeks so that
   * we can change the replay button back to a play button.
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#seeked
   */PlayToggle.prototype.handleSeeked=function handleSeeked(event){this.removeClass('vjs-ended');if(this.player_.paused()){this.handlePause(event);}else {this.handlePlay(event);}};/**
   * Add the vjs-playing class to the element so it can change appearance.
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#play
   */PlayToggle.prototype.handlePlay=function handlePlay(event){this.removeClass('vjs-ended');this.removeClass('vjs-paused');this.addClass('vjs-playing');// change the button text to "Pause"
this.controlText('Pause');};/**
   * Add the vjs-paused class to the element so it can change appearance.
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#pause
   */PlayToggle.prototype.handlePause=function handlePause(event){this.removeClass('vjs-playing');this.addClass('vjs-paused');// change the button text to "Play"
this.controlText('Play');};/**
   * Add the vjs-ended class to the element so it can change appearance
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#ended
   */PlayToggle.prototype.handleEnded=function handleEnded(event){this.removeClass('vjs-playing');this.addClass('vjs-ended');// change the button text to "Replay"
this.controlText('Replay');// on the next seek remove the replay button
this.one(this.player_,'seeked',this.handleSeeked);};return PlayToggle;}(Button);/**
 * The text that should display over the `PlayToggle`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */PlayToggle.prototype.controlText_='Play';Component.registerComponent('PlayToggle',PlayToggle);/**
 * @file format-time.js
 * @module format-time
 */ /**
* Format seconds as a time string, H:MM:SS or M:SS. Supplying a guide (in seconds)
* will force a number of leading zeros to cover the length of the guide.
*
* @param {number} seconds
*        Number of seconds to be turned into a string
*
* @param {number} guide
*        Number (in seconds) to model the string after
*
* @return {string}
*         Time formatted as H:MM:SS or M:SS
*/var defaultImplementation=function defaultImplementation(seconds,guide){seconds=seconds<0?0:seconds;var s=Math.floor(seconds%60);var m=Math.floor(seconds/60%60);var h=Math.floor(seconds/3600);var gm=Math.floor(guide/60%60);var gh=Math.floor(guide/3600);// handle invalid times
if(isNaN(seconds)||seconds===Infinity){// '-' is false for all relational operators (e.g. <, >=) so this setting
// will add the minimum number of fields specified by the guide
h=m=s='-';}// Check if we need to show hours
h=h>0||gh>0?h+':':'';// If hours are showing, we may need to add a leading zero.
// Always show at least one digit of minutes.
m=((h||gm>=10)&&m<10?'0'+m:m)+':';// Check if leading zero is need for seconds
s=s<10?'0'+s:s;return h+m+s;};var implementation=defaultImplementation;/**
 * Replaces the default formatTime implementation with a custom implementation.
 *
 * @param {Function} customImplementation
 *        A function which will be used in place of the default formatTime implementation.
 *        Will receive the current time in seconds and the guide (in seconds) as arguments.
 */function setFormatTime(customImplementation){implementation=customImplementation;}/**
 * Resets formatTime to the default implementation.
 */function resetFormatTime(){implementation=defaultImplementation;}var formatTime=function(seconds){var guide=arguments.length>1&&arguments[1]!==undefined?arguments[1]:seconds;return implementation(seconds,guide);};/**
 * @file time-display.js
 */ /**
 * Displays the time left in the video
 *
 * @extends Component
 */var TimeDisplay=function(_Component){inherits(TimeDisplay,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function TimeDisplay(player,options){classCallCheck(this,TimeDisplay);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.throttledUpdateContent=throttle(bind(_this,_this.updateContent),25);_this.on(player,'timeupdate',_this.throttledUpdateContent);return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */TimeDisplay.prototype.createEl=function createEl$$1(plainName){var className=this.buildCSSClass();var el=_Component.prototype.createEl.call(this,'div',{className:className+' vjs-time-control vjs-control',innerHTML:'<span class="vjs-control-text">'+this.localize(this.labelText_)+'\xA0</span>'});this.contentEl_=createEl('span',{className:className+'-display'},{// tell screen readers not to automatically read the time as it changes
'aria-live':'off'});this.updateTextNode_();el.appendChild(this.contentEl_);return el;};TimeDisplay.prototype.dispose=function dispose(){this.contentEl_=null;this.textNode_=null;_Component.prototype.dispose.call(this);};/**
   * Updates the "remaining time" text node with new content using the
   * contents of the `formattedTime_` property.
   *
   * @private
   */TimeDisplay.prototype.updateTextNode_=function updateTextNode_(){if(!this.contentEl_){return;}while(this.contentEl_.firstChild){this.contentEl_.removeChild(this.contentEl_.firstChild);}this.textNode_=document$1.createTextNode(this.formattedTime_||this.formatTime_(0));this.contentEl_.appendChild(this.textNode_);};/**
   * Generates a formatted time for this component to use in display.
   *
   * @param  {number} time
   *         A numeric time, in seconds.
   *
   * @return {string}
   *         A formatted time
   *
   * @private
   */TimeDisplay.prototype.formatTime_=function formatTime_(time){return formatTime(time);};/**
   * Updates the time display text node if it has what was passed in changed
   * the formatted time.
   *
   * @param {number} time
   *        The time to update to
   *
   * @private
   */TimeDisplay.prototype.updateFormattedTime_=function updateFormattedTime_(time){var formattedTime=this.formatTime_(time);if(formattedTime===this.formattedTime_){return;}this.formattedTime_=formattedTime;this.requestAnimationFrame(this.updateTextNode_);};/**
   * To be filled out in the child class, should update the displayed time
   * in accordance with the fact that the current time has changed.
   *
   * @param {EventTarget~Event} [event]
   *        The `timeupdate`  event that caused this to run.
   *
   * @listens Player#timeupdate
   */TimeDisplay.prototype.updateContent=function updateContent(event){};return TimeDisplay;}(Component);/**
 * The text that is added to the `TimeDisplay` for screen reader users.
 *
 * @type {string}
 * @private
 */TimeDisplay.prototype.labelText_='Time';/**
 * The text that should display over the `TimeDisplay`s controls. Added to for localization.
 *
 * @type {string}
 * @private
 *
 * @deprecated in v7; controlText_ is not used in non-active display Components
 */TimeDisplay.prototype.controlText_='Time';Component.registerComponent('TimeDisplay',TimeDisplay);/**
 * @file current-time-display.js
 */ /**
 * Displays the current time
 *
 * @extends Component
 */var CurrentTimeDisplay=function(_TimeDisplay){inherits(CurrentTimeDisplay,_TimeDisplay);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function CurrentTimeDisplay(player,options){classCallCheck(this,CurrentTimeDisplay);var _this=possibleConstructorReturn(this,_TimeDisplay.call(this,player,options));_this.on(player,'ended',_this.handleEnded);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */CurrentTimeDisplay.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-current-time';};/**
   * Update current time display
   *
   * @param {EventTarget~Event} [event]
   *        The `timeupdate` event that caused this function to run.
   *
   * @listens Player#timeupdate
   */CurrentTimeDisplay.prototype.updateContent=function updateContent(event){// Allows for smooth scrubbing, when player can't keep up.
var time=this.player_.scrubbing()?this.player_.getCache().currentTime:this.player_.currentTime();this.updateFormattedTime_(time);};/**
   * When the player fires ended there should be no time left. Sadly
   * this is not always the case, lets make it seem like that is the case
   * for users.
   *
   * @param {EventTarget~Event} [event]
   *        The `ended` event that caused this to run.
   *
   * @listens Player#ended
   */CurrentTimeDisplay.prototype.handleEnded=function handleEnded(event){if(!this.player_.duration()){return;}this.updateFormattedTime_(this.player_.duration());};return CurrentTimeDisplay;}(TimeDisplay);/**
 * The text that is added to the `CurrentTimeDisplay` for screen reader users.
 *
 * @type {string}
 * @private
 */CurrentTimeDisplay.prototype.labelText_='Current Time';/**
 * The text that should display over the `CurrentTimeDisplay`s controls. Added to for localization.
 *
 * @type {string}
 * @private
 *
 * @deprecated in v7; controlText_ is not used in non-active display Components
 */CurrentTimeDisplay.prototype.controlText_='Current Time';Component.registerComponent('CurrentTimeDisplay',CurrentTimeDisplay);/**
 * @file duration-display.js
 */ /**
 * Displays the duration
 *
 * @extends Component
 */var DurationDisplay=function(_TimeDisplay){inherits(DurationDisplay,_TimeDisplay);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function DurationDisplay(player,options){classCallCheck(this,DurationDisplay);// we do not want to/need to throttle duration changes,
// as they should always display the changed duration as
// it has changed
var _this=possibleConstructorReturn(this,_TimeDisplay.call(this,player,options));_this.on(player,'durationchange',_this.updateContent);// Also listen for timeupdate (in the parent) and loadedmetadata because removing those
// listeners could have broken dependent applications/libraries. These
// can likely be removed for 7.0.
_this.on(player,'loadedmetadata',_this.throttledUpdateContent);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */DurationDisplay.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-duration';};/**
   * Update duration time display.
   *
   * @param {EventTarget~Event} [event]
   *        The `durationchange`, `timeupdate`, or `loadedmetadata` event that caused
   *        this function to be called.
   *
   * @listens Player#durationchange
   * @listens Player#timeupdate
   * @listens Player#loadedmetadata
   */DurationDisplay.prototype.updateContent=function updateContent(event){var duration=this.player_.duration();if(duration&&this.duration_!==duration){this.duration_=duration;this.updateFormattedTime_(duration);}};return DurationDisplay;}(TimeDisplay);/**
 * The text that is added to the `DurationDisplay` for screen reader users.
 *
 * @type {string}
 * @private
 */DurationDisplay.prototype.labelText_='Duration';/**
 * The text that should display over the `DurationDisplay`s controls. Added to for localization.
 *
 * @type {string}
 * @private
 *
 * @deprecated in v7; controlText_ is not used in non-active display Components
 */DurationDisplay.prototype.controlText_='Duration';Component.registerComponent('DurationDisplay',DurationDisplay);/**
 * @file time-divider.js
 */ /**
 * The separator between the current time and duration.
 * Can be hidden if it's not needed in the design.
 *
 * @extends Component
 */var TimeDivider=function(_Component){inherits(TimeDivider,_Component);function TimeDivider(){classCallCheck(this,TimeDivider);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the component's DOM element
   *
   * @return {Element}
   *         The element that was created.
   */TimeDivider.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-time-control vjs-time-divider',innerHTML:'<div><span>/</span></div>'});};return TimeDivider;}(Component);Component.registerComponent('TimeDivider',TimeDivider);/**
 * @file remaining-time-display.js
 */ /**
 * Displays the time left in the video
 *
 * @extends Component
 */var RemainingTimeDisplay=function(_TimeDisplay){inherits(RemainingTimeDisplay,_TimeDisplay);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function RemainingTimeDisplay(player,options){classCallCheck(this,RemainingTimeDisplay);var _this=possibleConstructorReturn(this,_TimeDisplay.call(this,player,options));_this.on(player,'durationchange',_this.throttledUpdateContent);_this.on(player,'ended',_this.handleEnded);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */RemainingTimeDisplay.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-remaining-time';};/**
   * The remaining time display prefixes numbers with a "minus" character.
   *
   * @param  {number} time
   *         A numeric time, in seconds.
   *
   * @return {string}
   *         A formatted time
   *
   * @private
   */RemainingTimeDisplay.prototype.formatTime_=function formatTime_(time){// TODO: The "-" should be decorative, and not announced by a screen reader
return '-'+_TimeDisplay.prototype.formatTime_.call(this,time);};/**
   * Update remaining time display.
   *
   * @param {EventTarget~Event} [event]
   *        The `timeupdate` or `durationchange` event that caused this to run.
   *
   * @listens Player#timeupdate
   * @listens Player#durationchange
   */RemainingTimeDisplay.prototype.updateContent=function updateContent(event){if(!this.player_.duration()){return;}// @deprecated We should only use remainingTimeDisplay
// as of video.js 7
if(this.player_.remainingTimeDisplay){this.updateFormattedTime_(this.player_.remainingTimeDisplay());}else {this.updateFormattedTime_(this.player_.remainingTime());}};/**
   * When the player fires ended there should be no time left. Sadly
   * this is not always the case, lets make it seem like that is the case
   * for users.
   *
   * @param {EventTarget~Event} [event]
   *        The `ended` event that caused this to run.
   *
   * @listens Player#ended
   */RemainingTimeDisplay.prototype.handleEnded=function handleEnded(event){if(!this.player_.duration()){return;}this.updateFormattedTime_(0);};return RemainingTimeDisplay;}(TimeDisplay);/**
 * The text that is added to the `RemainingTimeDisplay` for screen reader users.
 *
 * @type {string}
 * @private
 */RemainingTimeDisplay.prototype.labelText_='Remaining Time';/**
 * The text that should display over the `RemainingTimeDisplay`s controls. Added to for localization.
 *
 * @type {string}
 * @private
 *
 * @deprecated in v7; controlText_ is not used in non-active display Components
 */RemainingTimeDisplay.prototype.controlText_='Remaining Time';Component.registerComponent('RemainingTimeDisplay',RemainingTimeDisplay);/**
 * @file live-display.js
 */ // TODO - Future make it click to snap to live
/**
 * Displays the live indicator when duration is Infinity.
 *
 * @extends Component
 */var LiveDisplay=function(_Component){inherits(LiveDisplay,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function LiveDisplay(player,options){classCallCheck(this,LiveDisplay);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.updateShowing();_this.on(_this.player(),'durationchange',_this.updateShowing);return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */LiveDisplay.prototype.createEl=function createEl$$1(){var el=_Component.prototype.createEl.call(this,'div',{className:'vjs-live-control vjs-control'});this.contentEl_=createEl('div',{className:'vjs-live-display',innerHTML:'<span class="vjs-control-text">'+this.localize('Stream Type')+'\xA0</span>'+this.localize('LIVE')},{'aria-live':'off'});el.appendChild(this.contentEl_);return el;};LiveDisplay.prototype.dispose=function dispose(){this.contentEl_=null;_Component.prototype.dispose.call(this);};/**
   * Check the duration to see if the LiveDisplay should be showing or not. Then show/hide
   * it accordingly
   *
   * @param {EventTarget~Event} [event]
   *        The {@link Player#durationchange} event that caused this function to run.
   *
   * @listens Player#durationchange
   */LiveDisplay.prototype.updateShowing=function updateShowing(event){if(this.player().duration()===Infinity){this.show();}else {this.hide();}};return LiveDisplay;}(Component);Component.registerComponent('LiveDisplay',LiveDisplay);/**
 * @file slider.js
 */ /**
 * The base functionality for a slider. Can be vertical or horizontal.
 * For instance the volume bar or the seek bar on a video is a slider.
 *
 * @extends Component
 */var Slider=function(_Component){inherits(Slider,_Component);/**
   * Create an instance of this class
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function Slider(player,options){classCallCheck(this,Slider);// Set property names to bar to match with the child Slider class is looking for
var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.bar=_this.getChild(_this.options_.barName);// Set a horizontal or vertical class on the slider depending on the slider type
_this.vertical(!!_this.options_.vertical);_this.enable();return _this;}/**
   * Are controls are currently enabled for this slider or not.
   *
   * @return {boolean}
   *         true if controls are enabled, false otherwise
   */Slider.prototype.enabled=function enabled(){return this.enabled_;};/**
   * Enable controls for this slider if they are disabled
   */Slider.prototype.enable=function enable(){if(this.enabled()){return;}this.on('mousedown',this.handleMouseDown);this.on('touchstart',this.handleMouseDown);this.on('focus',this.handleFocus);this.on('blur',this.handleBlur);this.on('click',this.handleClick);this.on(this.player_,'controlsvisible',this.update);if(this.playerEvent){this.on(this.player_,this.playerEvent,this.update);}this.removeClass('disabled');this.setAttribute('tabindex',0);this.enabled_=true;};/**
   * Disable controls for this slider if they are enabled
   */Slider.prototype.disable=function disable(){if(!this.enabled()){return;}var doc=this.bar.el_.ownerDocument;this.off('mousedown',this.handleMouseDown);this.off('touchstart',this.handleMouseDown);this.off('focus',this.handleFocus);this.off('blur',this.handleBlur);this.off('click',this.handleClick);this.off(this.player_,'controlsvisible',this.update);this.off(doc,'mousemove',this.handleMouseMove);this.off(doc,'mouseup',this.handleMouseUp);this.off(doc,'touchmove',this.handleMouseMove);this.off(doc,'touchend',this.handleMouseUp);this.removeAttribute('tabindex');this.addClass('disabled');if(this.playerEvent){this.off(this.player_,this.playerEvent,this.update);}this.enabled_=false;};/**
   * Create the `Button`s DOM element.
   *
   * @param {string} type
   *        Type of element to create.
   *
   * @param {Object} [props={}]
   *        List of properties in Object form.
   *
   * @param {Object} [attributes={}]
   *        list of attributes in Object form.
   *
   * @return {Element}
   *         The element that gets created.
   */Slider.prototype.createEl=function createEl$$1(type){var props=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var attributes=arguments.length>2&&arguments[2]!==undefined?arguments[2]:{};// Add the slider element class to all sub classes
props.className=props.className+' vjs-slider';props=assign$1({tabIndex:0},props);attributes=assign$1({'role':'slider','aria-valuenow':0,'aria-valuemin':0,'aria-valuemax':100,'tabIndex':0},attributes);return _Component.prototype.createEl.call(this,type,props,attributes);};/**
   * Handle `mousedown` or `touchstart` events on the `Slider`.
   *
   * @param {EventTarget~Event} event
   *        `mousedown` or `touchstart` event that triggered this function
   *
   * @listens mousedown
   * @listens touchstart
   * @fires Slider#slideractive
   */Slider.prototype.handleMouseDown=function handleMouseDown(event){var doc=this.bar.el_.ownerDocument;if(event.type==='mousedown'){event.preventDefault();}// Do not call preventDefault() on touchstart in Chrome
// to avoid console warnings. Use a 'touch-action: none' style
// instead to prevent unintented scrolling.
// https://developers.google.com/web/updates/2017/01/scrolling-intervention
if(event.type==='touchstart'&&!IS_CHROME){event.preventDefault();}blockTextSelection();this.addClass('vjs-sliding');/**
     * Triggered when the slider is in an active state
     *
     * @event Slider#slideractive
     * @type {EventTarget~Event}
     */this.trigger('slideractive');this.on(doc,'mousemove',this.handleMouseMove);this.on(doc,'mouseup',this.handleMouseUp);this.on(doc,'touchmove',this.handleMouseMove);this.on(doc,'touchend',this.handleMouseUp);this.handleMouseMove(event);};/**
   * Handle the `mousemove`, `touchmove`, and `mousedown` events on this `Slider`.
   * The `mousemove` and `touchmove` events will only only trigger this function during
   * `mousedown` and `touchstart`. This is due to {@link Slider#handleMouseDown} and
   * {@link Slider#handleMouseUp}.
   *
   * @param {EventTarget~Event} event
   *        `mousedown`, `mousemove`, `touchstart`, or `touchmove` event that triggered
   *        this function
   *
   * @listens mousemove
   * @listens touchmove
   */Slider.prototype.handleMouseMove=function handleMouseMove(event){};/**
   * Handle `mouseup` or `touchend` events on the `Slider`.
   *
   * @param {EventTarget~Event} event
   *        `mouseup` or `touchend` event that triggered this function.
   *
   * @listens touchend
   * @listens mouseup
   * @fires Slider#sliderinactive
   */Slider.prototype.handleMouseUp=function handleMouseUp(){var doc=this.bar.el_.ownerDocument;unblockTextSelection();this.removeClass('vjs-sliding');/**
     * Triggered when the slider is no longer in an active state.
     *
     * @event Slider#sliderinactive
     * @type {EventTarget~Event}
     */this.trigger('sliderinactive');this.off(doc,'mousemove',this.handleMouseMove);this.off(doc,'mouseup',this.handleMouseUp);this.off(doc,'touchmove',this.handleMouseMove);this.off(doc,'touchend',this.handleMouseUp);this.update();};/**
   * Update the progress bar of the `Slider`.
   *
   * @returns {number}
   *          The percentage of progress the progress bar represents as a
   *          number from 0 to 1.
   */Slider.prototype.update=function update(){// In VolumeBar init we have a setTimeout for update that pops and update
// to the end of the execution stack. The player is destroyed before then
// update will cause an error
if(!this.el_){return;}// If scrubbing, we could use a cached value to make the handle keep up
// with the user's mouse. On HTML5 browsers scrubbing is really smooth, but
// some flash players are slow, so we might want to utilize this later.
// var progress =  (this.player_.scrubbing()) ? this.player_.getCache().currentTime / this.player_.duration() : this.player_.currentTime() / this.player_.duration();
var progress=this.getPercent();var bar=this.bar;// If there's no bar...
if(!bar){return;}// Protect against no duration and other division issues
if(typeof progress!=='number'||progress!==progress||progress<0||progress===Infinity){progress=0;}// Convert to a percentage for setting
var percentage=(progress*100).toFixed(2)+'%';var style=bar.el().style;// Set the new bar width or height
if(this.vertical()){style.height=percentage;}else {style.width=percentage;}return progress;};/**
   * Calculate distance for slider
   *
   * @param {EventTarget~Event} event
   *        The event that caused this function to run.
   *
   * @return {number}
   *         The current position of the Slider.
   *         - postition.x for vertical `Slider`s
   *         - postition.y for horizontal `Slider`s
   */Slider.prototype.calculateDistance=function calculateDistance(event){var position=getPointerPosition(this.el_,event);if(this.vertical()){return position.y;}return position.x;};/**
   * Handle a `focus` event on this `Slider`.
   *
   * @param {EventTarget~Event} event
   *        The `focus` event that caused this function to run.
   *
   * @listens focus
   */Slider.prototype.handleFocus=function handleFocus(){this.on(this.bar.el_.ownerDocument,'keydown',this.handleKeyPress);};/**
   * Handle a `keydown` event on the `Slider`. Watches for left, rigth, up, and down
   * arrow keys. This function will only be called when the slider has focus. See
   * {@link Slider#handleFocus} and {@link Slider#handleBlur}.
   *
   * @param {EventTarget~Event} event
   *        the `keydown` event that caused this function to run.
   *
   * @listens keydown
   */Slider.prototype.handleKeyPress=function handleKeyPress(event){// Left and Down Arrows
if(event.which===37||event.which===40){event.preventDefault();this.stepBack();// Up and Right Arrows
}else if(event.which===38||event.which===39){event.preventDefault();this.stepForward();}};/**
   * Handle a `blur` event on this `Slider`.
   *
   * @param {EventTarget~Event} event
   *        The `blur` event that caused this function to run.
   *
   * @listens blur
   */Slider.prototype.handleBlur=function handleBlur(){this.off(this.bar.el_.ownerDocument,'keydown',this.handleKeyPress);};/**
   * Listener for click events on slider, used to prevent clicks
   *   from bubbling up to parent elements like button menus.
   *
   * @param {Object} event
   *        Event that caused this object to run
   */Slider.prototype.handleClick=function handleClick(event){event.stopImmediatePropagation();event.preventDefault();};/**
   * Get/set if slider is horizontal for vertical
   *
   * @param {boolean} [bool]
   *        - true if slider is vertical,
   *        - false is horizontal
   *
   * @return {boolean}
   *         - true if slider is vertical, and getting
   *         - false if the slider is horizontal, and getting
   */Slider.prototype.vertical=function vertical(bool){if(bool===undefined){return this.vertical_||false;}this.vertical_=!!bool;if(this.vertical_){this.addClass('vjs-slider-vertical');}else {this.addClass('vjs-slider-horizontal');}};return Slider;}(Component);Component.registerComponent('Slider',Slider);/**
 * @file load-progress-bar.js
 */ /**
 * Shows loading progress
 *
 * @extends Component
 */var LoadProgressBar=function(_Component){inherits(LoadProgressBar,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function LoadProgressBar(player,options){classCallCheck(this,LoadProgressBar);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.partEls_=[];_this.on(player,'progress',_this.update);return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */LoadProgressBar.prototype.createEl=function createEl$$1(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-load-progress',innerHTML:'<span class="vjs-control-text"><span>'+this.localize('Loaded')+'</span>: 0%</span>'});};LoadProgressBar.prototype.dispose=function dispose(){this.partEls_=null;_Component.prototype.dispose.call(this);};/**
   * Update progress bar
   *
   * @param {EventTarget~Event} [event]
   *        The `progress` event that caused this function to run.
   *
   * @listens Player#progress
   */LoadProgressBar.prototype.update=function update(event){var buffered=this.player_.buffered();var duration=this.player_.duration();var bufferedEnd=this.player_.bufferedEnd();var children=this.partEls_;// get the percent width of a time compared to the total end
var percentify=function percentify(time,end){// no NaN
var percent=time/end||0;return (percent>=1?1:percent)*100+'%';};// update the width of the progress bar
this.el_.style.width=percentify(bufferedEnd,duration);// add child elements to represent the individual buffered time ranges
for(var i=0;i<buffered.length;i++){var start=buffered.start(i);var end=buffered.end(i);var part=children[i];if(!part){part=this.el_.appendChild(createEl());children[i]=part;}// set the percent based on the width of the progress bar (bufferedEnd)
part.style.left=percentify(start,bufferedEnd);part.style.width=percentify(end-start,bufferedEnd);}// remove unused buffered range elements
for(var _i=children.length;_i>buffered.length;_i--){this.el_.removeChild(children[_i-1]);}children.length=buffered.length;};return LoadProgressBar;}(Component);Component.registerComponent('LoadProgressBar',LoadProgressBar);/**
 * @file time-tooltip.js
 */ /**
 * Time tooltips display a time above the progress bar.
 *
 * @extends Component
 */var TimeTooltip=function(_Component){inherits(TimeTooltip,_Component);function TimeTooltip(){classCallCheck(this,TimeTooltip);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the time tooltip DOM element
   *
   * @return {Element}
   *         The element that was created.
   */TimeTooltip.prototype.createEl=function createEl$$1(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-time-tooltip'});};/**
   * Updates the position of the time tooltip relative to the `SeekBar`.
   *
   * @param {Object} seekBarRect
   *        The `ClientRect` for the {@link SeekBar} element.
   *
   * @param {number} seekBarPoint
   *        A number from 0 to 1, representing a horizontal reference point
   *        from the left edge of the {@link SeekBar}
   */TimeTooltip.prototype.update=function update(seekBarRect,seekBarPoint,content){var tooltipRect=getBoundingClientRect(this.el_);var playerRect=getBoundingClientRect(this.player_.el());var seekBarPointPx=seekBarRect.width*seekBarPoint;// do nothing if either rect isn't available
// for example, if the player isn't in the DOM for testing
if(!playerRect||!tooltipRect){return;}// This is the space left of the `seekBarPoint` available within the bounds
// of the player. We calculate any gap between the left edge of the player
// and the left edge of the `SeekBar` and add the number of pixels in the
// `SeekBar` before hitting the `seekBarPoint`
var spaceLeftOfPoint=seekBarRect.left-playerRect.left+seekBarPointPx;// This is the space right of the `seekBarPoint` available within the bounds
// of the player. We calculate the number of pixels from the `seekBarPoint`
// to the right edge of the `SeekBar` and add to that any gap between the
// right edge of the `SeekBar` and the player.
var spaceRightOfPoint=seekBarRect.width-seekBarPointPx+(playerRect.right-seekBarRect.right);// This is the number of pixels by which the tooltip will need to be pulled
// further to the right to center it over the `seekBarPoint`.
var pullTooltipBy=tooltipRect.width/2;// Adjust the `pullTooltipBy` distance to the left or right depending on
// the results of the space calculations above.
if(spaceLeftOfPoint<pullTooltipBy){pullTooltipBy+=pullTooltipBy-spaceLeftOfPoint;}else if(spaceRightOfPoint<pullTooltipBy){pullTooltipBy=spaceRightOfPoint;}// Due to the imprecision of decimal/ratio based calculations and varying
// rounding behaviors, there are cases where the spacing adjustment is off
// by a pixel or two. This adds insurance to these calculations.
if(pullTooltipBy<0){pullTooltipBy=0;}else if(pullTooltipBy>tooltipRect.width){pullTooltipBy=tooltipRect.width;}this.el_.style.right='-'+pullTooltipBy+'px';textContent(this.el_,content);};return TimeTooltip;}(Component);Component.registerComponent('TimeTooltip',TimeTooltip);/**
 * @file play-progress-bar.js
 */ /**
 * Used by {@link SeekBar} to display media playback progress as part of the
 * {@link ProgressControl}.
 *
 * @extends Component
 */var PlayProgressBar=function(_Component){inherits(PlayProgressBar,_Component);function PlayProgressBar(){classCallCheck(this,PlayProgressBar);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the the DOM element for this class.
   *
   * @return {Element}
   *         The element that was created.
   */PlayProgressBar.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-play-progress vjs-slider-bar',innerHTML:'<span class="vjs-control-text"><span>'+this.localize('Progress')+'</span>: 0%</span>'});};/**
   * Enqueues updates to its own DOM as well as the DOM of its
   * {@link TimeTooltip} child.
   *
   * @param {Object} seekBarRect
   *        The `ClientRect` for the {@link SeekBar} element.
   *
   * @param {number} seekBarPoint
   *        A number from 0 to 1, representing a horizontal reference point
   *        from the left edge of the {@link SeekBar}
   */PlayProgressBar.prototype.update=function update(seekBarRect,seekBarPoint){var _this2=this;// If there is an existing rAF ID, cancel it so we don't over-queue.
if(this.rafId_){this.cancelAnimationFrame(this.rafId_);}this.rafId_=this.requestAnimationFrame(function(){var time=_this2.player_.scrubbing()?_this2.player_.getCache().currentTime:_this2.player_.currentTime();var content=formatTime(time,_this2.player_.duration());var timeTooltip=_this2.getChild('timeTooltip');if(timeTooltip){timeTooltip.update(seekBarRect,seekBarPoint,content);}});};return PlayProgressBar;}(Component);/**
 * Default options for {@link PlayProgressBar}.
 *
 * @type {Object}
 * @private
 */PlayProgressBar.prototype.options_={children:[]};// Time tooltips should not be added to a player on mobile devices or IE8
if((!IE_VERSION||IE_VERSION>8)&&!IS_IOS&&!IS_ANDROID){PlayProgressBar.prototype.options_.children.push('timeTooltip');}Component.registerComponent('PlayProgressBar',PlayProgressBar);/**
 * @file mouse-time-display.js
 */ /**
 * The {@link MouseTimeDisplay} component tracks mouse movement over the
 * {@link ProgressControl}. It displays an indicator and a {@link TimeTooltip}
 * indicating the time which is represented by a given point in the
 * {@link ProgressControl}.
 *
 * @extends Component
 */var MouseTimeDisplay=function(_Component){inherits(MouseTimeDisplay,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The {@link Player} that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function MouseTimeDisplay(player,options){classCallCheck(this,MouseTimeDisplay);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.update=throttle(bind(_this,_this.update),25);return _this;}/**
   * Create the DOM element for this class.
   *
   * @return {Element}
   *         The element that was created.
   */MouseTimeDisplay.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-mouse-display'});};/**
   * Enqueues updates to its own DOM as well as the DOM of its
   * {@link TimeTooltip} child.
   *
   * @param {Object} seekBarRect
   *        The `ClientRect` for the {@link SeekBar} element.
   *
   * @param {number} seekBarPoint
   *        A number from 0 to 1, representing a horizontal reference point
   *        from the left edge of the {@link SeekBar}
   */MouseTimeDisplay.prototype.update=function update(seekBarRect,seekBarPoint){var _this2=this;// If there is an existing rAF ID, cancel it so we don't over-queue.
if(this.rafId_){this.cancelAnimationFrame(this.rafId_);}this.rafId_=this.requestAnimationFrame(function(){var duration=_this2.player_.duration();var content=formatTime(seekBarPoint*duration,duration);_this2.el_.style.left=seekBarRect.width*seekBarPoint+'px';_this2.getChild('timeTooltip').update(seekBarRect,seekBarPoint,content);});};return MouseTimeDisplay;}(Component);/**
 * Default options for `MouseTimeDisplay`
 *
 * @type {Object}
 * @private
 */MouseTimeDisplay.prototype.options_={children:['timeTooltip']};Component.registerComponent('MouseTimeDisplay',MouseTimeDisplay);/**
 * @file seek-bar.js
 */ // The number of seconds the `step*` functions move the timeline.
var STEP_SECONDS=5;// The interval at which the bar should update as it progresses.
var UPDATE_REFRESH_INTERVAL=30;/**
 * Seek bar and container for the progress bars. Uses {@link PlayProgressBar}
 * as its `bar`.
 *
 * @extends Slider
 */var SeekBar=function(_Slider){inherits(SeekBar,_Slider);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function SeekBar(player,options){classCallCheck(this,SeekBar);var _this=possibleConstructorReturn(this,_Slider.call(this,player,options));_this.setEventHandlers_();return _this;}/**
   * Sets the event handlers
   *
   * @private
   */SeekBar.prototype.setEventHandlers_=function setEventHandlers_(){var _this2=this;this.update=throttle(bind(this,this.update),UPDATE_REFRESH_INTERVAL);this.on(this.player_,'timeupdate',this.update);this.on(this.player_,'ended',this.handleEnded);// when playing, let's ensure we smoothly update the play progress bar
// via an interval
this.updateInterval=null;this.on(this.player_,['playing'],function(){_this2.clearInterval(_this2.updateInterval);_this2.updateInterval=_this2.setInterval(function(){_this2.requestAnimationFrame(function(){_this2.update();});},UPDATE_REFRESH_INTERVAL);});this.on(this.player_,['ended','pause','waiting'],function(){_this2.clearInterval(_this2.updateInterval);});this.on(this.player_,['timeupdate','ended'],this.update);};/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */SeekBar.prototype.createEl=function createEl$$1(){return _Slider.prototype.createEl.call(this,'div',{className:'vjs-progress-holder'},{'aria-label':this.localize('Progress Bar')});};/**
   * This function updates the play progress bar and accessiblity
   * attributes to whatever is passed in.
   *
   * @param {number} currentTime
   *        The currentTime value that should be used for accessiblity
   *
   * @param {number} percent
   *        The percentage as a decimal that the bar should be filled from 0-1.
   *
   * @private
   */SeekBar.prototype.update_=function update_(currentTime,percent){var duration=this.player_.duration();// machine readable value of progress bar (percentage complete)
this.el_.setAttribute('aria-valuenow',(percent*100).toFixed(2));// human readable value of progress bar (time complete)
this.el_.setAttribute('aria-valuetext',this.localize('progress bar timing: currentTime={1} duration={2}',[formatTime(currentTime,duration),formatTime(duration,duration)],'{1} of {2}'));// Update the `PlayProgressBar`.
this.bar.update(getBoundingClientRect(this.el_),percent);};/**
   * Update the seek bar's UI.
   *
   * @param {EventTarget~Event} [event]
   *        The `timeupdate` or `ended` event that caused this to run.
   *
   * @listens Player#timeupdate
   *
   * @returns {number}
   *          The current percent at a number from 0-1
   */SeekBar.prototype.update=function update(event){var percent=_Slider.prototype.update.call(this);this.update_(this.getCurrentTime_(),percent);return percent;};/**
   * Get the value of current time but allows for smooth scrubbing,
   * when player can't keep up.
   *
   * @return {number}
   *         The current time value to display
   *
   * @private
   */SeekBar.prototype.getCurrentTime_=function getCurrentTime_(){return this.player_.scrubbing()?this.player_.getCache().currentTime:this.player_.currentTime();};/**
   * We want the seek bar to be full on ended
   * no matter what the actual internal values are. so we force it.
   *
   * @param {EventTarget~Event} [event]
   *        The `timeupdate` or `ended` event that caused this to run.
   *
   * @listens Player#ended
   */SeekBar.prototype.handleEnded=function handleEnded(event){this.update_(this.player_.duration(),1);};/**
   * Get the percentage of media played so far.
   *
   * @return {number}
   *         The percentage of media played so far (0 to 1).
   */SeekBar.prototype.getPercent=function getPercent(){var percent=this.getCurrentTime_()/this.player_.duration();return percent>=1?1:percent;};/**
   * Handle mouse down on seek bar
   *
   * @param {EventTarget~Event} event
   *        The `mousedown` event that caused this to run.
   *
   * @listens mousedown
   */SeekBar.prototype.handleMouseDown=function handleMouseDown(event){if(!isSingleLeftClick(event)){return;}// Stop event propagation to prevent double fire in progress-control.js
event.stopPropagation();this.player_.scrubbing(true);this.videoWasPlaying=!this.player_.paused();this.player_.pause();_Slider.prototype.handleMouseDown.call(this,event);};/**
   * Handle mouse move on seek bar
   *
   * @param {EventTarget~Event} event
   *        The `mousemove` event that caused this to run.
   *
   * @listens mousemove
   */SeekBar.prototype.handleMouseMove=function handleMouseMove(event){if(!isSingleLeftClick(event)){return;}var newTime=this.calculateDistance(event)*this.player_.duration();// Don't let video end while scrubbing.
if(newTime===this.player_.duration()){newTime=newTime-0.1;}// Set new time (tell player to seek to new time)
this.player_.currentTime(newTime);};SeekBar.prototype.enable=function enable(){_Slider.prototype.enable.call(this);var mouseTimeDisplay=this.getChild('mouseTimeDisplay');if(!mouseTimeDisplay){return;}mouseTimeDisplay.show();};SeekBar.prototype.disable=function disable(){_Slider.prototype.disable.call(this);var mouseTimeDisplay=this.getChild('mouseTimeDisplay');if(!mouseTimeDisplay){return;}mouseTimeDisplay.hide();};/**
   * Handle mouse up on seek bar
   *
   * @param {EventTarget~Event} event
   *        The `mouseup` event that caused this to run.
   *
   * @listens mouseup
   */SeekBar.prototype.handleMouseUp=function handleMouseUp(event){_Slider.prototype.handleMouseUp.call(this,event);// Stop event propagation to prevent double fire in progress-control.js
if(event){event.stopPropagation();}this.player_.scrubbing(false);/**
     * Trigger timeupdate because we're done seeking and the time has changed.
     * This is particularly useful for if the player is paused to time the time displays.
     *
     * @event Tech#timeupdate
     * @type {EventTarget~Event}
     */this.player_.trigger({type:'timeupdate',target:this,manuallyTriggered:true});if(this.videoWasPlaying){silencePromise(this.player_.play());}};/**
   * Move more quickly fast forward for keyboard-only users
   */SeekBar.prototype.stepForward=function stepForward(){this.player_.currentTime(this.player_.currentTime()+STEP_SECONDS);};/**
   * Move more quickly rewind for keyboard-only users
   */SeekBar.prototype.stepBack=function stepBack(){this.player_.currentTime(this.player_.currentTime()-STEP_SECONDS);};/**
   * Toggles the playback state of the player
   * This gets called when enter or space is used on the seekbar
   *
   * @param {EventTarget~Event} event
   *        The `keydown` event that caused this function to be called
   *
   */SeekBar.prototype.handleAction=function handleAction(event){if(this.player_.paused()){this.player_.play();}else {this.player_.pause();}};/**
   * Called when this SeekBar has focus and a key gets pressed down. By
   * default it will call `this.handleAction` when the key is space or enter.
   *
   * @param {EventTarget~Event} event
   *        The `keydown` event that caused this function to be called.
   *
   * @listens keydown
   */SeekBar.prototype.handleKeyPress=function handleKeyPress(event){// Support Space (32) or Enter (13) key operation to fire a click event
if(event.which===32||event.which===13){event.preventDefault();this.handleAction(event);}else if(_Slider.prototype.handleKeyPress){// Pass keypress handling up for unsupported keys
_Slider.prototype.handleKeyPress.call(this,event);}};return SeekBar;}(Slider);/**
 * Default options for the `SeekBar`
 *
 * @type {Object}
 * @private
 */SeekBar.prototype.options_={children:['loadProgressBar','playProgressBar'],barName:'playProgressBar'};// MouseTimeDisplay tooltips should not be added to a player on mobile devices or IE8
if((!IE_VERSION||IE_VERSION>8)&&!IS_IOS&&!IS_ANDROID){SeekBar.prototype.options_.children.splice(1,0,'mouseTimeDisplay');}/**
 * Call the update event for this Slider when this event happens on the player.
 *
 * @type {string}
 */SeekBar.prototype.playerEvent='timeupdate';Component.registerComponent('SeekBar',SeekBar);/**
 * @file progress-control.js
 */ /**
 * The Progress Control component contains the seek bar, load progress,
 * and play progress.
 *
 * @extends Component
 */var ProgressControl=function(_Component){inherits(ProgressControl,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function ProgressControl(player,options){classCallCheck(this,ProgressControl);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.handleMouseMove=throttle(bind(_this,_this.handleMouseMove),25);_this.throttledHandleMouseSeek=throttle(bind(_this,_this.handleMouseSeek),25);_this.enable();return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */ProgressControl.prototype.createEl=function createEl$$1(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-progress-control vjs-control'});};/**
   * When the mouse moves over the `ProgressControl`, the pointer position
   * gets passed down to the `MouseTimeDisplay` component.
   *
   * @param {EventTarget~Event} event
   *        The `mousemove` event that caused this function to run.
   *
   * @listen mousemove
   */ProgressControl.prototype.handleMouseMove=function handleMouseMove(event){var seekBar=this.getChild('seekBar');if(seekBar){var mouseTimeDisplay=seekBar.getChild('mouseTimeDisplay');var seekBarEl=seekBar.el();var seekBarRect=getBoundingClientRect(seekBarEl);var seekBarPoint=getPointerPosition(seekBarEl,event).x;// The default skin has a gap on either side of the `SeekBar`. This means
// that it's possible to trigger this behavior outside the boundaries of
// the `SeekBar`. This ensures we stay within it at all times.
if(seekBarPoint>1){seekBarPoint=1;}else if(seekBarPoint<0){seekBarPoint=0;}if(mouseTimeDisplay){mouseTimeDisplay.update(seekBarRect,seekBarPoint);}}};/**
   * A throttled version of the {@link ProgressControl#handleMouseSeek} listener.
   *
   * @method ProgressControl#throttledHandleMouseSeek
   * @param {EventTarget~Event} event
   *        The `mousemove` event that caused this function to run.
   *
   * @listen mousemove
   * @listen touchmove
   */ /**
   * Handle `mousemove` or `touchmove` events on the `ProgressControl`.
   *
   * @param {EventTarget~Event} event
   *        `mousedown` or `touchstart` event that triggered this function
   *
   * @listens mousemove
   * @listens touchmove
   */ProgressControl.prototype.handleMouseSeek=function handleMouseSeek(event){var seekBar=this.getChild('seekBar');if(seekBar){seekBar.handleMouseMove(event);}};/**
   * Are controls are currently enabled for this progress control.
   *
   * @return {boolean}
   *         true if controls are enabled, false otherwise
   */ProgressControl.prototype.enabled=function enabled(){return this.enabled_;};/**
   * Disable all controls on the progress control and its children
   */ProgressControl.prototype.disable=function disable(){this.children().forEach(function(child){return child.disable&&child.disable();});if(!this.enabled()){return;}this.off(['mousedown','touchstart'],this.handleMouseDown);this.off(this.el_,'mousemove',this.handleMouseMove);this.handleMouseUp();this.addClass('disabled');this.enabled_=false;};/**
   * Enable all controls on the progress control and its children
   */ProgressControl.prototype.enable=function enable(){this.children().forEach(function(child){return child.enable&&child.enable();});if(this.enabled()){return;}this.on(['mousedown','touchstart'],this.handleMouseDown);this.on(this.el_,'mousemove',this.handleMouseMove);this.removeClass('disabled');this.enabled_=true;};/**
   * Handle `mousedown` or `touchstart` events on the `ProgressControl`.
   *
   * @param {EventTarget~Event} event
   *        `mousedown` or `touchstart` event that triggered this function
   *
   * @listens mousedown
   * @listens touchstart
   */ProgressControl.prototype.handleMouseDown=function handleMouseDown(event){var doc=this.el_.ownerDocument;var seekBar=this.getChild('seekBar');if(seekBar){seekBar.handleMouseDown(event);}this.on(doc,'mousemove',this.throttledHandleMouseSeek);this.on(doc,'touchmove',this.throttledHandleMouseSeek);this.on(doc,'mouseup',this.handleMouseUp);this.on(doc,'touchend',this.handleMouseUp);};/**
   * Handle `mouseup` or `touchend` events on the `ProgressControl`.
   *
   * @param {EventTarget~Event} event
   *        `mouseup` or `touchend` event that triggered this function.
   *
   * @listens touchend
   * @listens mouseup
   */ProgressControl.prototype.handleMouseUp=function handleMouseUp(event){var doc=this.el_.ownerDocument;var seekBar=this.getChild('seekBar');if(seekBar){seekBar.handleMouseUp(event);}this.off(doc,'mousemove',this.throttledHandleMouseSeek);this.off(doc,'touchmove',this.throttledHandleMouseSeek);this.off(doc,'mouseup',this.handleMouseUp);this.off(doc,'touchend',this.handleMouseUp);};return ProgressControl;}(Component);/**
 * Default options for `ProgressControl`
 *
 * @type {Object}
 * @private
 */ProgressControl.prototype.options_={children:['seekBar']};Component.registerComponent('ProgressControl',ProgressControl);/**
 * @file fullscreen-toggle.js
 */ /**
 * Toggle fullscreen video
 *
 * @extends Button
 */var FullscreenToggle=function(_Button){inherits(FullscreenToggle,_Button);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function FullscreenToggle(player,options){classCallCheck(this,FullscreenToggle);var _this=possibleConstructorReturn(this,_Button.call(this,player,options));_this.on(player,'fullscreenchange',_this.handleFullscreenChange);if(document$1[FullscreenApi.fullscreenEnabled]===false){_this.disable();}return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */FullscreenToggle.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-fullscreen-control '+_Button.prototype.buildCSSClass.call(this);};/**
   * Handles fullscreenchange on the player and change control text accordingly.
   *
   * @param {EventTarget~Event} [event]
   *        The {@link Player#fullscreenchange} event that caused this function to be
   *        called.
   *
   * @listens Player#fullscreenchange
   */FullscreenToggle.prototype.handleFullscreenChange=function handleFullscreenChange(event){if(this.player_.isFullscreen()){this.controlText('Non-Fullscreen');}else {this.controlText('Fullscreen');}};/**
   * This gets called when an `FullscreenToggle` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */FullscreenToggle.prototype.handleClick=function handleClick(event){if(!this.player_.isFullscreen()){this.player_.requestFullscreen();}else {this.player_.exitFullscreen();}};return FullscreenToggle;}(Button);/**
 * The text that should display over the `FullscreenToggle`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */FullscreenToggle.prototype.controlText_='Fullscreen';Component.registerComponent('FullscreenToggle',FullscreenToggle);/**
 * Check if volume control is supported and if it isn't hide the
 * `Component` that was passed  using the `vjs-hidden` class.
 *
 * @param {Component} self
 *        The component that should be hidden if volume is unsupported
 *
 * @param {Player} player
 *        A reference to the player
 *
 * @private
 */var checkVolumeSupport=function checkVolumeSupport(self,player){// hide volume controls when they're not supported by the current tech
if(player.tech_&&!player.tech_.featuresVolumeControl){self.addClass('vjs-hidden');}self.on(player,'loadstart',function(){if(!player.tech_.featuresVolumeControl){self.addClass('vjs-hidden');}else {self.removeClass('vjs-hidden');}});};/**
 * @file volume-level.js
 */ /**
 * Shows volume level
 *
 * @extends Component
 */var VolumeLevel=function(_Component){inherits(VolumeLevel,_Component);function VolumeLevel(){classCallCheck(this,VolumeLevel);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */VolumeLevel.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-volume-level',innerHTML:'<span class="vjs-control-text"></span>'});};return VolumeLevel;}(Component);Component.registerComponent('VolumeLevel',VolumeLevel);/**
 * @file volume-bar.js
 */ // Required children
/**
 * The bar that contains the volume level and can be clicked on to adjust the level
 *
 * @extends Slider
 */var VolumeBar=function(_Slider){inherits(VolumeBar,_Slider);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function VolumeBar(player,options){classCallCheck(this,VolumeBar);var _this=possibleConstructorReturn(this,_Slider.call(this,player,options));_this.on('slideractive',_this.updateLastVolume_);_this.on(player,'volumechange',_this.updateARIAAttributes);player.ready(function(){return _this.updateARIAAttributes();});return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */VolumeBar.prototype.createEl=function createEl$$1(){return _Slider.prototype.createEl.call(this,'div',{className:'vjs-volume-bar vjs-slider-bar'},{'aria-label':this.localize('Volume Level'),'aria-live':'polite'});};/**
   * Handle mouse down on volume bar
   *
   * @param {EventTarget~Event} event
   *        The `mousedown` event that caused this to run.
   *
   * @listens mousedown
   */VolumeBar.prototype.handleMouseDown=function handleMouseDown(event){if(!isSingleLeftClick(event)){return;}_Slider.prototype.handleMouseDown.call(this,event);};/**
   * Handle movement events on the {@link VolumeMenuButton}.
   *
   * @param {EventTarget~Event} event
   *        The event that caused this function to run.
   *
   * @listens mousemove
   */VolumeBar.prototype.handleMouseMove=function handleMouseMove(event){if(!isSingleLeftClick(event)){return;}this.checkMuted();this.player_.volume(this.calculateDistance(event));};/**
   * If the player is muted unmute it.
   */VolumeBar.prototype.checkMuted=function checkMuted(){if(this.player_.muted()){this.player_.muted(false);}};/**
   * Get percent of volume level
   *
   * @return {number}
   *         Volume level percent as a decimal number.
   */VolumeBar.prototype.getPercent=function getPercent(){if(this.player_.muted()){return 0;}return this.player_.volume();};/**
   * Increase volume level for keyboard users
   */VolumeBar.prototype.stepForward=function stepForward(){this.checkMuted();this.player_.volume(this.player_.volume()+0.1);};/**
   * Decrease volume level for keyboard users
   */VolumeBar.prototype.stepBack=function stepBack(){this.checkMuted();this.player_.volume(this.player_.volume()-0.1);};/**
   * Update ARIA accessibility attributes
   *
   * @param {EventTarget~Event} [event]
   *        The `volumechange` event that caused this function to run.
   *
   * @listens Player#volumechange
   */VolumeBar.prototype.updateARIAAttributes=function updateARIAAttributes(event){var ariaValue=this.player_.muted()?0:this.volumeAsPercentage_();this.el_.setAttribute('aria-valuenow',ariaValue);this.el_.setAttribute('aria-valuetext',ariaValue+'%');};/**
   * Returns the current value of the player volume as a percentage
   *
   * @private
   */VolumeBar.prototype.volumeAsPercentage_=function volumeAsPercentage_(){return Math.round(this.player_.volume()*100);};/**
   * When user starts dragging the VolumeBar, store the volume and listen for
   * the end of the drag. When the drag ends, if the volume was set to zero,
   * set lastVolume to the stored volume.
   *
   * @listens slideractive
   * @private
   */VolumeBar.prototype.updateLastVolume_=function updateLastVolume_(){var _this2=this;var volumeBeforeDrag=this.player_.volume();this.one('sliderinactive',function(){if(_this2.player_.volume()===0){_this2.player_.lastVolume_(volumeBeforeDrag);}});};return VolumeBar;}(Slider);/**
 * Default options for the `VolumeBar`
 *
 * @type {Object}
 * @private
 */VolumeBar.prototype.options_={children:['volumeLevel'],barName:'volumeLevel'};/**
 * Call the update event for this Slider when this event happens on the player.
 *
 * @type {string}
 */VolumeBar.prototype.playerEvent='volumechange';Component.registerComponent('VolumeBar',VolumeBar);/**
 * @file volume-control.js
 */ // Required children
/**
 * The component for controlling the volume level
 *
 * @extends Component
 */var VolumeControl=function(_Component){inherits(VolumeControl,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   */function VolumeControl(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,VolumeControl);options.vertical=options.vertical||false;// Pass the vertical option down to the VolumeBar if
// the VolumeBar is turned on.
if(typeof options.volumeBar==='undefined'||isPlain(options.volumeBar)){options.volumeBar=options.volumeBar||{};options.volumeBar.vertical=options.vertical;}// hide this control if volume support is missing
var _this=possibleConstructorReturn(this,_Component.call(this,player,options));checkVolumeSupport(_this,player);_this.throttledHandleMouseMove=throttle(bind(_this,_this.handleMouseMove),25);_this.on('mousedown',_this.handleMouseDown);_this.on('touchstart',_this.handleMouseDown);// while the slider is active (the mouse has been pressed down and
// is dragging) or in focus we do not want to hide the VolumeBar
_this.on(_this.volumeBar,['focus','slideractive'],function(){_this.volumeBar.addClass('vjs-slider-active');_this.addClass('vjs-slider-active');_this.trigger('slideractive');});_this.on(_this.volumeBar,['blur','sliderinactive'],function(){_this.volumeBar.removeClass('vjs-slider-active');_this.removeClass('vjs-slider-active');_this.trigger('sliderinactive');});return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */VolumeControl.prototype.createEl=function createEl(){var orientationClass='vjs-volume-horizontal';if(this.options_.vertical){orientationClass='vjs-volume-vertical';}return _Component.prototype.createEl.call(this,'div',{className:'vjs-volume-control vjs-control '+orientationClass});};/**
   * Handle `mousedown` or `touchstart` events on the `VolumeControl`.
   *
   * @param {EventTarget~Event} event
   *        `mousedown` or `touchstart` event that triggered this function
   *
   * @listens mousedown
   * @listens touchstart
   */VolumeControl.prototype.handleMouseDown=function handleMouseDown(event){var doc=this.el_.ownerDocument;this.on(doc,'mousemove',this.throttledHandleMouseMove);this.on(doc,'touchmove',this.throttledHandleMouseMove);this.on(doc,'mouseup',this.handleMouseUp);this.on(doc,'touchend',this.handleMouseUp);};/**
   * Handle `mouseup` or `touchend` events on the `VolumeControl`.
   *
   * @param {EventTarget~Event} event
   *        `mouseup` or `touchend` event that triggered this function.
   *
   * @listens touchend
   * @listens mouseup
   */VolumeControl.prototype.handleMouseUp=function handleMouseUp(event){var doc=this.el_.ownerDocument;this.off(doc,'mousemove',this.throttledHandleMouseMove);this.off(doc,'touchmove',this.throttledHandleMouseMove);this.off(doc,'mouseup',this.handleMouseUp);this.off(doc,'touchend',this.handleMouseUp);};/**
   * Handle `mousedown` or `touchstart` events on the `VolumeControl`.
   *
   * @param {EventTarget~Event} event
   *        `mousedown` or `touchstart` event that triggered this function
   *
   * @listens mousedown
   * @listens touchstart
   */VolumeControl.prototype.handleMouseMove=function handleMouseMove(event){this.volumeBar.handleMouseMove(event);};return VolumeControl;}(Component);/**
 * Default options for the `VolumeControl`
 *
 * @type {Object}
 * @private
 */VolumeControl.prototype.options_={children:['volumeBar']};Component.registerComponent('VolumeControl',VolumeControl);/**
 * Check if muting volume is supported and if it isn't hide the mute toggle
 * button.
 *
 * @param {Component} self
 *        A reference to the mute toggle button
 *
 * @param {Player} player
 *        A reference to the player
 *
 * @private
 */var checkMuteSupport=function checkMuteSupport(self,player){// hide mute toggle button if it's not supported by the current tech
if(player.tech_&&!player.tech_.featuresMuteControl){self.addClass('vjs-hidden');}self.on(player,'loadstart',function(){if(!player.tech_.featuresMuteControl){self.addClass('vjs-hidden');}else {self.removeClass('vjs-hidden');}});};/**
 * @file mute-toggle.js
 */ /**
 * A button component for muting the audio.
 *
 * @extends Button
 */var MuteToggle=function(_Button){inherits(MuteToggle,_Button);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function MuteToggle(player,options){classCallCheck(this,MuteToggle);// hide this control if volume support is missing
var _this=possibleConstructorReturn(this,_Button.call(this,player,options));checkMuteSupport(_this,player);_this.on(player,['loadstart','volumechange'],_this.update);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */MuteToggle.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-mute-control '+_Button.prototype.buildCSSClass.call(this);};/**
   * This gets called when an `MuteToggle` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */MuteToggle.prototype.handleClick=function handleClick(event){var vol=this.player_.volume();var lastVolume=this.player_.lastVolume_();if(vol===0){var volumeToSet=lastVolume<0.1?0.1:lastVolume;this.player_.volume(volumeToSet);this.player_.muted(false);}else {this.player_.muted(this.player_.muted()?false:true);}};/**
   * Update the `MuteToggle` button based on the state of `volume` and `muted`
   * on the player.
   *
   * @param {EventTarget~Event} [event]
   *        The {@link Player#loadstart} event if this function was called
   *        through an event.
   *
   * @listens Player#loadstart
   * @listens Player#volumechange
   */MuteToggle.prototype.update=function update(event){this.updateIcon_();this.updateControlText_();};/**
   * Update the appearance of the `MuteToggle` icon.
   *
   * Possible states (given `level` variable below):
   * - 0: crossed out
   * - 1: zero bars of volume
   * - 2: one bar of volume
   * - 3: two bars of volume
   *
   * @private
   */MuteToggle.prototype.updateIcon_=function updateIcon_(){var vol=this.player_.volume();var level=3;// in iOS when a player is loaded with muted attribute
// and volume is changed with a native mute button
// we want to make sure muted state is updated
if(IS_IOS){this.player_.muted(this.player_.tech_.el_.muted);}if(vol===0||this.player_.muted()){level=0;}else if(vol<0.33){level=1;}else if(vol<0.67){level=2;}// TODO improve muted icon classes
for(var i=0;i<4;i++){removeClass(this.el_,'vjs-vol-'+i);}addClass(this.el_,'vjs-vol-'+level);};/**
   * If `muted` has changed on the player, update the control text
   * (`title` attribute on `vjs-mute-control` element and content of
   * `vjs-control-text` element).
   *
   * @private
   */MuteToggle.prototype.updateControlText_=function updateControlText_(){var soundOff=this.player_.muted()||this.player_.volume()===0;var text=soundOff?'Unmute':'Mute';if(this.controlText()!==text){this.controlText(text);}};return MuteToggle;}(Button);/**
 * The text that should display over the `MuteToggle`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */MuteToggle.prototype.controlText_='Mute';Component.registerComponent('MuteToggle',MuteToggle);/**
 * @file volume-control.js
 */ // Required children
/**
 * A Component to contain the MuteToggle and VolumeControl so that
 * they can work together.
 *
 * @extends Component
 */var VolumePanel=function(_Component){inherits(VolumePanel,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   */function VolumePanel(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,VolumePanel);if(typeof options.inline!=='undefined'){options.inline=options.inline;}else {options.inline=true;}// pass the inline option down to the VolumeControl as vertical if
// the VolumeControl is on.
if(typeof options.volumeControl==='undefined'||isPlain(options.volumeControl)){options.volumeControl=options.volumeControl||{};options.volumeControl.vertical=!options.inline;}var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.on(player,['loadstart'],_this.volumePanelState_);// while the slider is active (the mouse has been pressed down and
// is dragging) we do not want to hide the VolumeBar
_this.on(_this.volumeControl,['slideractive'],_this.sliderActive_);_this.on(_this.volumeControl,['sliderinactive'],_this.sliderInactive_);return _this;}/**
   * Add vjs-slider-active class to the VolumePanel
   *
   * @listens VolumeControl#slideractive
   * @private
   */VolumePanel.prototype.sliderActive_=function sliderActive_(){this.addClass('vjs-slider-active');};/**
   * Removes vjs-slider-active class to the VolumePanel
   *
   * @listens VolumeControl#sliderinactive
   * @private
   */VolumePanel.prototype.sliderInactive_=function sliderInactive_(){this.removeClass('vjs-slider-active');};/**
   * Adds vjs-hidden or vjs-mute-toggle-only to the VolumePanel
   * depending on MuteToggle and VolumeControl state
   *
   * @listens Player#loadstart
   * @private
   */VolumePanel.prototype.volumePanelState_=function volumePanelState_(){// hide volume panel if neither volume control or mute toggle
// are displayed
if(this.volumeControl.hasClass('vjs-hidden')&&this.muteToggle.hasClass('vjs-hidden')){this.addClass('vjs-hidden');}// if only mute toggle is visible we don't want
// volume panel expanding when hovered or active
if(this.volumeControl.hasClass('vjs-hidden')&&!this.muteToggle.hasClass('vjs-hidden')){this.addClass('vjs-mute-toggle-only');}};/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */VolumePanel.prototype.createEl=function createEl(){var orientationClass='vjs-volume-panel-horizontal';if(!this.options_.inline){orientationClass='vjs-volume-panel-vertical';}return _Component.prototype.createEl.call(this,'div',{className:'vjs-volume-panel vjs-control '+orientationClass});};return VolumePanel;}(Component);/**
 * Default options for the `VolumeControl`
 *
 * @type {Object}
 * @private
 */VolumePanel.prototype.options_={children:['muteToggle','volumeControl']};Component.registerComponent('VolumePanel',VolumePanel);/**
 * @file menu.js
 */ /**
 * The Menu component is used to build popup menus, including subtitle and
 * captions selection menus.
 *
 * @extends Component
 */var Menu=function(_Component){inherits(Menu,_Component);/**
   * Create an instance of this class.
   *
   * @param {Player} player
   *        the player that this component should attach to
   *
   * @param {Object} [options]
   *        Object of option names and values
   *
   */function Menu(player,options){classCallCheck(this,Menu);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));if(options){_this.menuButton_=options.menuButton;}_this.focusedChild_=-1;_this.on('keydown',_this.handleKeyPress);return _this;}/**
   * Add a {@link MenuItem} to the menu.
   *
   * @param {Object|string} component
   *        The name or instance of the `MenuItem` to add.
   *
   */Menu.prototype.addItem=function addItem(component){this.addChild(component);component.on('click',bind(this,function(event){// Unpress the associated MenuButton, and move focus back to it
if(this.menuButton_){this.menuButton_.unpressButton();// don't focus menu button if item is a caption settings item
// because focus will move elsewhere and it logs an error on IE8
if(component.name()!=='CaptionSettingsMenuItem'){this.menuButton_.focus();}}}));};/**
   * Create the `Menu`s DOM element.
   *
   * @return {Element}
   *         the element that was created
   */Menu.prototype.createEl=function createEl$$1(){var contentElType=this.options_.contentElType||'ul';this.contentEl_=createEl(contentElType,{className:'vjs-menu-content'});this.contentEl_.setAttribute('role','menu');var el=_Component.prototype.createEl.call(this,'div',{append:this.contentEl_,className:'vjs-menu'});el.appendChild(this.contentEl_);// Prevent clicks from bubbling up. Needed for Menu Buttons,
// where a click on the parent is significant
on(el,'click',function(event){event.preventDefault();event.stopImmediatePropagation();});return el;};Menu.prototype.dispose=function dispose(){this.contentEl_=null;_Component.prototype.dispose.call(this);};/**
   * Handle a `keydown` event on this menu. This listener is added in the constructor.
   *
   * @param {EventTarget~Event} event
   *        A `keydown` event that happened on the menu.
   *
   * @listens keydown
   */Menu.prototype.handleKeyPress=function handleKeyPress(event){// Left and Down Arrows
if(event.which===37||event.which===40){event.preventDefault();this.stepForward();// Up and Right Arrows
}else if(event.which===38||event.which===39){event.preventDefault();this.stepBack();}};/**
   * Move to next (lower) menu item for keyboard users.
   */Menu.prototype.stepForward=function stepForward(){var stepChild=0;if(this.focusedChild_!==undefined){stepChild=this.focusedChild_+1;}this.focus(stepChild);};/**
   * Move to previous (higher) menu item for keyboard users.
   */Menu.prototype.stepBack=function stepBack(){var stepChild=0;if(this.focusedChild_!==undefined){stepChild=this.focusedChild_-1;}this.focus(stepChild);};/**
   * Set focus on a {@link MenuItem} in the `Menu`.
   *
   * @param {Object|string} [item=0]
   *        Index of child item set focus on.
   */Menu.prototype.focus=function focus(){var item=arguments.length>0&&arguments[0]!==undefined?arguments[0]:0;var children=this.children().slice();var haveTitle=children.length&&children[0].className&&/vjs-menu-title/.test(children[0].className);if(haveTitle){children.shift();}if(children.length>0){if(item<0){item=0;}else if(item>=children.length){item=children.length-1;}this.focusedChild_=item;children[item].el_.focus();}};return Menu;}(Component);Component.registerComponent('Menu',Menu);/**
 * @file menu-button.js
 */ /**
 * A `MenuButton` class for any popup {@link Menu}.
 *
 * @extends Component
 */var MenuButton=function(_Component){inherits(MenuButton,_Component);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   */function MenuButton(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,MenuButton);var _this=possibleConstructorReturn(this,_Component.call(this,player,options));_this.menuButton_=new Button(player,options);_this.menuButton_.controlText(_this.controlText_);_this.menuButton_.el_.setAttribute('aria-haspopup','true');// Add buildCSSClass values to the button, not the wrapper
var buttonClass=Button.prototype.buildCSSClass();_this.menuButton_.el_.className=_this.buildCSSClass()+' '+buttonClass;_this.menuButton_.removeClass('vjs-control');_this.addChild(_this.menuButton_);_this.update();_this.enabled_=true;_this.on(_this.menuButton_,'tap',_this.handleClick);_this.on(_this.menuButton_,'click',_this.handleClick);_this.on(_this.menuButton_,'focus',_this.handleFocus);_this.on(_this.menuButton_,'blur',_this.handleBlur);_this.on('keydown',_this.handleSubmenuKeyPress);return _this;}/**
   * Update the menu based on the current state of its items.
   */MenuButton.prototype.update=function update(){var menu=this.createMenu();if(this.menu){this.menu.dispose();this.removeChild(this.menu);}this.menu=menu;this.addChild(menu);/**
     * Track the state of the menu button
     *
     * @type {Boolean}
     * @private
     */this.buttonPressed_=false;this.menuButton_.el_.setAttribute('aria-expanded','false');if(this.items&&this.items.length<=this.hideThreshold_){this.hide();}else {this.show();}};/**
   * Create the menu and add all items to it.
   *
   * @return {Menu}
   *         The constructed menu
   */MenuButton.prototype.createMenu=function createMenu(){var menu=new Menu(this.player_,{menuButton:this});/**
     * Hide the menu if the number of items is less than or equal to this threshold. This defaults
     * to 0 and whenever we add items which can be hidden to the menu we'll increment it. We list
     * it here because every time we run `createMenu` we need to reset the value.
     *
     * @protected
     * @type {Number}
     */this.hideThreshold_=0;// Add a title list item to the top
if(this.options_.title){var title=createEl('li',{className:'vjs-menu-title',innerHTML:toTitleCase(this.options_.title),tabIndex:-1});this.hideThreshold_+=1;menu.children_.unshift(title);prependTo(title,menu.contentEl());}this.items=this.createItems();if(this.items){// Add menu items to the menu
for(var i=0;i<this.items.length;i++){menu.addItem(this.items[i]);}}return menu;};/**
   * Create the list of menu items. Specific to each subclass.
   *
   * @abstract
   */MenuButton.prototype.createItems=function createItems(){};/**
   * Create the `MenuButtons`s DOM element.
   *
   * @return {Element}
   *         The element that gets created.
   */MenuButton.prototype.createEl=function createEl$$1(){return _Component.prototype.createEl.call(this,'div',{className:this.buildWrapperCSSClass()},{});};/**
   * Allow sub components to stack CSS class names for the wrapper element
   *
   * @return {string}
   *         The constructed wrapper DOM `className`
   */MenuButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){var menuButtonClass='vjs-menu-button';// If the inline option is passed, we want to use different styles altogether.
if(this.options_.inline===true){menuButtonClass+='-inline';}else {menuButtonClass+='-popup';}// TODO: Fix the CSS so that this isn't necessary
var buttonClass=Button.prototype.buildCSSClass();return 'vjs-menu-button '+menuButtonClass+' '+buttonClass+' '+_Component.prototype.buildCSSClass.call(this);};/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */MenuButton.prototype.buildCSSClass=function buildCSSClass(){var menuButtonClass='vjs-menu-button';// If the inline option is passed, we want to use different styles altogether.
if(this.options_.inline===true){menuButtonClass+='-inline';}else {menuButtonClass+='-popup';}return 'vjs-menu-button '+menuButtonClass+' '+_Component.prototype.buildCSSClass.call(this);};/**
   * Get or set the localized control text that will be used for accessibility.
   *
   * > NOTE: This will come from the internal `menuButton_` element.
   *
   * @param {string} [text]
   *        Control text for element.
   *
   * @param {Element} [el=this.menuButton_.el()]
   *        Element to set the title on.
   *
   * @return {string}
   *         - The control text when getting
   */MenuButton.prototype.controlText=function controlText(text){var el=arguments.length>1&&arguments[1]!==undefined?arguments[1]:this.menuButton_.el();return this.menuButton_.controlText(text,el);};/**
   * Handle a click on a `MenuButton`.
   * See {@link ClickableComponent#handleClick} for instances where this is called.
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */MenuButton.prototype.handleClick=function handleClick(event){// When you click the button it adds focus, which will show the menu.
// So we'll remove focus when the mouse leaves the button. Focus is needed
// for tab navigation.
this.one(this.menu.contentEl(),'mouseleave',bind(this,function(e){this.unpressButton();this.el_.blur();}));if(this.buttonPressed_){this.unpressButton();}else {this.pressButton();}};/**
   * Set the focus to the actual button, not to this element
   */MenuButton.prototype.focus=function focus(){this.menuButton_.focus();};/**
   * Remove the focus from the actual button, not this element
   */MenuButton.prototype.blur=function blur(){this.menuButton_.blur();};/**
   * This gets called when a `MenuButton` gains focus via a `focus` event.
   * Turns on listening for `keydown` events. When they happen it
   * calls `this.handleKeyPress`.
   *
   * @param {EventTarget~Event} event
   *        The `focus` event that caused this function to be called.
   *
   * @listens focus
   */MenuButton.prototype.handleFocus=function handleFocus(){on(document$1,'keydown',bind(this,this.handleKeyPress));};/**
   * Called when a `MenuButton` loses focus. Turns off the listener for
   * `keydown` events. Which Stops `this.handleKeyPress` from getting called.
   *
   * @param {EventTarget~Event} event
   *        The `blur` event that caused this function to be called.
   *
   * @listens blur
   */MenuButton.prototype.handleBlur=function handleBlur(){off(document$1,'keydown',bind(this,this.handleKeyPress));};/**
   * Handle tab, escape, down arrow, and up arrow keys for `MenuButton`. See
   * {@link ClickableComponent#handleKeyPress} for instances where this is called.
   *
   * @param {EventTarget~Event} event
   *        The `keydown` event that caused this function to be called.
   *
   * @listens keydown
   */MenuButton.prototype.handleKeyPress=function handleKeyPress(event){// Escape (27) key or Tab (9) key unpress the 'button'
if(event.which===27||event.which===9){if(this.buttonPressed_){this.unpressButton();}// Don't preventDefault for Tab key - we still want to lose focus
if(event.which!==9){event.preventDefault();// Set focus back to the menu button's button
this.menuButton_.el_.focus();}// Up (38) key or Down (40) key press the 'button'
}else if(event.which===38||event.which===40){if(!this.buttonPressed_){this.pressButton();event.preventDefault();}}};/**
   * Handle a `keydown` event on a sub-menu. The listener for this is added in
   * the constructor.
   *
   * @param {EventTarget~Event} event
   *        Key press event
   *
   * @listens keydown
   */MenuButton.prototype.handleSubmenuKeyPress=function handleSubmenuKeyPress(event){// Escape (27) key or Tab (9) key unpress the 'button'
if(event.which===27||event.which===9){if(this.buttonPressed_){this.unpressButton();}// Don't preventDefault for Tab key - we still want to lose focus
if(event.which!==9){event.preventDefault();// Set focus back to the menu button's button
this.menuButton_.el_.focus();}}};/**
   * Put the current `MenuButton` into a pressed state.
   */MenuButton.prototype.pressButton=function pressButton(){if(this.enabled_){this.buttonPressed_=true;this.menu.lockShowing();this.menuButton_.el_.setAttribute('aria-expanded','true');// set the focus into the submenu, except on iOS where it is resulting in
// undesired scrolling behavior when the player is in an iframe
if(IS_IOS&&isInFrame()){// Return early so that the menu isn't focused
return;}this.menu.focus();}};/**
   * Take the current `MenuButton` out of a pressed state.
   */MenuButton.prototype.unpressButton=function unpressButton(){if(this.enabled_){this.buttonPressed_=false;this.menu.unlockShowing();this.menuButton_.el_.setAttribute('aria-expanded','false');}};/**
   * Disable the `MenuButton`. Don't allow it to be clicked.
   */MenuButton.prototype.disable=function disable(){this.unpressButton();this.enabled_=false;this.addClass('vjs-disabled');this.menuButton_.disable();};/**
   * Enable the `MenuButton`. Allow it to be clicked.
   */MenuButton.prototype.enable=function enable(){this.enabled_=true;this.removeClass('vjs-disabled');this.menuButton_.enable();};return MenuButton;}(Component);Component.registerComponent('MenuButton',MenuButton);/**
 * @file track-button.js
 */ /**
 * The base class for buttons that toggle specific  track types (e.g. subtitles).
 *
 * @extends MenuButton
 */var TrackButton=function(_MenuButton){inherits(TrackButton,_MenuButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function TrackButton(player,options){classCallCheck(this,TrackButton);var tracks=options.tracks;var _this=possibleConstructorReturn(this,_MenuButton.call(this,player,options));if(_this.items.length<=1){_this.hide();}if(!tracks){return possibleConstructorReturn(_this);}var updateHandler=bind(_this,_this.update);tracks.addEventListener('removetrack',updateHandler);tracks.addEventListener('addtrack',updateHandler);_this.player_.on('ready',updateHandler);_this.player_.on('dispose',function(){tracks.removeEventListener('removetrack',updateHandler);tracks.removeEventListener('addtrack',updateHandler);});return _this;}return TrackButton;}(MenuButton);Component.registerComponent('TrackButton',TrackButton);/**
 * @file menu-item.js
 */ /**
 * The component for a menu item. `<li>`
 *
 * @extends ClickableComponent
 */var MenuItem=function(_ClickableComponent){inherits(MenuItem,_ClickableComponent);/**
   * Creates an instance of the this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   *
   */function MenuItem(player,options){classCallCheck(this,MenuItem);var _this=possibleConstructorReturn(this,_ClickableComponent.call(this,player,options));_this.selectable=options.selectable;_this.isSelected_=options.selected||false;_this.multiSelectable=options.multiSelectable;_this.selected(_this.isSelected_);if(_this.selectable){if(_this.multiSelectable){_this.el_.setAttribute('role','menuitemcheckbox');}else {_this.el_.setAttribute('role','menuitemradio');}}else {_this.el_.setAttribute('role','menuitem');}return _this;}/**
   * Create the `MenuItem's DOM element
   *
   * @param {string} [type=li]
   *        Element's node type, not actually used, always set to `li`.
   *
   * @param {Object} [props={}]
   *        An object of properties that should be set on the element
   *
   * @param {Object} [attrs={}]
   *        An object of attributes that should be set on the element
   *
   * @return {Element}
   *         The element that gets created.
   */MenuItem.prototype.createEl=function createEl(type,props,attrs){// The control is textual, not just an icon
this.nonIconControl=true;return _ClickableComponent.prototype.createEl.call(this,'li',assign$1({className:'vjs-menu-item',innerHTML:'<span class="vjs-menu-item-text">'+this.localize(this.options_.label)+'</span>',tabIndex:-1},props),attrs);};/**
   * Any click on a `MenuItem` puts it into the selected state.
   * See {@link ClickableComponent#handleClick} for instances where this is called.
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */MenuItem.prototype.handleClick=function handleClick(event){this.selected(true);};/**
   * Set the state for this menu item as selected or not.
   *
   * @param {boolean} selected
   *        if the menu item is selected or not
   */MenuItem.prototype.selected=function selected(_selected){if(this.selectable){if(_selected){this.addClass('vjs-selected');this.el_.setAttribute('aria-checked','true');// aria-checked isn't fully supported by browsers/screen readers,
// so indicate selected state to screen reader in the control text.
this.controlText(', selected');this.isSelected_=true;}else {this.removeClass('vjs-selected');this.el_.setAttribute('aria-checked','false');// Indicate un-selected state to screen reader
this.controlText('');this.isSelected_=false;}}};return MenuItem;}(ClickableComponent);Component.registerComponent('MenuItem',MenuItem);/**
 * @file text-track-menu-item.js
 */ /**
 * The specific menu item type for selecting a language within a text track kind
 *
 * @extends MenuItem
 */var TextTrackMenuItem=function(_MenuItem){inherits(TextTrackMenuItem,_MenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function TextTrackMenuItem(player,options){classCallCheck(this,TextTrackMenuItem);var track=options.track;var tracks=player.textTracks();// Modify options for parent MenuItem class's init.
options.label=track.label||track.language||'Unknown';options.selected=track.mode==='showing';var _this=possibleConstructorReturn(this,_MenuItem.call(this,player,options));_this.track=track;var changeHandler=function changeHandler(){for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}_this.handleTracksChange.apply(_this,args);};var selectedLanguageChangeHandler=function selectedLanguageChangeHandler(){for(var _len2=arguments.length,args=Array(_len2),_key2=0;_key2<_len2;_key2++){args[_key2]=arguments[_key2];}_this.handleSelectedLanguageChange.apply(_this,args);};player.on(['loadstart','texttrackchange'],changeHandler);tracks.addEventListener('change',changeHandler);tracks.addEventListener('selectedlanguagechange',selectedLanguageChangeHandler);_this.on('dispose',function(){player.off(['loadstart','texttrackchange'],changeHandler);tracks.removeEventListener('change',changeHandler);tracks.removeEventListener('selectedlanguagechange',selectedLanguageChangeHandler);});// iOS7 doesn't dispatch change events to TextTrackLists when an
// associated track's mode changes. Without something like
// Object.observe() (also not present on iOS7), it's not
// possible to detect changes to the mode attribute and polyfill
// the change event. As a poor substitute, we manually dispatch
// change events whenever the controls modify the mode.
if(tracks.onchange===undefined){var event=void 0;_this.on(['tap','click'],function(){if(_typeof$2(window$1.Event)!=='object'){// Android 2.3 throws an Illegal Constructor error for window.Event
try{event=new window$1.Event('change');}catch(err){// continue regardless of error
}}if(!event){event=document$1.createEvent('Event');event.initEvent('change',true,true);}tracks.dispatchEvent(event);});}// set the default state based on current tracks
_this.handleTracksChange();return _this;}/**
   * This gets called when an `TextTrackMenuItem` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} event
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */TextTrackMenuItem.prototype.handleClick=function handleClick(event){var kind=this.track.kind;var kinds=this.track.kinds;var tracks=this.player_.textTracks();if(!kinds){kinds=[kind];}_MenuItem.prototype.handleClick.call(this,event);if(!tracks){return;}for(var i=0;i<tracks.length;i++){var track=tracks[i];if(track===this.track&&kinds.indexOf(track.kind)>-1){if(track.mode!=='showing'){track.mode='showing';}}else if(track.mode!=='disabled'){track.mode='disabled';}}};/**
   * Handle text track list change
   *
   * @param {EventTarget~Event} event
   *        The `change` event that caused this function to be called.
   *
   * @listens TextTrackList#change
   */TextTrackMenuItem.prototype.handleTracksChange=function handleTracksChange(event){var shouldBeSelected=this.track.mode==='showing';// Prevent redundant selected() calls because they may cause
// screen readers to read the appended control text unnecessarily
if(shouldBeSelected!==this.isSelected_){this.selected(shouldBeSelected);}};TextTrackMenuItem.prototype.handleSelectedLanguageChange=function handleSelectedLanguageChange(event){if(this.track.mode==='showing'){var selectedLanguage=this.player_.cache_.selectedLanguage;// Don't replace the kind of track across the same language
if(selectedLanguage&&selectedLanguage.enabled&&selectedLanguage.language===this.track.language&&selectedLanguage.kind!==this.track.kind){return;}this.player_.cache_.selectedLanguage={enabled:true,language:this.track.language,kind:this.track.kind};}};TextTrackMenuItem.prototype.dispose=function dispose(){// remove reference to track object on dispose
this.track=null;_MenuItem.prototype.dispose.call(this);};return TextTrackMenuItem;}(MenuItem);Component.registerComponent('TextTrackMenuItem',TextTrackMenuItem);/**
 * @file off-text-track-menu-item.js
 */ /**
 * A special menu item for turning of a specific type of text track
 *
 * @extends TextTrackMenuItem
 */var OffTextTrackMenuItem=function(_TextTrackMenuItem){inherits(OffTextTrackMenuItem,_TextTrackMenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function OffTextTrackMenuItem(player,options){classCallCheck(this,OffTextTrackMenuItem);// Create pseudo track info
// Requires options['kind']
options.track={player:player,kind:options.kind,kinds:options.kinds,'default':false,mode:'disabled'};if(!options.kinds){options.kinds=[options.kind];}if(options.label){options.track.label=options.label;}else {options.track.label=options.kinds.join(' and ')+' off';}// MenuItem is selectable
options.selectable=true;// MenuItem is NOT multiSelectable (i.e. only one can be marked "selected" at a time)
options.multiSelectable=false;return possibleConstructorReturn(this,_TextTrackMenuItem.call(this,player,options));}/**
   * Handle text track change
   *
   * @param {EventTarget~Event} event
   *        The event that caused this function to run
   */OffTextTrackMenuItem.prototype.handleTracksChange=function handleTracksChange(event){var tracks=this.player().textTracks();var shouldBeSelected=true;for(var i=0,l=tracks.length;i<l;i++){var track=tracks[i];if(this.options_.kinds.indexOf(track.kind)>-1&&track.mode==='showing'){shouldBeSelected=false;break;}}// Prevent redundant selected() calls because they may cause
// screen readers to read the appended control text unnecessarily
if(shouldBeSelected!==this.isSelected_){this.selected(shouldBeSelected);}};OffTextTrackMenuItem.prototype.handleSelectedLanguageChange=function handleSelectedLanguageChange(event){var tracks=this.player().textTracks();var allHidden=true;for(var i=0,l=tracks.length;i<l;i++){var track=tracks[i];if(['captions','descriptions','subtitles'].indexOf(track.kind)>-1&&track.mode==='showing'){allHidden=false;break;}}if(allHidden){this.player_.cache_.selectedLanguage={enabled:false};}};return OffTextTrackMenuItem;}(TextTrackMenuItem);Component.registerComponent('OffTextTrackMenuItem',OffTextTrackMenuItem);/**
 * @file text-track-button.js
 */ /**
 * The base class for buttons that toggle specific text track types (e.g. subtitles)
 *
 * @extends MenuButton
 */var TextTrackButton=function(_TrackButton){inherits(TextTrackButton,_TrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   */function TextTrackButton(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,TextTrackButton);options.tracks=player.textTracks();return possibleConstructorReturn(this,_TrackButton.call(this,player,options));}/**
   * Create a menu item for each text track
   *
   * @param {TextTrackMenuItem[]} [items=[]]
   *        Existing array of items to use during creation
   *
   * @return {TextTrackMenuItem[]}
   *         Array of menu items that were created
   */TextTrackButton.prototype.createItems=function createItems(){var items=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];var TrackMenuItem=arguments.length>1&&arguments[1]!==undefined?arguments[1]:TextTrackMenuItem;// Label is an overide for the [track] off label
// USed to localise captions/subtitles
var label=void 0;if(this.label_){label=this.label_+' off';}// Add an OFF menu item to turn all tracks off
items.push(new OffTextTrackMenuItem(this.player_,{kinds:this.kinds_,kind:this.kind_,label:label}));this.hideThreshold_+=1;var tracks=this.player_.textTracks();if(!Array.isArray(this.kinds_)){this.kinds_=[this.kind_];}for(var i=0;i<tracks.length;i++){var track=tracks[i];// only add tracks that are of an appropriate kind and have a label
if(this.kinds_.indexOf(track.kind)>-1){var item=new TrackMenuItem(this.player_,{track:track,// MenuItem is selectable
selectable:true,// MenuItem is NOT multiSelectable (i.e. only one can be marked "selected" at a time)
multiSelectable:false});item.addClass('vjs-'+track.kind+'-menu-item');items.push(item);}}return items;};return TextTrackButton;}(TrackButton);Component.registerComponent('TextTrackButton',TextTrackButton);/**
 * @file chapters-track-menu-item.js
 */ /**
 * The chapter track menu item
 *
 * @extends MenuItem
 */var ChaptersTrackMenuItem=function(_MenuItem){inherits(ChaptersTrackMenuItem,_MenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function ChaptersTrackMenuItem(player,options){classCallCheck(this,ChaptersTrackMenuItem);var track=options.track;var cue=options.cue;var currentTime=player.currentTime();// Modify options for parent MenuItem class's init.
options.selectable=true;options.multiSelectable=false;options.label=cue.text;options.selected=cue.startTime<=currentTime&&currentTime<cue.endTime;var _this=possibleConstructorReturn(this,_MenuItem.call(this,player,options));_this.track=track;_this.cue=cue;track.addEventListener('cuechange',bind(_this,_this.update));return _this;}/**
   * This gets called when an `ChaptersTrackMenuItem` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */ChaptersTrackMenuItem.prototype.handleClick=function handleClick(event){_MenuItem.prototype.handleClick.call(this);this.player_.currentTime(this.cue.startTime);this.update(this.cue.startTime);};/**
   * Update chapter menu item
   *
   * @param {EventTarget~Event} [event]
   *        The `cuechange` event that caused this function to run.
   *
   * @listens TextTrack#cuechange
   */ChaptersTrackMenuItem.prototype.update=function update(event){var cue=this.cue;var currentTime=this.player_.currentTime();// vjs.log(currentTime, cue.startTime);
this.selected(cue.startTime<=currentTime&&currentTime<cue.endTime);};return ChaptersTrackMenuItem;}(MenuItem);Component.registerComponent('ChaptersTrackMenuItem',ChaptersTrackMenuItem);/**
 * @file chapters-button.js
 */ /**
 * The button component for toggling and selecting chapters
 * Chapters act much differently than other text tracks
 * Cues are navigation vs. other tracks of alternative languages
 *
 * @extends TextTrackButton
 */var ChaptersButton=function(_TextTrackButton){inherits(ChaptersButton,_TextTrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function to call when this function is ready.
   */function ChaptersButton(player,options,ready){classCallCheck(this,ChaptersButton);return possibleConstructorReturn(this,_TextTrackButton.call(this,player,options,ready));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */ChaptersButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-chapters-button '+_TextTrackButton.prototype.buildCSSClass.call(this);};ChaptersButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-chapters-button '+_TextTrackButton.prototype.buildWrapperCSSClass.call(this);};/**
   * Update the menu based on the current state of its items.
   *
   * @param {EventTarget~Event} [event]
   *        An event that triggered this function to run.
   *
   * @listens TextTrackList#addtrack
   * @listens TextTrackList#removetrack
   * @listens TextTrackList#change
   */ChaptersButton.prototype.update=function update(event){if(!this.track_||event&&(event.type==='addtrack'||event.type==='removetrack')){this.setTrack(this.findChaptersTrack());}_TextTrackButton.prototype.update.call(this);};/**
   * Set the currently selected track for the chapters button.
   *
   * @param {TextTrack} track
   *        The new track to select. Nothing will change if this is the currently selected
   *        track.
   */ChaptersButton.prototype.setTrack=function setTrack(track){if(this.track_===track){return;}if(!this.updateHandler_){this.updateHandler_=this.update.bind(this);}// here this.track_ refers to the old track instance
if(this.track_){var remoteTextTrackEl=this.player_.remoteTextTrackEls().getTrackElementByTrack_(this.track_);if(remoteTextTrackEl){remoteTextTrackEl.removeEventListener('load',this.updateHandler_);}this.track_=null;}this.track_=track;// here this.track_ refers to the new track instance
if(this.track_){this.track_.mode='hidden';var _remoteTextTrackEl=this.player_.remoteTextTrackEls().getTrackElementByTrack_(this.track_);if(_remoteTextTrackEl){_remoteTextTrackEl.addEventListener('load',this.updateHandler_);}}};/**
   * Find the track object that is currently in use by this ChaptersButton
   *
   * @return {TextTrack|undefined}
   *         The current track or undefined if none was found.
   */ChaptersButton.prototype.findChaptersTrack=function findChaptersTrack(){var tracks=this.player_.textTracks()||[];for(var i=tracks.length-1;i>=0;i--){// We will always choose the last track as our chaptersTrack
var track=tracks[i];if(track.kind===this.kind_){return track;}}};/**
   * Get the caption for the ChaptersButton based on the track label. This will also
   * use the current tracks localized kind as a fallback if a label does not exist.
   *
   * @return {string}
   *         The tracks current label or the localized track kind.
   */ChaptersButton.prototype.getMenuCaption=function getMenuCaption(){if(this.track_&&this.track_.label){return this.track_.label;}return this.localize(toTitleCase(this.kind_));};/**
   * Create menu from chapter track
   *
   * @return {Menu}
   *         New menu for the chapter buttons
   */ChaptersButton.prototype.createMenu=function createMenu(){this.options_.title=this.getMenuCaption();return _TextTrackButton.prototype.createMenu.call(this);};/**
   * Create a menu item for each text track
   *
   * @return {TextTrackMenuItem[]}
   *         Array of menu items
   */ChaptersButton.prototype.createItems=function createItems(){var items=[];if(!this.track_){return items;}var cues=this.track_.cues;if(!cues){return items;}for(var i=0,l=cues.length;i<l;i++){var cue=cues[i];var mi=new ChaptersTrackMenuItem(this.player_,{track:this.track_,cue:cue});items.push(mi);}return items;};return ChaptersButton;}(TextTrackButton);/**
 * `kind` of TextTrack to look for to associate it with this menu.
 *
 * @type {string}
 * @private
 */ChaptersButton.prototype.kind_='chapters';/**
 * The text that should display over the `ChaptersButton`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */ChaptersButton.prototype.controlText_='Chapters';Component.registerComponent('ChaptersButton',ChaptersButton);/**
 * @file descriptions-button.js
 */ /**
 * The button component for toggling and selecting descriptions
 *
 * @extends TextTrackButton
 */var DescriptionsButton=function(_TextTrackButton){inherits(DescriptionsButton,_TextTrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function to call when this component is ready.
   */function DescriptionsButton(player,options,ready){classCallCheck(this,DescriptionsButton);var _this=possibleConstructorReturn(this,_TextTrackButton.call(this,player,options,ready));var tracks=player.textTracks();var changeHandler=bind(_this,_this.handleTracksChange);tracks.addEventListener('change',changeHandler);_this.on('dispose',function(){tracks.removeEventListener('change',changeHandler);});return _this;}/**
   * Handle text track change
   *
   * @param {EventTarget~Event} event
   *        The event that caused this function to run
   *
   * @listens TextTrackList#change
   */DescriptionsButton.prototype.handleTracksChange=function handleTracksChange(event){var tracks=this.player().textTracks();var disabled=false;// Check whether a track of a different kind is showing
for(var i=0,l=tracks.length;i<l;i++){var track=tracks[i];if(track.kind!==this.kind_&&track.mode==='showing'){disabled=true;break;}}// If another track is showing, disable this menu button
if(disabled){this.disable();}else {this.enable();}};/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */DescriptionsButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-descriptions-button '+_TextTrackButton.prototype.buildCSSClass.call(this);};DescriptionsButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-descriptions-button '+_TextTrackButton.prototype.buildWrapperCSSClass.call(this);};return DescriptionsButton;}(TextTrackButton);/**
 * `kind` of TextTrack to look for to associate it with this menu.
 *
 * @type {string}
 * @private
 */DescriptionsButton.prototype.kind_='descriptions';/**
 * The text that should display over the `DescriptionsButton`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */DescriptionsButton.prototype.controlText_='Descriptions';Component.registerComponent('DescriptionsButton',DescriptionsButton);/**
 * @file subtitles-button.js
 */ /**
 * The button component for toggling and selecting subtitles
 *
 * @extends TextTrackButton
 */var SubtitlesButton=function(_TextTrackButton){inherits(SubtitlesButton,_TextTrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function to call when this component is ready.
   */function SubtitlesButton(player,options,ready){classCallCheck(this,SubtitlesButton);return possibleConstructorReturn(this,_TextTrackButton.call(this,player,options,ready));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */SubtitlesButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-subtitles-button '+_TextTrackButton.prototype.buildCSSClass.call(this);};SubtitlesButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-subtitles-button '+_TextTrackButton.prototype.buildWrapperCSSClass.call(this);};return SubtitlesButton;}(TextTrackButton);/**
 * `kind` of TextTrack to look for to associate it with this menu.
 *
 * @type {string}
 * @private
 */SubtitlesButton.prototype.kind_='subtitles';/**
 * The text that should display over the `SubtitlesButton`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */SubtitlesButton.prototype.controlText_='Subtitles';Component.registerComponent('SubtitlesButton',SubtitlesButton);/**
 * @file caption-settings-menu-item.js
 */ /**
 * The menu item for caption track settings menu
 *
 * @extends TextTrackMenuItem
 */var CaptionSettingsMenuItem=function(_TextTrackMenuItem){inherits(CaptionSettingsMenuItem,_TextTrackMenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function CaptionSettingsMenuItem(player,options){classCallCheck(this,CaptionSettingsMenuItem);options.track={player:player,kind:options.kind,label:options.kind+' settings',selectable:false,'default':false,mode:'disabled'};// CaptionSettingsMenuItem has no concept of 'selected'
options.selectable=false;options.name='CaptionSettingsMenuItem';var _this=possibleConstructorReturn(this,_TextTrackMenuItem.call(this,player,options));_this.addClass('vjs-texttrack-settings');_this.controlText(', opens '+options.kind+' settings dialog');return _this;}/**
   * This gets called when an `CaptionSettingsMenuItem` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */CaptionSettingsMenuItem.prototype.handleClick=function handleClick(event){this.player().getChild('textTrackSettings').open();};return CaptionSettingsMenuItem;}(TextTrackMenuItem);Component.registerComponent('CaptionSettingsMenuItem',CaptionSettingsMenuItem);/**
 * @file captions-button.js
 */ /**
 * The button component for toggling and selecting captions
 *
 * @extends TextTrackButton
 */var CaptionsButton=function(_TextTrackButton){inherits(CaptionsButton,_TextTrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} [ready]
   *        The function to call when this component is ready.
   */function CaptionsButton(player,options,ready){classCallCheck(this,CaptionsButton);return possibleConstructorReturn(this,_TextTrackButton.call(this,player,options,ready));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */CaptionsButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-captions-button '+_TextTrackButton.prototype.buildCSSClass.call(this);};CaptionsButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-captions-button '+_TextTrackButton.prototype.buildWrapperCSSClass.call(this);};/**
   * Create caption menu items
   *
   * @return {CaptionSettingsMenuItem[]}
   *         The array of current menu items.
   */CaptionsButton.prototype.createItems=function createItems(){var items=[];if(!(this.player().tech_&&this.player().tech_.featuresNativeTextTracks)&&this.player().getChild('textTrackSettings')){items.push(new CaptionSettingsMenuItem(this.player_,{kind:this.kind_}));this.hideThreshold_+=1;}return _TextTrackButton.prototype.createItems.call(this,items);};return CaptionsButton;}(TextTrackButton);/**
 * `kind` of TextTrack to look for to associate it with this menu.
 *
 * @type {string}
 * @private
 */CaptionsButton.prototype.kind_='captions';/**
 * The text that should display over the `CaptionsButton`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */CaptionsButton.prototype.controlText_='Captions';Component.registerComponent('CaptionsButton',CaptionsButton);/**
 * @file subs-caps-menu-item.js
 */ /**
 * SubsCapsMenuItem has an [cc] icon to distinguish captions from subtitles
 * in the SubsCapsMenu.
 *
 * @extends TextTrackMenuItem
 */var SubsCapsMenuItem=function(_TextTrackMenuItem){inherits(SubsCapsMenuItem,_TextTrackMenuItem);function SubsCapsMenuItem(){classCallCheck(this,SubsCapsMenuItem);return possibleConstructorReturn(this,_TextTrackMenuItem.apply(this,arguments));}SubsCapsMenuItem.prototype.createEl=function createEl(type,props,attrs){var innerHTML='<span class="vjs-menu-item-text">'+this.localize(this.options_.label);if(this.options_.track.kind==='captions'){innerHTML+='\n        <span aria-hidden="true" class="vjs-icon-placeholder"></span>\n        <span class="vjs-control-text"> '+this.localize('Captions')+'</span>\n      ';}innerHTML+='</span>';var el=_TextTrackMenuItem.prototype.createEl.call(this,type,assign$1({innerHTML:innerHTML},props),attrs);return el;};return SubsCapsMenuItem;}(TextTrackMenuItem);Component.registerComponent('SubsCapsMenuItem',SubsCapsMenuItem);/**
 * @file sub-caps-button.js
 */ /**
 * The button component for toggling and selecting captions and/or subtitles
 *
 * @extends TextTrackButton
 */var SubsCapsButton=function(_TextTrackButton){inherits(SubsCapsButton,_TextTrackButton);function SubsCapsButton(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,SubsCapsButton);// Although North America uses "captions" in most cases for
// "captions and subtitles" other locales use "subtitles"
var _this=possibleConstructorReturn(this,_TextTrackButton.call(this,player,options));_this.label_='subtitles';if(['en','en-us','en-ca','fr-ca'].indexOf(_this.player_.language_)>-1){_this.label_='captions';}_this.menuButton_.controlText(toTitleCase(_this.label_));return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */SubsCapsButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-subs-caps-button '+_TextTrackButton.prototype.buildCSSClass.call(this);};SubsCapsButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-subs-caps-button '+_TextTrackButton.prototype.buildWrapperCSSClass.call(this);};/**
   * Create caption/subtitles menu items
   *
   * @return {CaptionSettingsMenuItem[]}
   *         The array of current menu items.
   */SubsCapsButton.prototype.createItems=function createItems(){var items=[];if(!(this.player().tech_&&this.player().tech_.featuresNativeTextTracks)&&this.player().getChild('textTrackSettings')){items.push(new CaptionSettingsMenuItem(this.player_,{kind:this.label_}));this.hideThreshold_+=1;}items=_TextTrackButton.prototype.createItems.call(this,items,SubsCapsMenuItem);return items;};return SubsCapsButton;}(TextTrackButton);/**
 * `kind`s of TextTrack to look for to associate it with this menu.
 *
 * @type {array}
 * @private
 */SubsCapsButton.prototype.kinds_=['captions','subtitles'];/**
 * The text that should display over the `SubsCapsButton`s controls.
 *
 *
 * @type {string}
 * @private
 */SubsCapsButton.prototype.controlText_='Subtitles';Component.registerComponent('SubsCapsButton',SubsCapsButton);/**
 * @file audio-track-menu-item.js
 */ /**
 * An {@link AudioTrack} {@link MenuItem}
 *
 * @extends MenuItem
 */var AudioTrackMenuItem=function(_MenuItem){inherits(AudioTrackMenuItem,_MenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function AudioTrackMenuItem(player,options){classCallCheck(this,AudioTrackMenuItem);var track=options.track;var tracks=player.audioTracks();// Modify options for parent MenuItem class's init.
options.label=track.label||track.language||'Unknown';options.selected=track.enabled;var _this=possibleConstructorReturn(this,_MenuItem.call(this,player,options));_this.track=track;_this.addClass('vjs-'+track.kind+'-menu-item');var changeHandler=function changeHandler(){for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}_this.handleTracksChange.apply(_this,args);};tracks.addEventListener('change',changeHandler);_this.on('dispose',function(){tracks.removeEventListener('change',changeHandler);});return _this;}AudioTrackMenuItem.prototype.createEl=function createEl(type,props,attrs){var innerHTML='<span class="vjs-menu-item-text">'+this.localize(this.options_.label);if(this.options_.track.kind==='main-desc'){innerHTML+='\n        <span aria-hidden="true" class="vjs-icon-placeholder"></span>\n        <span class="vjs-control-text"> '+this.localize('Descriptions')+'</span>\n      ';}innerHTML+='</span>';var el=_MenuItem.prototype.createEl.call(this,type,assign$1({innerHTML:innerHTML},props),attrs);return el;};/**
   * This gets called when an `AudioTrackMenuItem is "clicked". See {@link ClickableComponent}
   * for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */AudioTrackMenuItem.prototype.handleClick=function handleClick(event){var tracks=this.player_.audioTracks();_MenuItem.prototype.handleClick.call(this,event);for(var i=0;i<tracks.length;i++){var track=tracks[i];track.enabled=track===this.track;}};/**
   * Handle any {@link AudioTrack} change.
   *
   * @param {EventTarget~Event} [event]
   *        The {@link AudioTrackList#change} event that caused this to run.
   *
   * @listens AudioTrackList#change
   */AudioTrackMenuItem.prototype.handleTracksChange=function handleTracksChange(event){this.selected(this.track.enabled);};return AudioTrackMenuItem;}(MenuItem);Component.registerComponent('AudioTrackMenuItem',AudioTrackMenuItem);/**
 * @file audio-track-button.js
 */ /**
 * The base class for buttons that toggle specific {@link AudioTrack} types.
 *
 * @extends TrackButton
 */var AudioTrackButton=function(_TrackButton){inherits(AudioTrackButton,_TrackButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options={}]
   *        The key/value store of player options.
   */function AudioTrackButton(player){var options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};classCallCheck(this,AudioTrackButton);options.tracks=player.audioTracks();return possibleConstructorReturn(this,_TrackButton.call(this,player,options));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */AudioTrackButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-audio-button '+_TrackButton.prototype.buildCSSClass.call(this);};AudioTrackButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-audio-button '+_TrackButton.prototype.buildWrapperCSSClass.call(this);};/**
   * Create a menu item for each audio track
   *
   * @param {AudioTrackMenuItem[]} [items=[]]
   *        An array of existing menu items to use.
   *
   * @return {AudioTrackMenuItem[]}
   *         An array of menu items
   */AudioTrackButton.prototype.createItems=function createItems(){var items=arguments.length>0&&arguments[0]!==undefined?arguments[0]:[];// if there's only one audio track, there no point in showing it
this.hideThreshold_=1;var tracks=this.player_.audioTracks();for(var i=0;i<tracks.length;i++){var track=tracks[i];items.push(new AudioTrackMenuItem(this.player_,{track:track,// MenuItem is selectable
selectable:true,// MenuItem is NOT multiSelectable (i.e. only one can be marked "selected" at a time)
multiSelectable:false}));}return items;};return AudioTrackButton;}(TrackButton);/**
 * The text that should display over the `AudioTrackButton`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */AudioTrackButton.prototype.controlText_='Audio Track';Component.registerComponent('AudioTrackButton',AudioTrackButton);/**
 * @file playback-rate-menu-item.js
 */ /**
 * The specific menu item type for selecting a playback rate.
 *
 * @extends MenuItem
 */var PlaybackRateMenuItem=function(_MenuItem){inherits(PlaybackRateMenuItem,_MenuItem);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function PlaybackRateMenuItem(player,options){classCallCheck(this,PlaybackRateMenuItem);var label=options.rate;var rate=parseFloat(label,10);// Modify options for parent MenuItem class's init.
options.label=label;options.selected=rate===1;options.selectable=true;options.multiSelectable=false;var _this=possibleConstructorReturn(this,_MenuItem.call(this,player,options));_this.label=label;_this.rate=rate;_this.on(player,'ratechange',_this.update);return _this;}/**
   * This gets called when an `PlaybackRateMenuItem` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */PlaybackRateMenuItem.prototype.handleClick=function handleClick(event){_MenuItem.prototype.handleClick.call(this);this.player().playbackRate(this.rate);};/**
   * Update the PlaybackRateMenuItem when the playbackrate changes.
   *
   * @param {EventTarget~Event} [event]
   *        The `ratechange` event that caused this function to run.
   *
   * @listens Player#ratechange
   */PlaybackRateMenuItem.prototype.update=function update(event){this.selected(this.player().playbackRate()===this.rate);};return PlaybackRateMenuItem;}(MenuItem);/**
 * The text that should display over the `PlaybackRateMenuItem`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */PlaybackRateMenuItem.prototype.contentElType='button';Component.registerComponent('PlaybackRateMenuItem',PlaybackRateMenuItem);/**
 * @file playback-rate-menu-button.js
 */ /**
 * The component for controlling the playback rate.
 *
 * @extends MenuButton
 */var PlaybackRateMenuButton=function(_MenuButton){inherits(PlaybackRateMenuButton,_MenuButton);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   */function PlaybackRateMenuButton(player,options){classCallCheck(this,PlaybackRateMenuButton);var _this=possibleConstructorReturn(this,_MenuButton.call(this,player,options));_this.updateVisibility();_this.updateLabel();_this.on(player,'loadstart',_this.updateVisibility);_this.on(player,'ratechange',_this.updateLabel);return _this;}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */PlaybackRateMenuButton.prototype.createEl=function createEl$$1(){var el=_MenuButton.prototype.createEl.call(this);this.labelEl_=createEl('div',{className:'vjs-playback-rate-value',innerHTML:'1x'});el.appendChild(this.labelEl_);return el;};PlaybackRateMenuButton.prototype.dispose=function dispose(){this.labelEl_=null;_MenuButton.prototype.dispose.call(this);};/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */PlaybackRateMenuButton.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-playback-rate '+_MenuButton.prototype.buildCSSClass.call(this);};PlaybackRateMenuButton.prototype.buildWrapperCSSClass=function buildWrapperCSSClass(){return 'vjs-playback-rate '+_MenuButton.prototype.buildWrapperCSSClass.call(this);};/**
   * Create the playback rate menu
   *
   * @return {Menu}
   *         Menu object populated with {@link PlaybackRateMenuItem}s
   */PlaybackRateMenuButton.prototype.createMenu=function createMenu(){var menu=new Menu(this.player());var rates=this.playbackRates();if(rates){for(var i=rates.length-1;i>=0;i--){menu.addChild(new PlaybackRateMenuItem(this.player(),{rate:rates[i]+'x'}));}}return menu;};/**
   * Updates ARIA accessibility attributes
   */PlaybackRateMenuButton.prototype.updateARIAAttributes=function updateARIAAttributes(){// Current playback rate
this.el().setAttribute('aria-valuenow',this.player().playbackRate());};/**
   * This gets called when an `PlaybackRateMenuButton` is "clicked". See
   * {@link ClickableComponent} for more detailed information on what a click can be.
   *
   * @param {EventTarget~Event} [event]
   *        The `keydown`, `tap`, or `click` event that caused this function to be
   *        called.
   *
   * @listens tap
   * @listens click
   */PlaybackRateMenuButton.prototype.handleClick=function handleClick(event){// select next rate option
var currentRate=this.player().playbackRate();var rates=this.playbackRates();// this will select first one if the last one currently selected
var newRate=rates[0];for(var i=0;i<rates.length;i++){if(rates[i]>currentRate){newRate=rates[i];break;}}this.player().playbackRate(newRate);};/**
   * Get possible playback rates
   *
   * @return {Array}
   *         All possible playback rates
   */PlaybackRateMenuButton.prototype.playbackRates=function playbackRates(){return this.options_.playbackRates||this.options_.playerOptions&&this.options_.playerOptions.playbackRates;};/**
   * Get whether playback rates is supported by the tech
   * and an array of playback rates exists
   *
   * @return {boolean}
   *         Whether changing playback rate is supported
   */PlaybackRateMenuButton.prototype.playbackRateSupported=function playbackRateSupported(){return this.player().tech_&&this.player().tech_.featuresPlaybackRate&&this.playbackRates()&&this.playbackRates().length>0;};/**
   * Hide playback rate controls when they're no playback rate options to select
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#loadstart
   */PlaybackRateMenuButton.prototype.updateVisibility=function updateVisibility(event){if(this.playbackRateSupported()){this.removeClass('vjs-hidden');}else {this.addClass('vjs-hidden');}};/**
   * Update button label when rate changed
   *
   * @param {EventTarget~Event} [event]
   *        The event that caused this function to run.
   *
   * @listens Player#ratechange
   */PlaybackRateMenuButton.prototype.updateLabel=function updateLabel(event){if(this.playbackRateSupported()){this.labelEl_.innerHTML=this.player().playbackRate()+'x';}};return PlaybackRateMenuButton;}(MenuButton);/**
 * The text that should display over the `FullscreenToggle`s controls. Added for localization.
 *
 * @type {string}
 * @private
 */PlaybackRateMenuButton.prototype.controlText_='Playback Rate';Component.registerComponent('PlaybackRateMenuButton',PlaybackRateMenuButton);/**
 * @file spacer.js
 */ /**
 * Just an empty spacer element that can be used as an append point for plugins, etc.
 * Also can be used to create space between elements when necessary.
 *
 * @extends Component
 */var Spacer=function(_Component){inherits(Spacer,_Component);function Spacer(){classCallCheck(this,Spacer);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */Spacer.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-spacer '+_Component.prototype.buildCSSClass.call(this);};/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */Spacer.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:this.buildCSSClass()});};return Spacer;}(Component);Component.registerComponent('Spacer',Spacer);/**
 * @file custom-control-spacer.js
 */ /**
 * Spacer specifically meant to be used as an insertion point for new plugins, etc.
 *
 * @extends Spacer
 */var CustomControlSpacer=function(_Spacer){inherits(CustomControlSpacer,_Spacer);function CustomControlSpacer(){classCallCheck(this,CustomControlSpacer);return possibleConstructorReturn(this,_Spacer.apply(this,arguments));}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   */CustomControlSpacer.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-custom-control-spacer '+_Spacer.prototype.buildCSSClass.call(this);};/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */CustomControlSpacer.prototype.createEl=function createEl(){var el=_Spacer.prototype.createEl.call(this,{className:this.buildCSSClass()});// No-flex/table-cell mode requires there be some content
// in the cell to fill the remaining space of the table.
el.innerHTML='\xA0';return el;};return CustomControlSpacer;}(Spacer);Component.registerComponent('CustomControlSpacer',CustomControlSpacer);/**
 * @file control-bar.js
 */ // Required children
/**
 * Container of main controls.
 *
 * @extends Component
 */var ControlBar=function(_Component){inherits(ControlBar,_Component);function ControlBar(){classCallCheck(this,ControlBar);return possibleConstructorReturn(this,_Component.apply(this,arguments));}/**
   * Create the `Component`'s DOM element
   *
   * @return {Element}
   *         The element that was created.
   */ControlBar.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'div',{className:'vjs-control-bar',dir:'ltr'});};return ControlBar;}(Component);/**
 * Default options for `ControlBar`
 *
 * @type {Object}
 * @private
 */ControlBar.prototype.options_={children:['playToggle','volumePanel','currentTimeDisplay','timeDivider','durationDisplay','progressControl','liveDisplay','remainingTimeDisplay','customControlSpacer','playbackRateMenuButton','chaptersButton','descriptionsButton','subsCapsButton','audioTrackButton','fullscreenToggle']};Component.registerComponent('ControlBar',ControlBar);/**
 * @file error-display.js
 */ /**
 * A display that indicates an error has occurred. This means that the video
 * is unplayable.
 *
 * @extends ModalDialog
 */var ErrorDisplay=function(_ModalDialog){inherits(ErrorDisplay,_ModalDialog);/**
   * Creates an instance of this class.
   *
   * @param  {Player} player
   *         The `Player` that this class should be attached to.
   *
   * @param  {Object} [options]
   *         The key/value store of player options.
   */function ErrorDisplay(player,options){classCallCheck(this,ErrorDisplay);var _this=possibleConstructorReturn(this,_ModalDialog.call(this,player,options));_this.on(player,'error',_this.open);return _this;}/**
   * Builds the default DOM `className`.
   *
   * @return {string}
   *         The DOM `className` for this object.
   *
   * @deprecated Since version 5.
   */ErrorDisplay.prototype.buildCSSClass=function buildCSSClass(){return 'vjs-error-display '+_ModalDialog.prototype.buildCSSClass.call(this);};/**
   * Gets the localized error message based on the `Player`s error.
   *
   * @return {string}
   *         The `Player`s error message localized or an empty string.
   */ErrorDisplay.prototype.content=function content(){var error=this.player().error();return error?this.localize(error.message):'';};return ErrorDisplay;}(ModalDialog);/**
 * The default options for an `ErrorDisplay`.
 *
 * @private
 */ErrorDisplay.prototype.options_=mergeOptions(ModalDialog.prototype.options_,{pauseOnOpen:false,fillAlways:true,temporary:false,uncloseable:true});Component.registerComponent('ErrorDisplay',ErrorDisplay);/**
 * @file text-track-settings.js
 */var LOCAL_STORAGE_KEY='vjs-text-track-settings';var COLOR_BLACK=['#000','Black'];var COLOR_BLUE=['#00F','Blue'];var COLOR_CYAN=['#0FF','Cyan'];var COLOR_GREEN=['#0F0','Green'];var COLOR_MAGENTA=['#F0F','Magenta'];var COLOR_RED=['#F00','Red'];var COLOR_WHITE=['#FFF','White'];var COLOR_YELLOW=['#FF0','Yellow'];var OPACITY_OPAQUE=['1','Opaque'];var OPACITY_SEMI=['0.5','Semi-Transparent'];var OPACITY_TRANS=['0','Transparent'];// Configuration for the various <select> elements in the DOM of this component.
//
// Possible keys include:
//
// `default`:
//   The default option index. Only needs to be provided if not zero.
// `parser`:
//   A function which is used to parse the value from the selected option in
//   a customized way.
// `selector`:
//   The selector used to find the associated <select> element.
var selectConfigs={backgroundColor:{selector:'.vjs-bg-color > select',id:'captions-background-color-%s',label:'Color',options:[COLOR_BLACK,COLOR_WHITE,COLOR_RED,COLOR_GREEN,COLOR_BLUE,COLOR_YELLOW,COLOR_MAGENTA,COLOR_CYAN]},backgroundOpacity:{selector:'.vjs-bg-opacity > select',id:'captions-background-opacity-%s',label:'Transparency',options:[OPACITY_OPAQUE,OPACITY_SEMI,OPACITY_TRANS]},color:{selector:'.vjs-fg-color > select',id:'captions-foreground-color-%s',label:'Color',options:[COLOR_WHITE,COLOR_BLACK,COLOR_RED,COLOR_GREEN,COLOR_BLUE,COLOR_YELLOW,COLOR_MAGENTA,COLOR_CYAN]},edgeStyle:{selector:'.vjs-edge-style > select',id:'%s',label:'Text Edge Style',options:[['none','None'],['raised','Raised'],['depressed','Depressed'],['uniform','Uniform'],['dropshadow','Dropshadow']]},fontFamily:{selector:'.vjs-font-family > select',id:'captions-font-family-%s',label:'Font Family',options:[['proportionalSansSerif','Proportional Sans-Serif'],['monospaceSansSerif','Monospace Sans-Serif'],['proportionalSerif','Proportional Serif'],['monospaceSerif','Monospace Serif'],['casual','Casual'],['script','Script'],['small-caps','Small Caps']]},fontPercent:{selector:'.vjs-font-percent > select',id:'captions-font-size-%s',label:'Font Size',options:[['0.50','50%'],['0.75','75%'],['1.00','100%'],['1.25','125%'],['1.50','150%'],['1.75','175%'],['2.00','200%'],['3.00','300%'],['4.00','400%']],'default':2,parser:function parser(v){return v==='1.00'?null:Number(v);}},textOpacity:{selector:'.vjs-text-opacity > select',id:'captions-foreground-opacity-%s',label:'Transparency',options:[OPACITY_OPAQUE,OPACITY_SEMI]},// Options for this object are defined below.
windowColor:{selector:'.vjs-window-color > select',id:'captions-window-color-%s',label:'Color'},// Options for this object are defined below.
windowOpacity:{selector:'.vjs-window-opacity > select',id:'captions-window-opacity-%s',label:'Transparency',options:[OPACITY_TRANS,OPACITY_SEMI,OPACITY_OPAQUE]}};selectConfigs.windowColor.options=selectConfigs.backgroundColor.options;/**
 * Get the actual value of an option.
 *
 * @param  {string} value
 *         The value to get
 *
 * @param  {Function} [parser]
 *         Optional function to adjust the value.
 *
 * @return {Mixed}
 *         - Will be `undefined` if no value exists
 *         - Will be `undefined` if the given value is "none".
 *         - Will be the actual value otherwise.
 *
 * @private
 */function parseOptionValue(value,parser){if(parser){value=parser(value);}if(value&&value!=='none'){return value;}}/**
 * Gets the value of the selected <option> element within a <select> element.
 *
 * @param  {Element} el
 *         the element to look in
 *
 * @param  {Function} [parser]
 *         Optional function to adjust the value.
 *
 * @return {Mixed}
 *         - Will be `undefined` if no value exists
 *         - Will be `undefined` if the given value is "none".
 *         - Will be the actual value otherwise.
 *
 * @private
 */function getSelectedOptionValue(el,parser){var value=el.options[el.options.selectedIndex].value;return parseOptionValue(value,parser);}/**
 * Sets the selected <option> element within a <select> element based on a
 * given value.
 *
 * @param {Element} el
 *        The element to look in.
 *
 * @param {string} value
 *        the property to look on.
 *
 * @param {Function} [parser]
 *        Optional function to adjust the value before comparing.
 *
 * @private
 */function setSelectedOption(el,value,parser){if(!value){return;}for(var i=0;i<el.options.length;i++){if(parseOptionValue(el.options[i].value,parser)===value){el.selectedIndex=i;break;}}}/**
 * Manipulate Text Tracks settings.
 *
 * @extends ModalDialog
 */var TextTrackSettings=function(_ModalDialog){inherits(TextTrackSettings,_ModalDialog);/**
   * Creates an instance of this class.
   *
   * @param {Player} player
   *         The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *         The key/value store of player options.
   */function TextTrackSettings(player,options){classCallCheck(this,TextTrackSettings);options.temporary=false;var _this=possibleConstructorReturn(this,_ModalDialog.call(this,player,options));_this.updateDisplay=bind(_this,_this.updateDisplay);// fill the modal and pretend we have opened it
_this.fill();_this.hasBeenOpened_=_this.hasBeenFilled_=true;_this.endDialog=createEl('p',{className:'vjs-control-text',textContent:_this.localize('End of dialog window.')});_this.el().appendChild(_this.endDialog);_this.setDefaults();// Grab `persistTextTrackSettings` from the player options if not passed in child options
if(options.persistTextTrackSettings===undefined){_this.options_.persistTextTrackSettings=_this.options_.playerOptions.persistTextTrackSettings;}_this.on(_this.$('.vjs-done-button'),'click',function(){_this.saveSettings();_this.close();});_this.on(_this.$('.vjs-default-button'),'click',function(){_this.setDefaults();_this.updateDisplay();});each(selectConfigs,function(config){_this.on(_this.$(config.selector),'change',_this.updateDisplay);});if(_this.options_.persistTextTrackSettings){_this.restoreSettings();}return _this;}TextTrackSettings.prototype.dispose=function dispose(){this.endDialog=null;_ModalDialog.prototype.dispose.call(this);};/**
   * Create a <select> element with configured options.
   *
   * @param {string} key
   *        Configuration key to use during creation.
   *
   * @return {string}
   *         An HTML string.
   *
   * @private
   */TextTrackSettings.prototype.createElSelect_=function createElSelect_(key){var _this2=this;var legendId=arguments.length>1&&arguments[1]!==undefined?arguments[1]:'';var type=arguments.length>2&&arguments[2]!==undefined?arguments[2]:'label';var config=selectConfigs[key];var id=config.id.replace('%s',this.id_);var selectLabelledbyIds=[legendId,id].join(' ').trim();return ['<'+type+' id="'+id+'" class="'+(type==='label'?'vjs-label':'')+'">',this.localize(config.label),'</'+type+'>','<select aria-labelledby="'+selectLabelledbyIds+'">'].concat(config.options.map(function(o){var optionId=id+'-'+o[1].replace(/\W+/g,'');return ['<option id="'+optionId+'" value="'+o[0]+'" ','aria-labelledby="'+selectLabelledbyIds+' '+optionId+'">',_this2.localize(o[1]),'</option>'].join('');})).concat('</select>').join('');};/**
   * Create foreground color element for the component
   *
   * @return {string}
   *         An HTML string.
   *
   * @private
   */TextTrackSettings.prototype.createElFgColor_=function createElFgColor_(){var legendId='captions-text-legend-'+this.id_;return ['<fieldset class="vjs-fg-color vjs-track-setting">','<legend id="'+legendId+'">',this.localize('Text'),'</legend>',this.createElSelect_('color',legendId),'<span class="vjs-text-opacity vjs-opacity">',this.createElSelect_('textOpacity',legendId),'</span>','</fieldset>'].join('');};/**
   * Create background color element for the component
   *
   * @return {string}
   *         An HTML string.
   *
   * @private
   */TextTrackSettings.prototype.createElBgColor_=function createElBgColor_(){var legendId='captions-background-'+this.id_;return ['<fieldset class="vjs-bg-color vjs-track-setting">','<legend id="'+legendId+'">',this.localize('Background'),'</legend>',this.createElSelect_('backgroundColor',legendId),'<span class="vjs-bg-opacity vjs-opacity">',this.createElSelect_('backgroundOpacity',legendId),'</span>','</fieldset>'].join('');};/**
   * Create window color element for the component
   *
   * @return {string}
   *         An HTML string.
   *
   * @private
   */TextTrackSettings.prototype.createElWinColor_=function createElWinColor_(){var legendId='captions-window-'+this.id_;return ['<fieldset class="vjs-window-color vjs-track-setting">','<legend id="'+legendId+'">',this.localize('Window'),'</legend>',this.createElSelect_('windowColor',legendId),'<span class="vjs-window-opacity vjs-opacity">',this.createElSelect_('windowOpacity',legendId),'</span>','</fieldset>'].join('');};/**
   * Create color elements for the component
   *
   * @return {Element}
   *         The element that was created
   *
   * @private
   */TextTrackSettings.prototype.createElColors_=function createElColors_(){return createEl('div',{className:'vjs-track-settings-colors',innerHTML:[this.createElFgColor_(),this.createElBgColor_(),this.createElWinColor_()].join('')});};/**
   * Create font elements for the component
   *
   * @return {Element}
   *         The element that was created.
   *
   * @private
   */TextTrackSettings.prototype.createElFont_=function createElFont_(){return createEl('div',{className:'vjs-track-settings-font',innerHTML:['<fieldset class="vjs-font-percent vjs-track-setting">',this.createElSelect_('fontPercent','','legend'),'</fieldset>','<fieldset class="vjs-edge-style vjs-track-setting">',this.createElSelect_('edgeStyle','','legend'),'</fieldset>','<fieldset class="vjs-font-family vjs-track-setting">',this.createElSelect_('fontFamily','','legend'),'</fieldset>'].join('')});};/**
   * Create controls for the component
   *
   * @return {Element}
   *         The element that was created.
   *
   * @private
   */TextTrackSettings.prototype.createElControls_=function createElControls_(){var defaultsDescription=this.localize('restore all settings to the default values');return createEl('div',{className:'vjs-track-settings-controls',innerHTML:['<button type="button" class="vjs-default-button" title="'+defaultsDescription+'">',this.localize('Reset'),'<span class="vjs-control-text"> '+defaultsDescription+'</span>','</button>','<button type="button" class="vjs-done-button">'+this.localize('Done')+'</button>'].join('')});};TextTrackSettings.prototype.content=function content(){return [this.createElColors_(),this.createElFont_(),this.createElControls_()];};TextTrackSettings.prototype.label=function label(){return this.localize('Caption Settings Dialog');};TextTrackSettings.prototype.description=function description(){return this.localize('Beginning of dialog window. Escape will cancel and close the window.');};TextTrackSettings.prototype.buildCSSClass=function buildCSSClass(){return _ModalDialog.prototype.buildCSSClass.call(this)+' vjs-text-track-settings';};/**
   * Gets an object of text track settings (or null).
   *
   * @return {Object}
   *         An object with config values parsed from the DOM or localStorage.
   */TextTrackSettings.prototype.getValues=function getValues(){var _this3=this;return reduce(selectConfigs,function(accum,config,key){var value=getSelectedOptionValue(_this3.$(config.selector),config.parser);if(value!==undefined){accum[key]=value;}return accum;},{});};/**
   * Sets text track settings from an object of values.
   *
   * @param {Object} values
   *        An object with config values parsed from the DOM or localStorage.
   */TextTrackSettings.prototype.setValues=function setValues(values){var _this4=this;each(selectConfigs,function(config,key){setSelectedOption(_this4.$(config.selector),values[key],config.parser);});};/**
   * Sets all `<select>` elements to their default values.
   */TextTrackSettings.prototype.setDefaults=function setDefaults(){var _this5=this;each(selectConfigs,function(config){var index=config.hasOwnProperty('default')?config['default']:0;_this5.$(config.selector).selectedIndex=index;});};/**
   * Restore texttrack settings from localStorage
   */TextTrackSettings.prototype.restoreSettings=function restoreSettings(){var values=void 0;try{values=JSON.parse(window$1.localStorage.getItem(LOCAL_STORAGE_KEY));}catch(err){log$1.warn(err);}if(values){this.setValues(values);}};/**
   * Save text track settings to localStorage
   */TextTrackSettings.prototype.saveSettings=function saveSettings(){if(!this.options_.persistTextTrackSettings){return;}var values=this.getValues();try{if(Object.keys(values).length){window$1.localStorage.setItem(LOCAL_STORAGE_KEY,JSON.stringify(values));}else {window$1.localStorage.removeItem(LOCAL_STORAGE_KEY);}}catch(err){log$1.warn(err);}};/**
   * Update display of text track settings
   */TextTrackSettings.prototype.updateDisplay=function updateDisplay(){var ttDisplay=this.player_.getChild('textTrackDisplay');if(ttDisplay){ttDisplay.updateDisplay();}};/**
   * conditionally blur the element and refocus the captions button
   *
   * @private
   */TextTrackSettings.prototype.conditionalBlur_=function conditionalBlur_(){this.previouslyActiveEl_=null;this.off(document$1,'keydown',this.handleKeyDown);var cb=this.player_.controlBar;var subsCapsBtn=cb&&cb.subsCapsButton;var ccBtn=cb&&cb.captionsButton;if(subsCapsBtn){subsCapsBtn.focus();}else if(ccBtn){ccBtn.focus();}};return TextTrackSettings;}(ModalDialog);Component.registerComponent('TextTrackSettings',TextTrackSettings);/**
 * @file resize-manager.js
 */ /**
 * A Resize Manager. It is in charge of triggering `playerresize` on the player in the right conditions.
 *
 * It'll either create an iframe and use a debounced resize handler on it or use the new {@link https://wicg.github.io/ResizeObserver/|ResizeObserver}.
 *
 * If the ResizeObserver is available natively, it will be used. A polyfill can be passed in as an option.
 * If a `playerresize` event is not needed, the ResizeManager component can be removed from the player, see the example below.
 * @example <caption>How to disable the resize manager</caption>
 * const player = videojs('#vid', {
 *   resizeManager: false
 * });
 *
 * @see {@link https://wicg.github.io/ResizeObserver/|ResizeObserver specification}
 *
 * @extends Component
 */var ResizeManager=function(_Component){inherits(ResizeManager,_Component);/**
   * Create the ResizeManager.
   *
   * @param {Object} player
   *        The `Player` that this class should be attached to.
   *
   * @param {Object} [options]
   *        The key/value store of ResizeManager options.
   *
   * @param {Object} [options.ResizeObserver]
   *        A polyfill for ResizeObserver can be passed in here.
   *        If this is set to null it will ignore the native ResizeObserver and fall back to the iframe fallback.
   */function ResizeManager(player,options){classCallCheck(this,ResizeManager);var RESIZE_OBSERVER_AVAILABLE=options.ResizeObserver||window$1.ResizeObserver;// if `null` was passed, we want to disable the ResizeObserver
if(options.ResizeObserver===null){RESIZE_OBSERVER_AVAILABLE=false;}// Only create an element when ResizeObserver isn't available
var options_=mergeOptions({createEl:!RESIZE_OBSERVER_AVAILABLE},options);var _this=possibleConstructorReturn(this,_Component.call(this,player,options_));_this.ResizeObserver=options.ResizeObserver||window$1.ResizeObserver;_this.loadListener_=null;_this.resizeObserver_=null;_this.debouncedHandler_=debounce(function(){_this.resizeHandler();},100,false,_this);if(RESIZE_OBSERVER_AVAILABLE){_this.resizeObserver_=new _this.ResizeObserver(_this.debouncedHandler_);_this.resizeObserver_.observe(player.el());}else {_this.loadListener_=function(){if(!_this.el_||!_this.el_.contentWindow){return;}on(_this.el_.contentWindow,'resize',_this.debouncedHandler_);};_this.one('load',_this.loadListener_);}return _this;}ResizeManager.prototype.createEl=function createEl(){return _Component.prototype.createEl.call(this,'iframe',{className:'vjs-resize-manager'});};/**
   * Called when a resize is triggered on the iframe or a resize is observed via the ResizeObserver
   *
   * @fires Player#playerresize
   */ResizeManager.prototype.resizeHandler=function resizeHandler(){/**
     * Called when the player size has changed
     *
     * @event Player#playerresize
     * @type {EventTarget~Event}
     */ // make sure player is still around to trigger
// prevents this from causing an error after dispose
if(!this.player_||!this.player_.trigger){return;}this.player_.trigger('playerresize');};ResizeManager.prototype.dispose=function dispose(){if(this.debouncedHandler_){this.debouncedHandler_.cancel();}if(this.resizeObserver_){if(this.player_.el()){this.resizeObserver_.unobserve(this.player_.el());}this.resizeObserver_.disconnect();}if(this.el_&&this.el_.contentWindow){off(this.el_.contentWindow,'resize',this.debouncedHandler_);}if(this.loadListener_){this.off('load',this.loadListener_);}this.ResizeObserver=null;this.resizeObserver=null;this.debouncedHandler_=null;this.loadListener_=null;};return ResizeManager;}(Component);Component.registerComponent('ResizeManager',ResizeManager);/**
 * This function is used to fire a sourceset when there is something
 * similar to `mediaEl.load()` being called. It will try to find the source via
 * the `src` attribute and then the `<source>` elements. It will then fire `sourceset`
 * with the source that was found or empty string if we cannot know. If it cannot
 * find a source then `sourceset` will not be fired.
 *
 * @param {Html5} tech
 *        The tech object that sourceset was setup on
 *
 * @return {boolean}
 *         returns false if the sourceset was not fired and true otherwise.
 */var sourcesetLoad=function sourcesetLoad(tech){var el=tech.el();// if `el.src` is set, that source will be loaded.
if(el.hasAttribute('src')){tech.triggerSourceset(el.src);return true;}/**
   * Since there isn't a src property on the media element, source elements will be used for
   * implementing the source selection algorithm. This happens asynchronously and
   * for most cases were there is more than one source we cannot tell what source will
   * be loaded, without re-implementing the source selection algorithm. At this time we are not
   * going to do that. There are three special cases that we do handle here though:
   *
   * 1. If there are no sources, do not fire `sourceset`.
   * 2. If there is only one `<source>` with a `src` property/attribute that is our `src`
   * 3. If there is more than one `<source>` but all of them have the same `src` url.
   *    That will be our src.
   */var sources=tech.$$('source');var srcUrls=[];var src='';// if there are no sources, do not fire sourceset
if(!sources.length){return false;}// only count valid/non-duplicate source elements
for(var i=0;i<sources.length;i++){var url=sources[i].src;if(url&&srcUrls.indexOf(url)===-1){srcUrls.push(url);}}// there were no valid sources
if(!srcUrls.length){return false;}// there is only one valid source element url
// use that
if(srcUrls.length===1){src=srcUrls[0];}tech.triggerSourceset(src);return true;};/**
 * our implementation of an `innerHTML` descriptor for browsers
 * that do not have one.
 */var innerHTMLDescriptorPolyfill={};if(!IS_IE8){innerHTMLDescriptorPolyfill=Object.defineProperty({},'innerHTML',{get:function get(){return this.cloneNode(true).innerHTML;},set:function set(v){// make a dummy node to use innerHTML on
var dummy=document$1.createElement(this.nodeName.toLowerCase());// set innerHTML to the value provided
dummy.innerHTML=v;// make a document fragment to hold the nodes from dummy
var docFrag=document$1.createDocumentFragment();// copy all of the nodes created by the innerHTML on dummy
// to the document fragment
while(dummy.childNodes.length){docFrag.appendChild(dummy.childNodes[0]);}// remove content
this.innerText='';// now we add all of that html in one by appending the
// document fragment. This is how innerHTML does it.
window$1.Element.prototype.appendChild.call(this,docFrag);// then return the result that innerHTML's setter would
return this.innerHTML;}});}/**
 * Get a property descriptor given a list of priorities and the
 * property to get.
 */var getDescriptor=function getDescriptor(priority,prop){var descriptor={};for(var i=0;i<priority.length;i++){descriptor=Object.getOwnPropertyDescriptor(priority[i],prop);if(descriptor&&descriptor.set&&descriptor.get){break;}}descriptor.enumerable=true;descriptor.configurable=true;return descriptor;};var getInnerHTMLDescriptor=function getInnerHTMLDescriptor(tech){return getDescriptor([tech.el(),window$1.HTMLMediaElement.prototype,window$1.Element.prototype,innerHTMLDescriptorPolyfill],'innerHTML');};/**
 * Patches browser internal functions so that we can tell syncronously
 * if a `<source>` was appended to the media element. For some reason this
 * causes a `sourceset` if the the media element is ready and has no source.
 * This happens when:
 * - The page has just loaded and the media element does not have a source.
 * - The media element was emptied of all sources, then `load()` was called.
 *
 * It does this by patching the following functions/properties when they are supported:
 *
 * - `append()` - can be used to add a `<source>` element to the media element
 * - `appendChild()` - can be used to add a `<source>` element to the media element
 * - `insertAdjacentHTML()` -  can be used to add a `<source>` element to the media element
 * - `innerHTML` -  can be used to add a `<source>` element to the media element
 *
 * @param {Html5} tech
 *        The tech object that sourceset is being setup on.
 */var firstSourceWatch=function firstSourceWatch(tech){var el=tech.el();// make sure firstSourceWatch isn't setup twice.
if(el.resetSourceWatch_){return;}var old={};var innerDescriptor=getInnerHTMLDescriptor(tech);var appendWrapper=function appendWrapper(appendFn){return function(){for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}var retval=appendFn.apply(el,args);sourcesetLoad(tech);return retval;};};['append','appendChild','insertAdjacentHTML'].forEach(function(k){if(!el[k]){return;}// store the old function
old[k]=el[k];// call the old function with a sourceset if a source
// was loaded
el[k]=appendWrapper(old[k]);});Object.defineProperty(el,'innerHTML',mergeOptions(innerDescriptor,{set:appendWrapper(innerDescriptor.set)}));el.resetSourceWatch_=function(){el.resetSourceWatch_=null;Object.keys(old).forEach(function(k){el[k]=old[k];});Object.defineProperty(el,'innerHTML',innerDescriptor);};// on the first sourceset, we need to revert our changes
tech.one('sourceset',el.resetSourceWatch_);};/**
 * our implementation of a `src` descriptor for browsers
 * that do not have one.
 */var srcDescriptorPolyfill={};if(!IS_IE8){srcDescriptorPolyfill=Object.defineProperty({},'src',{get:function get(){if(this.hasAttribute('src')){return getAbsoluteURL(window$1.Element.prototype.getAttribute.call(this,'src'));}return '';},set:function set(v){window$1.Element.prototype.setAttribute.call(this,'src',v);return v;}});}var getSrcDescriptor=function getSrcDescriptor(tech){return getDescriptor([tech.el(),window$1.HTMLMediaElement.prototype,srcDescriptorPolyfill],'src');};/**
 * setup `sourceset` handling on the `Html5` tech. This function
 * patches the following element properties/functions:
 *
 * - `src` - to determine when `src` is set
 * - `setAttribute()` - to determine when `src` is set
 * - `load()` - this re-triggers the source selection algorithm, and can
 *              cause a sourceset.
 *
 * If there is no source when we are adding `sourceset` support or during a `load()`
 * we also patch the functions listed in `firstSourceWatch`.
 *
 * @param {Html5} tech
 *        The tech to patch
 */var setupSourceset=function setupSourceset(tech){if(!tech.featuresSourceset){return;}var el=tech.el();// make sure sourceset isn't setup twice.
if(el.resetSourceset_){return;}var srcDescriptor=getSrcDescriptor(tech);var oldSetAttribute=el.setAttribute;var oldLoad=el.load;Object.defineProperty(el,'src',mergeOptions(srcDescriptor,{set:function set(v){var retval=srcDescriptor.set.call(el,v);// we use the getter here to get the actual value set on src
tech.triggerSourceset(el.src);return retval;}}));el.setAttribute=function(n,v){var retval=oldSetAttribute.call(el,n,v);if(/src/i.test(n)){tech.triggerSourceset(el.src);}return retval;};el.load=function(){var retval=oldLoad.call(el);// if load was called, but there was no source to fire
// sourceset on. We have to watch for a source append
// as that can trigger a `sourceset` when the media element
// has no source
if(!sourcesetLoad(tech)){tech.triggerSourceset('');firstSourceWatch(tech);}return retval;};if(el.currentSrc){tech.triggerSourceset(el.currentSrc);}else if(!sourcesetLoad(tech)){firstSourceWatch(tech);}el.resetSourceset_=function(){el.resetSourceset_=null;el.load=oldLoad;el.setAttribute=oldSetAttribute;Object.defineProperty(el,'src',srcDescriptor);if(el.resetSourceWatch_){el.resetSourceWatch_();}};};var _templateObject$2=taggedTemplateLiteralLoose(['Text Tracks are being loaded from another origin but the crossorigin attribute isn\'t used.\n            This may prevent text tracks from loading.'],['Text Tracks are being loaded from another origin but the crossorigin attribute isn\'t used.\n            This may prevent text tracks from loading.']);/**
 * @file html5.js
 */ /**
 * HTML5 Media Controller - Wrapper for HTML5 Media API
 *
 * @mixes Tech~SouceHandlerAdditions
 * @extends Tech
 */var Html5=function(_Tech){inherits(Html5,_Tech);/**
   * Create an instance of this Tech.
   *
   * @param {Object} [options]
   *        The key/value store of player options.
   *
   * @param {Component~ReadyCallback} ready
   *        Callback function to call when the `HTML5` Tech is ready.
   */function Html5(options,ready){classCallCheck(this,Html5);var _this=possibleConstructorReturn(this,_Tech.call(this,options,ready));var source=options.source;var crossoriginTracks=false;// Set the source if one is provided
// 1) Check if the source is new (if not, we want to keep the original so playback isn't interrupted)
// 2) Check to see if the network state of the tag was failed at init, and if so, reset the source
// anyway so the error gets fired.
if(source&&(_this.el_.currentSrc!==source.src||options.tag&&options.tag.initNetworkState_===3)){_this.setSource(source);}else {_this.handleLateInit_(_this.el_);}// setup sourceset after late sourceset/init
if(options.enableSourceset){_this.setupSourcesetHandling_();}if(_this.el_.hasChildNodes()){var nodes=_this.el_.childNodes;var nodesLength=nodes.length;var removeNodes=[];while(nodesLength--){var node=nodes[nodesLength];var nodeName=node.nodeName.toLowerCase();if(nodeName==='track'){if(!_this.featuresNativeTextTracks){// Empty video tag tracks so the built-in player doesn't use them also.
// This may not be fast enough to stop HTML5 browsers from reading the tags
// so we'll need to turn off any default tracks if we're manually doing
// captions and subtitles. videoElement.textTracks
removeNodes.push(node);}else {// store HTMLTrackElement and TextTrack to remote list
_this.remoteTextTrackEls().addTrackElement_(node);_this.remoteTextTracks().addTrack(node.track);_this.textTracks().addTrack(node.track);if(!crossoriginTracks&&!_this.el_.hasAttribute('crossorigin')&&isCrossOrigin(node.src)){crossoriginTracks=true;}}}}for(var i=0;i<removeNodes.length;i++){_this.el_.removeChild(removeNodes[i]);}}_this.proxyNativeTracks_();if(_this.featuresNativeTextTracks&&crossoriginTracks){log$1.warn(tsml(_templateObject$2));}// prevent iOS Safari from disabling metadata text tracks during native playback
_this.restoreMetadataTracksInIOSNativePlayer_();// Determine if native controls should be used
// Our goal should be to get the custom controls on mobile solid everywhere
// so we can remove this all together. Right now this will block custom
// controls on touch enabled laptops like the Chrome Pixel
if((TOUCH_ENABLED||IS_IPHONE||IS_NATIVE_ANDROID)&&options.nativeControlsForTouch===true){_this.setControls(true);}// on iOS, we want to proxy `webkitbeginfullscreen` and `webkitendfullscreen`
// into a `fullscreenchange` event
_this.proxyWebkitFullscreen_();_this.triggerReady();return _this;}/**
   * Dispose of `HTML5` media element and remove all tracks.
   */Html5.prototype.dispose=function dispose(){if(this.el_&&this.el_.resetSourceset_){this.el_.resetSourceset_();}Html5.disposeMediaElement(this.el_);this.options_=null;// tech will handle clearing of the emulated track list
_Tech.prototype.dispose.call(this);};/**
   * Modify the media element so that we can detect when
   * the source is changed. Fires `sourceset` just after the source has changed
   */Html5.prototype.setupSourcesetHandling_=function setupSourcesetHandling_(){setupSourceset(this);};/**
   * When a captions track is enabled in the iOS Safari native player, all other
   * tracks are disabled (including metadata tracks), which nulls all of their
   * associated cue points. This will restore metadata tracks to their pre-fullscreen
   * state in those cases so that cue points are not needlessly lost.
   *
   * @private
   */Html5.prototype.restoreMetadataTracksInIOSNativePlayer_=function restoreMetadataTracksInIOSNativePlayer_(){var textTracks=this.textTracks();var metadataTracksPreFullscreenState=void 0;// captures a snapshot of every metadata track's current state
var takeMetadataTrackSnapshot=function takeMetadataTrackSnapshot(){metadataTracksPreFullscreenState=[];for(var i=0;i<textTracks.length;i++){var track=textTracks[i];if(track.kind==='metadata'){metadataTracksPreFullscreenState.push({track:track,storedMode:track.mode});}}};// snapshot each metadata track's initial state, and update the snapshot
// each time there is a track 'change' event
takeMetadataTrackSnapshot();textTracks.addEventListener('change',takeMetadataTrackSnapshot);this.on('dispose',function(){return textTracks.removeEventListener('change',takeMetadataTrackSnapshot);});var restoreTrackMode=function restoreTrackMode(){for(var i=0;i<metadataTracksPreFullscreenState.length;i++){var storedTrack=metadataTracksPreFullscreenState[i];if(storedTrack.track.mode==='disabled'&&storedTrack.track.mode!==storedTrack.storedMode){storedTrack.track.mode=storedTrack.storedMode;}}// we only want this handler to be executed on the first 'change' event
textTracks.removeEventListener('change',restoreTrackMode);};// when we enter fullscreen playback, stop updating the snapshot and
// restore all track modes to their pre-fullscreen state
this.on('webkitbeginfullscreen',function(){textTracks.removeEventListener('change',takeMetadataTrackSnapshot);// remove the listener before adding it just in case it wasn't previously removed
textTracks.removeEventListener('change',restoreTrackMode);textTracks.addEventListener('change',restoreTrackMode);});// start updating the snapshot again after leaving fullscreen
this.on('webkitendfullscreen',function(){// remove the listener before adding it just in case it wasn't previously removed
textTracks.removeEventListener('change',takeMetadataTrackSnapshot);textTracks.addEventListener('change',takeMetadataTrackSnapshot);// remove the restoreTrackMode handler in case it wasn't triggered during fullscreen playback
textTracks.removeEventListener('change',restoreTrackMode);});};/**
   * Proxy all native track list events to our track lists if the browser we are playing
   * in supports that type of track list.
   *
   * @private
   */Html5.prototype.proxyNativeTracks_=function proxyNativeTracks_(){var _this2=this;NORMAL.names.forEach(function(name){var props=NORMAL[name];var elTracks=_this2.el()[props.getterName];var techTracks=_this2[props.getterName]();if(!_this2['featuresNative'+props.capitalName+'Tracks']||!elTracks||!elTracks.addEventListener){return;}var listeners={change:function change(e){techTracks.trigger({type:'change',target:techTracks,currentTarget:techTracks,srcElement:techTracks});},addtrack:function addtrack(e){techTracks.addTrack(e.track);},removetrack:function removetrack(e){techTracks.removeTrack(e.track);}};var removeOldTracks=function removeOldTracks(){var removeTracks=[];for(var i=0;i<techTracks.length;i++){var found=false;for(var j=0;j<elTracks.length;j++){if(elTracks[j]===techTracks[i]){found=true;break;}}if(!found){removeTracks.push(techTracks[i]);}}while(removeTracks.length){techTracks.removeTrack(removeTracks.shift());}};Object.keys(listeners).forEach(function(eventName){var listener=listeners[eventName];elTracks.addEventListener(eventName,listener);_this2.on('dispose',function(e){return elTracks.removeEventListener(eventName,listener);});});// Remove (native) tracks that are not used anymore
_this2.on('loadstart',removeOldTracks);_this2.on('dispose',function(e){return _this2.off('loadstart',removeOldTracks);});});};/**
   * Create the `Html5` Tech's DOM element.
   *
   * @return {Element}
   *         The element that gets created.
   */Html5.prototype.createEl=function createEl$$1(){var el=this.options_.tag;// Check if this browser supports moving the element into the box.
// On the iPhone video will break if you move the element,
// So we have to create a brand new element.
// If we ingested the player div, we do not need to move the media element.
if(!el||!(this.options_.playerElIngest||this.movingMediaElementInDOM)){// If the original tag is still there, clone and remove it.
if(el){var clone=el.cloneNode(true);if(el.parentNode){el.parentNode.insertBefore(clone,el);}Html5.disposeMediaElement(el);el=clone;}else {el=document$1.createElement('video');// determine if native controls should be used
var tagAttributes=this.options_.tag&&getAttributes(this.options_.tag);var attributes=mergeOptions({},tagAttributes);if(!TOUCH_ENABLED||this.options_.nativeControlsForTouch!==true){delete attributes.controls;}setAttributes(el,assign$1(attributes,{id:this.options_.techId,'class':'vjs-tech'}));}el.playerId=this.options_.playerId;}if(typeof this.options_.preload!=='undefined'){setAttribute(el,'preload',this.options_.preload);}// Update specific tag settings, in case they were overridden
// `autoplay` has to be *last* so that `muted` and `playsinline` are present
// when iOS/Safari or other browsers attempt to autoplay.
var settingsAttrs=['loop','muted','playsinline','autoplay'];for(var i=0;i<settingsAttrs.length;i++){var attr=settingsAttrs[i];var value=this.options_[attr];if(typeof value!=='undefined'){if(value){setAttribute(el,attr,attr);}else {removeAttribute(el,attr);}el[attr]=value;}}return el;};/**
   * This will be triggered if the loadstart event has already fired, before videojs was
   * ready. Two known examples of when this can happen are:
   * 1. If we're loading the playback object after it has started loading
   * 2. The media is already playing the (often with autoplay on) then
   *
   * This function will fire another loadstart so that videojs can catchup.
   *
   * @fires Tech#loadstart
   *
   * @return {undefined}
   *         returns nothing.
   */Html5.prototype.handleLateInit_=function handleLateInit_(el){if(el.networkState===0||el.networkState===3){// The video element hasn't started loading the source yet
// or didn't find a source
return;}if(el.readyState===0){// NetworkState is set synchronously BUT loadstart is fired at the
// end of the current stack, usually before setInterval(fn, 0).
// So at this point we know loadstart may have already fired or is
// about to fire, and either way the player hasn't seen it yet.
// We don't want to fire loadstart prematurely here and cause a
// double loadstart so we'll wait and see if it happens between now
// and the next loop, and fire it if not.
// HOWEVER, we also want to make sure it fires before loadedmetadata
// which could also happen between now and the next loop, so we'll
// watch for that also.
var loadstartFired=false;var setLoadstartFired=function setLoadstartFired(){loadstartFired=true;};this.on('loadstart',setLoadstartFired);var triggerLoadstart=function triggerLoadstart(){// We did miss the original loadstart. Make sure the player
// sees loadstart before loadedmetadata
if(!loadstartFired){this.trigger('loadstart');}};this.on('loadedmetadata',triggerLoadstart);this.ready(function(){this.off('loadstart',setLoadstartFired);this.off('loadedmetadata',triggerLoadstart);if(!loadstartFired){// We did miss the original native loadstart. Fire it now.
this.trigger('loadstart');}});return;}// From here on we know that loadstart already fired and we missed it.
// The other readyState events aren't as much of a problem if we double
// them, so not going to go to as much trouble as loadstart to prevent
// that unless we find reason to.
var eventsToTrigger=['loadstart'];// loadedmetadata: newly equal to HAVE_METADATA (1) or greater
eventsToTrigger.push('loadedmetadata');// loadeddata: newly increased to HAVE_CURRENT_DATA (2) or greater
if(el.readyState>=2){eventsToTrigger.push('loadeddata');}// canplay: newly increased to HAVE_FUTURE_DATA (3) or greater
if(el.readyState>=3){eventsToTrigger.push('canplay');}// canplaythrough: newly equal to HAVE_ENOUGH_DATA (4)
if(el.readyState>=4){eventsToTrigger.push('canplaythrough');}// We still need to give the player time to add event listeners
this.ready(function(){eventsToTrigger.forEach(function(type){this.trigger(type);},this);});};/**
   * Set current time for the `HTML5` tech.
   *
   * @param {number} seconds
   *        Set the current time of the media to this.
   */Html5.prototype.setCurrentTime=function setCurrentTime(seconds){try{this.el_.currentTime=seconds;}catch(e){log$1(e,'Video is not ready. (Video.js)');// this.warning(VideoJS.warnings.videoNotReady);
}};/**
   * Get the current duration of the HTML5 media element.
   *
   * @return {number}
   *         The duration of the media or 0 if there is no duration.
   */Html5.prototype.duration=function duration(){var _this3=this;// Android Chrome will report duration as Infinity for VOD HLS until after
// playback has started, which triggers the live display erroneously.
// Return NaN if playback has not started and trigger a durationupdate once
// the duration can be reliably known.
if(this.el_.duration===Infinity&&IS_ANDROID&&IS_CHROME&&this.el_.currentTime===0){// Wait for the first `timeupdate` with currentTime > 0 - there may be
// several with 0
var checkProgress=function checkProgress(){if(_this3.el_.currentTime>0){// Trigger durationchange for genuinely live video
if(_this3.el_.duration===Infinity){_this3.trigger('durationchange');}_this3.off('timeupdate',checkProgress);}};this.on('timeupdate',checkProgress);return NaN;}return this.el_.duration||NaN;};/**
   * Get the current width of the HTML5 media element.
   *
   * @return {number}
   *         The width of the HTML5 media element.
   */Html5.prototype.width=function width(){return this.el_.offsetWidth;};/**
   * Get the current height of the HTML5 media element.
   *
   * @return {number}
   *         The heigth of the HTML5 media element.
   */Html5.prototype.height=function height(){return this.el_.offsetHeight;};/**
   * Proxy iOS `webkitbeginfullscreen` and `webkitendfullscreen` into
   * `fullscreenchange` event.
   *
   * @private
   * @fires fullscreenchange
   * @listens webkitendfullscreen
   * @listens webkitbeginfullscreen
   * @listens webkitbeginfullscreen
   */Html5.prototype.proxyWebkitFullscreen_=function proxyWebkitFullscreen_(){var _this4=this;if(!('webkitDisplayingFullscreen'in this.el_)){return;}var endFn=function endFn(){this.trigger('fullscreenchange',{isFullscreen:false});};var beginFn=function beginFn(){if('webkitPresentationMode'in this.el_&&this.el_.webkitPresentationMode!=='picture-in-picture'){this.one('webkitendfullscreen',endFn);this.trigger('fullscreenchange',{isFullscreen:true});}};this.on('webkitbeginfullscreen',beginFn);this.on('dispose',function(){_this4.off('webkitbeginfullscreen',beginFn);_this4.off('webkitendfullscreen',endFn);});};/**
   * Check if fullscreen is supported on the current playback device.
   *
   * @return {boolean}
   *         - True if fullscreen is supported.
   *         - False if fullscreen is not supported.
   */Html5.prototype.supportsFullScreen=function supportsFullScreen(){if(typeof this.el_.webkitEnterFullScreen==='function'){var userAgent=window$1.navigator&&window$1.navigator.userAgent||'';// Seems to be broken in Chromium/Chrome && Safari in Leopard
if(/Android/.test(userAgent)||!/Chrome|Mac OS X 10.5/.test(userAgent)){return true;}}return false;};/**
   * Request that the `HTML5` Tech enter fullscreen.
   */Html5.prototype.enterFullScreen=function enterFullScreen(){var video=this.el_;if(video.paused&&video.networkState<=video.HAVE_METADATA){// attempt to prime the video element for programmatic access
// this isn't necessary on the desktop but shouldn't hurt
this.el_.play();// playing and pausing synchronously during the transition to fullscreen
// can get iOS ~6.1 devices into a play/pause loop
this.setTimeout(function(){video.pause();video.webkitEnterFullScreen();},0);}else {video.webkitEnterFullScreen();}};/**
   * Request that the `HTML5` Tech exit fullscreen.
   */Html5.prototype.exitFullScreen=function exitFullScreen(){this.el_.webkitExitFullScreen();};/**
   * A getter/setter for the `Html5` Tech's source object.
   * > Note: Please use {@link Html5#setSource}
   *
   * @param {Tech~SourceObject} [src]
   *        The source object you want to set on the `HTML5` techs element.
   *
   * @return {Tech~SourceObject|undefined}
   *         - The current source object when a source is not passed in.
   *         - undefined when setting
   *
   * @deprecated Since version 5.
   */Html5.prototype.src=function src(_src){if(_src===undefined){return this.el_.src;}// Setting src through `src` instead of `setSrc` will be deprecated
this.setSrc(_src);};/**
   * Reset the tech by removing all sources and then calling
   * {@link Html5.resetMediaElement}.
   */Html5.prototype.reset=function reset(){Html5.resetMediaElement(this.el_);};/**
   * Get the current source on the `HTML5` Tech. Falls back to returning the source from
   * the HTML5 media element.
   *
   * @return {Tech~SourceObject}
   *         The current source object from the HTML5 tech. With a fallback to the
   *         elements source.
   */Html5.prototype.currentSrc=function currentSrc(){if(this.currentSource_){return this.currentSource_.src;}return this.el_.currentSrc;};/**
   * Set controls attribute for the HTML5 media Element.
   *
   * @param {string} val
   *        Value to set the controls attribute to
   */Html5.prototype.setControls=function setControls(val){this.el_.controls=!!val;};/**
   * Create and returns a remote {@link TextTrack} object.
   *
   * @param {string} kind
   *        `TextTrack` kind (subtitles, captions, descriptions, chapters, or metadata)
   *
   * @param {string} [label]
   *        Label to identify the text track
   *
   * @param {string} [language]
   *        Two letter language abbreviation
   *
   * @return {TextTrack}
   *         The TextTrack that gets created.
   */Html5.prototype.addTextTrack=function addTextTrack(kind,label,language){if(!this.featuresNativeTextTracks){return _Tech.prototype.addTextTrack.call(this,kind,label,language);}return this.el_.addTextTrack(kind,label,language);};/**
   * Creates either native TextTrack or an emulated TextTrack depending
   * on the value of `featuresNativeTextTracks`
   *
   * @param {Object} options
   *        The object should contain the options to intialize the TextTrack with.
   *
   * @param {string} [options.kind]
   *        `TextTrack` kind (subtitles, captions, descriptions, chapters, or metadata).
   *
   * @param {string} [options.label].
   *        Label to identify the text track
   *
   * @param {string} [options.language]
   *        Two letter language abbreviation.
   *
   * @param {boolean} [options.default]
   *        Default this track to on.
   *
   * @param {string} [options.id]
   *        The internal id to assign this track.
   *
   * @param {string} [options.src]
   *        A source url for the track.
   *
   * @return {HTMLTrackElement}
   *         The track element that gets created.
   */Html5.prototype.createRemoteTextTrack=function createRemoteTextTrack(options){if(!this.featuresNativeTextTracks){return _Tech.prototype.createRemoteTextTrack.call(this,options);}var htmlTrackElement=document$1.createElement('track');if(options.kind){htmlTrackElement.kind=options.kind;}if(options.label){htmlTrackElement.label=options.label;}if(options.language||options.srclang){htmlTrackElement.srclang=options.language||options.srclang;}if(options['default']){htmlTrackElement['default']=options['default'];}if(options.id){htmlTrackElement.id=options.id;}if(options.src){htmlTrackElement.src=options.src;}return htmlTrackElement;};/**
   * Creates a remote text track object and returns an html track element.
   *
   * @param {Object} options The object should contain values for
   * kind, language, label, and src (location of the WebVTT file)
   * @param {Boolean} [manualCleanup=true] if set to false, the TextTrack will be
   * automatically removed from the video element whenever the source changes
   * @return {HTMLTrackElement} An Html Track Element.
   * This can be an emulated {@link HTMLTrackElement} or a native one.
   * @deprecated The default value of the "manualCleanup" parameter will default
   * to "false" in upcoming versions of Video.js
   */Html5.prototype.addRemoteTextTrack=function addRemoteTextTrack(options,manualCleanup){var htmlTrackElement=_Tech.prototype.addRemoteTextTrack.call(this,options,manualCleanup);if(this.featuresNativeTextTracks){this.el().appendChild(htmlTrackElement);}return htmlTrackElement;};/**
   * Remove remote `TextTrack` from `TextTrackList` object
   *
   * @param {TextTrack} track
   *        `TextTrack` object to remove
   */Html5.prototype.removeRemoteTextTrack=function removeRemoteTextTrack(track){_Tech.prototype.removeRemoteTextTrack.call(this,track);if(this.featuresNativeTextTracks){var tracks=this.$$('track');var i=tracks.length;while(i--){if(track===tracks[i]||track===tracks[i].track){this.el().removeChild(tracks[i]);}}}};/**
   * Gets available media playback quality metrics as specified by the W3C's Media
   * Playback Quality API.
   *
   * @see [Spec]{@link https://wicg.github.io/media-playback-quality}
   *
   * @return {Object}
   *         An object with supported media playback quality metrics
   */Html5.prototype.getVideoPlaybackQuality=function getVideoPlaybackQuality(){if(typeof this.el().getVideoPlaybackQuality==='function'){return this.el().getVideoPlaybackQuality();}var videoPlaybackQuality={};if(typeof this.el().webkitDroppedFrameCount!=='undefined'&&typeof this.el().webkitDecodedFrameCount!=='undefined'){videoPlaybackQuality.droppedVideoFrames=this.el().webkitDroppedFrameCount;videoPlaybackQuality.totalVideoFrames=this.el().webkitDecodedFrameCount;}if(window$1.performance&&typeof window$1.performance.now==='function'){videoPlaybackQuality.creationTime=window$1.performance.now();}else if(window$1.performance&&window$1.performance.timing&&typeof window$1.performance.timing.navigationStart==='number'){videoPlaybackQuality.creationTime=window$1.Date.now()-window$1.performance.timing.navigationStart;}return videoPlaybackQuality;};return Html5;}(Tech);/* HTML5 Support Testing ---------------------------------------------------- */if(isReal()){/**
   * Element for testing browser HTML5 media capabilities
   *
   * @type {Element}
   * @constant
   * @private
   */Html5.TEST_VID=document$1.createElement('video');var track=document$1.createElement('track');track.kind='captions';track.srclang='en';track.label='English';Html5.TEST_VID.appendChild(track);}/**
 * Check if HTML5 media is supported by this browser/device.
 *
 * @return {boolean}
 *         - True if HTML5 media is supported.
 *         - False if HTML5 media is not supported.
 */Html5.isSupported=function(){// IE9 with no Media Player is a LIAR! (#984)
try{Html5.TEST_VID.volume=0.5;}catch(e){return false;}return !!(Html5.TEST_VID&&Html5.TEST_VID.canPlayType);};/**
 * Check if the tech can support the given type
 *
 * @param {string} type
 *        The mimetype to check
 * @return {string} 'probably', 'maybe', or '' (empty string)
 */Html5.canPlayType=function(type){return Html5.TEST_VID.canPlayType(type);};/**
 * Check if the tech can support the given source
 * @param {Object} srcObj
 *        The source object
 * @param {Object} options
 *        The options passed to the tech
 * @return {string} 'probably', 'maybe', or '' (empty string)
 */Html5.canPlaySource=function(srcObj,options){return Html5.canPlayType(srcObj.type);};/**
 * Check if the volume can be changed in this browser/device.
 * Volume cannot be changed in a lot of mobile devices.
 * Specifically, it can't be changed from 1 on iOS.
 *
 * @return {boolean}
 *         - True if volume can be controlled
 *         - False otherwise
 */Html5.canControlVolume=function(){// IE will error if Windows Media Player not installed #3315
try{var volume=Html5.TEST_VID.volume;Html5.TEST_VID.volume=volume/2+0.1;return volume!==Html5.TEST_VID.volume;}catch(e){return false;}};/**
 * Check if the volume can be muted in this browser/device.
 * Some devices, e.g. iOS, don't allow changing volume
 * but permits muting/unmuting.
 *
 * @return {bolean}
 *      - True if volume can be muted
 *      - False otherwise
 */Html5.canMuteVolume=function(){try{var muted=Html5.TEST_VID.muted;// in some versions of iOS muted property doesn't always
// work, so we want to set both property and attribute
Html5.TEST_VID.muted=!muted;if(Html5.TEST_VID.muted){setAttribute(Html5.TEST_VID,'muted','muted');}else {removeAttribute(Html5.TEST_VID,'muted','muted');}return muted!==Html5.TEST_VID.muted;}catch(e){return false;}};/**
 * Check if the playback rate can be changed in this browser/device.
 *
 * @return {boolean}
 *         - True if playback rate can be controlled
 *         - False otherwise
 */Html5.canControlPlaybackRate=function(){// Playback rate API is implemented in Android Chrome, but doesn't do anything
// https://github.com/videojs/video.js/issues/3180
if(IS_ANDROID&&IS_CHROME&&CHROME_VERSION<58){return false;}// IE will error if Windows Media Player not installed #3315
try{var playbackRate=Html5.TEST_VID.playbackRate;Html5.TEST_VID.playbackRate=playbackRate/2+0.1;return playbackRate!==Html5.TEST_VID.playbackRate;}catch(e){return false;}};/**
 * Check if we can override a video/audio elements attributes, with
 * Object.defineProperty.
 *
 * @return {boolean}
 *         - True if builtin attributes can be overriden
 *         - False otherwise
 */Html5.canOverrideAttributes=function(){if(IS_IE8){return false;}// if we cannot overwrite the src/innerHTML property, there is no support
// iOS 7 safari for instance cannot do this.
try{var noop=function noop(){};Object.defineProperty(document$1.createElement('video'),'src',{get:noop,set:noop});Object.defineProperty(document$1.createElement('audio'),'src',{get:noop,set:noop});Object.defineProperty(document$1.createElement('video'),'innerHTML',{get:noop,set:noop});Object.defineProperty(document$1.createElement('audio'),'innerHTML',{get:noop,set:noop});}catch(e){return false;}return true;};/**
 * Check to see if native `TextTrack`s are supported by this browser/device.
 *
 * @return {boolean}
 *         - True if native `TextTrack`s are supported.
 *         - False otherwise
 */Html5.supportsNativeTextTracks=function(){return IS_ANY_SAFARI||IS_IOS&&IS_CHROME;};/**
 * Check to see if native `VideoTrack`s are supported by this browser/device
 *
 * @return {boolean}
 *        - True if native `VideoTrack`s are supported.
 *        - False otherwise
 */Html5.supportsNativeVideoTracks=function(){return !!(Html5.TEST_VID&&Html5.TEST_VID.videoTracks);};/**
 * Check to see if native `AudioTrack`s are supported by this browser/device
 *
 * @return {boolean}
 *        - True if native `AudioTrack`s are supported.
 *        - False otherwise
 */Html5.supportsNativeAudioTracks=function(){return !!(Html5.TEST_VID&&Html5.TEST_VID.audioTracks);};/**
 * An array of events available on the Html5 tech.
 *
 * @private
 * @type {Array}
 */Html5.Events=['loadstart','suspend','abort','error','emptied','stalled','loadedmetadata','loadeddata','canplay','canplaythrough','playing','waiting','seeking','seeked','ended','durationchange','timeupdate','progress','play','pause','ratechange','resize','volumechange'];/**
 * Boolean indicating whether the `Tech` supports volume control.
 *
 * @type {boolean}
 * @default {@link Html5.canControlVolume}
 */Html5.prototype.featuresVolumeControl=Html5.canControlVolume();/**
 * Boolean indicating whether the `Tech` supports muting volume.
 *
 * @type {bolean}
 * @default {@link Html5.canMuteVolume}
 */Html5.prototype.featuresMuteControl=Html5.canMuteVolume();/**
 * Boolean indicating whether the `Tech` supports changing the speed at which the media
 * plays. Examples:
 *   - Set player to play 2x (twice) as fast
 *   - Set player to play 0.5x (half) as fast
 *
 * @type {boolean}
 * @default {@link Html5.canControlPlaybackRate}
 */Html5.prototype.featuresPlaybackRate=Html5.canControlPlaybackRate();/**
 * Boolean indicating wether the `Tech` supports the `sourceset` event.
 *
 * @type {boolean}
 * @default
 */Html5.prototype.featuresSourceset=Html5.canOverrideAttributes();/**
 * Boolean indicating whether the `HTML5` tech currently supports the media element
 * moving in the DOM. iOS breaks if you move the media element, so this is set this to
 * false there. Everywhere else this should be true.
 *
 * @type {boolean}
 * @default
 */Html5.prototype.movingMediaElementInDOM=!IS_IOS;// TODO: Previous comment: No longer appears to be used. Can probably be removed.
//       Is this true?
/**
 * Boolean indicating whether the `HTML5` tech currently supports automatic media resize
 * when going into fullscreen.
 *
 * @type {boolean}
 * @default
 */Html5.prototype.featuresFullscreenResize=true;/**
 * Boolean indicating whether the `HTML5` tech currently supports the progress event.
 * If this is false, manual `progress` events will be triggred instead.
 *
 * @type {boolean}
 * @default
 */Html5.prototype.featuresProgressEvents=true;/**
 * Boolean indicating whether the `HTML5` tech currently supports the timeupdate event.
 * If this is false, manual `timeupdate` events will be triggred instead.
 *
 * @default
 */Html5.prototype.featuresTimeupdateEvents=true;/**
 * Boolean indicating whether the `HTML5` tech currently supports native `TextTrack`s.
 *
 * @type {boolean}
 * @default {@link Html5.supportsNativeTextTracks}
 */Html5.prototype.featuresNativeTextTracks=Html5.supportsNativeTextTracks();/**
 * Boolean indicating whether the `HTML5` tech currently supports native `VideoTrack`s.
 *
 * @type {boolean}
 * @default {@link Html5.supportsNativeVideoTracks}
 */Html5.prototype.featuresNativeVideoTracks=Html5.supportsNativeVideoTracks();/**
 * Boolean indicating whether the `HTML5` tech currently supports native `AudioTrack`s.
 *
 * @type {boolean}
 * @default {@link Html5.supportsNativeAudioTracks}
 */Html5.prototype.featuresNativeAudioTracks=Html5.supportsNativeAudioTracks();// HTML5 Feature detection and Device Fixes --------------------------------- //
var canPlayType=Html5.TEST_VID&&Html5.TEST_VID.constructor.prototype.canPlayType;var mpegurlRE=/^application\/(?:x-|vnd\.apple\.)mpegurl/i;var mp4RE=/^video\/mp4/i;Html5.patchCanPlayType=function(){// Android 4.0 and above can play HLS to some extent but it reports being unable to do so
// Firefox and Chrome report correctly
if(ANDROID_VERSION>=4.0&&!IS_FIREFOX&&!IS_CHROME){Html5.TEST_VID.constructor.prototype.canPlayType=function(type){if(type&&mpegurlRE.test(type)){return 'maybe';}return canPlayType.call(this,type);};// Override Android 2.2 and less canPlayType method which is broken
}else if(IS_OLD_ANDROID){Html5.TEST_VID.constructor.prototype.canPlayType=function(type){if(type&&mp4RE.test(type)){return 'maybe';}return canPlayType.call(this,type);};}};Html5.unpatchCanPlayType=function(){var r=Html5.TEST_VID.constructor.prototype.canPlayType;Html5.TEST_VID.constructor.prototype.canPlayType=canPlayType;return r;};// by default, patch the media element
Html5.patchCanPlayType();Html5.disposeMediaElement=function(el){if(!el){return;}if(el.parentNode){el.parentNode.removeChild(el);}// remove any child track or source nodes to prevent their loading
while(el.hasChildNodes()){el.removeChild(el.firstChild);}// remove any src reference. not setting `src=''` because that causes a warning
// in firefox
el.removeAttribute('src');// force the media element to update its loading state by calling load()
// however IE on Windows 7N has a bug that throws an error so need a try/catch (#793)
if(typeof el.load==='function'){// wrapping in an iife so it's not deoptimized (#1060#discussion_r10324473)
(function(){try{el.load();}catch(e){// not supported
}})();}};Html5.resetMediaElement=function(el){if(!el){return;}var sources=el.querySelectorAll('source');var i=sources.length;while(i--){el.removeChild(sources[i]);}// remove any src reference.
// not setting `src=''` because that throws an error
el.removeAttribute('src');if(typeof el.load==='function'){// wrapping in an iife so it's not deoptimized (#1060#discussion_r10324473)
(function(){try{el.load();}catch(e){// satisfy linter
}})();}};/* Native HTML5 element property wrapping ----------------------------------- */ // Wrap native boolean attributes with getters that check both property and attribute
// The list is as followed:
// muted, defaultMuted, autoplay, controls, loop, playsinline
[/**
 * Get the value of `muted` from the media element. `muted` indicates
 * that the volume for the media should be set to silent. This does not actually change
 * the `volume` attribute.
 *
 * @method Html5#muted
 * @return {boolean}
 *         - True if the value of `volume` should be ignored and the audio set to silent.
 *         - False if the value of `volume` should be used.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-muted}
 */'muted',/**
 * Get the value of `defaultMuted` from the media element. `defaultMuted` indicates
 * whether the media should start muted or not. Only changes the default state of the
 * media. `muted` and `defaultMuted` can have different values. {@link Html5#muted} indicates the
 * current state.
 *
 * @method Html5#defaultMuted
 * @return {boolean}
 *         - The value of `defaultMuted` from the media element.
 *         - True indicates that the media should start muted.
 *         - False indicates that the media should not start muted
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-defaultmuted}
 */'defaultMuted',/**
 * Get the value of `autoplay` from the media element. `autoplay` indicates
 * that the media should start to play as soon as the page is ready.
 *
 * @method Html5#autoplay
 * @return {boolean}
 *         - The value of `autoplay` from the media element.
 *         - True indicates that the media should start as soon as the page loads.
 *         - False indicates that the media should not start as soon as the page loads.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-autoplay}
 */'autoplay',/**
 * Get the value of `controls` from the media element. `controls` indicates
 * whether the native media controls should be shown or hidden.
 *
 * @method Html5#controls
 * @return {boolean}
 *         - The value of `controls` from the media element.
 *         - True indicates that native controls should be showing.
 *         - False indicates that native controls should be hidden.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-controls}
 */'controls',/**
 * Get the value of `loop` from the media element. `loop` indicates
 * that the media should return to the start of the media and continue playing once
 * it reaches the end.
 *
 * @method Html5#loop
 * @return {boolean}
 *         - The value of `loop` from the media element.
 *         - True indicates that playback should seek back to start once
 *           the end of a media is reached.
 *         - False indicates that playback should not loop back to the start when the
 *           end of the media is reached.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-loop}
 */'loop',/**
 * Get the value of `playsinline` from the media element. `playsinline` indicates
 * to the browser that non-fullscreen playback is preferred when fullscreen
 * playback is the native default, such as in iOS Safari.
 *
 * @method Html5#playsinline
 * @return {boolean}
 *         - The value of `playsinline` from the media element.
 *         - True indicates that the media should play inline.
 *         - False indicates that the media should not play inline.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/#attr-video-playsinline}
 */'playsinline'].forEach(function(prop){Html5.prototype[prop]=function(){return this.el_[prop]||this.el_.hasAttribute(prop);};});// Wrap native boolean attributes with setters that set both property and attribute
// The list is as followed:
// setMuted, setDefaultMuted, setAutoplay, setLoop, setPlaysinline
// setControls is special-cased above
[/**
 * Set the value of `muted` on the media element. `muted` indicates that the current
 * audio level should be silent.
 *
 * @method Html5#setMuted
 * @param {boolean} muted
 *        - True if the audio should be set to silent
 *        - False otherwise
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-muted}
 */'muted',/**
 * Set the value of `defaultMuted` on the media element. `defaultMuted` indicates that the current
 * audio level should be silent, but will only effect the muted level on intial playback..
 *
 * @method Html5.prototype.setDefaultMuted
 * @param {boolean} defaultMuted
 *        - True if the audio should be set to silent
 *        - False otherwise
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-defaultmuted}
 */'defaultMuted',/**
 * Set the value of `autoplay` on the media element. `autoplay` indicates
 * that the media should start to play as soon as the page is ready.
 *
 * @method Html5#setAutoplay
 * @param {boolean} autoplay
 *         - True indicates that the media should start as soon as the page loads.
 *         - False indicates that the media should not start as soon as the page loads.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-autoplay}
 */'autoplay',/**
 * Set the value of `loop` on the media element. `loop` indicates
 * that the media should return to the start of the media and continue playing once
 * it reaches the end.
 *
 * @method Html5#setLoop
 * @param {boolean} loop
 *         - True indicates that playback should seek back to start once
 *           the end of a media is reached.
 *         - False indicates that playback should not loop back to the start when the
 *           end of the media is reached.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-loop}
 */'loop',/**
 * Set the value of `playsinline` from the media element. `playsinline` indicates
 * to the browser that non-fullscreen playback is preferred when fullscreen
 * playback is the native default, such as in iOS Safari.
 *
 * @method Html5#setPlaysinline
 * @param {boolean} playsinline
 *         - True indicates that the media should play inline.
 *         - False indicates that the media should not play inline.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/#attr-video-playsinline}
 */'playsinline'].forEach(function(prop){Html5.prototype['set'+toTitleCase(prop)]=function(v){this.el_[prop]=v;if(v){this.el_.setAttribute(prop,prop);}else {this.el_.removeAttribute(prop);}};});// Wrap native properties with a getter
// The list is as followed
// paused, currentTime, buffered, volume, poster, preload, error, seeking
// seekable, ended, playbackRate, defaultPlaybackRate, played, networkState
// readyState, videoWidth, videoHeight
[/**
 * Get the value of `paused` from the media element. `paused` indicates whether the media element
 * is currently paused or not.
 *
 * @method Html5#paused
 * @return {boolean}
 *         The value of `paused` from the media element.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-paused}
 */'paused',/**
 * Get the value of `currentTime` from the media element. `currentTime` indicates
 * the current second that the media is at in playback.
 *
 * @method Html5#currentTime
 * @return {number}
 *         The value of `currentTime` from the media element.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-currenttime}
 */'currentTime',/**
 * Get the value of `buffered` from the media element. `buffered` is a `TimeRange`
 * object that represents the parts of the media that are already downloaded and
 * available for playback.
 *
 * @method Html5#buffered
 * @return {TimeRange}
 *         The value of `buffered` from the media element.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-buffered}
 */'buffered',/**
 * Get the value of `volume` from the media element. `volume` indicates
 * the current playback volume of audio for a media. `volume` will be a value from 0
 * (silent) to 1 (loudest and default).
 *
 * @method Html5#volume
 * @return {number}
 *         The value of `volume` from the media element. Value will be between 0-1.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-a-volume}
 */'volume',/**
 * Get the value of `poster` from the media element. `poster` indicates
 * that the url of an image file that can/will be shown when no media data is available.
 *
 * @method Html5#poster
 * @return {string}
 *         The value of `poster` from the media element. Value will be a url to an
 *         image.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-video-poster}
 */'poster',/**
 * Get the value of `preload` from the media element. `preload` indicates
 * what should download before the media is interacted with. It can have the following
 * values:
 * - none: nothing should be downloaded
 * - metadata: poster and the first few frames of the media may be downloaded to get
 *   media dimensions and other metadata
 * - auto: allow the media and metadata for the media to be downloaded before
 *    interaction
 *
 * @method Html5#preload
 * @return {string}
 *         The value of `preload` from the media element. Will be 'none', 'metadata',
 *         or 'auto'.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-preload}
 */'preload',/**
 * Get the value of the `error` from the media element. `error` indicates any
 * MediaError that may have occured during playback. If error returns null there is no
 * current error.
 *
 * @method Html5#error
 * @return {MediaError|null}
 *         The value of `error` from the media element. Will be `MediaError` if there
 *         is a current error and null otherwise.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-error}
 */'error',/**
 * Get the value of `seeking` from the media element. `seeking` indicates whether the
 * media is currently seeking to a new position or not.
 *
 * @method Html5#seeking
 * @return {boolean}
 *         - The value of `seeking` from the media element.
 *         - True indicates that the media is currently seeking to a new position.
 *         - Flase indicates that the media is not seeking to a new position at this time.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-seeking}
 */'seeking',/**
 * Get the value of `seekable` from the media element. `seekable` returns a
 * `TimeRange` object indicating ranges of time that can currently be `seeked` to.
 *
 * @method Html5#seekable
 * @return {TimeRange}
 *         The value of `seekable` from the media element. A `TimeRange` object
 *         indicating the current ranges of time that can be seeked to.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-seekable}
 */'seekable',/**
 * Get the value of `ended` from the media element. `ended` indicates whether
 * the media has reached the end or not.
 *
 * @method Html5#ended
 * @return {boolean}
 *         - The value of `ended` from the media element.
 *         - True indicates that the media has ended.
 *         - False indicates that the media has not ended.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-ended}
 */'ended',/**
 * Get the value of `playbackRate` from the media element. `playbackRate` indicates
 * the rate at which the media is currently playing back. Examples:
 *   - if playbackRate is set to 2, media will play twice as fast.
 *   - if playbackRate is set to 0.5, media will play half as fast.
 *
 * @method Html5#playbackRate
 * @return {number}
 *         The value of `playbackRate` from the media element. A number indicating
 *         the current playback speed of the media, where 1 is normal speed.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-playbackrate}
 */'playbackRate',/**
 * Get the value of `defaultPlaybackRate` from the media element. `defaultPlaybackRate` indicates
 * the rate at which the media is currently playing back. This value will not indicate the current
 * `playbackRate` after playback has started, use {@link Html5#playbackRate} for that.
 *
 * Examples:
 *   - if defaultPlaybackRate is set to 2, media will play twice as fast.
 *   - if defaultPlaybackRate is set to 0.5, media will play half as fast.
 *
 * @method Html5.prototype.defaultPlaybackRate
 * @return {number}
 *         The value of `defaultPlaybackRate` from the media element. A number indicating
 *         the current playback speed of the media, where 1 is normal speed.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-playbackrate}
 */'defaultPlaybackRate',/**
 * Get the value of `played` from the media element. `played` returns a `TimeRange`
 * object representing points in the media timeline that have been played.
 *
 * @method Html5#played
 * @return {TimeRange}
 *         The value of `played` from the media element. A `TimeRange` object indicating
 *         the ranges of time that have been played.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-played}
 */'played',/**
 * Get the value of `networkState` from the media element. `networkState` indicates
 * the current network state. It returns an enumeration from the following list:
 * - 0: NETWORK_EMPTY
 * - 1: NEWORK_IDLE
 * - 2: NETWORK_LOADING
 * - 3: NETWORK_NO_SOURCE
 *
 * @method Html5#networkState
 * @return {number}
 *         The value of `networkState` from the media element. This will be a number
 *         from the list in the description.
 *
 * @see [Spec] {@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-networkstate}
 */'networkState',/**
 * Get the value of `readyState` from the media element. `readyState` indicates
 * the current state of the media element. It returns an enumeration from the
 * following list:
 * - 0: HAVE_NOTHING
 * - 1: HAVE_METADATA
 * - 2: HAVE_CURRENT_DATA
 * - 3: HAVE_FUTURE_DATA
 * - 4: HAVE_ENOUGH_DATA
 *
 * @method Html5#readyState
 * @return {number}
 *         The value of `readyState` from the media element. This will be a number
 *         from the list in the description.
 *
 * @see [Spec] {@link https://www.w3.org/TR/html5/embedded-content-0.html#ready-states}
 */'readyState',/**
 * Get the value of `videoWidth` from the video element. `videoWidth` indicates
 * the current width of the video in css pixels.
 *
 * @method Html5#videoWidth
 * @return {number}
 *         The value of `videoWidth` from the video element. This will be a number
 *         in css pixels.
 *
 * @see [Spec] {@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-video-videowidth}
 */'videoWidth',/**
 * Get the value of `videoHeight` from the video element. `videoHeigth` indicates
 * the current height of the video in css pixels.
 *
 * @method Html5#videoHeight
 * @return {number}
 *         The value of `videoHeight` from the video element. This will be a number
 *         in css pixels.
 *
 * @see [Spec] {@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-video-videowidth}
 */'videoHeight'].forEach(function(prop){Html5.prototype[prop]=function(){return this.el_[prop];};});// Wrap native properties with a setter in this format:
// set + toTitleCase(name)
// The list is as follows:
// setVolume, setSrc, setPoster, setPreload, setPlaybackRate, setDefaultPlaybackRate
[/**
 * Set the value of `volume` on the media element. `volume` indicates the current
 * audio level as a percentage in decimal form. This means that 1 is 100%, 0.5 is 50%, and
 * so on.
 *
 * @method Html5#setVolume
 * @param {number} percentAsDecimal
 *        The volume percent as a decimal. Valid range is from 0-1.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-a-volume}
 */'volume',/**
 * Set the value of `src` on the media element. `src` indicates the current
 * {@link Tech~SourceObject} for the media.
 *
 * @method Html5#setSrc
 * @param {Tech~SourceObject} src
 *        The source object to set as the current source.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-src}
 */'src',/**
 * Set the value of `poster` on the media element. `poster` is the url to
 * an image file that can/will be shown when no media data is available.
 *
 * @method Html5#setPoster
 * @param {string} poster
 *        The url to an image that should be used as the `poster` for the media
 *        element.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-poster}
 */'poster',/**
 * Set the value of `preload` on the media element. `preload` indicates
 * what should download before the media is interacted with. It can have the following
 * values:
 * - none: nothing should be downloaded
 * - metadata: poster and the first few frames of the media may be downloaded to get
 *   media dimensions and other metadata
 * - auto: allow the media and metadata for the media to be downloaded before
 *    interaction
 *
 * @method Html5#setPreload
 * @param {string} preload
 *         The value of `preload` to set on the media element. Must be 'none', 'metadata',
 *         or 'auto'.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#attr-media-preload}
 */'preload',/**
 * Set the value of `playbackRate` on the media element. `playbackRate` indicates
 * the rate at which the media should play back. Examples:
 *   - if playbackRate is set to 2, media will play twice as fast.
 *   - if playbackRate is set to 0.5, media will play half as fast.
 *
 * @method Html5#setPlaybackRate
 * @return {number}
 *         The value of `playbackRate` from the media element. A number indicating
 *         the current playback speed of the media, where 1 is normal speed.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-playbackrate}
 */'playbackRate',/**
 * Set the value of `defaultPlaybackRate` on the media element. `defaultPlaybackRate` indicates
 * the rate at which the media should play back upon initial startup. Changing this value
 * after a video has started will do nothing. Instead you should used {@link Html5#setPlaybackRate}.
 *
 * Example Values:
 *   - if playbackRate is set to 2, media will play twice as fast.
 *   - if playbackRate is set to 0.5, media will play half as fast.
 *
 * @method Html5.prototype.setDefaultPlaybackRate
 * @return {number}
 *         The value of `defaultPlaybackRate` from the media element. A number indicating
 *         the current playback speed of the media, where 1 is normal speed.
 *
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-defaultplaybackrate}
 */'defaultPlaybackRate'].forEach(function(prop){Html5.prototype['set'+toTitleCase(prop)]=function(v){this.el_[prop]=v;};});// wrap native functions with a function
// The list is as follows:
// pause, load play
[/**
 * A wrapper around the media elements `pause` function. This will call the `HTML5`
 * media elements `pause` function.
 *
 * @method Html5#pause
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-pause}
 */'pause',/**
 * A wrapper around the media elements `load` function. This will call the `HTML5`s
 * media element `load` function.
 *
 * @method Html5#load
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-load}
 */'load',/**
 * A wrapper around the media elements `play` function. This will call the `HTML5`s
 * media element `play` function.
 *
 * @method Html5#play
 * @see [Spec]{@link https://www.w3.org/TR/html5/embedded-content-0.html#dom-media-play}
 */'play'].forEach(function(prop){Html5.prototype[prop]=function(){return this.el_[prop]();};});Tech.withSourceHandlers(Html5);/**
 * Native source handler for Html5, simply passes the source to the media element.
 *
 * @proprety {Tech~SourceObject} source
 *        The source object
 *
 * @proprety {Html5} tech
 *        The instance of the HTML5 tech.
 */Html5.nativeSourceHandler={};/**
 * Check if the media element can play the given mime type.
 *
 * @param {string} type
 *        The mimetype to check
 *
 * @return {string}
 *         'probably', 'maybe', or '' (empty string)
 */Html5.nativeSourceHandler.canPlayType=function(type){// IE9 on Windows 7 without MediaPlayer throws an error here
// https://github.com/videojs/video.js/issues/519
try{return Html5.TEST_VID.canPlayType(type);}catch(e){return '';}};/**
 * Check if the media element can handle a source natively.
 *
 * @param {Tech~SourceObject} source
 *         The source object
 *
 * @param {Object} [options]
 *         Options to be passed to the tech.
 *
 * @return {string}
 *         'probably', 'maybe', or '' (empty string).
 */Html5.nativeSourceHandler.canHandleSource=function(source,options){// If a type was provided we should rely on that
if(source.type){return Html5.nativeSourceHandler.canPlayType(source.type);// If no type, fall back to checking 'video/[EXTENSION]'
}else if(source.src){var ext=getFileExtension(source.src);return Html5.nativeSourceHandler.canPlayType('video/'+ext);}return '';};/**
 * Pass the source to the native media element.
 *
 * @param {Tech~SourceObject} source
 *        The source object
 *
 * @param {Html5} tech
 *        The instance of the Html5 tech
 *
 * @param {Object} [options]
 *        The options to pass to the source
 */Html5.nativeSourceHandler.handleSource=function(source,tech,options){tech.setSrc(source.src);};/**
 * A noop for the native dispose function, as cleanup is not needed.
 */Html5.nativeSourceHandler.dispose=function(){};// Register the native source handler
Html5.registerSourceHandler(Html5.nativeSourceHandler);Tech.registerTech('Html5',Html5);var _templateObject$1=taggedTemplateLiteralLoose(['\n        Using the tech directly can be dangerous. I hope you know what you\'re doing.\n        See https://github.com/videojs/video.js/issues/2617 for more info.\n      '],['\n        Using the tech directly can be dangerous. I hope you know what you\'re doing.\n        See https://github.com/videojs/video.js/issues/2617 for more info.\n      ']);/**
 * @file player.js
 */ // Subclasses Component
// The following imports are used only to ensure that the corresponding modules
// are always included in the video.js package. Importing the modules will
// execute them and they will register themselves with video.js.
// Import Html5 tech, at least for disposing the original video tag.
// The following tech events are simply re-triggered
// on the player when they happen
var TECH_EVENTS_RETRIGGER=[/**
 * Fired while the user agent is downloading media data.
 *
 * @event Player#progress
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `progress` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechProgress_
 * @fires Player#progress
 * @listens Tech#progress
 */'progress',/**
 * Fires when the loading of an audio/video is aborted.
 *
 * @event Player#abort
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `abort` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechAbort_
 * @fires Player#abort
 * @listens Tech#abort
 */'abort',/**
 * Fires when the browser is intentionally not getting media data.
 *
 * @event Player#suspend
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `suspend` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechSuspend_
 * @fires Player#suspend
 * @listens Tech#suspend
 */'suspend',/**
 * Fires when the current playlist is empty.
 *
 * @event Player#emptied
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `emptied` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechEmptied_
 * @fires Player#emptied
 * @listens Tech#emptied
 */'emptied',/**
 * Fires when the browser is trying to get media data, but data is not available.
 *
 * @event Player#stalled
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `stalled` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechStalled_
 * @fires Player#stalled
 * @listens Tech#stalled
 */'stalled',/**
 * Fires when the browser has loaded meta data for the audio/video.
 *
 * @event Player#loadedmetadata
 * @type {EventTarget~Event}
 */ /**
 * Retrigger the `stalled` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechLoadedmetadata_
 * @fires Player#loadedmetadata
 * @listens Tech#loadedmetadata
 */'loadedmetadata',/**
 * Fires when the browser has loaded the current frame of the audio/video.
 *
 * @event Player#loadeddata
 * @type {event}
 */ /**
 * Retrigger the `loadeddata` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechLoaddeddata_
 * @fires Player#loadeddata
 * @listens Tech#loadeddata
 */'loadeddata',/**
 * Fires when the current playback position has changed.
 *
 * @event Player#timeupdate
 * @type {event}
 */ /**
 * Retrigger the `timeupdate` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechTimeUpdate_
 * @fires Player#timeupdate
 * @listens Tech#timeupdate
 */'timeupdate',/**
 * Fires when the video's intrinsic dimensions change
 *
 * @event Player#resize
 * @type {event}
 */ /**
 * Retrigger the `resize` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechResize_
 * @fires Player#resize
 * @listens Tech#resize
 */'resize',/**
 * Fires when the volume has been changed
 *
 * @event Player#volumechange
 * @type {event}
 */ /**
 * Retrigger the `volumechange` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechVolumechange_
 * @fires Player#volumechange
 * @listens Tech#volumechange
 */'volumechange',/**
 * Fires when the text track has been changed
 *
 * @event Player#texttrackchange
 * @type {event}
 */ /**
 * Retrigger the `texttrackchange` event that was triggered by the {@link Tech}.
 *
 * @private
 * @method Player#handleTechTexttrackchange_
 * @fires Player#texttrackchange
 * @listens Tech#texttrackchange
 */'texttrackchange'];// events to queue when playback rate is zero
// this is a hash for the sole purpose of mapping non-camel-cased event names
// to camel-cased function names
var TECH_EVENTS_QUEUE={canplay:'CanPlay',canplaythrough:'CanPlayThrough',playing:'Playing',seeked:'Seeked'};var BREAKPOINT_ORDER=['tiny','xsmall','small','medium','large','xlarge','huge'];var BREAKPOINT_CLASSES={};// grep: vjs-layout-tiny
// grep: vjs-layout-x-small
// grep: vjs-layout-small
// grep: vjs-layout-medium
// grep: vjs-layout-large
// grep: vjs-layout-x-large
// grep: vjs-layout-huge
BREAKPOINT_ORDER.forEach(function(k){var v=k.charAt(0)==='x'?'x-'+k.substring(1):k;BREAKPOINT_CLASSES[k]='vjs-layout-'+v;});var DEFAULT_BREAKPOINTS={tiny:210,xsmall:320,small:425,medium:768,large:1440,xlarge:2560,huge:Infinity};/**
 * An instance of the `Player` class is created when any of the Video.js setup methods
 * are used to initialize a video.
 *
 * After an instance has been created it can be accessed globally in two ways:
 * 1. By calling `videojs('example_video_1');`
 * 2. By using it directly via  `videojs.players.example_video_1;`
 *
 * @extends Component
 */var Player=function(_Component){inherits(Player,_Component);/**
   * Create an instance of this class.
   *
   * @param {Element} tag
   *        The original video DOM element used for configuring options.
   *
   * @param {Object} [options]
   *        Object of option names and values.
   *
   * @param {Component~ReadyCallback} [ready]
   *        Ready callback function.
   */function Player(tag,options,ready){classCallCheck(this,Player);// Make sure tag ID exists
tag.id=tag.id||options.id||'vjs_video_'+newGUID();// Set Options
// The options argument overrides options set in the video tag
// which overrides globally set options.
// This latter part coincides with the load order
// (tag must exist before Player)
options=assign$1(Player.getTagSettings(tag),options);// Delay the initialization of children because we need to set up
// player properties first, and can't use `this` before `super()`
options.initChildren=false;// Same with creating the element
options.createEl=false;// don't auto mixin the evented mixin
options.evented=false;// we don't want the player to report touch activity on itself
// see enableTouchActivity in Component
options.reportTouchActivity=false;// If language is not set, get the closest lang attribute
if(!options.language){if(typeof tag.closest==='function'){var closest=tag.closest('[lang]');if(closest&&closest.getAttribute){options.language=closest.getAttribute('lang');}}else {var element=tag;while(element&&element.nodeType===1){if(getAttributes(element).hasOwnProperty('lang')){options.language=element.getAttribute('lang');break;}element=element.parentNode;}}}// Run base component initializing with new options
// create logger
var _this=possibleConstructorReturn(this,_Component.call(this,null,options,ready));_this.log=createLogger(_this.id_);// Tracks when a tech changes the poster
_this.isPosterFromTech_=false;// Holds callback info that gets queued when playback rate is zero
// and a seek is happening
_this.queuedCallbacks_=[];// Turn off API access because we're loading a new tech that might load asynchronously
_this.isReady_=false;// Init state hasStarted_
_this.hasStarted_=false;// Init state userActive_
_this.userActive_=false;// if the global option object was accidentally blown away by
// someone, bail early with an informative error
if(!_this.options_||!_this.options_.techOrder||!_this.options_.techOrder.length){throw new Error('No techOrder specified. Did you overwrite '+'videojs.options instead of just changing the '+'properties you want to override?');}// Store the original tag used to set options
_this.tag=tag;// Store the tag attributes used to restore html5 element
_this.tagAttributes=tag&&getAttributes(tag);// Update current language
_this.language(_this.options_.language);// Update Supported Languages
if(options.languages){// Normalise player option languages to lowercase
var languagesToLower={};Object.getOwnPropertyNames(options.languages).forEach(function(name$$1){languagesToLower[name$$1.toLowerCase()]=options.languages[name$$1];});_this.languages_=languagesToLower;}else {_this.languages_=Player.prototype.options_.languages;}// Cache for video property values.
_this.cache_={};// Set poster
_this.poster_=options.poster||'';// Set controls
_this.controls_=!!options.controls;// Set default values for lastVolume
_this.cache_.lastVolume=1;// Original tag settings stored in options
// now remove immediately so native controls don't flash.
// May be turned back on by HTML5 tech if nativeControlsForTouch is true
tag.controls=false;tag.removeAttribute('controls');// the attribute overrides the option
if(tag.hasAttribute('autoplay')){_this.options_.autoplay=true;}else {// otherwise use the setter to validate and
// set the correct value.
_this.autoplay(_this.options_.autoplay);}/*
     * Store the internal state of scrubbing
     *
     * @private
     * @return {Boolean} True if the user is scrubbing
     */_this.scrubbing_=false;_this.el_=_this.createEl();// Set default value for lastPlaybackRate
_this.cache_.lastPlaybackRate=_this.defaultPlaybackRate();// Make this an evented object and use `el_` as its event bus.
evented(_this,{eventBusKey:'el_'});// We also want to pass the original player options to each component and plugin
// as well so they don't need to reach back into the player for options later.
// We also need to do another copy of this.options_ so we don't end up with
// an infinite loop.
var playerOptionsCopy=mergeOptions(_this.options_);// Load plugins
if(options.plugins){var plugins=options.plugins;Object.keys(plugins).forEach(function(name$$1){if(typeof this[name$$1]==='function'){this[name$$1](plugins[name$$1]);}else {throw new Error('plugin "'+name$$1+'" does not exist');}},_this);}_this.options_.playerOptions=playerOptionsCopy;_this.middleware_=[];_this.initChildren();// Set isAudio based on whether or not an audio tag was used
_this.isAudio(tag.nodeName.toLowerCase()==='audio');// Update controls className. Can't do this when the controls are initially
// set because the element doesn't exist yet.
if(_this.controls()){_this.addClass('vjs-controls-enabled');}else {_this.addClass('vjs-controls-disabled');}// Set ARIA label and region role depending on player type
_this.el_.setAttribute('role','region');if(_this.isAudio()){_this.el_.setAttribute('aria-label',_this.localize('Audio Player'));}else {_this.el_.setAttribute('aria-label',_this.localize('Video Player'));}if(_this.isAudio()){_this.addClass('vjs-audio');}if(_this.flexNotSupported_()){_this.addClass('vjs-no-flex');}// TODO: Make this smarter. Toggle user state between touching/mousing
// using events, since devices can have both touch and mouse events.
// if (browser.TOUCH_ENABLED) {
//   this.addClass('vjs-touch-enabled');
// }
// iOS Safari has broken hover handling
if(!IS_IOS){_this.addClass('vjs-workinghover');}// Make player easily findable by ID
Player.players[_this.id_]=_this;// Add a major version class to aid css in plugins
var majorVersion=version.split('.')[0];_this.addClass('vjs-v'+majorVersion);// When the player is first initialized, trigger activity so components
// like the control bar show themselves if needed
_this.userActive(true);_this.reportUserActivity();_this.one('play',_this.listenForUserActivity_);_this.on('fullscreenchange',_this.handleFullscreenChange_);_this.on('stageclick',_this.handleStageClick_);_this.breakpoints(_this.options_.breakpoints);_this.responsive(_this.options_.responsive);_this.changingSrc_=false;_this.playWaitingForReady_=false;_this.playOnLoadstart_=null;return _this;}/**
   * Destroys the video player and does any necessary cleanup.
   *
   * This is especially helpful if you are dynamically adding and removing videos
   * to/from the DOM.
   *
   * @fires Player#dispose
   */Player.prototype.dispose=function dispose(){/**
     * Called when the player is being disposed of.
     *
     * @event Player#dispose
     * @type {EventTarget~Event}
     */this.trigger('dispose');// prevent dispose from being called twice
this.off('dispose');if(this.styleEl_&&this.styleEl_.parentNode){this.styleEl_.parentNode.removeChild(this.styleEl_);this.styleEl_=null;}// Kill reference to this player
Player.players[this.id_]=null;if(this.tag&&this.tag.player){this.tag.player=null;}if(this.el_&&this.el_.player){this.el_.player=null;}if(this.tech_){this.tech_.dispose();this.isPosterFromTech_=false;this.poster_='';}if(this.playerElIngest_){this.playerElIngest_=null;}if(this.tag){this.tag=null;}clearCacheForPlayer(this);// the actual .el_ is removed here
_Component.prototype.dispose.call(this);};/**
   * Create the `Player`'s DOM element.
   *
   * @return {Element}
   *         The DOM element that gets created.
   */Player.prototype.createEl=function createEl$$1(){var tag=this.tag;var el=void 0;var playerElIngest=this.playerElIngest_=tag.parentNode&&tag.parentNode.hasAttribute&&tag.parentNode.hasAttribute('data-vjs-player');var divEmbed=this.tag.tagName.toLowerCase()==='video-js';if(playerElIngest){el=this.el_=tag.parentNode;}else if(!divEmbed){el=this.el_=_Component.prototype.createEl.call(this,'div');}// Copy over all the attributes from the tag, including ID and class
// ID will now reference player box, not the video tag
var attrs=getAttributes(tag);if(divEmbed){el=this.el_=tag;tag=this.tag=document$1.createElement('video');while(el.children.length){tag.appendChild(el.firstChild);}if(!hasClass(el,'video-js')){addClass(el,'video-js');}el.appendChild(tag);playerElIngest=this.playerElIngest_=el;// copy over properties from the video-js element
// ie8 doesn't support Object.keys nor hasOwnProperty
// on dom elements so we have to specify properties individually
['autoplay','controls','crossOrigin','defaultMuted','defaultPlaybackRate','loop','muted','playbackRate','src','volume'].forEach(function(prop){if(typeof el[prop]!=='undefined'){tag[prop]=el[prop];}});}// set tabindex to -1 to remove the video element from the focus order
tag.setAttribute('tabindex','-1');attrs.tabindex='-1';// Workaround for #4583 (JAWS+IE doesn't announce BPB or play button)
// See https://github.com/FreedomScientific/VFO-standards-support/issues/78
// Note that we can't detect if JAWS is being used, but this ARIA attribute
//  doesn't change behavior of IE11 if JAWS is not being used
if(IE_VERSION){tag.setAttribute('role','application');attrs.role='application';}// Remove width/height attrs from tag so CSS can make it 100% width/height
tag.removeAttribute('width');tag.removeAttribute('height');if('width'in attrs){delete attrs.width;}if('height'in attrs){delete attrs.height;}Object.getOwnPropertyNames(attrs).forEach(function(attr){// workaround so we don't totally break IE7
// http://stackoverflow.com/questions/3653444/css-styles-not-applied-on-dynamic-elements-in-internet-explorer-7
if(attr==='class'){el.className+=' '+attrs[attr];if(divEmbed){tag.className+=' '+attrs[attr];}}else {el.setAttribute(attr,attrs[attr]);if(divEmbed){tag.setAttribute(attr,attrs[attr]);}}});// Update tag id/class for use as HTML5 playback tech
// Might think we should do this after embedding in container so .vjs-tech class
// doesn't flash 100% width/height, but class only applies with .video-js parent
tag.playerId=tag.id;tag.id+='_html5_api';tag.className='vjs-tech';// Make player findable on elements
tag.player=el.player=this;// Default state of video is paused
this.addClass('vjs-paused');// Add a style element in the player that we'll use to set the width/height
// of the player in a way that's still overrideable by CSS, just like the
// video element
if(window$1.VIDEOJS_NO_DYNAMIC_STYLE!==true){this.styleEl_=createStyleElement('vjs-styles-dimensions');var defaultsStyleEl=$$1('.vjs-styles-defaults');var head=$$1('head');head.insertBefore(this.styleEl_,defaultsStyleEl?defaultsStyleEl.nextSibling:head.firstChild);}this.fill_=false;this.fluid_=false;// Pass in the width/height/aspectRatio options which will update the style el
this.width(this.options_.width);this.height(this.options_.height);this.fill(this.options_.fill);this.fluid(this.options_.fluid);this.aspectRatio(this.options_.aspectRatio);// Hide any links within the video/audio tag, because IE doesn't hide them completely.
var links=tag.getElementsByTagName('a');for(var i=0;i<links.length;i++){var linkEl=links.item(i);addClass(linkEl,'vjs-hidden');linkEl.setAttribute('hidden','hidden');}// insertElFirst seems to cause the networkState to flicker from 3 to 2, so
// keep track of the original for later so we can know if the source originally failed
tag.initNetworkState_=tag.networkState;// Wrap video tag in div (el/box) container
if(tag.parentNode&&!playerElIngest){tag.parentNode.insertBefore(el,tag);}// insert the tag as the first child of the player element
// then manually add it to the children array so that this.addChild
// will work properly for other components
//
// Breaks iPhone, fixed in HTML5 setup.
prependTo(tag,el);this.children_.unshift(tag);// Set lang attr on player to ensure CSS :lang() in consistent with player
// if it's been set to something different to the doc
this.el_.setAttribute('lang',this.language_);this.el_=el;return el;};/**
   * A getter/setter for the `Player`'s width. Returns the player's configured value.
   * To get the current width use `currentWidth()`.
   *
   * @param {number} [value]
   *        The value to set the `Player`'s width to.
   *
   * @return {number}
   *         The current width of the `Player` when getting.
   */Player.prototype.width=function width(value){return this.dimension('width',value);};/**
   * A getter/setter for the `Player`'s height. Returns the player's configured value.
   * To get the current height use `currentheight()`.
   *
   * @param {number} [value]
   *        The value to set the `Player`'s heigth to.
   *
   * @return {number}
   *         The current height of the `Player` when getting.
   */Player.prototype.height=function height(value){return this.dimension('height',value);};/**
   * A getter/setter for the `Player`'s width & height.
   *
   * @param {string} dimension
   *        This string can be:
   *        - 'width'
   *        - 'height'
   *
   * @param {number} [value]
   *        Value for dimension specified in the first argument.
   *
   * @return {number}
   *         The dimension arguments value when getting (width/height).
   */Player.prototype.dimension=function dimension(_dimension,value){var privDimension=_dimension+'_';if(value===undefined){return this[privDimension]||0;}if(value===''){// If an empty string is given, reset the dimension to be automatic
this[privDimension]=undefined;this.updateStyleEl_();return;}var parsedVal=parseFloat(value);if(isNaN(parsedVal)){log$1.error('Improper value "'+value+'" supplied for for '+_dimension);return;}this[privDimension]=parsedVal;this.updateStyleEl_();};/**
   * A getter/setter/toggler for the vjs-fluid `className` on the `Player`.
   *
   * Turning this on will turn off fill mode.
   *
   * @param {boolean} [bool]
   *        - A value of true adds the class.
   *        - A value of false removes the class.
   *        - No value will be a getter.
   *
   * @return {boolean|undefined}
   *         - The value of fluid when getting.
   *         - `undefined` when setting.
   */Player.prototype.fluid=function fluid(bool){if(bool===undefined){return !!this.fluid_;}this.fluid_=!!bool;if(bool){this.addClass('vjs-fluid');this.fill(false);}else {this.removeClass('vjs-fluid');}this.updateStyleEl_();};/**
   * A getter/setter/toggler for the vjs-fill `className` on the `Player`.
   *
   * Turning this on will turn off fluid mode.
   *
   * @param {boolean} [bool]
   *        - A value of true adds the class.
   *        - A value of false removes the class.
   *        - No value will be a getter.
   *
   * @return {boolean|undefined}
   *         - The value of fluid when getting.
   *         - `undefined` when setting.
   */Player.prototype.fill=function fill(bool){if(bool===undefined){return !!this.fill_;}this.fill_=!!bool;if(bool){this.addClass('vjs-fill');this.fluid(false);}else {this.removeClass('vjs-fill');}};/**
   * Get/Set the aspect ratio
   *
   * @param {string} [ratio]
   *        Aspect ratio for player
   *
   * @return {string|undefined}
   *         returns the current aspect ratio when getting
   */ /**
   * A getter/setter for the `Player`'s aspect ratio.
   *
   * @param {string} [ratio]
   *        The value to set the `Player's aspect ratio to.
   *
   * @return {string|undefined}
   *         - The current aspect ratio of the `Player` when getting.
   *         - undefined when setting
   */Player.prototype.aspectRatio=function aspectRatio(ratio){if(ratio===undefined){return this.aspectRatio_;}// Check for width:height format
if(!/^\d+\:\d+$/.test(ratio)){throw new Error('Improper value supplied for aspect ratio. The format should be width:height, for example 16:9.');}this.aspectRatio_=ratio;// We're assuming if you set an aspect ratio you want fluid mode,
// because in fixed mode you could calculate width and height yourself.
this.fluid(true);this.updateStyleEl_();};/**
   * Update styles of the `Player` element (height, width and aspect ratio).
   *
   * @private
   * @listens Tech#loadedmetadata
   */Player.prototype.updateStyleEl_=function updateStyleEl_(){if(window$1.VIDEOJS_NO_DYNAMIC_STYLE===true){var _width=typeof this.width_==='number'?this.width_:this.options_.width;var _height=typeof this.height_==='number'?this.height_:this.options_.height;var techEl=this.tech_&&this.tech_.el();if(techEl){if(_width>=0){techEl.width=_width;}if(_height>=0){techEl.height=_height;}}return;}var width=void 0;var height=void 0;var aspectRatio=void 0;var idClass=void 0;// The aspect ratio is either used directly or to calculate width and height.
if(this.aspectRatio_!==undefined&&this.aspectRatio_!=='auto'){// Use any aspectRatio that's been specifically set
aspectRatio=this.aspectRatio_;}else if(this.videoWidth()>0){// Otherwise try to get the aspect ratio from the video metadata
aspectRatio=this.videoWidth()+':'+this.videoHeight();}else {// Or use a default. The video element's is 2:1, but 16:9 is more common.
aspectRatio='16:9';}// Get the ratio as a decimal we can use to calculate dimensions
var ratioParts=aspectRatio.split(':');var ratioMultiplier=ratioParts[1]/ratioParts[0];if(this.width_!==undefined){// Use any width that's been specifically set
width=this.width_;}else if(this.height_!==undefined){// Or calulate the width from the aspect ratio if a height has been set
width=this.height_/ratioMultiplier;}else {// Or use the video's metadata, or use the video el's default of 300
width=this.videoWidth()||300;}if(this.height_!==undefined){// Use any height that's been specifically set
height=this.height_;}else {// Otherwise calculate the height from the ratio and the width
height=width*ratioMultiplier;}// Ensure the CSS class is valid by starting with an alpha character
if(/^[^a-zA-Z]/.test(this.id())){idClass='dimensions-'+this.id();}else {idClass=this.id()+'-dimensions';}// Ensure the right class is still on the player for the style element
this.addClass(idClass);setTextContent(this.styleEl_,'\n      .'+idClass+' {\n        width: '+width+'px;\n        height: '+height+'px;\n      }\n\n      .'+idClass+'.vjs-fluid {\n        padding-top: '+ratioMultiplier*100+'%;\n      }\n    ');};/**
   * Load/Create an instance of playback {@link Tech} including element
   * and API methods. Then append the `Tech` element in `Player` as a child.
   *
   * @param {string} techName
   *        name of the playback technology
   *
   * @param {string} source
   *        video source
   *
   * @private
   */Player.prototype.loadTech_=function loadTech_(techName,source){var _this2=this;// Pause and remove current playback technology
if(this.tech_){this.unloadTech_();}var titleTechName=toTitleCase(techName);var camelTechName=techName.charAt(0).toLowerCase()+techName.slice(1);// get rid of the HTML5 video tag as soon as we are using another tech
if(titleTechName!=='Html5'&&this.tag){Tech.getTech('Html5').disposeMediaElement(this.tag);this.tag.player=null;this.tag=null;}this.techName_=titleTechName;// Turn off API access because we're loading a new tech that might load asynchronously
this.isReady_=false;// if autoplay is a string we pass false to the tech
// because the player is going to handle autoplay on `loadstart`
var autoplay=typeof this.autoplay()==='string'?false:this.autoplay();// Grab tech-specific options from player options and add source and parent element to use.
var techOptions={source:source,autoplay:autoplay,'nativeControlsForTouch':this.options_.nativeControlsForTouch,'playerId':this.id(),'techId':this.id()+'_'+camelTechName+'_api','playsinline':this.options_.playsinline,'preload':this.options_.preload,'loop':this.options_.loop,'muted':this.options_.muted,'poster':this.poster(),'language':this.language(),'playerElIngest':this.playerElIngest_||false,'vtt.js':this.options_['vtt.js'],'canOverridePoster':!!this.options_.techCanOverridePoster,'enableSourceset':this.options_.enableSourceset};ALL.names.forEach(function(name$$1){var props=ALL[name$$1];techOptions[props.getterName]=_this2[props.privateName];});assign$1(techOptions,this.options_[titleTechName]);assign$1(techOptions,this.options_[camelTechName]);assign$1(techOptions,this.options_[techName.toLowerCase()]);if(this.tag){techOptions.tag=this.tag;}if(source&&source.src===this.cache_.src&&this.cache_.currentTime>0){techOptions.startTime=this.cache_.currentTime;}// Initialize tech instance
var TechClass=Tech.getTech(techName);if(!TechClass){throw new Error('No Tech named \''+titleTechName+'\' exists! \''+titleTechName+'\' should be registered using videojs.registerTech()\'');}this.tech_=new TechClass(techOptions);// player.triggerReady is always async, so don't need this to be async
this.tech_.ready(bind(this,this.handleTechReady_),true);textTrackConverter.jsonToTextTracks(this.textTracksJson_||[],this.tech_);// Listen to all HTML5-defined events and trigger them on the player
TECH_EVENTS_RETRIGGER.forEach(function(event){_this2.on(_this2.tech_,event,_this2['handleTech'+toTitleCase(event)+'_']);});Object.keys(TECH_EVENTS_QUEUE).forEach(function(event){_this2.on(_this2.tech_,event,function(eventObj){if(_this2.tech_.playbackRate()===0&&_this2.tech_.seeking()){_this2.queuedCallbacks_.push({callback:_this2['handleTech'+TECH_EVENTS_QUEUE[event]+'_'].bind(_this2),event:eventObj});return;}_this2['handleTech'+TECH_EVENTS_QUEUE[event]+'_'](eventObj);});});this.on(this.tech_,'loadstart',this.handleTechLoadStart_);this.on(this.tech_,'sourceset',this.handleTechSourceset_);this.on(this.tech_,'waiting',this.handleTechWaiting_);this.on(this.tech_,'ended',this.handleTechEnded_);this.on(this.tech_,'seeking',this.handleTechSeeking_);this.on(this.tech_,'play',this.handleTechPlay_);this.on(this.tech_,'firstplay',this.handleTechFirstPlay_);this.on(this.tech_,'pause',this.handleTechPause_);this.on(this.tech_,'durationchange',this.handleTechDurationChange_);this.on(this.tech_,'fullscreenchange',this.handleTechFullscreenChange_);this.on(this.tech_,'error',this.handleTechError_);this.on(this.tech_,'loadedmetadata',this.updateStyleEl_);this.on(this.tech_,'posterchange',this.handleTechPosterChange_);this.on(this.tech_,'textdata',this.handleTechTextData_);this.on(this.tech_,'ratechange',this.handleTechRateChange_);this.usingNativeControls(this.techGet_('controls'));if(this.controls()&&!this.usingNativeControls()){this.addTechControlsListeners_();}// Add the tech element in the DOM if it was not already there
// Make sure to not insert the original video element if using Html5
if(this.tech_.el().parentNode!==this.el()&&(titleTechName!=='Html5'||!this.tag)){prependTo(this.tech_.el(),this.el());}// Get rid of the original video tag reference after the first tech is loaded
if(this.tag){this.tag.player=null;this.tag=null;}};/**
   * Unload and dispose of the current playback {@link Tech}.
   *
   * @private
   */Player.prototype.unloadTech_=function unloadTech_(){var _this3=this;// Save the current text tracks so that we can reuse the same text tracks with the next tech
ALL.names.forEach(function(name$$1){var props=ALL[name$$1];_this3[props.privateName]=_this3[props.getterName]();});this.textTracksJson_=textTrackConverter.textTracksToJson(this.tech_);this.isReady_=false;this.tech_.dispose();this.tech_=false;if(this.isPosterFromTech_){this.poster_='';this.trigger('posterchange');}this.isPosterFromTech_=false;};/**
   * Return a reference to the current {@link Tech}.
   * It will print a warning by default about the danger of using the tech directly
   * but any argument that is passed in will silence the warning.
   *
   * @param {*} [safety]
   *        Anything passed in to silence the warning
   *
   * @return {Tech}
   *         The Tech
   */Player.prototype.tech=function tech(safety){if(safety===undefined){log$1.warn(tsml(_templateObject$1));}return this.tech_;};/**
   * Set up click and touch listeners for the playback element
   *
   * - On desktops: a click on the video itself will toggle playback
   * - On mobile devices: a click on the video toggles controls
   *   which is done by toggling the user state between active and
   *   inactive
   * - A tap can signal that a user has become active or has become inactive
   *   e.g. a quick tap on an iPhone movie should reveal the controls. Another
   *   quick tap should hide them again (signaling the user is in an inactive
   *   viewing state)
   * - In addition to this, we still want the user to be considered inactive after
   *   a few seconds of inactivity.
   *
   * > Note: the only part of iOS interaction we can't mimic with this setup
   * is a touch and hold on the video element counting as activity in order to
   * keep the controls showing, but that shouldn't be an issue. A touch and hold
   * on any controls will still keep the user active
   *
   * @private
   */Player.prototype.addTechControlsListeners_=function addTechControlsListeners_(){// Make sure to remove all the previous listeners in case we are called multiple times.
this.removeTechControlsListeners_();// Some browsers (Chrome & IE) don't trigger a click on a flash swf, but do
// trigger mousedown/up.
// http://stackoverflow.com/questions/1444562/javascript-onclick-event-over-flash-object
// Any touch events are set to block the mousedown event from happening
this.on(this.tech_,'mousedown',this.handleTechClick_);// If the controls were hidden we don't want that to change without a tap event
// so we'll check if the controls were already showing before reporting user
// activity
this.on(this.tech_,'touchstart',this.handleTechTouchStart_);this.on(this.tech_,'touchmove',this.handleTechTouchMove_);this.on(this.tech_,'touchend',this.handleTechTouchEnd_);// The tap listener needs to come after the touchend listener because the tap
// listener cancels out any reportedUserActivity when setting userActive(false)
this.on(this.tech_,'tap',this.handleTechTap_);};/**
   * Remove the listeners used for click and tap controls. This is needed for
   * toggling to controls disabled, where a tap/touch should do nothing.
   *
   * @private
   */Player.prototype.removeTechControlsListeners_=function removeTechControlsListeners_(){// We don't want to just use `this.off()` because there might be other needed
// listeners added by techs that extend this.
this.off(this.tech_,'tap',this.handleTechTap_);this.off(this.tech_,'touchstart',this.handleTechTouchStart_);this.off(this.tech_,'touchmove',this.handleTechTouchMove_);this.off(this.tech_,'touchend',this.handleTechTouchEnd_);this.off(this.tech_,'mousedown',this.handleTechClick_);};/**
   * Player waits for the tech to be ready
   *
   * @private
   */Player.prototype.handleTechReady_=function handleTechReady_(){this.triggerReady();// Keep the same volume as before
if(this.cache_.volume){this.techCall_('setVolume',this.cache_.volume);}// Look if the tech found a higher resolution poster while loading
this.handleTechPosterChange_();// Update the duration if available
this.handleTechDurationChange_();// Chrome and Safari both have issues with autoplay.
// In Safari (5.1.1), when we move the video element into the container div, autoplay doesn't work.
// In Chrome (15), if you have autoplay + a poster + no controls, the video gets hidden (but audio plays)
// This fixes both issues. Need to wait for API, so it updates displays correctly
if((this.src()||this.currentSrc())&&this.tag&&this.options_.autoplay&&this.paused()){try{// Chrome Fix. Fixed in Chrome v16.
delete this.tag.poster;}catch(e){log$1('deleting tag.poster throws in some browsers',e);}}};/**
   * Retrigger the `loadstart` event that was triggered by the {@link Tech}. This
   * function will also trigger {@link Player#firstplay} if it is the first loadstart
   * for a video.
   *
   * @fires Player#loadstart
   * @fires Player#firstplay
   * @listens Tech#loadstart
   * @private
   */Player.prototype.handleTechLoadStart_=function handleTechLoadStart_(){// TODO: Update to use `emptied` event instead. See #1277.
this.removeClass('vjs-ended');this.removeClass('vjs-seeking');// reset the error state
this.error(null);// If it's already playing we want to trigger a firstplay event now.
// The firstplay event relies on both the play and loadstart events
// which can happen in any order for a new source
if(!this.paused()){/**
       * Fired when the user agent begins looking for media data
       *
       * @event Player#loadstart
       * @type {EventTarget~Event}
       */this.trigger('loadstart');this.trigger('firstplay');}else {// reset the hasStarted state
this.hasStarted(false);this.trigger('loadstart');}// autoplay happens after loadstart for the browser,
// so we mimic that behavior
this.manualAutoplay_(this.autoplay());};/**
   * Handle autoplay string values, rather than the typical boolean
   * values that should be handled by the tech. Note that this is not
   * part of any specification. Valid values and what they do can be
   * found on the autoplay getter at Player#autoplay()
   */Player.prototype.manualAutoplay_=function manualAutoplay_(type){var _this4=this;if(!this.tech_||typeof type!=='string'){return;}var muted=function muted(){var previouslyMuted=_this4.muted();_this4.muted(true);var playPromise=_this4.play();if(!playPromise||!playPromise.then||!playPromise['catch']){return;}return playPromise['catch'](function(e){// restore old value of muted on failure
_this4.muted(previouslyMuted);});};var promise=void 0;if(type==='any'){promise=this.play();if(promise&&promise.then&&promise['catch']){promise['catch'](function(){return muted();});}}else if(type==='muted'){promise=muted();}else {promise=this.play();}if(!promise||!promise.then||!promise['catch']){return;}return promise.then(function(){_this4.trigger({type:'autoplay-success',autoplay:type});})['catch'](function(e){_this4.trigger({type:'autoplay-failure',autoplay:type});});};/**
   * Update the internal source caches so that we return the correct source from
   * `src()`, `currentSource()`, and `currentSources()`.
   *
   * > Note: `currentSources` will not be updated if the source that is passed in exists
   *         in the current `currentSources` cache.
   *
   *
   * @param {Tech~SourceObject} srcObj
   *        A string or object source to update our caches to.
   */Player.prototype.updateSourceCaches_=function updateSourceCaches_(){var srcObj=arguments.length>0&&arguments[0]!==undefined?arguments[0]:'';var src=srcObj;var type='';if(typeof src!=='string'){src=srcObj.src;type=srcObj.type;}// make sure all the caches are set to default values
// to prevent null checking
this.cache_.source=this.cache_.source||{};this.cache_.sources=this.cache_.sources||[];// try to get the type of the src that was passed in
if(src&&!type){type=findMimetype(this,src);}// update `currentSource` cache always
this.cache_.source=mergeOptions({},srcObj,{src:src,type:type});var matchingSources=this.cache_.sources.filter(function(s){return s.src&&s.src===src;});var sourceElSources=[];var sourceEls=this.$$('source');var matchingSourceEls=[];for(var i=0;i<sourceEls.length;i++){var sourceObj=getAttributes(sourceEls[i]);sourceElSources.push(sourceObj);if(sourceObj.src&&sourceObj.src===src){matchingSourceEls.push(sourceObj.src);}}// if we have matching source els but not matching sources
// the current source cache is not up to date
if(matchingSourceEls.length&&!matchingSources.length){this.cache_.sources=sourceElSources;// if we don't have matching source or source els set the
// sources cache to the `currentSource` cache
}else if(!matchingSources.length){this.cache_.sources=[this.cache_.source];}// update the tech `src` cache
this.cache_.src=src;};/**
   * *EXPERIMENTAL* Fired when the source is set or changed on the {@link Tech}
   * causing the media element to reload.
   *
   * It will fire for the initial source and each subsequent source.
   * This event is a custom event from Video.js and is triggered by the {@link Tech}.
   *
   * The event object for this event contains a `src` property that will contain the source
   * that was available when the event was triggered. This is generally only necessary if Video.js
   * is switching techs while the source was being changed.
   *
   * It is also fired when `load` is called on the player (or media element)
   * because the {@link https://html.spec.whatwg.org/multipage/media.html#dom-media-load|specification for `load`}
   * says that the resource selection algorithm needs to be aborted and restarted.
   * In this case, it is very likely that the `src` property will be set to the
   * empty string `""` to indicate we do not know what the source will be but
   * that it is changing.
   *
   * *This event is currently still experimental and may change in minor releases.*
   * __To use this, pass `enableSourceset` option to the player.__
   *
   * @event Player#sourceset
   * @type {EventTarget~Event}
   * @prop {string} src
   *                The source url available when the `sourceset` was triggered.
   *                It will be an empty string if we cannot know what the source is
   *                but know that the source will change.
   */ /**
   * Retrigger the `sourceset` event that was triggered by the {@link Tech}.
   *
   * @fires Player#sourceset
   * @listens Tech#sourceset
   * @private
   */Player.prototype.handleTechSourceset_=function handleTechSourceset_(event){var _this5=this;// only update the source cache when the source
// was not updated using the player api
if(!this.changingSrc_){var updateSourceCaches=function updateSourceCaches(src){return _this5.updateSourceCaches_(src);};var playerSrc=this.currentSource().src;var eventSrc=event.src;// if we have a playerSrc that is not a blob, and a tech src that is a blob
if(playerSrc&&!/^blob:/.test(playerSrc)&&/^blob:/.test(eventSrc)){// if both the tech source and the player source were updated we assume
// something like @videojs/http-streaming did the sourceset and skip updating the source cache.
if(!this.lastSource_||this.lastSource_.tech!==eventSrc&&this.lastSource_.player!==playerSrc){updateSourceCaches=function updateSourceCaches(){};}}// update the source to the intial source right away
// in some cases this will be empty string
updateSourceCaches(eventSrc);// if the `sourceset` `src` was an empty string
// wait for a `loadstart` to update the cache to `currentSrc`.
// If a sourceset happens before a `loadstart`, we reset the state
// as this function will be called again.
if(!event.src){var updateCache=function updateCache(e){if(e.type!=='sourceset'){var techSrc=_this5.techGet('currentSrc');_this5.lastSource_.tech=techSrc;_this5.updateSourceCaches_(techSrc);}_this5.tech_.off(['sourceset','loadstart'],updateCache);};this.tech_.one(['sourceset','loadstart'],updateCache);}}this.lastSource_={player:this.currentSource().src,tech:event.src};this.trigger({src:event.src,type:'sourceset'});};/**
   * Add/remove the vjs-has-started class
   *
   * @fires Player#firstplay
   *
   * @param {boolean} request
   *        - true: adds the class
   *        - false: remove the class
   *
   * @return {boolean}
   *         the boolean value of hasStarted_
   */Player.prototype.hasStarted=function hasStarted(request){if(request===undefined){// act as getter, if we have no request to change
return this.hasStarted_;}if(request===this.hasStarted_){return;}this.hasStarted_=request;if(this.hasStarted_){this.addClass('vjs-has-started');this.trigger('firstplay');}else {this.removeClass('vjs-has-started');}};/**
   * Fired whenever the media begins or resumes playback
   *
   * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#dom-media-play}
   * @fires Player#play
   * @listens Tech#play
   * @private
   */Player.prototype.handleTechPlay_=function handleTechPlay_(){this.removeClass('vjs-ended');this.removeClass('vjs-paused');this.addClass('vjs-playing');// hide the poster when the user hits play
this.hasStarted(true);/**
     * Triggered whenever an {@link Tech#play} event happens. Indicates that
     * playback has started or resumed.
     *
     * @event Player#play
     * @type {EventTarget~Event}
     */this.trigger('play');};/**
   * Retrigger the `ratechange` event that was triggered by the {@link Tech}.
   *
   * If there were any events queued while the playback rate was zero, fire
   * those events now.
   *
   * @private
   * @method Player#handleTechRateChange_
   * @fires Player#ratechange
   * @listens Tech#ratechange
   */Player.prototype.handleTechRateChange_=function handleTechRateChange_(){if(this.tech_.playbackRate()>0&&this.cache_.lastPlaybackRate===0){this.queuedCallbacks_.forEach(function(queued){return queued.callback(queued.event);});this.queuedCallbacks_=[];}this.cache_.lastPlaybackRate=this.tech_.playbackRate();/**
     * Fires when the playing speed of the audio/video is changed
     *
     * @event Player#ratechange
     * @type {event}
     */this.trigger('ratechange');};/**
   * Retrigger the `waiting` event that was triggered by the {@link Tech}.
   *
   * @fires Player#waiting
   * @listens Tech#waiting
   * @private
   */Player.prototype.handleTechWaiting_=function handleTechWaiting_(){var _this6=this;this.addClass('vjs-waiting');/**
     * A readyState change on the DOM element has caused playback to stop.
     *
     * @event Player#waiting
     * @type {EventTarget~Event}
     */this.trigger('waiting');this.one('timeupdate',function(){return _this6.removeClass('vjs-waiting');});};/**
   * Retrigger the `canplay` event that was triggered by the {@link Tech}.
   * > Note: This is not consistent between browsers. See #1351
   *
   * @fires Player#canplay
   * @listens Tech#canplay
   * @private
   */Player.prototype.handleTechCanPlay_=function handleTechCanPlay_(){this.removeClass('vjs-waiting');/**
     * The media has a readyState of HAVE_FUTURE_DATA or greater.
     *
     * @event Player#canplay
     * @type {EventTarget~Event}
     */this.trigger('canplay');};/**
   * Retrigger the `canplaythrough` event that was triggered by the {@link Tech}.
   *
   * @fires Player#canplaythrough
   * @listens Tech#canplaythrough
   * @private
   */Player.prototype.handleTechCanPlayThrough_=function handleTechCanPlayThrough_(){this.removeClass('vjs-waiting');/**
     * The media has a readyState of HAVE_ENOUGH_DATA or greater. This means that the
     * entire media file can be played without buffering.
     *
     * @event Player#canplaythrough
     * @type {EventTarget~Event}
     */this.trigger('canplaythrough');};/**
   * Retrigger the `playing` event that was triggered by the {@link Tech}.
   *
   * @fires Player#playing
   * @listens Tech#playing
   * @private
   */Player.prototype.handleTechPlaying_=function handleTechPlaying_(){this.removeClass('vjs-waiting');/**
     * The media is no longer blocked from playback, and has started playing.
     *
     * @event Player#playing
     * @type {EventTarget~Event}
     */this.trigger('playing');};/**
   * Retrigger the `seeking` event that was triggered by the {@link Tech}.
   *
   * @fires Player#seeking
   * @listens Tech#seeking
   * @private
   */Player.prototype.handleTechSeeking_=function handleTechSeeking_(){this.addClass('vjs-seeking');/**
     * Fired whenever the player is jumping to a new time
     *
     * @event Player#seeking
     * @type {EventTarget~Event}
     */this.trigger('seeking');};/**
   * Retrigger the `seeked` event that was triggered by the {@link Tech}.
   *
   * @fires Player#seeked
   * @listens Tech#seeked
   * @private
   */Player.prototype.handleTechSeeked_=function handleTechSeeked_(){this.removeClass('vjs-seeking');/**
     * Fired when the player has finished jumping to a new time
     *
     * @event Player#seeked
     * @type {EventTarget~Event}
     */this.trigger('seeked');};/**
   * Retrigger the `firstplay` event that was triggered by the {@link Tech}.
   *
   * @fires Player#firstplay
   * @listens Tech#firstplay
   * @deprecated As of 6.0 firstplay event is deprecated.
   *             As of 6.0 passing the `starttime` option to the player and the firstplay event are deprecated.
   * @private
   */Player.prototype.handleTechFirstPlay_=function handleTechFirstPlay_(){// If the first starttime attribute is specified
// then we will start at the given offset in seconds
if(this.options_.starttime){log$1.warn('Passing the `starttime` option to the player will be deprecated in 6.0');this.currentTime(this.options_.starttime);}this.addClass('vjs-has-started');/**
     * Fired the first time a video is played. Not part of the HLS spec, and this is
     * probably not the best implementation yet, so use sparingly. If you don't have a
     * reason to prevent playback, use `myPlayer.one('play');` instead.
     *
     * @event Player#firstplay
     * @deprecated As of 6.0 firstplay event is deprecated.
     * @type {EventTarget~Event}
     */this.trigger('firstplay');};/**
   * Retrigger the `pause` event that was triggered by the {@link Tech}.
   *
   * @fires Player#pause
   * @listens Tech#pause
   * @private
   */Player.prototype.handleTechPause_=function handleTechPause_(){this.removeClass('vjs-playing');this.addClass('vjs-paused');/**
     * Fired whenever the media has been paused
     *
     * @event Player#pause
     * @type {EventTarget~Event}
     */this.trigger('pause');};/**
   * Retrigger the `ended` event that was triggered by the {@link Tech}.
   *
   * @fires Player#ended
   * @listens Tech#ended
   * @private
   */Player.prototype.handleTechEnded_=function handleTechEnded_(){this.addClass('vjs-ended');if(this.options_.loop){this.currentTime(0);this.play();}else if(!this.paused()){this.pause();}/**
     * Fired when the end of the media resource is reached (currentTime == duration)
     *
     * @event Player#ended
     * @type {EventTarget~Event}
     */this.trigger('ended');};/**
   * Fired when the duration of the media resource is first known or changed
   *
   * @listens Tech#durationchange
   * @private
   */Player.prototype.handleTechDurationChange_=function handleTechDurationChange_(){this.duration(this.techGet_('duration'));};/**
   * Handle a click on the media element to play/pause
   *
   * @param {EventTarget~Event} event
   *        the event that caused this function to trigger
   *
   * @listens Tech#mousedown
   * @private
   */Player.prototype.handleTechClick_=function handleTechClick_(event){if(!isSingleLeftClick(event)){return;}// When controls are disabled a click should not toggle playback because
// the click is considered a control
if(!this.controls_){return;}if(this.paused()){silencePromise(this.play());}else {this.pause();}};/**
   * Handle a tap on the media element. It will toggle the user
   * activity state, which hides and shows the controls.
   *
   * @listens Tech#tap
   * @private
   */Player.prototype.handleTechTap_=function handleTechTap_(){this.userActive(!this.userActive());};/**
   * Handle touch to start
   *
   * @listens Tech#touchstart
   * @private
   */Player.prototype.handleTechTouchStart_=function handleTechTouchStart_(){this.userWasActive=this.userActive();};/**
   * Handle touch to move
   *
   * @listens Tech#touchmove
   * @private
   */Player.prototype.handleTechTouchMove_=function handleTechTouchMove_(){if(this.userWasActive){this.reportUserActivity();}};/**
   * Handle touch to end
   *
   * @param {EventTarget~Event} event
   *        the touchend event that triggered
   *        this function
   *
   * @listens Tech#touchend
   * @private
   */Player.prototype.handleTechTouchEnd_=function handleTechTouchEnd_(event){// Stop the mouse events from also happening
event.preventDefault();};/**
   * Fired when the player switches in or out of fullscreen mode
   *
   * @private
   * @listens Player#fullscreenchange
   */Player.prototype.handleFullscreenChange_=function handleFullscreenChange_(){if(this.isFullscreen()){this.addClass('vjs-fullscreen');}else {this.removeClass('vjs-fullscreen');}};/**
   * native click events on the SWF aren't triggered on IE11, Win8.1RT
   * use stageclick events triggered from inside the SWF instead
   *
   * @private
   * @listens stageclick
   */Player.prototype.handleStageClick_=function handleStageClick_(){this.reportUserActivity();};/**
   * Handle Tech Fullscreen Change
   *
   * @param {EventTarget~Event} event
   *        the fullscreenchange event that triggered this function
   *
   * @param {Object} data
   *        the data that was sent with the event
   *
   * @private
   * @listens Tech#fullscreenchange
   * @fires Player#fullscreenchange
   */Player.prototype.handleTechFullscreenChange_=function handleTechFullscreenChange_(event,data){if(data){this.isFullscreen(data.isFullscreen);}/**
     * Fired when going in and out of fullscreen.
     *
     * @event Player#fullscreenchange
     * @type {EventTarget~Event}
     */this.trigger('fullscreenchange');};/**
   * Fires when an error occurred during the loading of an audio/video.
   *
   * @private
   * @listens Tech#error
   */Player.prototype.handleTechError_=function handleTechError_(){var error=this.tech_.error();this.error(error);};/**
   * Retrigger the `textdata` event that was triggered by the {@link Tech}.
   *
   * @fires Player#textdata
   * @listens Tech#textdata
   * @private
   */Player.prototype.handleTechTextData_=function handleTechTextData_(){var data=null;if(arguments.length>1){data=arguments[1];}/**
     * Fires when we get a textdata event from tech
     *
     * @event Player#textdata
     * @type {EventTarget~Event}
     */this.trigger('textdata',data);};/**
   * Get object for cached values.
   *
   * @return {Object}
   *         get the current object cache
   */Player.prototype.getCache=function getCache(){return this.cache_;};/**
   * Pass values to the playback tech
   *
   * @param {string} [method]
   *        the method to call
   *
   * @param {Object} arg
   *        the argument to pass
   *
   * @private
   */Player.prototype.techCall_=function techCall_(method,arg){// If it's not ready yet, call method when it is
this.ready(function(){if(method in allowedSetters){return set$1(this.middleware_,this.tech_,method,arg);}else if(method in allowedMediators){return mediate(this.middleware_,this.tech_,method,arg);}try{if(this.tech_){this.tech_[method](arg);}}catch(e){log$1(e);throw e;}},true);};/**
   * Get calls can't wait for the tech, and sometimes don't need to.
   *
   * @param {string} method
   *        Tech method
   *
   * @return {Function|undefined}
   *         the method or undefined
   *
   * @private
   */Player.prototype.techGet_=function techGet_(method){if(!this.tech_||!this.tech_.isReady_){return;}if(method in allowedGetters){return get$1(this.middleware_,this.tech_,method);}else if(method in allowedMediators){return mediate(this.middleware_,this.tech_,method);}// Flash likes to die and reload when you hide or reposition it.
// In these cases the object methods go away and we get errors.
// When that happens we'll catch the errors and inform tech that it's not ready any more.
try{return this.tech_[method]();}catch(e){// When building additional tech libs, an expected method may not be defined yet
if(this.tech_[method]===undefined){log$1('Video.js: '+method+' method not defined for '+this.techName_+' playback technology.',e);throw e;}// When a method isn't available on the object it throws a TypeError
if(e.name==='TypeError'){log$1('Video.js: '+method+' unavailable on '+this.techName_+' playback technology element.',e);this.tech_.isReady_=false;throw e;}// If error unknown, just log and throw
log$1(e);throw e;}};/**
   * Attempt to begin playback at the first opportunity.
   *
   * @return {Promise|undefined}
   *         Returns a promise if the browser supports Promises (or one
   *         was passed in as an option). This promise will be resolved on
   *         the return value of play. If this is undefined it will fulfill the
   *         promise chain otherwise the promise chain will be fulfilled when
   *         the promise from play is fulfilled.
   */Player.prototype.play=function play(){var _this7=this;var PromiseClass=this.options_.Promise||window$1.Promise;if(PromiseClass){return new PromiseClass(function(resolve){_this7.play_(resolve);});}return this.play_();};/**
   * The actual logic for play, takes a callback that will be resolved on the
   * return value of play. This allows us to resolve to the play promise if there
   * is one on modern browsers.
   *
   * @private
   * @param {Function} [callback]
   *        The callback that should be called when the techs play is actually called
   */Player.prototype.play_=function play_(){var _this8=this;var callback=arguments.length>0&&arguments[0]!==undefined?arguments[0]:silencePromise;// If this is called while we have a play queued up on a loadstart, remove
// that listener to avoid getting in a potentially bad state.
if(this.playOnLoadstart_){this.off('loadstart',this.playOnLoadstart_);}// If the player/tech is not ready, queue up another call to `play()` for
// when it is. This will loop back into this method for another attempt at
// playback when the tech is ready.
if(!this.isReady_){// Bail out if we're already waiting for `ready`!
if(this.playWaitingForReady_){return;}this.playWaitingForReady_=true;this.ready(function(){_this8.playWaitingForReady_=false;callback(_this8.play());});// If the player/tech is ready and we have a source, we can attempt playback.
}else if(!this.changingSrc_&&(this.src()||this.currentSrc())){callback(this.techGet_('play'));return;// If the tech is ready, but we do not have a source, we'll need to wait
// for both the `ready` and a `loadstart` when the source is finally
// resolved by middleware and set on the player.
//
// This can happen if `play()` is called while changing sources or before
// one has been set on the player.
}else {this.playOnLoadstart_=function(){_this8.playOnLoadstart_=null;callback(_this8.play());};this.one('loadstart',this.playOnLoadstart_);}};/**
   * Pause the video playback
   *
   * @return {Player}
   *         A reference to the player object this function was called on
   */Player.prototype.pause=function pause(){this.techCall_('pause');};/**
   * Check if the player is paused or has yet to play
   *
   * @return {boolean}
   *         - false: if the media is currently playing
   *         - true: if media is not currently playing
   */Player.prototype.paused=function paused(){// The initial state of paused should be true (in Safari it's actually false)
return this.techGet_('paused')===false?false:true;};/**
   * Get a TimeRange object representing the current ranges of time that the user
   * has played.
   *
   * @return {TimeRange}
   *         A time range object that represents all the increments of time that have
   *         been played.
   */Player.prototype.played=function played(){return this.techGet_('played')||createTimeRanges(0,0);};/**
   * Returns whether or not the user is "scrubbing". Scrubbing is
   * when the user has clicked the progress bar handle and is
   * dragging it along the progress bar.
   *
   * @param {boolean} [isScrubbing]
   *        wether the user is or is not scrubbing
   *
   * @return {boolean}
   *         The value of scrubbing when getting
   */Player.prototype.scrubbing=function scrubbing(isScrubbing){if(typeof isScrubbing==='undefined'){return this.scrubbing_;}this.scrubbing_=!!isScrubbing;if(isScrubbing){this.addClass('vjs-scrubbing');}else {this.removeClass('vjs-scrubbing');}};/**
   * Get or set the current time (in seconds)
   *
   * @param {number|string} [seconds]
   *        The time to seek to in seconds
   *
   * @return {number}
   *         - the current time in seconds when getting
   */Player.prototype.currentTime=function currentTime(seconds){if(typeof seconds!=='undefined'){if(seconds<0){seconds=0;}this.techCall_('setCurrentTime',seconds);return;}// cache last currentTime and return. default to 0 seconds
//
// Caching the currentTime is meant to prevent a massive amount of reads on the tech's
// currentTime when scrubbing, but may not provide much performance benefit afterall.
// Should be tested. Also something has to read the actual current time or the cache will
// never get updated.
this.cache_.currentTime=this.techGet_('currentTime')||0;return this.cache_.currentTime;};/**
   * Normally gets the length in time of the video in seconds;
   * in all but the rarest use cases an argument will NOT be passed to the method
   *
   * > **NOTE**: The video must have started loading before the duration can be
   * known, and in the case of Flash, may not be known until the video starts
   * playing.
   *
   * @fires Player#durationchange
   *
   * @param {number} [seconds]
   *        The duration of the video to set in seconds
   *
   * @return {number}
   *         - The duration of the video in seconds when getting
   */Player.prototype.duration=function duration(seconds){if(seconds===undefined){// return NaN if the duration is not known
return this.cache_.duration!==undefined?this.cache_.duration:NaN;}seconds=parseFloat(seconds);// Standardize on Inifity for signaling video is live
if(seconds<0){seconds=Infinity;}if(seconds!==this.cache_.duration){// Cache the last set value for optimized scrubbing (esp. Flash)
this.cache_.duration=seconds;if(seconds===Infinity){this.addClass('vjs-live');}else {this.removeClass('vjs-live');}/**
       * @event Player#durationchange
       * @type {EventTarget~Event}
       */this.trigger('durationchange');}};/**
   * Calculates how much time is left in the video. Not part
   * of the native video API.
   *
   * @return {number}
   *         The time remaining in seconds
   */Player.prototype.remainingTime=function remainingTime(){return this.duration()-this.currentTime();};/**
   * A remaining time function that is intented to be used when
   * the time is to be displayed directly to the user.
   *
   * @return {number}
   *         The rounded time remaining in seconds
   */Player.prototype.remainingTimeDisplay=function remainingTimeDisplay(){return Math.floor(this.duration())-Math.floor(this.currentTime());};//
// Kind of like an array of portions of the video that have been downloaded.
/**
   * Get a TimeRange object with an array of the times of the video
   * that have been downloaded. If you just want the percent of the
   * video that's been downloaded, use bufferedPercent.
   *
   * @see [Buffered Spec]{@link http://dev.w3.org/html5/spec/video.html#dom-media-buffered}
   *
   * @return {TimeRange}
   *         A mock TimeRange object (following HTML spec)
   */Player.prototype.buffered=function buffered(){var buffered=this.techGet_('buffered');if(!buffered||!buffered.length){buffered=createTimeRanges(0,0);}return buffered;};/**
   * Get the percent (as a decimal) of the video that's been downloaded.
   * This method is not a part of the native HTML video API.
   *
   * @return {number}
   *         A decimal between 0 and 1 representing the percent
   *         that is bufferred 0 being 0% and 1 being 100%
   */Player.prototype.bufferedPercent=function bufferedPercent$$1(){return bufferedPercent(this.buffered(),this.duration());};/**
   * Get the ending time of the last buffered time range
   * This is used in the progress bar to encapsulate all time ranges.
   *
   * @return {number}
   *         The end of the last buffered time range
   */Player.prototype.bufferedEnd=function bufferedEnd(){var buffered=this.buffered();var duration=this.duration();var end=buffered.end(buffered.length-1);if(end>duration){end=duration;}return end;};/**
   * Get or set the current volume of the media
   *
   * @param  {number} [percentAsDecimal]
   *         The new volume as a decimal percent:
   *         - 0 is muted/0%/off
   *         - 1.0 is 100%/full
   *         - 0.5 is half volume or 50%
   *
   * @return {number}
   *         The current volume as a percent when getting
   */Player.prototype.volume=function volume(percentAsDecimal){var vol=void 0;if(percentAsDecimal!==undefined){// Force value to between 0 and 1
vol=Math.max(0,Math.min(1,parseFloat(percentAsDecimal)));this.cache_.volume=vol;this.techCall_('setVolume',vol);if(vol>0){this.lastVolume_(vol);}return;}// Default to 1 when returning current volume.
vol=parseFloat(this.techGet_('volume'));return isNaN(vol)?1:vol;};/**
   * Get the current muted state, or turn mute on or off
   *
   * @param {boolean} [muted]
   *        - true to mute
   *        - false to unmute
   *
   * @return {boolean}
   *         - true if mute is on and getting
   *         - false if mute is off and getting
   */Player.prototype.muted=function muted(_muted){if(_muted!==undefined){this.techCall_('setMuted',_muted);return;}return this.techGet_('muted')||false;};/**
   * Get the current defaultMuted state, or turn defaultMuted on or off. defaultMuted
   * indicates the state of muted on intial playback.
   *
   * ```js
   *   var myPlayer = videojs('some-player-id');
   *
   *   myPlayer.src("http://www.example.com/path/to/video.mp4");
   *
   *   // get, should be false
   *   console.log(myPlayer.defaultMuted());
   *   // set to true
   *   myPlayer.defaultMuted(true);
   *   // get should be true
   *   console.log(myPlayer.defaultMuted());
   * ```
   *
   * @param {boolean} [defaultMuted]
   *        - true to mute
   *        - false to unmute
   *
   * @return {boolean|Player}
   *         - true if defaultMuted is on and getting
   *         - false if defaultMuted is off and getting
   *         - A reference to the current player when setting
   */Player.prototype.defaultMuted=function defaultMuted(_defaultMuted){if(_defaultMuted!==undefined){return this.techCall_('setDefaultMuted',_defaultMuted);}return this.techGet_('defaultMuted')||false;};/**
   * Get the last volume, or set it
   *
   * @param  {number} [percentAsDecimal]
   *         The new last volume as a decimal percent:
   *         - 0 is muted/0%/off
   *         - 1.0 is 100%/full
   *         - 0.5 is half volume or 50%
   *
   * @return {number}
   *         the current value of lastVolume as a percent when getting
   *
   * @private
   */Player.prototype.lastVolume_=function lastVolume_(percentAsDecimal){if(percentAsDecimal!==undefined&&percentAsDecimal!==0){this.cache_.lastVolume=percentAsDecimal;return;}return this.cache_.lastVolume;};/**
   * Check if current tech can support native fullscreen
   * (e.g. with built in controls like iOS, so not our flash swf)
   *
   * @return {boolean}
   *         if native fullscreen is supported
   */Player.prototype.supportsFullScreen=function supportsFullScreen(){return this.techGet_('supportsFullScreen')||false;};/**
   * Check if the player is in fullscreen mode or tell the player that it
   * is or is not in fullscreen mode.
   *
   * > NOTE: As of the latest HTML5 spec, isFullscreen is no longer an official
   * property and instead document.fullscreenElement is used. But isFullscreen is
   * still a valuable property for internal player workings.
   *
   * @param  {boolean} [isFS]
   *         Set the players current fullscreen state
   *
   * @return {boolean}
   *         - true if fullscreen is on and getting
   *         - false if fullscreen is off and getting
   */Player.prototype.isFullscreen=function isFullscreen(isFS){if(isFS!==undefined){this.isFullscreen_=!!isFS;return;}return !!this.isFullscreen_;};/**
   * Increase the size of the video to full screen
   * In some browsers, full screen is not supported natively, so it enters
   * "full window mode", where the video fills the browser window.
   * In browsers and devices that support native full screen, sometimes the
   * browser's default controls will be shown, and not the Video.js custom skin.
   * This includes most mobile devices (iOS, Android) and older versions of
   * Safari.
   *
   * @fires Player#fullscreenchange
   */Player.prototype.requestFullscreen=function requestFullscreen(){var fsApi=FullscreenApi;this.isFullscreen(true);if(fsApi.requestFullscreen){// the browser supports going fullscreen at the element level so we can
// take the controls fullscreen as well as the video
// Trigger fullscreenchange event after change
// We have to specifically add this each time, and remove
// when canceling fullscreen. Otherwise if there's multiple
// players on a page, they would all be reacting to the same fullscreen
// events
on(document$1,fsApi.fullscreenchange,bind(this,function documentFullscreenChange(e){this.isFullscreen(document$1[fsApi.fullscreenElement]);// If cancelling fullscreen, remove event listener.
if(this.isFullscreen()===false){off(document$1,fsApi.fullscreenchange,documentFullscreenChange);}/**
         * @event Player#fullscreenchange
         * @type {EventTarget~Event}
         */this.trigger('fullscreenchange');}));this.el_[fsApi.requestFullscreen]();}else if(this.tech_.supportsFullScreen()){// we can't take the video.js controls fullscreen but we can go fullscreen
// with native controls
this.techCall_('enterFullScreen');}else {// fullscreen isn't supported so we'll just stretch the video element to
// fill the viewport
this.enterFullWindow();/**
       * @event Player#fullscreenchange
       * @type {EventTarget~Event}
       */this.trigger('fullscreenchange');}};/**
   * Return the video to its normal size after having been in full screen mode
   *
   * @fires Player#fullscreenchange
   */Player.prototype.exitFullscreen=function exitFullscreen(){var fsApi=FullscreenApi;this.isFullscreen(false);// Check for browser element fullscreen support
if(fsApi.requestFullscreen){document$1[fsApi.exitFullscreen]();}else if(this.tech_.supportsFullScreen()){this.techCall_('exitFullScreen');}else {this.exitFullWindow();/**
       * @event Player#fullscreenchange
       * @type {EventTarget~Event}
       */this.trigger('fullscreenchange');}};/**
   * When fullscreen isn't supported we can stretch the
   * video container to as wide as the browser will let us.
   *
   * @fires Player#enterFullWindow
   */Player.prototype.enterFullWindow=function enterFullWindow(){this.isFullWindow=true;// Storing original doc overflow value to return to when fullscreen is off
this.docOrigOverflow=document$1.documentElement.style.overflow;// Add listener for esc key to exit fullscreen
on(document$1,'keydown',bind(this,this.fullWindowOnEscKey));// Hide any scroll bars
document$1.documentElement.style.overflow='hidden';// Apply fullscreen styles
addClass(document$1.body,'vjs-full-window');/**
     * @event Player#enterFullWindow
     * @type {EventTarget~Event}
     */this.trigger('enterFullWindow');};/**
   * Check for call to either exit full window or
   * full screen on ESC key
   *
   * @param {string} event
   *        Event to check for key press
   */Player.prototype.fullWindowOnEscKey=function fullWindowOnEscKey(event){if(event.keyCode===27){if(this.isFullscreen()===true){this.exitFullscreen();}else {this.exitFullWindow();}}};/**
   * Exit full window
   *
   * @fires Player#exitFullWindow
   */Player.prototype.exitFullWindow=function exitFullWindow(){this.isFullWindow=false;off(document$1,'keydown',this.fullWindowOnEscKey);// Unhide scroll bars.
document$1.documentElement.style.overflow=this.docOrigOverflow;// Remove fullscreen styles
removeClass(document$1.body,'vjs-full-window');// Resize the box, controller, and poster to original sizes
// this.positionAll();
/**
     * @event Player#exitFullWindow
     * @type {EventTarget~Event}
     */this.trigger('exitFullWindow');};/**
   * Check whether the player can play a given mimetype
   *
   * @see https://www.w3.org/TR/2011/WD-html5-20110113/video.html#dom-navigator-canplaytype
   *
   * @param {string} type
   *        The mimetype to check
   *
   * @return {string}
   *         'probably', 'maybe', or '' (empty string)
   */Player.prototype.canPlayType=function canPlayType(type){var can=void 0;// Loop through each playback technology in the options order
for(var i=0,j=this.options_.techOrder;i<j.length;i++){var techName=j[i];var tech=Tech.getTech(techName);// Support old behavior of techs being registered as components.
// Remove once that deprecated behavior is removed.
if(!tech){tech=Component.getComponent(techName);}// Check if the current tech is defined before continuing
if(!tech){log$1.error('The "'+techName+'" tech is undefined. Skipped browser support check for that tech.');continue;}// Check if the browser supports this technology
if(tech.isSupported()){can=tech.canPlayType(type);if(can){return can;}}}return '';};/**
   * Select source based on tech-order or source-order
   * Uses source-order selection if `options.sourceOrder` is truthy. Otherwise,
   * defaults to tech-order selection
   *
   * @param {Array} sources
   *        The sources for a media asset
   *
   * @return {Object|boolean}
   *         Object of source and tech order or false
   */Player.prototype.selectSource=function selectSource(sources){var _this9=this;// Get only the techs specified in `techOrder` that exist and are supported by the
// current platform
var techs=this.options_.techOrder.map(function(techName){return [techName,Tech.getTech(techName)];}).filter(function(_ref){var techName=_ref[0],tech=_ref[1];// Check if the current tech is defined before continuing
if(tech){// Check if the browser supports this technology
return tech.isSupported();}log$1.error('The "'+techName+'" tech is undefined. Skipped browser support check for that tech.');return false;});// Iterate over each `innerArray` element once per `outerArray` element and execute
// `tester` with both. If `tester` returns a non-falsy value, exit early and return
// that value.
var findFirstPassingTechSourcePair=function findFirstPassingTechSourcePair(outerArray,innerArray,tester){var found=void 0;outerArray.some(function(outerChoice){return innerArray.some(function(innerChoice){found=tester(outerChoice,innerChoice);if(found){return true;}});});return found;};var foundSourceAndTech=void 0;var flip=function flip(fn){return function(a,b){return fn(b,a);};};var finder=function finder(_ref2,source){var techName=_ref2[0],tech=_ref2[1];if(tech.canPlaySource(source,_this9.options_[techName.toLowerCase()])){return {source:source,tech:techName};}};// Depending on the truthiness of `options.sourceOrder`, we swap the order of techs and sources
// to select from them based on their priority.
if(this.options_.sourceOrder){// Source-first ordering
foundSourceAndTech=findFirstPassingTechSourcePair(sources,techs,flip(finder));}else {// Tech-first ordering
foundSourceAndTech=findFirstPassingTechSourcePair(techs,sources,finder);}return foundSourceAndTech||false;};/**
   * Get or set the video source.
   *
   * @param {Tech~SourceObject|Tech~SourceObject[]|string} [source]
   *        A SourceObject, an array of SourceObjects, or a string referencing
   *        a URL to a media source. It is _highly recommended_ that an object
   *        or array of objects is used here, so that source selection
   *        algorithms can take the `type` into account.
   *
   *        If not provided, this method acts as a getter.
   *
   * @return {string|undefined}
   *         If the `source` argument is missing, returns the current source
   *         URL. Otherwise, returns nothing/undefined.
   */Player.prototype.src=function src(source){var _this10=this;// getter usage
if(typeof source==='undefined'){return this.cache_.src||'';}// filter out invalid sources and turn our source into
// an array of source objects
var sources=filterSource(source);// if a source was passed in then it is invalid because
// it was filtered to a zero length Array. So we have to
// show an error
if(!sources.length){this.setTimeout(function(){this.error({code:4,message:this.localize(this.options_.notSupportedMessage)});},0);return;}// intial sources
this.changingSrc_=true;this.cache_.sources=sources;this.updateSourceCaches_(sources[0]);// middlewareSource is the source after it has been changed by middleware
setSource(this,sources[0],function(middlewareSource,mws){_this10.middleware_=mws;// since sourceSet is async we have to update the cache again after we select a source since
// the source that is selected could be out of order from the cache update above this callback.
_this10.cache_.sources=sources;_this10.updateSourceCaches_(middlewareSource);var err=_this10.src_(middlewareSource);if(err){if(sources.length>1){return _this10.src(sources.slice(1));}_this10.changingSrc_=false;// We need to wrap this in a timeout to give folks a chance to add error event handlers
_this10.setTimeout(function(){this.error({code:4,message:this.localize(this.options_.notSupportedMessage)});},0);// we could not find an appropriate tech, but let's still notify the delegate that this is it
// this needs a better comment about why this is needed
_this10.triggerReady();return;}setTech(mws,_this10.tech_);});};/**
   * Set the source object on the tech, returns a boolean that indicates wether
   * there is a tech that can play the source or not
   *
   * @param {Tech~SourceObject} source
   *        The source object to set on the Tech
   *
   * @return {Boolean}
   *         - True if there is no Tech to playback this source
   *         - False otherwise
   *
   * @private
   */Player.prototype.src_=function src_(source){var _this11=this;var sourceTech=this.selectSource([source]);if(!sourceTech){return true;}if(!titleCaseEquals(sourceTech.tech,this.techName_)){this.changingSrc_=true;// load this technology with the chosen source
this.loadTech_(sourceTech.tech,sourceTech.source);this.tech_.ready(function(){_this11.changingSrc_=false;});return false;}// wait until the tech is ready to set the source
// and set it synchronously if possible (#2326)
this.ready(function(){// The setSource tech method was added with source handlers
// so older techs won't support it
// We need to check the direct prototype for the case where subclasses
// of the tech do not support source handlers
if(this.tech_.constructor.prototype.hasOwnProperty('setSource')){this.techCall_('setSource',source);}else {this.techCall_('src',source.src);}this.changingSrc_=false;},true);return false;};/**
   * Begin loading the src data.
   */Player.prototype.load=function load(){this.techCall_('load');};/**
   * Reset the player. Loads the first tech in the techOrder,
   * removes all the text tracks in the existing `tech`,
   * and calls `reset` on the `tech`.
   */Player.prototype.reset=function reset(){if(this.tech_){this.tech_.clearTracks('text');}this.loadTech_(this.options_.techOrder[0],null);this.techCall_('reset');};/**
   * Returns all of the current source objects.
   *
   * @return {Tech~SourceObject[]}
   *         The current source objects
   */Player.prototype.currentSources=function currentSources(){var source=this.currentSource();var sources=[];// assume `{}` or `{ src }`
if(Object.keys(source).length!==0){sources.push(source);}return this.cache_.sources||sources;};/**
   * Returns the current source object.
   *
   * @return {Tech~SourceObject}
   *         The current source object
   */Player.prototype.currentSource=function currentSource(){return this.cache_.source||{};};/**
   * Returns the fully qualified URL of the current source value e.g. http://mysite.com/video.mp4
   * Can be used in conjuction with `currentType` to assist in rebuilding the current source object.
   *
   * @return {string}
   *         The current source
   */Player.prototype.currentSrc=function currentSrc(){return this.currentSource()&&this.currentSource().src||'';};/**
   * Get the current source type e.g. video/mp4
   * This can allow you rebuild the current source object so that you could load the same
   * source and tech later
   *
   * @return {string}
   *         The source MIME type
   */Player.prototype.currentType=function currentType(){return this.currentSource()&&this.currentSource().type||'';};/**
   * Get or set the preload attribute
   *
   * @param {boolean} [value]
   *        - true means that we should preload
   *        - false maens that we should not preload
   *
   * @return {string}
   *         The preload attribute value when getting
   */Player.prototype.preload=function preload(value){if(value!==undefined){this.techCall_('setPreload',value);this.options_.preload=value;return;}return this.techGet_('preload');};/**
   * Get or set the autoplay option. When this is a boolean it will
   * modify the attribute on the tech. When this is a string the attribute on
   * the tech will be removed and `Player` will handle autoplay on loadstarts.
   *
   * @param {boolean|string} [value]
   *        - true: autoplay using the browser behavior
   *        - false: do not autoplay
   *        - 'play': call play() on every loadstart
   *        - 'muted': call muted() then play() on every loadstart
   *        - 'any': call play() on every loadstart. if that fails call muted() then play().
   *        - *: values other than those listed here will be set `autoplay` to true
   *
   * @return {boolean|string}
   *         The current value of autoplay when getting
   */Player.prototype.autoplay=function autoplay(value){// getter usage
if(value===undefined){return this.options_.autoplay||false;}var techAutoplay=void 0;// if the value is a valid string set it to that
if(typeof value==='string'&&/(any|play|muted)/.test(value)){this.options_.autoplay=value;this.manualAutoplay_(value);techAutoplay=false;// any falsy value sets autoplay to false in the browser,
// lets do the same
}else if(!value){this.options_.autoplay=false;// any other value (ie truthy) sets autoplay to true
}else {this.options_.autoplay=true;}techAutoplay=techAutoplay||this.options_.autoplay;// if we don't have a tech then we do not queue up
// a setAutoplay call on tech ready. We do this because the
// autoplay option will be passed in the constructor and we
// do not need to set it twice
if(this.tech_){this.techCall_('setAutoplay',techAutoplay);}};/**
   * Set or unset the playsinline attribute.
   * Playsinline tells the browser that non-fullscreen playback is preferred.
   *
   * @param {boolean} [value]
   *        - true means that we should try to play inline by default
   *        - false means that we should use the browser's default playback mode,
   *          which in most cases is inline. iOS Safari is a notable exception
   *          and plays fullscreen by default.
   *
   * @return {string|Player}
   *         - the current value of playsinline
   *         - the player when setting
   *
   * @see [Spec]{@link https://html.spec.whatwg.org/#attr-video-playsinline}
   */Player.prototype.playsinline=function playsinline(value){if(value!==undefined){this.techCall_('setPlaysinline',value);this.options_.playsinline=value;return this;}return this.techGet_('playsinline');};/**
   * Get or set the loop attribute on the video element.
   *
   * @param {boolean} [value]
   *        - true means that we should loop the video
   *        - false means that we should not loop the video
   *
   * @return {string}
   *         The current value of loop when getting
   */Player.prototype.loop=function loop(value){if(value!==undefined){this.techCall_('setLoop',value);this.options_.loop=value;return;}return this.techGet_('loop');};/**
   * Get or set the poster image source url
   *
   * @fires Player#posterchange
   *
   * @param {string} [src]
   *        Poster image source URL
   *
   * @return {string}
   *         The current value of poster when getting
   */Player.prototype.poster=function poster(src){if(src===undefined){return this.poster_;}// The correct way to remove a poster is to set as an empty string
// other falsey values will throw errors
if(!src){src='';}if(src===this.poster_){return;}// update the internal poster variable
this.poster_=src;// update the tech's poster
this.techCall_('setPoster',src);this.isPosterFromTech_=false;// alert components that the poster has been set
/**
     * This event fires when the poster image is changed on the player.
     *
     * @event Player#posterchange
     * @type {EventTarget~Event}
     */this.trigger('posterchange');};/**
   * Some techs (e.g. YouTube) can provide a poster source in an
   * asynchronous way. We want the poster component to use this
   * poster source so that it covers up the tech's controls.
   * (YouTube's play button). However we only want to use this
   * source if the player user hasn't set a poster through
   * the normal APIs.
   *
   * @fires Player#posterchange
   * @listens Tech#posterchange
   * @private
   */Player.prototype.handleTechPosterChange_=function handleTechPosterChange_(){if((!this.poster_||this.options_.techCanOverridePoster)&&this.tech_&&this.tech_.poster){var newPoster=this.tech_.poster()||'';if(newPoster!==this.poster_){this.poster_=newPoster;this.isPosterFromTech_=true;// Let components know the poster has changed
this.trigger('posterchange');}}};/**
   * Get or set whether or not the controls are showing.
   *
   * @fires Player#controlsenabled
   *
   * @param {boolean} [bool]
   *        - true to turn controls on
   *        - false to turn controls off
   *
   * @return {boolean}
   *         The current value of controls when getting
   */Player.prototype.controls=function controls(bool){if(bool===undefined){return !!this.controls_;}bool=!!bool;// Don't trigger a change event unless it actually changed
if(this.controls_===bool){return;}this.controls_=bool;if(this.usingNativeControls()){this.techCall_('setControls',bool);}if(this.controls_){this.removeClass('vjs-controls-disabled');this.addClass('vjs-controls-enabled');/**
       * @event Player#controlsenabled
       * @type {EventTarget~Event}
       */this.trigger('controlsenabled');if(!this.usingNativeControls()){this.addTechControlsListeners_();}}else {this.removeClass('vjs-controls-enabled');this.addClass('vjs-controls-disabled');/**
       * @event Player#controlsdisabled
       * @type {EventTarget~Event}
       */this.trigger('controlsdisabled');if(!this.usingNativeControls()){this.removeTechControlsListeners_();}}};/**
   * Toggle native controls on/off. Native controls are the controls built into
   * devices (e.g. default iPhone controls), Flash, or other techs
   * (e.g. Vimeo Controls)
   * **This should only be set by the current tech, because only the tech knows
   * if it can support native controls**
   *
   * @fires Player#usingnativecontrols
   * @fires Player#usingcustomcontrols
   *
   * @param {boolean} [bool]
   *        - true to turn native controls on
   *        - false to turn native controls off
   *
   * @return {boolean}
   *         The current value of native controls when getting
   */Player.prototype.usingNativeControls=function usingNativeControls(bool){if(bool===undefined){return !!this.usingNativeControls_;}bool=!!bool;// Don't trigger a change event unless it actually changed
if(this.usingNativeControls_===bool){return;}this.usingNativeControls_=bool;if(this.usingNativeControls_){this.addClass('vjs-using-native-controls');/**
       * player is using the native device controls
       *
       * @event Player#usingnativecontrols
       * @type {EventTarget~Event}
       */this.trigger('usingnativecontrols');}else {this.removeClass('vjs-using-native-controls');/**
       * player is using the custom HTML controls
       *
       * @event Player#usingcustomcontrols
       * @type {EventTarget~Event}
       */this.trigger('usingcustomcontrols');}};/**
   * Set or get the current MediaError
   *
   * @fires Player#error
   *
   * @param  {MediaError|string|number} [err]
   *         A MediaError or a string/number to be turned
   *         into a MediaError
   *
   * @return {MediaError|null}
   *         The current MediaError when getting (or null)
   */Player.prototype.error=function error(err){if(err===undefined){return this.error_||null;}// restoring to default
if(err===null){this.error_=err;this.removeClass('vjs-error');if(this.errorDisplay){this.errorDisplay.close();}return;}this.error_=new MediaError(err);// add the vjs-error classname to the player
this.addClass('vjs-error');// log the name of the error type and any message
// ie8 just logs "[object object]" if you just log the error object
log$1.error('(CODE:'+this.error_.code+' '+MediaError.errorTypes[this.error_.code]+')',this.error_.message,this.error_);/**
     * @event Player#error
     * @type {EventTarget~Event}
     */this.trigger('error');return;};/**
   * Report user activity
   *
   * @param {Object} event
   *        Event object
   */Player.prototype.reportUserActivity=function reportUserActivity(event){this.userActivity_=true;};/**
   * Get/set if user is active
   *
   * @fires Player#useractive
   * @fires Player#userinactive
   *
   * @param {boolean} [bool]
   *        - true if the user is active
   *        - false if the user is inactive
   *
   * @return {boolean}
   *         The current value of userActive when getting
   */Player.prototype.userActive=function userActive(bool){if(bool===undefined){return this.userActive_;}bool=!!bool;if(bool===this.userActive_){return;}this.userActive_=bool;if(this.userActive_){this.userActivity_=true;this.removeClass('vjs-user-inactive');this.addClass('vjs-user-active');/**
       * @event Player#useractive
       * @type {EventTarget~Event}
       */this.trigger('useractive');return;}// Chrome/Safari/IE have bugs where when you change the cursor it can
// trigger a mousemove event. This causes an issue when you're hiding
// the cursor when the user is inactive, and a mousemove signals user
// activity. Making it impossible to go into inactive mode. Specifically
// this happens in fullscreen when we really need to hide the cursor.
//
// When this gets resolved in ALL browsers it can be removed
// https://code.google.com/p/chromium/issues/detail?id=103041
if(this.tech_){this.tech_.one('mousemove',function(e){e.stopPropagation();e.preventDefault();});}this.userActivity_=false;this.removeClass('vjs-user-active');this.addClass('vjs-user-inactive');/**
     * @event Player#userinactive
     * @type {EventTarget~Event}
     */this.trigger('userinactive');};/**
   * Listen for user activity based on timeout value
   *
   * @private
   */Player.prototype.listenForUserActivity_=function listenForUserActivity_(){var mouseInProgress=void 0;var lastMoveX=void 0;var lastMoveY=void 0;var handleActivity=bind(this,this.reportUserActivity);var handleMouseMove=function handleMouseMove(e){// #1068 - Prevent mousemove spamming
// Chrome Bug: https://code.google.com/p/chromium/issues/detail?id=366970
if(e.screenX!==lastMoveX||e.screenY!==lastMoveY){lastMoveX=e.screenX;lastMoveY=e.screenY;handleActivity();}};var handleMouseDown=function handleMouseDown(){handleActivity();// For as long as the they are touching the device or have their mouse down,
// we consider them active even if they're not moving their finger or mouse.
// So we want to continue to update that they are active
this.clearInterval(mouseInProgress);// Setting userActivity=true now and setting the interval to the same time
// as the activityCheck interval (250) should ensure we never miss the
// next activityCheck
mouseInProgress=this.setInterval(handleActivity,250);};var handleMouseUp=function handleMouseUp(event){handleActivity();// Stop the interval that maintains activity if the mouse/touch is down
this.clearInterval(mouseInProgress);};// Any mouse movement will be considered user activity
this.on('mousedown',handleMouseDown);this.on('mousemove',handleMouseMove);this.on('mouseup',handleMouseUp);// Listen for keyboard navigation
// Shouldn't need to use inProgress interval because of key repeat
this.on('keydown',handleActivity);this.on('keyup',handleActivity);// Run an interval every 250 milliseconds instead of stuffing everything into
// the mousemove/touchmove function itself, to prevent performance degradation.
// `this.reportUserActivity` simply sets this.userActivity_ to true, which
// then gets picked up by this loop
// http://ejohn.org/blog/learning-from-twitter/
var inactivityTimeout=void 0;this.setInterval(function(){// Check to see if mouse/touch activity has happened
if(!this.userActivity_){return;}// Reset the activity tracker
this.userActivity_=false;// If the user state was inactive, set the state to active
this.userActive(true);// Clear any existing inactivity timeout to start the timer over
this.clearTimeout(inactivityTimeout);var timeout=this.options_.inactivityTimeout;if(timeout<=0){return;}// In <timeout> milliseconds, if no more activity has occurred the
// user will be considered inactive
inactivityTimeout=this.setTimeout(function(){// Protect against the case where the inactivityTimeout can trigger just
// before the next user activity is picked up by the activity check loop
// causing a flicker
if(!this.userActivity_){this.userActive(false);}},timeout);},250);};/**
   * Gets or sets the current playback rate. A playback rate of
   * 1.0 represents normal speed and 0.5 would indicate half-speed
   * playback, for instance.
   *
   * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-media-playbackrate
   *
   * @param {number} [rate]
   *       New playback rate to set.
   *
   * @return {number}
   *         The current playback rate when getting or 1.0
   */Player.prototype.playbackRate=function playbackRate(rate){if(rate!==undefined){// NOTE: this.cache_.lastPlaybackRate is set from the tech handler
// that is registered above
this.techCall_('setPlaybackRate',rate);return;}if(this.tech_&&this.tech_.featuresPlaybackRate){return this.cache_.lastPlaybackRate||this.techGet_('playbackRate');}return 1.0;};/**
   * Gets or sets the current default playback rate. A default playback rate of
   * 1.0 represents normal speed and 0.5 would indicate half-speed playback, for instance.
   * defaultPlaybackRate will only represent what the intial playbackRate of a video was, not
   * not the current playbackRate.
   *
   * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-media-defaultplaybackrate
   *
   * @param {number} [rate]
   *       New default playback rate to set.
   *
   * @return {number|Player}
   *         - The default playback rate when getting or 1.0
   *         - the player when setting
   */Player.prototype.defaultPlaybackRate=function defaultPlaybackRate(rate){if(rate!==undefined){return this.techCall_('setDefaultPlaybackRate',rate);}if(this.tech_&&this.tech_.featuresPlaybackRate){return this.techGet_('defaultPlaybackRate');}return 1.0;};/**
   * Gets or sets the audio flag
   *
   * @param {boolean} bool
   *        - true signals that this is an audio player
   *        - false signals that this is not an audio player
   *
   * @return {boolean}
   *         The current value of isAudio when getting
   */Player.prototype.isAudio=function isAudio(bool){if(bool!==undefined){this.isAudio_=!!bool;return;}return !!this.isAudio_;};/**
   * A helper method for adding a {@link TextTrack} to our
   * {@link TextTrackList}.
   *
   * In addition to the W3C settings we allow adding additional info through options.
   *
   * @see http://www.w3.org/html/wg/drafts/html/master/embedded-content-0.html#dom-media-addtexttrack
   *
   * @param {string} [kind]
   *        the kind of TextTrack you are adding
   *
   * @param {string} [label]
   *        the label to give the TextTrack label
   *
   * @param {string} [language]
   *        the language to set on the TextTrack
   *
   * @return {TextTrack|undefined}
   *         the TextTrack that was added or undefined
   *         if there is no tech
   */Player.prototype.addTextTrack=function addTextTrack(kind,label,language){if(this.tech_){return this.tech_.addTextTrack(kind,label,language);}};/**
   * Create a remote {@link TextTrack} and an {@link HTMLTrackElement}. It will
   * automatically removed from the video element whenever the source changes, unless
   * manualCleanup is set to false.
   *
   * @param {Object} options
   *        Options to pass to {@link HTMLTrackElement} during creation. See
   *        {@link HTMLTrackElement} for object properties that you should use.
   *
   * @param {boolean} [manualCleanup=true] if set to false, the TextTrack will be
   *
   * @return {HtmlTrackElement}
   *         the HTMLTrackElement that was created and added
   *         to the HtmlTrackElementList and the remote
   *         TextTrackList
   *
   * @deprecated The default value of the "manualCleanup" parameter will default
   *             to "false" in upcoming versions of Video.js
   */Player.prototype.addRemoteTextTrack=function addRemoteTextTrack(options,manualCleanup){if(this.tech_){return this.tech_.addRemoteTextTrack(options,manualCleanup);}};/**
   * Remove a remote {@link TextTrack} from the respective
   * {@link TextTrackList} and {@link HtmlTrackElementList}.
   *
   * @param {Object} track
   *        Remote {@link TextTrack} to remove
   *
   * @return {undefined}
   *         does not return anything
   */Player.prototype.removeRemoteTextTrack=function removeRemoteTextTrack(){var _ref3=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{},_ref3$track=_ref3.track,track=_ref3$track===undefined?arguments[0]:_ref3$track;// destructure the input into an object with a track argument, defaulting to arguments[0]
// default the whole argument to an empty object if nothing was passed in
if(this.tech_){return this.tech_.removeRemoteTextTrack(track);}};/**
   * Gets available media playback quality metrics as specified by the W3C's Media
   * Playback Quality API.
   *
   * @see [Spec]{@link https://wicg.github.io/media-playback-quality}
   *
   * @return {Object|undefined}
   *         An object with supported media playback quality metrics or undefined if there
   *         is no tech or the tech does not support it.
   */Player.prototype.getVideoPlaybackQuality=function getVideoPlaybackQuality(){return this.techGet_('getVideoPlaybackQuality');};/**
   * Get video width
   *
   * @return {number}
   *         current video width
   */Player.prototype.videoWidth=function videoWidth(){return this.tech_&&this.tech_.videoWidth&&this.tech_.videoWidth()||0;};/**
   * Get video height
   *
   * @return {number}
   *         current video height
   */Player.prototype.videoHeight=function videoHeight(){return this.tech_&&this.tech_.videoHeight&&this.tech_.videoHeight()||0;};/**
   * The player's language code
   * NOTE: The language should be set in the player options if you want the
   * the controls to be built with a specific language. Changing the lanugage
   * later will not update controls text.
   *
   * @param {string} [code]
   *        the language code to set the player to
   *
   * @return {string}
   *         The current language code when getting
   */Player.prototype.language=function language(code){if(code===undefined){return this.language_;}this.language_=String(code).toLowerCase();};/**
   * Get the player's language dictionary
   * Merge every time, because a newly added plugin might call videojs.addLanguage() at any time
   * Languages specified directly in the player options have precedence
   *
   * @return {Array}
   *         An array of of supported languages
   */Player.prototype.languages=function languages(){return mergeOptions(Player.prototype.options_.languages,this.languages_);};/**
   * returns a JavaScript object reperesenting the current track
   * information. **DOES not return it as JSON**
   *
   * @return {Object}
   *         Object representing the current of track info
   */Player.prototype.toJSON=function toJSON(){var options=mergeOptions(this.options_);var tracks=options.tracks;options.tracks=[];for(var i=0;i<tracks.length;i++){var track=tracks[i];// deep merge tracks and null out player so no circular references
track=mergeOptions(track);track.player=undefined;options.tracks[i]=track;}return options;};/**
   * Creates a simple modal dialog (an instance of the {@link ModalDialog}
   * component) that immediately overlays the player with arbitrary
   * content and removes itself when closed.
   *
   * @param {string|Function|Element|Array|null} content
   *        Same as {@link ModalDialog#content}'s param of the same name.
   *        The most straight-forward usage is to provide a string or DOM
   *        element.
   *
   * @param {Object} [options]
   *        Extra options which will be passed on to the {@link ModalDialog}.
   *
   * @return {ModalDialog}
   *         the {@link ModalDialog} that was created
   */Player.prototype.createModal=function createModal(content,options){var _this12=this;options=options||{};options.content=content||'';var modal=new ModalDialog(this,options);this.addChild(modal);modal.on('dispose',function(){_this12.removeChild(modal);});modal.open();return modal;};/**
   * Change breakpoint classes when the player resizes.
   *
   * @private
   */Player.prototype.updateCurrentBreakpoint_=function updateCurrentBreakpoint_(){if(!this.responsive()){return;}var currentBreakpoint=this.currentBreakpoint();var currentWidth=this.currentWidth();for(var i=0;i<BREAKPOINT_ORDER.length;i++){var candidateBreakpoint=BREAKPOINT_ORDER[i];var maxWidth=this.breakpoints_[candidateBreakpoint];if(currentWidth<=maxWidth){// The current breakpoint did not change, nothing to do.
if(currentBreakpoint===candidateBreakpoint){return;}// Only remove a class if there is a current breakpoint.
if(currentBreakpoint){this.removeClass(BREAKPOINT_CLASSES[currentBreakpoint]);}this.addClass(BREAKPOINT_CLASSES[candidateBreakpoint]);this.breakpoint_=candidateBreakpoint;break;}}};/**
   * Removes the current breakpoint.
   *
   * @private
   */Player.prototype.removeCurrentBreakpoint_=function removeCurrentBreakpoint_(){var className=this.currentBreakpointClass();this.breakpoint_='';if(className){this.removeClass(className);}};/**
   * Get or set breakpoints on the player.
   *
   * Calling this method with an object or `true` will remove any previous
   * custom breakpoints and start from the defaults again.
   *
   * @param  {Object|boolean} [breakpoints]
   *         If an object is given, it can be used to provide custom
   *         breakpoints. If `true` is given, will set default breakpoints.
   *         If this argument is not given, will simply return the current
   *         breakpoints.
   *
   * @param  {number} [breakpoints.tiny]
   *         The maximum width for the "vjs-layout-tiny" class.
   *
   * @param  {number} [breakpoints.xsmall]
   *         The maximum width for the "vjs-layout-x-small" class.
   *
   * @param  {number} [breakpoints.small]
   *         The maximum width for the "vjs-layout-small" class.
   *
   * @param  {number} [breakpoints.medium]
   *         The maximum width for the "vjs-layout-medium" class.
   *
   * @param  {number} [breakpoints.large]
   *         The maximum width for the "vjs-layout-large" class.
   *
   * @param  {number} [breakpoints.xlarge]
   *         The maximum width for the "vjs-layout-x-large" class.
   *
   * @param  {number} [breakpoints.huge]
   *         The maximum width for the "vjs-layout-huge" class.
   *
   * @return {Object}
   *         An object mapping breakpoint names to maximum width values.
   */Player.prototype.breakpoints=function breakpoints(_breakpoints){// Used as a getter.
if(_breakpoints===undefined){return assign$1(this.breakpoints_);}this.breakpoint_='';this.breakpoints_=assign$1({},DEFAULT_BREAKPOINTS,_breakpoints);// When breakpoint definitions change, we need to update the currently
// selected breakpoint.
this.updateCurrentBreakpoint_();// Clone the breakpoints before returning.
return assign$1(this.breakpoints_);};/**
   * Get or set a flag indicating whether or not this player should adjust
   * its UI based on its dimensions.
   *
   * @param  {boolean} value
   *         Should be `true` if the player should adjust its UI based on its
   *         dimensions; otherwise, should be `false`.
   *
   * @return {boolean}
   *         Will be `true` if this player should adjust its UI based on its
   *         dimensions; otherwise, will be `false`.
   */Player.prototype.responsive=function responsive(value){// Used as a getter.
if(value===undefined){return this.responsive_;}value=Boolean(value);var current=this.responsive_;// Nothing changed.
if(value===current){return;}// The value actually changed, set it.
this.responsive_=value;// Start listening for breakpoints and set the initial breakpoint if the
// player is now responsive.
if(value){this.on('playerresize',this.updateCurrentBreakpoint_);this.updateCurrentBreakpoint_();// Stop listening for breakpoints if the player is no longer responsive.
}else {this.off('playerresize',this.updateCurrentBreakpoint_);this.removeCurrentBreakpoint_();}return value;};/**
   * Get current breakpoint name, if any.
   *
   * @return {string}
   *         If there is currently a breakpoint set, returns a the key from the
   *         breakpoints object matching it. Otherwise, returns an empty string.
   */Player.prototype.currentBreakpoint=function currentBreakpoint(){return this.breakpoint_;};/**
   * Get the current breakpoint class name.
   *
   * @return {string}
   *         The matching class name (e.g. `"vjs-layout-tiny"` or
   *         `"vjs-layout-large"`) for the current breakpoint. Empty string if
   *         there is no current breakpoint.
   */Player.prototype.currentBreakpointClass=function currentBreakpointClass(){return BREAKPOINT_CLASSES[this.breakpoint_]||'';};/**
   * Gets tag settings
   *
   * @param {Element} tag
   *        The player tag
   *
   * @return {Object}
   *         An object containing all of the settings
   *         for a player tag
   */Player.getTagSettings=function getTagSettings(tag){var baseOptions={sources:[],tracks:[]};var tagOptions=getAttributes(tag);var dataSetup=tagOptions['data-setup'];if(hasClass(tag,'vjs-fill')){tagOptions.fill=true;}if(hasClass(tag,'vjs-fluid')){tagOptions.fluid=true;}// Check if data-setup attr exists.
if(dataSetup!==null){// Parse options JSON
// If empty string, make it a parsable json object.
var _safeParseTuple=safeParseTuple(dataSetup||'{}'),err=_safeParseTuple[0],data=_safeParseTuple[1];if(err){log$1.error(err);}assign$1(tagOptions,data);}assign$1(baseOptions,tagOptions);// Get tag children settings
if(tag.hasChildNodes()){var children=tag.childNodes;for(var i=0,j=children.length;i<j;i++){var child=children[i];// Change case needed: http://ejohn.org/blog/nodename-case-sensitivity/
var childName=child.nodeName.toLowerCase();if(childName==='source'){baseOptions.sources.push(getAttributes(child));}else if(childName==='track'){baseOptions.tracks.push(getAttributes(child));}}}return baseOptions;};/**
   * Determine wether or not flexbox is supported
   *
   * @return {boolean}
   *         - true if flexbox is supported
   *         - false if flexbox is not supported
   */Player.prototype.flexNotSupported_=function flexNotSupported_(){var elem=document$1.createElement('i');// Note: We don't actually use flexBasis (or flexOrder), but it's one of the more
// common flex features that we can rely on when checking for flex support.
return !('flexBasis'in elem.style||'webkitFlexBasis'in elem.style||'mozFlexBasis'in elem.style||'msFlexBasis'in elem.style||// IE10-specific (2012 flex spec)
'msFlexOrder'in elem.style);};return Player;}(Component);/**
 * Get the {@link VideoTrackList}
 * @link https://html.spec.whatwg.org/multipage/embedded-content.html#videotracklist
 *
 * @return {VideoTrackList}
 *         the current video track list
 *
 * @method Player.prototype.videoTracks
 */ /**
 * Get the {@link AudioTrackList}
 * @link https://html.spec.whatwg.org/multipage/embedded-content.html#audiotracklist
 *
 * @return {AudioTrackList}
 *         the current audio track list
 *
 * @method Player.prototype.audioTracks
 */ /**
 * Get the {@link TextTrackList}
 *
 * @link http://www.w3.org/html/wg/drafts/html/master/embedded-content-0.html#dom-media-texttracks
 *
 * @return {TextTrackList}
 *         the current text track list
 *
 * @method Player.prototype.textTracks
 */ /**
 * Get the remote {@link TextTrackList}
 *
 * @return {TextTrackList}
 *         The current remote text track list
 *
 * @method Player.prototype.remoteTextTracks
 */ /**
 * Get the remote {@link HtmlTrackElementList} tracks.
 *
 * @return {HtmlTrackElementList}
 *         The current remote text track element list
 *
 * @method Player.prototype.remoteTextTrackEls
 */ALL.names.forEach(function(name$$1){var props=ALL[name$$1];Player.prototype[props.getterName]=function(){if(this.tech_){return this.tech_[props.getterName]();}// if we have not yet loadTech_, we create {video,audio,text}Tracks_
// these will be passed to the tech during loading
this[props.privateName]=this[props.privateName]||new props.ListClass();return this[props.privateName];};});/**
 * Global player list
 *
 * @type {Object}
 */Player.players={};var navigator$1=window$1.navigator;/*
 * Player instance options, surfaced using options
 * options = Player.prototype.options_
 * Make changes in options, not here.
 *
 * @type {Object}
 * @private
 */Player.prototype.options_={// Default order of fallback technology
techOrder:Tech.defaultTechOrder_,html5:{},flash:{},// default inactivity timeout
inactivityTimeout:2000,// default playback rates
playbackRates:[],// Add playback rate selection by adding rates
// 'playbackRates': [0.5, 1, 1.5, 2],
// Included control sets
children:['mediaLoader','posterImage','textTrackDisplay','loadingSpinner','bigPlayButton','controlBar','errorDisplay','textTrackSettings'],language:navigator$1&&(navigator$1.languages&&navigator$1.languages[0]||navigator$1.userLanguage||navigator$1.language)||'en',// locales and their language translations
languages:{},// Default message to show when a video cannot be played.
notSupportedMessage:'No compatible source was found for this media.',breakpoints:{},responsive:false};if(!IS_IE8){Player.prototype.options_.children.push('resizeManager');}[/**
 * Returns whether or not the player is in the "ended" state.
 *
 * @return {Boolean} True if the player is in the ended state, false if not.
 * @method Player#ended
 */'ended',/**
 * Returns whether or not the player is in the "seeking" state.
 *
 * @return {Boolean} True if the player is in the seeking state, false if not.
 * @method Player#seeking
 */'seeking',/**
 * Returns the TimeRanges of the media that are currently available
 * for seeking to.
 *
 * @return {TimeRanges} the seekable intervals of the media timeline
 * @method Player#seekable
 */'seekable',/**
 * Returns the current state of network activity for the element, from
 * the codes in the list below.
 * - NETWORK_EMPTY (numeric value 0)
 *   The element has not yet been initialised. All attributes are in
 *   their initial states.
 * - NETWORK_IDLE (numeric value 1)
 *   The element's resource selection algorithm is active and has
 *   selected a resource, but it is not actually using the network at
 *   this time.
 * - NETWORK_LOADING (numeric value 2)
 *   The user agent is actively trying to download data.
 * - NETWORK_NO_SOURCE (numeric value 3)
 *   The element's resource selection algorithm is active, but it has
 *   not yet found a resource to use.
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#network-states
 * @return {number} the current network activity state
 * @method Player#networkState
 */'networkState',/**
 * Returns a value that expresses the current state of the element
 * with respect to rendering the current playback position, from the
 * codes in the list below.
 * - HAVE_NOTHING (numeric value 0)
 *   No information regarding the media resource is available.
 * - HAVE_METADATA (numeric value 1)
 *   Enough of the resource has been obtained that the duration of the
 *   resource is available.
 * - HAVE_CURRENT_DATA (numeric value 2)
 *   Data for the immediate current playback position is available.
 * - HAVE_FUTURE_DATA (numeric value 3)
 *   Data for the immediate current playback position is available, as
 *   well as enough data for the user agent to advance the current
 *   playback position in the direction of playback.
 * - HAVE_ENOUGH_DATA (numeric value 4)
 *   The user agent estimates that enough data is available for
 *   playback to proceed uninterrupted.
 *
 * @see https://html.spec.whatwg.org/multipage/embedded-content.html#dom-media-readystate
 * @return {number} the current playback rendering state
 * @method Player#readyState
 */'readyState'].forEach(function(fn){Player.prototype[fn]=function(){return this.techGet_(fn);};});TECH_EVENTS_RETRIGGER.forEach(function(event){Player.prototype['handleTech'+toTitleCase(event)+'_']=function(){return this.trigger(event);};});/**
 * Fired when the player has initial duration and dimension information
 *
 * @event Player#loadedmetadata
 * @type {EventTarget~Event}
 */ /**
 * Fired when the player has downloaded data at the current playback position
 *
 * @event Player#loadeddata
 * @type {EventTarget~Event}
 */ /**
 * Fired when the current playback position has changed *
 * During playback this is fired every 15-250 milliseconds, depending on the
 * playback technology in use.
 *
 * @event Player#timeupdate
 * @type {EventTarget~Event}
 */ /**
 * Fired when the volume changes
 *
 * @event Player#volumechange
 * @type {EventTarget~Event}
 */ /**
 * Reports whether or not a player has a plugin available.
 *
 * This does not report whether or not the plugin has ever been initialized
 * on this player. For that, [usingPlugin]{@link Player#usingPlugin}.
 *
 * @method Player#hasPlugin
 * @param  {string}  name
 *         The name of a plugin.
 *
 * @return {boolean}
 *         Whether or not this player has the requested plugin available.
 */ /**
 * Reports whether or not a player is using a plugin by name.
 *
 * For basic plugins, this only reports whether the plugin has _ever_ been
 * initialized on this player.
 *
 * @method Player#usingPlugin
 * @param  {string} name
 *         The name of a plugin.
 *
 * @return {boolean}
 *         Whether or not this player is using the requested plugin.
 */Component.registerComponent('Player',Player);/**
 * @file plugin.js
 */ /**
 * The base plugin name.
 *
 * @private
 * @constant
 * @type {string}
 */var BASE_PLUGIN_NAME='plugin';/**
 * The key on which a player's active plugins cache is stored.
 *
 * @private
 * @constant
 * @type     {string}
 */var PLUGIN_CACHE_KEY='activePlugins_';/**
 * Stores registered plugins in a private space.
 *
 * @private
 * @type    {Object}
 */var pluginStorage={};/**
 * Reports whether or not a plugin has been registered.
 *
 * @private
 * @param   {string} name
 *          The name of a plugin.
 *
 * @returns {boolean}
 *          Whether or not the plugin has been registered.
 */var pluginExists=function pluginExists(name){return pluginStorage.hasOwnProperty(name);};/**
 * Get a single registered plugin by name.
 *
 * @private
 * @param   {string} name
 *          The name of a plugin.
 *
 * @returns {Function|undefined}
 *          The plugin (or undefined).
 */var getPlugin=function getPlugin(name){return pluginExists(name)?pluginStorage[name]:undefined;};/**
 * Marks a plugin as "active" on a player.
 *
 * Also, ensures that the player has an object for tracking active plugins.
 *
 * @private
 * @param   {Player} player
 *          A Video.js player instance.
 *
 * @param   {string} name
 *          The name of a plugin.
 */var markPluginAsActive=function markPluginAsActive(player,name){player[PLUGIN_CACHE_KEY]=player[PLUGIN_CACHE_KEY]||{};player[PLUGIN_CACHE_KEY][name]=true;};/**
 * Triggers a pair of plugin setup events.
 *
 * @private
 * @param  {Player} player
 *         A Video.js player instance.
 *
 * @param  {Plugin~PluginEventHash} hash
 *         A plugin event hash.
 *
 * @param  {Boolean} [before]
 *         If true, prefixes the event name with "before". In other words,
 *         use this to trigger "beforepluginsetup" instead of "pluginsetup".
 */var triggerSetupEvent=function triggerSetupEvent(player,hash,before){var eventName=(before?'before':'')+'pluginsetup';player.trigger(eventName,hash);player.trigger(eventName+':'+hash.name,hash);};/**
 * Takes a basic plugin function and returns a wrapper function which marks
 * on the player that the plugin has been activated.
 *
 * @private
 * @param   {string} name
 *          The name of the plugin.
 *
 * @param   {Function} plugin
 *          The basic plugin.
 *
 * @returns {Function}
 *          A wrapper function for the given plugin.
 */var createBasicPlugin=function createBasicPlugin(name,plugin){var basicPluginWrapper=function basicPluginWrapper(){// We trigger the "beforepluginsetup" and "pluginsetup" events on the player
// regardless, but we want the hash to be consistent with the hash provided
// for advanced plugins.
//
// The only potentially counter-intuitive thing here is the `instance` in
// the "pluginsetup" event is the value returned by the `plugin` function.
triggerSetupEvent(this,{name:name,plugin:plugin,instance:null},true);var instance=plugin.apply(this,arguments);markPluginAsActive(this,name);triggerSetupEvent(this,{name:name,plugin:plugin,instance:instance});return instance;};Object.keys(plugin).forEach(function(prop){basicPluginWrapper[prop]=plugin[prop];});return basicPluginWrapper;};/**
 * Takes a plugin sub-class and returns a factory function for generating
 * instances of it.
 *
 * This factory function will replace itself with an instance of the requested
 * sub-class of Plugin.
 *
 * @private
 * @param   {string} name
 *          The name of the plugin.
 *
 * @param   {Plugin} PluginSubClass
 *          The advanced plugin.
 *
 * @returns {Function}
 */var createPluginFactory=function createPluginFactory(name,PluginSubClass){// Add a `name` property to the plugin prototype so that each plugin can
// refer to itself by name.
PluginSubClass.prototype.name=name;return function(){triggerSetupEvent(this,{name:name,plugin:PluginSubClass,instance:null},true);for(var _len=arguments.length,args=Array(_len),_key=0;_key<_len;_key++){args[_key]=arguments[_key];}var instance=new(Function.prototype.bind.apply(PluginSubClass,[null].concat([this].concat(args))))();// The plugin is replaced by a function that returns the current instance.
this[name]=function(){return instance;};triggerSetupEvent(this,instance.getEventHash());return instance;};};/**
 * Parent class for all advanced plugins.
 *
 * @mixes   module:evented~EventedMixin
 * @mixes   module:stateful~StatefulMixin
 * @fires   Player#beforepluginsetup
 * @fires   Player#beforepluginsetup:$name
 * @fires   Player#pluginsetup
 * @fires   Player#pluginsetup:$name
 * @listens Player#dispose
 * @throws  {Error}
 *          If attempting to instantiate the base {@link Plugin} class
 *          directly instead of via a sub-class.
 */var Plugin=function(){/**
   * Creates an instance of this class.
   *
   * Sub-classes should call `super` to ensure plugins are properly initialized.
   *
   * @param {Player} player
   *        A Video.js player instance.
   */function Plugin(player){classCallCheck(this,Plugin);if(this.constructor===Plugin){throw new Error('Plugin must be sub-classed; not directly instantiated.');}this.player=player;// Make this object evented, but remove the added `trigger` method so we
// use the prototype version instead.
evented(this);delete this.trigger;stateful(this,this.constructor.defaultState);markPluginAsActive(player,this.name);// Auto-bind the dispose method so we can use it as a listener and unbind
// it later easily.
this.dispose=bind(this,this.dispose);// If the player is disposed, dispose the plugin.
player.on('dispose',this.dispose);}/**
   * Get the version of the plugin that was set on <pluginName>.VERSION
   */Plugin.prototype.version=function version(){return this.constructor.VERSION;};/**
   * Each event triggered by plugins includes a hash of additional data with
   * conventional properties.
   *
   * This returns that object or mutates an existing hash.
   *
   * @param   {Object} [hash={}]
   *          An object to be used as event an event hash.
   *
   * @returns {Plugin~PluginEventHash}
   *          An event hash object with provided properties mixed-in.
   */Plugin.prototype.getEventHash=function getEventHash(){var hash=arguments.length>0&&arguments[0]!==undefined?arguments[0]:{};hash.name=this.name;hash.plugin=this.constructor;hash.instance=this;return hash;};/**
   * Triggers an event on the plugin object and overrides
   * {@link module:evented~EventedMixin.trigger|EventedMixin.trigger}.
   *
   * @param   {string|Object} event
   *          An event type or an object with a type property.
   *
   * @param   {Object} [hash={}]
   *          Additional data hash to merge with a
   *          {@link Plugin~PluginEventHash|PluginEventHash}.
   *
   * @returns {boolean}
   *          Whether or not default was prevented.
   */Plugin.prototype.trigger=function trigger$$1(event){var hash=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};return trigger(this.eventBusEl_,event,this.getEventHash(hash));};/**
   * Handles "statechanged" events on the plugin. No-op by default, override by
   * subclassing.
   *
   * @abstract
   * @param    {Event} e
   *           An event object provided by a "statechanged" event.
   *
   * @param    {Object} e.changes
   *           An object describing changes that occurred with the "statechanged"
   *           event.
   */Plugin.prototype.handleStateChanged=function handleStateChanged(e){};/**
   * Disposes a plugin.
   *
   * Subclasses can override this if they want, but for the sake of safety,
   * it's probably best to subscribe the "dispose" event.
   *
   * @fires Plugin#dispose
   */Plugin.prototype.dispose=function dispose(){var name=this.name,player=this.player;/**
     * Signals that a advanced plugin is about to be disposed.
     *
     * @event Plugin#dispose
     * @type  {EventTarget~Event}
     */this.trigger('dispose');this.off();player.off('dispose',this.dispose);// Eliminate any possible sources of leaking memory by clearing up
// references between the player and the plugin instance and nulling out
// the plugin's state and replacing methods with a function that throws.
player[PLUGIN_CACHE_KEY][name]=false;this.player=this.state=null;// Finally, replace the plugin name on the player with a new factory
// function, so that the plugin is ready to be set up again.
player[name]=createPluginFactory(name,pluginStorage[name]);};/**
   * Determines if a plugin is a basic plugin (i.e. not a sub-class of `Plugin`).
   *
   * @param   {string|Function} plugin
   *          If a string, matches the name of a plugin. If a function, will be
   *          tested directly.
   *
   * @returns {boolean}
   *          Whether or not a plugin is a basic plugin.
   */Plugin.isBasic=function isBasic(plugin){var p=typeof plugin==='string'?getPlugin(plugin):plugin;return typeof p==='function'&&!Plugin.prototype.isPrototypeOf(p.prototype);};/**
   * Register a Video.js plugin.
   *
   * @param   {string} name
   *          The name of the plugin to be registered. Must be a string and
   *          must not match an existing plugin or a method on the `Player`
   *          prototype.
   *
   * @param   {Function} plugin
   *          A sub-class of `Plugin` or a function for basic plugins.
   *
   * @returns {Function}
   *          For advanced plugins, a factory function for that plugin. For
   *          basic plugins, a wrapper function that initializes the plugin.
   */Plugin.registerPlugin=function registerPlugin(name,plugin){if(typeof name!=='string'){throw new Error('Illegal plugin name, "'+name+'", must be a string, was '+(typeof name==='undefined'?'undefined':_typeof$2(name))+'.');}if(pluginExists(name)){log$1.warn('A plugin named "'+name+'" already exists. You may want to avoid re-registering plugins!');}else if(Player.prototype.hasOwnProperty(name)){throw new Error('Illegal plugin name, "'+name+'", cannot share a name with an existing player method!');}if(typeof plugin!=='function'){throw new Error('Illegal plugin for "'+name+'", must be a function, was '+(typeof plugin==='undefined'?'undefined':_typeof$2(plugin))+'.');}pluginStorage[name]=plugin;// Add a player prototype method for all sub-classed plugins (but not for
// the base Plugin class).
if(name!==BASE_PLUGIN_NAME){if(Plugin.isBasic(plugin)){Player.prototype[name]=createBasicPlugin(name,plugin);}else {Player.prototype[name]=createPluginFactory(name,plugin);}}return plugin;};/**
   * De-register a Video.js plugin.
   *
   * @param {string} name
   *        The name of the plugin to be deregistered.
   */Plugin.deregisterPlugin=function deregisterPlugin(name){if(name===BASE_PLUGIN_NAME){throw new Error('Cannot de-register base plugin.');}if(pluginExists(name)){delete pluginStorage[name];delete Player.prototype[name];}};/**
   * Gets an object containing multiple Video.js plugins.
   *
   * @param   {Array} [names]
   *          If provided, should be an array of plugin names. Defaults to _all_
   *          plugin names.
   *
   * @returns {Object|undefined}
   *          An object containing plugin(s) associated with their name(s) or
   *          `undefined` if no matching plugins exist).
   */Plugin.getPlugins=function getPlugins(){var names=arguments.length>0&&arguments[0]!==undefined?arguments[0]:Object.keys(pluginStorage);var result=void 0;names.forEach(function(name){var plugin=getPlugin(name);if(plugin){result=result||{};result[name]=plugin;}});return result;};/**
   * Gets a plugin's version, if available
   *
   * @param   {string} name
   *          The name of a plugin.
   *
   * @returns {string}
   *          The plugin's version or an empty string.
   */Plugin.getPluginVersion=function getPluginVersion(name){var plugin=getPlugin(name);return plugin&&plugin.VERSION||'';};return Plugin;}();/**
 * Gets a plugin by name if it exists.
 *
 * @static
 * @method   getPlugin
 * @memberOf Plugin
 * @param    {string} name
 *           The name of a plugin.
 *
 * @returns  {Function|undefined}
 *           The plugin (or `undefined`).
 */Plugin.getPlugin=getPlugin;/**
 * The name of the base plugin class as it is registered.
 *
 * @type {string}
 */Plugin.BASE_PLUGIN_NAME=BASE_PLUGIN_NAME;Plugin.registerPlugin(BASE_PLUGIN_NAME,Plugin);/**
 * Documented in player.js
 *
 * @ignore
 */Player.prototype.usingPlugin=function(name){return !!this[PLUGIN_CACHE_KEY]&&this[PLUGIN_CACHE_KEY][name]===true;};/**
 * Documented in player.js
 *
 * @ignore
 */Player.prototype.hasPlugin=function(name){return !!pluginExists(name);};/**
 * Signals that a plugin is about to be set up on a player.
 *
 * @event    Player#beforepluginsetup
 * @type     {Plugin~PluginEventHash}
 */ /**
 * Signals that a plugin is about to be set up on a player - by name. The name
 * is the name of the plugin.
 *
 * @event    Player#beforepluginsetup:$name
 * @type     {Plugin~PluginEventHash}
 */ /**
 * Signals that a plugin has just been set up on a player.
 *
 * @event    Player#pluginsetup
 * @type     {Plugin~PluginEventHash}
 */ /**
 * Signals that a plugin has just been set up on a player - by name. The name
 * is the name of the plugin.
 *
 * @event    Player#pluginsetup:$name
 * @type     {Plugin~PluginEventHash}
 */ /**
 * @typedef  {Object} Plugin~PluginEventHash
 *
 * @property {string} instance
 *           For basic plugins, the return value of the plugin function. For
 *           advanced plugins, the plugin instance on which the event is fired.
 *
 * @property {string} name
 *           The name of the plugin.
 *
 * @property {string} plugin
 *           For basic plugins, the plugin function. For advanced plugins, the
 *           plugin class/constructor.
 */ /**
 * @file extend.js
 * @module extend
 */ /**
 * A combination of node inherits and babel's inherits (after transpile).
 * Both work the same but node adds `super_` to the subClass
 * and Bable adds the superClass as __proto__. Both seem useful.
 *
 * @param {Object} subClass
 *        The class to inherit to
 *
 * @param {Object} superClass
 *        The class to inherit from
 *
 * @private
 */var _inherits=function _inherits(subClass,superClass){if(typeof superClass!=='function'&&superClass!==null){throw new TypeError('Super expression must either be null or a function, not '+(typeof superClass==='undefined'?'undefined':_typeof$2(superClass)));}subClass.prototype=Object.create(superClass&&superClass.prototype,{constructor:{value:subClass,enumerable:false,writable:true,configurable:true}});if(superClass){// node
subClass.super_=superClass;}};/**
 * Function for subclassing using the same inheritance that
 * videojs uses internally
 *
 * @static
 * @const
 *
 * @param {Object} superClass
 *        The class to inherit from
 *
 * @param {Object} [subClassMethods={}]
 *        The class to inherit to
 *
 * @return {Object}
 *         The new object with subClassMethods that inherited superClass.
 */var extendFn=function extendFn(superClass){var subClassMethods=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};var subClass=function subClass(){superClass.apply(this,arguments);};var methods={};if((typeof subClassMethods==='undefined'?'undefined':_typeof$2(subClassMethods))==='object'){if(subClassMethods.constructor!==Object.prototype.constructor){subClass=subClassMethods.constructor;}methods=subClassMethods;}else if(typeof subClassMethods==='function'){subClass=subClassMethods;}_inherits(subClass,superClass);// Extend subObj's prototype with functions and other properties from props
for(var name in methods){if(methods.hasOwnProperty(name)){subClass.prototype[name]=methods[name];}}return subClass;};/**
 * @file video.js
 * @module videojs
 */ // Include the built-in techs
// HTML5 Element Shim for IE8
if(typeof HTMLVideoElement==='undefined'&&isReal()){document$1.createElement('video');document$1.createElement('audio');document$1.createElement('track');document$1.createElement('video-js');}/**
 * Normalize an `id` value by trimming off a leading `#`
 *
 * @param   {string} id
 *          A string, maybe with a leading `#`.
 *
 * @returns {string}
 *          The string, without any leading `#`.
 */var normalizeId=function normalizeId(id){return id.indexOf('#')===0?id.slice(1):id;};/**
 * Doubles as the main function for users to create a player instance and also
 * the main library object.
 * The `videojs` function can be used to initialize or retrieve a player.
  *
 * @param {string|Element} id
 *        Video element or video element ID
 *
 * @param {Object} [options]
 *        Optional options object for config/settings
 *
 * @param {Component~ReadyCallback} [ready]
 *        Optional ready callback
 *
 * @return {Player}
 *         A player instance
 */function videojs(id,options,ready){var player=videojs.getPlayer(id);if(player){if(options){log$1.warn('Player "'+id+'" is already initialised. Options will not be applied.');}if(ready){player.ready(ready);}return player;}var el=typeof id==='string'?$$1('#'+normalizeId(id)):id;if(!isEl(el)){throw new TypeError('The element or ID supplied is not valid. (videojs)');}if(!document$1.body.contains(el)){log$1.warn('The element supplied is not included in the DOM');}options=options||{};videojs.hooks('beforesetup').forEach(function(hookFunction){var opts=hookFunction(el,mergeOptions(options));if(!isObject(opts)||Array.isArray(opts)){log$1.error('please return an object in beforesetup hooks');return;}options=mergeOptions(options,opts);});// We get the current "Player" component here in case an integration has
// replaced it with a custom player.
var PlayerComponent=Component.getComponent('Player');player=new PlayerComponent(el,options,ready);videojs.hooks('setup').forEach(function(hookFunction){return hookFunction(player);});return player;}/**
 * An Object that contains lifecycle hooks as keys which point to an array
 * of functions that are run when a lifecycle is triggered
 */videojs.hooks_={};/**
 * Get a list of hooks for a specific lifecycle
 * @function videojs.hooks
 *
 * @param {string} type
 *        the lifecyle to get hooks from
 *
 * @param {Function|Function[]} [fn]
 *        Optionally add a hook (or hooks) to the lifecycle that your are getting.
 *
 * @return {Array}
 *         an array of hooks, or an empty array if there are none.
 */videojs.hooks=function(type,fn){videojs.hooks_[type]=videojs.hooks_[type]||[];if(fn){videojs.hooks_[type]=videojs.hooks_[type].concat(fn);}return videojs.hooks_[type];};/**
 * Add a function hook to a specific videojs lifecycle.
 *
 * @param {string} type
 *        the lifecycle to hook the function to.
 *
 * @param {Function|Function[]}
 *        The function or array of functions to attach.
 */videojs.hook=function(type,fn){videojs.hooks(type,fn);};/**
 * Add a function hook that will only run once to a specific videojs lifecycle.
 *
 * @param {string} type
 *        the lifecycle to hook the function to.
 *
 * @param {Function|Function[]}
 *        The function or array of functions to attach.
 */videojs.hookOnce=function(type,fn){videojs.hooks(type,[].concat(fn).map(function(original){var wrapper=function wrapper(){videojs.removeHook(type,wrapper);return original.apply(undefined,arguments);};return wrapper;}));};/**
 * Remove a hook from a specific videojs lifecycle.
 *
 * @param {string} type
 *        the lifecycle that the function hooked to
 *
 * @param {Function} fn
 *        The hooked function to remove
 *
 * @return {boolean}
 *         The function that was removed or undef
 */videojs.removeHook=function(type,fn){var index=videojs.hooks(type).indexOf(fn);if(index<=-1){return false;}videojs.hooks_[type]=videojs.hooks_[type].slice();videojs.hooks_[type].splice(index,1);return true;};// Add default styles
if(window$1.VIDEOJS_NO_DYNAMIC_STYLE!==true&&isReal()){var style=$$1('.vjs-styles-defaults');if(!style){style=createStyleElement('vjs-styles-defaults');var head=$$1('head');if(head){head.insertBefore(style,head.firstChild);}setTextContent(style,'\n      .video-js {\n        width: 300px;\n        height: 150px;\n      }\n\n      .vjs-fluid {\n        padding-top: 56.25%\n      }\n    ');}}// Run Auto-load players
// You have to wait at least once in case this script is loaded after your
// video in the DOM (weird behavior only with minified version)
autoSetupTimeout(1,videojs);/**
 * Current software version. Follows semver.
 *
 * @type {string}
 */videojs.VERSION=version;/**
 * The global options object. These are the settings that take effect
 * if no overrides are specified when the player is created.
 *
 * @type {Object}
 */videojs.options=Player.prototype.options_;/**
 * Get an object with the currently created players, keyed by player ID
 *
 * @return {Object}
 *         The created players
 */videojs.getPlayers=function(){return Player.players;};/**
 * Get a single player based on an ID or DOM element.
 *
 * This is useful if you want to check if an element or ID has an associated
 * Video.js player, but not create one if it doesn't.
 *
 * @param   {string|Element} id
 *          An HTML element - `<video>`, `<audio>`, or `<video-js>` -
 *          or a string matching the `id` of such an element.
 *
 * @returns {Player|undefined}
 *          A player instance or `undefined` if there is no player instance
 *          matching the argument.
 */videojs.getPlayer=function(id){var players=Player.players;var tag=void 0;if(typeof id==='string'){var nId=normalizeId(id);var player=players[nId];if(player){return player;}tag=$$1('#'+nId);}else {tag=id;}if(isEl(tag)){var _tag=tag,_player=_tag.player,playerId=_tag.playerId;// Element may have a `player` property referring to an already created
// player instance. If so, return that.
if(_player||players[playerId]){return _player||players[playerId];}}};/**
 * Returns an array of all current players.
 *
 * @return {Array}
 *         An array of all players. The array will be in the order that
 *         `Object.keys` provides, which could potentially vary between
 *         JavaScript engines.
 *
 */videojs.getAllPlayers=function(){return(// Disposed players leave a key with a `null` value, so we need to make sure
// we filter those out.
Object.keys(Player.players).map(function(k){return Player.players[k];}).filter(Boolean));};/**
 * Expose players object.
 *
 * @memberOf videojs
 * @property {Object} players
 */videojs.players=Player.players;/**
 * Get a component class object by name
 *
 * @borrows Component.getComponent as videojs.getComponent
 */videojs.getComponent=Component.getComponent;/**
 * Register a component so it can referred to by name. Used when adding to other
 * components, either through addChild `component.addChild('myComponent')` or through
 * default children options  `{ children: ['myComponent'] }`.
 *
 * > NOTE: You could also just initialize the component before adding.
 * `component.addChild(new MyComponent());`
 *
 * @param {string} name
 *        The class name of the component
 *
 * @param {Component} comp
 *        The component class
 *
 * @return {Component}
 *         The newly registered component
 */videojs.registerComponent=function(name$$1,comp){if(Tech.isTech(comp)){log$1.warn('The '+name$$1+' tech was registered as a component. It should instead be registered using videojs.registerTech(name, tech)');}Component.registerComponent.call(Component,name$$1,comp);};/**
 * Get a Tech class object by name
 *
 * @borrows Tech.getTech as videojs.getTech
 */videojs.getTech=Tech.getTech;/**
 * Register a Tech so it can referred to by name.
 * This is used in the tech order for the player.
 *
 * @borrows Tech.registerTech as videojs.registerTech
 */videojs.registerTech=Tech.registerTech;/**
 * Register a middleware to a source type.
 *
 * @param {String} type A string representing a MIME type.
 * @param {function(player):object} middleware A middleware factory that takes a player.
 */videojs.use=use;/**
 * An object that can be returned by a middleware to signify
 * that the middleware is being terminated.
 *
 * @type {object}
 * @memberOf {videojs}
 * @property {object} middleware.TERMINATOR
 */ // Object.defineProperty is not available in IE8
if(!IS_IE8&&Object.defineProperty){Object.defineProperty(videojs,'middleware',{value:{},writeable:false,enumerable:true});Object.defineProperty(videojs.middleware,'TERMINATOR',{value:TERMINATOR,writeable:false,enumerable:true});}else {videojs.middleware={TERMINATOR:TERMINATOR};}/**
 * A suite of browser and device tests from {@link browser}.
 *
 * @type {Object}
 * @private
 */videojs.browser=browser;/**
 * Whether or not the browser supports touch events. Included for backward
 * compatibility with 4.x, but deprecated. Use `videojs.browser.TOUCH_ENABLED`
 * instead going forward.
 *
 * @deprecated since version 5.0
 * @type {boolean}
 */videojs.TOUCH_ENABLED=TOUCH_ENABLED;/**
 * Subclass an existing class
 * Mimics ES6 subclassing with the `extend` keyword
 *
 * @borrows extend:extendFn as videojs.extend
 */videojs.extend=extendFn;/**
 * Merge two options objects recursively
 * Performs a deep merge like lodash.merge but **only merges plain objects**
 * (not arrays, elements, anything else)
 * Other values will be copied directly from the second object.
 *
 * @borrows merge-options:mergeOptions as videojs.mergeOptions
 */videojs.mergeOptions=mergeOptions;/**
 * Change the context (this) of a function
 *
 * > NOTE: as of v5.0 we require an ES5 shim, so you should use the native
 * `function() {}.bind(newContext);` instead of this.
 *
 * @borrows fn:bind as videojs.bind
 */videojs.bind=bind;/**
 * Register a Video.js plugin.
 *
 * @borrows plugin:registerPlugin as videojs.registerPlugin
 * @method registerPlugin
 *
 * @param  {string} name
 *         The name of the plugin to be registered. Must be a string and
 *         must not match an existing plugin or a method on the `Player`
 *         prototype.
 *
 * @param  {Function} plugin
 *         A sub-class of `Plugin` or a function for basic plugins.
 *
 * @return {Function}
 *         For advanced plugins, a factory function for that plugin. For
 *         basic plugins, a wrapper function that initializes the plugin.
 */videojs.registerPlugin=Plugin.registerPlugin;/**
 * Deregister a Video.js plugin.
 *
 * @borrows plugin:deregisterPlugin as videojs.deregisterPlugin
 * @method deregisterPlugin
 *
 * @param  {string} name
 *         The name of the plugin to be deregistered. Must be a string and
 *         must match an existing plugin or a method on the `Player`
 *         prototype.
 *
 */videojs.deregisterPlugin=Plugin.deregisterPlugin;/**
 * Deprecated method to register a plugin with Video.js
 *
 * @deprecated
 *        videojs.plugin() is deprecated; use videojs.registerPlugin() instead
 *
 * @param {string} name
 *        The plugin name
 *
 * @param {Plugin|Function} plugin
 *         The plugin sub-class or function
 */videojs.plugin=function(name$$1,plugin){log$1.warn('videojs.plugin() is deprecated; use videojs.registerPlugin() instead');return Plugin.registerPlugin(name$$1,plugin);};/**
 * Gets an object containing multiple Video.js plugins.
 *
 * @param  {Array} [names]
 *         If provided, should be an array of plugin names. Defaults to _all_
 *         plugin names.
 *
 * @return {Object|undefined}
 *         An object containing plugin(s) associated with their name(s) or
 *         `undefined` if no matching plugins exist).
 */videojs.getPlugins=Plugin.getPlugins;/**
 * Gets a plugin by name if it exists.
 *
 * @param  {string} name
 *         The name of a plugin.
 *
 * @return {Function|undefined}
 *         The plugin (or `undefined`).
 */videojs.getPlugin=Plugin.getPlugin;/**
 * Gets a plugin's version, if available
 *
 * @param  {string} name
 *         The name of a plugin.
 *
 * @return {string}
 *         The plugin's version or an empty string.
 */videojs.getPluginVersion=Plugin.getPluginVersion;/**
 * Adding languages so that they're available to all players.
 * Example: `videojs.addLanguage('es', { 'Hello': 'Hola' });`
 *
 * @param {string} code
 *        The language code or dictionary property
 *
 * @param {Object} data
 *        The data values to be translated
 *
 * @return {Object}
 *         The resulting language dictionary object
 */videojs.addLanguage=function(code,data){var _mergeOptions;code=(''+code).toLowerCase();videojs.options.languages=mergeOptions(videojs.options.languages,(_mergeOptions={},_mergeOptions[code]=data,_mergeOptions));return videojs.options.languages[code];};/**
 * Log messages
 *
 * @borrows log:log as videojs.log
 */videojs.log=log$1;videojs.createLogger=createLogger;/**
 * Creates an emulated TimeRange object.
 *
 * @borrows time-ranges:createTimeRanges as videojs.createTimeRange
 */ /**
 * @borrows time-ranges:createTimeRanges as videojs.createTimeRanges
 */videojs.createTimeRange=videojs.createTimeRanges=createTimeRanges;/**
 * Format seconds as a time string, H:MM:SS or M:SS
 * Supplying a guide (in seconds) will force a number of leading zeros
 * to cover the length of the guide
 *
 * @borrows format-time:formatTime as videojs.formatTime
 */videojs.formatTime=formatTime;/**
 * Replaces format-time with a custom implementation, to be used in place of the default.
 *
 * @borrows format-time:setFormatTime as videojs.setFormatTime
 *
 * @method setFormatTime
 *
 * @param {Function} customFn
 *        A custom format-time function which will be called with the current time and guide (in seconds) as arguments.
 *        Passed fn should return a string.
 */videojs.setFormatTime=setFormatTime;/**
 * Resets format-time to the default implementation.
 *
 * @borrows format-time:resetFormatTime as videojs.resetFormatTime
 *
 * @method resetFormatTime
 */videojs.resetFormatTime=resetFormatTime;/**
 * Resolve and parse the elements of a URL
 *
 * @borrows url:parseUrl as videojs.parseUrl
 *
 */videojs.parseUrl=parseUrl;/**
 * Returns whether the url passed is a cross domain request or not.
 *
 * @borrows url:isCrossOrigin as videojs.isCrossOrigin
 */videojs.isCrossOrigin=isCrossOrigin;/**
 * Event target class.
 *
 * @borrows EventTarget as videojs.EventTarget
 */videojs.EventTarget=EventTarget;/**
 * Add an event listener to element
 * It stores the handler function in a separate cache object
 * and adds a generic handler to the element's event,
 * along with a unique id (guid) to the element.
 *
 * @borrows events:on as videojs.on
 */videojs.on=on;/**
 * Trigger a listener only once for an event
 *
 * @borrows events:one as videojs.one
 */videojs.one=one;/**
 * Removes event listeners from an element
 *
 * @borrows events:off as videojs.off
 */videojs.off=off;/**
 * Trigger an event for an element
 *
 * @borrows events:trigger as videojs.trigger
 */videojs.trigger=trigger;/**
 * A cross-browser XMLHttpRequest wrapper. Here's a simple example:
 *
 * @param {Object} options
 *        settings for the request.
 *
 * @return {XMLHttpRequest|XDomainRequest}
 *         The request object.
 *
 * @see https://github.com/Raynos/xhr
 */videojs.xhr=xhr;/**
 * TextTrack class
 *
 * @borrows TextTrack as videojs.TextTrack
 */videojs.TextTrack=TextTrack;/**
 * export the AudioTrack class so that source handlers can create
 * AudioTracks and then add them to the players AudioTrackList
 *
 * @borrows AudioTrack as videojs.AudioTrack
 */videojs.AudioTrack=AudioTrack;/**
 * export the VideoTrack class so that source handlers can create
 * VideoTracks and then add them to the players VideoTrackList
 *
 * @borrows VideoTrack as videojs.VideoTrack
 */videojs.VideoTrack=VideoTrack;/**
 * Determines, via duck typing, whether or not a value is a DOM element.
 *
 * @borrows dom:isEl as videojs.isEl
 * @deprecated Use videojs.dom.isEl() instead
 */ /**
 * Determines, via duck typing, whether or not a value is a text node.
 *
 * @borrows dom:isTextNode as videojs.isTextNode
 * @deprecated Use videojs.dom.isTextNode() instead
 */ /**
 * Creates an element and applies properties.
 *
 * @borrows dom:createEl as videojs.createEl
 * @deprecated Use videojs.dom.createEl() instead
 */ /**
 * Check if an element has a CSS class
 *
 * @borrows dom:hasElClass as videojs.hasClass
 * @deprecated Use videojs.dom.hasClass() instead
 */ /**
 * Add a CSS class name to an element
 *
 * @borrows dom:addElClass as videojs.addClass
 * @deprecated Use videojs.dom.addClass() instead
 */ /**
 * Remove a CSS class name from an element
 *
 * @borrows dom:removeElClass as videojs.removeClass
 * @deprecated Use videojs.dom.removeClass() instead
 */ /**
 * Adds or removes a CSS class name on an element depending on an optional
 * condition or the presence/absence of the class name.
 *
 * @borrows dom:toggleElClass as videojs.toggleClass
 * @deprecated Use videojs.dom.toggleClass() instead
 */ /**
 * Apply attributes to an HTML element.
 *
 * @borrows dom:setElAttributes as videojs.setAttribute
 * @deprecated Use videojs.dom.setAttributes() instead
 */ /**
 * Get an element's attribute values, as defined on the HTML tag
 * Attributes are not the same as properties. They're defined on the tag
 * or with setAttribute (which shouldn't be used with HTML)
 * This will return true or false for boolean attributes.
 *
 * @borrows dom:getElAttributes as videojs.getAttributes
 * @deprecated Use videojs.dom.getAttributes() instead
 */ /**
 * Empties the contents of an element.
 *
 * @borrows dom:emptyEl as videojs.emptyEl
 * @deprecated Use videojs.dom.emptyEl() instead
 */ /**
 * Normalizes and appends content to an element.
 *
 * The content for an element can be passed in multiple types and
 * combinations, whose behavior is as follows:
 *
 * - String
 *   Normalized into a text node.
 *
 * - Element, TextNode
 *   Passed through.
 *
 * - Array
 *   A one-dimensional array of strings, elements, nodes, or functions (which
 *   return single strings, elements, or nodes).
 *
 * - Function
 *   If the sole argument, is expected to produce a string, element,
 *   node, or array.
 *
 * @borrows dom:appendContents as videojs.appendContet
 * @deprecated Use videojs.dom.appendContent() instead
 */ /**
 * Normalizes and inserts content into an element; this is identical to
 * `appendContent()`, except it empties the element first.
 *
 * The content for an element can be passed in multiple types and
 * combinations, whose behavior is as follows:
 *
 * - String
 *   Normalized into a text node.
 *
 * - Element, TextNode
 *   Passed through.
 *
 * - Array
 *   A one-dimensional array of strings, elements, nodes, or functions (which
 *   return single strings, elements, or nodes).
 *
 * - Function
 *   If the sole argument, is expected to produce a string, element,
 *   node, or array.
 *
 * @borrows dom:insertContent as videojs.insertContent
 * @deprecated Use videojs.dom.insertContent() instead
 */['isEl','isTextNode','createEl','hasClass','addClass','removeClass','toggleClass','setAttributes','getAttributes','emptyEl','appendContent','insertContent'].forEach(function(k){videojs[k]=function(){log$1.warn('videojs.'+k+'() is deprecated; use videojs.dom.'+k+'() instead');return Dom[k].apply(null,arguments);};});/**
 * A safe getComputedStyle with an IE8 fallback.
 *
 * This is because in Firefox, if the player is loaded in an iframe with `display:none`,
 * then `getComputedStyle` returns `null`, so, we do a null-check to make sure
 * that the player doesn't break in these cases.
 * See https://bugzilla.mozilla.org/show_bug.cgi?id=548397 for more details.
 *
 * @borrows computed-style:computedStyle as videojs.computedStyle
 */videojs.computedStyle=computedStyle;/**
 * Export the Dom utilities for use in external plugins
 * and Tech's
 */videojs.dom=Dom;/**
 * Export the Url utilities for use in external plugins
 * and Tech's
 */videojs.url=Url;var video_cjs=videojs;

var vueVideoPlayer = createCommonjsModule(function (module, exports) {
!function (e, t) {
  module.exports = t(video_cjs) ;
}(commonjsGlobal, function (e) {
  return function (e) {
    function t(i) {
      if (n[i]) return n[i].exports;
      var r = n[i] = {
        i: i,
        l: !1,
        exports: {}
      };
      return e[i].call(r.exports, r, r.exports, t), r.l = !0, r.exports;
    }
    var n = {};
    return t.m = e, t.c = n, t.i = function (e) {
      return e;
    }, t.d = function (e, n, i) {
      t.o(e, n) || Object.defineProperty(e, n, {
        configurable: !1,
        enumerable: !0,
        get: i
      });
    }, t.n = function (e) {
      var n = e && e.__esModule ? function () {
        return e.default;
      } : function () {
        return e;
      };
      return t.d(n, "a", n), n;
    }, t.o = function (e, t) {
      return Object.prototype.hasOwnProperty.call(e, t);
    }, t.p = "/", t(t.s = 3);
  }([function (t, n) {
    t.exports = e;
  }, function (e, t, n) {

    function i(e, t, n) {
      return t in e ? Object.defineProperty(e, t, {
        value: n,
        enumerable: !0,
        configurable: !0,
        writable: !0
      }) : e[t] = n, e;
    }
    Object.defineProperty(t, "__esModule", {
      value: !0
    });
    var r = n(0),
      o = function (e) {
        return e && e.__esModule ? e : {
          default: e
        };
      }(r),
      s = window.videojs || o.default;
    "function" != typeof Object.assign && Object.defineProperty(Object, "assign", {
      value: function (e, t) {
        if (null == e) throw new TypeError("Cannot convert undefined or null to object");
        for (var n = Object(e), i = 1; i < arguments.length; i++) {
          var r = arguments[i];
          if (null != r) for (var o in r) Object.prototype.hasOwnProperty.call(r, o) && (n[o] = r[o]);
        }
        return n;
      },
      writable: !0,
      configurable: !0
    });
    var a = ["loadeddata", "canplay", "canplaythrough", "play", "pause", "waiting", "playing", "ended", "error"];
    t.default = {
      name: "video-player",
      props: {
        start: {
          type: Number,
          default: 0
        },
        crossOrigin: {
          type: String,
          default: ""
        },
        playsinline: {
          type: Boolean,
          default: !1
        },
        customEventName: {
          type: String,
          default: "statechanged"
        },
        options: {
          type: Object,
          required: !0
        },
        events: {
          type: Array,
          default: function () {
            return [];
          }
        },
        globalOptions: {
          type: Object,
          default: function () {
            return {
              controls: !0,
              controlBar: {
                remainingTimeDisplay: !1,
                playToggle: {},
                progressControl: {},
                fullscreenToggle: {},
                volumeMenuButton: {
                  inline: !1,
                  vertical: !0
                }
              },
              techOrder: ["html5"],
              plugins: {}
            };
          }
        },
        globalEvents: {
          type: Array,
          default: function () {
            return [];
          }
        }
      },
      data: function () {
        return {
          player: null,
          reseted: !0
        };
      },
      mounted: function () {
        this.player || this.initialize();
      },
      beforeDestroy: function () {
        this.player && this.dispose();
      },
      methods: {
        initialize: function () {
          var e = this,
            t = Object.assign({}, this.globalOptions, this.options);
          this.playsinline && (this.$refs.video.setAttribute("playsinline", this.playsinline), this.$refs.video.setAttribute("webkit-playsinline", this.playsinline), this.$refs.video.setAttribute("x5-playsinline", this.playsinline), this.$refs.video.setAttribute("x5-video-player-type", "h5"), this.$refs.video.setAttribute("x5-video-player-fullscreen", !1)), "" !== this.crossOrigin && (this.$refs.video.crossOrigin = this.crossOrigin, this.$refs.video.setAttribute("crossOrigin", this.crossOrigin));
          var n = function (t, n) {
            t && e.$emit(t, e.player), n && e.$emit(e.customEventName, i({}, t, n));
          };
          t.plugins && delete t.plugins.__ob__;
          var r = this;
          this.player = s(this.$refs.video, t, function () {
            for (var e = this, t = a.concat(r.events).concat(r.globalEvents), i = {}, o = 0; o < t.length; o++) "string" == typeof t[o] && void 0 === i[t[o]] && function (t) {
              i[t] = null, e.on(t, function () {
                n(t, !0);
              });
            }(t[o]);
            this.on("timeupdate", function () {
              n("timeupdate", this.currentTime());
            }), r.$emit("ready", this);
          });
        },
        dispose: function (e) {
          var t = this;
          this.player && this.player.dispose && ("Flash" !== this.player.techName_ && this.player.pause && this.player.pause(), this.player.dispose(), this.player = null, this.$nextTick(function () {
            t.reseted = !1, t.$nextTick(function () {
              t.reseted = !0, t.$nextTick(function () {
                e && e();
              });
            });
          }));
        }
      },
      watch: {
        options: {
          deep: !0,
          handler: function (e, t) {
            var n = this;
            this.dispose(function () {
              e && e.sources && e.sources.length && n.initialize();
            });
          }
        }
      }
    };
  }, function (e, t, n) {

    Object.defineProperty(t, "__esModule", {
      value: !0
    });
    var i = n(1),
      r = n.n(i);
    for (var o in i) ["default", "default"].indexOf(o) < 0 && function (e) {
      n.d(t, e, function () {
        return i[e];
      });
    }(o);
    var s = n(5),
      a = n(4),
      l = a(r.a, s.a, !1, null, null, null);
    t.default = l.exports;
  }, function (e, t, n) {

    function i(e) {
      return e && e.__esModule ? e : {
        default: e
      };
    }
    Object.defineProperty(t, "__esModule", {
      value: !0
    }), t.install = t.videoPlayer = t.videojs = void 0;
    var r = n(0),
      o = i(r),
      s = n(2),
      a = i(s),
      l = window.videojs || o.default,
      u = function (e, t) {
        t && (t.options && (a.default.props.globalOptions.default = function () {
          return t.options;
        }), t.events && (a.default.props.globalEvents.default = function () {
          return t.events;
        })), e.component(a.default.name, a.default);
      },
      d = {
        videojs: l,
        videoPlayer: a.default,
        install: u
      };
    t.default = d, t.videojs = l, t.videoPlayer = a.default, t.install = u;
  }, function (e, t) {
    e.exports = function (e, t, n, i, r, o) {
      var s,
        a = e = e || {},
        l = typeof e.default;
      "object" !== l && "function" !== l || (s = e, a = e.default);
      var u = "function" == typeof a ? a.options : a;
      t && (u.render = t.render, u.staticRenderFns = t.staticRenderFns, u._compiled = !0), n && (u.functional = !0), r && (u._scopeId = r);
      var d;
      if (o ? (d = function (e) {
        e = e || this.$vnode && this.$vnode.ssrContext || this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext, e || "undefined" == typeof __VUE_SSR_CONTEXT__ || (e = __VUE_SSR_CONTEXT__), i && i.call(this, e), e && e._registeredComponents && e._registeredComponents.add(o);
      }, u._ssrRegister = d) : i && (d = i), d) {
        var c = u.functional,
          f = c ? u.render : u.beforeCreate;
        c ? (u._injectStyles = d, u.render = function (e, t) {
          return d.call(t), f(e, t);
        }) : u.beforeCreate = f ? [].concat(f, d) : [d];
      }
      return {
        esModule: s,
        exports: a,
        options: u
      };
    };
  }, function (e, t, n) {

    var i = function () {
        var e = this,
          t = e.$createElement,
          n = e._self._c || t;
        return e.reseted ? n("div", {
          staticClass: "video-player"
        }, [n("video", {
          ref: "video",
          staticClass: "video-js"
        })]) : e._e();
      },
      r = [],
      o = {
        render: i,
        staticRenderFns: r
      };
    t.a = o;
  }]);
});
});

var VueVideoPlayer = unwrapExports(vueVideoPlayer);
vueVideoPlayer.VueVideoPlayer;

//
var script$5q = {
  name: 'video-play',
  components: {
    videoPlayer: VueVideoPlayer.videoPlayer
  },
  props: {
    url: {
      type: String,
      default: '',
      require: true
    }
  },
  watch: {
    url: {
      handler() {
        // this.isPlay = false
        this.playerOptions.sources[0].src = this.url;
      },
      immediate: true
    }
  },
  data() {
    return {
      pageType: 'pc',
      isShowPlay: false,
      videoIcon: videoIcon$1,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      isShowControls: false,
      playerOptions: {
        playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
        //播放速度
        autoplay: false,
        //如果true,浏览器准备好时开始回放。
        muted: false,
        // 默认情况下将会消除任何音频。
        loop: false,
        // 导致视频一结束就重新开始。
        preload: 'auto',
        // 建议浏览器在<video>加载元素后是否应该开始下载视频数据。auto浏览器选择最佳行为,立即开始加载视频（如果浏览器支持）
        language: 'zh-CN',
        aspectRatio: '16:9',
        // 将播放器置于流畅模式，并在计算播放器的动态大小时使用该值。值应该代表一个比例 - 用冒号分隔的两个数字（例如"16:9"或"4:3"）
        fluid: true,
        // 当true时，Video.js player将拥有流体大小。换句话说，它将按比例缩放以适应其容器。
        sources: [{
          type: '',
          //这里的种类支持很多种：基本视频格式、直播、流媒体等，具体可以参看git网址项目
          src: '' //url地址
        }],
        poster: '',
        //你的封面地址
        // width: document.documentElement.clientWidth, //播放器宽度
        notSupportedMessage: '获取视频失败，请刷新重试',
        //允许覆盖Video.js无法播放媒体源时显示的默认信息。
        controlBar: {
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true //全屏按钮
        }
      },
      playerEvents: ['fullscreenchange']
    };
  },
  inject: ['wrapRoot', 'inKey'],
  created() {
    this.pageType = this.$route.query.pageType || 'pc';
  },
  methods: {
    onPlayerPlay(e) {
      let currentVideo = e.el_.children[0].getAttribute('id', 64);
      let videoList = document.getElementsByTagName('video');
      for (let i = 0; i < videoList.length; i++) {
        let id = videoList[i].getAttribute('id');
        if (currentVideo != id) {
          videoList[i].pause();
        }
      }
      let audio = document.querySelectorAll('audio');
      if (audio && audio.length) audio.forEach(i => i.pause());
    },
    showPlay() {
      this.isShowPlay = true;
      this.wrapRoot.currentInKey = this.inKey;
    },
    toggleFullscreen(e) {
      if (this.wrapRoot.currentInKey === this.inKey) {
        const data = {
          type: 'setIframeClass',
          value: e.isFullscreen()
        };
        window.parent.postMessage(data);
      }
    }
  }
};

/* script */
const __vue_script__$5q = script$5q;
/* template */
var __vue_render__$5q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "video" },
    [
      _c(
        "div",
        {
          staticClass: "video-content",
          staticStyle: { "background-color": "#000" },
        },
        [
          _c("video", {
            staticStyle: { width: "180px", height: "100%" },
            attrs: { src: _vm.url },
          }),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "play",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.showPlay.apply(null, arguments)
                },
              },
            },
            [_c("img", { attrs: { src: _vm.videoIcon, alt: "" } })]
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          attrs: {
            top: "60px",
            width: _vm.pageType === "pc" ? "50%" : "100%",
            "custom-class": "video-play--dialog",
            "destroy-on-close": "",
            "append-to-body": "",
            visible: _vm.isShowPlay,
          },
          on: {
            "update:visible": function ($event) {
              _vm.isShowPlay = $event;
            },
          },
        },
        [
          _c("video-player", {
            ref: "videoPlayer",
            staticClass: "video-player vjs-custom-skin",
            attrs: {
              events: _vm.playerEvents,
              playsinline: true,
              options: _vm.playerOptions,
            },
            on: {
              "!fullscreenchange": function ($event) {
                return _vm.toggleFullscreen($event)
              },
              play: function ($event) {
                return _vm.onPlayerPlay($event)
              },
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$5q = [];
__vue_render__$5q._withStripped = true;

  /* style */
  const __vue_inject_styles__$5q = undefined;
  /* scoped */
  const __vue_scope_id__$5q = "data-v-641dedde";
  /* module identifier */
  const __vue_module_identifier__$5q = undefined;
  /* functional template */
  const __vue_is_functional_template__$5q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5q, staticRenderFns: __vue_staticRenderFns__$5q },
    __vue_inject_styles__$5q,
    __vue_script__$5q,
    __vue_scope_id__$5q,
    __vue_is_functional_template__$5q,
    __vue_module_identifier__$5q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5p = {
  name: 'bc-file-render',
  components: {
    audioPlay: __vue_component__$5r,
    videoPlay: __vue_component__$5q
  },
  props: {
    fileInfo: {
      type: Object,
      default: () => ({
        fileUrl: '',
        fileType: '',
        fileSuffix: ''
      }),
      required: true
    },
    showHandler: {
      type: Boolean,
      default: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    }
  },
  computed: {
    fileType() {
      let type;
      if (this.fileInfo.fileType.includes('image')) type = 'image';
      if (this.fileInfo.fileType.includes('audio')) type = 'audio';
      if (this.fileInfo.fileType.includes('video')) type = 'video';
      return type;
    }
  },
  methods: {
    againUpload() {
      // this.$emit('remove')
      this.$emit('againUpload');
    },
    remove() {
      this.$emit('remove');
    }
  }
};

/* script */
const __vue_script__$5p = script$5p;
/* template */
var __vue_render__$5p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "file-render", attrs: { flex: "" } },
    [
      _vm.fileType === "image"
        ? _c("div", [
            _c("img", {
              staticClass: "image-file",
              attrs: { src: _vm.fileInfo.fileUrl },
            }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.fileType === "audio"
        ? _c("audio-play", {
            style: { width: _vm.showHandler ? "256px" : "100%" },
            attrs: {
              playbackControl: _vm.playbackControl,
              isLineFile: _vm.isLineFile,
              mobile: _vm.mobile,
              url: _vm.fileInfo.fileUrl,
            },
            on: {
              play: function ($event) {
                return _vm.$emit("play")
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.fileType === "video"
        ? _c("video-play", {
            staticClass: "file-render--video",
            attrs: {
              playbackControl: _vm.playbackControl,
              isLineFile: _vm.isLineFile,
              mobile: _vm.mobile,
              url: _vm.fileInfo.fileUrl,
            },
            on: {
              play: function ($event) {
                return _vm.$emit("play")
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.showHandler
        ? _c(
            "div",
            {
              staticClass: "file-info",
              class: { videoHandler: _vm.fileType === "video" },
              staticStyle: { "margin-left": "28px" },
            },
            [
              _vm.fileType !== "audio"
                ? [
                    _vm.fileInfo.fileName
                      ? _c(
                          "el-tooltip",
                          {
                            staticClass: "item",
                            attrs: {
                              "popper-class": "atooltip",
                              effect: "dark",
                              content: "文件名称：" + _vm.fileInfo.fileName,
                              placement: "top-start",
                            },
                          },
                          [
                            _c(
                              "div",
                              { staticClass: "fileName text-overflow" },
                              [
                                _vm._v(
                                  "文件名称：" + _vm._s(_vm.fileInfo.fileName)
                                ),
                              ]
                            ),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _c("div", { staticClass: "fileInfo" }, [
                      _vm._v("视频格式：" + _vm._s(_vm.fileInfo.fileSuffix)),
                    ]),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _c(
                "div",
                { attrs: { flex: "cross:center" } },
                [
                  _c(
                    "el-button",
                    {
                      staticClass: "dialog-submit",
                      attrs: { type: "primary" },
                      on: { click: _vm.againUpload },
                    },
                    [_vm._v("重新上传")]
                  ),
                  _vm._v(" "),
                  _c(
                    "el-button",
                    { staticClass: "dialog-close", on: { click: _vm.remove } },
                    [_vm._v("删除")]
                  ),
                ],
                1
              ),
            ],
            2
          )
        : _vm._e(),
    ],
    1
  )
};
var __vue_staticRenderFns__$5p = [];
__vue_render__$5p._withStripped = true;

  /* style */
  const __vue_inject_styles__$5p = undefined;
  /* scoped */
  const __vue_scope_id__$5p = "data-v-db8ba39c";
  /* module identifier */
  const __vue_module_identifier__$5p = undefined;
  /* functional template */
  const __vue_is_functional_template__$5p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5p, staticRenderFns: __vue_staticRenderFns__$5p },
    __vue_inject_styles__$5p,
    __vue_script__$5p,
    __vue_scope_id__$5p,
    __vue_is_functional_template__$5p,
    __vue_module_identifier__$5p,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5o = {
  name: 'BcViewCommon',
  components: {
    bcFileRenderNew: __vue_component__$5p
  },
  props: {
    content: {
      type: String,
      default: ''
    },
    standardAnswer: {
      type: Array,
      default: () => []
    },
    fileInfo: {
      type: Object,
      default: () => ({
        fileUrl: '',
        fileType: '',
        fileSuffix: ''
      })
    },
    placeholder: {
      type: String,
      default: ''
    },
    border: {
      type: Boolean,
      default: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {};
  },
  methods: {
    formatImg(str) {
      if (str) {
        let data = str.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/gi, (match, p1) => {
          if (match.indexOf('style') !== -1) {
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image.split('style="')[0] + 'style="max-width:100% !important;' + image.split('style="')[1];
          } else if (match.indexOf('width') !== -1) {
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image;
          } else {
            return `<img src='${p1.indexOf('http') > -1 ? p1 : this.resourceBasisPath + p1.split('./')[1]}' style="max-width:100%;" />`;
          }
        });
        return data;
      }
    },
    showImage(e) {
      if (e.target.tagName == 'IMG') {
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: e.target.src,
            title: ''
          }]
        });
      }
    }
  }
};

/* script */
const __vue_script__$5o = script$5o;
/* template */
var __vue_render__$5o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "content" }, [
    _c(
      "div",
      { staticClass: "stem-content", class: { "no-border": !_vm.border } },
      [
        !_vm.content && !_vm.fileInfo.fileUrl && !_vm.standardAnswer
          ? _c("div", { staticClass: "placeholder" }, [
              _vm._v(_vm._s(_vm.placeholder)),
            ])
          : _vm.content
          ? _c("div", {
              staticClass: "text-break-all",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
              on: {
                click: function ($event) {
                  return _vm.showImage($event)
                },
              },
            })
          : _vm.standardAnswer
          ? _c(
              "div",
              _vm._l(_vm.standardAnswer, function (item, index) {
                return _c("div", {
                  key: index,
                  domProps: { innerHTML: _vm._s(_vm.formatImg(item.content)) },
                  on: {
                    click: function ($event) {
                      return _vm.showImage($event)
                    },
                  },
                })
              }),
              0
            )
          : _vm._e(),
        _vm._v(" "),
        _vm._t("default"),
        _vm._v(" "),
        _vm.fileInfo.fileUrl
          ? _c("bc-file-render-new", {
              style: {
                "margin-top": _vm.content ? "16px" : 0,
                padding: "10px 0",
              },
              attrs: {
                playbackControl: _vm.playbackControl,
                isLineFile: _vm.isLineFile,
                mobile: _vm.mobile,
                showHandler: false,
                fileInfo: _vm.fileInfo,
              },
              on: {
                play: function ($event) {
                  return _vm.$emit("play")
                },
              },
            })
          : _vm._e(),
      ],
      2
    ),
  ])
};
var __vue_staticRenderFns__$5o = [];
__vue_render__$5o._withStripped = true;

  /* style */
  const __vue_inject_styles__$5o = undefined;
  /* scoped */
  const __vue_scope_id__$5o = "data-v-aff680c8";
  /* module identifier */
  const __vue_module_identifier__$5o = undefined;
  /* functional template */
  const __vue_is_functional_template__$5o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5o, staticRenderFns: __vue_staticRenderFns__$5o },
    __vue_inject_styles__$5o,
    __vue_script__$5o,
    __vue_scope_id__$5o,
    __vue_is_functional_template__$5o,
    __vue_module_identifier__$5o,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5n = {
  name: 'bc-view-option-radio',
  components: {
    BcViewCommon: __vue_component__$5o
  },
  props: {
    option: {
      type: [String, Array],
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    isSubmit: {
      type: Boolean,
      default: true
    },
    value: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    answer: {
      type: Object,
      default: () => {}
    },
    type: {
      type: String,
      default: 'radio'
    },
    standardAnswer: {
      type: Array
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {
      optionsList: [],
      selectData: '',
      isAnswer: false,
      radioJudeMap: {
        A: '正确',
        B: '错误'
      },
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  computed: {},
  watch: {
    value: {
      handler(val) {
        this.selectData = val;
      },
      immediate: true
    },
    option: {
      handler(val) {
        try {
          if (Array.isArray(val)) {
            this.optionsList = val;
          } else {
            this.optionsList = JSON.parse(val);
          }
        } catch (err) {
          this.optionsList = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    isActive(item) {
      return item.title === this.selectData && !this.readonly;
    },
    isTrue(item) {
      // if (this.type === 'radio') {
      //   if (this.selectData) {
      //     //当前等于选中
      //     if (item.title === this.selectData) {
      //       //等于正确答案
      //       if (item.title === this.standardAnswer[0].title) {
      //         return true
      //       } else {
      //         return false
      //       }
      //     } else {
      //       //等于正确答案
      //       if (item.title === this.standardAnswer[0].title) {
      //         return true
      //       } else {
      //         return false
      //       }
      //     }
      //   } else {
      //     return false
      //   }
      // } else {
      //   if (this.selectData) {
      //     //当前等于选中
      //     if (item.title === this.selectData) {
      //       //等于正确答案
      //       if (item.content === this.standardAnswer[0]) {
      //         return true
      //       } else {
      //         return false
      //       }
      //     } else {
      //       //等于正确答案
      //       if (item.content === this.standardAnswer[0]) {
      //         return true
      //       } else {
      //         return false
      //       }
      //     }
      //   } else {
      //     return false
      //   }
      // }
      if (this.type === 'radio') {
        return this.isAnswer && this.selectData && this.selectData === item.title && this.standardAnswer.some(i => i.title === item.title);
      } else {
        return this.isAnswer && this.selectData && this.selectData === item.title && this.standardAnswer.some(i => i === item.content);
      }
    },
    isFalse(item) {
      if (!this.isAnswer) {
        return false;
      }
      if (this.type === 'radio') {
        if (this.selectData) {
          //当前为选中的
          if (item.title === this.selectData) {
            if (this.standardAnswer[0].title === item.title) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        if (this.selectData) {
          //当前为选中的
          if (item.title === this.selectData) {
            if (this.standardAnswer[0] === item.content) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      }
    },
    submitAnswer() {
      console.log('🚀 ~ file: radio.vue:258 ~ submitAnswer ~ this.selectData:', this.selectData);
      if (!this.selectData && this.isSubmit) {
        this.$message.error('请选择答案');
        return;
      }
      let answerIsTrue = false;
      if (this.type === 'radio') {
        answerIsTrue = this.standardAnswer.some(i => i.title === this.selectData);
      } else {
        answerIsTrue = this.standardAnswer.some(i => i === this.radioJudeMap[this.selectData]);
      }
      this.isAnswer = true;
      this.$emit('showAnalysis', answerIsTrue);
    },
    afresh() {
      this.selectData = '';
      this.isAnswer = false;
      this.$emit('afresh');
    },
    select(item) {
      this.selectData = item;
      // this.$emit('showAnalysis')
    }
  }
};

var __$_require_static_images_leaflet_icons_correct_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAA0klEQVQokWMs2MbsycDAMJeBgUGSgTjwnIGBIZmJRE0MULVzmUjUBNfMRIYmMCBaowSPFkO9433SNIrzaDKU215iuPpqK6ZGE6loBkZGTHMkebUZKmwvM+y918Ww5moOpkYb+SyGLrdPDKzMnCjOK7O5yLD3XifDlptV2P044bg1w7XX2xm63D4zCHMpMYhxq4Odt+tOC8OWm9UYLgElgP/IAv4a3QxaYp4MTIwsDKeeLGLYfbcNq78xNBILKIpHUNojFbwAaUwBMUjQ+ZSBgSEZAPrMNTg71FhgAAAAAElFTkSuQmCC";

var __$_require_static_images_leaflet_icons_error_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAABNElEQVQokWO8ySDtycDAMJeBgUGSgTjwnIGBIRmk8RkJmmDgORNME39SBANfTBBOlTyBHgwCWfEwriQTXIaNlUFi8SQG3jBfDE3c7vYMUuvmMDDx8yIEbzJI/4fhZyFp/0HgRVIxXOyJbwJY7GVm5X9ktSgaQfhFcglY4SPrgP/31e3A7FfFTf/R1TGCdaMBDktjBrljG8GCT73jGL5u24fhfCYMEQYGhr8vXyOx32ANLAyNPP7uDIp3jzE89U1geGTlzyB3ZhsDX1wIhkYWZA6Xiy2D1Ia5DK9Lmhm+btkDFnsRl88gsWgiw/9fvxk+r4A4HyVUn0fnQEIvuxojIOByOTVwMbiNTPx8DK8L6hk+TF2A4axPS9czMHJwMDAJ8sPFyE1yL0CBkwJikKDpKQMDQzIArpej/rmzX0cAAAAASUVORK5CYII=";

/* script */
const __vue_script__$5n = script$5n;
var __vue_render__$5n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      { staticClass: "xml-checkbox-box" },
      _vm._l(_vm.optionsList, function (item, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "option-item",
            class: {
              isActive: _vm.isActive(item),
              isTrue: _vm.isTrue(item),
              isFalse: _vm.isFalse(item),
            },
            attrs: { flex: "" },
            on: {
              click: function ($event) {
                $event.stopPropagation();
                return _vm.select(item.title)
              },
            },
          },
          [
            _c(
              "div",
              { staticClass: "questionSeq" },
              [
                _vm.isTrue(item)
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_correct_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.isFalse(item)
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_error_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                !_vm.readonly && !_vm.isTrue(item) && !_vm.isFalse(item)
                  ? _c(
                      "el-radio",
                      { attrs: { value: _vm.selectData, label: item.title } },
                      [_vm._v(_vm._s(""))]
                    )
                  : _vm._e(),
              ],
              1
            ),
            _vm._v(" "),
            _vm.showTitle
              ? _c(
                  "div",
                  {
                    staticClass: "questionSeq",
                    staticStyle: { "margin-right": "2px" },
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.serialNumber[index]) +
                        "\n      "
                    ),
                  ]
                )
              : _vm._e(),
            _vm._v(" "),
            _c("bc-view-common", {
              attrs: {
                "flex-box": "1",
                border: false,
                content: item.content,
                fileInfo: item.fileInfo,
                resourceBasisPath: _vm.resourceBasisPath,
                pageType: _vm.pageType,
              },
            }),
          ],
          1
        )
      }),
      0
    ),
    _vm._v(" "),
    _vm.isSubmit
      ? _c(
          "div",
          {
            staticClass: "mt-4",
            staticStyle: {
              display: "flex",
              "justify-content": "flex-end",
              "text-align": "center",
              "margin-top": "12px",
            },
          },
          [
            !_vm.selectData || !_vm.isAnswer
              ? _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: { click: _vm.submitAnswer },
                  },
                  [_vm._v("提交作答")]
                )
              : _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: {
                      click: function ($event) {
                        return _vm.afresh()
                      },
                    },
                  },
                  [_vm._v("重新作答")]
                ),
          ]
        )
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$5n = [];
__vue_render__$5n._withStripped = true;

  /* style */
  const __vue_inject_styles__$5n = undefined;
  /* scoped */
  const __vue_scope_id__$5n = "data-v-2bf2d811";
  /* module identifier */
  const __vue_module_identifier__$5n = undefined;
  /* functional template */
  const __vue_is_functional_template__$5n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5n, staticRenderFns: __vue_staticRenderFns__$5n },
    __vue_inject_styles__$5n,
    __vue_script__$5n,
    __vue_scope_id__$5n,
    __vue_is_functional_template__$5n,
    __vue_module_identifier__$5n,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5m = {
  name: 'bc-view-option-checkbox',
  components: {
    BcViewCommon: __vue_component__$5o
  },
  props: {
    option: {
      type: [String, Array],
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    isSubmit: {
      type: Boolean,
      default: true
    },
    value: {
      type: [String],
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    answer: {
      type: Object,
      default: () => {}
    },
    standardAnswer: {
      type: Array,
      default: () => []
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {
      optionsList: [],
      selectData: [],
      isAnswer: false,
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  watch: {
    // value: {
    //   handler(val) {
    //     this.selectData = val ? val : []
    //   },
    //   immediate: true
    // },
    option: {
      handler(val) {
        try {
          if (Array.isArray(val)) {
            this.optionsList = val;
          } else {
            this.optionsList = JSON.parse(val);
          }
        } catch (err) {
          this.optionsList = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    select(item, e) {
      // debugger
      if (e.target.tagName === 'INPUT') {
        e.stopPropagation();
        return;
      }
      if (this.readonly) return;
      let index = this.selectData.findIndex(i => i === item);
      if (index !== -1) {
        this.selectData.splice(index, 1);
      } else {
        this.selectData.push(item);
      }
      this.selectData.sort((a, b) => a.charCodeAt() - b.charCodeAt());
      this.$emit('change', this.selectData.join(''));
    },
    submitAnswer() {
      if (!this.selectData.length && this.isSubmit) {
        this.$message.error('请选择答案');
        return;
      }
      let answerIsTrue = this.standardAnswer.map(i => i.title).join('、') === this.selectData.join('、');
      this.isAnswer = true;
      this.$emit('showAnalysis', answerIsTrue);
    },
    afresh() {
      this.selectData = [];
      this.isAnswer = false;
      this.$emit('afresh');
    },
    SkipProgress(e, title, val) {
      if (e.target.targetName === 'INPUT') {
        e.stopPropagation();
      }
    }
  }
};

/* script */
const __vue_script__$5m = script$5m;
var __vue_render__$5m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      { staticClass: "xml-checkbox-box" },
      _vm._l(_vm.optionsList, function (item, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "option-item",
            class: {
              isActive: _vm.selectData.includes(item.title) && !_vm.readonly,
              isTrue:
                _vm.isAnswer &&
                _vm.selectData.length &&
                _vm.selectData.includes(item.title) &&
                _vm.standardAnswer.some(function (i) {
                  return i.title === item.title
                }),
              isFalse:
                _vm.isAnswer &&
                _vm.selectData.length &&
                _vm.selectData.includes(item.title) &&
                _vm.standardAnswer.every(function (i) {
                  return i.title !== item.title
                }),
            },
            attrs: { flex: "" },
            on: {
              click: function ($event) {
                $event.stopPropagation();
                return _vm.select(item.title, $event)
              },
            },
          },
          [
            _c(
              "div",
              { staticClass: "questionSeq" },
              [
                _vm.isAnswer &&
                _vm.selectData.length &&
                _vm.selectData.includes(item.title) &&
                _vm.standardAnswer.some(function (i) {
                  return i.title === item.title
                })
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_correct_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.isAnswer &&
                _vm.selectData.length &&
                _vm.selectData.includes(item.title) &&
                _vm.standardAnswer.every(function (i) {
                  return i.title !== item.title
                })
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_error_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                !_vm.readonly && !_vm.isAnswer
                  ? _c(
                      "el-checkbox",
                      {
                        attrs: { value: _vm.selectData.includes(item.title) },
                        nativeOn: {
                          click: function ($event) {
                            _vm.SkipProgress(
                              $event,
                              item.title,
                              _vm.selectData.includes(item.title)
                            );
                          },
                        },
                      },
                      [_vm._v(_vm._s(""))]
                    )
                  : _vm._e(),
              ],
              1
            ),
            _vm._v(" "),
            _vm.showTitle
              ? _c(
                  "div",
                  {
                    staticClass: "questionSeq",
                    staticStyle: { "margin-right": "2px" },
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.serialNumber[index]) +
                        "\n      "
                    ),
                  ]
                )
              : _vm._e(),
            _vm._v(" "),
            _c("bc-view-common", {
              attrs: {
                "flex-box": "1",
                border: false,
                content: item.content,
                fileInfo: item.fileInfo,
                resourceBasisPath: _vm.resourceBasisPath,
                pageType: _vm.pageType,
              },
            }),
          ],
          1
        )
      }),
      0
    ),
    _vm._v(" "),
    _vm.isSubmit
      ? _c(
          "div",
          {
            staticClass: "mt-4",
            staticStyle: {
              display: "flex",
              "justify-content": "flex-end",
              "text-align": "center",
              "margin-top": "12px",
            },
          },
          [
            !_vm.selectData || !_vm.isAnswer
              ? _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: { click: _vm.submitAnswer },
                  },
                  [_vm._v("提交作答")]
                )
              : _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: {
                      click: function ($event) {
                        return _vm.afresh()
                      },
                    },
                  },
                  [_vm._v("重新作答")]
                ),
          ]
        )
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$5m = [];
__vue_render__$5m._withStripped = true;

  /* style */
  const __vue_inject_styles__$5m = undefined;
  /* scoped */
  const __vue_scope_id__$5m = "data-v-26ee60ed";
  /* module identifier */
  const __vue_module_identifier__$5m = undefined;
  /* functional template */
  const __vue_is_functional_template__$5m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5m, staticRenderFns: __vue_staticRenderFns__$5m },
    __vue_inject_styles__$5m,
    __vue_script__$5m,
    __vue_scope_id__$5m,
    __vue_is_functional_template__$5m,
    __vue_module_identifier__$5m,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5l = {
  name: 'QuestionItem',
  components: {
    BcViewOptionCheckbox: __vue_component__$5m,
    BcViewOptionRadio: __vue_component__$5n,
    BcViewCommon: __vue_component__$5o
  },
  // dicts: ['question_type'],
  data() {
    return {
      // isShowAnalysis: false,
      // isShowAnswer: false,
      // isShowKnowledge: false,
      inputAnswer: '',
      dict: {
        label: {
          question_type: {
            1: '单选题',
            2: '多选题',
            3: '判断题',
            13: '简答题'
          }
        }
      },
      isAnswer: false,
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  props: {
    question: Object,
    canShowAnswer: String,
    canShowAnalysis: String,
    canShowKnowledge: String,
    inKey: String,
    border: {
      type: Boolean,
      default: false
    },
    isSubmit: {
      //是否显示提交作答按钮
      type: Boolean,
      default: true
    },
    questionNumber: {
      type: Number,
      default: 0
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  provide: function () {
    return {
      inKey: this.inKey
    };
  },
  mounted() {
    console.log(this.question);
  },
  computed: {
    isShowAnalysis() {
      return this.isAnswer && this.canShowAnalysis === 'true';
    },
    isShowAnswer() {
      return this.isAnswer && this.canShowAnswer === 'true';
    },
    isShowKnowledge() {
      return this.isAnswer && this.canShowKnowledge === 'true';
    },
    isRadio() {
      return isRadio$1(this.question.questionType);
    },
    isCheck() {
      return isCheck$1(this.question.questionType);
    },
    showJudgeAnswer() {
      return showJudgeAnswer$1(this.question.questionType);
    },
    showRichTextAnswer() {
      return showRichTextAnswer$1(this.question.questionType);
    },
    showOptions() {
      return showOptions$1(this.question.questionType);
    },
    showSort() {
      return showSort$1(this.question.questionType);
    },
    isCloze() {
      return isCloze$1(this.question.questionType);
    },
    clozeLength() {
      let fillInTheBlanksSpan = this.question.stem.content.match(/fillInTheBlanksSpan/g);
      let sortString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let data = fillInTheBlanksSpan ? fillInTheBlanksSpan.length : 0;
      let list = [];
      for (let i = 0; i < data; i++) {
        list.push({
          content: '',
          title: sortString.split('')[i]
        });
      }
      // return fillInTheBlanksSpan ? fillInTheBlanksSpan.length : 0
      return list;
    },
    standardAnswer() {
      try {
        let answerTitle = '';
        if (!this.showRichTextAnswer) {
          if (this.showOptions) {
            answerTitle = this.question.standardAnswer.map(i => i.title).join('、');
          } else if (this.showJudgeAnswer) {
            answerTitle = this.question.standardAnswer.map(i => i).join('、');
          } else if (this.showSort) {
            let questionOption = xeUtils.clone(this.question.questionOption, true);
            answerTitle = questionOption.sort((c, n) => c.index - n.index).map(i => i.title).join('、');
          }
        }
        return answerTitle;
      } catch (e) {
        console.log('异常：', e);
        return '';
      }
    }
  },
  methods: {
    showAnalysis(answer) {
      if (!this.showRichTextAnswer) {
        this.isAnswer = true;
        let params = {
          pass: answer,
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem)
        };
        this.$emit('submitAnswer', params);
      } else {
        this.isAnswer = true;
      }
    },
    showAnalysis2(answer) {
      if (answer.questionType == 'shortSnswer' && this.isSubmit) {
        if (!answer.userAnswer) {
          this.$message.error('请先输入答案');
          return;
        }
      } else if (answer.questionType == 'fillInTheBlanks' && this.isSubmit) {
        for (let i = 0; i < this.clozeLength.length; i++) {
          if (!this.clozeLength[i].content) {
            this.$message.error('请先输入答案');
            return;
          }
        }
      }
      this.isAnswer = true;
      let params = {
        pass: answer,
        questionId: this.question.id,
        questionName: JSON.stringify(this.question.stem)
      };
      this.$emit('submitAnswer', params);
    },
    afresh(questionType) {
      this.isAnswer = false;
      if (questionType == 'shortSnswer') {
        this.inputAnswer = '';
      } else if (questionType == 'fillInTheBlanks') {
        for (let i = 0; i < this.clozeLength.length; i++) {
          this.clozeLength[i].content = '';
        }
      }
    },
    //
    changeAnswer() {
      if (this.isRadio || this.showJudgeAnswer) {
        this.$refs.bcViewOptionRadio?.submitAnswer();
      }
      if (this.isCheck) {
        this.$refs.bcViewOptionCheckbox?.submitAnswer();
      }
      if (this.showRichTextAnswer && !this.isCloze) {
        this.showAnalysis2({
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem),
          userAnswer: this.inputAnswer,
          questionType: 'shortSnswer'
        });
      }
      if (this.isCloze) {
        this.showAnalysis2({
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem),
          userAnswer: this.clozeLength,
          questionType: 'fillInTheBlanks'
        });
      }
    },
    // AIanalysis(){
    //   let params = {
    //       questionOption: JSON.stringify(this.question.questionOption),
    //       questionName: JSON.stringify(this.question.stem)
    //   }
    //   this.$emit('setanalysis', params)
    // },
    AIanalysis() {
      let serialNumber = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];
      let option = '';
      let question = this.question.stem.content;
      let optionList = this.question.questionOption;
      for (let i = 0; i < optionList.length; i++) {
        if (optionList[i].content) {
          option += serialNumber[i] + '.' + this.removeStyle(optionList[i].content) + (i < optionList.length - 1 ? ';' : '');
        }
      }
      if (this.question.questionType == '3') {
        option = 'A.正确;B.错误';
      }
      const data = {
        type: 'AiAnalysis',
        value: this.removeStyle(question) + option,
        code: 'success'
      };
      this.$emit('setanalysis', data);
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    },
    getPaperList(list) {
      if (list.length > 0) {
        let answerList = [];
        list.map(item => {
          if (typeof item == 'string') {
            let data = {
              selected: false,
              ...JSON.parse(item)
            };
            answerList.push(data);
          } else {
            this.$set(item, 'selected', false);
            answerList = list;
          }
        });
        return answerList;
      }
    }
  }
};

var __$_require_static_images_leaflet_icons_ai_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$5l = script$5l;
var __vue_render__$5l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      {
        staticClass: "group-content",
        class: _vm.border ? "border-bottom" : "",
      },
      [
        _c("div", { staticClass: "group-content-box" }, [
          _c("div", { staticClass: "stem" }, [
            _c(
              "div",
              { staticClass: "stem-box", staticStyle: { display: "flex" } },
              [
                _c("bc-view-common", {
                  staticClass: "xml-question-title",
                  staticStyle: { flex: "1", "min-width": "0" },
                  attrs: {
                    content: _vm.question.stem.content,
                    border: false,
                    fileInfo: _vm.question.stem.fileInfo,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                }),
              ],
              1
            ),
          ]),
        ]),
        _vm._v(" "),
        _vm.isRadio
          ? _c(
              "div",
              { staticClass: "mt-4" },
              [
                _c("bc-view-option-radio", {
                  ref: "bcViewOptionRadio",
                  attrs: {
                    standardAnswer: _vm.question.standardAnswer,
                    option: _vm.question.questionOption,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isCheck
          ? _c(
              "div",
              { staticClass: "mt-4" },
              [
                _c("bc-view-option-checkbox", {
                  ref: "bcViewOptionCheckbox",
                  attrs: {
                    option: _vm.question.questionOption,
                    standardAnswer: _vm.question.standardAnswer,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.showRichTextAnswer && !_vm.isCloze
          ? _c(
              "div",
              {
                staticClass: "mt-4",
                staticStyle: { display: "flex", "flex-direction": "column" },
              },
              [
                _c("el-input", {
                  attrs: {
                    type: "textarea",
                    placeholder: "请输入您的回答",
                    autosize: { minRows: 2, maxRows: 8 },
                  },
                  model: {
                    value: _vm.inputAnswer,
                    callback: function ($$v) {
                      _vm.inputAnswer = $$v;
                    },
                    expression: "inputAnswer",
                  },
                }),
                _vm._v(" "),
                !_vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            _vm.showAnalysis2({
                              questionId: _vm.question.id,
                              questionName: JSON.stringify(_vm.question.stem),
                              userAnswer: _vm.inputAnswer,
                              questionType: "shortSnswer",
                            });
                          },
                        },
                      },
                      [_vm._v("\n        提交作答\n      ")]
                    )
                  : _vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            return _vm.afresh("shortSnswer")
                          },
                        },
                      },
                      [_vm._v("\n        重新作答\n      ")]
                    )
                  : _vm._e(),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.showJudgeAnswer
          ? _c(
              "div",
              [
                _c("bc-view-option-radio", {
                  ref: "bcViewOptionRadio",
                  attrs: {
                    standardAnswer: _vm.question.standardAnswer,
                    resourceBasisPath: _vm.resourceBasisPath,
                    pageType: _vm.pageType,
                    isSubmit: _vm.isSubmit,
                    type: "judge",
                    option: [
                      { title: "A", content: "正确" },
                      { title: "B", content: "错误" },
                    ],
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isCloze
          ? _c(
              "div",
              [
                _vm._l(_vm.clozeLength, function (item, index) {
                  return _c("div", { key: index, staticClass: "isTrue" }, [
                    _c("input", {
                      directives: [
                        {
                          name: "model",
                          rawName: "v-model",
                          value: item.content,
                          expression: "item.content",
                        },
                      ],
                      staticClass: "answer-cloze-input",
                      attrs: {
                        maxlength: "100",
                        placeholder: "请输入...",
                        type: "text",
                      },
                      domProps: { value: item.content },
                      on: {
                        input: function ($event) {
                          if ($event.target.composing) {
                            return
                          }
                          _vm.$set(item, "content", $event.target.value);
                        },
                      },
                    }),
                  ])
                }),
                _vm._v(" "),
                !_vm.isAnswer && _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            _vm.showAnalysis2({
                              questionId: _vm.question.id,
                              questionName: JSON.stringify(_vm.question.stem),
                              userAnswer: _vm.clozeLength,
                              questionType: "fillInTheBlanks",
                            });
                          },
                        },
                      },
                      [_vm._v("\n        提交作答\n      ")]
                    )
                  : _vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            return _vm.afresh("fillInTheBlanks")
                          },
                        },
                      },
                      [_vm._v("\n        重新作答\n      ")]
                    )
                  : _vm._e(),
              ],
              2
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowAnswer
          ? _c("div", [
              !_vm.showRichTextAnswer
                ? _c("div", { staticClass: "mt-6" }, [
                    _c(
                      "div",
                      {
                        staticStyle: {
                          display: "flex",
                          "flex-direction": "column",
                        },
                      },
                      [
                        _c("div", { staticClass: "question-label" }, [
                          _vm._v(
                            _vm._s(
                              _vm.question.questionType == 13
                                ? "参考答案:"
                                : "正确答案："
                            )
                          ),
                        ]),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticStyle: {
                              color: "#0070c1",
                              "min-width": "0",
                              flex: "1",
                              "line-height": "28px",
                              padding: "8px",
                              "border-radius": "6px",
                              "background-color": "#f5f5f5",
                            },
                          },
                          [
                            _vm.question.questionType == 3
                              ? [
                                  _c("span", [
                                    _vm._v(
                                      "\n                " +
                                        _vm._s(_vm.standardAnswer) +
                                        "\n              "
                                    ),
                                  ]),
                                ]
                              : _vm._l(
                                  _vm.getPaperList(_vm.question.questionOption),
                                  function (item, index) {
                                    return _c(
                                      "span",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: _vm.standardAnswer.includes(
                                              item.title
                                            ),
                                            expression:
                                              "standardAnswer.includes(item.title)",
                                          },
                                        ],
                                        key: index,
                                      },
                                      [
                                        _vm._v(
                                          "\n                " +
                                            _vm._s(_vm.serialNumber[index]) +
                                            "\n              "
                                        ),
                                      ]
                                    )
                                  }
                                ),
                          ],
                          2
                        ),
                      ]
                    ),
                  ])
                : _c("div", { staticClass: "mt-6" }, [
                    _c(
                      "div",
                      {
                        staticStyle: {
                          display: "flex",
                          "flex-direction": "column",
                        },
                      },
                      [
                        _c("div", { staticClass: "question-label" }, [
                          _vm._v(
                            _vm._s(
                              _vm.question.questionType == 13
                                ? "参考答案:"
                                : "正确答案："
                            )
                          ),
                        ]),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticStyle: {
                              flex: "1",
                              "min-width": "0",
                              padding: "8px",
                              "border-radius": "6px",
                              "background-color": "#f5f5f5",
                            },
                          },
                          [
                            _c("bc-view-common", {
                              staticStyle: { flex: "1", "min-width": "0" },
                              attrs: {
                                content: _vm.question.standardAnswer.content,
                                standardAnswer:
                                  _vm.question.questionType == 10
                                    ? _vm.question.standardAnswer
                                    : [],
                                border: false,
                                fileInfo: _vm.question.standardAnswer.fileInfo,
                                resourceBasisPath: _vm.resourceBasisPath,
                                isSubmit: _vm.isSubmit,
                                pageType: _vm.pageType,
                              },
                            }),
                          ],
                          1
                        ),
                      ]
                    ),
                  ]),
            ])
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowAnalysis &&
        _vm.question.analysis &&
        (_vm.question.analysis.content ||
          (_vm.question.analysis.fileInfo &&
            _vm.question.analysis.fileInfo.fileUrl))
          ? _c(
              "div",
              {
                staticClass: "mt-6 flex-direction-column",
                attrs: { flex: "" },
              },
              [
                _c(
                  "div",
                  {
                    staticClass: "question-label",
                    staticStyle: { "line-height": "40px" },
                  },
                  [_vm._v("解析：")]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "stem",
                    class: {
                      "analysis-content": _vm.question.analysis.content,
                    },
                    staticStyle: {
                      "min-width": "0",
                      flex: "1",
                      padding: "8px",
                      "border-radius": "6px",
                      "background-color": "#f5f5f5",
                    },
                  },
                  [
                    _c("bc-view-common", {
                      attrs: {
                        border: false,
                        content: _vm.question.analysis.content,
                        fileInfo: _vm.question.analysis.fileInfo,
                        resourceBasisPath: _vm.resourceBasisPath,
                        isSubmit: _vm.isSubmit,
                        pageType: _vm.pageType,
                      },
                    }),
                  ],
                  1
                ),
              ]
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowKnowledge && _vm.question.knowledgePointVoList.length
          ? _c("div", { staticClass: "info-item", attrs: { flex: "" } }, [
              _c("div", { staticClass: "info-item--label" }, [
                _vm._v("知识点："),
              ]),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticClass: "info-item--content",
                  staticStyle: { flex: "1" },
                },
                [
                  _vm.question.knowledgePointVoList &&
                  _vm.question.knowledgePointVoList.length
                    ? _vm._l(
                        _vm.question.knowledgePointVoList,
                        function (item) {
                          return _c(
                            "el-tag",
                            {
                              key: item.id,
                              staticStyle: {
                                "margin-right": "8px",
                                "margin-bottom": "8px",
                              },
                              attrs: { effect: "plain" },
                            },
                            [_vm._v(_vm._s(item.kpName))]
                          )
                        }
                      )
                    : [_c("span", [_vm._v("无")])],
                ],
                2
              ),
            ])
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowAi == "0"
          ? _c("div", { staticClass: "AIbtn", on: { click: _vm.AIanalysis } }, [
              _c("img", {
                attrs: {
                  src: __$_require_static_images_leaflet_icons_ai_png__,
                  alt: "",
                },
              }),
            ])
          : _vm._e(),
      ]
    ),
  ])
};
var __vue_staticRenderFns__$5l = [];
__vue_render__$5l._withStripped = true;

  /* style */
  const __vue_inject_styles__$5l = undefined;
  /* scoped */
  const __vue_scope_id__$5l = "data-v-3d67012a";
  /* module identifier */
  const __vue_module_identifier__$5l = undefined;
  /* functional template */
  const __vue_is_functional_template__$5l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5l, staticRenderFns: __vue_staticRenderFns__$5l },
    __vue_inject_styles__$5l,
    __vue_script__$5l,
    __vue_scope_id__$5l,
    __vue_is_functional_template__$5l,
    __vue_module_identifier__$5l,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5k = {
  name: 'XmlQuestionRender',
  components: {
    QuestionItem: __vue_component__$5l
  },
  // dicts: [
  //     "question_type",
  //     "difficulty",
  //     "examine_status",
  //     "answer_platform",
  //     "answer_situation",
  // ],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isSubmit: {
      type: Boolean,
      default: true
    }
  },
  computed: {},
  provide: function () {
    return {
      wrapRoot: this
    };
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
          this.questionSetList = this.blockData.questionSetList;
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-question-property',
      radio1: null,
      questionSetList: [],
      currentInKey: null
    };
  },
  methods: {
    //AI解析
    setanalysis(item) {
      this.$emit('setanalysis', item);
    },
    submitAnswer(val) {
      if (!this.$route.query.templateId) {
        this.mode === 'preview' && this.$emit('submitAnswer', val);
      }
    },
    changeAnswer() {
      for (let i = 0; i < this.questionSetList.length; i++) {
        this.$refs['questionItem_' + this.questionSetList[i].id][0].changeAnswer();
      }
    },
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    uuid(prefix) {
      const s = [];
      const hexDigits = '0123456789abcdef';
      for (let i = 0; i < 36; i += 1) {
        s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
      }
      s[14] = '4';
      s[19] = hexDigits.substr(s[19] & 0x3 | 0x8, 1);
      const id = s.join('');
      return prefix ? prefix + '__' + id : id;
    }
  },
  created() {}
};

/* script */
const __vue_script__$5k = script$5k;
/* template */
var __vue_render__$5k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: "xml-question-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _c("div", { staticClass: "question-box" }, [
        _c(
          "div",
          { staticClass: "question-item" },
          _vm._l(_vm.questionSetList, function (item, index) {
            return _c("QuestionItem", {
              key: item.id,
              ref: "questionItem_" + item.id,
              refInFor: true,
              staticClass: "mb-4 xml-group-item",
              attrs: {
                flex: "",
                inKey: _vm.uuid("qitem__"),
                question: item,
                "can-show-analysis": _vm.pBlockData.showAnalysis,
                "can-show-answer": _vm.pBlockData.showAnswer,
                "can-show-knowledge": _vm.pBlockData.showRecommend,
                border: index < _vm.questionSetList.length - 1 ? true : false,
                "question-number": index,
                "is-show-ai": _vm.isShowAi,
                resourceBasisPath: _vm.resourceBasisPath,
                pageType: _vm.pageType,
                isSubmit: _vm.isSubmit,
              },
              on: {
                submitAnswer: _vm.submitAnswer,
                setanalysis: _vm.setanalysis,
              },
            })
          }),
          1
        ),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$5k = [];
__vue_render__$5k._withStripped = true;

  /* style */
  const __vue_inject_styles__$5k = undefined;
  /* scoped */
  const __vue_scope_id__$5k = "data-v-140f62e6";
  /* module identifier */
  const __vue_module_identifier__$5k = undefined;
  /* functional template */
  const __vue_is_functional_template__$5k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5k, staticRenderFns: __vue_staticRenderFns__$5k },
    __vue_inject_styles__$5k,
    __vue_script__$5k,
    __vue_scope_id__$5k,
    __vue_is_functional_template__$5k,
    __vue_module_identifier__$5k,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5k.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('question-render', {
    prefix: options.prefix
  }), __vue_component__$5k);
};

var epub = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAoCAMAAAChHKjRAAAAAXNSR0IArs4c6QAAAjdQTFRFAAAAiXb/kG/0inD6inX6jW72jXL2eGLpe2fuinL2i3H3iHf6i3L5dmTvjHH3inP4inH2i3L2d2TuhW7vjHH5d2TqjnH5fWnyd2bskHb5jXX1jHH2i3H3i3H4i3H4d2Xsd2Xqi3H4cmLoi3L4inH3i3L3inL3i3L3jHL3hG3zi3L3jHL4jnT5i3L3i3L3jHH2bFPvbl/nb2DncF/ncVP1cWHncWHocWHpclP2cmLpc2Lpc2Lqc2Ppc2PqdGPpdGPqd1r1d1z0d2TreFv2eFz0eFz1eGDxeGbseVv1eVz1el31el32el71el72e133e1/2e2TxfGD1fGXxfWD3fWH2fmjxf2T2f2rwgGX3gGnygWX2gWb2gWb3gWrzgWzzgmb3gmf2gmzyg23zhGj3hGn2hGr2hGr3hG3zhWr3hWv2hWv3hWz3hW7zhW70hmz3h233h273iG34iG/3iW34iW74iW/3iXD3iXH2inD4inH3inP2i3D4i3H3i3H4i3L3i3L4i3P4jHL3jHL4jHP3jHP4jHT2jHT3jHrxjHryjXP4jXT3jXT5jXX3jXvyjnT5jnX4j3T5j3T6j3b3lYH2l4H4mIL4mYP4movynY/xpJL3pJfzpZL5pZjzqJX5q5n5r576saD6uaz3va/7zMT5zcP7z8X70Mb70sj708n708r71c381c761s382ND72db4393549z+4+H55+b66OL96+b+6+r67+v+8/L99PD/9vT+///9///+////0H5GmAAAADB0Uk5TAA0XMjI6OkZNVVpefX+HjJCRkpSeoausra2vs7W2t7i5v8TN1tbd3t/o6+7v8PH8u0jmrQAAAapJREFUOMtjYGBgYBZQ1sMAOpoKHAwIwF+HDfSlG9nzElADVGRuZScEVcNWiVORvY29KESRZDluRfb2VhJgRSp1OBQlgRTZW8uDFKnjUpRrZgtSZamER1Fji5MhSJG9qTgDgxoORXXd5e5mYGDMjltRXV9nTiIIxAkjKeoq8veFg5DK5rqWvgl9QDBRCklRacG8BfOhYMHs/Bq4hBySIu9Fe5DAXH+4hDRCUaXnqj3b12+EgE3rJidjVeS1ZM9ig4AgMAh1S+3HpWiFX2EJGBRXN9fhUrQyBFt0oylaahIWBQZ5bbgVrW2eMhUMyrLacCqCgx2TUnAr2gUFW1szcCpaHd7RCwLtkVlduB1uEREDAvGFXXWUBcFy5+x8MMgrwh2Yyxyy8sCgKDqtB6sijzV7dm7eAgHbNkxLwKaoznvhbqSkMjMYq6La2BlzZkHBnOmZ9VgVtVX4OLpCgUtgUzNWRXV1zS1w0Ig9CHADaZyZEwnIMGgQViTLoEpYkQiDWBUhNTXcDKwEDdJnZGAQJKCmgQtUQvHhVaPNCSkRWQSVdfWwAi1FHiagAgAPu3js6FbZbgAAAABJRU5ErkJggg==";

var resource = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAoCAMAAAChHKjRAAAAAXNSR0IArs4c6QAAAqNQTFRFAAAAiXb/kG/0inD6inX6jW72jXL2eGLpe2fuinL2i3H3iHf6i3L5dmTvjHH3inP4inH2i3L2d2TuhW7vjHH5d2TqjnH5fWnyd2bskHb5jXX1jHH2i3H3i3H4i3H4d2Xsd2Xqi3H4cmLoi3L4inH3i3L3inL3i3L3jHL3hG3zi3L3jHL4jnT5i3L3i3L3jHH2bl/nb2DncFfwcF/ncWHncWHocWHpcljxclnxcmLpc1f0c2Lpc2Lqc2Ppc2PqdGPpdGPqdVX3dVrydl7xd1n2d1r1d1r2d1z0d2TreFr2eFv2eFz2eGDweGDxeGbseVz2eV32el32e171e172e2H1e2PyfWD3fWH2fWL1fmH2fmL2fmP2f2P3f2T2gGrygWX2gWb2gWf2gWzxgWzzgmj2gmzyg2j3g2v1g23zg27xhGn3hGr2hG3zhHDxhW7zhW70hmv4hnHxh233h273h3D0h3LyiG73iW34iXD3iXH2inD3inH3inH4inP2i3D4i3H3i3L3i3P3i3P4jHL3jHL4jHL5jHP3jHP4jHT3jXP4jXP5jXT3jXT4jXT5jXf1jXryjnT5jnX4jnf3j3T5j3T6kX31kYDxkoLxk3v4k4PxlX74lYbxl4L2mInymYP4moT4m4n1n5HzoIz4oY35ppP5ppb2p5rzqZb5rp/4saH5s6P5s6T4tKX5tav1tqn4t6j5uan7urH1vKz8vrH5wbn2x7v7yL37yMH2yb77yb/6ycT2zMH7zMH8zML7zcj2zsT6zsT8z8X70Mb70Mv30sf80sn808741dL31dL41s3819D719P42M/929L93NX84N746OL+6Of67uj/8fH79PD/9fb8+Pn9+vf/+vn//fv//f3//f79/v3//v7////9///+////+rYR5QAAADB0Uk5TAA0XMjI6OkZNVVpefX+HjJCRkpSeoausra2vs7W2t7i5v8TN1tbd3t/o6+7v8PH8u0jmrQAAAe5JREFUOMtjYGBgYBZQ1sMAOpoKHAwIwN+ADUzIMXTgJaAGqMjM2l4IqoatGqciB1sHUYgiyUrcihwcrCXAilQacCjKAClysJEHKVLHpSjf1A6kylIJj6Lmdg8DkCIHE3EGBjUcihp6K/1MwcCYHbeihgmdeakgkCyMqai+HsZqnzB5AhBMkUJT1FYd6+Ud39CGIiiHoqitIth/3rats9zC65GVSSMp6ijyDl985M6DBzf2zA8IrmrDpqg7K2H5uQdQcHRRVHEbFkUpMy48QAJns4uxKIqZcw9Z0enEMiyKJkaUrr0GU3J4dmhWNxZF4RvOLshdexuk5Ni0zM2XlkRjUeSy/8GDM1MLt1w+MTNp9c0HD9Z7YlHkvwNkyIESZ6MV10GslYHYFO0Gu+aK4yaIs7ArOgSRnHQKQq/CpsjtJFjubvdBiKI1XlgUhS0Fy91q3Aumr/alYVHUHLLwPEhRw04geX9Xf3QPFkVtna4+yy4+eFBx6MH9fdPNIydiTQUNPeVWcRu3x647PtcisKsNe1IBRU2Bu1NVum9Qa3dDA05FDQ0tLW01dagJE1MRNkCkInXCimQYNAgrkmVQJaxIhEGslpCaem4GVoIG6TMyMAgSUNPEBSqh+PCq0eaElIgsgsq6eliBliIPE1ABAEFwqt5aEHsEAAAAAElFTkSuQmCC";

var image$1 = {
  leaflet: {
    epub,
    resource
  }
};

//
// import { OuchnOss } from '@/api/business/file/OuchnOss'
// import { defaultCover } from '@/assets/remote'

var script$5j = {
  name: 'XmlResourceRender',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    officePreviewPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      ouchnOss: null,
      image: image$1,
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-resource-property'
    };
  },
  computed: {
    // isNotLocalView() {
    //   return !(process.env.VUE_APP_ENV === 'localview')
    // }
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    countFileSize(fileSize) {
      if (!fileSize) return;
      if (fileSize < 1024) {
        return fileSize + 'B';
      } else if (fileSize < 1024 * 1024) {
        var temp = fileSize / 1024;
        temp = temp.toFixed(2);
        return temp + 'KB';
      } else if (fileSize < 1024 * 1024 * 1024) {
        var temp = fileSize / (1024 * 1024);
        temp = temp.toFixed(2);
        return temp + 'MB';
      } else {
        var temp = fileSize / (1024 * 1024 * 1024);
        temp = temp.toFixed(2);
        return temp + 'GB';
      }
    },
    downloadFile(item) {
      let data = {
        ...item
      };
      data.coverFileUrl = this.resourceBasisPath + data.coverFileUrl.split('./')[1];
      data.uploadFileUrl = this.resourceBasisPath + data.uploadFileUrl.split('./')[1];
      this.$emit('downloadFile', data);
    },
    // 无数据时
    noFileUrlClick() {
      this.$message.warning('没有目标文件');
    },
    downloadFileUrl(item, fileName) {
      let data = {
        ...item
      };
      if (item.uploadFileUrl.indexOf('http') === -1) {
        data.coverFileUrl = this.resourceBasisPath + data.coverFileUrl.split('./')[1];
        data.uploadFileUrl = this.resourceBasisPath + data.uploadFileUrl.split('./')[1];
      }
      if (this.mode == 'preview') {
        this.$emit('downloadFileUrl', {
          item,
          fileName,
          resourceType: this.blockData.resourceType
        });
      } else {
        this.$message.warning('资源仅支持在预览时下载');
      }
    },
    //  跳转office在线预览
    previewToPathClick(item) {
      console.log(item);
      if (this.mode == 'preview') {
        this.$EventBus.$emit('learningStatistics', {
          type: 'doc',
          info: null,
          data: item
        });
        // if (item.fileFormat == 'pdf') {
        //   // const pdfUrl = '/pdfjs/web/viewer.html?file=' + encodeURIComponent(item.uploadFileUrl)
        //   this.$router.push({
        //     path: '/webView',
        //     query: {
        //       baseUrl: encodeURIComponent('/pdfjs/web/viewer.html?file=' + item.uploadFileUrl),
        //       title: item.title
        //     }
        //   })
        //   return
        // }
        if (item.fileFormat == 'zip' || item.fileFormat == 'rar' || item.fileFormat == '7z') return;
        if (item.fileFormat == '无') {
          this.$message.warning('没有目标文件');
          return;
        }
        if (item.fileFormat == '外部链接' && !item.link) {
          this.$message.warning('获取资源链接失败，请检查链接地址');
          return;
        }
        if (item.fileFormat == '外部链接') {
          window.open(item.link);
          return;
        }
        let url = item.uploadFileUrl;
        if (item.uploadFileUrl.indexOf('./') !== -1) {
          url = this.resourceBasisPath + item.uploadFileUrl.split('./')[1];
        }
        if (this.isReader) {
          this.$emit('showFileReader', {
            fileFormat: item.fileFormat,
            fileUrl: url,
            title: item.title
          });
        } else {
          if (item.uploadFileUrl.indexOf('http') === -1) {
            window.open((this.officePreviewPath ? this.officePreviewPath : 'https://view.officeapps.live.com/op/view.aspx?src=') + this.resourceBasisPath + item.uploadFileUrl.split('./')[1]);
          } else {
            window.open((this.officePreviewPath ? this.officePreviewPath : 'https://view.officeapps.live.com/op/view.aspx?src=') + item.uploadFileUrl);
          }
        }
      }
    }
  },
  created() {
    // if (this.isNotLocalView) {
    //   this.ouchnOss = new OuchnOss(true)
    // }
  }
};

var __$_require_static_images_default_over_resource_png__ = "data:image/png;base64,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";

var __$_require_static_images_leaflet_icons_download_bule_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAWCAYAAADafVyIAAAAAXNSR0IArs4c6QAAAkVJREFUSEvtlEtoE1EUhv9zJ6lpCkYzBl3oSouioosiWII2kwas4gMXdeFSt0ViR0lXmu6UNpN0IejGunGhIPjowlczWdiN6ELFhYgbG8RHJglYsWSSe2QC1dqal5CF4F0Nc8//f3P/O+cQ2ryozf5oCRBOWpPMiCiCgo+j/vfNfFxLAM2wXgPYCpZHTD1wtw2AXAagPjBHTH3N9H/AsgQ041+IqOcKu/3FXOBRLPBh6RHqnSByIe8TSmnFw7PrPi/WLftNtVThHKQcJaKL6dP+kcXFtQB9qdwuIWkGwDdzWF1dH5DMB8E8BWAVBCbNqHpiQfATIDBgRtUHznstVTgMKe84z8QYTetqvC7A2ew1Zjs98DqNFAHwrGTL8Ews8FUzrCcAgkLQzumo/2UoacWJcd7RMLA/M6zeXxpr3U7WjNx1gI4D4pNS4s2VDk4AOMlwdTPKhwRgAPgOLvea+toXf2q8hqMibFjjDOgALAAfAWwD8TswbSTglbRxIBNTs7W6uiGgmrNhHQNw47dsmW6ndf/RRuOiKYBj0p/4okkSzvwhMF0ydf9QI/PBm6zQ3on8lMuW2+c8HT1Ph1Y6MdRc/WP5HVJwt6mrtxqZa+PWVSjYR7uvFdiXraDk5i1mLPCmkbDZfc2wZgGsp+DlXNFbgM/uog2ZU7Uvq1njhbpQMv9WsNxEexLFMY8snZnvVJ6zVEbcJRQAu1W/X/Xc4bK9GFDsctxVRrl6yaGJ/D3FrhxkEsyEub93ryoVkuwlwdl5T9fgD6jm9+bSxCy2AAAAAElFTkSuQmCC";

var __$_require_static_images_leaflet_icons_resource_icon_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAeCAYAAAA/xX6fAAAAAXNSR0IArs4c6QAAAqdJREFUSEvFls9LVFEUx7/fOzZTiWPjFKhgUG5Mg0QNQQpRgpCC/ol+ULSwtlFQQctcucj8I2oRVAszDENwrMBRCWpTGdX8yNFsZsx74j5nnjqN43tPpbN73HPO59x7frxDFEg0Lo2EPi9gDwSHrGPiIyFPBWqwKcypQhs338wrR0X8TOr7IrwMQG3gRAPyMLiortXV8bcbUF7XAhoYEvIEwCmHToZQxZ4mMutQ31azgJOx5X6SV9wYk9LfWOW76sbGyk40Lk2AvAPgc2csqPfNLAWYjgGYAvEc8D1isPl9KT+cjC/3Eex1B1vRrlLfUa2+FJqOQPEmK1peFvPJaFxPA2jwAgwwjXqfMf9HBMQAKlK9ZFd67akBZgD4vQAJwZGyt6VMXwOZs6zsSNhVuhWggkZDmUl/SRnDL+lmbdtirmh25EnXRyAc5L6WCxZwa0XzA9Xq82Y3NOcCxS5TSJxKyFERMYnw0hYwheNQXrGy9aTnxg+pGGrUJ4esnJpmsz3a/Mn0dFYCh514KOc8Dvo+wFSpO+Ede3jrn2+Gv0ltZ0IfKOnD3Mw0O6HdsayK4bANlLnILICajOxGUvZjQYJYEr91i13MwtwqxJibnBUL6OtaoOcB4OKq2f8KtJ7URbReVNc8aSoyBEGXFy+ObdYXzcRtQG45NvakyLurOUxOHIOyJs7OSb7x8wSZi4wAOLFDxNXRZgPnJzqh5YW1GG6vCLTuZuj4auPb0FTkAQQXt5VHDDDYeskaNoWOZXZ8L8o5BKB9m6Drf8DFnEpqLAwpM3vqVqFj4J8zDLbH85wNcyUyugfzgb7c87rN6coStSDX86vFpsCCQroHoMPhE49C6xumQIrpO45cUuMN0OoclJw2yxqAcM5hHMQ0NJ9B6ccMts2UCuwvOp8JMVRGUysAAAAASUVORK5CYII=";

var __$_require_static_images_leaflet_icons_resource_elliptic_png__ = "data:image/png;base64,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";

var __$_require_static_images_leaflet_icons_download_white_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAAAXNSR0IArs4c6QAAAjlJREFUSEvtlcurjWEUh59fSpFLJOUUcquDciuXRExkrkxMMDkGkpPEgAEDipJIIuU2MPAnKJLEQK4hhDoZSCEhcv1p7dbJbnf23t+3nW201+Sr911rPd+6vqJFsT0NeJ7mOyUdLONKZZSrdW1PBV7kWa+kI2V8/Qt4CvAyYVskHeuAG2Wgk+rC/WG701yDN062J0p6NVD+i6ba9gTgvaRv1X7qdrXtS8AqYI+kvbXwImDb64DzuVpnSnK/n0bgR8CsVNwnaXfNymzYXLY3AqfT5hcwVNLvIuAxwA2gO5X3S9rVb9goYtvrgbOpGyleKulOoVSHku3hwGNgchodkrQ972YAz/J8q6Sjed4DnMzz78AiSfdrS9V0c9keD1wHAhRSidz2WOBdnvVIOlWT3oh0uaRbAzVnU3BGMQ64CUxPJ5slHbf9BRgGrAA+A7fz/gewWNLdehupEDjhAYi/n53OLgArgS7gakQHDAG+AkskPWi0BguDEz4y0z6njtOALpD0pBE07kqBqxruIRDjVC0/gfmS4q6plAYnfFTWs7/mUdOItBC0ErHtgG8D3ko61/RXU8H26KxtfNdIulfCdlmA1wIX06hL0uuiDlrRsx1N+jHAO4AD6aRb0tNWHBa1yb3wJsC9wOH/GPGISlltbwDOJHhus/krGlk9Pdsxkh8CPAnoS8XLwGpJ8Zq0RWyfADZVxsl2PF/xjIV8Aq4B7YDPAyLQvwvE9hVgIRA1aKfEdutraYEMxl/9AUsz/weMzneQAAAAAElFTkSuQmCC";

/* script */
const __vue_script__$5j = script$5j;
var __vue_render__$5j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: "xml-resource-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _c("div", { staticClass: "resource-box" }, [
        _c(
          "div",
          {
            class: [
              "preview-container-resource",
              "preview-container-resource-" + _vm.mode,
            ],
          },
          [
            _vm.blockData.styleMode == "A" || !_vm.blockData.styleMode
              ? _c(
                  "div",
                  [
                    _vm.blockData.resourceSetList &&
                    _vm.blockData.resourceSetList.length
                      ? _vm._l(
                          _vm.blockData.resourceSetList,
                          function (item, index) {
                            return _c(
                              "div",
                              {
                                key: index,
                                staticClass:
                                  "workbench-shop-item resource-style1 xml-group-item",
                              },
                              [
                                _c(
                                  "div",
                                  {
                                    staticClass:
                                      "shop-item-img slip-over-cover",
                                    on: {
                                      click: function ($event) {
                                        return _vm.previewToPathClick(item)
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      staticClass:
                                        "shop-item-img-resource xml-round-6",
                                      attrs: {
                                        src:
                                          item.coverFileUrl &&
                                          item.coverFileUrl.indexOf(
                                            "http://124.133.23.84:20000"
                                          ) === -1
                                            ? item.coverFileUrl.indexOf(
                                                "./"
                                              ) !== -1
                                              ? _vm.resourceBasisPath +
                                                item.coverFileUrl.split("./")[1]
                                              : item.coverFileUrl
                                            : __$_require_static_images_default_over_resource_png__,
                                      },
                                    }),
                                  ]
                                ),
                                _vm._v(" "),
                                _c("div", { staticClass: "shop-item-desc" }, [
                                  _c("div", { staticClass: "filecont" }, [
                                    _c(
                                      "div",
                                      {
                                        staticClass:
                                          "xml-resource-title shop-item-desc-title preview-item-file",
                                        on: {
                                          click: function ($event) {
                                            return _vm.previewToPathClick(item)
                                          },
                                        },
                                      },
                                      [
                                        _vm._v(
                                          "\n                  " +
                                            _vm._s(
                                              item.title
                                                ? item.title
                                                : "资源样例"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _vm._v(
                                          "\n                  文件格式：" +
                                            _vm._s(
                                              item.fileFormat
                                                ? item.fileFormat
                                                : "无"
                                            ) +
                                            "\n\n                  "
                                        ),
                                        _vm.blockData.resourceType == "0" &&
                                        item.source != ""
                                          ? _c(
                                              "div",
                                              {
                                                staticClass: "pointer",
                                                on: {
                                                  click: function ($event) {
                                                    $event.stopPropagation();
                                                    return _vm.downloadFileUrl(
                                                      item,
                                                      item.title +
                                                        "." +
                                                        item.fileFormat
                                                    )
                                                  },
                                                },
                                              },
                                              [
                                                _c("img", {
                                                  staticClass:
                                                    "shop-item-img-resource",
                                                  attrs: {
                                                    src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                                  },
                                                }),
                                                _vm._v(
                                                  "立即下载\n                  "
                                                ),
                                              ]
                                            )
                                          : _vm._e(),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _vm._v(
                                          "\n                  文件大小：" +
                                            _vm._s(
                                              _vm.countFileSize(item.fileSize)
                                                ? _vm.countFileSize(
                                                    item.fileSize
                                                  )
                                                : "无"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                  ]),
                                ]),
                              ]
                            )
                          }
                        )
                      : [
                          _c(
                            "div",
                            {
                              staticClass:
                                "workbench-shop-item resource-style1 xml-group-item",
                            },
                            [
                              _c("div", { staticClass: "shop-item-img" }, [
                                _c("img", {
                                  staticClass: "shop-item-img-resource",
                                  attrs: {
                                    src: __$_require_static_images_default_over_resource_png__,
                                  },
                                }),
                              ]),
                              _vm._v(" "),
                              _c("div", { staticClass: "shop-item-desc" }, [
                                _vm._m(0),
                                _vm._v(" "),
                                _vm.blockData.resourceType == "0"
                                  ? _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _c("div", [
                                          _c("img", {
                                            staticClass:
                                              "shop-item-img-resource",
                                            attrs: {
                                              src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                            },
                                          }),
                                          _vm._v("立即下载\n                "),
                                        ]),
                                      ]
                                    )
                                  : _vm._e(),
                              ]),
                            ]
                          ),
                        ],
                  ],
                  2
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.blockData.styleMode == "B"
              ? _c(
                  "div",
                  [
                    _vm.blockData.resourceSetList &&
                    _vm.blockData.resourceSetList.length
                      ? _vm._l(
                          _vm.blockData.resourceSetList,
                          function (item, index) {
                            return _c(
                              "div",
                              {
                                key: index,
                                staticClass:
                                  "style-mode__item--body xml-group-item",
                              },
                              [
                                _c("div", { staticClass: "right" }, [
                                  _c(
                                    "div",
                                    {
                                      staticClass: "title",
                                      on: {
                                        click: function ($event) {
                                          return _vm.previewToPathClick(item)
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_leaflet_icons_resource_icon_png__,
                                          alt: "",
                                        },
                                      }),
                                      _vm._v(" "),
                                      _c(
                                        "span",
                                        { staticClass: "xml-resource-title" },
                                        [
                                          _vm._v(
                                            _vm._s(
                                              item.title
                                                ? item.title
                                                : "资源样例"
                                            )
                                          ),
                                        ]
                                      ),
                                    ]
                                  ),
                                  _vm._v(" "),
                                  _c("div", { staticClass: "downloadbtn" }, [
                                    _vm.blockData.resourceType == "0" &&
                                    item.source != ""
                                      ? _c(
                                          "div",
                                          {
                                            staticClass: "pointer",
                                            on: {
                                              click: function ($event) {
                                                $event.stopPropagation();
                                                return _vm.downloadFileUrl(
                                                  item,
                                                  item.title +
                                                    "." +
                                                    item.fileFormat
                                                )
                                              },
                                            },
                                          },
                                          [
                                            _c("img", {
                                              staticClass:
                                                "shop-item-img-resource",
                                              attrs: {
                                                src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                              },
                                            }),
                                            _vm._v(
                                              "立即下载\n                "
                                            ),
                                          ]
                                        )
                                      : _vm._e(),
                                  ]),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "left" }, [
                                  _c(
                                    "div",
                                    {
                                      staticClass: "default-cover",
                                      on: {
                                        click: function ($event) {
                                          return _vm.previewToPathClick(item)
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        staticClass:
                                          "shop-item-img-resource xml-round-6",
                                        attrs: {
                                          src:
                                            item.coverFileUrl &&
                                            item.coverFileUrl.indexOf(
                                              "http://124.133.23.84:20000"
                                            ) === -1
                                              ? item.coverFileUrl.indexOf(
                                                  "./"
                                                ) !== -1
                                                ? _vm.resourceBasisPath +
                                                  item.coverFileUrl.split(
                                                    "./"
                                                  )[1]
                                                : item.coverFileUrl
                                              : __$_require_static_images_default_over_resource_png__,
                                        },
                                      }),
                                    ]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "div",
                                    { staticClass: "cover-bot-elliptic" },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_leaflet_icons_resource_elliptic_png__,
                                          alt: "",
                                        },
                                      }),
                                    ]
                                  ),
                                ]),
                              ]
                            )
                          }
                        )
                      : [
                          _c(
                            "div",
                            {
                              staticClass:
                                "style-mode__item--body xml-group-item",
                            },
                            [
                              _c("div", { staticClass: "right" }, [
                                _c("div", { staticClass: "title" }, [
                                  _c("img", {
                                    attrs: {
                                      src: __$_require_static_images_leaflet_icons_resource_icon_png__,
                                      alt: "",
                                    },
                                  }),
                                  _vm._v("资源名称\n              "),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "downloadbtn" }, [
                                  _vm.blockData.resourceType == "0"
                                    ? _c("div", [
                                        _c("img", {
                                          staticClass: "shop-item-img-resource",
                                          attrs: {
                                            src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                          },
                                        }),
                                        _vm._v("立即下载\n                "),
                                      ])
                                    : _vm._e(),
                                ]),
                              ]),
                              _vm._v(" "),
                              _c("div", { staticClass: "left" }, [
                                _c("div", { staticClass: "default-cover" }, [
                                  _c("img", {
                                    staticClass: "shop-item-img-resource",
                                    attrs: {
                                      src: __$_require_static_images_default_over_resource_png__,
                                    },
                                  }),
                                ]),
                                _vm._v(" "),
                                _c(
                                  "div",
                                  { staticClass: "cover-bot-elliptic" },
                                  [
                                    _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_leaflet_icons_resource_elliptic_png__,
                                        alt: "",
                                      },
                                    }),
                                  ]
                                ),
                              ]),
                            ]
                          ),
                        ],
                  ],
                  2
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.blockData.styleMode == "C"
              ? _c(
                  "div",
                  [
                    _vm.blockData.resourceSetList &&
                    _vm.blockData.resourceSetList.length
                      ? [
                          _c(
                            "div",
                            { staticClass: "style-mode__item--body" },
                            [
                              _c(
                                "el-row",
                                _vm._l(
                                  _vm.blockData.resourceSetList,
                                  function (item, index) {
                                    return _c(
                                      "el-col",
                                      { key: index, attrs: { span: 6 } },
                                      [
                                        _c(
                                          "div",
                                          {
                                            staticClass:
                                              "filecard xml-group-item",
                                          },
                                          [
                                            _c(
                                              "div",
                                              { staticClass: "default-cover" },
                                              [
                                                _c("img", {
                                                  staticClass:
                                                    "shop-item-img-resource xml-round-6",
                                                  attrs: {
                                                    src:
                                                      item.coverFileUrl &&
                                                      item.coverFileUrl.indexOf(
                                                        "http://124.133.23.84:20000"
                                                      ) === -1
                                                        ? item.coverFileUrl.indexOf(
                                                            "./"
                                                          ) !== -1
                                                          ? _vm.resourceBasisPath +
                                                            item.coverFileUrl.split(
                                                              "./"
                                                            )[1]
                                                          : item.coverFileUrl
                                                        : __$_require_static_images_default_over_resource_png__,
                                                  },
                                                }),
                                                _vm._v(" "),
                                                _vm.blockData.resourceType ==
                                                  "0" && item.source != ""
                                                  ? _c(
                                                      "div",
                                                      {
                                                        staticClass:
                                                          "downloadmask pointer",
                                                        on: {
                                                          click: function (
                                                            $event
                                                          ) {
                                                            $event.stopPropagation();
                                                            return _vm.downloadFileUrl(
                                                              item,
                                                              item.title +
                                                                "." +
                                                                item.fileFormat
                                                            )
                                                          },
                                                        },
                                                      },
                                                      [
                                                        _c("img", {
                                                          staticClass:
                                                            "shop-item-img-resource",
                                                          attrs: {
                                                            src: __$_require_static_images_leaflet_icons_download_white_png__,
                                                          },
                                                        }),
                                                        _vm._v(
                                                          "\n                      立即下载\n                    "
                                                        ),
                                                      ]
                                                    )
                                                  : _vm._e(),
                                              ]
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "div",
                                              {
                                                staticClass:
                                                  "xml-resource-title filename",
                                                on: {
                                                  click: function ($event) {
                                                    return _vm.previewToPathClick(
                                                      item
                                                    )
                                                  },
                                                },
                                              },
                                              [
                                                _vm._v(
                                                  "\n                    " +
                                                    _vm._s(
                                                      item.title
                                                        ? item.title
                                                        : "资源样例"
                                                    ) +
                                                    "\n                  "
                                                ),
                                              ]
                                            ),
                                          ]
                                        ),
                                      ]
                                    )
                                  }
                                ),
                                1
                              ),
                            ],
                            1
                          ),
                        ]
                      : [
                          _c(
                            "div",
                            { staticClass: "style-mode__item--body" },
                            [
                              _c(
                                "el-row",
                                [
                                  _c("el-col", { attrs: { span: 8 } }, [
                                    _c(
                                      "div",
                                      {
                                        staticClass: "filecard xml-group-item",
                                      },
                                      [
                                        _c(
                                          "div",
                                          { staticClass: "default-cover" },
                                          [
                                            _c("img", {
                                              staticClass:
                                                "shop-item-img-resource",
                                              attrs: {
                                                src: __$_require_static_images_default_over_resource_png__,
                                              },
                                            }),
                                            _vm._v(" "),
                                            _vm.blockData.resourceType == "0"
                                              ? _c(
                                                  "div",
                                                  {
                                                    staticClass: "downloadmask",
                                                  },
                                                  [
                                                    _c("img", {
                                                      staticClass:
                                                        "shop-item-img-resource",
                                                      attrs: {
                                                        src: __$_require_static_images_leaflet_icons_download_white_png__,
                                                      },
                                                    }),
                                                    _vm._v(
                                                      "\n                      立即下载\n                    "
                                                    ),
                                                  ]
                                                )
                                              : _vm._e(),
                                          ]
                                        ),
                                        _vm._v(" "),
                                        _c("div", { staticClass: "filename" }, [
                                          _vm._v(
                                            "资源名称资源名称资源名称资源名称资源名称"
                                          ),
                                        ]),
                                      ]
                                    ),
                                  ]),
                                ],
                                1
                              ),
                            ],
                            1
                          ),
                        ],
                  ],
                  2
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.blockData.styleMode == "D"
              ? _c(
                  "div",
                  [
                    _vm.blockData.resourceSetList &&
                    _vm.blockData.resourceSetList.length
                      ? _vm._l(
                          _vm.blockData.resourceSetList,
                          function (item, index) {
                            return _c(
                              "div",
                              {
                                key: index,
                                staticClass:
                                  "workbench-shop-item resource-style4 xml-group-item",
                              },
                              [
                                _c(
                                  "div",
                                  {
                                    staticClass:
                                      "shop-item-img slip-over-cover",
                                    on: {
                                      click: function ($event) {
                                        return _vm.previewToPathClick(item)
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      staticClass:
                                        "shop-item-img-resource xml-round-6",
                                      attrs: {
                                        src:
                                          item.coverFileUrl &&
                                          item.coverFileUrl.indexOf(
                                            "http://124.133.23.84:20000"
                                          ) === -1
                                            ? item.coverFileUrl.indexOf(
                                                "./"
                                              ) !== -1
                                              ? _vm.resourceBasisPath +
                                                item.coverFileUrl.split("./")[1]
                                              : item.coverFileUrl
                                            : __$_require_static_images_default_over_resource_png__,
                                      },
                                    }),
                                  ]
                                ),
                                _vm._v(" "),
                                _c("div", { staticClass: "shop-item-desc" }, [
                                  _c("div", { staticClass: "filecont" }, [
                                    _c(
                                      "div",
                                      {
                                        staticClass:
                                          "shop-item-desc-title preview-item-file",
                                        on: {
                                          click: function ($event) {
                                            return _vm.previewToPathClick(item)
                                          },
                                        },
                                      },
                                      [
                                        _vm._v(
                                          "\n                  " +
                                            _vm._s(
                                              item.title
                                                ? item.title
                                                : "资源样例"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _vm._v(
                                          "\n                  文件格式：" +
                                            _vm._s(
                                              item.fileFormat
                                                ? item.fileFormat
                                                : "无"
                                            ) +
                                            "\n\n                  "
                                        ),
                                        _vm.blockData.resourceType == "0" &&
                                        item.source != ""
                                          ? _c(
                                              "div",
                                              {
                                                staticClass: "pointer",
                                                on: {
                                                  click: function ($event) {
                                                    $event.stopPropagation();
                                                    return _vm.downloadFileUrl(
                                                      item,
                                                      item.title +
                                                        "." +
                                                        item.fileFormat
                                                    )
                                                  },
                                                },
                                              },
                                              [
                                                _c("img", {
                                                  staticClass:
                                                    "shop-item-img-resource",
                                                  attrs: {
                                                    src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                                  },
                                                }),
                                                _vm._v(
                                                  "立即下载\n                  "
                                                ),
                                              ]
                                            )
                                          : _vm._e(),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _vm._v(
                                          "\n                  文件大小：" +
                                            _vm._s(
                                              _vm.countFileSize(item.fileSize)
                                                ? _vm.countFileSize(
                                                    item.fileSize
                                                  )
                                                : "无"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                  ]),
                                ]),
                              ]
                            )
                          }
                        )
                      : [
                          _c(
                            "div",
                            {
                              staticClass:
                                "workbench-shop-item resource-style4 xml-group-item",
                            },
                            [
                              _c("div", { staticClass: "shop-item-img" }, [
                                _c("img", {
                                  staticClass: "shop-item-img-resource",
                                  attrs: {
                                    src: __$_require_static_images_default_over_resource_png__,
                                  },
                                }),
                              ]),
                              _vm._v(" "),
                              _c("div", { staticClass: "shop-item-desc" }, [
                                _vm._m(1),
                                _vm._v(" "),
                                _vm.blockData.resourceType == "0"
                                  ? _c(
                                      "div",
                                      { staticClass: "shop-item-desc-detail" },
                                      [
                                        _c("div", [
                                          _c("img", {
                                            staticClass:
                                              "shop-item-img-resource",
                                            attrs: {
                                              src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                            },
                                          }),
                                          _vm._v("立即下载\n                "),
                                        ]),
                                      ]
                                    )
                                  : _vm._e(),
                              ]),
                            ]
                          ),
                        ],
                  ],
                  2
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.blockData.styleMode == "E"
              ? _c(
                  "div",
                  [
                    _vm.blockData.resourceSetList &&
                    _vm.blockData.resourceSetList.length
                      ? _vm._l(
                          _vm.blockData.resourceSetList,
                          function (item, index) {
                            return _c(
                              "div",
                              {
                                key: index,
                                staticClass:
                                  "workbench-shop-item resource-style5 xml-group-item",
                              },
                              [
                                _c(
                                  "div",
                                  {
                                    staticClass:
                                      "shop-item-img slip-over-cover",
                                    on: {
                                      click: function ($event) {
                                        return _vm.previewToPathClick(item)
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      staticClass:
                                        "shop-item-img-resource xml-round-6",
                                      attrs: {
                                        src:
                                          item.coverFileUrl &&
                                          item.coverFileUrl.indexOf(
                                            "http://124.133.23.84:20000"
                                          ) === -1
                                            ? item.coverFileUrl.indexOf(
                                                "./"
                                              ) !== -1
                                              ? _vm.resourceBasisPath +
                                                item.coverFileUrl.split("./")[1]
                                              : item.coverFileUrl
                                            : __$_require_static_images_default_over_resource_png__,
                                      },
                                    }),
                                  ]
                                ),
                                _vm._v(" "),
                                _c("div", { staticClass: "shop-item-desc" }, [
                                  _c("div", { staticClass: "filecont" }, [
                                    _c(
                                      "div",
                                      {
                                        staticClass:
                                          "shop-item-desc-title shop-item-desc-title2 preview-item-file",
                                        on: {
                                          click: function ($event) {
                                            return _vm.previewToPathClick(item)
                                          },
                                        },
                                      },
                                      [
                                        _vm._v(
                                          "\n                  " +
                                            _vm._s(
                                              item.title
                                                ? item.title
                                                : "资源样例"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      {
                                        staticClass:
                                          "shop-item-desc-detail shop-item-desc-detail2",
                                      },
                                      [
                                        _vm._v(
                                          "\n                  文件格式：" +
                                            _vm._s(
                                              item.fileFormat
                                                ? item.fileFormat
                                                : "无"
                                            ) +
                                            "\n\n                  "
                                        ),
                                        _vm.blockData.resourceType == "0" &&
                                        item.source != ""
                                          ? _c(
                                              "div",
                                              {
                                                staticClass: "pointer",
                                                on: {
                                                  click: function ($event) {
                                                    $event.stopPropagation();
                                                    return _vm.downloadFileUrl(
                                                      item,
                                                      item.title +
                                                        "." +
                                                        item.fileFormat
                                                    )
                                                  },
                                                },
                                              },
                                              [
                                                _c("img", {
                                                  staticClass:
                                                    "shop-item-img-resource",
                                                  attrs: {
                                                    src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                                  },
                                                }),
                                                _vm._v(
                                                  "立即下载\n                  "
                                                ),
                                              ]
                                            )
                                          : _vm._e(),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      {
                                        staticClass:
                                          "shop-item-desc-detail shop-item-desc-detail2",
                                      },
                                      [
                                        _vm._v(
                                          "\n                  文件大小：" +
                                            _vm._s(
                                              _vm.countFileSize(item.fileSize)
                                                ? _vm.countFileSize(
                                                    item.fileSize
                                                  )
                                                : "无"
                                            ) +
                                            "\n                "
                                        ),
                                      ]
                                    ),
                                  ]),
                                ]),
                              ]
                            )
                          }
                        )
                      : [
                          _c(
                            "div",
                            {
                              staticClass:
                                "workbench-shop-item resource-style5 xml-group-item",
                            },
                            [
                              _c("div", { staticClass: "shop-item-img" }, [
                                _c("img", {
                                  staticClass: "shop-item-img-resource",
                                  attrs: {
                                    src: __$_require_static_images_default_over_resource_png__,
                                  },
                                }),
                              ]),
                              _vm._v(" "),
                              _c("div", { staticClass: "shop-item-desc" }, [
                                _vm._m(2),
                                _vm._v(" "),
                                _vm.blockData.resourceType == "0"
                                  ? _c(
                                      "div",
                                      {
                                        staticClass:
                                          "shop-item-desc-detail shop-item-desc-detail2",
                                      },
                                      [
                                        _c("div", [
                                          _c("img", {
                                            staticClass:
                                              "shop-item-img-resource",
                                            attrs: {
                                              src: __$_require_static_images_leaflet_icons_download_bule_png__,
                                            },
                                          }),
                                          _vm._v("立即下载\n                "),
                                        ]),
                                      ]
                                    )
                                  : _vm._e(),
                              ]),
                            ]
                          ),
                        ],
                  ],
                  2
                )
              : _vm._e(),
          ]
        ),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$5j = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", [
      _c("div", { staticClass: "shop-item-desc-title" }, [_vm._v("资源样例")]),
      _vm._v(" "),
      _c("div", { staticClass: "shop-item-desc-detail" }, [
        _vm._v("文件格式：无"),
      ]),
      _vm._v(" "),
      _c("div", { staticClass: "shop-item-desc-detail" }, [
        _vm._v("文件大小：0"),
      ]),
    ])
  },
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", [
      _c("div", { staticClass: "shop-item-desc-title" }, [_vm._v("资源样例")]),
      _vm._v(" "),
      _c("div", { staticClass: "shop-item-desc-detail" }, [
        _vm._v("文件格式：无"),
      ]),
      _vm._v(" "),
      _c("div", { staticClass: "shop-item-desc-detail" }, [
        _vm._v("文件大小：0"),
      ]),
    ])
  },
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", [
      _c("div", { staticClass: "shop-item-desc-title shop-item-desc-title2" }, [
        _vm._v("资源样例"),
      ]),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "shop-item-desc-detail shop-item-desc-detail2" },
        [_vm._v("文件格式：无")]
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "shop-item-desc-detail shop-item-desc-detail2" },
        [_vm._v("文件大小：0")]
      ),
    ])
  },
];
__vue_render__$5j._withStripped = true;

  /* style */
  const __vue_inject_styles__$5j = undefined;
  /* scoped */
  const __vue_scope_id__$5j = "data-v-b3bca8c6";
  /* module identifier */
  const __vue_module_identifier__$5j = undefined;
  /* functional template */
  const __vue_is_functional_template__$5j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5j, staticRenderFns: __vue_staticRenderFns__$5j },
    __vue_inject_styles__$5j,
    __vue_script__$5j,
    __vue_scope_id__$5j,
    __vue_is_functional_template__$5j,
    __vue_module_identifier__$5j,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5j.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('resource-render', {
    prefix: options.prefix
  }), __vue_component__$5j);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$5i = {
  name: 'PdfView',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    baseUrl: {
      type: String,
      default: ''
    },
    textInfo: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'pc'
    }
  },
  data() {
    return {
      src: encodeURI('http://ouchn-textbook-public.oss-cn-qingdao.aliyuncs.com/ouchn-test-paper-web/stem-files/faf5276a-e523-4df1-8d11-ac858c954cf3.pdf'),
      loading: false,
      width: 300,
      height: 900
    };
  },
  methods: {
    onBackClick() {
      this.$emit('handleClose', false);
    }
  }
};

var __$_require_static_images_leaflet_icons_back_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACYAAABACAYAAAB2kAXpAAAAAXNSR0IArs4c6QAABABJREFUaEPVmluopmMUx39/UUy44cJMg+FikEMuGMnIOJcwlHIYpKkZmchxuJDT5MIpIzXIpMkhh5QwlPPx0uHKhbiYKCJXSMTor7U9W4/t29/3rP3t55usm137+z/v+r3Pca31vGIHme19gVXAEuBXYKukd6ZxtCO4bJ8HPAHsNsP/RknXxf8mDmZ7NfDYkA65UtKmiYLZvhp4YMQofSVpycTAbN8M3NkwdbZL2mUiYLbvBW5ogArJ15L27w5m+yHgikaokK2WtKUrmO2ngQsTUBsk3dZ1Vdp+EViZgFov6b5u+5jtnYE3gBMTUOskPVzr53UobS8A3gWWJaBWSYoh/5fNG5jtfYA4Ug5JQJ0j6aVB+nkBs70f8CEQf1tsO3CqpPdmE48NZvswIBzs1UJUDuwTJH00TD8WmO2jgbeBPRqhfgBOkvTZKP2cwWyvKFA7jXJSfv8GOF7Sthb9nMBsx/4U+1SrfQ6skPR9a4M0mO1LgcdbHQAxl2Ki/5hok4vHbK8DNiUcxPZxmqQ/E22mpM09Zvsm4K6EgwiVz07o8xus7TuAWxNOnpF0UUL/H+nIHrP9IHBVwslmSWsT+oHSoWC2twCXJZzcL+n6hH5W6axgtl8Azk04uV1SDPm82EAw26/Hakp4uFbSqCQj8bgZq7KELW8BxyaeslbS5oS+SfpPj9neE3gfOLKp5d+iCyQ9l9A3S6fASroeEcKBzS3hLEmvJPQpqWwvLWHLwsaWv5cj5oNG/ZxkAfZTImz5uRzGn87JW6JRgLlR/20kGJK+aNSPJWsF+wRYLum3sbwlGv/vweJdYyhPlhRBX3eLHvsDiCS1xX4pk//jFvE4mgA7puSDkay2WLxIBH+zpl4tDxmlmd5gDy9we49qUP2+UtLLCX1KWh9JB5SNtjVpDUd9j6TpVylpfhzihyZer+8hXsHtXgojRyXg+oY9FVys0jdjBSbg+geKFeBW4MwEXP/QuoJ7Fjg/Adc/GangHgEuT8D1T98quHuA9Qm4/glvBXcLsCEBFyWC0yVFoS5lIxPemU+zfQ2wMeElztVTuhZVqp5bAzyagOtfhqrgYqXGim21/oW7Cu4MIDKl1inRv9RZwR0HxPm6a2PXxW1u3+JwBRdJciTLkTS3WP9yegV3UInpFrWQFU3fC4gKbjEQiXDEdq3W98qmgouLiNhYj2glA/peclVw8XVA3MAtT8D1vRasQWy/FkdSAq7/RWrVe88D8Z1Fq10i6anWjbH1oQN1yVrul5KWTgQsaBPV78l93lANa3x3Ed9fDLPvJC2cWI9VcDcCdw8hm1qdEwcrwxphelzOz/T/pKS4RGuOCsaa/IMa2z4YuLicElHVfLWu6f4FuhpYUNovLf0AAAAASUVORK5CYII=";

/* script */
const __vue_script__$5i = script$5i;
var __vue_render__$5i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _vm.pageType == "pc"
        ? _c(
            "el-dialog",
            {
              attrs: {
                visible: _vm.show,
                width: "600px",
                top: "80px",
                "custom-class": "xml-epub-pdf-dialog",
                "modal-append-to-body": "",
                "before-close": _vm.onBackClick,
              },
              on: {
                "update:visible": function ($event) {
                  _vm.show = $event;
                },
              },
            },
            [
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "loading",
                      rawName: "v-loading",
                      value: _vm.loading,
                      expression: "loading",
                    },
                  ],
                  staticClass: "mod-config",
                  staticStyle: { height: "80vh", width: "100%" },
                  attrs: {
                    "element-loading-text": "拼命加载中",
                    "element-loading-spinner": "el-icon-loading",
                    "element-loading-background": "rgba(0, 0, 0, 0.8)",
                  },
                },
                [
                  _c("iframe", {
                    attrs: { width: "100%", height: "100%", src: _vm.baseUrl },
                  }),
                ]
              ),
            ]
          )
        : _c(
            "div",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: _vm.show,
                  expression: "show",
                },
              ],
              staticClass: "xml-epub-pdf-view",
            },
            [
              _vm.textInfo
                ? _c("div", { staticClass: "xmlPreviewSearch-title" }, [
                    _c("img", {
                      staticClass: "img",
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_back_png__,
                      },
                      on: { click: _vm.onBackClick },
                    }),
                    _vm._v(" "),
                    _c("div", { staticClass: "xmlPreviewSearch-sou" }, [
                      _vm._v(_vm._s(_vm.textInfo)),
                    ]),
                  ])
                : _c("div", { staticClass: "xmlPreviewSearch-navbar" }, [
                    _c("i", {
                      staticClass: "el-icon-close",
                      on: { click: _vm.onBackClick },
                    }),
                  ]),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "loading",
                      rawName: "v-loading",
                      value: _vm.loading,
                      expression: "loading",
                    },
                  ],
                  staticClass: "mod-config",
                  staticStyle: { height: "calc(100vh - 60px)", width: "100%" },
                  attrs: {
                    "element-loading-text": "拼命加载中",
                    "element-loading-spinner": "el-icon-loading",
                    "element-loading-background": "rgba(0, 0, 0, 0.8)",
                  },
                },
                [
                  _c("iframe", {
                    staticClass: "xml-iframe-web-view",
                    attrs: { frameborder: "0", src: _vm.baseUrl },
                  }),
                ]
              ),
            ]
          ),
    ],
    1
  )
};
var __vue_staticRenderFns__$5i = [];
__vue_render__$5i._withStripped = true;

  /* style */
  const __vue_inject_styles__$5i = undefined;
  /* scoped */
  const __vue_scope_id__$5i = undefined;
  /* module identifier */
  const __vue_module_identifier__$5i = undefined;
  /* functional template */
  const __vue_is_functional_template__$5i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5i, staticRenderFns: __vue_staticRenderFns__$5i },
    __vue_inject_styles__$5i,
    __vue_script__$5i,
    __vue_scope_id__$5i,
    __vue_is_functional_template__$5i,
    __vue_module_identifier__$5i,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5h = {
  name: 'XmlEbookRender',
  components: {
    PdfView: __vue_component__$5i
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'preview';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        console.log('XmlEbook组件(ruleIndex:' + this.ruleIndex + ')，watch触发，变量pBlockData：', newValue);
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
          // this.ebookSetList=Object.assign({},this.blockData.ebookSetList);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      image: image$1,
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-ebook-property',
      showPdf: false,
      baseUrl: '',
      textInfo: ''
      // ebookSetList:[]
    };
  },
  methods: {
    handleClose() {
      this.showPdf = false;
    },
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    goRead(info) {
      console.log('🚀 ~ goRead ~ info:', info);
      if (this.mode === 'preview') {
        this.$EventBus.$emit('learningStatistics', {
          type: 'ebook',
          info: null,
          data: info
        });
        if (info.source != 'netlink') {
          const baseUrl = info.uploadFileUrl.indexOf('./') !== -1 ? this.resourceBasisPath + info.uploadFileUrl.split('./')[1] : info.uploadFileUrl;
          if (info.uploadFileUrl.indexOf('.epub') > 0) {
            this.baseUrl = '/static/epubjs/index.html#/epub-read?flow=scrolled&bookPath=' + baseUrl;
          } else {
            // const baseUrl = this.resourceBasisPath + info.uploadFileUrl.split('./')[1]
            this.baseUrl = `/static/pdfjs/web/viewer.html?file=${baseUrl}`;
          }
          if (this.isReader) {
            this.$emit('showFileReader', {
              fileFormat: info.fileFormat,
              fileUrl: this.baseUrl,
              title: info.name || info.title
            });
          } else if (info.uploadFileUrl) {
            this.showPdf = true;
            this.textInfo = info.name || info.title;
          }
        } else {
          if (info.link) {
            window.open(info.link, '_blank');
            // this.$emit('showPdfRead', { type: 'link', url: info.link, pageType: this.pageType })
          } else {
            this.$message.warning('获取资源链接失败，请检查链接地址');
          }
        }
      }
    },
    countFileSize(fileSize) {
      if (!fileSize) return;
      if (fileSize < 1024) {
        return fileSize + 'B';
      } else if (fileSize < 1024 * 1024) {
        var temp = fileSize / 1024;
        temp = temp.toFixed(2);
        return temp + 'KB';
      } else if (fileSize < 1024 * 1024 * 1024) {
        var temp = fileSize / (1024 * 1024);
        temp = temp.toFixed(2);
        return temp + 'MB';
      } else {
        var temp = fileSize / (1024 * 1024 * 1024);
        temp = temp.toFixed(2);
        return temp + 'GB';
      }
    },
    iframeHeight(e) {
      let height = 600;
      if (e) {
        let dom = document.querySelector('.xml-ebook-container');
        let width = dom.clientWidth - 60;
        height = width * Number(e);
      }
      return height;
    }
  },
  created() {
    // this.blockData = Object.assign( this.blockData,this.pBlockData)
  }
};

var __$_require_static_images_default_over_ebook_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$5h = script$5h;
var __vue_render__$5h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: ["xml-ebook-container", "xml-ebook-container-" + _vm.pageType],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _vm.blockData.styleMode === "A"
        ? _c(
            "div",
            { staticClass: "resource-box xml-group-item" },
            _vm._l(_vm.blockData.ebookSetList, function (item, index) {
              return _c(
                "div",
                {
                  key: index + Math.random(),
                  staticClass: "workbench-shop-item",
                  on: {
                    click: function ($event) {
                      return _vm.goRead(item)
                    },
                  },
                },
                [
                  _c("img", {
                    staticClass: "shop-item-img-resource-first xml-round-6",
                    attrs: {
                      src:
                        item.coverFileUrl &&
                        item.coverFileUrl.indexOf(
                          "http://124.133.23.84:20000"
                        ) === -1
                          ? item.coverFileUrl.indexOf("./") !== -1
                            ? _vm.resourceBasisPath +
                              item.coverFileUrl.split("./")[1]
                            : item.coverFileUrl
                          : __$_require_static_images_default_over_ebook_png__,
                    },
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "shop-item-desc" }, [
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.name,
                            expression: "item.name",
                          },
                        ],
                        staticClass: "xml-ebook-title shop-item-desc-title",
                      },
                      [_vm._v(_vm._s(item.name))]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.author,
                            expression: "item.author",
                          },
                        ],
                        staticClass: "shop-item-desc-detail",
                      },
                      [_vm._v("作者：" + _vm._s(item.author))]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.publisher,
                            expression: "item.publisher",
                          },
                        ],
                        staticClass: "shop-item-desc-detail",
                      },
                      [_vm._v("出版社：" + _vm._s(item.publisher))]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.isbn,
                            expression: "item.isbn",
                          },
                        ],
                        staticClass: "shop-item-desc-detail",
                      },
                      [_vm._v("ISBN:" + _vm._s(item.isbn))]
                    ),
                  ]),
                ]
              )
            }),
            0
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.blockData.styleMode === "B"
        ? _c(
            "div",
            { staticClass: "resource-box xml-group-item" },
            _vm._l(_vm.blockData.ebookSetList, function (item, index) {
              return _c(
                "div",
                {
                  key: index + Math.random(),
                  staticClass: "workbench-shop-item styleB",
                },
                [
                  _c("iframe", {
                    attrs: {
                      width: "100%",
                      height: _vm.iframeHeight(item.proportion),
                      src:
                        item.uploadFileUrl.indexOf(".epub") !== -1
                          ? "/static/epubjs/index.html#/epub-read?flow=scrolled&bookPath=" +
                            item.uploadFileUrl
                          : item.uploadFileUrl.indexOf(".pdf") !== -1
                          ? "/static/pdfjs/web/viewer.html?file=" +
                            item.uploadFileUrl
                          : item.uploadFileUrl,
                      frameborder: "0",
                    },
                  }),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "xml-ebook-title title xml-text-center" },
                    [_vm._v(_vm._s(item.name))]
                  ),
                ]
              )
            }),
            0
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.blockData.styleMode === "C"
        ? _c(
            "div",
            { staticClass: "resource-box resource-box-sytleC xml-group-item" },
            _vm._l(_vm.blockData.ebookSetList, function (item, index) {
              return _c(
                "div",
                {
                  key: index + Math.random(),
                  staticClass: "workbench-shop-item",
                  on: {
                    click: function ($event) {
                      return _vm.goRead(item)
                    },
                  },
                },
                [
                  _c("img", {
                    staticClass: "shop-item-img-resource xml-round-6",
                    attrs: {
                      src:
                        item.coverFileUrl &&
                        item.coverFileUrl.indexOf(
                          "http://124.133.23.84:20000"
                        ) === -1
                          ? item.coverFileUrl.indexOf("./") !== -1
                            ? _vm.resourceBasisPath +
                              item.coverFileUrl.split("./")[1]
                            : item.coverFileUrl
                          : __$_require_static_images_default_over_ebook_png__,
                    },
                  }),
                  _vm._v(" "),
                  _c("span", { staticClass: "xml-ebook-title" }, [
                    _vm._v(_vm._s(item.name)),
                  ]),
                ]
              )
            }),
            0
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.blockData.styleMode === "D"
        ? _c(
            "div",
            { staticClass: "resource-box resource-box-sytleD xml-group-item" },
            _vm._l(_vm.blockData.ebookSetList, function (item, index) {
              return _c(
                "div",
                {
                  key: index + Math.random(),
                  staticClass: "ebook_style_4 workbench-shop-item",
                  on: {
                    click: function ($event) {
                      return _vm.goRead(item)
                    },
                  },
                },
                [
                  _c("div", { staticClass: "left" }, [
                    _c("img", {
                      attrs: {
                        src:
                          item.coverFileUrl &&
                          item.coverFileUrl.indexOf(
                            "http://124.133.23.84:20000"
                          ) === -1
                            ? item.coverFileUrl.indexOf("./") !== -1
                              ? _vm.resourceBasisPath +
                                item.coverFileUrl.split("./")[1]
                              : item.coverFileUrl
                            : __$_require_static_images_default_over_ebook_png__,
                      },
                    }),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "right" },
                    [
                      _c(
                        "el-tooltip",
                        {
                          staticClass: "item",
                          attrs: { effect: "dark", placement: "top-start" },
                        },
                        [
                          _c(
                            "div",
                            {
                              staticClass: "title-popper",
                              attrs: { slot: "content" },
                              slot: "content",
                            },
                            [_vm._v(_vm._s(item.name))]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            { staticClass: "xml-ebook-title title-box" },
                            [_vm._v(_vm._s(item.name))]
                          ),
                        ]
                      ),
                      _vm._v(" "),
                      _c("div", { staticClass: "book_info" }, [
                        _vm._v(
                          "\n          文件大小：" +
                            _vm._s(
                              _vm.countFileSize(item.fileSize)
                                ? _vm.countFileSize(item.fileSize)
                                : "无"
                            ) +
                            "\n        "
                        ),
                      ]),
                    ],
                    1
                  ),
                ]
              )
            }),
            0
          )
        : _vm._e(),
      _vm._v(" "),
      _c("PdfView", {
        attrs: {
          show: _vm.showPdf,
          baseUrl: _vm.baseUrl,
          textInfo: _vm.textInfo,
          pageType: "h5",
        },
        on: { handleClose: _vm.handleClose },
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$5h = [];
__vue_render__$5h._withStripped = true;

  /* style */
  const __vue_inject_styles__$5h = undefined;
  /* scoped */
  const __vue_scope_id__$5h = "data-v-6514a3f5";
  /* module identifier */
  const __vue_module_identifier__$5h = undefined;
  /* functional template */
  const __vue_is_functional_template__$5h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5h, staticRenderFns: __vue_staticRenderFns__$5h },
    __vue_inject_styles__$5h,
    __vue_script__$5h,
    __vue_scope_id__$5h,
    __vue_is_functional_template__$5h,
    __vue_module_identifier__$5h,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$5h.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('ebook-render', {
    prefix: options.prefix
  }), __vue_component__$5h);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// import { Player, Video, Ui, ClickToPlay, Skeleton, Controls, ScrubberControl } from '@vime/vue'
var script$5g = {
  name: 'VmPlayer',
  // components: { Player, Video, Ui, ClickToPlay, Skeleton, Controls, ScrubberControl },
  props: {
    src: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentTime: 0
    };
  },
  computed: {
    player() {
      return this.$refs.player;
    }
  },
  mounted() {},
  watch: {},
  methods: {
    // Example function to showcase updating property.
    seekForward() {
      this.currentTime = this.currentTime + 5;
    },
    // Example function to showcase calling player method.
    enterFullscreen() {
      this.player.enterFulllscreen();
    },
    onTimeUpdate(time) {
      this.currentTime = time;
    },
    onFullscreenChange(active) {
    },
    onPlaybackReady() {
      // ...
    }
  }
};

/* script */
const __vue_script__$5g = script$5g;

/* template */
var __vue_render__$5g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Player",
    {
      ref: "player",
      attrs: {
        controls: "",
        autoplay: "",
        muted: "",
        playsinline: "",
        currentTime: _vm.currentTime,
      },
      on: {
        vPlaybackReady: _vm.onPlaybackReady,
        vmCurrentTimeChange: _vm.onTimeUpdate,
        vmFullscreenChange: _vm.onFullscreenChange,
      },
    },
    [
      _c("Video", [
        _c("source", { attrs: { "data-src": _vm.src, type: "video/mp4" } }),
      ]),
      _vm._v(" "),
      _c(
        "Ui",
        [
          _c("ClickToPlay"),
          _vm._v(" "),
          _c("Skeleton"),
          _vm._v(" "),
          _c(
            "Controls",
            { attrs: { fullWidth: "", activeDuration: 3200 } },
            [_c("ScrubberControl")],
            1
          ),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$5g = [];
__vue_render__$5g._withStripped = true;

  /* style */
  const __vue_inject_styles__$5g = undefined;
  /* scoped */
  const __vue_scope_id__$5g = undefined;
  /* module identifier */
  const __vue_module_identifier__$5g = undefined;
  /* functional template */
  const __vue_is_functional_template__$5g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5g, staticRenderFns: __vue_staticRenderFns__$5g },
    __vue_inject_styles__$5g,
    __vue_script__$5g,
    __vue_scope_id__$5g,
    __vue_is_functional_template__$5g,
    __vue_module_identifier__$5g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$5f = {
  name: 'VideoPlayer',
  components: {
    VmPlayer: __vue_component__$5g,
    videoPlayer: VueVideoPlayer.videoPlayer
  },
  props: {
    src: {
      type: String,
      default: ''
    },
    // 这里接收父组件传来的src参数，视频的url
    loop: {
      type: Boolean,
      default: false
    },
    //控制循环还是单次播放
    canSkip: {
      type: Boolean,
      default: true
    },
    //是否允许控制播放进度
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    //yuan-wen:原文播放；tan-chaung：弹窗播放；tiao-zhuan：跳转播放
    title: {
      type: String,
      default: ''
    },
    //视频标题
    link: {
      type: String,
      default: ''
    },
    //链接地址
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    aspectRatio: '16:9',
    poster: {
      type: String,
      default: ''
    },
    titlePosition: {
      type: String,
      default: ''
    },
    titleAlign: {
      type: String,
      default: ''
    },
    playerIndex: {
      type: Number,
      default: -1
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    isEmptyPlaceholder: {
      type: Boolean,
      default: false
    },
    initialTime: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      // 用于存放<video>标签对象，方便调用原生API
      videoRef: null,
      videoContainer: null,
      // 是否暂停，用于控制播放与暂停
      isPaused: true,
      // 播放当前时间
      currentTime: '00:00',
      totalTime: '00:00',
      // 声音是否静音
      isMuted: false,
      scrollDuration: 0,
      isShow: true,
      // 是否全屏
      isFullScreen: false,
      controlsActive: false,
      isVideoEnd: false,
      stopDefaultEvent: false,
      lastOperateTimeStamp: '',
      timeData: null,
      isAmin: false,
      playerOptions: {
        playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
        //播放速度
        autoplay: false,
        //如果true,浏览器准备好时开始回放。
        muted: false,
        // 默认情况下将会消除任何音频。
        loop: false,
        // 导致视频一结束就重新开始。
        preload: 'auto',
        // 建议浏览器在<video>加载元素后是否应该开始下载视频数据。auto浏览器选择最佳行为,立即开始加载视频（如果浏览器支持）
        language: 'zh-CN',
        aspectRatio: this.aspectRatio,
        // 将播放器置于流畅模式，并在计算播放器的动态大小时使用该值。值应该代表一个比例 - 用冒号分隔的两个数字（例如"16:9"或"4:3"）
        fluid: true,
        // 当true时，Video.js player将拥有流体大小。换句话说，它将按比例缩放以适应其容器。
        sources: [{
          type: '',
          //这里的种类支持很多种：基本视频格式、直播、流媒体等，具体可以参看git网址项目
          src: this.src
        }],
        poster: this.poster,
        //你的封面地址
        // width: document.documentElement.clientWidth, //播放器宽度
        notSupportedMessage: '获取视频失败，请检查链接地址',
        //允许覆盖Video.js无法播放媒体源时显示的默认信息。
        controlBar: {
          timeDivider: false,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true,
          //全屏按钮
          progressControl: true,
          volumePanel: {
            inline: false
          }
        }
      },
      playerEvents: ['fullscreenchange', 'timeupdate']
    };
  },
  computed: {
    durationVar() {
      return {
        '--scroll-duration': this.scrollDuration + 's',
        'text-align': this.titleAlign
      };
    },
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    isAsyncConvert() {
      return this.src.split('__')[0] === 'convertTask';
    },
    computedAlign() {
      let style = {};
      switch (this.titleAlign) {
        case 'left':
          style = {
            justifyContent: 'flex-start'
          };
          break;
        case 'center':
          style = {
            justifyContent: 'center'
          };
          break;
        case 'right':
          style = {
            justifyContent: 'flex-end'
          };
          break;
      }
      return style;
    }
  },
  mounted() {
    this.timeData = 'v' + Math.random();
    // 加载完毕后，先获取<video>标签DOM对象
    this.videoRef = this.$refs.videoRef;
    this.videoContainer = this.$refs.videoContainer;
    this.checkTitleScroll();
    if (this.mode === 'preview' && this.playStyle === 'yuan-wen') {
      this.$emit('resetTimeUpdate');
    }
  },
  watch: {
    title: {
      handler(n, o) {
        this.checkTitleScroll();
      }
    },
    titlePosition: {
      handler(n, o) {
        this.checkTitleScroll();
      }
    },
    poster: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          this.playerOptions.poster = n;
        });
      }
    },
    canSkip: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          //alert(n);
          // this.playerOptions.controlBar.progressControl = n
        });
      },
      immediate: true
    },
    loop: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          //alert(n);
          this.playerOptions.loop = n;
        });
      },
      immediate: true
    },
    src: {
      handler(n, o) {
        if (n == '') {
          this.isShow = false;
        } else {
          this.isShow = true;
        }
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          this.playerOptions.sources[0].src = n;
        });
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      let currentPlayStamp = val || '--';
      if (currentPlayStamp !== this.src + '__playTimeStamp__' + this.lastOperateTimeStamp) {
        this.videoRef.stopPlay();
        this.handlePause();
      }
    }
  },
  methods: {
    checkTitleScroll() {
      this.$nextTick(() => {
        if (this.$refs.previewTitleText) {
          let titleWidth = this.$refs.previewTitleText.offsetWidth;
          let parentWidth = this.$refs.parentTitle.offsetWidth;
          if (titleWidth >= parentWidth + 1) {
            this.isAmin = true;
            // this.$refs.previewTitleText.classList.value = 'animate-title'
            this.scrollDuration = titleWidth / 60 + 1;
          } else {
            this.isAmin = false;
          }
        }
      });
    },
    playsinLine() {
      let ua = navigator.userAgent.toLocaleLowerCase();
      //
      if (ua.match(/tencenttraveler/) != null || ua.match(/qqbrowse/) != null) {
        return false;
      } else {
        return true;
      }
    },
    toggleFullscreen(item, e) {
      // this.isFullScreen = !this.isFullScreen
      let videoParent = document.getElementById(item);
      videoParent.getElementsByTagName('video')[0].id;
      let bar = videoParent.querySelector('.vjs-control-bar');
      if (e.isFullscreen() == true) bar.className = 'vjs-control-bar fullscren';else {
        // alert(1);
        bar.className = 'vjs-control-bar';
      }
      this.videoPlayerDom = videoParent.getElementsByTagName('video')[0];

      //  if (this.wrapRoot && this.wrapRoot.currentInKey === this.inKey) {
      const data = {
        type: 'setIframeClass',
        value: e.isFullscreen()
      };
      window.parent.postMessage(data);
      //  }
      //this.isFullScreen = !this.isFullScreen;
      if (/AppleWebKit.*mobile/i.test(navigator.userAgent) || /MIDP|SymbianOS|NOKIA|SAMSUNG|LG|NEC|TCL|Alcatel|BIRD|DBTEL|Dopod|PHILIPS|HAIER|LENOVO|MOT-|Nokia|SonyEricsson|SIE-|Amoi|ZTE/.test(navigator.userAgent)) {
        if (window.location.href.indexOf('?mobile') < 0) {
          try {
            if (/Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent)) {
              //   alert(1);

              this.videoPlayer.attr('x-webkit-airplay', 'true') //设置允许设备播放
              .attr('x5-playsinline', 'true') // 设置android在微信中内联播放视频
              .attr('x5-video-player-type', 'web') // 关闭同层X5内核播放器    x5-video-player-type='h5' 启用Ｈ5同层播放器
              // .attr('x5-video-player-fullscreen','true')  // 进入全屏通知
              .attr('x5-video-orientation', 'portrait'); //控制横竖屏 可选值： landscape 横屏, portraint竖屏  默

              return;
            } else if (/iPad/i.test(navigator.userAgent)) {
              //window.location.href="平板页面";
            } else {
              //window.location.href="其他移动端页面"
            }
          } catch (e) {}
        }
      }
      //this.videoPlayer.controls = false;
      //this.playerOptions.controls = true;
      //   bar.className = "vjs-control-bar";
    },
    onPlayerPlay(e) {
      let currentVideo = e.el_.children[0].getAttribute('id', 64);
      let videoList = document.getElementsByTagName('video');
      for (let i = 0; i < videoList.length; i++) {
        let id = videoList[i].getAttribute('id');
        if (currentVideo != id) {
          this.videoPlayerDom = videoList[i];
        }
      }

      //let audio = document.querySelectorAll('audio')
      //if (audio && audio.length) audio.forEach((i) => i.pause())
    },
    handlePlay(item, e) {
      // if (this.stopDefaultEvent) return
      // if (this.mode==='editor') {
      //   this.videoRef.pause()
      //   this.$message.warning('视频仅支持在预览模式下播放')
      //   return
      // }
      this.videoId = item;
      let videoParent = document.getElementById(item);
      let id = videoParent.getElementsByTagName('video')[0].id;
      this.videoPlayerDom = videoParent.getElementsByTagName('video')[0];
      item = id;
      this.isPaused = false;
      let audio = document.querySelectorAll('audio');
      if (audio && audio.length) {
        audio.forEach(i => i.pause());
      }
      let video = document.querySelectorAll('video');
      video.forEach(i => {
        if (i.id != item) {
          i.pause();
        }
      });
      this.setPlayStamp();
      if (this.playStyle === 'tiao-zhuan') {
        setTimeout(() => {
          videoParent.getElementsByTagName('video')[0].pause();
        }, 150);
        if (this.link) {
          window.location.href = this.link;
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }

        // window.open(this.src,"_blank")
      }
      if (this.playStyle === 'tan-chuang') {
        setTimeout(() => {
          videoParent.getElementsByTagName('video')[0].pause();
          videoParent.getElementsByTagName('video')[0].currentTime = 0;
          this.$refs.videoRef.player.src('');
        }, 300);
        this.$emit('showDialog', {
          url: this.src,
          title: this.title,
          content: this.content,
          poster: this.poster,
          playerIndex: this.playerIndex,
          initialTime: this.initialTime
        });
        setTimeout(() => {
          // videojs(videoParent.getElementsByTagName('video')[0]).trigger('loadstart')
          // this.$refs.videoRef.player.reset()
          this.$refs.videoRef.player.load();
          this.$refs.videoRef.player.src(this.src);
        }, 500);
      }
    },
    handlePause() {
      if (this.stopDefaultEvent) return;
      this.isPaused = true;
    },
    // 播放与暂停,注意,这里isPause的变化,对应图标的变化
    togglePlay(event) {
      if (this.mode === 'editor') ;
    },
    doPlay() {
      this.isPaused = !this.isPaused;
      this.stopDefaultEvent = true;
      if (this.isPaused) ; else {
        let audio = document.querySelectorAll('audio');
        if (audio && audio.length) {
          audio.forEach(i => i.pause());
        }
        let video = document.querySelectorAll('video');
        video.forEach(i => {
          if (i.id != this.timeData) {
            i.pause();
          }
        });
        // this.videoRef.play()
        this.setPlayStamp();
      }
      setTimeout(() => {
        this.stopDefaultEvent = false;
      }, 500);
    },
    setPlayStamp() {
      let timeStamp = new Date().getTime();
      this.lastOperateTimeStamp = timeStamp;
      let playStamp = `${this.src}__playTimeStamp__${timeStamp}`;
      // this.$store.commit('leaflet/SET_PLAY_STATE', this.src)
      // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
    },
    rePlay() {
      // this.videoRef.play()
      this.isPaused = false;
      this.isVideoEnd = false;
    },
    // 停止播放
    stopPlay() {
      //this.videoRef.pause()
      this.videoRef.currentTime = 0;
      // 停止播放了，要将播放图标从暂停切换到播放,isPaused为true的时候，图标是播放
      this.isPaused = true;
    },
    playEnd() {
      if (!this.loop) {
        this.videoRef.currentTime = 0;
        this.isPaused = true;
        this.isVideoEnd = true;
      }
    },
    // 定义一个时间处理函数，例如将100秒，处理成1:40
    timeFormat(time) {
      let minute = Math.floor(time % 3600 / 60); // 取余
      let second = Math.floor(time % 60);
      minute = minute < 10 ? '0' + minute : minute;
      second = second < 10 ? '0' + second : second;
      return `${minute}:${second}`;
    },
    // 当事件oncanplay触发时，获取视频的总时间信息，然后通过timeFormat函数处理成00:00的格式，并渲染到template中
    getTotalTime() {
      this.currentTime = this.timeFormat(this.videoRef.currentTime);
      this.totalTime = this.timeFormat(this.videoRef.duration);
    },
    // 时间timeupdate触发时，更新当前播放时间
    timeUpdate(e) {
      // this.currentTime = this.timeFormat(this.videoRef.currentTime)
      // this.$emit('timeUpdate', { event: e, time: this.videoRef.currentTime })
      if (e.currentTime() >= this.initialTime) {
        this.currentTime = this.timeFormat(e.currentTime());
        this.$emit('timeUpdate', {
          event: e,
          time: e.currentTime()
        });
      } else {
        e.currentTime(this.initialTime);
      }
    },
    handleError(player) {
      if (player.error()?.message.includes('The media could not be loaded')) {
        player.error('服务器或网络错误, 视频资源无法加载');
        player.errorDisplay.contentEl_.innerText = '服务器或网络错误, 视频资源无法加载';
      }
    },
    playerReadied(player) {
      if (this.initialTime > 0) {
        player.currentTime(this.initialTime);
      }
    },
    // 声音是否静音
    toggleMuted() {
      this.isMuted = !this.isMuted;
      this.videoRef.muted = !this.videoRef.muted;
    },
    // 切换全屏,注意，这里进入全屏时，使用的元素是this.video，而不是this.myvideo。video是myvideo的父标签，这样控制条才能正确显示
    toggleFullScreen(event) {
      let fullscreen = document.webkitIsFullScreen || document.fullscreen;
      this.isFullScreen = fullscreen;
      if (!this.isFullScreen) {
        this.isFullScreen = !this.isFullScreen;
        const inFun = this.videoContainer.requestFullscreen || this.videoContainer.webkitRequestFullScreen;
        inFun.call(this.videoContainer);
      } else {
        this.isFullScreen = !this.isFullScreen;
        const exitFun = document.exitFullscreen || this.document.webkitExitFullScreen;
        exitFun.call(document);
      }
    }
  }
};

/* script */
const __vue_script__$5f = script$5f;

/* template */
var __vue_render__$5f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "videoContainer",
      staticClass: "xml-video-player xml-video-player-16-9",
      on: { click: _vm.togglePlay },
    },
    [
      _vm.isAsyncConvert
        ? [_vm._m(0)]
        : [
            _c("video-player", {
              ref: "videoRef",
              class: [
                "video-player",
                "vjs-custom-skin",
                _vm.canSkip ? "" : "vjs-no-select",
                _vm.title && _vm.titlePosition === "bottom"
                  ? "with-bottom-title"
                  : "",
                _vm.mode === "editor"
                  ? "is-edit-mode"
                  : _vm.mode === "demo"
                  ? "is-demo-mode"
                  : "",
                _vm.isEmptyPlaceholder ? ".vjs-empty-placeholder" : "",
              ],
              style: { width: "100%", height: "100%", overflow: "hide" },
              attrs: {
                id: _vm.timeData,
                events: _vm.playerEvents,
                playsinline: true,
                options: _vm.playerOptions,
                "x5-video-orientation": "portraint",
              },
              on: {
                "!click": function ($event) {
                  return _vm.togglePlay($event)
                },
                play: function ($event) {
                  return _vm.handlePlay(_vm.timeData, $event)
                },
                error: _vm.handleError,
                ready: _vm.playerReadied,
                "!fullscreenchange": function ($event) {
                  return _vm.toggleFullscreen(_vm.timeData, $event)
                },
                timeupdate: _vm.timeUpdate,
              },
            }),
          ],
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: _vm.title && _vm.titlePosition === "bottom",
              expression: "title && titlePosition === 'bottom'",
            },
          ],
          staticClass: "video-title",
          style: _vm.computedAlign,
        },
        [
          _c(
            "div",
            {
              ref: "parentTitle",
              staticStyle: { width: "100%" },
              style: _vm.durationVar,
            },
            [
              _c(
                "div",
                {
                  ref: "previewTitleText",
                  class: _vm.isAmin ? "animate-title" : "",
                },
                [_c("div", { domProps: { innerHTML: _vm._s(_vm.title) } })]
              ),
            ]
          ),
        ]
      ),
      _vm._v(" "),
      _c("div", {
        directives: [
          {
            name: "show",
            rawName: "v-show",
            value: _vm.mode == "editor",
            expression: "mode == 'editor'",
          },
        ],
        staticClass: "dialog-box",
      }),
    ],
    2
  )
};
var __vue_staticRenderFns__$5f = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "convert-placeholder" }, [
      _c("div", { staticClass: "convert-placeholder__text" }, [
        _vm._v("视频转码中，无法即时预览，请稍候在活页/教材预览中查看"),
      ]),
    ])
  },
];
__vue_render__$5f._withStripped = true;

  /* style */
  const __vue_inject_styles__$5f = undefined;
  /* scoped */
  const __vue_scope_id__$5f = undefined;
  /* module identifier */
  const __vue_module_identifier__$5f = undefined;
  /* functional template */
  const __vue_is_functional_template__$5f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5f, staticRenderFns: __vue_staticRenderFns__$5f },
    __vue_inject_styles__$5f,
    __vue_script__$5f,
    __vue_scope_id__$5f,
    __vue_is_functional_template__$5f,
    __vue_module_identifier__$5f,
    false,
    undefined,
    undefined,
    undefined
  );

var playIcon$3 = "data:image/png;base64,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";

var titleLeftIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADYAAAAuCAYAAABjwOorAAAAAXNSR0IArs4c6QAABQ1JREFUaEPtWGtsk1UYft7TbmtBmOCFAHJryy0aYyJB4w8CatQfGo0LxsREQ2IwUYOoWceQyweBQiFozJQfmKi/xAsEjDEKiZj4A/+oIUGNhrZOLgqKMOSySdv3Medr6751q2xdRwfpyf4s5z19n+c87+18gqt0yVXKCzViV5qyNcVqig2TG6iF4jARot8wylbsJodjAw2YQsCU8pYF/moP4wgek2y/EVXIsGxioY2cbATrhBhfGot+pzRvJVslUSG8/f6ZsonhQ/oi7VglyuWUEo2eOEbq+uRY3zt4RtL9RlUBw/KJAZjxKidqmu8BuKNPLIQC3KXGOKmoHPLahONcIIq5vc4Z/JboxHY4khkMv0ERs46nxLmgTvkugLF9AhEcp8i65AVsxxq5WLAJbeJ0n3IvBZN6nCPOwCcPJprl66oSw0L6QrejWcCoBSI2LIn8H2yMksJPFen1v7QEfvaCDcUzi3xq2uiadS8K9iSXSRMgLJfcoBWzjm0hAfiGIe6U/C/S0mOOJAQnCVmbPIZdaJN//gPr0ESCiJFcbC+E9oRlKaAaWZyKys6qEoNDE27AfDF8XYgxxWBy1849MCaWiErSuz9l/fnxdSa4HYKZlpBrSSuVfJ/twsJ2RzrKIVcRxazjG1bzmsYReFrIJVoUWi4wwVmBrEUQnySWeFSzisc5xyg/gKe6ElDxycuJZvm4qsQASmgTIiarqygy1xta3RnELwkTSy2Tw8Vgbb4JzFIh/J6945kGaWp/ceCqVUwxF8xi1oXCmCfgagHGuDlWyLUc2k5A1tZ3Ys+PngppN8Zt5shRqm1QuTWvsE02Evp+ssW3ZaCqVZYYgKmv8Vp/Gk9AuUi8Iel6sqmDby+m4fz6ivxeDDa8jpNMPd+EojFPzhaTU5qRF5Ir5MhAyFWcmA3JSAyzYfC8CG/zgnELH5khJJZIYR+29Z5GbOM24FIq6gvkhLr7UKt/W5WJAXDoj4zI3A34nhXkb9+DSsmf0mqWH26V073AOvSHA7rUwHinkqwSKwcycw6BYjmoNiTrLuJxAR92G7ANwsKy/2Vky6ED2IePek/+EYejEdAWMZiALAQ+EGp+4Ai0FVfUUioOGTGXXIyz/KJPQmR2D2K5dDvaIab1ZIuc7QvczM2cllU8B2rANnsBMirpralooMfMWRViucaduU+MvwnE6GIQKtiWapEv+gTn0ITqsw8Yn8wr7BPmaLITW+GIXirfhlQx63zyBo4JAE00ehcBv533C09TEXMifQEr2x3pKkVuehBPKTChcIppfJZaId9UnZgFMCPGWerHI0Kdln9xdw+3YnYkorK3FNCJMV4XMHhUBCOtDYHT5wx2nGiW8/9HbsgVc507NNODuIfAAqiOoi0G+WUUZzo6TfzPNXKuFNBpcc6s09zzJmNAMTiYbJY/qk8s9wJoNMBDgN5M02NsAmk+L5lrl4q5EvuXR7G88/AGRkRwP0XH5apk/jsQ0ZEG3u6zr10JxNypZBPmkZgDIFBoASJut9qfiOKrwTwuvXdwWRWzjq+Pc1Sj4l4BptK2XrengaI43dWA3UdfklNlitTj2GUnZr3bYVfqMJ+ecYuKjBAHk8uxvxKqVYWY+3bbiFuQxY1+kxu17CcpfxZ/+9M4UPykKUfBKhEDsJr1oSCC2tU9Q/oBTQAXBvvpLRfdV+mqEbvShK0pVlNsmNxALRSHiRD9hlFTrN9XNUwM/wU+QeA+cB9fBQAAAABJRU5ErkJggg==";

var titleRightIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADYAAAAuCAYAAABjwOorAAAAAXNSR0IArs4c6QAABSNJREFUaEPtWEtslFUU/r77Tx9TmphqEHkUCp0GA0YN6soQE1e6kBijBInESAwFFFQS+kQZCNBSRCS6UeMGN6aAQlxQEhIIMS58kZCoCDMtYEuqAgWF0k7/uZ/5pw9m+rAz06EFMrO8c8893+Pcc88McZd+eJfyQpbYneZs1rGsY7eJAtlSvE2MSBrGxDj2iXLKLmI+cjBjJKTWItrt4mTrBrYlzSZu44QQm12vR3ywb0NmwUig5aBFxIrwev51RxArCSrfl29XA6wEce8IoCXDN8LX8TmCtBNETCzejql+i/zBADrzcKV1HS/HrwcatEBWmwk8DYww+Rh95cis/L2S/6ZDyosZcynO2KLpfh++lFScAILohlgequax/vUZH8if5+J1WFUTKBwOtIQLrstnz73LlnRJZYRYaX20nOBuMFEkAU3XDJf+uZ7X+wEGGiILYH1BAE8m3HNPXkEgBHBlqBL7AWrCiM2sU1GO0Q+0mBIPQsBFkU81V/F8//q0oAry8/GqkWpB5MSoMAY/VjUeCyvtu+4zFfFipEsu/VIMygT89j1YvtmrNEgCErqt4drmCu6PBzV7hx52rLYCeHS4pAIuRHu4uGUDz6VLJj4ubWJz6jXTQHsJTE90S0e7cs2K1nW80b8+ZYcmFUaxFFBVTIK+T28FAgaIiKwK3cAhbGJkQokFtkdXS1w3SKVLcvlieAP/uLkuBuoxD4i5NTe2HvMXkHcvBRfigS5hV1sNL2WCVNrNY9ZWTc119LUSO1tEYG24iofiwU3eqMKiSVgiac2wXRA8DUWCoarcX8faMMZWii/JKXvCVgNmkaxuljL1bVe7qWnddbMEPU9KtmFujtEWGM6EjTW/gRgCVy35cXc7DifGjd23lO9Y7G5RuwkUxaW/2kOWn61gezwkrxP6/dFXDMyywVAF9FBs6jHYMzhu7LTSeKDL6twVonm+t1kDNIhY8MNwJY8mAhLnNCDgyG4CeP8QYuSpqO35rKUq52QmSzC+MSUt0Oz3NcvnYhuA3P4gwZ5Am9kZ+ojdCQdtVG6Z374GcdFQUroMmMauXByJ755JA0liY/Kl2CinrBmrZezjiIJwIETxD2Q2h2r49+BcpXUKGIMaCPckPAeyPTDmuAz2pTu5J8Er+VmxdKeK6dq3QBR487YFXNHsaa7kj8MlCtRrFWkXxr7z5nPjtXkjyIatdRvDtbm/3IoSTLkUS+u1BMY+NhAonQp1Ol8M96B6btHYdwj4+96t2ItF4YqFPYw237EhpZuMDSnsSaoUS+pU4hgsI+B48lOms6cLn54N8sqQXBIDDXYViIcGRIiC1piIsTjRDRw8X82OFDCmtXV0YkH5Avl4QcSsWBe0cGnQdKaSvw2XsWSbHnQcu5yEb6AMvSoU2uiYA6EKhtNCmmLQqMRKGvSAY/EceydyMIrzZyJoQpDukFzeYFyA5bC2NKFhENccmuOnS/AdFjOaIsa0to9KbN5GFXbGjU6++9ARWjuotfelDtRrPoRFMP3PgYVoPCJnop04eDbIrrRQphE0KrGkz2yUE2jGywKmse9HJ70eCHREDb65FdPF/2HLGLHAZs1nHhYKvf990JvbhW65+D5Uy5+TFihDGzNDLChfWQGekRJ/SVuhvfknNGHv+NyreE0yQiywRnluMYp8nX2d0HuPfdC1yei4UM7ODJmQ0jEZIZZSxnHanCU2TkJnLE3WsYxJOU4HZR0bJ6EzlibrWMakHKeDso6Nk9AZS/Mf+LvuPmZJ/LcAAAAASUVORK5CYII=";

//
var script$5e = {
  name: 'VideoModalPlayer',
  components: {
    videoPlayer: VueVideoPlayer.videoPlayer
  },
  props: {
    src: {
      type: String,
      default: ''
    },
    // 这里接收父组件传来的src参数，视频的url
    loop: {
      type: Boolean,
      default: false
    },
    //控制循环还是单次播放
    canSkip: {
      type: Boolean,
      default: true
    },
    //是否允许控制播放进度
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    //yuan-wen:原文播放；tan-chaung：弹窗播放；tiao-zhuan：跳转播放
    title: {
      type: String,
      default: ''
    },
    //视频标题
    link: {
      type: String,
      default: ''
    },
    //链接地址
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    aspectRatio: '4:3',
    poster: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    initialTime: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      playIcon: playIcon$3,
      titleLeftIcon,
      titleRightIcon,
      // 用于存放<video>标签对象，方便调用原生API
      videoRef: null,
      videoContainer: null,
      // 是否暂停，用于控制播放与暂停
      isPaused: true,
      // 播放当前时间
      currentTime: '00:00',
      totalTime: '00:00',
      // 声音是否静音
      isMuted: false,
      // 是否全屏
      isFullScreen: false,
      controlsActive: false,
      isVideoEnd: false,
      stopDefaultEvent: false,
      lastOperateTimeStamp: '',
      timeData: null,
      playerEvents: ['fullscreenchange', 'timeupdate'],
      playerOptions: {
        playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
        //播放速度
        autoplay: false,
        //如果true,浏览器准备好时开始回放。
        muted: false,
        // 默认情况下将会消除任何音频。
        loop: false,
        // 导致视频一结束就重新开始。
        preload: 'auto',
        // 建议浏览器在<video>加载元素后是否应该开始下载视频数据。auto浏览器选择最佳行为,立即开始加载视频（如果浏览器支持）
        language: 'zh-CN',
        aspectRatio: this.aspectRatio,
        // 将播放器置于流畅模式，并在计算播放器的动态大小时使用该值。值应该代表一个比例 - 用冒号分隔的两个数字（例如"16:9"或"4:3"）
        fluid: true,
        // 当true时，Video.js player将拥有流体大小。换句话说，它将按比例缩放以适应其容器。
        sources: [{
          type: '',
          //这里的种类支持很多种：基本视频格式、直播、流媒体等，具体可以参看git网址项目
          src: this.src
        }],
        poster: this.poster,
        //你的封面地址
        // width: document.documentElement.clientWidth, //播放器宽度
        notSupportedMessage: '获取视频失败，请检查链接地址',
        //允许覆盖Video.js无法播放媒体源时显示的默认信息。
        controls: true,
        controlBar: {
          timeDivider: false,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true,
          //全屏按钮
          progressControl: true,
          volumePanel: {
            inline: false
          }
        }
      }
    };
  },
  computed: {
    player() {
      return this.$refs.videoRef.player;
    },
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    isAsyncConvert() {
      return this.src.split('__')[0] === 'convertTask';
    }
  },
  mounted() {
    this.timeData = 'v' + Math.random();
    // 加载完毕后，先获取<video>标签DOM对象
    this.videoRef = this.$refs.videoRef;
    this.videoContainer = this.$refs.videoContainer;
    if (this.mode === 'preview') {
      this.$emit('resetTimeUpdate');
    }
  },
  watch: {
    canSkip: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          //alert(n);
          // this.playerOptions.controlBar.progressControl = n;
        });
      },
      immediate: true
    },
    loop: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          //alert(n);
          this.playerOptions.loop = n;
        });
      },
      immediate: true
    },
    src: {
      handler(n, o) {
        !this.isAsyncConvert && this.$nextTick(function () {
          // this.$refs.videoRef.src = n
          this.playerOptions.sources.src = n;
        });
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch');
      let currentPlayStamp = val || '--';
      if (currentPlayStamp !== this.src + '__playTimeStamp__' + this.lastOperateTimeStamp) {
        this.videoRef.pause();
        this.handlePause();
      }
    }
  },
  methods: {
    toggleFullscreen(item, e) {
      // this.isFullScreen = !this.isFullScreen
      let videoParent = document.getElementById(item);
      videoParent.getElementsByTagName('video')[0].id;
      let bar = videoParent.querySelector('.vjs-control-bar');
      console.log(e.isFullscreen());
      if (e.isFullscreen() == true) {
        bar.className = 'vjs-control-bar fullscren';
      } else {
        // alert(1);
        bar.className = 'vjs-control-bar';
      }
      console.log(bar);
      this.videoPlayerDom = videoParent.getElementsByTagName('video')[0];
      console.log('======', e.isFullscreen());
      const data = {
        type: 'setIframeClass',
        value: e.isFullscreen()
      };
      window.parent.postMessage(data);

      //this.isFullScreen = !this.isFullScreen;
      if (/AppleWebKit.*mobile/i.test(navigator.userAgent) || /MIDP|SymbianOS|NOKIA|SAMSUNG|LG|NEC|TCL|Alcatel|BIRD|DBTEL|Dopod|PHILIPS|HAIER|LENOVO|MOT-|Nokia|SonyEricsson|SIE-|Amoi|ZTE/.test(navigator.userAgent)) {
        if (window.location.href.indexOf('?mobile') < 0) {
          try {
            if (/Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent)) {
              //   alert(1);

              this.videoPlayer.attr('x-webkit-airplay', 'true') //设置允许设备播放
              .attr('x5-playsinline', 'true') // 设置android在微信中内联播放视频
              .attr('x5-video-player-type', 'web') // 关闭同层X5内核播放器    x5-video-player-type='h5' 启用Ｈ5同层播放器
              // .attr('x5-video-player-fullscreen','true')  // 进入全屏通知
              .attr('x5-video-orientation', 'portrait'); //控制横竖屏 可选值： landscape 横屏, portraint竖屏  默

              return;
            } else if (/iPad/i.test(navigator.userAgent)) {
              //window.location.href="平板页面";
            } else {
              //window.location.href="其他移动端页面"
            }
          } catch (e) {}
        }
      }
      //this.videoPlayer.controls = false;
      //this.playerOptions.controls = true;
      //   bar.className = "vjs-control-bar";
    },
    handlePlay(item) {
      if (this.stopDefaultEvent) return;
      // if (this.mode==='editor') {
      //   this.$message.warning('视频仅支持在预览模式下播放')
      //   return
      // }
      this.isPaused = false;
      let audio = document.querySelectorAll('audio');
      if (audio && audio.length) {
        audio.forEach(i => i.pause());
      }
      let video = document.querySelectorAll('video');
      video.forEach(i => {
        if (i.id != item) {
          i.pause();
        }
      });
      this.setPlayStamp();
      // if (this.playStyle === 'tiao-zhuan') {
      //   setTimeout(() => {
      //     this.videoRef.pause()
      //   }, 650)
      //   if (this.link) {
      //     window.location.href = this.link
      //   } else {
      //     this.$message.warning('跳转链接未配置')
      //   }

      //   // window.open(this.src,"_blank")
      // }
      // if (this.playStyle === 'tan-chuang') {
      //   setTimeout(() => {
      //     this.videoRef.pause()
      //   }, 650)
      //   this.$emit('showDialog', { url: this.src, title: this.title })
      // }
    },
    handlePause() {
      if (this.stopDefaultEvent) return;
      this.isPaused = true;
    },
    // 播放与暂停,注意,这里isPause的变化,对应图标的变化
    togglePlay() {
      console.log('播放');
      if (this.mode === 'editor') ; else {
        if (this.playStyle === 'yuan-wen') ;
        // if (this.playStyle === 'tiao-zhuan') {
        //   setTimeout(() => {
        //     this.videoRef.pause()
        //   }, 650)
        //   if (this.link) {
        //     window.location.href = this.link
        //   } else {
        //     this.$message.error('跳转链接未配置')
        //   }

        //   // window.open(this.src,"_blank")
        // }
        // if (this.playStyle === 'tan-chuang') {
        //   setTimeout(() => {
        //     this.videoRef.pause()
        //   }, 150)
        //   this.$emit('showDialog', { url: this.src, title: this.title })
        // }
      }
    },
    doPlay() {
      this.isPaused = !this.isPaused;
      this.stopDefaultEvent = true;
      if (this.isPaused) {
        this.videoRef.pause();
      } else {
        let audio = document.querySelectorAll('audio');
        if (audio && audio.length) {
          audio.forEach(i => i.pause());
        }
        let video = document.querySelectorAll('video');
        video.forEach(i => {
          if (i.id != this.timeData) {
            i.pause();
          }
        });
        this.videoRef.play();
        this.setPlayStamp();
      }
      setTimeout(() => {
        this.stopDefaultEvent = false;
      }, 500);
    },
    setPlayStamp() {
      let timeStamp = new Date().getTime();
      this.lastOperateTimeStamp = timeStamp;
      let playStamp = `${this.src}__playTimeStamp__${timeStamp}`;
      // this.$store.commit('leaflet/SET_PLAY_STATE', this.src)
      // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
    },
    rePlay() {
      this.videoRef.play();
      this.isPaused = false;
      this.isVideoEnd = false;
    },
    // 停止播放
    stopPlay() {
      this.videoRef.pause();
      this.videoRef.currentTime = 0;
      // 停止播放了，要将播放图标从暂停切换到播放,isPaused为true的时候，图标是播放
      this.isPaused = true;
    },
    playEnd() {
      if (!this.loop) {
        this.videoRef.currentTime = 0;
        this.isPaused = true;
        this.isVideoEnd = true;
      }
    },
    // 定义一个时间处理函数，例如将100秒，处理成1:40
    timeFormat(time) {
      let minute = Math.floor(time % 3600 / 60); // 取余
      let second = Math.floor(time % 60);
      minute = minute < 10 ? '0' + minute : minute;
      second = second < 10 ? '0' + second : second;
      return `${minute}:${second}`;
    },
    // 当事件oncanplay触发时，获取视频的总时间信息，然后通过timeFormat函数处理成00:00的格式，并渲染到template中
    getTotalTime() {
      this.currentTime = this.timeFormat(this.videoRef.currentTime);
      this.totalTime = this.timeFormat(this.videoRef.duration);
    },
    handleError(player) {
      console.log('%c%s', 'font-size:2em;background: #00965E;color: #FFF', '播放器错误', player);
      if (player.error()?.message.includes('The media could not be loaded')) {
        player.error('服务器或网络错误, 视频资源无法加载');
        player.errorDisplay.contentEl_.innerText = '服务器或网络错误, 视频资源无法加载';
      }
    },
    playerReadied(player) {
      if (this.initialTime > 0) {
        player.currentTime(this.initialTime);
      }
    },
    // 时间timeupdate触发时，更新当前播放时间
    timeUpdate(e) {
      // this.currentTime = this.timeFormat(this.videoRef.currentTime)
      // this.$emit('timeUpdate', { event: e, time: this.videoRef.currentTime })

      if (e.currentTime() >= this.initialTime) {
        this.currentTime = this.timeFormat(e.currentTime());
        this.$emit('timeUpdate', {
          event: e,
          time: e.currentTime()
        });
      } else {
        e.currentTime(this.initialTime);
      }
    },
    // 声音是否静音
    toggleMuted() {
      this.isMuted = !this.isMuted;
      this.videoRef.muted = !this.videoRef.muted;
    },
    // 切换全屏,注意，这里进入全屏时，使用的元素是this.video，而不是this.myvideo。video是myvideo的父标签，这样控制条才能正确显示
    toggleFullScreen(event) {
      let fullscreen = document.webkitIsFullScreen || document.fullscreen;
      this.isFullScreen = fullscreen;
      if (!this.isFullScreen) {
        this.isFullScreen = !this.isFullScreen;
        const inFun = this.videoContainer.requestFullscreen || this.videoContainer.webkitRequestFullScreen;
        inFun.call(this.videoContainer);
      } else {
        this.isFullScreen = !this.isFullScreen;
        const exitFun = document.exitFullscreen || this.document.webkitExitFullScreen;
        exitFun.call(document);
      }
    }
  }
};

/* script */
const __vue_script__$5e = script$5e;

/* template */
var __vue_render__$5e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "videoContainer",
      staticClass: "xml-video-player modal-player xml-video-player-16-9",
    },
    [
      _vm.isAsyncConvert
        ? [_vm._m(0)]
        : [
            _vm.title
              ? _c("div", { staticClass: "title-block" }, [
                  _c("div", { staticClass: "title-adorn" }, [
                    _c("img", { attrs: { src: _vm.titleLeftIcon } }),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "title" }, [
                    _vm._v(_vm._s(_vm.title)),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "title-adorn" }, [
                    _c("img", { attrs: { src: _vm.titleRightIcon } }),
                  ]),
                ])
              : _vm._e(),
            _vm._v(" "),
            _c("div", { class: ["fixed-ratio-container"] }, [
              _c(
                "div",
                { class: ["xml-video__wrapper", _vm.mode] },
                [
                  _c("video-player", {
                    ref: "videoRef",
                    class: [
                      "video-player",
                      "vjs-custom-skin",
                      _vm.canSkip ? "" : "vjs-no-select",
                    ],
                    style: { width: "100%", height: "100%", overflow: "hide" },
                    attrs: {
                      id: _vm.timeData,
                      playsinline: true,
                      options: _vm.playerOptions,
                      events: _vm.playerEvents,
                    },
                    on: {
                      error: _vm.handleError,
                      ready: _vm.playerReadied,
                      "!fullscreenchange": function ($event) {
                        return _vm.toggleFullscreen(_vm.timeData, $event)
                      },
                      timeupdate: _vm.timeUpdate,
                    },
                  }),
                ],
                1
              ),
            ]),
          ],
    ],
    2
  )
};
var __vue_staticRenderFns__$5e = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "convert-placeholder" }, [
      _c("div", [
        _vm._v("视频转码中，无法即时预览，请稍候在活页/教材预览中查看"),
      ]),
    ])
  },
];
__vue_render__$5e._withStripped = true;

  /* style */
  const __vue_inject_styles__$5e = undefined;
  /* scoped */
  const __vue_scope_id__$5e = undefined;
  /* module identifier */
  const __vue_module_identifier__$5e = undefined;
  /* functional template */
  const __vue_is_functional_template__$5e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5e, staticRenderFns: __vue_staticRenderFns__$5e },
    __vue_inject_styles__$5e,
    __vue_script__$5e,
    __vue_scope_id__$5e,
    __vue_is_functional_template__$5e,
    __vue_module_identifier__$5e,
    false,
    undefined,
    undefined,
    undefined
  );

//
// import '../block.scss'
var script$5d = {
  name: 'XmlVideoRender',
  components: {
    VideoPlayer: __vue_component__$5f,
    VideoModalPlayer: __vue_component__$5e
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        playMode: 'xun-huan',
        scheduleControl: 'yes',
        playStyle: 'yuan-wen',
        link: '',
        titlePosition: 'under',
        titleAlign: 'left'
      },
      //属性组件名
      propertyComponentsName: 'xml-video-property',
      dialogShow: false,
      curVideoUrl: '',
      curVideoTitle: '',
      curVideoContent: '',
      curVideoPoster: '',
      currentPlayer: {},
      initialTime: 0,
      modalPlayerKey: 'mpk' + new Date().getTime()
    };
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog(param) {
      console.log(param);
      this.initialTime = param.initialTime;
      this.curVideoUrl = param.url;
      this.curVideoTitle = param.title;
      this.curVideoContent = param.content;
      this.curVideoPoster = param.poster;
      this.currentPlayer = this.blockData.videoSetList[param.playerIndex];
      this.modalPlayerKey = 'mpk' + new Date().getTime();
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    timeUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('videoTimeUpdate', {
        event,
        time,
        data
      });
    },
    resetTimeUpdate(data) {
      this.$EventBus.$emit('resetTimeUpdate', data);
    }
  },
  computed: {
    isEmpty() {
      return !(this.blockData.videoSetList && this.blockData.videoSetList.length);
    }
  }
};

/* script */
const __vue_script__$5d = script$5d;
/* template */
var __vue_render__$5d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        "xml-video-container",
        "video-content",
        "xml-video-container-" + _vm.pageType,
      ],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _vm.isEmpty
        ? [
            _c(
              "div",
              {
                style: {
                  width: _vm.blockData.width
                    ? _vm.blockData.width + "%"
                    : "100%",
                },
              },
              [
                _c(
                  "div",
                  {
                    class: [
                      "fixed-ratio-container xml-group-item",
                      _vm.blockData.titlePosition === "under"
                        ? "with-under-title"
                        : "",
                    ],
                  },
                  [
                    _c("div", { class: ["xml-video__wrapper", _vm.mode] }, [
                      _c(
                        "div",
                        { staticClass: "xml-video-player-box" },
                        [
                          _c("video-player", {
                            attrs: {
                              src: "",
                              title: "这是视频名称",
                              mode: _vm.mode,
                              isEmptyPlaceholder: "",
                              titleAlign: _vm.blockData.titleAlign,
                              titlePosition: _vm.blockData.titlePosition,
                            },
                          }),
                        ],
                        1
                      ),
                    ]),
                  ]
                ),
                _vm._v(" "),
                _vm.blockData.titlePosition === "under"
                  ? _c(
                      "div",
                      {
                        staticClass: "xml-video-title video-title--under",
                        style: { textAlign: _vm.blockData.titleAlign },
                      },
                      [_vm._v("\n        这是视频名称\n      ")]
                    )
                  : _vm._e(),
              ]
            ),
          ]
        : _vm._l(_vm.blockData.videoSetList, function (item, index) {
            return _c(
              "div",
              {
                key: index,
                style: {
                  width: _vm.blockData.width
                    ? _vm.blockData.width + "%"
                    : "100%",
                },
              },
              [
                _c(
                  "div",
                  {
                    class: [
                      "fixed-ratio-container xml-group-item",
                      _vm.blockData.titlePosition === "under"
                        ? "with-under-title"
                        : "",
                    ],
                  },
                  [
                    _c("div", { class: ["xml-video__wrapper", _vm.mode] }, [
                      _c(
                        "div",
                        { staticClass: "xml-video-player-box" },
                        [
                          _c("video-player", {
                            attrs: {
                              poster: item.coverFileUrl
                                ? item.coverFileUrl.indexOf("./") !== -1
                                  ? _vm.resourceBasisPath +
                                    item.coverFileUrl.split("./")[1]
                                  : item.coverFileUrl
                                : "",
                              mode: _vm.mode,
                              loop: _vm.blockData.playMode !== "dan-ci",
                              src: item.uploadFileUrl
                                ? item.uploadFileUrl.indexOf("./") !== -1
                                  ? _vm.resourceBasisPath +
                                    item.uploadFileUrl.split("./")[1]
                                  : item.uploadFileUrl
                                : "",
                              playStyle: _vm.blockData.playStyle,
                              title: item.title || "",
                              link: item.link,
                              playerIndex: index,
                              canSkip: _vm.blockData.scheduleControl === "yes",
                              content: item.content,
                              titlePosition: _vm.blockData.titlePosition,
                              titleAlign: _vm.blockData.titleAlign,
                              initialTime: item.currentTime
                                ? item.currentTime
                                : 0,
                            },
                            on: {
                              showDialog: _vm.showDialog,
                              timeUpdate: function (e) {
                                return _vm.timeUpdate(e, item)
                              },
                              resetTimeUpdate: function ($event) {
                                return _vm.resetTimeUpdate(item)
                              },
                            },
                          }),
                        ],
                        1
                      ),
                    ]),
                  ]
                ),
                _vm._v(" "),
                _vm.blockData.titlePosition === "under" && item.title
                  ? _c("div", {
                      staticClass: "xml-video-title video-title--under",
                      style: { textAlign: _vm.blockData.titleAlign },
                      domProps: { innerHTML: _vm._s(item.title) },
                    })
                  : _vm._e(),
              ]
            )
          }),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          staticClass: "resource-preview player-dialog",
          attrs: {
            title: "",
            visible: _vm.dialogShow,
            "destroy-on-close": true,
            width: "100%",
            modal: false,
            "show-close": false,
            "append-to-body": "",
            "custom-class": "preview-modal-video",
            "close-on-click-modal": true,
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogShow = $event;
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "xml-modal-player__wrapper xml-group-item" },
            [
              _c("video-modal-player", {
                key: _vm.modalPlayerKey,
                attrs: {
                  mode: _vm.mode,
                  loop: _vm.blockData.playMode !== "dan-ci",
                  src: _vm.curVideoUrl,
                  canSkip: _vm.blockData.scheduleControl === "yes",
                  playStyle: "yuan-wen",
                  title: _vm.curVideoTitle,
                  initialTime: _vm.initialTime,
                  poster: _vm.curVideoPoster,
                  content: _vm.curVideoContent,
                },
                on: {
                  timeUpdate: function (e) {
                    return _vm.timeUpdate(e, _vm.currentPlayer)
                  },
                  resetTimeUpdate: function ($event) {
                    return _vm.resetTimeUpdate(_vm.currentPlayer)
                  },
                },
              }),
            ],
            1
          ),
        ]
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$5d = [];
__vue_render__$5d._withStripped = true;

  /* style */
  const __vue_inject_styles__$5d = undefined;
  /* scoped */
  const __vue_scope_id__$5d = "data-v-082d5e49";
  /* module identifier */
  const __vue_module_identifier__$5d = undefined;
  /* functional template */
  const __vue_is_functional_template__$5d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5d, staticRenderFns: __vue_staticRenderFns__$5d },
    __vue_inject_styles__$5d,
    __vue_script__$5d,
    __vue_scope_id__$5d,
    __vue_is_functional_template__$5d,
    __vue_module_identifier__$5d,
    false,
    undefined,
    undefined,
    undefined
  );

//
// import '../block.scss'
var script$5c = {
  name: 'XmlVideoImgRender',
  components: {
    VideoPlayer: __vue_component__$5f,
    VideoModalPlayer: __vue_component__$5e
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        playMode: 'xun-huan',
        scheduleControl: 'yes',
        playStyle: 'yuan-wen',
        link: '',
        titlePosition: 'under',
        titleAlign: 'left'
      },
      //属性组件名
      propertyComponentsName: 'xml-video-property',
      dialogShow: false,
      curVideoUrl: '',
      curVideoTitle: '',
      curVideoContent: '',
      curVideoPoster: '',
      currentPlayer: {},
      modalPlayerKey: 'mpk' + new Date().getTime()
    };
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog(param) {
      this.curVideoUrl = param.url;
      this.curVideoTitle = param.title;
      this.curVideoContent = param.content;
      this.curVideoPoster = param.poster;
      this.currentPlayer = this.blockData.videoSetList[param.playerIndex];
      this.modalPlayerKey = 'mpk' + new Date().getTime();
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    timeUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('videoTimeUpdate', {
        event,
        time,
        data
      });
    },
    resetTimeUpdate(data) {
      this.$EventBus.$emit('resetTimeUpdate', data);
    }
  },
  computed: {
    isEmpty() {
      return !(this.blockData.videoSetList && this.blockData.videoSetList.length);
    }
  }
};

var __$_require_images_play_circle_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$5c = script$5c;
var __vue_render__$5c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        "xml-video-container",
        "video-content",
        "xml-video-container-" + _vm.pageType,
      ],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    _vm._l(_vm.blockData.videoSetList, function (item, index) {
      return _c("div", { key: index }, [
        _c("div", { staticClass: "xml-video-container-pdf" }, [
          _c("img", {
            staticClass: "cover",
            attrs: { src: item.coverFileUrl, alt: "" },
          }),
          _vm._v(" "),
          _vm._m(0, true),
        ]),
        _vm._v(" "),
        _vm.blockData.titlePosition === "under" && item.title
          ? _c(
              "div",
              {
                staticClass: "video-title--under",
                style: { textAlign: _vm.blockData.titleAlign },
              },
              [_vm._v("\n      " + _vm._s(item.title) + "\n    ")]
            )
          : _vm._e(),
      ])
    }),
    0
  )
};
var __vue_staticRenderFns__$5c = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "play-box" }, [
      _c("img", {
        staticClass: "play",
        attrs: { src: __$_require_images_play_circle_png__, alt: "" },
      }),
    ])
  },
];
__vue_render__$5c._withStripped = true;

  /* style */
  const __vue_inject_styles__$5c = undefined;
  /* scoped */
  const __vue_scope_id__$5c = "data-v-4645f3e6";
  /* module identifier */
  const __vue_module_identifier__$5c = undefined;
  /* functional template */
  const __vue_is_functional_template__$5c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5c, staticRenderFns: __vue_staticRenderFns__$5c },
    __vue_inject_styles__$5c,
    __vue_script__$5c,
    __vue_scope_id__$5c,
    __vue_is_functional_template__$5c,
    __vue_module_identifier__$5c,
    false,
    undefined,
    undefined,
    undefined
  );

const video = [__vue_component__$5d, __vue_component__$5c, __vue_component__$5e];
var XmlVideo = {
  ...video
};

const radioType = ['SingleSelect'];
const checkType = ['MultipleSelect'];
const showOptionsType = ['1', '2', '4', '5'];
const showJudgeAnswerType = ['Judgement'];
const showSortType = ['7'];
const showRichTextAnswerType = ['Filling', 'AskAnswer'];
const isclozeType = [''];
const isRadio = type => {
  return radioType.includes(type);
};
const isCheck = type => checkType.includes(type);
const showOptions = type => showOptionsType.includes(type);
const showJudgeAnswer = type => showJudgeAnswerType.includes(type);
const showSort = type => showSortType.includes(type);
const showRichTextAnswer = type => showRichTextAnswerType.includes(type);
const isCloze = type => isclozeType.includes(type);

var playIcon$2 = "data:image/png;base64,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";

var pauseIcon$2 = "data:image/png;base64,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";

var audioIcon$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAAAXNSR0IArs4c6QAAAzxJREFUWEftmE1rU1EQhp+5N2msQVCogtWCC0EwG4OCG3+ClGpxobhQ8GMjKFjbFBfWVBQpght3LvzoxoVYwT8gLkQQNyKKigEXpXXhB9LEmOSMTK7R1sTkRpOmggMhJ+TcmefOmXPeuVeYZzo6uhPnDgIJRLrn/9eG8WfgCdHoFRkff1TxLzbQoaE4kchNYFcbAtd36XmObDYtly+ftYkBUCp1F+hfdJhKQFXw/SE5f/6S6PDwAJ53p2MwP6FyqG4UHRm5jcjujgOVl0pPiKZSL4BNSwIIbhjQW6BviQDdaj2QFahZqQSFghUrdHWFvd8WADkXBDZbtgxWrYLeXujrC77fvYOpqQCssf0BUD4PkQgsXw5r1sC6dUFwG69eDbFYVVg9dw6Zm2uMA00C2V3u2RMArFwJnhcmCDo+jmSzYeY2AeQcum0bYkBN2pIAcrlcWQaku3txM6SquOlp3OwsxUwGMhl0ZgZvdpaukyfxtmxpP1Dh6VOKjx/jMhm8Dx/w8nk8Efx4HM92mghYER8+DIlE+4Hc1avIy5dINGrHffD51ayIjxxZHCAmJ+HZs/ql/R/on8mQSUmlrg4c6HANmaAmEtDfH2ia/fb9Nuyy7/qlySSydy81i9qyEo/DyEiVurf2pDYRHRiADRvQfB7p6akNVEdaNJ1GcrkwgtNAy6ytOHasDLPAam17VUpr1+IfP75gqrMDMp3GCyfEdYBsCVasQE+fDh5N5plOTiI1dpn78oXi4CBdO3aUZ2upRP76dWLPnyPWsjS2+hlyIsjYGDKv47MzWScm8N6/r3YvQuHjRwq9veWl1TdviBUK+CYn4aw+kBaLfE0mie3b98Pd1/v3iUxNBZr1OxMpZ0esf6olK7+/snE/VJqbI9/Tg5dM4l69Ivr6NVFrzpoLFC4/zXSMznZYLFZVT2EjhZzXOEMhHbVqWhnIZHtzqzz+lR/VawZkbz32/5WjVl3s3CHR0dHtOPcQsTavg6Y6Qza7qfI6ZgzVM+XWszNWpFTaJRMT934QaCp1CjgDxBeNyY4OkWlEjsqFC/cs7oKU6PDwekQGEdmKc9WPoK0i9X1FJIdzD1C9LRcvfqq4/gYOF90Ot1xnCQAAAABJRU5ErkJggg==";

//
var script$5b = {
  name: 'audio-play',
  props: {
    url: {
      type: String,
      default: '',
      require: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    }
  },
  data() {
    return {
      playIcon: playIcon$2,
      pauseIcon: pauseIcon$2,
      audioIcon: audioIcon$1,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      isShowPlay: false
    };
  },
  watch: {
    url: {
      handler() {
        this.isPlay = false;
      }
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : "0" + minute;
      second = second >= 10 ? second : "0" + second;
      return minute + ":" + second;
    },
    play() {
      // console.log()
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play();
      this.isPlay = !this.isPlay;
      this.$emit('play');
    },
    updateTime() {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    }
  }
};

/* script */
const __vue_script__$5b = script$5b;
/* template */
var __vue_render__$5b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticStyle: { width: "100%" } }, [
    !_vm.mobile && !_vm.isLineFile
      ? _c("div", { staticClass: "audio", attrs: { flex: "" } }, [
          _c("div", { staticClass: "audio-icon" }, [
            _c("img", { attrs: { src: _vm.audioIcon, alt: "" } }),
          ]),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "audio-controls",
              attrs: { flex: "dir:top main:justify" },
            },
            [
              _c(
                "div",
                {
                  staticClass: "audio-controls--handler",
                  attrs: { flex: "main:justify cross:center" },
                },
                [
                  _c("div", { staticClass: "current-time" }, [
                    _vm._v(_vm._s(_vm.playCurrentTime)),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "play" }, [
                    _c("img", {
                      staticClass: "play-handler",
                      attrs: { src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon },
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.play.apply(null, arguments)
                        },
                      },
                    }),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "total-time" }, [
                    _vm._v(_vm._s(_vm.playDuration)),
                  ]),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "audio-controls--progress" },
                [
                  _c("el-slider", {
                    attrs: {
                      disabled: _vm.playbackControl === "1",
                      max: _vm.totalDuration,
                      "show-tooltip": false,
                    },
                    on: { change: _vm.changeTime },
                    model: {
                      value: _vm.playTime,
                      callback: function ($$v) {
                        _vm.playTime = $$v;
                      },
                      expression: "playTime",
                    },
                  }),
                ],
                1
              ),
            ]
          ),
        ])
      : _c(
          "div",
          [
            _c("img", {
              staticClass: "play-handler mobile",
              attrs: { src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  _vm.isShowPlay = true;
                },
              },
            }),
            _vm._v(" "),
            _c(
              "el-dialog",
              {
                attrs: {
                  width: "100%",
                  top: "60px",
                  "custom-class": "video-play--dialog",
                  "destroy-on-close": "",
                  "append-to-body": "",
                  visible: _vm.isShowPlay,
                },
                on: {
                  "update:visible": function ($event) {
                    _vm.isShowPlay = $event;
                  },
                },
              },
              [
                _c("div", { staticClass: "audio", attrs: { flex: "" } }, [
                  _c("div", { staticClass: "audio-icon" }, [
                    _c("img", { attrs: { src: _vm.audioIcon, alt: "" } }),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "audio-controls",
                      attrs: { flex: "dir:top main:justify" },
                    },
                    [
                      _c(
                        "div",
                        {
                          staticClass: "audio-controls--handler",
                          attrs: { flex: "main:justify cross:center" },
                        },
                        [
                          _c("div", { staticClass: "current-time" }, [
                            _vm._v(_vm._s(_vm.playCurrentTime)),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "play" }, [
                            _c("img", {
                              staticClass: "play-handler",
                              attrs: {
                                src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                              },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.play.apply(null, arguments)
                                },
                              },
                            }),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "total-time" }, [
                            _vm._v(_vm._s(_vm.playDuration)),
                          ]),
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "div",
                        { staticClass: "audio-controls--progress" },
                        [
                          _c("el-slider", {
                            attrs: {
                              disabled: _vm.playbackControl === "1",
                              max: _vm.totalDuration,
                              "show-tooltip": false,
                            },
                            on: { change: _vm.changeTime },
                            model: {
                              value: _vm.playTime,
                              callback: function ($$v) {
                                _vm.playTime = $$v;
                              },
                              expression: "playTime",
                            },
                          }),
                        ],
                        1
                      ),
                    ]
                  ),
                ]),
              ]
            ),
          ],
          1
        ),
    _vm._v(" "),
    _c("audio", {
      ref: "musicAudio",
      staticClass: "audio-component",
      attrs: { controls: "", preload: "auto", src: _vm.url },
      on: {
        canplay: _vm.changeDuration,
        timeupdate: _vm.updateTime,
        pause: function ($event) {
          _vm.isPlay = false;
        },
      },
    }),
  ])
};
var __vue_staticRenderFns__$5b = [];
__vue_render__$5b._withStripped = true;

  /* style */
  const __vue_inject_styles__$5b = undefined;
  /* scoped */
  const __vue_scope_id__$5b = "data-v-74d98ece";
  /* module identifier */
  const __vue_module_identifier__$5b = undefined;
  /* functional template */
  const __vue_is_functional_template__$5b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5b, staticRenderFns: __vue_staticRenderFns__$5b },
    __vue_inject_styles__$5b,
    __vue_script__$5b,
    __vue_scope_id__$5b,
    __vue_is_functional_template__$5b,
    __vue_module_identifier__$5b,
    false,
    undefined,
    undefined,
    undefined
  );

var videoIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAaRJREFUSEu91surTXEUwPHP8iiSyAQTpRRTUkqu3CHFkETdy8BcKFNGir/AwKM8YuRRjOTGnfEHGJgwwUQMjDyWftr7dhxn733O0blruNfju9dvPX6/0CKZuQYHcQBbsL4y/4S3eILHEfG1KUwMUmTmOpzHKRRIm5TgV3EpIj73G/4DyMy9uIcNHYH71R9xJCJe9Cr+AmTmMdzE0hGD1+Y/MRMRt+sPC4DM3IPnWDZm8NrtB6YjYr58+APIzLV401PE/2QoTbAtIr7UgMs42xC1pP0AO7B5BPKViDgXmbkaH7CqwXk+IqYys+gv4nSdeQfsGzYWwCyutxjPRcR0rc/M3VUjlLnokhMFcAdHhwVUNVuJCziDJS2+dwvgFXaOAujJZleVzdYG/9cF8A6bxgFU2azAM5Sj65f3iwKY+BFNvMgzuDFsDUZs09nJD1rVCV2r4iG2j7UqFmXZVZApzHVMZtdqKPpf2BcRLxfWdc9kHsc1LB8m0gCb7zgZEbdq3aArs2Ryf8wr83D9542A6rgmd+n3pl09Ww5hf8Oz5SketT1bfgPyir14bBOMeQAAAABJRU5ErkJggg==";

//
var script$5a = {
  name: 'video-play',
  components: {
    videoPlayer: VueVideoPlayer.videoPlayer
  },
  props: {
    url: {
      type: String,
      default: '',
      require: true
    }
  },
  watch: {
    url: {
      handler() {
        // this.isPlay = false
        this.playerOptions.sources[0].src = this.url;
      },
      immediate: true
    }
  },
  data() {
    return {
      pageType: 'pc',
      isShowPlay: false,
      videoIcon,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      isShowControls: false,
      playerOptions: {
        playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
        //播放速度
        autoplay: false,
        //如果true,浏览器准备好时开始回放。
        muted: false,
        // 默认情况下将会消除任何音频。
        loop: false,
        // 导致视频一结束就重新开始。
        preload: 'auto',
        // 建议浏览器在<video>加载元素后是否应该开始下载视频数据。auto浏览器选择最佳行为,立即开始加载视频（如果浏览器支持）
        language: 'zh-CN',
        aspectRatio: '16:9',
        // 将播放器置于流畅模式，并在计算播放器的动态大小时使用该值。值应该代表一个比例 - 用冒号分隔的两个数字（例如"16:9"或"4:3"）
        fluid: true,
        // 当true时，Video.js player将拥有流体大小。换句话说，它将按比例缩放以适应其容器。
        sources: [{
          type: '',
          //这里的种类支持很多种：基本视频格式、直播、流媒体等，具体可以参看git网址项目
          src: '' //url地址
        }],
        poster: '',
        //你的封面地址
        // width: document.documentElement.clientWidth, //播放器宽度
        notSupportedMessage: '获取视频失败，请刷新重试',
        //允许覆盖Video.js无法播放媒体源时显示的默认信息。
        controlBar: {
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true //全屏按钮
        }
      },
      playerEvents: ['fullscreenchange']
    };
  },
  inject: ['wrapRoot', 'inKey'],
  created() {
    this.pageType = this.$route.query.pageType || 'pc';
  },
  methods: {
    onPlayerPlay(e) {
      let currentVideo = e.el_.children[0].getAttribute('id', 64);
      let videoList = document.getElementsByTagName('video');
      for (let i = 0; i < videoList.length; i++) {
        let id = videoList[i].getAttribute('id');
        if (currentVideo != id) {
          videoList[i].pause();
        }
      }
      let audio = document.querySelectorAll('audio');
      if (audio && audio.length) audio.forEach(i => i.pause());
    },
    showPlay() {
      this.isShowPlay = true;
      this.wrapRoot.currentInKey = this.inKey;
    },
    toggleFullscreen(e) {
      if (this.wrapRoot.currentInKey === this.inKey) {
        const data = {
          type: 'setIframeClass',
          value: e.isFullscreen()
        };
        window.parent.postMessage(data);
      }
    }
  }
};

/* script */
const __vue_script__$5a = script$5a;
/* template */
var __vue_render__$5a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "video" },
    [
      _c(
        "div",
        {
          staticClass: "video-content",
          staticStyle: { "background-color": "#000" },
        },
        [
          _c("video", {
            staticStyle: { width: "180px", height: "100%" },
            attrs: { src: _vm.url },
          }),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "play",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.showPlay.apply(null, arguments)
                },
              },
            },
            [_c("img", { attrs: { src: _vm.videoIcon, alt: "" } })]
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          attrs: {
            top: "60px",
            width: _vm.pageType === "pc" ? "50%" : "100%",
            "custom-class": "video-play--dialog",
            "destroy-on-close": "",
            "append-to-body": "",
            visible: _vm.isShowPlay,
          },
          on: {
            "update:visible": function ($event) {
              _vm.isShowPlay = $event;
            },
          },
        },
        [
          _c("video-player", {
            ref: "videoPlayer",
            staticClass: "video-player vjs-custom-skin",
            attrs: {
              events: _vm.playerEvents,
              playsinline: true,
              options: _vm.playerOptions,
            },
            on: {
              "!fullscreenchange": function ($event) {
                return _vm.toggleFullscreen($event)
              },
              play: function ($event) {
                return _vm.onPlayerPlay($event)
              },
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$5a = [];
__vue_render__$5a._withStripped = true;

  /* style */
  const __vue_inject_styles__$5a = undefined;
  /* scoped */
  const __vue_scope_id__$5a = "data-v-30e4662d";
  /* module identifier */
  const __vue_module_identifier__$5a = undefined;
  /* functional template */
  const __vue_is_functional_template__$5a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5a, staticRenderFns: __vue_staticRenderFns__$5a },
    __vue_inject_styles__$5a,
    __vue_script__$5a,
    __vue_scope_id__$5a,
    __vue_is_functional_template__$5a,
    __vue_module_identifier__$5a,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$59 = {
  name: 'bc-file-render',
  components: {
    audioPlay: __vue_component__$5b,
    videoPlay: __vue_component__$5a
  },
  props: {
    fileInfo: {
      type: Object,
      default: () => ({
        fileUrl: '',
        fileType: '',
        fileSuffix: ''
      }),
      required: true
    },
    showHandler: {
      type: Boolean,
      default: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    }
  },
  computed: {
    fileType() {
      let type;
      if (this.fileInfo.fileType.includes('image')) type = 'image';
      if (this.fileInfo.fileType.includes('audio')) type = 'audio';
      if (this.fileInfo.fileType.includes('video')) type = 'video';
      return type;
    }
  },
  methods: {
    againUpload() {
      // this.$emit('remove')
      this.$emit('againUpload');
    },
    remove() {
      this.$emit('remove');
    }
  }
};

/* script */
const __vue_script__$59 = script$59;
/* template */
var __vue_render__$59 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "file-render", attrs: { flex: "" } },
    [
      _vm.fileType === "image"
        ? _c("div", [
            _c("img", {
              staticClass: "image-file",
              attrs: { src: _vm.fileInfo.fileUrl },
            }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.fileType === "audio"
        ? _c("audio-play", {
            style: { width: _vm.showHandler ? "256px" : "100%" },
            attrs: {
              playbackControl: _vm.playbackControl,
              isLineFile: _vm.isLineFile,
              mobile: _vm.mobile,
              url: _vm.fileInfo.fileUrl,
            },
            on: {
              play: function ($event) {
                return _vm.$emit("play")
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.fileType === "video"
        ? _c("video-play", {
            staticClass: "file-render--video",
            attrs: {
              playbackControl: _vm.playbackControl,
              isLineFile: _vm.isLineFile,
              mobile: _vm.mobile,
              url: _vm.fileInfo.fileUrl,
            },
            on: {
              play: function ($event) {
                return _vm.$emit("play")
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.showHandler
        ? _c(
            "div",
            {
              staticClass: "file-info",
              class: { videoHandler: _vm.fileType === "video" },
              staticStyle: { "margin-left": "28px" },
            },
            [
              _vm.fileType !== "audio"
                ? [
                    _vm.fileInfo.fileName
                      ? _c(
                          "el-tooltip",
                          {
                            staticClass: "item",
                            attrs: {
                              "popper-class": "atooltip",
                              effect: "dark",
                              content: "文件名称：" + _vm.fileInfo.fileName,
                              placement: "top-start",
                            },
                          },
                          [
                            _c(
                              "div",
                              { staticClass: "fileName text-overflow" },
                              [
                                _vm._v(
                                  "文件名称：" + _vm._s(_vm.fileInfo.fileName)
                                ),
                              ]
                            ),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _c("div", { staticClass: "fileInfo" }, [
                      _vm._v("视频格式：" + _vm._s(_vm.fileInfo.fileSuffix)),
                    ]),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _c(
                "div",
                { attrs: { flex: "cross:center" } },
                [
                  _c(
                    "el-button",
                    {
                      staticClass: "dialog-submit",
                      attrs: { type: "primary" },
                      on: { click: _vm.againUpload },
                    },
                    [_vm._v("重新上传")]
                  ),
                  _vm._v(" "),
                  _c(
                    "el-button",
                    { staticClass: "dialog-close", on: { click: _vm.remove } },
                    [_vm._v("删除")]
                  ),
                ],
                1
              ),
            ],
            2
          )
        : _vm._e(),
    ],
    1
  )
};
var __vue_staticRenderFns__$59 = [];
__vue_render__$59._withStripped = true;

  /* style */
  const __vue_inject_styles__$59 = undefined;
  /* scoped */
  const __vue_scope_id__$59 = "data-v-662ea4d4";
  /* module identifier */
  const __vue_module_identifier__$59 = undefined;
  /* functional template */
  const __vue_is_functional_template__$59 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$59 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$59, staticRenderFns: __vue_staticRenderFns__$59 },
    __vue_inject_styles__$59,
    __vue_script__$59,
    __vue_scope_id__$59,
    __vue_is_functional_template__$59,
    __vue_module_identifier__$59,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$58 = {
  name: 'BcViewCommon',
  components: {
    bcFileRenderNew: __vue_component__$59
  },
  props: {
    content: {
      type: String,
      default: ''
    },
    standardAnswer: {
      type: Array,
      default: () => []
    },
    fileInfo: {
      type: Object,
      default: () => ({
        fileUrl: '',
        fileType: '',
        fileSuffix: ''
      })
    },
    placeholder: {
      type: String,
      default: ''
    },
    border: {
      type: Boolean,
      default: true
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isLineFile: {
      type: Boolean,
      default: false
    },
    playbackControl: {
      type: String,
      default: '0'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {};
  },
  methods: {
    formatImg(str) {
      if (str) {
        let data = str.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/gi, (match, p1) => {
          if (match.indexOf('style') !== -1) {
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image.split('style="')[0] + 'style="max-width:100% !important;' + image.split('style="')[1];
          } else if (match.indexOf('width') !== -1) {
            let src = this.resourceBasisPath + p1.split('./')[1];
            let image = match.indexOf('http') !== -1 ? match : match.replace(p1, src);
            return image;
          } else {
            return `<img src='${p1.indexOf('http') > -1 ? p1 : this.resourceBasisPath + p1.split('./')[1]}' style="max-width:100%;" />`;
          }
        });
        return data;
      }
    },
    showImage(e) {
      if (e.target.tagName == 'IMG') {
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: e.target.src,
            title: ''
          }]
        });
      }
    }
  }
};

/* script */
const __vue_script__$58 = script$58;
/* template */
var __vue_render__$58 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "content" }, [
    _c(
      "div",
      { staticClass: "stem-content", class: { "no-border": !_vm.border } },
      [
        !_vm.content && !_vm.fileInfo.fileUrl && !_vm.standardAnswer
          ? _c("div", { staticClass: "placeholder" }, [
              _vm._v(_vm._s(_vm.placeholder)),
            ])
          : _vm.content
          ? _c("div", {
              staticClass: "text-break-all",
              domProps: { innerHTML: _vm._s(_vm.formatImg(_vm.content)) },
              on: {
                click: function ($event) {
                  return _vm.showImage($event)
                },
              },
            })
          : _vm.standardAnswer
          ? _c(
              "div",
              _vm._l(_vm.standardAnswer, function (item, index) {
                return _c("div", {
                  key: index,
                  domProps: { innerHTML: _vm._s(_vm.formatImg(item.content)) },
                  on: {
                    click: function ($event) {
                      return _vm.showImage($event)
                    },
                  },
                })
              }),
              0
            )
          : _vm._e(),
        _vm._v(" "),
        _vm._t("default"),
        _vm._v(" "),
        _vm.fileInfo.fileUrl
          ? _c("bc-file-render-new", {
              style: {
                "margin-top": _vm.content ? "16px" : 0,
                padding: "10px 0",
              },
              attrs: {
                playbackControl: _vm.playbackControl,
                isLineFile: _vm.isLineFile,
                mobile: _vm.mobile,
                showHandler: false,
                fileInfo: _vm.fileInfo,
              },
              on: {
                play: function ($event) {
                  return _vm.$emit("play")
                },
              },
            })
          : _vm._e(),
      ],
      2
    ),
  ])
};
var __vue_staticRenderFns__$58 = [];
__vue_render__$58._withStripped = true;

  /* style */
  const __vue_inject_styles__$58 = undefined;
  /* scoped */
  const __vue_scope_id__$58 = "data-v-b1d65954";
  /* module identifier */
  const __vue_module_identifier__$58 = undefined;
  /* functional template */
  const __vue_is_functional_template__$58 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$58 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$58, staticRenderFns: __vue_staticRenderFns__$58 },
    __vue_inject_styles__$58,
    __vue_script__$58,
    __vue_scope_id__$58,
    __vue_is_functional_template__$58,
    __vue_module_identifier__$58,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$57 = {
  name: 'bc-view-option-radio',
  components: {
    BcViewCommon: __vue_component__$58
  },
  props: {
    option: {
      type: [String, Array],
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    isSubmit: {
      type: Boolean,
      default: true
    },
    value: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    answer: {
      type: Object,
      default: () => {}
    },
    type: {
      type: String,
      default: 'radio'
    },
    standardAnswer: {
      type: [String, Number],
      default: ''
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {
      optionsList: [],
      selectData: '',
      isAnswer: false,
      answerIsTrue: false,
      radioJudeMap: {
        A: '正确',
        B: '错误'
      },
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  computed: {},
  watch: {
    value: {
      handler(val) {
        this.selectData = val;
      },
      immediate: true
    },
    option: {
      handler(val) {
        try {
          if (Array.isArray(val)) {
            this.optionsList = val;
          } else {
            this.optionsList = JSON.parse(val);
          }
        } catch (err) {
          this.optionsList = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    isActive(item) {
      return item.number === this.selectData;
    },
    isTrue(item) {
      if (this.type === 'radio') {
        return this.isAnswer && item.number === this.selectData && item.isRightAnswer;
      }
    },
    isFalse(item) {
      if (!this.isAnswer) {
        return false;
      }
      if (this.type === 'radio') {
        if (this.selectData) {
          //当前为选中的
          return item.number === this.selectData && !item.isRightAnswer;
        } else {
          return false;
        }
      }
    },
    submitAnswer() {
      console.log('🚀 ~ file: radio.vue:258 ~ submitAnswer ~ this.selectData:', this.selectData);
      if (!this.selectData && this.isSubmit) {
        this.$message.error('请选择答案');
        return;
      }
      // let answerIsTrue = false
      // if (this.type === 'radio') {
      //   console.log(this.selectData, this.standardAnswer)
      //   answerIsTrue = this.optionsList[this.standardAnswer - 1].number === this.selectData ? true : false
      //   // answerIsTrue = this.standardAnswer.some((i) => i.title === this.selectData)
      // } else {
      //   answerIsTrue = this.standardAnswer.some((i) => i === this.radioJudeMap[this.selectData])
      // }

      this.isAnswer = true;
      this.$emit('showAnalysis', this.answerIsTrue);
    },
    afresh() {
      this.selectData = '';
      this.isAnswer = false;
      this.$emit('afresh');
    },
    select(item) {
      this.selectData = item.number;
      this.answerIsTrue = item.isRightAnswer;
      // this.$emit('showAnalysis')
    }
  }
};

/* script */
const __vue_script__$57 = script$57;
var __vue_render__$57 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      { staticClass: "xml-checkbox-box" },
      _vm._l(_vm.optionsList, function (item, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "option-item",
            class: {
              isActive: _vm.isActive(item),
              isTrue: _vm.isTrue(item),
              isFalse: _vm.isFalse(item),
            },
            attrs: { flex: "" },
            on: {
              click: function ($event) {
                $event.stopPropagation();
                return _vm.select(item)
              },
            },
          },
          [
            _c(
              "div",
              { staticClass: "questionSeq" },
              [
                _vm.isTrue(item)
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_correct_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.isFalse(item)
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_error_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                !_vm.readonly && !_vm.isTrue(item) && !_vm.isFalse(item)
                  ? _c(
                      "el-radio",
                      { attrs: { value: _vm.selectData, label: item.number } },
                      [_vm._v(_vm._s(""))]
                    )
                  : _vm._e(),
              ],
              1
            ),
            _vm._v(" "),
            _vm.showTitle
              ? _c(
                  "div",
                  {
                    staticClass: "questionSeq",
                    staticStyle: { "margin-right": "2px" },
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.serialNumber[index]) +
                        "\n      "
                    ),
                  ]
                )
              : _vm._e(),
            _vm._v(" "),
            _c("bc-view-common", {
              attrs: {
                "flex-box": "1",
                border: false,
                content: item.description,
                fileInfo: item.fileInfo,
                resourceBasisPath: _vm.resourceBasisPath,
                pageType: _vm.pageType,
              },
            }),
          ],
          1
        )
      }),
      0
    ),
    _vm._v(" "),
    _vm.isSubmit
      ? _c(
          "div",
          {
            staticClass: "mt-4",
            staticStyle: {
              display: "flex",
              "justify-content": "flex-end",
              "text-align": "center",
              "margin-top": "12px",
            },
          },
          [
            !_vm.selectData || !_vm.isAnswer
              ? _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: { click: _vm.submitAnswer },
                  },
                  [_vm._v("提交作答")]
                )
              : _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: {
                      click: function ($event) {
                        return _vm.afresh()
                      },
                    },
                  },
                  [_vm._v("重新作答")]
                ),
          ]
        )
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$57 = [];
__vue_render__$57._withStripped = true;

  /* style */
  const __vue_inject_styles__$57 = undefined;
  /* scoped */
  const __vue_scope_id__$57 = "data-v-99dd6af0";
  /* module identifier */
  const __vue_module_identifier__$57 = undefined;
  /* functional template */
  const __vue_is_functional_template__$57 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$57 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$57, staticRenderFns: __vue_staticRenderFns__$57 },
    __vue_inject_styles__$57,
    __vue_script__$57,
    __vue_scope_id__$57,
    __vue_is_functional_template__$57,
    __vue_module_identifier__$57,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$56 = {
  name: 'bc-view-option-checkbox',
  components: {
    BcViewCommon: __vue_component__$58
  },
  props: {
    option: {
      type: [String, Array],
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    isSubmit: {
      type: Boolean,
      default: true
    },
    value: {
      type: [String],
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    answer: {
      type: Object,
      default: () => {}
    },
    standardAnswer: {
      type: [String, Number],
      default: ''
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  data() {
    return {
      optionsList: [],
      selectData: [],
      isAnswer: false,
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  watch: {
    // value: {
    //   handler(val) {
    //     this.selectData = val ? val : []
    //   },
    //   immediate: true
    // },
    option: {
      handler(val) {
        try {
          if (Array.isArray(val)) {
            this.optionsList = val;
          } else {
            this.optionsList = JSON.parse(val);
          }
        } catch (err) {
          this.optionsList = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    select(item, e) {
      // debugger
      if (e.target.tagName === 'INPUT') {
        e.stopPropagation();
        return;
      }
      if (this.readonly) return;
      let index = this.selectData.findIndex(i => i === item);
      if (index !== -1) {
        this.selectData.splice(index, 1);
      } else {
        this.selectData.push(item);
      }
      this.$emit('change', this.selectData.join(''));
    },
    submitAnswer() {
      if (!this.selectData.length && this.isSubmit) {
        this.$message.error('请选择答案');
        return;
      }

      // let answerIsTrue = this.standardAnswer.map((i) => i.title).join('、') === this.selectData.join('、')
      let answerIsTrue = false;
      this.isAnswer = true;
      this.$emit('showAnalysis', answerIsTrue);
    },
    afresh() {
      this.selectData = [];
      this.isAnswer = false;
      this.$emit('afresh');
    },
    SkipProgress(e, title, val) {
      if (e.target.targetName === 'INPUT') {
        e.stopPropagation();
      }
    }
  }
};

/* script */
const __vue_script__$56 = script$56;
var __vue_render__$56 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      { staticClass: "xml-checkbox-box" },
      _vm._l(_vm.optionsList, function (item, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "option-item",
            class: {
              isActive: _vm.selectData.includes(item.number) && !_vm.readonly,
              isTrue:
                _vm.isAnswer &&
                _vm.selectData.includes(item.number) &&
                item.isRightAnswer,
              isFalse:
                _vm.isAnswer &&
                _vm.selectData.includes(item.number) &&
                !item.isRightAnswer,
            },
            attrs: { flex: "" },
            on: {
              click: function ($event) {
                $event.stopPropagation();
                return _vm.select(item.number, $event)
              },
            },
          },
          [
            _c(
              "div",
              { staticClass: "questionSeq" },
              [
                _vm.isAnswer &&
                _vm.selectData.includes(item.number) &&
                item.isRightAnswer
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_correct_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.isAnswer &&
                _vm.selectData.includes(item.number) &&
                !item.isRightAnswer
                  ? _c("img", {
                      staticStyle: { "margin-right": "10px" },
                      attrs: {
                        src: __$_require_static_images_leaflet_icons_error_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                !_vm.readonly && !_vm.isAnswer
                  ? _c(
                      "el-checkbox",
                      {
                        attrs: { value: _vm.selectData.includes(item.number) },
                        nativeOn: {
                          click: function ($event) {
                            _vm.SkipProgress(
                              $event,
                              item.number,
                              _vm.selectData.includes(item.number)
                            );
                          },
                        },
                      },
                      [_vm._v(_vm._s(""))]
                    )
                  : _vm._e(),
              ],
              1
            ),
            _vm._v(" "),
            _vm.showTitle
              ? _c(
                  "div",
                  {
                    staticClass: "questionSeq",
                    staticStyle: { "margin-right": "2px" },
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.serialNumber[index]) +
                        "\n      "
                    ),
                  ]
                )
              : _vm._e(),
            _vm._v(" "),
            _c("bc-view-common", {
              attrs: {
                "flex-box": "1",
                border: false,
                content: item.description,
                fileInfo: item.fileInfo,
                resourceBasisPath: _vm.resourceBasisPath,
                pageType: _vm.pageType,
              },
            }),
          ],
          1
        )
      }),
      0
    ),
    _vm._v(" "),
    _vm.isSubmit
      ? _c(
          "div",
          {
            staticClass: "mt-4",
            staticStyle: {
              display: "flex",
              "justify-content": "flex-end",
              "text-align": "center",
              "margin-top": "12px",
            },
          },
          [
            !_vm.selectData || !_vm.isAnswer
              ? _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: { click: _vm.submitAnswer },
                  },
                  [_vm._v("提交作答")]
                )
              : _c(
                  "div",
                  {
                    staticClass: "xml-submit-answer",
                    on: {
                      click: function ($event) {
                        return _vm.afresh()
                      },
                    },
                  },
                  [_vm._v("重新作答")]
                ),
          ]
        )
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$56 = [];
__vue_render__$56._withStripped = true;

  /* style */
  const __vue_inject_styles__$56 = undefined;
  /* scoped */
  const __vue_scope_id__$56 = "data-v-a1e4d57a";
  /* module identifier */
  const __vue_module_identifier__$56 = undefined;
  /* functional template */
  const __vue_is_functional_template__$56 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$56 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$56, staticRenderFns: __vue_staticRenderFns__$56 },
    __vue_inject_styles__$56,
    __vue_script__$56,
    __vue_scope_id__$56,
    __vue_is_functional_template__$56,
    __vue_module_identifier__$56,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$55 = {
  name: 'QuestionItem',
  components: {
    BcViewOptionCheckbox: __vue_component__$56,
    BcViewOptionRadio: __vue_component__$57,
    BcViewCommon: __vue_component__$58
  },
  // dicts: ['question_type'],
  data() {
    return {
      // isShowAnalysis: false,
      // isShowAnswer: false,
      // isShowKnowledge: false,
      inputAnswer: '',
      dict: {
        label: {
          question_type: {
            1: '单选题',
            2: '多选题',
            3: '判断题',
            13: '简答题'
          }
        }
      },
      isAnswer: false,
      serialNumber: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  props: {
    question: Object,
    canShowAnswer: String,
    canShowAnalysis: String,
    canShowKnowledge: String,
    inKey: String,
    border: {
      type: Boolean,
      default: false
    },
    isSubmit: {
      //是否显示提交作答按钮
      type: Boolean,
      default: true
    },
    questionNumber: {
      type: Number,
      default: -1
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    }
  },
  provide: function () {
    return {
      inKey: this.inKey
    };
  },
  mounted() {
    console.log(this.question);
  },
  computed: {
    isShowAnalysis() {
      return this.isAnswer && this.canShowAnalysis === 'true';
    },
    isShowAnswer() {
      return this.isAnswer && this.canShowAnswer === 'true';
    },
    isShowKnowledge() {
      return this.isAnswer && this.canShowKnowledge === 'true';
    },
    isRadio() {
      return isRadio(this.question.type);
    },
    isCheck() {
      return isCheck(this.question.type);
    },
    showJudgeAnswer() {
      return showJudgeAnswer(this.question.type);
    },
    showRichTextAnswer() {
      return showRichTextAnswer(this.question.type);
    },
    showOptions() {
      return showOptions(this.question.type);
    },
    showSort() {
      return showSort(this.question.type);
    },
    isCloze() {
      return isCloze(this.question.type);
    },
    clozeLength() {
      let fillInTheBlanksSpan = this.question.stem.content.match(/fillInTheBlanksSpan/g);
      let sortString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let data = fillInTheBlanksSpan ? fillInTheBlanksSpan.length : 0;
      let list = [];
      for (let i = 0; i < data; i++) {
        list.push({
          content: '',
          title: sortString.split('')[i]
        });
      }
      // return fillInTheBlanksSpan ? fillInTheBlanksSpan.length : 0
      return list;
    },
    standardAnswer() {
      try {
        let answerTitle = '';
        if (!this.showRichTextAnswer) {
          if (this.showOptions) {
            answerTitle = this.question.standardAnswer.map(i => i.title).join('、');
          } else if (this.showJudgeAnswer) {
            answerTitle = this.question.standardAnswer.map(i => i).join('、');
          } else if (this.showSort) {
            let questionOption = xeUtils.clone(this.question.questionOption, true);
            answerTitle = questionOption.sort((c, n) => c.index - n.index).map(i => i.title).join('、');
          }
        }
        return answerTitle;
      } catch (e) {
        console.log('异常：', e);
        return '';
      }
    }
  },
  methods: {
    showAnalysis(answer) {
      if (!this.showRichTextAnswer) {
        this.isAnswer = true;
        let params = {
          pass: answer,
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem)
        };
        this.$emit('submitAnswer', params);
      } else {
        this.isAnswer = true;
      }
    },
    showAnalysis2(answer) {
      if (answer.questionType == 'shortSnswer' && this.isSubmit) {
        if (!answer.userAnswer) {
          this.$message.error('请先输入答案');
          return;
        }
      } else if (answer.questionType == 'fillInTheBlanks' && this.isSubmit) {
        for (let i = 0; i < this.clozeLength.length; i++) {
          if (!this.clozeLength[i].content) {
            this.$message.error('请先输入答案');
            return;
          }
        }
      }
      this.isAnswer = true;
      let params = {
        pass: answer,
        questionId: this.question.id,
        questionName: JSON.stringify(this.question.stem)
      };
      this.$emit('submitAnswer', params);
    },
    afresh(questionType) {
      this.isAnswer = false;
      if (questionType == 'shortSnswer') {
        this.inputAnswer = '';
      } else if (questionType == 'fillInTheBlanks') {
        for (let i = 0; i < this.clozeLength.length; i++) {
          this.clozeLength[i].content = '';
        }
      }
    },
    //
    changeAnswer() {
      if (this.isRadio || this.showJudgeAnswer) {
        this.$refs.bcViewOptionRadio?.submitAnswer();
      }
      if (this.isCheck) {
        this.$refs.bcViewOptionCheckbox?.submitAnswer();
      }
      if (this.showRichTextAnswer && !this.isCloze) {
        this.showAnalysis2({
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem),
          userAnswer: this.inputAnswer,
          questionType: 'shortSnswer'
        });
      }
      if (this.isCloze) {
        this.showAnalysis2({
          questionId: this.question.id,
          questionName: JSON.stringify(this.question.stem),
          userAnswer: this.clozeLength,
          questionType: 'fillInTheBlanks'
        });
      }
    },
    // AIanalysis(){
    //   let params = {
    //       questionOption: JSON.stringify(this.question.questionOption),
    //       questionName: JSON.stringify(this.question.stem)
    //   }
    //   this.$emit('setanalysis', params)
    // },
    AIanalysis() {
      let serialNumber = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];
      let option = '';
      let question = this.question.stem.content;
      let optionList = this.question.questionOption;
      for (let i = 0; i < optionList.length; i++) {
        if (optionList[i].content) {
          option += serialNumber[i] + '.' + this.removeStyle(optionList[i].content) + (i < optionList.length - 1 ? ';' : '');
        }
      }
      if (this.question.questionType == '3') {
        option = 'A.正确;B.错误';
      }
      const data = {
        type: 'AiAnalysis',
        value: this.removeStyle(question) + option,
        code: 'success'
      };
      this.$emit('setanalysis', data);
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    }
  }
};

/* script */
const __vue_script__$55 = script$55;
/* template */
var __vue_render__$55 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "div",
      {
        staticClass: "group-content",
        class: _vm.border ? "border-bottom" : "",
      },
      [
        _c("div", { staticClass: "group-content-box" }, [
          _c("div", { staticClass: "stem" }, [
            _c(
              "div",
              { staticClass: "stem-box", staticStyle: { display: "flex" } },
              [
                _vm.questionNumber !== -1
                  ? _c(
                      "div",
                      {
                        staticClass: "question-number",
                        staticStyle: {
                          "line-height": "28px",
                          "padding-right": "4px",
                        },
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.questionNumber + 1) +
                            " .\n          "
                        ),
                      ]
                    )
                  : _vm._e(),
                _vm._v(" "),
                _c("bc-view-common", {
                  staticClass: "xml-question-title",
                  staticStyle: { flex: "1", "min-width": "0" },
                  attrs: {
                    content: _vm.question.description,
                    border: false,
                    fileInfo: _vm.question.stem.fileInfo,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                }),
              ],
              1
            ),
          ]),
        ]),
        _vm._v(" "),
        _vm.isRadio
          ? _c(
              "div",
              { staticClass: "mt-4" },
              [
                _c("bc-view-option-radio", {
                  ref: "bcViewOptionRadio",
                  attrs: {
                    standardAnswer: _vm.question.answer,
                    option: _vm.question.candidates,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isCheck
          ? _c(
              "div",
              { staticClass: "mt-4" },
              [
                _c("bc-view-option-checkbox", {
                  ref: "bcViewOptionCheckbox",
                  attrs: {
                    option: _vm.question.candidates,
                    standardAnswer: _vm.question.answer,
                    resourceBasisPath: _vm.resourceBasisPath,
                    isSubmit: _vm.isSubmit,
                    pageType: _vm.pageType,
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.showRichTextAnswer && !_vm.isCloze
          ? _c(
              "div",
              {
                staticClass: "mt-4",
                staticStyle: { display: "flex", "flex-direction": "column" },
              },
              [
                _c("el-input", {
                  attrs: {
                    type: "textarea",
                    placeholder: "请输入您的回答",
                    autosize: { minRows: 2, maxRows: 8 },
                  },
                  model: {
                    value: _vm.inputAnswer,
                    callback: function ($$v) {
                      _vm.inputAnswer = $$v;
                    },
                    expression: "inputAnswer",
                  },
                }),
                _vm._v(" "),
                !_vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            _vm.showAnalysis2({
                              questionId: _vm.question.id,
                              questionName: JSON.stringify(_vm.question.stem),
                              userAnswer: _vm.inputAnswer,
                              questionType: "shortSnswer",
                            });
                          },
                        },
                      },
                      [_vm._v("\n        提交作答\n      ")]
                    )
                  : _vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            return _vm.afresh("shortSnswer")
                          },
                        },
                      },
                      [_vm._v("\n        重新作答\n      ")]
                    )
                  : _vm._e(),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.showJudgeAnswer
          ? _c(
              "div",
              [
                _c("bc-view-option-radio", {
                  ref: "bcViewOptionRadio",
                  attrs: {
                    standardAnswer: _vm.question.answer,
                    resourceBasisPath: _vm.resourceBasisPath,
                    pageType: _vm.pageType,
                    isSubmit: _vm.isSubmit,
                    option: _vm.question.candidates,
                  },
                  on: { showAnalysis: _vm.showAnalysis, afresh: _vm.afresh },
                }),
              ],
              1
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isCloze
          ? _c(
              "div",
              [
                _vm._l(_vm.clozeLength, function (item, index) {
                  return _c("div", { key: index, staticClass: "isTrue" }, [
                    _c("input", {
                      directives: [
                        {
                          name: "model",
                          rawName: "v-model",
                          value: item.content,
                          expression: "item.content",
                        },
                      ],
                      staticClass: "answer-cloze-input",
                      attrs: {
                        maxlength: "100",
                        placeholder: "请输入...",
                        type: "text",
                      },
                      domProps: { value: item.content },
                      on: {
                        input: function ($event) {
                          if ($event.target.composing) {
                            return
                          }
                          _vm.$set(item, "content", $event.target.value);
                        },
                      },
                    }),
                  ])
                }),
                _vm._v(" "),
                !_vm.isAnswer && _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            _vm.showAnalysis2({
                              questionId: _vm.question.id,
                              questionName: JSON.stringify(_vm.question.stem),
                              userAnswer: _vm.clozeLength,
                              questionType: "Filling",
                            });
                          },
                        },
                      },
                      [_vm._v("\n        提交作答\n      ")]
                    )
                  : _vm.isAnswer & _vm.isSubmit
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "xml-submit-answer xml-submit-answer-right",
                        on: {
                          click: function ($event) {
                            return _vm.afresh("Filling")
                          },
                        },
                      },
                      [_vm._v("\n        重新作答\n      ")]
                    )
                  : _vm._e(),
              ],
              2
            )
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowAnswer
          ? _c("div", [
              !_vm.showRichTextAnswer
                ? _c("div", { staticClass: "mt-6" }, [
                    _c(
                      "div",
                      {
                        staticStyle: {
                          display: "flex",
                          "flex-direction": "column",
                        },
                      },
                      [
                        _c(
                          "div",
                          { staticClass: "question-label" },
                          [
                            _vm._v("正确答案：\n            "),
                            _vm.question.type == "SingleSelect"
                              ? [
                                  _c("span", [
                                    _vm._v(
                                      _vm._s(
                                        _vm.serialNumber[
                                          _vm.question.answer - 1
                                        ]
                                      )
                                    ),
                                  ]),
                                ]
                              : _vm._l(
                                  _vm.question.answer.split(","),
                                  function (item, index) {
                                    return _c("span", [
                                      _vm._v(
                                        "\n            " +
                                          _vm._s(_vm.serialNumber[item - 1]) +
                                          "\n          "
                                      ),
                                    ])
                                  }
                                ),
                          ],
                          2
                        ),
                        _vm._v(" "),
                        _c("div", {
                          staticStyle: {
                            color: "#0070c1",
                            "min-width": "0",
                            flex: "1",
                            "line-height": "28px",
                            display: "inline-block",
                          },
                        }),
                      ]
                    ),
                  ])
                : _c("div", { staticClass: "mt-6" }, [
                    _c(
                      "div",
                      {
                        staticStyle: {
                          display: "flex",
                          "flex-direction": "column",
                        },
                      },
                      [
                        _c("div", { staticClass: "question-label" }, [
                          _vm._v("正确答案："),
                        ]),
                        _vm._v(" "),
                        _c(
                          "div",
                          { staticStyle: { flex: "1", "min-width": "0" } },
                          [
                            _c("bc-view-common", {
                              staticStyle: { flex: "1", "min-width": "0" },
                              attrs: {
                                content: _vm.question.answer,
                                standardAnswer:
                                  _vm.question.questionType == 10
                                    ? _vm.question.standardAnswer
                                    : [],
                                border: false,
                                fileInfo: _vm.question.standardAnswer.fileInfo,
                                resourceBasisPath: _vm.resourceBasisPath,
                                isSubmit: _vm.isSubmit,
                                pageType: _vm.pageType,
                              },
                            }),
                          ],
                          1
                        ),
                      ]
                    ),
                  ]),
            ])
          : _vm._e(),
        _vm._v(" "),
        _vm.isShowAnalysis && _vm.question.feedback
          ? _c(
              "div",
              {
                staticClass: "mt-6 flex-direction-column",
                attrs: { flex: "" },
              },
              [
                _c(
                  "div",
                  {
                    staticClass: "question-label",
                    staticStyle: { "line-height": "40px" },
                  },
                  [_vm._v("解析：")]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "stem",
                    class: {
                      "analysis-content": _vm.question.feedback,
                    },
                    staticStyle: { "min-width": "0", flex: "1" },
                  },
                  [
                    _c("bc-view-common", {
                      attrs: {
                        border: false,
                        content: _vm.question.feedback,
                        fileInfo: _vm.question.analysis.fileInfo,
                        resourceBasisPath: _vm.resourceBasisPath,
                        isSubmit: _vm.isSubmit,
                        pageType: _vm.pageType,
                      },
                    }),
                  ],
                  1
                ),
              ]
            )
          : _vm._e(),
      ]
    ),
  ])
};
var __vue_staticRenderFns__$55 = [];
__vue_render__$55._withStripped = true;

  /* style */
  const __vue_inject_styles__$55 = undefined;
  /* scoped */
  const __vue_scope_id__$55 = "data-v-75c56b03";
  /* module identifier */
  const __vue_module_identifier__$55 = undefined;
  /* functional template */
  const __vue_is_functional_template__$55 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$55 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$55, staticRenderFns: __vue_staticRenderFns__$55 },
    __vue_inject_styles__$55,
    __vue_script__$55,
    __vue_scope_id__$55,
    __vue_is_functional_template__$55,
    __vue_module_identifier__$55,
    false,
    undefined,
    undefined,
    undefined
  );

//
// import { defaultCover } from '@/assets/remote'

/***
 * 试卷块儿
 */
var script$54 = {
  name: 'XmlTestPaperRender',
  components: {
    QuestionItem: __vue_component__$55
  },
  // dicts: ['question_type', 'difficulty', 'examine_status', 'answer_platform', 'answer_situation'],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    reader: {
      type: Boolean,
      default: false
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    BookName: {
      type: String,
      default: ''
    }
  },
  computed: {},
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('Question组件(ruleIndex:' + this.ruleIndex + ')，watch触发，变量pBlockData：', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    },
    pageType: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.tmpPageType = newValue;
      }
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-test-paper-property',
      radio1: null,
      tmpPageType: ''
      // defaultCover
    };
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    pageClick(info) {
      this.$emit('openTestPaper', {
        info: info,
        showRobot: this.isShowAi == '0' ? 'true' : 'false'
      });
      // console.log(this.BookName, '打印的info')
      //info.link 跳转地址
      // window.href = info.link
      // if (this.reader) {
      //   this.$emit('openTestPaper', { info: info, showRobot: this.isShowAi == '0' ? 'true' : 'false' })
      //   return
      // }
      // if (this.mode === 'preview') {
      //   if (this.tmpPageType === 'h5' || this.tmpPageType === 'pad') {
      // const url =
      //   process.env.VUE_APP_TESTPAPER_WEB +
      //   `/mobile-answer?testPaperId=${info.id}&showRobot=${this.isShowAi == '0' ? 'true' : 'false'}&BookName=${
      //     this.BookName
      //   }`
      // // const url = info.link
      // console.log('跳转url:' + url)
      // // window.location.href = url
      // this.$router.push({
      //   path: '/webView',
      //   query: {
      //     baseUrl: encodeURIComponent(url),
      //     title: info.paperName
      //   }
      // })
      //   } else {
      //     console.log(this.blockData)
      //     // window.location.href = process.env.VUE_APP_TESTPAPER_WEB + `/answer?testPaperId=${info.id}`
      //     this.$router.push({
      //       path: '/webView',
      //       query: {
      //         baseUrl: encodeURIComponent(
      //           process.env.VUE_APP_TESTPAPER_WEB +
      //             `/answer?testPaperId=${info.id}&showRobot=${this.isShowAi == '0' ? 'true' : 'false'}&BookName=${
      //               this.BookName
      //             }`
      //         ),
      //         title: info.paperName
      //       }
      //     })
      //     // window.location.href = info.link
      //   }
      // }
    }
  },
  created() {}
};

var __$_require_static_images_default_over_test_aper_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHgAAAB4CAYAAAA5ZDbSAAAACXBIWXMAAAsTAAALEwEAmpwYAAAE7mlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgOS4wLWMwMDAgNzkuMTcxYzI3ZiwgMjAyMi8wOC8xNi0xODowMjo0MyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczpkYz0iaHR0cDovL3B1cmwub3JnL2RjL2VsZW1lbnRzLzEuMS8iIHhtbG5zOnBob3Rvc2hvcD0iaHR0cDovL25zLmFkb2JlLmNvbS9waG90b3Nob3AvMS4wLyIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0RXZ0PSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VFdmVudCMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDI0LjAgKFdpbmRvd3MpIiB4bXA6Q3JlYXRlRGF0ZT0iMjAyNC0wNy0wNVQxNzoxOTo1MiswODowMCIgeG1wOk1vZGlmeURhdGU9IjIwMjQtMDctMDlUMTg6MTI6MTkrMDg6MDAiIHhtcDpNZXRhZGF0YURhdGU9IjIwMjQtMDctMDlUMTg6MTI6MTkrMDg6MDAiIGRjOmZvcm1hdD0iaW1hZ2UvcG5nIiBwaG90b3Nob3A6Q29sb3JNb2RlPSIzIiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOmUxYjk1YmU4LTdiMGUtMDE0Yi04YTNiLWExMjkwOGMzZWM2ZCIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDplMWI5NWJlOC03YjBlLTAxNGItOGEzYi1hMTI5MDhjM2VjNmQiIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDplMWI5NWJlOC03YjBlLTAxNGItOGEzYi1hMTI5MDhjM2VjNmQiPiA8eG1wTU06SGlzdG9yeT4gPHJkZjpTZXE+IDxyZGY6bGkgc3RFdnQ6YWN0aW9uPSJjcmVhdGVkIiBzdEV2dDppbnN0YW5jZUlEPSJ4bXAuaWlkOmUxYjk1YmU4LTdiMGUtMDE0Yi04YTNiLWExMjkwOGMzZWM2ZCIgc3RFdnQ6d2hlbj0iMjAyNC0wNy0wNVQxNzoxOTo1MiswODowMCIgc3RFdnQ6c29mdHdhcmVBZ2VudD0iQWRvYmUgUGhvdG9zaG9wIDI0LjAgKFdpbmRvd3MpIi8+IDwvcmRmOlNlcT4gPC94bXBNTTpIaXN0b3J5PiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PmuN73oAACVaSURBVHic7X1Zkx3Hld53Mqvuvd3oBtBYGyBAgAQJrpC405yhNZLomAiPJHNm5BnLdoT17gm/+c2P/geO0LveNJbt0IQshk1Zs2gomeJOgguIAUGA2Ahib3T33aoyjx9Onqq6t+9Sd2uSHp6IRqPvkpWZJ8++JDEzFOr1OpxzqFarc41G49tE9ANjzFPGmAVjDBMRvoLPH5gZ3nvy3t/y3r/KzP9jbm7ub1qtVt1ai/n5+eyzVERwq9VCs9k8wMw/tNY+b619MIqiLdZafIXcLxYwM5xzSNN01Tn3oXPu50T041qtdqFarWafI+ccWq0WiAhpmj7BzH9hrf0zRay19nNcxlcwDJxziui6c+6nRPSjKIped86hVquB2u02VldXEcfxo8z8n6Mo+k61Wh2KWAbwFU1/cUAJNU3TF4joP9Xr9XeWlpZgjDEwxtwB4D/EcTwUuZ6BtgPaqfz23PejX0EA5tnvk7UW1WoVcRx/h4j+fRzHe6y1MN77GoB/Z4z58ziOByLXOaDRlp9mG6i35P/OjT+xSdftGXAe8H7CgWYEzEAzkX1K0tk+y1qLOI5BRP86iqIfeu+jqNFoPBdF0Z/GcbwliqK+X/YMtFL5YQAGgAfAqSyiRkBkyk/GOaARFlyLgGhEUc8MJIGT6HziCIgt8EXSB70XxDoGDAERz3Z+URTBe7/ovf9+o9F4JwLwA2vtw4OQC4TNdPlmIvxmyCaDgLkYsGWQzEDby8kGhwMCQU4ZYJZnNtuyccoFUg/4GKhGXyAkEwADsJM5bca8oiiCc+5Ymqb/xhhjnrbW1owZjBkfENE9P4JscOqEVZYFCieaIIen0ZbfZXi2C9zEcb5pRIHLJPLzeaoGxWdTWKOhje/NCowxsNbOE9HvRcaYxWHIBWSChoRKuk8hUa5IlNKuSai1GijRsbCxyABxZfgCnO88TNT1XjOVOVXizdf0nevkKmCAvexP6gEqyGFLo4umsmCthTFmMbLW+jIIji2QRkDa3ohEDpRkRthNa0VuA0H5MIAxKI+RfqQQKLmRiI5QtWHcTQBGbmEUp6e+pCQVTgfI3CoG2GJmx7attT5CSa5hCKjG8ulWKhNURYsgJ9Ha0SjGGpHbqhgZE0wvL9Tc73RbI89Ke2jv+nzPQUaHeQGdK9VNVRaa/Q2ZR/G1sqDfNSZHKhhwQY6RyfWXjG3PCLnB88iDNasuiAigGADlm2sgG16NhOUUQSl7EBgjJxmQMRstYXGx7a94WQPUYqCB/uaR6gZtByCw7E7hGH4FBINkLcqJDAHGymuGBDllcFGxcjAVweyFmziW9ypR/vhRGNa4MBKCAdnc+RhIrFCJgVBIEbmegVTNFyPUWIYaOMhWz0A7KHWobEQyQV7zwcb0PfQC/VzHf3p8hlVeMqAMgVQGpbIua+QnsvJ70FKIAFt4FhNggkPIjGhKTgpENDqC5YtyGnspVKmTTU99TkWxFfY+bHHGimKUpOGQeGGzqAJx13eJhGuA5DNcStCUA87+AdLgSCECTBoQXUD20LFYDqBaIZsNYyFYYQNyg22r9rIi2AeEzVUGI9kEmUzIFZXEAWhCkNxNyYpkFjOrjEgotZ4eY6jHzHkgBUBWRFYcWHK/5zICi6fc2thMG30iBHdDmgaEoFOZUERRG6gNQDIhl68I3/HBKcJtgCvCObq/ExugGbTnWe1dUelyEA+egxzquKAUdiPPGuFKsR/Py+Z92Ifw/NgEcVhynKkheJAdrH8nDkBbKHkQe7MmaOwIshiykY1EXqt0aScZ59skyigiOw1+8ISAKBIlqvsAVyyCYB4NnHLEwM2IgNQK1yrr9ZsagikoI0SiLPU6qZlW2xYqHWTkRwZAHKg/LDBNgSYDVAnUAnlW2/X2sm0WcFDQXCJIqQYkT2J/c8Erp+AhJqqapWXWO1UWXbFyoltDTJck+GUxRKuMgvmVBtvYQDaw0Q7sLvyd+Hz8zQbK/ul02aqNP66nynPwiHF+UHS93gciKnGApopgCvIzC0Cgv++6pVGoIYqXNblDBZRvotq/X6RwtK7VeUEOWERNHI1++DIOqBtWsNsZvTlkL5i6VaZKUhwFs6AHBnRubRe8TQOCFJmdWviuLvKLhNwimOA0UatCbfWRxoBwREtyuNXUMkFz33Qlq2NQA2ypBAf7IEoOwQai/nZy6r+4iCwDqQdcG/CRsOzScpmAOHDDVpKbV5VI9qosR5gJggE5aVtioI7cdOoGgpxOtZtrUdCuwxvtFGg4bLrtOG1QhamML6AIBrnC5iF7amm0vZgZggFxYc5TIdbbDYGVcQgXep8jmEPYrZ8b8ssCqoRpBgq3gPlqeSQTTRZSLI1gRu4rViFvaLi7zho5tdQOJlIPUPwVkwY4KBZfZuQWQb1YaUhuqMXlbdlJoDSCEyfyNNW0HRJ5EtvhHhprgGoFcK3BClVxDP3/5+G/HRXKHkJVDtshc4VGoORxYSiC1eTR/KfMFGAAwY1WjUV+DkNyJQZabVE8hiUH6CGKuiJVmdmArtfQ4/UZAyPP1CjrJu326m2pjpYoMSoMRXDqBCnOhdmpPYbgnkQezanF/SerPuNWmVmROPJ1rGJOsUZ6irjM/N7dn5sQep2bXtZAbOTAl81Jyxw+qcS052aI5IEI9h5oh9BfL3mYsVGI4wIYjGTnc9k6CGIjDpDrq8CJi8BnK5zlgmn4rci6jSlEbLrmNgnowdqA4KDVz1eBPdsIh3YBOxdCMUBa7tmagJA4wCSyb7PQNwYiWE/lsAfrotvBT1rtgWTn82zHfsN5iHFvDHDiAvDKacZ754Grt/N5eGZJYut6PhnAEM0EwfnT8hNNJJS3e4Fx9zLw5BHCfcuCqGZa0k6lXARauzFSNg0oJYPLAnOeGF8rGOOOAydIJcOh3+bXYoA9452zhJ+9xnj7E0bqJBGg4yv9ZHDhjb4fmQg6R+VV4OPPgNfPAmevAj94hnDPXtE3ynquGCENOEGWqTJNGIrgDblMQ0CN+tTlGqLGdTEg3cWQGPWnLwN/+bLHG2eFVUemZDL95wAc1tRoA2+dYSzWgH/5NOHQbnmtDJKL8lhTeqbJqgciWPOQVHaO8uBum3bQd/Xk3lwD3jxL+OgzRuKAbXO5MjcqRZoCp5hW8dcGWRy0/MgCq03gjTOMZ+8H7l0mNEcYV5HcTjsTHqYBAxGsju3U9U5RHQS9bNpBn40tcO028OFFOfkLFYi2TL0ZiAYbBslz1ap7HZBRoO9zgtJnwhzXm7myOXLKLeWh1LhkvlcZGMqiI5tnVyR+BuEnBK+YFXPr1ioj5UIucx/IqgoH2J9dqtFYELhwljnKhdc7zPNeNvsYkHo5JHOV6egNw2UwQmIbJC9qlnWuTAxnOMsIYXTatkBQSgI3iaOgrc9wTgQ5SG0nCexFJSjLfYYcgshMRgCE3DTV/OpJobSrshqC1o0ZIZk84OYI6SIQJ+EB8xtdlc5LyGx5Cdi/nVCrTFafPHBOEJOt0QbOXWdcuR0yS/pgsZ84GemZBVZtp6BwjRRN0iB+M5kyklko1cVAWgMMA+TyLA71XKVOYqpfP0R47hiwbzvlzpMZgQZVrt4GXnyH8frHMo/i5mcerynMpejKjOzkZtNICFZ2TdSZ3D414KAQ0cZxNaS4bxvw5BHg948SYivUNauIk/qa5yvA/fsJl24y3rsArDXDIZzhc1MvB2liBHe3R2LOQ4LW9HA7UqivIVGKpoJklbceMOHZrGyw6C4ked56C6i3xYyqxrOlYGXHtxvArXpQ6gYsWA/FNCBxQDQhkjdQcOqAeiJyrRqJT7iXyl6xAMWy0ZvRiEUdIddWgZdOAjsXgYcOiNaauNkg2RgRS8028M4njDfOALebMo9ZRoB06DS0f5gIwcaYrMkZc55Fod10eECiehwBcwi1uDNugkKQOSQO+Pgy4ye/YezdLhTsJkRwv69qzLvRBi6vyOHKKgs2AzhPnx3nmRuKzzR5PTK5j5SdFF3N9Yl2qIO8mYzWwmFcqERyqj+6Anx8RezTSZBL2T893qNcZAHiYdKiu80ARiA2P9wv0At6VhfGFuCKUG7i8khR/1Fk0xkhmF8y8D02cNAugxKSlAzPbRgmeCos5RGsfgelEjJW1ITpORbnGve0lC/tO+LGkMPKlTcoWUBYUAWgRDZAK/hcYRHdUI3ETNBKwlkhOQ3UtFjL00fHoSil3CQVPYLHpJLuMae5boLseerzjgqjQk8EA7k/VNlUoy3O8HhAfm9kATvD7ndKKTsWgEcOS6A9a+UwyjNJvhcR8NkK8NoZxsUbowdUZFJiwxOQlePo61MBCi7ZEDYdFYYGGwA5Qe20EPZD0K67NmNSp/4g8Cxz2LUAPHs/4bmHCXu2FuY0IoI12X6lLrL1V+8xLt8quD/LDzVTUA6l9U6jHMDSFf5kQr8KzsNagGyMatfqr01nRb3hGVtqwJG9wNFl4SKtZHSq0/NQi8SWvmsPsFgFLnjpRNcLa8rJNht0KtqaadQDVQrBlkKM0ucVb5p+kzVQ8Xkvxlmcag2G364D//Ap4759hL3bQ2npmBvPDFxfA059xrhZD8H2LtGTurw8NbZ9CsmKHGQGi/fBdIXHyHXG5SgYQdOshCqFsJgkBZIen50FEERbv1kHfnsSiAzjyDLBUu55G/j9gsmTOMAQoxIBn67IeNdWg94RPq+uwshKQl1kRBlru020g3XuCPlxGF3RGq2NUiStjTjpvakzl0eBim+uA3/9PuPljzhzBmQ9p/pMSs2N7rdSBlYbeSedoh+4FgH37weeOUrYNk949TTj1dOMRkuUtA5ZPW3lqgC9MknLwsi1SXHwQzem5YceEbQH1Y014LPbuZsy1tytHkj2LNRnCLhnL3B0nzhmTl0Gbq5KFZ86bNqpvLd3G/CtBwnfuB+4ey+hGgFb54XS3zoLVADYSPznn4NoLg09EazZEqaPWy62ACqfD5IJkLaHIeK0Z1GiPatNsWWNyZURjas2E+DOHcBjdwNPHTF48A5heb88zvjZa5zVCtVbIubu3Qd88wHgjx4l7F6Uda635WA8eTdw9gqw2soD/ZuF4XHMzw0I9pwjLjJysuMexyC2AOLQxc2Pp+GNCoRgrrHkbB1ZIhy7U2qRj59nnL4sMjKyITMiyMvDu4HnHiZ8JyCs7WRdjx8GXvoQOHctVGcAuG8/8MePE779EMFa4L3zwPsXRbP8vaMGDx8kvP0J4/g5zn3EM0o46Fg4QiLjiPlaHajTznHa9KQVku3mkLfgK0LGrpPRk/LGARdqmvZtA/7JPYTfP0q4d69QEwi4cIOxFgICaXCz3ref8CdPSvzYWuCTq8CJTxkHlgh7thGevgdYqTOurwJfPwT8+dOEZ+8T9vC3HzB+8SbwwUXGgR3C3pe3y8/7F4ObFLMn4GISgHXA3LgIVmFe1NbSgHSgD5JDO4Fme7ZI1sqIe/cB33uM8Nhhwp5tMqd6IvFazS1uhyYv9+8nfP9pwjcfkDW8+Dbjr98HTn/G+JMnGf/2WYOnjxB+dwqoRYw/PEb4xgMEa4D/+n8Zv3iLceaq7MvhHYSdW+Uwtwumw2bKX4Y825ryVRAdZ0HjnxZ5YJuQ17T2q++NTaFyfYaODiZgaV7k5ekrwIXr8t65a8CbZyVWG1tBwvJ24PtPAd+4T+TzL970+OnvgJc+ZFy8mafdNFKg7RhP3A08d4zgGfhfx4H/+Sbjo8vSHHX3IvAHDwF37pTUnU9vBdehktYmKSGEvItuv64J3dCBYLU1a3FIEVUfK0Ijsraw7V44jELB2KyuWTJGzJZPbwIvvMX4+xOMlYaYOCcuMS7dYLHLHbC0RUybZ44SjAF+/QHjr14HTn7KqMbAsYPAkWWS4rZLDEOEBw4YbJ0DTl1m/Pw1j/M3gPma/OxbAg7tJhgCbqwxLt6QxHxjAW9C9smMHB2aIVLcc20l1e6Di459636BID7aaiUPj3GIIDkfTk+KniPHMyqg0olaA5y9Bpy5Kqz6yDJw4hLwyimGD6ZSKxXb9bmH5bCeuAj87QnGmSuyuMgADx8AHrqDcGsduLYCHNghFL/aAI6fQxZ4SD2wfzvwjQcIS1tC4P+mOFsczyZHfMO6+4QgnQeareFisecc1RGvJY0dp8fJwP3Y9SzZlRr7+5cIz9xL2FoDTl5knLwkc9Qk/cO7gaPLhEZLMiHfOyeHz5AUXD94QOT3WotxY51xYCfjjiU5PG+flepAXcoDBwjfeoiwbQ549zzjg0uyGx2V+bOIIiEvY5mvSuf67joxxwP6nwToewgNZPBalGf06xrS4DjQMo3sgb68bBgV9MKN/TuAP3gA2LMVOHcdOPVZcHZAJrhrEdi3jcAALtwQlrvWlM1YrAHP3gc8cIes5NY6cGNVxtqzDbh8S8ZbDSUoXzsIfPdRwvI2Wdtrp4XCLRX6gCGk904ZjAmcNM7FZjXK7XyFJB18H9NALkMhyKBF3VlGIcmGNdui0CQhs6IxQ03ae1GIDu8GvvmgaH+/eo/x3gXOuuUxixK2tCjs98RFxq2GvG4N8PBBwh89ZnBoF3B1FXjttCD/4E5JwV1tMs5dY6ysA/cuA9993OCJu+X5v/mQ5fONzqbgTLKLTHmLwUmcAoq8yOTtGoECwisBFz5Pwh/Ue6tU+WgtpKY2CwXchLwbqo4/y0A/M7A4B9y9B9i3XZLgPjgvVLd1LiwYos0v1MRsOn6Ocf66RIiOHSB851GhSgB46QTw6xOiFGppzvYtYuvOxYQ/fRp49qgc3tdPM/7bK8C5G8DC3MYNLXLnjhtXxl1vYc3Fg2JIWDUiaYVBCG7WAVgs7YuuBaWr2c4v4shaKoy1jHKgGjwDOLIbOLqPUG8Bpz5l3FxnuUei8HljNG+b0UxF6btnP/DHTxK+9ZDM9+VTwEsnPK6uisb9xsfAwZ3AwweB//hdwtY5wv4lSXB/+RTjxXcZpy7lDcD7IVCbuk3CxTIXqxenRq1LwSISKta6JW1f0Q9KI5gQ8q6CYGfkdvLUoEgKBbPDhf8f3CnB+XobOHNF/MPdbtRb68D128DhXYQ/PCYa9ZE9wFNHhH3+/UkxmT64KMkDbQf87+MMaxjfe9zg8btknPcvAi+8wXj5VB4rjs1g6uQQE1c9ZJIkAd1neGkV0b1NZQrTmHnE0pWgXKQecOnkrGjDhABJp+l6XWXN8jbCwR2EyyuMs9c4C/MRhfRZB1y8KVR3YCfhn95PIJINP38TeOkD4Lf/wPj4ioiXWiS+7SsrwIvvEs5f99ixIJ8/dx04eRFYaYjmHQU52+9AF4vG3IQbk51zzi/5qpW4MKwXjH7rCgnba2jV/3jPHQu040BsJbOj2c7TSSm830yA188wIst44i7CfA24dEvY8BsfM66shEu5QrrR1nngjh0EaxjnbkivDWMkQnV0vxS3iSdPFLJ2ioFX7Cjzmca+aNpsI8lN17KgN7uPjGANuqusmAmCiywawTvG4ol68wwhtgQynEVVKPxjwyZcWwV+/gbwq3cZZEL5R0Dm/iVRxOZiUdqWFsTGrVhCKxUTKfXirl1vA2sNoSLHgG/I776bFqJLlqancGr3gEZQcEfpNDsyi1bIcqNV25oyaBqqKm+VIPuOnwPWWh4HdhBWm8JJ2k78wo5lUy11dd1j0brv3084sgzsXpCaYk0caDmg3hR23wwIXW2y2PmJyFUHOSSVKMh8zi/HLrJTCtwtMnm7wqnsB3IrhijX+ofB2AjWtNOZlE92US+QP6eVSA+Pc9fk79Tnflrto7Vrq7gi790P7Jw3mZMgsoy1FnB1RVydN9c9VlvhpjUX2HZEqMbiENm+IFRtSTTatYYEM9abjJU60KrnSM3mqcEaC6CEn3hU0Fg9+/IyOWJm9Et+7wfaZdYM6ZFRGliolg3AVsYFcntbN6oWi4fp2qq8slCTSv87lgi7FoGdWyVWvGOBQMRYbzFuN4Hz14Hrq4yVpiBU7owg1GJg71YJKCxUC5USTGimwO0641Zdntlsh7LVllA9kCNXE/LVwzWrBqOEEE0KYdFKCZk8dr/o7C6FKQlihiDZsCC527jm8KwtNeDOXYRdC8DSImPXIrBjXhICDQkrPf0Z4+ZangWpDVBrseRazVflZ7EablpzEmdtJhJIuL0OXF8HLt2QBL/s5rNw4tQHoLJfT6DB7AMQyq6LMnkQjI1gY4UlQZuUTgIk1Bu1gbgBpOGWU02012tr5iviiXrqCGHbvJgwV1ckNnxlVWSpBbC0COxbIty9B9gyJ5NrtoNrtS2K1LXbQpG3m4zraxJA8cg796hcL14RlJ3l8LdhwFMXsou2/BhQpJdBtJNp10OC/xGPaYmrtjjAzz0akJSpmjbg1UsT3vJho3dsAR68w2DPNon6vHlGUm2qFcIdSxLnXd4O7FgwiCNgrSFm0YWbwOWbjNt1kaeJA5Ikv+u37XJEKqVqbbDtQwLEADkRJ85iKg1YwjZkfnXPg/VYDf5Tjws8FSJmxjhyWL0pic0N+0kImRhwEdCqyd/GIeMv6shYawKvfcx49WPg0k3GzgXCs/cDy9sJ2+YYcUS4VRcf9PU14NY64/oq4+a6NClLfK4/2OCkNxCzaUMySsGn3IsGGMHxMSWezMhleMXmLtpWIkmGReaQ7RlCCe2A8tKxKRiQQZNIbvya1CgmyA3j7YoMZQoyWH2tK3Xg0i0GAXj0MOH5xwm7tjJOX2acviJtjs5dBc7fEBs2MvmdgrWKJO0XH5hNd4A/XXPBisXtGlWbC7eidvfyGmv9HDT5Ss65bMh38+3+ROQRIlh9YKJLOSiw6aKmOxGkACci17iHt4hINnRLVdj17Yb4jH97Ulh1UQFaqMr/jRnfZ+68HOKdi/mtqB75dbnrLeDamihxqgROsg+quBahYgEfh0NWQHIWVqTBaVITUbBOyprJ2zcwAE4B7eLJdiNSDOU3q717jvHBBaCRcFbArbFR0+NwjDwfFh/4gT2Ebz9MOLAD2a5unxd2/3+OA7/50GfXzrbSwj6MMIFBooDUcYP8ah4FA7ECphIu7AexEdk56f29eiH0MHmu/tmVOpB4Dg6KTs/VpNxEHSeVGLhnWeLCOxdFIZsLnfVe+1i636228ruMqhFgx2i/oxp42wE22Wj66NhqBqqIiIzEg6cSLhw0u9gCicnbK4wDzLJx6nIc8kg5uV3fnwYQkHWsq8USL16ohYugQzDjd6cYf/Ua48PL8p1WIgj42p0Ge7UofYy90LSkXqYPkVCqDVUb/ZLxumFyBCNPJ3Ht8T1bhJD2kv0xjZmNCaowelEgtWXwpRXglY8Yf/euxJPVRgdJz65/9rB41vQqulGBMLz7ux0ic7thKggmhNYHoYHaWKZDUNhscFtOQzMdFTj8Q4WCujgStlxPgBffZbz4FuPG7cAuvcjBYweBf/EE4ZFDQmVaCTIupE7uScYA+7YsTAXBQM42PY9HxWSA2DEqLRGizox1afZkQMEeDt6F+Zq4RNfawK9PAr98m3HpuqTMpIFyHzlE+N7jwGN3EeJINOpewPkjSkHxxvOptjKcBCpWvFDNcdxbJCp/LciXTckq74LYChdpeckOmasCK03g795l/PI48GlArt4N/NTdkuv1tUO5fB7Wy3IUcCnQmJCSp4pg7SfpuNCvo8Ri1STZUgF2bwXOXg9JZ6M2/9LxRvta9l0KVMkhSX61wXjlNNBsSuAhjsK8DPDYYeD5JwiPHha/fLO10YpQlm+CwyXrtTHCpPQOYmC8CzumTieahjqKtaCtgvduIzx0p1T3rTeRx50x4IcG/GAwS+weh71c/WMhm7lSB05+CnxyQziTg5hP9+0Dnn+c8MghyflqtHpr/no1XzX0FtNWzKMcQEJexD6VAvBpQGTDPYVDLsJSYMgiti8Aj99F4m/+SGKvWnIy6Mu91q22Zfa7e4zgnsza8vcYxCMks0OcGCZoy//8EeDYIUIUKhl7OigQarViET3aB9R7sadHwhWHi1H86Jd1zATBJnhftMq+DHiWTdy/g/GvnjGwhvHWGc4WRd0nRf/us1MdLwelaSAL7+NFikzes/OhAwbPPwE8cUSosR9yi9OLTed4cyG/XA9/aQh+Aj/swHfBTBAMhHLPCuBb5ft4NBOgaqUtQyWSeO7pK8B6I7/hUzfUmLwzbq8FewhiWomw0FbK2dW4lsQDJGU5JLlWfShDMyeWt0t/jmOHhN02S9j8qReFMyscAzoqRbT/VikYJm/6wMwQDISa4TjULPUJeRXBIE8af2A/sGcr4cxVSZ1Jg783y56wOYJ7NYpRBDfbIs/X24R2Kq9HgZK2VOV3Le6fZkMk2R/L2wm7twrCG63+oqdoDmm5LXddv1tMgdWrAMuIsXHy4GaKYCC0Gg7ZBxptGTbHJFDr1jng63cCDMo00gwKLLfnojn3NKnc07oh7Zqn8p2GnTyV0x5o95H5qkGbwlf0+dryYS7Kn6OloR6hNeSQjcmuvRswzV4wcwQDIZmNhJJL9Vzk4JOGaOUdKTEFyERwHy3LhN9RVq8Uhg+eMta/+42RTyfrgt/rPT0fWcIdh96doQIwZQDB/VgtULIxwHws82wXteSudWq91Ti3oQ1GMIeUHFV0JjDgKyHkVS8Er4cBY3Dt6yjQHRPO/MgjfL8fGBIHiK4RYezI5Yc6ZQncM3dev6u6iipe3Zdha7/u7it71aZmILuZrRf0RbAWc2vusTGiLNgecdqyEEdAjYUlacXgsLGmmXvdjc9pjK3mUHfFARFQCSymkeQcqZnIl4q+AkM5AnW/dfDYdLY4Zg45ZUFBUwQbl0e8ihPp3ekupKm0QidXJki2RSzljJPk/VZDg5dWmmuRM0mg3wRg5AjotYSiMtV0+Y2s7SRo/wWqUyTHXQe/WIvsveRktzWFqGD3ESTlaS7urDzsieDEy0+3SNDr1jTAPS5EkQQXUGi69mVDdNFcG2iXBvZNwa/suSDuVB8pfL/vtXnBrOpm49n7yO31WtA/iPogmIEskSt7OGFq9/ACssj5KtAynflGXxoI+6LRs2Fu80ror+F93tW2lco4FTv8cHvOS1NVeeyeCyNcu2CBKJbc7p4IthQm4zq/rHbntChNtUoLOZ2Jz0N1XwgYoCToy84BqSkX7YkKpQ/NUElBLEl12mCl3zSGXaXb95n9JqJXxmkajqVQXTflIK0WlRsrLQuKCsTUOwiUnE/VymbrNQW+m5sVgCGfMT1yqfqBNiZPQ8tFDj0vK0OQDEwJwbpIVHIZGdtOl9tUgUI2R8hhtmneql9FwmbIZ3UjFq/rMcnw+6BcCM4b6t2ZtxdENnRLCAeo3hZltl+j0YI+VcrrlT2n76eCKq8pmZtFSZER+ZFYQXLqc3tPHRKzQDZzqCooaMQEcTGChcr62c2E0HQ8BeZpOJfTBDoNNaZa/zRgfM2ZTlAy0yWMNbyNUpnBRgSlyr7aJ+U2nWeRze203N0ME0Efj1hm6gy49Isg8rhsBgYhZE5WhYoNDc5v1kaxqVaR9Jl6HIrUlSA2xVVZhETbDAXtcZD7TSsntJ5I02qdz12HRfNq0sOoyetFn7HOoxrnIc1BbDINDqKyyqh2BCAM/rzKaEYwlbodRUZs8qyRbNAbNhXBSSqGOhEADzS9iIFhLYGIgDh4dfQ2ThecBpof5QvUnXVnLSIJgw+AJtS32jKe9uksvl8Nidgalep6RAbOC6LLKqSZlyr8QPPSaOPndF5p2skJ1V+tTij1LWwagtXF5n3YKCetCTXToaxc1YRv7VnlfY5o1XYzVt7Fysr4n70v9MLocj/awga30t7RsUluf/O+sEdx7zCompYVm68zAvpee7dpCNYS0JTzdsSaszSu0qTKh7J5TUJnHyiMc4SqQ8KxiIi+CpMJPuN2b7loAhsEgk+9KJNZ7FpLEnMeGVjqlh1LQkI/BqDsvMxdhpvKomObU7KexGn2s8hkcY8qPaUsZqBFIsf6jsN5wkDUI0Wm2OmG00JUhwqa+DgIVrac8erJYPwuO2OCUoSynkmod1QoxpRrgfW2ksGytJBAsgG0XMcE5Y/Da8MUx25wPi8oY+QXuLSS/GIRayfLi6ZJS0hHAUOA2XTdfeMcNAmh2VV3q//ReqShceAI0jQG4x1WFzTz1HV6sRIXPGQkwYNRERxwSpFzzkTR57zjnwNo5iehE8lFe7JiyyWOT8KFLIXU2tDF1gX9wNq8xcS4Ysw5ZyLv/apzbtnO6jaNLzKoR8lIjFXbAFsjcdVp+917gTVANRwQz+Ioca7TfByj5BjOOXjvb0fe+1edcwe99zUzqHX4/6dAgf0ZSFQHCFSziec9QyDn/neD8RALAN57pGlaZ+aXDYC/dM69n6ZTa4j0pQNCqMYIP5uJ3A4IzgpNjh8X0jSFc+59Zv6JmZub+1Wapj9LkqT+jxnJXwQwEA1/boL7p9I0RZIk68z83+fm5v7GGGOaAH7svf9pkiRw3ZfsfgVfGnDOIUkSMPNP0jT9sTEmMd57eO8vAvgvSZK80Gq1vkLylxCcc2i1WkiS5AVm/lGSJFeccyB9g4iQpukTzPwX1to/i6Joi7UW/yi16y8ROOfgnEOapnXn3E+J6EdRFL3unEOtVut0crRaLTSbzQPM/ENr7fPW2gcV0aO2OvwKZgvMrIhdc86dcM79nIh+XKvVLlSr+S0eHQiu1+twzqFarc41Go1vE9EPjDFPGWMWjDGsSP4K2Z8PKK6YGd578t6veO9fVYWq1WrVrbWYn5/PvvP/ABvZV9ggLzMqAAAAAElFTkSuQmCC";

/* script */
const __vue_script__$54 = script$54;
var __vue_render__$54 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: "xml-question-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _c(
        "div",
        { staticClass: "question", staticStyle: { height: "100%" } },
        _vm._l(_vm.blockData.testPaperSetList, function (item, index) {
          return _c(
            "div",
            {
              key: index + Math.random(),
              staticClass: "workbench-shop-item xml-group-item",
              on: {
                click: function ($event) {
                  return _vm.pageClick(item)
                },
              },
            },
            [
              _vm._m(0, true),
              _vm._v(" "),
              _c("div", { staticClass: "text" }, [
                _c("div", { staticClass: "xml-paper-title title" }, [
                  _vm._v(_vm._s(item.paperName || item.title)),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "info" }, [
                  _c(
                    "span",
                    {
                      directives: [
                        {
                          name: "show",
                          rawName: "v-show",
                          value: item.questionNum,
                          expression: "item.questionNum",
                        },
                      ],
                    },
                    [_vm._v("共" + _vm._s(item.questionNum) + "题")]
                  ),
                  _vm._v(" "),
                  _c("span"),
                  _vm._v(" "),
                  _c(
                    "span",
                    {
                      directives: [
                        {
                          name: "show",
                          rawName: "v-show",
                          value: item.fullMark > 0,
                          expression: "item.fullMark > 0",
                        },
                      ],
                    },
                    [
                      _vm._v("\n            满分"),
                      _c("span", [_vm._v(_vm._s(item.fullMark))]),
                      _vm._v("分"),
                    ]
                  ),
                ]),
              ]),
            ]
          )
        }),
        0
      ),
    ]
  )
};
var __vue_staticRenderFns__$54 = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "shop-item-img" }, [
      _c("img", {
        staticClass: "question-img",
        attrs: {
          src: __$_require_static_images_default_over_test_aper_png__,
        },
      }),
    ])
  },
];
__vue_render__$54._withStripped = true;

  /* style */
  const __vue_inject_styles__$54 = undefined;
  /* scoped */
  const __vue_scope_id__$54 = "data-v-4f86590c";
  /* module identifier */
  const __vue_module_identifier__$54 = undefined;
  /* functional template */
  const __vue_is_functional_template__$54 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$54 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$54, staticRenderFns: __vue_staticRenderFns__$54 },
    __vue_inject_styles__$54,
    __vue_script__$54,
    __vue_scope_id__$54,
    __vue_is_functional_template__$54,
    __vue_module_identifier__$54,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$54.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('test-paper-render', {
    prefix: options.prefix
  }), __vue_component__$54);
};

//
var script$53 = {
  name: 'XmlRecorderRender',
  components: {
    RecordPlayer: __vue_component__$5z
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('新data', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        playMode: 'xun-huan',
        scheduleControl: 'yes',
        playStyle: 'yuan-wen',
        styleMode: 'A'
      },
      //属性组件名
      propertyComponentsName: 'xml-recorder-property',
      dialogShow: false,
      curRecordUrl: '',
      curRecordTitle: '',
      curCoverFileUrl: '',
      curRecordContent: '',
      currentPlayer: {},
      modalPlayerKey: 'mpk' + new Date().getTime()
    };
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog(param) {
      this.curRecordUrl = param.url;
      this.curRecordTitle = param.title;
      this.curCoverFileUrl = param.coverFileUrl;
      this.curRecordContent = param.content;
      this.currentPlayer = this.blockData.recordSetList[param.playerIndex];
      this.modalPlayerKey = 'mpk' + new Date().getTime();
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    timeUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('audioTimeUpdate', {
        event,
        time,
        data
      });
    },
    getresdata({
      blob,
      duration
    }) {
      this.$emit('getresdata', {
        blob,
        duration
      });
    }
  },
  created() {},
  computed: {
    isEmpty() {
      return !(this.blockData.recordSetList && this.blockData.recordSetList.length);
    }
  }
};

/* script */
const __vue_script__$53 = script$53;

/* template */
var __vue_render__$53 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-recorder-container",
      class: "xml-recorder-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      [
        _c("div", { staticClass: "fixed-ratio-container" }, [
          _c(
            "div",
            {
              class: [
                "xml-recorder__wrapper",
                "style-mode--" + _vm.blockData.styleMode,
                _vm.mode,
              ],
            },
            [
              _c(
                "div",
                { staticClass: "xml-recorder-player-box xml-group-item" },
                [
                  _c("record-player", {
                    attrs: {
                      mode: _vm.mode,
                      loop: _vm.blockData.playMode === "xun-huan",
                      "play-mode": _vm.blockData.playMode,
                      "play-style": _vm.blockData.playStyle,
                      scheduleControl: _vm.blockData.scheduleControl,
                    },
                    on: { resData: _vm.getresdata },
                  }),
                ],
                1
              ),
            ]
          ),
        ]),
      ],
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          staticClass: "resource-preview player-dialog",
          attrs: {
            title: "",
            visible: _vm.dialogShow,
            "destroy-on-close": true,
            width: "100%",
            modal: false,
            "show-close": false,
            "append-to-body": "",
            "custom-class": "preview-modal-recorder",
            "close-on-click-modal": true,
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogShow = $event;
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "xml-modal-player__wrapper" },
            [
              _c("xml-audio-modal-player", {
                key: _vm.modalPlayerKey,
                attrs: {
                  mode: _vm.mode,
                  styleMode: _vm.blockData.styleMode,
                  "play-style": "yuan-wen",
                  "play-mode": _vm.blockData.playMode,
                  url: _vm.curRecordUrl,
                  loop: _vm.blockData.playMode === "xun-huan",
                  title: _vm.curRecordTitle,
                  content: _vm.curRecordContent,
                  scheduleControl: _vm.blockData.scheduleControl,
                  "cover-image-url": _vm.curCoverFileUrl,
                },
                on: {
                  timeUpdate: function (e) {
                    return _vm.timeUpdate(e, _vm.currentPlayer)
                  },
                },
              }),
            ],
            1
          ),
        ]
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$53 = [];
__vue_render__$53._withStripped = true;

  /* style */
  const __vue_inject_styles__$53 = undefined;
  /* scoped */
  const __vue_scope_id__$53 = undefined;
  /* module identifier */
  const __vue_module_identifier__$53 = undefined;
  /* functional template */
  const __vue_is_functional_template__$53 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$53 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$53, staticRenderFns: __vue_staticRenderFns__$53 },
    __vue_inject_styles__$53,
    __vue_script__$53,
    __vue_scope_id__$53,
    __vue_is_functional_template__$53,
    __vue_module_identifier__$53,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$53.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-recorder-group-render', {
    prefix: options.prefix
  }), __vue_component__$53);
};

var checkbox = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAPCAYAAADtc08vAAACT0lEQVQ4T3WTv2tUQRDHv7Pvnqig8VQwIvgjqJWVaGMj2PkHxE548N5uDrUwJBJNQK2NNhoOsrsXrxL1bJQgEhRPrGxUBG0iWIigEHJ3Ri3O93Zkz3vhjMk2y8585zOzszuEv4viOD5GREeJaBsRUdeeb98BvAXwXGv9Wym1nZljAC8piqL1YRgOE9FmAHPM/MU55/LILMuoUCh46HEi2ppl2VwQBKeY+UOz2ZwiKWWJiDY0Go2btVotW5G59yiklFeIaADA+0ajMen1pJQyhUJhuFwu/1greHBwcF2xWDwL4HBXM6W1ftW5u1JqRGt9QykVeoO/Yy9IKbURwAiAgwCWmPkNEf3UWt/pAJIkUcz8IAiC8845AeCWtfabd/pgIhpj5gFmZufcNQA7hBADxhizDEjT9FEYhuNEVATwi4jM4uLiu2KxONbNDOfcrLX2XpIkJwDst9bqZYA/RFHUH4bhBBFtAcAAvgLY6UXM/Hl+fv5yvV5P1wR4YalU2uWcuwSgL++Dr5yZr1prP3nbfwAp5RljTDkP8JA0TSeEEJu62Z8ZY6q5X0p5UgjRPz09fTt/heta69EVnd8N4CIzF7IsG5mZmVnK/Uqpc1mWfaxUKk86ACnlJIBZY8yLlRDn3CFr7ePcniTJPiHEeLvdvlCtVpsdwNDQ0AFmHnXOPWy1Wk9rtVp7lQ8l4jg+EgRB5Jy7b62t55rO0MRxvEcIcRrAXgALRJT2NFH4AQPQAnBXa/26N8E/U6eU6kvT1A+O/1CdFQSBf9KW1nphlcrwB3YxGYrLREgxAAAAAElFTkSuQmCC";

var chackboxactive = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAPCAYAAADtc08vAAACU0lEQVQ4T3VTO2hUQRQ9d+a91RWNaUJQMRHRCGlc2ey+N2sTsBHRxg+BdLEKllooKaxESSEoIQh+EFKI2ghRg5BgtspMgksKYQuDkE6DAXGbTeZ9rry37PKMZKq5d84998ydMwQADIjI80ZZiCsM9BNAST6zNph5boPo2VGtm1yp9IfMT4l5hnhwcH/Y1TULoh6O4ykC6gyE7WKSkmLmY4L5OoiOCOapmOg+AYvS2hEKlZph5oNOo3GN6nW7o3MnZEAGShkChgAsONZeoFotoECpRtPa41212uauxcXivtB1X4PoUoKJmUf2GPM22ScEH1ytL/Lw8N40Ua1uZYm4UOgO8vmPBFTAvMnAewJ+u8bcSvGh77+UUXQ3kPIdEUmH6CotLX1Ph1sodIf5/DwS2cwxmM8z0QkA5ZwxYx2CMI4fSCkXARwG8x8iGpPApwCYJ+Bsqoh50jXmjvX9cQF4TpYgCbbK5QEpZRXAoRTOvEZEA61a/uo2GkPJkHclSIGeNxgK8RlAb2YOEUeRn1tZ+ZLk/iOwSr3KaT3aLkhIIiGqDPSkd2R+4hhzo30e+P7NRJmj9XhrBkqtSa0HKDVka9ly+bQQYgGA29zePnlgdfVX+yxU6g0BWmr9KCWwvv9NEE06Wr/IPp8tlQpSiHNyeflhRl0xFGLRsfYU1Wo/Wj4olSqQcg5E9xxgmrRu7jRU4sJIqcsApmPmiZwxzztWTyUrdYaAx2AuMrBORFlLCwB9AH4S823HmNlsg39+HXtebyBlH6JIdkBE7AIbZMz6TmVJ/BfZ8hBQpXDnDQAAAABJRU5ErkJggg==";

var playIcon$1 = "data:image/png;base64,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";

var pauseIcon$1 = "data:image/png;base64,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";

var playIcon = "data:image/png;base64,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";

var pauseIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs4c6QAABrJJREFUeF7lm21sU1UYx//PXbcJQ0d7mREkuixqlqiwRA0MQdqymIAoIOAHooD4Fkkc6R0TSCCMQALK1js00YhRXNTEOExQJBOBtgg6UFFQTAxgggYRYe2GA2EvvY+5hc52Xdt7u1O4y863pv/zvPzuyzn3POcQrkFzqmfLwFQKULEE6XYGFwMoAmAHwQ6GHcBJgE4S8UlN035nSdq51yM3Zzs8yoaDivrQbd1aeJqEnMkMdgK4JUM/IQb2SCTt01hqCiiFJzK0k7SbUADO2tBEytHmgWkegELRwTLQJJHU1IW8rfs8BX+JsC8EgLM+NB0anifwoyKCSmuDcJo17e2wNPSt/oLoF4BrnnhvMjoIYHPAI69JCy2JICMAk9SLI23a5XUgLMrUsch+zNhFGq/yV484aNauaQBONTiHGGsBlJp1lmX9RQCr/IqsmvFjCoDb27KSQXryFm70vl9xzDcaoGEAbrXlJWZ6zajh66z7ya/IY43EYAiA0xusIqDWiEErafyKnDa/tAK3GlzKjI1WSsxoLAzeHlBGPJZKnxKAq651PkhrMOrQijrSuNK3dMTryWJLCsBdF5zAEnaCMcyKiZmJiUmaFfDYt/XVp08Azhq/jQrH7AHjITOOLKtlnGfSKgJK0feJc6k+onZ6W9YTaLllE8ogMP07IqDI09IC0Ke3pPH2DHxYvgsTPRnwOD6MDTThEXCrwW+Z8YDls8kkQMJBv0cenxSAS219HKx9kontVH3Gjs6FTQKOnu5GRzenNJ9vI9wzyoZuDThyqkt0KCCSKn0ee8+oEHcHuLzBrQBmi/TqnXsTykbbIiaPnw3jjb3/Jk1MB7V48lDceXNORH/4VDeUxn9EhgOAT+Qhd9xOpTCkG+4B4PS23U8IfyfS2+S78rD6kfhR9NMjHdjk079bEtsSdwFmjM2P+2PNjgvYe6xTZFhgQk30E7oHgMsbfBVAtUhPC8qHYMH4IXEmU13V2Lsl2qnhwCU0NF8SGZZ+2XveBREAD288U9CVk3sUgL5YKaxZFoCeYZjH6+sHEQDOupaFRLRFWOZXDVkZALO2JlBVVBMB4PIGdwBImCT0F4iVAQD4xq/ID14BoAb/BGNUfxPu3d/SAJgvc3u7nSrUtpIwh38Tnbxuz9IA9AGRUU7ZnPpaHgC0F8lZd66GSFo9GO8AItST2xusZ2DJ4ARA28ilhhrAbHgV1Qwoqz8CAB0mpzf0WbZKWtYHgDZyeYO7AFSYubJGtQMAwCX9HdDIwByjSZnRWR4A4TS51eDbzHjWTGJGtZYHAPxCbjW0kZmXGk3KjM7yAIj2k7MutIyIN5hJzKjW6gAI9BG564JzmfCx0aTM6AYAgA3kqj13HyQpYb3cTKLJtFYHwPpUeOL6NntufjiyPia6WR8Apl79HA4dAfOYQQeAJHsUwMtgfmWQATjgV+TyK0tiamsZsfbjYAJAoA0+xbEidlX4AIBxIiFY+R3AwNSAIn8RAyBUDbC+NC6sWRYAYb/fI0/SE/2/MKK2FhNr+tJ4gSgC7tI8rJwaXxj5/OcOeHf3XRhRKgow/d74wsi6pgvw/Sq2MEJMi31VjjfjAOg/3GpwCzMWigIwLJ+gFzvuKLpS6tKb0tiOw0lqfmWjc+Gde2OP9sS5cKQ0dqEjdT3RZLx/5HTSmN3LHecTALjqQtNArC+RC2t3j7ShvCQXtw7PQeB4Z9oyl64tL8lDWAMCxzqFF0gJtMKnOHqm/gnlcZc3uBnAc8IIWMoQ/XCDzT6hqZI6omElAKhQ/y4Ja7Z9IPF1guvNgpmeClQ5PoiNo889QgNsU6QhrsS0zVflmNVbnHyXWBZXigxFLFZ0lsM0JVDt0Ee5uJYUgLO2pZRySN8pJrxkJjY3Q9Ze8Cuy/m5LaKk3SqrBRWC8Y8iFdUUNfkVOOrSn3SqbzcpR1pkR3vV75GdS+UkLQO88ECEw0BhQ5CfSQTYEYABC2ONXZEO1DsMAIhCse1ok5kJn6cBE1IPVzgvFTWoI1T6PbOpcg6k7INaZSx8hgLVWGCb1550JaiYnTTMGEHkkaltKJYnWZqu0ZuAF1gyG6quSG9Npk/3fLwBRo25v69MMTT8taujFk2mwMf0OAdicbHJjxr4QAFGHrk1tU6CF50HDbJD4o7MENDPwnojEozELBRA16lRbh0MLz5QkaQYzzzRzReK1fIyZvpKAL7VcNAcq5VOZ2+q7Z1YA9HblVFuL0d1dDImKAS6WSBrORHZm2Ak8hIA2ENoAatOYzyCsHerScOjrZUXtohPube8/AMNbG4WK/N0AAAAASUVORK5CYII=";

var audioIcon = "data:image/png;base64,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";

var coverLocal$1 = "data:image/png;base64,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";

var playRepeat$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAaCAMAAADhRa4NAAAAAXNSR0IArs4c6QAAAi5QTFRFAAAAAID/QID/M5n/JJL/SZL/TYD/O4nrQJ/vR47xQIz/OovoQ5DpPYXrPYX1QJLtPo3tRJDuOI//QI/vOpL4QpLwOZzxQI7qRYr4Po/sPo/zPYztPZL5QIvuQJHuOZL1RI3vPYrwQYzwOY7xQo7sQo3tQpHtPY7vQY7zI4XzPI/wQI/sQZDtSpHtP4/uPo3nPo3xQYvuRoDuQY3vQIn1QI/tOpfxP4zvQY3sKrv1MYv1QJDtQI/tPpLwQJDxQo/sQI7tQYzuPI7vQZDvQY3xQI7sM6nyQY3sQJLvPpLtQI/tQI/vRIntQI7uQI7vQY7uQY7tQY3tQo7tPYzxP47wQpDuRJDxQo7tQo7vQI3tQ5PtRI3uQJXxQYvxQZDxQJDuQ5DsQI7uPozsOpPtQY7tQI7tQI7uQI/uQ43sQY7tQI/tQY7tQ43vQ4/tQY7tQovsQI/tQY7uQI/wQo7uQo7tQI/uQYztQ43vQYvtQY7tQI7qQI7vQY3uQo/tQI/rQY7uQY7tQo7uP43vQI3vQY7tQY7rQpDvQY7tQY/tQY7tQY7vQY7sQo7tQ5DrQY7sQY7tQo7tQY7tQI3sQI7qQI7sQI/qQI/tQI/uQY3rQY3sQY3tQY7rQY7sQY7tQY/sQY/tQozsQo3pQo3qQo3sQo3tQo7rQo7sQo7tQo7uQo/qQo/sQo/tQ4nrQ4vuQ4zsQ43sQ47qQ47rQ47sQ4/qQ4/sRIvrRobsSI/qS3/qS5DpVRNwpwAAAJJ0Uk5TAAIEBQcHCg0QEhQWFxkZHB0eICAjIyQkJSkpKiosLDExMjM2Njo6Pz9BRERHSElKSktMTlBUXV1eYmNjZGdsbXN2fH6Ch4iJjI+Pj4+Tk5WZmpqgpaWmq6usrq+wsLGzs7a4ubm8vsLHyMvLy8vMzc/V1tbZ3N3e4ePm5ubp6+vs7e7u8PHy8/T29vj5+vz8/P6b5lOEAAABY0lEQVQYGY3BhztVYRwH8C+XsiIjM3tlFZGSPcsoM5SVUXZ2ocgOmSUj76+Lm3OsZEb/HR5d93iPp8fnAyn3+JzyqlfpoddxoeC6n0QkkJJYtgNkHCqVIpU9CguMTG2gLywFHL+Z3wuPLXHqdpmgKlVAyuuv0GgFjYjpjRpIXJlgFbqQunv49Qk08nbfgxOy+OsW1DzWlzzBK9ivh5pzfyFkDH4o7fFfr/8k4J/7VyFz7V7sQr7eA5x4xrpNwHsx16V820o49lIg6jIHJ+ibSMQ+AcjcpkkS+0zAubNGRC4A3k2VjH8cITvw/FfGXHDM2MZU6HF1gpy+OU4pBma9IeemgFrGchNkbrAOqFnQwUPwauff4MzT5VEznBcn7DlCo32r1xZSUcSiIWE9vPrZF2e0k1SbuTjnZqdIxQFaOGEU1bYznQiO4XNSfR+qTkvOaiaRPoRDzqdokIgYI2qJ0cGlHQE0EGWmZ0WkvQAAAABJRU5ErkJggg==";

var playOnce$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAgCAMAAAA2a+hwAAAAAXNSR0IArs4c6QAAAlJQTFRFAAAAgID/QID/QID/OY7jRovoO4nrSZLtRIjuPIfwQIzyQJnySZLzRpfzQ4X0QIrqQo7sPo3tEN73RI/wPI3yQZDrPo/sQ4ztQ4zzO4jtQY7tPo7uPYvuQI/vOY3vPo3vPIzrQZHwNqL1QI7wRYTrK4b2QY/uP47uR47yQIvvP43rQI/wP5DwSXz0QozuPYvxQY/uQI3uQY3vQI/yQZDzQpDwQ472QI3uPIn3P4vqQo7vQo3tQI3uQJDuQI7uQI3uQI7sQo7sQpHxQY3sPo7vQI7tQI/xQY7tQY3tQY3vPo7vQo/rP5HuQY3uQJDxQorsQo7sQo7vOI3xQovrQo3rQY7tQY3tQI7tQI3sPY/wN5vvQo7vNKPyQY7tQ43tQY/uQorsQI7sQY3uPJfwQI7wPpPuP4/vQY7tTnTsRJPxP47vQYzsPZDvP43uQo7sO5jvQY3uPZDwQ4ftQI7tQo3uQY7uQo7uQY7tQY7tQ43wQY7tQI3tQI7uQI7tQI/wP4zuQ5DtQY7tRJHsQI7tQY7tQI7tQY7uQI7uQY3uQo7tPpHtQI7tQY/sQY7sQY/tQY7tQY7tQY3sQY/uQI7tQY7tQo7uQo/uSY7tQY/tTXnqQY3tQY7tQY7tQY7tQY7tQY7uQY7tPZXtQY3tQY7tQY7tQY7tQo/tP5LqQI/rQI/sQY3sQY7rQY7sQY7tQY/rQY/sQY/tQZDtQozsQo3rQo3sQo3tQo7qQo7rQo7sQo7tQo/tQ4vsQ4zrQ47qQ47sRIfrRInsRI/rRI/sRYnsR4TqR4fqvnKd/wAAAKd0Uk5TAAIECAkLDQ4PERQUFRYXGBsdHyImJykqKisrLS4wMTEzMzQ0NDs7PT1AQURFRklLS0xOUFNVWFxdYWFlZ2doa2xsbW5vb3BxcnJzdHl5fHx8fH+AgIGCg4iJjIyOjpGSkpOUlZeZm56goaKio6OjpKaqqq6vsLO0t7i5vL7GxsfHyMjLy8zN0tTW2trc4ubn6erq7Ozs7Ozu7vHz9Pb3+Pn6+/v9/v6utNnsAAABnklEQVQYGY3BhVsTcQDH4c8QRbCwu7tbVFQsPLu7u3t2YRfYXRhg7r4GMnYzMPm/nLf7nTjkeXxf/lv7I3OoVP+IllCpvg80l39rNiTrkrQymQpqTD4rV7QriabkRx3tnXdP9tQqkDGAP1IPKXw6qy7ZciYCqbLnY6RdkDKBUVI2MUlL39jr8eQV3elATKdTI4kb+6FsMa7NZY/rkWDw8589ielS8qU3FWwquRoAcu0N+Opg3AxNgFalr2rhSd/3qBuece8vwszvOXjGFEiz8dQOC46GRuNK3iMVaDzG+TBct3vgSpGGrpOFse0rPH3blLhGrdkqC2P5OyhUQ3y7ZWEs/AG31RlfUBbGmiLIdQbiC8rC2P8SNkaW4QvKwrgfhkGhu/h2aRKedsVhqGnb/TAOazqend8OAit0BWP4ycbEdXxR2hJIeRJZS6LArc87+K2Xo2n8Len46xsBXAs+fVxFeU3OhJ61wTNL0XPdMarOkPOwLb7M/GIdGNE8rVr1+n1WX3OcYw0oJ32LnKhcjvKGkaDFohOFirm8PQPPL6EvjEOwGD5qAAAAAElFTkSuQmCC";

var recStartIcon = "data:image/png;base64,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";

var recStopIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFIAAABSCAYAAADHLIObAAAAAXNSR0IArs4c6QAAFbVJREFUeNrdnQecVNW9x/e95+u95fXeE/OS9+wCy+yiGHsQQY0xlgSjSdQYY8OGnbIzsyAW7CgooiiiYsGGICgoTVRUmtLZmTuzve/J73vOuTgsO1uYOxTv53OYYXfm3pnf/Z9/+f1/52xJyZ4+jPm1gyaZ34yNWvc7sTu3/8FRo4M/Hjxu69diie3/HktUHVyeTJWXJVInxxLB6WXx1A/Kkunz7NDz8mRwRlkydUoskf5OeTx9ZGky9T/lidTf9b89+6f9xlT94YmTNv/esFHmt4ZNN79R8lU/BNrvx8Zu/2uAK4tnvjUomT5iUEX66LJ4emh5In2uALtY42r9/6ZYInV7LJ4eZwfPE+mbBdy15cn0LwXu+Q7s4LjyykxpWUXVQQBbGk//QyyZ+ZNRo8yvfzURlCUOmFD7l2WVqcMsAMngCgFXWZZIP6HxssZ8AbZIj8s0Vmqs0u8/lQWuZtjn7mcfaawQkO8J2AWy3jd1rmf1u3sBX5Y6Qr8fPCiZ/dcjEht+9yuBnZ2+E9J/P6gy+F++HBaHVcnipujxLQtSIl2vYQoc7RqbZa1LLKjxYIK1bNxAPH041s/03y9BxPeVxjP/Z/1bIp3Ql3zOW9wqvrRGjQfARDgaNao01jrLDt6QG3jIW/9gpj0zY7+YvoMqt/2VfNS3NcWGCcTrZHlP+2nZGjFovR3brPXLSmPx4Mf409Jx2X8hMO2zIArAf5bVnSbgKvQF5uDP5MO263nbXgIxHFj/Oo13ZaWPCNhLBlWmDiFr2KcAJH0h+pbHgws1hR91AO518PJaKDdZ40ZZ6AnlY7P/tk9Y51GVwT+Susjy7tSHe0fTeZMeW/ZREMOR0fhY46nQOvdahCcB1gf5RiyeOsc69ER6zT4O3q4jmQ5iyfQr+PJB8aqyAcmqv9mjwQjfokjYrzwRXOVzwI1FiMB70jqX2jw0EXyPSmmPgHhMovrPYhVBf0XlUZrSc/UBavdTAHNHh02ZbH4bnI67io0yBxQNRJLassr0MbrgGF14sUb1VwDE3KR+A36T6ogUqXg+UdPZgqjyzCe/O30YpT6mtCJt+o1NmSM1+o1LmYHx/Q7QLeS+sWRwJtM8UgIEn0h6g1N2uVjXZZ0+gAVugMDsP849xuL7pXVulNt6TOnR9yPzmfgKWBXKLPmP13WRbOcLW/DGucdjJgRm6KSMOeP+jDnlnsDoBljrBNR8H5z39Zf1Hj4mZQ69PWUO6WHwGl7bv3gWzzRfZ7MRuTLYpIKiOWZdXpH5J1FYZ+sOveQj3C4XBkTGsXcE5kePZs0tL9SaCa/Xm+tn1Zph92YEpAMz3wcPXQLA8LojxnQ/eA2vLS2uxbdZFioZ3FaWyAyEO9htIAdNrPlzGBSdbLJPtHcBIJzOgPjjqdVm4pv15tWPm82i9S1m1vImc83MGnPincEOv4ll6k7bEVohgJx0V2DOmZw1Fz1RbX7xZE2346Jp1each7P2Pbw3tE4+U3m0YFbre8+DH2VW7h7HSe0sCspXLOu7yhMBBRC/MwFLdCAu/rzFpOvaTU1jh/k83WaeXdZorn6m1oLZ2TKd9aXt7658utY8+m6Dmbe6xSz5ovsx77MW8+g7DfY9vJdzdGfxBQ5c2fMawyFk+oQhrQCmtBLUn/g0p8uL9BvrpvTQSYG5eXateeOTZlPb1GHCo6XNmDVVbeaZpU3mihlYZsZG8kNHp8xho/FvKTv1r3u21jyzrMms1mvb2k2PB69Zvb3Nvof3cg7OhdXzeYoAJoTHeKofGP7eBxi1ArgDssapnt/r8gLOV6VtYMEnYi0tnYBobu0w61OA2WhGess86NaUHQAQn1Nn3vqs2WzJtlvge3vwWt7DezkH58J/Ms2LAKS4g+B9PV5fVln1X32hw74t3xD3NFhTvgvgl5jeRGcCy6wVjXY6t7R17PKl18raZixpNL98qsYcPzEwx8odXPlMjXljVbN1A7t78N65nzbLfdTIT7u0y/rueDGouGCm4sXxWGWP/pJQ79gcSzU1dHfygf4Dk+IMU8pzzbM18olNFrTO1oVlfrat1Tz1fqO5dXaduUHAT5FP5LWFHhszbWbaokZziQLVcXe4ADRgXFEsc6VrzGW+1S31duwE89vWGjHhRPqT3l4A34S/PEnTluk7UwGG6dzZMuubAbPNvPVpi3n1o2azVK4gU99RMJCc94ONrea+efXm+w9m5YfTdpoXg8vULH2SRL3bwHP0mKq/tS1RTDhPzthlHlnh/CWA4gMJLASYriyzSZaZbegwqVoX2XsTXHpz1CnIvakpfrGsks9DMCsCkK2uq5mOx8YHB+bPG9XtEz+X9NbY3NcLHWlzRZfOXN2NZRbrWLm5zdzwXK0ZPN59lliiKL6yUQXKLJppzOBdQMR50qin29cVIVGIZa7rwjKLcZBqJV6tM6feG+wIOEUpIemp00hTirgTqcF/YhU1fwHb7UmJgi6UzzIJOMUG8u659ebshzPmqEoHYmlFUfLKNbBggxLBAAgdG8H5x7I7dNVo3ieCzwq9UD7L5Is2F9Ey1+lm3Tevwfzwkayd3kUEMuXbzOdaqk16I1vF0AV0CXjwiG/eR3LBIwTk4XL6xym/u+rpGlsublCq0tpePCDvn99giROALPV03sDo88oGq+pQJ5IEHUWJlZWgkxHCl6FM8D3ggi5UnpOwh9UGSTgExpyPm8y2mnbT3lEcIB8QkCO8RZZ6ei8kVyKm2aj4HkRBZ/vjJJalieCbvqm/W9E63yBRP7rSTfPDBObQezImqWCwaF3LTjV5pFNbQJ4rFsmxS18COaAitYNkiapsJDD7+vtrroUghZivq2t8I6igi4RT6QRZ4ZkPZM2pk5zzV1PJXDA1a6a+26hqpL0oweauuSTlGTsrBuuaQ+7O2OsfM8F9roij+HwrzaH+hnMEVZn+7ChAdFPaBRyqjNtUDsbn1JtLxSN+V19quMiFsS/XmY+3tEYO5KqtrZbAOF0kysniKi98rNqSzKNfqrOJ+lB/Q0MOtfDvKiIDnaboRq+UqDrJCo0iulNUFUfKP/5cH/6VD5vNsg2t5jHVwhdMdV8GgmPZhpbIgVyxqdXcLOBOE5Bc64G36827ciNLdf0n3mu02QMAl/rWRiS1t1owEBkl+Ec6ZlZuEhGQ9FWI2CMVXD6S5TUIs4VrWyyp8b0HxD8KSOrsqI+lujncpDN0jZEza83rnzhmiSzhA4EM1Qflx2w5IhoiWDEluJXau8QrJi7w4TwSIA/2QALcJ1tduvO+mPNRz9easzTdKeOKBSTnxqWM0uP81c26iS6orRe99+CCBvODh7LW9UREaqxTQ/AOYfezEmpG/eBSjeVRW+RV4ghXiJXBKuarhXCt2Oyz9EVuer44U5tz3vi8uwbXondE2wNiZK0i+kMC8uyHIwVygyjHB5jeAjI40Wt4VhYLyGoBOU9MNlP9TEVUrKVYQHJurjHS56wpAdkq0uTT7aLZ5tcL5Ex0QKoh6GSMqWsFZGqIXSngNI2RAnmlBxK+8D11Fa9/rsZG1GtkLcs3Rh+1OSfnJthwrXma2lB2UHcfbm41d6k5B8gRWuQW4fY4FY4Y8fSpELmRA6kPeoXKwuWKmPjI9crxSMZx9rRUF6+P3iI5J+fmZsECkWJBlHAj8dGVr7nUiCQ9oq7jNuE3nYBT4tsKRQHy8hkOSNtbkWVMUQsVIPFTz3/QZOr0BaOob1pUbwb17eZpESP4R6I2rd2qWpf0W9ci0nf0S67jCJD9ogFyK8tbtJDqFllk6ruqakbqBx9GDeRlanQt9b4Qh4/zHzEla4aoWYZ1kpJgLYUeNSo36WCOfaXOnHx3YK/BtUJCmdoewgTi5GQvKugfTdt2M1JADJH053ibVMbTH0QNJNNsSU6ag5+6ZbazCkpFkvQt2cJ5NSzvFfWArp9VY9Obm5SUr8jxwXQ26eecN/lLMiMieg0Z4IME6xK79o/FPW7FVTRAjnbyFKQlqCJCpocI+oKmNC1ZwIT0fXdt4b5yuyyO5BtSN6EScaY6mZtybhCVDWkRtT/dxehKxPR6tR3uRode4gndH/oGeCRAQpvhg84TnfXE4kazubrdBhymNwzNZPlKfgehMf41CQsUCEJ/tjtHtfwvFRQp1tuK1J+qUwm7xDXxnc+taDI/ebzaDKxw0TqWiIxWW2MbYcn0j0pKx2//DyK3W9cXDZD9PH11yj0uZ5wjf4U1hv3tZRsduUCVA5gQC3M0NXcXTHwhAYXkm3SnUdUMsyBQqxdgx7zilBj9bbSOjv2xARrNqAK2k6cgZ06kX4uK/Sn1dBV8JHnb+NfrLDOzw4J8gj5GLNCZAvMMm/fVmiclHli0vtWWlfi1LbLk7bVu8BxrJqVZpfGFfp+pzy9z6eiADWozd7whWk1+k88SdeuBxaVyjRfBnlllBeJ6/WJGT8qK3o5w6uAnIVihsF78sMn2ssMDEGBmJuqL/lR0F18WWeB1s+qsqu2xxQ12SmKpWDTPH15YL1dQZ+58o848t7zRAlqXJ+q3ySQXrmmx/ti6G/ntqKUsrK2EsID4sbLm8mTV/7vlEbZfE9kyj1BHDnV2u3I4NDq5ygqmJCkQ0hUqkhFTqs25j1Rbf3bZjGrLEkGLoXTjOTfkfKU2V+l35KSoK/IBSW5JNYVrQRN02OhUj8rhvrYbUDCzegwqsoQVT7GK1H/LT97gRfYNUQEZqnB5RPp3qwBZuKZ5p9wRdoZpukDWM02BiekObwiYF8pC4RUveMw9h6jFwibIVSAh3JSlju56auMjabQh3OJ89IyQ1fSLTkeZFV7TYM/sEmZaiSy/ZUkECgK9IB11+zKULA9T4570BHpra037Lt1Egg285XSRsLQMKPNIshk8J73hdwt0M8gEemqgUWOv2Z6rz9xZOVygLHoNC/dRp1jNJH1tEGWfCC3FnejVuVE54526iTSgTvUMObJoOMKuUpkNQZtNYT5SAr9ykxs8R4CFlXUVZIjUDT5adwYz1GfmUw7vjiTaLkyIp3/Behzb1w5X+ocrF6Kk07rymaHUmS81Tda15AtFYAGXbWi3AaK3By8l+tNEo55/U1Md94CbaM6jz4zQMokl99tNTFCohKvEsErM03bE3LK4SAHMtUwnnHfie6L0bS/WWT04ytu1Vaq9W3oGE+sjPVogN8DNuFXnQBuJpc9Ykl+fGaFlfojRYXzWGjsvHbEru1xbNlvMRZq5pMEQJe2kP2MU1ScrvZm9sslGWwIJgHV466NSwXIXK2V6Sa+B3YnLb14y3XUnKf2Om4g8Bj261xoVxzLbvLENt+tvujqocjwTtLCY6wxzl9mFS0uoPGiMjdDqiJtfqDOzVZMzbSkr8X0k9NOVsKP0PV+voXd92n0ZG41LvR4Saz9hYvf6zM6a9pPu7HM038JCJlbC5ZU/M9/FrR1LIe6XDhcFyFgOmCTKsEUH3ZYyXx9VZQ680Qmv6IXDeDeJ08goAL0mrflINdOOFnvzzZtS5hs3Vtn3HHL7l+7CLWb6Ujnck2Ui00ZGg2ybwqEXMsBG1t2w71C3iz7tshCpq2h6d7csJIoRrlm0chYPJuAAJBZ6i4QFVD7knKRFs1TZXDy92n5ZgDzwpipz8G2pHYl27sqx3iiHsUz87CxVSPhWONIBFT32uz+3KQ+Cip72w7BayWRVjEWNrLCP0leGYibuPoucIDUgLpDhMZiuZ6tUJOl+8O0GW/U0NDv2G1kzPvFnouZg2OEdUZ3BIuESCF7ojHboM3Oyg2fy6DNtm1h8Kf1uWhD9u29BtFhr9FK+Xq0Cw2xp0fpFnJkofWPoE1kCB1GBnvEJ1dVPvt9gpixqENnbYHNMluBBVGBJ+Mg1AmKuIvvT8m3U4ZDCT6lqmabn1OuXPllt/SySFAA5fHS6S31mrmWiO6dCgmym8nItiDzacS2Mt9wjGzL1dlE8CxghMtiPzO/ZEw295leKDVeQoH/ysogMmB5Y8m3VbXaZxyYN2B7bz+nY2Xroj9M2gLTldTzfrOdLlYtOXthg9UUAF2YFnZXDzy5364BqPeX2nnhQCBICHDMkdDddb9cQzLT7d/Rl2TFma8FUP8dryiOJ4AQWrIRpjBaHdCYKbTlECGXnOJWS3KTQ93ZWDhNYsHa6jfR0AJFclrU5A73b6YodQoVi9aNSne2oYvpykA7Rj/CbuTVFwZzjzOmdwPh8ogVMkK/kiEwzHrGWGj+oXCgZ7cj5Oa/JfQ/W+foqx28Ov3fXdmtomcN8eXqHfCJkMj4WNxMuk867PUMyfQ8zdLdA3LFJnBSpNMCjaNcy3RwTlLGSEsCk14LwlGnG4yJVK+9owCVS8i1Y3WxJCv7Pz+nx8Br7HlkWkf1FJegQuD8VY0QeSUshd4qGlgm5y3I/iOShVjMZ7LRoP1+XkIqPnQYL2kEARS/hXie7Tyf+opAoHgpQB493CThf/EZZBswOzXseYYfiPQxeE76HSI6VjZB10WodlMy/FjHWaZuIgd1LojMs9idKE3wj2XXFJuqSttB69GAWLotWdMU3kQKdLr9G+mEf73MVS3cjfB3v4f/kgExRQOzpRvbLyTW72YHAsTuqp7X26Ovk15Hsa2GXkGgfSOpL1uJRJkWRU5b6KcjUCvXefRk73lMR6cqFGmKCIvQ1lIGRb0QHmHYxPPveumR9UyElYphqhDV3IaNfdPtb1Hld+Ch09ejri7RDqTmA3e3UOTvL0uwqmfbivpCRL0LyrM717DFZNBBz63G75RUqNqUFUSbsexNEmy+L8WbT44J2VOmzdbJzszpoXvq7pBi9nj0wGv0ug08h/6b/stu5YiEHS0vYD1zl0+V+ecm2/QjEZm8A7P87xKU4Wgq393Z2Ngewj6TdkYXk1X24rVGpNoqyTazbbfplu6+5RGTsQLhP7A7LdGB/XT6UW9No9xZfuw/uKUmSPZcN4m1/SgawR/1hXwIRCSxRHd/55YbF1kKb9xp4zgLfcXrv4HK26Mrbb9mXwGRvDLcHeeoUhP6kSnY3+z2732SrF4LO8VvvnGsB1Obux05I/9H+93cZtGsLQiPPbT7ul+ot89Fyq19IWsjmxu1eZpPyrYCPEDfZDeS9CHTHCtb9/c8MYKF2Y2Onehsu//RzoiWWCrA507+hTxbnWiHckMVebjPJ6hW1G7/9kwYShxGN9zsL7O3OqEwvG5hkqV7dMRblgltqkX7Rqryc5b7tW8OMt93P9EcuEulX3ZY62vGAHe+dAOx8Fl/hUuyePPvFnxIoNMqrwUa0xFLt37HRHjr2b9koJ6XxhjXZZF/Vk/0bNtqN2VZSWsVr28XJ1FFszKHpe6h1HVLT2WrLykf2Yi64Nw5IkPAPBOFLsVTAJaezfyhIbJMTLMglCGj5uf+ENGGvcEhWoi5TFnYGaaLdRauYOzX3cPwKA2+loFSDBecAAAAASUVORK5CYII=";

var recDelete = "data:image/png;base64,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";

var recMockWave = "data:image/png;base64,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";

var core = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory();
  }
})(commonjsGlobal, function () {
  /*globals window, global, require*/

  /**
   * CryptoJS core components.
   */
  var CryptoJS = CryptoJS || function (Math, undefined$1) {
    var crypto$1;

    // Native crypto from window (Browser)
    if (typeof window !== 'undefined' && window.crypto) {
      crypto$1 = window.crypto;
    }

    // Native crypto in web worker (Browser)
    if (typeof self !== 'undefined' && self.crypto) {
      crypto$1 = self.crypto;
    }

    // Native crypto from worker
    if (typeof globalThis !== 'undefined' && globalThis.crypto) {
      crypto$1 = globalThis.crypto;
    }

    // Native (experimental IE 11) crypto from window (Browser)
    if (!crypto$1 && typeof window !== 'undefined' && window.msCrypto) {
      crypto$1 = window.msCrypto;
    }

    // Native crypto from global (NodeJS)
    if (!crypto$1 && typeof commonjsGlobal !== 'undefined' && commonjsGlobal.crypto) {
      crypto$1 = commonjsGlobal.crypto;
    }

    // Native crypto import via require (NodeJS)
    if (!crypto$1 && typeof commonjsRequire === 'function') {
      try {
        crypto$1 = crypto;
      } catch (err) {}
    }

    /*
     * Cryptographically secure pseudorandom number generator
     *
     * As Math.random() is cryptographically not safe to use
     */
    var cryptoSecureRandomInt = function () {
      if (crypto$1) {
        // Use getRandomValues method (Browser)
        if (typeof crypto$1.getRandomValues === 'function') {
          try {
            return crypto$1.getRandomValues(new Uint32Array(1))[0];
          } catch (err) {}
        }

        // Use randomBytes method (NodeJS)
        if (typeof crypto$1.randomBytes === 'function') {
          try {
            return crypto$1.randomBytes(4).readInt32LE();
          } catch (err) {}
        }
      }
      throw new Error('Native crypto module could not be used to get secure random number.');
    };

    /*
     * Local polyfill of Object.create
      */
    var create = Object.create || function () {
      function F() {}
      return function (obj) {
        var subtype;
        F.prototype = obj;
        subtype = new F();
        F.prototype = null;
        return subtype;
      };
    }();

    /**
     * CryptoJS namespace.
     */
    var C = {};

    /**
     * Library namespace.
     */
    var C_lib = C.lib = {};

    /**
     * Base object for prototypal inheritance.
     */
    var Base = C_lib.Base = function () {
      return {
        /**
         * Creates a new object that inherits from this object.
         *
         * @param {Object} overrides Properties to copy into the new object.
         *
         * @return {Object} The new object.
         *
         * @static
         *
         * @example
         *
         *     var MyType = CryptoJS.lib.Base.extend({
         *         field: 'value',
         *
         *         method: function () {
         *         }
         *     });
         */
        extend: function (overrides) {
          // Spawn
          var subtype = create(this);

          // Augment
          if (overrides) {
            subtype.mixIn(overrides);
          }

          // Create default initializer
          if (!subtype.hasOwnProperty('init') || this.init === subtype.init) {
            subtype.init = function () {
              subtype.$super.init.apply(this, arguments);
            };
          }

          // Initializer's prototype is the subtype object
          subtype.init.prototype = subtype;

          // Reference supertype
          subtype.$super = this;
          return subtype;
        },
        /**
         * Extends this object and runs the init method.
         * Arguments to create() will be passed to init().
         *
         * @return {Object} The new object.
         *
         * @static
         *
         * @example
         *
         *     var instance = MyType.create();
         */
        create: function () {
          var instance = this.extend();
          instance.init.apply(instance, arguments);
          return instance;
        },
        /**
         * Initializes a newly created object.
         * Override this method to add some logic when your objects are created.
         *
         * @example
         *
         *     var MyType = CryptoJS.lib.Base.extend({
         *         init: function () {
         *             // ...
         *         }
         *     });
         */
        init: function () {},
        /**
         * Copies properties into this object.
         *
         * @param {Object} properties The properties to mix in.
         *
         * @example
         *
         *     MyType.mixIn({
         *         field: 'value'
         *     });
         */
        mixIn: function (properties) {
          for (var propertyName in properties) {
            if (properties.hasOwnProperty(propertyName)) {
              this[propertyName] = properties[propertyName];
            }
          }

          // IE won't copy toString using the loop above
          if (properties.hasOwnProperty('toString')) {
            this.toString = properties.toString;
          }
        },
        /**
         * Creates a copy of this object.
         *
         * @return {Object} The clone.
         *
         * @example
         *
         *     var clone = instance.clone();
         */
        clone: function () {
          return this.init.prototype.extend(this);
        }
      };
    }();

    /**
     * An array of 32-bit words.
     *
     * @property {Array} words The array of 32-bit words.
     * @property {number} sigBytes The number of significant bytes in this word array.
     */
    var WordArray = C_lib.WordArray = Base.extend({
      /**
       * Initializes a newly created word array.
       *
       * @param {Array} words (Optional) An array of 32-bit words.
       * @param {number} sigBytes (Optional) The number of significant bytes in the words.
       *
       * @example
       *
       *     var wordArray = CryptoJS.lib.WordArray.create();
       *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607]);
       *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607], 6);
       */
      init: function (words, sigBytes) {
        words = this.words = words || [];
        if (sigBytes != undefined$1) {
          this.sigBytes = sigBytes;
        } else {
          this.sigBytes = words.length * 4;
        }
      },
      /**
       * Converts this word array to a string.
       *
       * @param {Encoder} encoder (Optional) The encoding strategy to use. Default: CryptoJS.enc.Hex
       *
       * @return {string} The stringified word array.
       *
       * @example
       *
       *     var string = wordArray + '';
       *     var string = wordArray.toString();
       *     var string = wordArray.toString(CryptoJS.enc.Utf8);
       */
      toString: function (encoder) {
        return (encoder || Hex).stringify(this);
      },
      /**
       * Concatenates a word array to this word array.
       *
       * @param {WordArray} wordArray The word array to append.
       *
       * @return {WordArray} This word array.
       *
       * @example
       *
       *     wordArray1.concat(wordArray2);
       */
      concat: function (wordArray) {
        // Shortcuts
        var thisWords = this.words;
        var thatWords = wordArray.words;
        var thisSigBytes = this.sigBytes;
        var thatSigBytes = wordArray.sigBytes;

        // Clamp excess bits
        this.clamp();

        // Concat
        if (thisSigBytes % 4) {
          // Copy one byte at a time
          for (var i = 0; i < thatSigBytes; i++) {
            var thatByte = thatWords[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
            thisWords[thisSigBytes + i >>> 2] |= thatByte << 24 - (thisSigBytes + i) % 4 * 8;
          }
        } else {
          // Copy one word at a time
          for (var j = 0; j < thatSigBytes; j += 4) {
            thisWords[thisSigBytes + j >>> 2] = thatWords[j >>> 2];
          }
        }
        this.sigBytes += thatSigBytes;

        // Chainable
        return this;
      },
      /**
       * Removes insignificant bits.
       *
       * @example
       *
       *     wordArray.clamp();
       */
      clamp: function () {
        // Shortcuts
        var words = this.words;
        var sigBytes = this.sigBytes;

        // Clamp
        words[sigBytes >>> 2] &= 0xffffffff << 32 - sigBytes % 4 * 8;
        words.length = Math.ceil(sigBytes / 4);
      },
      /**
       * Creates a copy of this word array.
       *
       * @return {WordArray} The clone.
       *
       * @example
       *
       *     var clone = wordArray.clone();
       */
      clone: function () {
        var clone = Base.clone.call(this);
        clone.words = this.words.slice(0);
        return clone;
      },
      /**
       * Creates a word array filled with random bytes.
       *
       * @param {number} nBytes The number of random bytes to generate.
       *
       * @return {WordArray} The random word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.lib.WordArray.random(16);
       */
      random: function (nBytes) {
        var words = [];
        for (var i = 0; i < nBytes; i += 4) {
          words.push(cryptoSecureRandomInt());
        }
        return new WordArray.init(words, nBytes);
      }
    });

    /**
     * Encoder namespace.
     */
    var C_enc = C.enc = {};

    /**
     * Hex encoding strategy.
     */
    var Hex = C_enc.Hex = {
      /**
       * Converts a word array to a hex string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The hex string.
       *
       * @static
       *
       * @example
       *
       *     var hexString = CryptoJS.enc.Hex.stringify(wordArray);
       */
      stringify: function (wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var hexChars = [];
        for (var i = 0; i < sigBytes; i++) {
          var bite = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          hexChars.push((bite >>> 4).toString(16));
          hexChars.push((bite & 0x0f).toString(16));
        }
        return hexChars.join('');
      },
      /**
       * Converts a hex string to a word array.
       *
       * @param {string} hexStr The hex string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Hex.parse(hexString);
       */
      parse: function (hexStr) {
        // Shortcut
        var hexStrLength = hexStr.length;

        // Convert
        var words = [];
        for (var i = 0; i < hexStrLength; i += 2) {
          words[i >>> 3] |= parseInt(hexStr.substr(i, 2), 16) << 24 - i % 8 * 4;
        }
        return new WordArray.init(words, hexStrLength / 2);
      }
    };

    /**
     * Latin1 encoding strategy.
     */
    var Latin1 = C_enc.Latin1 = {
      /**
       * Converts a word array to a Latin1 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The Latin1 string.
       *
       * @static
       *
       * @example
       *
       *     var latin1String = CryptoJS.enc.Latin1.stringify(wordArray);
       */
      stringify: function (wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var latin1Chars = [];
        for (var i = 0; i < sigBytes; i++) {
          var bite = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          latin1Chars.push(String.fromCharCode(bite));
        }
        return latin1Chars.join('');
      },
      /**
       * Converts a Latin1 string to a word array.
       *
       * @param {string} latin1Str The Latin1 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Latin1.parse(latin1String);
       */
      parse: function (latin1Str) {
        // Shortcut
        var latin1StrLength = latin1Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < latin1StrLength; i++) {
          words[i >>> 2] |= (latin1Str.charCodeAt(i) & 0xff) << 24 - i % 4 * 8;
        }
        return new WordArray.init(words, latin1StrLength);
      }
    };

    /**
     * UTF-8 encoding strategy.
     */
    var Utf8 = C_enc.Utf8 = {
      /**
       * Converts a word array to a UTF-8 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-8 string.
       *
       * @static
       *
       * @example
       *
       *     var utf8String = CryptoJS.enc.Utf8.stringify(wordArray);
       */
      stringify: function (wordArray) {
        try {
          return decodeURIComponent(escape(Latin1.stringify(wordArray)));
        } catch (e) {
          throw new Error('Malformed UTF-8 data');
        }
      },
      /**
       * Converts a UTF-8 string to a word array.
       *
       * @param {string} utf8Str The UTF-8 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf8.parse(utf8String);
       */
      parse: function (utf8Str) {
        return Latin1.parse(unescape(encodeURIComponent(utf8Str)));
      }
    };

    /**
     * Abstract buffered block algorithm template.
     *
     * The property blockSize must be implemented in a concrete subtype.
     *
     * @property {number} _minBufferSize The number of blocks that should be kept unprocessed in the buffer. Default: 0
     */
    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm = Base.extend({
      /**
       * Resets this block algorithm's data buffer to its initial state.
       *
       * @example
       *
       *     bufferedBlockAlgorithm.reset();
       */
      reset: function () {
        // Initial values
        this._data = new WordArray.init();
        this._nDataBytes = 0;
      },
      /**
       * Adds new data to this block algorithm's buffer.
       *
       * @param {WordArray|string} data The data to append. Strings are converted to a WordArray using UTF-8.
       *
       * @example
       *
       *     bufferedBlockAlgorithm._append('data');
       *     bufferedBlockAlgorithm._append(wordArray);
       */
      _append: function (data) {
        // Convert string to WordArray, else assume WordArray already
        if (typeof data == 'string') {
          data = Utf8.parse(data);
        }

        // Append
        this._data.concat(data);
        this._nDataBytes += data.sigBytes;
      },
      /**
       * Processes available data blocks.
       *
       * This method invokes _doProcessBlock(offset), which must be implemented by a concrete subtype.
       *
       * @param {boolean} doFlush Whether all blocks and partial blocks should be processed.
       *
       * @return {WordArray} The processed data.
       *
       * @example
       *
       *     var processedData = bufferedBlockAlgorithm._process();
       *     var processedData = bufferedBlockAlgorithm._process(!!'flush');
       */
      _process: function (doFlush) {
        var processedWords;

        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var dataSigBytes = data.sigBytes;
        var blockSize = this.blockSize;
        var blockSizeBytes = blockSize * 4;

        // Count blocks ready
        var nBlocksReady = dataSigBytes / blockSizeBytes;
        if (doFlush) {
          // Round up to include partial blocks
          nBlocksReady = Math.ceil(nBlocksReady);
        } else {
          // Round down to include only full blocks,
          // less the number of blocks that must remain in the buffer
          nBlocksReady = Math.max((nBlocksReady | 0) - this._minBufferSize, 0);
        }

        // Count words ready
        var nWordsReady = nBlocksReady * blockSize;

        // Count bytes ready
        var nBytesReady = Math.min(nWordsReady * 4, dataSigBytes);

        // Process blocks
        if (nWordsReady) {
          for (var offset = 0; offset < nWordsReady; offset += blockSize) {
            // Perform concrete-algorithm logic
            this._doProcessBlock(dataWords, offset);
          }

          // Remove processed words
          processedWords = dataWords.splice(0, nWordsReady);
          data.sigBytes -= nBytesReady;
        }

        // Return processed words
        return new WordArray.init(processedWords, nBytesReady);
      },
      /**
       * Creates a copy of this object.
       *
       * @return {Object} The clone.
       *
       * @example
       *
       *     var clone = bufferedBlockAlgorithm.clone();
       */
      clone: function () {
        var clone = Base.clone.call(this);
        clone._data = this._data.clone();
        return clone;
      },
      _minBufferSize: 0
    });

    /**
     * Abstract hasher template.
     *
     * @property {number} blockSize The number of 32-bit words this hasher operates on. Default: 16 (512 bits)
     */
    C_lib.Hasher = BufferedBlockAlgorithm.extend({
      /**
       * Configuration options.
       */
      cfg: Base.extend(),
      /**
       * Initializes a newly created hasher.
       *
       * @param {Object} cfg (Optional) The configuration options to use for this hash computation.
       *
       * @example
       *
       *     var hasher = CryptoJS.algo.SHA256.create();
       */
      init: function (cfg) {
        // Apply config defaults
        this.cfg = this.cfg.extend(cfg);

        // Set initial values
        this.reset();
      },
      /**
       * Resets this hasher to its initial state.
       *
       * @example
       *
       *     hasher.reset();
       */
      reset: function () {
        // Reset data buffer
        BufferedBlockAlgorithm.reset.call(this);

        // Perform concrete-hasher logic
        this._doReset();
      },
      /**
       * Updates this hasher with a message.
       *
       * @param {WordArray|string} messageUpdate The message to append.
       *
       * @return {Hasher} This hasher.
       *
       * @example
       *
       *     hasher.update('message');
       *     hasher.update(wordArray);
       */
      update: function (messageUpdate) {
        // Append
        this._append(messageUpdate);

        // Update the hash
        this._process();

        // Chainable
        return this;
      },
      /**
       * Finalizes the hash computation.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} messageUpdate (Optional) A final message update.
       *
       * @return {WordArray} The hash.
       *
       * @example
       *
       *     var hash = hasher.finalize();
       *     var hash = hasher.finalize('message');
       *     var hash = hasher.finalize(wordArray);
       */
      finalize: function (messageUpdate) {
        // Final message update
        if (messageUpdate) {
          this._append(messageUpdate);
        }

        // Perform concrete-hasher logic
        var hash = this._doFinalize();
        return hash;
      },
      blockSize: 512 / 32,
      /**
       * Creates a shortcut function to a hasher's object interface.
       *
       * @param {Hasher} hasher The hasher to create a helper for.
       *
       * @return {Function} The shortcut function.
       *
       * @static
       *
       * @example
       *
       *     var SHA256 = CryptoJS.lib.Hasher._createHelper(CryptoJS.algo.SHA256);
       */
      _createHelper: function (hasher) {
        return function (message, cfg) {
          return new hasher.init(cfg).finalize(message);
        };
      },
      /**
       * Creates a shortcut function to the HMAC's object interface.
       *
       * @param {Hasher} hasher The hasher to use in this HMAC helper.
       *
       * @return {Function} The shortcut function.
       *
       * @static
       *
       * @example
       *
       *     var HmacSHA256 = CryptoJS.lib.Hasher._createHmacHelper(CryptoJS.algo.SHA256);
       */
      _createHmacHelper: function (hasher) {
        return function (message, key) {
          return new C_algo.HMAC.init(hasher, key).finalize(message);
        };
      }
    });

    /**
     * Algorithm namespace.
     */
    var C_algo = C.algo = {};
    return C;
  }(Math);
  return CryptoJS;
});
});

var x64Core = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function (undefined$1) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var X32WordArray = C_lib.WordArray;

    /**
     * x64 namespace.
     */
    var C_x64 = C.x64 = {};

    /**
     * A 64-bit word.
     */
    C_x64.Word = Base.extend({
      /**
       * Initializes a newly created 64-bit word.
       *
       * @param {number} high The high 32 bits.
       * @param {number} low The low 32 bits.
       *
       * @example
       *
       *     var x64Word = CryptoJS.x64.Word.create(0x00010203, 0x04050607);
       */
      init: function (high, low) {
        this.high = high;
        this.low = low;
      }

      /**
       * Bitwise NOTs this word.
       *
       * @return {X64Word} A new x64-Word object after negating.
       *
       * @example
       *
       *     var negated = x64Word.not();
       */
      // not: function () {
      // var high = ~this.high;
      // var low = ~this.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise ANDs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to AND with this word.
       *
       * @return {X64Word} A new x64-Word object after ANDing.
       *
       * @example
       *
       *     var anded = x64Word.and(anotherX64Word);
       */
      // and: function (word) {
      // var high = this.high & word.high;
      // var low = this.low & word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise ORs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to OR with this word.
       *
       * @return {X64Word} A new x64-Word object after ORing.
       *
       * @example
       *
       *     var ored = x64Word.or(anotherX64Word);
       */
      // or: function (word) {
      // var high = this.high | word.high;
      // var low = this.low | word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise XORs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to XOR with this word.
       *
       * @return {X64Word} A new x64-Word object after XORing.
       *
       * @example
       *
       *     var xored = x64Word.xor(anotherX64Word);
       */
      // xor: function (word) {
      // var high = this.high ^ word.high;
      // var low = this.low ^ word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Shifts this word n bits to the left.
       *
       * @param {number} n The number of bits to shift.
       *
       * @return {X64Word} A new x64-Word object after shifting.
       *
       * @example
       *
       *     var shifted = x64Word.shiftL(25);
       */
      // shiftL: function (n) {
      // if (n < 32) {
      // var high = (this.high << n) | (this.low >>> (32 - n));
      // var low = this.low << n;
      // } else {
      // var high = this.low << (n - 32);
      // var low = 0;
      // }

      // return X64Word.create(high, low);
      // },

      /**
       * Shifts this word n bits to the right.
       *
       * @param {number} n The number of bits to shift.
       *
       * @return {X64Word} A new x64-Word object after shifting.
       *
       * @example
       *
       *     var shifted = x64Word.shiftR(7);
       */
      // shiftR: function (n) {
      // if (n < 32) {
      // var low = (this.low >>> n) | (this.high << (32 - n));
      // var high = this.high >>> n;
      // } else {
      // var low = this.high >>> (n - 32);
      // var high = 0;
      // }

      // return X64Word.create(high, low);
      // },

      /**
       * Rotates this word n bits to the left.
       *
       * @param {number} n The number of bits to rotate.
       *
       * @return {X64Word} A new x64-Word object after rotating.
       *
       * @example
       *
       *     var rotated = x64Word.rotL(25);
       */
      // rotL: function (n) {
      // return this.shiftL(n).or(this.shiftR(64 - n));
      // },

      /**
       * Rotates this word n bits to the right.
       *
       * @param {number} n The number of bits to rotate.
       *
       * @return {X64Word} A new x64-Word object after rotating.
       *
       * @example
       *
       *     var rotated = x64Word.rotR(7);
       */
      // rotR: function (n) {
      // return this.shiftR(n).or(this.shiftL(64 - n));
      // },

      /**
       * Adds this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to add with this word.
       *
       * @return {X64Word} A new x64-Word object after adding.
       *
       * @example
       *
       *     var added = x64Word.add(anotherX64Word);
       */
      // add: function (word) {
      // var low = (this.low + word.low) | 0;
      // var carry = (low >>> 0) < (this.low >>> 0) ? 1 : 0;
      // var high = (this.high + word.high + carry) | 0;

      // return X64Word.create(high, low);
      // }
    });

    /**
     * An array of 64-bit words.
     *
     * @property {Array} words The array of CryptoJS.x64.Word objects.
     * @property {number} sigBytes The number of significant bytes in this word array.
     */
    C_x64.WordArray = Base.extend({
      /**
       * Initializes a newly created word array.
       *
       * @param {Array} words (Optional) An array of CryptoJS.x64.Word objects.
       * @param {number} sigBytes (Optional) The number of significant bytes in the words.
       *
       * @example
       *
       *     var wordArray = CryptoJS.x64.WordArray.create();
       *
       *     var wordArray = CryptoJS.x64.WordArray.create([
       *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
       *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
       *     ]);
       *
       *     var wordArray = CryptoJS.x64.WordArray.create([
       *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
       *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
       *     ], 10);
       */
      init: function (words, sigBytes) {
        words = this.words = words || [];
        if (sigBytes != undefined$1) {
          this.sigBytes = sigBytes;
        } else {
          this.sigBytes = words.length * 8;
        }
      },
      /**
       * Converts this 64-bit word array to a 32-bit word array.
       *
       * @return {CryptoJS.lib.WordArray} This word array's data as a 32-bit word array.
       *
       * @example
       *
       *     var x32WordArray = x64WordArray.toX32();
       */
      toX32: function () {
        // Shortcuts
        var x64Words = this.words;
        var x64WordsLength = x64Words.length;

        // Convert
        var x32Words = [];
        for (var i = 0; i < x64WordsLength; i++) {
          var x64Word = x64Words[i];
          x32Words.push(x64Word.high);
          x32Words.push(x64Word.low);
        }
        return X32WordArray.create(x32Words, this.sigBytes);
      },
      /**
       * Creates a copy of this word array.
       *
       * @return {X64WordArray} The clone.
       *
       * @example
       *
       *     var clone = x64WordArray.clone();
       */
      clone: function () {
        var clone = Base.clone.call(this);

        // Clone "words" array
        var words = clone.words = this.words.slice(0);

        // Clone each X64Word object
        var wordsLength = words.length;
        for (var i = 0; i < wordsLength; i++) {
          words[i] = words[i].clone();
        }
        return clone;
      }
    });
  })();
  return CryptoJS;
});
});

var libTypedarrays = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Check if typed arrays are supported
    if (typeof ArrayBuffer != 'function') {
      return;
    }

    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;

    // Reference original init
    var superInit = WordArray.init;

    // Augment WordArray.init to handle typed arrays
    var subInit = WordArray.init = function (typedArray) {
      // Convert buffers to uint8
      if (typedArray instanceof ArrayBuffer) {
        typedArray = new Uint8Array(typedArray);
      }

      // Convert other array views to uint8
      if (typedArray instanceof Int8Array || typeof Uint8ClampedArray !== "undefined" && typedArray instanceof Uint8ClampedArray || typedArray instanceof Int16Array || typedArray instanceof Uint16Array || typedArray instanceof Int32Array || typedArray instanceof Uint32Array || typedArray instanceof Float32Array || typedArray instanceof Float64Array) {
        typedArray = new Uint8Array(typedArray.buffer, typedArray.byteOffset, typedArray.byteLength);
      }

      // Handle Uint8Array
      if (typedArray instanceof Uint8Array) {
        // Shortcut
        var typedArrayByteLength = typedArray.byteLength;

        // Extract bytes
        var words = [];
        for (var i = 0; i < typedArrayByteLength; i++) {
          words[i >>> 2] |= typedArray[i] << 24 - i % 4 * 8;
        }

        // Initialize this word array
        superInit.call(this, words, typedArrayByteLength);
      } else {
        // Else call normal init
        superInit.apply(this, arguments);
      }
    };
    subInit.prototype = WordArray;
  })();
  return CryptoJS.lib.WordArray;
});
});

var encUtf16 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * UTF-16 BE encoding strategy.
     */
    C_enc.Utf16 = C_enc.Utf16BE = {
      /**
       * Converts a word array to a UTF-16 BE string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-16 BE string.
       *
       * @static
       *
       * @example
       *
       *     var utf16String = CryptoJS.enc.Utf16.stringify(wordArray);
       */
      stringify: function (wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var utf16Chars = [];
        for (var i = 0; i < sigBytes; i += 2) {
          var codePoint = words[i >>> 2] >>> 16 - i % 4 * 8 & 0xffff;
          utf16Chars.push(String.fromCharCode(codePoint));
        }
        return utf16Chars.join('');
      },
      /**
       * Converts a UTF-16 BE string to a word array.
       *
       * @param {string} utf16Str The UTF-16 BE string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf16.parse(utf16String);
       */
      parse: function (utf16Str) {
        // Shortcut
        var utf16StrLength = utf16Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < utf16StrLength; i++) {
          words[i >>> 1] |= utf16Str.charCodeAt(i) << 16 - i % 2 * 16;
        }
        return WordArray.create(words, utf16StrLength * 2);
      }
    };

    /**
     * UTF-16 LE encoding strategy.
     */
    C_enc.Utf16LE = {
      /**
       * Converts a word array to a UTF-16 LE string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-16 LE string.
       *
       * @static
       *
       * @example
       *
       *     var utf16Str = CryptoJS.enc.Utf16LE.stringify(wordArray);
       */
      stringify: function (wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var utf16Chars = [];
        for (var i = 0; i < sigBytes; i += 2) {
          var codePoint = swapEndian(words[i >>> 2] >>> 16 - i % 4 * 8 & 0xffff);
          utf16Chars.push(String.fromCharCode(codePoint));
        }
        return utf16Chars.join('');
      },
      /**
       * Converts a UTF-16 LE string to a word array.
       *
       * @param {string} utf16Str The UTF-16 LE string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf16LE.parse(utf16Str);
       */
      parse: function (utf16Str) {
        // Shortcut
        var utf16StrLength = utf16Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < utf16StrLength; i++) {
          words[i >>> 1] |= swapEndian(utf16Str.charCodeAt(i) << 16 - i % 2 * 16);
        }
        return WordArray.create(words, utf16StrLength * 2);
      }
    };
    function swapEndian(word) {
      return word << 8 & 0xff00ff00 | word >>> 8 & 0x00ff00ff;
    }
  })();
  return CryptoJS.enc.Utf16;
});
});

var encBase64 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * Base64 encoding strategy.
     */
    C_enc.Base64 = {
      /**
       * Converts a word array to a Base64 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The Base64 string.
       *
       * @static
       *
       * @example
       *
       *     var base64String = CryptoJS.enc.Base64.stringify(wordArray);
       */
      stringify: function (wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;
        var map = this._map;

        // Clamp excess bits
        wordArray.clamp();

        // Convert
        var base64Chars = [];
        for (var i = 0; i < sigBytes; i += 3) {
          var byte1 = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          var byte2 = words[i + 1 >>> 2] >>> 24 - (i + 1) % 4 * 8 & 0xff;
          var byte3 = words[i + 2 >>> 2] >>> 24 - (i + 2) % 4 * 8 & 0xff;
          var triplet = byte1 << 16 | byte2 << 8 | byte3;
          for (var j = 0; j < 4 && i + j * 0.75 < sigBytes; j++) {
            base64Chars.push(map.charAt(triplet >>> 6 * (3 - j) & 0x3f));
          }
        }

        // Add padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          while (base64Chars.length % 4) {
            base64Chars.push(paddingChar);
          }
        }
        return base64Chars.join('');
      },
      /**
       * Converts a Base64 string to a word array.
       *
       * @param {string} base64Str The Base64 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Base64.parse(base64String);
       */
      parse: function (base64Str) {
        // Shortcuts
        var base64StrLength = base64Str.length;
        var map = this._map;
        var reverseMap = this._reverseMap;
        if (!reverseMap) {
          reverseMap = this._reverseMap = [];
          for (var j = 0; j < map.length; j++) {
            reverseMap[map.charCodeAt(j)] = j;
          }
        }

        // Ignore padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          var paddingIndex = base64Str.indexOf(paddingChar);
          if (paddingIndex !== -1) {
            base64StrLength = paddingIndex;
          }
        }

        // Convert
        return parseLoop(base64Str, base64StrLength, reverseMap);
      },
      _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
    };
    function parseLoop(base64Str, base64StrLength, reverseMap) {
      var words = [];
      var nBytes = 0;
      for (var i = 0; i < base64StrLength; i++) {
        if (i % 4) {
          var bits1 = reverseMap[base64Str.charCodeAt(i - 1)] << i % 4 * 2;
          var bits2 = reverseMap[base64Str.charCodeAt(i)] >>> 6 - i % 4 * 2;
          var bitsCombined = bits1 | bits2;
          words[nBytes >>> 2] |= bitsCombined << 24 - nBytes % 4 * 8;
          nBytes++;
        }
      }
      return WordArray.create(words, nBytes);
    }
  })();
  return CryptoJS.enc.Base64;
});
});

var encBase64url = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * Base64url encoding strategy.
     */
    C_enc.Base64url = {
      /**
       * Converts a word array to a Base64url string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @param {boolean} urlSafe Whether to use url safe
       *
       * @return {string} The Base64url string.
       *
       * @static
       *
       * @example
       *
       *     var base64String = CryptoJS.enc.Base64url.stringify(wordArray);
       */
      stringify: function (wordArray, urlSafe) {
        if (urlSafe === undefined) {
          urlSafe = true;
        }
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;
        var map = urlSafe ? this._safe_map : this._map;

        // Clamp excess bits
        wordArray.clamp();

        // Convert
        var base64Chars = [];
        for (var i = 0; i < sigBytes; i += 3) {
          var byte1 = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          var byte2 = words[i + 1 >>> 2] >>> 24 - (i + 1) % 4 * 8 & 0xff;
          var byte3 = words[i + 2 >>> 2] >>> 24 - (i + 2) % 4 * 8 & 0xff;
          var triplet = byte1 << 16 | byte2 << 8 | byte3;
          for (var j = 0; j < 4 && i + j * 0.75 < sigBytes; j++) {
            base64Chars.push(map.charAt(triplet >>> 6 * (3 - j) & 0x3f));
          }
        }

        // Add padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          while (base64Chars.length % 4) {
            base64Chars.push(paddingChar);
          }
        }
        return base64Chars.join('');
      },
      /**
       * Converts a Base64url string to a word array.
       *
       * @param {string} base64Str The Base64url string.
       *
       * @param {boolean} urlSafe Whether to use url safe
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Base64url.parse(base64String);
       */
      parse: function (base64Str, urlSafe) {
        if (urlSafe === undefined) {
          urlSafe = true;
        }

        // Shortcuts
        var base64StrLength = base64Str.length;
        var map = urlSafe ? this._safe_map : this._map;
        var reverseMap = this._reverseMap;
        if (!reverseMap) {
          reverseMap = this._reverseMap = [];
          for (var j = 0; j < map.length; j++) {
            reverseMap[map.charCodeAt(j)] = j;
          }
        }

        // Ignore padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          var paddingIndex = base64Str.indexOf(paddingChar);
          if (paddingIndex !== -1) {
            base64StrLength = paddingIndex;
          }
        }

        // Convert
        return parseLoop(base64Str, base64StrLength, reverseMap);
      },
      _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
      _safe_map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
    };
    function parseLoop(base64Str, base64StrLength, reverseMap) {
      var words = [];
      var nBytes = 0;
      for (var i = 0; i < base64StrLength; i++) {
        if (i % 4) {
          var bits1 = reverseMap[base64Str.charCodeAt(i - 1)] << i % 4 * 2;
          var bits2 = reverseMap[base64Str.charCodeAt(i)] >>> 6 - i % 4 * 2;
          var bitsCombined = bits1 | bits2;
          words[nBytes >>> 2] |= bitsCombined << 24 - nBytes % 4 * 8;
          nBytes++;
        }
      }
      return WordArray.create(words, nBytes);
    }
  })();
  return CryptoJS.enc.Base64url;
});
});

var md5 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Constants table
    var T = [];

    // Compute constants
    (function () {
      for (var i = 0; i < 64; i++) {
        T[i] = Math.abs(Math.sin(i + 1)) * 0x100000000 | 0;
      }
    })();

    /**
     * MD5 hash algorithm.
     */
    var MD5 = C_algo.MD5 = Hasher.extend({
      _doReset: function () {
        this._hash = new WordArray.init([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476]);
      },
      _doProcessBlock: function (M, offset) {
        // Swap endian
        for (var i = 0; i < 16; i++) {
          // Shortcuts
          var offset_i = offset + i;
          var M_offset_i = M[offset_i];
          M[offset_i] = (M_offset_i << 8 | M_offset_i >>> 24) & 0x00ff00ff | (M_offset_i << 24 | M_offset_i >>> 8) & 0xff00ff00;
        }

        // Shortcuts
        var H = this._hash.words;
        var M_offset_0 = M[offset + 0];
        var M_offset_1 = M[offset + 1];
        var M_offset_2 = M[offset + 2];
        var M_offset_3 = M[offset + 3];
        var M_offset_4 = M[offset + 4];
        var M_offset_5 = M[offset + 5];
        var M_offset_6 = M[offset + 6];
        var M_offset_7 = M[offset + 7];
        var M_offset_8 = M[offset + 8];
        var M_offset_9 = M[offset + 9];
        var M_offset_10 = M[offset + 10];
        var M_offset_11 = M[offset + 11];
        var M_offset_12 = M[offset + 12];
        var M_offset_13 = M[offset + 13];
        var M_offset_14 = M[offset + 14];
        var M_offset_15 = M[offset + 15];

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];

        // Computation
        a = FF(a, b, c, d, M_offset_0, 7, T[0]);
        d = FF(d, a, b, c, M_offset_1, 12, T[1]);
        c = FF(c, d, a, b, M_offset_2, 17, T[2]);
        b = FF(b, c, d, a, M_offset_3, 22, T[3]);
        a = FF(a, b, c, d, M_offset_4, 7, T[4]);
        d = FF(d, a, b, c, M_offset_5, 12, T[5]);
        c = FF(c, d, a, b, M_offset_6, 17, T[6]);
        b = FF(b, c, d, a, M_offset_7, 22, T[7]);
        a = FF(a, b, c, d, M_offset_8, 7, T[8]);
        d = FF(d, a, b, c, M_offset_9, 12, T[9]);
        c = FF(c, d, a, b, M_offset_10, 17, T[10]);
        b = FF(b, c, d, a, M_offset_11, 22, T[11]);
        a = FF(a, b, c, d, M_offset_12, 7, T[12]);
        d = FF(d, a, b, c, M_offset_13, 12, T[13]);
        c = FF(c, d, a, b, M_offset_14, 17, T[14]);
        b = FF(b, c, d, a, M_offset_15, 22, T[15]);
        a = GG(a, b, c, d, M_offset_1, 5, T[16]);
        d = GG(d, a, b, c, M_offset_6, 9, T[17]);
        c = GG(c, d, a, b, M_offset_11, 14, T[18]);
        b = GG(b, c, d, a, M_offset_0, 20, T[19]);
        a = GG(a, b, c, d, M_offset_5, 5, T[20]);
        d = GG(d, a, b, c, M_offset_10, 9, T[21]);
        c = GG(c, d, a, b, M_offset_15, 14, T[22]);
        b = GG(b, c, d, a, M_offset_4, 20, T[23]);
        a = GG(a, b, c, d, M_offset_9, 5, T[24]);
        d = GG(d, a, b, c, M_offset_14, 9, T[25]);
        c = GG(c, d, a, b, M_offset_3, 14, T[26]);
        b = GG(b, c, d, a, M_offset_8, 20, T[27]);
        a = GG(a, b, c, d, M_offset_13, 5, T[28]);
        d = GG(d, a, b, c, M_offset_2, 9, T[29]);
        c = GG(c, d, a, b, M_offset_7, 14, T[30]);
        b = GG(b, c, d, a, M_offset_12, 20, T[31]);
        a = HH(a, b, c, d, M_offset_5, 4, T[32]);
        d = HH(d, a, b, c, M_offset_8, 11, T[33]);
        c = HH(c, d, a, b, M_offset_11, 16, T[34]);
        b = HH(b, c, d, a, M_offset_14, 23, T[35]);
        a = HH(a, b, c, d, M_offset_1, 4, T[36]);
        d = HH(d, a, b, c, M_offset_4, 11, T[37]);
        c = HH(c, d, a, b, M_offset_7, 16, T[38]);
        b = HH(b, c, d, a, M_offset_10, 23, T[39]);
        a = HH(a, b, c, d, M_offset_13, 4, T[40]);
        d = HH(d, a, b, c, M_offset_0, 11, T[41]);
        c = HH(c, d, a, b, M_offset_3, 16, T[42]);
        b = HH(b, c, d, a, M_offset_6, 23, T[43]);
        a = HH(a, b, c, d, M_offset_9, 4, T[44]);
        d = HH(d, a, b, c, M_offset_12, 11, T[45]);
        c = HH(c, d, a, b, M_offset_15, 16, T[46]);
        b = HH(b, c, d, a, M_offset_2, 23, T[47]);
        a = II(a, b, c, d, M_offset_0, 6, T[48]);
        d = II(d, a, b, c, M_offset_7, 10, T[49]);
        c = II(c, d, a, b, M_offset_14, 15, T[50]);
        b = II(b, c, d, a, M_offset_5, 21, T[51]);
        a = II(a, b, c, d, M_offset_12, 6, T[52]);
        d = II(d, a, b, c, M_offset_3, 10, T[53]);
        c = II(c, d, a, b, M_offset_10, 15, T[54]);
        b = II(b, c, d, a, M_offset_1, 21, T[55]);
        a = II(a, b, c, d, M_offset_8, 6, T[56]);
        d = II(d, a, b, c, M_offset_15, 10, T[57]);
        c = II(c, d, a, b, M_offset_6, 15, T[58]);
        b = II(b, c, d, a, M_offset_13, 21, T[59]);
        a = II(a, b, c, d, M_offset_4, 6, T[60]);
        d = II(d, a, b, c, M_offset_11, 10, T[61]);
        c = II(c, d, a, b, M_offset_2, 15, T[62]);
        b = II(b, c, d, a, M_offset_9, 21, T[63]);

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        var nBitsTotalH = Math.floor(nBitsTotal / 0x100000000);
        var nBitsTotalL = nBitsTotal;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = (nBitsTotalH << 8 | nBitsTotalH >>> 24) & 0x00ff00ff | (nBitsTotalH << 24 | nBitsTotalH >>> 8) & 0xff00ff00;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = (nBitsTotalL << 8 | nBitsTotalL >>> 24) & 0x00ff00ff | (nBitsTotalL << 24 | nBitsTotalL >>> 8) & 0xff00ff00;
        data.sigBytes = (dataWords.length + 1) * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var hash = this._hash;
        var H = hash.words;

        // Swap endian
        for (var i = 0; i < 4; i++) {
          // Shortcut
          var H_i = H[i];
          H[i] = (H_i << 8 | H_i >>> 24) & 0x00ff00ff | (H_i << 24 | H_i >>> 8) & 0xff00ff00;
        }

        // Return final computed hash
        return hash;
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });
    function FF(a, b, c, d, x, s, t) {
      var n = a + (b & c | ~b & d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function GG(a, b, c, d, x, s, t) {
      var n = a + (b & d | c & ~d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function HH(a, b, c, d, x, s, t) {
      var n = a + (b ^ c ^ d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function II(a, b, c, d, x, s, t) {
      var n = a + (c ^ (b | ~d)) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.MD5('message');
     *     var hash = CryptoJS.MD5(wordArray);
     */
    C.MD5 = Hasher._createHelper(MD5);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacMD5(message, key);
     */
    C.HmacMD5 = Hasher._createHmacHelper(MD5);
  })(Math);
  return CryptoJS.MD5;
});
});

var sha1 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Reusable object
    var W = [];

    /**
     * SHA-1 hash algorithm.
     */
    var SHA1 = C_algo.SHA1 = Hasher.extend({
      _doReset: function () {
        this._hash = new WordArray.init([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0]);
      },
      _doProcessBlock: function (M, offset) {
        // Shortcut
        var H = this._hash.words;

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];
        var e = H[4];

        // Computation
        for (var i = 0; i < 80; i++) {
          if (i < 16) {
            W[i] = M[offset + i] | 0;
          } else {
            var n = W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16];
            W[i] = n << 1 | n >>> 31;
          }
          var t = (a << 5 | a >>> 27) + e + W[i];
          if (i < 20) {
            t += (b & c | ~b & d) + 0x5a827999;
          } else if (i < 40) {
            t += (b ^ c ^ d) + 0x6ed9eba1;
          } else if (i < 60) {
            t += (b & c | b & d | c & d) - 0x70e44324;
          } else /* if (i < 80) */{
              t += (b ^ c ^ d) - 0x359d3e2a;
            }
          e = d;
          d = c;
          c = b << 30 | b >>> 2;
          b = a;
          a = t;
        }

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
        H[4] = H[4] + e | 0;
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Return final computed hash
        return this._hash;
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA1('message');
     *     var hash = CryptoJS.SHA1(wordArray);
     */
    C.SHA1 = Hasher._createHelper(SHA1);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA1(message, key);
     */
    C.HmacSHA1 = Hasher._createHmacHelper(SHA1);
  })();
  return CryptoJS.SHA1;
});
});

var sha256 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Initialization and round constants tables
    var H = [];
    var K = [];

    // Compute constants
    (function () {
      function isPrime(n) {
        var sqrtN = Math.sqrt(n);
        for (var factor = 2; factor <= sqrtN; factor++) {
          if (!(n % factor)) {
            return false;
          }
        }
        return true;
      }
      function getFractionalBits(n) {
        return (n - (n | 0)) * 0x100000000 | 0;
      }
      var n = 2;
      var nPrime = 0;
      while (nPrime < 64) {
        if (isPrime(n)) {
          if (nPrime < 8) {
            H[nPrime] = getFractionalBits(Math.pow(n, 1 / 2));
          }
          K[nPrime] = getFractionalBits(Math.pow(n, 1 / 3));
          nPrime++;
        }
        n++;
      }
    })();

    // Reusable object
    var W = [];

    /**
     * SHA-256 hash algorithm.
     */
    var SHA256 = C_algo.SHA256 = Hasher.extend({
      _doReset: function () {
        this._hash = new WordArray.init(H.slice(0));
      },
      _doProcessBlock: function (M, offset) {
        // Shortcut
        var H = this._hash.words;

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];
        var e = H[4];
        var f = H[5];
        var g = H[6];
        var h = H[7];

        // Computation
        for (var i = 0; i < 64; i++) {
          if (i < 16) {
            W[i] = M[offset + i] | 0;
          } else {
            var gamma0x = W[i - 15];
            var gamma0 = (gamma0x << 25 | gamma0x >>> 7) ^ (gamma0x << 14 | gamma0x >>> 18) ^ gamma0x >>> 3;
            var gamma1x = W[i - 2];
            var gamma1 = (gamma1x << 15 | gamma1x >>> 17) ^ (gamma1x << 13 | gamma1x >>> 19) ^ gamma1x >>> 10;
            W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16];
          }
          var ch = e & f ^ ~e & g;
          var maj = a & b ^ a & c ^ b & c;
          var sigma0 = (a << 30 | a >>> 2) ^ (a << 19 | a >>> 13) ^ (a << 10 | a >>> 22);
          var sigma1 = (e << 26 | e >>> 6) ^ (e << 21 | e >>> 11) ^ (e << 7 | e >>> 25);
          var t1 = h + sigma1 + ch + K[i] + W[i];
          var t2 = sigma0 + maj;
          h = g;
          g = f;
          f = e;
          e = d + t1 | 0;
          d = c;
          c = b;
          b = a;
          a = t1 + t2 | 0;
        }

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
        H[4] = H[4] + e | 0;
        H[5] = H[5] + f | 0;
        H[6] = H[6] + g | 0;
        H[7] = H[7] + h | 0;
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Return final computed hash
        return this._hash;
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA256('message');
     *     var hash = CryptoJS.SHA256(wordArray);
     */
    C.SHA256 = Hasher._createHelper(SHA256);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA256(message, key);
     */
    C.HmacSHA256 = Hasher._createHmacHelper(SHA256);
  })(Math);
  return CryptoJS.SHA256;
});
});

var sha224 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, sha256);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var SHA256 = C_algo.SHA256;

    /**
     * SHA-224 hash algorithm.
     */
    var SHA224 = C_algo.SHA224 = SHA256.extend({
      _doReset: function () {
        this._hash = new WordArray.init([0xc1059ed8, 0x367cd507, 0x3070dd17, 0xf70e5939, 0xffc00b31, 0x68581511, 0x64f98fa7, 0xbefa4fa4]);
      },
      _doFinalize: function () {
        var hash = SHA256._doFinalize.call(this);
        hash.sigBytes -= 4;
        return hash;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA224('message');
     *     var hash = CryptoJS.SHA224(wordArray);
     */
    C.SHA224 = SHA256._createHelper(SHA224);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA224(message, key);
     */
    C.HmacSHA224 = SHA256._createHmacHelper(SHA224);
  })();
  return CryptoJS.SHA224;
});
});

var sha512 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, x64Core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Hasher = C_lib.Hasher;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var X64WordArray = C_x64.WordArray;
    var C_algo = C.algo;
    function X64Word_create() {
      return X64Word.create.apply(X64Word, arguments);
    }

    // Constants
    var K = [X64Word_create(0x428a2f98, 0xd728ae22), X64Word_create(0x71374491, 0x23ef65cd), X64Word_create(0xb5c0fbcf, 0xec4d3b2f), X64Word_create(0xe9b5dba5, 0x8189dbbc), X64Word_create(0x3956c25b, 0xf348b538), X64Word_create(0x59f111f1, 0xb605d019), X64Word_create(0x923f82a4, 0xaf194f9b), X64Word_create(0xab1c5ed5, 0xda6d8118), X64Word_create(0xd807aa98, 0xa3030242), X64Word_create(0x12835b01, 0x45706fbe), X64Word_create(0x243185be, 0x4ee4b28c), X64Word_create(0x550c7dc3, 0xd5ffb4e2), X64Word_create(0x72be5d74, 0xf27b896f), X64Word_create(0x80deb1fe, 0x3b1696b1), X64Word_create(0x9bdc06a7, 0x25c71235), X64Word_create(0xc19bf174, 0xcf692694), X64Word_create(0xe49b69c1, 0x9ef14ad2), X64Word_create(0xefbe4786, 0x384f25e3), X64Word_create(0x0fc19dc6, 0x8b8cd5b5), X64Word_create(0x240ca1cc, 0x77ac9c65), X64Word_create(0x2de92c6f, 0x592b0275), X64Word_create(0x4a7484aa, 0x6ea6e483), X64Word_create(0x5cb0a9dc, 0xbd41fbd4), X64Word_create(0x76f988da, 0x831153b5), X64Word_create(0x983e5152, 0xee66dfab), X64Word_create(0xa831c66d, 0x2db43210), X64Word_create(0xb00327c8, 0x98fb213f), X64Word_create(0xbf597fc7, 0xbeef0ee4), X64Word_create(0xc6e00bf3, 0x3da88fc2), X64Word_create(0xd5a79147, 0x930aa725), X64Word_create(0x06ca6351, 0xe003826f), X64Word_create(0x14292967, 0x0a0e6e70), X64Word_create(0x27b70a85, 0x46d22ffc), X64Word_create(0x2e1b2138, 0x5c26c926), X64Word_create(0x4d2c6dfc, 0x5ac42aed), X64Word_create(0x53380d13, 0x9d95b3df), X64Word_create(0x650a7354, 0x8baf63de), X64Word_create(0x766a0abb, 0x3c77b2a8), X64Word_create(0x81c2c92e, 0x47edaee6), X64Word_create(0x92722c85, 0x1482353b), X64Word_create(0xa2bfe8a1, 0x4cf10364), X64Word_create(0xa81a664b, 0xbc423001), X64Word_create(0xc24b8b70, 0xd0f89791), X64Word_create(0xc76c51a3, 0x0654be30), X64Word_create(0xd192e819, 0xd6ef5218), X64Word_create(0xd6990624, 0x5565a910), X64Word_create(0xf40e3585, 0x5771202a), X64Word_create(0x106aa070, 0x32bbd1b8), X64Word_create(0x19a4c116, 0xb8d2d0c8), X64Word_create(0x1e376c08, 0x5141ab53), X64Word_create(0x2748774c, 0xdf8eeb99), X64Word_create(0x34b0bcb5, 0xe19b48a8), X64Word_create(0x391c0cb3, 0xc5c95a63), X64Word_create(0x4ed8aa4a, 0xe3418acb), X64Word_create(0x5b9cca4f, 0x7763e373), X64Word_create(0x682e6ff3, 0xd6b2b8a3), X64Word_create(0x748f82ee, 0x5defb2fc), X64Word_create(0x78a5636f, 0x43172f60), X64Word_create(0x84c87814, 0xa1f0ab72), X64Word_create(0x8cc70208, 0x1a6439ec), X64Word_create(0x90befffa, 0x23631e28), X64Word_create(0xa4506ceb, 0xde82bde9), X64Word_create(0xbef9a3f7, 0xb2c67915), X64Word_create(0xc67178f2, 0xe372532b), X64Word_create(0xca273ece, 0xea26619c), X64Word_create(0xd186b8c7, 0x21c0c207), X64Word_create(0xeada7dd6, 0xcde0eb1e), X64Word_create(0xf57d4f7f, 0xee6ed178), X64Word_create(0x06f067aa, 0x72176fba), X64Word_create(0x0a637dc5, 0xa2c898a6), X64Word_create(0x113f9804, 0xbef90dae), X64Word_create(0x1b710b35, 0x131c471b), X64Word_create(0x28db77f5, 0x23047d84), X64Word_create(0x32caab7b, 0x40c72493), X64Word_create(0x3c9ebe0a, 0x15c9bebc), X64Word_create(0x431d67c4, 0x9c100d4c), X64Word_create(0x4cc5d4be, 0xcb3e42b6), X64Word_create(0x597f299c, 0xfc657e2a), X64Word_create(0x5fcb6fab, 0x3ad6faec), X64Word_create(0x6c44198c, 0x4a475817)];

    // Reusable objects
    var W = [];
    (function () {
      for (var i = 0; i < 80; i++) {
        W[i] = X64Word_create();
      }
    })();

    /**
     * SHA-512 hash algorithm.
     */
    var SHA512 = C_algo.SHA512 = Hasher.extend({
      _doReset: function () {
        this._hash = new X64WordArray.init([new X64Word.init(0x6a09e667, 0xf3bcc908), new X64Word.init(0xbb67ae85, 0x84caa73b), new X64Word.init(0x3c6ef372, 0xfe94f82b), new X64Word.init(0xa54ff53a, 0x5f1d36f1), new X64Word.init(0x510e527f, 0xade682d1), new X64Word.init(0x9b05688c, 0x2b3e6c1f), new X64Word.init(0x1f83d9ab, 0xfb41bd6b), new X64Word.init(0x5be0cd19, 0x137e2179)]);
      },
      _doProcessBlock: function (M, offset) {
        // Shortcuts
        var H = this._hash.words;
        var H0 = H[0];
        var H1 = H[1];
        var H2 = H[2];
        var H3 = H[3];
        var H4 = H[4];
        var H5 = H[5];
        var H6 = H[6];
        var H7 = H[7];
        var H0h = H0.high;
        var H0l = H0.low;
        var H1h = H1.high;
        var H1l = H1.low;
        var H2h = H2.high;
        var H2l = H2.low;
        var H3h = H3.high;
        var H3l = H3.low;
        var H4h = H4.high;
        var H4l = H4.low;
        var H5h = H5.high;
        var H5l = H5.low;
        var H6h = H6.high;
        var H6l = H6.low;
        var H7h = H7.high;
        var H7l = H7.low;

        // Working variables
        var ah = H0h;
        var al = H0l;
        var bh = H1h;
        var bl = H1l;
        var ch = H2h;
        var cl = H2l;
        var dh = H3h;
        var dl = H3l;
        var eh = H4h;
        var el = H4l;
        var fh = H5h;
        var fl = H5l;
        var gh = H6h;
        var gl = H6l;
        var hh = H7h;
        var hl = H7l;

        // Rounds
        for (var i = 0; i < 80; i++) {
          var Wil;
          var Wih;

          // Shortcut
          var Wi = W[i];

          // Extend message
          if (i < 16) {
            Wih = Wi.high = M[offset + i * 2] | 0;
            Wil = Wi.low = M[offset + i * 2 + 1] | 0;
          } else {
            // Gamma0
            var gamma0x = W[i - 15];
            var gamma0xh = gamma0x.high;
            var gamma0xl = gamma0x.low;
            var gamma0h = (gamma0xh >>> 1 | gamma0xl << 31) ^ (gamma0xh >>> 8 | gamma0xl << 24) ^ gamma0xh >>> 7;
            var gamma0l = (gamma0xl >>> 1 | gamma0xh << 31) ^ (gamma0xl >>> 8 | gamma0xh << 24) ^ (gamma0xl >>> 7 | gamma0xh << 25);

            // Gamma1
            var gamma1x = W[i - 2];
            var gamma1xh = gamma1x.high;
            var gamma1xl = gamma1x.low;
            var gamma1h = (gamma1xh >>> 19 | gamma1xl << 13) ^ (gamma1xh << 3 | gamma1xl >>> 29) ^ gamma1xh >>> 6;
            var gamma1l = (gamma1xl >>> 19 | gamma1xh << 13) ^ (gamma1xl << 3 | gamma1xh >>> 29) ^ (gamma1xl >>> 6 | gamma1xh << 26);

            // W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16]
            var Wi7 = W[i - 7];
            var Wi7h = Wi7.high;
            var Wi7l = Wi7.low;
            var Wi16 = W[i - 16];
            var Wi16h = Wi16.high;
            var Wi16l = Wi16.low;
            Wil = gamma0l + Wi7l;
            Wih = gamma0h + Wi7h + (Wil >>> 0 < gamma0l >>> 0 ? 1 : 0);
            Wil = Wil + gamma1l;
            Wih = Wih + gamma1h + (Wil >>> 0 < gamma1l >>> 0 ? 1 : 0);
            Wil = Wil + Wi16l;
            Wih = Wih + Wi16h + (Wil >>> 0 < Wi16l >>> 0 ? 1 : 0);
            Wi.high = Wih;
            Wi.low = Wil;
          }
          var chh = eh & fh ^ ~eh & gh;
          var chl = el & fl ^ ~el & gl;
          var majh = ah & bh ^ ah & ch ^ bh & ch;
          var majl = al & bl ^ al & cl ^ bl & cl;
          var sigma0h = (ah >>> 28 | al << 4) ^ (ah << 30 | al >>> 2) ^ (ah << 25 | al >>> 7);
          var sigma0l = (al >>> 28 | ah << 4) ^ (al << 30 | ah >>> 2) ^ (al << 25 | ah >>> 7);
          var sigma1h = (eh >>> 14 | el << 18) ^ (eh >>> 18 | el << 14) ^ (eh << 23 | el >>> 9);
          var sigma1l = (el >>> 14 | eh << 18) ^ (el >>> 18 | eh << 14) ^ (el << 23 | eh >>> 9);

          // t1 = h + sigma1 + ch + K[i] + W[i]
          var Ki = K[i];
          var Kih = Ki.high;
          var Kil = Ki.low;
          var t1l = hl + sigma1l;
          var t1h = hh + sigma1h + (t1l >>> 0 < hl >>> 0 ? 1 : 0);
          var t1l = t1l + chl;
          var t1h = t1h + chh + (t1l >>> 0 < chl >>> 0 ? 1 : 0);
          var t1l = t1l + Kil;
          var t1h = t1h + Kih + (t1l >>> 0 < Kil >>> 0 ? 1 : 0);
          var t1l = t1l + Wil;
          var t1h = t1h + Wih + (t1l >>> 0 < Wil >>> 0 ? 1 : 0);

          // t2 = sigma0 + maj
          var t2l = sigma0l + majl;
          var t2h = sigma0h + majh + (t2l >>> 0 < sigma0l >>> 0 ? 1 : 0);

          // Update working variables
          hh = gh;
          hl = gl;
          gh = fh;
          gl = fl;
          fh = eh;
          fl = el;
          el = dl + t1l | 0;
          eh = dh + t1h + (el >>> 0 < dl >>> 0 ? 1 : 0) | 0;
          dh = ch;
          dl = cl;
          ch = bh;
          cl = bl;
          bh = ah;
          bl = al;
          al = t1l + t2l | 0;
          ah = t1h + t2h + (al >>> 0 < t1l >>> 0 ? 1 : 0) | 0;
        }

        // Intermediate hash value
        H0l = H0.low = H0l + al;
        H0.high = H0h + ah + (H0l >>> 0 < al >>> 0 ? 1 : 0);
        H1l = H1.low = H1l + bl;
        H1.high = H1h + bh + (H1l >>> 0 < bl >>> 0 ? 1 : 0);
        H2l = H2.low = H2l + cl;
        H2.high = H2h + ch + (H2l >>> 0 < cl >>> 0 ? 1 : 0);
        H3l = H3.low = H3l + dl;
        H3.high = H3h + dh + (H3l >>> 0 < dl >>> 0 ? 1 : 0);
        H4l = H4.low = H4l + el;
        H4.high = H4h + eh + (H4l >>> 0 < el >>> 0 ? 1 : 0);
        H5l = H5.low = H5l + fl;
        H5.high = H5h + fh + (H5l >>> 0 < fl >>> 0 ? 1 : 0);
        H6l = H6.low = H6l + gl;
        H6.high = H6h + gh + (H6l >>> 0 < gl >>> 0 ? 1 : 0);
        H7l = H7.low = H7l + hl;
        H7.high = H7h + hh + (H7l >>> 0 < hl >>> 0 ? 1 : 0);
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 128 >>> 10 << 5) + 30] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 128 >>> 10 << 5) + 31] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Convert hash to 32-bit word array before returning
        var hash = this._hash.toX32();

        // Return final computed hash
        return hash;
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      },
      blockSize: 1024 / 32
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA512('message');
     *     var hash = CryptoJS.SHA512(wordArray);
     */
    C.SHA512 = Hasher._createHelper(SHA512);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA512(message, key);
     */
    C.HmacSHA512 = Hasher._createHmacHelper(SHA512);
  })();
  return CryptoJS.SHA512;
});
});

var sha384 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, x64Core, sha512);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var X64WordArray = C_x64.WordArray;
    var C_algo = C.algo;
    var SHA512 = C_algo.SHA512;

    /**
     * SHA-384 hash algorithm.
     */
    var SHA384 = C_algo.SHA384 = SHA512.extend({
      _doReset: function () {
        this._hash = new X64WordArray.init([new X64Word.init(0xcbbb9d5d, 0xc1059ed8), new X64Word.init(0x629a292a, 0x367cd507), new X64Word.init(0x9159015a, 0x3070dd17), new X64Word.init(0x152fecd8, 0xf70e5939), new X64Word.init(0x67332667, 0xffc00b31), new X64Word.init(0x8eb44a87, 0x68581511), new X64Word.init(0xdb0c2e0d, 0x64f98fa7), new X64Word.init(0x47b5481d, 0xbefa4fa4)]);
      },
      _doFinalize: function () {
        var hash = SHA512._doFinalize.call(this);
        hash.sigBytes -= 16;
        return hash;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA384('message');
     *     var hash = CryptoJS.SHA384(wordArray);
     */
    C.SHA384 = SHA512._createHelper(SHA384);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA384(message, key);
     */
    C.HmacSHA384 = SHA512._createHmacHelper(SHA384);
  })();
  return CryptoJS.SHA384;
});
});

var sha3 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, x64Core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var C_algo = C.algo;

    // Constants tables
    var RHO_OFFSETS = [];
    var PI_INDEXES = [];
    var ROUND_CONSTANTS = [];

    // Compute Constants
    (function () {
      // Compute rho offset constants
      var x = 1,
        y = 0;
      for (var t = 0; t < 24; t++) {
        RHO_OFFSETS[x + 5 * y] = (t + 1) * (t + 2) / 2 % 64;
        var newX = y % 5;
        var newY = (2 * x + 3 * y) % 5;
        x = newX;
        y = newY;
      }

      // Compute pi index constants
      for (var x = 0; x < 5; x++) {
        for (var y = 0; y < 5; y++) {
          PI_INDEXES[x + 5 * y] = y + (2 * x + 3 * y) % 5 * 5;
        }
      }

      // Compute round constants
      var LFSR = 0x01;
      for (var i = 0; i < 24; i++) {
        var roundConstantMsw = 0;
        var roundConstantLsw = 0;
        for (var j = 0; j < 7; j++) {
          if (LFSR & 0x01) {
            var bitPosition = (1 << j) - 1;
            if (bitPosition < 32) {
              roundConstantLsw ^= 1 << bitPosition;
            } else /* if (bitPosition >= 32) */{
                roundConstantMsw ^= 1 << bitPosition - 32;
              }
          }

          // Compute next LFSR
          if (LFSR & 0x80) {
            // Primitive polynomial over GF(2): x^8 + x^6 + x^5 + x^4 + 1
            LFSR = LFSR << 1 ^ 0x71;
          } else {
            LFSR <<= 1;
          }
        }
        ROUND_CONSTANTS[i] = X64Word.create(roundConstantMsw, roundConstantLsw);
      }
    })();

    // Reusable objects for temporary values
    var T = [];
    (function () {
      for (var i = 0; i < 25; i++) {
        T[i] = X64Word.create();
      }
    })();

    /**
     * SHA-3 hash algorithm.
     */
    var SHA3 = C_algo.SHA3 = Hasher.extend({
      /**
       * Configuration options.
       *
       * @property {number} outputLength
       *   The desired number of bits in the output hash.
       *   Only values permitted are: 224, 256, 384, 512.
       *   Default: 512
       */
      cfg: Hasher.cfg.extend({
        outputLength: 512
      }),
      _doReset: function () {
        var state = this._state = [];
        for (var i = 0; i < 25; i++) {
          state[i] = new X64Word.init();
        }
        this.blockSize = (1600 - 2 * this.cfg.outputLength) / 32;
      },
      _doProcessBlock: function (M, offset) {
        // Shortcuts
        var state = this._state;
        var nBlockSizeLanes = this.blockSize / 2;

        // Absorb
        for (var i = 0; i < nBlockSizeLanes; i++) {
          // Shortcuts
          var M2i = M[offset + 2 * i];
          var M2i1 = M[offset + 2 * i + 1];

          // Swap endian
          M2i = (M2i << 8 | M2i >>> 24) & 0x00ff00ff | (M2i << 24 | M2i >>> 8) & 0xff00ff00;
          M2i1 = (M2i1 << 8 | M2i1 >>> 24) & 0x00ff00ff | (M2i1 << 24 | M2i1 >>> 8) & 0xff00ff00;

          // Absorb message into state
          var lane = state[i];
          lane.high ^= M2i1;
          lane.low ^= M2i;
        }

        // Rounds
        for (var round = 0; round < 24; round++) {
          // Theta
          for (var x = 0; x < 5; x++) {
            // Mix column lanes
            var tMsw = 0,
              tLsw = 0;
            for (var y = 0; y < 5; y++) {
              var lane = state[x + 5 * y];
              tMsw ^= lane.high;
              tLsw ^= lane.low;
            }

            // Temporary values
            var Tx = T[x];
            Tx.high = tMsw;
            Tx.low = tLsw;
          }
          for (var x = 0; x < 5; x++) {
            // Shortcuts
            var Tx4 = T[(x + 4) % 5];
            var Tx1 = T[(x + 1) % 5];
            var Tx1Msw = Tx1.high;
            var Tx1Lsw = Tx1.low;

            // Mix surrounding columns
            var tMsw = Tx4.high ^ (Tx1Msw << 1 | Tx1Lsw >>> 31);
            var tLsw = Tx4.low ^ (Tx1Lsw << 1 | Tx1Msw >>> 31);
            for (var y = 0; y < 5; y++) {
              var lane = state[x + 5 * y];
              lane.high ^= tMsw;
              lane.low ^= tLsw;
            }
          }

          // Rho Pi
          for (var laneIndex = 1; laneIndex < 25; laneIndex++) {
            var tMsw;
            var tLsw;

            // Shortcuts
            var lane = state[laneIndex];
            var laneMsw = lane.high;
            var laneLsw = lane.low;
            var rhoOffset = RHO_OFFSETS[laneIndex];

            // Rotate lanes
            if (rhoOffset < 32) {
              tMsw = laneMsw << rhoOffset | laneLsw >>> 32 - rhoOffset;
              tLsw = laneLsw << rhoOffset | laneMsw >>> 32 - rhoOffset;
            } else /* if (rhoOffset >= 32) */{
                tMsw = laneLsw << rhoOffset - 32 | laneMsw >>> 64 - rhoOffset;
                tLsw = laneMsw << rhoOffset - 32 | laneLsw >>> 64 - rhoOffset;
              }

            // Transpose lanes
            var TPiLane = T[PI_INDEXES[laneIndex]];
            TPiLane.high = tMsw;
            TPiLane.low = tLsw;
          }

          // Rho pi at x = y = 0
          var T0 = T[0];
          var state0 = state[0];
          T0.high = state0.high;
          T0.low = state0.low;

          // Chi
          for (var x = 0; x < 5; x++) {
            for (var y = 0; y < 5; y++) {
              // Shortcuts
              var laneIndex = x + 5 * y;
              var lane = state[laneIndex];
              var TLane = T[laneIndex];
              var Tx1Lane = T[(x + 1) % 5 + 5 * y];
              var Tx2Lane = T[(x + 2) % 5 + 5 * y];

              // Mix rows
              lane.high = TLane.high ^ ~Tx1Lane.high & Tx2Lane.high;
              lane.low = TLane.low ^ ~Tx1Lane.low & Tx2Lane.low;
            }
          }

          // Iota
          var lane = state[0];
          var roundConstant = ROUND_CONSTANTS[round];
          lane.high ^= roundConstant.high;
          lane.low ^= roundConstant.low;
        }
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;
        var blockSizeBits = this.blockSize * 32;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x1 << 24 - nBitsLeft % 32;
        dataWords[(Math.ceil((nBitsLeft + 1) / blockSizeBits) * blockSizeBits >>> 5) - 1] |= 0x80;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var state = this._state;
        var outputLengthBytes = this.cfg.outputLength / 8;
        var outputLengthLanes = outputLengthBytes / 8;

        // Squeeze
        var hashWords = [];
        for (var i = 0; i < outputLengthLanes; i++) {
          // Shortcuts
          var lane = state[i];
          var laneMsw = lane.high;
          var laneLsw = lane.low;

          // Swap endian
          laneMsw = (laneMsw << 8 | laneMsw >>> 24) & 0x00ff00ff | (laneMsw << 24 | laneMsw >>> 8) & 0xff00ff00;
          laneLsw = (laneLsw << 8 | laneLsw >>> 24) & 0x00ff00ff | (laneLsw << 24 | laneLsw >>> 8) & 0xff00ff00;

          // Squeeze state to retrieve hash
          hashWords.push(laneLsw);
          hashWords.push(laneMsw);
        }

        // Return final computed hash
        return new WordArray.init(hashWords, outputLengthBytes);
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        var state = clone._state = this._state.slice(0);
        for (var i = 0; i < 25; i++) {
          state[i] = state[i].clone();
        }
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA3('message');
     *     var hash = CryptoJS.SHA3(wordArray);
     */
    C.SHA3 = Hasher._createHelper(SHA3);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA3(message, key);
     */
    C.HmacSHA3 = Hasher._createHmacHelper(SHA3);
  })(Math);
  return CryptoJS.SHA3;
});
});

var ripemd160 = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  /** @preserve
  (c) 2012 by Cédric Mesnil. All rights reserved.
  	Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
  	    - Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
      - Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
  	THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
  */

  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Constants table
    var _zl = WordArray.create([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 7, 4, 13, 1, 10, 6, 15, 3, 12, 0, 9, 5, 2, 14, 11, 8, 3, 10, 14, 4, 9, 15, 8, 1, 2, 7, 0, 6, 13, 11, 5, 12, 1, 9, 11, 10, 0, 8, 12, 4, 13, 3, 7, 15, 14, 5, 6, 2, 4, 0, 5, 9, 7, 12, 2, 10, 14, 1, 3, 8, 11, 6, 15, 13]);
    var _zr = WordArray.create([5, 14, 7, 0, 9, 2, 11, 4, 13, 6, 15, 8, 1, 10, 3, 12, 6, 11, 3, 7, 0, 13, 5, 10, 14, 15, 8, 12, 4, 9, 1, 2, 15, 5, 1, 3, 7, 14, 6, 9, 11, 8, 12, 2, 10, 0, 4, 13, 8, 6, 4, 1, 3, 11, 15, 0, 5, 12, 2, 13, 9, 7, 10, 14, 12, 15, 10, 4, 1, 5, 8, 7, 6, 2, 13, 14, 0, 3, 9, 11]);
    var _sl = WordArray.create([11, 14, 15, 12, 5, 8, 7, 9, 11, 13, 14, 15, 6, 7, 9, 8, 7, 6, 8, 13, 11, 9, 7, 15, 7, 12, 15, 9, 11, 7, 13, 12, 11, 13, 6, 7, 14, 9, 13, 15, 14, 8, 13, 6, 5, 12, 7, 5, 11, 12, 14, 15, 14, 15, 9, 8, 9, 14, 5, 6, 8, 6, 5, 12, 9, 15, 5, 11, 6, 8, 13, 12, 5, 12, 13, 14, 11, 8, 5, 6]);
    var _sr = WordArray.create([8, 9, 9, 11, 13, 15, 15, 5, 7, 7, 8, 11, 14, 14, 12, 6, 9, 13, 15, 7, 12, 8, 9, 11, 7, 7, 12, 7, 6, 15, 13, 11, 9, 7, 15, 11, 8, 6, 6, 14, 12, 13, 5, 14, 13, 13, 7, 5, 15, 5, 8, 11, 14, 14, 6, 14, 6, 9, 12, 9, 12, 5, 15, 8, 8, 5, 12, 9, 12, 5, 14, 6, 8, 13, 6, 5, 15, 13, 11, 11]);
    var _hl = WordArray.create([0x00000000, 0x5A827999, 0x6ED9EBA1, 0x8F1BBCDC, 0xA953FD4E]);
    var _hr = WordArray.create([0x50A28BE6, 0x5C4DD124, 0x6D703EF3, 0x7A6D76E9, 0x00000000]);

    /**
     * RIPEMD160 hash algorithm.
     */
    var RIPEMD160 = C_algo.RIPEMD160 = Hasher.extend({
      _doReset: function () {
        this._hash = WordArray.create([0x67452301, 0xEFCDAB89, 0x98BADCFE, 0x10325476, 0xC3D2E1F0]);
      },
      _doProcessBlock: function (M, offset) {
        // Swap endian
        for (var i = 0; i < 16; i++) {
          // Shortcuts
          var offset_i = offset + i;
          var M_offset_i = M[offset_i];

          // Swap
          M[offset_i] = (M_offset_i << 8 | M_offset_i >>> 24) & 0x00ff00ff | (M_offset_i << 24 | M_offset_i >>> 8) & 0xff00ff00;
        }
        // Shortcut
        var H = this._hash.words;
        var hl = _hl.words;
        var hr = _hr.words;
        var zl = _zl.words;
        var zr = _zr.words;
        var sl = _sl.words;
        var sr = _sr.words;

        // Working variables
        var al, bl, cl, dl, el;
        var ar, br, cr, dr, er;
        ar = al = H[0];
        br = bl = H[1];
        cr = cl = H[2];
        dr = dl = H[3];
        er = el = H[4];
        // Computation
        var t;
        for (var i = 0; i < 80; i += 1) {
          t = al + M[offset + zl[i]] | 0;
          if (i < 16) {
            t += f1(bl, cl, dl) + hl[0];
          } else if (i < 32) {
            t += f2(bl, cl, dl) + hl[1];
          } else if (i < 48) {
            t += f3(bl, cl, dl) + hl[2];
          } else if (i < 64) {
            t += f4(bl, cl, dl) + hl[3];
          } else {
            // if (i<80) {
            t += f5(bl, cl, dl) + hl[4];
          }
          t = t | 0;
          t = rotl(t, sl[i]);
          t = t + el | 0;
          al = el;
          el = dl;
          dl = rotl(cl, 10);
          cl = bl;
          bl = t;
          t = ar + M[offset + zr[i]] | 0;
          if (i < 16) {
            t += f5(br, cr, dr) + hr[0];
          } else if (i < 32) {
            t += f4(br, cr, dr) + hr[1];
          } else if (i < 48) {
            t += f3(br, cr, dr) + hr[2];
          } else if (i < 64) {
            t += f2(br, cr, dr) + hr[3];
          } else {
            // if (i<80) {
            t += f1(br, cr, dr) + hr[4];
          }
          t = t | 0;
          t = rotl(t, sr[i]);
          t = t + er | 0;
          ar = er;
          er = dr;
          dr = rotl(cr, 10);
          cr = br;
          br = t;
        }
        // Intermediate hash value
        t = H[1] + cl + dr | 0;
        H[1] = H[2] + dl + er | 0;
        H[2] = H[3] + el + ar | 0;
        H[3] = H[4] + al + br | 0;
        H[4] = H[0] + bl + cr | 0;
        H[0] = t;
      },
      _doFinalize: function () {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = (nBitsTotal << 8 | nBitsTotal >>> 24) & 0x00ff00ff | (nBitsTotal << 24 | nBitsTotal >>> 8) & 0xff00ff00;
        data.sigBytes = (dataWords.length + 1) * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var hash = this._hash;
        var H = hash.words;

        // Swap endian
        for (var i = 0; i < 5; i++) {
          // Shortcut
          var H_i = H[i];

          // Swap
          H[i] = (H_i << 8 | H_i >>> 24) & 0x00ff00ff | (H_i << 24 | H_i >>> 8) & 0xff00ff00;
        }

        // Return final computed hash
        return hash;
      },
      clone: function () {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });
    function f1(x, y, z) {
      return x ^ y ^ z;
    }
    function f2(x, y, z) {
      return x & y | ~x & z;
    }
    function f3(x, y, z) {
      return (x | ~y) ^ z;
    }
    function f4(x, y, z) {
      return x & z | y & ~z;
    }
    function f5(x, y, z) {
      return x ^ (y | ~z);
    }
    function rotl(x, n) {
      return x << n | x >>> 32 - n;
    }

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.RIPEMD160('message');
     *     var hash = CryptoJS.RIPEMD160(wordArray);
     */
    C.RIPEMD160 = Hasher._createHelper(RIPEMD160);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacRIPEMD160(message, key);
     */
    C.HmacRIPEMD160 = Hasher._createHmacHelper(RIPEMD160);
  })();
  return CryptoJS.RIPEMD160;
});
});

var hmac = createCommonjsModule(function (module, exports) {
(function (root, factory) {
  {
    // CommonJS
    module.exports = factory(core);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var C_enc = C.enc;
    var Utf8 = C_enc.Utf8;
    var C_algo = C.algo;

    /**
     * HMAC algorithm.
     */
    C_algo.HMAC = Base.extend({
      /**
       * Initializes a newly created HMAC.
       *
       * @param {Hasher} hasher The hash algorithm to use.
       * @param {WordArray|string} key The secret key.
       *
       * @example
       *
       *     var hmacHasher = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA256, key);
       */
      init: function (hasher, key) {
        // Init hasher
        hasher = this._hasher = new hasher.init();

        // Convert string to WordArray, else assume WordArray already
        if (typeof key == 'string') {
          key = Utf8.parse(key);
        }

        // Shortcuts
        var hasherBlockSize = hasher.blockSize;
        var hasherBlockSizeBytes = hasherBlockSize * 4;

        // Allow arbitrary length keys
        if (key.sigBytes > hasherBlockSizeBytes) {
          key = hasher.finalize(key);
        }

        // Clamp excess bits
        key.clamp();

        // Clone key for inner and outer pads
        var oKey = this._oKey = key.clone();
        var iKey = this._iKey = key.clone();

        // Shortcuts
        var oKeyWords = oKey.words;
        var iKeyWords = iKey.words;

        // XOR keys with pad constants
        for (var i = 0; i < hasherBlockSize; i++) {
          oKeyWords[i] ^= 0x5c5c5c5c;
          iKeyWords[i] ^= 0x36363636;
        }
        oKey.sigBytes = iKey.sigBytes = hasherBlockSizeBytes;

        // Set initial values
        this.reset();
      },
      /**
       * Resets this HMAC to its initial state.
       *
       * @example
       *
       *     hmacHasher.reset();
       */
      reset: function () {
        // Shortcut
        var hasher = this._hasher;

        // Reset
        hasher.reset();
        hasher.update(this._iKey);
      },
      /**
       * Updates this HMAC with a message.
       *
       * @param {WordArray|string} messageUpdate The message to append.
       *
       * @return {HMAC} This HMAC instance.
       *
       * @example
       *
       *     hmacHasher.update('message');
       *     hmacHasher.update(wordArray);
       */
      update: function (messageUpdate) {
        this._hasher.update(messageUpdate);

        // Chainable
        return this;
      },
      /**
       * Finalizes the HMAC computation.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} messageUpdate (Optional) A final message update.
       *
       * @return {WordArray} The HMAC.
       *
       * @example
       *
       *     var hmac = hmacHasher.finalize();
       *     var hmac = hmacHasher.finalize('message');
       *     var hmac = hmacHasher.finalize(wordArray);
       */
      finalize: function (messageUpdate) {
        // Shortcut
        var hasher = this._hasher;

        // Compute HMAC
        var innerHash = hasher.finalize(messageUpdate);
        hasher.reset();
        var hmac = hasher.finalize(this._oKey.clone().concat(innerHash));
        return hmac;
      }
    });
  })();
});
});

var pbkdf2 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, sha256, hmac);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var SHA256 = C_algo.SHA256;
    var HMAC = C_algo.HMAC;

    /**
     * Password-Based Key Derivation Function 2 algorithm.
     */
    var PBKDF2 = C_algo.PBKDF2 = Base.extend({
      /**
       * Configuration options.
       *
       * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
       * @property {Hasher} hasher The hasher to use. Default: SHA256
       * @property {number} iterations The number of iterations to perform. Default: 250000
       */
      cfg: Base.extend({
        keySize: 128 / 32,
        hasher: SHA256,
        iterations: 250000
      }),
      /**
       * Initializes a newly created key derivation function.
       *
       * @param {Object} cfg (Optional) The configuration options to use for the derivation.
       *
       * @example
       *
       *     var kdf = CryptoJS.algo.PBKDF2.create();
       *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8 });
       *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8, iterations: 1000 });
       */
      init: function (cfg) {
        this.cfg = this.cfg.extend(cfg);
      },
      /**
       * Computes the Password-Based Key Derivation Function 2.
       *
       * @param {WordArray|string} password The password.
       * @param {WordArray|string} salt A salt.
       *
       * @return {WordArray} The derived key.
       *
       * @example
       *
       *     var key = kdf.compute(password, salt);
       */
      compute: function (password, salt) {
        // Shortcut
        var cfg = this.cfg;

        // Init HMAC
        var hmac = HMAC.create(cfg.hasher, password);

        // Initial values
        var derivedKey = WordArray.create();
        var blockIndex = WordArray.create([0x00000001]);

        // Shortcuts
        var derivedKeyWords = derivedKey.words;
        var blockIndexWords = blockIndex.words;
        var keySize = cfg.keySize;
        var iterations = cfg.iterations;

        // Generate key
        while (derivedKeyWords.length < keySize) {
          var block = hmac.update(salt).finalize(blockIndex);
          hmac.reset();

          // Shortcuts
          var blockWords = block.words;
          var blockWordsLength = blockWords.length;

          // Iterations
          var intermediate = block;
          for (var i = 1; i < iterations; i++) {
            intermediate = hmac.finalize(intermediate);
            hmac.reset();

            // Shortcut
            var intermediateWords = intermediate.words;

            // XOR intermediate with block
            for (var j = 0; j < blockWordsLength; j++) {
              blockWords[j] ^= intermediateWords[j];
            }
          }
          derivedKey.concat(block);
          blockIndexWords[0]++;
        }
        derivedKey.sigBytes = keySize * 4;
        return derivedKey;
      }
    });

    /**
     * Computes the Password-Based Key Derivation Function 2.
     *
     * @param {WordArray|string} password The password.
     * @param {WordArray|string} salt A salt.
     * @param {Object} cfg (Optional) The configuration options to use for this computation.
     *
     * @return {WordArray} The derived key.
     *
     * @static
     *
     * @example
     *
     *     var key = CryptoJS.PBKDF2(password, salt);
     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8 });
     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8, iterations: 1000 });
     */
    C.PBKDF2 = function (password, salt, cfg) {
      return PBKDF2.create(cfg).compute(password, salt);
    };
  })();
  return CryptoJS.PBKDF2;
});
});

var evpkdf = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, sha1, hmac);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var MD5 = C_algo.MD5;

    /**
     * This key derivation function is meant to conform with EVP_BytesToKey.
     * www.openssl.org/docs/crypto/EVP_BytesToKey.html
     */
    var EvpKDF = C_algo.EvpKDF = Base.extend({
      /**
       * Configuration options.
       *
       * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
       * @property {Hasher} hasher The hash algorithm to use. Default: MD5
       * @property {number} iterations The number of iterations to perform. Default: 1
       */
      cfg: Base.extend({
        keySize: 128 / 32,
        hasher: MD5,
        iterations: 1
      }),
      /**
       * Initializes a newly created key derivation function.
       *
       * @param {Object} cfg (Optional) The configuration options to use for the derivation.
       *
       * @example
       *
       *     var kdf = CryptoJS.algo.EvpKDF.create();
       *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8 });
       *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8, iterations: 1000 });
       */
      init: function (cfg) {
        this.cfg = this.cfg.extend(cfg);
      },
      /**
       * Derives a key from a password.
       *
       * @param {WordArray|string} password The password.
       * @param {WordArray|string} salt A salt.
       *
       * @return {WordArray} The derived key.
       *
       * @example
       *
       *     var key = kdf.compute(password, salt);
       */
      compute: function (password, salt) {
        var block;

        // Shortcut
        var cfg = this.cfg;

        // Init hasher
        var hasher = cfg.hasher.create();

        // Initial values
        var derivedKey = WordArray.create();

        // Shortcuts
        var derivedKeyWords = derivedKey.words;
        var keySize = cfg.keySize;
        var iterations = cfg.iterations;

        // Generate key
        while (derivedKeyWords.length < keySize) {
          if (block) {
            hasher.update(block);
          }
          block = hasher.update(password).finalize(salt);
          hasher.reset();

          // Iterations
          for (var i = 1; i < iterations; i++) {
            block = hasher.finalize(block);
            hasher.reset();
          }
          derivedKey.concat(block);
        }
        derivedKey.sigBytes = keySize * 4;
        return derivedKey;
      }
    });

    /**
     * Derives a key from a password.
     *
     * @param {WordArray|string} password The password.
     * @param {WordArray|string} salt A salt.
     * @param {Object} cfg (Optional) The configuration options to use for this computation.
     *
     * @return {WordArray} The derived key.
     *
     * @static
     *
     * @example
     *
     *     var key = CryptoJS.EvpKDF(password, salt);
     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8 });
     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8, iterations: 1000 });
     */
    C.EvpKDF = function (password, salt, cfg) {
      return EvpKDF.create(cfg).compute(password, salt);
    };
  })();
  return CryptoJS.EvpKDF;
});
});

var cipherCore = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, evpkdf);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Cipher core components.
   */
  CryptoJS.lib.Cipher || function (undefined$1) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm;
    var C_enc = C.enc;
    C_enc.Utf8;
    var Base64 = C_enc.Base64;
    var C_algo = C.algo;
    var EvpKDF = C_algo.EvpKDF;

    /**
     * Abstract base cipher template.
     *
     * @property {number} keySize This cipher's key size. Default: 4 (128 bits)
     * @property {number} ivSize This cipher's IV size. Default: 4 (128 bits)
     * @property {number} _ENC_XFORM_MODE A constant representing encryption mode.
     * @property {number} _DEC_XFORM_MODE A constant representing decryption mode.
     */
    var Cipher = C_lib.Cipher = BufferedBlockAlgorithm.extend({
      /**
       * Configuration options.
       *
       * @property {WordArray} iv The IV to use for this operation.
       */
      cfg: Base.extend(),
      /**
       * Creates this cipher in encryption mode.
       *
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {Cipher} A cipher instance.
       *
       * @static
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.createEncryptor(keyWordArray, { iv: ivWordArray });
       */
      createEncryptor: function (key, cfg) {
        return this.create(this._ENC_XFORM_MODE, key, cfg);
      },
      /**
       * Creates this cipher in decryption mode.
       *
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {Cipher} A cipher instance.
       *
       * @static
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.createDecryptor(keyWordArray, { iv: ivWordArray });
       */
      createDecryptor: function (key, cfg) {
        return this.create(this._DEC_XFORM_MODE, key, cfg);
      },
      /**
       * Initializes a newly created cipher.
       *
       * @param {number} xformMode Either the encryption or decryption transormation mode constant.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.create(CryptoJS.algo.AES._ENC_XFORM_MODE, keyWordArray, { iv: ivWordArray });
       */
      init: function (xformMode, key, cfg) {
        // Apply config defaults
        this.cfg = this.cfg.extend(cfg);

        // Store transform mode and key
        this._xformMode = xformMode;
        this._key = key;

        // Set initial values
        this.reset();
      },
      /**
       * Resets this cipher to its initial state.
       *
       * @example
       *
       *     cipher.reset();
       */
      reset: function () {
        // Reset data buffer
        BufferedBlockAlgorithm.reset.call(this);

        // Perform concrete-cipher logic
        this._doReset();
      },
      /**
       * Adds data to be encrypted or decrypted.
       *
       * @param {WordArray|string} dataUpdate The data to encrypt or decrypt.
       *
       * @return {WordArray} The data after processing.
       *
       * @example
       *
       *     var encrypted = cipher.process('data');
       *     var encrypted = cipher.process(wordArray);
       */
      process: function (dataUpdate) {
        // Append
        this._append(dataUpdate);

        // Process available blocks
        return this._process();
      },
      /**
       * Finalizes the encryption or decryption process.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} dataUpdate The final data to encrypt or decrypt.
       *
       * @return {WordArray} The data after final processing.
       *
       * @example
       *
       *     var encrypted = cipher.finalize();
       *     var encrypted = cipher.finalize('data');
       *     var encrypted = cipher.finalize(wordArray);
       */
      finalize: function (dataUpdate) {
        // Final data update
        if (dataUpdate) {
          this._append(dataUpdate);
        }

        // Perform concrete-cipher logic
        var finalProcessedData = this._doFinalize();
        return finalProcessedData;
      },
      keySize: 128 / 32,
      ivSize: 128 / 32,
      _ENC_XFORM_MODE: 1,
      _DEC_XFORM_MODE: 2,
      /**
       * Creates shortcut functions to a cipher's object interface.
       *
       * @param {Cipher} cipher The cipher to create a helper for.
       *
       * @return {Object} An object with encrypt and decrypt shortcut functions.
       *
       * @static
       *
       * @example
       *
       *     var AES = CryptoJS.lib.Cipher._createHelper(CryptoJS.algo.AES);
       */
      _createHelper: function () {
        function selectCipherStrategy(key) {
          if (typeof key == 'string') {
            return PasswordBasedCipher;
          } else {
            return SerializableCipher;
          }
        }
        return function (cipher) {
          return {
            encrypt: function (message, key, cfg) {
              return selectCipherStrategy(key).encrypt(cipher, message, key, cfg);
            },
            decrypt: function (ciphertext, key, cfg) {
              return selectCipherStrategy(key).decrypt(cipher, ciphertext, key, cfg);
            }
          };
        };
      }()
    });

    /**
     * Abstract base stream cipher template.
     *
     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 1 (32 bits)
     */
    C_lib.StreamCipher = Cipher.extend({
      _doFinalize: function () {
        // Process partial blocks
        var finalProcessedBlocks = this._process(!!'flush');
        return finalProcessedBlocks;
      },
      blockSize: 1
    });

    /**
     * Mode namespace.
     */
    var C_mode = C.mode = {};

    /**
     * Abstract base block cipher mode template.
     */
    var BlockCipherMode = C_lib.BlockCipherMode = Base.extend({
      /**
       * Creates this mode for encryption.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @static
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.createEncryptor(cipher, iv.words);
       */
      createEncryptor: function (cipher, iv) {
        return this.Encryptor.create(cipher, iv);
      },
      /**
       * Creates this mode for decryption.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @static
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.createDecryptor(cipher, iv.words);
       */
      createDecryptor: function (cipher, iv) {
        return this.Decryptor.create(cipher, iv);
      },
      /**
       * Initializes a newly created mode.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.Encryptor.create(cipher, iv.words);
       */
      init: function (cipher, iv) {
        this._cipher = cipher;
        this._iv = iv;
      }
    });

    /**
     * Cipher Block Chaining mode.
     */
    var CBC = C_mode.CBC = function () {
      /**
       * Abstract base CBC mode.
       */
      var CBC = BlockCipherMode.extend();

      /**
       * CBC encryptor.
       */
      CBC.Encryptor = CBC.extend({
        /**
         * Processes the data block at offset.
         *
         * @param {Array} words The data words to operate on.
         * @param {number} offset The offset where the block starts.
         *
         * @example
         *
         *     mode.processBlock(data.words, offset);
         */
        processBlock: function (words, offset) {
          // Shortcuts
          var cipher = this._cipher;
          var blockSize = cipher.blockSize;

          // XOR and encrypt
          xorBlock.call(this, words, offset, blockSize);
          cipher.encryptBlock(words, offset);

          // Remember this block to use with next block
          this._prevBlock = words.slice(offset, offset + blockSize);
        }
      });

      /**
       * CBC decryptor.
       */
      CBC.Decryptor = CBC.extend({
        /**
         * Processes the data block at offset.
         *
         * @param {Array} words The data words to operate on.
         * @param {number} offset The offset where the block starts.
         *
         * @example
         *
         *     mode.processBlock(data.words, offset);
         */
        processBlock: function (words, offset) {
          // Shortcuts
          var cipher = this._cipher;
          var blockSize = cipher.blockSize;

          // Remember this block to use with next block
          var thisBlock = words.slice(offset, offset + blockSize);

          // Decrypt and XOR
          cipher.decryptBlock(words, offset);
          xorBlock.call(this, words, offset, blockSize);

          // This block becomes the previous block
          this._prevBlock = thisBlock;
        }
      });
      function xorBlock(words, offset, blockSize) {
        var block;

        // Shortcut
        var iv = this._iv;

        // Choose mixing block
        if (iv) {
          block = iv;

          // Remove IV for subsequent blocks
          this._iv = undefined$1;
        } else {
          block = this._prevBlock;
        }

        // XOR blocks
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= block[i];
        }
      }
      return CBC;
    }();

    /**
     * Padding namespace.
     */
    var C_pad = C.pad = {};

    /**
     * PKCS #5/7 padding strategy.
     */
    var Pkcs7 = C_pad.Pkcs7 = {
      /**
       * Pads data using the algorithm defined in PKCS #5/7.
       *
       * @param {WordArray} data The data to pad.
       * @param {number} blockSize The multiple that the data should be padded to.
       *
       * @static
       *
       * @example
       *
       *     CryptoJS.pad.Pkcs7.pad(wordArray, 4);
       */
      pad: function (data, blockSize) {
        // Shortcut
        var blockSizeBytes = blockSize * 4;

        // Count padding bytes
        var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

        // Create padding word
        var paddingWord = nPaddingBytes << 24 | nPaddingBytes << 16 | nPaddingBytes << 8 | nPaddingBytes;

        // Create padding
        var paddingWords = [];
        for (var i = 0; i < nPaddingBytes; i += 4) {
          paddingWords.push(paddingWord);
        }
        var padding = WordArray.create(paddingWords, nPaddingBytes);

        // Add padding
        data.concat(padding);
      },
      /**
       * Unpads data that had been padded using the algorithm defined in PKCS #5/7.
       *
       * @param {WordArray} data The data to unpad.
       *
       * @static
       *
       * @example
       *
       *     CryptoJS.pad.Pkcs7.unpad(wordArray);
       */
      unpad: function (data) {
        // Get number of padding bytes from last byte
        var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

        // Remove padding
        data.sigBytes -= nPaddingBytes;
      }
    };

    /**
     * Abstract base block cipher template.
     *
     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 4 (128 bits)
     */
    C_lib.BlockCipher = Cipher.extend({
      /**
       * Configuration options.
       *
       * @property {Mode} mode The block mode to use. Default: CBC
       * @property {Padding} padding The padding strategy to use. Default: Pkcs7
       */
      cfg: Cipher.cfg.extend({
        mode: CBC,
        padding: Pkcs7
      }),
      reset: function () {
        var modeCreator;

        // Reset cipher
        Cipher.reset.call(this);

        // Shortcuts
        var cfg = this.cfg;
        var iv = cfg.iv;
        var mode = cfg.mode;

        // Reset block mode
        if (this._xformMode == this._ENC_XFORM_MODE) {
          modeCreator = mode.createEncryptor;
        } else /* if (this._xformMode == this._DEC_XFORM_MODE) */{
            modeCreator = mode.createDecryptor;
            // Keep at least one block in the buffer for unpadding
            this._minBufferSize = 1;
          }
        if (this._mode && this._mode.__creator == modeCreator) {
          this._mode.init(this, iv && iv.words);
        } else {
          this._mode = modeCreator.call(mode, this, iv && iv.words);
          this._mode.__creator = modeCreator;
        }
      },
      _doProcessBlock: function (words, offset) {
        this._mode.processBlock(words, offset);
      },
      _doFinalize: function () {
        var finalProcessedBlocks;

        // Shortcut
        var padding = this.cfg.padding;

        // Finalize
        if (this._xformMode == this._ENC_XFORM_MODE) {
          // Pad data
          padding.pad(this._data, this.blockSize);

          // Process final blocks
          finalProcessedBlocks = this._process(!!'flush');
        } else /* if (this._xformMode == this._DEC_XFORM_MODE) */{
            // Process final blocks
            finalProcessedBlocks = this._process(!!'flush');

            // Unpad data
            padding.unpad(finalProcessedBlocks);
          }
        return finalProcessedBlocks;
      },
      blockSize: 128 / 32
    });

    /**
     * A collection of cipher parameters.
     *
     * @property {WordArray} ciphertext The raw ciphertext.
     * @property {WordArray} key The key to this ciphertext.
     * @property {WordArray} iv The IV used in the ciphering operation.
     * @property {WordArray} salt The salt used with a key derivation function.
     * @property {Cipher} algorithm The cipher algorithm.
     * @property {Mode} mode The block mode used in the ciphering operation.
     * @property {Padding} padding The padding scheme used in the ciphering operation.
     * @property {number} blockSize The block size of the cipher.
     * @property {Format} formatter The default formatting strategy to convert this cipher params object to a string.
     */
    var CipherParams = C_lib.CipherParams = Base.extend({
      /**
       * Initializes a newly created cipher params object.
       *
       * @param {Object} cipherParams An object with any of the possible cipher parameters.
       *
       * @example
       *
       *     var cipherParams = CryptoJS.lib.CipherParams.create({
       *         ciphertext: ciphertextWordArray,
       *         key: keyWordArray,
       *         iv: ivWordArray,
       *         salt: saltWordArray,
       *         algorithm: CryptoJS.algo.AES,
       *         mode: CryptoJS.mode.CBC,
       *         padding: CryptoJS.pad.PKCS7,
       *         blockSize: 4,
       *         formatter: CryptoJS.format.OpenSSL
       *     });
       */
      init: function (cipherParams) {
        this.mixIn(cipherParams);
      },
      /**
       * Converts this cipher params object to a string.
       *
       * @param {Format} formatter (Optional) The formatting strategy to use.
       *
       * @return {string} The stringified cipher params.
       *
       * @throws Error If neither the formatter nor the default formatter is set.
       *
       * @example
       *
       *     var string = cipherParams + '';
       *     var string = cipherParams.toString();
       *     var string = cipherParams.toString(CryptoJS.format.OpenSSL);
       */
      toString: function (formatter) {
        return (formatter || this.formatter).stringify(this);
      }
    });

    /**
     * Format namespace.
     */
    var C_format = C.format = {};

    /**
     * OpenSSL formatting strategy.
     */
    var OpenSSLFormatter = C_format.OpenSSL = {
      /**
       * Converts a cipher params object to an OpenSSL-compatible string.
       *
       * @param {CipherParams} cipherParams The cipher params object.
       *
       * @return {string} The OpenSSL-compatible string.
       *
       * @static
       *
       * @example
       *
       *     var openSSLString = CryptoJS.format.OpenSSL.stringify(cipherParams);
       */
      stringify: function (cipherParams) {
        var wordArray;

        // Shortcuts
        var ciphertext = cipherParams.ciphertext;
        var salt = cipherParams.salt;

        // Format
        if (salt) {
          wordArray = WordArray.create([0x53616c74, 0x65645f5f]).concat(salt).concat(ciphertext);
        } else {
          wordArray = ciphertext;
        }
        return wordArray.toString(Base64);
      },
      /**
       * Converts an OpenSSL-compatible string to a cipher params object.
       *
       * @param {string} openSSLStr The OpenSSL-compatible string.
       *
       * @return {CipherParams} The cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var cipherParams = CryptoJS.format.OpenSSL.parse(openSSLString);
       */
      parse: function (openSSLStr) {
        var salt;

        // Parse base64
        var ciphertext = Base64.parse(openSSLStr);

        // Shortcut
        var ciphertextWords = ciphertext.words;

        // Test for salt
        if (ciphertextWords[0] == 0x53616c74 && ciphertextWords[1] == 0x65645f5f) {
          // Extract salt
          salt = WordArray.create(ciphertextWords.slice(2, 4));

          // Remove salt from ciphertext
          ciphertextWords.splice(0, 4);
          ciphertext.sigBytes -= 16;
        }
        return CipherParams.create({
          ciphertext: ciphertext,
          salt: salt
        });
      }
    };

    /**
     * A cipher wrapper that returns ciphertext as a serializable cipher params object.
     */
    var SerializableCipher = C_lib.SerializableCipher = Base.extend({
      /**
       * Configuration options.
       *
       * @property {Formatter} format The formatting strategy to convert cipher param objects to and from a string. Default: OpenSSL
       */
      cfg: Base.extend({
        format: OpenSSLFormatter
      }),
      /**
       * Encrypts a message.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {WordArray|string} message The message to encrypt.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {CipherParams} A cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key);
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv });
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       */
      encrypt: function (cipher, message, key, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Encrypt
        var encryptor = cipher.createEncryptor(key, cfg);
        var ciphertext = encryptor.finalize(message);

        // Shortcut
        var cipherCfg = encryptor.cfg;

        // Create and return serializable cipher params
        return CipherParams.create({
          ciphertext: ciphertext,
          key: key,
          iv: cipherCfg.iv,
          algorithm: cipher,
          mode: cipherCfg.mode,
          padding: cipherCfg.padding,
          blockSize: cipher.blockSize,
          formatter: cfg.format
        });
      },
      /**
       * Decrypts serialized ciphertext.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {WordArray} The plaintext.
       *
       * @static
       *
       * @example
       *
       *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       */
      decrypt: function (cipher, ciphertext, key, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Convert string to CipherParams
        ciphertext = this._parse(ciphertext, cfg.format);

        // Decrypt
        var plaintext = cipher.createDecryptor(key, cfg).finalize(ciphertext.ciphertext);
        return plaintext;
      },
      /**
       * Converts serialized ciphertext to CipherParams,
       * else assumed CipherParams already and returns ciphertext unchanged.
       *
       * @param {CipherParams|string} ciphertext The ciphertext.
       * @param {Formatter} format The formatting strategy to use to parse serialized ciphertext.
       *
       * @return {CipherParams} The unserialized ciphertext.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher._parse(ciphertextStringOrParams, format);
       */
      _parse: function (ciphertext, format) {
        if (typeof ciphertext == 'string') {
          return format.parse(ciphertext, this);
        } else {
          return ciphertext;
        }
      }
    });

    /**
     * Key derivation function namespace.
     */
    var C_kdf = C.kdf = {};

    /**
     * OpenSSL key derivation function.
     */
    var OpenSSLKdf = C_kdf.OpenSSL = {
      /**
       * Derives a key and IV from a password.
       *
       * @param {string} password The password to derive from.
       * @param {number} keySize The size in words of the key to generate.
       * @param {number} ivSize The size in words of the IV to generate.
       * @param {WordArray|string} salt (Optional) A 64-bit salt to use. If omitted, a salt will be generated randomly.
       *
       * @return {CipherParams} A cipher params object with the key, IV, and salt.
       *
       * @static
       *
       * @example
       *
       *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32);
       *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32, 'saltsalt');
       */
      execute: function (password, keySize, ivSize, salt, hasher) {
        // Generate random salt
        if (!salt) {
          salt = WordArray.random(64 / 8);
        }

        // Derive key and IV
        if (!hasher) {
          var key = EvpKDF.create({
            keySize: keySize + ivSize
          }).compute(password, salt);
        } else {
          var key = EvpKDF.create({
            keySize: keySize + ivSize,
            hasher: hasher
          }).compute(password, salt);
        }

        // Separate key and IV
        var iv = WordArray.create(key.words.slice(keySize), ivSize * 4);
        key.sigBytes = keySize * 4;

        // Return params
        return CipherParams.create({
          key: key,
          iv: iv,
          salt: salt
        });
      }
    };

    /**
     * A serializable cipher wrapper that derives the key from a password,
     * and returns ciphertext as a serializable cipher params object.
     */
    var PasswordBasedCipher = C_lib.PasswordBasedCipher = SerializableCipher.extend({
      /**
       * Configuration options.
       *
       * @property {KDF} kdf The key derivation function to use to generate a key and IV from a password. Default: OpenSSL
       */
      cfg: SerializableCipher.cfg.extend({
        kdf: OpenSSLKdf
      }),
      /**
       * Encrypts a message using a password.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {WordArray|string} message The message to encrypt.
       * @param {string} password The password.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {CipherParams} A cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password');
       *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password', { format: CryptoJS.format.OpenSSL });
       */
      encrypt: function (cipher, message, password, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Derive key and other params
        var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, cfg.salt, cfg.hasher);

        // Add IV to config
        cfg.iv = derivedParams.iv;

        // Encrypt
        var ciphertext = SerializableCipher.encrypt.call(this, cipher, message, derivedParams.key, cfg);

        // Mix in derived params
        ciphertext.mixIn(derivedParams);
        return ciphertext;
      },
      /**
       * Decrypts serialized ciphertext using a password.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
       * @param {string} password The password.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {WordArray} The plaintext.
       *
       * @static
       *
       * @example
       *
       *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, 'password', { format: CryptoJS.format.OpenSSL });
       *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, 'password', { format: CryptoJS.format.OpenSSL });
       */
      decrypt: function (cipher, ciphertext, password, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Convert string to CipherParams
        ciphertext = this._parse(ciphertext, cfg.format);

        // Derive key and other params
        var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, ciphertext.salt, cfg.hasher);

        // Add IV to config
        cfg.iv = derivedParams.iv;

        // Decrypt
        var plaintext = SerializableCipher.decrypt.call(this, cipher, ciphertext, derivedParams.key, cfg);
        return plaintext;
      }
    });
  }();
});
});

var modeCfb = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Cipher Feedback block mode.
   */
  CryptoJS.mode.CFB = function () {
    var CFB = CryptoJS.lib.BlockCipherMode.extend();
    CFB.Encryptor = CFB.extend({
      processBlock: function (words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

        // Remember this block to use with next block
        this._prevBlock = words.slice(offset, offset + blockSize);
      }
    });
    CFB.Decryptor = CFB.extend({
      processBlock: function (words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;

        // Remember this block to use with next block
        var thisBlock = words.slice(offset, offset + blockSize);
        generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

        // This block becomes the previous block
        this._prevBlock = thisBlock;
      }
    });
    function generateKeystreamAndEncrypt(words, offset, blockSize, cipher) {
      var keystream;

      // Shortcut
      var iv = this._iv;

      // Generate keystream
      if (iv) {
        keystream = iv.slice(0);

        // Remove IV for subsequent blocks
        this._iv = undefined;
      } else {
        keystream = this._prevBlock;
      }
      cipher.encryptBlock(keystream, 0);

      // Encrypt
      for (var i = 0; i < blockSize; i++) {
        words[offset + i] ^= keystream[i];
      }
    }
    return CFB;
  }();
  return CryptoJS.mode.CFB;
});
});

var modeCtr = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Counter block mode.
   */
  CryptoJS.mode.CTR = function () {
    var CTR = CryptoJS.lib.BlockCipherMode.extend();
    var Encryptor = CTR.Encryptor = CTR.extend({
      processBlock: function (words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var counter = this._counter;

        // Generate keystream
        if (iv) {
          counter = this._counter = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        var keystream = counter.slice(0);
        cipher.encryptBlock(keystream, 0);

        // Increment counter
        counter[blockSize - 1] = counter[blockSize - 1] + 1 | 0;

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    CTR.Decryptor = Encryptor;
    return CTR;
  }();
  return CryptoJS.mode.CTR;
});
});

var modeCtrGladman = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /** @preserve
   * Counter block mode compatible with  Dr Brian Gladman fileenc.c
   * derived from CryptoJS.mode.CTR
   * Jan Hruby jhruby.web@gmail.com
   */
  CryptoJS.mode.CTRGladman = function () {
    var CTRGladman = CryptoJS.lib.BlockCipherMode.extend();
    function incWord(word) {
      if ((word >> 24 & 0xff) === 0xff) {
        //overflow
        var b1 = word >> 16 & 0xff;
        var b2 = word >> 8 & 0xff;
        var b3 = word & 0xff;
        if (b1 === 0xff)
          // overflow b1
          {
            b1 = 0;
            if (b2 === 0xff) {
              b2 = 0;
              if (b3 === 0xff) {
                b3 = 0;
              } else {
                ++b3;
              }
            } else {
              ++b2;
            }
          } else {
          ++b1;
        }
        word = 0;
        word += b1 << 16;
        word += b2 << 8;
        word += b3;
      } else {
        word += 0x01 << 24;
      }
      return word;
    }
    function incCounter(counter) {
      if ((counter[0] = incWord(counter[0])) === 0) {
        // encr_data in fileenc.c from  Dr Brian Gladman's counts only with DWORD j < 8
        counter[1] = incWord(counter[1]);
      }
      return counter;
    }
    var Encryptor = CTRGladman.Encryptor = CTRGladman.extend({
      processBlock: function (words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var counter = this._counter;

        // Generate keystream
        if (iv) {
          counter = this._counter = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        incCounter(counter);
        var keystream = counter.slice(0);
        cipher.encryptBlock(keystream, 0);

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    CTRGladman.Decryptor = Encryptor;
    return CTRGladman;
  }();
  return CryptoJS.mode.CTRGladman;
});
});

var modeOfb = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Output Feedback block mode.
   */
  CryptoJS.mode.OFB = function () {
    var OFB = CryptoJS.lib.BlockCipherMode.extend();
    var Encryptor = OFB.Encryptor = OFB.extend({
      processBlock: function (words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var keystream = this._keystream;

        // Generate keystream
        if (iv) {
          keystream = this._keystream = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        cipher.encryptBlock(keystream, 0);

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    OFB.Decryptor = Encryptor;
    return OFB;
  }();
  return CryptoJS.mode.OFB;
});
});

var modeEcb = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Electronic Codebook block mode.
   */
  CryptoJS.mode.ECB = function () {
    var ECB = CryptoJS.lib.BlockCipherMode.extend();
    ECB.Encryptor = ECB.extend({
      processBlock: function (words, offset) {
        this._cipher.encryptBlock(words, offset);
      }
    });
    ECB.Decryptor = ECB.extend({
      processBlock: function (words, offset) {
        this._cipher.decryptBlock(words, offset);
      }
    });
    return ECB;
  }();
  return CryptoJS.mode.ECB;
});
});

var padAnsix923 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * ANSI X.923 padding strategy.
   */
  CryptoJS.pad.AnsiX923 = {
    pad: function (data, blockSize) {
      // Shortcuts
      var dataSigBytes = data.sigBytes;
      var blockSizeBytes = blockSize * 4;

      // Count padding bytes
      var nPaddingBytes = blockSizeBytes - dataSigBytes % blockSizeBytes;

      // Compute last byte position
      var lastBytePos = dataSigBytes + nPaddingBytes - 1;

      // Pad
      data.clamp();
      data.words[lastBytePos >>> 2] |= nPaddingBytes << 24 - lastBytePos % 4 * 8;
      data.sigBytes += nPaddingBytes;
    },
    unpad: function (data) {
      // Get number of padding bytes from last byte
      var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

      // Remove padding
      data.sigBytes -= nPaddingBytes;
    }
  };
  return CryptoJS.pad.Ansix923;
});
});

var padIso10126 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * ISO 10126 padding strategy.
   */
  CryptoJS.pad.Iso10126 = {
    pad: function (data, blockSize) {
      // Shortcut
      var blockSizeBytes = blockSize * 4;

      // Count padding bytes
      var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

      // Pad
      data.concat(CryptoJS.lib.WordArray.random(nPaddingBytes - 1)).concat(CryptoJS.lib.WordArray.create([nPaddingBytes << 24], 1));
    },
    unpad: function (data) {
      // Get number of padding bytes from last byte
      var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

      // Remove padding
      data.sigBytes -= nPaddingBytes;
    }
  };
  return CryptoJS.pad.Iso10126;
});
});

var padIso97971 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * ISO/IEC 9797-1 Padding Method 2.
   */
  CryptoJS.pad.Iso97971 = {
    pad: function (data, blockSize) {
      // Add 0x80 byte
      data.concat(CryptoJS.lib.WordArray.create([0x80000000], 1));

      // Zero pad the rest
      CryptoJS.pad.ZeroPadding.pad(data, blockSize);
    },
    unpad: function (data) {
      // Remove zero padding
      CryptoJS.pad.ZeroPadding.unpad(data);

      // Remove one more byte -- the 0x80 byte
      data.sigBytes--;
    }
  };
  return CryptoJS.pad.Iso97971;
});
});

var padZeropadding = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * Zero padding strategy.
   */
  CryptoJS.pad.ZeroPadding = {
    pad: function (data, blockSize) {
      // Shortcut
      var blockSizeBytes = blockSize * 4;

      // Pad
      data.clamp();
      data.sigBytes += blockSizeBytes - (data.sigBytes % blockSizeBytes || blockSizeBytes);
    },
    unpad: function (data) {
      // Shortcut
      var dataWords = data.words;

      // Unpad
      var i = data.sigBytes - 1;
      for (var i = data.sigBytes - 1; i >= 0; i--) {
        if (dataWords[i >>> 2] >>> 24 - i % 4 * 8 & 0xff) {
          data.sigBytes = i + 1;
          break;
        }
      }
    }
  };
  return CryptoJS.pad.ZeroPadding;
});
});

var padNopadding = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  /**
   * A noop padding strategy.
   */
  CryptoJS.pad.NoPadding = {
    pad: function () {},
    unpad: function () {}
  };
  return CryptoJS.pad.NoPadding;
});
});

var formatHex = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function (undefined$1) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var CipherParams = C_lib.CipherParams;
    var C_enc = C.enc;
    var Hex = C_enc.Hex;
    var C_format = C.format;
    C_format.Hex = {
      /**
       * Converts the ciphertext of a cipher params object to a hexadecimally encoded string.
       *
       * @param {CipherParams} cipherParams The cipher params object.
       *
       * @return {string} The hexadecimally encoded string.
       *
       * @static
       *
       * @example
       *
       *     var hexString = CryptoJS.format.Hex.stringify(cipherParams);
       */
      stringify: function (cipherParams) {
        return cipherParams.ciphertext.toString(Hex);
      },
      /**
       * Converts a hexadecimally encoded ciphertext string to a cipher params object.
       *
       * @param {string} input The hexadecimally encoded string.
       *
       * @return {CipherParams} The cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var cipherParams = CryptoJS.format.Hex.parse(hexString);
       */
      parse: function (input) {
        var ciphertext = Hex.parse(input);
        return CipherParams.create({
          ciphertext: ciphertext
        });
      }
    };
  })();
  return CryptoJS.format.Hex;
});
});

var aes = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;

    // Lookup tables
    var SBOX = [];
    var INV_SBOX = [];
    var SUB_MIX_0 = [];
    var SUB_MIX_1 = [];
    var SUB_MIX_2 = [];
    var SUB_MIX_3 = [];
    var INV_SUB_MIX_0 = [];
    var INV_SUB_MIX_1 = [];
    var INV_SUB_MIX_2 = [];
    var INV_SUB_MIX_3 = [];

    // Compute lookup tables
    (function () {
      // Compute double table
      var d = [];
      for (var i = 0; i < 256; i++) {
        if (i < 128) {
          d[i] = i << 1;
        } else {
          d[i] = i << 1 ^ 0x11b;
        }
      }

      // Walk GF(2^8)
      var x = 0;
      var xi = 0;
      for (var i = 0; i < 256; i++) {
        // Compute sbox
        var sx = xi ^ xi << 1 ^ xi << 2 ^ xi << 3 ^ xi << 4;
        sx = sx >>> 8 ^ sx & 0xff ^ 0x63;
        SBOX[x] = sx;
        INV_SBOX[sx] = x;

        // Compute multiplication
        var x2 = d[x];
        var x4 = d[x2];
        var x8 = d[x4];

        // Compute sub bytes, mix columns tables
        var t = d[sx] * 0x101 ^ sx * 0x1010100;
        SUB_MIX_0[x] = t << 24 | t >>> 8;
        SUB_MIX_1[x] = t << 16 | t >>> 16;
        SUB_MIX_2[x] = t << 8 | t >>> 24;
        SUB_MIX_3[x] = t;

        // Compute inv sub bytes, inv mix columns tables
        var t = x8 * 0x1010101 ^ x4 * 0x10001 ^ x2 * 0x101 ^ x * 0x1010100;
        INV_SUB_MIX_0[sx] = t << 24 | t >>> 8;
        INV_SUB_MIX_1[sx] = t << 16 | t >>> 16;
        INV_SUB_MIX_2[sx] = t << 8 | t >>> 24;
        INV_SUB_MIX_3[sx] = t;

        // Compute next counter
        if (!x) {
          x = xi = 1;
        } else {
          x = x2 ^ d[d[d[x8 ^ x2]]];
          xi ^= d[d[xi]];
        }
      }
    })();

    // Precomputed Rcon lookup
    var RCON = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];

    /**
     * AES block cipher algorithm.
     */
    var AES = C_algo.AES = BlockCipher.extend({
      _doReset: function () {
        var t;

        // Skip reset of nRounds has been set before and key did not change
        if (this._nRounds && this._keyPriorReset === this._key) {
          return;
        }

        // Shortcuts
        var key = this._keyPriorReset = this._key;
        var keyWords = key.words;
        var keySize = key.sigBytes / 4;

        // Compute number of rounds
        var nRounds = this._nRounds = keySize + 6;

        // Compute number of key schedule rows
        var ksRows = (nRounds + 1) * 4;

        // Compute key schedule
        var keySchedule = this._keySchedule = [];
        for (var ksRow = 0; ksRow < ksRows; ksRow++) {
          if (ksRow < keySize) {
            keySchedule[ksRow] = keyWords[ksRow];
          } else {
            t = keySchedule[ksRow - 1];
            if (!(ksRow % keySize)) {
              // Rot word
              t = t << 8 | t >>> 24;

              // Sub word
              t = SBOX[t >>> 24] << 24 | SBOX[t >>> 16 & 0xff] << 16 | SBOX[t >>> 8 & 0xff] << 8 | SBOX[t & 0xff];

              // Mix Rcon
              t ^= RCON[ksRow / keySize | 0] << 24;
            } else if (keySize > 6 && ksRow % keySize == 4) {
              // Sub word
              t = SBOX[t >>> 24] << 24 | SBOX[t >>> 16 & 0xff] << 16 | SBOX[t >>> 8 & 0xff] << 8 | SBOX[t & 0xff];
            }
            keySchedule[ksRow] = keySchedule[ksRow - keySize] ^ t;
          }
        }

        // Compute inv key schedule
        var invKeySchedule = this._invKeySchedule = [];
        for (var invKsRow = 0; invKsRow < ksRows; invKsRow++) {
          var ksRow = ksRows - invKsRow;
          if (invKsRow % 4) {
            var t = keySchedule[ksRow];
          } else {
            var t = keySchedule[ksRow - 4];
          }
          if (invKsRow < 4 || ksRow <= 4) {
            invKeySchedule[invKsRow] = t;
          } else {
            invKeySchedule[invKsRow] = INV_SUB_MIX_0[SBOX[t >>> 24]] ^ INV_SUB_MIX_1[SBOX[t >>> 16 & 0xff]] ^ INV_SUB_MIX_2[SBOX[t >>> 8 & 0xff]] ^ INV_SUB_MIX_3[SBOX[t & 0xff]];
          }
        }
      },
      encryptBlock: function (M, offset) {
        this._doCryptBlock(M, offset, this._keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX);
      },
      decryptBlock: function (M, offset) {
        // Swap 2nd and 4th rows
        var t = M[offset + 1];
        M[offset + 1] = M[offset + 3];
        M[offset + 3] = t;
        this._doCryptBlock(M, offset, this._invKeySchedule, INV_SUB_MIX_0, INV_SUB_MIX_1, INV_SUB_MIX_2, INV_SUB_MIX_3, INV_SBOX);

        // Inv swap 2nd and 4th rows
        var t = M[offset + 1];
        M[offset + 1] = M[offset + 3];
        M[offset + 3] = t;
      },
      _doCryptBlock: function (M, offset, keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX) {
        // Shortcut
        var nRounds = this._nRounds;

        // Get input, add round key
        var s0 = M[offset] ^ keySchedule[0];
        var s1 = M[offset + 1] ^ keySchedule[1];
        var s2 = M[offset + 2] ^ keySchedule[2];
        var s3 = M[offset + 3] ^ keySchedule[3];

        // Key schedule row counter
        var ksRow = 4;

        // Rounds
        for (var round = 1; round < nRounds; round++) {
          // Shift rows, sub bytes, mix columns, add round key
          var t0 = SUB_MIX_0[s0 >>> 24] ^ SUB_MIX_1[s1 >>> 16 & 0xff] ^ SUB_MIX_2[s2 >>> 8 & 0xff] ^ SUB_MIX_3[s3 & 0xff] ^ keySchedule[ksRow++];
          var t1 = SUB_MIX_0[s1 >>> 24] ^ SUB_MIX_1[s2 >>> 16 & 0xff] ^ SUB_MIX_2[s3 >>> 8 & 0xff] ^ SUB_MIX_3[s0 & 0xff] ^ keySchedule[ksRow++];
          var t2 = SUB_MIX_0[s2 >>> 24] ^ SUB_MIX_1[s3 >>> 16 & 0xff] ^ SUB_MIX_2[s0 >>> 8 & 0xff] ^ SUB_MIX_3[s1 & 0xff] ^ keySchedule[ksRow++];
          var t3 = SUB_MIX_0[s3 >>> 24] ^ SUB_MIX_1[s0 >>> 16 & 0xff] ^ SUB_MIX_2[s1 >>> 8 & 0xff] ^ SUB_MIX_3[s2 & 0xff] ^ keySchedule[ksRow++];

          // Update state
          s0 = t0;
          s1 = t1;
          s2 = t2;
          s3 = t3;
        }

        // Shift rows, sub bytes, add round key
        var t0 = (SBOX[s0 >>> 24] << 24 | SBOX[s1 >>> 16 & 0xff] << 16 | SBOX[s2 >>> 8 & 0xff] << 8 | SBOX[s3 & 0xff]) ^ keySchedule[ksRow++];
        var t1 = (SBOX[s1 >>> 24] << 24 | SBOX[s2 >>> 16 & 0xff] << 16 | SBOX[s3 >>> 8 & 0xff] << 8 | SBOX[s0 & 0xff]) ^ keySchedule[ksRow++];
        var t2 = (SBOX[s2 >>> 24] << 24 | SBOX[s3 >>> 16 & 0xff] << 16 | SBOX[s0 >>> 8 & 0xff] << 8 | SBOX[s1 & 0xff]) ^ keySchedule[ksRow++];
        var t3 = (SBOX[s3 >>> 24] << 24 | SBOX[s0 >>> 16 & 0xff] << 16 | SBOX[s1 >>> 8 & 0xff] << 8 | SBOX[s2 & 0xff]) ^ keySchedule[ksRow++];

        // Set output
        M[offset] = t0;
        M[offset + 1] = t1;
        M[offset + 2] = t2;
        M[offset + 3] = t3;
      },
      keySize: 256 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.AES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.AES.decrypt(ciphertext, key, cfg);
     */
    C.AES = BlockCipher._createHelper(AES);
  })();
  return CryptoJS.AES;
});
});

var tripledes = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;

    // Permuted Choice 1 constants
    var PC1 = [57, 49, 41, 33, 25, 17, 9, 1, 58, 50, 42, 34, 26, 18, 10, 2, 59, 51, 43, 35, 27, 19, 11, 3, 60, 52, 44, 36, 63, 55, 47, 39, 31, 23, 15, 7, 62, 54, 46, 38, 30, 22, 14, 6, 61, 53, 45, 37, 29, 21, 13, 5, 28, 20, 12, 4];

    // Permuted Choice 2 constants
    var PC2 = [14, 17, 11, 24, 1, 5, 3, 28, 15, 6, 21, 10, 23, 19, 12, 4, 26, 8, 16, 7, 27, 20, 13, 2, 41, 52, 31, 37, 47, 55, 30, 40, 51, 45, 33, 48, 44, 49, 39, 56, 34, 53, 46, 42, 50, 36, 29, 32];

    // Cumulative bit shift constants
    var BIT_SHIFTS = [1, 2, 4, 6, 8, 10, 12, 14, 15, 17, 19, 21, 23, 25, 27, 28];

    // SBOXes and round permutation constants
    var SBOX_P = [{
      0x0: 0x808200,
      0x10000000: 0x8000,
      0x20000000: 0x808002,
      0x30000000: 0x2,
      0x40000000: 0x200,
      0x50000000: 0x808202,
      0x60000000: 0x800202,
      0x70000000: 0x800000,
      0x80000000: 0x202,
      0x90000000: 0x800200,
      0xa0000000: 0x8200,
      0xb0000000: 0x808000,
      0xc0000000: 0x8002,
      0xd0000000: 0x800002,
      0xe0000000: 0x0,
      0xf0000000: 0x8202,
      0x8000000: 0x0,
      0x18000000: 0x808202,
      0x28000000: 0x8202,
      0x38000000: 0x8000,
      0x48000000: 0x808200,
      0x58000000: 0x200,
      0x68000000: 0x808002,
      0x78000000: 0x2,
      0x88000000: 0x800200,
      0x98000000: 0x8200,
      0xa8000000: 0x808000,
      0xb8000000: 0x800202,
      0xc8000000: 0x800002,
      0xd8000000: 0x8002,
      0xe8000000: 0x202,
      0xf8000000: 0x800000,
      0x1: 0x8000,
      0x10000001: 0x2,
      0x20000001: 0x808200,
      0x30000001: 0x800000,
      0x40000001: 0x808002,
      0x50000001: 0x8200,
      0x60000001: 0x200,
      0x70000001: 0x800202,
      0x80000001: 0x808202,
      0x90000001: 0x808000,
      0xa0000001: 0x800002,
      0xb0000001: 0x8202,
      0xc0000001: 0x202,
      0xd0000001: 0x800200,
      0xe0000001: 0x8002,
      0xf0000001: 0x0,
      0x8000001: 0x808202,
      0x18000001: 0x808000,
      0x28000001: 0x800000,
      0x38000001: 0x200,
      0x48000001: 0x8000,
      0x58000001: 0x800002,
      0x68000001: 0x2,
      0x78000001: 0x8202,
      0x88000001: 0x8002,
      0x98000001: 0x800202,
      0xa8000001: 0x202,
      0xb8000001: 0x808200,
      0xc8000001: 0x800200,
      0xd8000001: 0x0,
      0xe8000001: 0x8200,
      0xf8000001: 0x808002
    }, {
      0x0: 0x40084010,
      0x1000000: 0x4000,
      0x2000000: 0x80000,
      0x3000000: 0x40080010,
      0x4000000: 0x40000010,
      0x5000000: 0x40084000,
      0x6000000: 0x40004000,
      0x7000000: 0x10,
      0x8000000: 0x84000,
      0x9000000: 0x40004010,
      0xa000000: 0x40000000,
      0xb000000: 0x84010,
      0xc000000: 0x80010,
      0xd000000: 0x0,
      0xe000000: 0x4010,
      0xf000000: 0x40080000,
      0x800000: 0x40004000,
      0x1800000: 0x84010,
      0x2800000: 0x10,
      0x3800000: 0x40004010,
      0x4800000: 0x40084010,
      0x5800000: 0x40000000,
      0x6800000: 0x80000,
      0x7800000: 0x40080010,
      0x8800000: 0x80010,
      0x9800000: 0x0,
      0xa800000: 0x4000,
      0xb800000: 0x40080000,
      0xc800000: 0x40000010,
      0xd800000: 0x84000,
      0xe800000: 0x40084000,
      0xf800000: 0x4010,
      0x10000000: 0x0,
      0x11000000: 0x40080010,
      0x12000000: 0x40004010,
      0x13000000: 0x40084000,
      0x14000000: 0x40080000,
      0x15000000: 0x10,
      0x16000000: 0x84010,
      0x17000000: 0x4000,
      0x18000000: 0x4010,
      0x19000000: 0x80000,
      0x1a000000: 0x80010,
      0x1b000000: 0x40000010,
      0x1c000000: 0x84000,
      0x1d000000: 0x40004000,
      0x1e000000: 0x40000000,
      0x1f000000: 0x40084010,
      0x10800000: 0x84010,
      0x11800000: 0x80000,
      0x12800000: 0x40080000,
      0x13800000: 0x4000,
      0x14800000: 0x40004000,
      0x15800000: 0x40084010,
      0x16800000: 0x10,
      0x17800000: 0x40000000,
      0x18800000: 0x40084000,
      0x19800000: 0x40000010,
      0x1a800000: 0x40004010,
      0x1b800000: 0x80010,
      0x1c800000: 0x0,
      0x1d800000: 0x4010,
      0x1e800000: 0x40080010,
      0x1f800000: 0x84000
    }, {
      0x0: 0x104,
      0x100000: 0x0,
      0x200000: 0x4000100,
      0x300000: 0x10104,
      0x400000: 0x10004,
      0x500000: 0x4000004,
      0x600000: 0x4010104,
      0x700000: 0x4010000,
      0x800000: 0x4000000,
      0x900000: 0x4010100,
      0xa00000: 0x10100,
      0xb00000: 0x4010004,
      0xc00000: 0x4000104,
      0xd00000: 0x10000,
      0xe00000: 0x4,
      0xf00000: 0x100,
      0x80000: 0x4010100,
      0x180000: 0x4010004,
      0x280000: 0x0,
      0x380000: 0x4000100,
      0x480000: 0x4000004,
      0x580000: 0x10000,
      0x680000: 0x10004,
      0x780000: 0x104,
      0x880000: 0x4,
      0x980000: 0x100,
      0xa80000: 0x4010000,
      0xb80000: 0x10104,
      0xc80000: 0x10100,
      0xd80000: 0x4000104,
      0xe80000: 0x4010104,
      0xf80000: 0x4000000,
      0x1000000: 0x4010100,
      0x1100000: 0x10004,
      0x1200000: 0x10000,
      0x1300000: 0x4000100,
      0x1400000: 0x100,
      0x1500000: 0x4010104,
      0x1600000: 0x4000004,
      0x1700000: 0x0,
      0x1800000: 0x4000104,
      0x1900000: 0x4000000,
      0x1a00000: 0x4,
      0x1b00000: 0x10100,
      0x1c00000: 0x4010000,
      0x1d00000: 0x104,
      0x1e00000: 0x10104,
      0x1f00000: 0x4010004,
      0x1080000: 0x4000000,
      0x1180000: 0x104,
      0x1280000: 0x4010100,
      0x1380000: 0x0,
      0x1480000: 0x10004,
      0x1580000: 0x4000100,
      0x1680000: 0x100,
      0x1780000: 0x4010004,
      0x1880000: 0x10000,
      0x1980000: 0x4010104,
      0x1a80000: 0x10104,
      0x1b80000: 0x4000004,
      0x1c80000: 0x4000104,
      0x1d80000: 0x4010000,
      0x1e80000: 0x4,
      0x1f80000: 0x10100
    }, {
      0x0: 0x80401000,
      0x10000: 0x80001040,
      0x20000: 0x401040,
      0x30000: 0x80400000,
      0x40000: 0x0,
      0x50000: 0x401000,
      0x60000: 0x80000040,
      0x70000: 0x400040,
      0x80000: 0x80000000,
      0x90000: 0x400000,
      0xa0000: 0x40,
      0xb0000: 0x80001000,
      0xc0000: 0x80400040,
      0xd0000: 0x1040,
      0xe0000: 0x1000,
      0xf0000: 0x80401040,
      0x8000: 0x80001040,
      0x18000: 0x40,
      0x28000: 0x80400040,
      0x38000: 0x80001000,
      0x48000: 0x401000,
      0x58000: 0x80401040,
      0x68000: 0x0,
      0x78000: 0x80400000,
      0x88000: 0x1000,
      0x98000: 0x80401000,
      0xa8000: 0x400000,
      0xb8000: 0x1040,
      0xc8000: 0x80000000,
      0xd8000: 0x400040,
      0xe8000: 0x401040,
      0xf8000: 0x80000040,
      0x100000: 0x400040,
      0x110000: 0x401000,
      0x120000: 0x80000040,
      0x130000: 0x0,
      0x140000: 0x1040,
      0x150000: 0x80400040,
      0x160000: 0x80401000,
      0x170000: 0x80001040,
      0x180000: 0x80401040,
      0x190000: 0x80000000,
      0x1a0000: 0x80400000,
      0x1b0000: 0x401040,
      0x1c0000: 0x80001000,
      0x1d0000: 0x400000,
      0x1e0000: 0x40,
      0x1f0000: 0x1000,
      0x108000: 0x80400000,
      0x118000: 0x80401040,
      0x128000: 0x0,
      0x138000: 0x401000,
      0x148000: 0x400040,
      0x158000: 0x80000000,
      0x168000: 0x80001040,
      0x178000: 0x40,
      0x188000: 0x80000040,
      0x198000: 0x1000,
      0x1a8000: 0x80001000,
      0x1b8000: 0x80400040,
      0x1c8000: 0x1040,
      0x1d8000: 0x80401000,
      0x1e8000: 0x400000,
      0x1f8000: 0x401040
    }, {
      0x0: 0x80,
      0x1000: 0x1040000,
      0x2000: 0x40000,
      0x3000: 0x20000000,
      0x4000: 0x20040080,
      0x5000: 0x1000080,
      0x6000: 0x21000080,
      0x7000: 0x40080,
      0x8000: 0x1000000,
      0x9000: 0x20040000,
      0xa000: 0x20000080,
      0xb000: 0x21040080,
      0xc000: 0x21040000,
      0xd000: 0x0,
      0xe000: 0x1040080,
      0xf000: 0x21000000,
      0x800: 0x1040080,
      0x1800: 0x21000080,
      0x2800: 0x80,
      0x3800: 0x1040000,
      0x4800: 0x40000,
      0x5800: 0x20040080,
      0x6800: 0x21040000,
      0x7800: 0x20000000,
      0x8800: 0x20040000,
      0x9800: 0x0,
      0xa800: 0x21040080,
      0xb800: 0x1000080,
      0xc800: 0x20000080,
      0xd800: 0x21000000,
      0xe800: 0x1000000,
      0xf800: 0x40080,
      0x10000: 0x40000,
      0x11000: 0x80,
      0x12000: 0x20000000,
      0x13000: 0x21000080,
      0x14000: 0x1000080,
      0x15000: 0x21040000,
      0x16000: 0x20040080,
      0x17000: 0x1000000,
      0x18000: 0x21040080,
      0x19000: 0x21000000,
      0x1a000: 0x1040000,
      0x1b000: 0x20040000,
      0x1c000: 0x40080,
      0x1d000: 0x20000080,
      0x1e000: 0x0,
      0x1f000: 0x1040080,
      0x10800: 0x21000080,
      0x11800: 0x1000000,
      0x12800: 0x1040000,
      0x13800: 0x20040080,
      0x14800: 0x20000000,
      0x15800: 0x1040080,
      0x16800: 0x80,
      0x17800: 0x21040000,
      0x18800: 0x40080,
      0x19800: 0x21040080,
      0x1a800: 0x0,
      0x1b800: 0x21000000,
      0x1c800: 0x1000080,
      0x1d800: 0x40000,
      0x1e800: 0x20040000,
      0x1f800: 0x20000080
    }, {
      0x0: 0x10000008,
      0x100: 0x2000,
      0x200: 0x10200000,
      0x300: 0x10202008,
      0x400: 0x10002000,
      0x500: 0x200000,
      0x600: 0x200008,
      0x700: 0x10000000,
      0x800: 0x0,
      0x900: 0x10002008,
      0xa00: 0x202000,
      0xb00: 0x8,
      0xc00: 0x10200008,
      0xd00: 0x202008,
      0xe00: 0x2008,
      0xf00: 0x10202000,
      0x80: 0x10200000,
      0x180: 0x10202008,
      0x280: 0x8,
      0x380: 0x200000,
      0x480: 0x202008,
      0x580: 0x10000008,
      0x680: 0x10002000,
      0x780: 0x2008,
      0x880: 0x200008,
      0x980: 0x2000,
      0xa80: 0x10002008,
      0xb80: 0x10200008,
      0xc80: 0x0,
      0xd80: 0x10202000,
      0xe80: 0x202000,
      0xf80: 0x10000000,
      0x1000: 0x10002000,
      0x1100: 0x10200008,
      0x1200: 0x10202008,
      0x1300: 0x2008,
      0x1400: 0x200000,
      0x1500: 0x10000000,
      0x1600: 0x10000008,
      0x1700: 0x202000,
      0x1800: 0x202008,
      0x1900: 0x0,
      0x1a00: 0x8,
      0x1b00: 0x10200000,
      0x1c00: 0x2000,
      0x1d00: 0x10002008,
      0x1e00: 0x10202000,
      0x1f00: 0x200008,
      0x1080: 0x8,
      0x1180: 0x202000,
      0x1280: 0x200000,
      0x1380: 0x10000008,
      0x1480: 0x10002000,
      0x1580: 0x2008,
      0x1680: 0x10202008,
      0x1780: 0x10200000,
      0x1880: 0x10202000,
      0x1980: 0x10200008,
      0x1a80: 0x2000,
      0x1b80: 0x202008,
      0x1c80: 0x200008,
      0x1d80: 0x0,
      0x1e80: 0x10000000,
      0x1f80: 0x10002008
    }, {
      0x0: 0x100000,
      0x10: 0x2000401,
      0x20: 0x400,
      0x30: 0x100401,
      0x40: 0x2100401,
      0x50: 0x0,
      0x60: 0x1,
      0x70: 0x2100001,
      0x80: 0x2000400,
      0x90: 0x100001,
      0xa0: 0x2000001,
      0xb0: 0x2100400,
      0xc0: 0x2100000,
      0xd0: 0x401,
      0xe0: 0x100400,
      0xf0: 0x2000000,
      0x8: 0x2100001,
      0x18: 0x0,
      0x28: 0x2000401,
      0x38: 0x2100400,
      0x48: 0x100000,
      0x58: 0x2000001,
      0x68: 0x2000000,
      0x78: 0x401,
      0x88: 0x100401,
      0x98: 0x2000400,
      0xa8: 0x2100000,
      0xb8: 0x100001,
      0xc8: 0x400,
      0xd8: 0x2100401,
      0xe8: 0x1,
      0xf8: 0x100400,
      0x100: 0x2000000,
      0x110: 0x100000,
      0x120: 0x2000401,
      0x130: 0x2100001,
      0x140: 0x100001,
      0x150: 0x2000400,
      0x160: 0x2100400,
      0x170: 0x100401,
      0x180: 0x401,
      0x190: 0x2100401,
      0x1a0: 0x100400,
      0x1b0: 0x1,
      0x1c0: 0x0,
      0x1d0: 0x2100000,
      0x1e0: 0x2000001,
      0x1f0: 0x400,
      0x108: 0x100400,
      0x118: 0x2000401,
      0x128: 0x2100001,
      0x138: 0x1,
      0x148: 0x2000000,
      0x158: 0x100000,
      0x168: 0x401,
      0x178: 0x2100400,
      0x188: 0x2000001,
      0x198: 0x2100000,
      0x1a8: 0x0,
      0x1b8: 0x2100401,
      0x1c8: 0x100401,
      0x1d8: 0x400,
      0x1e8: 0x2000400,
      0x1f8: 0x100001
    }, {
      0x0: 0x8000820,
      0x1: 0x20000,
      0x2: 0x8000000,
      0x3: 0x20,
      0x4: 0x20020,
      0x5: 0x8020820,
      0x6: 0x8020800,
      0x7: 0x800,
      0x8: 0x8020000,
      0x9: 0x8000800,
      0xa: 0x20800,
      0xb: 0x8020020,
      0xc: 0x820,
      0xd: 0x0,
      0xe: 0x8000020,
      0xf: 0x20820,
      0x80000000: 0x800,
      0x80000001: 0x8020820,
      0x80000002: 0x8000820,
      0x80000003: 0x8000000,
      0x80000004: 0x8020000,
      0x80000005: 0x20800,
      0x80000006: 0x20820,
      0x80000007: 0x20,
      0x80000008: 0x8000020,
      0x80000009: 0x820,
      0x8000000a: 0x20020,
      0x8000000b: 0x8020800,
      0x8000000c: 0x0,
      0x8000000d: 0x8020020,
      0x8000000e: 0x8000800,
      0x8000000f: 0x20000,
      0x10: 0x20820,
      0x11: 0x8020800,
      0x12: 0x20,
      0x13: 0x800,
      0x14: 0x8000800,
      0x15: 0x8000020,
      0x16: 0x8020020,
      0x17: 0x20000,
      0x18: 0x0,
      0x19: 0x20020,
      0x1a: 0x8020000,
      0x1b: 0x8000820,
      0x1c: 0x8020820,
      0x1d: 0x20800,
      0x1e: 0x820,
      0x1f: 0x8000000,
      0x80000010: 0x20000,
      0x80000011: 0x800,
      0x80000012: 0x8020020,
      0x80000013: 0x20820,
      0x80000014: 0x20,
      0x80000015: 0x8020000,
      0x80000016: 0x8000000,
      0x80000017: 0x8000820,
      0x80000018: 0x8020820,
      0x80000019: 0x8000020,
      0x8000001a: 0x8000800,
      0x8000001b: 0x0,
      0x8000001c: 0x20800,
      0x8000001d: 0x820,
      0x8000001e: 0x20020,
      0x8000001f: 0x8020800
    }];

    // Masks that select the SBOX input
    var SBOX_MASK = [0xf8000001, 0x1f800000, 0x01f80000, 0x001f8000, 0x0001f800, 0x00001f80, 0x000001f8, 0x8000001f];

    /**
     * DES block cipher algorithm.
     */
    var DES = C_algo.DES = BlockCipher.extend({
      _doReset: function () {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;

        // Select 56 bits according to PC1
        var keyBits = [];
        for (var i = 0; i < 56; i++) {
          var keyBitPos = PC1[i] - 1;
          keyBits[i] = keyWords[keyBitPos >>> 5] >>> 31 - keyBitPos % 32 & 1;
        }

        // Assemble 16 subkeys
        var subKeys = this._subKeys = [];
        for (var nSubKey = 0; nSubKey < 16; nSubKey++) {
          // Create subkey
          var subKey = subKeys[nSubKey] = [];

          // Shortcut
          var bitShift = BIT_SHIFTS[nSubKey];

          // Select 48 bits according to PC2
          for (var i = 0; i < 24; i++) {
            // Select from the left 28 key bits
            subKey[i / 6 | 0] |= keyBits[(PC2[i] - 1 + bitShift) % 28] << 31 - i % 6;

            // Select from the right 28 key bits
            subKey[4 + (i / 6 | 0)] |= keyBits[28 + (PC2[i + 24] - 1 + bitShift) % 28] << 31 - i % 6;
          }

          // Since each subkey is applied to an expanded 32-bit input,
          // the subkey can be broken into 8 values scaled to 32-bits,
          // which allows the key to be used without expansion
          subKey[0] = subKey[0] << 1 | subKey[0] >>> 31;
          for (var i = 1; i < 7; i++) {
            subKey[i] = subKey[i] >>> (i - 1) * 4 + 3;
          }
          subKey[7] = subKey[7] << 5 | subKey[7] >>> 27;
        }

        // Compute inverse subkeys
        var invSubKeys = this._invSubKeys = [];
        for (var i = 0; i < 16; i++) {
          invSubKeys[i] = subKeys[15 - i];
        }
      },
      encryptBlock: function (M, offset) {
        this._doCryptBlock(M, offset, this._subKeys);
      },
      decryptBlock: function (M, offset) {
        this._doCryptBlock(M, offset, this._invSubKeys);
      },
      _doCryptBlock: function (M, offset, subKeys) {
        // Get input
        this._lBlock = M[offset];
        this._rBlock = M[offset + 1];

        // Initial permutation
        exchangeLR.call(this, 4, 0x0f0f0f0f);
        exchangeLR.call(this, 16, 0x0000ffff);
        exchangeRL.call(this, 2, 0x33333333);
        exchangeRL.call(this, 8, 0x00ff00ff);
        exchangeLR.call(this, 1, 0x55555555);

        // Rounds
        for (var round = 0; round < 16; round++) {
          // Shortcuts
          var subKey = subKeys[round];
          var lBlock = this._lBlock;
          var rBlock = this._rBlock;

          // Feistel function
          var f = 0;
          for (var i = 0; i < 8; i++) {
            f |= SBOX_P[i][((rBlock ^ subKey[i]) & SBOX_MASK[i]) >>> 0];
          }
          this._lBlock = rBlock;
          this._rBlock = lBlock ^ f;
        }

        // Undo swap from last round
        var t = this._lBlock;
        this._lBlock = this._rBlock;
        this._rBlock = t;

        // Final permutation
        exchangeLR.call(this, 1, 0x55555555);
        exchangeRL.call(this, 8, 0x00ff00ff);
        exchangeRL.call(this, 2, 0x33333333);
        exchangeLR.call(this, 16, 0x0000ffff);
        exchangeLR.call(this, 4, 0x0f0f0f0f);

        // Set output
        M[offset] = this._lBlock;
        M[offset + 1] = this._rBlock;
      },
      keySize: 64 / 32,
      ivSize: 64 / 32,
      blockSize: 64 / 32
    });

    // Swap bits across the left and right words
    function exchangeLR(offset, mask) {
      var t = (this._lBlock >>> offset ^ this._rBlock) & mask;
      this._rBlock ^= t;
      this._lBlock ^= t << offset;
    }
    function exchangeRL(offset, mask) {
      var t = (this._rBlock >>> offset ^ this._lBlock) & mask;
      this._lBlock ^= t;
      this._rBlock ^= t << offset;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.DES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.DES.decrypt(ciphertext, key, cfg);
     */
    C.DES = BlockCipher._createHelper(DES);

    /**
     * Triple-DES block cipher algorithm.
     */
    var TripleDES = C_algo.TripleDES = BlockCipher.extend({
      _doReset: function () {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;
        // Make sure the key length is valid (64, 128 or >= 192 bit)
        if (keyWords.length !== 2 && keyWords.length !== 4 && keyWords.length < 6) {
          throw new Error('Invalid key length - 3DES requires the key length to be 64, 128, 192 or >192.');
        }

        // Extend the key according to the keying options defined in 3DES standard
        var key1 = keyWords.slice(0, 2);
        var key2 = keyWords.length < 4 ? keyWords.slice(0, 2) : keyWords.slice(2, 4);
        var key3 = keyWords.length < 6 ? keyWords.slice(0, 2) : keyWords.slice(4, 6);

        // Create DES instances
        this._des1 = DES.createEncryptor(WordArray.create(key1));
        this._des2 = DES.createEncryptor(WordArray.create(key2));
        this._des3 = DES.createEncryptor(WordArray.create(key3));
      },
      encryptBlock: function (M, offset) {
        this._des1.encryptBlock(M, offset);
        this._des2.decryptBlock(M, offset);
        this._des3.encryptBlock(M, offset);
      },
      decryptBlock: function (M, offset) {
        this._des3.decryptBlock(M, offset);
        this._des2.encryptBlock(M, offset);
        this._des1.decryptBlock(M, offset);
      },
      keySize: 192 / 32,
      ivSize: 64 / 32,
      blockSize: 64 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.TripleDES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.TripleDES.decrypt(ciphertext, key, cfg);
     */
    C.TripleDES = BlockCipher._createHelper(TripleDES);
  })();
  return CryptoJS.TripleDES;
});
});

var rc4 = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    /**
     * RC4 stream cipher algorithm.
     */
    var RC4 = C_algo.RC4 = StreamCipher.extend({
      _doReset: function () {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;
        var keySigBytes = key.sigBytes;

        // Init sbox
        var S = this._S = [];
        for (var i = 0; i < 256; i++) {
          S[i] = i;
        }

        // Key setup
        for (var i = 0, j = 0; i < 256; i++) {
          var keyByteIndex = i % keySigBytes;
          var keyByte = keyWords[keyByteIndex >>> 2] >>> 24 - keyByteIndex % 4 * 8 & 0xff;
          j = (j + S[i] + keyByte) % 256;

          // Swap
          var t = S[i];
          S[i] = S[j];
          S[j] = t;
        }

        // Counters
        this._i = this._j = 0;
      },
      _doProcessBlock: function (M, offset) {
        M[offset] ^= generateKeystreamWord.call(this);
      },
      keySize: 256 / 32,
      ivSize: 0
    });
    function generateKeystreamWord() {
      // Shortcuts
      var S = this._S;
      var i = this._i;
      var j = this._j;

      // Generate keystream word
      var keystreamWord = 0;
      for (var n = 0; n < 4; n++) {
        i = (i + 1) % 256;
        j = (j + S[i]) % 256;

        // Swap
        var t = S[i];
        S[i] = S[j];
        S[j] = t;
        keystreamWord |= S[(S[i] + S[j]) % 256] << 24 - n * 8;
      }

      // Update counters
      this._i = i;
      this._j = j;
      return keystreamWord;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RC4.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RC4.decrypt(ciphertext, key, cfg);
     */
    C.RC4 = StreamCipher._createHelper(RC4);

    /**
     * Modified RC4 stream cipher algorithm.
     */
    var RC4Drop = C_algo.RC4Drop = RC4.extend({
      /**
       * Configuration options.
       *
       * @property {number} drop The number of keystream words to drop. Default 192
       */
      cfg: RC4.cfg.extend({
        drop: 192
      }),
      _doReset: function () {
        RC4._doReset.call(this);

        // Drop
        for (var i = this.cfg.drop; i > 0; i--) {
          generateKeystreamWord.call(this);
        }
      }
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RC4Drop.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RC4Drop.decrypt(ciphertext, key, cfg);
     */
    C.RC4Drop = StreamCipher._createHelper(RC4Drop);
  })();
  return CryptoJS.RC4;
});
});

var rabbit = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    // Reusable objects
    var S = [];
    var C_ = [];
    var G = [];

    /**
     * Rabbit stream cipher algorithm
     */
    var Rabbit = C_algo.Rabbit = StreamCipher.extend({
      _doReset: function () {
        // Shortcuts
        var K = this._key.words;
        var iv = this.cfg.iv;

        // Swap endian
        for (var i = 0; i < 4; i++) {
          K[i] = (K[i] << 8 | K[i] >>> 24) & 0x00ff00ff | (K[i] << 24 | K[i] >>> 8) & 0xff00ff00;
        }

        // Generate initial state values
        var X = this._X = [K[0], K[3] << 16 | K[2] >>> 16, K[1], K[0] << 16 | K[3] >>> 16, K[2], K[1] << 16 | K[0] >>> 16, K[3], K[2] << 16 | K[1] >>> 16];

        // Generate initial counter values
        var C = this._C = [K[2] << 16 | K[2] >>> 16, K[0] & 0xffff0000 | K[1] & 0x0000ffff, K[3] << 16 | K[3] >>> 16, K[1] & 0xffff0000 | K[2] & 0x0000ffff, K[0] << 16 | K[0] >>> 16, K[2] & 0xffff0000 | K[3] & 0x0000ffff, K[1] << 16 | K[1] >>> 16, K[3] & 0xffff0000 | K[0] & 0x0000ffff];

        // Carry bit
        this._b = 0;

        // Iterate the system four times
        for (var i = 0; i < 4; i++) {
          nextState.call(this);
        }

        // Modify the counters
        for (var i = 0; i < 8; i++) {
          C[i] ^= X[i + 4 & 7];
        }

        // IV setup
        if (iv) {
          // Shortcuts
          var IV = iv.words;
          var IV_0 = IV[0];
          var IV_1 = IV[1];

          // Generate four subvectors
          var i0 = (IV_0 << 8 | IV_0 >>> 24) & 0x00ff00ff | (IV_0 << 24 | IV_0 >>> 8) & 0xff00ff00;
          var i2 = (IV_1 << 8 | IV_1 >>> 24) & 0x00ff00ff | (IV_1 << 24 | IV_1 >>> 8) & 0xff00ff00;
          var i1 = i0 >>> 16 | i2 & 0xffff0000;
          var i3 = i2 << 16 | i0 & 0x0000ffff;

          // Modify counter values
          C[0] ^= i0;
          C[1] ^= i1;
          C[2] ^= i2;
          C[3] ^= i3;
          C[4] ^= i0;
          C[5] ^= i1;
          C[6] ^= i2;
          C[7] ^= i3;

          // Iterate the system four times
          for (var i = 0; i < 4; i++) {
            nextState.call(this);
          }
        }
      },
      _doProcessBlock: function (M, offset) {
        // Shortcut
        var X = this._X;

        // Iterate the system
        nextState.call(this);

        // Generate four keystream words
        S[0] = X[0] ^ X[5] >>> 16 ^ X[3] << 16;
        S[1] = X[2] ^ X[7] >>> 16 ^ X[5] << 16;
        S[2] = X[4] ^ X[1] >>> 16 ^ X[7] << 16;
        S[3] = X[6] ^ X[3] >>> 16 ^ X[1] << 16;
        for (var i = 0; i < 4; i++) {
          // Swap endian
          S[i] = (S[i] << 8 | S[i] >>> 24) & 0x00ff00ff | (S[i] << 24 | S[i] >>> 8) & 0xff00ff00;

          // Encrypt
          M[offset + i] ^= S[i];
        }
      },
      blockSize: 128 / 32,
      ivSize: 64 / 32
    });
    function nextState() {
      // Shortcuts
      var X = this._X;
      var C = this._C;

      // Save old counter values
      for (var i = 0; i < 8; i++) {
        C_[i] = C[i];
      }

      // Calculate new counter values
      C[0] = C[0] + 0x4d34d34d + this._b | 0;
      C[1] = C[1] + 0xd34d34d3 + (C[0] >>> 0 < C_[0] >>> 0 ? 1 : 0) | 0;
      C[2] = C[2] + 0x34d34d34 + (C[1] >>> 0 < C_[1] >>> 0 ? 1 : 0) | 0;
      C[3] = C[3] + 0x4d34d34d + (C[2] >>> 0 < C_[2] >>> 0 ? 1 : 0) | 0;
      C[4] = C[4] + 0xd34d34d3 + (C[3] >>> 0 < C_[3] >>> 0 ? 1 : 0) | 0;
      C[5] = C[5] + 0x34d34d34 + (C[4] >>> 0 < C_[4] >>> 0 ? 1 : 0) | 0;
      C[6] = C[6] + 0x4d34d34d + (C[5] >>> 0 < C_[5] >>> 0 ? 1 : 0) | 0;
      C[7] = C[7] + 0xd34d34d3 + (C[6] >>> 0 < C_[6] >>> 0 ? 1 : 0) | 0;
      this._b = C[7] >>> 0 < C_[7] >>> 0 ? 1 : 0;

      // Calculate the g-values
      for (var i = 0; i < 8; i++) {
        var gx = X[i] + C[i];

        // Construct high and low argument for squaring
        var ga = gx & 0xffff;
        var gb = gx >>> 16;

        // Calculate high and low result of squaring
        var gh = ((ga * ga >>> 17) + ga * gb >>> 15) + gb * gb;
        var gl = ((gx & 0xffff0000) * gx | 0) + ((gx & 0x0000ffff) * gx | 0);

        // High XOR low
        G[i] = gh ^ gl;
      }

      // Calculate new state values
      X[0] = G[0] + (G[7] << 16 | G[7] >>> 16) + (G[6] << 16 | G[6] >>> 16) | 0;
      X[1] = G[1] + (G[0] << 8 | G[0] >>> 24) + G[7] | 0;
      X[2] = G[2] + (G[1] << 16 | G[1] >>> 16) + (G[0] << 16 | G[0] >>> 16) | 0;
      X[3] = G[3] + (G[2] << 8 | G[2] >>> 24) + G[1] | 0;
      X[4] = G[4] + (G[3] << 16 | G[3] >>> 16) + (G[2] << 16 | G[2] >>> 16) | 0;
      X[5] = G[5] + (G[4] << 8 | G[4] >>> 24) + G[3] | 0;
      X[6] = G[6] + (G[5] << 16 | G[5] >>> 16) + (G[4] << 16 | G[4] >>> 16) | 0;
      X[7] = G[7] + (G[6] << 8 | G[6] >>> 24) + G[5] | 0;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.Rabbit.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.Rabbit.decrypt(ciphertext, key, cfg);
     */
    C.Rabbit = StreamCipher._createHelper(Rabbit);
  })();
  return CryptoJS.Rabbit;
});
});

var rabbitLegacy = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    // Reusable objects
    var S = [];
    var C_ = [];
    var G = [];

    /**
     * Rabbit stream cipher algorithm.
     *
     * This is a legacy version that neglected to convert the key to little-endian.
     * This error doesn't affect the cipher's security,
     * but it does affect its compatibility with other implementations.
     */
    var RabbitLegacy = C_algo.RabbitLegacy = StreamCipher.extend({
      _doReset: function () {
        // Shortcuts
        var K = this._key.words;
        var iv = this.cfg.iv;

        // Generate initial state values
        var X = this._X = [K[0], K[3] << 16 | K[2] >>> 16, K[1], K[0] << 16 | K[3] >>> 16, K[2], K[1] << 16 | K[0] >>> 16, K[3], K[2] << 16 | K[1] >>> 16];

        // Generate initial counter values
        var C = this._C = [K[2] << 16 | K[2] >>> 16, K[0] & 0xffff0000 | K[1] & 0x0000ffff, K[3] << 16 | K[3] >>> 16, K[1] & 0xffff0000 | K[2] & 0x0000ffff, K[0] << 16 | K[0] >>> 16, K[2] & 0xffff0000 | K[3] & 0x0000ffff, K[1] << 16 | K[1] >>> 16, K[3] & 0xffff0000 | K[0] & 0x0000ffff];

        // Carry bit
        this._b = 0;

        // Iterate the system four times
        for (var i = 0; i < 4; i++) {
          nextState.call(this);
        }

        // Modify the counters
        for (var i = 0; i < 8; i++) {
          C[i] ^= X[i + 4 & 7];
        }

        // IV setup
        if (iv) {
          // Shortcuts
          var IV = iv.words;
          var IV_0 = IV[0];
          var IV_1 = IV[1];

          // Generate four subvectors
          var i0 = (IV_0 << 8 | IV_0 >>> 24) & 0x00ff00ff | (IV_0 << 24 | IV_0 >>> 8) & 0xff00ff00;
          var i2 = (IV_1 << 8 | IV_1 >>> 24) & 0x00ff00ff | (IV_1 << 24 | IV_1 >>> 8) & 0xff00ff00;
          var i1 = i0 >>> 16 | i2 & 0xffff0000;
          var i3 = i2 << 16 | i0 & 0x0000ffff;

          // Modify counter values
          C[0] ^= i0;
          C[1] ^= i1;
          C[2] ^= i2;
          C[3] ^= i3;
          C[4] ^= i0;
          C[5] ^= i1;
          C[6] ^= i2;
          C[7] ^= i3;

          // Iterate the system four times
          for (var i = 0; i < 4; i++) {
            nextState.call(this);
          }
        }
      },
      _doProcessBlock: function (M, offset) {
        // Shortcut
        var X = this._X;

        // Iterate the system
        nextState.call(this);

        // Generate four keystream words
        S[0] = X[0] ^ X[5] >>> 16 ^ X[3] << 16;
        S[1] = X[2] ^ X[7] >>> 16 ^ X[5] << 16;
        S[2] = X[4] ^ X[1] >>> 16 ^ X[7] << 16;
        S[3] = X[6] ^ X[3] >>> 16 ^ X[1] << 16;
        for (var i = 0; i < 4; i++) {
          // Swap endian
          S[i] = (S[i] << 8 | S[i] >>> 24) & 0x00ff00ff | (S[i] << 24 | S[i] >>> 8) & 0xff00ff00;

          // Encrypt
          M[offset + i] ^= S[i];
        }
      },
      blockSize: 128 / 32,
      ivSize: 64 / 32
    });
    function nextState() {
      // Shortcuts
      var X = this._X;
      var C = this._C;

      // Save old counter values
      for (var i = 0; i < 8; i++) {
        C_[i] = C[i];
      }

      // Calculate new counter values
      C[0] = C[0] + 0x4d34d34d + this._b | 0;
      C[1] = C[1] + 0xd34d34d3 + (C[0] >>> 0 < C_[0] >>> 0 ? 1 : 0) | 0;
      C[2] = C[2] + 0x34d34d34 + (C[1] >>> 0 < C_[1] >>> 0 ? 1 : 0) | 0;
      C[3] = C[3] + 0x4d34d34d + (C[2] >>> 0 < C_[2] >>> 0 ? 1 : 0) | 0;
      C[4] = C[4] + 0xd34d34d3 + (C[3] >>> 0 < C_[3] >>> 0 ? 1 : 0) | 0;
      C[5] = C[5] + 0x34d34d34 + (C[4] >>> 0 < C_[4] >>> 0 ? 1 : 0) | 0;
      C[6] = C[6] + 0x4d34d34d + (C[5] >>> 0 < C_[5] >>> 0 ? 1 : 0) | 0;
      C[7] = C[7] + 0xd34d34d3 + (C[6] >>> 0 < C_[6] >>> 0 ? 1 : 0) | 0;
      this._b = C[7] >>> 0 < C_[7] >>> 0 ? 1 : 0;

      // Calculate the g-values
      for (var i = 0; i < 8; i++) {
        var gx = X[i] + C[i];

        // Construct high and low argument for squaring
        var ga = gx & 0xffff;
        var gb = gx >>> 16;

        // Calculate high and low result of squaring
        var gh = ((ga * ga >>> 17) + ga * gb >>> 15) + gb * gb;
        var gl = ((gx & 0xffff0000) * gx | 0) + ((gx & 0x0000ffff) * gx | 0);

        // High XOR low
        G[i] = gh ^ gl;
      }

      // Calculate new state values
      X[0] = G[0] + (G[7] << 16 | G[7] >>> 16) + (G[6] << 16 | G[6] >>> 16) | 0;
      X[1] = G[1] + (G[0] << 8 | G[0] >>> 24) + G[7] | 0;
      X[2] = G[2] + (G[1] << 16 | G[1] >>> 16) + (G[0] << 16 | G[0] >>> 16) | 0;
      X[3] = G[3] + (G[2] << 8 | G[2] >>> 24) + G[1] | 0;
      X[4] = G[4] + (G[3] << 16 | G[3] >>> 16) + (G[2] << 16 | G[2] >>> 16) | 0;
      X[5] = G[5] + (G[4] << 8 | G[4] >>> 24) + G[3] | 0;
      X[6] = G[6] + (G[5] << 16 | G[5] >>> 16) + (G[4] << 16 | G[4] >>> 16) | 0;
      X[7] = G[7] + (G[6] << 8 | G[6] >>> 24) + G[5] | 0;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RabbitLegacy.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RabbitLegacy.decrypt(ciphertext, key, cfg);
     */
    C.RabbitLegacy = StreamCipher._createHelper(RabbitLegacy);
  })();
  return CryptoJS.RabbitLegacy;
});
});

var blowfish = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, encBase64, md5, evpkdf, cipherCore);
  }
})(commonjsGlobal, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;
    const N = 16;

    //Origin pbox and sbox, derived from PI
    const ORIG_P = [0x243F6A88, 0x85A308D3, 0x13198A2E, 0x03707344, 0xA4093822, 0x299F31D0, 0x082EFA98, 0xEC4E6C89, 0x452821E6, 0x38D01377, 0xBE5466CF, 0x34E90C6C, 0xC0AC29B7, 0xC97C50DD, 0x3F84D5B5, 0xB5470917, 0x9216D5D9, 0x8979FB1B];
    const ORIG_S = [[0xD1310BA6, 0x98DFB5AC, 0x2FFD72DB, 0xD01ADFB7, 0xB8E1AFED, 0x6A267E96, 0xBA7C9045, 0xF12C7F99, 0x24A19947, 0xB3916CF7, 0x0801F2E2, 0x858EFC16, 0x636920D8, 0x71574E69, 0xA458FEA3, 0xF4933D7E, 0x0D95748F, 0x728EB658, 0x718BCD58, 0x82154AEE, 0x7B54A41D, 0xC25A59B5, 0x9C30D539, 0x2AF26013, 0xC5D1B023, 0x286085F0, 0xCA417918, 0xB8DB38EF, 0x8E79DCB0, 0x603A180E, 0x6C9E0E8B, 0xB01E8A3E, 0xD71577C1, 0xBD314B27, 0x78AF2FDA, 0x55605C60, 0xE65525F3, 0xAA55AB94, 0x57489862, 0x63E81440, 0x55CA396A, 0x2AAB10B6, 0xB4CC5C34, 0x1141E8CE, 0xA15486AF, 0x7C72E993, 0xB3EE1411, 0x636FBC2A, 0x2BA9C55D, 0x741831F6, 0xCE5C3E16, 0x9B87931E, 0xAFD6BA33, 0x6C24CF5C, 0x7A325381, 0x28958677, 0x3B8F4898, 0x6B4BB9AF, 0xC4BFE81B, 0x66282193, 0x61D809CC, 0xFB21A991, 0x487CAC60, 0x5DEC8032, 0xEF845D5D, 0xE98575B1, 0xDC262302, 0xEB651B88, 0x23893E81, 0xD396ACC5, 0x0F6D6FF3, 0x83F44239, 0x2E0B4482, 0xA4842004, 0x69C8F04A, 0x9E1F9B5E, 0x21C66842, 0xF6E96C9A, 0x670C9C61, 0xABD388F0, 0x6A51A0D2, 0xD8542F68, 0x960FA728, 0xAB5133A3, 0x6EEF0B6C, 0x137A3BE4, 0xBA3BF050, 0x7EFB2A98, 0xA1F1651D, 0x39AF0176, 0x66CA593E, 0x82430E88, 0x8CEE8619, 0x456F9FB4, 0x7D84A5C3, 0x3B8B5EBE, 0xE06F75D8, 0x85C12073, 0x401A449F, 0x56C16AA6, 0x4ED3AA62, 0x363F7706, 0x1BFEDF72, 0x429B023D, 0x37D0D724, 0xD00A1248, 0xDB0FEAD3, 0x49F1C09B, 0x075372C9, 0x80991B7B, 0x25D479D8, 0xF6E8DEF7, 0xE3FE501A, 0xB6794C3B, 0x976CE0BD, 0x04C006BA, 0xC1A94FB6, 0x409F60C4, 0x5E5C9EC2, 0x196A2463, 0x68FB6FAF, 0x3E6C53B5, 0x1339B2EB, 0x3B52EC6F, 0x6DFC511F, 0x9B30952C, 0xCC814544, 0xAF5EBD09, 0xBEE3D004, 0xDE334AFD, 0x660F2807, 0x192E4BB3, 0xC0CBA857, 0x45C8740F, 0xD20B5F39, 0xB9D3FBDB, 0x5579C0BD, 0x1A60320A, 0xD6A100C6, 0x402C7279, 0x679F25FE, 0xFB1FA3CC, 0x8EA5E9F8, 0xDB3222F8, 0x3C7516DF, 0xFD616B15, 0x2F501EC8, 0xAD0552AB, 0x323DB5FA, 0xFD238760, 0x53317B48, 0x3E00DF82, 0x9E5C57BB, 0xCA6F8CA0, 0x1A87562E, 0xDF1769DB, 0xD542A8F6, 0x287EFFC3, 0xAC6732C6, 0x8C4F5573, 0x695B27B0, 0xBBCA58C8, 0xE1FFA35D, 0xB8F011A0, 0x10FA3D98, 0xFD2183B8, 0x4AFCB56C, 0x2DD1D35B, 0x9A53E479, 0xB6F84565, 0xD28E49BC, 0x4BFB9790, 0xE1DDF2DA, 0xA4CB7E33, 0x62FB1341, 0xCEE4C6E8, 0xEF20CADA, 0x36774C01, 0xD07E9EFE, 0x2BF11FB4, 0x95DBDA4D, 0xAE909198, 0xEAAD8E71, 0x6B93D5A0, 0xD08ED1D0, 0xAFC725E0, 0x8E3C5B2F, 0x8E7594B7, 0x8FF6E2FB, 0xF2122B64, 0x8888B812, 0x900DF01C, 0x4FAD5EA0, 0x688FC31C, 0xD1CFF191, 0xB3A8C1AD, 0x2F2F2218, 0xBE0E1777, 0xEA752DFE, 0x8B021FA1, 0xE5A0CC0F, 0xB56F74E8, 0x18ACF3D6, 0xCE89E299, 0xB4A84FE0, 0xFD13E0B7, 0x7CC43B81, 0xD2ADA8D9, 0x165FA266, 0x80957705, 0x93CC7314, 0x211A1477, 0xE6AD2065, 0x77B5FA86, 0xC75442F5, 0xFB9D35CF, 0xEBCDAF0C, 0x7B3E89A0, 0xD6411BD3, 0xAE1E7E49, 0x00250E2D, 0x2071B35E, 0x226800BB, 0x57B8E0AF, 0x2464369B, 0xF009B91E, 0x5563911D, 0x59DFA6AA, 0x78C14389, 0xD95A537F, 0x207D5BA2, 0x02E5B9C5, 0x83260376, 0x6295CFA9, 0x11C81968, 0x4E734A41, 0xB3472DCA, 0x7B14A94A, 0x1B510052, 0x9A532915, 0xD60F573F, 0xBC9BC6E4, 0x2B60A476, 0x81E67400, 0x08BA6FB5, 0x571BE91F, 0xF296EC6B, 0x2A0DD915, 0xB6636521, 0xE7B9F9B6, 0xFF34052E, 0xC5855664, 0x53B02D5D, 0xA99F8FA1, 0x08BA4799, 0x6E85076A], [0x4B7A70E9, 0xB5B32944, 0xDB75092E, 0xC4192623, 0xAD6EA6B0, 0x49A7DF7D, 0x9CEE60B8, 0x8FEDB266, 0xECAA8C71, 0x699A17FF, 0x5664526C, 0xC2B19EE1, 0x193602A5, 0x75094C29, 0xA0591340, 0xE4183A3E, 0x3F54989A, 0x5B429D65, 0x6B8FE4D6, 0x99F73FD6, 0xA1D29C07, 0xEFE830F5, 0x4D2D38E6, 0xF0255DC1, 0x4CDD2086, 0x8470EB26, 0x6382E9C6, 0x021ECC5E, 0x09686B3F, 0x3EBAEFC9, 0x3C971814, 0x6B6A70A1, 0x687F3584, 0x52A0E286, 0xB79C5305, 0xAA500737, 0x3E07841C, 0x7FDEAE5C, 0x8E7D44EC, 0x5716F2B8, 0xB03ADA37, 0xF0500C0D, 0xF01C1F04, 0x0200B3FF, 0xAE0CF51A, 0x3CB574B2, 0x25837A58, 0xDC0921BD, 0xD19113F9, 0x7CA92FF6, 0x94324773, 0x22F54701, 0x3AE5E581, 0x37C2DADC, 0xC8B57634, 0x9AF3DDA7, 0xA9446146, 0x0FD0030E, 0xECC8C73E, 0xA4751E41, 0xE238CD99, 0x3BEA0E2F, 0x3280BBA1, 0x183EB331, 0x4E548B38, 0x4F6DB908, 0x6F420D03, 0xF60A04BF, 0x2CB81290, 0x24977C79, 0x5679B072, 0xBCAF89AF, 0xDE9A771F, 0xD9930810, 0xB38BAE12, 0xDCCF3F2E, 0x5512721F, 0x2E6B7124, 0x501ADDE6, 0x9F84CD87, 0x7A584718, 0x7408DA17, 0xBC9F9ABC, 0xE94B7D8C, 0xEC7AEC3A, 0xDB851DFA, 0x63094366, 0xC464C3D2, 0xEF1C1847, 0x3215D908, 0xDD433B37, 0x24C2BA16, 0x12A14D43, 0x2A65C451, 0x50940002, 0x133AE4DD, 0x71DFF89E, 0x10314E55, 0x81AC77D6, 0x5F11199B, 0x043556F1, 0xD7A3C76B, 0x3C11183B, 0x5924A509, 0xF28FE6ED, 0x97F1FBFA, 0x9EBABF2C, 0x1E153C6E, 0x86E34570, 0xEAE96FB1, 0x860E5E0A, 0x5A3E2AB3, 0x771FE71C, 0x4E3D06FA, 0x2965DCB9, 0x99E71D0F, 0x803E89D6, 0x5266C825, 0x2E4CC978, 0x9C10B36A, 0xC6150EBA, 0x94E2EA78, 0xA5FC3C53, 0x1E0A2DF4, 0xF2F74EA7, 0x361D2B3D, 0x1939260F, 0x19C27960, 0x5223A708, 0xF71312B6, 0xEBADFE6E, 0xEAC31F66, 0xE3BC4595, 0xA67BC883, 0xB17F37D1, 0x018CFF28, 0xC332DDEF, 0xBE6C5AA5, 0x65582185, 0x68AB9802, 0xEECEA50F, 0xDB2F953B, 0x2AEF7DAD, 0x5B6E2F84, 0x1521B628, 0x29076170, 0xECDD4775, 0x619F1510, 0x13CCA830, 0xEB61BD96, 0x0334FE1E, 0xAA0363CF, 0xB5735C90, 0x4C70A239, 0xD59E9E0B, 0xCBAADE14, 0xEECC86BC, 0x60622CA7, 0x9CAB5CAB, 0xB2F3846E, 0x648B1EAF, 0x19BDF0CA, 0xA02369B9, 0x655ABB50, 0x40685A32, 0x3C2AB4B3, 0x319EE9D5, 0xC021B8F7, 0x9B540B19, 0x875FA099, 0x95F7997E, 0x623D7DA8, 0xF837889A, 0x97E32D77, 0x11ED935F, 0x16681281, 0x0E358829, 0xC7E61FD6, 0x96DEDFA1, 0x7858BA99, 0x57F584A5, 0x1B227263, 0x9B83C3FF, 0x1AC24696, 0xCDB30AEB, 0x532E3054, 0x8FD948E4, 0x6DBC3128, 0x58EBF2EF, 0x34C6FFEA, 0xFE28ED61, 0xEE7C3C73, 0x5D4A14D9, 0xE864B7E3, 0x42105D14, 0x203E13E0, 0x45EEE2B6, 0xA3AAABEA, 0xDB6C4F15, 0xFACB4FD0, 0xC742F442, 0xEF6ABBB5, 0x654F3B1D, 0x41CD2105, 0xD81E799E, 0x86854DC7, 0xE44B476A, 0x3D816250, 0xCF62A1F2, 0x5B8D2646, 0xFC8883A0, 0xC1C7B6A3, 0x7F1524C3, 0x69CB7492, 0x47848A0B, 0x5692B285, 0x095BBF00, 0xAD19489D, 0x1462B174, 0x23820E00, 0x58428D2A, 0x0C55F5EA, 0x1DADF43E, 0x233F7061, 0x3372F092, 0x8D937E41, 0xD65FECF1, 0x6C223BDB, 0x7CDE3759, 0xCBEE7460, 0x4085F2A7, 0xCE77326E, 0xA6078084, 0x19F8509E, 0xE8EFD855, 0x61D99735, 0xA969A7AA, 0xC50C06C2, 0x5A04ABFC, 0x800BCADC, 0x9E447A2E, 0xC3453484, 0xFDD56705, 0x0E1E9EC9, 0xDB73DBD3, 0x105588CD, 0x675FDA79, 0xE3674340, 0xC5C43465, 0x713E38D8, 0x3D28F89E, 0xF16DFF20, 0x153E21E7, 0x8FB03D4A, 0xE6E39F2B, 0xDB83ADF7], [0xE93D5A68, 0x948140F7, 0xF64C261C, 0x94692934, 0x411520F7, 0x7602D4F7, 0xBCF46B2E, 0xD4A20068, 0xD4082471, 0x3320F46A, 0x43B7D4B7, 0x500061AF, 0x1E39F62E, 0x97244546, 0x14214F74, 0xBF8B8840, 0x4D95FC1D, 0x96B591AF, 0x70F4DDD3, 0x66A02F45, 0xBFBC09EC, 0x03BD9785, 0x7FAC6DD0, 0x31CB8504, 0x96EB27B3, 0x55FD3941, 0xDA2547E6, 0xABCA0A9A, 0x28507825, 0x530429F4, 0x0A2C86DA, 0xE9B66DFB, 0x68DC1462, 0xD7486900, 0x680EC0A4, 0x27A18DEE, 0x4F3FFEA2, 0xE887AD8C, 0xB58CE006, 0x7AF4D6B6, 0xAACE1E7C, 0xD3375FEC, 0xCE78A399, 0x406B2A42, 0x20FE9E35, 0xD9F385B9, 0xEE39D7AB, 0x3B124E8B, 0x1DC9FAF7, 0x4B6D1856, 0x26A36631, 0xEAE397B2, 0x3A6EFA74, 0xDD5B4332, 0x6841E7F7, 0xCA7820FB, 0xFB0AF54E, 0xD8FEB397, 0x454056AC, 0xBA489527, 0x55533A3A, 0x20838D87, 0xFE6BA9B7, 0xD096954B, 0x55A867BC, 0xA1159A58, 0xCCA92963, 0x99E1DB33, 0xA62A4A56, 0x3F3125F9, 0x5EF47E1C, 0x9029317C, 0xFDF8E802, 0x04272F70, 0x80BB155C, 0x05282CE3, 0x95C11548, 0xE4C66D22, 0x48C1133F, 0xC70F86DC, 0x07F9C9EE, 0x41041F0F, 0x404779A4, 0x5D886E17, 0x325F51EB, 0xD59BC0D1, 0xF2BCC18F, 0x41113564, 0x257B7834, 0x602A9C60, 0xDFF8E8A3, 0x1F636C1B, 0x0E12B4C2, 0x02E1329E, 0xAF664FD1, 0xCAD18115, 0x6B2395E0, 0x333E92E1, 0x3B240B62, 0xEEBEB922, 0x85B2A20E, 0xE6BA0D99, 0xDE720C8C, 0x2DA2F728, 0xD0127845, 0x95B794FD, 0x647D0862, 0xE7CCF5F0, 0x5449A36F, 0x877D48FA, 0xC39DFD27, 0xF33E8D1E, 0x0A476341, 0x992EFF74, 0x3A6F6EAB, 0xF4F8FD37, 0xA812DC60, 0xA1EBDDF8, 0x991BE14C, 0xDB6E6B0D, 0xC67B5510, 0x6D672C37, 0x2765D43B, 0xDCD0E804, 0xF1290DC7, 0xCC00FFA3, 0xB5390F92, 0x690FED0B, 0x667B9FFB, 0xCEDB7D9C, 0xA091CF0B, 0xD9155EA3, 0xBB132F88, 0x515BAD24, 0x7B9479BF, 0x763BD6EB, 0x37392EB3, 0xCC115979, 0x8026E297, 0xF42E312D, 0x6842ADA7, 0xC66A2B3B, 0x12754CCC, 0x782EF11C, 0x6A124237, 0xB79251E7, 0x06A1BBE6, 0x4BFB6350, 0x1A6B1018, 0x11CAEDFA, 0x3D25BDD8, 0xE2E1C3C9, 0x44421659, 0x0A121386, 0xD90CEC6E, 0xD5ABEA2A, 0x64AF674E, 0xDA86A85F, 0xBEBFE988, 0x64E4C3FE, 0x9DBC8057, 0xF0F7C086, 0x60787BF8, 0x6003604D, 0xD1FD8346, 0xF6381FB0, 0x7745AE04, 0xD736FCCC, 0x83426B33, 0xF01EAB71, 0xB0804187, 0x3C005E5F, 0x77A057BE, 0xBDE8AE24, 0x55464299, 0xBF582E61, 0x4E58F48F, 0xF2DDFDA2, 0xF474EF38, 0x8789BDC2, 0x5366F9C3, 0xC8B38E74, 0xB475F255, 0x46FCD9B9, 0x7AEB2661, 0x8B1DDF84, 0x846A0E79, 0x915F95E2, 0x466E598E, 0x20B45770, 0x8CD55591, 0xC902DE4C, 0xB90BACE1, 0xBB8205D0, 0x11A86248, 0x7574A99E, 0xB77F19B6, 0xE0A9DC09, 0x662D09A1, 0xC4324633, 0xE85A1F02, 0x09F0BE8C, 0x4A99A025, 0x1D6EFE10, 0x1AB93D1D, 0x0BA5A4DF, 0xA186F20F, 0x2868F169, 0xDCB7DA83, 0x573906FE, 0xA1E2CE9B, 0x4FCD7F52, 0x50115E01, 0xA70683FA, 0xA002B5C4, 0x0DE6D027, 0x9AF88C27, 0x773F8641, 0xC3604C06, 0x61A806B5, 0xF0177A28, 0xC0F586E0, 0x006058AA, 0x30DC7D62, 0x11E69ED7, 0x2338EA63, 0x53C2DD94, 0xC2C21634, 0xBBCBEE56, 0x90BCB6DE, 0xEBFC7DA1, 0xCE591D76, 0x6F05E409, 0x4B7C0188, 0x39720A3D, 0x7C927C24, 0x86E3725F, 0x724D9DB9, 0x1AC15BB4, 0xD39EB8FC, 0xED545578, 0x08FCA5B5, 0xD83D7CD3, 0x4DAD0FC4, 0x1E50EF5E, 0xB161E6F8, 0xA28514D9, 0x6C51133C, 0x6FD5C7E7, 0x56E14EC4, 0x362ABFCE, 0xDDC6C837, 0xD79A3234, 0x92638212, 0x670EFA8E, 0x406000E0], [0x3A39CE37, 0xD3FAF5CF, 0xABC27737, 0x5AC52D1B, 0x5CB0679E, 0x4FA33742, 0xD3822740, 0x99BC9BBE, 0xD5118E9D, 0xBF0F7315, 0xD62D1C7E, 0xC700C47B, 0xB78C1B6B, 0x21A19045, 0xB26EB1BE, 0x6A366EB4, 0x5748AB2F, 0xBC946E79, 0xC6A376D2, 0x6549C2C8, 0x530FF8EE, 0x468DDE7D, 0xD5730A1D, 0x4CD04DC6, 0x2939BBDB, 0xA9BA4650, 0xAC9526E8, 0xBE5EE304, 0xA1FAD5F0, 0x6A2D519A, 0x63EF8CE2, 0x9A86EE22, 0xC089C2B8, 0x43242EF6, 0xA51E03AA, 0x9CF2D0A4, 0x83C061BA, 0x9BE96A4D, 0x8FE51550, 0xBA645BD6, 0x2826A2F9, 0xA73A3AE1, 0x4BA99586, 0xEF5562E9, 0xC72FEFD3, 0xF752F7DA, 0x3F046F69, 0x77FA0A59, 0x80E4A915, 0x87B08601, 0x9B09E6AD, 0x3B3EE593, 0xE990FD5A, 0x9E34D797, 0x2CF0B7D9, 0x022B8B51, 0x96D5AC3A, 0x017DA67D, 0xD1CF3ED6, 0x7C7D2D28, 0x1F9F25CF, 0xADF2B89B, 0x5AD6B472, 0x5A88F54C, 0xE029AC71, 0xE019A5E6, 0x47B0ACFD, 0xED93FA9B, 0xE8D3C48D, 0x283B57CC, 0xF8D56629, 0x79132E28, 0x785F0191, 0xED756055, 0xF7960E44, 0xE3D35E8C, 0x15056DD4, 0x88F46DBA, 0x03A16125, 0x0564F0BD, 0xC3EB9E15, 0x3C9057A2, 0x97271AEC, 0xA93A072A, 0x1B3F6D9B, 0x1E6321F5, 0xF59C66FB, 0x26DCF319, 0x7533D928, 0xB155FDF5, 0x03563482, 0x8ABA3CBB, 0x28517711, 0xC20AD9F8, 0xABCC5167, 0xCCAD925F, 0x4DE81751, 0x3830DC8E, 0x379D5862, 0x9320F991, 0xEA7A90C2, 0xFB3E7BCE, 0x5121CE64, 0x774FBE32, 0xA8B6E37E, 0xC3293D46, 0x48DE5369, 0x6413E680, 0xA2AE0810, 0xDD6DB224, 0x69852DFD, 0x09072166, 0xB39A460A, 0x6445C0DD, 0x586CDECF, 0x1C20C8AE, 0x5BBEF7DD, 0x1B588D40, 0xCCD2017F, 0x6BB4E3BB, 0xDDA26A7E, 0x3A59FF45, 0x3E350A44, 0xBCB4CDD5, 0x72EACEA8, 0xFA6484BB, 0x8D6612AE, 0xBF3C6F47, 0xD29BE463, 0x542F5D9E, 0xAEC2771B, 0xF64E6370, 0x740E0D8D, 0xE75B1357, 0xF8721671, 0xAF537D5D, 0x4040CB08, 0x4EB4E2CC, 0x34D2466A, 0x0115AF84, 0xE1B00428, 0x95983A1D, 0x06B89FB4, 0xCE6EA048, 0x6F3F3B82, 0x3520AB82, 0x011A1D4B, 0x277227F8, 0x611560B1, 0xE7933FDC, 0xBB3A792B, 0x344525BD, 0xA08839E1, 0x51CE794B, 0x2F32C9B7, 0xA01FBAC9, 0xE01CC87E, 0xBCC7D1F6, 0xCF0111C3, 0xA1E8AAC7, 0x1A908749, 0xD44FBD9A, 0xD0DADECB, 0xD50ADA38, 0x0339C32A, 0xC6913667, 0x8DF9317C, 0xE0B12B4F, 0xF79E59B7, 0x43F5BB3A, 0xF2D519FF, 0x27D9459C, 0xBF97222C, 0x15E6FC2A, 0x0F91FC71, 0x9B941525, 0xFAE59361, 0xCEB69CEB, 0xC2A86459, 0x12BAA8D1, 0xB6C1075E, 0xE3056A0C, 0x10D25065, 0xCB03A442, 0xE0EC6E0E, 0x1698DB3B, 0x4C98A0BE, 0x3278E964, 0x9F1F9532, 0xE0D392DF, 0xD3A0342B, 0x8971F21E, 0x1B0A7441, 0x4BA3348C, 0xC5BE7120, 0xC37632D8, 0xDF359F8D, 0x9B992F2E, 0xE60B6F47, 0x0FE3F11D, 0xE54CDA54, 0x1EDAD891, 0xCE6279CF, 0xCD3E7E6F, 0x1618B166, 0xFD2C1D05, 0x848FD2C5, 0xF6FB2299, 0xF523F357, 0xA6327623, 0x93A83531, 0x56CCCD02, 0xACF08162, 0x5A75EBB5, 0x6E163697, 0x88D273CC, 0xDE966292, 0x81B949D0, 0x4C50901B, 0x71C65614, 0xE6C6C7BD, 0x327A140A, 0x45E1D006, 0xC3F27B9A, 0xC9AA53FD, 0x62A80F00, 0xBB25BFE2, 0x35BDD2F6, 0x71126905, 0xB2040222, 0xB6CBCF7C, 0xCD769C2B, 0x53113EC0, 0x1640E3D3, 0x38ABBD60, 0x2547ADF0, 0xBA38209C, 0xF746CE76, 0x77AFA1C5, 0x20756060, 0x85CBFE4E, 0x8AE88DD8, 0x7AAAF9B0, 0x4CF9AA7E, 0x1948C25C, 0x02FB8A8C, 0x01C36AE4, 0xD6EBE1F9, 0x90D4F869, 0xA65CDEA0, 0x3F09252D, 0xC208E69F, 0xB74E6132, 0xCE77E25B, 0x578FDFE3, 0x3AC372E6]];
    var BLOWFISH_CTX = {
      pbox: [],
      sbox: []
    };
    function F(ctx, x) {
      let a = x >> 24 & 0xFF;
      let b = x >> 16 & 0xFF;
      let c = x >> 8 & 0xFF;
      let d = x & 0xFF;
      let y = ctx.sbox[0][a] + ctx.sbox[1][b];
      y = y ^ ctx.sbox[2][c];
      y = y + ctx.sbox[3][d];
      return y;
    }
    function BlowFish_Encrypt(ctx, left, right) {
      let Xl = left;
      let Xr = right;
      let temp;
      for (let i = 0; i < N; ++i) {
        Xl = Xl ^ ctx.pbox[i];
        Xr = F(ctx, Xl) ^ Xr;
        temp = Xl;
        Xl = Xr;
        Xr = temp;
      }
      temp = Xl;
      Xl = Xr;
      Xr = temp;
      Xr = Xr ^ ctx.pbox[N];
      Xl = Xl ^ ctx.pbox[N + 1];
      return {
        left: Xl,
        right: Xr
      };
    }
    function BlowFish_Decrypt(ctx, left, right) {
      let Xl = left;
      let Xr = right;
      let temp;
      for (let i = N + 1; i > 1; --i) {
        Xl = Xl ^ ctx.pbox[i];
        Xr = F(ctx, Xl) ^ Xr;
        temp = Xl;
        Xl = Xr;
        Xr = temp;
      }
      temp = Xl;
      Xl = Xr;
      Xr = temp;
      Xr = Xr ^ ctx.pbox[1];
      Xl = Xl ^ ctx.pbox[0];
      return {
        left: Xl,
        right: Xr
      };
    }

    /**
     * Initialization ctx's pbox and sbox.
     *
     * @param {Object} ctx The object has pbox and sbox.
     * @param {Array} key An array of 32-bit words.
     * @param {int} keysize The length of the key.
     *
     * @example
     *
     *     BlowFishInit(BLOWFISH_CTX, key, 128/32);
     */
    function BlowFishInit(ctx, key, keysize) {
      for (let Row = 0; Row < 4; Row++) {
        ctx.sbox[Row] = [];
        for (let Col = 0; Col < 256; Col++) {
          ctx.sbox[Row][Col] = ORIG_S[Row][Col];
        }
      }
      let keyIndex = 0;
      for (let index = 0; index < N + 2; index++) {
        ctx.pbox[index] = ORIG_P[index] ^ key[keyIndex];
        keyIndex++;
        if (keyIndex >= keysize) {
          keyIndex = 0;
        }
      }
      let Data1 = 0;
      let Data2 = 0;
      let res = 0;
      for (let i = 0; i < N + 2; i += 2) {
        res = BlowFish_Encrypt(ctx, Data1, Data2);
        Data1 = res.left;
        Data2 = res.right;
        ctx.pbox[i] = Data1;
        ctx.pbox[i + 1] = Data2;
      }
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 256; j += 2) {
          res = BlowFish_Encrypt(ctx, Data1, Data2);
          Data1 = res.left;
          Data2 = res.right;
          ctx.sbox[i][j] = Data1;
          ctx.sbox[i][j + 1] = Data2;
        }
      }
      return true;
    }

    /**
     * Blowfish block cipher algorithm.
     */
    var Blowfish = C_algo.Blowfish = BlockCipher.extend({
      _doReset: function () {
        // Skip reset of nRounds has been set before and key did not change
        if (this._keyPriorReset === this._key) {
          return;
        }

        // Shortcuts
        var key = this._keyPriorReset = this._key;
        var keyWords = key.words;
        var keySize = key.sigBytes / 4;

        //Initialization pbox and sbox
        BlowFishInit(BLOWFISH_CTX, keyWords, keySize);
      },
      encryptBlock: function (M, offset) {
        var res = BlowFish_Encrypt(BLOWFISH_CTX, M[offset], M[offset + 1]);
        M[offset] = res.left;
        M[offset + 1] = res.right;
      },
      decryptBlock: function (M, offset) {
        var res = BlowFish_Decrypt(BLOWFISH_CTX, M[offset], M[offset + 1]);
        M[offset] = res.left;
        M[offset + 1] = res.right;
      },
      blockSize: 64 / 32,
      keySize: 128 / 32,
      ivSize: 64 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.Blowfish.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.Blowfish.decrypt(ciphertext, key, cfg);
     */
    C.Blowfish = BlockCipher._createHelper(Blowfish);
  })();
  return CryptoJS.Blowfish;
});
});

var cryptoJs = createCommonjsModule(function (module, exports) {
(function (root, factory, undef) {
  {
    // CommonJS
    module.exports = factory(core, x64Core, libTypedarrays, encUtf16, encBase64, encBase64url, md5, sha1, sha256, sha224, sha512, sha384, sha3, ripemd160, hmac, pbkdf2, evpkdf, cipherCore, modeCfb, modeCtr, modeCtrGladman, modeOfb, modeEcb, padAnsix923, padIso10126, padIso97971, padZeropadding, padNopadding, formatHex, aes, tripledes, rc4, rabbit, rabbitLegacy, blowfish);
  }
})(commonjsGlobal, function (CryptoJS) {
  return CryptoJS;
});
});

//
const uuid = prefix => {
  const s = [];
  const hexDigits = '0123456789abcdef';
  for (let i = 0; i < 36; i += 1) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4';
  s[19] = hexDigits.substr(s[19] & 0x3 | 0x8, 1);
  const id = s.join('');
  return prefix ? prefix + '__' + id : id;
};

// import CryptoJS from 'crypto-js'
// import RecorderManager from '../processor.worker'
let Base64$1 = require('js-base64').Base64;
// import parser from 'fast-xml-parser'

// let transWorker = new RecorderManager()
//APPID，APISecret，APIKey在控制台-我的应用-语音评测（流式版）页面获取
const APPID$1 = '98fcf4d6';
const API_SECRET$1 = 'Y2FkN2Y1MGZjN2U0M2I1MTU4Y2FlZjA0';
const API_KEY$1 = 'b7e405d6af09050e4a6976f31ec37640';
const recorder$1 = new RecorderManager('/audio/recorder');
var script$52 = {
  name: 'VoicePlayer',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    targerLink: {
      type: String,
      default: ''
    },
    playerIndex: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      textarea: '语音评测',
      playIcon,
      pauseIcon,
      audioIcon,
      coverLocal: coverLocal$1,
      recStartIcon,
      recStopIcon,
      recDelete,
      recMockWave,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      recordAudio: null,
      permissionDialogVisible: false,
      Rec: recorder_mp3_min,
      type: 'mp3',
      bitRate: 16,
      sampleRate: 16000,
      testSampleRate: 16000,
      rec: null,
      recDuration: 0,
      powerLevel: 0,
      logs: [],
      recBolb: null,
      isRecing: false,
      hasInitSuccess: false,
      waveStore: {
        selected: 'Histogram2',
        WaveView: {},
        Histogram2: {},
        Histogram3: {}
      },
      recordDataUrl: '',
      lastAudioStamp: null,
      lastPlayStamp: null,
      waveBoxKey: uuid('wave'),
      recTimestamp: null,
      iseWS: null,
      SendFrameSize: 1280,
      realTimeSendTryNumber: null,
      transferUploadNumberMax: null,
      realTimeSendTryChunk: null,
      realTimeSendTryChunks: null,
      buffersArr: [],
      audioData: [],
      status: '',
      //
      webSocket: null,
      total: '0',
      accuracy: '0',
      fluency: '0',
      integrity: '0',
      phone: '0',
      tone: '0',
      emotion: '0',
      voicedata: {}
    };
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat$1,
        'dan-ci': playOnce$1
      };
      return srcMap[this.playMode];
    },
    controlStatusText() {
      let text = '';
      if (this.recBolb) {
        text = this.isPlay ? '' : '';
      } else {
        text = this.isRecing ? '结束录音' : '开始录音';
      }
      return text;
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        console.log('浏览器 UA', ua);
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        // isWeixin && isIOS && newVal && this.$message({
        //   message: `当前UA为${ua}`,
        //   duration: 3000
        // })
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.recordAudio.muted = true;
              const playPromise = _this.$refs.recordAudio.play();
              _this.$refs.recordAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.recordAudio.pause();
                }).catch(error => {
                  _this.$refs.recordAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.recordAudio.pause();
      }
    }
  },
  mounted() {
    console.log(recorder$1);
    // 加载完毕后，先获取<video>标签DOM对象
    this.recordAudio = this.$refs.recordAudio;
    // let _this = this;
    // if(!this.recBolb && !this.isRecing) {
    //   let rec = this.rec = Recorder({
    //       type:_this.type,
    //       bitRate:_this.bitRate,
    //       sampleRate:_this.sampleRate,
    //       onProcess:function(buffers,powerLevel,duration,sampleRate){
    //           _this.recDuration = duration;
    //           _this.powerLevel = powerLevel;
    //           // _this.wave.input(buffers[buffers.length-1],powerLevel,sampleRate);
    //           //可视化图形绘制
    //           $(".recwave").html("").append(_this.waveStore[_this.waveStore.selected].elem);
    //           _this.waveStore[_this.waveStore.selected].input(buffers[buffers.length-1],powerLevel,sampleRate);
    //       }
    //   });

    //   _this.dialogInt=setTimeout(function(){//定时8秒后打开弹窗，用于监测浏览器没有发起权限请求的情况
    //       _this.showDialog();
    //   },8000);

    //   rec.open(function(){
    //       _this.dialogCancel();
    //       // _this.reclog("已打开:"+_this.type+" "+_this.sampleRate+"hz "+_this.bitRate+"kbps",2);

    //       _this.initWaveStore(".recwave");
    //       // rec.start();
    //       _this.hasInitSuccess = true
    //       // _this.isRecing = true
    //   },function(msg,isUserNotAllow){
    //       _this.dialogCancel();
    //       isUserNotAllow && _this.$message.error('启动录音失败，用户拒绝了授权')
    //       // _this.reclog((isUserNotAllow?"UserNotAllow，":"")+"打开失败："+msg,1);
    //   });

    //   _this.waitDialogClickFn=function(){
    //       _this.dialogCancel();
    //       _this.$message.error('启动录音失败，用户忽略了权限请求')
    //       // _this.reclog("打开失败：权限请求被忽略，用户主动点击的弹窗",1);
    //   };
    // }
  },
  methods: {
    ontrolBtn() {
      if (!this.isRecing) {
        this.connectWebSocket();
        this.isRecing = true;
        recorder$1.onFrameRecorded = ({
          isLastFrame,
          frameBuffer
        }) => {
          this.buffersArr.push(frameBuffer);
          this.webSocket.send(JSON.stringify({
            business: {
              aue: 'raw',
              cmd: 'auw',
              aus: isLastFrame ? 4 : 2
            },
            data: {
              status: isLastFrame ? 2 : 1,
              data: this.toBase64(frameBuffer),
              data_type: 1
            }
          }));
          if (isLastFrame) {
            this.isRecing = false;
          }
        };
      } else {
        recorder$1.stop();
        let _this = this;
        _this.isRecing = false;
        recorder$1.onStop = function (res) {
          console.log(res);
          const blob = new Blob(_this.buffersArr, {
            type: 'audio/mp3'
          });
          // _this.recBolb = blob
          console.log(blob);
          console.log(URL.createObjectURL(blob));

          // this.recordedAudio = URL.createObjectURL(blob);
          // this.$refs.audioPlayer.src = this.recordedAudio;
        };
        console.log('结束');
      }
    },
    /**
     * 获取websocket url
     * 该接口需要后端提供，这里为了方便前端处理
     */
    getWebSocketUrl() {
      return new Promise((resolve, reject) => {
        // 请求地址根据语种不同变化
        var url = 'wss://ise-api.xfyun.cn/v2/open-ise';
        var host = 'ise-api.xfyun.cn';
        var apiKey = API_KEY$1;
        var apiSecret = API_SECRET$1;
        var date = new Date().toGMTString();
        var algorithm = 'hmac-sha256';
        var headers = 'host date request-line';
        var signatureOrigin = `host: ${host}\ndate: ${date}\nGET /v2/open-ise HTTP/1.1`;
        var signatureSha = cryptoJs.HmacSHA256(signatureOrigin, apiSecret);
        var signature = cryptoJs.enc.Base64.stringify(signatureSha);
        var authorizationOrigin = `api_key="${apiKey}", algorithm="${algorithm}", headers="${headers}", signature="${signature}"`;
        var authorization = btoa(authorizationOrigin);
        url = `${url}?authorization=${authorization}&date=${date}&host=${host}`;
        resolve(url);
      });
    },
    // 修改录音评测状态
    setStatus(status) {
      this.onWillStatusChange && this.status !== status && this.onWillStatusChange(this.status, status);
      this.status = status;
    },
    // 连接websocket
    connectWebSocket() {
      return this.getWebSocketUrl().then(url => {
        let iseWS;
        if ('WebSocket' in window) {
          iseWS = new WebSocket(url);
        } else if ('MozWebSocket' in window) {
          iseWS = new MozWebSocket(url);
        } else {
          alert('浏览器不支持WebSocket');
          return;
        }
        this.webSocket = iseWS;
        this.setStatus('init');
        iseWS.onopen = e => {
          this.setStatus('ing');
          // 重新开始录音

          recorder$1.start({
            sampleRate: 16000,
            frameSize: 1280
          });
          var params = {
            common: {
              app_id: APPID$1
            },
            business: {
              category: 'read_sentence',
              // read_syllable/单字朗读，汉语专有 read_word/词语朗读  read_sentence/句子朗读 https://www.xfyun.cn/doc/Ise/IseAPI.html#%E6%8E%A5%E5%8F%A3%E8%B0%83%E7%94%A8%E6%B5%81%E7%A8%8B
              rstcd: 'utf8',
              group: 'pupil',
              sub: 'ise',
              tte: 'utf-8',
              cmd: 'ssb',
              auf: 'audio/L16;rate=16000',
              ent: 'cn_vip',
              aus: 1,
              aue: 'raw',
              text: '\uFEFF' + (this.textarea || 'where are you')
            },
            data: {
              status: 0
              // data_type: 1,
              // encoding: "raw",
            }
          };
          iseWS.send(JSON.stringify(params));
        };
        iseWS.onmessage = e => {
          this.result(e.data);
        };
        iseWS.onerror = e => {
          // this.recorderStop()
        };
        iseWS.onclose = e => {
          // this.recorderStop()
        };
      });
    },
    // 对处理后的音频数据进行base64编码
    toBase64(buffer) {
      var binary = '';
      var bytes = new Uint8Array(buffer);
      var len = bytes.byteLength;
      for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    },
    // 向webSocket发送数据
    webSocketSend(audioDatajson) {
      if (this.webSocket.readyState !== 1) {
        return;
      }
      // let audioDatajson = this.audioData.splice(0, 1280)
      var params = {
        common: {
          app_id: APPID$1
        },
        business: {
          category: 'read_sentence',
          // read_syllable/单字朗读，汉语专有 read_word/词语朗读  read_sentence/句子朗读 https://www.xfyun.cn/doc/Ise/IseAPI.html#%E6%8E%A5%E5%8F%A3%E8%B0%83%E7%94%A8%E6%B5%81%E7%A8%8B
          rstcd: 'utf8',
          group: 'pupil',
          sub: 'ise',
          tte: 'utf-8',
          cmd: 'ssb',
          auf: 'audio/L16;rate=16000',
          ent: 'cn_vip',
          aus: 1,
          aue: 'lame',
          text: '\uFEFF' + '测试语音' ,
          ise_unite: '1',
          rst: 'entirety',
          extra_ability: 'multi_dimension'
        },
        data: {
          status: 0,
          encoding: 'lame',
          data_type: 1,
          data: this.toBase64(audioDatajson)
        }
      };
      this.webSocket.send(JSON.stringify(params));
      this.handlerInterval = setInterval(() => {
        // websocket未连接
        if (this.webSocket.readyState !== 1) {
          this.audioData = [];
          clearInterval(this.handlerInterval);
          return;
        }
        // 最后一帧
        if (this.audioData.length === 0) {
          if (this.status === 'end') {
            console.log('最后一帧并结束');
            this.webSocket.send(JSON.stringify({
              business: {
                cmd: 'auw',
                aus: 4,
                aue: 'lame'
              },
              data: {
                status: 2,
                data_type: 1,
                data: ''
              }
            }));
            this.audioData = [];
            clearInterval(this.handlerInterval);
          }
          return false;
        }
        this.audioData.splice(0, 1280);

        // 中间帧
        this.webSocket.send(JSON.stringify({
          business: {
            cmd: 'auw',
            aus: 2,
            aue: 'lame'
          },
          data: {
            status: 1,
            encoding: 'lame',
            data_type: 1,
            data: this.toBase64(audioDatajson)
          }
        }));
      }, 40);
    },
    result(resultData) {
      // 识别结束
      let jsonData = JSON.parse(resultData);
      console.log(jsonData);
      if (jsonData.data && jsonData.data.data) {
        // console.log(jsonData.data.data,'jsonData.data.data');
        let data = Base64$1.decode(jsonData.data.data);
        // console.log(data, 'dasdsadasdasdasdasdasdasdas');
        let grade = parser.parse(data, {
          attributeNamePrefix: '',
          ignoreAttributes: false
        });
        console.log(grade, 'grade结束的这一次');
        grade?.xml_result?.read_sentence?.rec_paper?.read_sentence;
        this.voicedata = grade?.xml_result?.read_sentence?.rec_paper?.read_sentence;
        console.log(this.voicedata);

        // this.accuracy = readSentence?.accuracy_score;
        // this.fluency= readSentence?.fluency_score;
        // this.integrity= readSentence?.integrity_score;
        // this.phone= readSentence?.phone_score;
        // this.tone= readSentence?.tone_score;
        // this.emotion= readSentence?.emotion_score;
      }
      if (jsonData.code === 0 && jsonData.data.status === 2) {
        this.webSocket.close();
      }
      if (jsonData.code !== 0) {
        this.webSocket.close();
      }
    },
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.recordAudio.duration);
        this.totalDuration = this.$refs.recordAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    resetRecorder() {
      let audio = this.$refs.recordAudio;
      if (!audio.paused) {
        audio.pause();
      }
      this.recBolb = null;
      this.isPlay = false;
      this.isRecing = false;
      this.playTime = 0;
      this.playDuration = '00:00';
      this.playCurrentTime = '00:00';
      this.totalDuration = 0;
      this.recDuration = 0;
      this.recordDataUrl = '';
      this.lastAudioStamp = null;
      this.lastPlayStamp = null;
    },
    setRecedDuration(ms) {
      this.playDuration = this.timeFormat(ms / 1000);
      this.totalDuration = ms / 1000;
    },
    initWaveStore(elem) {
      this.waveStore.WaveView = recorder_mp3_min.WaveView({
        elem: elem,
        width: 300,
        height: 100
      });
      this.waveStore.Histogram2 = recorder_mp3_min.FrequencyHistogramView({
        elem: elem,
        lineCount: 30,
        position: 0,
        minHeight: 12,
        mirrorEnable: true,
        stripeEnable: false,
        linear: [0, '#8FC1FD', 1, '#8FC1FD']
      });
      this.waveStore.Histogram3 = recorder_mp3_min.FrequencyHistogramView({
        elem: elem,
        lineCount: 20,
        position: 0,
        minHeight: 1,
        fallDuration: 400,
        stripeEnable: false,
        mirrorEnable: true,
        linear: [0, '#0ac', 1, '#0ac']
      });
    },
    toggleRec() {
      let _this = this;
      // if(!this.hasInitSuccess) {
      //   this.$message.warning('当前浏览器不支持或用户未进行授权，组件功能暂无法使用')
      //   return
      // }
      let waveBoxSelector = '#' + _this.waveBoxKey;
      if (!this.recBolb && !this.isRecing) {
        // _this.rec.start();
        // _this.isRecing = true
        if (recorder_mp3_min.IsOpen()) {
          _this.$message.warning('当前存在进行中的录制任务');
          return;
        }
        let rec = this.rec = recorder_mp3_min({
          type: _this.type,
          bitRate: _this.bitRate,
          sampleRate: _this.sampleRate,
          onProcess: function (buffers, powerLevel, duration, sampleRate) {
            _this.recDuration = duration;
            _this.powerLevel = powerLevel;
            // _this.wave.input(buffers[buffers.length-1],powerLevel,sampleRate);
            //可视化图形绘制
            jquery(waveBoxSelector).html('').append(_this.waveStore[_this.waveStore.selected].elem);
            _this.waveStore[_this.waveStore.selected].input(buffers[buffers.length - 1], powerLevel, sampleRate);

            // _this.audioData.push(buffers)
            // window.btoa(String.fromCharCode(...new Uint8Array(buffers[buffers.length - 1])))
            // _this.webSocketSend(buffers)
          }
        });
        var t = setTimeout(function () {
          // Runtime.Log("无法录音：权限请求被忽略（超时假装手动点击了确认对话框）",1);
        }, 8000);
        rec.open(function () {
          //打开麦克风授权获得相关资源
          clearTimeout(t);
          _this.dialogCancel();
          _this.initWaveStore(waveBoxSelector);
          rec.start();
          _this.isRecing = true;
          _this.recTimestamp = new Date().getTime();
        }, function (msg, isUserNotAllow) {
          clearTimeout(t);
          // Runtime.Log((isUserNotAllow?"UserNotAllow，":"")+"无法录音:"+msg, 1);
        });
      } else {
        if (!(_this.rec && recorder_mp3_min.IsOpen())) {
          _this.$message.error('录音未启动');
          return;
        }
        let doneTimestamp = new Date().getTime();
        if (!_this.recTimestamp || doneTimestamp - _this.recTimestamp < 5000) {
          _this.$message.warning('至少录制 5s 方可进行保存，请稍候');
          return;
        }
        let rec = _this.rec;
        rec.stop(function (blob, duration) {
          _this.$message.success('录制完成');
          _this.recDuration = duration;
          _this.setRecedDuration(duration);
          jquery(waveBoxSelector).html('');
          _this.$nextTick(() => {
            _this.recBolb = blob;
            _this.isRecing = false;
            _this.lastAudioStamp = new Date().getTime();
            _this.recTimestamp = null;
          });
          _this.setStatus('end');

          //_this.$emit('resData',{blob:blob,duration:duration})
        }, function (msg) {
          _this.recTimestamp = null;
          _this.$message.error(`录音失败：${msg}`);
        }, true); //录音结束时自动调用rec.close方法丢弃所有数据
      }
    },
    play() {
      // if(!this.recBolb) {
      //   // this.toggleRec()
      //   this.ontrolBtn()
      // }else {
      //   // if (this.playStyle === 'yuan-wen') {
      //   //   this.doPlay()
      //   // }
      //   // if (this.playStyle === 'tan-chuang') {
      //   //   this.$emit('showDialog', { url: this.url, title: this.title, coverFileUrl: this.coverImageUrl, content: this.content, playerIndex: this.playerIndex })
      //   // }
      //   this.togglePlay()
      // }
      this.$emit('showDialog');
    },
    togglePlay() {
      let _this = this;
      let audios = document.querySelectorAll('audio');
      audios.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      let audio = this.$refs.recordAudio;
      if (this.isPlay) {
        !(audio.ended || audio.paused) && audio.pause();
      } else {
        if (!this.lastPlayStamp || this.lastPlayStamp !== this.lastAudioStamp) {
          audio.onerror = function (e) {
            _this.$message.error(audio.error.message);
          };
          audio.currentTime = 0;
          audio.src = (window.URL || webkitURL).createObjectURL(_this.recBolb);
          console.log((window.URL || webkitURL).createObjectURL(_this.recBolb));
          audio.play();
          this.lastPlayStamp = this.lastAudioStamp;
        } else {
          audio.paused && audio.play();
        }
      }
      this.isPlay = !this.isPlay;
    },
    doPlay() {
      let _this = this;
      let rec = this.rec;
      let audio = this.$refs.LogAudioPlayer;
      audio.controls = true;
      if (!(audio.ended || audio.paused)) {
        audio.pause();
      }
      audio.onerror = function (e) {
        _this.$message.error(audio.error.message);
      };
      audio.src = (window.URL || webkitURL).createObjectURL(rec.blob);
      audio.play();
      // console.log('暂停')
      // let audio = document.querySelectorAll('audio')
      // audio.forEach((i) => i.pause())
      // let video = document.querySelectorAll('video')
      // if (video && video.length) {
      //   video.forEach((i) => i.pause())
      // }

      // this.isPlay ? this.$refs.recordAudio.pause() : this.$refs.recordAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.recordAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.recordAudio.currentTime);
      if (!this.loop && this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      // this.$emit('timeUpdate', {event:e, time: this.$refs.recordAudio.currentTime })
    },
    changeTime(val) {
      this.$refs.recordAudio.currentTime = val;
    },
    showDialog: function () {
      //选择性的弹一个对话框：为了防止移动端浏览器存在第三种情况：用户忽略，并且（或者国产系统UC系）浏览器没有任何回调
      if (!/mobile/i.test(navigator.userAgent)) {
        return; //只在移动端开启没有权限请求的检测
      }
      this.permissionDialogVisible = true;
    },
    dialogCancel: function () {
      clearTimeout(this.dialogInt);
      this.permissionDialogVisible = false;
    },
    waitDialogClick: function () {
      this.dialogCancel();
      this.waitDialogClickFn();
    }
  }
};

/* script */
const __vue_script__$52 = script$52;

/* template */
var __vue_render__$52 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "xml-voice-player", class: ["xml-audio-player", _vm.mode] },
    [
      _vm.recBolb
        ? _c("div", { staticClass: "play-mode-wrap" }, [
            _c("img", {
              staticClass: "play-control-image",
              attrs: { src: _vm.recDelete },
              on: { click: _vm.resetRecorder },
            }),
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.styleMode !== "C"
        ? _c("div", { staticClass: "audio-cover" }, [
            _c("img", {
              attrs: {
                src: _vm.coverImageUrl ? _vm.coverImageUrl : _vm.coverLocal,
                alt: "",
              },
            }),
            _vm._v(" "),
            _vm.styleMode === "A"
              ? _c("div", { staticClass: "play-button style-a" }, [
                  _c("img", {
                    staticClass: "button-image",
                    attrs: {
                      src: _vm.recBolb
                        ? _vm.isPlay
                          ? _vm.pauseIcon
                          : _vm.playIcon
                        : _vm.isRecing
                        ? _vm.recStopIcon
                        : _vm.recStartIcon,
                    },
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.play.apply(null, arguments)
                      },
                    },
                  }),
                ])
              : _vm._e(),
            _vm._v(" "),
            _vm.controlStatusText
              ? _c("div", { staticClass: "play-text" }, [
                  _vm._v(_vm._s(_vm.controlStatusText)),
                ])
              : _vm._e(),
          ])
        : _vm._e(),
      _vm._v(" "),
      !_vm.recBolb
        ? _c("div", { staticClass: "record-player-control" }, [
            !_vm.recBolb
              ? _c("div", {
                  staticClass: "recwave",
                  staticStyle: { height: "75%", width: "100%" },
                  attrs: { id: _vm.waveBoxKey },
                })
              : _vm._e(),
            _vm._v(" "),
            !_vm.isRecing
              ? _c("div", { staticClass: "mock-wave" }, [
                  _c("img", {
                    staticClass: "button-image",
                    attrs: { src: _vm.recMockWave },
                  }),
                ])
              : _vm._e(),
          ])
        : _c("div", { staticClass: "audio-player-control" }, [
            _c("div", { staticClass: "audio-controls" }, [
              _vm.styleMode !== "A"
                ? _c(
                    "div",
                    { staticClass: "audio-controls__play-button-wrapper" },
                    [
                      _c("div", { staticClass: "play-button" }, [
                        _c("img", {
                          staticClass: "button-image",
                          attrs: {
                            src: _vm.isPlay ? _vm.pauseIcon : _vm.playIcon,
                          },
                          on: {
                            click: function ($event) {
                              $event.stopPropagation();
                              return _vm.play.apply(null, arguments)
                            },
                          },
                        }),
                      ]),
                    ]
                  )
                : _vm._e(),
              _vm._v(" "),
              _c("div", { staticClass: "audio-controls__handler" }, [
                _c(
                  "div",
                  { staticClass: "progress" },
                  [
                    _c("el-slider", {
                      attrs: {
                        max: _vm.totalDuration,
                        disabled: _vm.scheduleControl !== "yes",
                        "show-tooltip": false,
                      },
                      on: { change: _vm.changeTime },
                      model: {
                        value: _vm.playTime,
                        callback: function ($$v) {
                          _vm.playTime = $$v;
                        },
                        expression: "playTime",
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "play-times" }, [
                  _c("div", { staticClass: "current-time" }, [
                    _vm._v(_vm._s(_vm.playCurrentTime)),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "total-time" }, [
                    _vm._v(_vm._s(_vm.playDuration)),
                  ]),
                ]),
              ]),
            ]),
          ]),
    ]
  )
};
var __vue_staticRenderFns__$52 = [];
__vue_render__$52._withStripped = true;

  /* style */
  const __vue_inject_styles__$52 = undefined;
  /* scoped */
  const __vue_scope_id__$52 = undefined;
  /* module identifier */
  const __vue_module_identifier__$52 = undefined;
  /* functional template */
  const __vue_is_functional_template__$52 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$52 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$52, staticRenderFns: __vue_staticRenderFns__$52 },
    __vue_inject_styles__$52,
    __vue_script__$52,
    __vue_scope_id__$52,
    __vue_is_functional_template__$52,
    __vue_module_identifier__$52,
    false,
    undefined,
    undefined,
    undefined
  );

var audioplay = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAjCAYAAABsFtHvAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6Nzg2N0EwMkRCOTk2MTFFRTkyODY5NjkyNzIzQkJCMjUiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6Nzg2N0EwMkVCOTk2MTFFRTkyODY5NjkyNzIzQkJCMjUiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo3ODY3QTAyQkI5OTYxMUVFOTI4Njk2OTI3MjNCQkIyNSIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo3ODY3QTAyQ0I5OTYxMUVFOTI4Njk2OTI3MjNCQkIyNSIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PqQzfXsAAABxSURBVHja7NfLDYAgEIThhS60I+vQ1gW7WCHhpEiYk5d/kk0Ij/nORHffyiSfS2r3rTNyT6gLM1tsPrnM2tmXeyrupid09uSeaD8GHBwcHBwcHBwcHBwcHPyFZ/HNNfi3Sz0VPwaFz5xl9o8zuecWYABkIYg317fc8QAAAABJRU5ErkJggg==";

var audiopluse = "data:image/png;base64,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";

var statr = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAARCAYAAADtyJ2fAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6MEI5NTc1MUNCOUJEMTFFRTkwRUJDMDQwM0I2NDI4OUQiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6MEI5NTc1MURCOUJEMTFFRTkwRUJDMDQwM0I2NDI4OUQiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDowQjk1NzUxQUI5QkQxMUVFOTBFQkMwNDAzQjY0Mjg5RCIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDowQjk1NzUxQkI5QkQxMUVFOTBFQkMwNDAzQjY0Mjg5RCIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PnfCIgEAAADUSURBVHjaYvz//z8DEHABsREQXwTizwzEAKBGbiC++R8CXgJxDMgwQhhEOP/HBLuBWAWfRiagpRxYHOICxJeAuAaI2XA51fs/fnAViG2x2UgIaAHxQSCeA8RCMEEmBuIAIxAnA/F1II4FCbAwkAbEgHgREGszMZAHwsnVuJpUp74H4gognk2KxuVAXAjEL4kNnHtAnAXEO5EF8fnxNxB3ArEOuiaYjT+xaDoOxOlAfBlf7uAF4gfQ5PUeiDOBmIlQ7mCE5kcBIDYH4rNA/IaYkAIIMABItNd0UvL9QAAAAABJRU5ErkJggg==";

//
let Base64 = require('js-base64').Base64;
const APPID = '98fcf4d6';
const API_SECRET = 'Y2FkN2Y1MGZjN2U0M2I1MTU4Y2FlZjA0';
const API_KEY = 'b7e405d6af09050e4a6976f31ec37640';
const recorder = new RecorderManager('/audio/recorder');
var script$51 = {
  name: 'XmlVoiceRender',
  components: {
    VoicePlayer: __vue_component__$52
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //评测数据
    voicedata: {},
    sourcedata: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('新data', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    },
    sourcedata: {
      handler(newval) {
        if (newval) {
          this.ratingData = newval;
        }
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        playMode: 'xun-huan',
        scheduleControl: 'yes',
        playStyle: 'yuan-wen',
        styleMode: 'A'
      },
      //属性组件名
      propertyComponentsName: 'xml-recorder-property',
      dialogShow: false,
      curRecordUrl: '',
      curRecordTitle: '',
      curCoverFileUrl: '',
      curRecordContent: '',
      currentPlayer: {},
      modalPlayerKey: 'mpk' + new Date().getTime(),
      isRecing: false,
      playIcon: playIcon$1,
      pauseIcon: pauseIcon$1,
      checkbox,
      chackboxactive,
      buffersArr: [],
      webSocket: null,
      ratingData: {},
      //测评数据
      showresult: false,
      //成绩弹窗
      countdown: 300,
      //倒计时长
      timer: null,
      //定时器
      loading: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      audioplay,
      audiopluse,
      statr,
      isPlay: false,
      recordAudio: null,
      lastAudioStamp: null
    };
  },
  methods: {
    updateTime(e) {
      this.playTime = this.$refs.recordAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.recordAudio.currentTime);
      if (this.voicedata.model != '1' && this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.recordAudio.duration);
        this.totalDuration = this.$refs.recordAudio.duration;
      });
    },
    togglePlay() {
      let _this = this;
      let audios = document.querySelectorAll('audio');
      audios.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      let audio = this.$refs.recordAudio;
      if (this.isPlay) {
        !(audio.ended || audio.paused) && audio.pause();
      } else {
        if (!this.lastPlayStamp || this.lastPlayStamp !== this.lastAudioStamp) {
          audio.onerror = function (e) {
            _this.$message.error(audio.error.message);
          };
          audio.currentTime = 0;
          audio.src = this.voicedata.recordDataUrl;
          audio.play();
          this.lastPlayStamp = this.lastAudioStamp;
        } else {
          audio.paused && audio.play();
        }
      }
      this.isPlay = !this.isPlay;
    },
    changeTime(val) {},
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog() {
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    timeUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('audioTimeUpdate', {
        event,
        time,
        data
      });
    },
    getresdata({
      blob,
      duration
    }) {
      this.$emit('getresdata', {
        blob,
        duration
      });
    },
    getTime(time) {
      let m = parseInt(time / 60 % 60);
      m = m < 10 ? '0' + m : m;
      let s = parseInt(time % 60);
      s = s < 10 ? '0' + s : s;
      // 作为返回值返回
      return m + ':' + s;
    },
    /**
     * 获取websocket url
     * 该接口需要后端提供，这里为了方便前端处理
     */
    getWebSocketUrl() {
      return new Promise((resolve, reject) => {
        // 请求地址根据语种不同变化
        var url = 'wss://ise-api.xfyun.cn/v2/open-ise';
        var host = 'ise-api.xfyun.cn';
        var apiKey = API_KEY;
        var apiSecret = API_SECRET;
        var date = new Date().toGMTString();
        var algorithm = 'hmac-sha256';
        var headers = 'host date request-line';
        var signatureOrigin = `host: ${host}\ndate: ${date}\nGET /v2/open-ise HTTP/1.1`;
        var signatureSha = cryptoJs.HmacSHA256(signatureOrigin, apiSecret);
        var signature = cryptoJs.enc.Base64.stringify(signatureSha);
        var authorizationOrigin = `api_key="${apiKey}", algorithm="${algorithm}", headers="${headers}", signature="${signature}"`;
        var authorization = btoa(authorizationOrigin);
        url = `${url}?authorization=${authorization}&date=${date}&host=${host}`;
        resolve(url);
      });
    },
    ontrolBtn() {
      if (!this.isRecing) {
        this.connectWebSocket();
        this.isRecing = true;
        recorder.onFrameRecorded = ({
          isLastFrame,
          frameBuffer
        }) => {
          this.buffersArr.push(frameBuffer);
          this.webSocket.send(JSON.stringify({
            business: {
              aue: 'raw',
              cmd: 'auw',
              aus: isLastFrame ? 4 : 2
            },
            data: {
              status: isLastFrame ? 2 : 1,
              data: this.toBase64(frameBuffer),
              data_type: 1
            }
          }));
          if (isLastFrame) {
            this.isRecing = false;
          }
        };
        this.timer = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown--;
          } else {
            clearInterval(this.timer);
            this.countdown = 300;
            recorder.stop();
          }
        }, 1000);
      } else {
        recorder.stop();
        let _this = this;
        _this.loading = true;
        _this.isRecing = false;
        clearInterval(this.timer);
        this.countdown = 300;
        recorder.onStop = function (res) {
          console.log(res);
          console.log(recorder);
          // const audioContext = new AudioContext();
          // const audioSource = audioContext.createBufferSource()
          // console.log(audioContext,audioSource);
          // audioContext.decodeAudioData(res[1], (buffer) => {
          //   console.log(buffer);
          //   audioSource.buffer = buffer;
          //   audioSource.connect(audioContext.destination);
          // })

          // const blob = new Blob(res, { type: 'audio/mp3' });
          // console.log(blob);
          // console.log((window.URL||webkitURL).createObjectURL(blob));

          // this.recordedAudio = URL.createObjectURL(blob);
          // this.$refs.audioPlayer.src = this.recordedAudio;
        };
        console.log('结束');
      }
    },
    // 连接websocket
    connectWebSocket() {
      return this.getWebSocketUrl().then(url => {
        let iseWS;
        if ('WebSocket' in window) {
          iseWS = new WebSocket(url);
        } else if ('MozWebSocket' in window) {
          iseWS = new MozWebSocket(url);
        } else {
          alert('浏览器不支持WebSocket');
          return;
        }
        this.webSocket = iseWS;
        this.webSocket.onopen = e => {
          // 重新开始录音

          recorder.start({
            sampleRate: 16000,
            frameSize: 1280
          });
          var params = {
            common: {
              app_id: APPID
            },
            business: {
              category: 'read_sentence',
              // read_syllable/单字朗读，汉语专有 read_word/词语朗读  read_sentence/句子朗读 https://www.xfyun.cn/doc/Ise/IseAPI.html#%E6%8E%A5%E5%8F%A3%E8%B0%83%E7%94%A8%E6%B5%81%E7%A8%8B
              rstcd: 'utf8',
              group: 'pupil',
              sub: 'ise',
              tte: 'utf-8',
              cmd: 'ssb',
              auf: 'audio/L16;rate=16000',
              ent: this.voicedata.language == 'cn' ? 'cn_vip' : 'en_vip',
              aus: 1,
              aue: 'raw',
              text: '\uFEFF' + this.voicedata.content
            },
            data: {
              status: 0
              // data_type: 1,
              // encoding: "raw",
            }
          };
          this.webSocket.send(JSON.stringify(params));
        };
        this.webSocket.onmessage = e => {
          this.result(e.data);
        };
        this.webSocket.onerror = e => {
          // this.recorderStop()
        };
        this.webSocket.onclose = e => {
          // this.recorderStop()
        };
      });
    },
    // 对处理后的音频数据进行base64编码
    toBase64(buffer) {
      var binary = '';
      var bytes = new Uint8Array(buffer);
      var len = bytes.byteLength;
      for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    },
    result(resultData) {

      // 识别结束
      let jsonData = JSON.parse(resultData);
      if (jsonData.data && jsonData.data.data) {
        // console.log(jsonData.data.data,'jsonData.data.data');
        let data = Base64.decode(jsonData.data.data);
        // console.log(data, 'dasdsadasdasdasdasdasdasdas');
        let grade = parser.parse(data, {
          attributeNamePrefix: '',
          ignoreAttributes: false
        });
        console.log(grade, 'grade结束的这一次');
        if (this.voicedata.language == 'en') {
          this.ratingData = grade?.xml_result?.read_sentence?.rec_paper?.read_chapter;
        } else {
          this.ratingData = grade?.xml_result?.read_sentence?.rec_paper?.read_sentence;
        }
        this.showresult = true;
        // this.blockData.ratingData = this.ratingData
        this.$emit('ratedata', {
          ratingData: this.ratingData,
          ruleIndex: this.ruleIndex,
          dataName: this.blockDataName
        });
        this.loading = false;
        // this.accuracy = readSentence?.accuracy_score;
        // this.fluency= readSentence?.fluency_score;
        // this.integrity= readSentence?.integrity_score;
        // this.phone= readSentence?.phone_score;
        // this.tone= readSentence?.tone_score;
        // this.emotion= readSentence?.emotion_score;
      }
      if (jsonData.code === 0 && jsonData.data.status === 2) {
        this.webSocket.close();
      }
      if (jsonData.code !== 0) {
        this.webSocket.close();
      }
    }
  },
  created() {},
  computed: {
    isEmpty() {
      return !(this.blockData.recordSetList && this.blockData.recordSetList.length);
    }
  }
};

var __$_require_images_voice_icon5_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$51 = script$51;
var __vue_render__$51 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-recorder-container",
      class: "xml-recorder-container-" + _vm.pageType,
      style: [
        {
          "--themecolor": _vm.voicedata.ThemeColor
            ? _vm.voicedata.ThemeColor
            : "#2D5DD8",
        },
      ],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      [
        _c("div", { staticClass: "fixed-voice-container" }, [
          _c(
            "div",
            {
              class: [
                "xml-voice__wrapper",
                "style-mode--" + _vm.blockData.styleMode,
                _vm.mode,
              ],
            },
            [
              _c(
                "div",
                { staticClass: "xml-recorder-player-box xml-group-item" },
                [
                  _c("div", { staticClass: "voice-image-con" }, [
                    _vm.voicedata.imgurl
                      ? _c("img", {
                          style: { width: _vm.voicedata.imgWidth + "%" },
                          attrs: { src: _vm.voicedata.imgurl, alt: "" },
                        })
                      : _c("div", [_vm._v(_vm._s(_vm.voicedata.content))]),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "btn-group" }, [
                    _c("div", { on: { click: _vm.togglePlay } }, [
                      _c(
                        "div",
                        {
                          staticClass: "icon-box",
                          staticStyle: {
                            "background-color": "var(--themecolor)",
                          },
                        },
                        [
                          _c("img", {
                            staticClass: "img1",
                            attrs: {
                              src: _vm.isPlay ? _vm.audioplay : _vm.audiopluse,
                              alt: "",
                            },
                          }),
                        ]
                      ),
                      _vm._v(" "),
                      _c("span", { staticClass: "text" }, [
                        _vm._v(
                          _vm._s(
                            _vm.voicedata.btntype == "en"
                              ? "listen"
                              : "原音试听"
                          )
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        on: {
                          click: function ($event) {
                            $event.stopPropagation();
                            return _vm.ontrolBtn.apply(null, arguments)
                          },
                        },
                      },
                      [
                        _c(
                          "div",
                          {
                            staticClass: "icon-box",
                            staticStyle: {
                              "background-color": "var(--themecolor)",
                            },
                          },
                          [
                            !_vm.isRecing
                              ? _c("img", {
                                  staticClass: "img4",
                                  attrs: { src: _vm.playIcon, alt: "" },
                                })
                              : _c("img", {
                                  staticClass: "img2",
                                  attrs: { src: _vm.pauseIcon, alt: "" },
                                }),
                          ]
                        ),
                        _vm._v(" "),
                        _c("span", { staticClass: "text" }, [
                          _vm._v(
                            _vm._s(
                              _vm.voicedata.btntype == "en"
                                ? "record"
                                : "点击话筒，录音测验"
                            )
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        on: {
                          click: function ($event) {
                            _vm.ratingData.total_score
                              ? (_vm.showresult = true)
                              : "";
                          },
                        },
                      },
                      [
                        _vm._m(0),
                        _vm._v(" "),
                        _c("span", { staticClass: "text" }, [
                          _vm._v(
                            _vm._s(
                              _vm.voicedata.btntype == "en"
                                ? "check out"
                                : "评分查看"
                            )
                          ),
                        ]),
                      ]
                    ),
                  ]),
                  _vm._v(" "),
                  _vm.voicedata.isProgress != "none"
                    ? _c(
                        "div",
                        {
                          staticClass: "play-box",
                          staticStyle: {
                            "background-color": "var(--themecolor)",
                          },
                        },
                        [
                          _c(
                            "div",
                            {
                              staticClass: "play-icon",
                              on: { click: _vm.togglePlay },
                            },
                            [
                              _c("img", {
                                attrs: {
                                  src: _vm.isPlay ? _vm.audioplay : _vm.statr,
                                  alt: "",
                                },
                              }),
                            ]
                          ),
                          _vm._v(" "),
                          _c("div", { staticClass: "text" }, [
                            _vm._v(
                              "\n                " +
                                _vm._s(_vm.playCurrentTime) +
                                " / " +
                                _vm._s(_vm.playDuration) +
                                "\n              "
                            ),
                          ]),
                          _vm._v(" "),
                          _c(
                            "div",
                            { staticClass: "slider-box" },
                            [
                              _c("el-slider", {
                                attrs: {
                                  max: _vm.totalDuration,
                                  disabled: _vm.voicedata.progress !== "1",
                                  "show-tooltip": false,
                                },
                                on: { change: _vm.changeTime },
                                model: {
                                  value: _vm.playTime,
                                  callback: function ($$v) {
                                    _vm.playTime = $$v;
                                  },
                                  expression: "playTime",
                                },
                              }),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _c("div", { staticClass: "text" }, [
                            _vm._v("原音试听"),
                          ]),
                        ]
                      )
                    : _vm._e(),
                ]
              ),
            ]
          ),
        ]),
      ],
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          style: [
            {
              "--themecolor": _vm.voicedata.ThemeColor
                ? _vm.voicedata.ThemeColor
                : "#2D5DD8",
            },
          ],
          attrs: {
            title: "查看分数",
            visible: _vm.showresult,
            width:
              _vm.pageType != "pc" && _vm.mode != "editor" ? "80vw" : "456px",
            top: "50vh",
            modal: false,
            "show-close": true,
            "append-to-body": "",
            "close-on-click-modal": true,
            "custom-class": "xml-voice-container",
          },
          on: {
            "update:visible": function ($event) {
              _vm.showresult = $event;
            },
          },
        },
        [
          _c("div", { staticClass: "xml-result-con" }, [
            _c(
              "div",
              { staticClass: "total" },
              [
                _c("div", { staticClass: "number-box" }, [
                  _c("div", [_vm._v("满分100分")]),
                  _vm._v(" "),
                  _c("div", [
                    _c("span", [
                      _vm._v(
                        _vm._s(Number(_vm.ratingData.total_score).toFixed(0))
                      ),
                    ]),
                    _vm._v("分\n          "),
                  ]),
                ]),
                _vm._v(" "),
                _c("el-progress", {
                  attrs: {
                    type: "circle",
                    percentage: Number(_vm.ratingData.total_score),
                    "stroke-width": 8,
                    color: _vm.voicedata.ThemeColor,
                    "show-text": false,
                  },
                }),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "comprehensive",
                    style: { background: _vm.voicedata.ThemeColor },
                  },
                  [_vm._v("综合评分")]
                ),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "score-item-box" }, [
              _c("div", [
                _c("div", [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1705998006086",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "11718",
                        width: "16",
                        height: "16",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                          fill: _vm.voicedata.ThemeColor,
                          "p-id": "11719",
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("span", [_vm._v("准确度分")]),
                ]),
                _vm._v(" "),
                _c("span", [
                  _vm._v(
                    _vm._s(Number(_vm.ratingData.accuracy_score).toFixed(2))
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("div", [
                _c("div", [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1705998006086",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "11718",
                        width: "16",
                        height: "16",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                          fill: _vm.voicedata.ThemeColor,
                          "p-id": "11719",
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("span", [_vm._v("流畅度分")]),
                ]),
                _vm._v(" "),
                _c("span", [
                  _vm._v(
                    _vm._s(Number(_vm.ratingData.fluency_score).toFixed(2))
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("div", [
                _c("div", [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1705998006086",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "11718",
                        width: "16",
                        height: "16",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                          fill: _vm.voicedata.ThemeColor,
                          "p-id": "11719",
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("span", [_vm._v("完整度分")]),
                ]),
                _vm._v(" "),
                _c("span", [
                  _vm._v(
                    _vm._s(Number(_vm.ratingData.integrity_score).toFixed(2))
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { class: _vm.voicedata.language == "cn" ? "" : "Gray" },
                [
                  _c("div", [
                    _c(
                      "svg",
                      {
                        staticClass: "icon",
                        attrs: {
                          t: "1705998006086",
                          viewBox: "0 0 1024 1024",
                          version: "1.1",
                          xmlns: "http://www.w3.org/2000/svg",
                          "p-id": "11718",
                          width: "16",
                          height: "16",
                        },
                      },
                      [
                        _c("path", {
                          attrs: {
                            d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                            fill:
                              _vm.voicedata.language == "cn"
                                ? _vm.voicedata.ThemeColor
                                : "#ccc",
                            "p-id": "11719",
                          },
                        }),
                      ]
                    ),
                    _vm._v(" "),
                    _c("span", [_vm._v("声韵分")]),
                  ]),
                  _vm._v(" "),
                  _c("span", [
                    _vm._v(
                      _vm._s(
                        _vm.voicedata.language == "cn"
                          ? Number(_vm.ratingData.phone_score).toFixed(2)
                          : "-"
                      )
                    ),
                  ]),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                { class: _vm.voicedata.language == "cn" ? "" : "Gray" },
                [
                  _c("div", [
                    _c(
                      "svg",
                      {
                        staticClass: "icon",
                        attrs: {
                          t: "1705998006086",
                          viewBox: "0 0 1024 1024",
                          version: "1.1",
                          xmlns: "http://www.w3.org/2000/svg",
                          "p-id": "11718",
                          width: "16",
                          height: "16",
                        },
                      },
                      [
                        _c("path", {
                          attrs: {
                            d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                            fill:
                              _vm.voicedata.language == "cn"
                                ? _vm.voicedata.ThemeColor
                                : "#ccc",
                            "p-id": "11719",
                          },
                        }),
                      ]
                    ),
                    _vm._v(" "),
                    _c("span", [_vm._v("调型分")]),
                  ]),
                  _vm._v(" "),
                  _c("span", [
                    _vm._v(
                      _vm._s(
                        _vm.voicedata.language == "cn"
                          ? Number(_vm.ratingData.tone_score).toFixed(2)
                          : "-"
                      )
                    ),
                  ]),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                { class: _vm.voicedata.language == "cn" ? "" : "Gray" },
                [
                  _c("div", [
                    _c(
                      "svg",
                      {
                        staticClass: "icon",
                        attrs: {
                          t: "1705998006086",
                          viewBox: "0 0 1024 1024",
                          version: "1.1",
                          xmlns: "http://www.w3.org/2000/svg",
                          "p-id": "11718",
                          width: "16",
                          height: "16",
                        },
                      },
                      [
                        _c("path", {
                          attrs: {
                            d: "M450.484288 840.399261L160.887246 543.231054l49.212569-70.033272 236.598891 181.707948L946.395564 246.062847C857.434381 100.31793 694.654344 1.892791 511.053604 1.892791 229.027726 1.892791 0 230.920518 0 512.946396s229.027726 511.053604 511.053604 511.053604 511.053604-229.027726 511.053605-511.053604c0-70.033272-13.249538-136.280961-39.748614-196.850278L450.484288 840.399261z",
                            fill:
                              _vm.voicedata.language == "cn"
                                ? _vm.voicedata.ThemeColor
                                : "#ccc",
                            "p-id": "11719",
                          },
                        }),
                      ]
                    ),
                    _vm._v(" "),
                    _c("span", [_vm._v("整体印象分")]),
                  ]),
                  _vm._v(" "),
                  _c("span", [
                    _vm._v(
                      _vm._s(
                        _vm.voicedata.language == "cn"
                          ? Number(_vm.ratingData.emotion_score).toFixed(2)
                          : "-"
                      )
                    ),
                  ]),
                ]
              ),
            ]),
          ]),
        ]
      ),
      _vm._v(" "),
      _c("audio", {
        ref: "recordAudio",
        staticClass: "audio-component",
        attrs: {
          controls: "",
          preload: "auto",
          src: _vm.voicedata.recordDataUrl,
          loop: _vm.voicedata.model == "1" ? true : false,
        },
        on: {
          canplay: _vm.changeDuration,
          timeupdate: _vm.updateTime,
          pause: function ($event) {
            _vm.isPlay = false;
          },
        },
      }),
    ],
    2
  )
};
var __vue_staticRenderFns__$51 = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c(
      "div",
      {
        staticClass: "icon-box",
        staticStyle: { "background-color": "var(--themecolor)" },
      },
      [
        _c("img", {
          staticClass: "img3",
          attrs: { src: __$_require_images_voice_icon5_png__, alt: "" },
        }),
      ]
    )
  },
];
__vue_render__$51._withStripped = true;

  /* style */
  const __vue_inject_styles__$51 = undefined;
  /* scoped */
  const __vue_scope_id__$51 = undefined;
  /* module identifier */
  const __vue_module_identifier__$51 = undefined;
  /* functional template */
  const __vue_is_functional_template__$51 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$51 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$51, staticRenderFns: __vue_staticRenderFns__$51 },
    __vue_inject_styles__$51,
    __vue_script__$51,
    __vue_scope_id__$51,
    __vue_is_functional_template__$51,
    __vue_module_identifier__$51,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$51.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-recorder-group-render', {
    prefix: options.prefix
  }), __vue_component__$51);
};

class DomUtils {
  static config = {
    MIN_LIMIT: 20,
    // Min size of zone
    DECIMAL_PLACES: 4 // Hotzone positioning decimal point limit number of digits
  };

  /**
   * Get a power result of 10 for the power of the constant
   * @return {Number}
   */
  static getMultiple = (decimalPlaces = this.config.DECIMAL_PLACES) => 10 ** decimalPlaces;

  /**
   * Limit decimal places
   * @param  {Number} num
   * @return {Number}
   */
  static decimalPoint = (val = 0) => Math.round(val * this.getMultiple()) / this.getMultiple() || 0;

  /**
   * Get element width and height
   * @param  {Object} elem
   * @return {Object}
   */
  static getOffset = (elem = {}) => ({
    width: elem.clientWidth || 0,
    height: elem.clientHeight || 0
  });

  /**
   * Get pageX
   * @param  {Object} e
   * @return {Number}
   */
  static getPageX = e => 'pageX' in e ? e.pageX : e.touches[0].pageX;

  /**
   * Get pageY
   * @param  {Object} e
   * @return {Number}
   */
  static getPageY = e => 'pageY' in e ? e.pageY : e.touches[0].pageY;

  /**
   * Gets the abscissa value of the mouse click relative to the target node
   * @param  {Object} e
   * @param  {Object} container
   * @return {Number}
   */
  static getDistanceX = (e, container) => this.getPageX(e) - (container.getBoundingClientRect().left + window.pageXOffset);

  /**
   * Gets the ordinate value of the mouse click relative to the target node
   * @param  {Object} e
   * @param  {Object} container
   * @return {Number}
   */
  static getDistanceY = (e, container) => this.getPageY(e) - (container.getBoundingClientRect().top + window.pageYOffset);

  /**
   * Treatment of boundary conditions when changing the size of the hotzone
   * @param {Object} itemInfo
   * @param {Object} styleInfo
   * @param {Object} container
   */
  static dealEdgeValue = (itemInfo, styleInfo, container) => {
    if (styleInfo.hasOwnProperty('left') && styleInfo.left < 0) {
      styleInfo.left = 0;
      styleInfo.width = itemInfo.width + itemInfo.left;
    }
    if (styleInfo.hasOwnProperty('top') && styleInfo.top < 0) {
      styleInfo.top = 0;
      styleInfo.height = itemInfo.height + itemInfo.top;
    }
    if (!styleInfo.hasOwnProperty('left') && styleInfo.hasOwnProperty('width')) {
      if (itemInfo.left + styleInfo.width > container.width) {
        styleInfo.width = container.width - itemInfo.left;
      }
    }
    if (!styleInfo.hasOwnProperty('top') && styleInfo.hasOwnProperty('height')) {
      if (itemInfo.top + styleInfo.height > container.height) {
        styleInfo.height = container.height - itemInfo.top;
      }
    }
    return Object.assign(itemInfo, styleInfo);
  };

  /**
   * Handle different drag points, capital letters mean: T-top，L-left，C-center，R-right，B-bottom
   * @param  {Object} itemInfo
   * @param  {Number} moveX
   * @param  {Number} moveY
   * @return {Object}
   */
  static dealTL = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width - moveX;
    const height = itemInfo.height - moveY;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width,
        left: itemInfo.left + moveX
      });
    }
    if (height >= Math.min(minLimit, itemInfo.height)) {
      Object.assign(styleInfo, {
        height,
        top: itemInfo.top + moveY
      });
    }
    return styleInfo;
  };
  static dealTC = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    let styleInfo = {};
    const height = itemInfo.height - moveY;
    if (height >= Math.min(minLimit, itemInfo.height)) {
      styleInfo = {
        height,
        top: itemInfo.top + moveY
      };
    }
    return styleInfo;
  };
  static dealTR = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width + moveX;
    const height = itemInfo.height - moveY;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width
      });
    }
    if (height >= Math.min(minLimit, itemInfo.height)) {
      Object.assign(styleInfo, {
        height,
        top: itemInfo.top + moveY
      });
    }
    return styleInfo;
  };
  static dealCL = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width - moveX;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width,
        left: itemInfo.left + moveX
      });
    }
    return styleInfo;
  };
  static dealCR = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width + moveX;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width
      });
    }
    return styleInfo;
  };
  static dealBL = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width - moveX;
    const height = itemInfo.height + moveY;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width,
        left: itemInfo.left + moveX
      });
    }
    if (height >= Math.min(minLimit, itemInfo.height)) {
      Object.assign(styleInfo, {
        height
      });
    }
    return styleInfo;
  };
  static dealBC = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const height = itemInfo.height + moveY;
    if (height >= Math.min(minLimit, itemInfo.height)) {
      Object.assign(styleInfo, {
        height
      });
    }
    return styleInfo;
  };
  static dealBR = (itemInfo, moveX, moveY, minLimit = this.config.MIN_LIMIT) => {
    const styleInfo = {};
    const width = itemInfo.width + moveX;
    const height = itemInfo.height + moveY;
    if (width >= Math.min(minLimit, itemInfo.width)) {
      Object.assign(styleInfo, {
        width
      });
    }
    if (height >= Math.min(minLimit, itemInfo.height)) {
      Object.assign(styleInfo, {
        height
      });
    }
    return styleInfo;
  };
}

var changeSize = {
  bind(el, binding, vnode) {
    if (!vnode.context.isInCreater) {
      return;
    }
    el.addEventListener('mousedown', handleMouseDown);
    function handleMouseDown(e) {
      const pointer = e.target.dataset.pointer;
      if (!pointer) {
        return;
      }
      e.stopPropagation();
      const zone = el.parentNode;
      const setting = vnode.context.setting;
      const container = DomUtils.getOffset(zone.parentNode);
      let itemInfo = {
        width: DomUtils.getOffset(zone).width || 0,
        height: DomUtils.getOffset(zone).height || 0,
        top: setting.topPer * container.height || 0,
        left: setting.leftPer * container.width || 0
      };
      let preX = DomUtils.getPageX(e);
      let preY = DomUtils.getPageY(e);
      let flag;

      // Hide the info displayed by hover
      vnode.context.handlehideZone(true);
      window.addEventListener('mousemove', handleChange);
      window.addEventListener('mouseup', handleMouseUp);

      // eslint-disable-next-line no-shadow
      function handleChange(e) {
        e.preventDefault();
        flag = true;
        const moveX = DomUtils.getPageX(e) - preX;
        const moveY = DomUtils.getPageY(e) - preY;
        preX = DomUtils.getPageX(e);
        preY = DomUtils.getPageY(e);

        // Handling the situation when different dragging points are selected
        const styleInfo = DomUtils[pointer](itemInfo, moveX, moveY);

        // Boundary value processing
        itemInfo = DomUtils.dealEdgeValue(itemInfo, styleInfo, container);
        Object.assign(zone.style, {
          top: `${itemInfo.top}px`,
          left: `${itemInfo.left}px`,
          width: `${itemInfo.width}px`,
          height: `${itemInfo.height}px`
        });
      }
      function handleMouseUp() {
        if (flag) {
          flag = false;
          const perInfo = {
            topPer: DomUtils.decimalPoint(itemInfo.top / container.height),
            leftPer: DomUtils.decimalPoint(itemInfo.left / container.width),
            widthPer: DomUtils.decimalPoint(itemInfo.width / container.width),
            heightPer: DomUtils.decimalPoint(itemInfo.height / container.height)
          };
          vnode.context.changeInfo(perInfo);

          // 兼容数据无变更情况下导致 computed 不更新，数据仍为 px 时 resize 出现的问题
          Object.assign(zone.style, {
            top: `${itemInfo.top}px`,
            left: `${itemInfo.left}px`,
            width: `${itemInfo.width}px`,
            height: `${itemInfo.height}px`
          });
        }
        // Show the info
        vnode.context.handlehideZone(false);
        window.removeEventListener('mousemove', handleChange);
        window.removeEventListener('mouseup', handleMouseUp);
      }
    }
    el.$destroy = () => el.removeEventListener('mousedown', handleMouseDown);
  },
  unbind(el) {
    el && el.$destroy && el.$destroy();
  }
};

console.log('工具函数集', DomUtils);
var dragItem = {
  bind(el, binding, vnode) {
    if (!vnode.context.isInCreater) {
      return;
    }
    el.addEventListener('mousedown', handleMouseDown);
    function handleMouseDown(e) {
      e.stopPropagation();
      const container = DomUtils.getOffset(el.parentNode);
      const preX = DomUtils.getPageX(e);
      const preY = DomUtils.getPageY(e);
      let topPer;
      let leftPer;
      let flag;
      window.addEventListener('mousemove', handleChange);
      window.addEventListener('mouseup', handleMouseUp);

      // eslint-disable-next-line no-shadow
      function handleChange(e) {
        e.preventDefault();
        flag = true;

        // Hide the info displayed by hover
        vnode.context.handlehideZone(true);
        const setting = vnode.context.setting;
        let moveX = DomUtils.getPageX(e) - preX;
        let moveY = DomUtils.getPageY(e) - preY;
        setting.topPer = setting.topPer || 0;
        setting.leftPer = setting.leftPer || 0;
        topPer = DomUtils.decimalPoint(moveY / container.height + setting.topPer);
        leftPer = DomUtils.decimalPoint(moveX / container.width + setting.leftPer);

        // Hotzone moving boundary processing
        if (topPer < 0) {
          topPer = 0;
          moveY = -container.height * setting.topPer;
        }
        if (leftPer < 0) {
          leftPer = 0;
          moveX = -container.width * setting.leftPer;
        }
        if (topPer + setting.heightPer > 1) {
          topPer = 1 - setting.heightPer;
          moveY = container.height * (topPer - setting.topPer);
        }
        if (leftPer + setting.widthPer > 1) {
          leftPer = 1 - setting.widthPer;
          moveX = container.width * (leftPer - setting.leftPer);
        }
        el.style.transform = `translate(${moveX}px, ${moveY}px)`;
      }
      function handleMouseUp() {
        if (flag) {
          flag = false;
          el.style.transform = 'translate(0, 0)';
          vnode.context.changeInfo({
            topPer,
            leftPer
          });
        }

        // Show the info
        vnode.context.handlehideZone(false);
        window.removeEventListener('mousemove', handleChange);
        window.removeEventListener('mouseup', handleMouseUp);
      }
    }
    el.$destroy = () => el.removeEventListener('mousedown', handleMouseDown);
  },
  unbind(el) {
    el && el.$destroy && el.$destroy();
  }
};

//
var script$50 = {
  name: 'Zone',
  directives: {
    changeSize,
    dragItem
  },
  props: {
    index: {
      type: [Number, String],
      default: ''
    },
    setting: {
      type: [Object],
      default: () => ({})
    },
    isInCreater: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      zoneTop: '',
      zoneLeft: '',
      zoneWidth: '',
      zoneHeight: '',
      hideZone: false,
      tooSmall: false
    };
  },
  computed: {
    hoverTitle() {
      let {
        hotTarget
      } = this.setting;
      let text = '';
      if (hotTarget) {
        switch (hotTarget.type) {
          case 'link':
            text = '链接';
            break;
          case 'looseleaf':
            text = '活页';
            break;
          case 'chapter':
            text = '章节';
            break;
        }
      }
      return text;
    }
  },
  watch: {
    setting: {
      handler(val) {
        this.setZoneInfo(val);
      },
      deep: true
    }
  },
  mounted() {
    this.setZoneInfo(this.setting);
  },
  methods: {
    setZoneInfo(val) {
      this.zoneTop = this.getZoneStyle(val.topPer);
      this.zoneLeft = this.getZoneStyle(val.leftPer);
      this.zoneWidth = this.getZoneStyle(val.widthPer);
      this.zoneHeight = this.getZoneStyle(val.heightPer);
      this.tooSmall = val.widthPer < 0.01 && val.heightPer < 0.01;
    },
    handlehideZone(isHide = true) {
      if (this.hideZone === isHide) {
        return;
      }
      this.hideZone = isHide;
    },
    changeInfo(info = {}) {
      const {
        index
      } = this;
      this.$emit('changeInfo', {
        info,
        index
      });
    },
    delItem(index) {
      this.$emit('delItem', index);
    },
    getZoneStyle(val) {
      return `${(val || 0) * 100}%`;
    }
  }
};

/* script */
const __vue_script__$50 = script$50;

/* template */
var __vue_render__$50 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      directives: [{ name: "drag-item", rawName: "v-drag-item" }],
      style: {
        top: _vm.zoneTop,
        left: _vm.zoneLeft,
        width: _vm.zoneWidth,
        height: _vm.zoneHeight,
      },
      attrs: { title: _vm.hoverTitle },
    },
    [
      _c(
        "ul",
        {
          directives: [{ name: "change-size", rawName: "v-change-size" }],
          staticClass: "hz-m-box",
          class: {
            "hz-z-hidden": _vm.tooSmall,
            "hz-m-hoverbox": !_vm.hideZone,
          },
        },
        [
          _c(
            "li",
            {
              staticClass: "hz-u-index",
              attrs: { title: "热区" + (_vm.index + 1) },
            },
            [_vm._v(_vm._s(_vm.isInCreater ? "热区" + (_vm.index + 1) : ""))]
          ),
          _vm._v(" "),
          _c(
            "li",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: !_vm.hideZone && _vm.isInCreater,
                  expression: "!hideZone && isInCreater",
                },
              ],
              staticClass: "hz-u-close",
              attrs: { title: "删除该热区" },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.delItem(_vm.index)
                },
              },
            },
            [
              _c(
                "svg",
                {
                  staticClass: "hz-u-close-icon",
                  attrs: {
                    xmlns: "http://www.w3.org/2000/svg",
                    viewBox: "0 0 1024 1024",
                  },
                },
                [
                  _c("path", {
                    attrs: {
                      d: "M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1\n          191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0\n          0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z",
                    },
                  }),
                ]
              ),
            ]
          ),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-tl",
            attrs: { "data-pointer": "dealTL" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-tc",
            attrs: { "data-pointer": "dealTC" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-tr",
            attrs: { "data-pointer": "dealTR" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-cl",
            attrs: { "data-pointer": "dealCL" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-cr",
            attrs: { "data-pointer": "dealCR" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-bl",
            attrs: { "data-pointer": "dealBL" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-bc",
            attrs: { "data-pointer": "dealBC" },
          }),
          _vm._v(" "),
          _c("li", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isInCreater,
                expression: "isInCreater",
              },
            ],
            staticClass: "hz-u-square hz-u-square-br",
            attrs: { "data-pointer": "dealBR" },
          }),
        ]
      ),
    ]
  )
};
var __vue_staticRenderFns__$50 = [];
__vue_render__$50._withStripped = true;

  /* style */
  const __vue_inject_styles__$50 = undefined;
  /* scoped */
  const __vue_scope_id__$50 = undefined;
  /* module identifier */
  const __vue_module_identifier__$50 = undefined;
  /* functional template */
  const __vue_is_functional_template__$50 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$50 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$50, staticRenderFns: __vue_staticRenderFns__$50 },
    __vue_inject_styles__$50,
    __vue_script__$50,
    __vue_scope_id__$50,
    __vue_is_functional_template__$50,
    __vue_module_identifier__$50,
    false,
    undefined,
    undefined,
    undefined
  );

var addItem = {
  bind(el, binding, vnode) {
    const MIN_LIMIT = DomUtils.config.MIN_LIMIT;
    if (!vnode.context.isInCreater) {
      return;
    }
    el.addEventListener('mousedown', handleMouseDown);
    function handleMouseDown(e) {
      e.preventDefault();
      if (vnode.context?.zones?.length >= vnode.context.max) {
        vnode.context.onMaxCount();
        return;
      }
      let itemInfo = {
        top: DomUtils.getDistanceY(e, el),
        left: DomUtils.getDistanceX(e, el),
        width: 0,
        height: 0
      };
      const container = DomUtils.getOffset(el);

      // Only used once at the beginning of init
      const setting = {
        topPer: DomUtils.decimalPoint(itemInfo.top / container.height),
        leftPer: DomUtils.decimalPoint(itemInfo.left / container.width),
        widthPer: 0,
        heightPer: 0,
        hotTarget: {
          type: null,
          value: ''
        }
      };
      let preX = DomUtils.getPageX(e);
      let preY = DomUtils.getPageY(e);
      vnode.context.addItem(setting);
      window.addEventListener('mousemove', handleChange);
      window.addEventListener('mouseup', handleMouseUp);

      // eslint-disable-next-line no-shadow
      function handleChange(e) {
        e.preventDefault();
        const moveX = DomUtils.getPageX(e) - preX;
        const moveY = DomUtils.getPageY(e) - preY;
        preX = DomUtils.getPageX(e);
        preY = DomUtils.getPageY(e);

        // Not consider the direction of movement first, consider only the lower right drag point
        const minLimit = 0;
        const styleInfo = DomUtils.dealBR(itemInfo, moveX, moveY, minLimit);

        // Boundary value processing
        itemInfo = DomUtils.dealEdgeValue(itemInfo, styleInfo, container);
        Object.assign(el.lastElementChild.style, {
          top: `${itemInfo.top}px`,
          left: `${itemInfo.left}px`,
          width: `${itemInfo.width}px`,
          height: `${itemInfo.height}px`
        });
      }
      function handleMouseUp() {
        const perInfo = {
          topPer: DomUtils.decimalPoint(itemInfo.top / container.height),
          leftPer: DomUtils.decimalPoint(itemInfo.left / container.width),
          widthPer: DomUtils.decimalPoint(itemInfo.width / container.width),
          heightPer: DomUtils.decimalPoint(itemInfo.height / container.height),
          hotTarget: {
            type: null,
            value: ''
          }
        };
        if (vnode.context.isOverRange()) {
          console.log('isOverRange!!!!!!!!!!');
          vnode.context.overRange();
        } else if (container.height < MIN_LIMIT && itemInfo.width > MIN_LIMIT) {
          vnode.context.changeItem(Object.assign(perInfo, {
            topPer: 0,
            heightPer: 1
          }));
        } else if (container.width < MIN_LIMIT && itemInfo.height > MIN_LIMIT) {
          vnode.context.changeItem(Object.assign(perInfo, {
            leftper: 0,
            widthPer: 1
          }));
        } else if (itemInfo.width > MIN_LIMIT && itemInfo.height > MIN_LIMIT) {
          vnode.context.changeItem(perInfo);
        } else {
          console.log('eraseItem!!!!!!!!!!');
          vnode.context.eraseItem();
        }
        window.removeEventListener('mousemove', handleChange);
        window.removeEventListener('mouseup', handleMouseUp);
      }
    }
    el.$destroy = () => el.removeEventListener('mousedown', handleMouseDown);
  },
  unbind(el) {
    el && el.$destroy && el.$destroy();
  }
};

//
var script$4$ = {
  name: 'HotZone',
  directives: {
    addItem
  },
  components: {
    Zone: __vue_component__$50
  },
  inject: {
    getCurrentRuleIndex: {
      value: 'getCurrentRuleIndex',
      default: null
    },
    getSelfRuleIndex: {
      value: 'getSelfRuleIndex',
      default: null
    }
  },
  props: {
    image: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    max: {
      type: Number,
      default: Infinity
    },
    isInCreater: {
      type: Boolean,
      default: false
    },
    zoneVisible: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String,
      default: 'editor'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  computed: {
    zones: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    isInEditor() {
      return !this.isInCreater && this.mode === 'editor';
    }
  },
  methods: {
    findCurSelect() {
      return this.value.find(item => item.selected);
    },
    onSelect(zone, by) {
      console.log('热区zone选择', this.isInCreater, by);
      if (!this.isInCreater && by === 'addItem') {
        this.$emit('wrap-click');
      }
      if (this.isInCreater || by === 'wrap') {
        const curZone = this.findCurSelect();
        if (curZone) curZone.selected = 0;
        if (zone) {
          this.$set(zone, 'selected', 1);
        }
        this.$emit('select-zone', zone);
        this.hasChange();
      } else {
        if (by === 'click') {
          const curZone = this.findCurSelect();
          if (curZone) curZone.selected = 0;
          if (zone) {
            this.$set(zone, 'selected', 1);
          }
          let {
            type,
            value,
            label,
            desc,
            cover
          } = zone.hotTarget || {};
          console.log('zone点击', type);
          this.$EventBus.$emit('hotzoneClick', {
            type,
            value,
            label,
            desc,
            cover
          });
          if (this.isInEditor) {
            this.$emit('wrap-click');
          }
        }
      }
    },
    changeInfo(res) {
      const {
        info,
        index
      } = res;
      this.changeItem(info, index, 1);
    },
    addItem(setting) {
      this.zones.push(setting);
      this.hasChange();
      this.$emit('add', setting);
      this.onSelect(setting, 'addItem');
    },
    onMaxCount() {
      this.$message.warning(`最多可创建${this.max}个热区`);
    },
    eraseItem(index = this.zones.length - 1) {
      this.removeItem(index);
      this.$emit('erase', index);
    },
    isOverRange() {
      const {
        max,
        zones
      } = this;
      return max && zones.length > max;
    },
    overRange() {
      const index = this.zones.length - 1;
      this.removeItem(index);
      this.$emit('overRange', index);
    },
    removeItem(index = this.zones.length - 1) {
      const curZone = this.zones[index];
      // 移除当前选中的
      if (curZone === this.findCurSelect()) {
        this.onSelect(null);
      }

      //  移除当前项
      this.zones.splice(index, 1);
      this.hasChange();
      this.$emit('remove', curZone, index);
    },
    changeItem(info, index = this.zones.length - 1, isChangeInfo) {
      Object.assign(this.zones[index], info);
      this.hasChange();
      this.onSelect(this.zones[index], isChangeInfo ? 'changeItem' : 'changeItem');
    },
    hasChange() {
      this.$emit('change', this.zones);
    },
    setZoneTarget(data) {
      const curZone = this.findCurSelect();
      let isOperating = this.getCurrentRuleIndex && this.getCurrentRuleIndex() === this.getSelfRuleIndex();
      if (curZone && (isOperating || this.isInCreater)) {
        curZone.hotTarget = {
          ...data
        };
        this.hasChange();
      }
    }
  },
  mounted() {
    let _this = this;
    this.$EventBus.$on('setZoneTarget', data => {
      _this.setZoneTarget(data);
    });
    // this.$EventBus.$on('setInitZones', (data) => {
    //   console.log('热区数据更新', data);
    //   try {
    //     let current = data.find(item => item.selected)
    //   if(!current) {
    //     data[0].selected = true
    //   }
    //   data.forEach((item,index) => {
    //     _this.zones.splice(index,1,item)
    //   })
    //   // setTimeout(() => {
    //   //   _this.zones = [...data]
    //   // },500)
    //   console.log('没更新????',this.zones);
    //   } catch (error) {
    //     console.log('出错了',error);
    //   }

    // })
  },
  destroyed() {
    this.$EventBus.$off('setZoneTarget');
    // this.$EventBus.$off('setInitZones')
  }
};

/* script */
const __vue_script__$4$ = script$4$;

/* template */
var __vue_render__$4$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "content",
      class: ["hz-m-wrap", _vm.isInCreater ? "editing" : ""],
      on: {
        click: function ($event) {
          if ($event.target !== $event.currentTarget) {
            return null
          }
          return _vm.onSelect(null, "wrap")
        },
      },
    },
    [
      _c("img", {
        staticClass: "hz-u-img",
        attrs: {
          src:
            _vm.image.indexOf("./") !== -1
              ? _vm.resourceBasisPath + _vm.image.split("./")[1]
              : _vm.image,
          alt: "",
        },
      }),
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [{ name: "add-item", rawName: "v-add-item" }],
          staticClass: "hz-m-area",
        },
        _vm._l(_vm.zones, function (zone, index) {
          return _c("zone", {
            key: index,
            staticClass: "hz-m-item",
            class: {
              "hz-m-item-selected": zone.selected,
              "in-editor": _vm.mode === "editor" && !_vm.isInCreater,
              "is-transparent": !_vm.isInCreater && !_vm.zoneVisible,
            },
            attrs: {
              index: index,
              setting: zone,
              isInCreater: _vm.isInCreater,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: {
              delItem: function ($event) {
                return _vm.removeItem($event)
              },
              changeInfo: function ($event) {
                return _vm.changeInfo($event)
              },
            },
            nativeOn: {
              click: function ($event) {
                $event.stopPropagation();
                return _vm.onSelect(zone, "click")
              },
            },
          })
        }),
        1
      ),
    ]
  )
};
var __vue_staticRenderFns__$4$ = [];
__vue_render__$4$._withStripped = true;

  /* style */
  const __vue_inject_styles__$4$ = undefined;
  /* scoped */
  const __vue_scope_id__$4$ = undefined;
  /* module identifier */
  const __vue_module_identifier__$4$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$4$ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4$, staticRenderFns: __vue_staticRenderFns__$4$ },
    __vue_inject_styles__$4$,
    __vue_script__$4$,
    __vue_scope_id__$4$,
    __vue_is_functional_template__$4$,
    __vue_module_identifier__$4$,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4_ = {
  name: 'XmlHotzoneRender',
  components: {
    HotZone: __vue_component__$4$
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    max: {
      type: Number,
      default: Infinity
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {
        titleVisible: 'yes',
        titlePosition: 'under',
        titleAlign: 'left',
        zoneVisible: 'yes',
        imageList: [{
          source: 'upload',
          //来源：上传，素材
          uploadFileUrl: '',
          title: '图片名称',
          content: '',
          hotzoneSetList: [{
            widthPer: 0.25,
            heightPer: 0.25,
            topPer: 0.25,
            leftPer: 0.25,
            selected: 0,
            hotTarget: {
              type: 'link',
              value: '',
              label: ''
            }
          }]
        }]
      },
      zones: [{
        widthPer: 0.25,
        heightPer: 0.25,
        topPer: 0.25,
        leftPer: 0.25,
        selected: 0,
        hotTarget: {
          type: 'link',
          value: '',
          label: ''
        }
      }],
      //属性组件名
      propertyComponentsName: 'xml-hotzone-property',
      dialogShow: false,
      curRecordUrl: '',
      curRecordTitle: '',
      curCoverFileUrl: '',
      curRecordContent: '',
      currentPlayer: {},
      modalPlayerKey: 'mpk' + new Date().getTime(),
      hasInitSet: false
    };
  },
  computed: {
    isEmpty() {
      return !(this.blockData.recordSetList && this.blockData.recordSetList.length);
    },
    zoneImageModel() {
      return this.blockData.imageList ? this.blockData.imageList[0] : {};
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('新data', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    },
    blockData: {
      handler(newValue) {
        if (!this.extendParams.isInCreater || this.extendParams.isEditMode && !this.hasInitSet) {
          if (this.extendParams.isEditMode) {
            this.hasInitSet = true;
          }
          this.$nextTick(() => {
            let newZones = this.zoneImageModel.hotzoneSetList || [];
            this.zones = [...newZones];
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showDialog(param) {
      this.curRecordUrl = param.url;
      this.curRecordTitle = param.title;
      this.curCoverFileUrl = param.coverFileUrl;
      this.curRecordContent = param.content;
      this.currentPlayer = this.blockData.recordSetList[param.playerIndex];
      this.modalPlayerKey = 'mpk' + new Date().getTime();
      this.$nextTick(() => {
        this.dialogShow = true;
      });
    },
    onSelect(zone) {
      // console.log('当前选择热区', zone, this.zones);
    },
    updateZones(zones) {
      this.$EventBus.$emit('updateZoneInfo', {
        zones
      });
    },
    remove(curZone, index) {
      this.$emit('remove', curZone, index);
    },
    addZone(zone) {
      // console.log('添加了', zone);
    },
    wrapClick() {
      this.$emit('wrap-click', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    }
  },
  created() {},
  mounted() {
    if (!this.extendParams.isInCreater) {
      this.zones = [...this.zoneImageModel.hotzoneSetList];
    }
  }
  // destroyed() {
  //   this.$EventBus.$off('setInitZones')
  // }
};

/* script */
const __vue_script__$4_ = script$4_;

/* template */
var __vue_render__$4_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-hotzone-container xml-group-item",
      class: "xml-hotzone-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      [
        _c("div", { staticClass: "fixed-ratio-container" }, [
          _c(
            "div",
            {
              class: [
                "xml-hotzone__wrapper",
                "style-mode--" + _vm.blockData.styleMode,
                _vm.mode,
              ],
            },
            [
              _c(
                "div",
                { staticClass: "xml-hotzone-box" },
                [
                  _c("hot-zone", {
                    staticStyle: { width: "100%", height: "100%" },
                    attrs: {
                      image:
                        _vm.zoneImageModel.uploadFileUrl ||
                        _vm.extendParams.emptyImage ||
                        "",
                      mode: _vm.mode,
                      max: _vm.max,
                      resourceBasisPath: _vm.resourceBasisPath,
                      isInCreater: _vm.extendParams.isInCreater,
                      zoneVisible: _vm.blockData.zoneVisible === "yes",
                    },
                    on: {
                      "wrap-click": _vm.wrapClick,
                      "select-zone": _vm.onSelect,
                      add: _vm.addZone,
                      change: _vm.updateZones,
                      remove: _vm.remove,
                    },
                    model: {
                      value: _vm.zones,
                      callback: function ($$v) {
                        _vm.zones = $$v;
                      },
                      expression: "zones",
                    },
                  }),
                  _vm._v(" "),
                  _vm.zoneImageModel.title &&
                  _vm.blockData.titleVisible !== "no" &&
                  _vm.blockData.titlePosition === "bottom"
                    ? _c(
                        "div",
                        {
                          staticClass: "xml-hot-zone-title image-title--bottom",
                          style: {
                            textAlign: _vm.blockData.titleAlign || "left",
                          },
                        },
                        [
                          _vm._v(
                            "\n            " +
                              _vm._s(_vm.zoneImageModel.title) +
                              "\n          "
                          ),
                        ]
                      )
                    : _vm._e(),
                ],
                1
              ),
            ]
          ),
        ]),
        _vm._v(" "),
        _vm.zoneImageModel.title &&
        _vm.blockData.titleVisible !== "no" &&
        _vm.blockData.titlePosition === "under"
          ? _c(
              "div",
              {
                staticClass: "xml-hot-zone-title image-title--under",
                style: { textAlign: _vm.blockData.titleAlign || "left" },
              },
              [_vm._v("\n      " + _vm._s(_vm.zoneImageModel.title) + "\n    ")]
            )
          : _vm._e(),
      ],
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          staticClass: "resource-preview player-dialog",
          attrs: {
            title: "",
            visible: _vm.dialogShow,
            "destroy-on-close": true,
            width: "100%",
            modal: false,
            "show-close": false,
            "append-to-body": "",
            "custom-class": "preview-modal-hotzone",
            "close-on-click-modal": true,
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogShow = $event;
            },
          },
        },
        [_c("div", { staticClass: "xml-modal-player__wrapper" })]
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$4_ = [];
__vue_render__$4_._withStripped = true;

  /* style */
  const __vue_inject_styles__$4_ = undefined;
  /* scoped */
  const __vue_scope_id__$4_ = undefined;
  /* module identifier */
  const __vue_module_identifier__$4_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$4_ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4_, staticRenderFns: __vue_staticRenderFns__$4_ },
    __vue_inject_styles__$4_,
    __vue_script__$4_,
    __vue_scope_id__$4_,
    __vue_is_functional_template__$4_,
    __vue_module_identifier__$4_,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4Z = {
  name: 'XmlHotTargetModalViewer',
  components: {
    XmlAudioModalPlayer: __vue_component__$5u,
    XmlVideoModalPlayer: __vue_component__$5e,
    XmlTextDialog: __vue_component__$5A
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      playIcon: playIcon$3,
      titleLeftIcon,
      titleRightIcon,
      modalPlayerKey: 'mpk' + new Date().getTime()
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    isMaterialType() {
      const dict = ['image', 'audio', 'video'];
      return dict.includes(this.data.type);
    }
  },
  mounted() {},
  watch: {
    visible: {
      handler(n, o) {
        if (n) {
          this.modalPlayerKey = 'mpk' + new Date().getTime();
        }
      },
      immediate: true
    }
  },
  methods: {
    timeUpdate(e) {},
    resetTimeUpdate() {}
  }
};

/* script */
const __vue_script__$4Z = script$4Z;

/* template */
var __vue_render__$4Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      staticClass: "xml-custom-modal player-dialog",
      attrs: {
        title: "",
        visible: _vm.dialogVisible,
        "destroy-on-close": true,
        width: "100%",
        modal: false,
        "show-close": false,
        "append-to-body": "",
        "custom-class": "xml-modal-viewer xml-modal-viewer--hotzone",
        "close-on-click-modal": true,
      },
      on: {
        "update:visible": function ($event) {
          _vm.dialogVisible = $event;
        },
      },
    },
    [
      _c(
        "div",
        { staticClass: "xml-modal-viewer__wrapper" },
        [
          _vm.isMaterialType
            ? [
                _vm.data.type === "audio"
                  ? _c("xml-audio-modal-player", {
                      key: _vm.modalPlayerKey,
                      attrs: {
                        mode: "preview",
                        "play-style": "yuan-wen",
                        loop: false,
                        scheduleControl: "yes",
                        url: _vm.data.value,
                        title: _vm.data.label,
                        content: _vm.data.desc,
                        "cover-image-url": _vm.data.cover,
                        styleMode: "A",
                        "play-mode": "dan-ci",
                      },
                      on: {
                        timeUpdate: function (e) {
                          return _vm.timeUpdate(e)
                        },
                        resetTimeUpdate: function ($event) {
                          return _vm.resetTimeUpdate()
                        },
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.data.type === "video"
                  ? _c("xml-video-modal-player", {
                      key: _vm.modalPlayerKey,
                      attrs: {
                        mode: "preview",
                        playStyle: "yuan-wen",
                        loop: false,
                        canSkip: true,
                        src: _vm.data.value,
                        title: _vm.data.label,
                        content: _vm.data.desc,
                        poster: _vm.data.cover,
                      },
                      on: {
                        timeUpdate: function (e) {
                          return _vm.timeUpdate(e)
                        },
                        resetTimeUpdate: function ($event) {
                          return _vm.resetTimeUpdate()
                        },
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.data.type === "image"
                  ? _c("div", { staticClass: "xml-modal-viewer__image" }, [
                      _vm.data.label
                        ? _c("div", { staticClass: "title-block" }, [
                            _c("div", { staticClass: "title-adorn" }, [
                              _c("img", { attrs: { src: _vm.titleLeftIcon } }),
                            ]),
                            _vm._v(" "),
                            _c("div", { staticClass: "title" }, [
                              _vm._v(_vm._s(_vm.data.label)),
                            ]),
                            _vm._v(" "),
                            _c("div", { staticClass: "title-adorn" }, [
                              _c("img", { attrs: { src: _vm.titleRightIcon } }),
                            ]),
                          ])
                        : _vm._e(),
                      _vm._v(" "),
                      _c(
                        "div",
                        { staticClass: "xml-modal-viewer__scroll-wrapper" },
                        [
                          _c(
                            "div",
                            {
                              class: [
                                "xml-image-container",
                                "xml-image-container--aspect-fit",
                              ],
                            },
                            [
                              _c("el-image", {
                                attrs: { src: _vm.data.value },
                              }),
                            ],
                            1
                          ),
                          _vm._v(" "),
                          _vm.data.desc
                            ? _c("div", { staticClass: "content-block" }, [
                                _c("div", { staticClass: "label" }, [
                                  _c("span", [_vm._v("简介")]),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "content" }, [
                                  _vm._v(_vm._s(_vm.data.desc || "暂无")),
                                ]),
                              ])
                            : _vm._e(),
                        ]
                      ),
                    ])
                  : _vm._e(),
              ]
            : void 0,
        ],
        2
      ),
    ]
  )
};
var __vue_staticRenderFns__$4Z = [];
__vue_render__$4Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$4Z = undefined;
  /* scoped */
  const __vue_scope_id__$4Z = undefined;
  /* module identifier */
  const __vue_module_identifier__$4Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$4Z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4Z, staticRenderFns: __vue_staticRenderFns__$4Z },
    __vue_inject_styles__$4Z,
    __vue_script__$4Z,
    __vue_scope_id__$4Z,
    __vue_is_functional_template__$4Z,
    __vue_module_identifier__$4Z,
    false,
    undefined,
    undefined,
    undefined
  );

const components$2 = [__vue_component__$4_, __vue_component__$4Z];
var XmlHotzone = {
  ...components$2
};

const presetBgColors = ['#ede3fc', '#ffe4d2', '#ecebb4', '#fcdff6', '#d9f0b3', '#ccf1d9', '#bef1fe', '#fee6b4', '#cdedfc', '#dce8fa'];

//
var script$4Y = {
  name: 'XmlLayoutRender',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    bindingComponents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      blockData: {
        //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
        height: 'auto',
        rows: [1],
        columns: [1, 1],
        rowsUnit: 'fr',
        columnsUnit: 'fr',
        rowGap: 0,
        columnGap: 0,
        autoFlow: ['row', 'dense'],
        padding: [2, 2, 2, 2],
        areaBoxes: []
      },
      propertyComponentsName: 'xml-layout-property',
      //属性组件名
      presetBgColors,
      refreshKey: 'mpk' + new Date().getTime()
    };
  },
  computed: {
    containerStyle() {
      let {
        height,
        padding,
        rows,
        columns,
        rowsUnit,
        columnsUnit,
        rowGap,
        columnGap,
        autoFlow
      } = this.blockData;
      let stringPadding = padding.map(item => item + 'px');
      let gridTemplateRows = rows.map(item => item + rowsUnit).join(' ');
      let gridTemplateColumns = columns.map(item => item + columnsUnit).join(' ');
      return {
        display: 'grid',
        height: height === 'auto' ? 'auto' : `${height}px`,
        padding: stringPadding.join(' '),
        gridAutoFlow: autoFlow.join(' '),
        gridTemplateRows,
        gridTemplateColumns,
        gap: `${rowGap}px ${columnGap}px`
      };
    },
    injectedAreaBoxes() {
      return this.blockData.areaBoxes.filter(item => !!item.injectComponent?.data);
    },
    spareAreaBoxes() {
      return this.blockData.areaBoxes.filter(item => !item.injectComponent?.data);
    },
    isEmpty() {
      return !(this.blockData.recordSetList && this.blockData.recordSetList.length);
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          console.log('新data', newValue);
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  provide() {
    return {
      getCatalogList: () => ({
        data: this.catalogList
      }),
      getCurrentRuleIndex: () => this.currentRuLeIndex,
      getXmlLayoutInstance: () => this
    };
  },
  methods: {
    // getAreaBoxMainIndex(uuid) {
    //   return this.blockData.areaBoxes.findIndex(item => item.uuid === uuid)
    // },
    // genAreaBoxStyle(areaBox,index) {
    //   let { rowStart, rowEnd, columnStart, columnEnd } = areaBox
    //   let target = index % this.presetBgColors.length

    //   return {
    //     '--areabox-color': this.presetBgColors[target],
    //     gridRow: `${rowStart} / ${rowEnd}`,
    //     gridColumn: `${columnStart} / ${columnEnd}`,
    //     backgroundColor: `${this.presetBgColors[target]}66`, // 66透明度
    //   }
    // },
    // spareAreaBoxClick(event, item) {
    //   let rect = event.target.getBoundingClientRect()
    //   this.$EventBus.$emit('areaBoxClick', { ...item, type: 'spare', rect }, { blockId: this.blockId, ruleIndex: this.ruleIndex})
    // },
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    }
  },
  created() {}
};

/* script */
const __vue_script__$4Y = script$4Y;

/* template */
var __vue_render__$4Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-layout-container",
      class: "xml-layout-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: {
        "!click": function ($event) {
          return _vm.blockClick.apply(null, arguments)
        },
      },
    },
    [
      _c("div", { class: ["xml-layout__wrapper", _vm.mode] }, [
        _c(
          "div",
          {
            staticClass: "xml-grid-layout xml-grid-layout__container",
            style: _vm.containerStyle,
          },
          [
            _vm._t("default"),
            _vm._v(" "),
            _vm._l(_vm.blockData.areaBoxes, function (item, index) {
              return _c("XmlAreaBox", {
                key: item.uuid,
                attrs: {
                  injected: _vm.bindingComponents.indexOf(item.uuid) !== -1,
                  data: item,
                  ruleIndex: _vm.ruleIndex,
                  boxIndex: index,
                  mode: _vm.mode,
                  sign: _vm.currentRule.sign,
                },
              })
            }),
          ],
          2
        ),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$4Y = [];
__vue_render__$4Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$4Y = undefined;
  /* scoped */
  const __vue_scope_id__$4Y = undefined;
  /* module identifier */
  const __vue_module_identifier__$4Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$4Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4Y, staticRenderFns: __vue_staticRenderFns__$4Y },
    __vue_inject_styles__$4Y,
    __vue_script__$4Y,
    __vue_scope_id__$4Y,
    __vue_is_functional_template__$4Y,
    __vue_module_identifier__$4Y,
    false,
    undefined,
    undefined,
    undefined
  );

/*! 
 * portal-vue © Thorsten Lünborg, 2019 
 * 
 * Version: 2.1.7
 * 
 * LICENCE: MIT 
 * 
 * https://github.com/linusborg/portal-vue
 * 
*/
function _typeof$1(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof$1 = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof$1 = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }
  return _typeof$1(obj);
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];
    return arr2;
  }
}
function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}
var inBrowser = typeof window !== 'undefined';
function freeze(item) {
  if (Array.isArray(item) || _typeof$1(item) === 'object') {
    return Object.freeze(item);
  }
  return item;
}
function combinePassengers(transports) {
  var slotProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return transports.reduce(function (passengers, transport) {
    var temp = transport.passengers[0];
    var newPassengers = typeof temp === 'function' ? temp(slotProps) : transport.passengers;
    return passengers.concat(newPassengers);
  }, []);
}
function stableSort(array, compareFn) {
  return array.map(function (v, idx) {
    return [idx, v];
  }).sort(function (a, b) {
    return compareFn(a[1], b[1]) || a[0] - b[0];
  }).map(function (c) {
    return c[1];
  });
}
function pick(obj, keys) {
  return keys.reduce(function (acc, key) {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}
var transports = {};
var targets = {};
var sources = {};
var Wormhole = Vue.extend({
  data: function data() {
    return {
      transports: transports,
      targets: targets,
      sources: sources,
      trackInstances: inBrowser
    };
  },
  methods: {
    open: function open(transport) {
      if (!inBrowser) return;
      var to = transport.to,
        from = transport.from,
        passengers = transport.passengers,
        _transport$order = transport.order,
        order = _transport$order === void 0 ? Infinity : _transport$order;
      if (!to || !from || !passengers) return;
      var newTransport = {
        to: to,
        from: from,
        passengers: freeze(passengers),
        order: order
      };
      var keys = Object.keys(this.transports);
      if (keys.indexOf(to) === -1) {
        Vue.set(this.transports, to, []);
      }
      var currentIndex = this.$_getTransportIndex(newTransport); // Copying the array here so that the PortalTarget change event will actually contain two distinct arrays

      var newTransports = this.transports[to].slice(0);
      if (currentIndex === -1) {
        newTransports.push(newTransport);
      } else {
        newTransports[currentIndex] = newTransport;
      }
      this.transports[to] = stableSort(newTransports, function (a, b) {
        return a.order - b.order;
      });
    },
    close: function close(transport) {
      var force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var to = transport.to,
        from = transport.from;
      if (!to || !from && force === false) return;
      if (!this.transports[to]) {
        return;
      }
      if (force) {
        this.transports[to] = [];
      } else {
        var index = this.$_getTransportIndex(transport);
        if (index >= 0) {
          // Copying the array here so that the PortalTarget change event will actually contain two distinct arrays
          var newTransports = this.transports[to].slice(0);
          newTransports.splice(index, 1);
          this.transports[to] = newTransports;
        }
      }
    },
    registerTarget: function registerTarget(target, vm, force) {
      if (!inBrowser) return;
      if (this.trackInstances && !force && this.targets[target]) {
        console.warn("[portal-vue]: Target ".concat(target, " already exists"));
      }
      this.$set(this.targets, target, Object.freeze([vm]));
    },
    unregisterTarget: function unregisterTarget(target) {
      this.$delete(this.targets, target);
    },
    registerSource: function registerSource(source, vm, force) {
      if (!inBrowser) return;
      if (this.trackInstances && !force && this.sources[source]) {
        console.warn("[portal-vue]: source ".concat(source, " already exists"));
      }
      this.$set(this.sources, source, Object.freeze([vm]));
    },
    unregisterSource: function unregisterSource(source) {
      this.$delete(this.sources, source);
    },
    hasTarget: function hasTarget(to) {
      return !!(this.targets[to] && this.targets[to][0]);
    },
    hasSource: function hasSource(to) {
      return !!(this.sources[to] && this.sources[to][0]);
    },
    hasContentFor: function hasContentFor(to) {
      return !!this.transports[to] && !!this.transports[to].length;
    },
    // Internal
    $_getTransportIndex: function $_getTransportIndex(_ref) {
      var to = _ref.to,
        from = _ref.from;
      for (var i in this.transports[to]) {
        if (this.transports[to][i].from === from) {
          return +i;
        }
      }
      return -1;
    }
  }
});
var wormhole = new Wormhole(transports);
var _id$3 = 1;
var Portal = Vue.extend({
  name: 'portal',
  props: {
    disabled: {
      type: Boolean
    },
    name: {
      type: String,
      default: function _default() {
        return String(_id$3++);
      }
    },
    order: {
      type: Number,
      default: 0
    },
    slim: {
      type: Boolean
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'DIV'
    },
    to: {
      type: String,
      default: function _default() {
        return String(Math.round(Math.random() * 10000000));
      }
    }
  },
  created: function created() {
    var _this = this;
    this.$nextTick(function () {
      wormhole.registerSource(_this.name, _this);
    });
  },
  mounted: function mounted() {
    if (!this.disabled) {
      this.sendUpdate();
    }
  },
  updated: function updated() {
    if (this.disabled) {
      this.clear();
    } else {
      this.sendUpdate();
    }
  },
  beforeDestroy: function beforeDestroy() {
    wormhole.unregisterSource(this.name);
    this.clear();
  },
  watch: {
    to: function to(newValue, oldValue) {
      oldValue && oldValue !== newValue && this.clear(oldValue);
      this.sendUpdate();
    }
  },
  methods: {
    clear: function clear(target) {
      var closer = {
        from: this.name,
        to: target || this.to
      };
      wormhole.close(closer);
    },
    normalizeSlots: function normalizeSlots() {
      return this.$scopedSlots.default ? [this.$scopedSlots.default] : this.$slots.default;
    },
    normalizeOwnChildren: function normalizeOwnChildren(children) {
      return typeof children === 'function' ? children(this.slotProps) : children;
    },
    sendUpdate: function sendUpdate() {
      var slotContent = this.normalizeSlots();
      if (slotContent) {
        var transport = {
          from: this.name,
          to: this.to,
          passengers: _toConsumableArray(slotContent),
          order: this.order
        };
        wormhole.open(transport);
      } else {
        this.clear();
      }
    }
  },
  render: function render(h) {
    var children = this.$slots.default || this.$scopedSlots.default || [];
    var Tag = this.tag;
    if (children && this.disabled) {
      return children.length <= 1 && this.slim ? this.normalizeOwnChildren(children)[0] : h(Tag, [this.normalizeOwnChildren(children)]);
    } else {
      return this.slim ? h() : h(Tag, {
        class: {
          'v-portal': true
        },
        style: {
          display: 'none'
        },
        key: 'v-portal-placeholder'
      });
    }
  }
});
var PortalTarget = Vue.extend({
  name: 'portalTarget',
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    slim: {
      type: Boolean,
      default: false
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'div'
    },
    transition: {
      type: [String, Object, Function]
    }
  },
  data: function data() {
    return {
      transports: wormhole.transports,
      firstRender: true
    };
  },
  created: function created() {
    var _this = this;
    this.$nextTick(function () {
      wormhole.registerTarget(_this.name, _this);
    });
  },
  watch: {
    ownTransports: function ownTransports() {
      this.$emit('change', this.children().length > 0);
    },
    name: function name(newVal, oldVal) {
      /**
       * TODO
       * This should warn as well ...
       */
      wormhole.unregisterTarget(oldVal);
      wormhole.registerTarget(newVal, this);
    }
  },
  mounted: function mounted() {
    var _this2 = this;
    if (this.transition) {
      this.$nextTick(function () {
        // only when we have a transition, because it causes a re-render
        _this2.firstRender = false;
      });
    }
  },
  beforeDestroy: function beforeDestroy() {
    wormhole.unregisterTarget(this.name);
  },
  computed: {
    ownTransports: function ownTransports() {
      var transports = this.transports[this.name] || [];
      if (this.multiple) {
        return transports;
      }
      return transports.length === 0 ? [] : [transports[transports.length - 1]];
    },
    passengers: function passengers() {
      return combinePassengers(this.ownTransports, this.slotProps);
    }
  },
  methods: {
    // can't be a computed prop because it has to "react" to $slot changes.
    children: function children() {
      return this.passengers.length !== 0 ? this.passengers : this.$scopedSlots.default ? this.$scopedSlots.default(this.slotProps) : this.$slots.default || [];
    },
    // can't be a computed prop because it has to "react" to this.children().
    noWrapper: function noWrapper() {
      var noWrapper = this.slim && !this.transition;
      if (noWrapper && this.children().length > 1) {
        console.warn('[portal-vue]: PortalTarget with `slim` option received more than one child element.');
      }
      return noWrapper;
    }
  },
  render: function render(h) {
    var noWrapper = this.noWrapper();
    var children = this.children();
    var Tag = this.transition || this.tag;
    return noWrapper ? children[0] : this.slim && !Tag ? h() : h(Tag, {
      props: {
        // if we have a transition component, pass the tag if it exists
        tag: this.transition && this.tag ? this.tag : undefined
      },
      class: {
        'vue-portal-target': true
      }
    }, children);
  }
});
var _id$1$1 = 0;
var portalProps = ['disabled', 'name', 'order', 'slim', 'slotProps', 'tag', 'to'];
var targetProps = ['multiple', 'transition'];
Vue.extend({
  name: 'MountingPortal',
  inheritAttrs: false,
  props: {
    append: {
      type: [Boolean, String]
    },
    bail: {
      type: Boolean
    },
    mountTo: {
      type: String,
      required: true
    },
    // Portal
    disabled: {
      type: Boolean
    },
    // name for the portal
    name: {
      type: String,
      default: function _default() {
        return 'mounted_' + String(_id$1$1++);
      }
    },
    order: {
      type: Number,
      default: 0
    },
    slim: {
      type: Boolean
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'DIV'
    },
    // name for the target
    to: {
      type: String,
      default: function _default() {
        return String(Math.round(Math.random() * 10000000));
      }
    },
    // Target
    multiple: {
      type: Boolean,
      default: false
    },
    targetSlim: {
      type: Boolean
    },
    targetSlotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    targetTag: {
      type: String,
      default: 'div'
    },
    transition: {
      type: [String, Object, Function]
    }
  },
  created: function created() {
    if (typeof document === 'undefined') return;
    var el = document.querySelector(this.mountTo);
    if (!el) {
      console.error("[portal-vue]: Mount Point '".concat(this.mountTo, "' not found in document"));
      return;
    }
    var props = this.$props; // Target already exists

    if (wormhole.targets[props.name]) {
      if (props.bail) {
        console.warn("[portal-vue]: Target ".concat(props.name, " is already mounted.\n        Aborting because 'bail: true' is set"));
      } else {
        this.portalTarget = wormhole.targets[props.name];
      }
      return;
    }
    var append = props.append;
    if (append) {
      var type = typeof append === 'string' ? append : 'DIV';
      var mountEl = document.createElement(type);
      el.appendChild(mountEl);
      el = mountEl;
    } // get props for target from $props
    // we have to rename a few of them

    var _props = pick(this.$props, targetProps);
    _props.slim = this.targetSlim;
    _props.tag = this.targetTag;
    _props.slotProps = this.targetSlotProps;
    _props.name = this.to;
    this.portalTarget = new PortalTarget({
      el: el,
      parent: this.$parent || this,
      propsData: _props
    });
  },
  beforeDestroy: function beforeDestroy() {
    var target = this.portalTarget;
    if (this.append) {
      var el = target.$el;
      el.parentNode.removeChild(el);
    }
    target.$destroy();
  },
  render: function render(h) {
    if (!this.portalTarget) {
      console.warn("[portal-vue] Target wasn't mounted");
      return h();
    } // if there's no "manual" scoped slot, so we create a <Portal> ourselves

    if (!this.$scopedSlots.manual) {
      var props = pick(this.$props, portalProps);
      return h(Portal, {
        props: props,
        attrs: this.$attrs,
        on: this.$listeners,
        scopedSlots: this.$scopedSlots
      }, this.$slots.default);
    } // else, we render the scoped slot

    var content = this.$scopedSlots.manual({
      to: this.to
    }); // if user used <template> for the scoped slot
    // content will be an array

    if (Array.isArray(content)) {
      content = content[0];
    }
    if (!content) return h();
    return content;
  }
});

var iconReplace = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAA2hJREFUWEe9l19oU2cYxn9vojNxCKMwRXexghPRZMw/DVpvZnGOaXGjbmNXgyJeiG2KvRJB0F7ohbKbtI6JF4JXA3WIjioq2ru4NWGMxgrqRfFCBUXc1DYpzXnlnJNzenIakxyb9tzl+97n+Z68/773E81yFqWTYN8YQlI28qcDW73n2ZKFHy65bP4W5W8DbhVkUfphv/xfjVo0gwY7u2St9EmCow52bXKiM4Sc9XFNCNwoGsbx0VOL/6p0jl/AUA0xW919n4BYrzbJVOEiMG3jJVPtyw1EXcHOllfAkLTQVk2ADrMV4bZl4xPg4D7vGm9FQptVaAV+LOMTLuZSkR+8aw0X4CWPd+W3E+YEyjpn3TCMzd5wzKkA59B4T+ECqt/bntN0biC6Zc5CUCmEaw7o8nCx8Hg6f6bzYV48YB4c7544isiRUv4M5gYi7XbJTpdhQ5LwXUlcXqb6fPKDyJr7v8jzeRMQ681/JlM8cGMvtI+kIoNzLuCLfeOfFBeE94IhbgjsZOzzh8Cq7aqNSDBrOFatD1TCx7vGOwiF/pixJ/Q0rBXX6KDEuwvHET3k2Clcvdsf2WkKuANsqkVQYb9LWvg1CC6WzN8U2GZiDNFPR1PRR6JpmllAcxAiirzA4IFsYSIILtb7X5NMLRoDDuf6IykrB4IQNMI21pP/5m4qcs2tBi+pZvhaWrjeiIPq5XA9oBnr5jpDmGZZz8t6CUy71l6Nvsq/WUU43BQElxuIDlkCdIRlFHhaKq8rkuDbIESx7vx+EU4FwZRss7aADGeAvS6BcFg2cqxewrI+Xy+oZCc6zM8I52bglHZJMFgPn09AFlV3VqyIdy4l6zIaLs11gn1TWS4pdUThtLTwpJaI8ptO28zYVsPEk3lzqrJGNzsEw3yHcMkDWlHPwY797AX8y1ImuYdgZ7Gwyztyz7kHLC9kuYHylRMC78g9PwLMXPDmQYiYbGC01uHm/qxD4OZehofAypIXMpIgMb8CsvyE8runH1xhkoPSyr2qWV02771HFXjJNWO996yB0fOdR0gzxR3ZRNovpmEhcEORpQNl5gRjG2QJsUM28KxiGdqLdT/x3nkda8bKhZPADiDi+9dlw8isWnGtJNN/+Iii9Sb80n1ivWKXtPHa9UCysBuM30A+rsXn2x96C4xUoavxY8kaAAAAAElFTkSuQmCC";

var iconSetup = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAA7NJREFUWEftl2tsVEUUx/9nku7ei2AkPEQCGlSg6b3bUiuPoCGCJVGTkihUwgdIUEBt9y4hqIkfUB6+osbEvatGTfmICV1C4AspjwR5JFBeoZ0SEjQhtDXAyhdM9t4t6T1m2t2lu92227ut8YPz6U7m3Pn/zpk5M3MI/0Kbt+X+1DItWAvunUGe6JDfa8cyspSvH4o4Gz0WywSwkMHGSHzM+Lkjpr0zlF2llVrjgT8FMP+hDe2VdvBt1c8BMC13P4D6kURzxpl3yZi+s9A/ZmPydQgRByBem3sMj0+8C5mocC50VetM/GVHVP8oC2CGnZ0g+gTAHYAaUBa4IL+lzlHBDDA2LLeOCHEwAu8tbELDor19o8kHOjYf+g5tdwyAeEMfgBlJLgWLswDapK1V+RXN/BcKu68yURzgCQPFM+NHf1+B7S17VLe5D8AIuz8RYQuReL49GrhUCoC51V0JDwcATCokrua+lijH2v1NIPCv/RGw3IsApklbe6oUcSPsrKA+zzF5KHE1v/JeRQHMuzIADOCktLXlfgFCjc4yFogDNG048R9a38Kl7gUHznfX3FPZMyYAZuTBUnCv8vyJ4cS/OLUN+9rXKM+Xy5h+MpuGpuX6jkBFY3KxEKIZwOzhxD87tR3ybvkJebvimoxpkZyDyC9AKNJTw+wp8TlFrvkmGdObBi6z7yUwrJ4qYi8OwrNFiQPN0tbezN9jvgAqIynTY1ael5ci7nsPmFbqMMB1pYr7Api1rVOf4k1JzHns5iMH160vmLXZPB8i7CXtgYpG52Uh6Hi9cQgfv/TVIIBixNP3zgRpax+Oeg8YYWcHEe3+vHY36ua35AAUI54+eVXaQ9oajR7Aco8Q8ErLhtWYOek27qceRWt3NY7cqO0/XosIezrt/QGEIsm/zenXJ9Y+/Rtau57DxT8XcKo3mLnWC6Za/jqVBmA5+xi0Ljsp4zIIZ0B0VkaD6kEzYisNIJyqZ3gGSLSVIXD6ik2JERXzDEoCGK1YIfv/Af6LEXA6QfSXjGrVY7HGw82hipRAMJAAuEva+uzMQaTSZ6W0tcnjDWCEky8QiTOZA6sf4GFN8KO0tYbxhDAt9zSAF9WDVBU02cIkZLndDMwE0RsyGjw4HhBm2P0ahPeJ6Hx7NLgkex2rj/Tb7lxa+DhAt8Ce78pooAMsMJeYVG1YA+ZOGdOfzHkTZjqV7/J0Luv5hcGrxiMCYHwjY9oHg94Dgy6LralKsFcFD8+MCYgQV8Gsyr4/8uf7B8Tx9p9D/L0VAAAAAElFTkSuQmCC";

var iconRemove = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAgCAYAAAB6kdqOAAAAAXNSR0IArs4c6QAAAtdJREFUWEftmM1LFHEYxz/Pau1YSpfq0AtIdIhaJdC9GFhePHgUBEtouwSGStHLWf0DpHLWgqiQjKhDRYfyEqmHCHPUYmeVwugShHkpFXxJfWq3dl213FlnJA8NDMww3+f5feY7v3me+Y3gYAvUTzUh0uhA6kwivBHlVsQ0wssDxEmGQMO0OtFlqlGR49FW//3UOGdAXjuUJJBXtukvyRgo0ztPp0913DaNJaY4cijdAJleTwt04NxUfta8r1HQ/EyTp9Wr9tjhnKZU3apABQ0zNYreTZvYpUCRQ1HTPxRLsypQoGH6IVDpcrz04arNCafSAa3LK/0Hwm7bNMqcOPQfaAM8Mh0FGf6nj0zRdhFeCFlDs5Pf3r+7vWMiMa8KG3SP6kyVilStqNTr0adUtSYazrmnXeSylUJ8FKKUIPiAXuaJMoctR/iyorl6CiQMb9LvRwfNvDG1OAtcAPauUhNapJiLS3qZh0DjtmlsiyXXfnpQStMXp7jiI0K5FDESOxGvgESpiISNTrX4AOxzCLMomyBPypj0BEiFy9FW47xa1ALXM4aJ28oDCVLtAZCO2WbOTn1NCT5erglmMeiMB0DEW4Ja3ABOuwSy3QMJ1+xWo077GEQ47BLI/aRWpc6uNjowGE+B6UZpdgTnI4RyKqF175BqWSSUA0JXEkApkyDdjoBi89ki2eBdAylywg75nwFfN4ZDcNE2jRbt5y1KoVNX/qZz7RDKYztsVKpF+8+qG3IJNOIe6BfB/shJ4xjCTVdASps3QL+/l9XiKVCxRqjPUswuCdRPfUJk9xqTJMJmFxYWSofatvSmvjEZ5VQqJEhnDMiTHwkKT4wZu8qqLT7IAs+B7Y6BlJAEuRPv9imLxGRxcpxopXAA31yFfTV3VPtoQlj9j4nQwWYuSQGjycKYOIiBZc/hzco12zdqX/EPq0U5ShAhCPE9tnQfRBnEx4AU8Wj5Pf0AgUJM93Ow4uMAAAAASUVORK5CYII=";

//
var script$4X = {
  name: 'XmlAreaBox',
  components: {
    PortalTarget
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    boxIndex: Number,
    // mainIndex: Number,
    injected: {
      type: Boolean,
      default: false
    },
    sign: {
      type: String,
      default: ''
    },
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    //属性中传递过来的块儿数据
    pBlockData: {
      type: Object,
      default: () => {}
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: () => {}
    },
    ruleIndex: Number
  },
  data() {
    return {
      presetBgColors,
      images: {
        iconReplace,
        iconSetup,
        iconRemove
      },
      clickInside: false,
      refreshKey: 'apk' + new Date().getTime()
    };
  },
  directives: {
    clickInside: {
      bind(el, binding, vnode) {
        function clickHandler(e) {
          let isInside = false;
          if (el.contains(e.target)) {
            isInside = true;
          }
          if (binding.expression) {
            binding.value(isInside);
          }
        }
        el.__vueClickOutside__ = clickHandler;
        document.addEventListener('click', clickHandler);
      },
      update() {},
      unbind(el, binding) {
        document.removeEventListener('click', el.__vueClickOutside__);
        delete el.__vueClickOutside__;
      }
    }
  },
  computed: {
    activeInjectId() {
      return this.getActiveInjectId();
    },
    activeInjectedBoxId() {
      return this.getActiveInjectedBoxId();
    },
    isInActive() {
      return this.activeInjectId && this.activeInjectId === this.data.uuid || this.activeInjectedBoxId && this.activeInjectedBoxId === this.data.uuid;
    }
  },
  provide() {
    return {
      getAreaBoxProvider: () => ({
        injected: this.injected,
        data: this.data,
        boxIndex: this.boxIndex,
        mainIndex: this.mainIndex
      })
    };
  },
  inject: {
    getActiveInjectId: {
      from: 'getActiveInjectId',
      default: () => ''
    },
    getActiveInjectedBoxId: {
      from: 'getActiveInjectedBoxId',
      default: () => ''
    }
  },
  methods: {
    setClickInside(isInside) {
      this.clickInside = isInside;
    },
    setupInjectComponent() {
      this.$emit('injectSetup', {
        ...this.data,
        type: 'injected',
        ruleIndex: this.ruleIndex,
        mainIndex: this.mainIndex
      });
    },
    replaceInjectComponent() {
      let rect = this.$el.getBoundingClientRect();
      this.$EventBus.$emit('injectComponentReplace', {
        ...this.data,
        type: 'injected',
        rect,
        ruleIndex: this.ruleIndex,
        boxIndex: this.boxIndex,
        sign: this.sign
      });
    },
    removeInjectComponent() {
      this.$EventBus.$emit('injectComponentRemove', {
        ...this.data,
        type: 'injected',
        ruleIndex: this.ruleIndex,
        boxIndex: this.boxIndex
      });
    },
    genAreaBoxStyle() {
      let {
        rowStart,
        rowEnd,
        columnStart,
        columnEnd
      } = this.data;
      let baseStyle = {
        gridRow: `${rowStart} / ${rowEnd}`,
        gridColumn: `${columnStart} / ${columnEnd}`
      };
      if (!this.injected) {
        let target = this.boxIndex % this.presetBgColors.length;
        baseStyle['--areabox-color'] = this.presetBgColors[target];
        baseStyle.backgroundColor = `${this.presetBgColors[target]}99`; // 66透明度
      }
      return baseStyle;
    },
    spareAreaBoxClick(event) {
      if (this.injected) {
        // this.$EventBus.$emit('injectedGroupProxy', { ...this.data, type: 'injected', ruleIndex: this.ruleIndex, mainIndex: this.mainIndex })
        // this.$emit('areaBoxClick', { ...this.data, type: 'injected' })
        this.$EventBus.$emit('areaBoxClick', {
          ...this.data,
          type: 'injected'
        }, {
          ruleIndex: this.ruleIndex,
          sign: this.sign
        });
      } else {
        let rect = event.target.getBoundingClientRect();
        this.$EventBus.$emit('areaBoxClick', {
          ...this.data,
          type: 'spare',
          rect
        }, {
          ruleIndex: this.ruleIndex,
          sign: this.sign
        });
      }
    }
  },
  created() {}
};

/* script */
const __vue_script__$4X = script$4X;

/* template */
var __vue_render__$4X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    {
      directives: [
        {
          name: "click-inside",
          rawName: "v-click-inside",
          value: _vm.setClickInside,
          expression: "setClickInside",
        },
      ],
      class: [
        "xml-grid-layout__area-box",
        "xml-grid-layout__area-box" + (_vm.injected ? "--injected" : "--spare"),
        _vm.mode === "editor" && "is-in-editor",
        _vm.isInActive && "inject-active",
      ],
      style: _vm.genAreaBoxStyle(),
      on: { click: _vm.spareAreaBoxClick },
    },
    [
      _vm.injected
        ? [
            _c("div", { staticClass: "xml-grid-layout__control" }, [
              _c("div", { staticClass: "xml-grid-layout__control-wrap" }, [
                _c(
                  "div",
                  {
                    staticClass: "xml-grid-layout__button",
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.replaceInjectComponent.apply(null, arguments)
                      },
                    },
                  },
                  [
                    _c(
                      "el-tooltip",
                      {
                        staticClass: "item",
                        attrs: {
                          effect: "dark",
                          content: "替换组件",
                          placement: "top",
                        },
                      },
                      [
                        _c("el-image", {
                          attrs: { src: _vm.images.iconReplace },
                        }),
                      ],
                      1
                    ),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "xml-grid-layout__button",
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.removeInjectComponent.apply(null, arguments)
                      },
                    },
                  },
                  [
                    _c(
                      "el-tooltip",
                      {
                        staticClass: "item",
                        attrs: {
                          effect: "dark",
                          content: "移除组件",
                          placement: "top",
                        },
                      },
                      [
                        _c("el-image", {
                          attrs: { src: _vm.images.iconRemove },
                        }),
                      ],
                      1
                    ),
                  ],
                  1
                ),
              ]),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "xml-grid-layout__inject-wrap" },
              [_c("PortalTarget", { attrs: { name: _vm.data.uuid } })],
              1
            ),
          ]
        : _vm._e(),
    ],
    2
  )
};
var __vue_staticRenderFns__$4X = [];
__vue_render__$4X._withStripped = true;

  /* style */
  const __vue_inject_styles__$4X = undefined;
  /* scoped */
  const __vue_scope_id__$4X = undefined;
  /* module identifier */
  const __vue_module_identifier__$4X = undefined;
  /* functional template */
  const __vue_is_functional_template__$4X = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4X, staticRenderFns: __vue_staticRenderFns__$4X },
    __vue_inject_styles__$4X,
    __vue_script__$4X,
    __vue_scope_id__$4X,
    __vue_is_functional_template__$4X,
    __vue_module_identifier__$4X,
    false,
    undefined,
    undefined,
    undefined
  );

const components$1 = [__vue_component__$4Y, __vue_component__$4X];
var XmlLayout = {
  ...components$1
};

var singleGroupMixin = {
  props: {
    activeIndex: {
      type: Number,
      default: -1
    },
    location: {
      type: String,
      default: ''
    },
    islast: {
      type: Boolean,
      default: false
    }
  },
  provide() {
    return {
      getSingleGroupLocation: () => this.location
    };
  },
  computed: {
    groupIsActive() {
      return this.ruleIndex === this.activeIndex;
    }
  },
  watch: {
    pGroupData: {
      handler() {
        if (this.pGroupData.options?.marginTop !== undefined && this.pGroupData.options.marginTop !== -1) {
          this.$nextTick(() => {
            this.$el && this.$el.style.setProperty('--book-marginTop', this.pGroupData.options.marginTop + 'px');
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    if (this.pGroupData.options?.marginTop === undefined) {
      let options = this.pGroupData.options ? {
        ...this.pGroupData.options,
        marginTop: -1
      } : {
        marginTop: -1
      };
      this.$emit('updateModel', {
        key: 'pGroupData',
        value: {
          ...this.pGroupData,
          options
        }
      });
    }
  },
  mounted() {
    if (this.islast) {
      this.$nextTick(() => {
        this.$EventBus.$emit('mounted_success');
      });
    }
  },
  methods: {
    emitClick(e) {
      this.$emit('click', e, this.ruleIndex, this.currentRule);
    }
  }
};

//
var script$4W = {
  mixins: [singleGroupMixin],
  name: 'titleFifthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4W = script$4W;
/* template */
var __vue_render__$4W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fifth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-bottom" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4W = [];
__vue_render__$4W._withStripped = true;

  /* style */
  const __vue_inject_styles__$4W = undefined;
  /* scoped */
  const __vue_scope_id__$4W = "data-v-b4957530";
  /* module identifier */
  const __vue_module_identifier__$4W = undefined;
  /* functional template */
  const __vue_is_functional_template__$4W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4W, staticRenderFns: __vue_staticRenderFns__$4W },
    __vue_inject_styles__$4W,
    __vue_script__$4W,
    __vue_scope_id__$4W,
    __vue_is_functional_template__$4W,
    __vue_module_identifier__$4W,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4V = {
  mixins: [singleGroupMixin],
  name: 'titleFirstRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4V = script$4V;
/* template */
var __vue_render__$4V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-first-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4V = [];
__vue_render__$4V._withStripped = true;

  /* style */
  const __vue_inject_styles__$4V = undefined;
  /* scoped */
  const __vue_scope_id__$4V = "data-v-6e4bd170";
  /* module identifier */
  const __vue_module_identifier__$4V = undefined;
  /* functional template */
  const __vue_is_functional_template__$4V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4V, staticRenderFns: __vue_staticRenderFns__$4V },
    __vue_inject_styles__$4V,
    __vue_script__$4V,
    __vue_scope_id__$4V,
    __vue_is_functional_template__$4V,
    __vue_module_identifier__$4V,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4U = {
  mixins: [singleGroupMixin],
  name: 'titleHexagonRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4U = script$4U;
/* template */
var __vue_render__$4U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-hexagon-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4U = [];
__vue_render__$4U._withStripped = true;

  /* style */
  const __vue_inject_styles__$4U = undefined;
  /* scoped */
  const __vue_scope_id__$4U = "data-v-a93da05e";
  /* module identifier */
  const __vue_module_identifier__$4U = undefined;
  /* functional template */
  const __vue_is_functional_template__$4U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4U, staticRenderFns: __vue_staticRenderFns__$4U },
    __vue_inject_styles__$4U,
    __vue_script__$4U,
    __vue_scope_id__$4U,
    __vue_is_functional_template__$4U,
    __vue_module_identifier__$4U,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4T = {
  mixins: [singleGroupMixin],
  name: 'titleRectangleRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_assets_images_title_rectangle_left_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACcAAAAlCAYAAADBa/A+AAACaklEQVRYhcWXu24TQRSGvzVxuAjEJQVSiFAuDRUIJIoYIZQGiSIhT0BHi8SL8AoUPAGXhgIaRAqEAwkgEJGJYickGBwwSSD2rnfRbDzG8p69RPLs/oWnODPWp3POnn/GmrpXI07P757yHBduP6hz8WyeO1NHAidWf7W4db8e+1/7US7p3ndrNiubLTbqLTFeXHH6CsZ+4F6WbH8tTAyK8WLZ7iPWnhLDzZWaWBZMjuUDMdeDN5WM4ErfW2z8djl3eoCho8EjH9cddhpeNnBzX5r+WpgIZk3ptYGSkhQurt/mM4QbWfrmMHwix9jQgUDwT9Pzy5oV3LTqpishWXtbsVEz0ISSwM2qn8nxkH4zMN+04uCOA9eOHbI4f0aGMzHftOLgrgMHC+OD5KxgsLrlUvkpO0YacDElNZc1YuAU0Q2gcXlUhjM1QrSi4K4CJ4Fnh/PBmnqe2X4jBu5me30oBZeqDvW//besbkXBzagEAU+kYLFsboRohcFdAEaBV8BXGc5sSYmAm2mvj6Rgw/H8y6dphcHpfhPhFlYdbHPjrSMJbgS4pK5xwHvpkOkREgU3DVhhXykpDF8tCW62vT6WDmzuuCz/SKGmApxv9IoBeCEdUCPE7HT7r1443+jbs01MT1olRYDTJRW/UqV5A6+sMHXDdYweeCrtX661qG0buvYK6obrGD2wLW0uplhSeuAijZ6U+40euEijV46wuGbe7Lul4WKN/sO6za6d1hDZk4aLNHoMv7LCpOEijZ4MPgbacLFGv7Xr8bmaTea00YdnrWz7b4a0lUviCmnceiUNAJ+A4TCj9+Ey6DeAfxBN203Xb87aAAAAAElFTkSuQmCC";

var __$_require_assets_images_title_rectangle_right_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB0AAAAlCAYAAAC+uuLPAAAA/0lEQVRYhb3W3QqCQBAF4Bl3s0iKKEIklW6iKEh0n6AX7E17hmK2IPP3pjl7E159jMc9Dd/ujycBT7wIKECCclxu8WiZGSzKLCh40sPW0HLGWLTKrf+Foi4zWHRqmc4JeNLrztDkPSgOdZ88oahcFSi6jpj2my8FQavMEteeIWg9TwgqE5YpGJUsJVMoWjVeLQbNwKg0kDQRFL0k1ncuFK3y9pT6aEeeqqhsCLIpQFEpeG7HqYs2qw+CyqoJRdNV4Dd5KNpVferoUJ4qqAmIio7qU0VPsaF52HNXtNCxPFVQ11N9amgUMh3j4Tz/jhap8R8SFB27Kipo31/ZzyGiFwxgDupQsmgnAAAAAElFTkSuQmCC";

/* script */
const __vue_script__$4T = script$4T;
var __vue_render__$4T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-rectangle-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_assets_images_title_rectangle_left_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_assets_images_title_rectangle_right_png__,
                    alt: "",
                  },
                }),
              ]),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4T = [];
__vue_render__$4T._withStripped = true;

  /* style */
  const __vue_inject_styles__$4T = undefined;
  /* scoped */
  const __vue_scope_id__$4T = "data-v-f0095c18";
  /* module identifier */
  const __vue_module_identifier__$4T = undefined;
  /* functional template */
  const __vue_is_functional_template__$4T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4T, staticRenderFns: __vue_staticRenderFns__$4T },
    __vue_inject_styles__$4T,
    __vue_script__$4T,
    __vue_scope_id__$4T,
    __vue_is_functional_template__$4T,
    __vue_module_identifier__$4T,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4S = {
  mixins: [singleGroupMixin],
  name: 'titleRotundityRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4S = script$4S;
/* template */
var __vue_render__$4S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-rotundity-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4S = [];
__vue_render__$4S._withStripped = true;

  /* style */
  const __vue_inject_styles__$4S = undefined;
  /* scoped */
  const __vue_scope_id__$4S = "data-v-e0be6b12";
  /* module identifier */
  const __vue_module_identifier__$4S = undefined;
  /* functional template */
  const __vue_is_functional_template__$4S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4S, staticRenderFns: __vue_staticRenderFns__$4S },
    __vue_inject_styles__$4S,
    __vue_script__$4S,
    __vue_scope_id__$4S,
    __vue_is_functional_template__$4S,
    __vue_module_identifier__$4S,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4R = {
  mixins: [singleGroupMixin],
  name: 'titleSecondRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4R = script$4R;
/* template */
var __vue_render__$4R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-second-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4R = [];
__vue_render__$4R._withStripped = true;

  /* style */
  const __vue_inject_styles__$4R = undefined;
  /* scoped */
  const __vue_scope_id__$4R = "data-v-2306d1de";
  /* module identifier */
  const __vue_module_identifier__$4R = undefined;
  /* functional template */
  const __vue_is_functional_template__$4R = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4R, staticRenderFns: __vue_staticRenderFns__$4R },
    __vue_inject_styles__$4R,
    __vue_script__$4R,
    __vue_scope_id__$4R,
    __vue_is_functional_template__$4R,
    __vue_module_identifier__$4R,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4Q = {
  mixins: [singleGroupMixin],
  name: 'titleSemicircleRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4Q = script$4Q;
/* template */
var __vue_render__$4Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-semicircle-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4Q = [];
__vue_render__$4Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$4Q = undefined;
  /* scoped */
  const __vue_scope_id__$4Q = "data-v-3221c087";
  /* module identifier */
  const __vue_module_identifier__$4Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$4Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4Q, staticRenderFns: __vue_staticRenderFns__$4Q },
    __vue_inject_styles__$4Q,
    __vue_script__$4Q,
    __vue_scope_id__$4Q,
    __vue_is_functional_template__$4Q,
    __vue_module_identifier__$4Q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4P = {
  mixins: [singleGroupMixin],
  name: 'titleSixthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4P = script$4P;
/* template */
var __vue_render__$4P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-sixth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left-div" }, [
              _c(
                "div",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container-left",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "title-left-line" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-right-div" },
              [
                _c("div", { staticClass: "title-right-line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "title-right-line2" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container-right",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
                _vm._v(" "),
                _c("div", { staticClass: "title-right-line3" }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4P = [];
__vue_render__$4P._withStripped = true;

  /* style */
  const __vue_inject_styles__$4P = undefined;
  /* scoped */
  const __vue_scope_id__$4P = "data-v-0a5d4770";
  /* module identifier */
  const __vue_module_identifier__$4P = undefined;
  /* functional template */
  const __vue_is_functional_template__$4P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4P, staticRenderFns: __vue_staticRenderFns__$4P },
    __vue_inject_styles__$4P,
    __vue_script__$4P,
    __vue_scope_id__$4P,
    __vue_is_functional_template__$4P,
    __vue_module_identifier__$4P,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4O = {
  mixins: [singleGroupMixin],
  name: 'titleSquareRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4O = script$4O;
/* template */
var __vue_render__$4O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-square-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4O = [];
__vue_render__$4O._withStripped = true;

  /* style */
  const __vue_inject_styles__$4O = undefined;
  /* scoped */
  const __vue_scope_id__$4O = "data-v-23b381f4";
  /* module identifier */
  const __vue_module_identifier__$4O = undefined;
  /* functional template */
  const __vue_is_functional_template__$4O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4O, staticRenderFns: __vue_staticRenderFns__$4O },
    __vue_inject_styles__$4O,
    __vue_script__$4O,
    __vue_scope_id__$4O,
    __vue_is_functional_template__$4O,
    __vue_module_identifier__$4O,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4N = {
  mixins: [singleGroupMixin],
  name: 'titleThirdRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    /**
     * 更新块数据
     * @param dataName  数据name
     * @param blockData 数据值
     */
    updateBlockData(dataName, blockData) {
      this.groupData[dataName] = blockData;
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4N = script$4N;
/* template */
var __vue_render__$4N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-third-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left" }, [
              _c(
                "span",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      "rule-index": _vm.ruleIndex,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4N = [];
__vue_render__$4N._withStripped = true;

  /* style */
  const __vue_inject_styles__$4N = undefined;
  /* scoped */
  const __vue_scope_id__$4N = "data-v-6c5b80dc";
  /* module identifier */
  const __vue_module_identifier__$4N = undefined;
  /* functional template */
  const __vue_is_functional_template__$4N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4N, staticRenderFns: __vue_staticRenderFns__$4N },
    __vue_inject_styles__$4N,
    __vue_script__$4N,
    __vue_scope_id__$4N,
    __vue_is_functional_template__$4N,
    __vue_module_identifier__$4N,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4M = {
  mixins: [singleGroupMixin],
  name: 'titleTriangleRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4M = script$4M;
/* template */
var __vue_render__$4M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-triangle-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4M = [];
__vue_render__$4M._withStripped = true;

  /* style */
  const __vue_inject_styles__$4M = undefined;
  /* scoped */
  const __vue_scope_id__$4M = "data-v-61d7e791";
  /* module identifier */
  const __vue_module_identifier__$4M = undefined;
  /* functional template */
  const __vue_is_functional_template__$4M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4M, staticRenderFns: __vue_staticRenderFns__$4M },
    __vue_inject_styles__$4M,
    __vue_script__$4M,
    __vue_scope_id__$4M,
    __vue_is_functional_template__$4M,
    __vue_module_identifier__$4M,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4L = {
  mixins: [singleGroupMixin],
  name: 'titleTwelfthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    /**
     * 更新块数据
     * @param dataName  数据name
     * @param blockData 数据值
     */
    updateBlockData(dataName, blockData) {
      this.groupData[dataName] = blockData;
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4L = script$4L;
/* template */
var __vue_render__$4L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twelfth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left" }, [
              _c(
                "span",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      "rule-index": _vm.ruleIndex,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4L = [];
__vue_render__$4L._withStripped = true;

  /* style */
  const __vue_inject_styles__$4L = undefined;
  /* scoped */
  const __vue_scope_id__$4L = "data-v-06acac49";
  /* module identifier */
  const __vue_module_identifier__$4L = undefined;
  /* functional template */
  const __vue_is_functional_template__$4L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4L, staticRenderFns: __vue_staticRenderFns__$4L },
    __vue_inject_styles__$4L,
    __vue_script__$4L,
    __vue_scope_id__$4L,
    __vue_is_functional_template__$4L,
    __vue_module_identifier__$4L,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4K = {
  mixins: [singleGroupMixin],
  name: 'titlethirteenthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4K = script$4K;
/* template */
var __vue_render__$4K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirteenth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "topleft" }),
              _vm._v(" "),
              _c("div", { staticClass: "topborder" }, [
                _c("div", { staticClass: "topborder-left" }, [
                  _c("div", { staticClass: "Line1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "Line2" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "topborder-right" }, [
                  _c("div", { staticClass: "Line3" }),
                ]),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "bottomborder" }, [
                _c("div", { staticClass: "Line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block" }),
                _vm._v(" "),
                _c("div", { staticClass: "block" }),
                _vm._v(" "),
                _c("div", { staticClass: "Line2" }),
                _vm._v(" "),
                _c("div", { staticClass: "Line3" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "rightbottom" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4K = [];
__vue_render__$4K._withStripped = true;

  /* style */
  const __vue_inject_styles__$4K = undefined;
  /* scoped */
  const __vue_scope_id__$4K = "data-v-8850ccfe";
  /* module identifier */
  const __vue_module_identifier__$4K = undefined;
  /* functional template */
  const __vue_is_functional_template__$4K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4K, staticRenderFns: __vue_staticRenderFns__$4K },
    __vue_inject_styles__$4K,
    __vue_script__$4K,
    __vue_scope_id__$4K,
    __vue_is_functional_template__$4K,
    __vue_module_identifier__$4K,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4J = {
  mixins: [singleGroupMixin],
  name: 'titlefourteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4J = script$4J;
/* template */
var __vue_render__$4J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fourteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "cont-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4J = [];
__vue_render__$4J._withStripped = true;

  /* style */
  const __vue_inject_styles__$4J = undefined;
  /* scoped */
  const __vue_scope_id__$4J = "data-v-2f96955a";
  /* module identifier */
  const __vue_module_identifier__$4J = undefined;
  /* functional template */
  const __vue_is_functional_template__$4J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4J, staticRenderFns: __vue_staticRenderFns__$4J },
    __vue_inject_styles__$4J,
    __vue_script__$4J,
    __vue_scope_id__$4J,
    __vue_is_functional_template__$4J,
    __vue_module_identifier__$4J,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4I = {
  mixins: [singleGroupMixin],
  name: 'titleFifteenRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  isUnPrefix: true,
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4I = script$4I;
/* template */
var __vue_render__$4I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fifteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4I = [];
__vue_render__$4I._withStripped = true;

  /* style */
  const __vue_inject_styles__$4I = undefined;
  /* scoped */
  const __vue_scope_id__$4I = "data-v-1df2118c";
  /* module identifier */
  const __vue_module_identifier__$4I = undefined;
  /* functional template */
  const __vue_is_functional_template__$4I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4I, staticRenderFns: __vue_staticRenderFns__$4I },
    __vue_inject_styles__$4I,
    __vue_script__$4I,
    __vue_scope_id__$4I,
    __vue_is_functional_template__$4I,
    __vue_module_identifier__$4I,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4H = {
  mixins: [singleGroupMixin],
  name: 'titleSixteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4H = script$4H;
/* template */
var __vue_render__$4H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-sixteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "left-image" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "right-icon" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4H = [];
__vue_render__$4H._withStripped = true;

  /* style */
  const __vue_inject_styles__$4H = undefined;
  /* scoped */
  const __vue_scope_id__$4H = "data-v-16547c44";
  /* module identifier */
  const __vue_module_identifier__$4H = undefined;
  /* functional template */
  const __vue_is_functional_template__$4H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4H, staticRenderFns: __vue_staticRenderFns__$4H },
    __vue_inject_styles__$4H,
    __vue_script__$4H,
    __vue_scope_id__$4H,
    __vue_is_functional_template__$4H,
    __vue_module_identifier__$4H,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4G = {
  mixins: [singleGroupMixin],
  name: 'titleSeventeenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4G = script$4G;
/* template */
var __vue_render__$4G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-seventeen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("div", { staticClass: "zhuanshi" }, [
                  _c("div", { staticClass: "line1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "line2" }),
                ]),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4G = [];
__vue_render__$4G._withStripped = true;

  /* style */
  const __vue_inject_styles__$4G = undefined;
  /* scoped */
  const __vue_scope_id__$4G = "data-v-2482e12e";
  /* module identifier */
  const __vue_module_identifier__$4G = undefined;
  /* functional template */
  const __vue_is_functional_template__$4G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4G, staticRenderFns: __vue_staticRenderFns__$4G },
    __vue_inject_styles__$4G,
    __vue_script__$4G,
    __vue_scope_id__$4G,
    __vue_is_functional_template__$4G,
    __vue_module_identifier__$4G,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4F = {
  mixins: [singleGroupMixin],
  name: 'titleeighteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4F = script$4F;
/* template */
var __vue_render__$4F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-eighteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "linebox" }, [
              _c("div", { staticClass: "line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4F = [];
__vue_render__$4F._withStripped = true;

  /* style */
  const __vue_inject_styles__$4F = undefined;
  /* scoped */
  const __vue_scope_id__$4F = "data-v-ff514a54";
  /* module identifier */
  const __vue_module_identifier__$4F = undefined;
  /* functional template */
  const __vue_is_functional_template__$4F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4F, staticRenderFns: __vue_staticRenderFns__$4F },
    __vue_inject_styles__$4F,
    __vue_script__$4F,
    __vue_scope_id__$4F,
    __vue_is_functional_template__$4F,
    __vue_module_identifier__$4F,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4E = {
  mixins: [singleGroupMixin],
  name: 'titleEighteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4E = script$4E;
/* template */
var __vue_render__$4E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-eighteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "linebox" }, [
              _c("div", { staticClass: "line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4E = [];
__vue_render__$4E._withStripped = true;

  /* style */
  const __vue_inject_styles__$4E = undefined;
  /* scoped */
  const __vue_scope_id__$4E = "data-v-9bba58d0";
  /* module identifier */
  const __vue_module_identifier__$4E = undefined;
  /* functional template */
  const __vue_is_functional_template__$4E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4E, staticRenderFns: __vue_staticRenderFns__$4E },
    __vue_inject_styles__$4E,
    __vue_script__$4E,
    __vue_scope_id__$4E,
    __vue_is_functional_template__$4E,
    __vue_module_identifier__$4E,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4D = {
  mixins: [singleGroupMixin],
  name: 'titleNineteenRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4D = script$4D;
/* template */
var __vue_render__$4D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-nineteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4D = [];
__vue_render__$4D._withStripped = true;

  /* style */
  const __vue_inject_styles__$4D = undefined;
  /* scoped */
  const __vue_scope_id__$4D = "data-v-223b64e7";
  /* module identifier */
  const __vue_module_identifier__$4D = undefined;
  /* functional template */
  const __vue_is_functional_template__$4D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4D, staticRenderFns: __vue_staticRenderFns__$4D },
    __vue_inject_styles__$4D,
    __vue_script__$4D,
    __vue_scope_id__$4D,
    __vue_is_functional_template__$4D,
    __vue_module_identifier__$4D,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4C = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4C = script$4C;
/* template */
var __vue_render__$4C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4C = [];
__vue_render__$4C._withStripped = true;

  /* style */
  const __vue_inject_styles__$4C = undefined;
  /* scoped */
  const __vue_scope_id__$4C = "data-v-649daf42";
  /* module identifier */
  const __vue_module_identifier__$4C = undefined;
  /* functional template */
  const __vue_is_functional_template__$4C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4C, staticRenderFns: __vue_staticRenderFns__$4C },
    __vue_inject_styles__$4C,
    __vue_script__$4C,
    __vue_scope_id__$4C,
    __vue_is_functional_template__$4C,
    __vue_module_identifier__$4C,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4B = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4B = script$4B;
/* template */
var __vue_render__$4B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4B = [];
__vue_render__$4B._withStripped = true;

  /* style */
  const __vue_inject_styles__$4B = undefined;
  /* scoped */
  const __vue_scope_id__$4B = "data-v-eace7d54";
  /* module identifier */
  const __vue_module_identifier__$4B = undefined;
  /* functional template */
  const __vue_is_functional_template__$4B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4B, staticRenderFns: __vue_staticRenderFns__$4B },
    __vue_inject_styles__$4B,
    __vue_script__$4B,
    __vue_scope_id__$4B,
    __vue_is_functional_template__$4B,
    __vue_module_identifier__$4B,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4A = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4A = script$4A;
/* template */
var __vue_render__$4A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
                _vm._v(" "),
                _c("div", { staticClass: "right-top-bg" }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4A = [];
__vue_render__$4A._withStripped = true;

  /* style */
  const __vue_inject_styles__$4A = undefined;
  /* scoped */
  const __vue_scope_id__$4A = "data-v-a00e7ea6";
  /* module identifier */
  const __vue_module_identifier__$4A = undefined;
  /* functional template */
  const __vue_is_functional_template__$4A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4A, staticRenderFns: __vue_staticRenderFns__$4A },
    __vue_inject_styles__$4A,
    __vue_script__$4A,
    __vue_scope_id__$4A,
    __vue_is_functional_template__$4A,
    __vue_module_identifier__$4A,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4z = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4z = script$4z;
/* template */
var __vue_render__$4z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "cont-box" }, [
            _c("div", { staticClass: "title-container" }, [
              _c("div", { staticClass: "right-top-bg" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4z = [];
__vue_render__$4z._withStripped = true;

  /* style */
  const __vue_inject_styles__$4z = undefined;
  /* scoped */
  const __vue_scope_id__$4z = "data-v-55dc42f8";
  /* module identifier */
  const __vue_module_identifier__$4z = undefined;
  /* functional template */
  const __vue_is_functional_template__$4z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4z, staticRenderFns: __vue_staticRenderFns__$4z },
    __vue_inject_styles__$4z,
    __vue_script__$4z,
    __vue_scope_id__$4z,
    __vue_is_functional_template__$4z,
    __vue_module_identifier__$4z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4y = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme5Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4y = script$4y;
/* template */
var __vue_render__$4y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4y = [];
__vue_render__$4y._withStripped = true;

  /* style */
  const __vue_inject_styles__$4y = undefined;
  /* scoped */
  const __vue_scope_id__$4y = "data-v-981c89f2";
  /* module identifier */
  const __vue_module_identifier__$4y = undefined;
  /* functional template */
  const __vue_is_functional_template__$4y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4y, staticRenderFns: __vue_staticRenderFns__$4y },
    __vue_inject_styles__$4y,
    __vue_script__$4y,
    __vue_scope_id__$4y,
    __vue_is_functional_template__$4y,
    __vue_module_identifier__$4y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4x = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme6Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4x = script$4x;
/* template */
var __vue_render__$4x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme6-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }, [
                _c("div", { staticClass: "line" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }, [
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4x = [];
__vue_render__$4x._withStripped = true;

  /* style */
  const __vue_inject_styles__$4x = undefined;
  /* scoped */
  const __vue_scope_id__$4x = "data-v-66320dfa";
  /* module identifier */
  const __vue_module_identifier__$4x = undefined;
  /* functional template */
  const __vue_is_functional_template__$4x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4x, staticRenderFns: __vue_staticRenderFns__$4x },
    __vue_inject_styles__$4x,
    __vue_script__$4x,
    __vue_scope_id__$4x,
    __vue_is_functional_template__$4x,
    __vue_module_identifier__$4x,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4w = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4w = script$4w;
/* template */
var __vue_render__$4w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    resourceBasisPath: _vm.resourceBasisPath,
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
                _vm._v(" "),
                _c("div", { staticClass: "jiantou1" }),
                _vm._v(" "),
                _c("div", { staticClass: "jiantou2" }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }, [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4w = [];
__vue_render__$4w._withStripped = true;

  /* style */
  const __vue_inject_styles__$4w = undefined;
  /* scoped */
  const __vue_scope_id__$4w = "data-v-39d4d402";
  /* module identifier */
  const __vue_module_identifier__$4w = undefined;
  /* functional template */
  const __vue_is_functional_template__$4w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4w, staticRenderFns: __vue_staticRenderFns__$4w },
    __vue_inject_styles__$4w,
    __vue_script__$4w,
    __vue_scope_id__$4w,
    __vue_is_functional_template__$4w,
    __vue_module_identifier__$4w,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4v = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4v = script$4v;
/* template */
var __vue_render__$4v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-icon" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4v = [];
__vue_render__$4v._withStripped = true;

  /* style */
  const __vue_inject_styles__$4v = undefined;
  /* scoped */
  const __vue_scope_id__$4v = "data-v-4a9ac670";
  /* module identifier */
  const __vue_module_identifier__$4v = undefined;
  /* functional template */
  const __vue_is_functional_template__$4v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4v, staticRenderFns: __vue_staticRenderFns__$4v },
    __vue_inject_styles__$4v,
    __vue_script__$4v,
    __vue_scope_id__$4v,
    __vue_is_functional_template__$4v,
    __vue_module_identifier__$4v,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4u = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4u = script$4u;
/* template */
var __vue_render__$4u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-icon" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4u = [];
__vue_render__$4u._withStripped = true;

  /* style */
  const __vue_inject_styles__$4u = undefined;
  /* scoped */
  const __vue_scope_id__$4u = "data-v-50e84429";
  /* module identifier */
  const __vue_module_identifier__$4u = undefined;
  /* functional template */
  const __vue_is_functional_template__$4u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4u, staticRenderFns: __vue_staticRenderFns__$4u },
    __vue_inject_styles__$4u,
    __vue_script__$4u,
    __vue_scope_id__$4u,
    __vue_is_functional_template__$4u,
    __vue_module_identifier__$4u,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4t = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4t = script$4t;
/* template */
var __vue_render__$4t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4t = [];
__vue_render__$4t._withStripped = true;

  /* style */
  const __vue_inject_styles__$4t = undefined;
  /* scoped */
  const __vue_scope_id__$4t = "data-v-26c2b954";
  /* module identifier */
  const __vue_module_identifier__$4t = undefined;
  /* functional template */
  const __vue_is_functional_template__$4t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4t, staticRenderFns: __vue_staticRenderFns__$4t },
    __vue_inject_styles__$4t,
    __vue_script__$4t,
    __vue_scope_id__$4t,
    __vue_is_functional_template__$4t,
    __vue_module_identifier__$4t,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4s = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme5Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4s = script$4s;
/* template */
var __vue_render__$4s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-img" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-img" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4s = [];
__vue_render__$4s._withStripped = true;

  /* style */
  const __vue_inject_styles__$4s = undefined;
  /* scoped */
  const __vue_scope_id__$4s = "data-v-41d0d0e4";
  /* module identifier */
  const __vue_module_identifier__$4s = undefined;
  /* functional template */
  const __vue_is_functional_template__$4s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4s, staticRenderFns: __vue_staticRenderFns__$4s },
    __vue_inject_styles__$4s,
    __vue_script__$4s,
    __vue_scope_id__$4s,
    __vue_is_functional_template__$4s,
    __vue_module_identifier__$4s,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4r = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme6Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4r = script$4r;
/* template */
var __vue_render__$4r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme6-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "zhuangshi1" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "zhuangshi2" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4r = [];
__vue_render__$4r._withStripped = true;

  /* style */
  const __vue_inject_styles__$4r = undefined;
  /* scoped */
  const __vue_scope_id__$4r = "data-v-71ef684f";
  /* module identifier */
  const __vue_module_identifier__$4r = undefined;
  /* functional template */
  const __vue_is_functional_template__$4r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4r, staticRenderFns: __vue_staticRenderFns__$4r },
    __vue_inject_styles__$4r,
    __vue_script__$4r,
    __vue_scope_id__$4r,
    __vue_is_functional_template__$4r,
    __vue_module_identifier__$4r,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4q = {
  mixins: [singleGroupMixin],
  name: 'titleKnowledgeRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4q = script$4q;
/* template */
var __vue_render__$4q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Knowledge-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.numberTextDataName],
                    "block-data-name": _vm.numberTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4q = [];
__vue_render__$4q._withStripped = true;

  /* style */
  const __vue_inject_styles__$4q = undefined;
  /* scoped */
  const __vue_scope_id__$4q = "data-v-04ce6ffe";
  /* module identifier */
  const __vue_module_identifier__$4q = undefined;
  /* functional template */
  const __vue_is_functional_template__$4q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4q, staticRenderFns: __vue_staticRenderFns__$4q },
    __vue_inject_styles__$4q,
    __vue_script__$4q,
    __vue_scope_id__$4q,
    __vue_is_functional_template__$4q,
    __vue_module_identifier__$4q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4p = {
  mixins: [singleGroupMixin],
  name: 'titleSanQintheme1Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {},
      mouseHover: false
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4p = script$4p;
/* template */
var __vue_render__$4p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group",
      class: [
        "xml-title-SanQintheme1-" + _vm.pageType,
        _vm.mouseHover ? "hovered" : "",
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4p = [];
__vue_render__$4p._withStripped = true;

  /* style */
  const __vue_inject_styles__$4p = undefined;
  /* scoped */
  const __vue_scope_id__$4p = "data-v-e0a9ee6a";
  /* module identifier */
  const __vue_module_identifier__$4p = undefined;
  /* functional template */
  const __vue_is_functional_template__$4p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4p, staticRenderFns: __vue_staticRenderFns__$4p },
    __vue_inject_styles__$4p,
    __vue_script__$4p,
    __vue_scope_id__$4p,
    __vue_is_functional_template__$4p,
    __vue_module_identifier__$4p,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4o = {
  mixins: [singleGroupMixin],
  name: 'titleSanQintheme2Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {},
      mouseHover: false
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4o = script$4o;
/* template */
var __vue_render__$4o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group",
      class: [
        "xml-title-SanQintheme2-" + _vm.pageType,
        _vm.mouseHover ? "hovered" : "",
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4o = [];
__vue_render__$4o._withStripped = true;

  /* style */
  const __vue_inject_styles__$4o = undefined;
  /* scoped */
  const __vue_scope_id__$4o = "data-v-52c9c268";
  /* module identifier */
  const __vue_module_identifier__$4o = undefined;
  /* functional template */
  const __vue_is_functional_template__$4o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4o, staticRenderFns: __vue_staticRenderFns__$4o },
    __vue_inject_styles__$4o,
    __vue_script__$4o,
    __vue_scope_id__$4o,
    __vue_is_functional_template__$4o,
    __vue_module_identifier__$4o,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4n = {
  mixins: [singleGroupMixin],
  name: 'titleSanQintheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4n = script$4n;
/* template */
var __vue_render__$4n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-SanQintheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4n = [];
__vue_render__$4n._withStripped = true;

  /* style */
  const __vue_inject_styles__$4n = undefined;
  /* scoped */
  const __vue_scope_id__$4n = "data-v-5f6e490d";
  /* module identifier */
  const __vue_module_identifier__$4n = undefined;
  /* functional template */
  const __vue_is_functional_template__$4n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4n, staticRenderFns: __vue_staticRenderFns__$4n },
    __vue_inject_styles__$4n,
    __vue_script__$4n,
    __vue_scope_id__$4n,
    __vue_is_functional_template__$4n,
    __vue_module_identifier__$4n,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4m = {
  mixins: [singleGroupMixin],
  name: 'titleTwentyRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$4m = script$4m;
/* template */
var __vue_render__$4m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twenty-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }),
            _vm._v(" "),
            _c("div", { staticClass: "left-block" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4m = [];
__vue_render__$4m._withStripped = true;

  /* style */
  const __vue_inject_styles__$4m = undefined;
  /* scoped */
  const __vue_scope_id__$4m = "data-v-68a2506c";
  /* module identifier */
  const __vue_module_identifier__$4m = undefined;
  /* functional template */
  const __vue_is_functional_template__$4m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4m, staticRenderFns: __vue_staticRenderFns__$4m },
    __vue_inject_styles__$4m,
    __vue_script__$4m,
    __vue_scope_id__$4m,
    __vue_is_functional_template__$4m,
    __vue_module_identifier__$4m,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4l = {
  mixins: [singleGroupMixin],
  name: 'titleTwentyoneRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon2_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$4l = script$4l;
var __vue_render__$4l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twentyone-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }, [
              _c("img", {
                attrs: {
                  src: __$_require_static_images_leaflet_resource_enter_title_icon2_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4l = [];
__vue_render__$4l._withStripped = true;

  /* style */
  const __vue_inject_styles__$4l = undefined;
  /* scoped */
  const __vue_scope_id__$4l = "data-v-189a191b";
  /* module identifier */
  const __vue_module_identifier__$4l = undefined;
  /* functional template */
  const __vue_is_functional_template__$4l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4l, staticRenderFns: __vue_staticRenderFns__$4l },
    __vue_inject_styles__$4l,
    __vue_script__$4l,
    __vue_scope_id__$4l,
    __vue_is_functional_template__$4l,
    __vue_module_identifier__$4l,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4k = {
  mixins: [singleGroupMixin],
  name: 'titleTwentytwoRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon3_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADMAAAAjCAYAAAA5dzKxAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6QTJFNDVBQkQzNTJCMTFFRjhENDZEMUE5MzEyNkI3OTkiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6QTJFNDVBQkUzNTJCMTFFRjhENDZEMUE5MzEyNkI3OTkiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDpBMkU0NUFCQjM1MkIxMUVGOEQ0NkQxQTkzMTI2Qjc5OSIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDpBMkU0NUFCQzM1MkIxMUVGOEQ0NkQxQTkzMTI2Qjc5OSIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PhQPLJgAAAHvSURBVHja3JhPKARhGMZnZ5eDQiklB/lbJJFwc1kXDg5KSi5KFBdcnJQo5cKWlhNJDo4ulFzERsvFxEXaA4VycVkXkfW8eqemMbNtmhnzzlO/dna+nT/Pfu/3ft/7hVJ7GcVCITALBkCB4h+lwQ5YtmqM2FzUDxYUf6oZ3IAjc4Nqc0Grgw9Pgii4dvCelu+nevBPnoFjcOn2gyIemJkEnaD9v83EOT6d0KYD9+gAI381cwgOfDT437KZUZUAyQ0zPZy5okEwUwWaOETHpZvR+DMPrIM1j7KmK2YoxL4M3ye4l0okmqGMkzKd6wIXoF6aGdKVxblaXtp0B8EMqRjsg2lJZrQsbWGwAjZAvoS1mZbDb2gmp2Jq1O9mXsAzKLdoOwGvoBCcS1k1azZmnsCQpDFjTgJU6r7z8SCXBKLMaIZ5ZwrEDPsLq5wIxJhJcm/EeIwscoiRWsCYJDOPoBTMGXpoxtA+7/Tz3a5n0px+de2CUz7+lLgHYBQZ6wO9IGFakIozo/D42fZyQ0MPgUpQ7aPKuCJbiNqZSfBn3IelfsbwfjmZoa3PYZ7gwj4y8gG2uDb6pZDNxrlI6T1TBOqEeqDeuKN5LMIF0y0oE9wp96CBJs1G4Ub0rFuj8jIjCPoJM9oYXwJtgo1QwffwLcAAudpcGOn8/bYAAAAASUVORK5CYII=";

/* script */
const __vue_script__$4k = script$4k;
var __vue_render__$4k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twentytwo-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }, [
              _c("div", [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_title_icon3_png__,
                    alt: "",
                  },
                }),
              ]),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "title-container" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4k = [];
__vue_render__$4k._withStripped = true;

  /* style */
  const __vue_inject_styles__$4k = undefined;
  /* scoped */
  const __vue_scope_id__$4k = "data-v-57c25178";
  /* module identifier */
  const __vue_module_identifier__$4k = undefined;
  /* functional template */
  const __vue_is_functional_template__$4k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4k, staticRenderFns: __vue_staticRenderFns__$4k },
    __vue_inject_styles__$4k,
    __vue_script__$4k,
    __vue_scope_id__$4k,
    __vue_is_functional_template__$4k,
    __vue_module_identifier__$4k,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4j = {
  mixins: [singleGroupMixin],
  name: 'titleTwentythreeRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon4_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$4j = script$4j;
var __vue_render__$4j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twentythree-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }, [
              _c("div", [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_title_icon4_png__,
                    alt: "",
                  },
                }),
              ]),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "title-container" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4j = [];
__vue_render__$4j._withStripped = true;

  /* style */
  const __vue_inject_styles__$4j = undefined;
  /* scoped */
  const __vue_scope_id__$4j = "data-v-3346546a";
  /* module identifier */
  const __vue_module_identifier__$4j = undefined;
  /* functional template */
  const __vue_is_functional_template__$4j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4j, staticRenderFns: __vue_staticRenderFns__$4j },
    __vue_inject_styles__$4j,
    __vue_script__$4j,
    __vue_scope_id__$4j,
    __vue_is_functional_template__$4j,
    __vue_module_identifier__$4j,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4i = {
  mixins: [singleGroupMixin],
  name: 'titleTwentyfourRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_bg2_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$4i = script$4i;
var __vue_render__$4i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twentyfour-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }, [
              _c("img", {
                attrs: {
                  src: __$_require_static_images_leaflet_resource_enter_title_bg2_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "title-container" }, [
              _c("div", { staticClass: "left-line" }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "right-line" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4i = [];
__vue_render__$4i._withStripped = true;

  /* style */
  const __vue_inject_styles__$4i = undefined;
  /* scoped */
  const __vue_scope_id__$4i = "data-v-2bd1868f";
  /* module identifier */
  const __vue_module_identifier__$4i = undefined;
  /* functional template */
  const __vue_is_functional_template__$4i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4i, staticRenderFns: __vue_staticRenderFns__$4i },
    __vue_inject_styles__$4i,
    __vue_script__$4i,
    __vue_scope_id__$4i,
    __vue_is_functional_template__$4i,
    __vue_module_identifier__$4i,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4h = {
  mixins: [singleGroupMixin],
  name: 'titleTwentyfiveRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon8_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$4h = script$4h;
var __vue_render__$4h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twentyfive-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c("div", { staticClass: "left-bg" }, [
              _c("img", {
                attrs: {
                  src: __$_require_static_images_leaflet_resource_enter_title_bg2_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "title-container" }, [
              _c("img", {
                staticClass: "icon",
                attrs: {
                  src: __$_require_static_images_leaflet_resource_enter_title_icon8_png__,
                  alt: "",
                },
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4h = [];
__vue_render__$4h._withStripped = true;

  /* style */
  const __vue_inject_styles__$4h = undefined;
  /* scoped */
  const __vue_scope_id__$4h = "data-v-284855f8";
  /* module identifier */
  const __vue_module_identifier__$4h = undefined;
  /* functional template */
  const __vue_is_functional_template__$4h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4h, staticRenderFns: __vue_staticRenderFns__$4h },
    __vue_inject_styles__$4h,
    __vue_script__$4h,
    __vue_scope_id__$4h,
    __vue_is_functional_template__$4h,
    __vue_module_identifier__$4h,
    false,
    undefined,
    undefined,
    undefined
  );

const TitleComponents$4 = [__vue_component__$4W, __vue_component__$4V, __vue_component__$4U, __vue_component__$4T, __vue_component__$4S, __vue_component__$4R, __vue_component__$4Q, __vue_component__$4P, __vue_component__$4O, __vue_component__$4N, __vue_component__$4M, __vue_component__$4L, __vue_component__$4K, __vue_component__$4J, __vue_component__$4I, __vue_component__$4H, __vue_component__$4G, __vue_component__$4F, __vue_component__$4E, __vue_component__$4D, __vue_component__$4C, __vue_component__$4B, __vue_component__$4A, __vue_component__$4z, __vue_component__$4y, __vue_component__$4x, __vue_component__$4w, __vue_component__$4v, __vue_component__$4u, __vue_component__$4t, __vue_component__$4s, __vue_component__$4r, __vue_component__$4q, __vue_component__$4p, __vue_component__$4o, __vue_component__$4n, __vue_component__$4m, __vue_component__$4l, __vue_component__$4k, __vue_component__$4j, __vue_component__$4i, __vue_component__$4h];
var XmlTitle = {
  // install,
  ...TitleComponents$4
};

//
var script$4g = {
  mixins: [singleGroupMixin],
  name: 'imageTextEighthRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4g = script$4g;
/* template */
var __vue_render__$4g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-eighth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "iamge-bg-box" },
            [
              _c("xml-image-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                  "block-data-name": _vm.imageBlockDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "image-container",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                },
                on: { blockclick: _vm.handleBlockClick },
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4g = [];
__vue_render__$4g._withStripped = true;

  /* style */
  const __vue_inject_styles__$4g = undefined;
  /* scoped */
  const __vue_scope_id__$4g = "data-v-9094a8a8";
  /* module identifier */
  const __vue_module_identifier__$4g = undefined;
  /* functional template */
  const __vue_is_functional_template__$4g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4g, staticRenderFns: __vue_staticRenderFns__$4g },
    __vue_inject_styles__$4g,
    __vue_script__$4g,
    __vue_scope_id__$4g,
    __vue_is_functional_template__$4g,
    __vue_module_identifier__$4g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4f = {
  mixins: [singleGroupMixin],
  name: 'imageTextFifthRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4f = script$4f;
/* template */
var __vue_render__$4f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-fifth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c(
                "div",
                { staticClass: "iamge-bg-box" },
                [
                  _c(
                    "div",
                    { staticClass: "image-top-title" },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "image-text-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4f = [];
__vue_render__$4f._withStripped = true;

  /* style */
  const __vue_inject_styles__$4f = undefined;
  /* scoped */
  const __vue_scope_id__$4f = "data-v-2aaa640c";
  /* module identifier */
  const __vue_module_identifier__$4f = undefined;
  /* functional template */
  const __vue_is_functional_template__$4f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4f, staticRenderFns: __vue_staticRenderFns__$4f },
    __vue_inject_styles__$4f,
    __vue_script__$4f,
    __vue_scope_id__$4f,
    __vue_is_functional_template__$4f,
    __vue_module_identifier__$4f,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4e = {
  mixins: [singleGroupMixin],
  name: 'imageTextFirstRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4e = script$4e;
/* template */
var __vue_render__$4e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-first-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticStyle: {
                background: "#4c95ee",
                "border-radius": "6px",
                padding: "9px 11px",
              },
            },
            [
              _c(
                "div",
                { staticStyle: { display: "flex", "flex-wrap": "nowrap" } },
                [
                  _c(
                    "div",
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _c("xml-image-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                          "block-data-name": _vm.imageBlockDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "image-container",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                        },
                        on: { blockclick: _vm.handleBlockClick },
                      }),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4e = [];
__vue_render__$4e._withStripped = true;

  /* style */
  const __vue_inject_styles__$4e = undefined;
  /* scoped */
  const __vue_scope_id__$4e = "data-v-6677213e";
  /* module identifier */
  const __vue_module_identifier__$4e = undefined;
  /* functional template */
  const __vue_is_functional_template__$4e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4e, staticRenderFns: __vue_staticRenderFns__$4e },
    __vue_inject_styles__$4e,
    __vue_script__$4e,
    __vue_scope_id__$4e,
    __vue_is_functional_template__$4e,
    __vue_module_identifier__$4e,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4d = {
  mixins: [singleGroupMixin],
  name: 'imageTextFourthRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4d = script$4d;
/* template */
var __vue_render__$4d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-fourth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c(
                "div",
                { staticClass: "iamge-bg-box" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "image-text-box" },
                    [
                      _c(
                        "div",
                        { staticClass: "image-top-title" },
                        [
                          _c("xml-text-render", {
                            attrs: {
                              "p-block-data":
                                _vm.groupData[_vm.numberTextDataName],
                              "block-data-name": _vm.numberTextDataName,
                              "extend-params": _vm.extendParams,
                              "current-rule": _vm.currentRule,
                              "rule-index": _vm.ruleIndex,
                              "container-class": "text-container-number",
                              "editor-class": "editor",
                              mode: _vm.mode,
                              "page-type": _vm.pageType,
                              resourceBasisPath: _vm.resourceBasisPath,
                              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                            },
                            on: {
                              blockclick: _vm.handleBlockClick,
                              groupDataChange: _vm.groupDataChange,
                            },
                            scopedSlots: _vm._u(
                              [
                                _vm._l(
                                  _vm.$scopedSlots,
                                  function (index, name) {
                                    return {
                                      key: name,
                                      fn: function (deliver) {
                                        return [
                                          _vm._t(name, null, null, deliver),
                                        ]
                                      },
                                    }
                                  }
                                ),
                              ],
                              null,
                              true
                            ),
                          }),
                          _vm._v(" "),
                          _c("xml-text-render", {
                            attrs: {
                              "p-block-data":
                                _vm.groupData[_vm.titleTextDataName],
                              "block-data-name": _vm.titleTextDataName,
                              "extend-params": _vm.extendParams,
                              "current-rule": _vm.currentRule,
                              "rule-index": _vm.ruleIndex,
                              "container-class": "text-container",
                              "editor-class": "editor",
                              mode: _vm.mode,
                              "page-type": _vm.pageType,
                              resourceBasisPath: _vm.resourceBasisPath,
                              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                            },
                            on: {
                              blockclick: _vm.handleBlockClick,
                              groupDataChange: _vm.groupDataChange,
                            },
                            scopedSlots: _vm._u(
                              [
                                _vm._l(
                                  _vm.$scopedSlots,
                                  function (index, name) {
                                    return {
                                      key: name,
                                      fn: function (deliver) {
                                        return [
                                          _vm._t(name, null, null, deliver),
                                        ]
                                      },
                                    }
                                  }
                                ),
                              ],
                              null,
                              true
                            ),
                          }),
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ],
                1
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4d = [];
__vue_render__$4d._withStripped = true;

  /* style */
  const __vue_inject_styles__$4d = undefined;
  /* scoped */
  const __vue_scope_id__$4d = "data-v-f76eba0a";
  /* module identifier */
  const __vue_module_identifier__$4d = undefined;
  /* functional template */
  const __vue_is_functional_template__$4d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4d, staticRenderFns: __vue_staticRenderFns__$4d },
    __vue_inject_styles__$4d,
    __vue_script__$4d,
    __vue_scope_id__$4d,
    __vue_is_functional_template__$4d,
    __vue_module_identifier__$4d,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4c = {
  mixins: [singleGroupMixin],
  name: 'imageTextSecondRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4c = script$4c;
/* template */
var __vue_render__$4c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-second-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { display: "flex", "align-items": "center" } },
            [
              _c(
                "div",
                { staticClass: "image-box" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "title-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: { blockclick: _vm.handleBlockClick },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-box" }, [
            _c(
              "div",
              { staticClass: "body-box-inner" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4c = [];
__vue_render__$4c._withStripped = true;

  /* style */
  const __vue_inject_styles__$4c = undefined;
  /* scoped */
  const __vue_scope_id__$4c = "data-v-7fa56ccd";
  /* module identifier */
  const __vue_module_identifier__$4c = undefined;
  /* functional template */
  const __vue_is_functional_template__$4c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4c, staticRenderFns: __vue_staticRenderFns__$4c },
    __vue_inject_styles__$4c,
    __vue_script__$4c,
    __vue_scope_id__$4c,
    __vue_is_functional_template__$4c,
    __vue_module_identifier__$4c,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4b = {
  mixins: [singleGroupMixin],
  name: 'imageTextSeventhRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4b = script$4b;
/* template */
var __vue_render__$4b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-seventh-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c("div", {}, [
                _c("div", { staticClass: "image-text-decorate2" }),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "iamge-bg-box" },
                  [
                    _c("xml-image-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                        "block-data-name": _vm.imageBlockDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "image-container",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                      },
                      on: { blockclick: _vm.handleBlockClick },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "image-text-box" },
                  [
                    _c(
                      "xml-text-render",
                      {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      },
                      [_vm._v("s\n          ")]
                    ),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "image-text-decorate" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4b = [];
__vue_render__$4b._withStripped = true;

  /* style */
  const __vue_inject_styles__$4b = undefined;
  /* scoped */
  const __vue_scope_id__$4b = "data-v-78447962";
  /* module identifier */
  const __vue_module_identifier__$4b = undefined;
  /* functional template */
  const __vue_is_functional_template__$4b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4b, staticRenderFns: __vue_staticRenderFns__$4b },
    __vue_inject_styles__$4b,
    __vue_script__$4b,
    __vue_scope_id__$4b,
    __vue_is_functional_template__$4b,
    __vue_module_identifier__$4b,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$4a = {
  mixins: [singleGroupMixin],
  name: 'imageTextSixthRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$4a = script$4a;
/* template */
var __vue_render__$4a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-sixth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticStyle: {
                "border-radius": "6px",
                padding: "9px 11px",
                display: "flex",
              },
            },
            [
              _c("div", { staticStyle: { display: "flex" } }, [
                _c(
                  "div",
                  { staticClass: "iamge-bg-box" },
                  [
                    _c(
                      "div",
                      { staticClass: "image-top-title" },
                      [
                        _c("xml-text-render", {
                          attrs: {
                            "p-block-data":
                              _vm.groupData[_vm.titleTextDataName],
                            "block-data-name": _vm.titleTextDataName,
                            "extend-params": _vm.extendParams,
                            "current-rule": _vm.currentRule,
                            "rule-index": _vm.ruleIndex,
                            "container-class": "text-container",
                            "editor-class": "editor",
                            mode: _vm.mode,
                            "page-type": _vm.pageType,
                            resourceBasisPath: _vm.resourceBasisPath,
                            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                          },
                          on: {
                            blockclick: _vm.handleBlockClick,
                            groupDataChange: _vm.groupDataChange,
                          },
                          scopedSlots: _vm._u(
                            [
                              _vm._l(_vm.$scopedSlots, function (index, name) {
                                return {
                                  key: name,
                                  fn: function (deliver) {
                                    return [_vm._t(name, null, null, deliver)]
                                  },
                                }
                              }),
                            ],
                            null,
                            true
                          ),
                        }),
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c("xml-image-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                        "block-data-name": _vm.imageBlockDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "image-container",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                      },
                      on: { blockclick: _vm.handleBlockClick },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "image-text-box" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "image-text-decorate" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$4a = [];
__vue_render__$4a._withStripped = true;

  /* style */
  const __vue_inject_styles__$4a = undefined;
  /* scoped */
  const __vue_scope_id__$4a = "data-v-782b11c5";
  /* module identifier */
  const __vue_module_identifier__$4a = undefined;
  /* functional template */
  const __vue_is_functional_template__$4a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4a, staticRenderFns: __vue_staticRenderFns__$4a },
    __vue_inject_styles__$4a,
    __vue_script__$4a,
    __vue_scope_id__$4a,
    __vue_is_functional_template__$4a,
    __vue_module_identifier__$4a,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$49 = {
  mixins: [singleGroupMixin],
  name: 'imageTextThirdRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$49 = script$49;
/* template */
var __vue_render__$49 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-third-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c(
                "div",
                {
                  staticStyle: {
                    display: "flex",
                    "flex-wrap": "nowrap",
                    "justify-content": "space-between",
                    "align-items": "center",
                  },
                },
                [
                  _c(
                    "div",
                    { staticStyle: { width: "48%" } },
                    [
                      _c("xml-image-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                          "block-data-name": _vm.imageBlockDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "image-container",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                        },
                        on: { blockclick: _vm.handleBlockClick },
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticStyle: { width: "48%" } },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$49 = [];
__vue_render__$49._withStripped = true;

  /* style */
  const __vue_inject_styles__$49 = undefined;
  /* scoped */
  const __vue_scope_id__$49 = "data-v-7d2599be";
  /* module identifier */
  const __vue_module_identifier__$49 = undefined;
  /* functional template */
  const __vue_is_functional_template__$49 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$49 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$49, staticRenderFns: __vue_staticRenderFns__$49 },
    __vue_inject_styles__$49,
    __vue_script__$49,
    __vue_scope_id__$49,
    __vue_is_functional_template__$49,
    __vue_module_identifier__$49,
    false,
    undefined,
    undefined,
    undefined
  );

const ImageTextComponents = [__vue_component__$4g, __vue_component__$4f, __vue_component__$4e, __vue_component__$4d, __vue_component__$4c, __vue_component__$4b, __vue_component__$4a, __vue_component__$49];
var ImageText = {
  // install,
  ...ImageTextComponents
};

//
var script$48 = {
  mixins: [singleGroupMixin],
  name: 'ParagrapheighthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$48 = script$48;
/* template */
var __vue_render__$48 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-eighth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                ],
                1
              ),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$48 = [];
__vue_render__$48._withStripped = true;

  /* style */
  const __vue_inject_styles__$48 = undefined;
  /* scoped */
  const __vue_scope_id__$48 = "data-v-e331edcc";
  /* module identifier */
  const __vue_module_identifier__$48 = undefined;
  /* functional template */
  const __vue_is_functional_template__$48 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$48 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$48, staticRenderFns: __vue_staticRenderFns__$48 },
    __vue_inject_styles__$48,
    __vue_script__$48,
    __vue_scope_id__$48,
    __vue_is_functional_template__$48,
    __vue_module_identifier__$48,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$47 = {
  mixins: [singleGroupMixin],
  name: 'paragraphFirstRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$47 = script$47;
/* template */
var __vue_render__$47 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-first-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "rotate-container" }, [
            _c(
              "div",
              { staticClass: "rotate-container-inner" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
          _vm._v(" "),
          _c("xml-text-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.bodyTextDataName],
              "block-data-name": _vm.bodyTextDataName,
              "extend-params": _vm.extendParams,
              "current-rule": _vm.currentRule,
              "rule-index": _vm.ruleIndex,
              "container-class": "text-body-container",
              "editor-class": "editor",
              mode: _vm.mode,
              "page-type": _vm.pageType,
              resourceBasisPath: _vm.resourceBasisPath,
              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
            },
            on: {
              blockclick: _vm.handleBlockClick,
              groupDataChange: _vm.groupDataChange,
            },
            scopedSlots: _vm._u(
              [
                _vm._l(_vm.$scopedSlots, function (index, name) {
                  return {
                    key: name,
                    fn: function (deliver) {
                      return [_vm._t(name, null, null, deliver)]
                    },
                  }
                }),
              ],
              null,
              true
            ),
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$47 = [];
__vue_render__$47._withStripped = true;

  /* style */
  const __vue_inject_styles__$47 = undefined;
  /* scoped */
  const __vue_scope_id__$47 = "data-v-38bb728f";
  /* module identifier */
  const __vue_module_identifier__$47 = undefined;
  /* functional template */
  const __vue_is_functional_template__$47 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$47 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$47, staticRenderFns: __vue_staticRenderFns__$47 },
    __vue_inject_styles__$47,
    __vue_script__$47,
    __vue_scope_id__$47,
    __vue_is_functional_template__$47,
    __vue_module_identifier__$47,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$46 = {
  mixins: [singleGroupMixin],
  name: 'paragraphIsoscelesRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$46 = script$46;
/* template */
var __vue_render__$46 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-isosceles-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "circle" }),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "shape" }, [
            _c("div", { staticClass: "triangle" }),
            _vm._v(" "),
            _c("div", { staticClass: "triangle2" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$46 = [];
__vue_render__$46._withStripped = true;

  /* style */
  const __vue_inject_styles__$46 = undefined;
  /* scoped */
  const __vue_scope_id__$46 = "data-v-ed58270a";
  /* module identifier */
  const __vue_module_identifier__$46 = undefined;
  /* functional template */
  const __vue_is_functional_template__$46 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$46 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$46, staticRenderFns: __vue_staticRenderFns__$46 },
    __vue_inject_styles__$46,
    __vue_script__$46,
    __vue_scope_id__$46,
    __vue_is_functional_template__$46,
    __vue_module_identifier__$46,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$45 = {
  mixins: [singleGroupMixin],
  name: 'paragraphninthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。

      bodyTextDataName2: 'bodyTextData2',
      titleTextDataName2: 'titleTextData2',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$45 = script$45;
/* template */
var __vue_render__$45 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-ninth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                      "block-data-name": _vm.bodyTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle2" }),
                ],
                1
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                  "block-data-name": _vm.titleTextDataName2,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$45 = [];
__vue_render__$45._withStripped = true;

  /* style */
  const __vue_inject_styles__$45 = undefined;
  /* scoped */
  const __vue_scope_id__$45 = "data-v-2fa14092";
  /* module identifier */
  const __vue_module_identifier__$45 = undefined;
  /* functional template */
  const __vue_is_functional_template__$45 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$45 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$45, staticRenderFns: __vue_staticRenderFns__$45 },
    __vue_inject_styles__$45,
    __vue_script__$45,
    __vue_scope_id__$45,
    __vue_is_functional_template__$45,
    __vue_module_identifier__$45,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$44 = {
  mixins: [singleGroupMixin],
  name: 'paragraphQuadrangleRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$44 = script$44;
/* template */
var __vue_render__$44 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-quadrangle-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin1" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin2" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin3" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin4" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin5" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin6" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin7" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin8" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$44 = [];
__vue_render__$44._withStripped = true;

  /* style */
  const __vue_inject_styles__$44 = undefined;
  /* scoped */
  const __vue_scope_id__$44 = "data-v-632dc964";
  /* module identifier */
  const __vue_module_identifier__$44 = undefined;
  /* functional template */
  const __vue_is_functional_template__$44 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$44 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$44, staticRenderFns: __vue_staticRenderFns__$44 },
    __vue_inject_styles__$44,
    __vue_script__$44,
    __vue_scope_id__$44,
    __vue_is_functional_template__$44,
    __vue_module_identifier__$44,
    false,
    undefined,
    undefined,
    undefined
  );

var Left$1 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA0AAAAkCAYAAABSSLCCAAAAAXNSR0IArs4c6QAAARVJREFUSEvtlbtOw0AQRe9dFAsTbEtIPCr+wTX0ES8FgahIfoeKBhMeSiq+jD/YQOOV7MSD1thSgpGXksLbn5md0ehcDhL90ffgLZZAIXA+ITJevujs/jrohZsKS7EUXWDO4bNOZ+PI391WsAydDAwvnnQ6G0X+XlhBq31+KUBYyHa6jfz9GvpTpw6yu+22V11Yt4huEetS+/8XUcsy+GFY67/a7SsuLGV59fpt2J2+aji8VLu1+7pADW+mOr0bBn7oKxRVALRKljAcJDrb8tCzFevKjtgwPJ3M3/OFHBYiqkwZmxwtlNiZTh710YbiAyGxAM3BmgUMAeH55DMGZQpIDAcoQF79RHiWzI+p8AbioG0mAbIvFBoA7XBwjR8AAAAASUVORK5CYII=";

var Right = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAkCAYAAACJ8xqgAAAAAXNSR0IArs4c6QAAAf9JREFUSEvtljtv01AcR8+182jUJg4gxMTCgPgI7IipDKxQqqA27YQCAx8Bda2EFPEYiICoCo+9dEAsSIyRmAEhnqmSJo5bOyGx70XXBBQ2O80E8Wrp3P/j6v6OWCy3baFIEfFTQMoEw4CDHzSAwk7pyGsQ+hfiezcYAMmIPECRMgVtT3Lz+b5subK+kEms71yz6hoqlFIekIkKVAqEgF0nYK3q0BsiFaqeSJghNASqqEAFGqjb/dDy2XjhUjibofKmL981/fqcIa5PXOH7ps/Gtsv9KxafOz63X3ny7dfg08TAb3ZA6anDw0Ke+bTg415A6YkzjA/UmwQ6nmS50mVrxSKXMWjuS1Yfd3vxgaOlaOBSxaa2mic3Z9BwJMV/FDg2w+m0PAMe/trMZjib4QTP19SvzX/4Ho63/MCmVpxiBCxXbLZW8mFI/ZUpkYI+NBeQSmEagtaBpFjt8uiqdbiQ+q0sOjZvPHO4ezlHdjz1Gk7goWK4DYqkKfjSkWy+dClfyjGfEmHL69VuT1y8044lS7pC09BBr8hnRLgUfcAf4LnNPU9ElaUxRev0JaeOJagVLQwh2HVG5nChHL9CrSLeQLKQNri3ZHE8a9BxJWu65cVyeyBiCedo2/LXxk+fSHL+TIqTRw1ubbtDDbQF0ZV4XEwDqej7YAjIpgU9n8FPuCBZn/dscCMAAAAASUVORK5CYII=";

//
var script$43 = {
  mixins: [singleGroupMixin],
  name: 'paragraphSecondRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {},
      Left: Left$1,
      Right
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$43 = script$43;
/* template */
var __vue_render__$43 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-second-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-second-title" },
            [
              _c("el-image", {
                staticClass: "left-image",
                attrs: { src: _vm.Left },
              }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "right-image",
                attrs: { src: _vm.Right },
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$43 = [];
__vue_render__$43._withStripped = true;

  /* style */
  const __vue_inject_styles__$43 = undefined;
  /* scoped */
  const __vue_scope_id__$43 = "data-v-31109da6";
  /* module identifier */
  const __vue_module_identifier__$43 = undefined;
  /* functional template */
  const __vue_is_functional_template__$43 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$43 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$43, staticRenderFns: __vue_staticRenderFns__$43 },
    __vue_inject_styles__$43,
    __vue_script__$43,
    __vue_scope_id__$43,
    __vue_is_functional_template__$43,
    __vue_module_identifier__$43,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$42 = {
  mixins: [singleGroupMixin],
  name: 'paragraphSeventhRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$42 = script$42;
/* template */
var __vue_render__$42 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-seventh-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-suptitle-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.numberTextDataName],
                  "block-data-name": _vm.numberTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container-left",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                    "block-data-name": _vm.titleTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                    "block-data-name": _vm.titleTextDataName3,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                    "block-data-name": _vm.bodyTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$42 = [];
__vue_render__$42._withStripped = true;

  /* style */
  const __vue_inject_styles__$42 = undefined;
  /* scoped */
  const __vue_scope_id__$42 = "data-v-1e2ce5a2";
  /* module identifier */
  const __vue_module_identifier__$42 = undefined;
  /* functional template */
  const __vue_is_functional_template__$42 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$42 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$42, staticRenderFns: __vue_staticRenderFns__$42 },
    __vue_inject_styles__$42,
    __vue_script__$42,
    __vue_scope_id__$42,
    __vue_is_functional_template__$42,
    __vue_module_identifier__$42,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$41 = {
  mixins: [singleGroupMixin],
  name: 'paragraphtenthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$41 = script$41;
/* template */
var __vue_render__$41 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-tenth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-suptitle-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.numberTextDataName],
                  "block-data-name": _vm.numberTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container-left",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                    "block-data-name": _vm.titleTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$41 = [];
__vue_render__$41._withStripped = true;

  /* style */
  const __vue_inject_styles__$41 = undefined;
  /* scoped */
  const __vue_scope_id__$41 = "data-v-4ec5f0ac";
  /* module identifier */
  const __vue_module_identifier__$41 = undefined;
  /* functional template */
  const __vue_is_functional_template__$41 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$41 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$41, staticRenderFns: __vue_staticRenderFns__$41 },
    __vue_inject_styles__$41,
    __vue_script__$41,
    __vue_scope_id__$41,
    __vue_is_functional_template__$41,
    __vue_module_identifier__$41,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$40 = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirdRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$40 = script$40;
/* template */
var __vue_render__$40 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-third-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$40 = [];
__vue_render__$40._withStripped = true;

  /* style */
  const __vue_inject_styles__$40 = undefined;
  /* scoped */
  const __vue_scope_id__$40 = "data-v-157d4fe0";
  /* module identifier */
  const __vue_module_identifier__$40 = undefined;
  /* functional template */
  const __vue_is_functional_template__$40 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$40 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$40, staticRenderFns: __vue_staticRenderFns__$40 },
    __vue_inject_styles__$40,
    __vue_script__$40,
    __vue_scope_id__$40,
    __vue_is_functional_template__$40,
    __vue_module_identifier__$40,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3$ = {
  mixins: [singleGroupMixin],
  name: 'paragraphWaveRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3$ = script$3$;
/* template */
var __vue_render__$3$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-wave-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                ],
                1
              ),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                      "block-data-name": _vm.bodyTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle2" }),
                ],
                1
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                  "block-data-name": _vm.titleTextDataName2,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                  "block-data-name": _vm.titleTextDataName3,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName3],
                      "block-data-name": _vm.bodyTextDataName3,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                ],
                1
              ),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3$ = [];
__vue_render__$3$._withStripped = true;

  /* style */
  const __vue_inject_styles__$3$ = undefined;
  /* scoped */
  const __vue_scope_id__$3$ = "data-v-348561bf";
  /* module identifier */
  const __vue_module_identifier__$3$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$3$ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3$, staticRenderFns: __vue_staticRenderFns__$3$ },
    __vue_inject_styles__$3$,
    __vue_script__$3$,
    __vue_scope_id__$3$,
    __vue_is_functional_template__$3$,
    __vue_module_identifier__$3$,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3_ = {
  mixins: [singleGroupMixin],
  name: 'paragraphElevenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3_ = script$3_;
/* template */
var __vue_render__$3_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-eleven-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text" }, [
            _c("div", { staticClass: "boder-top" }, [
              _c("div", { staticClass: "boder-top-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-top-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-bottom" }, [
              _c("div", { staticClass: "boder-bottom-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-bottom-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-left" }, [
              _c("div", { staticClass: "boder-left-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-left-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-right" }, [
              _c("div", { staticClass: "boder-right-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-right-line2" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3_ = [];
__vue_render__$3_._withStripped = true;

  /* style */
  const __vue_inject_styles__$3_ = undefined;
  /* scoped */
  const __vue_scope_id__$3_ = "data-v-11049dff";
  /* module identifier */
  const __vue_module_identifier__$3_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$3_ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3_, staticRenderFns: __vue_staticRenderFns__$3_ },
    __vue_inject_styles__$3_,
    __vue_script__$3_,
    __vue_scope_id__$3_,
    __vue_is_functional_template__$3_,
    __vue_module_identifier__$3_,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3Z = {
  mixins: [singleGroupMixin],
  name: 'paragraphTwelveRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_paragraph_tweleve_bg_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAxCAYAAACs0jsDAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6QTQ0NEJDQjZBQ0Q5MTFFREI5RDRDMjIyNEY3MEMwRjUiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6QTQ0NEJDQjdBQ0Q5MTFFREI5RDRDMjIyNEY3MEMwRjUiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDpBNDQ0QkNCNEFDRDkxMUVEQjlENEMyMjI0RjcwQzBGNSIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDpBNDQ0QkNCNUFDRDkxMUVEQjlENEMyMjI0RjcwQzBGNSIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Pqfr0oYAAAHtSURBVHja7JlLKERRGMdnNPIo71JSFiIrC1FKbAYpVkpmKUrKQs1CsVFWNpqRhc3IQhYspBQWYmPJRh6xmw0pzGxclOf/q6vG15lJc+fMfKNz6tftfPfW/XXu+c7rur2BR1cmy4G//Fe9Mxj5YvfddM1xZUkxokbUiBpRI2pEjej/FvVIkMCKiTxqQYNU0XZIDuDqBfUgX5ooyU2ADqmfvhnMgW7JfXQSzIKCBM+8glsQBRY4TKdoEQgBX5z7JLUHdsAxiWL7YaW7RSvBJiVNHMElsAyxcCY/fQXYBq2Ke1tgGlzzzV26RWmYWVdIfoApMC8lmRZBF4tRvxuyu4KImWkYjCqy2WcnjIi5vg4EFPExJ5I6RBdAKYvRAL8qaVHSD/pY7AjMqB7+a7anukXz7FkntryAcfAuaZk3CBpZLAjOk21BHS2aC/wsdpPMWKlblNaSTYpxNCpNdITV78GKk8TRIVoFelhsAzxI29yRZElM/ROsSdyF9rL6GTiRJkoL4jYW27VXSKJEadysZrF9iQcQLYpsP5UoysfOCxCRKMpPNbS1phPRYlDDYlcJjmwyJkobtzIWC0tsUTpEKIyp02/BO52iHvslToulM5FSuXB+thEvSnP8WzaIXoInrX3053+405KKIShR+RZgACQqX9Suk5GsAAAAAElFTkSuQmCC";

var __$_require_static_images_leaflet_resource_enter_clover_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAASCAYAAAC9+TVUAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6Mjg4QTIyRUVBQ0QzMTFFREI2MTJFMUY2MkYzNTBCNkIiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6Mjg4QTIyRUZBQ0QzMTFFREI2MTJFMUY2MkYzNTBCNkIiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDoyODhBMjJFQ0FDRDMxMUVEQjYxMkUxRjYyRjM1MEI2QiIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDoyODhBMjJFREFDRDMxMUVEQjYxMkUxRjYyRjM1MEI2QiIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PnzdDxEAAAGQSURBVHjajJJNKIRRFIa/GZM0SX5mY2GDkI2aEiXUzIYSyUIpGwthNZIaspAVkslCVrKxkZ2lECkyopGNGVmSyM9uhDGeq6O+7txpvree5s75zn3vveccVzqdtpSCkVdLUxlUQiGk4BkS++OlKT3RbWVKxabgGqJwAEcQg1MO8zsx8cEslGvxfGiEUX2Dx/CMd7iSDSZFndzkE2ayGJzBZsZNwAVz0AY7sAq7EIZ5W+49DFLYJLdvYD0BFTCtTJpsJyujHpUMC1AsZgnox+AWgyHWESiSPYtuMbGrVW7SDNUSO8QghkGI9brNQKneI/OgqwaO5blKwxhUqXEy5Ba4pSYmebT/wSx5f915McRvYEk6pfQILRAy5H4ok3NDG7tgUrr1Pztx6rLC7xgkbflxZXIip15IlwJwBwPQKYl1sEddfBitSfG3ZfDCrsCy6TWWXwrr1eLqZr0Y/eSa2DwZMq/hWzf0ORl71fJ2K7s6nJi8wQZ8G749wFauWVD6ghFQBayFEokpg0vq8aRv+BVgAPdmZWgTmMj6AAAAAElFTkSuQmCC";

/* script */
const __vue_script__$3Z = script$3Z;
var __vue_render__$3Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-twelve-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "left-top-img" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_paragraph_tweleve_bg_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "left-top-clover" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_clover_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "right-bottom-clover" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_clover_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "right-bottom-img" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_paragraph_tweleve_bg_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3Z = [];
__vue_render__$3Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$3Z = undefined;
  /* scoped */
  const __vue_scope_id__$3Z = "data-v-2bc30923";
  /* module identifier */
  const __vue_module_identifier__$3Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$3Z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3Z, staticRenderFns: __vue_staticRenderFns__$3Z },
    __vue_inject_styles__$3Z,
    __vue_script__$3Z,
    __vue_scope_id__$3Z,
    __vue_is_functional_template__$3Z,
    __vue_module_identifier__$3Z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3Y = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3Y = script$3Y;
/* template */
var __vue_render__$3Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-thirteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "text-border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-left" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-right" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3Y = [];
__vue_render__$3Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$3Y = undefined;
  /* scoped */
  const __vue_scope_id__$3Y = "data-v-51388e80";
  /* module identifier */
  const __vue_module_identifier__$3Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$3Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3Y, staticRenderFns: __vue_staticRenderFns__$3Y },
    __vue_inject_styles__$3Y,
    __vue_script__$3Y,
    __vue_scope_id__$3Y,
    __vue_is_functional_template__$3Y,
    __vue_module_identifier__$3Y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3X = {
  mixins: [singleGroupMixin],
  name: 'paragraphFourteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3X = script$3X;
/* template */
var __vue_render__$3X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-fourteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "right-image" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3X = [];
__vue_render__$3X._withStripped = true;

  /* style */
  const __vue_inject_styles__$3X = undefined;
  /* scoped */
  const __vue_scope_id__$3X = "data-v-7543aefd";
  /* module identifier */
  const __vue_module_identifier__$3X = undefined;
  /* functional template */
  const __vue_is_functional_template__$3X = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3X, staticRenderFns: __vue_staticRenderFns__$3X },
    __vue_inject_styles__$3X,
    __vue_script__$3X,
    __vue_scope_id__$3X,
    __vue_is_functional_template__$3X,
    __vue_module_identifier__$3X,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3W = {
  mixins: [singleGroupMixin],
  name: 'paragraphFifteenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3W = script$3W;
/* template */
var __vue_render__$3W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-fifteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "top" }),
              _vm._v(" "),
              _c("div", { staticClass: "left" }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "right" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3W = [];
__vue_render__$3W._withStripped = true;

  /* style */
  const __vue_inject_styles__$3W = undefined;
  /* scoped */
  const __vue_scope_id__$3W = "data-v-8d09dbfc";
  /* module identifier */
  const __vue_module_identifier__$3W = undefined;
  /* functional template */
  const __vue_is_functional_template__$3W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3W, staticRenderFns: __vue_staticRenderFns__$3W },
    __vue_inject_styles__$3W,
    __vue_script__$3W,
    __vue_scope_id__$3W,
    __vue_is_functional_template__$3W,
    __vue_module_identifier__$3W,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3V = {
  mixins: [singleGroupMixin],
  name: 'paragraphGreentheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3V = script$3V;
/* template */
var __vue_render__$3V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3V = [];
__vue_render__$3V._withStripped = true;

  /* style */
  const __vue_inject_styles__$3V = undefined;
  /* scoped */
  const __vue_scope_id__$3V = "data-v-1345fb7e";
  /* module identifier */
  const __vue_module_identifier__$3V = undefined;
  /* functional template */
  const __vue_is_functional_template__$3V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3V, staticRenderFns: __vue_staticRenderFns__$3V },
    __vue_inject_styles__$3V,
    __vue_script__$3V,
    __vue_scope_id__$3V,
    __vue_is_functional_template__$3V,
    __vue_module_identifier__$3V,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3U = {
  mixins: [singleGroupMixin],
  name: 'paragraphGreentheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3U = script$3U;
/* template */
var __vue_render__$3U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3U = [];
__vue_render__$3U._withStripped = true;

  /* style */
  const __vue_inject_styles__$3U = undefined;
  /* scoped */
  const __vue_scope_id__$3U = "data-v-5a02a2e8";
  /* module identifier */
  const __vue_module_identifier__$3U = undefined;
  /* functional template */
  const __vue_is_functional_template__$3U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3U, staticRenderFns: __vue_staticRenderFns__$3U },
    __vue_inject_styles__$3U,
    __vue_script__$3U,
    __vue_scope_id__$3U,
    __vue_is_functional_template__$3U,
    __vue_module_identifier__$3U,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3T = {
  mixins: [singleGroupMixin],
  name: 'paragraphGreentheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3T = script$3T;
/* template */
var __vue_render__$3T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "body-text-bg1" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg2" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3T = [];
__vue_render__$3T._withStripped = true;

  /* style */
  const __vue_inject_styles__$3T = undefined;
  /* scoped */
  const __vue_scope_id__$3T = "data-v-06e641dd";
  /* module identifier */
  const __vue_module_identifier__$3T = undefined;
  /* functional template */
  const __vue_is_functional_template__$3T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3T, staticRenderFns: __vue_staticRenderFns__$3T },
    __vue_inject_styles__$3T,
    __vue_script__$3T,
    __vue_scope_id__$3T,
    __vue_is_functional_template__$3T,
    __vue_module_identifier__$3T,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3S = {
  mixins: [singleGroupMixin],
  name: 'paragraphGreentheme5Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3S = script$3S;
/* template */
var __vue_render__$3S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "body-text-bg1" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg2" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3S = [];
__vue_render__$3S._withStripped = true;

  /* style */
  const __vue_inject_styles__$3S = undefined;
  /* scoped */
  const __vue_scope_id__$3S = "data-v-486d33bf";
  /* module identifier */
  const __vue_module_identifier__$3S = undefined;
  /* functional template */
  const __vue_is_functional_template__$3S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3S, staticRenderFns: __vue_staticRenderFns__$3S },
    __vue_inject_styles__$3S,
    __vue_script__$3S,
    __vue_scope_id__$3S,
    __vue_is_functional_template__$3S,
    __vue_module_identifier__$3S,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3R = {
  mixins: [singleGroupMixin],
  name: 'paragraphYellowtheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3R = script$3R;
/* template */
var __vue_render__$3R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "border-top" }, [
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "border-bottom" }, [
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3R = [];
__vue_render__$3R._withStripped = true;

  /* style */
  const __vue_inject_styles__$3R = undefined;
  /* scoped */
  const __vue_scope_id__$3R = "data-v-6b24580c";
  /* module identifier */
  const __vue_module_identifier__$3R = undefined;
  /* functional template */
  const __vue_is_functional_template__$3R = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3R, staticRenderFns: __vue_staticRenderFns__$3R },
    __vue_inject_styles__$3R,
    __vue_script__$3R,
    __vue_scope_id__$3R,
    __vue_is_functional_template__$3R,
    __vue_module_identifier__$3R,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3Q = {
  mixins: [singleGroupMixin],
  name: 'paragraphYellowtheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3Q = script$3Q;
/* template */
var __vue_render__$3Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "block1" }),
            _vm._v(" "),
            _c("div", { staticClass: "block2" }),
            _vm._v(" "),
            _c("div", { staticClass: "block3" }),
            _vm._v(" "),
            _c("div", { staticClass: "block4" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3Q = [];
__vue_render__$3Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$3Q = undefined;
  /* scoped */
  const __vue_scope_id__$3Q = "data-v-14063573";
  /* module identifier */
  const __vue_module_identifier__$3Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$3Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3Q, staticRenderFns: __vue_staticRenderFns__$3Q },
    __vue_inject_styles__$3Q,
    __vue_script__$3Q,
    __vue_scope_id__$3Q,
    __vue_is_functional_template__$3Q,
    __vue_module_identifier__$3Q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3P = {
  mixins: [singleGroupMixin],
  name: 'paragraphYellowtheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3P = script$3P;
/* template */
var __vue_render__$3P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "right-img" }, [_c("div")]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3P = [];
__vue_render__$3P._withStripped = true;

  /* style */
  const __vue_inject_styles__$3P = undefined;
  /* scoped */
  const __vue_scope_id__$3P = "data-v-4cb210ac";
  /* module identifier */
  const __vue_module_identifier__$3P = undefined;
  /* functional template */
  const __vue_is_functional_template__$3P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3P, staticRenderFns: __vue_staticRenderFns__$3P },
    __vue_inject_styles__$3P,
    __vue_script__$3P,
    __vue_scope_id__$3P,
    __vue_is_functional_template__$3P,
    __vue_module_identifier__$3P,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3O = {
  mixins: [singleGroupMixin],
  name: 'paragraphYellowtheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3O = script$3O;
/* template */
var __vue_render__$3O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "block1" }),
            _vm._v(" "),
            _c("div", { staticClass: "block2" }),
            _vm._v(" "),
            _c("div", { staticClass: "block3" }),
            _vm._v(" "),
            _c("div", { staticClass: "block4" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3O = [];
__vue_render__$3O._withStripped = true;

  /* style */
  const __vue_inject_styles__$3O = undefined;
  /* scoped */
  const __vue_scope_id__$3O = "data-v-5389fb1e";
  /* module identifier */
  const __vue_module_identifier__$3O = undefined;
  /* functional template */
  const __vue_is_functional_template__$3O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3O, staticRenderFns: __vue_staticRenderFns__$3O },
    __vue_inject_styles__$3O,
    __vue_script__$3O,
    __vue_scope_id__$3O,
    __vue_is_functional_template__$3O,
    __vue_module_identifier__$3O,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3N = {
  mixins: [singleGroupMixin],
  name: 'paragraphYellowtheme5Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3N = script$3N;
/* template */
var __vue_render__$3N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "left-img" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-img" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3N = [];
__vue_render__$3N._withStripped = true;

  /* style */
  const __vue_inject_styles__$3N = undefined;
  /* scoped */
  const __vue_scope_id__$3N = "data-v-63289eae";
  /* module identifier */
  const __vue_module_identifier__$3N = undefined;
  /* functional template */
  const __vue_is_functional_template__$3N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3N, staticRenderFns: __vue_staticRenderFns__$3N },
    __vue_inject_styles__$3N,
    __vue_script__$3N,
    __vue_scope_id__$3N,
    __vue_is_functional_template__$3N,
    __vue_module_identifier__$3N,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3M = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme1Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3M = script$3M;
/* template */
var __vue_render__$3M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3M = [];
__vue_render__$3M._withStripped = true;

  /* style */
  const __vue_inject_styles__$3M = undefined;
  /* scoped */
  const __vue_scope_id__$3M = "data-v-07399a7f";
  /* module identifier */
  const __vue_module_identifier__$3M = undefined;
  /* functional template */
  const __vue_is_functional_template__$3M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3M, staticRenderFns: __vue_staticRenderFns__$3M },
    __vue_inject_styles__$3M,
    __vue_script__$3M,
    __vue_scope_id__$3M,
    __vue_is_functional_template__$3M,
    __vue_module_identifier__$3M,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3L = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme2Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3L = script$3L;
/* template */
var __vue_render__$3L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "left-line" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-line" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3L = [];
__vue_render__$3L._withStripped = true;

  /* style */
  const __vue_inject_styles__$3L = undefined;
  /* scoped */
  const __vue_scope_id__$3L = "data-v-07ddc59b";
  /* module identifier */
  const __vue_module_identifier__$3L = undefined;
  /* functional template */
  const __vue_is_functional_template__$3L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3L, staticRenderFns: __vue_staticRenderFns__$3L },
    __vue_inject_styles__$3L,
    __vue_script__$3L,
    __vue_scope_id__$3L,
    __vue_is_functional_template__$3L,
    __vue_module_identifier__$3L,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3K = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme3Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3K = script$3K;
/* template */
var __vue_render__$3K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-line" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "bottom-line" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3K = [];
__vue_render__$3K._withStripped = true;

  /* style */
  const __vue_inject_styles__$3K = undefined;
  /* scoped */
  const __vue_scope_id__$3K = "data-v-4cd23c3b";
  /* module identifier */
  const __vue_module_identifier__$3K = undefined;
  /* functional template */
  const __vue_is_functional_template__$3K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3K, staticRenderFns: __vue_staticRenderFns__$3K },
    __vue_inject_styles__$3K,
    __vue_script__$3K,
    __vue_scope_id__$3K,
    __vue_is_functional_template__$3K,
    __vue_module_identifier__$3K,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3J = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme4Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3J = script$3J;
/* template */
var __vue_render__$3J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-title" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3J = [];
__vue_render__$3J._withStripped = true;

  /* style */
  const __vue_inject_styles__$3J = undefined;
  /* scoped */
  const __vue_scope_id__$3J = "data-v-5b18c8fa";
  /* module identifier */
  const __vue_module_identifier__$3J = undefined;
  /* functional template */
  const __vue_is_functional_template__$3J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3J, staticRenderFns: __vue_staticRenderFns__$3J },
    __vue_inject_styles__$3J,
    __vue_script__$3J,
    __vue_scope_id__$3J,
    __vue_is_functional_template__$3J,
    __vue_module_identifier__$3J,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3I = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme5Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3I = script$3I;
/* template */
var __vue_render__$3I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-box" }, [
            _c(
              "div",
              { staticClass: "body-text-bg" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3I = [];
__vue_render__$3I._withStripped = true;

  /* style */
  const __vue_inject_styles__$3I = undefined;
  /* scoped */
  const __vue_scope_id__$3I = "data-v-ed428dc0";
  /* module identifier */
  const __vue_module_identifier__$3I = undefined;
  /* functional template */
  const __vue_is_functional_template__$3I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3I, staticRenderFns: __vue_staticRenderFns__$3I },
    __vue_inject_styles__$3I,
    __vue_script__$3I,
    __vue_scope_id__$3I,
    __vue_is_functional_template__$3I,
    __vue_module_identifier__$3I,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3H = {
  mixins: [singleGroupMixin],
  name: 'paragraphRedtheme6Render',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3H = script$3H;
/* template */
var __vue_render__$3H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme6-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3H = [];
__vue_render__$3H._withStripped = true;

  /* style */
  const __vue_inject_styles__$3H = undefined;
  /* scoped */
  const __vue_scope_id__$3H = "data-v-3c26e20c";
  /* module identifier */
  const __vue_module_identifier__$3H = undefined;
  /* functional template */
  const __vue_is_functional_template__$3H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3H, staticRenderFns: __vue_staticRenderFns__$3H },
    __vue_inject_styles__$3H,
    __vue_script__$3H,
    __vue_scope_id__$3H,
    __vue_is_functional_template__$3H,
    __vue_module_identifier__$3H,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3G = {
  mixins: [singleGroupMixin],
  name: 'paragraphSixteenRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3G = script$3G;
/* template */
var __vue_render__$3G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-sixteen-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                    "block-data-name": _vm.titleTextDataName3,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                    "block-data-name": _vm.bodyTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3G = [];
__vue_render__$3G._withStripped = true;

  /* style */
  const __vue_inject_styles__$3G = undefined;
  /* scoped */
  const __vue_scope_id__$3G = "data-v-73ff8571";
  /* module identifier */
  const __vue_module_identifier__$3G = undefined;
  /* functional template */
  const __vue_is_functional_template__$3G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3G, staticRenderFns: __vue_staticRenderFns__$3G },
    __vue_inject_styles__$3G,
    __vue_script__$3G,
    __vue_scope_id__$3G,
    __vue_is_functional_template__$3G,
    __vue_module_identifier__$3G,
    false,
    undefined,
    undefined,
    undefined
  );

var Left = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAbCAYAAACJISRoAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6OEZERjBFNTUxMzJCMTFFRThFM0M5RDlDNDZFMDJEQjAiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6OEZERjBFNTYxMzJCMTFFRThFM0M5RDlDNDZFMDJEQjAiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo4RkRGMEU1MzEzMkIxMUVFOEUzQzlEOUM0NkUwMkRCMCIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo4RkRGMEU1NDEzMkIxMUVFOEUzQzlEOUM0NkUwMkRCMCIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Plhysp0AAAIfSURBVHjatJU7T1VBEMd3D/deeXmNhbw1hIRPYImaKEhpY6GJUFD7AWjUCKGgNSaQEBKMjYWJMXY0JBCgsbEyUBiNhdKY8BS4Isf/mP8Jw3oeey7XSX7Ze2d2d3ZnZufYxbG2S8aYXnBk/KQObIGP8ufGk+9/lUvj7Q8xPAcWOqsXFMAdMGvyySroc3SWfHEnF2gQ+QTWQJCwcQia5fBqjWs3cQcuKOMLMJFxgx4exua5duA4zJKixxyb5sRHfnL8HWM74HgYF6646hkBUnXHSi+/W/n7MhhlVRlW5nXaBqA7VnvbJCePwZWUG4ltMsE2SDLzIFevgEdgk2GVEHWCp+CzciI5+AVug/vgHXgLSllOAi6cAntK30Un38CM8xgtnSxDN+ebeFl03tE1q5C6Us/xXFp1RVVRYZLl/ew483c9qsukVddVcI9voUzbMHMSSQfHFoYmqi4T02ZOOYke2F2iZTphnTTUV74PVpx84GZHKt4PQBN4ycRbhu8iGAIb4LXzwkM6WPknuWj1cadZZ7m2qTxEj/ArN7qmq8u3d0VSot6qaoqkKaW6cjmpqM6879j2TRUiObmpelTImzTyALfAD95K5nQ73w5vJwsp9jcJ+oa8Tgw/me8Za6GfG80zRIHTjdfyOLFheHJz9bDW2adEsZ22QbXVVVT6BlMDCTI+n/Z/Oam5JDkpqzI+s8R9tKSNPwMXYlp9VfJHgAEAjWxw8vlaPHgAAAAASUVORK5CYII=";

//
var script$3F = {
  mixins: [singleGroupMixin],
  name: 'paragraphSanQintheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {},
      Left: Left
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3F = script$3F;
/* template */
var __vue_render__$3F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-SanQintheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-SanQintheme1-title" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "left-top-image",
                attrs: { src: _vm.Left },
              }),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "left-bottom-image",
                attrs: { src: _vm.Left },
              }),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "right-top-image",
                attrs: { src: _vm.Left },
              }),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "right-bottom-image",
                attrs: { src: _vm.Left },
              }),
              _vm._v(" "),
              _c("div", { staticClass: "top-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "right-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "left-line" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3F = [];
__vue_render__$3F._withStripped = true;

  /* style */
  const __vue_inject_styles__$3F = undefined;
  /* scoped */
  const __vue_scope_id__$3F = "data-v-7b19e590";
  /* module identifier */
  const __vue_module_identifier__$3F = undefined;
  /* functional template */
  const __vue_is_functional_template__$3F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3F, staticRenderFns: __vue_staticRenderFns__$3F },
    __vue_inject_styles__$3F,
    __vue_script__$3F,
    __vue_scope_id__$3F,
    __vue_is_functional_template__$3F,
    __vue_module_identifier__$3F,
    false,
    undefined,
    undefined,
    undefined
  );

var Icon$1 = "data:image/png;base64,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";

//
var script$3E = {
  mixins: [singleGroupMixin],
  name: 'paragraphSanQintheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {},
      Icon: Icon$1
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3E = script$3E;
/* template */
var __vue_render__$3E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-SanQintheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-SanQintheme2-title" },
            [
              _c("el-image", {
                staticClass: "left-image",
                attrs: { src: _vm.Icon },
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "text-container-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("el-image", {
                staticClass: "right-image",
                attrs: { src: _vm.Icon },
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3E = [];
__vue_render__$3E._withStripped = true;

  /* style */
  const __vue_inject_styles__$3E = undefined;
  /* scoped */
  const __vue_scope_id__$3E = "data-v-6d05cc82";
  /* module identifier */
  const __vue_module_identifier__$3E = undefined;
  /* functional template */
  const __vue_is_functional_template__$3E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3E, staticRenderFns: __vue_staticRenderFns__$3E },
    __vue_inject_styles__$3E,
    __vue_script__$3E,
    __vue_scope_id__$3E,
    __vue_is_functional_template__$3E,
    __vue_module_identifier__$3E,
    false,
    undefined,
    undefined,
    undefined
  );

var Icon = "data:image/png;base64,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";

//
var script$3D = {
  mixins: [singleGroupMixin],
  name: 'paragraphSanQintheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {},
      Icon: Icon
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3D = script$3D;
/* template */
var __vue_render__$3D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-SanQintheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "top-image" },
            [_c("el-image", { attrs: { src: _vm.Icon } })],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3D = [];
__vue_render__$3D._withStripped = true;

  /* style */
  const __vue_inject_styles__$3D = undefined;
  /* scoped */
  const __vue_scope_id__$3D = "data-v-2dc78421";
  /* module identifier */
  const __vue_module_identifier__$3D = undefined;
  /* functional template */
  const __vue_is_functional_template__$3D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3D, staticRenderFns: __vue_staticRenderFns__$3D },
    __vue_inject_styles__$3D,
    __vue_script__$3D,
    __vue_scope_id__$3D,
    __vue_is_functional_template__$3D,
    __vue_module_identifier__$3D,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3C = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtyfiveRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3C = script$3C;
/* template */
var __vue_render__$3C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtyfive-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c(
                "div",
                { staticClass: "left-image" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "show-title": false,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3C = [];
__vue_render__$3C._withStripped = true;

  /* style */
  const __vue_inject_styles__$3C = undefined;
  /* scoped */
  const __vue_scope_id__$3C = "data-v-4d160be8";
  /* module identifier */
  const __vue_module_identifier__$3C = undefined;
  /* functional template */
  const __vue_is_functional_template__$3C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3C, staticRenderFns: __vue_staticRenderFns__$3C },
    __vue_inject_styles__$3C,
    __vue_script__$3C,
    __vue_scope_id__$3C,
    __vue_is_functional_template__$3C,
    __vue_module_identifier__$3C,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3B = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtysixRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3B = script$3B;
/* template */
var __vue_render__$3B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtysix-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c(
                "div",
                { staticClass: "left-image" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "show-title": false,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3B = [];
__vue_render__$3B._withStripped = true;

  /* style */
  const __vue_inject_styles__$3B = undefined;
  /* scoped */
  const __vue_scope_id__$3B = "data-v-421a25c2";
  /* module identifier */
  const __vue_module_identifier__$3B = undefined;
  /* functional template */
  const __vue_is_functional_template__$3B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3B, staticRenderFns: __vue_staticRenderFns__$3B },
    __vue_inject_styles__$3B,
    __vue_script__$3B,
    __vue_scope_id__$3B,
    __vue_is_functional_template__$3B,
    __vue_module_identifier__$3B,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3A = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtysevenRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon5_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$3A = script$3A;
var __vue_render__$3A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtyseven-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "sub-title" },
            [
              _c("div", { staticClass: "icon-box" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_title_icon5_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "body-text",
              class: !_vm.isFold ? "body-text-fold" : "",
            },
            [
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [
                    _vm._v(_vm._s(!_vm.isFold ? "查看更多" : "折叠")),
                  ]),
                  _vm._v(" "),
                  !_vm.isFold
                    ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                    : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
                ]
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3A = [];
__vue_render__$3A._withStripped = true;

  /* style */
  const __vue_inject_styles__$3A = undefined;
  /* scoped */
  const __vue_scope_id__$3A = "data-v-579a8cb9";
  /* module identifier */
  const __vue_module_identifier__$3A = undefined;
  /* functional template */
  const __vue_is_functional_template__$3A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3A, staticRenderFns: __vue_staticRenderFns__$3A },
    __vue_inject_styles__$3A,
    __vue_script__$3A,
    __vue_scope_id__$3A,
    __vue_is_functional_template__$3A,
    __vue_module_identifier__$3A,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3z = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtyseven2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon6_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$3z = script$3z;
var __vue_render__$3z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtyseven-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "sub-title" },
            [
              _c("div", { staticClass: "icon-box" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_title_icon6_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "body-text",
              class: !_vm.isFold ? "body-text-fold" : "",
            },
            [
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [
                    _vm._v(_vm._s(!_vm.isFold ? "查看更多" : "折叠")),
                  ]),
                  _vm._v(" "),
                  !_vm.isFold
                    ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                    : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
                ]
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3z = [];
__vue_render__$3z._withStripped = true;

  /* style */
  const __vue_inject_styles__$3z = undefined;
  /* scoped */
  const __vue_scope_id__$3z = "data-v-30ba95a0";
  /* module identifier */
  const __vue_module_identifier__$3z = undefined;
  /* functional template */
  const __vue_is_functional_template__$3z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3z, staticRenderFns: __vue_staticRenderFns__$3z },
    __vue_inject_styles__$3z,
    __vue_script__$3z,
    __vue_scope_id__$3z,
    __vue_is_functional_template__$3z,
    __vue_module_identifier__$3z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3y = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtyseven3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_title_icon7_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAYAAADFniADAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6NEQ5MkI2QTIzOTExMTFFRjhGNTJGMjU5M0IyNjYyN0MiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6NEQ5MkI2QTMzOTExMTFFRjhGNTJGMjU5M0IyNjYyN0MiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo0RDkyQjZBMDM5MTExMUVGOEY1MkYyNTkzQjI2NjI3QyIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo0RDkyQjZBMTM5MTExMUVGOEY1MkYyNTkzQjI2NjI3QyIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Pgyuc50AAAQdSURBVHjazJgLaI1hGMe/Mwdr2BhzS3JZMpchyXWYGHOXWLnNvUhYIXKJcokScxmhaNiMYhIbidxis6a5ZrQQc537ZSOX/1v/tx6fc/m+s++Up37tfZ9955z/e3ue5/1cqwvyjACsDegIWoEIEAo+gGfgPshjPyBz23i2NZgJkkBjP8/+BOdAOsgCP+yICrHwTFN+8R2QYkGQsipgANgPisFYJ2dqOkgFYSb/S3AB3APPwW9QC0SDrqCDGHAzDmo8mArKAhWlRroFzBa+XyCbIi+z783UbCaDeaABfcNBPkjk7NlaPhfYaxJUBLqB0eCiH0HKSsE6zlwqZ1JZC3CJfluiVoKJop9OQdeFr7rF7fEZzAdDwSf66oMcUNuqqHiwVPS3gcmgnP1YfuFX0MXG3j0FEvg5gzO1y4ooNfo07idluRylnvo5oAAM4qw9sRl+rnGz6+8bwxn0KWoaY5HBE5LMeKNsCdgKvoFxoDtPoF3L5sC1rfe0hULE5k4R/lXgFdsjwBrwgmIyxWgDsWXgvcgMg72J6i1OxBuwh+2aYAf4DkaCu0blTQnaKfoTvIkaInyZXCYdPBuBTcxnTtk+0U4U+/gvUX2EL0e0nzBQbjCcNZW0S9gOZwb4R1SM8OWL9lEQB94ZzluBaMeYRUUwb+lgV2YEx+qZcuhj0W5iFhUu+u+CJMjFJTvg5bdqmkWVi35okETVAZGmCZC/VW4W9UEESfXhqkEQ1ZJ/HwlffVOY+EvUd7G+bhHVnbQEkWq0tRPtYk+nT564vg4LcjOFqcEfpy9MJHRVBhV6EnVW+JIcFjUXNAcHwWv6Rony54b5xGtRxzgSZT1tliW+TMW4tdwzK4R/jmhneUszb1nky+xdWRsGTnL5JoGn9I9k0Wgwne31VbqsF1chVezNCFBMW3CE+6cqE+4J/q8uSyBt21kAeL04PAAbwWL2N4ObFhJxLPdhFJe9I/23uMF1GV0NHBbRu5Qlkd/bjKrPB4JOPCGnWB1e9SEqhSWzDoK53AqHxAUjjNVHP3FZnWKOT95q9ApeHJ+zH8mb7iymCk+2gD/WjvlNlSIZQpCKe1d4xZIXilI7t5mHoD/fC+h0kMarVZyH59VxPs8b9Bfhj+LJKxJLqi2Cg21r59p+l6FBlhe9KOw290IPUV3oC2w0N3YGs8QS7iW9tGvEi48oDuYfYS4/b11UgFsOFoovN9sXxrjaPpb4Kl+O3Oa1/jRny2BAjedMW3rBUcFCXxVhu0WZLK0GE7knQYXcSz0oyOBpHuhrxkIsxp4SjrQhl2cfw4UU+Zvlcy4H0h50FjFKmk9hbpuB8SNz2EHTElfn/+xYHquHM1xKLSwuxIF0UhGAIHkfSDDN2CInRFXWtLAyboHLbuP/sHy+JlLB+tEfAQYAZSjxOOFdJ8EAAAAASUVORK5CYII=";

/* script */
const __vue_script__$3y = script$3y;
var __vue_render__$3y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtyseven-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "sub-title" },
            [
              _c("div", { staticClass: "icon-box" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_title_icon7_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "body-text",
              class: !_vm.isFold ? "body-text-fold" : "",
            },
            [
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [
                    _vm._v(_vm._s(!_vm.isFold ? "查看更多" : "折叠")),
                  ]),
                  _vm._v(" "),
                  !_vm.isFold
                    ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                    : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
                ]
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3y = [];
__vue_render__$3y._withStripped = true;

  /* style */
  const __vue_inject_styles__$3y = undefined;
  /* scoped */
  const __vue_scope_id__$3y = "data-v-0055241a";
  /* module identifier */
  const __vue_module_identifier__$3y = undefined;
  /* functional template */
  const __vue_is_functional_template__$3y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3y, staticRenderFns: __vue_staticRenderFns__$3y },
    __vue_inject_styles__$3y,
    __vue_script__$3y,
    __vue_scope_id__$3y,
    __vue_is_functional_template__$3y,
    __vue_module_identifier__$3y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3x = {
  mixins: [singleGroupMixin],
  name: 'paragraphThirtyeightRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

var __$_require_static_images_leaflet_resource_enter_paragraph_icon3_png__ = "data:image/png;base64,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";

var __$_require_static_images_leaflet_resource_enter_paragraph_icon4_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$3x = script$3x;
var __vue_render__$3x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Thirtyeight-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "sub-title" },
            [
              _c("div", { staticClass: "icon-box" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_paragraph_icon3_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "border-right" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_paragraph_icon4_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }, [
                _c("img", {
                  attrs: {
                    src: __$_require_static_images_leaflet_resource_enter_paragraph_icon4_png__,
                    alt: "",
                  },
                }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "icon-box" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3x = [];
__vue_render__$3x._withStripped = true;

  /* style */
  const __vue_inject_styles__$3x = undefined;
  /* scoped */
  const __vue_scope_id__$3x = "data-v-c953f3be";
  /* module identifier */
  const __vue_module_identifier__$3x = undefined;
  /* functional template */
  const __vue_is_functional_template__$3x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3x, staticRenderFns: __vue_staticRenderFns__$3x },
    __vue_inject_styles__$3x,
    __vue_script__$3x,
    __vue_scope_id__$3x,
    __vue_is_functional_template__$3x,
    __vue_module_identifier__$3x,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3w = {
  mixins: [singleGroupMixin],
  name: 'paragraphFortyRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$3w = script$3w;
/* template */
var __vue_render__$3w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Forty-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "top-image" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom-image" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3w = [];
__vue_render__$3w._withStripped = true;

  /* style */
  const __vue_inject_styles__$3w = undefined;
  /* scoped */
  const __vue_scope_id__$3w = "data-v-7b42d7e1";
  /* module identifier */
  const __vue_module_identifier__$3w = undefined;
  /* functional template */
  const __vue_is_functional_template__$3w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3w, staticRenderFns: __vue_staticRenderFns__$3w },
    __vue_inject_styles__$3w,
    __vue_script__$3w,
    __vue_scope_id__$3w,
    __vue_is_functional_template__$3w,
    __vue_module_identifier__$3w,
    false,
    undefined,
    undefined,
    undefined
  );

const ParagraphComponents = [__vue_component__$48, __vue_component__$47, __vue_component__$46, __vue_component__$45, __vue_component__$44, __vue_component__$43, __vue_component__$42, __vue_component__$41, __vue_component__$40, __vue_component__$3$, __vue_component__$3_, __vue_component__$3Z, __vue_component__$3Y, __vue_component__$3X, __vue_component__$3W, __vue_component__$3V, __vue_component__$3U, __vue_component__$3T, __vue_component__$3S, __vue_component__$3R, __vue_component__$3Q, __vue_component__$3P, __vue_component__$3O, __vue_component__$3N, __vue_component__$3M, __vue_component__$3L, __vue_component__$3K, __vue_component__$3J, __vue_component__$3I, __vue_component__$3H, __vue_component__$3G, __vue_component__$3F, __vue_component__$3E, __vue_component__$3D, __vue_component__$3C, __vue_component__$3B, __vue_component__$3A, __vue_component__$3z, __vue_component__$3y, __vue_component__$3x, __vue_component__$3w];
var paragraph = {
  // install,
  ...ParagraphComponents
};

//
var script$3v = {
  mixins: [singleGroupMixin],
  name: 'splitlineFirstRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'SplitlineData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex: this.ruleIndex,
        dataName: this.titleTextDataName,
        data: this.pGroupData
      });
    }
  }
};

/* script */
const __vue_script__$3v = script$3v;
/* template */
var __vue_render__$3v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-first-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c("xml-group-render", {
        ref: "xmlGroup",
        attrs: {
          "page-type": _vm.pageType,
          mode: _vm.mode,
          "type-text": _vm.typeText,
          showLine: false,
          "rule-index": _vm.ruleIndex,
          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
        },
        scopedSlots: _vm._u(
          [
            _vm._l(_vm.$scopedSlots, function (index, name) {
              return {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t(name)]
                },
                proxy: true,
              }
            }),
          ],
          null,
          true
        ),
      }),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "title-container",
          attrs: { id: _vm.blockId },
          on: { click: _vm.handleBlockClick },
        },
        [
          _c("div", { staticClass: "line" }),
          _vm._v(" "),
          _c("div", { staticClass: "centerimg" }),
          _vm._v(" "),
          _c("div", { staticClass: "line" }),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3v = [];
__vue_render__$3v._withStripped = true;

  /* style */
  const __vue_inject_styles__$3v = undefined;
  /* scoped */
  const __vue_scope_id__$3v = "data-v-e6c9fe86";
  /* module identifier */
  const __vue_module_identifier__$3v = undefined;
  /* functional template */
  const __vue_is_functional_template__$3v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3v, staticRenderFns: __vue_staticRenderFns__$3v },
    __vue_inject_styles__$3v,
    __vue_script__$3v,
    __vue_scope_id__$3v,
    __vue_is_functional_template__$3v,
    __vue_module_identifier__$3v,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3u = {
  mixins: [singleGroupMixin],
  name: 'splitlineSecondRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3u = script$3u;
/* template */
var __vue_render__$3u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-second-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line1" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }, [_c("div")]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3u = [];
__vue_render__$3u._withStripped = true;

  /* style */
  const __vue_inject_styles__$3u = undefined;
  /* scoped */
  const __vue_scope_id__$3u = "data-v-fb736abc";
  /* module identifier */
  const __vue_module_identifier__$3u = undefined;
  /* functional template */
  const __vue_is_functional_template__$3u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3u, staticRenderFns: __vue_staticRenderFns__$3u },
    __vue_inject_styles__$3u,
    __vue_script__$3u,
    __vue_scope_id__$3u,
    __vue_is_functional_template__$3u,
    __vue_module_identifier__$3u,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3t = {
  mixins: [singleGroupMixin],
  name: 'splitlineThirdRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3t = script$3t;
/* template */
var __vue_render__$3t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-third-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3t = [];
__vue_render__$3t._withStripped = true;

  /* style */
  const __vue_inject_styles__$3t = undefined;
  /* scoped */
  const __vue_scope_id__$3t = "data-v-15db9268";
  /* module identifier */
  const __vue_module_identifier__$3t = undefined;
  /* functional template */
  const __vue_is_functional_template__$3t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3t, staticRenderFns: __vue_staticRenderFns__$3t },
    __vue_inject_styles__$3t,
    __vue_script__$3t,
    __vue_scope_id__$3t,
    __vue_is_functional_template__$3t,
    __vue_module_identifier__$3t,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3s = {
  mixins: [singleGroupMixin],
  name: 'splitlineFourthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3s = script$3s;
/* template */
var __vue_render__$3s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-fourth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }),
              _vm._v(" "),
              _c("div", { staticClass: "line" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3s = [];
__vue_render__$3s._withStripped = true;

  /* style */
  const __vue_inject_styles__$3s = undefined;
  /* scoped */
  const __vue_scope_id__$3s = "data-v-f7e63308";
  /* module identifier */
  const __vue_module_identifier__$3s = undefined;
  /* functional template */
  const __vue_is_functional_template__$3s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3s, staticRenderFns: __vue_staticRenderFns__$3s },
    __vue_inject_styles__$3s,
    __vue_script__$3s,
    __vue_scope_id__$3s,
    __vue_is_functional_template__$3s,
    __vue_module_identifier__$3s,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3r = {
  mixins: [singleGroupMixin],
  name: 'splitlineFifthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3r = script$3r;
/* template */
var __vue_render__$3r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-fifth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "line" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3r = [];
__vue_render__$3r._withStripped = true;

  /* style */
  const __vue_inject_styles__$3r = undefined;
  /* scoped */
  const __vue_scope_id__$3r = "data-v-1aedf48a";
  /* module identifier */
  const __vue_module_identifier__$3r = undefined;
  /* functional template */
  const __vue_is_functional_template__$3r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3r, staticRenderFns: __vue_staticRenderFns__$3r },
    __vue_inject_styles__$3r,
    __vue_script__$3r,
    __vue_scope_id__$3r,
    __vue_is_functional_template__$3r,
    __vue_module_identifier__$3r,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3q = {
  mixins: [singleGroupMixin],
  name: 'splitlineSixthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3q = script$3q;
/* template */
var __vue_render__$3q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-sixth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block2" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3q = [];
__vue_render__$3q._withStripped = true;

  /* style */
  const __vue_inject_styles__$3q = undefined;
  /* scoped */
  const __vue_scope_id__$3q = "data-v-b4e57fd2";
  /* module identifier */
  const __vue_module_identifier__$3q = undefined;
  /* functional template */
  const __vue_is_functional_template__$3q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3q, staticRenderFns: __vue_staticRenderFns__$3q },
    __vue_inject_styles__$3q,
    __vue_script__$3q,
    __vue_scope_id__$3q,
    __vue_is_functional_template__$3q,
    __vue_module_identifier__$3q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3p = {
  mixins: [singleGroupMixin],
  name: 'splitlineSeventhRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3p = script$3p;
/* template */
var __vue_render__$3p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-seventh-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "dotted" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }),
              _vm._v(" "),
              _c("div", { staticClass: "dotted" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3p = [];
__vue_render__$3p._withStripped = true;

  /* style */
  const __vue_inject_styles__$3p = undefined;
  /* scoped */
  const __vue_scope_id__$3p = "data-v-573b56e9";
  /* module identifier */
  const __vue_module_identifier__$3p = undefined;
  /* functional template */
  const __vue_is_functional_template__$3p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3p, staticRenderFns: __vue_staticRenderFns__$3p },
    __vue_inject_styles__$3p,
    __vue_script__$3p,
    __vue_scope_id__$3p,
    __vue_is_functional_template__$3p,
    __vue_module_identifier__$3p,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3o = {
  mixins: [singleGroupMixin],
  name: 'splitlineEighthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3o = script$3o;
/* template */
var __vue_render__$3o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-eighth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3o = [];
__vue_render__$3o._withStripped = true;

  /* style */
  const __vue_inject_styles__$3o = undefined;
  /* scoped */
  const __vue_scope_id__$3o = "data-v-e50253fe";
  /* module identifier */
  const __vue_module_identifier__$3o = undefined;
  /* functional template */
  const __vue_is_functional_template__$3o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3o, staticRenderFns: __vue_staticRenderFns__$3o },
    __vue_inject_styles__$3o,
    __vue_script__$3o,
    __vue_scope_id__$3o,
    __vue_is_functional_template__$3o,
    __vue_module_identifier__$3o,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3n = {
  mixins: [singleGroupMixin],
  name: 'splitlineNinthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3n = script$3n;
/* template */
var __vue_render__$3n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-ninth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "skeybox" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3n = [];
__vue_render__$3n._withStripped = true;

  /* style */
  const __vue_inject_styles__$3n = undefined;
  /* scoped */
  const __vue_scope_id__$3n = "data-v-182aaf7b";
  /* module identifier */
  const __vue_module_identifier__$3n = undefined;
  /* functional template */
  const __vue_is_functional_template__$3n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3n, staticRenderFns: __vue_staticRenderFns__$3n },
    __vue_inject_styles__$3n,
    __vue_script__$3n,
    __vue_scope_id__$3n,
    __vue_is_functional_template__$3n,
    __vue_module_identifier__$3n,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3m = {
  mixins: [singleGroupMixin],
  name: 'splitlineTenthRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3m = script$3m;
/* template */
var __vue_render__$3m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-tenth-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", [
                _c("div", { staticClass: "centerimg" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3m = [];
__vue_render__$3m._withStripped = true;

  /* style */
  const __vue_inject_styles__$3m = undefined;
  /* scoped */
  const __vue_scope_id__$3m = "data-v-61e2f18f";
  /* module identifier */
  const __vue_module_identifier__$3m = undefined;
  /* functional template */
  const __vue_is_functional_template__$3m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3m, staticRenderFns: __vue_staticRenderFns__$3m },
    __vue_inject_styles__$3m,
    __vue_script__$3m,
    __vue_scope_id__$3m,
    __vue_is_functional_template__$3m,
    __vue_module_identifier__$3m,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3l = {
  mixins: [singleGroupMixin],
  name: 'splitlineEleventhRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3l = script$3l;
/* template */
var __vue_render__$3l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-eleventh-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
                _vm._v(" "),
                _c("div", { staticClass: "centerimg" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3l = [];
__vue_render__$3l._withStripped = true;

  /* style */
  const __vue_inject_styles__$3l = undefined;
  /* scoped */
  const __vue_scope_id__$3l = "data-v-3e2c52f5";
  /* module identifier */
  const __vue_module_identifier__$3l = undefined;
  /* functional template */
  const __vue_is_functional_template__$3l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3l, staticRenderFns: __vue_staticRenderFns__$3l },
    __vue_inject_styles__$3l,
    __vue_script__$3l,
    __vue_scope_id__$3l,
    __vue_is_functional_template__$3l,
    __vue_module_identifier__$3l,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3k = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme1_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAcCAYAAAC+lOV/AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6NkVEQjlDNkFFNDEzMTFFRDk3MjZDM0M4NDc0N0Y2M0QiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6NkVEQjlDNkJFNDEzMTFFRDk3MjZDM0M4NDc0N0Y2M0QiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo2RURCOUM2OEU0MTMxMUVEOTcyNkMzQzg0NzQ3RjYzRCIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo2RURCOUM2OUU0MTMxMUVEOTcyNkMzQzg0NzQ3RjYzRCIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PnHmU/UAAACvSURBVHjaYnx7MJ+HgYGBlYF08JMFSKwAYm8yNE9nYqAAjGoe1TyqeVBpBhUGC4D4MBl6z4M0XwbiCUDMRYLGv0DsC9J8E4hLgXgZCZqLgPgEzM/LgbiPSI1LgbgfPcDKgHgPIX8CcRq20Ab5IxKI7+PQ+BqIA4H4G66oegPEQcgKkAyOAOKHhOL5AhAno4mBAnQfsYkEVBH0QtlLYAGELZHgAuVAzAjEtbgUAAQYADfpIG4AZtNeAAAAAElFTkSuQmCC";

/* script */
const __vue_script__$3k = script$3k;
var __vue_render__$3k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme1_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3k = [];
__vue_render__$3k._withStripped = true;

  /* style */
  const __vue_inject_styles__$3k = undefined;
  /* scoped */
  const __vue_scope_id__$3k = "data-v-65cda570";
  /* module identifier */
  const __vue_module_identifier__$3k = undefined;
  /* functional template */
  const __vue_is_functional_template__$3k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3k, staticRenderFns: __vue_staticRenderFns__$3k },
    __vue_inject_styles__$3k,
    __vue_script__$3k,
    __vue_scope_id__$3k,
    __vue_is_functional_template__$3k,
    __vue_module_identifier__$3k,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3j = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme2_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAYCAYAAADkgu3FAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6ODBENkQyRjlFNDEzMTFFREI0QzA4RUJBQTYzQUY4QTMiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6ODBENkQyRkFFNDEzMTFFREI0QzA4RUJBQTYzQUY4QTMiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo4MEQ2RDJGN0U0MTMxMUVEQjRDMDhFQkFBNjNBRjhBMyIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo4MEQ2RDJGOEU0MTMxMUVEQjRDMDhFQkFBNjNBRjhBMyIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Pmq84K4AAAGmSURBVHjavNXPKwRhGAfwd8YmUgoHKeFA+RlFnIVcrLObhNrkpITk4Lqb/wDlwk3JRVws5WbXQbkoRKg5rMKBjfV96pl6m3Zmn9HOPPU57Ls7+33nfWee17BiyypPVUETvMCrklU5lMBHvi9Nx+deOAYLUhx0DRMuf07XT0MaPuEdnmEDqt2CxuECRnhmdnXAHmw6xivhiMd7wODxOljg8GZnUAvsQqnH0tDM17Xr9nlSbtUAB1CmByWgQrAPtKF9MAdDgt+3w7wdVA9R4YbT0sVhVcmLgowI377p48JB5a8aoY0COlXw1UpBtSEE1ZgqnIpQ0GMIQQ8UlAw4hLpF0uSWcxNg0A61Jwr6hRh8BxByC2t6ZziDKfgpYsg9DMObs6lSrxuDTBFCTqGfHgK3Y4K6cTec/DPgC5a4D1pe55Hix30UZuzbFlaaG26c910VCqLKwRZ0wbkghM6kAT4kleSEddYTL8O2x4RoqWYh69kaBLPN8jIa/GTqtcjHdsGS9rocz/pQG0tIQ6R3ZBe9Y5NwBXew4qur+nx8M3ycX/p9uf8EGACIV0806+7CtAAAAABJRU5ErkJggg==";

/* script */
const __vue_script__$3j = script$3j;
var __vue_render__$3j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme2_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3j = [];
__vue_render__$3j._withStripped = true;

  /* style */
  const __vue_inject_styles__$3j = undefined;
  /* scoped */
  const __vue_scope_id__$3j = "data-v-17b5d032";
  /* module identifier */
  const __vue_module_identifier__$3j = undefined;
  /* functional template */
  const __vue_is_functional_template__$3j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3j, staticRenderFns: __vue_staticRenderFns__$3j },
    __vue_inject_styles__$3j,
    __vue_script__$3j,
    __vue_scope_id__$3j,
    __vue_is_functional_template__$3j,
    __vue_module_identifier__$3j,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3i = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme3_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAZCAYAAADaILXQAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6OTAyNjJBQTlFNDEzMTFFRDk2NjdEMTQ0QjA1QzE1NUIiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6OTAyNjJBQUFFNDEzMTFFRDk2NjdEMTQ0QjA1QzE1NUIiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo5MDI2MkFBN0U0MTMxMUVEOTY2N0QxNDRCMDVDMTU1QiIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo5MDI2MkFBOEU0MTMxMUVEOTY2N0QxNDRCMDVDMTU1QiIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Ppo2R5QAAAF5SURBVHjatNbNKwVRGMfxmesl7yIl5Y66iYWIYmEhC2s7edtZSDd5ibJgYesf0BXlH5AFydKa3KQUW2QjlvKWt++p52pMM/eec2ec+tR05s5vnnPmnJlrPybbLINWgSfdH8cMgqtwaHKNSfg8+jASdXgNFuR4FYVRhi+iWo5bMR5VeB1mPX1a1euEL6HS05fARNjwekwHnFtBcbaLvUNT89ojutGLsoBrm3CFY6RxijM8u8Mn0S9hLbANlmdCZB7wBy7lRgcq/B6jKLDCN5XXgWZsqTnfx5wVXfvEGE4yD3QdqYjCZ6TgP6vltzNEW3MXGfMbTp7BqrDlbOtcLaNB3BoGp6Ww71yb6AHXhuHn7vWda4fGDcMd3e2v+hoNw+O64Q0o+q/KnYCANxzhy+dcOWp1wr1DfMeGbOkBtGPH5yaOSeUqdFNeZkncSb96MQ2jE7uu5efozvm2fM6mcBMwTRcYQhf2/B6q7fO/pRQveezQEry6O34EGACZzD919vhTGQAAAABJRU5ErkJggg==";

/* script */
const __vue_script__$3i = script$3i;
var __vue_render__$3i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme3_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3i = [];
__vue_render__$3i._withStripped = true;

  /* style */
  const __vue_inject_styles__$3i = undefined;
  /* scoped */
  const __vue_scope_id__$3i = "data-v-72f80cc0";
  /* module identifier */
  const __vue_module_identifier__$3i = undefined;
  /* functional template */
  const __vue_is_functional_template__$3i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3i, staticRenderFns: __vue_staticRenderFns__$3i },
    __vue_inject_styles__$3i,
    __vue_script__$3i,
    __vue_scope_id__$3i,
    __vue_is_functional_template__$3i,
    __vue_module_identifier__$3i,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3h = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme4_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAXCAYAAADk3wSdAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6OUVGMjNFQThFNDEzMTFFREFEM0VENTk1ODYyNDhCRTMiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6OUVGMjNFQTlFNDEzMTFFREFEM0VENTk1ODYyNDhCRTMiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo5RUYyM0VBNkU0MTMxMUVEQUQzRUQ1OTU4NjI0OEJFMyIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo5RUYyM0VBN0U0MTMxMUVEQUQzRUQ1OTU4NjI0OEJFMyIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PsVWod0AAAHgSURBVHjarJVJKIVRFMd5pkRYiY1nLBuJDLFSyhhRlpShlKQMRYaiZIEiUTYkZWHMQmFhKCIWyoJeFoaQMqYMCwn/W/+n4+t93sd7p3517v3O+X/3O/fc+7m2b1S5GLQcsAQ+7QWaDAqquAGQYTTYiBWASFDnLFFX0EY/EyQ4QzQXxIlxqzNEGzTjfJbi36JRIM1GToUjonE684l/EQ0EPmL8qJP3IHwP5nlpRdWKLOAaPNMvBxvgzoboLIgGC+CJeSquUT10B35gEQSJJJUwyg0ZAh3i2Sk4AzvAX8z7gm5wqVZapBGU1gyuwKuYGwFTGkFpNSZ77QFrAjP0X0AACP0lPkR9/oVm8gBscsMKQQSY5LNVkEV/HRwCM8hmKZWdK2cZfHDThkG1uIm6wC5453gf5HH1PWIhqXyJJ1gxsfDjTGzRXG1qQ7ZAGHgDwWL3pW2DeXAPBq0tVQ+mbfRlL09VuDhhyvr4Iq1wMbgxiSYv0QSZee6PQT9vqzE2fjW/zEPED7KUP07Uh/BTKKb6LhncUiCeibXgiD1ste+yueu0RQxwY1k6xWcngTJunrfe3aAnOs3L2MLfSKzYfbXCdNXkYOIvoqrGlWKsxE7AHMd7oNTRf9Qnb/w1I8FfAgwA7f9kYofT4PIAAAAASUVORK5CYII=";

/* script */
const __vue_script__$3h = script$3h;
var __vue_render__$3h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme4_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3h = [];
__vue_render__$3h._withStripped = true;

  /* style */
  const __vue_inject_styles__$3h = undefined;
  /* scoped */
  const __vue_scope_id__$3h = "data-v-45e77c7c";
  /* module identifier */
  const __vue_module_identifier__$3h = undefined;
  /* functional template */
  const __vue_is_functional_template__$3h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3h, staticRenderFns: __vue_staticRenderFns__$3h },
    __vue_inject_styles__$3h,
    __vue_script__$3h,
    __vue_scope_id__$3h,
    __vue_is_functional_template__$3h,
    __vue_module_identifier__$3h,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3g = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme5Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme5_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$3g = script$3g;
var __vue_render__$3g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme5-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme5_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3g = [];
__vue_render__$3g._withStripped = true;

  /* style */
  const __vue_inject_styles__$3g = undefined;
  /* scoped */
  const __vue_scope_id__$3g = "data-v-18d6ec38";
  /* module identifier */
  const __vue_module_identifier__$3g = undefined;
  /* functional template */
  const __vue_is_functional_template__$3g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3g, staticRenderFns: __vue_staticRenderFns__$3g },
    __vue_inject_styles__$3g,
    __vue_script__$3g,
    __vue_scope_id__$3g,
    __vue_is_functional_template__$3g,
    __vue_module_identifier__$3g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3f = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreytheme6Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

var __$_require_static_images_leaflet_resource_enter_reytheme6_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAaCAYAAABYQRdDAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6QzhCNDAxMkRFNDEzMTFFRDk0QkVFQjNBMzJBNTY3Q0QiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6QzhCNDAxMkVFNDEzMTFFRDk0QkVFQjNBMzJBNTY3Q0QiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDpDOEI0MDEyQkU0MTMxMUVEOTRCRUVCM0EzMkE1NjdDRCIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDpDOEI0MDEyQ0U0MTMxMUVEOTRCRUVCM0EzMkE1NjdDRCIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PuQHbIYAAAIZSURBVHjaYnzGIMhARaAExKZMVDTQDojPALEMC5UMdALizUDMBcRvqeFSHSBeBzUQBD5QaqgAEK8HYn4ksU+UGjoViFXQxBhZvAwVxIAMHlJNm/D8rYfai89RWKSEQBHVD8RRpBjIyfCfQfjHT1zSKmR5P+zrdwbhD79wSTugJ6lXQPwFn4GS//6yBNx9IwsKOyzS14C4Fd3Qwm3nHyyDskG++IdF4xwgTsYwTYr3h9azz1ZA5kdc3lcF4pNALI8mLgfEseiKL0nzMVSKC78GGQhzDToIh2Y3EyDOQpMrA2I2ZIEHYlwMLWKCDL8ZgLGH5EUwAKmc8uwVyFsrgJgPKpwEjmxEQk9ANhBoyuc2SWFgJKAGL9hQuX9/GSY8fcWg9PKbE5rLRECRDWWnADE3spm3JXiLnjAxY3gVbOhHRiaGuzycUMsxQA40ptPQxKcVSArvwhYhUEMZGXr5eRnWqop2gwoENDWgsC2GRh4M3IKGLwNOQ2FgLg/3RVDiBeIXaOrakdigZJYIxN+IMhQKQAa7wZIHFCCn55lAfAxfBsGVTi8DcRAQo+dFkA8qCWVjfHl/HzTGkUENmg9INhQEFgPxLKRgmU9MgYOe9/OA5WsAsgAPw3/mqQ9efFotIfh1CwfHCjT1XMQYag7FcADKLZVyYgzPmZitiC0aiapNn2HJNfgAo4GhoTm09KEUgNLt1vPnzjEABBgAWt12zyTTtPoAAAAASUVORK5CYII=";

/* script */
const __vue_script__$3f = script$3f;
var __vue_render__$3f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme6-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_static_images_leaflet_resource_enter_reytheme6_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3f = [];
__vue_render__$3f._withStripped = true;

  /* style */
  const __vue_inject_styles__$3f = undefined;
  /* scoped */
  const __vue_scope_id__$3f = "data-v-28734818";
  /* module identifier */
  const __vue_module_identifier__$3f = undefined;
  /* functional template */
  const __vue_is_functional_template__$3f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3f, staticRenderFns: __vue_staticRenderFns__$3f },
    __vue_inject_styles__$3f,
    __vue_script__$3f,
    __vue_scope_id__$3f,
    __vue_is_functional_template__$3f,
    __vue_module_identifier__$3f,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3e = {
  mixins: [singleGroupMixin],
  name: 'splitlineYellowtheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3e = script$3e;
/* template */
var __vue_render__$3e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3e = [];
__vue_render__$3e._withStripped = true;

  /* style */
  const __vue_inject_styles__$3e = undefined;
  /* scoped */
  const __vue_scope_id__$3e = "data-v-01d8a966";
  /* module identifier */
  const __vue_module_identifier__$3e = undefined;
  /* functional template */
  const __vue_is_functional_template__$3e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3e, staticRenderFns: __vue_staticRenderFns__$3e },
    __vue_inject_styles__$3e,
    __vue_script__$3e,
    __vue_scope_id__$3e,
    __vue_is_functional_template__$3e,
    __vue_module_identifier__$3e,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3d = {
  mixins: [singleGroupMixin],
  name: 'splitlineYellowtheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3d = script$3d;
/* template */
var __vue_render__$3d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3d = [];
__vue_render__$3d._withStripped = true;

  /* style */
  const __vue_inject_styles__$3d = undefined;
  /* scoped */
  const __vue_scope_id__$3d = "data-v-a61d382c";
  /* module identifier */
  const __vue_module_identifier__$3d = undefined;
  /* functional template */
  const __vue_is_functional_template__$3d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3d, staticRenderFns: __vue_staticRenderFns__$3d },
    __vue_inject_styles__$3d,
    __vue_script__$3d,
    __vue_scope_id__$3d,
    __vue_is_functional_template__$3d,
    __vue_module_identifier__$3d,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3c = {
  mixins: [singleGroupMixin],
  name: 'splitlineYellowtheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3c = script$3c;
/* template */
var __vue_render__$3c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3c = [];
__vue_render__$3c._withStripped = true;

  /* style */
  const __vue_inject_styles__$3c = undefined;
  /* scoped */
  const __vue_scope_id__$3c = "data-v-5acf1c87";
  /* module identifier */
  const __vue_module_identifier__$3c = undefined;
  /* functional template */
  const __vue_is_functional_template__$3c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3c, staticRenderFns: __vue_staticRenderFns__$3c },
    __vue_inject_styles__$3c,
    __vue_script__$3c,
    __vue_scope_id__$3c,
    __vue_is_functional_template__$3c,
    __vue_module_identifier__$3c,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3b = {
  mixins: [singleGroupMixin],
  name: 'splitlineYellowtheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3b = script$3b;
/* template */
var __vue_render__$3b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3b = [];
__vue_render__$3b._withStripped = true;

  /* style */
  const __vue_inject_styles__$3b = undefined;
  /* scoped */
  const __vue_scope_id__$3b = "data-v-08acd524";
  /* module identifier */
  const __vue_module_identifier__$3b = undefined;
  /* functional template */
  const __vue_is_functional_template__$3b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3b, staticRenderFns: __vue_staticRenderFns__$3b },
    __vue_inject_styles__$3b,
    __vue_script__$3b,
    __vue_scope_id__$3b,
    __vue_is_functional_template__$3b,
    __vue_module_identifier__$3b,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$3a = {
  mixins: [singleGroupMixin],
  name: 'splitlineRedtheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$3a = script$3a;
/* template */
var __vue_render__$3a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$3a = [];
__vue_render__$3a._withStripped = true;

  /* style */
  const __vue_inject_styles__$3a = undefined;
  /* scoped */
  const __vue_scope_id__$3a = "data-v-d692ea4c";
  /* module identifier */
  const __vue_module_identifier__$3a = undefined;
  /* functional template */
  const __vue_is_functional_template__$3a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3a, staticRenderFns: __vue_staticRenderFns__$3a },
    __vue_inject_styles__$3a,
    __vue_script__$3a,
    __vue_scope_id__$3a,
    __vue_is_functional_template__$3a,
    __vue_module_identifier__$3a,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$39 = {
  mixins: [singleGroupMixin],
  name: 'splitlineRedtheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$39 = script$39;
/* template */
var __vue_render__$39 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$39 = [];
__vue_render__$39._withStripped = true;

  /* style */
  const __vue_inject_styles__$39 = undefined;
  /* scoped */
  const __vue_scope_id__$39 = "data-v-3392c359";
  /* module identifier */
  const __vue_module_identifier__$39 = undefined;
  /* functional template */
  const __vue_is_functional_template__$39 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$39 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$39, staticRenderFns: __vue_staticRenderFns__$39 },
    __vue_inject_styles__$39,
    __vue_script__$39,
    __vue_scope_id__$39,
    __vue_is_functional_template__$39,
    __vue_module_identifier__$39,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$38 = {
  mixins: [singleGroupMixin],
  name: 'splitlineRedtheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$38 = script$38;
/* template */
var __vue_render__$38 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$38 = [];
__vue_render__$38._withStripped = true;

  /* style */
  const __vue_inject_styles__$38 = undefined;
  /* scoped */
  const __vue_scope_id__$38 = "data-v-5b220850";
  /* module identifier */
  const __vue_module_identifier__$38 = undefined;
  /* functional template */
  const __vue_is_functional_template__$38 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$38 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$38, staticRenderFns: __vue_staticRenderFns__$38 },
    __vue_inject_styles__$38,
    __vue_script__$38,
    __vue_scope_id__$38,
    __vue_is_functional_template__$38,
    __vue_module_identifier__$38,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$37 = {
  mixins: [singleGroupMixin],
  name: 'splitlineRedtheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$37 = script$37;
/* template */
var __vue_render__$37 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$37 = [];
__vue_render__$37._withStripped = true;

  /* style */
  const __vue_inject_styles__$37 = undefined;
  /* scoped */
  const __vue_scope_id__$37 = "data-v-714b3457";
  /* module identifier */
  const __vue_module_identifier__$37 = undefined;
  /* functional template */
  const __vue_is_functional_template__$37 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$37 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$37, staticRenderFns: __vue_staticRenderFns__$37 },
    __vue_inject_styles__$37,
    __vue_script__$37,
    __vue_scope_id__$37,
    __vue_is_functional_template__$37,
    __vue_module_identifier__$37,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$36 = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreentheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$36 = script$36;
/* template */
var __vue_render__$36 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greentheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$36 = [];
__vue_render__$36._withStripped = true;

  /* style */
  const __vue_inject_styles__$36 = undefined;
  /* scoped */
  const __vue_scope_id__$36 = "data-v-d8385330";
  /* module identifier */
  const __vue_module_identifier__$36 = undefined;
  /* functional template */
  const __vue_is_functional_template__$36 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$36 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$36, staticRenderFns: __vue_staticRenderFns__$36 },
    __vue_inject_styles__$36,
    __vue_script__$36,
    __vue_scope_id__$36,
    __vue_is_functional_template__$36,
    __vue_module_identifier__$36,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$35 = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreentheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$35 = script$35;
/* template */
var __vue_render__$35 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greentheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$35 = [];
__vue_render__$35._withStripped = true;

  /* style */
  const __vue_inject_styles__$35 = undefined;
  /* scoped */
  const __vue_scope_id__$35 = "data-v-21710b27";
  /* module identifier */
  const __vue_module_identifier__$35 = undefined;
  /* functional template */
  const __vue_is_functional_template__$35 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$35 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$35, staticRenderFns: __vue_staticRenderFns__$35 },
    __vue_inject_styles__$35,
    __vue_script__$35,
    __vue_scope_id__$35,
    __vue_is_functional_template__$35,
    __vue_module_identifier__$35,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$34 = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreentheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$34 = script$34;
/* template */
var __vue_render__$34 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greentheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$34 = [];
__vue_render__$34._withStripped = true;

  /* style */
  const __vue_inject_styles__$34 = undefined;
  /* scoped */
  const __vue_scope_id__$34 = "data-v-74b5aaba";
  /* module identifier */
  const __vue_module_identifier__$34 = undefined;
  /* functional template */
  const __vue_is_functional_template__$34 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$34 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$34, staticRenderFns: __vue_staticRenderFns__$34 },
    __vue_inject_styles__$34,
    __vue_script__$34,
    __vue_scope_id__$34,
    __vue_is_functional_template__$34,
    __vue_module_identifier__$34,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$33 = {
  mixins: [singleGroupMixin],
  name: 'splitlineGreentheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$33 = script$33;
/* template */
var __vue_render__$33 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greentheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$33 = [];
__vue_render__$33._withStripped = true;

  /* style */
  const __vue_inject_styles__$33 = undefined;
  /* scoped */
  const __vue_scope_id__$33 = "data-v-3c8b74a5";
  /* module identifier */
  const __vue_module_identifier__$33 = undefined;
  /* functional template */
  const __vue_is_functional_template__$33 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$33 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$33, staticRenderFns: __vue_staticRenderFns__$33 },
    __vue_inject_styles__$33,
    __vue_script__$33,
    __vue_scope_id__$33,
    __vue_is_functional_template__$33,
    __vue_module_identifier__$33,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$32 = {
  mixins: [singleGroupMixin],
  name: 'splitlineBluetheme1Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$32 = script$32;
/* template */
var __vue_render__$32 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme1-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$32 = [];
__vue_render__$32._withStripped = true;

  /* style */
  const __vue_inject_styles__$32 = undefined;
  /* scoped */
  const __vue_scope_id__$32 = "data-v-2c5cb5ca";
  /* module identifier */
  const __vue_module_identifier__$32 = undefined;
  /* functional template */
  const __vue_is_functional_template__$32 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$32 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$32, staticRenderFns: __vue_staticRenderFns__$32 },
    __vue_inject_styles__$32,
    __vue_script__$32,
    __vue_scope_id__$32,
    __vue_is_functional_template__$32,
    __vue_module_identifier__$32,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$31 = {
  mixins: [singleGroupMixin],
  name: 'splitlineBluetheme2Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$31 = script$31;
/* template */
var __vue_render__$31 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme2-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$31 = [];
__vue_render__$31._withStripped = true;

  /* style */
  const __vue_inject_styles__$31 = undefined;
  /* scoped */
  const __vue_scope_id__$31 = "data-v-593dd0f8";
  /* module identifier */
  const __vue_module_identifier__$31 = undefined;
  /* functional template */
  const __vue_is_functional_template__$31 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$31 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$31, staticRenderFns: __vue_staticRenderFns__$31 },
    __vue_inject_styles__$31,
    __vue_script__$31,
    __vue_scope_id__$31,
    __vue_is_functional_template__$31,
    __vue_module_identifier__$31,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$30 = {
  mixins: [singleGroupMixin],
  name: 'splitlineBluetheme3Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$30 = script$30;
/* template */
var __vue_render__$30 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme3-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$30 = [];
__vue_render__$30._withStripped = true;

  /* style */
  const __vue_inject_styles__$30 = undefined;
  /* scoped */
  const __vue_scope_id__$30 = "data-v-6eac0656";
  /* module identifier */
  const __vue_module_identifier__$30 = undefined;
  /* functional template */
  const __vue_is_functional_template__$30 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$30 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$30, staticRenderFns: __vue_staticRenderFns__$30 },
    __vue_inject_styles__$30,
    __vue_script__$30,
    __vue_scope_id__$30,
    __vue_is_functional_template__$30,
    __vue_module_identifier__$30,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2$ = {
  mixins: [singleGroupMixin],
  name: 'splitlineBluetheme4Render',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2$ = script$2$;
/* template */
var __vue_render__$2$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme4-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "linebox" })]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2$ = [];
__vue_render__$2$._withStripped = true;

  /* style */
  const __vue_inject_styles__$2$ = undefined;
  /* scoped */
  const __vue_scope_id__$2$ = "data-v-381628b2";
  /* module identifier */
  const __vue_module_identifier__$2$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$2$ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2$, staticRenderFns: __vue_staticRenderFns__$2$ },
    __vue_inject_styles__$2$,
    __vue_script__$2$,
    __vue_scope_id__$2$,
    __vue_is_functional_template__$2$,
    __vue_module_identifier__$2$,
    false,
    undefined,
    undefined,
    undefined
  );

const SplitlineComponents = [__vue_component__$3v, __vue_component__$3u, __vue_component__$3t, __vue_component__$3s, __vue_component__$3r, __vue_component__$3q, __vue_component__$3p, __vue_component__$3o, __vue_component__$3n, __vue_component__$3m, __vue_component__$3l, __vue_component__$3k, __vue_component__$3j, __vue_component__$3i, __vue_component__$3h, __vue_component__$3g, __vue_component__$3f, __vue_component__$3e, __vue_component__$3d, __vue_component__$3c, __vue_component__$3b, __vue_component__$3a, __vue_component__$39, __vue_component__$38, __vue_component__$37, __vue_component__$36, __vue_component__$35, __vue_component__$34, __vue_component__$33, __vue_component__$32, __vue_component__$31, __vue_component__$30, __vue_component__$2$];
var splitline = {
  // install,
  ...SplitlineComponents
};

//

/**
 * 单图片组合
 */
var script$2_ = {
  name: 'XmlSingleImageGroupRender',
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图片',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    clickImg() {
      this.$emit('clickImg');
    }
  }
};

/* script */
const __vue_script__$2_ = script$2_;

/* template */
var __vue_render__$2_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-image-group",
        "xml-single-image-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            pageType: _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-image-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.imageBlockDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.imageBlockDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              "show-title": true,
              pageType: _vm.pageType,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: { blockclick: _vm.handleBlockClick, clickImg: _vm.clickImg },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2_ = [];
__vue_render__$2_._withStripped = true;

  /* style */
  const __vue_inject_styles__$2_ = undefined;
  /* scoped */
  const __vue_scope_id__$2_ = undefined;
  /* module identifier */
  const __vue_module_identifier__$2_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$2_ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2_, staticRenderFns: __vue_staticRenderFns__$2_ },
    __vue_inject_styles__$2_,
    __vue_script__$2_,
    __vue_scope_id__$2_,
    __vue_is_functional_template__$2_,
    __vue_module_identifier__$2_,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2_.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-image-group-render', {
    prefix: options.prefix
  }), __vue_component__$2_);
};

//
var script$2Z = {
  name: 'XmlSingleCatalogGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '目录',
      dataName: 'catalogData',
      groupData: {},
      blockId: 'blockId' + Math.random()
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex: this.ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$2Z = script$2Z;
/* template */
var __vue_render__$2Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "xml-catalog-render",
            {
              attrs: {
                "p-block-data": _vm.groupData[_vm.dataName],
                "current-rule": _vm.currentRule,
                "block-data-name": _vm.dataName,
                "extend-params": _vm.extendParams,
                "rule-index": _vm.ruleIndex,
                pageType: _vm.pageType,
                mode: _vm.mode,
              },
              on: { blockclick: _vm.handleBlockClick },
            },
            [_vm._v("\n      >\n    ")]
          ),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2Z = [];
__vue_render__$2Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$2Z = undefined;
  /* scoped */
  const __vue_scope_id__$2Z = "data-v-25b5113c";
  /* module identifier */
  const __vue_module_identifier__$2Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$2Z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2Z, staticRenderFns: __vue_staticRenderFns__$2Z },
    __vue_inject_styles__$2Z,
    __vue_script__$2Z,
    __vue_scope_id__$2Z,
    __vue_is_functional_template__$2Z,
    __vue_module_identifier__$2Z,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2Z.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-ctalog-group-render', {
    prefix: options.prefix
  }), __vue_component__$2Z);
};

//
var script$2Y = {
  name: 'XmlSingleBookGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '电子书',
      ebookDataName: 'ebookData',
      groupData: {},
      blockId: 'blockId' + Math.random()
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex: this.ruleIndex
      });
    },
    showFileReader(e) {
      this.$emit('showFileReader', e);
    }
  }
};

/* script */
const __vue_script__$2Y = script$2Y;
/* template */
var __vue_render__$2Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-ebook-group",
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-ebook-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.ebookDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.ebookDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
              isReader: _vm.isReader,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              showFileReader: _vm.showFileReader,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2Y = [];
__vue_render__$2Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$2Y = undefined;
  /* scoped */
  const __vue_scope_id__$2Y = "data-v-756e85b0";
  /* module identifier */
  const __vue_module_identifier__$2Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$2Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2Y, staticRenderFns: __vue_staticRenderFns__$2Y },
    __vue_inject_styles__$2Y,
    __vue_script__$2Y,
    __vue_scope_id__$2Y,
    __vue_is_functional_template__$2Y,
    __vue_module_identifier__$2Y,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2Y.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-ebook-group-render', {
    prefix: options.prefix
  }), __vue_component__$2Y);
};

//
var script$2X = {
  name: 'XmlSingleHtmlGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: 'html',
      ebookDataName: 'htmlData',
      groupData: {},
      blockId: 'blockId' + Math.random()
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isCopyUrl: {
      type: Boolean,
      default: false
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex: this.ruleIndex
      });
    },
    openHtml(data) {
      this.$emit('openHtml', data);
    },
    copyHtmlUrl(data) {
      this.$emit('copyHtmlUrl', data);
    }
  }
};

/* script */
const __vue_script__$2X = script$2X;
/* template */
var __vue_render__$2X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-html-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.ebookDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.ebookDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              "page-type": _vm.pageType,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
              isCopyUrl: _vm.isCopyUrl,
              isReader: _vm.isReader,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              copyHtmlUrl: _vm.copyHtmlUrl,
              openHtml: _vm.openHtml,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2X = [];
__vue_render__$2X._withStripped = true;

  /* style */
  const __vue_inject_styles__$2X = undefined;
  /* scoped */
  const __vue_scope_id__$2X = "data-v-0d2309a4";
  /* module identifier */
  const __vue_module_identifier__$2X = undefined;
  /* functional template */
  const __vue_is_functional_template__$2X = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2X, staticRenderFns: __vue_staticRenderFns__$2X },
    __vue_inject_styles__$2X,
    __vue_script__$2X,
    __vue_scope_id__$2X,
    __vue_is_functional_template__$2X,
    __vue_module_identifier__$2X,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2X.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-html-group-render', {
    prefix: options.prefix
  }), __vue_component__$2X);
};

//
var script$2W = {
  name: 'XmlSingleMusicGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '音频',
      musicDataName: 'musicData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$2W = script$2W;
/* template */
var __vue_render__$2W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-music-group",
        "xml-single-music-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-music-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.musicDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.musicDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: { blockclick: _vm.handleBlockClick },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2W = [];
__vue_render__$2W._withStripped = true;

  /* style */
  const __vue_inject_styles__$2W = undefined;
  /* scoped */
  const __vue_scope_id__$2W = "data-v-368fdaaf";
  /* module identifier */
  const __vue_module_identifier__$2W = undefined;
  /* functional template */
  const __vue_is_functional_template__$2W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2W, staticRenderFns: __vue_staticRenderFns__$2W },
    __vue_inject_styles__$2W,
    __vue_script__$2W,
    __vue_scope_id__$2W,
    __vue_is_functional_template__$2W,
    __vue_module_identifier__$2W,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2W.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-music-group-render', {
    prefix: options.prefix
  }), __vue_component__$2W);
};

//
var script$2V = {
  name: 'XmlSingleQuestionGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '题库',
      questionDataName: 'questionData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    changeAnswer() {
      this.$refs.xmlQuestion.changeAnswer();
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$2V = script$2V;
/* template */
var __vue_render__$2V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-question-group",
        "xml-single-question-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-question-render", {
            ref: "xmlQuestion",
            attrs: {
              "p-block-data": _vm.groupData[_vm.questionDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.questionDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              isShowAi: _vm.isShowAi,
              pageType: _vm.pageType,
              isSubmit:
                _vm.groupData[_vm.questionDataName].questionMode == "false"
                  ? false
                  : true,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              submitAnswer: function ($event) {
                return _vm.$emit("submitAnswer", $event)
              },
              setanalysis: function ($event) {
                return _vm.$emit("setanalysis", $event)
              },
            },
          }),
        ],
        1
      ),
      _vm._v(" "),
      _vm.groupData[_vm.questionDataName].questionMode == "false"
        ? _c(
            "div",
            {
              staticClass: "xml-submit-answer xml-submit-answer-right",
              on: {
                click: function ($event) {
                  return _vm.changeAnswer()
                },
              },
            },
            [_vm._v("\n    提交作答\n  ")]
          )
        : _vm._e(),
    ],
    1
  )
};
var __vue_staticRenderFns__$2V = [];
__vue_render__$2V._withStripped = true;

  /* style */
  const __vue_inject_styles__$2V = undefined;
  /* scoped */
  const __vue_scope_id__$2V = "data-v-30e02b9d";
  /* module identifier */
  const __vue_module_identifier__$2V = undefined;
  /* functional template */
  const __vue_is_functional_template__$2V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2V, staticRenderFns: __vue_staticRenderFns__$2V },
    __vue_inject_styles__$2V,
    __vue_script__$2V,
    __vue_scope_id__$2V,
    __vue_is_functional_template__$2V,
    __vue_module_identifier__$2V,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2V.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-question-group-render', {
    prefix: options.prefix
  }), __vue_component__$2V);
};

//
// import { OuchnOss } from '@/api/business/file/OuchnOss'
var script$2U = {
  name: 'XmlSingleResourceGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '资源',
      resourceDataName: 'resourceData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    officePreviewPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  watch: {
    pGroupData(newVal) {
      this.groupData = this.pGroupData;
    }
  },
  computed: {
    isNotLocalView() {
      return !(process.env.VUE_APP_ENV === 'localview');
    }
  },
  created() {
    this.groupData = this.pGroupData;
    // console.log(this.pGroupData, 'pGroupData---------')
    // if (this.isNotLocalView) {
    //   this.ouchnOss = new OuchnOss(true)
    // }
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    downloadFileUrl({
      item,
      fileName,
      resourceType
    }) {
      this.$emit('downloadFileUrl', {
        item,
        fileName,
        resourceType
      });
    },
    showFileReader(e) {
      this.$emit('showFileReader', e);
    }
  }
};

/* script */
const __vue_script__$2U = script$2U;
/* template */
var __vue_render__$2U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-resource-group",
        "xml-single-resource-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-resource-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.resourceDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.resourceDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
              officePreviewPath: _vm.officePreviewPath,
              isReader: _vm.isReader,
              pageType: _vm.pageType,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              downloadFileUrl: _vm.downloadFileUrl,
              showFileReader: _vm.showFileReader,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2U = [];
__vue_render__$2U._withStripped = true;

  /* style */
  const __vue_inject_styles__$2U = undefined;
  /* scoped */
  const __vue_scope_id__$2U = "data-v-2d704356";
  /* module identifier */
  const __vue_module_identifier__$2U = undefined;
  /* functional template */
  const __vue_is_functional_template__$2U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2U, staticRenderFns: __vue_staticRenderFns__$2U },
    __vue_inject_styles__$2U,
    __vue_script__$2U,
    __vue_scope_id__$2U,
    __vue_is_functional_template__$2U,
    __vue_module_identifier__$2U,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2U.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-resource-group-render', {
    prefix: options.prefix
  }), __vue_component__$2U);
};

//
var script$2T = {
  name: 'XmlSingleTestPaperGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '试卷',
      questionDataName: 'testPaperData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    reader: {
      type: Boolean,
      default: false
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    BookName: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    openTestPaper({
      info,
      showRobot
    }) {
      console.log(showRobot);
      this.$emit('openTestPaper', {
        info: info,
        showRobot: showRobot
      });
    }
  }
};

/* script */
const __vue_script__$2T = script$2T;
/* template */
var __vue_render__$2T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-test-paper-group",
        "xml-single-question-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _vm.mode === "editor"
        ? _c("span", { staticClass: "xml-group-type-text" })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            BookName: _vm.BookName,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-test-paper-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.questionDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.questionDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              reader: _vm.reader,
              pageType: _vm.pageType,
              BookName: _vm.BookName,
              isShowAi: _vm.isShowAi,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              openTestPaper: _vm.openTestPaper,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2T = [];
__vue_render__$2T._withStripped = true;

  /* style */
  const __vue_inject_styles__$2T = undefined;
  /* scoped */
  const __vue_scope_id__$2T = "data-v-eb16744a";
  /* module identifier */
  const __vue_module_identifier__$2T = undefined;
  /* functional template */
  const __vue_is_functional_template__$2T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2T, staticRenderFns: __vue_staticRenderFns__$2T },
    __vue_inject_styles__$2T,
    __vue_script__$2T,
    __vue_scope_id__$2T,
    __vue_is_functional_template__$2T,
    __vue_module_identifier__$2T,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2T.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-test-paper-group-render', {
    prefix: options.prefix
  }), __vue_component__$2T);
};

//
/**
 * 单文本组合
 */
var script$2S = {
  name: 'XmlSingleTextGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '文本',
      textDataName: 'textData',
      showComponentButtons: false,
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    ruleIndex: Number,
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    projectId: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2S = script$2S;
/* template */
var __vue_render__$2S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark text-body-mark",
      class: [
        "xml-single-text-group",
        "xml-single-text-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          staticClass: "optional-text",
          attrs: {
            "page-type": _vm.pageType,
            "type-text": _vm.typeText,
            projectId: _vm.projectId,
            mode: _vm.mode,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-text-render", {
            ref: "xmlText",
            attrs: {
              projectId: _vm.projectId,
              pBlockData: _vm.groupData[_vm.textDataName],
              "block-data-name": _vm.textDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              currentRule: _vm.currentRule,
              "css-theme": "default",
              "container-class": "text-container",
              "text-container": "default-text-container",
              resourceBasisPath: _vm.resourceBasisPath,
              mode: _vm.mode,
              "page-type": _vm.pageType,
              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
            },
            on: {
              "update:pBlockData": function ($event) {
                return _vm.$set(_vm.groupData, _vm.textDataName, $event)
              },
              "update:p-block-data": function ($event) {
                return _vm.$set(_vm.groupData, _vm.textDataName, $event)
              },
              blockclick: _vm.handleBlockClick,
              groupDataChange: _vm.groupDataChange,
            },
            scopedSlots: _vm._u(
              [
                _vm._l(_vm.$scopedSlots, function (index, name) {
                  return {
                    key: name,
                    fn: function (deliver) {
                      return [_vm._t(name, null, null, deliver)]
                    },
                  }
                }),
              ],
              null,
              true
            ),
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2S = [];
__vue_render__$2S._withStripped = true;

  /* style */
  const __vue_inject_styles__$2S = undefined;
  /* scoped */
  const __vue_scope_id__$2S = "data-v-db5a69b0";
  /* module identifier */
  const __vue_module_identifier__$2S = undefined;
  /* functional template */
  const __vue_is_functional_template__$2S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2S, staticRenderFns: __vue_staticRenderFns__$2S },
    __vue_inject_styles__$2S,
    __vue_script__$2S,
    __vue_scope_id__$2S,
    __vue_is_functional_template__$2S,
    __vue_module_identifier__$2S,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2S.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-text-group-render', {
    prefix: options.prefix
  }), __vue_component__$2S);
};

//
var script$2R = {
  name: "XmlSingleVideoGroupRender",
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: "视频",
      videoDataName: "videoData",
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return "editor";
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: "h5"
    },
    resourceBasisPath: {
      type: String,
      default: ""
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    videoImg: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit("blockclick", {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$2R = script$2R;
/* template */
var __vue_render__$2R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-video-group",
        "xml-single-video-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _vm.videoImg
            ? _c("xml-video-img-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.videoDataName],
                  "current-rule": _vm.currentRule,
                  "block-data-name": _vm.videoDataName,
                  "extend-params": _vm.extendParams,
                  "rule-index": _vm.ruleIndex,
                  resourceBasisPath: _vm.resourceBasisPath,
                  mode: _vm.mode,
                },
                on: { blockclick: _vm.handleBlockClick },
              })
            : _c("xml-video-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.videoDataName],
                  "current-rule": _vm.currentRule,
                  "block-data-name": _vm.videoDataName,
                  "extend-params": _vm.extendParams,
                  "rule-index": _vm.ruleIndex,
                  resourceBasisPath: _vm.resourceBasisPath,
                  mode: _vm.mode,
                },
                on: { blockclick: _vm.handleBlockClick },
              }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2R = [];
__vue_render__$2R._withStripped = true;

  /* style */
  const __vue_inject_styles__$2R = undefined;
  /* scoped */
  const __vue_scope_id__$2R = "data-v-ef0341ca";
  /* module identifier */
  const __vue_module_identifier__$2R = undefined;
  /* functional template */
  const __vue_is_functional_template__$2R = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2R, staticRenderFns: __vue_staticRenderFns__$2R },
    __vue_inject_styles__$2R,
    __vue_script__$2R,
    __vue_scope_id__$2R,
    __vue_is_functional_template__$2R,
    __vue_module_identifier__$2R,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2R.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-video-group-render', {
    prefix: options.prefix
  }), __vue_component__$2R);
};

//
var script$2Q = {
  name: 'XmlSingleRecorderGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '录音',
      recorderDataName: 'recorderData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    getresdata({
      blob,
      duration
    }) {
      this.$emit('getresdata', {
        blob,
        duration
      });
    }
  }
};

/* script */
const __vue_script__$2Q = script$2Q;

/* template */
var __vue_render__$2Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-recorder-group",
        "xml-single-recorder-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-recorder-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.recorderDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.recorderDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              getresdata: _vm.getresdata,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2Q = [];
__vue_render__$2Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$2Q = undefined;
  /* scoped */
  const __vue_scope_id__$2Q = undefined;
  /* module identifier */
  const __vue_module_identifier__$2Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$2Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2Q, staticRenderFns: __vue_staticRenderFns__$2Q },
    __vue_inject_styles__$2Q,
    __vue_script__$2Q,
    __vue_scope_id__$2Q,
    __vue_is_functional_template__$2Q,
    __vue_module_identifier__$2Q,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2Q.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1(__vue_component__$2Q.name, {
    prefix: options.prefix
  }), __vue_component__$2Q);
};

//
var script$2P = {
  name: 'XmlSingleVoiceGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '录音',
      recorderDataName: 'recorderData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    sourcedata: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  watch: {
    currentRule: {
      handler(newValue) {
        this.groupData = newValue.data.pGroupData;
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.groupData = this.pGroupData;
    console.log(this.sourcedata, 'group页');
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    getresdata({
      blob,
      duration
    }) {
      this.$emit('getresdata', {
        blob,
        duration
      });
    },
    ratedata({
      ratingData,
      ruleIndex,
      dataName
    }) {
      this.$emit('ratedata', {
        ratingData: ratingData,
        ruleIndex: ruleIndex,
        dataName: dataName
      });
    }
  }
};

/* script */
const __vue_script__$2P = script$2P;

/* template */
var __vue_render__$2P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-recorder-group",
        "xml-single-recorder-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-voice-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.recorderDataName],
              voicedata: _vm.groupData,
              sourcedata: _vm.sourcedata,
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.recorderDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
              "page-type": _vm.pageType,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              getresdata: _vm.getresdata,
              ratedata: _vm.ratedata,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2P = [];
__vue_render__$2P._withStripped = true;

  /* style */
  const __vue_inject_styles__$2P = undefined;
  /* scoped */
  const __vue_scope_id__$2P = undefined;
  /* module identifier */
  const __vue_module_identifier__$2P = undefined;
  /* functional template */
  const __vue_is_functional_template__$2P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2P, staticRenderFns: __vue_staticRenderFns__$2P },
    __vue_inject_styles__$2P,
    __vue_script__$2P,
    __vue_scope_id__$2P,
    __vue_is_functional_template__$2P,
    __vue_module_identifier__$2P,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2P.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1(__vue_component__$2P.name, {
    prefix: options.prefix
  }), __vue_component__$2P);
};

//
/**
 * 热点组
 */
var script$2O = {
  name: 'XmlSingleHotzoneGroupRender',
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    max: {
      type: Number,
      default: 10
    }
  },
  provide() {
    return {
      getSelfRuleIndex: () => {
        return this.ruleIndex;
      }
    };
  },
  data() {
    return {
      typeText: '热区',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      blockDataKey: 'xmlBlockData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  watch: {
    pGroupData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.groupData = Object.assign({}, this.groupData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      console.log('触发');
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    remove(curZone, index) {
      this.$emit('remove', curZone, index);
    }
  }
};

/* script */
const __vue_script__$2O = script$2O;

/* template */
var __vue_render__$2O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-hotzone-group",
        "xml-single-hotzone-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": "pc",
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-hotzone-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.blockDataKey],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.blockDataKey,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              "show-title": true,
              mode: _vm.mode,
              max: _vm.max,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: {
              blockclick: _vm.handleBlockClick,
              "wrap-click": _vm.handleBlockClick,
              remove: _vm.remove,
            },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2O = [];
__vue_render__$2O._withStripped = true;

  /* style */
  const __vue_inject_styles__$2O = undefined;
  /* scoped */
  const __vue_scope_id__$2O = undefined;
  /* module identifier */
  const __vue_module_identifier__$2O = undefined;
  /* functional template */
  const __vue_is_functional_template__$2O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2O, staticRenderFns: __vue_staticRenderFns__$2O },
    __vue_inject_styles__$2O,
    __vue_script__$2O,
    __vue_scope_id__$2O,
    __vue_is_functional_template__$2O,
    __vue_module_identifier__$2O,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2O.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-hotzone-group-render', {
    prefix: options.prefix
  }), __vue_component__$2O);
};

//
/**
 * 热点组
 */
var script$2N = {
  name: 'XmlSingleLayoutGroupRender',
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    // activeIndex: {
    //   type: Number,
    //   default: -1
    // },
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    max: {
      type: Number,
      default: 10
    }
  },
  provide() {
    return {
      getSelfRuleIndex: () => {
        return this.ruleIndex;
      }
    };
  },
  data() {
    return {
      typeText: '分栏布局',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      blockDataKey: 'xmlBlockData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  // watch: {
  //   pGroupData: {
  //     handler(newValue) {
  //       this.$nextTick(() => {
  //         this.groupData = Object.assign({}, this.groupData, newValue)
  //       })
  //     },
  //     deep: true,
  //     immediate: true
  //   }
  // },

  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      console.log('触发块点击');
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$2N = script$2N;

/* template */
var __vue_render__$2N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-layout-group",
        "xml-single-layout-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "xml-layout-render",
            {
              attrs: {
                "p-block-data": _vm.groupData[_vm.blockDataKey],
                "current-rule": _vm.currentRule,
                blockDataName: _vm.blockDataKey,
                "extend-params": _vm.extendParams,
                "rule-index": _vm.ruleIndex,
                "show-title": true,
                mode: _vm.mode,
                max: _vm.max,
                bindingComponents: _vm.currentRule.bindingComponents,
                resourceBasisPath: _vm.resourceBasisPath,
              },
              on: { blockclick: _vm.handleBlockClick },
            },
            [
              _vm.$slots.injectComponents
                ? [_vm._t("injectComponents")]
                : _vm._e(),
            ],
            2
          ),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2N = [];
__vue_render__$2N._withStripped = true;

  /* style */
  const __vue_inject_styles__$2N = undefined;
  /* scoped */
  const __vue_scope_id__$2N = undefined;
  /* module identifier */
  const __vue_module_identifier__$2N = undefined;
  /* functional template */
  const __vue_is_functional_template__$2N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2N, staticRenderFns: __vue_staticRenderFns__$2N },
    __vue_inject_styles__$2N,
    __vue_script__$2N,
    __vue_scope_id__$2N,
    __vue_is_functional_template__$2N,
    __vue_module_identifier__$2N,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2N.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-layout-group-render', {
    prefix: options.prefix
  }), __vue_component__$2N);
};

const needWrapComponents = ['xml-single-text-group', 'imageTextFirst', 'imageTextSecond', 'imageTextThird', 'imageTextFourth', 'imageTextFifth', 'imageTextSixth', 'imageTextSeventh', 'titleFirst', 'titleSecond', 'titleThird', 'titleHexagon', 'titleFifth', 'titleSixth', 'titleTriangle', 'titleRectangle', 'titleRotundity', 'titleSquare', 'titleSemicircle', 'titleTwelfth', 'titlethirteenth', 'titlefourteen', 'titleFifteen', 'titleSixteen', 'titleSeventeen', 'titleEighteen', 'titleNineteen', 'titleGreentheme1', 'titleGreentheme2', 'titleGreentheme3', 'titleGreentheme4', 'titleGreentheme5', 'titleGreentheme6', 'titleYellowtheme1', 'titleYellowtheme2', 'titleYellowtheme3', 'titleYellowtheme4', 'titleYellowtheme5', 'titleYellowtheme6', 'titleKnowledge', 'paragraphFirst', 'paragraphSecond', 'paragraphThird', 'paragraphQuadrangle', 'paragraphIsosceles', 'paragrapheighth', 'paragraphninth', 'paragraphtenth', 'paragraphWave', 'paragraphSeventh', 'paragraphEleven', 'paragraphTwelve', 'paragraphThirteen', 'paragraphFourteen', 'paragraphFifteen', 'paragraphGreentheme2', 'paragraphGreentheme3', 'paragraphGreentheme4', 'paragraphGreentheme5', 'paragraphYellowtheme1', 'paragraphYellowtheme2', 'paragraphYellowtheme3', 'paragraphYellowtheme4', 'paragraphYellowtheme5', 'paragraphRedtheme1', 'paragraphRedtheme2', 'paragraphRedtheme3', 'paragraphRedtheme4', 'paragraphRedtheme5', 'paragraphRedtheme6', 'paragraphSixteen', 'splitlineFirst', 'splitlineSecond', 'splitlineThird', 'splitlineFourth', 'splitlineFifth', 'splitlineSixth', 'splitlineSeventh', 'splitlineEighth', 'splitlineNinth', 'splitlineTenth', 'splitlineEleventh', 'splitlineGreytheme1', 'splitlineGreytheme2', 'splitlineGreytheme3', 'splitlineGreytheme4', 'splitlineGreytheme5', 'splitlineGreytheme6', 'splitlineYellowtheme1', 'splitlineYellowtheme2', 'splitlineYellowtheme3', 'splitlineYellowtheme4', 'splitlineRedtheme1', 'splitlineRedtheme2', 'splitlineRedtheme3', 'splitlineRedtheme4', 'splitlineGreentheme1', 'splitlineGreentheme2', 'splitlineGreentheme3', 'splitlineGreentheme4', 'splitlineBluetheme1', 'splitlineBluetheme2', 'splitlineBluetheme3', 'splitlineBluetheme4', 'titleThirdLibrary', 'titleFirstLibrary', 'titleSixthLibrary', 'titleRectangleLibrary', 'titleSemicircleLibrary', 'titleYellowtheme1Library', 'titleYellowtheme2Library', 'titleYellowtheme4Library', 'titleYellowtheme5Library', 'titleYellowtheme6Library', 'titleThirteenthLibrary', 'titleTwelfthLibrary', 'titleGreentheme1Library', 'titleGreentheme2Library', 'titleGreentheme3Library', 'titleGreentheme4Library', 'titleGreentheme6Library', 'titleNineteenLibrary', 'titleHexagonLibrary', 'titleFourteenLibrary', 'titleSeventeenLibrary', 'titleSixteenLibrary', 'titleFifteenLibrary', 'titleKnowledgeLibrary', 'titleFifthLibrary', 'titleTriangleLibrary', 'paragraphFirstLibrary', 'paragraphSecondLibrary', 'paragraphThirdLibrary', 'paragraphQuadrangleLibrary', 'paragraphIsoscelesLibrary', 'paragraphNinthLibrary', 'paragraphEighthLibrary', 'paragraphTenthLibrary', 'paragraphSixteenLibrary', 'paragraphSeventhLibrary', 'paragraphElevenLibrary', 'paragraphThirteenLibrary', 'paragraphFourteenLibrary', 'paragraphGreentheme2Library', 'paragraphGreentheme3Library', 'paragraphGreentheme4Library', 'paragraphGreentheme5Library', 'paragraphYellowtheme1Library', 'paragraphYellowtheme2Library', 'paragraphYellowtheme3Library', 'paragraphYellowtheme4Library', 'paragraphYellowtheme5Library', 'paragraphRedtheme1Library', 'paragraphRedtheme2Library', 'paragraphRedtheme3Library', 'paragraphRedtheme4Library', 'paragraphRedtheme5Library', 'imageTextFirstLibrary', 'imageTextSecondLibrary', 'imageTextThirdLibrary', 'imageTextFourthLibrary', 'imageTextFifthLibrary', 'imageTextSixthLibrary', 'imageTextSeventhLibrary', 'splitlineFirstLibrary', 'splitlineSecondLibrary', 'splitlineFourthLibrary', 'splitlineFifthLibrary', 'splitlineSixthLibrary', 'splitlineSeventhLibrary', 'splitlineEighthLibrary', 'splitlineTenthLibrary', 'splitlineEleventhLibrary', 'splitlineNinthLibrary', 'splitlineRedtheme1Library', 'splitlineGreytheme2Library', 'splitlineGreytheme3Library', 'splitlineGreytheme5Library', 'splitlineYellowtheme3Library', 'splitlineBluetheme1Library', 'splitlineBluetheme2Library', 'splitlineYellowtheme2Library', 'splitlineRedtheme4Library', 'splitlineYellowtheme1Library', 'splitlineGreytheme1Library', 'splitlineBluetheme4Library', 'titleRedtheme1Library', 'titleRedtheme2Library', 'titleRedtheme3Library', 'titleRedtheme4Library', 'paragraphRedtheme6Library', 'paragraphRedtheme7Library', 'paragraphFoldtheme1Library', 'paragraphFoldtheme2Library', 'paragraphFoldtheme3Library', 'paragraphTabstheme1Library', 'paragraphTabstheme2Library', 'paragraphTabstheme3Library', 'paragraphTabstheme4Library', 'titleSanQintheme1', 'titleSanQintheme2', 'titleSanQintheme3', 'paragraphSanQintheme1', 'paragraphSanQintheme2', 'paragraphSanQintheme3', 'paragraphSanQintheme1Library', 'paragraphTwelveLibrary', 'titleThirtyoneLibrary', 'titleThirtytwoLibrary', 'titleThirtythreeLibrary', 'titleThirtyfourLibrary', 'titleThirtyfiveLibrary', 'titleThirtysixLibrary', 'titleThirtysevenLibrary', 'titleThirtyeightLibrary', 'paragraphFoldtheme4Library', 'paragraphFoldtheme5Library', 'paragraphTabstheme5Library', 'paragraphTabstheme6Library', 'paragraphFortytwoLibrary', 'paragraphFortythreeLibrary', 'paragraphFortyfourLibrary', 'paragraphFortyfiveLibrary'];
const toKebabCase = value => {
  if (allCapital(value)) {
    return value.toLocaleLowerCase();
  }
  return value.charAt(0).toLowerCase() + value.slice(1).replace(/([A-Z])/g, '-$1').toLowerCase();
};
const isComponentNameNeedEditorWrap = name => {
  if (needWrapComponents.includes(name)) {
    let formated;
    if (name.includes('-')) {
      formated = name + '-editor';
    } else {
      formated = toKebabCase(name) + '-editor';
    }
    return formated;
  }
  return name + '-render';
};

//
/**
 * SingleGroup代理
 */
var script$2M = {
  name: 'XmlSingleAgentGroupRender',
  components: {
    Portal
  },
  props: {
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      // typeText: '分栏布局',
      // //定义此变量，是为了解决页面中有多个块儿的情况。
      // blockDataKey: 'xmlBlockData',
      // groupData: {}
    };
  },
  computed: {
    isInjectSingleGroup() {
      return !!this.currentRule.trueComponentsName && !!this.currentRule.injectTarget;
    }
  },
  // created() {
  // },
  // watch: {
  //   pGroupData: {
  //     handler(newValue) {
  //       this.$nextTick(() => {
  //         this.groupData = Object.assign({}, this.groupData, newValue)
  //       })
  //     },
  //     deep: true,
  //     immediate: true
  //   }
  // },

  mounted() {},
  methods: {
    isComponentNameNeedEditorWrap,
    forwordAreaboxInjectClick(e) {
      this.$emit('agentClick', this.currentRule);
    }
  }
};

/* script */
const __vue_script__$2M = script$2M;
/* template */
var __vue_render__$2M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Portal",
    {
      staticClass: "parent-group parent-group-mark xml-single-agent-group",
      attrs: { to: this.currentRule.injectTarget },
    },
    [
      _c(
        _vm.isComponentNameNeedEditorWrap(_vm.currentRule.trueComponentsName),
        _vm._g(
          _vm._b(
            {
              tag: "component",
              attrs: {
                currentRule: _vm.currentRule,
                scopedSlots: { $scopedSlots: _vm.$scopedSlots },
              },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.forwordAreaboxInjectClick.apply(null, arguments)
                },
              },
              scopedSlots: _vm._u(
                [
                  _vm._l(_vm.$scopedSlots, function (index, name) {
                    return {
                      key: name,
                      fn: function (deliver) {
                        return [_vm._t(name, null, null, deliver)]
                      },
                    }
                  }),
                ],
                null,
                true
              ),
            },
            "component",
            _vm.$attrs,
            false
          ),
          _vm.$listeners
        )
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2M = [];
__vue_render__$2M._withStripped = true;

  /* style */
  const __vue_inject_styles__$2M = undefined;
  /* scoped */
  const __vue_scope_id__$2M = "data-v-016fa4bd";
  /* module identifier */
  const __vue_module_identifier__$2M = undefined;
  /* functional template */
  const __vue_is_functional_template__$2M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2M, staticRenderFns: __vue_staticRenderFns__$2M },
    __vue_inject_styles__$2M,
    __vue_script__$2M,
    __vue_scope_id__$2M,
    __vue_is_functional_template__$2M,
    __vue_module_identifier__$2M,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2M.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-agent-group-render', {
    prefix: options.prefix
  }), __vue_component__$2M);
};

//
var script$2L = {
  name: 'XmlSingleSkeletonGroupRender',
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: '录音',
      recorderDataName: 'recorderData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    sourcedata: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    pageHeight: {
      type: Number,
      default: 600
    }
  },
  watch: {
    currentRule: {
      handler(newValue) {
        this.groupData = newValue.data.pGroupData;
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.groupData = this.pGroupData;
    // console.log(this.sourcedata,'group页');
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    getresdata({
      blob,
      duration
    }) {
      this.$emit('getresdata', {
        blob,
        duration
      });
    },
    ratedata({
      ratingData,
      ruleIndex,
      dataName
    }) {
      this.$emit('ratedata', {
        ratingData: ratingData,
        ruleIndex: ruleIndex,
        dataName: dataName
      });
    }
  }
};

/* script */
const __vue_script__$2L = script$2L;
/* template */
var __vue_render__$2L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-recorder-group",
        "xml-single-recorder-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: {
        height: _vm.pageHeight + "px",
        overflow: "hidden",
      },
      on: { click: _vm.emitClick },
    },
    [_c("div", { staticClass: "xml-skeleton-item" })]
  )
};
var __vue_staticRenderFns__$2L = [];
__vue_render__$2L._withStripped = true;

  /* style */
  const __vue_inject_styles__$2L = undefined;
  /* scoped */
  const __vue_scope_id__$2L = undefined;
  /* module identifier */
  const __vue_module_identifier__$2L = undefined;
  /* functional template */
  const __vue_is_functional_template__$2L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2L, staticRenderFns: __vue_staticRenderFns__$2L },
    __vue_inject_styles__$2L,
    __vue_script__$2L,
    __vue_scope_id__$2L,
    __vue_is_functional_template__$2L,
    __vue_module_identifier__$2L,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2L.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1(__vue_component__$2L.name, {
    prefix: options.prefix
  }), __vue_component__$2L);
};

// This file is autogenerated. It's used to publish ESM to npm.
function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

// https://github.com/bgrins/TinyColor
// Brian Grinstead, MIT License

var trimLeft = /^\s+/;
var trimRight = /\s+$/;
function tinycolor(color, opts) {
  color = color ? color : "";
  opts = opts || {};

  // If input is already a tinycolor, return itself
  if (color instanceof tinycolor) {
    return color;
  }
  // If we are called as a function, call using new instead
  if (!(this instanceof tinycolor)) {
    return new tinycolor(color, opts);
  }
  var rgb = inputToRGB(color);
  this._originalInput = color, this._r = rgb.r, this._g = rgb.g, this._b = rgb.b, this._a = rgb.a, this._roundA = Math.round(100 * this._a) / 100, this._format = opts.format || rgb.format;
  this._gradientType = opts.gradientType;

  // Don't let the range of [0,255] come back in [0,1].
  // Potentially lose a little bit of precision here, but will fix issues where
  // .5 gets interpreted as half of the total, instead of half of 1
  // If it was supposed to be 128, this was already taken care of by `inputToRgb`
  if (this._r < 1) this._r = Math.round(this._r);
  if (this._g < 1) this._g = Math.round(this._g);
  if (this._b < 1) this._b = Math.round(this._b);
  this._ok = rgb.ok;
}
tinycolor.prototype = {
  isDark: function isDark() {
    return this.getBrightness() < 128;
  },
  isLight: function isLight() {
    return !this.isDark();
  },
  isValid: function isValid() {
    return this._ok;
  },
  getOriginalInput: function getOriginalInput() {
    return this._originalInput;
  },
  getFormat: function getFormat() {
    return this._format;
  },
  getAlpha: function getAlpha() {
    return this._a;
  },
  getBrightness: function getBrightness() {
    //http://www.w3.org/TR/AERT#color-contrast
    var rgb = this.toRgb();
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  },
  getLuminance: function getLuminance() {
    //http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
    var rgb = this.toRgb();
    var RsRGB, GsRGB, BsRGB, R, G, B;
    RsRGB = rgb.r / 255;
    GsRGB = rgb.g / 255;
    BsRGB = rgb.b / 255;
    if (RsRGB <= 0.03928) R = RsRGB / 12.92;else R = Math.pow((RsRGB + 0.055) / 1.055, 2.4);
    if (GsRGB <= 0.03928) G = GsRGB / 12.92;else G = Math.pow((GsRGB + 0.055) / 1.055, 2.4);
    if (BsRGB <= 0.03928) B = BsRGB / 12.92;else B = Math.pow((BsRGB + 0.055) / 1.055, 2.4);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  },
  setAlpha: function setAlpha(value) {
    this._a = boundAlpha(value);
    this._roundA = Math.round(100 * this._a) / 100;
    return this;
  },
  toHsv: function toHsv() {
    var hsv = rgbToHsv(this._r, this._g, this._b);
    return {
      h: hsv.h * 360,
      s: hsv.s,
      v: hsv.v,
      a: this._a
    };
  },
  toHsvString: function toHsvString() {
    var hsv = rgbToHsv(this._r, this._g, this._b);
    var h = Math.round(hsv.h * 360),
      s = Math.round(hsv.s * 100),
      v = Math.round(hsv.v * 100);
    return this._a == 1 ? "hsv(" + h + ", " + s + "%, " + v + "%)" : "hsva(" + h + ", " + s + "%, " + v + "%, " + this._roundA + ")";
  },
  toHsl: function toHsl() {
    var hsl = rgbToHsl(this._r, this._g, this._b);
    return {
      h: hsl.h * 360,
      s: hsl.s,
      l: hsl.l,
      a: this._a
    };
  },
  toHslString: function toHslString() {
    var hsl = rgbToHsl(this._r, this._g, this._b);
    var h = Math.round(hsl.h * 360),
      s = Math.round(hsl.s * 100),
      l = Math.round(hsl.l * 100);
    return this._a == 1 ? "hsl(" + h + ", " + s + "%, " + l + "%)" : "hsla(" + h + ", " + s + "%, " + l + "%, " + this._roundA + ")";
  },
  toHex: function toHex(allow3Char) {
    return rgbToHex(this._r, this._g, this._b, allow3Char);
  },
  toHexString: function toHexString(allow3Char) {
    return "#" + this.toHex(allow3Char);
  },
  toHex8: function toHex8(allow4Char) {
    return rgbaToHex(this._r, this._g, this._b, this._a, allow4Char);
  },
  toHex8String: function toHex8String(allow4Char) {
    return "#" + this.toHex8(allow4Char);
  },
  toRgb: function toRgb() {
    return {
      r: Math.round(this._r),
      g: Math.round(this._g),
      b: Math.round(this._b),
      a: this._a
    };
  },
  toRgbString: function toRgbString() {
    return this._a == 1 ? "rgb(" + Math.round(this._r) + ", " + Math.round(this._g) + ", " + Math.round(this._b) + ")" : "rgba(" + Math.round(this._r) + ", " + Math.round(this._g) + ", " + Math.round(this._b) + ", " + this._roundA + ")";
  },
  toPercentageRgb: function toPercentageRgb() {
    return {
      r: Math.round(bound01(this._r, 255) * 100) + "%",
      g: Math.round(bound01(this._g, 255) * 100) + "%",
      b: Math.round(bound01(this._b, 255) * 100) + "%",
      a: this._a
    };
  },
  toPercentageRgbString: function toPercentageRgbString() {
    return this._a == 1 ? "rgb(" + Math.round(bound01(this._r, 255) * 100) + "%, " + Math.round(bound01(this._g, 255) * 100) + "%, " + Math.round(bound01(this._b, 255) * 100) + "%)" : "rgba(" + Math.round(bound01(this._r, 255) * 100) + "%, " + Math.round(bound01(this._g, 255) * 100) + "%, " + Math.round(bound01(this._b, 255) * 100) + "%, " + this._roundA + ")";
  },
  toName: function toName() {
    if (this._a === 0) {
      return "transparent";
    }
    if (this._a < 1) {
      return false;
    }
    return hexNames[rgbToHex(this._r, this._g, this._b, true)] || false;
  },
  toFilter: function toFilter(secondColor) {
    var hex8String = "#" + rgbaToArgbHex(this._r, this._g, this._b, this._a);
    var secondHex8String = hex8String;
    var gradientType = this._gradientType ? "GradientType = 1, " : "";
    if (secondColor) {
      var s = tinycolor(secondColor);
      secondHex8String = "#" + rgbaToArgbHex(s._r, s._g, s._b, s._a);
    }
    return "progid:DXImageTransform.Microsoft.gradient(" + gradientType + "startColorstr=" + hex8String + ",endColorstr=" + secondHex8String + ")";
  },
  toString: function toString(format) {
    var formatSet = !!format;
    format = format || this._format;
    var formattedString = false;
    var hasAlpha = this._a < 1 && this._a >= 0;
    var needsAlphaFormat = !formatSet && hasAlpha && (format === "hex" || format === "hex6" || format === "hex3" || format === "hex4" || format === "hex8" || format === "name");
    if (needsAlphaFormat) {
      // Special case for "transparent", all other non-alpha formats
      // will return rgba when there is transparency.
      if (format === "name" && this._a === 0) {
        return this.toName();
      }
      return this.toRgbString();
    }
    if (format === "rgb") {
      formattedString = this.toRgbString();
    }
    if (format === "prgb") {
      formattedString = this.toPercentageRgbString();
    }
    if (format === "hex" || format === "hex6") {
      formattedString = this.toHexString();
    }
    if (format === "hex3") {
      formattedString = this.toHexString(true);
    }
    if (format === "hex4") {
      formattedString = this.toHex8String(true);
    }
    if (format === "hex8") {
      formattedString = this.toHex8String();
    }
    if (format === "name") {
      formattedString = this.toName();
    }
    if (format === "hsl") {
      formattedString = this.toHslString();
    }
    if (format === "hsv") {
      formattedString = this.toHsvString();
    }
    return formattedString || this.toHexString();
  },
  clone: function clone() {
    return tinycolor(this.toString());
  },
  _applyModification: function _applyModification(fn, args) {
    var color = fn.apply(null, [this].concat([].slice.call(args)));
    this._r = color._r;
    this._g = color._g;
    this._b = color._b;
    this.setAlpha(color._a);
    return this;
  },
  lighten: function lighten() {
    return this._applyModification(_lighten, arguments);
  },
  brighten: function brighten() {
    return this._applyModification(_brighten, arguments);
  },
  darken: function darken() {
    return this._applyModification(_darken, arguments);
  },
  desaturate: function desaturate() {
    return this._applyModification(_desaturate, arguments);
  },
  saturate: function saturate() {
    return this._applyModification(_saturate, arguments);
  },
  greyscale: function greyscale() {
    return this._applyModification(_greyscale, arguments);
  },
  spin: function spin() {
    return this._applyModification(_spin, arguments);
  },
  _applyCombination: function _applyCombination(fn, args) {
    return fn.apply(null, [this].concat([].slice.call(args)));
  },
  analogous: function analogous() {
    return this._applyCombination(_analogous, arguments);
  },
  complement: function complement() {
    return this._applyCombination(_complement, arguments);
  },
  monochromatic: function monochromatic() {
    return this._applyCombination(_monochromatic, arguments);
  },
  splitcomplement: function splitcomplement() {
    return this._applyCombination(_splitcomplement, arguments);
  },
  // Disabled until https://github.com/bgrins/TinyColor/issues/254
  // polyad: function (number) {
  //   return this._applyCombination(polyad, [number]);
  // },
  triad: function triad() {
    return this._applyCombination(polyad, [3]);
  },
  tetrad: function tetrad() {
    return this._applyCombination(polyad, [4]);
  }
};

// If input is an object, force 1 into "1.0" to handle ratios properly
// String input requires "1.0" as input, so 1 will be treated as 1
tinycolor.fromRatio = function (color, opts) {
  if (_typeof(color) == "object") {
    var newColor = {};
    for (var i in color) {
      if (color.hasOwnProperty(i)) {
        if (i === "a") {
          newColor[i] = color[i];
        } else {
          newColor[i] = convertToPercentage(color[i]);
        }
      }
    }
    color = newColor;
  }
  return tinycolor(color, opts);
};

// Given a string or object, convert that input to RGB
// Possible string inputs:
//
//     "red"
//     "#f00" or "f00"
//     "#ff0000" or "ff0000"
//     "#ff000000" or "ff000000"
//     "rgb 255 0 0" or "rgb (255, 0, 0)"
//     "rgb 1.0 0 0" or "rgb (1, 0, 0)"
//     "rgba (255, 0, 0, 1)" or "rgba 255, 0, 0, 1"
//     "rgba (1.0, 0, 0, 1)" or "rgba 1.0, 0, 0, 1"
//     "hsl(0, 100%, 50%)" or "hsl 0 100% 50%"
//     "hsla(0, 100%, 50%, 1)" or "hsla 0 100% 50%, 1"
//     "hsv(0, 100%, 100%)" or "hsv 0 100% 100%"
//
function inputToRGB(color) {
  var rgb = {
    r: 0,
    g: 0,
    b: 0
  };
  var a = 1;
  var s = null;
  var v = null;
  var l = null;
  var ok = false;
  var format = false;
  if (typeof color == "string") {
    color = stringInputToObject(color);
  }
  if (_typeof(color) == "object") {
    if (isValidCSSUnit(color.r) && isValidCSSUnit(color.g) && isValidCSSUnit(color.b)) {
      rgb = rgbToRgb(color.r, color.g, color.b);
      ok = true;
      format = String(color.r).substr(-1) === "%" ? "prgb" : "rgb";
    } else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.v)) {
      s = convertToPercentage(color.s);
      v = convertToPercentage(color.v);
      rgb = hsvToRgb(color.h, s, v);
      ok = true;
      format = "hsv";
    } else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.l)) {
      s = convertToPercentage(color.s);
      l = convertToPercentage(color.l);
      rgb = hslToRgb(color.h, s, l);
      ok = true;
      format = "hsl";
    }
    if (color.hasOwnProperty("a")) {
      a = color.a;
    }
  }
  a = boundAlpha(a);
  return {
    ok: ok,
    format: color.format || format,
    r: Math.min(255, Math.max(rgb.r, 0)),
    g: Math.min(255, Math.max(rgb.g, 0)),
    b: Math.min(255, Math.max(rgb.b, 0)),
    a: a
  };
}

// Conversion Functions
// --------------------

// `rgbToHsl`, `rgbToHsv`, `hslToRgb`, `hsvToRgb` modified from:
// <http://mjijackson.com/2008/02/rgb-to-hsl-and-rgb-to-hsv-color-model-conversion-algorithms-in-javascript>

// `rgbToRgb`
// Handle bounds / percentage checking to conform to CSS color spec
// <http://www.w3.org/TR/css3-color/>
// *Assumes:* r, g, b in [0, 255] or [0, 1]
// *Returns:* { r, g, b } in [0, 255]
function rgbToRgb(r, g, b) {
  return {
    r: bound01(r, 255) * 255,
    g: bound01(g, 255) * 255,
    b: bound01(b, 255) * 255
  };
}

// `rgbToHsl`
// Converts an RGB color value to HSL.
// *Assumes:* r, g, and b are contained in [0, 255] or [0, 1]
// *Returns:* { h, s, l } in [0,1]
function rgbToHsl(r, g, b) {
  r = bound01(r, 255);
  g = bound01(g, 255);
  b = bound01(b, 255);
  var max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  var h,
    s,
    l = (max + min) / 2;
  if (max == min) {
    h = s = 0; // achromatic
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return {
    h: h,
    s: s,
    l: l
  };
}

// `hslToRgb`
// Converts an HSL color value to RGB.
// *Assumes:* h is contained in [0, 1] or [0, 360] and s and l are contained [0, 1] or [0, 100]
// *Returns:* { r, g, b } in the set [0, 255]
function hslToRgb(h, s, l) {
  var r, g, b;
  h = bound01(h, 360);
  s = bound01(s, 100);
  l = bound01(l, 100);
  function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: r * 255,
    g: g * 255,
    b: b * 255
  };
}

// `rgbToHsv`
// Converts an RGB color value to HSV
// *Assumes:* r, g, and b are contained in the set [0, 255] or [0, 1]
// *Returns:* { h, s, v } in [0,1]
function rgbToHsv(r, g, b) {
  r = bound01(r, 255);
  g = bound01(g, 255);
  b = bound01(b, 255);
  var max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  var h,
    s,
    v = max;
  var d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max == min) {
    h = 0; // achromatic
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return {
    h: h,
    s: s,
    v: v
  };
}

// `hsvToRgb`
// Converts an HSV color value to RGB.
// *Assumes:* h is contained in [0, 1] or [0, 360] and s and v are contained in [0, 1] or [0, 100]
// *Returns:* { r, g, b } in the set [0, 255]
function hsvToRgb(h, s, v) {
  h = bound01(h, 360) * 6;
  s = bound01(s, 100);
  v = bound01(v, 100);
  var i = Math.floor(h),
    f = h - i,
    p = v * (1 - s),
    q = v * (1 - f * s),
    t = v * (1 - (1 - f) * s),
    mod = i % 6,
    r = [v, q, p, p, t, v][mod],
    g = [t, v, v, q, p, p][mod],
    b = [p, p, t, v, v, q][mod];
  return {
    r: r * 255,
    g: g * 255,
    b: b * 255
  };
}

// `rgbToHex`
// Converts an RGB color to hex
// Assumes r, g, and b are contained in the set [0, 255]
// Returns a 3 or 6 character hex
function rgbToHex(r, g, b, allow3Char) {
  var hex = [pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16))];

  // Return a 3 character hex if possible
  if (allow3Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1)) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0);
  }
  return hex.join("");
}

// `rgbaToHex`
// Converts an RGBA color plus alpha transparency to hex
// Assumes r, g, b are contained in the set [0, 255] and
// a in [0, 1]. Returns a 4 or 8 character rgba hex
function rgbaToHex(r, g, b, a, allow4Char) {
  var hex = [pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16)), pad2(convertDecimalToHex(a))];

  // Return a 4 character hex if possible
  if (allow4Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1) && hex[3].charAt(0) == hex[3].charAt(1)) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0) + hex[3].charAt(0);
  }
  return hex.join("");
}

// `rgbaToArgbHex`
// Converts an RGBA color to an ARGB Hex8 string
// Rarely used, but required for "toFilter()"
function rgbaToArgbHex(r, g, b, a) {
  var hex = [pad2(convertDecimalToHex(a)), pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16))];
  return hex.join("");
}

// `equals`
// Can be called with any tinycolor input
tinycolor.equals = function (color1, color2) {
  if (!color1 || !color2) return false;
  return tinycolor(color1).toRgbString() == tinycolor(color2).toRgbString();
};
tinycolor.random = function () {
  return tinycolor.fromRatio({
    r: Math.random(),
    g: Math.random(),
    b: Math.random()
  });
};

// Modification Functions
// ----------------------
// Thanks to less.js for some of the basics here
// <https://github.com/cloudhead/less.js/blob/master/lib/less/functions.js>

function _desaturate(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.s -= amount / 100;
  hsl.s = clamp01(hsl.s);
  return tinycolor(hsl);
}
function _saturate(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.s += amount / 100;
  hsl.s = clamp01(hsl.s);
  return tinycolor(hsl);
}
function _greyscale(color) {
  return tinycolor(color).desaturate(100);
}
function _lighten(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.l += amount / 100;
  hsl.l = clamp01(hsl.l);
  return tinycolor(hsl);
}
function _brighten(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var rgb = tinycolor(color).toRgb();
  rgb.r = Math.max(0, Math.min(255, rgb.r - Math.round(255 * -(amount / 100))));
  rgb.g = Math.max(0, Math.min(255, rgb.g - Math.round(255 * -(amount / 100))));
  rgb.b = Math.max(0, Math.min(255, rgb.b - Math.round(255 * -(amount / 100))));
  return tinycolor(rgb);
}
function _darken(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.l -= amount / 100;
  hsl.l = clamp01(hsl.l);
  return tinycolor(hsl);
}

// Spin takes a positive or negative amount within [-360, 360] indicating the change of hue.
// Values outside of this range will be wrapped into this range.
function _spin(color, amount) {
  var hsl = tinycolor(color).toHsl();
  var hue = (hsl.h + amount) % 360;
  hsl.h = hue < 0 ? 360 + hue : hue;
  return tinycolor(hsl);
}

// Combination Functions
// ---------------------
// Thanks to jQuery xColor for some of the ideas behind these
// <https://github.com/infusion/jQuery-xcolor/blob/master/jquery.xcolor.js>

function _complement(color) {
  var hsl = tinycolor(color).toHsl();
  hsl.h = (hsl.h + 180) % 360;
  return tinycolor(hsl);
}
function polyad(color, number) {
  if (isNaN(number) || number <= 0) {
    throw new Error("Argument to polyad must be a positive number");
  }
  var hsl = tinycolor(color).toHsl();
  var result = [tinycolor(color)];
  var step = 360 / number;
  for (var i = 1; i < number; i++) {
    result.push(tinycolor({
      h: (hsl.h + i * step) % 360,
      s: hsl.s,
      l: hsl.l
    }));
  }
  return result;
}
function _splitcomplement(color) {
  var hsl = tinycolor(color).toHsl();
  var h = hsl.h;
  return [tinycolor(color), tinycolor({
    h: (h + 72) % 360,
    s: hsl.s,
    l: hsl.l
  }), tinycolor({
    h: (h + 216) % 360,
    s: hsl.s,
    l: hsl.l
  })];
}
function _analogous(color, results, slices) {
  results = results || 6;
  slices = slices || 30;
  var hsl = tinycolor(color).toHsl();
  var part = 360 / slices;
  var ret = [tinycolor(color)];
  for (hsl.h = (hsl.h - (part * results >> 1) + 720) % 360; --results;) {
    hsl.h = (hsl.h + part) % 360;
    ret.push(tinycolor(hsl));
  }
  return ret;
}
function _monochromatic(color, results) {
  results = results || 6;
  var hsv = tinycolor(color).toHsv();
  var h = hsv.h,
    s = hsv.s,
    v = hsv.v;
  var ret = [];
  var modification = 1 / results;
  while (results--) {
    ret.push(tinycolor({
      h: h,
      s: s,
      v: v
    }));
    v = (v + modification) % 1;
  }
  return ret;
}

// Utility Functions
// ---------------------

tinycolor.mix = function (color1, color2, amount) {
  amount = amount === 0 ? 0 : amount || 50;
  var rgb1 = tinycolor(color1).toRgb();
  var rgb2 = tinycolor(color2).toRgb();
  var p = amount / 100;
  var rgba = {
    r: (rgb2.r - rgb1.r) * p + rgb1.r,
    g: (rgb2.g - rgb1.g) * p + rgb1.g,
    b: (rgb2.b - rgb1.b) * p + rgb1.b,
    a: (rgb2.a - rgb1.a) * p + rgb1.a
  };
  return tinycolor(rgba);
};

// Readability Functions
// ---------------------
// <http://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef (WCAG Version 2)

// `contrast`
// Analyze the 2 colors and returns the color contrast defined by (WCAG Version 2)
tinycolor.readability = function (color1, color2) {
  var c1 = tinycolor(color1);
  var c2 = tinycolor(color2);
  return (Math.max(c1.getLuminance(), c2.getLuminance()) + 0.05) / (Math.min(c1.getLuminance(), c2.getLuminance()) + 0.05);
};

// `isReadable`
// Ensure that foreground and background color combinations meet WCAG2 guidelines.
// The third argument is an optional Object.
//      the 'level' property states 'AA' or 'AAA' - if missing or invalid, it defaults to 'AA';
//      the 'size' property states 'large' or 'small' - if missing or invalid, it defaults to 'small'.
// If the entire object is absent, isReadable defaults to {level:"AA",size:"small"}.

// *Example*
//    tinycolor.isReadable("#000", "#111") => false
//    tinycolor.isReadable("#000", "#111",{level:"AA",size:"large"}) => false
tinycolor.isReadable = function (color1, color2, wcag2) {
  var readability = tinycolor.readability(color1, color2);
  var wcag2Parms, out;
  out = false;
  wcag2Parms = validateWCAG2Parms(wcag2);
  switch (wcag2Parms.level + wcag2Parms.size) {
    case "AAsmall":
    case "AAAlarge":
      out = readability >= 4.5;
      break;
    case "AAlarge":
      out = readability >= 3;
      break;
    case "AAAsmall":
      out = readability >= 7;
      break;
  }
  return out;
};

// `mostReadable`
// Given a base color and a list of possible foreground or background
// colors for that base, returns the most readable color.
// Optionally returns Black or White if the most readable color is unreadable.
// *Example*
//    tinycolor.mostReadable(tinycolor.mostReadable("#123", ["#124", "#125"],{includeFallbackColors:false}).toHexString(); // "#112255"
//    tinycolor.mostReadable(tinycolor.mostReadable("#123", ["#124", "#125"],{includeFallbackColors:true}).toHexString();  // "#ffffff"
//    tinycolor.mostReadable("#a8015a", ["#faf3f3"],{includeFallbackColors:true,level:"AAA",size:"large"}).toHexString(); // "#faf3f3"
//    tinycolor.mostReadable("#a8015a", ["#faf3f3"],{includeFallbackColors:true,level:"AAA",size:"small"}).toHexString(); // "#ffffff"
tinycolor.mostReadable = function (baseColor, colorList, args) {
  var bestColor = null;
  var bestScore = 0;
  var readability;
  var includeFallbackColors, level, size;
  args = args || {};
  includeFallbackColors = args.includeFallbackColors;
  level = args.level;
  size = args.size;
  for (var i = 0; i < colorList.length; i++) {
    readability = tinycolor.readability(baseColor, colorList[i]);
    if (readability > bestScore) {
      bestScore = readability;
      bestColor = tinycolor(colorList[i]);
    }
  }
  if (tinycolor.isReadable(baseColor, bestColor, {
    level: level,
    size: size
  }) || !includeFallbackColors) {
    return bestColor;
  } else {
    args.includeFallbackColors = false;
    return tinycolor.mostReadable(baseColor, ["#fff", "#000"], args);
  }
};

// Big List of Colors
// ------------------
// <https://www.w3.org/TR/css-color-4/#named-colors>
var names = tinycolor.names = {
  aliceblue: "f0f8ff",
  antiquewhite: "faebd7",
  aqua: "0ff",
  aquamarine: "7fffd4",
  azure: "f0ffff",
  beige: "f5f5dc",
  bisque: "ffe4c4",
  black: "000",
  blanchedalmond: "ffebcd",
  blue: "00f",
  blueviolet: "8a2be2",
  brown: "a52a2a",
  burlywood: "deb887",
  burntsienna: "ea7e5d",
  cadetblue: "5f9ea0",
  chartreuse: "7fff00",
  chocolate: "d2691e",
  coral: "ff7f50",
  cornflowerblue: "6495ed",
  cornsilk: "fff8dc",
  crimson: "dc143c",
  cyan: "0ff",
  darkblue: "00008b",
  darkcyan: "008b8b",
  darkgoldenrod: "b8860b",
  darkgray: "a9a9a9",
  darkgreen: "006400",
  darkgrey: "a9a9a9",
  darkkhaki: "bdb76b",
  darkmagenta: "8b008b",
  darkolivegreen: "556b2f",
  darkorange: "ff8c00",
  darkorchid: "9932cc",
  darkred: "8b0000",
  darksalmon: "e9967a",
  darkseagreen: "8fbc8f",
  darkslateblue: "483d8b",
  darkslategray: "2f4f4f",
  darkslategrey: "2f4f4f",
  darkturquoise: "00ced1",
  darkviolet: "9400d3",
  deeppink: "ff1493",
  deepskyblue: "00bfff",
  dimgray: "696969",
  dimgrey: "696969",
  dodgerblue: "1e90ff",
  firebrick: "b22222",
  floralwhite: "fffaf0",
  forestgreen: "228b22",
  fuchsia: "f0f",
  gainsboro: "dcdcdc",
  ghostwhite: "f8f8ff",
  gold: "ffd700",
  goldenrod: "daa520",
  gray: "808080",
  green: "008000",
  greenyellow: "adff2f",
  grey: "808080",
  honeydew: "f0fff0",
  hotpink: "ff69b4",
  indianred: "cd5c5c",
  indigo: "4b0082",
  ivory: "fffff0",
  khaki: "f0e68c",
  lavender: "e6e6fa",
  lavenderblush: "fff0f5",
  lawngreen: "7cfc00",
  lemonchiffon: "fffacd",
  lightblue: "add8e6",
  lightcoral: "f08080",
  lightcyan: "e0ffff",
  lightgoldenrodyellow: "fafad2",
  lightgray: "d3d3d3",
  lightgreen: "90ee90",
  lightgrey: "d3d3d3",
  lightpink: "ffb6c1",
  lightsalmon: "ffa07a",
  lightseagreen: "20b2aa",
  lightskyblue: "87cefa",
  lightslategray: "789",
  lightslategrey: "789",
  lightsteelblue: "b0c4de",
  lightyellow: "ffffe0",
  lime: "0f0",
  limegreen: "32cd32",
  linen: "faf0e6",
  magenta: "f0f",
  maroon: "800000",
  mediumaquamarine: "66cdaa",
  mediumblue: "0000cd",
  mediumorchid: "ba55d3",
  mediumpurple: "9370db",
  mediumseagreen: "3cb371",
  mediumslateblue: "7b68ee",
  mediumspringgreen: "00fa9a",
  mediumturquoise: "48d1cc",
  mediumvioletred: "c71585",
  midnightblue: "191970",
  mintcream: "f5fffa",
  mistyrose: "ffe4e1",
  moccasin: "ffe4b5",
  navajowhite: "ffdead",
  navy: "000080",
  oldlace: "fdf5e6",
  olive: "808000",
  olivedrab: "6b8e23",
  orange: "ffa500",
  orangered: "ff4500",
  orchid: "da70d6",
  palegoldenrod: "eee8aa",
  palegreen: "98fb98",
  paleturquoise: "afeeee",
  palevioletred: "db7093",
  papayawhip: "ffefd5",
  peachpuff: "ffdab9",
  peru: "cd853f",
  pink: "ffc0cb",
  plum: "dda0dd",
  powderblue: "b0e0e6",
  purple: "800080",
  rebeccapurple: "663399",
  red: "f00",
  rosybrown: "bc8f8f",
  royalblue: "4169e1",
  saddlebrown: "8b4513",
  salmon: "fa8072",
  sandybrown: "f4a460",
  seagreen: "2e8b57",
  seashell: "fff5ee",
  sienna: "a0522d",
  silver: "c0c0c0",
  skyblue: "87ceeb",
  slateblue: "6a5acd",
  slategray: "708090",
  slategrey: "708090",
  snow: "fffafa",
  springgreen: "00ff7f",
  steelblue: "4682b4",
  tan: "d2b48c",
  teal: "008080",
  thistle: "d8bfd8",
  tomato: "ff6347",
  turquoise: "40e0d0",
  violet: "ee82ee",
  wheat: "f5deb3",
  white: "fff",
  whitesmoke: "f5f5f5",
  yellow: "ff0",
  yellowgreen: "9acd32"
};

// Make it easy to access colors via `hexNames[hex]`
var hexNames = tinycolor.hexNames = flip(names);

// Utilities
// ---------

// `{ 'name1': 'val1' }` becomes `{ 'val1': 'name1' }`
function flip(o) {
  var flipped = {};
  for (var i in o) {
    if (o.hasOwnProperty(i)) {
      flipped[o[i]] = i;
    }
  }
  return flipped;
}

// Return a valid alpha value [0,1] with all invalid values being set to 1
function boundAlpha(a) {
  a = parseFloat(a);
  if (isNaN(a) || a < 0 || a > 1) {
    a = 1;
  }
  return a;
}

// Take input from [0, n] and return it as [0, 1]
function bound01(n, max) {
  if (isOnePointZero(n)) n = "100%";
  var processPercent = isPercentage(n);
  n = Math.min(max, Math.max(0, parseFloat(n)));

  // Automatically convert percentage into number
  if (processPercent) {
    n = parseInt(n * max, 10) / 100;
  }

  // Handle floating point rounding errors
  if (Math.abs(n - max) < 0.000001) {
    return 1;
  }

  // Convert into [0, 1] range if it isn't already
  return n % max / parseFloat(max);
}

// Force a number between 0 and 1
function clamp01(val) {
  return Math.min(1, Math.max(0, val));
}

// Parse a base-16 hex value into a base-10 integer
function parseIntFromHex(val) {
  return parseInt(val, 16);
}

// Need to handle 1.0 as 100%, since once it is a number, there is no difference between it and 1
// <http://stackoverflow.com/questions/7422072/javascript-how-to-detect-number-as-a-decimal-including-1-0>
function isOnePointZero(n) {
  return typeof n == "string" && n.indexOf(".") != -1 && parseFloat(n) === 1;
}

// Check to see if string passed in is a percentage
function isPercentage(n) {
  return typeof n === "string" && n.indexOf("%") != -1;
}

// Force a hex value to have 2 characters
function pad2(c) {
  return c.length == 1 ? "0" + c : "" + c;
}

// Replace a decimal with it's percentage value
function convertToPercentage(n) {
  if (n <= 1) {
    n = n * 100 + "%";
  }
  return n;
}

// Converts a decimal to a hex value
function convertDecimalToHex(d) {
  return Math.round(parseFloat(d) * 255).toString(16);
}
// Converts a hex value to a decimal
function convertHexToDecimal(h) {
  return parseIntFromHex(h) / 255;
}
var matchers = function () {
  // <http://www.w3.org/TR/css3-values/#integers>
  var CSS_INTEGER = "[-\\+]?\\d+%?";

  // <http://www.w3.org/TR/css3-values/#number-value>
  var CSS_NUMBER = "[-\\+]?\\d*\\.\\d+%?";

  // Allow positive/negative integer/number.  Don't capture the either/or, just the entire outcome.
  var CSS_UNIT = "(?:" + CSS_NUMBER + ")|(?:" + CSS_INTEGER + ")";

  // Actual matching.
  // Parentheses and commas are optional, but not required.
  // Whitespace can take the place of commas or opening paren
  var PERMISSIVE_MATCH3 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
  var PERMISSIVE_MATCH4 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
  return {
    CSS_UNIT: new RegExp(CSS_UNIT),
    rgb: new RegExp("rgb" + PERMISSIVE_MATCH3),
    rgba: new RegExp("rgba" + PERMISSIVE_MATCH4),
    hsl: new RegExp("hsl" + PERMISSIVE_MATCH3),
    hsla: new RegExp("hsla" + PERMISSIVE_MATCH4),
    hsv: new RegExp("hsv" + PERMISSIVE_MATCH3),
    hsva: new RegExp("hsva" + PERMISSIVE_MATCH4),
    hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
    hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
  };
}();

// `isValidCSSUnit`
// Take in a single string / number and check to see if it looks like a CSS unit
// (see `matchers` above for definition).
function isValidCSSUnit(color) {
  return !!matchers.CSS_UNIT.exec(color);
}

// `stringInputToObject`
// Permissive string parsing.  Take in a number of formats, and output an object
// based on detected format.  Returns `{ r, g, b }` or `{ h, s, l }` or `{ h, s, v}`
function stringInputToObject(color) {
  color = color.replace(trimLeft, "").replace(trimRight, "").toLowerCase();
  var named = false;
  if (names[color]) {
    color = names[color];
    named = true;
  } else if (color == "transparent") {
    return {
      r: 0,
      g: 0,
      b: 0,
      a: 0,
      format: "name"
    };
  }

  // Try to match string input using regular expressions.
  // Keep most of the number bounding out of this function - don't worry about [0,1] or [0,100] or [0,360]
  // Just return an object and let the conversion functions handle that.
  // This way the result will be the same whether the tinycolor is initialized with string or object.
  var match;
  if (match = matchers.rgb.exec(color)) {
    return {
      r: match[1],
      g: match[2],
      b: match[3]
    };
  }
  if (match = matchers.rgba.exec(color)) {
    return {
      r: match[1],
      g: match[2],
      b: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hsl.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      l: match[3]
    };
  }
  if (match = matchers.hsla.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      l: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hsv.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      v: match[3]
    };
  }
  if (match = matchers.hsva.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      v: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hex8.exec(color)) {
    return {
      r: parseIntFromHex(match[1]),
      g: parseIntFromHex(match[2]),
      b: parseIntFromHex(match[3]),
      a: convertHexToDecimal(match[4]),
      format: named ? "name" : "hex8"
    };
  }
  if (match = matchers.hex6.exec(color)) {
    return {
      r: parseIntFromHex(match[1]),
      g: parseIntFromHex(match[2]),
      b: parseIntFromHex(match[3]),
      format: named ? "name" : "hex"
    };
  }
  if (match = matchers.hex4.exec(color)) {
    return {
      r: parseIntFromHex(match[1] + "" + match[1]),
      g: parseIntFromHex(match[2] + "" + match[2]),
      b: parseIntFromHex(match[3] + "" + match[3]),
      a: convertHexToDecimal(match[4] + "" + match[4]),
      format: named ? "name" : "hex8"
    };
  }
  if (match = matchers.hex3.exec(color)) {
    return {
      r: parseIntFromHex(match[1] + "" + match[1]),
      g: parseIntFromHex(match[2] + "" + match[2]),
      b: parseIntFromHex(match[3] + "" + match[3]),
      format: named ? "name" : "hex"
    };
  }
  return false;
}
function validateWCAG2Parms(parms) {
  // return valid WCAG2 parms for isReadable.
  // If input parms are invalid, return {"level":"AA", "size":"small"}
  var level, size;
  parms = parms || {
    level: "AA",
    size: "small"
  };
  level = (parms.level || "AA").toUpperCase();
  size = (parms.size || "small").toLowerCase();
  if (level !== "AA" && level !== "AAA") {
    level = "AA";
  }
  if (size !== "small" && size !== "large") {
    size = "small";
  }
  return {
    level: level,
    size: size
  };
}

//arr遍历的数组   search 搜索的值
function XmlSearchWord(arr, search) {
  let searchLis = [];
  arr.map(item => {
    if (item.Text) {
      if (item.Text.indexOf(search) != -1) {
        let pList = item.Text.split('</p>');
        for (let i = 0; i < pList.length; i++) {
          if (pList[i].indexOf(search) != -1) {
            const Reg = new RegExp(search, 'g');
            if (pList[i].indexOf('id="paragraph') === -1) {
              let searchText = pList[i]?.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, '');
              searchText.replace(Reg, (match, searchIndex) => {
                let data = {
                  pId: '',
                  searchText,
                  ...item
                };
                // console.log(pList[i].replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ''));
                let StrIndex = searchIndex > 30 ? searchIndex - 30 : 0;
                let EndIndex = data.searchText.length - searchIndex > 30 ? searchIndex + 30 : data.searchText.length;
                data.searchText = data.searchText.slice(StrIndex, EndIndex) + '...';
                searchLis.push(data);
              });
            } else {
              pList[i].replace(/id=['"]([^'"]+)[^>]/gi, (match, pId) => {
                if (pId.indexOf('paragraph') !== -1) {
                  let searchText = pList[i]?.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, '');
                  searchText.replace(Reg, (match, searchIndex) => {
                    let data = {
                      pId,
                      searchText,
                      ...item
                    };
                    // console.log(pList[i].replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ''));
                    let StrIndex = searchIndex > 30 ? searchIndex - 30 : 0;
                    let EndIndex = data.searchText.length - searchIndex > 30 ? searchIndex + 30 : data.searchText.length;
                    data.searchText = data.searchText.slice(StrIndex, EndIndex) + '...';
                    searchLis.push(data);
                  });
                }
              });
            }
          }
        }
      }
    } else if (item.textList) {
      if (JSON.stringify(item.textList).indexOf(search) != -1) {
        for (let i = 0; i < item.textList.length; i++) {
          let searchText = item.textList[i].title?.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, '');
          if (searchText.indexOf(search) != -1) {
            let data = {
              searchText,
              className: item.textList[i].titleClass,
              index: item.textList[i].index,
              ...item
            };
            let searchIndex = searchText.indexOf(search);
            let StrIndex = searchIndex > 30 ? searchIndex - 30 : 0;
            let EndIndex = data.searchText.length - searchIndex > 30 ? searchIndex + 30 : data.searchText.length;
            data.searchText = data.searchText.slice(StrIndex, EndIndex) + '...';
            searchLis.push(data);
          }
        }
      }
    }
  });
  return searchLis;
}

//
var script$2K = {
  name: 'xmlPreviewSearch',
  props: {
    viewList: Object,
    componentList: Array,
    title: String,
    pageType: {
      type: String,
      default: 'h5'
    },
    searchValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      list: [],
      input: '',
      ArrList: [],
      show: false
    };
  },
  created() {
    // this.ArrList = XmlSearchList(this.viewList, this.componentList).arr
  },
  watch: {
    input(a, b) {
      if (a.replace(/\s+/g, '') != b.replace(/\s+/g, '')) {
        this.changeInput();
      }
    },
    searchValue: {
      handler(newValue) {
        this.input = newValue;
      }
    }
  },
  methods: {
    changeInput() {
      if (this.input.replace(/\s+/g, '') !== '') {
        this.list = XmlSearchWord(this.componentList, this.input.replace(/\s+/g, ''));
        if (this.list.length > 0) {
          this.show = true;
        } else {
          this.show = false;
        }
      } else {
        this.list = [];
        this.show = false;
      }
    },
    seachItem(e) {
      e.inputText = this.input.replace(/\s+/g, '');
      this.$emit('seachItem', e, this.input);
      this.input = '';
    },
    onBackClick() {
      this.$emit('onBackClick');
    },
    quxiao() {
      this.input = '';
      this.list = [];
      this.show = false;
    },
    brightenKeyword(val) {
      const keyword = this.input.replace(/\s+/g, '');
      const Reg = new RegExp(keyword, 'g');
      let res = '';
      if (val) {
        res = val.replace(Reg, `<span style="color: red;">${keyword}</span>`);
        return res;
      }
    }
  }
};

var __$_require_assets_images_icons_back_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACYAAABACAYAAAB2kAXpAAAAAXNSR0IArs4c6QAABABJREFUaEPVmluopmMUx39/UUy44cJMg+FikEMuGMnIOJcwlHIYpKkZmchxuJDT5MIpIzXIpMkhh5QwlPPx0uHKhbiYKCJXSMTor7U9W4/t29/3rP3t55usm137+z/v+r3Pca31vGIHme19gVXAEuBXYKukd6ZxtCO4bJ8HPAHsNsP/RknXxf8mDmZ7NfDYkA65UtKmiYLZvhp4YMQofSVpycTAbN8M3NkwdbZL2mUiYLbvBW5ogArJ15L27w5m+yHgikaokK2WtKUrmO2ngQsTUBsk3dZ1Vdp+EViZgFov6b5u+5jtnYE3gBMTUOskPVzr53UobS8A3gWWJaBWSYoh/5fNG5jtfYA4Ug5JQJ0j6aVB+nkBs70f8CEQf1tsO3CqpPdmE48NZvswIBzs1UJUDuwTJH00TD8WmO2jgbeBPRqhfgBOkvTZKP2cwWyvKFA7jXJSfv8GOF7Sthb9nMBsx/4U+1SrfQ6skPR9a4M0mO1LgcdbHQAxl2Ki/5hok4vHbK8DNiUcxPZxmqQ/E22mpM09Zvsm4K6EgwiVz07o8xus7TuAWxNOnpF0UUL/H+nIHrP9IHBVwslmSWsT+oHSoWC2twCXJZzcL+n6hH5W6axgtl8Azk04uV1SDPm82EAw26/Hakp4uFbSqCQj8bgZq7KELW8BxyaeslbS5oS+SfpPj9neE3gfOLKp5d+iCyQ9l9A3S6fASroeEcKBzS3hLEmvJPQpqWwvLWHLwsaWv5cj5oNG/ZxkAfZTImz5uRzGn87JW6JRgLlR/20kGJK+aNSPJWsF+wRYLum3sbwlGv/vweJdYyhPlhRBX3eLHvsDiCS1xX4pk//jFvE4mgA7puSDkay2WLxIBH+zpl4tDxmlmd5gDy9we49qUP2+UtLLCX1KWh9JB5SNtjVpDUd9j6TpVylpfhzihyZer+8hXsHtXgojRyXg+oY9FVys0jdjBSbg+geKFeBW4MwEXP/QuoJ7Fjg/Adc/GangHgEuT8D1T98quHuA9Qm4/glvBXcLsCEBFyWC0yVFoS5lIxPemU+zfQ2wMeElztVTuhZVqp5bAzyagOtfhqrgYqXGim21/oW7Cu4MIDKl1inRv9RZwR0HxPm6a2PXxW1u3+JwBRdJciTLkTS3WP9yegV3UInpFrWQFU3fC4gKbjEQiXDEdq3W98qmgouLiNhYj2glA/peclVw8XVA3MAtT8D1vRasQWy/FkdSAq7/RWrVe88D8Z1Fq10i6anWjbH1oQN1yVrul5KWTgQsaBPV78l93lANa3x3Ed9fDLPvJC2cWI9VcDcCdw8hm1qdEwcrwxphelzOz/T/pKS4RGuOCsaa/IMa2z4YuLicElHVfLWu6f4FuhpYUNovLf0AAAAASUVORK5CYII=";

var __$_require_assets_images_icons_zanwu_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$2K = script$2K;
var __vue_render__$2K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { class: "xmlPreviewSearch-box-" + _vm.pageType }, [
    _vm.pageType == "h5"
      ? _c("div", { staticClass: "xmlPreviewSearch-title" }, [
          _c("img", {
            staticClass: "img",
            staticStyle: { cursor: "pointer" },
            attrs: { src: __$_require_assets_images_icons_back_png__ },
            on: { click: _vm.onBackClick },
          }),
          _vm._v(" "),
          _c("div", { staticClass: "xmlPreviewSearch-sou" }, [
            _vm._v(_vm._s(_vm.title)),
          ]),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.pageType == "h5"
      ? _c("div", { staticClass: "xmlPreviewSearch-input" }, [
          _c(
            "div",
            {
              staticClass: "xmlPreviewSearch-icon",
              on: { click: _vm.changeInput },
            },
            [_c("i", { staticClass: "el-icon-search" })]
          ),
          _vm._v(" "),
          _c("input", {
            directives: [
              {
                name: "model",
                rawName: "v-model",
                value: _vm.input,
                expression: "input",
              },
            ],
            staticClass: "input",
            attrs: { maxlength: "20", placeholder: "请输入搜索内容" },
            domProps: { value: _vm.input },
            on: {
              change: _vm.changeInput,
              input: function ($event) {
                if ($event.target.composing) {
                  return
                }
                _vm.input = $event.target.value;
              },
            },
          }),
          _vm._v(" "),
          _c(
            "button",
            {
              staticClass: "input_text",
              on: {
                click: function ($event) {
                  return _vm.quxiao()
                },
              },
            },
            [_vm._v("取消")]
          ),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.pageType == "pc"
      ? _c("div", { class: "xmlPreviewSearch-input-" + _vm.pageType }, [
          _c(
            "div",
            {
              staticStyle: { "font-size": "16px", cursor: "pointer" },
              on: { click: _vm.onBackClick },
            },
            [
              _c("i", {
                staticClass: "el-icon-arrow-left",
                staticStyle: { "font-size": "16px" },
              }),
              _vm._v("返回\n    "),
            ]
          ),
        ])
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "seach-list" },
      [
        _vm._l(_vm.list, function (item, index) {
          return _c(
            "div",
            {
              key: index,
              staticClass: "seach-box",
              on: {
                click: function ($event) {
                  return _vm.seachItem(item)
                },
              },
            },
            [
              _c("div", { staticClass: "seach-label" }, [
                _vm._v(_vm._s(item.label)),
              ]),
              _vm._v(" "),
              _c("div", {
                staticClass: "seach-text",
                domProps: {
                  innerHTML: _vm._s(
                    _vm.brightenKeyword(item.searchText, _vm.input)
                  ),
                },
              }),
            ]
          )
        }),
        _vm._v(" "),
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: !_vm.show,
                expression: "!show",
              },
            ],
            staticClass: "seach_zaiwu",
          },
          [
            _c("img", {
              attrs: { src: __$_require_assets_images_icons_zanwu_png__ },
            }),
            _vm._v(" "),
            _c("div", [
              _vm._v(_vm._s(_vm.input ? "未搜索到相关内容" : "请输入搜索内容")),
            ]),
          ]
        ),
      ],
      2
    ),
  ])
};
var __vue_staticRenderFns__$2K = [];
__vue_render__$2K._withStripped = true;

  /* style */
  const __vue_inject_styles__$2K = undefined;
  /* scoped */
  const __vue_scope_id__$2K = "data-v-2f88b0c1";
  /* module identifier */
  const __vue_module_identifier__$2K = undefined;
  /* functional template */
  const __vue_is_functional_template__$2K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2K, staticRenderFns: __vue_staticRenderFns__$2K },
    __vue_inject_styles__$2K,
    __vue_script__$2K,
    __vue_scope_id__$2K,
    __vue_is_functional_template__$2K,
    __vue_module_identifier__$2K,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2J = {
  name: 'xml-toolbar-pc',
  props: {
    textBookResource: {
      type: Object,
      default: () => {}
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: 'student'
    },
    NumShow: {
      type: Number,
      default: 0
    },
    isShowTeacherResource: {
      type: Boolean,
      default: false
    },
    sysEnv: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      toolbarList: this.sysEnv.toolbarList,
      current: 0
    };
  },
  mounted() {
    console.log("🚀 ~ mounted ~ sysEnv:", this.sysEnv);
    if (!this.isShowTeacherResource && this.userType != 'teacher') {
      this.toolbarList.splice(1, 1);
    }
  },
  methods: {
    clickToolbar(index) {
      this.current = index;
      this.$emit('clickToolbar', this.toolbarList[index]);
    }
  }
};

/* script */
const __vue_script__$2J = script$2J;

/* template */
var __vue_render__$2J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-toolbar-pc",
      style: _vm.toolbarList[_vm.current].style,
    },
    [
      _vm._l(_vm.toolbarList, function (item, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "toolbar-item",
            class: [
              index == _vm.current ? item.select : item.default,
              _vm.sysEnv.toolbarSecond && index == _vm.current
                ? "current-toolbar"
                : "",
            ],
            on: {
              click: function ($event) {
                return _vm.clickToolbar(index)
              },
            },
          },
          [
            _c("div", { staticClass: "toolbar-icon" }),
            _vm._v(" "),
            _c("div", [_vm._v(_vm._s(item.name))]),
            _vm._v(" "),
            _c(
              "div",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: item.total != "none",
                    expression: "item.total != 'none'",
                  },
                ],
              },
              [
                _vm._v(
                  "(" +
                    _vm._s(
                      _vm.textBookResource[item.total]
                        ? _vm.textBookResource[item.total]
                        : "0"
                    ) +
                    ")"
                ),
              ]
            ),
          ]
        )
      }),
      _vm._v(" "),
      _vm.sysEnv.toolbarFirst
        ? _c(
            "div",
            {
              staticClass: "select-toolbar",
              style: { top: _vm.current * 86 + 8 + "px" },
            },
            [_c("div", { staticClass: "toolbar" })]
          )
        : _vm._e(),
    ],
    2
  )
};
var __vue_staticRenderFns__$2J = [];
__vue_render__$2J._withStripped = true;

  /* style */
  const __vue_inject_styles__$2J = undefined;
  /* scoped */
  const __vue_scope_id__$2J = undefined;
  /* module identifier */
  const __vue_module_identifier__$2J = undefined;
  /* functional template */
  const __vue_is_functional_template__$2J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2J, staticRenderFns: __vue_staticRenderFns__$2J },
    __vue_inject_styles__$2J,
    __vue_script__$2J,
    __vue_scope_id__$2J,
    __vue_is_functional_template__$2J,
    __vue_module_identifier__$2J,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2I = {
  name: 'xmlPreviewSearch',
  props: {
    viewList: Object,
    componentList: Array,
    title: String,
    pageType: {
      type: String,
      default: 'pc'
    },
    searchValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      urlPre: "../assets/images/icons/",
      list: [],
      input: '',
      ArrList: [],
      searchTextShow: true,
      showEllipsisButton: false,
      contentExpand: false,
      show: false,
      launchValue: 0
    };
  },
  computed: {
    searchList() {
      let list = this.list;
      const map = [];
      list.forEach(e => {
        if (e.type == 'note__teacher') {
          this.teacherNoteList.push(e);
        }
        const findObj = map.find(innerTmp => innerTmp.firstLevelId === e.firstLevelId);
        if (findObj) {
          findObj.list.push(e);
        } else {
          map.push({
            firstLevelId: e.firstLevelId,
            firstLevelLabel: e.firstLevelLabel,
            chapterOrderNum: e.chapterOrderNum,
            list: [e]
          });
        }
      });
      return map;
    }
  },
  watch: {
    // 监听输入框
    // 监听input的值，当值改变时，调用changeInput方法
    input(a, b) {
      if (a.replace(/\s+/g, '') != b.replace(/\s+/g, '')) {
        this.changeInput();
        this.contentExpand = false;
      }
    },
    searchValue: {
      handler(newValue) {
        this.input = newValue;
      }
    }
  },
  methods: {
    changeInput() {
      if (this.input.replace(/\s+/g, '') !== '') {
        this.list = XmlSearchWord(this.componentList, this.input.replace(/\s+/g, ''));
        if (this.list.length > 0) {
          this.showEllipsisButton = true;
        }
      } else {
        this.list = [];
        console.log('输入为空');
      }
    },
    seachItem(e) {
      e.inputText = this.input.replace(/\s+/g, '');
      this.$emit('seachItem', e, this.input);
    },
    onBackClick() {
      this.$emit('onBackClick');
    },
    brightenKeyword(val) {
      const keyword = this.input.replace(/\s+/g, '');
      const Reg = new RegExp(keyword, 'g');
      let res = '';
      if (val) {
        res = val.replace(Reg, `<span style="background-color: #e7f0ff; color: #75a9f2;">${keyword}</span>`);
        return res;
      }
    },
    // 展开下拉
    toggleSearchText(id) {
      this.contentExpand = !this.contentExpand;
      this.searchTextShow = !this.searchTextShow;
    }
  }
};

var __$_require_assets_images_icons_searchmin_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAAAXNSR0IArs4c6QAAATtJREFUOE+V0j1rVVEQheFnYWFhMPkLEQT9AZKAIJLGyjaN4AfWEpKQKqYyFlHEyk7Q1o/KykLQTkRbG6sQiIha2FjK9s71nHC8xAvucu95Z9Zas2PitNbmsIBZ7OF9kl+TdekvWmsz2MF1HB0UfsZmksdDeAx20GucxG08wzecxg1cxvZo8lYP9+ADXMLZJB8Pkb+Ke1hK8qbe03n6gq0kdyehgZV3+JrkYg9ewEvMJ9mdAm7gZpIKbTxxeeTlCWaS/JwCXsPDJEd6cBFvcSbJhylgJb6cZL4Hq0Pt61WSK4eBrbXj+FTKkqyMwW4dV/EIayMP94dwBz2txHEqyf4B2MG3atH1U/Ac36sQpeJYZdCFU3v+M3EQ+Xms41xXWBZe4A5K1XYP/wX+K5hB41JUjU/8F9hZmkvy4zcDEm8cuHdPWwAAAABJRU5ErkJggg==";

var __$_require_assets_images_icons_without_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$2I = script$2I;
var __vue_render__$2I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { class: "xmlPreviewSearch-box-" + _vm.pageType }, [
    _vm.pageType == "pc"
      ? _c("div", { staticClass: "xmlPreviewSearch-input" }, [
          _c("input", {
            directives: [
              {
                name: "model",
                rawName: "v-model",
                value: _vm.input,
                expression: "input",
              },
            ],
            staticClass: "input",
            attrs: { maxlength: "20", placeholder: "请输入" },
            domProps: { value: _vm.input },
            on: {
              change: _vm.changeInput,
              input: function ($event) {
                if ($event.target.composing) {
                  return
                }
                _vm.input = $event.target.value;
              },
            },
          }),
          _vm._v(" "),
          _c(
            "button",
            {
              staticClass: "input_text",
              on: {
                click: function ($event) {
                  return _vm.changeInput()
                },
              },
            },
            [
              _c("img", {
                attrs: {
                  src: __$_require_assets_images_icons_searchmin_png__,
                  alt: "",
                },
              }),
            ]
          ),
        ])
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "seach-list" },
      [
        _c(
          "el-collapse",
          {
            attrs: { accordion: "" },
            model: {
              value: _vm.launchValue,
              callback: function ($$v) {
                _vm.launchValue = $$v;
              },
              expression: "launchValue",
            },
          },
          _vm._l(_vm.searchList, function (item, index) {
            return _c(
              "el-collapse-item",
              { key: index, attrs: { name: index } },
              [
                _c("template", { slot: "title" }, [
                  _c("div", { staticClass: "collapse-title" }, [
                    _vm._v(
                      "\n              " +
                        _vm._s(item.firstLevelLabel) +
                        "\n            "
                    ),
                  ]),
                ]),
                _vm._v(" "),
                _vm._l(item.list, function (items, index) {
                  return _c(
                    "div",
                    {
                      on: {
                        click: function ($event) {
                          return _vm.seachItem(items)
                        },
                      },
                    },
                    [
                      _c("div", {
                        staticClass: "xml-seach-item-text",
                        domProps: {
                          innerHTML: _vm._s(
                            _vm.brightenKeyword(items.searchText, _vm.input)
                          ),
                        },
                      }),
                    ]
                  )
                }),
              ],
              2
            )
          }),
          1
        ),
        _vm._v(" "),
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.searchList.length == 0 && _vm.input == "",
                expression: "searchList.length==0 && input==''",
              },
            ],
            staticClass: "seach_zaiwu",
          },
          [
            _c("div", { staticClass: "search_left_img" }, [
              _c("img", {
                attrs: { src: __$_require_assets_images_icons_without_png__ },
              }),
            ]),
          ]
        ),
      ],
      1
    ),
  ])
};
var __vue_staticRenderFns__$2I = [];
__vue_render__$2I._withStripped = true;

  /* style */
  const __vue_inject_styles__$2I = undefined;
  /* scoped */
  const __vue_scope_id__$2I = "data-v-a32ce766";
  /* module identifier */
  const __vue_module_identifier__$2I = undefined;
  /* functional template */
  const __vue_is_functional_template__$2I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2I, staticRenderFns: __vue_staticRenderFns__$2I },
    __vue_inject_styles__$2I,
    __vue_script__$2I,
    __vue_scope_id__$2I,
    __vue_is_functional_template__$2I,
    __vue_module_identifier__$2I,
    false,
    undefined,
    undefined,
    undefined
  );

var coverLocal = "data:image/png;base64,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";

//
var script$2H = {
  name: 'audioPlayerPage',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    targerLink: {
      type: String,
      default: ''
    }
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat,
        'dan-ci': playOnce
      };
      return srcMap[this.playMode];
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.musicAudio.muted = true;
              const playPromise = _this.$refs.musicAudio.play();
              _this.$refs.musicAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.musicAudio.pause();
                }).catch(error => {
                  _this.$refs.musicAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.musicAudio.pause();
      }
    }
  },
  data() {
    return {
      coverLocal,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      musicAudio: null,
      manualPlay: false,
      Volume: 100,
      showVolume: false
    };
  },
  mounted() {
    // 加载完毕后，先获取<video>标签DOM对象
    this.musicAudio = this.$refs.musicAudio;
    // this.videoContainer = this.$refs.videoContainer
    if (this.mode === 'preview' && this.playStyle === 'yuan-wen') {
      this.$emit('resetTimeUpdate');
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    play() {
      // if (this.mode==='editor') {
      //   this.$message.warning('音频仅支持在预览模式下播放')
      //   return
      // }
      if (this.playStyle === 'yuan-wen') {
        this.doPlay();
      }
      if (this.playStyle === 'tiao-zhuan') {
        if (this.targerLink) {
          // window.location.href=this.targerLink
          window.open(this.targerLink, '_blank');
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }
      }
      if (this.playStyle === 'tan-chuang') {
        this.$emit('showDialog', {
          url: this.url,
          title: this.title,
          coverFileUrl: this.coverImageUrl
        });
      }
    },
    doPlay() {
      console.log('暂停');
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      if (this.isPlay) {
        this.$refs.musicAudio.pause();
        this.isPlay = !this.isPlay;
      } else {
        this.manualPlay = true;
        this.$refs.musicAudio.play().then(() => {
          this.isPlay = !this.isPlay;
          let timeStamp = new Date().getTime();
          this.lastOperateTimeStamp = timeStamp;
          let playStamp = `${this.url}__playTimeStamp__${timeStamp}`;
          sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
        }).catch(error => {
          this.$message.warning('无效音频资源，请检查资源地址');
          console.log('音频播放catch', error);
        });
      }
      // this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      this.$emit('timeUpdate', {
        event: e,
        time: this.$refs.musicAudio.currentTime
      });
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    },
    changeVolume(val) {
      this.$refs.musicAudio.volume = val / 100;
    },
    onPlay() {
      if (!this.manualPlay) {
        this.$refs.musicAudio.pause();
      }
    }
  }
};

/* script */
const __vue_script__$2H = script$2H;

/* template */
var __vue_render__$2H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-audio-player-page" }, [
    _c("i", {
      staticClass: "el-icon-back",
      on: {
        click: function ($event) {
          return _vm.$emit("back")
        },
      },
    }),
    _vm._v(" "),
    _c("div", { staticClass: "audio-cover" }, [
      _c(
        "div",
        { staticClass: "square-wrapper" },
        [
          _c("el-image", {
            staticClass: "rotate",
            class: _vm.isPlay ? "active" : "paused",
            attrs: {
              src: _vm.coverImageUrl ? _vm.coverImageUrl : _vm.coverLocal,
              alt: "",
            },
          }),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "play-button",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.play.apply(null, arguments)
                },
              },
            },
            [
              _c("i", {
                class: _vm.isPlay
                  ? "el-icon-video-pause"
                  : "el-icon-video-play",
              }),
            ]
          ),
        ],
        1
      ),
    ]),
    _vm._v(" "),
    _c("div", { staticClass: "audio-player-title" }, [
      _vm._v("\n    " + _vm._s(_vm.title) + "\n  "),
    ]),
    _vm._v(" "),
    _c(
      "div",
      {
        staticClass: "audio-controls",
        on: {
          click: function ($event) {
            _vm.showVolume = false;
          },
        },
      },
      [
        _c("div", { staticClass: "audio-controls__handler" }, [
          _c("div", { staticClass: "play-times" }, [
            _c("div", { staticClass: "current-time" }, [
              _vm._v(_vm._s(_vm.playCurrentTime)),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "progress" },
              [
                _c("el-slider", {
                  attrs: { max: _vm.totalDuration, "show-tooltip": false },
                  on: { change: _vm.changeTime },
                  model: {
                    value: _vm.playTime,
                    callback: function ($$v) {
                      _vm.playTime = $$v;
                    },
                    expression: "playTime",
                  },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "total-time" }, [
              _vm._v(_vm._s(_vm.playDuration)),
            ]),
          ]),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "volume-box",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _c("el-slider", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.showVolume,
                    expression: "showVolume",
                  },
                ],
                attrs: { vertical: "", height: "100px", "show-tooltip": false },
                on: { change: _vm.changeVolume },
                model: {
                  value: _vm.Volume,
                  callback: function ($$v) {
                    _vm.Volume = $$v;
                  },
                  expression: "Volume",
                },
              }),
              _vm._v(" "),
              _c("div", {
                staticClass: "volume-btn",
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                    _vm.showVolume = !_vm.showVolume;
                  },
                },
              }),
            ],
            1
          ),
        ]),
      ]
    ),
    _vm._v(" "),
    _c("audio", {
      ref: "musicAudio",
      staticClass: "audio-component",
      attrs: {
        controls: "",
        preload: "auto",
        src: _vm.url,
        loop: _vm.loop,
        link: _vm.link,
      },
      on: {
        canplay: _vm.changeDuration,
        timeupdate: _vm.updateTime,
        pause: function ($event) {
          _vm.isPlay = false;
        },
        play: _vm.onPlay,
      },
    }),
  ])
};
var __vue_staticRenderFns__$2H = [];
__vue_render__$2H._withStripped = true;

  /* style */
  const __vue_inject_styles__$2H = undefined;
  /* scoped */
  const __vue_scope_id__$2H = undefined;
  /* module identifier */
  const __vue_module_identifier__$2H = undefined;
  /* functional template */
  const __vue_is_functional_template__$2H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2H, staticRenderFns: __vue_staticRenderFns__$2H },
    __vue_inject_styles__$2H,
    __vue_script__$2H,
    __vue_scope_id__$2H,
    __vue_is_functional_template__$2H,
    __vue_module_identifier__$2H,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2G = {
  name: 'audioPlayerPage',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: 'editor'
    },
    playStyle: {
      type: String,
      default: 'yuan-wen'
    },
    styleMode: {
      type: String,
      default: 'A'
    },
    url: {
      type: String,
      default: '',
      require: true
    },
    title: {
      type: String,
      default: '音频文件'
    },
    coverImageUrl: {
      type: String,
      default: ''
    },
    //进度是否能控制
    scheduleControl: {
      type: String,
      default: ''
    },
    loop: {
      type: Boolean,
      default: true
    },
    playMode: {
      type: String,
      default: 'xun-huan'
    },
    content: {
      type: String,
      default: '暂无简介'
    },
    targerLink: {
      type: String,
      default: ''
    }
  },
  computed: {
    videoAndMusicStop() {
      let playState = sessionStorage.getItem('SET_PLAY_STATE');
      return playState;
    },
    playModeIconSrc() {
      let srcMap = {
        'xun-huan': playRepeat,
        'dan-ci': playOnce
      };
      return srcMap[this.playMode];
    }
  },
  watch: {
    url: {
      handler(newVal) {
        let _this = this;
        this.isPlay = false;
        let ua = navigator.userAgent;
        /micromessenger/.test(ua.toLowerCase());
        let isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
        if (isIOS && newVal) {
          try {
            this.$nextTick(() => {
              _this.$refs.musicAudio.muted = true;
              const playPromise = _this.$refs.musicAudio.play();
              _this.$refs.musicAudio.muted = false;
              if (playPromise) {
                playPromise.then(() => {
                  _this.$refs.musicAudio.pause();
                }).catch(error => {
                  _this.$refs.musicAudio.pause();
                  console.log(error);
                });
              }
            });
          } catch (error) {
            console.log(error);
            this.$message({
              type: 'warning',
              message: error,
              duration: 5000
            });
          }
        }
      },
      immediate: true
    },
    videoAndMusicStop(val) {
      console.log('watch', val);
      let currentPlayStamp = val || '--';
      if (`${this.url}__playTimeStamp__${this.lastOperateTimeStamp}` !== currentPlayStamp) {
        this.musicAudio.pause();
      }
    }
  },
  data() {
    return {
      coverLocal,
      isPlay: false,
      playTime: 0,
      playDuration: '00:00',
      playCurrentTime: '00:00',
      totalDuration: 0,
      link: {
        type: String,
        default: ''
      },
      //链接地址
      lastOperateTimeStamp: '',
      musicAudio: null,
      manualPlay: false,
      Volume: 100,
      showVolume: false
    };
  },
  mounted() {
    // 加载完毕后，先获取<video>标签DOM对象
    this.musicAudio = this.$refs.musicAudio;
    // this.videoContainer = this.$refs.videoContainer
    if (this.mode === 'preview' && this.playStyle === 'yuan-wen') {
      this.$emit('resetTimeUpdate');
    }
  },
  methods: {
    changeDuration() {
      this.$nextTick(() => {
        this.playDuration = this.timeFormat(this.$refs.musicAudio.duration);
        this.totalDuration = this.$refs.musicAudio.duration;
      });
    },
    //将单位为秒的的时间转换成mm:ss的形式
    timeFormat(num) {
      let minute = parseInt(num / 60);
      let second = parseInt(num % 60);
      minute = minute >= 10 ? minute : `0${minute}`;
      second = second >= 10 ? second : `0${second}`;
      return `${minute}:${second}`;
    },
    play() {
      // if (this.mode==='editor') {
      //   this.$message.warning('音频仅支持在预览模式下播放')
      //   return
      // }
      if (this.playStyle === 'yuan-wen') {
        this.doPlay();
      }
      if (this.playStyle === 'tiao-zhuan') {
        if (this.targerLink) {
          // window.location.href=this.targerLink
          window.open(this.targerLink, '_blank');
        } else {
          this.$message.warning('获取资源链接失败，请检查链接地址');
        }
      }
      if (this.playStyle === 'tan-chuang') {
        this.$emit('showDialog', {
          url: this.url,
          title: this.title,
          coverFileUrl: this.coverImageUrl
        });
      }
    },
    doPlay() {
      console.log('暂停');
      let audio = document.querySelectorAll('audio');
      audio.forEach(i => i.pause());
      let video = document.querySelectorAll('video');
      if (video && video.length) {
        video.forEach(i => i.pause());
      }
      if (this.isPlay) {
        this.$refs.musicAudio.pause();
        this.isPlay = !this.isPlay;
      } else {
        this.manualPlay = true;
        this.$refs.musicAudio.play().then(() => {
          this.isPlay = !this.isPlay;
          let timeStamp = new Date().getTime();
          this.lastOperateTimeStamp = timeStamp;
          let playStamp = `${this.url}__playTimeStamp__${timeStamp}`;
          sessionStorage.setItem('SET_PLAY_STATE', playStamp); //存储
        }).catch(error => {
          this.$message.warning('无效音频资源，请检查资源地址');
          console.log('音频播放catch', error);
        });
      }
      // this.isPlay ? this.$refs.musicAudio.pause() : this.$refs.musicAudio.play()
      // this.isPlay = !this.isPlay
      // let timeStamp = new Date().getTime()
      // this.lastOperateTimeStamp = timeStamp
      // let playStamp = `${this.url}__playTimeStamp__${timeStamp}`
      // // !this.isPlay && this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // // this.$store.commit('leaflet/SET_PLAY_STATE', playStamp)
      // sessionStorage.setItem('SET_PLAY_STATE', playStamp) //存储
    },
    updateTime(e) {
      this.playTime = this.$refs.musicAudio.currentTime;
      this.playCurrentTime = this.timeFormat(this.$refs.musicAudio.currentTime);
      if (this.playTime >= this.totalDuration) {
        this.isPlay = false;
      }
      this.$emit('timeUpdate', {
        event: e,
        time: this.$refs.musicAudio.currentTime
      });
    },
    changeTime(val) {
      this.$refs.musicAudio.currentTime = val;
    },
    changeVolume(val) {
      this.$refs.musicAudio.volume = val / 100;
    },
    onPlay() {
      if (!this.manualPlay) {
        this.$refs.musicAudio.pause();
      }
    }
  }
};

/* script */
const __vue_script__$2G = script$2G;

/* template */
var __vue_render__$2G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-audio-player-page-pc" }, [
    _c("i", {
      staticClass: "el-icon-back",
      on: {
        click: function ($event) {
          return _vm.$emit("back")
        },
      },
    }),
    _vm._v(" "),
    _c("div", { staticClass: "audio-cover" }, [
      _c(
        "div",
        { staticClass: "square-wrapper" },
        [
          _c("el-image", {
            staticClass: "rotate",
            class: _vm.isPlay ? "active" : "paused",
            attrs: {
              src: _vm.coverImageUrl ? _vm.coverImageUrl : _vm.coverLocal,
              alt: "",
            },
          }),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "play-button",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  return _vm.play.apply(null, arguments)
                },
              },
            },
            [
              _c("i", {
                class: _vm.isPlay
                  ? "el-icon-video-pause"
                  : "el-icon-video-play",
              }),
            ]
          ),
        ],
        1
      ),
    ]),
    _vm._v(" "),
    _c("div", { staticClass: "audio-player-title" }, [
      _vm._v("\n    " + _vm._s(_vm.title) + "\n  "),
    ]),
    _vm._v(" "),
    _c(
      "div",
      {
        staticClass: "audio-controls",
        on: {
          click: function ($event) {
            _vm.showVolume = false;
          },
        },
      },
      [
        _c("div", { staticClass: "audio-controls__handler" }, [
          _c("div", { staticClass: "play-times" }, [
            _c("div", { staticClass: "current-time" }, [
              _vm._v(_vm._s(_vm.playCurrentTime)),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "progress" },
              [
                _c("el-slider", {
                  attrs: { max: _vm.totalDuration, "show-tooltip": false },
                  on: { change: _vm.changeTime },
                  model: {
                    value: _vm.playTime,
                    callback: function ($$v) {
                      _vm.playTime = $$v;
                    },
                    expression: "playTime",
                  },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "total-time" }, [
              _vm._v(_vm._s(_vm.playDuration)),
            ]),
          ]),
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "volume-box",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _c("el-slider", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.showVolume,
                    expression: "showVolume",
                  },
                ],
                attrs: { vertical: "", height: "100px", "show-tooltip": false },
                on: { change: _vm.changeVolume },
                model: {
                  value: _vm.Volume,
                  callback: function ($$v) {
                    _vm.Volume = $$v;
                  },
                  expression: "Volume",
                },
              }),
              _vm._v(" "),
              _c("div", {
                staticClass: "volume-btn",
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                    _vm.showVolume = !_vm.showVolume;
                  },
                },
              }),
            ],
            1
          ),
        ]),
      ]
    ),
    _vm._v(" "),
    _c("audio", {
      ref: "musicAudio",
      staticClass: "audio-component",
      attrs: {
        controls: "",
        preload: "auto",
        src: _vm.url,
        loop: _vm.loop,
        link: _vm.link,
      },
      on: {
        canplay: _vm.changeDuration,
        timeupdate: _vm.updateTime,
        pause: function ($event) {
          _vm.isPlay = false;
        },
        play: _vm.onPlay,
      },
    }),
  ])
};
var __vue_staticRenderFns__$2G = [];
__vue_render__$2G._withStripped = true;

  /* style */
  const __vue_inject_styles__$2G = undefined;
  /* scoped */
  const __vue_scope_id__$2G = undefined;
  /* module identifier */
  const __vue_module_identifier__$2G = undefined;
  /* functional template */
  const __vue_is_functional_template__$2G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2G, staticRenderFns: __vue_staticRenderFns__$2G },
    __vue_inject_styles__$2G,
    __vue_script__$2G,
    __vue_scope_id__$2G,
    __vue_is_functional_template__$2G,
    __vue_module_identifier__$2G,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2F = {
  name: 'resource-list-pc',
  components: {
    xmlPreviewSearch: __vue_component__$2I,
    VideoModalPlayer: __vue_component__$5e,
    audioPlayerPage: __vue_component__$2H,
    audioPlayerPagePc: __vue_component__$2G
  },
  props: {
    textBookResource: {
      type: Object,
      default: () => {}
    },
    resourceType: {
      type: String,
      default: ''
    },
    resourceIcon: {
      type: String,
      default: ''
    },
    resourcesList: {
      type: Array,
      default: () => []
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: 'teacher'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    componentList: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    resourceType() {
      if (this.resourceType == 'teachingResource') {
        this.list = this.resourcesList || [];
      } else {
        this.list = this.textBookResource[this.resourceType] || [];
      }
      if (this.list.length > 0) {
        this.launchValue = 'teaching-resource_' + (this.resourceType == 'teachingResource' ? this.list[0].firstLevelId : this.list[0].chapterId);
      }
    },
    resourcesList() {
      if (this.resourceType == 'teachingResource') {
        this.list = this.resourcesList || [];
      }
    }
  },
  data() {
    return {
      list: [],
      launchValue: '',
      //教师资源展开的唯一值
      location: '',
      Ahover: false,
      itemHoverStates: {},
      videoData: {
        show: false,
        src: '',
        info: {}
      },
      audioData: {
        show: false,
        src: '',
        title: '',
        info: {}
      },
      isMasked: false,
      audioUrl: '',
      isPlay: '',
      currentResources: '',
      endTime: 0
    };
  },
  mounted() {
    this.sendList();
  },
  methods: {
    sendList() {
      this.$EventBus.$emit('listUpdate', this.list);
    },
    getFileUrl(url) {
      if (url && url.indexOf('./') !== -1) {
        return this.resourceBasisPath + url.split('./')[1];
      } else if (url && url.indexOf('./') === -1) {
        return url;
      }
    },
    // 跳转到资源所在位置
    resourceDetails(item, items) {
      this.currentResources = items.id;
      this.location = '';
      let data = {
        location: items.location,
        maxTop: -30,
        type: 'resource',
        selectorType: 'class',
        selectorId: items.titleClass,
        markTarget: '',
        classIndex: items.index
      };
      this.$emit('resourceDetails', data);
    },
    // 跳转教师笔记
    goTeacherNotes(item, items) {
      this.location = '';
      let list = items.location.split('/');
      let selectorId = `${list[2]}_#_${items.markEfiCode}`;
      let data = {
        location: items.location,
        maxTop: -30,
        type: 'marker',
        selectorType: 'id',
        selectorId,
        markTarget: items,
        classIndex: 0
      };
      this.$emit('resourceDetails', data);
    },
    resourcePlay(item, items) {
      if (items.resourcesType == 'recorded') {
        if (this.isPlay == '') {
          this.isPlay = items.id;
          this.audioUrl = items.fileUrl;
          this.$nextTick(() => {
            this.$refs.musicAudio.play();
          });
        } else {
          this.isPlay = '';
          this.$refs.musicAudio.pause();
        }
      }
    },
    // 跳转到资源所在位置
    resourceReader(item, items) {
      this.$emit('resourceReader', items);
    },
    setHoverState(index, isHovered) {
      this.$set(this.itemHoverStates, index, isHovered);
    },
    isItemHovered(index) {
      return this.itemHoverStates[index] || false;
    },
    // 图片预览
    imgPreview(item, items) {
      if (!items.isTrialExternal) {
        this.$EventBus.$emit('learningStatistics', {
          type: 'image',
          info: items
        });
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: this.getFileUrl(items.uploadFileUrl),
            title: items.title
          }]
        });
      }
      // this.$refs.xmlPopup.show = false
    },
    //资源预览
    resourcePrewiew(item, items) {
      if (items.isTrialExternal) {
        return;
      }
      if (items.resourcesType == 'image') {
        this.$EventBus.$emit('learningStatistics', {
          type: 'image',
          info: items
        });
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: this.getFileUrl(items.fileUrl),
            title: items.resourcesName
          }]
        });
      } else if (items.resourcesType == 'video') {
        this.videoData.src = this.getFileUrl(items.fileUrl);
        this.videoData.show = true;
        this.videoData.info = items;
      } else if (items.resourcesType == 'audio') {
        this.audioData.src = this.getFileUrl(items.fileUrl);
        this.audioData.show = true;
        this.audioData.title = items.resourcesName;
        this.audioData.info = items;
      } else if (items.resourcesType == 'office' || items.resourcesType == 'resources') {
        this.$emit('PrewiewOff', items);
      } else if (items.resourcesType == 'recorded') {
        if (this.isPlay == '') {
          this.isPlay = items.id;
          this.audioUrl = items.fileUrl;
          this.$nextTick(() => {
            this.$refs.musicAudio.play();
          });
        } else {
          this.isPlay = '';
          this.$refs.musicAudio.pause();
        }
      }
    },
    // 视频预览
    videoPreview(item, items) {
      if (!items.isTrialExternal) {
        this.videoData.src = this.getFileUrl(items.uploadFileUrl);
        this.videoData.show = true;
        this.videoData.info = items;
      }
      // this.$refs.xmlPopup.show = false
    },
    handleClose(done) {
      this.$confirm('确认关闭？').then(_ => {
        done();
      }).catch(_ => {});
    },
    // 音频预览
    audioPreview(item, items) {
      this.audioData.src = this.getFileUrl(items.uploadFileUrl);
      this.audioData.show = true;
      this.audioData.title = items.resourcesName;
      this.audioData.info = items;
      // this.$refs.xmlPopup.show = false
    },
    // 删除教师笔记
    deleteTeacherNotes(items) {
      this.$confirm(`确认删除此条笔记吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        duration: 1000
      }).then(() => {
        this.$emit('deleteTeacherNotes', items);
      }).catch(() => {});
    },
    deleteFile(item, items) {
      this.$confirm(`确认删除${items.resourcesName}吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        let data = {
          firstLevelId: item.chapterId,
          firstLevelLabel: item.firstLevelLabel,
          ...items
        };
        this.$emit('deleteTeachingResource', data);
      }).catch(() => {});
    },
    editFile(item, items) {
      let data = {
        firstLevelId: item.chapterId,
        firstLevelLabel: item.firstLevelLabel,
        ...items
      };
      this.$emit('editTeachingResource', data);
    },
    //下载
    downLoadFile(item, items) {
      // this.$emit('downLoadFile',items)
      // console.log(items,this.resourceBasisPath);
      window.open(items.fileUrl);
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      } else {
        return '';
      }
    },
    change(e) {
      let i = e.split('_');
      let id = i[1];
      for (let index = 0; index < this.list.length; index++) {
        const element = this.list[index];
        if (element.chapterId == id) {
          this.list[index].state = true;
          return;
        }
      }
    },
    seachItem(item, text) {
      let data = {
        location: item.location,
        maxTop: -30,
        type: 'search',
        selectorType: 'class',
        selectorId: 'xml-search-highlight',
        markTarget: '',
        classIndex: 0,
        seachText: text
      };
      this.$emit('resourceDetails', data);
    },
    scrollFun(firstLevelId, location) {
      let currentTime = new Date().getTime();
      this.launchValue = 'teaching-resource_' + firstLevelId;
      if (location == this.location && currentTime > this.endTime + 2000) {
        this.location = '';
        this.endTime = new Date().getTime();
      }
      setTimeout(() => {
        this.location = location;
      }, 200);
      setTimeout(() => {
        let Dom = document.getElementsByClassName('current-teacher-resources')[0];
        if (Dom) {
          let scrollTop = this.$refs.xmlResourceListPc.scrollTop + (Dom.getBoundingClientRect().top > 50 ? Dom.getBoundingClientRect().top - 50 : Dom.getBoundingClientRect().top < 0 ? Dom.getBoundingClientRect().top - 50 : 0);
          this.$refs.xmlResourceListPc.scrollTop = scrollTop;
        }
      }, 700);
    },
    formatTime(time) {
      if (time) {
        let Seconds = time / 1000;
        let m = parseInt(Seconds / 60);
        let s = Seconds % 60;
        // let date = (m > 9 ? m : '0' + m) + "'" + (s > 9 ? s : '0' + s) +
        let date = (m > 0 ? m + "'" : '') + s + '"';
        return date + ' 录音';
      } else {
        return '录音';
      }
    },
    timeVideoUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('videoTimeUpdate', {
        event,
        time,
        data,
        info: this.videoData.info
      });
    },
    timeAudioUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('audioTimeUpdate', {
        event,
        time,
        data,
        info: this.videoData.info
      });
    }
  }
};

var __$_require_static_images_icons_resource_lock_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2230%22%20height%3D%2230%22%20viewBox%3D%220%200%2030%2030%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M25.0612%2012.739H22.8417V8.07855C22.8417%204.17358%2019.3255%201%2014.9998%201C10.6744%201%207.15441%204.17358%207.15441%208.07855V12.739H4.93665C3.86676%2012.739%203%2013.5216%203%2014.4874V27.25C3%2028.2171%203.86676%2029%204.93665%2029H25.0632C26.1331%2029%2027%2028.2171%2027%2027.25V14.4874C27%2013.5216%2026.1332%2012.739%2025.0612%2012.739ZM16.0872%2021.2017V23.9876C16.0872%2024.1124%2015.9729%2024.2183%2015.8342%2024.2183H14.1676C14.0268%2024.2183%2013.9129%2024.1124%2013.9129%2023.9876V21.2017C13.1299%2020.8396%2012.5881%2020.1168%2012.5881%2019.2691C12.5881%2018.0675%2013.6696%2017.0917%2014.9998%2017.0917C16.3305%2017.0917%2017.4099%2018.0675%2017.4099%2019.2691C17.4136%2020.1168%2016.8702%2020.8396%2016.0872%2021.2017ZM19.831%2012.739H10.1687V8.19313C10.1687%205.7886%2012.3392%203.83183%2015.0019%203.83183C17.6664%203.83183%2019.8309%205.7886%2019.8309%208.19313L19.831%2012.739Z%22%20fill%3D%22white%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_default_over_video_cover_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_resource_note_icon_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2218%22%20height%3D%2218%22%20viewBox%3D%220%200%2018%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M9.27034%2010.2555C9.80903%209.48573%2011.7823%206.71929%2012.5213%206.12418C11.579%208.23479%209.72279%2010.4064%208.61195%2012.4771C10.664%2013.1309%2011.594%2012.356%2012.7261%2011.4539L11.6043%2011.2515C13.029%2010.8125%2012.9295%2010.9751%2013.4284%2010.3266C13.8242%209.8123%2014.1527%209.11816%2014.2731%208.39322L13.346%208.50179C13.6418%208.33986%2014.3005%208.05532%2014.4052%207.89662C14.685%207.47231%2015.42%203.41471%2015.4286%202.57129C12.5954%202.59592%208.06861%208.16155%207.74116%2010.8215C7.41169%2013.4959%204.17444%2017.537%209.27034%2010.2555ZM13.4937%2011.7593C14.3387%2012.2331%2014.8413%2012.845%2014.8413%2013.5268C14.8413%2017.2052%202.57141%2017.2052%202.57141%2013.5268C2.57141%2011.8377%205.35854%2011.0801%206.80242%2010.8726C6.76843%2011.0999%206.69425%2011.3317%206.59731%2011.5687C5.60493%2011.7394%203.26887%2012.3708%203.26887%2013.5268C3.26887%2016.3054%2014.1438%2016.3054%2014.1438%2013.5268C14.1438%2013.0392%2013.6767%2012.574%2012.9049%2012.2C13.1025%2012.0647%2013.2964%2011.9154%2013.4937%2011.7593Z%22%20fill%3D%22%23ffffff%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_resource_invisible_02_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAWCAYAAADTlvzyAAAAAXNSR0IArs4c6QAAAstJREFUSEu9lVmIjmEYhq/HHkWWE0v2UrInSSlLyhZFzoyEI2eWKCYksmQ5c2QcYE6USGMpWVLW5ECRbexbKSRkncd3j+f7vfP7zUhj3pr+f/q/972e537v+/mMJl7WxDx+A7r7BGA6UG5mHxu7oFLAHcAS4IzAjQ0tBWwLVAHj/we05B26ezH0IDAOGAz0BlqH1J+BB8B14BxwyMye13cNfzSNuw8HLgGtkgO+A0+Bt0BzoDPQBWgZz9QICqw1sxulwKUk1UHl8dcC+AS0AW4BY4BO8enABeBJBhkKTAPmhwIqbFMGXGdm+l5YdYAh5YHYrC5WAZXA4bjTR0CvZL+ge4DFZvbF3VWgoJuj+2PAnNR4BaC7q7PjwKTMnVeB2Wb2WIdHITcT2BbgA7AgOqows0V5Ie7eI6QdCZwEpuSdpsANwGrgiqBm9i45oB9wL+msNjJhnmtRSH8zu5/saR+wUdkdbzQzXdPP4Lt7H+AOIMgQM3uW6u7uZcBeQJ3pgEJkgGXAeqDMzPYX7eseDhZ8gArKgergNvAmgC+KNs4F9gFrgO1FOZUiK4F5ZqZnCsvduwawYwCrU0lV/YpwniQtjDV37xuSyjQjAOUvHw45QJJWJ5Iqy7o/OXurmamoX7M0HHYCmAhczCqbZWYvkwN2ZxFYmLnwYTizXXSmR1TIwLxId++WDQkNi9HAqSzPk7M7/FYHmLjxSEBfxf1UmlmNu2sA7ApnpnHKoyIjzZS7gW0RC8FmlIxF0ommhhy7PDNDM+AuUAEcBRQNjTbJJKiCr8LOA4MiKupcYRdUb5zazvJV32gbFuCpifSaOpqVrzMDaYx1KJqtOvdrJuNYM7ucghoEJh33DJny4a3/NSS0VL0ipOF9NrJZ71vmn9747i7ZzMzeF8WgwVfbPwFLSVUUhzwyO7OJtfSv7rC+Qxv6LWavjFdlZqfT538AuZz9F89GCIAAAAAASUVORK5CYII=";

var __$_require_static_images_icons_resource_invisible_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAWCAYAAADTlvzyAAAAAXNSR0IArs4c6QAAA6pJREFUSEu9lVtoXFUUhr91ztgJBup4IRAVrVooiNYLIhIQqhLwUhQszpMVMWZ2koGAVixo0EEqWqkKecjkzDh9iPZlQCJSLxBqpWC8ID4ELN61XiaGghkalYzJ2cvZ9ZzhNJm2UGL30wxn7/Xttdb/ry2c5SVnmccqYLlcvt1auxUYMcb8vdYXWgUMguAV4DHgILB1raHtgOcC+4Hb/g9o2x4GQbAS+qaIbFHVa4ENQDoqdQP4UVVnRORQKpWa7Ovrq52qDScVTbFYvMHzvE+AdYkAIfArUAd84ELgIuCcaI8FJkXk2Vwu92U78CpgtVr15+fnR5xogBSwCHQAX4Vh2OP7/gUi0mOtVWvttO/7v6jqdSJyD/BwVIFQRF7IZDKFbDbrLtlaJwCjUlYBd9hl8VSj0diXTqffinp6BLg8cV5FZG8mkxnKZrP/FAqFVHd3t4O+6LIXkXdV9YGk8FpAl1m9Xn9PVXub6vwc2GaM+dkFjy5yOIaJyG5V/Qt4xGUkIpVcLvdofJFyuXyptXayKb6bRGQqk8ncFWfaAgZBsAt4WkQ+W1xc7B0eHj4WBwiC4Crgu0Rmxy0ThmHa9/0v3EXCMNw4NDT0Q7xndHR0fUdHx5Sq3tzs8fPGGNei/4xfLpevsNZ+Axyz1m4eHBz8LVn3Uqm0XVUnosxcgJZlgB3Ac6q6fWBg4I3kuWKxeInneTPA+jAMN7kLHQdGGXwNzAObjTGzyYPj4+MPisjrwDPAy0mfuoqo6k4ReSiXy7k9rRUEQTfggOcDm4wx3ydLuht4EpgGepONHhsbu9L3fVfSI2EY3uj7vvNfPBxiwEYXMNEG5+UpoAd4yRizs1VS9yNS2PvAHcDHy8vL9+fz+d/jAKVS6TVV7WtK/ydgr4h0usyi7069V8eXrFQqFy8tLblhcQtwYHZ29s5CobB8AjChxrcj6FER2VGr1fYVCgVbrVbX1ev1MVV1ykzaKbbKwVQqdV8YhttUdU80FA4A97a1RaIUbmo4xT7RFIMHfKuqFVV9Z25u7nBXV9cGz/N6PM8TVZ1uNBpH0+n0R8A1gLNKJ+CMv6dWq43EmcXxTzraSqXS9arqwHcnMnJTx83KP0TEqup5K2ari7vULOOtxphPkwI6LTCR8WVuCABbADe83X83R91yfXEWmlHVD0XEvaOnfGXO6MWfmJjoXFhYkHw+/+cKG5z2aTsjYLtSrbBDbJlXjTGPJ/evOTCh9l2e5+3v7+//IAn8F8PthyYfFsMJAAAAAElFTkSuQmCC";

var __$_require_static_images_icons_books_edit_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAeCAYAAAAy2w7YAAAAAXNSR0IArs4c6QAAAphJREFUSEu11k9IFFEcB/Dvb8ZNF3cmgi3Cg/1B7BIkEYTOJrKnPUQh/YEoPHiNCFxWy5MFhe0sEXiqDkUURNElpItgUUyGYJfCUikIwQ4FhTOuUrvvF7PbbLrN/pvdfcf3Zn4ffr/33m+GUIMRCOk9xNAB+Ag8vGwMPssPS9U6gS49TIRxBmYl4BMDvYB03DSiT9fHrgpyEIDeKGvW4aWZkaSixS8AdDkf8wz9Q7DYCHHguzFkOhm4YZ6gXLkYH4jQwcBDq2VHHx6fTBfCKobyy2U1BXoZfK8YRqCjFUFue2JnoGqJ0wWxkD4ORrBsqBDilMoNCxy6upXEpmkGz5cFlULcMPZxVPpNEwC2M9I9JaFykXwMwDKAlBByeGVq4F1RqFLExuxyScI3w0BQCPmgjdjzBSGvCAnfpF0uJxMnU1eo1ohrRvVA/oPqhWyA6onkIDUU72OmWwCmlbWViN2FS30+spfRfePd3s0cBkXTXwHoAmAxke5v8N/49uKsVQirFMllpGiJ94C4D9BOAP3Zy8Y3zR/JS5gd+bUe9IJkoROPZGXpS5IhIpYx9HyLNtqagnwFwBmAL5rG4KgDeUUy0OZufZdI43NKllpXX0YXc0E1/QEBYVPIuzE1sFoNkoHUUDwimJ5YRqx5fYnUzuttLKU/gnmY5dSdSjbe9TCoocR5Zu43jdi+/AcUTb8L4AiArwC25beVUidzw89JQNPHCGgxjdgxeyGoXVOSoqFNktLtYHQS0Tn7cAghdzsNshIg1+tUTZ9gYC+AeQDtdkP8u7iUmWP+yaAx63XMbpaeByla/DYDfjAtAJgTLM01NzYtFLtHXrSSHz4vQQt2hloFKxaH1FDiFMB76ovRHClaYhLg/XWG3v4BEfuzLdxGxe4AAAAASUVORK5CYII=";

var __$_require_static_images_icons_books_delete_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAeCAYAAAA2Lt7lAAAAAXNSR0IArs4c6QAAAfBJREFUSEvtlrFrVEEQxr+Zl+SCWuTSihEh9hGEFCKJCCa7uesUMRYasPVv8AoFO9E/QCxExMZY5O07SJEiWiloZZugoGJjRJHD7H66icpx9+5y78BGb9qdmd/sx+zMCv6ySS/5Wa3uQ5KM5Po2GkGc+9wpT1cAFxbKnnwi5Mk9CtlU4KI497TVryvAG3MVwB0B6gTe50JIhch5AdbVudPFANbWQF5TkWOSpi873cJbu3OWpOlULoCVytHg/WMAB1ockhgH4DsAdpFp+NdZ9Gu2LzsScW5u0qs+EJERkJMA9kPkVS8N0OZDTgAoA3hNYBfQbNvGrAkwkzjXU4e1xntj7gG4pOQRybKN/wTgjbkO4Fvi3I1taxeFrCbOXaC1UyRvSghLUq+/i3L1JZE3ZgMin2Ib/klQKpVDo3EZwK1AnhrOsrUBYCDR7pvMe8mDLtpz2P0DEs3PP6fI1yHnZry1t0FeUWDcA2cFuK+qx2Vl5UXfoyIuf8Tfw+rqFo0pIYTxONwICKydkDTd/K1zX8OuyGbrGaDOqXTfw7ncYO0jkucUOCTOvW3baN6YuwCWSD5T4GOR6qE69DPuDIAPOj19WGq10AZgpXLQe/9QgBOIGhe3N0ouSpatx9COCTg7O4qxsdHC+ZeXt5ql7afCQswffRcc+OCKtZMAAAAASUVORK5CYII=";

var __$_require_static_images_icons_resource_download_icon_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAA7RJREFUWEftlk9oFHcUx79vZsXdzExZTz0YMIKlQSxdQci6o5DSHCL2EJCempKA9laoJftPrDiiEHY3oMUeBD2kVMFCwYCUWkIxh8xutDm0UEoKFiPYPwdB6c7UmJ2dZ36bbjPZbHZnlxUv/q7z3vt+fu/3/gzhJR96yfp4BdByBkJ9magcoBEwDYC4RzwhMR66wAxYum4X4t+38qz+ASJGWFO6zgM0KgSY8QuABQAOwL1EFFkV5luOwx89vZN66AfEF4DSd+F1kkszROgF4yqxc+afwol7XoFgdLxnixw4C2AYzIslSP1L+fiDZhC+AFQ9O02gARc0apvxLxsF1fTcUQBXmLHgAmkq2XfsH42/N/NpCqDpEyMATzLzOSufPNXsRuK7pmfHAUpXbZl5iojTRTP1W61/fYCIEVa7lE8AHiSiPQxY1h/2TiwaS34A0G8EQk9D+wIy9UKiw8w8JGqFXXfILqTXFekGAOVg5l0q0yQRdTMgUncPkL6wzLGvfYnXMXrtQK6PGVMMDhM44s3EOgBNz7zJoJ/AeMQsj9qFsR/aFa31ExAu8yxAs5aZeKf6fR2AquduExAplZ29S3MnFjslXo2jxXJfgTBcKjs7q/H/B/ivje6DkS7mE5lOi4t4aix7ZKWmvnHhDttm6lpliFWFFD3zgQTpKvPyW1b+pBgyHT9qdLyf5MBt1+XjdiH5eQ3AxIgEnlx2nTee1QyZTpGsZWBtnqxlIJobkGRMlxnv/ZtPfNspUW8cTc9eBuiY95JrRdhjBLXtyl8rvT9XNJOH/ACosewNMSPsfPLDZvbB2MSOALkLBJovmomD9bsgljtJhHPMMKx84kyzoFose5+BJ1Y+ubehrVhkXV3TTNjDJTlq3x37uS6AmGBaSbkJYBDMc2WmKxI7v8NxH1nzGwuzIUDECCvBrfsgSW8T6LgYbPU6bOMo7jeC6rKSAnGaQMEqab3ibASg6rmLBHws/MVEZVDcNuOV1vOezZdRZR+EBiSSul24S5aZulTr3Ahg6/7xXVskaZDL7oIdfDaDGcOp90xNt6HXKdSX6ZYDpFtmsrIXagFEmzmQ5v38B9SvgSZVp8ayZ4nos5WhNlE0EwkvgBrLnSaCwcAFy0x82qyA2wLAbkNVtynfEXBACBHzkOgCgKZWxWnWemwdwq+G9WIARFQPxGqBsUOgQDvi60axX+KKXQ0EgFvFx/b7rdy8vSfwUq5C3AQjbP1p7/f9t+S7DVtKSfvGLbVh+zKbe74CeA6HppswnaiUHAAAAABJRU5ErkJggg==";

/* script */
const __vue_script__$2F = script$2F;
var __vue_render__$2F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { ref: "xmlResourceListPc", staticClass: "xml-resource-list-pc" },
    [
      _vm.resourceType == "imageList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "flie-title resource-list",
                        class: { "is-masked": _vm.isMasked },
                      },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item image-item",
                            class: { "is-masked": _vm.isMasked },
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.imgPreview(item, items)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass: "mask-layer-trial-external",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "cover",
                                attrs: { title: "点击预览" },
                              },
                              [
                                item.state
                                  ? _c("img", {
                                      attrs: {
                                        src: _vm.getFileUrl(
                                          items.uploadFileUrl
                                        ),
                                        alt: "",
                                      },
                                    })
                                  : _vm._e(),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", { staticClass: "image-title-box" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "image-title",
                                  attrs: { title: items.title },
                                },
                                [
                                  _vm._v(
                                    _vm._s(
                                      items.title ? items.title : item.label
                                    )
                                  ),
                                ]
                              ),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "resource-icon",
                                attrs: { title: "跳转" },
                                on: {
                                  click: function ($event) {
                                    $event.stopPropagation();
                                    return _vm.resourceDetails(item, items)
                                  },
                                },
                              }),
                            ]),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "videoList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item image-item",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.videoPreview(item, items)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass: "mask-layer-trial-external",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "cover",
                                attrs: { title: "点击预览" },
                              },
                              [
                                item.state && items.coverFileUrl
                                  ? _c("img", {
                                      attrs: {
                                        src: _vm.getFileUrl(items.coverFileUrl),
                                        alt: "",
                                      },
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                !items.coverFileUrl
                                  ? _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_default_over_video_cover_png__,
                                        alt: "",
                                      },
                                    })
                                  : _vm._e(),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", { staticClass: "image-title-box" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "image-title",
                                  attrs: { title: items.title },
                                },
                                [_vm._v(_vm._s(items.title))]
                              ),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "resource-icon",
                                attrs: { title: "跳转" },
                                on: {
                                  click: function ($event) {
                                    $event.stopPropagation();
                                    return _vm.resourceDetails(item, items)
                                  },
                                },
                              }),
                            ]),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "audioList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.audioPreview(item, items)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass:
                                  "mask-layer-trial-external min-mask-layer",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "icon",
                              class: _vm.resourceIcon,
                            }),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "flie-title resource-title",
                                attrs: { title: items.title },
                              },
                              [_vm._v(_vm._s(items.title))]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "resource-icon",
                              attrs: { title: "跳转" },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.resourceDetails(item, items)
                                },
                              },
                            }),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "questionList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass:
                                  "mask-layer-trial-external min-mask-layer",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "icon",
                              class: _vm.resourceIcon,
                            }),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "flie-title resource-title",
                                attrs: {
                                  title: _vm.removeStyle(items.stem.content),
                                },
                              },
                              [
                                _vm._v(
                                  "\n              " +
                                    _vm._s(
                                      _vm.removeStyle(items.stem.content)
                                    ) +
                                    "\n            "
                                ),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "resource-icon",
                              attrs: { title: "跳转" },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.resourceDetails(item, items)
                                },
                              },
                            }),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "testPaperList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass:
                                  "mask-layer-trial-external min-mask-layer",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "icon",
                              class: _vm.resourceIcon,
                            }),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "flie-title resource-title",
                                attrs: { title: items.paperName },
                              },
                              [
                                _vm._v(
                                  "\n              " +
                                    _vm._s(items.paperName || items.title) +
                                    "\n            "
                                ),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "resource-icon",
                              attrs: { title: "跳转" },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.resourceDetails(item, items)
                                },
                              },
                            }),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "formulaList" ||
          _vm.resourceType == "interactionList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item flex-column",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass: "mask-layer-trial-external",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", { staticClass: "resource-title-box" }, [
                              _c("div", {
                                staticClass: "icon",
                                class: _vm.resourceIcon,
                              }),
                              _vm._v(" "),
                              _c(
                                "div",
                                {
                                  staticClass: "flie-title resource-title",
                                  attrs: { title: items.fileNames },
                                },
                                [_vm._v(_vm._s(items.fileNames))]
                              ),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "resource-icon",
                                attrs: { title: "跳转" },
                                on: {
                                  click: function ($event) {
                                    $event.stopPropagation();
                                    return _vm.resourceDetails(item, items)
                                  },
                                },
                              }),
                            ]),
                            _vm._v(" "),
                            _c("XmlSingleHtmlGroupRender", {
                              directives: [
                                {
                                  name: "show",
                                  rawName: "v-show",
                                  value: _vm.resourceType == "formulaList",
                                  expression: "resourceType == 'formulaList'",
                                },
                              ],
                              attrs: {
                                pageType: "pc",
                                mode: "preview",
                                "p-group-data": {
                                  htmlData: Object.assign({}, items),
                                },
                                resourceBasisPath: _vm.resourceBasisPath,
                              },
                            }),
                          ],
                          1
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "resourceList"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.chapterId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " + _vm._s(item.label) + "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item[_vm.resourceType], function (items, index) {
                        return _c(
                          "div",
                          {
                            staticClass: "resource-item",
                            on: {
                              mouseover: function ($event) {
                                return _vm.setHoverState(index, true)
                              },
                              mouseout: function ($event) {
                                return _vm.setHoverState(index, false)
                              },
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.resourceDetails(item, items)
                              },
                            },
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: items.isTrialExternal,
                                    expression: "items.isTrialExternal",
                                  },
                                ],
                                staticClass:
                                  "mask-layer-trial-external min-mask-layer",
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_resource_lock_svg__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "icon",
                              class: _vm.resourceIcon,
                            }),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "flie-title resource-title",
                                attrs: {
                                  title:
                                    items.ilk == "ebook"
                                      ? items.name
                                      : items.title,
                                },
                              },
                              [
                                _vm._v(
                                  _vm._s(
                                    items.ilk == "ebook"
                                      ? items.name
                                      : items.title
                                  )
                                ),
                              ]
                            ),
                            _vm._v(" "),
                            _c("div", {
                              staticClass: "resource-icon",
                              attrs: { title: "跳转" },
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.resourceDetails(item, items)
                                },
                              },
                            }),
                          ]
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm.resourceType == "teachingResource"
        ? [
            _c(
              "el-collapse",
              {
                attrs: { accordion: "" },
                on: { change: _vm.change },
                model: {
                  value: _vm.launchValue,
                  callback: function ($$v) {
                    _vm.launchValue = $$v;
                  },
                  expression: "launchValue",
                },
              },
              _vm._l(_vm.list, function (item, index) {
                return _c(
                  "el-collapse-item",
                  {
                    key: index,
                    attrs: { name: "teaching-resource_" + item.firstLevelId },
                  },
                  [
                    _c("template", { slot: "title" }, [
                      _c("div", { staticClass: "collapse-title" }, [
                        _vm._v(
                          "\n            " +
                            _vm._s(item.firstLevelLabel) +
                            "\n          "
                        ),
                      ]),
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "resource-list" },
                      _vm._l(item.list, function (items, index) {
                        return _c(
                          "div",
                          {
                            key: index,
                            staticClass: "xml-teaching-resource",
                            class:
                              items.location == _vm.location
                                ? "current-teacher-resources"
                                : "",
                          },
                          [
                            items.resourcesType == "notes"
                              ? [
                                  _c(
                                    "div",
                                    { staticClass: "xml-teacher-note" },
                                    [
                                      _c(
                                        "div",
                                        { staticClass: "xml-note-header" },
                                        [
                                          _c("img", {
                                            staticClass: "note-icon",
                                            attrs: {
                                              src: __$_require_static_images_icons_resource_note_icon_svg__,
                                              alt: "",
                                            },
                                          }),
                                          _vm._v(" "),
                                          _c("img", {
                                            directives: [
                                              {
                                                name: "show",
                                                rawName: "v-show",
                                                value:
                                                  items.isStudentShow != 0 &&
                                                  _vm.userType == "teacher",
                                                expression:
                                                  "items.isStudentShow != 0 && userType == 'teacher'",
                                              },
                                            ],
                                            staticClass: "eye",
                                            attrs: {
                                              src: __$_require_static_images_icons_resource_invisible_02_png__,
                                              alt: "",
                                            },
                                          }),
                                          _vm._v(" "),
                                          _vm.userType == "teacher"
                                            ? _c(
                                                "el-popover",
                                                {
                                                  attrs: {
                                                    placement: "bottom",
                                                    width: "40px",
                                                    trigger: "hover",
                                                    "popper-class":
                                                      "customPopper",
                                                  },
                                                },
                                                [
                                                  _c(
                                                    "div",
                                                    {
                                                      staticClass:
                                                        "teacher-note-popover",
                                                    },
                                                    [
                                                      _c(
                                                        "span",
                                                        {
                                                          on: {
                                                            click: function (
                                                              $event
                                                            ) {
                                                              return _vm.$emit(
                                                                "updateTeacherNotes",
                                                                items
                                                              )
                                                            },
                                                          },
                                                        },
                                                        [_vm._v("编辑")]
                                                      ),
                                                      _vm._v(" "),
                                                      _c(
                                                        "span",
                                                        {
                                                          on: {
                                                            click: function (
                                                              $event
                                                            ) {
                                                              return _vm.deleteTeacherNotes(
                                                                items
                                                              )
                                                            },
                                                          },
                                                        },
                                                        [_vm._v("删除")]
                                                      ),
                                                    ]
                                                  ),
                                                  _vm._v(" "),
                                                  _c("i", {
                                                    staticClass: "el-icon-more",
                                                    attrs: {
                                                      slot: "reference",
                                                    },
                                                    slot: "reference",
                                                  }),
                                                ]
                                              )
                                            : _vm._e(),
                                        ],
                                        1
                                      ),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        {
                                          staticClass: "note-content",
                                          on: {
                                            click: function ($event) {
                                              return _vm.goTeacherNotes(
                                                item,
                                                items
                                              )
                                            },
                                          },
                                        },
                                        [
                                          _c("xmlTextOverflow", {
                                            attrs: { info: items.noteContent },
                                          }),
                                        ],
                                        1
                                      ),
                                    ]
                                  ),
                                ]
                              : [
                                  _c(
                                    "div",
                                    {
                                      staticClass:
                                        "resource-item teaching-resource-item",
                                      class:
                                        _vm.currentResources == items.id
                                          ? "xml-current-resources"
                                          : "",
                                    },
                                    [
                                      _c("div", {
                                        staticClass: "icon",
                                        class: [
                                          items.resourcesType == "recorded"
                                            ? "recorded"
                                            : items.fileExt
                                            ? items.fileExt.replace(/\./gi, "")
                                            : "",
                                          _vm.isPlay == items.id
                                            ? "isPlay"
                                            : "",
                                        ],
                                      }),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        {
                                          staticClass:
                                            "resource-title resource-title-width",
                                          on: {
                                            click: function ($event) {
                                              $event.stopPropagation();
                                              return _vm.resourcePrewiew(
                                                item,
                                                items
                                              )
                                            },
                                          },
                                        },
                                        [
                                          _c(
                                            "span",
                                            {
                                              staticClass: "teacher-flie-title",
                                              attrs: {
                                                title: items.resourcesName,
                                              },
                                            },
                                            [
                                              _vm._v(
                                                _vm._s(
                                                  items.resourcesType ==
                                                    "recorded"
                                                    ? _vm.formatTime(
                                                        items.duration
                                                      )
                                                    : items.resourcesName
                                                )
                                              ),
                                            ]
                                          ),
                                          _vm._v(" "),
                                          _c("img", {
                                            directives: [
                                              {
                                                name: "show",
                                                rawName: "v-show",
                                                value:
                                                  items.isStudentShow != 0 &&
                                                  _vm.userType == "teacher",
                                                expression:
                                                  "items.isStudentShow != 0 && userType == 'teacher'",
                                              },
                                            ],
                                            attrs: {
                                              src: __$_require_static_images_icons_resource_invisible_png__,
                                              alt: "",
                                            },
                                          }),
                                        ]
                                      ),
                                      _vm._v(" "),
                                      _vm.userType == "teacher"
                                        ? _c("img", {
                                            attrs: {
                                              src: __$_require_static_images_icons_books_edit_png__,
                                              alt: "",
                                            },
                                            on: {
                                              click: function ($event) {
                                                $event.stopPropagation();
                                                return _vm.editFile(item, items)
                                              },
                                            },
                                          })
                                        : _vm._e(),
                                      _vm._v(" "),
                                      _vm.userType == "teacher"
                                        ? _c("img", {
                                            attrs: {
                                              src: __$_require_static_images_icons_books_delete_png__,
                                              alt: "",
                                            },
                                            on: {
                                              click: function ($event) {
                                                $event.stopPropagation();
                                                return _vm.deleteFile(
                                                  item,
                                                  items
                                                )
                                              },
                                            },
                                          })
                                        : _vm._e(),
                                      _vm._v(" "),
                                      items.isDownload == 1 &&
                                      _vm.userType != "teacher"
                                        ? _c("img", {
                                            staticStyle: { width: "20px" },
                                            attrs: {
                                              src: __$_require_static_images_icons_resource_download_icon_png__,
                                              alt: "",
                                            },
                                            on: {
                                              click: function ($event) {
                                                $event.stopPropagation();
                                                return _vm.downLoadFile(
                                                  item,
                                                  items
                                                )
                                              },
                                            },
                                          })
                                        : _vm._e(),
                                      _vm._v(" "),
                                      _c("div", {
                                        staticClass: "resource-icon",
                                        attrs: { title: "跳转" },
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.resourceDetails(
                                              item,
                                              items
                                            )
                                          },
                                        },
                                      }),
                                    ]
                                  ),
                                ],
                          ],
                          2
                        )
                      }),
                      0
                    ),
                  ],
                  2
                )
              }),
              1
            ),
          ]
        : _vm._e(),
      _vm._v(" "),
      _c("xml-empty", {
        directives: [
          {
            name: "show",
            rawName: "v-show",
            value: _vm.list.length == 0 && _vm.resourceType != "search",
            expression: "list.length == 0 && resourceType != 'search'",
          },
        ],
      }),
      _vm._v(" "),
      _vm.resourceType == "search"
        ? _c("xml-preview-search", {
            staticClass: "search-toobar",
            attrs: { componentList: _vm.componentList },
            on: { seachItem: _vm.seachItem },
          })
        : _vm._e(),
      _vm._v(" "),
      _c("audio", {
        ref: "musicAudio",
        staticClass: "audio-component",
        attrs: { controls: "", preload: "auto", src: _vm.audioUrl },
        on: {
          ended: function ($event) {
            _vm.isPlay = "";
          },
        },
      }),
      _vm._v(" "),
      _c(
        "el-dialog",
        {
          attrs: {
            visible: _vm.videoData.show,
            width: "1000px",
            "z-index": "1000",
            "show-close": false,
            "before-close": _vm.handleClose,
          },
          on: {
            "update:visible": function ($event) {
              return _vm.$set(_vm.videoData, "show", $event)
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "video-content" },
            [
              _c(
                "div",
                {
                  staticClass: "xml-el-icon-close-dialog",
                  on: {
                    click: function ($event) {
(_vm.videoData.show = false), (_vm.videoData.src = "");
                    },
                  },
                },
                [_c("i", { staticClass: "el-icon-close" })]
              ),
              _vm._v(" "),
              _vm.videoData.show
                ? _c("video-modal-player", {
                    attrs: { src: _vm.videoData.src },
                    on: {
                      timeUpdate: function (e) {
                        return _vm.timeVideoUpdate(e)
                      },
                    },
                  })
                : _vm._e(),
            ],
            1
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "resource-item", staticStyle: { position: "relative" } },
        [
          _c(
            "el-dialog",
            {
              attrs: {
                visible: _vm.audioData.show,
                width: "840px",
                "show-close": false,
                "before-close": _vm.handleClose,
              },
              on: {
                "update:visible": function ($event) {
                  return _vm.$set(_vm.audioData, "show", $event)
                },
              },
            },
            [
              _c(
                "div",
                {
                  staticClass: "xml-el-icon-close-dialog",
                  on: {
                    click: function ($event) {
                      _vm.audioData.show = false;
                    },
                  },
                },
                [_c("i", { staticClass: "el-icon-close" })]
              ),
              _vm._v(" "),
              _vm.audioData.show
                ? _c("audio-player-page-pc", {
                    staticStyle: { "z-index": "9999" },
                    attrs: {
                      url: _vm.audioData.src,
                      title: _vm.audioData.title,
                    },
                    on: {
                      back: function ($event) {
                        _vm.audioData.show = false;
                      },
                      timeUpdate: function (e) {
                        return _vm.timeAudioUpdate(e)
                      },
                    },
                  })
                : _vm._e(),
            ],
            1
          ),
        ],
        1
      ),
    ],
    2
  )
};
var __vue_staticRenderFns__$2F = [];
__vue_render__$2F._withStripped = true;

  /* style */
  const __vue_inject_styles__$2F = undefined;
  /* scoped */
  const __vue_scope_id__$2F = undefined;
  /* module identifier */
  const __vue_module_identifier__$2F = undefined;
  /* functional template */
  const __vue_is_functional_template__$2F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2F, staticRenderFns: __vue_staticRenderFns__$2F },
    __vue_inject_styles__$2F,
    __vue_script__$2F,
    __vue_scope_id__$2F,
    __vue_is_functional_template__$2F,
    __vue_module_identifier__$2F,
    false,
    undefined,
    undefined,
    undefined
  );

const fontSizeLabelMap = {
  1: '小',
  2: '中',
  3: '大'
};
const fontSizeValueMap = {
  1: '12px',
  2: '16px',
  3: '20px'
};

// 系统环境变量
class systemEnvironmentVariables {
  constructor(key, e) {
    let systemEnvList = [{
      systemName: '机械工业天工讲堂',
      system: 'jx',
      //系统缩写
      isJiXie: true,
      //是否是机械工业天工讲堂
      className: '',
      systemId: '885d44db-8f0f-41ff-b2af-7ec4a6844685',
      theFirstLayout: true,
      //第一种布局
      toolbarFirst: true,
      //第一种pc端工具栏
      isShowAi: false,
      //是否显示Ai
      toolbarList: [{
        name: '搜索',
        type: 'search',
        default: 'icon_01',
        select: 'icon_up_01',
        total: 'none',
        icon: ''
      }, {
        name: '授课',
        type: 'teachingResource',
        default: 'icon_02',
        select: 'icon_up_02',
        total: 'teachingResourceTotal',
        icon: ''
      }, {
        name: '图片',
        type: 'imageList',
        default: 'icon_03',
        select: 'icon_up_03',
        total: 'imageTotal',
        icon: ''
      }, {
        name: '视频',
        type: 'videoList',
        default: 'icon_04',
        select: 'icon_up_04',
        total: 'videoTotal',
        icon: ''
      }, {
        name: '音频',
        type: 'audioList',
        default: 'icon_05',
        select: 'icon_up_05',
        total: 'audioTotal',
        icon: 'icon_01'
      }, {
        name: '公式',
        type: 'formulaList',
        default: 'icon_06',
        select: 'icon_up_06',
        total: 'formulaTotal',
        icon: 'icon_02'
      }, {
        name: '互动',
        type: 'interactionList',
        default: 'icon_07',
        select: 'icon_up_07',
        total: 'interactionTotal',
        icon: 'icon_03'
      }, {
        name: '试题',
        type: 'questionList',
        default: 'icon_08',
        select: 'icon_up_08',
        total: 'questionTotal',
        icon: 'icon_04'
      }, {
        name: '文件',
        type: 'resourceList',
        default: 'icon_09',
        select: 'icon_up_09',
        total: 'resourceTotal',
        icon: 'icon_05'
      }],
      isAddMark: e.isAddMark,
      //是否可以添加标记
      isNoteHighlight: e.userType == 'student',
      //是否有笔记高亮功能
      tippy: {
        copy: true,
        //复制
        copyIcon: 'copy',
        copyLine: false,
        note: e.userType == 'student',
        //笔记
        noteIcon: 'note',
        noteLine: true,
        resources: e.userType == 'teacher',
        //资源
        resourcesIcon: 'addTeacherResources',
        resourcesLine: true,
        highlight: e.userType == 'student',
        //高亮
        highlightIcon: 'highlight',
        highlightLine: true,
        link: false,
        //划线
        linkIcon: '',
        linkLine: false,
        dictionary: false,
        //词典
        dictionaryIcon: 'dictionary',
        dictionaryLine: true,
        encyclopedias: false,
        //百科
        encyclopediasIcon: 'encyclopedias',
        encyclopediasLine: true,
        maxWordCount: true,
        //最大选择字数
        wordIcon: '',
        wordLine: true,
        bookmark: e.userType == 'student',
        //书签
        bookmarkIcon: 'bookmark',
        bookmarkLine: true
      },
      noteStyle: 'mk-underline__dashed',
      highlight: 'mk-hightlight__default',
      linkStyle: '',
      resourcesStyle: 'mk-underline__default',
      isShowToolbar: true //移动端工具栏默认是否显示
    }, {
      systemName: '华腾',
      system: 'ht',
      //系统缩写
      isHuaTeng: true,
      //是否是华腾
      className: e.pageType == 'pc' ? 'xml-reader-preview-huateng' : 'xml-reader-preview-guokai',
      systemId: '855e5965-d6a9-4aa0-9933-177e705399af',
      theFirstLayout: true,
      //第一种布局
      toolbarSecond: true,
      //第二种pc端工具栏
      isShowAi: true,
      //是否显示Ai
      toolbarList: [{
        name: '搜索',
        type: 'search',
        default: 'icon_01',
        select: 'icon_up_01',
        total: 'none',
        icon: ''
      }, {
        name: '授课',
        type: 'teachingResource',
        default: 'icon_02',
        select: 'icon_up_02',
        total: 'teachingResourceTotal',
        icon: ''
      }, {
        name: '图片',
        type: 'imageList',
        default: 'icon_03',
        select: 'icon_up_03',
        total: 'imageTotal',
        icon: ''
      }, {
        name: '视频',
        type: 'videoList',
        default: 'icon_04',
        select: 'icon_up_04',
        total: 'videoTotal',
        icon: ''
      }, {
        name: '音频',
        type: 'audioList',
        default: 'icon_05',
        select: 'icon_up_05',
        total: 'audioTotal',
        icon: 'icon_01'
      }, {
        name: '公式',
        type: 'formulaList',
        default: 'icon_06',
        select: 'icon_up_06',
        total: 'formulaTotal',
        icon: 'icon_02'
      }, {
        name: '互动',
        type: 'interactionList',
        default: 'icon_07',
        select: 'icon_up_07',
        total: 'interactionTotal',
        icon: 'icon_03'
      }, {
        name: '试题',
        type: 'questionList',
        default: 'icon_08',
        select: 'icon_up_08',
        total: 'questionTotal',
        icon: 'icon_04'
      }, {
        name: '文件',
        type: 'resourceList',
        default: 'icon_09',
        select: 'icon_up_09',
        total: 'resourceTotal',
        icon: 'icon_05'
      }],
      isAddMark: e.isAddMark,
      //是否可以添加标记
      isNoteHighlight: e.isAddMark,
      //是否有笔记高亮功能
      tippy: {
        copy: true,
        //复制
        copyIcon: 'copy',
        copyLine: false,
        note: true,
        //笔记
        noteIcon: 'note',
        noteLine: e.pageType == 'pc' ? true : false,
        resources: e.userType == 'teacher' && e.pageType == 'pc',
        //资源
        resourcesIcon: 'addTeacherResources',
        resourcesLine: e.pageType == 'pc' ? true : false,
        highlight: true,
        //高亮
        highlightIcon: 'highlight',
        highlightLine: e.pageType == 'pc' ? true : false,
        link: false,
        //划线
        linkIcon: '',
        linkLine: false,
        dictionary: true,
        //词典
        dictionaryIcon: 'dictionary',
        dictionaryLine: e.pageType == 'pc' ? true : false,
        encyclopedias: true,
        //百科
        encyclopediasIcon: 'encyclopedias',
        encyclopediasLine: e.pageType == 'pc' ? true : false,
        maxWordCount: true,
        //最大选择字数
        wordIcon: '',
        wordLine: true,
        bookmark: true,
        //书签
        bookmarkIcon: 'bookmark',
        bookmarkLine: true
      },
      noteStyle: 'mk-underline__dashed',
      highlight: 'mk-hightlight__default',
      linkStyle: '',
      resourcesStyle: 'mk-underline__default',
      isShowToolbar: true //移动端工具栏默认是否显示
    }, {
      systemName: '国家开放大学',
      system: 'gk',
      //系统缩写
      isGuoKai: true,
      //是否是国家开放大学
      className: 'xml-reader-preview-guokai',
      systemId: 'd58a0f05-b562-46fc-9516-55be91e21655',
      theFirstLayout: true,
      //第一种布局
      toolbarSecond: true,
      //第二种pc端工具栏
      isShowAi: false,
      //是否显示Ai
      toolbarList: [{
        name: '搜索',
        type: 'search',
        default: 'icon_01',
        select: 'icon_up_01',
        total: 'none',
        icon: '',
        style: {
          '--xml-reader-toolbar-color': '#93ccf9',
          '--xml-reader-toolbar-border-color': '#7abeea'
        }
      }, {
        name: '授课',
        type: 'teachingResource',
        default: 'icon_02',
        select: 'icon_up_02',
        total: 'teachingResourceTotal',
        icon: '',
        style: {
          '--xml-reader-toolbar-color': '#f8d9a6',
          '--xml-reader-toolbar-border-color': '#e6b57b'
        }
      }, {
        name: '图片',
        type: 'imageList',
        default: 'icon_03',
        select: 'icon_up_03',
        total: 'imageTotal',
        icon: '',
        style: {
          '--xml-reader-toolbar-color': '#f8d9a6',
          '--xml-reader-toolbar-border-color': '#e6b57b'
        }
      }, {
        name: '视频',
        type: 'videoList',
        default: 'icon_04',
        select: 'icon_up_04',
        total: 'videoTotal',
        icon: '',
        style: {
          '--xml-reader-toolbar-color': '#f6ada3',
          '--xml-reader-toolbar-border-color': '#e37f73'
        }
      }, {
        name: '音频',
        type: 'audioList',
        default: 'icon_05',
        select: 'icon_up_05',
        total: 'audioTotal',
        icon: 'icon_01',
        style: {
          '--xml-reader-toolbar-color': '#a684f5',
          '--xml-reader-toolbar-border-color': '#7852cb'
        }
      }, {
        name: '试题',
        type: 'questionList',
        default: 'icon_06',
        select: 'icon_up_06',
        total: 'questionTotal',
        icon: 'icon_04',
        style: {
          '--xml-reader-toolbar-color': '#8fe7b8',
          '--xml-reader-toolbar-border-color': '#6dd0a0'
        }
      }, {
        name: '试卷',
        type: 'testPaperList',
        default: 'icon_08',
        select: 'icon_up_08',
        total: 'testPaperTotal',
        icon: 'icon_04',
        style: {
          '--xml-reader-toolbar-color': '#bb8ef9',
          '--xml-reader-toolbar-border-color': '#8d52cb'
        }
      }, {
        name: '文件',
        type: 'resourceList',
        default: 'icon_09',
        select: 'icon_up_09',
        total: 'resourceTotal',
        icon: 'icon_05',
        style: {
          '--xml-reader-toolbar-color': '#84aaf4',
          '--xml-reader-toolbar-border-color': '#5c7dcf'
        }
      }],
      isAddMark: e.userType == 'student',
      //是否可以添加标记
      isNoteHighlight: e.userType == 'student',
      //是否有笔记高亮功能
      tippy: {
        copy: false,
        //复制
        copyIcon: '',
        copyLine: false,
        note: e.userType == 'student',
        //笔记
        noteIcon: '',
        noteLine: false,
        resources: false,
        //资源
        resourcesIcon: '',
        resourcesLine: false,
        highlight: e.userType == 'student',
        //高亮
        highlightIcon: '',
        highlightLine: true,
        link: e.userType == 'student',
        //划线
        linkIcon: '',
        linkLine: true,
        dictionary: false,
        //词典
        dictionaryIcon: '',
        dictionaryLine: false,
        encyclopedias: false,
        //百科
        encyclopediasIcon: '',
        encyclopediasLine: false,
        maxWordCount: false,
        //最大选择字数
        wordIcon: '',
        wordLine: false,
        bookmark: e.userType == 'student',
        //书签
        bookmarkIcon: 'bookmark',
        bookmarkLine: true
      },
      noteStyle: 'mk-underline__dashed',
      highlight: 'mk-hightlight__default',
      linkStyle: 'mk-underline__default',
      resourcesStyle: '',
      isShowToolbar: true //移动端工具栏默认是否显示
    }];
    this.data = systemEnvList.find(arr => arr.systemId === key);
  }
}

//
var script$2E = {
  name: 'setPop',
  props: {
    settingData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      visible: false,
      selectedColor: null,
      isBlackBackground: false,
      ruleForm: {
        font: '宋体',
        //。。默认字体
        fontSize: 30,
        // 默认字号
        backgroundColor: '#f87db7',
        // 默认背景颜色
        lineHeight: '20',
        // 默认行间距
        WordOption: '20'
      },
      form: {
        themeFontFamily: '',
        //字体
        themeFontSize: 2,
        //字号
        themeBgColor: '',
        //当前颜色
        themeLineHeight: undefined,
        //行间距
        themeLetterSpacing: undefined //字间距
      },
      fontSizeLabelMap,
      RowOptions: [{
        value: '10',
        label: '10'
      }, {
        value: '20',
        label: '20'
      }, {
        value: '30',
        label: '30'
      }],
      WordOptions: [{
        value: '10',
        label: '10'
      }, {
        value: '20',
        label: '20'
      }, {
        value: '30',
        label: '30'
      }],
      colorOptions: [{
        label: '蓝色',
        value: '#efefef'
      }, {
        label: '暗绿色',
        value: '#ddd8c5'
      }, {
        label: '黄色',
        value: '#f2dcc4'
      }, {
        label: '绿色',
        value: '#e6f7e5'
      }, {
        label: '粉色',
        value: '#fbdee2'
      }
      // { label: '黑色', value: '#000000' }
      ],
      WordOptions: [{
        value: '10',
        label: '10'
      }, {
        value: '20',
        label: '20'
      }, {
        value: '30',
        label: '30'
      }]
    };
  },
  watch: {
    settingData: {
      handler(newValue) {
        this.form = {
          ...this.form,
          ...newValue
        };
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // handleColorClick(colorValue) {
    //   this.ruleForm.backgroundColor = colorValue
    //   this.selectedColor = colorValue
    //   this.isBlackBackground = colorValue.toLowerCase() === '#000000'
    // },
    // 设置字体
    setThemeFontFamily(value) {
      this.form.themeFontFamily = value;
      this.$emit('settingChange', 'themeFontFamily', value);
    },
    // 设置字号
    setThemeFontSize(type) {
      let emit = false;
      switch (type) {
        case 'plus':
          if (this.form.themeFontSize < 3) {
            this.form.themeFontSize++;
            emit = true;
          }
          break;
        case 'minus':
          if (this.form.themeFontSize > 1) {
            this.form.themeFontSize--;
            emit = true;
          }
          break;
      }
      emit && this.$emit('settingChange', 'themeFontSize', this.form.themeFontSize);
    },
    readingMode(type) {
      this.$emit('settingChange', 'readingMode', type);
    },
    // 设置背景色
    setThemeBgColor(color) {
      if (color === this.form.themeBgColor) {
        color = '';
      }
      this.form.themeBgColor = color;
      this.selectedColor = color;
      this.isBlackBackground = color.toLowerCase() === '#000000';
      this.$emit('settingChange', 'themeBgColor', color);
    },
    setThemeLineHeight(value) {
      this.form.themeLineHeight = value;
      this.$emit('settingChange', 'themeLineHeight', value);
    },
    setThemeLetterSpacing(value) {
      this.form.themeLetterSpacing = value;
      this.$emit('settingChange', 'themeLetterSpacing', value);
    }
  }
};

/* script */
const __vue_script__$2E = script$2E;
/* template */
var __vue_render__$2E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      staticClass: "header",
      attrs: {
        "append-to-body": "",
        title: "文本设置",
        width: "500px",
        visible: _vm.visible,
      },
      on: {
        "update:visible": function ($event) {
          _vm.visible = $event;
        },
      },
    },
    [
      _c(
        "div",
        {
          staticClass: "xml-set-pop-pc",
          staticStyle: { width: "500px", margin: "0 auto", height: "300px" },
        },
        [
          _c(
            "el-form",
            { staticClass: "pop" },
            [
              _c(
                "el-form-item",
                { attrs: { label: "字体", prop: "font" } },
                [
                  _c(
                    "el-select",
                    {
                      staticClass: "select-font",
                      on: { change: _vm.setThemeFontFamily },
                      model: {
                        value: _vm.form.themeFontFamily,
                        callback: function ($$v) {
                          _vm.$set(_vm.form, "themeFontFamily", $$v);
                        },
                        expression: "form.themeFontFamily",
                      },
                    },
                    [
                      _c("el-option", { attrs: { label: "默认", value: "" } }),
                      _vm._v(" "),
                      _c("el-option", {
                        attrs: { label: "宋体", value: "宋体, SimSun" },
                      }),
                      _vm._v(" "),
                      _c("el-option", {
                        attrs: {
                          label: "楷体",
                          value: "楷体, 楷体_GB2312, SimKai",
                        },
                      }),
                      _vm._v(" "),
                      _c("el-option", {
                        attrs: { label: "黑体", value: "黑体, SimHei" },
                      }),
                    ],
                    1
                  ),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("字号")]),
                _vm._v(" "),
                _c("div", { staticStyle: { "margin-left": "14px" } }, [
                  _c(
                    "div",
                    {
                      staticClass: "size-btn",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.setThemeFontSize("minus")
                        },
                      },
                    },
                    [_vm._v("A-")]
                  ),
                  _vm._v(" "),
                  _c("span", [
                    _vm._v(
                      _vm._s(_vm.fontSizeLabelMap[_vm.form.themeFontSize])
                    ),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "size-btn",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.setThemeFontSize("plus")
                        },
                      },
                    },
                    [_vm._v("A+")]
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "set-item spacing-box" },
                [
                  _c("span", { staticClass: "keyname" }, [_vm._v("行间距")]),
                  _vm._v(" "),
                  _c("el-input-number", {
                    attrs: { "controls-position": "right", min: 30, max: 100 },
                    on: { change: _vm.setThemeLineHeight },
                    model: {
                      value: _vm.form.themeLineHeight,
                      callback: function ($$v) {
                        _vm.$set(_vm.form, "themeLineHeight", $$v);
                      },
                      expression: "form.themeLineHeight",
                    },
                  }),
                  _vm._v(" "),
                  _c("span", { staticClass: "keyname m-l23" }, [
                    _vm._v("字间距"),
                  ]),
                  _vm._v(" "),
                  _c("el-input-number", {
                    attrs: { "controls-position": "right", min: 0, max: 30 },
                    on: { change: _vm.setThemeLetterSpacing },
                    model: {
                      value: _vm.form.themeLetterSpacing,
                      callback: function ($$v) {
                        _vm.$set(_vm.form, "themeLetterSpacing", $$v);
                      },
                      expression: "form.themeLetterSpacing",
                    },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("阅读模式")]),
                _vm._v(" "),
                _c("div", { staticStyle: { "margin-left": "14px" } }, [
                  _c(
                    "div",
                    {
                      staticClass: "reading-mode",
                      class:
                        _vm.settingData.readingMode == "roll"
                          ? "current-reading-mode"
                          : "",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.readingMode("roll")
                        },
                      },
                    },
                    [_vm._v("滑动")]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "reading-mode",
                      class:
                        _vm.settingData.readingMode == "page"
                          ? "current-reading-mode"
                          : "",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.readingMode("page")
                        },
                      },
                    },
                    [_vm._v("翻页")]
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("el-form-item", { attrs: { label: "背景色" } }, [
                _c(
                  "div",
                  { staticClass: "color-container" },
                  _vm._l(_vm.colorOptions, function (color, index) {
                    return _c(
                      "div",
                      {
                        key: index,
                        staticClass: "color-item",
                        class: {
                          "active-color": _vm.form.themeBgColor === color.value,
                        },
                        style: { backgroundColor: color.value },
                        on: {
                          click: function ($event) {
                            return _vm.setThemeBgColor(color.value)
                          },
                        },
                      },
                      [
                        _vm.form.themeBgColor === color.value
                          ? _c(
                              "div",
                              {
                                staticClass: "check-mark",
                                class: { "white-check": _vm.isBlackBackground },
                              },
                              [
                                _vm._v(
                                  "\n              " +
                                    _vm._s("✔") +
                                    "\n            "
                                ),
                              ]
                            )
                          : _vm._e(),
                      ]
                    )
                  }),
                  0
                ),
              ]),
            ],
            1
          ),
        ],
        1
      ),
    ]
  )
};
var __vue_staticRenderFns__$2E = [];
__vue_render__$2E._withStripped = true;

  /* style */
  const __vue_inject_styles__$2E = undefined;
  /* scoped */
  const __vue_scope_id__$2E = undefined;
  /* module identifier */
  const __vue_module_identifier__$2E = undefined;
  /* functional template */
  const __vue_is_functional_template__$2E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2E, staticRenderFns: __vue_staticRenderFns__$2E },
    __vue_inject_styles__$2E,
    __vue_script__$2E,
    __vue_scope_id__$2E,
    __vue_is_functional_template__$2E,
    __vue_module_identifier__$2E,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2D = {
  name: 'highlight',
  props: {
    sysEnv: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      visiblea: false
    };
  }
};

var __$_require_static_images_icons_books_green_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAD0AAAACCAYAAADrTtSRAAAAAXNSR0IArs4c6QAAAGJJREFUKFNjVN5WksPE+F+XAQEe///PyAPnMv7/cMeztwNJHsxU2lqoy8zElDPU9P37z3iZUWFrqQQr0z8/mOP//2P8wMDIoIjwzL9Pd7z6pmN4elOeHDMrq8dQ0/fv379HAIOFVE1DkUsjAAAAAElFTkSuQmCC";

var __$_require_static_images_icons_books_yellow_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAE8AAAAPCAYAAAClbJLqAAAAAXNSR0IArs4c6QAAAExJREFUWEft1LENACAMBDHYf+ggMUKudfprrFfuzJnjVgIX3srtR/D2dvCCHTx4RSC0fh68IBBSy4MXBEJqefCCQEgtD14QCKnlBbwHYtAs4xEgiVMAAAAASUVORK5CYII=";

/* script */
const __vue_script__$2D = script$2D;
var __vue_render__$2D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      staticClass: "header",
      attrs: {
        "append-to-body": "",
        title: "图例",
        width: "240px",
        height: "192px",
        visible: _vm.visiblea,
      },
      on: {
        "update:visible": function ($event) {
          _vm.visiblea = $event;
        },
      },
    },
    [
      _c("div", { staticClass: "legend" }, [
        _c("span", [_vm._v("笔记：")]),
        _vm._v(" "),
        _c("img", {
          attrs: {
            src: __$_require_static_images_icons_books_green_png__,
            alt: "",
          },
        }),
      ]),
      _vm._v(" "),
      _c("div", { staticClass: "legend" }, [
        _c("span", [_vm._v("高亮：")]),
        _vm._v(" "),
        _c("img", {
          attrs: {
            src: __$_require_static_images_icons_books_yellow_png__,
            alt: "",
          },
        }),
      ]),
      _vm._v(" "),
      _c("div", { staticClass: "legend" }, [
        _c("span", [
          _vm._v(_vm._s(_vm.sysEnv.isGuoKai ? "划线" : "授课资源") + "："),
        ]),
        _vm._v(" "),
        _c("p", {
          staticStyle: {
            width: "61px",
            height: "1px",
            border: "1px solid #f17f53",
          },
        }),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$2D = [];
__vue_render__$2D._withStripped = true;

  /* style */
  const __vue_inject_styles__$2D = undefined;
  /* scoped */
  const __vue_scope_id__$2D = undefined;
  /* module identifier */
  const __vue_module_identifier__$2D = undefined;
  /* functional template */
  const __vue_is_functional_template__$2D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2D, staticRenderFns: __vue_staticRenderFns__$2D },
    __vue_inject_styles__$2D,
    __vue_script__$2D,
    __vue_scope_id__$2D,
    __vue_is_functional_template__$2D,
    __vue_module_identifier__$2D,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//

var script$2C = {
  name: 'bookmark',
  data() {
    return {};
  },
  props: {
    visibleb: false
  },
  mounted() {}
};

/* script */
const __vue_script__$2C = script$2C;

/* template */
var __vue_render__$2C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      staticClass: "header",
      attrs: {
        "append-to-body": "",
        title: "书签设置",
        width: "500px",
        visible: _vm.visibleb,
      },
      on: {
        "update:visible": function ($event) {
          _vm.visibleb = $event;
        },
      },
    },
    [_vm._v(" ??? ")]
  )
};
var __vue_staticRenderFns__$2C = [];
__vue_render__$2C._withStripped = true;

  /* style */
  const __vue_inject_styles__$2C = undefined;
  /* scoped */
  const __vue_scope_id__$2C = undefined;
  /* module identifier */
  const __vue_module_identifier__$2C = undefined;
  /* functional template */
  const __vue_is_functional_template__$2C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2C, staticRenderFns: __vue_staticRenderFns__$2C },
    __vue_inject_styles__$2C,
    __vue_script__$2C,
    __vue_scope_id__$2C,
    __vue_is_functional_template__$2C,
    __vue_module_identifier__$2C,
    false,
    undefined,
    undefined,
    undefined
  );

/*!
 * html2canvas 1.4.1 <https://html2canvas.hertzen.com>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function (d, b) {
  extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
  };
  return extendStatics(d, b);
};
function __extends(d, b) {
  if (typeof b !== "function" && b !== null) throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
  extendStatics(d, b);
  function __() {
    this.constructor = d;
  }
  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}
function __generator(thisArg, body) {
  var _ = {
      label: 0,
      sent: function () {
        if (t[0] & 1) throw t[1];
        return t[1];
      },
      trys: [],
      ops: []
    },
    f,
    y,
    t,
    g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;
  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }
  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");
    while (_) try {
      if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
      if (y = 0, t) op = [op[0] & 2, t.value];
      switch (op[0]) {
        case 0:
        case 1:
          t = op;
          break;
        case 4:
          _.label++;
          return {
            value: op[1],
            done: false
          };
        case 5:
          _.label++;
          y = op[1];
          op = [0];
          continue;
        case 7:
          op = _.ops.pop();
          _.trys.pop();
          continue;
        default:
          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
            _ = 0;
            continue;
          }
          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
            _.label = op[1];
            break;
          }
          if (op[0] === 6 && _.label < t[1]) {
            _.label = t[1];
            t = op;
            break;
          }
          if (t && _.label < t[2]) {
            _.label = t[2];
            _.ops.push(op);
            break;
          }
          if (t[2]) _.ops.pop();
          _.trys.pop();
          continue;
      }
      op = body.call(thisArg, _);
    } catch (e) {
      op = [6, e];
      y = 0;
    } finally {
      f = t = 0;
    }
    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || from);
}
var Bounds = /** @class */function () {
  function Bounds(left, top, width, height) {
    this.left = left;
    this.top = top;
    this.width = width;
    this.height = height;
  }
  Bounds.prototype.add = function (x, y, w, h) {
    return new Bounds(this.left + x, this.top + y, this.width + w, this.height + h);
  };
  Bounds.fromClientRect = function (context, clientRect) {
    return new Bounds(clientRect.left + context.windowBounds.left, clientRect.top + context.windowBounds.top, clientRect.width, clientRect.height);
  };
  Bounds.fromDOMRectList = function (context, domRectList) {
    var domRect = Array.from(domRectList).find(function (rect) {
      return rect.width !== 0;
    });
    return domRect ? new Bounds(domRect.left + context.windowBounds.left, domRect.top + context.windowBounds.top, domRect.width, domRect.height) : Bounds.EMPTY;
  };
  Bounds.EMPTY = new Bounds(0, 0, 0, 0);
  return Bounds;
}();
var parseBounds = function (context, node) {
  return Bounds.fromClientRect(context, node.getBoundingClientRect());
};
var parseDocumentSize = function (document) {
  var body = document.body;
  var documentElement = document.documentElement;
  if (!body || !documentElement) {
    throw new Error("Unable to get document size");
  }
  var width = Math.max(Math.max(body.scrollWidth, documentElement.scrollWidth), Math.max(body.offsetWidth, documentElement.offsetWidth), Math.max(body.clientWidth, documentElement.clientWidth));
  var height = Math.max(Math.max(body.scrollHeight, documentElement.scrollHeight), Math.max(body.offsetHeight, documentElement.offsetHeight), Math.max(body.clientHeight, documentElement.clientHeight));
  return new Bounds(0, 0, width, height);
};

/*
 * css-line-break 2.1.0 <https://github.com/niklasvh/css-line-break#readme>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var toCodePoints$1 = function (str) {
  var codePoints = [];
  var i = 0;
  var length = str.length;
  while (i < length) {
    var value = str.charCodeAt(i++);
    if (value >= 0xd800 && value <= 0xdbff && i < length) {
      var extra = str.charCodeAt(i++);
      if ((extra & 0xfc00) === 0xdc00) {
        codePoints.push(((value & 0x3ff) << 10) + (extra & 0x3ff) + 0x10000);
      } else {
        codePoints.push(value);
        i--;
      }
    } else {
      codePoints.push(value);
    }
  }
  return codePoints;
};
var fromCodePoint$1 = function () {
  var codePoints = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    codePoints[_i] = arguments[_i];
  }
  if (String.fromCodePoint) {
    return String.fromCodePoint.apply(String, codePoints);
  }
  var length = codePoints.length;
  if (!length) {
    return '';
  }
  var codeUnits = [];
  var index = -1;
  var result = '';
  while (++index < length) {
    var codePoint = codePoints[index];
    if (codePoint <= 0xffff) {
      codeUnits.push(codePoint);
    } else {
      codePoint -= 0x10000;
      codeUnits.push((codePoint >> 10) + 0xd800, codePoint % 0x400 + 0xdc00);
    }
    if (index + 1 === length || codeUnits.length > 0x4000) {
      result += String.fromCharCode.apply(String, codeUnits);
      codeUnits.length = 0;
    }
  }
  return result;
};
var chars$2 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
var lookup$2 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (var i$2 = 0; i$2 < chars$2.length; i$2++) {
  lookup$2[chars$2.charCodeAt(i$2)] = i$2;
}

/*
 * utrie 1.0.2 <https://github.com/niklasvh/utrie>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var chars$1$1 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
var lookup$1$1 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (var i$1$1 = 0; i$1$1 < chars$1$1.length; i$1$1++) {
  lookup$1$1[chars$1$1.charCodeAt(i$1$1)] = i$1$1;
}
var decode$1 = function (base64) {
  var bufferLength = base64.length * 0.75,
    len = base64.length,
    i,
    p = 0,
    encoded1,
    encoded2,
    encoded3,
    encoded4;
  if (base64[base64.length - 1] === '=') {
    bufferLength--;
    if (base64[base64.length - 2] === '=') {
      bufferLength--;
    }
  }
  var buffer = typeof ArrayBuffer !== 'undefined' && typeof Uint8Array !== 'undefined' && typeof Uint8Array.prototype.slice !== 'undefined' ? new ArrayBuffer(bufferLength) : new Array(bufferLength);
  var bytes = Array.isArray(buffer) ? buffer : new Uint8Array(buffer);
  for (i = 0; i < len; i += 4) {
    encoded1 = lookup$1$1[base64.charCodeAt(i)];
    encoded2 = lookup$1$1[base64.charCodeAt(i + 1)];
    encoded3 = lookup$1$1[base64.charCodeAt(i + 2)];
    encoded4 = lookup$1$1[base64.charCodeAt(i + 3)];
    bytes[p++] = encoded1 << 2 | encoded2 >> 4;
    bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
    bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
  }
  return buffer;
};
var polyUint16Array$1 = function (buffer) {
  var length = buffer.length;
  var bytes = [];
  for (var i = 0; i < length; i += 2) {
    bytes.push(buffer[i + 1] << 8 | buffer[i]);
  }
  return bytes;
};
var polyUint32Array$1 = function (buffer) {
  var length = buffer.length;
  var bytes = [];
  for (var i = 0; i < length; i += 4) {
    bytes.push(buffer[i + 3] << 24 | buffer[i + 2] << 16 | buffer[i + 1] << 8 | buffer[i]);
  }
  return bytes;
};

/** Shift size for getting the index-2 table offset. */
var UTRIE2_SHIFT_2$1 = 5;
/** Shift size for getting the index-1 table offset. */
var UTRIE2_SHIFT_1$1 = 6 + 5;
/**
 * Shift size for shifting left the index array values.
 * Increases possible data size with 16-bit index values at the cost
 * of compactability.
 * This requires data blocks to be aligned by UTRIE2_DATA_GRANULARITY.
 */
var UTRIE2_INDEX_SHIFT$1 = 2;
/**
 * Difference between the two shift sizes,
 * for getting an index-1 offset from an index-2 offset. 6=11-5
 */
var UTRIE2_SHIFT_1_2$1 = UTRIE2_SHIFT_1$1 - UTRIE2_SHIFT_2$1;
/**
 * The part of the index-2 table for U+D800..U+DBFF stores values for
 * lead surrogate code _units_ not code _points_.
 * Values for lead surrogate code _points_ are indexed with this portion of the table.
 * Length=32=0x20=0x400>>UTRIE2_SHIFT_2. (There are 1024=0x400 lead surrogates.)
 */
var UTRIE2_LSCP_INDEX_2_OFFSET$1 = 0x10000 >> UTRIE2_SHIFT_2$1;
/** Number of entries in a data block. 32=0x20 */
var UTRIE2_DATA_BLOCK_LENGTH$1 = 1 << UTRIE2_SHIFT_2$1;
/** Mask for getting the lower bits for the in-data-block offset. */
var UTRIE2_DATA_MASK$1 = UTRIE2_DATA_BLOCK_LENGTH$1 - 1;
var UTRIE2_LSCP_INDEX_2_LENGTH$1 = 0x400 >> UTRIE2_SHIFT_2$1;
/** Count the lengths of both BMP pieces. 2080=0x820 */
var UTRIE2_INDEX_2_BMP_LENGTH$1 = UTRIE2_LSCP_INDEX_2_OFFSET$1 + UTRIE2_LSCP_INDEX_2_LENGTH$1;
/**
 * The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
 * Length 32=0x20 for lead bytes C0..DF, regardless of UTRIE2_SHIFT_2.
 */
var UTRIE2_UTF8_2B_INDEX_2_OFFSET$1 = UTRIE2_INDEX_2_BMP_LENGTH$1;
var UTRIE2_UTF8_2B_INDEX_2_LENGTH$1 = 0x800 >> 6; /* U+0800 is the first code point after 2-byte UTF-8 */
/**
 * The index-1 table, only used for supplementary code points, at offset 2112=0x840.
 * Variable length, for code points up to highStart, where the last single-value range starts.
 * Maximum length 512=0x200=0x100000>>UTRIE2_SHIFT_1.
 * (For 0x100000 supplementary code points U+10000..U+10ffff.)
 *
 * The part of the index-2 table for supplementary code points starts
 * after this index-1 table.
 *
 * Both the index-1 table and the following part of the index-2 table
 * are omitted completely if there is only BMP data.
 */
var UTRIE2_INDEX_1_OFFSET$1 = UTRIE2_UTF8_2B_INDEX_2_OFFSET$1 + UTRIE2_UTF8_2B_INDEX_2_LENGTH$1;
/**
 * Number of index-1 entries for the BMP. 32=0x20
 * This part of the index-1 table is omitted from the serialized form.
 */
var UTRIE2_OMITTED_BMP_INDEX_1_LENGTH$1 = 0x10000 >> UTRIE2_SHIFT_1$1;
/** Number of entries in an index-2 block. 64=0x40 */
var UTRIE2_INDEX_2_BLOCK_LENGTH$1 = 1 << UTRIE2_SHIFT_1_2$1;
/** Mask for getting the lower bits for the in-index-2-block offset. */
var UTRIE2_INDEX_2_MASK$1 = UTRIE2_INDEX_2_BLOCK_LENGTH$1 - 1;
var slice16$1 = function (view, start, end) {
  if (view.slice) {
    return view.slice(start, end);
  }
  return new Uint16Array(Array.prototype.slice.call(view, start, end));
};
var slice32$1 = function (view, start, end) {
  if (view.slice) {
    return view.slice(start, end);
  }
  return new Uint32Array(Array.prototype.slice.call(view, start, end));
};
var createTrieFromBase64$1 = function (base64, _byteLength) {
  var buffer = decode$1(base64);
  var view32 = Array.isArray(buffer) ? polyUint32Array$1(buffer) : new Uint32Array(buffer);
  var view16 = Array.isArray(buffer) ? polyUint16Array$1(buffer) : new Uint16Array(buffer);
  var headerLength = 24;
  var index = slice16$1(view16, headerLength / 2, view32[4] / 2);
  var data = view32[5] === 2 ? slice16$1(view16, (headerLength + view32[4]) / 2) : slice32$1(view32, Math.ceil((headerLength + view32[4]) / 4));
  return new Trie$1(view32[0], view32[1], view32[2], view32[3], index, data);
};
var Trie$1 = /** @class */function () {
  function Trie(initialValue, errorValue, highStart, highValueIndex, index, data) {
    this.initialValue = initialValue;
    this.errorValue = errorValue;
    this.highStart = highStart;
    this.highValueIndex = highValueIndex;
    this.index = index;
    this.data = data;
  }
  /**
   * Get the value for a code point as stored in the Trie.
   *
   * @param codePoint the code point
   * @return the value
   */
  Trie.prototype.get = function (codePoint) {
    var ix;
    if (codePoint >= 0) {
      if (codePoint < 0x0d800 || codePoint > 0x0dbff && codePoint <= 0x0ffff) {
        // Ordinary BMP code point, excluding leading surrogates.
        // BMP uses a single level lookup.  BMP index starts at offset 0 in the Trie2 index.
        // 16 bit data is stored in the index array itself.
        ix = this.index[codePoint >> UTRIE2_SHIFT_2$1];
        ix = (ix << UTRIE2_INDEX_SHIFT$1) + (codePoint & UTRIE2_DATA_MASK$1);
        return this.data[ix];
      }
      if (codePoint <= 0xffff) {
        // Lead Surrogate Code Point.  A Separate index section is stored for
        // lead surrogate code units and code points.
        //   The main index has the code unit data.
        //   For this function, we need the code point data.
        // Note: this expression could be refactored for slightly improved efficiency, but
        //       surrogate code points will be so rare in practice that it's not worth it.
        ix = this.index[UTRIE2_LSCP_INDEX_2_OFFSET$1 + (codePoint - 0xd800 >> UTRIE2_SHIFT_2$1)];
        ix = (ix << UTRIE2_INDEX_SHIFT$1) + (codePoint & UTRIE2_DATA_MASK$1);
        return this.data[ix];
      }
      if (codePoint < this.highStart) {
        // Supplemental code point, use two-level lookup.
        ix = UTRIE2_INDEX_1_OFFSET$1 - UTRIE2_OMITTED_BMP_INDEX_1_LENGTH$1 + (codePoint >> UTRIE2_SHIFT_1$1);
        ix = this.index[ix];
        ix += codePoint >> UTRIE2_SHIFT_2$1 & UTRIE2_INDEX_2_MASK$1;
        ix = this.index[ix];
        ix = (ix << UTRIE2_INDEX_SHIFT$1) + (codePoint & UTRIE2_DATA_MASK$1);
        return this.data[ix];
      }
      if (codePoint <= 0x10ffff) {
        return this.data[this.highValueIndex];
      }
    }
    // Fall through.  The code point is outside of the legal range of 0..0x10ffff.
    return this.errorValue;
  };
  return Trie;
}();

/*
 * base64-arraybuffer 1.0.2 <https://github.com/niklasvh/base64-arraybuffer>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var chars$3 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
var lookup$3 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (var i$3 = 0; i$3 < chars$3.length; i$3++) {
  lookup$3[chars$3.charCodeAt(i$3)] = i$3;
}
var base64$1 = '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';
var LETTER_NUMBER_MODIFIER = 50;
// Non-tailorable Line Breaking Classes
var BK = 1; //  Cause a line break (after)
var CR$1 = 2; //  Cause a line break (after), except between CR and LF
var LF$1 = 3; //  Cause a line break (after)
var CM = 4; //  Prohibit a line break between the character and the preceding character
var NL = 5; //  Cause a line break (after)
var WJ = 7; //  Prohibit line breaks before and after
var ZW = 8; //  Provide a break opportunity
var GL = 9; //  Prohibit line breaks before and after
var SP = 10; // Enable indirect line breaks
var ZWJ$1 = 11; // Prohibit line breaks within joiner sequences
// Break Opportunities
var B2 = 12; //  Provide a line break opportunity before and after the character
var BA = 13; //  Generally provide a line break opportunity after the character
var BB = 14; //  Generally provide a line break opportunity before the character
var HY = 15; //  Provide a line break opportunity after the character, except in numeric context
var CB = 16; //   Provide a line break opportunity contingent on additional information
// Characters Prohibiting Certain Breaks
var CL = 17; //  Prohibit line breaks before
var CP = 18; //  Prohibit line breaks before
var EX = 19; //  Prohibit line breaks before
var IN = 20; //  Allow only indirect line breaks between pairs
var NS = 21; //  Allow only indirect line breaks before
var OP = 22; //  Prohibit line breaks after
var QU = 23; //  Act like they are both opening and closing
// Numeric Context
var IS = 24; //  Prevent breaks after any and before numeric
var NU = 25; //  Form numeric expressions for line breaking purposes
var PO = 26; //  Do not break following a numeric expression
var PR = 27; //  Do not break in front of a numeric expression
var SY = 28; //  Prevent a break before; and allow a break after
// Other Characters
var AI = 29; //  Act like AL when the resolvedEAW is N; otherwise; act as ID
var AL = 30; //  Are alphabetic characters or symbols that are used with alphabetic characters
var CJ = 31; //  Treat as NS or ID for strict or normal breaking.
var EB = 32; //  Do not break from following Emoji Modifier
var EM = 33; //  Do not break from preceding Emoji Base
var H2 = 34; //  Form Korean syllable blocks
var H3 = 35; //  Form Korean syllable blocks
var HL = 36; //  Do not break around a following hyphen; otherwise act as Alphabetic
var ID = 37; //  Break before or after; except in some numeric context
var JL = 38; //  Form Korean syllable blocks
var JV = 39; //  Form Korean syllable blocks
var JT = 40; //  Form Korean syllable blocks
var RI$1 = 41; //  Keep pairs together. For pairs; break before and after other classes
var SA = 42; //  Provide a line break opportunity contingent on additional, language-specific context analysis
var XX = 43; //  Have as yet unknown line breaking behavior or unassigned code positions
var ea_OP = [0x2329, 0xff08];
var BREAK_MANDATORY = '!';
var BREAK_NOT_ALLOWED$1 = '×';
var BREAK_ALLOWED$1 = '÷';
var UnicodeTrie$1 = createTrieFromBase64$1(base64$1);
var ALPHABETICS = [AL, HL];
var HARD_LINE_BREAKS = [BK, CR$1, LF$1, NL];
var SPACE$1 = [SP, ZW];
var PREFIX_POSTFIX = [PR, PO];
var LINE_BREAKS = HARD_LINE_BREAKS.concat(SPACE$1);
var KOREAN_SYLLABLE_BLOCK = [JL, JV, JT, H2, H3];
var HYPHEN = [HY, BA];
var codePointsToCharacterClasses = function (codePoints, lineBreak) {
  if (lineBreak === void 0) {
    lineBreak = 'strict';
  }
  var types = [];
  var indices = [];
  var categories = [];
  codePoints.forEach(function (codePoint, index) {
    var classType = UnicodeTrie$1.get(codePoint);
    if (classType > LETTER_NUMBER_MODIFIER) {
      categories.push(true);
      classType -= LETTER_NUMBER_MODIFIER;
    } else {
      categories.push(false);
    }
    if (['normal', 'auto', 'loose'].indexOf(lineBreak) !== -1) {
      // U+2010, – U+2013, 〜 U+301C, ゠ U+30A0
      if ([0x2010, 0x2013, 0x301c, 0x30a0].indexOf(codePoint) !== -1) {
        indices.push(index);
        return types.push(CB);
      }
    }
    if (classType === CM || classType === ZWJ$1) {
      // LB10 Treat any remaining combining mark or ZWJ as AL.
      if (index === 0) {
        indices.push(index);
        return types.push(AL);
      }
      // LB9 Do not break a combining character sequence; treat it as if it has the line breaking class of
      // the base character in all of the following rules. Treat ZWJ as if it were CM.
      var prev = types[index - 1];
      if (LINE_BREAKS.indexOf(prev) === -1) {
        indices.push(indices[index - 1]);
        return types.push(prev);
      }
      indices.push(index);
      return types.push(AL);
    }
    indices.push(index);
    if (classType === CJ) {
      return types.push(lineBreak === 'strict' ? NS : ID);
    }
    if (classType === SA) {
      return types.push(AL);
    }
    if (classType === AI) {
      return types.push(AL);
    }
    // For supplementary characters, a useful default is to treat characters in the range 10000..1FFFD as AL
    // and characters in the ranges 20000..2FFFD and 30000..3FFFD as ID, until the implementation can be revised
    // to take into account the actual line breaking properties for these characters.
    if (classType === XX) {
      if (codePoint >= 0x20000 && codePoint <= 0x2fffd || codePoint >= 0x30000 && codePoint <= 0x3fffd) {
        return types.push(ID);
      } else {
        return types.push(AL);
      }
    }
    types.push(classType);
  });
  return [indices, types, categories];
};
var isAdjacentWithSpaceIgnored = function (a, b, currentIndex, classTypes) {
  var current = classTypes[currentIndex];
  if (Array.isArray(a) ? a.indexOf(current) !== -1 : a === current) {
    var i = currentIndex;
    while (i <= classTypes.length) {
      i++;
      var next = classTypes[i];
      if (next === b) {
        return true;
      }
      if (next !== SP) {
        break;
      }
    }
  }
  if (current === SP) {
    var i = currentIndex;
    while (i > 0) {
      i--;
      var prev = classTypes[i];
      if (Array.isArray(a) ? a.indexOf(prev) !== -1 : a === prev) {
        var n = currentIndex;
        while (n <= classTypes.length) {
          n++;
          var next = classTypes[n];
          if (next === b) {
            return true;
          }
          if (next !== SP) {
            break;
          }
        }
      }
      if (prev !== SP) {
        break;
      }
    }
  }
  return false;
};
var previousNonSpaceClassType = function (currentIndex, classTypes) {
  var i = currentIndex;
  while (i >= 0) {
    var type = classTypes[i];
    if (type === SP) {
      i--;
    } else {
      return type;
    }
  }
  return 0;
};
var _lineBreakAtIndex = function (codePoints, classTypes, indicies, index, forbiddenBreaks) {
  if (indicies[index] === 0) {
    return BREAK_NOT_ALLOWED$1;
  }
  var currentIndex = index - 1;
  if (Array.isArray(forbiddenBreaks) && forbiddenBreaks[currentIndex] === true) {
    return BREAK_NOT_ALLOWED$1;
  }
  var beforeIndex = currentIndex - 1;
  var afterIndex = currentIndex + 1;
  var current = classTypes[currentIndex];
  // LB4 Always break after hard line breaks.
  // LB5 Treat CR followed by LF, as well as CR, LF, and NL as hard line breaks.
  var before = beforeIndex >= 0 ? classTypes[beforeIndex] : 0;
  var next = classTypes[afterIndex];
  if (current === CR$1 && next === LF$1) {
    return BREAK_NOT_ALLOWED$1;
  }
  if (HARD_LINE_BREAKS.indexOf(current) !== -1) {
    return BREAK_MANDATORY;
  }
  // LB6 Do not break before hard line breaks.
  if (HARD_LINE_BREAKS.indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB7 Do not break before spaces or zero width space.
  if (SPACE$1.indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB8 Break before any character following a zero-width space, even if one or more spaces intervene.
  if (previousNonSpaceClassType(currentIndex, classTypes) === ZW) {
    return BREAK_ALLOWED$1;
  }
  // LB8a Do not break after a zero width joiner.
  if (UnicodeTrie$1.get(codePoints[currentIndex]) === ZWJ$1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // zwj emojis
  if ((current === EB || current === EM) && UnicodeTrie$1.get(codePoints[afterIndex]) === ZWJ$1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB11 Do not break before or after Word joiner and related characters.
  if (current === WJ || next === WJ) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB12 Do not break after NBSP and related characters.
  if (current === GL) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB12a Do not break before NBSP and related characters, except after spaces and hyphens.
  if ([SP, BA, HY].indexOf(current) === -1 && next === GL) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB13 Do not break before ‘]’ or ‘!’ or ‘;’ or ‘/’, even after spaces.
  if ([CL, CP, EX, IS, SY].indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB14 Do not break after ‘[’, even after spaces.
  if (previousNonSpaceClassType(currentIndex, classTypes) === OP) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB15 Do not break within ‘”[’, even with intervening spaces.
  if (isAdjacentWithSpaceIgnored(QU, OP, currentIndex, classTypes)) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB16 Do not break between closing punctuation and a nonstarter (lb=NS), even with intervening spaces.
  if (isAdjacentWithSpaceIgnored([CL, CP], NS, currentIndex, classTypes)) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB17 Do not break within ‘——’, even with intervening spaces.
  if (isAdjacentWithSpaceIgnored(B2, B2, currentIndex, classTypes)) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB18 Break after spaces.
  if (current === SP) {
    return BREAK_ALLOWED$1;
  }
  // LB19 Do not break before or after quotation marks, such as ‘ ” ’.
  if (current === QU || next === QU) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB20 Break before and after unresolved CB.
  if (next === CB || current === CB) {
    return BREAK_ALLOWED$1;
  }
  // LB21 Do not break before hyphen-minus, other hyphens, fixed-width spaces, small kana, and other non-starters, or after acute accents.
  if ([BA, HY, NS].indexOf(next) !== -1 || current === BB) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB21a Don't break after Hebrew + Hyphen.
  if (before === HL && HYPHEN.indexOf(current) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB21b Don’t break between Solidus and Hebrew letters.
  if (current === SY && next === HL) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB22 Do not break before ellipsis.
  if (next === IN) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB23 Do not break between digits and letters.
  if (ALPHABETICS.indexOf(next) !== -1 && current === NU || ALPHABETICS.indexOf(current) !== -1 && next === NU) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB23a Do not break between numeric prefixes and ideographs, or between ideographs and numeric postfixes.
  if (current === PR && [ID, EB, EM].indexOf(next) !== -1 || [ID, EB, EM].indexOf(current) !== -1 && next === PO) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB24 Do not break between numeric prefix/postfix and letters, or between letters and prefix/postfix.
  if (ALPHABETICS.indexOf(current) !== -1 && PREFIX_POSTFIX.indexOf(next) !== -1 || PREFIX_POSTFIX.indexOf(current) !== -1 && ALPHABETICS.indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB25 Do not break between the following pairs of classes relevant to numbers:
  if (
  // (PR | PO) × ( OP | HY )? NU
  [PR, PO].indexOf(current) !== -1 && (next === NU || [OP, HY].indexOf(next) !== -1 && classTypes[afterIndex + 1] === NU) ||
  // ( OP | HY ) × NU
  [OP, HY].indexOf(current) !== -1 && next === NU ||
  // NU ×	(NU | SY | IS)
  current === NU && [NU, SY, IS].indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // NU (NU | SY | IS)* × (NU | SY | IS | CL | CP)
  if ([NU, SY, IS, CL, CP].indexOf(next) !== -1) {
    var prevIndex = currentIndex;
    while (prevIndex >= 0) {
      var type = classTypes[prevIndex];
      if (type === NU) {
        return BREAK_NOT_ALLOWED$1;
      } else if ([SY, IS].indexOf(type) !== -1) {
        prevIndex--;
      } else {
        break;
      }
    }
  }
  // NU (NU | SY | IS)* (CL | CP)? × (PO | PR))
  if ([PR, PO].indexOf(next) !== -1) {
    var prevIndex = [CL, CP].indexOf(current) !== -1 ? beforeIndex : currentIndex;
    while (prevIndex >= 0) {
      var type = classTypes[prevIndex];
      if (type === NU) {
        return BREAK_NOT_ALLOWED$1;
      } else if ([SY, IS].indexOf(type) !== -1) {
        prevIndex--;
      } else {
        break;
      }
    }
  }
  // LB26 Do not break a Korean syllable.
  if (JL === current && [JL, JV, H2, H3].indexOf(next) !== -1 || [JV, H2].indexOf(current) !== -1 && [JV, JT].indexOf(next) !== -1 || [JT, H3].indexOf(current) !== -1 && next === JT) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB27 Treat a Korean Syllable Block the same as ID.
  if (KOREAN_SYLLABLE_BLOCK.indexOf(current) !== -1 && [IN, PO].indexOf(next) !== -1 || KOREAN_SYLLABLE_BLOCK.indexOf(next) !== -1 && current === PR) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB28 Do not break between alphabetics (“at”).
  if (ALPHABETICS.indexOf(current) !== -1 && ALPHABETICS.indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB29 Do not break between numeric punctuation and alphabetics (“e.g.”).
  if (current === IS && ALPHABETICS.indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB30 Do not break between letters, numbers, or ordinary symbols and opening or closing parentheses.
  if (ALPHABETICS.concat(NU).indexOf(current) !== -1 && next === OP && ea_OP.indexOf(codePoints[afterIndex]) === -1 || ALPHABETICS.concat(NU).indexOf(next) !== -1 && current === CP) {
    return BREAK_NOT_ALLOWED$1;
  }
  // LB30a Break between two regional indicator symbols if and only if there are an even number of regional
  // indicators preceding the position of the break.
  if (current === RI$1 && next === RI$1) {
    var i = indicies[currentIndex];
    var count = 1;
    while (i > 0) {
      i--;
      if (classTypes[i] === RI$1) {
        count++;
      } else {
        break;
      }
    }
    if (count % 2 !== 0) {
      return BREAK_NOT_ALLOWED$1;
    }
  }
  // LB30b Do not break between an emoji base and an emoji modifier.
  if (current === EB && next === EM) {
    return BREAK_NOT_ALLOWED$1;
  }
  return BREAK_ALLOWED$1;
};
var cssFormattedClasses = function (codePoints, options) {
  if (!options) {
    options = {
      lineBreak: 'normal',
      wordBreak: 'normal'
    };
  }
  var _a = codePointsToCharacterClasses(codePoints, options.lineBreak),
    indicies = _a[0],
    classTypes = _a[1],
    isLetterNumber = _a[2];
  if (options.wordBreak === 'break-all' || options.wordBreak === 'break-word') {
    classTypes = classTypes.map(function (type) {
      return [NU, AL, SA].indexOf(type) !== -1 ? ID : type;
    });
  }
  var forbiddenBreakpoints = options.wordBreak === 'keep-all' ? isLetterNumber.map(function (letterNumber, i) {
    return letterNumber && codePoints[i] >= 0x4e00 && codePoints[i] <= 0x9fff;
  }) : undefined;
  return [indicies, classTypes, forbiddenBreakpoints];
};
var Break$1 = /** @class */function () {
  function Break(codePoints, lineBreak, start, end) {
    this.codePoints = codePoints;
    this.required = lineBreak === BREAK_MANDATORY;
    this.start = start;
    this.end = end;
  }
  Break.prototype.slice = function () {
    return fromCodePoint$1.apply(void 0, this.codePoints.slice(this.start, this.end));
  };
  return Break;
}();
var LineBreaker = function (str, options) {
  var codePoints = toCodePoints$1(str);
  var _a = cssFormattedClasses(codePoints, options),
    indicies = _a[0],
    classTypes = _a[1],
    forbiddenBreakpoints = _a[2];
  var length = codePoints.length;
  var lastEnd = 0;
  var nextIndex = 0;
  return {
    next: function () {
      if (nextIndex >= length) {
        return {
          done: true,
          value: null
        };
      }
      var lineBreak = BREAK_NOT_ALLOWED$1;
      while (nextIndex < length && (lineBreak = _lineBreakAtIndex(codePoints, classTypes, indicies, ++nextIndex, forbiddenBreakpoints)) === BREAK_NOT_ALLOWED$1) {}
      if (lineBreak !== BREAK_NOT_ALLOWED$1 || nextIndex === length) {
        var value = new Break$1(codePoints, lineBreak, lastEnd, nextIndex);
        lastEnd = nextIndex;
        return {
          value: value,
          done: false
        };
      }
      return {
        done: true,
        value: null
      };
    }
  };
};

// https://www.w3.org/TR/css-syntax-3
var FLAG_UNRESTRICTED = 1 << 0;
var FLAG_ID = 1 << 1;
var FLAG_INTEGER = 1 << 2;
var FLAG_NUMBER = 1 << 3;
var LINE_FEED = 0x000a;
var SOLIDUS = 0x002f;
var REVERSE_SOLIDUS = 0x005c;
var CHARACTER_TABULATION = 0x0009;
var SPACE = 0x0020;
var QUOTATION_MARK = 0x0022;
var EQUALS_SIGN = 0x003d;
var NUMBER_SIGN = 0x0023;
var DOLLAR_SIGN = 0x0024;
var PERCENTAGE_SIGN = 0x0025;
var APOSTROPHE = 0x0027;
var LEFT_PARENTHESIS = 0x0028;
var RIGHT_PARENTHESIS = 0x0029;
var LOW_LINE = 0x005f;
var HYPHEN_MINUS = 0x002d;
var EXCLAMATION_MARK = 0x0021;
var LESS_THAN_SIGN = 0x003c;
var GREATER_THAN_SIGN = 0x003e;
var COMMERCIAL_AT = 0x0040;
var LEFT_SQUARE_BRACKET = 0x005b;
var RIGHT_SQUARE_BRACKET = 0x005d;
var CIRCUMFLEX_ACCENT = 0x003d;
var LEFT_CURLY_BRACKET = 0x007b;
var QUESTION_MARK = 0x003f;
var RIGHT_CURLY_BRACKET = 0x007d;
var VERTICAL_LINE = 0x007c;
var TILDE = 0x007e;
var CONTROL = 0x0080;
var REPLACEMENT_CHARACTER = 0xfffd;
var ASTERISK = 0x002a;
var PLUS_SIGN = 0x002b;
var COMMA = 0x002c;
var COLON = 0x003a;
var SEMICOLON = 0x003b;
var FULL_STOP = 0x002e;
var NULL = 0x0000;
var BACKSPACE = 0x0008;
var LINE_TABULATION = 0x000b;
var SHIFT_OUT = 0x000e;
var INFORMATION_SEPARATOR_ONE = 0x001f;
var DELETE = 0x007f;
var EOF = -1;
var ZERO = 0x0030;
var a = 0x0061;
var e = 0x0065;
var f = 0x0066;
var u = 0x0075;
var z = 0x007a;
var A = 0x0041;
var E = 0x0045;
var F = 0x0046;
var U = 0x0055;
var Z = 0x005a;
var isDigit = function (codePoint) {
  return codePoint >= ZERO && codePoint <= 0x0039;
};
var isSurrogateCodePoint = function (codePoint) {
  return codePoint >= 0xd800 && codePoint <= 0xdfff;
};
var isHex = function (codePoint) {
  return isDigit(codePoint) || codePoint >= A && codePoint <= F || codePoint >= a && codePoint <= f;
};
var isLowerCaseLetter = function (codePoint) {
  return codePoint >= a && codePoint <= z;
};
var isUpperCaseLetter = function (codePoint) {
  return codePoint >= A && codePoint <= Z;
};
var isLetter = function (codePoint) {
  return isLowerCaseLetter(codePoint) || isUpperCaseLetter(codePoint);
};
var isNonASCIICodePoint = function (codePoint) {
  return codePoint >= CONTROL;
};
var isWhiteSpace = function (codePoint) {
  return codePoint === LINE_FEED || codePoint === CHARACTER_TABULATION || codePoint === SPACE;
};
var isNameStartCodePoint = function (codePoint) {
  return isLetter(codePoint) || isNonASCIICodePoint(codePoint) || codePoint === LOW_LINE;
};
var isNameCodePoint = function (codePoint) {
  return isNameStartCodePoint(codePoint) || isDigit(codePoint) || codePoint === HYPHEN_MINUS;
};
var isNonPrintableCodePoint = function (codePoint) {
  return codePoint >= NULL && codePoint <= BACKSPACE || codePoint === LINE_TABULATION || codePoint >= SHIFT_OUT && codePoint <= INFORMATION_SEPARATOR_ONE || codePoint === DELETE;
};
var isValidEscape = function (c1, c2) {
  if (c1 !== REVERSE_SOLIDUS) {
    return false;
  }
  return c2 !== LINE_FEED;
};
var isIdentifierStart = function (c1, c2, c3) {
  if (c1 === HYPHEN_MINUS) {
    return isNameStartCodePoint(c2) || isValidEscape(c2, c3);
  } else if (isNameStartCodePoint(c1)) {
    return true;
  } else if (c1 === REVERSE_SOLIDUS && isValidEscape(c1, c2)) {
    return true;
  }
  return false;
};
var isNumberStart = function (c1, c2, c3) {
  if (c1 === PLUS_SIGN || c1 === HYPHEN_MINUS) {
    if (isDigit(c2)) {
      return true;
    }
    return c2 === FULL_STOP && isDigit(c3);
  }
  if (c1 === FULL_STOP) {
    return isDigit(c2);
  }
  return isDigit(c1);
};
var stringToNumber = function (codePoints) {
  var c = 0;
  var sign = 1;
  if (codePoints[c] === PLUS_SIGN || codePoints[c] === HYPHEN_MINUS) {
    if (codePoints[c] === HYPHEN_MINUS) {
      sign = -1;
    }
    c++;
  }
  var integers = [];
  while (isDigit(codePoints[c])) {
    integers.push(codePoints[c++]);
  }
  var int = integers.length ? parseInt(fromCodePoint$1.apply(void 0, integers), 10) : 0;
  if (codePoints[c] === FULL_STOP) {
    c++;
  }
  var fraction = [];
  while (isDigit(codePoints[c])) {
    fraction.push(codePoints[c++]);
  }
  var fracd = fraction.length;
  var frac = fracd ? parseInt(fromCodePoint$1.apply(void 0, fraction), 10) : 0;
  if (codePoints[c] === E || codePoints[c] === e) {
    c++;
  }
  var expsign = 1;
  if (codePoints[c] === PLUS_SIGN || codePoints[c] === HYPHEN_MINUS) {
    if (codePoints[c] === HYPHEN_MINUS) {
      expsign = -1;
    }
    c++;
  }
  var exponent = [];
  while (isDigit(codePoints[c])) {
    exponent.push(codePoints[c++]);
  }
  var exp = exponent.length ? parseInt(fromCodePoint$1.apply(void 0, exponent), 10) : 0;
  return sign * (int + frac * Math.pow(10, -fracd)) * Math.pow(10, expsign * exp);
};
var LEFT_PARENTHESIS_TOKEN = {
  type: 2 /* LEFT_PARENTHESIS_TOKEN */
};
var RIGHT_PARENTHESIS_TOKEN = {
  type: 3 /* RIGHT_PARENTHESIS_TOKEN */
};
var COMMA_TOKEN = {
  type: 4 /* COMMA_TOKEN */
};
var SUFFIX_MATCH_TOKEN = {
  type: 13 /* SUFFIX_MATCH_TOKEN */
};
var PREFIX_MATCH_TOKEN = {
  type: 8 /* PREFIX_MATCH_TOKEN */
};
var COLUMN_TOKEN = {
  type: 21 /* COLUMN_TOKEN */
};
var DASH_MATCH_TOKEN = {
  type: 9 /* DASH_MATCH_TOKEN */
};
var INCLUDE_MATCH_TOKEN = {
  type: 10 /* INCLUDE_MATCH_TOKEN */
};
var LEFT_CURLY_BRACKET_TOKEN = {
  type: 11 /* LEFT_CURLY_BRACKET_TOKEN */
};
var RIGHT_CURLY_BRACKET_TOKEN = {
  type: 12 /* RIGHT_CURLY_BRACKET_TOKEN */
};
var SUBSTRING_MATCH_TOKEN = {
  type: 14 /* SUBSTRING_MATCH_TOKEN */
};
var BAD_URL_TOKEN = {
  type: 23 /* BAD_URL_TOKEN */
};
var BAD_STRING_TOKEN = {
  type: 1 /* BAD_STRING_TOKEN */
};
var CDO_TOKEN = {
  type: 25 /* CDO_TOKEN */
};
var CDC_TOKEN = {
  type: 24 /* CDC_TOKEN */
};
var COLON_TOKEN = {
  type: 26 /* COLON_TOKEN */
};
var SEMICOLON_TOKEN = {
  type: 27 /* SEMICOLON_TOKEN */
};
var LEFT_SQUARE_BRACKET_TOKEN = {
  type: 28 /* LEFT_SQUARE_BRACKET_TOKEN */
};
var RIGHT_SQUARE_BRACKET_TOKEN = {
  type: 29 /* RIGHT_SQUARE_BRACKET_TOKEN */
};
var WHITESPACE_TOKEN = {
  type: 31 /* WHITESPACE_TOKEN */
};
var EOF_TOKEN = {
  type: 32 /* EOF_TOKEN */
};
var Tokenizer = /** @class */function () {
  function Tokenizer() {
    this._value = [];
  }
  Tokenizer.prototype.write = function (chunk) {
    this._value = this._value.concat(toCodePoints$1(chunk));
  };
  Tokenizer.prototype.read = function () {
    var tokens = [];
    var token = this.consumeToken();
    while (token !== EOF_TOKEN) {
      tokens.push(token);
      token = this.consumeToken();
    }
    return tokens;
  };
  Tokenizer.prototype.consumeToken = function () {
    var codePoint = this.consumeCodePoint();
    switch (codePoint) {
      case QUOTATION_MARK:
        return this.consumeStringToken(QUOTATION_MARK);
      case NUMBER_SIGN:
        var c1 = this.peekCodePoint(0);
        var c2 = this.peekCodePoint(1);
        var c3 = this.peekCodePoint(2);
        if (isNameCodePoint(c1) || isValidEscape(c2, c3)) {
          var flags = isIdentifierStart(c1, c2, c3) ? FLAG_ID : FLAG_UNRESTRICTED;
          var value = this.consumeName();
          return {
            type: 5 /* HASH_TOKEN */,
            value: value,
            flags: flags
          };
        }
        break;
      case DOLLAR_SIGN:
        if (this.peekCodePoint(0) === EQUALS_SIGN) {
          this.consumeCodePoint();
          return SUFFIX_MATCH_TOKEN;
        }
        break;
      case APOSTROPHE:
        return this.consumeStringToken(APOSTROPHE);
      case LEFT_PARENTHESIS:
        return LEFT_PARENTHESIS_TOKEN;
      case RIGHT_PARENTHESIS:
        return RIGHT_PARENTHESIS_TOKEN;
      case ASTERISK:
        if (this.peekCodePoint(0) === EQUALS_SIGN) {
          this.consumeCodePoint();
          return SUBSTRING_MATCH_TOKEN;
        }
        break;
      case PLUS_SIGN:
        if (isNumberStart(codePoint, this.peekCodePoint(0), this.peekCodePoint(1))) {
          this.reconsumeCodePoint(codePoint);
          return this.consumeNumericToken();
        }
        break;
      case COMMA:
        return COMMA_TOKEN;
      case HYPHEN_MINUS:
        var e1 = codePoint;
        var e2 = this.peekCodePoint(0);
        var e3 = this.peekCodePoint(1);
        if (isNumberStart(e1, e2, e3)) {
          this.reconsumeCodePoint(codePoint);
          return this.consumeNumericToken();
        }
        if (isIdentifierStart(e1, e2, e3)) {
          this.reconsumeCodePoint(codePoint);
          return this.consumeIdentLikeToken();
        }
        if (e2 === HYPHEN_MINUS && e3 === GREATER_THAN_SIGN) {
          this.consumeCodePoint();
          this.consumeCodePoint();
          return CDC_TOKEN;
        }
        break;
      case FULL_STOP:
        if (isNumberStart(codePoint, this.peekCodePoint(0), this.peekCodePoint(1))) {
          this.reconsumeCodePoint(codePoint);
          return this.consumeNumericToken();
        }
        break;
      case SOLIDUS:
        if (this.peekCodePoint(0) === ASTERISK) {
          this.consumeCodePoint();
          while (true) {
            var c = this.consumeCodePoint();
            if (c === ASTERISK) {
              c = this.consumeCodePoint();
              if (c === SOLIDUS) {
                return this.consumeToken();
              }
            }
            if (c === EOF) {
              return this.consumeToken();
            }
          }
        }
        break;
      case COLON:
        return COLON_TOKEN;
      case SEMICOLON:
        return SEMICOLON_TOKEN;
      case LESS_THAN_SIGN:
        if (this.peekCodePoint(0) === EXCLAMATION_MARK && this.peekCodePoint(1) === HYPHEN_MINUS && this.peekCodePoint(2) === HYPHEN_MINUS) {
          this.consumeCodePoint();
          this.consumeCodePoint();
          return CDO_TOKEN;
        }
        break;
      case COMMERCIAL_AT:
        var a1 = this.peekCodePoint(0);
        var a2 = this.peekCodePoint(1);
        var a3 = this.peekCodePoint(2);
        if (isIdentifierStart(a1, a2, a3)) {
          var value = this.consumeName();
          return {
            type: 7 /* AT_KEYWORD_TOKEN */,
            value: value
          };
        }
        break;
      case LEFT_SQUARE_BRACKET:
        return LEFT_SQUARE_BRACKET_TOKEN;
      case REVERSE_SOLIDUS:
        if (isValidEscape(codePoint, this.peekCodePoint(0))) {
          this.reconsumeCodePoint(codePoint);
          return this.consumeIdentLikeToken();
        }
        break;
      case RIGHT_SQUARE_BRACKET:
        return RIGHT_SQUARE_BRACKET_TOKEN;
      case CIRCUMFLEX_ACCENT:
        if (this.peekCodePoint(0) === EQUALS_SIGN) {
          this.consumeCodePoint();
          return PREFIX_MATCH_TOKEN;
        }
        break;
      case LEFT_CURLY_BRACKET:
        return LEFT_CURLY_BRACKET_TOKEN;
      case RIGHT_CURLY_BRACKET:
        return RIGHT_CURLY_BRACKET_TOKEN;
      case u:
      case U:
        var u1 = this.peekCodePoint(0);
        var u2 = this.peekCodePoint(1);
        if (u1 === PLUS_SIGN && (isHex(u2) || u2 === QUESTION_MARK)) {
          this.consumeCodePoint();
          this.consumeUnicodeRangeToken();
        }
        this.reconsumeCodePoint(codePoint);
        return this.consumeIdentLikeToken();
      case VERTICAL_LINE:
        if (this.peekCodePoint(0) === EQUALS_SIGN) {
          this.consumeCodePoint();
          return DASH_MATCH_TOKEN;
        }
        if (this.peekCodePoint(0) === VERTICAL_LINE) {
          this.consumeCodePoint();
          return COLUMN_TOKEN;
        }
        break;
      case TILDE:
        if (this.peekCodePoint(0) === EQUALS_SIGN) {
          this.consumeCodePoint();
          return INCLUDE_MATCH_TOKEN;
        }
        break;
      case EOF:
        return EOF_TOKEN;
    }
    if (isWhiteSpace(codePoint)) {
      this.consumeWhiteSpace();
      return WHITESPACE_TOKEN;
    }
    if (isDigit(codePoint)) {
      this.reconsumeCodePoint(codePoint);
      return this.consumeNumericToken();
    }
    if (isNameStartCodePoint(codePoint)) {
      this.reconsumeCodePoint(codePoint);
      return this.consumeIdentLikeToken();
    }
    return {
      type: 6 /* DELIM_TOKEN */,
      value: fromCodePoint$1(codePoint)
    };
  };
  Tokenizer.prototype.consumeCodePoint = function () {
    var value = this._value.shift();
    return typeof value === 'undefined' ? -1 : value;
  };
  Tokenizer.prototype.reconsumeCodePoint = function (codePoint) {
    this._value.unshift(codePoint);
  };
  Tokenizer.prototype.peekCodePoint = function (delta) {
    if (delta >= this._value.length) {
      return -1;
    }
    return this._value[delta];
  };
  Tokenizer.prototype.consumeUnicodeRangeToken = function () {
    var digits = [];
    var codePoint = this.consumeCodePoint();
    while (isHex(codePoint) && digits.length < 6) {
      digits.push(codePoint);
      codePoint = this.consumeCodePoint();
    }
    var questionMarks = false;
    while (codePoint === QUESTION_MARK && digits.length < 6) {
      digits.push(codePoint);
      codePoint = this.consumeCodePoint();
      questionMarks = true;
    }
    if (questionMarks) {
      var start_1 = parseInt(fromCodePoint$1.apply(void 0, digits.map(function (digit) {
        return digit === QUESTION_MARK ? ZERO : digit;
      })), 16);
      var end = parseInt(fromCodePoint$1.apply(void 0, digits.map(function (digit) {
        return digit === QUESTION_MARK ? F : digit;
      })), 16);
      return {
        type: 30 /* UNICODE_RANGE_TOKEN */,
        start: start_1,
        end: end
      };
    }
    var start = parseInt(fromCodePoint$1.apply(void 0, digits), 16);
    if (this.peekCodePoint(0) === HYPHEN_MINUS && isHex(this.peekCodePoint(1))) {
      this.consumeCodePoint();
      codePoint = this.consumeCodePoint();
      var endDigits = [];
      while (isHex(codePoint) && endDigits.length < 6) {
        endDigits.push(codePoint);
        codePoint = this.consumeCodePoint();
      }
      var end = parseInt(fromCodePoint$1.apply(void 0, endDigits), 16);
      return {
        type: 30 /* UNICODE_RANGE_TOKEN */,
        start: start,
        end: end
      };
    } else {
      return {
        type: 30 /* UNICODE_RANGE_TOKEN */,
        start: start,
        end: start
      };
    }
  };
  Tokenizer.prototype.consumeIdentLikeToken = function () {
    var value = this.consumeName();
    if (value.toLowerCase() === 'url' && this.peekCodePoint(0) === LEFT_PARENTHESIS) {
      this.consumeCodePoint();
      return this.consumeUrlToken();
    } else if (this.peekCodePoint(0) === LEFT_PARENTHESIS) {
      this.consumeCodePoint();
      return {
        type: 19 /* FUNCTION_TOKEN */,
        value: value
      };
    }
    return {
      type: 20 /* IDENT_TOKEN */,
      value: value
    };
  };
  Tokenizer.prototype.consumeUrlToken = function () {
    var value = [];
    this.consumeWhiteSpace();
    if (this.peekCodePoint(0) === EOF) {
      return {
        type: 22 /* URL_TOKEN */,
        value: ''
      };
    }
    var next = this.peekCodePoint(0);
    if (next === APOSTROPHE || next === QUOTATION_MARK) {
      var stringToken = this.consumeStringToken(this.consumeCodePoint());
      if (stringToken.type === 0 /* STRING_TOKEN */) {
        this.consumeWhiteSpace();
        if (this.peekCodePoint(0) === EOF || this.peekCodePoint(0) === RIGHT_PARENTHESIS) {
          this.consumeCodePoint();
          return {
            type: 22 /* URL_TOKEN */,
            value: stringToken.value
          };
        }
      }
      this.consumeBadUrlRemnants();
      return BAD_URL_TOKEN;
    }
    while (true) {
      var codePoint = this.consumeCodePoint();
      if (codePoint === EOF || codePoint === RIGHT_PARENTHESIS) {
        return {
          type: 22 /* URL_TOKEN */,
          value: fromCodePoint$1.apply(void 0, value)
        };
      } else if (isWhiteSpace(codePoint)) {
        this.consumeWhiteSpace();
        if (this.peekCodePoint(0) === EOF || this.peekCodePoint(0) === RIGHT_PARENTHESIS) {
          this.consumeCodePoint();
          return {
            type: 22 /* URL_TOKEN */,
            value: fromCodePoint$1.apply(void 0, value)
          };
        }
        this.consumeBadUrlRemnants();
        return BAD_URL_TOKEN;
      } else if (codePoint === QUOTATION_MARK || codePoint === APOSTROPHE || codePoint === LEFT_PARENTHESIS || isNonPrintableCodePoint(codePoint)) {
        this.consumeBadUrlRemnants();
        return BAD_URL_TOKEN;
      } else if (codePoint === REVERSE_SOLIDUS) {
        if (isValidEscape(codePoint, this.peekCodePoint(0))) {
          value.push(this.consumeEscapedCodePoint());
        } else {
          this.consumeBadUrlRemnants();
          return BAD_URL_TOKEN;
        }
      } else {
        value.push(codePoint);
      }
    }
  };
  Tokenizer.prototype.consumeWhiteSpace = function () {
    while (isWhiteSpace(this.peekCodePoint(0))) {
      this.consumeCodePoint();
    }
  };
  Tokenizer.prototype.consumeBadUrlRemnants = function () {
    while (true) {
      var codePoint = this.consumeCodePoint();
      if (codePoint === RIGHT_PARENTHESIS || codePoint === EOF) {
        return;
      }
      if (isValidEscape(codePoint, this.peekCodePoint(0))) {
        this.consumeEscapedCodePoint();
      }
    }
  };
  Tokenizer.prototype.consumeStringSlice = function (count) {
    var SLICE_STACK_SIZE = 50000;
    var value = '';
    while (count > 0) {
      var amount = Math.min(SLICE_STACK_SIZE, count);
      value += fromCodePoint$1.apply(void 0, this._value.splice(0, amount));
      count -= amount;
    }
    this._value.shift();
    return value;
  };
  Tokenizer.prototype.consumeStringToken = function (endingCodePoint) {
    var value = '';
    var i = 0;
    do {
      var codePoint = this._value[i];
      if (codePoint === EOF || codePoint === undefined || codePoint === endingCodePoint) {
        value += this.consumeStringSlice(i);
        return {
          type: 0 /* STRING_TOKEN */,
          value: value
        };
      }
      if (codePoint === LINE_FEED) {
        this._value.splice(0, i);
        return BAD_STRING_TOKEN;
      }
      if (codePoint === REVERSE_SOLIDUS) {
        var next = this._value[i + 1];
        if (next !== EOF && next !== undefined) {
          if (next === LINE_FEED) {
            value += this.consumeStringSlice(i);
            i = -1;
            this._value.shift();
          } else if (isValidEscape(codePoint, next)) {
            value += this.consumeStringSlice(i);
            value += fromCodePoint$1(this.consumeEscapedCodePoint());
            i = -1;
          }
        }
      }
      i++;
    } while (true);
  };
  Tokenizer.prototype.consumeNumber = function () {
    var repr = [];
    var type = FLAG_INTEGER;
    var c1 = this.peekCodePoint(0);
    if (c1 === PLUS_SIGN || c1 === HYPHEN_MINUS) {
      repr.push(this.consumeCodePoint());
    }
    while (isDigit(this.peekCodePoint(0))) {
      repr.push(this.consumeCodePoint());
    }
    c1 = this.peekCodePoint(0);
    var c2 = this.peekCodePoint(1);
    if (c1 === FULL_STOP && isDigit(c2)) {
      repr.push(this.consumeCodePoint(), this.consumeCodePoint());
      type = FLAG_NUMBER;
      while (isDigit(this.peekCodePoint(0))) {
        repr.push(this.consumeCodePoint());
      }
    }
    c1 = this.peekCodePoint(0);
    c2 = this.peekCodePoint(1);
    var c3 = this.peekCodePoint(2);
    if ((c1 === E || c1 === e) && ((c2 === PLUS_SIGN || c2 === HYPHEN_MINUS) && isDigit(c3) || isDigit(c2))) {
      repr.push(this.consumeCodePoint(), this.consumeCodePoint());
      type = FLAG_NUMBER;
      while (isDigit(this.peekCodePoint(0))) {
        repr.push(this.consumeCodePoint());
      }
    }
    return [stringToNumber(repr), type];
  };
  Tokenizer.prototype.consumeNumericToken = function () {
    var _a = this.consumeNumber(),
      number = _a[0],
      flags = _a[1];
    var c1 = this.peekCodePoint(0);
    var c2 = this.peekCodePoint(1);
    var c3 = this.peekCodePoint(2);
    if (isIdentifierStart(c1, c2, c3)) {
      var unit = this.consumeName();
      return {
        type: 15 /* DIMENSION_TOKEN */,
        number: number,
        flags: flags,
        unit: unit
      };
    }
    if (c1 === PERCENTAGE_SIGN) {
      this.consumeCodePoint();
      return {
        type: 16 /* PERCENTAGE_TOKEN */,
        number: number,
        flags: flags
      };
    }
    return {
      type: 17 /* NUMBER_TOKEN */,
      number: number,
      flags: flags
    };
  };
  Tokenizer.prototype.consumeEscapedCodePoint = function () {
    var codePoint = this.consumeCodePoint();
    if (isHex(codePoint)) {
      var hex = fromCodePoint$1(codePoint);
      while (isHex(this.peekCodePoint(0)) && hex.length < 6) {
        hex += fromCodePoint$1(this.consumeCodePoint());
      }
      if (isWhiteSpace(this.peekCodePoint(0))) {
        this.consumeCodePoint();
      }
      var hexCodePoint = parseInt(hex, 16);
      if (hexCodePoint === 0 || isSurrogateCodePoint(hexCodePoint) || hexCodePoint > 0x10ffff) {
        return REPLACEMENT_CHARACTER;
      }
      return hexCodePoint;
    }
    if (codePoint === EOF) {
      return REPLACEMENT_CHARACTER;
    }
    return codePoint;
  };
  Tokenizer.prototype.consumeName = function () {
    var result = '';
    while (true) {
      var codePoint = this.consumeCodePoint();
      if (isNameCodePoint(codePoint)) {
        result += fromCodePoint$1(codePoint);
      } else if (isValidEscape(codePoint, this.peekCodePoint(0))) {
        result += fromCodePoint$1(this.consumeEscapedCodePoint());
      } else {
        this.reconsumeCodePoint(codePoint);
        return result;
      }
    }
  };
  return Tokenizer;
}();
var Parser = /** @class */function () {
  function Parser(tokens) {
    this._tokens = tokens;
  }
  Parser.create = function (value) {
    var tokenizer = new Tokenizer();
    tokenizer.write(value);
    return new Parser(tokenizer.read());
  };
  Parser.parseValue = function (value) {
    return Parser.create(value).parseComponentValue();
  };
  Parser.parseValues = function (value) {
    return Parser.create(value).parseComponentValues();
  };
  Parser.prototype.parseComponentValue = function () {
    var token = this.consumeToken();
    while (token.type === 31 /* WHITESPACE_TOKEN */) {
      token = this.consumeToken();
    }
    if (token.type === 32 /* EOF_TOKEN */) {
      throw new SyntaxError("Error parsing CSS component value, unexpected EOF");
    }
    this.reconsumeToken(token);
    var value = this.consumeComponentValue();
    do {
      token = this.consumeToken();
    } while (token.type === 31 /* WHITESPACE_TOKEN */);
    if (token.type === 32 /* EOF_TOKEN */) {
      return value;
    }
    throw new SyntaxError("Error parsing CSS component value, multiple values found when expecting only one");
  };
  Parser.prototype.parseComponentValues = function () {
    var values = [];
    while (true) {
      var value = this.consumeComponentValue();
      if (value.type === 32 /* EOF_TOKEN */) {
        return values;
      }
      values.push(value);
      values.push();
    }
  };
  Parser.prototype.consumeComponentValue = function () {
    var token = this.consumeToken();
    switch (token.type) {
      case 11 /* LEFT_CURLY_BRACKET_TOKEN */:
      case 28 /* LEFT_SQUARE_BRACKET_TOKEN */:
      case 2 /* LEFT_PARENTHESIS_TOKEN */:
        return this.consumeSimpleBlock(token.type);
      case 19 /* FUNCTION_TOKEN */:
        return this.consumeFunction(token);
    }
    return token;
  };
  Parser.prototype.consumeSimpleBlock = function (type) {
    var block = {
      type: type,
      values: []
    };
    var token = this.consumeToken();
    while (true) {
      if (token.type === 32 /* EOF_TOKEN */ || isEndingTokenFor(token, type)) {
        return block;
      }
      this.reconsumeToken(token);
      block.values.push(this.consumeComponentValue());
      token = this.consumeToken();
    }
  };
  Parser.prototype.consumeFunction = function (functionToken) {
    var cssFunction = {
      name: functionToken.value,
      values: [],
      type: 18 /* FUNCTION */
    };
    while (true) {
      var token = this.consumeToken();
      if (token.type === 32 /* EOF_TOKEN */ || token.type === 3 /* RIGHT_PARENTHESIS_TOKEN */) {
        return cssFunction;
      }
      this.reconsumeToken(token);
      cssFunction.values.push(this.consumeComponentValue());
    }
  };
  Parser.prototype.consumeToken = function () {
    var token = this._tokens.shift();
    return typeof token === 'undefined' ? EOF_TOKEN : token;
  };
  Parser.prototype.reconsumeToken = function (token) {
    this._tokens.unshift(token);
  };
  return Parser;
}();
var isDimensionToken = function (token) {
  return token.type === 15 /* DIMENSION_TOKEN */;
};
var isNumberToken = function (token) {
  return token.type === 17 /* NUMBER_TOKEN */;
};
var isIdentToken = function (token) {
  return token.type === 20 /* IDENT_TOKEN */;
};
var isStringToken = function (token) {
  return token.type === 0 /* STRING_TOKEN */;
};
var isIdentWithValue = function (token, value) {
  return isIdentToken(token) && token.value === value;
};
var nonWhiteSpace = function (token) {
  return token.type !== 31 /* WHITESPACE_TOKEN */;
};
var nonFunctionArgSeparator = function (token) {
  return token.type !== 31 /* WHITESPACE_TOKEN */ && token.type !== 4 /* COMMA_TOKEN */;
};
var parseFunctionArgs = function (tokens) {
  var args = [];
  var arg = [];
  tokens.forEach(function (token) {
    if (token.type === 4 /* COMMA_TOKEN */) {
      if (arg.length === 0) {
        throw new Error("Error parsing function args, zero tokens for arg");
      }
      args.push(arg);
      arg = [];
      return;
    }
    if (token.type !== 31 /* WHITESPACE_TOKEN */) {
      arg.push(token);
    }
  });
  if (arg.length) {
    args.push(arg);
  }
  return args;
};
var isEndingTokenFor = function (token, type) {
  if (type === 11 /* LEFT_CURLY_BRACKET_TOKEN */ && token.type === 12 /* RIGHT_CURLY_BRACKET_TOKEN */) {
    return true;
  }
  if (type === 28 /* LEFT_SQUARE_BRACKET_TOKEN */ && token.type === 29 /* RIGHT_SQUARE_BRACKET_TOKEN */) {
    return true;
  }
  return type === 2 /* LEFT_PARENTHESIS_TOKEN */ && token.type === 3 /* RIGHT_PARENTHESIS_TOKEN */;
};
var isLength = function (token) {
  return token.type === 17 /* NUMBER_TOKEN */ || token.type === 15 /* DIMENSION_TOKEN */;
};
var isLengthPercentage = function (token) {
  return token.type === 16 /* PERCENTAGE_TOKEN */ || isLength(token);
};
var parseLengthPercentageTuple = function (tokens) {
  return tokens.length > 1 ? [tokens[0], tokens[1]] : [tokens[0]];
};
var ZERO_LENGTH = {
  type: 17 /* NUMBER_TOKEN */,
  number: 0,
  flags: FLAG_INTEGER
};
var FIFTY_PERCENT = {
  type: 16 /* PERCENTAGE_TOKEN */,
  number: 50,
  flags: FLAG_INTEGER
};
var HUNDRED_PERCENT = {
  type: 16 /* PERCENTAGE_TOKEN */,
  number: 100,
  flags: FLAG_INTEGER
};
var getAbsoluteValueForTuple = function (tuple, width, height) {
  var x = tuple[0],
    y = tuple[1];
  return [getAbsoluteValue(x, width), getAbsoluteValue(typeof y !== 'undefined' ? y : x, height)];
};
var getAbsoluteValue = function (token, parent) {
  if (token.type === 16 /* PERCENTAGE_TOKEN */) {
    return token.number / 100 * parent;
  }
  if (isDimensionToken(token)) {
    switch (token.unit) {
      case 'rem':
      case 'em':
        return 16 * token.number;
      // TODO use correct font-size
      case 'px':
      default:
        return token.number;
    }
  }
  return token.number;
};
var DEG = 'deg';
var GRAD = 'grad';
var RAD = 'rad';
var TURN = 'turn';
var angle = {
  name: 'angle',
  parse: function (_context, value) {
    if (value.type === 15 /* DIMENSION_TOKEN */) {
      switch (value.unit) {
        case DEG:
          return Math.PI * value.number / 180;
        case GRAD:
          return Math.PI / 200 * value.number;
        case RAD:
          return value.number;
        case TURN:
          return Math.PI * 2 * value.number;
      }
    }
    throw new Error("Unsupported angle type");
  }
};
var isAngle = function (value) {
  if (value.type === 15 /* DIMENSION_TOKEN */) {
    if (value.unit === DEG || value.unit === GRAD || value.unit === RAD || value.unit === TURN) {
      return true;
    }
  }
  return false;
};
var parseNamedSide = function (tokens) {
  var sideOrCorner = tokens.filter(isIdentToken).map(function (ident) {
    return ident.value;
  }).join(' ');
  switch (sideOrCorner) {
    case 'to bottom right':
    case 'to right bottom':
    case 'left top':
    case 'top left':
      return [ZERO_LENGTH, ZERO_LENGTH];
    case 'to top':
    case 'bottom':
      return deg(0);
    case 'to bottom left':
    case 'to left bottom':
    case 'right top':
    case 'top right':
      return [ZERO_LENGTH, HUNDRED_PERCENT];
    case 'to right':
    case 'left':
      return deg(90);
    case 'to top left':
    case 'to left top':
    case 'right bottom':
    case 'bottom right':
      return [HUNDRED_PERCENT, HUNDRED_PERCENT];
    case 'to bottom':
    case 'top':
      return deg(180);
    case 'to top right':
    case 'to right top':
    case 'left bottom':
    case 'bottom left':
      return [HUNDRED_PERCENT, ZERO_LENGTH];
    case 'to left':
    case 'right':
      return deg(270);
  }
  return 0;
};
var deg = function (deg) {
  return Math.PI * deg / 180;
};
var color$1 = {
  name: 'color',
  parse: function (context, value) {
    if (value.type === 18 /* FUNCTION */) {
      var colorFunction = SUPPORTED_COLOR_FUNCTIONS[value.name];
      if (typeof colorFunction === 'undefined') {
        throw new Error("Attempting to parse an unsupported color function \"" + value.name + "\"");
      }
      return colorFunction(context, value.values);
    }
    if (value.type === 5 /* HASH_TOKEN */) {
      if (value.value.length === 3) {
        var r = value.value.substring(0, 1);
        var g = value.value.substring(1, 2);
        var b = value.value.substring(2, 3);
        return pack(parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16), 1);
      }
      if (value.value.length === 4) {
        var r = value.value.substring(0, 1);
        var g = value.value.substring(1, 2);
        var b = value.value.substring(2, 3);
        var a = value.value.substring(3, 4);
        return pack(parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16), parseInt(a + a, 16) / 255);
      }
      if (value.value.length === 6) {
        var r = value.value.substring(0, 2);
        var g = value.value.substring(2, 4);
        var b = value.value.substring(4, 6);
        return pack(parseInt(r, 16), parseInt(g, 16), parseInt(b, 16), 1);
      }
      if (value.value.length === 8) {
        var r = value.value.substring(0, 2);
        var g = value.value.substring(2, 4);
        var b = value.value.substring(4, 6);
        var a = value.value.substring(6, 8);
        return pack(parseInt(r, 16), parseInt(g, 16), parseInt(b, 16), parseInt(a, 16) / 255);
      }
    }
    if (value.type === 20 /* IDENT_TOKEN */) {
      var namedColor = COLORS[value.value.toUpperCase()];
      if (typeof namedColor !== 'undefined') {
        return namedColor;
      }
    }
    return COLORS.TRANSPARENT;
  }
};
var isTransparent = function (color) {
  return (0xff & color) === 0;
};
var asString = function (color) {
  var alpha = 0xff & color;
  var blue = 0xff & color >> 8;
  var green = 0xff & color >> 16;
  var red = 0xff & color >> 24;
  return alpha < 255 ? "rgba(" + red + "," + green + "," + blue + "," + alpha / 255 + ")" : "rgb(" + red + "," + green + "," + blue + ")";
};
var pack = function (r, g, b, a) {
  return (r << 24 | g << 16 | b << 8 | Math.round(a * 255) << 0) >>> 0;
};
var getTokenColorValue = function (token, i) {
  if (token.type === 17 /* NUMBER_TOKEN */) {
    return token.number;
  }
  if (token.type === 16 /* PERCENTAGE_TOKEN */) {
    var max = i === 3 ? 1 : 255;
    return i === 3 ? token.number / 100 * max : Math.round(token.number / 100 * max);
  }
  return 0;
};
var rgb = function (_context, args) {
  var tokens = args.filter(nonFunctionArgSeparator);
  if (tokens.length === 3) {
    var _a = tokens.map(getTokenColorValue),
      r = _a[0],
      g = _a[1],
      b = _a[2];
    return pack(r, g, b, 1);
  }
  if (tokens.length === 4) {
    var _b = tokens.map(getTokenColorValue),
      r = _b[0],
      g = _b[1],
      b = _b[2],
      a = _b[3];
    return pack(r, g, b, a);
  }
  return 0;
};
function hue2rgb$2(t1, t2, hue) {
  if (hue < 0) {
    hue += 1;
  }
  if (hue >= 1) {
    hue -= 1;
  }
  if (hue < 1 / 6) {
    return (t2 - t1) * hue * 6 + t1;
  } else if (hue < 1 / 2) {
    return t2;
  } else if (hue < 2 / 3) {
    return (t2 - t1) * 6 * (2 / 3 - hue) + t1;
  } else {
    return t1;
  }
}
var hsl = function (context, args) {
  var tokens = args.filter(nonFunctionArgSeparator);
  var hue = tokens[0],
    saturation = tokens[1],
    lightness = tokens[2],
    alpha = tokens[3];
  var h = (hue.type === 17 /* NUMBER_TOKEN */ ? deg(hue.number) : angle.parse(context, hue)) / (Math.PI * 2);
  var s = isLengthPercentage(saturation) ? saturation.number / 100 : 0;
  var l = isLengthPercentage(lightness) ? lightness.number / 100 : 0;
  var a = typeof alpha !== 'undefined' && isLengthPercentage(alpha) ? getAbsoluteValue(alpha, 1) : 1;
  if (s === 0) {
    return pack(l * 255, l * 255, l * 255, 1);
  }
  var t2 = l <= 0.5 ? l * (s + 1) : l + s - l * s;
  var t1 = l * 2 - t2;
  var r = hue2rgb$2(t1, t2, h + 1 / 3);
  var g = hue2rgb$2(t1, t2, h);
  var b = hue2rgb$2(t1, t2, h - 1 / 3);
  return pack(r * 255, g * 255, b * 255, a);
};
var SUPPORTED_COLOR_FUNCTIONS = {
  hsl: hsl,
  hsla: hsl,
  rgb: rgb,
  rgba: rgb
};
var parseColor = function (context, value) {
  return color$1.parse(context, Parser.create(value).parseComponentValue());
};
var COLORS = {
  ALICEBLUE: 0xf0f8ffff,
  ANTIQUEWHITE: 0xfaebd7ff,
  AQUA: 0x00ffffff,
  AQUAMARINE: 0x7fffd4ff,
  AZURE: 0xf0ffffff,
  BEIGE: 0xf5f5dcff,
  BISQUE: 0xffe4c4ff,
  BLACK: 0x000000ff,
  BLANCHEDALMOND: 0xffebcdff,
  BLUE: 0x0000ffff,
  BLUEVIOLET: 0x8a2be2ff,
  BROWN: 0xa52a2aff,
  BURLYWOOD: 0xdeb887ff,
  CADETBLUE: 0x5f9ea0ff,
  CHARTREUSE: 0x7fff00ff,
  CHOCOLATE: 0xd2691eff,
  CORAL: 0xff7f50ff,
  CORNFLOWERBLUE: 0x6495edff,
  CORNSILK: 0xfff8dcff,
  CRIMSON: 0xdc143cff,
  CYAN: 0x00ffffff,
  DARKBLUE: 0x00008bff,
  DARKCYAN: 0x008b8bff,
  DARKGOLDENROD: 0xb886bbff,
  DARKGRAY: 0xa9a9a9ff,
  DARKGREEN: 0x006400ff,
  DARKGREY: 0xa9a9a9ff,
  DARKKHAKI: 0xbdb76bff,
  DARKMAGENTA: 0x8b008bff,
  DARKOLIVEGREEN: 0x556b2fff,
  DARKORANGE: 0xff8c00ff,
  DARKORCHID: 0x9932ccff,
  DARKRED: 0x8b0000ff,
  DARKSALMON: 0xe9967aff,
  DARKSEAGREEN: 0x8fbc8fff,
  DARKSLATEBLUE: 0x483d8bff,
  DARKSLATEGRAY: 0x2f4f4fff,
  DARKSLATEGREY: 0x2f4f4fff,
  DARKTURQUOISE: 0x00ced1ff,
  DARKVIOLET: 0x9400d3ff,
  DEEPPINK: 0xff1493ff,
  DEEPSKYBLUE: 0x00bfffff,
  DIMGRAY: 0x696969ff,
  DIMGREY: 0x696969ff,
  DODGERBLUE: 0x1e90ffff,
  FIREBRICK: 0xb22222ff,
  FLORALWHITE: 0xfffaf0ff,
  FORESTGREEN: 0x228b22ff,
  FUCHSIA: 0xff00ffff,
  GAINSBORO: 0xdcdcdcff,
  GHOSTWHITE: 0xf8f8ffff,
  GOLD: 0xffd700ff,
  GOLDENROD: 0xdaa520ff,
  GRAY: 0x808080ff,
  GREEN: 0x008000ff,
  GREENYELLOW: 0xadff2fff,
  GREY: 0x808080ff,
  HONEYDEW: 0xf0fff0ff,
  HOTPINK: 0xff69b4ff,
  INDIANRED: 0xcd5c5cff,
  INDIGO: 0x4b0082ff,
  IVORY: 0xfffff0ff,
  KHAKI: 0xf0e68cff,
  LAVENDER: 0xe6e6faff,
  LAVENDERBLUSH: 0xfff0f5ff,
  LAWNGREEN: 0x7cfc00ff,
  LEMONCHIFFON: 0xfffacdff,
  LIGHTBLUE: 0xadd8e6ff,
  LIGHTCORAL: 0xf08080ff,
  LIGHTCYAN: 0xe0ffffff,
  LIGHTGOLDENRODYELLOW: 0xfafad2ff,
  LIGHTGRAY: 0xd3d3d3ff,
  LIGHTGREEN: 0x90ee90ff,
  LIGHTGREY: 0xd3d3d3ff,
  LIGHTPINK: 0xffb6c1ff,
  LIGHTSALMON: 0xffa07aff,
  LIGHTSEAGREEN: 0x20b2aaff,
  LIGHTSKYBLUE: 0x87cefaff,
  LIGHTSLATEGRAY: 0x778899ff,
  LIGHTSLATEGREY: 0x778899ff,
  LIGHTSTEELBLUE: 0xb0c4deff,
  LIGHTYELLOW: 0xffffe0ff,
  LIME: 0x00ff00ff,
  LIMEGREEN: 0x32cd32ff,
  LINEN: 0xfaf0e6ff,
  MAGENTA: 0xff00ffff,
  MAROON: 0x800000ff,
  MEDIUMAQUAMARINE: 0x66cdaaff,
  MEDIUMBLUE: 0x0000cdff,
  MEDIUMORCHID: 0xba55d3ff,
  MEDIUMPURPLE: 0x9370dbff,
  MEDIUMSEAGREEN: 0x3cb371ff,
  MEDIUMSLATEBLUE: 0x7b68eeff,
  MEDIUMSPRINGGREEN: 0x00fa9aff,
  MEDIUMTURQUOISE: 0x48d1ccff,
  MEDIUMVIOLETRED: 0xc71585ff,
  MIDNIGHTBLUE: 0x191970ff,
  MINTCREAM: 0xf5fffaff,
  MISTYROSE: 0xffe4e1ff,
  MOCCASIN: 0xffe4b5ff,
  NAVAJOWHITE: 0xffdeadff,
  NAVY: 0x000080ff,
  OLDLACE: 0xfdf5e6ff,
  OLIVE: 0x808000ff,
  OLIVEDRAB: 0x6b8e23ff,
  ORANGE: 0xffa500ff,
  ORANGERED: 0xff4500ff,
  ORCHID: 0xda70d6ff,
  PALEGOLDENROD: 0xeee8aaff,
  PALEGREEN: 0x98fb98ff,
  PALETURQUOISE: 0xafeeeeff,
  PALEVIOLETRED: 0xdb7093ff,
  PAPAYAWHIP: 0xffefd5ff,
  PEACHPUFF: 0xffdab9ff,
  PERU: 0xcd853fff,
  PINK: 0xffc0cbff,
  PLUM: 0xdda0ddff,
  POWDERBLUE: 0xb0e0e6ff,
  PURPLE: 0x800080ff,
  REBECCAPURPLE: 0x663399ff,
  RED: 0xff0000ff,
  ROSYBROWN: 0xbc8f8fff,
  ROYALBLUE: 0x4169e1ff,
  SADDLEBROWN: 0x8b4513ff,
  SALMON: 0xfa8072ff,
  SANDYBROWN: 0xf4a460ff,
  SEAGREEN: 0x2e8b57ff,
  SEASHELL: 0xfff5eeff,
  SIENNA: 0xa0522dff,
  SILVER: 0xc0c0c0ff,
  SKYBLUE: 0x87ceebff,
  SLATEBLUE: 0x6a5acdff,
  SLATEGRAY: 0x708090ff,
  SLATEGREY: 0x708090ff,
  SNOW: 0xfffafaff,
  SPRINGGREEN: 0x00ff7fff,
  STEELBLUE: 0x4682b4ff,
  TAN: 0xd2b48cff,
  TEAL: 0x008080ff,
  THISTLE: 0xd8bfd8ff,
  TOMATO: 0xff6347ff,
  TRANSPARENT: 0x00000000,
  TURQUOISE: 0x40e0d0ff,
  VIOLET: 0xee82eeff,
  WHEAT: 0xf5deb3ff,
  WHITE: 0xffffffff,
  WHITESMOKE: 0xf5f5f5ff,
  YELLOW: 0xffff00ff,
  YELLOWGREEN: 0x9acd32ff
};
var backgroundClip = {
  name: 'background-clip',
  initialValue: 'border-box',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return tokens.map(function (token) {
      if (isIdentToken(token)) {
        switch (token.value) {
          case 'padding-box':
            return 1 /* PADDING_BOX */;
          case 'content-box':
            return 2 /* CONTENT_BOX */;
        }
      }
      return 0 /* BORDER_BOX */;
    });
  }
};
var backgroundColor = {
  name: "background-color",
  initialValue: 'transparent',
  prefix: false,
  type: 3 /* TYPE_VALUE */,
  format: 'color'
};
var parseColorStop = function (context, args) {
  var color = color$1.parse(context, args[0]);
  var stop = args[1];
  return stop && isLengthPercentage(stop) ? {
    color: color,
    stop: stop
  } : {
    color: color,
    stop: null
  };
};
var processColorStops = function (stops, lineLength) {
  var first = stops[0];
  var last = stops[stops.length - 1];
  if (first.stop === null) {
    first.stop = ZERO_LENGTH;
  }
  if (last.stop === null) {
    last.stop = HUNDRED_PERCENT;
  }
  var processStops = [];
  var previous = 0;
  for (var i = 0; i < stops.length; i++) {
    var stop_1 = stops[i].stop;
    if (stop_1 !== null) {
      var absoluteValue = getAbsoluteValue(stop_1, lineLength);
      if (absoluteValue > previous) {
        processStops.push(absoluteValue);
      } else {
        processStops.push(previous);
      }
      previous = absoluteValue;
    } else {
      processStops.push(null);
    }
  }
  var gapBegin = null;
  for (var i = 0; i < processStops.length; i++) {
    var stop_2 = processStops[i];
    if (stop_2 === null) {
      if (gapBegin === null) {
        gapBegin = i;
      }
    } else if (gapBegin !== null) {
      var gapLength = i - gapBegin;
      var beforeGap = processStops[gapBegin - 1];
      var gapValue = (stop_2 - beforeGap) / (gapLength + 1);
      for (var g = 1; g <= gapLength; g++) {
        processStops[gapBegin + g - 1] = gapValue * g;
      }
      gapBegin = null;
    }
  }
  return stops.map(function (_a, i) {
    var color = _a.color;
    return {
      color: color,
      stop: Math.max(Math.min(1, processStops[i] / lineLength), 0)
    };
  });
};
var getAngleFromCorner = function (corner, width, height) {
  var centerX = width / 2;
  var centerY = height / 2;
  var x = getAbsoluteValue(corner[0], width) - centerX;
  var y = centerY - getAbsoluteValue(corner[1], height);
  return (Math.atan2(y, x) + Math.PI * 2) % (Math.PI * 2);
};
var calculateGradientDirection = function (angle, width, height) {
  var radian = typeof angle === 'number' ? angle : getAngleFromCorner(angle, width, height);
  var lineLength = Math.abs(width * Math.sin(radian)) + Math.abs(height * Math.cos(radian));
  var halfWidth = width / 2;
  var halfHeight = height / 2;
  var halfLineLength = lineLength / 2;
  var yDiff = Math.sin(radian - Math.PI / 2) * halfLineLength;
  var xDiff = Math.cos(radian - Math.PI / 2) * halfLineLength;
  return [lineLength, halfWidth - xDiff, halfWidth + xDiff, halfHeight - yDiff, halfHeight + yDiff];
};
var distance$1 = function (a, b) {
  return Math.sqrt(a * a + b * b);
};
var findCorner = function (width, height, x, y, closest) {
  var corners = [[0, 0], [0, height], [width, 0], [width, height]];
  return corners.reduce(function (stat, corner) {
    var cx = corner[0],
      cy = corner[1];
    var d = distance$1(x - cx, y - cy);
    if (closest ? d < stat.optimumDistance : d > stat.optimumDistance) {
      return {
        optimumCorner: corner,
        optimumDistance: d
      };
    }
    return stat;
  }, {
    optimumDistance: closest ? Infinity : -Infinity,
    optimumCorner: null
  }).optimumCorner;
};
var calculateRadius = function (gradient, x, y, width, height) {
  var rx = 0;
  var ry = 0;
  switch (gradient.size) {
    case 0 /* CLOSEST_SIDE */:
      // The ending shape is sized so that that it exactly meets the side of the gradient box closest to the gradient’s center.
      // If the shape is an ellipse, it exactly meets the closest side in each dimension.
      if (gradient.shape === 0 /* CIRCLE */) {
        rx = ry = Math.min(Math.abs(x), Math.abs(x - width), Math.abs(y), Math.abs(y - height));
      } else if (gradient.shape === 1 /* ELLIPSE */) {
        rx = Math.min(Math.abs(x), Math.abs(x - width));
        ry = Math.min(Math.abs(y), Math.abs(y - height));
      }
      break;
    case 2 /* CLOSEST_CORNER */:
      // The ending shape is sized so that that it passes through the corner of the gradient box closest to the gradient’s center.
      // If the shape is an ellipse, the ending shape is given the same aspect-ratio it would have if closest-side were specified.
      if (gradient.shape === 0 /* CIRCLE */) {
        rx = ry = Math.min(distance$1(x, y), distance$1(x, y - height), distance$1(x - width, y), distance$1(x - width, y - height));
      } else if (gradient.shape === 1 /* ELLIPSE */) {
        // Compute the ratio ry/rx (which is to be the same as for "closest-side")
        var c = Math.min(Math.abs(y), Math.abs(y - height)) / Math.min(Math.abs(x), Math.abs(x - width));
        var _a = findCorner(width, height, x, y, true),
          cx = _a[0],
          cy = _a[1];
        rx = distance$1(cx - x, (cy - y) / c);
        ry = c * rx;
      }
      break;
    case 1 /* FARTHEST_SIDE */:
      // Same as closest-side, except the ending shape is sized based on the farthest side(s)
      if (gradient.shape === 0 /* CIRCLE */) {
        rx = ry = Math.max(Math.abs(x), Math.abs(x - width), Math.abs(y), Math.abs(y - height));
      } else if (gradient.shape === 1 /* ELLIPSE */) {
        rx = Math.max(Math.abs(x), Math.abs(x - width));
        ry = Math.max(Math.abs(y), Math.abs(y - height));
      }
      break;
    case 3 /* FARTHEST_CORNER */:
      // Same as closest-corner, except the ending shape is sized based on the farthest corner.
      // If the shape is an ellipse, the ending shape is given the same aspect ratio it would have if farthest-side were specified.
      if (gradient.shape === 0 /* CIRCLE */) {
        rx = ry = Math.max(distance$1(x, y), distance$1(x, y - height), distance$1(x - width, y), distance$1(x - width, y - height));
      } else if (gradient.shape === 1 /* ELLIPSE */) {
        // Compute the ratio ry/rx (which is to be the same as for "farthest-side")
        var c = Math.max(Math.abs(y), Math.abs(y - height)) / Math.max(Math.abs(x), Math.abs(x - width));
        var _b = findCorner(width, height, x, y, false),
          cx = _b[0],
          cy = _b[1];
        rx = distance$1(cx - x, (cy - y) / c);
        ry = c * rx;
      }
      break;
  }
  if (Array.isArray(gradient.size)) {
    rx = getAbsoluteValue(gradient.size[0], width);
    ry = gradient.size.length === 2 ? getAbsoluteValue(gradient.size[1], height) : rx;
  }
  return [rx, ry];
};
var linearGradient = function (context, tokens) {
  var angle$1 = deg(180);
  var stops = [];
  parseFunctionArgs(tokens).forEach(function (arg, i) {
    if (i === 0) {
      var firstToken = arg[0];
      if (firstToken.type === 20 /* IDENT_TOKEN */ && firstToken.value === 'to') {
        angle$1 = parseNamedSide(arg);
        return;
      } else if (isAngle(firstToken)) {
        angle$1 = angle.parse(context, firstToken);
        return;
      }
    }
    var colorStop = parseColorStop(context, arg);
    stops.push(colorStop);
  });
  return {
    angle: angle$1,
    stops: stops,
    type: 1 /* LINEAR_GRADIENT */
  };
};
var prefixLinearGradient = function (context, tokens) {
  var angle$1 = deg(180);
  var stops = [];
  parseFunctionArgs(tokens).forEach(function (arg, i) {
    if (i === 0) {
      var firstToken = arg[0];
      if (firstToken.type === 20 /* IDENT_TOKEN */ && ['top', 'left', 'right', 'bottom'].indexOf(firstToken.value) !== -1) {
        angle$1 = parseNamedSide(arg);
        return;
      } else if (isAngle(firstToken)) {
        angle$1 = (angle.parse(context, firstToken) + deg(270)) % deg(360);
        return;
      }
    }
    var colorStop = parseColorStop(context, arg);
    stops.push(colorStop);
  });
  return {
    angle: angle$1,
    stops: stops,
    type: 1 /* LINEAR_GRADIENT */
  };
};
var webkitGradient = function (context, tokens) {
  var angle = deg(180);
  var stops = [];
  var type = 1 /* LINEAR_GRADIENT */;
  var shape = 0 /* CIRCLE */;
  var size = 3 /* FARTHEST_CORNER */;
  var position = [];
  parseFunctionArgs(tokens).forEach(function (arg, i) {
    var firstToken = arg[0];
    if (i === 0) {
      if (isIdentToken(firstToken) && firstToken.value === 'linear') {
        type = 1 /* LINEAR_GRADIENT */;
        return;
      } else if (isIdentToken(firstToken) && firstToken.value === 'radial') {
        type = 2 /* RADIAL_GRADIENT */;
        return;
      }
    }
    if (firstToken.type === 18 /* FUNCTION */) {
      if (firstToken.name === 'from') {
        var color = color$1.parse(context, firstToken.values[0]);
        stops.push({
          stop: ZERO_LENGTH,
          color: color
        });
      } else if (firstToken.name === 'to') {
        var color = color$1.parse(context, firstToken.values[0]);
        stops.push({
          stop: HUNDRED_PERCENT,
          color: color
        });
      } else if (firstToken.name === 'color-stop') {
        var values = firstToken.values.filter(nonFunctionArgSeparator);
        if (values.length === 2) {
          var color = color$1.parse(context, values[1]);
          var stop_1 = values[0];
          if (isNumberToken(stop_1)) {
            stops.push({
              stop: {
                type: 16 /* PERCENTAGE_TOKEN */,
                number: stop_1.number * 100,
                flags: stop_1.flags
              },
              color: color
            });
          }
        }
      }
    }
  });
  return type === 1 /* LINEAR_GRADIENT */ ? {
    angle: (angle + deg(180)) % deg(360),
    stops: stops,
    type: type
  } : {
    size: size,
    shape: shape,
    stops: stops,
    position: position,
    type: type
  };
};
var CLOSEST_SIDE = 'closest-side';
var FARTHEST_SIDE = 'farthest-side';
var CLOSEST_CORNER = 'closest-corner';
var FARTHEST_CORNER = 'farthest-corner';
var CIRCLE = 'circle';
var ELLIPSE = 'ellipse';
var COVER = 'cover';
var CONTAIN = 'contain';
var radialGradient = function (context, tokens) {
  var shape = 0 /* CIRCLE */;
  var size = 3 /* FARTHEST_CORNER */;
  var stops = [];
  var position = [];
  parseFunctionArgs(tokens).forEach(function (arg, i) {
    var isColorStop = true;
    if (i === 0) {
      var isAtPosition_1 = false;
      isColorStop = arg.reduce(function (acc, token) {
        if (isAtPosition_1) {
          if (isIdentToken(token)) {
            switch (token.value) {
              case 'center':
                position.push(FIFTY_PERCENT);
                return acc;
              case 'top':
              case 'left':
                position.push(ZERO_LENGTH);
                return acc;
              case 'right':
              case 'bottom':
                position.push(HUNDRED_PERCENT);
                return acc;
            }
          } else if (isLengthPercentage(token) || isLength(token)) {
            position.push(token);
          }
        } else if (isIdentToken(token)) {
          switch (token.value) {
            case CIRCLE:
              shape = 0 /* CIRCLE */;
              return false;
            case ELLIPSE:
              shape = 1 /* ELLIPSE */;
              return false;
            case 'at':
              isAtPosition_1 = true;
              return false;
            case CLOSEST_SIDE:
              size = 0 /* CLOSEST_SIDE */;
              return false;
            case COVER:
            case FARTHEST_SIDE:
              size = 1 /* FARTHEST_SIDE */;
              return false;
            case CONTAIN:
            case CLOSEST_CORNER:
              size = 2 /* CLOSEST_CORNER */;
              return false;
            case FARTHEST_CORNER:
              size = 3 /* FARTHEST_CORNER */;
              return false;
          }
        } else if (isLength(token) || isLengthPercentage(token)) {
          if (!Array.isArray(size)) {
            size = [];
          }
          size.push(token);
          return false;
        }
        return acc;
      }, isColorStop);
    }
    if (isColorStop) {
      var colorStop = parseColorStop(context, arg);
      stops.push(colorStop);
    }
  });
  return {
    size: size,
    shape: shape,
    stops: stops,
    position: position,
    type: 2 /* RADIAL_GRADIENT */
  };
};
var prefixRadialGradient = function (context, tokens) {
  var shape = 0 /* CIRCLE */;
  var size = 3 /* FARTHEST_CORNER */;
  var stops = [];
  var position = [];
  parseFunctionArgs(tokens).forEach(function (arg, i) {
    var isColorStop = true;
    if (i === 0) {
      isColorStop = arg.reduce(function (acc, token) {
        if (isIdentToken(token)) {
          switch (token.value) {
            case 'center':
              position.push(FIFTY_PERCENT);
              return false;
            case 'top':
            case 'left':
              position.push(ZERO_LENGTH);
              return false;
            case 'right':
            case 'bottom':
              position.push(HUNDRED_PERCENT);
              return false;
          }
        } else if (isLengthPercentage(token) || isLength(token)) {
          position.push(token);
          return false;
        }
        return acc;
      }, isColorStop);
    } else if (i === 1) {
      isColorStop = arg.reduce(function (acc, token) {
        if (isIdentToken(token)) {
          switch (token.value) {
            case CIRCLE:
              shape = 0 /* CIRCLE */;
              return false;
            case ELLIPSE:
              shape = 1 /* ELLIPSE */;
              return false;
            case CONTAIN:
            case CLOSEST_SIDE:
              size = 0 /* CLOSEST_SIDE */;
              return false;
            case FARTHEST_SIDE:
              size = 1 /* FARTHEST_SIDE */;
              return false;
            case CLOSEST_CORNER:
              size = 2 /* CLOSEST_CORNER */;
              return false;
            case COVER:
            case FARTHEST_CORNER:
              size = 3 /* FARTHEST_CORNER */;
              return false;
          }
        } else if (isLength(token) || isLengthPercentage(token)) {
          if (!Array.isArray(size)) {
            size = [];
          }
          size.push(token);
          return false;
        }
        return acc;
      }, isColorStop);
    }
    if (isColorStop) {
      var colorStop = parseColorStop(context, arg);
      stops.push(colorStop);
    }
  });
  return {
    size: size,
    shape: shape,
    stops: stops,
    position: position,
    type: 2 /* RADIAL_GRADIENT */
  };
};
var isLinearGradient = function (background) {
  return background.type === 1 /* LINEAR_GRADIENT */;
};
var isRadialGradient = function (background) {
  return background.type === 2 /* RADIAL_GRADIENT */;
};
var image = {
  name: 'image',
  parse: function (context, value) {
    if (value.type === 22 /* URL_TOKEN */) {
      var image_1 = {
        url: value.value,
        type: 0 /* URL */
      };
      context.cache.addImage(value.value);
      return image_1;
    }
    if (value.type === 18 /* FUNCTION */) {
      var imageFunction = SUPPORTED_IMAGE_FUNCTIONS[value.name];
      if (typeof imageFunction === 'undefined') {
        throw new Error("Attempting to parse an unsupported image function \"" + value.name + "\"");
      }
      return imageFunction(context, value.values);
    }
    throw new Error("Unsupported image type " + value.type);
  }
};
function isSupportedImage(value) {
  return !(value.type === 20 /* IDENT_TOKEN */ && value.value === 'none') && (value.type !== 18 /* FUNCTION */ || !!SUPPORTED_IMAGE_FUNCTIONS[value.name]);
}
var SUPPORTED_IMAGE_FUNCTIONS = {
  'linear-gradient': linearGradient,
  '-moz-linear-gradient': prefixLinearGradient,
  '-ms-linear-gradient': prefixLinearGradient,
  '-o-linear-gradient': prefixLinearGradient,
  '-webkit-linear-gradient': prefixLinearGradient,
  'radial-gradient': radialGradient,
  '-moz-radial-gradient': prefixRadialGradient,
  '-ms-radial-gradient': prefixRadialGradient,
  '-o-radial-gradient': prefixRadialGradient,
  '-webkit-radial-gradient': prefixRadialGradient,
  '-webkit-gradient': webkitGradient
};
var backgroundImage = {
  name: 'background-image',
  initialValue: 'none',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (context, tokens) {
    if (tokens.length === 0) {
      return [];
    }
    var first = tokens[0];
    if (first.type === 20 /* IDENT_TOKEN */ && first.value === 'none') {
      return [];
    }
    return tokens.filter(function (value) {
      return nonFunctionArgSeparator(value) && isSupportedImage(value);
    }).map(function (value) {
      return image.parse(context, value);
    });
  }
};
var backgroundOrigin = {
  name: 'background-origin',
  initialValue: 'border-box',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return tokens.map(function (token) {
      if (isIdentToken(token)) {
        switch (token.value) {
          case 'padding-box':
            return 1 /* PADDING_BOX */;
          case 'content-box':
            return 2 /* CONTENT_BOX */;
        }
      }
      return 0 /* BORDER_BOX */;
    });
  }
};
var backgroundPosition = {
  name: 'background-position',
  initialValue: '0% 0%',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (_context, tokens) {
    return parseFunctionArgs(tokens).map(function (values) {
      return values.filter(isLengthPercentage);
    }).map(parseLengthPercentageTuple);
  }
};
var backgroundRepeat = {
  name: 'background-repeat',
  initialValue: 'repeat',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return parseFunctionArgs(tokens).map(function (values) {
      return values.filter(isIdentToken).map(function (token) {
        return token.value;
      }).join(' ');
    }).map(parseBackgroundRepeat);
  }
};
var parseBackgroundRepeat = function (value) {
  switch (value) {
    case 'no-repeat':
      return 1 /* NO_REPEAT */;
    case 'repeat-x':
    case 'repeat no-repeat':
      return 2 /* REPEAT_X */;
    case 'repeat-y':
    case 'no-repeat repeat':
      return 3 /* REPEAT_Y */;
    case 'repeat':
    default:
      return 0 /* REPEAT */;
  }
};
var BACKGROUND_SIZE;
(function (BACKGROUND_SIZE) {
  BACKGROUND_SIZE["AUTO"] = "auto";
  BACKGROUND_SIZE["CONTAIN"] = "contain";
  BACKGROUND_SIZE["COVER"] = "cover";
})(BACKGROUND_SIZE || (BACKGROUND_SIZE = {}));
var backgroundSize = {
  name: 'background-size',
  initialValue: '0',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return parseFunctionArgs(tokens).map(function (values) {
      return values.filter(isBackgroundSizeInfoToken);
    });
  }
};
var isBackgroundSizeInfoToken = function (value) {
  return isIdentToken(value) || isLengthPercentage(value);
};
var borderColorForSide = function (side) {
  return {
    name: "border-" + side + "-color",
    initialValue: 'transparent',
    prefix: false,
    type: 3 /* TYPE_VALUE */,
    format: 'color'
  };
};
var borderTopColor = borderColorForSide('top');
var borderRightColor = borderColorForSide('right');
var borderBottomColor = borderColorForSide('bottom');
var borderLeftColor = borderColorForSide('left');
var borderRadiusForSide = function (side) {
  return {
    name: "border-radius-" + side,
    initialValue: '0 0',
    prefix: false,
    type: 1 /* LIST */,
    parse: function (_context, tokens) {
      return parseLengthPercentageTuple(tokens.filter(isLengthPercentage));
    }
  };
};
var borderTopLeftRadius = borderRadiusForSide('top-left');
var borderTopRightRadius = borderRadiusForSide('top-right');
var borderBottomRightRadius = borderRadiusForSide('bottom-right');
var borderBottomLeftRadius = borderRadiusForSide('bottom-left');
var borderStyleForSide = function (side) {
  return {
    name: "border-" + side + "-style",
    initialValue: 'solid',
    prefix: false,
    type: 2 /* IDENT_VALUE */,
    parse: function (_context, style) {
      switch (style) {
        case 'none':
          return 0 /* NONE */;
        case 'dashed':
          return 2 /* DASHED */;
        case 'dotted':
          return 3 /* DOTTED */;
        case 'double':
          return 4 /* DOUBLE */;
      }
      return 1 /* SOLID */;
    }
  };
};
var borderTopStyle = borderStyleForSide('top');
var borderRightStyle = borderStyleForSide('right');
var borderBottomStyle = borderStyleForSide('bottom');
var borderLeftStyle = borderStyleForSide('left');
var borderWidthForSide = function (side) {
  return {
    name: "border-" + side + "-width",
    initialValue: '0',
    type: 0 /* VALUE */,
    prefix: false,
    parse: function (_context, token) {
      if (isDimensionToken(token)) {
        return token.number;
      }
      return 0;
    }
  };
};
var borderTopWidth = borderWidthForSide('top');
var borderRightWidth = borderWidthForSide('right');
var borderBottomWidth = borderWidthForSide('bottom');
var borderLeftWidth = borderWidthForSide('left');
var color$2 = {
  name: "color",
  initialValue: 'transparent',
  prefix: false,
  type: 3 /* TYPE_VALUE */,
  format: 'color'
};
var direction = {
  name: 'direction',
  initialValue: 'ltr',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, direction) {
    switch (direction) {
      case 'rtl':
        return 1 /* RTL */;
      case 'ltr':
      default:
        return 0 /* LTR */;
    }
  }
};
var display = {
  name: 'display',
  initialValue: 'inline-block',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return tokens.filter(isIdentToken).reduce(function (bit, token) {
      return bit | parseDisplayValue(token.value);
    }, 0 /* NONE */);
  }
};
var parseDisplayValue = function (display) {
  switch (display) {
    case 'block':
    case '-webkit-box':
      return 2 /* BLOCK */;
    case 'inline':
      return 4 /* INLINE */;
    case 'run-in':
      return 8 /* RUN_IN */;
    case 'flow':
      return 16 /* FLOW */;
    case 'flow-root':
      return 32 /* FLOW_ROOT */;
    case 'table':
      return 64 /* TABLE */;
    case 'flex':
    case '-webkit-flex':
      return 128 /* FLEX */;
    case 'grid':
    case '-ms-grid':
      return 256 /* GRID */;
    case 'ruby':
      return 512 /* RUBY */;
    case 'subgrid':
      return 1024 /* SUBGRID */;
    case 'list-item':
      return 2048 /* LIST_ITEM */;
    case 'table-row-group':
      return 4096 /* TABLE_ROW_GROUP */;
    case 'table-header-group':
      return 8192 /* TABLE_HEADER_GROUP */;
    case 'table-footer-group':
      return 16384 /* TABLE_FOOTER_GROUP */;
    case 'table-row':
      return 32768 /* TABLE_ROW */;
    case 'table-cell':
      return 65536 /* TABLE_CELL */;
    case 'table-column-group':
      return 131072 /* TABLE_COLUMN_GROUP */;
    case 'table-column':
      return 262144 /* TABLE_COLUMN */;
    case 'table-caption':
      return 524288 /* TABLE_CAPTION */;
    case 'ruby-base':
      return 1048576 /* RUBY_BASE */;
    case 'ruby-text':
      return 2097152 /* RUBY_TEXT */;
    case 'ruby-base-container':
      return 4194304 /* RUBY_BASE_CONTAINER */;
    case 'ruby-text-container':
      return 8388608 /* RUBY_TEXT_CONTAINER */;
    case 'contents':
      return 16777216 /* CONTENTS */;
    case 'inline-block':
      return 33554432 /* INLINE_BLOCK */;
    case 'inline-list-item':
      return 67108864 /* INLINE_LIST_ITEM */;
    case 'inline-table':
      return 134217728 /* INLINE_TABLE */;
    case 'inline-flex':
      return 268435456 /* INLINE_FLEX */;
    case 'inline-grid':
      return 536870912 /* INLINE_GRID */;
  }
  return 0 /* NONE */;
};
var float$1 = {
  name: 'float',
  initialValue: 'none',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, float) {
    switch (float) {
      case 'left':
        return 1 /* LEFT */;
      case 'right':
        return 2 /* RIGHT */;
      case 'inline-start':
        return 3 /* INLINE_START */;
      case 'inline-end':
        return 4 /* INLINE_END */;
    }
    return 0 /* NONE */;
  }
};
var letterSpacing = {
  name: 'letter-spacing',
  initialValue: '0',
  prefix: false,
  type: 0 /* VALUE */,
  parse: function (_context, token) {
    if (token.type === 20 /* IDENT_TOKEN */ && token.value === 'normal') {
      return 0;
    }
    if (token.type === 17 /* NUMBER_TOKEN */) {
      return token.number;
    }
    if (token.type === 15 /* DIMENSION_TOKEN */) {
      return token.number;
    }
    return 0;
  }
};
var LINE_BREAK;
(function (LINE_BREAK) {
  LINE_BREAK["NORMAL"] = "normal";
  LINE_BREAK["STRICT"] = "strict";
})(LINE_BREAK || (LINE_BREAK = {}));
var lineBreak = {
  name: 'line-break',
  initialValue: 'normal',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, lineBreak) {
    switch (lineBreak) {
      case 'strict':
        return LINE_BREAK.STRICT;
      case 'normal':
      default:
        return LINE_BREAK.NORMAL;
    }
  }
};
var lineHeight = {
  name: 'line-height',
  initialValue: 'normal',
  prefix: false,
  type: 4 /* TOKEN_VALUE */
};
var computeLineHeight = function (token, fontSize) {
  if (isIdentToken(token) && token.value === 'normal') {
    return 1.2 * fontSize;
  } else if (token.type === 17 /* NUMBER_TOKEN */) {
    return fontSize * token.number;
  } else if (isLengthPercentage(token)) {
    return getAbsoluteValue(token, fontSize);
  }
  return fontSize;
};
var listStyleImage = {
  name: 'list-style-image',
  initialValue: 'none',
  type: 0 /* VALUE */,
  prefix: false,
  parse: function (context, token) {
    if (token.type === 20 /* IDENT_TOKEN */ && token.value === 'none') {
      return null;
    }
    return image.parse(context, token);
  }
};
var listStylePosition = {
  name: 'list-style-position',
  initialValue: 'outside',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, position) {
    switch (position) {
      case 'inside':
        return 0 /* INSIDE */;
      case 'outside':
      default:
        return 1 /* OUTSIDE */;
    }
  }
};
var listStyleType = {
  name: 'list-style-type',
  initialValue: 'none',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, type) {
    switch (type) {
      case 'disc':
        return 0 /* DISC */;
      case 'circle':
        return 1 /* CIRCLE */;
      case 'square':
        return 2 /* SQUARE */;
      case 'decimal':
        return 3 /* DECIMAL */;
      case 'cjk-decimal':
        return 4 /* CJK_DECIMAL */;
      case 'decimal-leading-zero':
        return 5 /* DECIMAL_LEADING_ZERO */;
      case 'lower-roman':
        return 6 /* LOWER_ROMAN */;
      case 'upper-roman':
        return 7 /* UPPER_ROMAN */;
      case 'lower-greek':
        return 8 /* LOWER_GREEK */;
      case 'lower-alpha':
        return 9 /* LOWER_ALPHA */;
      case 'upper-alpha':
        return 10 /* UPPER_ALPHA */;
      case 'arabic-indic':
        return 11 /* ARABIC_INDIC */;
      case 'armenian':
        return 12 /* ARMENIAN */;
      case 'bengali':
        return 13 /* BENGALI */;
      case 'cambodian':
        return 14 /* CAMBODIAN */;
      case 'cjk-earthly-branch':
        return 15 /* CJK_EARTHLY_BRANCH */;
      case 'cjk-heavenly-stem':
        return 16 /* CJK_HEAVENLY_STEM */;
      case 'cjk-ideographic':
        return 17 /* CJK_IDEOGRAPHIC */;
      case 'devanagari':
        return 18 /* DEVANAGARI */;
      case 'ethiopic-numeric':
        return 19 /* ETHIOPIC_NUMERIC */;
      case 'georgian':
        return 20 /* GEORGIAN */;
      case 'gujarati':
        return 21 /* GUJARATI */;
      case 'gurmukhi':
        return 22 /* GURMUKHI */;
      case 'hebrew':
        return 22 /* HEBREW */;
      case 'hiragana':
        return 23 /* HIRAGANA */;
      case 'hiragana-iroha':
        return 24 /* HIRAGANA_IROHA */;
      case 'japanese-formal':
        return 25 /* JAPANESE_FORMAL */;
      case 'japanese-informal':
        return 26 /* JAPANESE_INFORMAL */;
      case 'kannada':
        return 27 /* KANNADA */;
      case 'katakana':
        return 28 /* KATAKANA */;
      case 'katakana-iroha':
        return 29 /* KATAKANA_IROHA */;
      case 'khmer':
        return 30 /* KHMER */;
      case 'korean-hangul-formal':
        return 31 /* KOREAN_HANGUL_FORMAL */;
      case 'korean-hanja-formal':
        return 32 /* KOREAN_HANJA_FORMAL */;
      case 'korean-hanja-informal':
        return 33 /* KOREAN_HANJA_INFORMAL */;
      case 'lao':
        return 34 /* LAO */;
      case 'lower-armenian':
        return 35 /* LOWER_ARMENIAN */;
      case 'malayalam':
        return 36 /* MALAYALAM */;
      case 'mongolian':
        return 37 /* MONGOLIAN */;
      case 'myanmar':
        return 38 /* MYANMAR */;
      case 'oriya':
        return 39 /* ORIYA */;
      case 'persian':
        return 40 /* PERSIAN */;
      case 'simp-chinese-formal':
        return 41 /* SIMP_CHINESE_FORMAL */;
      case 'simp-chinese-informal':
        return 42 /* SIMP_CHINESE_INFORMAL */;
      case 'tamil':
        return 43 /* TAMIL */;
      case 'telugu':
        return 44 /* TELUGU */;
      case 'thai':
        return 45 /* THAI */;
      case 'tibetan':
        return 46 /* TIBETAN */;
      case 'trad-chinese-formal':
        return 47 /* TRAD_CHINESE_FORMAL */;
      case 'trad-chinese-informal':
        return 48 /* TRAD_CHINESE_INFORMAL */;
      case 'upper-armenian':
        return 49 /* UPPER_ARMENIAN */;
      case 'disclosure-open':
        return 50 /* DISCLOSURE_OPEN */;
      case 'disclosure-closed':
        return 51 /* DISCLOSURE_CLOSED */;
      case 'none':
      default:
        return -1 /* NONE */;
    }
  }
};
var marginForSide = function (side) {
  return {
    name: "margin-" + side,
    initialValue: '0',
    prefix: false,
    type: 4 /* TOKEN_VALUE */
  };
};
var marginTop = marginForSide('top');
var marginRight = marginForSide('right');
var marginBottom = marginForSide('bottom');
var marginLeft = marginForSide('left');
var overflow = {
  name: 'overflow',
  initialValue: 'visible',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return tokens.filter(isIdentToken).map(function (overflow) {
      switch (overflow.value) {
        case 'hidden':
          return 1 /* HIDDEN */;
        case 'scroll':
          return 2 /* SCROLL */;
        case 'clip':
          return 3 /* CLIP */;
        case 'auto':
          return 4 /* AUTO */;
        case 'visible':
        default:
          return 0 /* VISIBLE */;
      }
    });
  }
};
var overflowWrap = {
  name: 'overflow-wrap',
  initialValue: 'normal',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, overflow) {
    switch (overflow) {
      case 'break-word':
        return "break-word" /* BREAK_WORD */;
      case 'normal':
      default:
        return "normal" /* NORMAL */;
    }
  }
};
var paddingForSide = function (side) {
  return {
    name: "padding-" + side,
    initialValue: '0',
    prefix: false,
    type: 3 /* TYPE_VALUE */,
    format: 'length-percentage'
  };
};
var paddingTop = paddingForSide('top');
var paddingRight = paddingForSide('right');
var paddingBottom = paddingForSide('bottom');
var paddingLeft = paddingForSide('left');
var textAlign = {
  name: 'text-align',
  initialValue: 'left',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, textAlign) {
    switch (textAlign) {
      case 'right':
        return 2 /* RIGHT */;
      case 'center':
      case 'justify':
        return 1 /* CENTER */;
      case 'left':
      default:
        return 0 /* LEFT */;
    }
  }
};
var position = {
  name: 'position',
  initialValue: 'static',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, position) {
    switch (position) {
      case 'relative':
        return 1 /* RELATIVE */;
      case 'absolute':
        return 2 /* ABSOLUTE */;
      case 'fixed':
        return 3 /* FIXED */;
      case 'sticky':
        return 4 /* STICKY */;
    }
    return 0 /* STATIC */;
  }
};
var textShadow = {
  name: 'text-shadow',
  initialValue: 'none',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (context, tokens) {
    if (tokens.length === 1 && isIdentWithValue(tokens[0], 'none')) {
      return [];
    }
    return parseFunctionArgs(tokens).map(function (values) {
      var shadow = {
        color: COLORS.TRANSPARENT,
        offsetX: ZERO_LENGTH,
        offsetY: ZERO_LENGTH,
        blur: ZERO_LENGTH
      };
      var c = 0;
      for (var i = 0; i < values.length; i++) {
        var token = values[i];
        if (isLength(token)) {
          if (c === 0) {
            shadow.offsetX = token;
          } else if (c === 1) {
            shadow.offsetY = token;
          } else {
            shadow.blur = token;
          }
          c++;
        } else {
          shadow.color = color$1.parse(context, token);
        }
      }
      return shadow;
    });
  }
};
var textTransform = {
  name: 'text-transform',
  initialValue: 'none',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, textTransform) {
    switch (textTransform) {
      case 'uppercase':
        return 2 /* UPPERCASE */;
      case 'lowercase':
        return 1 /* LOWERCASE */;
      case 'capitalize':
        return 3 /* CAPITALIZE */;
    }
    return 0 /* NONE */;
  }
};
var transform$1 = {
  name: 'transform',
  initialValue: 'none',
  prefix: true,
  type: 0 /* VALUE */,
  parse: function (_context, token) {
    if (token.type === 20 /* IDENT_TOKEN */ && token.value === 'none') {
      return null;
    }
    if (token.type === 18 /* FUNCTION */) {
      var transformFunction = SUPPORTED_TRANSFORM_FUNCTIONS[token.name];
      if (typeof transformFunction === 'undefined') {
        throw new Error("Attempting to parse an unsupported transform function \"" + token.name + "\"");
      }
      return transformFunction(token.values);
    }
    return null;
  }
};
var matrix = function (args) {
  var values = args.filter(function (arg) {
    return arg.type === 17 /* NUMBER_TOKEN */;
  }).map(function (arg) {
    return arg.number;
  });
  return values.length === 6 ? values : null;
};
// doesn't support 3D transforms at the moment
var matrix3d = function (args) {
  var values = args.filter(function (arg) {
    return arg.type === 17 /* NUMBER_TOKEN */;
  }).map(function (arg) {
    return arg.number;
  });
  var a1 = values[0],
    b1 = values[1];
  values[2];
  values[3];
  var a2 = values[4],
    b2 = values[5];
  values[6];
  values[7];
  values[8];
  values[9];
  values[10];
  values[11];
  var a4 = values[12],
    b4 = values[13];
  values[14];
  values[15];
  return values.length === 16 ? [a1, b1, a2, b2, a4, b4] : null;
};
var SUPPORTED_TRANSFORM_FUNCTIONS = {
  matrix: matrix,
  matrix3d: matrix3d
};
var DEFAULT_VALUE = {
  type: 16 /* PERCENTAGE_TOKEN */,
  number: 50,
  flags: FLAG_INTEGER
};
var DEFAULT = [DEFAULT_VALUE, DEFAULT_VALUE];
var transformOrigin = {
  name: 'transform-origin',
  initialValue: '50% 50%',
  prefix: true,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    var origins = tokens.filter(isLengthPercentage);
    if (origins.length !== 2) {
      return DEFAULT;
    }
    return [origins[0], origins[1]];
  }
};
var visibility = {
  name: 'visible',
  initialValue: 'none',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, visibility) {
    switch (visibility) {
      case 'hidden':
        return 1 /* HIDDEN */;
      case 'collapse':
        return 2 /* COLLAPSE */;
      case 'visible':
      default:
        return 0 /* VISIBLE */;
    }
  }
};
var WORD_BREAK;
(function (WORD_BREAK) {
  WORD_BREAK["NORMAL"] = "normal";
  WORD_BREAK["BREAK_ALL"] = "break-all";
  WORD_BREAK["KEEP_ALL"] = "keep-all";
})(WORD_BREAK || (WORD_BREAK = {}));
var wordBreak = {
  name: 'word-break',
  initialValue: 'normal',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, wordBreak) {
    switch (wordBreak) {
      case 'break-all':
        return WORD_BREAK.BREAK_ALL;
      case 'keep-all':
        return WORD_BREAK.KEEP_ALL;
      case 'normal':
      default:
        return WORD_BREAK.NORMAL;
    }
  }
};
var zIndex = {
  name: 'z-index',
  initialValue: 'auto',
  prefix: false,
  type: 0 /* VALUE */,
  parse: function (_context, token) {
    if (token.type === 20 /* IDENT_TOKEN */) {
      return {
        auto: true,
        order: 0
      };
    }
    if (isNumberToken(token)) {
      return {
        auto: false,
        order: token.number
      };
    }
    throw new Error("Invalid z-index number parsed");
  }
};
var time = {
  name: 'time',
  parse: function (_context, value) {
    if (value.type === 15 /* DIMENSION_TOKEN */) {
      switch (value.unit.toLowerCase()) {
        case 's':
          return 1000 * value.number;
        case 'ms':
          return value.number;
      }
    }
    throw new Error("Unsupported time type");
  }
};
var opacity = {
  name: 'opacity',
  initialValue: '1',
  type: 0 /* VALUE */,
  prefix: false,
  parse: function (_context, token) {
    if (isNumberToken(token)) {
      return token.number;
    }
    return 1;
  }
};
var textDecorationColor = {
  name: "text-decoration-color",
  initialValue: 'transparent',
  prefix: false,
  type: 3 /* TYPE_VALUE */,
  format: 'color'
};
var textDecorationLine = {
  name: 'text-decoration-line',
  initialValue: 'none',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    return tokens.filter(isIdentToken).map(function (token) {
      switch (token.value) {
        case 'underline':
          return 1 /* UNDERLINE */;
        case 'overline':
          return 2 /* OVERLINE */;
        case 'line-through':
          return 3 /* LINE_THROUGH */;
        case 'none':
          return 4 /* BLINK */;
      }
      return 0 /* NONE */;
    }).filter(function (line) {
      return line !== 0 /* NONE */;
    });
  }
};
var fontFamily = {
  name: "font-family",
  initialValue: '',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    var accumulator = [];
    var results = [];
    tokens.forEach(function (token) {
      switch (token.type) {
        case 20 /* IDENT_TOKEN */:
        case 0 /* STRING_TOKEN */:
          accumulator.push(token.value);
          break;
        case 17 /* NUMBER_TOKEN */:
          accumulator.push(token.number.toString());
          break;
        case 4 /* COMMA_TOKEN */:
          results.push(accumulator.join(' '));
          accumulator.length = 0;
          break;
      }
    });
    if (accumulator.length) {
      results.push(accumulator.join(' '));
    }
    return results.map(function (result) {
      return result.indexOf(' ') === -1 ? result : "'" + result + "'";
    });
  }
};
var fontSize = {
  name: "font-size",
  initialValue: '0',
  prefix: false,
  type: 3 /* TYPE_VALUE */,
  format: 'length'
};
var fontWeight = {
  name: 'font-weight',
  initialValue: 'normal',
  type: 0 /* VALUE */,
  prefix: false,
  parse: function (_context, token) {
    if (isNumberToken(token)) {
      return token.number;
    }
    if (isIdentToken(token)) {
      switch (token.value) {
        case 'bold':
          return 700;
        case 'normal':
        default:
          return 400;
      }
    }
    return 400;
  }
};
var fontVariant = {
  name: 'font-variant',
  initialValue: 'none',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (_context, tokens) {
    return tokens.filter(isIdentToken).map(function (token) {
      return token.value;
    });
  }
};
var fontStyle = {
  name: 'font-style',
  initialValue: 'normal',
  prefix: false,
  type: 2 /* IDENT_VALUE */,
  parse: function (_context, overflow) {
    switch (overflow) {
      case 'oblique':
        return "oblique" /* OBLIQUE */;
      case 'italic':
        return "italic" /* ITALIC */;
      case 'normal':
      default:
        return "normal" /* NORMAL */;
    }
  }
};
var contains = function (bit, value) {
  return (bit & value) !== 0;
};
var content = {
  name: 'content',
  initialValue: 'none',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (_context, tokens) {
    if (tokens.length === 0) {
      return [];
    }
    var first = tokens[0];
    if (first.type === 20 /* IDENT_TOKEN */ && first.value === 'none') {
      return [];
    }
    return tokens;
  }
};
var counterIncrement = {
  name: 'counter-increment',
  initialValue: 'none',
  prefix: true,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    if (tokens.length === 0) {
      return null;
    }
    var first = tokens[0];
    if (first.type === 20 /* IDENT_TOKEN */ && first.value === 'none') {
      return null;
    }
    var increments = [];
    var filtered = tokens.filter(nonWhiteSpace);
    for (var i = 0; i < filtered.length; i++) {
      var counter = filtered[i];
      var next = filtered[i + 1];
      if (counter.type === 20 /* IDENT_TOKEN */) {
        var increment = next && isNumberToken(next) ? next.number : 1;
        increments.push({
          counter: counter.value,
          increment: increment
        });
      }
    }
    return increments;
  }
};
var counterReset = {
  name: 'counter-reset',
  initialValue: 'none',
  prefix: true,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    if (tokens.length === 0) {
      return [];
    }
    var resets = [];
    var filtered = tokens.filter(nonWhiteSpace);
    for (var i = 0; i < filtered.length; i++) {
      var counter = filtered[i];
      var next = filtered[i + 1];
      if (isIdentToken(counter) && counter.value !== 'none') {
        var reset = next && isNumberToken(next) ? next.number : 0;
        resets.push({
          counter: counter.value,
          reset: reset
        });
      }
    }
    return resets;
  }
};
var duration = {
  name: 'duration',
  initialValue: '0s',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (context, tokens) {
    return tokens.filter(isDimensionToken).map(function (token) {
      return time.parse(context, token);
    });
  }
};
var quotes = {
  name: 'quotes',
  initialValue: 'none',
  prefix: true,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    if (tokens.length === 0) {
      return null;
    }
    var first = tokens[0];
    if (first.type === 20 /* IDENT_TOKEN */ && first.value === 'none') {
      return null;
    }
    var quotes = [];
    var filtered = tokens.filter(isStringToken);
    if (filtered.length % 2 !== 0) {
      return null;
    }
    for (var i = 0; i < filtered.length; i += 2) {
      var open_1 = filtered[i].value;
      var close_1 = filtered[i + 1].value;
      quotes.push({
        open: open_1,
        close: close_1
      });
    }
    return quotes;
  }
};
var getQuote = function (quotes, depth, open) {
  if (!quotes) {
    return '';
  }
  var quote = quotes[Math.min(depth, quotes.length - 1)];
  if (!quote) {
    return '';
  }
  return open ? quote.open : quote.close;
};
var boxShadow = {
  name: 'box-shadow',
  initialValue: 'none',
  type: 1 /* LIST */,
  prefix: false,
  parse: function (context, tokens) {
    if (tokens.length === 1 && isIdentWithValue(tokens[0], 'none')) {
      return [];
    }
    return parseFunctionArgs(tokens).map(function (values) {
      var shadow = {
        color: 0x000000ff,
        offsetX: ZERO_LENGTH,
        offsetY: ZERO_LENGTH,
        blur: ZERO_LENGTH,
        spread: ZERO_LENGTH,
        inset: false
      };
      var c = 0;
      for (var i = 0; i < values.length; i++) {
        var token = values[i];
        if (isIdentWithValue(token, 'inset')) {
          shadow.inset = true;
        } else if (isLength(token)) {
          if (c === 0) {
            shadow.offsetX = token;
          } else if (c === 1) {
            shadow.offsetY = token;
          } else if (c === 2) {
            shadow.blur = token;
          } else {
            shadow.spread = token;
          }
          c++;
        } else {
          shadow.color = color$1.parse(context, token);
        }
      }
      return shadow;
    });
  }
};
var paintOrder = {
  name: 'paint-order',
  initialValue: 'normal',
  prefix: false,
  type: 1 /* LIST */,
  parse: function (_context, tokens) {
    var DEFAULT_VALUE = [0 /* FILL */, 1 /* STROKE */, 2 /* MARKERS */];
    var layers = [];
    tokens.filter(isIdentToken).forEach(function (token) {
      switch (token.value) {
        case 'stroke':
          layers.push(1 /* STROKE */);
          break;
        case 'fill':
          layers.push(0 /* FILL */);
          break;
        case 'markers':
          layers.push(2 /* MARKERS */);
          break;
      }
    });
    DEFAULT_VALUE.forEach(function (value) {
      if (layers.indexOf(value) === -1) {
        layers.push(value);
      }
    });
    return layers;
  }
};
var webkitTextStrokeColor = {
  name: "-webkit-text-stroke-color",
  initialValue: 'currentcolor',
  prefix: false,
  type: 3 /* TYPE_VALUE */,
  format: 'color'
};
var webkitTextStrokeWidth = {
  name: "-webkit-text-stroke-width",
  initialValue: '0',
  type: 0 /* VALUE */,
  prefix: false,
  parse: function (_context, token) {
    if (isDimensionToken(token)) {
      return token.number;
    }
    return 0;
  }
};
var CSSParsedDeclaration = /** @class */function () {
  function CSSParsedDeclaration(context, declaration) {
    var _a, _b;
    this.animationDuration = parse(context, duration, declaration.animationDuration);
    this.backgroundClip = parse(context, backgroundClip, declaration.backgroundClip);
    this.backgroundColor = parse(context, backgroundColor, declaration.backgroundColor);
    this.backgroundImage = parse(context, backgroundImage, declaration.backgroundImage);
    this.backgroundOrigin = parse(context, backgroundOrigin, declaration.backgroundOrigin);
    this.backgroundPosition = parse(context, backgroundPosition, declaration.backgroundPosition);
    this.backgroundRepeat = parse(context, backgroundRepeat, declaration.backgroundRepeat);
    this.backgroundSize = parse(context, backgroundSize, declaration.backgroundSize);
    this.borderTopColor = parse(context, borderTopColor, declaration.borderTopColor);
    this.borderRightColor = parse(context, borderRightColor, declaration.borderRightColor);
    this.borderBottomColor = parse(context, borderBottomColor, declaration.borderBottomColor);
    this.borderLeftColor = parse(context, borderLeftColor, declaration.borderLeftColor);
    this.borderTopLeftRadius = parse(context, borderTopLeftRadius, declaration.borderTopLeftRadius);
    this.borderTopRightRadius = parse(context, borderTopRightRadius, declaration.borderTopRightRadius);
    this.borderBottomRightRadius = parse(context, borderBottomRightRadius, declaration.borderBottomRightRadius);
    this.borderBottomLeftRadius = parse(context, borderBottomLeftRadius, declaration.borderBottomLeftRadius);
    this.borderTopStyle = parse(context, borderTopStyle, declaration.borderTopStyle);
    this.borderRightStyle = parse(context, borderRightStyle, declaration.borderRightStyle);
    this.borderBottomStyle = parse(context, borderBottomStyle, declaration.borderBottomStyle);
    this.borderLeftStyle = parse(context, borderLeftStyle, declaration.borderLeftStyle);
    this.borderTopWidth = parse(context, borderTopWidth, declaration.borderTopWidth);
    this.borderRightWidth = parse(context, borderRightWidth, declaration.borderRightWidth);
    this.borderBottomWidth = parse(context, borderBottomWidth, declaration.borderBottomWidth);
    this.borderLeftWidth = parse(context, borderLeftWidth, declaration.borderLeftWidth);
    this.boxShadow = parse(context, boxShadow, declaration.boxShadow);
    this.color = parse(context, color$2, declaration.color);
    this.direction = parse(context, direction, declaration.direction);
    this.display = parse(context, display, declaration.display);
    this.float = parse(context, float$1, declaration.cssFloat);
    this.fontFamily = parse(context, fontFamily, declaration.fontFamily);
    this.fontSize = parse(context, fontSize, declaration.fontSize);
    this.fontStyle = parse(context, fontStyle, declaration.fontStyle);
    this.fontVariant = parse(context, fontVariant, declaration.fontVariant);
    this.fontWeight = parse(context, fontWeight, declaration.fontWeight);
    this.letterSpacing = parse(context, letterSpacing, declaration.letterSpacing);
    this.lineBreak = parse(context, lineBreak, declaration.lineBreak);
    this.lineHeight = parse(context, lineHeight, declaration.lineHeight);
    this.listStyleImage = parse(context, listStyleImage, declaration.listStyleImage);
    this.listStylePosition = parse(context, listStylePosition, declaration.listStylePosition);
    this.listStyleType = parse(context, listStyleType, declaration.listStyleType);
    this.marginTop = parse(context, marginTop, declaration.marginTop);
    this.marginRight = parse(context, marginRight, declaration.marginRight);
    this.marginBottom = parse(context, marginBottom, declaration.marginBottom);
    this.marginLeft = parse(context, marginLeft, declaration.marginLeft);
    this.opacity = parse(context, opacity, declaration.opacity);
    var overflowTuple = parse(context, overflow, declaration.overflow);
    this.overflowX = overflowTuple[0];
    this.overflowY = overflowTuple[overflowTuple.length > 1 ? 1 : 0];
    this.overflowWrap = parse(context, overflowWrap, declaration.overflowWrap);
    this.paddingTop = parse(context, paddingTop, declaration.paddingTop);
    this.paddingRight = parse(context, paddingRight, declaration.paddingRight);
    this.paddingBottom = parse(context, paddingBottom, declaration.paddingBottom);
    this.paddingLeft = parse(context, paddingLeft, declaration.paddingLeft);
    this.paintOrder = parse(context, paintOrder, declaration.paintOrder);
    this.position = parse(context, position, declaration.position);
    this.textAlign = parse(context, textAlign, declaration.textAlign);
    this.textDecorationColor = parse(context, textDecorationColor, (_a = declaration.textDecorationColor) !== null && _a !== void 0 ? _a : declaration.color);
    this.textDecorationLine = parse(context, textDecorationLine, (_b = declaration.textDecorationLine) !== null && _b !== void 0 ? _b : declaration.textDecoration);
    this.textShadow = parse(context, textShadow, declaration.textShadow);
    this.textTransform = parse(context, textTransform, declaration.textTransform);
    this.transform = parse(context, transform$1, declaration.transform);
    this.transformOrigin = parse(context, transformOrigin, declaration.transformOrigin);
    this.visibility = parse(context, visibility, declaration.visibility);
    this.webkitTextStrokeColor = parse(context, webkitTextStrokeColor, declaration.webkitTextStrokeColor);
    this.webkitTextStrokeWidth = parse(context, webkitTextStrokeWidth, declaration.webkitTextStrokeWidth);
    this.wordBreak = parse(context, wordBreak, declaration.wordBreak);
    this.zIndex = parse(context, zIndex, declaration.zIndex);
  }
  CSSParsedDeclaration.prototype.isVisible = function () {
    return this.display > 0 && this.opacity > 0 && this.visibility === 0 /* VISIBLE */;
  };
  CSSParsedDeclaration.prototype.isTransparent = function () {
    return isTransparent(this.backgroundColor);
  };
  CSSParsedDeclaration.prototype.isTransformed = function () {
    return this.transform !== null;
  };
  CSSParsedDeclaration.prototype.isPositioned = function () {
    return this.position !== 0 /* STATIC */;
  };
  CSSParsedDeclaration.prototype.isPositionedWithZIndex = function () {
    return this.isPositioned() && !this.zIndex.auto;
  };
  CSSParsedDeclaration.prototype.isFloating = function () {
    return this.float !== 0 /* NONE */;
  };
  CSSParsedDeclaration.prototype.isInlineLevel = function () {
    return contains(this.display, 4 /* INLINE */) || contains(this.display, 33554432 /* INLINE_BLOCK */) || contains(this.display, 268435456 /* INLINE_FLEX */) || contains(this.display, 536870912 /* INLINE_GRID */) || contains(this.display, 67108864 /* INLINE_LIST_ITEM */) || contains(this.display, 134217728 /* INLINE_TABLE */);
  };
  return CSSParsedDeclaration;
}();
var CSSParsedPseudoDeclaration = /** @class */function () {
  function CSSParsedPseudoDeclaration(context, declaration) {
    this.content = parse(context, content, declaration.content);
    this.quotes = parse(context, quotes, declaration.quotes);
  }
  return CSSParsedPseudoDeclaration;
}();
var CSSParsedCounterDeclaration = /** @class */function () {
  function CSSParsedCounterDeclaration(context, declaration) {
    this.counterIncrement = parse(context, counterIncrement, declaration.counterIncrement);
    this.counterReset = parse(context, counterReset, declaration.counterReset);
  }
  return CSSParsedCounterDeclaration;
}();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var parse = function (context, descriptor, style) {
  var tokenizer = new Tokenizer();
  var value = style !== null && typeof style !== 'undefined' ? style.toString() : descriptor.initialValue;
  tokenizer.write(value);
  var parser = new Parser(tokenizer.read());
  switch (descriptor.type) {
    case 2 /* IDENT_VALUE */:
      var token = parser.parseComponentValue();
      return descriptor.parse(context, isIdentToken(token) ? token.value : descriptor.initialValue);
    case 0 /* VALUE */:
      return descriptor.parse(context, parser.parseComponentValue());
    case 1 /* LIST */:
      return descriptor.parse(context, parser.parseComponentValues());
    case 4 /* TOKEN_VALUE */:
      return parser.parseComponentValue();
    case 3 /* TYPE_VALUE */:
      switch (descriptor.format) {
        case 'angle':
          return angle.parse(context, parser.parseComponentValue());
        case 'color':
          return color$1.parse(context, parser.parseComponentValue());
        case 'image':
          return image.parse(context, parser.parseComponentValue());
        case 'length':
          var length_1 = parser.parseComponentValue();
          return isLength(length_1) ? length_1 : ZERO_LENGTH;
        case 'length-percentage':
          var value_1 = parser.parseComponentValue();
          return isLengthPercentage(value_1) ? value_1 : ZERO_LENGTH;
        case 'time':
          return time.parse(context, parser.parseComponentValue());
      }
      break;
  }
};
var elementDebuggerAttribute = 'data-html2canvas-debug';
var getElementDebugType = function (element) {
  var attribute = element.getAttribute(elementDebuggerAttribute);
  switch (attribute) {
    case 'all':
      return 1 /* ALL */;
    case 'clone':
      return 2 /* CLONE */;
    case 'parse':
      return 3 /* PARSE */;
    case 'render':
      return 4 /* RENDER */;
    default:
      return 0 /* NONE */;
  }
};
var isDebugging = function (element, type) {
  var elementType = getElementDebugType(element);
  return elementType === 1 /* ALL */ || type === elementType;
};
var ElementContainer = /** @class */function () {
  function ElementContainer(context, element) {
    this.context = context;
    this.textNodes = [];
    this.elements = [];
    this.flags = 0;
    if (isDebugging(element, 3 /* PARSE */)) {
      debugger;
    }
    this.styles = new CSSParsedDeclaration(context, window.getComputedStyle(element, null));
    if (isHTMLElementNode(element)) {
      if (this.styles.animationDuration.some(function (duration) {
        return duration > 0;
      })) {
        element.style.animationDuration = '0s';
      }
      if (this.styles.transform !== null) {
        // getBoundingClientRect takes transforms into account
        element.style.transform = 'none';
      }
    }
    this.bounds = parseBounds(this.context, element);
    if (isDebugging(element, 4 /* RENDER */)) {
      this.flags |= 16 /* DEBUG_RENDER */;
    }
  }
  return ElementContainer;
}();

/*
 * text-segmentation 1.0.3 <https://github.com/niklasvh/text-segmentation>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var base64 = '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';

/*
 * utrie 1.0.2 <https://github.com/niklasvh/utrie>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var chars$1 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
var lookup$1 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (var i$1 = 0; i$1 < chars$1.length; i$1++) {
  lookup$1[chars$1.charCodeAt(i$1)] = i$1;
}
var decode = function (base64) {
  var bufferLength = base64.length * 0.75,
    len = base64.length,
    i,
    p = 0,
    encoded1,
    encoded2,
    encoded3,
    encoded4;
  if (base64[base64.length - 1] === '=') {
    bufferLength--;
    if (base64[base64.length - 2] === '=') {
      bufferLength--;
    }
  }
  var buffer = typeof ArrayBuffer !== 'undefined' && typeof Uint8Array !== 'undefined' && typeof Uint8Array.prototype.slice !== 'undefined' ? new ArrayBuffer(bufferLength) : new Array(bufferLength);
  var bytes = Array.isArray(buffer) ? buffer : new Uint8Array(buffer);
  for (i = 0; i < len; i += 4) {
    encoded1 = lookup$1[base64.charCodeAt(i)];
    encoded2 = lookup$1[base64.charCodeAt(i + 1)];
    encoded3 = lookup$1[base64.charCodeAt(i + 2)];
    encoded4 = lookup$1[base64.charCodeAt(i + 3)];
    bytes[p++] = encoded1 << 2 | encoded2 >> 4;
    bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
    bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
  }
  return buffer;
};
var polyUint16Array = function (buffer) {
  var length = buffer.length;
  var bytes = [];
  for (var i = 0; i < length; i += 2) {
    bytes.push(buffer[i + 1] << 8 | buffer[i]);
  }
  return bytes;
};
var polyUint32Array = function (buffer) {
  var length = buffer.length;
  var bytes = [];
  for (var i = 0; i < length; i += 4) {
    bytes.push(buffer[i + 3] << 24 | buffer[i + 2] << 16 | buffer[i + 1] << 8 | buffer[i]);
  }
  return bytes;
};

/** Shift size for getting the index-2 table offset. */
var UTRIE2_SHIFT_2 = 5;
/** Shift size for getting the index-1 table offset. */
var UTRIE2_SHIFT_1 = 6 + 5;
/**
 * Shift size for shifting left the index array values.
 * Increases possible data size with 16-bit index values at the cost
 * of compactability.
 * This requires data blocks to be aligned by UTRIE2_DATA_GRANULARITY.
 */
var UTRIE2_INDEX_SHIFT = 2;
/**
 * Difference between the two shift sizes,
 * for getting an index-1 offset from an index-2 offset. 6=11-5
 */
var UTRIE2_SHIFT_1_2 = UTRIE2_SHIFT_1 - UTRIE2_SHIFT_2;
/**
 * The part of the index-2 table for U+D800..U+DBFF stores values for
 * lead surrogate code _units_ not code _points_.
 * Values for lead surrogate code _points_ are indexed with this portion of the table.
 * Length=32=0x20=0x400>>UTRIE2_SHIFT_2. (There are 1024=0x400 lead surrogates.)
 */
var UTRIE2_LSCP_INDEX_2_OFFSET = 0x10000 >> UTRIE2_SHIFT_2;
/** Number of entries in a data block. 32=0x20 */
var UTRIE2_DATA_BLOCK_LENGTH = 1 << UTRIE2_SHIFT_2;
/** Mask for getting the lower bits for the in-data-block offset. */
var UTRIE2_DATA_MASK = UTRIE2_DATA_BLOCK_LENGTH - 1;
var UTRIE2_LSCP_INDEX_2_LENGTH = 0x400 >> UTRIE2_SHIFT_2;
/** Count the lengths of both BMP pieces. 2080=0x820 */
var UTRIE2_INDEX_2_BMP_LENGTH = UTRIE2_LSCP_INDEX_2_OFFSET + UTRIE2_LSCP_INDEX_2_LENGTH;
/**
 * The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
 * Length 32=0x20 for lead bytes C0..DF, regardless of UTRIE2_SHIFT_2.
 */
var UTRIE2_UTF8_2B_INDEX_2_OFFSET = UTRIE2_INDEX_2_BMP_LENGTH;
var UTRIE2_UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6; /* U+0800 is the first code point after 2-byte UTF-8 */
/**
 * The index-1 table, only used for supplementary code points, at offset 2112=0x840.
 * Variable length, for code points up to highStart, where the last single-value range starts.
 * Maximum length 512=0x200=0x100000>>UTRIE2_SHIFT_1.
 * (For 0x100000 supplementary code points U+10000..U+10ffff.)
 *
 * The part of the index-2 table for supplementary code points starts
 * after this index-1 table.
 *
 * Both the index-1 table and the following part of the index-2 table
 * are omitted completely if there is only BMP data.
 */
var UTRIE2_INDEX_1_OFFSET = UTRIE2_UTF8_2B_INDEX_2_OFFSET + UTRIE2_UTF8_2B_INDEX_2_LENGTH;
/**
 * Number of index-1 entries for the BMP. 32=0x20
 * This part of the index-1 table is omitted from the serialized form.
 */
var UTRIE2_OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> UTRIE2_SHIFT_1;
/** Number of entries in an index-2 block. 64=0x40 */
var UTRIE2_INDEX_2_BLOCK_LENGTH = 1 << UTRIE2_SHIFT_1_2;
/** Mask for getting the lower bits for the in-index-2-block offset. */
var UTRIE2_INDEX_2_MASK = UTRIE2_INDEX_2_BLOCK_LENGTH - 1;
var slice16 = function (view, start, end) {
  if (view.slice) {
    return view.slice(start, end);
  }
  return new Uint16Array(Array.prototype.slice.call(view, start, end));
};
var slice32 = function (view, start, end) {
  if (view.slice) {
    return view.slice(start, end);
  }
  return new Uint32Array(Array.prototype.slice.call(view, start, end));
};
var createTrieFromBase64 = function (base64, _byteLength) {
  var buffer = decode(base64);
  var view32 = Array.isArray(buffer) ? polyUint32Array(buffer) : new Uint32Array(buffer);
  var view16 = Array.isArray(buffer) ? polyUint16Array(buffer) : new Uint16Array(buffer);
  var headerLength = 24;
  var index = slice16(view16, headerLength / 2, view32[4] / 2);
  var data = view32[5] === 2 ? slice16(view16, (headerLength + view32[4]) / 2) : slice32(view32, Math.ceil((headerLength + view32[4]) / 4));
  return new Trie(view32[0], view32[1], view32[2], view32[3], index, data);
};
var Trie = /** @class */function () {
  function Trie(initialValue, errorValue, highStart, highValueIndex, index, data) {
    this.initialValue = initialValue;
    this.errorValue = errorValue;
    this.highStart = highStart;
    this.highValueIndex = highValueIndex;
    this.index = index;
    this.data = data;
  }
  /**
   * Get the value for a code point as stored in the Trie.
   *
   * @param codePoint the code point
   * @return the value
   */
  Trie.prototype.get = function (codePoint) {
    var ix;
    if (codePoint >= 0) {
      if (codePoint < 0x0d800 || codePoint > 0x0dbff && codePoint <= 0x0ffff) {
        // Ordinary BMP code point, excluding leading surrogates.
        // BMP uses a single level lookup.  BMP index starts at offset 0 in the Trie2 index.
        // 16 bit data is stored in the index array itself.
        ix = this.index[codePoint >> UTRIE2_SHIFT_2];
        ix = (ix << UTRIE2_INDEX_SHIFT) + (codePoint & UTRIE2_DATA_MASK);
        return this.data[ix];
      }
      if (codePoint <= 0xffff) {
        // Lead Surrogate Code Point.  A Separate index section is stored for
        // lead surrogate code units and code points.
        //   The main index has the code unit data.
        //   For this function, we need the code point data.
        // Note: this expression could be refactored for slightly improved efficiency, but
        //       surrogate code points will be so rare in practice that it's not worth it.
        ix = this.index[UTRIE2_LSCP_INDEX_2_OFFSET + (codePoint - 0xd800 >> UTRIE2_SHIFT_2)];
        ix = (ix << UTRIE2_INDEX_SHIFT) + (codePoint & UTRIE2_DATA_MASK);
        return this.data[ix];
      }
      if (codePoint < this.highStart) {
        // Supplemental code point, use two-level lookup.
        ix = UTRIE2_INDEX_1_OFFSET - UTRIE2_OMITTED_BMP_INDEX_1_LENGTH + (codePoint >> UTRIE2_SHIFT_1);
        ix = this.index[ix];
        ix += codePoint >> UTRIE2_SHIFT_2 & UTRIE2_INDEX_2_MASK;
        ix = this.index[ix];
        ix = (ix << UTRIE2_INDEX_SHIFT) + (codePoint & UTRIE2_DATA_MASK);
        return this.data[ix];
      }
      if (codePoint <= 0x10ffff) {
        return this.data[this.highValueIndex];
      }
    }
    // Fall through.  The code point is outside of the legal range of 0..0x10ffff.
    return this.errorValue;
  };
  return Trie;
}();

/*
 * base64-arraybuffer 1.0.2 <https://github.com/niklasvh/base64-arraybuffer>
 * Copyright (c) 2022 Niklas von Hertzen <https://hertzen.com>
 * Released under MIT License
 */
var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
var lookup = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (var i = 0; i < chars.length; i++) {
  lookup[chars.charCodeAt(i)] = i;
}
var Prepend = 1;
var CR = 2;
var LF = 3;
var Control = 4;
var Extend = 5;
var SpacingMark = 7;
var L = 8;
var V = 9;
var T = 10;
var LV = 11;
var LVT = 12;
var ZWJ = 13;
var Extended_Pictographic = 14;
var RI = 15;
var toCodePoints = function (str) {
  var codePoints = [];
  var i = 0;
  var length = str.length;
  while (i < length) {
    var value = str.charCodeAt(i++);
    if (value >= 0xd800 && value <= 0xdbff && i < length) {
      var extra = str.charCodeAt(i++);
      if ((extra & 0xfc00) === 0xdc00) {
        codePoints.push(((value & 0x3ff) << 10) + (extra & 0x3ff) + 0x10000);
      } else {
        codePoints.push(value);
        i--;
      }
    } else {
      codePoints.push(value);
    }
  }
  return codePoints;
};
var fromCodePoint = function () {
  var codePoints = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    codePoints[_i] = arguments[_i];
  }
  if (String.fromCodePoint) {
    return String.fromCodePoint.apply(String, codePoints);
  }
  var length = codePoints.length;
  if (!length) {
    return '';
  }
  var codeUnits = [];
  var index = -1;
  var result = '';
  while (++index < length) {
    var codePoint = codePoints[index];
    if (codePoint <= 0xffff) {
      codeUnits.push(codePoint);
    } else {
      codePoint -= 0x10000;
      codeUnits.push((codePoint >> 10) + 0xd800, codePoint % 0x400 + 0xdc00);
    }
    if (index + 1 === length || codeUnits.length > 0x4000) {
      result += String.fromCharCode.apply(String, codeUnits);
      codeUnits.length = 0;
    }
  }
  return result;
};
var UnicodeTrie = createTrieFromBase64(base64);
var BREAK_NOT_ALLOWED = '×';
var BREAK_ALLOWED = '÷';
var codePointToClass = function (codePoint) {
  return UnicodeTrie.get(codePoint);
};
var _graphemeBreakAtIndex = function (_codePoints, classTypes, index) {
  var prevIndex = index - 2;
  var prev = classTypes[prevIndex];
  var current = classTypes[index - 1];
  var next = classTypes[index];
  // GB3 Do not break between a CR and LF
  if (current === CR && next === LF) {
    return BREAK_NOT_ALLOWED;
  }
  // GB4 Otherwise, break before and after controls.
  if (current === CR || current === LF || current === Control) {
    return BREAK_ALLOWED;
  }
  // GB5
  if (next === CR || next === LF || next === Control) {
    return BREAK_ALLOWED;
  }
  // Do not break Hangul syllable sequences.
  // GB6
  if (current === L && [L, V, LV, LVT].indexOf(next) !== -1) {
    return BREAK_NOT_ALLOWED;
  }
  // GB7
  if ((current === LV || current === V) && (next === V || next === T)) {
    return BREAK_NOT_ALLOWED;
  }
  // GB8
  if ((current === LVT || current === T) && next === T) {
    return BREAK_NOT_ALLOWED;
  }
  // GB9 Do not break before extending characters or ZWJ.
  if (next === ZWJ || next === Extend) {
    return BREAK_NOT_ALLOWED;
  }
  // Do not break before SpacingMarks, or after Prepend characters.
  // GB9a
  if (next === SpacingMark) {
    return BREAK_NOT_ALLOWED;
  }
  // GB9a
  if (current === Prepend) {
    return BREAK_NOT_ALLOWED;
  }
  // GB11 Do not break within emoji modifier sequences or emoji zwj sequences.
  if (current === ZWJ && next === Extended_Pictographic) {
    while (prev === Extend) {
      prev = classTypes[--prevIndex];
    }
    if (prev === Extended_Pictographic) {
      return BREAK_NOT_ALLOWED;
    }
  }
  // GB12 Do not break within emoji flag sequences.
  // That is, do not break between regional indicator (RI) symbols
  // if there is an odd number of RI characters before the break point.
  if (current === RI && next === RI) {
    var countRI = 0;
    while (prev === RI) {
      countRI++;
      prev = classTypes[--prevIndex];
    }
    if (countRI % 2 === 0) {
      return BREAK_NOT_ALLOWED;
    }
  }
  return BREAK_ALLOWED;
};
var GraphemeBreaker = function (str) {
  var codePoints = toCodePoints(str);
  var length = codePoints.length;
  var index = 0;
  var lastEnd = 0;
  var classTypes = codePoints.map(codePointToClass);
  return {
    next: function () {
      if (index >= length) {
        return {
          done: true,
          value: null
        };
      }
      var graphemeBreak = BREAK_NOT_ALLOWED;
      while (index < length && (graphemeBreak = _graphemeBreakAtIndex(codePoints, classTypes, ++index)) === BREAK_NOT_ALLOWED) {}
      if (graphemeBreak !== BREAK_NOT_ALLOWED || index === length) {
        var value = fromCodePoint.apply(null, codePoints.slice(lastEnd, index));
        lastEnd = index;
        return {
          value: value,
          done: false
        };
      }
      return {
        done: true,
        value: null
      };
    }
  };
};
var splitGraphemes = function (str) {
  var breaker = GraphemeBreaker(str);
  var graphemes = [];
  var bk;
  while (!(bk = breaker.next()).done) {
    if (bk.value) {
      graphemes.push(bk.value.slice());
    }
  }
  return graphemes;
};
var testRangeBounds = function (document) {
  var TEST_HEIGHT = 123;
  if (document.createRange) {
    var range = document.createRange();
    if (range.getBoundingClientRect) {
      var testElement = document.createElement('boundtest');
      testElement.style.height = TEST_HEIGHT + "px";
      testElement.style.display = 'block';
      document.body.appendChild(testElement);
      range.selectNode(testElement);
      var rangeBounds = range.getBoundingClientRect();
      var rangeHeight = Math.round(rangeBounds.height);
      document.body.removeChild(testElement);
      if (rangeHeight === TEST_HEIGHT) {
        return true;
      }
    }
  }
  return false;
};
var testIOSLineBreak = function (document) {
  var testElement = document.createElement('boundtest');
  testElement.style.width = '50px';
  testElement.style.display = 'block';
  testElement.style.fontSize = '12px';
  testElement.style.letterSpacing = '0px';
  testElement.style.wordSpacing = '0px';
  document.body.appendChild(testElement);
  var range = document.createRange();
  testElement.innerHTML = typeof ''.repeat === 'function' ? '&#128104;'.repeat(10) : '';
  var node = testElement.firstChild;
  var textList = toCodePoints$1(node.data).map(function (i) {
    return fromCodePoint$1(i);
  });
  var offset = 0;
  var prev = {};
  // ios 13 does not handle range getBoundingClientRect line changes correctly #2177
  var supports = textList.every(function (text, i) {
    range.setStart(node, offset);
    range.setEnd(node, offset + text.length);
    var rect = range.getBoundingClientRect();
    offset += text.length;
    var boundAhead = rect.x > prev.x || rect.y > prev.y;
    prev = rect;
    if (i === 0) {
      return true;
    }
    return boundAhead;
  });
  document.body.removeChild(testElement);
  return supports;
};
var testCORS = function () {
  return typeof new Image().crossOrigin !== 'undefined';
};
var testResponseType = function () {
  return typeof new XMLHttpRequest().responseType === 'string';
};
var testSVG = function (document) {
  var img = new Image();
  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d');
  if (!ctx) {
    return false;
  }
  img.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>";
  try {
    ctx.drawImage(img, 0, 0);
    canvas.toDataURL();
  } catch (e) {
    return false;
  }
  return true;
};
var isGreenPixel = function (data) {
  return data[0] === 0 && data[1] === 255 && data[2] === 0 && data[3] === 255;
};
var testForeignObject = function (document) {
  var canvas = document.createElement('canvas');
  var size = 100;
  canvas.width = size;
  canvas.height = size;
  var ctx = canvas.getContext('2d');
  if (!ctx) {
    return Promise.reject(false);
  }
  ctx.fillStyle = 'rgb(0, 255, 0)';
  ctx.fillRect(0, 0, size, size);
  var img = new Image();
  var greenImageSrc = canvas.toDataURL();
  img.src = greenImageSrc;
  var svg = createForeignObjectSVG(size, size, 0, 0, img);
  ctx.fillStyle = 'red';
  ctx.fillRect(0, 0, size, size);
  return loadSerializedSVG$1(svg).then(function (img) {
    ctx.drawImage(img, 0, 0);
    var data = ctx.getImageData(0, 0, size, size).data;
    ctx.fillStyle = 'red';
    ctx.fillRect(0, 0, size, size);
    var node = document.createElement('div');
    node.style.backgroundImage = "url(" + greenImageSrc + ")";
    node.style.height = size + "px";
    // Firefox 55 does not render inline <img /> tags
    return isGreenPixel(data) ? loadSerializedSVG$1(createForeignObjectSVG(size, size, 0, 0, node)) : Promise.reject(false);
  }).then(function (img) {
    ctx.drawImage(img, 0, 0);
    // Edge does not render background-images
    return isGreenPixel(ctx.getImageData(0, 0, size, size).data);
  }).catch(function () {
    return false;
  });
};
var createForeignObjectSVG = function (width, height, x, y, node) {
  var xmlns = 'http://www.w3.org/2000/svg';
  var svg = document.createElementNS(xmlns, 'svg');
  var foreignObject = document.createElementNS(xmlns, 'foreignObject');
  svg.setAttributeNS(null, 'width', width.toString());
  svg.setAttributeNS(null, 'height', height.toString());
  foreignObject.setAttributeNS(null, 'width', '100%');
  foreignObject.setAttributeNS(null, 'height', '100%');
  foreignObject.setAttributeNS(null, 'x', x.toString());
  foreignObject.setAttributeNS(null, 'y', y.toString());
  foreignObject.setAttributeNS(null, 'externalResourcesRequired', 'true');
  svg.appendChild(foreignObject);
  foreignObject.appendChild(node);
  return svg;
};
var loadSerializedSVG$1 = function (svg) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.onload = function () {
      return resolve(img);
    };
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(svg));
  });
};
var FEATURES = {
  get SUPPORT_RANGE_BOUNDS() {
    var value = testRangeBounds(document);
    Object.defineProperty(FEATURES, 'SUPPORT_RANGE_BOUNDS', {
      value: value
    });
    return value;
  },
  get SUPPORT_WORD_BREAKING() {
    var value = FEATURES.SUPPORT_RANGE_BOUNDS && testIOSLineBreak(document);
    Object.defineProperty(FEATURES, 'SUPPORT_WORD_BREAKING', {
      value: value
    });
    return value;
  },
  get SUPPORT_SVG_DRAWING() {
    var value = testSVG(document);
    Object.defineProperty(FEATURES, 'SUPPORT_SVG_DRAWING', {
      value: value
    });
    return value;
  },
  get SUPPORT_FOREIGNOBJECT_DRAWING() {
    var value = typeof Array.from === 'function' && typeof window.fetch === 'function' ? testForeignObject(document) : Promise.resolve(false);
    Object.defineProperty(FEATURES, 'SUPPORT_FOREIGNOBJECT_DRAWING', {
      value: value
    });
    return value;
  },
  get SUPPORT_CORS_IMAGES() {
    var value = testCORS();
    Object.defineProperty(FEATURES, 'SUPPORT_CORS_IMAGES', {
      value: value
    });
    return value;
  },
  get SUPPORT_RESPONSE_TYPE() {
    var value = testResponseType();
    Object.defineProperty(FEATURES, 'SUPPORT_RESPONSE_TYPE', {
      value: value
    });
    return value;
  },
  get SUPPORT_CORS_XHR() {
    var value = 'withCredentials' in new XMLHttpRequest();
    Object.defineProperty(FEATURES, 'SUPPORT_CORS_XHR', {
      value: value
    });
    return value;
  },
  get SUPPORT_NATIVE_TEXT_SEGMENTATION() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var value = !!(typeof Intl !== 'undefined' && Intl.Segmenter);
    Object.defineProperty(FEATURES, 'SUPPORT_NATIVE_TEXT_SEGMENTATION', {
      value: value
    });
    return value;
  }
};
var TextBounds = /** @class */function () {
  function TextBounds(text, bounds) {
    this.text = text;
    this.bounds = bounds;
  }
  return TextBounds;
}();
var parseTextBounds = function (context, value, styles, node) {
  var textList = breakText(value, styles);
  var textBounds = [];
  var offset = 0;
  textList.forEach(function (text) {
    if (styles.textDecorationLine.length || text.trim().length > 0) {
      if (FEATURES.SUPPORT_RANGE_BOUNDS) {
        var clientRects = createRange(node, offset, text.length).getClientRects();
        if (clientRects.length > 1) {
          var subSegments = segmentGraphemes(text);
          var subOffset_1 = 0;
          subSegments.forEach(function (subSegment) {
            textBounds.push(new TextBounds(subSegment, Bounds.fromDOMRectList(context, createRange(node, subOffset_1 + offset, subSegment.length).getClientRects())));
            subOffset_1 += subSegment.length;
          });
        } else {
          textBounds.push(new TextBounds(text, Bounds.fromDOMRectList(context, clientRects)));
        }
      } else {
        var replacementNode = node.splitText(text.length);
        textBounds.push(new TextBounds(text, getWrapperBounds(context, node)));
        node = replacementNode;
      }
    } else if (!FEATURES.SUPPORT_RANGE_BOUNDS) {
      node = node.splitText(text.length);
    }
    offset += text.length;
  });
  return textBounds;
};
var getWrapperBounds = function (context, node) {
  var ownerDocument = node.ownerDocument;
  if (ownerDocument) {
    var wrapper = ownerDocument.createElement('html2canvaswrapper');
    wrapper.appendChild(node.cloneNode(true));
    var parentNode = node.parentNode;
    if (parentNode) {
      parentNode.replaceChild(wrapper, node);
      var bounds = parseBounds(context, wrapper);
      if (wrapper.firstChild) {
        parentNode.replaceChild(wrapper.firstChild, wrapper);
      }
      return bounds;
    }
  }
  return Bounds.EMPTY;
};
var createRange = function (node, offset, length) {
  var ownerDocument = node.ownerDocument;
  if (!ownerDocument) {
    throw new Error('Node has no owner document');
  }
  var range = ownerDocument.createRange();
  range.setStart(node, offset);
  range.setEnd(node, offset + length);
  return range;
};
var segmentGraphemes = function (value) {
  if (FEATURES.SUPPORT_NATIVE_TEXT_SEGMENTATION) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var segmenter = new Intl.Segmenter(void 0, {
      granularity: 'grapheme'
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Array.from(segmenter.segment(value)).map(function (segment) {
      return segment.segment;
    });
  }
  return splitGraphemes(value);
};
var segmentWords = function (value, styles) {
  if (FEATURES.SUPPORT_NATIVE_TEXT_SEGMENTATION) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var segmenter = new Intl.Segmenter(void 0, {
      granularity: 'word'
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Array.from(segmenter.segment(value)).map(function (segment) {
      return segment.segment;
    });
  }
  return breakWords(value, styles);
};
var breakText = function (value, styles) {
  return styles.letterSpacing !== 0 ? segmentGraphemes(value) : segmentWords(value, styles);
};
// https://drafts.csswg.org/css-text/#word-separator
var wordSeparators = [0x0020, 0x00a0, 0x1361, 0x10100, 0x10101, 0x1039, 0x1091];
var breakWords = function (str, styles) {
  var breaker = LineBreaker(str, {
    lineBreak: styles.lineBreak,
    wordBreak: styles.overflowWrap === "break-word" /* BREAK_WORD */ ? 'break-word' : styles.wordBreak
  });
  var words = [];
  var bk;
  var _loop_1 = function () {
    if (bk.value) {
      var value = bk.value.slice();
      var codePoints = toCodePoints$1(value);
      var word_1 = '';
      codePoints.forEach(function (codePoint) {
        if (wordSeparators.indexOf(codePoint) === -1) {
          word_1 += fromCodePoint$1(codePoint);
        } else {
          if (word_1.length) {
            words.push(word_1);
          }
          words.push(fromCodePoint$1(codePoint));
          word_1 = '';
        }
      });
      if (word_1.length) {
        words.push(word_1);
      }
    }
  };
  while (!(bk = breaker.next()).done) {
    _loop_1();
  }
  return words;
};
var TextContainer = /** @class */function () {
  function TextContainer(context, node, styles) {
    this.text = transform(node.data, styles.textTransform);
    this.textBounds = parseTextBounds(context, this.text, styles, node);
  }
  return TextContainer;
}();
var transform = function (text, transform) {
  switch (transform) {
    case 1 /* LOWERCASE */:
      return text.toLowerCase();
    case 3 /* CAPITALIZE */:
      return text.replace(CAPITALIZE, capitalize);
    case 2 /* UPPERCASE */:
      return text.toUpperCase();
    default:
      return text;
  }
};
var CAPITALIZE = /(^|\s|:|-|\(|\))([a-z])/g;
var capitalize = function (m, p1, p2) {
  if (m.length > 0) {
    return p1 + p2.toUpperCase();
  }
  return m;
};
var ImageElementContainer = /** @class */function (_super) {
  __extends(ImageElementContainer, _super);
  function ImageElementContainer(context, img) {
    var _this = _super.call(this, context, img) || this;
    _this.src = img.currentSrc || img.src;
    _this.intrinsicWidth = img.naturalWidth;
    _this.intrinsicHeight = img.naturalHeight;
    _this.context.cache.addImage(_this.src);
    return _this;
  }
  return ImageElementContainer;
}(ElementContainer);
var CanvasElementContainer = /** @class */function (_super) {
  __extends(CanvasElementContainer, _super);
  function CanvasElementContainer(context, canvas) {
    var _this = _super.call(this, context, canvas) || this;
    _this.canvas = canvas;
    _this.intrinsicWidth = canvas.width;
    _this.intrinsicHeight = canvas.height;
    return _this;
  }
  return CanvasElementContainer;
}(ElementContainer);
var SVGElementContainer = /** @class */function (_super) {
  __extends(SVGElementContainer, _super);
  function SVGElementContainer(context, img) {
    var _this = _super.call(this, context, img) || this;
    var s = new XMLSerializer();
    var bounds = parseBounds(context, img);
    img.setAttribute('width', bounds.width + "px");
    img.setAttribute('height', bounds.height + "px");
    _this.svg = "data:image/svg+xml," + encodeURIComponent(s.serializeToString(img));
    _this.intrinsicWidth = img.width.baseVal.value;
    _this.intrinsicHeight = img.height.baseVal.value;
    _this.context.cache.addImage(_this.svg);
    return _this;
  }
  return SVGElementContainer;
}(ElementContainer);
var LIElementContainer = /** @class */function (_super) {
  __extends(LIElementContainer, _super);
  function LIElementContainer(context, element) {
    var _this = _super.call(this, context, element) || this;
    _this.value = element.value;
    return _this;
  }
  return LIElementContainer;
}(ElementContainer);
var OLElementContainer = /** @class */function (_super) {
  __extends(OLElementContainer, _super);
  function OLElementContainer(context, element) {
    var _this = _super.call(this, context, element) || this;
    _this.start = element.start;
    _this.reversed = typeof element.reversed === 'boolean' && element.reversed === true;
    return _this;
  }
  return OLElementContainer;
}(ElementContainer);
var CHECKBOX_BORDER_RADIUS = [{
  type: 15 /* DIMENSION_TOKEN */,
  flags: 0,
  unit: 'px',
  number: 3
}];
var RADIO_BORDER_RADIUS = [{
  type: 16 /* PERCENTAGE_TOKEN */,
  flags: 0,
  number: 50
}];
var reformatInputBounds = function (bounds) {
  if (bounds.width > bounds.height) {
    return new Bounds(bounds.left + (bounds.width - bounds.height) / 2, bounds.top, bounds.height, bounds.height);
  } else if (bounds.width < bounds.height) {
    return new Bounds(bounds.left, bounds.top + (bounds.height - bounds.width) / 2, bounds.width, bounds.width);
  }
  return bounds;
};
var getInputValue = function (node) {
  var value = node.type === PASSWORD ? new Array(node.value.length + 1).join('\u2022') : node.value;
  return value.length === 0 ? node.placeholder || '' : value;
};
var CHECKBOX = 'checkbox';
var RADIO = 'radio';
var PASSWORD = 'password';
var INPUT_COLOR = 0x2a2a2aff;
var InputElementContainer = /** @class */function (_super) {
  __extends(InputElementContainer, _super);
  function InputElementContainer(context, input) {
    var _this = _super.call(this, context, input) || this;
    _this.type = input.type.toLowerCase();
    _this.checked = input.checked;
    _this.value = getInputValue(input);
    if (_this.type === CHECKBOX || _this.type === RADIO) {
      _this.styles.backgroundColor = 0xdededeff;
      _this.styles.borderTopColor = _this.styles.borderRightColor = _this.styles.borderBottomColor = _this.styles.borderLeftColor = 0xa5a5a5ff;
      _this.styles.borderTopWidth = _this.styles.borderRightWidth = _this.styles.borderBottomWidth = _this.styles.borderLeftWidth = 1;
      _this.styles.borderTopStyle = _this.styles.borderRightStyle = _this.styles.borderBottomStyle = _this.styles.borderLeftStyle = 1 /* SOLID */;
      _this.styles.backgroundClip = [0 /* BORDER_BOX */];
      _this.styles.backgroundOrigin = [0 /* BORDER_BOX */];
      _this.bounds = reformatInputBounds(_this.bounds);
    }
    switch (_this.type) {
      case CHECKBOX:
        _this.styles.borderTopRightRadius = _this.styles.borderTopLeftRadius = _this.styles.borderBottomRightRadius = _this.styles.borderBottomLeftRadius = CHECKBOX_BORDER_RADIUS;
        break;
      case RADIO:
        _this.styles.borderTopRightRadius = _this.styles.borderTopLeftRadius = _this.styles.borderBottomRightRadius = _this.styles.borderBottomLeftRadius = RADIO_BORDER_RADIUS;
        break;
    }
    return _this;
  }
  return InputElementContainer;
}(ElementContainer);
var SelectElementContainer = /** @class */function (_super) {
  __extends(SelectElementContainer, _super);
  function SelectElementContainer(context, element) {
    var _this = _super.call(this, context, element) || this;
    var option = element.options[element.selectedIndex || 0];
    _this.value = option ? option.text || '' : '';
    return _this;
  }
  return SelectElementContainer;
}(ElementContainer);
var TextareaElementContainer = /** @class */function (_super) {
  __extends(TextareaElementContainer, _super);
  function TextareaElementContainer(context, element) {
    var _this = _super.call(this, context, element) || this;
    _this.value = element.value;
    return _this;
  }
  return TextareaElementContainer;
}(ElementContainer);
var IFrameElementContainer = /** @class */function (_super) {
  __extends(IFrameElementContainer, _super);
  function IFrameElementContainer(context, iframe) {
    var _this = _super.call(this, context, iframe) || this;
    _this.src = iframe.src;
    _this.width = parseInt(iframe.width, 10) || 0;
    _this.height = parseInt(iframe.height, 10) || 0;
    _this.backgroundColor = _this.styles.backgroundColor;
    try {
      if (iframe.contentWindow && iframe.contentWindow.document && iframe.contentWindow.document.documentElement) {
        _this.tree = parseTree(context, iframe.contentWindow.document.documentElement);
        // http://www.w3.org/TR/css3-background/#special-backgrounds
        var documentBackgroundColor = iframe.contentWindow.document.documentElement ? parseColor(context, getComputedStyle(iframe.contentWindow.document.documentElement).backgroundColor) : COLORS.TRANSPARENT;
        var bodyBackgroundColor = iframe.contentWindow.document.body ? parseColor(context, getComputedStyle(iframe.contentWindow.document.body).backgroundColor) : COLORS.TRANSPARENT;
        _this.backgroundColor = isTransparent(documentBackgroundColor) ? isTransparent(bodyBackgroundColor) ? _this.styles.backgroundColor : bodyBackgroundColor : documentBackgroundColor;
      }
    } catch (e) {}
    return _this;
  }
  return IFrameElementContainer;
}(ElementContainer);
var LIST_OWNERS = ['OL', 'UL', 'MENU'];
var parseNodeTree = function (context, node, parent, root) {
  for (var childNode = node.firstChild, nextNode = void 0; childNode; childNode = nextNode) {
    nextNode = childNode.nextSibling;
    if (isTextNode(childNode) && childNode.data.trim().length > 0) {
      parent.textNodes.push(new TextContainer(context, childNode, parent.styles));
    } else if (isElementNode(childNode)) {
      if (isSlotElement(childNode) && childNode.assignedNodes) {
        childNode.assignedNodes().forEach(function (childNode) {
          return parseNodeTree(context, childNode, parent, root);
        });
      } else {
        var container = createContainer(context, childNode);
        if (container.styles.isVisible()) {
          if (createsRealStackingContext(childNode, container, root)) {
            container.flags |= 4 /* CREATES_REAL_STACKING_CONTEXT */;
          } else if (createsStackingContext(container.styles)) {
            container.flags |= 2 /* CREATES_STACKING_CONTEXT */;
          }
          if (LIST_OWNERS.indexOf(childNode.tagName) !== -1) {
            container.flags |= 8 /* IS_LIST_OWNER */;
          }
          parent.elements.push(container);
          childNode.slot;
          if (childNode.shadowRoot) {
            parseNodeTree(context, childNode.shadowRoot, container, root);
          } else if (!isTextareaElement(childNode) && !isSVGElement(childNode) && !isSelectElement(childNode)) {
            parseNodeTree(context, childNode, container, root);
          }
        }
      }
    }
  }
};
var createContainer = function (context, element) {
  if (isImageElement(element)) {
    return new ImageElementContainer(context, element);
  }
  if (isCanvasElement(element)) {
    return new CanvasElementContainer(context, element);
  }
  if (isSVGElement(element)) {
    return new SVGElementContainer(context, element);
  }
  if (isLIElement(element)) {
    return new LIElementContainer(context, element);
  }
  if (isOLElement(element)) {
    return new OLElementContainer(context, element);
  }
  if (isInputElement(element)) {
    return new InputElementContainer(context, element);
  }
  if (isSelectElement(element)) {
    return new SelectElementContainer(context, element);
  }
  if (isTextareaElement(element)) {
    return new TextareaElementContainer(context, element);
  }
  if (isIFrameElement(element)) {
    return new IFrameElementContainer(context, element);
  }
  return new ElementContainer(context, element);
};
var parseTree = function (context, element) {
  var container = createContainer(context, element);
  container.flags |= 4 /* CREATES_REAL_STACKING_CONTEXT */;
  parseNodeTree(context, element, container, container);
  return container;
};
var createsRealStackingContext = function (node, container, root) {
  return container.styles.isPositionedWithZIndex() || container.styles.opacity < 1 || container.styles.isTransformed() || isBodyElement(node) && root.styles.isTransparent();
};
var createsStackingContext = function (styles) {
  return styles.isPositioned() || styles.isFloating();
};
var isTextNode = function (node) {
  return node.nodeType === Node.TEXT_NODE;
};
var isElementNode = function (node) {
  return node.nodeType === Node.ELEMENT_NODE;
};
var isHTMLElementNode = function (node) {
  return isElementNode(node) && typeof node.style !== 'undefined' && !isSVGElementNode(node);
};
var isSVGElementNode = function (element) {
  return typeof element.className === 'object';
};
var isLIElement = function (node) {
  return node.tagName === 'LI';
};
var isOLElement = function (node) {
  return node.tagName === 'OL';
};
var isInputElement = function (node) {
  return node.tagName === 'INPUT';
};
var isHTMLElement = function (node) {
  return node.tagName === 'HTML';
};
var isSVGElement = function (node) {
  return node.tagName === 'svg';
};
var isBodyElement = function (node) {
  return node.tagName === 'BODY';
};
var isCanvasElement = function (node) {
  return node.tagName === 'CANVAS';
};
var isVideoElement = function (node) {
  return node.tagName === 'VIDEO';
};
var isImageElement = function (node) {
  return node.tagName === 'IMG';
};
var isIFrameElement = function (node) {
  return node.tagName === 'IFRAME';
};
var isStyleElement = function (node) {
  return node.tagName === 'STYLE';
};
var isScriptElement = function (node) {
  return node.tagName === 'SCRIPT';
};
var isTextareaElement = function (node) {
  return node.tagName === 'TEXTAREA';
};
var isSelectElement = function (node) {
  return node.tagName === 'SELECT';
};
var isSlotElement = function (node) {
  return node.tagName === 'SLOT';
};
// https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
var isCustomElement = function (node) {
  return node.tagName.indexOf('-') > 0;
};
var CounterState = /** @class */function () {
  function CounterState() {
    this.counters = {};
  }
  CounterState.prototype.getCounterValue = function (name) {
    var counter = this.counters[name];
    if (counter && counter.length) {
      return counter[counter.length - 1];
    }
    return 1;
  };
  CounterState.prototype.getCounterValues = function (name) {
    var counter = this.counters[name];
    return counter ? counter : [];
  };
  CounterState.prototype.pop = function (counters) {
    var _this = this;
    counters.forEach(function (counter) {
      return _this.counters[counter].pop();
    });
  };
  CounterState.prototype.parse = function (style) {
    var _this = this;
    var counterIncrement = style.counterIncrement;
    var counterReset = style.counterReset;
    var canReset = true;
    if (counterIncrement !== null) {
      counterIncrement.forEach(function (entry) {
        var counter = _this.counters[entry.counter];
        if (counter && entry.increment !== 0) {
          canReset = false;
          if (!counter.length) {
            counter.push(1);
          }
          counter[Math.max(0, counter.length - 1)] += entry.increment;
        }
      });
    }
    var counterNames = [];
    if (canReset) {
      counterReset.forEach(function (entry) {
        var counter = _this.counters[entry.counter];
        counterNames.push(entry.counter);
        if (!counter) {
          counter = _this.counters[entry.counter] = [];
        }
        counter.push(entry.reset);
      });
    }
    return counterNames;
  };
  return CounterState;
}();
var ROMAN_UPPER = {
  integers: [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1],
  values: ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I']
};
var ARMENIAN = {
  integers: [9000, 8000, 7000, 6000, 5000, 4000, 3000, 2000, 1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
  values: ['Ք', 'Փ', 'Ւ', 'Ց', 'Ր', 'Տ', 'Վ', 'Ս', 'Ռ', 'Ջ', 'Պ', 'Չ', 'Ո', 'Շ', 'Ն', 'Յ', 'Մ', 'Ճ', 'Ղ', 'Ձ', 'Հ', 'Կ', 'Ծ', 'Խ', 'Լ', 'Ի', 'Ժ', 'Թ', 'Ը', 'Է', 'Զ', 'Ե', 'Դ', 'Գ', 'Բ', 'Ա']
};
var HEBREW = {
  integers: [10000, 9000, 8000, 7000, 6000, 5000, 4000, 3000, 2000, 1000, 400, 300, 200, 100, 90, 80, 70, 60, 50, 40, 30, 20, 19, 18, 17, 16, 15, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
  values: ['י׳', 'ט׳', 'ח׳', 'ז׳', 'ו׳', 'ה׳', 'ד׳', 'ג׳', 'ב׳', 'א׳', 'ת', 'ש', 'ר', 'ק', 'צ', 'פ', 'ע', 'ס', 'נ', 'מ', 'ל', 'כ', 'יט', 'יח', 'יז', 'טז', 'טו', 'י', 'ט', 'ח', 'ז', 'ו', 'ה', 'ד', 'ג', 'ב', 'א']
};
var GEORGIAN = {
  integers: [10000, 9000, 8000, 7000, 6000, 5000, 4000, 3000, 2000, 1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
  values: ['ჵ', 'ჰ', 'ჯ', 'ჴ', 'ხ', 'ჭ', 'წ', 'ძ', 'ც', 'ჩ', 'შ', 'ყ', 'ღ', 'ქ', 'ფ', 'ჳ', 'ტ', 'ს', 'რ', 'ჟ', 'პ', 'ო', 'ჲ', 'ნ', 'მ', 'ლ', 'კ', 'ი', 'თ', 'ჱ', 'ზ', 'ვ', 'ე', 'დ', 'გ', 'ბ', 'ა']
};
var createAdditiveCounter = function (value, min, max, symbols, fallback, suffix) {
  if (value < min || value > max) {
    return createCounterText(value, fallback, suffix.length > 0);
  }
  return symbols.integers.reduce(function (string, integer, index) {
    while (value >= integer) {
      value -= integer;
      string += symbols.values[index];
    }
    return string;
  }, '') + suffix;
};
var createCounterStyleWithSymbolResolver = function (value, codePointRangeLength, isNumeric, resolver) {
  var string = '';
  do {
    if (!isNumeric) {
      value--;
    }
    string = resolver(value) + string;
    value /= codePointRangeLength;
  } while (value * codePointRangeLength >= codePointRangeLength);
  return string;
};
var createCounterStyleFromRange = function (value, codePointRangeStart, codePointRangeEnd, isNumeric, suffix) {
  var codePointRangeLength = codePointRangeEnd - codePointRangeStart + 1;
  return (value < 0 ? '-' : '') + (createCounterStyleWithSymbolResolver(Math.abs(value), codePointRangeLength, isNumeric, function (codePoint) {
    return fromCodePoint$1(Math.floor(codePoint % codePointRangeLength) + codePointRangeStart);
  }) + suffix);
};
var createCounterStyleFromSymbols = function (value, symbols, suffix) {
  if (suffix === void 0) {
    suffix = '. ';
  }
  var codePointRangeLength = symbols.length;
  return createCounterStyleWithSymbolResolver(Math.abs(value), codePointRangeLength, false, function (codePoint) {
    return symbols[Math.floor(codePoint % codePointRangeLength)];
  }) + suffix;
};
var CJK_ZEROS = 1 << 0;
var CJK_TEN_COEFFICIENTS = 1 << 1;
var CJK_TEN_HIGH_COEFFICIENTS = 1 << 2;
var CJK_HUNDRED_COEFFICIENTS = 1 << 3;
var createCJKCounter = function (value, numbers, multipliers, negativeSign, suffix, flags) {
  if (value < -9999 || value > 9999) {
    return createCounterText(value, 4 /* CJK_DECIMAL */, suffix.length > 0);
  }
  var tmp = Math.abs(value);
  var string = suffix;
  if (tmp === 0) {
    return numbers[0] + string;
  }
  for (var digit = 0; tmp > 0 && digit <= 4; digit++) {
    var coefficient = tmp % 10;
    if (coefficient === 0 && contains(flags, CJK_ZEROS) && string !== '') {
      string = numbers[coefficient] + string;
    } else if (coefficient > 1 || coefficient === 1 && digit === 0 || coefficient === 1 && digit === 1 && contains(flags, CJK_TEN_COEFFICIENTS) || coefficient === 1 && digit === 1 && contains(flags, CJK_TEN_HIGH_COEFFICIENTS) && value > 100 || coefficient === 1 && digit > 1 && contains(flags, CJK_HUNDRED_COEFFICIENTS)) {
      string = numbers[coefficient] + (digit > 0 ? multipliers[digit - 1] : '') + string;
    } else if (coefficient === 1 && digit > 0) {
      string = multipliers[digit - 1] + string;
    }
    tmp = Math.floor(tmp / 10);
  }
  return (value < 0 ? negativeSign : '') + string;
};
var CHINESE_INFORMAL_MULTIPLIERS = '十百千萬';
var CHINESE_FORMAL_MULTIPLIERS = '拾佰仟萬';
var JAPANESE_NEGATIVE = 'マイナス';
var KOREAN_NEGATIVE = '마이너스';
var createCounterText = function (value, type, appendSuffix) {
  var defaultSuffix = appendSuffix ? '. ' : '';
  var cjkSuffix = appendSuffix ? '、' : '';
  var koreanSuffix = appendSuffix ? ', ' : '';
  var spaceSuffix = appendSuffix ? ' ' : '';
  switch (type) {
    case 0 /* DISC */:
      return '•' + spaceSuffix;
    case 1 /* CIRCLE */:
      return '◦' + spaceSuffix;
    case 2 /* SQUARE */:
      return '◾' + spaceSuffix;
    case 5 /* DECIMAL_LEADING_ZERO */:
      var string = createCounterStyleFromRange(value, 48, 57, true, defaultSuffix);
      return string.length < 4 ? "0" + string : string;
    case 4 /* CJK_DECIMAL */:
      return createCounterStyleFromSymbols(value, '〇一二三四五六七八九', cjkSuffix);
    case 6 /* LOWER_ROMAN */:
      return createAdditiveCounter(value, 1, 3999, ROMAN_UPPER, 3 /* DECIMAL */, defaultSuffix).toLowerCase();
    case 7 /* UPPER_ROMAN */:
      return createAdditiveCounter(value, 1, 3999, ROMAN_UPPER, 3 /* DECIMAL */, defaultSuffix);
    case 8 /* LOWER_GREEK */:
      return createCounterStyleFromRange(value, 945, 969, false, defaultSuffix);
    case 9 /* LOWER_ALPHA */:
      return createCounterStyleFromRange(value, 97, 122, false, defaultSuffix);
    case 10 /* UPPER_ALPHA */:
      return createCounterStyleFromRange(value, 65, 90, false, defaultSuffix);
    case 11 /* ARABIC_INDIC */:
      return createCounterStyleFromRange(value, 1632, 1641, true, defaultSuffix);
    case 12 /* ARMENIAN */:
    case 49 /* UPPER_ARMENIAN */:
      return createAdditiveCounter(value, 1, 9999, ARMENIAN, 3 /* DECIMAL */, defaultSuffix);
    case 35 /* LOWER_ARMENIAN */:
      return createAdditiveCounter(value, 1, 9999, ARMENIAN, 3 /* DECIMAL */, defaultSuffix).toLowerCase();
    case 13 /* BENGALI */:
      return createCounterStyleFromRange(value, 2534, 2543, true, defaultSuffix);
    case 14 /* CAMBODIAN */:
    case 30 /* KHMER */:
      return createCounterStyleFromRange(value, 6112, 6121, true, defaultSuffix);
    case 15 /* CJK_EARTHLY_BRANCH */:
      return createCounterStyleFromSymbols(value, '子丑寅卯辰巳午未申酉戌亥', cjkSuffix);
    case 16 /* CJK_HEAVENLY_STEM */:
      return createCounterStyleFromSymbols(value, '甲乙丙丁戊己庚辛壬癸', cjkSuffix);
    case 17 /* CJK_IDEOGRAPHIC */:
    case 48 /* TRAD_CHINESE_INFORMAL */:
      return createCJKCounter(value, '零一二三四五六七八九', CHINESE_INFORMAL_MULTIPLIERS, '負', cjkSuffix, CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS | CJK_HUNDRED_COEFFICIENTS);
    case 47 /* TRAD_CHINESE_FORMAL */:
      return createCJKCounter(value, '零壹貳參肆伍陸柒捌玖', CHINESE_FORMAL_MULTIPLIERS, '負', cjkSuffix, CJK_ZEROS | CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS | CJK_HUNDRED_COEFFICIENTS);
    case 42 /* SIMP_CHINESE_INFORMAL */:
      return createCJKCounter(value, '零一二三四五六七八九', CHINESE_INFORMAL_MULTIPLIERS, '负', cjkSuffix, CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS | CJK_HUNDRED_COEFFICIENTS);
    case 41 /* SIMP_CHINESE_FORMAL */:
      return createCJKCounter(value, '零壹贰叁肆伍陆柒捌玖', CHINESE_FORMAL_MULTIPLIERS, '负', cjkSuffix, CJK_ZEROS | CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS | CJK_HUNDRED_COEFFICIENTS);
    case 26 /* JAPANESE_INFORMAL */:
      return createCJKCounter(value, '〇一二三四五六七八九', '十百千万', JAPANESE_NEGATIVE, cjkSuffix, 0);
    case 25 /* JAPANESE_FORMAL */:
      return createCJKCounter(value, '零壱弐参四伍六七八九', '拾百千万', JAPANESE_NEGATIVE, cjkSuffix, CJK_ZEROS | CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS);
    case 31 /* KOREAN_HANGUL_FORMAL */:
      return createCJKCounter(value, '영일이삼사오육칠팔구', '십백천만', KOREAN_NEGATIVE, koreanSuffix, CJK_ZEROS | CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS);
    case 33 /* KOREAN_HANJA_INFORMAL */:
      return createCJKCounter(value, '零一二三四五六七八九', '十百千萬', KOREAN_NEGATIVE, koreanSuffix, 0);
    case 32 /* KOREAN_HANJA_FORMAL */:
      return createCJKCounter(value, '零壹貳參四五六七八九', '拾百千', KOREAN_NEGATIVE, koreanSuffix, CJK_ZEROS | CJK_TEN_COEFFICIENTS | CJK_TEN_HIGH_COEFFICIENTS);
    case 18 /* DEVANAGARI */:
      return createCounterStyleFromRange(value, 0x966, 0x96f, true, defaultSuffix);
    case 20 /* GEORGIAN */:
      return createAdditiveCounter(value, 1, 19999, GEORGIAN, 3 /* DECIMAL */, defaultSuffix);
    case 21 /* GUJARATI */:
      return createCounterStyleFromRange(value, 0xae6, 0xaef, true, defaultSuffix);
    case 22 /* GURMUKHI */:
      return createCounterStyleFromRange(value, 0xa66, 0xa6f, true, defaultSuffix);
    case 22 /* HEBREW */:
      return createAdditiveCounter(value, 1, 10999, HEBREW, 3 /* DECIMAL */, defaultSuffix);
    case 23 /* HIRAGANA */:
      return createCounterStyleFromSymbols(value, 'あいうえおかきくけこさしすせそたちつてとなにぬねのはひふへほまみむめもやゆよらりるれろわゐゑをん');
    case 24 /* HIRAGANA_IROHA */:
      return createCounterStyleFromSymbols(value, 'いろはにほへとちりぬるをわかよたれそつねならむうゐのおくやまけふこえてあさきゆめみしゑひもせす');
    case 27 /* KANNADA */:
      return createCounterStyleFromRange(value, 0xce6, 0xcef, true, defaultSuffix);
    case 28 /* KATAKANA */:
      return createCounterStyleFromSymbols(value, 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヰヱヲン', cjkSuffix);
    case 29 /* KATAKANA_IROHA */:
      return createCounterStyleFromSymbols(value, 'イロハニホヘトチリヌルヲワカヨタレソツネナラムウヰノオクヤマケフコエテアサキユメミシヱヒモセス', cjkSuffix);
    case 34 /* LAO */:
      return createCounterStyleFromRange(value, 0xed0, 0xed9, true, defaultSuffix);
    case 37 /* MONGOLIAN */:
      return createCounterStyleFromRange(value, 0x1810, 0x1819, true, defaultSuffix);
    case 38 /* MYANMAR */:
      return createCounterStyleFromRange(value, 0x1040, 0x1049, true, defaultSuffix);
    case 39 /* ORIYA */:
      return createCounterStyleFromRange(value, 0xb66, 0xb6f, true, defaultSuffix);
    case 40 /* PERSIAN */:
      return createCounterStyleFromRange(value, 0x6f0, 0x6f9, true, defaultSuffix);
    case 43 /* TAMIL */:
      return createCounterStyleFromRange(value, 0xbe6, 0xbef, true, defaultSuffix);
    case 44 /* TELUGU */:
      return createCounterStyleFromRange(value, 0xc66, 0xc6f, true, defaultSuffix);
    case 45 /* THAI */:
      return createCounterStyleFromRange(value, 0xe50, 0xe59, true, defaultSuffix);
    case 46 /* TIBETAN */:
      return createCounterStyleFromRange(value, 0xf20, 0xf29, true, defaultSuffix);
    case 3 /* DECIMAL */:
    default:
      return createCounterStyleFromRange(value, 48, 57, true, defaultSuffix);
  }
};
var IGNORE_ATTRIBUTE = 'data-html2canvas-ignore';
var DocumentCloner = /** @class */function () {
  function DocumentCloner(context, element, options) {
    this.context = context;
    this.options = options;
    this.scrolledElements = [];
    this.referenceElement = element;
    this.counters = new CounterState();
    this.quoteDepth = 0;
    if (!element.ownerDocument) {
      throw new Error('Cloned element does not have an owner document');
    }
    this.documentElement = this.cloneNode(element.ownerDocument.documentElement, false);
  }
  DocumentCloner.prototype.toIFrame = function (ownerDocument, windowSize) {
    var _this = this;
    var iframe = createIFrameContainer(ownerDocument, windowSize);
    if (!iframe.contentWindow) {
      return Promise.reject("Unable to find iframe window");
    }
    var scrollX = ownerDocument.defaultView.pageXOffset;
    var scrollY = ownerDocument.defaultView.pageYOffset;
    var cloneWindow = iframe.contentWindow;
    var documentClone = cloneWindow.document;
    /* Chrome doesn't detect relative background-images assigned in inline <style> sheets when fetched through getComputedStyle
     if window url is about:blank, we can assign the url to current by writing onto the document
     */
    var iframeLoad = iframeLoader(iframe).then(function () {
      return __awaiter(_this, void 0, void 0, function () {
        var onclone, referenceElement;
        return __generator(this, function (_a) {
          switch (_a.label) {
            case 0:
              this.scrolledElements.forEach(restoreNodeScroll);
              if (cloneWindow) {
                cloneWindow.scrollTo(windowSize.left, windowSize.top);
                if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent) && (cloneWindow.scrollY !== windowSize.top || cloneWindow.scrollX !== windowSize.left)) {
                  this.context.logger.warn('Unable to restore scroll position for cloned document');
                  this.context.windowBounds = this.context.windowBounds.add(cloneWindow.scrollX - windowSize.left, cloneWindow.scrollY - windowSize.top, 0, 0);
                }
              }
              onclone = this.options.onclone;
              referenceElement = this.clonedReferenceElement;
              if (typeof referenceElement === 'undefined') {
                return [2 /*return*/, Promise.reject("Error finding the " + this.referenceElement.nodeName + " in the cloned document")];
              }
              if (!(documentClone.fonts && documentClone.fonts.ready)) return [3 /*break*/, 2];
              return [4 /*yield*/, documentClone.fonts.ready];
            case 1:
              _a.sent();
              _a.label = 2;
            case 2:
              if (!/(AppleWebKit)/g.test(navigator.userAgent)) return [3 /*break*/, 4];
              return [4 /*yield*/, imagesReady(documentClone)];
            case 3:
              _a.sent();
              _a.label = 4;
            case 4:
              if (typeof onclone === 'function') {
                return [2 /*return*/, Promise.resolve().then(function () {
                  return onclone(documentClone, referenceElement);
                }).then(function () {
                  return iframe;
                })];
              }
              return [2 /*return*/, iframe];
          }
        });
      });
    });
    documentClone.open();
    documentClone.write(serializeDoctype(document.doctype) + "<html></html>");
    // Chrome scrolls the parent document for some reason after the write to the cloned window???
    restoreOwnerScroll(this.referenceElement.ownerDocument, scrollX, scrollY);
    documentClone.replaceChild(documentClone.adoptNode(this.documentElement), documentClone.documentElement);
    documentClone.close();
    return iframeLoad;
  };
  DocumentCloner.prototype.createElementClone = function (node) {
    if (isDebugging(node, 2 /* CLONE */)) {
      debugger;
    }
    if (isCanvasElement(node)) {
      return this.createCanvasClone(node);
    }
    if (isVideoElement(node)) {
      return this.createVideoClone(node);
    }
    if (isStyleElement(node)) {
      return this.createStyleClone(node);
    }
    var clone = node.cloneNode(false);
    if (isImageElement(clone)) {
      if (isImageElement(node) && node.currentSrc && node.currentSrc !== node.src) {
        clone.src = node.currentSrc;
        clone.srcset = '';
      }
      if (clone.loading === 'lazy') {
        clone.loading = 'eager';
      }
    }
    if (isCustomElement(clone)) {
      return this.createCustomElementClone(clone);
    }
    return clone;
  };
  DocumentCloner.prototype.createCustomElementClone = function (node) {
    var clone = document.createElement('html2canvascustomelement');
    copyCSSStyles(node.style, clone);
    return clone;
  };
  DocumentCloner.prototype.createStyleClone = function (node) {
    try {
      var sheet = node.sheet;
      if (sheet && sheet.cssRules) {
        var css = [].slice.call(sheet.cssRules, 0).reduce(function (css, rule) {
          if (rule && typeof rule.cssText === 'string') {
            return css + rule.cssText;
          }
          return css;
        }, '');
        var style = node.cloneNode(false);
        style.textContent = css;
        return style;
      }
    } catch (e) {
      // accessing node.sheet.cssRules throws a DOMException
      this.context.logger.error('Unable to access cssRules property', e);
      if (e.name !== 'SecurityError') {
        throw e;
      }
    }
    return node.cloneNode(false);
  };
  DocumentCloner.prototype.createCanvasClone = function (canvas) {
    var _a;
    if (this.options.inlineImages && canvas.ownerDocument) {
      var img = canvas.ownerDocument.createElement('img');
      try {
        img.src = canvas.toDataURL();
        return img;
      } catch (e) {
        this.context.logger.info("Unable to inline canvas contents, canvas is tainted", canvas);
      }
    }
    var clonedCanvas = canvas.cloneNode(false);
    try {
      clonedCanvas.width = canvas.width;
      clonedCanvas.height = canvas.height;
      var ctx = canvas.getContext('2d');
      var clonedCtx = clonedCanvas.getContext('2d');
      if (clonedCtx) {
        if (!this.options.allowTaint && ctx) {
          clonedCtx.putImageData(ctx.getImageData(0, 0, canvas.width, canvas.height), 0, 0);
        } else {
          var gl = (_a = canvas.getContext('webgl2')) !== null && _a !== void 0 ? _a : canvas.getContext('webgl');
          if (gl) {
            var attribs = gl.getContextAttributes();
            if ((attribs === null || attribs === void 0 ? void 0 : attribs.preserveDrawingBuffer) === false) {
              this.context.logger.warn('Unable to clone WebGL context as it has preserveDrawingBuffer=false', canvas);
            }
          }
          clonedCtx.drawImage(canvas, 0, 0);
        }
      }
      return clonedCanvas;
    } catch (e) {
      this.context.logger.info("Unable to clone canvas as it is tainted", canvas);
    }
    return clonedCanvas;
  };
  DocumentCloner.prototype.createVideoClone = function (video) {
    var canvas = video.ownerDocument.createElement('canvas');
    canvas.width = video.offsetWidth;
    canvas.height = video.offsetHeight;
    var ctx = canvas.getContext('2d');
    try {
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        if (!this.options.allowTaint) {
          ctx.getImageData(0, 0, canvas.width, canvas.height);
        }
      }
      return canvas;
    } catch (e) {
      this.context.logger.info("Unable to clone video as it is tainted", video);
    }
    var blankCanvas = video.ownerDocument.createElement('canvas');
    blankCanvas.width = video.offsetWidth;
    blankCanvas.height = video.offsetHeight;
    return blankCanvas;
  };
  DocumentCloner.prototype.appendChildNode = function (clone, child, copyStyles) {
    if (!isElementNode(child) || !isScriptElement(child) && !child.hasAttribute(IGNORE_ATTRIBUTE) && (typeof this.options.ignoreElements !== 'function' || !this.options.ignoreElements(child))) {
      if (!this.options.copyStyles || !isElementNode(child) || !isStyleElement(child)) {
        clone.appendChild(this.cloneNode(child, copyStyles));
      }
    }
  };
  DocumentCloner.prototype.cloneChildNodes = function (node, clone, copyStyles) {
    var _this = this;
    for (var child = node.shadowRoot ? node.shadowRoot.firstChild : node.firstChild; child; child = child.nextSibling) {
      if (isElementNode(child) && isSlotElement(child) && typeof child.assignedNodes === 'function') {
        var assignedNodes = child.assignedNodes();
        if (assignedNodes.length) {
          assignedNodes.forEach(function (assignedNode) {
            return _this.appendChildNode(clone, assignedNode, copyStyles);
          });
        }
      } else {
        this.appendChildNode(clone, child, copyStyles);
      }
    }
  };
  DocumentCloner.prototype.cloneNode = function (node, copyStyles) {
    if (isTextNode(node)) {
      return document.createTextNode(node.data);
    }
    if (!node.ownerDocument) {
      return node.cloneNode(false);
    }
    var window = node.ownerDocument.defaultView;
    if (window && isElementNode(node) && (isHTMLElementNode(node) || isSVGElementNode(node))) {
      var clone = this.createElementClone(node);
      clone.style.transitionProperty = 'none';
      var style = window.getComputedStyle(node);
      var styleBefore = window.getComputedStyle(node, ':before');
      var styleAfter = window.getComputedStyle(node, ':after');
      if (this.referenceElement === node && isHTMLElementNode(clone)) {
        this.clonedReferenceElement = clone;
      }
      if (isBodyElement(clone)) {
        createPseudoHideStyles(clone);
      }
      var counters = this.counters.parse(new CSSParsedCounterDeclaration(this.context, style));
      var before = this.resolvePseudoContent(node, clone, styleBefore, PseudoElementType.BEFORE);
      if (isCustomElement(node)) {
        copyStyles = true;
      }
      if (!isVideoElement(node)) {
        this.cloneChildNodes(node, clone, copyStyles);
      }
      if (before) {
        clone.insertBefore(before, clone.firstChild);
      }
      var after = this.resolvePseudoContent(node, clone, styleAfter, PseudoElementType.AFTER);
      if (after) {
        clone.appendChild(after);
      }
      this.counters.pop(counters);
      if (style && (this.options.copyStyles || isSVGElementNode(node)) && !isIFrameElement(node) || copyStyles) {
        copyCSSStyles(style, clone);
      }
      if (node.scrollTop !== 0 || node.scrollLeft !== 0) {
        this.scrolledElements.push([clone, node.scrollLeft, node.scrollTop]);
      }
      if ((isTextareaElement(node) || isSelectElement(node)) && (isTextareaElement(clone) || isSelectElement(clone))) {
        clone.value = node.value;
      }
      return clone;
    }
    return node.cloneNode(false);
  };
  DocumentCloner.prototype.resolvePseudoContent = function (node, clone, style, pseudoElt) {
    var _this = this;
    if (!style) {
      return;
    }
    var value = style.content;
    var document = clone.ownerDocument;
    if (!document || !value || value === 'none' || value === '-moz-alt-content' || style.display === 'none') {
      return;
    }
    this.counters.parse(new CSSParsedCounterDeclaration(this.context, style));
    var declaration = new CSSParsedPseudoDeclaration(this.context, style);
    var anonymousReplacedElement = document.createElement('html2canvaspseudoelement');
    copyCSSStyles(style, anonymousReplacedElement);
    declaration.content.forEach(function (token) {
      if (token.type === 0 /* STRING_TOKEN */) {
        anonymousReplacedElement.appendChild(document.createTextNode(token.value));
      } else if (token.type === 22 /* URL_TOKEN */) {
        var img = document.createElement('img');
        img.src = token.value;
        img.style.opacity = '1';
        anonymousReplacedElement.appendChild(img);
      } else if (token.type === 18 /* FUNCTION */) {
        if (token.name === 'attr') {
          var attr = token.values.filter(isIdentToken);
          if (attr.length) {
            anonymousReplacedElement.appendChild(document.createTextNode(node.getAttribute(attr[0].value) || ''));
          }
        } else if (token.name === 'counter') {
          var _a = token.values.filter(nonFunctionArgSeparator),
            counter = _a[0],
            counterStyle = _a[1];
          if (counter && isIdentToken(counter)) {
            var counterState = _this.counters.getCounterValue(counter.value);
            var counterType = counterStyle && isIdentToken(counterStyle) ? listStyleType.parse(_this.context, counterStyle.value) : 3 /* DECIMAL */;
            anonymousReplacedElement.appendChild(document.createTextNode(createCounterText(counterState, counterType, false)));
          }
        } else if (token.name === 'counters') {
          var _b = token.values.filter(nonFunctionArgSeparator),
            counter = _b[0],
            delim = _b[1],
            counterStyle = _b[2];
          if (counter && isIdentToken(counter)) {
            var counterStates = _this.counters.getCounterValues(counter.value);
            var counterType_1 = counterStyle && isIdentToken(counterStyle) ? listStyleType.parse(_this.context, counterStyle.value) : 3 /* DECIMAL */;
            var separator = delim && delim.type === 0 /* STRING_TOKEN */ ? delim.value : '';
            var text = counterStates.map(function (value) {
              return createCounterText(value, counterType_1, false);
            }).join(separator);
            anonymousReplacedElement.appendChild(document.createTextNode(text));
          }
        } else ;
      } else if (token.type === 20 /* IDENT_TOKEN */) {
        switch (token.value) {
          case 'open-quote':
            anonymousReplacedElement.appendChild(document.createTextNode(getQuote(declaration.quotes, _this.quoteDepth++, true)));
            break;
          case 'close-quote':
            anonymousReplacedElement.appendChild(document.createTextNode(getQuote(declaration.quotes, --_this.quoteDepth, false)));
            break;
          default:
            // safari doesn't parse string tokens correctly because of lack of quotes
            anonymousReplacedElement.appendChild(document.createTextNode(token.value));
        }
      }
    });
    anonymousReplacedElement.className = PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + " " + PSEUDO_HIDE_ELEMENT_CLASS_AFTER;
    var newClassName = pseudoElt === PseudoElementType.BEFORE ? " " + PSEUDO_HIDE_ELEMENT_CLASS_BEFORE : " " + PSEUDO_HIDE_ELEMENT_CLASS_AFTER;
    if (isSVGElementNode(clone)) {
      clone.className.baseValue += newClassName;
    } else {
      clone.className += newClassName;
    }
    return anonymousReplacedElement;
  };
  DocumentCloner.destroy = function (container) {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
      return true;
    }
    return false;
  };
  return DocumentCloner;
}();
var PseudoElementType;
(function (PseudoElementType) {
  PseudoElementType[PseudoElementType["BEFORE"] = 0] = "BEFORE";
  PseudoElementType[PseudoElementType["AFTER"] = 1] = "AFTER";
})(PseudoElementType || (PseudoElementType = {}));
var createIFrameContainer = function (ownerDocument, bounds) {
  var cloneIframeContainer = ownerDocument.createElement('iframe');
  cloneIframeContainer.className = 'html2canvas-container';
  cloneIframeContainer.style.visibility = 'hidden';
  cloneIframeContainer.style.position = 'fixed';
  cloneIframeContainer.style.left = '-10000px';
  cloneIframeContainer.style.top = '0px';
  cloneIframeContainer.style.border = '0';
  cloneIframeContainer.width = bounds.width.toString();
  cloneIframeContainer.height = bounds.height.toString();
  cloneIframeContainer.scrolling = 'no'; // ios won't scroll without it
  cloneIframeContainer.setAttribute(IGNORE_ATTRIBUTE, 'true');
  ownerDocument.body.appendChild(cloneIframeContainer);
  return cloneIframeContainer;
};
var imageReady = function (img) {
  return new Promise(function (resolve) {
    if (img.complete) {
      resolve();
      return;
    }
    if (!img.src) {
      resolve();
      return;
    }
    img.onload = resolve;
    img.onerror = resolve;
  });
};
var imagesReady = function (document) {
  return Promise.all([].slice.call(document.images, 0).map(imageReady));
};
var iframeLoader = function (iframe) {
  return new Promise(function (resolve, reject) {
    var cloneWindow = iframe.contentWindow;
    if (!cloneWindow) {
      return reject("No window assigned for iframe");
    }
    var documentClone = cloneWindow.document;
    cloneWindow.onload = iframe.onload = function () {
      cloneWindow.onload = iframe.onload = null;
      var interval = setInterval(function () {
        if (documentClone.body.childNodes.length > 0 && documentClone.readyState === 'complete') {
          clearInterval(interval);
          resolve(iframe);
        }
      }, 50);
    };
  });
};
var ignoredStyleProperties = ['all', 'd', 'content' // Safari shows pseudoelements if content is set
];
var copyCSSStyles = function (style, target) {
  // Edge does not provide value for cssText
  for (var i = style.length - 1; i >= 0; i--) {
    var property = style.item(i);
    if (ignoredStyleProperties.indexOf(property) === -1) {
      target.style.setProperty(property, style.getPropertyValue(property));
    }
  }
  return target;
};
var serializeDoctype = function (doctype) {
  var str = '';
  if (doctype) {
    str += '<!DOCTYPE ';
    if (doctype.name) {
      str += doctype.name;
    }
    if (doctype.internalSubset) {
      str += doctype.internalSubset;
    }
    if (doctype.publicId) {
      str += "\"" + doctype.publicId + "\"";
    }
    if (doctype.systemId) {
      str += "\"" + doctype.systemId + "\"";
    }
    str += '>';
  }
  return str;
};
var restoreOwnerScroll = function (ownerDocument, x, y) {
  if (ownerDocument && ownerDocument.defaultView && (x !== ownerDocument.defaultView.pageXOffset || y !== ownerDocument.defaultView.pageYOffset)) {
    ownerDocument.defaultView.scrollTo(x, y);
  }
};
var restoreNodeScroll = function (_a) {
  var element = _a[0],
    x = _a[1],
    y = _a[2];
  element.scrollLeft = x;
  element.scrollTop = y;
};
var PSEUDO_BEFORE = ':before';
var PSEUDO_AFTER = ':after';
var PSEUDO_HIDE_ELEMENT_CLASS_BEFORE = '___html2canvas___pseudoelement_before';
var PSEUDO_HIDE_ELEMENT_CLASS_AFTER = '___html2canvas___pseudoelement_after';
var PSEUDO_HIDE_ELEMENT_STYLE = "{\n    content: \"\" !important;\n    display: none !important;\n}";
var createPseudoHideStyles = function (body) {
  createStyles(body, "." + PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + PSEUDO_BEFORE + PSEUDO_HIDE_ELEMENT_STYLE + "\n         ." + PSEUDO_HIDE_ELEMENT_CLASS_AFTER + PSEUDO_AFTER + PSEUDO_HIDE_ELEMENT_STYLE);
};
var createStyles = function (body, styles) {
  var document = body.ownerDocument;
  if (document) {
    var style = document.createElement('style');
    style.textContent = styles;
    body.appendChild(style);
  }
};
var CacheStorage = /** @class */function () {
  function CacheStorage() {}
  CacheStorage.getOrigin = function (url) {
    var link = CacheStorage._link;
    if (!link) {
      return 'about:blank';
    }
    link.href = url;
    link.href = link.href; // IE9, LOL! - http://jsfiddle.net/niklasvh/2e48b/
    return link.protocol + link.hostname + link.port;
  };
  CacheStorage.isSameOrigin = function (src) {
    return CacheStorage.getOrigin(src) === CacheStorage._origin;
  };
  CacheStorage.setContext = function (window) {
    CacheStorage._link = window.document.createElement('a');
    CacheStorage._origin = CacheStorage.getOrigin(window.location.href);
  };
  CacheStorage._origin = 'about:blank';
  return CacheStorage;
}();
var Cache$1 = /** @class */function () {
  function Cache(context, _options) {
    this.context = context;
    this._options = _options;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._cache = {};
  }
  Cache.prototype.addImage = function (src) {
    var result = Promise.resolve();
    if (this.has(src)) {
      return result;
    }
    if (isBlobImage(src) || isRenderable(src)) {
      (this._cache[src] = this.loadImage(src)).catch(function () {
        // prevent unhandled rejection
      });
      return result;
    }
    return result;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Cache.prototype.match = function (src) {
    return this._cache[src];
  };
  Cache.prototype.loadImage = function (key) {
    return __awaiter(this, void 0, void 0, function () {
      var isSameOrigin, useCORS, useProxy, src;
      var _this = this;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            isSameOrigin = CacheStorage.isSameOrigin(key);
            useCORS = !isInlineImage(key) && this._options.useCORS === true && FEATURES.SUPPORT_CORS_IMAGES && !isSameOrigin;
            useProxy = !isInlineImage(key) && !isSameOrigin && !isBlobImage(key) && typeof this._options.proxy === 'string' && FEATURES.SUPPORT_CORS_XHR && !useCORS;
            if (!isSameOrigin && this._options.allowTaint === false && !isInlineImage(key) && !isBlobImage(key) && !useProxy && !useCORS) {
              return [2 /*return*/];
            }
            src = key;
            if (!useProxy) return [3 /*break*/, 2];
            return [4 /*yield*/, this.proxy(src)];
          case 1:
            src = _a.sent();
            _a.label = 2;
          case 2:
            this.context.logger.debug("Added image " + key.substring(0, 256));
            return [4 /*yield*/, new Promise(function (resolve, reject) {
              var img = new Image();
              img.onload = function () {
                return resolve(img);
              };
              img.onerror = reject;
              //ios safari 10.3 taints canvas with data urls unless crossOrigin is set to anonymous
              if (isInlineBase64Image(src) || useCORS) {
                img.crossOrigin = 'anonymous';
              }
              img.src = src;
              if (img.complete === true) {
                // Inline XML images may fail to parse, throwing an Error later on
                setTimeout(function () {
                  return resolve(img);
                }, 500);
              }
              if (_this._options.imageTimeout > 0) {
                setTimeout(function () {
                  return reject("Timed out (" + _this._options.imageTimeout + "ms) loading image");
                }, _this._options.imageTimeout);
              }
            })];
          case 3:
            return [2 /*return*/, _a.sent()];
        }
      });
    });
  };
  Cache.prototype.has = function (key) {
    return typeof this._cache[key] !== 'undefined';
  };
  Cache.prototype.keys = function () {
    return Promise.resolve(Object.keys(this._cache));
  };
  Cache.prototype.proxy = function (src) {
    var _this = this;
    var proxy = this._options.proxy;
    if (!proxy) {
      throw new Error('No proxy defined');
    }
    var key = src.substring(0, 256);
    return new Promise(function (resolve, reject) {
      var responseType = FEATURES.SUPPORT_RESPONSE_TYPE ? 'blob' : 'text';
      var xhr = new XMLHttpRequest();
      xhr.onload = function () {
        if (xhr.status === 200) {
          if (responseType === 'text') {
            resolve(xhr.response);
          } else {
            var reader_1 = new FileReader();
            reader_1.addEventListener('load', function () {
              return resolve(reader_1.result);
            }, false);
            reader_1.addEventListener('error', function (e) {
              return reject(e);
            }, false);
            reader_1.readAsDataURL(xhr.response);
          }
        } else {
          reject("Failed to proxy resource " + key + " with status code " + xhr.status);
        }
      };
      xhr.onerror = reject;
      var queryString = proxy.indexOf('?') > -1 ? '&' : '?';
      xhr.open('GET', "" + proxy + queryString + "url=" + encodeURIComponent(src) + "&responseType=" + responseType);
      if (responseType !== 'text' && xhr instanceof XMLHttpRequest) {
        xhr.responseType = responseType;
      }
      if (_this._options.imageTimeout) {
        var timeout_1 = _this._options.imageTimeout;
        xhr.timeout = timeout_1;
        xhr.ontimeout = function () {
          return reject("Timed out (" + timeout_1 + "ms) proxying " + key);
        };
      }
      xhr.send();
    });
  };
  return Cache;
}();
var INLINE_SVG = /^data:image\/svg\+xml/i;
var INLINE_BASE64 = /^data:image\/.*;base64,/i;
var INLINE_IMG = /^data:image\/.*/i;
var isRenderable = function (src) {
  return FEATURES.SUPPORT_SVG_DRAWING || !isSVG(src);
};
var isInlineImage = function (src) {
  return INLINE_IMG.test(src);
};
var isInlineBase64Image = function (src) {
  return INLINE_BASE64.test(src);
};
var isBlobImage = function (src) {
  return src.substr(0, 4) === 'blob';
};
var isSVG = function (src) {
  return src.substr(-3).toLowerCase() === 'svg' || INLINE_SVG.test(src);
};
var Vector = /** @class */function () {
  function Vector(x, y) {
    this.type = 0 /* VECTOR */;
    this.x = x;
    this.y = y;
  }
  Vector.prototype.add = function (deltaX, deltaY) {
    return new Vector(this.x + deltaX, this.y + deltaY);
  };
  return Vector;
}();
var lerp$2 = function (a, b, t) {
  return new Vector(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
};
var BezierCurve = /** @class */function () {
  function BezierCurve(start, startControl, endControl, end) {
    this.type = 1 /* BEZIER_CURVE */;
    this.start = start;
    this.startControl = startControl;
    this.endControl = endControl;
    this.end = end;
  }
  BezierCurve.prototype.subdivide = function (t, firstHalf) {
    var ab = lerp$2(this.start, this.startControl, t);
    var bc = lerp$2(this.startControl, this.endControl, t);
    var cd = lerp$2(this.endControl, this.end, t);
    var abbc = lerp$2(ab, bc, t);
    var bccd = lerp$2(bc, cd, t);
    var dest = lerp$2(abbc, bccd, t);
    return firstHalf ? new BezierCurve(this.start, ab, abbc, dest) : new BezierCurve(dest, bccd, cd, this.end);
  };
  BezierCurve.prototype.add = function (deltaX, deltaY) {
    return new BezierCurve(this.start.add(deltaX, deltaY), this.startControl.add(deltaX, deltaY), this.endControl.add(deltaX, deltaY), this.end.add(deltaX, deltaY));
  };
  BezierCurve.prototype.reverse = function () {
    return new BezierCurve(this.end, this.endControl, this.startControl, this.start);
  };
  return BezierCurve;
}();
var isBezierCurve = function (path) {
  return path.type === 1 /* BEZIER_CURVE */;
};
var BoundCurves = /** @class */function () {
  function BoundCurves(element) {
    var styles = element.styles;
    var bounds = element.bounds;
    var _a = getAbsoluteValueForTuple(styles.borderTopLeftRadius, bounds.width, bounds.height),
      tlh = _a[0],
      tlv = _a[1];
    var _b = getAbsoluteValueForTuple(styles.borderTopRightRadius, bounds.width, bounds.height),
      trh = _b[0],
      trv = _b[1];
    var _c = getAbsoluteValueForTuple(styles.borderBottomRightRadius, bounds.width, bounds.height),
      brh = _c[0],
      brv = _c[1];
    var _d = getAbsoluteValueForTuple(styles.borderBottomLeftRadius, bounds.width, bounds.height),
      blh = _d[0],
      blv = _d[1];
    var factors = [];
    factors.push((tlh + trh) / bounds.width);
    factors.push((blh + brh) / bounds.width);
    factors.push((tlv + blv) / bounds.height);
    factors.push((trv + brv) / bounds.height);
    var maxFactor = Math.max.apply(Math, factors);
    if (maxFactor > 1) {
      tlh /= maxFactor;
      tlv /= maxFactor;
      trh /= maxFactor;
      trv /= maxFactor;
      brh /= maxFactor;
      brv /= maxFactor;
      blh /= maxFactor;
      blv /= maxFactor;
    }
    var topWidth = bounds.width - trh;
    var rightHeight = bounds.height - brv;
    var bottomWidth = bounds.width - brh;
    var leftHeight = bounds.height - blv;
    var borderTopWidth = styles.borderTopWidth;
    var borderRightWidth = styles.borderRightWidth;
    var borderBottomWidth = styles.borderBottomWidth;
    var borderLeftWidth = styles.borderLeftWidth;
    var paddingTop = getAbsoluteValue(styles.paddingTop, element.bounds.width);
    var paddingRight = getAbsoluteValue(styles.paddingRight, element.bounds.width);
    var paddingBottom = getAbsoluteValue(styles.paddingBottom, element.bounds.width);
    var paddingLeft = getAbsoluteValue(styles.paddingLeft, element.bounds.width);
    this.topLeftBorderDoubleOuterBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + borderLeftWidth / 3, bounds.top + borderTopWidth / 3, tlh - borderLeftWidth / 3, tlv - borderTopWidth / 3, CORNER.TOP_LEFT) : new Vector(bounds.left + borderLeftWidth / 3, bounds.top + borderTopWidth / 3);
    this.topRightBorderDoubleOuterBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + topWidth, bounds.top + borderTopWidth / 3, trh - borderRightWidth / 3, trv - borderTopWidth / 3, CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth / 3, bounds.top + borderTopWidth / 3);
    this.bottomRightBorderDoubleOuterBox = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + bottomWidth, bounds.top + rightHeight, brh - borderRightWidth / 3, brv - borderBottomWidth / 3, CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth / 3, bounds.top + bounds.height - borderBottomWidth / 3);
    this.bottomLeftBorderDoubleOuterBox = blh > 0 || blv > 0 ? getCurvePoints(bounds.left + borderLeftWidth / 3, bounds.top + leftHeight, blh - borderLeftWidth / 3, blv - borderBottomWidth / 3, CORNER.BOTTOM_LEFT) : new Vector(bounds.left + borderLeftWidth / 3, bounds.top + bounds.height - borderBottomWidth / 3);
    this.topLeftBorderDoubleInnerBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + borderLeftWidth * 2 / 3, bounds.top + borderTopWidth * 2 / 3, tlh - borderLeftWidth * 2 / 3, tlv - borderTopWidth * 2 / 3, CORNER.TOP_LEFT) : new Vector(bounds.left + borderLeftWidth * 2 / 3, bounds.top + borderTopWidth * 2 / 3);
    this.topRightBorderDoubleInnerBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + topWidth, bounds.top + borderTopWidth * 2 / 3, trh - borderRightWidth * 2 / 3, trv - borderTopWidth * 2 / 3, CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth * 2 / 3, bounds.top + borderTopWidth * 2 / 3);
    this.bottomRightBorderDoubleInnerBox = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + bottomWidth, bounds.top + rightHeight, brh - borderRightWidth * 2 / 3, brv - borderBottomWidth * 2 / 3, CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth * 2 / 3, bounds.top + bounds.height - borderBottomWidth * 2 / 3);
    this.bottomLeftBorderDoubleInnerBox = blh > 0 || blv > 0 ? getCurvePoints(bounds.left + borderLeftWidth * 2 / 3, bounds.top + leftHeight, blh - borderLeftWidth * 2 / 3, blv - borderBottomWidth * 2 / 3, CORNER.BOTTOM_LEFT) : new Vector(bounds.left + borderLeftWidth * 2 / 3, bounds.top + bounds.height - borderBottomWidth * 2 / 3);
    this.topLeftBorderStroke = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + borderLeftWidth / 2, bounds.top + borderTopWidth / 2, tlh - borderLeftWidth / 2, tlv - borderTopWidth / 2, CORNER.TOP_LEFT) : new Vector(bounds.left + borderLeftWidth / 2, bounds.top + borderTopWidth / 2);
    this.topRightBorderStroke = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + topWidth, bounds.top + borderTopWidth / 2, trh - borderRightWidth / 2, trv - borderTopWidth / 2, CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth / 2, bounds.top + borderTopWidth / 2);
    this.bottomRightBorderStroke = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + bottomWidth, bounds.top + rightHeight, brh - borderRightWidth / 2, brv - borderBottomWidth / 2, CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth / 2, bounds.top + bounds.height - borderBottomWidth / 2);
    this.bottomLeftBorderStroke = blh > 0 || blv > 0 ? getCurvePoints(bounds.left + borderLeftWidth / 2, bounds.top + leftHeight, blh - borderLeftWidth / 2, blv - borderBottomWidth / 2, CORNER.BOTTOM_LEFT) : new Vector(bounds.left + borderLeftWidth / 2, bounds.top + bounds.height - borderBottomWidth / 2);
    this.topLeftBorderBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left, bounds.top, tlh, tlv, CORNER.TOP_LEFT) : new Vector(bounds.left, bounds.top);
    this.topRightBorderBox = trh > 0 || trv > 0 ? getCurvePoints(bounds.left + topWidth, bounds.top, trh, trv, CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width, bounds.top);
    this.bottomRightBorderBox = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + bottomWidth, bounds.top + rightHeight, brh, brv, CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width, bounds.top + bounds.height);
    this.bottomLeftBorderBox = blh > 0 || blv > 0 ? getCurvePoints(bounds.left, bounds.top + leftHeight, blh, blv, CORNER.BOTTOM_LEFT) : new Vector(bounds.left, bounds.top + bounds.height);
    this.topLeftPaddingBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + borderLeftWidth, bounds.top + borderTopWidth, Math.max(0, tlh - borderLeftWidth), Math.max(0, tlv - borderTopWidth), CORNER.TOP_LEFT) : new Vector(bounds.left + borderLeftWidth, bounds.top + borderTopWidth);
    this.topRightPaddingBox = trh > 0 || trv > 0 ? getCurvePoints(bounds.left + Math.min(topWidth, bounds.width - borderRightWidth), bounds.top + borderTopWidth, topWidth > bounds.width + borderRightWidth ? 0 : Math.max(0, trh - borderRightWidth), Math.max(0, trv - borderTopWidth), CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth, bounds.top + borderTopWidth);
    this.bottomRightPaddingBox = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + Math.min(bottomWidth, bounds.width - borderLeftWidth), bounds.top + Math.min(rightHeight, bounds.height - borderBottomWidth), Math.max(0, brh - borderRightWidth), Math.max(0, brv - borderBottomWidth), CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width - borderRightWidth, bounds.top + bounds.height - borderBottomWidth);
    this.bottomLeftPaddingBox = blh > 0 || blv > 0 ? getCurvePoints(bounds.left + borderLeftWidth, bounds.top + Math.min(leftHeight, bounds.height - borderBottomWidth), Math.max(0, blh - borderLeftWidth), Math.max(0, blv - borderBottomWidth), CORNER.BOTTOM_LEFT) : new Vector(bounds.left + borderLeftWidth, bounds.top + bounds.height - borderBottomWidth);
    this.topLeftContentBox = tlh > 0 || tlv > 0 ? getCurvePoints(bounds.left + borderLeftWidth + paddingLeft, bounds.top + borderTopWidth + paddingTop, Math.max(0, tlh - (borderLeftWidth + paddingLeft)), Math.max(0, tlv - (borderTopWidth + paddingTop)), CORNER.TOP_LEFT) : new Vector(bounds.left + borderLeftWidth + paddingLeft, bounds.top + borderTopWidth + paddingTop);
    this.topRightContentBox = trh > 0 || trv > 0 ? getCurvePoints(bounds.left + Math.min(topWidth, bounds.width + borderLeftWidth + paddingLeft), bounds.top + borderTopWidth + paddingTop, topWidth > bounds.width + borderLeftWidth + paddingLeft ? 0 : trh - borderLeftWidth + paddingLeft, trv - (borderTopWidth + paddingTop), CORNER.TOP_RIGHT) : new Vector(bounds.left + bounds.width - (borderRightWidth + paddingRight), bounds.top + borderTopWidth + paddingTop);
    this.bottomRightContentBox = brh > 0 || brv > 0 ? getCurvePoints(bounds.left + Math.min(bottomWidth, bounds.width - (borderLeftWidth + paddingLeft)), bounds.top + Math.min(rightHeight, bounds.height + borderTopWidth + paddingTop), Math.max(0, brh - (borderRightWidth + paddingRight)), brv - (borderBottomWidth + paddingBottom), CORNER.BOTTOM_RIGHT) : new Vector(bounds.left + bounds.width - (borderRightWidth + paddingRight), bounds.top + bounds.height - (borderBottomWidth + paddingBottom));
    this.bottomLeftContentBox = blh > 0 || blv > 0 ? getCurvePoints(bounds.left + borderLeftWidth + paddingLeft, bounds.top + leftHeight, Math.max(0, blh - (borderLeftWidth + paddingLeft)), blv - (borderBottomWidth + paddingBottom), CORNER.BOTTOM_LEFT) : new Vector(bounds.left + borderLeftWidth + paddingLeft, bounds.top + bounds.height - (borderBottomWidth + paddingBottom));
  }
  return BoundCurves;
}();
var CORNER;
(function (CORNER) {
  CORNER[CORNER["TOP_LEFT"] = 0] = "TOP_LEFT";
  CORNER[CORNER["TOP_RIGHT"] = 1] = "TOP_RIGHT";
  CORNER[CORNER["BOTTOM_RIGHT"] = 2] = "BOTTOM_RIGHT";
  CORNER[CORNER["BOTTOM_LEFT"] = 3] = "BOTTOM_LEFT";
})(CORNER || (CORNER = {}));
var getCurvePoints = function (x, y, r1, r2, position) {
  var kappa = 4 * ((Math.sqrt(2) - 1) / 3);
  var ox = r1 * kappa; // control point offset horizontal
  var oy = r2 * kappa; // control point offset vertical
  var xm = x + r1; // x-middle
  var ym = y + r2; // y-middle
  switch (position) {
    case CORNER.TOP_LEFT:
      return new BezierCurve(new Vector(x, ym), new Vector(x, ym - oy), new Vector(xm - ox, y), new Vector(xm, y));
    case CORNER.TOP_RIGHT:
      return new BezierCurve(new Vector(x, y), new Vector(x + ox, y), new Vector(xm, ym - oy), new Vector(xm, ym));
    case CORNER.BOTTOM_RIGHT:
      return new BezierCurve(new Vector(xm, y), new Vector(xm, y + oy), new Vector(x + ox, ym), new Vector(x, ym));
    case CORNER.BOTTOM_LEFT:
    default:
      return new BezierCurve(new Vector(xm, ym), new Vector(xm - ox, ym), new Vector(x, y + oy), new Vector(x, y));
  }
};
var calculateBorderBoxPath = function (curves) {
  return [curves.topLeftBorderBox, curves.topRightBorderBox, curves.bottomRightBorderBox, curves.bottomLeftBorderBox];
};
var calculateContentBoxPath = function (curves) {
  return [curves.topLeftContentBox, curves.topRightContentBox, curves.bottomRightContentBox, curves.bottomLeftContentBox];
};
var calculatePaddingBoxPath = function (curves) {
  return [curves.topLeftPaddingBox, curves.topRightPaddingBox, curves.bottomRightPaddingBox, curves.bottomLeftPaddingBox];
};
var TransformEffect = /** @class */function () {
  function TransformEffect(offsetX, offsetY, matrix) {
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.matrix = matrix;
    this.type = 0 /* TRANSFORM */;
    this.target = 2 /* BACKGROUND_BORDERS */ | 4 /* CONTENT */;
  }
  return TransformEffect;
}();
var ClipEffect = /** @class */function () {
  function ClipEffect(path, target) {
    this.path = path;
    this.target = target;
    this.type = 1 /* CLIP */;
  }
  return ClipEffect;
}();
var OpacityEffect = /** @class */function () {
  function OpacityEffect(opacity) {
    this.opacity = opacity;
    this.type = 2 /* OPACITY */;
    this.target = 2 /* BACKGROUND_BORDERS */ | 4 /* CONTENT */;
  }
  return OpacityEffect;
}();
var isTransformEffect = function (effect) {
  return effect.type === 0 /* TRANSFORM */;
};
var isClipEffect = function (effect) {
  return effect.type === 1 /* CLIP */;
};
var isOpacityEffect = function (effect) {
  return effect.type === 2 /* OPACITY */;
};
var equalPath = function (a, b) {
  if (a.length === b.length) {
    return a.some(function (v, i) {
      return v === b[i];
    });
  }
  return false;
};
var transformPath = function (path, deltaX, deltaY, deltaW, deltaH) {
  return path.map(function (point, index) {
    switch (index) {
      case 0:
        return point.add(deltaX, deltaY);
      case 1:
        return point.add(deltaX + deltaW, deltaY);
      case 2:
        return point.add(deltaX + deltaW, deltaY + deltaH);
      case 3:
        return point.add(deltaX, deltaY + deltaH);
    }
    return point;
  });
};
var StackingContext = /** @class */function () {
  function StackingContext(container) {
    this.element = container;
    this.inlineLevel = [];
    this.nonInlineLevel = [];
    this.negativeZIndex = [];
    this.zeroOrAutoZIndexOrTransformedOrOpacity = [];
    this.positiveZIndex = [];
    this.nonPositionedFloats = [];
    this.nonPositionedInlineLevel = [];
  }
  return StackingContext;
}();
var ElementPaint = /** @class */function () {
  function ElementPaint(container, parent) {
    this.container = container;
    this.parent = parent;
    this.effects = [];
    this.curves = new BoundCurves(this.container);
    if (this.container.styles.opacity < 1) {
      this.effects.push(new OpacityEffect(this.container.styles.opacity));
    }
    if (this.container.styles.transform !== null) {
      var offsetX = this.container.bounds.left + this.container.styles.transformOrigin[0].number;
      var offsetY = this.container.bounds.top + this.container.styles.transformOrigin[1].number;
      var matrix = this.container.styles.transform;
      this.effects.push(new TransformEffect(offsetX, offsetY, matrix));
    }
    if (this.container.styles.overflowX !== 0 /* VISIBLE */) {
      var borderBox = calculateBorderBoxPath(this.curves);
      var paddingBox = calculatePaddingBoxPath(this.curves);
      if (equalPath(borderBox, paddingBox)) {
        this.effects.push(new ClipEffect(borderBox, 2 /* BACKGROUND_BORDERS */ | 4 /* CONTENT */));
      } else {
        this.effects.push(new ClipEffect(borderBox, 2 /* BACKGROUND_BORDERS */));
        this.effects.push(new ClipEffect(paddingBox, 4 /* CONTENT */));
      }
    }
  }
  ElementPaint.prototype.getEffects = function (target) {
    var inFlow = [2 /* ABSOLUTE */, 3 /* FIXED */].indexOf(this.container.styles.position) === -1;
    var parent = this.parent;
    var effects = this.effects.slice(0);
    while (parent) {
      var croplessEffects = parent.effects.filter(function (effect) {
        return !isClipEffect(effect);
      });
      if (inFlow || parent.container.styles.position !== 0 /* STATIC */ || !parent.parent) {
        effects.unshift.apply(effects, croplessEffects);
        inFlow = [2 /* ABSOLUTE */, 3 /* FIXED */].indexOf(parent.container.styles.position) === -1;
        if (parent.container.styles.overflowX !== 0 /* VISIBLE */) {
          var borderBox = calculateBorderBoxPath(parent.curves);
          var paddingBox = calculatePaddingBoxPath(parent.curves);
          if (!equalPath(borderBox, paddingBox)) {
            effects.unshift(new ClipEffect(paddingBox, 2 /* BACKGROUND_BORDERS */ | 4 /* CONTENT */));
          }
        }
      } else {
        effects.unshift.apply(effects, croplessEffects);
      }
      parent = parent.parent;
    }
    return effects.filter(function (effect) {
      return contains(effect.target, target);
    });
  };
  return ElementPaint;
}();
var parseStackTree = function (parent, stackingContext, realStackingContext, listItems) {
  parent.container.elements.forEach(function (child) {
    var treatAsRealStackingContext = contains(child.flags, 4 /* CREATES_REAL_STACKING_CONTEXT */);
    var createsStackingContext = contains(child.flags, 2 /* CREATES_STACKING_CONTEXT */);
    var paintContainer = new ElementPaint(child, parent);
    if (contains(child.styles.display, 2048 /* LIST_ITEM */)) {
      listItems.push(paintContainer);
    }
    var listOwnerItems = contains(child.flags, 8 /* IS_LIST_OWNER */) ? [] : listItems;
    if (treatAsRealStackingContext || createsStackingContext) {
      var parentStack = treatAsRealStackingContext || child.styles.isPositioned() ? realStackingContext : stackingContext;
      var stack = new StackingContext(paintContainer);
      if (child.styles.isPositioned() || child.styles.opacity < 1 || child.styles.isTransformed()) {
        var order_1 = child.styles.zIndex.order;
        if (order_1 < 0) {
          var index_1 = 0;
          parentStack.negativeZIndex.some(function (current, i) {
            if (order_1 > current.element.container.styles.zIndex.order) {
              index_1 = i;
              return false;
            } else if (index_1 > 0) {
              return true;
            }
            return false;
          });
          parentStack.negativeZIndex.splice(index_1, 0, stack);
        } else if (order_1 > 0) {
          var index_2 = 0;
          parentStack.positiveZIndex.some(function (current, i) {
            if (order_1 >= current.element.container.styles.zIndex.order) {
              index_2 = i + 1;
              return false;
            } else if (index_2 > 0) {
              return true;
            }
            return false;
          });
          parentStack.positiveZIndex.splice(index_2, 0, stack);
        } else {
          parentStack.zeroOrAutoZIndexOrTransformedOrOpacity.push(stack);
        }
      } else {
        if (child.styles.isFloating()) {
          parentStack.nonPositionedFloats.push(stack);
        } else {
          parentStack.nonPositionedInlineLevel.push(stack);
        }
      }
      parseStackTree(paintContainer, stack, treatAsRealStackingContext ? stack : realStackingContext, listOwnerItems);
    } else {
      if (child.styles.isInlineLevel()) {
        stackingContext.inlineLevel.push(paintContainer);
      } else {
        stackingContext.nonInlineLevel.push(paintContainer);
      }
      parseStackTree(paintContainer, stackingContext, realStackingContext, listOwnerItems);
    }
    if (contains(child.flags, 8 /* IS_LIST_OWNER */)) {
      processListItems(child, listOwnerItems);
    }
  });
};
var processListItems = function (owner, elements) {
  var numbering = owner instanceof OLElementContainer ? owner.start : 1;
  var reversed = owner instanceof OLElementContainer ? owner.reversed : false;
  for (var i = 0; i < elements.length; i++) {
    var item = elements[i];
    if (item.container instanceof LIElementContainer && typeof item.container.value === 'number' && item.container.value !== 0) {
      numbering = item.container.value;
    }
    item.listValue = createCounterText(numbering, item.container.styles.listStyleType, true);
    numbering += reversed ? -1 : 1;
  }
};
var parseStackingContexts = function (container) {
  var paintContainer = new ElementPaint(container, null);
  var root = new StackingContext(paintContainer);
  var listItems = [];
  parseStackTree(paintContainer, root, root, listItems);
  processListItems(paintContainer.container, listItems);
  return root;
};
var parsePathForBorder = function (curves, borderSide) {
  switch (borderSide) {
    case 0:
      return createPathFromCurves(curves.topLeftBorderBox, curves.topLeftPaddingBox, curves.topRightBorderBox, curves.topRightPaddingBox);
    case 1:
      return createPathFromCurves(curves.topRightBorderBox, curves.topRightPaddingBox, curves.bottomRightBorderBox, curves.bottomRightPaddingBox);
    case 2:
      return createPathFromCurves(curves.bottomRightBorderBox, curves.bottomRightPaddingBox, curves.bottomLeftBorderBox, curves.bottomLeftPaddingBox);
    case 3:
    default:
      return createPathFromCurves(curves.bottomLeftBorderBox, curves.bottomLeftPaddingBox, curves.topLeftBorderBox, curves.topLeftPaddingBox);
  }
};
var parsePathForBorderDoubleOuter = function (curves, borderSide) {
  switch (borderSide) {
    case 0:
      return createPathFromCurves(curves.topLeftBorderBox, curves.topLeftBorderDoubleOuterBox, curves.topRightBorderBox, curves.topRightBorderDoubleOuterBox);
    case 1:
      return createPathFromCurves(curves.topRightBorderBox, curves.topRightBorderDoubleOuterBox, curves.bottomRightBorderBox, curves.bottomRightBorderDoubleOuterBox);
    case 2:
      return createPathFromCurves(curves.bottomRightBorderBox, curves.bottomRightBorderDoubleOuterBox, curves.bottomLeftBorderBox, curves.bottomLeftBorderDoubleOuterBox);
    case 3:
    default:
      return createPathFromCurves(curves.bottomLeftBorderBox, curves.bottomLeftBorderDoubleOuterBox, curves.topLeftBorderBox, curves.topLeftBorderDoubleOuterBox);
  }
};
var parsePathForBorderDoubleInner = function (curves, borderSide) {
  switch (borderSide) {
    case 0:
      return createPathFromCurves(curves.topLeftBorderDoubleInnerBox, curves.topLeftPaddingBox, curves.topRightBorderDoubleInnerBox, curves.topRightPaddingBox);
    case 1:
      return createPathFromCurves(curves.topRightBorderDoubleInnerBox, curves.topRightPaddingBox, curves.bottomRightBorderDoubleInnerBox, curves.bottomRightPaddingBox);
    case 2:
      return createPathFromCurves(curves.bottomRightBorderDoubleInnerBox, curves.bottomRightPaddingBox, curves.bottomLeftBorderDoubleInnerBox, curves.bottomLeftPaddingBox);
    case 3:
    default:
      return createPathFromCurves(curves.bottomLeftBorderDoubleInnerBox, curves.bottomLeftPaddingBox, curves.topLeftBorderDoubleInnerBox, curves.topLeftPaddingBox);
  }
};
var parsePathForBorderStroke = function (curves, borderSide) {
  switch (borderSide) {
    case 0:
      return createStrokePathFromCurves(curves.topLeftBorderStroke, curves.topRightBorderStroke);
    case 1:
      return createStrokePathFromCurves(curves.topRightBorderStroke, curves.bottomRightBorderStroke);
    case 2:
      return createStrokePathFromCurves(curves.bottomRightBorderStroke, curves.bottomLeftBorderStroke);
    case 3:
    default:
      return createStrokePathFromCurves(curves.bottomLeftBorderStroke, curves.topLeftBorderStroke);
  }
};
var createStrokePathFromCurves = function (outer1, outer2) {
  var path = [];
  if (isBezierCurve(outer1)) {
    path.push(outer1.subdivide(0.5, false));
  } else {
    path.push(outer1);
  }
  if (isBezierCurve(outer2)) {
    path.push(outer2.subdivide(0.5, true));
  } else {
    path.push(outer2);
  }
  return path;
};
var createPathFromCurves = function (outer1, inner1, outer2, inner2) {
  var path = [];
  if (isBezierCurve(outer1)) {
    path.push(outer1.subdivide(0.5, false));
  } else {
    path.push(outer1);
  }
  if (isBezierCurve(outer2)) {
    path.push(outer2.subdivide(0.5, true));
  } else {
    path.push(outer2);
  }
  if (isBezierCurve(inner2)) {
    path.push(inner2.subdivide(0.5, true).reverse());
  } else {
    path.push(inner2);
  }
  if (isBezierCurve(inner1)) {
    path.push(inner1.subdivide(0.5, false).reverse());
  } else {
    path.push(inner1);
  }
  return path;
};
var paddingBox = function (element) {
  var bounds = element.bounds;
  var styles = element.styles;
  return bounds.add(styles.borderLeftWidth, styles.borderTopWidth, -(styles.borderRightWidth + styles.borderLeftWidth), -(styles.borderTopWidth + styles.borderBottomWidth));
};
var contentBox = function (element) {
  var styles = element.styles;
  var bounds = element.bounds;
  var paddingLeft = getAbsoluteValue(styles.paddingLeft, bounds.width);
  var paddingRight = getAbsoluteValue(styles.paddingRight, bounds.width);
  var paddingTop = getAbsoluteValue(styles.paddingTop, bounds.width);
  var paddingBottom = getAbsoluteValue(styles.paddingBottom, bounds.width);
  return bounds.add(paddingLeft + styles.borderLeftWidth, paddingTop + styles.borderTopWidth, -(styles.borderRightWidth + styles.borderLeftWidth + paddingLeft + paddingRight), -(styles.borderTopWidth + styles.borderBottomWidth + paddingTop + paddingBottom));
};
var calculateBackgroundPositioningArea = function (backgroundOrigin, element) {
  if (backgroundOrigin === 0 /* BORDER_BOX */) {
    return element.bounds;
  }
  if (backgroundOrigin === 2 /* CONTENT_BOX */) {
    return contentBox(element);
  }
  return paddingBox(element);
};
var calculateBackgroundPaintingArea = function (backgroundClip, element) {
  if (backgroundClip === 0 /* BORDER_BOX */) {
    return element.bounds;
  }
  if (backgroundClip === 2 /* CONTENT_BOX */) {
    return contentBox(element);
  }
  return paddingBox(element);
};
var calculateBackgroundRendering = function (container, index, intrinsicSize) {
  var backgroundPositioningArea = calculateBackgroundPositioningArea(getBackgroundValueForIndex(container.styles.backgroundOrigin, index), container);
  var backgroundPaintingArea = calculateBackgroundPaintingArea(getBackgroundValueForIndex(container.styles.backgroundClip, index), container);
  var backgroundImageSize = calculateBackgroundSize(getBackgroundValueForIndex(container.styles.backgroundSize, index), intrinsicSize, backgroundPositioningArea);
  var sizeWidth = backgroundImageSize[0],
    sizeHeight = backgroundImageSize[1];
  var position = getAbsoluteValueForTuple(getBackgroundValueForIndex(container.styles.backgroundPosition, index), backgroundPositioningArea.width - sizeWidth, backgroundPositioningArea.height - sizeHeight);
  var path = calculateBackgroundRepeatPath(getBackgroundValueForIndex(container.styles.backgroundRepeat, index), position, backgroundImageSize, backgroundPositioningArea, backgroundPaintingArea);
  var offsetX = Math.round(backgroundPositioningArea.left + position[0]);
  var offsetY = Math.round(backgroundPositioningArea.top + position[1]);
  return [path, offsetX, offsetY, sizeWidth, sizeHeight];
};
var isAuto = function (token) {
  return isIdentToken(token) && token.value === BACKGROUND_SIZE.AUTO;
};
var hasIntrinsicValue = function (value) {
  return typeof value === 'number';
};
var calculateBackgroundSize = function (size, _a, bounds) {
  var intrinsicWidth = _a[0],
    intrinsicHeight = _a[1],
    intrinsicProportion = _a[2];
  var first = size[0],
    second = size[1];
  if (!first) {
    return [0, 0];
  }
  if (isLengthPercentage(first) && second && isLengthPercentage(second)) {
    return [getAbsoluteValue(first, bounds.width), getAbsoluteValue(second, bounds.height)];
  }
  var hasIntrinsicProportion = hasIntrinsicValue(intrinsicProportion);
  if (isIdentToken(first) && (first.value === BACKGROUND_SIZE.CONTAIN || first.value === BACKGROUND_SIZE.COVER)) {
    if (hasIntrinsicValue(intrinsicProportion)) {
      var targetRatio = bounds.width / bounds.height;
      return targetRatio < intrinsicProportion !== (first.value === BACKGROUND_SIZE.COVER) ? [bounds.width, bounds.width / intrinsicProportion] : [bounds.height * intrinsicProportion, bounds.height];
    }
    return [bounds.width, bounds.height];
  }
  var hasIntrinsicWidth = hasIntrinsicValue(intrinsicWidth);
  var hasIntrinsicHeight = hasIntrinsicValue(intrinsicHeight);
  var hasIntrinsicDimensions = hasIntrinsicWidth || hasIntrinsicHeight;
  // If the background-size is auto or auto auto:
  if (isAuto(first) && (!second || isAuto(second))) {
    // If the image has both horizontal and vertical intrinsic dimensions, it's rendered at that size.
    if (hasIntrinsicWidth && hasIntrinsicHeight) {
      return [intrinsicWidth, intrinsicHeight];
    }
    // If the image has no intrinsic dimensions and has no intrinsic proportions,
    // it's rendered at the size of the background positioning area.
    if (!hasIntrinsicProportion && !hasIntrinsicDimensions) {
      return [bounds.width, bounds.height];
    }
    // TODO If the image has no intrinsic dimensions but has intrinsic proportions, it's rendered as if contain had been specified instead.
    // If the image has only one intrinsic dimension and has intrinsic proportions, it's rendered at the size corresponding to that one dimension.
    // The other dimension is computed using the specified dimension and the intrinsic proportions.
    if (hasIntrinsicDimensions && hasIntrinsicProportion) {
      var width_1 = hasIntrinsicWidth ? intrinsicWidth : intrinsicHeight * intrinsicProportion;
      var height_1 = hasIntrinsicHeight ? intrinsicHeight : intrinsicWidth / intrinsicProportion;
      return [width_1, height_1];
    }
    // If the image has only one intrinsic dimension but has no intrinsic proportions,
    // it's rendered using the specified dimension and the other dimension of the background positioning area.
    var width_2 = hasIntrinsicWidth ? intrinsicWidth : bounds.width;
    var height_2 = hasIntrinsicHeight ? intrinsicHeight : bounds.height;
    return [width_2, height_2];
  }
  // If the image has intrinsic proportions, it's stretched to the specified dimension.
  // The unspecified dimension is computed using the specified dimension and the intrinsic proportions.
  if (hasIntrinsicProportion) {
    var width_3 = 0;
    var height_3 = 0;
    if (isLengthPercentage(first)) {
      width_3 = getAbsoluteValue(first, bounds.width);
    } else if (isLengthPercentage(second)) {
      height_3 = getAbsoluteValue(second, bounds.height);
    }
    if (isAuto(first)) {
      width_3 = height_3 * intrinsicProportion;
    } else if (!second || isAuto(second)) {
      height_3 = width_3 / intrinsicProportion;
    }
    return [width_3, height_3];
  }
  // If the image has no intrinsic proportions, it's stretched to the specified dimension.
  // The unspecified dimension is computed using the image's corresponding intrinsic dimension,
  // if there is one. If there is no such intrinsic dimension,
  // it becomes the corresponding dimension of the background positioning area.
  var width = null;
  var height = null;
  if (isLengthPercentage(first)) {
    width = getAbsoluteValue(first, bounds.width);
  } else if (second && isLengthPercentage(second)) {
    height = getAbsoluteValue(second, bounds.height);
  }
  if (width !== null && (!second || isAuto(second))) {
    height = hasIntrinsicWidth && hasIntrinsicHeight ? width / intrinsicWidth * intrinsicHeight : bounds.height;
  }
  if (height !== null && isAuto(first)) {
    width = hasIntrinsicWidth && hasIntrinsicHeight ? height / intrinsicHeight * intrinsicWidth : bounds.width;
  }
  if (width !== null && height !== null) {
    return [width, height];
  }
  throw new Error("Unable to calculate background-size for element");
};
var getBackgroundValueForIndex = function (values, index) {
  var value = values[index];
  if (typeof value === 'undefined') {
    return values[0];
  }
  return value;
};
var calculateBackgroundRepeatPath = function (repeat, _a, _b, backgroundPositioningArea, backgroundPaintingArea) {
  var x = _a[0],
    y = _a[1];
  var width = _b[0],
    height = _b[1];
  switch (repeat) {
    case 2 /* REPEAT_X */:
      return [new Vector(Math.round(backgroundPositioningArea.left), Math.round(backgroundPositioningArea.top + y)), new Vector(Math.round(backgroundPositioningArea.left + backgroundPositioningArea.width), Math.round(backgroundPositioningArea.top + y)), new Vector(Math.round(backgroundPositioningArea.left + backgroundPositioningArea.width), Math.round(height + backgroundPositioningArea.top + y)), new Vector(Math.round(backgroundPositioningArea.left), Math.round(height + backgroundPositioningArea.top + y))];
    case 3 /* REPEAT_Y */:
      return [new Vector(Math.round(backgroundPositioningArea.left + x), Math.round(backgroundPositioningArea.top)), new Vector(Math.round(backgroundPositioningArea.left + x + width), Math.round(backgroundPositioningArea.top)), new Vector(Math.round(backgroundPositioningArea.left + x + width), Math.round(backgroundPositioningArea.height + backgroundPositioningArea.top)), new Vector(Math.round(backgroundPositioningArea.left + x), Math.round(backgroundPositioningArea.height + backgroundPositioningArea.top))];
    case 1 /* NO_REPEAT */:
      return [new Vector(Math.round(backgroundPositioningArea.left + x), Math.round(backgroundPositioningArea.top + y)), new Vector(Math.round(backgroundPositioningArea.left + x + width), Math.round(backgroundPositioningArea.top + y)), new Vector(Math.round(backgroundPositioningArea.left + x + width), Math.round(backgroundPositioningArea.top + y + height)), new Vector(Math.round(backgroundPositioningArea.left + x), Math.round(backgroundPositioningArea.top + y + height))];
    default:
      return [new Vector(Math.round(backgroundPaintingArea.left), Math.round(backgroundPaintingArea.top)), new Vector(Math.round(backgroundPaintingArea.left + backgroundPaintingArea.width), Math.round(backgroundPaintingArea.top)), new Vector(Math.round(backgroundPaintingArea.left + backgroundPaintingArea.width), Math.round(backgroundPaintingArea.height + backgroundPaintingArea.top)), new Vector(Math.round(backgroundPaintingArea.left), Math.round(backgroundPaintingArea.height + backgroundPaintingArea.top))];
  }
};
var SMALL_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
var SAMPLE_TEXT = 'Hidden Text';
var FontMetrics = /** @class */function () {
  function FontMetrics(document) {
    this._data = {};
    this._document = document;
  }
  FontMetrics.prototype.parseMetrics = function (fontFamily, fontSize) {
    var container = this._document.createElement('div');
    var img = this._document.createElement('img');
    var span = this._document.createElement('span');
    var body = this._document.body;
    container.style.visibility = 'hidden';
    container.style.fontFamily = fontFamily;
    container.style.fontSize = fontSize;
    container.style.margin = '0';
    container.style.padding = '0';
    container.style.whiteSpace = 'nowrap';
    body.appendChild(container);
    img.src = SMALL_IMAGE;
    img.width = 1;
    img.height = 1;
    img.style.margin = '0';
    img.style.padding = '0';
    img.style.verticalAlign = 'baseline';
    span.style.fontFamily = fontFamily;
    span.style.fontSize = fontSize;
    span.style.margin = '0';
    span.style.padding = '0';
    span.appendChild(this._document.createTextNode(SAMPLE_TEXT));
    container.appendChild(span);
    container.appendChild(img);
    var baseline = img.offsetTop - span.offsetTop + 2;
    container.removeChild(span);
    container.appendChild(this._document.createTextNode(SAMPLE_TEXT));
    container.style.lineHeight = 'normal';
    img.style.verticalAlign = 'super';
    var middle = img.offsetTop - container.offsetTop + 2;
    body.removeChild(container);
    return {
      baseline: baseline,
      middle: middle
    };
  };
  FontMetrics.prototype.getMetrics = function (fontFamily, fontSize) {
    var key = fontFamily + " " + fontSize;
    if (typeof this._data[key] === 'undefined') {
      this._data[key] = this.parseMetrics(fontFamily, fontSize);
    }
    return this._data[key];
  };
  return FontMetrics;
}();
var Renderer = /** @class */function () {
  function Renderer(context, options) {
    this.context = context;
    this.options = options;
  }
  return Renderer;
}();
var MASK_OFFSET = 10000;
var CanvasRenderer = /** @class */function (_super) {
  __extends(CanvasRenderer, _super);
  function CanvasRenderer(context, options) {
    var _this = _super.call(this, context, options) || this;
    _this._activeEffects = [];
    _this.canvas = options.canvas ? options.canvas : document.createElement('canvas');
    _this.ctx = _this.canvas.getContext('2d');
    if (!options.canvas) {
      _this.canvas.width = Math.floor(options.width * options.scale);
      _this.canvas.height = Math.floor(options.height * options.scale);
      _this.canvas.style.width = options.width + "px";
      _this.canvas.style.height = options.height + "px";
    }
    _this.fontMetrics = new FontMetrics(document);
    _this.ctx.scale(_this.options.scale, _this.options.scale);
    _this.ctx.translate(-options.x, -options.y);
    _this.ctx.textBaseline = 'bottom';
    _this._activeEffects = [];
    _this.context.logger.debug("Canvas renderer initialized (" + options.width + "x" + options.height + ") with scale " + options.scale);
    return _this;
  }
  CanvasRenderer.prototype.applyEffects = function (effects) {
    var _this = this;
    while (this._activeEffects.length) {
      this.popEffect();
    }
    effects.forEach(function (effect) {
      return _this.applyEffect(effect);
    });
  };
  CanvasRenderer.prototype.applyEffect = function (effect) {
    this.ctx.save();
    if (isOpacityEffect(effect)) {
      this.ctx.globalAlpha = effect.opacity;
    }
    if (isTransformEffect(effect)) {
      this.ctx.translate(effect.offsetX, effect.offsetY);
      this.ctx.transform(effect.matrix[0], effect.matrix[1], effect.matrix[2], effect.matrix[3], effect.matrix[4], effect.matrix[5]);
      this.ctx.translate(-effect.offsetX, -effect.offsetY);
    }
    if (isClipEffect(effect)) {
      this.path(effect.path);
      this.ctx.clip();
    }
    this._activeEffects.push(effect);
  };
  CanvasRenderer.prototype.popEffect = function () {
    this._activeEffects.pop();
    this.ctx.restore();
  };
  CanvasRenderer.prototype.renderStack = function (stack) {
    return __awaiter(this, void 0, void 0, function () {
      var styles;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            styles = stack.element.container.styles;
            if (!styles.isVisible()) return [3 /*break*/, 2];
            return [4 /*yield*/, this.renderStackContent(stack)];
          case 1:
            _a.sent();
            _a.label = 2;
          case 2:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderNode = function (paint) {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (contains(paint.container.flags, 16 /* DEBUG_RENDER */)) {
              debugger;
            }
            if (!paint.container.styles.isVisible()) return [3 /*break*/, 3];
            return [4 /*yield*/, this.renderNodeBackgroundAndBorders(paint)];
          case 1:
            _a.sent();
            return [4 /*yield*/, this.renderNodeContent(paint)];
          case 2:
            _a.sent();
            _a.label = 3;
          case 3:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderTextWithLetterSpacing = function (text, letterSpacing, baseline) {
    var _this = this;
    if (letterSpacing === 0) {
      this.ctx.fillText(text.text, text.bounds.left, text.bounds.top + baseline);
    } else {
      var letters = segmentGraphemes(text.text);
      letters.reduce(function (left, letter) {
        _this.ctx.fillText(letter, left, text.bounds.top + baseline);
        return left + _this.ctx.measureText(letter).width;
      }, text.bounds.left);
    }
  };
  CanvasRenderer.prototype.createFontStyle = function (styles) {
    var fontVariant = styles.fontVariant.filter(function (variant) {
      return variant === 'normal' || variant === 'small-caps';
    }).join('');
    var fontFamily = fixIOSSystemFonts(styles.fontFamily).join(', ');
    var fontSize = isDimensionToken(styles.fontSize) ? "" + styles.fontSize.number + styles.fontSize.unit : styles.fontSize.number + "px";
    return [[styles.fontStyle, fontVariant, styles.fontWeight, fontSize, fontFamily].join(' '), fontFamily, fontSize];
  };
  CanvasRenderer.prototype.renderTextNode = function (text, styles) {
    return __awaiter(this, void 0, void 0, function () {
      var _a, font, fontFamily, fontSize, _b, baseline, middle, paintOrder;
      var _this = this;
      return __generator(this, function (_c) {
        _a = this.createFontStyle(styles), font = _a[0], fontFamily = _a[1], fontSize = _a[2];
        this.ctx.font = font;
        this.ctx.direction = styles.direction === 1 /* RTL */ ? 'rtl' : 'ltr';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'alphabetic';
        _b = this.fontMetrics.getMetrics(fontFamily, fontSize), baseline = _b.baseline, middle = _b.middle;
        paintOrder = styles.paintOrder;
        text.textBounds.forEach(function (text) {
          paintOrder.forEach(function (paintOrderLayer) {
            switch (paintOrderLayer) {
              case 0 /* FILL */:
                _this.ctx.fillStyle = asString(styles.color);
                _this.renderTextWithLetterSpacing(text, styles.letterSpacing, baseline);
                var textShadows = styles.textShadow;
                if (textShadows.length && text.text.trim().length) {
                  textShadows.slice(0).reverse().forEach(function (textShadow) {
                    _this.ctx.shadowColor = asString(textShadow.color);
                    _this.ctx.shadowOffsetX = textShadow.offsetX.number * _this.options.scale;
                    _this.ctx.shadowOffsetY = textShadow.offsetY.number * _this.options.scale;
                    _this.ctx.shadowBlur = textShadow.blur.number;
                    _this.renderTextWithLetterSpacing(text, styles.letterSpacing, baseline);
                  });
                  _this.ctx.shadowColor = '';
                  _this.ctx.shadowOffsetX = 0;
                  _this.ctx.shadowOffsetY = 0;
                  _this.ctx.shadowBlur = 0;
                }
                if (styles.textDecorationLine.length) {
                  _this.ctx.fillStyle = asString(styles.textDecorationColor || styles.color);
                  styles.textDecorationLine.forEach(function (textDecorationLine) {
                    switch (textDecorationLine) {
                      case 1 /* UNDERLINE */:
                        // Draws a line at the baseline of the font
                        // TODO As some browsers display the line as more than 1px if the font-size is big,
                        // need to take that into account both in position and size
                        _this.ctx.fillRect(text.bounds.left, Math.round(text.bounds.top + baseline), text.bounds.width, 1);
                        break;
                      case 2 /* OVERLINE */:
                        _this.ctx.fillRect(text.bounds.left, Math.round(text.bounds.top), text.bounds.width, 1);
                        break;
                      case 3 /* LINE_THROUGH */:
                        // TODO try and find exact position for line-through
                        _this.ctx.fillRect(text.bounds.left, Math.ceil(text.bounds.top + middle), text.bounds.width, 1);
                        break;
                    }
                  });
                }
                break;
              case 1 /* STROKE */:
                if (styles.webkitTextStrokeWidth && text.text.trim().length) {
                  _this.ctx.strokeStyle = asString(styles.webkitTextStrokeColor);
                  _this.ctx.lineWidth = styles.webkitTextStrokeWidth;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  _this.ctx.lineJoin = !!window.chrome ? 'miter' : 'round';
                  _this.ctx.strokeText(text.text, text.bounds.left, text.bounds.top + baseline);
                }
                _this.ctx.strokeStyle = '';
                _this.ctx.lineWidth = 0;
                _this.ctx.lineJoin = 'miter';
                break;
            }
          });
        });
        return [2 /*return*/];
      });
    });
  };
  CanvasRenderer.prototype.renderReplacedElement = function (container, curves, image) {
    if (image && container.intrinsicWidth > 0 && container.intrinsicHeight > 0) {
      var box = contentBox(container);
      var path = calculatePaddingBoxPath(curves);
      this.path(path);
      this.ctx.save();
      this.ctx.clip();
      this.ctx.drawImage(image, 0, 0, container.intrinsicWidth, container.intrinsicHeight, box.left, box.top, box.width, box.height);
      this.ctx.restore();
    }
  };
  CanvasRenderer.prototype.renderNodeContent = function (paint) {
    return __awaiter(this, void 0, void 0, function () {
      var container, curves, styles, _i, _a, child, image, image, iframeRenderer, canvas, size, _b, fontFamily, fontSize, baseline, bounds, x, textBounds, img, image, url, fontFamily, bounds;
      return __generator(this, function (_c) {
        switch (_c.label) {
          case 0:
            this.applyEffects(paint.getEffects(4 /* CONTENT */));
            container = paint.container;
            curves = paint.curves;
            styles = container.styles;
            _i = 0, _a = container.textNodes;
            _c.label = 1;
          case 1:
            if (!(_i < _a.length)) return [3 /*break*/, 4];
            child = _a[_i];
            return [4 /*yield*/, this.renderTextNode(child, styles)];
          case 2:
            _c.sent();
            _c.label = 3;
          case 3:
            _i++;
            return [3 /*break*/, 1];
          case 4:
            if (!(container instanceof ImageElementContainer)) return [3 /*break*/, 8];
            _c.label = 5;
          case 5:
            _c.trys.push([5, 7,, 8]);
            return [4 /*yield*/, this.context.cache.match(container.src)];
          case 6:
            image = _c.sent();
            this.renderReplacedElement(container, curves, image);
            return [3 /*break*/, 8];
          case 7:
            _c.sent();
            this.context.logger.error("Error loading image " + container.src);
            return [3 /*break*/, 8];
          case 8:
            if (container instanceof CanvasElementContainer) {
              this.renderReplacedElement(container, curves, container.canvas);
            }
            if (!(container instanceof SVGElementContainer)) return [3 /*break*/, 12];
            _c.label = 9;
          case 9:
            _c.trys.push([9, 11,, 12]);
            return [4 /*yield*/, this.context.cache.match(container.svg)];
          case 10:
            image = _c.sent();
            this.renderReplacedElement(container, curves, image);
            return [3 /*break*/, 12];
          case 11:
            _c.sent();
            this.context.logger.error("Error loading svg " + container.svg.substring(0, 255));
            return [3 /*break*/, 12];
          case 12:
            if (!(container instanceof IFrameElementContainer && container.tree)) return [3 /*break*/, 14];
            iframeRenderer = new CanvasRenderer(this.context, {
              scale: this.options.scale,
              backgroundColor: container.backgroundColor,
              x: 0,
              y: 0,
              width: container.width,
              height: container.height
            });
            return [4 /*yield*/, iframeRenderer.render(container.tree)];
          case 13:
            canvas = _c.sent();
            if (container.width && container.height) {
              this.ctx.drawImage(canvas, 0, 0, container.width, container.height, container.bounds.left, container.bounds.top, container.bounds.width, container.bounds.height);
            }
            _c.label = 14;
          case 14:
            if (container instanceof InputElementContainer) {
              size = Math.min(container.bounds.width, container.bounds.height);
              if (container.type === CHECKBOX) {
                if (container.checked) {
                  this.ctx.save();
                  this.path([new Vector(container.bounds.left + size * 0.39363, container.bounds.top + size * 0.79), new Vector(container.bounds.left + size * 0.16, container.bounds.top + size * 0.5549), new Vector(container.bounds.left + size * 0.27347, container.bounds.top + size * 0.44071), new Vector(container.bounds.left + size * 0.39694, container.bounds.top + size * 0.5649), new Vector(container.bounds.left + size * 0.72983, container.bounds.top + size * 0.23), new Vector(container.bounds.left + size * 0.84, container.bounds.top + size * 0.34085), new Vector(container.bounds.left + size * 0.39363, container.bounds.top + size * 0.79)]);
                  this.ctx.fillStyle = asString(INPUT_COLOR);
                  this.ctx.fill();
                  this.ctx.restore();
                }
              } else if (container.type === RADIO) {
                if (container.checked) {
                  this.ctx.save();
                  this.ctx.beginPath();
                  this.ctx.arc(container.bounds.left + size / 2, container.bounds.top + size / 2, size / 4, 0, Math.PI * 2, true);
                  this.ctx.fillStyle = asString(INPUT_COLOR);
                  this.ctx.fill();
                  this.ctx.restore();
                }
              }
            }
            if (isTextInputElement(container) && container.value.length) {
              _b = this.createFontStyle(styles), fontFamily = _b[0], fontSize = _b[1];
              baseline = this.fontMetrics.getMetrics(fontFamily, fontSize).baseline;
              this.ctx.font = fontFamily;
              this.ctx.fillStyle = asString(styles.color);
              this.ctx.textBaseline = 'alphabetic';
              this.ctx.textAlign = canvasTextAlign(container.styles.textAlign);
              bounds = contentBox(container);
              x = 0;
              switch (container.styles.textAlign) {
                case 1 /* CENTER */:
                  x += bounds.width / 2;
                  break;
                case 2 /* RIGHT */:
                  x += bounds.width;
                  break;
              }
              textBounds = bounds.add(x, 0, 0, -bounds.height / 2 + 1);
              this.ctx.save();
              this.path([new Vector(bounds.left, bounds.top), new Vector(bounds.left + bounds.width, bounds.top), new Vector(bounds.left + bounds.width, bounds.top + bounds.height), new Vector(bounds.left, bounds.top + bounds.height)]);
              this.ctx.clip();
              this.renderTextWithLetterSpacing(new TextBounds(container.value, textBounds), styles.letterSpacing, baseline);
              this.ctx.restore();
              this.ctx.textBaseline = 'alphabetic';
              this.ctx.textAlign = 'left';
            }
            if (!contains(container.styles.display, 2048 /* LIST_ITEM */)) return [3 /*break*/, 20];
            if (!(container.styles.listStyleImage !== null)) return [3 /*break*/, 19];
            img = container.styles.listStyleImage;
            if (!(img.type === 0 /* URL */)) return [3 /*break*/, 18];
            image = void 0;
            url = img.url;
            _c.label = 15;
          case 15:
            _c.trys.push([15, 17,, 18]);
            return [4 /*yield*/, this.context.cache.match(url)];
          case 16:
            image = _c.sent();
            this.ctx.drawImage(image, container.bounds.left - (image.width + 10), container.bounds.top);
            return [3 /*break*/, 18];
          case 17:
            _c.sent();
            this.context.logger.error("Error loading list-style-image " + url);
            return [3 /*break*/, 18];
          case 18:
            return [3 /*break*/, 20];
          case 19:
            if (paint.listValue && container.styles.listStyleType !== -1 /* NONE */) {
              fontFamily = this.createFontStyle(styles)[0];
              this.ctx.font = fontFamily;
              this.ctx.fillStyle = asString(styles.color);
              this.ctx.textBaseline = 'middle';
              this.ctx.textAlign = 'right';
              bounds = new Bounds(container.bounds.left, container.bounds.top + getAbsoluteValue(container.styles.paddingTop, container.bounds.width), container.bounds.width, computeLineHeight(styles.lineHeight, styles.fontSize.number) / 2 + 1);
              this.renderTextWithLetterSpacing(new TextBounds(paint.listValue, bounds), styles.letterSpacing, computeLineHeight(styles.lineHeight, styles.fontSize.number) / 2 + 2);
              this.ctx.textBaseline = 'bottom';
              this.ctx.textAlign = 'left';
            }
            _c.label = 20;
          case 20:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderStackContent = function (stack) {
    return __awaiter(this, void 0, void 0, function () {
      var _i, _a, child, _b, _c, child, _d, _e, child, _f, _g, child, _h, _j, child, _k, _l, child, _m, _o, child;
      return __generator(this, function (_p) {
        switch (_p.label) {
          case 0:
            if (contains(stack.element.container.flags, 16 /* DEBUG_RENDER */)) {
              debugger;
            }
            // https://www.w3.org/TR/css-position-3/#painting-order
            // 1. the background and borders of the element forming the stacking context.
            return [4 /*yield*/, this.renderNodeBackgroundAndBorders(stack.element)];
          case 1:
            // https://www.w3.org/TR/css-position-3/#painting-order
            // 1. the background and borders of the element forming the stacking context.
            _p.sent();
            _i = 0, _a = stack.negativeZIndex;
            _p.label = 2;
          case 2:
            if (!(_i < _a.length)) return [3 /*break*/, 5];
            child = _a[_i];
            return [4 /*yield*/, this.renderStack(child)];
          case 3:
            _p.sent();
            _p.label = 4;
          case 4:
            _i++;
            return [3 /*break*/, 2];
          case 5:
            // 3. For all its in-flow, non-positioned, block-level descendants in tree order:
            return [4 /*yield*/, this.renderNodeContent(stack.element)];
          case 6:
            // 3. For all its in-flow, non-positioned, block-level descendants in tree order:
            _p.sent();
            _b = 0, _c = stack.nonInlineLevel;
            _p.label = 7;
          case 7:
            if (!(_b < _c.length)) return [3 /*break*/, 10];
            child = _c[_b];
            return [4 /*yield*/, this.renderNode(child)];
          case 8:
            _p.sent();
            _p.label = 9;
          case 9:
            _b++;
            return [3 /*break*/, 7];
          case 10:
            _d = 0, _e = stack.nonPositionedFloats;
            _p.label = 11;
          case 11:
            if (!(_d < _e.length)) return [3 /*break*/, 14];
            child = _e[_d];
            return [4 /*yield*/, this.renderStack(child)];
          case 12:
            _p.sent();
            _p.label = 13;
          case 13:
            _d++;
            return [3 /*break*/, 11];
          case 14:
            _f = 0, _g = stack.nonPositionedInlineLevel;
            _p.label = 15;
          case 15:
            if (!(_f < _g.length)) return [3 /*break*/, 18];
            child = _g[_f];
            return [4 /*yield*/, this.renderStack(child)];
          case 16:
            _p.sent();
            _p.label = 17;
          case 17:
            _f++;
            return [3 /*break*/, 15];
          case 18:
            _h = 0, _j = stack.inlineLevel;
            _p.label = 19;
          case 19:
            if (!(_h < _j.length)) return [3 /*break*/, 22];
            child = _j[_h];
            return [4 /*yield*/, this.renderNode(child)];
          case 20:
            _p.sent();
            _p.label = 21;
          case 21:
            _h++;
            return [3 /*break*/, 19];
          case 22:
            _k = 0, _l = stack.zeroOrAutoZIndexOrTransformedOrOpacity;
            _p.label = 23;
          case 23:
            if (!(_k < _l.length)) return [3 /*break*/, 26];
            child = _l[_k];
            return [4 /*yield*/, this.renderStack(child)];
          case 24:
            _p.sent();
            _p.label = 25;
          case 25:
            _k++;
            return [3 /*break*/, 23];
          case 26:
            _m = 0, _o = stack.positiveZIndex;
            _p.label = 27;
          case 27:
            if (!(_m < _o.length)) return [3 /*break*/, 30];
            child = _o[_m];
            return [4 /*yield*/, this.renderStack(child)];
          case 28:
            _p.sent();
            _p.label = 29;
          case 29:
            _m++;
            return [3 /*break*/, 27];
          case 30:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.mask = function (paths) {
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.lineTo(this.canvas.width, 0);
    this.ctx.lineTo(this.canvas.width, this.canvas.height);
    this.ctx.lineTo(0, this.canvas.height);
    this.ctx.lineTo(0, 0);
    this.formatPath(paths.slice(0).reverse());
    this.ctx.closePath();
  };
  CanvasRenderer.prototype.path = function (paths) {
    this.ctx.beginPath();
    this.formatPath(paths);
    this.ctx.closePath();
  };
  CanvasRenderer.prototype.formatPath = function (paths) {
    var _this = this;
    paths.forEach(function (point, index) {
      var start = isBezierCurve(point) ? point.start : point;
      if (index === 0) {
        _this.ctx.moveTo(start.x, start.y);
      } else {
        _this.ctx.lineTo(start.x, start.y);
      }
      if (isBezierCurve(point)) {
        _this.ctx.bezierCurveTo(point.startControl.x, point.startControl.y, point.endControl.x, point.endControl.y, point.end.x, point.end.y);
      }
    });
  };
  CanvasRenderer.prototype.renderRepeat = function (path, pattern, offsetX, offsetY) {
    this.path(path);
    this.ctx.fillStyle = pattern;
    this.ctx.translate(offsetX, offsetY);
    this.ctx.fill();
    this.ctx.translate(-offsetX, -offsetY);
  };
  CanvasRenderer.prototype.resizeImage = function (image, width, height) {
    var _a;
    if (image.width === width && image.height === height) {
      return image;
    }
    var ownerDocument = (_a = this.canvas.ownerDocument) !== null && _a !== void 0 ? _a : document;
    var canvas = ownerDocument.createElement('canvas');
    canvas.width = Math.max(1, width);
    canvas.height = Math.max(1, height);
    var ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);
    return canvas;
  };
  CanvasRenderer.prototype.renderBackgroundImage = function (container) {
    return __awaiter(this, void 0, void 0, function () {
      var index, _loop_1, this_1, _i, _a, backgroundImage;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            index = container.styles.backgroundImage.length - 1;
            _loop_1 = function (backgroundImage) {
              var image, url, _c, path, x, y, width, height, pattern, _d, path, x, y, width, height, _e, lineLength, x0, x1, y0, y1, canvas, ctx, gradient_1, pattern, _f, path, left, top_1, width, height, position, x, y, _g, rx, ry, radialGradient_1, midX, midY, f, invF;
              return __generator(this, function (_h) {
                switch (_h.label) {
                  case 0:
                    if (!(backgroundImage.type === 0 /* URL */)) return [3 /*break*/, 5];
                    image = void 0;
                    url = backgroundImage.url;
                    _h.label = 1;
                  case 1:
                    _h.trys.push([1, 3,, 4]);
                    return [4 /*yield*/, this_1.context.cache.match(url)];
                  case 2:
                    image = _h.sent();
                    return [3 /*break*/, 4];
                  case 3:
                    _h.sent();
                    this_1.context.logger.error("Error loading background-image " + url);
                    return [3 /*break*/, 4];
                  case 4:
                    if (image) {
                      _c = calculateBackgroundRendering(container, index, [image.width, image.height, image.width / image.height]), path = _c[0], x = _c[1], y = _c[2], width = _c[3], height = _c[4];
                      pattern = this_1.ctx.createPattern(this_1.resizeImage(image, width, height), 'repeat');
                      this_1.renderRepeat(path, pattern, x, y);
                    }
                    return [3 /*break*/, 6];
                  case 5:
                    if (isLinearGradient(backgroundImage)) {
                      _d = calculateBackgroundRendering(container, index, [null, null, null]), path = _d[0], x = _d[1], y = _d[2], width = _d[3], height = _d[4];
                      _e = calculateGradientDirection(backgroundImage.angle, width, height), lineLength = _e[0], x0 = _e[1], x1 = _e[2], y0 = _e[3], y1 = _e[4];
                      canvas = document.createElement('canvas');
                      canvas.width = width;
                      canvas.height = height;
                      ctx = canvas.getContext('2d');
                      gradient_1 = ctx.createLinearGradient(x0, y0, x1, y1);
                      processColorStops(backgroundImage.stops, lineLength).forEach(function (colorStop) {
                        return gradient_1.addColorStop(colorStop.stop, asString(colorStop.color));
                      });
                      ctx.fillStyle = gradient_1;
                      ctx.fillRect(0, 0, width, height);
                      if (width > 0 && height > 0) {
                        pattern = this_1.ctx.createPattern(canvas, 'repeat');
                        this_1.renderRepeat(path, pattern, x, y);
                      }
                    } else if (isRadialGradient(backgroundImage)) {
                      _f = calculateBackgroundRendering(container, index, [null, null, null]), path = _f[0], left = _f[1], top_1 = _f[2], width = _f[3], height = _f[4];
                      position = backgroundImage.position.length === 0 ? [FIFTY_PERCENT] : backgroundImage.position;
                      x = getAbsoluteValue(position[0], width);
                      y = getAbsoluteValue(position[position.length - 1], height);
                      _g = calculateRadius(backgroundImage, x, y, width, height), rx = _g[0], ry = _g[1];
                      if (rx > 0 && ry > 0) {
                        radialGradient_1 = this_1.ctx.createRadialGradient(left + x, top_1 + y, 0, left + x, top_1 + y, rx);
                        processColorStops(backgroundImage.stops, rx * 2).forEach(function (colorStop) {
                          return radialGradient_1.addColorStop(colorStop.stop, asString(colorStop.color));
                        });
                        this_1.path(path);
                        this_1.ctx.fillStyle = radialGradient_1;
                        if (rx !== ry) {
                          midX = container.bounds.left + 0.5 * container.bounds.width;
                          midY = container.bounds.top + 0.5 * container.bounds.height;
                          f = ry / rx;
                          invF = 1 / f;
                          this_1.ctx.save();
                          this_1.ctx.translate(midX, midY);
                          this_1.ctx.transform(1, 0, 0, f, 0, 0);
                          this_1.ctx.translate(-midX, -midY);
                          this_1.ctx.fillRect(left, invF * (top_1 - midY) + midY, width, height * invF);
                          this_1.ctx.restore();
                        } else {
                          this_1.ctx.fill();
                        }
                      }
                    }
                    _h.label = 6;
                  case 6:
                    index--;
                    return [2 /*return*/];
                }
              });
            };
            this_1 = this;
            _i = 0, _a = container.styles.backgroundImage.slice(0).reverse();
            _b.label = 1;
          case 1:
            if (!(_i < _a.length)) return [3 /*break*/, 4];
            backgroundImage = _a[_i];
            return [5 /*yield**/, _loop_1(backgroundImage)];
          case 2:
            _b.sent();
            _b.label = 3;
          case 3:
            _i++;
            return [3 /*break*/, 1];
          case 4:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderSolidBorder = function (color, side, curvePoints) {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        this.path(parsePathForBorder(curvePoints, side));
        this.ctx.fillStyle = asString(color);
        this.ctx.fill();
        return [2 /*return*/];
      });
    });
  };
  CanvasRenderer.prototype.renderDoubleBorder = function (color, width, side, curvePoints) {
    return __awaiter(this, void 0, void 0, function () {
      var outerPaths, innerPaths;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!(width < 3)) return [3 /*break*/, 2];
            return [4 /*yield*/, this.renderSolidBorder(color, side, curvePoints)];
          case 1:
            _a.sent();
            return [2 /*return*/];
          case 2:
            outerPaths = parsePathForBorderDoubleOuter(curvePoints, side);
            this.path(outerPaths);
            this.ctx.fillStyle = asString(color);
            this.ctx.fill();
            innerPaths = parsePathForBorderDoubleInner(curvePoints, side);
            this.path(innerPaths);
            this.ctx.fill();
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderNodeBackgroundAndBorders = function (paint) {
    return __awaiter(this, void 0, void 0, function () {
      var styles, hasBackground, borders, backgroundPaintingArea, side, _i, borders_1, border;
      var _this = this;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.applyEffects(paint.getEffects(2 /* BACKGROUND_BORDERS */));
            styles = paint.container.styles;
            hasBackground = !isTransparent(styles.backgroundColor) || styles.backgroundImage.length;
            borders = [{
              style: styles.borderTopStyle,
              color: styles.borderTopColor,
              width: styles.borderTopWidth
            }, {
              style: styles.borderRightStyle,
              color: styles.borderRightColor,
              width: styles.borderRightWidth
            }, {
              style: styles.borderBottomStyle,
              color: styles.borderBottomColor,
              width: styles.borderBottomWidth
            }, {
              style: styles.borderLeftStyle,
              color: styles.borderLeftColor,
              width: styles.borderLeftWidth
            }];
            backgroundPaintingArea = calculateBackgroundCurvedPaintingArea(getBackgroundValueForIndex(styles.backgroundClip, 0), paint.curves);
            if (!(hasBackground || styles.boxShadow.length)) return [3 /*break*/, 2];
            this.ctx.save();
            this.path(backgroundPaintingArea);
            this.ctx.clip();
            if (!isTransparent(styles.backgroundColor)) {
              this.ctx.fillStyle = asString(styles.backgroundColor);
              this.ctx.fill();
            }
            return [4 /*yield*/, this.renderBackgroundImage(paint.container)];
          case 1:
            _a.sent();
            this.ctx.restore();
            styles.boxShadow.slice(0).reverse().forEach(function (shadow) {
              _this.ctx.save();
              var borderBoxArea = calculateBorderBoxPath(paint.curves);
              var maskOffset = shadow.inset ? 0 : MASK_OFFSET;
              var shadowPaintingArea = transformPath(borderBoxArea, -maskOffset + (shadow.inset ? 1 : -1) * shadow.spread.number, (shadow.inset ? 1 : -1) * shadow.spread.number, shadow.spread.number * (shadow.inset ? -2 : 2), shadow.spread.number * (shadow.inset ? -2 : 2));
              if (shadow.inset) {
                _this.path(borderBoxArea);
                _this.ctx.clip();
                _this.mask(shadowPaintingArea);
              } else {
                _this.mask(borderBoxArea);
                _this.ctx.clip();
                _this.path(shadowPaintingArea);
              }
              _this.ctx.shadowOffsetX = shadow.offsetX.number + maskOffset;
              _this.ctx.shadowOffsetY = shadow.offsetY.number;
              _this.ctx.shadowColor = asString(shadow.color);
              _this.ctx.shadowBlur = shadow.blur.number;
              _this.ctx.fillStyle = shadow.inset ? asString(shadow.color) : 'rgba(0,0,0,1)';
              _this.ctx.fill();
              _this.ctx.restore();
            });
            _a.label = 2;
          case 2:
            side = 0;
            _i = 0, borders_1 = borders;
            _a.label = 3;
          case 3:
            if (!(_i < borders_1.length)) return [3 /*break*/, 13];
            border = borders_1[_i];
            if (!(border.style !== 0 /* NONE */ && !isTransparent(border.color) && border.width > 0)) return [3 /*break*/, 11];
            if (!(border.style === 2 /* DASHED */)) return [3 /*break*/, 5];
            return [4 /*yield*/, this.renderDashedDottedBorder(border.color, border.width, side, paint.curves, 2 /* DASHED */)];
          case 4:
            _a.sent();
            return [3 /*break*/, 11];
          case 5:
            if (!(border.style === 3 /* DOTTED */)) return [3 /*break*/, 7];
            return [4 /*yield*/, this.renderDashedDottedBorder(border.color, border.width, side, paint.curves, 3 /* DOTTED */)];
          case 6:
            _a.sent();
            return [3 /*break*/, 11];
          case 7:
            if (!(border.style === 4 /* DOUBLE */)) return [3 /*break*/, 9];
            return [4 /*yield*/, this.renderDoubleBorder(border.color, border.width, side, paint.curves)];
          case 8:
            _a.sent();
            return [3 /*break*/, 11];
          case 9:
            return [4 /*yield*/, this.renderSolidBorder(border.color, side, paint.curves)];
          case 10:
            _a.sent();
            _a.label = 11;
          case 11:
            side++;
            _a.label = 12;
          case 12:
            _i++;
            return [3 /*break*/, 3];
          case 13:
            return [2 /*return*/];
        }
      });
    });
  };
  CanvasRenderer.prototype.renderDashedDottedBorder = function (color, width, side, curvePoints, style) {
    return __awaiter(this, void 0, void 0, function () {
      var strokePaths, boxPaths, startX, startY, endX, endY, length, dashLength, spaceLength, useLineDash, multiplier, numberOfDashes, minSpace, maxSpace, path1, path2, path1, path2;
      return __generator(this, function (_a) {
        this.ctx.save();
        strokePaths = parsePathForBorderStroke(curvePoints, side);
        boxPaths = parsePathForBorder(curvePoints, side);
        if (style === 2 /* DASHED */) {
          this.path(boxPaths);
          this.ctx.clip();
        }
        if (isBezierCurve(boxPaths[0])) {
          startX = boxPaths[0].start.x;
          startY = boxPaths[0].start.y;
        } else {
          startX = boxPaths[0].x;
          startY = boxPaths[0].y;
        }
        if (isBezierCurve(boxPaths[1])) {
          endX = boxPaths[1].end.x;
          endY = boxPaths[1].end.y;
        } else {
          endX = boxPaths[1].x;
          endY = boxPaths[1].y;
        }
        if (side === 0 || side === 2) {
          length = Math.abs(startX - endX);
        } else {
          length = Math.abs(startY - endY);
        }
        this.ctx.beginPath();
        if (style === 3 /* DOTTED */) {
          this.formatPath(strokePaths);
        } else {
          this.formatPath(boxPaths.slice(0, 2));
        }
        dashLength = width < 3 ? width * 3 : width * 2;
        spaceLength = width < 3 ? width * 2 : width;
        if (style === 3 /* DOTTED */) {
          dashLength = width;
          spaceLength = width;
        }
        useLineDash = true;
        if (length <= dashLength * 2) {
          useLineDash = false;
        } else if (length <= dashLength * 2 + spaceLength) {
          multiplier = length / (2 * dashLength + spaceLength);
          dashLength *= multiplier;
          spaceLength *= multiplier;
        } else {
          numberOfDashes = Math.floor((length + spaceLength) / (dashLength + spaceLength));
          minSpace = (length - numberOfDashes * dashLength) / (numberOfDashes - 1);
          maxSpace = (length - (numberOfDashes + 1) * dashLength) / numberOfDashes;
          spaceLength = maxSpace <= 0 || Math.abs(spaceLength - minSpace) < Math.abs(spaceLength - maxSpace) ? minSpace : maxSpace;
        }
        if (useLineDash) {
          if (style === 3 /* DOTTED */) {
            this.ctx.setLineDash([0, dashLength + spaceLength]);
          } else {
            this.ctx.setLineDash([dashLength, spaceLength]);
          }
        }
        if (style === 3 /* DOTTED */) {
          this.ctx.lineCap = 'round';
          this.ctx.lineWidth = width;
        } else {
          this.ctx.lineWidth = width * 2 + 1.1;
        }
        this.ctx.strokeStyle = asString(color);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        // dashed round edge gap
        if (style === 2 /* DASHED */) {
          if (isBezierCurve(boxPaths[0])) {
            path1 = boxPaths[3];
            path2 = boxPaths[0];
            this.ctx.beginPath();
            this.formatPath([new Vector(path1.end.x, path1.end.y), new Vector(path2.start.x, path2.start.y)]);
            this.ctx.stroke();
          }
          if (isBezierCurve(boxPaths[1])) {
            path1 = boxPaths[1];
            path2 = boxPaths[2];
            this.ctx.beginPath();
            this.formatPath([new Vector(path1.end.x, path1.end.y), new Vector(path2.start.x, path2.start.y)]);
            this.ctx.stroke();
          }
        }
        this.ctx.restore();
        return [2 /*return*/];
      });
    });
  };
  CanvasRenderer.prototype.render = function (element) {
    return __awaiter(this, void 0, void 0, function () {
      var stack;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (this.options.backgroundColor) {
              this.ctx.fillStyle = asString(this.options.backgroundColor);
              this.ctx.fillRect(this.options.x, this.options.y, this.options.width, this.options.height);
            }
            stack = parseStackingContexts(element);
            return [4 /*yield*/, this.renderStack(stack)];
          case 1:
            _a.sent();
            this.applyEffects([]);
            return [2 /*return*/, this.canvas];
        }
      });
    });
  };
  return CanvasRenderer;
}(Renderer);
var isTextInputElement = function (container) {
  if (container instanceof TextareaElementContainer) {
    return true;
  } else if (container instanceof SelectElementContainer) {
    return true;
  } else if (container instanceof InputElementContainer && container.type !== RADIO && container.type !== CHECKBOX) {
    return true;
  }
  return false;
};
var calculateBackgroundCurvedPaintingArea = function (clip, curves) {
  switch (clip) {
    case 0 /* BORDER_BOX */:
      return calculateBorderBoxPath(curves);
    case 2 /* CONTENT_BOX */:
      return calculateContentBoxPath(curves);
    case 1 /* PADDING_BOX */:
    default:
      return calculatePaddingBoxPath(curves);
  }
};
var canvasTextAlign = function (textAlign) {
  switch (textAlign) {
    case 1 /* CENTER */:
      return 'center';
    case 2 /* RIGHT */:
      return 'right';
    case 0 /* LEFT */:
    default:
      return 'left';
  }
};
// see https://github.com/niklasvh/html2canvas/pull/2645
var iOSBrokenFonts = ['-apple-system', 'system-ui'];
var fixIOSSystemFonts = function (fontFamilies) {
  return /iPhone OS 15_(0|1)/.test(window.navigator.userAgent) ? fontFamilies.filter(function (fontFamily) {
    return iOSBrokenFonts.indexOf(fontFamily) === -1;
  }) : fontFamilies;
};
var ForeignObjectRenderer = /** @class */function (_super) {
  __extends(ForeignObjectRenderer, _super);
  function ForeignObjectRenderer(context, options) {
    var _this = _super.call(this, context, options) || this;
    _this.canvas = options.canvas ? options.canvas : document.createElement('canvas');
    _this.ctx = _this.canvas.getContext('2d');
    _this.options = options;
    _this.canvas.width = Math.floor(options.width * options.scale);
    _this.canvas.height = Math.floor(options.height * options.scale);
    _this.canvas.style.width = options.width + "px";
    _this.canvas.style.height = options.height + "px";
    _this.ctx.scale(_this.options.scale, _this.options.scale);
    _this.ctx.translate(-options.x, -options.y);
    _this.context.logger.debug("EXPERIMENTAL ForeignObject renderer initialized (" + options.width + "x" + options.height + " at " + options.x + "," + options.y + ") with scale " + options.scale);
    return _this;
  }
  ForeignObjectRenderer.prototype.render = function (element) {
    return __awaiter(this, void 0, void 0, function () {
      var svg, img;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            svg = createForeignObjectSVG(this.options.width * this.options.scale, this.options.height * this.options.scale, this.options.scale, this.options.scale, element);
            return [4 /*yield*/, loadSerializedSVG(svg)];
          case 1:
            img = _a.sent();
            if (this.options.backgroundColor) {
              this.ctx.fillStyle = asString(this.options.backgroundColor);
              this.ctx.fillRect(0, 0, this.options.width * this.options.scale, this.options.height * this.options.scale);
            }
            this.ctx.drawImage(img, -this.options.x * this.options.scale, -this.options.y * this.options.scale);
            return [2 /*return*/, this.canvas];
        }
      });
    });
  };
  return ForeignObjectRenderer;
}(Renderer);
var loadSerializedSVG = function (svg) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(svg));
  });
};
var Logger = /** @class */function () {
  function Logger(_a) {
    var id = _a.id,
      enabled = _a.enabled;
    this.id = id;
    this.enabled = enabled;
    this.start = Date.now();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Logger.prototype.debug = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    if (this.enabled) {
      // eslint-disable-next-line no-console
      if (typeof window !== 'undefined' && window.console && typeof console.debug === 'function') {
        // eslint-disable-next-line no-console
        console.debug.apply(console, __spreadArray([this.id, this.getTime() + "ms"], args));
      } else {
        this.info.apply(this, args);
      }
    }
  };
  Logger.prototype.getTime = function () {
    return Date.now() - this.start;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Logger.prototype.info = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    if (this.enabled) {
      // eslint-disable-next-line no-console
      if (typeof window !== 'undefined' && window.console && typeof console.info === 'function') {
        // eslint-disable-next-line no-console
        console.info.apply(console, __spreadArray([this.id, this.getTime() + "ms"], args));
      }
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Logger.prototype.warn = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    if (this.enabled) {
      // eslint-disable-next-line no-console
      if (typeof window !== 'undefined' && window.console && typeof console.warn === 'function') {
        // eslint-disable-next-line no-console
        console.warn.apply(console, __spreadArray([this.id, this.getTime() + "ms"], args));
      } else {
        this.info.apply(this, args);
      }
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Logger.prototype.error = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    if (this.enabled) {
      // eslint-disable-next-line no-console
      if (typeof window !== 'undefined' && window.console && typeof console.error === 'function') {
        // eslint-disable-next-line no-console
        console.error.apply(console, __spreadArray([this.id, this.getTime() + "ms"], args));
      } else {
        this.info.apply(this, args);
      }
    }
  };
  Logger.instances = {};
  return Logger;
}();
var Context = /** @class */function () {
  function Context(options, windowBounds) {
    var _a;
    this.windowBounds = windowBounds;
    this.instanceName = "#" + Context.instanceCount++;
    this.logger = new Logger({
      id: this.instanceName,
      enabled: options.logging
    });
    this.cache = (_a = options.cache) !== null && _a !== void 0 ? _a : new Cache$1(this, options);
  }
  Context.instanceCount = 1;
  return Context;
}();
var html2canvas = function (element, options) {
  if (options === void 0) {
    options = {};
  }
  return renderElement(element, options);
};
if (typeof window !== 'undefined') {
  CacheStorage.setContext(window);
}
var renderElement = function (element, opts) {
  return __awaiter(void 0, void 0, void 0, function () {
    var ownerDocument, defaultView, resourceOptions, contextOptions, windowOptions, windowBounds, context, foreignObjectRendering, cloneOptions, documentCloner, clonedElement, container, _a, width, height, left, top, backgroundColor, renderOptions, canvas, renderer, root, renderer;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    return __generator(this, function (_u) {
      switch (_u.label) {
        case 0:
          if (!element || typeof element !== 'object') {
            return [2 /*return*/, Promise.reject('Invalid element provided as first argument')];
          }
          ownerDocument = element.ownerDocument;
          if (!ownerDocument) {
            throw new Error("Element is not attached to a Document");
          }
          defaultView = ownerDocument.defaultView;
          if (!defaultView) {
            throw new Error("Document is not attached to a Window");
          }
          resourceOptions = {
            allowTaint: (_b = opts.allowTaint) !== null && _b !== void 0 ? _b : false,
            imageTimeout: (_c = opts.imageTimeout) !== null && _c !== void 0 ? _c : 15000,
            proxy: opts.proxy,
            useCORS: (_d = opts.useCORS) !== null && _d !== void 0 ? _d : false
          };
          contextOptions = __assign({
            logging: (_e = opts.logging) !== null && _e !== void 0 ? _e : true,
            cache: opts.cache
          }, resourceOptions);
          windowOptions = {
            windowWidth: (_f = opts.windowWidth) !== null && _f !== void 0 ? _f : defaultView.innerWidth,
            windowHeight: (_g = opts.windowHeight) !== null && _g !== void 0 ? _g : defaultView.innerHeight,
            scrollX: (_h = opts.scrollX) !== null && _h !== void 0 ? _h : defaultView.pageXOffset,
            scrollY: (_j = opts.scrollY) !== null && _j !== void 0 ? _j : defaultView.pageYOffset
          };
          windowBounds = new Bounds(windowOptions.scrollX, windowOptions.scrollY, windowOptions.windowWidth, windowOptions.windowHeight);
          context = new Context(contextOptions, windowBounds);
          foreignObjectRendering = (_k = opts.foreignObjectRendering) !== null && _k !== void 0 ? _k : false;
          cloneOptions = {
            allowTaint: (_l = opts.allowTaint) !== null && _l !== void 0 ? _l : false,
            onclone: opts.onclone,
            ignoreElements: opts.ignoreElements,
            inlineImages: foreignObjectRendering,
            copyStyles: foreignObjectRendering
          };
          context.logger.debug("Starting document clone with size " + windowBounds.width + "x" + windowBounds.height + " scrolled to " + -windowBounds.left + "," + -windowBounds.top);
          documentCloner = new DocumentCloner(context, element, cloneOptions);
          clonedElement = documentCloner.clonedReferenceElement;
          if (!clonedElement) {
            return [2 /*return*/, Promise.reject("Unable to find element in cloned iframe")];
          }
          return [4 /*yield*/, documentCloner.toIFrame(ownerDocument, windowBounds)];
        case 1:
          container = _u.sent();
          _a = isBodyElement(clonedElement) || isHTMLElement(clonedElement) ? parseDocumentSize(clonedElement.ownerDocument) : parseBounds(context, clonedElement), width = _a.width, height = _a.height, left = _a.left, top = _a.top;
          backgroundColor = parseBackgroundColor(context, clonedElement, opts.backgroundColor);
          renderOptions = {
            canvas: opts.canvas,
            backgroundColor: backgroundColor,
            scale: (_o = (_m = opts.scale) !== null && _m !== void 0 ? _m : defaultView.devicePixelRatio) !== null && _o !== void 0 ? _o : 1,
            x: ((_p = opts.x) !== null && _p !== void 0 ? _p : 0) + left,
            y: ((_q = opts.y) !== null && _q !== void 0 ? _q : 0) + top,
            width: (_r = opts.width) !== null && _r !== void 0 ? _r : Math.ceil(width),
            height: (_s = opts.height) !== null && _s !== void 0 ? _s : Math.ceil(height)
          };
          if (!foreignObjectRendering) return [3 /*break*/, 3];
          context.logger.debug("Document cloned, using foreign object rendering");
          renderer = new ForeignObjectRenderer(context, renderOptions);
          return [4 /*yield*/, renderer.render(clonedElement)];
        case 2:
          canvas = _u.sent();
          return [3 /*break*/, 5];
        case 3:
          context.logger.debug("Document cloned, element located at " + left + "," + top + " with size " + width + "x" + height + " using computed rendering");
          context.logger.debug("Starting DOM parsing");
          root = parseTree(context, clonedElement);
          if (backgroundColor === root.styles.backgroundColor) {
            root.styles.backgroundColor = COLORS.TRANSPARENT;
          }
          context.logger.debug("Starting renderer for element at " + renderOptions.x + "," + renderOptions.y + " with size " + renderOptions.width + "x" + renderOptions.height);
          renderer = new CanvasRenderer(context, renderOptions);
          return [4 /*yield*/, renderer.render(root)];
        case 4:
          canvas = _u.sent();
          _u.label = 5;
        case 5:
          if ((_t = opts.removeContainer) !== null && _t !== void 0 ? _t : true) {
            if (!DocumentCloner.destroy(container)) {
              context.logger.error("Cannot detach cloned iframe as it is not in the DOM anymore");
            }
          }
          context.logger.debug("Finished rendering");
          return [2 /*return*/, canvas];
      }
    });
  });
};
var parseBackgroundColor = function (context, element, backgroundColorOverride) {
  var ownerDocument = element.ownerDocument;
  // http://www.w3.org/TR/css3-background/#special-backgrounds
  var documentBackgroundColor = ownerDocument.documentElement ? parseColor(context, getComputedStyle(ownerDocument.documentElement).backgroundColor) : COLORS.TRANSPARENT;
  var bodyBackgroundColor = ownerDocument.body ? parseColor(context, getComputedStyle(ownerDocument.body).backgroundColor) : COLORS.TRANSPARENT;
  var defaultBackgroundColor = typeof backgroundColorOverride === 'string' ? parseColor(context, backgroundColorOverride) : backgroundColorOverride === null ? COLORS.TRANSPARENT : 0xffffffff;
  return element === ownerDocument.documentElement ? isTransparent(documentBackgroundColor) ? isTransparent(bodyBackgroundColor) ? defaultBackgroundColor : bodyBackgroundColor : documentBackgroundColor : defaultBackgroundColor;
};

//
var script$2B = {
  name: 'xmlRobotPc',
  props: {
    bookName: {
      type: String,
      default: ''
    },
    goodsId: {
      type: String,
      default: ''
    },
    robotData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      list: [],
      robotShwo: false,
      timer: 0,
      textIndex: 1,
      Interval: null,
      robotWidth: 362,
      //400
      robotHeight: 500,
      //600
      left: 0,
      current: 0,
      screenX: 0,
      screenY: 0,
      correct: ['Hi', '，', '快', '来', '一', '起', '学', '习', '吧', '!'],
      elfinStatic: true,
      exportStatic: false,
      timeout: null,
      conversationId: ''
    };
  },
  directives: {
    drag: {
      bind: function (el, direction) {
        // console.log(el);
        let oDiv = el; // 获取当前元素
        oDiv.onmousedown = e => {
          // console.log(e);
          let dir = direction.value || {
            top: true,
            left: true,
            dragClass: ''
          };
          if (!dir.dragClass || e.target.className.indexOf(dir.dragClass) !== -1) {
            // 算出鼠标相对元素的位置
            let disX = e.clientX - oDiv.offsetLeft;
            let disY = e.clientY - oDiv.offsetTop;
            document.onmousemove = e => {
              // 用鼠标的位置减去鼠标相对元素的位置，得到元素的位置
              let left = e.clientX - disX;
              let top = e.clientY - disY;
              if (dir.top && top > 0) {
                oDiv.style.top = top + 'px';
              }
              if (dir.left) {
                oDiv.style.left = left + 'px';
              }
            };
            document.onmouseup = e => {
              document.onmousemove = null;
              document.onmouseup = null;
            };
          }
        };
      }
    }
  },
  mounted() {
    this.robotHeight = window.innerHeight - 300;
    this.createAConversation();
    // calc((100% - 220px) / 2)
    this.left = (document.documentElement.clientWidth - 220) / 2 + 'px';
    setTimeout(() => {
      this.elfinStatic = false;
    }, 5000);
  },
  methods: {
    mousedown(event, axial, direction) {
      this.current = axial == 'X' ? event.screenX : event.screenY;
      let top = Number(this.$refs.robotView.style.top.split('px')[0]);
      let left = Number(this.$refs.robotView.style.left.split('px')[0]);
      document.onmousemove = e => {
        if (direction == 'right') {
          if (e.screenX - this.current < 0 && this.robotWidth + this.screenX > 300) {
            this.screenX = e.screenX - this.current;
          } else if (e.screenX - this.current > 0 && this.robotWidth + this.screenX < 1000) {
            this.screenX = e.screenX - this.current;
          }
        } else if (direction == 'bottom') {
          if (e.screenY - this.current < 0 && this.robotHeight + this.screenY > 500) {
            this.screenY = e.screenY - this.current;
          } else if (e.screenY - this.current > 0 && this.robotHeight + this.screenY < document.documentElement.clientHeight - 140) {
            this.screenY = e.screenY - this.current;
          }
        } else if (direction == 'top') {
          if (this.current - e.screenY < 0 && this.robotHeight + this.screenY > 500) {
            this.screenY = this.current - e.screenY;
          } else if (this.current - e.screenY > 0 && this.robotHeight + this.screenY < document.documentElement.clientHeight - 140 && top - this.screenY > 0) {
            this.screenY = this.current - e.screenY;
          }
          this.$refs.robotView.style.top = top - this.screenY + 'px';
        } else if (direction == 'left') {
          if (this.current - e.screenX < 0 && this.robotWidth + this.screenX > 300) {
            this.screenX = this.current - e.screenX;
          } else if (this.current - e.screenX > 0 && this.robotWidth + this.screenX < 1000) {
            this.screenX = this.current - e.screenX;
          }
          this.$refs.robotView.style.left = left - this.screenX + 'px';
        }
      };
      document.onmouseup = e => {
        this.robotWidth += this.screenX;
        this.robotHeight += this.screenY;
        this.screenX = 0;
        this.screenY = 0;
        document.onmousemove = null;
      };
    },
    //   发送
    sending(Text) {
      if (Text.replace(/\s+/g, '') != '') {
        let rightData = {
          id: 'right_' + new Date().getTime(),
          direction: 'right',
          content: Text.replace(/\s+/g, ''),
          type: 'text',
          loading: false,
          time: this.getTime(),
          videoUrl: '',
          loadingCompleted: true
        };
        this.list.push(rightData);
        // 发送消息是创建一条内为空的回答
        let leftData = {
          id: 'left_' + new Date().getTime(),
          direction: 'left',
          content: '',
          type: 'text',
          loading: true,
          time: this.getTime(),
          videoUrl: '',
          loadingCompleted: false
        };
        this.list.push(leftData);
        // 获取回答内容
        this.loadData(Text.replace(/\s+/g, ''), this.list.length - 1);
        this.$refs.Input.innerHTML = '';
        setTimeout(() => {
          this.scrollToBottom();
        }, 200);
      }
    },
    // 获取当前时间
    getTime() {
      let date = new Date();
      let h = date.getHours();
      let m = date.getMinutes();
      return (h > 9 ? h : '0' + h) + ':' + (m > 9 ? m : '0' + m);
    },
    // 回车事件
    handlePushKeyword(event) {
      if (event.keyCode === 13) {
        event.preventDefault(); // 阻止浏览器默认换行操作
        this.sending(this.$refs.Input.innerText); // 发送文本
        return false;
      }
    },
    // 滚动内容
    scrollToBottom() {
      let Dom = this.$refs.scrollDiv;
      Dom.scrollTop = Dom.scrollHeight;
    },
    moveStart() {
      this.timer = new Date().getTime();
    },
    // 打开小智答疑窗口
    elfinClick() {
      let timer = new Date().getTime();
      if (timer - this.timer < 400) {
        // this.$message.warning("功能升级中..")
        this.robotShwo = true;
        if (this.list.length === 0) {
          //创建一条欢迎语
          ({
            id: 'left_777',
            direction: 'left',
            content: '您好，欢迎使用国开出版社＂小智＂达人~',
            type: 'text',
            loading: false,
            time: this.getTime(),
            videoUrl: '',
            loadingCompleted: true
          });
          this.printer(18);
        }
      }
    },
    // 创建消息
    async createAConversation() {
      fetch(this.robotData.baseUrl, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: 'Bearer ' + this.robotData.token
        },
        body: JSON.stringify({
          app_id: this.robotData.appId
        })
      }).then(response => response.json()).then(json => {
        this.conversationId = json.conversation_id;
      });
    },
    // 获取小智答疑答案
    async loadData(question, index) {
      this.$nextTick;
      let controller = new AbortController();
      try {
        const response = await fetch(this.robotData.conversationUrl, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json;charset=utf-8',
            Authorization: 'Bearer ' + this.robotData.token
          },
          body: JSON.stringify({
            app_id: this.robotData.appId,
            query: question,
            stream: true,
            conversation_id: this.conversationId,
            signal: controller.signal
          })
        });
        const reader = response.body.getReader();
        let data = '';
        let videoUrl = '';
        let content = '';
        while (true) {
          const {
            done,
            value
          } = await reader.read();
          console.log('🚀 ~ loadData ~ done:', done);
          if (done) {
            this.list[index].loadingCompleted = true;
            // setLoading(false);
            break;
          }
          data = new TextDecoder().decode(value);
          if (data.indexOf('answer')) {
            let textList = data.split('"answer": "') || [];
            if (textList.length > 1) {
              let text = textList[1].split('", "conversation_id')[0] || '';
              if (text.replace(/\s+/g, '') != '') {
                this.list[index].content += text;
              }
            }
          }

          // if (data.indexOf('DONE') !== -1) {
          //   this.list[index].loadingCompleted = true
          // }
          // content = ''
          // // 数据更新时销毁加载完成计时器
          // if (this.timeout) {
          //   clearTimeout(this.timeout)
          //   this.timeout = null
          // }
          // let eaderStream = data.split('\r\n\r\n')
          // eaderStream.map((item) => {
          //   if (item) {
          //     let textData = item.split('data:')

          //     textData.map((items) => {
          //       if (items && items.indexOf('{') !== -1) {
          //         let text = JSON.parse(items)
          //         if (text.content) {
          //           content += text.content
          //         }
          //       } else if (items.indexOf('.mp4') !== -1) {
          //         videoUrl = items.slice(1, items.length - 7)
          //       }
          //     })
          //   }
          // })
          this.list[index].loading = false;
          this.list[index].type = 'text';

          // this.list[index].videoUrl = videoUrl
          setTimeout(() => {
            // this.scrollToBottom();
            let itemDom = document.getElementById('robot-content-' + index);
            let Dom = this.$refs.scrollDiv;
            Dom.scrollTop = itemDom.offsetTop + itemDom.offsetHeight - 500;
            // console.log(DOM.offsetHeight);
            // console.log(DOM.offsetTop);
          }, 200);

          // // 防止加载完成还显示加载中进度条，5s后默认关闭
          // this.timeout = setTimeout(() => {
          //   this.list[index].loadingCompleted = true
          // }, 5000)
        }
      } catch {
        this.list[index].loading = false;
        this.list[index].loadingCompleted = true;
        this.list[index].content = '亲，我的知识库里无法找到答案，请您修改问题重新提问。';
        this.list[index].type = 'text';
      }
    },
    closeRobot() {
      this.robotShwo = false;
      let videoList = document.getElementById('robot-content-box').querySelectorAll('video') || [];
      videoList.forEach(i => {
        i.pause();
      });
    },
    /**
     *
     */
    // 文字打印机
    printer(num) {
      this.textIndex = 1;
      this.Interval = setInterval(() => {
        this.textIndex++;
        setTimeout(() => {
          this.scrollToBottom();
        }, 200);
        if (this.textIndex > num) {
          clearInterval(this.Interval);
          this.Interval = null;
        }
      }, 50);
    },
    // 删除消息
    removalMessage(item, index) {
      this.list.splice(index, 1);
      console.log(item, index);
    },
    // 刷新消息
    refreshMessage(item, index) {
      this.list[index].loadingCompleted = false;
      this.list[index].loading = true;
      this.list[index].content = '';
      this.list[index].type = '';
      this.list[index].videoUrl = '';
      this.loadData(this.list[index - 1].content, index);
      console.log(item, index);
    },
    /**
     * 将页面指定节点内容转为图片
     * 1.拿到想要转换为图片的内容节点DOM；
     * 2.转换，拿到转换后的canvas
     * 3.转换为图片
     */
    clickGeneratePicture() {
      const loading = this.$loading({
        lock: true,
        text: '正在生成图片',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      this.exportStatic = true;
      html2canvas(this.$refs.downloadDom, {
        allowTaint: true,
        // 允许渲染跨域图片
        scale: window.devicePixelRatio * 2,
        // 增加清晰度
        useCORS: true // 允许跨域
      }).then(canvas => {
        this.exportStatic = false;
        // 转成图片，生成图片地址
        let imgUrl = canvas.toDataURL('image/png');
        document.querySelector('#downimg').href = imgUrl;
        document.querySelector('#downimg').download = new Date().getTime() + '.png';
        document.querySelector('#downimg').click();
        loading.close();
      });
    }
  }
};

/* script */
const __vue_script__$2B = script$2B;
/* template */
var __vue_render__$2B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-robot-content-pc",
      style: { pointerEvents: !_vm.robotShwo ? "none" : "fill" },
      on: {
        click: function ($event) {
          if ($event.target !== $event.currentTarget) {
            return null
          }
          _vm.robotShwo = false;
        },
      },
    },
    [
      _c("transition", { attrs: { name: "Fade" } }, [
        _c(
          "div",
          {
            directives: [
              {
                name: "drag",
                rawName: "v-drag:{top:false,left:false,dragClass:null}",
                value: {
                  top: true,
                  left: true,
                  dragClass: "drag-box",
                },
                expression:
                  "{\n        top: true,\n        left: true,\n        dragClass: 'drag-box'\n      }",
                arg: "{top:false,left:false,dragClass:null}",
              },
              {
                name: "show",
                rawName: "v-show",
                value: _vm.robotShwo,
                expression: "robotShwo",
              },
            ],
            ref: "robotView",
            staticClass: "robot-view",
            style: {
              width: _vm.robotWidth + _vm.screenX + "px",
              top: "66px",
              left: _vm.left,
            },
          },
          [
            _c("div", { staticClass: "title-bar drag-box" }, [
              _c("div", { staticClass: "logo drag-box" }, [
                _c("span", { staticClass: "drag-box" }, [_vm._v("小智AI")]),
              ]),
            ]),
            _vm._v(" "),
            _c(
              "div",
              {
                ref: "scrollDiv",
                staticClass: "content scroll",
                style: { height: _vm.robotHeight + _vm.screenY + "px" },
                attrs: { id: "robot-content-box" },
              },
              [
                _c("div", { staticClass: "xiaozhi-tips" }, [
                  _vm._v(
                    "\n          你好，我是你的学习助手“小智”\n          ，我可以为你解答此教材相关的内容，如章节摘要、段意总结、习题解析等，试试在下方输入问题吧\n        "
                  ),
                ]),
                _vm._v(" "),
                _vm._l(_vm.list, function (item, index) {
                  return _c("div", { key: item.id }, [
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value:
                              item.direction === "right" ||
                              item.id == "left_777",
                            expression:
                              "item.direction === 'right' || item.id == 'left_777'",
                          },
                        ],
                        staticClass: "timer",
                      },
                      [
                        _vm._v(
                          "\n            " + _vm._s(item.time) + "\n          "
                        ),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.direction === "left",
                            expression: "item.direction === 'left'",
                          },
                        ],
                        staticClass: "item",
                        class: item.direction,
                        attrs: { id: "robot-content-" + index },
                      },
                      [
                        _c("div", { staticClass: "text" }, [
                          _vm._v(
                            "\n              " +
                              _vm._s(item.content) +
                              "\n              "
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              directives: [
                                {
                                  name: "show",
                                  rawName: "v-show",
                                  value: item.videoUrl,
                                  expression: "item.videoUrl",
                                },
                              ],
                            },
                            [
                              _vm._v(
                                "\n                下面“小智”还为您推荐视频\n                "
                              ),
                              item.videoUrl
                                ? _c(
                                    "video",
                                    {
                                      staticStyle: { margin: "14px 0 0 0" },
                                      attrs: {
                                        width: "100%",
                                        controls: "",
                                        controlslist: "nodownload",
                                      },
                                    },
                                    [
                                      _c("source", {
                                        attrs: {
                                          src: item.videoUrl,
                                          type: "video/mp4",
                                        },
                                      }),
                                    ]
                                  )
                                : _vm._e(),
                            ]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              directives: [
                                {
                                  name: "show",
                                  rawName: "v-show",
                                  value: item.loading,
                                  expression: "item.loading",
                                },
                              ],
                              staticStyle: { "line-height": "24px" },
                            },
                            [_vm._v("正在生成. . .")]
                          ),
                          _vm._v(" "),
                          _c("span", {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: !item.loadingCompleted,
                                expression: "!item.loadingCompleted",
                              },
                            ],
                          }),
                        ]),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: item.loadingCompleted,
                                expression: "item.loadingCompleted",
                              },
                            ],
                            staticClass: "operating",
                          },
                          [
                            _c("div", {
                              staticClass: "delete",
                              on: {
                                click: function ($event) {
                                  return _vm.removalMessage(item, index)
                                },
                              },
                            }),
                            _vm._v(" "),
                            _c("div", {
                              directives: [
                                {
                                  name: "show",
                                  rawName: "v-show",
                                  value: item.id != "left_777",
                                  expression: "item.id != 'left_777'",
                                },
                              ],
                              staticClass: "refresh",
                              on: {
                                click: function ($event) {
                                  return _vm.refreshMessage(item, index)
                                },
                              },
                            }),
                          ]
                        ),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        directives: [
                          {
                            name: "show",
                            rawName: "v-show",
                            value: item.direction === "right",
                            expression: "item.direction === 'right'",
                          },
                        ],
                        staticClass: "item",
                        class: item.direction,
                      },
                      [
                        _c("div", { staticClass: "operating" }, [
                          _c("div", {
                            staticClass: "delete",
                            on: {
                              click: function ($event) {
                                return _vm.removalMessage(item, index)
                              },
                            },
                          }),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "text" }, [
                          _vm._v(
                            "\n              " +
                              _vm._s(item.content) +
                              "\n            "
                          ),
                        ]),
                      ]
                    ),
                  ])
                }),
              ],
              2
            ),
            _vm._v(" "),
            _c(
              "div",
              {
                staticClass: "download-dom",
                style: {
                  width: _vm.robotWidth + _vm.screenX + "px",
                },
              },
              [
                _c(
                  "div",
                  { ref: "downloadDom", staticClass: "content" },
                  _vm._l(_vm.list, function (item) {
                    return _c("div", { key: item.id }, [
                      _c(
                        "div",
                        {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: item.direction === "left",
                              expression: "item.direction === 'left'",
                            },
                          ],
                          staticClass: "item",
                          class: item.direction,
                        },
                        [
                          _c("div", { staticClass: "text" }, [
                            _vm._v(
                              "\n                " +
                                _vm._s(item.content) +
                                "\n                "
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: item.videoUrl,
                                    expression: "item.videoUrl",
                                  },
                                ],
                              },
                              [
                                _vm._v(
                                  "\n                  下面“小智”还为您推荐视频\n                  "
                                ),
                                item.videoUrl
                                  ? _c(
                                      "video",
                                      {
                                        staticStyle: { margin: "14px 0 0 0" },
                                        attrs: {
                                          width: "100%",
                                          controls: "",
                                          controlslist: "nodownload",
                                        },
                                      },
                                      [
                                        _c("source", {
                                          attrs: {
                                            src: item.videoUrl,
                                            type: "video/mp4",
                                          },
                                        }),
                                      ]
                                    )
                                  : _vm._e(),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value: item.loading,
                                    expression: "item.loading",
                                  },
                                ],
                                staticStyle: { "line-height": "24px" },
                              },
                              [_vm._v("正在生成. . .")]
                            ),
                            _vm._v(" "),
                            _c("span", {
                              directives: [
                                {
                                  name: "show",
                                  rawName: "v-show",
                                  value: !item.loadingCompleted,
                                  expression: "!item.loadingCompleted",
                                },
                              ],
                            }),
                          ]),
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "div",
                        {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: item.direction === "right",
                              expression: "item.direction === 'right'",
                            },
                          ],
                          staticClass: "item",
                          class: item.direction,
                        },
                        [
                          _c("div", { staticClass: "text" }, [
                            _vm._v(
                              "\n                " +
                                _vm._s(item.content) +
                                "\n              "
                            ),
                          ]),
                        ]
                      ),
                    ])
                  }),
                  0
                ),
              ]
            ),
            _vm._v(" "),
            _c("div", { staticClass: "input-box" }, [
              _c("div", { staticClass: "input-right" }, [
                _c("div", {
                  ref: "Input",
                  staticClass: "input-content",
                  attrs: {
                    contenteditable: "true",
                    placeholder: "请输入问题描述",
                  },
                  on: {
                    keydown: function ($event) {
                      return _vm.handlePushKeyword($event)
                    },
                  },
                }),
                _vm._v(" "),
                _c("div", {
                  staticClass: "sending",
                  on: {
                    click: function ($event) {
                      return _vm.sending(_vm.$refs.Input.innerText)
                    },
                  },
                }),
              ]),
            ]),
            _vm._v(" "),
            _c("div", {
              staticClass: "point point-right",
              on: {
                mousedown: function ($event) {
                  return _vm.mousedown($event, "X", "right")
                },
              },
            }),
            _vm._v(" "),
            _c("div", {
              staticClass: "point point-bottom",
              on: {
                mousedown: function ($event) {
                  return _vm.mousedown($event, "Y", "bottom")
                },
              },
            }),
          ]
        ),
      ]),
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: !_vm.robotShwo,
              expression: "!robotShwo",
            },
            {
              name: "drag",
              rawName: "v-drag:{top:false,left:false}",
              value: { top: true, left: true },
              expression: "{ top: true, left: true }",
              arg: "{top:false,left:false}",
            },
          ],
          staticClass: "elfin-box",
          on: {
            mousedown: _vm.moveStart,
            click: function ($event) {
              $event.preventDefault();
              return _vm.elfinClick.apply(null, arguments)
            },
          },
        },
        [
          _c("transition", { attrs: { name: "Fade" } }, [
            _c(
              "div",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.elfinStatic,
                    expression: "elfinStatic",
                  },
                ],
                staticClass: "dialog-box",
              },
              _vm._l(_vm.correct, function (item, index) {
                return _c(
                  "span",
                  {
                    key: index,
                    style: {
                      "animation-delay": 0.2 + index / _vm.correct.length + "s",
                    },
                  },
                  [_vm._v(_vm._s(item))]
                )
              }),
              0
            ),
          ]),
        ],
        1
      ),
      _vm._v(" "),
      _c("a", {
        staticStyle: { display: "none" },
        attrs: { id: "downimg", href: "" },
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$2B = [];
__vue_render__$2B._withStripped = true;

  /* style */
  const __vue_inject_styles__$2B = undefined;
  /* scoped */
  const __vue_scope_id__$2B = "data-v-2624c07c";
  /* module identifier */
  const __vue_module_identifier__$2B = undefined;
  /* functional template */
  const __vue_is_functional_template__$2B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2B, staticRenderFns: __vue_staticRenderFns__$2B },
    __vue_inject_styles__$2B,
    __vue_script__$2B,
    __vue_scope_id__$2B,
    __vue_is_functional_template__$2B,
    __vue_module_identifier__$2B,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2A = {
  name: 'xmlRobotPhone',
  props: {
    bookName: {
      type: String,
      default: '建筑构造'
    },
    robotData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      startTop: 0,
      startLeft: 0,
      top: 200,
      left: 0,
      width: 100,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      drag: false,
      show: false,
      question: '',
      list: [],
      minimize: false,
      //是否最小化
      click: false,
      exportStatic: false,
      conversationId: ''
    };
  },
  computed: {
    // 悬浮小智的宽度
    robotWidth() {
      let px = this.windowWidth / 750 * Number(this.width);
      return Math.floor(px);
    },
    robotX() {
      let px = this.windowWidth / 750 * Number(this.width);
      return this.windowWidth - Math.floor(px);
    },
    scale() {
      let scale = this.windowWidth / 750;
      return scale;
    }
  },
  mounted() {
    this.left = this.robotX;
    this.createAConversation();
  },
  methods: {
    // 发送消息
    sendingMsg(question) {
      if (question.replace(/\s+/g, '') != '') {
        let rightData = {
          id: 'right_' + new Date().getTime(),
          direction: 'right',
          content: question.replace(/\s+/g, ''),
          type: 'text',
          loading: false,
          time: this.getTime(),
          videoUrl: '',
          question: question.replace(/\s+/g, ''),
          loadingCompleted: true
        };
        this.list.push(rightData);
        // 发送消息是创建一条内为空的回答
        let leftData = {
          id: 'left_' + new Date().getTime(),
          direction: 'left',
          content: '',
          type: 'text',
          loading: true,
          time: this.getTime(),
          videoUrl: '',
          question: question.replace(/\s+/g, ''),
          loadingCompleted: false
        };
        this.list.push(leftData);
        // this.scrollPage()
        // 获取回答内容
        this.loadData(question.replace(/\s+/g, ''), this.list.length - 1);
        this.question = '';
        setTimeout(() => {
          this.scrollToBottom();
        }, 200);
      }
    },
    // 创建消息
    async createAConversation() {
      fetch(this.robotData.baseUrl, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: 'Bearer ' + this.robotData.token
        },
        body: JSON.stringify({
          app_id: this.robotData.appId
        })
      }).then(response => response.json()).then(json => {
        this.conversationId = json.conversation_id;
      });
    },
    // 获取小智答疑答案
    async loadData(question, index) {
      this.$nextTick;
      let controller = new AbortController();
      try {
        const response = await fetch(this.robotData.conversationUrl, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json;charset=utf-8',
            Authorization: 'Bearer ' + this.robotData.token
          },
          body: JSON.stringify({
            app_id: this.robotData.appId,
            query: question,
            stream: true,
            conversation_id: this.conversationId,
            signal: controller.signal
          })
        });
        const reader = response.body.getReader();
        let data = '';
        let videoUrl = '';
        let content = '';
        while (true) {
          const {
            done,
            value
          } = await reader.read();
          console.log('🚀 ~ loadData ~ done:', done);
          if (done) {
            this.list[index].loadingCompleted = true;
            break;
          }
          data = new TextDecoder().decode(value);
          if (data.indexOf('answer')) {
            let textList = data.split('"answer": "') || [];
            if (textList.length > 1) {
              let text = textList[1].split('", "conversation_id')[0] || '';
              if (text.replace(/\s+/g, '') != '') {
                this.list[index].content += text;
              }
            }
          }
          this.list[index].loading = false;
          this.list[index].type = 'text';
          setTimeout(() => {
            this.scrollToBottom();
          }, 200);
        }
      } catch {
        this.list[index].loading = false;
        this.list[index].loadingCompleted = true;
        this.list[index].content = '亲，我的知识库里无法找到答案，请您修改问题重新提问。';
        this.list[index].type = 'text';
      }
    },
    // 滚动内容
    scrollToBottom() {
      let Dom = this.$refs.scrollDiv;
      Dom.scrollTop = Dom.scrollHeight;
    },
    // 删除消息
    removalMessage(item, index) {
      this.list.splice(index, 1);
    },
    // 刷新消息
    refreshMessage(item, index) {
      this.list[index].loadingCompleted = false;
      this.list[index].loading = true;
      this.list[index].content = '';
      this.list[index].type = '';
      this.list[index].videoUrl = '';
      this.loadData(item.question, index);
    },
    // 点击小智
    clickRobot() {
      if (this.minimize) {
        this.minimize = false;
        this.width = 100;
        this.$nextTick(() => {
          this.left = this.robotX;
        });
      } else {
        this.show = true;
      }
    },
    onTouchstart(e) {
      this.drag = true;
      this.click = true;
      this.startTop = e.touches[0].radiusY;
      this.startLeft = e.touches[0].radiusX;
      setTimeout(() => {
        this.click = false;
      }, 200);
    },
    noTouchmove(e) {
      let top = e.changedTouches[0].clientY - this.startTop;
      let left = e.changedTouches[0].clientX - this.startLeft;
      if (left > -20 && top > -20 && left < this.robotX + 20 && top < this.windowHeight - 60 && !this.minimize) {
        this.top = e.changedTouches[0].clientY - this.startTop;
        this.left = e.changedTouches[0].clientX - this.startLeft;
      }
    },
    // 手指松开
    onMouseup(e) {
      this.drag = false;
      if (!this.click) {
        let robotX = this.windowWidth - (e.changedTouches[0].clientX + (this.robotWidth - this.startLeft));
        if (robotX < -10) {
          this.minimize = true;
          this.width = 30;
        }
        this.$nextTick(() => {
          this.left = this.robotX;
        });
      }
    },
    /**
     * 将页面指定节点内容转为图片
     * 1.拿到想要转换为图片的内容节点DOM；
     * 2.转换，拿到转换后的canvas
     * 3.转换为图片
     */
    clickGeneratePicture() {
      // const loading = this.$loading({
      //   lock: true,
      //   text: "正在生成图片",
      //   spinner: "el-icon-loading",
      //   background: "rgba(0, 0, 0, 0.7)",
      // });
      this.exportStatic = true;
      setTimeout(() => {
        html2canvas(this.$refs.downloadDom, {
          allowTaint: true,
          // 允许渲染跨域图片
          scale: window.devicePixelRatio * 2,
          // 增加清晰度
          useCORS: true // 允许跨域
        }).then(canvas => {
          this.exportStatic = false;
          // 转成图片，生成图片地址
          let imgUrl = canvas.toDataURL('image/png');
          console.log('🚀 ~ file: xmlRobot.vue:345 ~ clickGeneratePicture ~ imgUrl:', imgUrl);
          document.querySelector('#downimg').href = imgUrl;
          document.querySelector('#downimg').download = new Date().getTime() + '.png';
          document.querySelector('#downimg').click();
          // loading.close();
        });
      }, 1000);
    },
    // 获取当前时间
    getTime() {
      let date = new Date();
      let h = date.getHours();
      let m = date.getMinutes();
      return (h > 9 ? h : '0' + h) + ':' + (m > 9 ? m : '0' + m);
    }
  }
};

var __$_require_static_images_icons_ai_xiaozhi_logo2_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$2A = script$2A;
var __vue_render__$2A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-robot-content-phone",
      class: _vm.show ? "pointer-events" : "",
    },
    [
      _c(
        "div",
        {
          staticClass: "levitated-sphere",
          class: _vm.minimize ? "min-robot" : !_vm.drag ? "drag-left" : "",
          style: {
            width: _vm.robotWidth + "px",
            top: _vm.top + "px",
            left: _vm.left + "px",
            height: 192 * _vm.scale + "px",
          },
          on: {
            touchstart: _vm.onTouchstart,
            touchmove: _vm.noTouchmove,
            touchend: _vm.onMouseup,
            click: _vm.clickRobot,
          },
        },
        [
          _c("div", {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: !_vm.minimize,
                expression: "!minimize",
              },
            ],
            staticClass: "xml-robot-image",
            style: {
              width: 160 * _vm.scale + "px",
              height: 192 * _vm.scale + "px",
            },
          }),
        ]
      ),
      _vm._v(" "),
      _c(
        "van-popup",
        {
          attrs: { position: "bottom", round: "" },
          model: {
            value: _vm.show,
            callback: function ($$v) {
              _vm.show = $$v;
            },
            expression: "show",
          },
        },
        [
          _c("div", { staticClass: "xml-robot-content" }, [
            _c(
              "div",
              {
                staticClass: "navbar-box",
                style: {
                  width: 750 * _vm.scale + "px",
                  height: 80 * _vm.scale + "px",
                },
              },
              [
                _c("img", {
                  style: { width: 76 * _vm.scale + "px" },
                  attrs: {
                    src: __$_require_static_images_icons_ai_xiaozhi_logo2_png__,
                    alt: "",
                  },
                }),
                _vm._v(" "),
                _c("span", { style: { fontSize: 34 * _vm.scale + "px" } }, [
                  _vm._v("小智AI"),
                ]),
              ]
            ),
            _vm._v(" "),
            _c(
              "div",
              {
                ref: "scrollDiv",
                staticClass: "robot-dialog-box",
                style: { height: "calc(80vh - " + 220 * _vm.scale + "px)" },
              },
              [
                _c(
                  "div",
                  {
                    staticClass: "xiaozhi-tips",
                    style: {
                      fontSize: 22 * _vm.scale + "px",
                      lineHeight: 48 * _vm.scale + "px",
                      padding: 26 * _vm.scale + "px",
                    },
                  },
                  [
                    _vm._v(
                      "\n          你好，我是你的学习助手“小智”\n          ，我可以为你解答此教材相关的内容，如章节摘要、段意总结、习题解析等，试试在下方输入问题吧\n        "
                    ),
                  ]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    ref: "downloadDom",
                    staticClass: "robot-dialog-main",
                    style: { padding: "0 " + 32 * _vm.scale + "px" },
                  },
                  _vm._l(_vm.list, function (item, index) {
                    return _c(
                      "div",
                      {
                        key: index,
                        staticClass: "robot-item-box",
                        style: { paddingBottom: 56 * _vm.scale + "px" },
                      },
                      [
                        _c(
                          "div",
                          {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: item.direction === "right",
                                expression: "item.direction === 'right'",
                              },
                            ],
                            staticClass: "timer",
                            style: {
                              fontSize: 24 * _vm.scale + "px",
                              lineHeight: 30 * _vm.scale + "px",
                              paddingBottom: 34 * _vm.scale + "px",
                            },
                          },
                          [
                            _vm._v(
                              "\n              " +
                                _vm._s(item.time) +
                                "\n            "
                            ),
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticClass: "item",
                            class: item.direction + "-item",
                          },
                          [
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value:
                                      item.direction === "right" &&
                                      !_vm.exportStatic,
                                    expression:
                                      "item.direction === 'right' && !exportStatic",
                                  },
                                ],
                                staticClass: "icon-box",
                                style: {
                                  width: 42 * _vm.scale + "px",
                                  paddingRight: 16 * _vm.scale + "px",
                                },
                              },
                              [
                                _c("div", {
                                  staticClass: "delete",
                                  style: {
                                    width: 26 * _vm.scale + "px",
                                    height: 30 * _vm.scale + "px",
                                    paddingRight: 16 * _vm.scale + "px",
                                  },
                                  on: {
                                    click: function ($event) {
                                      return _vm.removalMessage(item, index)
                                    },
                                  },
                                }),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "content-box",
                                class: item.direction,
                                style: {
                                  maxWidth:
                                    "calc(100% - " + 42 * _vm.scale + "px)",
                                  minHeight: 70 * _vm.scale + "px",
                                  padding:
                                    28 * _vm.scale +
                                    "px " +
                                    30 * _vm.scale +
                                    "px",
                                  fontSize: 28 * _vm.scale + "px",
                                  lineHeight: 34 * _vm.scale + "px",
                                },
                              },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(
                                      item.content
                                        ? item.content
                                        : "正在生成 . . ."
                                    ) +
                                    "\n                "
                                ),
                                item.videoUrl
                                  ? _c("div", [
                                      _vm._v(
                                        "\n                  下面“小智”还为您推荐视频\n                  "
                                      ),
                                      _vm._v(" "),
                                      item.videoUrl
                                        ? _c(
                                            "video",
                                            {
                                              staticStyle: {
                                                margin: "14px 0 0 0",
                                              },
                                              attrs: {
                                                width: "100%",
                                                controls: "",
                                                controlslist: "nodownload",
                                              },
                                            },
                                            [
                                              _c("source", {
                                                attrs: {
                                                  src: item.videoUrl,
                                                  type: "video/mp4",
                                                },
                                              }),
                                            ]
                                          )
                                        : _vm._e(),
                                    ])
                                  : _vm._e(),
                                _vm._v(" "),
                                !item.loadingCompleted
                                  ? _c("div", {
                                      staticClass: "loading-box",
                                      style: {
                                        height: 12 * _vm.scale + "px",
                                        border:
                                          "0 0 " +
                                          16 * _vm.scale +
                                          "px " +
                                          16 * _vm.scale +
                                          "px",
                                      },
                                    })
                                  : _vm._e(),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                directives: [
                                  {
                                    name: "show",
                                    rawName: "v-show",
                                    value:
                                      item.direction === "left" &&
                                      item.loadingCompleted &&
                                      !_vm.exportStatic,
                                    expression:
                                      "item.direction === 'left' && item.loadingCompleted && !exportStatic",
                                  },
                                ],
                                staticClass: "icon-box",
                                style: {
                                  width: 42 * _vm.scale + "px",
                                  paddingLeft: 16 * _vm.scale + "px",
                                },
                              },
                              [
                                _c("div", {
                                  staticClass: "delete",
                                  style: {
                                    width: 26 * _vm.scale + "px",
                                    height: 30 * _vm.scale + "px",
                                    paddingRight: 16 * _vm.scale + "px",
                                  },
                                  on: {
                                    click: function ($event) {
                                      return _vm.removalMessage(item, index)
                                    },
                                  },
                                }),
                                _vm._v(" "),
                                _c("div", {
                                  staticClass: "refresh",
                                  style: {
                                    width: 26 * _vm.scale + "px",
                                    height: 30 * _vm.scale + "px",
                                    marginTop: 14 * _vm.scale + "px",
                                  },
                                  on: {
                                    click: function ($event) {
                                      return _vm.refreshMessage(item, index)
                                    },
                                  },
                                }),
                              ]
                            ),
                          ]
                        ),
                      ]
                    )
                  }),
                  0
                ),
              ]
            ),
            _vm._v(" "),
            _c(
              "div",
              {
                staticClass: "bottom-box",
                style: {
                  bottom: 0,
                  height: 160 * _vm.scale + "px",
                  padding: 28 * _vm.scale + "px",
                },
              },
              [
                _c(
                  "div",
                  {
                    staticClass: "input-right",
                    style: {
                      width: 684 * _vm.scale + "px",
                      borderRadius: 10 * _vm.scale + "px",
                      padding:
                        4 * _vm.scale +
                        "px " +
                        20 * _vm.scale +
                        "px " +
                        4 * _vm.scale +
                        "px 0",
                    },
                  },
                  [
                    _c("textarea", {
                      directives: [
                        {
                          name: "model",
                          rawName: "v-model",
                          value: _vm.question,
                          expression: "question",
                        },
                      ],
                      staticClass: "input-content",
                      style: {
                        width: "calc(100% - " + 40 * _vm.scale + "px)",
                        maxHeight: 80 * _vm.scale + "px",
                        padding: 12 * _vm.scale + "px " + 20 * _vm.scale + "px",
                        fontSize: 28 * _vm.scale + "px",
                        borderRadius: 10 * _vm.scale + "px",
                      },
                      attrs: { placeholder: "请输入问题描述" },
                      domProps: { value: _vm.question },
                      on: {
                        input: function ($event) {
                          if ($event.target.composing) {
                            return
                          }
                          _vm.question = $event.target.value;
                        },
                      },
                    }),
                    _vm._v(" "),
                    _c("div", {
                      staticClass: "sending",
                      style: {
                        width: 24 * _vm.scale + "px",
                        height: 32 * _vm.scale + "px",
                      },
                      on: {
                        click: function ($event) {
                          return _vm.sendingMsg(_vm.question)
                        },
                      },
                    }),
                  ]
                ),
              ]
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2A = [];
__vue_render__$2A._withStripped = true;

  /* style */
  const __vue_inject_styles__$2A = undefined;
  /* scoped */
  const __vue_scope_id__$2A = "data-v-71ec9456";
  /* module identifier */
  const __vue_module_identifier__$2A = undefined;
  /* functional template */
  const __vue_is_functional_template__$2A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2A, staticRenderFns: __vue_staticRenderFns__$2A },
    __vue_inject_styles__$2A,
    __vue_script__$2A,
    __vue_scope_id__$2A,
    __vue_is_functional_template__$2A,
    __vue_module_identifier__$2A,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2z = {
  name: 'DownloadFileUppop',
  props: {
    downloadFileLink: {
      type: String,
      default: ''
    },
    showToast: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      isShow: false,
      prompt: ''
    };
  },
  methods: {
    close() {
      setTimeout(() => {
        this.prompt = '';
      }, 700);
    },
    // async copyInputContent() {

    //   try {
    //     await navigator.clipboard.writeText(this.codeData)
    //     console.log('复制成功')
    //   } catch (error) {
    //     console.log('复制失败')
    //   }
    // }

    // /**
    //  * 提示语
    //  * type
    //  * @param {*}  warning : 提示
    //  * @param {*}  error : 错误
    //  * @param {*}  success : 成功
    //  */
    //  showToast(text, type) {
    //   this.showPrompt = true
    //   this.promptText = text
    //   this.toastType = type ? type : 'warning'
    //   setTimeout(() => {
    //     this.showPrompt = false
    //   }, 2000)
    // },

    /**
     * 复制文本
     * @param {*}  text : 复制的文本
     */
    copyInputContent() {
      let oInput = document.createElement('input');
      oInput.value = this.downloadFileLink;
      document.body.appendChild(oInput);
      oInput.select();
      document.execCommand('Copy');
      oInput.remove();
      // alert('复制成功,请到浏览器进行下载')
      this.showToast('复制成功', 'success');
      console.log('成功');
      this.isShow = false;
      this.$emit('CopySuccess');
    }
  },
  watch: {}
};

/* script */
const __vue_script__$2z = script$2z;

/* template */
var __vue_render__$2z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "van-popup",
    {
      style: { borderRadius: "8px" },
      on: { close: _vm.close },
      model: {
        value: _vm.isShow,
        callback: function ($$v) {
          _vm.isShow = $$v;
        },
        expression: "isShow",
      },
    },
    [
      _c("div", { staticClass: "xml-download-file-uppop" }, [
        _c("div", { staticClass: "code-path" }, [
          _c("div", { staticClass: "download-file-link" }, [
            _vm._v("\n        " + _vm._s(_vm.downloadFileLink) + "\n      "),
          ]),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "copy-btn", on: { click: _vm.copyInputContent } },
            [_vm._v("复制")]
          ),
        ]),
        _vm._v(" "),
        _c("p", { staticClass: "code-text" }, [
          _vm._v(
            "*" + _vm._s(_vm.prompt ? _vm.prompt : "请复制链接至浏览器下载")
          ),
        ]),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$2z = [];
__vue_render__$2z._withStripped = true;

  /* style */
  const __vue_inject_styles__$2z = undefined;
  /* scoped */
  const __vue_scope_id__$2z = undefined;
  /* module identifier */
  const __vue_module_identifier__$2z = undefined;
  /* functional template */
  const __vue_is_functional_template__$2z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2z, staticRenderFns: __vue_staticRenderFns__$2z },
    __vue_inject_styles__$2z,
    __vue_script__$2z,
    __vue_scope_id__$2z,
    __vue_is_functional_template__$2z,
    __vue_module_identifier__$2z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2y = {
  name: 'resource-list-phone',
  components: {
    VideoModalPlayer: __vue_component__$5e,
    audioPlayerPage: __vue_component__$2H,
    downFileUppop: __vue_component__$2z
  },
  props: {
    textBookResource: {
      type: Object,
      default: () => {}
    },
    resourcesList: {
      type: Array,
      default: () => []
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: 'teacher'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    showToast: {
      type: Function,
      default: null
    },
    isShowTeacherResource: {
      type: Boolean,
      default: false
    },
    sysEnv: {
      type: Object,
      default: () => {}
    }
  },
  watch: {
    resourceType() {
      if (this.resourceType == 'teachingResource') {
        this.list = this.resourcesList || [];
      } else {
        this.list = this.textBookResource[this.resourceType] || [];
      }
    },
    resourcesList() {
      if (this.isShowTeacherResource && this.resourceType == 'teachingResource') {
        this.list = this.resourcesList || [];
      }
    }
  },
  data() {
    return {
      toolbarList: this.sysEnv.toolbarList,
      current: 0,
      resourceType: 'teachingResource',
      list: this.resourcesList,
      collapseCurrent: 0,
      resourceIcon: '',
      audioData: {
        show: false,
        src: '',
        info: {}
      },
      videoData: {
        show: false,
        src: '',
        info: {}
      },
      downloadFileLink: '',
      showDown: false,
      audioUrl: '',
      isPlay: '',
      location: '',
      endTime: 0
    };
  },
  mounted() {
    if (!this.isShowTeacherResource) {
      this.resourceType = 'imageList';
      this.list = this.textBookResource[this.resourceType] || [];
      this.toolbarList.splice(0, 1);
    }
  },
  methods: {
    clickToolbar(e) {
      console.log(e);

      // this.location = ''
      // this.current = index
      // this.resourceType = item.type
      // this.resourceIcon = item.icon

      this.location = '';
      // this.current = index
      this.resourceType = this.toolbarList[e].type;
      this.resourceIcon = this.toolbarList[e].icon;
    },
    getFileUrl(url) {
      if (url && url.indexOf('./') !== -1) {
        return this.resourceBasisPath + url.split('./')[1];
      } else if (url && url.indexOf('./') === -1) {
        return url;
      }
    },
    // 跳转到资源所在位置
    resourceDetails(item, items, type) {
      this.location = '';
      // this.$emit('resourceDetails', items)
      if (items.resourcesType == 'recorded') {
        if (this.isPlay == '') {
          this.isPlay = items.id;
          this.audioUrl = items.fileUrl;
          this.$nextTick(() => {
            this.$refs.musicAudio.play();
          });
        } else {
          this.isPlay = '';
          this.$refs.musicAudio.pause();
        }
      } else {
        this.$emit('resourceDetails', {
          jumpType: type,
          toolbarIndex: 2,
          ...items
        });
      }
    },
    // 教师资源预览
    resourceReader(item, items) {
      if (items.resourcesType == 'image') {
        this.$EventBus.$emit('learningStatistics', {
          type: 'image',
          info: items
        });
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList: [{
            url: items.fileUrl,
            title: items.resourcesName
          }]
        });
      } else if (items.resourcesType == 'audio') {
        this.audioData.src = items.fileUrl;
        this.audioData.show = true;
        this.audioData.info = items;
      } else if (items.resourcesType == 'recorded') {
        this.resourceDetails(item, items);
      } else if (items.resourcesType == 'video') {
        this.videoData.src = items.fileUrl;
        this.videoData.show = true;
        this.videoData.info = items;
      } else {
        this.$emit('resourceReader', items);
      }
      // this.$refs.xmlPopup.show = false
    },
    closeViewer() {
      this.videoData.show = false;
    },
    // 图片预览
    imgPreview(item, items) {
      this.$EventBus.$emit('learningStatistics', {
        type: 'image',
        info: items
      });
      this.$xmlImgPreview({
        multiple: true,
        nowImgIndex: 0,
        imgList: [{
          url: this.getFileUrl(items.uploadFileUrl),
          title: items.title
        }]
      });
    },
    // 音频预览
    audioPreview(item, items) {
      this.audioData.src = this.getFileUrl(items.uploadFileUrl);
      this.audioData.show = true;
      this.audioData.info = items;
      // this.$refs.xmlPopup.show = false
    },
    // 视频预览
    videoPreview(item, items) {
      this.videoData.src = this.getFileUrl(items.uploadFileUrl);
      this.videoData.show = true;
      this.videoData.info = items;
      // this.$refs.xmlPopup.show = false
    },
    close() {
      // this.$refs.xmlPopup.show = false
      this.$emit('close');
    },
    showResourceList(type) {
      if (type) {
        this.$refs.xmlPopup.show = !this.$refs.xmlPopup.show;
      } else {
        this.$refs.xmlPopup.show = false;
      }
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    },
    openTeacherResoucesList(e) {
      let index = this.list.findIndex(item => item.firstLevelId === e.firstLevelId);
      this.current = 0;
      this.resourceType = 'teachingResource';
      this.resourceIcon = '';
      this.collapseCurrent = index !== -1 ? index : 0;
      // this.location = e.location
      let currentTime = new Date().getTime();
      // this.launchValue = 'teaching-resource_' + firstLevelId
      if (e.location == this.location && currentTime > this.endTime + 2000) {
        this.location = '';
        this.endTime = new Date().getTime();
      }
      setTimeout(() => {
        this.location = e.location;
      }, 200);
      this.$refs.xmlPopup.show = true;
    },
    formatTime(time) {
      if (time) {
        let Seconds = time / 1000;
        let m = parseInt(Seconds / 60);
        let s = Seconds % 60;
        // let date = (m > 9 ? m : '0' + m) + "'" + (s > 9 ? s : '0' + s) +
        let date = (m > 0 ? m + "'" : '') + s + '"';
        return date + ' 录音';
      } else {
        return '录音';
      }
    },
    timeVideoUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('videoTimeUpdate', {
        event,
        time,
        data,
        info: this.videoData.info
      });
    },
    timeAudioUpdate({
      event,
      time
    }, data) {
      // console.log({event, time, data});
      this.$EventBus.$emit('audioTimeUpdate', {
        event,
        time,
        data,
        info: this.videoData.info
      });
    }
  }
};

var __$_require_static_images_icons_resource_positioning_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$2y = script$2y;
var __vue_render__$2y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c(
        "xml-popup",
        {
          ref: "xmlPopup",
          attrs: { round: false },
          on: {
            close: function ($event) {
              return _vm.close()
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "xml-resource-list-phone" },
            [
              _c("div", { staticClass: "navBar" }, [
                _c(
                  "div",
                  {
                    staticClass: "back-icon",
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        _vm.$refs.xmlPopup.show = false;
                      },
                    },
                  },
                  [_c("i", { staticClass: "el-icon-arrow-left" })]
                ),
                _vm._v(" "),
                _c("div", [_vm._v("资源")]),
              ]),
              _vm._v(" "),
              _c(
                "van-tabs",
                {
                  attrs: {
                    color: "#57A9FB",
                    background: "#F6FBFF",
                    "line-width": "45px",
                  },
                  on: { click: _vm.clickToolbar },
                },
                _vm._l(_vm.toolbarList, function (item, index) {
                  return _c("van-tab", {
                    scopedSlots: _vm._u(
                      [
                        {
                          key: "title",
                          fn: function () {
                            return [
                              _c("div", [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(item.name) +
                                    "\n                "
                                ),
                                _c("span", [
                                  _vm._v(
                                    "(" +
                                      _vm._s(
                                        _vm.textBookResource[item.total]
                                          ? _vm.textBookResource[item.total]
                                          : "0"
                                      ) +
                                      ")"
                                  ),
                                ]),
                              ]),
                            ]
                          },
                          proxy: true,
                        },
                      ],
                      null,
                      true
                    ),
                  })
                }),
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "xml-resource-list" }, [
                _c(
                  "div",
                  { staticClass: "xml-resource-content" },
                  [
                    _vm.resourceType == "imageList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["imageCurrent"],
                                className: "max-resource-content",
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["imageCurrent"] = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "max-resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      {
                                        staticClass: "resource-cover",
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.imgPreview(item, items)
                                          },
                                        },
                                      },
                                      [
                                        item.state
                                          ? _c("img", {
                                              attrs: {
                                                src: _vm.getFileUrl(
                                                  items.uploadFileUrl
                                                ),
                                                alt: "",
                                              },
                                            })
                                          : _vm._e(),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", { staticClass: "item-bottom" }, [
                                      _c(
                                        "div",
                                        { staticClass: "resource-title" },
                                        [
                                          _vm._v(
                                            _vm._s(
                                              items.title
                                                ? items.title
                                                : item.label
                                            )
                                          ),
                                        ]
                                      ),
                                      _vm._v(" "),
                                      _c("img", {
                                        staticClass: "location",
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_positioning_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.resourceDetails(
                                              item,
                                              items,
                                              "resource"
                                            )
                                          },
                                        },
                                      }),
                                    ]),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "videoList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["videoCurrent"],
                                className: "max-resource-content",
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["videoCurrent"] = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "max-resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      {
                                        staticClass: "resource-cover",
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.videoPreview(item, items)
                                          },
                                        },
                                      },
                                      [
                                        item.state && items.coverFileUrl
                                          ? _c("img", {
                                              attrs: {
                                                src: _vm.getFileUrl(
                                                  items.coverFileUrl
                                                ),
                                                alt: "",
                                              },
                                            })
                                          : _vm._e(),
                                        _vm._v(" "),
                                        !items.coverFileUrl
                                          ? _c("img", {
                                              attrs: {
                                                src: __$_require_static_images_default_over_video_cover_png__,
                                                alt: "",
                                              },
                                            })
                                          : _vm._e(),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", { staticClass: "item-bottom" }, [
                                      _c(
                                        "div",
                                        { staticClass: "resource-title" },
                                        [_vm._v(_vm._s(items.title))]
                                      ),
                                      _vm._v(" "),
                                      _c("img", {
                                        staticClass: "location",
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_positioning_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.resourceDetails(
                                              item,
                                              items,
                                              "resource"
                                            )
                                          },
                                        },
                                      }),
                                    ]),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "audioList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["audioCurrent"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["audioCurrent"] = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external min-mask-layer",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", {
                                      staticClass: "resource-icon",
                                      class: _vm.resourceIcon,
                                    }),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      {
                                        staticClass: "resource-title",
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation();
                                            return _vm.audioPreview(item, items)
                                          },
                                        },
                                      },
                                      [_vm._v(_vm._s(items.title))]
                                    ),
                                    _vm._v(" "),
                                    _c("img", {
                                      staticClass: "location",
                                      attrs: {
                                        src: __$_require_static_images_icons_resource_positioning_png__,
                                        alt: "",
                                      },
                                      on: {
                                        click: function ($event) {
                                          $event.stopPropagation();
                                          return _vm.resourceDetails(
                                            item,
                                            items,
                                            "resource"
                                          )
                                        },
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "questionList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current:
                                  _vm.textBookResource["questionCurrent"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["questionCurrent"] =
                                      e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external min-mask-layer",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", {
                                      staticClass: "resource-icon",
                                      class: _vm.resourceIcon,
                                    }),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "resource-title" },
                                      [
                                        _vm._v(
                                          _vm._s(
                                            _vm.removeStyle(items.stem.content)
                                          )
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("img", {
                                      staticClass: "location",
                                      attrs: {
                                        src: __$_require_static_images_icons_resource_positioning_png__,
                                        alt: "",
                                      },
                                      on: {
                                        click: function ($event) {
                                          $event.stopPropagation();
                                          return _vm.resourceDetails(
                                            item,
                                            items,
                                            "resource"
                                          )
                                        },
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "testPaperList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["testPaperTotal"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["testPaperTotal"] =
                                      e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external min-mask-layer",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", {
                                      staticClass: "resource-icon",
                                      class: _vm.resourceIcon,
                                    }),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "resource-title" },
                                      [
                                        _vm._v(
                                          _vm._s(items.paperName || items.title)
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("img", {
                                      staticClass: "location",
                                      attrs: {
                                        src: __$_require_static_images_icons_resource_positioning_png__,
                                        alt: "",
                                      },
                                      on: {
                                        click: function ($event) {
                                          $event.stopPropagation();
                                          return _vm.resourceDetails(
                                            item,
                                            items,
                                            "resource"
                                          )
                                        },
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "formulaList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["formulaCurrent"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["formulaCurrent"] =
                                      e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item flex-column" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "resource-title-box" },
                                      [
                                        _c("div", {
                                          staticClass: "resource-icon",
                                          class: _vm.resourceIcon,
                                        }),
                                        _vm._v(" "),
                                        _c(
                                          "div",
                                          { staticClass: "resource-title" },
                                          [_vm._v(_vm._s(items.fileNames))]
                                        ),
                                        _vm._v(" "),
                                        _c("img", {
                                          staticClass: "location",
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_positioning_png__,
                                            alt: "",
                                          },
                                          on: {
                                            click: function ($event) {
                                              $event.stopPropagation();
                                              return _vm.resourceDetails(
                                                item,
                                                items,
                                                "resource"
                                              )
                                            },
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("iframe", {
                                      attrs: {
                                        width: "100%",
                                        height: "120px",
                                        src: _vm.getFileUrl(items.file),
                                        frameborder: "0",
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "interactionList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current:
                                  _vm.textBookResource["interactionCurrent"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource[
                                      "interactionCurrent"
                                    ] = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external min-mask-layer",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", {
                                      staticClass: "resource-icon",
                                      class: _vm.resourceIcon,
                                    }),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "resource-title" },
                                      [_vm._v(_vm._s(items.fileNames))]
                                    ),
                                    _vm._v(" "),
                                    _c("img", {
                                      staticClass: "location",
                                      attrs: {
                                        src: __$_require_static_images_icons_resource_positioning_png__,
                                        alt: "",
                                      },
                                      on: {
                                        click: function ($event) {
                                          $event.stopPropagation();
                                          return _vm.resourceDetails(
                                            item,
                                            items,
                                            "resource"
                                          )
                                        },
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "resourceList"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.label,
                                name: index,
                                current: _vm.textBookResource["imageCurrent"],
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.textBookResource["imageCurrent"] = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(
                              item[_vm.resourceType],
                              function (items, index) {
                                return _c(
                                  "div",
                                  { staticClass: "resource-item" },
                                  [
                                    _c(
                                      "div",
                                      {
                                        directives: [
                                          {
                                            name: "show",
                                            rawName: "v-show",
                                            value: items.isTrialExternal,
                                            expression: "items.isTrialExternal",
                                          },
                                        ],
                                        staticClass:
                                          "mask-layer-trial-external min-mask-layer",
                                      },
                                      [
                                        _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_resource_lock_svg__,
                                            alt: "",
                                          },
                                        }),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("div", {
                                      staticClass: "resource-icon",
                                      class: _vm.resourceIcon,
                                    }),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "resource-title" },
                                      [
                                        _vm._v(
                                          _vm._s(
                                            items.ilk == "ebook"
                                              ? items.name
                                              : items.title
                                          )
                                        ),
                                      ]
                                    ),
                                    _vm._v(" "),
                                    _c("img", {
                                      staticClass: "location",
                                      attrs: {
                                        src: __$_require_static_images_icons_resource_positioning_png__,
                                        alt: "",
                                      },
                                      on: {
                                        click: function ($event) {
                                          $event.stopPropagation();
                                          return _vm.resourceDetails(
                                            item,
                                            items,
                                            "resource"
                                          )
                                        },
                                      },
                                    }),
                                  ]
                                )
                              }
                            ),
                            0
                          )
                        })
                      : _vm.resourceType == "teachingResource"
                      ? _vm._l(_vm.list, function (item, index) {
                          return _c(
                            "xml-collapse",
                            {
                              key: index,
                              attrs: {
                                title: item.firstLevelLabel,
                                name: index,
                                current: _vm.collapseCurrent,
                              },
                              on: {
                                collapseClick: function (e) {
                                  return (
                                    (_vm.collapseCurrent = e),
                                    (item.state = true)
                                  )
                                },
                              },
                            },
                            _vm._l(item.list, function (items, index) {
                              return _c(
                                "div",
                                {
                                  key: index,
                                  class: [
                                    items.location == _vm.location
                                      ? "current-teacher-resources"
                                      : "",
                                    items.resourcesType == "notes"
                                      ? "xml-teacher-note-box"
                                      : "",
                                  ],
                                },
                                [
                                  items.resourcesType == "notes"
                                    ? [
                                        _c(
                                          "div",
                                          { staticClass: "xml-teacher-note" },
                                          [
                                            _c(
                                              "div",
                                              {
                                                staticClass: "xml-note-header",
                                              },
                                              [
                                                _c("img", {
                                                  staticClass: "note-icon",
                                                  attrs: {
                                                    src: __$_require_static_images_icons_resource_note_icon_svg__,
                                                    alt: "",
                                                  },
                                                }),
                                                _vm._v(" "),
                                                _c("img", {
                                                  staticClass: "location",
                                                  attrs: {
                                                    src: __$_require_static_images_icons_resource_positioning_png__,
                                                    alt: "",
                                                  },
                                                  on: {
                                                    click: function ($event) {
                                                      $event.stopPropagation();
                                                      return _vm.resourceDetails(
                                                        item,
                                                        items,
                                                        "marker"
                                                      )
                                                    },
                                                  },
                                                }),
                                              ]
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "div",
                                              { staticClass: "note-content" },
                                              [
                                                _c("xmlTextOverflow", {
                                                  attrs: {
                                                    info: items.noteContent,
                                                  },
                                                }),
                                              ],
                                              1
                                            ),
                                          ]
                                        ),
                                      ]
                                    : [
                                        _c(
                                          "div",
                                          { staticClass: "resource-item" },
                                          [
                                            _c("div", {
                                              staticClass: "resource-icon",
                                              class: [
                                                items.resourcesType ==
                                                "recorded"
                                                  ? "recorded"
                                                  : items.fileExt
                                                  ? items.fileExt.replace(
                                                      /\./gi,
                                                      ""
                                                    )
                                                  : "",
                                                _vm.isPlay == items.id
                                                  ? "isPlay"
                                                  : "",
                                              ],
                                              attrs: { userType: _vm.userType },
                                            }),
                                            _vm._v(" "),
                                            _c(
                                              "div",
                                              {
                                                staticClass: "resource-title",
                                                on: {
                                                  click: function ($event) {
                                                    return _vm.resourceReader(
                                                      item,
                                                      items
                                                    )
                                                  },
                                                },
                                              },
                                              [
                                                _vm._v(
                                                  "\n                      " +
                                                    _vm._s(
                                                      items.resourcesType ==
                                                        "recorded"
                                                        ? _vm.formatTime(
                                                            items.duration
                                                          )
                                                        : items.resourcesName
                                                    ) +
                                                    "\n                    "
                                                ),
                                              ]
                                            ),
                                            _vm._v(" "),
                                            _c("img", {
                                              directives: [
                                                {
                                                  name: "show",
                                                  rawName: "v-show",
                                                  value: items.isDownload == 1,
                                                  expression:
                                                    "items.isDownload == 1",
                                                },
                                              ],
                                              staticClass: "location",
                                              attrs: {
                                                src: __$_require_static_images_icons_resource_download_icon_png__,
                                                alt: "",
                                              },
                                              on: {
                                                click: function ($event) {
                                                  $event.stopPropagation()
                                                  ;(_vm.downloadFileLink =
                                                    items.fileUrl),
                                                    (_vm.$refs.downFileUppop.isShow = true);
                                                },
                                              },
                                            }),
                                            _vm._v(" "),
                                            _c("img", {
                                              staticClass: "location",
                                              attrs: {
                                                src: __$_require_static_images_icons_resource_positioning_png__,
                                                alt: "",
                                              },
                                              on: {
                                                click: function ($event) {
                                                  $event.stopPropagation();
                                                  return _vm.resourceDetails(
                                                    item,
                                                    items,
                                                    "marker"
                                                  )
                                                },
                                              },
                                            }),
                                          ]
                                        ),
                                      ],
                                ],
                                2
                              )
                            }),
                            0
                          )
                        })
                      : _vm._e(),
                    _vm._v(" "),
                    _c("xml-empty", {
                      directives: [
                        {
                          name: "show",
                          rawName: "v-show",
                          value: _vm.list.length == 0,
                          expression: "list.length == 0",
                        },
                      ],
                    }),
                  ],
                  2
                ),
              ]),
            ],
            1
          ),
        ]
      ),
      _vm._v(" "),
      _vm.audioData.show
        ? _c("audio-player-page", {
            attrs: { url: _vm.audioData.src },
            on: {
              back: function ($event) {
                _vm.audioData.show = false;
              },
              timeUpdate: function (e) {
                return _vm.timeAudioUpdate(e)
              },
            },
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.videoData.show
        ? _c("div", { staticClass: "video-resource-player" }, [
            _c("i", {
              staticClass: "el-icon-back",
              attrs: { "on-close": _vm.closeViewer },
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                  _vm.videoData.show = false;
                },
              },
            }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "video-content" },
              [
                _c("video-modal-player", {
                  attrs: { src: _vm.videoData.src },
                  on: {
                    timeUpdate: function (e) {
                      return _vm.timeVideoUpdate(e)
                    },
                  },
                }),
              ],
              1
            ),
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("down-file-uppop", {
        ref: "downFileUppop",
        attrs: {
          downloadFileLink: _vm.downloadFileLink,
          showToast: _vm.showToast,
        },
      }),
      _vm._v(" "),
      _c("audio", {
        ref: "musicAudio",
        staticClass: "audio-component",
        attrs: { controls: "", preload: "auto", src: _vm.audioUrl },
        on: {
          ended: function ($event) {
            _vm.isPlay = "";
          },
        },
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$2y = [];
__vue_render__$2y._withStripped = true;

  /* style */
  const __vue_inject_styles__$2y = undefined;
  /* scoped */
  const __vue_scope_id__$2y = undefined;
  /* module identifier */
  const __vue_module_identifier__$2y = undefined;
  /* functional template */
  const __vue_is_functional_template__$2y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2y, staticRenderFns: __vue_staticRenderFns__$2y },
    __vue_inject_styles__$2y,
    __vue_script__$2y,
    __vue_scope_id__$2y,
    __vue_is_functional_template__$2y,
    __vue_module_identifier__$2y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2x = {
  name: 'xmlPreviewSearch',
  props: {
    viewList: Object,
    componentList: Array,
    title: String,
    pageType: {
      type: String,
      default: 'h5'
    },
    searchValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      urlPre: '../assets/images/icons/',
      list: [],
      input: '',
      ArrList: [],
      searchTextShow: true,
      showEllipsisButton: false,
      contentExpand: false,
      show: true,
      collapseCurrent: 0
    };
  },
  watch: {
    // 监听输入框
    // 监听input的值，当值改变时，调用changeInput方法
    input(a, b) {
      if (a.replace(/\s+/g, '') != b.replace(/\s+/g, '')) {
        this.changeInput();
        this.contentExpand = false;
      }
    },
    searchValue: {
      handler(newValue) {
        this.input = newValue;
      }
    }
  },
  computed: {
    searchList() {
      let list = this.list;
      const map = [];
      list.forEach(e => {
        if (e.type == 'note__teacher') {
          this.teacherNoteList.push(e);
        }
        const findObj = map.find(innerTmp => innerTmp.firstLevelId === e.firstLevelId);
        if (findObj) {
          findObj.list.push(e);
        } else {
          map.push({
            firstLevelId: e.firstLevelId,
            firstLevelLabel: e.firstLevelLabel,
            chapterOrderNum: e.chapterOrderNum,
            list: [e]
          });
        }
      });
      return map;
    }
  },
  methods: {
    changeInput() {
      if (this.input.replace(/\s+/g, '') !== '') {
        this.list = XmlSearchWord(this.componentList, this.input.replace(/\s+/g, ''));
        if (this.list.length > 0) {
          this.showEllipsisButton = true;
        }
      } else {
        this.list = [];
        console.log('输入为空');
      }
    },
    stopPropagation(event) {
      event.stopPropagation();
    },
    seachItem(e) {
      e.inputText = this.input.replace(/\s+/g, '');
      this.$emit('seachItem', e, this.input, this.show);
    },
    onBackClick() {
      this.$emit('onBackClick');
    },
    brightenKeyword(val) {
      const keyword = this.input.replace(/\s+/g, '');
      const Reg = new RegExp(keyword, 'g');
      let res = '';
      if (val) {
        res = val.replace(Reg, `<span style="color: red;">${keyword}</span>`);
        return res;
      }
    },
    // 展开下拉
    toggleSearchText(id) {
      this.contentExpand = !this.contentExpand;
      this.searchTextShow = !this.searchTextShow;
    }
  }
};

/* script */
const __vue_script__$2x = script$2x;
var __vue_render__$2x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { class: "xmlPreviewSearch-box-" + _vm.pageType }, [
    _vm.pageType == "h5"
      ? _c("div", { staticClass: "xmlPreviewSearch-input" }, [
          _c("input", {
            directives: [
              {
                name: "model",
                rawName: "v-model",
                value: _vm.input,
                expression: "input",
              },
            ],
            staticClass: "input",
            attrs: { maxlength: "20", placeholder: "请输入" },
            domProps: { value: _vm.input },
            on: {
              change: _vm.changeInput,
              input: function ($event) {
                if ($event.target.composing) {
                  return
                }
                _vm.input = $event.target.value;
              },
            },
          }),
          _vm._v(" "),
          _c(
            "button",
            {
              staticClass: "input_text",
              on: {
                click: function ($event) {
                  return _vm.changeInput()
                },
              },
            },
            [
              _c(
                "span",
                {
                  staticStyle: {
                    color: "#fff",
                    "font-size": "14px",
                    "font-weight": "normal",
                  },
                },
                [_vm._v("搜索")]
              ),
            ]
          ),
        ])
      : _vm._e(),
    _vm._v(" "),
    _vm.pageType == "pc"
      ? _c("div", { class: "xmlPreviewSearch-input-" + _vm.pageType }, [
          _c(
            "div",
            {
              staticStyle: { "font-size": "16px", cursor: "pointer" },
              on: { click: _vm.onBackClick },
            },
            [
              _c("i", {
                staticClass: "el-icon-arrow-left",
                staticStyle: { "font-size": "16px" },
              }),
              _vm._v("返回\n    "),
            ]
          ),
        ])
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "seach-list", on: { click: _vm.stopPropagation } },
      [
        _vm._l(_vm.searchList, function (item, index) {
          return _c(
            "xml-collapse",
            {
              key: index,
              attrs: {
                title: item.firstLevelLabel,
                name: index,
                current: _vm.collapseCurrent,
              },
              on: {
                collapseClick: function (e) {
                  return (_vm.collapseCurrent = e)
                },
              },
            },
            _vm._l(item.list, function (items, index) {
              return _c(
                "div",
                {
                  staticClass: "resource-item",
                  on: {
                    click: function ($event) {
                      return _vm.seachItem(items)
                    },
                  },
                },
                [
                  _c("div", {
                    staticClass: "seach-text",
                    domProps: {
                      innerHTML: _vm._s(
                        _vm.brightenKeyword(items.searchText, _vm.input)
                      ),
                    },
                  }),
                ]
              )
            }),
            0
          )
        }),
        _vm._v(" "),
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.list.length == 0 && _vm.input == "",
                expression: "list.length == 0 && input == ''",
              },
            ],
            staticClass: "seach_zaiwu",
          },
          [
            _c("div", { staticClass: "search_left_img" }, [
              _c("img", {
                attrs: { src: __$_require_assets_images_icons_without_png__ },
              }),
            ]),
          ]
        ),
        _vm._v(" "),
        _c(
          "p",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.input !== "" && _vm.list.length == 0,
                expression: "input !== '' && list.length == 0",
              },
            ],
            staticClass: "search_no_data",
            staticStyle: {
              margin: "0",
              "text-align": "center",
              "line-height": "14",
              color: "#666666",
              "font-size": "24px",
              "font-weight": "100",
            },
          },
          [_vm._v("\n      搜索无结果\n    ")]
        ),
      ],
      2
    ),
  ])
};
var __vue_staticRenderFns__$2x = [];
__vue_render__$2x._withStripped = true;

  /* style */
  const __vue_inject_styles__$2x = undefined;
  /* scoped */
  const __vue_scope_id__$2x = "data-v-009c493b";
  /* module identifier */
  const __vue_module_identifier__$2x = undefined;
  /* functional template */
  const __vue_is_functional_template__$2x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2x, staticRenderFns: __vue_staticRenderFns__$2x },
    __vue_inject_styles__$2x,
    __vue_script__$2x,
    __vue_scope_id__$2x,
    __vue_is_functional_template__$2x,
    __vue_module_identifier__$2x,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2w = {
  name: 'xml-toolbar-phone',
  components: {
    resourceListPhone: __vue_component__$2y,
    searchPhone: __vue_component__$2x
  },
  props: {
    pageType: {
      type: String,
      default: 'h5'
    },
    textBookName: {
      type: String,
      default: ''
    },
    textBookResource: {
      type: Object,
      default: () => {}
    },
    resourcesList: {
      type: Array,
      default: () => []
    },
    catalogList: {
      type: Array,
      default: () => []
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: ''
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    componentList: {
      type: Array,
      default: () => []
    },
    schedule: {
      type: String,
      default: '0'
    },
    location: {
      type: String,
      default: ''
    },
    showToast: {
      type: Function,
      default: null
    },
    notesList: {
      type: Array,
      default: () => []
    },
    //笔记列表
    bookmarkList: {
      type: Array,
      default: () => []
    },
    //书签列表
    highlightList: {
      //高亮列表
      type: Array,
      default: () => []
    },
    linkList: {
      //划线列表
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    checkedId: {
      //当前选中视频
      type: String,
      default: ''
    },
    isShowTeacherResource: {
      type: Boolean,
      default: false
    },
    settingData: {
      type: Object,
      default: () => {}
    },
    sysEnv: {
      type: Object,
      default: () => {}
    },
    loadingMode: {
      // 加载模式，可选roll、page
      type: String,
      default: 'page'
    },
    showToolbar: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activeNames: ['1'],
      curnotetabs: 0,
      noteTabbarList: [{
        id: 0,
        name: '笔记',
        show: this.sysEnv.tippy.note
      }, {
        id: 3,
        name: '划线',
        show: this.sysEnv.tippy.link
      }, {
        id: 1,
        name: '书签',
        show: this.sysEnv.tippy.bookmark
      }, {
        id: 2,
        name: '高亮',
        show: this.sysEnv.tippy.highlight
      }],
      toolbarList: [{
        name: '目录',
        default: 'icon_10',
        select: 'icon_up_10'
      }, {
        name: '记录',
        default: 'icon_11',
        select: 'icon_up_11'
      }, {
        name: '资源',
        default: 'icon_12',
        select: 'icon_up_12'
      }, {
        name: '设置',
        default: 'icon_13',
        select: 'icon_up_13'
      }],
      current: -1,
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      form: {
        themeFontFamily: '',
        //字体
        themeFontSize: 2,
        //字号
        themeBgColor: '',
        //当前颜色
        themeLineHeight: undefined,
        //行间距
        themeLetterSpacing: undefined //字间距
      },
      fontSizeLabelMap,
      options: [{
        value: '',
        label: '默认'
      }, {
        value: '宋体, SimSun',
        label: '宋体'
      }, {
        value: '楷体, 楷体_GB2312, SimKai',
        label: '楷体'
      }, {
        value: '黑体, SimHei',
        label: '黑体'
      }],
      RowOptions: [{
        value: '10',
        label: '10'
      }, {
        value: '20',
        label: '20'
      }, {
        value: '30',
        label: '30'
      }],
      WordOptions: [{
        value: '10',
        label: '10'
      }, {
        value: '20',
        label: '20'
      }, {
        value: '30',
        label: '30'
      }],
      colorList: [{
        colorval: '#EFEFEF'
      }, {
        colorval: '#DDD8C5'
      }, {
        colorval: '#F2DCC4'
      }, {
        colorval: '#E6F7E5'
      }, {
        colorval: '#FBDEE2'
      }
      // {
      //   colorval: '#000000'
      // }
      ],
      selectId: '',
      isShowSearch: false,
      isShowToolbar: this.showToolbar,
      innerHeight: window.innerHeight,
      noteNames: '',
      bookmarkNames: '',
      highlightNames: '',
      linkNames: ''
    };
  },
  watch: {
    settingData: {
      handler(newValue) {
        this.form = {
          ...this.form,
          ...newValue
        };
      },
      immediate: true,
      deep: true
    },
    notesList: {
      handler(newValue) {
        if (this.noteNames == '' || JSON.stringify(newValue).indexOf(this.noteNames) == -1) {
          this.noteNames = newValue[0]?.firstLevelId;
        }
      },
      immediate: true,
      deep: true
    },
    bookmarkList: {
      handler(newValue) {
        if (this.bookmarkNames == '' || JSON.stringify(newValue).indexOf(this.bookmarkNames) == -1) {
          this.bookmarkNames = newValue[0]?.firstLevelId;
        }
      },
      immediate: true,
      deep: true
    },
    highlightList: {
      handler(newValue) {
        if (this.highlightNames == '' || JSON.stringify(newValue).indexOf(this.highlightNames) == -1) {
          this.highlightNames = newValue[0]?.firstLevelId;
        }
      },
      immediate: true,
      deep: true
    },
    linkList: {
      handler(newValue) {
        if (this.linkNames == '' || JSON.stringify(newValue).indexOf(this.linkNames) == -1) {
          this.linkNames = newValue[0]?.firstLevelId;
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // console.log('this.notesList[0]111111', this.notesList[0])
    // this.noteNames = this.notesList[0]?.firstLevelId
    // this.noteNames = this.notesList[0]?.firstLevelId
    // this.noteNames = this.notesList[0]?.firstLevelId
  },
  methods: {
    //
    clickToolbar(e) {
      if (this.current != e) {
        setTimeout(() => {
          this.current = e;
        }, 100);
      } else {
        this.current = -1;
      }
      if (e == 0) {
        this.$refs.catalogPopup.show = !this.$refs.catalogPopup.show;
        console.log(this.checkedId);
        this.selectId = this.checkedId;
        this.$refs.setPopup.show = false;
        this.$refs.notePpoup.show = false;
        this.$refs.resourceListPopup.showResourceList(false);
      } else if (e == 1) {
        this.$refs.notePpoup.show = !this.$refs.notePpoup.show;
        this.$refs.setPopup.show = false;
        this.$refs.catalogPopup.show = false;
        this.$refs.resourceListPopup.showResourceList(false);
      } else if (e == 3) {
        this.$refs.setPopup.show = !this.$refs.setPopup.show;
        this.$refs.catalogPopup.show = false;
        this.$refs.notePpoup.show = false;
        this.$refs.resourceListPopup.showResourceList(false);
      } else {
        this.$refs.setPopup.show = false;
        this.$refs.catalogPopup.show = false;
        this.$refs.notePpoup.show = false;
        this.$refs.resourceListPopup.showResourceList(true);
      }
      this.$refs.searchPopShow.show = false;
      this.$emit('clickToolbarPhone', this.toolbarList[e]);
    },
    // 设置字体
    setThemeFontFamily(value) {
      this.form.themeFontFamily = value;
      this.$emit('settingChange', 'themeFontFamily', value);
    },
    // 设置字号
    setThemeFontSize(type) {
      let emit = false;
      switch (type) {
        case 'plus':
          if (this.form.themeFontSize < 3) {
            this.form.themeFontSize++;
            emit = true;
          }
          break;
        case 'minus':
          if (this.form.themeFontSize > 1) {
            this.form.themeFontSize--;
            emit = true;
          }
          break;
      }
      emit && this.$emit('settingChange', 'themeFontSize', this.form.themeFontSize);
    },
    readingMode(type) {
      this.$emit('settingChange', 'readingMode', type);
    },
    // 选择颜色
    setThemeBgColor(color) {
      if (color === this.form.themeBgColor) {
        color = '';
      }
      this.form.themeBgColor = color;
      this.$emit('settingChange', 'themeBgColor', color);
    },
    setThemeLineHeight(value) {
      this.form.themeLineHeight = value;
      this.$emit('settingChange', 'themeLineHeight', value);
    },
    setThemeLetterSpacing(value) {
      this.form.themeLetterSpacing = value;
      this.$emit('settingChange', 'themeLetterSpacing', value);
    },
    //笔记点击标签
    clicknotetab(e) {
      this.curnotetabs = e;
    },
    //当前展开
    handleChange(val) {
      console.log(val);
    },
    expandOrCollapseNode(node) {
      node.expanded = !node.expanded;
    },
    nodeClick(data) {
      this.$emit('onNodeClick', {
        id: data.id,
        label: data.label,
        xmlCompRef: data.xmlCompRef,
        locationChapterId: data.locationChapterId
      });
      this.selectId = data.id;
      this.clickToolbar(0);
    },
    backClick() {
      this.$emit('onBackClick');
    },
    filterNode(value, data, node) {
      return data.hiddenFlag !== '1';
    },
    expandHandle() {
      //   this.expandAll = !this.expandAll;
      // this.expandNodes(this.$refs.tree.store.root, 1)
    },
    // 遍历树形数据，设置每一项的expanded属性，实现展开收起
    expandNodes(node, level) {
      //   node.expanded = this.expandAll;

      node.expanded = true;
      for (let i = 0; i < node.childNodes.length; i++) {
        node.childNodes[i].expanded = this.expandAll;
        if (node.childNodes[i].childNodes.length > 0 && this.catalogShow == 0) {
          this.expandNodes(node.childNodes[i], level + 1);
        } else if (node.childNodes[i].childNodes.length > 0 && level < this.catalogShow) {
          this.expandNodes(node.childNodes[i], level + 1);
        }
      }
    },
    // 跳转到资源所在位置
    resourceDetails(data) {
      let list = data.location.split('/');
      let markId = `${list[2]}_#_${data.markEfiCode}`;
      let selectorId = data.jumpType == 'resource' ? data.titleClass : data.jumpType == 'marker' ? markId : '';
      console.log('🚀 ~ resourceDetails ~ selectorId:', data.type, selectorId);
      let selectorType = data.jumpType == 'resource' ? 'class' : data.jumpType == 'marker' ? 'id' : '';
      this.$emit('resourceDetails', {
        location: data.location,
        maxTop: -30,
        type: data.jumpType,
        selectorType,
        selectorId,
        markTarget: data,
        classIndex: data.index
      });
      this.clickToolbar(data.toolbarIndex);
    },
    // 教师资源预览
    resourceReader(items) {
      this.isShowToolbar = false;
      this.$emit('resourceReader', items);
    },
    showSearch() {
      this.$refs.searchPopShow.show = true;
      this.current = 9;
    },
    seachItem(item, text) {
      let data = {
        location: item.location,
        maxTop: -30,
        type: 'search',
        selectorType: 'class',
        selectorId: 'xml-search-highlight',
        markTarget: '',
        classIndex: 0,
        seachText: text
      };
      this.$emit('resourceDetails', data);
      this.$refs.searchPopShow.show = false;
    },
    // 添加书签
    addBookmark(location) {
      if (JSON.stringify(this.bookmarkList).indexOf(this.location) === -1) {
        // 添加书签

        this.$emit('addBookmark', location);
      } else {
        console.log('移除', location);
        // 移除书签
        this.$emit('deleteBookmark', location);
      }
    },
    // 打开教师资源列表
    openTeacherResoucesList(e) {
      this.$refs.resourceListPopup.openTeacherResoucesList(e);
    },
    deleteNote(items) {
      Dialog.confirm({
        title: '提示',
        message: '确认删除此条笔记吗？',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        overlayClass: 'xml-dialog-overlay',
        className: 'xml-dialog-confirm'
      }).then(() => {
        this.$EventBus.$emit('deleteNote', items, () => {});
      }).catch(() => {});
    },
    deleteBookmark(items) {
      Dialog.confirm({
        title: '提示',
        message: '确认删除此条书签吗？',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        overlayClass: 'xml-dialog-overlay',
        className: 'xml-dialog-confirm'
      }).then(() => {
        this.$EventBus.$emit('deleteBookmark', items);
      }).catch(() => {});
    },
    deleteHighlight(items, type) {
      Dialog.confirm({
        title: '提示',
        message: `确认删除此条${type == 'highlight' ? '高亮' : '划线'}吗？`,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        overlayClass: 'xml-dialog-overlay',
        className: 'xml-dialog-confirm'
      }).then(() => {
        this.$EventBus.$emit(type == 'highlight' ? 'deleteHighlight' : 'deleteLink', items, () => {});
      }).catch(() => {});
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    }
  }
};

var __$_require_static_images_icons_toolbar_bookmark_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M12%2010.5V14.5C12%2014.5905%2011.9755%2014.6793%2011.929%2014.757C11.8825%2014.8346%2011.8158%2014.8982%2011.736%2014.9409C11.6562%2014.9836%2011.5663%2015.0038%2011.4759%2014.9995C11.3855%2014.9951%2011.298%2014.9663%2011.2227%2014.9161L7%2012.1009L2.77734%2014.9161C2.70204%2014.9663%202.61452%2014.9951%202.52412%2014.9995C2.43372%2015.0038%202.34383%2014.9836%202.26404%2014.9409C2.18425%2014.8982%202.11754%2014.8346%202.07104%2014.757C2.02454%2014.6793%201.99999%2014.5905%202%2014.5V2C2.00029%201.73487%202.10574%201.48069%202.29321%201.29321C2.48069%201.10574%202.73487%201.00029%203%201H11C11.2651%201.00029%2011.5193%201.10574%2011.7068%201.29321C11.8943%201.48069%2011.9997%201.73487%2012%202V2.5C12%202.63261%2011.9473%202.75979%2011.8536%202.85355C11.7598%202.94732%2011.6326%203%2011.5%203C11.3674%203%2011.2402%202.94732%2011.1464%202.85355C11.0527%202.75979%2011%202.63261%2011%202.5V2H3V13.5658L6.72266%2011.0839C6.80479%2011.0292%206.90129%2010.9999%207%2010.9999C7.09871%2010.9999%207.19521%2011.0292%207.27734%2011.0839L11%2013.5658V10.5C11%2010.3674%2011.0527%2010.2402%2011.1464%2010.1464C11.2402%2010.0527%2011.3674%2010%2011.5%2010C11.6326%2010%2011.7598%2010.0527%2011.8536%2010.1464C11.9473%2010.2402%2012%2010.3674%2012%2010.5ZM13.5%206H12V4.5C12%204.36739%2011.9473%204.24021%2011.8536%204.14645C11.7598%204.05268%2011.6326%204%2011.5%204C11.3674%204%2011.2402%204.05268%2011.1464%204.14645C11.0527%204.24021%2011%204.36739%2011%204.5V6H9.5C9.36739%206%209.24021%206.05268%209.14645%206.14645C9.05268%206.24021%209%206.36739%209%206.5C9%206.63261%209.05268%206.75979%209.14645%206.85355C9.24021%206.94732%209.36739%207%209.5%207H11V8.5C11%208.63261%2011.0527%208.75979%2011.1464%208.85355C11.2402%208.94732%2011.3674%209%2011.5%209C11.6326%209%2011.7598%208.94732%2011.8536%208.85355C11.9473%208.75979%2012%208.63261%2012%208.5V7H13.5C13.6326%207%2013.7598%206.94732%2013.8536%206.85355C13.9473%206.75979%2014%206.63261%2014%206.5C14%206.36739%2013.9473%206.24021%2013.8536%206.14645C13.7598%206.05268%2013.6326%206%2013.5%206Z%22%20fill%3D%22%230D3B8B%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_toolbar_bookmark_up_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20clip-path%3D%22url%28%23clip0_134_1211%29%22%3E%3Cpath%20d%3D%22M13.3333%200H2.66666C2.43091%200%202.20482%200.0936505%202.03812%200.260349C1.87142%200.427048%201.77777%200.653141%201.77777%200.888888V15.1111C1.77823%2015.2626%201.81741%2015.4115%201.8916%2015.5436C1.9658%2015.6758%202.07253%2015.7867%202.20167%2015.866C2.3308%2015.9452%202.47806%2015.9902%202.62945%2015.9965C2.78084%2016.0029%202.93134%2015.9704%203.06666%2015.9022L7.99999%2013.44L12.9333%2015.9022C13.0572%2015.9657%2013.1942%2015.9992%2013.3333%2016C13.4997%2016.0005%2013.663%2015.9543%2013.8044%2015.8667C13.9325%2015.7866%2014.0381%2015.6752%2014.1112%2015.543C14.1843%2015.4108%2014.2225%2015.2622%2014.2222%2015.1111V0.888888C14.2222%200.653141%2014.1286%200.427048%2013.9619%200.260349C13.7952%200.0936505%2013.5691%200%2013.3333%200ZM11.2978%205.96444L7.74221%209.51999C7.65958%209.60331%207.56127%209.66944%207.45295%209.71456C7.34463%209.75969%207.22844%209.78293%207.1111%209.78293C6.99376%209.78293%206.87757%209.75969%206.76926%209.71456C6.66094%209.66944%206.56262%209.60331%206.47999%209.51999L4.70221%207.74222C4.53483%207.57484%204.4408%207.34782%204.4408%207.11111C4.4408%206.87439%204.53483%206.64738%204.70221%206.48C4.86959%206.31262%205.09661%206.21858%205.33332%206.21858C5.57004%206.21858%205.79705%206.31262%205.96444%206.48L7.1111%207.63555L10.0355%204.70222C10.2029%204.53484%2010.4299%204.4408%2010.6667%204.4408C10.9034%204.4408%2011.1304%204.53484%2011.2978%204.70222C11.4651%204.8696%2011.5592%205.09662%2011.5592%205.33333C11.5592%205.57004%2011.4651%205.79706%2011.2978%205.96444Z%22%20fill%3D%22%231681E5%22%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_134_1211%22%3E%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22white%22%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_toolbar_search_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6.97055%202.77438C9.12004%202.77782%2010.9176%204.40873%2011.1293%206.54772C11.3413%208.68671%209.89843%2010.6386%207.79156%2011.0635C5.68449%2011.4885%203.59802%2010.2485%202.96428%208.19459C2.33055%206.14065%203.35519%203.94065%205.33519%203.10428C5.85257%202.88543%206.40879%202.77322%206.97055%202.77438ZM6.97055%201.80469C4.11438%201.80469%201.79883%204.12024%201.79883%206.9764C1.79883%209.83257%204.11438%2012.1481%206.97055%2012.1481C9.82671%2012.1481%2012.1423%209.83257%2012.1423%206.9764C12.1423%204.12024%209.82671%201.80469%206.97055%201.80469ZM13.7099%2014.2006C13.6462%2014.2008%2013.5831%2014.1883%2013.5243%2014.1639C13.4654%2014.1394%2013.412%2014.1036%2013.3671%2014.0584L10.9105%2011.6019C10.7269%2011.4116%2010.7295%2011.1091%2010.9164%2010.9221C11.1035%2010.735%2011.4059%2010.7324%2011.596%2010.9162L14.0526%2013.3728C14.1203%2013.4406%2014.1665%2013.527%2014.1852%2013.621C14.2039%2013.715%2014.1943%2013.8125%2014.1576%2013.9011C14.121%2013.9897%2014.059%2014.0655%2013.9793%2014.1188C13.8996%2014.1721%2013.8058%2014.2006%2013.7099%2014.2006Z%22%20fill%3D%22%230D3B8B%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_resource_note_icon_02_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2218%22%20height%3D%2218%22%20viewBox%3D%220%200%2018%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M9.27034%2010.2555C9.80903%209.48573%2011.7823%206.71929%2012.5213%206.12418C11.579%208.23479%209.72279%2010.4064%208.61195%2012.4771C10.664%2013.1309%2011.594%2012.356%2012.7261%2011.4539L11.6043%2011.2515C13.029%2010.8125%2012.9295%2010.9751%2013.4284%2010.3266C13.8242%209.8123%2014.1527%209.11816%2014.2731%208.39322L13.346%208.50179C13.6418%208.33986%2014.3005%208.05532%2014.4052%207.89662C14.685%207.47231%2015.42%203.41471%2015.4286%202.57129C12.5954%202.59592%208.06861%208.16155%207.74116%2010.8215C7.41169%2013.4959%204.17444%2017.537%209.27034%2010.2555ZM13.4937%2011.7593C14.3387%2012.2331%2014.8413%2012.845%2014.8413%2013.5268C14.8413%2017.2052%202.57141%2017.2052%202.57141%2013.5268C2.57141%2011.8377%205.35854%2011.0801%206.80242%2010.8726C6.76843%2011.0999%206.69425%2011.3317%206.59731%2011.5687C5.60493%2011.7394%203.26887%2012.3708%203.26887%2013.5268C3.26887%2016.3054%2014.1438%2016.3054%2014.1438%2013.5268C14.1438%2013.0392%2013.6767%2012.574%2012.9049%2012.2C13.1025%2012.0647%2013.2964%2011.9154%2013.4937%2011.7593Z%22%20fill%3D%22%23333333%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_resource_delete_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2214%22%20height%3D%2214%22%20viewBox%3D%220%200%2014%2014%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20id%3D%22%26%23229%3B%26%23136%3B%26%23160%3B%26%23233%3B%26%23153%3B%26%23164%3B3%201%22%20clip-path%3D%22url%28%23clip0_235_1461%29%22%3E%3Cpath%20id%3D%22Vector%22%20d%3D%22M10.9266%2011.9545C10.9266%2012.4248%2010.5437%2012.8076%2010.0734%2012.8076H3.92656C3.45625%2012.8076%203.07344%2012.4248%203.07344%2011.9545V2.89824H10.9266V11.9545ZM4.77969%201.36699C4.77969%201.26855%204.85625%201.19199%204.95469%201.19199H9.05625C9.15469%201.19199%209.23125%201.26855%209.23125%201.36699V1.88105H4.77969V1.36699ZM13.4859%201.88105H10.2484V1.36699C10.2484%200.710728%209.7125%200.174805%209.05625%200.174805H4.95469C4.29844%200.174805%203.7625%200.710728%203.7625%201.36699V1.88105H0.514062C0.229687%201.88105%200%202.11074%200%202.39512C0%202.67949%200.229687%202.90918%200.514062%202.90918H2.04531V11.9545C2.04531%2012.9936%202.8875%2013.8357%203.92656%2013.8357H10.0734C11.1125%2013.8357%2011.9547%2012.9936%2011.9547%2011.9545V2.89824H13.4859C13.7703%202.89824%2014%202.66855%2014%202.38418C14%202.0998%2013.7703%201.88105%2013.4859%201.88105ZM7%2011.2654C7.28437%2011.2654%207.51406%2011.0357%207.51406%2010.7514V5.28262C7.51406%204.99824%207.28437%204.76855%207%204.76855C6.71563%204.76855%206.48594%204.99824%206.48594%205.28262V10.7514C6.48594%2011.0357%206.71563%2011.2654%207%2011.2654ZM4.60469%2011.2654C4.88906%2011.2654%205.11875%2011.0357%205.11875%2010.7514V5.28262C5.11875%204.99824%204.88906%204.76855%204.60469%204.76855C4.32031%204.76855%204.09063%204.99824%204.09063%205.28262V10.7514C4.10156%2011.0357%204.33125%2011.2654%204.60469%2011.2654ZM9.39531%2011.2654C9.67969%2011.2654%209.90937%2011.0357%209.90937%2010.7514V5.28262C9.90937%204.99824%209.67969%204.76855%209.39531%204.76855C9.11094%204.76855%208.88125%204.99824%208.88125%205.28262V10.7514C8.88125%2011.0357%209.11094%2011.2654%209.39531%2011.2654Z%22%20fill%3D%22%23FF5A5A%22%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_235_1461%22%3E%3Crect%20width%3D%2214%22%20height%3D%2214%22%20fill%3D%22white%22%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_resource_student_bookmark_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2218%22%20height%3D%2218%22%20viewBox%3D%220%200%2018%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M10.1977%2015.4284C10.0303%2015.4296%209.8643%2015.3975%209.70937%2015.3341C9.55445%2015.2707%209.41363%2015.1772%209.29508%2015.059L8.89674%2014.6617C6.96962%2012.7382%205.04489%2010.8123%203.12255%208.8839C2.92267%208.68157%202.6826%208.40348%202.60468%208.03972C2.58456%207.94673%202.57413%207.8519%202.57355%207.75675C2.57115%206.4344%202.57077%205.11234%202.5724%203.79056C2.57326%203.10466%203.08898%202.58176%203.77086%202.57459C4.02714%202.57129%205.15529%202.57129%205.15529%202.57129H7.69226C8.00694%202.57129%208.28532%202.66887%208.57847%202.87995C8.81567%203.05099%209.01556%203.25174%209.2275%203.4644L15.0368%209.27005C15.4408%209.67427%2015.5434%2010.279%2015.2928%2010.7754C15.2315%2010.8974%2015.1512%2011.0088%2015.0549%2011.1055C13.7412%2012.4255%2012.4255%2013.7421%2011.1078%2015.0553C10.9883%2015.1745%2010.8463%2015.2689%2010.6901%2015.3329C10.5338%2015.3969%2010.3665%2015.4294%2010.1977%2015.4284ZM3.80487%203.42594C3.57327%203.42594%203.42375%203.56944%203.42375%203.79056C3.42217%205.33428%203.4226%206.5943%203.42533%207.75517C3.42533%207.8064%203.43609%207.87829%203.46694%207.9255C3.55017%208.05579%203.64444%208.20288%203.76081%208.31882C5.42706%209.98996%209.86102%2014.4219%209.86102%2014.4219C9.96147%2014.5224%2010.0786%2014.5762%2010.1978%2014.5762C10.317%2014.5762%2010.4361%2014.5218%2010.5385%2014.4183L11.6277%2013.3286C12.5426%2012.415%2013.4857%2011.471%2014.419%2010.5476C14.5091%2010.4541%2014.5595%2010.3293%2014.5595%2010.1994C14.5595%2010.0695%2014.5091%209.94468%2014.419%209.8512C13.5696%209.00788%2012.7078%208.14677%2011.8748%207.31378L9.34903%204.79545C9.23108%204.67793%209.11514%204.55639%209.00278%204.43887C8.69327%204.11515%208.4037%203.81209%208.053%203.56025C7.9382%203.47875%207.83388%203.4235%207.69183%203.4235L4.54056%203.42436L3.80487%203.42594ZM7.03807%208.69047C6.60029%208.68872%206.18103%208.5136%205.87208%208.20344C5.56313%207.89327%205.38965%207.47333%205.38961%207.03555C5.39363%206.13053%206.13377%205.39497%207.03979%205.39497C7.25757%205.39437%207.47331%205.43705%207.67446%205.52054C7.8756%205.60403%208.05816%205.72666%208.2115%205.8813C8.36485%206.03595%208.48594%206.21953%208.56773%206.42137C8.64953%206.62321%208.69039%206.8393%208.68796%207.05707C8.68394%207.95807%207.9461%208.69033%207.04352%208.69033L7.03807%208.69047ZM7.04453%206.03381C6.49064%206.03381%206.03591%206.48309%206.03074%207.03469C6.02929%207.16662%206.05418%207.29751%206.10395%207.4197C6.15373%207.54188%206.22738%207.65291%206.3206%207.74628C6.41406%207.84151%206.52548%207.91726%206.64841%207.96913C6.77134%208.021%206.90335%208.04797%207.03678%208.04848C7.59497%208.04848%208.04898%207.59676%208.04927%207.04201C8.04905%206.77597%207.94378%206.52079%207.75637%206.33198C7.56896%206.14317%207.31457%206.03601%207.04854%206.03381H7.04453Z%22%20fill%3D%22%23333333%22%2F%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_resource_student_lighheight_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2218%22%20height%3D%2218%22%20viewBox%3D%220%200%2018%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M9.00006%204.7421C10.7915%204.7421%2012.2487%206.18175%2012.2487%207.9509C12.2511%208.59456%2012.0565%209.22392%2011.6904%209.75641C11.3243%2010.2889%2010.8038%2010.6997%2010.1972%2010.9348L10.2025%2010.9489C10.1438%2010.9763%2010.0943%2011.0196%2010.0597%2011.0739C10.0251%2011.1281%2010.0068%2011.1909%2010.0071%2011.255H9.97036V12.0391H7.97819V11.255H7.94136C7.94136%2011.2506%207.94365%2011.2465%207.94365%2011.2421C7.9409%2011.178%207.91967%2011.1161%207.88247%2011.0636C7.84528%2011.0111%207.79366%2010.9702%207.7337%2010.9457L7.7476%2010.9126C7.15564%2010.6693%206.65003%2010.258%206.29471%209.73086C5.93938%209.20369%205.75031%208.58428%205.7514%207.95096C5.75133%206.18141%207.20852%204.7421%209.00006%204.7421ZM9.66757%2014.2579H8.28105C8.20085%2014.2578%208.12395%2014.2263%208.06719%2014.1703C8.01044%2014.1144%207.97846%2014.0385%207.97826%2013.9592V12.7812H9.97036V13.9592C9.97027%2014.0385%209.93833%2014.1145%209.88155%2014.1705C9.82477%2014.2265%209.74781%2014.2579%209.66757%2014.2579ZM7.22694%2011.4918V13.9592C7.22777%2014.5338%207.69923%2014.9993%208.28098%2015H9.66757C10.2493%2014.9993%2010.7209%2014.5338%2010.7217%2013.9592V11.517C11.4053%2011.198%2011.9827%2010.6932%2012.3862%2010.0616C12.7897%209.43008%2013.0026%208.69802%2013%207.95124C13%205.7725%2011.2054%204%208.99999%204C6.79453%204%205%205.7725%205%207.9509C4.99892%208.68773%205.20701%209.4101%205.60065%2010.036C5.99428%2010.6619%206.5577%2011.1662%207.22694%2011.4918Z%22%20fill%3D%22%23333333%22%2F%3E%3Cpath%20d%3D%22M9.0153%203.84177C9.22323%203.83368%209.384%203.65988%209.38436%203.45215L9.38471%203.44979C9.38471%203.44521%209.38663%203.44127%209.38663%203.43705C9.38663%203.43397%209.38471%203.43161%209.38471%203.42853V2.38767C9.38471%202.38266%209.38706%202.37844%209.38663%202.37293C9.38276%202.27142%209.33946%202.1755%209.26604%202.10578C9.19262%202.03606%209.09495%201.9981%208.99402%202.00007C8.89309%202.00204%208.79696%202.04378%208.72628%202.11631C8.65561%202.18884%208.61604%202.28638%208.61607%202.38796L8.61564%202.39068C8.61564%202.39454%208.61337%202.39841%208.61337%202.40306C8.61372%202.40614%208.61529%202.4085%208.61564%202.41158V3.45201C8.61564%203.45738%208.61337%203.4621%208.61337%203.46711C8.61742%203.57038%208.66201%203.66781%208.73736%203.73805C8.81271%203.80828%208.91266%203.84558%209.0153%203.84177ZM6.68205%204.46259C6.85811%204.35092%206.91097%204.11957%206.80803%203.93947L6.80711%203.93733C6.80483%203.93332%206.80455%203.92895%206.80248%203.9253C6.80099%203.92265%206.79807%203.92158%206.79658%203.91893L6.27934%203.01758C6.27685%203.01322%206.27678%203.00835%206.27372%203.00384C6.22074%202.91614%206.13536%202.85316%206.03631%202.82869C5.93726%202.80422%205.8326%202.82026%205.74528%202.8733C5.65796%202.92634%205.5951%203.01205%205.57048%203.11164C5.54586%203.21124%205.56147%203.31659%205.61391%203.40462L5.61491%203.4072C5.61683%203.41056%205.61676%203.41507%205.61911%203.41908C5.62096%203.42159%205.62352%203.4228%205.6253%203.42531L6.14233%204.32637C6.14503%204.33102%206.14538%204.33625%206.14787%204.34061C6.20273%204.42795%206.28977%204.48984%206.3899%204.51271C6.49004%204.53557%206.5951%204.51755%206.68205%204.46259ZM4.9699%206.17416C5.06694%205.9889%204.99772%205.76192%204.81902%205.6577L4.81717%205.65627C4.81319%205.65398%204.81077%205.65033%204.80714%205.64818C4.80451%205.64668%204.80145%205.64718%204.79882%205.6456L3.90305%205.12521C3.89871%205.1227%203.89629%205.11848%203.89138%205.11619C3.8019%205.06652%203.69647%205.05465%203.59829%205.0832C3.5001%205.11174%203.41721%205.17836%203.36785%205.26841C3.31848%205.35845%203.30669%205.46454%203.33506%205.56333C3.36342%205.66213%203.42964%205.74553%203.51912%205.79521L3.52126%205.79692C3.5246%205.79886%203.5268%205.80279%203.53079%205.80508C3.53363%205.8063%203.53641%205.80609%203.53925%205.80738L4.43474%206.32762C4.43936%206.33027%204.44228%206.33464%204.44662%206.33715C4.53751%206.38524%204.64365%206.39509%204.74175%206.36453C4.83986%206.33398%204.92191%206.26551%204.9699%206.17416ZM13.0302%2010.8922C12.9332%2011.0774%2013.0024%2011.3044%2013.1811%2011.4086L13.1829%2011.4102C13.1869%2011.4124%2013.1892%2011.4162%2013.1928%2011.4182C13.1954%2011.4197%2013.1986%2011.4192%2013.2012%2011.4207L14.097%2011.9412C14.1013%2011.9437%2014.1038%2011.9479%2014.1086%2011.9503C14.153%2011.9754%2014.2018%2011.9914%2014.2523%2011.9973C14.3028%2012.0033%2014.354%2011.9992%2014.4029%2011.9852C14.4518%2011.9712%2014.4975%2011.9475%2014.5373%2011.9157C14.5771%2011.8838%2014.6102%2011.8444%2014.6348%2011.7995C14.6594%2011.7547%2014.6749%2011.7055%2014.6804%2011.6546C14.686%2011.6037%2014.6815%2011.5523%2014.6672%2011.5032C14.6529%2011.4541%2014.629%2011.4083%2014.5971%2011.3685C14.5651%2011.3287%2014.5256%2011.2957%2014.4809%2011.2713L14.4788%2011.2695C14.4755%2011.2676%2014.4732%2011.2637%2014.4692%2011.2614C14.4665%2011.26%2014.4636%2011.2603%2014.4608%2011.259L13.5653%2010.7388C13.5608%2010.7361%2013.5577%2010.7318%2013.5534%2010.7293C13.4625%2010.6812%2013.3564%2010.6714%2013.2583%2010.7019C13.1602%2010.7324%2013.0782%2010.8009%2013.0302%2010.8922ZM4.33763%208.51778C4.32959%208.30855%204.15687%208.14678%203.95036%208.14642L3.94801%208.14606C3.94346%208.14606%203.93947%208.14413%203.93535%208.14413C3.93236%208.14413%203.92994%208.14606%203.92688%208.14606H2.89254C2.88756%208.14606%202.88336%208.1437%202.87789%208.14413C2.7757%208.14613%202.67849%208.18888%202.60764%208.263C2.53678%208.33711%202.49809%208.43651%202.50007%208.53933C2.50205%208.64215%202.54455%208.73997%202.6182%208.81126C2.69186%208.88256%202.79064%208.92149%202.89282%208.9195L2.89553%208.91993C2.89937%208.91993%202.90321%208.92222%202.90783%208.92222C2.91089%208.92186%202.91324%208.92028%202.9163%208.91993H3.95029C3.95562%208.91993%203.96032%208.92222%203.9653%208.92222C4.06793%208.91815%204.16477%208.87329%204.23458%208.79747C4.30438%208.72165%204.34144%208.62107%204.33763%208.51778ZM13.6624%208.54856C13.6704%208.75772%2013.8431%208.9195%2014.0496%208.91993L14.052%208.92028C14.0565%208.92028%2014.0604%208.92236%2014.0646%208.92236C14.0676%208.92236%2014.0701%208.92028%2014.0731%208.92035H15.1075C15.1124%208.92035%2015.1167%208.92279%2015.122%208.92236C15.2242%208.92033%2015.3214%208.87755%2015.3923%208.80343C15.4632%208.72931%2015.5019%208.62992%2015.4999%208.52709C15.4979%208.42428%2015.4554%208.3265%2015.3817%208.25522C15.3081%208.18395%2015.2093%208.14502%2015.1071%208.147L15.1045%208.14649C15.1006%208.14649%2015.0967%208.14435%2015.0921%208.14435C15.0891%208.14456%2015.0868%208.14628%2015.0837%208.14649H14.0497C14.0444%208.14649%2014.0396%208.14428%2014.0346%208.14435C13.932%208.14837%2013.8352%208.19319%2013.7654%208.26898C13.6956%208.34476%2013.6586%208.44531%2013.6624%208.54856ZM4.95461%2010.8656C4.84371%2010.6884%204.61372%2010.6352%204.43474%2010.7389L4.4326%2010.7397C4.42862%2010.742%204.42428%2010.7423%204.42065%2010.7444C4.41802%2010.7459%204.41695%2010.7488%204.41432%2010.7503L3.51855%2011.2708C3.51421%2011.2733%203.50938%2011.2734%203.50489%2011.2764C3.41774%2011.3298%203.35514%2011.4157%203.33083%2011.5153C3.30651%2011.615%203.32245%2011.7203%203.37516%2011.8082C3.42788%2011.896%203.51305%2011.9593%203.61203%2011.9841C3.71101%2012.0088%203.81571%2011.9931%203.90319%2011.9404L3.90575%2011.9393C3.9091%2011.9374%203.91358%2011.9375%203.91756%2011.9351C3.92005%2011.9333%203.92126%2011.9307%203.92375%2011.9288L4.81923%2011.4086C4.82386%2011.4059%204.82905%2011.4056%204.83339%2011.4031C4.92019%2011.3479%204.98169%2011.2603%205.00442%2011.1596C5.02714%2011.0588%205.00923%2010.9531%204.95461%2010.8656ZM13.0454%206.20078C13.1564%206.37787%2013.3863%206.43106%2013.5653%206.32755L13.5675%206.32662C13.5715%206.32433%2013.5759%206.32419%2013.5795%206.32211C13.5821%206.32061%2013.5832%206.3176%2013.5859%206.31603L14.4817%205.79564C14.4859%205.79313%2014.4908%205.79306%2014.4953%205.79005C14.5828%205.7369%2014.6457%205.65095%2014.6702%205.55111C14.6948%205.45126%2014.6789%205.34569%2014.6261%205.25763C14.5732%205.1696%2014.4878%205.10629%2014.3886%205.08163C14.2893%205.05698%2014.1844%205.07298%2014.097%205.12614L14.0945%205.12707C14.091%205.129%2014.0866%205.12908%2014.0827%205.13144C14.0802%205.13308%2014.079%205.1358%2014.0765%205.13752L13.181%205.65777C13.1764%205.66042%2013.1711%205.66085%2013.1669%205.66343C13.08%205.71854%2013.0185%205.80607%2012.9957%205.90682C12.9729%206.00756%2012.9908%206.11329%2013.0454%206.20078ZM11.3444%204.47798C11.5285%204.57554%2011.7541%204.50596%2011.8577%204.32615L11.8592%204.32422C11.8614%204.32021%2011.8652%204.31792%2011.8673%204.31427C11.8688%204.31162%2011.8682%204.30847%2011.8698%204.30575L12.387%203.40441C12.3894%203.40011%2012.3936%203.39753%2012.396%203.39274C12.4444%203.30277%2012.4555%203.19719%2012.4269%203.099C12.3983%203.00082%2012.3323%202.91799%2012.2433%202.86856C12.1542%202.81914%2012.0494%202.80713%2011.9516%202.83515C11.8538%202.86317%2011.771%202.92895%2011.7212%203.01816L11.7195%203.02023C11.7175%203.02367%2011.7136%203.02596%2011.7113%203.02997C11.71%203.03269%2011.7103%203.03562%2011.7091%203.03834L11.192%203.9394C11.1893%203.94398%2011.1851%203.94706%2011.1827%203.95143C11.1348%204.04285%2011.1249%204.14965%2011.1553%204.24838C11.1856%204.34711%2011.2536%204.42968%2011.3444%204.47798Z%22%20fill%3D%22%23333333%22%2F%3E%3C%2Fsvg%3E";

/* script */
const __vue_script__$2w = script$2w;
var __vue_render__$2w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: _vm.isShowToolbar,
              expression: "isShowToolbar",
            },
          ],
          staticClass: "title-bar",
        },
        [
          _c("div", { staticClass: "title-bar-con" }, [
            _c("div", { staticClass: "cur-name" }, [_vm._v(_vm._s(_vm.title))]),
            _vm._v(" "),
            _c("div", { staticClass: "search-box" }, [
              _c("div", [_vm._v(_vm._s(_vm.schedule) + "%")]),
              _vm._v(" "),
              _c(
                "div",
                {
                  on: {
                    click: function ($event) {
                      $event.stopPropagation();
                      return _vm.addBookmark(_vm.location)
                    },
                  },
                },
                [
                  _c("img", {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value:
                          JSON.stringify(_vm.bookmarkList).indexOf(
                            _vm.location
                          ) == -1,
                        expression:
                          "JSON.stringify(bookmarkList).indexOf(location) == -1",
                      },
                    ],
                    staticClass: "icon",
                    attrs: {
                      src: __$_require_static_images_icons_toolbar_bookmark_svg__,
                      alt: "",
                    },
                  }),
                  _vm._v(" "),
                  _c("img", {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value:
                          JSON.stringify(_vm.bookmarkList).indexOf(
                            _vm.location
                          ) !== -1,
                        expression:
                          "JSON.stringify(bookmarkList).indexOf(location) !== -1",
                      },
                    ],
                    staticClass: "icon",
                    attrs: {
                      src: __$_require_static_images_icons_toolbar_bookmark_up_svg__,
                      alt: "",
                    },
                  }),
                ]
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  on: {
                    click: function ($event) {
                      $event.stopPropagation();
                      return _vm.showSearch.apply(null, arguments)
                    },
                  },
                },
                [
                  _c("img", {
                    staticClass: "icon",
                    attrs: {
                      src: __$_require_static_images_icons_toolbar_search_svg__,
                      alt: "",
                    },
                  }),
                ]
              ),
            ]),
          ]),
        ]
      ),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "xml-toolbar-phone-content",
          style: { height: _vm.innerHeight + "px" },
        },
        [
          _c(
            "div",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: _vm.isShowToolbar,
                  expression: "isShowToolbar",
                },
              ],
              staticClass: "xml-toolbar-phone",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _vm.loadingMode == "page" && _vm.current == -1
                ? _c("div", { staticClass: "btn-list" }, [
                    _c(
                      "div",
                      {
                        staticClass: "btn-item",
                        on: {
                          click: function ($event) {
                            return _vm.$emit("prevPage")
                          },
                        },
                      },
                      [_c("i", { staticClass: "el-icon-d-arrow-left" })]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "btn-item",
                        on: {
                          click: function ($event) {
                            return _vm.$emit("nextPage")
                          },
                        },
                      },
                      [_c("i", { staticClass: "el-icon-d-arrow-right" })]
                    ),
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm._l(_vm.toolbarList, function (item, index) {
                return _c(
                  "div",
                  {
                    key: index,
                    staticClass: "toolbar-phone-item",
                    class: index == _vm.current ? item.select : item.default,
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.clickToolbar(index)
                      },
                    },
                  },
                  [
                    _c("div", { staticClass: "toolbar-icon" }),
                    _vm._v(" "),
                    _c("div", [_vm._v(_vm._s(item.name))]),
                  ]
                )
              }),
            ],
            2
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "xml-popup",
        {
          ref: "searchPopShow",
          on: {
            close: function ($event) {
              _vm.current = -1;
            },
          },
        },
        [
          _c(
            "div",
            {
              staticClass: "search-pop",
              style: { height: (_vm.innerHeight / 10) * 8 + "px" },
            },
            [
              _c("div", {
                staticClass: "xml-popup-mback",
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                    _vm.$refs.searchPopShow.show = false;
                  },
                },
              }),
              _vm._v(" "),
              _c("search-phone", {
                staticClass: "search-pop-content",
                attrs: { componentList: _vm.componentList },
                on: { seachItem: _vm.seachItem },
              }),
            ],
            1
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "xml-popup",
        {
          ref: "catalogPopup",
          on: {
            close: function ($event) {
              _vm.current = -1;
            },
          },
        },
        [
          _c(
            "div",
            { staticClass: "catalog-pop" },
            [
              _c("div", {
                staticClass: "xml-popup-mback",
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                    return _vm.clickToolbar(0)
                  },
                },
              }),
              _vm._v(" "),
              _c("div", { staticClass: "book-name" }, [
                _vm._v(_vm._s(_vm.textBookName)),
              ]),
              _vm._v(" "),
              _c("el-tree", {
                ref: "tree",
                class:
                  "preview-tree-box-" + _vm.pageType + " custom-catalog-tree",
                attrs: {
                  id: "preview-tree",
                  indent: 24,
                  draggable: false,
                  "default-expand-all": true,
                  data: _vm.catalogList,
                  "node-key": "id",
                  props: _vm.defaultProps,
                  "filter-node-method": _vm.filterNode,
                  "expand-on-click-node": false,
                },
                scopedSlots: _vm._u([
                  {
                    key: "default",
                    fn: function (ref) {
                      var node = ref.node;
                      var data = ref.data;
                      return _c(
                        "div",
                        {
                          staticClass: "catalog-node",
                          class: [node.childNodes.length ? "parent-node" : ""],
                        },
                        [
                          _c(
                            "div",
                            {
                              class: [
                                "catalog-node__left",
                                node.level === 1 ? "with-prefix" : "",
                              ],
                              on: {
                                click: function () {
                                  return _vm.nodeClick(data)
                                },
                              },
                            },
                            [
                              _c(
                                "div",
                                {
                                  staticClass: "catalog-node__label",
                                  class:
                                    data.isSampleSeal == "0"
                                      ? "sample-seal"
                                      : "",
                                },
                                [
                                  _c(
                                    "div",
                                    {
                                      class: [
                                        "node-label",
                                        node.expanded ? "expanded-node" : "",
                                      ],
                                    },
                                    [
                                      _c(
                                        "span",
                                        {
                                          class:
                                            _vm.selectId == data.id
                                              ? "highlight-bg"
                                              : "",
                                        },
                                        [
                                          _vm._v(
                                            "\n                  " +
                                              _vm._s(node.label) +
                                              "\n                "
                                          ),
                                        ]
                                      ),
                                    ]
                                  ),
                                ]
                              ),
                            ]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              staticClass: "catalog-node__right",
                              on: {
                                click: function () {
                                  return _vm.expandOrCollapseNode(node)
                                },
                              },
                            },
                            [
                              node.childNodes.length && node.expanded
                                ? _c("i", {
                                    staticClass: "el-icon-arrow-down",
                                    staticStyle: {
                                      color: "var(--preview-theme-color)",
                                      "font-size": "16px",
                                      "font-weight": "600",
                                    },
                                  })
                                : node.childNodes.length && !node.expanded
                                ? _c("i", {
                                    staticClass: "el-icon-arrow-right",
                                    staticStyle: {
                                      "font-size": "16px",
                                      "font-weight": "600",
                                    },
                                  })
                                : _vm._e(),
                            ]
                          ),
                        ]
                      )
                    },
                  },
                ]),
              }),
            ],
            1
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "xml-popup",
        {
          ref: "setPopup",
          on: {
            close: function ($event) {
              _vm.current = -1;
            },
          },
        },
        [
          _c(
            "div",
            {
              staticClass: "set-pop",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("字体")]),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "family-select-box",
                    staticStyle: { "margin-left": "14px" },
                  },
                  [
                    _c(
                      "el-select",
                      {
                        attrs: {
                          placeholder: "请选择",
                          "popper-append-to-body": false,
                        },
                        on: { change: _vm.setThemeFontFamily },
                        model: {
                          value: _vm.form.themeFontFamily,
                          callback: function ($$v) {
                            _vm.$set(_vm.form, "themeFontFamily", $$v);
                          },
                          expression: "form.themeFontFamily",
                        },
                      },
                      _vm._l(_vm.options, function (item) {
                        return _c("el-option", {
                          key: item.value,
                          attrs: { label: item.label, value: item.value },
                        })
                      }),
                      1
                    ),
                  ],
                  1
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("字号")]),
                _vm._v(" "),
                _c("div", { staticStyle: { "margin-left": "14px" } }, [
                  _c(
                    "div",
                    {
                      staticClass: "size-btn",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.setThemeFontSize("minus")
                        },
                      },
                    },
                    [_vm._v("A-")]
                  ),
                  _vm._v(" "),
                  _c("span", [
                    _vm._v(
                      _vm._s(_vm.fontSizeLabelMap[_vm.form.themeFontSize])
                    ),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "size-btn",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.setThemeFontSize("plus")
                        },
                      },
                    },
                    [_vm._v("A+")]
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "set-item spacing-box number-input" },
                [
                  _c("span", { staticClass: "keyname" }, [_vm._v("行间距")]),
                  _vm._v(" "),
                  _c("el-input-number", {
                    attrs: { size: "small", min: 30, max: 100 },
                    on: { change: _vm.setThemeLineHeight },
                    model: {
                      value: _vm.form.themeLineHeight,
                      callback: function ($$v) {
                        _vm.$set(_vm.form, "themeLineHeight", $$v);
                      },
                      expression: "form.themeLineHeight",
                    },
                  }),
                  _vm._v(" "),
                  _c("span", { staticClass: "keyname m-l23" }, [
                    _vm._v("字间距"),
                  ]),
                  _vm._v(" "),
                  _c("el-input-number", {
                    attrs: { size: "small", min: 0, max: 30 },
                    on: { change: _vm.setThemeLetterSpacing },
                    model: {
                      value: _vm.form.themeLetterSpacing,
                      callback: function ($$v) {
                        _vm.$set(_vm.form, "themeLetterSpacing", $$v);
                      },
                      expression: "form.themeLetterSpacing",
                    },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("阅读模式")]),
                _vm._v(" "),
                _c("div", { staticStyle: { "margin-left": "14px" } }, [
                  _c(
                    "div",
                    {
                      staticClass: "reading-mode",
                      class:
                        _vm.settingData.readingMode == "roll"
                          ? "current-reading-mode"
                          : "",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.readingMode("roll")
                        },
                      },
                    },
                    [_vm._v("滑动")]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "reading-mode",
                      class:
                        _vm.settingData.readingMode == "page"
                          ? "current-reading-mode"
                          : "",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.readingMode("page")
                        },
                      },
                    },
                    [_vm._v("翻页")]
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "set-item" }, [
                _c("span", { staticClass: "keyname" }, [_vm._v("背景色")]),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "color-box" },
                  _vm._l(_vm.colorList, function (item, index) {
                    return _c("div", {
                      staticClass: "color-tag",
                      class:
                        _vm.form.themeBgColor === item.colorval ? "active" : "",
                      style: { background: item.colorval },
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.setThemeBgColor(item.colorval)
                        },
                      },
                    })
                  }),
                  0
                ),
              ]),
            ]
          ),
        ]
      ),
      _vm._v(" "),
      _c(
        "xml-popup",
        {
          ref: "notePpoup",
          on: {
            close: function ($event) {
              _vm.current = -1;
            },
          },
        },
        [
          _c(
            "div",
            {
              staticClass: "note-pop",
              on: {
                click: function ($event) {
                  $event.stopPropagation();
                },
              },
            },
            [
              _c("div", {
                staticClass: "xml-popup-mback",
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                    return _vm.clickToolbar(1)
                  },
                },
              }),
              _vm._v(" "),
              _vm.curnotetabs == 0
                ? [
                    _c(
                      "div",
                      { staticClass: "note-list" },
                      [
                        _c(
                          "el-collapse",
                          {
                            attrs: { accordion: "" },
                            on: { change: _vm.handleChange },
                            model: {
                              value: _vm.noteNames,
                              callback: function ($$v) {
                                _vm.noteNames = $$v;
                              },
                              expression: "noteNames",
                            },
                          },
                          _vm._l(_vm.notesList, function (item, index) {
                            return _c(
                              "el-collapse-item",
                              {
                                key: index,
                                attrs: { name: item.firstLevelId },
                              },
                              [
                                _c("template", { slot: "title" }, [
                                  _c("div", { staticClass: "collapse-title" }, [
                                    _vm._v(
                                      "\n                  " +
                                        _vm._s(item.firstLevelLabel) +
                                        "\n                "
                                    ),
                                  ]),
                                ]),
                                _vm._v(" "),
                                _vm._l(item.list, function (items, index) {
                                  return _c(
                                    "div",
                                    {
                                      key: index,
                                      staticClass: "note-item collapse-item",
                                      on: {
                                        click: function ($event) {
                                          return _vm.resourceDetails(
                                            Object.assign(
                                              {},
                                              {
                                                jumpType: "marker",
                                                toolbarIndex: 1,
                                              },
                                              items
                                            )
                                          )
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_note_icon_02_svg__,
                                          alt: "",
                                        },
                                      }),
                                      _vm._v(" "),
                                      _c("div", [
                                        _c("div", { staticClass: "or-text" }, [
                                          _vm._v(
                                            "\n                    " +
                                              _vm._s(
                                                _vm.removeStyle(
                                                  items.selectionText
                                                )
                                              ) +
                                              "\n                  "
                                          ),
                                        ]),
                                        _vm._v(" "),
                                        _c(
                                          "div",
                                          { staticClass: "note-con" },
                                          [
                                            _c("xmlTextOverflow", {
                                              attrs: {
                                                info: items.noteContent,
                                              },
                                            }),
                                          ],
                                          1
                                        ),
                                      ]),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        { staticClass: "operate-btns" },
                                        [
                                          _c(
                                            "div",
                                            {
                                              staticClass: "delete-btn",
                                              on: {
                                                "!click": function ($event) {
                                                  $event.stopPropagation();
                                                  return _vm.deleteNote(items)
                                                },
                                              },
                                            },
                                            [
                                              _c("img", {
                                                attrs: {
                                                  src: __$_require_static_images_icons_resource_delete_svg__,
                                                  alt: "",
                                                },
                                              }),
                                              _vm._v(
                                                "\n                    删除\n                  "
                                              ),
                                            ]
                                          ),
                                        ]
                                      ),
                                    ]
                                  )
                                }),
                              ],
                              2
                            )
                          }),
                          1
                        ),
                        _vm._v(" "),
                        _c("xml-empty", {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.notesList.length == 0,
                              expression: "notesList.length == 0",
                            },
                          ],
                        }),
                      ],
                      1
                    ),
                  ]
                : _vm.curnotetabs == 1
                ? [
                    _c(
                      "div",
                      { staticClass: "note-list" },
                      [
                        _c(
                          "el-collapse",
                          {
                            attrs: { accordion: "" },
                            on: { change: _vm.handleChange },
                            model: {
                              value: _vm.bookmarkNames,
                              callback: function ($$v) {
                                _vm.bookmarkNames = $$v;
                              },
                              expression: "bookmarkNames",
                            },
                          },
                          _vm._l(_vm.bookmarkList, function (item, index) {
                            return _c(
                              "el-collapse-item",
                              {
                                key: index,
                                attrs: { name: item.firstLevelId },
                              },
                              [
                                _c("template", { slot: "title" }, [
                                  _c("div", { staticClass: "collapse-title" }, [
                                    _vm._v(
                                      "\n                  " +
                                        _vm._s(item.firstLevelLabel) +
                                        "\n                "
                                    ),
                                  ]),
                                ]),
                                _vm._v(" "),
                                _vm._l(item.list, function (items, k) {
                                  return _c(
                                    "div",
                                    {
                                      key: k,
                                      staticClass: "mark-item collapse-item",
                                      on: {
                                        click: function ($event) {
                                          return _vm.resourceDetails(
                                            Object.assign(
                                              {},
                                              {
                                                jumpType: "marker",
                                                toolbarIndex: 1,
                                              },
                                              items
                                            )
                                          )
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_student_bookmark_svg__,
                                          alt: "",
                                        },
                                      }),
                                      _vm._v(" "),
                                      _c("div", [
                                        _vm._v(
                                          _vm._s(
                                            _vm.removeStyle(items.bookmarkText)
                                          )
                                        ),
                                      ]),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        { staticClass: "operate-btns" },
                                        [
                                          _c(
                                            "div",
                                            {
                                              staticClass: "delete-btn",
                                              on: {
                                                "!click": function ($event) {
                                                  $event.stopPropagation();
                                                  return _vm.deleteBookmark(
                                                    items
                                                  )
                                                },
                                              },
                                            },
                                            [
                                              _c("img", {
                                                attrs: {
                                                  src: __$_require_static_images_icons_resource_delete_svg__,
                                                  alt: "",
                                                },
                                              }),
                                              _vm._v(
                                                "\n                    删除\n                  "
                                              ),
                                            ]
                                          ),
                                        ]
                                      ),
                                    ]
                                  )
                                }),
                              ],
                              2
                            )
                          }),
                          1
                        ),
                        _vm._v(" "),
                        _c("xml-empty", {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.bookmarkList.length == 0,
                              expression: "bookmarkList.length == 0",
                            },
                          ],
                        }),
                      ],
                      1
                    ),
                  ]
                : _vm.curnotetabs == 2
                ? [
                    _c(
                      "div",
                      { staticClass: "note-list" },
                      [
                        _c(
                          "el-collapse",
                          {
                            attrs: { accordion: "" },
                            on: { change: _vm.handleChange },
                            model: {
                              value: _vm.highlightNames,
                              callback: function ($$v) {
                                _vm.highlightNames = $$v;
                              },
                              expression: "highlightNames",
                            },
                          },
                          _vm._l(_vm.highlightList, function (item, index) {
                            return _c(
                              "el-collapse-item",
                              {
                                key: index,
                                attrs: { name: item.firstLevelId },
                              },
                              [
                                _c("template", { slot: "title" }, [
                                  _c("div", { staticClass: "collapse-title" }, [
                                    _vm._v(
                                      "\n                  " +
                                        _vm._s(item.firstLevelLabel) +
                                        "\n                "
                                    ),
                                  ]),
                                ]),
                                _vm._v(" "),
                                _vm._l(item.list, function (items, index) {
                                  return _c(
                                    "div",
                                    {
                                      key: index,
                                      staticClass:
                                        "Highlight-item collapse-item",
                                      on: {
                                        click: function ($event) {
                                          return _vm.resourceDetails(
                                            Object.assign(
                                              {},
                                              {
                                                jumpType: "marker",
                                                toolbarIndex: 1,
                                              },
                                              items
                                            )
                                          )
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_student_lighheight_svg__,
                                          alt: "",
                                        },
                                      }),
                                      _vm._v(" "),
                                      _c("div", [
                                        _vm._v(
                                          "\n                  " +
                                            _vm._s(
                                              _vm.removeStyle(
                                                items.selectionText
                                              )
                                            ) +
                                            "\n                "
                                        ),
                                      ]),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        { staticClass: "operate-btns" },
                                        [
                                          _c(
                                            "div",
                                            {
                                              staticClass: "delete-btn",
                                              on: {
                                                "!click": function ($event) {
                                                  $event.stopPropagation();
                                                  return _vm.deleteHighlight(
                                                    items,
                                                    "highlight"
                                                  )
                                                },
                                              },
                                            },
                                            [
                                              _c("img", {
                                                attrs: {
                                                  src: __$_require_static_images_icons_resource_delete_svg__,
                                                  alt: "",
                                                },
                                              }),
                                              _vm._v(
                                                "\n                    删除\n                  "
                                              ),
                                            ]
                                          ),
                                        ]
                                      ),
                                    ]
                                  )
                                }),
                              ],
                              2
                            )
                          }),
                          1
                        ),
                        _vm._v(" "),
                        _c("xml-empty", {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.highlightList.length == 0,
                              expression: "highlightList.length == 0",
                            },
                          ],
                        }),
                      ],
                      1
                    ),
                  ]
                : _vm.curnotetabs == 3
                ? [
                    _c(
                      "div",
                      { staticClass: "note-list" },
                      [
                        _c(
                          "el-collapse",
                          {
                            attrs: { accordion: "" },
                            on: { change: _vm.handleChange },
                            model: {
                              value: _vm.linkNames,
                              callback: function ($$v) {
                                _vm.linkNames = $$v;
                              },
                              expression: "linkNames",
                            },
                          },
                          _vm._l(_vm.linkList, function (item, index) {
                            return _c(
                              "el-collapse-item",
                              {
                                key: index,
                                attrs: { name: item.firstLevelId },
                              },
                              [
                                _c("template", { slot: "title" }, [
                                  _c("div", { staticClass: "collapse-title" }, [
                                    _vm._v(
                                      "\n                  " +
                                        _vm._s(item.firstLevelLabel) +
                                        "\n                "
                                    ),
                                  ]),
                                ]),
                                _vm._v(" "),
                                _vm._l(item.list, function (items, index) {
                                  return _c(
                                    "div",
                                    {
                                      key: index,
                                      staticClass:
                                        "Highlight-item collapse-item",
                                      on: {
                                        click: function ($event) {
                                          return _vm.resourceDetails(
                                            Object.assign(
                                              {},
                                              {
                                                jumpType: "marker",
                                                toolbarIndex: 1,
                                              },
                                              items
                                            )
                                          )
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_resource_student_lighheight_svg__,
                                          alt: "",
                                        },
                                      }),
                                      _vm._v(" "),
                                      _c("div", [
                                        _vm._v(
                                          "\n                  " +
                                            _vm._s(
                                              _vm.removeStyle(
                                                items.selectionText
                                              )
                                            ) +
                                            "\n                "
                                        ),
                                      ]),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        { staticClass: "operate-btns" },
                                        [
                                          _c(
                                            "div",
                                            {
                                              staticClass: "delete-btn",
                                              on: {
                                                "!click": function ($event) {
                                                  $event.stopPropagation();
                                                  return _vm.deleteHighlight(
                                                    items,
                                                    "link"
                                                  )
                                                },
                                              },
                                            },
                                            [
                                              _c("img", {
                                                attrs: {
                                                  src: __$_require_static_images_icons_resource_delete_svg__,
                                                  alt: "",
                                                },
                                              }),
                                              _vm._v(
                                                "\n                    删除\n                  "
                                              ),
                                            ]
                                          ),
                                        ]
                                      ),
                                    ]
                                  )
                                }),
                              ],
                              2
                            )
                          }),
                          1
                        ),
                        _vm._v(" "),
                        _c("xml-empty", {
                          directives: [
                            {
                              name: "show",
                              rawName: "v-show",
                              value: _vm.linkList.length == 0,
                              expression: "linkList.length == 0",
                            },
                          ],
                        }),
                      ],
                      1
                    ),
                  ]
                : _vm._e(),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "note-tabbar" },
                _vm._l(_vm.noteTabbarList, function (item, index) {
                  return _c(
                    "div",
                    {
                      directives: [
                        {
                          name: "show",
                          rawName: "v-show",
                          value: item.show,
                          expression: "item.show",
                        },
                      ],
                      class: _vm.curnotetabs == item.id ? "active" : "",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.clicknotetab(item.id)
                        },
                      },
                    },
                    [_vm._v("\n          " + _vm._s(item.name) + "\n        ")]
                  )
                }),
                0
              ),
            ],
            2
          ),
        ]
      ),
      _vm._v(" "),
      _c("resource-list-phone", {
        ref: "resourceListPopup",
        attrs: {
          textBookResource: _vm.textBookResource,
          resourcesList: _vm.resourcesList,
          resourceBasisPath: _vm.resourceBasisPath,
          showToast: _vm.showToast,
          isShowTeacherResource: _vm.isShowTeacherResource,
          userType: _vm.userType,
          sysEnv: _vm.sysEnv,
        },
        on: {
          resourceDetails: _vm.resourceDetails,
          resourceReader: _vm.resourceReader,
          close: function ($event) {
            _vm.current = -1;
          },
        },
      }),
    ],
    1
  )
};
var __vue_staticRenderFns__$2w = [];
__vue_render__$2w._withStripped = true;

  /* style */
  const __vue_inject_styles__$2w = undefined;
  /* scoped */
  const __vue_scope_id__$2w = undefined;
  /* module identifier */
  const __vue_module_identifier__$2w = undefined;
  /* functional template */
  const __vue_is_functional_template__$2w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2w, staticRenderFns: __vue_staticRenderFns__$2w },
    __vue_inject_styles__$2w,
    __vue_script__$2w,
    __vue_scope_id__$2w,
    __vue_is_functional_template__$2w,
    __vue_module_identifier__$2w,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2v = {
  name: 'modifyNotes',
  props: {},
  data() {
    return {
      show: false,
      noteContent: '',
      selectionText: '',
      data: ''
    };
  },
  methods: {
    open(data) {
      this.show = true;
      this.noteContent = data.noteContent;
      this.selectionText = data.selectionText;
      this.data = data;
    },
    // 点击关闭按钮
    close() {},
    onCancelClick() {
      this.show = false;
    },
    onSaveClick() {
      console.log('保存');
      if (!this.noteContent.trim()) {
        this.$message({
          message: '笔记内容不能为空',
          type: 'warning'
        });
        return;
      }
      this.data.noteContent = this.noteContent;
      this.$emit('saveNote', this.data);
      // 关闭对话框
      this.show = false;
    }
  }
};

/* script */
const __vue_script__$2v = script$2v;
/* template */
var __vue_render__$2v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "el-dialog",
    {
      attrs: {
        "append-to-body": "",
        title: "编辑笔记",
        visible: _vm.show,
        width: "400px",
      },
      on: {
        "update:visible": function ($event) {
          _vm.show = $event;
        },
        close: _vm.close,
      },
      scopedSlots: _vm._u([
        {
          key: "footer",
          fn: function () {
            return [
              _c("el-button", { on: { click: _vm.onCancelClick } }, [
                _vm._v("取消"),
              ]),
              _vm._v(" "),
              _c(
                "el-button",
                { attrs: { type: "primary" }, on: { click: _vm.onSaveClick } },
                [_vm._v("保存")]
              ),
            ]
          },
          proxy: true,
        },
      ]),
    },
    [
      _c(
        "div",
        { staticClass: "add-teacher-resources-dialog" },
        [
          _c(
            "el-form",
            { attrs: { "label-width": "100px", "label-position": "left" } },
            [
              _c("div", { staticClass: "content-left" }, [
                _vm._v(_vm._s(_vm.selectionText)),
              ]),
              _vm._v(" "),
              _c("el-input", {
                attrs: {
                  type: "textarea",
                  rows: 4,
                  placeholder: "请输入笔记内容",
                  maxlength: "200",
                },
                model: {
                  value: _vm.noteContent,
                  callback: function ($$v) {
                    _vm.noteContent = $$v;
                  },
                  expression: "noteContent",
                },
              }),
            ],
            1
          ),
        ],
        1
      ),
    ]
  )
};
var __vue_staticRenderFns__$2v = [];
__vue_render__$2v._withStripped = true;

  /* style */
  const __vue_inject_styles__$2v = undefined;
  /* scoped */
  const __vue_scope_id__$2v = undefined;
  /* module identifier */
  const __vue_module_identifier__$2v = undefined;
  /* functional template */
  const __vue_is_functional_template__$2v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2v, staticRenderFns: __vue_staticRenderFns__$2v },
    __vue_inject_styles__$2v,
    __vue_script__$2v,
    __vue_scope_id__$2v,
    __vue_is_functional_template__$2v,
    __vue_module_identifier__$2v,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2u = {
  //预览教材目录
  name: 'PreviewCatalog',
  props: {
    textBookName: String,
    textBookId: String,
    catalogList: Array,
    userType: {
      type: String,
      default: 'teacher'
    },
    notesList: {
      type: Array,
      default: []
    },
    checkedId: {
      type: String,
      default: ''
    },
    pageType: {
      type: String,
      default: 'h5'
    },
    styleType: {
      type: String,
      default: ''
    },
    isShow: false,
    title: String,
    PreviewCatalog: false,
    sysEnv: {
      type: Object,
      default: () => {}
    },
    componentList: {
      type: Array,
      default: () => {}
    }
  },
  data() {
    return {
      deleteItem: '',
      deletetype: '',
      reminder: false,
      xmlshow: false,
      currentActive: 0,
      tabs: ['目录', '笔记', '书签'],
      scrollTop: 0,
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      selectId: '',
      catalogLists: [],
      searchValue: '',
      currentNodeKey: ''
      // image
    };
  },
  watch: {
    catalogList: {
      handler(newValue) {
        if (this.catalogLists.length > 0) {
          this.currentNodeKey = this.catalogLists[0].id;
        }
      },
      immediate: true
    },
    checkedId: {
      handler(newValue) {
        this.currentNodeKey = this.checkedId || this.catalogLists[0].id;
        this.scrollTopFun();
        // if (this.$refs.tree) {
        //   this.$refs.tree.setCurrentKey(newValue)
        //   let Dom = document.getElementsByClassName('highlight-bg')
        //   let DomBox = document.getElementById('preview-tree')

        //   if (Dom) {
        //     setTimeout(() => {
        //       if (Dom[0].getBoundingClientRect().top > 400 || Dom[0].getBoundingClientRect().top < 200) {
        //         DomBox.scrollTop = 0
        //         DomBox.scrollTop = Dom[0].getBoundingClientRect().top - 300
        //       }
        //     }, 500)
        //   }
        // }
      },
      immediate: true,
      deep: true
    },
    isShow: {
      handler(newValue) {
        let dom = document.getElementById('preview-tree');
        if (dom) {
          dom.animate({
            scrollTop: this.scrollTop
          }, 200);
        }
      },
      immediate: true
    },
    searchValue: {
      handler(newValue) {
        this.$emit('onSearchPc', newValue);
      }
    }
  },
  created() {},
  mounted() {
    this.scrollTopFun();
    const origin = this.catalogList;
    const filterMenu = menuList => {
      return menuList.filter(item => {
        return item.hiddenFlag !== '1';
      }).map(item => {
        item = Object.assign({}, item);
        if (item.children?.length) {
          item.children = filterMenu(item.children);
        }
        return item;
      });
    };
    this.catalogLists = filterMenu(origin);
    setTimeout(() => {
      if (this.catalogLists.length > 0) {
        this.currentNodeKey = this.catalogLists[0].id;
      }
    }, 500);
  },
  methods: {
    nodeclick(data, onj, node) {
      this.$emit('onNodeClick', {
        id: data.id,
        label: data.label,
        xmlCompRef: data.xmlCompRef,
        locationChapterId: data.locationChapterId
      });
      this.selectId = data.id;
    },
    changeTab(val) {
      this.currentActive = val;
      if (this.currentActive == 0) {
        setTimeout(() => {
          this.scrollTopFun();
        }, 500);
      }
    },
    expandOrCollapseNode(node, data) {
      node.expanded = !node.expanded;
    },
    // nodeClick(data) {
    //   this.$emit('onNodeClick', { id: data.id, label: data.label, xmlCompRef: data.xmlCompRef })
    //   this.$emit('resourceDetails', data, 'id', '')
    //   this.selectId = data.id
    // },
    backClick() {
      this.$emit('onBackClick');
    },
    jumpingNotes(item) {
      this.$emit('onBackClick');
      this.$emit('jumpingNotes', item);
    },
    jumpingBookmark(item) {
      this.$emit('onBackClick');
      this.$emit('jumpingBookmark', item);
    },
    editNotoPC(item) {
      if (this.pageType == 'h5') {
        this.$emit('onBackClick');
      }
      this.$emit('editNotoPC', item);
    },
    deleteNotoPC(item, type) {
      this.reminder = true;
      // this.$emit('deleteNotoPC', item, index)
      this.deleteItem = item;
      this.deletetype = type;
    },
    confirmDelete() {
      this.reminder = false;
      this.$emit('deleteNotoPC', this.deleteItem, this.deletetype);
    },
    scrollTopFun() {
      if (this.$refs.tree) {
        this.$refs.tree.setCurrentKey(this.checkedId);
        let Dom = document.getElementsByClassName('is-current');
        let DomBox = document.getElementById('preview-left-student');
        if (Dom) {
          setTimeout(() => {
            if (Dom[0]) {
              if (Dom[0].getBoundingClientRect().top > document.documentElement.clientHeight - 40 || Dom[0].getBoundingClientRect().top < 60) {
                if (DomBox) {
                  DomBox.scrollTop = 0;
                  DomBox.scrollTo({
                    top: Dom[0].getBoundingClientRect().top - 300,
                    behavior: 'smooth'
                  });
                }
              }
            }
          }, 500);
        }
      }
    },
    filterNode(value, data, node) {
      return data.hiddenFlag !== '1';
    },
    isTrialReading(chapterId) {
      let i = this.componentList.findIndex(item => item.chapterId == chapterId) == -1;
      console.log(i);
      return i;
    }
  }
};

/* script */
const __vue_script__$2u = script$2u;
/* template */
var __vue_render__$2u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-preview-catalog-pc",
      class: _vm.sysEnv.isHuaTeng ? "preview-left-student" : "",
      attrs: { id: "xmlPreviewCatalogPc" },
    },
    [
      !_vm.sysEnv.isNoteHighlight && !_vm.sysEnv.isHuaTeng
        ? _c(
            "div",
            {
              staticClass: "catalog-header",
              class: _vm.userType == "teacher" ? "catalogHeaderHeader" : "",
            },
            [
              _c("div", { staticClass: "catalog-img catalog-img-left" }),
              _vm._v(" "),
              _c("div", { staticClass: "catalog-title" }, [_vm._v("教材目录")]),
              _vm._v(" "),
              _c("div", { staticClass: "catalog-img catalog-img-right" }),
            ]
          )
        : _vm._e(),
      _vm._v(" "),
      !_vm.sysEnv.isNoteHighlight
        ? _c(
            "div",
            {
              staticClass: "text-book-name",
              class: _vm.userType == "teacher" ? "textbookNameTeacer" : "",
            },
            [
              _c("span", [
                _vm._v(
                  _vm._s(_vm.textBookName ? _vm.textBookName : "未选择教材")
                ),
              ]),
            ]
          )
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "catalog-content",
          class: _vm.userType == "teacher" ? "catalogContentTeacher" : "",
        },
        [
          _vm.currentNodeKey != ""
            ? _c("el-tree", {
                ref: "tree",
                attrs: {
                  id: "preview-tree",
                  "default-expand-all": true,
                  data: _vm.catalogLists,
                  props: _vm.defaultProps,
                  "highlight-current": _vm.PreviewCatalog,
                  indent: 24,
                  draggable: false,
                  "node-key": "id",
                  "filter-node-method": _vm.filterNode,
                  "expand-on-click-node": false,
                  "current-node-key": _vm.currentNodeKey,
                },
                on: { "node-click": _vm.nodeclick },
                scopedSlots: _vm._u(
                  [
                    {
                      key: "default",
                      fn: function (ref) {
                        var node = ref.node;
                        var data = ref.data;
                        return _c(
                          "span",
                          { staticStyle: { width: "100%" } },
                          [
                            [
                              _c(
                                "div",
                                {
                                  staticClass: "catalog-node__right",
                                  on: {
                                    click: function ($event) {
                                      $event.stopPropagation();
                                      return function () {
                                        return _vm.expandOrCollapseNode(
                                          node,
                                          data
                                        )
                                      }.apply(null, arguments)
                                    },
                                  },
                                },
                                [
                                  node.childNodes.length && node.expanded
                                    ? _c("i", {
                                        staticClass: "el-icon-arrow-down",
                                        staticStyle: {
                                          "font-size": "16px",
                                          "font-weight": "600",
                                        },
                                      })
                                    : node.childNodes.length && !node.expanded
                                    ? _c("i", {
                                        staticClass: "el-icon-arrow-right",
                                        staticStyle: {
                                          "font-size": "16px",
                                          "font-weight": "600",
                                        },
                                      })
                                    : _vm._e(),
                                ]
                              ),
                              _vm._v(" "),
                              _c("span", {
                                staticClass: "custom-tree-node",
                                attrs: { title: node.label },
                                domProps: { textContent: _vm._s(node.label) },
                              }),
                              _vm._v(" "),
                              _vm.isTrialReading(data.id)
                                ? _c("div", {
                                    staticClass: "exceeding_the_trial_reading",
                                  })
                                : _vm._e(),
                            ],
                          ],
                          2
                        )
                      },
                    },
                  ],
                  null,
                  false,
                  3452408602
                ),
              })
            : _vm._e(),
        ],
        1
      ),
    ]
  )
};
var __vue_staticRenderFns__$2u = [];
__vue_render__$2u._withStripped = true;

  /* style */
  const __vue_inject_styles__$2u = undefined;
  /* scoped */
  const __vue_scope_id__$2u = "data-v-d1b4ea06";
  /* module identifier */
  const __vue_module_identifier__$2u = undefined;
  /* functional template */
  const __vue_is_functional_template__$2u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2u, staticRenderFns: __vue_staticRenderFns__$2u },
    __vue_inject_styles__$2u,
    __vue_script__$2u,
    __vue_scope_id__$2u,
    __vue_is_functional_template__$2u,
    __vue_module_identifier__$2u,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2t = {
  name: 'xmlEmpty'
};

/* script */
const __vue_script__$2t = script$2t;

/* template */
var __vue_render__$2t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-empty-content" }, [
    _c(
      "svg",
      {
        staticClass: "icon",
        attrs: {
          t: "1712834383184",
          viewBox: "0 0 1238 1024",
          version: "1.1",
          xmlns: "http://www.w3.org/2000/svg",
          "p-id": "2473",
          "xmlns:xlink": "http://www.w3.org/1999/xlink",
          width: "154.75",
          height: "128",
        },
      },
      [
        _c("path", {
          attrs: {
            d: "M23.81001 23.81h904.93a23.81 23.81 0 0 1 23.82 23.82v940.65a23.81 23.81 0 0 1-23.82 23.81H23.81001A23.81 23.81 0 0 1 0.00001 988.28V47.63A23.81 23.81 0 0 1 23.81001 23.8z m95.26 95.26a23.81 23.81 0 0 0-23.81 23.81v750.14a23.81 23.81 0 0 0 23.81 23.82h714.42a23.81 23.81 0 0 0 23.81-23.82V142.88a23.81 23.81 0 0 0-23.81-23.81H119.07001z",
            fill: "#ECF0F9",
            "p-id": "2474",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M190.51001 285.77h571.54v59.53H190.50001v-59.53z m0 142.88h571.54v59.54H190.50001v-59.54z m0 142.88h404.84v59.54H190.50001v-59.54z",
            fill: "#ECF0F9",
            "p-id": "2475",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M309.58001 0h333.4a23.81 23.81 0 0 1 23.81 23.81v83.35a23.81 23.81 0 0 1-23.81 23.82h-333.4a23.81 23.81 0 0 1-23.81-23.82V23.81A23.81 23.81 0 0 1 309.58001 0z",
            fill: "#E0E4ED",
            "p-id": "2476",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M995.98001 357.44c-5.13 10.8-6.46 23.04-3.99 36.73 3.73 20.54 15.16 35.14 28.1 35.14l0.92 17.76c13.4 3.99 24.96 1.67 34.7-6.98-5.61-12.37-7.88-23.83-6.8-34.4l8.36-7.03 1.88-18.82-11.22-4.76-13.21-28.73-38.73 11.07z",
            fill: "#FFDCBC",
            "p-id": "2477",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M883.63001 965.52a142.88 47.63 0 1 0 285.77 0 142.88 47.63 0 1 0-285.77 0z",
            fill: "#E2E6EF",
            "p-id": "2478",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M1006.22001 909.48c-1.82 12.8-8.83 23.06-21.04 30.81-18.31 11.63-7.91 19.98 5.52 19.98 13.44 0 47.5-14.02 49.89-25.4 1.58-7.57-1.58-17.2-9.45-28.88l-24.92 3.49z",
            fill: "#416396",
            "p-id": "2479",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M1036.86001 926.15c-32.34 23.38-40.82 38.89-25.43 46.5 15.37 7.62 34.5-0.14 57.38-23.25 5.89-7.7 3.02-16.76-8.61-27.2-8.48-2.93-16.26-1.61-23.34 3.95z",
            fill: "#466DA6",
            "p-id": "2480",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M992.15001 360.3c7.73-19.88 21.82-28.74 42.28-26.6 20.47 2.15 32.03 14.98 34.66 38.49-0.43 17.35-6.84 30.22-19.23 38.61-2.02-2.49-1.87-4.76 0.47-6.86 3.04-2.72 9.22-10.14 6.25-17.1-1.98-4.61-5.05-5.91-9.2-3.86 1.18-12.46-5-20.9-18.53-25.34-13.55-4.43-25.78-3.55-36.7 2.67z",
            fill: "#58626B",
            "p-id": "2481",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M1085.63001 670.67l-16.97 260.96c-12.02 8.57-22.86 7.84-32.53-2.15l-11.05-218.81-11.3 103.91 18.4 95.37c-5.31 7.2-13.9 9.34-25.74 6.41l-39.46-105.03 4.86-150.5 113.8 9.84z",
            fill: "#2274FF",
            "p-id": "2482",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M1102.20001 513.96c15.71 26.9 29.17 38.96 40.36 36.23 16.79-4.11 51.67-60.17 61.21-66.28 5.96-3.8 18.39-3.95 33.48-1.49 1.43 0 1.7-0.68 0.8-1.99-20.12-17.83-35.08-22.14-44.89-12.91a4921.51 4921.51 0 0 1-50.6 46.44l-17-26.49-23.38 26.5z m-108.52-2.35c-15.71 34.82-29.17 50.88-40.36 48.14-16.79-4.11-51.68-60.17-61.21-66.28-5.96-3.8-18.39-15.87-33.49-13.4-1.42-0.02-1.7-0.68-0.8-1.98 20.13-9.9 35.08-10.24 44.9-1.03 9.82 9.22 26.68 24.71 50.6 46.45l16.98-26.5 23.38 14.6z",
            fill: "#FFDCBC",
            "p-id": "2483",
          },
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M1023.64001 445.38c11.87 3.05 23.4 0.71 34.6-7 19.41-2.35 32.8 1.08 40.14 10.29a938.75 938.75 0 0 0 28.73 33.88c-3.8 18.21-13.38 29.93-28.73 35.17l-7.34-7.43c3.34 98.59 2.23 153.57-3.34 164.93-47.95 3.1-87.28-0.51-117.95-10.79l14.74-149.92-22.8-19.58L990.27001 454c5.69-6.34 10.9-10.13 15.64-11.36 4.74-1.22 10.66-0.32 17.74 2.74z",
            fill: "#F6F8FC",
            "p-id": "2484",
          },
        }),
      ]
    ),
  ])
};
var __vue_staticRenderFns__$2t = [];
__vue_render__$2t._withStripped = true;

  /* style */
  const __vue_inject_styles__$2t = undefined;
  /* scoped */
  const __vue_scope_id__$2t = undefined;
  /* module identifier */
  const __vue_module_identifier__$2t = undefined;
  /* functional template */
  const __vue_is_functional_template__$2t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2t, staticRenderFns: __vue_staticRenderFns__$2t },
    __vue_inject_styles__$2t,
    __vue_script__$2t,
    __vue_scope_id__$2t,
    __vue_is_functional_template__$2t,
    __vue_module_identifier__$2t,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2s = {
  name: 'BookMarkStudent',
  components: {
    xmlResourceListPc: __vue_component__$2F,
    xmlEmpty: __vue_component__$2t
  },
  props: {
    bookmarkList: [],
    location: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      launchValue: ''
    };
  },
  watch: {
    bookmarkList() {
      if (this.launchValue == '' || JSON.stringify(this.bookmarkList).indexOf(this.launchValue) == -1) {
        this.launchValue = this.bookmarkList[0]?.firstLevelId;
      }
    },
    settingData: {
      handler(newValue) {
        this.form = {
          ...this.form,
          ...newValue
        };
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.launchValue = this.bookmarkList[0]?.firstLevelId;
  },
  methods: {
    deleteBookmarkItem(item) {
      this.$confirm(`确认删除此条书签吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        duration: 1000
      }).then(() => {
        this.$EventBus.$emit('deleteBookmark', item);
      });
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    }
  }
};

var __$_require_static_images_icons_resource_student_bookmark_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAAAXNSR0IArs4c6QAAASRJREFUWEftmO1twkAQRGc6SDqghLRAJYQOSAUhHZAO6CDpAKgESqCDCSPZEjH49pDMl7T316u757fns8fEgw0+GA8SKOpI0ZCkTwAzAC/RRJXX9wCWJD/66nuBGph55UKXln2RPDt3CWh7uJvRpStV1u9Jvp6rLQGpcvKaMrfqX9t5UHRPoDGAdwCTFiKBOu1IQ9HGTkNPacjn0FtDviO5vOs5RHIdafT1W53U4wQK2pGGov2ahtJQn4Fd5WfuTfbQN8mZJKcSp5NSMrkqkK1Mj09eSQ4Dq4KtqwH9NjD+aD8ZkhxtbKs7BgcygLPUInqcJPmN/tOxNSjQxomBpFtVNZoW2labMgYBclD0xg2t9FFKcvRxCw1UdUP59yPqeRqKDP0BRLexJdlzdwMAAAAASUVORK5CYII=";

/* script */
const __vue_script__$2s = script$2s;
var __vue_render__$2s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "bookm-mark-student demo-collapse" },
    [
      _vm.bookmarkList.length == 0
        ? _c("xmlEmpty", { staticClass: "xml-empty" })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "el-collapse",
        {
          staticClass: "pcbookmarkList",
          attrs: { accordion: "" },
          model: {
            value: _vm.launchValue,
            callback: function ($$v) {
              _vm.launchValue = $$v;
            },
            expression: "launchValue",
          },
        },
        _vm._l(_vm.bookmarkList, function (item) {
          return _c(
            "el-collapse-item",
            { key: item.firstLevelId, attrs: { name: item.firstLevelId } },
            [
              _c("template", { slot: "title" }, [
                _c("div", { staticClass: "collapse-title" }, [
                  _vm._v(
                    "\n          " + _vm._s(item.firstLevelLabel) + "\n        "
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "resource-list" }, [
                _c(
                  "div",
                  { staticStyle: { width: "100%" } },
                  [
                    _vm._l(item.list, function (items, index) {
                      return _c(
                        "div",
                        { key: index, staticClass: "xml-teacher-note" },
                        [
                          _c(
                            "div",
                            { staticClass: "xml-note-header delete-btn" },
                            [
                              _c("img", {
                                staticClass: "note-icon",
                                attrs: {
                                  src: __$_require_static_images_icons_resource_student_bookmark_png__,
                                  alt: "",
                                },
                              }),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "delete-btn-item",
                                on: {
                                  click: function ($event) {
                                    return _vm.deleteBookmarkItem(items)
                                  },
                                },
                              }),
                            ]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              staticClass: "note-content",
                              attrs: { title: items.bookmarkText },
                              on: {
                                click: function ($event) {
                                  return _vm.$emit("resourceDetails", items)
                                },
                              },
                            },
                            [
                              _vm._v(
                                "\n                " +
                                  _vm._s(_vm.removeStyle(items.bookmarkText)) +
                                  "\n              "
                              ),
                            ]
                          ),
                        ]
                      )
                    }),
                  ],
                  2
                ),
              ]),
            ],
            2
          )
        }),
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2s = [];
__vue_render__$2s._withStripped = true;

  /* style */
  const __vue_inject_styles__$2s = undefined;
  /* scoped */
  const __vue_scope_id__$2s = undefined;
  /* module identifier */
  const __vue_module_identifier__$2s = undefined;
  /* functional template */
  const __vue_is_functional_template__$2s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2s, staticRenderFns: __vue_staticRenderFns__$2s },
    __vue_inject_styles__$2s,
    __vue_script__$2s,
    __vue_scope_id__$2s,
    __vue_is_functional_template__$2s,
    __vue_module_identifier__$2s,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2r = {
  name: 'nodeStudent',
  components: {
    xmlResourceListPc: __vue_component__$2F,
    xmlEmpty: __vue_component__$2t
  },
  props: {
    nodesList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      show: false,
      expansion: false,
      isContentLong: false,
      isMasked: false,
      exoabsionShowFalse: false,
      exoabsionShow: true,
      launchValue: ''
    };
  },
  watch: {
    nodesList() {
      if (this.launchValue == '' || JSON.stringify(this.nodesList).indexOf(this.launchValue) == -1) {
        this.launchValue = this.nodesList[0]?.firstLevelId;
      }
    },
    settingData: {
      handler(newValue) {
        this.form = {
          ...this.form,
          ...newValue
        };
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.launchValue = this.nodesList[0]?.firstLevelId;
  },
  methods: {
    toggleExpansion(e) {
      this.expansion = !this.expansion;
      //  e= e.noteContent.split('')
      e = e.split('');
      if (e.length >= 151) {
        this.exoabsionShow = true;
      }
    },
    deleteItem(items) {
      // 删除提示
      this.$confirm(`确认删除此条笔记吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        duration: 1000
      }).then(() => {
        this.$EventBus.$emit('deleteNote', items, () => {});
      }).catch(() => {});
    },
    editNoteStudent(items) {
      this.$EventBus.$emit('editStudentNote', items);
    }
  },
  created() {}
};

/* script */
const __vue_script__$2r = script$2r;
var __vue_render__$2r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "node-student demo-collapse" },
    [
      _vm.nodesList.length == 0
        ? _c("xmlEmpty", { staticClass: "xml-empty" })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "el-collapse",
        {
          staticClass: "pcnotelist",
          attrs: { accordion: "" },
          model: {
            value: _vm.launchValue,
            callback: function ($$v) {
              _vm.launchValue = $$v;
            },
            expression: "launchValue",
          },
        },
        _vm._l(_vm.nodesList, function (item) {
          return _c(
            "el-collapse-item",
            { key: item.firstLevelId, attrs: { name: item.firstLevelId } },
            [
              _c("template", { slot: "title" }, [
                _c("div", { staticClass: "collapse-title" }, [
                  _vm._v(
                    "\n          " + _vm._s(item.firstLevelLabel) + "\n        "
                  ),
                ]),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "resource-list" }, [
                _c(
                  "div",
                  { staticStyle: { width: "100%" } },
                  [
                    _vm._l(item.list, function (items, index) {
                      return _c(
                        "div",
                        { key: index, staticClass: "xml-teacher-note" },
                        [
                          _c(
                            "div",
                            { staticClass: "xml-note-header delete-btn" },
                            [
                              _c("img", {
                                staticClass: "note-icon",
                                attrs: {
                                  src: __$_require_static_images_icons_resource_note_icon_svg__,
                                  alt: "",
                                },
                              }),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "delete-btn-item",
                                on: {
                                  click: function ($event) {
                                    return _vm.deleteItem(items)
                                  },
                                },
                              }),
                            ]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              staticClass:
                                "note-content note-content-studentnode",
                              class: _vm.expansion
                                ? "note-content-studentnodeFlex"
                                : "",
                              on: {
                                click: function ($event) {
                                  $event.stopPropagation();
                                  return _vm.$emit("resourceDetails", items)
                                },
                              },
                            },
                            [
                              _c("xmlTextOverflow", {
                                attrs: { info: items.noteContent },
                              }),
                            ],
                            1
                          ),
                        ]
                      )
                    }),
                  ],
                  2
                ),
              ]),
            ],
            2
          )
        }),
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2r = [];
__vue_render__$2r._withStripped = true;

  /* style */
  const __vue_inject_styles__$2r = undefined;
  /* scoped */
  const __vue_scope_id__$2r = undefined;
  /* module identifier */
  const __vue_module_identifier__$2r = undefined;
  /* functional template */
  const __vue_is_functional_template__$2r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2r, staticRenderFns: __vue_staticRenderFns__$2r },
    __vue_inject_styles__$2r,
    __vue_script__$2r,
    __vue_scope_id__$2r,
    __vue_is_functional_template__$2r,
    __vue_module_identifier__$2r,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2q = {
  name: 'highlightStudent',
  components: {
    xmlResourceListPc: __vue_component__$2F,
    xmlEmpty: __vue_component__$2t
  },
  props: {
    highlightList: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'highlight'
    }
  },
  data() {
    return {
      launchValue: ''
    };
  },
  watch: {
    highlightList() {
      if (this.launchValue == '' || JSON.stringify(this.highlightList).indexOf(this.launchValue) == -1) {
        this.launchValue = this.highlightList[0]?.firstLevelId;
      }
    },
    settingData: {
      handler(newValue) {
        this.form = {
          ...this.form,
          ...newValue
        };
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.launchValue = this.highlightList[0]?.firstLevelId;
  },
  methods: {
    deleteHighlightItem(items) {
      this.$confirm(`确认删除此条${this.type == 'highlight' ? '高亮' : '划线'}吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        duration: 1000
      }).then(() => {
        this.$EventBus.$emit(this.type == 'highlight' ? 'deleteHighlight' : 'deleteLink', items, () => {});
      }).catch(() => {});
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    }
  },
  created() {}
};

var __$_require_static_images_icons_resource_student_lighheight_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAbCAYAAACN1PRVAAAAAXNSR0IArs4c6QAAAihJREFUSEvNlkuIjXEYxn9PriU2QogpERsjdlIWrinZWIiysJCFIlFmIRvXmrJwSUlZkCRRKJSFslBDLhuXhbKSGU1pWLgMj/9b36nPzPedOedwvpw6m3Pe8/7+7/t/nud8osKXKmTxf8NsjwKmAr2SfjWzmaYns90BvAY6JPVVAXsLzKgCNgt4DiyQ9LHdk8V9rQHuSOpvC8x2J3AemA+MAX4Aj4Ddkt4MhdqOmkFJrn33h0BsTwLuZU0uAi8lfbO9D+gGbidhXADeZ9A9wCJgo6TrtscCsebjISBglaSBMtjoKAB2AiujCTAXOAmsk3S3YIIu4BiwCfiQ4DeAm0lA54AeST8LYbUPbY8DpgCTgQfACUmHyu7Hdky8OEGWZDX9kgaH1tf1me047ZW0lvGxzjqwAPWkK1gq6UlZ3UiwXUkMRyRNrKc62xOAL8AGSbdahW0GLksa6VBxr5EqMdnjVmHzgFhLt6TDddYYYgjlzam3gdIT214WPgFWAEeB1ZLuF6hxfyb1MPqnLDOvFUGHwTKvnQK2JEWeAfYCB7N3SDp89g6IKbalydfnfLY158MDyTaX8v8MRbBovD0gkq7m7LAWOA1MyxLke/LWK2CHpBe5ukiOs0BEWVdpgsQPbIe/Pkv6WrQK27OB5ZE0VQTx9JQuDzPltT31ZwLPgIWSetuS+rk7CdhToLMKWO0ZpC8fso1M2PQzSCNNW0qQv2nckKn/NSDf7zfsx8Uc87Rk2QAAAABJRU5ErkJggg==";

/* script */
const __vue_script__$2q = script$2q;
var __vue_render__$2q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "highlight-student demo-collapse" },
    [
      _vm.highlightList.length == 0
        ? _c("xmlEmpty", { staticClass: "xml-empty" })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "el-collapse",
        {
          staticClass: "pcstudenthighlight",
          attrs: { accordion: "" },
          model: {
            value: _vm.launchValue,
            callback: function ($$v) {
              _vm.launchValue = $$v;
            },
            expression: "launchValue",
          },
        },
        _vm._l(_vm.highlightList, function (item) {
          return _c(
            "el-collapse-item",
            { key: item.firstLevelId, attrs: { name: item.firstLevelId } },
            [
              _c("template", { slot: "title" }, [
                _c("div", { staticClass: "collapse-title" }, [
                  _vm._v(
                    "\n          " + _vm._s(item.firstLevelLabel) + "\n        "
                  ),
                ]),
              ]),
              _vm._v(" "),
              _vm._l(item.list, function (items, index) {
                return _c("div", { key: index, staticClass: "resource-list" }, [
                  _c(
                    "div",
                    { staticStyle: { width: "100%" } },
                    [
                      [
                        _c("div", { staticClass: "xml-teacher-note" }, [
                          _c(
                            "div",
                            { staticClass: "xml-note-header delete-btn" },
                            [
                              _c("img", {
                                staticClass: "note-icon",
                                attrs: {
                                  src: __$_require_static_images_icons_resource_student_lighheight_png__,
                                  alt: "",
                                },
                              }),
                              _vm._v(" "),
                              _c("div", {
                                staticClass: "delete-btn-item",
                                on: {
                                  click: function ($event) {
                                    return _vm.deleteHighlightItem(items)
                                  },
                                },
                              }),
                            ]
                          ),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              staticClass: "note-content",
                              attrs: { title: items.selectionText },
                              on: {
                                click: function ($event) {
                                  return _vm.$emit("resourceDetails", items)
                                },
                              },
                            },
                            [
                              _c("xmlTextOverflow", {
                                attrs: {
                                  info: _vm.removeStyle(items.selectionText),
                                },
                              }),
                            ],
                            1
                          ),
                        ]),
                      ],
                    ],
                    2
                  ),
                ])
              }),
            ],
            2
          )
        }),
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2q = [];
__vue_render__$2q._withStripped = true;

  /* style */
  const __vue_inject_styles__$2q = undefined;
  /* scoped */
  const __vue_scope_id__$2q = undefined;
  /* module identifier */
  const __vue_module_identifier__$2q = undefined;
  /* functional template */
  const __vue_is_functional_template__$2q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2q, staticRenderFns: __vue_staticRenderFns__$2q },
    __vue_inject_styles__$2q,
    __vue_script__$2q,
    __vue_scope_id__$2q,
    __vue_is_functional_template__$2q,
    __vue_module_identifier__$2q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2p = {
  name: 'PreviewLeftStudent',
  data() {
    return {
      GkActiveName: 'first',
      activeName: 'fourth',
      data: {
        notes: {
          list: []
        },
        bookmark: {
          list: []
        },
        highlight: {
          list: []
        },
        resources: {
          list: []
        }
      },
      businessType: 'textBook',
      selectId: '',
      PreviewCatalog: false,
      textBookList: [],
      scrollTop: 0,
      catalogLists: [],
      suptablist: [{
        name: '目录'
      }, {
        name: '资源'
      }],
      cursuptab: 0
    };
  },
  components: {
    PreviewCatalog: __vue_component__$2u,
    BookMarkStudent: __vue_component__$2s,
    nodeStudent: __vue_component__$2r,
    highlightStudent: __vue_component__$2q
  },
  props: {
    textBookId: String,
    catalogList: Array,
    location: {
      type: String,
      default: ''
    },
    deleteNote: {
      type: Function,
      default: () => {}
    },
    editNote: {
      type: Function,
      default: () => {}
    },
    deleteBookmark: {
      type: Function,
      default: () => {}
    },
    deleteHighlight: {
      type: Function,
      default: () => {}
    },
    sysEnv: {
      type: Object,
      default: () => {}
    },
    userType: {
      type: String,
      default: 'student'
    },
    pageType: {
      //页面类型：取值h5、pc、pad
      type: String,
      default: 'h5'
    },
    styleType: {
      //阅读风格
      type: String,
      default: 'A'
    },
    textBookId: {
      //数字教材Id
      type: String,
      default: ''
    },
    checkedId: {
      type: String,
      default: ''
    },
    looseLeafData: Object,
    //所有活页信息
    resourcesList: {
      type: Array,
      default: () => []
    },
    textBookName: {
      type: String,
      default: ''
    },
    notesList: {
      type: Array,
      default: () => []
    },
    highlightList: {
      type: Array,
      default: () => []
    },
    linkList: {
      type: Array,
      default: () => []
    },
    bookmarkList: {
      type: Array,
      default: () => []
    },
    componentList: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    teacherResourcesList() {
      this.textBookResource.teachingResourceTotal = 0;
      let list = this.resourcesList;
      list.sort(this.sortBy('chapterOrderNum', 1));
      const map = [];
      list.forEach(e => {
        if (e.type == 'note__teacher') {
          this.teacherNoteList.push(e);
        }
        if (e.isStudentShow != 1 || this.userType == 'teacher') {
          this.textBookResource.teachingResourceTotal += 1;
          const findObj = map.find(innerTmp => innerTmp.firstLevelId === e.firstLevelId);
          if (findObj) {
            findObj.list.push(e);
          } else {
            map.push({
              firstLevelId: e.firstLevelId,
              firstLevelLabel: e.firstLevelLabel,
              chapterOrderNum: e.chapterOrderNum,
              list: [e]
            });
          }
        }
      });
      return map;
    }
  },
  watch: {
    catalogList: {
      handler(newValue) {
        if (this.catalogLists.length > 0) {
          this.currentNodeKey = this.catalogLists[0].id;
        }
      },
      immediate: true
    }
  },
  mounted() {
    const origin = this.catalogList;
    const filterMenu = menuList => {
      return menuList.filter(item => {
        return item.hiddenFlag !== '1';
      }).map(item => {
        item = Object.assign({}, item);
        if (item.children?.length) {
          item.children = filterMenu(item.children);
        }
        return item;
      });
    };
    this.catalogLists = filterMenu(origin), setTimeout(() => {
      if (this.catalogLists.length > 0) {
        this.currentNodeKey = this.catalogLists[0].id;
      }
    }, 500);
    this.$nextTick(() => {
      let height = document.getElementById('textBookName').clientHeight;
      let Dom = document.getElementById('preview-left-student').getElementsByClassName('el-tabs__header');
      if (Dom) {
        Dom[0].style.top = height + 'px';
      }
      console.log(height, Dom);
    });
  },
  methods: {
    // 目录跳转指定章节
    catalogClick(obj) {
      this.$emit('onNodeClick', obj);
    },
    resourceDetails(items, maxTop) {
      console.log('🚀 ~ resourceDetails ~ items:', items);
      let list = items.location.split('/');
      let selectorId = `${list[2]}_#_${items.markEfiCode}`;
      let data = {
        location: items.location,
        maxTop: maxTop ? maxTop : -30,
        type: 'marker',
        selectorType: 'id',
        selectorId,
        markTarget: items,
        classIndex: 0
      };
      this.$emit('resourceDetails', data);
    }
  }
};

/* script */
const __vue_script__$2p = script$2p;
/* template */
var __vue_render__$2p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "preview-left-student",
      attrs: { id: "preview-left-student" },
    },
    [
      _vm.sysEnv.isGuoKai
        ? [
            _c(
              "div",
              { staticClass: "text-book-name", attrs: { id: "textBookName" } },
              [
                _c("span", [
                  _vm._v(
                    _vm._s(_vm.textBookName ? _vm.textBookName : "未选择教材")
                  ),
                ]),
              ]
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "sup-tabs" },
              _vm._l(_vm.suptablist, function (item, index) {
                return _c(
                  "div",
                  {
                    key: index,
                    class: _vm.cursuptab == index ? "sup-tabs-active" : "",
                    on: {
                      click: function ($event) {
                        _vm.cursuptab = index;
                      },
                    },
                  },
                  [
                    _vm.cursuptab == index
                      ? _c("div", { staticClass: "left-icon" })
                      : _vm._e(),
                    _vm._v(" "),
                    _c("span", [_vm._v(_vm._s(item.name))]),
                    _vm._v(" "),
                    _vm.cursuptab == index
                      ? _c("div", { staticClass: "right-icon" })
                      : _vm._e(),
                  ]
                )
              }),
              0
            ),
            _vm._v(" "),
            _vm.cursuptab == 0
              ? _c(
                  "div",
                  [
                    _vm.businessType === "textBook"
                      ? _c("xml-preview-catalog", {
                          ref: "xmlPreviewCatalog",
                          staticClass:
                            "pc-catalog pc-catalog--render pc-student-catalog pc-student-left",
                          attrs: {
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.catalogList,
                            userType: _vm.userType,
                            notesList: _vm.notesList,
                            checkedId: _vm.checkedId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            textBookName: _vm.textBookName,
                            sysEnv: _vm.sysEnv,
                            componentList: _vm.componentList,
                          },
                          on: { onNodeClick: _vm.catalogClick },
                        })
                      : _vm._e(),
                  ],
                  1
                )
              : _c(
                  "div",
                  [
                    _vm.userType == "student"
                      ? _c(
                          "el-tabs",
                          {
                            model: {
                              value: _vm.GkActiveName,
                              callback: function ($$v) {
                                _vm.GkActiveName = $$v;
                              },
                              expression: "GkActiveName",
                            },
                          },
                          [
                            _c(
                              "el-tab-pane",
                              { attrs: { label: "书签", name: "first" } },
                              [
                                _c("book-mark-student", {
                                  attrs: { bookmarkList: _vm.bookmarkList },
                                  on: {
                                    deleteBookmark: _vm.deleteBookmark,
                                    resourceDetails: function (e) {
                                      return _vm.resourceDetails(e, 0)
                                    },
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c(
                              "el-tab-pane",
                              { attrs: { label: "笔记", name: "second" } },
                              [
                                _c("node-student", {
                                  attrs: { nodesList: _vm.notesList },
                                  on: {
                                    deleteNote: _vm.deleteNote,
                                    editNote: function ($event) {
                                      _vm.$emit("editNote", function (e) {
                                        return e
                                      });
                                    },
                                    resourceDetails: _vm.resourceDetails,
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c(
                              "el-tab-pane",
                              { attrs: { label: "划线", name: "four" } },
                              [
                                _c("highlight-student", {
                                  attrs: {
                                    type: "link",
                                    highlightList: _vm.linkList,
                                  },
                                  on: {
                                    deleteHighlight: _vm.deleteHighlight,
                                    resourceDetails: _vm.resourceDetails,
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c(
                              "el-tab-pane",
                              { attrs: { label: "高亮", name: "third" } },
                              [
                                _c("highlight-student", {
                                  attrs: { highlightList: _vm.highlightList },
                                  on: {
                                    deleteHighlight: _vm.deleteHighlight,
                                    resourceDetails: _vm.resourceDetails,
                                  },
                                }),
                              ],
                              1
                            ),
                          ],
                          1
                        )
                      : _vm._e(),
                  ],
                  1
                ),
          ]
        : [
            _c(
              "div",
              { staticClass: "text-book-name", attrs: { id: "textBookName" } },
              [
                _c("span", [
                  _vm._v(
                    _vm._s(_vm.textBookName ? _vm.textBookName : "未选择教材")
                  ),
                ]),
              ]
            ),
            _vm._v(" "),
            _c(
              "el-tabs",
              {
                staticClass: "demo-tabs Preview-left-student",
                attrs: {
                  type: _vm.sysEnv.isHuaTeng ? "card" : "",
                  userType: _vm.userType,
                },
                model: {
                  value: _vm.activeName,
                  callback: function ($$v) {
                    _vm.activeName = $$v;
                  },
                  expression: "activeName",
                },
              },
              [
                _c(
                  "el-tab-pane",
                  { attrs: { label: "目录", name: "fourth" } },
                  [
                    _vm.businessType === "textBook"
                      ? _c("xml-preview-catalog", {
                          ref: "xmlPreviewCatalog",
                          staticClass:
                            "pc-catalog pc-catalog--render pc-student-catalog pc-student-left",
                          attrs: {
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.catalogList,
                            userType: _vm.userType,
                            notesList: _vm.notesList,
                            checkedId: _vm.checkedId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            textBookName: _vm.textBookName,
                            sysEnv: _vm.sysEnv,
                            componentList: _vm.componentList,
                          },
                          on: { onNodeClick: _vm.catalogClick },
                        })
                      : _vm._e(),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "el-tab-pane",
                  { attrs: { label: "书签", name: "first" } },
                  [
                    _c("book-mark-student", {
                      attrs: { bookmarkList: _vm.bookmarkList },
                      on: {
                        deleteBookmark: _vm.deleteBookmark,
                        resourceDetails: function (e) {
                          return _vm.resourceDetails(e, 0)
                        },
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "el-tab-pane",
                  { attrs: { label: "笔记", name: "second" } },
                  [
                    _c("node-student", {
                      attrs: { nodesList: _vm.notesList },
                      on: {
                        deleteNote: _vm.deleteNote,
                        editNote: function ($event) {
                          _vm.$emit("editNote", function (e) {
                            return e
                          });
                        },
                        resourceDetails: _vm.resourceDetails,
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "el-tab-pane",
                  { attrs: { label: "高亮", name: "third" } },
                  [
                    _c("highlight-student", {
                      attrs: { highlightList: _vm.highlightList },
                      on: {
                        deleteHighlight: _vm.deleteHighlight,
                        resourceDetails: _vm.resourceDetails,
                      },
                    }),
                  ],
                  1
                ),
              ],
              1
            ),
          ],
    ],
    2
  )
};
var __vue_staticRenderFns__$2p = [];
__vue_render__$2p._withStripped = true;

  /* style */
  const __vue_inject_styles__$2p = undefined;
  /* scoped */
  const __vue_scope_id__$2p = undefined;
  /* module identifier */
  const __vue_module_identifier__$2p = undefined;
  /* functional template */
  const __vue_is_functional_template__$2p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2p, staticRenderFns: __vue_staticRenderFns__$2p },
    __vue_inject_styles__$2p,
    __vue_script__$2p,
    __vue_scope_id__$2p,
    __vue_is_functional_template__$2p,
    __vue_module_identifier__$2p,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2o = {
  name: 'xmlStatisticalChart',
  props: {
    pageType: {
      type: String,
      default: 'h5'
    },
    userType: {
      type: String,
      default: 'teacher'
    },
    teacherDate: {
      type: Object,
      default: () => {}
    },
    studentDate: {
      type: Object,
      default: () => {}
    },
    userInfoparms: {
      type: Object,
      default: () => {}
    },
    teacherDateclass: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    teacherDate() {
      console.log('切换班级：teacherDate数据变动');
      if (this.teacherDate.studentList?.length > 0) {
        this.total = this.teacherDate.studentList.length;
        this.select_item = this.teacherDate.studentList[0] || {};
        this.tableData = this.teacherDate.studentList.slice(this.page * 7, 7) || [];
      } else {
        this.total = 0;
        this.select_item = {};
        this.tableData = [];
      }
    },
    teacherDateclass() {
      if (this.teacherDateclass.length > 0) {
        this.classValue = this.teacherDateclass[0].title;
      } else {
        this.classValue = '';
      }
    }
  },
  mounted() {
    if (this.teacherDate?.studentList?.length > 0) {
      this.total = this.teacherDate.studentList.length;
      this.select_item = this.teacherDate.studentList[0] || {};
      this.tableData = this.teacherDate.studentList.slice(this.page * 7, 7);
    }
    if (this.teacherDateclass.length > 0) {
      this.classValue = this.teacherDateclass[0].title;
    }
  },
  data() {
    return {
      dialogVisible: false,
      select_index: '',
      select_item: {},
      visible: false,
      tableData: [],
      total: 0,
      page: 0,
      size: 7,
      classValue: ''
      // studentDate: {
      //   name: 'xxx',//名字
      //   class: '一班',//班级
      //   fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //   learningProgress: '11',//学习进度
      //   learningNumber: '22',//学习次数
      //   learningTime: '333',//学习时长
      //   studySituation: {
      //     learning: '0.5',//本次学习情况
      //     picture: '25',//图片学习情况
      //     audio: '67h/25',//音频学习情况
      //     video: '67h/25',//视频学习情况
      //     answersNumber: '25',//答题数量
      //     noteNumber: '67',//笔记数量
      //     bookmarkNumber: '67',//书签数量
      //     lineationNumber: '67'//划线数量
      //   }
      // },
      // teacherDate: {
      //   name: '11', //教师名字
      //   class: '99班',//班级
      //   fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //   studentNumber: '9999',//学员数量
      //   schoolingTime: '9999',//授课时长
      //   schoolingNumber: '9999',//授课次数
      //   classstudent: [
      //     {
      //       name: '1',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '111',//学习进度
      //       learningNumber: '111',//学习次数
      //       learningTime: '1111',//学习时长
      //       answerNumber: '1111',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }, {
      //       name: '2',//学员名字
      //       class: '一班',//班级
      //       fileUrl: 'https://fuss10.elemecdn.com/e/5d/4a731a90594a4af544c0c25941171jpeg.jpeg',//头像
      //       learningProgress: '222',//学习进度
      //       learningNumber: '222',//学习次数
      //       learningTime: '2222',//学习时长
      //       answerNumber: '222',//答题完成次数
      //     }]//班级学员
      // },
    };
  },
  methods: {
    open() {
      this.dialogVisible = true;
    },
    getbox(item, index) {
      this.select_index = index;
      this.select_item = item || {};
    },
    //分页
    gettableData(type) {
      if (type == 'next' && this.total > this.page + 1) {
        this.page++;
      } else if (type == 'up' && this.page > 0) {
        this.page--;
      }
      this.tableData = this.teacherDate.studentList.slice(this.page * 7, (this.page + 1) * 7);
      this.select_item = this.tableData[0] || {};
      this.select_index = 0;
      // let data = JSON.parse(JSON.stringify(this.teacherDate.studentList))
      // this.tableData = data.splice((this.page - 1) * this.size, this.size)
      // this.total = this.teacherDate.studentList.length
    },
    changeClass(e) {
      let data = this.teacherDateclass.find(item => item.title == e);
      this.$emit('changeClass', data);
    }
  }
};

/* script */
const __vue_script__$2o = script$2o;
/* template */
var __vue_render__$2o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c(
        "el-dialog",
        {
          staticClass: "xmlStatisticalCharts",
          attrs: {
            visible: _vm.dialogVisible,
            width: _vm.pageType == "pc" ? "910px" : "90%",
            "custom-class": "xml-epub-pdf-dialog",
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogVisible = $event;
            },
          },
        },
        [
          _vm.userType == "student"
            ? _c("div", [
                _c("div", { staticClass: "student_top" }, [
                  _c("img", {
                    staticClass: "student_img",
                    attrs: { src: _vm.userInfoparms.img },
                  }),
                  _vm._v(" "),
                  _c("div", { staticStyle: { "margin-left": "30px" } }, [
                    _c("div", { staticStyle: { "font-size": "20px" } }, [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.userInfoparms.nickname) +
                          _vm._s(
                            _vm.userType == "teacher" ? "(教师)" : "(学生)"
                          ) +
                          "\n          "
                      ),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "student_title" }, [
                      _c(
                        "div",
                        [
                          _vm._v("\n              班级名称：\n              "),
                          _c(
                            "el-select",
                            {
                              attrs: { placeholder: "请选择" },
                              on: { change: _vm.changeClass },
                              model: {
                                value: _vm.classValue,
                                callback: function ($$v) {
                                  _vm.classValue = $$v;
                                },
                                expression: "classValue",
                              },
                            },
                            _vm._l(_vm.teacherDateclass, function (item) {
                              return _c("el-option", {
                                key: item.title,
                                attrs: { label: item.title, value: item.title },
                              })
                            }),
                            1
                          ),
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "学习进度：" +
                            _vm._s(_vm.studentDate.learningProgress || "0") +
                            "%"
                        ),
                      ]),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "学习次数：" + _vm._s(_vm.studentDate.learnNum || "1")
                        ),
                      ]),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "学习总时长：" +
                            _vm._s(_vm.studentDate.totalLearnTime || "0") +
                            "h"
                        ),
                      ]),
                    ]),
                  ]),
                ]),
                _vm._v(" "),
                _c("div", { staticStyle: { padding: "10px 30px 50px 30px" } }, [
                  _c("div", { staticStyle: { "font-size": "16px" } }, [
                    _vm._v("学习情况"),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "student_bottom" }, [
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color1" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("本次学习情况"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(
                            _vm._s(_vm.studentDate.learnTime || "0") + "h"
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color2" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("图片学习情况"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(
                            _vm._s(_vm.studentDate.imgLearnNum || "0") + "次"
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color3" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("音频学习情况"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(
                            _vm._s(_vm.studentDate.audioLearnTime || "0") + "h"
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color4" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("视频学习情况"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(
                            _vm._s(_vm.studentDate.videoLearnTime || "0") + "h"
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color1" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("答题次数"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(
                            _vm._s(_vm.studentDate.answersNum || "0") + "次"
                          ),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color2" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("笔记数量"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(_vm._s(_vm.studentDate.noteNum || "0")),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color3" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("书签数量"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(_vm._s(_vm.studentDate.bookmarkNum || "0")),
                        ]),
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "student_bottom_box student_color4" },
                      [
                        _c("div", { staticClass: "title" }, [
                          _vm._v("划线数量"),
                        ]),
                        _vm._v(" "),
                        _c("div", { staticClass: "time" }, [
                          _vm._v(_vm._s(_vm.studentDate.lineationNum || "0")),
                        ]),
                      ]
                    ),
                  ]),
                ]),
              ])
            : _vm._e(),
          _vm._v(" "),
          _vm.userType == "teacher"
            ? _c("div", [
                _c("div", { staticClass: "teacher_top" }, [
                  _c("img", {
                    staticClass: "img",
                    attrs: { src: _vm.userInfoparms.img },
                  }),
                  _vm._v(" "),
                  _c("div", { staticStyle: { "margin-left": "30px" } }, [
                    _c("div", { staticStyle: { "font-size": "20px" } }, [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.userInfoparms.nickname) +
                          _vm._s(
                            _vm.userType == "teacher" ? "(教师)" : "(学生)"
                          ) +
                          "\n          "
                      ),
                    ]),
                    _vm._v(" "),
                    _c("div", { staticClass: "title" }, [
                      _c(
                        "div",
                        [
                          _vm._v("\n              班级名称：\n              "),
                          _vm._v(" "),
                          _c(
                            "el-select",
                            {
                              attrs: { placeholder: "请选择" },
                              on: { change: _vm.changeClass },
                              model: {
                                value: _vm.classValue,
                                callback: function ($$v) {
                                  _vm.classValue = $$v;
                                },
                                expression: "classValue",
                              },
                            },
                            _vm._l(_vm.teacherDateclass, function (item) {
                              return _c("el-option", {
                                key: item.title,
                                attrs: { label: item.title, value: item.title },
                              })
                            }),
                            1
                          ),
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "学习进度：" +
                            _vm._s(_vm.teacherDate.learningProgress || "0") +
                            "%"
                        ),
                      ]),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "授课次数：" + _vm._s(_vm.teacherDate.learnNum || "0")
                        ),
                      ]),
                      _vm._v(" "),
                      _c("div", [
                        _vm._v(
                          "授课总时长：" +
                            _vm._s(_vm.teacherDate.totalLearnTime || "0") +
                            "h"
                        ),
                      ]),
                    ]),
                  ]),
                ]),
                _vm._v(" "),
                _c(
                  "div",
                  { staticStyle: { padding: "20px 30px 50px 30px" } },
                  [
                    _c("div", { staticStyle: { "font-size": "16px" } }, [
                      _vm._v("班级学员情况"),
                    ]),
                    _vm._v(" "),
                    _vm.teacherDate.studentList &&
                    _vm.teacherDate.studentList.length > 0
                      ? [
                          _c(
                            "div",
                            {
                              staticStyle: {
                                display: "flex",
                                "flex-direction": "row",
                                padding: "30px 0 0 0",
                              },
                            },
                            [
                              _c(
                                "div",
                                {
                                  staticClass: "wrap_left",
                                  on: {
                                    click: function ($event) {
                                      return _vm.gettableData("up")
                                    },
                                  },
                                },
                                [
                                  _c("i", {
                                    staticClass: "el-icon-arrow-left",
                                    staticStyle: { "font-size": "20px" },
                                  }),
                                ]
                              ),
                              _vm._v(" "),
                              _c(
                                "div",
                                { staticClass: "teacher_swipers" },
                                _vm._l(_vm.tableData, function (item, index) {
                                  return _c(
                                    "div",
                                    {
                                      key: index,
                                      class:
                                        _vm.select_index == index
                                          ? "select_swiper_box"
                                          : "swiper_box",
                                      on: {
                                        click: function ($event) {
                                          return _vm.getbox(item, index)
                                        },
                                      },
                                    },
                                    [
                                      _c("img", {
                                        staticClass: "teacher_img",
                                        attrs: { src: item.img },
                                      }),
                                      _vm._v(" "),
                                      _c(
                                        "div",
                                        { staticClass: "teacher_name" },
                                        [_vm._v(_vm._s(item.nickname))]
                                      ),
                                      _vm._v(" "),
                                      _c("div", {
                                        class:
                                          _vm.select_index == index
                                            ? "uptri"
                                            : "uptri1",
                                      }),
                                    ]
                                  )
                                }),
                                0
                              ),
                              _vm._v(" "),
                              _c(
                                "div",
                                {
                                  staticClass: "wrap_right",
                                  on: {
                                    click: function ($event) {
                                      return _vm.gettableData("next")
                                    },
                                  },
                                },
                                [
                                  _c("i", {
                                    staticClass: "el-icon-arrow-right",
                                    staticStyle: { "font-size": "20px" },
                                  }),
                                ]
                              ),
                            ]
                          ),
                          _vm._v(" "),
                          _c("div", { staticClass: "teacher_bottom" }, [
                            _c(
                              "div",
                              {
                                staticClass:
                                  "teacher_bottom_box student_color1",
                              },
                              [
                                _c("div", { staticClass: "title" }, [
                                  _vm._v("学习总时长"),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "time" }, [
                                  _vm._v(
                                    _vm._s(
                                      _vm.select_item.totalLearnTime || "0"
                                    ) + "h"
                                  ),
                                ]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass:
                                  "teacher_bottom_box student_color2",
                              },
                              [
                                _c("div", { staticClass: "title" }, [
                                  _vm._v("学习次数"),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "time" }, [
                                  _vm._v(
                                    _vm._s(_vm.select_item.learnNum || "0") +
                                      "次"
                                  ),
                                ]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass:
                                  "teacher_bottom_box student_color3",
                              },
                              [
                                _c("div", { staticClass: "title" }, [
                                  _vm._v("学习进度"),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "time" }, [
                                  _vm._v(
                                    _vm._s(
                                      _vm.select_item.learningProgress || "0"
                                    ) + "%"
                                  ),
                                ]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass:
                                  "teacher_bottom_box student_color4",
                              },
                              [
                                _c("div", { staticClass: "title" }, [
                                  _vm._v("答题完成次数"),
                                ]),
                                _vm._v(" "),
                                _c("div", { staticClass: "time" }, [
                                  _vm._v(
                                    _vm._s(_vm.select_item.answersNum || "0") +
                                      "次"
                                  ),
                                ]),
                              ]
                            ),
                          ]),
                        ]
                      : [
                          _c(
                            "div",
                            { staticStyle: { "padding-top": "60px" } },
                            [_c("xml-empty")],
                            1
                          ),
                        ],
                  ],
                  2
                ),
              ])
            : _vm._e(),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2o = [];
__vue_render__$2o._withStripped = true;

  /* style */
  const __vue_inject_styles__$2o = undefined;
  /* scoped */
  const __vue_scope_id__$2o = "data-v-2e3ddc25";
  /* module identifier */
  const __vue_module_identifier__$2o = undefined;
  /* functional template */
  const __vue_is_functional_template__$2o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2o, staticRenderFns: __vue_staticRenderFns__$2o },
    __vue_inject_styles__$2o,
    __vue_script__$2o,
    __vue_scope_id__$2o,
    __vue_is_functional_template__$2o,
    __vue_module_identifier__$2o,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2n = {
  name: 'classGrade',
  props: {
    pageType: {
      type: String,
      default: 'h5'
    },
    userType: {
      type: String,
      default: 'teacher'
    },
    teacherDateclass: {
      type: Array,
      default: () => []
    },
    userInfoparms: {
      type: Object,
      default: () => {}
    }
  },
  mounted() {},
  data() {
    return {
      dialogVisible: false,
      select_index: '',
      select_item: '',
      visible: false,
      tableData: [],
      total: 0,
      page: 1,
      size: 7,
      colorData: ['#FC7293', '#9D91F3', '#47C8E8', '#64A4FC']
    };
  },
  methods: {
    open() {
      this.dialogVisible = true;
    },
    getbox(item, index) {
      this.select_index = index;
      this.select_item = item;
    },
    //分页
    gettableData() {
      let data = JSON.parse(JSON.stringify(this.teacherDate.classstudent));
      this.tableData = data.splice((this.page - 1) * this.size, this.size);
      this.total = this.teacherDate.classstudent.length;
    },
    openClass(item) {
      window.open(item.redirctUrl);
    }
  }
};

/* script */
const __vue_script__$2n = script$2n;
/* template */
var __vue_render__$2n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c(
        "el-dialog",
        {
          staticClass: "xmlStatisticalCharts",
          attrs: {
            visible: _vm.dialogVisible,
            width: _vm.pageType == "pc" ? "910px" : "90%",
            "custom-class": "xml-epub-pdf-dialog",
          },
          on: {
            "update:visible": function ($event) {
              _vm.dialogVisible = $event;
            },
          },
        },
        [
          _vm.userType == "student"
            ? _c("div", [
                _c("div", { staticClass: "student_top" }, [
                  _c("img", {
                    staticClass: "student_img",
                    attrs: { src: _vm.userInfoparms.img },
                  }),
                  _vm._v(" "),
                  _c("div", { staticStyle: { "margin-left": "30px" } }, [
                    _c("div", { staticStyle: { "font-size": "20px" } }, [
                      _vm._v(
                        _vm._s(_vm.userInfoparms.nickname) +
                          _vm._s(
                            _vm.userType == "teacher" ? "(教师)" : "(学生)"
                          )
                      ),
                    ]),
                  ]),
                ]),
                _vm._v(" "),
                _c("div", { staticStyle: { padding: "10px 30px 50px 30px" } }, [
                  _c("div", { staticStyle: { "font-size": "16px" } }, [
                    _vm._v("班级列表"),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "student_bottom" },
                    _vm._l(_vm.teacherDateclass, function (item, index) {
                      return _c(
                        "div",
                        {
                          key: index,
                          staticClass: "student_bottom_box",
                          style: { background: _vm.colorData[index % 4] },
                          on: {
                            click: function ($event) {
                              return _vm.openClass(item)
                            },
                          },
                        },
                        [
                          _c(
                            "div",
                            {
                              staticStyle: {
                                display: "flex",
                                padding: "10px",
                                "align-items": "center",
                              },
                            },
                            [
                              _c("div", [
                                _c("img", {
                                  staticStyle: {
                                    width: "50px",
                                    height: "50px",
                                    "border-radius": "50px",
                                  },
                                  attrs: { src: item.litpic },
                                }),
                              ]),
                              _vm._v(" "),
                              _c(
                                "div",
                                { staticStyle: { "margin-left": "20px" } },
                                [
                                  _c("div", [
                                    _vm._v("班级名称： " + _vm._s(item.title)),
                                  ]),
                                  _vm._v(" "),
                                  _c("div", [
                                    _vm._v(
                                      "班级人数： " +
                                        _vm._s(item.classStudentNum) +
                                        " 人"
                                    ),
                                  ]),
                                ]
                              ),
                            ]
                          ),
                        ]
                      )
                    }),
                    0
                  ),
                ]),
              ])
            : _vm._e(),
          _vm._v(" "),
          _vm.userType == "teacher"
            ? _c("div", [
                _c("div", { staticClass: "student_top" }, [
                  _c("img", {
                    staticClass: "student_img",
                    attrs: { src: _vm.userInfoparms.img },
                  }),
                  _vm._v(" "),
                  _c("div", { staticStyle: { "margin-left": "30px" } }, [
                    _c("div", { staticStyle: { "font-size": "20px" } }, [
                      _vm._v(
                        _vm._s(_vm.userInfoparms.nickname) +
                          _vm._s(
                            _vm.userType == "teacher" ? "(教师)" : "(学生)"
                          )
                      ),
                    ]),
                  ]),
                ]),
                _vm._v(" "),
                _c("div", { staticStyle: { padding: "10px 30px 50px 30px" } }, [
                  _c("div", { staticStyle: { "font-size": "16px" } }, [
                    _vm._v("班级列表"),
                  ]),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "student_bottom" },
                    _vm._l(_vm.teacherDateclass, function (item, index) {
                      return _c(
                        "div",
                        {
                          key: index,
                          staticClass: "student_bottom_box",
                          style: { background: _vm.colorData[index % 4] },
                          on: {
                            click: function ($event) {
                              return _vm.openClass(item)
                            },
                          },
                        },
                        [
                          _c(
                            "div",
                            {
                              staticStyle: {
                                display: "flex",
                                padding: "10px",
                                "align-items": "center",
                              },
                            },
                            [
                              _c("div", [
                                _c("img", {
                                  staticStyle: {
                                    width: "50px",
                                    height: "50px",
                                    "border-radius": "50px",
                                  },
                                  attrs: { src: item.litpic },
                                }),
                              ]),
                              _vm._v(" "),
                              _c(
                                "div",
                                { staticStyle: { "margin-left": "20px" } },
                                [
                                  _c("div", [
                                    _vm._v(" 班级名称：" + _vm._s(item.title)),
                                  ]),
                                  _vm._v(" "),
                                  _c("div", [
                                    _vm._v(
                                      " 班级人数：" +
                                        _vm._s(item.classStudentNum) +
                                        " 人"
                                    ),
                                  ]),
                                ]
                              ),
                            ]
                          ),
                        ]
                      )
                    }),
                    0
                  ),
                ]),
              ])
            : _vm._e(),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2n = [];
__vue_render__$2n._withStripped = true;

  /* style */
  const __vue_inject_styles__$2n = undefined;
  /* scoped */
  const __vue_scope_id__$2n = "data-v-3c1121c8";
  /* module identifier */
  const __vue_module_identifier__$2n = undefined;
  /* functional template */
  const __vue_is_functional_template__$2n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2n, staticRenderFns: __vue_staticRenderFns__$2n },
    __vue_inject_styles__$2n,
    __vue_script__$2n,
    __vue_scope_id__$2n,
    __vue_is_functional_template__$2n,
    __vue_module_identifier__$2n,
    false,
    undefined,
    undefined,
    undefined
  );

var mark = createCommonjsModule(function (module, exports) {
/*!***************************************************
* mark.js v8.11.1
* https://markjs.io/
* Copyright (c) 2014–2018, Julian Kühnel
* Released under the MIT license https://git.io/vwTVl
*****************************************************/

(function (global, factory) {
  module.exports = factory() ;
})(commonjsGlobal, function () {

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  };
  var classCallCheck = function (instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  };
  var createClass = function () {
    function defineProperties(target, props) {
      for (var i = 0; i < props.length; i++) {
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
      }
    }
    return function (Constructor, protoProps, staticProps) {
      if (protoProps) defineProperties(Constructor.prototype, protoProps);
      if (staticProps) defineProperties(Constructor, staticProps);
      return Constructor;
    };
  }();
  var _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  var DOMIterator = function () {
    function DOMIterator(ctx) {
      var iframes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var exclude = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
      var iframesTimeout = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 5000;
      classCallCheck(this, DOMIterator);
      this.ctx = ctx;
      this.iframes = iframes;
      this.exclude = exclude;
      this.iframesTimeout = iframesTimeout;
    }
    createClass(DOMIterator, [{
      key: 'getContexts',
      value: function getContexts() {
        var ctx = void 0,
          filteredCtx = [];
        if (typeof this.ctx === 'undefined' || !this.ctx) {
          ctx = [];
        } else if (NodeList.prototype.isPrototypeOf(this.ctx)) {
          ctx = Array.prototype.slice.call(this.ctx);
        } else if (Array.isArray(this.ctx)) {
          ctx = this.ctx;
        } else if (typeof this.ctx === 'string') {
          ctx = Array.prototype.slice.call(document.querySelectorAll(this.ctx));
        } else {
          ctx = [this.ctx];
        }
        ctx.forEach(function (ctx) {
          var isDescendant = filteredCtx.filter(function (contexts) {
            return contexts.contains(ctx);
          }).length > 0;
          if (filteredCtx.indexOf(ctx) === -1 && !isDescendant) {
            filteredCtx.push(ctx);
          }
        });
        return filteredCtx;
      }
    }, {
      key: 'getIframeContents',
      value: function getIframeContents(ifr, successFn) {
        var errorFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};
        var doc = void 0;
        try {
          var ifrWin = ifr.contentWindow;
          doc = ifrWin.document;
          if (!ifrWin || !doc) {
            throw new Error('iframe inaccessible');
          }
        } catch (e) {
          errorFn();
        }
        if (doc) {
          successFn(doc);
        }
      }
    }, {
      key: 'isIframeBlank',
      value: function isIframeBlank(ifr) {
        var bl = 'about:blank',
          src = ifr.getAttribute('src').trim(),
          href = ifr.contentWindow.location.href;
        return href === bl && src !== bl && src;
      }
    }, {
      key: 'observeIframeLoad',
      value: function observeIframeLoad(ifr, successFn, errorFn) {
        var _this = this;
        var called = false,
          tout = null;
        var listener = function listener() {
          if (called) {
            return;
          }
          called = true;
          clearTimeout(tout);
          try {
            if (!_this.isIframeBlank(ifr)) {
              ifr.removeEventListener('load', listener);
              _this.getIframeContents(ifr, successFn, errorFn);
            }
          } catch (e) {
            errorFn();
          }
        };
        ifr.addEventListener('load', listener);
        tout = setTimeout(listener, this.iframesTimeout);
      }
    }, {
      key: 'onIframeReady',
      value: function onIframeReady(ifr, successFn, errorFn) {
        try {
          if (ifr.contentWindow.document.readyState === 'complete') {
            if (this.isIframeBlank(ifr)) {
              this.observeIframeLoad(ifr, successFn, errorFn);
            } else {
              this.getIframeContents(ifr, successFn, errorFn);
            }
          } else {
            this.observeIframeLoad(ifr, successFn, errorFn);
          }
        } catch (e) {
          errorFn();
        }
      }
    }, {
      key: 'waitForIframes',
      value: function waitForIframes(ctx, done) {
        var _this2 = this;
        var eachCalled = 0;
        this.forEachIframe(ctx, function () {
          return true;
        }, function (ifr) {
          eachCalled++;
          _this2.waitForIframes(ifr.querySelector('html'), function () {
            if (! --eachCalled) {
              done();
            }
          });
        }, function (handled) {
          if (!handled) {
            done();
          }
        });
      }
    }, {
      key: 'forEachIframe',
      value: function forEachIframe(ctx, filter, each) {
        var _this3 = this;
        var end = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};
        var ifr = ctx.querySelectorAll('iframe'),
          open = ifr.length,
          handled = 0;
        ifr = Array.prototype.slice.call(ifr);
        var checkEnd = function checkEnd() {
          if (--open <= 0) {
            end(handled);
          }
        };
        if (!open) {
          checkEnd();
        }
        ifr.forEach(function (ifr) {
          if (DOMIterator.matches(ifr, _this3.exclude)) {
            checkEnd();
          } else {
            _this3.onIframeReady(ifr, function (con) {
              if (filter(ifr)) {
                handled++;
                each(con);
              }
              checkEnd();
            }, checkEnd);
          }
        });
      }
    }, {
      key: 'createIterator',
      value: function createIterator(ctx, whatToShow, filter) {
        return document.createNodeIterator(ctx, whatToShow, filter, false);
      }
    }, {
      key: 'createInstanceOnIframe',
      value: function createInstanceOnIframe(contents) {
        return new DOMIterator(contents.querySelector('html'), this.iframes);
      }
    }, {
      key: 'compareNodeIframe',
      value: function compareNodeIframe(node, prevNode, ifr) {
        var compCurr = node.compareDocumentPosition(ifr),
          prev = Node.DOCUMENT_POSITION_PRECEDING;
        if (compCurr & prev) {
          if (prevNode !== null) {
            var compPrev = prevNode.compareDocumentPosition(ifr),
              after = Node.DOCUMENT_POSITION_FOLLOWING;
            if (compPrev & after) {
              return true;
            }
          } else {
            return true;
          }
        }
        return false;
      }
    }, {
      key: 'getIteratorNode',
      value: function getIteratorNode(itr) {
        var prevNode = itr.previousNode();
        var node = void 0;
        if (prevNode === null) {
          node = itr.nextNode();
        } else {
          node = itr.nextNode() && itr.nextNode();
        }
        return {
          prevNode: prevNode,
          node: node
        };
      }
    }, {
      key: 'checkIframeFilter',
      value: function checkIframeFilter(node, prevNode, currIfr, ifr) {
        var key = false,
          handled = false;
        ifr.forEach(function (ifrDict, i) {
          if (ifrDict.val === currIfr) {
            key = i;
            handled = ifrDict.handled;
          }
        });
        if (this.compareNodeIframe(node, prevNode, currIfr)) {
          if (key === false && !handled) {
            ifr.push({
              val: currIfr,
              handled: true
            });
          } else if (key !== false && !handled) {
            ifr[key].handled = true;
          }
          return true;
        }
        if (key === false) {
          ifr.push({
            val: currIfr,
            handled: false
          });
        }
        return false;
      }
    }, {
      key: 'handleOpenIframes',
      value: function handleOpenIframes(ifr, whatToShow, eCb, fCb) {
        var _this4 = this;
        ifr.forEach(function (ifrDict) {
          if (!ifrDict.handled) {
            _this4.getIframeContents(ifrDict.val, function (con) {
              _this4.createInstanceOnIframe(con).forEachNode(whatToShow, eCb, fCb);
            });
          }
        });
      }
    }, {
      key: 'iterateThroughNodes',
      value: function iterateThroughNodes(whatToShow, ctx, eachCb, filterCb, doneCb) {
        var _this5 = this;
        var itr = this.createIterator(ctx, whatToShow, filterCb);
        var ifr = [],
          elements = [],
          node = void 0,
          prevNode = void 0,
          retrieveNodes = function retrieveNodes() {
            var _getIteratorNode = _this5.getIteratorNode(itr);
            prevNode = _getIteratorNode.prevNode;
            node = _getIteratorNode.node;
            return node;
          };
        while (retrieveNodes()) {
          if (this.iframes) {
            this.forEachIframe(ctx, function (currIfr) {
              return _this5.checkIframeFilter(node, prevNode, currIfr, ifr);
            }, function (con) {
              _this5.createInstanceOnIframe(con).forEachNode(whatToShow, function (ifrNode) {
                return elements.push(ifrNode);
              }, filterCb);
            });
          }
          elements.push(node);
        }
        elements.forEach(function (node) {
          eachCb(node);
        });
        if (this.iframes) {
          this.handleOpenIframes(ifr, whatToShow, eachCb, filterCb);
        }
        doneCb();
      }
    }, {
      key: 'forEachNode',
      value: function forEachNode(whatToShow, each, filter) {
        var _this6 = this;
        var done = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};
        var contexts = this.getContexts();
        var open = contexts.length;
        if (!open) {
          done();
        }
        contexts.forEach(function (ctx) {
          var ready = function ready() {
            _this6.iterateThroughNodes(whatToShow, ctx, each, filter, function () {
              if (--open <= 0) {
                done();
              }
            });
          };
          if (_this6.iframes) {
            _this6.waitForIframes(ctx, ready);
          } else {
            ready();
          }
        });
      }
    }], [{
      key: 'matches',
      value: function matches(element, selector) {
        var selectors = typeof selector === 'string' ? [selector] : selector,
          fn = element.matches || element.matchesSelector || element.msMatchesSelector || element.mozMatchesSelector || element.oMatchesSelector || element.webkitMatchesSelector;
        if (fn) {
          var match = false;
          selectors.every(function (sel) {
            if (fn.call(element, sel)) {
              match = true;
              return false;
            }
            return true;
          });
          return match;
        } else {
          return false;
        }
      }
    }]);
    return DOMIterator;
  }();
  var Mark$1 = function () {
    function Mark(ctx) {
      classCallCheck(this, Mark);
      this.ctx = ctx;
      this.ie = false;
      var ua = window.navigator.userAgent;
      if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
        this.ie = true;
      }
    }
    createClass(Mark, [{
      key: 'log',
      value: function log(msg) {
        var level = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'debug';
        var log = this.opt.log;
        if (!this.opt.debug) {
          return;
        }
        if ((typeof log === 'undefined' ? 'undefined' : _typeof(log)) === 'object' && typeof log[level] === 'function') {
          log[level]('mark.js: ' + msg);
        }
      }
    }, {
      key: 'escapeStr',
      value: function escapeStr(str) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
      }
    }, {
      key: 'createRegExp',
      value: function createRegExp(str) {
        if (this.opt.wildcards !== 'disabled') {
          str = this.setupWildcardsRegExp(str);
        }
        str = this.escapeStr(str);
        if (Object.keys(this.opt.synonyms).length) {
          str = this.createSynonymsRegExp(str);
        }
        if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
          str = this.setupIgnoreJoinersRegExp(str);
        }
        if (this.opt.diacritics) {
          str = this.createDiacriticsRegExp(str);
        }
        str = this.createMergedBlanksRegExp(str);
        if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
          str = this.createJoinersRegExp(str);
        }
        if (this.opt.wildcards !== 'disabled') {
          str = this.createWildcardsRegExp(str);
        }
        str = this.createAccuracyRegExp(str);
        return str;
      }
    }, {
      key: 'createSynonymsRegExp',
      value: function createSynonymsRegExp(str) {
        var syn = this.opt.synonyms,
          sens = this.opt.caseSensitive ? '' : 'i',
          joinerPlaceholder = this.opt.ignoreJoiners || this.opt.ignorePunctuation.length ? '\0' : '';
        for (var index in syn) {
          if (syn.hasOwnProperty(index)) {
            var value = syn[index],
              k1 = this.opt.wildcards !== 'disabled' ? this.setupWildcardsRegExp(index) : this.escapeStr(index),
              k2 = this.opt.wildcards !== 'disabled' ? this.setupWildcardsRegExp(value) : this.escapeStr(value);
            if (k1 !== '' && k2 !== '') {
              str = str.replace(new RegExp('(' + this.escapeStr(k1) + '|' + this.escapeStr(k2) + ')', 'gm' + sens), joinerPlaceholder + ('(' + this.processSynomyms(k1) + '|') + (this.processSynomyms(k2) + ')') + joinerPlaceholder);
            }
          }
        }
        return str;
      }
    }, {
      key: 'processSynomyms',
      value: function processSynomyms(str) {
        if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
          str = this.setupIgnoreJoinersRegExp(str);
        }
        return str;
      }
    }, {
      key: 'setupWildcardsRegExp',
      value: function setupWildcardsRegExp(str) {
        str = str.replace(/(?:\\)*\?/g, function (val) {
          return val.charAt(0) === '\\' ? '?' : '\x01';
        });
        return str.replace(/(?:\\)*\*/g, function (val) {
          return val.charAt(0) === '\\' ? '*' : '\x02';
        });
      }
    }, {
      key: 'createWildcardsRegExp',
      value: function createWildcardsRegExp(str) {
        var spaces = this.opt.wildcards === 'withSpaces';
        return str.replace(/\u0001/g, spaces ? '[\\S\\s]?' : '\\S?').replace(/\u0002/g, spaces ? '[\\S\\s]*?' : '\\S*');
      }
    }, {
      key: 'setupIgnoreJoinersRegExp',
      value: function setupIgnoreJoinersRegExp(str) {
        return str.replace(/[^(|)\\]/g, function (val, indx, original) {
          var nextChar = original.charAt(indx + 1);
          if (/[(|)\\]/.test(nextChar) || nextChar === '') {
            return val;
          } else {
            return val + '\0';
          }
        });
      }
    }, {
      key: 'createJoinersRegExp',
      value: function createJoinersRegExp(str) {
        var joiner = [];
        var ignorePunctuation = this.opt.ignorePunctuation;
        if (Array.isArray(ignorePunctuation) && ignorePunctuation.length) {
          joiner.push(this.escapeStr(ignorePunctuation.join('')));
        }
        if (this.opt.ignoreJoiners) {
          joiner.push('\\u00ad\\u200b\\u200c\\u200d');
        }
        return joiner.length ? str.split(/\u0000+/).join('[' + joiner.join('') + ']*') : str;
      }
    }, {
      key: 'createDiacriticsRegExp',
      value: function createDiacriticsRegExp(str) {
        var sens = this.opt.caseSensitive ? '' : 'i',
          dct = this.opt.caseSensitive ? ['aàáảãạăằắẳẵặâầấẩẫậäåāą', 'AÀÁẢÃẠĂẰẮẲẴẶÂẦẤẨẪẬÄÅĀĄ', 'cçćč', 'CÇĆČ', 'dđď', 'DĐĎ', 'eèéẻẽẹêềếểễệëěēę', 'EÈÉẺẼẸÊỀẾỂỄỆËĚĒĘ', 'iìíỉĩịîïī', 'IÌÍỈĨỊÎÏĪ', 'lł', 'LŁ', 'nñňń', 'NÑŇŃ', 'oòóỏõọôồốổỗộơởỡớờợöøō', 'OÒÓỎÕỌÔỒỐỔỖỘƠỞỠỚỜỢÖØŌ', 'rř', 'RŘ', 'sšśșş', 'SŠŚȘŞ', 'tťțţ', 'TŤȚŢ', 'uùúủũụưừứửữựûüůū', 'UÙÚỦŨỤƯỪỨỬỮỰÛÜŮŪ', 'yýỳỷỹỵÿ', 'YÝỲỶỸỴŸ', 'zžżź', 'ZŽŻŹ'] : ['aàáảãạăằắẳẵặâầấẩẫậäåāąAÀÁẢÃẠĂẰẮẲẴẶÂẦẤẨẪẬÄÅĀĄ', 'cçćčCÇĆČ', 'dđďDĐĎ', 'eèéẻẽẹêềếểễệëěēęEÈÉẺẼẸÊỀẾỂỄỆËĚĒĘ', 'iìíỉĩịîïīIÌÍỈĨỊÎÏĪ', 'lłLŁ', 'nñňńNÑŇŃ', 'oòóỏõọôồốổỗộơởỡớờợöøōOÒÓỎÕỌÔỒỐỔỖỘƠỞỠỚỜỢÖØŌ', 'rřRŘ', 'sšśșşSŠŚȘŞ', 'tťțţTŤȚŢ', 'uùúủũụưừứửữựûüůūUÙÚỦŨỤƯỪỨỬỮỰÛÜŮŪ', 'yýỳỷỹỵÿYÝỲỶỸỴŸ', 'zžżźZŽŻŹ'];
        var handled = [];
        str.split('').forEach(function (ch) {
          dct.every(function (dct) {
            if (dct.indexOf(ch) !== -1) {
              if (handled.indexOf(dct) > -1) {
                return false;
              }
              str = str.replace(new RegExp('[' + dct + ']', 'gm' + sens), '[' + dct + ']');
              handled.push(dct);
            }
            return true;
          });
        });
        return str;
      }
    }, {
      key: 'createMergedBlanksRegExp',
      value: function createMergedBlanksRegExp(str) {
        return str.replace(/[\s]+/gmi, '[\\s]+');
      }
    }, {
      key: 'createAccuracyRegExp',
      value: function createAccuracyRegExp(str) {
        var _this = this;
        var chars = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~¡¿';
        var acc = this.opt.accuracy,
          val = typeof acc === 'string' ? acc : acc.value,
          ls = typeof acc === 'string' ? [] : acc.limiters,
          lsJoin = '';
        ls.forEach(function (limiter) {
          lsJoin += '|' + _this.escapeStr(limiter);
        });
        switch (val) {
          case 'partially':
          default:
            return '()(' + str + ')';
          case 'complementary':
            lsJoin = '\\s' + (lsJoin ? lsJoin : this.escapeStr(chars));
            return '()([^' + lsJoin + ']*' + str + '[^' + lsJoin + ']*)';
          case 'exactly':
            return '(^|\\s' + lsJoin + ')(' + str + ')(?=$|\\s' + lsJoin + ')';
        }
      }
    }, {
      key: 'getSeparatedKeywords',
      value: function getSeparatedKeywords(sv) {
        var _this2 = this;
        var stack = [];
        sv.forEach(function (kw) {
          if (!_this2.opt.separateWordSearch) {
            if (kw.trim() && stack.indexOf(kw) === -1) {
              stack.push(kw);
            }
          } else {
            kw.split(' ').forEach(function (kwSplitted) {
              if (kwSplitted.trim() && stack.indexOf(kwSplitted) === -1) {
                stack.push(kwSplitted);
              }
            });
          }
        });
        return {
          'keywords': stack.sort(function (a, b) {
            return b.length - a.length;
          }),
          'length': stack.length
        };
      }
    }, {
      key: 'isNumeric',
      value: function isNumeric(value) {
        return Number(parseFloat(value)) == value;
      }
    }, {
      key: 'checkRanges',
      value: function checkRanges(array) {
        var _this3 = this;
        if (!Array.isArray(array) || Object.prototype.toString.call(array[0]) !== '[object Object]') {
          this.log('markRanges() will only accept an array of objects');
          this.opt.noMatch(array);
          return [];
        }
        var stack = [];
        var last = 0;
        array.sort(function (a, b) {
          return a.start - b.start;
        }).forEach(function (item) {
          var _callNoMatchOnInvalid = _this3.callNoMatchOnInvalidRanges(item, last),
            start = _callNoMatchOnInvalid.start,
            end = _callNoMatchOnInvalid.end,
            valid = _callNoMatchOnInvalid.valid;
          if (valid) {
            item.start = start;
            item.length = end - start;
            stack.push(item);
            last = end;
          }
        });
        return stack;
      }
    }, {
      key: 'callNoMatchOnInvalidRanges',
      value: function callNoMatchOnInvalidRanges(range, last) {
        var start = void 0,
          end = void 0,
          valid = false;
        if (range && typeof range.start !== 'undefined') {
          start = parseInt(range.start, 10);
          end = start + parseInt(range.length, 10);
          if (this.isNumeric(range.start) && this.isNumeric(range.length) && end - last > 0 && end - start > 0) {
            valid = true;
          } else {
            this.log('Ignoring invalid or overlapping range: ' + ('' + JSON.stringify(range)));
            this.opt.noMatch(range);
          }
        } else {
          this.log('Ignoring invalid range: ' + JSON.stringify(range));
          this.opt.noMatch(range);
        }
        return {
          start: start,
          end: end,
          valid: valid
        };
      }
    }, {
      key: 'checkWhitespaceRanges',
      value: function checkWhitespaceRanges(range, originalLength, string) {
        var end = void 0,
          valid = true,
          max = string.length,
          offset = originalLength - max,
          start = parseInt(range.start, 10) - offset;
        start = start > max ? max : start;
        end = start + parseInt(range.length, 10);
        if (end > max) {
          end = max;
          this.log('End range automatically set to the max value of ' + max);
        }
        if (start < 0 || end - start < 0 || start > max || end > max) {
          valid = false;
          this.log('Invalid range: ' + JSON.stringify(range));
          this.opt.noMatch(range);
        } else if (string.substring(start, end).replace(/\s+/g, '') === '') {
          valid = false;
          this.log('Skipping whitespace only range: ' + JSON.stringify(range));
          this.opt.noMatch(range);
        }
        return {
          start: start,
          end: end,
          valid: valid
        };
      }
    }, {
      key: 'getTextNodes',
      value: function getTextNodes(cb) {
        var _this4 = this;
        var val = '',
          nodes = [];
        this.iterator.forEachNode(NodeFilter.SHOW_TEXT, function (node) {
          nodes.push({
            start: val.length,
            end: (val += node.textContent).length,
            node: node
          });
        }, function (node) {
          if (_this4.matchesExclude(node.parentNode)) {
            return NodeFilter.FILTER_REJECT;
          } else {
            return NodeFilter.FILTER_ACCEPT;
          }
        }, function () {
          cb({
            value: val,
            nodes: nodes
          });
        });
      }
    }, {
      key: 'matchesExclude',
      value: function matchesExclude(el) {
        return DOMIterator.matches(el, this.opt.exclude.concat(['script', 'style', 'title', 'head', 'html']));
      }
    }, {
      key: 'wrapRangeInTextNode',
      value: function wrapRangeInTextNode(node, start, end) {
        var hEl = !this.opt.element ? 'mark' : this.opt.element,
          startNode = node.splitText(start),
          ret = startNode.splitText(end - start);
        var repl = document.createElement(hEl);
        repl.setAttribute('data-markjs', 'true');
        if (this.opt.className) {
          repl.setAttribute('class', this.opt.className);
        }
        repl.textContent = startNode.textContent;
        startNode.parentNode.replaceChild(repl, startNode);
        return ret;
      }
    }, {
      key: 'wrapRangeInMappedTextNode',
      value: function wrapRangeInMappedTextNode(dict, start, end, filterCb, eachCb) {
        var _this5 = this;
        dict.nodes.every(function (n, i) {
          var sibl = dict.nodes[i + 1];
          if (typeof sibl === 'undefined' || sibl.start > start) {
            if (!filterCb(n.node)) {
              return false;
            }
            var s = start - n.start,
              e = (end > n.end ? n.end : end) - n.start,
              startStr = dict.value.substr(0, n.start),
              endStr = dict.value.substr(e + n.start);
            n.node = _this5.wrapRangeInTextNode(n.node, s, e);
            dict.value = startStr + endStr;
            dict.nodes.forEach(function (k, j) {
              if (j >= i) {
                if (dict.nodes[j].start > 0 && j !== i) {
                  dict.nodes[j].start -= e;
                }
                dict.nodes[j].end -= e;
              }
            });
            end -= e;
            eachCb(n.node.previousSibling, n.start);
            if (end > n.end) {
              start = n.end;
            } else {
              return false;
            }
          }
          return true;
        });
      }
    }, {
      key: 'wrapMatches',
      value: function wrapMatches(regex, ignoreGroups, filterCb, eachCb, endCb) {
        var _this6 = this;
        var matchIdx = ignoreGroups === 0 ? 0 : ignoreGroups + 1;
        this.getTextNodes(function (dict) {
          dict.nodes.forEach(function (node) {
            node = node.node;
            var match = void 0;
            while ((match = regex.exec(node.textContent)) !== null && match[matchIdx] !== '') {
              if (!filterCb(match[matchIdx], node)) {
                continue;
              }
              var pos = match.index;
              if (matchIdx !== 0) {
                for (var i = 1; i < matchIdx; i++) {
                  pos += match[i].length;
                }
              }
              node = _this6.wrapRangeInTextNode(node, pos, pos + match[matchIdx].length);
              eachCb(node.previousSibling);
              regex.lastIndex = 0;
            }
          });
          endCb();
        });
      }
    }, {
      key: 'wrapMatchesAcrossElements',
      value: function wrapMatchesAcrossElements(regex, ignoreGroups, filterCb, eachCb, endCb) {
        var _this7 = this;
        var matchIdx = ignoreGroups === 0 ? 0 : ignoreGroups + 1;
        this.getTextNodes(function (dict) {
          var match = void 0;
          while ((match = regex.exec(dict.value)) !== null && match[matchIdx] !== '') {
            var start = match.index;
            if (matchIdx !== 0) {
              for (var i = 1; i < matchIdx; i++) {
                start += match[i].length;
              }
            }
            var end = start + match[matchIdx].length;
            _this7.wrapRangeInMappedTextNode(dict, start, end, function (node) {
              return filterCb(match[matchIdx], node);
            }, function (node, lastIndex) {
              regex.lastIndex = lastIndex;
              eachCb(node);
            });
          }
          endCb();
        });
      }
    }, {
      key: 'wrapRangeFromIndex',
      value: function wrapRangeFromIndex(ranges, filterCb, eachCb, endCb) {
        var _this8 = this;
        this.getTextNodes(function (dict) {
          var originalLength = dict.value.length;
          ranges.forEach(function (range, counter) {
            var _checkWhitespaceRange = _this8.checkWhitespaceRanges(range, originalLength, dict.value),
              start = _checkWhitespaceRange.start,
              end = _checkWhitespaceRange.end,
              valid = _checkWhitespaceRange.valid;
            if (valid) {
              _this8.wrapRangeInMappedTextNode(dict, start, end, function (node) {
                return filterCb(node, range, dict.value.substring(start, end), counter);
              }, function (node) {
                eachCb(node, range);
              });
            }
          });
          endCb();
        });
      }
    }, {
      key: 'unwrapMatches',
      value: function unwrapMatches(node) {
        var parent = node.parentNode;
        var docFrag = document.createDocumentFragment();
        while (node.firstChild) {
          docFrag.appendChild(node.removeChild(node.firstChild));
        }
        parent.replaceChild(docFrag, node);
        if (!this.ie) {
          parent.normalize();
        } else {
          this.normalizeTextNode(parent);
        }
      }
    }, {
      key: 'normalizeTextNode',
      value: function normalizeTextNode(node) {
        if (!node) {
          return;
        }
        if (node.nodeType === 3) {
          while (node.nextSibling && node.nextSibling.nodeType === 3) {
            node.nodeValue += node.nextSibling.nodeValue;
            node.parentNode.removeChild(node.nextSibling);
          }
        } else {
          this.normalizeTextNode(node.firstChild);
        }
        this.normalizeTextNode(node.nextSibling);
      }
    }, {
      key: 'markRegExp',
      value: function markRegExp(regexp, opt) {
        var _this9 = this;
        this.opt = opt;
        this.log('Searching with expression "' + regexp + '"');
        var totalMatches = 0,
          fn = 'wrapMatches';
        var eachCb = function eachCb(element) {
          totalMatches++;
          _this9.opt.each(element);
        };
        if (this.opt.acrossElements) {
          fn = 'wrapMatchesAcrossElements';
        }
        this[fn](regexp, this.opt.ignoreGroups, function (match, node) {
          return _this9.opt.filter(node, match, totalMatches);
        }, eachCb, function () {
          if (totalMatches === 0) {
            _this9.opt.noMatch(regexp);
          }
          _this9.opt.done(totalMatches);
        });
      }
    }, {
      key: 'mark',
      value: function mark(sv, opt) {
        var _this10 = this;
        this.opt = opt;
        var totalMatches = 0,
          fn = 'wrapMatches';
        var _getSeparatedKeywords = this.getSeparatedKeywords(typeof sv === 'string' ? [sv] : sv),
          kwArr = _getSeparatedKeywords.keywords,
          kwArrLen = _getSeparatedKeywords.length,
          sens = this.opt.caseSensitive ? '' : 'i',
          handler = function handler(kw) {
            var regex = new RegExp(_this10.createRegExp(kw), 'gm' + sens),
              matches = 0;
            _this10.log('Searching with expression "' + regex + '"');
            _this10[fn](regex, 1, function (term, node) {
              return _this10.opt.filter(node, kw, totalMatches, matches);
            }, function (element) {
              matches++;
              totalMatches++;
              _this10.opt.each(element);
            }, function () {
              if (matches === 0) {
                _this10.opt.noMatch(kw);
              }
              if (kwArr[kwArrLen - 1] === kw) {
                _this10.opt.done(totalMatches);
              } else {
                handler(kwArr[kwArr.indexOf(kw) + 1]);
              }
            });
          };
        if (this.opt.acrossElements) {
          fn = 'wrapMatchesAcrossElements';
        }
        if (kwArrLen === 0) {
          this.opt.done(totalMatches);
        } else {
          handler(kwArr[0]);
        }
      }
    }, {
      key: 'markRanges',
      value: function markRanges(rawRanges, opt) {
        var _this11 = this;
        this.opt = opt;
        var totalMatches = 0,
          ranges = this.checkRanges(rawRanges);
        if (ranges && ranges.length) {
          this.log('Starting to mark with the following ranges: ' + JSON.stringify(ranges));
          this.wrapRangeFromIndex(ranges, function (node, range, match, counter) {
            return _this11.opt.filter(node, range, match, counter);
          }, function (element, range) {
            totalMatches++;
            _this11.opt.each(element, range);
          }, function () {
            _this11.opt.done(totalMatches);
          });
        } else {
          this.opt.done(totalMatches);
        }
      }
    }, {
      key: 'unmark',
      value: function unmark(opt) {
        var _this12 = this;
        this.opt = opt;
        var sel = this.opt.element ? this.opt.element : '*';
        sel += '[data-markjs]';
        if (this.opt.className) {
          sel += '.' + this.opt.className;
        }
        this.log('Removal selector "' + sel + '"');
        this.iterator.forEachNode(NodeFilter.SHOW_ELEMENT, function (node) {
          _this12.unwrapMatches(node);
        }, function (node) {
          var matchesSel = DOMIterator.matches(node, sel),
            matchesExclude = _this12.matchesExclude(node);
          if (!matchesSel || matchesExclude) {
            return NodeFilter.FILTER_REJECT;
          } else {
            return NodeFilter.FILTER_ACCEPT;
          }
        }, this.opt.done);
      }
    }, {
      key: 'opt',
      set: function set$$1(val) {
        this._opt = _extends({}, {
          'element': '',
          'className': '',
          'exclude': [],
          'iframes': false,
          'iframesTimeout': 5000,
          'separateWordSearch': true,
          'diacritics': true,
          'synonyms': {},
          'accuracy': 'partially',
          'acrossElements': false,
          'caseSensitive': false,
          'ignoreJoiners': false,
          'ignoreGroups': 0,
          'ignorePunctuation': [],
          'wildcards': 'disabled',
          'each': function each() {},
          'noMatch': function noMatch() {},
          'filter': function filter() {
            return true;
          },
          'done': function done() {},
          'debug': false,
          'log': window.console
        }, val);
      },
      get: function get$$1() {
        return this._opt;
      }
    }, {
      key: 'iterator',
      get: function get$$1() {
        return new DOMIterator(this.ctx, this.opt.iframes, this.opt.exclude, this.opt.iframesTimeout);
      }
    }]);
    return Mark;
  }();
  function Mark(ctx) {
    var _this = this;
    var instance = new Mark$1(ctx);
    this.mark = function (sv, opt) {
      instance.mark(sv, opt);
      return _this;
    };
    this.markRegExp = function (sv, opt) {
      instance.markRegExp(sv, opt);
      return _this;
    };
    this.markRanges = function (sv, opt) {
      instance.markRanges(sv, opt);
      return _this;
    };
    this.unmark = function (opt) {
      instance.unmark(opt);
      return _this;
    };
    return this;
  }
  return Mark;
});
});

//
let target;
var script$2m = {
  name: 'XmlDigitalTeaching',
  components: {
    xmlPreviewSearch: __vue_component__$2K,
    xmlToolbarPc: __vue_component__$2J,
    xmlResourceListPc: __vue_component__$2F,
    addFile: __vue_component__$5y,
    setPop: __vue_component__$2E,
    xmlToolbarPhone: __vue_component__$2w,
    downFileUppop: __vue_component__$2z,
    PdfView: __vue_component__$5i,
    modifyNotes: __vue_component__$2v,
    resourceListPhone: __vue_component__$2y,
    PreviewLeftStudent: __vue_component__$2p,
    highlight: __vue_component__$2D,
    bookmark: __vue_component__$2C,
    xmlRobotPc: __vue_component__$2B,
    xmlRobotPhone: __vue_component__$2A,
    xmlStatisticalChart: __vue_component__$2o,
    classGrade: __vue_component__$2n
  },
  props: {
    textBookData: Object,
    //数字教材信息
    catalogList: Array,
    //目录
    looseLeafData: Object,
    //所有活页信息
    deleteNote: {
      type: Function,
      default: () => {}
    },
    editNote: {
      type: Function,
      default: () => {}
    },
    deleteHighlight: {
      type: Function,
      default: () => {}
    },
    isShowTeacherResource: {
      // 是否显示教师资源
      type: Boolean,
      default: false
    },
    notesList: {
      type: Array,
      default: () => []
    },
    //笔记列表
    bookmarkList: {
      type: Array,
      default: () => []
    },
    //书签列表
    resourcesList: {
      //教师资源列表
      type: Array,
      default: () => []
    },
    highlightList: {
      //高亮列表
      type: Array,
      default: () => []
    },
    linkList: {
      //划线列表
      type: Array,
      default: () => []
    },
    readerType: {
      //阅读器类型,reader:阅读器器模式可添加书签、笔记等，preview：预览模式不可添加书签、笔记等，loose-leaf:活页模式只显示教材内容
      type: String,
      default: 'reader'
    },
    pageType: {
      //页面类型：取值h5、pc、pad
      type: String,
      default: 'h5'
    },
    styleType: {
      //阅读风格
      type: String,
      default: 'A'
    },
    isTrial: {
      // 是否为试读模式
      type: Boolean,
      default: false
    },
    isTrialIndex: {
      // 是否为试读模式进度
      type: Number,
      default: 10
    },
    textBookId: {
      //数字教材Id
      type: String,
      default: ''
    },
    basisPath: {
      //资源文件基础路径
      type: String,
      default: ''
    },
    officePreviewPath: {
      //office 在线预览路径
      type: String,
      default: 'https://view.officeapps.live.com/op/view.aspx?src='
    },
    looseleafUrl: {
      //活页在线预览路径
      type: String,
      default: 'https://dcldbook.airlib.cn:90/'
    },
    location: {
      type: String,
      default: ''
    },
    releaseUrlList: {
      //需要放行的Url
      type: Array,
      default: () => []
    },
    continueReading: {
      // 是否显示继续阅读弹窗
      type: Boolean,
      default: false
    },
    downloadType: {
      // 下载方式弹窗
      type: String,
      default: 'pop-up'
    },
    testPaperPath: {
      //试题预览地址
      type: String,
      default: 'http://124.133.23.84:10014'
    },
    trialText: {
      // 试读结束提示语
      type: String,
      default: '<p>--- 试读结束 ---</p>'
    },
    trialPrompt: {
      // 试读模式弹窗提示语
      type: String,
      default: '该处已超出试读范围'
    },
    readOnly: {
      // 只读模式，只可阅读不能添加笔记书签
      type: Boolean,
      default: false
    },
    isShowAi: {
      //0:显示Ai，1不显示
      type: String,
      default: '0'
    },
    catalogLevel: {
      //目录显示的最小层级
      type: Number,
      default: 3
    },
    textBookMergeLevel: {
      //教材内容合并到几级目录下
      type: Number,
      default: 1
    },
    learningDuration: {
      //已学习总时长
      type: Number,
      default: 0
    },
    classId: {
      // 班级唯一标识
      type: String,
      default: ''
    },
    gradesName: {
      // 班级名称
      type: String,
      default: ''
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: 'teacher'
      // default: 'student'
    },
    action: {
      // 上传文件地址
      type: String,
      default: ''
    },
    headers: {
      // 上传文件头部
      type: Object,
      default: () => {}
    },
    data: {
      // 上传文件附带的额外参数
      type: Object,
      default: () => {}
    },
    setting: {
      // @pos 此参数留作外部使用, 内部临时使用data.persistentReaderSetting
      // 设置
      type: Object,
      default: () => {}
    },
    fileSize: {
      // 最大上传文件大小
      type: Number,
      default: 300
    },
    isOpenHyperlink: {
      // 移动端是否可以直接打开超链接
      type: Boolean,
      default: false
    },
    linkOpenLabel: {
      // pc端链接是否打开新标签
      type: Boolean,
      default: true
    },
    navBarHeight: {
      // 业务系统导航栏高度
      type: Number,
      default: 0
    },
    systemEnvId: {
      // 系统环境ID,用于区分不同出版社
      type: String,
      default: '885d44db-8f0f-41ff-b2af-7ec4a6844685'
    },
    chapterId: {
      // 跳转到章节ID
      type: String,
      default: ''
    },
    teacherData: {
      type: Object,
      default: () => {}
    },
    studentData: {
      type: Object,
      default: () => {}
    },
    teacherDateclass: {
      type: Array,
      default: () => []
    },
    userInfoparms: {
      type: Object,
      default: () => {}
    },
    updateFREQUENCY: {
      // 音视频更新频率，默认10s
      type: Number,
      default: 10000
    },
    isAddMark: {
      type: Boolean,
      default: true
    },
    loadingMode: {
      // 加载模式，可选roll、page
      type: String,
      default: 'roll'
    },
    robotData: {
      // 机器人数据
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      pageTitle: '',
      businessType: 'textBook',
      //业务类型：looseLeaf，textBook,此组件只处理textBook
      showType: 'leaf',
      //显示的类型：leaf 活页，catalog
      contentList: [],
      //内容list，只有3个
      selectId: '',
      chapterIndex: 0,
      componentList: [],
      textCatalog: [],
      componentTotal: 0,
      scrollObserver: null,
      showPrompt: false,
      promptText: '',
      toastType: '',
      readLocation: '',
      //当前阅读的位置
      startIndex: 0,
      endIndex: 1,
      continueRead: false,
      isiOS: false,
      //是否是ios
      downloadFileLink: '',
      seachInput: '',
      videoDomId: '',
      videoDomList: [],
      audioDomId: '',
      audioDomList: [],
      scrollTimer: null,
      componentIndex: 0,
      xmlDigitalTeaching: '',
      scrollId: '',
      themeColor: '',
      currentLocation: '',
      //当前点击的组件id
      learningTime: 0,
      //本次学习时长

      locationChapterId: '',
      //当前内容阅读所在目录id
      locationLabel: '',
      //当前内容阅读所在目录名称
      firstLevelLabel: '',
      //一级目录名称
      firstLevelChapterId: '',
      //一级目录id
      textBookResource: {
        imageList: [],
        audioList: [],
        videoList: [],
        resourceList: [],
        formulaList: [],
        interactionList: [],
        questionList: [],
        testPaperList: [],
        imageTotal: 0,
        audioTotal: 0,
        videoTotal: 0,
        resourceTotal: 0,
        formulaTotal: 0,
        interactionTotal: 0,
        questionTotal: 0,
        testPaperTotal: 0,
        teachingResourceTotal: 0,
        imageCurrent: 0,
        audioCurrent: 0,
        videoCurrent: 0,
        resourceCurrent: 0,
        formulaCurrent: 0,
        interactionCurrent: 0,
        questionCurrent: 0
      },
      resourceType: 'search',
      resourceIcon: 'icon_01',
      imageViewTitles: [],
      imageViewSrcs: [],
      playTimeMap: {},
      viewerVisible: false,
      hotzoneTarget: {
        type: '',
        value: '',
        label: '',
        desc: '',
        cover: ''
      },
      hotTargetViewerVisible: false,
      schedule: '0',
      currentGroupInfo: {},
      //当前阅读位置块信息
      showPdf: false,
      fileBaseUrl: '',
      fileBaseTitle: '',
      PreviewCatalog: false,
      currentIndex: 0,
      isBookmarkAddSuccess: false,
      keyboardShow: false,
      //移动端键盘是否弹出
      persistentReaderSetting: {},
      isBookmarked: false,
      // bookmarkIconSrc: '../../../static/images/icons/books/bookmark-top.png', // 原始图片路径
      // bookmarkedIconSrc: '../../../static/images/icons/books/bookmark-top-active.svg', // 激活图片路径
      isShowToolbarTip: false,
      tippyTplBarId: '',
      xmlReaderPageHeight: window.innerHeight,
      xmlReaderWidth: 0,
      env: {},
      immersiveReader: false,
      currentProgress: 0
      // localIsShowTeacherResource:this.isShowTeacherResource
    };
  },
  computed: {
    teacherResourcesList() {
      this.textBookResource.teachingResourceTotal = 0;
      let list = this.resourcesList;
      list.sort(this.sortBy('chapterOrderNum', 1));
      const map = [];
      list.forEach(e => {
        if (e.type == 'note__teacher') ;
        if (e.isStudentShow != 1 || this.userType == 'teacher') {
          this.textBookResource.teachingResourceTotal += 1;
          const findObj = map.find(innerTmp => innerTmp.firstLevelId === e.firstLevelId);
          if (findObj) {
            findObj.list.push(e);
            findObj.list.sort(this.sortBy('textOrderNum', 1));
          } else {
            map.push({
              firstLevelId: e.firstLevelId,
              firstLevelLabel: e.firstLevelLabel,
              chapterOrderNum: e.chapterOrderNum,
              list: [e]
            });
          }
        }
      });
      return map;
    },
    teacherNoteList() {
      let list = this.resourcesList || [];
      let teacherNoteList = [];
      list.forEach(e => {
        if ((e.isStudentShow != 1 || this.userType == 'teacher') && this.isShowTeacherResource) {
          teacherNoteList.push(e);
        }
      });
      return teacherNoteList;
    },
    bookmarkData() {
      let list = this.bookmarkList;
      list.sort(this.sortBy('orderNum', 1));
      const map = [];
      list.forEach((e, index) => {
        const findObj = map.find(innerTmp => innerTmp.firstLevelId === e.firstLevelId);
        if (findObj) {
          findObj.list.push({
            ...e,
            bookmarkIndex: index + 1
          });
        } else {
          map.push({
            firstLevelId: e.firstLevelId,
            firstLevelLabel: e.firstLevelLabel,
            chapterOrderNum: e.chapterOrderNum,
            list: [{
              ...e,
              bookmarkIndex: index + 1
            }]
          });
        }
      });
      return map;
    },
    highlightData() {
      let list = this.highlightList;
      list.sort(this.sortBy('chapterOrderNum', 1));
      const map = [];
      list.forEach(e => {
        const findObj = map.find(innerTmp => innerTmp.topChapterId === e.topChapterId);
        if (findObj) {
          findObj.list.push(e);
          findObj.list.sort(this.sortBy('textOrderNum', 1));
        } else {
          map.push({
            topChapterId: e.topChapterId,
            firstLevelId: e.topChapterId,
            firstLevelLabel: e.topChapterLabel,
            chapterOrderNum: e.blockIndex,
            list: [e]
          });
        }
      });
      return map;
    },
    linkData() {
      let list = this.linkList;
      list.sort(this.sortBy('chapterOrderNum', 1));
      const map = [];
      list.forEach(e => {
        const findObj = map.find(innerTmp => innerTmp.topChapterId === e.topChapterId);
        if (findObj) {
          findObj.list.push(e);
          findObj.list.sort(this.sortBy('textOrderNum', 1));
        } else {
          map.push({
            topChapterId: e.topChapterId,
            firstLevelId: e.topChapterId,
            firstLevelLabel: e.topChapterLabel,
            chapterOrderNum: e.blockIndex,
            list: [e]
          });
        }
      });
      return map;
    },
    notesListData() {
      let list = this.notesList;
      list.sort(this.sortBy('chapterOrderNum', 1));
      const map = [];
      list.forEach(e => {
        const findObj = map.find(innerTmp => innerTmp.topChapterId === e.topChapterId);
        if (findObj) {
          findObj.list.push(e);
          findObj.list.sort(this.sortBy('textOrderNum', 1));
        } else {
          map.push({
            topChapterId: e.topChapterId,
            firstLevelId: e.topChapterId,
            firstLevelLabel: e.topChapterLabel,
            chapterOrderNum: e.blockIndex,
            list: [e]
          });
        }
      });
      return map;
    },
    useReaderSetting() {
      if (this.setting) {
        return Object.keys(this.setting).length ? this.setting : this.persistentReaderSetting;
      } else {
        return this.persistentReaderSetting;
      }
    }
  },
  provide() {
    return {
      getToolbarVisible: () => this.$refs.xmlToolbarPhone?.isShowToolbar || false,
      getKeyboardShow: () => this.keyboardShow,
      getSysEvn: () => this.env
    };
  },
  watch: {
    setting: {
      handler: function (newSetting, oldSetting) {
        if (oldSetting) {
          let {
            themeBgColor,
            themeFontSize,
            themeFontFamily,
            themeLineHeight,
            themeLetterSpacing
          } = newSetting;
          if (themeBgColor !== undefined && themeBgColor !== oldSetting?.themeBgColor) {
            this.setThemeColor(themeBgColor);
          }
          if (themeFontSize !== undefined && themeFontSize !== oldSetting?.themeFontSize) {
            this.setThemeFontSize(themeFontSize);
          }
          if (themeFontFamily !== undefined && themeFontFamily !== oldSetting?.themeFontFamily) {
            this.setThemeFontFamily(themeFontFamily);
          }
          if (themeLineHeight !== undefined && themeLineHeight !== oldSetting?.themeLineHeight) {
            this.setThemeLineHeight(themeLineHeight);
          }
          if (themeLetterSpacing !== undefined && themeLetterSpacing !== oldSetting?.themeLetterSpacing) {
            this.setThemeLetterSpacing(themeLetterSpacing);
          }
        }
      },
      immediate: true,
      deep: true
    },
    persistentReaderSetting: {
      handler: function (newSetting, oldSetting) {
        let {
          themeBgColor,
          themeFontSize,
          themeFontFamily,
          themeLineHeight,
          themeLetterSpacing
        } = newSetting;
        if (oldSetting) {
          if (themeBgColor !== undefined && themeBgColor !== oldSetting?.themeBgColor) {
            this.setThemeColor(themeBgColor);
          }
          if (themeFontSize !== undefined && themeFontSize !== oldSetting?.themeFontSize) {
            this.setThemeFontSize(themeFontSize);
          }
          if (themeFontFamily !== undefined && themeFontFamily !== oldSetting?.themeFontFamily) {
            this.setThemeFontFamily(themeFontFamily);
          }
          if (themeLineHeight !== oldSetting?.themeLineHeight) {
            this.setThemeLineHeight(themeLineHeight);
          }
          if (themeLetterSpacing !== oldSetting?.themeLetterSpacing) {
            this.setThemeLetterSpacing(themeLetterSpacing);
          }
        }
      },
      immediate: true,
      deep: true
    },
    schedule: {
      handler: function (newSetting, oldSetting) {
        this.currentProgress = Number(newSetting);
      },
      immediate: true,
      deep: true
    }
    // isShowTeacherResource(newVal) {
    //   this.localIsShowTeacherResource = newVal;
    // }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
  },
  created() {
    this.env = new systemEnvironmentVariables(this.systemEnvId, {
      userType: this.userType,
      pageType: this.pageType,
      isAddMark: this.isAddMark
    }).data || {};
    this.getPersistentSetting();
    this.queryData();
    this.$nextTick(() => {
      window.showImageBYDate = () => {
        target = window.event.target;
        if (target.isShow == true) return;
        target.isShow = true;
        $(target).find('img').click();
        setTimeout(() => {
          target.isShow = false;
        }, 1000);
        return false;
      };
      window.inlineAudioPlay = uuid => {
        target = window.event.target;
        if (target.isShow == true) return;
        target.isShow = true;
        let audio = document.getElementById(`audio${uuid}`);
        if (audio.paused) {
          audio.play();
          document.getElementById(`icon${uuid}`).className = 'wifi-symbol playing';
        } else {
          audio.pause();
          audio.currentTime = 0; //音乐从头播放
          document.getElementById(`icon${uuid}`).className = 'wifi-symbol';
        }
        setTimeout(() => {
          target.isShow = false;
        }, 1000);
        return false;
      };
    });
  },
  mounted() {
    this.$nextTick(() => {
      document.body.style.overflow = 'hidden'; //禁用ios浏览器橡皮筋效果
      this.xmlReaderWidth = this.$refs.xmlPreviewDom?.offsetWidth;
      // console.log(this.$refs.xmlPreviewDom., '1111111111111')
      setTimeout(() => {
        this.initObserver();
      }, 1500);
      window.document.oncontextmenu = function (e) {
        e.preventDefault();
      };
      let u = navigator.userAgent;
      this.isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); //ios终端
      this.textBookData.themeColor ? this.themeColor = this.textBookData.themeColor : this.themeColor = '#6883FA';
      if (this.textBookData.captionText) {
        document.title = this.textBookData.captionText;
      }
      let _this = this;
      this.$EventBus.$on('videoTimeUpdate', throttle$2(this.updateFREQUENCY, function (options) {
        // console.log('更新视频播放时长', options)
        let info = options.data || _this.currentGroupInfo;
        _this.$emit('learningStatistics', {
          type: 'video',
          firstLevelId: info.firstLevelId,
          firstLevelLabel: info.firstLevelLabel,
          locationChapterId: info.locationChapterId,
          locationLabel: info.locationLabel,
          chapterId: info.chapterId,
          label: info.label,
          title: info.title,
          coverFileUrl: info.coverFileUrl,
          materialRelationId: info.materialRelationId
        });
      }));
      this.$EventBus.$on('audioTimeUpdate', throttle$2(this.updateFREQUENCY, function (options) {
        console.log('更新音频播放时长', options);
        let info = options.data || _this.currentGroupInfo;
        _this.$emit('learningStatistics', {
          type: 'audio',
          firstLevelId: info.firstLevelId,
          firstLevelLabel: info.firstLevelLabel,
          locationChapterId: info.locationChapterId,
          locationLabel: info.locationLabel,
          chapterId: info.chapterId,
          label: info.label,
          title: info.title,
          coverFileUrl: info.coverFileUrl,
          materialRelationId: info.materialRelationId
        });
      }));
      this.$EventBus.$on('resetTimeUpdate', function (data) {
        const {
          statisticsId
        } = data;
        if (statisticsId) {
          _this.$set(_this.playTimeMap, statisticsId, undefined);
        }
      });
      this.$EventBus.$on('handleMagicLinkAudioPlay', function (data) {
        const {
          targetId,
          plaintext,
          title,
          content,
          type,
          isPlaying,
          callback
        } = data;
        const playerId = 'player__' + targetId.split('__')[1];
        if (!this.magicLinkAudio) {
          const audioElem = new Audio(content);
          audioElem.setAttribute('id', playerId);
          // audioElem.addEventListener("canplaythrough", event => {
          //     audioElem.play();
          //     typeof callback === 'function' && callback(audioElem)
          // });
          this.magicLinkAudio = audioElem;
          this.magicLinkAudio.play().then(() => $(`#${targetId}`).attr('data-isplaying', 'true'));
        } else {
          if (this.magicLinkAudio.id === playerId) {
            // 如果是同一个魔链音频, 仅切换播放状态
            if (this.magicLinkAudio.paused) {
              this.magicLinkAudio.play().then(() => $(`#${targetId}`).attr('data-isplaying', 'true'));
            } else {
              this.magicLinkAudio.pause();
            }
            $(`#${targetId}`).attr('data-isplaying', this.magicLinkAudio.paused ? 'false' : 'true');
          } else {
            const oldTarget = `#${this.magicLinkAudio.id.replace('player__', 'ml-audio__')}`;
            this.magicLinkAudio.pause();
            $(oldTarget).attr('data-isplaying', 'false');
            // 不是同一个魔链音频直接切换音频源
            this.magicLinkAudio.setAttribute('id', playerId);
            this.magicLinkAudio.setAttribute('src', content);
            this.magicLinkAudio.play().then(() => $(`#${targetId}`).attr('data-isplaying', 'true'));
          }
        }
      });
      this.$EventBus.$on('hotzoneClick', function (set) {
        const {
          type = '',
          value,
          label,
          desc = '',
          cover = ''
        } = set || {};
        _this.hotzoneTarget = {
          type,
          value,
          label,
          desc,
          cover
        };
        if (!type || !value) {
          that.$message.error('当前热区绑定非有效目标');
          return;
        }
        const modalTypeDict = ['richtext', 'image', 'audio', 'video'];
        if (type === 'looseleaf') {
          // 浏览活页
          // _this.viewerVisible = true
          let fileBaseUrl = _this.looseleafUrl + 'previewLooseLeaf?pageType=' + _this.pageType + '&businessType=looseLeaf' + '&looseLeafId=' + _this.hotzoneTarget.value + '&isTrial=' + true + '&uuid=' + v4();
          _this.openPage({
            url: fileBaseUrl,
            title: ''
          });
        }
        if (type === 'chapter') {
          // 浏览章节
          // _this.$message.error('暂不支持热区跳转章节')
          // _this.JumpPosition('xml-digital-teaching/' + value + '/' + 'placeholders', 50)
          let obj = _this.looseLeafData[value];
          if (obj && obj.looseLeafInfo && JSON.parse(obj.looseLeafInfo).length > 0) {
            let xmlCompRef = JSON.parse(obj.looseLeafInfo)[0].xmlCompRef;
            _this.JumpPosition({
              location: 'xml-digital-teaching/' + value + '/' + xmlCompRef,
              maxTop: -30,
              type: 'catalog',
              selectorType: '',
              selectorId: '',
              markTarget: '',
              classIndex: 0
            });
          }
        }
        if (type === 'link') {
          const re = /((http|ftp|https):\/\/[\w\-_]+(\.[\w\-_]+)+([\w\-\.,@?^=%&:/~\+#]*[\w\-\@?^=%&/~\+#])?)/;
          const result = re.test(value);
          if (!result) {
            _this.$message.error('当前热区绑定非有效链接');
            return;
          }
          _this.openPage({
            url: value,
            title: ''
          });
          // _this
          //   .$confirm(`当前热区绑定外部链接地址为：${value} ，确认打开？`, '', {
          //     customClass: 'mobile-fix-msgbox'
          //   })
          //   .then((_) => {
          //     window.open(value, '_blank')
          //     done()
          //   })
          //   .catch((_) => {})
        }
        if (modalTypeDict.includes(type)) {
          _this.hotTargetViewerVisible = true;
        }
      });

      // window.showleafletDate = uuid => {
      //   window.open(ENV_CONFIG.VUE_APP_TEXTBOOK_WEB + 'previewLooseLeaf?pageType=h5' + '&businessType=looseLeaf' + '&looseLeafId=' + uuid + '&isTrial=' + this.isTrial + '&uuid=' + uuidv4())
      // }
      window.showImageBYDate = uuid => {
        target = window.event.target;
        let imgs = $(target).find('img');
        if (!imgs.length) {
          imgs = $('#image__' + uuid) ? $('#image__' + uuid).find('img') : null;
        }
        let imgList = [];
        if (imgs) {
          imgs.each(function (img) {
            const title = $(this).attr('preview-text') || '';
            const src = $(this).prop('src') || '';
            imgList.push({
              url: src,
              title: title
            });
          });
        }
        this.$xmlImgPreview({
          multiple: true,
          nowImgIndex: 0,
          imgList
        });
      };
      window.inlineAudioPlay = uuid => {
        target = window.event.target;
        if (target.isShow == true) return;
        target.isShow = true;
        const audio = document.getElementById('audio' + uuid);
        if (audio.paused) {
          audio.play();
          document.getElementById('icon' + uuid).className = 'wifi-symbol playing';
        } else {
          audio.pause();
          audio.currentTime = 0; // 音乐从头播放
          document.getElementById('icon' + uuid).className = 'wifi-symbol';
        }
        setTimeout(() => {
          target.isShow = false;
        }, 1000);
        return false;
      };
      this.$EventBus.$on('addTeacherResources', function (model, rollback) {
        _this.$refs.addTeacherResources.open({
          ...model,
          textBookId: _this.textBookData.id,
          isDownload: 0,
          resourcesName: '',
          fileUrl: '',
          location: _this.currentLocation,
          chapterId: _this.componentList[_this.currentIndex].chapterId,
          orderNum: 0,
          firstLevelId: _this.componentList[_this.currentIndex].firstLevelId,
          firstLevelLabel: _this.componentList[_this.currentIndex].firstLevelLabel,
          chapterOrderNum: _this.componentList[_this.currentIndex].chapterOrderNum,
          textOrderNum: _this.getTextOrderNum(model),
          locationChapterId: _this.componentList[_this.currentIndex].locationChapterId,
          id: '',
          resourcesType: '',
          fileExt: '',
          isStudentShow: 0
        });
      });
      this.$EventBus.$on('openTeacherResources', function (e, markEfiStr) {
        let itemResources = _this.resourcesList.find(item => item.markEfiCode === e.markEfiStr);
        _this.openTeacherResoucesList(itemResources);
      });
      // @pos 添加笔记
      this.$EventBus.$on('createNote', function (model, rollback) {
        if (!_this.currentLocation) {
          _this.$message.error('匹配笔记位置失败');
          return;
        }
        if (_this.userType) {
          _this.$emit('addNote', {
            ...model,
            location: _this.currentLocation,
            topChapterLabel: _this.componentList[_this.currentIndex].firstLevelLabel,
            topChapterId: _this.componentList[_this.currentIndex].firstLevelId,
            locationChapterId: _this.componentList[_this.currentIndex].locationChapterId,
            chapterOrderNum: _this.componentList[_this.currentIndex].chapterOrderNum,
            textOrderNum: _this.getTextOrderNum(model),
            id: new Date().getTime()
          }, () => rollback(model.markId));
        }
      });
      this.$EventBus.$on('editStudentNote', function (model) {
        _this.$refs.modifyNotes.open(model);
      });
      this.$EventBus.$on('editNote', function (model) {
        if (_this.userType) {
          _this.$emit('editNote', {
            ...model
          });
        }
      });

      //  删除笔记
      this.$EventBus.$on('deleteNote', function (model, feedback) {
        console.log('🚀 ~ model:', model);
        if (_this.userType) {
          _this.$emit('deleteNote', {
            ...model
          }, () => feedback(model.markId));
          _this.$EventBus.$emit(`underlineSign-${model.location}`, {
            ...model,
            signType: 'underline',
            operationType: 'remove'
          });
        }
      });

      // 删除高亮
      this.$EventBus.$on('deleteHighlight', function (model, feedback) {
        _this.$emit('deleteHighlight', {
          ...model
        }, () => feedback(model.markId));
        _this.$EventBus.$emit(`underlineSign-${model.location}`, {
          ...model,
          signType: 'highlight',
          operationType: 'remove'
        });
      });
      // 删除划线
      this.$EventBus.$on('deleteLink', function (model, feedback) {
        _this.$emit('deleteLink', {
          ...model
        }, () => feedback(model.markId));
        _this.$EventBus.$emit(`underlineSign-${model.location}`, {
          ...model,
          signType: 'underline',
          operationType: 'remove'
        });
      });
      // 是否显示移动端提示气泡
      this.$EventBus.$on('selectionTextChange', function (data) {
        // console.log('🚀 ~ data:', {
        //   location: _this.currentLocation,
        //   topChapterLabel: _this.componentList[_this.currentIndex].firstLevelLabel,
        //   topChapterId: _this.componentList[_this.currentIndex].firstLevelId,
        //   locationChapterId: _this.componentList[_this.currentIndex].locationChapterId,
        //   chapterOrderNum: _this.componentList[_this.currentIndex].chapterOrderNum,
        //   textOrderNum: _this.getTextOrderNum(data.noteModel),
        //   id: new Date().getTime(),
        //   ...data.noteModel
        // })
        if (data.state) {
          _this.$refs.xmlToolbarPhone.isShowToolbar = false;
        }
        _this.isShowToolbarTip = data.state;
        _this.tippyTplBarId = data.tippyTplBarId;
      });
      this.$EventBus.$on('showToastTippy', function (data) {
        _this.showToast(data.msg, 'warning');
      });
      this.$EventBus.$on('xmlOpenLink', function (data) {
        _this.openPage({
          url: data.url,
          title: data.title
        });
      });

      // 删除书签
      this.$EventBus.$on('deleteBookmark', function (model, feedback) {
        _this.$emit('deleteBookmark', {
          ...model
        }, () => feedback(model.markId));
      });
      this.$EventBus.$on('createHighlight', function (model, rollback) {
        if (!_this.currentLocation) {
          _this.$message.error('匹配高亮位置失败');
          rollback(model.markId);
          return;
        }
        _this.$emit('addHighlight', {
          ...model,
          location: _this.currentLocation,
          topChapterLabel: _this.componentList[_this.currentIndex].firstLevelLabel,
          topChapterId: _this.componentList[_this.currentIndex].firstLevelId,
          locationChapterId: _this.componentList[_this.currentIndex].locationChapterId,
          chapterOrderNum: _this.componentList[_this.currentIndex].chapterOrderNum,
          textOrderNum: _this.getTextOrderNum(model),
          id: new Date().getTime()
        }, () => rollback(model.markId));
      });
      this.$EventBus.$on('createLink', function (model, rollback) {
        if (!_this.currentLocation) {
          _this.$message.error('匹配划线位置失败');
          rollback(model.markId);
          return;
        }
        _this.$emit('addLink', {
          ...model,
          location: _this.currentLocation,
          topChapterLabel: _this.componentList[_this.currentIndex].firstLevelLabel,
          topChapterId: _this.componentList[_this.currentIndex].firstLevelId,
          locationChapterId: _this.componentList[_this.currentIndex].locationChapterId,
          chapterOrderNum: _this.componentList[_this.currentIndex].chapterOrderNum,
          textOrderNum: _this.getTextOrderNum(model),
          id: new Date().getTime()
        }, () => rollback(model.markId));
      });

      // 更新学习统计情况
      this.$EventBus.$on('learningStatistics', function (model) {
        let info = model.info || _this.currentGroupInfo;
        _this.$emit('learningStatistics', {
          type: model.type,
          firstLevelId: info.firstLevelId,
          firstLevelLabel: info.firstLevelLabel,
          locationChapterId: info.locationChapterId,
          locationLabel: info.locationLabel,
          chapterId: info.chapterId,
          label: info.label
        });
      });
      function isMobile() {
        return 'ontouchstart' in window || 'onmsgesturechange' in window;
      }
      const diffThreshold = 50;
      const originHeight = document.documentElement.clientHeight || document.body.clientHeight;
      function handleMobileKeyboard() {
        const resizeHeight = document.documentElement.clientHeight || document.body.clientHeight;
        if (Math.abs(originHeight - resizeHeight) > diffThreshold) {
          _this.keyboardShow = true;
        } else {
          _this.keyboardShow = false;
        }
      }
      if (isMobile()) {
        window.addEventListener('resize', handleMobileKeyboard);
      }
      this.$once('hook:beforeDestroy', () => {
        _this.$EventBus.$off('createNote');
        _this.$EventBus.$off('createHighlight');
        _this.$EventBus.$off('createLink');
        _this.$EventBus.$off('addTeacherResources');
        _this.$EventBus.$off('openTeacherResources');
        isMobile() && window.removeEventListener('resize', handleMobileKeyboard);
      });

      // 监听键盘事件，选中内容大于200字符时禁止复制
      document.addEventListener('keydown', function (event) {
        let text = '';
        if (event.ctrlKey === true && event.which === 67) {
          //67：ctrl + C
          if (document.Selection) {
            //ie浏览器
            text = document.selection.createRange().text || '';
          } else {
            //标准浏览器
            text = window.getSelection().toString() || '';
          }
          if (text.length > 200) {
            event.preventDefault();
          }
        }
      }, false);
    });
    // try {
    //   // 将未标记图像路径转换为 base64
    //   this.bookmarkIconSrc = convertImageToBase64(bookmarkIconSrc);
    // } catch (error) {
    //   console.error('notbase64:', error);
    // }
    // if (this.pageType == 'h5') {
    //   window.addEventListener('scroll', this.updateScrollTop)
    // }

    // this.$EventBus.$on('addTeacherResources', function (model, rollback) {
    //   // this.$emit('addTeacherResources', model, rollback)
    //   console.log(addTeacherResources)
    // }),
  },
  methods: {
    // @pos 过滤当前章节的教师笔记
    filterTeacherNoteList(targetChapterId) {
      if (this.isShowTeacherResource) {
        return this.resourcesList.filter(item => {
          // let chapterId = item.location.split('/')[1]
          return item.locationChapterId === targetChapterId;
        });
      }
    },
    filterStudentNoteList(targetChapterId) {
      // @todo locationChapterId数据不准确, 待修复后校验此处处理
      return this.notesList.filter(item => {
        // let chapterId = item.location.split('/')[1]
        return item.locationChapterId === targetChapterId;
      });
    },
    filterHighlightList(targetChapterId) {
      // @todo locationChapterId数据不准确, 待修复后校验此处处理
      return this.highlightList.filter(item => {
        // let chapterId = item.location.split('/')[1]
        return item.locationChapterId === targetChapterId;
      });
    },
    getPersistentSetting() {
      this.persistentReaderSetting = JSON.parse(localStorage.getItem('reader-setting_#_' + this.textBookData.id)) || {
        themeBgColor: '',
        themeFontFamily: '',
        themeFontSize: 2,
        themeLetterSpacing: 0,
        themeLineHeight: 30,
        readingMode: this.loadingMode
      };
    },
    persistentSettingChange(key, value) {
      let newSetting = Object.assign({}, this.persistentReaderSetting, {
        [key]: value
      });
      localStorage.setItem('reader-setting_#_' + this.textBookData.id, JSON.stringify(newSetting));
      this.persistentReaderSetting = JSON.parse(localStorage.getItem('reader-setting_#_' + this.textBookData.id)) || {};
    },
    settingChange(key, value) {
      this.currentGroupInfo.readingMode = value;
      if (key == 'readingMode') {
        setTimeout(() => {
          this.$refs.xmlPreviewLooseLeaf.jumpTo({
            location: this.currentGroupInfo.location,
            maxTop: -30,
            type: 'catalog',
            selectorType: '',
            selectorId: '',
            markTarget: '',
            classIndex: 0,
            componentIndex: this.currentGroupInfo.componentIndex
          });
        }, 200);
      }
      this.$emit('settingChange', key, value);
      this.persistentSettingChange(key, value);
      setTimeout(() => {
        // @todo 处理样式变更引发的重排或重绘后笔记位置的同步更新
        this.$refs.xmlPreviewLooseLeaf.previewRepaint();
      }, 300);
    },
    clickToolbarPhone(e) {
      if (e.name == '设置') {
        this.$refs.xmlPreviewLooseLeaf.getPaddingTop();
      }
    },
    setThemeSecondaryColor(bgColor) {
      let color = tinycolor(bgColor);
      if (!color.isValid()) {
        document.getElementById('xmlPreviewDom').setAttribute('data-theme-secondary-color', '');
        document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-secondary-color');
        return;
      }
      let secondaryColor = '';
      if (color.getLuminance() < 0.4) {
        secondaryColor = color.clone().lighten(15).toString();
      } else {
        secondaryColor = color.clone().darken(5).toString();
      }
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-secondary-color', secondaryColor);
      document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-secondary-color', secondaryColor);
    },
    setThemeFontColor(darkColor) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-font-color', darkColor);
      darkColor !== '' ? document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-font-color', '#FEFEFE') : document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-font-color');
    },
    setThemeLinkColor(darkColor) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-link-color', darkColor);
      darkColor !== '' ? document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-link-color', '#23ABF2') : document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-link-color');
    },
    setThemeColor(bgColor) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-bg-color', bgColor);
      let color = tinycolor(bgColor),
        darkThemeBgColor = '';
      color.isValid() ? document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-color', color.toString()) : document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-color');
      this.setThemeSecondaryColor(bgColor);
      if (color.isValid() && color.getLuminance() < 0.25) {
        darkThemeBgColor = color.toString();
      }
      this.setThemeFontColor(darkThemeBgColor);
      this.setThemeLinkColor(darkThemeBgColor);
    },
    setThemeFontSize(size) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-font-size', size == '2' ? '' : size);
      document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-font-size', fontSizeValueMap[size]);
      let moduleAttribute = this.textBookData.moduleAttributeInfo || '[]';
      if (JSON.parse(moduleAttribute)?.length) {
        let i = JSON.parse(moduleAttribute).findIndex(item => item.type === 'freeOptions' && item.target === 'textbook');
        let newValue = JSON.parse(moduleAttribute)[i]?.data || {};
        document.getElementById('xmlPreviewDom').style.setProperty('--book-padding', (newValue.padding ? newValue.padding : '15') + 'px');
        document.getElementById('xmlPreviewDom').style.setProperty('--book-marginTop', newValue.marginTop + 'px');
        // JSON.parse(moduleAttribute).forEach((module) => {
        //   if (module.type === 'component' && module.target === 'xml-single-text-group') {
        //     if (module.data?.injectStyle) {
        //       injectStyle += module.data.injectStyle
        //     }
        //   }
        // })
      }
      // if(this.userType === 'student' && isMobile()) {
      // }
    },
    setThemeFontFamily(firstFamily) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-font-family', firstFamily);
      firstFamily === '' ? document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-font-family') : document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-font-family', firstFamily);
      // if(this.userType === 'student' && isMobile()) {
      // }
    },
    setThemeLineHeight(lineHeight) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-line-height', lineHeight ? lineHeight + 'px' : '');
      lineHeight === undefined ? document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-line-height') : document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-line-height', lineHeight + 'px');
    },
    setThemeLetterSpacing(letterSpacing) {
      document.getElementById('xmlPreviewDom').setAttribute('data-theme-letter-spacing', letterSpacing ? letterSpacing + 'px' : '');
      letterSpacing === undefined ? document.getElementById('xmlPreviewDom').style.removeProperty('--reader-theme-letter-spacing') : document.getElementById('xmlPreviewDom').style.setProperty('--reader-theme-letter-spacing', letterSpacing + 'px');
    },
    clickdigital(e) {
      let nodeName = 'IMG,A,Video';
      if (nodeName.indexOf(e.target.nodeName) == -1) {
        this.$refs.xmlToolbarPhone.isShowToolbar = !this.$refs.xmlToolbarPhone.isShowToolbar;
      }
    },
    // 通过目录列表和活页列表获取数据
    queryData() {
      this.learningTime = parseInt(new Date().getTime() / 1000);
      this.pageTitle = this.textBookData.textBooksName;
      const filterMenu = (menuList, level, id) => {
        return menuList.filter(item => {
          return level <= this.catalogLevel;
        }).map(item => {
          let xmlCompRef = '';
          if (this.looseLeafData[item.id] && this.looseLeafData[item.id].looseLeafInfo && JSON.parse(this.looseLeafData[item.id].looseLeafInfo).length > 0) {
            xmlCompRef = JSON.parse(this.looseLeafData[item.id].looseLeafInfo)[0].xmlCompRef;
          }
          item = Object.assign({}, {
            ...item,
            locationChapterId: id ? id : item.id,
            xmlCompRef
          });
          if (item.children?.length) {
            let locationChapterId = id ? id : level == this.textBookMergeLevel ? item.id : '';
            item.children = filterMenu(item.children, level + 1, locationChapterId);
          }
          return item;
        });
      };
      this.textCatalog = filterMenu(this.catalogList, 1);
      this.contentList = [];
      const getItemByChild = (list, level, orderNum) => {
        for (let i = 0; i < list.length; i++) {
          let it = list[i];
          if (this.looseLeafData[it.id] && this.looseLeafData[it.id].looseLeafInfo) {
            if (JSON.parse(this.looseLeafData[it.id].looseLeafInfo).length > 0) {
              // 更新组件总数
              this.componentTotal += JSON.parse(this.looseLeafData[it.id].looseLeafInfo).length;
              this.$set(list[i], 'xmlCompRef', JSON.parse(this.looseLeafData[it.id].looseLeafInfo)[0].xmlCompRef);
              this.$set(list[i], 'chapterOrderNum', orderNum ? orderNum : i); //目录层级
              this.$set(list[i], 'catalogLevel', level); //目录层级
              this.looseLeafData[it.id].label = it.label;
              this.contentList.push(it);
              if (it.children) {
                getItemByChild(it.children, level + 1, orderNum ? orderNum : i);
              }
            }
          } else {
            this.$set(list[i], 'xmlCompRef', v4());
          }
        }
      };
      getItemByChild(this.catalogList, 1, '');
      // 处理数据格式

      this.chapterIndex = 0;
      this.formattedData(this.contentList[0].id); //@format
      this.selectId = this.contentList[0].id;
    },
    // 处理活页json数据信息 传入id获取数据解析为list
    formattedData(chapterId) {
      let list = JSON.parse(this.looseLeafData[chapterId].looseLeafInfo);
      let imageList = []; //图片列表
      let audioList = []; //音频列表
      let videoList = []; //视频列表
      let formulaList = []; //公式列表
      let interactionList = []; //互动列表
      let resourceList = []; //资源列表
      let questionList = []; //试题列表
      let testPaperList = []; //试卷列表
      // 设置一级标题的ID和名称。
      if (this.contentList[this.chapterIndex].catalogLevel == 1) {
        this.firstLevelLabel = this.looseLeafData[chapterId].label;
        this.firstLevelChapterId = chapterId;
      }
      // 设置当前位置的章节ID和名称。 若当前目录层级小于等于教材合并到目录的层级
      if (this.contentList[this.chapterIndex].catalogLevel <= this.textBookMergeLevel) {
        this.locationChapterId = chapterId;
        this.locationLabel = this.looseLeafData[chapterId].label;
      }
      let readIndex = Number(this.componentTotal * (this.isTrialIndex / 100)).toFixed(0); //可以试读到的组件index
      let startIndex = this.componentIndex;
      list.map(element => {
        let isTrialExternal = this.isTrial ? this.componentIndex < readIndex ? false : true : false;
        // 向数据里添加位置信息，第一位：章节Id 、第二位：模块Id
        let location = `xml-digital-teaching/${chapterId}/${element.xmlCompRef}`;
        this.$set(element, 'location', location);

        // 向数据里添加章节Id
        this.$set(element, 'chapterId', chapterId);
        this.$set(element, 'componentIndex', this.componentIndex);
        this.componentIndex++;
        this.$set(element, 'label', this.looseLeafData[chapterId].label); //章节名字
        this.$set(element, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
        this.$set(element, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
        this.$set(element, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
        this.$set(element, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
        this.$set(element, 'chapterOrderNum', this.contentList[this.chapterIndex].chapterOrderNum); //当前内容阅读所在目录顺序
        this.$set(element, 'startIndex', startIndex); //章节开始位置
        this.$set(element, 'endIndex', startIndex + list.length); //章节开始位置

        // 根据不同的组件类型进行处理
        if (element.componentsName == 'xml-single-text-group') {
          // 文本
          this.$set(element, 'Text', element.data.pGroupData.textData?.html); //文本内容
        } else if (element.componentsName == 'xml-single-image-group') {
          // 图片
          let textList = [];
          element.data.pGroupData.imageData.imageList.map((item, index) => {
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-image-title');
            this.$set(item, 'index', index);
            textList.push({
              title: item.title,
              titleClass: 'xml-image-title',
              index: index
            });
          });
          if (element.data.pGroupData.imageData.imgType != '0') {
            imageList = imageList.concat(element.data.pGroupData.imageData.imageList);
          }
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-music-group') {
          // 音频
          let textList = [];
          element.data.pGroupData.musicData.musicSetList.map((item, index) => {
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-audio-title');
            this.$set(item, 'index', index);
            textList.push({
              title: item.title,
              titleClass: 'xml-audio-title',
              index: index
            });
          });
          audioList = audioList.concat(element.data.pGroupData.musicData.musicSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-video-group') {
          // 视频
          let textList = [];
          element.data.pGroupData.videoData.videoSetList.map((item, index) => {
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-video-title');
            this.$set(item, 'index', index);
            textList.push({
              title: item.title,
              titleClass: 'xml-video-title',
              index: index
            });
          });
          videoList = videoList.concat(element.data.pGroupData.videoData.videoSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-resource-group') {
          // 资源

          let textList = [];
          element.data.pGroupData.resourceData.resourceSetList.map((item, index) => {
            let title = item.title || item.fileFormat;
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-resource-title');
            this.$set(item, 'index', index);
            this.$set(item, 'ilk', 'resource');
            textList.push({
              title: title,
              titleClass: 'xml-resource-title',
              index: index
            });
          });
          resourceList = resourceList.concat(element.data.pGroupData.resourceData.resourceSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-book-group') {
          // 电子书
          let textList = [];
          element.data.pGroupData.ebookData.ebookSetList.map((item, index) => {
            let title = item.title || item.name;
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-ebook-title');
            this.$set(item, 'index', index);
            this.$set(item, 'ilk', 'ebook');
            textList.push({
              title: title,
              titleClass: 'xml-ebook-title',
              index: index
            });
          });
          resourceList = resourceList.concat(element.data.pGroupData.ebookData.ebookSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-question-group') {
          // 试题

          let textList = [];
          element.data.pGroupData.questionData.questionSetList.map((item, index) => {
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-question-title');
            this.$set(item, 'index', index);
            textList.push({
              title: item.stem.content,
              titleClass: 'xml-question-title',
              index: index
            });
          });
          questionList = questionList.concat(element.data.pGroupData.questionData.questionSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-test-paper-group') {
          // 试卷
          let textList = [];
          element.data.pGroupData.testPaperData.testPaperSetList.map((item, index) => {
            this.$set(item, 'chapterId', chapterId); //章节Id
            this.$set(item, 'label', this.looseLeafData[chapterId].label); //章节名字
            this.$set(item, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
            this.$set(item, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
            this.$set(item, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
            this.$set(item, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
            this.$set(item, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
            this.$set(item, 'location', location);
            this.$set(item, 'titleClass', 'xml-paper-title');
            this.$set(item, 'index', index);
            textList.push({
              title: item.paperName,
              titleClass: 'xml-paper-title',
              index: index
            });
          });
          testPaperList = testPaperList.concat(element.data.pGroupData.testPaperData.testPaperSetList);
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-hotzone-group') {
          //热区
          let textList = [];
          element.data.pGroupData.xmlBlockData.imageList.map((item, index) => {
            textList.push({
              title: item.title,
              titleClass: 'xml-hot-zone-title',
              index: index
            });
          });
          this.$set(element, 'textList', textList); //文本内容
        } else if (element.componentsName == 'xml-single-html-group') {
          this.$set(element, 'chapterId', chapterId); //章节Id
          this.$set(element, 'label', this.looseLeafData[chapterId].label); //章节名字
          this.$set(element, 'firstLevelId', this.firstLevelChapterId); //章节所在一级标题的Id
          this.$set(element, 'firstLevelLabel', this.firstLevelLabel); //章节所在一级标题的名称
          this.$set(element, 'locationChapterId', this.locationChapterId); //当前内容阅读所在目录id
          this.$set(element, 'locationLabel', this.locationLabel); //当前内容阅读所在目录名称
          this.$set(element.data.pGroupData.htmlData, 'isTrialExternal', isTrialExternal); //是否是试读之外的资源
          this.$set(element.data.pGroupData.htmlData, 'location', location);
          this.$set(element.data.pGroupData.htmlData, 'titleClass', 'xml-html-title');
          this.$set(element.data.pGroupData.htmlData, 'index', 0);
          // if (element.data.pGroupData.htmlData.code) {
          //   //代码块
          //   textList.push({ title: element.data.pGroupData.htmlData.code, titleClass: '', index: 0 })
          //   this.$set(element, 'textList', textList) //文本内容
          // }
          if (element.data.pGroupData.htmlData.htmlFileType == 'formula') {
            //公式
            formulaList.push(element.data.pGroupData.htmlData);
          } else if (element.data.pGroupData.htmlData.htmlFileType == 'pattern') {
            //虚拟仿真模型
            interactionList.push(element.data.pGroupData.htmlData);
          }
        } else if (element.componentsName.indexOf('title') !== -1) {
          //标题
          this.$set(element, 'Text', element.data.pGroupData.titleTextData?.html); //文本内容
          if (element.data.pGroupData.numberTextData) {
            element.Text += element.data.pGroupData.numberTextData.html;
          }
        } else if (element.componentsName.indexOf('paragraph') !== -1) {
          //段落
          this.$set(element, 'Text', ''); //文本内容
          for (let item in element.data.pGroupData) {
            if (Array.isArray(element.data.pGroupData[item])) {
              for (let index in element.data.pGroupData[item]) {
                if (element.data.pGroupData[item][index].html) {
                  element.Text += element.data.pGroupData[item][index].html;
                }
              }
            } else if (element.data.pGroupData[item].html) {
              element.Text += element.data.pGroupData[item].html;
            }
          }
        } else if (element.componentsName.indexOf('imageText') !== -1) {
          //图文
          this.$set(element, 'Text', ''); //文本内容
          for (let item in element.data.pGroupData) {
            if (element.data.pGroupData[item].html) {
              element.Text += element.data.pGroupData[item].html;
            }
          }
        }
      });
      ({
        label: this.looseLeafData[chapterId].label,
        chapterId: chapterId,
        list: list
      });
      if (!this.isTrial || this.componentList.length < readIndex && this.isTrial) {
        //判断是否非试读模式或当前内容在可试读内
        // 组件数据添加到componentList
        let compList = [];
        if (this.componentList.length + list.length > readIndex && this.isTrial) {
          //如果当前章节内容累加进去之后会超出试读区域就截取试读区域内的内容
          let i = readIndex - this.componentList.length;
          compList = list.slice(0, i);
        } else {
          compList = list;
        }
        this.componentList = this.componentList.concat(compList);
      }

      //
      if (this.contentList[this.chapterIndex].catalogLevel == 1) {
        this.formattedResourceList(imageList, audioList, videoList, formulaList, interactionList, resourceList, questionList, testPaperList, this.firstLevelChapterId, this.firstLevelLabel, true);
      } else {
        this.formattedResourceList(imageList, audioList, videoList, formulaList, interactionList, resourceList, questionList, testPaperList, this.firstLevelChapterId, this.firstLevelLabel, false);
      }
      if (this.contentList.length - 1 > this.chapterIndex) {
        this.chapterIndex++;
        this.formattedData(this.contentList[this.chapterIndex].id);
      } else {
        //数据遍历结束
        let locationList = this.location ? this.location.split('/') : [];
        locationList.length > 3 ? this.scrollId = locationList[3] : this.scrollId = this.location;
        if (locationList.length > 0 && locationList[1] != this.contentList[0].id && this.chapterId == '') {
          if (this.continueReading && !this.readOnly && JSON.stringify(this.componentList).indexOf(this.location) !== -1) {
            //是否展示继续阅读弹窗
            this.continueRead = true;
          }
        } else if (this.chapterId) {
          setTimeout(() => {
            for (let i = 0; i < this.componentList.length; i++) {
              if (this.componentList[i].chapterId == this.chapterId) {
                let data = {
                  location: this.componentList[i].location,
                  maxTop: -30,
                  type: 'catalog',
                  selectorType: '',
                  selectorId: '',
                  markTarget: '',
                  classIndex: 0
                };
                this.$refs.xmlPreviewLooseLeaf.jumpTo({
                  componentIndex: i,
                  ...data
                });
                return;
              }
            }
          }, 500);
        }
      }
      this.currentGroupInfo = this.componentList[0] || {};
    },
    // 初始化资源列表
    formattedResourceList(imageList, audioList, videoList, formulaList, interactionList, resourceList, questionList, testPaperList, chapterId, label, state) {
      if (imageList.length > 0 && JSON.stringify(this.textBookResource.imageList).indexOf(chapterId) == -1) {
        this.textBookResource.imageList.push({
          chapterId: chapterId,
          imageList: imageList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (imageList.length > 0) {
        let i = this.textBookResource.imageList.length - 1;
        this.textBookResource.imageList[i].imageList = this.textBookResource.imageList[i].imageList.concat(imageList);
      }
      if (audioList.length > 0 && JSON.stringify(this.textBookResource.audioList).indexOf(chapterId) == -1) {
        this.textBookResource.audioList.push({
          chapterId: chapterId,
          audioList: audioList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (audioList.length > 0) {
        let i = this.textBookResource.audioList.length - 1;
        this.textBookResource.audioList[i].audioList = this.textBookResource.audioList[i].audioList.concat(audioList);
      }
      if (videoList.length > 0 && JSON.stringify(this.textBookResource.videoList).indexOf(chapterId) == -1) {
        this.textBookResource.videoList.push({
          chapterId: chapterId,
          videoList: videoList,
          label: label,
          state: this.textBookResource.videoList.length == 0 ? true : false
        });
      } else if (videoList.length > 0) {
        let i = this.textBookResource.videoList.length - 1;
        this.textBookResource.videoList[i].videoList = this.textBookResource.videoList[i].videoList.concat(videoList);
      }
      if (formulaList.length > 0 && JSON.stringify(this.textBookResource.formulaList).indexOf(chapterId) == -1) {
        this.textBookResource.formulaList.push({
          chapterId: chapterId,
          formulaList: formulaList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (formulaList.length > 0) {
        let i = this.textBookResource.formulaList.length - 1;
        this.textBookResource.formulaList[i].formulaList = this.textBookResource.formulaList[i].formulaList.concat(formulaList);
      }
      if (interactionList.length > 0 && JSON.stringify(this.textBookResource.interactionList).indexOf(chapterId) == -1) {
        this.textBookResource.interactionList.push({
          chapterId: chapterId,
          interactionList: interactionList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (interactionList.length > 0) {
        let i = this.textBookResource.interactionList.length - 1;
        this.textBookResource.interactionList[i].interactionList = this.textBookResource.interactionList[i].interactionList.concat(interactionList);
      }
      if (resourceList.length > 0 && JSON.stringify(this.textBookResource.resourceList).indexOf(chapterId) == -1) {
        this.textBookResource.resourceList.push({
          chapterId: chapterId,
          resourceList: resourceList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (resourceList.length > 0) {
        let i = this.textBookResource.resourceList.length - 1;
        this.textBookResource.resourceList[i].resourceList = this.textBookResource.resourceList[i].resourceList.concat(resourceList);
      }
      if (questionList.length > 0 && JSON.stringify(this.textBookResource.questionList).indexOf(chapterId) == -1) {
        this.textBookResource.questionList.push({
          chapterId: chapterId,
          questionList: questionList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (questionList.length > 0) {
        let i = this.textBookResource.questionList.length - 1;
        this.textBookResource.questionList[i].questionList = this.textBookResource.questionList[i].questionList.concat(questionList);
      }
      if (testPaperList.length > 0 && JSON.stringify(this.textBookResource.testPaperList).indexOf(chapterId) == -1) {
        this.textBookResource.testPaperList.push({
          chapterId: chapterId,
          testPaperList: testPaperList,
          label: label,
          state: this.textBookResource.imageList.length == 0 ? true : false
        });
      } else if (testPaperList.length > 0) {
        let i = this.textBookResource.testPaperList.length - 1;
        this.textBookResource.testPaperList[i].testPaperList = this.textBookResource.testPaperList[i].testPaperList.concat(testPaperList);
      }
      this.textBookResource.imageTotal += imageList.length;
      this.textBookResource.audioTotal += audioList.length;
      this.textBookResource.videoTotal += videoList.length;
      this.textBookResource.formulaTotal += formulaList.length;
      this.textBookResource.interactionTotal += interactionList.length;
      this.textBookResource.resourceTotal += resourceList.length;
      this.textBookResource.questionTotal += questionList.length;
      this.textBookResource.testPaperTotal += testPaperList.length;
    },
    // 查询元素是否在可视窗口
    initObserver() {
      let _this = this;
      this.videoDomList = document.querySelectorAll('video') || [];
      this.audioDomList = document.querySelectorAll('aodio') || [];
      let ADom = document.getElementsByTagName('a');
      if (ADom) {
        for (let i = 0; i < ADom.length; i++) {
          if (JSON.stringify(this.releaseUrlList).indexOf(ADom[i].href) == -1) {
            ADom[i].onclick = function (e) {
              if (_this.pageType == 'pc' && _this.linkOpenLabel) {
                window.open(ADom[i].href, 'mozillaTab');
              } else {
                if (!_this.isOpenHyperlink) {
                  _this.downloadFileLink = ADom[i].href;
                  _this.$refs.downFileUppop.prompt = '受小程序限制该网址无法直接打开，请复制至手机浏览器观看';
                  setTimeout(() => {
                    _this.$refs.downFileUppop.isShow = true;
                  }, 200);
                } else {
                  _this.fileBaseUrl = ADom[i].href;
                  _this.fileBaseTitle = '';
                  _this.showPdf = true;
                }
              }
              return false;
            };
          }
        }
      }
      let elems = document.querySelectorAll('.xml-parent-group-block');
      let rootElem = document.querySelector('.preview-counter-main');
      let rootRect = rootElem.getBoundingClientRect();
      let options = {
        root: rootElem,
        rootMargin: `0px 0px ${0 - rootRect.height + 160}px 0px`,
        threshold: 0
      };
      this.scrollObserver = new IntersectionObserver(entries => {
        entries.forEach(entrie => {
          // 遍历entries数组
          if (entrie.isIntersecting) {
            // console.log(entrie)
            entrie.target.id.split('scroll-item-')[1];
            // 当前元素可见
            let boxId = entrie.target.classList[2];
            this.selectId = boxId.split('/').length > 2 ? boxId.split('/')[1] : this.selectId;
            if (boxId !== this.readLocation && boxId) {
              if (boxId.indexOf('xml-single-video-group') !== -1) {
                this.videoDomId = boxId;
              }
              if (boxId.indexOf('xml-single-audio-group') !== -1) {
                this.audioDomId = boxId;
              }
              if (boxId.indexOf('xml-digital-teaching') !== -1) {
                this.xmlDigitalTeaching = boxId;
              }
              if (boxId.indexOf('xml-single-image-group') !== -1) {
                //如果是图片
                let Dom = entrie.target.querySelector('.hua-lang-image-container');
                if (Dom) {
                  let scrollDom = Dom.querySelector('.hua-lang');
                  //如果是画廊
                  if (scrollDom) {
                    let time = parseInt((scrollDom.offsetWidth - this.xmlReaderWidth) / 50);
                    scrollDom.style.animation = `scroll ${time}s`;
                    Dom.classList.add('hua-lang-image-container-scroll');
                  }
                }
              }
              this.readLocation = boxId;
            }
            this.updateReadLocation();
          }
        });
      }, this.pageType == 'pc' ? options : {
        threshold: 0.01,
        rootMargin: `0px 0px ${0 - window.innerHeight + 60}px 0px`
      });
      if (elems) {
        elems.forEach(elem => {
          this.scrollObserver.observe(elem);
        });
      }
    },
    updateReadLocation() {
      clearTimeout(this.scrollTimer);
      let location = this.readLocation.indexOf('xml-digital-teaching') !== -1 ? this.readLocation : this.xmlDigitalTeaching + '/' + this.readLocation;
      let t1 = document.documentElement.scrollTop || document.body.scrollTop;
      let list = location.split('/');
      let locationId = list[0] + '/' + list[1] + '/' + list[2];
      let index = this.componentList.findIndex(value => value.location == locationId);
      this.currentGroupInfo = index !== -1 ? this.componentList[index] : this.currentGroupInfo;
      this.schedule = ((index + 1) / this.componentTotal * 100).toFixed(1);
      this.scrollTimer = setTimeout(() => {
        let t2 = document.documentElement.scrollTop || document.body.scrollTop;
        if (t2 == t1) {
          let learningTime = parseInt(new Date().getTime() / 1000) - this.learningTime;
          let locationData = {
            location: location,
            //当前阅读位置
            schedule: this.schedule,
            //当前阅读进度
            learningTime: learningTime,
            //本次学习时长
            learningDuration: this.learningDuration + learningTime,
            //总学习时长
            chapterId: this.currentGroupInfo.chapterId,
            chaptName: this.currentGroupInfo.label
          };
          this.$emit('updateReadLocation', locationData);
        }
      }, 1000);
    },
    // pc删除笔记
    deleteNotoPC(item, type) {
      if (type == 'notes') {
        this.$emit('deleteNotes', item);
      } else if (type == 'bookmark') {
        this.$emit('deleteBookmark', item);
      }
    },
    // 目录跳转指定章节
    catalogClick(obj) {
      this.showType = 'leaf';
      this.selectId = obj.id;
      this.JumpPosition({
        location: 'xml-digital-teaching/' + obj.id + '/' + obj.xmlCompRef,
        maxTop: -30,
        type: 'catalog',
        selectorType: '',
        selectorId: '',
        markTarget: '',
        classIndex: 0
      });
    },
    /**
     * 跳转到指定位置
     * @param {*} location 当前块的id
     * @param {*} maxTop 滚动距离后距离可视窗口顶部的距离
     * @param {*} type 跳转类型resource：资源，search：搜索，marker：标记
     * @param {*} selectorType 选择器selectorId的类型：id，class
     * @param {*} selectorId 选择器id或class值
     * @param {*} markTarget 笔记、高亮的item
     * @param {*} classIndex type为class时，用于滚动到第几个Dom
     */

    JumpPosition(data) {
      const {
        location,
        type,
        markTarget
      } = data;
      let list = location.split('/');
      let chapterId = list[1];
      if (this.isTrial) {
        if (this.componentList.findIndex(item => item.chapterId == chapterId) == -1) {
          this.showToast(this.trialPrompt, 'warning');
          return;
        }
      }
      let index = this.componentList.findIndex(item => item.location == location);
      setTimeout(() => {
        this.$refs.xmlPreviewLooseLeaf.jumpTo({
          componentIndex: index,
          ...data
        }, () => {
          if (markTarget) {
            setTimeout(() => {
              this.$EventBus.$emit(`markJumpIn-${markTarget.location}`, markTarget);
            }, 500);
          }
          if (type === 'search') {
            let Dom = document.getElementById(location);
            if (Dom) {
              this.searchHighlight(Dom);
            }
          }
        });
        this.currentGroupInfo = index !== -1 ? this.componentList[index] : this.currentGroupInfo;
        this.selectId = this.currentGroupInfo.chapterId;
        let learningTime = parseInt(new Date().getTime() / 1000) - this.learningTime;
        this.schedule = ((index + 1) / this.componentTotal * 100).toFixed(1);
        let locationData = {
          location: location,
          //当前阅读位置
          schedule: this.schedule,
          //当前阅读进度
          learningTime: learningTime,
          //本次学习时长
          learningDuration: this.learningDuration + learningTime //总学习时长
        };
        this.$emit('updateReadLocation', locationData);
      }, 300);
    },
    prevPage() {
      // this.$refs.xmlPreviewLooseLeaf
      if (this.currentGroupInfo.startIndex > 0) {
        let i = this.currentGroupInfo.startIndex - 1;
        console.log(i, 'aaa========', this.currentGroupInfo);
        this.$refs.xmlPreviewLooseLeaf.jumpTo({
          componentIndex: this.componentList[i].startIndex,
          maxTop: 0
        });
      }
    },
    nextPage() {
      if (this.currentGroupInfo.endIndex < this.componentList.length) {
        let i = this.currentGroupInfo.endIndex + 1;
        this.$refs.xmlPreviewLooseLeaf.jumpTo({
          componentIndex: this.componentList[i].startIndex,
          maxTop: 0
        });
      }
    },
    // 搜索内容高亮
    searchHighlight(Dom) {
      const keyword = this.seachInput.replace(/\s+/g, '');
      let context = new mark(Dom);
      context.mark(keyword, {
        className: 'xml-search-highlight'
      });
      setTimeout(() => {
        context.unmark();
      }, 3000);
    },
    // 打开教师资源列表
    openTeacherResoucesList(item) {
      if (this.pageType == 'pc') {
        this.$refs.xmlToolBarPc.clickToolbar(1);
        setTimeout(() => {
          this.$refs.xmlResourceListPc.scrollFun(item.firstLevelId, item.location);
        }, 200);
      } else {
        this.$refs.xmlToolbarPhone.openTeacherResoucesList(item);
      }
    },
    // 跳转到资源所在位置
    resourceDetails(data) {
      this.seachInput = data.seachText;
      this.JumpPosition(data);
    },
    // 获取当前点击的块
    clickComp(item) {
      this.currentLocation = item.location;
      this.currentIndex = item.componentIndex;
    },
    // 资源类型文件下载
    downloadFile(e) {
      let url = e.item.uploadFileUrl;
      if (e.item.uploadFileUrl.indexOf('./') !== -1) {
        url = this.basisPath + e.item.uploadFileUrl.split('./')[1];
      }
      if (this.downloadType == 'pop-up') {
        if (this.pageType == 'pc') {
          window.open(url);
        } else {
          this.downloadFileLink = url;
          this.$refs.downFileUppop.isShow = true;
        }
      } else {
        this.$emit('downloadFile', e);
      }
    },
    // 编辑教师笔记
    handleUpdateTeacherNotes(e) {
      this.$refs.addTeacherResources.open({
        isEdit: true,
        ...e
      });
    },
    // 保存修改的笔记
    saveNote(data) {
      this.$emit('editNote', data);
    },
    // 确认添加教师资源
    addResources(e) {
      this.$emit('addResources', e);
      this.$EventBus.$emit(`underlineSign-${e.location}`, {
        ...e,
        signType: 'underline',
        operationType: 'add'
      });
    },
    // 确认添加教师笔记
    addTeacherNote(e) {
      this.$emit('addTeacherNote', e);
      this.$EventBus.$emit(`underlineSign-${e.location}`, {
        ...e,
        signType: 'underline',
        operationType: 'add'
      });
    },
    underlineSign(e) {
      this.$EventBus.$emit(`underlineSign-${e.location}`, {
        ...model,
        signType: 'underline',
        operationType: 'add'
      });
    },
    // 编辑教师资源
    editTeachingResource(e) {
      this.$refs.addTeacherResources.open({
        isEdit: true,
        ...e
      });
    },
    // 确认编辑教师资源
    editResources(e) {
      this.$emit('editResources', e);
    },
    // 确认删除教师笔记
    deleteTeacherNotes(e) {
      this.$EventBus.$emit(`underlineSign-${e.location}`, {
        ...e,
        signType: 'underline',
        operationType: 'remove'
      });
      this.$emit('deleteResource', e);
    },
    // 确认删除教师资源
    deleteTeachingResource(e) {
      this.$EventBus.$emit(`underlineSign-${e.location}`, {
        ...e,
        signType: 'underline',
        operationType: 'remove'
      });
      this.$emit('deleteResource', e);
    },
    // 教师资源预览
    resourceReader(e) {
      let url = '';
      if (e.fileUrl.indexOf('.epub' !== -1) || e.fileUrl.indexOf('.pdf' !== -1)) {
        url = e.fileUrl;
      } else {
        url = this.officePreviewPath + e.fileUrl;
      }
      this.openPage({
        url: url,
        title: e.resourcesName
      });
      this.$refs.xmlToolbarPhone.isShowToolbar = false;
    },
    // 打开html包
    openHtml(data) {
      this.openPage({
        url: data.previewUrl,
        title: data.title
      });
      if (this.pageType !== 'pc') {
        this.$refs.xmlToolbarPhone.isShowToolbar = false;
      }
    },
    // 复制html链接
    copyHtmlUrl(data) {
      this.downloadFileLink = data.link;
      this.$refs.downFileUppop.isShow = true;
      this.$refs.downFileUppop.prompt = '受小程序限制该网址无法直接打开，请复制至手机浏览器观看';
    },
    // 添加书签
    addBookmark(location) {
      this.isBookmarked = !this.isBookmarked;
      this.isBookmarkAddSuccess = !this.isBookmarkAddSuccess;
      let bookmarkText = '';
      if (this.currentGroupInfo.textList && this.currentGroupInfo.textList.length > 0) {
        bookmarkText = this.currentGroupInfo.textList[0].title || this.currentGroupInfo.label;
      } else if (this.currentGroupInfo.Text) {
        bookmarkText = this.removeStyle(this.currentGroupInfo.Text) || this.currentGroupInfo.label;
      } else {
        bookmarkText = this.currentGroupInfo.label;
      }
      let data = {
        classId: this.classId,
        chapterId: this.currentGroupInfo.chapterId,
        firstLevelId: this.currentGroupInfo.firstLevelId,
        firstLevelLabel: this.currentGroupInfo.firstLevelLabel,
        orderNum: this.currentGroupInfo.componentIndex,
        locationChapterId: this.componentList[this.currentIndex].locationChapterId,
        chapterOrderNum: this.componentList[this.currentIndex].chapterOrderNum,
        type: 'bookmark',
        location: location,
        creationTime: new Date(),
        bookmarkId: new Date().getTime(),
        bookmarkText: bookmarkText,
        id: new Date().getTime()
      };
      this.$emit('addBookmark', data);
    },
    // 移除书签
    deleteBookmark(location) {
      let i = this.bookmarkList.findIndex(item => item.location === location);
      if (i !== -1) {
        this.$emit('deleteBookmark', this.bookmarkList[i]);
      }
    },
    // 资源阅读
    showFileReader(e) {
      let url = '';
      if (e.fileUrl.indexOf('.epub' !== -1) || e.fileUrl.indexOf('.pdf' !== -1)) {
        url = e.fileUrl;
      } else {
        url = this.officePreviewPath + e.fileUrl;
      }
      this.openPage({
        url: url,
        title: e.title
      });
    },
    // 复制文本
    CopyFileLkin() {
      let oInput = document.createElement('input');
      oInput.value = this.downloadFileLink;
      document.body.appendChild(oInput);
      oInput.select();
      document.execCommand('Copy');
      oInput.remove();
      this.showToast('复制成功', 'success');
    },
    // 打开试卷
    openTestPaper({
      info,
      showRobot
    }) {
      if (this.pageType == 'pc') {
        const url = this.testPaperPath + `/answer?testPaperId=${info.id}&showRobot=${showRobot}&BookName=${this.pageTitle}`;
        window.open(url);
      } else {
        this.fileBaseUrl = this.testPaperPath + `/mobile-answer?testPaperId=${info.id}&showRobot=${showRobot}&BookName=${this.pageTitle}`;
        this.fileBaseTitle = '';
        this.showPdf = true;
      }
    },
    openPage(e) {
      if (this.pageType == 'pc' && this.linkOpenLabel) {
        window.open(e.url, 'mozillaTab');
      } else {
        this.fileBaseUrl = e.url;
        this.fileBaseTitle = e.title || '';
        this.showPdf = true;
      }
    },
    //AI解析
    setanalysis(e) {
      this.$emit('setanalysis', e);
    },
    // 音视频移出窗口暂停
    moveWindowPause() {
      //如果当前窗口的是视频
      if (this.videoDomId) {
        let videoDom = document.getElementById(this.videoDomId);
        if (videoDom.getBoundingClientRect().top < -100 || videoDom.getBoundingClientRect().bottom > (window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight + 100)) {
          this.videoDomId = '';
          this.videoDomList.forEach(i => {
            i.pause();
          });
        }
      } else if (this.audioDomId) {
        //如果当前窗口的是视频
        let audioDom = document.getElementById(this.audioDomId);
        if (audioDom.getBoundingClientRect().top < -100 || audioDom.getBoundingClientRect().bottom > (window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight + 100)) {
          this.audioDomId = '';
          this.audioDomList.forEach(i => {
            i.pause();
          });
        }
      }
    },
    /**
     * 提示语
     * type
     * @param {*}  warning : 提示
     * @param {*}  error : 错误
     * @param {*}  success : 成功
     */
    showToast(text, type) {
      this.showPrompt = true;
      this.promptText = text;
      this.toastType = type ? type : 'warning';
      setTimeout(() => {
        this.showPrompt = false;
      }, 2000);
    },
    // pc端切换工具栏
    clickToolbar(item) {
      this.resourceType = item.type;
      this.resourceIcon = item.icon;
    },
    removeStyle(e) {
      if (e) {
        e = e.replace(/<[^>]+>/g, ''); //去除所有标签，以纯文本展示
        e = e.replace(/\s/gi, ''); //去除空格
        e = e.replace(/&nbsp;/gi, ''); //去除空格
        return e;
      }
    },
    //计算文本在教材中的位置
    getTextOrderNum(e) {
      let paragraph = e.markEfiCode.split('mark(')[1].split('/')[0];
      let start = e.markEfiCode.split(':')[1].split(',')[0];
      let orderNum = e.blockIndex + paragraph.padStart(6, '0') + start.padStart(6, '0');
      return orderNum;
    },
    // 对象数组排序
    sortBy(attr, rev) {
      if (rev == undefined) {
        rev = 1;
      }
      return function (a, b) {
        a = a[attr];
        b = b[attr];
        if (a < b) {
          return rev * -1;
        }
        if (a > b) {
          return rev * 1;
        }
        return 0;
      };
    },
    bookmarkAddSuccess() {
      this.isBookmarkAddSuccess = !this.isBookmarkAddSuccess;
      if (this.isBookmarkAddSuccess) {
        this.$message.success('已添加书签');
      } else {
        this.$message.warning('书签已移除');
      }
    },
    openPcSet() {
      this.$refs.visible.visible = true;
      this.$refs.xmlPreviewLooseLeaf.getPaddingTop();
    },
    openStatistical() {
      this.$refs.xmlStatisticalChart.open();
    },
    openclassGrade() {
      this.$refs.classGrade.open();
    },
    joinClass() {
      this.$refs.classGrade.open();
      // if (this.gradesName == '') {
      //   this.$prompt(`邀请码：`, '加入班级', {
      //     confirmButtonText: '加入班级',
      //     showCancelButton:false,
      //     inputPlaceholder:'请输入班级邀请码',
      //     inputPattern: /^.+$/,
      //     inputErrorMessage: '请输入邀请码'
      //   })
      //     .then(({ value }) => {
      //       this.$emit('joinClass', { code: value })
      //     })
      //     .catch(() => {})
      // }
    },
    /**
     * 阅读模式
     * @param {string} type：
     *   - initial：初始值
     *   - immersive：沉浸式阅读，隐藏目录、资源列表
     */
    READER_MODE(type) {
      if (type == 'initial') {
        this.immersiveReader = false;
      } else if (type == 'immersive') {
        this.immersiveReader = true;
      }
    },
    submitAnswer(e) {
      let info = e.info || this.currentGroupInfo;
      this.$emit('learningStatistics', {
        type: 'question',
        firstLevelId: info.firstLevelId,
        firstLevelLabel: info.firstLevelLabel,
        locationChapterId: info.locationChapterId,
        locationLabel: info.locationLabel,
        chapterId: info.chapterId,
        label: info.label
      });
    }
  }
};

var __$_require_static_images_icons_books_highlight_ht_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_bookmark_ht_top_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_bookmark_ht_top_active_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_statistical_ht_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_grades_ht_02_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_set_ht_up_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_grades_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAAgCAYAAAB3j6rJAAAAAXNSR0IArs4c6QAABDdJREFUWEftlnuIVHUUxz/n7qyvnTuD4R9poWX0gsJA2dW5Y5klIQgZRP0hFfTASkpq52pmj1F67dyRxOhBUPQihCKQkESKBnbuKEGBIpJgmEFh9HDbO0ttu3tP/sbZdVZnxru6S/3R76/L73fO+X3O+X1/53eF/8iQZhxJJ79M4dYQvVyUyXVthcOB795/vvnUBZna0XVxLGZ9CCyOsMG+wHevi2DX1OQMkGlO16wWrK+Ai0566s8gRaA8KpLQjnI1MDEgiVTucxW5CQhVeabc0+dxMPv36enEU7mtIrJ2QkDsxTmHsJI9qmTLJXdTo3pOLIjj5QAX6Ala+2ZSyP71b4F8cqLUtynsLvvuLc3U1bwiWSvh2O0h4TwLpp4Zx9rb63furZ0fJVY75RUQbgB2BL678lxAEou62tWy3gKuaeQvwqbeopttCBJ3crsFWRYFxHa8N4DVCl+XfXeBCZpIex2qfMmpKoRAULNh0nyfFcROee8h3BXlJtiO9wWwFGFnUHRXsCQbswfaDgBXntBZH8iaIDFpO5892j8MYjteD5A8K0jcyT8o6OvGMbSY19ft7q9X3uT13qXhEIeAVpCNgZ95IZ7ylopg4EzGq3qLrmmIo0ZkEHtJfgYDethQAwdolRuDQubX2mgXdGxLDMT6dwGLgP4YQ1cc95/4wXZyGRDPVCOYNSfJR3cMnTOIcYyn8w+L6qvVID8JvBgqBVokREmLsgH0kmqveapccp+v6iOryrPA0cB3K+vnBVIJ6nibFZ5udmsUeaXsd64FUWNnO/knQQ3Ub4HvzmgAYp6JNlTXB6V1pmeNjIavb1vau9lSNlePYMTB3BIjtqDoflobKO7k7hRku5mzLJ3/R/e6b2rXE86WhUq4pzKnrAxK7o5IIMNGbUtyF1r9LXOx1BoMrSN/7nn8x3rZTp//UnJwSsvRqr4OxhhabrRjbJPpl+eGOrgTuAo4PqV12uxfCmtGPaJN/0eaHU29tXgq95CIvFZd6xfoVrCANDCpMi96X1Bc9/bp/iMg9qItKaxwBchM0OiAyjG12FUuuoWTWvHMW2W00nraZgOiZHpL7rZ6SVQ2jDv5bYI+MtYKjLaXdwK/816TcjKVu0xF7tFqm1dlXywcfLdn74bvG+0hCSe/StEPqga9wJExAs0Gpp/00Q2hYnpMwzFJwt+HtTNKrCOtGg61tMYW9hQeM2048pjhdNn9Ij4q10Z2gv2Ctbr2BRbbyX0HMheRXFDMrB9DsBHTRNp7TpWNY/TtI5QFwZ7MtxUN245nzm1OvYcoauBE2hvuqsdC1eWN/GItWEOh1S7oVmAyyvtByb17IkAatvdaODvtvYnyAHAo8F3TW8a9IpFAaio4Yj/eR/M/SOXo65W6mdCbHk3UGzLOdmdqZJw3iBruFEg8lb9dCONRPcfTTrHK5VLmYxPzH7R7Aj+Ga+t2AAAAAElFTkSuQmCC";

var __$_require_static_images_icons_books_highlight_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_bookmark_top_png__ = "data:image/png;base64,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";

var __$_require_static_images_icons_books_bookmark_top_active_svg__ = "data:image/svg+xml,%3Csvg%20width%3D%2234%22%20height%3D%2234%22%20viewBox%3D%220%200%2034%2034%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Ccircle%20cx%3D%2217%22%20cy%3D%2217%22%20r%3D%2217%22%20fill%3D%22%23E3F1FA%22%2F%3E%3Cg%20clip-path%3D%22url%28%23clip0_213_1676%29%22%3E%3Cpath%20d%3D%22M22.3333%209H11.6667C11.4309%209%2011.2048%209.09365%2011.0381%209.26035C10.8714%209.42705%2010.7778%209.65314%2010.7778%209.88889V24.1111C10.7782%2024.2626%2010.8174%2024.4115%2010.8916%2024.5436C10.9658%2024.6758%2011.0725%2024.7867%2011.2017%2024.866C11.3308%2024.9452%2011.4781%2024.9902%2011.6295%2024.9965C11.7808%2025.0029%2011.9313%2024.9704%2012.0667%2024.9022L17%2022.44L21.9333%2024.9022C22.0572%2024.9657%2022.1942%2024.9992%2022.3333%2025C22.4997%2025.0005%2022.663%2024.9543%2022.8044%2024.8667C22.9325%2024.7866%2023.0381%2024.6752%2023.1112%2024.543C23.1843%2024.4108%2023.2225%2024.2622%2023.2222%2024.1111V9.88889C23.2222%209.65314%2023.1286%209.42705%2022.9619%209.26035C22.7952%209.09365%2022.5691%209%2022.3333%209ZM20.2978%2014.9644L16.7422%2018.52C16.6596%2018.6033%2016.5613%2018.6694%2016.4529%2018.7146C16.3446%2018.7597%2016.2284%2018.7829%2016.1111%2018.7829C15.9938%2018.7829%2015.8776%2018.7597%2015.7693%2018.7146C15.6609%2018.6694%2015.5626%2018.6033%2015.48%2018.52L13.7022%2016.7422C13.5348%2016.5748%2013.4408%2016.3478%2013.4408%2016.1111C13.4408%2015.8744%2013.5348%2015.6474%2013.7022%2015.48C13.8696%2015.3126%2014.0966%2015.2186%2014.3333%2015.2186C14.57%2015.2186%2014.7971%2015.3126%2014.9644%2015.48L16.1111%2016.6356L19.0355%2013.7022C19.2029%2013.5348%2019.4299%2013.4408%2019.6667%2013.4408C19.9034%2013.4408%2020.1304%2013.5348%2020.2978%2013.7022C20.4651%2013.8696%2020.5592%2014.0966%2020.5592%2014.3333C20.5592%2014.57%2020.4651%2014.7971%2020.2978%2014.9644Z%22%20fill%3D%22%231681E5%22%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_213_1676%22%3E%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22white%22%20transform%3D%22translate%289%209%29%22%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E";

var __$_require_static_images_icons_books_set_up_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$2m = script$2m;
var __vue_render__$2m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-digital-teaching-preview",
      class: _vm.env.className,
      style: {
        "--preview-theme-color": _vm.themeColor,
        "--xml-reader-page-height":
          _vm.xmlReaderPageHeight - _vm.navBarHeight + "px",
        "--xml-reader-width": _vm.xmlReaderWidth + "px",
      },
    },
    [
      _vm.pageType == "pc" &&
      _vm.readerType !== "loose-leaf" &&
      _vm.env.theFirstLayout
        ? [
            _c(
              "div",
              {
                staticClass: "xml-digital-teaching-pc",
                class:
                  _vm.persistentReaderSetting.readingMode == "page"
                    ? "xml-reader-page-mode"
                    : "",
              },
              [
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !_vm.immersiveReader,
                        expression: "!immersiveReader",
                      },
                    ],
                    staticClass: "preview-container-pc-left",
                  },
                  [
                    _vm.businessType === "textBook" && !_vm.env.isNoteHighlight
                      ? _c("xml-preview-catalog", {
                          ref: "xmlPreviewCatalog",
                          staticClass: "pc-catalog pc-catalog--render",
                          attrs: {
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.textCatalog,
                            userType: _vm.userType,
                            componentList: _vm.componentList,
                            notesList: _vm.notesList,
                            checkedId: _vm.selectId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            textBookName: _vm.textBookData.textBooksName,
                            sysEnv: _vm.env,
                          },
                          on: {
                            onNodeClick: _vm.catalogClick,
                            deleteNotoPC: _vm.deleteNotoPC,
                          },
                        })
                      : _c("preview-left-student", {
                          ref: "xmlPreviewCatalog",
                          attrs: {
                            location: _vm.location,
                            userType: _vm.userType,
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.textCatalog,
                            bookmarkList: _vm.bookmarkData,
                            notesList: _vm.notesListData,
                            highlightList: _vm.highlightData,
                            linkList: _vm.linkData,
                            checkedId: _vm.selectId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            looseLeaf: _vm.looseLeafData,
                            resourcesList: _vm.teacherResourcesList,
                            textBookName: _vm.textBookData.textBooksName,
                            textBookResource: _vm.textBookResource,
                            sysEnv: _vm.env,
                            componentList: _vm.componentList,
                          },
                          on: {
                            onNodeClick: _vm.catalogClick,
                            deleteNotoPC: _vm.deleteNotoPC,
                            addBookmark: _vm.addBookmark,
                            deleteNote: _vm.deleteNote,
                            deleteBookmark: _vm.deleteBookmark,
                            deleteHighlight: _vm.deleteHighlight,
                            resourceDetails: _vm.JumpPosition,
                          },
                        }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    ref: "rightContent",
                    staticClass: "preview-counter-main-pc",
                  },
                  [
                    _vm.env.isHuaTeng
                      ? _c("div", { staticClass: "preview-counter-header" }, [
                          _c("div", { staticClass: "text-book-title" }, [
                            _c(
                              "div",
                              { staticClass: "xml-progress" },
                              [
                                _c("el-progress", {
                                  attrs: {
                                    percentage: Number(_vm.schedule),
                                    "stroke-width": 20,
                                    color: "#4d90f9",
                                    "define-back-color": "#ffffff",
                                    "text-color": "#4d90f9",
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c("div", { staticClass: "title" }, [
                              _vm._v(
                                "\n              " +
                                  _vm._s(_vm.currentGroupInfo.locationLabel) +
                                  "\n            "
                              ),
                            ]),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "header-right" }, [
                            _vm.userType === "student"
                              ? _c(
                                  "div",
                                  {
                                    staticClass: "set-up",
                                    on: {
                                      click: function ($event) {
                                        _vm.$refs.visiblea.visiblea = true;
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_icons_books_highlight_ht_png__,
                                        alt: "",
                                      },
                                    }),
                                    _vm._v(" "),
                                    _c("span", [_vm._v("图例")]),
                                  ]
                                )
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.isAddMark
                              ? _c("div", { staticClass: "set-up bookmark" }, [
                                  JSON.stringify(_vm.bookmarkList).indexOf(
                                    _vm.currentGroupInfo.location
                                  ) == -1
                                    ? _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_ht_top_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.addBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      })
                                    : _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_ht_top_active_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.deleteBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      }),
                                  _vm._v(" "),
                                  _c("span", [_vm._v("书签")]),
                                ])
                              : _vm._e(),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                on: {
                                  click: function ($event) {
                                    return _vm.openStatistical()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_statistical_ht_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("学习情况")]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                attrs: {
                                  title: _vm.gradesName
                                    ? _vm.gradesName
                                    : "请点击加入班级",
                                },
                                on: {
                                  click: function ($event) {
                                    return _vm.joinClass()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_grades_ht_02_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("班级")]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                on: {
                                  click: function ($event) {
                                    return _vm.openPcSet()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_set_ht_up_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("设置")]),
                              ]
                            ),
                          ]),
                        ])
                      : _c("div", { staticClass: "preview-counter-header" }, [
                          _c("div", { staticClass: "text-book-title" }, [
                            _vm._v(_vm._s(_vm.currentGroupInfo.locationLabel)),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "header-right" }, [
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                staticStyle: { color: "#70acff" },
                              },
                              [_vm._v(_vm._s(_vm.schedule) + "%")]
                            ),
                            _vm._v(" "),
                            _vm.gradesName
                              ? _c("div", { staticClass: "grades" }, [
                                  _c("img", {
                                    attrs: {
                                      src: __$_require_static_images_icons_books_grades_png__,
                                      alt: "",
                                    },
                                  }),
                                  _vm._v(" "),
                                  _c(
                                    "span",
                                    { attrs: { title: _vm.gradesName } },
                                    [_vm._v(_vm._s(_vm.gradesName))]
                                  ),
                                ])
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.userType === "student"
                              ? _c(
                                  "div",
                                  {
                                    staticClass: "set-up highlight",
                                    attrs: { title: "图例" },
                                    on: {
                                      click: function ($event) {
                                        _vm.$refs.visiblea.visiblea = true;
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_icons_books_highlight_png__,
                                        alt: "",
                                      },
                                    }),
                                  ]
                                )
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.userType === "student" && _vm.isAddMark
                              ? _c(
                                  "div",
                                  {
                                    staticClass: "set-up bookmark",
                                    attrs: { title: "书签" },
                                  },
                                  [
                                    JSON.stringify(_vm.bookmarkList).indexOf(
                                      _vm.currentGroupInfo.location
                                    ) == -1
                                      ? _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_books_bookmark_top_png__,
                                            alt: "",
                                          },
                                          on: {
                                            click: function ($event) {
                                              return _vm.addBookmark(
                                                _vm.currentGroupInfo.location
                                              )
                                            },
                                          },
                                        })
                                      : _c("img", {
                                          attrs: {
                                            src: __$_require_static_images_icons_books_bookmark_top_active_svg__,
                                            alt: "",
                                          },
                                          on: {
                                            click: function ($event) {
                                              return _vm.deleteBookmark(
                                                _vm.currentGroupInfo.location
                                              )
                                            },
                                          },
                                        }),
                                  ]
                                )
                              : _vm._e(),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                attrs: { title: "设置" },
                                on: {
                                  click: function ($event) {
                                    return _vm.openPcSet()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_set_up_png__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                          ]),
                        ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        ref: "xmlPreviewDom",
                        staticClass: "preview-counter-main preview-list-pc",
                        attrs: { id: "xmlPreviewDom" },
                      },
                      [
                        _c("xml-preview-loose-leaf", {
                          ref: "xmlPreviewLooseLeaf",
                          class: "preview-screen-" + _vm.pageType,
                          attrs: {
                            "business-type": _vm.businessType,
                            looseLeaf: _vm.looseLeafData,
                            CompList: _vm.componentList,
                            "page-type": _vm.pageType,
                            resourceBasisPath: _vm.basisPath,
                            officePreviewPath: _vm.officePreviewPath,
                            endIndex: _vm.endIndex,
                            isTrial: _vm.isTrial,
                            trialText: _vm.trialText,
                            isShowAi: _vm.isShowAi,
                            resourcesList: _vm.teacherResourcesList,
                            highlightList: _vm.highlightList,
                            studentNoteList: _vm.notesList,
                            teacherNoteList: _vm.teacherNoteList,
                            userType: _vm.userType,
                            readerType: _vm.readerType,
                            loadingMode:
                              _vm.persistentReaderSetting.readingMode,
                            isShowTeacherResource: _vm.isShowTeacherResource,
                          },
                          on: {
                            downloadFile: _vm.downloadFile,
                            showFileReader: _vm.showFileReader,
                            openTestPaper: _vm.openTestPaper,
                            setanalysis: _vm.setanalysis,
                            clickComp: _vm.clickComp,
                            openTeacherResoucesList:
                              _vm.openTeacherResoucesList,
                            initObserver: _vm.initObserver,
                            openHtml: _vm.openHtml,
                            submitAnswer: _vm.submitAnswer,
                          },
                        }),
                        _vm._v(" "),
                        _vm.persistentReaderSetting.readingMode == "page"
                          ? _c(
                              "div",
                              { staticClass: "preview-counter-footer" },
                              [
                                _c(
                                  "div",
                                  { staticClass: "xml-progress" },
                                  [
                                    _c("el-slider", {
                                      attrs: { disabled: true },
                                      model: {
                                        value: _vm.currentProgress,
                                        callback: function ($$v) {
                                          _vm.currentProgress = $$v;
                                        },
                                        expression: "currentProgress",
                                      },
                                    }),
                                  ],
                                  1
                                ),
                                _vm._v(" "),
                                _c("div", { staticClass: "btn-list" }, [
                                  _c(
                                    "div",
                                    {
                                      staticClass: "btn-item",
                                      on: { click: _vm.prevPage },
                                    },
                                    [
                                      _c("i", {
                                        staticClass: "el-icon-arrow-left",
                                      }),
                                    ]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "div",
                                    {
                                      staticClass: "btn-item",
                                      on: { click: _vm.nextPage },
                                    },
                                    [
                                      _c("i", {
                                        staticClass: "el-icon-arrow-right",
                                      }),
                                    ]
                                  ),
                                ]),
                              ]
                            )
                          : _vm._e(),
                      ],
                      1
                    ),
                  ]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !_vm.immersiveReader,
                        expression: "!immersiveReader",
                      },
                    ],
                    staticClass: "preview-container-pc-right",
                  },
                  [
                    _c(
                      "div",
                      { staticClass: "preview-toolbar-content" },
                      [
                        _c("xml-resource-list-pc", {
                          ref: "xmlResourceListPc",
                          attrs: {
                            textBookResource: _vm.textBookResource,
                            resourceType: _vm.resourceType,
                            resourceIcon: _vm.resourceIcon,
                            resourcesList: _vm.teacherResourcesList,
                            resourceBasisPath: _vm.basisPath,
                            isShowTeacherResource: _vm.isShowTeacherResource,
                            componentList: _vm.componentList,
                            userType: _vm.userType,
                          },
                          on: {
                            deleteTeachingResource: _vm.deleteTeachingResource,
                            deleteTeacherNotes: _vm.deleteTeacherNotes,
                            editTeachingResource: _vm.editTeachingResource,
                            resourceDetails: _vm.resourceDetails,
                            updateTeacherNotes: _vm.handleUpdateTeacherNotes,
                            PrewiewOff: _vm.resourceReader,
                          },
                        }),
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "xml-toolbar-pc-content" },
                      [
                        _c("xml-toolbar-pc", {
                          ref: "xmlToolBarPc",
                          attrs: {
                            textBookResource: _vm.textBookResource,
                            isShowTeacherResource: _vm.isShowTeacherResource,
                            sysEnv: _vm.env,
                          },
                          on: { clickToolbar: _vm.clickToolbar },
                        }),
                      ],
                      1
                    ),
                  ]
                ),
                _vm._v(" "),
                _vm.isShowAi == 0 && _vm.env.isShowAi
                  ? _c("xml-robot-pc", { attrs: { robotData: _vm.robotData } })
                  : _vm._e(),
              ],
              1
            ),
          ]
        : _vm._e(),
      _vm._v(" "),
      _vm.pageType == "pc" &&
      _vm.readerType !== "loose-leaf" &&
      _vm.env.theSecondLayout
        ? [
            _c(
              "div",
              { staticClass: "xml-digital-teaching-pc" },
              [
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !_vm.immersiveReader,
                        expression: "!immersiveReader",
                      },
                    ],
                    staticClass: "preview-container-pc-right",
                  },
                  [
                    _c(
                      "div",
                      { staticClass: "preview-toolbar-content" },
                      [
                        _c(
                          "div",
                          { staticClass: "xml-toolbar-pc-content" },
                          [
                            _c("xml-toolbar-pc", {
                              ref: "xmlToolBarPc",
                              attrs: {
                                textBookResource: _vm.textBookResource,
                                isShowTeacherResource:
                                  _vm.isShowTeacherResource,
                                sysEnv: _vm.env,
                              },
                              on: { clickToolbar: _vm.clickToolbar },
                            }),
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c("xml-resource-list-pc", {
                          ref: "xmlResourceListPc",
                          attrs: {
                            textBookResource: _vm.textBookResource,
                            resourceType: _vm.resourceType,
                            resourceIcon: _vm.resourceIcon,
                            resourcesList: _vm.teacherResourcesList,
                            resourceBasisPath: _vm.basisPath,
                            isShowTeacherResource: _vm.isShowTeacherResource,
                            componentList: _vm.componentList,
                            userType: _vm.userType,
                          },
                          on: {
                            deleteTeachingResource: _vm.deleteTeachingResource,
                            deleteTeacherNotes: _vm.deleteTeacherNotes,
                            editTeachingResource: _vm.editTeachingResource,
                            resourceDetails: _vm.resourceDetails,
                            updateTeacherNotes: _vm.handleUpdateTeacherNotes,
                            PrewiewOff: _vm.resourceReader,
                          },
                        }),
                      ],
                      1
                    ),
                  ]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    ref: "rightContent",
                    staticClass: "preview-counter-main-pc",
                  },
                  [
                    _vm.env.isHuaTeng
                      ? _c("div", { staticClass: "preview-counter-header" }, [
                          _c("div", { staticClass: "text-book-title" }, [
                            _c(
                              "div",
                              { staticClass: "xml-progress" },
                              [
                                _c("el-progress", {
                                  attrs: {
                                    percentage: Number(_vm.schedule),
                                    "stroke-width": 20,
                                    color: "#4d90f9",
                                    "define-back-color": "#ffffff",
                                    "text-color": "#4d90f9",
                                  },
                                }),
                              ],
                              1
                            ),
                            _vm._v(" "),
                            _c("div", { staticClass: "title" }, [
                              _vm._v(
                                "\n              " +
                                  _vm._s(_vm.currentGroupInfo.locationLabel) +
                                  "\n            "
                              ),
                            ]),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "header-right" }, [
                            _vm.userType === "student"
                              ? _c(
                                  "div",
                                  {
                                    staticClass: "set-up",
                                    on: {
                                      click: function ($event) {
                                        _vm.$refs.visiblea.visiblea = true;
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_icons_books_highlight_ht_png__,
                                        alt: "",
                                      },
                                    }),
                                    _vm._v(" "),
                                    _c("span", [_vm._v("图例")]),
                                  ]
                                )
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.userType === "student" && _vm.isAddMark
                              ? _c("div", { staticClass: "set-up bookmark" }, [
                                  JSON.stringify(_vm.bookmarkList).indexOf(
                                    _vm.currentGroupInfo.location
                                  ) == -1
                                    ? _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_ht_top_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.addBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      })
                                    : _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_ht_top_active_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.deleteBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      }),
                                  _vm._v(" "),
                                  _c("span", [_vm._v("书签")]),
                                ])
                              : _vm._e(),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                on: {
                                  click: function ($event) {
                                    return _vm.openStatistical()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_statistical_ht_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("学习情况")]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                attrs: {
                                  title: _vm.gradesName
                                    ? _vm.gradesName
                                    : "请点击加入班级",
                                },
                                on: {
                                  click: function ($event) {
                                    return _vm.joinClass()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_grades_ht_02_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("班级")]),
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                on: {
                                  click: function ($event) {
                                    return _vm.openPcSet()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_set_ht_up_png__,
                                    alt: "",
                                  },
                                }),
                                _vm._v(" "),
                                _c("span", [_vm._v("设置")]),
                              ]
                            ),
                          ]),
                        ])
                      : _c("div", { staticClass: "preview-counter-header" }, [
                          _c("div", { staticClass: "text-book-title" }, [
                            _vm._v(_vm._s(_vm.currentGroupInfo.locationLabel)),
                          ]),
                          _vm._v(" "),
                          _c("div", { staticClass: "header-right" }, [
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                staticStyle: { color: "#70acff" },
                              },
                              [_vm._v(_vm._s(_vm.schedule) + "%")]
                            ),
                            _vm._v(" "),
                            _vm.gradesName
                              ? _c("div", { staticClass: "grades" }, [
                                  _c("img", {
                                    attrs: {
                                      src: __$_require_static_images_icons_books_grades_png__,
                                      alt: "",
                                    },
                                  }),
                                  _vm._v(" "),
                                  _c(
                                    "span",
                                    { attrs: { title: _vm.gradesName } },
                                    [_vm._v(_vm._s(_vm.gradesName))]
                                  ),
                                ])
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.userType === "student"
                              ? _c(
                                  "div",
                                  {
                                    staticClass: "set-up highlight",
                                    on: {
                                      click: function ($event) {
                                        _vm.$refs.visiblea.visiblea = true;
                                      },
                                    },
                                  },
                                  [
                                    _c("img", {
                                      attrs: {
                                        src: __$_require_static_images_icons_books_highlight_png__,
                                        alt: "",
                                      },
                                    }),
                                  ]
                                )
                              : _vm._e(),
                            _vm._v(" "),
                            _vm.userType === "student"
                              ? _c("div", { staticClass: "set-up bookmark" }, [
                                  JSON.stringify(_vm.bookmarkList).indexOf(
                                    _vm.currentGroupInfo.location
                                  ) == -1
                                    ? _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_top_png__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.addBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      })
                                    : _c("img", {
                                        attrs: {
                                          src: __$_require_static_images_icons_books_bookmark_top_active_svg__,
                                          alt: "",
                                        },
                                        on: {
                                          click: function ($event) {
                                            return _vm.deleteBookmark(
                                              _vm.currentGroupInfo.location
                                            )
                                          },
                                        },
                                      }),
                                ])
                              : _vm._e(),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "set-up",
                                on: {
                                  click: function ($event) {
                                    return _vm.openPcSet()
                                  },
                                },
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: __$_require_static_images_icons_books_set_up_png__,
                                    alt: "",
                                  },
                                }),
                              ]
                            ),
                          ]),
                        ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        ref: "xmlPreviewDom",
                        staticClass: "preview-counter-main preview-list-pc",
                        attrs: { id: "xmlPreviewDom" },
                      },
                      [
                        _c("xml-preview-loose-leaf", {
                          ref: "xmlPreviewLooseLeaf",
                          class: "preview-screen-" + _vm.pageType,
                          attrs: {
                            "business-type": _vm.businessType,
                            looseLeaf: _vm.looseLeafData,
                            CompList: _vm.componentList,
                            "page-type": _vm.pageType,
                            resourceBasisPath: _vm.basisPath,
                            officePreviewPath: _vm.officePreviewPath,
                            endIndex: _vm.endIndex,
                            isTrial: _vm.isTrial,
                            trialText: _vm.trialText,
                            isShowAi: _vm.isShowAi,
                            resourcesList: _vm.teacherResourcesList,
                            highlightList: _vm.highlightList,
                            studentNoteList: _vm.notesList,
                            teacherNoteList: _vm.teacherNoteList,
                            userType: _vm.userType,
                            readerType: _vm.readerType,
                            loadingMode:
                              _vm.persistentReaderSetting.readingMode,
                            isShowTeacherResource: _vm.isShowTeacherResource,
                          },
                          on: {
                            downloadFile: _vm.downloadFile,
                            showFileReader: _vm.showFileReader,
                            openTestPaper: _vm.openTestPaper,
                            setanalysis: _vm.setanalysis,
                            clickComp: _vm.clickComp,
                            openTeacherResoucesList:
                              _vm.openTeacherResoucesList,
                            initObserver: _vm.initObserver,
                            openHtml: _vm.openHtml,
                            submitAnswer: _vm.submitAnswer,
                          },
                        }),
                      ],
                      1
                    ),
                  ]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !_vm.immersiveReader,
                        expression: "!immersiveReader",
                      },
                    ],
                    staticClass: "preview-container-pc-left",
                  },
                  [
                    _vm.businessType === "textBook" &&
                    _vm.userType === "teacher"
                      ? _c("xml-preview-catalog", {
                          ref: "xmlPreviewCatalog",
                          staticClass: "pc-catalog pc-catalog--render",
                          attrs: {
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.textCatalog,
                            userType: _vm.userType,
                            notesList: _vm.notesList,
                            checkedId: _vm.selectId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            textBookName: _vm.textBookData.textBooksName,
                            sysEnv: _vm.env,
                          },
                          on: {
                            onNodeClick: _vm.catalogClick,
                            deleteNotoPC: _vm.deleteNotoPC,
                          },
                        })
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.userType === "student"
                      ? _c("preview-left-student", {
                          ref: "xmlPreviewCatalog",
                          attrs: {
                            location: _vm.location,
                            userType: _vm.userType,
                            "page-type": _vm.pageType,
                            "style-type": _vm.styleType,
                            "text-book-id": _vm.textBookId,
                            "catalog-list": _vm.textCatalog,
                            bookmarkList: _vm.bookmarkData,
                            notesList: _vm.notesListData,
                            highlightList: _vm.highlightData,
                            linkList: _vm.linkData,
                            checkedId: _vm.selectId,
                            PreviewCatalog: _vm.PreviewCatalog,
                            looseLeaf: _vm.looseLeafData,
                            resourcesList: _vm.teacherResourcesList,
                            textBookName: _vm.textBookData.textBooksName,
                            textBookResource: _vm.textBookResource,
                            sysEnv: _vm.env,
                          },
                          on: {
                            onNodeClick: _vm.catalogClick,
                            deleteNotoPC: _vm.deleteNotoPC,
                            addBookmark: _vm.addBookmark,
                            deleteNote: _vm.deleteNote,
                            deleteBookmark: _vm.deleteBookmark,
                            deleteHighlight: _vm.deleteHighlight,
                            resourceDetails: _vm.JumpPosition,
                          },
                        })
                      : _vm._e(),
                  ],
                  1
                ),
                _vm._v(" "),
                _vm.isShowAi == 0 && _vm.env.isShowAi
                  ? _c("xml-robot-pc", { attrs: { robotData: _vm.robotData } })
                  : _vm._e(),
              ],
              1
            ),
          ]
        : (_vm.pageType == "h5" || _vm.pageType == "pad") &&
          _vm.readerType !== "loose-leaf"
        ? [
            _c(
              "div",
              { staticClass: "xml-digital-teaching-phone" },
              [
                _c(
                  "div",
                  {
                    ref: "xmlPreviewDom",
                    staticClass: "preview-counter-main preview-list",
                    attrs: { id: "xmlPreviewDom" },
                    on: {
                      click: function ($event) {
                        $event.stopPropagation();
                        return _vm.clickdigital($event)
                      },
                    },
                  },
                  [
                    _c("xml-preview-loose-leaf", {
                      ref: "xmlPreviewLooseLeaf",
                      class: "preview-screen-" + _vm.pageType,
                      attrs: {
                        "business-type": _vm.businessType,
                        looseLeaf: _vm.looseLeafData,
                        CompList: _vm.componentList,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.basisPath,
                        officePreviewPath: _vm.officePreviewPath,
                        endIndex: _vm.endIndex,
                        isTrial: _vm.isTrial,
                        trialText: _vm.trialText,
                        isShowAi: _vm.isShowAi,
                        resourcesList: _vm.teacherResourcesList,
                        highlightList: _vm.highlightList,
                        studentNoteList: _vm.notesList,
                        teacherNoteList: _vm.teacherNoteList,
                        userType: _vm.userType,
                        readerType: _vm.readerType,
                        loadingMode: _vm.persistentReaderSetting.readingMode,
                        groupHeight: 1000,
                        isShowTeacherResource: _vm.isShowTeacherResource,
                        isCopyUrl:
                          !_vm.isOpenHyperlink && _vm.pageType !== "pc",
                      },
                      on: {
                        downloadFile: _vm.downloadFile,
                        showFileReader: _vm.showFileReader,
                        openTestPaper: _vm.openTestPaper,
                        setanalysis: _vm.setanalysis,
                        clickComp: _vm.clickComp,
                        openTeacherResoucesList: _vm.openTeacherResoucesList,
                        openHtml: _vm.openHtml,
                        copyHtmlUrl: _vm.copyHtmlUrl,
                        initObserver: _vm.initObserver,
                        submitAnswer: _vm.submitAnswer,
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "toolbar-pop" },
                  [
                    _c("xml-toolbar-phone", {
                      ref: "xmlToolbarPhone",
                      attrs: {
                        textBookResource: _vm.textBookResource,
                        resourcesList: _vm.teacherResourcesList,
                        catalogList: _vm.textCatalog,
                        resourceBasisPath: _vm.basisPath,
                        componentList: _vm.componentList,
                        schedule: _vm.schedule,
                        showToast: _vm.showToast,
                        bookmarkList: _vm.bookmarkData,
                        notesList: _vm.notesListData,
                        highlightList: _vm.highlightData,
                        linkList: _vm.linkData,
                        location: _vm.currentGroupInfo.location,
                        title: _vm.currentGroupInfo.locationLabel,
                        checkedId: _vm.selectId,
                        isShowTeacherResource: _vm.isShowTeacherResource,
                        settingData: _vm.useReaderSetting,
                        sysEnv: _vm.env,
                        loadingMode: _vm.persistentReaderSetting.readingMode,
                        userType: _vm.userType,
                        textBookName: _vm.textBookData.textBooksName,
                        showToolbar: _vm.env.isShowToolbar,
                      },
                      on: {
                        resourceDetails: _vm.resourceDetails,
                        resourceReader: _vm.resourceReader,
                        onNodeClick: _vm.catalogClick,
                        addBookmark: _vm.addBookmark,
                        deleteBookmark: _vm.deleteBookmark,
                        settingChange: _vm.settingChange,
                        clickToolbarPhone: _vm.clickToolbarPhone,
                        prevPage: _vm.prevPage,
                        nextPage: _vm.nextPage,
                      },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.isShowToolbarTip && _vm.isiOS,
                        expression: "isShowToolbarTip && isiOS",
                      },
                    ],
                    staticClass: "xml-mobile-toolbar-tip",
                  },
                  [
                    _vm.env.tippy.copy
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "copy",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", { staticClass: "tipbar-icon copy" }),
                            _vm._v(" "),
                            _c("span", [_vm._v("复制")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.link
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "link",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", { staticClass: "tipbar-icon note" }),
                            _vm._v(" "),
                            _c("span", [_vm._v("划线")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.note
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "note",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", { staticClass: "tipbar-icon note" }),
                            _vm._v(" "),
                            _c("span", [_vm._v("笔记")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.highlight
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "highlight",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", { staticClass: "tipbar-icon highlight" }),
                            _vm._v(" "),
                            _c("span", [_vm._v("高亮")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.dictionary
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "dictionary",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", { staticClass: "tipbar-icon dictionary" }),
                            _vm._v(" "),
                            _c("span", [_vm._v("词典")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.encyclopedias
                      ? _c(
                          "div",
                          {
                            staticClass: "tipbar-action",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.$EventBus.$emit(
                                  "clickToolbarTip-" + _vm.tippyTplBarId,
                                  {
                                    type: "encyclopedias",
                                    tippyTplBarId: _vm.tippyTplBarId,
                                  }
                                )
                              },
                            },
                          },
                          [
                            _c("i", {
                              staticClass: "tipbar-icon encyclopedias",
                            }),
                            _vm._v(" "),
                            _c("span", [_vm._v("百科")]),
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.env.tippy.maxWordCount && !_vm.env.isHuaTeng
                      ? _c("span", { staticClass: "tipbar-remark" }, [
                          _vm._v("均<=200字"),
                        ])
                      : _vm._e(),
                  ]
                ),
                _vm._v(" "),
                _vm._m(0),
                _vm._v(" "),
                _vm.isShowAi == 0 && _vm.env.isShowAi
                  ? _c("xml-robot-phone", {
                      attrs: { robotData: _vm.robotData },
                    })
                  : _vm._e(),
              ],
              1
            ),
          ]
        : _vm.readerType === "loose-leaf"
        ? [
            _c(
              "div",
              {
                ref: "xmlPreviewDom",
                staticClass: "preview-counter-main preview-list-pc",
                attrs: { id: "xmlPreviewDom" },
              },
              [
                _c("xml-preview-loose-leaf", {
                  ref: "xmlPreviewLooseLeaf",
                  class: "preview-screen-" + _vm.pageType,
                  attrs: {
                    "business-type": _vm.businessType,
                    looseLeaf: _vm.looseLeafData,
                    CompList: _vm.componentList,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.basisPath,
                    officePreviewPath: _vm.officePreviewPath,
                    endIndex: _vm.endIndex,
                    isTrial: _vm.isTrial,
                    trialText: _vm.trialText,
                    isShowAi: _vm.isShowAi,
                    resourcesList: _vm.teacherResourcesList,
                    highlightList: _vm.highlightList,
                    studentNoteList: _vm.notesList,
                    teacherNoteList: _vm.teacherNoteList,
                    userType: _vm.userType,
                    readerType: _vm.readerType,
                    loadingMode: _vm.persistentReaderSetting.readingMode,
                    isShowTeacherResource: _vm.isShowTeacherResource,
                  },
                  on: {
                    downloadFile: _vm.downloadFile,
                    showFileReader: _vm.showFileReader,
                    openTestPaper: _vm.openTestPaper,
                    setanalysis: _vm.setanalysis,
                    clickComp: _vm.clickComp,
                    openTeacherResoucesList: _vm.openTeacherResoucesList,
                    initObserver: _vm.initObserver,
                    openHtml: _vm.openHtml,
                    submitAnswer: _vm.submitAnswer,
                  },
                }),
              ],
              1
            ),
          ]
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: _vm.continueRead,
              expression: "continueRead",
            },
          ],
          staticClass: "continue-read-box",
          class: _vm.pageType != "pc" ? "continue-read-h5" : "",
        },
        [
          _c("div", { staticClass: "notes-text-box" }, [
            _c("div", { staticClass: "title-box" }, [_vm._v("提示")]),
            _vm._v(" "),
            _c("div", { staticClass: "text" }, [
              _vm._v("检测到上次阅读位置,是否继续阅读."),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "add-notes-btn" }, [
              _c(
                "div",
                {
                  on: {
                    click: function ($event) {
                      _vm.continueRead = false;
                    },
                  },
                },
                [_vm._v("取消")]
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticClass: "confirm",
                  on: {
                    click: function ($event) {
                      _vm.JumpPosition({
                        location: _vm.location,
                        maxTop: -30,
                        type: "initial",
                        selectorType: "id",
                        selectorId: _vm.scrollId,
                        markTarget: "",
                        classIndex: 0,
                      }),
                        (_vm.continueRead = false);
                    },
                  },
                },
                [_vm._v("\n          确认\n        ")]
              ),
            ]),
          ]),
        ]
      ),
      _vm._v(" "),
      _c("down-file-uppop", {
        ref: "downFileUppop",
        attrs: {
          downloadFileLink: _vm.downloadFileLink,
          showToast: _vm.showToast,
        },
      }),
      _vm._v(" "),
      _c("XmlHotTargetModalViewer", {
        attrs: { visible: _vm.hotTargetViewerVisible, data: _vm.hotzoneTarget },
        on: {
          "update:visible": function ($event) {
            _vm.hotTargetViewerVisible = $event;
          },
        },
      }),
      _vm._v(" "),
      _c("add-file", {
        ref: "addTeacherResources",
        attrs: {
          actionUrl: _vm.action,
          headers: _vm.headers,
          fileSize: _vm.fileSize,
          data: _vm.data,
        },
        on: {
          addResources: _vm.addResources,
          editResources: _vm.editResources,
          addTeacherNote: _vm.addTeacherNote,
        },
      }),
      _vm._v(" "),
      _c("set-pop", {
        ref: "visible",
        attrs: { settingData: _vm.useReaderSetting },
        on: { settingChange: _vm.settingChange },
      }),
      _vm._v(" "),
      _c("highlight", { ref: "visiblea", attrs: { sysEnv: _vm.env } }),
      _vm._v(" "),
      _c("bookmark", { ref: "visibleb" }),
      _vm._v(" "),
      _c(
        "div",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: _vm.showPrompt,
              expression: "showPrompt",
            },
          ],
          staticClass: "xml-toast-prompt-box",
        },
        [
          _c("div", {
            staticClass: "prompt-text",
            class: _vm.toastType,
            domProps: { innerHTML: _vm._s(_vm.promptText) },
          }),
        ]
      ),
      _vm._v(" "),
      _c("modify-notes", {
        ref: "modifyNotes",
        on: { saveNote: _vm.saveNote },
      }),
      _vm._v(" "),
      _c("PdfView", {
        attrs: {
          pageType: _vm.pageType,
          show: _vm.showPdf,
          baseUrl: _vm.fileBaseUrl,
          textInfo: _vm.fileBaseTitle,
        },
        on: {
          handleClose: function ($event) {
            _vm.showPdf = false;
          },
        },
      }),
      _vm._v(" "),
      _c("xml-statistical-chart", {
        ref: "xmlStatisticalChart",
        attrs: {
          userType: _vm.userType,
          pageType: _vm.pageType,
          teacherDateclass: _vm.teacherDateclass,
          studentDate: _vm.studentData,
          teacherDate: _vm.teacherData,
          userInfoparms: _vm.userInfoparms,
        },
        on: {
          changeClass: function ($event) {
            return _vm.$emit("changeClass", $event)
          },
        },
      }),
      _vm._v(" "),
      _c("classGrade", {
        ref: "classGrade",
        attrs: {
          userType: _vm.userType,
          pageType: _vm.pageType,
          teacherDateclass: _vm.teacherDateclass,
          userInfoparms: _vm.userInfoparms,
        },
      }),
    ],
    2
  )
};
var __vue_staticRenderFns__$2m = [
  function () {
    var _vm = this;
    var _h = _vm.$createElement;
    var _c = _vm._self._c || _h;
    return _c("div", { staticClass: "font-pre-loader" }, [
      _c("div", { attrs: { id: "sim-sun" } }, [_vm._v("宋体")]),
      _vm._v(" "),
      _c("div", { attrs: { id: "sim-hei" } }, [_vm._v("黑体")]),
      _vm._v(" "),
      _c("div", { attrs: { id: "sim-kai" } }, [_vm._v("楷体")]),
    ])
  },
];
__vue_render__$2m._withStripped = true;

  /* style */
  const __vue_inject_styles__$2m = undefined;
  /* scoped */
  const __vue_scope_id__$2m = undefined;
  /* module identifier */
  const __vue_module_identifier__$2m = undefined;
  /* functional template */
  const __vue_is_functional_template__$2m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2m, staticRenderFns: __vue_staticRenderFns__$2m },
    __vue_inject_styles__$2m,
    __vue_script__$2m,
    __vue_scope_id__$2m,
    __vue_is_functional_template__$2m,
    __vue_module_identifier__$2m,
    false,
    undefined,
    undefined,
    undefined
  );

var hammer = createCommonjsModule(function (module) {
/*! Hammer.JS - v2.0.7 - 2016-04-22
 * http://hammerjs.github.io/
 *
 * Copyright (c) 2016 Jorik Tangelder;
 * Licensed under the MIT license */
(function (window, document, exportName, undefined$1) {

  var VENDOR_PREFIXES = ['', 'webkit', 'Moz', 'MS', 'ms', 'o'];
  var TEST_ELEMENT = document.createElement('div');
  var TYPE_FUNCTION = 'function';
  var round = Math.round;
  var abs = Math.abs;
  var now = Date.now;

  /**
   * set a timeout with a given scope
   * @param {Function} fn
   * @param {Number} timeout
   * @param {Object} context
   * @returns {number}
   */
  function setTimeoutContext(fn, timeout, context) {
    return setTimeout(bindFn(fn, context), timeout);
  }

  /**
   * if the argument is an array, we want to execute the fn on each entry
   * if it aint an array we don't want to do a thing.
   * this is used by all the methods that accept a single and array argument.
   * @param {*|Array} arg
   * @param {String} fn
   * @param {Object} [context]
   * @returns {Boolean}
   */
  function invokeArrayArg(arg, fn, context) {
    if (Array.isArray(arg)) {
      each(arg, context[fn], context);
      return true;
    }
    return false;
  }

  /**
   * walk objects and arrays
   * @param {Object} obj
   * @param {Function} iterator
   * @param {Object} context
   */
  function each(obj, iterator, context) {
    var i;
    if (!obj) {
      return;
    }
    if (obj.forEach) {
      obj.forEach(iterator, context);
    } else if (obj.length !== undefined$1) {
      i = 0;
      while (i < obj.length) {
        iterator.call(context, obj[i], i, obj);
        i++;
      }
    } else {
      for (i in obj) {
        obj.hasOwnProperty(i) && iterator.call(context, obj[i], i, obj);
      }
    }
  }

  /**
   * wrap a method with a deprecation warning and stack trace
   * @param {Function} method
   * @param {String} name
   * @param {String} message
   * @returns {Function} A new function wrapping the supplied method.
   */
  function deprecate(method, name, message) {
    var deprecationMessage = 'DEPRECATED METHOD: ' + name + '\n' + message + ' AT \n';
    return function () {
      var e = new Error('get-stack-trace');
      var stack = e && e.stack ? e.stack.replace(/^[^\(]+?[\n$]/gm, '').replace(/^\s+at\s+/gm, '').replace(/^Object.<anonymous>\s*\(/gm, '{anonymous}()@') : 'Unknown Stack Trace';
      var log = window.console && (window.console.warn || window.console.log);
      if (log) {
        log.call(window.console, deprecationMessage, stack);
      }
      return method.apply(this, arguments);
    };
  }

  /**
   * extend object.
   * means that properties in dest will be overwritten by the ones in src.
   * @param {Object} target
   * @param {...Object} objects_to_assign
   * @returns {Object} target
   */
  var assign;
  if (typeof Object.assign !== 'function') {
    assign = function assign(target) {
      if (target === undefined$1 || target === null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }
      var output = Object(target);
      for (var index = 1; index < arguments.length; index++) {
        var source = arguments[index];
        if (source !== undefined$1 && source !== null) {
          for (var nextKey in source) {
            if (source.hasOwnProperty(nextKey)) {
              output[nextKey] = source[nextKey];
            }
          }
        }
      }
      return output;
    };
  } else {
    assign = Object.assign;
  }

  /**
   * extend object.
   * means that properties in dest will be overwritten by the ones in src.
   * @param {Object} dest
   * @param {Object} src
   * @param {Boolean} [merge=false]
   * @returns {Object} dest
   */
  var extend = deprecate(function extend(dest, src, merge) {
    var keys = Object.keys(src);
    var i = 0;
    while (i < keys.length) {
      if (!merge || merge && dest[keys[i]] === undefined$1) {
        dest[keys[i]] = src[keys[i]];
      }
      i++;
    }
    return dest;
  }, 'extend', 'Use `assign`.');

  /**
   * merge the values from src in the dest.
   * means that properties that exist in dest will not be overwritten by src
   * @param {Object} dest
   * @param {Object} src
   * @returns {Object} dest
   */
  var merge = deprecate(function merge(dest, src) {
    return extend(dest, src, true);
  }, 'merge', 'Use `assign`.');

  /**
   * simple class inheritance
   * @param {Function} child
   * @param {Function} base
   * @param {Object} [properties]
   */
  function inherit(child, base, properties) {
    var baseP = base.prototype,
      childP;
    childP = child.prototype = Object.create(baseP);
    childP.constructor = child;
    childP._super = baseP;
    if (properties) {
      assign(childP, properties);
    }
  }

  /**
   * simple function bind
   * @param {Function} fn
   * @param {Object} context
   * @returns {Function}
   */
  function bindFn(fn, context) {
    return function boundFn() {
      return fn.apply(context, arguments);
    };
  }

  /**
   * let a boolean value also be a function that must return a boolean
   * this first item in args will be used as the context
   * @param {Boolean|Function} val
   * @param {Array} [args]
   * @returns {Boolean}
   */
  function boolOrFn(val, args) {
    if (typeof val == TYPE_FUNCTION) {
      return val.apply(args ? args[0] || undefined$1 : undefined$1, args);
    }
    return val;
  }

  /**
   * use the val2 when val1 is undefined
   * @param {*} val1
   * @param {*} val2
   * @returns {*}
   */
  function ifUndefined(val1, val2) {
    return val1 === undefined$1 ? val2 : val1;
  }

  /**
   * addEventListener with multiple events at once
   * @param {EventTarget} target
   * @param {String} types
   * @param {Function} handler
   */
  function addEventListeners(target, types, handler) {
    each(splitStr(types), function (type) {
      target.addEventListener(type, handler, false);
    });
  }

  /**
   * removeEventListener with multiple events at once
   * @param {EventTarget} target
   * @param {String} types
   * @param {Function} handler
   */
  function removeEventListeners(target, types, handler) {
    each(splitStr(types), function (type) {
      target.removeEventListener(type, handler, false);
    });
  }

  /**
   * find if a node is in the given parent
   * @method hasParent
   * @param {HTMLElement} node
   * @param {HTMLElement} parent
   * @return {Boolean} found
   */
  function hasParent(node, parent) {
    while (node) {
      if (node == parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  /**
   * small indexOf wrapper
   * @param {String} str
   * @param {String} find
   * @returns {Boolean} found
   */
  function inStr(str, find) {
    return str.indexOf(find) > -1;
  }

  /**
   * split string on whitespace
   * @param {String} str
   * @returns {Array} words
   */
  function splitStr(str) {
    return str.trim().split(/\s+/g);
  }

  /**
   * find if a array contains the object using indexOf or a simple polyFill
   * @param {Array} src
   * @param {String} find
   * @param {String} [findByKey]
   * @return {Boolean|Number} false when not found, or the index
   */
  function inArray(src, find, findByKey) {
    if (src.indexOf && !findByKey) {
      return src.indexOf(find);
    } else {
      var i = 0;
      while (i < src.length) {
        if (findByKey && src[i][findByKey] == find || !findByKey && src[i] === find) {
          return i;
        }
        i++;
      }
      return -1;
    }
  }

  /**
   * convert array-like objects to real arrays
   * @param {Object} obj
   * @returns {Array}
   */
  function toArray(obj) {
    return Array.prototype.slice.call(obj, 0);
  }

  /**
   * unique array with objects based on a key (like 'id') or just by the array's value
   * @param {Array} src [{id:1},{id:2},{id:1}]
   * @param {String} [key]
   * @param {Boolean} [sort=False]
   * @returns {Array} [{id:1},{id:2}]
   */
  function uniqueArray(src, key, sort) {
    var results = [];
    var values = [];
    var i = 0;
    while (i < src.length) {
      var val = key ? src[i][key] : src[i];
      if (inArray(values, val) < 0) {
        results.push(src[i]);
      }
      values[i] = val;
      i++;
    }
    if (sort) {
      if (!key) {
        results = results.sort();
      } else {
        results = results.sort(function sortUniqueArray(a, b) {
          return a[key] > b[key];
        });
      }
    }
    return results;
  }

  /**
   * get the prefixed property
   * @param {Object} obj
   * @param {String} property
   * @returns {String|Undefined} prefixed
   */
  function prefixed(obj, property) {
    var prefix, prop;
    var camelProp = property[0].toUpperCase() + property.slice(1);
    var i = 0;
    while (i < VENDOR_PREFIXES.length) {
      prefix = VENDOR_PREFIXES[i];
      prop = prefix ? prefix + camelProp : property;
      if (prop in obj) {
        return prop;
      }
      i++;
    }
    return undefined$1;
  }

  /**
   * get a unique id
   * @returns {number} uniqueId
   */
  var _uniqueId = 1;
  function uniqueId() {
    return _uniqueId++;
  }

  /**
   * get the window object of an element
   * @param {HTMLElement} element
   * @returns {DocumentView|Window}
   */
  function getWindowForElement(element) {
    var doc = element.ownerDocument || element;
    return doc.defaultView || doc.parentWindow || window;
  }
  var MOBILE_REGEX = /mobile|tablet|ip(ad|hone|od)|android/i;
  var SUPPORT_TOUCH = 'ontouchstart' in window;
  var SUPPORT_POINTER_EVENTS = prefixed(window, 'PointerEvent') !== undefined$1;
  var SUPPORT_ONLY_TOUCH = SUPPORT_TOUCH && MOBILE_REGEX.test(navigator.userAgent);
  var INPUT_TYPE_TOUCH = 'touch';
  var INPUT_TYPE_PEN = 'pen';
  var INPUT_TYPE_MOUSE = 'mouse';
  var INPUT_TYPE_KINECT = 'kinect';
  var COMPUTE_INTERVAL = 25;
  var INPUT_START = 1;
  var INPUT_MOVE = 2;
  var INPUT_END = 4;
  var INPUT_CANCEL = 8;
  var DIRECTION_NONE = 1;
  var DIRECTION_LEFT = 2;
  var DIRECTION_RIGHT = 4;
  var DIRECTION_UP = 8;
  var DIRECTION_DOWN = 16;
  var DIRECTION_HORIZONTAL = DIRECTION_LEFT | DIRECTION_RIGHT;
  var DIRECTION_VERTICAL = DIRECTION_UP | DIRECTION_DOWN;
  var DIRECTION_ALL = DIRECTION_HORIZONTAL | DIRECTION_VERTICAL;
  var PROPS_XY = ['x', 'y'];
  var PROPS_CLIENT_XY = ['clientX', 'clientY'];

  /**
   * create new input type manager
   * @param {Manager} manager
   * @param {Function} callback
   * @returns {Input}
   * @constructor
   */
  function Input(manager, callback) {
    var self = this;
    this.manager = manager;
    this.callback = callback;
    this.element = manager.element;
    this.target = manager.options.inputTarget;

    // smaller wrapper around the handler, for the scope and the enabled state of the manager,
    // so when disabled the input events are completely bypassed.
    this.domHandler = function (ev) {
      if (boolOrFn(manager.options.enable, [manager])) {
        self.handler(ev);
      }
    };
    this.init();
  }
  Input.prototype = {
    /**
     * should handle the inputEvent data and trigger the callback
     * @virtual
     */
    handler: function () {},
    /**
     * bind the events
     */
    init: function () {
      this.evEl && addEventListeners(this.element, this.evEl, this.domHandler);
      this.evTarget && addEventListeners(this.target, this.evTarget, this.domHandler);
      this.evWin && addEventListeners(getWindowForElement(this.element), this.evWin, this.domHandler);
    },
    /**
     * unbind the events
     */
    destroy: function () {
      this.evEl && removeEventListeners(this.element, this.evEl, this.domHandler);
      this.evTarget && removeEventListeners(this.target, this.evTarget, this.domHandler);
      this.evWin && removeEventListeners(getWindowForElement(this.element), this.evWin, this.domHandler);
    }
  };

  /**
   * create new input type manager
   * called by the Manager constructor
   * @param {Hammer} manager
   * @returns {Input}
   */
  function createInputInstance(manager) {
    var Type;
    var inputClass = manager.options.inputClass;
    if (inputClass) {
      Type = inputClass;
    } else if (SUPPORT_POINTER_EVENTS) {
      Type = PointerEventInput;
    } else if (SUPPORT_ONLY_TOUCH) {
      Type = TouchInput;
    } else if (!SUPPORT_TOUCH) {
      Type = MouseInput;
    } else {
      Type = TouchMouseInput;
    }
    return new Type(manager, inputHandler);
  }

  /**
   * handle input events
   * @param {Manager} manager
   * @param {String} eventType
   * @param {Object} input
   */
  function inputHandler(manager, eventType, input) {
    var pointersLen = input.pointers.length;
    var changedPointersLen = input.changedPointers.length;
    var isFirst = eventType & INPUT_START && pointersLen - changedPointersLen === 0;
    var isFinal = eventType & (INPUT_END | INPUT_CANCEL) && pointersLen - changedPointersLen === 0;
    input.isFirst = !!isFirst;
    input.isFinal = !!isFinal;
    if (isFirst) {
      manager.session = {};
    }

    // source event is the normalized value of the domEvents
    // like 'touchstart, mouseup, pointerdown'
    input.eventType = eventType;

    // compute scale, rotation etc
    computeInputData(manager, input);

    // emit secret event
    manager.emit('hammer.input', input);
    manager.recognize(input);
    manager.session.prevInput = input;
  }

  /**
   * extend the data with some usable properties like scale, rotate, velocity etc
   * @param {Object} manager
   * @param {Object} input
   */
  function computeInputData(manager, input) {
    var session = manager.session;
    var pointers = input.pointers;
    var pointersLength = pointers.length;

    // store the first input to calculate the distance and direction
    if (!session.firstInput) {
      session.firstInput = simpleCloneInputData(input);
    }

    // to compute scale and rotation we need to store the multiple touches
    if (pointersLength > 1 && !session.firstMultiple) {
      session.firstMultiple = simpleCloneInputData(input);
    } else if (pointersLength === 1) {
      session.firstMultiple = false;
    }
    var firstInput = session.firstInput;
    var firstMultiple = session.firstMultiple;
    var offsetCenter = firstMultiple ? firstMultiple.center : firstInput.center;
    var center = input.center = getCenter(pointers);
    input.timeStamp = now();
    input.deltaTime = input.timeStamp - firstInput.timeStamp;
    input.angle = getAngle(offsetCenter, center);
    input.distance = getDistance(offsetCenter, center);
    computeDeltaXY(session, input);
    input.offsetDirection = getDirection(input.deltaX, input.deltaY);
    var overallVelocity = getVelocity(input.deltaTime, input.deltaX, input.deltaY);
    input.overallVelocityX = overallVelocity.x;
    input.overallVelocityY = overallVelocity.y;
    input.overallVelocity = abs(overallVelocity.x) > abs(overallVelocity.y) ? overallVelocity.x : overallVelocity.y;
    input.scale = firstMultiple ? getScale(firstMultiple.pointers, pointers) : 1;
    input.rotation = firstMultiple ? getRotation(firstMultiple.pointers, pointers) : 0;
    input.maxPointers = !session.prevInput ? input.pointers.length : input.pointers.length > session.prevInput.maxPointers ? input.pointers.length : session.prevInput.maxPointers;
    computeIntervalInputData(session, input);

    // find the correct target
    var target = manager.element;
    if (hasParent(input.srcEvent.target, target)) {
      target = input.srcEvent.target;
    }
    input.target = target;
  }
  function computeDeltaXY(session, input) {
    var center = input.center;
    var offset = session.offsetDelta || {};
    var prevDelta = session.prevDelta || {};
    var prevInput = session.prevInput || {};
    if (input.eventType === INPUT_START || prevInput.eventType === INPUT_END) {
      prevDelta = session.prevDelta = {
        x: prevInput.deltaX || 0,
        y: prevInput.deltaY || 0
      };
      offset = session.offsetDelta = {
        x: center.x,
        y: center.y
      };
    }
    input.deltaX = prevDelta.x + (center.x - offset.x);
    input.deltaY = prevDelta.y + (center.y - offset.y);
  }

  /**
   * velocity is calculated every x ms
   * @param {Object} session
   * @param {Object} input
   */
  function computeIntervalInputData(session, input) {
    var last = session.lastInterval || input,
      deltaTime = input.timeStamp - last.timeStamp,
      velocity,
      velocityX,
      velocityY,
      direction;
    if (input.eventType != INPUT_CANCEL && (deltaTime > COMPUTE_INTERVAL || last.velocity === undefined$1)) {
      var deltaX = input.deltaX - last.deltaX;
      var deltaY = input.deltaY - last.deltaY;
      var v = getVelocity(deltaTime, deltaX, deltaY);
      velocityX = v.x;
      velocityY = v.y;
      velocity = abs(v.x) > abs(v.y) ? v.x : v.y;
      direction = getDirection(deltaX, deltaY);
      session.lastInterval = input;
    } else {
      // use latest velocity info if it doesn't overtake a minimum period
      velocity = last.velocity;
      velocityX = last.velocityX;
      velocityY = last.velocityY;
      direction = last.direction;
    }
    input.velocity = velocity;
    input.velocityX = velocityX;
    input.velocityY = velocityY;
    input.direction = direction;
  }

  /**
   * create a simple clone from the input used for storage of firstInput and firstMultiple
   * @param {Object} input
   * @returns {Object} clonedInputData
   */
  function simpleCloneInputData(input) {
    // make a simple copy of the pointers because we will get a reference if we don't
    // we only need clientXY for the calculations
    var pointers = [];
    var i = 0;
    while (i < input.pointers.length) {
      pointers[i] = {
        clientX: round(input.pointers[i].clientX),
        clientY: round(input.pointers[i].clientY)
      };
      i++;
    }
    return {
      timeStamp: now(),
      pointers: pointers,
      center: getCenter(pointers),
      deltaX: input.deltaX,
      deltaY: input.deltaY
    };
  }

  /**
   * get the center of all the pointers
   * @param {Array} pointers
   * @return {Object} center contains `x` and `y` properties
   */
  function getCenter(pointers) {
    var pointersLength = pointers.length;

    // no need to loop when only one touch
    if (pointersLength === 1) {
      return {
        x: round(pointers[0].clientX),
        y: round(pointers[0].clientY)
      };
    }
    var x = 0,
      y = 0,
      i = 0;
    while (i < pointersLength) {
      x += pointers[i].clientX;
      y += pointers[i].clientY;
      i++;
    }
    return {
      x: round(x / pointersLength),
      y: round(y / pointersLength)
    };
  }

  /**
   * calculate the velocity between two points. unit is in px per ms.
   * @param {Number} deltaTime
   * @param {Number} x
   * @param {Number} y
   * @return {Object} velocity `x` and `y`
   */
  function getVelocity(deltaTime, x, y) {
    return {
      x: x / deltaTime || 0,
      y: y / deltaTime || 0
    };
  }

  /**
   * get the direction between two points
   * @param {Number} x
   * @param {Number} y
   * @return {Number} direction
   */
  function getDirection(x, y) {
    if (x === y) {
      return DIRECTION_NONE;
    }
    if (abs(x) >= abs(y)) {
      return x < 0 ? DIRECTION_LEFT : DIRECTION_RIGHT;
    }
    return y < 0 ? DIRECTION_UP : DIRECTION_DOWN;
  }

  /**
   * calculate the absolute distance between two points
   * @param {Object} p1 {x, y}
   * @param {Object} p2 {x, y}
   * @param {Array} [props] containing x and y keys
   * @return {Number} distance
   */
  function getDistance(p1, p2, props) {
    if (!props) {
      props = PROPS_XY;
    }
    var x = p2[props[0]] - p1[props[0]],
      y = p2[props[1]] - p1[props[1]];
    return Math.sqrt(x * x + y * y);
  }

  /**
   * calculate the angle between two coordinates
   * @param {Object} p1
   * @param {Object} p2
   * @param {Array} [props] containing x and y keys
   * @return {Number} angle
   */
  function getAngle(p1, p2, props) {
    if (!props) {
      props = PROPS_XY;
    }
    var x = p2[props[0]] - p1[props[0]],
      y = p2[props[1]] - p1[props[1]];
    return Math.atan2(y, x) * 180 / Math.PI;
  }

  /**
   * calculate the rotation degrees between two pointersets
   * @param {Array} start array of pointers
   * @param {Array} end array of pointers
   * @return {Number} rotation
   */
  function getRotation(start, end) {
    return getAngle(end[1], end[0], PROPS_CLIENT_XY) + getAngle(start[1], start[0], PROPS_CLIENT_XY);
  }

  /**
   * calculate the scale factor between two pointersets
   * no scale is 1, and goes down to 0 when pinched together, and bigger when pinched out
   * @param {Array} start array of pointers
   * @param {Array} end array of pointers
   * @return {Number} scale
   */
  function getScale(start, end) {
    return getDistance(end[0], end[1], PROPS_CLIENT_XY) / getDistance(start[0], start[1], PROPS_CLIENT_XY);
  }
  var MOUSE_INPUT_MAP = {
    mousedown: INPUT_START,
    mousemove: INPUT_MOVE,
    mouseup: INPUT_END
  };
  var MOUSE_ELEMENT_EVENTS = 'mousedown';
  var MOUSE_WINDOW_EVENTS = 'mousemove mouseup';

  /**
   * Mouse events input
   * @constructor
   * @extends Input
   */
  function MouseInput() {
    this.evEl = MOUSE_ELEMENT_EVENTS;
    this.evWin = MOUSE_WINDOW_EVENTS;
    this.pressed = false; // mousedown state

    Input.apply(this, arguments);
  }
  inherit(MouseInput, Input, {
    /**
     * handle mouse events
     * @param {Object} ev
     */
    handler: function MEhandler(ev) {
      var eventType = MOUSE_INPUT_MAP[ev.type];

      // on start we want to have the left mouse button down
      if (eventType & INPUT_START && ev.button === 0) {
        this.pressed = true;
      }
      if (eventType & INPUT_MOVE && ev.which !== 1) {
        eventType = INPUT_END;
      }

      // mouse must be down
      if (!this.pressed) {
        return;
      }
      if (eventType & INPUT_END) {
        this.pressed = false;
      }
      this.callback(this.manager, eventType, {
        pointers: [ev],
        changedPointers: [ev],
        pointerType: INPUT_TYPE_MOUSE,
        srcEvent: ev
      });
    }
  });
  var POINTER_INPUT_MAP = {
    pointerdown: INPUT_START,
    pointermove: INPUT_MOVE,
    pointerup: INPUT_END,
    pointercancel: INPUT_CANCEL,
    pointerout: INPUT_CANCEL
  };

  // in IE10 the pointer types is defined as an enum
  var IE10_POINTER_TYPE_ENUM = {
    2: INPUT_TYPE_TOUCH,
    3: INPUT_TYPE_PEN,
    4: INPUT_TYPE_MOUSE,
    5: INPUT_TYPE_KINECT // see https://twitter.com/jacobrossi/status/480596438489890816
  };
  var POINTER_ELEMENT_EVENTS = 'pointerdown';
  var POINTER_WINDOW_EVENTS = 'pointermove pointerup pointercancel';

  // IE10 has prefixed support, and case-sensitive
  if (window.MSPointerEvent && !window.PointerEvent) {
    POINTER_ELEMENT_EVENTS = 'MSPointerDown';
    POINTER_WINDOW_EVENTS = 'MSPointerMove MSPointerUp MSPointerCancel';
  }

  /**
   * Pointer events input
   * @constructor
   * @extends Input
   */
  function PointerEventInput() {
    this.evEl = POINTER_ELEMENT_EVENTS;
    this.evWin = POINTER_WINDOW_EVENTS;
    Input.apply(this, arguments);
    this.store = this.manager.session.pointerEvents = [];
  }
  inherit(PointerEventInput, Input, {
    /**
     * handle mouse events
     * @param {Object} ev
     */
    handler: function PEhandler(ev) {
      var store = this.store;
      var removePointer = false;
      var eventTypeNormalized = ev.type.toLowerCase().replace('ms', '');
      var eventType = POINTER_INPUT_MAP[eventTypeNormalized];
      var pointerType = IE10_POINTER_TYPE_ENUM[ev.pointerType] || ev.pointerType;
      var isTouch = pointerType == INPUT_TYPE_TOUCH;

      // get index of the event in the store
      var storeIndex = inArray(store, ev.pointerId, 'pointerId');

      // start and mouse must be down
      if (eventType & INPUT_START && (ev.button === 0 || isTouch)) {
        if (storeIndex < 0) {
          store.push(ev);
          storeIndex = store.length - 1;
        }
      } else if (eventType & (INPUT_END | INPUT_CANCEL)) {
        removePointer = true;
      }

      // it not found, so the pointer hasn't been down (so it's probably a hover)
      if (storeIndex < 0) {
        return;
      }

      // update the event in the store
      store[storeIndex] = ev;
      this.callback(this.manager, eventType, {
        pointers: store,
        changedPointers: [ev],
        pointerType: pointerType,
        srcEvent: ev
      });
      if (removePointer) {
        // remove from the store
        store.splice(storeIndex, 1);
      }
    }
  });
  var SINGLE_TOUCH_INPUT_MAP = {
    touchstart: INPUT_START,
    touchmove: INPUT_MOVE,
    touchend: INPUT_END,
    touchcancel: INPUT_CANCEL
  };
  var SINGLE_TOUCH_TARGET_EVENTS = 'touchstart';
  var SINGLE_TOUCH_WINDOW_EVENTS = 'touchstart touchmove touchend touchcancel';

  /**
   * Touch events input
   * @constructor
   * @extends Input
   */
  function SingleTouchInput() {
    this.evTarget = SINGLE_TOUCH_TARGET_EVENTS;
    this.evWin = SINGLE_TOUCH_WINDOW_EVENTS;
    this.started = false;
    Input.apply(this, arguments);
  }
  inherit(SingleTouchInput, Input, {
    handler: function TEhandler(ev) {
      var type = SINGLE_TOUCH_INPUT_MAP[ev.type];

      // should we handle the touch events?
      if (type === INPUT_START) {
        this.started = true;
      }
      if (!this.started) {
        return;
      }
      var touches = normalizeSingleTouches.call(this, ev, type);

      // when done, reset the started state
      if (type & (INPUT_END | INPUT_CANCEL) && touches[0].length - touches[1].length === 0) {
        this.started = false;
      }
      this.callback(this.manager, type, {
        pointers: touches[0],
        changedPointers: touches[1],
        pointerType: INPUT_TYPE_TOUCH,
        srcEvent: ev
      });
    }
  });

  /**
   * @this {TouchInput}
   * @param {Object} ev
   * @param {Number} type flag
   * @returns {undefined|Array} [all, changed]
   */
  function normalizeSingleTouches(ev, type) {
    var all = toArray(ev.touches);
    var changed = toArray(ev.changedTouches);
    if (type & (INPUT_END | INPUT_CANCEL)) {
      all = uniqueArray(all.concat(changed), 'identifier', true);
    }
    return [all, changed];
  }
  var TOUCH_INPUT_MAP = {
    touchstart: INPUT_START,
    touchmove: INPUT_MOVE,
    touchend: INPUT_END,
    touchcancel: INPUT_CANCEL
  };
  var TOUCH_TARGET_EVENTS = 'touchstart touchmove touchend touchcancel';

  /**
   * Multi-user touch events input
   * @constructor
   * @extends Input
   */
  function TouchInput() {
    this.evTarget = TOUCH_TARGET_EVENTS;
    this.targetIds = {};
    Input.apply(this, arguments);
  }
  inherit(TouchInput, Input, {
    handler: function MTEhandler(ev) {
      var type = TOUCH_INPUT_MAP[ev.type];
      var touches = getTouches.call(this, ev, type);
      if (!touches) {
        return;
      }
      this.callback(this.manager, type, {
        pointers: touches[0],
        changedPointers: touches[1],
        pointerType: INPUT_TYPE_TOUCH,
        srcEvent: ev
      });
    }
  });

  /**
   * @this {TouchInput}
   * @param {Object} ev
   * @param {Number} type flag
   * @returns {undefined|Array} [all, changed]
   */
  function getTouches(ev, type) {
    var allTouches = toArray(ev.touches);
    var targetIds = this.targetIds;

    // when there is only one touch, the process can be simplified
    if (type & (INPUT_START | INPUT_MOVE) && allTouches.length === 1) {
      targetIds[allTouches[0].identifier] = true;
      return [allTouches, allTouches];
    }
    var i,
      targetTouches,
      changedTouches = toArray(ev.changedTouches),
      changedTargetTouches = [],
      target = this.target;

    // get target touches from touches
    targetTouches = allTouches.filter(function (touch) {
      return hasParent(touch.target, target);
    });

    // collect touches
    if (type === INPUT_START) {
      i = 0;
      while (i < targetTouches.length) {
        targetIds[targetTouches[i].identifier] = true;
        i++;
      }
    }

    // filter changed touches to only contain touches that exist in the collected target ids
    i = 0;
    while (i < changedTouches.length) {
      if (targetIds[changedTouches[i].identifier]) {
        changedTargetTouches.push(changedTouches[i]);
      }

      // cleanup removed touches
      if (type & (INPUT_END | INPUT_CANCEL)) {
        delete targetIds[changedTouches[i].identifier];
      }
      i++;
    }
    if (!changedTargetTouches.length) {
      return;
    }
    return [
    // merge targetTouches with changedTargetTouches so it contains ALL touches, including 'end' and 'cancel'
    uniqueArray(targetTouches.concat(changedTargetTouches), 'identifier', true), changedTargetTouches];
  }

  /**
   * Combined touch and mouse input
   *
   * Touch has a higher priority then mouse, and while touching no mouse events are allowed.
   * This because touch devices also emit mouse events while doing a touch.
   *
   * @constructor
   * @extends Input
   */

  var DEDUP_TIMEOUT = 2500;
  var DEDUP_DISTANCE = 25;
  function TouchMouseInput() {
    Input.apply(this, arguments);
    var handler = bindFn(this.handler, this);
    this.touch = new TouchInput(this.manager, handler);
    this.mouse = new MouseInput(this.manager, handler);
    this.primaryTouch = null;
    this.lastTouches = [];
  }
  inherit(TouchMouseInput, Input, {
    /**
     * handle mouse and touch events
     * @param {Hammer} manager
     * @param {String} inputEvent
     * @param {Object} inputData
     */
    handler: function TMEhandler(manager, inputEvent, inputData) {
      var isTouch = inputData.pointerType == INPUT_TYPE_TOUCH,
        isMouse = inputData.pointerType == INPUT_TYPE_MOUSE;
      if (isMouse && inputData.sourceCapabilities && inputData.sourceCapabilities.firesTouchEvents) {
        return;
      }

      // when we're in a touch event, record touches to  de-dupe synthetic mouse event
      if (isTouch) {
        recordTouches.call(this, inputEvent, inputData);
      } else if (isMouse && isSyntheticEvent.call(this, inputData)) {
        return;
      }
      this.callback(manager, inputEvent, inputData);
    },
    /**
     * remove the event listeners
     */
    destroy: function destroy() {
      this.touch.destroy();
      this.mouse.destroy();
    }
  });
  function recordTouches(eventType, eventData) {
    if (eventType & INPUT_START) {
      this.primaryTouch = eventData.changedPointers[0].identifier;
      setLastTouch.call(this, eventData);
    } else if (eventType & (INPUT_END | INPUT_CANCEL)) {
      setLastTouch.call(this, eventData);
    }
  }
  function setLastTouch(eventData) {
    var touch = eventData.changedPointers[0];
    if (touch.identifier === this.primaryTouch) {
      var lastTouch = {
        x: touch.clientX,
        y: touch.clientY
      };
      this.lastTouches.push(lastTouch);
      var lts = this.lastTouches;
      var removeLastTouch = function () {
        var i = lts.indexOf(lastTouch);
        if (i > -1) {
          lts.splice(i, 1);
        }
      };
      setTimeout(removeLastTouch, DEDUP_TIMEOUT);
    }
  }
  function isSyntheticEvent(eventData) {
    var x = eventData.srcEvent.clientX,
      y = eventData.srcEvent.clientY;
    for (var i = 0; i < this.lastTouches.length; i++) {
      var t = this.lastTouches[i];
      var dx = Math.abs(x - t.x),
        dy = Math.abs(y - t.y);
      if (dx <= DEDUP_DISTANCE && dy <= DEDUP_DISTANCE) {
        return true;
      }
    }
    return false;
  }
  var PREFIXED_TOUCH_ACTION = prefixed(TEST_ELEMENT.style, 'touchAction');
  var NATIVE_TOUCH_ACTION = PREFIXED_TOUCH_ACTION !== undefined$1;

  // magical touchAction value
  var TOUCH_ACTION_COMPUTE = 'compute';
  var TOUCH_ACTION_AUTO = 'auto';
  var TOUCH_ACTION_MANIPULATION = 'manipulation'; // not implemented
  var TOUCH_ACTION_NONE = 'none';
  var TOUCH_ACTION_PAN_X = 'pan-x';
  var TOUCH_ACTION_PAN_Y = 'pan-y';
  var TOUCH_ACTION_MAP = getTouchActionProps();

  /**
   * Touch Action
   * sets the touchAction property or uses the js alternative
   * @param {Manager} manager
   * @param {String} value
   * @constructor
   */
  function TouchAction(manager, value) {
    this.manager = manager;
    this.set(value);
  }
  TouchAction.prototype = {
    /**
     * set the touchAction value on the element or enable the polyfill
     * @param {String} value
     */
    set: function (value) {
      // find out the touch-action by the event handlers
      if (value == TOUCH_ACTION_COMPUTE) {
        value = this.compute();
      }
      if (NATIVE_TOUCH_ACTION && this.manager.element.style && TOUCH_ACTION_MAP[value]) {
        this.manager.element.style[PREFIXED_TOUCH_ACTION] = value;
      }
      this.actions = value.toLowerCase().trim();
    },
    /**
     * just re-set the touchAction value
     */
    update: function () {
      this.set(this.manager.options.touchAction);
    },
    /**
     * compute the value for the touchAction property based on the recognizer's settings
     * @returns {String} value
     */
    compute: function () {
      var actions = [];
      each(this.manager.recognizers, function (recognizer) {
        if (boolOrFn(recognizer.options.enable, [recognizer])) {
          actions = actions.concat(recognizer.getTouchAction());
        }
      });
      return cleanTouchActions(actions.join(' '));
    },
    /**
     * this method is called on each input cycle and provides the preventing of the browser behavior
     * @param {Object} input
     */
    preventDefaults: function (input) {
      var srcEvent = input.srcEvent;
      var direction = input.offsetDirection;

      // if the touch action did prevented once this session
      if (this.manager.session.prevented) {
        srcEvent.preventDefault();
        return;
      }
      var actions = this.actions;
      var hasNone = inStr(actions, TOUCH_ACTION_NONE) && !TOUCH_ACTION_MAP[TOUCH_ACTION_NONE];
      var hasPanY = inStr(actions, TOUCH_ACTION_PAN_Y) && !TOUCH_ACTION_MAP[TOUCH_ACTION_PAN_Y];
      var hasPanX = inStr(actions, TOUCH_ACTION_PAN_X) && !TOUCH_ACTION_MAP[TOUCH_ACTION_PAN_X];
      if (hasNone) {
        //do not prevent defaults if this is a tap gesture

        var isTapPointer = input.pointers.length === 1;
        var isTapMovement = input.distance < 2;
        var isTapTouchTime = input.deltaTime < 250;
        if (isTapPointer && isTapMovement && isTapTouchTime) {
          return;
        }
      }
      if (hasPanX && hasPanY) {
        // `pan-x pan-y` means browser handles all scrolling/panning, do not prevent
        return;
      }
      if (hasNone || hasPanY && direction & DIRECTION_HORIZONTAL || hasPanX && direction & DIRECTION_VERTICAL) {
        return this.preventSrc(srcEvent);
      }
    },
    /**
     * call preventDefault to prevent the browser's default behavior (scrolling in most cases)
     * @param {Object} srcEvent
     */
    preventSrc: function (srcEvent) {
      this.manager.session.prevented = true;
      srcEvent.preventDefault();
    }
  };

  /**
   * when the touchActions are collected they are not a valid value, so we need to clean things up. *
   * @param {String} actions
   * @returns {*}
   */
  function cleanTouchActions(actions) {
    // none
    if (inStr(actions, TOUCH_ACTION_NONE)) {
      return TOUCH_ACTION_NONE;
    }
    var hasPanX = inStr(actions, TOUCH_ACTION_PAN_X);
    var hasPanY = inStr(actions, TOUCH_ACTION_PAN_Y);

    // if both pan-x and pan-y are set (different recognizers
    // for different directions, e.g. horizontal pan but vertical swipe?)
    // we need none (as otherwise with pan-x pan-y combined none of these
    // recognizers will work, since the browser would handle all panning
    if (hasPanX && hasPanY) {
      return TOUCH_ACTION_NONE;
    }

    // pan-x OR pan-y
    if (hasPanX || hasPanY) {
      return hasPanX ? TOUCH_ACTION_PAN_X : TOUCH_ACTION_PAN_Y;
    }

    // manipulation
    if (inStr(actions, TOUCH_ACTION_MANIPULATION)) {
      return TOUCH_ACTION_MANIPULATION;
    }
    return TOUCH_ACTION_AUTO;
  }
  function getTouchActionProps() {
    if (!NATIVE_TOUCH_ACTION) {
      return false;
    }
    var touchMap = {};
    var cssSupports = window.CSS && window.CSS.supports;
    ['auto', 'manipulation', 'pan-y', 'pan-x', 'pan-x pan-y', 'none'].forEach(function (val) {
      // If css.supports is not supported but there is native touch-action assume it supports
      // all values. This is the case for IE 10 and 11.
      touchMap[val] = cssSupports ? window.CSS.supports('touch-action', val) : true;
    });
    return touchMap;
  }

  /**
   * Recognizer flow explained; *
   * All recognizers have the initial state of POSSIBLE when a input session starts.
   * The definition of a input session is from the first input until the last input, with all it's movement in it. *
   * Example session for mouse-input: mousedown -> mousemove -> mouseup
   *
   * On each recognizing cycle (see Manager.recognize) the .recognize() method is executed
   * which determines with state it should be.
   *
   * If the recognizer has the state FAILED, CANCELLED or RECOGNIZED (equals ENDED), it is reset to
   * POSSIBLE to give it another change on the next cycle.
   *
   *               Possible
   *                  |
   *            +-----+---------------+
   *            |                     |
   *      +-----+-----+               |
   *      |           |               |
   *   Failed      Cancelled          |
   *                          +-------+------+
   *                          |              |
   *                      Recognized       Began
   *                                         |
   *                                      Changed
   *                                         |
   *                                  Ended/Recognized
   */
  var STATE_POSSIBLE = 1;
  var STATE_BEGAN = 2;
  var STATE_CHANGED = 4;
  var STATE_ENDED = 8;
  var STATE_RECOGNIZED = STATE_ENDED;
  var STATE_CANCELLED = 16;
  var STATE_FAILED = 32;

  /**
   * Recognizer
   * Every recognizer needs to extend from this class.
   * @constructor
   * @param {Object} options
   */
  function Recognizer(options) {
    this.options = assign({}, this.defaults, options || {});
    this.id = uniqueId();
    this.manager = null;

    // default is enable true
    this.options.enable = ifUndefined(this.options.enable, true);
    this.state = STATE_POSSIBLE;
    this.simultaneous = {};
    this.requireFail = [];
  }
  Recognizer.prototype = {
    /**
     * @virtual
     * @type {Object}
     */
    defaults: {},
    /**
     * set options
     * @param {Object} options
     * @return {Recognizer}
     */
    set: function (options) {
      assign(this.options, options);

      // also update the touchAction, in case something changed about the directions/enabled state
      this.manager && this.manager.touchAction.update();
      return this;
    },
    /**
     * recognize simultaneous with an other recognizer.
     * @param {Recognizer} otherRecognizer
     * @returns {Recognizer} this
     */
    recognizeWith: function (otherRecognizer) {
      if (invokeArrayArg(otherRecognizer, 'recognizeWith', this)) {
        return this;
      }
      var simultaneous = this.simultaneous;
      otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
      if (!simultaneous[otherRecognizer.id]) {
        simultaneous[otherRecognizer.id] = otherRecognizer;
        otherRecognizer.recognizeWith(this);
      }
      return this;
    },
    /**
     * drop the simultaneous link. it doesnt remove the link on the other recognizer.
     * @param {Recognizer} otherRecognizer
     * @returns {Recognizer} this
     */
    dropRecognizeWith: function (otherRecognizer) {
      if (invokeArrayArg(otherRecognizer, 'dropRecognizeWith', this)) {
        return this;
      }
      otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
      delete this.simultaneous[otherRecognizer.id];
      return this;
    },
    /**
     * recognizer can only run when an other is failing
     * @param {Recognizer} otherRecognizer
     * @returns {Recognizer} this
     */
    requireFailure: function (otherRecognizer) {
      if (invokeArrayArg(otherRecognizer, 'requireFailure', this)) {
        return this;
      }
      var requireFail = this.requireFail;
      otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
      if (inArray(requireFail, otherRecognizer) === -1) {
        requireFail.push(otherRecognizer);
        otherRecognizer.requireFailure(this);
      }
      return this;
    },
    /**
     * drop the requireFailure link. it does not remove the link on the other recognizer.
     * @param {Recognizer} otherRecognizer
     * @returns {Recognizer} this
     */
    dropRequireFailure: function (otherRecognizer) {
      if (invokeArrayArg(otherRecognizer, 'dropRequireFailure', this)) {
        return this;
      }
      otherRecognizer = getRecognizerByNameIfManager(otherRecognizer, this);
      var index = inArray(this.requireFail, otherRecognizer);
      if (index > -1) {
        this.requireFail.splice(index, 1);
      }
      return this;
    },
    /**
     * has require failures boolean
     * @returns {boolean}
     */
    hasRequireFailures: function () {
      return this.requireFail.length > 0;
    },
    /**
     * if the recognizer can recognize simultaneous with an other recognizer
     * @param {Recognizer} otherRecognizer
     * @returns {Boolean}
     */
    canRecognizeWith: function (otherRecognizer) {
      return !!this.simultaneous[otherRecognizer.id];
    },
    /**
     * You should use `tryEmit` instead of `emit` directly to check
     * that all the needed recognizers has failed before emitting.
     * @param {Object} input
     */
    emit: function (input) {
      var self = this;
      var state = this.state;
      function emit(event) {
        self.manager.emit(event, input);
      }

      // 'panstart' and 'panmove'
      if (state < STATE_ENDED) {
        emit(self.options.event + stateStr(state));
      }
      emit(self.options.event); // simple 'eventName' events

      if (input.additionalEvent) {
        // additional event(panleft, panright, pinchin, pinchout...)
        emit(input.additionalEvent);
      }

      // panend and pancancel
      if (state >= STATE_ENDED) {
        emit(self.options.event + stateStr(state));
      }
    },
    /**
     * Check that all the require failure recognizers has failed,
     * if true, it emits a gesture event,
     * otherwise, setup the state to FAILED.
     * @param {Object} input
     */
    tryEmit: function (input) {
      if (this.canEmit()) {
        return this.emit(input);
      }
      // it's failing anyway
      this.state = STATE_FAILED;
    },
    /**
     * can we emit?
     * @returns {boolean}
     */
    canEmit: function () {
      var i = 0;
      while (i < this.requireFail.length) {
        if (!(this.requireFail[i].state & (STATE_FAILED | STATE_POSSIBLE))) {
          return false;
        }
        i++;
      }
      return true;
    },
    /**
     * update the recognizer
     * @param {Object} inputData
     */
    recognize: function (inputData) {
      // make a new copy of the inputData
      // so we can change the inputData without messing up the other recognizers
      var inputDataClone = assign({}, inputData);

      // is is enabled and allow recognizing?
      if (!boolOrFn(this.options.enable, [this, inputDataClone])) {
        this.reset();
        this.state = STATE_FAILED;
        return;
      }

      // reset when we've reached the end
      if (this.state & (STATE_RECOGNIZED | STATE_CANCELLED | STATE_FAILED)) {
        this.state = STATE_POSSIBLE;
      }
      this.state = this.process(inputDataClone);

      // the recognizer has recognized a gesture
      // so trigger an event
      if (this.state & (STATE_BEGAN | STATE_CHANGED | STATE_ENDED | STATE_CANCELLED)) {
        this.tryEmit(inputDataClone);
      }
    },
    /**
     * return the state of the recognizer
     * the actual recognizing happens in this method
     * @virtual
     * @param {Object} inputData
     * @returns {Const} STATE
     */
    process: function (inputData) {},
    // jshint ignore:line

    /**
     * return the preferred touch-action
     * @virtual
     * @returns {Array}
     */
    getTouchAction: function () {},
    /**
     * called when the gesture isn't allowed to recognize
     * like when another is being recognized or it is disabled
     * @virtual
     */
    reset: function () {}
  };

  /**
   * get a usable string, used as event postfix
   * @param {Const} state
   * @returns {String} state
   */
  function stateStr(state) {
    if (state & STATE_CANCELLED) {
      return 'cancel';
    } else if (state & STATE_ENDED) {
      return 'end';
    } else if (state & STATE_CHANGED) {
      return 'move';
    } else if (state & STATE_BEGAN) {
      return 'start';
    }
    return '';
  }

  /**
   * direction cons to string
   * @param {Const} direction
   * @returns {String}
   */
  function directionStr(direction) {
    if (direction == DIRECTION_DOWN) {
      return 'down';
    } else if (direction == DIRECTION_UP) {
      return 'up';
    } else if (direction == DIRECTION_LEFT) {
      return 'left';
    } else if (direction == DIRECTION_RIGHT) {
      return 'right';
    }
    return '';
  }

  /**
   * get a recognizer by name if it is bound to a manager
   * @param {Recognizer|String} otherRecognizer
   * @param {Recognizer} recognizer
   * @returns {Recognizer}
   */
  function getRecognizerByNameIfManager(otherRecognizer, recognizer) {
    var manager = recognizer.manager;
    if (manager) {
      return manager.get(otherRecognizer);
    }
    return otherRecognizer;
  }

  /**
   * This recognizer is just used as a base for the simple attribute recognizers.
   * @constructor
   * @extends Recognizer
   */
  function AttrRecognizer() {
    Recognizer.apply(this, arguments);
  }
  inherit(AttrRecognizer, Recognizer, {
    /**
     * @namespace
     * @memberof AttrRecognizer
     */
    defaults: {
      /**
       * @type {Number}
       * @default 1
       */
      pointers: 1
    },
    /**
     * Used to check if it the recognizer receives valid input, like input.distance > 10.
     * @memberof AttrRecognizer
     * @param {Object} input
     * @returns {Boolean} recognized
     */
    attrTest: function (input) {
      var optionPointers = this.options.pointers;
      return optionPointers === 0 || input.pointers.length === optionPointers;
    },
    /**
     * Process the input and return the state for the recognizer
     * @memberof AttrRecognizer
     * @param {Object} input
     * @returns {*} State
     */
    process: function (input) {
      var state = this.state;
      var eventType = input.eventType;
      var isRecognized = state & (STATE_BEGAN | STATE_CHANGED);
      var isValid = this.attrTest(input);

      // on cancel input and we've recognized before, return STATE_CANCELLED
      if (isRecognized && (eventType & INPUT_CANCEL || !isValid)) {
        return state | STATE_CANCELLED;
      } else if (isRecognized || isValid) {
        if (eventType & INPUT_END) {
          return state | STATE_ENDED;
        } else if (!(state & STATE_BEGAN)) {
          return STATE_BEGAN;
        }
        return state | STATE_CHANGED;
      }
      return STATE_FAILED;
    }
  });

  /**
   * Pan
   * Recognized when the pointer is down and moved in the allowed direction.
   * @constructor
   * @extends AttrRecognizer
   */
  function PanRecognizer() {
    AttrRecognizer.apply(this, arguments);
    this.pX = null;
    this.pY = null;
  }
  inherit(PanRecognizer, AttrRecognizer, {
    /**
     * @namespace
     * @memberof PanRecognizer
     */
    defaults: {
      event: 'pan',
      threshold: 10,
      pointers: 1,
      direction: DIRECTION_ALL
    },
    getTouchAction: function () {
      var direction = this.options.direction;
      var actions = [];
      if (direction & DIRECTION_HORIZONTAL) {
        actions.push(TOUCH_ACTION_PAN_Y);
      }
      if (direction & DIRECTION_VERTICAL) {
        actions.push(TOUCH_ACTION_PAN_X);
      }
      return actions;
    },
    directionTest: function (input) {
      var options = this.options;
      var hasMoved = true;
      var distance = input.distance;
      var direction = input.direction;
      var x = input.deltaX;
      var y = input.deltaY;

      // lock to axis?
      if (!(direction & options.direction)) {
        if (options.direction & DIRECTION_HORIZONTAL) {
          direction = x === 0 ? DIRECTION_NONE : x < 0 ? DIRECTION_LEFT : DIRECTION_RIGHT;
          hasMoved = x != this.pX;
          distance = Math.abs(input.deltaX);
        } else {
          direction = y === 0 ? DIRECTION_NONE : y < 0 ? DIRECTION_UP : DIRECTION_DOWN;
          hasMoved = y != this.pY;
          distance = Math.abs(input.deltaY);
        }
      }
      input.direction = direction;
      return hasMoved && distance > options.threshold && direction & options.direction;
    },
    attrTest: function (input) {
      return AttrRecognizer.prototype.attrTest.call(this, input) && (this.state & STATE_BEGAN || !(this.state & STATE_BEGAN) && this.directionTest(input));
    },
    emit: function (input) {
      this.pX = input.deltaX;
      this.pY = input.deltaY;
      var direction = directionStr(input.direction);
      if (direction) {
        input.additionalEvent = this.options.event + direction;
      }
      this._super.emit.call(this, input);
    }
  });

  /**
   * Pinch
   * Recognized when two or more pointers are moving toward (zoom-in) or away from each other (zoom-out).
   * @constructor
   * @extends AttrRecognizer
   */
  function PinchRecognizer() {
    AttrRecognizer.apply(this, arguments);
  }
  inherit(PinchRecognizer, AttrRecognizer, {
    /**
     * @namespace
     * @memberof PinchRecognizer
     */
    defaults: {
      event: 'pinch',
      threshold: 0,
      pointers: 2
    },
    getTouchAction: function () {
      return [TOUCH_ACTION_NONE];
    },
    attrTest: function (input) {
      return this._super.attrTest.call(this, input) && (Math.abs(input.scale - 1) > this.options.threshold || this.state & STATE_BEGAN);
    },
    emit: function (input) {
      if (input.scale !== 1) {
        var inOut = input.scale < 1 ? 'in' : 'out';
        input.additionalEvent = this.options.event + inOut;
      }
      this._super.emit.call(this, input);
    }
  });

  /**
   * Press
   * Recognized when the pointer is down for x ms without any movement.
   * @constructor
   * @extends Recognizer
   */
  function PressRecognizer() {
    Recognizer.apply(this, arguments);
    this._timer = null;
    this._input = null;
  }
  inherit(PressRecognizer, Recognizer, {
    /**
     * @namespace
     * @memberof PressRecognizer
     */
    defaults: {
      event: 'press',
      pointers: 1,
      time: 251,
      // minimal time of the pointer to be pressed
      threshold: 9 // a minimal movement is ok, but keep it low
    },
    getTouchAction: function () {
      return [TOUCH_ACTION_AUTO];
    },
    process: function (input) {
      var options = this.options;
      var validPointers = input.pointers.length === options.pointers;
      var validMovement = input.distance < options.threshold;
      var validTime = input.deltaTime > options.time;
      this._input = input;

      // we only allow little movement
      // and we've reached an end event, so a tap is possible
      if (!validMovement || !validPointers || input.eventType & (INPUT_END | INPUT_CANCEL) && !validTime) {
        this.reset();
      } else if (input.eventType & INPUT_START) {
        this.reset();
        this._timer = setTimeoutContext(function () {
          this.state = STATE_RECOGNIZED;
          this.tryEmit();
        }, options.time, this);
      } else if (input.eventType & INPUT_END) {
        return STATE_RECOGNIZED;
      }
      return STATE_FAILED;
    },
    reset: function () {
      clearTimeout(this._timer);
    },
    emit: function (input) {
      if (this.state !== STATE_RECOGNIZED) {
        return;
      }
      if (input && input.eventType & INPUT_END) {
        this.manager.emit(this.options.event + 'up', input);
      } else {
        this._input.timeStamp = now();
        this.manager.emit(this.options.event, this._input);
      }
    }
  });

  /**
   * Rotate
   * Recognized when two or more pointer are moving in a circular motion.
   * @constructor
   * @extends AttrRecognizer
   */
  function RotateRecognizer() {
    AttrRecognizer.apply(this, arguments);
  }
  inherit(RotateRecognizer, AttrRecognizer, {
    /**
     * @namespace
     * @memberof RotateRecognizer
     */
    defaults: {
      event: 'rotate',
      threshold: 0,
      pointers: 2
    },
    getTouchAction: function () {
      return [TOUCH_ACTION_NONE];
    },
    attrTest: function (input) {
      return this._super.attrTest.call(this, input) && (Math.abs(input.rotation) > this.options.threshold || this.state & STATE_BEGAN);
    }
  });

  /**
   * Swipe
   * Recognized when the pointer is moving fast (velocity), with enough distance in the allowed direction.
   * @constructor
   * @extends AttrRecognizer
   */
  function SwipeRecognizer() {
    AttrRecognizer.apply(this, arguments);
  }
  inherit(SwipeRecognizer, AttrRecognizer, {
    /**
     * @namespace
     * @memberof SwipeRecognizer
     */
    defaults: {
      event: 'swipe',
      threshold: 10,
      velocity: 0.3,
      direction: DIRECTION_HORIZONTAL | DIRECTION_VERTICAL,
      pointers: 1
    },
    getTouchAction: function () {
      return PanRecognizer.prototype.getTouchAction.call(this);
    },
    attrTest: function (input) {
      var direction = this.options.direction;
      var velocity;
      if (direction & (DIRECTION_HORIZONTAL | DIRECTION_VERTICAL)) {
        velocity = input.overallVelocity;
      } else if (direction & DIRECTION_HORIZONTAL) {
        velocity = input.overallVelocityX;
      } else if (direction & DIRECTION_VERTICAL) {
        velocity = input.overallVelocityY;
      }
      return this._super.attrTest.call(this, input) && direction & input.offsetDirection && input.distance > this.options.threshold && input.maxPointers == this.options.pointers && abs(velocity) > this.options.velocity && input.eventType & INPUT_END;
    },
    emit: function (input) {
      var direction = directionStr(input.offsetDirection);
      if (direction) {
        this.manager.emit(this.options.event + direction, input);
      }
      this.manager.emit(this.options.event, input);
    }
  });

  /**
   * A tap is ecognized when the pointer is doing a small tap/click. Multiple taps are recognized if they occur
   * between the given interval and position. The delay option can be used to recognize multi-taps without firing
   * a single tap.
   *
   * The eventData from the emitted event contains the property `tapCount`, which contains the amount of
   * multi-taps being recognized.
   * @constructor
   * @extends Recognizer
   */
  function TapRecognizer() {
    Recognizer.apply(this, arguments);

    // previous time and center,
    // used for tap counting
    this.pTime = false;
    this.pCenter = false;
    this._timer = null;
    this._input = null;
    this.count = 0;
  }
  inherit(TapRecognizer, Recognizer, {
    /**
     * @namespace
     * @memberof PinchRecognizer
     */
    defaults: {
      event: 'tap',
      pointers: 1,
      taps: 1,
      interval: 300,
      // max time between the multi-tap taps
      time: 250,
      // max time of the pointer to be down (like finger on the screen)
      threshold: 9,
      // a minimal movement is ok, but keep it low
      posThreshold: 10 // a multi-tap can be a bit off the initial position
    },
    getTouchAction: function () {
      return [TOUCH_ACTION_MANIPULATION];
    },
    process: function (input) {
      var options = this.options;
      var validPointers = input.pointers.length === options.pointers;
      var validMovement = input.distance < options.threshold;
      var validTouchTime = input.deltaTime < options.time;
      this.reset();
      if (input.eventType & INPUT_START && this.count === 0) {
        return this.failTimeout();
      }

      // we only allow little movement
      // and we've reached an end event, so a tap is possible
      if (validMovement && validTouchTime && validPointers) {
        if (input.eventType != INPUT_END) {
          return this.failTimeout();
        }
        var validInterval = this.pTime ? input.timeStamp - this.pTime < options.interval : true;
        var validMultiTap = !this.pCenter || getDistance(this.pCenter, input.center) < options.posThreshold;
        this.pTime = input.timeStamp;
        this.pCenter = input.center;
        if (!validMultiTap || !validInterval) {
          this.count = 1;
        } else {
          this.count += 1;
        }
        this._input = input;

        // if tap count matches we have recognized it,
        // else it has began recognizing...
        var tapCount = this.count % options.taps;
        if (tapCount === 0) {
          // no failing requirements, immediately trigger the tap event
          // or wait as long as the multitap interval to trigger
          if (!this.hasRequireFailures()) {
            return STATE_RECOGNIZED;
          } else {
            this._timer = setTimeoutContext(function () {
              this.state = STATE_RECOGNIZED;
              this.tryEmit();
            }, options.interval, this);
            return STATE_BEGAN;
          }
        }
      }
      return STATE_FAILED;
    },
    failTimeout: function () {
      this._timer = setTimeoutContext(function () {
        this.state = STATE_FAILED;
      }, this.options.interval, this);
      return STATE_FAILED;
    },
    reset: function () {
      clearTimeout(this._timer);
    },
    emit: function () {
      if (this.state == STATE_RECOGNIZED) {
        this._input.tapCount = this.count;
        this.manager.emit(this.options.event, this._input);
      }
    }
  });

  /**
   * Simple way to create a manager with a default set of recognizers.
   * @param {HTMLElement} element
   * @param {Object} [options]
   * @constructor
   */
  function Hammer(element, options) {
    options = options || {};
    options.recognizers = ifUndefined(options.recognizers, Hammer.defaults.preset);
    return new Manager(element, options);
  }

  /**
   * @const {string}
   */
  Hammer.VERSION = '2.0.7';

  /**
   * default settings
   * @namespace
   */
  Hammer.defaults = {
    /**
     * set if DOM events are being triggered.
     * But this is slower and unused by simple implementations, so disabled by default.
     * @type {Boolean}
     * @default false
     */
    domEvents: false,
    /**
     * The value for the touchAction property/fallback.
     * When set to `compute` it will magically set the correct value based on the added recognizers.
     * @type {String}
     * @default compute
     */
    touchAction: TOUCH_ACTION_COMPUTE,
    /**
     * @type {Boolean}
     * @default true
     */
    enable: true,
    /**
     * EXPERIMENTAL FEATURE -- can be removed/changed
     * Change the parent input target element.
     * If Null, then it is being set the to main element.
     * @type {Null|EventTarget}
     * @default null
     */
    inputTarget: null,
    /**
     * force an input class
     * @type {Null|Function}
     * @default null
     */
    inputClass: null,
    /**
     * Default recognizer setup when calling `Hammer()`
     * When creating a new Manager these will be skipped.
     * @type {Array}
     */
    preset: [
    // RecognizerClass, options, [recognizeWith, ...], [requireFailure, ...]
    [RotateRecognizer, {
      enable: false
    }], [PinchRecognizer, {
      enable: false
    }, ['rotate']], [SwipeRecognizer, {
      direction: DIRECTION_HORIZONTAL
    }], [PanRecognizer, {
      direction: DIRECTION_HORIZONTAL
    }, ['swipe']], [TapRecognizer], [TapRecognizer, {
      event: 'doubletap',
      taps: 2
    }, ['tap']], [PressRecognizer]],
    /**
     * Some CSS properties can be used to improve the working of Hammer.
     * Add them to this method and they will be set when creating a new Manager.
     * @namespace
     */
    cssProps: {
      /**
       * Disables text selection to improve the dragging gesture. Mainly for desktop browsers.
       * @type {String}
       * @default 'none'
       */
      userSelect: 'none',
      /**
       * Disable the Windows Phone grippers when pressing an element.
       * @type {String}
       * @default 'none'
       */
      touchSelect: 'none',
      /**
       * Disables the default callout shown when you touch and hold a touch target.
       * On iOS, when you touch and hold a touch target such as a link, Safari displays
       * a callout containing information about the link. This property allows you to disable that callout.
       * @type {String}
       * @default 'none'
       */
      touchCallout: 'none',
      /**
       * Specifies whether zooming is enabled. Used by IE10>
       * @type {String}
       * @default 'none'
       */
      contentZooming: 'none',
      /**
       * Specifies that an entire element should be draggable instead of its contents. Mainly for desktop browsers.
       * @type {String}
       * @default 'none'
       */
      userDrag: 'none',
      /**
       * Overrides the highlight color shown when the user taps a link or a JavaScript
       * clickable element in iOS. This property obeys the alpha value, if specified.
       * @type {String}
       * @default 'rgba(0,0,0,0)'
       */
      tapHighlightColor: 'rgba(0,0,0,0)'
    }
  };
  var STOP = 1;
  var FORCED_STOP = 2;

  /**
   * Manager
   * @param {HTMLElement} element
   * @param {Object} [options]
   * @constructor
   */
  function Manager(element, options) {
    this.options = assign({}, Hammer.defaults, options || {});
    this.options.inputTarget = this.options.inputTarget || element;
    this.handlers = {};
    this.session = {};
    this.recognizers = [];
    this.oldCssProps = {};
    this.element = element;
    this.input = createInputInstance(this);
    this.touchAction = new TouchAction(this, this.options.touchAction);
    toggleCssProps(this, true);
    each(this.options.recognizers, function (item) {
      var recognizer = this.add(new item[0](item[1]));
      item[2] && recognizer.recognizeWith(item[2]);
      item[3] && recognizer.requireFailure(item[3]);
    }, this);
  }
  Manager.prototype = {
    /**
     * set options
     * @param {Object} options
     * @returns {Manager}
     */
    set: function (options) {
      assign(this.options, options);

      // Options that need a little more setup
      if (options.touchAction) {
        this.touchAction.update();
      }
      if (options.inputTarget) {
        // Clean up existing event listeners and reinitialize
        this.input.destroy();
        this.input.target = options.inputTarget;
        this.input.init();
      }
      return this;
    },
    /**
     * stop recognizing for this session.
     * This session will be discarded, when a new [input]start event is fired.
     * When forced, the recognizer cycle is stopped immediately.
     * @param {Boolean} [force]
     */
    stop: function (force) {
      this.session.stopped = force ? FORCED_STOP : STOP;
    },
    /**
     * run the recognizers!
     * called by the inputHandler function on every movement of the pointers (touches)
     * it walks through all the recognizers and tries to detect the gesture that is being made
     * @param {Object} inputData
     */
    recognize: function (inputData) {
      var session = this.session;
      if (session.stopped) {
        return;
      }

      // run the touch-action polyfill
      this.touchAction.preventDefaults(inputData);
      var recognizer;
      var recognizers = this.recognizers;

      // this holds the recognizer that is being recognized.
      // so the recognizer's state needs to be BEGAN, CHANGED, ENDED or RECOGNIZED
      // if no recognizer is detecting a thing, it is set to `null`
      var curRecognizer = session.curRecognizer;

      // reset when the last recognizer is recognized
      // or when we're in a new session
      if (!curRecognizer || curRecognizer && curRecognizer.state & STATE_RECOGNIZED) {
        curRecognizer = session.curRecognizer = null;
      }
      var i = 0;
      while (i < recognizers.length) {
        recognizer = recognizers[i];

        // find out if we are allowed try to recognize the input for this one.
        // 1.   allow if the session is NOT forced stopped (see the .stop() method)
        // 2.   allow if we still haven't recognized a gesture in this session, or the this recognizer is the one
        //      that is being recognized.
        // 3.   allow if the recognizer is allowed to run simultaneous with the current recognized recognizer.
        //      this can be setup with the `recognizeWith()` method on the recognizer.
        if (session.stopped !== FORCED_STOP && (
        // 1
        !curRecognizer || recognizer == curRecognizer ||
        // 2
        recognizer.canRecognizeWith(curRecognizer))) {
          // 3
          recognizer.recognize(inputData);
        } else {
          recognizer.reset();
        }

        // if the recognizer has been recognizing the input as a valid gesture, we want to store this one as the
        // current active recognizer. but only if we don't already have an active recognizer
        if (!curRecognizer && recognizer.state & (STATE_BEGAN | STATE_CHANGED | STATE_ENDED)) {
          curRecognizer = session.curRecognizer = recognizer;
        }
        i++;
      }
    },
    /**
     * get a recognizer by its event name.
     * @param {Recognizer|String} recognizer
     * @returns {Recognizer|Null}
     */
    get: function (recognizer) {
      if (recognizer instanceof Recognizer) {
        return recognizer;
      }
      var recognizers = this.recognizers;
      for (var i = 0; i < recognizers.length; i++) {
        if (recognizers[i].options.event == recognizer) {
          return recognizers[i];
        }
      }
      return null;
    },
    /**
     * add a recognizer to the manager
     * existing recognizers with the same event name will be removed
     * @param {Recognizer} recognizer
     * @returns {Recognizer|Manager}
     */
    add: function (recognizer) {
      if (invokeArrayArg(recognizer, 'add', this)) {
        return this;
      }

      // remove existing
      var existing = this.get(recognizer.options.event);
      if (existing) {
        this.remove(existing);
      }
      this.recognizers.push(recognizer);
      recognizer.manager = this;
      this.touchAction.update();
      return recognizer;
    },
    /**
     * remove a recognizer by name or instance
     * @param {Recognizer|String} recognizer
     * @returns {Manager}
     */
    remove: function (recognizer) {
      if (invokeArrayArg(recognizer, 'remove', this)) {
        return this;
      }
      recognizer = this.get(recognizer);

      // let's make sure this recognizer exists
      if (recognizer) {
        var recognizers = this.recognizers;
        var index = inArray(recognizers, recognizer);
        if (index !== -1) {
          recognizers.splice(index, 1);
          this.touchAction.update();
        }
      }
      return this;
    },
    /**
     * bind event
     * @param {String} events
     * @param {Function} handler
     * @returns {EventEmitter} this
     */
    on: function (events, handler) {
      if (events === undefined$1) {
        return;
      }
      if (handler === undefined$1) {
        return;
      }
      var handlers = this.handlers;
      each(splitStr(events), function (event) {
        handlers[event] = handlers[event] || [];
        handlers[event].push(handler);
      });
      return this;
    },
    /**
     * unbind event, leave emit blank to remove all handlers
     * @param {String} events
     * @param {Function} [handler]
     * @returns {EventEmitter} this
     */
    off: function (events, handler) {
      if (events === undefined$1) {
        return;
      }
      var handlers = this.handlers;
      each(splitStr(events), function (event) {
        if (!handler) {
          delete handlers[event];
        } else {
          handlers[event] && handlers[event].splice(inArray(handlers[event], handler), 1);
        }
      });
      return this;
    },
    /**
     * emit event to the listeners
     * @param {String} event
     * @param {Object} data
     */
    emit: function (event, data) {
      // we also want to trigger dom events
      if (this.options.domEvents) {
        triggerDomEvent(event, data);
      }

      // no handlers, so skip it all
      var handlers = this.handlers[event] && this.handlers[event].slice();
      if (!handlers || !handlers.length) {
        return;
      }
      data.type = event;
      data.preventDefault = function () {
        data.srcEvent.preventDefault();
      };
      var i = 0;
      while (i < handlers.length) {
        handlers[i](data);
        i++;
      }
    },
    /**
     * destroy the manager and unbinds all events
     * it doesn't unbind dom events, that is the user own responsibility
     */
    destroy: function () {
      this.element && toggleCssProps(this, false);
      this.handlers = {};
      this.session = {};
      this.input.destroy();
      this.element = null;
    }
  };

  /**
   * add/remove the css properties as defined in manager.options.cssProps
   * @param {Manager} manager
   * @param {Boolean} add
   */
  function toggleCssProps(manager, add) {
    var element = manager.element;
    if (!element.style) {
      return;
    }
    var prop;
    each(manager.options.cssProps, function (value, name) {
      prop = prefixed(element.style, name);
      if (add) {
        manager.oldCssProps[prop] = element.style[prop];
        element.style[prop] = value;
      } else {
        element.style[prop] = manager.oldCssProps[prop] || '';
      }
    });
    if (!add) {
      manager.oldCssProps = {};
    }
  }

  /**
   * trigger dom event
   * @param {String} event
   * @param {Object} data
   */
  function triggerDomEvent(event, data) {
    var gestureEvent = document.createEvent('Event');
    gestureEvent.initEvent(event, true, true);
    gestureEvent.gesture = data;
    data.target.dispatchEvent(gestureEvent);
  }
  assign(Hammer, {
    INPUT_START: INPUT_START,
    INPUT_MOVE: INPUT_MOVE,
    INPUT_END: INPUT_END,
    INPUT_CANCEL: INPUT_CANCEL,
    STATE_POSSIBLE: STATE_POSSIBLE,
    STATE_BEGAN: STATE_BEGAN,
    STATE_CHANGED: STATE_CHANGED,
    STATE_ENDED: STATE_ENDED,
    STATE_RECOGNIZED: STATE_RECOGNIZED,
    STATE_CANCELLED: STATE_CANCELLED,
    STATE_FAILED: STATE_FAILED,
    DIRECTION_NONE: DIRECTION_NONE,
    DIRECTION_LEFT: DIRECTION_LEFT,
    DIRECTION_RIGHT: DIRECTION_RIGHT,
    DIRECTION_UP: DIRECTION_UP,
    DIRECTION_DOWN: DIRECTION_DOWN,
    DIRECTION_HORIZONTAL: DIRECTION_HORIZONTAL,
    DIRECTION_VERTICAL: DIRECTION_VERTICAL,
    DIRECTION_ALL: DIRECTION_ALL,
    Manager: Manager,
    Input: Input,
    TouchAction: TouchAction,
    TouchInput: TouchInput,
    MouseInput: MouseInput,
    PointerEventInput: PointerEventInput,
    TouchMouseInput: TouchMouseInput,
    SingleTouchInput: SingleTouchInput,
    Recognizer: Recognizer,
    AttrRecognizer: AttrRecognizer,
    Tap: TapRecognizer,
    Pan: PanRecognizer,
    Swipe: SwipeRecognizer,
    Pinch: PinchRecognizer,
    Rotate: RotateRecognizer,
    Press: PressRecognizer,
    on: addEventListeners,
    off: removeEventListeners,
    each: each,
    merge: merge,
    extend: extend,
    assign: assign,
    inherit: inherit,
    bindFn: bindFn,
    prefixed: prefixed
  });

  // this prevents errors when Hammer is loaded in the presence of an AMD
  //  style loader but by script tag, not by the loader.
  var freeGlobal = typeof window !== 'undefined' ? window : typeof self !== 'undefined' ? self : {}; // jshint ignore:line
  freeGlobal.Hammer = Hammer;
  if (typeof undefined$1 === 'function' && undefined$1.amd) {
    undefined$1(function () {
      return Hammer;
    });
  } else if (module.exports) {
    module.exports = Hammer;
  } else {
    window[exportName] = Hammer;
  }
})(window, document, 'Hammer');
});

//
// import BScroll from 'better-scroll'

var script$2l = {
  name: 'PreviewLooseLeaf',
  props: {
    looseLeaf: Object,
    businessType: String,
    // 业务类型：looseLeaf，textBook
    looseLeafId: {
      type: String,
      default: ''
    },
    pTitle: {
      type: String,
      default: ''
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    officePreviewPath: {
      type: String,
      default: ''
    },
    readerType: {
      type: String,
      default: 'reader'
    },
    pageType: {
      type: String,
      default: 'h5'
    },
    // 页面类型：取值h5、pc、pad
    CompList: Array,
    textDomList: Array,
    textBookList: Array,
    isTrial: {
      // 是否为试读模式
      type: Boolean,
      default: false
    },
    trialText: {
      // 试读结束提示语
      type: String,
      default: '<p>--- 试读结束 ---</p>'
    },
    isShowAi: {
      type: String,
      default: '1'
    },
    userType: {
      // 用户类型，可选teacher、student
      type: String,
      default: 'teacher'
    },
    resourcesList: {
      //教师资源列表
      type: Array,
      default: () => []
    },
    teacherNoteList: {
      //本章节教师笔记列表
      type: Array,
      default: () => []
    },
    studentNoteList: {
      //本章节教师笔记列表
      type: Array,
      default: () => []
    },
    highlightList: {
      //本章节高亮列表
      type: Array,
      default: () => []
    },
    isShowTeacherResource: {
      // 是否显示教师资源
      type: Boolean,
      default: false
    },
    isCopyUrl: {
      type: Boolean,
      default: false
    },
    groupHeight: {
      //每个块的占位高度
      type: Number,
      default: 600
    },
    loadingMode: {
      // 加载模式，可选roll、page
      type: String,
      default: 'roll'
    }
  },
  data() {
    return {
      xmlCompList: [],
      looseLeafData: {},
      title: '',
      showEmpty: false,
      inlineWordsModal: {
        title: '',
        visible: false,
        content: ''
      },
      mouseOverGroupIndex: -1,
      readerBoxId: `readerbox_#_${new Date().getTime()}`,
      hammerManager: null,
      bscroll: null,
      startIndex: 0,
      endIndex: 20,
      isInitialLoad: true,
      //是否是初次加载
      isScrollMore: true,
      //是否可以加载更多
      isScrollDown: true,
      //是否向下滚动
      pageHeight: this.CompList.length * this.groupHeight,
      //页面总高度
      contnetHeight: 0,
      //当前加载教材内容的高度
      paddingTop: 0,
      //顶部占位高度
      paddingBottom: 0,
      //底部占位高度
      endTime: 0,
      pointerEvents: 'auto',
      scrollTop: 0,
      stopScroll: false
    };
  },
  provide() {
    return {
      getProviderUserType: () => this.userType,
      getProviderInsideIndex: () => this.mouseOverGroupIndex,
      getReaderBoxId: () => this.readerBoxId,
      getTeacherNoteList: () => this.teacherNoteList,
      getStudentNoteList: () => this.studentNoteList,
      getHighlightList: () => this.highlightList,
      getInitialLoad: () => this.isInitialLoad,
      getResourceBasisPath: () => this.resourceBasisPath
    };
  },
  watch: {
    looseLeafId: {
      handler(newValue) {
        // if (newValue) {
        //   this.queryData()
        // }
      },
      immediate: true,
      deep: true
    },
    pTitle: {
      handler(newValue) {
        this.title = newValue;
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    contentStyle() {
      let data = {
        height: this.pageHeight + 'px',
        padding: `${this.paddingTop}px 0px ${this.paddingBottom}px`,
        justifyContent: this.isScrollDown ? 'flex-start' : 'flex-end'
      };
      return data;
    },
    xmlDomList() {
      return this.CompList.slice(this.startIndex, this.endIndex);
    }
  },
  created() {
    // this.$nextTick(() => {
    //   setTimeout(() => {
    //     $('a').attr('target', '_self')
    //   }, 2000)
    //   window.inlineWordsShow = (uuid) => {
    //     const ele = document.getElementById(`words__${uuid}`)
    //     const { title = '', content = '' } = ele.dataset || {}
    //     this.inlineWordsModal = { title, content, visible: true }
    //   }
    // })
    // this.xmlCompList = this.CompList
    // setTimeout(() => {
    //   this.showDom = true
    // }, 1000)
  },
  mounted() {
    delete hammer.defaults.cssProps.userSelect;
    this.$nextTick(() => {
      let readerBox = document.getElementById(this.readerBoxId);
      let hammerManager = new hammer.Manager(readerBox, {
        touchAction: 'pan-y'
      });
      hammerManager.add(new hammer.Pan({
        event: 'panstart',
        pointers: 0,
        threshold: 0
      }));
      hammerManager.get('panstart').set({
        direction: hammer.DIRECTION_ALL
      });
      hammerManager.add(new hammer.Pan({
        event: 'panmove',
        pointers: 0,
        threshold: 5
      }).recognizeWith(hammerManager.get('panstart')));
      hammerManager.add(new hammer.Pan({
        event: 'panend',
        pointers: 1,
        threshold: 32
      }).recognizeWith(hammerManager.get('panstart')));
      hammerManager.on('panend', function (e) {
        // console.log("%c%s","font-size:2em;background: #00965E;color: #FFF",'Panend~~~',e)
      });
      this.hammerManager = hammerManager;
      setTimeout(() => {
        if (this.readerType !== 'loose-leaf') {
          if (this.loadingMode == 'page') {
            this.endIndex = this.CompList[0].endIndex;
          }
          // this.initSlider()
          // this.getPageHeight()
        } else {
          this.endIndex = this.CompList.length;
        }
      }, 200);
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 3000);

      // this.$EventBus.$on('tippyChange', (data) => {
      //   // tippy显示时禁止页面滚动
      //   if (data.state) {
      //     this.bscroll.disable() //禁用 BetterScroll
      //   } else {
      //     this.bscroll.enable() //启用 BetterScroll
      //   }
      // })
    });
  },
  methods: {
    goBack() {
      this.$emit('onGoBack');
    },
    handleMouseOver(e) {
      const path = e.composedPath();
      const groupTarget = path.find(item => item.classList && item.classList.contains('parent-group'));
      groupTarget?.getAttribute('id') || '';
      // this.mouseOverGroupIndex = id ? this.CompList.findIndex((item) => item.location === id) : -1
    },
    downloadFile(e) {
      this.$emit('downloadFile', e);
    },
    showFileReader(e) {
      this.$emit('showFileReader', e);
    },
    openTestPaper({
      info,
      showRobot
    }) {
      this.$emit('openTestPaper', {
        info: info,
        showRobot: showRobot
      });
    },
    openAIanalysis(e) {
      this.$emit('setanalysis', e);
    },
    clickComp(item) {
      this.mouseOverGroupIndex = item.componentIndex;
      this.$emit('clickComp', item);
    },
    // 驼峰命名
    allCapital(value) {
      const matched = value.match(/[A-Z]+/);
      return matched && matched[0] === value;
    },
    // 添加教师资源
    addTeacherResources(item) {
      this.$emit('addTeacherResources', item);
    },
    // 短横线命名
    toKebabCase(value) {
      if (this.allCapital(value)) {
        return value.toLocaleLowerCase();
      }
      return value.charAt(0).toLowerCase() + value.slice(1).replace(/([A-Z])/g, '-$1').toLowerCase();
    },
    // 计算某个块中的教师资源数量
    getResourcesNumber(f) {
      let s = JSON.stringify(this.resourcesList);
      let r = s.indexOf(f);
      let c = 0;
      while (r != -1) {
        c++;
        r = s.indexOf(f, r + 1);
      }
      return c;
    },
    // 打开教师资源列表
    openTeacherResoucesList(item) {
      this.getResourcesNumber(item.location);
      setTimeout(() => {
        // if (num > 0) {
        this.$emit('openTeacherResoucesList', item);
        // }
      }, 300);
    },
    openHtml(data) {
      this.$emit('openHtml', data);
    },
    copyHtmlUrl(data) {
      this.$emit('copyHtmlUrl', data);
    },
    scroll(e) {
      if (this.loadingMode == 'roll') {
        if (this.endTime + 1000 < new Date().getTime()) {
          this.endTime = new Date().getTime();
          this.getPageHeight();
        }
        let scrollDom = document.querySelector('.xml-reader-window-Preview-dom');
        if (scrollDom.scrollTop > this.scrollTop && this.isScrollMore) {
          // 向下滚动
          this.getPaddingTop(e);
        } else if (scrollDom.scrollTop < this.scrollTop && this.isScrollMore) {
          // 向上滚动
          this.getPaddingBottom(e);
        }
        this.scrollTop = scrollDom.scrollTop;
      }
    },
    // 计算顶部占位高度
    getPaddingTop() {
      if (!this.isScrollDown) {
        this.isScrollMore = false;
        let contnetHeight = 0;
        let nodes = this.$refs.items;
        nodes.forEach(node => {
          // 获取渲染内容真实DOM高度
          const {
            height
          } = node.getBoundingClientRect();
          contnetHeight += height;
        });
        this.contnetHeight = contnetHeight;
        // 如果上次滚动方向是向上滚动，将占位内容改为顶部
        this.paddingTop = this.pageHeight - this.paddingBottom - this.contnetHeight;
        setTimeout(() => {
          this.isScrollMore = true;
        }, 500);
      }
      this.paddingBottom = 0;
      this.isScrollDown = true;
      //向下滚动
      if (this.isScrollDown && this.isScrollMore && this.CompList.length > this.endIndex) {
        // 如果顶部占位高度+渲染内容高度+可视窗口高度-滚动条高度小于1000，就加载更多
        if (this.paddingTop + this.contnetHeight - window.innerHeight - this.scrollTop < 1000) {
          this.isScrollMore = false;
          if (this.pageType != 'pc') {
            // 防止移动端滚动惯性导致页面滑动超出，停止页面滚动
            setTimeout(() => {
              this.stopScroll = true;
            }, 100);
          }
          let startIndex = 0;
          let paddingTop = 0;
          let nodes = this.$refs.items || [];
          nodes.forEach((node, index) => {
            // 遍历当前渲染的Dom，如果超出可视窗口超过3000px就销毁
            const {
              height,
              y
            } = node.getBoundingClientRect();
            if (y < -1500) {
              startIndex++;
              paddingTop += height;
            }
          });
          if (startIndex > 0) {
            this.paddingTop += paddingTop;
            this.startIndex += startIndex;
          }
          setTimeout(() => {
            this.loadMore('down');
          }, 70);
        }
      }
    },
    // 计算底部占位高度
    getPaddingBottom() {
      if (this.isScrollDown) {
        this.isScrollMore = false;
        let contnetHeight = 0;
        let nodes = this.$refs.items;
        nodes.forEach(node => {
          // 获取渲染内容真实DOM高度
          const {
            height
          } = node.getBoundingClientRect();
          contnetHeight += height;
        });
        this.contnetHeight = contnetHeight;
        // 如果上次滚动方向是向下滚动，将占位内容改为底部
        this.paddingBottom = this.pageHeight - this.paddingTop - this.contnetHeight;
        setTimeout(() => {
          this.isScrollMore = true;
        }, 500);
      }
      this.paddingTop = 0;
      this.isScrollDown = false;
      //向上滚动
      if (!this.isScrollDown && this.isScrollMore && this.startIndex > 0) {
        // 如果页面总高度-滚动条高度-底部占位高度-渲染内容高度大于-1000，就加载更多
        if (this.pageHeight - this.scrollTop - this.paddingBottom - this.contnetHeight > -2000) {
          this.isScrollMore = false;
          if (this.pageType != 'pc') {
            // 防止移动端滚动惯性导致页面滑动超出，停止页面滚动
            setTimeout(() => {
              this.stopScroll = true;
            }, 100);
          }
          // this.$refs.items获取的dom数组是乱序的，使用querySelectorAll获取
          let nodeList = document.querySelectorAll('.xml-parent-group-block') || [];
          let nodes = Array.from(nodeList);
          let endIndex = 0;
          let paddingBottom = 0;
          nodes.forEach((node, index) => {
            // 遍历当前渲染的Dom，如果超出可视窗口超过1000px就销毁
            const {
              height,
              y
            } = node.getBoundingClientRect();
            if (y - window.innerHeight - 2000 > 0) {
              endIndex++;
              paddingBottom += height;
            }
          });
          if (endIndex > 0) {
            this.endIndex -= endIndex;
            this.paddingBottom += paddingBottom;
          }
          setTimeout(() => {
            this.loadMore('up');
          }, 70);
        }
      }
    },
    // 加载更多
    loadMore(direction) {
      let num = 0;
      this.isScrollMore = false;
      if (direction == 'down') {
        num = this.CompList.length - this.endIndex > 10 ? 10 : this.CompList.length - this.endIndex;
        this.endIndex += num;
      } else if (direction == 'up') {
        num = this.startIndex > 10 ? 10 : this.startIndex;
        this.startIndex -= num;
      }
      this.getPageHeight();
      setTimeout(() => {
        this.isScrollMore = true;
        this.stopScroll = false;
        this.$emit('initObserver');
      }, 200);
    },
    // 获取页面高度
    getPageHeight() {
      let contnetHeight = 0;
      this.$nextTick(() => {
        setTimeout(() => {
          let nodes = this.$refs.items;
          nodes.forEach(node => {
            // 获取渲染内容真实DOM高度
            const {
              height
            } = node.getBoundingClientRect();
            contnetHeight += height;
          });
          this.contnetHeight = contnetHeight;
          if (this.endIndex === this.CompList.length && this.isScrollDown && this.pageHeight != this.paddingTop + this.contnetHeight) {
            //向下滚动加载完成了最后一个块
            this.stopScroll = true; //禁止滚动
            this.pageHeight = this.paddingTop + this.contnetHeight;
            this.paddingBottom = 0;
            setTimeout(() => {
              this.stopScroll = false; //启用滚动
            }, 200);
          } else if (this.startIndex === 0 && !this.isScrollDown && this.pageHeight != this.paddingBottom + this.contnetHeight) {
            //向上滚动加载了第一个块
            this.stopScroll = true; //禁止滚动
            let pageHeight = this.pageHeight;
            this.pageHeight = this.paddingBottom + this.contnetHeight;
            this.paddingTop = 0;
            this.scrollPageTo(pageHeight - this.pageHeight - this.scrollTop);
            setTimeout(() => {
              this.stopScroll = false; //启用滚动
            }, 200);
          }
          if (this.pageHeight < this.paddingTop + this.contnetHeight || this.pageHeight < this.paddingBottom + this.contnetHeight) {
            //当前页面高度小于 占位高度+渲染内容高度,如果页面高度不够则增加一万px
            this.stopScroll = true; //禁止滚动
            this.pageHeight += 10000;
            if (!this.isScrollDown) {
              let y = this.scrollTop;
              this.scrollPageTo(y + 10000);
            }
            setTimeout(() => {
              this.stopScroll = false; //启用滚动
            }, 200);
          }
        }, 100);
      });
    },
    /**
     * 跳转到指定位置
     * @param {*} componentIndex 要跳转到块的索引
     * @param {*} maxTop 滚动距离后距离可视窗口顶部的距离
     * @param {*} type 跳转类型resource：资源，search：搜索，marker：标记
     * @param {*} selectorType 选择器selectorId的类型：id，class
     * @param {*} selectorId 选择器id或class值
     * @param {*} markTarget 笔记、高亮的item
     * @param {*} classIndex type为class时，用于滚动到第几个Dom
     */
    jumpTo(data, callback) {
      // console.log('🚀 ~ jumpTo ~ data:', data, this.CompList.length)
      this.isScrollMore = false;
      const {
        componentIndex,
        maxTop,
        selectorType,
        selectorId,
        classIndex
      } = data;
      if (this.loadingMode == 'page') {
        this.startIndex = this.CompList[componentIndex].startIndex;
        this.endIndex = this.CompList[componentIndex].endIndex;
        console.log(this.startIndex, this.endIndex, 'aaaaaaaaaaaaaaaa');
      } else if (this.startIndex > componentIndex || this.endIndex < componentIndex) {
        if (componentIndex < 10) {
          this.startIndex = 0;
          this.endIndex = 20;
        } else if (this.CompList.length - componentIndex > 20 && componentIndex >= 10) {
          this.startIndex = componentIndex - 10;
          this.endIndex = componentIndex + 10;
        } else if (this.CompList.length - componentIndex <= 20) {
          this.endIndex = this.CompList.length;
          this.startIndex = this.CompList.length > 20 ? this.CompList.length - 20 : 0;
        }
        this.paddingTop = this.startIndex * this.groupHeight;
        this.paddingBottom = 0;
      }
      setTimeout(() => {
        this.$nextTick(() => {
          let scrollItem = document.getElementById('scroll-item-' + componentIndex);
          if (scrollItem) {
            let {
              height
            } = scrollItem.getBoundingClientRect(); //要跳转到块的高度
            let scrollTo = scrollItem.getBoundingClientRect().top + this.scrollTop;
            this.$nextTick(() => {
              this.$emit('initObserver');
              if (this.loadingMode == 'roll') {
                this.getPageHeight();
              }
              let top = 0;
              this.scrollPageTo(scrollTo + maxTop - 100);
              if (callback && typeof callback === 'function') {
                callback();
              }
              if (selectorType == 'id') {
                //跳转目标是笔记、高亮、等通过id获取位置的元素
                setTimeout(() => {
                  top = document.getElementById(selectorId) ? document.getElementById(selectorId)?.getBoundingClientRect().top : 0;
                  this.scrollPageTo(scrollTo + maxTop - 100 + top - 230);
                }, 200);
              } else if (selectorType == 'class') {
                //跳转目标是资源类型通过class获取位置的元素
                let typeList = ['xml-video-title', 'xml-image-title'];
                if (!typeList.includes(selectorId)) {
                  height = 0;
                }
                setTimeout(() => {
                  let Dom = scrollItem.querySelectorAll(`.${selectorId}`)[classIndex];
                  top = Dom ? Dom?.getBoundingClientRect().top : 0;
                  this.scrollPageTo(scrollTo + maxTop - 100 + top - height - 230);
                }, 200);
              }
            });
          }
          if (this.loadingMode == 'roll') {
            setTimeout(() => {
              this.isScrollMore = true;
              if (this.paddingBottom == 0) {
                // 向下滚动
                this.getPaddingTop();
              } else {
                // 向上滚动
                this.getPaddingBottom();
              }
            }, 700);
          }
        });
      }, 500);
    },
    scrollPageTo(top) {
      let scrollDom = document.querySelector('.xml-reader-window-Preview-dom');
      if (scrollDom) {
        scrollDom.scroll(0, top);
      }
    },
    // 处理样式变更引发的重排或重绘后笔记位置的同步更新
    previewRepaint() {
      this.xmlDomList.map(item => {
        this.$EventBus.$emit(`previewRepaint-${item.location}`);
      });
    }
  }
};

/* script */
const __vue_script__$2l = script$2l;
/* template */
var __vue_render__$2l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-reader-window-box xml-reader-window-Preview-dom",
      style: {
        "--xml-iframe-pointer-events": _vm.pointerEvents,
        overflow: _vm.stopScroll ? "hidden" : "auto",
      },
      attrs: { id: _vm.readerBoxId },
      on: { mouseover: _vm.handleMouseOver, scroll: _vm.scroll },
    },
    [
      _c("div", { staticClass: "content reader-content" }, [
        _c(
          "div",
          {
            staticClass: "text-books-content",
            style:
              _vm.readerType !== "loose-leaf" && _vm.loadingMode !== "page"
                ? _vm.contentStyle
                : "",
          },
          _vm._l(_vm.xmlDomList, function (item, index) {
            return _c(
              "div",
              {
                key: item.componentIndex,
                ref: "items",
                refInFor: true,
                staticClass: "xml-parent-group-block",
                class: [item.chapterId, item.location],
                attrs: {
                  componentIndex: item.componentIndex,
                  id: "scroll-item-" + item.componentIndex,
                },
                on: {
                  mousedown: function ($event) {
                    return _vm.clickComp(item)
                  },
                  touchstart: function ($event) {
                    return _vm.clickComp(item)
                  },
                },
              },
              [
                _c(_vm.toKebabCase(item.componentsName) + "-render", {
                  ref: item.xmlCompRef,
                  refInFor: true,
                  tag: "component",
                  attrs: {
                    id: item.location,
                    location: item.location,
                    componentIndex: item.componentIndex,
                    mode: "preview",
                    "page-type": _vm.pageType,
                    "p-group-data": item.data.pGroupData,
                    "current-rule": item,
                    "rule-index": item.componentIndex,
                    "extend-params": item.data.extendParams,
                    resourceBasisPath: _vm.resourceBasisPath,
                    officePreviewPath: _vm.officePreviewPath,
                    isShowAi: _vm.isShowAi,
                    isCopyUrl: _vm.isCopyUrl,
                    isReader: true,
                  },
                  on: {
                    setanalysis: _vm.openAIanalysis,
                    downloadFileUrl: _vm.downloadFile,
                    showFileReader: _vm.showFileReader,
                    openTestPaper: _vm.openTestPaper,
                    openHtml: _vm.openHtml,
                    copyHtmlUrl: _vm.copyHtmlUrl,
                    submitAnswer: function ($event) {
                      return _vm.$emit("submitAnswer", $event)
                    },
                  },
                }),
                _vm._v(" "),
                _vm.getResourcesNumber(item.location) > 0 &&
                _vm.isShowTeacherResource
                  ? [
                      _c(
                        "div",
                        {
                          staticClass: "teacher-resources-number-h5",
                          on: {
                            click: function ($event) {
                              return _vm.openTeacherResoucesList(item)
                            },
                          },
                        },
                        [
                          _c("span", { staticClass: "number" }, [
                            _vm._v(
                              _vm._s(_vm.getResourcesNumber(item.location))
                            ),
                          ]),
                        ]
                      ),
                    ]
                  : _vm._e(),
              ],
              2
            )
          }),
          0
        ),
        _vm._v(" "),
        _vm.isTrial && _vm.endIndex >= _vm.CompList.length
          ? _c("div", {
              staticClass: "the-end-of-the-trial",
              domProps: { innerHTML: _vm._s(_vm.trialText) },
            })
          : _vm._e(),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$2l = [];
__vue_render__$2l._withStripped = true;

  /* style */
  const __vue_inject_styles__$2l = undefined;
  /* scoped */
  const __vue_scope_id__$2l = "data-v-bfd21e06";
  /* module identifier */
  const __vue_module_identifier__$2l = undefined;
  /* functional template */
  const __vue_is_functional_template__$2l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2l, staticRenderFns: __vue_staticRenderFns__$2l },
    __vue_inject_styles__$2l,
    __vue_script__$2l,
    __vue_scope_id__$2l,
    __vue_is_functional_template__$2l,
    __vue_module_identifier__$2l,
    false,
    undefined,
    undefined,
    undefined
  );

const ReaderComponents = [__vue_component__$2m, __vue_component__$2l, __vue_component__$2u];
var XmlReader = {
  // install,
  ...ReaderComponents
};

//
var script$2k = {
  mixins: [singleGroupMixin],
  name: 'titleThirdLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
    console.log('此方法接收块儿点击事件，事件传递块相关参数=1111111=2=', this.groupData);
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    /**
     * 更新块数据
     * @param dataName  数据name
     * @param blockData 数据值
     */
    updateBlockData(dataName, blockData) {
      this.groupData[dataName] = blockData;
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2k = script$2k;
/* template */
var __vue_render__$2k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-third-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left" }, [
              _c(
                "span",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      "rule-index": _vm.ruleIndex,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2k = [];
__vue_render__$2k._withStripped = true;

  /* style */
  const __vue_inject_styles__$2k = undefined;
  /* scoped */
  const __vue_scope_id__$2k = "data-v-14dffda4";
  /* module identifier */
  const __vue_module_identifier__$2k = undefined;
  /* functional template */
  const __vue_is_functional_template__$2k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2k, staticRenderFns: __vue_staticRenderFns__$2k },
    __vue_inject_styles__$2k,
    __vue_script__$2k,
    __vue_scope_id__$2k,
    __vue_is_functional_template__$2k,
    __vue_module_identifier__$2k,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2j = {
  mixins: [singleGroupMixin],
  name: 'titleFirstLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
    console.log(this.groupData);
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2j = script$2j;
/* template */
var __vue_render__$2j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-first-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2j = [];
__vue_render__$2j._withStripped = true;

  /* style */
  const __vue_inject_styles__$2j = undefined;
  /* scoped */
  const __vue_scope_id__$2j = "data-v-236ee035";
  /* module identifier */
  const __vue_module_identifier__$2j = undefined;
  /* functional template */
  const __vue_is_functional_template__$2j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2j, staticRenderFns: __vue_staticRenderFns__$2j },
    __vue_inject_styles__$2j,
    __vue_script__$2j,
    __vue_scope_id__$2j,
    __vue_is_functional_template__$2j,
    __vue_module_identifier__$2j,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2i = {
  mixins: [singleGroupMixin],
  name: 'titleSixthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2i = script$2i;
/* template */
var __vue_render__$2i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-sixth-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left-div" }, [
              _c(
                "div",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container-left",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "title-left-line" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-right-div" },
              [
                _c("div", { staticClass: "title-right-line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "title-right-line2" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container-right",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
                _vm._v(" "),
                _c("div", { staticClass: "title-right-line3" }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2i = [];
__vue_render__$2i._withStripped = true;

  /* style */
  const __vue_inject_styles__$2i = undefined;
  /* scoped */
  const __vue_scope_id__$2i = "data-v-287a88b8";
  /* module identifier */
  const __vue_module_identifier__$2i = undefined;
  /* functional template */
  const __vue_is_functional_template__$2i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2i, staticRenderFns: __vue_staticRenderFns__$2i },
    __vue_inject_styles__$2i,
    __vue_script__$2i,
    __vue_scope_id__$2i,
    __vue_is_functional_template__$2i,
    __vue_module_identifier__$2i,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2h = {
  mixins: [singleGroupMixin],
  name: 'titleSemicircleLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2h = script$2h;
/* template */
var __vue_render__$2h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-semicircle-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2h = [];
__vue_render__$2h._withStripped = true;

  /* style */
  const __vue_inject_styles__$2h = undefined;
  /* scoped */
  const __vue_scope_id__$2h = "data-v-a09b1208";
  /* module identifier */
  const __vue_module_identifier__$2h = undefined;
  /* functional template */
  const __vue_is_functional_template__$2h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2h, staticRenderFns: __vue_staticRenderFns__$2h },
    __vue_inject_styles__$2h,
    __vue_script__$2h,
    __vue_scope_id__$2h,
    __vue_is_functional_template__$2h,
    __vue_module_identifier__$2h,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2g = {
  mixins: [singleGroupMixin],
  name: 'titleRectangleLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$2g = script$2g;
/* template */
var __vue_render__$2g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-rectangle-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2g = [];
__vue_render__$2g._withStripped = true;

  /* style */
  const __vue_inject_styles__$2g = undefined;
  /* scoped */
  const __vue_scope_id__$2g = "data-v-530cf0c3";
  /* module identifier */
  const __vue_module_identifier__$2g = undefined;
  /* functional template */
  const __vue_is_functional_template__$2g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2g, staticRenderFns: __vue_staticRenderFns__$2g },
    __vue_inject_styles__$2g,
    __vue_script__$2g,
    __vue_scope_id__$2g,
    __vue_is_functional_template__$2g,
    __vue_module_identifier__$2g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2f = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2f = script$2f;
/* template */
var __vue_render__$2f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme1-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    resourceBasisPath: _vm.resourceBasisPath,
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
                _vm._v(" "),
                _c("div", { staticClass: "jiantou1" }),
                _vm._v(" "),
                _c("div", { staticClass: "jiantou2" }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }, [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2f = [];
__vue_render__$2f._withStripped = true;

  /* style */
  const __vue_inject_styles__$2f = undefined;
  /* scoped */
  const __vue_scope_id__$2f = "data-v-365aa88d";
  /* module identifier */
  const __vue_module_identifier__$2f = undefined;
  /* functional template */
  const __vue_is_functional_template__$2f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2f, staticRenderFns: __vue_staticRenderFns__$2f },
    __vue_inject_styles__$2f,
    __vue_script__$2f,
    __vue_scope_id__$2f,
    __vue_is_functional_template__$2f,
    __vue_module_identifier__$2f,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2e = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2e = script$2e;
/* template */
var __vue_render__$2e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme2-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-icon" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2e = [];
__vue_render__$2e._withStripped = true;

  /* style */
  const __vue_inject_styles__$2e = undefined;
  /* scoped */
  const __vue_scope_id__$2e = "data-v-2ccaa237";
  /* module identifier */
  const __vue_module_identifier__$2e = undefined;
  /* functional template */
  const __vue_is_functional_template__$2e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2e, staticRenderFns: __vue_staticRenderFns__$2e },
    __vue_inject_styles__$2e,
    __vue_script__$2e,
    __vue_scope_id__$2e,
    __vue_is_functional_template__$2e,
    __vue_module_identifier__$2e,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2d = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2d = script$2d;
/* template */
var __vue_render__$2d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme4-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2d = [];
__vue_render__$2d._withStripped = true;

  /* style */
  const __vue_inject_styles__$2d = undefined;
  /* scoped */
  const __vue_scope_id__$2d = "data-v-77a57b9d";
  /* module identifier */
  const __vue_module_identifier__$2d = undefined;
  /* functional template */
  const __vue_is_functional_template__$2d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2d, staticRenderFns: __vue_staticRenderFns__$2d },
    __vue_inject_styles__$2d,
    __vue_script__$2d,
    __vue_scope_id__$2d,
    __vue_is_functional_template__$2d,
    __vue_module_identifier__$2d,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2c = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2c = script$2c;
/* template */
var __vue_render__$2c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme5-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-img" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-img" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2c = [];
__vue_render__$2c._withStripped = true;

  /* style */
  const __vue_inject_styles__$2c = undefined;
  /* scoped */
  const __vue_scope_id__$2c = "data-v-52282817";
  /* module identifier */
  const __vue_module_identifier__$2c = undefined;
  /* functional template */
  const __vue_is_functional_template__$2c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2c, staticRenderFns: __vue_staticRenderFns__$2c },
    __vue_inject_styles__$2c,
    __vue_script__$2c,
    __vue_scope_id__$2c,
    __vue_is_functional_template__$2c,
    __vue_module_identifier__$2c,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2b = {
  mixins: [singleGroupMixin],
  name: 'titleYellowtheme6LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2b = script$2b;
/* template */
var __vue_render__$2b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Yellowtheme6-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "zhuangshi1" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "zhuangshi2" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2b = [];
__vue_render__$2b._withStripped = true;

  /* style */
  const __vue_inject_styles__$2b = undefined;
  /* scoped */
  const __vue_scope_id__$2b = "data-v-01df3a41";
  /* module identifier */
  const __vue_module_identifier__$2b = undefined;
  /* functional template */
  const __vue_is_functional_template__$2b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2b, staticRenderFns: __vue_staticRenderFns__$2b },
    __vue_inject_styles__$2b,
    __vue_script__$2b,
    __vue_scope_id__$2b,
    __vue_is_functional_template__$2b,
    __vue_module_identifier__$2b,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$2a = {
  mixins: [singleGroupMixin],
  name: 'titleFifteenLibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  isUnPrefix: true,
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$2a = script$2a;
/* template */
var __vue_render__$2a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fifteen-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$2a = [];
__vue_render__$2a._withStripped = true;

  /* style */
  const __vue_inject_styles__$2a = undefined;
  /* scoped */
  const __vue_scope_id__$2a = "data-v-ecf0144a";
  /* module identifier */
  const __vue_module_identifier__$2a = undefined;
  /* functional template */
  const __vue_is_functional_template__$2a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2a, staticRenderFns: __vue_staticRenderFns__$2a },
    __vue_inject_styles__$2a,
    __vue_script__$2a,
    __vue_scope_id__$2a,
    __vue_is_functional_template__$2a,
    __vue_module_identifier__$2a,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$29 = {
  mixins: [singleGroupMixin],
  name: 'titleThirteenthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$29 = script$29;
/* template */
var __vue_render__$29 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirteenth-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "topleft" }),
              _vm._v(" "),
              _c("div", { staticClass: "topborder" }, [
                _c("div", { staticClass: "topborder-left" }, [
                  _c("div", { staticClass: "Line1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "Line2" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "block" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "topborder-right" }, [
                  _c("div", { staticClass: "Line3" }),
                ]),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "bottomborder" }, [
                _c("div", { staticClass: "Line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block" }),
                _vm._v(" "),
                _c("div", { staticClass: "block" }),
                _vm._v(" "),
                _c("div", { staticClass: "Line2" }),
                _vm._v(" "),
                _c("div", { staticClass: "Line3" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "rightbottom" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$29 = [];
__vue_render__$29._withStripped = true;

  /* style */
  const __vue_inject_styles__$29 = undefined;
  /* scoped */
  const __vue_scope_id__$29 = "data-v-b9e73b40";
  /* module identifier */
  const __vue_module_identifier__$29 = undefined;
  /* functional template */
  const __vue_is_functional_template__$29 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$29 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$29, staticRenderFns: __vue_staticRenderFns__$29 },
    __vue_inject_styles__$29,
    __vue_script__$29,
    __vue_scope_id__$29,
    __vue_is_functional_template__$29,
    __vue_module_identifier__$29,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$28 = {
  mixins: [singleGroupMixin],
  name: 'titleTwelfthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    /**
     * 更新块数据
     * @param dataName  数据name
     * @param blockData 数据值
     */
    updateBlockData(dataName, blockData) {
      this.groupData[dataName] = blockData;
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$28 = script$28;
/* template */
var __vue_render__$28 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-twelfth-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-left" }, [
              _c(
                "span",
                { staticClass: "title-number" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.numberTextDataName],
                      "block-data-name": _vm.numberTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      "rule-index": _vm.ruleIndex,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$28 = [];
__vue_render__$28._withStripped = true;

  /* style */
  const __vue_inject_styles__$28 = undefined;
  /* scoped */
  const __vue_scope_id__$28 = "data-v-3c3dd07c";
  /* module identifier */
  const __vue_module_identifier__$28 = undefined;
  /* functional template */
  const __vue_is_functional_template__$28 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$28 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$28, staticRenderFns: __vue_staticRenderFns__$28 },
    __vue_inject_styles__$28,
    __vue_script__$28,
    __vue_scope_id__$28,
    __vue_is_functional_template__$28,
    __vue_module_identifier__$28,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$27 = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$27 = script$27;
/* template */
var __vue_render__$27 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme1-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$27 = [];
__vue_render__$27._withStripped = true;

  /* style */
  const __vue_inject_styles__$27 = undefined;
  /* scoped */
  const __vue_scope_id__$27 = "data-v-5bb43fc2";
  /* module identifier */
  const __vue_module_identifier__$27 = undefined;
  /* functional template */
  const __vue_is_functional_template__$27 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$27 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$27, staticRenderFns: __vue_staticRenderFns__$27 },
    __vue_inject_styles__$27,
    __vue_script__$27,
    __vue_scope_id__$27,
    __vue_is_functional_template__$27,
    __vue_module_identifier__$27,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$26 = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$26 = script$26;
/* template */
var __vue_render__$26 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme2-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$26 = [];
__vue_render__$26._withStripped = true;

  /* style */
  const __vue_inject_styles__$26 = undefined;
  /* scoped */
  const __vue_scope_id__$26 = "data-v-e0f4b3ec";
  /* module identifier */
  const __vue_module_identifier__$26 = undefined;
  /* functional template */
  const __vue_is_functional_template__$26 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$26 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$26, staticRenderFns: __vue_staticRenderFns__$26 },
    __vue_inject_styles__$26,
    __vue_script__$26,
    __vue_scope_id__$26,
    __vue_is_functional_template__$26,
    __vue_module_identifier__$26,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$25 = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$25 = script$25;
/* template */
var __vue_render__$25 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme3-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
                _vm._v(" "),
                _c("div", { staticClass: "right-top-bg" }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$25 = [];
__vue_render__$25._withStripped = true;

  /* style */
  const __vue_inject_styles__$25 = undefined;
  /* scoped */
  const __vue_scope_id__$25 = "data-v-2428ae66";
  /* module identifier */
  const __vue_module_identifier__$25 = undefined;
  /* functional template */
  const __vue_is_functional_template__$25 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$25 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$25, staticRenderFns: __vue_staticRenderFns__$25 },
    __vue_inject_styles__$25,
    __vue_script__$25,
    __vue_scope_id__$25,
    __vue_is_functional_template__$25,
    __vue_module_identifier__$25,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$24 = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$24 = script$24;
/* template */
var __vue_render__$24 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme4-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "cont-box" }, [
            _c("div", { staticClass: "title-container" }, [
              _c("div", { staticClass: "right-top-bg" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$24 = [];
__vue_render__$24._withStripped = true;

  /* style */
  const __vue_inject_styles__$24 = undefined;
  /* scoped */
  const __vue_scope_id__$24 = "data-v-4964d0d9";
  /* module identifier */
  const __vue_module_identifier__$24 = undefined;
  /* functional template */
  const __vue_is_functional_template__$24 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$24 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$24, staticRenderFns: __vue_staticRenderFns__$24 },
    __vue_inject_styles__$24,
    __vue_script__$24,
    __vue_scope_id__$24,
    __vue_is_functional_template__$24,
    __vue_module_identifier__$24,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$23 = {
  mixins: [singleGroupMixin],
  name: 'titleGreentheme6LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$23 = script$23;
/* template */
var __vue_render__$23 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Greentheme6-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }, [
                _c("div", { staticClass: "line" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }, [
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$23 = [];
__vue_render__$23._withStripped = true;

  /* style */
  const __vue_inject_styles__$23 = undefined;
  /* scoped */
  const __vue_scope_id__$23 = "data-v-6fcbabaa";
  /* module identifier */
  const __vue_module_identifier__$23 = undefined;
  /* functional template */
  const __vue_is_functional_template__$23 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$23 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$23, staticRenderFns: __vue_staticRenderFns__$23 },
    __vue_inject_styles__$23,
    __vue_script__$23,
    __vue_scope_id__$23,
    __vue_is_functional_template__$23,
    __vue_module_identifier__$23,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$22 = {
  mixins: [singleGroupMixin],
  name: 'titleNineteenLibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$22 = script$22;
/* template */
var __vue_render__$22 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-nineteen-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container-box" }, [
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$22 = [];
__vue_render__$22._withStripped = true;

  /* style */
  const __vue_inject_styles__$22 = undefined;
  /* scoped */
  const __vue_scope_id__$22 = "data-v-f4944fb0";
  /* module identifier */
  const __vue_module_identifier__$22 = undefined;
  /* functional template */
  const __vue_is_functional_template__$22 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$22 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$22, staticRenderFns: __vue_staticRenderFns__$22 },
    __vue_inject_styles__$22,
    __vue_script__$22,
    __vue_scope_id__$22,
    __vue_is_functional_template__$22,
    __vue_module_identifier__$22,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$21 = {
  mixins: [singleGroupMixin],
  name: 'titleHexagonLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$21 = script$21;
/* template */
var __vue_render__$21 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-hexagon-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$21 = [];
__vue_render__$21._withStripped = true;

  /* style */
  const __vue_inject_styles__$21 = undefined;
  /* scoped */
  const __vue_scope_id__$21 = "data-v-0a2fb09a";
  /* module identifier */
  const __vue_module_identifier__$21 = undefined;
  /* functional template */
  const __vue_is_functional_template__$21 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$21 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$21, staticRenderFns: __vue_staticRenderFns__$21 },
    __vue_inject_styles__$21,
    __vue_script__$21,
    __vue_scope_id__$21,
    __vue_is_functional_template__$21,
    __vue_module_identifier__$21,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$20 = {
  mixins: [singleGroupMixin],
  name: 'titleFourteenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$20 = script$20;
/* template */
var __vue_render__$20 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fourteen-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "cont-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$20 = [];
__vue_render__$20._withStripped = true;

  /* style */
  const __vue_inject_styles__$20 = undefined;
  /* scoped */
  const __vue_scope_id__$20 = "data-v-abad1a9e";
  /* module identifier */
  const __vue_module_identifier__$20 = undefined;
  /* functional template */
  const __vue_is_functional_template__$20 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$20 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$20, staticRenderFns: __vue_staticRenderFns__$20 },
    __vue_inject_styles__$20,
    __vue_script__$20,
    __vue_scope_id__$20,
    __vue_is_functional_template__$20,
    __vue_module_identifier__$20,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1$ = {
  mixins: [singleGroupMixin],
  name: 'titleSeventeenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1$ = script$1$;
/* template */
var __vue_render__$1$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-seventeen-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-image" },
              [
                _c("xml-image-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                    "block-data-name": _vm.imageBlockDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "show-title": false,
                    "container-class": "image-container",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                  },
                  on: { blockclick: _vm.handleBlockClick },
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("div", { staticClass: "zhuanshi" }, [
                  _c("div", { staticClass: "line1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "line2" }),
                ]),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1$ = [];
__vue_render__$1$._withStripped = true;

  /* style */
  const __vue_inject_styles__$1$ = undefined;
  /* scoped */
  const __vue_scope_id__$1$ = "data-v-7e8d373a";
  /* module identifier */
  const __vue_module_identifier__$1$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$1$ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1$, staticRenderFns: __vue_staticRenderFns__$1$ },
    __vue_inject_styles__$1$,
    __vue_script__$1$,
    __vue_scope_id__$1$,
    __vue_is_functional_template__$1$,
    __vue_module_identifier__$1$,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1_ = {
  mixins: [singleGroupMixin],
  name: 'titleSixteenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1_ = script$1_;
/* template */
var __vue_render__$1_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-sixteen-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "left-image" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "right-icon" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1_ = [];
__vue_render__$1_._withStripped = true;

  /* style */
  const __vue_inject_styles__$1_ = undefined;
  /* scoped */
  const __vue_scope_id__$1_ = "data-v-dd1a399e";
  /* module identifier */
  const __vue_module_identifier__$1_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$1_ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1_, staticRenderFns: __vue_staticRenderFns__$1_ },
    __vue_inject_styles__$1_,
    __vue_script__$1_,
    __vue_scope_id__$1_,
    __vue_is_functional_template__$1_,
    __vue_module_identifier__$1_,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1Z = {
  mixins: [singleGroupMixin],
  name: 'titleKnowledgeLibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1Z = script$1Z;
/* template */
var __vue_render__$1Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Knowledge-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "left-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.numberTextDataName],
                    "block-data-name": _vm.numberTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "rigth-titlebox" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1Z = [];
__vue_render__$1Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Z = undefined;
  /* scoped */
  const __vue_scope_id__$1Z = "data-v-5e734877";
  /* module identifier */
  const __vue_module_identifier__$1Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Z, staticRenderFns: __vue_staticRenderFns__$1Z },
    __vue_inject_styles__$1Z,
    __vue_script__$1Z,
    __vue_scope_id__$1Z,
    __vue_is_functional_template__$1Z,
    __vue_module_identifier__$1Z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1Y = {
  mixins: [singleGroupMixin],
  name: 'titleFifthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1Y = script$1Y;
/* template */
var __vue_render__$1Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-fifth-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-bottom" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1Y = [];
__vue_render__$1Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Y = undefined;
  /* scoped */
  const __vue_scope_id__$1Y = "data-v-48665bd2";
  /* module identifier */
  const __vue_module_identifier__$1Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Y, staticRenderFns: __vue_staticRenderFns__$1Y },
    __vue_inject_styles__$1Y,
    __vue_script__$1Y,
    __vue_scope_id__$1Y,
    __vue_is_functional_template__$1Y,
    __vue_module_identifier__$1Y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1X = {
  mixins: [singleGroupMixin],
  name: 'titleTriangleLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1X = script$1X;
/* template */
var __vue_render__$1X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-triangle-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-left-img" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1X = [];
__vue_render__$1X._withStripped = true;

  /* style */
  const __vue_inject_styles__$1X = undefined;
  /* scoped */
  const __vue_scope_id__$1X = "data-v-7b621006";
  /* module identifier */
  const __vue_module_identifier__$1X = undefined;
  /* functional template */
  const __vue_is_functional_template__$1X = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1X, staticRenderFns: __vue_staticRenderFns__$1X },
    __vue_inject_styles__$1X,
    __vue_script__$1X,
    __vue_scope_id__$1X,
    __vue_is_functional_template__$1X,
    __vue_module_identifier__$1X,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1W = {
  mixins: [singleGroupMixin],
  name: 'titleRedtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1W = script$1W;
/* template */
var __vue_render__$1W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Redtheme1-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "title-bg" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1W = [];
__vue_render__$1W._withStripped = true;

  /* style */
  const __vue_inject_styles__$1W = undefined;
  /* scoped */
  const __vue_scope_id__$1W = "data-v-fd63433c";
  /* module identifier */
  const __vue_module_identifier__$1W = undefined;
  /* functional template */
  const __vue_is_functional_template__$1W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1W, staticRenderFns: __vue_staticRenderFns__$1W },
    __vue_inject_styles__$1W,
    __vue_script__$1W,
    __vue_scope_id__$1W,
    __vue_is_functional_template__$1W,
    __vue_module_identifier__$1W,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1V = {
  mixins: [singleGroupMixin],
  name: 'titleRedtheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1V = script$1V;
/* template */
var __vue_render__$1V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Redtheme2-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1V = [];
__vue_render__$1V._withStripped = true;

  /* style */
  const __vue_inject_styles__$1V = undefined;
  /* scoped */
  const __vue_scope_id__$1V = "data-v-7cb64bdc";
  /* module identifier */
  const __vue_module_identifier__$1V = undefined;
  /* functional template */
  const __vue_is_functional_template__$1V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1V, staticRenderFns: __vue_staticRenderFns__$1V },
    __vue_inject_styles__$1V,
    __vue_script__$1V,
    __vue_scope_id__$1V,
    __vue_is_functional_template__$1V,
    __vue_module_identifier__$1V,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1U = {
  mixins: [singleGroupMixin],
  name: 'titleRedtheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName1: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1U = script$1U;
/* template */
var __vue_render__$1U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Redtheme3-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "suptitle" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName1],
                  "block-data-name": _vm.titleTextDataName1,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                  "block-data-name": _vm.titleTextDataName2,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1U = [];
__vue_render__$1U._withStripped = true;

  /* style */
  const __vue_inject_styles__$1U = undefined;
  /* scoped */
  const __vue_scope_id__$1U = "data-v-5372169c";
  /* module identifier */
  const __vue_module_identifier__$1U = undefined;
  /* functional template */
  const __vue_is_functional_template__$1U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1U, staticRenderFns: __vue_staticRenderFns__$1U },
    __vue_inject_styles__$1U,
    __vue_script__$1U,
    __vue_scope_id__$1U,
    __vue_is_functional_template__$1U,
    __vue_module_identifier__$1U,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1T = {
  mixins: [singleGroupMixin],
  name: 'titleRedtheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1T = script$1T;
/* template */
var __vue_render__$1T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Redtheme4-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "left-icon" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "right-icon" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1T = [];
__vue_render__$1T._withStripped = true;

  /* style */
  const __vue_inject_styles__$1T = undefined;
  /* scoped */
  const __vue_scope_id__$1T = "data-v-2e411e2f";
  /* module identifier */
  const __vue_module_identifier__$1T = undefined;
  /* functional template */
  const __vue_is_functional_template__$1T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1T, staticRenderFns: __vue_staticRenderFns__$1T },
    __vue_inject_styles__$1T,
    __vue_script__$1T,
    __vue_scope_id__$1T,
    __vue_is_functional_template__$1T,
    __vue_module_identifier__$1T,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1S = {
  mixins: [singleGroupMixin],
  name: 'titleThirtyoneLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1S = script$1S;
/* template */
var __vue_render__$1S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtyone-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1S = [];
__vue_render__$1S._withStripped = true;

  /* style */
  const __vue_inject_styles__$1S = undefined;
  /* scoped */
  const __vue_scope_id__$1S = "data-v-dda94bf6";
  /* module identifier */
  const __vue_module_identifier__$1S = undefined;
  /* functional template */
  const __vue_is_functional_template__$1S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1S, staticRenderFns: __vue_staticRenderFns__$1S },
    __vue_inject_styles__$1S,
    __vue_script__$1S,
    __vue_scope_id__$1S,
    __vue_is_functional_template__$1S,
    __vue_module_identifier__$1S,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1R = {
  mixins: [singleGroupMixin],
  name: 'titleThirtytwoLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1R = script$1R;
/* template */
var __vue_render__$1R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtytwo-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1R = [];
__vue_render__$1R._withStripped = true;

  /* style */
  const __vue_inject_styles__$1R = undefined;
  /* scoped */
  const __vue_scope_id__$1R = "data-v-6536eac2";
  /* module identifier */
  const __vue_module_identifier__$1R = undefined;
  /* functional template */
  const __vue_is_functional_template__$1R = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1R, staticRenderFns: __vue_staticRenderFns__$1R },
    __vue_inject_styles__$1R,
    __vue_script__$1R,
    __vue_scope_id__$1R,
    __vue_is_functional_template__$1R,
    __vue_module_identifier__$1R,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1Q = {
  mixins: [singleGroupMixin],
  name: 'titleThirtythreeLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1Q = script$1Q;
/* template */
var __vue_render__$1Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtythree-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "main-con" }, [
            _c("div", { staticClass: "left-block" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1Q = [];
__vue_render__$1Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Q = undefined;
  /* scoped */
  const __vue_scope_id__$1Q = "data-v-16fa0d25";
  /* module identifier */
  const __vue_module_identifier__$1Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Q, staticRenderFns: __vue_staticRenderFns__$1Q },
    __vue_inject_styles__$1Q,
    __vue_script__$1Q,
    __vue_scope_id__$1Q,
    __vue_is_functional_template__$1Q,
    __vue_module_identifier__$1Q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1P = {
  mixins: [singleGroupMixin],
  name: 'titleThirtyfourLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1P = script$1P;
/* template */
var __vue_render__$1P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtyfour-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1P = [];
__vue_render__$1P._withStripped = true;

  /* style */
  const __vue_inject_styles__$1P = undefined;
  /* scoped */
  const __vue_scope_id__$1P = "data-v-6a0923f9";
  /* module identifier */
  const __vue_module_identifier__$1P = undefined;
  /* functional template */
  const __vue_is_functional_template__$1P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1P, staticRenderFns: __vue_staticRenderFns__$1P },
    __vue_inject_styles__$1P,
    __vue_script__$1P,
    __vue_scope_id__$1P,
    __vue_is_functional_template__$1P,
    __vue_module_identifier__$1P,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1O = {
  mixins: [singleGroupMixin],
  name: 'titleThirtyfiveLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1O = script$1O;
/* template */
var __vue_render__$1O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtyfive-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c(
              "div",
              { staticClass: "suptitle-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", [
              _c(
                "div",
                { staticClass: "subtitle-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                      "block-data-name": _vm.titleTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1O = [];
__vue_render__$1O._withStripped = true;

  /* style */
  const __vue_inject_styles__$1O = undefined;
  /* scoped */
  const __vue_scope_id__$1O = "data-v-e71cd272";
  /* module identifier */
  const __vue_module_identifier__$1O = undefined;
  /* functional template */
  const __vue_is_functional_template__$1O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1O, staticRenderFns: __vue_staticRenderFns__$1O },
    __vue_inject_styles__$1O,
    __vue_script__$1O,
    __vue_scope_id__$1O,
    __vue_is_functional_template__$1O,
    __vue_module_identifier__$1O,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1N = {
  mixins: [singleGroupMixin],
  name: 'titleThirtysixLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1N = script$1N;
/* template */
var __vue_render__$1N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtysix-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "left-line" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "right-line" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1N = [];
__vue_render__$1N._withStripped = true;

  /* style */
  const __vue_inject_styles__$1N = undefined;
  /* scoped */
  const __vue_scope_id__$1N = "data-v-5c76cc74";
  /* module identifier */
  const __vue_module_identifier__$1N = undefined;
  /* functional template */
  const __vue_is_functional_template__$1N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1N, staticRenderFns: __vue_staticRenderFns__$1N },
    __vue_inject_styles__$1N,
    __vue_script__$1N,
    __vue_scope_id__$1N,
    __vue_is_functional_template__$1N,
    __vue_module_identifier__$1N,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1M = {
  mixins: [singleGroupMixin],
  name: 'titleThirtysevenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1M = script$1M;
/* template */
var __vue_render__$1M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtyseven-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("div", { staticClass: "left-line" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "right-line" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1M = [];
__vue_render__$1M._withStripped = true;

  /* style */
  const __vue_inject_styles__$1M = undefined;
  /* scoped */
  const __vue_scope_id__$1M = "data-v-54541772";
  /* module identifier */
  const __vue_module_identifier__$1M = undefined;
  /* functional template */
  const __vue_is_functional_template__$1M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1M, staticRenderFns: __vue_staticRenderFns__$1M },
    __vue_inject_styles__$1M,
    __vue_script__$1M,
    __vue_scope_id__$1M,
    __vue_is_functional_template__$1M,
    __vue_module_identifier__$1M,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1L = {
  mixins: [singleGroupMixin],
  name: 'titleThirtyeightLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1L = script$1L;
/* template */
var __vue_render__$1L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtyeight-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "title-container" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1L = [];
__vue_render__$1L._withStripped = true;

  /* style */
  const __vue_inject_styles__$1L = undefined;
  /* scoped */
  const __vue_scope_id__$1L = "data-v-ee6a3c48";
  /* module identifier */
  const __vue_module_identifier__$1L = undefined;
  /* functional template */
  const __vue_is_functional_template__$1L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1L, staticRenderFns: __vue_staticRenderFns__$1L },
    __vue_inject_styles__$1L,
    __vue_script__$1L,
    __vue_scope_id__$1L,
    __vue_is_functional_template__$1L,
    __vue_module_identifier__$1L,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1K = {
  mixins: [singleGroupMixin],
  name: 'titleThirtynineLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1K = script$1K;
/* template */
var __vue_render__$1K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-thirtynine-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "mask" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-number" },
              [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.numberTextDataName],
                    "block-data-name": _vm.numberTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    "rule-index": _vm.ruleIndex,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "title-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1K = [];
__vue_render__$1K._withStripped = true;

  /* style */
  const __vue_inject_styles__$1K = undefined;
  /* scoped */
  const __vue_scope_id__$1K = "data-v-e4f9bb02";
  /* module identifier */
  const __vue_module_identifier__$1K = undefined;
  /* functional template */
  const __vue_is_functional_template__$1K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1K, staticRenderFns: __vue_staticRenderFns__$1K },
    __vue_inject_styles__$1K,
    __vue_script__$1K,
    __vue_scope_id__$1K,
    __vue_is_functional_template__$1K,
    __vue_module_identifier__$1K,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1J = {
  mixins: [singleGroupMixin],
  name: 'titleBuleRectangleLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_ifting_achines_left_png__ = "data:image/png;base64,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";

var __$_require_style_title_bucket_right_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1J = script$1J;
var __vue_render__$1J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-BuleRectangle-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_ifting_achines_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_bucket_right_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "trapezoid-border" }, [
                _c("div", { staticClass: "rightTopround" }),
                _vm._v(" "),
                _c("div", { staticClass: "leftTopround" }),
                _vm._v(" "),
                _c("div", { staticClass: "trapezoid" }, [
                  _c(
                    "div",
                    { staticClass: "rigth-titlebox" },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "rigth-titlebox-usb" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                        "block-data-name": _vm.titleTextDataName2,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "right-dot" }, [
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-rapezoid" }, [
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                ]),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1J = [];
__vue_render__$1J._withStripped = true;

  /* style */
  const __vue_inject_styles__$1J = undefined;
  /* scoped */
  const __vue_scope_id__$1J = "data-v-49223b7c";
  /* module identifier */
  const __vue_module_identifier__$1J = undefined;
  /* functional template */
  const __vue_is_functional_template__$1J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1J, staticRenderFns: __vue_staticRenderFns__$1J },
    __vue_inject_styles__$1J,
    __vue_script__$1J,
    __vue_scope_id__$1J,
    __vue_is_functional_template__$1J,
    __vue_module_identifier__$1J,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1I = {
  mixins: [singleGroupMixin],
  name: 'titleBuleRectangleTwoLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1I = script$1I;
var __vue_render__$1I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-BuleRectangleTwo-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_ifting_achines_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "trapezoid-border" }, [
                _c("div", { staticClass: "right-image" }, [
                  _c("img", {
                    attrs: {
                      src: __$_require_style_title_bucket_right_png__,
                      alt: "",
                    },
                  }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "rightTopround" }),
                _vm._v(" "),
                _c("div", { staticClass: "leftTopround" }),
                _vm._v(" "),
                _c("div", { staticClass: "rapezoid" }, [
                  _c(
                    "div",
                    { staticClass: "rigth-titlebox" },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-dot" }, [
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-rapezoid" }, [
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "dot" }),
                ]),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1I = [];
__vue_render__$1I._withStripped = true;

  /* style */
  const __vue_inject_styles__$1I = undefined;
  /* scoped */
  const __vue_scope_id__$1I = "data-v-31126d09";
  /* module identifier */
  const __vue_module_identifier__$1I = undefined;
  /* functional template */
  const __vue_is_functional_template__$1I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1I, staticRenderFns: __vue_staticRenderFns__$1I },
    __vue_inject_styles__$1I,
    __vue_script__$1I,
    __vue_scope_id__$1I,
    __vue_is_functional_template__$1I,
    __vue_module_identifier__$1I,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1H = {
  mixins: [singleGroupMixin],
  name: 'titleLoggingLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_text_left_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1H = script$1H;
var __vue_render__$1H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleLogging-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_text_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }, [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1H = [];
__vue_render__$1H._withStripped = true;

  /* style */
  const __vue_inject_styles__$1H = undefined;
  /* scoped */
  const __vue_scope_id__$1H = "data-v-109de780";
  /* module identifier */
  const __vue_module_identifier__$1H = undefined;
  /* functional template */
  const __vue_is_functional_template__$1H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1H, staticRenderFns: __vue_staticRenderFns__$1H },
    __vue_inject_styles__$1H,
    __vue_script__$1H,
    __vue_scope_id__$1H,
    __vue_is_functional_template__$1H,
    __vue_module_identifier__$1H,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1G = {
  mixins: [singleGroupMixin],
  name: 'titleQuestionLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_question_left_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1G = script$1G;
var __vue_render__$1G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleQuestion-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_question_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1G = [];
__vue_render__$1G._withStripped = true;

  /* style */
  const __vue_inject_styles__$1G = undefined;
  /* scoped */
  const __vue_scope_id__$1G = "data-v-2da71084";
  /* module identifier */
  const __vue_module_identifier__$1G = undefined;
  /* functional template */
  const __vue_is_functional_template__$1G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1G, staticRenderFns: __vue_staticRenderFns__$1G },
    __vue_inject_styles__$1G,
    __vue_script__$1G,
    __vue_scope_id__$1G,
    __vue_is_functional_template__$1G,
    __vue_module_identifier__$1G,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1F = {
  mixins: [singleGroupMixin],
  name: 'titleCraneLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_crane_eft_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABWCAYAAABPaoF5AAAACXBIWXMAAAsTAAALEwEAmpwYAAAGiGlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgOS4wLWMwMDAgNzkuMTcxYzI3ZiwgMjAyMi8wOC8xNi0xODowMjo0MyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczpkYz0iaHR0cDovL3B1cmwub3JnL2RjL2VsZW1lbnRzLzEuMS8iIHhtbG5zOnBob3Rvc2hvcD0iaHR0cDovL25zLmFkb2JlLmNvbS9waG90b3Nob3AvMS4wLyIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0RXZ0PSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VFdmVudCMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDI0LjAgKFdpbmRvd3MpIiB4bXA6Q3JlYXRlRGF0ZT0iMjAyNC0wNy0wNVQxNzozNjozNSswODowMCIgeG1wOk1vZGlmeURhdGU9IjIwMjQtMDgtMDNUMTM6NTc6MDkrMDg6MDAiIHhtcDpNZXRhZGF0YURhdGU9IjIwMjQtMDgtMDNUMTM6NTc6MDkrMDg6MDAiIGRjOmZvcm1hdD0iaW1hZ2UvcG5nIiBwaG90b3Nob3A6Q29sb3JNb2RlPSIzIiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOmVlNjkxZmExLTIxOTYtODk0NC04ZTE4LTcyZDMyYWFjNjU1MiIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDozNDUxZTBkZS1hOWZmLTFjNGUtODcwOC1mNmYzMDdlODA0ZTEiIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDozNDUxZTBkZS1hOWZmLTFjNGUtODcwOC1mNmYzMDdlODA0ZTEiPiA8eG1wTU06SGlzdG9yeT4gPHJkZjpTZXE+IDxyZGY6bGkgc3RFdnQ6YWN0aW9uPSJjcmVhdGVkIiBzdEV2dDppbnN0YW5jZUlEPSJ4bXAuaWlkOjM0NTFlMGRlLWE5ZmYtMWM0ZS04NzA4LWY2ZjMwN2U4MDRlMSIgc3RFdnQ6d2hlbj0iMjAyNC0wNy0wNVQxNzozNjozNSswODowMCIgc3RFdnQ6c29mdHdhcmVBZ2VudD0iQWRvYmUgUGhvdG9zaG9wIDI0LjAgKFdpbmRvd3MpIi8+IDxyZGY6bGkgc3RFdnQ6YWN0aW9uPSJzYXZlZCIgc3RFdnQ6aW5zdGFuY2VJRD0ieG1wLmlpZDo2OTUxZDA2MC0zOTU1LTY3NGYtYmE5NS1kNjE2NjZmNDdjNzgiIHN0RXZ0OndoZW49IjIwMjQtMDgtMDNUMTA6MTQ6MjIrMDg6MDAiIHN0RXZ0OnNvZnR3YXJlQWdlbnQ9IkFkb2JlIFBob3Rvc2hvcCAyNC4wIChXaW5kb3dzKSIgc3RFdnQ6Y2hhbmdlZD0iLyIvPiA8cmRmOmxpIHN0RXZ0OmFjdGlvbj0ic2F2ZWQiIHN0RXZ0Omluc3RhbmNlSUQ9InhtcC5paWQ6ZWU2OTFmYTEtMjE5Ni04OTQ0LThlMTgtNzJkMzJhYWM2NTUyIiBzdEV2dDp3aGVuPSIyMDI0LTA4LTAzVDEzOjU3OjA5KzA4OjAwIiBzdEV2dDpzb2Z0d2FyZUFnZW50PSJBZG9iZSBQaG90b3Nob3AgMjQuMCAoV2luZG93cykiIHN0RXZ0OmNoYW5nZWQ9Ii8iLz4gPC9yZGY6U2VxPiA8L3htcE1NOkhpc3Rvcnk+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+Otyz8wAAJuRJREFUeJzdnXm8ZUV177+rqvY+0526+/YI3c3QzCggICjgGEGUPMUBwTlPISDO0cT3fMYYfcZHYp5CNCKaF6Pic4wSlSh5IIKIyuSAQDP0SA+3u+98zzn77F1rvT/2uU1Ptycalfw+n/r0p+85u3bVb9detWqtX9WRwTd+H0wQxw4QnIt01ADPTh/vLQyC4x8I7oL2homnWdS1iOxnXYZUPMns2jbVCxgg9th3pus3w8SBKgKYK/8uBkh5tZmAPFaHiGBaIOIwc4CWf7NunWJlXQriDLOyj+IFKxxmkTRRJKnSmWhRtJRKf52wu35JWQcdSdgfagxwYoNB8hdK1LmVOY1nAdfuR1VlfdGwxEDAdBtCnwTYLdHTcJrj8PtcuQHO2ckicriYQGqndcyu3R96BMFXHFbErYP3yYS9INpwKIkTlH3roQEJnL2V2JwzY4yJQL6v9Xjv8N51X/F9asYfBPZMtAmIo3CRgojsSy+NnqqFF1lJTuGRo1Lc00T42d5XUj5gcbKPj/kPC3tlOsAI4imKNqrFXtlGw6i66oVCOMpQTDUgEpLEn2+2D0Qb4BR7Eo7ibbFXRJeTmuBxdNTj3J59EMMIpC8zizfm1vnvLlQONSte4tU9o+vj6N7cuxBQ61qLJzHZezmiwcxIfRXMkeWKuN33Wg3Myybn9fWqdpTE4ote/CYx/UukqII0Z7y4+8ZEwMQ9KSe/HbH3RAOCUU0iRiRG3e0IEzHUkmHFfaTqKm+LFg/X6H/iHU3E/O6Hp1CIofCfgmRg79ch0z61GlSSQE/wDKijX/0uy0AMJCILcXZXnvN0CIeKFD9UJ5OW9k1Y0mCXRRIi5SrgSWwpdsJej+htYQbRO1wUvDDj4IximcY4ZSQvdSE/wifh/s5kJxSjDzjxyc42WhzSM/iktsUzYb+IFkqyOxXFA06FXS2rTSXF+R/40Pn32AwvKSrVxSLNT8TYnivoxu3q1Iir9JS24j+JudgW+0X0NETBRNACpCi2xkvMwFSRSigwj/giI3GbYqu5IFTTnsq8p6Riim1l1KHWAYslybZ7+/9kxOMiehoWDDNFOwJi+CBI6r05OqrxVF/4P3Lob1zdvoLZep9NhiiCc+WyXlFMDDHb5ZvxnwGPn2gDcRDTlDwXnAOxJvhGDN5VKNpzY0g+pmY/8spnwFtmxXBUI/ikjLAB7j+jvdgGj5/oaXckGqIgTnAhPRstXiOE05DQ47y9AKePaMfu98KLvPOfxfRbGF+dXtI/SUMYe41ponuBuZTBnumhJYAzWG/Q2V0lBogHSQVTnhYq1W9YzHstFlPO+0MptLQRIm3FpnyQC5y5l6NxKAo3meyDn/kkRXCOHuDrIpwFbHrsI8Pwg6nTT5rx/m58G9jFyCtzAyQ1Qzt6UMxjXURGca4DNoVsXW4HQcyUMTHrN8egipWL8RmG84z33M13tjVCAmXU0aB8nNubqOkXcptcQDmbz7BSms4xmAm7CnPZDBYwGG6eEztFhLoZS3f8gscuJvGLCvEjkus6RHKgN3g5XNUMI4JUTdkswm/T4F5kSodyBT09UKe97en+mCGZGq9z8HRBNpoxR2AxRgsRZ4aJyP2CzHJOF6nJbwQWicg8jLYgXiA340ETFmGMiYiKc8tMYwcQxCVmdr/zaU3MDjbNf4lzhwg2GyTDJDWRtsByhy02GBGPSVJdRqeTd5tbFViFMIFxpKjc65I46FxxsGaVtjjzCAG15eKY44QVBlfu+Ib6xkmvbYjwJwJVyhHdAprAJOAJyaMxi1+3ybHxtFr/oHecj1qRt+Va7wMmdPDu5yGn4sX+HliHiKM0R3Ebktnm34AjQe0GMbEgfNApd5jzN5u4OuI2OvyvgtrlznEM0W8OuEss6o24cK84ScCv1ch9CfylwxY75ExRc5h92/lKDYsTqN7u1M73PpzjXWh508tM+TfzYQX4ICIrvMaHnfHfxLlDg8lzdao9qZlc7xp9QTSbdD7cinJ0EN4txqNB7O2xnd5uRe1Wl8aqSBgW7GaJ7qVOuSgk7iiReCqSXBc7hWlhhGqy3WTodiDFEGo4f4tlY18gc1+Oi+wqRuQ6cf7qXFu9RavysVrN4yymJO66ZlPfKq793Wqleo8ZCbu27QakGImI+56a3lBgK7xzF/oifixPkmtdzAnev6PApmKhF2keVmhoXVEJ/lkai/MlhEkRxTv5aKuIDwrF85yvPhex/x3z1uogcq1U+ojmnltpTyzON9z/Zj//6OsJtauk0zrBYvFqE29lh+WKQrhTTF7qJD5XpPIJHRn6mOv3X8I8proAeLdG3mLC52Ol/Q5rNl6VrznqL6vH392yokC8Hu8qyWKdKo7XPDzgSG6Vit0N8XyUh9nDHOQxmnSypyez/O1+9pyH4pR80NA7zeTiRk/4SAj652IRUbutrdlq4FNIOMqghz2GQU0QSQUB5VPq5Do8NwWf9ia4t8QiviOqXSrGiiTJUYo/LzpyX8DdIFbg1P5GiRcVqq8WXGFqN6jI+0Ja+TZFfgIWTgnZyLew4s+o9l9vWkCRvc0FvEOv86Y4xycj4QyU16AFqnKTq/ovVBbM+barhCVxkj4X7ScC16na5zEF5ZOht/Xr+vHLfyLRE5LieIf7vhbxr0jjA2aKwplWxJ+64G9zafJUU9uje5cjclKRNd4ZZOIzPtrRUdLgLa4pOu6Pk0SudMIHOh35P4n5P0srMjeTZJGZFbJ7oq3MnMg8RAZEWeQ7+g+Fc+o1H4/OPRLz4kWpC49G0WPNoN/XowZ7bx7lQ96cqdqvTeX0vuA7ETlOUIj8e+HdG0Na/ZlkY5KPbThfBo/8YUgbx2lnCjGbFJPXqCT/HBIsWrzBE//Ym5vdETvU1ArpXfSJ2Byb77LW90PdNiJ8zkz/RrwMEh2SVzrRt/5UtPVpT+PnVvi6tpOrXLX5XYvJUwyioR0drf8prrKy2pffVXTsFYHHzEXslmlEoG4Wl4v4w9Qq3zSJPYgUUQjA/QbDERt3QVKH/1qOVYJIrxoZj9nnHSGU84BhxcWInGfOzVGzpoit1pg9ZCLLXfDvjMaRVub3rYMl4mwU4kbFrTbP/UHkI4VwpBk5iBdBHdyr6K8hNxlYfI6h79GiJTgXFUnFZL2IjSvyCCYjKlyljiUgGYLTzqYJI3vImnm/qyRHaCFDDrkVoYEnRyTxlgxr7kfMxxPM/EPm80ONcJ2KJKWH5TzVbBLjV2q6TrxcInP/6/XLxOQX4hjYwfUpRQ8+/KIjnTea8wUa0+klskACDBmMChyMkwbR1kumZychXE1pn9u7INuAhhOpZ4W+OU/j1713i1GClNeswazHRBZ2H5Zuc11COVGvFZgNzNvGxy+dTEEEW2UmgvglxFxxot21vQGpCBNmrAMGQeaAddjq3UVQebha+P8mIh8wEesmTsu7qJQ1iZFZvMCqfB2zY4k+Qawov4shFhArBD+kWV4EnK0Tif8u6s4w9Vu6/qNhKuIrA5qNfxNr/5aeWbuzAw+UA08wkWHA89hT25UrbGZGwG8usHGDe7d7GiKTwIYZb1iiabB21x9N16YjXVZ2anAXq7tl6wcijgQHDuv6xFNmRBFNRMCC5aivOqOiYsSoBPG/fcwbn66qK8pxZXsCXpp4/a+0XQ0l3zo9qiFpJWhrbAJy6J29215Lx5AiIkIq4oOp9CE2DFQEGla6ixNABZOaieEqRV9NSjtj+5Zff0JgAqEjuEIwb1chdgvGn4P9kyA/Fq9atP0LvcirzfFhhDs0Gvg9BxBCmTaRFtDa+buKuMBuFR1ShktlsgPRMO9/Yr3JT8XlzzCjjkiqamsEogiHGNZGcIY8hNhPxSBVoYP8QeStRAVTw5xs1sTf4VS3pBYfNefXqTqixg1JxW8Wc3eb0TJnexWn2eugkgCtPBINRB4LBalBLXEklIt2omwwCy/GZ0/Xtn8Xrvhn58MwZp8x05VGGBJXfFzU/Uhb6RCAmOCTgpgo8nvUFTh14DyagHlHqajTHjNLTLVUtaSaGr4hzqiYIyUBE3J2T/YeiXZOyHJlfLjFojkpvT1ViiJ219OCdzA6mdOhwHk3rYsbUfU/EGcvF+wUMZI28j+CyC+98fdm/m6BoW1H8Ez8OhHGJzOKTqTaSKlXA6oHduQbhuDwmpRiRbovl3bnq7JTqAIiZr58wVMp6SuNuZKLzmgA90C0MNXOSZ3xvKct4sKnzeeYQwaZnMzK0SuOwUbgkn+8g3u2ZMwfqHWDKhkCIibHiyTP0Nbkm1yoX0twR4Au8Go9htumSYZXQU22akgmpjKKLEJUnn7cfA5f2MfPHtjEI6tGoFI2W5zQ15OW0of94l4wDGcOr+k2pO0Iw3CIAzPtttttY1CFtKtN7IgitvPo3iXRIkJhxsRUjuF4+8uexquf91TWbh5nol3gpRu3EiEvjJedfggPrb+HvNnCT+s9zOZbUlktqfueVHovrwn1doxfjMhmH3wNM/w27IgJ3jwtIsNTGa84ZSlPPaSPjaNtzjtlEcfM7+c/7h3irpWbaaQJ4pRWpnzxtlWMN3O8E+pp2McEzTTJpeHb+VPp6nZMtRuhMCsUQImy40MJURARohhRtid7J6KdCENjGWkwFs71vPGlz+DcUxbx4IZxnEVIA2XQrnRn2p2C552wgG/+pIeHN0xQTbcG7BpedLbk2WcijY/nYeobQXreZjGOxZhv2YUgm4mJgmMO6uPi1zyVpbP6OGywRjtXNrfa3LVyhGXzejjjiAGyQoiWEzvGWYcv4nv3ruG6u9YyMppRaQR6qoEYdz/ETUDM4TRBZ3w4JqY2FX0wjAucSfRWb5oWU+Z0p6RQqV4TnAgajG3j7FuJFidkHWVkpMVTj5zFc54yl5ectgA1YcNIe8agSKHGrNRxwZmH8v4v/oaenhqxtG1VM2uaOZEkniBFbQDNquAKxMUdA7ci0M5zslbkaYfMYv1Ih5Wbp0oz6Y1KcExmBflwTjRHoTkuCvN667zp2Ydx/qkH8807VnLTL4dYu36CvjkN3AxKYxPwCr4AKLCZvZ3NgrVM5V8UfuAwc47zovCvqB/blbmatu8+GrlTzJezWWhmEXEF2WRk2fx+LjnnEM46bjbzB6qsH2kDhkkoTcIM7ZloR05a2sfpRw3wi4dGmNObYsq4VPwCQnKTdoq1IZn14db4A7f6Wu930tqsJZpNPrTtqI7R6K+nvOLZB7FxrE0RBecdOlMkvWwZY60OiRr9jYSPXnAU3z9oHr8eH+PLP17F6FSOx2hUk8dMioBEIXS6XpLsVqiTOs8ywb5TRHu/edCgEeUIq7U9sl3IYjuyPYBT0JTCHGHJYJ1W3uais5dw+rK5LJlXY8NoxqNbWrgd9XXlIssDCyjfik1Au5kVHDy7ytknzefnq8aJzuGCFMRillM9Uq36rXZrzQ/S2qwB8aEeO5lCst2Dy/LIcYfM4pxT5/PocN69zd6gHDFFVJZvaLJkdg9nP2sBF56xkC/fvJav3bqWLcNt0qqnpzeFwkiK0jvaC3veMJNEvOWJi781BIvyDRNbSCjmIAzNdOFjZGd4lxD+9jXHYwaNqscwVm9qIcLOJBtg8kLU3gH21O7Gj0cM+ZpzcvXqLc3snKfM46Z7N3Pb8i3M7kkPAn93oe5VPmQfMic/MVf9E8Gti1Mbe1Hd2lPvhfFHJ3jDm59PTy2liB2Sfd9ggHeO8XbOhpEW8weqvP3Fy7joWYv5wo9Wc9PdG3lkqMmCag3TrRGJPWFKXKzGnJpKcjlRVchf6NIk6mRjfE8LrNJhMBIRXPkfmGwXTLXjrp+yGTh5lwv+ehf1hSIsEpGFAmeo45Pm+DpIH0ARY6nTIIrzbkGauk3AS2jWrheNvzYpTiZprLdKHUvrUKmzcdJx5pmHcerRg4xN7TYPvEdM8zc6lTPeymmkgb99/fF87u0ns3hWlbF2BxJDwl6VDGc3m3MXSbS6FxuQJLzOkNshaZcxrj0USxESgtmeZMqGF3umE3eFblUQPTYcxAy0+ONQDf89l8r73nz2Maz68j0024V3odMmlWGTnoXimsdF7fzIqczzSX3OtEopquGSwEvPOpKl83u5874t2967H3g1yvPFMdvBlBp3Y9wMdgsmnRnf/u4HhSr3rZlg2cIePv2nJ3LBe2+i1fQkfi/y7qq4auVR1xvWQnEuIsJU57Z8/pxHY62CxL2SeAMQwh5uaBhe5OUCYdfJaikbFPyrXWpXLplfW1erBFq5OYvZxrypF4Z68qHQ0C9EV3zAKd+QIjnEul7q0HCbU4+azcueMZ8VGybxzqEWEeEFpvZJw44RkzJ/bUo0Oc85+4DD/Vw8f1Xkdv0eO+mFTeMdDlnUy7MOG+AHN62i0Uj3SI5mkcqCnqnanJ5N7anO5eryZn3OQbd0Vt7UzEeW45LGHuvY2gaR3d9QoGZiZ2Ezz85qgua6OCadp06183Wx6EDR2ezrPedh9vrOaOs5vpLeHHpSIlIr87HlaO5pJFz03MW0OrH7zIxakIv6euTabMq2Ct6nF0hlCt1w4p+Ot++Hin3F4FJgfHf9yHNlbn/KJZefzA9WtUjm1agkfrdeTdl/W0k7nhRq9VOs06qZdQ53UR5OtYFodbfXbou9MB04Mamps93afidEJ9JxIpgTzMssU9voUnnQavWLLHMrtJmvFheG8UUNoFMoxx7axwtOnU87K+eHat09Wws+pzm7dQum4x1JqhfFQg4z5MXAlpm+X749GUcvavCONx/PNdevxCfusZXsDJ2ydnFnkcUv+Rr/14W0GtsTN1jfkrvcwGL2RVsV9uRFGUw5sduksOMjpbZux9layo6syHPuKmLXbxJZQuF/nVv78iSRj4iE7+PsTTgdk6hRgKyVc9nZh1GXwERREFUPL9pcE1Xqmu/e/Zr+LEbB4DSB7wIvBTbOdE2uhutE3veSw7jluuXcdfsGeubXZxS9AGhHN6aL6l+qVXtfbt79hfh4dTEJLtk3NV1wfvdPRQSK3D7r4A2Jp1LE7QkwNRo9jvEp+dL6R2S0ox7M45xPcPks71xTcy61il7sxf3UMm0a7tUmUIhguSPJAkUU3+zEzxWT7gi/Z/O5K5wO9hUzeSEzSNicQB6N4bGM115wNA9qABGS3XBgBknF3uNa4fbcx6uj87j+xh738OyIMDq659k363Dngvn2ltTHz3emHJ7HUnAijijFl3INH1o4p841P/otm8bb9NUq1UImVloMMa00PqrSeYZm7j2SxHPo6MFDmzNef/ahnHbUbDa1MvKWvj/mPCdUZpZVzQQR0Ki08vDcSq//n87Ze0Nw6Axda+WRN597OHGgznv/z4MsGKxOhw12RhLQDSMfjzb1aZL0fTH6j7l62OdoYYi7XETugAga/D8V2COuiJdbxR/nzPpioesJ6ZdE9FONNPCrh4e46/7lSLuNmtvoBw59gfnm7ZZP3qXou0iSewh2uNcwSEexYBRqrB3Jnl1L9QOY22eStzYxAuao1PQ9rYzrW5PxRt3N9LO8Ocax9cCChmPTcIs5jWSXE6OYoVm8PY+dCxxJcAB7CFjtCmFvworTm/9N7UcW44+cT/soin7FhgzLHDDQqHDLfetZu6nF/ME+1DR12eQW6xQVdTZE4B5V8K3QGG5nHHlMP+eftZSNw80jPHy+iATvZYZ48N61UZxhhTAxoldMDjefR9TxmQz9OjVm1zz/8Kojee/3VrFiwyRz+9JdPuhusPSh/WpYFy4WsLtSFEJUeawBpckYB9YAmQB9jcBdj2zm1t+MM9i/GOIAPg4uzNzUjQWtZ4ak50zn6z/zhcdV2/dmDzUGT+tfxunH9qWT7eLq1Mvh4vaf5GlYmYsgSdzJfYONN/XPa9A/t77LMnteA+2tsmxxH5+48FgOHayzebyzey/kccA1+mB3pa8/UqsWJIlhznXX7N2OTVdiKVuGxxkeG6JaayJhEmQ0Sn32Uj84d4KYP5tQuT+k6VebTY6be2z64Dv/ZCmr1mSfSyruubptZY8TZoaI4VN3sQ9Jn08CuyqSBNJKYEuzw5LZVa563XEcPLvG0HiGewK2d7iB2cbMBebOjfT35dAuyHKY6n0sJ554T0AZ2pxz4pI6Z504n3WbCqzjsEbwYailuqIp6tDmZOcNG8amivF12RtOPTqZUJp/NTGSvS54f8D3eZeblTgG5BWlvmL7IiYUUNpkg43jGQv7K3z2jU9h6WCNofH2Ad9K42IsJ5JdF6OIguHIW0ZRGJ2qMJ1gd67M+zU7OQtn1Tj76HnohNJpG176G3TWY8Uaa0cZnFiz8drD+tNbjjh67jsX9dc+6518ME4b/ycA5akxxbkxy4h5Z5uS0SmK7SQCXoRNEx0W9Fe48jXHc9jcxgE3I3u/c9aX+TC3rRvUtanBOdZsbvP8kwa47FWLGRkdY2xsdKC+cOGvilkL546uGvna0046eP33/vp5j1x+7rLXrR/tJK123DkUewAhBiLu1Bj8Qbl3FNuUuIvbeleSvXh2lateexyL55Rm5ECdcrOvW0c80McOucYyXQ8jkxmvfc5SPvynT6eesGrliubL2x39xcfeflrthx99Tt9gX/Ld9cPNk73b7W6KAwIzQ5wsVTimKJQiGnmhgCOEZJcTrwgMjWUs6JqRQ+bUGRrLDoiGam/XkQcZvDuNelal6eugY4qsc7AabJ2IDDnvh4zQbuZW/7OXL6s/PDx53o9/s/nkeQOVuGG8PWvNhubpRy+u01tPyKI+bg9jb1BEpZb6I7zz/6FWbrHrr6dsnNTu27gzga47suf3Vfjka4/j3V+/n4d+Ocbcnsd3MEvY3UQkAk54lqj7F0GWipSBJeu+TmqCoHgRQqNe6jIEVmzKwITXPmcpF5651F/+2Tv6nvmDh7j6slNZPKeBk80zr8QOEETAopA45qcJFAoDVU/w0hXCzHzttmbkytcdz2VrRli1cZg5jdp+i3dCZYbrRKDd4qhOIt8QYe5WgYFs+51yVBhgsdxy7LwjqjHeytk42ibxwj+97TR++sBmrvjX+1m+dpwLzzqERiVhpMj2q9F7AzMI3mh3XG+r4ylUmVOTvfYmps3I/L4Kn3nHabz1cz9j7WhGvbJ/gZhQtHadM/cBiPr+Zou5Idlb76DM3k4rdcyEsamcg+bWmdWTkjhhoJEAStw6rJ7Ake082iks62TM70/oqwYmSgXzLJTjBVY6Yc1MJ0W5bsJgcF6Dqy5/JlvGsulF+D4jbBzaOdihCmmFI3tnyXne76MLVnJNu1Dm9lc46dA67/nn+/jk9x7gmrc+nUbq+fz/e4TJdoGTvTzvZz9Q6q+FzBXjUzZF7vrJqTLZyp7tTa/xIkeYMZrn/C8kfmymuLwDhrc0aaSe/sHavke8uggx3zkgrREssWd7Z7P2Jua0LaYzJ7WkPDbpLdf8mu/8dC3fet9Z/JeT5/Dx764iyxU1CE62eiwHGs4JhUZE0w1ze+o0enrY3GHZhtGpa6uORXhBzQa04G+cdkaJxWd2Z1daj9PKhd7+nYk2E5JUjzct9rlCNQjeMaunwv/8xzt50dmHcfeV5zI0mvGLFU0SX76mRaGE9IAcrrATyrCpIM6o1vRBNDI2OkWMenbqWWTd3RIOKbdSBPdSCZWreQLtWKj27DxmRcCMJTHf1Zmlu0cZXFdanYIlx8xh01iH2+7bxFMOmYWqkQZBrZSSbd1VcqCX4ArOGxpZPThHlyPK2tWK91gZIZTtOmom6xBvT+R8EWwX7oqVewx795VkrMw4Z7ny6JY2H33tiXQK5dV/dxuXnHM4H3rN8azaXCcvlDzqE3Y0R1SYNVvZ8Ki754bvVh5dsNgYXAga+YYXLhb0JDMrH0hSoT0x8pUiayEzifUOAMKMw0mQ/TH8IoKa0Skiqzc1ee9LD6WanM7ln7mDhzZM8eevOIa5A1WyTJFeeSIGNM5BaxJC8P+vf44DZ11RD5tU9QKMTznnzogax9XiX5jFGzTmyJ4T1fuNMMN6R80s3x8KHEIRS/oE456VTY5Y1Mv1f/UcrvmPR3jnp+9g/uwa/b3pE7c6FKOduaFKwvUHHRxxDrTbJsweUnUvxFjiVCej2hYQRFx3C/sTA2dW2ixju2KGbNkvIlzpdZgaaeLL5K4q8weqnHXMIPeuGWdopEUtdU9MsKOrcfOObwZXPNioRWppJGzvpRqwit3IEw40HKbkMaKiuNSBl/JUDSdrZD8iP45y9OSqzO5NOXFpneGJjHP/+mY+8Z3lXHnZKSxe0MvGkfaBD7CXoimcs04S7J9x3W0o0o1R/z43IkHp3HsFixHxOUiB+HgfIuyr2RKBLCrVxLNksMY//mA1r7ziNo5f3M9X/ux0Tj5sgNGpfI+K/P2BFkKjx2hN2tWbNrifj2xxjGxxbB5yTA5beRzR7+mom+6um5KgqUmPoVRqES/ycx/cRFTp3Re3RxUaqUeAt11zFwfPqfGPl57CWcfNY2IqZ/XmFuXOsgO/UDE1IqyfmHJ/5xVc2s0h1oWsmSAullu1t25//91h6/MVBzGHmAmpCBvWyEN5wV3VurG7tP2OMIxKJZDlkd/evZ5zTpzP689ayIoNU6wfadGo+K5XMn10/IEZ2WbggpG15K8HBv3qvvlC2ifU+iJJFfCeIgaKImAiiNgTGWXZCU66jRTsRDPONKw2OJDiLM03b7YfiuyVMn4rvBO2jLeZ1VPhr992GtfcsJKX/e0vWDynxrKFvUQr7WYnV5zsd+hgJ6hCvc4PexruMzgjSYRUFNDuPQwUWp0KnTxFMNxWMaE94cWZakiS9NN54e7u6y9uqffIDe22HP6U44zFC+0beSab9oVpJ0KMxqbxNn904gKue/9ZPLxhknM/fDO/XDnCcYv7SIPQymcQve8P1EgSu7eVcfFkU7FCCTkk0p0Mt3mYTgxTodWpkhVpN9TrnvASEHmDd1xmXZVXpSJnrN9SfKrRCi95/pnp8l9uKL65Zsgurc3kce+A6cnQTHh43SRLT2jw5Xc/kw9/7Td84Mu/4q0vPor+enrArKSZICKTvl5cmk3IaouG1zKzbjNMfCJGVCGSEltgbjZSm3UAWjMzgnj/YisKEEG1dDbrdZ67ua3Hr9ni7yya8reI/hdEFu0N0yJCJ1dwUKt4Ht4wSR6Nr77rZP7p5kd500duZcmhA7zyjMWPW2ZQBo9yOmn65pC4W70YYr5LPohwFPBHBjdowfLtPY7pHQfC78IVCU5l/bZhJacQkYfrJ1RXrukUaEsfqYh7n6r9i5Pdj2qjqz6NiqpSq3iOWNjLyqEpLr76V/x8+TDvet1TWb5ugo2jGYfN69n3uVDKMK53RhKcdXJ3SVT96tYGdEnOoy0SZ/8m5o5InN07sEBfgNn6fbzbAUMw5OM4f56YLtEykkUhdmVeJFs2jyu9EUKqX2xFPTO1cEm5H3r7SlTLGVytDOhMNHN6KwnHHdzLLfdu5APX/prgHZeeewQXnLGYt159J8MTGWpW5g6le0TZ3sgPIqSJ0cxsk2XJn3gv3ytEwWAiU2LMp+s7pd+5IxxCnscjfa8cifL7I1rhEXX+LK96CcgSc3xFjOsxJekeEK8YEXlLVJmVir1y+mwD6G6PqAYGGgmFGYtm1Vi3pU6SOD76zQf4xYNbuPjsZbzheYdgBs2soBOVOb0VDh6sEYJQCZ6xqQ5jrRzv3Iy2W4DCjL4GP56YkjeOj8uK/rnlzyiJwKyegFm5czhx8n0zuzq2i0vE+8+PtdzNv8/jQKYj76tV3P/YmuwrdtmiaMaFBWxypm8RJ+RqNGqB0Ymcr966mjwqg70VNk12uPWXG7jx9rW88gWHMd4qeMvVd5IXSm814Z4Vo+TReHS4yebxDlGVF5+yiMVz64xOzBDLEkBtLIr7yyh8KjiLxTaHegvCYF+CdIn2TorNU/HtRvxwNXEbJ+R3vUTZofnX3zOGBodYpPxBr9LGNhf1UpvMqY200VRpRnAxUKkqQd0lhXCFEPtrvsIV3/ktm0YzTlg6wES7IE0dfdVAEoQsGhtH2wjgnENVGexLyQuj2Y701RKWb5hgsl1wxetOIEkco1N56ed2lTZm1sLxr1oUHyxIHpo9WxjepGRjKX1zcqa8MTAAPRq2uhrewWjLKKY6pBXP5nR7gebvGjPkkkqb21sLzI4peaJUCvAxUK9FnE8+u2XU3bBsIHknPdkbRyY7fZe/6AjOOGYOG0bbhODwrvy5pdjNpGw7msqYtRLVWDBQZe3mFpd8+uc8vHGCU5cN0imUQpUi1yFzfAPcp53qvU9I8Pp3hF0SHYJj8dyEr377EW68cSX9/WkpcjfBO8MnnrFmvsKNF+84uNb4+7FO/NZkzNuIzKpWkmOmf3tOkFJfNx2inJYhSCksdAi++9NMA40Kh83vyWqpv2NsMr9pVm/lNwNJfvN4ZEOr8+Q/9niX53UkieNr163gyi/8EopYvoflARelPiANoJ3zGWqec3ezfm/lqJ6+v/v2A+t+eu/w6r5KcmRIxPfVfbndobSt5ZkY3UkLKaWzhtFXC6zZ3GTDaItrf7z6wbUj7RX//osVP77shcfecOySPhbNq3PMon5Wb5z4ncYmDjR2stFp6snaGa/6Xz+lGbXcjL+dgNQIITynEfyXDb1JQ9xIIVvGxotKu11ExMYBo2s6cJAk3gcRKaW0Jp1oMRZRp0MB3iE9falOjmV5xM2qVNxl5sJfdJp67UnHzuFFJ87nRSfNRTUyljnmzIGhdZFs/Elqo6Mag/0p//v61UyOB3rqHmlvr+IxBV+1V1o1/luR6aWWCSbGrN6A9iREVbJOJMvBKH/qqV4LVMSVx+CY0i6ULFeIYDjSIAQvzBqokDjDJSHJc3kFNXftfQ9v4p77NvLt23o575RFPOuEgyiiMqevQrNIGOgRUq/090EcKxOuf4h2fDuia5XAivWT/OjODZApPvXsFExUwXw0zFa6xBO7J2Pl00bYCZVqIK08dokIxGlNkkAlEdKwvYZNABwUGJIXQ2bJqOHobVRp1OHetS3WDq/hczc+yslH9XD+SUsZ3SLUhwuaogwMCCcuHCA42eO2498HtiN67kCVz/3bclY9tI6ehb0UtvO+SI1KmvaMR5MXF53iK86JgO2k/NtTZG6Gz4tCKuuC8Xy8NuEx2cfc2QlFoRQaufU3w9x4z2bEl5G4Ilf6Ginfft8zmN0byPI/cKI1KvW+FObUSBrJLi8wMwhylRTxxMT7Txu2Afbjd1J3Cckd1ifI3A52qYpubwW64ogUSLfxQ7ICehvuCddMPh78f1GAH1GcLEt1AAAAAElFTkSuQmCC";

/* script */
const __vue_script__$1F = script$1F;
var __vue_render__$1F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleCrane-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_crane_eft_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1F = [];
__vue_render__$1F._withStripped = true;

  /* style */
  const __vue_inject_styles__$1F = undefined;
  /* scoped */
  const __vue_scope_id__$1F = "data-v-e8bf5730";
  /* module identifier */
  const __vue_module_identifier__$1F = undefined;
  /* functional template */
  const __vue_is_functional_template__$1F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1F, staticRenderFns: __vue_staticRenderFns__$1F },
    __vue_inject_styles__$1F,
    __vue_script__$1F,
    __vue_scope_id__$1F,
    __vue_is_functional_template__$1F,
    __vue_module_identifier__$1F,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1E = {
  mixins: [singleGroupMixin],
  name: 'titleLightLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_light_left_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1E = script$1E;
var __vue_render__$1E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleLight-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_light_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1E = [];
__vue_render__$1E._withStripped = true;

  /* style */
  const __vue_inject_styles__$1E = undefined;
  /* scoped */
  const __vue_scope_id__$1E = "data-v-18c1370c";
  /* module identifier */
  const __vue_module_identifier__$1E = undefined;
  /* functional template */
  const __vue_is_functional_template__$1E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1E, staticRenderFns: __vue_staticRenderFns__$1E },
    __vue_inject_styles__$1E,
    __vue_script__$1E,
    __vue_scope_id__$1E,
    __vue_is_functional_template__$1E,
    __vue_module_identifier__$1E,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1D = {
  mixins: [singleGroupMixin],
  name: 'titleTearcherLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_tearcher_left_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1D = script$1D;
var __vue_render__$1D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleTearcher-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_tearcher_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1D = [];
__vue_render__$1D._withStripped = true;

  /* style */
  const __vue_inject_styles__$1D = undefined;
  /* scoped */
  const __vue_scope_id__$1D = "data-v-5f583260";
  /* module identifier */
  const __vue_module_identifier__$1D = undefined;
  /* functional template */
  const __vue_is_functional_template__$1D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1D, staticRenderFns: __vue_staticRenderFns__$1D },
    __vue_inject_styles__$1D,
    __vue_script__$1D,
    __vue_scope_id__$1D,
    __vue_is_functional_template__$1D,
    __vue_module_identifier__$1D,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1C = {
  mixins: [singleGroupMixin],
  name: 'titleTextLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_logging_left_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1C = script$1C;
var __vue_render__$1C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-titleText-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c("img", {
                attrs: {
                  src: __$_require_style_title_logging_left_png__,
                  alt: "",
                },
              }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "top-zhuangshi" }, [
                _c("div", [
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                  _vm._v(" "),
                  _c("div", [
                    _c("div", { staticClass: "top-jiantou" }),
                    _vm._v(" "),
                    _c("div", { staticClass: "bottom-jiantou" }),
                  ]),
                ]),
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1C = [];
__vue_render__$1C._withStripped = true;

  /* style */
  const __vue_inject_styles__$1C = undefined;
  /* scoped */
  const __vue_scope_id__$1C = "data-v-2b850a5e";
  /* module identifier */
  const __vue_module_identifier__$1C = undefined;
  /* functional template */
  const __vue_is_functional_template__$1C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1C, staticRenderFns: __vue_staticRenderFns__$1C },
    __vue_inject_styles__$1C,
    __vue_script__$1C,
    __vue_scope_id__$1C,
    __vue_is_functional_template__$1C,
    __vue_module_identifier__$1C,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1B = {
  mixins: [singleGroupMixin],
  name: 'titleSevenpageoneLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1B = script$1B;
/* template */
var __vue_render__$1B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Sevenpageone-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-bgimg" }, [
              _c(
                "div",
                { staticClass: "big-titlebox" },
                [
                  _c("div", { staticClass: "triangle_left" }),
                  _vm._v(" "),
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle_right" }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "small-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                      "block-data-name": _vm.titleTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1B = [];
__vue_render__$1B._withStripped = true;

  /* style */
  const __vue_inject_styles__$1B = undefined;
  /* scoped */
  const __vue_scope_id__$1B = "data-v-16943e38";
  /* module identifier */
  const __vue_module_identifier__$1B = undefined;
  /* functional template */
  const __vue_is_functional_template__$1B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1B, staticRenderFns: __vue_staticRenderFns__$1B },
    __vue_inject_styles__$1B,
    __vue_script__$1B,
    __vue_scope_id__$1B,
    __vue_is_functional_template__$1B,
    __vue_module_identifier__$1B,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1A = {
  mixins: [singleGroupMixin],
  name: 'titleSevenpagetwoLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {},
      imgUrl: "title_icon_01.png"
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
    this.imgUrl = this.groupData.titleTextData.title_icon ? this.groupData.titleTextData.title_icon : "title_icon_01.png";
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_icon_01_png__ = "data:image/png;base64,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";

var __$_require_style_title_icon_02_png__ = "data:image/png;base64,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";

var __$_require_style_title_icon_03_png__ = "data:image/png;base64,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";

var __$_require_style_title_icon_04_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1A = script$1A;
var __vue_render__$1A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Sevenpagetwo-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-box" }, [
              _c("div", { staticClass: "left-top" }, [
                _vm.imgUrl == "title_icon_01.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_01_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_02.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_02_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_03.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_03_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_04.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_04_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "rigth-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName],
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "bottomzhuangshi" }),
              _vm._v(" "),
              _c("div", { staticClass: "left-tiao-bottom" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-box" }, [
              _c("div", { staticClass: "xietiao" }),
              _vm._v(" "),
              _c("div", { staticClass: "right-top" }, [
                _c("div", { staticClass: "bottom-zhuangshi" }, [
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }, [
                    _vm._v("···················"),
                  ]),
                ]),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1A = [];
__vue_render__$1A._withStripped = true;

  /* style */
  const __vue_inject_styles__$1A = undefined;
  /* scoped */
  const __vue_scope_id__$1A = "data-v-83fc3204";
  /* module identifier */
  const __vue_module_identifier__$1A = undefined;
  /* functional template */
  const __vue_is_functional_template__$1A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1A, staticRenderFns: __vue_staticRenderFns__$1A },
    __vue_inject_styles__$1A,
    __vue_script__$1A,
    __vue_scope_id__$1A,
    __vue_is_functional_template__$1A,
    __vue_module_identifier__$1A,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1z = {
  mixins: [singleGroupMixin],
  name: 'titleSevenpagethreeLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {},
      imgUrl: "title_icon_05.png"
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
    this.imgUrl = this.groupData.titleTextData.title_icon ? this.groupData.titleTextData.title_icon : "title_icon_05.png";
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

var __$_require_style_title_icon_05_png__ = "data:image/png;base64,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";

var __$_require_style_title_icon_06_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAABJCAYAAABhE0UAAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA3ppVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDkuMC1jMDAwIDc5LjE3MWMyN2ZhYiwgMjAyMi8wOC8xNi0yMjozNTo0MSAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0iOEREMzIzRDRGRjEwMEMyRTZERDRDMjhCMkY3ODJFMEIiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6OEQwRkQwMjcxRTY4MTFFRkFGRkFCQjA1MDU4Njg4MjkiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6OEQwRkQwMjYxRTY4MTFFRkFGRkFCQjA1MDU4Njg4MjkiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDIxLjAgKE1hY2ludG9zaCkiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDowN2MzN2Q4Zi1iNDg2LTcwNGMtOThiZS02ODQ3YTRkMjEyZWIiIHN0UmVmOmRvY3VtZW50SUQ9ImFkb2JlOmRvY2lkOnBob3Rvc2hvcDo5MjliNGNhMS03NDAxLWUyNGItYWMzMy02MTljNDM3OTlmZWYiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz6zqnOrAAAKlElEQVR42sybC4xW1RHHv33AKrs8dEVYdrEFeaxWjEApAonlVQQbQdC2UaRqY3wVH6hprG2NMRZFRRLfFUnbYKxY30ZRKSARFVhQqi6KFhFYFll2YXGXZZ/f1xnzv+3JOHMf33c/Pyf5Rfd+95575tw5c2bmHPJSqVQiZulGDCPGEqOJk4n+xAlET6KEyFeeO0I0EgeIfcSnxLvEBqImrs7lxajwqcQ04hxHyR4ZttkI5TcSrxKriUO5VvgU4hpiOnES0T2RHWkm/k08CZrTaoUVTpM+xO+J/anvVjqJ9cTZRLeo/U5X2bHEmxl0up1oBW1QIqrws4uJsih9T8ekLyDuJwaGMSDiC6KWqMd85P9+TbTingKimOhLVBD90PagkP15i7iZ2JKNOXwR8RBxXMB9PNfWEZuJaijdGPIdvYmhxOnEWcTkEIP7OXEd8Xqcc3gWcTDAzFYSFxFDM/ANLgXEKOKPRHXAu/cRM+Oaw/zSGp+XfU7MI0pjUlRjGLGIOOLTj13ExEwVLiZe93nJq0RlhI6zZ+2l0JPID3i2kJhBfOTTnw+IUzNxWtcT9xjr67PElcTBkPOTI7AFWLu74NQSiLySxBvEw0RLQDvDiUeJScbvzxGXEU1R5/BA4mNjJN8gToxolvcGzMMuYkrItioClsYF2nP5ASM5D6MppQZLQV2UqI4oDbiH+1MWsj3uwxVYCSzLrNReYAmvizOJQnG9C+vwR1GDOhEOcocfJP6JxMGTwxHa/JK4AQmHlB8Q8zHQoUx6NqIZKRvT9MbHEa857bwJB3Ymsdu5fksabd9kmDV79DFhTJpHZQJRJK53Ei8QDcozxxK3I+K5CQFEAm2w6a0lpgjr6iDaEW15cgvxEoIO10ktQ1Q1S3n3I8Qa5Tpna1eF+cIDiLeVEdtDjDae+TWcjhfgr8O1fyB2lrIUzw0ntiq/H0DAsQDv9aQaa7J8/3TE5VL2EqcErcNWoPEa1mV5fznxfoiAnwdkJ/EXYojz/M+JVURtyMSBk4Y80YcS4jnjnTcHKTxLiWiSxB3G/X8I0cld+GIDAyI6/vJNAW3xVxuvPP9LI/PiwKnIT+EroKB0ABcq97JJfuXct4m4h/jMubYOKWVYJzRPWNha4j7isHPtGcXarLiBLWeEn8LXKw/xqM8R93EouFwMykzna3En70SQYAUPfY3fJhMPETfCpyTw9T05SsxVwtYlSt9biEuIYyyFL1FMg+fCbeK+k8Sos3P6G3Gyz9crRaXkX3BWPPefxTKoZUuuU9ok+rRC+cpzjGnAg9ffr6KxT3loueIo1ir38br6K6XdESjPaPPsa+J+JBEyYVgsBtaTP4lBYc4gvlTu5XV/uKXwYMPrrlRGdBBGT3ZoC9HDua8vPHGQ3CranyTqZjxYXxDzMeCy7/2Mj7CZU0cr8GhBGUZKCQIMV3YihJuLUM8tsSZFaeinzt/VyMKWiWzrWlHeaUeA4gkHMFNRedEql1zG3W8EIf0KDYU78SIpBT7x93vEDuKH+Huz01F+z3gU6Vm2E7NRmmGpIu7DgPZFlLUTv32GJKUcf3+IkpEl7SI294TT2z5W5/ugoKY11uFTi3KzoQPOF+4NRTxZ5SjL8iJ2GryQc7DzW4NQoDREUtFlJC9d+cZXvBjJupRaNanWR/YY5/95kNpEJuPKAGdAkqLgVyisKqg4kK/kAJ7VtmkKT0bwXyCud8BsO32mQb4I+L0p0wQz9uRnxJ3IV3le34VdC6+dKufeClElTQYozNPieOV66zdTQwkE3vepFR2veEUOPs4iXhFJwlbhpceL5YK9bR3RIN7D7RzrPDeVqHd+b8AyNchYYYYayQgnQ6e7N3YnnjSU5SzkAiMw4Pj4kBKkPK5shcwPiJM/UbIxTjKqlHu3IxrLUyK0ZuX+v3OS4954g09HHjEqihVC2SSyoTtQidS+wFwo0IiBbMU6+zLxY+OZkUgz65VIy7WGPKST2gfjcLnEjU72GMpu8tm/4Q21D0VoeaEw9bHGQHEIeA0SlSlKxMQBzjQ3lyWWib7dJayojxHc8EBN4AHxsp0VhrI1skRifDHXlBcS44jHkMTXwHqilGz6ISZvRvbzW3zl90TxX+5wTDQK9RswGN9kS35bnpxWXcVZhk/neO6/JDKmPYpJPYrBDdpaOVuptnQIh8ftXadkSg8qOiSRsf2vALAUgXvKpxD2V+FxJeMVx6XJNiQIU4Wz4QzqUuJpWEWQrEFRUBYPGgxz/omrMM+hHSFKM3MCtk8eEM8cRcCuZV0HYaaesk8b9ahPYdJJkZf/Qlkalxl9f951uN58uc0otLkyP8AcB6OEexhr+aV40TSY6BFl5yIBz1wjTLAeCpRj7b8b0+sgVozu4t0zjJJyB5apb22mFWEu7/ZZh8eEcDas9PmKV2ePezm+mFS4Urz3RdlJwOvzOcpyV2qs0ylYTje/3cPbjQdfSOc8hcLDIl+eiqXJnbfnRWxzidHnBnfuesj0UAu625B7dmR6REps2/AO/wpcKxFpXFiZjSK/Jk9oxyAKlZxRu6d/Fo4hFRpBflg5jVhonAX7gHhMSxNltlRtpItXi6+Q1gkpnPuo93JThySuVeHkXZDwDuO92u4gMrOFTgHB91BLBczgRCU1HELszlBpHuAzkGunnA3xAuS5G4zyjCzVLPEx5SVIb1NhDrUMEEV11wGUpbJ3fiMsecjOLFnlU+dWvfQIo4S6JSC8/K64HGurtZUzOuopnnIjRGxCRJZLZScZoaO1KxJK4SKfzKkW8WoulOVo6x2fY4zXhm0rX1lzlxr13jKcjMmFcB16pPHbA9gQD+01pfBO+iLDy/GZ6F45UHiCsgHA8gzxZ6MsG1phXhPXG2cj20JUDbMh2smevcTiqAfG840QcKJygLQLZdrmxPdD6oyzJpEVLkJhXCu01+VIuSbjWFXk0LTQMGnNbHkn4UZiFPE28RU64p3EacaAtGRB4Vr4FPfMFZ+rPiEOhTvELoFr6sPAPCjbClPvxN/7saG2GvtFcckOvMt1XN3SSmqM9WoIdhrSlUacqBkS0zo80Qg67lbKu5HWYU/+g7OUO9L8IrxbOIdY7mxzZiJ7jS1QPpXbM1On5clqbGKvzMAzn0ncGpNH3qVcrwy5fRpKYZatOOp3JY4c8g7/0YidPc/Yeo0ihw2/Uu5swKfttDQn9hQYg85XYFng3LQYG+h8TGGE8jx70unYyc9Etiueuhgh5xoz/01DYVeqxN5tAfDW7t8Rv1HKRqNiMOutWAlkaPsjvKMtDpMOc7Sg3dnwXuQcXUiIHf/iDN+1Bc5LykhxnCKrCmvevdqIisoybJtP+nxiFPOG5krhZEI/nd4rylfwkVVGkXFcrhROIORMKecuSmNoe61RH58R1h9lQ+FDiW8ffOkRUx7Nc/hd5fqoRMh/q5gNhZsVhbsbCXxUaTHMmgf03Fwp3KpkW3kJfRsnHR+xMfH/f5nqyuRcKdxlpJd5MbW/DWuy5q0rc6FwpxH1xKVwvQh+3Px4XC4U7jCU64yp/XbU3FKKn+idC4W3KWnlzkTIf8EdUjYq7dXheloFgEzhYw6rcXZkvXImIw74bNdb2GKpwhGLwOf+K8AAQevneUuGGeQAAAAASUVORK5CYII=";

var __$_require_style_title_icon_07_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAD4AAABJCAYAAABl5pU9AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA3ppVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDkuMC1jMDAwIDc5LjE3MWMyN2ZhYiwgMjAyMi8wOC8xNi0yMjozNTo0MSAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0iOEREMzIzRDRGRjEwMEMyRTZERDRDMjhCMkY3ODJFMEIiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6Q0U0ODYxRjkxRTY4MTFFRjk5NUJCRTQ5NDM0Q0NGODkiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6Q0U0ODYxRjgxRTY4MTFFRjk5NUJCRTQ5NDM0Q0NGODkiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDIxLjAgKE1hY2ludG9zaCkiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo0OTY3M2NhZi1lYmNmLTA3NGUtYjVkNC0xMjg4NWJlMGQwYTgiIHN0UmVmOmRvY3VtZW50SUQ9ImFkb2JlOmRvY2lkOnBob3Rvc2hvcDo5MjliNGNhMS03NDAxLWUyNGItYWMzMy02MTljNDM3OTlmZWYiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz72+xUNAAAFWUlEQVR42uyce2gcVRTG7242aUw0VWPro1ofVNrSguILKkhRbEBEqihUxAc+aPUfX6DofyqCgqIIvhCs0qr4RikFjbQqBt+P1mrVtqhVqdHG1NjUxE12x+9jv4HL7M66O+bO7mzmwI+2k83M/e7jnHPP3G3G8zwTo80AveAQ0AMmwRAYBn+CYlwNycUoeh44D5wDjgcdEjoCPgavgXfAX3E0JhPDiM8B14GrNdKVOpuNmACbwb1gPfjHaaso3CEng4+8chsHe8EomAz8LA8eBL0u2+ZyxE8Ba8BC69ov4HPwIdgJOsFisER/7m99djW4WUshMSN+JPggMIovgdNDPt8OVoFvA6N/K8i6aKML0WzoXVbjJ8BDoLOG3z0NbLJ+dwiclBThHO3f1fACWK0RrccvbLPEP+5CeL3h7HAr/obZhWCW/r5bHprevKvGZ/wE3lXIo50P1oJdIFslLP8M/p7qcJZTA26SgELY/cBRlvBRsF2hqq3GNuXBEWCu7kf7poqojDpkK7gTbJtK4QeAl0GfaW67BjxZb+bGkWyvkDayZ2aDmab57TAtxaI1W3xrU2j0/BGnqBsVS02IcMbbReBATfMXwDrl3pkGiZzQklpp5Qo7wI8hvoCDvA88BV6hh7tfIadW42dXyjtmGsxs8KZXnzHiLGMvnBths9JpzYZGWluEtnOWLOGU6I7wwEKTrOlCxM7vyUX8Rc/q8UaKjupfvLBp8jbYImGewlmfEhjOkuXgoAY6Nz5zTJFmnnX9ffCZ2ljUpqdPW+OyTcrOCg5gRSDF6wYDXvPbDYF27xfi/O4LSwE7KvTw7gTE8aHADOwKS3NzVbylbUwX7wG/aYpPNpFYP2XlFH8r4LOy9QoPWlF1sS8VBYpNONp7XBYbx0XiLWumqaXCU+Gp8FR4mM1RiSiRFuXdGfP2y8AF2ig8B16sEObalTm5iPkZ3Xc0LuGcIReDR6xrS03pTec6K2taAG5RJ7kUPgCeVmXFqfCZKlwEixJng36NOvP8y8FVMczYZab0onHA9RrPm8pl3lFrZDn9B2NaqnzO3jim+j5NraWWY2M9+1V1ii/8GVN6z83a+ISjkhM3Su+Br+NybhtM6W3JGRrl/goPH1YHdRh3dbmi+R8lsCjC+TC+5v1Uogr/sTRaJpz5NmkSbGnmlgqvL4lIrEVd46eaUtmWjo3vv7dMB+FngUfBfP17BbgSbArk8zzT1uswZWVEYR3wkziEd0vkfOvaieAiJTJ53fNSdY5rY2X1iihJTL1rfIZGM2g91r04GsfGNGN5XGRWHCM+okxtuXWNG5ONVrLC+M530IeCYxylrFk9Z71G3blwOrM1SkUv0cOfAG9Ya5lrj2dWrjfuavD+tnQ4asdGcW7ciT2sjYiflxdDZseIaVKLGs7849Zp5pYKT4W3ZsrKuH2CvPxmE6HYl0Thx5nS0Uk/V38d3GFK785tY8cc7Dic8fjmYBzCmbmtUkrq27VKGR9TR2TVKQ8oq3IlvKBk6jbwq2vhTFcXVbi+WEnNmO7JL9wsjGHGco+wNg7hrJx+Z8pr61sDKSszuTNN6diIizNx/hGPfmWJzqc6xfEtylz1No2nhZ+3BPqV1x0SXjRTX2n1c3Webf8jLuf2vSkdnL1bgn4w5UV9dsL2Vgxne0ydh23SBCYVngpPhafCGyg8M12Fj7eIvnxY5pirsu3s0s+TOvoU3GvKj6BXFX67KX3RPck+gFllu7bRNQs/WrT0Gm95RxYmfNc01D3Iqc7/kmCBnFnGNP5LdK4so63sF9w2UziPZI6pcJAzzfm1i6kwOjrW554FX/0rwABTF+N7BLI4ZAAAAABJRU5ErkJggg==";

var __$_require_style_title_icon_08_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAE8AAABECAYAAADA82hFAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA3ppVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDkuMC1jMDAwIDc5LjE3MWMyN2ZhYiwgMjAyMi8wOC8xNi0yMjozNTo0MSAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0iOEREMzIzRDRGRjEwMEMyRTZERDRDMjhCMkY3ODJFMEIiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6RjgwNTdFQUYxRTY5MTFFRkFFQjA5REY4NzFCMTlCRjYiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6RjgwNTdFQUUxRTY5MTFFRkFFQjA5REY4NzFCMTlCRjYiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDIxLjAgKE1hY2ludG9zaCkiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDpkMDcxY2Q3OS0xYTJhLTIxNDMtOThjYi1hMTY5NWYzYzRmZjAiIHN0UmVmOmRvY3VtZW50SUQ9ImFkb2JlOmRvY2lkOnBob3Rvc2hvcDo5MjliNGNhMS03NDAxLWUyNGItYWMzMy02MTljNDM3OTlmZWYiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz62N1/TAAAJ2UlEQVR42uRcaWxUVRS+0w4FC8i+CShlKQjKLksLLTu4IG4YNRp3XDBGDT9cEgiKxvhDjcYliBsBNVFwQ4mAslvBWnawIAgClQpSoGxl6XhO5nvp7e25M++9GUxn5iRfZua+++68+d65Z7v3TSAUCqkUkosITQiZhEOEI7EMFkwBwuoQGhGuJIwjjCS0JKwkTCH87XfgQJJqXitCFxA2jDCc0ELo9yVhIuF8qpPXmzCI0JfQC6/RZtY/hBGELbFM22zCk4SuhBBQmySNcJawlPAW4Tim4lDCGJDWjtDa47jNce4Wv5rXnl6/g4ongiwknAFxTGB6jOO9TZjsV/PyE4g4lqvjPF5PeOAyP9OhVZJ7Ww5HviY8RvjYYis7+dW8yiQj6yhhP2ExpvhGaNVpQi7hbqN/A0J3QmG84rwz+NITPmxKAFOhIT7/RdgNLfciZzErukfpx6QUE7YTVhGWELZa+u5AcNzcaB9CmONVkWzkHSbchQvyEkiHEJSyVxyANp4q033cBL6BNxLmC8f4pi6CtmzAa6nLKbyCcJPRPhw3+2i8MozTuBNnfGjMeeGzn0C0IgIJPP3KfdyQlQJ5nQkdcCM8OQyb+A0Bgpi6br4jmtiuoSEyCD/C5uic0D7Cj7dNRGHyRkWwuRMID1tSsu2WoHjMhSaPqxIPIhtpEAcSusEejvThlAZZjg2HnXyH8JJwvMRCnhPvXTDybiO8S3iV8HwcUq65hKmIw3p4PJ9TykuE9o7a77pVuMlsx4uE85oSBl5I8npo5/SNkbxGMNIs9QlZHs/PsmRGa7XcvIFFQ39RNWt59Qh5F5K8Sst7v3IuhnMzUT0xhePKbdrvk0zCOiXX8fp5Cc0S1WE4MgAao8sxwjLt81DhvJMg0JQuXlK1RCePp2RLYXb8rH2+DHGcKT8KbR28FEkSnby2FmKKNZvG5F4l9FkqJADsxfukCnkswyyxnJMtZBD6W2zjZqF9oJaXJz15o4yMxrF7OjHsWC42+pxHEcEU1tLWqUJebyXXJAu1fLqXJRRaLrQxyVekCnn1kFWYUqDCCzwszZHNSLbxkEWbU4K8gCWpZ7u3V/ucI0zvAwiYpSJBWiqQp+AQzOCWs4zVRs5b3+jDdcHfLClel1Qhry1snymLtfdXoJ8p61XNumEdN8WKZCGvmaqqXNtyWJ6y+Rby9gimIGXIS7MEt0fgOBwZLfTZTdgltPPaSeNkJG+/0NZTSNVCRizH2llXOHeNxRT0SzbyQoYt021aJ8vUDWnTWypRcSHhlNHW0GIKEl7zpKQ+E9onTcsdeM/eNs8SEx4W2ntF4igRyQvARv0hHBsqhCylxrTk3NVcWKqwxHvswdsnm+Yds6RW+UJSzymaXrtrpeSC5yKhrbOlauOLPL1/ehxISDc0yq2wF10ltPM2s2yhfZ4Kl6BYC2creT14haq5tS49ktPwSp7u0n+Pg/Yc0rRjr4dzK1E1OSkck+IzLj+Nh0180zImr6ptEtqHWTy0Z/I+U+HlPL6A6TGSx4RNUuEVtPuVt9X6urB5Wzwk9Se0QoEk5ap6+d6RwbZ4z+uGbvZIz8XRdq0AvEoGpi5rilkl7oEqyiEfIRA7jceNdiaOVwoXJovDcOxjoWCnGlvSMDeyzaKdY5IxPVsjaBhr5RCf4+1Ucmk+PxnJ22xJ1XrbjHwUYbu3UWjvIIUsiU4er36ttcRnXX2O+auquarWSAkLTclQVVliSeql9Veu092OAHsvQpivVPVK9C+CKWCecpKRvJVCvBfA1A0Y2vMR4RPkt+2Qek1ArvwiyOVYdo/wPZeb2YvXUCUP4K1YRxFmLPP5o7n+NlaF99CVw/gv9DEOe8ciwUkMBGFOMXQG4Y4I4zyLDOQNaOZg43g2KjdV9cFQKPRkqKb8TejEj1YB9QnvEQ4b/coIswlNtL5BQoHWZ5p2jFGH8DLhgDFWOWE+P1Sj9R0fkqWbMeY0oU8F4XIczyEc044VE6YQZhAOau1bCW0IuZbvnaR/rxvN46W9WSq8N08JMRVv/Oa9bbeo8D7maPI64VGhnbeD8Qbu1ngt9Th1pUC6N2K3XG3K7YPdK9JiRZ7KvHGTF314MWkBEoKmxpj9wUVlNJvnBJ+8QXCi1s5bs34wctFrCQ+5+JFjDeLKUM3YaaRDjxjX4Cbn3ie0O46gmda2TlXf3LhCy9ODyE5ClhsySGlbddOi5J5cVbheq37wYsk4aMZ4VX194AYY3Ei73h/Q3u9W4V3pPNY1qnp1+HZcm9sd9CWWetxILa/VKy96ub4j2hw5GcGLd9f7RiOvoREczkIQeQqJ/EztWAvtrtlEXwt9H86GL5YXqF9RVRsmszGN3Goex2XS+it700445ozN1eG3kQPnwUG00GaVU2xYaylRDXIbqgS0PiELwVJfNxJtV6jXMGqjYHN5Gl6HUOQnbdybkZ2YXnWelp7ttaRqo9xcYDpCiB0aOZMQ7zjqrtuvUoQNkcbUS+eTVNUGaq7uPqOdux0a6YXA9Ybt1JN6Ln4+ocJ7U2yyGOGMHgJJq2q5cG4RLy4I7VigtfWEgV+Ou6mvRH2rwk/7RBrzA+0971riXfCrMJ4e5c+FVnshr0S70bp0hY3j6ciPm36owg87n8fv42ziBUQTpcasknbNN3fKYG4u7lPCF4bBzVNVO9kVvO87Lsb6HnZTaRqXa+ShBbCHSnkrzSvDgTnSWlVtbvyTcB/s+HBMWbbDU5W8esam4F/BlI12Sx7bkcn4QWeFcIa15F6XMR7LU4TXcPdN+UaFq8r7fWYtyw3PylJfKJiWIhQpVJH/HmQTCDclx0t69g/CjDm4EE68D6AC8ZPy9p8E5SDwc9z5drjrRRjrtPIvRbjWLKFElaG8P4R4DLbU3JbL9r6j19x2WQy5rDTFCuJcJOCZsVogj8OTS5W81itJJvL3q5S81YxNweBk/FMadmh3Gm1ZICESeVmwhf3gvIYg1lSW0lafZCSvANMzw2gfYFRtMtCWg2ndS0V/qlyXJsEIea1f23NOVX+06mwMRFT4OOcA7JS5SScf2jUA6SCviLUx8l4v8puNvLr4sj3CHVRRiglBVf3RS54uQ33UDo8r+eETN+cts5C3Db8n4JOwSkQCnInMtP2gpghB4iH3AP+nrLGke34XhdYhbPkVQf1OJ1QJqOSTYiT5bXyez2sbS1Gu2orxymwpWKJJtFLVLiT1bsg7B2K2w1Mvglcui/Y9QSVvbqnNwlpxMEqfU4j3RluO/wuytmp5eonXCwnCtXM14WlV+/+MkB3YY8rdPy7ORM3OqYJztfhn1PY2wCOfiOVinP/PC+CLmqnauxzJsds+JS8L2oTz2mwtxSxRcfx7u/8EGADNYDPRVsUY2wAAAABJRU5ErkJggg==";

var __$_require_style_title_icon_09_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$1z = script$1z;
var __vue_render__$1z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Sevenpagethree-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "bottomzhuangshi" }),
            _vm._v(" "),
            _c("div", { staticClass: "left-box" }, [
              _c("div", { staticClass: "toptiao" }),
              _vm._v(" "),
              _c("div", { staticClass: "tixing-bg" }),
              _vm._v(" "),
              _c("div", { staticClass: "left-content" }, [
                _vm.imgUrl == "title_icon_05.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_05_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_06.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_06_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_07.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_07_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_08.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_08_png__,
                        alt: "",
                      },
                    })
                  : _vm.imgUrl == "title_icon_09.png"
                  ? _c("img", {
                      attrs: {
                        src: __$_require_style_title_icon_09_png__,
                        alt: "",
                      },
                    })
                  : _vm._e(),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "rigth-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName],
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "right-xietiao" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-box" }, [
              _c("div", { staticClass: "right-bottom" }, [
                _c("div", { staticClass: "bottom-zhuangshi" }, [
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cuxietiao" }),
                ]),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "circle" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1z = [];
__vue_render__$1z._withStripped = true;

  /* style */
  const __vue_inject_styles__$1z = undefined;
  /* scoped */
  const __vue_scope_id__$1z = "data-v-49383e20";
  /* module identifier */
  const __vue_module_identifier__$1z = undefined;
  /* functional template */
  const __vue_is_functional_template__$1z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1z, staticRenderFns: __vue_staticRenderFns__$1z },
    __vue_inject_styles__$1z,
    __vue_script__$1z,
    __vue_scope_id__$1z,
    __vue_is_functional_template__$1z,
    __vue_module_identifier__$1z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1y = {
  mixins: [singleGroupMixin],
  name: 'titleSevenpagefourLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1y = script$1y;
/* template */
var __vue_render__$1y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Sevenpagefour-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-box" }, [
              _c("div", { staticClass: "title-top" }, [
                _c(
                  "div",
                  { staticClass: "rigth-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName],
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "shutiao" }),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "rigth-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                        "block-data-name": _vm.titleTextDataName2,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "title-bottom" }, [
                _c("div", { staticClass: "title-bottom-bg0" }),
                _vm._v(" "),
                _c("div", { staticClass: "left-xian" }, [
                  _c("div", { staticClass: "title-bottom-bg1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "title-bottom-bg2" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "title-bottom-bg3" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-xian" }, [
                  _c("div", { staticClass: "top-xian" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "right-xiexian" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "circle" }),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1y = [];
__vue_render__$1y._withStripped = true;

  /* style */
  const __vue_inject_styles__$1y = undefined;
  /* scoped */
  const __vue_scope_id__$1y = "data-v-c9cc7c0e";
  /* module identifier */
  const __vue_module_identifier__$1y = undefined;
  /* functional template */
  const __vue_is_functional_template__$1y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1y, staticRenderFns: __vue_staticRenderFns__$1y },
    __vue_inject_styles__$1y,
    __vue_script__$1y,
    __vue_scope_id__$1y,
    __vue_is_functional_template__$1y,
    __vue_module_identifier__$1y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1x = {
  mixins: [singleGroupMixin],
  name: 'titleSevenpagefiveLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1x = script$1x;
/* template */
var __vue_render__$1x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Sevenpagefive-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-box" }, [
              _c("div", { staticClass: "title-bg" }),
              _vm._v(" "),
              _c("div", { staticClass: "title-content" }, [
                _c(
                  "div",
                  { staticClass: "small-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName],
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "big-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                        "block-data-name": _vm.titleTextDataName2,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "right-jiao" }),
              _vm._v(" "),
              _c("div", { staticClass: "title-border" }, [
                _c("div", { staticClass: "border-top" }, [
                  _c("div", { staticClass: "border-left" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "border-right" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "xie-cutiao" }, [
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "xie-cutiao-item" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "sanjiao" }),
                _vm._v(" "),
                _c("div", { staticClass: "circle" }),
                _vm._v(" "),
                _c("div", { staticClass: "border-bottom" }, [
                  _c("div", { staticClass: "left-xiexian" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "cutiao" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "right-xiexian" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "point" }, [
                  _vm._v("· · · · · · · · · · · · · ·"),
                ]),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1x = [];
__vue_render__$1x._withStripped = true;

  /* style */
  const __vue_inject_styles__$1x = undefined;
  /* scoped */
  const __vue_scope_id__$1x = "data-v-ed7e1f3a";
  /* module identifier */
  const __vue_module_identifier__$1x = undefined;
  /* functional template */
  const __vue_is_functional_template__$1x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1x, staticRenderFns: __vue_staticRenderFns__$1x },
    __vue_inject_styles__$1x,
    __vue_script__$1x,
    __vue_scope_id__$1x,
    __vue_is_functional_template__$1x,
    __vue_module_identifier__$1x,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1w = {
  mixins: [singleGroupMixin],
  name: 'titleFortyLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1w = script$1w;
/* template */
var __vue_render__$1w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Forty-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "left-image" }, [
              _c(
                "div",
                { staticClass: "image-box" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "show-title": false,
                      "container-class": "image-container",
                      resourceBasisPath: _vm.resourceBasisPath,
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "right-top-bg" }, [
              _c("div", { staticClass: "zhuangshi" }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "rigth-titlebox" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c("div", { staticClass: "right-block" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1w = [];
__vue_render__$1w._withStripped = true;

  /* style */
  const __vue_inject_styles__$1w = undefined;
  /* scoped */
  const __vue_scope_id__$1w = "data-v-68d58234";
  /* module identifier */
  const __vue_module_identifier__$1w = undefined;
  /* functional template */
  const __vue_is_functional_template__$1w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1w, staticRenderFns: __vue_staticRenderFns__$1w },
    __vue_inject_styles__$1w,
    __vue_script__$1w,
    __vue_scope_id__$1w,
    __vue_is_functional_template__$1w,
    __vue_module_identifier__$1w,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1v = {
  mixins: [singleGroupMixin],
  name: 'titleFortyoneLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '标题',
      titleTextDataName: 'titleTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1v = script$1v;
/* template */
var __vue_render__$1v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-title-Fortyone-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "title-container" }, [
            _c("div", { staticClass: "title-bg" }, [
              _c("div", { staticClass: "title-card" }, [
                _c(
                  "div",
                  { staticClass: "left-image" },
                  [
                    _c("xml-image-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                        "block-data-name": _vm.imageBlockDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "show-title": false,
                        "container-class": "image-container",
                        resourceBasisPath: _vm.resourceBasisPath,
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                      },
                      on: { blockclick: _vm.handleBlockClick },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "rigth-titlebox" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.titleTextDataName],
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
              ]),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1v = [];
__vue_render__$1v._withStripped = true;

  /* style */
  const __vue_inject_styles__$1v = undefined;
  /* scoped */
  const __vue_scope_id__$1v = "data-v-c2c03408";
  /* module identifier */
  const __vue_module_identifier__$1v = undefined;
  /* functional template */
  const __vue_is_functional_template__$1v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1v, staticRenderFns: __vue_staticRenderFns__$1v },
    __vue_inject_styles__$1v,
    __vue_script__$1v,
    __vue_scope_id__$1v,
    __vue_is_functional_template__$1v,
    __vue_module_identifier__$1v,
    false,
    undefined,
    undefined,
    undefined
  );

const TitleComponents$3 = [__vue_component__$2k, __vue_component__$2j, __vue_component__$2i, __vue_component__$2g, __vue_component__$2h, __vue_component__$2f, __vue_component__$2e, __vue_component__$2d, __vue_component__$2c, __vue_component__$2b, __vue_component__$2a, __vue_component__$29, __vue_component__$28, __vue_component__$27, __vue_component__$26, __vue_component__$25, __vue_component__$24, __vue_component__$23, __vue_component__$22, __vue_component__$21, __vue_component__$20, __vue_component__$1$, __vue_component__$1_, __vue_component__$1Z, __vue_component__$1Y, __vue_component__$1X, __vue_component__$1W, __vue_component__$1V, __vue_component__$1U, __vue_component__$1T, __vue_component__$1S, __vue_component__$1R, __vue_component__$1Q, __vue_component__$1P, __vue_component__$1O, __vue_component__$1N, __vue_component__$1M, __vue_component__$1L, __vue_component__$1K, __vue_component__$1J, __vue_component__$1I, __vue_component__$1H, __vue_component__$1G, __vue_component__$1F, __vue_component__$1E, __vue_component__$1D, __vue_component__$1C, __vue_component__$1B, __vue_component__$1A, __vue_component__$1z, __vue_component__$1y, __vue_component__$1x, __vue_component__$1w, __vue_component__$1v];
var XmlTitleLibrary = {
  // install,
  ...TitleComponents$3
};

//
/**
 * 单图片组合
 */
var script$1u = {
  name: 'paragraphFirstLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1u = script$1u;
/* template */
var __vue_render__$1u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-first-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "rotate-container" }, [
            _c(
              "div",
              { staticClass: "rotate-container-inner" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName],
                    "block-data-name": _vm.titleTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
          _vm._v(" "),
          _c("xml-text-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.bodyTextDataName],
              "block-data-name": _vm.bodyTextDataName,
              "extend-params": _vm.extendParams,
              "current-rule": _vm.currentRule,
              "rule-index": _vm.ruleIndex,
              "container-class": "text-body-container",
              "editor-class": "editor",
              mode: _vm.mode,
              "page-type": _vm.pageType,
              resourceBasisPath: _vm.resourceBasisPath,
              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
            },
            on: {
              blockclick: _vm.handleBlockClick,
              groupDataChange: _vm.groupDataChange,
            },
            scopedSlots: _vm._u(
              [
                _vm._l(_vm.$scopedSlots, function (index, name) {
                  return {
                    key: name,
                    fn: function (deliver) {
                      return [_vm._t(name, null, null, deliver)]
                    },
                  }
                }),
              ],
              null,
              true
            ),
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1u = [];
__vue_render__$1u._withStripped = true;

  /* style */
  const __vue_inject_styles__$1u = undefined;
  /* scoped */
  const __vue_scope_id__$1u = "data-v-c42bb684";
  /* module identifier */
  const __vue_module_identifier__$1u = undefined;
  /* functional template */
  const __vue_is_functional_template__$1u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1u, staticRenderFns: __vue_staticRenderFns__$1u },
    __vue_inject_styles__$1u,
    __vue_script__$1u,
    __vue_scope_id__$1u,
    __vue_is_functional_template__$1u,
    __vue_module_identifier__$1u,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1t = {
  name: 'paragraphSecondLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1t = script$1t;
/* template */
var __vue_render__$1t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-second-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-second-title" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-top-border" }),
              _vm._v(" "),
              _c("div", { staticClass: "title-left-border" }),
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1t = [];
__vue_render__$1t._withStripped = true;

  /* style */
  const __vue_inject_styles__$1t = undefined;
  /* scoped */
  const __vue_scope_id__$1t = "data-v-4d9eb16a";
  /* module identifier */
  const __vue_module_identifier__$1t = undefined;
  /* functional template */
  const __vue_is_functional_template__$1t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1t, staticRenderFns: __vue_staticRenderFns__$1t },
    __vue_inject_styles__$1t,
    __vue_script__$1t,
    __vue_scope_id__$1t,
    __vue_is_functional_template__$1t,
    __vue_module_identifier__$1t,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1s = {
  name: 'paragraphThirdLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1s = script$1s;
/* template */
var __vue_render__$1s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-third-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "body-text",
              class: _vm.groupData.diagonalLine != false ? "diagonal-line" : "",
            },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1s = [];
__vue_render__$1s._withStripped = true;

  /* style */
  const __vue_inject_styles__$1s = undefined;
  /* scoped */
  const __vue_scope_id__$1s = "data-v-baadd36c";
  /* module identifier */
  const __vue_module_identifier__$1s = undefined;
  /* functional template */
  const __vue_is_functional_template__$1s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1s, staticRenderFns: __vue_staticRenderFns__$1s },
    __vue_inject_styles__$1s,
    __vue_script__$1s,
    __vue_scope_id__$1s,
    __vue_is_functional_template__$1s,
    __vue_module_identifier__$1s,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1r = {
  name: 'paragraphQuadrangleLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1r = script$1r;
/* template */
var __vue_render__$1r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-quadrangle-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin1" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin2" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin3" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin4" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin5" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin6" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin7" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-lin8" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1r = [];
__vue_render__$1r._withStripped = true;

  /* style */
  const __vue_inject_styles__$1r = undefined;
  /* scoped */
  const __vue_scope_id__$1r = "data-v-162956d1";
  /* module identifier */
  const __vue_module_identifier__$1r = undefined;
  /* functional template */
  const __vue_is_functional_template__$1r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1r, staticRenderFns: __vue_staticRenderFns__$1r },
    __vue_inject_styles__$1r,
    __vue_script__$1r,
    __vue_scope_id__$1r,
    __vue_is_functional_template__$1r,
    __vue_module_identifier__$1r,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1q = {
  name: 'paragraphIsoscelesLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1q = script$1q;
/* template */
var __vue_render__$1q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-isosceles-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "circle" }),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "shape" }, [
            _c("div", { staticClass: "triangle" }),
            _vm._v(" "),
            _c("div", { staticClass: "triangle2" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1q = [];
__vue_render__$1q._withStripped = true;

  /* style */
  const __vue_inject_styles__$1q = undefined;
  /* scoped */
  const __vue_scope_id__$1q = "data-v-054d3a2c";
  /* module identifier */
  const __vue_module_identifier__$1q = undefined;
  /* functional template */
  const __vue_is_functional_template__$1q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1q, staticRenderFns: __vue_staticRenderFns__$1q },
    __vue_inject_styles__$1q,
    __vue_script__$1q,
    __vue_scope_id__$1q,
    __vue_is_functional_template__$1q,
    __vue_module_identifier__$1q,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1p = {
  name: 'paragraphNinthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。

      bodyTextDataName2: 'bodyTextData',
      titleTextDataName2: 'titleTextData2',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1p = script$1p;
/* template */
var __vue_render__$1p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-ninth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                      "block-data-name": _vm.bodyTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle2" }),
                ],
                1
              ),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                  "block-data-name": _vm.titleTextDataName2,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1p = [];
__vue_render__$1p._withStripped = true;

  /* style */
  const __vue_inject_styles__$1p = undefined;
  /* scoped */
  const __vue_scope_id__$1p = "data-v-33af8fd8";
  /* module identifier */
  const __vue_module_identifier__$1p = undefined;
  /* functional template */
  const __vue_is_functional_template__$1p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1p, staticRenderFns: __vue_staticRenderFns__$1p },
    __vue_inject_styles__$1p,
    __vue_script__$1p,
    __vue_scope_id__$1p,
    __vue_is_functional_template__$1p,
    __vue_module_identifier__$1p,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1o = {
  name: 'ParagraphEighthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1o = script$1o;
/* template */
var __vue_render__$1o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-eighth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-row-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "text-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                ],
                1
              ),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1o = [];
__vue_render__$1o._withStripped = true;

  /* style */
  const __vue_inject_styles__$1o = undefined;
  /* scoped */
  const __vue_scope_id__$1o = "data-v-eb53fdd2";
  /* module identifier */
  const __vue_module_identifier__$1o = undefined;
  /* functional template */
  const __vue_is_functional_template__$1o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1o, staticRenderFns: __vue_staticRenderFns__$1o },
    __vue_inject_styles__$1o,
    __vue_script__$1o,
    __vue_scope_id__$1o,
    __vue_is_functional_template__$1o,
    __vue_module_identifier__$1o,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1n = {
  name: 'paragraphTenthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1n = script$1n;
/* template */
var __vue_render__$1n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-tenth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-suptitle-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.numberTextDataName],
                  "block-data-name": _vm.numberTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container-left",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                    "block-data-name": _vm.titleTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1n = [];
__vue_render__$1n._withStripped = true;

  /* style */
  const __vue_inject_styles__$1n = undefined;
  /* scoped */
  const __vue_scope_id__$1n = "data-v-ce523202";
  /* module identifier */
  const __vue_module_identifier__$1n = undefined;
  /* functional template */
  const __vue_is_functional_template__$1n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1n, staticRenderFns: __vue_staticRenderFns__$1n },
    __vue_inject_styles__$1n,
    __vue_script__$1n,
    __vue_scope_id__$1n,
    __vue_is_functional_template__$1n,
    __vue_module_identifier__$1n,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1m = {
  name: 'paragraphSixteenLibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1m = script$1m;
/* template */
var __vue_render__$1m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-sixteen-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                    "block-data-name": _vm.titleTextDataName3,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                    "block-data-name": _vm.bodyTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1m = [];
__vue_render__$1m._withStripped = true;

  /* style */
  const __vue_inject_styles__$1m = undefined;
  /* scoped */
  const __vue_scope_id__$1m = "data-v-7edcdee1";
  /* module identifier */
  const __vue_module_identifier__$1m = undefined;
  /* functional template */
  const __vue_is_functional_template__$1m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1m, staticRenderFns: __vue_staticRenderFns__$1m },
    __vue_inject_styles__$1m,
    __vue_script__$1m,
    __vue_scope_id__$1m,
    __vue_is_functional_template__$1m,
    __vue_module_identifier__$1m,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1l = {
  name: 'paragraphSeventhLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      numberTextDataName: 'numberTextData',
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      titleTextDataName: 'titleTextData',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$1l = script$1l;
/* template */
var __vue_render__$1l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-seventh-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "paragraph-suptitle-box" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.numberTextDataName],
                  "block-data-name": _vm.numberTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container-left",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "title-right-img" }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "paragraph-row-box" }, [
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                    "block-data-name": _vm.titleTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "paragraph-subtitle-box" },
              [
                _c("div", { staticClass: "subtitle-left-dec" }),
                _vm._v(" "),
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                    "block-data-name": _vm.titleTextDataName3,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-body-box" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                    "block-data-name": _vm.bodyTextDataName2,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1l = [];
__vue_render__$1l._withStripped = true;

  /* style */
  const __vue_inject_styles__$1l = undefined;
  /* scoped */
  const __vue_scope_id__$1l = "data-v-c2dc1818";
  /* module identifier */
  const __vue_module_identifier__$1l = undefined;
  /* functional template */
  const __vue_is_functional_template__$1l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1l, staticRenderFns: __vue_staticRenderFns__$1l },
    __vue_inject_styles__$1l,
    __vue_script__$1l,
    __vue_scope_id__$1l,
    __vue_is_functional_template__$1l,
    __vue_module_identifier__$1l,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1k = {
  name: 'paragraphElevenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1k = script$1k;
/* template */
var __vue_render__$1k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-eleven-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text" }, [
            _c("div", { staticClass: "boder-top" }, [
              _c("div", { staticClass: "boder-top-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-top-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-bottom" }, [
              _c("div", { staticClass: "boder-bottom-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-bottom-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-left" }, [
              _c("div", { staticClass: "boder-left-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-left-line2" }),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "boder-right" }, [
              _c("div", { staticClass: "boder-right-line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "boder-right-line2" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "text-container" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1k = [];
__vue_render__$1k._withStripped = true;

  /* style */
  const __vue_inject_styles__$1k = undefined;
  /* scoped */
  const __vue_scope_id__$1k = "data-v-4d77fa50";
  /* module identifier */
  const __vue_module_identifier__$1k = undefined;
  /* functional template */
  const __vue_is_functional_template__$1k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1k, staticRenderFns: __vue_staticRenderFns__$1k },
    __vue_inject_styles__$1k,
    __vue_script__$1k,
    __vue_scope_id__$1k,
    __vue_is_functional_template__$1k,
    __vue_module_identifier__$1k,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1j = {
  name: 'paragraphThirteenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1j = script$1j;
/* template */
var __vue_render__$1j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-thirteen-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "text-border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-left" }),
              _vm._v(" "),
              _c("div", { staticClass: "text-border-right" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1j = [];
__vue_render__$1j._withStripped = true;

  /* style */
  const __vue_inject_styles__$1j = undefined;
  /* scoped */
  const __vue_scope_id__$1j = "data-v-25089d37";
  /* module identifier */
  const __vue_module_identifier__$1j = undefined;
  /* functional template */
  const __vue_is_functional_template__$1j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1j, staticRenderFns: __vue_staticRenderFns__$1j },
    __vue_inject_styles__$1j,
    __vue_script__$1j,
    __vue_scope_id__$1j,
    __vue_is_functional_template__$1j,
    __vue_module_identifier__$1j,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1i = {
  name: 'paragraphFourteenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1i = script$1i;
/* template */
var __vue_render__$1i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-fourteen-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "right-image" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1i = [];
__vue_render__$1i._withStripped = true;

  /* style */
  const __vue_inject_styles__$1i = undefined;
  /* scoped */
  const __vue_scope_id__$1i = "data-v-227a1a94";
  /* module identifier */
  const __vue_module_identifier__$1i = undefined;
  /* functional template */
  const __vue_is_functional_template__$1i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1i, staticRenderFns: __vue_staticRenderFns__$1i },
    __vue_inject_styles__$1i,
    __vue_script__$1i,
    __vue_scope_id__$1i,
    __vue_is_functional_template__$1i,
    __vue_module_identifier__$1i,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1h = {
  name: 'paragraphGreentheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1h = script$1h;
/* template */
var __vue_render__$1h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1h = [];
__vue_render__$1h._withStripped = true;

  /* style */
  const __vue_inject_styles__$1h = undefined;
  /* scoped */
  const __vue_scope_id__$1h = "data-v-93f5aae8";
  /* module identifier */
  const __vue_module_identifier__$1h = undefined;
  /* functional template */
  const __vue_is_functional_template__$1h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1h, staticRenderFns: __vue_staticRenderFns__$1h },
    __vue_inject_styles__$1h,
    __vue_script__$1h,
    __vue_scope_id__$1h,
    __vue_is_functional_template__$1h,
    __vue_module_identifier__$1h,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1g = {
  name: 'paragraphGreentheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1g = script$1g;
/* template */
var __vue_render__$1g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1g = [];
__vue_render__$1g._withStripped = true;

  /* style */
  const __vue_inject_styles__$1g = undefined;
  /* scoped */
  const __vue_scope_id__$1g = "data-v-3a2410ee";
  /* module identifier */
  const __vue_module_identifier__$1g = undefined;
  /* functional template */
  const __vue_is_functional_template__$1g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1g, staticRenderFns: __vue_staticRenderFns__$1g },
    __vue_inject_styles__$1g,
    __vue_script__$1g,
    __vue_scope_id__$1g,
    __vue_is_functional_template__$1g,
    __vue_module_identifier__$1g,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1f = {
  name: 'paragraphGreentheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1f = script$1f;
/* template */
var __vue_render__$1f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "body-text-bg1" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg2" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1f = [];
__vue_render__$1f._withStripped = true;

  /* style */
  const __vue_inject_styles__$1f = undefined;
  /* scoped */
  const __vue_scope_id__$1f = "data-v-357d09c8";
  /* module identifier */
  const __vue_module_identifier__$1f = undefined;
  /* functional template */
  const __vue_is_functional_template__$1f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1f, staticRenderFns: __vue_staticRenderFns__$1f },
    __vue_inject_styles__$1f,
    __vue_script__$1f,
    __vue_scope_id__$1f,
    __vue_is_functional_template__$1f,
    __vue_module_identifier__$1f,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1e = {
  name: 'paragraphGreentheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1e = script$1e;
/* template */
var __vue_render__$1e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Greentheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "body-text-bg1" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "border-top" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-bottom" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-left" }),
              _vm._v(" "),
              _c("div", { staticClass: "border-right" }),
              _vm._v(" "),
              _c("div", { staticClass: "body-text-bg2" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1e = [];
__vue_render__$1e._withStripped = true;

  /* style */
  const __vue_inject_styles__$1e = undefined;
  /* scoped */
  const __vue_scope_id__$1e = "data-v-31549304";
  /* module identifier */
  const __vue_module_identifier__$1e = undefined;
  /* functional template */
  const __vue_is_functional_template__$1e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1e, staticRenderFns: __vue_staticRenderFns__$1e },
    __vue_inject_styles__$1e,
    __vue_script__$1e,
    __vue_scope_id__$1e,
    __vue_is_functional_template__$1e,
    __vue_module_identifier__$1e,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1d = {
  name: 'paragraphYellowtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1d = script$1d;
/* template */
var __vue_render__$1d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "border-top" }, [
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "border-bottom" }, [
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1d = [];
__vue_render__$1d._withStripped = true;

  /* style */
  const __vue_inject_styles__$1d = undefined;
  /* scoped */
  const __vue_scope_id__$1d = "data-v-5f5b0577";
  /* module identifier */
  const __vue_module_identifier__$1d = undefined;
  /* functional template */
  const __vue_is_functional_template__$1d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1d, staticRenderFns: __vue_staticRenderFns__$1d },
    __vue_inject_styles__$1d,
    __vue_script__$1d,
    __vue_scope_id__$1d,
    __vue_is_functional_template__$1d,
    __vue_module_identifier__$1d,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1c = {
  name: 'paragraphYellowtheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1c = script$1c;
/* template */
var __vue_render__$1c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "block1" }),
            _vm._v(" "),
            _c("div", { staticClass: "block2" }),
            _vm._v(" "),
            _c("div", { staticClass: "block3" }),
            _vm._v(" "),
            _c("div", { staticClass: "block4" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1c = [];
__vue_render__$1c._withStripped = true;

  /* style */
  const __vue_inject_styles__$1c = undefined;
  /* scoped */
  const __vue_scope_id__$1c = "data-v-767df838";
  /* module identifier */
  const __vue_module_identifier__$1c = undefined;
  /* functional template */
  const __vue_is_functional_template__$1c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1c, staticRenderFns: __vue_staticRenderFns__$1c },
    __vue_inject_styles__$1c,
    __vue_script__$1c,
    __vue_scope_id__$1c,
    __vue_is_functional_template__$1c,
    __vue_module_identifier__$1c,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1b = {
  name: 'paragraphYellowtheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1b = script$1b;
/* template */
var __vue_render__$1b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "right-img" }, [_c("div")]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1b = [];
__vue_render__$1b._withStripped = true;

  /* style */
  const __vue_inject_styles__$1b = undefined;
  /* scoped */
  const __vue_scope_id__$1b = "data-v-620ef54d";
  /* module identifier */
  const __vue_module_identifier__$1b = undefined;
  /* functional template */
  const __vue_is_functional_template__$1b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1b, staticRenderFns: __vue_staticRenderFns__$1b },
    __vue_inject_styles__$1b,
    __vue_script__$1b,
    __vue_scope_id__$1b,
    __vue_is_functional_template__$1b,
    __vue_module_identifier__$1b,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$1a = {
  name: 'paragraphYellowtheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$1a = script$1a;
/* template */
var __vue_render__$1a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "block1" }),
            _vm._v(" "),
            _c("div", { staticClass: "block2" }),
            _vm._v(" "),
            _c("div", { staticClass: "block3" }),
            _vm._v(" "),
            _c("div", { staticClass: "block4" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1a = [];
__vue_render__$1a._withStripped = true;

  /* style */
  const __vue_inject_styles__$1a = undefined;
  /* scoped */
  const __vue_scope_id__$1a = "data-v-4e0c8826";
  /* module identifier */
  const __vue_module_identifier__$1a = undefined;
  /* functional template */
  const __vue_is_functional_template__$1a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1a, staticRenderFns: __vue_staticRenderFns__$1a },
    __vue_inject_styles__$1a,
    __vue_script__$1a,
    __vue_scope_id__$1a,
    __vue_is_functional_template__$1a,
    __vue_module_identifier__$1a,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$19 = {
  name: 'paragraphYellowtheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$19 = script$19;
/* template */
var __vue_render__$19 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Yellowtheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "left-img" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-img" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$19 = [];
__vue_render__$19._withStripped = true;

  /* style */
  const __vue_inject_styles__$19 = undefined;
  /* scoped */
  const __vue_scope_id__$19 = "data-v-38ae234a";
  /* module identifier */
  const __vue_module_identifier__$19 = undefined;
  /* functional template */
  const __vue_is_functional_template__$19 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$19 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$19, staticRenderFns: __vue_staticRenderFns__$19 },
    __vue_inject_styles__$19,
    __vue_script__$19,
    __vue_scope_id__$19,
    __vue_is_functional_template__$19,
    __vue_module_identifier__$19,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$18 = {
  name: 'paragraphRedtheme1LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$18 = script$18;
/* template */
var __vue_render__$18 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$18 = [];
__vue_render__$18._withStripped = true;

  /* style */
  const __vue_inject_styles__$18 = undefined;
  /* scoped */
  const __vue_scope_id__$18 = "data-v-3819bb3e";
  /* module identifier */
  const __vue_module_identifier__$18 = undefined;
  /* functional template */
  const __vue_is_functional_template__$18 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$18 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$18, staticRenderFns: __vue_staticRenderFns__$18 },
    __vue_inject_styles__$18,
    __vue_script__$18,
    __vue_scope_id__$18,
    __vue_is_functional_template__$18,
    __vue_module_identifier__$18,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$17 = {
  name: 'paragraphRedtheme2LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$17 = script$17;
/* template */
var __vue_render__$17 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "left-line" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "right-line" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$17 = [];
__vue_render__$17._withStripped = true;

  /* style */
  const __vue_inject_styles__$17 = undefined;
  /* scoped */
  const __vue_scope_id__$17 = "data-v-4a6e607a";
  /* module identifier */
  const __vue_module_identifier__$17 = undefined;
  /* functional template */
  const __vue_is_functional_template__$17 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$17 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$17, staticRenderFns: __vue_staticRenderFns__$17 },
    __vue_inject_styles__$17,
    __vue_script__$17,
    __vue_scope_id__$17,
    __vue_is_functional_template__$17,
    __vue_module_identifier__$17,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$16 = {
  name: 'paragraphRedtheme3LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$16 = script$16;
/* template */
var __vue_render__$16 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-line" }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "bottom-line" }),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$16 = [];
__vue_render__$16._withStripped = true;

  /* style */
  const __vue_inject_styles__$16 = undefined;
  /* scoped */
  const __vue_scope_id__$16 = "data-v-08aa6fda";
  /* module identifier */
  const __vue_module_identifier__$16 = undefined;
  /* functional template */
  const __vue_is_functional_template__$16 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$16 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$16, staticRenderFns: __vue_staticRenderFns__$16 },
    __vue_inject_styles__$16,
    __vue_script__$16,
    __vue_scope_id__$16,
    __vue_is_functional_template__$16,
    __vue_module_identifier__$16,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$15 = {
  name: 'paragraphRedtheme4LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$15 = script$15;
/* template */
var __vue_render__$15 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-title" }, [
            _c("div", { staticClass: "body-title-box" }, [
              _c(
                "div",
                { staticClass: "body-title-con" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle2" }),
                ],
                1
              ),
            ]),
          ]),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$15 = [];
__vue_render__$15._withStripped = true;

  /* style */
  const __vue_inject_styles__$15 = undefined;
  /* scoped */
  const __vue_scope_id__$15 = "data-v-c5af4d62";
  /* module identifier */
  const __vue_module_identifier__$15 = undefined;
  /* functional template */
  const __vue_is_functional_template__$15 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$15 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$15, staticRenderFns: __vue_staticRenderFns__$15 },
    __vue_inject_styles__$15,
    __vue_script__$15,
    __vue_scope_id__$15,
    __vue_is_functional_template__$15,
    __vue_module_identifier__$15,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$14 = {
  name: 'paragraphRedtheme5LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$14 = script$14;
/* template */
var __vue_render__$14 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-box" }, [
            _c(
              "div",
              { staticClass: "body-text-bg" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$14 = [];
__vue_render__$14._withStripped = true;

  /* style */
  const __vue_inject_styles__$14 = undefined;
  /* scoped */
  const __vue_scope_id__$14 = "data-v-e3ad24ec";
  /* module identifier */
  const __vue_module_identifier__$14 = undefined;
  /* functional template */
  const __vue_is_functional_template__$14 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$14 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$14, staticRenderFns: __vue_staticRenderFns__$14 },
    __vue_inject_styles__$14,
    __vue_script__$14,
    __vue_scope_id__$14,
    __vue_is_functional_template__$14,
    __vue_module_identifier__$14,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$13 = {
  name: 'paragraphRedtheme6LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$13 = script$13;
/* template */
var __vue_render__$13 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme6-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-title" }, [
            _c("div", { staticClass: "body-title-box" }, [
              _c(
                "div",
                { staticClass: "body-title-con" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle2" }),
                ],
                1
              ),
            ]),
          ]),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$13 = [];
__vue_render__$13._withStripped = true;

  /* style */
  const __vue_inject_styles__$13 = undefined;
  /* scoped */
  const __vue_scope_id__$13 = "data-v-a4ef1d66";
  /* module identifier */
  const __vue_module_identifier__$13 = undefined;
  /* functional template */
  const __vue_is_functional_template__$13 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$13 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$13, staticRenderFns: __vue_staticRenderFns__$13 },
    __vue_inject_styles__$13,
    __vue_script__$13,
    __vue_scope_id__$13,
    __vue_is_functional_template__$13,
    __vue_module_identifier__$13,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$12 = {
  name: 'paragraphRedtheme7LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$12 = script$12;
/* template */
var __vue_render__$12 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Redtheme7-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-title" }, [
            _c("div", { staticClass: "body-title-box" }, [
              _c(
                "div",
                { staticClass: "body-title-con" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle2" }),
                ],
                1
              ),
            ]),
          ]),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$12 = [];
__vue_render__$12._withStripped = true;

  /* style */
  const __vue_inject_styles__$12 = undefined;
  /* scoped */
  const __vue_scope_id__$12 = "data-v-35b87d4c";
  /* module identifier */
  const __vue_module_identifier__$12 = undefined;
  /* functional template */
  const __vue_is_functional_template__$12 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$12 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$12, staticRenderFns: __vue_staticRenderFns__$12 },
    __vue_inject_styles__$12,
    __vue_script__$12,
    __vue_scope_id__$12,
    __vue_is_functional_template__$12,
    __vue_module_identifier__$12,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$11 = {
  name: 'paragraphFoldtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$11 = script$11;
/* template */
var __vue_render__$11 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Foldtheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-style" }),
            _vm._v(" "),
            _c(
              "div",
              {
                staticClass: "body-text",
                class: !_vm.isFold ? "body-text-fold" : "",
              },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "seemore", on: { click: _vm.changepanel } },
              [
                _c("span", [_vm._v(_vm._s(!_vm.isFold ? "查看更多" : "折叠"))]),
                _vm._v(" "),
                !_vm.isFold
                  ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                  : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
              ]
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$11 = [];
__vue_render__$11._withStripped = true;

  /* style */
  const __vue_inject_styles__$11 = undefined;
  /* scoped */
  const __vue_scope_id__$11 = "data-v-4d640410";
  /* module identifier */
  const __vue_module_identifier__$11 = undefined;
  /* functional template */
  const __vue_is_functional_template__$11 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$11 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$11, staticRenderFns: __vue_staticRenderFns__$11 },
    __vue_inject_styles__$11,
    __vue_script__$11,
    __vue_scope_id__$11,
    __vue_is_functional_template__$11,
    __vue_module_identifier__$11,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$10 = {
  name: 'paragraphFoldtheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$10 = script$10;
/* template */
var __vue_render__$10 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Foldtheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-title-style" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [_vm._v(_vm._s(!_vm.isFold ? "更多" : "收起"))]),
                  _vm._v(" "),
                  !_vm.isFold
                    ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                    : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
                ]
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              {
                staticClass: "body-text",
                class: !_vm.isFold ? "body-text-fold" : "",
              },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$10 = [];
__vue_render__$10._withStripped = true;

  /* style */
  const __vue_inject_styles__$10 = undefined;
  /* scoped */
  const __vue_scope_id__$10 = "data-v-23f9ec98";
  /* module identifier */
  const __vue_module_identifier__$10 = undefined;
  /* functional template */
  const __vue_is_functional_template__$10 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$10 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$10, staticRenderFns: __vue_staticRenderFns__$10 },
    __vue_inject_styles__$10,
    __vue_script__$10,
    __vue_scope_id__$10,
    __vue_is_functional_template__$10,
    __vue_module_identifier__$10,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$$ = {
  name: 'paragraphFoldtheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$$ = script$$;
/* template */
var __vue_render__$$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Foldtheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-title-style" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [_vm._v(_vm._s(!_vm.isFold ? "更多" : "收起"))]),
                  _vm._v(" "),
                  !_vm.isFold
                    ? _c("i", { staticClass: "el-icon-d-arrow-left down" })
                    : _c("i", { staticClass: "el-icon-d-arrow-left up" }),
                ]
              ),
            ]),
            _vm._v(" "),
            _c(
              "div",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.isFold,
                    expression: "isFold",
                  },
                ],
                staticClass: "body-text",
                class: !_vm.isFold ? "body-text-fold" : "",
              },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$$ = [];
__vue_render__$$._withStripped = true;

  /* style */
  const __vue_inject_styles__$$ = undefined;
  /* scoped */
  const __vue_scope_id__$$ = "data-v-6f30c078";
  /* module identifier */
  const __vue_module_identifier__$$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$$ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$$, staticRenderFns: __vue_staticRenderFns__$$ },
    __vue_inject_styles__$$,
    __vue_script__$$,
    __vue_scope_id__$$,
    __vue_is_functional_template__$$,
    __vue_module_identifier__$$,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$_ = {
  name: 'paragraphTabstheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      bodyTextDataName4: 'bodyTextData4',
      titleTextDataName1: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      titleTextDataName4: 'titleTextData4',
      groupData: {},
      tabslist: [],
      chooseindex: 0
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    clicktabs(e) {
      this.chooseindex = e;
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    },
    addtabs() {
      console.log(this.groupData);
      // {html: "<p style='font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #fff;'>这是一个标题</p>"}
      this.tabslist.push({
        html: "<p style='font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;'>选项一</p>"
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$_ = script$_;
/* template */
var __vue_render__$_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 0,
                      expression: "chooseindex==0",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 1,
                      expression: "chooseindex==1",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                      "block-data-name": _vm.bodyTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 2,
                      expression: "chooseindex==2",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName3],
                      "block-data-name": _vm.bodyTextDataName3,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 3,
                      expression: "chooseindex==3",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName4],
                      "block-data-name": _vm.bodyTextDataName4,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "top-style" }, [
              _c(
                "div",
                {
                  class: _vm.chooseindex == 0 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(0)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName1],
                      "block-data-name": _vm.titleTextDataName1,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 1 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(1)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                      "block-data-name": _vm.titleTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 2 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(2)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                      "block-data-name": _vm.titleTextDataName3,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 3 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(3)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName4],
                      "block-data-name": _vm.titleTextDataName4,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$_ = [];
__vue_render__$_._withStripped = true;

  /* style */
  const __vue_inject_styles__$_ = undefined;
  /* scoped */
  const __vue_scope_id__$_ = "data-v-28378f0e";
  /* module identifier */
  const __vue_module_identifier__$_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$_ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$_, staticRenderFns: __vue_staticRenderFns__$_ },
    __vue_inject_styles__$_,
    __vue_script__$_,
    __vue_scope_id__$_,
    __vue_is_functional_template__$_,
    __vue_module_identifier__$_,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$Z = {
  name: 'paragraphTabstheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      bodyTextDataName2: 'bodyTextData2',
      bodyTextDataName3: 'bodyTextData3',
      bodyTextDataName4: 'bodyTextData4',
      titleTextDataName: 'titleTextData',
      titleTextDataName1: 'titleTextData1',
      titleTextDataName2: 'titleTextData2',
      titleTextDataName3: 'titleTextData3',
      titleTextDataName4: 'titleTextData4',
      groupData: {},
      tabslist: [],
      chooseindex: 0
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    clicktabs(e) {
      this.chooseindex = e;
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    },
    addtabs() {
      console.log(this.groupData);
      // {html: "<p style='font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #fff;'>这是一个标题</p>"}
      this.tabslist.push({
        html: "<p style='font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;'>选项一</p>"
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$Z = script$Z;
/* template */
var __vue_render__$Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "suptitle" },
            [
              _c("div", { staticClass: "block1" }),
              _vm._v(" "),
              _c("div", { staticClass: "block2" }),
              _vm._v(" "),
              _c("div", { staticClass: "block3" }),
              _vm._v(" "),
              _c("div", { staticClass: "block4" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName],
                  "block-data-name": _vm.titleTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-style" }, [
              _c(
                "div",
                {
                  class: _vm.chooseindex == 0 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(0)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName1],
                      "block-data-name": _vm.titleTextDataName1,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 1 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(1)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName2],
                      "block-data-name": _vm.titleTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 2 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(2)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName3],
                      "block-data-name": _vm.titleTextDataName3,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  class: _vm.chooseindex == 3 ? "active" : "",
                  on: {
                    click: function ($event) {
                      return _vm.clicktabs(3)
                    },
                  },
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName4],
                      "block-data-name": _vm.titleTextDataName4,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 0,
                      expression: "chooseindex==0",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 1,
                      expression: "chooseindex==1",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName2],
                      "block-data-name": _vm.bodyTextDataName2,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 2,
                      expression: "chooseindex==2",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName3],
                      "block-data-name": _vm.bodyTextDataName3,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.chooseindex == 3,
                      expression: "chooseindex==3",
                    },
                  ],
                },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName4],
                      "block-data-name": _vm.bodyTextDataName4,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$Z = [];
__vue_render__$Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$Z = undefined;
  /* scoped */
  const __vue_scope_id__$Z = "data-v-b85e9146";
  /* module identifier */
  const __vue_module_identifier__$Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$Z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Z, staticRenderFns: __vue_staticRenderFns__$Z },
    __vue_inject_styles__$Z,
    __vue_script__$Z,
    __vue_scope_id__$Z,
    __vue_is_functional_template__$Z,
    __vue_module_identifier__$Z,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$Y = {
  name: 'paragraphTabstheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      titleTextDataName1: 'titleTextData1',
      groupData: {},
      tabslist: [],
      chooseindex: 0,
      timer: ''
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  watch: {
    pGroupData: {
      handler(newValue, oldValue) {
        this.$set(this.groupData, 'titleTextData', newValue.titleTextData);
        this.$set(this.groupData, 'bodyTextData', newValue.bodyTextData);
        this.handleLoad();
      },
      deep: true
    }
  },
  methods: {
    handleLoad() {
      this.timer = new Date().getTime();
    },
    clicktabs(e) {
      this.chooseindex = e;
    },
    addtabs() {
      // this.groupData.titleTextData.push({
      //       html: '<p style="font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;">选项一</p>',
      // })
      // this.groupData.bodyTextData.push({
      //     html: '<p style="font-size: 16px;font-family: Microsoft YaHei;font-weight: 400;color: #333333;">夏天的风是蓝色的温柔。我时常仰望夏日天空，蓝得清澈。夏日的风，漫过无边无垠的往事，送来对未来的美好希望。</p>',
      // })
      this.$emit('addtabs', {
        ruleIndex: this.ruleIndex
      });
    },
    deltabs(index) {
      this.$emit('deltabs', {
        ruleIndex: this.ruleIndex,
        dataIndex: index
      });
      if (this.groupData.titleTextData.length == 0) {
        this.chooseindex = 0;
      } else {
        this.chooseindex = this.groupData.titleTextData.length - 1;
      }
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data,
      dataIndex
    }) {
      console.log(ruleIndex, dataName, data);
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data,
        dataIndex
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

var __$_require_assets_images_closeicon_png__ = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAAAXNSR0IArs4c6QAAAbBJREFUOE99ks1qWkEUx//H61cWogvBRRaxCuqmbu4NClkJyaLrpts8QLtwH2mhD1Dool30CQLtE7RQhBDczBhfQMVFQUqSi+aKetF7T5mhN4yh6VnN+c/5zfmYQ3hkUspEGIaHlmU9Z+YUEf1i5kvHcW7NUDIdIcRLIvoA4GAniMhn5i+e53VardZC3T2AUsr3AN49rsD0U6kUlstlvtFo3GlQCHFKRN/UOR6PY7vd7vCxWEz7YRiCiH7atn1M3W43nslkJgD2FVSv1zEejzGbzXSw0qrVKlzXxXQ61RoRvaDBYHAUBMFVlCKbzaJcLmvY8zwNrddr7Rv2lvr9/htm/mSqCi4WiwiCAIvFApOJKmjHzklK2Qbw0ZQty0KtVkM6ncZwOMR8Pv8n+ArA1+hG9VSpVLBarXSfKrMq04SZuaMy5gH8BhBT01OZfN/HaDTSb+VyOZRKJe0bcFt/h5TyM4DXCiwUCg/TMwemzn/Bm0Qi8UyDvV5vL5lMXgOo/W8BALD6Ctu2v5ubk2fmCyI6fgJW7Zw5jvNjZ+WiYCHECRE5JkxE7mazuWg2m/eR/gfAL7CDWnQ6sgAAAABJRU5ErkJggg==";

var __$_require_assets_images_addtabicon_png__ = "data:image/png;base64,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";

/* script */
const __vue_script__$Y = script$Y;
var __vue_render__$Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "suptitle" },
            [
              _c("div", { staticClass: "block1" }),
              _vm._v(" "),
              _c("div", { staticClass: "block2" }),
              _vm._v(" "),
              _c("div", { staticClass: "block3" }),
              _vm._v(" "),
              _c("div", { staticClass: "block4" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName1],
                  "block-data-name": _vm.titleTextDataName1,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "top-style" },
              [
                _vm._l(_vm.groupData.titleTextData, function (item, k) {
                  return _c(
                    "div",
                    {
                      key: k,
                      class: _vm.chooseindex == k ? "active" : "",
                      on: {
                        click: function ($event) {
                          return _vm.clicktabs(k)
                        },
                      },
                    },
                    [
                      _c("xml-text-render", {
                        key: _vm.timer,
                        attrs: {
                          "p-block-data": item,
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "data-index": k,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _vm.mode == "editor"
                        ? _c("img", {
                            staticClass: "deltabs",
                            attrs: {
                              src: __$_require_assets_images_closeicon_png__,
                            },
                            on: {
                              click: function ($event) {
                                return _vm.deltabs(k)
                              },
                            },
                          })
                        : _vm._e(),
                    ],
                    1
                  )
                }),
                _vm._v(" "),
                _vm.mode == "editor"
                  ? _c(
                      "span",
                      { staticClass: "addtabs", on: { click: _vm.addtabs } },
                      [
                        _c("img", {
                          attrs: {
                            src: __$_require_assets_images_addtabicon_png__,
                            alt: "",
                          },
                        }),
                      ]
                    )
                  : _vm._e(),
              ],
              2
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              _vm._l(_vm.groupData.bodyTextData, function (item, i) {
                return _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.chooseindex == i,
                        expression: "chooseindex==i",
                      },
                    ],
                    key: i,
                  },
                  [
                    _c("xml-text-render", {
                      key: _vm.timer,
                      attrs: {
                        "p-block-data": item,
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "data-index": i,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                )
              }),
              0
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$Y = [];
__vue_render__$Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$Y = undefined;
  /* scoped */
  const __vue_scope_id__$Y = "data-v-221179d4";
  /* module identifier */
  const __vue_module_identifier__$Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Y, staticRenderFns: __vue_staticRenderFns__$Y },
    __vue_inject_styles__$Y,
    __vue_script__$Y,
    __vue_scope_id__$Y,
    __vue_is_functional_template__$Y,
    __vue_module_identifier__$Y,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$X = {
  name: 'paragraphTabstheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      titleTextDataName1: 'titleTextData1',
      groupData: {},
      tabslist: [],
      chooseindex: 0,
      timer: ''
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  watch: {
    pGroupData: {
      handler(newValue, oldValue) {
        this.$set(this.groupData, 'titleTextData', newValue.titleTextData);
        this.$set(this.groupData, 'bodyTextData', newValue.bodyTextData);
        this.handleLoad();
      },
      deep: true
    }
  },
  methods: {
    handleLoad() {
      this.timer = new Date().getTime();
    },
    clicktabs(e) {
      this.chooseindex = e;
    },
    addtabs() {
      // this.groupData.titleTextData.push({
      //       html: '<p style="font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;">选项一</p>',
      // })
      // this.groupData.bodyTextData.push({
      //     html: '<p style="font-size: 16px;font-family: Microsoft YaHei;font-weight: 400;color: #333333;">夏天的风是蓝色的温柔。我时常仰望夏日天空，蓝得清澈。夏日的风，漫过无边无垠的往事，送来对未来的美好希望。</p>',
      // })
      this.$emit('addtabs', {
        ruleIndex: this.ruleIndex
      });
    },
    deltabs(index) {
      this.$emit('deltabs', {
        ruleIndex: this.ruleIndex,
        dataIndex: index
      });
      if (this.groupData.titleTextData.length == 0) {
        this.chooseindex = 0;
      } else {
        this.chooseindex = this.groupData.titleTextData.length - 1;
      }
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data,
      dataIndex
    }) {
      console.log(ruleIndex, dataName, data);
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data,
        dataIndex
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$X = script$X;
var __vue_render__$X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              _vm._l(_vm.groupData.bodyTextData, function (item, i) {
                return _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.chooseindex == i,
                        expression: "chooseindex==i",
                      },
                    ],
                    key: i,
                  },
                  [
                    _c("xml-text-render", {
                      key: _vm.timer,
                      attrs: {
                        "p-block-data": item,
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "data-index": i,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                )
              }),
              0
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "top-style" },
              [
                _vm._l(_vm.groupData.titleTextData, function (item, k) {
                  return _c(
                    "div",
                    {
                      key: k,
                      class: _vm.chooseindex == k ? "active" : "",
                      on: {
                        click: function ($event) {
                          return _vm.clicktabs(k)
                        },
                      },
                    },
                    [
                      _c("xml-text-render", {
                        key: _vm.timer,
                        attrs: {
                          "p-block-data": item,
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "data-index": k,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _vm.mode == "editor"
                        ? _c("img", {
                            staticClass: "deltabs",
                            attrs: {
                              src: __$_require_assets_images_closeicon_png__,
                            },
                            on: {
                              click: function ($event) {
                                return _vm.deltabs(k)
                              },
                            },
                          })
                        : _vm._e(),
                    ],
                    1
                  )
                }),
                _vm._v(" "),
                _vm.mode == "editor"
                  ? _c(
                      "span",
                      { staticClass: "addtabs", on: { click: _vm.addtabs } },
                      [
                        _c("img", {
                          attrs: {
                            src: __$_require_assets_images_addtabicon_png__,
                            alt: "",
                          },
                        }),
                      ]
                    )
                  : _vm._e(),
              ],
              2
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$X = [];
__vue_render__$X._withStripped = true;

  /* style */
  const __vue_inject_styles__$X = undefined;
  /* scoped */
  const __vue_scope_id__$X = "data-v-f9dec122";
  /* module identifier */
  const __vue_module_identifier__$X = undefined;
  /* functional template */
  const __vue_is_functional_template__$X = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$X, staticRenderFns: __vue_staticRenderFns__$X },
    __vue_inject_styles__$X,
    __vue_script__$X,
    __vue_scope_id__$X,
    __vue_is_functional_template__$X,
    __vue_module_identifier__$X,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$W = {
  name: 'paragraphSanQintheme1LibraryRender',
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$W = script$W;
/* template */
var __vue_render__$W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-SanQintheme1-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
                _vm._v(" "),
                _c("div", { staticClass: "left-top-image" }, [
                  _c("div", { staticClass: "left-bottom-block1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "left-bottom-block2" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "left-bottom-image" }, [
                  _c("div", { staticClass: "left-bottom-block1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "left-bottom-block2" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-top-image" }, [
                  _c("div", { staticClass: "left-bottom-block1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "left-bottom-block2" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-bottom-image" }, [
                  _c("div", { staticClass: "left-bottom-block1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "left-bottom-block2" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "top-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "bottom-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "left-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "right-line" }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$W = [];
__vue_render__$W._withStripped = true;

  /* style */
  const __vue_inject_styles__$W = undefined;
  /* scoped */
  const __vue_scope_id__$W = "data-v-7706565a";
  /* module identifier */
  const __vue_module_identifier__$W = undefined;
  /* functional template */
  const __vue_is_functional_template__$W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$W, staticRenderFns: __vue_staticRenderFns__$W },
    __vue_inject_styles__$W,
    __vue_script__$W,
    __vue_scope_id__$W,
    __vue_is_functional_template__$W,
    __vue_module_identifier__$W,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$V = {
  mixins: [singleGroupMixin],
  name: 'paragraphTwelveLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$V = script$V;
/* template */
var __vue_render__$V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-twelve-library-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "left-top-img" }),
              _vm._v(" "),
              _c("div", { staticClass: "right-bottom-img" }),
              _vm._v(" "),
              _c("div", { staticClass: "left-top-clover" }, [
                _c(
                  "svg",
                  {
                    staticClass: "icon",
                    attrs: {
                      t: "1690358656753",
                      viewBox: "0 0 1024 1024",
                      version: "1.1",
                      xmlns: "http://www.w3.org/2000/svg",
                      "p-id": "8155",
                      width: "18",
                      height: "18",
                    },
                  },
                  [
                    _c("path", {
                      attrs: {
                        d: "M950.6 515.4c0.4-6 1.4-11.2 2.5-17.1 0 0 19.8-47.1-11.3-80.4-31.1-33.3-43.1-59.4-114.1-65-70.9-5.6-114-3-155 28.9-35.6 27.6-88.9 85.2-102.5 100.1-5-14.8-16.2-26.7-30.3-32.8 12.4-10.1 76.7-62.7 107.5-97.9 34.2-39.1 39.2-82 37.7-153.1s-26.9-84.6-58.3-117.6-79.6-15.9-79.6-15.9c-6.3 0.9-12 1.6-18.6 1.6v0.1c-6-0.4-11.3-1.3-17.1-2.3 0 0-47.4-19.1-80.2 12.5-32.8 31.6-58.8 44-63.3 115s-1.3 114 31.2 154.6c28.3 35.3 87.2 88.3 101.9 101.3-8.5 2.8-16.1 7.5-22.3 13.7-6.4 5.6-11.4 12.7-14.7 20.8-12-15.5-60.8-77.8-94.2-108.4-38.3-35.1-81-41.2-152.1-41.5-71.1-0.3-85.3 24.8-119 55.4-33.7 30.6-17.9 79.2-17.9 79.2 0.7 5.9 1.2 11.3 1.2 17.2H82c-0.5 6.5-1.6 12.1-2.9 18.4 0 0-20.2 46.9 10.5 80.5 30.8 33.6 42.5 59.8 113.4 66.1 70.9 6.3 114 4.1 155.3-27.3 37.2-28.3 94.1-88.9 105-100.6 5.2 14.5 16.3 26.4 30.7 32.4-15.8 12.6-76.8 61.9-106.8 95.5-34.6 38.8-40 81.6-39.3 152.7 0.8 71.1 26.1 84.9 57.2 118.2s79.4 16.7 79.4 16.7c5.9-0.7 11.2-1.4 17.2-1.4v0.1c6.5 0.4 12.1 1.4 18.4 2.6 0 0 47.2 19.6 80.3-11.7 33.1-31.3 59.2-43.4 64.4-114.4 5.2-71 2.4-114-29.6-154.9-28.1-35.8-87-89.8-101.1-102.5 7.7-2.8 14.7-7.3 20.4-13.1 5.9-5.2 10.6-11.6 13.8-18.9C580 537 629.9 599.5 663.8 630c38.6 34.8 81.4 40.4 152.5 40 71.1-0.4 85-25.7 118.5-56.6C968.3 582.5 952 534 952 534c-0.8-6.3-1.4-12-1.4-18.6z",
                        fill: _vm.groupData.themeColor,
                        "p-id": "8156",
                      },
                    }),
                  ]
                ),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "right-bottom-clover" }, [
                _c(
                  "svg",
                  {
                    staticClass: "icon",
                    attrs: {
                      t: "1690358656753",
                      viewBox: "0 0 1024 1024",
                      version: "1.1",
                      xmlns: "http://www.w3.org/2000/svg",
                      "p-id": "8155",
                      width: "18",
                      height: "18",
                    },
                  },
                  [
                    _c("path", {
                      attrs: {
                        d: "M950.6 515.4c0.4-6 1.4-11.2 2.5-17.1 0 0 19.8-47.1-11.3-80.4-31.1-33.3-43.1-59.4-114.1-65-70.9-5.6-114-3-155 28.9-35.6 27.6-88.9 85.2-102.5 100.1-5-14.8-16.2-26.7-30.3-32.8 12.4-10.1 76.7-62.7 107.5-97.9 34.2-39.1 39.2-82 37.7-153.1s-26.9-84.6-58.3-117.6-79.6-15.9-79.6-15.9c-6.3 0.9-12 1.6-18.6 1.6v0.1c-6-0.4-11.3-1.3-17.1-2.3 0 0-47.4-19.1-80.2 12.5-32.8 31.6-58.8 44-63.3 115s-1.3 114 31.2 154.6c28.3 35.3 87.2 88.3 101.9 101.3-8.5 2.8-16.1 7.5-22.3 13.7-6.4 5.6-11.4 12.7-14.7 20.8-12-15.5-60.8-77.8-94.2-108.4-38.3-35.1-81-41.2-152.1-41.5-71.1-0.3-85.3 24.8-119 55.4-33.7 30.6-17.9 79.2-17.9 79.2 0.7 5.9 1.2 11.3 1.2 17.2H82c-0.5 6.5-1.6 12.1-2.9 18.4 0 0-20.2 46.9 10.5 80.5 30.8 33.6 42.5 59.8 113.4 66.1 70.9 6.3 114 4.1 155.3-27.3 37.2-28.3 94.1-88.9 105-100.6 5.2 14.5 16.3 26.4 30.7 32.4-15.8 12.6-76.8 61.9-106.8 95.5-34.6 38.8-40 81.6-39.3 152.7 0.8 71.1 26.1 84.9 57.2 118.2s79.4 16.7 79.4 16.7c5.9-0.7 11.2-1.4 17.2-1.4v0.1c6.5 0.4 12.1 1.4 18.4 2.6 0 0 47.2 19.6 80.3-11.7 33.1-31.3 59.2-43.4 64.4-114.4 5.2-71 2.4-114-29.6-154.9-28.1-35.8-87-89.8-101.1-102.5 7.7-2.8 14.7-7.3 20.4-13.1 5.9-5.2 10.6-11.6 13.8-18.9C580 537 629.9 599.5 663.8 630c38.6 34.8 81.4 40.4 152.5 40 71.1-0.4 85-25.7 118.5-56.6C968.3 582.5 952 534 952 534c-0.8-6.3-1.4-12-1.4-18.6z",
                        fill: _vm.groupData.themeColor,
                        "p-id": "8156",
                      },
                    }),
                  ]
                ),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$V = [];
__vue_render__$V._withStripped = true;

  /* style */
  const __vue_inject_styles__$V = undefined;
  /* scoped */
  const __vue_scope_id__$V = "data-v-9dd55cd6";
  /* module identifier */
  const __vue_module_identifier__$V = undefined;
  /* functional template */
  const __vue_is_functional_template__$V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$V, staticRenderFns: __vue_staticRenderFns__$V },
    __vue_inject_styles__$V,
    __vue_script__$V,
    __vue_scope_id__$V,
    __vue_is_functional_template__$V,
    __vue_module_identifier__$V,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$U = {
  name: 'paragraphFoldtheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$U = script$U;
/* template */
var __vue_render__$U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Foldtheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-title-style" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("div", { staticClass: "line" }),
                  _vm._v(" "),
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "line" }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                { class: !_vm.isFold ? "body-text-fold" : "" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [
                    _vm._v(_vm._s(!_vm.isFold ? "查看更多" : "收起")),
                  ]),
                ]
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$U = [];
__vue_render__$U._withStripped = true;

  /* style */
  const __vue_inject_styles__$U = undefined;
  /* scoped */
  const __vue_scope_id__$U = "data-v-6918d1b9";
  /* module identifier */
  const __vue_module_identifier__$U = undefined;
  /* functional template */
  const __vue_is_functional_template__$U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$U, staticRenderFns: __vue_staticRenderFns__$U },
    __vue_inject_styles__$U,
    __vue_script__$U,
    __vue_scope_id__$U,
    __vue_is_functional_template__$U,
    __vue_module_identifier__$U,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$T = {
  name: 'paragraphFoldtheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$T = script$T;
/* template */
var __vue_render__$T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Foldtheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-title-style" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                { class: !_vm.isFold ? "body-text-fold" : "" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "seemore", on: { click: _vm.changepanel } },
                [
                  _c("span", [
                    _vm._v(_vm._s(!_vm.isFold ? "查看更多" : "收起")),
                  ]),
                ]
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$T = [];
__vue_render__$T._withStripped = true;

  /* style */
  const __vue_inject_styles__$T = undefined;
  /* scoped */
  const __vue_scope_id__$T = "data-v-d53c8d8c";
  /* module identifier */
  const __vue_module_identifier__$T = undefined;
  /* functional template */
  const __vue_is_functional_template__$T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$T, staticRenderFns: __vue_staticRenderFns__$T },
    __vue_inject_styles__$T,
    __vue_script__$T,
    __vue_scope_id__$T,
    __vue_is_functional_template__$T,
    __vue_module_identifier__$T,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$S = {
  name: 'paragraphTabstheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      titleTextDataName1: 'titleTextData1',
      groupData: {},
      tabslist: [],
      chooseindex: 0,
      timer: ''
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  watch: {
    pGroupData: {
      handler(newValue, oldValue) {
        this.$set(this.groupData, 'titleTextData', newValue.titleTextData);
        this.$set(this.groupData, 'bodyTextData', newValue.bodyTextData);
        this.handleLoad();
      },
      deep: true
    }
  },
  methods: {
    handleLoad() {
      this.timer = new Date().getTime();
    },
    clicktabs(e) {
      this.chooseindex = e;
    },
    addtabs() {
      // this.groupData.titleTextData.push({
      //       html: '<p style="font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;">选项一</p>',
      // })
      // this.groupData.bodyTextData.push({
      //     html: '<p style="font-size: 16px;font-family: Microsoft YaHei;font-weight: 400;color: #333333;">夏天的风是蓝色的温柔。我时常仰望夏日天空，蓝得清澈。夏日的风，漫过无边无垠的往事，送来对未来的美好希望。</p>',
      // })
      this.$emit('addtabs', {
        ruleIndex: this.ruleIndex
      });
    },
    deltabs(index) {
      this.$emit('deltabs', {
        ruleIndex: this.ruleIndex,
        dataIndex: index
      });
      if (this.groupData.titleTextData.length == 0) {
        this.chooseindex = 0;
      } else {
        this.chooseindex = this.groupData.titleTextData.length - 1;
      }
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data,
      dataIndex
    }) {
      console.log(ruleIndex, dataName, data);
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data,
        dataIndex
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$S = script$S;
var __vue_render__$S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "top-style" },
            [
              _vm._l(_vm.groupData.titleTextData, function (item, k) {
                return _c(
                  "div",
                  {
                    key: k,
                    on: {
                      click: function ($event) {
                        return _vm.clicktabs(k)
                      },
                    },
                  },
                  [
                    _c("xml-text-render", {
                      key: _vm.timer,
                      class: _vm.chooseindex == k ? "active" : "",
                      attrs: {
                        "p-block-data": item,
                        "block-data-name": _vm.titleTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "data-index": k,
                        "container-class": "text-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                    _vm._v(" "),
                    _vm.mode == "editor"
                      ? _c("img", {
                          staticClass: "deltabs",
                          attrs: {
                            src: __$_require_assets_images_closeicon_png__,
                          },
                          on: {
                            click: function ($event) {
                              return _vm.deltabs(k)
                            },
                          },
                        })
                      : _vm._e(),
                  ],
                  1
                )
              }),
              _vm._v(" "),
              _vm.mode == "editor"
                ? _c(
                    "span",
                    { staticClass: "addtabs", on: { click: _vm.addtabs } },
                    [
                      _c("img", {
                        attrs: {
                          src: __$_require_assets_images_addtabicon_png__,
                          alt: "",
                        },
                      }),
                    ]
                  )
                : _vm._e(),
            ],
            2
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "body-text" },
              _vm._l(_vm.groupData.bodyTextData, function (item, i) {
                return _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.chooseindex == i,
                        expression: "chooseindex==i",
                      },
                    ],
                    key: i,
                  },
                  [
                    _c("xml-text-render", {
                      key: _vm.timer,
                      attrs: {
                        "p-block-data": item,
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "data-index": i,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                )
              }),
              0
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$S = [];
__vue_render__$S._withStripped = true;

  /* style */
  const __vue_inject_styles__$S = undefined;
  /* scoped */
  const __vue_scope_id__$S = "data-v-13389f86";
  /* module identifier */
  const __vue_module_identifier__$S = undefined;
  /* functional template */
  const __vue_is_functional_template__$S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$S, staticRenderFns: __vue_staticRenderFns__$S },
    __vue_inject_styles__$S,
    __vue_script__$S,
    __vue_scope_id__$S,
    __vue_is_functional_template__$S,
    __vue_module_identifier__$S,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$R = {
  name: 'paragraphTabstheme6LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      titleTextDataName1: 'titleTextData1',
      groupData: {},
      tabslist: [],
      chooseindex: 0,
      timer: ''
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  watch: {
    pGroupData: {
      handler(newValue, oldValue) {
        this.$set(this.groupData, 'titleTextData', newValue.titleTextData);
        this.$set(this.groupData, 'bodyTextData', newValue.bodyTextData);
        this.handleLoad();
      },
      deep: true
    }
  },
  methods: {
    handleLoad() {
      this.timer = new Date().getTime();
    },
    clicktabs(e) {
      this.chooseindex = e;
    },
    addtabs() {
      // this.groupData.titleTextData.push({
      //       html: '<p style="font-size: 16px;font-family: PingFang SC;font-weight: bold;color: #000;">选项一</p>',
      // })
      // this.groupData.bodyTextData.push({
      //     html: '<p style="font-size: 16px;font-family: Microsoft YaHei;font-weight: 400;color: #333333;">夏天的风是蓝色的温柔。我时常仰望夏日天空，蓝得清澈。夏日的风，漫过无边无垠的往事，送来对未来的美好希望。</p>',
      // })
      this.$emit('addtabs', {
        ruleIndex: this.ruleIndex
      });
    },
    deltabs(index) {
      this.$emit('deltabs', {
        ruleIndex: this.ruleIndex,
        dataIndex: index
      });
      if (this.groupData.titleTextData.length == 0) {
        this.chooseindex = 0;
      } else {
        this.chooseindex = this.groupData.titleTextData.length - 1;
      }
    },
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data,
      dataIndex
    }) {
      console.log(ruleIndex, dataName, data);
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data,
        dataIndex
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$R = script$R;
var __vue_render__$R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Tabstheme6-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "suptitle" },
            [
              _c("div", { staticClass: "block1" }),
              _vm._v(" "),
              _c("div", { staticClass: "block2" }),
              _vm._v(" "),
              _c("div", { staticClass: "block3" }),
              _vm._v(" "),
              _c("div", { staticClass: "block4" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.titleTextDataName1],
                  "block-data-name": _vm.titleTextDataName1,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  resourceBasisPath: _vm.resourceBasisPath,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
            ],
            1
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c(
              "div",
              { staticClass: "top-style" },
              [
                _vm._l(_vm.groupData.titleTextData, function (item, k) {
                  return _c(
                    "div",
                    {
                      key: k,
                      class: _vm.chooseindex == k ? "active" : "",
                      on: {
                        click: function ($event) {
                          return _vm.clicktabs(k)
                        },
                      },
                    },
                    [
                      _c("xml-text-render", {
                        key: _vm.timer,
                        attrs: {
                          "p-block-data": item,
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "data-index": k,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _vm.mode == "editor"
                        ? _c("img", {
                            staticClass: "deltabs",
                            attrs: {
                              src: __$_require_assets_images_closeicon_png__,
                            },
                            on: {
                              click: function ($event) {
                                return _vm.deltabs(k)
                              },
                            },
                          })
                        : _vm._e(),
                    ],
                    1
                  )
                }),
                _vm._v(" "),
                _vm.mode == "editor"
                  ? _c(
                      "span",
                      { staticClass: "addtabs", on: { click: _vm.addtabs } },
                      [
                        _c("img", {
                          attrs: {
                            src: __$_require_assets_images_addtabicon_png__,
                            alt: "",
                          },
                        }),
                      ]
                    )
                  : _vm._e(),
              ],
              2
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "body-text" },
              _vm._l(_vm.groupData.bodyTextData, function (item, i) {
                return _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.chooseindex == i,
                        expression: "chooseindex==i",
                      },
                    ],
                    key: i,
                  },
                  [
                    _c("xml-text-render", {
                      key: _vm.timer,
                      attrs: {
                        "p-block-data": item,
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "data-index": i,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                )
              }),
              0
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$R = [];
__vue_render__$R._withStripped = true;

  /* style */
  const __vue_inject_styles__$R = undefined;
  /* scoped */
  const __vue_scope_id__$R = "data-v-153c6ca0";
  /* module identifier */
  const __vue_module_identifier__$R = undefined;
  /* functional template */
  const __vue_is_functional_template__$R = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$R, staticRenderFns: __vue_staticRenderFns__$R },
    __vue_inject_styles__$R,
    __vue_script__$R,
    __vue_scope_id__$R,
    __vue_is_functional_template__$R,
    __vue_module_identifier__$R,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$Q = {
  name: 'paragraphFortytwoLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$Q = script$Q;
/* template */
var __vue_render__$Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortytwo-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "top-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "left-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "bottom-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "right-line" }),
              _vm._v(" "),
              _c("div", { staticClass: "right-bottom-triangle" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$Q = [];
__vue_render__$Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$Q = undefined;
  /* scoped */
  const __vue_scope_id__$Q = "data-v-448d3084";
  /* module identifier */
  const __vue_module_identifier__$Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Q, staticRenderFns: __vue_staticRenderFns__$Q },
    __vue_inject_styles__$Q,
    __vue_script__$Q,
    __vue_scope_id__$Q,
    __vue_is_functional_template__$Q,
    __vue_module_identifier__$Q,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$P = {
  name: 'paragraphFortythreeLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$P = script$P;
/* template */
var __vue_render__$P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortythree-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "top-round" }),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "top-right-triangle" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$P = [];
__vue_render__$P._withStripped = true;

  /* style */
  const __vue_inject_styles__$P = undefined;
  /* scoped */
  const __vue_scope_id__$P = "data-v-35df8a24";
  /* module identifier */
  const __vue_module_identifier__$P = undefined;
  /* functional template */
  const __vue_is_functional_template__$P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$P, staticRenderFns: __vue_staticRenderFns__$P },
    __vue_inject_styles__$P,
    __vue_script__$P,
    __vue_scope_id__$P,
    __vue_is_functional_template__$P,
    __vue_module_identifier__$P,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$O = {
  name: 'paragraphFortyfourLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$O = script$O;
/* template */
var __vue_render__$O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortyfour-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "body-text" },
            [
              _c("div", { staticClass: "top-round" }, [
                _c(
                  "svg",
                  {
                    staticClass: "icon",
                    attrs: {
                      t: "1691747531614",
                      viewBox: "0 0 1024 1024",
                      version: "1.1",
                      xmlns: "http://www.w3.org/2000/svg",
                      "p-id": "4801",
                      width: "32",
                      height: "32",
                    },
                  },
                  [
                    _c("path", {
                      attrs: {
                        d: "M470.72 140.032a259.584 259.584 0 0 1 377.216 356.352l-10.112 10.688-408.384 408.384a152 152 0 0 1-223.04-206.144l8.128-8.768 403.968-403.968a50.56 50.56 0 0 1 76.48 65.92l-4.928 5.632-403.968 404.032a50.752 50.752 0 0 0 66.112 76.672l5.632-4.928 408.384-408.32A158.336 158.336 0 0 0 551.04 203.52l-8.704 8.064L214.4 539.52A50.56 50.56 0 0 1 137.92 473.6l4.928-5.696 327.872-327.872z",
                        fill: _vm.groupData.themeColor,
                        "p-id": "4802",
                      },
                    }),
                  ]
                ),
              ]),
              _vm._v(" "),
              _c("xml-text-render", {
                attrs: {
                  "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                  "block-data-name": _vm.bodyTextDataName,
                  "extend-params": _vm.extendParams,
                  "current-rule": _vm.currentRule,
                  "rule-index": _vm.ruleIndex,
                  "container-class": "text-body-container",
                  "editor-class": "editor",
                  mode: _vm.mode,
                  "page-type": _vm.pageType,
                  scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                },
                on: {
                  blockclick: _vm.handleBlockClick,
                  groupDataChange: _vm.groupDataChange,
                },
                scopedSlots: _vm._u(
                  [
                    _vm._l(_vm.$scopedSlots, function (index, name) {
                      return {
                        key: name,
                        fn: function (deliver) {
                          return [_vm._t(name, null, null, deliver)]
                        },
                      }
                    }),
                  ],
                  null,
                  true
                ),
              }),
              _vm._v(" "),
              _c("div", { staticClass: "top-right-triangle" }),
            ],
            1
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$O = [];
__vue_render__$O._withStripped = true;

  /* style */
  const __vue_inject_styles__$O = undefined;
  /* scoped */
  const __vue_scope_id__$O = "data-v-4ac8c8f4";
  /* module identifier */
  const __vue_module_identifier__$O = undefined;
  /* functional template */
  const __vue_is_functional_template__$O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$O, staticRenderFns: __vue_staticRenderFns__$O },
    __vue_inject_styles__$O,
    __vue_script__$O,
    __vue_scope_id__$O,
    __vue_is_functional_template__$O,
    __vue_module_identifier__$O,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$N = {
  name: 'paragraphFortyfiveLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$N = script$N;
/* template */
var __vue_render__$N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortyfive-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "top-title-style" }, [
              _c(
                "div",
                { staticClass: "title-body-box" },
                [
                  _c("div", { staticClass: "line" }),
                  _vm._v(" "),
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                  _vm._v(" "),
                  _c("div", { staticClass: "line" }),
                ],
                1
              ),
            ]),
            _vm._v(" "),
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$N = [];
__vue_render__$N._withStripped = true;

  /* style */
  const __vue_inject_styles__$N = undefined;
  /* scoped */
  const __vue_scope_id__$N = "data-v-460cc49d";
  /* module identifier */
  const __vue_module_identifier__$N = undefined;
  /* functional template */
  const __vue_is_functional_template__$N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$N, staticRenderFns: __vue_staticRenderFns__$N },
    __vue_inject_styles__$N,
    __vue_script__$N,
    __vue_scope_id__$N,
    __vue_is_functional_template__$N,
    __vue_module_identifier__$N,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$M = {
  name: 'paragraphFortysixLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：prediv 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$M = script$M;
/* template */
var __vue_render__$M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortysix-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text" }, [
            _c("div", { staticClass: "custom-border left-border" }, [
              _c("div", { staticClass: "cir1" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir2" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir3" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir4" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir5" }),
            ]),
            _vm._v(" "),
            _c(
              "div",
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "custom-border right-border" }, [
              _c("div", { staticClass: "cir1" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir2" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir3" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir4" }),
              _vm._v(" "),
              _c("div", { staticClass: "cir5" }),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$M = [];
__vue_render__$M._withStripped = true;

  /* style */
  const __vue_inject_styles__$M = undefined;
  /* scoped */
  const __vue_scope_id__$M = "data-v-b2ff931a";
  /* module identifier */
  const __vue_module_identifier__$M = undefined;
  /* functional template */
  const __vue_is_functional_template__$M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$M, staticRenderFns: __vue_staticRenderFns__$M },
    __vue_inject_styles__$M,
    __vue_script__$M,
    __vue_scope_id__$M,
    __vue_is_functional_template__$M,
    __vue_module_identifier__$M,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$L = {
  name: 'paragraphFortysevenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：prediv 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$L = script$L;
/* template */
var __vue_render__$L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortyseven-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text" }, [
            _c(
              "div",
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$L = [];
__vue_render__$L._withStripped = true;

  /* style */
  const __vue_inject_styles__$L = undefined;
  /* scoped */
  const __vue_scope_id__$L = "data-v-4d776143";
  /* module identifier */
  const __vue_module_identifier__$L = undefined;
  /* functional template */
  const __vue_is_functional_template__$L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$L, staticRenderFns: __vue_staticRenderFns__$L },
    __vue_inject_styles__$L,
    __vue_script__$L,
    __vue_scope_id__$L,
    __vue_is_functional_template__$L,
    __vue_module_identifier__$L,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$K = {
  name: 'paragraphFortyeightLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  data() {
    return {
      typeText: '段落',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      bodyTextDataName: 'bodyTextData',
      titleTextDataName: 'titleTextData',
      groupData: {},
      isFold: this.isExpand //是否折叠
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    isExpand: {
      //是否展开
      type: Boolean,
      default: false
    }
  },
  created() {
    console.log(this.pGroupData, '组件内部');
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    },
    changepanel() {
      this.isFold = !this.isFold;
    }
  },
  mounted() {}
};

/* script */
const __vue_script__$K = script$K;
/* template */
var __vue_render__$K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-paragraph-Fortyeight-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("div", { staticClass: "body-text-bg" }, [
            _c("div", { staticClass: "body-text" }, [
              _c(
                "div",
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$K = [];
__vue_render__$K._withStripped = true;

  /* style */
  const __vue_inject_styles__$K = undefined;
  /* scoped */
  const __vue_scope_id__$K = "data-v-2757ab0b";
  /* module identifier */
  const __vue_module_identifier__$K = undefined;
  /* functional template */
  const __vue_is_functional_template__$K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$K, staticRenderFns: __vue_staticRenderFns__$K },
    __vue_inject_styles__$K,
    __vue_script__$K,
    __vue_scope_id__$K,
    __vue_is_functional_template__$K,
    __vue_module_identifier__$K,
    false,
    undefined,
    undefined,
    undefined
  );

const TitleComponents$2 = [__vue_component__$1u, __vue_component__$1t, __vue_component__$1s, __vue_component__$1r, __vue_component__$1q, __vue_component__$1p, __vue_component__$1o, __vue_component__$1n, __vue_component__$1m, __vue_component__$1l, __vue_component__$1k, __vue_component__$1j, __vue_component__$1i, __vue_component__$1h, __vue_component__$1g, __vue_component__$1f, __vue_component__$1e, __vue_component__$1d, __vue_component__$1c, __vue_component__$1b, __vue_component__$1a, __vue_component__$19, __vue_component__$18, __vue_component__$17, __vue_component__$16, __vue_component__$15, __vue_component__$14, __vue_component__$13, __vue_component__$12, __vue_component__$11, __vue_component__$10, __vue_component__$$, __vue_component__$_, __vue_component__$Z, __vue_component__$Y, __vue_component__$X, __vue_component__$W, __vue_component__$V, __vue_component__$U, __vue_component__$T, __vue_component__$S, __vue_component__$R, __vue_component__$Q, __vue_component__$P, __vue_component__$O, __vue_component__$N, __vue_component__$M, __vue_component__$L, __vue_component__$K];
var xmlParagraphLibrary = {
  // install,
  ...TitleComponents$2
};

//
/**
 * 单图片组合
 */
var script$J = {
  name: 'imageTextFirstLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$J = script$J;
/* template */
var __vue_render__$J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-first-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticStyle: {
                background: "var(--background-color)",
                "border-radius": "6px",
                padding: "9px 11px",
              },
            },
            [
              _c(
                "div",
                { staticStyle: { display: "flex", "flex-wrap": "nowrap" } },
                [
                  _c(
                    "div",
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _c("xml-image-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                          "block-data-name": _vm.imageBlockDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "image-container",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                        },
                        on: { blockclick: _vm.handleBlockClick },
                      }),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$J = [];
__vue_render__$J._withStripped = true;

  /* style */
  const __vue_inject_styles__$J = undefined;
  /* scoped */
  const __vue_scope_id__$J = "data-v-5ac95e4b";
  /* module identifier */
  const __vue_module_identifier__$J = undefined;
  /* functional template */
  const __vue_is_functional_template__$J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$J, staticRenderFns: __vue_staticRenderFns__$J },
    __vue_inject_styles__$J,
    __vue_script__$J,
    __vue_scope_id__$J,
    __vue_is_functional_template__$J,
    __vue_module_identifier__$J,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$I = {
  name: 'imageTextSecondLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$I = script$I;
/* template */
var __vue_render__$I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-second-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { display: "flex", "align-items": "center" } },
            [
              _c(
                "div",
                { staticClass: "image-box" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "title-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.titleTextDataName],
                      "block-data-name": _vm.titleTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: { blockclick: _vm.handleBlockClick },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]
          ),
          _vm._v(" "),
          _c("div", { staticClass: "body-box" }, [
            _c(
              "div",
              { staticClass: "body-box-inner" },
              [
                _c("xml-text-render", {
                  attrs: {
                    "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                    "block-data-name": _vm.bodyTextDataName,
                    "extend-params": _vm.extendParams,
                    "current-rule": _vm.currentRule,
                    "rule-index": _vm.ruleIndex,
                    "container-class": "text-body-container",
                    "editor-class": "editor",
                    mode: _vm.mode,
                    "page-type": _vm.pageType,
                    resourceBasisPath: _vm.resourceBasisPath,
                    scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                  },
                  on: {
                    blockclick: _vm.handleBlockClick,
                    groupDataChange: _vm.groupDataChange,
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm._l(_vm.$scopedSlots, function (index, name) {
                        return {
                          key: name,
                          fn: function (deliver) {
                            return [_vm._t(name, null, null, deliver)]
                          },
                        }
                      }),
                    ],
                    null,
                    true
                  ),
                }),
              ],
              1
            ),
          ]),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$I = [];
__vue_render__$I._withStripped = true;

  /* style */
  const __vue_inject_styles__$I = undefined;
  /* scoped */
  const __vue_scope_id__$I = "data-v-3be51a0c";
  /* module identifier */
  const __vue_module_identifier__$I = undefined;
  /* functional template */
  const __vue_is_functional_template__$I = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$I, staticRenderFns: __vue_staticRenderFns__$I },
    __vue_inject_styles__$I,
    __vue_script__$I,
    __vue_scope_id__$I,
    __vue_is_functional_template__$I,
    __vue_module_identifier__$I,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$H = {
  name: 'imageTextThirdLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$H = script$H;
/* template */
var __vue_render__$H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-third-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticStyle: {
                "border-radius": "6px",
                padding: "9px 11px",
                "background-color": "var(--background-color)",
              },
            },
            [
              _c(
                "div",
                {
                  staticStyle: {
                    display: "flex",
                    "flex-wrap": "nowrap",
                    "justify-content": "space-between",
                    "align-items": "center",
                  },
                },
                [
                  _c(
                    "div",
                    { staticStyle: { width: "48%" } },
                    [
                      _c("xml-image-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                          "block-data-name": _vm.imageBlockDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "image-container",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                        },
                        on: { blockclick: _vm.handleBlockClick },
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticStyle: { width: "48%" } },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$H = [];
__vue_render__$H._withStripped = true;

  /* style */
  const __vue_inject_styles__$H = undefined;
  /* scoped */
  const __vue_scope_id__$H = "data-v-17d1f2b0";
  /* module identifier */
  const __vue_module_identifier__$H = undefined;
  /* functional template */
  const __vue_is_functional_template__$H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$H, staticRenderFns: __vue_staticRenderFns__$H },
    __vue_inject_styles__$H,
    __vue_script__$H,
    __vue_scope_id__$H,
    __vue_is_functional_template__$H,
    __vue_module_identifier__$H,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$G = {
  name: 'imageTextFourthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$G = script$G;
/* template */
var __vue_render__$G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-fourth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c(
                "div",
                { staticClass: "iamge-bg-box" },
                [
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "image-text-box" },
                    [
                      _c(
                        "div",
                        { staticClass: "image-top-title" },
                        [
                          _c("xml-text-render", {
                            attrs: {
                              "p-block-data":
                                _vm.groupData[_vm.numberTextDataName],
                              "block-data-name": _vm.numberTextDataName,
                              "extend-params": _vm.extendParams,
                              "current-rule": _vm.currentRule,
                              "rule-index": _vm.ruleIndex,
                              "container-class": "text-container-number",
                              "editor-class": "editor",
                              mode: _vm.mode,
                              "page-type": _vm.pageType,
                              resourceBasisPath: _vm.resourceBasisPath,
                              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                            },
                            on: {
                              blockclick: _vm.handleBlockClick,
                              groupDataChange: _vm.groupDataChange,
                            },
                            scopedSlots: _vm._u(
                              [
                                _vm._l(
                                  _vm.$scopedSlots,
                                  function (index, name) {
                                    return {
                                      key: name,
                                      fn: function (deliver) {
                                        return [
                                          _vm._t(name, null, null, deliver),
                                        ]
                                      },
                                    }
                                  }
                                ),
                              ],
                              null,
                              true
                            ),
                          }),
                          _vm._v(" "),
                          _c("xml-text-render", {
                            attrs: {
                              "p-block-data":
                                _vm.groupData[_vm.titleTextDataName],
                              "block-data-name": _vm.titleTextDataName,
                              "extend-params": _vm.extendParams,
                              "current-rule": _vm.currentRule,
                              "rule-index": _vm.ruleIndex,
                              "container-class": "text-container",
                              "editor-class": "editor",
                              mode: _vm.mode,
                              "page-type": _vm.pageType,
                              resourceBasisPath: _vm.resourceBasisPath,
                              scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                            },
                            on: {
                              blockclick: _vm.handleBlockClick,
                              groupDataChange: _vm.groupDataChange,
                            },
                            scopedSlots: _vm._u(
                              [
                                _vm._l(
                                  _vm.$scopedSlots,
                                  function (index, name) {
                                    return {
                                      key: name,
                                      fn: function (deliver) {
                                        return [
                                          _vm._t(name, null, null, deliver),
                                        ]
                                      },
                                    }
                                  }
                                ),
                              ],
                              null,
                              true
                            ),
                          }),
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                ],
                1
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$G = [];
__vue_render__$G._withStripped = true;

  /* style */
  const __vue_inject_styles__$G = undefined;
  /* scoped */
  const __vue_scope_id__$G = "data-v-36f99eea";
  /* module identifier */
  const __vue_module_identifier__$G = undefined;
  /* functional template */
  const __vue_is_functional_template__$G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$G, staticRenderFns: __vue_staticRenderFns__$G },
    __vue_inject_styles__$G,
    __vue_script__$G,
    __vue_scope_id__$G,
    __vue_is_functional_template__$G,
    __vue_module_identifier__$G,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$F = {
  name: 'imageTextFifthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$F = script$F;
/* template */
var __vue_render__$F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-fifth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c(
                "div",
                { staticClass: "iamge-bg-box" },
                [
                  _c(
                    "div",
                    { staticClass: "image-top-title" },
                    [
                      _c("xml-text-render", {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.titleTextDataName],
                          "block-data-name": _vm.titleTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      }),
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("xml-image-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                      "block-data-name": _vm.imageBlockDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "image-container",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                    },
                    on: { blockclick: _vm.handleBlockClick },
                  }),
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "image-text-box" },
                [
                  _c("xml-text-render", {
                    attrs: {
                      "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                      "block-data-name": _vm.bodyTextDataName,
                      "extend-params": _vm.extendParams,
                      "current-rule": _vm.currentRule,
                      "rule-index": _vm.ruleIndex,
                      "container-class": "text-body-container",
                      "editor-class": "editor",
                      mode: _vm.mode,
                      "page-type": _vm.pageType,
                      resourceBasisPath: _vm.resourceBasisPath,
                      scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                    },
                    on: {
                      blockclick: _vm.handleBlockClick,
                      groupDataChange: _vm.groupDataChange,
                    },
                    scopedSlots: _vm._u(
                      [
                        _vm._l(_vm.$scopedSlots, function (index, name) {
                          return {
                            key: name,
                            fn: function (deliver) {
                              return [_vm._t(name, null, null, deliver)]
                            },
                          }
                        }),
                      ],
                      null,
                      true
                    ),
                  }),
                ],
                1
              ),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$F = [];
__vue_render__$F._withStripped = true;

  /* style */
  const __vue_inject_styles__$F = undefined;
  /* scoped */
  const __vue_scope_id__$F = "data-v-8db63b36";
  /* module identifier */
  const __vue_module_identifier__$F = undefined;
  /* functional template */
  const __vue_is_functional_template__$F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$F, staticRenderFns: __vue_staticRenderFns__$F },
    __vue_inject_styles__$F,
    __vue_script__$F,
    __vue_scope_id__$F,
    __vue_is_functional_template__$F,
    __vue_module_identifier__$F,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$E = {
  name: 'imageTextSixthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$E = script$E;
/* template */
var __vue_render__$E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-sixth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticStyle: {
                "border-radius": "6px",
                padding: "9px 11px",
                display: "flex",
              },
            },
            [
              _c("div", { staticStyle: { display: "flex" } }, [
                _c(
                  "div",
                  { staticClass: "iamge-bg-box" },
                  [
                    _c(
                      "div",
                      { staticClass: "image-top-title" },
                      [
                        _c("xml-text-render", {
                          attrs: {
                            "p-block-data":
                              _vm.groupData[_vm.titleTextDataName],
                            "block-data-name": _vm.titleTextDataName,
                            "extend-params": _vm.extendParams,
                            "current-rule": _vm.currentRule,
                            "rule-index": _vm.ruleIndex,
                            "container-class": "text-container",
                            "editor-class": "editor",
                            mode: _vm.mode,
                            "page-type": _vm.pageType,
                            resourceBasisPath: _vm.resourceBasisPath,
                            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                          },
                          on: {
                            blockclick: _vm.handleBlockClick,
                            groupDataChange: _vm.groupDataChange,
                          },
                          scopedSlots: _vm._u(
                            [
                              _vm._l(_vm.$scopedSlots, function (index, name) {
                                return {
                                  key: name,
                                  fn: function (deliver) {
                                    return [_vm._t(name, null, null, deliver)]
                                  },
                                }
                              }),
                            ],
                            null,
                            true
                          ),
                        }),
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c("xml-image-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                        "block-data-name": _vm.imageBlockDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "image-container",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                      },
                      on: { blockclick: _vm.handleBlockClick },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "image-text-box" },
                  [
                    _c("xml-text-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                        "block-data-name": _vm.bodyTextDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "text-body-container",
                        "editor-class": "editor",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                        scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                      },
                      on: {
                        blockclick: _vm.handleBlockClick,
                        groupDataChange: _vm.groupDataChange,
                      },
                      scopedSlots: _vm._u(
                        [
                          _vm._l(_vm.$scopedSlots, function (index, name) {
                            return {
                              key: name,
                              fn: function (deliver) {
                                return [_vm._t(name, null, null, deliver)]
                              },
                            }
                          }),
                        ],
                        null,
                        true
                      ),
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "image-text-decorate" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$E = [];
__vue_render__$E._withStripped = true;

  /* style */
  const __vue_inject_styles__$E = undefined;
  /* scoped */
  const __vue_scope_id__$E = "data-v-2e5c1f5a";
  /* module identifier */
  const __vue_module_identifier__$E = undefined;
  /* functional template */
  const __vue_is_functional_template__$E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$E, staticRenderFns: __vue_staticRenderFns__$E },
    __vue_inject_styles__$E,
    __vue_script__$E,
    __vue_scope_id__$E,
    __vue_is_functional_template__$E,
    __vue_module_identifier__$E,
    false,
    undefined,
    undefined,
    undefined
  );

//
/**
 * 单图片组合
 */
var script$D = {
  name: 'imageTextSeventhLibraryRender',
  isUnPrefix: true,
  components: {
    XmlImageRender: __vue_component__$5D,
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图文',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      titleTextDataName: 'titleTextData',
      bodyTextDataName: 'bodyTextData',
      imageBlockDataName: 'imageData',
      numberTextDataName: 'numberTextData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$D = script$D;
/* template */
var __vue_render__$D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-text-image-seventh-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            { staticStyle: { "border-radius": "6px", padding: "9px 11px" } },
            [
              _c("div", {}, [
                _c("div", { staticClass: "image-text-decorate2" }),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "iamge-bg-box" },
                  [
                    _c("xml-image-render", {
                      attrs: {
                        "p-block-data": _vm.groupData[_vm.imageBlockDataName],
                        "block-data-name": _vm.imageBlockDataName,
                        "extend-params": _vm.extendParams,
                        "current-rule": _vm.currentRule,
                        "rule-index": _vm.ruleIndex,
                        "container-class": "image-container",
                        mode: _vm.mode,
                        "page-type": _vm.pageType,
                        resourceBasisPath: _vm.resourceBasisPath,
                      },
                      on: { blockclick: _vm.handleBlockClick },
                    }),
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "image-text-box" },
                  [
                    _c(
                      "xml-text-render",
                      {
                        attrs: {
                          "p-block-data": _vm.groupData[_vm.bodyTextDataName],
                          "block-data-name": _vm.bodyTextDataName,
                          "extend-params": _vm.extendParams,
                          "current-rule": _vm.currentRule,
                          "rule-index": _vm.ruleIndex,
                          "container-class": "text-body-container",
                          "editor-class": "editor",
                          mode: _vm.mode,
                          "page-type": _vm.pageType,
                          resourceBasisPath: _vm.resourceBasisPath,
                          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
                        },
                        on: {
                          blockclick: _vm.handleBlockClick,
                          groupDataChange: _vm.groupDataChange,
                        },
                        scopedSlots: _vm._u(
                          [
                            _vm._l(_vm.$scopedSlots, function (index, name) {
                              return {
                                key: name,
                                fn: function (deliver) {
                                  return [_vm._t(name, null, null, deliver)]
                                },
                              }
                            }),
                          ],
                          null,
                          true
                        ),
                      },
                      [_vm._v("s\n          ")]
                    ),
                  ],
                  1
                ),
                _vm._v(" "),
                _c("div", { staticClass: "image-text-decorate" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$D = [];
__vue_render__$D._withStripped = true;

  /* style */
  const __vue_inject_styles__$D = undefined;
  /* scoped */
  const __vue_scope_id__$D = "data-v-e5f3e808";
  /* module identifier */
  const __vue_module_identifier__$D = undefined;
  /* functional template */
  const __vue_is_functional_template__$D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$D, staticRenderFns: __vue_staticRenderFns__$D },
    __vue_inject_styles__$D,
    __vue_script__$D,
    __vue_scope_id__$D,
    __vue_is_functional_template__$D,
    __vue_module_identifier__$D,
    false,
    undefined,
    undefined,
    undefined
  );

const TitleComponents$1 = [__vue_component__$J, __vue_component__$I, __vue_component__$H, __vue_component__$G, __vue_component__$F, __vue_component__$E, __vue_component__$D];
var xmlImageTextLibrary = {
  // install,
  ...TitleComponents$1
};

//
var script$C = {
  name: 'splitlineFirstLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'SplitlineData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex: this.ruleIndex,
        dataName: this.titleTextDataName,
        data: this.pGroupData
      });
    }
  }
};

/* script */
const __vue_script__$C = script$C;
/* template */
var __vue_render__$C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-first-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c("xml-group-render", {
        ref: "xmlGroup",
        attrs: {
          "page-type": _vm.pageType,
          mode: _vm.mode,
          "type-text": _vm.typeText,
          showLine: false,
          "rule-index": _vm.ruleIndex,
          scopedSlots: { $scopedSlots: _vm.$scopedSlots },
        },
        scopedSlots: _vm._u(
          [
            _vm._l(_vm.$scopedSlots, function (index, name) {
              return {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t(name)]
                },
                proxy: true,
              }
            }),
          ],
          null,
          true
        ),
      }),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "title-container",
          attrs: { id: _vm.blockId },
          on: { click: _vm.handleBlockClick },
        },
        [
          _c("div", { staticClass: "line" }),
          _vm._v(" "),
          _c("div", { staticClass: "centerimg" }),
          _vm._v(" "),
          _c("div", { staticClass: "line" }),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$C = [];
__vue_render__$C._withStripped = true;

  /* style */
  const __vue_inject_styles__$C = undefined;
  /* scoped */
  const __vue_scope_id__$C = "data-v-3ffd714e";
  /* module identifier */
  const __vue_module_identifier__$C = undefined;
  /* functional template */
  const __vue_is_functional_template__$C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$C, staticRenderFns: __vue_staticRenderFns__$C },
    __vue_inject_styles__$C,
    __vue_script__$C,
    __vue_scope_id__$C,
    __vue_is_functional_template__$C,
    __vue_module_identifier__$C,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$B = {
  name: 'splitlineSecondLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$B = script$B;
/* template */
var __vue_render__$B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-second-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line1" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }, [_c("div")]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$B = [];
__vue_render__$B._withStripped = true;

  /* style */
  const __vue_inject_styles__$B = undefined;
  /* scoped */
  const __vue_scope_id__$B = "data-v-9ef13f76";
  /* module identifier */
  const __vue_module_identifier__$B = undefined;
  /* functional template */
  const __vue_is_functional_template__$B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$B, staticRenderFns: __vue_staticRenderFns__$B },
    __vue_inject_styles__$B,
    __vue_script__$B,
    __vue_scope_id__$B,
    __vue_is_functional_template__$B,
    __vue_module_identifier__$B,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$A = {
  name: 'splitlineFourthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$A = script$A;
/* template */
var __vue_render__$A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-fourth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "foldline1" }),
                _vm._v(" "),
                _c("div", { staticClass: "foldline2" }),
                _vm._v(" "),
                _c("div", { staticClass: "foldline3" }),
                _vm._v(" "),
                _c("div", { staticClass: "foldline4" }),
                _vm._v(" "),
                _c("div", { staticClass: "foldline5" }),
                _vm._v(" "),
                _c("div", { staticClass: "foldline6" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "line" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$A = [];
__vue_render__$A._withStripped = true;

  /* style */
  const __vue_inject_styles__$A = undefined;
  /* scoped */
  const __vue_scope_id__$A = "data-v-5acad3b2";
  /* module identifier */
  const __vue_module_identifier__$A = undefined;
  /* functional template */
  const __vue_is_functional_template__$A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$A, staticRenderFns: __vue_staticRenderFns__$A },
    __vue_inject_styles__$A,
    __vue_script__$A,
    __vue_scope_id__$A,
    __vue_is_functional_template__$A,
    __vue_module_identifier__$A,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$z = {
  name: 'splitlineFifthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$z = script$z;
/* template */
var __vue_render__$z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-fifth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "line" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$z = [];
__vue_render__$z._withStripped = true;

  /* style */
  const __vue_inject_styles__$z = undefined;
  /* scoped */
  const __vue_scope_id__$z = "data-v-fc9a58d2";
  /* module identifier */
  const __vue_module_identifier__$z = undefined;
  /* functional template */
  const __vue_is_functional_template__$z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$z, staticRenderFns: __vue_staticRenderFns__$z },
    __vue_inject_styles__$z,
    __vue_script__$z,
    __vue_scope_id__$z,
    __vue_is_functional_template__$z,
    __vue_module_identifier__$z,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$y = {
  name: 'splitlineSixthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$y = script$y;
/* template */
var __vue_render__$y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-sixth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "line1" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "block1" }),
                _vm._v(" "),
                _c("div", { staticClass: "block2" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "line2" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$y = [];
__vue_render__$y._withStripped = true;

  /* style */
  const __vue_inject_styles__$y = undefined;
  /* scoped */
  const __vue_scope_id__$y = "data-v-9af2cc8c";
  /* module identifier */
  const __vue_module_identifier__$y = undefined;
  /* functional template */
  const __vue_is_functional_template__$y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$y, staticRenderFns: __vue_staticRenderFns__$y },
    __vue_inject_styles__$y,
    __vue_script__$y,
    __vue_scope_id__$y,
    __vue_is_functional_template__$y,
    __vue_module_identifier__$y,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$x = {
  name: 'splitlineSeventhLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$x = script$x;
/* template */
var __vue_render__$x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-seventh-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "dotted" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }),
              _vm._v(" "),
              _c("div", { staticClass: "dotted" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$x = [];
__vue_render__$x._withStripped = true;

  /* style */
  const __vue_inject_styles__$x = undefined;
  /* scoped */
  const __vue_scope_id__$x = "data-v-29013930";
  /* module identifier */
  const __vue_module_identifier__$x = undefined;
  /* functional template */
  const __vue_is_functional_template__$x = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$x, staticRenderFns: __vue_staticRenderFns__$x },
    __vue_inject_styles__$x,
    __vue_script__$x,
    __vue_scope_id__$x,
    __vue_is_functional_template__$x,
    __vue_module_identifier__$x,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$w = {
  name: 'splitlineEighthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$w = script$w;
/* template */
var __vue_render__$w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-eighth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$w = [];
__vue_render__$w._withStripped = true;

  /* style */
  const __vue_inject_styles__$w = undefined;
  /* scoped */
  const __vue_scope_id__$w = "data-v-476d62c6";
  /* module identifier */
  const __vue_module_identifier__$w = undefined;
  /* functional template */
  const __vue_is_functional_template__$w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$w, staticRenderFns: __vue_staticRenderFns__$w },
    __vue_inject_styles__$w,
    __vue_script__$w,
    __vue_scope_id__$w,
    __vue_is_functional_template__$w,
    __vue_module_identifier__$w,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$v = {
  name: 'splitlineTenthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$v = script$v;
/* template */
var __vue_render__$v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-tenth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", [
                _c("div", { staticClass: "centerimg" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$v = [];
__vue_render__$v._withStripped = true;

  /* style */
  const __vue_inject_styles__$v = undefined;
  /* scoped */
  const __vue_scope_id__$v = "data-v-7fd04fec";
  /* module identifier */
  const __vue_module_identifier__$v = undefined;
  /* functional template */
  const __vue_is_functional_template__$v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$v, staticRenderFns: __vue_staticRenderFns__$v },
    __vue_inject_styles__$v,
    __vue_script__$v,
    __vue_scope_id__$v,
    __vue_is_functional_template__$v,
    __vue_module_identifier__$v,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$u = {
  name: 'splitlineEleventhLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$u = script$u;
/* template */
var __vue_render__$u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-eleventh-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", [
                _c("div", { staticClass: "line1" }),
                _vm._v(" "),
                _c("div", { staticClass: "line2" }),
                _vm._v(" "),
                _c("div", { staticClass: "centerimg" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$u = [];
__vue_render__$u._withStripped = true;

  /* style */
  const __vue_inject_styles__$u = undefined;
  /* scoped */
  const __vue_scope_id__$u = "data-v-6a41025c";
  /* module identifier */
  const __vue_module_identifier__$u = undefined;
  /* functional template */
  const __vue_is_functional_template__$u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$u, staticRenderFns: __vue_staticRenderFns__$u },
    __vue_inject_styles__$u,
    __vue_script__$u,
    __vue_scope_id__$u,
    __vue_is_functional_template__$u,
    __vue_module_identifier__$u,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$t = {
  name: 'splitlineNinthLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$t = script$t;
/* template */
var __vue_render__$t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-ninth-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "centerimg" }, [
                _c("div", { staticClass: "skeybox" }),
                _vm._v(" "),
                _c("div", { staticClass: "line" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$t = [];
__vue_render__$t._withStripped = true;

  /* style */
  const __vue_inject_styles__$t = undefined;
  /* scoped */
  const __vue_scope_id__$t = "data-v-d8198274";
  /* module identifier */
  const __vue_module_identifier__$t = undefined;
  /* functional template */
  const __vue_is_functional_template__$t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$t, staticRenderFns: __vue_staticRenderFns__$t },
    __vue_inject_styles__$t,
    __vue_script__$t,
    __vue_scope_id__$t,
    __vue_is_functional_template__$t,
    __vue_module_identifier__$t,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$s = {
  name: 'splitlineRedtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$s = script$s;
/* template */
var __vue_render__$s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-line" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(90deg)" },
                      attrs: {
                        t: "1684829468544",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "28595",
                        width: "20",
                        height: "20",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M533.504 268.288q33.792-41.984 71.68-75.776 32.768-27.648 74.24-50.176t86.528-19.456q63.488 5.12 105.984 30.208t67.584 63.488 34.304 87.04 6.144 99.84-17.92 97.792-36.864 87.04-48.64 74.752-53.248 61.952q-40.96 41.984-85.504 78.336t-84.992 62.464-73.728 41.472-51.712 15.36q-20.48 1.024-52.224-14.336t-69.632-41.472-79.872-61.952-82.944-75.776q-26.624-25.6-57.344-59.392t-57.856-74.24-46.592-87.552-21.504-100.352 11.264-99.84 39.936-83.456 65.536-61.952 88.064-35.328q24.576-5.12 49.152-1.536t48.128 12.288 45.056 22.016 40.96 27.648q45.056 33.792 86.016 80.896z",
                          "p-id": "28596",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "line" }, [
                    _c("div", { staticClass: "circle" }),
                  ]),
                ]),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "left-line",
                    staticStyle: { transform: "rotate(180deg)" },
                  },
                  [
                    _c(
                      "svg",
                      {
                        staticClass: "icon",
                        staticStyle: { transform: "rotate(90deg)" },
                        attrs: {
                          t: "1684829468544",
                          viewBox: "0 0 1024 1024",
                          version: "1.1",
                          xmlns: "http://www.w3.org/2000/svg",
                          "p-id": "28595",
                          width: "20",
                          height: "20",
                        },
                      },
                      [
                        _c("path", {
                          attrs: {
                            d: "M533.504 268.288q33.792-41.984 71.68-75.776 32.768-27.648 74.24-50.176t86.528-19.456q63.488 5.12 105.984 30.208t67.584 63.488 34.304 87.04 6.144 99.84-17.92 97.792-36.864 87.04-48.64 74.752-53.248 61.952q-40.96 41.984-85.504 78.336t-84.992 62.464-73.728 41.472-51.712 15.36q-20.48 1.024-52.224-14.336t-69.632-41.472-79.872-61.952-82.944-75.776q-26.624-25.6-57.344-59.392t-57.856-74.24-46.592-87.552-21.504-100.352 11.264-99.84 39.936-83.456 65.536-61.952 88.064-35.328q24.576-5.12 49.152-1.536t48.128 12.288 45.056 22.016 40.96 27.648q45.056 33.792 86.016 80.896z",
                            "p-id": "28596",
                            fill: _vm.groupData.themeColor,
                          },
                        }),
                      ]
                    ),
                    _vm._v(" "),
                    _c("div", { staticClass: "line" }, [
                      _c("div", { staticClass: "circle" }),
                    ]),
                  ]
                ),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$s = [];
__vue_render__$s._withStripped = true;

  /* style */
  const __vue_inject_styles__$s = undefined;
  /* scoped */
  const __vue_scope_id__$s = "data-v-25bb3d7a";
  /* module identifier */
  const __vue_module_identifier__$s = undefined;
  /* functional template */
  const __vue_is_functional_template__$s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$s, staticRenderFns: __vue_staticRenderFns__$s },
    __vue_inject_styles__$s,
    __vue_script__$s,
    __vue_scope_id__$s,
    __vue_is_functional_template__$s,
    __vue_module_identifier__$s,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$r = {
  name: 'splitlineGreytheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$r = script$r;
/* template */
var __vue_render__$r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1684833198250",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "31621",
                        width: "25",
                        height: "31",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M288.105 122.229C174.047 122.21 64.691 231.687 64.691 388.095c0 176.07 156.747 241.891 261.855 312.283 101.962 68.266 174.057 161.602 185.453 201.393 9.743-38.936 91.03-135.019 185.448-203.373 103.196-74.727 261.861-136.19 261.861-312.262 0-152.117-109.34-260.294-222.908-260.294-86.792 0-167.781 15.754-224.419 128.441-65.593-111.977-138.495-132.042-223.876-132.054",
                          fill: _vm.groupData.themeColor,
                          "p-id": "31622",
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$r = [];
__vue_render__$r._withStripped = true;

  /* style */
  const __vue_inject_styles__$r = undefined;
  /* scoped */
  const __vue_scope_id__$r = "data-v-5952aa1e";
  /* module identifier */
  const __vue_module_identifier__$r = undefined;
  /* functional template */
  const __vue_is_functional_template__$r = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$r, staticRenderFns: __vue_staticRenderFns__$r },
    __vue_inject_styles__$r,
    __vue_script__$r,
    __vue_scope_id__$r,
    __vue_is_functional_template__$r,
    __vue_module_identifier__$r,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$q = {
  name: 'splitlineGreytheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$q = script$q;
/* template */
var __vue_render__$q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1684833525217",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "32635",
                        width: "25",
                        height: "31",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M957.111079 403.480424c-3.927449-11.669784-14.447037-19.86442-26.723641-20.816095L647.68068 360.749204 540.183388 84.126003c-4.586458-11.803837-15.951297-19.579941-28.614711-19.579941s-24.02723 7.776104-28.614711 19.579941L375.458719 360.749204l-282.708803 21.915126c-12.276604 0.951675-22.796192 9.146311-26.723641 20.816095-3.927449 11.669784-0.50142 24.557302 8.701173 32.737612l217.547735 193.358823-67.980277 291.298436c-2.848884 12.20702 2.009773 24.919553 12.273535 32.114418 10.264784 7.195889 23.87271 7.42818 34.375925 0.586354l240.624313-156.709111 240.625336 156.709111c5.099135 3.320629 10.92891 4.974291 16.752546 4.974291 6.173606 0 12.342096-1.858324 17.623379-5.561669 10.263761-7.194866 15.122419-19.907399 12.273535-32.114418l-67.980277-291.299459L948.409906 436.218036C957.613522 428.037726 961.037505 415.150208 957.111079 403.480424z",
                          fill: _vm.groupData.themeColor,
                          "p-id": "32636",
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$q = [];
__vue_render__$q._withStripped = true;

  /* style */
  const __vue_inject_styles__$q = undefined;
  /* scoped */
  const __vue_scope_id__$q = "data-v-e669eeae";
  /* module identifier */
  const __vue_module_identifier__$q = undefined;
  /* functional template */
  const __vue_is_functional_template__$q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$q, staticRenderFns: __vue_staticRenderFns__$q },
    __vue_inject_styles__$q,
    __vue_script__$q,
    __vue_scope_id__$q,
    __vue_is_functional_template__$q,
    __vue_module_identifier__$q,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$p = {
  name: 'splitlineGreytheme5LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$p = script$p;
/* template */
var __vue_render__$p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme5-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1684834224886",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "42504",
                        width: "25",
                        height: "31",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M992 475.648h-73.088l36.32-62.848a32 32 0 0 0-55.392-32.032l-54.752 94.88H631.52l90.496-52.256a32 32 0 0 0 11.68-43.744 31.904 31.904 0 0 0-43.68-11.68l-90.592 52.288 106.848-185.04h109.536a31.84 31.84 0 0 0 31.904-31.968 31.824 31.824 0 0 0-31.872-32l-72.624 0.032 36.528-63.312a31.952 31.952 0 0 0-11.712-43.696 31.872 31.872 0 0 0-43.68 11.728l-36.56 63.344-36.256-62.944a31.984 31.984 0 0 0-43.776-11.696 31.84 31.84 0 0 0-11.68 43.632l54.816 94.928L544 388.304v-104.656a32 32 0 1 0-64 0v104.56l-106.816-184.992 54.752-94.88a31.904 31.904 0 0 0-11.712-43.632 31.856 31.856 0 0 0-43.664 11.632l-36.32 62.912-36.528-63.28A31.968 31.968 0 0 0 256 64.272a31.92 31.92 0 0 0-11.68 43.696l36.56 63.312-72.624-0.064c-17.712 0-32 14.32-32 32.064a31.84 31.84 0 0 0 31.936 31.936h109.6l106.848 185.056-90.624-52.304a31.888 31.888 0 0 0-43.68 11.712 32 32 0 0 0 11.648 43.728l90.496 52.256H178.944l-54.784-94.88a31.968 31.968 0 0 0-43.648-11.664 31.904 31.904 0 0 0-11.744 43.664l36.32 62.88H32a32 32 0 1 0 0 64h73.088l-36.336 62.848a32 32 0 0 0 55.392 32.032l54.784-94.88h213.68l-90.624 52.32a31.904 31.904 0 0 0-11.68 43.68 31.984 31.984 0 0 0 43.68 11.744l90.624-52.32-106.816 184.992H208.16c-17.632 0-31.936 14.32-31.936 32s14.288 32 31.936 32h72.656l-36.528 63.28a31.968 31.968 0 1 0 55.392 31.968l36.528-63.28 36.256 62.88a32 32 0 0 0 43.744 11.712 31.936 31.936 0 0 0 11.68-43.68l-54.752-94.88L480 627.056v104.592c0 17.696 14.32 32 32 32s32-14.304 32-32v-104.592l106.848 185.024-54.816 94.88a31.952 31.952 0 0 0 11.712 43.68c15.312 8.816 34.88 3.632 43.68-11.68l36.352-62.912 36.528 63.28a31.952 31.952 0 0 0 43.68 11.744 32.016 32.016 0 0 0 11.712-43.744l-36.528-63.28 72.56 0.032a32 32 0 0 0 0.032-64h-109.536l-106.752-184.944 90.464 52.256a32 32 0 0 0 43.728-11.744 31.872 31.872 0 0 0-11.728-43.68l-90.592-52.32h213.68l54.752 94.88a31.936 31.936 0 0 0 43.68 11.68 31.952 31.952 0 0 0 11.712-43.68l-36.288-62.88H992c17.68 0 32-14.304 32-32 0-17.696-14.32-32-32-32z m-480 96a64 64 0 1 1 0-128 64 64 0 0 1 0 128z",
                          fill: _vm.groupData.themeColor,
                          "p-id": "42505",
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$p = [];
__vue_render__$p._withStripped = true;

  /* style */
  const __vue_inject_styles__$p = undefined;
  /* scoped */
  const __vue_scope_id__$p = "data-v-5209060b";
  /* module identifier */
  const __vue_module_identifier__$p = undefined;
  /* functional template */
  const __vue_is_functional_template__$p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$p, staticRenderFns: __vue_staticRenderFns__$p },
    __vue_inject_styles__$p,
    __vue_script__$p,
    __vue_scope_id__$p,
    __vue_is_functional_template__$p,
    __vue_module_identifier__$p,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$o = {
  name: 'splitlineYellowtheme3LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$o = script$o;
/* template */
var __vue_render__$o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme3-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-block-box" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "line-con" }),
                _vm._v(" "),
                _c("div", { staticClass: "right-block-box" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$o = [];
__vue_render__$o._withStripped = true;

  /* style */
  const __vue_inject_styles__$o = undefined;
  /* scoped */
  const __vue_scope_id__$o = "data-v-883e244c";
  /* module identifier */
  const __vue_module_identifier__$o = undefined;
  /* functional template */
  const __vue_is_functional_template__$o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$o, staticRenderFns: __vue_staticRenderFns__$o },
    __vue_inject_styles__$o,
    __vue_script__$o,
    __vue_scope_id__$o,
    __vue_is_functional_template__$o,
    __vue_module_identifier__$o,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$n = {
  name: 'splitlineBluetheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$n = script$n;
/* template */
var __vue_render__$n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "iconbox" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(-90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "iconbox" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(-90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "iconbox" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(-90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "iconbox" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(-90deg)" },
                      attrs: {
                        t: "1684838451897",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "51769",
                        width: "10",
                        height: "10",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M525.873548 897.156129l-383.174193-761.723871 763.045161-1.981935-379.870968 763.705806z",
                          "p-id": "51770",
                          fill: _vm.groupData.themeColor,
                        },
                      }),
                    ]
                  ),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$n = [];
__vue_render__$n._withStripped = true;

  /* style */
  const __vue_inject_styles__$n = undefined;
  /* scoped */
  const __vue_scope_id__$n = "data-v-5d80a0e0";
  /* module identifier */
  const __vue_module_identifier__$n = undefined;
  /* functional template */
  const __vue_is_functional_template__$n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$n, staticRenderFns: __vue_staticRenderFns__$n },
    __vue_inject_styles__$n,
    __vue_script__$n,
    __vue_scope_id__$n,
    __vue_is_functional_template__$n,
    __vue_module_identifier__$n,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$m = {
  name: 'splitlineBluetheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$m = script$m;
/* template */
var __vue_render__$m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
                _vm._v(" "),
                _c("div"),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$m = [];
__vue_render__$m._withStripped = true;

  /* style */
  const __vue_inject_styles__$m = undefined;
  /* scoped */
  const __vue_scope_id__$m = "data-v-c549a7b8";
  /* module identifier */
  const __vue_module_identifier__$m = undefined;
  /* functional template */
  const __vue_is_functional_template__$m = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$m, staticRenderFns: __vue_staticRenderFns__$m },
    __vue_inject_styles__$m,
    __vue_script__$m,
    __vue_scope_id__$m,
    __vue_is_functional_template__$m,
    __vue_module_identifier__$m,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$l = {
  name: 'splitlineYellowtheme2LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$l = script$l;
/* template */
var __vue_render__$l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme2-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "linebox-circle" }),
                _vm._v(" "),
                _c("div", { staticClass: "iconbox" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { "margin-left": "-5px" },
                      attrs: {
                        t: "1684841153840",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "52311",
                        width: "35",
                        height: "35",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M960 512L512 769.9 64 512l448-257.9z",
                          fill: _vm.groupData.themeColor,
                          "p-id": "52312",
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      attrs: {
                        t: "1684841153840",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "52311",
                        width: "20",
                        height: "20",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M960 512L512 769.9 64 512l448-257.9z",
                          fill: _vm.groupData.themeColor,
                          "p-id": "52312",
                        },
                      }),
                    ]
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "circle" }),
                  _vm._v(" "),
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { "margin-right": "-5px" },
                      attrs: {
                        t: "1684841153840",
                        viewBox: "0 0 1024 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "52311",
                        width: "35",
                        height: "35",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M960 512L512 769.9 64 512l448-257.9z",
                          fill: _vm.groupData.themeColor,
                          "p-id": "52312",
                        },
                      }),
                    ]
                  ),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "linebox-circle" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$l = [];
__vue_render__$l._withStripped = true;

  /* style */
  const __vue_inject_styles__$l = undefined;
  /* scoped */
  const __vue_scope_id__$l = "data-v-69eabc3c";
  /* module identifier */
  const __vue_module_identifier__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$l, staticRenderFns: __vue_staticRenderFns__$l },
    __vue_inject_styles__$l,
    __vue_script__$l,
    __vue_scope_id__$l,
    __vue_is_functional_template__$l,
    __vue_module_identifier__$l,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$k = {
  name: 'splitlineRedtheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$k = script$k;
/* template */
var __vue_render__$k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Redtheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "top-linecon" }),
                _vm._v(" "),
                _c("div", { staticClass: "bottom-linecon" }, [
                  _c("div", { staticClass: "left-trianglebox" }, [
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "line" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "right-trianglebox" }, [
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                    _vm._v(" "),
                    _c("div"),
                  ]),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$k = [];
__vue_render__$k._withStripped = true;

  /* style */
  const __vue_inject_styles__$k = undefined;
  /* scoped */
  const __vue_scope_id__$k = "data-v-13640122";
  /* module identifier */
  const __vue_module_identifier__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$k = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$k, staticRenderFns: __vue_staticRenderFns__$k },
    __vue_inject_styles__$k,
    __vue_script__$k,
    __vue_scope_id__$k,
    __vue_is_functional_template__$k,
    __vue_module_identifier__$k,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$j = {
  name: 'splitlineYellowtheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$j = script$j;
/* template */
var __vue_render__$j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Yellowtheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-block" }),
                _vm._v(" "),
                _c("div", { staticClass: "linecon" }),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c(
                    "svg",
                    {
                      staticClass: "icon",
                      staticStyle: { transform: "rotate(180deg)" },
                      attrs: {
                        t: "1684893686787",
                        viewBox: "0 0 1096 1024",
                        version: "1.1",
                        xmlns: "http://www.w3.org/2000/svg",
                        "p-id": "4676",
                        width: "32",
                        height: "32",
                      },
                    },
                    [
                      _c("path", {
                        attrs: {
                          d: "M29.125818 609.321374c0 63.550061 50.313051 113.859232 113.864404 113.859232 63.544889 0 113.857939-50.307879 113.85794-113.859232s-50.313051-113.863111-113.85794-113.863111c-63.551354 0-113.864404 50.313051-113.864404 113.863111z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4677",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M516.342949 296.868202c0 45.014626 34.424242 79.433697 79.440162 79.433697 42.364121 0 79.43499-37.070869 79.43499-79.433697 0-45.015919-34.419071-79.440162-79.43499-79.440162s-79.440162 37.070869-79.440162 79.440162z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4678",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M852.631273 675.519354c0 50.313051 39.717495 90.030545 90.030545 90.030545 50.307879 0 90.025374-39.717495 90.025374-90.030545 0-50.307879-39.717495-90.030545-90.025374-90.030546-50.313051-2.646626-90.030545 39.722667-90.030545 90.030546z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4679",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M0 815.857778c0 26.479192 21.182061 47.661253 47.661253 47.661252 26.480485 0 47.662545-21.182061 47.662545-47.661252s-21.182061-47.65996-47.662545-47.65996c-26.479192-0.001293-47.661253 21.179475-47.661253 47.65996z m1048.576-373.35402a23.731717 23.731717 0 0 0 23.833859 23.828686 23.730424 23.730424 0 0 0 23.828686-23.828686 23.730424 23.730424 0 0 0-23.828686-23.832566 23.731717 23.731717 0 0 0-23.833859 23.832566z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4680",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M887.050343 408.079515c0 15.888808 13.242182 26.480485 26.479192 26.480485 15.888808 0 26.479192-13.243475 26.479192-26.480485 0-15.888808-13.23701-26.479192-26.479192-26.479192-15.883636-2.646626-26.479192 10.590384-26.479192 26.479192z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4681",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M296.565657 196.24598c0 18.535434 15.888808 34.424242 34.424242 34.424242s34.424242-15.888808 34.424242-34.424242-15.888808-34.424242-34.424242-34.424243c-18.535434-2.646626-34.424242 13.243475-34.424242 34.424243z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4682",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M675.216808 511.347071c0 18.535434 13.242182 31.777616 31.777616 31.777616s31.772444-15.888808 31.772445-31.777616c0-18.535434-13.23701-31.772444-31.772445-31.772445s-31.777616 13.23701-31.777616 31.772445z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4683",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M378.651152 508.700444c0 18.535434 13.242182 31.773737 31.777616 31.773738s31.773737-13.23701 31.773737-31.773738c0-18.535434-13.238303-31.777616-31.773737-31.777616s-31.777616 13.242182-31.777616 31.777616z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4684",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M264.793212 352.473212c0 13.238303 10.590384 26.480485 23.827394 26.480485 13.243475 0 23.833859-10.591677 23.833859-26.480485 0-13.242182-10.590384-26.479192-23.833859-26.479192-13.23701 0-23.827394 13.23701-23.827394 26.479192z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4685",
                        },
                      }),
                      _vm._v(" "),
                      _c("path", {
                        attrs: {
                          d: "M526.934626 654.337293a23.731717 23.731717 0 0 0 23.833859 23.828687 23.730424 23.730424 0 0 0 23.828687-23.828687 23.731717 23.731717 0 0 0-23.828687-23.833859 23.73301 23.73301 0 0 0-23.833859 23.833859z m0 0",
                          fill: _vm.groupData.themeColor,
                          "p-id": "4686",
                        },
                      }),
                    ]
                  ),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$j = [];
__vue_render__$j._withStripped = true;

  /* style */
  const __vue_inject_styles__$j = undefined;
  /* scoped */
  const __vue_scope_id__$j = "data-v-5004ae91";
  /* module identifier */
  const __vue_module_identifier__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$j, staticRenderFns: __vue_staticRenderFns__$j },
    __vue_inject_styles__$j,
    __vue_script__$j,
    __vue_scope_id__$j,
    __vue_is_functional_template__$j,
    __vue_module_identifier__$j,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$i = {
  name: 'splitlineGreytheme1LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$i = script$i;
/* template */
var __vue_render__$i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Greytheme1-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "left-icon" }, [
                  _c("div", { staticClass: "block" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "triangle" }),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-icon" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$i = [];
__vue_render__$i._withStripped = true;

  /* style */
  const __vue_inject_styles__$i = undefined;
  /* scoped */
  const __vue_scope_id__$i = "data-v-d79a25f8";
  /* module identifier */
  const __vue_module_identifier__$i = undefined;
  /* functional template */
  const __vue_is_functional_template__$i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$i, staticRenderFns: __vue_staticRenderFns__$i },
    __vue_inject_styles__$i,
    __vue_script__$i,
    __vue_scope_id__$i,
    __vue_is_functional_template__$i,
    __vue_module_identifier__$i,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$h = {
  name: 'splitlineBluetheme4LibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$h = script$h;
/* template */
var __vue_render__$h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Bluetheme4-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "linebox" }, [
                _c("div", { staticClass: "top-lineconbox" }),
                _vm._v(" "),
                _c("div", { staticClass: "bottom-lineconbox" }, [
                  _c("div", { staticClass: "linecon1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "bottom-block1" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "bottom-block2" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "linecon2" }),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$h = [];
__vue_render__$h._withStripped = true;

  /* style */
  const __vue_inject_styles__$h = undefined;
  /* scoped */
  const __vue_scope_id__$h = "data-v-eaf7e5d2";
  /* module identifier */
  const __vue_module_identifier__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$h, staticRenderFns: __vue_staticRenderFns__$h },
    __vue_inject_styles__$h,
    __vue_script__$h,
    __vue_scope_id__$h,
    __vue_is_functional_template__$h,
    __vue_module_identifier__$h,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$g = {
  name: 'splitlineTwentytwoLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$g = script$g;
/* template */
var __vue_render__$g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentytwo-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "centerimg" }, [_c("div")])]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$g = [];
__vue_render__$g._withStripped = true;

  /* style */
  const __vue_inject_styles__$g = undefined;
  /* scoped */
  const __vue_scope_id__$g = "data-v-72ea4cee";
  /* module identifier */
  const __vue_module_identifier__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$g, staticRenderFns: __vue_staticRenderFns__$g },
    __vue_inject_styles__$g,
    __vue_script__$g,
    __vue_scope_id__$g,
    __vue_is_functional_template__$g,
    __vue_module_identifier__$g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$f = {
  name: 'splitlineTwentythreeLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$f = script$f;
/* template */
var __vue_render__$f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentythree-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", { staticClass: "centerimg" }, [_c("div")])]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$f = [];
__vue_render__$f._withStripped = true;

  /* style */
  const __vue_inject_styles__$f = undefined;
  /* scoped */
  const __vue_scope_id__$f = "data-v-76902253";
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$f,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f,
    __vue_module_identifier__$f,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$e = {
  name: 'splitlineTwentyfourLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$e = script$e;
/* template */
var __vue_render__$e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentyfour-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [_c("div", [_c("div", { staticClass: "centerimg" }, [_c("div")])])]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$e = [];
__vue_render__$e._withStripped = true;

  /* style */
  const __vue_inject_styles__$e = undefined;
  /* scoped */
  const __vue_scope_id__$e = "data-v-0522d83a";
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$e,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e,
    __vue_module_identifier__$e,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$d = {
  name: 'splitlineTwentyfiveLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$d = script$d;
/* template */
var __vue_render__$d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentyfive-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", [
                _c("div", { staticClass: "left-top-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "left-bottom-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "left-block-box" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "right-top-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "right-bottom-line" }),
                _vm._v(" "),
                _c("div", { staticClass: "right-block-box" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "centerimg" }, [
                  _c("div"),
                  _vm._v(" "),
                  _c("div"),
                  _vm._v(" "),
                  _c("div", { staticClass: "left-line-box" }),
                  _vm._v(" "),
                  _c("div", { staticClass: "right-line-box" }),
                ]),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$d = [];
__vue_render__$d._withStripped = true;

  /* style */
  const __vue_inject_styles__$d = undefined;
  /* scoped */
  const __vue_scope_id__$d = "data-v-ade18eee";
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    __vue_script__$d,
    __vue_scope_id__$d,
    __vue_is_functional_template__$d,
    __vue_module_identifier__$d,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$c = {
  name: 'splitlineTwentysixLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$c = script$c;
/* template */
var __vue_render__$c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentysix-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "left-triangle" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "right-triangle" }),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$c = [];
__vue_render__$c._withStripped = true;

  /* style */
  const __vue_inject_styles__$c = undefined;
  /* scoped */
  const __vue_scope_id__$c = "data-v-adf49234";
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$c,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$c,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$b = {
  name: 'splitlineTwentysevenLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$b = script$b;
/* template */
var __vue_render__$b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentyseven-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
              _vm._v(" "),
              _c("div"),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$b = [];
__vue_render__$b._withStripped = true;

  /* style */
  const __vue_inject_styles__$b = undefined;
  /* scoped */
  const __vue_scope_id__$b = "data-v-6bea3d52";
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$b,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b,
    __vue_module_identifier__$b,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$a = {
  name: 'splitlineTwentyeightLibraryRender',
  isUnPrefix: true,
  components: {
    XmlTextRender: __vue_component__$5x
  },
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '分割线',
      titleTextDataName: 'titleTextData',
      groupData: {},
      blockId: `blockId${Math.random()}`,
      propertyComponentsName: 'XmlSplitlineProperty'
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName: this.titleTextDataName,
        propertyComponentsName: this.propertyComponentsName,
        currentRule: this.currentRule,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    groupDataChange({
      ruleIndex,
      dataName,
      data
    }) {
      this.$emit('groupDataChange', {
        ruleIndex,
        dataName,
        data
      });
    }
  }
};

/* script */
const __vue_script__$a = script$a;
/* template */
var __vue_render__$a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-splitline-Twentyeight-library-" + _vm.pageType,
        "xml-single-ebook-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      style: { "--background-color": _vm.groupData.themeColor },
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            showLine: false,
            "rule-index": _vm.ruleIndex,
            scopedSlots: { $scopedSlots: _vm.$scopedSlots },
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function (index, name) {
                return {
                  key: "action-buttons",
                  fn: function () {
                    return [_vm._t(name)]
                  },
                  proxy: true,
                }
              }),
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c(
            "div",
            {
              staticClass: "title-container",
              attrs: { id: _vm.blockId },
              on: { click: _vm.handleBlockClick },
            },
            [
              _c("div", { staticClass: "side-block-box" }, [
                _c("div", { staticClass: "round" }),
                _vm._v(" "),
                _c("div", { staticClass: "icon" }),
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "left-line-box" }),
              _vm._v(" "),
              _c("div", { staticClass: "centerimg" }, [_c("div")]),
              _vm._v(" "),
              _c("div", { staticClass: "right-line-box" }),
              _vm._v(" "),
              _c("div", { staticClass: "side-block-box" }, [
                _c("div", { staticClass: "icon" }),
                _vm._v(" "),
                _c("div", { staticClass: "round" }),
              ]),
            ]
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$a = [];
__vue_render__$a._withStripped = true;

  /* style */
  const __vue_inject_styles__$a = undefined;
  /* scoped */
  const __vue_scope_id__$a = "data-v-aa77d95e";
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$a,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$a,
    false,
    undefined,
    undefined,
    undefined
  );

const TitleComponents = [__vue_component__$C, __vue_component__$B, __vue_component__$A, __vue_component__$z, __vue_component__$y, __vue_component__$x, __vue_component__$w, __vue_component__$v, __vue_component__$u, __vue_component__$t, __vue_component__$s, __vue_component__$r, __vue_component__$q, __vue_component__$p, __vue_component__$o, __vue_component__$n, __vue_component__$m, __vue_component__$l, __vue_component__$k, __vue_component__$j, __vue_component__$i, __vue_component__$h, __vue_component__$g, __vue_component__$f, __vue_component__$e, __vue_component__$d, __vue_component__$c, __vue_component__$b, __vue_component__$a];
var xmlSplitlineLibrary = {
  // install,
  ...TitleComponents
};

//
//
//
//
//
//
//
//
//
//
//

var script$9 = {
  name: 'xmlPopup',
  props: {
    position: {
      //弹窗方向top、bottom、left、right、center
      type: String,
      default: 'bottom'
    },
    round: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.showPopup = true;
      } else {
        setTimeout(() => {
          this.showPopup = false;
        }, 300);
      }
    }
  },
  data() {
    return {
      showPopup: false,
      show: false
    };
  },
  computed: {
    styleData() {
      let data = {};
      if (this.position == 'top') {
        data = {
          'align-items': 'center'
        };
      } else if (this.position == 'bottom') {
        data = {
          'justify-content': 'flex-end',
          'align-items': 'cneter'
        };
      } else if (this.position == 'left') {
        data = {
          'justify-content': 'center'
        };
      } else if (this.position == 'right') {
        data = {
          'justify-content': 'center',
          'align-items': 'flex-end'
        };
      } else if (this.position == 'center') {
        data = {
          'justify-content': 'center',
          'align-items': 'center'
        };
      }
      return data;
    }
  },
  methods: {
    close() {
      this.$emit('close');
    }
  },
  created() {},
  mounted() {}
};

/* script */
const __vue_script__$9 = script$9;
/* template */
var __vue_render__$9 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "van-popup",
    {
      attrs: { round: _vm.round, position: _vm.position },
      on: { close: _vm.close },
      model: {
        value: _vm.show,
        callback: function ($$v) {
          _vm.show = $$v;
        },
        expression: "show",
      },
    },
    [
      _c("div", { staticClass: "xml-popup-content" }, [
        _c("div", { staticClass: "content" }, [_vm._t("default")], 2),
      ]),
    ]
  )
};
var __vue_staticRenderFns__$9 = [];
__vue_render__$9._withStripped = true;

  /* style */
  const __vue_inject_styles__$9 = undefined;
  /* scoped */
  const __vue_scope_id__$9 = "data-v-0b198cae";
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$9 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$9,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9,
    __vue_module_identifier__$9,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$8 = {
  name: 'xmlCollapse',
  props: {
    title: {
      type: String,
      default: ''
    },
    name: {
      type: Number,
      default: 0
    },
    current: {
      type: Number,
      default: 0
    },
    className: {
      type: String,
      default: ''
    }
  },
  data() {
    return {};
  },
  methods: {},
  created() {},
  mounted() {}
};

/* script */
const __vue_script__$8 = script$8;
/* template */
var __vue_render__$8 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-collapse-content" }, [
    _c("div", { staticClass: "xml-collapse-item" }, [
      _c(
        "div",
        {
          staticClass: "collapse-top",
          on: {
            click: function ($event) {
              $event.stopPropagation();
              return _vm.$emit(
                "collapseClick",
                _vm.name != _vm.current ? _vm.name : -1
              )
            },
          },
        },
        [
          _c("div", { staticClass: "collapse-title" }, [
            _vm._v(_vm._s(_vm.title)),
          ]),
          _vm._v(" "),
          _c("i", {
            staticClass: "el-icon-arrow-down",
            class: _vm.name == _vm.current ? "collapse" : "",
          }),
        ]
      ),
      _vm._v(" "),
      _vm.name == _vm.current
        ? _c(
            "div",
            { staticClass: "collapse-content", class: _vm.className },
            [_vm._t("default")],
            2
          )
        : _vm._e(),
    ]),
  ])
};
var __vue_staticRenderFns__$8 = [];
__vue_render__$8._withStripped = true;

  /* style */
  const __vue_inject_styles__$8 = undefined;
  /* scoped */
  const __vue_scope_id__$8 = "data-v-051dec8f";
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$8 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$8,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8,
    __vue_module_identifier__$8,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// 拖拽状态 true：下拉  false：上拉
let SCROLLSTATUS;
var script$7 = {
  name: 'xmlWrapperPage',
  props: {
    // 能够拖拽的最大距离
    maxDistance: {
      type: Number,
      default: 120
    },
    // 定义触发加载刷新事件的拉伸长度
    triggerDistance: {
      type: Number,
      default: 100
    },
    isFirstOne: {
      // 是否是第一章
      type: Boolean,
      default: false
    },
    isLastOne: {
      // 是否是最后一章
      type: Boolean,
      default: false
    },
    showToast: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      startLocation: '',
      // 记录触摸开始的位置
      moveDistance: 0,
      // 记录移动的位置
      isTransition: false,
      // 是否启动transition
      isRotate: false,
      // 是否选择箭头
      isShrinked: false,
      // 是否收缩完成
      isTop: false,
      isBottom: false,
      isFlippingPages: false,
      //是否可以翻页
      startTime: 0 //触摸开始时间
    };
  },
  mounted() {},
  methods: {
    // 获取手指触屏时的屏幕Y轴位置
    handlerTouchStart(e) {
      this.startTime = new Date().getTime();
      let xmlPreviewDom = document.getElementById('xmlPreviewDom');
      this.isTop = false;
      this.isBottom = false;
      this.isFlippingPages = true;
      if (xmlPreviewDom.scrollTop == 0) {
        this.isTop = true;
      }
      if (xmlPreviewDom.scrollHeight < xmlPreviewDom.scrollTop + xmlPreviewDom.offsetHeight + 2) {
        this.isBottom = true;
      }
      this.isTransition = false;
      this.startLocation = e.touches[0].pageY;
      // 重置箭头反转
      this.isRotate = false;
      // 重置箭头
      this.isShrinked = false;
      //   触摸500毫秒后，代表长按 禁用拖拽
      setTimeout(() => {
        this.isFlippingPages = false;
      }, 500);
    },
    // 获取手指移动的距离
    handlerTouchMove(e) {
      if (!this.isFlippingPages) return;
      if (this.moveDistance > this.maxDistance + 1 || -this.moveDistance > this.maxDistance + 1) {
        this.isRotate = true;
        return;
      }
      this.moveDistance = Math.floor((e.touches[0].pageY - this.startLocation) / 2);
      // console.log(this.moveDistance)
      if (this.moveDistance > 0 && this.isTop || this.moveDistance < 0 && this.isBottom) {
        this.$refs.scrollContainer.style.transform = `translateY(${this.moveDistance}px)`;
      } else {
        this.moveDistance = 0;
      }
    },
    // 获取手指松开的Y轴位置
    handlerTouchEnd(e) {
      // 记录拖拽状态是为上拉还是下拉
      SCROLLSTATUS = this.moveDistance > 0;
      this.isTransition = true; // 开启transition

      this.isFlippingPages = true;
      let endTime = new Date().getTime() - this.startTime;
      if (Math.abs(this.moveDistance) < this.triggerDistance || !this.isTop && !this.isBottom || endTime < 300 || this.isFirstOne && SCROLLSTATUS || this.isLastOne && !SCROLLSTATUS) {
        this.$refs.scrollContainer.style.transform = 'translateY(0px)';
        this.moveDistance = 0;
      } else {
        // 拖拽距离是否大于指定的触发长度
        // 容器位置恢复后触发
        setTimeout(() => {
          setTimeout(() => {
            this.moveDistance = 0;
            this.$refs.scrollContainer.style.transform = 'translateY(0px)';
          }, 200);
          this.shrinked();
        }, 700);
      }
    },
    // 容器恢复后的操作
    shrinked() {
      //   this.showToast(SCROLLSTATUS)
      if (SCROLLSTATUS) {
        // 下拉刷新业务逻辑
        // 已经恢复完，箭头转为加载
        this.isShrinked = true;
        this.$emit('previousChapter');
      } else {
        // 上拉加载业务逻辑
        this.$emit('nextChapter');
      }
    }
  }
};

/* script */
const __vue_script__$7 = script$7;
/* template */
var __vue_render__$7 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "xml-drag-and-drop-loading" }, [
    _c(
      "div",
      {
        ref: "scrollContainer",
        class: { transition: _vm.isTransition },
        attrs: { id: "xml-scroll-container" },
        on: {
          touchstart: function ($event) {
            $event.stopPropagation();
            return _vm.handlerTouchStart.apply(null, arguments)
          },
          touchmove: function ($event) {
            $event.stopPropagation();
            return _vm.handlerTouchMove.apply(null, arguments)
          },
          touchend: function ($event) {
            $event.stopPropagation();
            return _vm.handlerTouchEnd.apply(null, arguments)
          },
        },
      },
      [
        _c(
          "div",
          { staticClass: "xml-loading-text", attrs: { id: "up-loading-text" } },
          [
            _c(
              "span",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: !_vm.isTransition,
                    expression: "!isTransition",
                  },
                ],
              },
              [
                _vm._v(
                  _vm._s(
                    _vm.isFirstOne
                      ? "已经是第一章啦"
                      : _vm.triggerDistance < _vm.moveDistance
                      ? "释放即可加载上一章"
                      : "继续上拉加载上一章"
                  )
                ),
              ]
            ),
            _vm._v(" "),
            _c(
              "span",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.isTransition,
                    expression: "isTransition",
                  },
                ],
              },
              [_vm._v("加载中"), _c("i", { staticClass: "el-icon-loading" })]
            ),
          ]
        ),
        _vm._v(" "),
        _vm._t("default"),
        _vm._v(" "),
        _c(
          "div",
          {
            staticClass: "xml-loading-text",
            attrs: { id: "down-loading-text" },
          },
          [
            _c(
              "span",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: !_vm.isTransition,
                    expression: "!isTransition",
                  },
                ],
              },
              [
                _vm._v(
                  _vm._s(
                    _vm.isLastOne
                      ? "已经是最后一章啦"
                      : _vm.triggerDistance < -_vm.moveDistance
                      ? "释放即可加载下一章"
                      : "继续上拉加载下一章"
                  )
                ),
              ]
            ),
            _vm._v(" "),
            _c(
              "span",
              {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.isTransition,
                    expression: "isTransition",
                  },
                ],
              },
              [_vm._v("加载中"), _c("i", { staticClass: "el-icon-loading" })]
            ),
          ]
        ),
      ],
      2
    ),
  ])
};
var __vue_staticRenderFns__$7 = [];
__vue_render__$7._withStripped = true;

  /* style */
  const __vue_inject_styles__$7 = undefined;
  /* scoped */
  const __vue_scope_id__$7 = undefined;
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$7 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    __vue_script__$7,
    __vue_scope_id__$7,
    __vue_is_functional_template__$7,
    __vue_module_identifier__$7,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//

var script$6 = {
  name: 'xmlTextOverflow',
  props: {
    info: {
      type: String,
      default: ''
    },
    lineClamp: {
      type: Number,
      default: 3
    },
    hiddenBtn: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showall: false
    };
  },
  computed: {
    textStyleObject() {
      return {
        'max-height': this.showall ? 'none' : `${1.5 * this.lineClamp}em`
      };
    },
    textClass() {
      let cls = this.showall ? 'showall' : '';
      cls = cls + (this.hiddenBtn ? ' hidden-btn' : '');
      return cls;
    }
  },
  watch: {
    info() {
      this.showall = false;
    }
  }
};

/* script */
const __vue_script__$6 = script$6;
/* template */
var __vue_render__$6 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "dp-text-ellipsis-wrapper" }, [
    _c(
      "div",
      { staticClass: "text", class: _vm.textClass, style: _vm.textStyleObject },
      [
        _c("label", {
          class: _vm.info.length >= 48 ? "btn" : "",
          on: {
            click: function ($event) {
              $event.stopPropagation();
              _vm.showall = !_vm.showall;
            },
          },
        }),
        _vm._v("\n    " + _vm._s(_vm.info) + "\n  "),
      ]
    ),
  ])
};
var __vue_staticRenderFns__$6 = [];
__vue_render__$6._withStripped = true;

  /* style */
  const __vue_inject_styles__$6 = undefined;
  /* scoped */
  const __vue_scope_id__$6 = undefined;
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$6 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$6,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6,
    __vue_module_identifier__$6,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$5 = {
  name: 'xmlWatermarkBg',
  data() {
    return {
      div: null,
      base64: 'url("data:image/png;base64,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")'
    };
  },
  mounted() {
    this.resetWatermark();
    const ob = new MutationObserver(entries => {
      for (const entry of entries) {
        for (const node of entry.removedNodes) {
          if (node === this.div) {
            // 处理删除
            this.resetWatermark();
          }
        }
        if (entry.target === this.div) {
          // 处理修改
          this.resetWatermark();
        }
      }
    });
    ob.observe(this.$refs.xmlWatermarkBg, {
      childList: true,
      subtree: true,
      attributes: true
    });
  },
  methods: {
    resetWatermark() {
      if (!this.$refs.xmlWatermarkBg) {
        return;
      }
      if (this.div) {
        this.div.remove();
      }
      this.div = document.createElement('div');
      this.div.style.position = 'absolute';
      this.div.style.zIndex = 999;
      this.div.style.backgroundImage = this.base64;
      this.div.style.backgroundSize = '50%';
      this.div.style.backgroundRepeat = 'repeat';
      //   this.div.style.backgroundPositionY = 'calc(80vh - 100px)'
      this.div.style.pointerEvents = 'none';
      this.div.style.inset = 0;
      this.$refs.xmlWatermarkBg.appendChild(this.div);
    }
  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { ref: "xmlWatermarkBg" }, [_vm._t("default")], 2)
};
var __vue_staticRenderFns__$5 = [];
__vue_render__$5._withStripped = true;

  /* style */
  const __vue_inject_styles__$5 = undefined;
  /* scoped */
  const __vue_scope_id__$5 = undefined;
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$5,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$5,
    false,
    undefined,
    undefined,
    undefined
  );

const xmlView = [__vue_component__$9, __vue_component__$8, __vue_component__$2t, __vue_component__$7, __vue_component__$6, __vue_component__$5];
var xmlView$1 = {
  // install,
  ...xmlView
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$4 = {
  name: 'xml-hevue-img-preview',
  data() {
    return {
      // imgWidth: 0,
      // imgHeight: 0,
      visible: false,
      // 插件显示，默认为false
      imgScale: 1,
      imgTop: 0,
      imgLeft: 0,
      imgRotate: 0,
      isFull: false,
      maxWH: window.innerWidth < 1000 ? 'max-width:100%;max-height:100%;' : 'max-width:90%;max-height:86%;',
      clientX: 0,
      clientY: 0,
      imgIndex: 0,
      canRun: true,
      imgurl: '',
      imgState: 1,
      start: [{}, {}],
      mobileScale: 0,
      // 手指离开时图片的缩放比例
      // 以下内容为用户传入配置
      // show: true, // 插件显示，默认为false
      url: '',
      // 预览图片的地址
      nowImgIndex: 0,
      multiple: false,
      imgList: [],
      imgKey: 'url',
      titleKey: 'title',
      imgTitle: '',
      // 以下为可全局配置
      controlBar: true,
      closeBtn: true,
      arrowBtn: true,
      keyboard: false,
      clickMaskCLose: false,
      // 是否点击遮罩关闭，默认false
      closeFn: null,
      // 关闭回调函数
      changeFn: null // 切换图片回调函数
    };
  },
  mounted() {
    this.initImg();
  },
  watch: {
    url() {
      this.initImg();
    },
    visible: {
      handler(newV) {
        if (newV) {
          this.$nextTick(() => {
            let _dom = document.getElementById('hevue-imgpreview-wrap');
            _dom.onmousewheel = this.scrollFunc;
            // 火狐浏览器没有onmousewheel事件，用DOMMouseScroll代替(滚轮事件)
            document.body.addEventListener('DOMMouseScroll', this.scrollFunc);
            // 禁止火狐浏览器下拖拽图片的默认事件
            document.ondragstart = function () {
              return false;
            };
            // 判断是否多图
            if (this.multiple) {
              if (Array.isArray(this.imgList) && this.imgList.length > 0) {
                this.imgIndex = Number(this.nowImgIndex) || 0;
                // this.url = this.imgList[this.imgIndex]
                this.imgTitle = this.imgList[this.imgIndex][this.titleKey];
                this.changeUrl(this.imgList[this.imgIndex][this.imgKey], this.imgIndex);
              }
            } else {
              this.changeUrl(this.url);
            }
            // 判断是否开启键盘事件
            if (this.keyboard) {
              document.addEventListener('keydown', this.keyHandleDebounce);
            }
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    show() {
      this.visible = true;
    },
    close(data) {
      this.initImg();
      // this.maxWH = "max-width:100%;max-height:100%;";
      // this.isFull = false;
      // 移除火狐浏览器下的鼠标滚动事件
      document.body.removeEventListener('DOMMouseScroll', this.scrollFunc);
      //恢复火狐及Safari浏览器下的图片拖拽
      document.ondragstart = null;
      // 移除键盘事件
      if (this.keyboard) {
        document.removeEventListener('keydown', this.keyHandleDebounce);
      }
      this.visible = false;
      this.closeFn && this.closeFn(data);
    },
    initImg() {
      this.mobileScale = 1;
      this.imgScale = 1;
      this.imgRotate = 0;
      this.imgTop = 0;
      this.imgLeft = 0;
    },
    /**
     * 切换图片
     * true 下一张
     * false 上一张
     */
    toogleImg(bool, way) {
      let fromIndex = this.imgIndex;
      if (bool) {
        this.imgIndex++;
        if (this.imgIndex > this.imgList.length - 1) {
          this.imgIndex = 0;
        }
      } else {
        this.imgIndex--;
        if (this.imgIndex < 0) {
          this.imgIndex = this.imgList.length - 1;
        }
      }
      this.changeFn && this.changeFn({
        type: bool ? 'next' : 'prev',
        fromImgIndex: fromIndex,
        fromImgUrl: this.imgList[fromIndex],
        toImgIndex: this.imgIndex,
        toImgUrl: this.imgList[this.imgIndex],
        way
      });
      // this.url = this.imgList[this.imgIndex]
      this.imgTitle = this.imgList[this.imgIndex][this.titleKey];
      this.changeUrl(this.imgList[this.imgIndex][this.imgKey], this.imgIndex);
    },
    // 改变图片地址
    /**
     * @description:
     * @param {String} url 要显示的图片的url
     * @param {Number} index 当前显示当图片下标，防止用户点击切换图片过快
     * @return {*}
     */
    changeUrl(url, index) {
      this.imgState = 1;
      let img = new Image();
      img.src = url;
      img.onload = () => {
        // 如果加载出来图片当下标不是当前显示图片当下标，则不予显示（用户点击过快当时候，会出现用户点到第三张了，此时第一张图片才加载完当情况）
        if (index != undefined && index == this.imgIndex) {
          this.imgState = 2;
          this.imgurl = url;
        } else if (index == undefined) {
          this.imgState = 2;
          this.imgurl = url;
        }
      };
      img.onerror = () => {
        if (index != undefined && index == this.imgIndex) {
          this.imgState = 3;
        } else if (index == undefined) {
          this.imgState = 3;
        }
      };
    },
    // 旋转图片
    rotateFunc(deg) {
      this.imgRotate += deg;
    },
    // 图片缩放
    scaleFunc(num, bool) {
      if (this.imgScale <= 0.2 && num < 0) return;
      if (bool) {
        this.imgScale = num;
      } else {
        this.imgScale += num;
      }
    },
    // 图片原尺寸切换
    imgToggle() {
      this.initImg();
      if (this.isFull) {
        this.maxWH = window.innerWidth < 1000 ? 'max-width:100%;max-height:100%;' : 'max-width:90%;max-height:86%;';
      } else {
        this.maxWH = '';
      }
      this.isFull = !this.isFull;
    },
    // 鼠标滚轮缩放
    scrollFunc(e) {
      e = e || window.event;
      // e.returnValue = false // ie
      // 火狐下没有wheelDelta，用detail代替，由于detail值的正负和wheelDelta相反，所以取反
      e.delta = e.wheelDelta || -e.detail;
      e.preventDefault();
      if (e.delta > 0) {
        //当滑轮向上滚动时
        this.scaleFunc(0.05);
      }
      if (e.delta < 0) {
        //当滑轮向下滚动时
        this.scaleFunc(-0.05);
      }
    },
    // 鼠标按下
    addMove(e) {
      e = e || window.event;
      this.clientX = e.clientX;
      this.clientY = e.clientY;
      this.$refs.heImg.onmousemove = this.moveFunc;
    },
    // 手指按下事件
    addMoveMobile(e) {
      e.preventDefault();
      e = e || window.event;
      if (e.touches.length > 1) {
        this.start = e.touches;
      } else {
        this.clientX = e.touches[0].pageX;
        this.clientY = e.touches[0].pageY;
      }
      // 添加手指拖动事件
      this.$refs.heImg.ontouchmove = this.moveFuncMobile;
    },
    // 鼠标拖动
    moveFunc(e) {
      e = e || window.event;
      e.preventDefault();
      let movementX = e.clientX - this.clientX;
      let movementY = e.clientY - this.clientY;
      // event.clientY;
      this.imgLeft += movementX * 2;
      this.imgTop += movementY * 2;
      this.clientX = e.clientX;
      this.clientY = e.clientY;
    },
    // 手指拖动
    moveFuncMobile(e) {
      e = e || window.event;
      if (e.touches.length > 1) {
        var now = e.touches;
        var scale = this.getDistance(now[0], now[1]) / this.getDistance(this.start[0], this.start[1]);
        // 判断是否手指缩放过，如果缩放过，要在上次缩放的比例基础上进行缩放
        if (this.mobileScale) {
          if (scale > 1) {
            // 放大
            this.scaleFunc(scale + this.mobileScale - 1, true);
          } else {
            // 缩小
            this.scaleFunc(scale * this.mobileScale, true);
          }
        } else {
          this.scaleFunc(scale, true);
        }
      } else {
        let touch = e.touches[0];
        e.preventDefault();
        let movementX = touch.pageX - this.clientX;
        let movementY = touch.pageY - this.clientY;
        // event.clientY;
        this.imgLeft += movementX * 2;
        this.imgTop += movementY * 2;
        this.clientX = touch.pageX;
        this.clientY = touch.pageY;
      }
    },
    // 移除拖动事件
    removeMove(type) {
      if (type === 'pc') {
        this.$refs.heImg.onmousemove = null;
      } else {
        this.mobileScale = this.imgScale;
        this.$refs.heImg.ontouchmove = null;
      }
    },
    keyHandleDebounce(e) {
      if (this.canRun) {
        // 如果this.canRun为true证明当前可以执行函数
        this.keyHandle(e);
        this.canRun = false; // 执行函数后一段时间内不可再次执行
        setTimeout(() => {
          this.canRun = true; // 等到了我们设定的时间之后，把this.canRun改为true，可以再次执行函数
        }, 300);
      }
    },
    // 键盘事件
    keyHandle(e) {
      e = window.event || e;
      var key = e.keyCode || e.which || e.charCode;
      switch (key) {
        case 27:
          // esc
          this.close({
            way: 'esc'
          });
          break;
        case 65:
          // a键-上一张
          if (this.multiple) {
            this.toogleImg(false, 'key-a');
          }
          break;
        case 68:
          // d键-下一张
          if (this.multiple) {
            this.toogleImg(true, 'key-d');
          }
          break;
        case 87:
          // w键-放大
          this.scaleFunc(0.15);
          break;
        case 83:
          // s键-缩小
          this.scaleFunc(-0.15);
          break;
        case 81:
          // q键-逆时针旋转
          this.rotateFunc(-90);
          break;
        case 69:
          // e键-顺时针旋转
          this.rotateFunc(90);
          break;
        case 82:
          // r键-复位键
          this.initImg();
          break;
      }
    },
    // 点击遮罩层
    clickMask() {
      if (this.clickMaskCLose) {
        this.close({
          way: 'mask'
        });
      }
    },
    //缩放 勾股定理方法-求两点之间的距离
    getDistance(p1, p2) {
      var x = p2.pageX - p1.pageX,
        y = p2.pageY - p1.pageY;
      return Math.sqrt(x * x + y * y);
    },
    /**
     * @description:
     * @param {String} imgsrc
     * @param {*} name
     * @return {*}
     */
    downloadIamge() {
      //下载图片地址和图片名
      let image = new Image();
      // 解决跨域 Canvas 污染问题
      image.setAttribute('crossOrigin', 'anonymous');
      image.onload = function () {
        let canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        let context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, image.width, image.height);
        let url = canvas.toDataURL('image/png'); //得到图片的base64编码数据
        let a = document.createElement('a'); // 生成一个a元素
        let event = new MouseEvent('click'); // 创建一个单击事件
        a.download = 'photo' + +new Date(); // 设置图片名称
        a.href = url; // 将生成的URL设置为a.href属性
        a.dispatchEvent(event); // 触发a的单击事件
      };
      image.onerror = function (err) {
        console.log('图片信息不正确或图片服务器禁止访问');
        console.log(err);
      };
      if (this.multiple) {
        image.src = this.imgList[this.imgIndex];
      } else {
        image.src = this.url;
      }
    }
  }
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("transition", { attrs: { name: "fade" } }, [
    _vm.visible
      ? _c(
          "div",
          {
            ref: "heImg",
            staticClass: "xml-hevue-imgpreview-wrap",
            attrs: { id: "hevue-imgpreview-wrap" },
            on: {
              mouseup: function ($event) {
                return _vm.removeMove("pc")
              },
              touchend: function ($event) {
                return _vm.removeMove("mobile")
              },
              click: function ($event) {
                $event.stopPropagation();
                return _vm.clickMask.apply(null, arguments)
              },
            },
          },
          [
            _c("div", { staticClass: "he-img-wrap" }, [
              _c("div", { staticClass: "he-control-title" }, [
                _vm._v(_vm._s(_vm.imgTitle)),
              ]),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.imgState === 1,
                      expression: "imgState === 1",
                    },
                  ],
                  staticClass:
                    "heimgfont hevue-img-status-icon rotate-animation",
                },
                [_vm._v("")]
              ),
              _vm._v(" "),
              _c("img", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.imgState === 2,
                    expression: "imgState === 2",
                  },
                ],
                ref: "heImView",
                staticClass: "he-img-view",
                style:
                  "background-color: #fff;" +
                  "transform: scale(" +
                  _vm.imgScale +
                  ") rotate(" +
                  _vm.imgRotate +
                  "deg);margin-top:" +
                  _vm.imgTop +
                  "px;margin-left:" +
                  _vm.imgLeft +
                  "px;" +
                  _vm.maxWH,
                attrs: { src: _vm.imgurl },
                on: {
                  click: function ($event) {
                    $event.stopPropagation();
                  },
                  mousedown: _vm.addMove,
                  touchstart: _vm.addMoveMobile,
                },
              }),
              _vm._v(" "),
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.imgState === 3,
                      expression: "imgState === 3",
                    },
                  ],
                  staticClass: "heimgfont hevue-img-status-icon",
                },
                [_vm._v("")]
              ),
              _vm._v(" "),
              _vm.closeBtn
                ? _c(
                    "div",
                    {
                      staticClass: "heimgfont he-close-icon",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.close({ way: "closeBtn" })
                        },
                      },
                    },
                    [_vm._v("")]
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.arrowBtn && _vm.multiple && _vm.imgList.length > 1
                ? _c(
                    "div",
                    {
                      staticClass: "arrow arrow-left heimgfont",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.toogleImg(false, "btn")
                        },
                      },
                    },
                    [_vm._v("\n        \n      ")]
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.arrowBtn && _vm.multiple && _vm.imgList.length > 1
                ? _c(
                    "div",
                    {
                      staticClass: "arrow arrow-right heimgfont",
                      on: {
                        click: function ($event) {
                          $event.stopPropagation();
                          return _vm.toogleImg(true, "btn")
                        },
                      },
                    },
                    [_vm._v("\n        \n      ")]
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.controlBar
                ? _c("div", { staticClass: "he-control-bar-wrap" }, [
                    _c(
                      "div",
                      {
                        staticClass: "he-control-bar",
                        on: {
                          click: function ($event) {
                            $event.stopPropagation();
                          },
                        },
                      },
                      [
                        _c(
                          "div",
                          {
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.scaleFunc(-0.15)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.scaleFunc(0.15)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: _vm.isFull,
                                expression: "isFull",
                              },
                            ],
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.imgToggle.apply(null, arguments)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: !_vm.isFull,
                                expression: "!isFull",
                              },
                            ],
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.imgToggle.apply(null, arguments)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.rotateFunc(-90)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            staticClass: "he-control-btn heimgfont",
                            on: {
                              click: function ($event) {
                                $event.stopPropagation();
                                return _vm.rotateFunc(90)
                              },
                            },
                          },
                          [_vm._v("")]
                        ),
                      ]
                    ),
                  ])
                : _vm._e(),
            ]),
          ]
        )
      : _vm._e(),
  ])
};
var __vue_staticRenderFns__$4 = [];
__vue_render__$4._withStripped = true;

  /* style */
  const __vue_inject_styles__$4 = undefined;
  /* scoped */
  const __vue_scope_id__$4 = undefined;
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$4,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4,
    __vue_module_identifier__$4,
    false,
    undefined,
    undefined,
    undefined
  );

/*
 * @Author: 贺永胜
 * @Date: 2020-04-22 15:40:42
 * @公众号: 百里青山
 * @LastEditors: 贺永胜
 * @LastEditTime: 2022-07-21 16:21:47
 * @Descripttion: 
 */
const install$1 = async (app, opts = {}) => {
  app.version.split(".")[0];
  // if (vueVersion > 2) {
  //   let {createApp} = await import("vue");
  //   imgApp = createApp(VueToast)
  //   app.config.globalProperties.$hevueImgPreview = ImgPreview;
  // } else {
  // let _vue = await (await import("vue")).default;
  // imgApp = _vue.extend(VueToast)
  // _vue.prototype.$hevueImgPreview = ImgPreview;
  // }    
};
if (typeof window !== "undefined" && window.Vue) {
  install$1(window.Vue);
}

/**
 * @license
 * Copyright 2010-2024 Three.js Authors
 * SPDX-License-Identifier: MIT
 */const REVISION='168';const MOUSE={LEFT:0,MIDDLE:1,RIGHT:2,ROTATE:0,DOLLY:1,PAN:2};const TOUCH={ROTATE:0,PAN:1,DOLLY_PAN:2,DOLLY_ROTATE:3};const CullFaceNone=0;const CullFaceBack=1;const CullFaceFront=2;const PCFShadowMap=1;const PCFSoftShadowMap=2;const VSMShadowMap=3;const FrontSide$1=0;const BackSide$1=1;const DoubleSide=2;const NoBlending=0;const NormalBlending$1=1;const AdditiveBlending$1=2;const SubtractiveBlending=3;const MultiplyBlending=4;const CustomBlending=5;const AddEquation$1=100;const SubtractEquation=101;const ReverseSubtractEquation=102;const MinEquation=103;const MaxEquation=104;const ZeroFactor=200;const OneFactor=201;const SrcColorFactor=202;const OneMinusSrcColorFactor=203;const SrcAlphaFactor$1=204;const OneMinusSrcAlphaFactor$1=205;const DstAlphaFactor=206;const OneMinusDstAlphaFactor=207;const DstColorFactor=208;const OneMinusDstColorFactor=209;const SrcAlphaSaturateFactor=210;const ConstantColorFactor=211;const OneMinusConstantColorFactor=212;const ConstantAlphaFactor=213;const OneMinusConstantAlphaFactor=214;const NeverDepth=0;const AlwaysDepth=1;const LessDepth=2;const LessEqualDepth$1=3;const EqualDepth=4;const GreaterEqualDepth=5;const GreaterDepth=6;const NotEqualDepth=7;const MultiplyOperation$1=0;const MixOperation=1;const AddOperation=2;const NoToneMapping$1=0;const LinearToneMapping=1;const ReinhardToneMapping=2;const CineonToneMapping=3;const ACESFilmicToneMapping=4;const CustomToneMapping=5;const AgXToneMapping=6;const NeutralToneMapping=7;const AttachedBindMode='attached';const DetachedBindMode='detached';const UVMapping$1=300;const CubeReflectionMapping$1=301;const CubeRefractionMapping$1=302;const EquirectangularReflectionMapping=303;const EquirectangularRefractionMapping=304;const CubeUVReflectionMapping=306;const RepeatWrapping$1=1000;const ClampToEdgeWrapping$1=1001;const MirroredRepeatWrapping$1=1002;const NearestFilter$1=1003;const NearestMipmapNearestFilter=1004;const NearestMipmapLinearFilter=1005;const LinearFilter$1=1006;const LinearMipmapNearestFilter=1007;const LinearMipmapLinearFilter$1=1008;const UnsignedByteType$1=1009;const ByteType=1010;const ShortType=1011;const UnsignedShortType=1012;const IntType$1=1013;const UnsignedIntType$1=1014;const FloatType$1=1015;const HalfFloatType$1=1016;const UnsignedShort4444Type=1017;const UnsignedShort5551Type=1018;const UnsignedInt248Type$1=1020;const UnsignedInt5999Type=35902;const AlphaFormat=1021;const RGBFormat=1022;const RGBAFormat$1=1023;const LuminanceFormat=1024;const LuminanceAlphaFormat=1025;const DepthFormat$1=1026;const DepthStencilFormat$1=1027;const RedFormat=1028;const RedIntegerFormat=1029;const RGFormat=1030;const RGIntegerFormat=1031;const RGBAIntegerFormat=1033;const RGB_S3TC_DXT1_Format=33776;const RGBA_S3TC_DXT1_Format=33777;const RGBA_S3TC_DXT3_Format=33778;const RGBA_S3TC_DXT5_Format=33779;const RGB_PVRTC_4BPPV1_Format=35840;const RGB_PVRTC_2BPPV1_Format=35841;const RGBA_PVRTC_4BPPV1_Format=35842;const RGBA_PVRTC_2BPPV1_Format=35843;const RGB_ETC1_Format=36196;const RGB_ETC2_Format=37492;const RGBA_ETC2_EAC_Format=37496;const RGBA_ASTC_4x4_Format=37808;const RGBA_ASTC_5x4_Format=37809;const RGBA_ASTC_5x5_Format=37810;const RGBA_ASTC_6x5_Format=37811;const RGBA_ASTC_6x6_Format=37812;const RGBA_ASTC_8x5_Format=37813;const RGBA_ASTC_8x6_Format=37814;const RGBA_ASTC_8x8_Format=37815;const RGBA_ASTC_10x5_Format=37816;const RGBA_ASTC_10x6_Format=37817;const RGBA_ASTC_10x8_Format=37818;const RGBA_ASTC_10x10_Format=37819;const RGBA_ASTC_12x10_Format=37820;const RGBA_ASTC_12x12_Format=37821;const RGBA_BPTC_Format=36492;const RGB_BPTC_SIGNED_Format=36494;const RGB_BPTC_UNSIGNED_Format=36495;const RED_RGTC1_Format=36283;const SIGNED_RED_RGTC1_Format=36284;const RED_GREEN_RGTC2_Format=36285;const SIGNED_RED_GREEN_RGTC2_Format=36286;const InterpolateDiscrete=2300;const InterpolateLinear=2301;const InterpolateSmooth=2302;const ZeroCurvatureEnding=2400;const ZeroSlopeEnding=2401;const WrapAroundEnding=2402;const NormalAnimationBlendMode=2500;const TrianglesDrawMode=0;const TriangleStripDrawMode=1;const TriangleFanDrawMode=2;const BasicDepthPacking=3200;const RGBADepthPacking=3201;const TangentSpaceNormalMap$1=0;const ObjectSpaceNormalMap$1=1;// Color space string identifiers, matching CSS Color Module Level 4 and WebGPU names where available.
const NoColorSpace$1='';const SRGBColorSpace$1='srgb';const LinearSRGBColorSpace$1='srgb-linear';const DisplayP3ColorSpace$1='display-p3';const LinearDisplayP3ColorSpace$1='display-p3-linear';const LinearTransfer$1='linear';const SRGBTransfer$1='srgb';const Rec709Primaries$1='rec709';const P3Primaries$1='p3';const KeepStencilOp$1=7680;const AlwaysStencilFunc$1=519;const NeverCompare=512;const LessCompare=513;const EqualCompare=514;const LessEqualCompare=515;const GreaterCompare=516;const NotEqualCompare=517;const GreaterEqualCompare=518;const AlwaysCompare=519;const StaticDrawUsage$1=35044;const GLSL3='300 es';const WebGLCoordinateSystem$1=2000;const WebGPUCoordinateSystem$1=2001;/**
 * https://github.com/mrdoob/eventdispatcher.js/
 */class EventDispatcher$1{addEventListener(type,listener){if(this._listeners===undefined)this._listeners={};const listeners=this._listeners;if(listeners[type]===undefined){listeners[type]=[];}if(listeners[type].indexOf(listener)===-1){listeners[type].push(listener);}}hasEventListener(type,listener){if(this._listeners===undefined)return false;const listeners=this._listeners;return listeners[type]!==undefined&&listeners[type].indexOf(listener)!==-1;}removeEventListener(type,listener){if(this._listeners===undefined)return;const listeners=this._listeners;const listenerArray=listeners[type];if(listenerArray!==undefined){const index=listenerArray.indexOf(listener);if(index!==-1){listenerArray.splice(index,1);}}}dispatchEvent(event){if(this._listeners===undefined)return;const listeners=this._listeners;const listenerArray=listeners[event.type];if(listenerArray!==undefined){event.target=this;// Make a copy, in case listeners are removed while iterating.
const array=listenerArray.slice(0);for(let i=0,l=array.length;i<l;i++){array[i].call(this,event);}event.target=null;}}}const _lut$1=['00','01','02','03','04','05','06','07','08','09','0a','0b','0c','0d','0e','0f','10','11','12','13','14','15','16','17','18','19','1a','1b','1c','1d','1e','1f','20','21','22','23','24','25','26','27','28','29','2a','2b','2c','2d','2e','2f','30','31','32','33','34','35','36','37','38','39','3a','3b','3c','3d','3e','3f','40','41','42','43','44','45','46','47','48','49','4a','4b','4c','4d','4e','4f','50','51','52','53','54','55','56','57','58','59','5a','5b','5c','5d','5e','5f','60','61','62','63','64','65','66','67','68','69','6a','6b','6c','6d','6e','6f','70','71','72','73','74','75','76','77','78','79','7a','7b','7c','7d','7e','7f','80','81','82','83','84','85','86','87','88','89','8a','8b','8c','8d','8e','8f','90','91','92','93','94','95','96','97','98','99','9a','9b','9c','9d','9e','9f','a0','a1','a2','a3','a4','a5','a6','a7','a8','a9','aa','ab','ac','ad','ae','af','b0','b1','b2','b3','b4','b5','b6','b7','b8','b9','ba','bb','bc','bd','be','bf','c0','c1','c2','c3','c4','c5','c6','c7','c8','c9','ca','cb','cc','cd','ce','cf','d0','d1','d2','d3','d4','d5','d6','d7','d8','d9','da','db','dc','dd','de','df','e0','e1','e2','e3','e4','e5','e6','e7','e8','e9','ea','eb','ec','ed','ee','ef','f0','f1','f2','f3','f4','f5','f6','f7','f8','f9','fa','fb','fc','fd','fe','ff'];let _seed$1=1234567;const DEG2RAD$1=Math.PI/180;const RAD2DEG$1=180/Math.PI;// http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
function generateUUID$1(){const d0=Math.random()*0xffffffff|0;const d1=Math.random()*0xffffffff|0;const d2=Math.random()*0xffffffff|0;const d3=Math.random()*0xffffffff|0;const uuid=_lut$1[d0&0xff]+_lut$1[d0>>8&0xff]+_lut$1[d0>>16&0xff]+_lut$1[d0>>24&0xff]+'-'+_lut$1[d1&0xff]+_lut$1[d1>>8&0xff]+'-'+_lut$1[d1>>16&0x0f|0x40]+_lut$1[d1>>24&0xff]+'-'+_lut$1[d2&0x3f|0x80]+_lut$1[d2>>8&0xff]+'-'+_lut$1[d2>>16&0xff]+_lut$1[d2>>24&0xff]+_lut$1[d3&0xff]+_lut$1[d3>>8&0xff]+_lut$1[d3>>16&0xff]+_lut$1[d3>>24&0xff];// .toLowerCase() here flattens concatenated strings to save heap memory space.
return uuid.toLowerCase();}function clamp$2(value,min,max){return Math.max(min,Math.min(max,value));}// compute euclidean modulo of m % n
// https://en.wikipedia.org/wiki/Modulo_operation
function euclideanModulo$1(n,m){return (n%m+m)%m;}// Linear mapping from range <a1, a2> to range <b1, b2>
function mapLinear$1(x,a1,a2,b1,b2){return b1+(x-a1)*(b2-b1)/(a2-a1);}// https://www.gamedev.net/tutorials/programming/general-and-gameplay-programming/inverse-lerp-a-super-useful-yet-often-overlooked-function-r5230/
function inverseLerp$1(x,y,value){if(x!==y){return (value-x)/(y-x);}else {return 0;}}// https://en.wikipedia.org/wiki/Linear_interpolation
function lerp$1(x,y,t){return (1-t)*x+t*y;}// http://www.rorydriscoll.com/2016/03/07/frame-rate-independent-damping-using-lerp/
function damp$1(x,y,lambda,dt){return lerp$1(x,y,1-Math.exp(-lambda*dt));}// https://www.desmos.com/calculator/vcsjnyz7x4
function pingpong$1(x,length=1){return length-Math.abs(euclideanModulo$1(x,length*2)-length);}// http://en.wikipedia.org/wiki/Smoothstep
function smoothstep$2(x,min,max){if(x<=min)return 0;if(x>=max)return 1;x=(x-min)/(max-min);return x*x*(3-2*x);}function smootherstep$1(x,min,max){if(x<=min)return 0;if(x>=max)return 1;x=(x-min)/(max-min);return x*x*x*(x*(x*6-15)+10);}// Random integer from <low, high> interval
function randInt$1(low,high){return low+Math.floor(Math.random()*(high-low+1));}// Random float from <low, high> interval
function randFloat$1(low,high){return low+Math.random()*(high-low);}// Random float from <-range/2, range/2> interval
function randFloatSpread$1(range){return range*(0.5-Math.random());}// Deterministic pseudo-random float in the interval [ 0, 1 ]
function seededRandom$1(s){if(s!==undefined)_seed$1=s;// Mulberry32 generator
let t=_seed$1+=0x6D2B79F5;t=Math.imul(t^t>>>15,t|1);t^=t+Math.imul(t^t>>>7,t|61);return ((t^t>>>14)>>>0)/4294967296;}function degToRad$1(degrees){return degrees*DEG2RAD$1;}function radToDeg$1(radians){return radians*RAD2DEG$1;}function isPowerOfTwo$1(value){return (value&value-1)===0&&value!==0;}function ceilPowerOfTwo$1(value){return Math.pow(2,Math.ceil(Math.log(value)/Math.LN2));}function floorPowerOfTwo$1(value){return Math.pow(2,Math.floor(Math.log(value)/Math.LN2));}function setQuaternionFromProperEuler$1(q,a,b,c,order){// Intrinsic Proper Euler Angles - see https://en.wikipedia.org/wiki/Euler_angles
// rotations are applied to the axes in the order specified by 'order'
// rotation by angle 'a' is applied first, then by angle 'b', then by angle 'c'
// angles are in radians
const cos=Math.cos;const sin=Math.sin;const c2=cos(b/2);const s2=sin(b/2);const c13=cos((a+c)/2);const s13=sin((a+c)/2);const c1_3=cos((a-c)/2);const s1_3=sin((a-c)/2);const c3_1=cos((c-a)/2);const s3_1=sin((c-a)/2);switch(order){case'XYX':q.set(c2*s13,s2*c1_3,s2*s1_3,c2*c13);break;case'YZY':q.set(s2*s1_3,c2*s13,s2*c1_3,c2*c13);break;case'ZXZ':q.set(s2*c1_3,s2*s1_3,c2*s13,c2*c13);break;case'XZX':q.set(c2*s13,s2*s3_1,s2*c3_1,c2*c13);break;case'YXY':q.set(s2*c3_1,c2*s13,s2*s3_1,c2*c13);break;case'ZYZ':q.set(s2*s3_1,s2*c3_1,c2*s13,c2*c13);break;default:console.warn('THREE.MathUtils: .setQuaternionFromProperEuler() encountered an unknown order: '+order);}}function denormalize$1(value,array){switch(array.constructor){case Float32Array:return value;case Uint32Array:return value/4294967295.0;case Uint16Array:return value/65535.0;case Uint8Array:return value/255.0;case Int32Array:return Math.max(value/2147483647.0,-1.0);case Int16Array:return Math.max(value/32767.0,-1.0);case Int8Array:return Math.max(value/127.0,-1.0);default:throw new Error('Invalid component type.');}}function normalize$2(value,array){switch(array.constructor){case Float32Array:return value;case Uint32Array:return Math.round(value*4294967295.0);case Uint16Array:return Math.round(value*65535.0);case Uint8Array:return Math.round(value*255.0);case Int32Array:return Math.round(value*2147483647.0);case Int16Array:return Math.round(value*32767.0);case Int8Array:return Math.round(value*127.0);default:throw new Error('Invalid component type.');}}const MathUtils$1={DEG2RAD:DEG2RAD$1,RAD2DEG:RAD2DEG$1,generateUUID:generateUUID$1,clamp:clamp$2,euclideanModulo:euclideanModulo$1,mapLinear:mapLinear$1,inverseLerp:inverseLerp$1,lerp:lerp$1,damp:damp$1,pingpong:pingpong$1,smoothstep:smoothstep$2,smootherstep:smootherstep$1,randInt:randInt$1,randFloat:randFloat$1,randFloatSpread:randFloatSpread$1,seededRandom:seededRandom$1,degToRad:degToRad$1,radToDeg:radToDeg$1,isPowerOfTwo:isPowerOfTwo$1,ceilPowerOfTwo:ceilPowerOfTwo$1,floorPowerOfTwo:floorPowerOfTwo$1,setQuaternionFromProperEuler:setQuaternionFromProperEuler$1,normalize:normalize$2,denormalize:denormalize$1};class Vector2$1{constructor(x=0,y=0){Vector2$1.prototype.isVector2=true;this.x=x;this.y=y;}get width(){return this.x;}set width(value){this.x=value;}get height(){return this.y;}set height(value){this.y=value;}set(x,y){this.x=x;this.y=y;return this;}setScalar(scalar){this.x=scalar;this.y=scalar;return this;}setX(x){this.x=x;return this;}setY(y){this.y=y;return this;}setComponent(index,value){switch(index){case 0:this.x=value;break;case 1:this.y=value;break;default:throw new Error('index is out of range: '+index);}return this;}getComponent(index){switch(index){case 0:return this.x;case 1:return this.y;default:throw new Error('index is out of range: '+index);}}clone(){return new this.constructor(this.x,this.y);}copy(v){this.x=v.x;this.y=v.y;return this;}add(v){this.x+=v.x;this.y+=v.y;return this;}addScalar(s){this.x+=s;this.y+=s;return this;}addVectors(a,b){this.x=a.x+b.x;this.y=a.y+b.y;return this;}addScaledVector(v,s){this.x+=v.x*s;this.y+=v.y*s;return this;}sub(v){this.x-=v.x;this.y-=v.y;return this;}subScalar(s){this.x-=s;this.y-=s;return this;}subVectors(a,b){this.x=a.x-b.x;this.y=a.y-b.y;return this;}multiply(v){this.x*=v.x;this.y*=v.y;return this;}multiplyScalar(scalar){this.x*=scalar;this.y*=scalar;return this;}divide(v){this.x/=v.x;this.y/=v.y;return this;}divideScalar(scalar){return this.multiplyScalar(1/scalar);}applyMatrix3(m){const x=this.x,y=this.y;const e=m.elements;this.x=e[0]*x+e[3]*y+e[6];this.y=e[1]*x+e[4]*y+e[7];return this;}min(v){this.x=Math.min(this.x,v.x);this.y=Math.min(this.y,v.y);return this;}max(v){this.x=Math.max(this.x,v.x);this.y=Math.max(this.y,v.y);return this;}clamp(min,max){// assumes min < max, componentwise
this.x=Math.max(min.x,Math.min(max.x,this.x));this.y=Math.max(min.y,Math.min(max.y,this.y));return this;}clampScalar(minVal,maxVal){this.x=Math.max(minVal,Math.min(maxVal,this.x));this.y=Math.max(minVal,Math.min(maxVal,this.y));return this;}clampLength(min,max){const length=this.length();return this.divideScalar(length||1).multiplyScalar(Math.max(min,Math.min(max,length)));}floor(){this.x=Math.floor(this.x);this.y=Math.floor(this.y);return this;}ceil(){this.x=Math.ceil(this.x);this.y=Math.ceil(this.y);return this;}round(){this.x=Math.round(this.x);this.y=Math.round(this.y);return this;}roundToZero(){this.x=Math.trunc(this.x);this.y=Math.trunc(this.y);return this;}negate(){this.x=-this.x;this.y=-this.y;return this;}dot(v){return this.x*v.x+this.y*v.y;}cross(v){return this.x*v.y-this.y*v.x;}lengthSq(){return this.x*this.x+this.y*this.y;}length(){return Math.sqrt(this.x*this.x+this.y*this.y);}manhattanLength(){return Math.abs(this.x)+Math.abs(this.y);}normalize(){return this.divideScalar(this.length()||1);}angle(){// computes the angle in radians with respect to the positive x-axis
const angle=Math.atan2(-this.y,-this.x)+Math.PI;return angle;}angleTo(v){const denominator=Math.sqrt(this.lengthSq()*v.lengthSq());if(denominator===0)return Math.PI/2;const theta=this.dot(v)/denominator;// clamp, to handle numerical problems
return Math.acos(clamp$2(theta,-1,1));}distanceTo(v){return Math.sqrt(this.distanceToSquared(v));}distanceToSquared(v){const dx=this.x-v.x,dy=this.y-v.y;return dx*dx+dy*dy;}manhattanDistanceTo(v){return Math.abs(this.x-v.x)+Math.abs(this.y-v.y);}setLength(length){return this.normalize().multiplyScalar(length);}lerp(v,alpha){this.x+=(v.x-this.x)*alpha;this.y+=(v.y-this.y)*alpha;return this;}lerpVectors(v1,v2,alpha){this.x=v1.x+(v2.x-v1.x)*alpha;this.y=v1.y+(v2.y-v1.y)*alpha;return this;}equals(v){return v.x===this.x&&v.y===this.y;}fromArray(array,offset=0){this.x=array[offset];this.y=array[offset+1];return this;}toArray(array=[],offset=0){array[offset]=this.x;array[offset+1]=this.y;return array;}fromBufferAttribute(attribute,index){this.x=attribute.getX(index);this.y=attribute.getY(index);return this;}rotateAround(center,angle){const c=Math.cos(angle),s=Math.sin(angle);const x=this.x-center.x;const y=this.y-center.y;this.x=x*c-y*s+center.x;this.y=x*s+y*c+center.y;return this;}random(){this.x=Math.random();this.y=Math.random();return this;}*[Symbol.iterator](){yield this.x;yield this.y;}}class Matrix3$1{constructor(n11,n12,n13,n21,n22,n23,n31,n32,n33){Matrix3$1.prototype.isMatrix3=true;this.elements=[1,0,0,0,1,0,0,0,1];if(n11!==undefined){this.set(n11,n12,n13,n21,n22,n23,n31,n32,n33);}}set(n11,n12,n13,n21,n22,n23,n31,n32,n33){const te=this.elements;te[0]=n11;te[1]=n21;te[2]=n31;te[3]=n12;te[4]=n22;te[5]=n32;te[6]=n13;te[7]=n23;te[8]=n33;return this;}identity(){this.set(1,0,0,0,1,0,0,0,1);return this;}copy(m){const te=this.elements;const me=m.elements;te[0]=me[0];te[1]=me[1];te[2]=me[2];te[3]=me[3];te[4]=me[4];te[5]=me[5];te[6]=me[6];te[7]=me[7];te[8]=me[8];return this;}extractBasis(xAxis,yAxis,zAxis){xAxis.setFromMatrix3Column(this,0);yAxis.setFromMatrix3Column(this,1);zAxis.setFromMatrix3Column(this,2);return this;}setFromMatrix4(m){const me=m.elements;this.set(me[0],me[4],me[8],me[1],me[5],me[9],me[2],me[6],me[10]);return this;}multiply(m){return this.multiplyMatrices(this,m);}premultiply(m){return this.multiplyMatrices(m,this);}multiplyMatrices(a,b){const ae=a.elements;const be=b.elements;const te=this.elements;const a11=ae[0],a12=ae[3],a13=ae[6];const a21=ae[1],a22=ae[4],a23=ae[7];const a31=ae[2],a32=ae[5],a33=ae[8];const b11=be[0],b12=be[3],b13=be[6];const b21=be[1],b22=be[4],b23=be[7];const b31=be[2],b32=be[5],b33=be[8];te[0]=a11*b11+a12*b21+a13*b31;te[3]=a11*b12+a12*b22+a13*b32;te[6]=a11*b13+a12*b23+a13*b33;te[1]=a21*b11+a22*b21+a23*b31;te[4]=a21*b12+a22*b22+a23*b32;te[7]=a21*b13+a22*b23+a23*b33;te[2]=a31*b11+a32*b21+a33*b31;te[5]=a31*b12+a32*b22+a33*b32;te[8]=a31*b13+a32*b23+a33*b33;return this;}multiplyScalar(s){const te=this.elements;te[0]*=s;te[3]*=s;te[6]*=s;te[1]*=s;te[4]*=s;te[7]*=s;te[2]*=s;te[5]*=s;te[8]*=s;return this;}determinant(){const te=this.elements;const a=te[0],b=te[1],c=te[2],d=te[3],e=te[4],f=te[5],g=te[6],h=te[7],i=te[8];return a*e*i-a*f*h-b*d*i+b*f*g+c*d*h-c*e*g;}invert(){const te=this.elements,n11=te[0],n21=te[1],n31=te[2],n12=te[3],n22=te[4],n32=te[5],n13=te[6],n23=te[7],n33=te[8],t11=n33*n22-n32*n23,t12=n32*n13-n33*n12,t13=n23*n12-n22*n13,det=n11*t11+n21*t12+n31*t13;if(det===0)return this.set(0,0,0,0,0,0,0,0,0);const detInv=1/det;te[0]=t11*detInv;te[1]=(n31*n23-n33*n21)*detInv;te[2]=(n32*n21-n31*n22)*detInv;te[3]=t12*detInv;te[4]=(n33*n11-n31*n13)*detInv;te[5]=(n31*n12-n32*n11)*detInv;te[6]=t13*detInv;te[7]=(n21*n13-n23*n11)*detInv;te[8]=(n22*n11-n21*n12)*detInv;return this;}transpose(){let tmp;const m=this.elements;tmp=m[1];m[1]=m[3];m[3]=tmp;tmp=m[2];m[2]=m[6];m[6]=tmp;tmp=m[5];m[5]=m[7];m[7]=tmp;return this;}getNormalMatrix(matrix4){return this.setFromMatrix4(matrix4).invert().transpose();}transposeIntoArray(r){const m=this.elements;r[0]=m[0];r[1]=m[3];r[2]=m[6];r[3]=m[1];r[4]=m[4];r[5]=m[7];r[6]=m[2];r[7]=m[5];r[8]=m[8];return this;}setUvTransform(tx,ty,sx,sy,rotation,cx,cy){const c=Math.cos(rotation);const s=Math.sin(rotation);this.set(sx*c,sx*s,-sx*(c*cx+s*cy)+cx+tx,-sy*s,sy*c,-sy*(-s*cx+c*cy)+cy+ty,0,0,1);return this;}//
scale(sx,sy){this.premultiply(_m3$1.makeScale(sx,sy));return this;}rotate(theta){this.premultiply(_m3$1.makeRotation(-theta));return this;}translate(tx,ty){this.premultiply(_m3$1.makeTranslation(tx,ty));return this;}// for 2D Transforms
makeTranslation(x,y){if(x.isVector2){this.set(1,0,x.x,0,1,x.y,0,0,1);}else {this.set(1,0,x,0,1,y,0,0,1);}return this;}makeRotation(theta){// counterclockwise
const c=Math.cos(theta);const s=Math.sin(theta);this.set(c,-s,0,s,c,0,0,0,1);return this;}makeScale(x,y){this.set(x,0,0,0,y,0,0,0,1);return this;}//
equals(matrix){const te=this.elements;const me=matrix.elements;for(let i=0;i<9;i++){if(te[i]!==me[i])return false;}return true;}fromArray(array,offset=0){for(let i=0;i<9;i++){this.elements[i]=array[i+offset];}return this;}toArray(array=[],offset=0){const te=this.elements;array[offset]=te[0];array[offset+1]=te[1];array[offset+2]=te[2];array[offset+3]=te[3];array[offset+4]=te[4];array[offset+5]=te[5];array[offset+6]=te[6];array[offset+7]=te[7];array[offset+8]=te[8];return array;}clone(){return new this.constructor().fromArray(this.elements);}}const _m3$1=/*@__PURE__*/new Matrix3$1();function arrayNeedsUint32$1(array){// assumes larger values usually on last
for(let i=array.length-1;i>=0;--i){if(array[i]>=65535)return true;// account for PRIMITIVE_RESTART_FIXED_INDEX, #24565
}return false;}function createElementNS$1(name){return document.createElementNS('http://www.w3.org/1999/xhtml',name);}function createCanvasElement(){const canvas=createElementNS$1('canvas');canvas.style.display='block';return canvas;}const _cache$2={};function warnOnce$1(message){if(message in _cache$2)return;_cache$2[message]=true;console.warn(message);}function probeAsync(gl,sync,interval){return new Promise(function(resolve,reject){function probe(){switch(gl.clientWaitSync(sync,gl.SYNC_FLUSH_COMMANDS_BIT,0)){case gl.WAIT_FAILED:reject();break;case gl.TIMEOUT_EXPIRED:setTimeout(probe,interval);break;default:resolve();}}setTimeout(probe,interval);});}/**
 * Matrices converting P3 <-> Rec. 709 primaries, without gamut mapping
 * or clipping. Based on W3C specifications for sRGB and Display P3,
 * and ICC specifications for the D50 connection space. Values in/out
 * are _linear_ sRGB and _linear_ Display P3.
 *
 * Note that both sRGB and Display P3 use the sRGB transfer functions.
 *
 * Reference:
 * - http://www.russellcottrell.com/photo/matrixCalculator.htm
 */const LINEAR_SRGB_TO_LINEAR_DISPLAY_P3$1=/*@__PURE__*/new Matrix3$1().set(0.8224621,0.177538,0.0,0.0331941,0.9668058,0.0,0.0170827,0.0723974,0.9105199);const LINEAR_DISPLAY_P3_TO_LINEAR_SRGB$1=/*@__PURE__*/new Matrix3$1().set(1.2249401,-0.2249404,0.0,-0.0420569,1.0420571,0.0,-0.0196376,-0.0786361,1.0982735);/**
 * Defines supported color spaces by transfer function and primaries,
 * and provides conversions to/from the Linear-sRGB reference space.
 */const COLOR_SPACES$1={[LinearSRGBColorSpace$1]:{transfer:LinearTransfer$1,primaries:Rec709Primaries$1,luminanceCoefficients:[0.2126,0.7152,0.0722],toReference:color=>color,fromReference:color=>color},[SRGBColorSpace$1]:{transfer:SRGBTransfer$1,primaries:Rec709Primaries$1,luminanceCoefficients:[0.2126,0.7152,0.0722],toReference:color=>color.convertSRGBToLinear(),fromReference:color=>color.convertLinearToSRGB()},[LinearDisplayP3ColorSpace$1]:{transfer:LinearTransfer$1,primaries:P3Primaries$1,luminanceCoefficients:[0.2289,0.6917,0.0793],toReference:color=>color.applyMatrix3(LINEAR_DISPLAY_P3_TO_LINEAR_SRGB$1),fromReference:color=>color.applyMatrix3(LINEAR_SRGB_TO_LINEAR_DISPLAY_P3$1)},[DisplayP3ColorSpace$1]:{transfer:SRGBTransfer$1,primaries:P3Primaries$1,luminanceCoefficients:[0.2289,0.6917,0.0793],toReference:color=>color.convertSRGBToLinear().applyMatrix3(LINEAR_DISPLAY_P3_TO_LINEAR_SRGB$1),fromReference:color=>color.applyMatrix3(LINEAR_SRGB_TO_LINEAR_DISPLAY_P3$1).convertLinearToSRGB()}};const SUPPORTED_WORKING_COLOR_SPACES$1=new Set([LinearSRGBColorSpace$1,LinearDisplayP3ColorSpace$1]);const ColorManagement$1={enabled:true,_workingColorSpace:LinearSRGBColorSpace$1,get workingColorSpace(){return this._workingColorSpace;},set workingColorSpace(colorSpace){if(!SUPPORTED_WORKING_COLOR_SPACES$1.has(colorSpace)){throw new Error(`Unsupported working color space, "${colorSpace}".`);}this._workingColorSpace=colorSpace;},convert:function(color,sourceColorSpace,targetColorSpace){if(this.enabled===false||sourceColorSpace===targetColorSpace||!sourceColorSpace||!targetColorSpace){return color;}const sourceToReference=COLOR_SPACES$1[sourceColorSpace].toReference;const targetFromReference=COLOR_SPACES$1[targetColorSpace].fromReference;return targetFromReference(sourceToReference(color));},fromWorkingColorSpace:function(color,targetColorSpace){return this.convert(color,this._workingColorSpace,targetColorSpace);},toWorkingColorSpace:function(color,sourceColorSpace){return this.convert(color,sourceColorSpace,this._workingColorSpace);},getPrimaries:function(colorSpace){return COLOR_SPACES$1[colorSpace].primaries;},getTransfer:function(colorSpace){if(colorSpace===NoColorSpace$1)return LinearTransfer$1;return COLOR_SPACES$1[colorSpace].transfer;},getLuminanceCoefficients:function(target,colorSpace=this._workingColorSpace){return target.fromArray(COLOR_SPACES$1[colorSpace].luminanceCoefficients);}};function SRGBToLinear$1(c){return c<0.04045?c*0.0773993808:Math.pow(c*0.9478672986+0.0521327014,2.4);}function LinearToSRGB$1(c){return c<0.0031308?c*12.92:1.055*Math.pow(c,0.41666)-0.055;}let _canvas$1;class ImageUtils$1{static getDataURL(image){if(/^data:/i.test(image.src)){return image.src;}if(typeof HTMLCanvasElement==='undefined'){return image.src;}let canvas;if(image instanceof HTMLCanvasElement){canvas=image;}else {if(_canvas$1===undefined)_canvas$1=createElementNS$1('canvas');_canvas$1.width=image.width;_canvas$1.height=image.height;const context=_canvas$1.getContext('2d');if(image instanceof ImageData){context.putImageData(image,0,0);}else {context.drawImage(image,0,0,image.width,image.height);}canvas=_canvas$1;}if(canvas.width>2048||canvas.height>2048){console.warn('THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons',image);return canvas.toDataURL('image/jpeg',0.6);}else {return canvas.toDataURL('image/png');}}static sRGBToLinear(image){if(typeof HTMLImageElement!=='undefined'&&image instanceof HTMLImageElement||typeof HTMLCanvasElement!=='undefined'&&image instanceof HTMLCanvasElement||typeof ImageBitmap!=='undefined'&&image instanceof ImageBitmap){const canvas=createElementNS$1('canvas');canvas.width=image.width;canvas.height=image.height;const context=canvas.getContext('2d');context.drawImage(image,0,0,image.width,image.height);const imageData=context.getImageData(0,0,image.width,image.height);const data=imageData.data;for(let i=0;i<data.length;i++){data[i]=SRGBToLinear$1(data[i]/255)*255;}context.putImageData(imageData,0,0);return canvas;}else if(image.data){const data=image.data.slice(0);for(let i=0;i<data.length;i++){if(data instanceof Uint8Array||data instanceof Uint8ClampedArray){data[i]=Math.floor(SRGBToLinear$1(data[i]/255)*255);}else {// assuming float
data[i]=SRGBToLinear$1(data[i]);}}return {data:data,width:image.width,height:image.height};}else {console.warn('THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied.');return image;}}}let _sourceId$1=0;class Source$1{constructor(data=null){this.isSource=true;Object.defineProperty(this,'id',{value:_sourceId$1++});this.uuid=generateUUID$1();this.data=data;this.dataReady=true;this.version=0;}set needsUpdate(value){if(value===true)this.version++;}toJSON(meta){const isRootObject=meta===undefined||typeof meta==='string';if(!isRootObject&&meta.images[this.uuid]!==undefined){return meta.images[this.uuid];}const output={uuid:this.uuid,url:''};const data=this.data;if(data!==null){let url;if(Array.isArray(data)){// cube texture
url=[];for(let i=0,l=data.length;i<l;i++){if(data[i].isDataTexture){url.push(serializeImage$1(data[i].image));}else {url.push(serializeImage$1(data[i]));}}}else {// texture
url=serializeImage$1(data);}output.url=url;}if(!isRootObject){meta.images[this.uuid]=output;}return output;}}function serializeImage$1(image){if(typeof HTMLImageElement!=='undefined'&&image instanceof HTMLImageElement||typeof HTMLCanvasElement!=='undefined'&&image instanceof HTMLCanvasElement||typeof ImageBitmap!=='undefined'&&image instanceof ImageBitmap){// default images
return ImageUtils$1.getDataURL(image);}else {if(image.data){// images of DataTexture
return {data:Array.from(image.data),width:image.width,height:image.height,type:image.data.constructor.name};}else {console.warn('THREE.Texture: Unable to serialize Texture.');return {};}}}let _textureId$1=0;class Texture$1 extends EventDispatcher$1{constructor(image=Texture$1.DEFAULT_IMAGE,mapping=Texture$1.DEFAULT_MAPPING,wrapS=ClampToEdgeWrapping$1,wrapT=ClampToEdgeWrapping$1,magFilter=LinearFilter$1,minFilter=LinearMipmapLinearFilter$1,format=RGBAFormat$1,type=UnsignedByteType$1,anisotropy=Texture$1.DEFAULT_ANISOTROPY,colorSpace=NoColorSpace$1){super();this.isTexture=true;Object.defineProperty(this,'id',{value:_textureId$1++});this.uuid=generateUUID$1();this.name='';this.source=new Source$1(image);this.mipmaps=[];this.mapping=mapping;this.channel=0;this.wrapS=wrapS;this.wrapT=wrapT;this.magFilter=magFilter;this.minFilter=minFilter;this.anisotropy=anisotropy;this.format=format;this.internalFormat=null;this.type=type;this.offset=new Vector2$1(0,0);this.repeat=new Vector2$1(1,1);this.center=new Vector2$1(0,0);this.rotation=0;this.matrixAutoUpdate=true;this.matrix=new Matrix3$1();this.generateMipmaps=true;this.premultiplyAlpha=false;this.flipY=true;this.unpackAlignment=4;// valid values: 1, 2, 4, 8 (see http://www.khronos.org/opengles/sdk/docs/man/xhtml/glPixelStorei.xml)
this.colorSpace=colorSpace;this.userData={};this.version=0;this.onUpdate=null;this.isRenderTargetTexture=false;// indicates whether a texture belongs to a render target or not
this.pmremVersion=0;// indicates whether this texture should be processed by PMREMGenerator or not (only relevant for render target textures)
}get image(){return this.source.data;}set image(value=null){this.source.data=value;}updateMatrix(){this.matrix.setUvTransform(this.offset.x,this.offset.y,this.repeat.x,this.repeat.y,this.rotation,this.center.x,this.center.y);}clone(){return new this.constructor().copy(this);}copy(source){this.name=source.name;this.source=source.source;this.mipmaps=source.mipmaps.slice(0);this.mapping=source.mapping;this.channel=source.channel;this.wrapS=source.wrapS;this.wrapT=source.wrapT;this.magFilter=source.magFilter;this.minFilter=source.minFilter;this.anisotropy=source.anisotropy;this.format=source.format;this.internalFormat=source.internalFormat;this.type=source.type;this.offset.copy(source.offset);this.repeat.copy(source.repeat);this.center.copy(source.center);this.rotation=source.rotation;this.matrixAutoUpdate=source.matrixAutoUpdate;this.matrix.copy(source.matrix);this.generateMipmaps=source.generateMipmaps;this.premultiplyAlpha=source.premultiplyAlpha;this.flipY=source.flipY;this.unpackAlignment=source.unpackAlignment;this.colorSpace=source.colorSpace;this.userData=JSON.parse(JSON.stringify(source.userData));this.needsUpdate=true;return this;}toJSON(meta){const isRootObject=meta===undefined||typeof meta==='string';if(!isRootObject&&meta.textures[this.uuid]!==undefined){return meta.textures[this.uuid];}const output={metadata:{version:4.6,type:'Texture',generator:'Texture.toJSON'},uuid:this.uuid,name:this.name,image:this.source.toJSON(meta).uuid,mapping:this.mapping,channel:this.channel,repeat:[this.repeat.x,this.repeat.y],offset:[this.offset.x,this.offset.y],center:[this.center.x,this.center.y],rotation:this.rotation,wrap:[this.wrapS,this.wrapT],format:this.format,internalFormat:this.internalFormat,type:this.type,colorSpace:this.colorSpace,minFilter:this.minFilter,magFilter:this.magFilter,anisotropy:this.anisotropy,flipY:this.flipY,generateMipmaps:this.generateMipmaps,premultiplyAlpha:this.premultiplyAlpha,unpackAlignment:this.unpackAlignment};if(Object.keys(this.userData).length>0)output.userData=this.userData;if(!isRootObject){meta.textures[this.uuid]=output;}return output;}dispose(){this.dispatchEvent({type:'dispose'});}transformUv(uv){if(this.mapping!==UVMapping$1)return uv;uv.applyMatrix3(this.matrix);if(uv.x<0||uv.x>1){switch(this.wrapS){case RepeatWrapping$1:uv.x=uv.x-Math.floor(uv.x);break;case ClampToEdgeWrapping$1:uv.x=uv.x<0?0:1;break;case MirroredRepeatWrapping$1:if(Math.abs(Math.floor(uv.x)%2)===1){uv.x=Math.ceil(uv.x)-uv.x;}else {uv.x=uv.x-Math.floor(uv.x);}break;}}if(uv.y<0||uv.y>1){switch(this.wrapT){case RepeatWrapping$1:uv.y=uv.y-Math.floor(uv.y);break;case ClampToEdgeWrapping$1:uv.y=uv.y<0?0:1;break;case MirroredRepeatWrapping$1:if(Math.abs(Math.floor(uv.y)%2)===1){uv.y=Math.ceil(uv.y)-uv.y;}else {uv.y=uv.y-Math.floor(uv.y);}break;}}if(this.flipY){uv.y=1-uv.y;}return uv;}set needsUpdate(value){if(value===true){this.version++;this.source.needsUpdate=true;}}set needsPMREMUpdate(value){if(value===true){this.pmremVersion++;}}}Texture$1.DEFAULT_IMAGE=null;Texture$1.DEFAULT_MAPPING=UVMapping$1;Texture$1.DEFAULT_ANISOTROPY=1;class Vector4$1{constructor(x=0,y=0,z=0,w=1){Vector4$1.prototype.isVector4=true;this.x=x;this.y=y;this.z=z;this.w=w;}get width(){return this.z;}set width(value){this.z=value;}get height(){return this.w;}set height(value){this.w=value;}set(x,y,z,w){this.x=x;this.y=y;this.z=z;this.w=w;return this;}setScalar(scalar){this.x=scalar;this.y=scalar;this.z=scalar;this.w=scalar;return this;}setX(x){this.x=x;return this;}setY(y){this.y=y;return this;}setZ(z){this.z=z;return this;}setW(w){this.w=w;return this;}setComponent(index,value){switch(index){case 0:this.x=value;break;case 1:this.y=value;break;case 2:this.z=value;break;case 3:this.w=value;break;default:throw new Error('index is out of range: '+index);}return this;}getComponent(index){switch(index){case 0:return this.x;case 1:return this.y;case 2:return this.z;case 3:return this.w;default:throw new Error('index is out of range: '+index);}}clone(){return new this.constructor(this.x,this.y,this.z,this.w);}copy(v){this.x=v.x;this.y=v.y;this.z=v.z;this.w=v.w!==undefined?v.w:1;return this;}add(v){this.x+=v.x;this.y+=v.y;this.z+=v.z;this.w+=v.w;return this;}addScalar(s){this.x+=s;this.y+=s;this.z+=s;this.w+=s;return this;}addVectors(a,b){this.x=a.x+b.x;this.y=a.y+b.y;this.z=a.z+b.z;this.w=a.w+b.w;return this;}addScaledVector(v,s){this.x+=v.x*s;this.y+=v.y*s;this.z+=v.z*s;this.w+=v.w*s;return this;}sub(v){this.x-=v.x;this.y-=v.y;this.z-=v.z;this.w-=v.w;return this;}subScalar(s){this.x-=s;this.y-=s;this.z-=s;this.w-=s;return this;}subVectors(a,b){this.x=a.x-b.x;this.y=a.y-b.y;this.z=a.z-b.z;this.w=a.w-b.w;return this;}multiply(v){this.x*=v.x;this.y*=v.y;this.z*=v.z;this.w*=v.w;return this;}multiplyScalar(scalar){this.x*=scalar;this.y*=scalar;this.z*=scalar;this.w*=scalar;return this;}applyMatrix4(m){const x=this.x,y=this.y,z=this.z,w=this.w;const e=m.elements;this.x=e[0]*x+e[4]*y+e[8]*z+e[12]*w;this.y=e[1]*x+e[5]*y+e[9]*z+e[13]*w;this.z=e[2]*x+e[6]*y+e[10]*z+e[14]*w;this.w=e[3]*x+e[7]*y+e[11]*z+e[15]*w;return this;}divideScalar(scalar){return this.multiplyScalar(1/scalar);}setAxisAngleFromQuaternion(q){// http://www.euclideanspace.com/maths/geometry/rotations/conversions/quaternionToAngle/index.htm
// q is assumed to be normalized
this.w=2*Math.acos(q.w);const s=Math.sqrt(1-q.w*q.w);if(s<0.0001){this.x=1;this.y=0;this.z=0;}else {this.x=q.x/s;this.y=q.y/s;this.z=q.z/s;}return this;}setAxisAngleFromRotationMatrix(m){// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToAngle/index.htm
// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)
let angle,x,y,z;// variables for result
const epsilon=0.01,// margin to allow for rounding errors
epsilon2=0.1,// margin to distinguish between 0 and 180 degrees
te=m.elements,m11=te[0],m12=te[4],m13=te[8],m21=te[1],m22=te[5],m23=te[9],m31=te[2],m32=te[6],m33=te[10];if(Math.abs(m12-m21)<epsilon&&Math.abs(m13-m31)<epsilon&&Math.abs(m23-m32)<epsilon){// singularity found
// first check for identity matrix which must have +1 for all terms
// in leading diagonal and zero in other terms
if(Math.abs(m12+m21)<epsilon2&&Math.abs(m13+m31)<epsilon2&&Math.abs(m23+m32)<epsilon2&&Math.abs(m11+m22+m33-3)<epsilon2){// this singularity is identity matrix so angle = 0
this.set(1,0,0,0);return this;// zero angle, arbitrary axis
}// otherwise this singularity is angle = 180
angle=Math.PI;const xx=(m11+1)/2;const yy=(m22+1)/2;const zz=(m33+1)/2;const xy=(m12+m21)/4;const xz=(m13+m31)/4;const yz=(m23+m32)/4;if(xx>yy&&xx>zz){// m11 is the largest diagonal term
if(xx<epsilon){x=0;y=0.707106781;z=0.707106781;}else {x=Math.sqrt(xx);y=xy/x;z=xz/x;}}else if(yy>zz){// m22 is the largest diagonal term
if(yy<epsilon){x=0.707106781;y=0;z=0.707106781;}else {y=Math.sqrt(yy);x=xy/y;z=yz/y;}}else {// m33 is the largest diagonal term so base result on this
if(zz<epsilon){x=0.707106781;y=0.707106781;z=0;}else {z=Math.sqrt(zz);x=xz/z;y=yz/z;}}this.set(x,y,z,angle);return this;// return 180 deg rotation
}// as we have reached here there are no singularities so we can handle normally
let s=Math.sqrt((m32-m23)*(m32-m23)+(m13-m31)*(m13-m31)+(m21-m12)*(m21-m12));// used to normalize
if(Math.abs(s)<0.001)s=1;// prevent divide by zero, should not happen if matrix is orthogonal and should be
// caught by singularity test above, but I've left it in just in case
this.x=(m32-m23)/s;this.y=(m13-m31)/s;this.z=(m21-m12)/s;this.w=Math.acos((m11+m22+m33-1)/2);return this;}setFromMatrixPosition(m){const e=m.elements;this.x=e[12];this.y=e[13];this.z=e[14];this.w=e[15];return this;}min(v){this.x=Math.min(this.x,v.x);this.y=Math.min(this.y,v.y);this.z=Math.min(this.z,v.z);this.w=Math.min(this.w,v.w);return this;}max(v){this.x=Math.max(this.x,v.x);this.y=Math.max(this.y,v.y);this.z=Math.max(this.z,v.z);this.w=Math.max(this.w,v.w);return this;}clamp(min,max){// assumes min < max, componentwise
this.x=Math.max(min.x,Math.min(max.x,this.x));this.y=Math.max(min.y,Math.min(max.y,this.y));this.z=Math.max(min.z,Math.min(max.z,this.z));this.w=Math.max(min.w,Math.min(max.w,this.w));return this;}clampScalar(minVal,maxVal){this.x=Math.max(minVal,Math.min(maxVal,this.x));this.y=Math.max(minVal,Math.min(maxVal,this.y));this.z=Math.max(minVal,Math.min(maxVal,this.z));this.w=Math.max(minVal,Math.min(maxVal,this.w));return this;}clampLength(min,max){const length=this.length();return this.divideScalar(length||1).multiplyScalar(Math.max(min,Math.min(max,length)));}floor(){this.x=Math.floor(this.x);this.y=Math.floor(this.y);this.z=Math.floor(this.z);this.w=Math.floor(this.w);return this;}ceil(){this.x=Math.ceil(this.x);this.y=Math.ceil(this.y);this.z=Math.ceil(this.z);this.w=Math.ceil(this.w);return this;}round(){this.x=Math.round(this.x);this.y=Math.round(this.y);this.z=Math.round(this.z);this.w=Math.round(this.w);return this;}roundToZero(){this.x=Math.trunc(this.x);this.y=Math.trunc(this.y);this.z=Math.trunc(this.z);this.w=Math.trunc(this.w);return this;}negate(){this.x=-this.x;this.y=-this.y;this.z=-this.z;this.w=-this.w;return this;}dot(v){return this.x*v.x+this.y*v.y+this.z*v.z+this.w*v.w;}lengthSq(){return this.x*this.x+this.y*this.y+this.z*this.z+this.w*this.w;}length(){return Math.sqrt(this.x*this.x+this.y*this.y+this.z*this.z+this.w*this.w);}manhattanLength(){return Math.abs(this.x)+Math.abs(this.y)+Math.abs(this.z)+Math.abs(this.w);}normalize(){return this.divideScalar(this.length()||1);}setLength(length){return this.normalize().multiplyScalar(length);}lerp(v,alpha){this.x+=(v.x-this.x)*alpha;this.y+=(v.y-this.y)*alpha;this.z+=(v.z-this.z)*alpha;this.w+=(v.w-this.w)*alpha;return this;}lerpVectors(v1,v2,alpha){this.x=v1.x+(v2.x-v1.x)*alpha;this.y=v1.y+(v2.y-v1.y)*alpha;this.z=v1.z+(v2.z-v1.z)*alpha;this.w=v1.w+(v2.w-v1.w)*alpha;return this;}equals(v){return v.x===this.x&&v.y===this.y&&v.z===this.z&&v.w===this.w;}fromArray(array,offset=0){this.x=array[offset];this.y=array[offset+1];this.z=array[offset+2];this.w=array[offset+3];return this;}toArray(array=[],offset=0){array[offset]=this.x;array[offset+1]=this.y;array[offset+2]=this.z;array[offset+3]=this.w;return array;}fromBufferAttribute(attribute,index){this.x=attribute.getX(index);this.y=attribute.getY(index);this.z=attribute.getZ(index);this.w=attribute.getW(index);return this;}random(){this.x=Math.random();this.y=Math.random();this.z=Math.random();this.w=Math.random();return this;}*[Symbol.iterator](){yield this.x;yield this.y;yield this.z;yield this.w;}}/*
 In options, we can specify:
 * Texture parameters for an auto-generated target texture
 * depthBuffer/stencilBuffer: Booleans to indicate if we should generate these buffers
*/class RenderTarget$1 extends EventDispatcher$1{constructor(width=1,height=1,options={}){super();this.isRenderTarget=true;this.width=width;this.height=height;this.depth=1;this.scissor=new Vector4$1(0,0,width,height);this.scissorTest=false;this.viewport=new Vector4$1(0,0,width,height);const image={width:width,height:height,depth:1};options=Object.assign({generateMipmaps:false,internalFormat:null,minFilter:LinearFilter$1,depthBuffer:true,stencilBuffer:false,resolveDepthBuffer:true,resolveStencilBuffer:true,depthTexture:null,samples:0,count:1},options);const texture=new Texture$1(image,options.mapping,options.wrapS,options.wrapT,options.magFilter,options.minFilter,options.format,options.type,options.anisotropy,options.colorSpace);texture.flipY=false;texture.generateMipmaps=options.generateMipmaps;texture.internalFormat=options.internalFormat;this.textures=[];const count=options.count;for(let i=0;i<count;i++){this.textures[i]=texture.clone();this.textures[i].isRenderTargetTexture=true;}this.depthBuffer=options.depthBuffer;this.stencilBuffer=options.stencilBuffer;this.resolveDepthBuffer=options.resolveDepthBuffer;this.resolveStencilBuffer=options.resolveStencilBuffer;this.depthTexture=options.depthTexture;this.samples=options.samples;}get texture(){return this.textures[0];}set texture(value){this.textures[0]=value;}setSize(width,height,depth=1){if(this.width!==width||this.height!==height||this.depth!==depth){this.width=width;this.height=height;this.depth=depth;for(let i=0,il=this.textures.length;i<il;i++){this.textures[i].image.width=width;this.textures[i].image.height=height;this.textures[i].image.depth=depth;}this.dispose();}this.viewport.set(0,0,width,height);this.scissor.set(0,0,width,height);}clone(){return new this.constructor().copy(this);}copy(source){this.width=source.width;this.height=source.height;this.depth=source.depth;this.scissor.copy(source.scissor);this.scissorTest=source.scissorTest;this.viewport.copy(source.viewport);this.textures.length=0;for(let i=0,il=source.textures.length;i<il;i++){this.textures[i]=source.textures[i].clone();this.textures[i].isRenderTargetTexture=true;}// ensure image object is not shared, see #20328
const image=Object.assign({},source.texture.image);this.texture.source=new Source$1(image);this.depthBuffer=source.depthBuffer;this.stencilBuffer=source.stencilBuffer;this.resolveDepthBuffer=source.resolveDepthBuffer;this.resolveStencilBuffer=source.resolveStencilBuffer;if(source.depthTexture!==null)this.depthTexture=source.depthTexture.clone();this.samples=source.samples;return this;}dispose(){this.dispatchEvent({type:'dispose'});}}class WebGLRenderTarget extends RenderTarget$1{constructor(width=1,height=1,options={}){super(width,height,options);this.isWebGLRenderTarget=true;}}class DataArrayTexture$1 extends Texture$1{constructor(data=null,width=1,height=1,depth=1){super(null);this.isDataArrayTexture=true;this.image={data,width,height,depth};this.magFilter=NearestFilter$1;this.minFilter=NearestFilter$1;this.wrapR=ClampToEdgeWrapping$1;this.generateMipmaps=false;this.flipY=false;this.unpackAlignment=1;this.layerUpdates=new Set();}addLayerUpdate(layerIndex){this.layerUpdates.add(layerIndex);}clearLayerUpdates(){this.layerUpdates.clear();}}class Data3DTexture extends Texture$1{constructor(data=null,width=1,height=1,depth=1){// We're going to add .setXXX() methods for setting properties later.
// Users can still set in DataTexture3D directly.
//
//	const texture = new THREE.DataTexture3D( data, width, height, depth );
// 	texture.anisotropy = 16;
//
// See #14839
super(null);this.isData3DTexture=true;this.image={data,width,height,depth};this.magFilter=NearestFilter$1;this.minFilter=NearestFilter$1;this.wrapR=ClampToEdgeWrapping$1;this.generateMipmaps=false;this.flipY=false;this.unpackAlignment=1;}}class Quaternion$1{constructor(x=0,y=0,z=0,w=1){this.isQuaternion=true;this._x=x;this._y=y;this._z=z;this._w=w;}static slerpFlat(dst,dstOffset,src0,srcOffset0,src1,srcOffset1,t){// fuzz-free, array-based Quaternion SLERP operation
let x0=src0[srcOffset0+0],y0=src0[srcOffset0+1],z0=src0[srcOffset0+2],w0=src0[srcOffset0+3];const x1=src1[srcOffset1+0],y1=src1[srcOffset1+1],z1=src1[srcOffset1+2],w1=src1[srcOffset1+3];if(t===0){dst[dstOffset+0]=x0;dst[dstOffset+1]=y0;dst[dstOffset+2]=z0;dst[dstOffset+3]=w0;return;}if(t===1){dst[dstOffset+0]=x1;dst[dstOffset+1]=y1;dst[dstOffset+2]=z1;dst[dstOffset+3]=w1;return;}if(w0!==w1||x0!==x1||y0!==y1||z0!==z1){let s=1-t;const cos=x0*x1+y0*y1+z0*z1+w0*w1,dir=cos>=0?1:-1,sqrSin=1-cos*cos;// Skip the Slerp for tiny steps to avoid numeric problems:
if(sqrSin>Number.EPSILON){const sin=Math.sqrt(sqrSin),len=Math.atan2(sin,cos*dir);s=Math.sin(s*len)/sin;t=Math.sin(t*len)/sin;}const tDir=t*dir;x0=x0*s+x1*tDir;y0=y0*s+y1*tDir;z0=z0*s+z1*tDir;w0=w0*s+w1*tDir;// Normalize in case we just did a lerp:
if(s===1-t){const f=1/Math.sqrt(x0*x0+y0*y0+z0*z0+w0*w0);x0*=f;y0*=f;z0*=f;w0*=f;}}dst[dstOffset]=x0;dst[dstOffset+1]=y0;dst[dstOffset+2]=z0;dst[dstOffset+3]=w0;}static multiplyQuaternionsFlat(dst,dstOffset,src0,srcOffset0,src1,srcOffset1){const x0=src0[srcOffset0];const y0=src0[srcOffset0+1];const z0=src0[srcOffset0+2];const w0=src0[srcOffset0+3];const x1=src1[srcOffset1];const y1=src1[srcOffset1+1];const z1=src1[srcOffset1+2];const w1=src1[srcOffset1+3];dst[dstOffset]=x0*w1+w0*x1+y0*z1-z0*y1;dst[dstOffset+1]=y0*w1+w0*y1+z0*x1-x0*z1;dst[dstOffset+2]=z0*w1+w0*z1+x0*y1-y0*x1;dst[dstOffset+3]=w0*w1-x0*x1-y0*y1-z0*z1;return dst;}get x(){return this._x;}set x(value){this._x=value;this._onChangeCallback();}get y(){return this._y;}set y(value){this._y=value;this._onChangeCallback();}get z(){return this._z;}set z(value){this._z=value;this._onChangeCallback();}get w(){return this._w;}set w(value){this._w=value;this._onChangeCallback();}set(x,y,z,w){this._x=x;this._y=y;this._z=z;this._w=w;this._onChangeCallback();return this;}clone(){return new this.constructor(this._x,this._y,this._z,this._w);}copy(quaternion){this._x=quaternion.x;this._y=quaternion.y;this._z=quaternion.z;this._w=quaternion.w;this._onChangeCallback();return this;}setFromEuler(euler,update=true){const x=euler._x,y=euler._y,z=euler._z,order=euler._order;// http://www.mathworks.com/matlabcentral/fileexchange/
// 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
//	content/SpinCalc.m
const cos=Math.cos;const sin=Math.sin;const c1=cos(x/2);const c2=cos(y/2);const c3=cos(z/2);const s1=sin(x/2);const s2=sin(y/2);const s3=sin(z/2);switch(order){case'XYZ':this._x=s1*c2*c3+c1*s2*s3;this._y=c1*s2*c3-s1*c2*s3;this._z=c1*c2*s3+s1*s2*c3;this._w=c1*c2*c3-s1*s2*s3;break;case'YXZ':this._x=s1*c2*c3+c1*s2*s3;this._y=c1*s2*c3-s1*c2*s3;this._z=c1*c2*s3-s1*s2*c3;this._w=c1*c2*c3+s1*s2*s3;break;case'ZXY':this._x=s1*c2*c3-c1*s2*s3;this._y=c1*s2*c3+s1*c2*s3;this._z=c1*c2*s3+s1*s2*c3;this._w=c1*c2*c3-s1*s2*s3;break;case'ZYX':this._x=s1*c2*c3-c1*s2*s3;this._y=c1*s2*c3+s1*c2*s3;this._z=c1*c2*s3-s1*s2*c3;this._w=c1*c2*c3+s1*s2*s3;break;case'YZX':this._x=s1*c2*c3+c1*s2*s3;this._y=c1*s2*c3+s1*c2*s3;this._z=c1*c2*s3-s1*s2*c3;this._w=c1*c2*c3-s1*s2*s3;break;case'XZY':this._x=s1*c2*c3-c1*s2*s3;this._y=c1*s2*c3-s1*c2*s3;this._z=c1*c2*s3+s1*s2*c3;this._w=c1*c2*c3+s1*s2*s3;break;default:console.warn('THREE.Quaternion: .setFromEuler() encountered an unknown order: '+order);}if(update===true)this._onChangeCallback();return this;}setFromAxisAngle(axis,angle){// http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm
// assumes axis is normalized
const halfAngle=angle/2,s=Math.sin(halfAngle);this._x=axis.x*s;this._y=axis.y*s;this._z=axis.z*s;this._w=Math.cos(halfAngle);this._onChangeCallback();return this;}setFromRotationMatrix(m){// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm
// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)
const te=m.elements,m11=te[0],m12=te[4],m13=te[8],m21=te[1],m22=te[5],m23=te[9],m31=te[2],m32=te[6],m33=te[10],trace=m11+m22+m33;if(trace>0){const s=0.5/Math.sqrt(trace+1.0);this._w=0.25/s;this._x=(m32-m23)*s;this._y=(m13-m31)*s;this._z=(m21-m12)*s;}else if(m11>m22&&m11>m33){const s=2.0*Math.sqrt(1.0+m11-m22-m33);this._w=(m32-m23)/s;this._x=0.25*s;this._y=(m12+m21)/s;this._z=(m13+m31)/s;}else if(m22>m33){const s=2.0*Math.sqrt(1.0+m22-m11-m33);this._w=(m13-m31)/s;this._x=(m12+m21)/s;this._y=0.25*s;this._z=(m23+m32)/s;}else {const s=2.0*Math.sqrt(1.0+m33-m11-m22);this._w=(m21-m12)/s;this._x=(m13+m31)/s;this._y=(m23+m32)/s;this._z=0.25*s;}this._onChangeCallback();return this;}setFromUnitVectors(vFrom,vTo){// assumes direction vectors vFrom and vTo are normalized
let r=vFrom.dot(vTo)+1;if(r<Number.EPSILON){// vFrom and vTo point in opposite directions
r=0;if(Math.abs(vFrom.x)>Math.abs(vFrom.z)){this._x=-vFrom.y;this._y=vFrom.x;this._z=0;this._w=r;}else {this._x=0;this._y=-vFrom.z;this._z=vFrom.y;this._w=r;}}else {// crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3
this._x=vFrom.y*vTo.z-vFrom.z*vTo.y;this._y=vFrom.z*vTo.x-vFrom.x*vTo.z;this._z=vFrom.x*vTo.y-vFrom.y*vTo.x;this._w=r;}return this.normalize();}angleTo(q){return 2*Math.acos(Math.abs(clamp$2(this.dot(q),-1,1)));}rotateTowards(q,step){const angle=this.angleTo(q);if(angle===0)return this;const t=Math.min(1,step/angle);this.slerp(q,t);return this;}identity(){return this.set(0,0,0,1);}invert(){// quaternion is assumed to have unit length
return this.conjugate();}conjugate(){this._x*=-1;this._y*=-1;this._z*=-1;this._onChangeCallback();return this;}dot(v){return this._x*v._x+this._y*v._y+this._z*v._z+this._w*v._w;}lengthSq(){return this._x*this._x+this._y*this._y+this._z*this._z+this._w*this._w;}length(){return Math.sqrt(this._x*this._x+this._y*this._y+this._z*this._z+this._w*this._w);}normalize(){let l=this.length();if(l===0){this._x=0;this._y=0;this._z=0;this._w=1;}else {l=1/l;this._x=this._x*l;this._y=this._y*l;this._z=this._z*l;this._w=this._w*l;}this._onChangeCallback();return this;}multiply(q){return this.multiplyQuaternions(this,q);}premultiply(q){return this.multiplyQuaternions(q,this);}multiplyQuaternions(a,b){// from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm
const qax=a._x,qay=a._y,qaz=a._z,qaw=a._w;const qbx=b._x,qby=b._y,qbz=b._z,qbw=b._w;this._x=qax*qbw+qaw*qbx+qay*qbz-qaz*qby;this._y=qay*qbw+qaw*qby+qaz*qbx-qax*qbz;this._z=qaz*qbw+qaw*qbz+qax*qby-qay*qbx;this._w=qaw*qbw-qax*qbx-qay*qby-qaz*qbz;this._onChangeCallback();return this;}slerp(qb,t){if(t===0)return this;if(t===1)return this.copy(qb);const x=this._x,y=this._y,z=this._z,w=this._w;// http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/
let cosHalfTheta=w*qb._w+x*qb._x+y*qb._y+z*qb._z;if(cosHalfTheta<0){this._w=-qb._w;this._x=-qb._x;this._y=-qb._y;this._z=-qb._z;cosHalfTheta=-cosHalfTheta;}else {this.copy(qb);}if(cosHalfTheta>=1.0){this._w=w;this._x=x;this._y=y;this._z=z;return this;}const sqrSinHalfTheta=1.0-cosHalfTheta*cosHalfTheta;if(sqrSinHalfTheta<=Number.EPSILON){const s=1-t;this._w=s*w+t*this._w;this._x=s*x+t*this._x;this._y=s*y+t*this._y;this._z=s*z+t*this._z;this.normalize();// normalize calls _onChangeCallback()
return this;}const sinHalfTheta=Math.sqrt(sqrSinHalfTheta);const halfTheta=Math.atan2(sinHalfTheta,cosHalfTheta);const ratioA=Math.sin((1-t)*halfTheta)/sinHalfTheta,ratioB=Math.sin(t*halfTheta)/sinHalfTheta;this._w=w*ratioA+this._w*ratioB;this._x=x*ratioA+this._x*ratioB;this._y=y*ratioA+this._y*ratioB;this._z=z*ratioA+this._z*ratioB;this._onChangeCallback();return this;}slerpQuaternions(qa,qb,t){return this.copy(qa).slerp(qb,t);}random(){// sets this quaternion to a uniform random unit quaternnion
// Ken Shoemake
// Uniform random rotations
// D. Kirk, editor, Graphics Gems III, pages 124-132. Academic Press, New York, 1992.
const theta1=2*Math.PI*Math.random();const theta2=2*Math.PI*Math.random();const x0=Math.random();const r1=Math.sqrt(1-x0);const r2=Math.sqrt(x0);return this.set(r1*Math.sin(theta1),r1*Math.cos(theta1),r2*Math.sin(theta2),r2*Math.cos(theta2));}equals(quaternion){return quaternion._x===this._x&&quaternion._y===this._y&&quaternion._z===this._z&&quaternion._w===this._w;}fromArray(array,offset=0){this._x=array[offset];this._y=array[offset+1];this._z=array[offset+2];this._w=array[offset+3];this._onChangeCallback();return this;}toArray(array=[],offset=0){array[offset]=this._x;array[offset+1]=this._y;array[offset+2]=this._z;array[offset+3]=this._w;return array;}fromBufferAttribute(attribute,index){this._x=attribute.getX(index);this._y=attribute.getY(index);this._z=attribute.getZ(index);this._w=attribute.getW(index);this._onChangeCallback();return this;}toJSON(){return this.toArray();}_onChange(callback){this._onChangeCallback=callback;return this;}_onChangeCallback(){}*[Symbol.iterator](){yield this._x;yield this._y;yield this._z;yield this._w;}}class Vector3$1{constructor(x=0,y=0,z=0){Vector3$1.prototype.isVector3=true;this.x=x;this.y=y;this.z=z;}set(x,y,z){if(z===undefined)z=this.z;// sprite.scale.set(x,y)
this.x=x;this.y=y;this.z=z;return this;}setScalar(scalar){this.x=scalar;this.y=scalar;this.z=scalar;return this;}setX(x){this.x=x;return this;}setY(y){this.y=y;return this;}setZ(z){this.z=z;return this;}setComponent(index,value){switch(index){case 0:this.x=value;break;case 1:this.y=value;break;case 2:this.z=value;break;default:throw new Error('index is out of range: '+index);}return this;}getComponent(index){switch(index){case 0:return this.x;case 1:return this.y;case 2:return this.z;default:throw new Error('index is out of range: '+index);}}clone(){return new this.constructor(this.x,this.y,this.z);}copy(v){this.x=v.x;this.y=v.y;this.z=v.z;return this;}add(v){this.x+=v.x;this.y+=v.y;this.z+=v.z;return this;}addScalar(s){this.x+=s;this.y+=s;this.z+=s;return this;}addVectors(a,b){this.x=a.x+b.x;this.y=a.y+b.y;this.z=a.z+b.z;return this;}addScaledVector(v,s){this.x+=v.x*s;this.y+=v.y*s;this.z+=v.z*s;return this;}sub(v){this.x-=v.x;this.y-=v.y;this.z-=v.z;return this;}subScalar(s){this.x-=s;this.y-=s;this.z-=s;return this;}subVectors(a,b){this.x=a.x-b.x;this.y=a.y-b.y;this.z=a.z-b.z;return this;}multiply(v){this.x*=v.x;this.y*=v.y;this.z*=v.z;return this;}multiplyScalar(scalar){this.x*=scalar;this.y*=scalar;this.z*=scalar;return this;}multiplyVectors(a,b){this.x=a.x*b.x;this.y=a.y*b.y;this.z=a.z*b.z;return this;}applyEuler(euler){return this.applyQuaternion(_quaternion$4.setFromEuler(euler));}applyAxisAngle(axis,angle){return this.applyQuaternion(_quaternion$4.setFromAxisAngle(axis,angle));}applyMatrix3(m){const x=this.x,y=this.y,z=this.z;const e=m.elements;this.x=e[0]*x+e[3]*y+e[6]*z;this.y=e[1]*x+e[4]*y+e[7]*z;this.z=e[2]*x+e[5]*y+e[8]*z;return this;}applyNormalMatrix(m){return this.applyMatrix3(m).normalize();}applyMatrix4(m){const x=this.x,y=this.y,z=this.z;const e=m.elements;const w=1/(e[3]*x+e[7]*y+e[11]*z+e[15]);this.x=(e[0]*x+e[4]*y+e[8]*z+e[12])*w;this.y=(e[1]*x+e[5]*y+e[9]*z+e[13])*w;this.z=(e[2]*x+e[6]*y+e[10]*z+e[14])*w;return this;}applyQuaternion(q){// quaternion q is assumed to have unit length
const vx=this.x,vy=this.y,vz=this.z;const qx=q.x,qy=q.y,qz=q.z,qw=q.w;// t = 2 * cross( q.xyz, v );
const tx=2*(qy*vz-qz*vy);const ty=2*(qz*vx-qx*vz);const tz=2*(qx*vy-qy*vx);// v + q.w * t + cross( q.xyz, t );
this.x=vx+qw*tx+qy*tz-qz*ty;this.y=vy+qw*ty+qz*tx-qx*tz;this.z=vz+qw*tz+qx*ty-qy*tx;return this;}project(camera){return this.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(camera.projectionMatrix);}unproject(camera){return this.applyMatrix4(camera.projectionMatrixInverse).applyMatrix4(camera.matrixWorld);}transformDirection(m){// input: THREE.Matrix4 affine matrix
// vector interpreted as a direction
const x=this.x,y=this.y,z=this.z;const e=m.elements;this.x=e[0]*x+e[4]*y+e[8]*z;this.y=e[1]*x+e[5]*y+e[9]*z;this.z=e[2]*x+e[6]*y+e[10]*z;return this.normalize();}divide(v){this.x/=v.x;this.y/=v.y;this.z/=v.z;return this;}divideScalar(scalar){return this.multiplyScalar(1/scalar);}min(v){this.x=Math.min(this.x,v.x);this.y=Math.min(this.y,v.y);this.z=Math.min(this.z,v.z);return this;}max(v){this.x=Math.max(this.x,v.x);this.y=Math.max(this.y,v.y);this.z=Math.max(this.z,v.z);return this;}clamp(min,max){// assumes min < max, componentwise
this.x=Math.max(min.x,Math.min(max.x,this.x));this.y=Math.max(min.y,Math.min(max.y,this.y));this.z=Math.max(min.z,Math.min(max.z,this.z));return this;}clampScalar(minVal,maxVal){this.x=Math.max(minVal,Math.min(maxVal,this.x));this.y=Math.max(minVal,Math.min(maxVal,this.y));this.z=Math.max(minVal,Math.min(maxVal,this.z));return this;}clampLength(min,max){const length=this.length();return this.divideScalar(length||1).multiplyScalar(Math.max(min,Math.min(max,length)));}floor(){this.x=Math.floor(this.x);this.y=Math.floor(this.y);this.z=Math.floor(this.z);return this;}ceil(){this.x=Math.ceil(this.x);this.y=Math.ceil(this.y);this.z=Math.ceil(this.z);return this;}round(){this.x=Math.round(this.x);this.y=Math.round(this.y);this.z=Math.round(this.z);return this;}roundToZero(){this.x=Math.trunc(this.x);this.y=Math.trunc(this.y);this.z=Math.trunc(this.z);return this;}negate(){this.x=-this.x;this.y=-this.y;this.z=-this.z;return this;}dot(v){return this.x*v.x+this.y*v.y+this.z*v.z;}// TODO lengthSquared?
lengthSq(){return this.x*this.x+this.y*this.y+this.z*this.z;}length(){return Math.sqrt(this.x*this.x+this.y*this.y+this.z*this.z);}manhattanLength(){return Math.abs(this.x)+Math.abs(this.y)+Math.abs(this.z);}normalize(){return this.divideScalar(this.length()||1);}setLength(length){return this.normalize().multiplyScalar(length);}lerp(v,alpha){this.x+=(v.x-this.x)*alpha;this.y+=(v.y-this.y)*alpha;this.z+=(v.z-this.z)*alpha;return this;}lerpVectors(v1,v2,alpha){this.x=v1.x+(v2.x-v1.x)*alpha;this.y=v1.y+(v2.y-v1.y)*alpha;this.z=v1.z+(v2.z-v1.z)*alpha;return this;}cross(v){return this.crossVectors(this,v);}crossVectors(a,b){const ax=a.x,ay=a.y,az=a.z;const bx=b.x,by=b.y,bz=b.z;this.x=ay*bz-az*by;this.y=az*bx-ax*bz;this.z=ax*by-ay*bx;return this;}projectOnVector(v){const denominator=v.lengthSq();if(denominator===0)return this.set(0,0,0);const scalar=v.dot(this)/denominator;return this.copy(v).multiplyScalar(scalar);}projectOnPlane(planeNormal){_vector$c.copy(this).projectOnVector(planeNormal);return this.sub(_vector$c);}reflect(normal){// reflect incident vector off plane orthogonal to normal
// normal is assumed to have unit length
return this.sub(_vector$c.copy(normal).multiplyScalar(2*this.dot(normal)));}angleTo(v){const denominator=Math.sqrt(this.lengthSq()*v.lengthSq());if(denominator===0)return Math.PI/2;const theta=this.dot(v)/denominator;// clamp, to handle numerical problems
return Math.acos(clamp$2(theta,-1,1));}distanceTo(v){return Math.sqrt(this.distanceToSquared(v));}distanceToSquared(v){const dx=this.x-v.x,dy=this.y-v.y,dz=this.z-v.z;return dx*dx+dy*dy+dz*dz;}manhattanDistanceTo(v){return Math.abs(this.x-v.x)+Math.abs(this.y-v.y)+Math.abs(this.z-v.z);}setFromSpherical(s){return this.setFromSphericalCoords(s.radius,s.phi,s.theta);}setFromSphericalCoords(radius,phi,theta){const sinPhiRadius=Math.sin(phi)*radius;this.x=sinPhiRadius*Math.sin(theta);this.y=Math.cos(phi)*radius;this.z=sinPhiRadius*Math.cos(theta);return this;}setFromCylindrical(c){return this.setFromCylindricalCoords(c.radius,c.theta,c.y);}setFromCylindricalCoords(radius,theta,y){this.x=radius*Math.sin(theta);this.y=y;this.z=radius*Math.cos(theta);return this;}setFromMatrixPosition(m){const e=m.elements;this.x=e[12];this.y=e[13];this.z=e[14];return this;}setFromMatrixScale(m){const sx=this.setFromMatrixColumn(m,0).length();const sy=this.setFromMatrixColumn(m,1).length();const sz=this.setFromMatrixColumn(m,2).length();this.x=sx;this.y=sy;this.z=sz;return this;}setFromMatrixColumn(m,index){return this.fromArray(m.elements,index*4);}setFromMatrix3Column(m,index){return this.fromArray(m.elements,index*3);}setFromEuler(e){this.x=e._x;this.y=e._y;this.z=e._z;return this;}setFromColor(c){this.x=c.r;this.y=c.g;this.z=c.b;return this;}equals(v){return v.x===this.x&&v.y===this.y&&v.z===this.z;}fromArray(array,offset=0){this.x=array[offset];this.y=array[offset+1];this.z=array[offset+2];return this;}toArray(array=[],offset=0){array[offset]=this.x;array[offset+1]=this.y;array[offset+2]=this.z;return array;}fromBufferAttribute(attribute,index){this.x=attribute.getX(index);this.y=attribute.getY(index);this.z=attribute.getZ(index);return this;}random(){this.x=Math.random();this.y=Math.random();this.z=Math.random();return this;}randomDirection(){// https://mathworld.wolfram.com/SpherePointPicking.html
const theta=Math.random()*Math.PI*2;const u=Math.random()*2-1;const c=Math.sqrt(1-u*u);this.x=c*Math.cos(theta);this.y=u;this.z=c*Math.sin(theta);return this;}*[Symbol.iterator](){yield this.x;yield this.y;yield this.z;}}const _vector$c=/*@__PURE__*/new Vector3$1();const _quaternion$4=/*@__PURE__*/new Quaternion$1();class Box3$1{constructor(min=new Vector3$1(+Infinity,+Infinity,+Infinity),max=new Vector3$1(-Infinity,-Infinity,-Infinity)){this.isBox3=true;this.min=min;this.max=max;}set(min,max){this.min.copy(min);this.max.copy(max);return this;}setFromArray(array){this.makeEmpty();for(let i=0,il=array.length;i<il;i+=3){this.expandByPoint(_vector$b.fromArray(array,i));}return this;}setFromBufferAttribute(attribute){this.makeEmpty();for(let i=0,il=attribute.count;i<il;i++){this.expandByPoint(_vector$b.fromBufferAttribute(attribute,i));}return this;}setFromPoints(points){this.makeEmpty();for(let i=0,il=points.length;i<il;i++){this.expandByPoint(points[i]);}return this;}setFromCenterAndSize(center,size){const halfSize=_vector$b.copy(size).multiplyScalar(0.5);this.min.copy(center).sub(halfSize);this.max.copy(center).add(halfSize);return this;}setFromObject(object,precise=false){this.makeEmpty();return this.expandByObject(object,precise);}clone(){return new this.constructor().copy(this);}copy(box){this.min.copy(box.min);this.max.copy(box.max);return this;}makeEmpty(){this.min.x=this.min.y=this.min.z=+Infinity;this.max.x=this.max.y=this.max.z=-Infinity;return this;}isEmpty(){// this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes
return this.max.x<this.min.x||this.max.y<this.min.y||this.max.z<this.min.z;}getCenter(target){return this.isEmpty()?target.set(0,0,0):target.addVectors(this.min,this.max).multiplyScalar(0.5);}getSize(target){return this.isEmpty()?target.set(0,0,0):target.subVectors(this.max,this.min);}expandByPoint(point){this.min.min(point);this.max.max(point);return this;}expandByVector(vector){this.min.sub(vector);this.max.add(vector);return this;}expandByScalar(scalar){this.min.addScalar(-scalar);this.max.addScalar(scalar);return this;}expandByObject(object,precise=false){// Computes the world-axis-aligned bounding box of an object (including its children),
// accounting for both the object's, and children's, world transforms
object.updateWorldMatrix(false,false);const geometry=object.geometry;if(geometry!==undefined){const positionAttribute=geometry.getAttribute('position');// precise AABB computation based on vertex data requires at least a position attribute.
// instancing isn't supported so far and uses the normal (conservative) code path.
if(precise===true&&positionAttribute!==undefined&&object.isInstancedMesh!==true){for(let i=0,l=positionAttribute.count;i<l;i++){if(object.isMesh===true){object.getVertexPosition(i,_vector$b);}else {_vector$b.fromBufferAttribute(positionAttribute,i);}_vector$b.applyMatrix4(object.matrixWorld);this.expandByPoint(_vector$b);}}else {if(object.boundingBox!==undefined){// object-level bounding box
if(object.boundingBox===null){object.computeBoundingBox();}_box$4.copy(object.boundingBox);}else {// geometry-level bounding box
if(geometry.boundingBox===null){geometry.computeBoundingBox();}_box$4.copy(geometry.boundingBox);}_box$4.applyMatrix4(object.matrixWorld);this.union(_box$4);}}const children=object.children;for(let i=0,l=children.length;i<l;i++){this.expandByObject(children[i],precise);}return this;}containsPoint(point){return point.x>=this.min.x&&point.x<=this.max.x&&point.y>=this.min.y&&point.y<=this.max.y&&point.z>=this.min.z&&point.z<=this.max.z;}containsBox(box){return this.min.x<=box.min.x&&box.max.x<=this.max.x&&this.min.y<=box.min.y&&box.max.y<=this.max.y&&this.min.z<=box.min.z&&box.max.z<=this.max.z;}getParameter(point,target){// This can potentially have a divide by zero if the box
// has a size dimension of 0.
return target.set((point.x-this.min.x)/(this.max.x-this.min.x),(point.y-this.min.y)/(this.max.y-this.min.y),(point.z-this.min.z)/(this.max.z-this.min.z));}intersectsBox(box){// using 6 splitting planes to rule out intersections.
return box.max.x>=this.min.x&&box.min.x<=this.max.x&&box.max.y>=this.min.y&&box.min.y<=this.max.y&&box.max.z>=this.min.z&&box.min.z<=this.max.z;}intersectsSphere(sphere){// Find the point on the AABB closest to the sphere center.
this.clampPoint(sphere.center,_vector$b);// If that point is inside the sphere, the AABB and sphere intersect.
return _vector$b.distanceToSquared(sphere.center)<=sphere.radius*sphere.radius;}intersectsPlane(plane){// We compute the minimum and maximum dot product values. If those values
// are on the same side (back or front) of the plane, then there is no intersection.
let min,max;if(plane.normal.x>0){min=plane.normal.x*this.min.x;max=plane.normal.x*this.max.x;}else {min=plane.normal.x*this.max.x;max=plane.normal.x*this.min.x;}if(plane.normal.y>0){min+=plane.normal.y*this.min.y;max+=plane.normal.y*this.max.y;}else {min+=plane.normal.y*this.max.y;max+=plane.normal.y*this.min.y;}if(plane.normal.z>0){min+=plane.normal.z*this.min.z;max+=plane.normal.z*this.max.z;}else {min+=plane.normal.z*this.max.z;max+=plane.normal.z*this.min.z;}return min<=-plane.constant&&max>=-plane.constant;}intersectsTriangle(triangle){if(this.isEmpty()){return false;}// compute box center and extents
this.getCenter(_center$1);_extents$1.subVectors(this.max,_center$1);// translate triangle to aabb origin
_v0$3.subVectors(triangle.a,_center$1);_v1$7.subVectors(triangle.b,_center$1);_v2$4.subVectors(triangle.c,_center$1);// compute edge vectors for triangle
_f0$1.subVectors(_v1$7,_v0$3);_f1$1.subVectors(_v2$4,_v1$7);_f2$1.subVectors(_v0$3,_v2$4);// test against axes that are given by cross product combinations of the edges of the triangle and the edges of the aabb
// make an axis testing of each of the 3 sides of the aabb against each of the 3 sides of the triangle = 9 axis of separation
// axis_ij = u_i x f_j (u0, u1, u2 = face normals of aabb = x,y,z axes vectors since aabb is axis aligned)
let axes=[0,-_f0$1.z,_f0$1.y,0,-_f1$1.z,_f1$1.y,0,-_f2$1.z,_f2$1.y,_f0$1.z,0,-_f0$1.x,_f1$1.z,0,-_f1$1.x,_f2$1.z,0,-_f2$1.x,-_f0$1.y,_f0$1.x,0,-_f1$1.y,_f1$1.x,0,-_f2$1.y,_f2$1.x,0];if(!satForAxes$1(axes,_v0$3,_v1$7,_v2$4,_extents$1)){return false;}// test 3 face normals from the aabb
axes=[1,0,0,0,1,0,0,0,1];if(!satForAxes$1(axes,_v0$3,_v1$7,_v2$4,_extents$1)){return false;}// finally testing the face normal of the triangle
// use already existing triangle edge vectors here
_triangleNormal$1.crossVectors(_f0$1,_f1$1);axes=[_triangleNormal$1.x,_triangleNormal$1.y,_triangleNormal$1.z];return satForAxes$1(axes,_v0$3,_v1$7,_v2$4,_extents$1);}clampPoint(point,target){return target.copy(point).clamp(this.min,this.max);}distanceToPoint(point){return this.clampPoint(point,_vector$b).distanceTo(point);}getBoundingSphere(target){if(this.isEmpty()){target.makeEmpty();}else {this.getCenter(target.center);target.radius=this.getSize(_vector$b).length()*0.5;}return target;}intersect(box){this.min.max(box.min);this.max.min(box.max);// ensure that if there is no overlap, the result is fully empty, not slightly empty with non-inf/+inf values that will cause subsequence intersects to erroneously return valid values.
if(this.isEmpty())this.makeEmpty();return this;}union(box){this.min.min(box.min);this.max.max(box.max);return this;}applyMatrix4(matrix){// transform of empty box is an empty box.
if(this.isEmpty())return this;// NOTE: I am using a binary pattern to specify all 2^3 combinations below
_points$1[0].set(this.min.x,this.min.y,this.min.z).applyMatrix4(matrix);// 000
_points$1[1].set(this.min.x,this.min.y,this.max.z).applyMatrix4(matrix);// 001
_points$1[2].set(this.min.x,this.max.y,this.min.z).applyMatrix4(matrix);// 010
_points$1[3].set(this.min.x,this.max.y,this.max.z).applyMatrix4(matrix);// 011
_points$1[4].set(this.max.x,this.min.y,this.min.z).applyMatrix4(matrix);// 100
_points$1[5].set(this.max.x,this.min.y,this.max.z).applyMatrix4(matrix);// 101
_points$1[6].set(this.max.x,this.max.y,this.min.z).applyMatrix4(matrix);// 110
_points$1[7].set(this.max.x,this.max.y,this.max.z).applyMatrix4(matrix);// 111
this.setFromPoints(_points$1);return this;}translate(offset){this.min.add(offset);this.max.add(offset);return this;}equals(box){return box.min.equals(this.min)&&box.max.equals(this.max);}}const _points$1=[/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1(),/*@__PURE__*/new Vector3$1()];const _vector$b=/*@__PURE__*/new Vector3$1();const _box$4=/*@__PURE__*/new Box3$1();// triangle centered vertices
const _v0$3=/*@__PURE__*/new Vector3$1();const _v1$7=/*@__PURE__*/new Vector3$1();const _v2$4=/*@__PURE__*/new Vector3$1();// triangle edge vectors
const _f0$1=/*@__PURE__*/new Vector3$1();const _f1$1=/*@__PURE__*/new Vector3$1();const _f2$1=/*@__PURE__*/new Vector3$1();const _center$1=/*@__PURE__*/new Vector3$1();const _extents$1=/*@__PURE__*/new Vector3$1();const _triangleNormal$1=/*@__PURE__*/new Vector3$1();const _testAxis$1=/*@__PURE__*/new Vector3$1();function satForAxes$1(axes,v0,v1,v2,extents){for(let i=0,j=axes.length-3;i<=j;i+=3){_testAxis$1.fromArray(axes,i);// project the aabb onto the separating axis
const r=extents.x*Math.abs(_testAxis$1.x)+extents.y*Math.abs(_testAxis$1.y)+extents.z*Math.abs(_testAxis$1.z);// project all 3 vertices of the triangle onto the separating axis
const p0=v0.dot(_testAxis$1);const p1=v1.dot(_testAxis$1);const p2=v2.dot(_testAxis$1);// actual test, basically see if either of the most extreme of the triangle points intersects r
if(Math.max(-Math.max(p0,p1,p2),Math.min(p0,p1,p2))>r){// points of the projected triangle are outside the projected half-length of the aabb
// the axis is separating and we can exit
return false;}}return true;}const _box$3=/*@__PURE__*/new Box3$1();const _v1$6=/*@__PURE__*/new Vector3$1();const _v2$3=/*@__PURE__*/new Vector3$1();class Sphere$1{constructor(center=new Vector3$1(),radius=-1){this.isSphere=true;this.center=center;this.radius=radius;}set(center,radius){this.center.copy(center);this.radius=radius;return this;}setFromPoints(points,optionalCenter){const center=this.center;if(optionalCenter!==undefined){center.copy(optionalCenter);}else {_box$3.setFromPoints(points).getCenter(center);}let maxRadiusSq=0;for(let i=0,il=points.length;i<il;i++){maxRadiusSq=Math.max(maxRadiusSq,center.distanceToSquared(points[i]));}this.radius=Math.sqrt(maxRadiusSq);return this;}copy(sphere){this.center.copy(sphere.center);this.radius=sphere.radius;return this;}isEmpty(){return this.radius<0;}makeEmpty(){this.center.set(0,0,0);this.radius=-1;return this;}containsPoint(point){return point.distanceToSquared(this.center)<=this.radius*this.radius;}distanceToPoint(point){return point.distanceTo(this.center)-this.radius;}intersectsSphere(sphere){const radiusSum=this.radius+sphere.radius;return sphere.center.distanceToSquared(this.center)<=radiusSum*radiusSum;}intersectsBox(box){return box.intersectsSphere(this);}intersectsPlane(plane){return Math.abs(plane.distanceToPoint(this.center))<=this.radius;}clampPoint(point,target){const deltaLengthSq=this.center.distanceToSquared(point);target.copy(point);if(deltaLengthSq>this.radius*this.radius){target.sub(this.center).normalize();target.multiplyScalar(this.radius).add(this.center);}return target;}getBoundingBox(target){if(this.isEmpty()){// Empty sphere produces empty bounding box
target.makeEmpty();return target;}target.set(this.center,this.center);target.expandByScalar(this.radius);return target;}applyMatrix4(matrix){this.center.applyMatrix4(matrix);this.radius=this.radius*matrix.getMaxScaleOnAxis();return this;}translate(offset){this.center.add(offset);return this;}expandByPoint(point){if(this.isEmpty()){this.center.copy(point);this.radius=0;return this;}_v1$6.subVectors(point,this.center);const lengthSq=_v1$6.lengthSq();if(lengthSq>this.radius*this.radius){// calculate the minimal sphere
const length=Math.sqrt(lengthSq);const delta=(length-this.radius)*0.5;this.center.addScaledVector(_v1$6,delta/length);this.radius+=delta;}return this;}union(sphere){if(sphere.isEmpty()){return this;}if(this.isEmpty()){this.copy(sphere);return this;}if(this.center.equals(sphere.center)===true){this.radius=Math.max(this.radius,sphere.radius);}else {_v2$3.subVectors(sphere.center,this.center).setLength(sphere.radius);this.expandByPoint(_v1$6.copy(sphere.center).add(_v2$3));this.expandByPoint(_v1$6.copy(sphere.center).sub(_v2$3));}return this;}equals(sphere){return sphere.center.equals(this.center)&&sphere.radius===this.radius;}clone(){return new this.constructor().copy(this);}}const _vector$a=/*@__PURE__*/new Vector3$1();const _segCenter$1=/*@__PURE__*/new Vector3$1();const _segDir$1=/*@__PURE__*/new Vector3$1();const _diff$1=/*@__PURE__*/new Vector3$1();const _edge1$1=/*@__PURE__*/new Vector3$1();const _edge2$1=/*@__PURE__*/new Vector3$1();const _normal$1$1=/*@__PURE__*/new Vector3$1();class Ray$1{constructor(origin=new Vector3$1(),direction=new Vector3$1(0,0,-1)){this.origin=origin;this.direction=direction;}set(origin,direction){this.origin.copy(origin);this.direction.copy(direction);return this;}copy(ray){this.origin.copy(ray.origin);this.direction.copy(ray.direction);return this;}at(t,target){return target.copy(this.origin).addScaledVector(this.direction,t);}lookAt(v){this.direction.copy(v).sub(this.origin).normalize();return this;}recast(t){this.origin.copy(this.at(t,_vector$a));return this;}closestPointToPoint(point,target){target.subVectors(point,this.origin);const directionDistance=target.dot(this.direction);if(directionDistance<0){return target.copy(this.origin);}return target.copy(this.origin).addScaledVector(this.direction,directionDistance);}distanceToPoint(point){return Math.sqrt(this.distanceSqToPoint(point));}distanceSqToPoint(point){const directionDistance=_vector$a.subVectors(point,this.origin).dot(this.direction);// point behind the ray
if(directionDistance<0){return this.origin.distanceToSquared(point);}_vector$a.copy(this.origin).addScaledVector(this.direction,directionDistance);return _vector$a.distanceToSquared(point);}distanceSqToSegment(v0,v1,optionalPointOnRay,optionalPointOnSegment){// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteDistRaySegment.h
// It returns the min distance between the ray and the segment
// defined by v0 and v1
// It can also set two optional targets :
// - The closest point on the ray
// - The closest point on the segment
_segCenter$1.copy(v0).add(v1).multiplyScalar(0.5);_segDir$1.copy(v1).sub(v0).normalize();_diff$1.copy(this.origin).sub(_segCenter$1);const segExtent=v0.distanceTo(v1)*0.5;const a01=-this.direction.dot(_segDir$1);const b0=_diff$1.dot(this.direction);const b1=-_diff$1.dot(_segDir$1);const c=_diff$1.lengthSq();const det=Math.abs(1-a01*a01);let s0,s1,sqrDist,extDet;if(det>0){// The ray and segment are not parallel.
s0=a01*b1-b0;s1=a01*b0-b1;extDet=segExtent*det;if(s0>=0){if(s1>=-extDet){if(s1<=extDet){// region 0
// Minimum at interior points of ray and segment.
const invDet=1/det;s0*=invDet;s1*=invDet;sqrDist=s0*(s0+a01*s1+2*b0)+s1*(a01*s0+s1+2*b1)+c;}else {// region 1
s1=segExtent;s0=Math.max(0,-(a01*s1+b0));sqrDist=-s0*s0+s1*(s1+2*b1)+c;}}else {// region 5
s1=-segExtent;s0=Math.max(0,-(a01*s1+b0));sqrDist=-s0*s0+s1*(s1+2*b1)+c;}}else {if(s1<=-extDet){// region 4
s0=Math.max(0,-(-a01*segExtent+b0));s1=s0>0?-segExtent:Math.min(Math.max(-segExtent,-b1),segExtent);sqrDist=-s0*s0+s1*(s1+2*b1)+c;}else if(s1<=extDet){// region 3
s0=0;s1=Math.min(Math.max(-segExtent,-b1),segExtent);sqrDist=s1*(s1+2*b1)+c;}else {// region 2
s0=Math.max(0,-(a01*segExtent+b0));s1=s0>0?segExtent:Math.min(Math.max(-segExtent,-b1),segExtent);sqrDist=-s0*s0+s1*(s1+2*b1)+c;}}}else {// Ray and segment are parallel.
s1=a01>0?-segExtent:segExtent;s0=Math.max(0,-(a01*s1+b0));sqrDist=-s0*s0+s1*(s1+2*b1)+c;}if(optionalPointOnRay){optionalPointOnRay.copy(this.origin).addScaledVector(this.direction,s0);}if(optionalPointOnSegment){optionalPointOnSegment.copy(_segCenter$1).addScaledVector(_segDir$1,s1);}return sqrDist;}intersectSphere(sphere,target){_vector$a.subVectors(sphere.center,this.origin);const tca=_vector$a.dot(this.direction);const d2=_vector$a.dot(_vector$a)-tca*tca;const radius2=sphere.radius*sphere.radius;if(d2>radius2)return null;const thc=Math.sqrt(radius2-d2);// t0 = first intersect point - entrance on front of sphere
const t0=tca-thc;// t1 = second intersect point - exit point on back of sphere
const t1=tca+thc;// test to see if t1 is behind the ray - if so, return null
if(t1<0)return null;// test to see if t0 is behind the ray:
// if it is, the ray is inside the sphere, so return the second exit point scaled by t1,
// in order to always return an intersect point that is in front of the ray.
if(t0<0)return this.at(t1,target);// else t0 is in front of the ray, so return the first collision point scaled by t0
return this.at(t0,target);}intersectsSphere(sphere){return this.distanceSqToPoint(sphere.center)<=sphere.radius*sphere.radius;}distanceToPlane(plane){const denominator=plane.normal.dot(this.direction);if(denominator===0){// line is coplanar, return origin
if(plane.distanceToPoint(this.origin)===0){return 0;}// Null is preferable to undefined since undefined means.... it is undefined
return null;}const t=-(this.origin.dot(plane.normal)+plane.constant)/denominator;// Return if the ray never intersects the plane
return t>=0?t:null;}intersectPlane(plane,target){const t=this.distanceToPlane(plane);if(t===null){return null;}return this.at(t,target);}intersectsPlane(plane){// check if the ray lies on the plane first
const distToPoint=plane.distanceToPoint(this.origin);if(distToPoint===0){return true;}const denominator=plane.normal.dot(this.direction);if(denominator*distToPoint<0){return true;}// ray origin is behind the plane (and is pointing behind it)
return false;}intersectBox(box,target){let tmin,tmax,tymin,tymax,tzmin,tzmax;const invdirx=1/this.direction.x,invdiry=1/this.direction.y,invdirz=1/this.direction.z;const origin=this.origin;if(invdirx>=0){tmin=(box.min.x-origin.x)*invdirx;tmax=(box.max.x-origin.x)*invdirx;}else {tmin=(box.max.x-origin.x)*invdirx;tmax=(box.min.x-origin.x)*invdirx;}if(invdiry>=0){tymin=(box.min.y-origin.y)*invdiry;tymax=(box.max.y-origin.y)*invdiry;}else {tymin=(box.max.y-origin.y)*invdiry;tymax=(box.min.y-origin.y)*invdiry;}if(tmin>tymax||tymin>tmax)return null;if(tymin>tmin||isNaN(tmin))tmin=tymin;if(tymax<tmax||isNaN(tmax))tmax=tymax;if(invdirz>=0){tzmin=(box.min.z-origin.z)*invdirz;tzmax=(box.max.z-origin.z)*invdirz;}else {tzmin=(box.max.z-origin.z)*invdirz;tzmax=(box.min.z-origin.z)*invdirz;}if(tmin>tzmax||tzmin>tmax)return null;if(tzmin>tmin||tmin!==tmin)tmin=tzmin;if(tzmax<tmax||tmax!==tmax)tmax=tzmax;//return point closest to the ray (positive side)
if(tmax<0)return null;return this.at(tmin>=0?tmin:tmax,target);}intersectsBox(box){return this.intersectBox(box,_vector$a)!==null;}intersectTriangle(a,b,c,backfaceCulling,target){// Compute the offset origin, edges, and normal.
// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteIntrRay3Triangle3.h
_edge1$1.subVectors(b,a);_edge2$1.subVectors(c,a);_normal$1$1.crossVectors(_edge1$1,_edge2$1);// Solve Q + t*D = b1*E1 + b2*E2 (Q = kDiff, D = ray direction,
// E1 = kEdge1, E2 = kEdge2, N = Cross(E1,E2)) by
//   |Dot(D,N)|*b1 = sign(Dot(D,N))*Dot(D,Cross(Q,E2))
//   |Dot(D,N)|*b2 = sign(Dot(D,N))*Dot(D,Cross(E1,Q))
//   |Dot(D,N)|*t = -sign(Dot(D,N))*Dot(Q,N)
let DdN=this.direction.dot(_normal$1$1);let sign;if(DdN>0){if(backfaceCulling)return null;sign=1;}else if(DdN<0){sign=-1;DdN=-DdN;}else {return null;}_diff$1.subVectors(this.origin,a);const DdQxE2=sign*this.direction.dot(_edge2$1.crossVectors(_diff$1,_edge2$1));// b1 < 0, no intersection
if(DdQxE2<0){return null;}const DdE1xQ=sign*this.direction.dot(_edge1$1.cross(_diff$1));// b2 < 0, no intersection
if(DdE1xQ<0){return null;}// b1+b2 > 1, no intersection
if(DdQxE2+DdE1xQ>DdN){return null;}// Line intersects triangle, check if ray does.
const QdN=-sign*_diff$1.dot(_normal$1$1);// t < 0, no intersection
if(QdN<0){return null;}// Ray intersects triangle.
return this.at(QdN/DdN,target);}applyMatrix4(matrix4){this.origin.applyMatrix4(matrix4);this.direction.transformDirection(matrix4);return this;}equals(ray){return ray.origin.equals(this.origin)&&ray.direction.equals(this.direction);}clone(){return new this.constructor().copy(this);}}class Matrix4$1{constructor(n11,n12,n13,n14,n21,n22,n23,n24,n31,n32,n33,n34,n41,n42,n43,n44){Matrix4$1.prototype.isMatrix4=true;this.elements=[1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1];if(n11!==undefined){this.set(n11,n12,n13,n14,n21,n22,n23,n24,n31,n32,n33,n34,n41,n42,n43,n44);}}set(n11,n12,n13,n14,n21,n22,n23,n24,n31,n32,n33,n34,n41,n42,n43,n44){const te=this.elements;te[0]=n11;te[4]=n12;te[8]=n13;te[12]=n14;te[1]=n21;te[5]=n22;te[9]=n23;te[13]=n24;te[2]=n31;te[6]=n32;te[10]=n33;te[14]=n34;te[3]=n41;te[7]=n42;te[11]=n43;te[15]=n44;return this;}identity(){this.set(1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1);return this;}clone(){return new Matrix4$1().fromArray(this.elements);}copy(m){const te=this.elements;const me=m.elements;te[0]=me[0];te[1]=me[1];te[2]=me[2];te[3]=me[3];te[4]=me[4];te[5]=me[5];te[6]=me[6];te[7]=me[7];te[8]=me[8];te[9]=me[9];te[10]=me[10];te[11]=me[11];te[12]=me[12];te[13]=me[13];te[14]=me[14];te[15]=me[15];return this;}copyPosition(m){const te=this.elements,me=m.elements;te[12]=me[12];te[13]=me[13];te[14]=me[14];return this;}setFromMatrix3(m){const me=m.elements;this.set(me[0],me[3],me[6],0,me[1],me[4],me[7],0,me[2],me[5],me[8],0,0,0,0,1);return this;}extractBasis(xAxis,yAxis,zAxis){xAxis.setFromMatrixColumn(this,0);yAxis.setFromMatrixColumn(this,1);zAxis.setFromMatrixColumn(this,2);return this;}makeBasis(xAxis,yAxis,zAxis){this.set(xAxis.x,yAxis.x,zAxis.x,0,xAxis.y,yAxis.y,zAxis.y,0,xAxis.z,yAxis.z,zAxis.z,0,0,0,0,1);return this;}extractRotation(m){// this method does not support reflection matrices
const te=this.elements;const me=m.elements;const scaleX=1/_v1$5.setFromMatrixColumn(m,0).length();const scaleY=1/_v1$5.setFromMatrixColumn(m,1).length();const scaleZ=1/_v1$5.setFromMatrixColumn(m,2).length();te[0]=me[0]*scaleX;te[1]=me[1]*scaleX;te[2]=me[2]*scaleX;te[3]=0;te[4]=me[4]*scaleY;te[5]=me[5]*scaleY;te[6]=me[6]*scaleY;te[7]=0;te[8]=me[8]*scaleZ;te[9]=me[9]*scaleZ;te[10]=me[10]*scaleZ;te[11]=0;te[12]=0;te[13]=0;te[14]=0;te[15]=1;return this;}makeRotationFromEuler(euler){const te=this.elements;const x=euler.x,y=euler.y,z=euler.z;const a=Math.cos(x),b=Math.sin(x);const c=Math.cos(y),d=Math.sin(y);const e=Math.cos(z),f=Math.sin(z);if(euler.order==='XYZ'){const ae=a*e,af=a*f,be=b*e,bf=b*f;te[0]=c*e;te[4]=-c*f;te[8]=d;te[1]=af+be*d;te[5]=ae-bf*d;te[9]=-b*c;te[2]=bf-ae*d;te[6]=be+af*d;te[10]=a*c;}else if(euler.order==='YXZ'){const ce=c*e,cf=c*f,de=d*e,df=d*f;te[0]=ce+df*b;te[4]=de*b-cf;te[8]=a*d;te[1]=a*f;te[5]=a*e;te[9]=-b;te[2]=cf*b-de;te[6]=df+ce*b;te[10]=a*c;}else if(euler.order==='ZXY'){const ce=c*e,cf=c*f,de=d*e,df=d*f;te[0]=ce-df*b;te[4]=-a*f;te[8]=de+cf*b;te[1]=cf+de*b;te[5]=a*e;te[9]=df-ce*b;te[2]=-a*d;te[6]=b;te[10]=a*c;}else if(euler.order==='ZYX'){const ae=a*e,af=a*f,be=b*e,bf=b*f;te[0]=c*e;te[4]=be*d-af;te[8]=ae*d+bf;te[1]=c*f;te[5]=bf*d+ae;te[9]=af*d-be;te[2]=-d;te[6]=b*c;te[10]=a*c;}else if(euler.order==='YZX'){const ac=a*c,ad=a*d,bc=b*c,bd=b*d;te[0]=c*e;te[4]=bd-ac*f;te[8]=bc*f+ad;te[1]=f;te[5]=a*e;te[9]=-b*e;te[2]=-d*e;te[6]=ad*f+bc;te[10]=ac-bd*f;}else if(euler.order==='XZY'){const ac=a*c,ad=a*d,bc=b*c,bd=b*d;te[0]=c*e;te[4]=-f;te[8]=d*e;te[1]=ac*f+bd;te[5]=a*e;te[9]=ad*f-bc;te[2]=bc*f-ad;te[6]=b*e;te[10]=bd*f+ac;}// bottom row
te[3]=0;te[7]=0;te[11]=0;// last column
te[12]=0;te[13]=0;te[14]=0;te[15]=1;return this;}makeRotationFromQuaternion(q){return this.compose(_zero$1,q,_one$1);}lookAt(eye,target,up){const te=this.elements;_z$1.subVectors(eye,target);if(_z$1.lengthSq()===0){// eye and target are in the same position
_z$1.z=1;}_z$1.normalize();_x$1.crossVectors(up,_z$1);if(_x$1.lengthSq()===0){// up and z are parallel
if(Math.abs(up.z)===1){_z$1.x+=0.0001;}else {_z$1.z+=0.0001;}_z$1.normalize();_x$1.crossVectors(up,_z$1);}_x$1.normalize();_y$1.crossVectors(_z$1,_x$1);te[0]=_x$1.x;te[4]=_y$1.x;te[8]=_z$1.x;te[1]=_x$1.y;te[5]=_y$1.y;te[9]=_z$1.y;te[2]=_x$1.z;te[6]=_y$1.z;te[10]=_z$1.z;return this;}multiply(m){return this.multiplyMatrices(this,m);}premultiply(m){return this.multiplyMatrices(m,this);}multiplyMatrices(a,b){const ae=a.elements;const be=b.elements;const te=this.elements;const a11=ae[0],a12=ae[4],a13=ae[8],a14=ae[12];const a21=ae[1],a22=ae[5],a23=ae[9],a24=ae[13];const a31=ae[2],a32=ae[6],a33=ae[10],a34=ae[14];const a41=ae[3],a42=ae[7],a43=ae[11],a44=ae[15];const b11=be[0],b12=be[4],b13=be[8],b14=be[12];const b21=be[1],b22=be[5],b23=be[9],b24=be[13];const b31=be[2],b32=be[6],b33=be[10],b34=be[14];const b41=be[3],b42=be[7],b43=be[11],b44=be[15];te[0]=a11*b11+a12*b21+a13*b31+a14*b41;te[4]=a11*b12+a12*b22+a13*b32+a14*b42;te[8]=a11*b13+a12*b23+a13*b33+a14*b43;te[12]=a11*b14+a12*b24+a13*b34+a14*b44;te[1]=a21*b11+a22*b21+a23*b31+a24*b41;te[5]=a21*b12+a22*b22+a23*b32+a24*b42;te[9]=a21*b13+a22*b23+a23*b33+a24*b43;te[13]=a21*b14+a22*b24+a23*b34+a24*b44;te[2]=a31*b11+a32*b21+a33*b31+a34*b41;te[6]=a31*b12+a32*b22+a33*b32+a34*b42;te[10]=a31*b13+a32*b23+a33*b33+a34*b43;te[14]=a31*b14+a32*b24+a33*b34+a34*b44;te[3]=a41*b11+a42*b21+a43*b31+a44*b41;te[7]=a41*b12+a42*b22+a43*b32+a44*b42;te[11]=a41*b13+a42*b23+a43*b33+a44*b43;te[15]=a41*b14+a42*b24+a43*b34+a44*b44;return this;}multiplyScalar(s){const te=this.elements;te[0]*=s;te[4]*=s;te[8]*=s;te[12]*=s;te[1]*=s;te[5]*=s;te[9]*=s;te[13]*=s;te[2]*=s;te[6]*=s;te[10]*=s;te[14]*=s;te[3]*=s;te[7]*=s;te[11]*=s;te[15]*=s;return this;}determinant(){const te=this.elements;const n11=te[0],n12=te[4],n13=te[8],n14=te[12];const n21=te[1],n22=te[5],n23=te[9],n24=te[13];const n31=te[2],n32=te[6],n33=te[10],n34=te[14];const n41=te[3],n42=te[7],n43=te[11],n44=te[15];//TODO: make this more efficient
//( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )
return n41*(+n14*n23*n32-n13*n24*n32-n14*n22*n33+n12*n24*n33+n13*n22*n34-n12*n23*n34)+n42*(+n11*n23*n34-n11*n24*n33+n14*n21*n33-n13*n21*n34+n13*n24*n31-n14*n23*n31)+n43*(+n11*n24*n32-n11*n22*n34-n14*n21*n32+n12*n21*n34+n14*n22*n31-n12*n24*n31)+n44*(-n13*n22*n31-n11*n23*n32+n11*n22*n33+n13*n21*n32-n12*n21*n33+n12*n23*n31);}transpose(){const te=this.elements;let tmp;tmp=te[1];te[1]=te[4];te[4]=tmp;tmp=te[2];te[2]=te[8];te[8]=tmp;tmp=te[6];te[6]=te[9];te[9]=tmp;tmp=te[3];te[3]=te[12];te[12]=tmp;tmp=te[7];te[7]=te[13];te[13]=tmp;tmp=te[11];te[11]=te[14];te[14]=tmp;return this;}setPosition(x,y,z){const te=this.elements;if(x.isVector3){te[12]=x.x;te[13]=x.y;te[14]=x.z;}else {te[12]=x;te[13]=y;te[14]=z;}return this;}invert(){// based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
const te=this.elements,n11=te[0],n21=te[1],n31=te[2],n41=te[3],n12=te[4],n22=te[5],n32=te[6],n42=te[7],n13=te[8],n23=te[9],n33=te[10],n43=te[11],n14=te[12],n24=te[13],n34=te[14],n44=te[15],t11=n23*n34*n42-n24*n33*n42+n24*n32*n43-n22*n34*n43-n23*n32*n44+n22*n33*n44,t12=n14*n33*n42-n13*n34*n42-n14*n32*n43+n12*n34*n43+n13*n32*n44-n12*n33*n44,t13=n13*n24*n42-n14*n23*n42+n14*n22*n43-n12*n24*n43-n13*n22*n44+n12*n23*n44,t14=n14*n23*n32-n13*n24*n32-n14*n22*n33+n12*n24*n33+n13*n22*n34-n12*n23*n34;const det=n11*t11+n21*t12+n31*t13+n41*t14;if(det===0)return this.set(0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0);const detInv=1/det;te[0]=t11*detInv;te[1]=(n24*n33*n41-n23*n34*n41-n24*n31*n43+n21*n34*n43+n23*n31*n44-n21*n33*n44)*detInv;te[2]=(n22*n34*n41-n24*n32*n41+n24*n31*n42-n21*n34*n42-n22*n31*n44+n21*n32*n44)*detInv;te[3]=(n23*n32*n41-n22*n33*n41-n23*n31*n42+n21*n33*n42+n22*n31*n43-n21*n32*n43)*detInv;te[4]=t12*detInv;te[5]=(n13*n34*n41-n14*n33*n41+n14*n31*n43-n11*n34*n43-n13*n31*n44+n11*n33*n44)*detInv;te[6]=(n14*n32*n41-n12*n34*n41-n14*n31*n42+n11*n34*n42+n12*n31*n44-n11*n32*n44)*detInv;te[7]=(n12*n33*n41-n13*n32*n41+n13*n31*n42-n11*n33*n42-n12*n31*n43+n11*n32*n43)*detInv;te[8]=t13*detInv;te[9]=(n14*n23*n41-n13*n24*n41-n14*n21*n43+n11*n24*n43+n13*n21*n44-n11*n23*n44)*detInv;te[10]=(n12*n24*n41-n14*n22*n41+n14*n21*n42-n11*n24*n42-n12*n21*n44+n11*n22*n44)*detInv;te[11]=(n13*n22*n41-n12*n23*n41-n13*n21*n42+n11*n23*n42+n12*n21*n43-n11*n22*n43)*detInv;te[12]=t14*detInv;te[13]=(n13*n24*n31-n14*n23*n31+n14*n21*n33-n11*n24*n33-n13*n21*n34+n11*n23*n34)*detInv;te[14]=(n14*n22*n31-n12*n24*n31-n14*n21*n32+n11*n24*n32+n12*n21*n34-n11*n22*n34)*detInv;te[15]=(n12*n23*n31-n13*n22*n31+n13*n21*n32-n11*n23*n32-n12*n21*n33+n11*n22*n33)*detInv;return this;}scale(v){const te=this.elements;const x=v.x,y=v.y,z=v.z;te[0]*=x;te[4]*=y;te[8]*=z;te[1]*=x;te[5]*=y;te[9]*=z;te[2]*=x;te[6]*=y;te[10]*=z;te[3]*=x;te[7]*=y;te[11]*=z;return this;}getMaxScaleOnAxis(){const te=this.elements;const scaleXSq=te[0]*te[0]+te[1]*te[1]+te[2]*te[2];const scaleYSq=te[4]*te[4]+te[5]*te[5]+te[6]*te[6];const scaleZSq=te[8]*te[8]+te[9]*te[9]+te[10]*te[10];return Math.sqrt(Math.max(scaleXSq,scaleYSq,scaleZSq));}makeTranslation(x,y,z){if(x.isVector3){this.set(1,0,0,x.x,0,1,0,x.y,0,0,1,x.z,0,0,0,1);}else {this.set(1,0,0,x,0,1,0,y,0,0,1,z,0,0,0,1);}return this;}makeRotationX(theta){const c=Math.cos(theta),s=Math.sin(theta);this.set(1,0,0,0,0,c,-s,0,0,s,c,0,0,0,0,1);return this;}makeRotationY(theta){const c=Math.cos(theta),s=Math.sin(theta);this.set(c,0,s,0,0,1,0,0,-s,0,c,0,0,0,0,1);return this;}makeRotationZ(theta){const c=Math.cos(theta),s=Math.sin(theta);this.set(c,-s,0,0,s,c,0,0,0,0,1,0,0,0,0,1);return this;}makeRotationAxis(axis,angle){// Based on http://www.gamedev.net/reference/articles/article1199.asp
const c=Math.cos(angle);const s=Math.sin(angle);const t=1-c;const x=axis.x,y=axis.y,z=axis.z;const tx=t*x,ty=t*y;this.set(tx*x+c,tx*y-s*z,tx*z+s*y,0,tx*y+s*z,ty*y+c,ty*z-s*x,0,tx*z-s*y,ty*z+s*x,t*z*z+c,0,0,0,0,1);return this;}makeScale(x,y,z){this.set(x,0,0,0,0,y,0,0,0,0,z,0,0,0,0,1);return this;}makeShear(xy,xz,yx,yz,zx,zy){this.set(1,yx,zx,0,xy,1,zy,0,xz,yz,1,0,0,0,0,1);return this;}compose(position,quaternion,scale){const te=this.elements;const x=quaternion._x,y=quaternion._y,z=quaternion._z,w=quaternion._w;const x2=x+x,y2=y+y,z2=z+z;const xx=x*x2,xy=x*y2,xz=x*z2;const yy=y*y2,yz=y*z2,zz=z*z2;const wx=w*x2,wy=w*y2,wz=w*z2;const sx=scale.x,sy=scale.y,sz=scale.z;te[0]=(1-(yy+zz))*sx;te[1]=(xy+wz)*sx;te[2]=(xz-wy)*sx;te[3]=0;te[4]=(xy-wz)*sy;te[5]=(1-(xx+zz))*sy;te[6]=(yz+wx)*sy;te[7]=0;te[8]=(xz+wy)*sz;te[9]=(yz-wx)*sz;te[10]=(1-(xx+yy))*sz;te[11]=0;te[12]=position.x;te[13]=position.y;te[14]=position.z;te[15]=1;return this;}decompose(position,quaternion,scale){const te=this.elements;let sx=_v1$5.set(te[0],te[1],te[2]).length();const sy=_v1$5.set(te[4],te[5],te[6]).length();const sz=_v1$5.set(te[8],te[9],te[10]).length();// if determine is negative, we need to invert one scale
const det=this.determinant();if(det<0)sx=-sx;position.x=te[12];position.y=te[13];position.z=te[14];// scale the rotation part
_m1$4.copy(this);const invSX=1/sx;const invSY=1/sy;const invSZ=1/sz;_m1$4.elements[0]*=invSX;_m1$4.elements[1]*=invSX;_m1$4.elements[2]*=invSX;_m1$4.elements[4]*=invSY;_m1$4.elements[5]*=invSY;_m1$4.elements[6]*=invSY;_m1$4.elements[8]*=invSZ;_m1$4.elements[9]*=invSZ;_m1$4.elements[10]*=invSZ;quaternion.setFromRotationMatrix(_m1$4);scale.x=sx;scale.y=sy;scale.z=sz;return this;}makePerspective(left,right,top,bottom,near,far,coordinateSystem=WebGLCoordinateSystem$1){const te=this.elements;const x=2*near/(right-left);const y=2*near/(top-bottom);const a=(right+left)/(right-left);const b=(top+bottom)/(top-bottom);let c,d;if(coordinateSystem===WebGLCoordinateSystem$1){c=-(far+near)/(far-near);d=-2*far*near/(far-near);}else if(coordinateSystem===WebGPUCoordinateSystem$1){c=-far/(far-near);d=-far*near/(far-near);}else {throw new Error('THREE.Matrix4.makePerspective(): Invalid coordinate system: '+coordinateSystem);}te[0]=x;te[4]=0;te[8]=a;te[12]=0;te[1]=0;te[5]=y;te[9]=b;te[13]=0;te[2]=0;te[6]=0;te[10]=c;te[14]=d;te[3]=0;te[7]=0;te[11]=-1;te[15]=0;return this;}makeOrthographic(left,right,top,bottom,near,far,coordinateSystem=WebGLCoordinateSystem$1){const te=this.elements;const w=1.0/(right-left);const h=1.0/(top-bottom);const p=1.0/(far-near);const x=(right+left)*w;const y=(top+bottom)*h;let z,zInv;if(coordinateSystem===WebGLCoordinateSystem$1){z=(far+near)*p;zInv=-2*p;}else if(coordinateSystem===WebGPUCoordinateSystem$1){z=near*p;zInv=-1*p;}else {throw new Error('THREE.Matrix4.makeOrthographic(): Invalid coordinate system: '+coordinateSystem);}te[0]=2*w;te[4]=0;te[8]=0;te[12]=-x;te[1]=0;te[5]=2*h;te[9]=0;te[13]=-y;te[2]=0;te[6]=0;te[10]=zInv;te[14]=-z;te[3]=0;te[7]=0;te[11]=0;te[15]=1;return this;}equals(matrix){const te=this.elements;const me=matrix.elements;for(let i=0;i<16;i++){if(te[i]!==me[i])return false;}return true;}fromArray(array,offset=0){for(let i=0;i<16;i++){this.elements[i]=array[i+offset];}return this;}toArray(array=[],offset=0){const te=this.elements;array[offset]=te[0];array[offset+1]=te[1];array[offset+2]=te[2];array[offset+3]=te[3];array[offset+4]=te[4];array[offset+5]=te[5];array[offset+6]=te[6];array[offset+7]=te[7];array[offset+8]=te[8];array[offset+9]=te[9];array[offset+10]=te[10];array[offset+11]=te[11];array[offset+12]=te[12];array[offset+13]=te[13];array[offset+14]=te[14];array[offset+15]=te[15];return array;}}const _v1$5=/*@__PURE__*/new Vector3$1();const _m1$4=/*@__PURE__*/new Matrix4$1();const _zero$1=/*@__PURE__*/new Vector3$1(0,0,0);const _one$1=/*@__PURE__*/new Vector3$1(1,1,1);const _x$1=/*@__PURE__*/new Vector3$1();const _y$1=/*@__PURE__*/new Vector3$1();const _z$1=/*@__PURE__*/new Vector3$1();const _matrix$2=/*@__PURE__*/new Matrix4$1();const _quaternion$3=/*@__PURE__*/new Quaternion$1();class Euler$1{constructor(x=0,y=0,z=0,order=Euler$1.DEFAULT_ORDER){this.isEuler=true;this._x=x;this._y=y;this._z=z;this._order=order;}get x(){return this._x;}set x(value){this._x=value;this._onChangeCallback();}get y(){return this._y;}set y(value){this._y=value;this._onChangeCallback();}get z(){return this._z;}set z(value){this._z=value;this._onChangeCallback();}get order(){return this._order;}set order(value){this._order=value;this._onChangeCallback();}set(x,y,z,order=this._order){this._x=x;this._y=y;this._z=z;this._order=order;this._onChangeCallback();return this;}clone(){return new this.constructor(this._x,this._y,this._z,this._order);}copy(euler){this._x=euler._x;this._y=euler._y;this._z=euler._z;this._order=euler._order;this._onChangeCallback();return this;}setFromRotationMatrix(m,order=this._order,update=true){// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)
const te=m.elements;const m11=te[0],m12=te[4],m13=te[8];const m21=te[1],m22=te[5],m23=te[9];const m31=te[2],m32=te[6],m33=te[10];switch(order){case'XYZ':this._y=Math.asin(clamp$2(m13,-1,1));if(Math.abs(m13)<0.9999999){this._x=Math.atan2(-m23,m33);this._z=Math.atan2(-m12,m11);}else {this._x=Math.atan2(m32,m22);this._z=0;}break;case'YXZ':this._x=Math.asin(-clamp$2(m23,-1,1));if(Math.abs(m23)<0.9999999){this._y=Math.atan2(m13,m33);this._z=Math.atan2(m21,m22);}else {this._y=Math.atan2(-m31,m11);this._z=0;}break;case'ZXY':this._x=Math.asin(clamp$2(m32,-1,1));if(Math.abs(m32)<0.9999999){this._y=Math.atan2(-m31,m33);this._z=Math.atan2(-m12,m22);}else {this._y=0;this._z=Math.atan2(m21,m11);}break;case'ZYX':this._y=Math.asin(-clamp$2(m31,-1,1));if(Math.abs(m31)<0.9999999){this._x=Math.atan2(m32,m33);this._z=Math.atan2(m21,m11);}else {this._x=0;this._z=Math.atan2(-m12,m22);}break;case'YZX':this._z=Math.asin(clamp$2(m21,-1,1));if(Math.abs(m21)<0.9999999){this._x=Math.atan2(-m23,m22);this._y=Math.atan2(-m31,m11);}else {this._x=0;this._y=Math.atan2(m13,m33);}break;case'XZY':this._z=Math.asin(-clamp$2(m12,-1,1));if(Math.abs(m12)<0.9999999){this._x=Math.atan2(m32,m22);this._y=Math.atan2(m13,m11);}else {this._x=Math.atan2(-m23,m33);this._y=0;}break;default:console.warn('THREE.Euler: .setFromRotationMatrix() encountered an unknown order: '+order);}this._order=order;if(update===true)this._onChangeCallback();return this;}setFromQuaternion(q,order,update){_matrix$2.makeRotationFromQuaternion(q);return this.setFromRotationMatrix(_matrix$2,order,update);}setFromVector3(v,order=this._order){return this.set(v.x,v.y,v.z,order);}reorder(newOrder){// WARNING: this discards revolution information -bhouston
_quaternion$3.setFromEuler(this);return this.setFromQuaternion(_quaternion$3,newOrder);}equals(euler){return euler._x===this._x&&euler._y===this._y&&euler._z===this._z&&euler._order===this._order;}fromArray(array){this._x=array[0];this._y=array[1];this._z=array[2];if(array[3]!==undefined)this._order=array[3];this._onChangeCallback();return this;}toArray(array=[],offset=0){array[offset]=this._x;array[offset+1]=this._y;array[offset+2]=this._z;array[offset+3]=this._order;return array;}_onChange(callback){this._onChangeCallback=callback;return this;}_onChangeCallback(){}*[Symbol.iterator](){yield this._x;yield this._y;yield this._z;yield this._order;}}Euler$1.DEFAULT_ORDER='XYZ';class Layers$1{constructor(){this.mask=1|0;}set(channel){this.mask=(1<<channel|0)>>>0;}enable(channel){this.mask|=1<<channel|0;}enableAll(){this.mask=0xffffffff|0;}toggle(channel){this.mask^=1<<channel|0;}disable(channel){this.mask&=~(1<<channel|0);}disableAll(){this.mask=0;}test(layers){return (this.mask&layers.mask)!==0;}isEnabled(channel){return (this.mask&(1<<channel|0))!==0;}}let _object3DId$1=0;const _v1$4$1=/*@__PURE__*/new Vector3$1();const _q1$1=/*@__PURE__*/new Quaternion$1();const _m1$3=/*@__PURE__*/new Matrix4$1();const _target$2=/*@__PURE__*/new Vector3$1();const _position$3=/*@__PURE__*/new Vector3$1();const _scale$2=/*@__PURE__*/new Vector3$1();const _quaternion$2$1=/*@__PURE__*/new Quaternion$1();const _xAxis$1=/*@__PURE__*/new Vector3$1(1,0,0);const _yAxis$1=/*@__PURE__*/new Vector3$1(0,1,0);const _zAxis$1=/*@__PURE__*/new Vector3$1(0,0,1);const _addedEvent$1={type:'added'};const _removedEvent$1={type:'removed'};const _childaddedEvent$1={type:'childadded',child:null};const _childremovedEvent$1={type:'childremoved',child:null};class Object3D$1 extends EventDispatcher$1{constructor(){super();this.isObject3D=true;Object.defineProperty(this,'id',{value:_object3DId$1++});this.uuid=generateUUID$1();this.name='';this.type='Object3D';this.parent=null;this.children=[];this.up=Object3D$1.DEFAULT_UP.clone();const position=new Vector3$1();const rotation=new Euler$1();const quaternion=new Quaternion$1();const scale=new Vector3$1(1,1,1);function onRotationChange(){quaternion.setFromEuler(rotation,false);}function onQuaternionChange(){rotation.setFromQuaternion(quaternion,undefined,false);}rotation._onChange(onRotationChange);quaternion._onChange(onQuaternionChange);Object.defineProperties(this,{position:{configurable:true,enumerable:true,value:position},rotation:{configurable:true,enumerable:true,value:rotation},quaternion:{configurable:true,enumerable:true,value:quaternion},scale:{configurable:true,enumerable:true,value:scale},modelViewMatrix:{value:new Matrix4$1()},normalMatrix:{value:new Matrix3$1()}});this.matrix=new Matrix4$1();this.matrixWorld=new Matrix4$1();this.matrixAutoUpdate=Object3D$1.DEFAULT_MATRIX_AUTO_UPDATE;this.matrixWorldAutoUpdate=Object3D$1.DEFAULT_MATRIX_WORLD_AUTO_UPDATE;// checked by the renderer
this.matrixWorldNeedsUpdate=false;this.layers=new Layers$1();this.visible=true;this.castShadow=false;this.receiveShadow=false;this.frustumCulled=true;this.renderOrder=0;this.animations=[];this.userData={};}onBeforeShadow(/* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */){}onAfterShadow(/* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */){}onBeforeRender(/* renderer, scene, camera, geometry, material, group */){}onAfterRender(/* renderer, scene, camera, geometry, material, group */){}applyMatrix4(matrix){if(this.matrixAutoUpdate)this.updateMatrix();this.matrix.premultiply(matrix);this.matrix.decompose(this.position,this.quaternion,this.scale);}applyQuaternion(q){this.quaternion.premultiply(q);return this;}setRotationFromAxisAngle(axis,angle){// assumes axis is normalized
this.quaternion.setFromAxisAngle(axis,angle);}setRotationFromEuler(euler){this.quaternion.setFromEuler(euler,true);}setRotationFromMatrix(m){// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)
this.quaternion.setFromRotationMatrix(m);}setRotationFromQuaternion(q){// assumes q is normalized
this.quaternion.copy(q);}rotateOnAxis(axis,angle){// rotate object on axis in object space
// axis is assumed to be normalized
_q1$1.setFromAxisAngle(axis,angle);this.quaternion.multiply(_q1$1);return this;}rotateOnWorldAxis(axis,angle){// rotate object on axis in world space
// axis is assumed to be normalized
// method assumes no rotated parent
_q1$1.setFromAxisAngle(axis,angle);this.quaternion.premultiply(_q1$1);return this;}rotateX(angle){return this.rotateOnAxis(_xAxis$1,angle);}rotateY(angle){return this.rotateOnAxis(_yAxis$1,angle);}rotateZ(angle){return this.rotateOnAxis(_zAxis$1,angle);}translateOnAxis(axis,distance){// translate object by distance along axis in object space
// axis is assumed to be normalized
_v1$4$1.copy(axis).applyQuaternion(this.quaternion);this.position.add(_v1$4$1.multiplyScalar(distance));return this;}translateX(distance){return this.translateOnAxis(_xAxis$1,distance);}translateY(distance){return this.translateOnAxis(_yAxis$1,distance);}translateZ(distance){return this.translateOnAxis(_zAxis$1,distance);}localToWorld(vector){this.updateWorldMatrix(true,false);return vector.applyMatrix4(this.matrixWorld);}worldToLocal(vector){this.updateWorldMatrix(true,false);return vector.applyMatrix4(_m1$3.copy(this.matrixWorld).invert());}lookAt(x,y,z){// This method does not support objects having non-uniformly-scaled parent(s)
if(x.isVector3){_target$2.copy(x);}else {_target$2.set(x,y,z);}const parent=this.parent;this.updateWorldMatrix(true,false);_position$3.setFromMatrixPosition(this.matrixWorld);if(this.isCamera||this.isLight){_m1$3.lookAt(_position$3,_target$2,this.up);}else {_m1$3.lookAt(_target$2,_position$3,this.up);}this.quaternion.setFromRotationMatrix(_m1$3);if(parent){_m1$3.extractRotation(parent.matrixWorld);_q1$1.setFromRotationMatrix(_m1$3);this.quaternion.premultiply(_q1$1.invert());}}add(object){if(arguments.length>1){for(let i=0;i<arguments.length;i++){this.add(arguments[i]);}return this;}if(object===this){console.error('THREE.Object3D.add: object can\'t be added as a child of itself.',object);return this;}if(object&&object.isObject3D){object.removeFromParent();object.parent=this;this.children.push(object);object.dispatchEvent(_addedEvent$1);_childaddedEvent$1.child=object;this.dispatchEvent(_childaddedEvent$1);_childaddedEvent$1.child=null;}else {console.error('THREE.Object3D.add: object not an instance of THREE.Object3D.',object);}return this;}remove(object){if(arguments.length>1){for(let i=0;i<arguments.length;i++){this.remove(arguments[i]);}return this;}const index=this.children.indexOf(object);if(index!==-1){object.parent=null;this.children.splice(index,1);object.dispatchEvent(_removedEvent$1);_childremovedEvent$1.child=object;this.dispatchEvent(_childremovedEvent$1);_childremovedEvent$1.child=null;}return this;}removeFromParent(){const parent=this.parent;if(parent!==null){parent.remove(this);}return this;}clear(){return this.remove(...this.children);}attach(object){// adds object as a child of this, while maintaining the object's world transform
// Note: This method does not support scene graphs having non-uniformly-scaled nodes(s)
this.updateWorldMatrix(true,false);_m1$3.copy(this.matrixWorld).invert();if(object.parent!==null){object.parent.updateWorldMatrix(true,false);_m1$3.multiply(object.parent.matrixWorld);}object.applyMatrix4(_m1$3);object.removeFromParent();object.parent=this;this.children.push(object);object.updateWorldMatrix(false,true);object.dispatchEvent(_addedEvent$1);_childaddedEvent$1.child=object;this.dispatchEvent(_childaddedEvent$1);_childaddedEvent$1.child=null;return this;}getObjectById(id){return this.getObjectByProperty('id',id);}getObjectByName(name){return this.getObjectByProperty('name',name);}getObjectByProperty(name,value){if(this[name]===value)return this;for(let i=0,l=this.children.length;i<l;i++){const child=this.children[i];const object=child.getObjectByProperty(name,value);if(object!==undefined){return object;}}return undefined;}getObjectsByProperty(name,value,result=[]){if(this[name]===value)result.push(this);const children=this.children;for(let i=0,l=children.length;i<l;i++){children[i].getObjectsByProperty(name,value,result);}return result;}getWorldPosition(target){this.updateWorldMatrix(true,false);return target.setFromMatrixPosition(this.matrixWorld);}getWorldQuaternion(target){this.updateWorldMatrix(true,false);this.matrixWorld.decompose(_position$3,target,_scale$2);return target;}getWorldScale(target){this.updateWorldMatrix(true,false);this.matrixWorld.decompose(_position$3,_quaternion$2$1,target);return target;}getWorldDirection(target){this.updateWorldMatrix(true,false);const e=this.matrixWorld.elements;return target.set(e[8],e[9],e[10]).normalize();}raycast(/* raycaster, intersects */){}traverse(callback){callback(this);const children=this.children;for(let i=0,l=children.length;i<l;i++){children[i].traverse(callback);}}traverseVisible(callback){if(this.visible===false)return;callback(this);const children=this.children;for(let i=0,l=children.length;i<l;i++){children[i].traverseVisible(callback);}}traverseAncestors(callback){const parent=this.parent;if(parent!==null){callback(parent);parent.traverseAncestors(callback);}}updateMatrix(){this.matrix.compose(this.position,this.quaternion,this.scale);this.matrixWorldNeedsUpdate=true;}updateMatrixWorld(force){if(this.matrixAutoUpdate)this.updateMatrix();if(this.matrixWorldNeedsUpdate||force){if(this.matrixWorldAutoUpdate===true){if(this.parent===null){this.matrixWorld.copy(this.matrix);}else {this.matrixWorld.multiplyMatrices(this.parent.matrixWorld,this.matrix);}}this.matrixWorldNeedsUpdate=false;force=true;}// make sure descendants are updated if required
const children=this.children;for(let i=0,l=children.length;i<l;i++){const child=children[i];child.updateMatrixWorld(force);}}updateWorldMatrix(updateParents,updateChildren){const parent=this.parent;if(updateParents===true&&parent!==null){parent.updateWorldMatrix(true,false);}if(this.matrixAutoUpdate)this.updateMatrix();if(this.matrixWorldAutoUpdate===true){if(this.parent===null){this.matrixWorld.copy(this.matrix);}else {this.matrixWorld.multiplyMatrices(this.parent.matrixWorld,this.matrix);}}// make sure descendants are updated
if(updateChildren===true){const children=this.children;for(let i=0,l=children.length;i<l;i++){const child=children[i];child.updateWorldMatrix(false,true);}}}toJSON(meta){// meta is a string when called from JSON.stringify
const isRootObject=meta===undefined||typeof meta==='string';const output={};// meta is a hash used to collect geometries, materials.
// not providing it implies that this is the root object
// being serialized.
if(isRootObject){// initialize meta obj
meta={geometries:{},materials:{},textures:{},images:{},shapes:{},skeletons:{},animations:{},nodes:{}};output.metadata={version:4.6,type:'Object',generator:'Object3D.toJSON'};}// standard Object3D serialization
const object={};object.uuid=this.uuid;object.type=this.type;if(this.name!=='')object.name=this.name;if(this.castShadow===true)object.castShadow=true;if(this.receiveShadow===true)object.receiveShadow=true;if(this.visible===false)object.visible=false;if(this.frustumCulled===false)object.frustumCulled=false;if(this.renderOrder!==0)object.renderOrder=this.renderOrder;if(Object.keys(this.userData).length>0)object.userData=this.userData;object.layers=this.layers.mask;object.matrix=this.matrix.toArray();object.up=this.up.toArray();if(this.matrixAutoUpdate===false)object.matrixAutoUpdate=false;// object specific properties
if(this.isInstancedMesh){object.type='InstancedMesh';object.count=this.count;object.instanceMatrix=this.instanceMatrix.toJSON();if(this.instanceColor!==null)object.instanceColor=this.instanceColor.toJSON();}if(this.isBatchedMesh){object.type='BatchedMesh';object.perObjectFrustumCulled=this.perObjectFrustumCulled;object.sortObjects=this.sortObjects;object.drawRanges=this._drawRanges;object.reservedRanges=this._reservedRanges;object.visibility=this._visibility;object.active=this._active;object.bounds=this._bounds.map(bound=>({boxInitialized:bound.boxInitialized,boxMin:bound.box.min.toArray(),boxMax:bound.box.max.toArray(),sphereInitialized:bound.sphereInitialized,sphereRadius:bound.sphere.radius,sphereCenter:bound.sphere.center.toArray()}));object.maxInstanceCount=this._maxInstanceCount;object.maxVertexCount=this._maxVertexCount;object.maxIndexCount=this._maxIndexCount;object.geometryInitialized=this._geometryInitialized;object.geometryCount=this._geometryCount;object.matricesTexture=this._matricesTexture.toJSON(meta);if(this._colorsTexture!==null)object.colorsTexture=this._colorsTexture.toJSON(meta);if(this.boundingSphere!==null){object.boundingSphere={center:object.boundingSphere.center.toArray(),radius:object.boundingSphere.radius};}if(this.boundingBox!==null){object.boundingBox={min:object.boundingBox.min.toArray(),max:object.boundingBox.max.toArray()};}}//
function serialize(library,element){if(library[element.uuid]===undefined){library[element.uuid]=element.toJSON(meta);}return element.uuid;}if(this.isScene){if(this.background){if(this.background.isColor){object.background=this.background.toJSON();}else if(this.background.isTexture){object.background=this.background.toJSON(meta).uuid;}}if(this.environment&&this.environment.isTexture&&this.environment.isRenderTargetTexture!==true){object.environment=this.environment.toJSON(meta).uuid;}}else if(this.isMesh||this.isLine||this.isPoints){object.geometry=serialize(meta.geometries,this.geometry);const parameters=this.geometry.parameters;if(parameters!==undefined&&parameters.shapes!==undefined){const shapes=parameters.shapes;if(Array.isArray(shapes)){for(let i=0,l=shapes.length;i<l;i++){const shape=shapes[i];serialize(meta.shapes,shape);}}else {serialize(meta.shapes,shapes);}}}if(this.isSkinnedMesh){object.bindMode=this.bindMode;object.bindMatrix=this.bindMatrix.toArray();if(this.skeleton!==undefined){serialize(meta.skeletons,this.skeleton);object.skeleton=this.skeleton.uuid;}}if(this.material!==undefined){if(Array.isArray(this.material)){const uuids=[];for(let i=0,l=this.material.length;i<l;i++){uuids.push(serialize(meta.materials,this.material[i]));}object.material=uuids;}else {object.material=serialize(meta.materials,this.material);}}//
if(this.children.length>0){object.children=[];for(let i=0;i<this.children.length;i++){object.children.push(this.children[i].toJSON(meta).object);}}//
if(this.animations.length>0){object.animations=[];for(let i=0;i<this.animations.length;i++){const animation=this.animations[i];object.animations.push(serialize(meta.animations,animation));}}if(isRootObject){const geometries=extractFromCache(meta.geometries);const materials=extractFromCache(meta.materials);const textures=extractFromCache(meta.textures);const images=extractFromCache(meta.images);const shapes=extractFromCache(meta.shapes);const skeletons=extractFromCache(meta.skeletons);const animations=extractFromCache(meta.animations);const nodes=extractFromCache(meta.nodes);if(geometries.length>0)output.geometries=geometries;if(materials.length>0)output.materials=materials;if(textures.length>0)output.textures=textures;if(images.length>0)output.images=images;if(shapes.length>0)output.shapes=shapes;if(skeletons.length>0)output.skeletons=skeletons;if(animations.length>0)output.animations=animations;if(nodes.length>0)output.nodes=nodes;}output.object=object;return output;// extract data from the cache hash
// remove metadata on each item
// and return as array
function extractFromCache(cache){const values=[];for(const key in cache){const data=cache[key];delete data.metadata;values.push(data);}return values;}}clone(recursive){return new this.constructor().copy(this,recursive);}copy(source,recursive=true){this.name=source.name;this.up.copy(source.up);this.position.copy(source.position);this.rotation.order=source.rotation.order;this.quaternion.copy(source.quaternion);this.scale.copy(source.scale);this.matrix.copy(source.matrix);this.matrixWorld.copy(source.matrixWorld);this.matrixAutoUpdate=source.matrixAutoUpdate;this.matrixWorldAutoUpdate=source.matrixWorldAutoUpdate;this.matrixWorldNeedsUpdate=source.matrixWorldNeedsUpdate;this.layers.mask=source.layers.mask;this.visible=source.visible;this.castShadow=source.castShadow;this.receiveShadow=source.receiveShadow;this.frustumCulled=source.frustumCulled;this.renderOrder=source.renderOrder;this.animations=source.animations.slice();this.userData=JSON.parse(JSON.stringify(source.userData));if(recursive===true){for(let i=0;i<source.children.length;i++){const child=source.children[i];this.add(child.clone());}}return this;}}Object3D$1.DEFAULT_UP=/*@__PURE__*/new Vector3$1(0,1,0);Object3D$1.DEFAULT_MATRIX_AUTO_UPDATE=true;Object3D$1.DEFAULT_MATRIX_WORLD_AUTO_UPDATE=true;const _v0$2=/*@__PURE__*/new Vector3$1();const _v1$3$1=/*@__PURE__*/new Vector3$1();const _v2$2$1=/*@__PURE__*/new Vector3$1();const _v3$2=/*@__PURE__*/new Vector3$1();const _vab$1=/*@__PURE__*/new Vector3$1();const _vac$1=/*@__PURE__*/new Vector3$1();const _vbc$1=/*@__PURE__*/new Vector3$1();const _vap$1=/*@__PURE__*/new Vector3$1();const _vbp$1=/*@__PURE__*/new Vector3$1();const _vcp$1=/*@__PURE__*/new Vector3$1();class Triangle$1{constructor(a=new Vector3$1(),b=new Vector3$1(),c=new Vector3$1()){this.a=a;this.b=b;this.c=c;}static getNormal(a,b,c,target){target.subVectors(c,b);_v0$2.subVectors(a,b);target.cross(_v0$2);const targetLengthSq=target.lengthSq();if(targetLengthSq>0){return target.multiplyScalar(1/Math.sqrt(targetLengthSq));}return target.set(0,0,0);}// static/instance method to calculate barycentric coordinates
// based on: http://www.blackpawn.com/texts/pointinpoly/default.html
static getBarycoord(point,a,b,c,target){_v0$2.subVectors(c,a);_v1$3$1.subVectors(b,a);_v2$2$1.subVectors(point,a);const dot00=_v0$2.dot(_v0$2);const dot01=_v0$2.dot(_v1$3$1);const dot02=_v0$2.dot(_v2$2$1);const dot11=_v1$3$1.dot(_v1$3$1);const dot12=_v1$3$1.dot(_v2$2$1);const denom=dot00*dot11-dot01*dot01;// collinear or singular triangle
if(denom===0){target.set(0,0,0);return null;}const invDenom=1/denom;const u=(dot11*dot02-dot01*dot12)*invDenom;const v=(dot00*dot12-dot01*dot02)*invDenom;// barycentric coordinates must always sum to 1
return target.set(1-u-v,v,u);}static containsPoint(point,a,b,c){// if the triangle is degenerate then we can't contain a point
if(this.getBarycoord(point,a,b,c,_v3$2)===null){return false;}return _v3$2.x>=0&&_v3$2.y>=0&&_v3$2.x+_v3$2.y<=1;}static getInterpolation(point,p1,p2,p3,v1,v2,v3,target){if(this.getBarycoord(point,p1,p2,p3,_v3$2)===null){target.x=0;target.y=0;if('z'in target)target.z=0;if('w'in target)target.w=0;return null;}target.setScalar(0);target.addScaledVector(v1,_v3$2.x);target.addScaledVector(v2,_v3$2.y);target.addScaledVector(v3,_v3$2.z);return target;}static isFrontFacing(a,b,c,direction){_v0$2.subVectors(c,b);_v1$3$1.subVectors(a,b);// strictly front facing
return _v0$2.cross(_v1$3$1).dot(direction)<0?true:false;}set(a,b,c){this.a.copy(a);this.b.copy(b);this.c.copy(c);return this;}setFromPointsAndIndices(points,i0,i1,i2){this.a.copy(points[i0]);this.b.copy(points[i1]);this.c.copy(points[i2]);return this;}setFromAttributeAndIndices(attribute,i0,i1,i2){this.a.fromBufferAttribute(attribute,i0);this.b.fromBufferAttribute(attribute,i1);this.c.fromBufferAttribute(attribute,i2);return this;}clone(){return new this.constructor().copy(this);}copy(triangle){this.a.copy(triangle.a);this.b.copy(triangle.b);this.c.copy(triangle.c);return this;}getArea(){_v0$2.subVectors(this.c,this.b);_v1$3$1.subVectors(this.a,this.b);return _v0$2.cross(_v1$3$1).length()*0.5;}getMidpoint(target){return target.addVectors(this.a,this.b).add(this.c).multiplyScalar(1/3);}getNormal(target){return Triangle$1.getNormal(this.a,this.b,this.c,target);}getPlane(target){return target.setFromCoplanarPoints(this.a,this.b,this.c);}getBarycoord(point,target){return Triangle$1.getBarycoord(point,this.a,this.b,this.c,target);}getInterpolation(point,v1,v2,v3,target){return Triangle$1.getInterpolation(point,this.a,this.b,this.c,v1,v2,v3,target);}containsPoint(point){return Triangle$1.containsPoint(point,this.a,this.b,this.c);}isFrontFacing(direction){return Triangle$1.isFrontFacing(this.a,this.b,this.c,direction);}intersectsBox(box){return box.intersectsTriangle(this);}closestPointToPoint(p,target){const a=this.a,b=this.b,c=this.c;let v,w;// algorithm thanks to Real-Time Collision Detection by Christer Ericson,
// published by Morgan Kaufmann Publishers, (c) 2005 Elsevier Inc.,
// under the accompanying license; see chapter 5.1.5 for detailed explanation.
// basically, we're distinguishing which of the voronoi regions of the triangle
// the point lies in with the minimum amount of redundant computation.
_vab$1.subVectors(b,a);_vac$1.subVectors(c,a);_vap$1.subVectors(p,a);const d1=_vab$1.dot(_vap$1);const d2=_vac$1.dot(_vap$1);if(d1<=0&&d2<=0){// vertex region of A; barycentric coords (1, 0, 0)
return target.copy(a);}_vbp$1.subVectors(p,b);const d3=_vab$1.dot(_vbp$1);const d4=_vac$1.dot(_vbp$1);if(d3>=0&&d4<=d3){// vertex region of B; barycentric coords (0, 1, 0)
return target.copy(b);}const vc=d1*d4-d3*d2;if(vc<=0&&d1>=0&&d3<=0){v=d1/(d1-d3);// edge region of AB; barycentric coords (1-v, v, 0)
return target.copy(a).addScaledVector(_vab$1,v);}_vcp$1.subVectors(p,c);const d5=_vab$1.dot(_vcp$1);const d6=_vac$1.dot(_vcp$1);if(d6>=0&&d5<=d6){// vertex region of C; barycentric coords (0, 0, 1)
return target.copy(c);}const vb=d5*d2-d1*d6;if(vb<=0&&d2>=0&&d6<=0){w=d2/(d2-d6);// edge region of AC; barycentric coords (1-w, 0, w)
return target.copy(a).addScaledVector(_vac$1,w);}const va=d3*d6-d5*d4;if(va<=0&&d4-d3>=0&&d5-d6>=0){_vbc$1.subVectors(c,b);w=(d4-d3)/(d4-d3+(d5-d6));// edge region of BC; barycentric coords (0, 1-w, w)
return target.copy(b).addScaledVector(_vbc$1,w);// edge region of BC
}// face region
const denom=1/(va+vb+vc);// u = va * denom
v=vb*denom;w=vc*denom;return target.copy(a).addScaledVector(_vab$1,v).addScaledVector(_vac$1,w);}equals(triangle){return triangle.a.equals(this.a)&&triangle.b.equals(this.b)&&triangle.c.equals(this.c);}}const _colorKeywords$1={'aliceblue':0xF0F8FF,'antiquewhite':0xFAEBD7,'aqua':0x00FFFF,'aquamarine':0x7FFFD4,'azure':0xF0FFFF,'beige':0xF5F5DC,'bisque':0xFFE4C4,'black':0x000000,'blanchedalmond':0xFFEBCD,'blue':0x0000FF,'blueviolet':0x8A2BE2,'brown':0xA52A2A,'burlywood':0xDEB887,'cadetblue':0x5F9EA0,'chartreuse':0x7FFF00,'chocolate':0xD2691E,'coral':0xFF7F50,'cornflowerblue':0x6495ED,'cornsilk':0xFFF8DC,'crimson':0xDC143C,'cyan':0x00FFFF,'darkblue':0x00008B,'darkcyan':0x008B8B,'darkgoldenrod':0xB8860B,'darkgray':0xA9A9A9,'darkgreen':0x006400,'darkgrey':0xA9A9A9,'darkkhaki':0xBDB76B,'darkmagenta':0x8B008B,'darkolivegreen':0x556B2F,'darkorange':0xFF8C00,'darkorchid':0x9932CC,'darkred':0x8B0000,'darksalmon':0xE9967A,'darkseagreen':0x8FBC8F,'darkslateblue':0x483D8B,'darkslategray':0x2F4F4F,'darkslategrey':0x2F4F4F,'darkturquoise':0x00CED1,'darkviolet':0x9400D3,'deeppink':0xFF1493,'deepskyblue':0x00BFFF,'dimgray':0x696969,'dimgrey':0x696969,'dodgerblue':0x1E90FF,'firebrick':0xB22222,'floralwhite':0xFFFAF0,'forestgreen':0x228B22,'fuchsia':0xFF00FF,'gainsboro':0xDCDCDC,'ghostwhite':0xF8F8FF,'gold':0xFFD700,'goldenrod':0xDAA520,'gray':0x808080,'green':0x008000,'greenyellow':0xADFF2F,'grey':0x808080,'honeydew':0xF0FFF0,'hotpink':0xFF69B4,'indianred':0xCD5C5C,'indigo':0x4B0082,'ivory':0xFFFFF0,'khaki':0xF0E68C,'lavender':0xE6E6FA,'lavenderblush':0xFFF0F5,'lawngreen':0x7CFC00,'lemonchiffon':0xFFFACD,'lightblue':0xADD8E6,'lightcoral':0xF08080,'lightcyan':0xE0FFFF,'lightgoldenrodyellow':0xFAFAD2,'lightgray':0xD3D3D3,'lightgreen':0x90EE90,'lightgrey':0xD3D3D3,'lightpink':0xFFB6C1,'lightsalmon':0xFFA07A,'lightseagreen':0x20B2AA,'lightskyblue':0x87CEFA,'lightslategray':0x778899,'lightslategrey':0x778899,'lightsteelblue':0xB0C4DE,'lightyellow':0xFFFFE0,'lime':0x00FF00,'limegreen':0x32CD32,'linen':0xFAF0E6,'magenta':0xFF00FF,'maroon':0x800000,'mediumaquamarine':0x66CDAA,'mediumblue':0x0000CD,'mediumorchid':0xBA55D3,'mediumpurple':0x9370DB,'mediumseagreen':0x3CB371,'mediumslateblue':0x7B68EE,'mediumspringgreen':0x00FA9A,'mediumturquoise':0x48D1CC,'mediumvioletred':0xC71585,'midnightblue':0x191970,'mintcream':0xF5FFFA,'mistyrose':0xFFE4E1,'moccasin':0xFFE4B5,'navajowhite':0xFFDEAD,'navy':0x000080,'oldlace':0xFDF5E6,'olive':0x808000,'olivedrab':0x6B8E23,'orange':0xFFA500,'orangered':0xFF4500,'orchid':0xDA70D6,'palegoldenrod':0xEEE8AA,'palegreen':0x98FB98,'paleturquoise':0xAFEEEE,'palevioletred':0xDB7093,'papayawhip':0xFFEFD5,'peachpuff':0xFFDAB9,'peru':0xCD853F,'pink':0xFFC0CB,'plum':0xDDA0DD,'powderblue':0xB0E0E6,'purple':0x800080,'rebeccapurple':0x663399,'red':0xFF0000,'rosybrown':0xBC8F8F,'royalblue':0x4169E1,'saddlebrown':0x8B4513,'salmon':0xFA8072,'sandybrown':0xF4A460,'seagreen':0x2E8B57,'seashell':0xFFF5EE,'sienna':0xA0522D,'silver':0xC0C0C0,'skyblue':0x87CEEB,'slateblue':0x6A5ACD,'slategray':0x708090,'slategrey':0x708090,'snow':0xFFFAFA,'springgreen':0x00FF7F,'steelblue':0x4682B4,'tan':0xD2B48C,'teal':0x008080,'thistle':0xD8BFD8,'tomato':0xFF6347,'turquoise':0x40E0D0,'violet':0xEE82EE,'wheat':0xF5DEB3,'white':0xFFFFFF,'whitesmoke':0xF5F5F5,'yellow':0xFFFF00,'yellowgreen':0x9ACD32};const _hslA$1={h:0,s:0,l:0};const _hslB$1={h:0,s:0,l:0};function hue2rgb$1(p,q,t){if(t<0)t+=1;if(t>1)t-=1;if(t<1/6)return p+(q-p)*6*t;if(t<1/2)return q;if(t<2/3)return p+(q-p)*6*(2/3-t);return p;}class Color$1{constructor(r,g,b){this.isColor=true;this.r=1;this.g=1;this.b=1;return this.set(r,g,b);}set(r,g,b){if(g===undefined&&b===undefined){// r is THREE.Color, hex or string
const value=r;if(value&&value.isColor){this.copy(value);}else if(typeof value==='number'){this.setHex(value);}else if(typeof value==='string'){this.setStyle(value);}}else {this.setRGB(r,g,b);}return this;}setScalar(scalar){this.r=scalar;this.g=scalar;this.b=scalar;return this;}setHex(hex,colorSpace=SRGBColorSpace$1){hex=Math.floor(hex);this.r=(hex>>16&255)/255;this.g=(hex>>8&255)/255;this.b=(hex&255)/255;ColorManagement$1.toWorkingColorSpace(this,colorSpace);return this;}setRGB(r,g,b,colorSpace=ColorManagement$1.workingColorSpace){this.r=r;this.g=g;this.b=b;ColorManagement$1.toWorkingColorSpace(this,colorSpace);return this;}setHSL(h,s,l,colorSpace=ColorManagement$1.workingColorSpace){// h,s,l ranges are in 0.0 - 1.0
h=euclideanModulo$1(h,1);s=clamp$2(s,0,1);l=clamp$2(l,0,1);if(s===0){this.r=this.g=this.b=l;}else {const p=l<=0.5?l*(1+s):l+s-l*s;const q=2*l-p;this.r=hue2rgb$1(q,p,h+1/3);this.g=hue2rgb$1(q,p,h);this.b=hue2rgb$1(q,p,h-1/3);}ColorManagement$1.toWorkingColorSpace(this,colorSpace);return this;}setStyle(style,colorSpace=SRGBColorSpace$1){function handleAlpha(string){if(string===undefined)return;if(parseFloat(string)<1){console.warn('THREE.Color: Alpha component of '+style+' will be ignored.');}}let m;if(m=/^(\w+)\(([^\)]*)\)/.exec(style)){// rgb / hsl
let color;const name=m[1];const components=m[2];switch(name){case'rgb':case'rgba':if(color=/^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)){// rgb(255,0,0) rgba(255,0,0,0.5)
handleAlpha(color[4]);return this.setRGB(Math.min(255,parseInt(color[1],10))/255,Math.min(255,parseInt(color[2],10))/255,Math.min(255,parseInt(color[3],10))/255,colorSpace);}if(color=/^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)){// rgb(100%,0%,0%) rgba(100%,0%,0%,0.5)
handleAlpha(color[4]);return this.setRGB(Math.min(100,parseInt(color[1],10))/100,Math.min(100,parseInt(color[2],10))/100,Math.min(100,parseInt(color[3],10))/100,colorSpace);}break;case'hsl':case'hsla':if(color=/^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)){// hsl(120,50%,50%) hsla(120,50%,50%,0.5)
handleAlpha(color[4]);return this.setHSL(parseFloat(color[1])/360,parseFloat(color[2])/100,parseFloat(color[3])/100,colorSpace);}break;default:console.warn('THREE.Color: Unknown color model '+style);}}else if(m=/^\#([A-Fa-f\d]+)$/.exec(style)){// hex color
const hex=m[1];const size=hex.length;if(size===3){// #ff0
return this.setRGB(parseInt(hex.charAt(0),16)/15,parseInt(hex.charAt(1),16)/15,parseInt(hex.charAt(2),16)/15,colorSpace);}else if(size===6){// #ff0000
return this.setHex(parseInt(hex,16),colorSpace);}else {console.warn('THREE.Color: Invalid hex color '+style);}}else if(style&&style.length>0){return this.setColorName(style,colorSpace);}return this;}setColorName(style,colorSpace=SRGBColorSpace$1){// color keywords
const hex=_colorKeywords$1[style.toLowerCase()];if(hex!==undefined){// red
this.setHex(hex,colorSpace);}else {// unknown color
console.warn('THREE.Color: Unknown color '+style);}return this;}clone(){return new this.constructor(this.r,this.g,this.b);}copy(color){this.r=color.r;this.g=color.g;this.b=color.b;return this;}copySRGBToLinear(color){this.r=SRGBToLinear$1(color.r);this.g=SRGBToLinear$1(color.g);this.b=SRGBToLinear$1(color.b);return this;}copyLinearToSRGB(color){this.r=LinearToSRGB$1(color.r);this.g=LinearToSRGB$1(color.g);this.b=LinearToSRGB$1(color.b);return this;}convertSRGBToLinear(){this.copySRGBToLinear(this);return this;}convertLinearToSRGB(){this.copyLinearToSRGB(this);return this;}getHex(colorSpace=SRGBColorSpace$1){ColorManagement$1.fromWorkingColorSpace(_color$1.copy(this),colorSpace);return Math.round(clamp$2(_color$1.r*255,0,255))*65536+Math.round(clamp$2(_color$1.g*255,0,255))*256+Math.round(clamp$2(_color$1.b*255,0,255));}getHexString(colorSpace=SRGBColorSpace$1){return ('000000'+this.getHex(colorSpace).toString(16)).slice(-6);}getHSL(target,colorSpace=ColorManagement$1.workingColorSpace){// h,s,l ranges are in 0.0 - 1.0
ColorManagement$1.fromWorkingColorSpace(_color$1.copy(this),colorSpace);const r=_color$1.r,g=_color$1.g,b=_color$1.b;const max=Math.max(r,g,b);const min=Math.min(r,g,b);let hue,saturation;const lightness=(min+max)/2.0;if(min===max){hue=0;saturation=0;}else {const delta=max-min;saturation=lightness<=0.5?delta/(max+min):delta/(2-max-min);switch(max){case r:hue=(g-b)/delta+(g<b?6:0);break;case g:hue=(b-r)/delta+2;break;case b:hue=(r-g)/delta+4;break;}hue/=6;}target.h=hue;target.s=saturation;target.l=lightness;return target;}getRGB(target,colorSpace=ColorManagement$1.workingColorSpace){ColorManagement$1.fromWorkingColorSpace(_color$1.copy(this),colorSpace);target.r=_color$1.r;target.g=_color$1.g;target.b=_color$1.b;return target;}getStyle(colorSpace=SRGBColorSpace$1){ColorManagement$1.fromWorkingColorSpace(_color$1.copy(this),colorSpace);const r=_color$1.r,g=_color$1.g,b=_color$1.b;if(colorSpace!==SRGBColorSpace$1){// Requires CSS Color Module Level 4 (https://www.w3.org/TR/css-color-4/).
return `color(${colorSpace} ${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)})`;}return `rgb(${Math.round(r*255)},${Math.round(g*255)},${Math.round(b*255)})`;}offsetHSL(h,s,l){this.getHSL(_hslA$1);return this.setHSL(_hslA$1.h+h,_hslA$1.s+s,_hslA$1.l+l);}add(color){this.r+=color.r;this.g+=color.g;this.b+=color.b;return this;}addColors(color1,color2){this.r=color1.r+color2.r;this.g=color1.g+color2.g;this.b=color1.b+color2.b;return this;}addScalar(s){this.r+=s;this.g+=s;this.b+=s;return this;}sub(color){this.r=Math.max(0,this.r-color.r);this.g=Math.max(0,this.g-color.g);this.b=Math.max(0,this.b-color.b);return this;}multiply(color){this.r*=color.r;this.g*=color.g;this.b*=color.b;return this;}multiplyScalar(s){this.r*=s;this.g*=s;this.b*=s;return this;}lerp(color,alpha){this.r+=(color.r-this.r)*alpha;this.g+=(color.g-this.g)*alpha;this.b+=(color.b-this.b)*alpha;return this;}lerpColors(color1,color2,alpha){this.r=color1.r+(color2.r-color1.r)*alpha;this.g=color1.g+(color2.g-color1.g)*alpha;this.b=color1.b+(color2.b-color1.b)*alpha;return this;}lerpHSL(color,alpha){this.getHSL(_hslA$1);color.getHSL(_hslB$1);const h=lerp$1(_hslA$1.h,_hslB$1.h,alpha);const s=lerp$1(_hslA$1.s,_hslB$1.s,alpha);const l=lerp$1(_hslA$1.l,_hslB$1.l,alpha);this.setHSL(h,s,l);return this;}setFromVector3(v){this.r=v.x;this.g=v.y;this.b=v.z;return this;}applyMatrix3(m){const r=this.r,g=this.g,b=this.b;const e=m.elements;this.r=e[0]*r+e[3]*g+e[6]*b;this.g=e[1]*r+e[4]*g+e[7]*b;this.b=e[2]*r+e[5]*g+e[8]*b;return this;}equals(c){return c.r===this.r&&c.g===this.g&&c.b===this.b;}fromArray(array,offset=0){this.r=array[offset];this.g=array[offset+1];this.b=array[offset+2];return this;}toArray(array=[],offset=0){array[offset]=this.r;array[offset+1]=this.g;array[offset+2]=this.b;return array;}fromBufferAttribute(attribute,index){this.r=attribute.getX(index);this.g=attribute.getY(index);this.b=attribute.getZ(index);return this;}toJSON(){return this.getHex();}*[Symbol.iterator](){yield this.r;yield this.g;yield this.b;}}const _color$1=/*@__PURE__*/new Color$1();Color$1.NAMES=_colorKeywords$1;let _materialId$1=0;class Material$1 extends EventDispatcher$1{constructor(){super();this.isMaterial=true;Object.defineProperty(this,'id',{value:_materialId$1++});this.uuid=generateUUID$1();this.name='';this.type='Material';this.blending=NormalBlending$1;this.side=FrontSide$1;this.vertexColors=false;this.opacity=1;this.transparent=false;this.alphaHash=false;this.blendSrc=SrcAlphaFactor$1;this.blendDst=OneMinusSrcAlphaFactor$1;this.blendEquation=AddEquation$1;this.blendSrcAlpha=null;this.blendDstAlpha=null;this.blendEquationAlpha=null;this.blendColor=new Color$1(0,0,0);this.blendAlpha=0;this.depthFunc=LessEqualDepth$1;this.depthTest=true;this.depthWrite=true;this.stencilWriteMask=0xff;this.stencilFunc=AlwaysStencilFunc$1;this.stencilRef=0;this.stencilFuncMask=0xff;this.stencilFail=KeepStencilOp$1;this.stencilZFail=KeepStencilOp$1;this.stencilZPass=KeepStencilOp$1;this.stencilWrite=false;this.clippingPlanes=null;this.clipIntersection=false;this.clipShadows=false;this.shadowSide=null;this.colorWrite=true;this.precision=null;// override the renderer's default precision for this material
this.polygonOffset=false;this.polygonOffsetFactor=0;this.polygonOffsetUnits=0;this.dithering=false;this.alphaToCoverage=false;this.premultipliedAlpha=false;this.forceSinglePass=false;this.visible=true;this.toneMapped=true;this.userData={};this.version=0;this._alphaTest=0;}get alphaTest(){return this._alphaTest;}set alphaTest(value){if(this._alphaTest>0!==value>0){this.version++;}this._alphaTest=value;}// onBeforeRender and onBeforeCompile only supported in WebGLRenderer
onBeforeRender(/* renderer, scene, camera, geometry, object, group */){}onBeforeCompile(/* shaderobject, renderer */){}customProgramCacheKey(){return this.onBeforeCompile.toString();}setValues(values){if(values===undefined)return;for(const key in values){const newValue=values[key];if(newValue===undefined){console.warn(`THREE.Material: parameter '${key}' has value of undefined.`);continue;}const currentValue=this[key];if(currentValue===undefined){console.warn(`THREE.Material: '${key}' is not a property of THREE.${this.type}.`);continue;}if(currentValue&&currentValue.isColor){currentValue.set(newValue);}else if(currentValue&&currentValue.isVector3&&newValue&&newValue.isVector3){currentValue.copy(newValue);}else {this[key]=newValue;}}}toJSON(meta){const isRootObject=meta===undefined||typeof meta==='string';if(isRootObject){meta={textures:{},images:{}};}const data={metadata:{version:4.6,type:'Material',generator:'Material.toJSON'}};// standard Material serialization
data.uuid=this.uuid;data.type=this.type;if(this.name!=='')data.name=this.name;if(this.color&&this.color.isColor)data.color=this.color.getHex();if(this.roughness!==undefined)data.roughness=this.roughness;if(this.metalness!==undefined)data.metalness=this.metalness;if(this.sheen!==undefined)data.sheen=this.sheen;if(this.sheenColor&&this.sheenColor.isColor)data.sheenColor=this.sheenColor.getHex();if(this.sheenRoughness!==undefined)data.sheenRoughness=this.sheenRoughness;if(this.emissive&&this.emissive.isColor)data.emissive=this.emissive.getHex();if(this.emissiveIntensity!==undefined&&this.emissiveIntensity!==1)data.emissiveIntensity=this.emissiveIntensity;if(this.specular&&this.specular.isColor)data.specular=this.specular.getHex();if(this.specularIntensity!==undefined)data.specularIntensity=this.specularIntensity;if(this.specularColor&&this.specularColor.isColor)data.specularColor=this.specularColor.getHex();if(this.shininess!==undefined)data.shininess=this.shininess;if(this.clearcoat!==undefined)data.clearcoat=this.clearcoat;if(this.clearcoatRoughness!==undefined)data.clearcoatRoughness=this.clearcoatRoughness;if(this.clearcoatMap&&this.clearcoatMap.isTexture){data.clearcoatMap=this.clearcoatMap.toJSON(meta).uuid;}if(this.clearcoatRoughnessMap&&this.clearcoatRoughnessMap.isTexture){data.clearcoatRoughnessMap=this.clearcoatRoughnessMap.toJSON(meta).uuid;}if(this.clearcoatNormalMap&&this.clearcoatNormalMap.isTexture){data.clearcoatNormalMap=this.clearcoatNormalMap.toJSON(meta).uuid;data.clearcoatNormalScale=this.clearcoatNormalScale.toArray();}if(this.dispersion!==undefined)data.dispersion=this.dispersion;if(this.iridescence!==undefined)data.iridescence=this.iridescence;if(this.iridescenceIOR!==undefined)data.iridescenceIOR=this.iridescenceIOR;if(this.iridescenceThicknessRange!==undefined)data.iridescenceThicknessRange=this.iridescenceThicknessRange;if(this.iridescenceMap&&this.iridescenceMap.isTexture){data.iridescenceMap=this.iridescenceMap.toJSON(meta).uuid;}if(this.iridescenceThicknessMap&&this.iridescenceThicknessMap.isTexture){data.iridescenceThicknessMap=this.iridescenceThicknessMap.toJSON(meta).uuid;}if(this.anisotropy!==undefined)data.anisotropy=this.anisotropy;if(this.anisotropyRotation!==undefined)data.anisotropyRotation=this.anisotropyRotation;if(this.anisotropyMap&&this.anisotropyMap.isTexture){data.anisotropyMap=this.anisotropyMap.toJSON(meta).uuid;}if(this.map&&this.map.isTexture)data.map=this.map.toJSON(meta).uuid;if(this.matcap&&this.matcap.isTexture)data.matcap=this.matcap.toJSON(meta).uuid;if(this.alphaMap&&this.alphaMap.isTexture)data.alphaMap=this.alphaMap.toJSON(meta).uuid;if(this.lightMap&&this.lightMap.isTexture){data.lightMap=this.lightMap.toJSON(meta).uuid;data.lightMapIntensity=this.lightMapIntensity;}if(this.aoMap&&this.aoMap.isTexture){data.aoMap=this.aoMap.toJSON(meta).uuid;data.aoMapIntensity=this.aoMapIntensity;}if(this.bumpMap&&this.bumpMap.isTexture){data.bumpMap=this.bumpMap.toJSON(meta).uuid;data.bumpScale=this.bumpScale;}if(this.normalMap&&this.normalMap.isTexture){data.normalMap=this.normalMap.toJSON(meta).uuid;data.normalMapType=this.normalMapType;data.normalScale=this.normalScale.toArray();}if(this.displacementMap&&this.displacementMap.isTexture){data.displacementMap=this.displacementMap.toJSON(meta).uuid;data.displacementScale=this.displacementScale;data.displacementBias=this.displacementBias;}if(this.roughnessMap&&this.roughnessMap.isTexture)data.roughnessMap=this.roughnessMap.toJSON(meta).uuid;if(this.metalnessMap&&this.metalnessMap.isTexture)data.metalnessMap=this.metalnessMap.toJSON(meta).uuid;if(this.emissiveMap&&this.emissiveMap.isTexture)data.emissiveMap=this.emissiveMap.toJSON(meta).uuid;if(this.specularMap&&this.specularMap.isTexture)data.specularMap=this.specularMap.toJSON(meta).uuid;if(this.specularIntensityMap&&this.specularIntensityMap.isTexture)data.specularIntensityMap=this.specularIntensityMap.toJSON(meta).uuid;if(this.specularColorMap&&this.specularColorMap.isTexture)data.specularColorMap=this.specularColorMap.toJSON(meta).uuid;if(this.envMap&&this.envMap.isTexture){data.envMap=this.envMap.toJSON(meta).uuid;if(this.combine!==undefined)data.combine=this.combine;}if(this.envMapRotation!==undefined)data.envMapRotation=this.envMapRotation.toArray();if(this.envMapIntensity!==undefined)data.envMapIntensity=this.envMapIntensity;if(this.reflectivity!==undefined)data.reflectivity=this.reflectivity;if(this.refractionRatio!==undefined)data.refractionRatio=this.refractionRatio;if(this.gradientMap&&this.gradientMap.isTexture){data.gradientMap=this.gradientMap.toJSON(meta).uuid;}if(this.transmission!==undefined)data.transmission=this.transmission;if(this.transmissionMap&&this.transmissionMap.isTexture)data.transmissionMap=this.transmissionMap.toJSON(meta).uuid;if(this.thickness!==undefined)data.thickness=this.thickness;if(this.thicknessMap&&this.thicknessMap.isTexture)data.thicknessMap=this.thicknessMap.toJSON(meta).uuid;if(this.attenuationDistance!==undefined&&this.attenuationDistance!==Infinity)data.attenuationDistance=this.attenuationDistance;if(this.attenuationColor!==undefined)data.attenuationColor=this.attenuationColor.getHex();if(this.size!==undefined)data.size=this.size;if(this.shadowSide!==null)data.shadowSide=this.shadowSide;if(this.sizeAttenuation!==undefined)data.sizeAttenuation=this.sizeAttenuation;if(this.blending!==NormalBlending$1)data.blending=this.blending;if(this.side!==FrontSide$1)data.side=this.side;if(this.vertexColors===true)data.vertexColors=true;if(this.opacity<1)data.opacity=this.opacity;if(this.transparent===true)data.transparent=true;if(this.blendSrc!==SrcAlphaFactor$1)data.blendSrc=this.blendSrc;if(this.blendDst!==OneMinusSrcAlphaFactor$1)data.blendDst=this.blendDst;if(this.blendEquation!==AddEquation$1)data.blendEquation=this.blendEquation;if(this.blendSrcAlpha!==null)data.blendSrcAlpha=this.blendSrcAlpha;if(this.blendDstAlpha!==null)data.blendDstAlpha=this.blendDstAlpha;if(this.blendEquationAlpha!==null)data.blendEquationAlpha=this.blendEquationAlpha;if(this.blendColor&&this.blendColor.isColor)data.blendColor=this.blendColor.getHex();if(this.blendAlpha!==0)data.blendAlpha=this.blendAlpha;if(this.depthFunc!==LessEqualDepth$1)data.depthFunc=this.depthFunc;if(this.depthTest===false)data.depthTest=this.depthTest;if(this.depthWrite===false)data.depthWrite=this.depthWrite;if(this.colorWrite===false)data.colorWrite=this.colorWrite;if(this.stencilWriteMask!==0xff)data.stencilWriteMask=this.stencilWriteMask;if(this.stencilFunc!==AlwaysStencilFunc$1)data.stencilFunc=this.stencilFunc;if(this.stencilRef!==0)data.stencilRef=this.stencilRef;if(this.stencilFuncMask!==0xff)data.stencilFuncMask=this.stencilFuncMask;if(this.stencilFail!==KeepStencilOp$1)data.stencilFail=this.stencilFail;if(this.stencilZFail!==KeepStencilOp$1)data.stencilZFail=this.stencilZFail;if(this.stencilZPass!==KeepStencilOp$1)data.stencilZPass=this.stencilZPass;if(this.stencilWrite===true)data.stencilWrite=this.stencilWrite;// rotation (SpriteMaterial)
if(this.rotation!==undefined&&this.rotation!==0)data.rotation=this.rotation;if(this.polygonOffset===true)data.polygonOffset=true;if(this.polygonOffsetFactor!==0)data.polygonOffsetFactor=this.polygonOffsetFactor;if(this.polygonOffsetUnits!==0)data.polygonOffsetUnits=this.polygonOffsetUnits;if(this.linewidth!==undefined&&this.linewidth!==1)data.linewidth=this.linewidth;if(this.dashSize!==undefined)data.dashSize=this.dashSize;if(this.gapSize!==undefined)data.gapSize=this.gapSize;if(this.scale!==undefined)data.scale=this.scale;if(this.dithering===true)data.dithering=true;if(this.alphaTest>0)data.alphaTest=this.alphaTest;if(this.alphaHash===true)data.alphaHash=true;if(this.alphaToCoverage===true)data.alphaToCoverage=true;if(this.premultipliedAlpha===true)data.premultipliedAlpha=true;if(this.forceSinglePass===true)data.forceSinglePass=true;if(this.wireframe===true)data.wireframe=true;if(this.wireframeLinewidth>1)data.wireframeLinewidth=this.wireframeLinewidth;if(this.wireframeLinecap!=='round')data.wireframeLinecap=this.wireframeLinecap;if(this.wireframeLinejoin!=='round')data.wireframeLinejoin=this.wireframeLinejoin;if(this.flatShading===true)data.flatShading=true;if(this.visible===false)data.visible=false;if(this.toneMapped===false)data.toneMapped=false;if(this.fog===false)data.fog=false;if(Object.keys(this.userData).length>0)data.userData=this.userData;// TODO: Copied from Object3D.toJSON
function extractFromCache(cache){const values=[];for(const key in cache){const data=cache[key];delete data.metadata;values.push(data);}return values;}if(isRootObject){const textures=extractFromCache(meta.textures);const images=extractFromCache(meta.images);if(textures.length>0)data.textures=textures;if(images.length>0)data.images=images;}return data;}clone(){return new this.constructor().copy(this);}copy(source){this.name=source.name;this.blending=source.blending;this.side=source.side;this.vertexColors=source.vertexColors;this.opacity=source.opacity;this.transparent=source.transparent;this.blendSrc=source.blendSrc;this.blendDst=source.blendDst;this.blendEquation=source.blendEquation;this.blendSrcAlpha=source.blendSrcAlpha;this.blendDstAlpha=source.blendDstAlpha;this.blendEquationAlpha=source.blendEquationAlpha;this.blendColor.copy(source.blendColor);this.blendAlpha=source.blendAlpha;this.depthFunc=source.depthFunc;this.depthTest=source.depthTest;this.depthWrite=source.depthWrite;this.stencilWriteMask=source.stencilWriteMask;this.stencilFunc=source.stencilFunc;this.stencilRef=source.stencilRef;this.stencilFuncMask=source.stencilFuncMask;this.stencilFail=source.stencilFail;this.stencilZFail=source.stencilZFail;this.stencilZPass=source.stencilZPass;this.stencilWrite=source.stencilWrite;const srcPlanes=source.clippingPlanes;let dstPlanes=null;if(srcPlanes!==null){const n=srcPlanes.length;dstPlanes=new Array(n);for(let i=0;i!==n;++i){dstPlanes[i]=srcPlanes[i].clone();}}this.clippingPlanes=dstPlanes;this.clipIntersection=source.clipIntersection;this.clipShadows=source.clipShadows;this.shadowSide=source.shadowSide;this.colorWrite=source.colorWrite;this.precision=source.precision;this.polygonOffset=source.polygonOffset;this.polygonOffsetFactor=source.polygonOffsetFactor;this.polygonOffsetUnits=source.polygonOffsetUnits;this.dithering=source.dithering;this.alphaTest=source.alphaTest;this.alphaHash=source.alphaHash;this.alphaToCoverage=source.alphaToCoverage;this.premultipliedAlpha=source.premultipliedAlpha;this.forceSinglePass=source.forceSinglePass;this.visible=source.visible;this.toneMapped=source.toneMapped;this.userData=JSON.parse(JSON.stringify(source.userData));return this;}dispose(){this.dispatchEvent({type:'dispose'});}set needsUpdate(value){if(value===true)this.version++;}onBuild(/* shaderobject, renderer */){console.warn('Material: onBuild() has been removed.');// @deprecated, r166
}}class MeshBasicMaterial$1 extends Material$1{constructor(parameters){super();this.isMeshBasicMaterial=true;this.type='MeshBasicMaterial';this.color=new Color$1(0xffffff);// emissive
this.map=null;this.lightMap=null;this.lightMapIntensity=1.0;this.aoMap=null;this.aoMapIntensity=1.0;this.specularMap=null;this.alphaMap=null;this.envMap=null;this.envMapRotation=new Euler$1();this.combine=MultiplyOperation$1;this.reflectivity=1;this.refractionRatio=0.98;this.wireframe=false;this.wireframeLinewidth=1;this.wireframeLinecap='round';this.wireframeLinejoin='round';this.fog=true;this.setValues(parameters);}copy(source){super.copy(source);this.color.copy(source.color);this.map=source.map;this.lightMap=source.lightMap;this.lightMapIntensity=source.lightMapIntensity;this.aoMap=source.aoMap;this.aoMapIntensity=source.aoMapIntensity;this.specularMap=source.specularMap;this.alphaMap=source.alphaMap;this.envMap=source.envMap;this.envMapRotation.copy(source.envMapRotation);this.combine=source.combine;this.reflectivity=source.reflectivity;this.refractionRatio=source.refractionRatio;this.wireframe=source.wireframe;this.wireframeLinewidth=source.wireframeLinewidth;this.wireframeLinecap=source.wireframeLinecap;this.wireframeLinejoin=source.wireframeLinejoin;this.fog=source.fog;return this;}}// Fast Half Float Conversions, http://www.fox-toolkit.org/ftp/fasthalffloatconversion.pdf
const _vector$9=/*@__PURE__*/new Vector3$1();const _vector2$1$1=/*@__PURE__*/new Vector2$1();class BufferAttribute$1{constructor(array,itemSize,normalized=false){if(Array.isArray(array)){throw new TypeError('THREE.BufferAttribute: array should be a Typed Array.');}this.isBufferAttribute=true;this.name='';this.array=array;this.itemSize=itemSize;this.count=array!==undefined?array.length/itemSize:0;this.normalized=normalized;this.usage=StaticDrawUsage$1;this._updateRange={offset:0,count:-1};this.updateRanges=[];this.gpuType=FloatType$1;this.version=0;}onUploadCallback(){}set needsUpdate(value){if(value===true)this.version++;}get updateRange(){warnOnce$1('THREE.BufferAttribute: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead.');// @deprecated, r159
return this._updateRange;}setUsage(value){this.usage=value;return this;}addUpdateRange(start,count){this.updateRanges.push({start,count});}clearUpdateRanges(){this.updateRanges.length=0;}copy(source){this.name=source.name;this.array=new source.array.constructor(source.array);this.itemSize=source.itemSize;this.count=source.count;this.normalized=source.normalized;this.usage=source.usage;this.gpuType=source.gpuType;return this;}copyAt(index1,attribute,index2){index1*=this.itemSize;index2*=attribute.itemSize;for(let i=0,l=this.itemSize;i<l;i++){this.array[index1+i]=attribute.array[index2+i];}return this;}copyArray(array){this.array.set(array);return this;}applyMatrix3(m){if(this.itemSize===2){for(let i=0,l=this.count;i<l;i++){_vector2$1$1.fromBufferAttribute(this,i);_vector2$1$1.applyMatrix3(m);this.setXY(i,_vector2$1$1.x,_vector2$1$1.y);}}else if(this.itemSize===3){for(let i=0,l=this.count;i<l;i++){_vector$9.fromBufferAttribute(this,i);_vector$9.applyMatrix3(m);this.setXYZ(i,_vector$9.x,_vector$9.y,_vector$9.z);}}return this;}applyMatrix4(m){for(let i=0,l=this.count;i<l;i++){_vector$9.fromBufferAttribute(this,i);_vector$9.applyMatrix4(m);this.setXYZ(i,_vector$9.x,_vector$9.y,_vector$9.z);}return this;}applyNormalMatrix(m){for(let i=0,l=this.count;i<l;i++){_vector$9.fromBufferAttribute(this,i);_vector$9.applyNormalMatrix(m);this.setXYZ(i,_vector$9.x,_vector$9.y,_vector$9.z);}return this;}transformDirection(m){for(let i=0,l=this.count;i<l;i++){_vector$9.fromBufferAttribute(this,i);_vector$9.transformDirection(m);this.setXYZ(i,_vector$9.x,_vector$9.y,_vector$9.z);}return this;}set(value,offset=0){// Matching BufferAttribute constructor, do not normalize the array.
this.array.set(value,offset);return this;}getComponent(index,component){let value=this.array[index*this.itemSize+component];if(this.normalized)value=denormalize$1(value,this.array);return value;}setComponent(index,component,value){if(this.normalized)value=normalize$2(value,this.array);this.array[index*this.itemSize+component]=value;return this;}getX(index){let x=this.array[index*this.itemSize];if(this.normalized)x=denormalize$1(x,this.array);return x;}setX(index,x){if(this.normalized)x=normalize$2(x,this.array);this.array[index*this.itemSize]=x;return this;}getY(index){let y=this.array[index*this.itemSize+1];if(this.normalized)y=denormalize$1(y,this.array);return y;}setY(index,y){if(this.normalized)y=normalize$2(y,this.array);this.array[index*this.itemSize+1]=y;return this;}getZ(index){let z=this.array[index*this.itemSize+2];if(this.normalized)z=denormalize$1(z,this.array);return z;}setZ(index,z){if(this.normalized)z=normalize$2(z,this.array);this.array[index*this.itemSize+2]=z;return this;}getW(index){let w=this.array[index*this.itemSize+3];if(this.normalized)w=denormalize$1(w,this.array);return w;}setW(index,w){if(this.normalized)w=normalize$2(w,this.array);this.array[index*this.itemSize+3]=w;return this;}setXY(index,x,y){index*=this.itemSize;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);}this.array[index+0]=x;this.array[index+1]=y;return this;}setXYZ(index,x,y,z){index*=this.itemSize;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);z=normalize$2(z,this.array);}this.array[index+0]=x;this.array[index+1]=y;this.array[index+2]=z;return this;}setXYZW(index,x,y,z,w){index*=this.itemSize;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);z=normalize$2(z,this.array);w=normalize$2(w,this.array);}this.array[index+0]=x;this.array[index+1]=y;this.array[index+2]=z;this.array[index+3]=w;return this;}onUpload(callback){this.onUploadCallback=callback;return this;}clone(){return new this.constructor(this.array,this.itemSize).copy(this);}toJSON(){const data={itemSize:this.itemSize,type:this.array.constructor.name,array:Array.from(this.array),normalized:this.normalized};if(this.name!=='')data.name=this.name;if(this.usage!==StaticDrawUsage$1)data.usage=this.usage;return data;}}//
class Uint16BufferAttribute$1 extends BufferAttribute$1{constructor(array,itemSize,normalized){super(new Uint16Array(array),itemSize,normalized);}}class Uint32BufferAttribute$1 extends BufferAttribute$1{constructor(array,itemSize,normalized){super(new Uint32Array(array),itemSize,normalized);}}class Float32BufferAttribute$1 extends BufferAttribute$1{constructor(array,itemSize,normalized){super(new Float32Array(array),itemSize,normalized);}}let _id$2=0;const _m1$2$1=/*@__PURE__*/new Matrix4$1();const _obj$1=/*@__PURE__*/new Object3D$1();const _offset$1=/*@__PURE__*/new Vector3$1();const _box$2$1=/*@__PURE__*/new Box3$1();const _boxMorphTargets$1=/*@__PURE__*/new Box3$1();const _vector$8=/*@__PURE__*/new Vector3$1();class BufferGeometry$1 extends EventDispatcher$1{constructor(){super();this.isBufferGeometry=true;Object.defineProperty(this,'id',{value:_id$2++});this.uuid=generateUUID$1();this.name='';this.type='BufferGeometry';this.index=null;this.attributes={};this.morphAttributes={};this.morphTargetsRelative=false;this.groups=[];this.boundingBox=null;this.boundingSphere=null;this.drawRange={start:0,count:Infinity};this.userData={};}getIndex(){return this.index;}setIndex(index){if(Array.isArray(index)){this.index=new(arrayNeedsUint32$1(index)?Uint32BufferAttribute$1:Uint16BufferAttribute$1)(index,1);}else {this.index=index;}return this;}getAttribute(name){return this.attributes[name];}setAttribute(name,attribute){this.attributes[name]=attribute;return this;}deleteAttribute(name){delete this.attributes[name];return this;}hasAttribute(name){return this.attributes[name]!==undefined;}addGroup(start,count,materialIndex=0){this.groups.push({start:start,count:count,materialIndex:materialIndex});}clearGroups(){this.groups=[];}setDrawRange(start,count){this.drawRange.start=start;this.drawRange.count=count;}applyMatrix4(matrix){const position=this.attributes.position;if(position!==undefined){position.applyMatrix4(matrix);position.needsUpdate=true;}const normal=this.attributes.normal;if(normal!==undefined){const normalMatrix=new Matrix3$1().getNormalMatrix(matrix);normal.applyNormalMatrix(normalMatrix);normal.needsUpdate=true;}const tangent=this.attributes.tangent;if(tangent!==undefined){tangent.transformDirection(matrix);tangent.needsUpdate=true;}if(this.boundingBox!==null){this.computeBoundingBox();}if(this.boundingSphere!==null){this.computeBoundingSphere();}return this;}applyQuaternion(q){_m1$2$1.makeRotationFromQuaternion(q);this.applyMatrix4(_m1$2$1);return this;}rotateX(angle){// rotate geometry around world x-axis
_m1$2$1.makeRotationX(angle);this.applyMatrix4(_m1$2$1);return this;}rotateY(angle){// rotate geometry around world y-axis
_m1$2$1.makeRotationY(angle);this.applyMatrix4(_m1$2$1);return this;}rotateZ(angle){// rotate geometry around world z-axis
_m1$2$1.makeRotationZ(angle);this.applyMatrix4(_m1$2$1);return this;}translate(x,y,z){// translate geometry
_m1$2$1.makeTranslation(x,y,z);this.applyMatrix4(_m1$2$1);return this;}scale(x,y,z){// scale geometry
_m1$2$1.makeScale(x,y,z);this.applyMatrix4(_m1$2$1);return this;}lookAt(vector){_obj$1.lookAt(vector);_obj$1.updateMatrix();this.applyMatrix4(_obj$1.matrix);return this;}center(){this.computeBoundingBox();this.boundingBox.getCenter(_offset$1).negate();this.translate(_offset$1.x,_offset$1.y,_offset$1.z);return this;}setFromPoints(points){const position=[];for(let i=0,l=points.length;i<l;i++){const point=points[i];position.push(point.x,point.y,point.z||0);}this.setAttribute('position',new Float32BufferAttribute$1(position,3));return this;}computeBoundingBox(){if(this.boundingBox===null){this.boundingBox=new Box3$1();}const position=this.attributes.position;const morphAttributesPosition=this.morphAttributes.position;if(position&&position.isGLBufferAttribute){console.error('THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.',this);this.boundingBox.set(new Vector3$1(-Infinity,-Infinity,-Infinity),new Vector3$1(+Infinity,+Infinity,+Infinity));return;}if(position!==undefined){this.boundingBox.setFromBufferAttribute(position);// process morph attributes if present
if(morphAttributesPosition){for(let i=0,il=morphAttributesPosition.length;i<il;i++){const morphAttribute=morphAttributesPosition[i];_box$2$1.setFromBufferAttribute(morphAttribute);if(this.morphTargetsRelative){_vector$8.addVectors(this.boundingBox.min,_box$2$1.min);this.boundingBox.expandByPoint(_vector$8);_vector$8.addVectors(this.boundingBox.max,_box$2$1.max);this.boundingBox.expandByPoint(_vector$8);}else {this.boundingBox.expandByPoint(_box$2$1.min);this.boundingBox.expandByPoint(_box$2$1.max);}}}}else {this.boundingBox.makeEmpty();}if(isNaN(this.boundingBox.min.x)||isNaN(this.boundingBox.min.y)||isNaN(this.boundingBox.min.z)){console.error('THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.',this);}}computeBoundingSphere(){if(this.boundingSphere===null){this.boundingSphere=new Sphere$1();}const position=this.attributes.position;const morphAttributesPosition=this.morphAttributes.position;if(position&&position.isGLBufferAttribute){console.error('THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.',this);this.boundingSphere.set(new Vector3$1(),Infinity);return;}if(position){// first, find the center of the bounding sphere
const center=this.boundingSphere.center;_box$2$1.setFromBufferAttribute(position);// process morph attributes if present
if(morphAttributesPosition){for(let i=0,il=morphAttributesPosition.length;i<il;i++){const morphAttribute=morphAttributesPosition[i];_boxMorphTargets$1.setFromBufferAttribute(morphAttribute);if(this.morphTargetsRelative){_vector$8.addVectors(_box$2$1.min,_boxMorphTargets$1.min);_box$2$1.expandByPoint(_vector$8);_vector$8.addVectors(_box$2$1.max,_boxMorphTargets$1.max);_box$2$1.expandByPoint(_vector$8);}else {_box$2$1.expandByPoint(_boxMorphTargets$1.min);_box$2$1.expandByPoint(_boxMorphTargets$1.max);}}}_box$2$1.getCenter(center);// second, try to find a boundingSphere with a radius smaller than the
// boundingSphere of the boundingBox: sqrt(3) smaller in the best case
let maxRadiusSq=0;for(let i=0,il=position.count;i<il;i++){_vector$8.fromBufferAttribute(position,i);maxRadiusSq=Math.max(maxRadiusSq,center.distanceToSquared(_vector$8));}// process morph attributes if present
if(morphAttributesPosition){for(let i=0,il=morphAttributesPosition.length;i<il;i++){const morphAttribute=morphAttributesPosition[i];const morphTargetsRelative=this.morphTargetsRelative;for(let j=0,jl=morphAttribute.count;j<jl;j++){_vector$8.fromBufferAttribute(morphAttribute,j);if(morphTargetsRelative){_offset$1.fromBufferAttribute(position,j);_vector$8.add(_offset$1);}maxRadiusSq=Math.max(maxRadiusSq,center.distanceToSquared(_vector$8));}}}this.boundingSphere.radius=Math.sqrt(maxRadiusSq);if(isNaN(this.boundingSphere.radius)){console.error('THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.',this);}}}computeTangents(){const index=this.index;const attributes=this.attributes;// based on http://www.terathon.com/code/tangent.html
// (per vertex tangents)
if(index===null||attributes.position===undefined||attributes.normal===undefined||attributes.uv===undefined){console.error('THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)');return;}const positionAttribute=attributes.position;const normalAttribute=attributes.normal;const uvAttribute=attributes.uv;if(this.hasAttribute('tangent')===false){this.setAttribute('tangent',new BufferAttribute$1(new Float32Array(4*positionAttribute.count),4));}const tangentAttribute=this.getAttribute('tangent');const tan1=[],tan2=[];for(let i=0;i<positionAttribute.count;i++){tan1[i]=new Vector3$1();tan2[i]=new Vector3$1();}const vA=new Vector3$1(),vB=new Vector3$1(),vC=new Vector3$1(),uvA=new Vector2$1(),uvB=new Vector2$1(),uvC=new Vector2$1(),sdir=new Vector3$1(),tdir=new Vector3$1();function handleTriangle(a,b,c){vA.fromBufferAttribute(positionAttribute,a);vB.fromBufferAttribute(positionAttribute,b);vC.fromBufferAttribute(positionAttribute,c);uvA.fromBufferAttribute(uvAttribute,a);uvB.fromBufferAttribute(uvAttribute,b);uvC.fromBufferAttribute(uvAttribute,c);vB.sub(vA);vC.sub(vA);uvB.sub(uvA);uvC.sub(uvA);const r=1.0/(uvB.x*uvC.y-uvC.x*uvB.y);// silently ignore degenerate uv triangles having coincident or colinear vertices
if(!isFinite(r))return;sdir.copy(vB).multiplyScalar(uvC.y).addScaledVector(vC,-uvB.y).multiplyScalar(r);tdir.copy(vC).multiplyScalar(uvB.x).addScaledVector(vB,-uvC.x).multiplyScalar(r);tan1[a].add(sdir);tan1[b].add(sdir);tan1[c].add(sdir);tan2[a].add(tdir);tan2[b].add(tdir);tan2[c].add(tdir);}let groups=this.groups;if(groups.length===0){groups=[{start:0,count:index.count}];}for(let i=0,il=groups.length;i<il;++i){const group=groups[i];const start=group.start;const count=group.count;for(let j=start,jl=start+count;j<jl;j+=3){handleTriangle(index.getX(j+0),index.getX(j+1),index.getX(j+2));}}const tmp=new Vector3$1(),tmp2=new Vector3$1();const n=new Vector3$1(),n2=new Vector3$1();function handleVertex(v){n.fromBufferAttribute(normalAttribute,v);n2.copy(n);const t=tan1[v];// Gram-Schmidt orthogonalize
tmp.copy(t);tmp.sub(n.multiplyScalar(n.dot(t))).normalize();// Calculate handedness
tmp2.crossVectors(n2,t);const test=tmp2.dot(tan2[v]);const w=test<0.0?-1.0:1.0;tangentAttribute.setXYZW(v,tmp.x,tmp.y,tmp.z,w);}for(let i=0,il=groups.length;i<il;++i){const group=groups[i];const start=group.start;const count=group.count;for(let j=start,jl=start+count;j<jl;j+=3){handleVertex(index.getX(j+0));handleVertex(index.getX(j+1));handleVertex(index.getX(j+2));}}}computeVertexNormals(){const index=this.index;const positionAttribute=this.getAttribute('position');if(positionAttribute!==undefined){let normalAttribute=this.getAttribute('normal');if(normalAttribute===undefined){normalAttribute=new BufferAttribute$1(new Float32Array(positionAttribute.count*3),3);this.setAttribute('normal',normalAttribute);}else {// reset existing normals to zero
for(let i=0,il=normalAttribute.count;i<il;i++){normalAttribute.setXYZ(i,0,0,0);}}const pA=new Vector3$1(),pB=new Vector3$1(),pC=new Vector3$1();const nA=new Vector3$1(),nB=new Vector3$1(),nC=new Vector3$1();const cb=new Vector3$1(),ab=new Vector3$1();// indexed elements
if(index){for(let i=0,il=index.count;i<il;i+=3){const vA=index.getX(i+0);const vB=index.getX(i+1);const vC=index.getX(i+2);pA.fromBufferAttribute(positionAttribute,vA);pB.fromBufferAttribute(positionAttribute,vB);pC.fromBufferAttribute(positionAttribute,vC);cb.subVectors(pC,pB);ab.subVectors(pA,pB);cb.cross(ab);nA.fromBufferAttribute(normalAttribute,vA);nB.fromBufferAttribute(normalAttribute,vB);nC.fromBufferAttribute(normalAttribute,vC);nA.add(cb);nB.add(cb);nC.add(cb);normalAttribute.setXYZ(vA,nA.x,nA.y,nA.z);normalAttribute.setXYZ(vB,nB.x,nB.y,nB.z);normalAttribute.setXYZ(vC,nC.x,nC.y,nC.z);}}else {// non-indexed elements (unconnected triangle soup)
for(let i=0,il=positionAttribute.count;i<il;i+=3){pA.fromBufferAttribute(positionAttribute,i+0);pB.fromBufferAttribute(positionAttribute,i+1);pC.fromBufferAttribute(positionAttribute,i+2);cb.subVectors(pC,pB);ab.subVectors(pA,pB);cb.cross(ab);normalAttribute.setXYZ(i+0,cb.x,cb.y,cb.z);normalAttribute.setXYZ(i+1,cb.x,cb.y,cb.z);normalAttribute.setXYZ(i+2,cb.x,cb.y,cb.z);}}this.normalizeNormals();normalAttribute.needsUpdate=true;}}normalizeNormals(){const normals=this.attributes.normal;for(let i=0,il=normals.count;i<il;i++){_vector$8.fromBufferAttribute(normals,i);_vector$8.normalize();normals.setXYZ(i,_vector$8.x,_vector$8.y,_vector$8.z);}}toNonIndexed(){function convertBufferAttribute(attribute,indices){const array=attribute.array;const itemSize=attribute.itemSize;const normalized=attribute.normalized;const array2=new array.constructor(indices.length*itemSize);let index=0,index2=0;for(let i=0,l=indices.length;i<l;i++){if(attribute.isInterleavedBufferAttribute){index=indices[i]*attribute.data.stride+attribute.offset;}else {index=indices[i]*itemSize;}for(let j=0;j<itemSize;j++){array2[index2++]=array[index++];}}return new BufferAttribute$1(array2,itemSize,normalized);}//
if(this.index===null){console.warn('THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed.');return this;}const geometry2=new BufferGeometry$1();const indices=this.index.array;const attributes=this.attributes;// attributes
for(const name in attributes){const attribute=attributes[name];const newAttribute=convertBufferAttribute(attribute,indices);geometry2.setAttribute(name,newAttribute);}// morph attributes
const morphAttributes=this.morphAttributes;for(const name in morphAttributes){const morphArray=[];const morphAttribute=morphAttributes[name];// morphAttribute: array of Float32BufferAttributes
for(let i=0,il=morphAttribute.length;i<il;i++){const attribute=morphAttribute[i];const newAttribute=convertBufferAttribute(attribute,indices);morphArray.push(newAttribute);}geometry2.morphAttributes[name]=morphArray;}geometry2.morphTargetsRelative=this.morphTargetsRelative;// groups
const groups=this.groups;for(let i=0,l=groups.length;i<l;i++){const group=groups[i];geometry2.addGroup(group.start,group.count,group.materialIndex);}return geometry2;}toJSON(){const data={metadata:{version:4.6,type:'BufferGeometry',generator:'BufferGeometry.toJSON'}};// standard BufferGeometry serialization
data.uuid=this.uuid;data.type=this.type;if(this.name!=='')data.name=this.name;if(Object.keys(this.userData).length>0)data.userData=this.userData;if(this.parameters!==undefined){const parameters=this.parameters;for(const key in parameters){if(parameters[key]!==undefined)data[key]=parameters[key];}return data;}// for simplicity the code assumes attributes are not shared across geometries, see #15811
data.data={attributes:{}};const index=this.index;if(index!==null){data.data.index={type:index.array.constructor.name,array:Array.prototype.slice.call(index.array)};}const attributes=this.attributes;for(const key in attributes){const attribute=attributes[key];data.data.attributes[key]=attribute.toJSON(data.data);}const morphAttributes={};let hasMorphAttributes=false;for(const key in this.morphAttributes){const attributeArray=this.morphAttributes[key];const array=[];for(let i=0,il=attributeArray.length;i<il;i++){const attribute=attributeArray[i];array.push(attribute.toJSON(data.data));}if(array.length>0){morphAttributes[key]=array;hasMorphAttributes=true;}}if(hasMorphAttributes){data.data.morphAttributes=morphAttributes;data.data.morphTargetsRelative=this.morphTargetsRelative;}const groups=this.groups;if(groups.length>0){data.data.groups=JSON.parse(JSON.stringify(groups));}const boundingSphere=this.boundingSphere;if(boundingSphere!==null){data.data.boundingSphere={center:boundingSphere.center.toArray(),radius:boundingSphere.radius};}return data;}clone(){return new this.constructor().copy(this);}copy(source){// reset
this.index=null;this.attributes={};this.morphAttributes={};this.groups=[];this.boundingBox=null;this.boundingSphere=null;// used for storing cloned, shared data
const data={};// name
this.name=source.name;// index
const index=source.index;if(index!==null){this.setIndex(index.clone(data));}// attributes
const attributes=source.attributes;for(const name in attributes){const attribute=attributes[name];this.setAttribute(name,attribute.clone(data));}// morph attributes
const morphAttributes=source.morphAttributes;for(const name in morphAttributes){const array=[];const morphAttribute=morphAttributes[name];// morphAttribute: array of Float32BufferAttributes
for(let i=0,l=morphAttribute.length;i<l;i++){array.push(morphAttribute[i].clone(data));}this.morphAttributes[name]=array;}this.morphTargetsRelative=source.morphTargetsRelative;// groups
const groups=source.groups;for(let i=0,l=groups.length;i<l;i++){const group=groups[i];this.addGroup(group.start,group.count,group.materialIndex);}// bounding box
const boundingBox=source.boundingBox;if(boundingBox!==null){this.boundingBox=boundingBox.clone();}// bounding sphere
const boundingSphere=source.boundingSphere;if(boundingSphere!==null){this.boundingSphere=boundingSphere.clone();}// draw range
this.drawRange.start=source.drawRange.start;this.drawRange.count=source.drawRange.count;// user data
this.userData=source.userData;return this;}dispose(){this.dispatchEvent({type:'dispose'});}}const _inverseMatrix$3=/*@__PURE__*/new Matrix4$1();const _ray$3=/*@__PURE__*/new Ray$1();const _sphere$6=/*@__PURE__*/new Sphere$1();const _sphereHitAt$1=/*@__PURE__*/new Vector3$1();const _vA$1=/*@__PURE__*/new Vector3$1();const _vB$1=/*@__PURE__*/new Vector3$1();const _vC$1=/*@__PURE__*/new Vector3$1();const _tempA$1=/*@__PURE__*/new Vector3$1();const _morphA$1=/*@__PURE__*/new Vector3$1();const _uvA$1=/*@__PURE__*/new Vector2$1();const _uvB$1=/*@__PURE__*/new Vector2$1();const _uvC$1=/*@__PURE__*/new Vector2$1();const _normalA$1=/*@__PURE__*/new Vector3$1();const _normalB$1=/*@__PURE__*/new Vector3$1();const _normalC$1=/*@__PURE__*/new Vector3$1();const _intersectionPoint$1=/*@__PURE__*/new Vector3$1();const _intersectionPointWorld$1=/*@__PURE__*/new Vector3$1();class Mesh$1 extends Object3D$1{constructor(geometry=new BufferGeometry$1(),material=new MeshBasicMaterial$1()){super();this.isMesh=true;this.type='Mesh';this.geometry=geometry;this.material=material;this.updateMorphTargets();}copy(source,recursive){super.copy(source,recursive);if(source.morphTargetInfluences!==undefined){this.morphTargetInfluences=source.morphTargetInfluences.slice();}if(source.morphTargetDictionary!==undefined){this.morphTargetDictionary=Object.assign({},source.morphTargetDictionary);}this.material=Array.isArray(source.material)?source.material.slice():source.material;this.geometry=source.geometry;return this;}updateMorphTargets(){const geometry=this.geometry;const morphAttributes=geometry.morphAttributes;const keys=Object.keys(morphAttributes);if(keys.length>0){const morphAttribute=morphAttributes[keys[0]];if(morphAttribute!==undefined){this.morphTargetInfluences=[];this.morphTargetDictionary={};for(let m=0,ml=morphAttribute.length;m<ml;m++){const name=morphAttribute[m].name||String(m);this.morphTargetInfluences.push(0);this.morphTargetDictionary[name]=m;}}}}getVertexPosition(index,target){const geometry=this.geometry;const position=geometry.attributes.position;const morphPosition=geometry.morphAttributes.position;const morphTargetsRelative=geometry.morphTargetsRelative;target.fromBufferAttribute(position,index);const morphInfluences=this.morphTargetInfluences;if(morphPosition&&morphInfluences){_morphA$1.set(0,0,0);for(let i=0,il=morphPosition.length;i<il;i++){const influence=morphInfluences[i];const morphAttribute=morphPosition[i];if(influence===0)continue;_tempA$1.fromBufferAttribute(morphAttribute,index);if(morphTargetsRelative){_morphA$1.addScaledVector(_tempA$1,influence);}else {_morphA$1.addScaledVector(_tempA$1.sub(target),influence);}}target.add(_morphA$1);}return target;}raycast(raycaster,intersects){const geometry=this.geometry;const material=this.material;const matrixWorld=this.matrixWorld;if(material===undefined)return;// test with bounding sphere in world space
if(geometry.boundingSphere===null)geometry.computeBoundingSphere();_sphere$6.copy(geometry.boundingSphere);_sphere$6.applyMatrix4(matrixWorld);// check distance from ray origin to bounding sphere
_ray$3.copy(raycaster.ray).recast(raycaster.near);if(_sphere$6.containsPoint(_ray$3.origin)===false){if(_ray$3.intersectSphere(_sphere$6,_sphereHitAt$1)===null)return;if(_ray$3.origin.distanceToSquared(_sphereHitAt$1)>(raycaster.far-raycaster.near)**2)return;}// convert ray to local space of mesh
_inverseMatrix$3.copy(matrixWorld).invert();_ray$3.copy(raycaster.ray).applyMatrix4(_inverseMatrix$3);// test with bounding box in local space
if(geometry.boundingBox!==null){if(_ray$3.intersectsBox(geometry.boundingBox)===false)return;}// test for intersections with geometry
this._computeIntersections(raycaster,intersects,_ray$3);}_computeIntersections(raycaster,intersects,rayLocalSpace){let intersection;const geometry=this.geometry;const material=this.material;const index=geometry.index;const position=geometry.attributes.position;const uv=geometry.attributes.uv;const uv1=geometry.attributes.uv1;const normal=geometry.attributes.normal;const groups=geometry.groups;const drawRange=geometry.drawRange;if(index!==null){// indexed buffer geometry
if(Array.isArray(material)){for(let i=0,il=groups.length;i<il;i++){const group=groups[i];const groupMaterial=material[group.materialIndex];const start=Math.max(group.start,drawRange.start);const end=Math.min(index.count,Math.min(group.start+group.count,drawRange.start+drawRange.count));for(let j=start,jl=end;j<jl;j+=3){const a=index.getX(j);const b=index.getX(j+1);const c=index.getX(j+2);intersection=checkGeometryIntersection$1(this,groupMaterial,raycaster,rayLocalSpace,uv,uv1,normal,a,b,c);if(intersection){intersection.faceIndex=Math.floor(j/3);// triangle number in indexed buffer semantics
intersection.face.materialIndex=group.materialIndex;intersects.push(intersection);}}}}else {const start=Math.max(0,drawRange.start);const end=Math.min(index.count,drawRange.start+drawRange.count);for(let i=start,il=end;i<il;i+=3){const a=index.getX(i);const b=index.getX(i+1);const c=index.getX(i+2);intersection=checkGeometryIntersection$1(this,material,raycaster,rayLocalSpace,uv,uv1,normal,a,b,c);if(intersection){intersection.faceIndex=Math.floor(i/3);// triangle number in indexed buffer semantics
intersects.push(intersection);}}}}else if(position!==undefined){// non-indexed buffer geometry
if(Array.isArray(material)){for(let i=0,il=groups.length;i<il;i++){const group=groups[i];const groupMaterial=material[group.materialIndex];const start=Math.max(group.start,drawRange.start);const end=Math.min(position.count,Math.min(group.start+group.count,drawRange.start+drawRange.count));for(let j=start,jl=end;j<jl;j+=3){const a=j;const b=j+1;const c=j+2;intersection=checkGeometryIntersection$1(this,groupMaterial,raycaster,rayLocalSpace,uv,uv1,normal,a,b,c);if(intersection){intersection.faceIndex=Math.floor(j/3);// triangle number in non-indexed buffer semantics
intersection.face.materialIndex=group.materialIndex;intersects.push(intersection);}}}}else {const start=Math.max(0,drawRange.start);const end=Math.min(position.count,drawRange.start+drawRange.count);for(let i=start,il=end;i<il;i+=3){const a=i;const b=i+1;const c=i+2;intersection=checkGeometryIntersection$1(this,material,raycaster,rayLocalSpace,uv,uv1,normal,a,b,c);if(intersection){intersection.faceIndex=Math.floor(i/3);// triangle number in non-indexed buffer semantics
intersects.push(intersection);}}}}}}function checkIntersection$1(object,material,raycaster,ray,pA,pB,pC,point){let intersect;if(material.side===BackSide$1){intersect=ray.intersectTriangle(pC,pB,pA,true,point);}else {intersect=ray.intersectTriangle(pA,pB,pC,material.side===FrontSide$1,point);}if(intersect===null)return null;_intersectionPointWorld$1.copy(point);_intersectionPointWorld$1.applyMatrix4(object.matrixWorld);const distance=raycaster.ray.origin.distanceTo(_intersectionPointWorld$1);if(distance<raycaster.near||distance>raycaster.far)return null;return {distance:distance,point:_intersectionPointWorld$1.clone(),object:object};}function checkGeometryIntersection$1(object,material,raycaster,ray,uv,uv1,normal,a,b,c){object.getVertexPosition(a,_vA$1);object.getVertexPosition(b,_vB$1);object.getVertexPosition(c,_vC$1);const intersection=checkIntersection$1(object,material,raycaster,ray,_vA$1,_vB$1,_vC$1,_intersectionPoint$1);if(intersection){if(uv){_uvA$1.fromBufferAttribute(uv,a);_uvB$1.fromBufferAttribute(uv,b);_uvC$1.fromBufferAttribute(uv,c);intersection.uv=Triangle$1.getInterpolation(_intersectionPoint$1,_vA$1,_vB$1,_vC$1,_uvA$1,_uvB$1,_uvC$1,new Vector2$1());}if(uv1){_uvA$1.fromBufferAttribute(uv1,a);_uvB$1.fromBufferAttribute(uv1,b);_uvC$1.fromBufferAttribute(uv1,c);intersection.uv1=Triangle$1.getInterpolation(_intersectionPoint$1,_vA$1,_vB$1,_vC$1,_uvA$1,_uvB$1,_uvC$1,new Vector2$1());}if(normal){_normalA$1.fromBufferAttribute(normal,a);_normalB$1.fromBufferAttribute(normal,b);_normalC$1.fromBufferAttribute(normal,c);intersection.normal=Triangle$1.getInterpolation(_intersectionPoint$1,_vA$1,_vB$1,_vC$1,_normalA$1,_normalB$1,_normalC$1,new Vector3$1());if(intersection.normal.dot(ray.direction)>0){intersection.normal.multiplyScalar(-1);}}const face={a:a,b:b,c:c,normal:new Vector3$1(),materialIndex:0};Triangle$1.getNormal(_vA$1,_vB$1,_vC$1,face.normal);intersection.face=face;}return intersection;}class BoxGeometry extends BufferGeometry$1{constructor(width=1,height=1,depth=1,widthSegments=1,heightSegments=1,depthSegments=1){super();this.type='BoxGeometry';this.parameters={width:width,height:height,depth:depth,widthSegments:widthSegments,heightSegments:heightSegments,depthSegments:depthSegments};const scope=this;// segments
widthSegments=Math.floor(widthSegments);heightSegments=Math.floor(heightSegments);depthSegments=Math.floor(depthSegments);// buffers
const indices=[];const vertices=[];const normals=[];const uvs=[];// helper variables
let numberOfVertices=0;let groupStart=0;// build each side of the box geometry
buildPlane('z','y','x',-1,-1,depth,height,width,depthSegments,heightSegments,0);// px
buildPlane('z','y','x',1,-1,depth,height,-width,depthSegments,heightSegments,1);// nx
buildPlane('x','z','y',1,1,width,depth,height,widthSegments,depthSegments,2);// py
buildPlane('x','z','y',1,-1,width,depth,-height,widthSegments,depthSegments,3);// ny
buildPlane('x','y','z',1,-1,width,height,depth,widthSegments,heightSegments,4);// pz
buildPlane('x','y','z',-1,-1,width,height,-depth,widthSegments,heightSegments,5);// nz
// build geometry
this.setIndex(indices);this.setAttribute('position',new Float32BufferAttribute$1(vertices,3));this.setAttribute('normal',new Float32BufferAttribute$1(normals,3));this.setAttribute('uv',new Float32BufferAttribute$1(uvs,2));function buildPlane(u,v,w,udir,vdir,width,height,depth,gridX,gridY,materialIndex){const segmentWidth=width/gridX;const segmentHeight=height/gridY;const widthHalf=width/2;const heightHalf=height/2;const depthHalf=depth/2;const gridX1=gridX+1;const gridY1=gridY+1;let vertexCounter=0;let groupCount=0;const vector=new Vector3$1();// generate vertices, normals and uvs
for(let iy=0;iy<gridY1;iy++){const y=iy*segmentHeight-heightHalf;for(let ix=0;ix<gridX1;ix++){const x=ix*segmentWidth-widthHalf;// set values to correct vector component
vector[u]=x*udir;vector[v]=y*vdir;vector[w]=depthHalf;// now apply vector to vertex buffer
vertices.push(vector.x,vector.y,vector.z);// set values to correct vector component
vector[u]=0;vector[v]=0;vector[w]=depth>0?1:-1;// now apply vector to normal buffer
normals.push(vector.x,vector.y,vector.z);// uvs
uvs.push(ix/gridX);uvs.push(1-iy/gridY);// counters
vertexCounter+=1;}}// indices
// 1. you need three indices to draw a single face
// 2. a single segment consists of two faces
// 3. so we need to generate six (2*3) indices per segment
for(let iy=0;iy<gridY;iy++){for(let ix=0;ix<gridX;ix++){const a=numberOfVertices+ix+gridX1*iy;const b=numberOfVertices+ix+gridX1*(iy+1);const c=numberOfVertices+(ix+1)+gridX1*(iy+1);const d=numberOfVertices+(ix+1)+gridX1*iy;// faces
indices.push(a,b,d);indices.push(b,c,d);// increase counter
groupCount+=6;}}// add a group to the geometry. this will ensure multi material support
scope.addGroup(groupStart,groupCount,materialIndex);// calculate new start value for groups
groupStart+=groupCount;// update total number of vertices
numberOfVertices+=vertexCounter;}}copy(source){super.copy(source);this.parameters=Object.assign({},source.parameters);return this;}static fromJSON(data){return new BoxGeometry(data.width,data.height,data.depth,data.widthSegments,data.heightSegments,data.depthSegments);}}/**
 * Uniform Utilities
 */function cloneUniforms(src){const dst={};for(const u in src){dst[u]={};for(const p in src[u]){const property=src[u][p];if(property&&(property.isColor||property.isMatrix3||property.isMatrix4||property.isVector2||property.isVector3||property.isVector4||property.isTexture||property.isQuaternion)){if(property.isRenderTargetTexture){console.warn('UniformsUtils: Textures of render targets cannot be cloned via cloneUniforms() or mergeUniforms().');dst[u][p]=null;}else {dst[u][p]=property.clone();}}else if(Array.isArray(property)){dst[u][p]=property.slice();}else {dst[u][p]=property;}}}return dst;}function mergeUniforms(uniforms){const merged={};for(let u=0;u<uniforms.length;u++){const tmp=cloneUniforms(uniforms[u]);for(const p in tmp){merged[p]=tmp[p];}}return merged;}function cloneUniformsGroups(src){const dst=[];for(let u=0;u<src.length;u++){dst.push(src[u].clone());}return dst;}function getUnlitUniformColorSpace(renderer){const currentRenderTarget=renderer.getRenderTarget();if(currentRenderTarget===null){// https://github.com/mrdoob/three.js/pull/23937#issuecomment-1111067398
return renderer.outputColorSpace;}// https://github.com/mrdoob/three.js/issues/27868
if(currentRenderTarget.isXRRenderTarget===true){return currentRenderTarget.texture.colorSpace;}return ColorManagement$1.workingColorSpace;}// Legacy
const UniformsUtils={clone:cloneUniforms,merge:mergeUniforms};var default_vertex="void main() {\n\tgl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );\n}";var default_fragment="void main() {\n\tgl_FragColor = vec4( 1.0, 0.0, 0.0, 1.0 );\n}";class ShaderMaterial extends Material$1{constructor(parameters){super();this.isShaderMaterial=true;this.type='ShaderMaterial';this.defines={};this.uniforms={};this.uniformsGroups=[];this.vertexShader=default_vertex;this.fragmentShader=default_fragment;this.linewidth=1;this.wireframe=false;this.wireframeLinewidth=1;this.fog=false;// set to use scene fog
this.lights=false;// set to use scene lights
this.clipping=false;// set to use user-defined clipping planes
this.forceSinglePass=true;this.extensions={clipCullDistance:false,// set to use vertex shader clipping
multiDraw:false// set to use vertex shader multi_draw / enable gl_DrawID
};// When rendered geometry doesn't include these attributes but the material does,
// use these default values in WebGL. This avoids errors when buffer data is missing.
this.defaultAttributeValues={'color':[1,1,1],'uv':[0,0],'uv1':[0,0]};this.index0AttributeName=undefined;this.uniformsNeedUpdate=false;this.glslVersion=null;if(parameters!==undefined){this.setValues(parameters);}}copy(source){super.copy(source);this.fragmentShader=source.fragmentShader;this.vertexShader=source.vertexShader;this.uniforms=cloneUniforms(source.uniforms);this.uniformsGroups=cloneUniformsGroups(source.uniformsGroups);this.defines=Object.assign({},source.defines);this.wireframe=source.wireframe;this.wireframeLinewidth=source.wireframeLinewidth;this.fog=source.fog;this.lights=source.lights;this.clipping=source.clipping;this.extensions=Object.assign({},source.extensions);this.glslVersion=source.glslVersion;return this;}toJSON(meta){const data=super.toJSON(meta);data.glslVersion=this.glslVersion;data.uniforms={};for(const name in this.uniforms){const uniform=this.uniforms[name];const value=uniform.value;if(value&&value.isTexture){data.uniforms[name]={type:'t',value:value.toJSON(meta).uuid};}else if(value&&value.isColor){data.uniforms[name]={type:'c',value:value.getHex()};}else if(value&&value.isVector2){data.uniforms[name]={type:'v2',value:value.toArray()};}else if(value&&value.isVector3){data.uniforms[name]={type:'v3',value:value.toArray()};}else if(value&&value.isVector4){data.uniforms[name]={type:'v4',value:value.toArray()};}else if(value&&value.isMatrix3){data.uniforms[name]={type:'m3',value:value.toArray()};}else if(value&&value.isMatrix4){data.uniforms[name]={type:'m4',value:value.toArray()};}else {data.uniforms[name]={value:value};// note: the array variants v2v, v3v, v4v, m4v and tv are not supported so far
}}if(Object.keys(this.defines).length>0)data.defines=this.defines;data.vertexShader=this.vertexShader;data.fragmentShader=this.fragmentShader;data.lights=this.lights;data.clipping=this.clipping;const extensions={};for(const key in this.extensions){if(this.extensions[key]===true)extensions[key]=true;}if(Object.keys(extensions).length>0)data.extensions=extensions;return data;}}class Camera$1 extends Object3D$1{constructor(){super();this.isCamera=true;this.type='Camera';this.matrixWorldInverse=new Matrix4$1();this.projectionMatrix=new Matrix4$1();this.projectionMatrixInverse=new Matrix4$1();this.coordinateSystem=WebGLCoordinateSystem$1;}copy(source,recursive){super.copy(source,recursive);this.matrixWorldInverse.copy(source.matrixWorldInverse);this.projectionMatrix.copy(source.projectionMatrix);this.projectionMatrixInverse.copy(source.projectionMatrixInverse);this.coordinateSystem=source.coordinateSystem;return this;}getWorldDirection(target){return super.getWorldDirection(target).negate();}updateMatrixWorld(force){super.updateMatrixWorld(force);this.matrixWorldInverse.copy(this.matrixWorld).invert();}updateWorldMatrix(updateParents,updateChildren){super.updateWorldMatrix(updateParents,updateChildren);this.matrixWorldInverse.copy(this.matrixWorld).invert();}clone(){return new this.constructor().copy(this);}}const _v3$1$1=/*@__PURE__*/new Vector3$1();const _minTarget$1=/*@__PURE__*/new Vector2$1();const _maxTarget$1=/*@__PURE__*/new Vector2$1();class PerspectiveCamera$1 extends Camera$1{constructor(fov=50,aspect=1,near=0.1,far=2000){super();this.isPerspectiveCamera=true;this.type='PerspectiveCamera';this.fov=fov;this.zoom=1;this.near=near;this.far=far;this.focus=10;this.aspect=aspect;this.view=null;this.filmGauge=35;// width of the film (default in millimeters)
this.filmOffset=0;// horizontal film offset (same unit as gauge)
this.updateProjectionMatrix();}copy(source,recursive){super.copy(source,recursive);this.fov=source.fov;this.zoom=source.zoom;this.near=source.near;this.far=source.far;this.focus=source.focus;this.aspect=source.aspect;this.view=source.view===null?null:Object.assign({},source.view);this.filmGauge=source.filmGauge;this.filmOffset=source.filmOffset;return this;}/**
	 * Sets the FOV by focal length in respect to the current .filmGauge.
	 *
	 * The default film gauge is 35, so that the focal length can be specified for
	 * a 35mm (full frame) camera.
	 *
	 * Values for focal length and film gauge must have the same unit.
	 */setFocalLength(focalLength){/** see {@link http://www.bobatkins.com/photography/technical/field_of_view.html} */const vExtentSlope=0.5*this.getFilmHeight()/focalLength;this.fov=RAD2DEG$1*2*Math.atan(vExtentSlope);this.updateProjectionMatrix();}/**
	 * Calculates the focal length from the current .fov and .filmGauge.
	 */getFocalLength(){const vExtentSlope=Math.tan(DEG2RAD$1*0.5*this.fov);return 0.5*this.getFilmHeight()/vExtentSlope;}getEffectiveFOV(){return RAD2DEG$1*2*Math.atan(Math.tan(DEG2RAD$1*0.5*this.fov)/this.zoom);}getFilmWidth(){// film not completely covered in portrait format (aspect < 1)
return this.filmGauge*Math.min(this.aspect,1);}getFilmHeight(){// film not completely covered in landscape format (aspect > 1)
return this.filmGauge/Math.max(this.aspect,1);}/**
	 * Computes the 2D bounds of the camera's viewable rectangle at a given distance along the viewing direction.
	 * Sets minTarget and maxTarget to the coordinates of the lower-left and upper-right corners of the view rectangle.
	 */getViewBounds(distance,minTarget,maxTarget){_v3$1$1.set(-1,-1,0.5).applyMatrix4(this.projectionMatrixInverse);minTarget.set(_v3$1$1.x,_v3$1$1.y).multiplyScalar(-distance/_v3$1$1.z);_v3$1$1.set(1,1,0.5).applyMatrix4(this.projectionMatrixInverse);maxTarget.set(_v3$1$1.x,_v3$1$1.y).multiplyScalar(-distance/_v3$1$1.z);}/**
	 * Computes the width and height of the camera's viewable rectangle at a given distance along the viewing direction.
	 * Copies the result into the target Vector2, where x is width and y is height.
	 */getViewSize(distance,target){this.getViewBounds(distance,_minTarget$1,_maxTarget$1);return target.subVectors(_maxTarget$1,_minTarget$1);}/**
	 * Sets an offset in a larger frustum. This is useful for multi-window or
	 * multi-monitor/multi-machine setups.
	 *
	 * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
	 * the monitors are in grid like this
	 *
	 *   +---+---+---+
	 *   | A | B | C |
	 *   +---+---+---+
	 *   | D | E | F |
	 *   +---+---+---+
	 *
	 * then for each monitor you would call it like this
	 *
	 *   const w = 1920;
	 *   const h = 1080;
	 *   const fullWidth = w * 3;
	 *   const fullHeight = h * 2;
	 *
	 *   --A--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
	 *   --B--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
	 *   --C--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
	 *   --D--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
	 *   --E--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
	 *   --F--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
	 *
	 *   Note there is no reason monitors have to be the same size or in a grid.
	 */setViewOffset(fullWidth,fullHeight,x,y,width,height){this.aspect=fullWidth/fullHeight;if(this.view===null){this.view={enabled:true,fullWidth:1,fullHeight:1,offsetX:0,offsetY:0,width:1,height:1};}this.view.enabled=true;this.view.fullWidth=fullWidth;this.view.fullHeight=fullHeight;this.view.offsetX=x;this.view.offsetY=y;this.view.width=width;this.view.height=height;this.updateProjectionMatrix();}clearViewOffset(){if(this.view!==null){this.view.enabled=false;}this.updateProjectionMatrix();}updateProjectionMatrix(){const near=this.near;let top=near*Math.tan(DEG2RAD$1*0.5*this.fov)/this.zoom;let height=2*top;let width=this.aspect*height;let left=-0.5*width;const view=this.view;if(this.view!==null&&this.view.enabled){const fullWidth=view.fullWidth,fullHeight=view.fullHeight;left+=view.offsetX*width/fullWidth;top-=view.offsetY*height/fullHeight;width*=view.width/fullWidth;height*=view.height/fullHeight;}const skew=this.filmOffset;if(skew!==0)left+=near*skew/this.getFilmWidth();this.projectionMatrix.makePerspective(left,left+width,top,top-height,near,this.far,this.coordinateSystem);this.projectionMatrixInverse.copy(this.projectionMatrix).invert();}toJSON(meta){const data=super.toJSON(meta);data.object.fov=this.fov;data.object.zoom=this.zoom;data.object.near=this.near;data.object.far=this.far;data.object.focus=this.focus;data.object.aspect=this.aspect;if(this.view!==null)data.object.view=Object.assign({},this.view);data.object.filmGauge=this.filmGauge;data.object.filmOffset=this.filmOffset;return data;}}const fov=-90;// negative fov is not an error
const aspect=1;class CubeCamera extends Object3D$1{constructor(near,far,renderTarget){super();this.type='CubeCamera';this.renderTarget=renderTarget;this.coordinateSystem=null;this.activeMipmapLevel=0;const cameraPX=new PerspectiveCamera$1(fov,aspect,near,far);cameraPX.layers=this.layers;this.add(cameraPX);const cameraNX=new PerspectiveCamera$1(fov,aspect,near,far);cameraNX.layers=this.layers;this.add(cameraNX);const cameraPY=new PerspectiveCamera$1(fov,aspect,near,far);cameraPY.layers=this.layers;this.add(cameraPY);const cameraNY=new PerspectiveCamera$1(fov,aspect,near,far);cameraNY.layers=this.layers;this.add(cameraNY);const cameraPZ=new PerspectiveCamera$1(fov,aspect,near,far);cameraPZ.layers=this.layers;this.add(cameraPZ);const cameraNZ=new PerspectiveCamera$1(fov,aspect,near,far);cameraNZ.layers=this.layers;this.add(cameraNZ);}updateCoordinateSystem(){const coordinateSystem=this.coordinateSystem;const cameras=this.children.concat();const[cameraPX,cameraNX,cameraPY,cameraNY,cameraPZ,cameraNZ]=cameras;for(const camera of cameras)this.remove(camera);if(coordinateSystem===WebGLCoordinateSystem$1){cameraPX.up.set(0,1,0);cameraPX.lookAt(1,0,0);cameraNX.up.set(0,1,0);cameraNX.lookAt(-1,0,0);cameraPY.up.set(0,0,-1);cameraPY.lookAt(0,1,0);cameraNY.up.set(0,0,1);cameraNY.lookAt(0,-1,0);cameraPZ.up.set(0,1,0);cameraPZ.lookAt(0,0,1);cameraNZ.up.set(0,1,0);cameraNZ.lookAt(0,0,-1);}else if(coordinateSystem===WebGPUCoordinateSystem$1){cameraPX.up.set(0,-1,0);cameraPX.lookAt(-1,0,0);cameraNX.up.set(0,-1,0);cameraNX.lookAt(1,0,0);cameraPY.up.set(0,0,1);cameraPY.lookAt(0,1,0);cameraNY.up.set(0,0,-1);cameraNY.lookAt(0,-1,0);cameraPZ.up.set(0,-1,0);cameraPZ.lookAt(0,0,1);cameraNZ.up.set(0,-1,0);cameraNZ.lookAt(0,0,-1);}else {throw new Error('THREE.CubeCamera.updateCoordinateSystem(): Invalid coordinate system: '+coordinateSystem);}for(const camera of cameras){this.add(camera);camera.updateMatrixWorld();}}update(renderer,scene){if(this.parent===null)this.updateMatrixWorld();const{renderTarget,activeMipmapLevel}=this;if(this.coordinateSystem!==renderer.coordinateSystem){this.coordinateSystem=renderer.coordinateSystem;this.updateCoordinateSystem();}const[cameraPX,cameraNX,cameraPY,cameraNY,cameraPZ,cameraNZ]=this.children;const currentRenderTarget=renderer.getRenderTarget();const currentActiveCubeFace=renderer.getActiveCubeFace();const currentActiveMipmapLevel=renderer.getActiveMipmapLevel();const currentXrEnabled=renderer.xr.enabled;renderer.xr.enabled=false;const generateMipmaps=renderTarget.texture.generateMipmaps;renderTarget.texture.generateMipmaps=false;renderer.setRenderTarget(renderTarget,0,activeMipmapLevel);renderer.render(scene,cameraPX);renderer.setRenderTarget(renderTarget,1,activeMipmapLevel);renderer.render(scene,cameraNX);renderer.setRenderTarget(renderTarget,2,activeMipmapLevel);renderer.render(scene,cameraPY);renderer.setRenderTarget(renderTarget,3,activeMipmapLevel);renderer.render(scene,cameraNY);renderer.setRenderTarget(renderTarget,4,activeMipmapLevel);renderer.render(scene,cameraPZ);// mipmaps are generated during the last call of render()
// at this point, all sides of the cube render target are defined
renderTarget.texture.generateMipmaps=generateMipmaps;renderer.setRenderTarget(renderTarget,5,activeMipmapLevel);renderer.render(scene,cameraNZ);renderer.setRenderTarget(currentRenderTarget,currentActiveCubeFace,currentActiveMipmapLevel);renderer.xr.enabled=currentXrEnabled;renderTarget.texture.needsPMREMUpdate=true;}}class CubeTexture extends Texture$1{constructor(images,mapping,wrapS,wrapT,magFilter,minFilter,format,type,anisotropy,colorSpace){images=images!==undefined?images:[];mapping=mapping!==undefined?mapping:CubeReflectionMapping$1;super(images,mapping,wrapS,wrapT,magFilter,minFilter,format,type,anisotropy,colorSpace);this.isCubeTexture=true;this.flipY=false;}get images(){return this.image;}set images(value){this.image=value;}}class WebGLCubeRenderTarget extends WebGLRenderTarget{constructor(size=1,options={}){super(size,size,options);this.isWebGLCubeRenderTarget=true;const image={width:size,height:size,depth:1};const images=[image,image,image,image,image,image];this.texture=new CubeTexture(images,options.mapping,options.wrapS,options.wrapT,options.magFilter,options.minFilter,options.format,options.type,options.anisotropy,options.colorSpace);// By convention -- likely based on the RenderMan spec from the 1990's -- cube maps are specified by WebGL (and three.js)
// in a coordinate system in which positive-x is to the right when looking up the positive-z axis -- in other words,
// in a left-handed coordinate system. By continuing this convention, preexisting cube maps continued to render correctly.
// three.js uses a right-handed coordinate system. So environment maps used in three.js appear to have px and nx swapped
// and the flag isRenderTargetTexture controls this conversion. The flip is not required when using WebGLCubeRenderTarget.texture
// as a cube texture (this is detected when isRenderTargetTexture is set to true for cube textures).
this.texture.isRenderTargetTexture=true;this.texture.generateMipmaps=options.generateMipmaps!==undefined?options.generateMipmaps:false;this.texture.minFilter=options.minFilter!==undefined?options.minFilter:LinearFilter$1;}fromEquirectangularTexture(renderer,texture){this.texture.type=texture.type;this.texture.colorSpace=texture.colorSpace;this.texture.generateMipmaps=texture.generateMipmaps;this.texture.minFilter=texture.minFilter;this.texture.magFilter=texture.magFilter;const shader={uniforms:{tEquirect:{value:null}},vertexShader:/* glsl */`

				varying vec3 vWorldDirection;

				vec3 transformDirection( in vec3 dir, in mat4 matrix ) {

					return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );

				}

				void main() {

					vWorldDirection = transformDirection( position, modelMatrix );

					#include <begin_vertex>
					#include <project_vertex>

				}
			`,fragmentShader:/* glsl */`

				uniform sampler2D tEquirect;

				varying vec3 vWorldDirection;

				#include <common>

				void main() {

					vec3 direction = normalize( vWorldDirection );

					vec2 sampleUV = equirectUv( direction );

					gl_FragColor = texture2D( tEquirect, sampleUV );

				}
			`};const geometry=new BoxGeometry(5,5,5);const material=new ShaderMaterial({name:'CubemapFromEquirect',uniforms:cloneUniforms(shader.uniforms),vertexShader:shader.vertexShader,fragmentShader:shader.fragmentShader,side:BackSide$1,blending:NoBlending});material.uniforms.tEquirect.value=texture;const mesh=new Mesh$1(geometry,material);const currentMinFilter=texture.minFilter;// Avoid blurred poles
if(texture.minFilter===LinearMipmapLinearFilter$1)texture.minFilter=LinearFilter$1;const camera=new CubeCamera(1,10,this);camera.update(renderer,mesh);texture.minFilter=currentMinFilter;mesh.geometry.dispose();mesh.material.dispose();return this;}clear(renderer,color,depth,stencil){const currentRenderTarget=renderer.getRenderTarget();for(let i=0;i<6;i++){renderer.setRenderTarget(this,i);renderer.clear(color,depth,stencil);}renderer.setRenderTarget(currentRenderTarget);}}const _vector1$1=/*@__PURE__*/new Vector3$1();const _vector2$2=/*@__PURE__*/new Vector3$1();const _normalMatrix$1=/*@__PURE__*/new Matrix3$1();class Plane$1{constructor(normal=new Vector3$1(1,0,0),constant=0){this.isPlane=true;// normal is assumed to be normalized
this.normal=normal;this.constant=constant;}set(normal,constant){this.normal.copy(normal);this.constant=constant;return this;}setComponents(x,y,z,w){this.normal.set(x,y,z);this.constant=w;return this;}setFromNormalAndCoplanarPoint(normal,point){this.normal.copy(normal);this.constant=-point.dot(this.normal);return this;}setFromCoplanarPoints(a,b,c){const normal=_vector1$1.subVectors(c,b).cross(_vector2$2.subVectors(a,b)).normalize();// Q: should an error be thrown if normal is zero (e.g. degenerate plane)?
this.setFromNormalAndCoplanarPoint(normal,a);return this;}copy(plane){this.normal.copy(plane.normal);this.constant=plane.constant;return this;}normalize(){// Note: will lead to a divide by zero if the plane is invalid.
const inverseNormalLength=1.0/this.normal.length();this.normal.multiplyScalar(inverseNormalLength);this.constant*=inverseNormalLength;return this;}negate(){this.constant*=-1;this.normal.negate();return this;}distanceToPoint(point){return this.normal.dot(point)+this.constant;}distanceToSphere(sphere){return this.distanceToPoint(sphere.center)-sphere.radius;}projectPoint(point,target){return target.copy(point).addScaledVector(this.normal,-this.distanceToPoint(point));}intersectLine(line,target){const direction=line.delta(_vector1$1);const denominator=this.normal.dot(direction);if(denominator===0){// line is coplanar, return origin
if(this.distanceToPoint(line.start)===0){return target.copy(line.start);}// Unsure if this is the correct method to handle this case.
return null;}const t=-(line.start.dot(this.normal)+this.constant)/denominator;if(t<0||t>1){return null;}return target.copy(line.start).addScaledVector(direction,t);}intersectsLine(line){// Note: this tests if a line intersects the plane, not whether it (or its end-points) are coplanar with it.
const startSign=this.distanceToPoint(line.start);const endSign=this.distanceToPoint(line.end);return startSign<0&&endSign>0||endSign<0&&startSign>0;}intersectsBox(box){return box.intersectsPlane(this);}intersectsSphere(sphere){return sphere.intersectsPlane(this);}coplanarPoint(target){return target.copy(this.normal).multiplyScalar(-this.constant);}applyMatrix4(matrix,optionalNormalMatrix){const normalMatrix=optionalNormalMatrix||_normalMatrix$1.getNormalMatrix(matrix);const referencePoint=this.coplanarPoint(_vector1$1).applyMatrix4(matrix);const normal=this.normal.applyMatrix3(normalMatrix).normalize();this.constant=-referencePoint.dot(normal);return this;}translate(offset){this.constant-=offset.dot(this.normal);return this;}equals(plane){return plane.normal.equals(this.normal)&&plane.constant===this.constant;}clone(){return new this.constructor().copy(this);}}const _sphere$5=/*@__PURE__*/new Sphere$1();const _vector$7=/*@__PURE__*/new Vector3$1();class Frustum{constructor(p0=new Plane$1(),p1=new Plane$1(),p2=new Plane$1(),p3=new Plane$1(),p4=new Plane$1(),p5=new Plane$1()){this.planes=[p0,p1,p2,p3,p4,p5];}set(p0,p1,p2,p3,p4,p5){const planes=this.planes;planes[0].copy(p0);planes[1].copy(p1);planes[2].copy(p2);planes[3].copy(p3);planes[4].copy(p4);planes[5].copy(p5);return this;}copy(frustum){const planes=this.planes;for(let i=0;i<6;i++){planes[i].copy(frustum.planes[i]);}return this;}setFromProjectionMatrix(m,coordinateSystem=WebGLCoordinateSystem$1){const planes=this.planes;const me=m.elements;const me0=me[0],me1=me[1],me2=me[2],me3=me[3];const me4=me[4],me5=me[5],me6=me[6],me7=me[7];const me8=me[8],me9=me[9],me10=me[10],me11=me[11];const me12=me[12],me13=me[13],me14=me[14],me15=me[15];planes[0].setComponents(me3-me0,me7-me4,me11-me8,me15-me12).normalize();planes[1].setComponents(me3+me0,me7+me4,me11+me8,me15+me12).normalize();planes[2].setComponents(me3+me1,me7+me5,me11+me9,me15+me13).normalize();planes[3].setComponents(me3-me1,me7-me5,me11-me9,me15-me13).normalize();planes[4].setComponents(me3-me2,me7-me6,me11-me10,me15-me14).normalize();if(coordinateSystem===WebGLCoordinateSystem$1){planes[5].setComponents(me3+me2,me7+me6,me11+me10,me15+me14).normalize();}else if(coordinateSystem===WebGPUCoordinateSystem$1){planes[5].setComponents(me2,me6,me10,me14).normalize();}else {throw new Error('THREE.Frustum.setFromProjectionMatrix(): Invalid coordinate system: '+coordinateSystem);}return this;}intersectsObject(object){if(object.boundingSphere!==undefined){if(object.boundingSphere===null)object.computeBoundingSphere();_sphere$5.copy(object.boundingSphere).applyMatrix4(object.matrixWorld);}else {const geometry=object.geometry;if(geometry.boundingSphere===null)geometry.computeBoundingSphere();_sphere$5.copy(geometry.boundingSphere).applyMatrix4(object.matrixWorld);}return this.intersectsSphere(_sphere$5);}intersectsSprite(sprite){_sphere$5.center.set(0,0,0);_sphere$5.radius=0.7071067811865476;_sphere$5.applyMatrix4(sprite.matrixWorld);return this.intersectsSphere(_sphere$5);}intersectsSphere(sphere){const planes=this.planes;const center=sphere.center;const negRadius=-sphere.radius;for(let i=0;i<6;i++){const distance=planes[i].distanceToPoint(center);if(distance<negRadius){return false;}}return true;}intersectsBox(box){const planes=this.planes;for(let i=0;i<6;i++){const plane=planes[i];// corner at max distance
_vector$7.x=plane.normal.x>0?box.max.x:box.min.x;_vector$7.y=plane.normal.y>0?box.max.y:box.min.y;_vector$7.z=plane.normal.z>0?box.max.z:box.min.z;if(plane.distanceToPoint(_vector$7)<0){return false;}}return true;}containsPoint(point){const planes=this.planes;for(let i=0;i<6;i++){if(planes[i].distanceToPoint(point)<0){return false;}}return true;}clone(){return new this.constructor().copy(this);}}function WebGLAnimation(){let context=null;let isAnimating=false;let animationLoop=null;let requestId=null;function onAnimationFrame(time,frame){animationLoop(time,frame);requestId=context.requestAnimationFrame(onAnimationFrame);}return {start:function(){if(isAnimating===true)return;if(animationLoop===null)return;requestId=context.requestAnimationFrame(onAnimationFrame);isAnimating=true;},stop:function(){context.cancelAnimationFrame(requestId);isAnimating=false;},setAnimationLoop:function(callback){animationLoop=callback;},setContext:function(value){context=value;}};}function WebGLAttributes(gl){const buffers=new WeakMap();function createBuffer(attribute,bufferType){const array=attribute.array;const usage=attribute.usage;const size=array.byteLength;const buffer=gl.createBuffer();gl.bindBuffer(bufferType,buffer);gl.bufferData(bufferType,array,usage);attribute.onUploadCallback();let type;if(array instanceof Float32Array){type=gl.FLOAT;}else if(array instanceof Uint16Array){if(attribute.isFloat16BufferAttribute){type=gl.HALF_FLOAT;}else {type=gl.UNSIGNED_SHORT;}}else if(array instanceof Int16Array){type=gl.SHORT;}else if(array instanceof Uint32Array){type=gl.UNSIGNED_INT;}else if(array instanceof Int32Array){type=gl.INT;}else if(array instanceof Int8Array){type=gl.BYTE;}else if(array instanceof Uint8Array){type=gl.UNSIGNED_BYTE;}else if(array instanceof Uint8ClampedArray){type=gl.UNSIGNED_BYTE;}else {throw new Error('THREE.WebGLAttributes: Unsupported buffer data format: '+array);}return {buffer:buffer,type:type,bytesPerElement:array.BYTES_PER_ELEMENT,version:attribute.version,size:size};}function updateBuffer(buffer,attribute,bufferType){const array=attribute.array;const updateRange=attribute._updateRange;// @deprecated, r159
const updateRanges=attribute.updateRanges;gl.bindBuffer(bufferType,buffer);if(updateRange.count===-1&&updateRanges.length===0){// Not using update ranges
gl.bufferSubData(bufferType,0,array);}if(updateRanges.length!==0){for(let i=0,l=updateRanges.length;i<l;i++){const range=updateRanges[i];gl.bufferSubData(bufferType,range.start*array.BYTES_PER_ELEMENT,array,range.start,range.count);}attribute.clearUpdateRanges();}// @deprecated, r159
if(updateRange.count!==-1){gl.bufferSubData(bufferType,updateRange.offset*array.BYTES_PER_ELEMENT,array,updateRange.offset,updateRange.count);updateRange.count=-1;// reset range
}attribute.onUploadCallback();}//
function get(attribute){if(attribute.isInterleavedBufferAttribute)attribute=attribute.data;return buffers.get(attribute);}function remove(attribute){if(attribute.isInterleavedBufferAttribute)attribute=attribute.data;const data=buffers.get(attribute);if(data){gl.deleteBuffer(data.buffer);buffers.delete(attribute);}}function update(attribute,bufferType){if(attribute.isInterleavedBufferAttribute)attribute=attribute.data;if(attribute.isGLBufferAttribute){const cached=buffers.get(attribute);if(!cached||cached.version<attribute.version){buffers.set(attribute,{buffer:attribute.buffer,type:attribute.type,bytesPerElement:attribute.elementSize,version:attribute.version});}return;}const data=buffers.get(attribute);if(data===undefined){buffers.set(attribute,createBuffer(attribute,bufferType));}else if(data.version<attribute.version){if(data.size!==attribute.array.byteLength){throw new Error('THREE.WebGLAttributes: The size of the buffer attribute\'s array buffer does not match the original size. Resizing buffer attributes is not supported.');}updateBuffer(data.buffer,attribute,bufferType);data.version=attribute.version;}}return {get:get,remove:remove,update:update};}class PlaneGeometry extends BufferGeometry$1{constructor(width=1,height=1,widthSegments=1,heightSegments=1){super();this.type='PlaneGeometry';this.parameters={width:width,height:height,widthSegments:widthSegments,heightSegments:heightSegments};const width_half=width/2;const height_half=height/2;const gridX=Math.floor(widthSegments);const gridY=Math.floor(heightSegments);const gridX1=gridX+1;const gridY1=gridY+1;const segment_width=width/gridX;const segment_height=height/gridY;//
const indices=[];const vertices=[];const normals=[];const uvs=[];for(let iy=0;iy<gridY1;iy++){const y=iy*segment_height-height_half;for(let ix=0;ix<gridX1;ix++){const x=ix*segment_width-width_half;vertices.push(x,-y,0);normals.push(0,0,1);uvs.push(ix/gridX);uvs.push(1-iy/gridY);}}for(let iy=0;iy<gridY;iy++){for(let ix=0;ix<gridX;ix++){const a=ix+gridX1*iy;const b=ix+gridX1*(iy+1);const c=ix+1+gridX1*(iy+1);const d=ix+1+gridX1*iy;indices.push(a,b,d);indices.push(b,c,d);}}this.setIndex(indices);this.setAttribute('position',new Float32BufferAttribute$1(vertices,3));this.setAttribute('normal',new Float32BufferAttribute$1(normals,3));this.setAttribute('uv',new Float32BufferAttribute$1(uvs,2));}copy(source){super.copy(source);this.parameters=Object.assign({},source.parameters);return this;}static fromJSON(data){return new PlaneGeometry(data.width,data.height,data.widthSegments,data.heightSegments);}}var alphahash_fragment="#ifdef USE_ALPHAHASH\n\tif ( diffuseColor.a < getAlphaHashThreshold( vPosition ) ) discard;\n#endif";var alphahash_pars_fragment="#ifdef USE_ALPHAHASH\n\tconst float ALPHA_HASH_SCALE = 0.05;\n\tfloat hash2D( vec2 value ) {\n\t\treturn fract( 1.0e4 * sin( 17.0 * value.x + 0.1 * value.y ) * ( 0.1 + abs( sin( 13.0 * value.y + value.x ) ) ) );\n\t}\n\tfloat hash3D( vec3 value ) {\n\t\treturn hash2D( vec2( hash2D( value.xy ), value.z ) );\n\t}\n\tfloat getAlphaHashThreshold( vec3 position ) {\n\t\tfloat maxDeriv = max(\n\t\t\tlength( dFdx( position.xyz ) ),\n\t\t\tlength( dFdy( position.xyz ) )\n\t\t);\n\t\tfloat pixScale = 1.0 / ( ALPHA_HASH_SCALE * maxDeriv );\n\t\tvec2 pixScales = vec2(\n\t\t\texp2( floor( log2( pixScale ) ) ),\n\t\t\texp2( ceil( log2( pixScale ) ) )\n\t\t);\n\t\tvec2 alpha = vec2(\n\t\t\thash3D( floor( pixScales.x * position.xyz ) ),\n\t\t\thash3D( floor( pixScales.y * position.xyz ) )\n\t\t);\n\t\tfloat lerpFactor = fract( log2( pixScale ) );\n\t\tfloat x = ( 1.0 - lerpFactor ) * alpha.x + lerpFactor * alpha.y;\n\t\tfloat a = min( lerpFactor, 1.0 - lerpFactor );\n\t\tvec3 cases = vec3(\n\t\t\tx * x / ( 2.0 * a * ( 1.0 - a ) ),\n\t\t\t( x - 0.5 * a ) / ( 1.0 - a ),\n\t\t\t1.0 - ( ( 1.0 - x ) * ( 1.0 - x ) / ( 2.0 * a * ( 1.0 - a ) ) )\n\t\t);\n\t\tfloat threshold = ( x < ( 1.0 - a ) )\n\t\t\t? ( ( x < a ) ? cases.x : cases.y )\n\t\t\t: cases.z;\n\t\treturn clamp( threshold , 1.0e-6, 1.0 );\n\t}\n#endif";var alphamap_fragment="#ifdef USE_ALPHAMAP\n\tdiffuseColor.a *= texture2D( alphaMap, vAlphaMapUv ).g;\n#endif";var alphamap_pars_fragment="#ifdef USE_ALPHAMAP\n\tuniform sampler2D alphaMap;\n#endif";var alphatest_fragment="#ifdef USE_ALPHATEST\n\t#ifdef ALPHA_TO_COVERAGE\n\tdiffuseColor.a = smoothstep( alphaTest, alphaTest + fwidth( diffuseColor.a ), diffuseColor.a );\n\tif ( diffuseColor.a == 0.0 ) discard;\n\t#else\n\tif ( diffuseColor.a < alphaTest ) discard;\n\t#endif\n#endif";var alphatest_pars_fragment="#ifdef USE_ALPHATEST\n\tuniform float alphaTest;\n#endif";var aomap_fragment="#ifdef USE_AOMAP\n\tfloat ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;\n\treflectedLight.indirectDiffuse *= ambientOcclusion;\n\t#if defined( USE_CLEARCOAT ) \n\t\tclearcoatSpecularIndirect *= ambientOcclusion;\n\t#endif\n\t#if defined( USE_SHEEN ) \n\t\tsheenSpecularIndirect *= ambientOcclusion;\n\t#endif\n\t#if defined( USE_ENVMAP ) && defined( STANDARD )\n\t\tfloat dotNV = saturate( dot( geometryNormal, geometryViewDir ) );\n\t\treflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV, ambientOcclusion, material.roughness );\n\t#endif\n#endif";var aomap_pars_fragment="#ifdef USE_AOMAP\n\tuniform sampler2D aoMap;\n\tuniform float aoMapIntensity;\n#endif";var batching_pars_vertex="#ifdef USE_BATCHING\n\t#if ! defined( GL_ANGLE_multi_draw )\n\t#define gl_DrawID _gl_DrawID\n\tuniform int _gl_DrawID;\n\t#endif\n\tuniform highp sampler2D batchingTexture;\n\tuniform highp usampler2D batchingIdTexture;\n\tmat4 getBatchingMatrix( const in float i ) {\n\t\tint size = textureSize( batchingTexture, 0 ).x;\n\t\tint j = int( i ) * 4;\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\tvec4 v1 = texelFetch( batchingTexture, ivec2( x, y ), 0 );\n\t\tvec4 v2 = texelFetch( batchingTexture, ivec2( x + 1, y ), 0 );\n\t\tvec4 v3 = texelFetch( batchingTexture, ivec2( x + 2, y ), 0 );\n\t\tvec4 v4 = texelFetch( batchingTexture, ivec2( x + 3, y ), 0 );\n\t\treturn mat4( v1, v2, v3, v4 );\n\t}\n\tfloat getIndirectIndex( const in int i ) {\n\t\tint size = textureSize( batchingIdTexture, 0 ).x;\n\t\tint x = i % size;\n\t\tint y = i / size;\n\t\treturn float( texelFetch( batchingIdTexture, ivec2( x, y ), 0 ).r );\n\t}\n#endif\n#ifdef USE_BATCHING_COLOR\n\tuniform sampler2D batchingColorTexture;\n\tvec3 getBatchingColor( const in float i ) {\n\t\tint size = textureSize( batchingColorTexture, 0 ).x;\n\t\tint j = int( i );\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\treturn texelFetch( batchingColorTexture, ivec2( x, y ), 0 ).rgb;\n\t}\n#endif";var batching_vertex="#ifdef USE_BATCHING\n\tmat4 batchingMatrix = getBatchingMatrix( getIndirectIndex( gl_DrawID ) );\n#endif";var begin_vertex="vec3 transformed = vec3( position );\n#ifdef USE_ALPHAHASH\n\tvPosition = vec3( position );\n#endif";var beginnormal_vertex="vec3 objectNormal = vec3( normal );\n#ifdef USE_TANGENT\n\tvec3 objectTangent = vec3( tangent.xyz );\n#endif";var bsdfs="float G_BlinnPhong_Implicit( ) {\n\treturn 0.25;\n}\nfloat D_BlinnPhong( const in float shininess, const in float dotNH ) {\n\treturn RECIPROCAL_PI * ( shininess * 0.5 + 1.0 ) * pow( dotNH, shininess );\n}\nvec3 BRDF_BlinnPhong( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 specularColor, const in float shininess ) {\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\tvec3 F = F_Schlick( specularColor, 1.0, dotVH );\n\tfloat G = G_BlinnPhong_Implicit( );\n\tfloat D = D_BlinnPhong( shininess, dotNH );\n\treturn F * ( G * D );\n} // validated";var iridescence_fragment="#ifdef USE_IRIDESCENCE\n\tconst mat3 XYZ_TO_REC709 = mat3(\n\t\t 3.2404542, -0.9692660,  0.0556434,\n\t\t-1.5371385,  1.8760108, -0.2040259,\n\t\t-0.4985314,  0.0415560,  1.0572252\n\t);\n\tvec3 Fresnel0ToIor( vec3 fresnel0 ) {\n\t\tvec3 sqrtF0 = sqrt( fresnel0 );\n\t\treturn ( vec3( 1.0 ) + sqrtF0 ) / ( vec3( 1.0 ) - sqrtF0 );\n\t}\n\tvec3 IorToFresnel0( vec3 transmittedIor, float incidentIor ) {\n\t\treturn pow2( ( transmittedIor - vec3( incidentIor ) ) / ( transmittedIor + vec3( incidentIor ) ) );\n\t}\n\tfloat IorToFresnel0( float transmittedIor, float incidentIor ) {\n\t\treturn pow2( ( transmittedIor - incidentIor ) / ( transmittedIor + incidentIor ));\n\t}\n\tvec3 evalSensitivity( float OPD, vec3 shift ) {\n\t\tfloat phase = 2.0 * PI * OPD * 1.0e-9;\n\t\tvec3 val = vec3( 5.4856e-13, 4.4201e-13, 5.2481e-13 );\n\t\tvec3 pos = vec3( 1.6810e+06, 1.7953e+06, 2.2084e+06 );\n\t\tvec3 var = vec3( 4.3278e+09, 9.3046e+09, 6.6121e+09 );\n\t\tvec3 xyz = val * sqrt( 2.0 * PI * var ) * cos( pos * phase + shift ) * exp( - pow2( phase ) * var );\n\t\txyz.x += 9.7470e-14 * sqrt( 2.0 * PI * 4.5282e+09 ) * cos( 2.2399e+06 * phase + shift[ 0 ] ) * exp( - 4.5282e+09 * pow2( phase ) );\n\t\txyz /= 1.0685e-7;\n\t\tvec3 rgb = XYZ_TO_REC709 * xyz;\n\t\treturn rgb;\n\t}\n\tvec3 evalIridescence( float outsideIOR, float eta2, float cosTheta1, float thinFilmThickness, vec3 baseF0 ) {\n\t\tvec3 I;\n\t\tfloat iridescenceIOR = mix( outsideIOR, eta2, smoothstep( 0.0, 0.03, thinFilmThickness ) );\n\t\tfloat sinTheta2Sq = pow2( outsideIOR / iridescenceIOR ) * ( 1.0 - pow2( cosTheta1 ) );\n\t\tfloat cosTheta2Sq = 1.0 - sinTheta2Sq;\n\t\tif ( cosTheta2Sq < 0.0 ) {\n\t\t\treturn vec3( 1.0 );\n\t\t}\n\t\tfloat cosTheta2 = sqrt( cosTheta2Sq );\n\t\tfloat R0 = IorToFresnel0( iridescenceIOR, outsideIOR );\n\t\tfloat R12 = F_Schlick( R0, 1.0, cosTheta1 );\n\t\tfloat T121 = 1.0 - R12;\n\t\tfloat phi12 = 0.0;\n\t\tif ( iridescenceIOR < outsideIOR ) phi12 = PI;\n\t\tfloat phi21 = PI - phi12;\n\t\tvec3 baseIOR = Fresnel0ToIor( clamp( baseF0, 0.0, 0.9999 ) );\t\tvec3 R1 = IorToFresnel0( baseIOR, iridescenceIOR );\n\t\tvec3 R23 = F_Schlick( R1, 1.0, cosTheta2 );\n\t\tvec3 phi23 = vec3( 0.0 );\n\t\tif ( baseIOR[ 0 ] < iridescenceIOR ) phi23[ 0 ] = PI;\n\t\tif ( baseIOR[ 1 ] < iridescenceIOR ) phi23[ 1 ] = PI;\n\t\tif ( baseIOR[ 2 ] < iridescenceIOR ) phi23[ 2 ] = PI;\n\t\tfloat OPD = 2.0 * iridescenceIOR * thinFilmThickness * cosTheta2;\n\t\tvec3 phi = vec3( phi21 ) + phi23;\n\t\tvec3 R123 = clamp( R12 * R23, 1e-5, 0.9999 );\n\t\tvec3 r123 = sqrt( R123 );\n\t\tvec3 Rs = pow2( T121 ) * R23 / ( vec3( 1.0 ) - R123 );\n\t\tvec3 C0 = R12 + Rs;\n\t\tI = C0;\n\t\tvec3 Cm = Rs - T121;\n\t\tfor ( int m = 1; m <= 2; ++ m ) {\n\t\t\tCm *= r123;\n\t\t\tvec3 Sm = 2.0 * evalSensitivity( float( m ) * OPD, float( m ) * phi );\n\t\t\tI += Cm * Sm;\n\t\t}\n\t\treturn max( I, vec3( 0.0 ) );\n\t}\n#endif";var bumpmap_pars_fragment="#ifdef USE_BUMPMAP\n\tuniform sampler2D bumpMap;\n\tuniform float bumpScale;\n\tvec2 dHdxy_fwd() {\n\t\tvec2 dSTdx = dFdx( vBumpMapUv );\n\t\tvec2 dSTdy = dFdy( vBumpMapUv );\n\t\tfloat Hll = bumpScale * texture2D( bumpMap, vBumpMapUv ).x;\n\t\tfloat dBx = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdx ).x - Hll;\n\t\tfloat dBy = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdy ).x - Hll;\n\t\treturn vec2( dBx, dBy );\n\t}\n\tvec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {\n\t\tvec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );\n\t\tvec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );\n\t\tvec3 vN = surf_norm;\n\t\tvec3 R1 = cross( vSigmaY, vN );\n\t\tvec3 R2 = cross( vN, vSigmaX );\n\t\tfloat fDet = dot( vSigmaX, R1 ) * faceDirection;\n\t\tvec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );\n\t\treturn normalize( abs( fDet ) * surf_norm - vGrad );\n\t}\n#endif";var clipping_planes_fragment="#if NUM_CLIPPING_PLANES > 0\n\tvec4 plane;\n\t#ifdef ALPHA_TO_COVERAGE\n\t\tfloat distanceToPlane, distanceGradient;\n\t\tfloat clipOpacity = 1.0;\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {\n\t\t\tplane = clippingPlanes[ i ];\n\t\t\tdistanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;\n\t\t\tdistanceGradient = fwidth( distanceToPlane ) / 2.0;\n\t\t\tclipOpacity *= smoothstep( - distanceGradient, distanceGradient, distanceToPlane );\n\t\t\tif ( clipOpacity == 0.0 ) discard;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t\t#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES\n\t\t\tfloat unionClipOpacity = 1.0;\n\t\t\t#pragma unroll_loop_start\n\t\t\tfor ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {\n\t\t\t\tplane = clippingPlanes[ i ];\n\t\t\t\tdistanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;\n\t\t\t\tdistanceGradient = fwidth( distanceToPlane ) / 2.0;\n\t\t\t\tunionClipOpacity *= 1.0 - smoothstep( - distanceGradient, distanceGradient, distanceToPlane );\n\t\t\t}\n\t\t\t#pragma unroll_loop_end\n\t\t\tclipOpacity *= 1.0 - unionClipOpacity;\n\t\t#endif\n\t\tdiffuseColor.a *= clipOpacity;\n\t\tif ( diffuseColor.a == 0.0 ) discard;\n\t#else\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {\n\t\t\tplane = clippingPlanes[ i ];\n\t\t\tif ( dot( vClipPosition, plane.xyz ) > plane.w ) discard;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t\t#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES\n\t\t\tbool clipped = true;\n\t\t\t#pragma unroll_loop_start\n\t\t\tfor ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {\n\t\t\t\tplane = clippingPlanes[ i ];\n\t\t\t\tclipped = ( dot( vClipPosition, plane.xyz ) > plane.w ) && clipped;\n\t\t\t}\n\t\t\t#pragma unroll_loop_end\n\t\t\tif ( clipped ) discard;\n\t\t#endif\n\t#endif\n#endif";var clipping_planes_pars_fragment="#if NUM_CLIPPING_PLANES > 0\n\tvarying vec3 vClipPosition;\n\tuniform vec4 clippingPlanes[ NUM_CLIPPING_PLANES ];\n#endif";var clipping_planes_pars_vertex="#if NUM_CLIPPING_PLANES > 0\n\tvarying vec3 vClipPosition;\n#endif";var clipping_planes_vertex="#if NUM_CLIPPING_PLANES > 0\n\tvClipPosition = - mvPosition.xyz;\n#endif";var color_fragment="#if defined( USE_COLOR_ALPHA )\n\tdiffuseColor *= vColor;\n#elif defined( USE_COLOR )\n\tdiffuseColor.rgb *= vColor;\n#endif";var color_pars_fragment="#if defined( USE_COLOR_ALPHA )\n\tvarying vec4 vColor;\n#elif defined( USE_COLOR )\n\tvarying vec3 vColor;\n#endif";var color_pars_vertex="#if defined( USE_COLOR_ALPHA )\n\tvarying vec4 vColor;\n#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )\n\tvarying vec3 vColor;\n#endif";var color_vertex="#if defined( USE_COLOR_ALPHA )\n\tvColor = vec4( 1.0 );\n#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )\n\tvColor = vec3( 1.0 );\n#endif\n#ifdef USE_COLOR\n\tvColor *= color;\n#endif\n#ifdef USE_INSTANCING_COLOR\n\tvColor.xyz *= instanceColor.xyz;\n#endif\n#ifdef USE_BATCHING_COLOR\n\tvec3 batchingColor = getBatchingColor( getIndirectIndex( gl_DrawID ) );\n\tvColor.xyz *= batchingColor.xyz;\n#endif";var common="#define PI 3.141592653589793\n#define PI2 6.283185307179586\n#define PI_HALF 1.5707963267948966\n#define RECIPROCAL_PI 0.3183098861837907\n#define RECIPROCAL_PI2 0.15915494309189535\n#define EPSILON 1e-6\n#ifndef saturate\n#define saturate( a ) clamp( a, 0.0, 1.0 )\n#endif\n#define whiteComplement( a ) ( 1.0 - saturate( a ) )\nfloat pow2( const in float x ) { return x*x; }\nvec3 pow2( const in vec3 x ) { return x*x; }\nfloat pow3( const in float x ) { return x*x*x; }\nfloat pow4( const in float x ) { float x2 = x*x; return x2*x2; }\nfloat max3( const in vec3 v ) { return max( max( v.x, v.y ), v.z ); }\nfloat average( const in vec3 v ) { return dot( v, vec3( 0.3333333 ) ); }\nhighp float rand( const in vec2 uv ) {\n\tconst highp float a = 12.9898, b = 78.233, c = 43758.5453;\n\thighp float dt = dot( uv.xy, vec2( a,b ) ), sn = mod( dt, PI );\n\treturn fract( sin( sn ) * c );\n}\n#ifdef HIGH_PRECISION\n\tfloat precisionSafeLength( vec3 v ) { return length( v ); }\n#else\n\tfloat precisionSafeLength( vec3 v ) {\n\t\tfloat maxComponent = max3( abs( v ) );\n\t\treturn length( v / maxComponent ) * maxComponent;\n\t}\n#endif\nstruct IncidentLight {\n\tvec3 color;\n\tvec3 direction;\n\tbool visible;\n};\nstruct ReflectedLight {\n\tvec3 directDiffuse;\n\tvec3 directSpecular;\n\tvec3 indirectDiffuse;\n\tvec3 indirectSpecular;\n};\n#ifdef USE_ALPHAHASH\n\tvarying vec3 vPosition;\n#endif\nvec3 transformDirection( in vec3 dir, in mat4 matrix ) {\n\treturn normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );\n}\nvec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {\n\treturn normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );\n}\nmat3 transposeMat3( const in mat3 m ) {\n\tmat3 tmp;\n\ttmp[ 0 ] = vec3( m[ 0 ].x, m[ 1 ].x, m[ 2 ].x );\n\ttmp[ 1 ] = vec3( m[ 0 ].y, m[ 1 ].y, m[ 2 ].y );\n\ttmp[ 2 ] = vec3( m[ 0 ].z, m[ 1 ].z, m[ 2 ].z );\n\treturn tmp;\n}\nbool isPerspectiveMatrix( mat4 m ) {\n\treturn m[ 2 ][ 3 ] == - 1.0;\n}\nvec2 equirectUv( in vec3 dir ) {\n\tfloat u = atan( dir.z, dir.x ) * RECIPROCAL_PI2 + 0.5;\n\tfloat v = asin( clamp( dir.y, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;\n\treturn vec2( u, v );\n}\nvec3 BRDF_Lambert( const in vec3 diffuseColor ) {\n\treturn RECIPROCAL_PI * diffuseColor;\n}\nvec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {\n\tfloat fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );\n\treturn f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );\n}\nfloat F_Schlick( const in float f0, const in float f90, const in float dotVH ) {\n\tfloat fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );\n\treturn f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );\n} // validated";var cube_uv_reflection_fragment="#ifdef ENVMAP_TYPE_CUBE_UV\n\t#define cubeUV_minMipLevel 4.0\n\t#define cubeUV_minTileSize 16.0\n\tfloat getFace( vec3 direction ) {\n\t\tvec3 absDirection = abs( direction );\n\t\tfloat face = - 1.0;\n\t\tif ( absDirection.x > absDirection.z ) {\n\t\t\tif ( absDirection.x > absDirection.y )\n\t\t\t\tface = direction.x > 0.0 ? 0.0 : 3.0;\n\t\t\telse\n\t\t\t\tface = direction.y > 0.0 ? 1.0 : 4.0;\n\t\t} else {\n\t\t\tif ( absDirection.z > absDirection.y )\n\t\t\t\tface = direction.z > 0.0 ? 2.0 : 5.0;\n\t\t\telse\n\t\t\t\tface = direction.y > 0.0 ? 1.0 : 4.0;\n\t\t}\n\t\treturn face;\n\t}\n\tvec2 getUV( vec3 direction, float face ) {\n\t\tvec2 uv;\n\t\tif ( face == 0.0 ) {\n\t\t\tuv = vec2( direction.z, direction.y ) / abs( direction.x );\n\t\t} else if ( face == 1.0 ) {\n\t\t\tuv = vec2( - direction.x, - direction.z ) / abs( direction.y );\n\t\t} else if ( face == 2.0 ) {\n\t\t\tuv = vec2( - direction.x, direction.y ) / abs( direction.z );\n\t\t} else if ( face == 3.0 ) {\n\t\t\tuv = vec2( - direction.z, direction.y ) / abs( direction.x );\n\t\t} else if ( face == 4.0 ) {\n\t\t\tuv = vec2( - direction.x, direction.z ) / abs( direction.y );\n\t\t} else {\n\t\t\tuv = vec2( direction.x, direction.y ) / abs( direction.z );\n\t\t}\n\t\treturn 0.5 * ( uv + 1.0 );\n\t}\n\tvec3 bilinearCubeUV( sampler2D envMap, vec3 direction, float mipInt ) {\n\t\tfloat face = getFace( direction );\n\t\tfloat filterInt = max( cubeUV_minMipLevel - mipInt, 0.0 );\n\t\tmipInt = max( mipInt, cubeUV_minMipLevel );\n\t\tfloat faceSize = exp2( mipInt );\n\t\thighp vec2 uv = getUV( direction, face ) * ( faceSize - 2.0 ) + 1.0;\n\t\tif ( face > 2.0 ) {\n\t\t\tuv.y += faceSize;\n\t\t\tface -= 3.0;\n\t\t}\n\t\tuv.x += face * faceSize;\n\t\tuv.x += filterInt * 3.0 * cubeUV_minTileSize;\n\t\tuv.y += 4.0 * ( exp2( CUBEUV_MAX_MIP ) - faceSize );\n\t\tuv.x *= CUBEUV_TEXEL_WIDTH;\n\t\tuv.y *= CUBEUV_TEXEL_HEIGHT;\n\t\t#ifdef texture2DGradEXT\n\t\t\treturn texture2DGradEXT( envMap, uv, vec2( 0.0 ), vec2( 0.0 ) ).rgb;\n\t\t#else\n\t\t\treturn texture2D( envMap, uv ).rgb;\n\t\t#endif\n\t}\n\t#define cubeUV_r0 1.0\n\t#define cubeUV_m0 - 2.0\n\t#define cubeUV_r1 0.8\n\t#define cubeUV_m1 - 1.0\n\t#define cubeUV_r4 0.4\n\t#define cubeUV_m4 2.0\n\t#define cubeUV_r5 0.305\n\t#define cubeUV_m5 3.0\n\t#define cubeUV_r6 0.21\n\t#define cubeUV_m6 4.0\n\tfloat roughnessToMip( float roughness ) {\n\t\tfloat mip = 0.0;\n\t\tif ( roughness >= cubeUV_r1 ) {\n\t\t\tmip = ( cubeUV_r0 - roughness ) * ( cubeUV_m1 - cubeUV_m0 ) / ( cubeUV_r0 - cubeUV_r1 ) + cubeUV_m0;\n\t\t} else if ( roughness >= cubeUV_r4 ) {\n\t\t\tmip = ( cubeUV_r1 - roughness ) * ( cubeUV_m4 - cubeUV_m1 ) / ( cubeUV_r1 - cubeUV_r4 ) + cubeUV_m1;\n\t\t} else if ( roughness >= cubeUV_r5 ) {\n\t\t\tmip = ( cubeUV_r4 - roughness ) * ( cubeUV_m5 - cubeUV_m4 ) / ( cubeUV_r4 - cubeUV_r5 ) + cubeUV_m4;\n\t\t} else if ( roughness >= cubeUV_r6 ) {\n\t\t\tmip = ( cubeUV_r5 - roughness ) * ( cubeUV_m6 - cubeUV_m5 ) / ( cubeUV_r5 - cubeUV_r6 ) + cubeUV_m5;\n\t\t} else {\n\t\t\tmip = - 2.0 * log2( 1.16 * roughness );\t\t}\n\t\treturn mip;\n\t}\n\tvec4 textureCubeUV( sampler2D envMap, vec3 sampleDir, float roughness ) {\n\t\tfloat mip = clamp( roughnessToMip( roughness ), cubeUV_m0, CUBEUV_MAX_MIP );\n\t\tfloat mipF = fract( mip );\n\t\tfloat mipInt = floor( mip );\n\t\tvec3 color0 = bilinearCubeUV( envMap, sampleDir, mipInt );\n\t\tif ( mipF == 0.0 ) {\n\t\t\treturn vec4( color0, 1.0 );\n\t\t} else {\n\t\t\tvec3 color1 = bilinearCubeUV( envMap, sampleDir, mipInt + 1.0 );\n\t\t\treturn vec4( mix( color0, color1, mipF ), 1.0 );\n\t\t}\n\t}\n#endif";var defaultnormal_vertex="vec3 transformedNormal = objectNormal;\n#ifdef USE_TANGENT\n\tvec3 transformedTangent = objectTangent;\n#endif\n#ifdef USE_BATCHING\n\tmat3 bm = mat3( batchingMatrix );\n\ttransformedNormal /= vec3( dot( bm[ 0 ], bm[ 0 ] ), dot( bm[ 1 ], bm[ 1 ] ), dot( bm[ 2 ], bm[ 2 ] ) );\n\ttransformedNormal = bm * transformedNormal;\n\t#ifdef USE_TANGENT\n\t\ttransformedTangent = bm * transformedTangent;\n\t#endif\n#endif\n#ifdef USE_INSTANCING\n\tmat3 im = mat3( instanceMatrix );\n\ttransformedNormal /= vec3( dot( im[ 0 ], im[ 0 ] ), dot( im[ 1 ], im[ 1 ] ), dot( im[ 2 ], im[ 2 ] ) );\n\ttransformedNormal = im * transformedNormal;\n\t#ifdef USE_TANGENT\n\t\ttransformedTangent = im * transformedTangent;\n\t#endif\n#endif\ntransformedNormal = normalMatrix * transformedNormal;\n#ifdef FLIP_SIDED\n\ttransformedNormal = - transformedNormal;\n#endif\n#ifdef USE_TANGENT\n\ttransformedTangent = ( modelViewMatrix * vec4( transformedTangent, 0.0 ) ).xyz;\n\t#ifdef FLIP_SIDED\n\t\ttransformedTangent = - transformedTangent;\n\t#endif\n#endif";var displacementmap_pars_vertex="#ifdef USE_DISPLACEMENTMAP\n\tuniform sampler2D displacementMap;\n\tuniform float displacementScale;\n\tuniform float displacementBias;\n#endif";var displacementmap_vertex="#ifdef USE_DISPLACEMENTMAP\n\ttransformed += normalize( objectNormal ) * ( texture2D( displacementMap, vDisplacementMapUv ).x * displacementScale + displacementBias );\n#endif";var emissivemap_fragment="#ifdef USE_EMISSIVEMAP\n\tvec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );\n\ttotalEmissiveRadiance *= emissiveColor.rgb;\n#endif";var emissivemap_pars_fragment="#ifdef USE_EMISSIVEMAP\n\tuniform sampler2D emissiveMap;\n#endif";var colorspace_fragment="gl_FragColor = linearToOutputTexel( gl_FragColor );";var colorspace_pars_fragment="\nconst mat3 LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = mat3(\n\tvec3( 0.8224621, 0.177538, 0.0 ),\n\tvec3( 0.0331941, 0.9668058, 0.0 ),\n\tvec3( 0.0170827, 0.0723974, 0.9105199 )\n);\nconst mat3 LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = mat3(\n\tvec3( 1.2249401, - 0.2249404, 0.0 ),\n\tvec3( - 0.0420569, 1.0420571, 0.0 ),\n\tvec3( - 0.0196376, - 0.0786361, 1.0982735 )\n);\nvec4 LinearSRGBToLinearDisplayP3( in vec4 value ) {\n\treturn vec4( value.rgb * LINEAR_SRGB_TO_LINEAR_DISPLAY_P3, value.a );\n}\nvec4 LinearDisplayP3ToLinearSRGB( in vec4 value ) {\n\treturn vec4( value.rgb * LINEAR_DISPLAY_P3_TO_LINEAR_SRGB, value.a );\n}\nvec4 LinearTransferOETF( in vec4 value ) {\n\treturn value;\n}\nvec4 sRGBTransferOETF( in vec4 value ) {\n\treturn vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );\n}";var envmap_fragment="#ifdef USE_ENVMAP\n\t#ifdef ENV_WORLDPOS\n\t\tvec3 cameraToFrag;\n\t\tif ( isOrthographic ) {\n\t\t\tcameraToFrag = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );\n\t\t} else {\n\t\t\tcameraToFrag = normalize( vWorldPosition - cameraPosition );\n\t\t}\n\t\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\t\t#ifdef ENVMAP_MODE_REFLECTION\n\t\t\tvec3 reflectVec = reflect( cameraToFrag, worldNormal );\n\t\t#else\n\t\t\tvec3 reflectVec = refract( cameraToFrag, worldNormal, refractionRatio );\n\t\t#endif\n\t#else\n\t\tvec3 reflectVec = vReflect;\n\t#endif\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tvec4 envColor = textureCube( envMap, envMapRotation * vec3( flipEnvMap * reflectVec.x, reflectVec.yz ) );\n\t#else\n\t\tvec4 envColor = vec4( 0.0 );\n\t#endif\n\t#ifdef ENVMAP_BLENDING_MULTIPLY\n\t\toutgoingLight = mix( outgoingLight, outgoingLight * envColor.xyz, specularStrength * reflectivity );\n\t#elif defined( ENVMAP_BLENDING_MIX )\n\t\toutgoingLight = mix( outgoingLight, envColor.xyz, specularStrength * reflectivity );\n\t#elif defined( ENVMAP_BLENDING_ADD )\n\t\toutgoingLight += envColor.xyz * specularStrength * reflectivity;\n\t#endif\n#endif";var envmap_common_pars_fragment="#ifdef USE_ENVMAP\n\tuniform float envMapIntensity;\n\tuniform float flipEnvMap;\n\tuniform mat3 envMapRotation;\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tuniform samplerCube envMap;\n\t#else\n\t\tuniform sampler2D envMap;\n\t#endif\n\t\n#endif";var envmap_pars_fragment="#ifdef USE_ENVMAP\n\tuniform float reflectivity;\n\t#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )\n\t\t#define ENV_WORLDPOS\n\t#endif\n\t#ifdef ENV_WORLDPOS\n\t\tvarying vec3 vWorldPosition;\n\t\tuniform float refractionRatio;\n\t#else\n\t\tvarying vec3 vReflect;\n\t#endif\n#endif";var envmap_pars_vertex="#ifdef USE_ENVMAP\n\t#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )\n\t\t#define ENV_WORLDPOS\n\t#endif\n\t#ifdef ENV_WORLDPOS\n\t\t\n\t\tvarying vec3 vWorldPosition;\n\t#else\n\t\tvarying vec3 vReflect;\n\t\tuniform float refractionRatio;\n\t#endif\n#endif";var envmap_vertex="#ifdef USE_ENVMAP\n\t#ifdef ENV_WORLDPOS\n\t\tvWorldPosition = worldPosition.xyz;\n\t#else\n\t\tvec3 cameraToVertex;\n\t\tif ( isOrthographic ) {\n\t\t\tcameraToVertex = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );\n\t\t} else {\n\t\t\tcameraToVertex = normalize( worldPosition.xyz - cameraPosition );\n\t\t}\n\t\tvec3 worldNormal = inverseTransformDirection( transformedNormal, viewMatrix );\n\t\t#ifdef ENVMAP_MODE_REFLECTION\n\t\t\tvReflect = reflect( cameraToVertex, worldNormal );\n\t\t#else\n\t\t\tvReflect = refract( cameraToVertex, worldNormal, refractionRatio );\n\t\t#endif\n\t#endif\n#endif";var fog_vertex="#ifdef USE_FOG\n\tvFogDepth = - mvPosition.z;\n#endif";var fog_pars_vertex="#ifdef USE_FOG\n\tvarying float vFogDepth;\n#endif";var fog_fragment="#ifdef USE_FOG\n\t#ifdef FOG_EXP2\n\t\tfloat fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );\n\t#else\n\t\tfloat fogFactor = smoothstep( fogNear, fogFar, vFogDepth );\n\t#endif\n\tgl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );\n#endif";var fog_pars_fragment="#ifdef USE_FOG\n\tuniform vec3 fogColor;\n\tvarying float vFogDepth;\n\t#ifdef FOG_EXP2\n\t\tuniform float fogDensity;\n\t#else\n\t\tuniform float fogNear;\n\t\tuniform float fogFar;\n\t#endif\n#endif";var gradientmap_pars_fragment="#ifdef USE_GRADIENTMAP\n\tuniform sampler2D gradientMap;\n#endif\nvec3 getGradientIrradiance( vec3 normal, vec3 lightDirection ) {\n\tfloat dotNL = dot( normal, lightDirection );\n\tvec2 coord = vec2( dotNL * 0.5 + 0.5, 0.0 );\n\t#ifdef USE_GRADIENTMAP\n\t\treturn vec3( texture2D( gradientMap, coord ).r );\n\t#else\n\t\tvec2 fw = fwidth( coord ) * 0.5;\n\t\treturn mix( vec3( 0.7 ), vec3( 1.0 ), smoothstep( 0.7 - fw.x, 0.7 + fw.x, coord.x ) );\n\t#endif\n}";var lightmap_pars_fragment="#ifdef USE_LIGHTMAP\n\tuniform sampler2D lightMap;\n\tuniform float lightMapIntensity;\n#endif";var lights_lambert_fragment="LambertMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;\nmaterial.specularStrength = specularStrength;";var lights_lambert_pars_fragment="varying vec3 vViewPosition;\nstruct LambertMaterial {\n\tvec3 diffuseColor;\n\tfloat specularStrength;\n};\nvoid RE_Direct_Lambert( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Lambert( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_Lambert\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Lambert";var lights_pars_begin="uniform bool receiveShadow;\nuniform vec3 ambientLightColor;\n#if defined( USE_LIGHT_PROBES )\n\tuniform vec3 lightProbe[ 9 ];\n#endif\nvec3 shGetIrradianceAt( in vec3 normal, in vec3 shCoefficients[ 9 ] ) {\n\tfloat x = normal.x, y = normal.y, z = normal.z;\n\tvec3 result = shCoefficients[ 0 ] * 0.886227;\n\tresult += shCoefficients[ 1 ] * 2.0 * 0.511664 * y;\n\tresult += shCoefficients[ 2 ] * 2.0 * 0.511664 * z;\n\tresult += shCoefficients[ 3 ] * 2.0 * 0.511664 * x;\n\tresult += shCoefficients[ 4 ] * 2.0 * 0.429043 * x * y;\n\tresult += shCoefficients[ 5 ] * 2.0 * 0.429043 * y * z;\n\tresult += shCoefficients[ 6 ] * ( 0.743125 * z * z - 0.247708 );\n\tresult += shCoefficients[ 7 ] * 2.0 * 0.429043 * x * z;\n\tresult += shCoefficients[ 8 ] * 0.429043 * ( x * x - y * y );\n\treturn result;\n}\nvec3 getLightProbeIrradiance( const in vec3 lightProbe[ 9 ], const in vec3 normal ) {\n\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\tvec3 irradiance = shGetIrradianceAt( worldNormal, lightProbe );\n\treturn irradiance;\n}\nvec3 getAmbientLightIrradiance( const in vec3 ambientLightColor ) {\n\tvec3 irradiance = ambientLightColor;\n\treturn irradiance;\n}\nfloat getDistanceAttenuation( const in float lightDistance, const in float cutoffDistance, const in float decayExponent ) {\n\tfloat distanceFalloff = 1.0 / max( pow( lightDistance, decayExponent ), 0.01 );\n\tif ( cutoffDistance > 0.0 ) {\n\t\tdistanceFalloff *= pow2( saturate( 1.0 - pow4( lightDistance / cutoffDistance ) ) );\n\t}\n\treturn distanceFalloff;\n}\nfloat getSpotAttenuation( const in float coneCosine, const in float penumbraCosine, const in float angleCosine ) {\n\treturn smoothstep( coneCosine, penumbraCosine, angleCosine );\n}\n#if NUM_DIR_LIGHTS > 0\n\tstruct DirectionalLight {\n\t\tvec3 direction;\n\t\tvec3 color;\n\t};\n\tuniform DirectionalLight directionalLights[ NUM_DIR_LIGHTS ];\n\tvoid getDirectionalLightInfo( const in DirectionalLight directionalLight, out IncidentLight light ) {\n\t\tlight.color = directionalLight.color;\n\t\tlight.direction = directionalLight.direction;\n\t\tlight.visible = true;\n\t}\n#endif\n#if NUM_POINT_LIGHTS > 0\n\tstruct PointLight {\n\t\tvec3 position;\n\t\tvec3 color;\n\t\tfloat distance;\n\t\tfloat decay;\n\t};\n\tuniform PointLight pointLights[ NUM_POINT_LIGHTS ];\n\tvoid getPointLightInfo( const in PointLight pointLight, const in vec3 geometryPosition, out IncidentLight light ) {\n\t\tvec3 lVector = pointLight.position - geometryPosition;\n\t\tlight.direction = normalize( lVector );\n\t\tfloat lightDistance = length( lVector );\n\t\tlight.color = pointLight.color;\n\t\tlight.color *= getDistanceAttenuation( lightDistance, pointLight.distance, pointLight.decay );\n\t\tlight.visible = ( light.color != vec3( 0.0 ) );\n\t}\n#endif\n#if NUM_SPOT_LIGHTS > 0\n\tstruct SpotLight {\n\t\tvec3 position;\n\t\tvec3 direction;\n\t\tvec3 color;\n\t\tfloat distance;\n\t\tfloat decay;\n\t\tfloat coneCos;\n\t\tfloat penumbraCos;\n\t};\n\tuniform SpotLight spotLights[ NUM_SPOT_LIGHTS ];\n\tvoid getSpotLightInfo( const in SpotLight spotLight, const in vec3 geometryPosition, out IncidentLight light ) {\n\t\tvec3 lVector = spotLight.position - geometryPosition;\n\t\tlight.direction = normalize( lVector );\n\t\tfloat angleCos = dot( light.direction, spotLight.direction );\n\t\tfloat spotAttenuation = getSpotAttenuation( spotLight.coneCos, spotLight.penumbraCos, angleCos );\n\t\tif ( spotAttenuation > 0.0 ) {\n\t\t\tfloat lightDistance = length( lVector );\n\t\t\tlight.color = spotLight.color * spotAttenuation;\n\t\t\tlight.color *= getDistanceAttenuation( lightDistance, spotLight.distance, spotLight.decay );\n\t\t\tlight.visible = ( light.color != vec3( 0.0 ) );\n\t\t} else {\n\t\t\tlight.color = vec3( 0.0 );\n\t\t\tlight.visible = false;\n\t\t}\n\t}\n#endif\n#if NUM_RECT_AREA_LIGHTS > 0\n\tstruct RectAreaLight {\n\t\tvec3 color;\n\t\tvec3 position;\n\t\tvec3 halfWidth;\n\t\tvec3 halfHeight;\n\t};\n\tuniform sampler2D ltc_1;\tuniform sampler2D ltc_2;\n\tuniform RectAreaLight rectAreaLights[ NUM_RECT_AREA_LIGHTS ];\n#endif\n#if NUM_HEMI_LIGHTS > 0\n\tstruct HemisphereLight {\n\t\tvec3 direction;\n\t\tvec3 skyColor;\n\t\tvec3 groundColor;\n\t};\n\tuniform HemisphereLight hemisphereLights[ NUM_HEMI_LIGHTS ];\n\tvec3 getHemisphereLightIrradiance( const in HemisphereLight hemiLight, const in vec3 normal ) {\n\t\tfloat dotNL = dot( normal, hemiLight.direction );\n\t\tfloat hemiDiffuseWeight = 0.5 * dotNL + 0.5;\n\t\tvec3 irradiance = mix( hemiLight.groundColor, hemiLight.skyColor, hemiDiffuseWeight );\n\t\treturn irradiance;\n\t}\n#endif";var envmap_physical_pars_fragment="#ifdef USE_ENVMAP\n\tvec3 getIBLIrradiance( const in vec3 normal ) {\n\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\t\t\tvec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, 1.0 );\n\t\t\treturn PI * envMapColor.rgb * envMapIntensity;\n\t\t#else\n\t\t\treturn vec3( 0.0 );\n\t\t#endif\n\t}\n\tvec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {\n\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\tvec3 reflectVec = reflect( - viewDir, normal );\n\t\t\treflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );\n\t\t\treflectVec = inverseTransformDirection( reflectVec, viewMatrix );\n\t\t\tvec4 envMapColor = textureCubeUV( envMap, envMapRotation * reflectVec, roughness );\n\t\t\treturn envMapColor.rgb * envMapIntensity;\n\t\t#else\n\t\t\treturn vec3( 0.0 );\n\t\t#endif\n\t}\n\t#ifdef USE_ANISOTROPY\n\t\tvec3 getIBLAnisotropyRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness, const in vec3 bitangent, const in float anisotropy ) {\n\t\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\t\tvec3 bentNormal = cross( bitangent, viewDir );\n\t\t\t\tbentNormal = normalize( cross( bentNormal, bitangent ) );\n\t\t\t\tbentNormal = normalize( mix( bentNormal, normal, pow2( pow2( 1.0 - anisotropy * ( 1.0 - roughness ) ) ) ) );\n\t\t\t\treturn getIBLRadiance( viewDir, bentNormal, roughness );\n\t\t\t#else\n\t\t\t\treturn vec3( 0.0 );\n\t\t\t#endif\n\t\t}\n\t#endif\n#endif";var lights_toon_fragment="ToonMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;";var lights_toon_pars_fragment="varying vec3 vViewPosition;\nstruct ToonMaterial {\n\tvec3 diffuseColor;\n};\nvoid RE_Direct_Toon( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {\n\tvec3 irradiance = getGradientIrradiance( geometryNormal, directLight.direction ) * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Toon( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_Toon\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Toon";var lights_phong_fragment="BlinnPhongMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;\nmaterial.specularColor = specular;\nmaterial.specularShininess = shininess;\nmaterial.specularStrength = specularStrength;";var lights_phong_pars_fragment="varying vec3 vViewPosition;\nstruct BlinnPhongMaterial {\n\tvec3 diffuseColor;\n\tvec3 specularColor;\n\tfloat specularShininess;\n\tfloat specularStrength;\n};\nvoid RE_Direct_BlinnPhong( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n\treflectedLight.directSpecular += irradiance * BRDF_BlinnPhong( directLight.direction, geometryViewDir, geometryNormal, material.specularColor, material.specularShininess ) * material.specularStrength;\n}\nvoid RE_IndirectDiffuse_BlinnPhong( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_BlinnPhong\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_BlinnPhong";var lights_physical_fragment="PhysicalMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb * ( 1.0 - metalnessFactor );\nvec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );\nfloat geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );\nmaterial.roughness = max( roughnessFactor, 0.0525 );material.roughness += geometryRoughness;\nmaterial.roughness = min( material.roughness, 1.0 );\n#ifdef IOR\n\tmaterial.ior = ior;\n\t#ifdef USE_SPECULAR\n\t\tfloat specularIntensityFactor = specularIntensity;\n\t\tvec3 specularColorFactor = specularColor;\n\t\t#ifdef USE_SPECULAR_COLORMAP\n\t\t\tspecularColorFactor *= texture2D( specularColorMap, vSpecularColorMapUv ).rgb;\n\t\t#endif\n\t\t#ifdef USE_SPECULAR_INTENSITYMAP\n\t\t\tspecularIntensityFactor *= texture2D( specularIntensityMap, vSpecularIntensityMapUv ).a;\n\t\t#endif\n\t\tmaterial.specularF90 = mix( specularIntensityFactor, 1.0, metalnessFactor );\n\t#else\n\t\tfloat specularIntensityFactor = 1.0;\n\t\tvec3 specularColorFactor = vec3( 1.0 );\n\t\tmaterial.specularF90 = 1.0;\n\t#endif\n\tmaterial.specularColor = mix( min( pow2( ( material.ior - 1.0 ) / ( material.ior + 1.0 ) ) * specularColorFactor, vec3( 1.0 ) ) * specularIntensityFactor, diffuseColor.rgb, metalnessFactor );\n#else\n\tmaterial.specularColor = mix( vec3( 0.04 ), diffuseColor.rgb, metalnessFactor );\n\tmaterial.specularF90 = 1.0;\n#endif\n#ifdef USE_CLEARCOAT\n\tmaterial.clearcoat = clearcoat;\n\tmaterial.clearcoatRoughness = clearcoatRoughness;\n\tmaterial.clearcoatF0 = vec3( 0.04 );\n\tmaterial.clearcoatF90 = 1.0;\n\t#ifdef USE_CLEARCOATMAP\n\t\tmaterial.clearcoat *= texture2D( clearcoatMap, vClearcoatMapUv ).x;\n\t#endif\n\t#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\t\tmaterial.clearcoatRoughness *= texture2D( clearcoatRoughnessMap, vClearcoatRoughnessMapUv ).y;\n\t#endif\n\tmaterial.clearcoat = saturate( material.clearcoat );\tmaterial.clearcoatRoughness = max( material.clearcoatRoughness, 0.0525 );\n\tmaterial.clearcoatRoughness += geometryRoughness;\n\tmaterial.clearcoatRoughness = min( material.clearcoatRoughness, 1.0 );\n#endif\n#ifdef USE_DISPERSION\n\tmaterial.dispersion = dispersion;\n#endif\n#ifdef USE_IRIDESCENCE\n\tmaterial.iridescence = iridescence;\n\tmaterial.iridescenceIOR = iridescenceIOR;\n\t#ifdef USE_IRIDESCENCEMAP\n\t\tmaterial.iridescence *= texture2D( iridescenceMap, vIridescenceMapUv ).r;\n\t#endif\n\t#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\t\tmaterial.iridescenceThickness = (iridescenceThicknessMaximum - iridescenceThicknessMinimum) * texture2D( iridescenceThicknessMap, vIridescenceThicknessMapUv ).g + iridescenceThicknessMinimum;\n\t#else\n\t\tmaterial.iridescenceThickness = iridescenceThicknessMaximum;\n\t#endif\n#endif\n#ifdef USE_SHEEN\n\tmaterial.sheenColor = sheenColor;\n\t#ifdef USE_SHEEN_COLORMAP\n\t\tmaterial.sheenColor *= texture2D( sheenColorMap, vSheenColorMapUv ).rgb;\n\t#endif\n\tmaterial.sheenRoughness = clamp( sheenRoughness, 0.07, 1.0 );\n\t#ifdef USE_SHEEN_ROUGHNESSMAP\n\t\tmaterial.sheenRoughness *= texture2D( sheenRoughnessMap, vSheenRoughnessMapUv ).a;\n\t#endif\n#endif\n#ifdef USE_ANISOTROPY\n\t#ifdef USE_ANISOTROPYMAP\n\t\tmat2 anisotropyMat = mat2( anisotropyVector.x, anisotropyVector.y, - anisotropyVector.y, anisotropyVector.x );\n\t\tvec3 anisotropyPolar = texture2D( anisotropyMap, vAnisotropyMapUv ).rgb;\n\t\tvec2 anisotropyV = anisotropyMat * normalize( 2.0 * anisotropyPolar.rg - vec2( 1.0 ) ) * anisotropyPolar.b;\n\t#else\n\t\tvec2 anisotropyV = anisotropyVector;\n\t#endif\n\tmaterial.anisotropy = length( anisotropyV );\n\tif( material.anisotropy == 0.0 ) {\n\t\tanisotropyV = vec2( 1.0, 0.0 );\n\t} else {\n\t\tanisotropyV /= material.anisotropy;\n\t\tmaterial.anisotropy = saturate( material.anisotropy );\n\t}\n\tmaterial.alphaT = mix( pow2( material.roughness ), 1.0, pow2( material.anisotropy ) );\n\tmaterial.anisotropyT = tbn[ 0 ] * anisotropyV.x + tbn[ 1 ] * anisotropyV.y;\n\tmaterial.anisotropyB = tbn[ 1 ] * anisotropyV.x - tbn[ 0 ] * anisotropyV.y;\n#endif";var lights_physical_pars_fragment="struct PhysicalMaterial {\n\tvec3 diffuseColor;\n\tfloat roughness;\n\tvec3 specularColor;\n\tfloat specularF90;\n\tfloat dispersion;\n\t#ifdef USE_CLEARCOAT\n\t\tfloat clearcoat;\n\t\tfloat clearcoatRoughness;\n\t\tvec3 clearcoatF0;\n\t\tfloat clearcoatF90;\n\t#endif\n\t#ifdef USE_IRIDESCENCE\n\t\tfloat iridescence;\n\t\tfloat iridescenceIOR;\n\t\tfloat iridescenceThickness;\n\t\tvec3 iridescenceFresnel;\n\t\tvec3 iridescenceF0;\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tvec3 sheenColor;\n\t\tfloat sheenRoughness;\n\t#endif\n\t#ifdef IOR\n\t\tfloat ior;\n\t#endif\n\t#ifdef USE_TRANSMISSION\n\t\tfloat transmission;\n\t\tfloat transmissionAlpha;\n\t\tfloat thickness;\n\t\tfloat attenuationDistance;\n\t\tvec3 attenuationColor;\n\t#endif\n\t#ifdef USE_ANISOTROPY\n\t\tfloat anisotropy;\n\t\tfloat alphaT;\n\t\tvec3 anisotropyT;\n\t\tvec3 anisotropyB;\n\t#endif\n};\nvec3 clearcoatSpecularDirect = vec3( 0.0 );\nvec3 clearcoatSpecularIndirect = vec3( 0.0 );\nvec3 sheenSpecularDirect = vec3( 0.0 );\nvec3 sheenSpecularIndirect = vec3(0.0 );\nvec3 Schlick_to_F0( const in vec3 f, const in float f90, const in float dotVH ) {\n    float x = clamp( 1.0 - dotVH, 0.0, 1.0 );\n    float x2 = x * x;\n    float x5 = clamp( x * x2 * x2, 0.0, 0.9999 );\n    return ( f - vec3( f90 ) * x5 ) / ( 1.0 - x5 );\n}\nfloat V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {\n\tfloat a2 = pow2( alpha );\n\tfloat gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );\n\tfloat gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );\n\treturn 0.5 / max( gv + gl, EPSILON );\n}\nfloat D_GGX( const in float alpha, const in float dotNH ) {\n\tfloat a2 = pow2( alpha );\n\tfloat denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0;\n\treturn RECIPROCAL_PI * a2 / pow2( denom );\n}\n#ifdef USE_ANISOTROPY\n\tfloat V_GGX_SmithCorrelated_Anisotropic( const in float alphaT, const in float alphaB, const in float dotTV, const in float dotBV, const in float dotTL, const in float dotBL, const in float dotNV, const in float dotNL ) {\n\t\tfloat gv = dotNL * length( vec3( alphaT * dotTV, alphaB * dotBV, dotNV ) );\n\t\tfloat gl = dotNV * length( vec3( alphaT * dotTL, alphaB * dotBL, dotNL ) );\n\t\tfloat v = 0.5 / ( gv + gl );\n\t\treturn saturate(v);\n\t}\n\tfloat D_GGX_Anisotropic( const in float alphaT, const in float alphaB, const in float dotNH, const in float dotTH, const in float dotBH ) {\n\t\tfloat a2 = alphaT * alphaB;\n\t\thighp vec3 v = vec3( alphaB * dotTH, alphaT * dotBH, a2 * dotNH );\n\t\thighp float v2 = dot( v, v );\n\t\tfloat w2 = a2 / v2;\n\t\treturn RECIPROCAL_PI * a2 * pow2 ( w2 );\n\t}\n#endif\n#ifdef USE_CLEARCOAT\n\tvec3 BRDF_GGX_Clearcoat( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material) {\n\t\tvec3 f0 = material.clearcoatF0;\n\t\tfloat f90 = material.clearcoatF90;\n\t\tfloat roughness = material.clearcoatRoughness;\n\t\tfloat alpha = pow2( roughness );\n\t\tvec3 halfDir = normalize( lightDir + viewDir );\n\t\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\t\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\t\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\t\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\t\tvec3 F = F_Schlick( f0, f90, dotVH );\n\t\tfloat V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );\n\t\tfloat D = D_GGX( alpha, dotNH );\n\t\treturn F * ( V * D );\n\t}\n#endif\nvec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material ) {\n\tvec3 f0 = material.specularColor;\n\tfloat f90 = material.specularF90;\n\tfloat roughness = material.roughness;\n\tfloat alpha = pow2( roughness );\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\tvec3 F = F_Schlick( f0, f90, dotVH );\n\t#ifdef USE_IRIDESCENCE\n\t\tF = mix( F, material.iridescenceFresnel, material.iridescence );\n\t#endif\n\t#ifdef USE_ANISOTROPY\n\t\tfloat dotTL = dot( material.anisotropyT, lightDir );\n\t\tfloat dotTV = dot( material.anisotropyT, viewDir );\n\t\tfloat dotTH = dot( material.anisotropyT, halfDir );\n\t\tfloat dotBL = dot( material.anisotropyB, lightDir );\n\t\tfloat dotBV = dot( material.anisotropyB, viewDir );\n\t\tfloat dotBH = dot( material.anisotropyB, halfDir );\n\t\tfloat V = V_GGX_SmithCorrelated_Anisotropic( material.alphaT, alpha, dotTV, dotBV, dotTL, dotBL, dotNV, dotNL );\n\t\tfloat D = D_GGX_Anisotropic( material.alphaT, alpha, dotNH, dotTH, dotBH );\n\t#else\n\t\tfloat V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );\n\t\tfloat D = D_GGX( alpha, dotNH );\n\t#endif\n\treturn F * ( V * D );\n}\nvec2 LTC_Uv( const in vec3 N, const in vec3 V, const in float roughness ) {\n\tconst float LUT_SIZE = 64.0;\n\tconst float LUT_SCALE = ( LUT_SIZE - 1.0 ) / LUT_SIZE;\n\tconst float LUT_BIAS = 0.5 / LUT_SIZE;\n\tfloat dotNV = saturate( dot( N, V ) );\n\tvec2 uv = vec2( roughness, sqrt( 1.0 - dotNV ) );\n\tuv = uv * LUT_SCALE + LUT_BIAS;\n\treturn uv;\n}\nfloat LTC_ClippedSphereFormFactor( const in vec3 f ) {\n\tfloat l = length( f );\n\treturn max( ( l * l + f.z ) / ( l + 1.0 ), 0.0 );\n}\nvec3 LTC_EdgeVectorFormFactor( const in vec3 v1, const in vec3 v2 ) {\n\tfloat x = dot( v1, v2 );\n\tfloat y = abs( x );\n\tfloat a = 0.8543985 + ( 0.4965155 + 0.0145206 * y ) * y;\n\tfloat b = 3.4175940 + ( 4.1616724 + y ) * y;\n\tfloat v = a / b;\n\tfloat theta_sintheta = ( x > 0.0 ) ? v : 0.5 * inversesqrt( max( 1.0 - x * x, 1e-7 ) ) - v;\n\treturn cross( v1, v2 ) * theta_sintheta;\n}\nvec3 LTC_Evaluate( const in vec3 N, const in vec3 V, const in vec3 P, const in mat3 mInv, const in vec3 rectCoords[ 4 ] ) {\n\tvec3 v1 = rectCoords[ 1 ] - rectCoords[ 0 ];\n\tvec3 v2 = rectCoords[ 3 ] - rectCoords[ 0 ];\n\tvec3 lightNormal = cross( v1, v2 );\n\tif( dot( lightNormal, P - rectCoords[ 0 ] ) < 0.0 ) return vec3( 0.0 );\n\tvec3 T1, T2;\n\tT1 = normalize( V - N * dot( V, N ) );\n\tT2 = - cross( N, T1 );\n\tmat3 mat = mInv * transposeMat3( mat3( T1, T2, N ) );\n\tvec3 coords[ 4 ];\n\tcoords[ 0 ] = mat * ( rectCoords[ 0 ] - P );\n\tcoords[ 1 ] = mat * ( rectCoords[ 1 ] - P );\n\tcoords[ 2 ] = mat * ( rectCoords[ 2 ] - P );\n\tcoords[ 3 ] = mat * ( rectCoords[ 3 ] - P );\n\tcoords[ 0 ] = normalize( coords[ 0 ] );\n\tcoords[ 1 ] = normalize( coords[ 1 ] );\n\tcoords[ 2 ] = normalize( coords[ 2 ] );\n\tcoords[ 3 ] = normalize( coords[ 3 ] );\n\tvec3 vectorFormFactor = vec3( 0.0 );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 0 ], coords[ 1 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 1 ], coords[ 2 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 2 ], coords[ 3 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 3 ], coords[ 0 ] );\n\tfloat result = LTC_ClippedSphereFormFactor( vectorFormFactor );\n\treturn vec3( result );\n}\n#if defined( USE_SHEEN )\nfloat D_Charlie( float roughness, float dotNH ) {\n\tfloat alpha = pow2( roughness );\n\tfloat invAlpha = 1.0 / alpha;\n\tfloat cos2h = dotNH * dotNH;\n\tfloat sin2h = max( 1.0 - cos2h, 0.0078125 );\n\treturn ( 2.0 + invAlpha ) * pow( sin2h, invAlpha * 0.5 ) / ( 2.0 * PI );\n}\nfloat V_Neubelt( float dotNV, float dotNL ) {\n\treturn saturate( 1.0 / ( 4.0 * ( dotNL + dotNV - dotNL * dotNV ) ) );\n}\nvec3 BRDF_Sheen( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, vec3 sheenColor, const in float sheenRoughness ) {\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat D = D_Charlie( sheenRoughness, dotNH );\n\tfloat V = V_Neubelt( dotNV, dotNL );\n\treturn sheenColor * ( D * V );\n}\n#endif\nfloat IBLSheenBRDF( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat r2 = roughness * roughness;\n\tfloat a = roughness < 0.25 ? -339.2 * r2 + 161.4 * roughness - 25.9 : -8.48 * r2 + 14.3 * roughness - 9.95;\n\tfloat b = roughness < 0.25 ? 44.0 * r2 - 23.7 * roughness + 3.26 : 1.97 * r2 - 3.27 * roughness + 0.72;\n\tfloat DG = exp( a * dotNV + b ) + ( roughness < 0.25 ? 0.0 : 0.1 * ( roughness - 0.25 ) );\n\treturn saturate( DG * RECIPROCAL_PI );\n}\nvec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tconst vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );\n\tconst vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );\n\tvec4 r = roughness * c0 + c1;\n\tfloat a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;\n\tvec2 fab = vec2( - 1.04, 1.04 ) * a004 + r.zw;\n\treturn fab;\n}\nvec3 EnvironmentBRDF( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness ) {\n\tvec2 fab = DFGApprox( normal, viewDir, roughness );\n\treturn specularColor * fab.x + specularF90 * fab.y;\n}\n#ifdef USE_IRIDESCENCE\nvoid computeMultiscatteringIridescence( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float iridescence, const in vec3 iridescenceF0, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {\n#else\nvoid computeMultiscattering( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {\n#endif\n\tvec2 fab = DFGApprox( normal, viewDir, roughness );\n\t#ifdef USE_IRIDESCENCE\n\t\tvec3 Fr = mix( specularColor, iridescenceF0, iridescence );\n\t#else\n\t\tvec3 Fr = specularColor;\n\t#endif\n\tvec3 FssEss = Fr * fab.x + specularF90 * fab.y;\n\tfloat Ess = fab.x + fab.y;\n\tfloat Ems = 1.0 - Ess;\n\tvec3 Favg = Fr + ( 1.0 - Fr ) * 0.047619;\tvec3 Fms = FssEss * Favg / ( 1.0 - Ems * Favg );\n\tsingleScatter += FssEss;\n\tmultiScatter += Fms * Ems;\n}\n#if NUM_RECT_AREA_LIGHTS > 0\n\tvoid RE_Direct_RectArea_Physical( const in RectAreaLight rectAreaLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\t\tvec3 normal = geometryNormal;\n\t\tvec3 viewDir = geometryViewDir;\n\t\tvec3 position = geometryPosition;\n\t\tvec3 lightPos = rectAreaLight.position;\n\t\tvec3 halfWidth = rectAreaLight.halfWidth;\n\t\tvec3 halfHeight = rectAreaLight.halfHeight;\n\t\tvec3 lightColor = rectAreaLight.color;\n\t\tfloat roughness = material.roughness;\n\t\tvec3 rectCoords[ 4 ];\n\t\trectCoords[ 0 ] = lightPos + halfWidth - halfHeight;\t\trectCoords[ 1 ] = lightPos - halfWidth - halfHeight;\n\t\trectCoords[ 2 ] = lightPos - halfWidth + halfHeight;\n\t\trectCoords[ 3 ] = lightPos + halfWidth + halfHeight;\n\t\tvec2 uv = LTC_Uv( normal, viewDir, roughness );\n\t\tvec4 t1 = texture2D( ltc_1, uv );\n\t\tvec4 t2 = texture2D( ltc_2, uv );\n\t\tmat3 mInv = mat3(\n\t\t\tvec3( t1.x, 0, t1.y ),\n\t\t\tvec3(    0, 1,    0 ),\n\t\t\tvec3( t1.z, 0, t1.w )\n\t\t);\n\t\tvec3 fresnel = ( material.specularColor * t2.x + ( vec3( 1.0 ) - material.specularColor ) * t2.y );\n\t\treflectedLight.directSpecular += lightColor * fresnel * LTC_Evaluate( normal, viewDir, position, mInv, rectCoords );\n\t\treflectedLight.directDiffuse += lightColor * material.diffuseColor * LTC_Evaluate( normal, viewDir, position, mat3( 1.0 ), rectCoords );\n\t}\n#endif\nvoid RE_Direct_Physical( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\t#ifdef USE_CLEARCOAT\n\t\tfloat dotNLcc = saturate( dot( geometryClearcoatNormal, directLight.direction ) );\n\t\tvec3 ccIrradiance = dotNLcc * directLight.color;\n\t\tclearcoatSpecularDirect += ccIrradiance * BRDF_GGX_Clearcoat( directLight.direction, geometryViewDir, geometryClearcoatNormal, material );\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tsheenSpecularDirect += irradiance * BRDF_Sheen( directLight.direction, geometryViewDir, geometryNormal, material.sheenColor, material.sheenRoughness );\n\t#endif\n\treflectedLight.directSpecular += irradiance * BRDF_GGX( directLight.direction, geometryViewDir, geometryNormal, material );\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Physical( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectSpecular_Physical( const in vec3 radiance, const in vec3 irradiance, const in vec3 clearcoatRadiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {\n\t#ifdef USE_CLEARCOAT\n\t\tclearcoatSpecularIndirect += clearcoatRadiance * EnvironmentBRDF( geometryClearcoatNormal, geometryViewDir, material.clearcoatF0, material.clearcoatF90, material.clearcoatRoughness );\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tsheenSpecularIndirect += irradiance * material.sheenColor * IBLSheenBRDF( geometryNormal, geometryViewDir, material.sheenRoughness );\n\t#endif\n\tvec3 singleScattering = vec3( 0.0 );\n\tvec3 multiScattering = vec3( 0.0 );\n\tvec3 cosineWeightedIrradiance = irradiance * RECIPROCAL_PI;\n\t#ifdef USE_IRIDESCENCE\n\t\tcomputeMultiscatteringIridescence( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.iridescence, material.iridescenceFresnel, material.roughness, singleScattering, multiScattering );\n\t#else\n\t\tcomputeMultiscattering( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.roughness, singleScattering, multiScattering );\n\t#endif\n\tvec3 totalScattering = singleScattering + multiScattering;\n\tvec3 diffuse = material.diffuseColor * ( 1.0 - max( max( totalScattering.r, totalScattering.g ), totalScattering.b ) );\n\treflectedLight.indirectSpecular += radiance * singleScattering;\n\treflectedLight.indirectSpecular += multiScattering * cosineWeightedIrradiance;\n\treflectedLight.indirectDiffuse += diffuse * cosineWeightedIrradiance;\n}\n#define RE_Direct\t\t\t\tRE_Direct_Physical\n#define RE_Direct_RectArea\t\tRE_Direct_RectArea_Physical\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Physical\n#define RE_IndirectSpecular\t\tRE_IndirectSpecular_Physical\nfloat computeSpecularOcclusion( const in float dotNV, const in float ambientOcclusion, const in float roughness ) {\n\treturn saturate( pow( dotNV + ambientOcclusion, exp2( - 16.0 * roughness - 1.0 ) ) - 1.0 + ambientOcclusion );\n}";var lights_fragment_begin="\nvec3 geometryPosition = - vViewPosition;\nvec3 geometryNormal = normal;\nvec3 geometryViewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );\nvec3 geometryClearcoatNormal = vec3( 0.0 );\n#ifdef USE_CLEARCOAT\n\tgeometryClearcoatNormal = clearcoatNormal;\n#endif\n#ifdef USE_IRIDESCENCE\n\tfloat dotNVi = saturate( dot( normal, geometryViewDir ) );\n\tif ( material.iridescenceThickness == 0.0 ) {\n\t\tmaterial.iridescence = 0.0;\n\t} else {\n\t\tmaterial.iridescence = saturate( material.iridescence );\n\t}\n\tif ( material.iridescence > 0.0 ) {\n\t\tmaterial.iridescenceFresnel = evalIridescence( 1.0, material.iridescenceIOR, dotNVi, material.iridescenceThickness, material.specularColor );\n\t\tmaterial.iridescenceF0 = Schlick_to_F0( material.iridescenceFresnel, 1.0, dotNVi );\n\t}\n#endif\nIncidentLight directLight;\n#if ( NUM_POINT_LIGHTS > 0 ) && defined( RE_Direct )\n\tPointLight pointLight;\n\t#if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0\n\tPointLightShadow pointLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {\n\t\tpointLight = pointLights[ i ];\n\t\tgetPointLightInfo( pointLight, geometryPosition, directLight );\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )\n\t\tpointLightShadow = pointLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getPointShadow( pointShadowMap[ i ], pointLightShadow.shadowMapSize, pointLightShadow.shadowIntensity, pointLightShadow.shadowBias, pointLightShadow.shadowRadius, vPointShadowCoord[ i ], pointLightShadow.shadowCameraNear, pointLightShadow.shadowCameraFar ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_SPOT_LIGHTS > 0 ) && defined( RE_Direct )\n\tSpotLight spotLight;\n\tvec4 spotColor;\n\tvec3 spotLightCoord;\n\tbool inSpotLightMap;\n\t#if defined( USE_SHADOWMAP ) && NUM_SPOT_LIGHT_SHADOWS > 0\n\tSpotLightShadow spotLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {\n\t\tspotLight = spotLights[ i ];\n\t\tgetSpotLightInfo( spotLight, geometryPosition, directLight );\n\t\t#if ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )\n\t\t#define SPOT_LIGHT_MAP_INDEX UNROLLED_LOOP_INDEX\n\t\t#elif ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\t#define SPOT_LIGHT_MAP_INDEX NUM_SPOT_LIGHT_MAPS\n\t\t#else\n\t\t#define SPOT_LIGHT_MAP_INDEX ( UNROLLED_LOOP_INDEX - NUM_SPOT_LIGHT_SHADOWS + NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )\n\t\t#endif\n\t\t#if ( SPOT_LIGHT_MAP_INDEX < NUM_SPOT_LIGHT_MAPS )\n\t\t\tspotLightCoord = vSpotLightCoord[ i ].xyz / vSpotLightCoord[ i ].w;\n\t\t\tinSpotLightMap = all( lessThan( abs( spotLightCoord * 2. - 1. ), vec3( 1.0 ) ) );\n\t\t\tspotColor = texture2D( spotLightMap[ SPOT_LIGHT_MAP_INDEX ], spotLightCoord.xy );\n\t\t\tdirectLight.color = inSpotLightMap ? directLight.color * spotColor.rgb : directLight.color;\n\t\t#endif\n\t\t#undef SPOT_LIGHT_MAP_INDEX\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\tspotLightShadow = spotLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( spotShadowMap[ i ], spotLightShadow.shadowMapSize, spotLightShadow.shadowIntensity, spotLightShadow.shadowBias, spotLightShadow.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct )\n\tDirectionalLight directionalLight;\n\t#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0\n\tDirectionalLightShadow directionalLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {\n\t\tdirectionalLight = directionalLights[ i ];\n\t\tgetDirectionalLightInfo( directionalLight, directLight );\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )\n\t\tdirectionalLightShadow = directionalLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_RECT_AREA_LIGHTS > 0 ) && defined( RE_Direct_RectArea )\n\tRectAreaLight rectAreaLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_RECT_AREA_LIGHTS; i ++ ) {\n\t\trectAreaLight = rectAreaLights[ i ];\n\t\tRE_Direct_RectArea( rectAreaLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if defined( RE_IndirectDiffuse )\n\tvec3 iblIrradiance = vec3( 0.0 );\n\tvec3 irradiance = getAmbientLightIrradiance( ambientLightColor );\n\t#if defined( USE_LIGHT_PROBES )\n\t\tirradiance += getLightProbeIrradiance( lightProbe, geometryNormal );\n\t#endif\n\t#if ( NUM_HEMI_LIGHTS > 0 )\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {\n\t\t\tirradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal );\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n#endif\n#if defined( RE_IndirectSpecular )\n\tvec3 radiance = vec3( 0.0 );\n\tvec3 clearcoatRadiance = vec3( 0.0 );\n#endif";var lights_fragment_maps="#if defined( RE_IndirectDiffuse )\n\t#ifdef USE_LIGHTMAP\n\t\tvec4 lightMapTexel = texture2D( lightMap, vLightMapUv );\n\t\tvec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;\n\t\tirradiance += lightMapIrradiance;\n\t#endif\n\t#if defined( USE_ENVMAP ) && defined( STANDARD ) && defined( ENVMAP_TYPE_CUBE_UV )\n\t\tiblIrradiance += getIBLIrradiance( geometryNormal );\n\t#endif\n#endif\n#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )\n\t#ifdef USE_ANISOTROPY\n\t\tradiance += getIBLAnisotropyRadiance( geometryViewDir, geometryNormal, material.roughness, material.anisotropyB, material.anisotropy );\n\t#else\n\t\tradiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );\n\t#endif\n\t#ifdef USE_CLEARCOAT\n\t\tclearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );\n\t#endif\n#endif";var lights_fragment_end="#if defined( RE_IndirectDiffuse )\n\tRE_IndirectDiffuse( irradiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n#endif\n#if defined( RE_IndirectSpecular )\n\tRE_IndirectSpecular( radiance, iblIrradiance, clearcoatRadiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n#endif";var logdepthbuf_fragment="#if defined( USE_LOGDEPTHBUF )\n\tgl_FragDepth = vIsPerspective == 0.0 ? gl_FragCoord.z : log2( vFragDepth ) * logDepthBufFC * 0.5;\n#endif";var logdepthbuf_pars_fragment="#if defined( USE_LOGDEPTHBUF )\n\tuniform float logDepthBufFC;\n\tvarying float vFragDepth;\n\tvarying float vIsPerspective;\n#endif";var logdepthbuf_pars_vertex="#ifdef USE_LOGDEPTHBUF\n\tvarying float vFragDepth;\n\tvarying float vIsPerspective;\n#endif";var logdepthbuf_vertex="#ifdef USE_LOGDEPTHBUF\n\tvFragDepth = 1.0 + gl_Position.w;\n\tvIsPerspective = float( isPerspectiveMatrix( projectionMatrix ) );\n#endif";var map_fragment="#ifdef USE_MAP\n\tvec4 sampledDiffuseColor = texture2D( map, vMapUv );\n\t#ifdef DECODE_VIDEO_TEXTURE\n\t\tsampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );\n\t\n\t#endif\n\tdiffuseColor *= sampledDiffuseColor;\n#endif";var map_pars_fragment="#ifdef USE_MAP\n\tuniform sampler2D map;\n#endif";var map_particle_fragment="#if defined( USE_MAP ) || defined( USE_ALPHAMAP )\n\t#if defined( USE_POINTS_UV )\n\t\tvec2 uv = vUv;\n\t#else\n\t\tvec2 uv = ( uvTransform * vec3( gl_PointCoord.x, 1.0 - gl_PointCoord.y, 1 ) ).xy;\n\t#endif\n#endif\n#ifdef USE_MAP\n\tdiffuseColor *= texture2D( map, uv );\n#endif\n#ifdef USE_ALPHAMAP\n\tdiffuseColor.a *= texture2D( alphaMap, uv ).g;\n#endif";var map_particle_pars_fragment="#if defined( USE_POINTS_UV )\n\tvarying vec2 vUv;\n#else\n\t#if defined( USE_MAP ) || defined( USE_ALPHAMAP )\n\t\tuniform mat3 uvTransform;\n\t#endif\n#endif\n#ifdef USE_MAP\n\tuniform sampler2D map;\n#endif\n#ifdef USE_ALPHAMAP\n\tuniform sampler2D alphaMap;\n#endif";var metalnessmap_fragment="float metalnessFactor = metalness;\n#ifdef USE_METALNESSMAP\n\tvec4 texelMetalness = texture2D( metalnessMap, vMetalnessMapUv );\n\tmetalnessFactor *= texelMetalness.b;\n#endif";var metalnessmap_pars_fragment="#ifdef USE_METALNESSMAP\n\tuniform sampler2D metalnessMap;\n#endif";var morphinstance_vertex="#ifdef USE_INSTANCING_MORPH\n\tfloat morphTargetInfluences[ MORPHTARGETS_COUNT ];\n\tfloat morphTargetBaseInfluence = texelFetch( morphTexture, ivec2( 0, gl_InstanceID ), 0 ).r;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tmorphTargetInfluences[i] =  texelFetch( morphTexture, ivec2( i + 1, gl_InstanceID ), 0 ).r;\n\t}\n#endif";var morphcolor_vertex="#if defined( USE_MORPHCOLORS )\n\tvColor *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\t#if defined( USE_COLOR_ALPHA )\n\t\t\tif ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ) * morphTargetInfluences[ i ];\n\t\t#elif defined( USE_COLOR )\n\t\t\tif ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ).rgb * morphTargetInfluences[ i ];\n\t\t#endif\n\t}\n#endif";var morphnormal_vertex="#ifdef USE_MORPHNORMALS\n\tobjectNormal *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tif ( morphTargetInfluences[ i ] != 0.0 ) objectNormal += getMorph( gl_VertexID, i, 1 ).xyz * morphTargetInfluences[ i ];\n\t}\n#endif";var morphtarget_pars_vertex="#ifdef USE_MORPHTARGETS\n\t#ifndef USE_INSTANCING_MORPH\n\t\tuniform float morphTargetBaseInfluence;\n\t\tuniform float morphTargetInfluences[ MORPHTARGETS_COUNT ];\n\t#endif\n\tuniform sampler2DArray morphTargetsTexture;\n\tuniform ivec2 morphTargetsTextureSize;\n\tvec4 getMorph( const in int vertexIndex, const in int morphTargetIndex, const in int offset ) {\n\t\tint texelIndex = vertexIndex * MORPHTARGETS_TEXTURE_STRIDE + offset;\n\t\tint y = texelIndex / morphTargetsTextureSize.x;\n\t\tint x = texelIndex - y * morphTargetsTextureSize.x;\n\t\tivec3 morphUV = ivec3( x, y, morphTargetIndex );\n\t\treturn texelFetch( morphTargetsTexture, morphUV, 0 );\n\t}\n#endif";var morphtarget_vertex="#ifdef USE_MORPHTARGETS\n\ttransformed *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tif ( morphTargetInfluences[ i ] != 0.0 ) transformed += getMorph( gl_VertexID, i, 0 ).xyz * morphTargetInfluences[ i ];\n\t}\n#endif";var normal_fragment_begin="float faceDirection = gl_FrontFacing ? 1.0 : - 1.0;\n#ifdef FLAT_SHADED\n\tvec3 fdx = dFdx( vViewPosition );\n\tvec3 fdy = dFdy( vViewPosition );\n\tvec3 normal = normalize( cross( fdx, fdy ) );\n#else\n\tvec3 normal = normalize( vNormal );\n\t#ifdef DOUBLE_SIDED\n\t\tnormal *= faceDirection;\n\t#endif\n#endif\n#if defined( USE_NORMALMAP_TANGENTSPACE ) || defined( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY )\n\t#ifdef USE_TANGENT\n\t\tmat3 tbn = mat3( normalize( vTangent ), normalize( vBitangent ), normal );\n\t#else\n\t\tmat3 tbn = getTangentFrame( - vViewPosition, normal,\n\t\t#if defined( USE_NORMALMAP )\n\t\t\tvNormalMapUv\n\t\t#elif defined( USE_CLEARCOAT_NORMALMAP )\n\t\t\tvClearcoatNormalMapUv\n\t\t#else\n\t\t\tvUv\n\t\t#endif\n\t\t);\n\t#endif\n\t#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )\n\t\ttbn[0] *= faceDirection;\n\t\ttbn[1] *= faceDirection;\n\t#endif\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\t#ifdef USE_TANGENT\n\t\tmat3 tbn2 = mat3( normalize( vTangent ), normalize( vBitangent ), normal );\n\t#else\n\t\tmat3 tbn2 = getTangentFrame( - vViewPosition, normal, vClearcoatNormalMapUv );\n\t#endif\n\t#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )\n\t\ttbn2[0] *= faceDirection;\n\t\ttbn2[1] *= faceDirection;\n\t#endif\n#endif\nvec3 nonPerturbedNormal = normal;";var normal_fragment_maps="#ifdef USE_NORMALMAP_OBJECTSPACE\n\tnormal = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;\n\t#ifdef FLIP_SIDED\n\t\tnormal = - normal;\n\t#endif\n\t#ifdef DOUBLE_SIDED\n\t\tnormal = normal * faceDirection;\n\t#endif\n\tnormal = normalize( normalMatrix * normal );\n#elif defined( USE_NORMALMAP_TANGENTSPACE )\n\tvec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;\n\tmapN.xy *= normalScale;\n\tnormal = normalize( tbn * mapN );\n#elif defined( USE_BUMPMAP )\n\tnormal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );\n#endif";var normal_pars_fragment="#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n\t#ifdef USE_TANGENT\n\t\tvarying vec3 vTangent;\n\t\tvarying vec3 vBitangent;\n\t#endif\n#endif";var normal_pars_vertex="#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n\t#ifdef USE_TANGENT\n\t\tvarying vec3 vTangent;\n\t\tvarying vec3 vBitangent;\n\t#endif\n#endif";var normal_vertex="#ifndef FLAT_SHADED\n\tvNormal = normalize( transformedNormal );\n\t#ifdef USE_TANGENT\n\t\tvTangent = normalize( transformedTangent );\n\t\tvBitangent = normalize( cross( vNormal, vTangent ) * tangent.w );\n\t#endif\n#endif";var normalmap_pars_fragment="#ifdef USE_NORMALMAP\n\tuniform sampler2D normalMap;\n\tuniform vec2 normalScale;\n#endif\n#ifdef USE_NORMALMAP_OBJECTSPACE\n\tuniform mat3 normalMatrix;\n#endif\n#if ! defined ( USE_TANGENT ) && ( defined ( USE_NORMALMAP_TANGENTSPACE ) || defined ( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY ) )\n\tmat3 getTangentFrame( vec3 eye_pos, vec3 surf_norm, vec2 uv ) {\n\t\tvec3 q0 = dFdx( eye_pos.xyz );\n\t\tvec3 q1 = dFdy( eye_pos.xyz );\n\t\tvec2 st0 = dFdx( uv.st );\n\t\tvec2 st1 = dFdy( uv.st );\n\t\tvec3 N = surf_norm;\n\t\tvec3 q1perp = cross( q1, N );\n\t\tvec3 q0perp = cross( N, q0 );\n\t\tvec3 T = q1perp * st0.x + q0perp * st1.x;\n\t\tvec3 B = q1perp * st0.y + q0perp * st1.y;\n\t\tfloat det = max( dot( T, T ), dot( B, B ) );\n\t\tfloat scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );\n\t\treturn mat3( T * scale, B * scale, N );\n\t}\n#endif";var clearcoat_normal_fragment_begin="#ifdef USE_CLEARCOAT\n\tvec3 clearcoatNormal = nonPerturbedNormal;\n#endif";var clearcoat_normal_fragment_maps="#ifdef USE_CLEARCOAT_NORMALMAP\n\tvec3 clearcoatMapN = texture2D( clearcoatNormalMap, vClearcoatNormalMapUv ).xyz * 2.0 - 1.0;\n\tclearcoatMapN.xy *= clearcoatNormalScale;\n\tclearcoatNormal = normalize( tbn2 * clearcoatMapN );\n#endif";var clearcoat_pars_fragment="#ifdef USE_CLEARCOATMAP\n\tuniform sampler2D clearcoatMap;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tuniform sampler2D clearcoatNormalMap;\n\tuniform vec2 clearcoatNormalScale;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tuniform sampler2D clearcoatRoughnessMap;\n#endif";var iridescence_pars_fragment="#ifdef USE_IRIDESCENCEMAP\n\tuniform sampler2D iridescenceMap;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tuniform sampler2D iridescenceThicknessMap;\n#endif";var opaque_fragment="#ifdef OPAQUE\ndiffuseColor.a = 1.0;\n#endif\n#ifdef USE_TRANSMISSION\ndiffuseColor.a *= material.transmissionAlpha;\n#endif\ngl_FragColor = vec4( outgoingLight, diffuseColor.a );";var packing="vec3 packNormalToRGB( const in vec3 normal ) {\n\treturn normalize( normal ) * 0.5 + 0.5;\n}\nvec3 unpackRGBToNormal( const in vec3 rgb ) {\n\treturn 2.0 * rgb.xyz - 1.0;\n}\nconst float PackUpscale = 256. / 255.;const float UnpackDownscale = 255. / 256.;const float ShiftRight8 = 1. / 256.;\nconst float Inv255 = 1. / 255.;\nconst vec4 PackFactors = vec4( 1.0, 256.0, 256.0 * 256.0, 256.0 * 256.0 * 256.0 );\nconst vec2 UnpackFactors2 = vec2( UnpackDownscale, 1.0 / PackFactors.g );\nconst vec3 UnpackFactors3 = vec3( UnpackDownscale / PackFactors.rg, 1.0 / PackFactors.b );\nconst vec4 UnpackFactors4 = vec4( UnpackDownscale / PackFactors.rgb, 1.0 / PackFactors.a );\nvec4 packDepthToRGBA( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec4( 0., 0., 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec4( 1., 1., 1., 1. );\n\tfloat vuf;\n\tfloat af = modf( v * PackFactors.a, vuf );\n\tfloat bf = modf( vuf * ShiftRight8, vuf );\n\tfloat gf = modf( vuf * ShiftRight8, vuf );\n\treturn vec4( vuf * Inv255, gf * PackUpscale, bf * PackUpscale, af );\n}\nvec3 packDepthToRGB( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec3( 0., 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec3( 1., 1., 1. );\n\tfloat vuf;\n\tfloat bf = modf( v * PackFactors.b, vuf );\n\tfloat gf = modf( vuf * ShiftRight8, vuf );\n\treturn vec3( vuf * Inv255, gf * PackUpscale, bf );\n}\nvec2 packDepthToRG( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec2( 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec2( 1., 1. );\n\tfloat vuf;\n\tfloat gf = modf( v * 256., vuf );\n\treturn vec2( vuf * Inv255, gf );\n}\nfloat unpackRGBAToDepth( const in vec4 v ) {\n\treturn dot( v, UnpackFactors4 );\n}\nfloat unpackRGBToDepth( const in vec3 v ) {\n\treturn dot( v, UnpackFactors3 );\n}\nfloat unpackRGToDepth( const in vec2 v ) {\n\treturn v.r * UnpackFactors2.r + v.g * UnpackFactors2.g;\n}\nvec4 pack2HalfToRGBA( const in vec2 v ) {\n\tvec4 r = vec4( v.x, fract( v.x * 255.0 ), v.y, fract( v.y * 255.0 ) );\n\treturn vec4( r.x - r.y / 255.0, r.y, r.z - r.w / 255.0, r.w );\n}\nvec2 unpackRGBATo2Half( const in vec4 v ) {\n\treturn vec2( v.x + ( v.y / 255.0 ), v.z + ( v.w / 255.0 ) );\n}\nfloat viewZToOrthographicDepth( const in float viewZ, const in float near, const in float far ) {\n\treturn ( viewZ + near ) / ( near - far );\n}\nfloat orthographicDepthToViewZ( const in float depth, const in float near, const in float far ) {\n\treturn depth * ( near - far ) - near;\n}\nfloat viewZToPerspectiveDepth( const in float viewZ, const in float near, const in float far ) {\n\treturn ( ( near + viewZ ) * far ) / ( ( far - near ) * viewZ );\n}\nfloat perspectiveDepthToViewZ( const in float depth, const in float near, const in float far ) {\n\treturn ( near * far ) / ( ( far - near ) * depth - far );\n}";var premultiplied_alpha_fragment="#ifdef PREMULTIPLIED_ALPHA\n\tgl_FragColor.rgb *= gl_FragColor.a;\n#endif";var project_vertex="vec4 mvPosition = vec4( transformed, 1.0 );\n#ifdef USE_BATCHING\n\tmvPosition = batchingMatrix * mvPosition;\n#endif\n#ifdef USE_INSTANCING\n\tmvPosition = instanceMatrix * mvPosition;\n#endif\nmvPosition = modelViewMatrix * mvPosition;\ngl_Position = projectionMatrix * mvPosition;";var dithering_fragment="#ifdef DITHERING\n\tgl_FragColor.rgb = dithering( gl_FragColor.rgb );\n#endif";var dithering_pars_fragment="#ifdef DITHERING\n\tvec3 dithering( vec3 color ) {\n\t\tfloat grid_position = rand( gl_FragCoord.xy );\n\t\tvec3 dither_shift_RGB = vec3( 0.25 / 255.0, -0.25 / 255.0, 0.25 / 255.0 );\n\t\tdither_shift_RGB = mix( 2.0 * dither_shift_RGB, -2.0 * dither_shift_RGB, grid_position );\n\t\treturn color + dither_shift_RGB;\n\t}\n#endif";var roughnessmap_fragment="float roughnessFactor = roughness;\n#ifdef USE_ROUGHNESSMAP\n\tvec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );\n\troughnessFactor *= texelRoughness.g;\n#endif";var roughnessmap_pars_fragment="#ifdef USE_ROUGHNESSMAP\n\tuniform sampler2D roughnessMap;\n#endif";var shadowmap_pars_fragment="#if NUM_SPOT_LIGHT_COORDS > 0\n\tvarying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];\n#endif\n#if NUM_SPOT_LIGHT_MAPS > 0\n\tuniform sampler2D spotLightMap[ NUM_SPOT_LIGHT_MAPS ];\n#endif\n#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tvarying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tstruct DirectionalLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D spotShadowMap[ NUM_SPOT_LIGHT_SHADOWS ];\n\t\tstruct SpotLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D pointShadowMap[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tvarying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tstruct PointLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t\tfloat shadowCameraNear;\n\t\t\tfloat shadowCameraFar;\n\t\t};\n\t\tuniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];\n\t#endif\n\tfloat texture2DCompare( sampler2D depths, vec2 uv, float compare ) {\n\t\treturn step( compare, unpackRGBAToDepth( texture2D( depths, uv ) ) );\n\t}\n\tvec2 texture2DDistribution( sampler2D shadow, vec2 uv ) {\n\t\treturn unpackRGBATo2Half( texture2D( shadow, uv ) );\n\t}\n\tfloat VSMShadow (sampler2D shadow, vec2 uv, float compare ){\n\t\tfloat occlusion = 1.0;\n\t\tvec2 distribution = texture2DDistribution( shadow, uv );\n\t\tfloat hard_shadow = step( compare , distribution.x );\n\t\tif (hard_shadow != 1.0 ) {\n\t\t\tfloat distance = compare - distribution.x ;\n\t\t\tfloat variance = max( 0.00000, distribution.y * distribution.y );\n\t\t\tfloat softness_probability = variance / (variance + distance * distance );\t\t\tsoftness_probability = clamp( ( softness_probability - 0.3 ) / ( 0.95 - 0.3 ), 0.0, 1.0 );\t\t\tocclusion = clamp( max( hard_shadow, softness_probability ), 0.0, 1.0 );\n\t\t}\n\t\treturn occlusion;\n\t}\n\tfloat getShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord ) {\n\t\tfloat shadow = 1.0;\n\t\tshadowCoord.xyz /= shadowCoord.w;\n\t\tshadowCoord.z += shadowBias;\n\t\tbool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;\n\t\tbool frustumTest = inFrustum && shadowCoord.z <= 1.0;\n\t\tif ( frustumTest ) {\n\t\t#if defined( SHADOWMAP_TYPE_PCF )\n\t\t\tvec2 texelSize = vec2( 1.0 ) / shadowMapSize;\n\t\t\tfloat dx0 = - texelSize.x * shadowRadius;\n\t\t\tfloat dy0 = - texelSize.y * shadowRadius;\n\t\t\tfloat dx1 = + texelSize.x * shadowRadius;\n\t\t\tfloat dy1 = + texelSize.y * shadowRadius;\n\t\t\tfloat dx2 = dx0 / 2.0;\n\t\t\tfloat dy2 = dy0 / 2.0;\n\t\t\tfloat dx3 = dx1 / 2.0;\n\t\t\tfloat dy3 = dy1 / 2.0;\n\t\t\tshadow = (\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy1 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy1 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy1 ), shadowCoord.z )\n\t\t\t) * ( 1.0 / 17.0 );\n\t\t#elif defined( SHADOWMAP_TYPE_PCF_SOFT )\n\t\t\tvec2 texelSize = vec2( 1.0 ) / shadowMapSize;\n\t\t\tfloat dx = texelSize.x;\n\t\t\tfloat dy = texelSize.y;\n\t\t\tvec2 uv = shadowCoord.xy;\n\t\t\tvec2 f = fract( uv * shadowMapSize + 0.5 );\n\t\t\tuv -= f * texelSize;\n\t\t\tshadow = (\n\t\t\t\ttexture2DCompare( shadowMap, uv, shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + vec2( dx, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + vec2( 0.0, dy ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + texelSize, shadowCoord.z ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( -dx, 0.0 ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 0.0 ), shadowCoord.z ),\n\t\t\t\t\t f.x ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( -dx, dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, dy ), shadowCoord.z ),\n\t\t\t\t\t f.x ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( 0.0, -dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 0.0, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t f.y ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t f.y ) +\n\t\t\t\tmix( mix( texture2DCompare( shadowMap, uv + vec2( -dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t\t  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t\t  f.x ),\n\t\t\t\t\t mix( texture2DCompare( shadowMap, uv + vec2( -dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t\t  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t\t  f.x ),\n\t\t\t\t\t f.y )\n\t\t\t) * ( 1.0 / 9.0 );\n\t\t#elif defined( SHADOWMAP_TYPE_VSM )\n\t\t\tshadow = VSMShadow( shadowMap, shadowCoord.xy, shadowCoord.z );\n\t\t#else\n\t\t\tshadow = texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z );\n\t\t#endif\n\t\t}\n\t\treturn mix( 1.0, shadow, shadowIntensity );\n\t}\n\tvec2 cubeToUV( vec3 v, float texelSizeY ) {\n\t\tvec3 absV = abs( v );\n\t\tfloat scaleToCube = 1.0 / max( absV.x, max( absV.y, absV.z ) );\n\t\tabsV *= scaleToCube;\n\t\tv *= scaleToCube * ( 1.0 - 2.0 * texelSizeY );\n\t\tvec2 planar = v.xy;\n\t\tfloat almostATexel = 1.5 * texelSizeY;\n\t\tfloat almostOne = 1.0 - almostATexel;\n\t\tif ( absV.z >= almostOne ) {\n\t\t\tif ( v.z > 0.0 )\n\t\t\t\tplanar.x = 4.0 - v.x;\n\t\t} else if ( absV.x >= almostOne ) {\n\t\t\tfloat signX = sign( v.x );\n\t\t\tplanar.x = v.z * signX + 2.0 * signX;\n\t\t} else if ( absV.y >= almostOne ) {\n\t\t\tfloat signY = sign( v.y );\n\t\t\tplanar.x = v.x + 2.0 * signY + 2.0;\n\t\t\tplanar.y = v.z * signY - 2.0;\n\t\t}\n\t\treturn vec2( 0.125, 0.25 ) * planar + vec2( 0.375, 0.75 );\n\t}\n\tfloat getPointShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord, float shadowCameraNear, float shadowCameraFar ) {\n\t\tfloat shadow = 1.0;\n\t\tvec3 lightToPosition = shadowCoord.xyz;\n\t\t\n\t\tfloat lightToPositionLength = length( lightToPosition );\n\t\tif ( lightToPositionLength - shadowCameraFar <= 0.0 && lightToPositionLength - shadowCameraNear >= 0.0 ) {\n\t\t\tfloat dp = ( lightToPositionLength - shadowCameraNear ) / ( shadowCameraFar - shadowCameraNear );\t\t\tdp += shadowBias;\n\t\t\tvec3 bd3D = normalize( lightToPosition );\n\t\t\tvec2 texelSize = vec2( 1.0 ) / ( shadowMapSize * vec2( 4.0, 2.0 ) );\n\t\t\t#if defined( SHADOWMAP_TYPE_PCF ) || defined( SHADOWMAP_TYPE_PCF_SOFT ) || defined( SHADOWMAP_TYPE_VSM )\n\t\t\t\tvec2 offset = vec2( - 1, 1 ) * shadowRadius * texelSize.y;\n\t\t\t\tshadow = (\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxx, texelSize.y ), dp )\n\t\t\t\t) * ( 1.0 / 9.0 );\n\t\t\t#else\n\t\t\t\tshadow = texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp );\n\t\t\t#endif\n\t\t}\n\t\treturn mix( 1.0, shadow, shadowIntensity );\n\t}\n#endif";var shadowmap_pars_vertex="#if NUM_SPOT_LIGHT_COORDS > 0\n\tuniform mat4 spotLightMatrix[ NUM_SPOT_LIGHT_COORDS ];\n\tvarying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];\n#endif\n#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\tuniform mat4 directionalShadowMatrix[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tvarying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tstruct DirectionalLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\t\tstruct SpotLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\tuniform mat4 pointShadowMatrix[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tvarying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tstruct PointLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t\tfloat shadowCameraNear;\n\t\t\tfloat shadowCameraFar;\n\t\t};\n\t\tuniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];\n\t#endif\n#endif";var shadowmap_vertex="#if ( defined( USE_SHADOWMAP ) && ( NUM_DIR_LIGHT_SHADOWS > 0 || NUM_POINT_LIGHT_SHADOWS > 0 ) ) || ( NUM_SPOT_LIGHT_COORDS > 0 )\n\tvec3 shadowWorldNormal = inverseTransformDirection( transformedNormal, viewMatrix );\n\tvec4 shadowWorldPosition;\n#endif\n#if defined( USE_SHADOWMAP )\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {\n\t\t\tshadowWorldPosition = worldPosition + vec4( shadowWorldNormal * directionalLightShadows[ i ].shadowNormalBias, 0 );\n\t\t\tvDirectionalShadowCoord[ i ] = directionalShadowMatrix[ i ] * shadowWorldPosition;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {\n\t\t\tshadowWorldPosition = worldPosition + vec4( shadowWorldNormal * pointLightShadows[ i ].shadowNormalBias, 0 );\n\t\t\tvPointShadowCoord[ i ] = pointShadowMatrix[ i ] * shadowWorldPosition;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n#endif\n#if NUM_SPOT_LIGHT_COORDS > 0\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHT_COORDS; i ++ ) {\n\t\tshadowWorldPosition = worldPosition;\n\t\t#if ( defined( USE_SHADOWMAP ) && UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\t\tshadowWorldPosition.xyz += shadowWorldNormal * spotLightShadows[ i ].shadowNormalBias;\n\t\t#endif\n\t\tvSpotLightCoord[ i ] = spotLightMatrix[ i ] * shadowWorldPosition;\n\t}\n\t#pragma unroll_loop_end\n#endif";var shadowmask_pars_fragment="float getShadowMask() {\n\tfloat shadow = 1.0;\n\t#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\tDirectionalLightShadow directionalLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {\n\t\tdirectionalLight = directionalLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getShadow( directionalShadowMap[ i ], directionalLight.shadowMapSize, directionalLight.shadowIntensity, directionalLight.shadowBias, directionalLight.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\tSpotLightShadow spotLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHT_SHADOWS; i ++ ) {\n\t\tspotLight = spotLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getShadow( spotShadowMap[ i ], spotLight.shadowMapSize, spotLight.shadowIntensity, spotLight.shadowBias, spotLight.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\tPointLightShadow pointLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {\n\t\tpointLight = pointLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getPointShadow( pointShadowMap[ i ], pointLight.shadowMapSize, pointLight.shadowIntensity, pointLight.shadowBias, pointLight.shadowRadius, vPointShadowCoord[ i ], pointLight.shadowCameraNear, pointLight.shadowCameraFar ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#endif\n\treturn shadow;\n}";var skinbase_vertex="#ifdef USE_SKINNING\n\tmat4 boneMatX = getBoneMatrix( skinIndex.x );\n\tmat4 boneMatY = getBoneMatrix( skinIndex.y );\n\tmat4 boneMatZ = getBoneMatrix( skinIndex.z );\n\tmat4 boneMatW = getBoneMatrix( skinIndex.w );\n#endif";var skinning_pars_vertex="#ifdef USE_SKINNING\n\tuniform mat4 bindMatrix;\n\tuniform mat4 bindMatrixInverse;\n\tuniform highp sampler2D boneTexture;\n\tmat4 getBoneMatrix( const in float i ) {\n\t\tint size = textureSize( boneTexture, 0 ).x;\n\t\tint j = int( i ) * 4;\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\tvec4 v1 = texelFetch( boneTexture, ivec2( x, y ), 0 );\n\t\tvec4 v2 = texelFetch( boneTexture, ivec2( x + 1, y ), 0 );\n\t\tvec4 v3 = texelFetch( boneTexture, ivec2( x + 2, y ), 0 );\n\t\tvec4 v4 = texelFetch( boneTexture, ivec2( x + 3, y ), 0 );\n\t\treturn mat4( v1, v2, v3, v4 );\n\t}\n#endif";var skinning_vertex="#ifdef USE_SKINNING\n\tvec4 skinVertex = bindMatrix * vec4( transformed, 1.0 );\n\tvec4 skinned = vec4( 0.0 );\n\tskinned += boneMatX * skinVertex * skinWeight.x;\n\tskinned += boneMatY * skinVertex * skinWeight.y;\n\tskinned += boneMatZ * skinVertex * skinWeight.z;\n\tskinned += boneMatW * skinVertex * skinWeight.w;\n\ttransformed = ( bindMatrixInverse * skinned ).xyz;\n#endif";var skinnormal_vertex="#ifdef USE_SKINNING\n\tmat4 skinMatrix = mat4( 0.0 );\n\tskinMatrix += skinWeight.x * boneMatX;\n\tskinMatrix += skinWeight.y * boneMatY;\n\tskinMatrix += skinWeight.z * boneMatZ;\n\tskinMatrix += skinWeight.w * boneMatW;\n\tskinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;\n\tobjectNormal = vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz;\n\t#ifdef USE_TANGENT\n\t\tobjectTangent = vec4( skinMatrix * vec4( objectTangent, 0.0 ) ).xyz;\n\t#endif\n#endif";var specularmap_fragment="float specularStrength;\n#ifdef USE_SPECULARMAP\n\tvec4 texelSpecular = texture2D( specularMap, vSpecularMapUv );\n\tspecularStrength = texelSpecular.r;\n#else\n\tspecularStrength = 1.0;\n#endif";var specularmap_pars_fragment="#ifdef USE_SPECULARMAP\n\tuniform sampler2D specularMap;\n#endif";var tonemapping_fragment="#if defined( TONE_MAPPING )\n\tgl_FragColor.rgb = toneMapping( gl_FragColor.rgb );\n#endif";var tonemapping_pars_fragment="#ifndef saturate\n#define saturate( a ) clamp( a, 0.0, 1.0 )\n#endif\nuniform float toneMappingExposure;\nvec3 LinearToneMapping( vec3 color ) {\n\treturn saturate( toneMappingExposure * color );\n}\nvec3 ReinhardToneMapping( vec3 color ) {\n\tcolor *= toneMappingExposure;\n\treturn saturate( color / ( vec3( 1.0 ) + color ) );\n}\nvec3 CineonToneMapping( vec3 color ) {\n\tcolor *= toneMappingExposure;\n\tcolor = max( vec3( 0.0 ), color - 0.004 );\n\treturn pow( ( color * ( 6.2 * color + 0.5 ) ) / ( color * ( 6.2 * color + 1.7 ) + 0.06 ), vec3( 2.2 ) );\n}\nvec3 RRTAndODTFit( vec3 v ) {\n\tvec3 a = v * ( v + 0.0245786 ) - 0.000090537;\n\tvec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;\n\treturn a / b;\n}\nvec3 ACESFilmicToneMapping( vec3 color ) {\n\tconst mat3 ACESInputMat = mat3(\n\t\tvec3( 0.59719, 0.07600, 0.02840 ),\t\tvec3( 0.35458, 0.90834, 0.13383 ),\n\t\tvec3( 0.04823, 0.01566, 0.83777 )\n\t);\n\tconst mat3 ACESOutputMat = mat3(\n\t\tvec3(  1.60475, -0.10208, -0.00327 ),\t\tvec3( -0.53108,  1.10813, -0.07276 ),\n\t\tvec3( -0.07367, -0.00605,  1.07602 )\n\t);\n\tcolor *= toneMappingExposure / 0.6;\n\tcolor = ACESInputMat * color;\n\tcolor = RRTAndODTFit( color );\n\tcolor = ACESOutputMat * color;\n\treturn saturate( color );\n}\nconst mat3 LINEAR_REC2020_TO_LINEAR_SRGB = mat3(\n\tvec3( 1.6605, - 0.1246, - 0.0182 ),\n\tvec3( - 0.5876, 1.1329, - 0.1006 ),\n\tvec3( - 0.0728, - 0.0083, 1.1187 )\n);\nconst mat3 LINEAR_SRGB_TO_LINEAR_REC2020 = mat3(\n\tvec3( 0.6274, 0.0691, 0.0164 ),\n\tvec3( 0.3293, 0.9195, 0.0880 ),\n\tvec3( 0.0433, 0.0113, 0.8956 )\n);\nvec3 agxDefaultContrastApprox( vec3 x ) {\n\tvec3 x2 = x * x;\n\tvec3 x4 = x2 * x2;\n\treturn + 15.5 * x4 * x2\n\t\t- 40.14 * x4 * x\n\t\t+ 31.96 * x4\n\t\t- 6.868 * x2 * x\n\t\t+ 0.4298 * x2\n\t\t+ 0.1191 * x\n\t\t- 0.00232;\n}\nvec3 AgXToneMapping( vec3 color ) {\n\tconst mat3 AgXInsetMatrix = mat3(\n\t\tvec3( 0.856627153315983, 0.137318972929847, 0.11189821299995 ),\n\t\tvec3( 0.0951212405381588, 0.761241990602591, 0.0767994186031903 ),\n\t\tvec3( 0.0482516061458583, 0.101439036467562, 0.811302368396859 )\n\t);\n\tconst mat3 AgXOutsetMatrix = mat3(\n\t\tvec3( 1.1271005818144368, - 0.1413297634984383, - 0.14132976349843826 ),\n\t\tvec3( - 0.11060664309660323, 1.157823702216272, - 0.11060664309660294 ),\n\t\tvec3( - 0.016493938717834573, - 0.016493938717834257, 1.2519364065950405 )\n\t);\n\tconst float AgxMinEv = - 12.47393;\tconst float AgxMaxEv = 4.026069;\n\tcolor *= toneMappingExposure;\n\tcolor = LINEAR_SRGB_TO_LINEAR_REC2020 * color;\n\tcolor = AgXInsetMatrix * color;\n\tcolor = max( color, 1e-10 );\tcolor = log2( color );\n\tcolor = ( color - AgxMinEv ) / ( AgxMaxEv - AgxMinEv );\n\tcolor = clamp( color, 0.0, 1.0 );\n\tcolor = agxDefaultContrastApprox( color );\n\tcolor = AgXOutsetMatrix * color;\n\tcolor = pow( max( vec3( 0.0 ), color ), vec3( 2.2 ) );\n\tcolor = LINEAR_REC2020_TO_LINEAR_SRGB * color;\n\tcolor = clamp( color, 0.0, 1.0 );\n\treturn color;\n}\nvec3 NeutralToneMapping( vec3 color ) {\n\tconst float StartCompression = 0.8 - 0.04;\n\tconst float Desaturation = 0.15;\n\tcolor *= toneMappingExposure;\n\tfloat x = min( color.r, min( color.g, color.b ) );\n\tfloat offset = x < 0.08 ? x - 6.25 * x * x : 0.04;\n\tcolor -= offset;\n\tfloat peak = max( color.r, max( color.g, color.b ) );\n\tif ( peak < StartCompression ) return color;\n\tfloat d = 1. - StartCompression;\n\tfloat newPeak = 1. - d * d / ( peak + d - StartCompression );\n\tcolor *= newPeak / peak;\n\tfloat g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. );\n\treturn mix( color, vec3( newPeak ), g );\n}\nvec3 CustomToneMapping( vec3 color ) { return color; }";var transmission_fragment="#ifdef USE_TRANSMISSION\n\tmaterial.transmission = transmission;\n\tmaterial.transmissionAlpha = 1.0;\n\tmaterial.thickness = thickness;\n\tmaterial.attenuationDistance = attenuationDistance;\n\tmaterial.attenuationColor = attenuationColor;\n\t#ifdef USE_TRANSMISSIONMAP\n\t\tmaterial.transmission *= texture2D( transmissionMap, vTransmissionMapUv ).r;\n\t#endif\n\t#ifdef USE_THICKNESSMAP\n\t\tmaterial.thickness *= texture2D( thicknessMap, vThicknessMapUv ).g;\n\t#endif\n\tvec3 pos = vWorldPosition;\n\tvec3 v = normalize( cameraPosition - pos );\n\tvec3 n = inverseTransformDirection( normal, viewMatrix );\n\tvec4 transmitted = getIBLVolumeRefraction(\n\t\tn, v, material.roughness, material.diffuseColor, material.specularColor, material.specularF90,\n\t\tpos, modelMatrix, viewMatrix, projectionMatrix, material.dispersion, material.ior, material.thickness,\n\t\tmaterial.attenuationColor, material.attenuationDistance );\n\tmaterial.transmissionAlpha = mix( material.transmissionAlpha, transmitted.a, material.transmission );\n\ttotalDiffuse = mix( totalDiffuse, transmitted.rgb, material.transmission );\n#endif";var transmission_pars_fragment="#ifdef USE_TRANSMISSION\n\tuniform float transmission;\n\tuniform float thickness;\n\tuniform float attenuationDistance;\n\tuniform vec3 attenuationColor;\n\t#ifdef USE_TRANSMISSIONMAP\n\t\tuniform sampler2D transmissionMap;\n\t#endif\n\t#ifdef USE_THICKNESSMAP\n\t\tuniform sampler2D thicknessMap;\n\t#endif\n\tuniform vec2 transmissionSamplerSize;\n\tuniform sampler2D transmissionSamplerMap;\n\tuniform mat4 modelMatrix;\n\tuniform mat4 projectionMatrix;\n\tvarying vec3 vWorldPosition;\n\tfloat w0( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a * ( a * ( - a + 3.0 ) - 3.0 ) + 1.0 );\n\t}\n\tfloat w1( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a *  a * ( 3.0 * a - 6.0 ) + 4.0 );\n\t}\n\tfloat w2( float a ){\n\t\treturn ( 1.0 / 6.0 ) * ( a * ( a * ( - 3.0 * a + 3.0 ) + 3.0 ) + 1.0 );\n\t}\n\tfloat w3( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a * a * a );\n\t}\n\tfloat g0( float a ) {\n\t\treturn w0( a ) + w1( a );\n\t}\n\tfloat g1( float a ) {\n\t\treturn w2( a ) + w3( a );\n\t}\n\tfloat h0( float a ) {\n\t\treturn - 1.0 + w1( a ) / ( w0( a ) + w1( a ) );\n\t}\n\tfloat h1( float a ) {\n\t\treturn 1.0 + w3( a ) / ( w2( a ) + w3( a ) );\n\t}\n\tvec4 bicubic( sampler2D tex, vec2 uv, vec4 texelSize, float lod ) {\n\t\tuv = uv * texelSize.zw + 0.5;\n\t\tvec2 iuv = floor( uv );\n\t\tvec2 fuv = fract( uv );\n\t\tfloat g0x = g0( fuv.x );\n\t\tfloat g1x = g1( fuv.x );\n\t\tfloat h0x = h0( fuv.x );\n\t\tfloat h1x = h1( fuv.x );\n\t\tfloat h0y = h0( fuv.y );\n\t\tfloat h1y = h1( fuv.y );\n\t\tvec2 p0 = ( vec2( iuv.x + h0x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p1 = ( vec2( iuv.x + h1x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p2 = ( vec2( iuv.x + h0x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p3 = ( vec2( iuv.x + h1x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;\n\t\treturn g0( fuv.y ) * ( g0x * textureLod( tex, p0, lod ) + g1x * textureLod( tex, p1, lod ) ) +\n\t\t\tg1( fuv.y ) * ( g0x * textureLod( tex, p2, lod ) + g1x * textureLod( tex, p3, lod ) );\n\t}\n\tvec4 textureBicubic( sampler2D sampler, vec2 uv, float lod ) {\n\t\tvec2 fLodSize = vec2( textureSize( sampler, int( lod ) ) );\n\t\tvec2 cLodSize = vec2( textureSize( sampler, int( lod + 1.0 ) ) );\n\t\tvec2 fLodSizeInv = 1.0 / fLodSize;\n\t\tvec2 cLodSizeInv = 1.0 / cLodSize;\n\t\tvec4 fSample = bicubic( sampler, uv, vec4( fLodSizeInv, fLodSize ), floor( lod ) );\n\t\tvec4 cSample = bicubic( sampler, uv, vec4( cLodSizeInv, cLodSize ), ceil( lod ) );\n\t\treturn mix( fSample, cSample, fract( lod ) );\n\t}\n\tvec3 getVolumeTransmissionRay( const in vec3 n, const in vec3 v, const in float thickness, const in float ior, const in mat4 modelMatrix ) {\n\t\tvec3 refractionVector = refract( - v, normalize( n ), 1.0 / ior );\n\t\tvec3 modelScale;\n\t\tmodelScale.x = length( vec3( modelMatrix[ 0 ].xyz ) );\n\t\tmodelScale.y = length( vec3( modelMatrix[ 1 ].xyz ) );\n\t\tmodelScale.z = length( vec3( modelMatrix[ 2 ].xyz ) );\n\t\treturn normalize( refractionVector ) * thickness * modelScale;\n\t}\n\tfloat applyIorToRoughness( const in float roughness, const in float ior ) {\n\t\treturn roughness * clamp( ior * 2.0 - 2.0, 0.0, 1.0 );\n\t}\n\tvec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {\n\t\tfloat lod = log2( transmissionSamplerSize.x ) * applyIorToRoughness( roughness, ior );\n\t\treturn textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );\n\t}\n\tvec3 volumeAttenuation( const in float transmissionDistance, const in vec3 attenuationColor, const in float attenuationDistance ) {\n\t\tif ( isinf( attenuationDistance ) ) {\n\t\t\treturn vec3( 1.0 );\n\t\t} else {\n\t\t\tvec3 attenuationCoefficient = -log( attenuationColor ) / attenuationDistance;\n\t\t\tvec3 transmittance = exp( - attenuationCoefficient * transmissionDistance );\t\t\treturn transmittance;\n\t\t}\n\t}\n\tvec4 getIBLVolumeRefraction( const in vec3 n, const in vec3 v, const in float roughness, const in vec3 diffuseColor,\n\t\tconst in vec3 specularColor, const in float specularF90, const in vec3 position, const in mat4 modelMatrix,\n\t\tconst in mat4 viewMatrix, const in mat4 projMatrix, const in float dispersion, const in float ior, const in float thickness,\n\t\tconst in vec3 attenuationColor, const in float attenuationDistance ) {\n\t\tvec4 transmittedLight;\n\t\tvec3 transmittance;\n\t\t#ifdef USE_DISPERSION\n\t\t\tfloat halfSpread = ( ior - 1.0 ) * 0.025 * dispersion;\n\t\t\tvec3 iors = vec3( ior - halfSpread, ior, ior + halfSpread );\n\t\t\tfor ( int i = 0; i < 3; i ++ ) {\n\t\t\t\tvec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, iors[ i ], modelMatrix );\n\t\t\t\tvec3 refractedRayExit = position + transmissionRay;\n\t\t\n\t\t\t\tvec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );\n\t\t\t\tvec2 refractionCoords = ndcPos.xy / ndcPos.w;\n\t\t\t\trefractionCoords += 1.0;\n\t\t\t\trefractionCoords /= 2.0;\n\t\t\n\t\t\t\tvec4 transmissionSample = getTransmissionSample( refractionCoords, roughness, iors[ i ] );\n\t\t\t\ttransmittedLight[ i ] = transmissionSample[ i ];\n\t\t\t\ttransmittedLight.a += transmissionSample.a;\n\t\t\t\ttransmittance[ i ] = diffuseColor[ i ] * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance )[ i ];\n\t\t\t}\n\t\t\ttransmittedLight.a /= 3.0;\n\t\t\n\t\t#else\n\t\t\n\t\t\tvec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, ior, modelMatrix );\n\t\t\tvec3 refractedRayExit = position + transmissionRay;\n\t\t\tvec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );\n\t\t\tvec2 refractionCoords = ndcPos.xy / ndcPos.w;\n\t\t\trefractionCoords += 1.0;\n\t\t\trefractionCoords /= 2.0;\n\t\t\ttransmittedLight = getTransmissionSample( refractionCoords, roughness, ior );\n\t\t\ttransmittance = diffuseColor * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance );\n\t\t\n\t\t#endif\n\t\tvec3 attenuatedColor = transmittance * transmittedLight.rgb;\n\t\tvec3 F = EnvironmentBRDF( n, v, specularColor, specularF90, roughness );\n\t\tfloat transmittanceFactor = ( transmittance.r + transmittance.g + transmittance.b ) / 3.0;\n\t\treturn vec4( ( 1.0 - F ) * attenuatedColor, 1.0 - ( 1.0 - transmittedLight.a ) * transmittanceFactor );\n\t}\n#endif";var uv_pars_fragment="#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvarying vec2 vUv;\n#endif\n#ifdef USE_MAP\n\tvarying vec2 vMapUv;\n#endif\n#ifdef USE_ALPHAMAP\n\tvarying vec2 vAlphaMapUv;\n#endif\n#ifdef USE_LIGHTMAP\n\tvarying vec2 vLightMapUv;\n#endif\n#ifdef USE_AOMAP\n\tvarying vec2 vAoMapUv;\n#endif\n#ifdef USE_BUMPMAP\n\tvarying vec2 vBumpMapUv;\n#endif\n#ifdef USE_NORMALMAP\n\tvarying vec2 vNormalMapUv;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tvarying vec2 vEmissiveMapUv;\n#endif\n#ifdef USE_METALNESSMAP\n\tvarying vec2 vMetalnessMapUv;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tvarying vec2 vRoughnessMapUv;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tvarying vec2 vAnisotropyMapUv;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tvarying vec2 vClearcoatMapUv;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tvarying vec2 vClearcoatNormalMapUv;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tvarying vec2 vClearcoatRoughnessMapUv;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tvarying vec2 vIridescenceMapUv;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tvarying vec2 vIridescenceThicknessMapUv;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tvarying vec2 vSheenColorMapUv;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tvarying vec2 vSheenRoughnessMapUv;\n#endif\n#ifdef USE_SPECULARMAP\n\tvarying vec2 vSpecularMapUv;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tvarying vec2 vSpecularColorMapUv;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tvarying vec2 vSpecularIntensityMapUv;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tuniform mat3 transmissionMapTransform;\n\tvarying vec2 vTransmissionMapUv;\n#endif\n#ifdef USE_THICKNESSMAP\n\tuniform mat3 thicknessMapTransform;\n\tvarying vec2 vThicknessMapUv;\n#endif";var uv_pars_vertex="#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvarying vec2 vUv;\n#endif\n#ifdef USE_MAP\n\tuniform mat3 mapTransform;\n\tvarying vec2 vMapUv;\n#endif\n#ifdef USE_ALPHAMAP\n\tuniform mat3 alphaMapTransform;\n\tvarying vec2 vAlphaMapUv;\n#endif\n#ifdef USE_LIGHTMAP\n\tuniform mat3 lightMapTransform;\n\tvarying vec2 vLightMapUv;\n#endif\n#ifdef USE_AOMAP\n\tuniform mat3 aoMapTransform;\n\tvarying vec2 vAoMapUv;\n#endif\n#ifdef USE_BUMPMAP\n\tuniform mat3 bumpMapTransform;\n\tvarying vec2 vBumpMapUv;\n#endif\n#ifdef USE_NORMALMAP\n\tuniform mat3 normalMapTransform;\n\tvarying vec2 vNormalMapUv;\n#endif\n#ifdef USE_DISPLACEMENTMAP\n\tuniform mat3 displacementMapTransform;\n\tvarying vec2 vDisplacementMapUv;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tuniform mat3 emissiveMapTransform;\n\tvarying vec2 vEmissiveMapUv;\n#endif\n#ifdef USE_METALNESSMAP\n\tuniform mat3 metalnessMapTransform;\n\tvarying vec2 vMetalnessMapUv;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tuniform mat3 roughnessMapTransform;\n\tvarying vec2 vRoughnessMapUv;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tuniform mat3 anisotropyMapTransform;\n\tvarying vec2 vAnisotropyMapUv;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tuniform mat3 clearcoatMapTransform;\n\tvarying vec2 vClearcoatMapUv;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tuniform mat3 clearcoatNormalMapTransform;\n\tvarying vec2 vClearcoatNormalMapUv;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tuniform mat3 clearcoatRoughnessMapTransform;\n\tvarying vec2 vClearcoatRoughnessMapUv;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tuniform mat3 sheenColorMapTransform;\n\tvarying vec2 vSheenColorMapUv;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tuniform mat3 sheenRoughnessMapTransform;\n\tvarying vec2 vSheenRoughnessMapUv;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tuniform mat3 iridescenceMapTransform;\n\tvarying vec2 vIridescenceMapUv;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tuniform mat3 iridescenceThicknessMapTransform;\n\tvarying vec2 vIridescenceThicknessMapUv;\n#endif\n#ifdef USE_SPECULARMAP\n\tuniform mat3 specularMapTransform;\n\tvarying vec2 vSpecularMapUv;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tuniform mat3 specularColorMapTransform;\n\tvarying vec2 vSpecularColorMapUv;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tuniform mat3 specularIntensityMapTransform;\n\tvarying vec2 vSpecularIntensityMapUv;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tuniform mat3 transmissionMapTransform;\n\tvarying vec2 vTransmissionMapUv;\n#endif\n#ifdef USE_THICKNESSMAP\n\tuniform mat3 thicknessMapTransform;\n\tvarying vec2 vThicknessMapUv;\n#endif";var uv_vertex="#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvUv = vec3( uv, 1 ).xy;\n#endif\n#ifdef USE_MAP\n\tvMapUv = ( mapTransform * vec3( MAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ALPHAMAP\n\tvAlphaMapUv = ( alphaMapTransform * vec3( ALPHAMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_LIGHTMAP\n\tvLightMapUv = ( lightMapTransform * vec3( LIGHTMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_AOMAP\n\tvAoMapUv = ( aoMapTransform * vec3( AOMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_BUMPMAP\n\tvBumpMapUv = ( bumpMapTransform * vec3( BUMPMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_NORMALMAP\n\tvNormalMapUv = ( normalMapTransform * vec3( NORMALMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_DISPLACEMENTMAP\n\tvDisplacementMapUv = ( displacementMapTransform * vec3( DISPLACEMENTMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tvEmissiveMapUv = ( emissiveMapTransform * vec3( EMISSIVEMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_METALNESSMAP\n\tvMetalnessMapUv = ( metalnessMapTransform * vec3( METALNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tvRoughnessMapUv = ( roughnessMapTransform * vec3( ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tvAnisotropyMapUv = ( anisotropyMapTransform * vec3( ANISOTROPYMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tvClearcoatMapUv = ( clearcoatMapTransform * vec3( CLEARCOATMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tvClearcoatNormalMapUv = ( clearcoatNormalMapTransform * vec3( CLEARCOAT_NORMALMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tvClearcoatRoughnessMapUv = ( clearcoatRoughnessMapTransform * vec3( CLEARCOAT_ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tvIridescenceMapUv = ( iridescenceMapTransform * vec3( IRIDESCENCEMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tvIridescenceThicknessMapUv = ( iridescenceThicknessMapTransform * vec3( IRIDESCENCE_THICKNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tvSheenColorMapUv = ( sheenColorMapTransform * vec3( SHEEN_COLORMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tvSheenRoughnessMapUv = ( sheenRoughnessMapTransform * vec3( SHEEN_ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULARMAP\n\tvSpecularMapUv = ( specularMapTransform * vec3( SPECULARMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tvSpecularColorMapUv = ( specularColorMapTransform * vec3( SPECULAR_COLORMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tvSpecularIntensityMapUv = ( specularIntensityMapTransform * vec3( SPECULAR_INTENSITYMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tvTransmissionMapUv = ( transmissionMapTransform * vec3( TRANSMISSIONMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_THICKNESSMAP\n\tvThicknessMapUv = ( thicknessMapTransform * vec3( THICKNESSMAP_UV, 1 ) ).xy;\n#endif";var worldpos_vertex="#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0\n\tvec4 worldPosition = vec4( transformed, 1.0 );\n\t#ifdef USE_BATCHING\n\t\tworldPosition = batchingMatrix * worldPosition;\n\t#endif\n\t#ifdef USE_INSTANCING\n\t\tworldPosition = instanceMatrix * worldPosition;\n\t#endif\n\tworldPosition = modelMatrix * worldPosition;\n#endif";const vertex$h="varying vec2 vUv;\nuniform mat3 uvTransform;\nvoid main() {\n\tvUv = ( uvTransform * vec3( uv, 1 ) ).xy;\n\tgl_Position = vec4( position.xy, 1.0, 1.0 );\n}";const fragment$h="uniform sampler2D t2D;\nuniform float backgroundIntensity;\nvarying vec2 vUv;\nvoid main() {\n\tvec4 texColor = texture2D( t2D, vUv );\n\t#ifdef DECODE_VIDEO_TEXTURE\n\t\ttexColor = vec4( mix( pow( texColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), texColor.rgb * 0.0773993808, vec3( lessThanEqual( texColor.rgb, vec3( 0.04045 ) ) ) ), texColor.w );\n\t#endif\n\ttexColor.rgb *= backgroundIntensity;\n\tgl_FragColor = texColor;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";const vertex$g="varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n\tgl_Position.z = gl_Position.w;\n}";const fragment$g="#ifdef ENVMAP_TYPE_CUBE\n\tuniform samplerCube envMap;\n#elif defined( ENVMAP_TYPE_CUBE_UV )\n\tuniform sampler2D envMap;\n#endif\nuniform float flipEnvMap;\nuniform float backgroundBlurriness;\nuniform float backgroundIntensity;\nuniform mat3 backgroundRotation;\nvarying vec3 vWorldDirection;\n#include <cube_uv_reflection_fragment>\nvoid main() {\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tvec4 texColor = textureCube( envMap, backgroundRotation * vec3( flipEnvMap * vWorldDirection.x, vWorldDirection.yz ) );\n\t#elif defined( ENVMAP_TYPE_CUBE_UV )\n\t\tvec4 texColor = textureCubeUV( envMap, backgroundRotation * vWorldDirection, backgroundBlurriness );\n\t#else\n\t\tvec4 texColor = vec4( 0.0, 0.0, 0.0, 1.0 );\n\t#endif\n\ttexColor.rgb *= backgroundIntensity;\n\tgl_FragColor = texColor;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";const vertex$f="varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n\tgl_Position.z = gl_Position.w;\n}";const fragment$f="uniform samplerCube tCube;\nuniform float tFlip;\nuniform float opacity;\nvarying vec3 vWorldDirection;\nvoid main() {\n\tvec4 texColor = textureCube( tCube, vec3( tFlip * vWorldDirection.x, vWorldDirection.yz ) );\n\tgl_FragColor = texColor;\n\tgl_FragColor.a *= opacity;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";const vertex$e="#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvarying vec2 vHighPrecisionZW;\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <skinbase_vertex>\n\t#include <morphinstance_vertex>\n\t#ifdef USE_DISPLACEMENTMAP\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvHighPrecisionZW = gl_Position.zw;\n}";const fragment$e="#if DEPTH_PACKING == 3200\n\tuniform float opacity;\n#endif\n#include <common>\n#include <packing>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvarying vec2 vHighPrecisionZW;\nvoid main() {\n\tvec4 diffuseColor = vec4( 1.0 );\n\t#include <clipping_planes_fragment>\n\t#if DEPTH_PACKING == 3200\n\t\tdiffuseColor.a = opacity;\n\t#endif\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <logdepthbuf_fragment>\n\tfloat fragCoordZ = 0.5 * vHighPrecisionZW[0] / vHighPrecisionZW[1] + 0.5;\n\t#if DEPTH_PACKING == 3200\n\t\tgl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );\n\t#elif DEPTH_PACKING == 3201\n\t\tgl_FragColor = packDepthToRGBA( fragCoordZ );\n\t#elif DEPTH_PACKING == 3202\n\t\tgl_FragColor = vec4( packDepthToRGB( fragCoordZ ), 1.0 );\n\t#elif DEPTH_PACKING == 3203\n\t\tgl_FragColor = vec4( packDepthToRG( fragCoordZ ), 0.0, 1.0 );\n\t#endif\n}";const vertex$d="#define DISTANCE\nvarying vec3 vWorldPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <skinbase_vertex>\n\t#include <morphinstance_vertex>\n\t#ifdef USE_DISPLACEMENTMAP\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <worldpos_vertex>\n\t#include <clipping_planes_vertex>\n\tvWorldPosition = worldPosition.xyz;\n}";const fragment$d="#define DISTANCE\nuniform vec3 referencePosition;\nuniform float nearDistance;\nuniform float farDistance;\nvarying vec3 vWorldPosition;\n#include <common>\n#include <packing>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main () {\n\tvec4 diffuseColor = vec4( 1.0 );\n\t#include <clipping_planes_fragment>\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\tfloat dist = length( vWorldPosition - referencePosition );\n\tdist = ( dist - nearDistance ) / ( farDistance - nearDistance );\n\tdist = saturate( dist );\n\tgl_FragColor = packDepthToRGBA( dist );\n}";const vertex$c="varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n}";const fragment$c="uniform sampler2D tEquirect;\nvarying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvec3 direction = normalize( vWorldDirection );\n\tvec2 sampleUV = equirectUv( direction );\n\tgl_FragColor = texture2D( tEquirect, sampleUV );\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";const vertex$b="uniform float scale;\nattribute float lineDistance;\nvarying float vLineDistance;\n#include <common>\n#include <uv_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\tvLineDistance = scale * lineDistance;\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n}";const fragment$b="uniform vec3 diffuse;\nuniform float opacity;\nuniform float dashSize;\nuniform float totalSize;\nvarying float vLineDistance;\n#include <common>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tif ( mod( vLineDistance, totalSize ) > dashSize ) {\n\t\tdiscard;\n\t}\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n}";const vertex$a="#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#if defined ( USE_ENVMAP ) || defined ( USE_SKINNING )\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinbase_vertex>\n\t\t#include <skinnormal_vertex>\n\t\t#include <defaultnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <fog_vertex>\n}";const fragment$a="uniform vec3 diffuse;\nuniform float opacity;\n#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n#endif\n#include <common>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\t#ifdef USE_LIGHTMAP\n\t\tvec4 lightMapTexel = texture2D( lightMap, vLightMapUv );\n\t\treflectedLight.indirectDiffuse += lightMapTexel.rgb * lightMapIntensity * RECIPROCAL_PI;\n\t#else\n\t\treflectedLight.indirectDiffuse += vec3( 1.0 );\n\t#endif\n\t#include <aomap_fragment>\n\treflectedLight.indirectDiffuse *= diffuseColor.rgb;\n\tvec3 outgoingLight = reflectedLight.indirectDiffuse;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$9="#define LAMBERT\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";const fragment$9="#define LAMBERT\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_lambert_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_lambert_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$8="#define MATCAP\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <color_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n\tvViewPosition = - mvPosition.xyz;\n}";const fragment$8="#define MATCAP\nuniform vec3 diffuse;\nuniform float opacity;\nuniform sampler2D matcap;\nvarying vec3 vViewPosition;\n#include <common>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <normal_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\tvec3 viewDir = normalize( vViewPosition );\n\tvec3 x = normalize( vec3( viewDir.z, 0.0, - viewDir.x ) );\n\tvec3 y = cross( viewDir, x );\n\tvec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5;\n\t#ifdef USE_MATCAP\n\t\tvec4 matcapColor = texture2D( matcap, uv );\n\t#else\n\t\tvec4 matcapColor = vec4( vec3( mix( 0.2, 0.8, uv.y ) ), 1.0 );\n\t#endif\n\tvec3 outgoingLight = diffuseColor.rgb * matcapColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$7="#define NORMAL\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvarying vec3 vViewPosition;\n#endif\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvViewPosition = - mvPosition.xyz;\n#endif\n}";const fragment$7="#define NORMAL\nuniform float opacity;\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvarying vec3 vViewPosition;\n#endif\n#include <packing>\n#include <uv_pars_fragment>\n#include <normal_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( 0.0, 0.0, 0.0, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\tgl_FragColor = vec4( packNormalToRGB( normal ), diffuseColor.a );\n\t#ifdef OPAQUE\n\t\tgl_FragColor.a = 1.0;\n\t#endif\n}";const vertex$6="#define PHONG\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";const fragment$6="#define PHONG\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform vec3 specular;\nuniform float shininess;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_phong_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_phong_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$5="#define STANDARD\nvarying vec3 vViewPosition;\n#ifdef USE_TRANSMISSION\n\tvarying vec3 vWorldPosition;\n#endif\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n#ifdef USE_TRANSMISSION\n\tvWorldPosition = worldPosition.xyz;\n#endif\n}";const fragment$5="#define STANDARD\n#ifdef PHYSICAL\n\t#define IOR\n\t#define USE_SPECULAR\n#endif\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float roughness;\nuniform float metalness;\nuniform float opacity;\n#ifdef IOR\n\tuniform float ior;\n#endif\n#ifdef USE_SPECULAR\n\tuniform float specularIntensity;\n\tuniform vec3 specularColor;\n\t#ifdef USE_SPECULAR_COLORMAP\n\t\tuniform sampler2D specularColorMap;\n\t#endif\n\t#ifdef USE_SPECULAR_INTENSITYMAP\n\t\tuniform sampler2D specularIntensityMap;\n\t#endif\n#endif\n#ifdef USE_CLEARCOAT\n\tuniform float clearcoat;\n\tuniform float clearcoatRoughness;\n#endif\n#ifdef USE_DISPERSION\n\tuniform float dispersion;\n#endif\n#ifdef USE_IRIDESCENCE\n\tuniform float iridescence;\n\tuniform float iridescenceIOR;\n\tuniform float iridescenceThicknessMinimum;\n\tuniform float iridescenceThicknessMaximum;\n#endif\n#ifdef USE_SHEEN\n\tuniform vec3 sheenColor;\n\tuniform float sheenRoughness;\n\t#ifdef USE_SHEEN_COLORMAP\n\t\tuniform sampler2D sheenColorMap;\n\t#endif\n\t#ifdef USE_SHEEN_ROUGHNESSMAP\n\t\tuniform sampler2D sheenRoughnessMap;\n\t#endif\n#endif\n#ifdef USE_ANISOTROPY\n\tuniform vec2 anisotropyVector;\n\t#ifdef USE_ANISOTROPYMAP\n\t\tuniform sampler2D anisotropyMap;\n\t#endif\n#endif\nvarying vec3 vViewPosition;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <iridescence_fragment>\n#include <cube_uv_reflection_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_physical_pars_fragment>\n#include <fog_pars_fragment>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_physical_pars_fragment>\n#include <transmission_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <clearcoat_pars_fragment>\n#include <iridescence_pars_fragment>\n#include <roughnessmap_pars_fragment>\n#include <metalnessmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <roughnessmap_fragment>\n\t#include <metalnessmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <clearcoat_normal_fragment_begin>\n\t#include <clearcoat_normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_physical_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;\n\tvec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;\n\t#include <transmission_fragment>\n\tvec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;\n\t#ifdef USE_SHEEN\n\t\tfloat sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );\n\t\toutgoingLight = outgoingLight * sheenEnergyComp + sheenSpecularDirect + sheenSpecularIndirect;\n\t#endif\n\t#ifdef USE_CLEARCOAT\n\t\tfloat dotNVcc = saturate( dot( geometryClearcoatNormal, geometryViewDir ) );\n\t\tvec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );\n\t\toutgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * material.clearcoat;\n\t#endif\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$4="#define TOON\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";const fragment$4="#define TOON\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <gradientmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_toon_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_toon_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";const vertex$3="uniform float size;\nuniform float scale;\n#include <common>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\n#ifdef USE_POINTS_UV\n\tvarying vec2 vUv;\n\tuniform mat3 uvTransform;\n#endif\nvoid main() {\n\t#ifdef USE_POINTS_UV\n\t\tvUv = ( uvTransform * vec3( uv, 1 ) ).xy;\n\t#endif\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <project_vertex>\n\tgl_PointSize = size;\n\t#ifdef USE_SIZEATTENUATION\n\t\tbool isPerspective = isPerspectiveMatrix( projectionMatrix );\n\t\tif ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );\n\t#endif\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <worldpos_vertex>\n\t#include <fog_vertex>\n}";const fragment$3="uniform vec3 diffuse;\nuniform float opacity;\n#include <common>\n#include <color_pars_fragment>\n#include <map_particle_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_particle_fragment>\n\t#include <color_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n}";const vertex$2="#include <common>\n#include <batching_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <shadowmap_pars_vertex>\nvoid main() {\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";const fragment$2="uniform vec3 color;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <logdepthbuf_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <shadowmask_pars_fragment>\nvoid main() {\n\t#include <logdepthbuf_fragment>\n\tgl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n}";const vertex$1="uniform float rotation;\nuniform vec2 center;\n#include <common>\n#include <uv_pars_vertex>\n#include <fog_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\tvec4 mvPosition = modelViewMatrix * vec4( 0.0, 0.0, 0.0, 1.0 );\n\tvec2 scale;\n\tscale.x = length( vec3( modelMatrix[ 0 ].x, modelMatrix[ 0 ].y, modelMatrix[ 0 ].z ) );\n\tscale.y = length( vec3( modelMatrix[ 1 ].x, modelMatrix[ 1 ].y, modelMatrix[ 1 ].z ) );\n\t#ifndef USE_SIZEATTENUATION\n\t\tbool isPerspective = isPerspectiveMatrix( projectionMatrix );\n\t\tif ( isPerspective ) scale *= - mvPosition.z;\n\t#endif\n\tvec2 alignedPosition = ( position.xy - ( center - vec2( 0.5 ) ) ) * scale;\n\tvec2 rotatedPosition;\n\trotatedPosition.x = cos( rotation ) * alignedPosition.x - sin( rotation ) * alignedPosition.y;\n\trotatedPosition.y = sin( rotation ) * alignedPosition.x + cos( rotation ) * alignedPosition.y;\n\tmvPosition.xy += rotatedPosition;\n\tgl_Position = projectionMatrix * mvPosition;\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n}";const fragment$1="uniform vec3 diffuse;\nuniform float opacity;\n#include <common>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n}";const ShaderChunk={alphahash_fragment:alphahash_fragment,alphahash_pars_fragment:alphahash_pars_fragment,alphamap_fragment:alphamap_fragment,alphamap_pars_fragment:alphamap_pars_fragment,alphatest_fragment:alphatest_fragment,alphatest_pars_fragment:alphatest_pars_fragment,aomap_fragment:aomap_fragment,aomap_pars_fragment:aomap_pars_fragment,batching_pars_vertex:batching_pars_vertex,batching_vertex:batching_vertex,begin_vertex:begin_vertex,beginnormal_vertex:beginnormal_vertex,bsdfs:bsdfs,iridescence_fragment:iridescence_fragment,bumpmap_pars_fragment:bumpmap_pars_fragment,clipping_planes_fragment:clipping_planes_fragment,clipping_planes_pars_fragment:clipping_planes_pars_fragment,clipping_planes_pars_vertex:clipping_planes_pars_vertex,clipping_planes_vertex:clipping_planes_vertex,color_fragment:color_fragment,color_pars_fragment:color_pars_fragment,color_pars_vertex:color_pars_vertex,color_vertex:color_vertex,common:common,cube_uv_reflection_fragment:cube_uv_reflection_fragment,defaultnormal_vertex:defaultnormal_vertex,displacementmap_pars_vertex:displacementmap_pars_vertex,displacementmap_vertex:displacementmap_vertex,emissivemap_fragment:emissivemap_fragment,emissivemap_pars_fragment:emissivemap_pars_fragment,colorspace_fragment:colorspace_fragment,colorspace_pars_fragment:colorspace_pars_fragment,envmap_fragment:envmap_fragment,envmap_common_pars_fragment:envmap_common_pars_fragment,envmap_pars_fragment:envmap_pars_fragment,envmap_pars_vertex:envmap_pars_vertex,envmap_physical_pars_fragment:envmap_physical_pars_fragment,envmap_vertex:envmap_vertex,fog_vertex:fog_vertex,fog_pars_vertex:fog_pars_vertex,fog_fragment:fog_fragment,fog_pars_fragment:fog_pars_fragment,gradientmap_pars_fragment:gradientmap_pars_fragment,lightmap_pars_fragment:lightmap_pars_fragment,lights_lambert_fragment:lights_lambert_fragment,lights_lambert_pars_fragment:lights_lambert_pars_fragment,lights_pars_begin:lights_pars_begin,lights_toon_fragment:lights_toon_fragment,lights_toon_pars_fragment:lights_toon_pars_fragment,lights_phong_fragment:lights_phong_fragment,lights_phong_pars_fragment:lights_phong_pars_fragment,lights_physical_fragment:lights_physical_fragment,lights_physical_pars_fragment:lights_physical_pars_fragment,lights_fragment_begin:lights_fragment_begin,lights_fragment_maps:lights_fragment_maps,lights_fragment_end:lights_fragment_end,logdepthbuf_fragment:logdepthbuf_fragment,logdepthbuf_pars_fragment:logdepthbuf_pars_fragment,logdepthbuf_pars_vertex:logdepthbuf_pars_vertex,logdepthbuf_vertex:logdepthbuf_vertex,map_fragment:map_fragment,map_pars_fragment:map_pars_fragment,map_particle_fragment:map_particle_fragment,map_particle_pars_fragment:map_particle_pars_fragment,metalnessmap_fragment:metalnessmap_fragment,metalnessmap_pars_fragment:metalnessmap_pars_fragment,morphinstance_vertex:morphinstance_vertex,morphcolor_vertex:morphcolor_vertex,morphnormal_vertex:morphnormal_vertex,morphtarget_pars_vertex:morphtarget_pars_vertex,morphtarget_vertex:morphtarget_vertex,normal_fragment_begin:normal_fragment_begin,normal_fragment_maps:normal_fragment_maps,normal_pars_fragment:normal_pars_fragment,normal_pars_vertex:normal_pars_vertex,normal_vertex:normal_vertex,normalmap_pars_fragment:normalmap_pars_fragment,clearcoat_normal_fragment_begin:clearcoat_normal_fragment_begin,clearcoat_normal_fragment_maps:clearcoat_normal_fragment_maps,clearcoat_pars_fragment:clearcoat_pars_fragment,iridescence_pars_fragment:iridescence_pars_fragment,opaque_fragment:opaque_fragment,packing:packing,premultiplied_alpha_fragment:premultiplied_alpha_fragment,project_vertex:project_vertex,dithering_fragment:dithering_fragment,dithering_pars_fragment:dithering_pars_fragment,roughnessmap_fragment:roughnessmap_fragment,roughnessmap_pars_fragment:roughnessmap_pars_fragment,shadowmap_pars_fragment:shadowmap_pars_fragment,shadowmap_pars_vertex:shadowmap_pars_vertex,shadowmap_vertex:shadowmap_vertex,shadowmask_pars_fragment:shadowmask_pars_fragment,skinbase_vertex:skinbase_vertex,skinning_pars_vertex:skinning_pars_vertex,skinning_vertex:skinning_vertex,skinnormal_vertex:skinnormal_vertex,specularmap_fragment:specularmap_fragment,specularmap_pars_fragment:specularmap_pars_fragment,tonemapping_fragment:tonemapping_fragment,tonemapping_pars_fragment:tonemapping_pars_fragment,transmission_fragment:transmission_fragment,transmission_pars_fragment:transmission_pars_fragment,uv_pars_fragment:uv_pars_fragment,uv_pars_vertex:uv_pars_vertex,uv_vertex:uv_vertex,worldpos_vertex:worldpos_vertex,background_vert:vertex$h,background_frag:fragment$h,backgroundCube_vert:vertex$g,backgroundCube_frag:fragment$g,cube_vert:vertex$f,cube_frag:fragment$f,depth_vert:vertex$e,depth_frag:fragment$e,distanceRGBA_vert:vertex$d,distanceRGBA_frag:fragment$d,equirect_vert:vertex$c,equirect_frag:fragment$c,linedashed_vert:vertex$b,linedashed_frag:fragment$b,meshbasic_vert:vertex$a,meshbasic_frag:fragment$a,meshlambert_vert:vertex$9,meshlambert_frag:fragment$9,meshmatcap_vert:vertex$8,meshmatcap_frag:fragment$8,meshnormal_vert:vertex$7,meshnormal_frag:fragment$7,meshphong_vert:vertex$6,meshphong_frag:fragment$6,meshphysical_vert:vertex$5,meshphysical_frag:fragment$5,meshtoon_vert:vertex$4,meshtoon_frag:fragment$4,points_vert:vertex$3,points_frag:fragment$3,shadow_vert:vertex$2,shadow_frag:fragment$2,sprite_vert:vertex$1,sprite_frag:fragment$1};/**
 * Uniforms library for shared webgl shaders
 */const UniformsLib={common:{diffuse:{value:/*@__PURE__*/new Color$1(0xffffff)},opacity:{value:1.0},map:{value:null},mapTransform:{value:/*@__PURE__*/new Matrix3$1()},alphaMap:{value:null},alphaMapTransform:{value:/*@__PURE__*/new Matrix3$1()},alphaTest:{value:0}},specularmap:{specularMap:{value:null},specularMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},envmap:{envMap:{value:null},envMapRotation:{value:/*@__PURE__*/new Matrix3$1()},flipEnvMap:{value:-1},reflectivity:{value:1.0},// basic, lambert, phong
ior:{value:1.5},// physical
refractionRatio:{value:0.98}// basic, lambert, phong
},aomap:{aoMap:{value:null},aoMapIntensity:{value:1},aoMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},lightmap:{lightMap:{value:null},lightMapIntensity:{value:1},lightMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},bumpmap:{bumpMap:{value:null},bumpMapTransform:{value:/*@__PURE__*/new Matrix3$1()},bumpScale:{value:1}},normalmap:{normalMap:{value:null},normalMapTransform:{value:/*@__PURE__*/new Matrix3$1()},normalScale:{value:/*@__PURE__*/new Vector2$1(1,1)}},displacementmap:{displacementMap:{value:null},displacementMapTransform:{value:/*@__PURE__*/new Matrix3$1()},displacementScale:{value:1},displacementBias:{value:0}},emissivemap:{emissiveMap:{value:null},emissiveMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},metalnessmap:{metalnessMap:{value:null},metalnessMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},roughnessmap:{roughnessMap:{value:null},roughnessMapTransform:{value:/*@__PURE__*/new Matrix3$1()}},gradientmap:{gradientMap:{value:null}},fog:{fogDensity:{value:0.00025},fogNear:{value:1},fogFar:{value:2000},fogColor:{value:/*@__PURE__*/new Color$1(0xffffff)}},lights:{ambientLightColor:{value:[]},lightProbe:{value:[]},directionalLights:{value:[],properties:{direction:{},color:{}}},directionalLightShadows:{value:[],properties:{shadowIntensity:1,shadowBias:{},shadowNormalBias:{},shadowRadius:{},shadowMapSize:{}}},directionalShadowMap:{value:[]},directionalShadowMatrix:{value:[]},spotLights:{value:[],properties:{color:{},position:{},direction:{},distance:{},coneCos:{},penumbraCos:{},decay:{}}},spotLightShadows:{value:[],properties:{shadowIntensity:1,shadowBias:{},shadowNormalBias:{},shadowRadius:{},shadowMapSize:{}}},spotLightMap:{value:[]},spotShadowMap:{value:[]},spotLightMatrix:{value:[]},pointLights:{value:[],properties:{color:{},position:{},decay:{},distance:{}}},pointLightShadows:{value:[],properties:{shadowIntensity:1,shadowBias:{},shadowNormalBias:{},shadowRadius:{},shadowMapSize:{},shadowCameraNear:{},shadowCameraFar:{}}},pointShadowMap:{value:[]},pointShadowMatrix:{value:[]},hemisphereLights:{value:[],properties:{direction:{},skyColor:{},groundColor:{}}},// TODO (abelnation): RectAreaLight BRDF data needs to be moved from example to main src
rectAreaLights:{value:[],properties:{color:{},position:{},width:{},height:{}}},ltc_1:{value:null},ltc_2:{value:null}},points:{diffuse:{value:/*@__PURE__*/new Color$1(0xffffff)},opacity:{value:1.0},size:{value:1.0},scale:{value:1.0},map:{value:null},alphaMap:{value:null},alphaMapTransform:{value:/*@__PURE__*/new Matrix3$1()},alphaTest:{value:0},uvTransform:{value:/*@__PURE__*/new Matrix3$1()}},sprite:{diffuse:{value:/*@__PURE__*/new Color$1(0xffffff)},opacity:{value:1.0},center:{value:/*@__PURE__*/new Vector2$1(0.5,0.5)},rotation:{value:0.0},map:{value:null},mapTransform:{value:/*@__PURE__*/new Matrix3$1()},alphaMap:{value:null},alphaMapTransform:{value:/*@__PURE__*/new Matrix3$1()},alphaTest:{value:0}}};const ShaderLib={basic:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.specularmap,UniformsLib.envmap,UniformsLib.aomap,UniformsLib.lightmap,UniformsLib.fog]),vertexShader:ShaderChunk.meshbasic_vert,fragmentShader:ShaderChunk.meshbasic_frag},lambert:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.specularmap,UniformsLib.envmap,UniformsLib.aomap,UniformsLib.lightmap,UniformsLib.emissivemap,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,UniformsLib.fog,UniformsLib.lights,{emissive:{value:/*@__PURE__*/new Color$1(0x000000)}}]),vertexShader:ShaderChunk.meshlambert_vert,fragmentShader:ShaderChunk.meshlambert_frag},phong:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.specularmap,UniformsLib.envmap,UniformsLib.aomap,UniformsLib.lightmap,UniformsLib.emissivemap,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,UniformsLib.fog,UniformsLib.lights,{emissive:{value:/*@__PURE__*/new Color$1(0x000000)},specular:{value:/*@__PURE__*/new Color$1(0x111111)},shininess:{value:30}}]),vertexShader:ShaderChunk.meshphong_vert,fragmentShader:ShaderChunk.meshphong_frag},standard:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.envmap,UniformsLib.aomap,UniformsLib.lightmap,UniformsLib.emissivemap,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,UniformsLib.roughnessmap,UniformsLib.metalnessmap,UniformsLib.fog,UniformsLib.lights,{emissive:{value:/*@__PURE__*/new Color$1(0x000000)},roughness:{value:1.0},metalness:{value:0.0},envMapIntensity:{value:1}}]),vertexShader:ShaderChunk.meshphysical_vert,fragmentShader:ShaderChunk.meshphysical_frag},toon:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.aomap,UniformsLib.lightmap,UniformsLib.emissivemap,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,UniformsLib.gradientmap,UniformsLib.fog,UniformsLib.lights,{emissive:{value:/*@__PURE__*/new Color$1(0x000000)}}]),vertexShader:ShaderChunk.meshtoon_vert,fragmentShader:ShaderChunk.meshtoon_frag},matcap:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,UniformsLib.fog,{matcap:{value:null}}]),vertexShader:ShaderChunk.meshmatcap_vert,fragmentShader:ShaderChunk.meshmatcap_frag},points:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.points,UniformsLib.fog]),vertexShader:ShaderChunk.points_vert,fragmentShader:ShaderChunk.points_frag},dashed:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.fog,{scale:{value:1},dashSize:{value:1},totalSize:{value:2}}]),vertexShader:ShaderChunk.linedashed_vert,fragmentShader:ShaderChunk.linedashed_frag},depth:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.displacementmap]),vertexShader:ShaderChunk.depth_vert,fragmentShader:ShaderChunk.depth_frag},normal:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.bumpmap,UniformsLib.normalmap,UniformsLib.displacementmap,{opacity:{value:1.0}}]),vertexShader:ShaderChunk.meshnormal_vert,fragmentShader:ShaderChunk.meshnormal_frag},sprite:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.sprite,UniformsLib.fog]),vertexShader:ShaderChunk.sprite_vert,fragmentShader:ShaderChunk.sprite_frag},background:{uniforms:{uvTransform:{value:/*@__PURE__*/new Matrix3$1()},t2D:{value:null},backgroundIntensity:{value:1}},vertexShader:ShaderChunk.background_vert,fragmentShader:ShaderChunk.background_frag},backgroundCube:{uniforms:{envMap:{value:null},flipEnvMap:{value:-1},backgroundBlurriness:{value:0},backgroundIntensity:{value:1},backgroundRotation:{value:/*@__PURE__*/new Matrix3$1()}},vertexShader:ShaderChunk.backgroundCube_vert,fragmentShader:ShaderChunk.backgroundCube_frag},cube:{uniforms:{tCube:{value:null},tFlip:{value:-1},opacity:{value:1.0}},vertexShader:ShaderChunk.cube_vert,fragmentShader:ShaderChunk.cube_frag},equirect:{uniforms:{tEquirect:{value:null}},vertexShader:ShaderChunk.equirect_vert,fragmentShader:ShaderChunk.equirect_frag},distanceRGBA:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.common,UniformsLib.displacementmap,{referencePosition:{value:/*@__PURE__*/new Vector3$1()},nearDistance:{value:1},farDistance:{value:1000}}]),vertexShader:ShaderChunk.distanceRGBA_vert,fragmentShader:ShaderChunk.distanceRGBA_frag},shadow:{uniforms:/*@__PURE__*/mergeUniforms([UniformsLib.lights,UniformsLib.fog,{color:{value:/*@__PURE__*/new Color$1(0x00000)},opacity:{value:1.0}}]),vertexShader:ShaderChunk.shadow_vert,fragmentShader:ShaderChunk.shadow_frag}};ShaderLib.physical={uniforms:/*@__PURE__*/mergeUniforms([ShaderLib.standard.uniforms,{clearcoat:{value:0},clearcoatMap:{value:null},clearcoatMapTransform:{value:/*@__PURE__*/new Matrix3$1()},clearcoatNormalMap:{value:null},clearcoatNormalMapTransform:{value:/*@__PURE__*/new Matrix3$1()},clearcoatNormalScale:{value:/*@__PURE__*/new Vector2$1(1,1)},clearcoatRoughness:{value:0},clearcoatRoughnessMap:{value:null},clearcoatRoughnessMapTransform:{value:/*@__PURE__*/new Matrix3$1()},dispersion:{value:0},iridescence:{value:0},iridescenceMap:{value:null},iridescenceMapTransform:{value:/*@__PURE__*/new Matrix3$1()},iridescenceIOR:{value:1.3},iridescenceThicknessMinimum:{value:100},iridescenceThicknessMaximum:{value:400},iridescenceThicknessMap:{value:null},iridescenceThicknessMapTransform:{value:/*@__PURE__*/new Matrix3$1()},sheen:{value:0},sheenColor:{value:/*@__PURE__*/new Color$1(0x000000)},sheenColorMap:{value:null},sheenColorMapTransform:{value:/*@__PURE__*/new Matrix3$1()},sheenRoughness:{value:1},sheenRoughnessMap:{value:null},sheenRoughnessMapTransform:{value:/*@__PURE__*/new Matrix3$1()},transmission:{value:0},transmissionMap:{value:null},transmissionMapTransform:{value:/*@__PURE__*/new Matrix3$1()},transmissionSamplerSize:{value:/*@__PURE__*/new Vector2$1()},transmissionSamplerMap:{value:null},thickness:{value:0},thicknessMap:{value:null},thicknessMapTransform:{value:/*@__PURE__*/new Matrix3$1()},attenuationDistance:{value:0},attenuationColor:{value:/*@__PURE__*/new Color$1(0x000000)},specularColor:{value:/*@__PURE__*/new Color$1(1,1,1)},specularColorMap:{value:null},specularColorMapTransform:{value:/*@__PURE__*/new Matrix3$1()},specularIntensity:{value:1},specularIntensityMap:{value:null},specularIntensityMapTransform:{value:/*@__PURE__*/new Matrix3$1()},anisotropyVector:{value:/*@__PURE__*/new Vector2$1()},anisotropyMap:{value:null},anisotropyMapTransform:{value:/*@__PURE__*/new Matrix3$1()}}]),vertexShader:ShaderChunk.meshphysical_vert,fragmentShader:ShaderChunk.meshphysical_frag};const _rgb={r:0,b:0,g:0};const _e1$1=/*@__PURE__*/new Euler$1();const _m1$1$1=/*@__PURE__*/new Matrix4$1();function WebGLBackground(renderer,cubemaps,cubeuvmaps,state,objects,alpha,premultipliedAlpha){const clearColor=new Color$1(0x000000);let clearAlpha=alpha===true?0:1;let planeMesh;let boxMesh;let currentBackground=null;let currentBackgroundVersion=0;let currentTonemapping=null;function getBackground(scene){let background=scene.isScene===true?scene.background:null;if(background&&background.isTexture){const usePMREM=scene.backgroundBlurriness>0;// use PMREM if the user wants to blur the background
background=(usePMREM?cubeuvmaps:cubemaps).get(background);}return background;}function render(scene){let forceClear=false;const background=getBackground(scene);if(background===null){setClear(clearColor,clearAlpha);}else if(background&&background.isColor){setClear(background,1);forceClear=true;}const environmentBlendMode=renderer.xr.getEnvironmentBlendMode();if(environmentBlendMode==='additive'){state.buffers.color.setClear(0,0,0,1,premultipliedAlpha);}else if(environmentBlendMode==='alpha-blend'){state.buffers.color.setClear(0,0,0,0,premultipliedAlpha);}if(renderer.autoClear||forceClear){// buffers might not be writable which is required to ensure a correct clear
state.buffers.depth.setTest(true);state.buffers.depth.setMask(true);state.buffers.color.setMask(true);renderer.clear(renderer.autoClearColor,renderer.autoClearDepth,renderer.autoClearStencil);}}function addToRenderList(renderList,scene){const background=getBackground(scene);if(background&&(background.isCubeTexture||background.mapping===CubeUVReflectionMapping)){if(boxMesh===undefined){boxMesh=new Mesh$1(new BoxGeometry(1,1,1),new ShaderMaterial({name:'BackgroundCubeMaterial',uniforms:cloneUniforms(ShaderLib.backgroundCube.uniforms),vertexShader:ShaderLib.backgroundCube.vertexShader,fragmentShader:ShaderLib.backgroundCube.fragmentShader,side:BackSide$1,depthTest:false,depthWrite:false,fog:false}));boxMesh.geometry.deleteAttribute('normal');boxMesh.geometry.deleteAttribute('uv');boxMesh.onBeforeRender=function(renderer,scene,camera){this.matrixWorld.copyPosition(camera.matrixWorld);};// add "envMap" material property so the renderer can evaluate it like for built-in materials
Object.defineProperty(boxMesh.material,'envMap',{get:function(){return this.uniforms.envMap.value;}});objects.update(boxMesh);}_e1$1.copy(scene.backgroundRotation);// accommodate left-handed frame
_e1$1.x*=-1;_e1$1.y*=-1;_e1$1.z*=-1;if(background.isCubeTexture&&background.isRenderTargetTexture===false){// environment maps which are not cube render targets or PMREMs follow a different convention
_e1$1.y*=-1;_e1$1.z*=-1;}boxMesh.material.uniforms.envMap.value=background;boxMesh.material.uniforms.flipEnvMap.value=background.isCubeTexture&&background.isRenderTargetTexture===false?-1:1;boxMesh.material.uniforms.backgroundBlurriness.value=scene.backgroundBlurriness;boxMesh.material.uniforms.backgroundIntensity.value=scene.backgroundIntensity;boxMesh.material.uniforms.backgroundRotation.value.setFromMatrix4(_m1$1$1.makeRotationFromEuler(_e1$1));boxMesh.material.toneMapped=ColorManagement$1.getTransfer(background.colorSpace)!==SRGBTransfer$1;if(currentBackground!==background||currentBackgroundVersion!==background.version||currentTonemapping!==renderer.toneMapping){boxMesh.material.needsUpdate=true;currentBackground=background;currentBackgroundVersion=background.version;currentTonemapping=renderer.toneMapping;}boxMesh.layers.enableAll();// push to the pre-sorted opaque render list
renderList.unshift(boxMesh,boxMesh.geometry,boxMesh.material,0,0,null);}else if(background&&background.isTexture){if(planeMesh===undefined){planeMesh=new Mesh$1(new PlaneGeometry(2,2),new ShaderMaterial({name:'BackgroundMaterial',uniforms:cloneUniforms(ShaderLib.background.uniforms),vertexShader:ShaderLib.background.vertexShader,fragmentShader:ShaderLib.background.fragmentShader,side:FrontSide$1,depthTest:false,depthWrite:false,fog:false}));planeMesh.geometry.deleteAttribute('normal');// add "map" material property so the renderer can evaluate it like for built-in materials
Object.defineProperty(planeMesh.material,'map',{get:function(){return this.uniforms.t2D.value;}});objects.update(planeMesh);}planeMesh.material.uniforms.t2D.value=background;planeMesh.material.uniforms.backgroundIntensity.value=scene.backgroundIntensity;planeMesh.material.toneMapped=ColorManagement$1.getTransfer(background.colorSpace)!==SRGBTransfer$1;if(background.matrixAutoUpdate===true){background.updateMatrix();}planeMesh.material.uniforms.uvTransform.value.copy(background.matrix);if(currentBackground!==background||currentBackgroundVersion!==background.version||currentTonemapping!==renderer.toneMapping){planeMesh.material.needsUpdate=true;currentBackground=background;currentBackgroundVersion=background.version;currentTonemapping=renderer.toneMapping;}planeMesh.layers.enableAll();// push to the pre-sorted opaque render list
renderList.unshift(planeMesh,planeMesh.geometry,planeMesh.material,0,0,null);}}function setClear(color,alpha){color.getRGB(_rgb,getUnlitUniformColorSpace(renderer));state.buffers.color.setClear(_rgb.r,_rgb.g,_rgb.b,alpha,premultipliedAlpha);}return {getClearColor:function(){return clearColor;},setClearColor:function(color,alpha=1){clearColor.set(color);clearAlpha=alpha;setClear(clearColor,clearAlpha);},getClearAlpha:function(){return clearAlpha;},setClearAlpha:function(alpha){clearAlpha=alpha;setClear(clearColor,clearAlpha);},render:render,addToRenderList:addToRenderList};}function WebGLBindingStates(gl,attributes){const maxVertexAttributes=gl.getParameter(gl.MAX_VERTEX_ATTRIBS);const bindingStates={};const defaultState=createBindingState(null);let currentState=defaultState;let forceUpdate=false;function setup(object,material,program,geometry,index){let updateBuffers=false;const state=getBindingState(geometry,program,material);if(currentState!==state){currentState=state;bindVertexArrayObject(currentState.object);}updateBuffers=needsUpdate(object,geometry,program,index);if(updateBuffers)saveCache(object,geometry,program,index);if(index!==null){attributes.update(index,gl.ELEMENT_ARRAY_BUFFER);}if(updateBuffers||forceUpdate){forceUpdate=false;setupVertexAttributes(object,material,program,geometry);if(index!==null){gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,attributes.get(index).buffer);}}}function createVertexArrayObject(){return gl.createVertexArray();}function bindVertexArrayObject(vao){return gl.bindVertexArray(vao);}function deleteVertexArrayObject(vao){return gl.deleteVertexArray(vao);}function getBindingState(geometry,program,material){const wireframe=material.wireframe===true;let programMap=bindingStates[geometry.id];if(programMap===undefined){programMap={};bindingStates[geometry.id]=programMap;}let stateMap=programMap[program.id];if(stateMap===undefined){stateMap={};programMap[program.id]=stateMap;}let state=stateMap[wireframe];if(state===undefined){state=createBindingState(createVertexArrayObject());stateMap[wireframe]=state;}return state;}function createBindingState(vao){const newAttributes=[];const enabledAttributes=[];const attributeDivisors=[];for(let i=0;i<maxVertexAttributes;i++){newAttributes[i]=0;enabledAttributes[i]=0;attributeDivisors[i]=0;}return {// for backward compatibility on non-VAO support browser
geometry:null,program:null,wireframe:false,newAttributes:newAttributes,enabledAttributes:enabledAttributes,attributeDivisors:attributeDivisors,object:vao,attributes:{},index:null};}function needsUpdate(object,geometry,program,index){const cachedAttributes=currentState.attributes;const geometryAttributes=geometry.attributes;let attributesNum=0;const programAttributes=program.getAttributes();for(const name in programAttributes){const programAttribute=programAttributes[name];if(programAttribute.location>=0){const cachedAttribute=cachedAttributes[name];let geometryAttribute=geometryAttributes[name];if(geometryAttribute===undefined){if(name==='instanceMatrix'&&object.instanceMatrix)geometryAttribute=object.instanceMatrix;if(name==='instanceColor'&&object.instanceColor)geometryAttribute=object.instanceColor;}if(cachedAttribute===undefined)return true;if(cachedAttribute.attribute!==geometryAttribute)return true;if(geometryAttribute&&cachedAttribute.data!==geometryAttribute.data)return true;attributesNum++;}}if(currentState.attributesNum!==attributesNum)return true;if(currentState.index!==index)return true;return false;}function saveCache(object,geometry,program,index){const cache={};const attributes=geometry.attributes;let attributesNum=0;const programAttributes=program.getAttributes();for(const name in programAttributes){const programAttribute=programAttributes[name];if(programAttribute.location>=0){let attribute=attributes[name];if(attribute===undefined){if(name==='instanceMatrix'&&object.instanceMatrix)attribute=object.instanceMatrix;if(name==='instanceColor'&&object.instanceColor)attribute=object.instanceColor;}const data={};data.attribute=attribute;if(attribute&&attribute.data){data.data=attribute.data;}cache[name]=data;attributesNum++;}}currentState.attributes=cache;currentState.attributesNum=attributesNum;currentState.index=index;}function initAttributes(){const newAttributes=currentState.newAttributes;for(let i=0,il=newAttributes.length;i<il;i++){newAttributes[i]=0;}}function enableAttribute(attribute){enableAttributeAndDivisor(attribute,0);}function enableAttributeAndDivisor(attribute,meshPerAttribute){const newAttributes=currentState.newAttributes;const enabledAttributes=currentState.enabledAttributes;const attributeDivisors=currentState.attributeDivisors;newAttributes[attribute]=1;if(enabledAttributes[attribute]===0){gl.enableVertexAttribArray(attribute);enabledAttributes[attribute]=1;}if(attributeDivisors[attribute]!==meshPerAttribute){gl.vertexAttribDivisor(attribute,meshPerAttribute);attributeDivisors[attribute]=meshPerAttribute;}}function disableUnusedAttributes(){const newAttributes=currentState.newAttributes;const enabledAttributes=currentState.enabledAttributes;for(let i=0,il=enabledAttributes.length;i<il;i++){if(enabledAttributes[i]!==newAttributes[i]){gl.disableVertexAttribArray(i);enabledAttributes[i]=0;}}}function vertexAttribPointer(index,size,type,normalized,stride,offset,integer){if(integer===true){gl.vertexAttribIPointer(index,size,type,stride,offset);}else {gl.vertexAttribPointer(index,size,type,normalized,stride,offset);}}function setupVertexAttributes(object,material,program,geometry){initAttributes();const geometryAttributes=geometry.attributes;const programAttributes=program.getAttributes();const materialDefaultAttributeValues=material.defaultAttributeValues;for(const name in programAttributes){const programAttribute=programAttributes[name];if(programAttribute.location>=0){let geometryAttribute=geometryAttributes[name];if(geometryAttribute===undefined){if(name==='instanceMatrix'&&object.instanceMatrix)geometryAttribute=object.instanceMatrix;if(name==='instanceColor'&&object.instanceColor)geometryAttribute=object.instanceColor;}if(geometryAttribute!==undefined){const normalized=geometryAttribute.normalized;const size=geometryAttribute.itemSize;const attribute=attributes.get(geometryAttribute);// TODO Attribute may not be available on context restore
if(attribute===undefined)continue;const buffer=attribute.buffer;const type=attribute.type;const bytesPerElement=attribute.bytesPerElement;// check for integer attributes
const integer=type===gl.INT||type===gl.UNSIGNED_INT||geometryAttribute.gpuType===IntType$1;if(geometryAttribute.isInterleavedBufferAttribute){const data=geometryAttribute.data;const stride=data.stride;const offset=geometryAttribute.offset;if(data.isInstancedInterleavedBuffer){for(let i=0;i<programAttribute.locationSize;i++){enableAttributeAndDivisor(programAttribute.location+i,data.meshPerAttribute);}if(object.isInstancedMesh!==true&&geometry._maxInstanceCount===undefined){geometry._maxInstanceCount=data.meshPerAttribute*data.count;}}else {for(let i=0;i<programAttribute.locationSize;i++){enableAttribute(programAttribute.location+i);}}gl.bindBuffer(gl.ARRAY_BUFFER,buffer);for(let i=0;i<programAttribute.locationSize;i++){vertexAttribPointer(programAttribute.location+i,size/programAttribute.locationSize,type,normalized,stride*bytesPerElement,(offset+size/programAttribute.locationSize*i)*bytesPerElement,integer);}}else {if(geometryAttribute.isInstancedBufferAttribute){for(let i=0;i<programAttribute.locationSize;i++){enableAttributeAndDivisor(programAttribute.location+i,geometryAttribute.meshPerAttribute);}if(object.isInstancedMesh!==true&&geometry._maxInstanceCount===undefined){geometry._maxInstanceCount=geometryAttribute.meshPerAttribute*geometryAttribute.count;}}else {for(let i=0;i<programAttribute.locationSize;i++){enableAttribute(programAttribute.location+i);}}gl.bindBuffer(gl.ARRAY_BUFFER,buffer);for(let i=0;i<programAttribute.locationSize;i++){vertexAttribPointer(programAttribute.location+i,size/programAttribute.locationSize,type,normalized,size*bytesPerElement,size/programAttribute.locationSize*i*bytesPerElement,integer);}}}else if(materialDefaultAttributeValues!==undefined){const value=materialDefaultAttributeValues[name];if(value!==undefined){switch(value.length){case 2:gl.vertexAttrib2fv(programAttribute.location,value);break;case 3:gl.vertexAttrib3fv(programAttribute.location,value);break;case 4:gl.vertexAttrib4fv(programAttribute.location,value);break;default:gl.vertexAttrib1fv(programAttribute.location,value);}}}}}disableUnusedAttributes();}function dispose(){reset();for(const geometryId in bindingStates){const programMap=bindingStates[geometryId];for(const programId in programMap){const stateMap=programMap[programId];for(const wireframe in stateMap){deleteVertexArrayObject(stateMap[wireframe].object);delete stateMap[wireframe];}delete programMap[programId];}delete bindingStates[geometryId];}}function releaseStatesOfGeometry(geometry){if(bindingStates[geometry.id]===undefined)return;const programMap=bindingStates[geometry.id];for(const programId in programMap){const stateMap=programMap[programId];for(const wireframe in stateMap){deleteVertexArrayObject(stateMap[wireframe].object);delete stateMap[wireframe];}delete programMap[programId];}delete bindingStates[geometry.id];}function releaseStatesOfProgram(program){for(const geometryId in bindingStates){const programMap=bindingStates[geometryId];if(programMap[program.id]===undefined)continue;const stateMap=programMap[program.id];for(const wireframe in stateMap){deleteVertexArrayObject(stateMap[wireframe].object);delete stateMap[wireframe];}delete programMap[program.id];}}function reset(){resetDefaultState();forceUpdate=true;if(currentState===defaultState)return;currentState=defaultState;bindVertexArrayObject(currentState.object);}// for backward-compatibility
function resetDefaultState(){defaultState.geometry=null;defaultState.program=null;defaultState.wireframe=false;}return {setup:setup,reset:reset,resetDefaultState:resetDefaultState,dispose:dispose,releaseStatesOfGeometry:releaseStatesOfGeometry,releaseStatesOfProgram:releaseStatesOfProgram,initAttributes:initAttributes,enableAttribute:enableAttribute,disableUnusedAttributes:disableUnusedAttributes};}function WebGLBufferRenderer(gl,extensions,info){let mode;function setMode(value){mode=value;}function render(start,count){gl.drawArrays(mode,start,count);info.update(count,mode,1);}function renderInstances(start,count,primcount){if(primcount===0)return;gl.drawArraysInstanced(mode,start,count,primcount);info.update(count,mode,primcount);}function renderMultiDraw(starts,counts,drawCount){if(drawCount===0)return;const extension=extensions.get('WEBGL_multi_draw');extension.multiDrawArraysWEBGL(mode,starts,0,counts,0,drawCount);let elementCount=0;for(let i=0;i<drawCount;i++){elementCount+=counts[i];}info.update(elementCount,mode,1);}function renderMultiDrawInstances(starts,counts,drawCount,primcount){if(drawCount===0)return;const extension=extensions.get('WEBGL_multi_draw');if(extension===null){for(let i=0;i<starts.length;i++){renderInstances(starts[i],counts[i],primcount[i]);}}else {extension.multiDrawArraysInstancedWEBGL(mode,starts,0,counts,0,primcount,0,drawCount);let elementCount=0;for(let i=0;i<drawCount;i++){elementCount+=counts[i];}for(let i=0;i<primcount.length;i++){info.update(elementCount,mode,primcount[i]);}}}//
this.setMode=setMode;this.render=render;this.renderInstances=renderInstances;this.renderMultiDraw=renderMultiDraw;this.renderMultiDrawInstances=renderMultiDrawInstances;}function WebGLCapabilities(gl,extensions,parameters,utils){let maxAnisotropy;function getMaxAnisotropy(){if(maxAnisotropy!==undefined)return maxAnisotropy;if(extensions.has('EXT_texture_filter_anisotropic')===true){const extension=extensions.get('EXT_texture_filter_anisotropic');maxAnisotropy=gl.getParameter(extension.MAX_TEXTURE_MAX_ANISOTROPY_EXT);}else {maxAnisotropy=0;}return maxAnisotropy;}function textureFormatReadable(textureFormat){if(textureFormat!==RGBAFormat$1&&utils.convert(textureFormat)!==gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_FORMAT)){return false;}return true;}function textureTypeReadable(textureType){const halfFloatSupportedByExt=textureType===HalfFloatType$1&&(extensions.has('EXT_color_buffer_half_float')||extensions.has('EXT_color_buffer_float'));if(textureType!==UnsignedByteType$1&&utils.convert(textureType)!==gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_TYPE)&&// Edge and Chrome Mac < 52 (#9513)
textureType!==FloatType$1&&!halfFloatSupportedByExt){return false;}return true;}function getMaxPrecision(precision){if(precision==='highp'){if(gl.getShaderPrecisionFormat(gl.VERTEX_SHADER,gl.HIGH_FLOAT).precision>0&&gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER,gl.HIGH_FLOAT).precision>0){return 'highp';}precision='mediump';}if(precision==='mediump'){if(gl.getShaderPrecisionFormat(gl.VERTEX_SHADER,gl.MEDIUM_FLOAT).precision>0&&gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER,gl.MEDIUM_FLOAT).precision>0){return 'mediump';}}return 'lowp';}let precision=parameters.precision!==undefined?parameters.precision:'highp';const maxPrecision=getMaxPrecision(precision);if(maxPrecision!==precision){console.warn('THREE.WebGLRenderer:',precision,'not supported, using',maxPrecision,'instead.');precision=maxPrecision;}const logarithmicDepthBuffer=parameters.logarithmicDepthBuffer===true;const maxTextures=gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);const maxVertexTextures=gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS);const maxTextureSize=gl.getParameter(gl.MAX_TEXTURE_SIZE);const maxCubemapSize=gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE);const maxAttributes=gl.getParameter(gl.MAX_VERTEX_ATTRIBS);const maxVertexUniforms=gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS);const maxVaryings=gl.getParameter(gl.MAX_VARYING_VECTORS);const maxFragmentUniforms=gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS);const vertexTextures=maxVertexTextures>0;const maxSamples=gl.getParameter(gl.MAX_SAMPLES);return {isWebGL2:true,// keeping this for backwards compatibility
getMaxAnisotropy:getMaxAnisotropy,getMaxPrecision:getMaxPrecision,textureFormatReadable:textureFormatReadable,textureTypeReadable:textureTypeReadable,precision:precision,logarithmicDepthBuffer:logarithmicDepthBuffer,maxTextures:maxTextures,maxVertexTextures:maxVertexTextures,maxTextureSize:maxTextureSize,maxCubemapSize:maxCubemapSize,maxAttributes:maxAttributes,maxVertexUniforms:maxVertexUniforms,maxVaryings:maxVaryings,maxFragmentUniforms:maxFragmentUniforms,vertexTextures:vertexTextures,maxSamples:maxSamples};}function WebGLClipping(properties){const scope=this;let globalState=null,numGlobalPlanes=0,localClippingEnabled=false,renderingShadows=false;const plane=new Plane$1(),viewNormalMatrix=new Matrix3$1(),uniform={value:null,needsUpdate:false};this.uniform=uniform;this.numPlanes=0;this.numIntersection=0;this.init=function(planes,enableLocalClipping){const enabled=planes.length!==0||enableLocalClipping||// enable state of previous frame - the clipping code has to
// run another frame in order to reset the state:
numGlobalPlanes!==0||localClippingEnabled;localClippingEnabled=enableLocalClipping;numGlobalPlanes=planes.length;return enabled;};this.beginShadows=function(){renderingShadows=true;projectPlanes(null);};this.endShadows=function(){renderingShadows=false;};this.setGlobalState=function(planes,camera){globalState=projectPlanes(planes,camera,0);};this.setState=function(material,camera,useCache){const planes=material.clippingPlanes,clipIntersection=material.clipIntersection,clipShadows=material.clipShadows;const materialProperties=properties.get(material);if(!localClippingEnabled||planes===null||planes.length===0||renderingShadows&&!clipShadows){// there's no local clipping
if(renderingShadows){// there's no global clipping
projectPlanes(null);}else {resetGlobalState();}}else {const nGlobal=renderingShadows?0:numGlobalPlanes,lGlobal=nGlobal*4;let dstArray=materialProperties.clippingState||null;uniform.value=dstArray;// ensure unique state
dstArray=projectPlanes(planes,camera,lGlobal,useCache);for(let i=0;i!==lGlobal;++i){dstArray[i]=globalState[i];}materialProperties.clippingState=dstArray;this.numIntersection=clipIntersection?this.numPlanes:0;this.numPlanes+=nGlobal;}};function resetGlobalState(){if(uniform.value!==globalState){uniform.value=globalState;uniform.needsUpdate=numGlobalPlanes>0;}scope.numPlanes=numGlobalPlanes;scope.numIntersection=0;}function projectPlanes(planes,camera,dstOffset,skipTransform){const nPlanes=planes!==null?planes.length:0;let dstArray=null;if(nPlanes!==0){dstArray=uniform.value;if(skipTransform!==true||dstArray===null){const flatSize=dstOffset+nPlanes*4,viewMatrix=camera.matrixWorldInverse;viewNormalMatrix.getNormalMatrix(viewMatrix);if(dstArray===null||dstArray.length<flatSize){dstArray=new Float32Array(flatSize);}for(let i=0,i4=dstOffset;i!==nPlanes;++i,i4+=4){plane.copy(planes[i]).applyMatrix4(viewMatrix,viewNormalMatrix);plane.normal.toArray(dstArray,i4);dstArray[i4+3]=plane.constant;}}uniform.value=dstArray;uniform.needsUpdate=true;}scope.numPlanes=nPlanes;scope.numIntersection=0;return dstArray;}}function WebGLCubeMaps(renderer){let cubemaps=new WeakMap();function mapTextureMapping(texture,mapping){if(mapping===EquirectangularReflectionMapping){texture.mapping=CubeReflectionMapping$1;}else if(mapping===EquirectangularRefractionMapping){texture.mapping=CubeRefractionMapping$1;}return texture;}function get(texture){if(texture&&texture.isTexture){const mapping=texture.mapping;if(mapping===EquirectangularReflectionMapping||mapping===EquirectangularRefractionMapping){if(cubemaps.has(texture)){const cubemap=cubemaps.get(texture).texture;return mapTextureMapping(cubemap,texture.mapping);}else {const image=texture.image;if(image&&image.height>0){const renderTarget=new WebGLCubeRenderTarget(image.height);renderTarget.fromEquirectangularTexture(renderer,texture);cubemaps.set(texture,renderTarget);texture.addEventListener('dispose',onTextureDispose);return mapTextureMapping(renderTarget.texture,texture.mapping);}else {// image not yet ready. try the conversion next frame
return null;}}}}return texture;}function onTextureDispose(event){const texture=event.target;texture.removeEventListener('dispose',onTextureDispose);const cubemap=cubemaps.get(texture);if(cubemap!==undefined){cubemaps.delete(texture);cubemap.dispose();}}function dispose(){cubemaps=new WeakMap();}return {get:get,dispose:dispose};}class OrthographicCamera$1 extends Camera$1{constructor(left=-1,right=1,top=1,bottom=-1,near=0.1,far=2000){super();this.isOrthographicCamera=true;this.type='OrthographicCamera';this.zoom=1;this.view=null;this.left=left;this.right=right;this.top=top;this.bottom=bottom;this.near=near;this.far=far;this.updateProjectionMatrix();}copy(source,recursive){super.copy(source,recursive);this.left=source.left;this.right=source.right;this.top=source.top;this.bottom=source.bottom;this.near=source.near;this.far=source.far;this.zoom=source.zoom;this.view=source.view===null?null:Object.assign({},source.view);return this;}setViewOffset(fullWidth,fullHeight,x,y,width,height){if(this.view===null){this.view={enabled:true,fullWidth:1,fullHeight:1,offsetX:0,offsetY:0,width:1,height:1};}this.view.enabled=true;this.view.fullWidth=fullWidth;this.view.fullHeight=fullHeight;this.view.offsetX=x;this.view.offsetY=y;this.view.width=width;this.view.height=height;this.updateProjectionMatrix();}clearViewOffset(){if(this.view!==null){this.view.enabled=false;}this.updateProjectionMatrix();}updateProjectionMatrix(){const dx=(this.right-this.left)/(2*this.zoom);const dy=(this.top-this.bottom)/(2*this.zoom);const cx=(this.right+this.left)/2;const cy=(this.top+this.bottom)/2;let left=cx-dx;let right=cx+dx;let top=cy+dy;let bottom=cy-dy;if(this.view!==null&&this.view.enabled){const scaleW=(this.right-this.left)/this.view.fullWidth/this.zoom;const scaleH=(this.top-this.bottom)/this.view.fullHeight/this.zoom;left+=scaleW*this.view.offsetX;right=left+scaleW*this.view.width;top-=scaleH*this.view.offsetY;bottom=top-scaleH*this.view.height;}this.projectionMatrix.makeOrthographic(left,right,top,bottom,this.near,this.far,this.coordinateSystem);this.projectionMatrixInverse.copy(this.projectionMatrix).invert();}toJSON(meta){const data=super.toJSON(meta);data.object.zoom=this.zoom;data.object.left=this.left;data.object.right=this.right;data.object.top=this.top;data.object.bottom=this.bottom;data.object.near=this.near;data.object.far=this.far;if(this.view!==null)data.object.view=Object.assign({},this.view);return data;}}const LOD_MIN=4;// The standard deviations (radians) associated with the extra mips. These are
// chosen to approximate a Trowbridge-Reitz distribution function times the
// geometric shadowing function. These sigma values squared must match the
// variance #defines in cube_uv_reflection_fragment.glsl.js.
const EXTRA_LOD_SIGMA=[0.125,0.215,0.35,0.446,0.526,0.582];// The maximum length of the blur for loop. Smaller sigmas will use fewer
// samples and exit early, but not recompile the shader.
const MAX_SAMPLES=20;const _flatCamera=/*@__PURE__*/new OrthographicCamera$1();const _clearColor$1=/*@__PURE__*/new Color$1();let _oldTarget=null;let _oldActiveCubeFace=0;let _oldActiveMipmapLevel=0;let _oldXrEnabled=false;// Golden Ratio
const PHI=(1+Math.sqrt(5))/2;const INV_PHI=1/PHI;// Vertices of a dodecahedron (except the opposites, which represent the
// same axis), used as axis directions evenly spread on a sphere.
const _axisDirections=[/*@__PURE__*/new Vector3$1(-PHI,INV_PHI,0),/*@__PURE__*/new Vector3$1(PHI,INV_PHI,0),/*@__PURE__*/new Vector3$1(-INV_PHI,0,PHI),/*@__PURE__*/new Vector3$1(INV_PHI,0,PHI),/*@__PURE__*/new Vector3$1(0,PHI,-INV_PHI),/*@__PURE__*/new Vector3$1(0,PHI,INV_PHI),/*@__PURE__*/new Vector3$1(-1,1,-1),/*@__PURE__*/new Vector3$1(1,1,-1),/*@__PURE__*/new Vector3$1(-1,1,1),/*@__PURE__*/new Vector3$1(1,1,1)];/**
 * This class generates a Prefiltered, Mipmapped Radiance Environment Map
 * (PMREM) from a cubeMap environment texture. This allows different levels of
 * blur to be quickly accessed based on material roughness. It is packed into a
 * special CubeUV format that allows us to perform custom interpolation so that
 * we can support nonlinear formats such as RGBE. Unlike a traditional mipmap
 * chain, it only goes down to the LOD_MIN level (above), and then creates extra
 * even more filtered 'mips' at the same LOD_MIN resolution, associated with
 * higher roughness levels. In this way we maintain resolution to smoothly
 * interpolate diffuse lighting while limiting sampling computation.
 *
 * Paper: Fast, Accurate Image-Based Lighting
 * https://drive.google.com/file/d/15y8r_UpKlU9SvV4ILb0C3qCPecS8pvLz/view
*/class PMREMGenerator{constructor(renderer){this._renderer=renderer;this._pingPongRenderTarget=null;this._lodMax=0;this._cubeSize=0;this._lodPlanes=[];this._sizeLods=[];this._sigmas=[];this._blurMaterial=null;this._cubemapMaterial=null;this._equirectMaterial=null;this._compileMaterial(this._blurMaterial);}/**
	 * Generates a PMREM from a supplied Scene, which can be faster than using an
	 * image if networking bandwidth is low. Optional sigma specifies a blur radius
	 * in radians to be applied to the scene before PMREM generation. Optional near
	 * and far planes ensure the scene is rendered in its entirety (the cubeCamera
	 * is placed at the origin).
	 */fromScene(scene,sigma=0,near=0.1,far=100){_oldTarget=this._renderer.getRenderTarget();_oldActiveCubeFace=this._renderer.getActiveCubeFace();_oldActiveMipmapLevel=this._renderer.getActiveMipmapLevel();_oldXrEnabled=this._renderer.xr.enabled;this._renderer.xr.enabled=false;this._setSize(256);const cubeUVRenderTarget=this._allocateTargets();cubeUVRenderTarget.depthBuffer=true;this._sceneToCubeUV(scene,near,far,cubeUVRenderTarget);if(sigma>0){this._blur(cubeUVRenderTarget,0,0,sigma);}this._applyPMREM(cubeUVRenderTarget);this._cleanup(cubeUVRenderTarget);return cubeUVRenderTarget;}/**
	 * Generates a PMREM from an equirectangular texture, which can be either LDR
	 * or HDR. The ideal input image size is 1k (1024 x 512),
	 * as this matches best with the 256 x 256 cubemap output.
	 * The smallest supported equirectangular image size is 64 x 32.
	 */fromEquirectangular(equirectangular,renderTarget=null){return this._fromTexture(equirectangular,renderTarget);}/**
	 * Generates a PMREM from an cubemap texture, which can be either LDR
	 * or HDR. The ideal input cube size is 256 x 256,
	 * as this matches best with the 256 x 256 cubemap output.
	 * The smallest supported cube size is 16 x 16.
	 */fromCubemap(cubemap,renderTarget=null){return this._fromTexture(cubemap,renderTarget);}/**
	 * Pre-compiles the cubemap shader. You can get faster start-up by invoking this method during
	 * your texture's network fetch for increased concurrency.
	 */compileCubemapShader(){if(this._cubemapMaterial===null){this._cubemapMaterial=_getCubemapMaterial();this._compileMaterial(this._cubemapMaterial);}}/**
	 * Pre-compiles the equirectangular shader. You can get faster start-up by invoking this method during
	 * your texture's network fetch for increased concurrency.
	 */compileEquirectangularShader(){if(this._equirectMaterial===null){this._equirectMaterial=_getEquirectMaterial();this._compileMaterial(this._equirectMaterial);}}/**
	 * Disposes of the PMREMGenerator's internal memory. Note that PMREMGenerator is a static class,
	 * so you should not need more than one PMREMGenerator object. If you do, calling dispose() on
	 * one of them will cause any others to also become unusable.
	 */dispose(){this._dispose();if(this._cubemapMaterial!==null)this._cubemapMaterial.dispose();if(this._equirectMaterial!==null)this._equirectMaterial.dispose();}// private interface
_setSize(cubeSize){this._lodMax=Math.floor(Math.log2(cubeSize));this._cubeSize=Math.pow(2,this._lodMax);}_dispose(){if(this._blurMaterial!==null)this._blurMaterial.dispose();if(this._pingPongRenderTarget!==null)this._pingPongRenderTarget.dispose();for(let i=0;i<this._lodPlanes.length;i++){this._lodPlanes[i].dispose();}}_cleanup(outputTarget){this._renderer.setRenderTarget(_oldTarget,_oldActiveCubeFace,_oldActiveMipmapLevel);this._renderer.xr.enabled=_oldXrEnabled;outputTarget.scissorTest=false;_setViewport(outputTarget,0,0,outputTarget.width,outputTarget.height);}_fromTexture(texture,renderTarget){if(texture.mapping===CubeReflectionMapping$1||texture.mapping===CubeRefractionMapping$1){this._setSize(texture.image.length===0?16:texture.image[0].width||texture.image[0].image.width);}else {// Equirectangular
this._setSize(texture.image.width/4);}_oldTarget=this._renderer.getRenderTarget();_oldActiveCubeFace=this._renderer.getActiveCubeFace();_oldActiveMipmapLevel=this._renderer.getActiveMipmapLevel();_oldXrEnabled=this._renderer.xr.enabled;this._renderer.xr.enabled=false;const cubeUVRenderTarget=renderTarget||this._allocateTargets();this._textureToCubeUV(texture,cubeUVRenderTarget);this._applyPMREM(cubeUVRenderTarget);this._cleanup(cubeUVRenderTarget);return cubeUVRenderTarget;}_allocateTargets(){const width=3*Math.max(this._cubeSize,16*7);const height=4*this._cubeSize;const params={magFilter:LinearFilter$1,minFilter:LinearFilter$1,generateMipmaps:false,type:HalfFloatType$1,format:RGBAFormat$1,colorSpace:LinearSRGBColorSpace$1,depthBuffer:false};const cubeUVRenderTarget=_createRenderTarget(width,height,params);if(this._pingPongRenderTarget===null||this._pingPongRenderTarget.width!==width||this._pingPongRenderTarget.height!==height){if(this._pingPongRenderTarget!==null){this._dispose();}this._pingPongRenderTarget=_createRenderTarget(width,height,params);const{_lodMax}=this;({sizeLods:this._sizeLods,lodPlanes:this._lodPlanes,sigmas:this._sigmas}=_createPlanes(_lodMax));this._blurMaterial=_getBlurShader(_lodMax,width,height);}return cubeUVRenderTarget;}_compileMaterial(material){const tmpMesh=new Mesh$1(this._lodPlanes[0],material);this._renderer.compile(tmpMesh,_flatCamera);}_sceneToCubeUV(scene,near,far,cubeUVRenderTarget){const fov=90;const aspect=1;const cubeCamera=new PerspectiveCamera$1(fov,aspect,near,far);const upSign=[1,-1,1,1,1,1];const forwardSign=[1,1,1,-1,-1,-1];const renderer=this._renderer;const originalAutoClear=renderer.autoClear;const toneMapping=renderer.toneMapping;renderer.getClearColor(_clearColor$1);renderer.toneMapping=NoToneMapping$1;renderer.autoClear=false;const backgroundMaterial=new MeshBasicMaterial$1({name:'PMREM.Background',side:BackSide$1,depthWrite:false,depthTest:false});const backgroundBox=new Mesh$1(new BoxGeometry(),backgroundMaterial);let useSolidColor=false;const background=scene.background;if(background){if(background.isColor){backgroundMaterial.color.copy(background);scene.background=null;useSolidColor=true;}}else {backgroundMaterial.color.copy(_clearColor$1);useSolidColor=true;}for(let i=0;i<6;i++){const col=i%3;if(col===0){cubeCamera.up.set(0,upSign[i],0);cubeCamera.lookAt(forwardSign[i],0,0);}else if(col===1){cubeCamera.up.set(0,0,upSign[i]);cubeCamera.lookAt(0,forwardSign[i],0);}else {cubeCamera.up.set(0,upSign[i],0);cubeCamera.lookAt(0,0,forwardSign[i]);}const size=this._cubeSize;_setViewport(cubeUVRenderTarget,col*size,i>2?size:0,size,size);renderer.setRenderTarget(cubeUVRenderTarget);if(useSolidColor){renderer.render(backgroundBox,cubeCamera);}renderer.render(scene,cubeCamera);}backgroundBox.geometry.dispose();backgroundBox.material.dispose();renderer.toneMapping=toneMapping;renderer.autoClear=originalAutoClear;scene.background=background;}_textureToCubeUV(texture,cubeUVRenderTarget){const renderer=this._renderer;const isCubeTexture=texture.mapping===CubeReflectionMapping$1||texture.mapping===CubeRefractionMapping$1;if(isCubeTexture){if(this._cubemapMaterial===null){this._cubemapMaterial=_getCubemapMaterial();}this._cubemapMaterial.uniforms.flipEnvMap.value=texture.isRenderTargetTexture===false?-1:1;}else {if(this._equirectMaterial===null){this._equirectMaterial=_getEquirectMaterial();}}const material=isCubeTexture?this._cubemapMaterial:this._equirectMaterial;const mesh=new Mesh$1(this._lodPlanes[0],material);const uniforms=material.uniforms;uniforms['envMap'].value=texture;const size=this._cubeSize;_setViewport(cubeUVRenderTarget,0,0,3*size,2*size);renderer.setRenderTarget(cubeUVRenderTarget);renderer.render(mesh,_flatCamera);}_applyPMREM(cubeUVRenderTarget){const renderer=this._renderer;const autoClear=renderer.autoClear;renderer.autoClear=false;const n=this._lodPlanes.length;for(let i=1;i<n;i++){const sigma=Math.sqrt(this._sigmas[i]*this._sigmas[i]-this._sigmas[i-1]*this._sigmas[i-1]);const poleAxis=_axisDirections[(n-i-1)%_axisDirections.length];this._blur(cubeUVRenderTarget,i-1,i,sigma,poleAxis);}renderer.autoClear=autoClear;}/**
	 * This is a two-pass Gaussian blur for a cubemap. Normally this is done
	 * vertically and horizontally, but this breaks down on a cube. Here we apply
	 * the blur latitudinally (around the poles), and then longitudinally (towards
	 * the poles) to approximate the orthogonally-separable blur. It is least
	 * accurate at the poles, but still does a decent job.
	 */_blur(cubeUVRenderTarget,lodIn,lodOut,sigma,poleAxis){const pingPongRenderTarget=this._pingPongRenderTarget;this._halfBlur(cubeUVRenderTarget,pingPongRenderTarget,lodIn,lodOut,sigma,'latitudinal',poleAxis);this._halfBlur(pingPongRenderTarget,cubeUVRenderTarget,lodOut,lodOut,sigma,'longitudinal',poleAxis);}_halfBlur(targetIn,targetOut,lodIn,lodOut,sigmaRadians,direction,poleAxis){const renderer=this._renderer;const blurMaterial=this._blurMaterial;if(direction!=='latitudinal'&&direction!=='longitudinal'){console.error('blur direction must be either latitudinal or longitudinal!');}// Number of standard deviations at which to cut off the discrete approximation.
const STANDARD_DEVIATIONS=3;const blurMesh=new Mesh$1(this._lodPlanes[lodOut],blurMaterial);const blurUniforms=blurMaterial.uniforms;const pixels=this._sizeLods[lodIn]-1;const radiansPerPixel=isFinite(sigmaRadians)?Math.PI/(2*pixels):2*Math.PI/(2*MAX_SAMPLES-1);const sigmaPixels=sigmaRadians/radiansPerPixel;const samples=isFinite(sigmaRadians)?1+Math.floor(STANDARD_DEVIATIONS*sigmaPixels):MAX_SAMPLES;if(samples>MAX_SAMPLES){console.warn(`sigmaRadians, ${sigmaRadians}, is too large and will clip, as it requested ${samples} samples when the maximum is set to ${MAX_SAMPLES}`);}const weights=[];let sum=0;for(let i=0;i<MAX_SAMPLES;++i){const x=i/sigmaPixels;const weight=Math.exp(-x*x/2);weights.push(weight);if(i===0){sum+=weight;}else if(i<samples){sum+=2*weight;}}for(let i=0;i<weights.length;i++){weights[i]=weights[i]/sum;}blurUniforms['envMap'].value=targetIn.texture;blurUniforms['samples'].value=samples;blurUniforms['weights'].value=weights;blurUniforms['latitudinal'].value=direction==='latitudinal';if(poleAxis){blurUniforms['poleAxis'].value=poleAxis;}const{_lodMax}=this;blurUniforms['dTheta'].value=radiansPerPixel;blurUniforms['mipInt'].value=_lodMax-lodIn;const outputSize=this._sizeLods[lodOut];const x=3*outputSize*(lodOut>_lodMax-LOD_MIN?lodOut-_lodMax+LOD_MIN:0);const y=4*(this._cubeSize-outputSize);_setViewport(targetOut,x,y,3*outputSize,2*outputSize);renderer.setRenderTarget(targetOut);renderer.render(blurMesh,_flatCamera);}}function _createPlanes(lodMax){const lodPlanes=[];const sizeLods=[];const sigmas=[];let lod=lodMax;const totalLods=lodMax-LOD_MIN+1+EXTRA_LOD_SIGMA.length;for(let i=0;i<totalLods;i++){const sizeLod=Math.pow(2,lod);sizeLods.push(sizeLod);let sigma=1.0/sizeLod;if(i>lodMax-LOD_MIN){sigma=EXTRA_LOD_SIGMA[i-lodMax+LOD_MIN-1];}else if(i===0){sigma=0;}sigmas.push(sigma);const texelSize=1.0/(sizeLod-2);const min=-texelSize;const max=1+texelSize;const uv1=[min,min,max,min,max,max,min,min,max,max,min,max];const cubeFaces=6;const vertices=6;const positionSize=3;const uvSize=2;const faceIndexSize=1;const position=new Float32Array(positionSize*vertices*cubeFaces);const uv=new Float32Array(uvSize*vertices*cubeFaces);const faceIndex=new Float32Array(faceIndexSize*vertices*cubeFaces);for(let face=0;face<cubeFaces;face++){const x=face%3*2/3-1;const y=face>2?0:-1;const coordinates=[x,y,0,x+2/3,y,0,x+2/3,y+1,0,x,y,0,x+2/3,y+1,0,x,y+1,0];position.set(coordinates,positionSize*vertices*face);uv.set(uv1,uvSize*vertices*face);const fill=[face,face,face,face,face,face];faceIndex.set(fill,faceIndexSize*vertices*face);}const planes=new BufferGeometry$1();planes.setAttribute('position',new BufferAttribute$1(position,positionSize));planes.setAttribute('uv',new BufferAttribute$1(uv,uvSize));planes.setAttribute('faceIndex',new BufferAttribute$1(faceIndex,faceIndexSize));lodPlanes.push(planes);if(lod>LOD_MIN){lod--;}}return {lodPlanes,sizeLods,sigmas};}function _createRenderTarget(width,height,params){const cubeUVRenderTarget=new WebGLRenderTarget(width,height,params);cubeUVRenderTarget.texture.mapping=CubeUVReflectionMapping;cubeUVRenderTarget.texture.name='PMREM.cubeUv';cubeUVRenderTarget.scissorTest=true;return cubeUVRenderTarget;}function _setViewport(target,x,y,width,height){target.viewport.set(x,y,width,height);target.scissor.set(x,y,width,height);}function _getBlurShader(lodMax,width,height){const weights=new Float32Array(MAX_SAMPLES);const poleAxis=new Vector3$1(0,1,0);const shaderMaterial=new ShaderMaterial({name:'SphericalGaussianBlur',defines:{'n':MAX_SAMPLES,'CUBEUV_TEXEL_WIDTH':1.0/width,'CUBEUV_TEXEL_HEIGHT':1.0/height,'CUBEUV_MAX_MIP':`${lodMax}.0`},uniforms:{'envMap':{value:null},'samples':{value:1},'weights':{value:weights},'latitudinal':{value:false},'dTheta':{value:0},'mipInt':{value:0},'poleAxis':{value:poleAxis}},vertexShader:_getCommonVertexShader(),fragmentShader:/* glsl */`

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;
			uniform int samples;
			uniform float weights[ n ];
			uniform bool latitudinal;
			uniform float dTheta;
			uniform float mipInt;
			uniform vec3 poleAxis;

			#define ENVMAP_TYPE_CUBE_UV
			#include <cube_uv_reflection_fragment>

			vec3 getSample( float theta, vec3 axis ) {

				float cosTheta = cos( theta );
				// Rodrigues' axis-angle rotation
				vec3 sampleDirection = vOutputDirection * cosTheta
					+ cross( axis, vOutputDirection ) * sin( theta )
					+ axis * dot( axis, vOutputDirection ) * ( 1.0 - cosTheta );

				return bilinearCubeUV( envMap, sampleDirection, mipInt );

			}

			void main() {

				vec3 axis = latitudinal ? poleAxis : cross( poleAxis, vOutputDirection );

				if ( all( equal( axis, vec3( 0.0 ) ) ) ) {

					axis = vec3( vOutputDirection.z, 0.0, - vOutputDirection.x );

				}

				axis = normalize( axis );

				gl_FragColor = vec4( 0.0, 0.0, 0.0, 1.0 );
				gl_FragColor.rgb += weights[ 0 ] * getSample( 0.0, axis );

				for ( int i = 1; i < n; i++ ) {

					if ( i >= samples ) {

						break;

					}

					float theta = dTheta * float( i );
					gl_FragColor.rgb += weights[ i ] * getSample( -1.0 * theta, axis );
					gl_FragColor.rgb += weights[ i ] * getSample( theta, axis );

				}

			}
		`,blending:NoBlending,depthTest:false,depthWrite:false});return shaderMaterial;}function _getEquirectMaterial(){return new ShaderMaterial({name:'EquirectangularToCubeUV',uniforms:{'envMap':{value:null}},vertexShader:_getCommonVertexShader(),fragmentShader:/* glsl */`

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;

			#include <common>

			void main() {

				vec3 outputDirection = normalize( vOutputDirection );
				vec2 uv = equirectUv( outputDirection );

				gl_FragColor = vec4( texture2D ( envMap, uv ).rgb, 1.0 );

			}
		`,blending:NoBlending,depthTest:false,depthWrite:false});}function _getCubemapMaterial(){return new ShaderMaterial({name:'CubemapToCubeUV',uniforms:{'envMap':{value:null},'flipEnvMap':{value:-1}},vertexShader:_getCommonVertexShader(),fragmentShader:/* glsl */`

			precision mediump float;
			precision mediump int;

			uniform float flipEnvMap;

			varying vec3 vOutputDirection;

			uniform samplerCube envMap;

			void main() {

				gl_FragColor = textureCube( envMap, vec3( flipEnvMap * vOutputDirection.x, vOutputDirection.yz ) );

			}
		`,blending:NoBlending,depthTest:false,depthWrite:false});}function _getCommonVertexShader(){return/* glsl */`

		precision mediump float;
		precision mediump int;

		attribute float faceIndex;

		varying vec3 vOutputDirection;

		// RH coordinate system; PMREM face-indexing convention
		vec3 getDirection( vec2 uv, float face ) {

			uv = 2.0 * uv - 1.0;

			vec3 direction = vec3( uv, 1.0 );

			if ( face == 0.0 ) {

				direction = direction.zyx; // ( 1, v, u ) pos x

			} else if ( face == 1.0 ) {

				direction = direction.xzy;
				direction.xz *= -1.0; // ( -u, 1, -v ) pos y

			} else if ( face == 2.0 ) {

				direction.x *= -1.0; // ( -u, v, 1 ) pos z

			} else if ( face == 3.0 ) {

				direction = direction.zyx;
				direction.xz *= -1.0; // ( -1, v, -u ) neg x

			} else if ( face == 4.0 ) {

				direction = direction.xzy;
				direction.xy *= -1.0; // ( -u, -1, v ) neg y

			} else if ( face == 5.0 ) {

				direction.z *= -1.0; // ( u, v, -1 ) neg z

			}

			return direction;

		}

		void main() {

			vOutputDirection = getDirection( uv, faceIndex );
			gl_Position = vec4( position, 1.0 );

		}
	`;}function WebGLCubeUVMaps(renderer){let cubeUVmaps=new WeakMap();let pmremGenerator=null;function get(texture){if(texture&&texture.isTexture){const mapping=texture.mapping;const isEquirectMap=mapping===EquirectangularReflectionMapping||mapping===EquirectangularRefractionMapping;const isCubeMap=mapping===CubeReflectionMapping$1||mapping===CubeRefractionMapping$1;// equirect/cube map to cubeUV conversion
if(isEquirectMap||isCubeMap){let renderTarget=cubeUVmaps.get(texture);const currentPMREMVersion=renderTarget!==undefined?renderTarget.texture.pmremVersion:0;if(texture.isRenderTargetTexture&&texture.pmremVersion!==currentPMREMVersion){if(pmremGenerator===null)pmremGenerator=new PMREMGenerator(renderer);renderTarget=isEquirectMap?pmremGenerator.fromEquirectangular(texture,renderTarget):pmremGenerator.fromCubemap(texture,renderTarget);renderTarget.texture.pmremVersion=texture.pmremVersion;cubeUVmaps.set(texture,renderTarget);return renderTarget.texture;}else {if(renderTarget!==undefined){return renderTarget.texture;}else {const image=texture.image;if(isEquirectMap&&image&&image.height>0||isCubeMap&&image&&isCubeTextureComplete(image)){if(pmremGenerator===null)pmremGenerator=new PMREMGenerator(renderer);renderTarget=isEquirectMap?pmremGenerator.fromEquirectangular(texture):pmremGenerator.fromCubemap(texture);renderTarget.texture.pmremVersion=texture.pmremVersion;cubeUVmaps.set(texture,renderTarget);texture.addEventListener('dispose',onTextureDispose);return renderTarget.texture;}else {// image not yet ready. try the conversion next frame
return null;}}}}}return texture;}function isCubeTextureComplete(image){let count=0;const length=6;for(let i=0;i<length;i++){if(image[i]!==undefined)count++;}return count===length;}function onTextureDispose(event){const texture=event.target;texture.removeEventListener('dispose',onTextureDispose);const cubemapUV=cubeUVmaps.get(texture);if(cubemapUV!==undefined){cubeUVmaps.delete(texture);cubemapUV.dispose();}}function dispose(){cubeUVmaps=new WeakMap();if(pmremGenerator!==null){pmremGenerator.dispose();pmremGenerator=null;}}return {get:get,dispose:dispose};}function WebGLExtensions(gl){const extensions={};function getExtension(name){if(extensions[name]!==undefined){return extensions[name];}let extension;switch(name){case'WEBGL_depth_texture':extension=gl.getExtension('WEBGL_depth_texture')||gl.getExtension('MOZ_WEBGL_depth_texture')||gl.getExtension('WEBKIT_WEBGL_depth_texture');break;case'EXT_texture_filter_anisotropic':extension=gl.getExtension('EXT_texture_filter_anisotropic')||gl.getExtension('MOZ_EXT_texture_filter_anisotropic')||gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');break;case'WEBGL_compressed_texture_s3tc':extension=gl.getExtension('WEBGL_compressed_texture_s3tc')||gl.getExtension('MOZ_WEBGL_compressed_texture_s3tc')||gl.getExtension('WEBKIT_WEBGL_compressed_texture_s3tc');break;case'WEBGL_compressed_texture_pvrtc':extension=gl.getExtension('WEBGL_compressed_texture_pvrtc')||gl.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc');break;default:extension=gl.getExtension(name);}extensions[name]=extension;return extension;}return {has:function(name){return getExtension(name)!==null;},init:function(){getExtension('EXT_color_buffer_float');getExtension('WEBGL_clip_cull_distance');getExtension('OES_texture_float_linear');getExtension('EXT_color_buffer_half_float');getExtension('WEBGL_multisampled_render_to_texture');getExtension('WEBGL_render_shared_exponent');},get:function(name){const extension=getExtension(name);if(extension===null){warnOnce$1('THREE.WebGLRenderer: '+name+' extension not supported.');}return extension;}};}function WebGLGeometries(gl,attributes,info,bindingStates){const geometries={};const wireframeAttributes=new WeakMap();function onGeometryDispose(event){const geometry=event.target;if(geometry.index!==null){attributes.remove(geometry.index);}for(const name in geometry.attributes){attributes.remove(geometry.attributes[name]);}for(const name in geometry.morphAttributes){const array=geometry.morphAttributes[name];for(let i=0,l=array.length;i<l;i++){attributes.remove(array[i]);}}geometry.removeEventListener('dispose',onGeometryDispose);delete geometries[geometry.id];const attribute=wireframeAttributes.get(geometry);if(attribute){attributes.remove(attribute);wireframeAttributes.delete(geometry);}bindingStates.releaseStatesOfGeometry(geometry);if(geometry.isInstancedBufferGeometry===true){delete geometry._maxInstanceCount;}//
info.memory.geometries--;}function get(object,geometry){if(geometries[geometry.id]===true)return geometry;geometry.addEventListener('dispose',onGeometryDispose);geometries[geometry.id]=true;info.memory.geometries++;return geometry;}function update(geometry){const geometryAttributes=geometry.attributes;// Updating index buffer in VAO now. See WebGLBindingStates.
for(const name in geometryAttributes){attributes.update(geometryAttributes[name],gl.ARRAY_BUFFER);}// morph targets
const morphAttributes=geometry.morphAttributes;for(const name in morphAttributes){const array=morphAttributes[name];for(let i=0,l=array.length;i<l;i++){attributes.update(array[i],gl.ARRAY_BUFFER);}}}function updateWireframeAttribute(geometry){const indices=[];const geometryIndex=geometry.index;const geometryPosition=geometry.attributes.position;let version=0;if(geometryIndex!==null){const array=geometryIndex.array;version=geometryIndex.version;for(let i=0,l=array.length;i<l;i+=3){const a=array[i+0];const b=array[i+1];const c=array[i+2];indices.push(a,b,b,c,c,a);}}else if(geometryPosition!==undefined){const array=geometryPosition.array;version=geometryPosition.version;for(let i=0,l=array.length/3-1;i<l;i+=3){const a=i+0;const b=i+1;const c=i+2;indices.push(a,b,b,c,c,a);}}else {return;}const attribute=new(arrayNeedsUint32$1(indices)?Uint32BufferAttribute$1:Uint16BufferAttribute$1)(indices,1);attribute.version=version;// Updating index buffer in VAO now. See WebGLBindingStates
//
const previousAttribute=wireframeAttributes.get(geometry);if(previousAttribute)attributes.remove(previousAttribute);//
wireframeAttributes.set(geometry,attribute);}function getWireframeAttribute(geometry){const currentAttribute=wireframeAttributes.get(geometry);if(currentAttribute){const geometryIndex=geometry.index;if(geometryIndex!==null){// if the attribute is obsolete, create a new one
if(currentAttribute.version<geometryIndex.version){updateWireframeAttribute(geometry);}}}else {updateWireframeAttribute(geometry);}return wireframeAttributes.get(geometry);}return {get:get,update:update,getWireframeAttribute:getWireframeAttribute};}function WebGLIndexedBufferRenderer(gl,extensions,info){let mode;function setMode(value){mode=value;}let type,bytesPerElement;function setIndex(value){type=value.type;bytesPerElement=value.bytesPerElement;}function render(start,count){gl.drawElements(mode,count,type,start*bytesPerElement);info.update(count,mode,1);}function renderInstances(start,count,primcount){if(primcount===0)return;gl.drawElementsInstanced(mode,count,type,start*bytesPerElement,primcount);info.update(count,mode,primcount);}function renderMultiDraw(starts,counts,drawCount){if(drawCount===0)return;const extension=extensions.get('WEBGL_multi_draw');extension.multiDrawElementsWEBGL(mode,counts,0,type,starts,0,drawCount);let elementCount=0;for(let i=0;i<drawCount;i++){elementCount+=counts[i];}info.update(elementCount,mode,1);}function renderMultiDrawInstances(starts,counts,drawCount,primcount){if(drawCount===0)return;const extension=extensions.get('WEBGL_multi_draw');if(extension===null){for(let i=0;i<starts.length;i++){renderInstances(starts[i]/bytesPerElement,counts[i],primcount[i]);}}else {extension.multiDrawElementsInstancedWEBGL(mode,counts,0,type,starts,0,primcount,0,drawCount);let elementCount=0;for(let i=0;i<drawCount;i++){elementCount+=counts[i];}for(let i=0;i<primcount.length;i++){info.update(elementCount,mode,primcount[i]);}}}//
this.setMode=setMode;this.setIndex=setIndex;this.render=render;this.renderInstances=renderInstances;this.renderMultiDraw=renderMultiDraw;this.renderMultiDrawInstances=renderMultiDrawInstances;}function WebGLInfo(gl){const memory={geometries:0,textures:0};const render={frame:0,calls:0,triangles:0,points:0,lines:0};function update(count,mode,instanceCount){render.calls++;switch(mode){case gl.TRIANGLES:render.triangles+=instanceCount*(count/3);break;case gl.LINES:render.lines+=instanceCount*(count/2);break;case gl.LINE_STRIP:render.lines+=instanceCount*(count-1);break;case gl.LINE_LOOP:render.lines+=instanceCount*count;break;case gl.POINTS:render.points+=instanceCount*count;break;default:console.error('THREE.WebGLInfo: Unknown draw mode:',mode);break;}}function reset(){render.calls=0;render.triangles=0;render.points=0;render.lines=0;}return {memory:memory,render:render,programs:null,autoReset:true,reset:reset,update:update};}function WebGLMorphtargets(gl,capabilities,textures){const morphTextures=new WeakMap();const morph=new Vector4$1();function update(object,geometry,program){const objectInfluences=object.morphTargetInfluences;// the following encodes morph targets into an array of data textures. Each layer represents a single morph target.
const morphAttribute=geometry.morphAttributes.position||geometry.morphAttributes.normal||geometry.morphAttributes.color;const morphTargetsCount=morphAttribute!==undefined?morphAttribute.length:0;let entry=morphTextures.get(geometry);if(entry===undefined||entry.count!==morphTargetsCount){if(entry!==undefined)entry.texture.dispose();const hasMorphPosition=geometry.morphAttributes.position!==undefined;const hasMorphNormals=geometry.morphAttributes.normal!==undefined;const hasMorphColors=geometry.morphAttributes.color!==undefined;const morphTargets=geometry.morphAttributes.position||[];const morphNormals=geometry.morphAttributes.normal||[];const morphColors=geometry.morphAttributes.color||[];let vertexDataCount=0;if(hasMorphPosition===true)vertexDataCount=1;if(hasMorphNormals===true)vertexDataCount=2;if(hasMorphColors===true)vertexDataCount=3;let width=geometry.attributes.position.count*vertexDataCount;let height=1;if(width>capabilities.maxTextureSize){height=Math.ceil(width/capabilities.maxTextureSize);width=capabilities.maxTextureSize;}const buffer=new Float32Array(width*height*4*morphTargetsCount);const texture=new DataArrayTexture$1(buffer,width,height,morphTargetsCount);texture.type=FloatType$1;texture.needsUpdate=true;// fill buffer
const vertexDataStride=vertexDataCount*4;for(let i=0;i<morphTargetsCount;i++){const morphTarget=morphTargets[i];const morphNormal=morphNormals[i];const morphColor=morphColors[i];const offset=width*height*4*i;for(let j=0;j<morphTarget.count;j++){const stride=j*vertexDataStride;if(hasMorphPosition===true){morph.fromBufferAttribute(morphTarget,j);buffer[offset+stride+0]=morph.x;buffer[offset+stride+1]=morph.y;buffer[offset+stride+2]=morph.z;buffer[offset+stride+3]=0;}if(hasMorphNormals===true){morph.fromBufferAttribute(morphNormal,j);buffer[offset+stride+4]=morph.x;buffer[offset+stride+5]=morph.y;buffer[offset+stride+6]=morph.z;buffer[offset+stride+7]=0;}if(hasMorphColors===true){morph.fromBufferAttribute(morphColor,j);buffer[offset+stride+8]=morph.x;buffer[offset+stride+9]=morph.y;buffer[offset+stride+10]=morph.z;buffer[offset+stride+11]=morphColor.itemSize===4?morph.w:1;}}}entry={count:morphTargetsCount,texture:texture,size:new Vector2$1(width,height)};morphTextures.set(geometry,entry);function disposeTexture(){texture.dispose();morphTextures.delete(geometry);geometry.removeEventListener('dispose',disposeTexture);}geometry.addEventListener('dispose',disposeTexture);}//
if(object.isInstancedMesh===true&&object.morphTexture!==null){program.getUniforms().setValue(gl,'morphTexture',object.morphTexture,textures);}else {let morphInfluencesSum=0;for(let i=0;i<objectInfluences.length;i++){morphInfluencesSum+=objectInfluences[i];}const morphBaseInfluence=geometry.morphTargetsRelative?1:1-morphInfluencesSum;program.getUniforms().setValue(gl,'morphTargetBaseInfluence',morphBaseInfluence);program.getUniforms().setValue(gl,'morphTargetInfluences',objectInfluences);}program.getUniforms().setValue(gl,'morphTargetsTexture',entry.texture,textures);program.getUniforms().setValue(gl,'morphTargetsTextureSize',entry.size);}return {update:update};}function WebGLObjects(gl,geometries,attributes,info){let updateMap=new WeakMap();function update(object){const frame=info.render.frame;const geometry=object.geometry;const buffergeometry=geometries.get(object,geometry);// Update once per frame
if(updateMap.get(buffergeometry)!==frame){geometries.update(buffergeometry);updateMap.set(buffergeometry,frame);}if(object.isInstancedMesh){if(object.hasEventListener('dispose',onInstancedMeshDispose)===false){object.addEventListener('dispose',onInstancedMeshDispose);}if(updateMap.get(object)!==frame){attributes.update(object.instanceMatrix,gl.ARRAY_BUFFER);if(object.instanceColor!==null){attributes.update(object.instanceColor,gl.ARRAY_BUFFER);}updateMap.set(object,frame);}}if(object.isSkinnedMesh){const skeleton=object.skeleton;if(updateMap.get(skeleton)!==frame){skeleton.update();updateMap.set(skeleton,frame);}}return buffergeometry;}function dispose(){updateMap=new WeakMap();}function onInstancedMeshDispose(event){const instancedMesh=event.target;instancedMesh.removeEventListener('dispose',onInstancedMeshDispose);attributes.remove(instancedMesh.instanceMatrix);if(instancedMesh.instanceColor!==null)attributes.remove(instancedMesh.instanceColor);}return {update:update,dispose:dispose};}class DepthTexture$1 extends Texture$1{constructor(width,height,type,mapping,wrapS,wrapT,magFilter,minFilter,anisotropy,format=DepthFormat$1){if(format!==DepthFormat$1&&format!==DepthStencilFormat$1){throw new Error('DepthTexture format must be either THREE.DepthFormat or THREE.DepthStencilFormat');}if(type===undefined&&format===DepthFormat$1)type=UnsignedIntType$1;if(type===undefined&&format===DepthStencilFormat$1)type=UnsignedInt248Type$1;super(null,mapping,wrapS,wrapT,magFilter,minFilter,format,type,anisotropy);this.isDepthTexture=true;this.image={width:width,height:height};this.magFilter=magFilter!==undefined?magFilter:NearestFilter$1;this.minFilter=minFilter!==undefined?minFilter:NearestFilter$1;this.flipY=false;this.generateMipmaps=false;this.compareFunction=null;}copy(source){super.copy(source);this.compareFunction=source.compareFunction;return this;}toJSON(meta){const data=super.toJSON(meta);if(this.compareFunction!==null)data.compareFunction=this.compareFunction;return data;}}/**
 * Uniforms of a program.
 * Those form a tree structure with a special top-level container for the root,
 * which you get by calling 'new WebGLUniforms( gl, program )'.
 *
 *
 * Properties of inner nodes including the top-level container:
 *
 * .seq - array of nested uniforms
 * .map - nested uniforms by name
 *
 *
 * Methods of all nodes except the top-level container:
 *
 * .setValue( gl, value, [textures] )
 *
 * 		uploads a uniform value(s)
 *  	the 'textures' parameter is needed for sampler uniforms
 *
 *
 * Static methods of the top-level container (textures factorizations):
 *
 * .upload( gl, seq, values, textures )
 *
 * 		sets uniforms in 'seq' to 'values[id].value'
 *
 * .seqWithValue( seq, values ) : filteredSeq
 *
 * 		filters 'seq' entries with corresponding entry in values
 *
 *
 * Methods of the top-level container (textures factorizations):
 *
 * .setValue( gl, name, value, textures )
 *
 * 		sets uniform with  name 'name' to 'value'
 *
 * .setOptional( gl, obj, prop )
 *
 * 		like .set for an optional property of the object
 *
 */const emptyTexture=/*@__PURE__*/new Texture$1();const emptyShadowTexture=/*@__PURE__*/new DepthTexture$1(1,1);const emptyArrayTexture=/*@__PURE__*/new DataArrayTexture$1();const empty3dTexture=/*@__PURE__*/new Data3DTexture();const emptyCubeTexture=/*@__PURE__*/new CubeTexture();// --- Utilities ---
// Array Caches (provide typed arrays for temporary by size)
const arrayCacheF32=[];const arrayCacheI32=[];// Float32Array caches used for uploading Matrix uniforms
const mat4array=new Float32Array(16);const mat3array=new Float32Array(9);const mat2array=new Float32Array(4);// Flattening for arrays of vectors and matrices
function flatten(array,nBlocks,blockSize){const firstElem=array[0];if(firstElem<=0||firstElem>0)return array;// unoptimized: ! isNaN( firstElem )
// see http://jacksondunstan.com/articles/983
const n=nBlocks*blockSize;let r=arrayCacheF32[n];if(r===undefined){r=new Float32Array(n);arrayCacheF32[n]=r;}if(nBlocks!==0){firstElem.toArray(r,0);for(let i=1,offset=0;i!==nBlocks;++i){offset+=blockSize;array[i].toArray(r,offset);}}return r;}function arraysEqual(a,b){if(a.length!==b.length)return false;for(let i=0,l=a.length;i<l;i++){if(a[i]!==b[i])return false;}return true;}function copyArray(a,b){for(let i=0,l=b.length;i<l;i++){a[i]=b[i];}}// Texture unit allocation
function allocTexUnits(textures,n){let r=arrayCacheI32[n];if(r===undefined){r=new Int32Array(n);arrayCacheI32[n]=r;}for(let i=0;i!==n;++i){r[i]=textures.allocateTextureUnit();}return r;}// --- Setters ---
// Note: Defining these methods externally, because they come in a bunch
// and this way their names minify.
// Single scalar
function setValueV1f(gl,v){const cache=this.cache;if(cache[0]===v)return;gl.uniform1f(this.addr,v);cache[0]=v;}// Single float vector (from flat array or THREE.VectorN)
function setValueV2f(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y){gl.uniform2f(this.addr,v.x,v.y);cache[0]=v.x;cache[1]=v.y;}}else {if(arraysEqual(cache,v))return;gl.uniform2fv(this.addr,v);copyArray(cache,v);}}function setValueV3f(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z){gl.uniform3f(this.addr,v.x,v.y,v.z);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;}}else if(v.r!==undefined){if(cache[0]!==v.r||cache[1]!==v.g||cache[2]!==v.b){gl.uniform3f(this.addr,v.r,v.g,v.b);cache[0]=v.r;cache[1]=v.g;cache[2]=v.b;}}else {if(arraysEqual(cache,v))return;gl.uniform3fv(this.addr,v);copyArray(cache,v);}}function setValueV4f(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z||cache[3]!==v.w){gl.uniform4f(this.addr,v.x,v.y,v.z,v.w);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;cache[3]=v.w;}}else {if(arraysEqual(cache,v))return;gl.uniform4fv(this.addr,v);copyArray(cache,v);}}// Single matrix (from flat array or THREE.MatrixN)
function setValueM2(gl,v){const cache=this.cache;const elements=v.elements;if(elements===undefined){if(arraysEqual(cache,v))return;gl.uniformMatrix2fv(this.addr,false,v);copyArray(cache,v);}else {if(arraysEqual(cache,elements))return;mat2array.set(elements);gl.uniformMatrix2fv(this.addr,false,mat2array);copyArray(cache,elements);}}function setValueM3(gl,v){const cache=this.cache;const elements=v.elements;if(elements===undefined){if(arraysEqual(cache,v))return;gl.uniformMatrix3fv(this.addr,false,v);copyArray(cache,v);}else {if(arraysEqual(cache,elements))return;mat3array.set(elements);gl.uniformMatrix3fv(this.addr,false,mat3array);copyArray(cache,elements);}}function setValueM4(gl,v){const cache=this.cache;const elements=v.elements;if(elements===undefined){if(arraysEqual(cache,v))return;gl.uniformMatrix4fv(this.addr,false,v);copyArray(cache,v);}else {if(arraysEqual(cache,elements))return;mat4array.set(elements);gl.uniformMatrix4fv(this.addr,false,mat4array);copyArray(cache,elements);}}// Single integer / boolean
function setValueV1i(gl,v){const cache=this.cache;if(cache[0]===v)return;gl.uniform1i(this.addr,v);cache[0]=v;}// Single integer / boolean vector (from flat array or THREE.VectorN)
function setValueV2i(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y){gl.uniform2i(this.addr,v.x,v.y);cache[0]=v.x;cache[1]=v.y;}}else {if(arraysEqual(cache,v))return;gl.uniform2iv(this.addr,v);copyArray(cache,v);}}function setValueV3i(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z){gl.uniform3i(this.addr,v.x,v.y,v.z);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;}}else {if(arraysEqual(cache,v))return;gl.uniform3iv(this.addr,v);copyArray(cache,v);}}function setValueV4i(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z||cache[3]!==v.w){gl.uniform4i(this.addr,v.x,v.y,v.z,v.w);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;cache[3]=v.w;}}else {if(arraysEqual(cache,v))return;gl.uniform4iv(this.addr,v);copyArray(cache,v);}}// Single unsigned integer
function setValueV1ui(gl,v){const cache=this.cache;if(cache[0]===v)return;gl.uniform1ui(this.addr,v);cache[0]=v;}// Single unsigned integer vector (from flat array or THREE.VectorN)
function setValueV2ui(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y){gl.uniform2ui(this.addr,v.x,v.y);cache[0]=v.x;cache[1]=v.y;}}else {if(arraysEqual(cache,v))return;gl.uniform2uiv(this.addr,v);copyArray(cache,v);}}function setValueV3ui(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z){gl.uniform3ui(this.addr,v.x,v.y,v.z);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;}}else {if(arraysEqual(cache,v))return;gl.uniform3uiv(this.addr,v);copyArray(cache,v);}}function setValueV4ui(gl,v){const cache=this.cache;if(v.x!==undefined){if(cache[0]!==v.x||cache[1]!==v.y||cache[2]!==v.z||cache[3]!==v.w){gl.uniform4ui(this.addr,v.x,v.y,v.z,v.w);cache[0]=v.x;cache[1]=v.y;cache[2]=v.z;cache[3]=v.w;}}else {if(arraysEqual(cache,v))return;gl.uniform4uiv(this.addr,v);copyArray(cache,v);}}// Single texture (2D / Cube)
function setValueT1(gl,v,textures){const cache=this.cache;const unit=textures.allocateTextureUnit();if(cache[0]!==unit){gl.uniform1i(this.addr,unit);cache[0]=unit;}let emptyTexture2D;if(this.type===gl.SAMPLER_2D_SHADOW){emptyShadowTexture.compareFunction=LessEqualCompare;// #28670
emptyTexture2D=emptyShadowTexture;}else {emptyTexture2D=emptyTexture;}textures.setTexture2D(v||emptyTexture2D,unit);}function setValueT3D1(gl,v,textures){const cache=this.cache;const unit=textures.allocateTextureUnit();if(cache[0]!==unit){gl.uniform1i(this.addr,unit);cache[0]=unit;}textures.setTexture3D(v||empty3dTexture,unit);}function setValueT6(gl,v,textures){const cache=this.cache;const unit=textures.allocateTextureUnit();if(cache[0]!==unit){gl.uniform1i(this.addr,unit);cache[0]=unit;}textures.setTextureCube(v||emptyCubeTexture,unit);}function setValueT2DArray1(gl,v,textures){const cache=this.cache;const unit=textures.allocateTextureUnit();if(cache[0]!==unit){gl.uniform1i(this.addr,unit);cache[0]=unit;}textures.setTexture2DArray(v||emptyArrayTexture,unit);}// Helper to pick the right setter for the singular case
function getSingularSetter(type){switch(type){case 0x1406:return setValueV1f;// FLOAT
case 0x8b50:return setValueV2f;// _VEC2
case 0x8b51:return setValueV3f;// _VEC3
case 0x8b52:return setValueV4f;// _VEC4
case 0x8b5a:return setValueM2;// _MAT2
case 0x8b5b:return setValueM3;// _MAT3
case 0x8b5c:return setValueM4;// _MAT4
case 0x1404:case 0x8b56:return setValueV1i;// INT, BOOL
case 0x8b53:case 0x8b57:return setValueV2i;// _VEC2
case 0x8b54:case 0x8b58:return setValueV3i;// _VEC3
case 0x8b55:case 0x8b59:return setValueV4i;// _VEC4
case 0x1405:return setValueV1ui;// UINT
case 0x8dc6:return setValueV2ui;// _VEC2
case 0x8dc7:return setValueV3ui;// _VEC3
case 0x8dc8:return setValueV4ui;// _VEC4
case 0x8b5e:// SAMPLER_2D
case 0x8d66:// SAMPLER_EXTERNAL_OES
case 0x8dca:// INT_SAMPLER_2D
case 0x8dd2:// UNSIGNED_INT_SAMPLER_2D
case 0x8b62:// SAMPLER_2D_SHADOW
return setValueT1;case 0x8b5f:// SAMPLER_3D
case 0x8dcb:// INT_SAMPLER_3D
case 0x8dd3:// UNSIGNED_INT_SAMPLER_3D
return setValueT3D1;case 0x8b60:// SAMPLER_CUBE
case 0x8dcc:// INT_SAMPLER_CUBE
case 0x8dd4:// UNSIGNED_INT_SAMPLER_CUBE
case 0x8dc5:// SAMPLER_CUBE_SHADOW
return setValueT6;case 0x8dc1:// SAMPLER_2D_ARRAY
case 0x8dcf:// INT_SAMPLER_2D_ARRAY
case 0x8dd7:// UNSIGNED_INT_SAMPLER_2D_ARRAY
case 0x8dc4:// SAMPLER_2D_ARRAY_SHADOW
return setValueT2DArray1;}}// Array of scalars
function setValueV1fArray(gl,v){gl.uniform1fv(this.addr,v);}// Array of vectors (from flat array or array of THREE.VectorN)
function setValueV2fArray(gl,v){const data=flatten(v,this.size,2);gl.uniform2fv(this.addr,data);}function setValueV3fArray(gl,v){const data=flatten(v,this.size,3);gl.uniform3fv(this.addr,data);}function setValueV4fArray(gl,v){const data=flatten(v,this.size,4);gl.uniform4fv(this.addr,data);}// Array of matrices (from flat array or array of THREE.MatrixN)
function setValueM2Array(gl,v){const data=flatten(v,this.size,4);gl.uniformMatrix2fv(this.addr,false,data);}function setValueM3Array(gl,v){const data=flatten(v,this.size,9);gl.uniformMatrix3fv(this.addr,false,data);}function setValueM4Array(gl,v){const data=flatten(v,this.size,16);gl.uniformMatrix4fv(this.addr,false,data);}// Array of integer / boolean
function setValueV1iArray(gl,v){gl.uniform1iv(this.addr,v);}// Array of integer / boolean vectors (from flat array)
function setValueV2iArray(gl,v){gl.uniform2iv(this.addr,v);}function setValueV3iArray(gl,v){gl.uniform3iv(this.addr,v);}function setValueV4iArray(gl,v){gl.uniform4iv(this.addr,v);}// Array of unsigned integer
function setValueV1uiArray(gl,v){gl.uniform1uiv(this.addr,v);}// Array of unsigned integer vectors (from flat array)
function setValueV2uiArray(gl,v){gl.uniform2uiv(this.addr,v);}function setValueV3uiArray(gl,v){gl.uniform3uiv(this.addr,v);}function setValueV4uiArray(gl,v){gl.uniform4uiv(this.addr,v);}// Array of textures (2D / 3D / Cube / 2DArray)
function setValueT1Array(gl,v,textures){const cache=this.cache;const n=v.length;const units=allocTexUnits(textures,n);if(!arraysEqual(cache,units)){gl.uniform1iv(this.addr,units);copyArray(cache,units);}for(let i=0;i!==n;++i){textures.setTexture2D(v[i]||emptyTexture,units[i]);}}function setValueT3DArray(gl,v,textures){const cache=this.cache;const n=v.length;const units=allocTexUnits(textures,n);if(!arraysEqual(cache,units)){gl.uniform1iv(this.addr,units);copyArray(cache,units);}for(let i=0;i!==n;++i){textures.setTexture3D(v[i]||empty3dTexture,units[i]);}}function setValueT6Array(gl,v,textures){const cache=this.cache;const n=v.length;const units=allocTexUnits(textures,n);if(!arraysEqual(cache,units)){gl.uniform1iv(this.addr,units);copyArray(cache,units);}for(let i=0;i!==n;++i){textures.setTextureCube(v[i]||emptyCubeTexture,units[i]);}}function setValueT2DArrayArray(gl,v,textures){const cache=this.cache;const n=v.length;const units=allocTexUnits(textures,n);if(!arraysEqual(cache,units)){gl.uniform1iv(this.addr,units);copyArray(cache,units);}for(let i=0;i!==n;++i){textures.setTexture2DArray(v[i]||emptyArrayTexture,units[i]);}}// Helper to pick the right setter for a pure (bottom-level) array
function getPureArraySetter(type){switch(type){case 0x1406:return setValueV1fArray;// FLOAT
case 0x8b50:return setValueV2fArray;// _VEC2
case 0x8b51:return setValueV3fArray;// _VEC3
case 0x8b52:return setValueV4fArray;// _VEC4
case 0x8b5a:return setValueM2Array;// _MAT2
case 0x8b5b:return setValueM3Array;// _MAT3
case 0x8b5c:return setValueM4Array;// _MAT4
case 0x1404:case 0x8b56:return setValueV1iArray;// INT, BOOL
case 0x8b53:case 0x8b57:return setValueV2iArray;// _VEC2
case 0x8b54:case 0x8b58:return setValueV3iArray;// _VEC3
case 0x8b55:case 0x8b59:return setValueV4iArray;// _VEC4
case 0x1405:return setValueV1uiArray;// UINT
case 0x8dc6:return setValueV2uiArray;// _VEC2
case 0x8dc7:return setValueV3uiArray;// _VEC3
case 0x8dc8:return setValueV4uiArray;// _VEC4
case 0x8b5e:// SAMPLER_2D
case 0x8d66:// SAMPLER_EXTERNAL_OES
case 0x8dca:// INT_SAMPLER_2D
case 0x8dd2:// UNSIGNED_INT_SAMPLER_2D
case 0x8b62:// SAMPLER_2D_SHADOW
return setValueT1Array;case 0x8b5f:// SAMPLER_3D
case 0x8dcb:// INT_SAMPLER_3D
case 0x8dd3:// UNSIGNED_INT_SAMPLER_3D
return setValueT3DArray;case 0x8b60:// SAMPLER_CUBE
case 0x8dcc:// INT_SAMPLER_CUBE
case 0x8dd4:// UNSIGNED_INT_SAMPLER_CUBE
case 0x8dc5:// SAMPLER_CUBE_SHADOW
return setValueT6Array;case 0x8dc1:// SAMPLER_2D_ARRAY
case 0x8dcf:// INT_SAMPLER_2D_ARRAY
case 0x8dd7:// UNSIGNED_INT_SAMPLER_2D_ARRAY
case 0x8dc4:// SAMPLER_2D_ARRAY_SHADOW
return setValueT2DArrayArray;}}// --- Uniform Classes ---
class SingleUniform{constructor(id,activeInfo,addr){this.id=id;this.addr=addr;this.cache=[];this.type=activeInfo.type;this.setValue=getSingularSetter(activeInfo.type);// this.path = activeInfo.name; // DEBUG
}}class PureArrayUniform{constructor(id,activeInfo,addr){this.id=id;this.addr=addr;this.cache=[];this.type=activeInfo.type;this.size=activeInfo.size;this.setValue=getPureArraySetter(activeInfo.type);// this.path = activeInfo.name; // DEBUG
}}class StructuredUniform{constructor(id){this.id=id;this.seq=[];this.map={};}setValue(gl,value,textures){const seq=this.seq;for(let i=0,n=seq.length;i!==n;++i){const u=seq[i];u.setValue(gl,value[u.id],textures);}}}// --- Top-level ---
// Parser - builds up the property tree from the path strings
const RePathPart=/(\w+)(\])?(\[|\.)?/g;// extracts
// 	- the identifier (member name or array index)
//  - followed by an optional right bracket (found when array index)
//  - followed by an optional left bracket or dot (type of subscript)
//
// Note: These portions can be read in a non-overlapping fashion and
// allow straightforward parsing of the hierarchy that WebGL encodes
// in the uniform names.
function addUniform(container,uniformObject){container.seq.push(uniformObject);container.map[uniformObject.id]=uniformObject;}function parseUniform(activeInfo,addr,container){const path=activeInfo.name,pathLength=path.length;// reset RegExp object, because of the early exit of a previous run
RePathPart.lastIndex=0;while(true){const match=RePathPart.exec(path),matchEnd=RePathPart.lastIndex;let id=match[1];const idIsIndex=match[2]===']',subscript=match[3];if(idIsIndex)id=id|0;// convert to integer
if(subscript===undefined||subscript==='['&&matchEnd+2===pathLength){// bare name or "pure" bottom-level array "[0]" suffix
addUniform(container,subscript===undefined?new SingleUniform(id,activeInfo,addr):new PureArrayUniform(id,activeInfo,addr));break;}else {// step into inner node / create it in case it doesn't exist
const map=container.map;let next=map[id];if(next===undefined){next=new StructuredUniform(id);addUniform(container,next);}container=next;}}}// Root Container
class WebGLUniforms{constructor(gl,program){this.seq=[];this.map={};const n=gl.getProgramParameter(program,gl.ACTIVE_UNIFORMS);for(let i=0;i<n;++i){const info=gl.getActiveUniform(program,i),addr=gl.getUniformLocation(program,info.name);parseUniform(info,addr,this);}}setValue(gl,name,value,textures){const u=this.map[name];if(u!==undefined)u.setValue(gl,value,textures);}setOptional(gl,object,name){const v=object[name];if(v!==undefined)this.setValue(gl,name,v);}static upload(gl,seq,values,textures){for(let i=0,n=seq.length;i!==n;++i){const u=seq[i],v=values[u.id];if(v.needsUpdate!==false){// note: always updating when .needsUpdate is undefined
u.setValue(gl,v.value,textures);}}}static seqWithValue(seq,values){const r=[];for(let i=0,n=seq.length;i!==n;++i){const u=seq[i];if(u.id in values)r.push(u);}return r;}}function WebGLShader(gl,type,string){const shader=gl.createShader(type);gl.shaderSource(shader,string);gl.compileShader(shader);return shader;}// From https://www.khronos.org/registry/webgl/extensions/KHR_parallel_shader_compile/
const COMPLETION_STATUS_KHR=0x91B1;let programIdCount=0;function handleSource(string,errorLine){const lines=string.split('\n');const lines2=[];const from=Math.max(errorLine-6,0);const to=Math.min(errorLine+6,lines.length);for(let i=from;i<to;i++){const line=i+1;lines2.push(`${line===errorLine?'>':' '} ${line}: ${lines[i]}`);}return lines2.join('\n');}function getEncodingComponents(colorSpace){const workingPrimaries=ColorManagement$1.getPrimaries(ColorManagement$1.workingColorSpace);const encodingPrimaries=ColorManagement$1.getPrimaries(colorSpace);let gamutMapping;if(workingPrimaries===encodingPrimaries){gamutMapping='';}else if(workingPrimaries===P3Primaries$1&&encodingPrimaries===Rec709Primaries$1){gamutMapping='LinearDisplayP3ToLinearSRGB';}else if(workingPrimaries===Rec709Primaries$1&&encodingPrimaries===P3Primaries$1){gamutMapping='LinearSRGBToLinearDisplayP3';}switch(colorSpace){case LinearSRGBColorSpace$1:case LinearDisplayP3ColorSpace$1:return [gamutMapping,'LinearTransferOETF'];case SRGBColorSpace$1:case DisplayP3ColorSpace$1:return [gamutMapping,'sRGBTransferOETF'];default:console.warn('THREE.WebGLProgram: Unsupported color space:',colorSpace);return [gamutMapping,'LinearTransferOETF'];}}function getShaderErrors(gl,shader,type){const status=gl.getShaderParameter(shader,gl.COMPILE_STATUS);const errors=gl.getShaderInfoLog(shader).trim();if(status&&errors==='')return '';const errorMatches=/ERROR: 0:(\d+)/.exec(errors);if(errorMatches){// --enable-privileged-webgl-extension
// console.log( '**' + type + '**', gl.getExtension( 'WEBGL_debug_shaders' ).getTranslatedShaderSource( shader ) );
const errorLine=parseInt(errorMatches[1]);return type.toUpperCase()+'\n\n'+errors+'\n\n'+handleSource(gl.getShaderSource(shader),errorLine);}else {return errors;}}function getTexelEncodingFunction(functionName,colorSpace){const components=getEncodingComponents(colorSpace);return `vec4 ${functionName}( vec4 value ) { return ${components[0]}( ${components[1]}( value ) ); }`;}function getToneMappingFunction(functionName,toneMapping){let toneMappingName;switch(toneMapping){case LinearToneMapping:toneMappingName='Linear';break;case ReinhardToneMapping:toneMappingName='Reinhard';break;case CineonToneMapping:toneMappingName='Cineon';break;case ACESFilmicToneMapping:toneMappingName='ACESFilmic';break;case AgXToneMapping:toneMappingName='AgX';break;case NeutralToneMapping:toneMappingName='Neutral';break;case CustomToneMapping:toneMappingName='Custom';break;default:console.warn('THREE.WebGLProgram: Unsupported toneMapping:',toneMapping);toneMappingName='Linear';}return 'vec3 '+functionName+'( vec3 color ) { return '+toneMappingName+'ToneMapping( color ); }';}const _v0$1$1=/*@__PURE__*/new Vector3$1();function getLuminanceFunction(){ColorManagement$1.getLuminanceCoefficients(_v0$1$1);const r=_v0$1$1.x.toFixed(4);const g=_v0$1$1.y.toFixed(4);const b=_v0$1$1.z.toFixed(4);return ['float luminance( const in vec3 rgb ) {',`	const vec3 weights = vec3( ${r}, ${g}, ${b} );`,'	return dot( weights, rgb );','}'].join('\n');}function generateVertexExtensions(parameters){const chunks=[parameters.extensionClipCullDistance?'#extension GL_ANGLE_clip_cull_distance : require':'',parameters.extensionMultiDraw?'#extension GL_ANGLE_multi_draw : require':''];return chunks.filter(filterEmptyLine).join('\n');}function generateDefines(defines){const chunks=[];for(const name in defines){const value=defines[name];if(value===false)continue;chunks.push('#define '+name+' '+value);}return chunks.join('\n');}function fetchAttributeLocations(gl,program){const attributes={};const n=gl.getProgramParameter(program,gl.ACTIVE_ATTRIBUTES);for(let i=0;i<n;i++){const info=gl.getActiveAttrib(program,i);const name=info.name;let locationSize=1;if(info.type===gl.FLOAT_MAT2)locationSize=2;if(info.type===gl.FLOAT_MAT3)locationSize=3;if(info.type===gl.FLOAT_MAT4)locationSize=4;// console.log( 'THREE.WebGLProgram: ACTIVE VERTEX ATTRIBUTE:', name, i );
attributes[name]={type:info.type,location:gl.getAttribLocation(program,name),locationSize:locationSize};}return attributes;}function filterEmptyLine(string){return string!=='';}function replaceLightNums(string,parameters){const numSpotLightCoords=parameters.numSpotLightShadows+parameters.numSpotLightMaps-parameters.numSpotLightShadowsWithMaps;return string.replace(/NUM_DIR_LIGHTS/g,parameters.numDirLights).replace(/NUM_SPOT_LIGHTS/g,parameters.numSpotLights).replace(/NUM_SPOT_LIGHT_MAPS/g,parameters.numSpotLightMaps).replace(/NUM_SPOT_LIGHT_COORDS/g,numSpotLightCoords).replace(/NUM_RECT_AREA_LIGHTS/g,parameters.numRectAreaLights).replace(/NUM_POINT_LIGHTS/g,parameters.numPointLights).replace(/NUM_HEMI_LIGHTS/g,parameters.numHemiLights).replace(/NUM_DIR_LIGHT_SHADOWS/g,parameters.numDirLightShadows).replace(/NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS/g,parameters.numSpotLightShadowsWithMaps).replace(/NUM_SPOT_LIGHT_SHADOWS/g,parameters.numSpotLightShadows).replace(/NUM_POINT_LIGHT_SHADOWS/g,parameters.numPointLightShadows);}function replaceClippingPlaneNums(string,parameters){return string.replace(/NUM_CLIPPING_PLANES/g,parameters.numClippingPlanes).replace(/UNION_CLIPPING_PLANES/g,parameters.numClippingPlanes-parameters.numClipIntersection);}// Resolve Includes
const includePattern=/^[ \t]*#include +<([\w\d./]+)>/gm;function resolveIncludes(string){return string.replace(includePattern,includeReplacer);}const shaderChunkMap=new Map();function includeReplacer(match,include){let string=ShaderChunk[include];if(string===undefined){const newInclude=shaderChunkMap.get(include);if(newInclude!==undefined){string=ShaderChunk[newInclude];console.warn('THREE.WebGLRenderer: Shader chunk "%s" has been deprecated. Use "%s" instead.',include,newInclude);}else {throw new Error('Can not resolve #include <'+include+'>');}}return resolveIncludes(string);}// Unroll Loops
const unrollLoopPattern=/#pragma unroll_loop_start\s+for\s*\(\s*int\s+i\s*=\s*(\d+)\s*;\s*i\s*<\s*(\d+)\s*;\s*i\s*\+\+\s*\)\s*{([\s\S]+?)}\s+#pragma unroll_loop_end/g;function unrollLoops(string){return string.replace(unrollLoopPattern,loopReplacer);}function loopReplacer(match,start,end,snippet){let string='';for(let i=parseInt(start);i<parseInt(end);i++){string+=snippet.replace(/\[\s*i\s*\]/g,'[ '+i+' ]').replace(/UNROLLED_LOOP_INDEX/g,i);}return string;}//
function generatePrecision(parameters){let precisionstring=`precision ${parameters.precision} float;
	precision ${parameters.precision} int;
	precision ${parameters.precision} sampler2D;
	precision ${parameters.precision} samplerCube;
	precision ${parameters.precision} sampler3D;
	precision ${parameters.precision} sampler2DArray;
	precision ${parameters.precision} sampler2DShadow;
	precision ${parameters.precision} samplerCubeShadow;
	precision ${parameters.precision} sampler2DArrayShadow;
	precision ${parameters.precision} isampler2D;
	precision ${parameters.precision} isampler3D;
	precision ${parameters.precision} isamplerCube;
	precision ${parameters.precision} isampler2DArray;
	precision ${parameters.precision} usampler2D;
	precision ${parameters.precision} usampler3D;
	precision ${parameters.precision} usamplerCube;
	precision ${parameters.precision} usampler2DArray;
	`;if(parameters.precision==='highp'){precisionstring+='\n#define HIGH_PRECISION';}else if(parameters.precision==='mediump'){precisionstring+='\n#define MEDIUM_PRECISION';}else if(parameters.precision==='lowp'){precisionstring+='\n#define LOW_PRECISION';}return precisionstring;}function generateShadowMapTypeDefine(parameters){let shadowMapTypeDefine='SHADOWMAP_TYPE_BASIC';if(parameters.shadowMapType===PCFShadowMap){shadowMapTypeDefine='SHADOWMAP_TYPE_PCF';}else if(parameters.shadowMapType===PCFSoftShadowMap){shadowMapTypeDefine='SHADOWMAP_TYPE_PCF_SOFT';}else if(parameters.shadowMapType===VSMShadowMap){shadowMapTypeDefine='SHADOWMAP_TYPE_VSM';}return shadowMapTypeDefine;}function generateEnvMapTypeDefine(parameters){let envMapTypeDefine='ENVMAP_TYPE_CUBE';if(parameters.envMap){switch(parameters.envMapMode){case CubeReflectionMapping$1:case CubeRefractionMapping$1:envMapTypeDefine='ENVMAP_TYPE_CUBE';break;case CubeUVReflectionMapping:envMapTypeDefine='ENVMAP_TYPE_CUBE_UV';break;}}return envMapTypeDefine;}function generateEnvMapModeDefine(parameters){let envMapModeDefine='ENVMAP_MODE_REFLECTION';if(parameters.envMap){switch(parameters.envMapMode){case CubeRefractionMapping$1:envMapModeDefine='ENVMAP_MODE_REFRACTION';break;}}return envMapModeDefine;}function generateEnvMapBlendingDefine(parameters){let envMapBlendingDefine='ENVMAP_BLENDING_NONE';if(parameters.envMap){switch(parameters.combine){case MultiplyOperation$1:envMapBlendingDefine='ENVMAP_BLENDING_MULTIPLY';break;case MixOperation:envMapBlendingDefine='ENVMAP_BLENDING_MIX';break;case AddOperation:envMapBlendingDefine='ENVMAP_BLENDING_ADD';break;}}return envMapBlendingDefine;}function generateCubeUVSize(parameters){const imageHeight=parameters.envMapCubeUVHeight;if(imageHeight===null)return null;const maxMip=Math.log2(imageHeight)-2;const texelHeight=1.0/imageHeight;const texelWidth=1.0/(3*Math.max(Math.pow(2,maxMip),7*16));return {texelWidth,texelHeight,maxMip};}function WebGLProgram(renderer,cacheKey,parameters,bindingStates){// TODO Send this event to Three.js DevTools
// console.log( 'WebGLProgram', cacheKey );
const gl=renderer.getContext();const defines=parameters.defines;let vertexShader=parameters.vertexShader;let fragmentShader=parameters.fragmentShader;const shadowMapTypeDefine=generateShadowMapTypeDefine(parameters);const envMapTypeDefine=generateEnvMapTypeDefine(parameters);const envMapModeDefine=generateEnvMapModeDefine(parameters);const envMapBlendingDefine=generateEnvMapBlendingDefine(parameters);const envMapCubeUVSize=generateCubeUVSize(parameters);const customVertexExtensions=generateVertexExtensions(parameters);const customDefines=generateDefines(defines);const program=gl.createProgram();let prefixVertex,prefixFragment;let versionString=parameters.glslVersion?'#version '+parameters.glslVersion+'\n':'';if(parameters.isRawShaderMaterial){prefixVertex=['#define SHADER_TYPE '+parameters.shaderType,'#define SHADER_NAME '+parameters.shaderName,customDefines].filter(filterEmptyLine).join('\n');if(prefixVertex.length>0){prefixVertex+='\n';}prefixFragment=['#define SHADER_TYPE '+parameters.shaderType,'#define SHADER_NAME '+parameters.shaderName,customDefines].filter(filterEmptyLine).join('\n');if(prefixFragment.length>0){prefixFragment+='\n';}}else {prefixVertex=[generatePrecision(parameters),'#define SHADER_TYPE '+parameters.shaderType,'#define SHADER_NAME '+parameters.shaderName,customDefines,parameters.extensionClipCullDistance?'#define USE_CLIP_DISTANCE':'',parameters.batching?'#define USE_BATCHING':'',parameters.batchingColor?'#define USE_BATCHING_COLOR':'',parameters.instancing?'#define USE_INSTANCING':'',parameters.instancingColor?'#define USE_INSTANCING_COLOR':'',parameters.instancingMorph?'#define USE_INSTANCING_MORPH':'',parameters.useFog&&parameters.fog?'#define USE_FOG':'',parameters.useFog&&parameters.fogExp2?'#define FOG_EXP2':'',parameters.map?'#define USE_MAP':'',parameters.envMap?'#define USE_ENVMAP':'',parameters.envMap?'#define '+envMapModeDefine:'',parameters.lightMap?'#define USE_LIGHTMAP':'',parameters.aoMap?'#define USE_AOMAP':'',parameters.bumpMap?'#define USE_BUMPMAP':'',parameters.normalMap?'#define USE_NORMALMAP':'',parameters.normalMapObjectSpace?'#define USE_NORMALMAP_OBJECTSPACE':'',parameters.normalMapTangentSpace?'#define USE_NORMALMAP_TANGENTSPACE':'',parameters.displacementMap?'#define USE_DISPLACEMENTMAP':'',parameters.emissiveMap?'#define USE_EMISSIVEMAP':'',parameters.anisotropy?'#define USE_ANISOTROPY':'',parameters.anisotropyMap?'#define USE_ANISOTROPYMAP':'',parameters.clearcoatMap?'#define USE_CLEARCOATMAP':'',parameters.clearcoatRoughnessMap?'#define USE_CLEARCOAT_ROUGHNESSMAP':'',parameters.clearcoatNormalMap?'#define USE_CLEARCOAT_NORMALMAP':'',parameters.iridescenceMap?'#define USE_IRIDESCENCEMAP':'',parameters.iridescenceThicknessMap?'#define USE_IRIDESCENCE_THICKNESSMAP':'',parameters.specularMap?'#define USE_SPECULARMAP':'',parameters.specularColorMap?'#define USE_SPECULAR_COLORMAP':'',parameters.specularIntensityMap?'#define USE_SPECULAR_INTENSITYMAP':'',parameters.roughnessMap?'#define USE_ROUGHNESSMAP':'',parameters.metalnessMap?'#define USE_METALNESSMAP':'',parameters.alphaMap?'#define USE_ALPHAMAP':'',parameters.alphaHash?'#define USE_ALPHAHASH':'',parameters.transmission?'#define USE_TRANSMISSION':'',parameters.transmissionMap?'#define USE_TRANSMISSIONMAP':'',parameters.thicknessMap?'#define USE_THICKNESSMAP':'',parameters.sheenColorMap?'#define USE_SHEEN_COLORMAP':'',parameters.sheenRoughnessMap?'#define USE_SHEEN_ROUGHNESSMAP':'',//
parameters.mapUv?'#define MAP_UV '+parameters.mapUv:'',parameters.alphaMapUv?'#define ALPHAMAP_UV '+parameters.alphaMapUv:'',parameters.lightMapUv?'#define LIGHTMAP_UV '+parameters.lightMapUv:'',parameters.aoMapUv?'#define AOMAP_UV '+parameters.aoMapUv:'',parameters.emissiveMapUv?'#define EMISSIVEMAP_UV '+parameters.emissiveMapUv:'',parameters.bumpMapUv?'#define BUMPMAP_UV '+parameters.bumpMapUv:'',parameters.normalMapUv?'#define NORMALMAP_UV '+parameters.normalMapUv:'',parameters.displacementMapUv?'#define DISPLACEMENTMAP_UV '+parameters.displacementMapUv:'',parameters.metalnessMapUv?'#define METALNESSMAP_UV '+parameters.metalnessMapUv:'',parameters.roughnessMapUv?'#define ROUGHNESSMAP_UV '+parameters.roughnessMapUv:'',parameters.anisotropyMapUv?'#define ANISOTROPYMAP_UV '+parameters.anisotropyMapUv:'',parameters.clearcoatMapUv?'#define CLEARCOATMAP_UV '+parameters.clearcoatMapUv:'',parameters.clearcoatNormalMapUv?'#define CLEARCOAT_NORMALMAP_UV '+parameters.clearcoatNormalMapUv:'',parameters.clearcoatRoughnessMapUv?'#define CLEARCOAT_ROUGHNESSMAP_UV '+parameters.clearcoatRoughnessMapUv:'',parameters.iridescenceMapUv?'#define IRIDESCENCEMAP_UV '+parameters.iridescenceMapUv:'',parameters.iridescenceThicknessMapUv?'#define IRIDESCENCE_THICKNESSMAP_UV '+parameters.iridescenceThicknessMapUv:'',parameters.sheenColorMapUv?'#define SHEEN_COLORMAP_UV '+parameters.sheenColorMapUv:'',parameters.sheenRoughnessMapUv?'#define SHEEN_ROUGHNESSMAP_UV '+parameters.sheenRoughnessMapUv:'',parameters.specularMapUv?'#define SPECULARMAP_UV '+parameters.specularMapUv:'',parameters.specularColorMapUv?'#define SPECULAR_COLORMAP_UV '+parameters.specularColorMapUv:'',parameters.specularIntensityMapUv?'#define SPECULAR_INTENSITYMAP_UV '+parameters.specularIntensityMapUv:'',parameters.transmissionMapUv?'#define TRANSMISSIONMAP_UV '+parameters.transmissionMapUv:'',parameters.thicknessMapUv?'#define THICKNESSMAP_UV '+parameters.thicknessMapUv:'',//
parameters.vertexTangents&&parameters.flatShading===false?'#define USE_TANGENT':'',parameters.vertexColors?'#define USE_COLOR':'',parameters.vertexAlphas?'#define USE_COLOR_ALPHA':'',parameters.vertexUv1s?'#define USE_UV1':'',parameters.vertexUv2s?'#define USE_UV2':'',parameters.vertexUv3s?'#define USE_UV3':'',parameters.pointsUvs?'#define USE_POINTS_UV':'',parameters.flatShading?'#define FLAT_SHADED':'',parameters.skinning?'#define USE_SKINNING':'',parameters.morphTargets?'#define USE_MORPHTARGETS':'',parameters.morphNormals&&parameters.flatShading===false?'#define USE_MORPHNORMALS':'',parameters.morphColors?'#define USE_MORPHCOLORS':'',parameters.morphTargetsCount>0?'#define MORPHTARGETS_TEXTURE_STRIDE '+parameters.morphTextureStride:'',parameters.morphTargetsCount>0?'#define MORPHTARGETS_COUNT '+parameters.morphTargetsCount:'',parameters.doubleSided?'#define DOUBLE_SIDED':'',parameters.flipSided?'#define FLIP_SIDED':'',parameters.shadowMapEnabled?'#define USE_SHADOWMAP':'',parameters.shadowMapEnabled?'#define '+shadowMapTypeDefine:'',parameters.sizeAttenuation?'#define USE_SIZEATTENUATION':'',parameters.numLightProbes>0?'#define USE_LIGHT_PROBES':'',parameters.logarithmicDepthBuffer?'#define USE_LOGDEPTHBUF':'','uniform mat4 modelMatrix;','uniform mat4 modelViewMatrix;','uniform mat4 projectionMatrix;','uniform mat4 viewMatrix;','uniform mat3 normalMatrix;','uniform vec3 cameraPosition;','uniform bool isOrthographic;','#ifdef USE_INSTANCING','	attribute mat4 instanceMatrix;','#endif','#ifdef USE_INSTANCING_COLOR','	attribute vec3 instanceColor;','#endif','#ifdef USE_INSTANCING_MORPH','	uniform sampler2D morphTexture;','#endif','attribute vec3 position;','attribute vec3 normal;','attribute vec2 uv;','#ifdef USE_UV1','	attribute vec2 uv1;','#endif','#ifdef USE_UV2','	attribute vec2 uv2;','#endif','#ifdef USE_UV3','	attribute vec2 uv3;','#endif','#ifdef USE_TANGENT','	attribute vec4 tangent;','#endif','#if defined( USE_COLOR_ALPHA )','	attribute vec4 color;','#elif defined( USE_COLOR )','	attribute vec3 color;','#endif','#ifdef USE_SKINNING','	attribute vec4 skinIndex;','	attribute vec4 skinWeight;','#endif','\n'].filter(filterEmptyLine).join('\n');prefixFragment=[generatePrecision(parameters),'#define SHADER_TYPE '+parameters.shaderType,'#define SHADER_NAME '+parameters.shaderName,customDefines,parameters.useFog&&parameters.fog?'#define USE_FOG':'',parameters.useFog&&parameters.fogExp2?'#define FOG_EXP2':'',parameters.alphaToCoverage?'#define ALPHA_TO_COVERAGE':'',parameters.map?'#define USE_MAP':'',parameters.matcap?'#define USE_MATCAP':'',parameters.envMap?'#define USE_ENVMAP':'',parameters.envMap?'#define '+envMapTypeDefine:'',parameters.envMap?'#define '+envMapModeDefine:'',parameters.envMap?'#define '+envMapBlendingDefine:'',envMapCubeUVSize?'#define CUBEUV_TEXEL_WIDTH '+envMapCubeUVSize.texelWidth:'',envMapCubeUVSize?'#define CUBEUV_TEXEL_HEIGHT '+envMapCubeUVSize.texelHeight:'',envMapCubeUVSize?'#define CUBEUV_MAX_MIP '+envMapCubeUVSize.maxMip+'.0':'',parameters.lightMap?'#define USE_LIGHTMAP':'',parameters.aoMap?'#define USE_AOMAP':'',parameters.bumpMap?'#define USE_BUMPMAP':'',parameters.normalMap?'#define USE_NORMALMAP':'',parameters.normalMapObjectSpace?'#define USE_NORMALMAP_OBJECTSPACE':'',parameters.normalMapTangentSpace?'#define USE_NORMALMAP_TANGENTSPACE':'',parameters.emissiveMap?'#define USE_EMISSIVEMAP':'',parameters.anisotropy?'#define USE_ANISOTROPY':'',parameters.anisotropyMap?'#define USE_ANISOTROPYMAP':'',parameters.clearcoat?'#define USE_CLEARCOAT':'',parameters.clearcoatMap?'#define USE_CLEARCOATMAP':'',parameters.clearcoatRoughnessMap?'#define USE_CLEARCOAT_ROUGHNESSMAP':'',parameters.clearcoatNormalMap?'#define USE_CLEARCOAT_NORMALMAP':'',parameters.dispersion?'#define USE_DISPERSION':'',parameters.iridescence?'#define USE_IRIDESCENCE':'',parameters.iridescenceMap?'#define USE_IRIDESCENCEMAP':'',parameters.iridescenceThicknessMap?'#define USE_IRIDESCENCE_THICKNESSMAP':'',parameters.specularMap?'#define USE_SPECULARMAP':'',parameters.specularColorMap?'#define USE_SPECULAR_COLORMAP':'',parameters.specularIntensityMap?'#define USE_SPECULAR_INTENSITYMAP':'',parameters.roughnessMap?'#define USE_ROUGHNESSMAP':'',parameters.metalnessMap?'#define USE_METALNESSMAP':'',parameters.alphaMap?'#define USE_ALPHAMAP':'',parameters.alphaTest?'#define USE_ALPHATEST':'',parameters.alphaHash?'#define USE_ALPHAHASH':'',parameters.sheen?'#define USE_SHEEN':'',parameters.sheenColorMap?'#define USE_SHEEN_COLORMAP':'',parameters.sheenRoughnessMap?'#define USE_SHEEN_ROUGHNESSMAP':'',parameters.transmission?'#define USE_TRANSMISSION':'',parameters.transmissionMap?'#define USE_TRANSMISSIONMAP':'',parameters.thicknessMap?'#define USE_THICKNESSMAP':'',parameters.vertexTangents&&parameters.flatShading===false?'#define USE_TANGENT':'',parameters.vertexColors||parameters.instancingColor||parameters.batchingColor?'#define USE_COLOR':'',parameters.vertexAlphas?'#define USE_COLOR_ALPHA':'',parameters.vertexUv1s?'#define USE_UV1':'',parameters.vertexUv2s?'#define USE_UV2':'',parameters.vertexUv3s?'#define USE_UV3':'',parameters.pointsUvs?'#define USE_POINTS_UV':'',parameters.gradientMap?'#define USE_GRADIENTMAP':'',parameters.flatShading?'#define FLAT_SHADED':'',parameters.doubleSided?'#define DOUBLE_SIDED':'',parameters.flipSided?'#define FLIP_SIDED':'',parameters.shadowMapEnabled?'#define USE_SHADOWMAP':'',parameters.shadowMapEnabled?'#define '+shadowMapTypeDefine:'',parameters.premultipliedAlpha?'#define PREMULTIPLIED_ALPHA':'',parameters.numLightProbes>0?'#define USE_LIGHT_PROBES':'',parameters.decodeVideoTexture?'#define DECODE_VIDEO_TEXTURE':'',parameters.logarithmicDepthBuffer?'#define USE_LOGDEPTHBUF':'','uniform mat4 viewMatrix;','uniform vec3 cameraPosition;','uniform bool isOrthographic;',parameters.toneMapping!==NoToneMapping$1?'#define TONE_MAPPING':'',parameters.toneMapping!==NoToneMapping$1?ShaderChunk['tonemapping_pars_fragment']:'',// this code is required here because it is used by the toneMapping() function defined below
parameters.toneMapping!==NoToneMapping$1?getToneMappingFunction('toneMapping',parameters.toneMapping):'',parameters.dithering?'#define DITHERING':'',parameters.opaque?'#define OPAQUE':'',ShaderChunk['colorspace_pars_fragment'],// this code is required here because it is used by the various encoding/decoding function defined below
getTexelEncodingFunction('linearToOutputTexel',parameters.outputColorSpace),getLuminanceFunction(),parameters.useDepthPacking?'#define DEPTH_PACKING '+parameters.depthPacking:'','\n'].filter(filterEmptyLine).join('\n');}vertexShader=resolveIncludes(vertexShader);vertexShader=replaceLightNums(vertexShader,parameters);vertexShader=replaceClippingPlaneNums(vertexShader,parameters);fragmentShader=resolveIncludes(fragmentShader);fragmentShader=replaceLightNums(fragmentShader,parameters);fragmentShader=replaceClippingPlaneNums(fragmentShader,parameters);vertexShader=unrollLoops(vertexShader);fragmentShader=unrollLoops(fragmentShader);if(parameters.isRawShaderMaterial!==true){// GLSL 3.0 conversion for built-in materials and ShaderMaterial
versionString='#version 300 es\n';prefixVertex=[customVertexExtensions,'#define attribute in','#define varying out','#define texture2D texture'].join('\n')+'\n'+prefixVertex;prefixFragment=['#define varying in',parameters.glslVersion===GLSL3?'':'layout(location = 0) out highp vec4 pc_fragColor;',parameters.glslVersion===GLSL3?'':'#define gl_FragColor pc_fragColor','#define gl_FragDepthEXT gl_FragDepth','#define texture2D texture','#define textureCube texture','#define texture2DProj textureProj','#define texture2DLodEXT textureLod','#define texture2DProjLodEXT textureProjLod','#define textureCubeLodEXT textureLod','#define texture2DGradEXT textureGrad','#define texture2DProjGradEXT textureProjGrad','#define textureCubeGradEXT textureGrad'].join('\n')+'\n'+prefixFragment;}const vertexGlsl=versionString+prefixVertex+vertexShader;const fragmentGlsl=versionString+prefixFragment+fragmentShader;// console.log( '*VERTEX*', vertexGlsl );
// console.log( '*FRAGMENT*', fragmentGlsl );
const glVertexShader=WebGLShader(gl,gl.VERTEX_SHADER,vertexGlsl);const glFragmentShader=WebGLShader(gl,gl.FRAGMENT_SHADER,fragmentGlsl);gl.attachShader(program,glVertexShader);gl.attachShader(program,glFragmentShader);// Force a particular attribute to index 0.
if(parameters.index0AttributeName!==undefined){gl.bindAttribLocation(program,0,parameters.index0AttributeName);}else if(parameters.morphTargets===true){// programs with morphTargets displace position out of attribute 0
gl.bindAttribLocation(program,0,'position');}gl.linkProgram(program);function onFirstUse(self){// check for link errors
if(renderer.debug.checkShaderErrors){const programLog=gl.getProgramInfoLog(program).trim();const vertexLog=gl.getShaderInfoLog(glVertexShader).trim();const fragmentLog=gl.getShaderInfoLog(glFragmentShader).trim();let runnable=true;let haveDiagnostics=true;if(gl.getProgramParameter(program,gl.LINK_STATUS)===false){runnable=false;if(typeof renderer.debug.onShaderError==='function'){renderer.debug.onShaderError(gl,program,glVertexShader,glFragmentShader);}else {// default error reporting
const vertexErrors=getShaderErrors(gl,glVertexShader,'vertex');const fragmentErrors=getShaderErrors(gl,glFragmentShader,'fragment');console.error('THREE.WebGLProgram: Shader Error '+gl.getError()+' - '+'VALIDATE_STATUS '+gl.getProgramParameter(program,gl.VALIDATE_STATUS)+'\n\n'+'Material Name: '+self.name+'\n'+'Material Type: '+self.type+'\n\n'+'Program Info Log: '+programLog+'\n'+vertexErrors+'\n'+fragmentErrors);}}else if(programLog!==''){console.warn('THREE.WebGLProgram: Program Info Log:',programLog);}else if(vertexLog===''||fragmentLog===''){haveDiagnostics=false;}if(haveDiagnostics){self.diagnostics={runnable:runnable,programLog:programLog,vertexShader:{log:vertexLog,prefix:prefixVertex},fragmentShader:{log:fragmentLog,prefix:prefixFragment}};}}// Clean up
// Crashes in iOS9 and iOS10. #18402
// gl.detachShader( program, glVertexShader );
// gl.detachShader( program, glFragmentShader );
gl.deleteShader(glVertexShader);gl.deleteShader(glFragmentShader);cachedUniforms=new WebGLUniforms(gl,program);cachedAttributes=fetchAttributeLocations(gl,program);}// set up caching for uniform locations
let cachedUniforms;this.getUniforms=function(){if(cachedUniforms===undefined){// Populates cachedUniforms and cachedAttributes
onFirstUse(this);}return cachedUniforms;};// set up caching for attribute locations
let cachedAttributes;this.getAttributes=function(){if(cachedAttributes===undefined){// Populates cachedAttributes and cachedUniforms
onFirstUse(this);}return cachedAttributes;};// indicate when the program is ready to be used. if the KHR_parallel_shader_compile extension isn't supported,
// flag the program as ready immediately. It may cause a stall when it's first used.
let programReady=parameters.rendererExtensionParallelShaderCompile===false;this.isReady=function(){if(programReady===false){programReady=gl.getProgramParameter(program,COMPLETION_STATUS_KHR);}return programReady;};// free resource
this.destroy=function(){bindingStates.releaseStatesOfProgram(this);gl.deleteProgram(program);this.program=undefined;};//
this.type=parameters.shaderType;this.name=parameters.shaderName;this.id=programIdCount++;this.cacheKey=cacheKey;this.usedTimes=1;this.program=program;this.vertexShader=glVertexShader;this.fragmentShader=glFragmentShader;return this;}let _id$1=0;class WebGLShaderCache{constructor(){this.shaderCache=new Map();this.materialCache=new Map();}update(material){const vertexShader=material.vertexShader;const fragmentShader=material.fragmentShader;const vertexShaderStage=this._getShaderStage(vertexShader);const fragmentShaderStage=this._getShaderStage(fragmentShader);const materialShaders=this._getShaderCacheForMaterial(material);if(materialShaders.has(vertexShaderStage)===false){materialShaders.add(vertexShaderStage);vertexShaderStage.usedTimes++;}if(materialShaders.has(fragmentShaderStage)===false){materialShaders.add(fragmentShaderStage);fragmentShaderStage.usedTimes++;}return this;}remove(material){const materialShaders=this.materialCache.get(material);for(const shaderStage of materialShaders){shaderStage.usedTimes--;if(shaderStage.usedTimes===0)this.shaderCache.delete(shaderStage.code);}this.materialCache.delete(material);return this;}getVertexShaderID(material){return this._getShaderStage(material.vertexShader).id;}getFragmentShaderID(material){return this._getShaderStage(material.fragmentShader).id;}dispose(){this.shaderCache.clear();this.materialCache.clear();}_getShaderCacheForMaterial(material){const cache=this.materialCache;let set=cache.get(material);if(set===undefined){set=new Set();cache.set(material,set);}return set;}_getShaderStage(code){const cache=this.shaderCache;let stage=cache.get(code);if(stage===undefined){stage=new WebGLShaderStage(code);cache.set(code,stage);}return stage;}}class WebGLShaderStage{constructor(code){this.id=_id$1++;this.code=code;this.usedTimes=0;}}function WebGLPrograms(renderer,cubemaps,cubeuvmaps,extensions,capabilities,bindingStates,clipping){const _programLayers=new Layers$1();const _customShaders=new WebGLShaderCache();const _activeChannels=new Set();const programs=[];const logarithmicDepthBuffer=capabilities.logarithmicDepthBuffer;const SUPPORTS_VERTEX_TEXTURES=capabilities.vertexTextures;let precision=capabilities.precision;const shaderIDs={MeshDepthMaterial:'depth',MeshDistanceMaterial:'distanceRGBA',MeshNormalMaterial:'normal',MeshBasicMaterial:'basic',MeshLambertMaterial:'lambert',MeshPhongMaterial:'phong',MeshToonMaterial:'toon',MeshStandardMaterial:'physical',MeshPhysicalMaterial:'physical',MeshMatcapMaterial:'matcap',LineBasicMaterial:'basic',LineDashedMaterial:'dashed',PointsMaterial:'points',ShadowMaterial:'shadow',SpriteMaterial:'sprite'};function getChannel(value){_activeChannels.add(value);if(value===0)return 'uv';return `uv${value}`;}function getParameters(material,lights,shadows,scene,object){const fog=scene.fog;const geometry=object.geometry;const environment=material.isMeshStandardMaterial?scene.environment:null;const envMap=(material.isMeshStandardMaterial?cubeuvmaps:cubemaps).get(material.envMap||environment);const envMapCubeUVHeight=!!envMap&&envMap.mapping===CubeUVReflectionMapping?envMap.image.height:null;const shaderID=shaderIDs[material.type];// heuristics to create shader parameters according to lights in the scene
// (not to blow over maxLights budget)
if(material.precision!==null){precision=capabilities.getMaxPrecision(material.precision);if(precision!==material.precision){console.warn('THREE.WebGLProgram.getParameters:',material.precision,'not supported, using',precision,'instead.');}}//
const morphAttribute=geometry.morphAttributes.position||geometry.morphAttributes.normal||geometry.morphAttributes.color;const morphTargetsCount=morphAttribute!==undefined?morphAttribute.length:0;let morphTextureStride=0;if(geometry.morphAttributes.position!==undefined)morphTextureStride=1;if(geometry.morphAttributes.normal!==undefined)morphTextureStride=2;if(geometry.morphAttributes.color!==undefined)morphTextureStride=3;//
let vertexShader,fragmentShader;let customVertexShaderID,customFragmentShaderID;if(shaderID){const shader=ShaderLib[shaderID];vertexShader=shader.vertexShader;fragmentShader=shader.fragmentShader;}else {vertexShader=material.vertexShader;fragmentShader=material.fragmentShader;_customShaders.update(material);customVertexShaderID=_customShaders.getVertexShaderID(material);customFragmentShaderID=_customShaders.getFragmentShaderID(material);}const currentRenderTarget=renderer.getRenderTarget();const IS_INSTANCEDMESH=object.isInstancedMesh===true;const IS_BATCHEDMESH=object.isBatchedMesh===true;const HAS_MAP=!!material.map;const HAS_MATCAP=!!material.matcap;const HAS_ENVMAP=!!envMap;const HAS_AOMAP=!!material.aoMap;const HAS_LIGHTMAP=!!material.lightMap;const HAS_BUMPMAP=!!material.bumpMap;const HAS_NORMALMAP=!!material.normalMap;const HAS_DISPLACEMENTMAP=!!material.displacementMap;const HAS_EMISSIVEMAP=!!material.emissiveMap;const HAS_METALNESSMAP=!!material.metalnessMap;const HAS_ROUGHNESSMAP=!!material.roughnessMap;const HAS_ANISOTROPY=material.anisotropy>0;const HAS_CLEARCOAT=material.clearcoat>0;const HAS_DISPERSION=material.dispersion>0;const HAS_IRIDESCENCE=material.iridescence>0;const HAS_SHEEN=material.sheen>0;const HAS_TRANSMISSION=material.transmission>0;const HAS_ANISOTROPYMAP=HAS_ANISOTROPY&&!!material.anisotropyMap;const HAS_CLEARCOATMAP=HAS_CLEARCOAT&&!!material.clearcoatMap;const HAS_CLEARCOAT_NORMALMAP=HAS_CLEARCOAT&&!!material.clearcoatNormalMap;const HAS_CLEARCOAT_ROUGHNESSMAP=HAS_CLEARCOAT&&!!material.clearcoatRoughnessMap;const HAS_IRIDESCENCEMAP=HAS_IRIDESCENCE&&!!material.iridescenceMap;const HAS_IRIDESCENCE_THICKNESSMAP=HAS_IRIDESCENCE&&!!material.iridescenceThicknessMap;const HAS_SHEEN_COLORMAP=HAS_SHEEN&&!!material.sheenColorMap;const HAS_SHEEN_ROUGHNESSMAP=HAS_SHEEN&&!!material.sheenRoughnessMap;const HAS_SPECULARMAP=!!material.specularMap;const HAS_SPECULAR_COLORMAP=!!material.specularColorMap;const HAS_SPECULAR_INTENSITYMAP=!!material.specularIntensityMap;const HAS_TRANSMISSIONMAP=HAS_TRANSMISSION&&!!material.transmissionMap;const HAS_THICKNESSMAP=HAS_TRANSMISSION&&!!material.thicknessMap;const HAS_GRADIENTMAP=!!material.gradientMap;const HAS_ALPHAMAP=!!material.alphaMap;const HAS_ALPHATEST=material.alphaTest>0;const HAS_ALPHAHASH=!!material.alphaHash;const HAS_EXTENSIONS=!!material.extensions;let toneMapping=NoToneMapping$1;if(material.toneMapped){if(currentRenderTarget===null||currentRenderTarget.isXRRenderTarget===true){toneMapping=renderer.toneMapping;}}const parameters={shaderID:shaderID,shaderType:material.type,shaderName:material.name,vertexShader:vertexShader,fragmentShader:fragmentShader,defines:material.defines,customVertexShaderID:customVertexShaderID,customFragmentShaderID:customFragmentShaderID,isRawShaderMaterial:material.isRawShaderMaterial===true,glslVersion:material.glslVersion,precision:precision,batching:IS_BATCHEDMESH,batchingColor:IS_BATCHEDMESH&&object._colorsTexture!==null,instancing:IS_INSTANCEDMESH,instancingColor:IS_INSTANCEDMESH&&object.instanceColor!==null,instancingMorph:IS_INSTANCEDMESH&&object.morphTexture!==null,supportsVertexTextures:SUPPORTS_VERTEX_TEXTURES,outputColorSpace:currentRenderTarget===null?renderer.outputColorSpace:currentRenderTarget.isXRRenderTarget===true?currentRenderTarget.texture.colorSpace:LinearSRGBColorSpace$1,alphaToCoverage:!!material.alphaToCoverage,map:HAS_MAP,matcap:HAS_MATCAP,envMap:HAS_ENVMAP,envMapMode:HAS_ENVMAP&&envMap.mapping,envMapCubeUVHeight:envMapCubeUVHeight,aoMap:HAS_AOMAP,lightMap:HAS_LIGHTMAP,bumpMap:HAS_BUMPMAP,normalMap:HAS_NORMALMAP,displacementMap:SUPPORTS_VERTEX_TEXTURES&&HAS_DISPLACEMENTMAP,emissiveMap:HAS_EMISSIVEMAP,normalMapObjectSpace:HAS_NORMALMAP&&material.normalMapType===ObjectSpaceNormalMap$1,normalMapTangentSpace:HAS_NORMALMAP&&material.normalMapType===TangentSpaceNormalMap$1,metalnessMap:HAS_METALNESSMAP,roughnessMap:HAS_ROUGHNESSMAP,anisotropy:HAS_ANISOTROPY,anisotropyMap:HAS_ANISOTROPYMAP,clearcoat:HAS_CLEARCOAT,clearcoatMap:HAS_CLEARCOATMAP,clearcoatNormalMap:HAS_CLEARCOAT_NORMALMAP,clearcoatRoughnessMap:HAS_CLEARCOAT_ROUGHNESSMAP,dispersion:HAS_DISPERSION,iridescence:HAS_IRIDESCENCE,iridescenceMap:HAS_IRIDESCENCEMAP,iridescenceThicknessMap:HAS_IRIDESCENCE_THICKNESSMAP,sheen:HAS_SHEEN,sheenColorMap:HAS_SHEEN_COLORMAP,sheenRoughnessMap:HAS_SHEEN_ROUGHNESSMAP,specularMap:HAS_SPECULARMAP,specularColorMap:HAS_SPECULAR_COLORMAP,specularIntensityMap:HAS_SPECULAR_INTENSITYMAP,transmission:HAS_TRANSMISSION,transmissionMap:HAS_TRANSMISSIONMAP,thicknessMap:HAS_THICKNESSMAP,gradientMap:HAS_GRADIENTMAP,opaque:material.transparent===false&&material.blending===NormalBlending$1&&material.alphaToCoverage===false,alphaMap:HAS_ALPHAMAP,alphaTest:HAS_ALPHATEST,alphaHash:HAS_ALPHAHASH,combine:material.combine,//
mapUv:HAS_MAP&&getChannel(material.map.channel),aoMapUv:HAS_AOMAP&&getChannel(material.aoMap.channel),lightMapUv:HAS_LIGHTMAP&&getChannel(material.lightMap.channel),bumpMapUv:HAS_BUMPMAP&&getChannel(material.bumpMap.channel),normalMapUv:HAS_NORMALMAP&&getChannel(material.normalMap.channel),displacementMapUv:HAS_DISPLACEMENTMAP&&getChannel(material.displacementMap.channel),emissiveMapUv:HAS_EMISSIVEMAP&&getChannel(material.emissiveMap.channel),metalnessMapUv:HAS_METALNESSMAP&&getChannel(material.metalnessMap.channel),roughnessMapUv:HAS_ROUGHNESSMAP&&getChannel(material.roughnessMap.channel),anisotropyMapUv:HAS_ANISOTROPYMAP&&getChannel(material.anisotropyMap.channel),clearcoatMapUv:HAS_CLEARCOATMAP&&getChannel(material.clearcoatMap.channel),clearcoatNormalMapUv:HAS_CLEARCOAT_NORMALMAP&&getChannel(material.clearcoatNormalMap.channel),clearcoatRoughnessMapUv:HAS_CLEARCOAT_ROUGHNESSMAP&&getChannel(material.clearcoatRoughnessMap.channel),iridescenceMapUv:HAS_IRIDESCENCEMAP&&getChannel(material.iridescenceMap.channel),iridescenceThicknessMapUv:HAS_IRIDESCENCE_THICKNESSMAP&&getChannel(material.iridescenceThicknessMap.channel),sheenColorMapUv:HAS_SHEEN_COLORMAP&&getChannel(material.sheenColorMap.channel),sheenRoughnessMapUv:HAS_SHEEN_ROUGHNESSMAP&&getChannel(material.sheenRoughnessMap.channel),specularMapUv:HAS_SPECULARMAP&&getChannel(material.specularMap.channel),specularColorMapUv:HAS_SPECULAR_COLORMAP&&getChannel(material.specularColorMap.channel),specularIntensityMapUv:HAS_SPECULAR_INTENSITYMAP&&getChannel(material.specularIntensityMap.channel),transmissionMapUv:HAS_TRANSMISSIONMAP&&getChannel(material.transmissionMap.channel),thicknessMapUv:HAS_THICKNESSMAP&&getChannel(material.thicknessMap.channel),alphaMapUv:HAS_ALPHAMAP&&getChannel(material.alphaMap.channel),//
vertexTangents:!!geometry.attributes.tangent&&(HAS_NORMALMAP||HAS_ANISOTROPY),vertexColors:material.vertexColors,vertexAlphas:material.vertexColors===true&&!!geometry.attributes.color&&geometry.attributes.color.itemSize===4,pointsUvs:object.isPoints===true&&!!geometry.attributes.uv&&(HAS_MAP||HAS_ALPHAMAP),fog:!!fog,useFog:material.fog===true,fogExp2:!!fog&&fog.isFogExp2,flatShading:material.flatShading===true,sizeAttenuation:material.sizeAttenuation===true,logarithmicDepthBuffer:logarithmicDepthBuffer,skinning:object.isSkinnedMesh===true,morphTargets:geometry.morphAttributes.position!==undefined,morphNormals:geometry.morphAttributes.normal!==undefined,morphColors:geometry.morphAttributes.color!==undefined,morphTargetsCount:morphTargetsCount,morphTextureStride:morphTextureStride,numDirLights:lights.directional.length,numPointLights:lights.point.length,numSpotLights:lights.spot.length,numSpotLightMaps:lights.spotLightMap.length,numRectAreaLights:lights.rectArea.length,numHemiLights:lights.hemi.length,numDirLightShadows:lights.directionalShadowMap.length,numPointLightShadows:lights.pointShadowMap.length,numSpotLightShadows:lights.spotShadowMap.length,numSpotLightShadowsWithMaps:lights.numSpotLightShadowsWithMaps,numLightProbes:lights.numLightProbes,numClippingPlanes:clipping.numPlanes,numClipIntersection:clipping.numIntersection,dithering:material.dithering,shadowMapEnabled:renderer.shadowMap.enabled&&shadows.length>0,shadowMapType:renderer.shadowMap.type,toneMapping:toneMapping,decodeVideoTexture:HAS_MAP&&material.map.isVideoTexture===true&&ColorManagement$1.getTransfer(material.map.colorSpace)===SRGBTransfer$1,premultipliedAlpha:material.premultipliedAlpha,doubleSided:material.side===DoubleSide,flipSided:material.side===BackSide$1,useDepthPacking:material.depthPacking>=0,depthPacking:material.depthPacking||0,index0AttributeName:material.index0AttributeName,extensionClipCullDistance:HAS_EXTENSIONS&&material.extensions.clipCullDistance===true&&extensions.has('WEBGL_clip_cull_distance'),extensionMultiDraw:(HAS_EXTENSIONS&&material.extensions.multiDraw===true||IS_BATCHEDMESH)&&extensions.has('WEBGL_multi_draw'),rendererExtensionParallelShaderCompile:extensions.has('KHR_parallel_shader_compile'),customProgramCacheKey:material.customProgramCacheKey()};// the usage of getChannel() determines the active texture channels for this shader
parameters.vertexUv1s=_activeChannels.has(1);parameters.vertexUv2s=_activeChannels.has(2);parameters.vertexUv3s=_activeChannels.has(3);_activeChannels.clear();return parameters;}function getProgramCacheKey(parameters){const array=[];if(parameters.shaderID){array.push(parameters.shaderID);}else {array.push(parameters.customVertexShaderID);array.push(parameters.customFragmentShaderID);}if(parameters.defines!==undefined){for(const name in parameters.defines){array.push(name);array.push(parameters.defines[name]);}}if(parameters.isRawShaderMaterial===false){getProgramCacheKeyParameters(array,parameters);getProgramCacheKeyBooleans(array,parameters);array.push(renderer.outputColorSpace);}array.push(parameters.customProgramCacheKey);return array.join();}function getProgramCacheKeyParameters(array,parameters){array.push(parameters.precision);array.push(parameters.outputColorSpace);array.push(parameters.envMapMode);array.push(parameters.envMapCubeUVHeight);array.push(parameters.mapUv);array.push(parameters.alphaMapUv);array.push(parameters.lightMapUv);array.push(parameters.aoMapUv);array.push(parameters.bumpMapUv);array.push(parameters.normalMapUv);array.push(parameters.displacementMapUv);array.push(parameters.emissiveMapUv);array.push(parameters.metalnessMapUv);array.push(parameters.roughnessMapUv);array.push(parameters.anisotropyMapUv);array.push(parameters.clearcoatMapUv);array.push(parameters.clearcoatNormalMapUv);array.push(parameters.clearcoatRoughnessMapUv);array.push(parameters.iridescenceMapUv);array.push(parameters.iridescenceThicknessMapUv);array.push(parameters.sheenColorMapUv);array.push(parameters.sheenRoughnessMapUv);array.push(parameters.specularMapUv);array.push(parameters.specularColorMapUv);array.push(parameters.specularIntensityMapUv);array.push(parameters.transmissionMapUv);array.push(parameters.thicknessMapUv);array.push(parameters.combine);array.push(parameters.fogExp2);array.push(parameters.sizeAttenuation);array.push(parameters.morphTargetsCount);array.push(parameters.morphAttributeCount);array.push(parameters.numDirLights);array.push(parameters.numPointLights);array.push(parameters.numSpotLights);array.push(parameters.numSpotLightMaps);array.push(parameters.numHemiLights);array.push(parameters.numRectAreaLights);array.push(parameters.numDirLightShadows);array.push(parameters.numPointLightShadows);array.push(parameters.numSpotLightShadows);array.push(parameters.numSpotLightShadowsWithMaps);array.push(parameters.numLightProbes);array.push(parameters.shadowMapType);array.push(parameters.toneMapping);array.push(parameters.numClippingPlanes);array.push(parameters.numClipIntersection);array.push(parameters.depthPacking);}function getProgramCacheKeyBooleans(array,parameters){_programLayers.disableAll();if(parameters.supportsVertexTextures)_programLayers.enable(0);if(parameters.instancing)_programLayers.enable(1);if(parameters.instancingColor)_programLayers.enable(2);if(parameters.instancingMorph)_programLayers.enable(3);if(parameters.matcap)_programLayers.enable(4);if(parameters.envMap)_programLayers.enable(5);if(parameters.normalMapObjectSpace)_programLayers.enable(6);if(parameters.normalMapTangentSpace)_programLayers.enable(7);if(parameters.clearcoat)_programLayers.enable(8);if(parameters.iridescence)_programLayers.enable(9);if(parameters.alphaTest)_programLayers.enable(10);if(parameters.vertexColors)_programLayers.enable(11);if(parameters.vertexAlphas)_programLayers.enable(12);if(parameters.vertexUv1s)_programLayers.enable(13);if(parameters.vertexUv2s)_programLayers.enable(14);if(parameters.vertexUv3s)_programLayers.enable(15);if(parameters.vertexTangents)_programLayers.enable(16);if(parameters.anisotropy)_programLayers.enable(17);if(parameters.alphaHash)_programLayers.enable(18);if(parameters.batching)_programLayers.enable(19);if(parameters.dispersion)_programLayers.enable(20);if(parameters.batchingColor)_programLayers.enable(21);array.push(_programLayers.mask);_programLayers.disableAll();if(parameters.fog)_programLayers.enable(0);if(parameters.useFog)_programLayers.enable(1);if(parameters.flatShading)_programLayers.enable(2);if(parameters.logarithmicDepthBuffer)_programLayers.enable(3);if(parameters.skinning)_programLayers.enable(4);if(parameters.morphTargets)_programLayers.enable(5);if(parameters.morphNormals)_programLayers.enable(6);if(parameters.morphColors)_programLayers.enable(7);if(parameters.premultipliedAlpha)_programLayers.enable(8);if(parameters.shadowMapEnabled)_programLayers.enable(9);if(parameters.doubleSided)_programLayers.enable(10);if(parameters.flipSided)_programLayers.enable(11);if(parameters.useDepthPacking)_programLayers.enable(12);if(parameters.dithering)_programLayers.enable(13);if(parameters.transmission)_programLayers.enable(14);if(parameters.sheen)_programLayers.enable(15);if(parameters.opaque)_programLayers.enable(16);if(parameters.pointsUvs)_programLayers.enable(17);if(parameters.decodeVideoTexture)_programLayers.enable(18);if(parameters.alphaToCoverage)_programLayers.enable(19);array.push(_programLayers.mask);}function getUniforms(material){const shaderID=shaderIDs[material.type];let uniforms;if(shaderID){const shader=ShaderLib[shaderID];uniforms=UniformsUtils.clone(shader.uniforms);}else {uniforms=material.uniforms;}return uniforms;}function acquireProgram(parameters,cacheKey){let program;// Check if code has been already compiled
for(let p=0,pl=programs.length;p<pl;p++){const preexistingProgram=programs[p];if(preexistingProgram.cacheKey===cacheKey){program=preexistingProgram;++program.usedTimes;break;}}if(program===undefined){program=new WebGLProgram(renderer,cacheKey,parameters,bindingStates);programs.push(program);}return program;}function releaseProgram(program){if(--program.usedTimes===0){// Remove from unordered set
const i=programs.indexOf(program);programs[i]=programs[programs.length-1];programs.pop();// Free WebGL resources
program.destroy();}}function releaseShaderCache(material){_customShaders.remove(material);}function dispose(){_customShaders.dispose();}return {getParameters:getParameters,getProgramCacheKey:getProgramCacheKey,getUniforms:getUniforms,acquireProgram:acquireProgram,releaseProgram:releaseProgram,releaseShaderCache:releaseShaderCache,// Exposed for resource monitoring & error feedback via renderer.info:
programs:programs,dispose:dispose};}function WebGLProperties(){let properties=new WeakMap();function has(object){return properties.has(object);}function get(object){let map=properties.get(object);if(map===undefined){map={};properties.set(object,map);}return map;}function remove(object){properties.delete(object);}function update(object,key,value){properties.get(object)[key]=value;}function dispose(){properties=new WeakMap();}return {has:has,get:get,remove:remove,update:update,dispose:dispose};}function painterSortStable(a,b){if(a.groupOrder!==b.groupOrder){return a.groupOrder-b.groupOrder;}else if(a.renderOrder!==b.renderOrder){return a.renderOrder-b.renderOrder;}else if(a.material.id!==b.material.id){return a.material.id-b.material.id;}else if(a.z!==b.z){return a.z-b.z;}else {return a.id-b.id;}}function reversePainterSortStable(a,b){if(a.groupOrder!==b.groupOrder){return a.groupOrder-b.groupOrder;}else if(a.renderOrder!==b.renderOrder){return a.renderOrder-b.renderOrder;}else if(a.z!==b.z){return b.z-a.z;}else {return a.id-b.id;}}function WebGLRenderList(){const renderItems=[];let renderItemsIndex=0;const opaque=[];const transmissive=[];const transparent=[];function init(){renderItemsIndex=0;opaque.length=0;transmissive.length=0;transparent.length=0;}function getNextRenderItem(object,geometry,material,groupOrder,z,group){let renderItem=renderItems[renderItemsIndex];if(renderItem===undefined){renderItem={id:object.id,object:object,geometry:geometry,material:material,groupOrder:groupOrder,renderOrder:object.renderOrder,z:z,group:group};renderItems[renderItemsIndex]=renderItem;}else {renderItem.id=object.id;renderItem.object=object;renderItem.geometry=geometry;renderItem.material=material;renderItem.groupOrder=groupOrder;renderItem.renderOrder=object.renderOrder;renderItem.z=z;renderItem.group=group;}renderItemsIndex++;return renderItem;}function push(object,geometry,material,groupOrder,z,group){const renderItem=getNextRenderItem(object,geometry,material,groupOrder,z,group);if(material.transmission>0.0){transmissive.push(renderItem);}else if(material.transparent===true){transparent.push(renderItem);}else {opaque.push(renderItem);}}function unshift(object,geometry,material,groupOrder,z,group){const renderItem=getNextRenderItem(object,geometry,material,groupOrder,z,group);if(material.transmission>0.0){transmissive.unshift(renderItem);}else if(material.transparent===true){transparent.unshift(renderItem);}else {opaque.unshift(renderItem);}}function sort(customOpaqueSort,customTransparentSort){if(opaque.length>1)opaque.sort(customOpaqueSort||painterSortStable);if(transmissive.length>1)transmissive.sort(customTransparentSort||reversePainterSortStable);if(transparent.length>1)transparent.sort(customTransparentSort||reversePainterSortStable);}function finish(){// Clear references from inactive renderItems in the list
for(let i=renderItemsIndex,il=renderItems.length;i<il;i++){const renderItem=renderItems[i];if(renderItem.id===null)break;renderItem.id=null;renderItem.object=null;renderItem.geometry=null;renderItem.material=null;renderItem.group=null;}}return {opaque:opaque,transmissive:transmissive,transparent:transparent,init:init,push:push,unshift:unshift,finish:finish,sort:sort};}function WebGLRenderLists(){let lists=new WeakMap();function get(scene,renderCallDepth){const listArray=lists.get(scene);let list;if(listArray===undefined){list=new WebGLRenderList();lists.set(scene,[list]);}else {if(renderCallDepth>=listArray.length){list=new WebGLRenderList();listArray.push(list);}else {list=listArray[renderCallDepth];}}return list;}function dispose(){lists=new WeakMap();}return {get:get,dispose:dispose};}function UniformsCache(){const lights={};return {get:function(light){if(lights[light.id]!==undefined){return lights[light.id];}let uniforms;switch(light.type){case'DirectionalLight':uniforms={direction:new Vector3$1(),color:new Color$1()};break;case'SpotLight':uniforms={position:new Vector3$1(),direction:new Vector3$1(),color:new Color$1(),distance:0,coneCos:0,penumbraCos:0,decay:0};break;case'PointLight':uniforms={position:new Vector3$1(),color:new Color$1(),distance:0,decay:0};break;case'HemisphereLight':uniforms={direction:new Vector3$1(),skyColor:new Color$1(),groundColor:new Color$1()};break;case'RectAreaLight':uniforms={color:new Color$1(),position:new Vector3$1(),halfWidth:new Vector3$1(),halfHeight:new Vector3$1()};break;}lights[light.id]=uniforms;return uniforms;}};}function ShadowUniformsCache(){const lights={};return {get:function(light){if(lights[light.id]!==undefined){return lights[light.id];}let uniforms;switch(light.type){case'DirectionalLight':uniforms={shadowIntensity:1,shadowBias:0,shadowNormalBias:0,shadowRadius:1,shadowMapSize:new Vector2$1()};break;case'SpotLight':uniforms={shadowIntensity:1,shadowBias:0,shadowNormalBias:0,shadowRadius:1,shadowMapSize:new Vector2$1()};break;case'PointLight':uniforms={shadowIntensity:1,shadowBias:0,shadowNormalBias:0,shadowRadius:1,shadowMapSize:new Vector2$1(),shadowCameraNear:1,shadowCameraFar:1000};break;// TODO (abelnation): set RectAreaLight shadow uniforms
}lights[light.id]=uniforms;return uniforms;}};}let nextVersion=0;function shadowCastingAndTexturingLightsFirst(lightA,lightB){return (lightB.castShadow?2:0)-(lightA.castShadow?2:0)+(lightB.map?1:0)-(lightA.map?1:0);}function WebGLLights(extensions){const cache=new UniformsCache();const shadowCache=ShadowUniformsCache();const state={version:0,hash:{directionalLength:-1,pointLength:-1,spotLength:-1,rectAreaLength:-1,hemiLength:-1,numDirectionalShadows:-1,numPointShadows:-1,numSpotShadows:-1,numSpotMaps:-1,numLightProbes:-1},ambient:[0,0,0],probe:[],directional:[],directionalShadow:[],directionalShadowMap:[],directionalShadowMatrix:[],spot:[],spotLightMap:[],spotShadow:[],spotShadowMap:[],spotLightMatrix:[],rectArea:[],rectAreaLTC1:null,rectAreaLTC2:null,point:[],pointShadow:[],pointShadowMap:[],pointShadowMatrix:[],hemi:[],numSpotLightShadowsWithMaps:0,numLightProbes:0};for(let i=0;i<9;i++)state.probe.push(new Vector3$1());const vector3=new Vector3$1();const matrix4=new Matrix4$1();const matrix42=new Matrix4$1();function setup(lights){let r=0,g=0,b=0;for(let i=0;i<9;i++)state.probe[i].set(0,0,0);let directionalLength=0;let pointLength=0;let spotLength=0;let rectAreaLength=0;let hemiLength=0;let numDirectionalShadows=0;let numPointShadows=0;let numSpotShadows=0;let numSpotMaps=0;let numSpotShadowsWithMaps=0;let numLightProbes=0;// ordering : [shadow casting + map texturing, map texturing, shadow casting, none ]
lights.sort(shadowCastingAndTexturingLightsFirst);for(let i=0,l=lights.length;i<l;i++){const light=lights[i];const color=light.color;const intensity=light.intensity;const distance=light.distance;const shadowMap=light.shadow&&light.shadow.map?light.shadow.map.texture:null;if(light.isAmbientLight){r+=color.r*intensity;g+=color.g*intensity;b+=color.b*intensity;}else if(light.isLightProbe){for(let j=0;j<9;j++){state.probe[j].addScaledVector(light.sh.coefficients[j],intensity);}numLightProbes++;}else if(light.isDirectionalLight){const uniforms=cache.get(light);uniforms.color.copy(light.color).multiplyScalar(light.intensity);if(light.castShadow){const shadow=light.shadow;const shadowUniforms=shadowCache.get(light);shadowUniforms.shadowIntensity=shadow.intensity;shadowUniforms.shadowBias=shadow.bias;shadowUniforms.shadowNormalBias=shadow.normalBias;shadowUniforms.shadowRadius=shadow.radius;shadowUniforms.shadowMapSize=shadow.mapSize;state.directionalShadow[directionalLength]=shadowUniforms;state.directionalShadowMap[directionalLength]=shadowMap;state.directionalShadowMatrix[directionalLength]=light.shadow.matrix;numDirectionalShadows++;}state.directional[directionalLength]=uniforms;directionalLength++;}else if(light.isSpotLight){const uniforms=cache.get(light);uniforms.position.setFromMatrixPosition(light.matrixWorld);uniforms.color.copy(color).multiplyScalar(intensity);uniforms.distance=distance;uniforms.coneCos=Math.cos(light.angle);uniforms.penumbraCos=Math.cos(light.angle*(1-light.penumbra));uniforms.decay=light.decay;state.spot[spotLength]=uniforms;const shadow=light.shadow;if(light.map){state.spotLightMap[numSpotMaps]=light.map;numSpotMaps++;// make sure the lightMatrix is up to date
// TODO : do it if required only
shadow.updateMatrices(light);if(light.castShadow)numSpotShadowsWithMaps++;}state.spotLightMatrix[spotLength]=shadow.matrix;if(light.castShadow){const shadowUniforms=shadowCache.get(light);shadowUniforms.shadowIntensity=shadow.intensity;shadowUniforms.shadowBias=shadow.bias;shadowUniforms.shadowNormalBias=shadow.normalBias;shadowUniforms.shadowRadius=shadow.radius;shadowUniforms.shadowMapSize=shadow.mapSize;state.spotShadow[spotLength]=shadowUniforms;state.spotShadowMap[spotLength]=shadowMap;numSpotShadows++;}spotLength++;}else if(light.isRectAreaLight){const uniforms=cache.get(light);uniforms.color.copy(color).multiplyScalar(intensity);uniforms.halfWidth.set(light.width*0.5,0.0,0.0);uniforms.halfHeight.set(0.0,light.height*0.5,0.0);state.rectArea[rectAreaLength]=uniforms;rectAreaLength++;}else if(light.isPointLight){const uniforms=cache.get(light);uniforms.color.copy(light.color).multiplyScalar(light.intensity);uniforms.distance=light.distance;uniforms.decay=light.decay;if(light.castShadow){const shadow=light.shadow;const shadowUniforms=shadowCache.get(light);shadowUniforms.shadowIntensity=shadow.intensity;shadowUniforms.shadowBias=shadow.bias;shadowUniforms.shadowNormalBias=shadow.normalBias;shadowUniforms.shadowRadius=shadow.radius;shadowUniforms.shadowMapSize=shadow.mapSize;shadowUniforms.shadowCameraNear=shadow.camera.near;shadowUniforms.shadowCameraFar=shadow.camera.far;state.pointShadow[pointLength]=shadowUniforms;state.pointShadowMap[pointLength]=shadowMap;state.pointShadowMatrix[pointLength]=light.shadow.matrix;numPointShadows++;}state.point[pointLength]=uniforms;pointLength++;}else if(light.isHemisphereLight){const uniforms=cache.get(light);uniforms.skyColor.copy(light.color).multiplyScalar(intensity);uniforms.groundColor.copy(light.groundColor).multiplyScalar(intensity);state.hemi[hemiLength]=uniforms;hemiLength++;}}if(rectAreaLength>0){if(extensions.has('OES_texture_float_linear')===true){state.rectAreaLTC1=UniformsLib.LTC_FLOAT_1;state.rectAreaLTC2=UniformsLib.LTC_FLOAT_2;}else {state.rectAreaLTC1=UniformsLib.LTC_HALF_1;state.rectAreaLTC2=UniformsLib.LTC_HALF_2;}}state.ambient[0]=r;state.ambient[1]=g;state.ambient[2]=b;const hash=state.hash;if(hash.directionalLength!==directionalLength||hash.pointLength!==pointLength||hash.spotLength!==spotLength||hash.rectAreaLength!==rectAreaLength||hash.hemiLength!==hemiLength||hash.numDirectionalShadows!==numDirectionalShadows||hash.numPointShadows!==numPointShadows||hash.numSpotShadows!==numSpotShadows||hash.numSpotMaps!==numSpotMaps||hash.numLightProbes!==numLightProbes){state.directional.length=directionalLength;state.spot.length=spotLength;state.rectArea.length=rectAreaLength;state.point.length=pointLength;state.hemi.length=hemiLength;state.directionalShadow.length=numDirectionalShadows;state.directionalShadowMap.length=numDirectionalShadows;state.pointShadow.length=numPointShadows;state.pointShadowMap.length=numPointShadows;state.spotShadow.length=numSpotShadows;state.spotShadowMap.length=numSpotShadows;state.directionalShadowMatrix.length=numDirectionalShadows;state.pointShadowMatrix.length=numPointShadows;state.spotLightMatrix.length=numSpotShadows+numSpotMaps-numSpotShadowsWithMaps;state.spotLightMap.length=numSpotMaps;state.numSpotLightShadowsWithMaps=numSpotShadowsWithMaps;state.numLightProbes=numLightProbes;hash.directionalLength=directionalLength;hash.pointLength=pointLength;hash.spotLength=spotLength;hash.rectAreaLength=rectAreaLength;hash.hemiLength=hemiLength;hash.numDirectionalShadows=numDirectionalShadows;hash.numPointShadows=numPointShadows;hash.numSpotShadows=numSpotShadows;hash.numSpotMaps=numSpotMaps;hash.numLightProbes=numLightProbes;state.version=nextVersion++;}}function setupView(lights,camera){let directionalLength=0;let pointLength=0;let spotLength=0;let rectAreaLength=0;let hemiLength=0;const viewMatrix=camera.matrixWorldInverse;for(let i=0,l=lights.length;i<l;i++){const light=lights[i];if(light.isDirectionalLight){const uniforms=state.directional[directionalLength];uniforms.direction.setFromMatrixPosition(light.matrixWorld);vector3.setFromMatrixPosition(light.target.matrixWorld);uniforms.direction.sub(vector3);uniforms.direction.transformDirection(viewMatrix);directionalLength++;}else if(light.isSpotLight){const uniforms=state.spot[spotLength];uniforms.position.setFromMatrixPosition(light.matrixWorld);uniforms.position.applyMatrix4(viewMatrix);uniforms.direction.setFromMatrixPosition(light.matrixWorld);vector3.setFromMatrixPosition(light.target.matrixWorld);uniforms.direction.sub(vector3);uniforms.direction.transformDirection(viewMatrix);spotLength++;}else if(light.isRectAreaLight){const uniforms=state.rectArea[rectAreaLength];uniforms.position.setFromMatrixPosition(light.matrixWorld);uniforms.position.applyMatrix4(viewMatrix);// extract local rotation of light to derive width/height half vectors
matrix42.identity();matrix4.copy(light.matrixWorld);matrix4.premultiply(viewMatrix);matrix42.extractRotation(matrix4);uniforms.halfWidth.set(light.width*0.5,0.0,0.0);uniforms.halfHeight.set(0.0,light.height*0.5,0.0);uniforms.halfWidth.applyMatrix4(matrix42);uniforms.halfHeight.applyMatrix4(matrix42);rectAreaLength++;}else if(light.isPointLight){const uniforms=state.point[pointLength];uniforms.position.setFromMatrixPosition(light.matrixWorld);uniforms.position.applyMatrix4(viewMatrix);pointLength++;}else if(light.isHemisphereLight){const uniforms=state.hemi[hemiLength];uniforms.direction.setFromMatrixPosition(light.matrixWorld);uniforms.direction.transformDirection(viewMatrix);hemiLength++;}}}return {setup:setup,setupView:setupView,state:state};}function WebGLRenderState(extensions){const lights=new WebGLLights(extensions);const lightsArray=[];const shadowsArray=[];function init(camera){state.camera=camera;lightsArray.length=0;shadowsArray.length=0;}function pushLight(light){lightsArray.push(light);}function pushShadow(shadowLight){shadowsArray.push(shadowLight);}function setupLights(){lights.setup(lightsArray);}function setupLightsView(camera){lights.setupView(lightsArray,camera);}const state={lightsArray:lightsArray,shadowsArray:shadowsArray,camera:null,lights:lights,transmissionRenderTarget:{}};return {init:init,state:state,setupLights:setupLights,setupLightsView:setupLightsView,pushLight:pushLight,pushShadow:pushShadow};}function WebGLRenderStates(extensions){let renderStates=new WeakMap();function get(scene,renderCallDepth=0){const renderStateArray=renderStates.get(scene);let renderState;if(renderStateArray===undefined){renderState=new WebGLRenderState(extensions);renderStates.set(scene,[renderState]);}else {if(renderCallDepth>=renderStateArray.length){renderState=new WebGLRenderState(extensions);renderStateArray.push(renderState);}else {renderState=renderStateArray[renderCallDepth];}}return renderState;}function dispose(){renderStates=new WeakMap();}return {get:get,dispose:dispose};}class MeshDepthMaterial extends Material$1{constructor(parameters){super();this.isMeshDepthMaterial=true;this.type='MeshDepthMaterial';this.depthPacking=BasicDepthPacking;this.map=null;this.alphaMap=null;this.displacementMap=null;this.displacementScale=1;this.displacementBias=0;this.wireframe=false;this.wireframeLinewidth=1;this.setValues(parameters);}copy(source){super.copy(source);this.depthPacking=source.depthPacking;this.map=source.map;this.alphaMap=source.alphaMap;this.displacementMap=source.displacementMap;this.displacementScale=source.displacementScale;this.displacementBias=source.displacementBias;this.wireframe=source.wireframe;this.wireframeLinewidth=source.wireframeLinewidth;return this;}}class MeshDistanceMaterial extends Material$1{constructor(parameters){super();this.isMeshDistanceMaterial=true;this.type='MeshDistanceMaterial';this.map=null;this.alphaMap=null;this.displacementMap=null;this.displacementScale=1;this.displacementBias=0;this.setValues(parameters);}copy(source){super.copy(source);this.map=source.map;this.alphaMap=source.alphaMap;this.displacementMap=source.displacementMap;this.displacementScale=source.displacementScale;this.displacementBias=source.displacementBias;return this;}}const vertex="void main() {\n\tgl_Position = vec4( position, 1.0 );\n}";const fragment="uniform sampler2D shadow_pass;\nuniform vec2 resolution;\nuniform float radius;\n#include <packing>\nvoid main() {\n\tconst float samples = float( VSM_SAMPLES );\n\tfloat mean = 0.0;\n\tfloat squared_mean = 0.0;\n\tfloat uvStride = samples <= 1.0 ? 0.0 : 2.0 / ( samples - 1.0 );\n\tfloat uvStart = samples <= 1.0 ? 0.0 : - 1.0;\n\tfor ( float i = 0.0; i < samples; i ++ ) {\n\t\tfloat uvOffset = uvStart + i * uvStride;\n\t\t#ifdef HORIZONTAL_PASS\n\t\t\tvec2 distribution = unpackRGBATo2Half( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( uvOffset, 0.0 ) * radius ) / resolution ) );\n\t\t\tmean += distribution.x;\n\t\t\tsquared_mean += distribution.y * distribution.y + distribution.x * distribution.x;\n\t\t#else\n\t\t\tfloat depth = unpackRGBAToDepth( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( 0.0, uvOffset ) * radius ) / resolution ) );\n\t\t\tmean += depth;\n\t\t\tsquared_mean += depth * depth;\n\t\t#endif\n\t}\n\tmean = mean / samples;\n\tsquared_mean = squared_mean / samples;\n\tfloat std_dev = sqrt( squared_mean - mean * mean );\n\tgl_FragColor = pack2HalfToRGBA( vec2( mean, std_dev ) );\n}";function WebGLShadowMap(renderer,objects,capabilities){let _frustum=new Frustum();const _shadowMapSize=new Vector2$1(),_viewportSize=new Vector2$1(),_viewport=new Vector4$1(),_depthMaterial=new MeshDepthMaterial({depthPacking:RGBADepthPacking}),_distanceMaterial=new MeshDistanceMaterial(),_materialCache={},_maxTextureSize=capabilities.maxTextureSize;const shadowSide={[FrontSide$1]:BackSide$1,[BackSide$1]:FrontSide$1,[DoubleSide]:DoubleSide};const shadowMaterialVertical=new ShaderMaterial({defines:{VSM_SAMPLES:8},uniforms:{shadow_pass:{value:null},resolution:{value:new Vector2$1()},radius:{value:4.0}},vertexShader:vertex,fragmentShader:fragment});const shadowMaterialHorizontal=shadowMaterialVertical.clone();shadowMaterialHorizontal.defines.HORIZONTAL_PASS=1;const fullScreenTri=new BufferGeometry$1();fullScreenTri.setAttribute('position',new BufferAttribute$1(new Float32Array([-1,-1,0.5,3,-1,0.5,-1,3,0.5]),3));const fullScreenMesh=new Mesh$1(fullScreenTri,shadowMaterialVertical);const scope=this;this.enabled=false;this.autoUpdate=true;this.needsUpdate=false;this.type=PCFShadowMap;let _previousType=this.type;this.render=function(lights,scene,camera){if(scope.enabled===false)return;if(scope.autoUpdate===false&&scope.needsUpdate===false)return;if(lights.length===0)return;const currentRenderTarget=renderer.getRenderTarget();const activeCubeFace=renderer.getActiveCubeFace();const activeMipmapLevel=renderer.getActiveMipmapLevel();const _state=renderer.state;// Set GL state for depth map.
_state.setBlending(NoBlending);_state.buffers.color.setClear(1,1,1,1);_state.buffers.depth.setTest(true);_state.setScissorTest(false);// check for shadow map type changes
const toVSM=_previousType!==VSMShadowMap&&this.type===VSMShadowMap;const fromVSM=_previousType===VSMShadowMap&&this.type!==VSMShadowMap;// render depth map
for(let i=0,il=lights.length;i<il;i++){const light=lights[i];const shadow=light.shadow;if(shadow===undefined){console.warn('THREE.WebGLShadowMap:',light,'has no shadow.');continue;}if(shadow.autoUpdate===false&&shadow.needsUpdate===false)continue;_shadowMapSize.copy(shadow.mapSize);const shadowFrameExtents=shadow.getFrameExtents();_shadowMapSize.multiply(shadowFrameExtents);_viewportSize.copy(shadow.mapSize);if(_shadowMapSize.x>_maxTextureSize||_shadowMapSize.y>_maxTextureSize){if(_shadowMapSize.x>_maxTextureSize){_viewportSize.x=Math.floor(_maxTextureSize/shadowFrameExtents.x);_shadowMapSize.x=_viewportSize.x*shadowFrameExtents.x;shadow.mapSize.x=_viewportSize.x;}if(_shadowMapSize.y>_maxTextureSize){_viewportSize.y=Math.floor(_maxTextureSize/shadowFrameExtents.y);_shadowMapSize.y=_viewportSize.y*shadowFrameExtents.y;shadow.mapSize.y=_viewportSize.y;}}if(shadow.map===null||toVSM===true||fromVSM===true){const pars=this.type!==VSMShadowMap?{minFilter:NearestFilter$1,magFilter:NearestFilter$1}:{};if(shadow.map!==null){shadow.map.dispose();}shadow.map=new WebGLRenderTarget(_shadowMapSize.x,_shadowMapSize.y,pars);shadow.map.texture.name=light.name+'.shadowMap';shadow.camera.updateProjectionMatrix();}renderer.setRenderTarget(shadow.map);renderer.clear();const viewportCount=shadow.getViewportCount();for(let vp=0;vp<viewportCount;vp++){const viewport=shadow.getViewport(vp);_viewport.set(_viewportSize.x*viewport.x,_viewportSize.y*viewport.y,_viewportSize.x*viewport.z,_viewportSize.y*viewport.w);_state.viewport(_viewport);shadow.updateMatrices(light,vp);_frustum=shadow.getFrustum();renderObject(scene,camera,shadow.camera,light,this.type);}// do blur pass for VSM
if(shadow.isPointLightShadow!==true&&this.type===VSMShadowMap){VSMPass(shadow,camera);}shadow.needsUpdate=false;}_previousType=this.type;scope.needsUpdate=false;renderer.setRenderTarget(currentRenderTarget,activeCubeFace,activeMipmapLevel);};function VSMPass(shadow,camera){const geometry=objects.update(fullScreenMesh);if(shadowMaterialVertical.defines.VSM_SAMPLES!==shadow.blurSamples){shadowMaterialVertical.defines.VSM_SAMPLES=shadow.blurSamples;shadowMaterialHorizontal.defines.VSM_SAMPLES=shadow.blurSamples;shadowMaterialVertical.needsUpdate=true;shadowMaterialHorizontal.needsUpdate=true;}if(shadow.mapPass===null){shadow.mapPass=new WebGLRenderTarget(_shadowMapSize.x,_shadowMapSize.y);}// vertical pass
shadowMaterialVertical.uniforms.shadow_pass.value=shadow.map.texture;shadowMaterialVertical.uniforms.resolution.value=shadow.mapSize;shadowMaterialVertical.uniforms.radius.value=shadow.radius;renderer.setRenderTarget(shadow.mapPass);renderer.clear();renderer.renderBufferDirect(camera,null,geometry,shadowMaterialVertical,fullScreenMesh,null);// horizontal pass
shadowMaterialHorizontal.uniforms.shadow_pass.value=shadow.mapPass.texture;shadowMaterialHorizontal.uniforms.resolution.value=shadow.mapSize;shadowMaterialHorizontal.uniforms.radius.value=shadow.radius;renderer.setRenderTarget(shadow.map);renderer.clear();renderer.renderBufferDirect(camera,null,geometry,shadowMaterialHorizontal,fullScreenMesh,null);}function getDepthMaterial(object,material,light,type){let result=null;const customMaterial=light.isPointLight===true?object.customDistanceMaterial:object.customDepthMaterial;if(customMaterial!==undefined){result=customMaterial;}else {result=light.isPointLight===true?_distanceMaterial:_depthMaterial;if(renderer.localClippingEnabled&&material.clipShadows===true&&Array.isArray(material.clippingPlanes)&&material.clippingPlanes.length!==0||material.displacementMap&&material.displacementScale!==0||material.alphaMap&&material.alphaTest>0||material.map&&material.alphaTest>0){// in this case we need a unique material instance reflecting the
// appropriate state
const keyA=result.uuid,keyB=material.uuid;let materialsForVariant=_materialCache[keyA];if(materialsForVariant===undefined){materialsForVariant={};_materialCache[keyA]=materialsForVariant;}let cachedMaterial=materialsForVariant[keyB];if(cachedMaterial===undefined){cachedMaterial=result.clone();materialsForVariant[keyB]=cachedMaterial;material.addEventListener('dispose',onMaterialDispose);}result=cachedMaterial;}}result.visible=material.visible;result.wireframe=material.wireframe;if(type===VSMShadowMap){result.side=material.shadowSide!==null?material.shadowSide:material.side;}else {result.side=material.shadowSide!==null?material.shadowSide:shadowSide[material.side];}result.alphaMap=material.alphaMap;result.alphaTest=material.alphaTest;result.map=material.map;result.clipShadows=material.clipShadows;result.clippingPlanes=material.clippingPlanes;result.clipIntersection=material.clipIntersection;result.displacementMap=material.displacementMap;result.displacementScale=material.displacementScale;result.displacementBias=material.displacementBias;result.wireframeLinewidth=material.wireframeLinewidth;result.linewidth=material.linewidth;if(light.isPointLight===true&&result.isMeshDistanceMaterial===true){const materialProperties=renderer.properties.get(result);materialProperties.light=light;}return result;}function renderObject(object,camera,shadowCamera,light,type){if(object.visible===false)return;const visible=object.layers.test(camera.layers);if(visible&&(object.isMesh||object.isLine||object.isPoints)){if((object.castShadow||object.receiveShadow&&type===VSMShadowMap)&&(!object.frustumCulled||_frustum.intersectsObject(object))){object.modelViewMatrix.multiplyMatrices(shadowCamera.matrixWorldInverse,object.matrixWorld);const geometry=objects.update(object);const material=object.material;if(Array.isArray(material)){const groups=geometry.groups;for(let k=0,kl=groups.length;k<kl;k++){const group=groups[k];const groupMaterial=material[group.materialIndex];if(groupMaterial&&groupMaterial.visible){const depthMaterial=getDepthMaterial(object,groupMaterial,light,type);object.onBeforeShadow(renderer,object,camera,shadowCamera,geometry,depthMaterial,group);renderer.renderBufferDirect(shadowCamera,null,geometry,depthMaterial,object,group);object.onAfterShadow(renderer,object,camera,shadowCamera,geometry,depthMaterial,group);}}}else if(material.visible){const depthMaterial=getDepthMaterial(object,material,light,type);object.onBeforeShadow(renderer,object,camera,shadowCamera,geometry,depthMaterial,null);renderer.renderBufferDirect(shadowCamera,null,geometry,depthMaterial,object,null);object.onAfterShadow(renderer,object,camera,shadowCamera,geometry,depthMaterial,null);}}}const children=object.children;for(let i=0,l=children.length;i<l;i++){renderObject(children[i],camera,shadowCamera,light,type);}}function onMaterialDispose(event){const material=event.target;material.removeEventListener('dispose',onMaterialDispose);// make sure to remove the unique distance/depth materials used for shadow map rendering
for(const id in _materialCache){const cache=_materialCache[id];const uuid=event.target.uuid;if(uuid in cache){const shadowMaterial=cache[uuid];shadowMaterial.dispose();delete cache[uuid];}}}}function WebGLState(gl){function ColorBuffer(){let locked=false;const color=new Vector4$1();let currentColorMask=null;const currentColorClear=new Vector4$1(0,0,0,0);return {setMask:function(colorMask){if(currentColorMask!==colorMask&&!locked){gl.colorMask(colorMask,colorMask,colorMask,colorMask);currentColorMask=colorMask;}},setLocked:function(lock){locked=lock;},setClear:function(r,g,b,a,premultipliedAlpha){if(premultipliedAlpha===true){r*=a;g*=a;b*=a;}color.set(r,g,b,a);if(currentColorClear.equals(color)===false){gl.clearColor(r,g,b,a);currentColorClear.copy(color);}},reset:function(){locked=false;currentColorMask=null;currentColorClear.set(-1,0,0,0);// set to invalid state
}};}function DepthBuffer(){let locked=false;let currentDepthMask=null;let currentDepthFunc=null;let currentDepthClear=null;return {setTest:function(depthTest){if(depthTest){enable(gl.DEPTH_TEST);}else {disable(gl.DEPTH_TEST);}},setMask:function(depthMask){if(currentDepthMask!==depthMask&&!locked){gl.depthMask(depthMask);currentDepthMask=depthMask;}},setFunc:function(depthFunc){if(currentDepthFunc!==depthFunc){switch(depthFunc){case NeverDepth:gl.depthFunc(gl.NEVER);break;case AlwaysDepth:gl.depthFunc(gl.ALWAYS);break;case LessDepth:gl.depthFunc(gl.LESS);break;case LessEqualDepth$1:gl.depthFunc(gl.LEQUAL);break;case EqualDepth:gl.depthFunc(gl.EQUAL);break;case GreaterEqualDepth:gl.depthFunc(gl.GEQUAL);break;case GreaterDepth:gl.depthFunc(gl.GREATER);break;case NotEqualDepth:gl.depthFunc(gl.NOTEQUAL);break;default:gl.depthFunc(gl.LEQUAL);}currentDepthFunc=depthFunc;}},setLocked:function(lock){locked=lock;},setClear:function(depth){if(currentDepthClear!==depth){gl.clearDepth(depth);currentDepthClear=depth;}},reset:function(){locked=false;currentDepthMask=null;currentDepthFunc=null;currentDepthClear=null;}};}function StencilBuffer(){let locked=false;let currentStencilMask=null;let currentStencilFunc=null;let currentStencilRef=null;let currentStencilFuncMask=null;let currentStencilFail=null;let currentStencilZFail=null;let currentStencilZPass=null;let currentStencilClear=null;return {setTest:function(stencilTest){if(!locked){if(stencilTest){enable(gl.STENCIL_TEST);}else {disable(gl.STENCIL_TEST);}}},setMask:function(stencilMask){if(currentStencilMask!==stencilMask&&!locked){gl.stencilMask(stencilMask);currentStencilMask=stencilMask;}},setFunc:function(stencilFunc,stencilRef,stencilMask){if(currentStencilFunc!==stencilFunc||currentStencilRef!==stencilRef||currentStencilFuncMask!==stencilMask){gl.stencilFunc(stencilFunc,stencilRef,stencilMask);currentStencilFunc=stencilFunc;currentStencilRef=stencilRef;currentStencilFuncMask=stencilMask;}},setOp:function(stencilFail,stencilZFail,stencilZPass){if(currentStencilFail!==stencilFail||currentStencilZFail!==stencilZFail||currentStencilZPass!==stencilZPass){gl.stencilOp(stencilFail,stencilZFail,stencilZPass);currentStencilFail=stencilFail;currentStencilZFail=stencilZFail;currentStencilZPass=stencilZPass;}},setLocked:function(lock){locked=lock;},setClear:function(stencil){if(currentStencilClear!==stencil){gl.clearStencil(stencil);currentStencilClear=stencil;}},reset:function(){locked=false;currentStencilMask=null;currentStencilFunc=null;currentStencilRef=null;currentStencilFuncMask=null;currentStencilFail=null;currentStencilZFail=null;currentStencilZPass=null;currentStencilClear=null;}};}//
const colorBuffer=new ColorBuffer();const depthBuffer=new DepthBuffer();const stencilBuffer=new StencilBuffer();const uboBindings=new WeakMap();const uboProgramMap=new WeakMap();let enabledCapabilities={};let currentBoundFramebuffers={};let currentDrawbuffers=new WeakMap();let defaultDrawbuffers=[];let currentProgram=null;let currentBlendingEnabled=false;let currentBlending=null;let currentBlendEquation=null;let currentBlendSrc=null;let currentBlendDst=null;let currentBlendEquationAlpha=null;let currentBlendSrcAlpha=null;let currentBlendDstAlpha=null;let currentBlendColor=new Color$1(0,0,0);let currentBlendAlpha=0;let currentPremultipledAlpha=false;let currentFlipSided=null;let currentCullFace=null;let currentLineWidth=null;let currentPolygonOffsetFactor=null;let currentPolygonOffsetUnits=null;const maxTextures=gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS);let lineWidthAvailable=false;let version=0;const glVersion=gl.getParameter(gl.VERSION);if(glVersion.indexOf('WebGL')!==-1){version=parseFloat(/^WebGL (\d)/.exec(glVersion)[1]);lineWidthAvailable=version>=1.0;}else if(glVersion.indexOf('OpenGL ES')!==-1){version=parseFloat(/^OpenGL ES (\d)/.exec(glVersion)[1]);lineWidthAvailable=version>=2.0;}let currentTextureSlot=null;let currentBoundTextures={};const scissorParam=gl.getParameter(gl.SCISSOR_BOX);const viewportParam=gl.getParameter(gl.VIEWPORT);const currentScissor=new Vector4$1().fromArray(scissorParam);const currentViewport=new Vector4$1().fromArray(viewportParam);function createTexture(type,target,count,dimensions){const data=new Uint8Array(4);// 4 is required to match default unpack alignment of 4.
const texture=gl.createTexture();gl.bindTexture(type,texture);gl.texParameteri(type,gl.TEXTURE_MIN_FILTER,gl.NEAREST);gl.texParameteri(type,gl.TEXTURE_MAG_FILTER,gl.NEAREST);for(let i=0;i<count;i++){if(type===gl.TEXTURE_3D||type===gl.TEXTURE_2D_ARRAY){gl.texImage3D(target,0,gl.RGBA,1,1,dimensions,0,gl.RGBA,gl.UNSIGNED_BYTE,data);}else {gl.texImage2D(target+i,0,gl.RGBA,1,1,0,gl.RGBA,gl.UNSIGNED_BYTE,data);}}return texture;}const emptyTextures={};emptyTextures[gl.TEXTURE_2D]=createTexture(gl.TEXTURE_2D,gl.TEXTURE_2D,1);emptyTextures[gl.TEXTURE_CUBE_MAP]=createTexture(gl.TEXTURE_CUBE_MAP,gl.TEXTURE_CUBE_MAP_POSITIVE_X,6);emptyTextures[gl.TEXTURE_2D_ARRAY]=createTexture(gl.TEXTURE_2D_ARRAY,gl.TEXTURE_2D_ARRAY,1,1);emptyTextures[gl.TEXTURE_3D]=createTexture(gl.TEXTURE_3D,gl.TEXTURE_3D,1,1);// init
colorBuffer.setClear(0,0,0,1);depthBuffer.setClear(1);stencilBuffer.setClear(0);enable(gl.DEPTH_TEST);depthBuffer.setFunc(LessEqualDepth$1);setFlipSided(false);setCullFace(CullFaceBack);enable(gl.CULL_FACE);setBlending(NoBlending);//
function enable(id){if(enabledCapabilities[id]!==true){gl.enable(id);enabledCapabilities[id]=true;}}function disable(id){if(enabledCapabilities[id]!==false){gl.disable(id);enabledCapabilities[id]=false;}}function bindFramebuffer(target,framebuffer){if(currentBoundFramebuffers[target]!==framebuffer){gl.bindFramebuffer(target,framebuffer);currentBoundFramebuffers[target]=framebuffer;// gl.DRAW_FRAMEBUFFER is equivalent to gl.FRAMEBUFFER
if(target===gl.DRAW_FRAMEBUFFER){currentBoundFramebuffers[gl.FRAMEBUFFER]=framebuffer;}if(target===gl.FRAMEBUFFER){currentBoundFramebuffers[gl.DRAW_FRAMEBUFFER]=framebuffer;}return true;}return false;}function drawBuffers(renderTarget,framebuffer){let drawBuffers=defaultDrawbuffers;let needsUpdate=false;if(renderTarget){drawBuffers=currentDrawbuffers.get(framebuffer);if(drawBuffers===undefined){drawBuffers=[];currentDrawbuffers.set(framebuffer,drawBuffers);}const textures=renderTarget.textures;if(drawBuffers.length!==textures.length||drawBuffers[0]!==gl.COLOR_ATTACHMENT0){for(let i=0,il=textures.length;i<il;i++){drawBuffers[i]=gl.COLOR_ATTACHMENT0+i;}drawBuffers.length=textures.length;needsUpdate=true;}}else {if(drawBuffers[0]!==gl.BACK){drawBuffers[0]=gl.BACK;needsUpdate=true;}}if(needsUpdate){gl.drawBuffers(drawBuffers);}}function useProgram(program){if(currentProgram!==program){gl.useProgram(program);currentProgram=program;return true;}return false;}const equationToGL={[AddEquation$1]:gl.FUNC_ADD,[SubtractEquation]:gl.FUNC_SUBTRACT,[ReverseSubtractEquation]:gl.FUNC_REVERSE_SUBTRACT};equationToGL[MinEquation]=gl.MIN;equationToGL[MaxEquation]=gl.MAX;const factorToGL={[ZeroFactor]:gl.ZERO,[OneFactor]:gl.ONE,[SrcColorFactor]:gl.SRC_COLOR,[SrcAlphaFactor$1]:gl.SRC_ALPHA,[SrcAlphaSaturateFactor]:gl.SRC_ALPHA_SATURATE,[DstColorFactor]:gl.DST_COLOR,[DstAlphaFactor]:gl.DST_ALPHA,[OneMinusSrcColorFactor]:gl.ONE_MINUS_SRC_COLOR,[OneMinusSrcAlphaFactor$1]:gl.ONE_MINUS_SRC_ALPHA,[OneMinusDstColorFactor]:gl.ONE_MINUS_DST_COLOR,[OneMinusDstAlphaFactor]:gl.ONE_MINUS_DST_ALPHA,[ConstantColorFactor]:gl.CONSTANT_COLOR,[OneMinusConstantColorFactor]:gl.ONE_MINUS_CONSTANT_COLOR,[ConstantAlphaFactor]:gl.CONSTANT_ALPHA,[OneMinusConstantAlphaFactor]:gl.ONE_MINUS_CONSTANT_ALPHA};function setBlending(blending,blendEquation,blendSrc,blendDst,blendEquationAlpha,blendSrcAlpha,blendDstAlpha,blendColor,blendAlpha,premultipliedAlpha){if(blending===NoBlending){if(currentBlendingEnabled===true){disable(gl.BLEND);currentBlendingEnabled=false;}return;}if(currentBlendingEnabled===false){enable(gl.BLEND);currentBlendingEnabled=true;}if(blending!==CustomBlending){if(blending!==currentBlending||premultipliedAlpha!==currentPremultipledAlpha){if(currentBlendEquation!==AddEquation$1||currentBlendEquationAlpha!==AddEquation$1){gl.blendEquation(gl.FUNC_ADD);currentBlendEquation=AddEquation$1;currentBlendEquationAlpha=AddEquation$1;}if(premultipliedAlpha){switch(blending){case NormalBlending$1:gl.blendFuncSeparate(gl.ONE,gl.ONE_MINUS_SRC_ALPHA,gl.ONE,gl.ONE_MINUS_SRC_ALPHA);break;case AdditiveBlending$1:gl.blendFunc(gl.ONE,gl.ONE);break;case SubtractiveBlending:gl.blendFuncSeparate(gl.ZERO,gl.ONE_MINUS_SRC_COLOR,gl.ZERO,gl.ONE);break;case MultiplyBlending:gl.blendFuncSeparate(gl.ZERO,gl.SRC_COLOR,gl.ZERO,gl.SRC_ALPHA);break;default:console.error('THREE.WebGLState: Invalid blending: ',blending);break;}}else {switch(blending){case NormalBlending$1:gl.blendFuncSeparate(gl.SRC_ALPHA,gl.ONE_MINUS_SRC_ALPHA,gl.ONE,gl.ONE_MINUS_SRC_ALPHA);break;case AdditiveBlending$1:gl.blendFunc(gl.SRC_ALPHA,gl.ONE);break;case SubtractiveBlending:gl.blendFuncSeparate(gl.ZERO,gl.ONE_MINUS_SRC_COLOR,gl.ZERO,gl.ONE);break;case MultiplyBlending:gl.blendFunc(gl.ZERO,gl.SRC_COLOR);break;default:console.error('THREE.WebGLState: Invalid blending: ',blending);break;}}currentBlendSrc=null;currentBlendDst=null;currentBlendSrcAlpha=null;currentBlendDstAlpha=null;currentBlendColor.set(0,0,0);currentBlendAlpha=0;currentBlending=blending;currentPremultipledAlpha=premultipliedAlpha;}return;}// custom blending
blendEquationAlpha=blendEquationAlpha||blendEquation;blendSrcAlpha=blendSrcAlpha||blendSrc;blendDstAlpha=blendDstAlpha||blendDst;if(blendEquation!==currentBlendEquation||blendEquationAlpha!==currentBlendEquationAlpha){gl.blendEquationSeparate(equationToGL[blendEquation],equationToGL[blendEquationAlpha]);currentBlendEquation=blendEquation;currentBlendEquationAlpha=blendEquationAlpha;}if(blendSrc!==currentBlendSrc||blendDst!==currentBlendDst||blendSrcAlpha!==currentBlendSrcAlpha||blendDstAlpha!==currentBlendDstAlpha){gl.blendFuncSeparate(factorToGL[blendSrc],factorToGL[blendDst],factorToGL[blendSrcAlpha],factorToGL[blendDstAlpha]);currentBlendSrc=blendSrc;currentBlendDst=blendDst;currentBlendSrcAlpha=blendSrcAlpha;currentBlendDstAlpha=blendDstAlpha;}if(blendColor.equals(currentBlendColor)===false||blendAlpha!==currentBlendAlpha){gl.blendColor(blendColor.r,blendColor.g,blendColor.b,blendAlpha);currentBlendColor.copy(blendColor);currentBlendAlpha=blendAlpha;}currentBlending=blending;currentPremultipledAlpha=false;}function setMaterial(material,frontFaceCW){material.side===DoubleSide?disable(gl.CULL_FACE):enable(gl.CULL_FACE);let flipSided=material.side===BackSide$1;if(frontFaceCW)flipSided=!flipSided;setFlipSided(flipSided);material.blending===NormalBlending$1&&material.transparent===false?setBlending(NoBlending):setBlending(material.blending,material.blendEquation,material.blendSrc,material.blendDst,material.blendEquationAlpha,material.blendSrcAlpha,material.blendDstAlpha,material.blendColor,material.blendAlpha,material.premultipliedAlpha);depthBuffer.setFunc(material.depthFunc);depthBuffer.setTest(material.depthTest);depthBuffer.setMask(material.depthWrite);colorBuffer.setMask(material.colorWrite);const stencilWrite=material.stencilWrite;stencilBuffer.setTest(stencilWrite);if(stencilWrite){stencilBuffer.setMask(material.stencilWriteMask);stencilBuffer.setFunc(material.stencilFunc,material.stencilRef,material.stencilFuncMask);stencilBuffer.setOp(material.stencilFail,material.stencilZFail,material.stencilZPass);}setPolygonOffset(material.polygonOffset,material.polygonOffsetFactor,material.polygonOffsetUnits);material.alphaToCoverage===true?enable(gl.SAMPLE_ALPHA_TO_COVERAGE):disable(gl.SAMPLE_ALPHA_TO_COVERAGE);}//
function setFlipSided(flipSided){if(currentFlipSided!==flipSided){if(flipSided){gl.frontFace(gl.CW);}else {gl.frontFace(gl.CCW);}currentFlipSided=flipSided;}}function setCullFace(cullFace){if(cullFace!==CullFaceNone){enable(gl.CULL_FACE);if(cullFace!==currentCullFace){if(cullFace===CullFaceBack){gl.cullFace(gl.BACK);}else if(cullFace===CullFaceFront){gl.cullFace(gl.FRONT);}else {gl.cullFace(gl.FRONT_AND_BACK);}}}else {disable(gl.CULL_FACE);}currentCullFace=cullFace;}function setLineWidth(width){if(width!==currentLineWidth){if(lineWidthAvailable)gl.lineWidth(width);currentLineWidth=width;}}function setPolygonOffset(polygonOffset,factor,units){if(polygonOffset){enable(gl.POLYGON_OFFSET_FILL);if(currentPolygonOffsetFactor!==factor||currentPolygonOffsetUnits!==units){gl.polygonOffset(factor,units);currentPolygonOffsetFactor=factor;currentPolygonOffsetUnits=units;}}else {disable(gl.POLYGON_OFFSET_FILL);}}function setScissorTest(scissorTest){if(scissorTest){enable(gl.SCISSOR_TEST);}else {disable(gl.SCISSOR_TEST);}}// texture
function activeTexture(webglSlot){if(webglSlot===undefined)webglSlot=gl.TEXTURE0+maxTextures-1;if(currentTextureSlot!==webglSlot){gl.activeTexture(webglSlot);currentTextureSlot=webglSlot;}}function bindTexture(webglType,webglTexture,webglSlot){if(webglSlot===undefined){if(currentTextureSlot===null){webglSlot=gl.TEXTURE0+maxTextures-1;}else {webglSlot=currentTextureSlot;}}let boundTexture=currentBoundTextures[webglSlot];if(boundTexture===undefined){boundTexture={type:undefined,texture:undefined};currentBoundTextures[webglSlot]=boundTexture;}if(boundTexture.type!==webglType||boundTexture.texture!==webglTexture){if(currentTextureSlot!==webglSlot){gl.activeTexture(webglSlot);currentTextureSlot=webglSlot;}gl.bindTexture(webglType,webglTexture||emptyTextures[webglType]);boundTexture.type=webglType;boundTexture.texture=webglTexture;}}function unbindTexture(){const boundTexture=currentBoundTextures[currentTextureSlot];if(boundTexture!==undefined&&boundTexture.type!==undefined){gl.bindTexture(boundTexture.type,null);boundTexture.type=undefined;boundTexture.texture=undefined;}}function compressedTexImage2D(){try{gl.compressedTexImage2D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function compressedTexImage3D(){try{gl.compressedTexImage3D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texSubImage2D(){try{gl.texSubImage2D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texSubImage3D(){try{gl.texSubImage3D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function compressedTexSubImage2D(){try{gl.compressedTexSubImage2D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function compressedTexSubImage3D(){try{gl.compressedTexSubImage3D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texStorage2D(){try{gl.texStorage2D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texStorage3D(){try{gl.texStorage3D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texImage2D(){try{gl.texImage2D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}function texImage3D(){try{gl.texImage3D.apply(gl,arguments);}catch(error){console.error('THREE.WebGLState:',error);}}//
function scissor(scissor){if(currentScissor.equals(scissor)===false){gl.scissor(scissor.x,scissor.y,scissor.z,scissor.w);currentScissor.copy(scissor);}}function viewport(viewport){if(currentViewport.equals(viewport)===false){gl.viewport(viewport.x,viewport.y,viewport.z,viewport.w);currentViewport.copy(viewport);}}function updateUBOMapping(uniformsGroup,program){let mapping=uboProgramMap.get(program);if(mapping===undefined){mapping=new WeakMap();uboProgramMap.set(program,mapping);}let blockIndex=mapping.get(uniformsGroup);if(blockIndex===undefined){blockIndex=gl.getUniformBlockIndex(program,uniformsGroup.name);mapping.set(uniformsGroup,blockIndex);}}function uniformBlockBinding(uniformsGroup,program){const mapping=uboProgramMap.get(program);const blockIndex=mapping.get(uniformsGroup);if(uboBindings.get(program)!==blockIndex){// bind shader specific block index to global block point
gl.uniformBlockBinding(program,blockIndex,uniformsGroup.__bindingPointIndex);uboBindings.set(program,blockIndex);}}//
function reset(){// reset state
gl.disable(gl.BLEND);gl.disable(gl.CULL_FACE);gl.disable(gl.DEPTH_TEST);gl.disable(gl.POLYGON_OFFSET_FILL);gl.disable(gl.SCISSOR_TEST);gl.disable(gl.STENCIL_TEST);gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE);gl.blendEquation(gl.FUNC_ADD);gl.blendFunc(gl.ONE,gl.ZERO);gl.blendFuncSeparate(gl.ONE,gl.ZERO,gl.ONE,gl.ZERO);gl.blendColor(0,0,0,0);gl.colorMask(true,true,true,true);gl.clearColor(0,0,0,0);gl.depthMask(true);gl.depthFunc(gl.LESS);gl.clearDepth(1);gl.stencilMask(0xffffffff);gl.stencilFunc(gl.ALWAYS,0,0xffffffff);gl.stencilOp(gl.KEEP,gl.KEEP,gl.KEEP);gl.clearStencil(0);gl.cullFace(gl.BACK);gl.frontFace(gl.CCW);gl.polygonOffset(0,0);gl.activeTexture(gl.TEXTURE0);gl.bindFramebuffer(gl.FRAMEBUFFER,null);gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER,null);gl.bindFramebuffer(gl.READ_FRAMEBUFFER,null);gl.useProgram(null);gl.lineWidth(1);gl.scissor(0,0,gl.canvas.width,gl.canvas.height);gl.viewport(0,0,gl.canvas.width,gl.canvas.height);// reset internals
enabledCapabilities={};currentTextureSlot=null;currentBoundTextures={};currentBoundFramebuffers={};currentDrawbuffers=new WeakMap();defaultDrawbuffers=[];currentProgram=null;currentBlendingEnabled=false;currentBlending=null;currentBlendEquation=null;currentBlendSrc=null;currentBlendDst=null;currentBlendEquationAlpha=null;currentBlendSrcAlpha=null;currentBlendDstAlpha=null;currentBlendColor=new Color$1(0,0,0);currentBlendAlpha=0;currentPremultipledAlpha=false;currentFlipSided=null;currentCullFace=null;currentLineWidth=null;currentPolygonOffsetFactor=null;currentPolygonOffsetUnits=null;currentScissor.set(0,0,gl.canvas.width,gl.canvas.height);currentViewport.set(0,0,gl.canvas.width,gl.canvas.height);colorBuffer.reset();depthBuffer.reset();stencilBuffer.reset();}return {buffers:{color:colorBuffer,depth:depthBuffer,stencil:stencilBuffer},enable:enable,disable:disable,bindFramebuffer:bindFramebuffer,drawBuffers:drawBuffers,useProgram:useProgram,setBlending:setBlending,setMaterial:setMaterial,setFlipSided:setFlipSided,setCullFace:setCullFace,setLineWidth:setLineWidth,setPolygonOffset:setPolygonOffset,setScissorTest:setScissorTest,activeTexture:activeTexture,bindTexture:bindTexture,unbindTexture:unbindTexture,compressedTexImage2D:compressedTexImage2D,compressedTexImage3D:compressedTexImage3D,texImage2D:texImage2D,texImage3D:texImage3D,updateUBOMapping:updateUBOMapping,uniformBlockBinding:uniformBlockBinding,texStorage2D:texStorage2D,texStorage3D:texStorage3D,texSubImage2D:texSubImage2D,texSubImage3D:texSubImage3D,compressedTexSubImage2D:compressedTexSubImage2D,compressedTexSubImage3D:compressedTexSubImage3D,scissor:scissor,viewport:viewport,reset:reset};}/**
 * Given the width, height, format, and type of a texture. Determines how many
 * bytes must be used to represent the texture.
 */function getByteLength(width,height,format,type){const typeByteLength=getTextureTypeByteLength(type);switch(format){// https://registry.khronos.org/OpenGL-Refpages/es3.0/html/glTexImage2D.xhtml
case AlphaFormat:return width*height;case LuminanceFormat:return width*height;case LuminanceAlphaFormat:return width*height*2;case RedFormat:return width*height/typeByteLength.components*typeByteLength.byteLength;case RedIntegerFormat:return width*height/typeByteLength.components*typeByteLength.byteLength;case RGFormat:return width*height*2/typeByteLength.components*typeByteLength.byteLength;case RGIntegerFormat:return width*height*2/typeByteLength.components*typeByteLength.byteLength;case RGBFormat:return width*height*3/typeByteLength.components*typeByteLength.byteLength;case RGBAFormat$1:return width*height*4/typeByteLength.components*typeByteLength.byteLength;case RGBAIntegerFormat:return width*height*4/typeByteLength.components*typeByteLength.byteLength;// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_s3tc_srgb/
case RGB_S3TC_DXT1_Format:case RGBA_S3TC_DXT1_Format:return Math.floor((width+3)/4)*Math.floor((height+3)/4)*8;case RGBA_S3TC_DXT3_Format:case RGBA_S3TC_DXT5_Format:return Math.floor((width+3)/4)*Math.floor((height+3)/4)*16;// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_pvrtc/
case RGB_PVRTC_2BPPV1_Format:case RGBA_PVRTC_2BPPV1_Format:return Math.max(width,16)*Math.max(height,8)/4;case RGB_PVRTC_4BPPV1_Format:case RGBA_PVRTC_4BPPV1_Format:return Math.max(width,8)*Math.max(height,8)/2;// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_etc/
case RGB_ETC1_Format:case RGB_ETC2_Format:return Math.floor((width+3)/4)*Math.floor((height+3)/4)*8;case RGBA_ETC2_EAC_Format:return Math.floor((width+3)/4)*Math.floor((height+3)/4)*16;// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_astc/
case RGBA_ASTC_4x4_Format:return Math.floor((width+3)/4)*Math.floor((height+3)/4)*16;case RGBA_ASTC_5x4_Format:return Math.floor((width+4)/5)*Math.floor((height+3)/4)*16;case RGBA_ASTC_5x5_Format:return Math.floor((width+4)/5)*Math.floor((height+4)/5)*16;case RGBA_ASTC_6x5_Format:return Math.floor((width+5)/6)*Math.floor((height+4)/5)*16;case RGBA_ASTC_6x6_Format:return Math.floor((width+5)/6)*Math.floor((height+5)/6)*16;case RGBA_ASTC_8x5_Format:return Math.floor((width+7)/8)*Math.floor((height+4)/5)*16;case RGBA_ASTC_8x6_Format:return Math.floor((width+7)/8)*Math.floor((height+5)/6)*16;case RGBA_ASTC_8x8_Format:return Math.floor((width+7)/8)*Math.floor((height+7)/8)*16;case RGBA_ASTC_10x5_Format:return Math.floor((width+9)/10)*Math.floor((height+4)/5)*16;case RGBA_ASTC_10x6_Format:return Math.floor((width+9)/10)*Math.floor((height+5)/6)*16;case RGBA_ASTC_10x8_Format:return Math.floor((width+9)/10)*Math.floor((height+7)/8)*16;case RGBA_ASTC_10x10_Format:return Math.floor((width+9)/10)*Math.floor((height+9)/10)*16;case RGBA_ASTC_12x10_Format:return Math.floor((width+11)/12)*Math.floor((height+9)/10)*16;case RGBA_ASTC_12x12_Format:return Math.floor((width+11)/12)*Math.floor((height+11)/12)*16;// https://registry.khronos.org/webgl/extensions/EXT_texture_compression_bptc/
case RGBA_BPTC_Format:case RGB_BPTC_SIGNED_Format:case RGB_BPTC_UNSIGNED_Format:return Math.ceil(width/4)*Math.ceil(height/4)*16;// https://registry.khronos.org/webgl/extensions/EXT_texture_compression_rgtc/
case RED_RGTC1_Format:case SIGNED_RED_RGTC1_Format:return Math.ceil(width/4)*Math.ceil(height/4)*8;case RED_GREEN_RGTC2_Format:case SIGNED_RED_GREEN_RGTC2_Format:return Math.ceil(width/4)*Math.ceil(height/4)*16;}throw new Error(`Unable to determine texture byte length for ${format} format.`);}function getTextureTypeByteLength(type){switch(type){case UnsignedByteType$1:case ByteType:return {byteLength:1,components:1};case UnsignedShortType:case ShortType:case HalfFloatType$1:return {byteLength:2,components:1};case UnsignedShort4444Type:case UnsignedShort5551Type:return {byteLength:2,components:4};case UnsignedIntType$1:case IntType$1:case FloatType$1:return {byteLength:4,components:1};case UnsignedInt5999Type:return {byteLength:4,components:3};}throw new Error(`Unknown texture type ${type}.`);}function WebGLTextures(_gl,extensions,state,properties,capabilities,utils,info){const multisampledRTTExt=extensions.has('WEBGL_multisampled_render_to_texture')?extensions.get('WEBGL_multisampled_render_to_texture'):null;const supportsInvalidateFramebuffer=typeof navigator==='undefined'?false:/OculusBrowser/g.test(navigator.userAgent);const _imageDimensions=new Vector2$1();const _videoTextures=new WeakMap();let _canvas;const _sources=new WeakMap();// maps WebglTexture objects to instances of Source
// cordova iOS (as of 5.0) still uses UIWebView, which provides OffscreenCanvas,
// also OffscreenCanvas.getContext("webgl"), but not OffscreenCanvas.getContext("2d")!
// Some implementations may only implement OffscreenCanvas partially (e.g. lacking 2d).
let useOffscreenCanvas=false;try{useOffscreenCanvas=typeof OffscreenCanvas!=='undefined'// eslint-disable-next-line compat/compat
&&new OffscreenCanvas(1,1).getContext('2d')!==null;}catch(err){// Ignore any errors
}function createCanvas(width,height){// Use OffscreenCanvas when available. Specially needed in web workers
return useOffscreenCanvas?// eslint-disable-next-line compat/compat
new OffscreenCanvas(width,height):createElementNS$1('canvas');}function resizeImage(image,needsNewCanvas,maxSize){let scale=1;const dimensions=getDimensions(image);// handle case if texture exceeds max size
if(dimensions.width>maxSize||dimensions.height>maxSize){scale=maxSize/Math.max(dimensions.width,dimensions.height);}// only perform resize if necessary
if(scale<1){// only perform resize for certain image types
if(typeof HTMLImageElement!=='undefined'&&image instanceof HTMLImageElement||typeof HTMLCanvasElement!=='undefined'&&image instanceof HTMLCanvasElement||typeof ImageBitmap!=='undefined'&&image instanceof ImageBitmap||typeof VideoFrame!=='undefined'&&image instanceof VideoFrame){const width=Math.floor(scale*dimensions.width);const height=Math.floor(scale*dimensions.height);if(_canvas===undefined)_canvas=createCanvas(width,height);// cube textures can't reuse the same canvas
const canvas=needsNewCanvas?createCanvas(width,height):_canvas;canvas.width=width;canvas.height=height;const context=canvas.getContext('2d');context.drawImage(image,0,0,width,height);console.warn('THREE.WebGLRenderer: Texture has been resized from ('+dimensions.width+'x'+dimensions.height+') to ('+width+'x'+height+').');return canvas;}else {if('data'in image){console.warn('THREE.WebGLRenderer: Image in DataTexture is too big ('+dimensions.width+'x'+dimensions.height+').');}return image;}}return image;}function textureNeedsGenerateMipmaps(texture){return texture.generateMipmaps&&texture.minFilter!==NearestFilter$1&&texture.minFilter!==LinearFilter$1;}function generateMipmap(target){_gl.generateMipmap(target);}function getInternalFormat(internalFormatName,glFormat,glType,colorSpace,forceLinearTransfer=false){if(internalFormatName!==null){if(_gl[internalFormatName]!==undefined)return _gl[internalFormatName];console.warn('THREE.WebGLRenderer: Attempt to use non-existing WebGL internal format \''+internalFormatName+'\'');}let internalFormat=glFormat;if(glFormat===_gl.RED){if(glType===_gl.FLOAT)internalFormat=_gl.R32F;if(glType===_gl.HALF_FLOAT)internalFormat=_gl.R16F;if(glType===_gl.UNSIGNED_BYTE)internalFormat=_gl.R8;}if(glFormat===_gl.RED_INTEGER){if(glType===_gl.UNSIGNED_BYTE)internalFormat=_gl.R8UI;if(glType===_gl.UNSIGNED_SHORT)internalFormat=_gl.R16UI;if(glType===_gl.UNSIGNED_INT)internalFormat=_gl.R32UI;if(glType===_gl.BYTE)internalFormat=_gl.R8I;if(glType===_gl.SHORT)internalFormat=_gl.R16I;if(glType===_gl.INT)internalFormat=_gl.R32I;}if(glFormat===_gl.RG){if(glType===_gl.FLOAT)internalFormat=_gl.RG32F;if(glType===_gl.HALF_FLOAT)internalFormat=_gl.RG16F;if(glType===_gl.UNSIGNED_BYTE)internalFormat=_gl.RG8;}if(glFormat===_gl.RG_INTEGER){if(glType===_gl.UNSIGNED_BYTE)internalFormat=_gl.RG8UI;if(glType===_gl.UNSIGNED_SHORT)internalFormat=_gl.RG16UI;if(glType===_gl.UNSIGNED_INT)internalFormat=_gl.RG32UI;if(glType===_gl.BYTE)internalFormat=_gl.RG8I;if(glType===_gl.SHORT)internalFormat=_gl.RG16I;if(glType===_gl.INT)internalFormat=_gl.RG32I;}if(glFormat===_gl.RGB){if(glType===_gl.UNSIGNED_INT_5_9_9_9_REV)internalFormat=_gl.RGB9_E5;}if(glFormat===_gl.RGBA){const transfer=forceLinearTransfer?LinearTransfer$1:ColorManagement$1.getTransfer(colorSpace);if(glType===_gl.FLOAT)internalFormat=_gl.RGBA32F;if(glType===_gl.HALF_FLOAT)internalFormat=_gl.RGBA16F;if(glType===_gl.UNSIGNED_BYTE)internalFormat=transfer===SRGBTransfer$1?_gl.SRGB8_ALPHA8:_gl.RGBA8;if(glType===_gl.UNSIGNED_SHORT_4_4_4_4)internalFormat=_gl.RGBA4;if(glType===_gl.UNSIGNED_SHORT_5_5_5_1)internalFormat=_gl.RGB5_A1;}if(internalFormat===_gl.R16F||internalFormat===_gl.R32F||internalFormat===_gl.RG16F||internalFormat===_gl.RG32F||internalFormat===_gl.RGBA16F||internalFormat===_gl.RGBA32F){extensions.get('EXT_color_buffer_float');}return internalFormat;}function getInternalDepthFormat(useStencil,depthType){let glInternalFormat;if(useStencil){if(depthType===null||depthType===UnsignedIntType$1||depthType===UnsignedInt248Type$1){glInternalFormat=_gl.DEPTH24_STENCIL8;}else if(depthType===FloatType$1){glInternalFormat=_gl.DEPTH32F_STENCIL8;}else if(depthType===UnsignedShortType){glInternalFormat=_gl.DEPTH24_STENCIL8;console.warn('DepthTexture: 16 bit depth attachment is not supported with stencil. Using 24-bit attachment.');}}else {if(depthType===null||depthType===UnsignedIntType$1||depthType===UnsignedInt248Type$1){glInternalFormat=_gl.DEPTH_COMPONENT24;}else if(depthType===FloatType$1){glInternalFormat=_gl.DEPTH_COMPONENT32F;}else if(depthType===UnsignedShortType){glInternalFormat=_gl.DEPTH_COMPONENT16;}}return glInternalFormat;}function getMipLevels(texture,image){if(textureNeedsGenerateMipmaps(texture)===true||texture.isFramebufferTexture&&texture.minFilter!==NearestFilter$1&&texture.minFilter!==LinearFilter$1){return Math.log2(Math.max(image.width,image.height))+1;}else if(texture.mipmaps!==undefined&&texture.mipmaps.length>0){// user-defined mipmaps
return texture.mipmaps.length;}else if(texture.isCompressedTexture&&Array.isArray(texture.image)){return image.mipmaps.length;}else {// texture without mipmaps (only base level)
return 1;}}//
function onTextureDispose(event){const texture=event.target;texture.removeEventListener('dispose',onTextureDispose);deallocateTexture(texture);if(texture.isVideoTexture){_videoTextures.delete(texture);}}function onRenderTargetDispose(event){const renderTarget=event.target;renderTarget.removeEventListener('dispose',onRenderTargetDispose);deallocateRenderTarget(renderTarget);}//
function deallocateTexture(texture){const textureProperties=properties.get(texture);if(textureProperties.__webglInit===undefined)return;// check if it's necessary to remove the WebGLTexture object
const source=texture.source;const webglTextures=_sources.get(source);if(webglTextures){const webglTexture=webglTextures[textureProperties.__cacheKey];webglTexture.usedTimes--;// the WebGLTexture object is not used anymore, remove it
if(webglTexture.usedTimes===0){deleteTexture(texture);}// remove the weak map entry if no WebGLTexture uses the source anymore
if(Object.keys(webglTextures).length===0){_sources.delete(source);}}properties.remove(texture);}function deleteTexture(texture){const textureProperties=properties.get(texture);_gl.deleteTexture(textureProperties.__webglTexture);const source=texture.source;const webglTextures=_sources.get(source);delete webglTextures[textureProperties.__cacheKey];info.memory.textures--;}function deallocateRenderTarget(renderTarget){const renderTargetProperties=properties.get(renderTarget);if(renderTarget.depthTexture){renderTarget.depthTexture.dispose();}if(renderTarget.isWebGLCubeRenderTarget){for(let i=0;i<6;i++){if(Array.isArray(renderTargetProperties.__webglFramebuffer[i])){for(let level=0;level<renderTargetProperties.__webglFramebuffer[i].length;level++)_gl.deleteFramebuffer(renderTargetProperties.__webglFramebuffer[i][level]);}else {_gl.deleteFramebuffer(renderTargetProperties.__webglFramebuffer[i]);}if(renderTargetProperties.__webglDepthbuffer)_gl.deleteRenderbuffer(renderTargetProperties.__webglDepthbuffer[i]);}}else {if(Array.isArray(renderTargetProperties.__webglFramebuffer)){for(let level=0;level<renderTargetProperties.__webglFramebuffer.length;level++)_gl.deleteFramebuffer(renderTargetProperties.__webglFramebuffer[level]);}else {_gl.deleteFramebuffer(renderTargetProperties.__webglFramebuffer);}if(renderTargetProperties.__webglDepthbuffer)_gl.deleteRenderbuffer(renderTargetProperties.__webglDepthbuffer);if(renderTargetProperties.__webglMultisampledFramebuffer)_gl.deleteFramebuffer(renderTargetProperties.__webglMultisampledFramebuffer);if(renderTargetProperties.__webglColorRenderbuffer){for(let i=0;i<renderTargetProperties.__webglColorRenderbuffer.length;i++){if(renderTargetProperties.__webglColorRenderbuffer[i])_gl.deleteRenderbuffer(renderTargetProperties.__webglColorRenderbuffer[i]);}}if(renderTargetProperties.__webglDepthRenderbuffer)_gl.deleteRenderbuffer(renderTargetProperties.__webglDepthRenderbuffer);}const textures=renderTarget.textures;for(let i=0,il=textures.length;i<il;i++){const attachmentProperties=properties.get(textures[i]);if(attachmentProperties.__webglTexture){_gl.deleteTexture(attachmentProperties.__webglTexture);info.memory.textures--;}properties.remove(textures[i]);}properties.remove(renderTarget);}//
let textureUnits=0;function resetTextureUnits(){textureUnits=0;}function allocateTextureUnit(){const textureUnit=textureUnits;if(textureUnit>=capabilities.maxTextures){console.warn('THREE.WebGLTextures: Trying to use '+textureUnit+' texture units while this GPU supports only '+capabilities.maxTextures);}textureUnits+=1;return textureUnit;}function getTextureCacheKey(texture){const array=[];array.push(texture.wrapS);array.push(texture.wrapT);array.push(texture.wrapR||0);array.push(texture.magFilter);array.push(texture.minFilter);array.push(texture.anisotropy);array.push(texture.internalFormat);array.push(texture.format);array.push(texture.type);array.push(texture.generateMipmaps);array.push(texture.premultiplyAlpha);array.push(texture.flipY);array.push(texture.unpackAlignment);array.push(texture.colorSpace);return array.join();}//
function setTexture2D(texture,slot){const textureProperties=properties.get(texture);if(texture.isVideoTexture)updateVideoTexture(texture);if(texture.isRenderTargetTexture===false&&texture.version>0&&textureProperties.__version!==texture.version){const image=texture.image;if(image===null){console.warn('THREE.WebGLRenderer: Texture marked for update but no image data found.');}else if(image.complete===false){console.warn('THREE.WebGLRenderer: Texture marked for update but image is incomplete');}else {uploadTexture(textureProperties,texture,slot);return;}}state.bindTexture(_gl.TEXTURE_2D,textureProperties.__webglTexture,_gl.TEXTURE0+slot);}function setTexture2DArray(texture,slot){const textureProperties=properties.get(texture);if(texture.version>0&&textureProperties.__version!==texture.version){uploadTexture(textureProperties,texture,slot);return;}state.bindTexture(_gl.TEXTURE_2D_ARRAY,textureProperties.__webglTexture,_gl.TEXTURE0+slot);}function setTexture3D(texture,slot){const textureProperties=properties.get(texture);if(texture.version>0&&textureProperties.__version!==texture.version){uploadTexture(textureProperties,texture,slot);return;}state.bindTexture(_gl.TEXTURE_3D,textureProperties.__webglTexture,_gl.TEXTURE0+slot);}function setTextureCube(texture,slot){const textureProperties=properties.get(texture);if(texture.version>0&&textureProperties.__version!==texture.version){uploadCubeTexture(textureProperties,texture,slot);return;}state.bindTexture(_gl.TEXTURE_CUBE_MAP,textureProperties.__webglTexture,_gl.TEXTURE0+slot);}const wrappingToGL={[RepeatWrapping$1]:_gl.REPEAT,[ClampToEdgeWrapping$1]:_gl.CLAMP_TO_EDGE,[MirroredRepeatWrapping$1]:_gl.MIRRORED_REPEAT};const filterToGL={[NearestFilter$1]:_gl.NEAREST,[NearestMipmapNearestFilter]:_gl.NEAREST_MIPMAP_NEAREST,[NearestMipmapLinearFilter]:_gl.NEAREST_MIPMAP_LINEAR,[LinearFilter$1]:_gl.LINEAR,[LinearMipmapNearestFilter]:_gl.LINEAR_MIPMAP_NEAREST,[LinearMipmapLinearFilter$1]:_gl.LINEAR_MIPMAP_LINEAR};const compareToGL={[NeverCompare]:_gl.NEVER,[AlwaysCompare]:_gl.ALWAYS,[LessCompare]:_gl.LESS,[LessEqualCompare]:_gl.LEQUAL,[EqualCompare]:_gl.EQUAL,[GreaterEqualCompare]:_gl.GEQUAL,[GreaterCompare]:_gl.GREATER,[NotEqualCompare]:_gl.NOTEQUAL};function setTextureParameters(textureType,texture){if(texture.type===FloatType$1&&extensions.has('OES_texture_float_linear')===false&&(texture.magFilter===LinearFilter$1||texture.magFilter===LinearMipmapNearestFilter||texture.magFilter===NearestMipmapLinearFilter||texture.magFilter===LinearMipmapLinearFilter$1||texture.minFilter===LinearFilter$1||texture.minFilter===LinearMipmapNearestFilter||texture.minFilter===NearestMipmapLinearFilter||texture.minFilter===LinearMipmapLinearFilter$1)){console.warn('THREE.WebGLRenderer: Unable to use linear filtering with floating point textures. OES_texture_float_linear not supported on this device.');}_gl.texParameteri(textureType,_gl.TEXTURE_WRAP_S,wrappingToGL[texture.wrapS]);_gl.texParameteri(textureType,_gl.TEXTURE_WRAP_T,wrappingToGL[texture.wrapT]);if(textureType===_gl.TEXTURE_3D||textureType===_gl.TEXTURE_2D_ARRAY){_gl.texParameteri(textureType,_gl.TEXTURE_WRAP_R,wrappingToGL[texture.wrapR]);}_gl.texParameteri(textureType,_gl.TEXTURE_MAG_FILTER,filterToGL[texture.magFilter]);_gl.texParameteri(textureType,_gl.TEXTURE_MIN_FILTER,filterToGL[texture.minFilter]);if(texture.compareFunction){_gl.texParameteri(textureType,_gl.TEXTURE_COMPARE_MODE,_gl.COMPARE_REF_TO_TEXTURE);_gl.texParameteri(textureType,_gl.TEXTURE_COMPARE_FUNC,compareToGL[texture.compareFunction]);}if(extensions.has('EXT_texture_filter_anisotropic')===true){if(texture.magFilter===NearestFilter$1)return;if(texture.minFilter!==NearestMipmapLinearFilter&&texture.minFilter!==LinearMipmapLinearFilter$1)return;if(texture.type===FloatType$1&&extensions.has('OES_texture_float_linear')===false)return;// verify extension
if(texture.anisotropy>1||properties.get(texture).__currentAnisotropy){const extension=extensions.get('EXT_texture_filter_anisotropic');_gl.texParameterf(textureType,extension.TEXTURE_MAX_ANISOTROPY_EXT,Math.min(texture.anisotropy,capabilities.getMaxAnisotropy()));properties.get(texture).__currentAnisotropy=texture.anisotropy;}}}function initTexture(textureProperties,texture){let forceUpload=false;if(textureProperties.__webglInit===undefined){textureProperties.__webglInit=true;texture.addEventListener('dispose',onTextureDispose);}// create Source <-> WebGLTextures mapping if necessary
const source=texture.source;let webglTextures=_sources.get(source);if(webglTextures===undefined){webglTextures={};_sources.set(source,webglTextures);}// check if there is already a WebGLTexture object for the given texture parameters
const textureCacheKey=getTextureCacheKey(texture);if(textureCacheKey!==textureProperties.__cacheKey){// if not, create a new instance of WebGLTexture
if(webglTextures[textureCacheKey]===undefined){// create new entry
webglTextures[textureCacheKey]={texture:_gl.createTexture(),usedTimes:0};info.memory.textures++;// when a new instance of WebGLTexture was created, a texture upload is required
// even if the image contents are identical
forceUpload=true;}webglTextures[textureCacheKey].usedTimes++;// every time the texture cache key changes, it's necessary to check if an instance of
// WebGLTexture can be deleted in order to avoid a memory leak.
const webglTexture=webglTextures[textureProperties.__cacheKey];if(webglTexture!==undefined){webglTextures[textureProperties.__cacheKey].usedTimes--;if(webglTexture.usedTimes===0){deleteTexture(texture);}}// store references to cache key and WebGLTexture object
textureProperties.__cacheKey=textureCacheKey;textureProperties.__webglTexture=webglTextures[textureCacheKey].texture;}return forceUpload;}function uploadTexture(textureProperties,texture,slot){let textureType=_gl.TEXTURE_2D;if(texture.isDataArrayTexture||texture.isCompressedArrayTexture)textureType=_gl.TEXTURE_2D_ARRAY;if(texture.isData3DTexture)textureType=_gl.TEXTURE_3D;const forceUpload=initTexture(textureProperties,texture);const source=texture.source;state.bindTexture(textureType,textureProperties.__webglTexture,_gl.TEXTURE0+slot);const sourceProperties=properties.get(source);if(source.version!==sourceProperties.__version||forceUpload===true){state.activeTexture(_gl.TEXTURE0+slot);const workingPrimaries=ColorManagement$1.getPrimaries(ColorManagement$1.workingColorSpace);const texturePrimaries=texture.colorSpace===NoColorSpace$1?null:ColorManagement$1.getPrimaries(texture.colorSpace);const unpackConversion=texture.colorSpace===NoColorSpace$1||workingPrimaries===texturePrimaries?_gl.NONE:_gl.BROWSER_DEFAULT_WEBGL;_gl.pixelStorei(_gl.UNPACK_FLIP_Y_WEBGL,texture.flipY);_gl.pixelStorei(_gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,texture.premultiplyAlpha);_gl.pixelStorei(_gl.UNPACK_ALIGNMENT,texture.unpackAlignment);_gl.pixelStorei(_gl.UNPACK_COLORSPACE_CONVERSION_WEBGL,unpackConversion);let image=resizeImage(texture.image,false,capabilities.maxTextureSize);image=verifyColorSpace(texture,image);const glFormat=utils.convert(texture.format,texture.colorSpace);const glType=utils.convert(texture.type);let glInternalFormat=getInternalFormat(texture.internalFormat,glFormat,glType,texture.colorSpace,texture.isVideoTexture);setTextureParameters(textureType,texture);let mipmap;const mipmaps=texture.mipmaps;const useTexStorage=texture.isVideoTexture!==true;const allocateMemory=sourceProperties.__version===undefined||forceUpload===true;const dataReady=source.dataReady;const levels=getMipLevels(texture,image);if(texture.isDepthTexture){glInternalFormat=getInternalDepthFormat(texture.format===DepthStencilFormat$1,texture.type);//
if(allocateMemory){if(useTexStorage){state.texStorage2D(_gl.TEXTURE_2D,1,glInternalFormat,image.width,image.height);}else {state.texImage2D(_gl.TEXTURE_2D,0,glInternalFormat,image.width,image.height,0,glFormat,glType,null);}}}else if(texture.isDataTexture){// use manually created mipmaps if available
// if there are no manual mipmaps
// set 0 level mipmap and then use GL to generate other mipmap levels
if(mipmaps.length>0){if(useTexStorage&&allocateMemory){state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,mipmaps[0].width,mipmaps[0].height);}for(let i=0,il=mipmaps.length;i<il;i++){mipmap=mipmaps[i];if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_2D,i,0,0,mipmap.width,mipmap.height,glFormat,glType,mipmap.data);}}else {state.texImage2D(_gl.TEXTURE_2D,i,glInternalFormat,mipmap.width,mipmap.height,0,glFormat,glType,mipmap.data);}}texture.generateMipmaps=false;}else {if(useTexStorage){if(allocateMemory){state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,image.width,image.height);}if(dataReady){state.texSubImage2D(_gl.TEXTURE_2D,0,0,0,image.width,image.height,glFormat,glType,image.data);}}else {state.texImage2D(_gl.TEXTURE_2D,0,glInternalFormat,image.width,image.height,0,glFormat,glType,image.data);}}}else if(texture.isCompressedTexture){if(texture.isCompressedArrayTexture){if(useTexStorage&&allocateMemory){state.texStorage3D(_gl.TEXTURE_2D_ARRAY,levels,glInternalFormat,mipmaps[0].width,mipmaps[0].height,image.depth);}for(let i=0,il=mipmaps.length;i<il;i++){mipmap=mipmaps[i];if(texture.format!==RGBAFormat$1){if(glFormat!==null){if(useTexStorage){if(dataReady){if(texture.layerUpdates.size>0){const layerByteLength=getByteLength(mipmap.width,mipmap.height,texture.format,texture.type);for(const layerIndex of texture.layerUpdates){const layerData=mipmap.data.subarray(layerIndex*layerByteLength/mipmap.data.BYTES_PER_ELEMENT,(layerIndex+1)*layerByteLength/mipmap.data.BYTES_PER_ELEMENT);state.compressedTexSubImage3D(_gl.TEXTURE_2D_ARRAY,i,0,0,layerIndex,mipmap.width,mipmap.height,1,glFormat,layerData,0,0);}texture.clearLayerUpdates();}else {state.compressedTexSubImage3D(_gl.TEXTURE_2D_ARRAY,i,0,0,0,mipmap.width,mipmap.height,image.depth,glFormat,mipmap.data,0,0);}}}else {state.compressedTexImage3D(_gl.TEXTURE_2D_ARRAY,i,glInternalFormat,mipmap.width,mipmap.height,image.depth,0,mipmap.data,0,0);}}else {console.warn('THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()');}}else {if(useTexStorage){if(dataReady){state.texSubImage3D(_gl.TEXTURE_2D_ARRAY,i,0,0,0,mipmap.width,mipmap.height,image.depth,glFormat,glType,mipmap.data);}}else {state.texImage3D(_gl.TEXTURE_2D_ARRAY,i,glInternalFormat,mipmap.width,mipmap.height,image.depth,0,glFormat,glType,mipmap.data);}}}}else {if(useTexStorage&&allocateMemory){state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,mipmaps[0].width,mipmaps[0].height);}for(let i=0,il=mipmaps.length;i<il;i++){mipmap=mipmaps[i];if(texture.format!==RGBAFormat$1){if(glFormat!==null){if(useTexStorage){if(dataReady){state.compressedTexSubImage2D(_gl.TEXTURE_2D,i,0,0,mipmap.width,mipmap.height,glFormat,mipmap.data);}}else {state.compressedTexImage2D(_gl.TEXTURE_2D,i,glInternalFormat,mipmap.width,mipmap.height,0,mipmap.data);}}else {console.warn('THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()');}}else {if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_2D,i,0,0,mipmap.width,mipmap.height,glFormat,glType,mipmap.data);}}else {state.texImage2D(_gl.TEXTURE_2D,i,glInternalFormat,mipmap.width,mipmap.height,0,glFormat,glType,mipmap.data);}}}}}else if(texture.isDataArrayTexture){if(useTexStorage){if(allocateMemory){state.texStorage3D(_gl.TEXTURE_2D_ARRAY,levels,glInternalFormat,image.width,image.height,image.depth);}if(dataReady){if(texture.layerUpdates.size>0){const layerByteLength=getByteLength(image.width,image.height,texture.format,texture.type);for(const layerIndex of texture.layerUpdates){const layerData=image.data.subarray(layerIndex*layerByteLength/image.data.BYTES_PER_ELEMENT,(layerIndex+1)*layerByteLength/image.data.BYTES_PER_ELEMENT);state.texSubImage3D(_gl.TEXTURE_2D_ARRAY,0,0,0,layerIndex,image.width,image.height,1,glFormat,glType,layerData);}texture.clearLayerUpdates();}else {state.texSubImage3D(_gl.TEXTURE_2D_ARRAY,0,0,0,0,image.width,image.height,image.depth,glFormat,glType,image.data);}}}else {state.texImage3D(_gl.TEXTURE_2D_ARRAY,0,glInternalFormat,image.width,image.height,image.depth,0,glFormat,glType,image.data);}}else if(texture.isData3DTexture){if(useTexStorage){if(allocateMemory){state.texStorage3D(_gl.TEXTURE_3D,levels,glInternalFormat,image.width,image.height,image.depth);}if(dataReady){state.texSubImage3D(_gl.TEXTURE_3D,0,0,0,0,image.width,image.height,image.depth,glFormat,glType,image.data);}}else {state.texImage3D(_gl.TEXTURE_3D,0,glInternalFormat,image.width,image.height,image.depth,0,glFormat,glType,image.data);}}else if(texture.isFramebufferTexture){if(allocateMemory){if(useTexStorage){state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,image.width,image.height);}else {let width=image.width,height=image.height;for(let i=0;i<levels;i++){state.texImage2D(_gl.TEXTURE_2D,i,glInternalFormat,width,height,0,glFormat,glType,null);width>>=1;height>>=1;}}}}else {// regular Texture (image, video, canvas)
// use manually created mipmaps if available
// if there are no manual mipmaps
// set 0 level mipmap and then use GL to generate other mipmap levels
if(mipmaps.length>0){if(useTexStorage&&allocateMemory){const dimensions=getDimensions(mipmaps[0]);state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,dimensions.width,dimensions.height);}for(let i=0,il=mipmaps.length;i<il;i++){mipmap=mipmaps[i];if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_2D,i,0,0,glFormat,glType,mipmap);}}else {state.texImage2D(_gl.TEXTURE_2D,i,glInternalFormat,glFormat,glType,mipmap);}}texture.generateMipmaps=false;}else {if(useTexStorage){if(allocateMemory){const dimensions=getDimensions(image);state.texStorage2D(_gl.TEXTURE_2D,levels,glInternalFormat,dimensions.width,dimensions.height);}if(dataReady){state.texSubImage2D(_gl.TEXTURE_2D,0,0,0,glFormat,glType,image);}}else {state.texImage2D(_gl.TEXTURE_2D,0,glInternalFormat,glFormat,glType,image);}}}if(textureNeedsGenerateMipmaps(texture)){generateMipmap(textureType);}sourceProperties.__version=source.version;if(texture.onUpdate)texture.onUpdate(texture);}textureProperties.__version=texture.version;}function uploadCubeTexture(textureProperties,texture,slot){if(texture.image.length!==6)return;const forceUpload=initTexture(textureProperties,texture);const source=texture.source;state.bindTexture(_gl.TEXTURE_CUBE_MAP,textureProperties.__webglTexture,_gl.TEXTURE0+slot);const sourceProperties=properties.get(source);if(source.version!==sourceProperties.__version||forceUpload===true){state.activeTexture(_gl.TEXTURE0+slot);const workingPrimaries=ColorManagement$1.getPrimaries(ColorManagement$1.workingColorSpace);const texturePrimaries=texture.colorSpace===NoColorSpace$1?null:ColorManagement$1.getPrimaries(texture.colorSpace);const unpackConversion=texture.colorSpace===NoColorSpace$1||workingPrimaries===texturePrimaries?_gl.NONE:_gl.BROWSER_DEFAULT_WEBGL;_gl.pixelStorei(_gl.UNPACK_FLIP_Y_WEBGL,texture.flipY);_gl.pixelStorei(_gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,texture.premultiplyAlpha);_gl.pixelStorei(_gl.UNPACK_ALIGNMENT,texture.unpackAlignment);_gl.pixelStorei(_gl.UNPACK_COLORSPACE_CONVERSION_WEBGL,unpackConversion);const isCompressed=texture.isCompressedTexture||texture.image[0].isCompressedTexture;const isDataTexture=texture.image[0]&&texture.image[0].isDataTexture;const cubeImage=[];for(let i=0;i<6;i++){if(!isCompressed&&!isDataTexture){cubeImage[i]=resizeImage(texture.image[i],true,capabilities.maxCubemapSize);}else {cubeImage[i]=isDataTexture?texture.image[i].image:texture.image[i];}cubeImage[i]=verifyColorSpace(texture,cubeImage[i]);}const image=cubeImage[0],glFormat=utils.convert(texture.format,texture.colorSpace),glType=utils.convert(texture.type),glInternalFormat=getInternalFormat(texture.internalFormat,glFormat,glType,texture.colorSpace);const useTexStorage=texture.isVideoTexture!==true;const allocateMemory=sourceProperties.__version===undefined||forceUpload===true;const dataReady=source.dataReady;let levels=getMipLevels(texture,image);setTextureParameters(_gl.TEXTURE_CUBE_MAP,texture);let mipmaps;if(isCompressed){if(useTexStorage&&allocateMemory){state.texStorage2D(_gl.TEXTURE_CUBE_MAP,levels,glInternalFormat,image.width,image.height);}for(let i=0;i<6;i++){mipmaps=cubeImage[i].mipmaps;for(let j=0;j<mipmaps.length;j++){const mipmap=mipmaps[j];if(texture.format!==RGBAFormat$1){if(glFormat!==null){if(useTexStorage){if(dataReady){state.compressedTexSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j,0,0,mipmap.width,mipmap.height,glFormat,mipmap.data);}}else {state.compressedTexImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j,glInternalFormat,mipmap.width,mipmap.height,0,mipmap.data);}}else {console.warn('THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .setTextureCube()');}}else {if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j,0,0,mipmap.width,mipmap.height,glFormat,glType,mipmap.data);}}else {state.texImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j,glInternalFormat,mipmap.width,mipmap.height,0,glFormat,glType,mipmap.data);}}}}}else {mipmaps=texture.mipmaps;if(useTexStorage&&allocateMemory){// TODO: Uniformly handle mipmap definitions
// Normal textures and compressed cube textures define base level + mips with their mipmap array
// Uncompressed cube textures use their mipmap array only for mips (no base level)
if(mipmaps.length>0)levels++;const dimensions=getDimensions(cubeImage[0]);state.texStorage2D(_gl.TEXTURE_CUBE_MAP,levels,glInternalFormat,dimensions.width,dimensions.height);}for(let i=0;i<6;i++){if(isDataTexture){if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,0,0,0,cubeImage[i].width,cubeImage[i].height,glFormat,glType,cubeImage[i].data);}}else {state.texImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,0,glInternalFormat,cubeImage[i].width,cubeImage[i].height,0,glFormat,glType,cubeImage[i].data);}for(let j=0;j<mipmaps.length;j++){const mipmap=mipmaps[j];const mipmapImage=mipmap.image[i].image;if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j+1,0,0,mipmapImage.width,mipmapImage.height,glFormat,glType,mipmapImage.data);}}else {state.texImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j+1,glInternalFormat,mipmapImage.width,mipmapImage.height,0,glFormat,glType,mipmapImage.data);}}}else {if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,0,0,0,glFormat,glType,cubeImage[i]);}}else {state.texImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,0,glInternalFormat,glFormat,glType,cubeImage[i]);}for(let j=0;j<mipmaps.length;j++){const mipmap=mipmaps[j];if(useTexStorage){if(dataReady){state.texSubImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j+1,0,0,glFormat,glType,mipmap.image[i]);}}else {state.texImage2D(_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,j+1,glInternalFormat,glFormat,glType,mipmap.image[i]);}}}}}if(textureNeedsGenerateMipmaps(texture)){// We assume images for cube map have the same size.
generateMipmap(_gl.TEXTURE_CUBE_MAP);}sourceProperties.__version=source.version;if(texture.onUpdate)texture.onUpdate(texture);}textureProperties.__version=texture.version;}// Render targets
// Setup storage for target texture and bind it to correct framebuffer
function setupFrameBufferTexture(framebuffer,renderTarget,texture,attachment,textureTarget,level){const glFormat=utils.convert(texture.format,texture.colorSpace);const glType=utils.convert(texture.type);const glInternalFormat=getInternalFormat(texture.internalFormat,glFormat,glType,texture.colorSpace);const renderTargetProperties=properties.get(renderTarget);if(!renderTargetProperties.__hasExternalTextures){const width=Math.max(1,renderTarget.width>>level);const height=Math.max(1,renderTarget.height>>level);if(textureTarget===_gl.TEXTURE_3D||textureTarget===_gl.TEXTURE_2D_ARRAY){state.texImage3D(textureTarget,level,glInternalFormat,width,height,renderTarget.depth,0,glFormat,glType,null);}else {state.texImage2D(textureTarget,level,glInternalFormat,width,height,0,glFormat,glType,null);}}state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);if(useMultisampledRTT(renderTarget)){multisampledRTTExt.framebufferTexture2DMultisampleEXT(_gl.FRAMEBUFFER,attachment,textureTarget,properties.get(texture).__webglTexture,0,getRenderTargetSamples(renderTarget));}else if(textureTarget===_gl.TEXTURE_2D||textureTarget>=_gl.TEXTURE_CUBE_MAP_POSITIVE_X&&textureTarget<=_gl.TEXTURE_CUBE_MAP_NEGATIVE_Z){// see #24753
_gl.framebufferTexture2D(_gl.FRAMEBUFFER,attachment,textureTarget,properties.get(texture).__webglTexture,level);}state.bindFramebuffer(_gl.FRAMEBUFFER,null);}// Setup storage for internal depth/stencil buffers and bind to correct framebuffer
function setupRenderBufferStorage(renderbuffer,renderTarget,isMultisample){_gl.bindRenderbuffer(_gl.RENDERBUFFER,renderbuffer);if(renderTarget.depthBuffer){// retrieve the depth attachment types
const depthTexture=renderTarget.depthTexture;const depthType=depthTexture&&depthTexture.isDepthTexture?depthTexture.type:null;const glInternalFormat=getInternalDepthFormat(renderTarget.stencilBuffer,depthType);const glAttachmentType=renderTarget.stencilBuffer?_gl.DEPTH_STENCIL_ATTACHMENT:_gl.DEPTH_ATTACHMENT;// set up the attachment
const samples=getRenderTargetSamples(renderTarget);const isUseMultisampledRTT=useMultisampledRTT(renderTarget);if(isUseMultisampledRTT){multisampledRTTExt.renderbufferStorageMultisampleEXT(_gl.RENDERBUFFER,samples,glInternalFormat,renderTarget.width,renderTarget.height);}else if(isMultisample){_gl.renderbufferStorageMultisample(_gl.RENDERBUFFER,samples,glInternalFormat,renderTarget.width,renderTarget.height);}else {_gl.renderbufferStorage(_gl.RENDERBUFFER,glInternalFormat,renderTarget.width,renderTarget.height);}_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,glAttachmentType,_gl.RENDERBUFFER,renderbuffer);}else {const textures=renderTarget.textures;for(let i=0;i<textures.length;i++){const texture=textures[i];const glFormat=utils.convert(texture.format,texture.colorSpace);const glType=utils.convert(texture.type);const glInternalFormat=getInternalFormat(texture.internalFormat,glFormat,glType,texture.colorSpace);const samples=getRenderTargetSamples(renderTarget);if(isMultisample&&useMultisampledRTT(renderTarget)===false){_gl.renderbufferStorageMultisample(_gl.RENDERBUFFER,samples,glInternalFormat,renderTarget.width,renderTarget.height);}else if(useMultisampledRTT(renderTarget)){multisampledRTTExt.renderbufferStorageMultisampleEXT(_gl.RENDERBUFFER,samples,glInternalFormat,renderTarget.width,renderTarget.height);}else {_gl.renderbufferStorage(_gl.RENDERBUFFER,glInternalFormat,renderTarget.width,renderTarget.height);}}}_gl.bindRenderbuffer(_gl.RENDERBUFFER,null);}// Setup resources for a Depth Texture for a FBO (needs an extension)
function setupDepthTexture(framebuffer,renderTarget){const isCube=renderTarget&&renderTarget.isWebGLCubeRenderTarget;if(isCube)throw new Error('Depth Texture with cube render targets is not supported');state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);if(!(renderTarget.depthTexture&&renderTarget.depthTexture.isDepthTexture)){throw new Error('renderTarget.depthTexture must be an instance of THREE.DepthTexture');}// upload an empty depth texture with framebuffer size
if(!properties.get(renderTarget.depthTexture).__webglTexture||renderTarget.depthTexture.image.width!==renderTarget.width||renderTarget.depthTexture.image.height!==renderTarget.height){renderTarget.depthTexture.image.width=renderTarget.width;renderTarget.depthTexture.image.height=renderTarget.height;renderTarget.depthTexture.needsUpdate=true;}setTexture2D(renderTarget.depthTexture,0);const webglDepthTexture=properties.get(renderTarget.depthTexture).__webglTexture;const samples=getRenderTargetSamples(renderTarget);if(renderTarget.depthTexture.format===DepthFormat$1){if(useMultisampledRTT(renderTarget)){multisampledRTTExt.framebufferTexture2DMultisampleEXT(_gl.FRAMEBUFFER,_gl.DEPTH_ATTACHMENT,_gl.TEXTURE_2D,webglDepthTexture,0,samples);}else {_gl.framebufferTexture2D(_gl.FRAMEBUFFER,_gl.DEPTH_ATTACHMENT,_gl.TEXTURE_2D,webglDepthTexture,0);}}else if(renderTarget.depthTexture.format===DepthStencilFormat$1){if(useMultisampledRTT(renderTarget)){multisampledRTTExt.framebufferTexture2DMultisampleEXT(_gl.FRAMEBUFFER,_gl.DEPTH_STENCIL_ATTACHMENT,_gl.TEXTURE_2D,webglDepthTexture,0,samples);}else {_gl.framebufferTexture2D(_gl.FRAMEBUFFER,_gl.DEPTH_STENCIL_ATTACHMENT,_gl.TEXTURE_2D,webglDepthTexture,0);}}else {throw new Error('Unknown depthTexture format');}}// Setup GL resources for a non-texture depth buffer
function setupDepthRenderbuffer(renderTarget){const renderTargetProperties=properties.get(renderTarget);const isCube=renderTarget.isWebGLCubeRenderTarget===true;// if the bound depth texture has changed
if(renderTargetProperties.__boundDepthTexture!==renderTarget.depthTexture){// fire the dispose event to get rid of stored state associated with the previously bound depth buffer
const depthTexture=renderTarget.depthTexture;if(renderTargetProperties.__depthDisposeCallback){renderTargetProperties.__depthDisposeCallback();}// set up dispose listeners to track when the currently attached buffer is implicitly unbound
if(depthTexture){const disposeEvent=()=>{delete renderTargetProperties.__boundDepthTexture;delete renderTargetProperties.__depthDisposeCallback;depthTexture.removeEventListener('dispose',disposeEvent);};depthTexture.addEventListener('dispose',disposeEvent);renderTargetProperties.__depthDisposeCallback=disposeEvent;}renderTargetProperties.__boundDepthTexture=depthTexture;}if(renderTarget.depthTexture&&!renderTargetProperties.__autoAllocateDepthBuffer){if(isCube)throw new Error('target.depthTexture not supported in Cube render targets');setupDepthTexture(renderTargetProperties.__webglFramebuffer,renderTarget);}else {if(isCube){renderTargetProperties.__webglDepthbuffer=[];for(let i=0;i<6;i++){state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglFramebuffer[i]);if(renderTargetProperties.__webglDepthbuffer[i]===undefined){renderTargetProperties.__webglDepthbuffer[i]=_gl.createRenderbuffer();setupRenderBufferStorage(renderTargetProperties.__webglDepthbuffer[i],renderTarget,false);}else {// attach buffer if it's been created already
const glAttachmentType=renderTarget.stencilBuffer?_gl.DEPTH_STENCIL_ATTACHMENT:_gl.DEPTH_ATTACHMENT;const renderbuffer=renderTargetProperties.__webglDepthbuffer[i];_gl.bindRenderbuffer(_gl.RENDERBUFFER,renderbuffer);_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,glAttachmentType,_gl.RENDERBUFFER,renderbuffer);}}}else {state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglFramebuffer);if(renderTargetProperties.__webglDepthbuffer===undefined){renderTargetProperties.__webglDepthbuffer=_gl.createRenderbuffer();setupRenderBufferStorage(renderTargetProperties.__webglDepthbuffer,renderTarget,false);}else {// attach buffer if it's been created already
const glAttachmentType=renderTarget.stencilBuffer?_gl.DEPTH_STENCIL_ATTACHMENT:_gl.DEPTH_ATTACHMENT;const renderbuffer=renderTargetProperties.__webglDepthbuffer;_gl.bindRenderbuffer(_gl.RENDERBUFFER,renderbuffer);_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,glAttachmentType,_gl.RENDERBUFFER,renderbuffer);}}}state.bindFramebuffer(_gl.FRAMEBUFFER,null);}// rebind framebuffer with external textures
function rebindTextures(renderTarget,colorTexture,depthTexture){const renderTargetProperties=properties.get(renderTarget);if(colorTexture!==undefined){setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer,renderTarget,renderTarget.texture,_gl.COLOR_ATTACHMENT0,_gl.TEXTURE_2D,0);}if(depthTexture!==undefined){setupDepthRenderbuffer(renderTarget);}}// Set up GL resources for the render target
function setupRenderTarget(renderTarget){const texture=renderTarget.texture;const renderTargetProperties=properties.get(renderTarget);const textureProperties=properties.get(texture);renderTarget.addEventListener('dispose',onRenderTargetDispose);const textures=renderTarget.textures;const isCube=renderTarget.isWebGLCubeRenderTarget===true;const isMultipleRenderTargets=textures.length>1;if(!isMultipleRenderTargets){if(textureProperties.__webglTexture===undefined){textureProperties.__webglTexture=_gl.createTexture();}textureProperties.__version=texture.version;info.memory.textures++;}// Setup framebuffer
if(isCube){renderTargetProperties.__webglFramebuffer=[];for(let i=0;i<6;i++){if(texture.mipmaps&&texture.mipmaps.length>0){renderTargetProperties.__webglFramebuffer[i]=[];for(let level=0;level<texture.mipmaps.length;level++){renderTargetProperties.__webglFramebuffer[i][level]=_gl.createFramebuffer();}}else {renderTargetProperties.__webglFramebuffer[i]=_gl.createFramebuffer();}}}else {if(texture.mipmaps&&texture.mipmaps.length>0){renderTargetProperties.__webglFramebuffer=[];for(let level=0;level<texture.mipmaps.length;level++){renderTargetProperties.__webglFramebuffer[level]=_gl.createFramebuffer();}}else {renderTargetProperties.__webglFramebuffer=_gl.createFramebuffer();}if(isMultipleRenderTargets){for(let i=0,il=textures.length;i<il;i++){const attachmentProperties=properties.get(textures[i]);if(attachmentProperties.__webglTexture===undefined){attachmentProperties.__webglTexture=_gl.createTexture();info.memory.textures++;}}}if(renderTarget.samples>0&&useMultisampledRTT(renderTarget)===false){renderTargetProperties.__webglMultisampledFramebuffer=_gl.createFramebuffer();renderTargetProperties.__webglColorRenderbuffer=[];state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglMultisampledFramebuffer);for(let i=0;i<textures.length;i++){const texture=textures[i];renderTargetProperties.__webglColorRenderbuffer[i]=_gl.createRenderbuffer();_gl.bindRenderbuffer(_gl.RENDERBUFFER,renderTargetProperties.__webglColorRenderbuffer[i]);const glFormat=utils.convert(texture.format,texture.colorSpace);const glType=utils.convert(texture.type);const glInternalFormat=getInternalFormat(texture.internalFormat,glFormat,glType,texture.colorSpace,renderTarget.isXRRenderTarget===true);const samples=getRenderTargetSamples(renderTarget);_gl.renderbufferStorageMultisample(_gl.RENDERBUFFER,samples,glInternalFormat,renderTarget.width,renderTarget.height);_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,_gl.COLOR_ATTACHMENT0+i,_gl.RENDERBUFFER,renderTargetProperties.__webglColorRenderbuffer[i]);}_gl.bindRenderbuffer(_gl.RENDERBUFFER,null);if(renderTarget.depthBuffer){renderTargetProperties.__webglDepthRenderbuffer=_gl.createRenderbuffer();setupRenderBufferStorage(renderTargetProperties.__webglDepthRenderbuffer,renderTarget,true);}state.bindFramebuffer(_gl.FRAMEBUFFER,null);}}// Setup color buffer
if(isCube){state.bindTexture(_gl.TEXTURE_CUBE_MAP,textureProperties.__webglTexture);setTextureParameters(_gl.TEXTURE_CUBE_MAP,texture);for(let i=0;i<6;i++){if(texture.mipmaps&&texture.mipmaps.length>0){for(let level=0;level<texture.mipmaps.length;level++){setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer[i][level],renderTarget,texture,_gl.COLOR_ATTACHMENT0,_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,level);}}else {setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer[i],renderTarget,texture,_gl.COLOR_ATTACHMENT0,_gl.TEXTURE_CUBE_MAP_POSITIVE_X+i,0);}}if(textureNeedsGenerateMipmaps(texture)){generateMipmap(_gl.TEXTURE_CUBE_MAP);}state.unbindTexture();}else if(isMultipleRenderTargets){for(let i=0,il=textures.length;i<il;i++){const attachment=textures[i];const attachmentProperties=properties.get(attachment);state.bindTexture(_gl.TEXTURE_2D,attachmentProperties.__webglTexture);setTextureParameters(_gl.TEXTURE_2D,attachment);setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer,renderTarget,attachment,_gl.COLOR_ATTACHMENT0+i,_gl.TEXTURE_2D,0);if(textureNeedsGenerateMipmaps(attachment)){generateMipmap(_gl.TEXTURE_2D);}}state.unbindTexture();}else {let glTextureType=_gl.TEXTURE_2D;if(renderTarget.isWebGL3DRenderTarget||renderTarget.isWebGLArrayRenderTarget){glTextureType=renderTarget.isWebGL3DRenderTarget?_gl.TEXTURE_3D:_gl.TEXTURE_2D_ARRAY;}state.bindTexture(glTextureType,textureProperties.__webglTexture);setTextureParameters(glTextureType,texture);if(texture.mipmaps&&texture.mipmaps.length>0){for(let level=0;level<texture.mipmaps.length;level++){setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer[level],renderTarget,texture,_gl.COLOR_ATTACHMENT0,glTextureType,level);}}else {setupFrameBufferTexture(renderTargetProperties.__webglFramebuffer,renderTarget,texture,_gl.COLOR_ATTACHMENT0,glTextureType,0);}if(textureNeedsGenerateMipmaps(texture)){generateMipmap(glTextureType);}state.unbindTexture();}// Setup depth and stencil buffers
if(renderTarget.depthBuffer){setupDepthRenderbuffer(renderTarget);}}function updateRenderTargetMipmap(renderTarget){const textures=renderTarget.textures;for(let i=0,il=textures.length;i<il;i++){const texture=textures[i];if(textureNeedsGenerateMipmaps(texture)){const target=renderTarget.isWebGLCubeRenderTarget?_gl.TEXTURE_CUBE_MAP:_gl.TEXTURE_2D;const webglTexture=properties.get(texture).__webglTexture;state.bindTexture(target,webglTexture);generateMipmap(target);state.unbindTexture();}}}const invalidationArrayRead=[];const invalidationArrayDraw=[];function updateMultisampleRenderTarget(renderTarget){if(renderTarget.samples>0){if(useMultisampledRTT(renderTarget)===false){const textures=renderTarget.textures;const width=renderTarget.width;const height=renderTarget.height;let mask=_gl.COLOR_BUFFER_BIT;const depthStyle=renderTarget.stencilBuffer?_gl.DEPTH_STENCIL_ATTACHMENT:_gl.DEPTH_ATTACHMENT;const renderTargetProperties=properties.get(renderTarget);const isMultipleRenderTargets=textures.length>1;// If MRT we need to remove FBO attachments
if(isMultipleRenderTargets){for(let i=0;i<textures.length;i++){state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglMultisampledFramebuffer);_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,_gl.COLOR_ATTACHMENT0+i,_gl.RENDERBUFFER,null);state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglFramebuffer);_gl.framebufferTexture2D(_gl.DRAW_FRAMEBUFFER,_gl.COLOR_ATTACHMENT0+i,_gl.TEXTURE_2D,null,0);}}state.bindFramebuffer(_gl.READ_FRAMEBUFFER,renderTargetProperties.__webglMultisampledFramebuffer);state.bindFramebuffer(_gl.DRAW_FRAMEBUFFER,renderTargetProperties.__webglFramebuffer);for(let i=0;i<textures.length;i++){if(renderTarget.resolveDepthBuffer){if(renderTarget.depthBuffer)mask|=_gl.DEPTH_BUFFER_BIT;// resolving stencil is slow with a D3D backend. disable it for all transmission render targets (see #27799)
if(renderTarget.stencilBuffer&&renderTarget.resolveStencilBuffer)mask|=_gl.STENCIL_BUFFER_BIT;}if(isMultipleRenderTargets){_gl.framebufferRenderbuffer(_gl.READ_FRAMEBUFFER,_gl.COLOR_ATTACHMENT0,_gl.RENDERBUFFER,renderTargetProperties.__webglColorRenderbuffer[i]);const webglTexture=properties.get(textures[i]).__webglTexture;_gl.framebufferTexture2D(_gl.DRAW_FRAMEBUFFER,_gl.COLOR_ATTACHMENT0,_gl.TEXTURE_2D,webglTexture,0);}_gl.blitFramebuffer(0,0,width,height,0,0,width,height,mask,_gl.NEAREST);if(supportsInvalidateFramebuffer===true){invalidationArrayRead.length=0;invalidationArrayDraw.length=0;invalidationArrayRead.push(_gl.COLOR_ATTACHMENT0+i);if(renderTarget.depthBuffer&&renderTarget.resolveDepthBuffer===false){invalidationArrayRead.push(depthStyle);invalidationArrayDraw.push(depthStyle);_gl.invalidateFramebuffer(_gl.DRAW_FRAMEBUFFER,invalidationArrayDraw);}_gl.invalidateFramebuffer(_gl.READ_FRAMEBUFFER,invalidationArrayRead);}}state.bindFramebuffer(_gl.READ_FRAMEBUFFER,null);state.bindFramebuffer(_gl.DRAW_FRAMEBUFFER,null);// If MRT since pre-blit we removed the FBO we need to reconstruct the attachments
if(isMultipleRenderTargets){for(let i=0;i<textures.length;i++){state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglMultisampledFramebuffer);_gl.framebufferRenderbuffer(_gl.FRAMEBUFFER,_gl.COLOR_ATTACHMENT0+i,_gl.RENDERBUFFER,renderTargetProperties.__webglColorRenderbuffer[i]);const webglTexture=properties.get(textures[i]).__webglTexture;state.bindFramebuffer(_gl.FRAMEBUFFER,renderTargetProperties.__webglFramebuffer);_gl.framebufferTexture2D(_gl.DRAW_FRAMEBUFFER,_gl.COLOR_ATTACHMENT0+i,_gl.TEXTURE_2D,webglTexture,0);}}state.bindFramebuffer(_gl.DRAW_FRAMEBUFFER,renderTargetProperties.__webglMultisampledFramebuffer);}else {if(renderTarget.depthBuffer&&renderTarget.resolveDepthBuffer===false&&supportsInvalidateFramebuffer){const depthStyle=renderTarget.stencilBuffer?_gl.DEPTH_STENCIL_ATTACHMENT:_gl.DEPTH_ATTACHMENT;_gl.invalidateFramebuffer(_gl.DRAW_FRAMEBUFFER,[depthStyle]);}}}}function getRenderTargetSamples(renderTarget){return Math.min(capabilities.maxSamples,renderTarget.samples);}function useMultisampledRTT(renderTarget){const renderTargetProperties=properties.get(renderTarget);return renderTarget.samples>0&&extensions.has('WEBGL_multisampled_render_to_texture')===true&&renderTargetProperties.__useRenderToTexture!==false;}function updateVideoTexture(texture){const frame=info.render.frame;// Check the last frame we updated the VideoTexture
if(_videoTextures.get(texture)!==frame){_videoTextures.set(texture,frame);texture.update();}}function verifyColorSpace(texture,image){const colorSpace=texture.colorSpace;const format=texture.format;const type=texture.type;if(texture.isCompressedTexture===true||texture.isVideoTexture===true)return image;if(colorSpace!==LinearSRGBColorSpace$1&&colorSpace!==NoColorSpace$1){// sRGB
if(ColorManagement$1.getTransfer(colorSpace)===SRGBTransfer$1){// in WebGL 2 uncompressed textures can only be sRGB encoded if they have the RGBA8 format
if(format!==RGBAFormat$1||type!==UnsignedByteType$1){console.warn('THREE.WebGLTextures: sRGB encoded textures have to use RGBAFormat and UnsignedByteType.');}}else {console.error('THREE.WebGLTextures: Unsupported texture color space:',colorSpace);}}return image;}function getDimensions(image){if(typeof HTMLImageElement!=='undefined'&&image instanceof HTMLImageElement){// if intrinsic data are not available, fallback to width/height
_imageDimensions.width=image.naturalWidth||image.width;_imageDimensions.height=image.naturalHeight||image.height;}else if(typeof VideoFrame!=='undefined'&&image instanceof VideoFrame){_imageDimensions.width=image.displayWidth;_imageDimensions.height=image.displayHeight;}else {_imageDimensions.width=image.width;_imageDimensions.height=image.height;}return _imageDimensions;}//
this.allocateTextureUnit=allocateTextureUnit;this.resetTextureUnits=resetTextureUnits;this.setTexture2D=setTexture2D;this.setTexture2DArray=setTexture2DArray;this.setTexture3D=setTexture3D;this.setTextureCube=setTextureCube;this.rebindTextures=rebindTextures;this.setupRenderTarget=setupRenderTarget;this.updateRenderTargetMipmap=updateRenderTargetMipmap;this.updateMultisampleRenderTarget=updateMultisampleRenderTarget;this.setupDepthRenderbuffer=setupDepthRenderbuffer;this.setupFrameBufferTexture=setupFrameBufferTexture;this.useMultisampledRTT=useMultisampledRTT;}function WebGLUtils(gl,extensions){function convert(p,colorSpace=NoColorSpace$1){let extension;const transfer=ColorManagement$1.getTransfer(colorSpace);if(p===UnsignedByteType$1)return gl.UNSIGNED_BYTE;if(p===UnsignedShort4444Type)return gl.UNSIGNED_SHORT_4_4_4_4;if(p===UnsignedShort5551Type)return gl.UNSIGNED_SHORT_5_5_5_1;if(p===UnsignedInt5999Type)return gl.UNSIGNED_INT_5_9_9_9_REV;if(p===ByteType)return gl.BYTE;if(p===ShortType)return gl.SHORT;if(p===UnsignedShortType)return gl.UNSIGNED_SHORT;if(p===IntType$1)return gl.INT;if(p===UnsignedIntType$1)return gl.UNSIGNED_INT;if(p===FloatType$1)return gl.FLOAT;if(p===HalfFloatType$1)return gl.HALF_FLOAT;if(p===AlphaFormat)return gl.ALPHA;if(p===RGBFormat)return gl.RGB;if(p===RGBAFormat$1)return gl.RGBA;if(p===LuminanceFormat)return gl.LUMINANCE;if(p===LuminanceAlphaFormat)return gl.LUMINANCE_ALPHA;if(p===DepthFormat$1)return gl.DEPTH_COMPONENT;if(p===DepthStencilFormat$1)return gl.DEPTH_STENCIL;// WebGL2 formats.
if(p===RedFormat)return gl.RED;if(p===RedIntegerFormat)return gl.RED_INTEGER;if(p===RGFormat)return gl.RG;if(p===RGIntegerFormat)return gl.RG_INTEGER;if(p===RGBAIntegerFormat)return gl.RGBA_INTEGER;// S3TC
if(p===RGB_S3TC_DXT1_Format||p===RGBA_S3TC_DXT1_Format||p===RGBA_S3TC_DXT3_Format||p===RGBA_S3TC_DXT5_Format){if(transfer===SRGBTransfer$1){extension=extensions.get('WEBGL_compressed_texture_s3tc_srgb');if(extension!==null){if(p===RGB_S3TC_DXT1_Format)return extension.COMPRESSED_SRGB_S3TC_DXT1_EXT;if(p===RGBA_S3TC_DXT1_Format)return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT1_EXT;if(p===RGBA_S3TC_DXT3_Format)return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT3_EXT;if(p===RGBA_S3TC_DXT5_Format)return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT5_EXT;}else {return null;}}else {extension=extensions.get('WEBGL_compressed_texture_s3tc');if(extension!==null){if(p===RGB_S3TC_DXT1_Format)return extension.COMPRESSED_RGB_S3TC_DXT1_EXT;if(p===RGBA_S3TC_DXT1_Format)return extension.COMPRESSED_RGBA_S3TC_DXT1_EXT;if(p===RGBA_S3TC_DXT3_Format)return extension.COMPRESSED_RGBA_S3TC_DXT3_EXT;if(p===RGBA_S3TC_DXT5_Format)return extension.COMPRESSED_RGBA_S3TC_DXT5_EXT;}else {return null;}}}// PVRTC
if(p===RGB_PVRTC_4BPPV1_Format||p===RGB_PVRTC_2BPPV1_Format||p===RGBA_PVRTC_4BPPV1_Format||p===RGBA_PVRTC_2BPPV1_Format){extension=extensions.get('WEBGL_compressed_texture_pvrtc');if(extension!==null){if(p===RGB_PVRTC_4BPPV1_Format)return extension.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;if(p===RGB_PVRTC_2BPPV1_Format)return extension.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;if(p===RGBA_PVRTC_4BPPV1_Format)return extension.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG;if(p===RGBA_PVRTC_2BPPV1_Format)return extension.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG;}else {return null;}}// ETC
if(p===RGB_ETC1_Format||p===RGB_ETC2_Format||p===RGBA_ETC2_EAC_Format){extension=extensions.get('WEBGL_compressed_texture_etc');if(extension!==null){if(p===RGB_ETC1_Format||p===RGB_ETC2_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ETC2:extension.COMPRESSED_RGB8_ETC2;if(p===RGBA_ETC2_EAC_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ETC2_EAC:extension.COMPRESSED_RGBA8_ETC2_EAC;}else {return null;}}// ASTC
if(p===RGBA_ASTC_4x4_Format||p===RGBA_ASTC_5x4_Format||p===RGBA_ASTC_5x5_Format||p===RGBA_ASTC_6x5_Format||p===RGBA_ASTC_6x6_Format||p===RGBA_ASTC_8x5_Format||p===RGBA_ASTC_8x6_Format||p===RGBA_ASTC_8x8_Format||p===RGBA_ASTC_10x5_Format||p===RGBA_ASTC_10x6_Format||p===RGBA_ASTC_10x8_Format||p===RGBA_ASTC_10x10_Format||p===RGBA_ASTC_12x10_Format||p===RGBA_ASTC_12x12_Format){extension=extensions.get('WEBGL_compressed_texture_astc');if(extension!==null){if(p===RGBA_ASTC_4x4_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_4x4_KHR:extension.COMPRESSED_RGBA_ASTC_4x4_KHR;if(p===RGBA_ASTC_5x4_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_5x4_KHR:extension.COMPRESSED_RGBA_ASTC_5x4_KHR;if(p===RGBA_ASTC_5x5_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_5x5_KHR:extension.COMPRESSED_RGBA_ASTC_5x5_KHR;if(p===RGBA_ASTC_6x5_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_6x5_KHR:extension.COMPRESSED_RGBA_ASTC_6x5_KHR;if(p===RGBA_ASTC_6x6_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_6x6_KHR:extension.COMPRESSED_RGBA_ASTC_6x6_KHR;if(p===RGBA_ASTC_8x5_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x5_KHR:extension.COMPRESSED_RGBA_ASTC_8x5_KHR;if(p===RGBA_ASTC_8x6_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x6_KHR:extension.COMPRESSED_RGBA_ASTC_8x6_KHR;if(p===RGBA_ASTC_8x8_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x8_KHR:extension.COMPRESSED_RGBA_ASTC_8x8_KHR;if(p===RGBA_ASTC_10x5_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x5_KHR:extension.COMPRESSED_RGBA_ASTC_10x5_KHR;if(p===RGBA_ASTC_10x6_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x6_KHR:extension.COMPRESSED_RGBA_ASTC_10x6_KHR;if(p===RGBA_ASTC_10x8_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x8_KHR:extension.COMPRESSED_RGBA_ASTC_10x8_KHR;if(p===RGBA_ASTC_10x10_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x10_KHR:extension.COMPRESSED_RGBA_ASTC_10x10_KHR;if(p===RGBA_ASTC_12x10_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_12x10_KHR:extension.COMPRESSED_RGBA_ASTC_12x10_KHR;if(p===RGBA_ASTC_12x12_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB8_ALPHA8_ASTC_12x12_KHR:extension.COMPRESSED_RGBA_ASTC_12x12_KHR;}else {return null;}}// BPTC
if(p===RGBA_BPTC_Format||p===RGB_BPTC_SIGNED_Format||p===RGB_BPTC_UNSIGNED_Format){extension=extensions.get('EXT_texture_compression_bptc');if(extension!==null){if(p===RGBA_BPTC_Format)return transfer===SRGBTransfer$1?extension.COMPRESSED_SRGB_ALPHA_BPTC_UNORM_EXT:extension.COMPRESSED_RGBA_BPTC_UNORM_EXT;if(p===RGB_BPTC_SIGNED_Format)return extension.COMPRESSED_RGB_BPTC_SIGNED_FLOAT_EXT;if(p===RGB_BPTC_UNSIGNED_Format)return extension.COMPRESSED_RGB_BPTC_UNSIGNED_FLOAT_EXT;}else {return null;}}// RGTC
if(p===RED_RGTC1_Format||p===SIGNED_RED_RGTC1_Format||p===RED_GREEN_RGTC2_Format||p===SIGNED_RED_GREEN_RGTC2_Format){extension=extensions.get('EXT_texture_compression_rgtc');if(extension!==null){if(p===RGBA_BPTC_Format)return extension.COMPRESSED_RED_RGTC1_EXT;if(p===SIGNED_RED_RGTC1_Format)return extension.COMPRESSED_SIGNED_RED_RGTC1_EXT;if(p===RED_GREEN_RGTC2_Format)return extension.COMPRESSED_RED_GREEN_RGTC2_EXT;if(p===SIGNED_RED_GREEN_RGTC2_Format)return extension.COMPRESSED_SIGNED_RED_GREEN_RGTC2_EXT;}else {return null;}}//
if(p===UnsignedInt248Type$1)return gl.UNSIGNED_INT_24_8;// if "p" can't be resolved, assume the user defines a WebGL constant as a string (fallback/workaround for packed RGB formats)
return gl[p]!==undefined?gl[p]:null;}return {convert:convert};}class ArrayCamera extends PerspectiveCamera$1{constructor(array=[]){super();this.isArrayCamera=true;this.cameras=array;}}class Group extends Object3D$1{constructor(){super();this.isGroup=true;this.type='Group';}}const _moveEvent={type:'move'};class WebXRController{constructor(){this._targetRay=null;this._grip=null;this._hand=null;}getHandSpace(){if(this._hand===null){this._hand=new Group();this._hand.matrixAutoUpdate=false;this._hand.visible=false;this._hand.joints={};this._hand.inputState={pinching:false};}return this._hand;}getTargetRaySpace(){if(this._targetRay===null){this._targetRay=new Group();this._targetRay.matrixAutoUpdate=false;this._targetRay.visible=false;this._targetRay.hasLinearVelocity=false;this._targetRay.linearVelocity=new Vector3$1();this._targetRay.hasAngularVelocity=false;this._targetRay.angularVelocity=new Vector3$1();}return this._targetRay;}getGripSpace(){if(this._grip===null){this._grip=new Group();this._grip.matrixAutoUpdate=false;this._grip.visible=false;this._grip.hasLinearVelocity=false;this._grip.linearVelocity=new Vector3$1();this._grip.hasAngularVelocity=false;this._grip.angularVelocity=new Vector3$1();}return this._grip;}dispatchEvent(event){if(this._targetRay!==null){this._targetRay.dispatchEvent(event);}if(this._grip!==null){this._grip.dispatchEvent(event);}if(this._hand!==null){this._hand.dispatchEvent(event);}return this;}connect(inputSource){if(inputSource&&inputSource.hand){const hand=this._hand;if(hand){for(const inputjoint of inputSource.hand.values()){// Initialize hand with joints when connected
this._getHandJoint(hand,inputjoint);}}}this.dispatchEvent({type:'connected',data:inputSource});return this;}disconnect(inputSource){this.dispatchEvent({type:'disconnected',data:inputSource});if(this._targetRay!==null){this._targetRay.visible=false;}if(this._grip!==null){this._grip.visible=false;}if(this._hand!==null){this._hand.visible=false;}return this;}update(inputSource,frame,referenceSpace){let inputPose=null;let gripPose=null;let handPose=null;const targetRay=this._targetRay;const grip=this._grip;const hand=this._hand;if(inputSource&&frame.session.visibilityState!=='visible-blurred'){if(hand&&inputSource.hand){handPose=true;for(const inputjoint of inputSource.hand.values()){// Update the joints groups with the XRJoint poses
const jointPose=frame.getJointPose(inputjoint,referenceSpace);// The transform of this joint will be updated with the joint pose on each frame
const joint=this._getHandJoint(hand,inputjoint);if(jointPose!==null){joint.matrix.fromArray(jointPose.transform.matrix);joint.matrix.decompose(joint.position,joint.rotation,joint.scale);joint.matrixWorldNeedsUpdate=true;joint.jointRadius=jointPose.radius;}joint.visible=jointPose!==null;}// Custom events
// Check pinchz
const indexTip=hand.joints['index-finger-tip'];const thumbTip=hand.joints['thumb-tip'];const distance=indexTip.position.distanceTo(thumbTip.position);const distanceToPinch=0.02;const threshold=0.005;if(hand.inputState.pinching&&distance>distanceToPinch+threshold){hand.inputState.pinching=false;this.dispatchEvent({type:'pinchend',handedness:inputSource.handedness,target:this});}else if(!hand.inputState.pinching&&distance<=distanceToPinch-threshold){hand.inputState.pinching=true;this.dispatchEvent({type:'pinchstart',handedness:inputSource.handedness,target:this});}}else {if(grip!==null&&inputSource.gripSpace){gripPose=frame.getPose(inputSource.gripSpace,referenceSpace);if(gripPose!==null){grip.matrix.fromArray(gripPose.transform.matrix);grip.matrix.decompose(grip.position,grip.rotation,grip.scale);grip.matrixWorldNeedsUpdate=true;if(gripPose.linearVelocity){grip.hasLinearVelocity=true;grip.linearVelocity.copy(gripPose.linearVelocity);}else {grip.hasLinearVelocity=false;}if(gripPose.angularVelocity){grip.hasAngularVelocity=true;grip.angularVelocity.copy(gripPose.angularVelocity);}else {grip.hasAngularVelocity=false;}}}}if(targetRay!==null){inputPose=frame.getPose(inputSource.targetRaySpace,referenceSpace);// Some runtimes (namely Vive Cosmos with Vive OpenXR Runtime) have only grip space and ray space is equal to it
if(inputPose===null&&gripPose!==null){inputPose=gripPose;}if(inputPose!==null){targetRay.matrix.fromArray(inputPose.transform.matrix);targetRay.matrix.decompose(targetRay.position,targetRay.rotation,targetRay.scale);targetRay.matrixWorldNeedsUpdate=true;if(inputPose.linearVelocity){targetRay.hasLinearVelocity=true;targetRay.linearVelocity.copy(inputPose.linearVelocity);}else {targetRay.hasLinearVelocity=false;}if(inputPose.angularVelocity){targetRay.hasAngularVelocity=true;targetRay.angularVelocity.copy(inputPose.angularVelocity);}else {targetRay.hasAngularVelocity=false;}this.dispatchEvent(_moveEvent);}}}if(targetRay!==null){targetRay.visible=inputPose!==null;}if(grip!==null){grip.visible=gripPose!==null;}if(hand!==null){hand.visible=handPose!==null;}return this;}// private method
_getHandJoint(hand,inputjoint){if(hand.joints[inputjoint.jointName]===undefined){const joint=new Group();joint.matrixAutoUpdate=false;joint.visible=false;hand.joints[inputjoint.jointName]=joint;hand.add(joint);}return hand.joints[inputjoint.jointName];}}const _occlusion_vertex=`
void main() {

	gl_Position = vec4( position, 1.0 );

}`;const _occlusion_fragment=`
uniform sampler2DArray depthColor;
uniform float depthWidth;
uniform float depthHeight;

void main() {

	vec2 coord = vec2( gl_FragCoord.x / depthWidth, gl_FragCoord.y / depthHeight );

	if ( coord.x >= 1.0 ) {

		gl_FragDepth = texture( depthColor, vec3( coord.x - 1.0, coord.y, 1 ) ).r;

	} else {

		gl_FragDepth = texture( depthColor, vec3( coord.x, coord.y, 0 ) ).r;

	}

}`;class WebXRDepthSensing{constructor(){this.texture=null;this.mesh=null;this.depthNear=0;this.depthFar=0;}init(renderer,depthData,renderState){if(this.texture===null){const texture=new Texture$1();const texProps=renderer.properties.get(texture);texProps.__webglTexture=depthData.texture;if(depthData.depthNear!=renderState.depthNear||depthData.depthFar!=renderState.depthFar){this.depthNear=depthData.depthNear;this.depthFar=depthData.depthFar;}this.texture=texture;}}getMesh(cameraXR){if(this.texture!==null){if(this.mesh===null){const viewport=cameraXR.cameras[0].viewport;const material=new ShaderMaterial({vertexShader:_occlusion_vertex,fragmentShader:_occlusion_fragment,uniforms:{depthColor:{value:this.texture},depthWidth:{value:viewport.z},depthHeight:{value:viewport.w}}});this.mesh=new Mesh$1(new PlaneGeometry(20,20),material);}}return this.mesh;}reset(){this.texture=null;this.mesh=null;}getDepthTexture(){return this.texture;}}class WebXRManager extends EventDispatcher$1{constructor(renderer,gl){super();const scope=this;let session=null;let framebufferScaleFactor=1.0;let referenceSpace=null;let referenceSpaceType='local-floor';// Set default foveation to maximum.
let foveation=1.0;let customReferenceSpace=null;let pose=null;let glBinding=null;let glProjLayer=null;let glBaseLayer=null;let xrFrame=null;const depthSensing=new WebXRDepthSensing();const attributes=gl.getContextAttributes();let initialRenderTarget=null;let newRenderTarget=null;const controllers=[];const controllerInputSources=[];const currentSize=new Vector2$1();let currentPixelRatio=null;//
const cameraL=new PerspectiveCamera$1();cameraL.layers.enable(1);cameraL.viewport=new Vector4$1();const cameraR=new PerspectiveCamera$1();cameraR.layers.enable(2);cameraR.viewport=new Vector4$1();const cameras=[cameraL,cameraR];const cameraXR=new ArrayCamera();cameraXR.layers.enable(1);cameraXR.layers.enable(2);let _currentDepthNear=null;let _currentDepthFar=null;//
this.cameraAutoUpdate=true;this.enabled=false;this.isPresenting=false;this.getController=function(index){let controller=controllers[index];if(controller===undefined){controller=new WebXRController();controllers[index]=controller;}return controller.getTargetRaySpace();};this.getControllerGrip=function(index){let controller=controllers[index];if(controller===undefined){controller=new WebXRController();controllers[index]=controller;}return controller.getGripSpace();};this.getHand=function(index){let controller=controllers[index];if(controller===undefined){controller=new WebXRController();controllers[index]=controller;}return controller.getHandSpace();};//
function onSessionEvent(event){const controllerIndex=controllerInputSources.indexOf(event.inputSource);if(controllerIndex===-1){return;}const controller=controllers[controllerIndex];if(controller!==undefined){controller.update(event.inputSource,event.frame,customReferenceSpace||referenceSpace);controller.dispatchEvent({type:event.type,data:event.inputSource});}}function onSessionEnd(){session.removeEventListener('select',onSessionEvent);session.removeEventListener('selectstart',onSessionEvent);session.removeEventListener('selectend',onSessionEvent);session.removeEventListener('squeeze',onSessionEvent);session.removeEventListener('squeezestart',onSessionEvent);session.removeEventListener('squeezeend',onSessionEvent);session.removeEventListener('end',onSessionEnd);session.removeEventListener('inputsourceschange',onInputSourcesChange);for(let i=0;i<controllers.length;i++){const inputSource=controllerInputSources[i];if(inputSource===null)continue;controllerInputSources[i]=null;controllers[i].disconnect(inputSource);}_currentDepthNear=null;_currentDepthFar=null;depthSensing.reset();// restore framebuffer/rendering state
renderer.setRenderTarget(initialRenderTarget);glBaseLayer=null;glProjLayer=null;glBinding=null;session=null;newRenderTarget=null;//
animation.stop();scope.isPresenting=false;renderer.setPixelRatio(currentPixelRatio);renderer.setSize(currentSize.width,currentSize.height,false);scope.dispatchEvent({type:'sessionend'});}this.setFramebufferScaleFactor=function(value){framebufferScaleFactor=value;if(scope.isPresenting===true){console.warn('THREE.WebXRManager: Cannot change framebuffer scale while presenting.');}};this.setReferenceSpaceType=function(value){referenceSpaceType=value;if(scope.isPresenting===true){console.warn('THREE.WebXRManager: Cannot change reference space type while presenting.');}};this.getReferenceSpace=function(){return customReferenceSpace||referenceSpace;};this.setReferenceSpace=function(space){customReferenceSpace=space;};this.getBaseLayer=function(){return glProjLayer!==null?glProjLayer:glBaseLayer;};this.getBinding=function(){return glBinding;};this.getFrame=function(){return xrFrame;};this.getSession=function(){return session;};this.setSession=async function(value){session=value;if(session!==null){initialRenderTarget=renderer.getRenderTarget();session.addEventListener('select',onSessionEvent);session.addEventListener('selectstart',onSessionEvent);session.addEventListener('selectend',onSessionEvent);session.addEventListener('squeeze',onSessionEvent);session.addEventListener('squeezestart',onSessionEvent);session.addEventListener('squeezeend',onSessionEvent);session.addEventListener('end',onSessionEnd);session.addEventListener('inputsourceschange',onInputSourcesChange);if(attributes.xrCompatible!==true){await gl.makeXRCompatible();}currentPixelRatio=renderer.getPixelRatio();renderer.getSize(currentSize);if(session.renderState.layers===undefined){const layerInit={antialias:attributes.antialias,alpha:true,depth:attributes.depth,stencil:attributes.stencil,framebufferScaleFactor:framebufferScaleFactor};glBaseLayer=new XRWebGLLayer(session,gl,layerInit);session.updateRenderState({baseLayer:glBaseLayer});renderer.setPixelRatio(1);renderer.setSize(glBaseLayer.framebufferWidth,glBaseLayer.framebufferHeight,false);newRenderTarget=new WebGLRenderTarget(glBaseLayer.framebufferWidth,glBaseLayer.framebufferHeight,{format:RGBAFormat$1,type:UnsignedByteType$1,colorSpace:renderer.outputColorSpace,stencilBuffer:attributes.stencil});}else {let depthFormat=null;let depthType=null;let glDepthFormat=null;if(attributes.depth){glDepthFormat=attributes.stencil?gl.DEPTH24_STENCIL8:gl.DEPTH_COMPONENT24;depthFormat=attributes.stencil?DepthStencilFormat$1:DepthFormat$1;depthType=attributes.stencil?UnsignedInt248Type$1:UnsignedIntType$1;}const projectionlayerInit={colorFormat:gl.RGBA8,depthFormat:glDepthFormat,scaleFactor:framebufferScaleFactor};glBinding=new XRWebGLBinding(session,gl);glProjLayer=glBinding.createProjectionLayer(projectionlayerInit);session.updateRenderState({layers:[glProjLayer]});renderer.setPixelRatio(1);renderer.setSize(glProjLayer.textureWidth,glProjLayer.textureHeight,false);newRenderTarget=new WebGLRenderTarget(glProjLayer.textureWidth,glProjLayer.textureHeight,{format:RGBAFormat$1,type:UnsignedByteType$1,depthTexture:new DepthTexture$1(glProjLayer.textureWidth,glProjLayer.textureHeight,depthType,undefined,undefined,undefined,undefined,undefined,undefined,depthFormat),stencilBuffer:attributes.stencil,colorSpace:renderer.outputColorSpace,samples:attributes.antialias?4:0,resolveDepthBuffer:glProjLayer.ignoreDepthValues===false});}newRenderTarget.isXRRenderTarget=true;// TODO Remove this when possible, see #23278
this.setFoveation(foveation);customReferenceSpace=null;referenceSpace=await session.requestReferenceSpace(referenceSpaceType);animation.setContext(session);animation.start();scope.isPresenting=true;scope.dispatchEvent({type:'sessionstart'});}};this.getEnvironmentBlendMode=function(){if(session!==null){return session.environmentBlendMode;}};this.getDepthTexture=function(){return depthSensing.getDepthTexture();};function onInputSourcesChange(event){// Notify disconnected
for(let i=0;i<event.removed.length;i++){const inputSource=event.removed[i];const index=controllerInputSources.indexOf(inputSource);if(index>=0){controllerInputSources[index]=null;controllers[index].disconnect(inputSource);}}// Notify connected
for(let i=0;i<event.added.length;i++){const inputSource=event.added[i];let controllerIndex=controllerInputSources.indexOf(inputSource);if(controllerIndex===-1){// Assign input source a controller that currently has no input source
for(let i=0;i<controllers.length;i++){if(i>=controllerInputSources.length){controllerInputSources.push(inputSource);controllerIndex=i;break;}else if(controllerInputSources[i]===null){controllerInputSources[i]=inputSource;controllerIndex=i;break;}}// If all controllers do currently receive input we ignore new ones
if(controllerIndex===-1)break;}const controller=controllers[controllerIndex];if(controller){controller.connect(inputSource);}}}//
const cameraLPos=new Vector3$1();const cameraRPos=new Vector3$1();/**
		 * Assumes 2 cameras that are parallel and share an X-axis, and that
		 * the cameras' projection and world matrices have already been set.
		 * And that near and far planes are identical for both cameras.
		 * Visualization of this technique: https://computergraphics.stackexchange.com/a/4765
		 */function setProjectionFromUnion(camera,cameraL,cameraR){cameraLPos.setFromMatrixPosition(cameraL.matrixWorld);cameraRPos.setFromMatrixPosition(cameraR.matrixWorld);const ipd=cameraLPos.distanceTo(cameraRPos);const projL=cameraL.projectionMatrix.elements;const projR=cameraR.projectionMatrix.elements;// VR systems will have identical far and near planes, and
// most likely identical top and bottom frustum extents.
// Use the left camera for these values.
const near=projL[14]/(projL[10]-1);const far=projL[14]/(projL[10]+1);const topFov=(projL[9]+1)/projL[5];const bottomFov=(projL[9]-1)/projL[5];const leftFov=(projL[8]-1)/projL[0];const rightFov=(projR[8]+1)/projR[0];const left=near*leftFov;const right=near*rightFov;// Calculate the new camera's position offset from the
// left camera. xOffset should be roughly half `ipd`.
const zOffset=ipd/(-leftFov+rightFov);const xOffset=zOffset*-leftFov;// TODO: Better way to apply this offset?
cameraL.matrixWorld.decompose(camera.position,camera.quaternion,camera.scale);camera.translateX(xOffset);camera.translateZ(zOffset);camera.matrixWorld.compose(camera.position,camera.quaternion,camera.scale);camera.matrixWorldInverse.copy(camera.matrixWorld).invert();// Check if the projection uses an infinite far plane.
if(projL[10]===-1.0){// Use the projection matrix from the left eye.
// The camera offset is sufficient to include the view volumes
// of both eyes (assuming symmetric projections).
camera.projectionMatrix.copy(cameraL.projectionMatrix);camera.projectionMatrixInverse.copy(cameraL.projectionMatrixInverse);}else {// Find the union of the frustum values of the cameras and scale
// the values so that the near plane's position does not change in world space,
// although must now be relative to the new union camera.
const near2=near+zOffset;const far2=far+zOffset;const left2=left-xOffset;const right2=right+(ipd-xOffset);const top2=topFov*far/far2*near2;const bottom2=bottomFov*far/far2*near2;camera.projectionMatrix.makePerspective(left2,right2,top2,bottom2,near2,far2);camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();}}function updateCamera(camera,parent){if(parent===null){camera.matrixWorld.copy(camera.matrix);}else {camera.matrixWorld.multiplyMatrices(parent.matrixWorld,camera.matrix);}camera.matrixWorldInverse.copy(camera.matrixWorld).invert();}this.updateCamera=function(camera){if(session===null)return;let depthNear=camera.near;let depthFar=camera.far;if(depthSensing.texture!==null){if(depthSensing.depthNear>0)depthNear=depthSensing.depthNear;if(depthSensing.depthFar>0)depthFar=depthSensing.depthFar;}cameraXR.near=cameraR.near=cameraL.near=depthNear;cameraXR.far=cameraR.far=cameraL.far=depthFar;if(_currentDepthNear!==cameraXR.near||_currentDepthFar!==cameraXR.far){// Note that the new renderState won't apply until the next frame. See #18320
session.updateRenderState({depthNear:cameraXR.near,depthFar:cameraXR.far});_currentDepthNear=cameraXR.near;_currentDepthFar=cameraXR.far;}const parent=camera.parent;const cameras=cameraXR.cameras;updateCamera(cameraXR,parent);for(let i=0;i<cameras.length;i++){updateCamera(cameras[i],parent);}// update projection matrix for proper view frustum culling
if(cameras.length===2){setProjectionFromUnion(cameraXR,cameraL,cameraR);}else {// assume single camera setup (AR)
cameraXR.projectionMatrix.copy(cameraL.projectionMatrix);}// update user camera and its children
updateUserCamera(camera,cameraXR,parent);};function updateUserCamera(camera,cameraXR,parent){if(parent===null){camera.matrix.copy(cameraXR.matrixWorld);}else {camera.matrix.copy(parent.matrixWorld);camera.matrix.invert();camera.matrix.multiply(cameraXR.matrixWorld);}camera.matrix.decompose(camera.position,camera.quaternion,camera.scale);camera.updateMatrixWorld(true);camera.projectionMatrix.copy(cameraXR.projectionMatrix);camera.projectionMatrixInverse.copy(cameraXR.projectionMatrixInverse);if(camera.isPerspectiveCamera){camera.fov=RAD2DEG$1*2*Math.atan(1/camera.projectionMatrix.elements[5]);camera.zoom=1;}}this.getCamera=function(){return cameraXR;};this.getFoveation=function(){if(glProjLayer===null&&glBaseLayer===null){return undefined;}return foveation;};this.setFoveation=function(value){// 0 = no foveation = full resolution
// 1 = maximum foveation = the edges render at lower resolution
foveation=value;if(glProjLayer!==null){glProjLayer.fixedFoveation=value;}if(glBaseLayer!==null&&glBaseLayer.fixedFoveation!==undefined){glBaseLayer.fixedFoveation=value;}};this.hasDepthSensing=function(){return depthSensing.texture!==null;};this.getDepthSensingMesh=function(){return depthSensing.getMesh(cameraXR);};// Animation Loop
let onAnimationFrameCallback=null;function onAnimationFrame(time,frame){pose=frame.getViewerPose(customReferenceSpace||referenceSpace);xrFrame=frame;if(pose!==null){const views=pose.views;if(glBaseLayer!==null){renderer.setRenderTargetFramebuffer(newRenderTarget,glBaseLayer.framebuffer);renderer.setRenderTarget(newRenderTarget);}let cameraXRNeedsUpdate=false;// check if it's necessary to rebuild cameraXR's camera list
if(views.length!==cameraXR.cameras.length){cameraXR.cameras.length=0;cameraXRNeedsUpdate=true;}for(let i=0;i<views.length;i++){const view=views[i];let viewport=null;if(glBaseLayer!==null){viewport=glBaseLayer.getViewport(view);}else {const glSubImage=glBinding.getViewSubImage(glProjLayer,view);viewport=glSubImage.viewport;// For side-by-side projection, we only produce a single texture for both eyes.
if(i===0){renderer.setRenderTargetTextures(newRenderTarget,glSubImage.colorTexture,glProjLayer.ignoreDepthValues?undefined:glSubImage.depthStencilTexture);renderer.setRenderTarget(newRenderTarget);}}let camera=cameras[i];if(camera===undefined){camera=new PerspectiveCamera$1();camera.layers.enable(i);camera.viewport=new Vector4$1();cameras[i]=camera;}camera.matrix.fromArray(view.transform.matrix);camera.matrix.decompose(camera.position,camera.quaternion,camera.scale);camera.projectionMatrix.fromArray(view.projectionMatrix);camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();camera.viewport.set(viewport.x,viewport.y,viewport.width,viewport.height);if(i===0){cameraXR.matrix.copy(camera.matrix);cameraXR.matrix.decompose(cameraXR.position,cameraXR.quaternion,cameraXR.scale);}if(cameraXRNeedsUpdate===true){cameraXR.cameras.push(camera);}}//
const enabledFeatures=session.enabledFeatures;if(enabledFeatures&&enabledFeatures.includes('depth-sensing')){const depthData=glBinding.getDepthInformation(views[0]);if(depthData&&depthData.isValid&&depthData.texture){depthSensing.init(renderer,depthData,session.renderState);}}}//
for(let i=0;i<controllers.length;i++){const inputSource=controllerInputSources[i];const controller=controllers[i];if(inputSource!==null&&controller!==undefined){controller.update(inputSource,frame,customReferenceSpace||referenceSpace);}}if(onAnimationFrameCallback)onAnimationFrameCallback(time,frame);if(frame.detectedPlanes){scope.dispatchEvent({type:'planesdetected',data:frame});}xrFrame=null;}const animation=new WebGLAnimation();animation.setAnimationLoop(onAnimationFrame);this.setAnimationLoop=function(callback){onAnimationFrameCallback=callback;};this.dispose=function(){};}}const _e1=/*@__PURE__*/new Euler$1();const _m1$5=/*@__PURE__*/new Matrix4$1();function WebGLMaterials(renderer,properties){function refreshTransformUniform(map,uniform){if(map.matrixAutoUpdate===true){map.updateMatrix();}uniform.value.copy(map.matrix);}function refreshFogUniforms(uniforms,fog){fog.color.getRGB(uniforms.fogColor.value,getUnlitUniformColorSpace(renderer));if(fog.isFog){uniforms.fogNear.value=fog.near;uniforms.fogFar.value=fog.far;}else if(fog.isFogExp2){uniforms.fogDensity.value=fog.density;}}function refreshMaterialUniforms(uniforms,material,pixelRatio,height,transmissionRenderTarget){if(material.isMeshBasicMaterial){refreshUniformsCommon(uniforms,material);}else if(material.isMeshLambertMaterial){refreshUniformsCommon(uniforms,material);}else if(material.isMeshToonMaterial){refreshUniformsCommon(uniforms,material);refreshUniformsToon(uniforms,material);}else if(material.isMeshPhongMaterial){refreshUniformsCommon(uniforms,material);refreshUniformsPhong(uniforms,material);}else if(material.isMeshStandardMaterial){refreshUniformsCommon(uniforms,material);refreshUniformsStandard(uniforms,material);if(material.isMeshPhysicalMaterial){refreshUniformsPhysical(uniforms,material,transmissionRenderTarget);}}else if(material.isMeshMatcapMaterial){refreshUniformsCommon(uniforms,material);refreshUniformsMatcap(uniforms,material);}else if(material.isMeshDepthMaterial){refreshUniformsCommon(uniforms,material);}else if(material.isMeshDistanceMaterial){refreshUniformsCommon(uniforms,material);refreshUniformsDistance(uniforms,material);}else if(material.isMeshNormalMaterial){refreshUniformsCommon(uniforms,material);}else if(material.isLineBasicMaterial){refreshUniformsLine(uniforms,material);if(material.isLineDashedMaterial){refreshUniformsDash(uniforms,material);}}else if(material.isPointsMaterial){refreshUniformsPoints(uniforms,material,pixelRatio,height);}else if(material.isSpriteMaterial){refreshUniformsSprites(uniforms,material);}else if(material.isShadowMaterial){uniforms.color.value.copy(material.color);uniforms.opacity.value=material.opacity;}else if(material.isShaderMaterial){material.uniformsNeedUpdate=false;// #15581
}}function refreshUniformsCommon(uniforms,material){uniforms.opacity.value=material.opacity;if(material.color){uniforms.diffuse.value.copy(material.color);}if(material.emissive){uniforms.emissive.value.copy(material.emissive).multiplyScalar(material.emissiveIntensity);}if(material.map){uniforms.map.value=material.map;refreshTransformUniform(material.map,uniforms.mapTransform);}if(material.alphaMap){uniforms.alphaMap.value=material.alphaMap;refreshTransformUniform(material.alphaMap,uniforms.alphaMapTransform);}if(material.bumpMap){uniforms.bumpMap.value=material.bumpMap;refreshTransformUniform(material.bumpMap,uniforms.bumpMapTransform);uniforms.bumpScale.value=material.bumpScale;if(material.side===BackSide$1){uniforms.bumpScale.value*=-1;}}if(material.normalMap){uniforms.normalMap.value=material.normalMap;refreshTransformUniform(material.normalMap,uniforms.normalMapTransform);uniforms.normalScale.value.copy(material.normalScale);if(material.side===BackSide$1){uniforms.normalScale.value.negate();}}if(material.displacementMap){uniforms.displacementMap.value=material.displacementMap;refreshTransformUniform(material.displacementMap,uniforms.displacementMapTransform);uniforms.displacementScale.value=material.displacementScale;uniforms.displacementBias.value=material.displacementBias;}if(material.emissiveMap){uniforms.emissiveMap.value=material.emissiveMap;refreshTransformUniform(material.emissiveMap,uniforms.emissiveMapTransform);}if(material.specularMap){uniforms.specularMap.value=material.specularMap;refreshTransformUniform(material.specularMap,uniforms.specularMapTransform);}if(material.alphaTest>0){uniforms.alphaTest.value=material.alphaTest;}const materialProperties=properties.get(material);const envMap=materialProperties.envMap;const envMapRotation=materialProperties.envMapRotation;if(envMap){uniforms.envMap.value=envMap;_e1.copy(envMapRotation);// accommodate left-handed frame
_e1.x*=-1;_e1.y*=-1;_e1.z*=-1;if(envMap.isCubeTexture&&envMap.isRenderTargetTexture===false){// environment maps which are not cube render targets or PMREMs follow a different convention
_e1.y*=-1;_e1.z*=-1;}uniforms.envMapRotation.value.setFromMatrix4(_m1$5.makeRotationFromEuler(_e1));uniforms.flipEnvMap.value=envMap.isCubeTexture&&envMap.isRenderTargetTexture===false?-1:1;uniforms.reflectivity.value=material.reflectivity;uniforms.ior.value=material.ior;uniforms.refractionRatio.value=material.refractionRatio;}if(material.lightMap){uniforms.lightMap.value=material.lightMap;uniforms.lightMapIntensity.value=material.lightMapIntensity;refreshTransformUniform(material.lightMap,uniforms.lightMapTransform);}if(material.aoMap){uniforms.aoMap.value=material.aoMap;uniforms.aoMapIntensity.value=material.aoMapIntensity;refreshTransformUniform(material.aoMap,uniforms.aoMapTransform);}}function refreshUniformsLine(uniforms,material){uniforms.diffuse.value.copy(material.color);uniforms.opacity.value=material.opacity;if(material.map){uniforms.map.value=material.map;refreshTransformUniform(material.map,uniforms.mapTransform);}}function refreshUniformsDash(uniforms,material){uniforms.dashSize.value=material.dashSize;uniforms.totalSize.value=material.dashSize+material.gapSize;uniforms.scale.value=material.scale;}function refreshUniformsPoints(uniforms,material,pixelRatio,height){uniforms.diffuse.value.copy(material.color);uniforms.opacity.value=material.opacity;uniforms.size.value=material.size*pixelRatio;uniforms.scale.value=height*0.5;if(material.map){uniforms.map.value=material.map;refreshTransformUniform(material.map,uniforms.uvTransform);}if(material.alphaMap){uniforms.alphaMap.value=material.alphaMap;refreshTransformUniform(material.alphaMap,uniforms.alphaMapTransform);}if(material.alphaTest>0){uniforms.alphaTest.value=material.alphaTest;}}function refreshUniformsSprites(uniforms,material){uniforms.diffuse.value.copy(material.color);uniforms.opacity.value=material.opacity;uniforms.rotation.value=material.rotation;if(material.map){uniforms.map.value=material.map;refreshTransformUniform(material.map,uniforms.mapTransform);}if(material.alphaMap){uniforms.alphaMap.value=material.alphaMap;refreshTransformUniform(material.alphaMap,uniforms.alphaMapTransform);}if(material.alphaTest>0){uniforms.alphaTest.value=material.alphaTest;}}function refreshUniformsPhong(uniforms,material){uniforms.specular.value.copy(material.specular);uniforms.shininess.value=Math.max(material.shininess,1e-4);// to prevent pow( 0.0, 0.0 )
}function refreshUniformsToon(uniforms,material){if(material.gradientMap){uniforms.gradientMap.value=material.gradientMap;}}function refreshUniformsStandard(uniforms,material){uniforms.metalness.value=material.metalness;if(material.metalnessMap){uniforms.metalnessMap.value=material.metalnessMap;refreshTransformUniform(material.metalnessMap,uniforms.metalnessMapTransform);}uniforms.roughness.value=material.roughness;if(material.roughnessMap){uniforms.roughnessMap.value=material.roughnessMap;refreshTransformUniform(material.roughnessMap,uniforms.roughnessMapTransform);}if(material.envMap){//uniforms.envMap.value = material.envMap; // part of uniforms common
uniforms.envMapIntensity.value=material.envMapIntensity;}}function refreshUniformsPhysical(uniforms,material,transmissionRenderTarget){uniforms.ior.value=material.ior;// also part of uniforms common
if(material.sheen>0){uniforms.sheenColor.value.copy(material.sheenColor).multiplyScalar(material.sheen);uniforms.sheenRoughness.value=material.sheenRoughness;if(material.sheenColorMap){uniforms.sheenColorMap.value=material.sheenColorMap;refreshTransformUniform(material.sheenColorMap,uniforms.sheenColorMapTransform);}if(material.sheenRoughnessMap){uniforms.sheenRoughnessMap.value=material.sheenRoughnessMap;refreshTransformUniform(material.sheenRoughnessMap,uniforms.sheenRoughnessMapTransform);}}if(material.clearcoat>0){uniforms.clearcoat.value=material.clearcoat;uniforms.clearcoatRoughness.value=material.clearcoatRoughness;if(material.clearcoatMap){uniforms.clearcoatMap.value=material.clearcoatMap;refreshTransformUniform(material.clearcoatMap,uniforms.clearcoatMapTransform);}if(material.clearcoatRoughnessMap){uniforms.clearcoatRoughnessMap.value=material.clearcoatRoughnessMap;refreshTransformUniform(material.clearcoatRoughnessMap,uniforms.clearcoatRoughnessMapTransform);}if(material.clearcoatNormalMap){uniforms.clearcoatNormalMap.value=material.clearcoatNormalMap;refreshTransformUniform(material.clearcoatNormalMap,uniforms.clearcoatNormalMapTransform);uniforms.clearcoatNormalScale.value.copy(material.clearcoatNormalScale);if(material.side===BackSide$1){uniforms.clearcoatNormalScale.value.negate();}}}if(material.dispersion>0){uniforms.dispersion.value=material.dispersion;}if(material.iridescence>0){uniforms.iridescence.value=material.iridescence;uniforms.iridescenceIOR.value=material.iridescenceIOR;uniforms.iridescenceThicknessMinimum.value=material.iridescenceThicknessRange[0];uniforms.iridescenceThicknessMaximum.value=material.iridescenceThicknessRange[1];if(material.iridescenceMap){uniforms.iridescenceMap.value=material.iridescenceMap;refreshTransformUniform(material.iridescenceMap,uniforms.iridescenceMapTransform);}if(material.iridescenceThicknessMap){uniforms.iridescenceThicknessMap.value=material.iridescenceThicknessMap;refreshTransformUniform(material.iridescenceThicknessMap,uniforms.iridescenceThicknessMapTransform);}}if(material.transmission>0){uniforms.transmission.value=material.transmission;uniforms.transmissionSamplerMap.value=transmissionRenderTarget.texture;uniforms.transmissionSamplerSize.value.set(transmissionRenderTarget.width,transmissionRenderTarget.height);if(material.transmissionMap){uniforms.transmissionMap.value=material.transmissionMap;refreshTransformUniform(material.transmissionMap,uniforms.transmissionMapTransform);}uniforms.thickness.value=material.thickness;if(material.thicknessMap){uniforms.thicknessMap.value=material.thicknessMap;refreshTransformUniform(material.thicknessMap,uniforms.thicknessMapTransform);}uniforms.attenuationDistance.value=material.attenuationDistance;uniforms.attenuationColor.value.copy(material.attenuationColor);}if(material.anisotropy>0){uniforms.anisotropyVector.value.set(material.anisotropy*Math.cos(material.anisotropyRotation),material.anisotropy*Math.sin(material.anisotropyRotation));if(material.anisotropyMap){uniforms.anisotropyMap.value=material.anisotropyMap;refreshTransformUniform(material.anisotropyMap,uniforms.anisotropyMapTransform);}}uniforms.specularIntensity.value=material.specularIntensity;uniforms.specularColor.value.copy(material.specularColor);if(material.specularColorMap){uniforms.specularColorMap.value=material.specularColorMap;refreshTransformUniform(material.specularColorMap,uniforms.specularColorMapTransform);}if(material.specularIntensityMap){uniforms.specularIntensityMap.value=material.specularIntensityMap;refreshTransformUniform(material.specularIntensityMap,uniforms.specularIntensityMapTransform);}}function refreshUniformsMatcap(uniforms,material){if(material.matcap){uniforms.matcap.value=material.matcap;}}function refreshUniformsDistance(uniforms,material){const light=properties.get(material).light;uniforms.referencePosition.value.setFromMatrixPosition(light.matrixWorld);uniforms.nearDistance.value=light.shadow.camera.near;uniforms.farDistance.value=light.shadow.camera.far;}return {refreshFogUniforms:refreshFogUniforms,refreshMaterialUniforms:refreshMaterialUniforms};}function WebGLUniformsGroups(gl,info,capabilities,state){let buffers={};let updateList={};let allocatedBindingPoints=[];const maxBindingPoints=gl.getParameter(gl.MAX_UNIFORM_BUFFER_BINDINGS);// binding points are global whereas block indices are per shader program
function bind(uniformsGroup,program){const webglProgram=program.program;state.uniformBlockBinding(uniformsGroup,webglProgram);}function update(uniformsGroup,program){let buffer=buffers[uniformsGroup.id];if(buffer===undefined){prepareUniformsGroup(uniformsGroup);buffer=createBuffer(uniformsGroup);buffers[uniformsGroup.id]=buffer;uniformsGroup.addEventListener('dispose',onUniformsGroupsDispose);}// ensure to update the binding points/block indices mapping for this program
const webglProgram=program.program;state.updateUBOMapping(uniformsGroup,webglProgram);// update UBO once per frame
const frame=info.render.frame;if(updateList[uniformsGroup.id]!==frame){updateBufferData(uniformsGroup);updateList[uniformsGroup.id]=frame;}}function createBuffer(uniformsGroup){// the setup of an UBO is independent of a particular shader program but global
const bindingPointIndex=allocateBindingPointIndex();uniformsGroup.__bindingPointIndex=bindingPointIndex;const buffer=gl.createBuffer();const size=uniformsGroup.__size;const usage=uniformsGroup.usage;gl.bindBuffer(gl.UNIFORM_BUFFER,buffer);gl.bufferData(gl.UNIFORM_BUFFER,size,usage);gl.bindBuffer(gl.UNIFORM_BUFFER,null);gl.bindBufferBase(gl.UNIFORM_BUFFER,bindingPointIndex,buffer);return buffer;}function allocateBindingPointIndex(){for(let i=0;i<maxBindingPoints;i++){if(allocatedBindingPoints.indexOf(i)===-1){allocatedBindingPoints.push(i);return i;}}console.error('THREE.WebGLRenderer: Maximum number of simultaneously usable uniforms groups reached.');return 0;}function updateBufferData(uniformsGroup){const buffer=buffers[uniformsGroup.id];const uniforms=uniformsGroup.uniforms;const cache=uniformsGroup.__cache;gl.bindBuffer(gl.UNIFORM_BUFFER,buffer);for(let i=0,il=uniforms.length;i<il;i++){const uniformArray=Array.isArray(uniforms[i])?uniforms[i]:[uniforms[i]];for(let j=0,jl=uniformArray.length;j<jl;j++){const uniform=uniformArray[j];if(hasUniformChanged(uniform,i,j,cache)===true){const offset=uniform.__offset;const values=Array.isArray(uniform.value)?uniform.value:[uniform.value];let arrayOffset=0;for(let k=0;k<values.length;k++){const value=values[k];const info=getUniformSize(value);// TODO add integer and struct support
if(typeof value==='number'||typeof value==='boolean'){uniform.__data[0]=value;gl.bufferSubData(gl.UNIFORM_BUFFER,offset+arrayOffset,uniform.__data);}else if(value.isMatrix3){// manually converting 3x3 to 3x4
uniform.__data[0]=value.elements[0];uniform.__data[1]=value.elements[1];uniform.__data[2]=value.elements[2];uniform.__data[3]=0;uniform.__data[4]=value.elements[3];uniform.__data[5]=value.elements[4];uniform.__data[6]=value.elements[5];uniform.__data[7]=0;uniform.__data[8]=value.elements[6];uniform.__data[9]=value.elements[7];uniform.__data[10]=value.elements[8];uniform.__data[11]=0;}else {value.toArray(uniform.__data,arrayOffset);arrayOffset+=info.storage/Float32Array.BYTES_PER_ELEMENT;}}gl.bufferSubData(gl.UNIFORM_BUFFER,offset,uniform.__data);}}}gl.bindBuffer(gl.UNIFORM_BUFFER,null);}function hasUniformChanged(uniform,index,indexArray,cache){const value=uniform.value;const indexString=index+'_'+indexArray;if(cache[indexString]===undefined){// cache entry does not exist so far
if(typeof value==='number'||typeof value==='boolean'){cache[indexString]=value;}else {cache[indexString]=value.clone();}return true;}else {const cachedObject=cache[indexString];// compare current value with cached entry
if(typeof value==='number'||typeof value==='boolean'){if(cachedObject!==value){cache[indexString]=value;return true;}}else {if(cachedObject.equals(value)===false){cachedObject.copy(value);return true;}}}return false;}function prepareUniformsGroup(uniformsGroup){// determine total buffer size according to the STD140 layout
// Hint: STD140 is the only supported layout in WebGL 2
const uniforms=uniformsGroup.uniforms;let offset=0;// global buffer offset in bytes
const chunkSize=16;// size of a chunk in bytes
for(let i=0,l=uniforms.length;i<l;i++){const uniformArray=Array.isArray(uniforms[i])?uniforms[i]:[uniforms[i]];for(let j=0,jl=uniformArray.length;j<jl;j++){const uniform=uniformArray[j];const values=Array.isArray(uniform.value)?uniform.value:[uniform.value];for(let k=0,kl=values.length;k<kl;k++){const value=values[k];const info=getUniformSize(value);const chunkOffset=offset%chunkSize;// offset in the current chunk
const chunkPadding=chunkOffset%info.boundary;// required padding to match boundary
const chunkStart=chunkOffset+chunkPadding;// the start position in the current chunk for the data
offset+=chunkPadding;// Check for chunk overflow
if(chunkStart!==0&&chunkSize-chunkStart<info.storage){// Add padding and adjust offset
offset+=chunkSize-chunkStart;}// the following two properties will be used for partial buffer updates
uniform.__data=new Float32Array(info.storage/Float32Array.BYTES_PER_ELEMENT);uniform.__offset=offset;// Update the global offset
offset+=info.storage;}}}// ensure correct final padding
const chunkOffset=offset%chunkSize;if(chunkOffset>0)offset+=chunkSize-chunkOffset;//
uniformsGroup.__size=offset;uniformsGroup.__cache={};return this;}function getUniformSize(value){const info={boundary:0,// bytes
storage:0// bytes
};// determine sizes according to STD140
if(typeof value==='number'||typeof value==='boolean'){// float/int/bool
info.boundary=4;info.storage=4;}else if(value.isVector2){// vec2
info.boundary=8;info.storage=8;}else if(value.isVector3||value.isColor){// vec3
info.boundary=16;info.storage=12;// evil: vec3 must start on a 16-byte boundary but it only consumes 12 bytes
}else if(value.isVector4){// vec4
info.boundary=16;info.storage=16;}else if(value.isMatrix3){// mat3 (in STD140 a 3x3 matrix is represented as 3x4)
info.boundary=48;info.storage=48;}else if(value.isMatrix4){// mat4
info.boundary=64;info.storage=64;}else if(value.isTexture){console.warn('THREE.WebGLRenderer: Texture samplers can not be part of an uniforms group.');}else {console.warn('THREE.WebGLRenderer: Unsupported uniform value type.',value);}return info;}function onUniformsGroupsDispose(event){const uniformsGroup=event.target;uniformsGroup.removeEventListener('dispose',onUniformsGroupsDispose);const index=allocatedBindingPoints.indexOf(uniformsGroup.__bindingPointIndex);allocatedBindingPoints.splice(index,1);gl.deleteBuffer(buffers[uniformsGroup.id]);delete buffers[uniformsGroup.id];delete updateList[uniformsGroup.id];}function dispose(){for(const id in buffers){gl.deleteBuffer(buffers[id]);}allocatedBindingPoints=[];buffers={};updateList={};}return {bind:bind,update:update,dispose:dispose};}class WebGLRenderer{constructor(parameters={}){const{canvas=createCanvasElement(),context=null,depth=true,stencil=false,alpha=false,antialias=false,premultipliedAlpha=true,preserveDrawingBuffer=false,powerPreference='default',failIfMajorPerformanceCaveat=false}=parameters;this.isWebGLRenderer=true;let _alpha;if(context!==null){if(typeof WebGLRenderingContext!=='undefined'&&context instanceof WebGLRenderingContext){throw new Error('THREE.WebGLRenderer: WebGL 1 is not supported since r163.');}_alpha=context.getContextAttributes().alpha;}else {_alpha=alpha;}const uintClearColor=new Uint32Array(4);const intClearColor=new Int32Array(4);let currentRenderList=null;let currentRenderState=null;// render() can be called from within a callback triggered by another render.
// We track this so that the nested render call gets its list and state isolated from the parent render call.
const renderListStack=[];const renderStateStack=[];// public properties
this.domElement=canvas;// Debug configuration container
this.debug={/**
			 * Enables error checking and reporting when shader programs are being compiled
			 * @type {boolean}
			 */checkShaderErrors:true,/**
			 * Callback for custom error reporting.
			 * @type {?Function}
			 */onShaderError:null};// clearing
this.autoClear=true;this.autoClearColor=true;this.autoClearDepth=true;this.autoClearStencil=true;// scene graph
this.sortObjects=true;// user-defined clipping
this.clippingPlanes=[];this.localClippingEnabled=false;// physically based shading
this._outputColorSpace=SRGBColorSpace$1;// tone mapping
this.toneMapping=NoToneMapping$1;this.toneMappingExposure=1.0;// internal properties
const _this=this;let _isContextLost=false;// internal state cache
let _currentActiveCubeFace=0;let _currentActiveMipmapLevel=0;let _currentRenderTarget=null;let _currentMaterialId=-1;let _currentCamera=null;const _currentViewport=new Vector4$1();const _currentScissor=new Vector4$1();let _currentScissorTest=null;const _currentClearColor=new Color$1(0x000000);let _currentClearAlpha=0;//
let _width=canvas.width;let _height=canvas.height;let _pixelRatio=1;let _opaqueSort=null;let _transparentSort=null;const _viewport=new Vector4$1(0,0,_width,_height);const _scissor=new Vector4$1(0,0,_width,_height);let _scissorTest=false;// frustum
const _frustum=new Frustum();// clipping
let _clippingEnabled=false;let _localClippingEnabled=false;// camera matrices cache
const _projScreenMatrix=new Matrix4$1();const _vector3=new Vector3$1();const _vector4=new Vector4$1();const _emptyScene={background:null,fog:null,environment:null,overrideMaterial:null,isScene:true};let _renderBackground=false;function getTargetPixelRatio(){return _currentRenderTarget===null?_pixelRatio:1;}// initialize
let _gl=context;function getContext(contextName,contextAttributes){return canvas.getContext(contextName,contextAttributes);}try{const contextAttributes={alpha:true,depth,stencil,antialias,premultipliedAlpha,preserveDrawingBuffer,powerPreference,failIfMajorPerformanceCaveat};// OffscreenCanvas does not have setAttribute, see #22811
if('setAttribute'in canvas)canvas.setAttribute('data-engine',`three.js r${REVISION}`);// event listeners must be registered before WebGL context is created, see #12753
canvas.addEventListener('webglcontextlost',onContextLost,false);canvas.addEventListener('webglcontextrestored',onContextRestore,false);canvas.addEventListener('webglcontextcreationerror',onContextCreationError,false);if(_gl===null){const contextName='webgl2';_gl=getContext(contextName,contextAttributes);if(_gl===null){if(getContext(contextName)){throw new Error('Error creating WebGL context with your selected attributes.');}else {throw new Error('Error creating WebGL context.');}}}}catch(error){console.error('THREE.WebGLRenderer: '+error.message);throw error;}let extensions,capabilities,state,info;let properties,textures,cubemaps,cubeuvmaps,attributes,geometries,objects;let programCache,materials,renderLists,renderStates,clipping,shadowMap;let background,morphtargets,bufferRenderer,indexedBufferRenderer;let utils,bindingStates,uniformsGroups;function initGLContext(){extensions=new WebGLExtensions(_gl);extensions.init();utils=new WebGLUtils(_gl,extensions);capabilities=new WebGLCapabilities(_gl,extensions,parameters,utils);state=new WebGLState(_gl);info=new WebGLInfo(_gl);properties=new WebGLProperties();textures=new WebGLTextures(_gl,extensions,state,properties,capabilities,utils,info);cubemaps=new WebGLCubeMaps(_this);cubeuvmaps=new WebGLCubeUVMaps(_this);attributes=new WebGLAttributes(_gl);bindingStates=new WebGLBindingStates(_gl,attributes);geometries=new WebGLGeometries(_gl,attributes,info,bindingStates);objects=new WebGLObjects(_gl,geometries,attributes,info);morphtargets=new WebGLMorphtargets(_gl,capabilities,textures);clipping=new WebGLClipping(properties);programCache=new WebGLPrograms(_this,cubemaps,cubeuvmaps,extensions,capabilities,bindingStates,clipping);materials=new WebGLMaterials(_this,properties);renderLists=new WebGLRenderLists();renderStates=new WebGLRenderStates(extensions);background=new WebGLBackground(_this,cubemaps,cubeuvmaps,state,objects,_alpha,premultipliedAlpha);shadowMap=new WebGLShadowMap(_this,objects,capabilities);uniformsGroups=new WebGLUniformsGroups(_gl,info,capabilities,state);bufferRenderer=new WebGLBufferRenderer(_gl,extensions,info);indexedBufferRenderer=new WebGLIndexedBufferRenderer(_gl,extensions,info);info.programs=programCache.programs;_this.capabilities=capabilities;_this.extensions=extensions;_this.properties=properties;_this.renderLists=renderLists;_this.shadowMap=shadowMap;_this.state=state;_this.info=info;}initGLContext();// xr
const xr=new WebXRManager(_this,_gl);this.xr=xr;// API
this.getContext=function(){return _gl;};this.getContextAttributes=function(){return _gl.getContextAttributes();};this.forceContextLoss=function(){const extension=extensions.get('WEBGL_lose_context');if(extension)extension.loseContext();};this.forceContextRestore=function(){const extension=extensions.get('WEBGL_lose_context');if(extension)extension.restoreContext();};this.getPixelRatio=function(){return _pixelRatio;};this.setPixelRatio=function(value){if(value===undefined)return;_pixelRatio=value;this.setSize(_width,_height,false);};this.getSize=function(target){return target.set(_width,_height);};this.setSize=function(width,height,updateStyle=true){if(xr.isPresenting){console.warn('THREE.WebGLRenderer: Can\'t change size while VR device is presenting.');return;}_width=width;_height=height;canvas.width=Math.floor(width*_pixelRatio);canvas.height=Math.floor(height*_pixelRatio);if(updateStyle===true){canvas.style.width=width+'px';canvas.style.height=height+'px';}this.setViewport(0,0,width,height);};this.getDrawingBufferSize=function(target){return target.set(_width*_pixelRatio,_height*_pixelRatio).floor();};this.setDrawingBufferSize=function(width,height,pixelRatio){_width=width;_height=height;_pixelRatio=pixelRatio;canvas.width=Math.floor(width*pixelRatio);canvas.height=Math.floor(height*pixelRatio);this.setViewport(0,0,width,height);};this.getCurrentViewport=function(target){return target.copy(_currentViewport);};this.getViewport=function(target){return target.copy(_viewport);};this.setViewport=function(x,y,width,height){if(x.isVector4){_viewport.set(x.x,x.y,x.z,x.w);}else {_viewport.set(x,y,width,height);}state.viewport(_currentViewport.copy(_viewport).multiplyScalar(_pixelRatio).round());};this.getScissor=function(target){return target.copy(_scissor);};this.setScissor=function(x,y,width,height){if(x.isVector4){_scissor.set(x.x,x.y,x.z,x.w);}else {_scissor.set(x,y,width,height);}state.scissor(_currentScissor.copy(_scissor).multiplyScalar(_pixelRatio).round());};this.getScissorTest=function(){return _scissorTest;};this.setScissorTest=function(boolean){state.setScissorTest(_scissorTest=boolean);};this.setOpaqueSort=function(method){_opaqueSort=method;};this.setTransparentSort=function(method){_transparentSort=method;};// Clearing
this.getClearColor=function(target){return target.copy(background.getClearColor());};this.setClearColor=function(){background.setClearColor.apply(background,arguments);};this.getClearAlpha=function(){return background.getClearAlpha();};this.setClearAlpha=function(){background.setClearAlpha.apply(background,arguments);};this.clear=function(color=true,depth=true,stencil=true){let bits=0;if(color){// check if we're trying to clear an integer target
let isIntegerFormat=false;if(_currentRenderTarget!==null){const targetFormat=_currentRenderTarget.texture.format;isIntegerFormat=targetFormat===RGBAIntegerFormat||targetFormat===RGIntegerFormat||targetFormat===RedIntegerFormat;}// use the appropriate clear functions to clear the target if it's a signed
// or unsigned integer target
if(isIntegerFormat){const targetType=_currentRenderTarget.texture.type;const isUnsignedType=targetType===UnsignedByteType$1||targetType===UnsignedIntType$1||targetType===UnsignedShortType||targetType===UnsignedInt248Type$1||targetType===UnsignedShort4444Type||targetType===UnsignedShort5551Type;const clearColor=background.getClearColor();const a=background.getClearAlpha();const r=clearColor.r;const g=clearColor.g;const b=clearColor.b;if(isUnsignedType){uintClearColor[0]=r;uintClearColor[1]=g;uintClearColor[2]=b;uintClearColor[3]=a;_gl.clearBufferuiv(_gl.COLOR,0,uintClearColor);}else {intClearColor[0]=r;intClearColor[1]=g;intClearColor[2]=b;intClearColor[3]=a;_gl.clearBufferiv(_gl.COLOR,0,intClearColor);}}else {bits|=_gl.COLOR_BUFFER_BIT;}}if(depth)bits|=_gl.DEPTH_BUFFER_BIT;if(stencil){bits|=_gl.STENCIL_BUFFER_BIT;this.state.buffers.stencil.setMask(0xffffffff);}_gl.clear(bits);};this.clearColor=function(){this.clear(true,false,false);};this.clearDepth=function(){this.clear(false,true,false);};this.clearStencil=function(){this.clear(false,false,true);};//
this.dispose=function(){canvas.removeEventListener('webglcontextlost',onContextLost,false);canvas.removeEventListener('webglcontextrestored',onContextRestore,false);canvas.removeEventListener('webglcontextcreationerror',onContextCreationError,false);renderLists.dispose();renderStates.dispose();properties.dispose();cubemaps.dispose();cubeuvmaps.dispose();objects.dispose();bindingStates.dispose();uniformsGroups.dispose();programCache.dispose();xr.dispose();xr.removeEventListener('sessionstart',onXRSessionStart);xr.removeEventListener('sessionend',onXRSessionEnd);animation.stop();};// Events
function onContextLost(event){event.preventDefault();console.log('THREE.WebGLRenderer: Context Lost.');_isContextLost=true;}function onContextRestore(/* event */){console.log('THREE.WebGLRenderer: Context Restored.');_isContextLost=false;const infoAutoReset=info.autoReset;const shadowMapEnabled=shadowMap.enabled;const shadowMapAutoUpdate=shadowMap.autoUpdate;const shadowMapNeedsUpdate=shadowMap.needsUpdate;const shadowMapType=shadowMap.type;initGLContext();info.autoReset=infoAutoReset;shadowMap.enabled=shadowMapEnabled;shadowMap.autoUpdate=shadowMapAutoUpdate;shadowMap.needsUpdate=shadowMapNeedsUpdate;shadowMap.type=shadowMapType;}function onContextCreationError(event){console.error('THREE.WebGLRenderer: A WebGL context could not be created. Reason: ',event.statusMessage);}function onMaterialDispose(event){const material=event.target;material.removeEventListener('dispose',onMaterialDispose);deallocateMaterial(material);}// Buffer deallocation
function deallocateMaterial(material){releaseMaterialProgramReferences(material);properties.remove(material);}function releaseMaterialProgramReferences(material){const programs=properties.get(material).programs;if(programs!==undefined){programs.forEach(function(program){programCache.releaseProgram(program);});if(material.isShaderMaterial){programCache.releaseShaderCache(material);}}}// Buffer rendering
this.renderBufferDirect=function(camera,scene,geometry,material,object,group){if(scene===null)scene=_emptyScene;// renderBufferDirect second parameter used to be fog (could be null)
const frontFaceCW=object.isMesh&&object.matrixWorld.determinant()<0;const program=setProgram(camera,scene,geometry,material,object);state.setMaterial(material,frontFaceCW);//
let index=geometry.index;let rangeFactor=1;if(material.wireframe===true){index=geometries.getWireframeAttribute(geometry);if(index===undefined)return;rangeFactor=2;}//
const drawRange=geometry.drawRange;const position=geometry.attributes.position;let drawStart=drawRange.start*rangeFactor;let drawEnd=(drawRange.start+drawRange.count)*rangeFactor;if(group!==null){drawStart=Math.max(drawStart,group.start*rangeFactor);drawEnd=Math.min(drawEnd,(group.start+group.count)*rangeFactor);}if(index!==null){drawStart=Math.max(drawStart,0);drawEnd=Math.min(drawEnd,index.count);}else if(position!==undefined&&position!==null){drawStart=Math.max(drawStart,0);drawEnd=Math.min(drawEnd,position.count);}const drawCount=drawEnd-drawStart;if(drawCount<0||drawCount===Infinity)return;//
bindingStates.setup(object,material,program,geometry,index);let attribute;let renderer=bufferRenderer;if(index!==null){attribute=attributes.get(index);renderer=indexedBufferRenderer;renderer.setIndex(attribute);}//
if(object.isMesh){if(material.wireframe===true){state.setLineWidth(material.wireframeLinewidth*getTargetPixelRatio());renderer.setMode(_gl.LINES);}else {renderer.setMode(_gl.TRIANGLES);}}else if(object.isLine){let lineWidth=material.linewidth;if(lineWidth===undefined)lineWidth=1;// Not using Line*Material
state.setLineWidth(lineWidth*getTargetPixelRatio());if(object.isLineSegments){renderer.setMode(_gl.LINES);}else if(object.isLineLoop){renderer.setMode(_gl.LINE_LOOP);}else {renderer.setMode(_gl.LINE_STRIP);}}else if(object.isPoints){renderer.setMode(_gl.POINTS);}else if(object.isSprite){renderer.setMode(_gl.TRIANGLES);}if(object.isBatchedMesh){if(object._multiDrawInstances!==null){renderer.renderMultiDrawInstances(object._multiDrawStarts,object._multiDrawCounts,object._multiDrawCount,object._multiDrawInstances);}else {if(!extensions.get('WEBGL_multi_draw')){const starts=object._multiDrawStarts;const counts=object._multiDrawCounts;const drawCount=object._multiDrawCount;const bytesPerElement=index?attributes.get(index).bytesPerElement:1;const uniforms=properties.get(material).currentProgram.getUniforms();for(let i=0;i<drawCount;i++){uniforms.setValue(_gl,'_gl_DrawID',i);renderer.render(starts[i]/bytesPerElement,counts[i]);}}else {renderer.renderMultiDraw(object._multiDrawStarts,object._multiDrawCounts,object._multiDrawCount);}}}else if(object.isInstancedMesh){renderer.renderInstances(drawStart,drawCount,object.count);}else if(geometry.isInstancedBufferGeometry){const maxInstanceCount=geometry._maxInstanceCount!==undefined?geometry._maxInstanceCount:Infinity;const instanceCount=Math.min(geometry.instanceCount,maxInstanceCount);renderer.renderInstances(drawStart,drawCount,instanceCount);}else {renderer.render(drawStart,drawCount);}};// Compile
function prepareMaterial(material,scene,object){if(material.transparent===true&&material.side===DoubleSide&&material.forceSinglePass===false){material.side=BackSide$1;material.needsUpdate=true;getProgram(material,scene,object);material.side=FrontSide$1;material.needsUpdate=true;getProgram(material,scene,object);material.side=DoubleSide;}else {getProgram(material,scene,object);}}this.compile=function(scene,camera,targetScene=null){if(targetScene===null)targetScene=scene;currentRenderState=renderStates.get(targetScene);currentRenderState.init(camera);renderStateStack.push(currentRenderState);// gather lights from both the target scene and the new object that will be added to the scene.
targetScene.traverseVisible(function(object){if(object.isLight&&object.layers.test(camera.layers)){currentRenderState.pushLight(object);if(object.castShadow){currentRenderState.pushShadow(object);}}});if(scene!==targetScene){scene.traverseVisible(function(object){if(object.isLight&&object.layers.test(camera.layers)){currentRenderState.pushLight(object);if(object.castShadow){currentRenderState.pushShadow(object);}}});}currentRenderState.setupLights();// Only initialize materials in the new scene, not the targetScene.
const materials=new Set();scene.traverse(function(object){const material=object.material;if(material){if(Array.isArray(material)){for(let i=0;i<material.length;i++){const material2=material[i];prepareMaterial(material2,targetScene,object);materials.add(material2);}}else {prepareMaterial(material,targetScene,object);materials.add(material);}}});renderStateStack.pop();currentRenderState=null;return materials;};// compileAsync
this.compileAsync=function(scene,camera,targetScene=null){const materials=this.compile(scene,camera,targetScene);// Wait for all the materials in the new object to indicate that they're
// ready to be used before resolving the promise.
return new Promise(resolve=>{function checkMaterialsReady(){materials.forEach(function(material){const materialProperties=properties.get(material);const program=materialProperties.currentProgram;if(program.isReady()){// remove any programs that report they're ready to use from the list
materials.delete(material);}});// once the list of compiling materials is empty, call the callback
if(materials.size===0){resolve(scene);return;}// if some materials are still not ready, wait a bit and check again
setTimeout(checkMaterialsReady,10);}if(extensions.get('KHR_parallel_shader_compile')!==null){// If we can check the compilation status of the materials without
// blocking then do so right away.
checkMaterialsReady();}else {// Otherwise start by waiting a bit to give the materials we just
// initialized a chance to finish.
setTimeout(checkMaterialsReady,10);}});};// Animation Loop
let onAnimationFrameCallback=null;function onAnimationFrame(time){if(onAnimationFrameCallback)onAnimationFrameCallback(time);}function onXRSessionStart(){animation.stop();}function onXRSessionEnd(){animation.start();}const animation=new WebGLAnimation();animation.setAnimationLoop(onAnimationFrame);if(typeof self!=='undefined')animation.setContext(self);this.setAnimationLoop=function(callback){onAnimationFrameCallback=callback;xr.setAnimationLoop(callback);callback===null?animation.stop():animation.start();};xr.addEventListener('sessionstart',onXRSessionStart);xr.addEventListener('sessionend',onXRSessionEnd);// Rendering
this.render=function(scene,camera){if(camera!==undefined&&camera.isCamera!==true){console.error('THREE.WebGLRenderer.render: camera is not an instance of THREE.Camera.');return;}if(_isContextLost===true)return;// update scene graph
if(scene.matrixWorldAutoUpdate===true)scene.updateMatrixWorld();// update camera matrices and frustum
if(camera.parent===null&&camera.matrixWorldAutoUpdate===true)camera.updateMatrixWorld();if(xr.enabled===true&&xr.isPresenting===true){if(xr.cameraAutoUpdate===true)xr.updateCamera(camera);camera=xr.getCamera();// use XR camera for rendering
}//
if(scene.isScene===true)scene.onBeforeRender(_this,scene,camera,_currentRenderTarget);currentRenderState=renderStates.get(scene,renderStateStack.length);currentRenderState.init(camera);renderStateStack.push(currentRenderState);_projScreenMatrix.multiplyMatrices(camera.projectionMatrix,camera.matrixWorldInverse);_frustum.setFromProjectionMatrix(_projScreenMatrix);_localClippingEnabled=this.localClippingEnabled;_clippingEnabled=clipping.init(this.clippingPlanes,_localClippingEnabled);currentRenderList=renderLists.get(scene,renderListStack.length);currentRenderList.init();renderListStack.push(currentRenderList);if(xr.enabled===true&&xr.isPresenting===true){const depthSensingMesh=_this.xr.getDepthSensingMesh();if(depthSensingMesh!==null){projectObject(depthSensingMesh,camera,-Infinity,_this.sortObjects);}}projectObject(scene,camera,0,_this.sortObjects);currentRenderList.finish();if(_this.sortObjects===true){currentRenderList.sort(_opaqueSort,_transparentSort);}_renderBackground=xr.enabled===false||xr.isPresenting===false||xr.hasDepthSensing()===false;if(_renderBackground){background.addToRenderList(currentRenderList,scene);}//
this.info.render.frame++;if(_clippingEnabled===true)clipping.beginShadows();const shadowsArray=currentRenderState.state.shadowsArray;shadowMap.render(shadowsArray,scene,camera);if(_clippingEnabled===true)clipping.endShadows();//
if(this.info.autoReset===true)this.info.reset();// render scene
const opaqueObjects=currentRenderList.opaque;const transmissiveObjects=currentRenderList.transmissive;currentRenderState.setupLights();if(camera.isArrayCamera){const cameras=camera.cameras;if(transmissiveObjects.length>0){for(let i=0,l=cameras.length;i<l;i++){const camera2=cameras[i];renderTransmissionPass(opaqueObjects,transmissiveObjects,scene,camera2);}}if(_renderBackground)background.render(scene);for(let i=0,l=cameras.length;i<l;i++){const camera2=cameras[i];renderScene(currentRenderList,scene,camera2,camera2.viewport);}}else {if(transmissiveObjects.length>0)renderTransmissionPass(opaqueObjects,transmissiveObjects,scene,camera);if(_renderBackground)background.render(scene);renderScene(currentRenderList,scene,camera);}//
if(_currentRenderTarget!==null){// resolve multisample renderbuffers to a single-sample texture if necessary
textures.updateMultisampleRenderTarget(_currentRenderTarget);// Generate mipmap if we're using any kind of mipmap filtering
textures.updateRenderTargetMipmap(_currentRenderTarget);}//
if(scene.isScene===true)scene.onAfterRender(_this,scene,camera);// _gl.finish();
bindingStates.resetDefaultState();_currentMaterialId=-1;_currentCamera=null;renderStateStack.pop();if(renderStateStack.length>0){currentRenderState=renderStateStack[renderStateStack.length-1];if(_clippingEnabled===true)clipping.setGlobalState(_this.clippingPlanes,currentRenderState.state.camera);}else {currentRenderState=null;}renderListStack.pop();if(renderListStack.length>0){currentRenderList=renderListStack[renderListStack.length-1];}else {currentRenderList=null;}};function projectObject(object,camera,groupOrder,sortObjects){if(object.visible===false)return;const visible=object.layers.test(camera.layers);if(visible){if(object.isGroup){groupOrder=object.renderOrder;}else if(object.isLOD){if(object.autoUpdate===true)object.update(camera);}else if(object.isLight){currentRenderState.pushLight(object);if(object.castShadow){currentRenderState.pushShadow(object);}}else if(object.isSprite){if(!object.frustumCulled||_frustum.intersectsSprite(object)){if(sortObjects){_vector4.setFromMatrixPosition(object.matrixWorld).applyMatrix4(_projScreenMatrix);}const geometry=objects.update(object);const material=object.material;if(material.visible){currentRenderList.push(object,geometry,material,groupOrder,_vector4.z,null);}}}else if(object.isMesh||object.isLine||object.isPoints){if(!object.frustumCulled||_frustum.intersectsObject(object)){const geometry=objects.update(object);const material=object.material;if(sortObjects){if(object.boundingSphere!==undefined){if(object.boundingSphere===null)object.computeBoundingSphere();_vector4.copy(object.boundingSphere.center);}else {if(geometry.boundingSphere===null)geometry.computeBoundingSphere();_vector4.copy(geometry.boundingSphere.center);}_vector4.applyMatrix4(object.matrixWorld).applyMatrix4(_projScreenMatrix);}if(Array.isArray(material)){const groups=geometry.groups;for(let i=0,l=groups.length;i<l;i++){const group=groups[i];const groupMaterial=material[group.materialIndex];if(groupMaterial&&groupMaterial.visible){currentRenderList.push(object,geometry,groupMaterial,groupOrder,_vector4.z,group);}}}else if(material.visible){currentRenderList.push(object,geometry,material,groupOrder,_vector4.z,null);}}}}const children=object.children;for(let i=0,l=children.length;i<l;i++){projectObject(children[i],camera,groupOrder,sortObjects);}}function renderScene(currentRenderList,scene,camera,viewport){const opaqueObjects=currentRenderList.opaque;const transmissiveObjects=currentRenderList.transmissive;const transparentObjects=currentRenderList.transparent;currentRenderState.setupLightsView(camera);if(_clippingEnabled===true)clipping.setGlobalState(_this.clippingPlanes,camera);if(viewport)state.viewport(_currentViewport.copy(viewport));if(opaqueObjects.length>0)renderObjects(opaqueObjects,scene,camera);if(transmissiveObjects.length>0)renderObjects(transmissiveObjects,scene,camera);if(transparentObjects.length>0)renderObjects(transparentObjects,scene,camera);// Ensure depth buffer writing is enabled so it can be cleared on next render
state.buffers.depth.setTest(true);state.buffers.depth.setMask(true);state.buffers.color.setMask(true);state.setPolygonOffset(false);}function renderTransmissionPass(opaqueObjects,transmissiveObjects,scene,camera){const overrideMaterial=scene.isScene===true?scene.overrideMaterial:null;if(overrideMaterial!==null){return;}if(currentRenderState.state.transmissionRenderTarget[camera.id]===undefined){currentRenderState.state.transmissionRenderTarget[camera.id]=new WebGLRenderTarget(1,1,{generateMipmaps:true,type:extensions.has('EXT_color_buffer_half_float')||extensions.has('EXT_color_buffer_float')?HalfFloatType$1:UnsignedByteType$1,minFilter:LinearMipmapLinearFilter$1,samples:4,stencilBuffer:stencil,resolveDepthBuffer:false,resolveStencilBuffer:false,colorSpace:ColorManagement$1.workingColorSpace});// debug
/*
				const geometry = new PlaneGeometry();
				const material = new MeshBasicMaterial( { map: _transmissionRenderTarget.texture } );

				const mesh = new Mesh( geometry, material );
				scene.add( mesh );
				*/}const transmissionRenderTarget=currentRenderState.state.transmissionRenderTarget[camera.id];const activeViewport=camera.viewport||_currentViewport;transmissionRenderTarget.setSize(activeViewport.z,activeViewport.w);//
const currentRenderTarget=_this.getRenderTarget();_this.setRenderTarget(transmissionRenderTarget);_this.getClearColor(_currentClearColor);_currentClearAlpha=_this.getClearAlpha();if(_currentClearAlpha<1)_this.setClearColor(0xffffff,0.5);_this.clear();if(_renderBackground)background.render(scene);// Turn off the features which can affect the frag color for opaque objects pass.
// Otherwise they are applied twice in opaque objects pass and transmission objects pass.
const currentToneMapping=_this.toneMapping;_this.toneMapping=NoToneMapping$1;// Remove viewport from camera to avoid nested render calls resetting viewport to it (e.g Reflector).
// Transmission render pass requires viewport to match the transmissionRenderTarget.
const currentCameraViewport=camera.viewport;if(camera.viewport!==undefined)camera.viewport=undefined;currentRenderState.setupLightsView(camera);if(_clippingEnabled===true)clipping.setGlobalState(_this.clippingPlanes,camera);renderObjects(opaqueObjects,scene,camera);textures.updateMultisampleRenderTarget(transmissionRenderTarget);textures.updateRenderTargetMipmap(transmissionRenderTarget);if(extensions.has('WEBGL_multisampled_render_to_texture')===false){// see #28131
let renderTargetNeedsUpdate=false;for(let i=0,l=transmissiveObjects.length;i<l;i++){const renderItem=transmissiveObjects[i];const object=renderItem.object;const geometry=renderItem.geometry;const material=renderItem.material;const group=renderItem.group;if(material.side===DoubleSide&&object.layers.test(camera.layers)){const currentSide=material.side;material.side=BackSide$1;material.needsUpdate=true;renderObject(object,scene,camera,geometry,material,group);material.side=currentSide;material.needsUpdate=true;renderTargetNeedsUpdate=true;}}if(renderTargetNeedsUpdate===true){textures.updateMultisampleRenderTarget(transmissionRenderTarget);textures.updateRenderTargetMipmap(transmissionRenderTarget);}}_this.setRenderTarget(currentRenderTarget);_this.setClearColor(_currentClearColor,_currentClearAlpha);if(currentCameraViewport!==undefined)camera.viewport=currentCameraViewport;_this.toneMapping=currentToneMapping;}function renderObjects(renderList,scene,camera){const overrideMaterial=scene.isScene===true?scene.overrideMaterial:null;for(let i=0,l=renderList.length;i<l;i++){const renderItem=renderList[i];const object=renderItem.object;const geometry=renderItem.geometry;const material=overrideMaterial===null?renderItem.material:overrideMaterial;const group=renderItem.group;if(object.layers.test(camera.layers)){renderObject(object,scene,camera,geometry,material,group);}}}function renderObject(object,scene,camera,geometry,material,group){object.onBeforeRender(_this,scene,camera,geometry,material,group);object.modelViewMatrix.multiplyMatrices(camera.matrixWorldInverse,object.matrixWorld);object.normalMatrix.getNormalMatrix(object.modelViewMatrix);material.onBeforeRender(_this,scene,camera,geometry,object,group);if(material.transparent===true&&material.side===DoubleSide&&material.forceSinglePass===false){material.side=BackSide$1;material.needsUpdate=true;_this.renderBufferDirect(camera,scene,geometry,material,object,group);material.side=FrontSide$1;material.needsUpdate=true;_this.renderBufferDirect(camera,scene,geometry,material,object,group);material.side=DoubleSide;}else {_this.renderBufferDirect(camera,scene,geometry,material,object,group);}object.onAfterRender(_this,scene,camera,geometry,material,group);}function getProgram(material,scene,object){if(scene.isScene!==true)scene=_emptyScene;// scene could be a Mesh, Line, Points, ...
const materialProperties=properties.get(material);const lights=currentRenderState.state.lights;const shadowsArray=currentRenderState.state.shadowsArray;const lightsStateVersion=lights.state.version;const parameters=programCache.getParameters(material,lights.state,shadowsArray,scene,object);const programCacheKey=programCache.getProgramCacheKey(parameters);let programs=materialProperties.programs;// always update environment and fog - changing these trigger an getProgram call, but it's possible that the program doesn't change
materialProperties.environment=material.isMeshStandardMaterial?scene.environment:null;materialProperties.fog=scene.fog;materialProperties.envMap=(material.isMeshStandardMaterial?cubeuvmaps:cubemaps).get(material.envMap||materialProperties.environment);materialProperties.envMapRotation=materialProperties.environment!==null&&material.envMap===null?scene.environmentRotation:material.envMapRotation;if(programs===undefined){// new material
material.addEventListener('dispose',onMaterialDispose);programs=new Map();materialProperties.programs=programs;}let program=programs.get(programCacheKey);if(program!==undefined){// early out if program and light state is identical
if(materialProperties.currentProgram===program&&materialProperties.lightsStateVersion===lightsStateVersion){updateCommonMaterialProperties(material,parameters);return program;}}else {parameters.uniforms=programCache.getUniforms(material);material.onBeforeCompile(parameters,_this);program=programCache.acquireProgram(parameters,programCacheKey);programs.set(programCacheKey,program);materialProperties.uniforms=parameters.uniforms;}const uniforms=materialProperties.uniforms;if(!material.isShaderMaterial&&!material.isRawShaderMaterial||material.clipping===true){uniforms.clippingPlanes=clipping.uniform;}updateCommonMaterialProperties(material,parameters);// store the light setup it was created for
materialProperties.needsLights=materialNeedsLights(material);materialProperties.lightsStateVersion=lightsStateVersion;if(materialProperties.needsLights){// wire up the material to this renderer's lighting state
uniforms.ambientLightColor.value=lights.state.ambient;uniforms.lightProbe.value=lights.state.probe;uniforms.directionalLights.value=lights.state.directional;uniforms.directionalLightShadows.value=lights.state.directionalShadow;uniforms.spotLights.value=lights.state.spot;uniforms.spotLightShadows.value=lights.state.spotShadow;uniforms.rectAreaLights.value=lights.state.rectArea;uniforms.ltc_1.value=lights.state.rectAreaLTC1;uniforms.ltc_2.value=lights.state.rectAreaLTC2;uniforms.pointLights.value=lights.state.point;uniforms.pointLightShadows.value=lights.state.pointShadow;uniforms.hemisphereLights.value=lights.state.hemi;uniforms.directionalShadowMap.value=lights.state.directionalShadowMap;uniforms.directionalShadowMatrix.value=lights.state.directionalShadowMatrix;uniforms.spotShadowMap.value=lights.state.spotShadowMap;uniforms.spotLightMatrix.value=lights.state.spotLightMatrix;uniforms.spotLightMap.value=lights.state.spotLightMap;uniforms.pointShadowMap.value=lights.state.pointShadowMap;uniforms.pointShadowMatrix.value=lights.state.pointShadowMatrix;// TODO (abelnation): add area lights shadow info to uniforms
}materialProperties.currentProgram=program;materialProperties.uniformsList=null;return program;}function getUniformList(materialProperties){if(materialProperties.uniformsList===null){const progUniforms=materialProperties.currentProgram.getUniforms();materialProperties.uniformsList=WebGLUniforms.seqWithValue(progUniforms.seq,materialProperties.uniforms);}return materialProperties.uniformsList;}function updateCommonMaterialProperties(material,parameters){const materialProperties=properties.get(material);materialProperties.outputColorSpace=parameters.outputColorSpace;materialProperties.batching=parameters.batching;materialProperties.batchingColor=parameters.batchingColor;materialProperties.instancing=parameters.instancing;materialProperties.instancingColor=parameters.instancingColor;materialProperties.instancingMorph=parameters.instancingMorph;materialProperties.skinning=parameters.skinning;materialProperties.morphTargets=parameters.morphTargets;materialProperties.morphNormals=parameters.morphNormals;materialProperties.morphColors=parameters.morphColors;materialProperties.morphTargetsCount=parameters.morphTargetsCount;materialProperties.numClippingPlanes=parameters.numClippingPlanes;materialProperties.numIntersection=parameters.numClipIntersection;materialProperties.vertexAlphas=parameters.vertexAlphas;materialProperties.vertexTangents=parameters.vertexTangents;materialProperties.toneMapping=parameters.toneMapping;}function setProgram(camera,scene,geometry,material,object){if(scene.isScene!==true)scene=_emptyScene;// scene could be a Mesh, Line, Points, ...
textures.resetTextureUnits();const fog=scene.fog;const environment=material.isMeshStandardMaterial?scene.environment:null;const colorSpace=_currentRenderTarget===null?_this.outputColorSpace:_currentRenderTarget.isXRRenderTarget===true?_currentRenderTarget.texture.colorSpace:LinearSRGBColorSpace$1;const envMap=(material.isMeshStandardMaterial?cubeuvmaps:cubemaps).get(material.envMap||environment);const vertexAlphas=material.vertexColors===true&&!!geometry.attributes.color&&geometry.attributes.color.itemSize===4;const vertexTangents=!!geometry.attributes.tangent&&(!!material.normalMap||material.anisotropy>0);const morphTargets=!!geometry.morphAttributes.position;const morphNormals=!!geometry.morphAttributes.normal;const morphColors=!!geometry.morphAttributes.color;let toneMapping=NoToneMapping$1;if(material.toneMapped){if(_currentRenderTarget===null||_currentRenderTarget.isXRRenderTarget===true){toneMapping=_this.toneMapping;}}const morphAttribute=geometry.morphAttributes.position||geometry.morphAttributes.normal||geometry.morphAttributes.color;const morphTargetsCount=morphAttribute!==undefined?morphAttribute.length:0;const materialProperties=properties.get(material);const lights=currentRenderState.state.lights;if(_clippingEnabled===true){if(_localClippingEnabled===true||camera!==_currentCamera){const useCache=camera===_currentCamera&&material.id===_currentMaterialId;// we might want to call this function with some ClippingGroup
// object instead of the material, once it becomes feasible
// (#8465, #8379)
clipping.setState(material,camera,useCache);}}//
let needsProgramChange=false;if(material.version===materialProperties.__version){if(materialProperties.needsLights&&materialProperties.lightsStateVersion!==lights.state.version){needsProgramChange=true;}else if(materialProperties.outputColorSpace!==colorSpace){needsProgramChange=true;}else if(object.isBatchedMesh&&materialProperties.batching===false){needsProgramChange=true;}else if(!object.isBatchedMesh&&materialProperties.batching===true){needsProgramChange=true;}else if(object.isBatchedMesh&&materialProperties.batchingColor===true&&object.colorTexture===null){needsProgramChange=true;}else if(object.isBatchedMesh&&materialProperties.batchingColor===false&&object.colorTexture!==null){needsProgramChange=true;}else if(object.isInstancedMesh&&materialProperties.instancing===false){needsProgramChange=true;}else if(!object.isInstancedMesh&&materialProperties.instancing===true){needsProgramChange=true;}else if(object.isSkinnedMesh&&materialProperties.skinning===false){needsProgramChange=true;}else if(!object.isSkinnedMesh&&materialProperties.skinning===true){needsProgramChange=true;}else if(object.isInstancedMesh&&materialProperties.instancingColor===true&&object.instanceColor===null){needsProgramChange=true;}else if(object.isInstancedMesh&&materialProperties.instancingColor===false&&object.instanceColor!==null){needsProgramChange=true;}else if(object.isInstancedMesh&&materialProperties.instancingMorph===true&&object.morphTexture===null){needsProgramChange=true;}else if(object.isInstancedMesh&&materialProperties.instancingMorph===false&&object.morphTexture!==null){needsProgramChange=true;}else if(materialProperties.envMap!==envMap){needsProgramChange=true;}else if(material.fog===true&&materialProperties.fog!==fog){needsProgramChange=true;}else if(materialProperties.numClippingPlanes!==undefined&&(materialProperties.numClippingPlanes!==clipping.numPlanes||materialProperties.numIntersection!==clipping.numIntersection)){needsProgramChange=true;}else if(materialProperties.vertexAlphas!==vertexAlphas){needsProgramChange=true;}else if(materialProperties.vertexTangents!==vertexTangents){needsProgramChange=true;}else if(materialProperties.morphTargets!==morphTargets){needsProgramChange=true;}else if(materialProperties.morphNormals!==morphNormals){needsProgramChange=true;}else if(materialProperties.morphColors!==morphColors){needsProgramChange=true;}else if(materialProperties.toneMapping!==toneMapping){needsProgramChange=true;}else if(materialProperties.morphTargetsCount!==morphTargetsCount){needsProgramChange=true;}}else {needsProgramChange=true;materialProperties.__version=material.version;}//
let program=materialProperties.currentProgram;if(needsProgramChange===true){program=getProgram(material,scene,object);}let refreshProgram=false;let refreshMaterial=false;let refreshLights=false;const p_uniforms=program.getUniforms(),m_uniforms=materialProperties.uniforms;if(state.useProgram(program.program)){refreshProgram=true;refreshMaterial=true;refreshLights=true;}if(material.id!==_currentMaterialId){_currentMaterialId=material.id;refreshMaterial=true;}if(refreshProgram||_currentCamera!==camera){// common camera uniforms
p_uniforms.setValue(_gl,'projectionMatrix',camera.projectionMatrix);p_uniforms.setValue(_gl,'viewMatrix',camera.matrixWorldInverse);const uCamPos=p_uniforms.map.cameraPosition;if(uCamPos!==undefined){uCamPos.setValue(_gl,_vector3.setFromMatrixPosition(camera.matrixWorld));}if(capabilities.logarithmicDepthBuffer){p_uniforms.setValue(_gl,'logDepthBufFC',2.0/(Math.log(camera.far+1.0)/Math.LN2));}// consider moving isOrthographic to UniformLib and WebGLMaterials, see https://github.com/mrdoob/three.js/pull/26467#issuecomment-1645185067
if(material.isMeshPhongMaterial||material.isMeshToonMaterial||material.isMeshLambertMaterial||material.isMeshBasicMaterial||material.isMeshStandardMaterial||material.isShaderMaterial){p_uniforms.setValue(_gl,'isOrthographic',camera.isOrthographicCamera===true);}if(_currentCamera!==camera){_currentCamera=camera;// lighting uniforms depend on the camera so enforce an update
// now, in case this material supports lights - or later, when
// the next material that does gets activated:
refreshMaterial=true;// set to true on material change
refreshLights=true;// remains set until update done
}}// skinning and morph target uniforms must be set even if material didn't change
// auto-setting of texture unit for bone and morph texture must go before other textures
// otherwise textures used for skinning and morphing can take over texture units reserved for other material textures
if(object.isSkinnedMesh){p_uniforms.setOptional(_gl,object,'bindMatrix');p_uniforms.setOptional(_gl,object,'bindMatrixInverse');const skeleton=object.skeleton;if(skeleton){if(skeleton.boneTexture===null)skeleton.computeBoneTexture();p_uniforms.setValue(_gl,'boneTexture',skeleton.boneTexture,textures);}}if(object.isBatchedMesh){p_uniforms.setOptional(_gl,object,'batchingTexture');p_uniforms.setValue(_gl,'batchingTexture',object._matricesTexture,textures);p_uniforms.setOptional(_gl,object,'batchingIdTexture');p_uniforms.setValue(_gl,'batchingIdTexture',object._indirectTexture,textures);p_uniforms.setOptional(_gl,object,'batchingColorTexture');if(object._colorsTexture!==null){p_uniforms.setValue(_gl,'batchingColorTexture',object._colorsTexture,textures);}}const morphAttributes=geometry.morphAttributes;if(morphAttributes.position!==undefined||morphAttributes.normal!==undefined||morphAttributes.color!==undefined){morphtargets.update(object,geometry,program);}if(refreshMaterial||materialProperties.receiveShadow!==object.receiveShadow){materialProperties.receiveShadow=object.receiveShadow;p_uniforms.setValue(_gl,'receiveShadow',object.receiveShadow);}// https://github.com/mrdoob/three.js/pull/24467#issuecomment-1209031512
if(material.isMeshGouraudMaterial&&material.envMap!==null){m_uniforms.envMap.value=envMap;m_uniforms.flipEnvMap.value=envMap.isCubeTexture&&envMap.isRenderTargetTexture===false?-1:1;}if(material.isMeshStandardMaterial&&material.envMap===null&&scene.environment!==null){m_uniforms.envMapIntensity.value=scene.environmentIntensity;}if(refreshMaterial){p_uniforms.setValue(_gl,'toneMappingExposure',_this.toneMappingExposure);if(materialProperties.needsLights){// the current material requires lighting info
// note: all lighting uniforms are always set correctly
// they simply reference the renderer's state for their
// values
//
// use the current material's .needsUpdate flags to set
// the GL state when required
markUniformsLightsNeedsUpdate(m_uniforms,refreshLights);}// refresh uniforms common to several materials
if(fog&&material.fog===true){materials.refreshFogUniforms(m_uniforms,fog);}materials.refreshMaterialUniforms(m_uniforms,material,_pixelRatio,_height,currentRenderState.state.transmissionRenderTarget[camera.id]);WebGLUniforms.upload(_gl,getUniformList(materialProperties),m_uniforms,textures);}if(material.isShaderMaterial&&material.uniformsNeedUpdate===true){WebGLUniforms.upload(_gl,getUniformList(materialProperties),m_uniforms,textures);material.uniformsNeedUpdate=false;}if(material.isSpriteMaterial){p_uniforms.setValue(_gl,'center',object.center);}// common matrices
p_uniforms.setValue(_gl,'modelViewMatrix',object.modelViewMatrix);p_uniforms.setValue(_gl,'normalMatrix',object.normalMatrix);p_uniforms.setValue(_gl,'modelMatrix',object.matrixWorld);// UBOs
if(material.isShaderMaterial||material.isRawShaderMaterial){const groups=material.uniformsGroups;for(let i=0,l=groups.length;i<l;i++){const group=groups[i];uniformsGroups.update(group,program);uniformsGroups.bind(group,program);}}return program;}// If uniforms are marked as clean, they don't need to be loaded to the GPU.
function markUniformsLightsNeedsUpdate(uniforms,value){uniforms.ambientLightColor.needsUpdate=value;uniforms.lightProbe.needsUpdate=value;uniforms.directionalLights.needsUpdate=value;uniforms.directionalLightShadows.needsUpdate=value;uniforms.pointLights.needsUpdate=value;uniforms.pointLightShadows.needsUpdate=value;uniforms.spotLights.needsUpdate=value;uniforms.spotLightShadows.needsUpdate=value;uniforms.rectAreaLights.needsUpdate=value;uniforms.hemisphereLights.needsUpdate=value;}function materialNeedsLights(material){return material.isMeshLambertMaterial||material.isMeshToonMaterial||material.isMeshPhongMaterial||material.isMeshStandardMaterial||material.isShadowMaterial||material.isShaderMaterial&&material.lights===true;}this.getActiveCubeFace=function(){return _currentActiveCubeFace;};this.getActiveMipmapLevel=function(){return _currentActiveMipmapLevel;};this.getRenderTarget=function(){return _currentRenderTarget;};this.setRenderTargetTextures=function(renderTarget,colorTexture,depthTexture){properties.get(renderTarget.texture).__webglTexture=colorTexture;properties.get(renderTarget.depthTexture).__webglTexture=depthTexture;const renderTargetProperties=properties.get(renderTarget);renderTargetProperties.__hasExternalTextures=true;renderTargetProperties.__autoAllocateDepthBuffer=depthTexture===undefined;if(!renderTargetProperties.__autoAllocateDepthBuffer){// The multisample_render_to_texture extension doesn't work properly if there
// are midframe flushes and an external depth buffer. Disable use of the extension.
if(extensions.has('WEBGL_multisampled_render_to_texture')===true){console.warn('THREE.WebGLRenderer: Render-to-texture extension was disabled because an external texture was provided');renderTargetProperties.__useRenderToTexture=false;}}};this.setRenderTargetFramebuffer=function(renderTarget,defaultFramebuffer){const renderTargetProperties=properties.get(renderTarget);renderTargetProperties.__webglFramebuffer=defaultFramebuffer;renderTargetProperties.__useDefaultFramebuffer=defaultFramebuffer===undefined;};this.setRenderTarget=function(renderTarget,activeCubeFace=0,activeMipmapLevel=0){_currentRenderTarget=renderTarget;_currentActiveCubeFace=activeCubeFace;_currentActiveMipmapLevel=activeMipmapLevel;let useDefaultFramebuffer=true;let framebuffer=null;let isCube=false;let isRenderTarget3D=false;if(renderTarget){const renderTargetProperties=properties.get(renderTarget);if(renderTargetProperties.__useDefaultFramebuffer!==undefined){// We need to make sure to rebind the framebuffer.
state.bindFramebuffer(_gl.FRAMEBUFFER,null);useDefaultFramebuffer=false;}else if(renderTargetProperties.__webglFramebuffer===undefined){textures.setupRenderTarget(renderTarget);}else if(renderTargetProperties.__hasExternalTextures){// Color and depth texture must be rebound in order for the swapchain to update.
textures.rebindTextures(renderTarget,properties.get(renderTarget.texture).__webglTexture,properties.get(renderTarget.depthTexture).__webglTexture);}else if(renderTarget.depthBuffer){// check if the depth texture is already bound to the frame buffer and that it's been initialized
const depthTexture=renderTarget.depthTexture;if(renderTargetProperties.__boundDepthTexture!==depthTexture){// check if the depth texture is compatible
if(depthTexture!==null&&properties.has(depthTexture)&&(renderTarget.width!==depthTexture.image.width||renderTarget.height!==depthTexture.image.height)){throw new Error('WebGLRenderTarget: Attached DepthTexture is initialized to the incorrect size.');}// Swap the depth buffer to the currently attached one
textures.setupDepthRenderbuffer(renderTarget);}}const texture=renderTarget.texture;if(texture.isData3DTexture||texture.isDataArrayTexture||texture.isCompressedArrayTexture){isRenderTarget3D=true;}const __webglFramebuffer=properties.get(renderTarget).__webglFramebuffer;if(renderTarget.isWebGLCubeRenderTarget){if(Array.isArray(__webglFramebuffer[activeCubeFace])){framebuffer=__webglFramebuffer[activeCubeFace][activeMipmapLevel];}else {framebuffer=__webglFramebuffer[activeCubeFace];}isCube=true;}else if(renderTarget.samples>0&&textures.useMultisampledRTT(renderTarget)===false){framebuffer=properties.get(renderTarget).__webglMultisampledFramebuffer;}else {if(Array.isArray(__webglFramebuffer)){framebuffer=__webglFramebuffer[activeMipmapLevel];}else {framebuffer=__webglFramebuffer;}}_currentViewport.copy(renderTarget.viewport);_currentScissor.copy(renderTarget.scissor);_currentScissorTest=renderTarget.scissorTest;}else {_currentViewport.copy(_viewport).multiplyScalar(_pixelRatio).floor();_currentScissor.copy(_scissor).multiplyScalar(_pixelRatio).floor();_currentScissorTest=_scissorTest;}const framebufferBound=state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);if(framebufferBound&&useDefaultFramebuffer){state.drawBuffers(renderTarget,framebuffer);}state.viewport(_currentViewport);state.scissor(_currentScissor);state.setScissorTest(_currentScissorTest);if(isCube){const textureProperties=properties.get(renderTarget.texture);_gl.framebufferTexture2D(_gl.FRAMEBUFFER,_gl.COLOR_ATTACHMENT0,_gl.TEXTURE_CUBE_MAP_POSITIVE_X+activeCubeFace,textureProperties.__webglTexture,activeMipmapLevel);}else if(isRenderTarget3D){const textureProperties=properties.get(renderTarget.texture);const layer=activeCubeFace||0;_gl.framebufferTextureLayer(_gl.FRAMEBUFFER,_gl.COLOR_ATTACHMENT0,textureProperties.__webglTexture,activeMipmapLevel||0,layer);}_currentMaterialId=-1;// reset current material to ensure correct uniform bindings
};this.readRenderTargetPixels=function(renderTarget,x,y,width,height,buffer,activeCubeFaceIndex){if(!(renderTarget&&renderTarget.isWebGLRenderTarget)){console.error('THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.');return;}let framebuffer=properties.get(renderTarget).__webglFramebuffer;if(renderTarget.isWebGLCubeRenderTarget&&activeCubeFaceIndex!==undefined){framebuffer=framebuffer[activeCubeFaceIndex];}if(framebuffer){state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);try{const texture=renderTarget.texture;const textureFormat=texture.format;const textureType=texture.type;if(!capabilities.textureFormatReadable(textureFormat)){console.error('THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in RGBA or implementation defined format.');return;}if(!capabilities.textureTypeReadable(textureType)){console.error('THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in UnsignedByteType or implementation defined type.');return;}// the following if statement ensures valid read requests (no out-of-bounds pixels, see #8604)
if(x>=0&&x<=renderTarget.width-width&&y>=0&&y<=renderTarget.height-height){_gl.readPixels(x,y,width,height,utils.convert(textureFormat),utils.convert(textureType),buffer);}}finally{// restore framebuffer of current render target if necessary
const framebuffer=_currentRenderTarget!==null?properties.get(_currentRenderTarget).__webglFramebuffer:null;state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);}}};this.readRenderTargetPixelsAsync=async function(renderTarget,x,y,width,height,buffer,activeCubeFaceIndex){if(!(renderTarget&&renderTarget.isWebGLRenderTarget)){throw new Error('THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.');}let framebuffer=properties.get(renderTarget).__webglFramebuffer;if(renderTarget.isWebGLCubeRenderTarget&&activeCubeFaceIndex!==undefined){framebuffer=framebuffer[activeCubeFaceIndex];}if(framebuffer){state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);try{const texture=renderTarget.texture;const textureFormat=texture.format;const textureType=texture.type;if(!capabilities.textureFormatReadable(textureFormat)){throw new Error('THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in RGBA or implementation defined format.');}if(!capabilities.textureTypeReadable(textureType)){throw new Error('THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in UnsignedByteType or implementation defined type.');}// the following if statement ensures valid read requests (no out-of-bounds pixels, see #8604)
if(x>=0&&x<=renderTarget.width-width&&y>=0&&y<=renderTarget.height-height){const glBuffer=_gl.createBuffer();_gl.bindBuffer(_gl.PIXEL_PACK_BUFFER,glBuffer);_gl.bufferData(_gl.PIXEL_PACK_BUFFER,buffer.byteLength,_gl.STREAM_READ);_gl.readPixels(x,y,width,height,utils.convert(textureFormat),utils.convert(textureType),0);_gl.flush();// check if the commands have finished every 8 ms
const sync=_gl.fenceSync(_gl.SYNC_GPU_COMMANDS_COMPLETE,0);await probeAsync(_gl,sync,4);try{_gl.bindBuffer(_gl.PIXEL_PACK_BUFFER,glBuffer);_gl.getBufferSubData(_gl.PIXEL_PACK_BUFFER,0,buffer);}finally{_gl.deleteBuffer(glBuffer);_gl.deleteSync(sync);}return buffer;}}finally{// restore framebuffer of current render target if necessary
const framebuffer=_currentRenderTarget!==null?properties.get(_currentRenderTarget).__webglFramebuffer:null;state.bindFramebuffer(_gl.FRAMEBUFFER,framebuffer);}}};this.copyFramebufferToTexture=function(texture,position=null,level=0){// support previous signature with position first
if(texture.isTexture!==true){// @deprecated, r165
warnOnce$1('WebGLRenderer: copyFramebufferToTexture function signature has changed.');position=arguments[0]||null;texture=arguments[1];}const levelScale=Math.pow(2,-level);const width=Math.floor(texture.image.width*levelScale);const height=Math.floor(texture.image.height*levelScale);const x=position!==null?position.x:0;const y=position!==null?position.y:0;textures.setTexture2D(texture,0);_gl.copyTexSubImage2D(_gl.TEXTURE_2D,level,0,0,x,y,width,height);state.unbindTexture();};this.copyTextureToTexture=function(srcTexture,dstTexture,srcRegion=null,dstPosition=null,level=0){// support previous signature with dstPosition first
if(srcTexture.isTexture!==true){// @deprecated, r165
warnOnce$1('WebGLRenderer: copyTextureToTexture function signature has changed.');dstPosition=arguments[0]||null;srcTexture=arguments[1];dstTexture=arguments[2];level=arguments[3]||0;srcRegion=null;}let width,height,minX,minY;let dstX,dstY;if(srcRegion!==null){width=srcRegion.max.x-srcRegion.min.x;height=srcRegion.max.y-srcRegion.min.y;minX=srcRegion.min.x;minY=srcRegion.min.y;}else {width=srcTexture.image.width;height=srcTexture.image.height;minX=0;minY=0;}if(dstPosition!==null){dstX=dstPosition.x;dstY=dstPosition.y;}else {dstX=0;dstY=0;}const glFormat=utils.convert(dstTexture.format);const glType=utils.convert(dstTexture.type);textures.setTexture2D(dstTexture,0);// As another texture upload may have changed pixelStorei
// parameters, make sure they are correct for the dstTexture
_gl.pixelStorei(_gl.UNPACK_FLIP_Y_WEBGL,dstTexture.flipY);_gl.pixelStorei(_gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,dstTexture.premultiplyAlpha);_gl.pixelStorei(_gl.UNPACK_ALIGNMENT,dstTexture.unpackAlignment);const currentUnpackRowLen=_gl.getParameter(_gl.UNPACK_ROW_LENGTH);const currentUnpackImageHeight=_gl.getParameter(_gl.UNPACK_IMAGE_HEIGHT);const currentUnpackSkipPixels=_gl.getParameter(_gl.UNPACK_SKIP_PIXELS);const currentUnpackSkipRows=_gl.getParameter(_gl.UNPACK_SKIP_ROWS);const currentUnpackSkipImages=_gl.getParameter(_gl.UNPACK_SKIP_IMAGES);const image=srcTexture.isCompressedTexture?srcTexture.mipmaps[level]:srcTexture.image;_gl.pixelStorei(_gl.UNPACK_ROW_LENGTH,image.width);_gl.pixelStorei(_gl.UNPACK_IMAGE_HEIGHT,image.height);_gl.pixelStorei(_gl.UNPACK_SKIP_PIXELS,minX);_gl.pixelStorei(_gl.UNPACK_SKIP_ROWS,minY);if(srcTexture.isDataTexture){_gl.texSubImage2D(_gl.TEXTURE_2D,level,dstX,dstY,width,height,glFormat,glType,image.data);}else {if(srcTexture.isCompressedTexture){_gl.compressedTexSubImage2D(_gl.TEXTURE_2D,level,dstX,dstY,image.width,image.height,glFormat,image.data);}else {_gl.texSubImage2D(_gl.TEXTURE_2D,level,dstX,dstY,width,height,glFormat,glType,image);}}_gl.pixelStorei(_gl.UNPACK_ROW_LENGTH,currentUnpackRowLen);_gl.pixelStorei(_gl.UNPACK_IMAGE_HEIGHT,currentUnpackImageHeight);_gl.pixelStorei(_gl.UNPACK_SKIP_PIXELS,currentUnpackSkipPixels);_gl.pixelStorei(_gl.UNPACK_SKIP_ROWS,currentUnpackSkipRows);_gl.pixelStorei(_gl.UNPACK_SKIP_IMAGES,currentUnpackSkipImages);// Generate mipmaps only when copying level 0
if(level===0&&dstTexture.generateMipmaps)_gl.generateMipmap(_gl.TEXTURE_2D);state.unbindTexture();};this.copyTextureToTexture3D=function(srcTexture,dstTexture,srcRegion=null,dstPosition=null,level=0){// support previous signature with source box first
if(srcTexture.isTexture!==true){// @deprecated, r165
warnOnce$1('WebGLRenderer: copyTextureToTexture3D function signature has changed.');srcRegion=arguments[0]||null;dstPosition=arguments[1]||null;srcTexture=arguments[2];dstTexture=arguments[3];level=arguments[4]||0;}let width,height,depth,minX,minY,minZ;let dstX,dstY,dstZ;const image=srcTexture.isCompressedTexture?srcTexture.mipmaps[level]:srcTexture.image;if(srcRegion!==null){width=srcRegion.max.x-srcRegion.min.x;height=srcRegion.max.y-srcRegion.min.y;depth=srcRegion.max.z-srcRegion.min.z;minX=srcRegion.min.x;minY=srcRegion.min.y;minZ=srcRegion.min.z;}else {width=image.width;height=image.height;depth=image.depth;minX=0;minY=0;minZ=0;}if(dstPosition!==null){dstX=dstPosition.x;dstY=dstPosition.y;dstZ=dstPosition.z;}else {dstX=0;dstY=0;dstZ=0;}const glFormat=utils.convert(dstTexture.format);const glType=utils.convert(dstTexture.type);let glTarget;if(dstTexture.isData3DTexture){textures.setTexture3D(dstTexture,0);glTarget=_gl.TEXTURE_3D;}else if(dstTexture.isDataArrayTexture||dstTexture.isCompressedArrayTexture){textures.setTexture2DArray(dstTexture,0);glTarget=_gl.TEXTURE_2D_ARRAY;}else {console.warn('THREE.WebGLRenderer.copyTextureToTexture3D: only supports THREE.DataTexture3D and THREE.DataTexture2DArray.');return;}_gl.pixelStorei(_gl.UNPACK_FLIP_Y_WEBGL,dstTexture.flipY);_gl.pixelStorei(_gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,dstTexture.premultiplyAlpha);_gl.pixelStorei(_gl.UNPACK_ALIGNMENT,dstTexture.unpackAlignment);const currentUnpackRowLen=_gl.getParameter(_gl.UNPACK_ROW_LENGTH);const currentUnpackImageHeight=_gl.getParameter(_gl.UNPACK_IMAGE_HEIGHT);const currentUnpackSkipPixels=_gl.getParameter(_gl.UNPACK_SKIP_PIXELS);const currentUnpackSkipRows=_gl.getParameter(_gl.UNPACK_SKIP_ROWS);const currentUnpackSkipImages=_gl.getParameter(_gl.UNPACK_SKIP_IMAGES);_gl.pixelStorei(_gl.UNPACK_ROW_LENGTH,image.width);_gl.pixelStorei(_gl.UNPACK_IMAGE_HEIGHT,image.height);_gl.pixelStorei(_gl.UNPACK_SKIP_PIXELS,minX);_gl.pixelStorei(_gl.UNPACK_SKIP_ROWS,minY);_gl.pixelStorei(_gl.UNPACK_SKIP_IMAGES,minZ);if(srcTexture.isDataTexture||srcTexture.isData3DTexture){_gl.texSubImage3D(glTarget,level,dstX,dstY,dstZ,width,height,depth,glFormat,glType,image.data);}else {if(dstTexture.isCompressedArrayTexture){_gl.compressedTexSubImage3D(glTarget,level,dstX,dstY,dstZ,width,height,depth,glFormat,image.data);}else {_gl.texSubImage3D(glTarget,level,dstX,dstY,dstZ,width,height,depth,glFormat,glType,image);}}_gl.pixelStorei(_gl.UNPACK_ROW_LENGTH,currentUnpackRowLen);_gl.pixelStorei(_gl.UNPACK_IMAGE_HEIGHT,currentUnpackImageHeight);_gl.pixelStorei(_gl.UNPACK_SKIP_PIXELS,currentUnpackSkipPixels);_gl.pixelStorei(_gl.UNPACK_SKIP_ROWS,currentUnpackSkipRows);_gl.pixelStorei(_gl.UNPACK_SKIP_IMAGES,currentUnpackSkipImages);// Generate mipmaps only when copying level 0
if(level===0&&dstTexture.generateMipmaps)_gl.generateMipmap(glTarget);state.unbindTexture();};this.initRenderTarget=function(target){if(properties.get(target).__webglFramebuffer===undefined){textures.setupRenderTarget(target);}};this.initTexture=function(texture){if(texture.isCubeTexture){textures.setTextureCube(texture,0);}else if(texture.isData3DTexture){textures.setTexture3D(texture,0);}else if(texture.isDataArrayTexture||texture.isCompressedArrayTexture){textures.setTexture2DArray(texture,0);}else {textures.setTexture2D(texture,0);}state.unbindTexture();};this.resetState=function(){_currentActiveCubeFace=0;_currentActiveMipmapLevel=0;_currentRenderTarget=null;state.reset();bindingStates.reset();};if(typeof __THREE_DEVTOOLS__!=='undefined'){__THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe',{detail:this}));}}get coordinateSystem(){return WebGLCoordinateSystem$1;}get outputColorSpace(){return this._outputColorSpace;}set outputColorSpace(colorSpace){this._outputColorSpace=colorSpace;const gl=this.getContext();gl.drawingBufferColorSpace=colorSpace===DisplayP3ColorSpace$1?'display-p3':'srgb';gl.unpackColorSpace=ColorManagement$1.workingColorSpace===LinearDisplayP3ColorSpace$1?'display-p3':'srgb';}}class Scene extends Object3D$1{constructor(){super();this.isScene=true;this.type='Scene';this.background=null;this.environment=null;this.fog=null;this.backgroundBlurriness=0;this.backgroundIntensity=1;this.backgroundRotation=new Euler$1();this.environmentIntensity=1;this.environmentRotation=new Euler$1();this.overrideMaterial=null;if(typeof __THREE_DEVTOOLS__!=='undefined'){__THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe',{detail:this}));}}copy(source,recursive){super.copy(source,recursive);if(source.background!==null)this.background=source.background.clone();if(source.environment!==null)this.environment=source.environment.clone();if(source.fog!==null)this.fog=source.fog.clone();this.backgroundBlurriness=source.backgroundBlurriness;this.backgroundIntensity=source.backgroundIntensity;this.backgroundRotation.copy(source.backgroundRotation);this.environmentIntensity=source.environmentIntensity;this.environmentRotation.copy(source.environmentRotation);if(source.overrideMaterial!==null)this.overrideMaterial=source.overrideMaterial.clone();this.matrixAutoUpdate=source.matrixAutoUpdate;return this;}toJSON(meta){const data=super.toJSON(meta);if(this.fog!==null)data.object.fog=this.fog.toJSON();if(this.backgroundBlurriness>0)data.object.backgroundBlurriness=this.backgroundBlurriness;if(this.backgroundIntensity!==1)data.object.backgroundIntensity=this.backgroundIntensity;data.object.backgroundRotation=this.backgroundRotation.toArray();if(this.environmentIntensity!==1)data.object.environmentIntensity=this.environmentIntensity;data.object.environmentRotation=this.environmentRotation.toArray();return data;}}class InterleavedBuffer$1{constructor(array,stride){this.isInterleavedBuffer=true;this.array=array;this.stride=stride;this.count=array!==undefined?array.length/stride:0;this.usage=StaticDrawUsage$1;this._updateRange={offset:0,count:-1};this.updateRanges=[];this.version=0;this.uuid=generateUUID$1();}onUploadCallback(){}set needsUpdate(value){if(value===true)this.version++;}get updateRange(){warnOnce$1('THREE.InterleavedBuffer: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead.');// @deprecated, r159
return this._updateRange;}setUsage(value){this.usage=value;return this;}addUpdateRange(start,count){this.updateRanges.push({start,count});}clearUpdateRanges(){this.updateRanges.length=0;}copy(source){this.array=new source.array.constructor(source.array);this.count=source.count;this.stride=source.stride;this.usage=source.usage;return this;}copyAt(index1,attribute,index2){index1*=this.stride;index2*=attribute.stride;for(let i=0,l=this.stride;i<l;i++){this.array[index1+i]=attribute.array[index2+i];}return this;}set(value,offset=0){this.array.set(value,offset);return this;}clone(data){if(data.arrayBuffers===undefined){data.arrayBuffers={};}if(this.array.buffer._uuid===undefined){this.array.buffer._uuid=generateUUID$1();}if(data.arrayBuffers[this.array.buffer._uuid]===undefined){data.arrayBuffers[this.array.buffer._uuid]=this.array.slice(0).buffer;}const array=new this.array.constructor(data.arrayBuffers[this.array.buffer._uuid]);const ib=new this.constructor(array,this.stride);ib.setUsage(this.usage);return ib;}onUpload(callback){this.onUploadCallback=callback;return this;}toJSON(data){if(data.arrayBuffers===undefined){data.arrayBuffers={};}// generate UUID for array buffer if necessary
if(this.array.buffer._uuid===undefined){this.array.buffer._uuid=generateUUID$1();}if(data.arrayBuffers[this.array.buffer._uuid]===undefined){data.arrayBuffers[this.array.buffer._uuid]=Array.from(new Uint32Array(this.array.buffer));}//
return {uuid:this.uuid,buffer:this.array.buffer._uuid,type:this.array.constructor.name,stride:this.stride};}}const _vector$6=/*@__PURE__*/new Vector3$1();class InterleavedBufferAttribute$1{constructor(interleavedBuffer,itemSize,offset,normalized=false){this.isInterleavedBufferAttribute=true;this.name='';this.data=interleavedBuffer;this.itemSize=itemSize;this.offset=offset;this.normalized=normalized;}get count(){return this.data.count;}get array(){return this.data.array;}set needsUpdate(value){this.data.needsUpdate=value;}applyMatrix4(m){for(let i=0,l=this.data.count;i<l;i++){_vector$6.fromBufferAttribute(this,i);_vector$6.applyMatrix4(m);this.setXYZ(i,_vector$6.x,_vector$6.y,_vector$6.z);}return this;}applyNormalMatrix(m){for(let i=0,l=this.count;i<l;i++){_vector$6.fromBufferAttribute(this,i);_vector$6.applyNormalMatrix(m);this.setXYZ(i,_vector$6.x,_vector$6.y,_vector$6.z);}return this;}transformDirection(m){for(let i=0,l=this.count;i<l;i++){_vector$6.fromBufferAttribute(this,i);_vector$6.transformDirection(m);this.setXYZ(i,_vector$6.x,_vector$6.y,_vector$6.z);}return this;}getComponent(index,component){let value=this.array[index*this.data.stride+this.offset+component];if(this.normalized)value=denormalize$1(value,this.array);return value;}setComponent(index,component,value){if(this.normalized)value=normalize$2(value,this.array);this.data.array[index*this.data.stride+this.offset+component]=value;return this;}setX(index,x){if(this.normalized)x=normalize$2(x,this.array);this.data.array[index*this.data.stride+this.offset]=x;return this;}setY(index,y){if(this.normalized)y=normalize$2(y,this.array);this.data.array[index*this.data.stride+this.offset+1]=y;return this;}setZ(index,z){if(this.normalized)z=normalize$2(z,this.array);this.data.array[index*this.data.stride+this.offset+2]=z;return this;}setW(index,w){if(this.normalized)w=normalize$2(w,this.array);this.data.array[index*this.data.stride+this.offset+3]=w;return this;}getX(index){let x=this.data.array[index*this.data.stride+this.offset];if(this.normalized)x=denormalize$1(x,this.array);return x;}getY(index){let y=this.data.array[index*this.data.stride+this.offset+1];if(this.normalized)y=denormalize$1(y,this.array);return y;}getZ(index){let z=this.data.array[index*this.data.stride+this.offset+2];if(this.normalized)z=denormalize$1(z,this.array);return z;}getW(index){let w=this.data.array[index*this.data.stride+this.offset+3];if(this.normalized)w=denormalize$1(w,this.array);return w;}setXY(index,x,y){index=index*this.data.stride+this.offset;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);}this.data.array[index+0]=x;this.data.array[index+1]=y;return this;}setXYZ(index,x,y,z){index=index*this.data.stride+this.offset;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);z=normalize$2(z,this.array);}this.data.array[index+0]=x;this.data.array[index+1]=y;this.data.array[index+2]=z;return this;}setXYZW(index,x,y,z,w){index=index*this.data.stride+this.offset;if(this.normalized){x=normalize$2(x,this.array);y=normalize$2(y,this.array);z=normalize$2(z,this.array);w=normalize$2(w,this.array);}this.data.array[index+0]=x;this.data.array[index+1]=y;this.data.array[index+2]=z;this.data.array[index+3]=w;return this;}clone(data){if(data===undefined){console.log('THREE.InterleavedBufferAttribute.clone(): Cloning an interleaved buffer attribute will de-interleave buffer data.');const array=[];for(let i=0;i<this.count;i++){const index=i*this.data.stride+this.offset;for(let j=0;j<this.itemSize;j++){array.push(this.data.array[index+j]);}}return new BufferAttribute$1(new this.array.constructor(array),this.itemSize,this.normalized);}else {if(data.interleavedBuffers===undefined){data.interleavedBuffers={};}if(data.interleavedBuffers[this.data.uuid]===undefined){data.interleavedBuffers[this.data.uuid]=this.data.clone(data);}return new InterleavedBufferAttribute$1(data.interleavedBuffers[this.data.uuid],this.itemSize,this.offset,this.normalized);}}toJSON(data){if(data===undefined){console.log('THREE.InterleavedBufferAttribute.toJSON(): Serializing an interleaved buffer attribute will de-interleave buffer data.');const array=[];for(let i=0;i<this.count;i++){const index=i*this.data.stride+this.offset;for(let j=0;j<this.itemSize;j++){array.push(this.data.array[index+j]);}}// de-interleave data and save it as an ordinary buffer attribute for now
return {itemSize:this.itemSize,type:this.array.constructor.name,array:array,normalized:this.normalized};}else {// save as true interleaved attribute
if(data.interleavedBuffers===undefined){data.interleavedBuffers={};}if(data.interleavedBuffers[this.data.uuid]===undefined){data.interleavedBuffers[this.data.uuid]=this.data.toJSON(data);}return {isInterleavedBufferAttribute:true,itemSize:this.itemSize,data:this.data.uuid,offset:this.offset,normalized:this.normalized};}}}const _basePosition=/*@__PURE__*/new Vector3$1();const _skinIndex=/*@__PURE__*/new Vector4$1();const _skinWeight=/*@__PURE__*/new Vector4$1();const _vector3=/*@__PURE__*/new Vector3$1();const _matrix4=/*@__PURE__*/new Matrix4$1();const _vertex=/*@__PURE__*/new Vector3$1();const _sphere$4=/*@__PURE__*/new Sphere$1();const _inverseMatrix$2=/*@__PURE__*/new Matrix4$1();const _ray$2=/*@__PURE__*/new Ray$1();class SkinnedMesh extends Mesh$1{constructor(geometry,material){super(geometry,material);this.isSkinnedMesh=true;this.type='SkinnedMesh';this.bindMode=AttachedBindMode;this.bindMatrix=new Matrix4$1();this.bindMatrixInverse=new Matrix4$1();this.boundingBox=null;this.boundingSphere=null;}computeBoundingBox(){const geometry=this.geometry;if(this.boundingBox===null){this.boundingBox=new Box3$1();}this.boundingBox.makeEmpty();const positionAttribute=geometry.getAttribute('position');for(let i=0;i<positionAttribute.count;i++){this.getVertexPosition(i,_vertex);this.boundingBox.expandByPoint(_vertex);}}computeBoundingSphere(){const geometry=this.geometry;if(this.boundingSphere===null){this.boundingSphere=new Sphere$1();}this.boundingSphere.makeEmpty();const positionAttribute=geometry.getAttribute('position');for(let i=0;i<positionAttribute.count;i++){this.getVertexPosition(i,_vertex);this.boundingSphere.expandByPoint(_vertex);}}copy(source,recursive){super.copy(source,recursive);this.bindMode=source.bindMode;this.bindMatrix.copy(source.bindMatrix);this.bindMatrixInverse.copy(source.bindMatrixInverse);this.skeleton=source.skeleton;if(source.boundingBox!==null)this.boundingBox=source.boundingBox.clone();if(source.boundingSphere!==null)this.boundingSphere=source.boundingSphere.clone();return this;}raycast(raycaster,intersects){const material=this.material;const matrixWorld=this.matrixWorld;if(material===undefined)return;// test with bounding sphere in world space
if(this.boundingSphere===null)this.computeBoundingSphere();_sphere$4.copy(this.boundingSphere);_sphere$4.applyMatrix4(matrixWorld);if(raycaster.ray.intersectsSphere(_sphere$4)===false)return;// convert ray to local space of skinned mesh
_inverseMatrix$2.copy(matrixWorld).invert();_ray$2.copy(raycaster.ray).applyMatrix4(_inverseMatrix$2);// test with bounding box in local space
if(this.boundingBox!==null){if(_ray$2.intersectsBox(this.boundingBox)===false)return;}// test for intersections with geometry
this._computeIntersections(raycaster,intersects,_ray$2);}getVertexPosition(index,target){super.getVertexPosition(index,target);this.applyBoneTransform(index,target);return target;}bind(skeleton,bindMatrix){this.skeleton=skeleton;if(bindMatrix===undefined){this.updateMatrixWorld(true);this.skeleton.calculateInverses();bindMatrix=this.matrixWorld;}this.bindMatrix.copy(bindMatrix);this.bindMatrixInverse.copy(bindMatrix).invert();}pose(){this.skeleton.pose();}normalizeSkinWeights(){const vector=new Vector4$1();const skinWeight=this.geometry.attributes.skinWeight;for(let i=0,l=skinWeight.count;i<l;i++){vector.fromBufferAttribute(skinWeight,i);const scale=1.0/vector.manhattanLength();if(scale!==Infinity){vector.multiplyScalar(scale);}else {vector.set(1,0,0,0);// do something reasonable
}skinWeight.setXYZW(i,vector.x,vector.y,vector.z,vector.w);}}updateMatrixWorld(force){super.updateMatrixWorld(force);if(this.bindMode===AttachedBindMode){this.bindMatrixInverse.copy(this.matrixWorld).invert();}else if(this.bindMode===DetachedBindMode){this.bindMatrixInverse.copy(this.bindMatrix).invert();}else {console.warn('THREE.SkinnedMesh: Unrecognized bindMode: '+this.bindMode);}}applyBoneTransform(index,vector){const skeleton=this.skeleton;const geometry=this.geometry;_skinIndex.fromBufferAttribute(geometry.attributes.skinIndex,index);_skinWeight.fromBufferAttribute(geometry.attributes.skinWeight,index);_basePosition.copy(vector).applyMatrix4(this.bindMatrix);vector.set(0,0,0);for(let i=0;i<4;i++){const weight=_skinWeight.getComponent(i);if(weight!==0){const boneIndex=_skinIndex.getComponent(i);_matrix4.multiplyMatrices(skeleton.bones[boneIndex].matrixWorld,skeleton.boneInverses[boneIndex]);vector.addScaledVector(_vector3.copy(_basePosition).applyMatrix4(_matrix4),weight);}}return vector.applyMatrix4(this.bindMatrixInverse);}}class Bone extends Object3D$1{constructor(){super();this.isBone=true;this.type='Bone';}}class DataTexture$1 extends Texture$1{constructor(data=null,width=1,height=1,format,type,mapping,wrapS,wrapT,magFilter=NearestFilter$1,minFilter=NearestFilter$1,anisotropy,colorSpace){super(null,mapping,wrapS,wrapT,magFilter,minFilter,format,type,anisotropy,colorSpace);this.isDataTexture=true;this.image={data:data,width:width,height:height};this.generateMipmaps=false;this.flipY=false;this.unpackAlignment=1;}}const _offsetMatrix=/*@__PURE__*/new Matrix4$1();const _identityMatrix$1=/*@__PURE__*/new Matrix4$1();class Skeleton{constructor(bones=[],boneInverses=[]){this.uuid=generateUUID$1();this.bones=bones.slice(0);this.boneInverses=boneInverses;this.boneMatrices=null;this.boneTexture=null;this.init();}init(){const bones=this.bones;const boneInverses=this.boneInverses;this.boneMatrices=new Float32Array(bones.length*16);// calculate inverse bone matrices if necessary
if(boneInverses.length===0){this.calculateInverses();}else {// handle special case
if(bones.length!==boneInverses.length){console.warn('THREE.Skeleton: Number of inverse bone matrices does not match amount of bones.');this.boneInverses=[];for(let i=0,il=this.bones.length;i<il;i++){this.boneInverses.push(new Matrix4$1());}}}}calculateInverses(){this.boneInverses.length=0;for(let i=0,il=this.bones.length;i<il;i++){const inverse=new Matrix4$1();if(this.bones[i]){inverse.copy(this.bones[i].matrixWorld).invert();}this.boneInverses.push(inverse);}}pose(){// recover the bind-time world matrices
for(let i=0,il=this.bones.length;i<il;i++){const bone=this.bones[i];if(bone){bone.matrixWorld.copy(this.boneInverses[i]).invert();}}// compute the local matrices, positions, rotations and scales
for(let i=0,il=this.bones.length;i<il;i++){const bone=this.bones[i];if(bone){if(bone.parent&&bone.parent.isBone){bone.matrix.copy(bone.parent.matrixWorld).invert();bone.matrix.multiply(bone.matrixWorld);}else {bone.matrix.copy(bone.matrixWorld);}bone.matrix.decompose(bone.position,bone.quaternion,bone.scale);}}}update(){const bones=this.bones;const boneInverses=this.boneInverses;const boneMatrices=this.boneMatrices;const boneTexture=this.boneTexture;// flatten bone matrices to array
for(let i=0,il=bones.length;i<il;i++){// compute the offset between the current and the original transform
const matrix=bones[i]?bones[i].matrixWorld:_identityMatrix$1;_offsetMatrix.multiplyMatrices(matrix,boneInverses[i]);_offsetMatrix.toArray(boneMatrices,i*16);}if(boneTexture!==null){boneTexture.needsUpdate=true;}}clone(){return new Skeleton(this.bones,this.boneInverses);}computeBoneTexture(){// layout (1 matrix = 4 pixels)
//      RGBA RGBA RGBA RGBA (=> column1, column2, column3, column4)
//  with  8x8  pixel texture max   16 bones * 4 pixels =  (8 * 8)
//       16x16 pixel texture max   64 bones * 4 pixels = (16 * 16)
//       32x32 pixel texture max  256 bones * 4 pixels = (32 * 32)
//       64x64 pixel texture max 1024 bones * 4 pixels = (64 * 64)
let size=Math.sqrt(this.bones.length*4);// 4 pixels needed for 1 matrix
size=Math.ceil(size/4)*4;size=Math.max(size,4);const boneMatrices=new Float32Array(size*size*4);// 4 floats per RGBA pixel
boneMatrices.set(this.boneMatrices);// copy current values
const boneTexture=new DataTexture$1(boneMatrices,size,size,RGBAFormat$1,FloatType$1);boneTexture.needsUpdate=true;this.boneMatrices=boneMatrices;this.boneTexture=boneTexture;return this;}getBoneByName(name){for(let i=0,il=this.bones.length;i<il;i++){const bone=this.bones[i];if(bone.name===name){return bone;}}return undefined;}dispose(){if(this.boneTexture!==null){this.boneTexture.dispose();this.boneTexture=null;}}fromJSON(json,bones){this.uuid=json.uuid;for(let i=0,l=json.bones.length;i<l;i++){const uuid=json.bones[i];let bone=bones[uuid];if(bone===undefined){console.warn('THREE.Skeleton: No bone found with UUID:',uuid);bone=new Bone();}this.bones.push(bone);this.boneInverses.push(new Matrix4$1().fromArray(json.boneInverses[i]));}this.init();return this;}toJSON(){const data={metadata:{version:4.6,type:'Skeleton',generator:'Skeleton.toJSON'},bones:[],boneInverses:[]};data.uuid=this.uuid;const bones=this.bones;const boneInverses=this.boneInverses;for(let i=0,l=bones.length;i<l;i++){const bone=bones[i];data.bones.push(bone.uuid);const boneInverse=boneInverses[i];data.boneInverses.push(boneInverse.toArray());}return data;}}class InstancedBufferAttribute$1 extends BufferAttribute$1{constructor(array,itemSize,normalized,meshPerAttribute=1){super(array,itemSize,normalized);this.isInstancedBufferAttribute=true;this.meshPerAttribute=meshPerAttribute;}copy(source){super.copy(source);this.meshPerAttribute=source.meshPerAttribute;return this;}toJSON(){const data=super.toJSON();data.meshPerAttribute=this.meshPerAttribute;data.isInstancedBufferAttribute=true;return data;}}const _instanceLocalMatrix=/*@__PURE__*/new Matrix4$1();const _instanceWorldMatrix=/*@__PURE__*/new Matrix4$1();const _instanceIntersects=[];const _box3=/*@__PURE__*/new Box3$1();const _identity=/*@__PURE__*/new Matrix4$1();const _mesh$1=/*@__PURE__*/new Mesh$1();const _sphere$3=/*@__PURE__*/new Sphere$1();class InstancedMesh extends Mesh$1{constructor(geometry,material,count){super(geometry,material);this.isInstancedMesh=true;this.instanceMatrix=new InstancedBufferAttribute$1(new Float32Array(count*16),16);this.instanceColor=null;this.morphTexture=null;this.count=count;this.boundingBox=null;this.boundingSphere=null;for(let i=0;i<count;i++){this.setMatrixAt(i,_identity);}}computeBoundingBox(){const geometry=this.geometry;const count=this.count;if(this.boundingBox===null){this.boundingBox=new Box3$1();}if(geometry.boundingBox===null){geometry.computeBoundingBox();}this.boundingBox.makeEmpty();for(let i=0;i<count;i++){this.getMatrixAt(i,_instanceLocalMatrix);_box3.copy(geometry.boundingBox).applyMatrix4(_instanceLocalMatrix);this.boundingBox.union(_box3);}}computeBoundingSphere(){const geometry=this.geometry;const count=this.count;if(this.boundingSphere===null){this.boundingSphere=new Sphere$1();}if(geometry.boundingSphere===null){geometry.computeBoundingSphere();}this.boundingSphere.makeEmpty();for(let i=0;i<count;i++){this.getMatrixAt(i,_instanceLocalMatrix);_sphere$3.copy(geometry.boundingSphere).applyMatrix4(_instanceLocalMatrix);this.boundingSphere.union(_sphere$3);}}copy(source,recursive){super.copy(source,recursive);this.instanceMatrix.copy(source.instanceMatrix);if(source.morphTexture!==null)this.morphTexture=source.morphTexture.clone();if(source.instanceColor!==null)this.instanceColor=source.instanceColor.clone();this.count=source.count;if(source.boundingBox!==null)this.boundingBox=source.boundingBox.clone();if(source.boundingSphere!==null)this.boundingSphere=source.boundingSphere.clone();return this;}getColorAt(index,color){color.fromArray(this.instanceColor.array,index*3);}getMatrixAt(index,matrix){matrix.fromArray(this.instanceMatrix.array,index*16);}getMorphAt(index,object){const objectInfluences=object.morphTargetInfluences;const array=this.morphTexture.source.data.data;const len=objectInfluences.length+1;// All influences + the baseInfluenceSum
const dataIndex=index*len+1;// Skip the baseInfluenceSum at the beginning
for(let i=0;i<objectInfluences.length;i++){objectInfluences[i]=array[dataIndex+i];}}raycast(raycaster,intersects){const matrixWorld=this.matrixWorld;const raycastTimes=this.count;_mesh$1.geometry=this.geometry;_mesh$1.material=this.material;if(_mesh$1.material===undefined)return;// test with bounding sphere first
if(this.boundingSphere===null)this.computeBoundingSphere();_sphere$3.copy(this.boundingSphere);_sphere$3.applyMatrix4(matrixWorld);if(raycaster.ray.intersectsSphere(_sphere$3)===false)return;// now test each instance
for(let instanceId=0;instanceId<raycastTimes;instanceId++){// calculate the world matrix for each instance
this.getMatrixAt(instanceId,_instanceLocalMatrix);_instanceWorldMatrix.multiplyMatrices(matrixWorld,_instanceLocalMatrix);// the mesh represents this single instance
_mesh$1.matrixWorld=_instanceWorldMatrix;_mesh$1.raycast(raycaster,_instanceIntersects);// process the result of raycast
for(let i=0,l=_instanceIntersects.length;i<l;i++){const intersect=_instanceIntersects[i];intersect.instanceId=instanceId;intersect.object=this;intersects.push(intersect);}_instanceIntersects.length=0;}}setColorAt(index,color){if(this.instanceColor===null){this.instanceColor=new InstancedBufferAttribute$1(new Float32Array(this.instanceMatrix.count*3).fill(1),3);}color.toArray(this.instanceColor.array,index*3);}setMatrixAt(index,matrix){matrix.toArray(this.instanceMatrix.array,index*16);}setMorphAt(index,object){const objectInfluences=object.morphTargetInfluences;const len=objectInfluences.length+1;// morphBaseInfluence + all influences
if(this.morphTexture===null){this.morphTexture=new DataTexture$1(new Float32Array(len*this.count),len,this.count,RedFormat,FloatType$1);}const array=this.morphTexture.source.data.data;let morphInfluencesSum=0;for(let i=0;i<objectInfluences.length;i++){morphInfluencesSum+=objectInfluences[i];}const morphBaseInfluence=this.geometry.morphTargetsRelative?1:1-morphInfluencesSum;const dataIndex=len*index;array[dataIndex]=morphBaseInfluence;array.set(objectInfluences,dataIndex+1);}updateMorphTargets(){}dispose(){this.dispatchEvent({type:'dispose'});if(this.morphTexture!==null){this.morphTexture.dispose();this.morphTexture=null;}return this;}}class LineBasicMaterial extends Material$1{constructor(parameters){super();this.isLineBasicMaterial=true;this.type='LineBasicMaterial';this.color=new Color$1(0xffffff);this.map=null;this.linewidth=1;this.linecap='round';this.linejoin='round';this.fog=true;this.setValues(parameters);}copy(source){super.copy(source);this.color.copy(source.color);this.map=source.map;this.linewidth=source.linewidth;this.linecap=source.linecap;this.linejoin=source.linejoin;this.fog=source.fog;return this;}}const _vStart=/*@__PURE__*/new Vector3$1();const _vEnd=/*@__PURE__*/new Vector3$1();const _inverseMatrix$1=/*@__PURE__*/new Matrix4$1();const _ray$1$1=/*@__PURE__*/new Ray$1();const _sphere$1=/*@__PURE__*/new Sphere$1();const _intersectPointOnRay=/*@__PURE__*/new Vector3$1();const _intersectPointOnSegment=/*@__PURE__*/new Vector3$1();class Line extends Object3D$1{constructor(geometry=new BufferGeometry$1(),material=new LineBasicMaterial()){super();this.isLine=true;this.type='Line';this.geometry=geometry;this.material=material;this.updateMorphTargets();}copy(source,recursive){super.copy(source,recursive);this.material=Array.isArray(source.material)?source.material.slice():source.material;this.geometry=source.geometry;return this;}computeLineDistances(){const geometry=this.geometry;// we assume non-indexed geometry
if(geometry.index===null){const positionAttribute=geometry.attributes.position;const lineDistances=[0];for(let i=1,l=positionAttribute.count;i<l;i++){_vStart.fromBufferAttribute(positionAttribute,i-1);_vEnd.fromBufferAttribute(positionAttribute,i);lineDistances[i]=lineDistances[i-1];lineDistances[i]+=_vStart.distanceTo(_vEnd);}geometry.setAttribute('lineDistance',new Float32BufferAttribute$1(lineDistances,1));}else {console.warn('THREE.Line.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.');}return this;}raycast(raycaster,intersects){const geometry=this.geometry;const matrixWorld=this.matrixWorld;const threshold=raycaster.params.Line.threshold;const drawRange=geometry.drawRange;// Checking boundingSphere distance to ray
if(geometry.boundingSphere===null)geometry.computeBoundingSphere();_sphere$1.copy(geometry.boundingSphere);_sphere$1.applyMatrix4(matrixWorld);_sphere$1.radius+=threshold;if(raycaster.ray.intersectsSphere(_sphere$1)===false)return;//
_inverseMatrix$1.copy(matrixWorld).invert();_ray$1$1.copy(raycaster.ray).applyMatrix4(_inverseMatrix$1);const localThreshold=threshold/((this.scale.x+this.scale.y+this.scale.z)/3);const localThresholdSq=localThreshold*localThreshold;const step=this.isLineSegments?2:1;const index=geometry.index;const attributes=geometry.attributes;const positionAttribute=attributes.position;if(index!==null){const start=Math.max(0,drawRange.start);const end=Math.min(index.count,drawRange.start+drawRange.count);for(let i=start,l=end-1;i<l;i+=step){const a=index.getX(i);const b=index.getX(i+1);const intersect=checkIntersection$2(this,raycaster,_ray$1$1,localThresholdSq,a,b);if(intersect){intersects.push(intersect);}}if(this.isLineLoop){const a=index.getX(end-1);const b=index.getX(start);const intersect=checkIntersection$2(this,raycaster,_ray$1$1,localThresholdSq,a,b);if(intersect){intersects.push(intersect);}}}else {const start=Math.max(0,drawRange.start);const end=Math.min(positionAttribute.count,drawRange.start+drawRange.count);for(let i=start,l=end-1;i<l;i+=step){const intersect=checkIntersection$2(this,raycaster,_ray$1$1,localThresholdSq,i,i+1);if(intersect){intersects.push(intersect);}}if(this.isLineLoop){const intersect=checkIntersection$2(this,raycaster,_ray$1$1,localThresholdSq,end-1,start);if(intersect){intersects.push(intersect);}}}}updateMorphTargets(){const geometry=this.geometry;const morphAttributes=geometry.morphAttributes;const keys=Object.keys(morphAttributes);if(keys.length>0){const morphAttribute=morphAttributes[keys[0]];if(morphAttribute!==undefined){this.morphTargetInfluences=[];this.morphTargetDictionary={};for(let m=0,ml=morphAttribute.length;m<ml;m++){const name=morphAttribute[m].name||String(m);this.morphTargetInfluences.push(0);this.morphTargetDictionary[name]=m;}}}}}function checkIntersection$2(object,raycaster,ray,thresholdSq,a,b){const positionAttribute=object.geometry.attributes.position;_vStart.fromBufferAttribute(positionAttribute,a);_vEnd.fromBufferAttribute(positionAttribute,b);const distSq=ray.distanceSqToSegment(_vStart,_vEnd,_intersectPointOnRay,_intersectPointOnSegment);if(distSq>thresholdSq)return;_intersectPointOnRay.applyMatrix4(object.matrixWorld);// Move back to world space for distance calculation
const distance=raycaster.ray.origin.distanceTo(_intersectPointOnRay);if(distance<raycaster.near||distance>raycaster.far)return;return {distance:distance,// What do we want? intersection point on the ray or on the segment??
// point: raycaster.ray.at( distance ),
point:_intersectPointOnSegment.clone().applyMatrix4(object.matrixWorld),index:a,face:null,faceIndex:null,object:object};}const _start=/*@__PURE__*/new Vector3$1();const _end=/*@__PURE__*/new Vector3$1();class LineSegments extends Line{constructor(geometry,material){super(geometry,material);this.isLineSegments=true;this.type='LineSegments';}computeLineDistances(){const geometry=this.geometry;// we assume non-indexed geometry
if(geometry.index===null){const positionAttribute=geometry.attributes.position;const lineDistances=[];for(let i=0,l=positionAttribute.count;i<l;i+=2){_start.fromBufferAttribute(positionAttribute,i);_end.fromBufferAttribute(positionAttribute,i+1);lineDistances[i]=i===0?0:lineDistances[i-1];lineDistances[i+1]=lineDistances[i]+_start.distanceTo(_end);}geometry.setAttribute('lineDistance',new Float32BufferAttribute$1(lineDistances,1));}else {console.warn('THREE.LineSegments.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.');}return this;}}class LineLoop extends Line{constructor(geometry,material){super(geometry,material);this.isLineLoop=true;this.type='LineLoop';}}class PointsMaterial extends Material$1{constructor(parameters){super();this.isPointsMaterial=true;this.type='PointsMaterial';this.color=new Color$1(0xffffff);this.map=null;this.alphaMap=null;this.size=1;this.sizeAttenuation=true;this.fog=true;this.setValues(parameters);}copy(source){super.copy(source);this.color.copy(source.color);this.map=source.map;this.alphaMap=source.alphaMap;this.size=source.size;this.sizeAttenuation=source.sizeAttenuation;this.fog=source.fog;return this;}}const _inverseMatrix$4=/*@__PURE__*/new Matrix4$1();const _ray$4=/*@__PURE__*/new Ray$1();const _sphere$2=/*@__PURE__*/new Sphere$1();const _position$2=/*@__PURE__*/new Vector3$1();class Points extends Object3D$1{constructor(geometry=new BufferGeometry$1(),material=new PointsMaterial()){super();this.isPoints=true;this.type='Points';this.geometry=geometry;this.material=material;this.updateMorphTargets();}copy(source,recursive){super.copy(source,recursive);this.material=Array.isArray(source.material)?source.material.slice():source.material;this.geometry=source.geometry;return this;}raycast(raycaster,intersects){const geometry=this.geometry;const matrixWorld=this.matrixWorld;const threshold=raycaster.params.Points.threshold;const drawRange=geometry.drawRange;// Checking boundingSphere distance to ray
if(geometry.boundingSphere===null)geometry.computeBoundingSphere();_sphere$2.copy(geometry.boundingSphere);_sphere$2.applyMatrix4(matrixWorld);_sphere$2.radius+=threshold;if(raycaster.ray.intersectsSphere(_sphere$2)===false)return;//
_inverseMatrix$4.copy(matrixWorld).invert();_ray$4.copy(raycaster.ray).applyMatrix4(_inverseMatrix$4);const localThreshold=threshold/((this.scale.x+this.scale.y+this.scale.z)/3);const localThresholdSq=localThreshold*localThreshold;const index=geometry.index;const attributes=geometry.attributes;const positionAttribute=attributes.position;if(index!==null){const start=Math.max(0,drawRange.start);const end=Math.min(index.count,drawRange.start+drawRange.count);for(let i=start,il=end;i<il;i++){const a=index.getX(i);_position$2.fromBufferAttribute(positionAttribute,a);testPoint(_position$2,a,localThresholdSq,matrixWorld,raycaster,intersects,this);}}else {const start=Math.max(0,drawRange.start);const end=Math.min(positionAttribute.count,drawRange.start+drawRange.count);for(let i=start,l=end;i<l;i++){_position$2.fromBufferAttribute(positionAttribute,i);testPoint(_position$2,i,localThresholdSq,matrixWorld,raycaster,intersects,this);}}}updateMorphTargets(){const geometry=this.geometry;const morphAttributes=geometry.morphAttributes;const keys=Object.keys(morphAttributes);if(keys.length>0){const morphAttribute=morphAttributes[keys[0]];if(morphAttribute!==undefined){this.morphTargetInfluences=[];this.morphTargetDictionary={};for(let m=0,ml=morphAttribute.length;m<ml;m++){const name=morphAttribute[m].name||String(m);this.morphTargetInfluences.push(0);this.morphTargetDictionary[name]=m;}}}}}function testPoint(point,index,localThresholdSq,matrixWorld,raycaster,intersects,object){const rayPointDistanceSq=_ray$4.distanceSqToPoint(point);if(rayPointDistanceSq<localThresholdSq){const intersectPoint=new Vector3$1();_ray$4.closestPointToPoint(point,intersectPoint);intersectPoint.applyMatrix4(matrixWorld);const distance=raycaster.ray.origin.distanceTo(intersectPoint);if(distance<raycaster.near||distance>raycaster.far)return;intersects.push({distance:distance,distanceToRay:Math.sqrt(rayPointDistanceSq),point:intersectPoint,index:index,face:null,object:object});}}class MeshStandardMaterial extends Material$1{constructor(parameters){super();this.isMeshStandardMaterial=true;this.defines={'STANDARD':''};this.type='MeshStandardMaterial';this.color=new Color$1(0xffffff);// diffuse
this.roughness=1.0;this.metalness=0.0;this.map=null;this.lightMap=null;this.lightMapIntensity=1.0;this.aoMap=null;this.aoMapIntensity=1.0;this.emissive=new Color$1(0x000000);this.emissiveIntensity=1.0;this.emissiveMap=null;this.bumpMap=null;this.bumpScale=1;this.normalMap=null;this.normalMapType=TangentSpaceNormalMap$1;this.normalScale=new Vector2$1(1,1);this.displacementMap=null;this.displacementScale=1;this.displacementBias=0;this.roughnessMap=null;this.metalnessMap=null;this.alphaMap=null;this.envMap=null;this.envMapRotation=new Euler$1();this.envMapIntensity=1.0;this.wireframe=false;this.wireframeLinewidth=1;this.wireframeLinecap='round';this.wireframeLinejoin='round';this.flatShading=false;this.fog=true;this.setValues(parameters);}copy(source){super.copy(source);this.defines={'STANDARD':''};this.color.copy(source.color);this.roughness=source.roughness;this.metalness=source.metalness;this.map=source.map;this.lightMap=source.lightMap;this.lightMapIntensity=source.lightMapIntensity;this.aoMap=source.aoMap;this.aoMapIntensity=source.aoMapIntensity;this.emissive.copy(source.emissive);this.emissiveMap=source.emissiveMap;this.emissiveIntensity=source.emissiveIntensity;this.bumpMap=source.bumpMap;this.bumpScale=source.bumpScale;this.normalMap=source.normalMap;this.normalMapType=source.normalMapType;this.normalScale.copy(source.normalScale);this.displacementMap=source.displacementMap;this.displacementScale=source.displacementScale;this.displacementBias=source.displacementBias;this.roughnessMap=source.roughnessMap;this.metalnessMap=source.metalnessMap;this.alphaMap=source.alphaMap;this.envMap=source.envMap;this.envMapRotation.copy(source.envMapRotation);this.envMapIntensity=source.envMapIntensity;this.wireframe=source.wireframe;this.wireframeLinewidth=source.wireframeLinewidth;this.wireframeLinecap=source.wireframeLinecap;this.wireframeLinejoin=source.wireframeLinejoin;this.flatShading=source.flatShading;this.fog=source.fog;return this;}}class MeshPhysicalMaterial extends MeshStandardMaterial{constructor(parameters){super();this.isMeshPhysicalMaterial=true;this.defines={'STANDARD':'','PHYSICAL':''};this.type='MeshPhysicalMaterial';this.anisotropyRotation=0;this.anisotropyMap=null;this.clearcoatMap=null;this.clearcoatRoughness=0.0;this.clearcoatRoughnessMap=null;this.clearcoatNormalScale=new Vector2$1(1,1);this.clearcoatNormalMap=null;this.ior=1.5;Object.defineProperty(this,'reflectivity',{get:function(){return clamp$2(2.5*(this.ior-1)/(this.ior+1),0,1);},set:function(reflectivity){this.ior=(1+0.4*reflectivity)/(1-0.4*reflectivity);}});this.iridescenceMap=null;this.iridescenceIOR=1.3;this.iridescenceThicknessRange=[100,400];this.iridescenceThicknessMap=null;this.sheenColor=new Color$1(0x000000);this.sheenColorMap=null;this.sheenRoughness=1.0;this.sheenRoughnessMap=null;this.transmissionMap=null;this.thickness=0;this.thicknessMap=null;this.attenuationDistance=Infinity;this.attenuationColor=new Color$1(1,1,1);this.specularIntensity=1.0;this.specularIntensityMap=null;this.specularColor=new Color$1(1,1,1);this.specularColorMap=null;this._anisotropy=0;this._clearcoat=0;this._dispersion=0;this._iridescence=0;this._sheen=0.0;this._transmission=0;this.setValues(parameters);}get anisotropy(){return this._anisotropy;}set anisotropy(value){if(this._anisotropy>0!==value>0){this.version++;}this._anisotropy=value;}get clearcoat(){return this._clearcoat;}set clearcoat(value){if(this._clearcoat>0!==value>0){this.version++;}this._clearcoat=value;}get iridescence(){return this._iridescence;}set iridescence(value){if(this._iridescence>0!==value>0){this.version++;}this._iridescence=value;}get dispersion(){return this._dispersion;}set dispersion(value){if(this._dispersion>0!==value>0){this.version++;}this._dispersion=value;}get sheen(){return this._sheen;}set sheen(value){if(this._sheen>0!==value>0){this.version++;}this._sheen=value;}get transmission(){return this._transmission;}set transmission(value){if(this._transmission>0!==value>0){this.version++;}this._transmission=value;}copy(source){super.copy(source);this.defines={'STANDARD':'','PHYSICAL':''};this.anisotropy=source.anisotropy;this.anisotropyRotation=source.anisotropyRotation;this.anisotropyMap=source.anisotropyMap;this.clearcoat=source.clearcoat;this.clearcoatMap=source.clearcoatMap;this.clearcoatRoughness=source.clearcoatRoughness;this.clearcoatRoughnessMap=source.clearcoatRoughnessMap;this.clearcoatNormalMap=source.clearcoatNormalMap;this.clearcoatNormalScale.copy(source.clearcoatNormalScale);this.dispersion=source.dispersion;this.ior=source.ior;this.iridescence=source.iridescence;this.iridescenceMap=source.iridescenceMap;this.iridescenceIOR=source.iridescenceIOR;this.iridescenceThicknessRange=[...source.iridescenceThicknessRange];this.iridescenceThicknessMap=source.iridescenceThicknessMap;this.sheen=source.sheen;this.sheenColor.copy(source.sheenColor);this.sheenColorMap=source.sheenColorMap;this.sheenRoughness=source.sheenRoughness;this.sheenRoughnessMap=source.sheenRoughnessMap;this.transmission=source.transmission;this.transmissionMap=source.transmissionMap;this.thickness=source.thickness;this.thicknessMap=source.thicknessMap;this.attenuationDistance=source.attenuationDistance;this.attenuationColor.copy(source.attenuationColor);this.specularIntensity=source.specularIntensity;this.specularIntensityMap=source.specularIntensityMap;this.specularColor.copy(source.specularColor);this.specularColorMap=source.specularColorMap;return this;}}function convertArray(array,type,forceClone){if(!array||// let 'undefined' and 'null' pass
!forceClone&&array.constructor===type)return array;if(typeof type.BYTES_PER_ELEMENT==='number'){return new type(array);// create typed array
}return Array.prototype.slice.call(array);// create Array
}function isTypedArray(object){return ArrayBuffer.isView(object)&&!(object instanceof DataView);}// returns an array by which times and values can be sorted
function getKeyframeOrder(times){function compareTime(i,j){return times[i]-times[j];}const n=times.length;const result=new Array(n);for(let i=0;i!==n;++i)result[i]=i;result.sort(compareTime);return result;}// uses the array previously returned by 'getKeyframeOrder' to sort data
function sortedArray(values,stride,order){const nValues=values.length;const result=new values.constructor(nValues);for(let i=0,dstOffset=0;dstOffset!==nValues;++i){const srcOffset=order[i]*stride;for(let j=0;j!==stride;++j){result[dstOffset++]=values[srcOffset+j];}}return result;}// function for parsing AOS keyframe formats
function flattenJSON(jsonKeys,times,values,valuePropertyName){let i=1,key=jsonKeys[0];while(key!==undefined&&key[valuePropertyName]===undefined){key=jsonKeys[i++];}if(key===undefined)return;// no data
let value=key[valuePropertyName];if(value===undefined)return;// no data
if(Array.isArray(value)){do{value=key[valuePropertyName];if(value!==undefined){times.push(key.time);values.push.apply(values,value);// push all elements
}key=jsonKeys[i++];}while(key!==undefined);}else if(value.toArray!==undefined){// ...assume THREE.Math-ish
do{value=key[valuePropertyName];if(value!==undefined){times.push(key.time);value.toArray(values,values.length);}key=jsonKeys[i++];}while(key!==undefined);}else {// otherwise push as-is
do{value=key[valuePropertyName];if(value!==undefined){times.push(key.time);values.push(value);}key=jsonKeys[i++];}while(key!==undefined);}}/**
 * Abstract base class of interpolants over parametric samples.
 *
 * The parameter domain is one dimensional, typically the time or a path
 * along a curve defined by the data.
 *
 * The sample values can have any dimensionality and derived classes may
 * apply special interpretations to the data.
 *
 * This class provides the interval seek in a Template Method, deferring
 * the actual interpolation to derived classes.
 *
 * Time complexity is O(1) for linear access crossing at most two points
 * and O(log N) for random access, where N is the number of positions.
 *
 * References:
 *
 * 		http://www.oodesign.com/template-method-pattern.html
 *
 */class Interpolant{constructor(parameterPositions,sampleValues,sampleSize,resultBuffer){this.parameterPositions=parameterPositions;this._cachedIndex=0;this.resultBuffer=resultBuffer!==undefined?resultBuffer:new sampleValues.constructor(sampleSize);this.sampleValues=sampleValues;this.valueSize=sampleSize;this.settings=null;this.DefaultSettings_={};}evaluate(t){const pp=this.parameterPositions;let i1=this._cachedIndex,t1=pp[i1],t0=pp[i1-1];validate_interval:{seek:{let right;linear_scan:{//- See http://jsperf.com/comparison-to-undefined/3
//- slower code:
//-
//- 				if ( t >= t1 || t1 === undefined ) {
forward_scan:if(!(t<t1)){for(let giveUpAt=i1+2;;){if(t1===undefined){if(t<t0)break forward_scan;// after end
i1=pp.length;this._cachedIndex=i1;return this.copySampleValue_(i1-1);}if(i1===giveUpAt)break;// this loop
t0=t1;t1=pp[++i1];if(t<t1){// we have arrived at the sought interval
break seek;}}// prepare binary search on the right side of the index
right=pp.length;break linear_scan;}//- slower code:
//-					if ( t < t0 || t0 === undefined ) {
if(!(t>=t0)){// looping?
const t1global=pp[1];if(t<t1global){i1=2;// + 1, using the scan for the details
t0=t1global;}// linear reverse scan
for(let giveUpAt=i1-2;;){if(t0===undefined){// before start
this._cachedIndex=0;return this.copySampleValue_(0);}if(i1===giveUpAt)break;// this loop
t1=t0;t0=pp[--i1-1];if(t>=t0){// we have arrived at the sought interval
break seek;}}// prepare binary search on the left side of the index
right=i1;i1=0;break linear_scan;}// the interval is valid
break validate_interval;}// linear scan
// binary search
while(i1<right){const mid=i1+right>>>1;if(t<pp[mid]){right=mid;}else {i1=mid+1;}}t1=pp[i1];t0=pp[i1-1];// check boundary cases, again
if(t0===undefined){this._cachedIndex=0;return this.copySampleValue_(0);}if(t1===undefined){i1=pp.length;this._cachedIndex=i1;return this.copySampleValue_(i1-1);}}// seek
this._cachedIndex=i1;this.intervalChanged_(i1,t0,t1);}// validate_interval
return this.interpolate_(i1,t0,t,t1);}getSettings_(){return this.settings||this.DefaultSettings_;}copySampleValue_(index){// copies a sample value to the result buffer
const result=this.resultBuffer,values=this.sampleValues,stride=this.valueSize,offset=index*stride;for(let i=0;i!==stride;++i){result[i]=values[offset+i];}return result;}// Template methods for derived classes:
interpolate_(/* i1, t0, t, t1 */){throw new Error('call to abstract method');// implementations shall return this.resultBuffer
}intervalChanged_(/* i1, t0, t1 */){// empty
}}/**
 * Fast and simple cubic spline interpolant.
 *
 * It was derived from a Hermitian construction setting the first derivative
 * at each sample position to the linear slope between neighboring positions
 * over their parameter interval.
 */class CubicInterpolant extends Interpolant{constructor(parameterPositions,sampleValues,sampleSize,resultBuffer){super(parameterPositions,sampleValues,sampleSize,resultBuffer);this._weightPrev=-0;this._offsetPrev=-0;this._weightNext=-0;this._offsetNext=-0;this.DefaultSettings_={endingStart:ZeroCurvatureEnding,endingEnd:ZeroCurvatureEnding};}intervalChanged_(i1,t0,t1){const pp=this.parameterPositions;let iPrev=i1-2,iNext=i1+1,tPrev=pp[iPrev],tNext=pp[iNext];if(tPrev===undefined){switch(this.getSettings_().endingStart){case ZeroSlopeEnding:// f'(t0) = 0
iPrev=i1;tPrev=2*t0-t1;break;case WrapAroundEnding:// use the other end of the curve
iPrev=pp.length-2;tPrev=t0+pp[iPrev]-pp[iPrev+1];break;default:// ZeroCurvatureEnding
// f''(t0) = 0 a.k.a. Natural Spline
iPrev=i1;tPrev=t1;}}if(tNext===undefined){switch(this.getSettings_().endingEnd){case ZeroSlopeEnding:// f'(tN) = 0
iNext=i1;tNext=2*t1-t0;break;case WrapAroundEnding:// use the other end of the curve
iNext=1;tNext=t1+pp[1]-pp[0];break;default:// ZeroCurvatureEnding
// f''(tN) = 0, a.k.a. Natural Spline
iNext=i1-1;tNext=t0;}}const halfDt=(t1-t0)*0.5,stride=this.valueSize;this._weightPrev=halfDt/(t0-tPrev);this._weightNext=halfDt/(tNext-t1);this._offsetPrev=iPrev*stride;this._offsetNext=iNext*stride;}interpolate_(i1,t0,t,t1){const result=this.resultBuffer,values=this.sampleValues,stride=this.valueSize,o1=i1*stride,o0=o1-stride,oP=this._offsetPrev,oN=this._offsetNext,wP=this._weightPrev,wN=this._weightNext,p=(t-t0)/(t1-t0),pp=p*p,ppp=pp*p;// evaluate polynomials
const sP=-wP*ppp+2*wP*pp-wP*p;const s0=(1+wP)*ppp+(-1.5-2*wP)*pp+(-0.5+wP)*p+1;const s1=(-1-wN)*ppp+(1.5+wN)*pp+0.5*p;const sN=wN*ppp-wN*pp;// combine data linearly
for(let i=0;i!==stride;++i){result[i]=sP*values[oP+i]+s0*values[o0+i]+s1*values[o1+i]+sN*values[oN+i];}return result;}}class LinearInterpolant extends Interpolant{constructor(parameterPositions,sampleValues,sampleSize,resultBuffer){super(parameterPositions,sampleValues,sampleSize,resultBuffer);}interpolate_(i1,t0,t,t1){const result=this.resultBuffer,values=this.sampleValues,stride=this.valueSize,offset1=i1*stride,offset0=offset1-stride,weight1=(t-t0)/(t1-t0),weight0=1-weight1;for(let i=0;i!==stride;++i){result[i]=values[offset0+i]*weight0+values[offset1+i]*weight1;}return result;}}/**
 *
 * Interpolant that evaluates to the sample value at the position preceding
 * the parameter.
 */class DiscreteInterpolant extends Interpolant{constructor(parameterPositions,sampleValues,sampleSize,resultBuffer){super(parameterPositions,sampleValues,sampleSize,resultBuffer);}interpolate_(i1/*, t0, t, t1 */){return this.copySampleValue_(i1-1);}}class KeyframeTrack{constructor(name,times,values,interpolation){if(name===undefined)throw new Error('THREE.KeyframeTrack: track name is undefined');if(times===undefined||times.length===0)throw new Error('THREE.KeyframeTrack: no keyframes in track named '+name);this.name=name;this.times=convertArray(times,this.TimeBufferType);this.values=convertArray(values,this.ValueBufferType);this.setInterpolation(interpolation||this.DefaultInterpolation);}// Serialization (in static context, because of constructor invocation
// and automatic invocation of .toJSON):
static toJSON(track){const trackType=track.constructor;let json;// derived classes can define a static toJSON method
if(trackType.toJSON!==this.toJSON){json=trackType.toJSON(track);}else {// by default, we assume the data can be serialized as-is
json={'name':track.name,'times':convertArray(track.times,Array),'values':convertArray(track.values,Array)};const interpolation=track.getInterpolation();if(interpolation!==track.DefaultInterpolation){json.interpolation=interpolation;}}json.type=track.ValueTypeName;// mandatory
return json;}InterpolantFactoryMethodDiscrete(result){return new DiscreteInterpolant(this.times,this.values,this.getValueSize(),result);}InterpolantFactoryMethodLinear(result){return new LinearInterpolant(this.times,this.values,this.getValueSize(),result);}InterpolantFactoryMethodSmooth(result){return new CubicInterpolant(this.times,this.values,this.getValueSize(),result);}setInterpolation(interpolation){let factoryMethod;switch(interpolation){case InterpolateDiscrete:factoryMethod=this.InterpolantFactoryMethodDiscrete;break;case InterpolateLinear:factoryMethod=this.InterpolantFactoryMethodLinear;break;case InterpolateSmooth:factoryMethod=this.InterpolantFactoryMethodSmooth;break;}if(factoryMethod===undefined){const message='unsupported interpolation for '+this.ValueTypeName+' keyframe track named '+this.name;if(this.createInterpolant===undefined){// fall back to default, unless the default itself is messed up
if(interpolation!==this.DefaultInterpolation){this.setInterpolation(this.DefaultInterpolation);}else {throw new Error(message);// fatal, in this case
}}console.warn('THREE.KeyframeTrack:',message);return this;}this.createInterpolant=factoryMethod;return this;}getInterpolation(){switch(this.createInterpolant){case this.InterpolantFactoryMethodDiscrete:return InterpolateDiscrete;case this.InterpolantFactoryMethodLinear:return InterpolateLinear;case this.InterpolantFactoryMethodSmooth:return InterpolateSmooth;}}getValueSize(){return this.values.length/this.times.length;}// move all keyframes either forwards or backwards in time
shift(timeOffset){if(timeOffset!==0.0){const times=this.times;for(let i=0,n=times.length;i!==n;++i){times[i]+=timeOffset;}}return this;}// scale all keyframe times by a factor (useful for frame <-> seconds conversions)
scale(timeScale){if(timeScale!==1.0){const times=this.times;for(let i=0,n=times.length;i!==n;++i){times[i]*=timeScale;}}return this;}// removes keyframes before and after animation without changing any values within the range [startTime, endTime].
// IMPORTANT: We do not shift around keys to the start of the track time, because for interpolated keys this will change their values
trim(startTime,endTime){const times=this.times,nKeys=times.length;let from=0,to=nKeys-1;while(from!==nKeys&&times[from]<startTime){++from;}while(to!==-1&&times[to]>endTime){--to;}++to;// inclusive -> exclusive bound
if(from!==0||to!==nKeys){// empty tracks are forbidden, so keep at least one keyframe
if(from>=to){to=Math.max(to,1);from=to-1;}const stride=this.getValueSize();this.times=times.slice(from,to);this.values=this.values.slice(from*stride,to*stride);}return this;}// ensure we do not get a GarbageInGarbageOut situation, make sure tracks are at least minimally viable
validate(){let valid=true;const valueSize=this.getValueSize();if(valueSize-Math.floor(valueSize)!==0){console.error('THREE.KeyframeTrack: Invalid value size in track.',this);valid=false;}const times=this.times,values=this.values,nKeys=times.length;if(nKeys===0){console.error('THREE.KeyframeTrack: Track is empty.',this);valid=false;}let prevTime=null;for(let i=0;i!==nKeys;i++){const currTime=times[i];if(typeof currTime==='number'&&isNaN(currTime)){console.error('THREE.KeyframeTrack: Time is not a valid number.',this,i,currTime);valid=false;break;}if(prevTime!==null&&prevTime>currTime){console.error('THREE.KeyframeTrack: Out of order keys.',this,i,currTime,prevTime);valid=false;break;}prevTime=currTime;}if(values!==undefined){if(isTypedArray(values)){for(let i=0,n=values.length;i!==n;++i){const value=values[i];if(isNaN(value)){console.error('THREE.KeyframeTrack: Value is not a valid number.',this,i,value);valid=false;break;}}}}return valid;}// removes equivalent sequential keys as common in morph target sequences
// (0,0,0,0,1,1,1,0,0,0,0,0,0,0) --> (0,0,1,1,0,0)
optimize(){// times or values may be shared with other tracks, so overwriting is unsafe
const times=this.times.slice(),values=this.values.slice(),stride=this.getValueSize(),smoothInterpolation=this.getInterpolation()===InterpolateSmooth,lastIndex=times.length-1;let writeIndex=1;for(let i=1;i<lastIndex;++i){let keep=false;const time=times[i];const timeNext=times[i+1];// remove adjacent keyframes scheduled at the same time
if(time!==timeNext&&(i!==1||time!==times[0])){if(!smoothInterpolation){// remove unnecessary keyframes same as their neighbors
const offset=i*stride,offsetP=offset-stride,offsetN=offset+stride;for(let j=0;j!==stride;++j){const value=values[offset+j];if(value!==values[offsetP+j]||value!==values[offsetN+j]){keep=true;break;}}}else {keep=true;}}// in-place compaction
if(keep){if(i!==writeIndex){times[writeIndex]=times[i];const readOffset=i*stride,writeOffset=writeIndex*stride;for(let j=0;j!==stride;++j){values[writeOffset+j]=values[readOffset+j];}}++writeIndex;}}// flush last keyframe (compaction looks ahead)
if(lastIndex>0){times[writeIndex]=times[lastIndex];for(let readOffset=lastIndex*stride,writeOffset=writeIndex*stride,j=0;j!==stride;++j){values[writeOffset+j]=values[readOffset+j];}++writeIndex;}if(writeIndex!==times.length){this.times=times.slice(0,writeIndex);this.values=values.slice(0,writeIndex*stride);}else {this.times=times;this.values=values;}return this;}clone(){const times=this.times.slice();const values=this.values.slice();const TypedKeyframeTrack=this.constructor;const track=new TypedKeyframeTrack(this.name,times,values);// Interpolant argument to constructor is not saved, so copy the factory method directly.
track.createInterpolant=this.createInterpolant;return track;}}KeyframeTrack.prototype.TimeBufferType=Float32Array;KeyframeTrack.prototype.ValueBufferType=Float32Array;KeyframeTrack.prototype.DefaultInterpolation=InterpolateLinear;/**
 * A Track of Boolean keyframe values.
 */class BooleanKeyframeTrack extends KeyframeTrack{// No interpolation parameter because only InterpolateDiscrete is valid.
constructor(name,times,values){super(name,times,values);}}BooleanKeyframeTrack.prototype.ValueTypeName='bool';BooleanKeyframeTrack.prototype.ValueBufferType=Array;BooleanKeyframeTrack.prototype.DefaultInterpolation=InterpolateDiscrete;BooleanKeyframeTrack.prototype.InterpolantFactoryMethodLinear=undefined;BooleanKeyframeTrack.prototype.InterpolantFactoryMethodSmooth=undefined;/**
 * A Track of keyframe values that represent color.
 */class ColorKeyframeTrack extends KeyframeTrack{}ColorKeyframeTrack.prototype.ValueTypeName='color';/**
 * A Track of numeric keyframe values.
 */class NumberKeyframeTrack extends KeyframeTrack{}NumberKeyframeTrack.prototype.ValueTypeName='number';/**
 * Spherical linear unit quaternion interpolant.
 */class QuaternionLinearInterpolant extends Interpolant{constructor(parameterPositions,sampleValues,sampleSize,resultBuffer){super(parameterPositions,sampleValues,sampleSize,resultBuffer);}interpolate_(i1,t0,t,t1){const result=this.resultBuffer,values=this.sampleValues,stride=this.valueSize,alpha=(t-t0)/(t1-t0);let offset=i1*stride;for(let end=offset+stride;offset!==end;offset+=4){Quaternion$1.slerpFlat(result,0,values,offset-stride,values,offset,alpha);}return result;}}/**
 * A Track of quaternion keyframe values.
 */class QuaternionKeyframeTrack extends KeyframeTrack{InterpolantFactoryMethodLinear(result){return new QuaternionLinearInterpolant(this.times,this.values,this.getValueSize(),result);}}QuaternionKeyframeTrack.prototype.ValueTypeName='quaternion';// ValueBufferType is inherited
// DefaultInterpolation is inherited;
QuaternionKeyframeTrack.prototype.InterpolantFactoryMethodSmooth=undefined;/**
 * A Track that interpolates Strings
 */class StringKeyframeTrack extends KeyframeTrack{// No interpolation parameter because only InterpolateDiscrete is valid.
constructor(name,times,values){super(name,times,values);}}StringKeyframeTrack.prototype.ValueTypeName='string';StringKeyframeTrack.prototype.ValueBufferType=Array;StringKeyframeTrack.prototype.DefaultInterpolation=InterpolateDiscrete;StringKeyframeTrack.prototype.InterpolantFactoryMethodLinear=undefined;StringKeyframeTrack.prototype.InterpolantFactoryMethodSmooth=undefined;/**
 * A Track of vectored keyframe values.
 */class VectorKeyframeTrack extends KeyframeTrack{}VectorKeyframeTrack.prototype.ValueTypeName='vector';class AnimationClip{constructor(name='',duration=-1,tracks=[],blendMode=NormalAnimationBlendMode){this.name=name;this.tracks=tracks;this.duration=duration;this.blendMode=blendMode;this.uuid=generateUUID$1();// this means it should figure out its duration by scanning the tracks
if(this.duration<0){this.resetDuration();}}static parse(json){const tracks=[],jsonTracks=json.tracks,frameTime=1.0/(json.fps||1.0);for(let i=0,n=jsonTracks.length;i!==n;++i){tracks.push(parseKeyframeTrack(jsonTracks[i]).scale(frameTime));}const clip=new this(json.name,json.duration,tracks,json.blendMode);clip.uuid=json.uuid;return clip;}static toJSON(clip){const tracks=[],clipTracks=clip.tracks;const json={'name':clip.name,'duration':clip.duration,'tracks':tracks,'uuid':clip.uuid,'blendMode':clip.blendMode};for(let i=0,n=clipTracks.length;i!==n;++i){tracks.push(KeyframeTrack.toJSON(clipTracks[i]));}return json;}static CreateFromMorphTargetSequence(name,morphTargetSequence,fps,noLoop){const numMorphTargets=morphTargetSequence.length;const tracks=[];for(let i=0;i<numMorphTargets;i++){let times=[];let values=[];times.push((i+numMorphTargets-1)%numMorphTargets,i,(i+1)%numMorphTargets);values.push(0,1,0);const order=getKeyframeOrder(times);times=sortedArray(times,1,order);values=sortedArray(values,1,order);// if there is a key at the first frame, duplicate it as the
// last frame as well for perfect loop.
if(!noLoop&&times[0]===0){times.push(numMorphTargets);values.push(values[0]);}tracks.push(new NumberKeyframeTrack('.morphTargetInfluences['+morphTargetSequence[i].name+']',times,values).scale(1.0/fps));}return new this(name,-1,tracks);}static findByName(objectOrClipArray,name){let clipArray=objectOrClipArray;if(!Array.isArray(objectOrClipArray)){const o=objectOrClipArray;clipArray=o.geometry&&o.geometry.animations||o.animations;}for(let i=0;i<clipArray.length;i++){if(clipArray[i].name===name){return clipArray[i];}}return null;}static CreateClipsFromMorphTargetSequences(morphTargets,fps,noLoop){const animationToMorphTargets={};// tested with https://regex101.com/ on trick sequences
// such flamingo_flyA_003, flamingo_run1_003, crdeath0059
const pattern=/^([\w-]*?)([\d]+)$/;// sort morph target names into animation groups based
// patterns like Walk_001, Walk_002, Run_001, Run_002
for(let i=0,il=morphTargets.length;i<il;i++){const morphTarget=morphTargets[i];const parts=morphTarget.name.match(pattern);if(parts&&parts.length>1){const name=parts[1];let animationMorphTargets=animationToMorphTargets[name];if(!animationMorphTargets){animationToMorphTargets[name]=animationMorphTargets=[];}animationMorphTargets.push(morphTarget);}}const clips=[];for(const name in animationToMorphTargets){clips.push(this.CreateFromMorphTargetSequence(name,animationToMorphTargets[name],fps,noLoop));}return clips;}// parse the animation.hierarchy format
static parseAnimation(animation,bones){if(!animation){console.error('THREE.AnimationClip: No animation in JSONLoader data.');return null;}const addNonemptyTrack=function(trackType,trackName,animationKeys,propertyName,destTracks){// only return track if there are actually keys.
if(animationKeys.length!==0){const times=[];const values=[];flattenJSON(animationKeys,times,values,propertyName);// empty keys are filtered out, so check again
if(times.length!==0){destTracks.push(new trackType(trackName,times,values));}}};const tracks=[];const clipName=animation.name||'default';const fps=animation.fps||30;const blendMode=animation.blendMode;// automatic length determination in AnimationClip.
let duration=animation.length||-1;const hierarchyTracks=animation.hierarchy||[];for(let h=0;h<hierarchyTracks.length;h++){const animationKeys=hierarchyTracks[h].keys;// skip empty tracks
if(!animationKeys||animationKeys.length===0)continue;// process morph targets
if(animationKeys[0].morphTargets){// figure out all morph targets used in this track
const morphTargetNames={};let k;for(k=0;k<animationKeys.length;k++){if(animationKeys[k].morphTargets){for(let m=0;m<animationKeys[k].morphTargets.length;m++){morphTargetNames[animationKeys[k].morphTargets[m]]=-1;}}}// create a track for each morph target with all zero
// morphTargetInfluences except for the keys in which
// the morphTarget is named.
for(const morphTargetName in morphTargetNames){const times=[];const values=[];for(let m=0;m!==animationKeys[k].morphTargets.length;++m){const animationKey=animationKeys[k];times.push(animationKey.time);values.push(animationKey.morphTarget===morphTargetName?1:0);}tracks.push(new NumberKeyframeTrack('.morphTargetInfluence['+morphTargetName+']',times,values));}duration=morphTargetNames.length*fps;}else {// ...assume skeletal animation
const boneName='.bones['+bones[h].name+']';addNonemptyTrack(VectorKeyframeTrack,boneName+'.position',animationKeys,'pos',tracks);addNonemptyTrack(QuaternionKeyframeTrack,boneName+'.quaternion',animationKeys,'rot',tracks);addNonemptyTrack(VectorKeyframeTrack,boneName+'.scale',animationKeys,'scl',tracks);}}if(tracks.length===0){return null;}const clip=new this(clipName,duration,tracks,blendMode);return clip;}resetDuration(){const tracks=this.tracks;let duration=0;for(let i=0,n=tracks.length;i!==n;++i){const track=this.tracks[i];duration=Math.max(duration,track.times[track.times.length-1]);}this.duration=duration;return this;}trim(){for(let i=0;i<this.tracks.length;i++){this.tracks[i].trim(0,this.duration);}return this;}validate(){let valid=true;for(let i=0;i<this.tracks.length;i++){valid=valid&&this.tracks[i].validate();}return valid;}optimize(){for(let i=0;i<this.tracks.length;i++){this.tracks[i].optimize();}return this;}clone(){const tracks=[];for(let i=0;i<this.tracks.length;i++){tracks.push(this.tracks[i].clone());}return new this.constructor(this.name,this.duration,tracks,this.blendMode);}toJSON(){return this.constructor.toJSON(this);}}function getTrackTypeForValueTypeName(typeName){switch(typeName.toLowerCase()){case'scalar':case'double':case'float':case'number':case'integer':return NumberKeyframeTrack;case'vector':case'vector2':case'vector3':case'vector4':return VectorKeyframeTrack;case'color':return ColorKeyframeTrack;case'quaternion':return QuaternionKeyframeTrack;case'bool':case'boolean':return BooleanKeyframeTrack;case'string':return StringKeyframeTrack;}throw new Error('THREE.KeyframeTrack: Unsupported typeName: '+typeName);}function parseKeyframeTrack(json){if(json.type===undefined){throw new Error('THREE.KeyframeTrack: track type undefined, can not parse');}const trackType=getTrackTypeForValueTypeName(json.type);if(json.times===undefined){const times=[],values=[];flattenJSON(json.keys,times,values,'value');json.times=times;json.values=values;}// derived classes can define a static parse method
if(trackType.parse!==undefined){return trackType.parse(json);}else {// by default, we assume a constructor compatible with the base
return new trackType(json.name,json.times,json.values,json.interpolation);}}const Cache={enabled:false,files:{},add:function(key,file){if(this.enabled===false)return;// console.log( 'THREE.Cache', 'Adding key:', key );
this.files[key]=file;},get:function(key){if(this.enabled===false)return;// console.log( 'THREE.Cache', 'Checking key:', key );
return this.files[key];},remove:function(key){delete this.files[key];},clear:function(){this.files={};}};class LoadingManager{constructor(onLoad,onProgress,onError){const scope=this;let isLoading=false;let itemsLoaded=0;let itemsTotal=0;let urlModifier=undefined;const handlers=[];// Refer to #5689 for the reason why we don't set .onStart
// in the constructor
this.onStart=undefined;this.onLoad=onLoad;this.onProgress=onProgress;this.onError=onError;this.itemStart=function(url){itemsTotal++;if(isLoading===false){if(scope.onStart!==undefined){scope.onStart(url,itemsLoaded,itemsTotal);}}isLoading=true;};this.itemEnd=function(url){itemsLoaded++;if(scope.onProgress!==undefined){scope.onProgress(url,itemsLoaded,itemsTotal);}if(itemsLoaded===itemsTotal){isLoading=false;if(scope.onLoad!==undefined){scope.onLoad();}}};this.itemError=function(url){if(scope.onError!==undefined){scope.onError(url);}};this.resolveURL=function(url){if(urlModifier){return urlModifier(url);}return url;};this.setURLModifier=function(transform){urlModifier=transform;return this;};this.addHandler=function(regex,loader){handlers.push(regex,loader);return this;};this.removeHandler=function(regex){const index=handlers.indexOf(regex);if(index!==-1){handlers.splice(index,2);}return this;};this.getHandler=function(file){for(let i=0,l=handlers.length;i<l;i+=2){const regex=handlers[i];const loader=handlers[i+1];if(regex.global)regex.lastIndex=0;// see #17920
if(regex.test(file)){return loader;}}return null;};}}const DefaultLoadingManager=/*@__PURE__*/new LoadingManager();class Loader{constructor(manager){this.manager=manager!==undefined?manager:DefaultLoadingManager;this.crossOrigin='anonymous';this.withCredentials=false;this.path='';this.resourcePath='';this.requestHeader={};}load(/* url, onLoad, onProgress, onError */){}loadAsync(url,onProgress){const scope=this;return new Promise(function(resolve,reject){scope.load(url,resolve,onProgress,reject);});}parse(/* data */){}setCrossOrigin(crossOrigin){this.crossOrigin=crossOrigin;return this;}setWithCredentials(value){this.withCredentials=value;return this;}setPath(path){this.path=path;return this;}setResourcePath(resourcePath){this.resourcePath=resourcePath;return this;}setRequestHeader(requestHeader){this.requestHeader=requestHeader;return this;}}Loader.DEFAULT_MATERIAL_NAME='__DEFAULT';const loading={};class HttpError extends Error{constructor(message,response){super(message);this.response=response;}}class FileLoader extends Loader{constructor(manager){super(manager);}load(url,onLoad,onProgress,onError){if(url===undefined)url='';if(this.path!==undefined)url=this.path+url;url=this.manager.resolveURL(url);const cached=Cache.get(url);if(cached!==undefined){this.manager.itemStart(url);setTimeout(()=>{if(onLoad)onLoad(cached);this.manager.itemEnd(url);},0);return cached;}// Check if request is duplicate
if(loading[url]!==undefined){loading[url].push({onLoad:onLoad,onProgress:onProgress,onError:onError});return;}// Initialise array for duplicate requests
loading[url]=[];loading[url].push({onLoad:onLoad,onProgress:onProgress,onError:onError});// create request
const req=new Request(url,{headers:new Headers(this.requestHeader),credentials:this.withCredentials?'include':'same-origin'// An abort controller could be added within a future PR
});// record states ( avoid data race )
const mimeType=this.mimeType;const responseType=this.responseType;// start the fetch
fetch(req).then(response=>{if(response.status===200||response.status===0){// Some browsers return HTTP Status 0 when using non-http protocol
// e.g. 'file://' or 'data://'. Handle as success.
if(response.status===0){console.warn('THREE.FileLoader: HTTP Status 0 received.');}// Workaround: Checking if response.body === undefined for Alipay browser #23548
if(typeof ReadableStream==='undefined'||response.body===undefined||response.body.getReader===undefined){return response;}const callbacks=loading[url];const reader=response.body.getReader();// Nginx needs X-File-Size check
// https://serverfault.com/questions/482875/why-does-nginx-remove-content-length-header-for-chunked-content
const contentLength=response.headers.get('X-File-Size')||response.headers.get('Content-Length');const total=contentLength?parseInt(contentLength):0;const lengthComputable=total!==0;let loaded=0;// periodically read data into the new stream tracking while download progress
const stream=new ReadableStream({start(controller){readData();function readData(){reader.read().then(({done,value})=>{if(done){controller.close();}else {loaded+=value.byteLength;const event=new ProgressEvent('progress',{lengthComputable,loaded,total});for(let i=0,il=callbacks.length;i<il;i++){const callback=callbacks[i];if(callback.onProgress)callback.onProgress(event);}controller.enqueue(value);readData();}},e=>{controller.error(e);});}}});return new Response(stream);}else {throw new HttpError(`fetch for "${response.url}" responded with ${response.status}: ${response.statusText}`,response);}}).then(response=>{switch(responseType){case'arraybuffer':return response.arrayBuffer();case'blob':return response.blob();case'document':return response.text().then(text=>{const parser=new DOMParser();return parser.parseFromString(text,mimeType);});case'json':return response.json();default:if(mimeType===undefined){return response.text();}else {// sniff encoding
const re=/charset="?([^;"\s]*)"?/i;const exec=re.exec(mimeType);const label=exec&&exec[1]?exec[1].toLowerCase():undefined;const decoder=new TextDecoder(label);return response.arrayBuffer().then(ab=>decoder.decode(ab));}}}).then(data=>{// Add to cache only on HTTP success, so that we do not cache
// error response bodies as proper responses to requests.
Cache.add(url,data);const callbacks=loading[url];delete loading[url];for(let i=0,il=callbacks.length;i<il;i++){const callback=callbacks[i];if(callback.onLoad)callback.onLoad(data);}}).catch(err=>{// Abort errors and other errors are handled the same
const callbacks=loading[url];if(callbacks===undefined){// When onLoad was called and url was deleted in `loading`
this.manager.itemError(url);throw err;}delete loading[url];for(let i=0,il=callbacks.length;i<il;i++){const callback=callbacks[i];if(callback.onError)callback.onError(err);}this.manager.itemError(url);}).finally(()=>{this.manager.itemEnd(url);});this.manager.itemStart(url);}setResponseType(value){this.responseType=value;return this;}setMimeType(value){this.mimeType=value;return this;}}class ImageLoader extends Loader{constructor(manager){super(manager);}load(url,onLoad,onProgress,onError){if(this.path!==undefined)url=this.path+url;url=this.manager.resolveURL(url);const scope=this;const cached=Cache.get(url);if(cached!==undefined){scope.manager.itemStart(url);setTimeout(function(){if(onLoad)onLoad(cached);scope.manager.itemEnd(url);},0);return cached;}const image=createElementNS$1('img');function onImageLoad(){removeEventListeners();Cache.add(url,this);if(onLoad)onLoad(this);scope.manager.itemEnd(url);}function onImageError(event){removeEventListeners();if(onError)onError(event);scope.manager.itemError(url);scope.manager.itemEnd(url);}function removeEventListeners(){image.removeEventListener('load',onImageLoad,false);image.removeEventListener('error',onImageError,false);}image.addEventListener('load',onImageLoad,false);image.addEventListener('error',onImageError,false);if(url.slice(0,5)!=='data:'){if(this.crossOrigin!==undefined)image.crossOrigin=this.crossOrigin;}scope.manager.itemStart(url);image.src=url;return image;}}class TextureLoader extends Loader{constructor(manager){super(manager);}load(url,onLoad,onProgress,onError){const texture=new Texture$1();const loader=new ImageLoader(this.manager);loader.setCrossOrigin(this.crossOrigin);loader.setPath(this.path);loader.load(url,function(image){texture.image=image;texture.needsUpdate=true;if(onLoad!==undefined){onLoad(texture);}},onProgress,onError);return texture;}}class Light extends Object3D$1{constructor(color,intensity=1){super();this.isLight=true;this.type='Light';this.color=new Color$1(color);this.intensity=intensity;}dispose(){// Empty here in base class; some subclasses override.
}copy(source,recursive){super.copy(source,recursive);this.color.copy(source.color);this.intensity=source.intensity;return this;}toJSON(meta){const data=super.toJSON(meta);data.object.color=this.color.getHex();data.object.intensity=this.intensity;if(this.groundColor!==undefined)data.object.groundColor=this.groundColor.getHex();if(this.distance!==undefined)data.object.distance=this.distance;if(this.angle!==undefined)data.object.angle=this.angle;if(this.decay!==undefined)data.object.decay=this.decay;if(this.penumbra!==undefined)data.object.penumbra=this.penumbra;if(this.shadow!==undefined)data.object.shadow=this.shadow.toJSON();if(this.target!==undefined)data.object.target=this.target.uuid;return data;}}const _projScreenMatrix$1=/*@__PURE__*/new Matrix4$1();const _lightPositionWorld$1=/*@__PURE__*/new Vector3$1();const _lookTarget$1=/*@__PURE__*/new Vector3$1();class LightShadow{constructor(camera){this.camera=camera;this.intensity=1;this.bias=0;this.normalBias=0;this.radius=1;this.blurSamples=8;this.mapSize=new Vector2$1(512,512);this.map=null;this.mapPass=null;this.matrix=new Matrix4$1();this.autoUpdate=true;this.needsUpdate=false;this._frustum=new Frustum();this._frameExtents=new Vector2$1(1,1);this._viewportCount=1;this._viewports=[new Vector4$1(0,0,1,1)];}getViewportCount(){return this._viewportCount;}getFrustum(){return this._frustum;}updateMatrices(light){const shadowCamera=this.camera;const shadowMatrix=this.matrix;_lightPositionWorld$1.setFromMatrixPosition(light.matrixWorld);shadowCamera.position.copy(_lightPositionWorld$1);_lookTarget$1.setFromMatrixPosition(light.target.matrixWorld);shadowCamera.lookAt(_lookTarget$1);shadowCamera.updateMatrixWorld();_projScreenMatrix$1.multiplyMatrices(shadowCamera.projectionMatrix,shadowCamera.matrixWorldInverse);this._frustum.setFromProjectionMatrix(_projScreenMatrix$1);shadowMatrix.set(0.5,0.0,0.0,0.5,0.0,0.5,0.0,0.5,0.0,0.0,0.5,0.5,0.0,0.0,0.0,1.0);shadowMatrix.multiply(_projScreenMatrix$1);}getViewport(viewportIndex){return this._viewports[viewportIndex];}getFrameExtents(){return this._frameExtents;}dispose(){if(this.map){this.map.dispose();}if(this.mapPass){this.mapPass.dispose();}}copy(source){this.camera=source.camera.clone();this.intensity=source.intensity;this.bias=source.bias;this.radius=source.radius;this.mapSize.copy(source.mapSize);return this;}clone(){return new this.constructor().copy(this);}toJSON(){const object={};if(this.intensity!==1)object.intensity=this.intensity;if(this.bias!==0)object.bias=this.bias;if(this.normalBias!==0)object.normalBias=this.normalBias;if(this.radius!==1)object.radius=this.radius;if(this.mapSize.x!==512||this.mapSize.y!==512)object.mapSize=this.mapSize.toArray();object.camera=this.camera.toJSON(false).object;delete object.camera.matrix;return object;}}class SpotLightShadow extends LightShadow{constructor(){super(new PerspectiveCamera$1(50,1,0.5,500));this.isSpotLightShadow=true;this.focus=1;}updateMatrices(light){const camera=this.camera;const fov=RAD2DEG$1*2*light.angle*this.focus;const aspect=this.mapSize.width/this.mapSize.height;const far=light.distance||camera.far;if(fov!==camera.fov||aspect!==camera.aspect||far!==camera.far){camera.fov=fov;camera.aspect=aspect;camera.far=far;camera.updateProjectionMatrix();}super.updateMatrices(light);}copy(source){super.copy(source);this.focus=source.focus;return this;}}class SpotLight extends Light{constructor(color,intensity,distance=0,angle=Math.PI/3,penumbra=0,decay=2){super(color,intensity);this.isSpotLight=true;this.type='SpotLight';this.position.copy(Object3D$1.DEFAULT_UP);this.updateMatrix();this.target=new Object3D$1();this.distance=distance;this.angle=angle;this.penumbra=penumbra;this.decay=decay;this.map=null;this.shadow=new SpotLightShadow();}get power(){// compute the light's luminous power (in lumens) from its intensity (in candela)
// by convention for a spotlight, luminous power (lm) = π * luminous intensity (cd)
return this.intensity*Math.PI;}set power(power){// set the light's intensity (in candela) from the desired luminous power (in lumens)
this.intensity=power/Math.PI;}dispose(){this.shadow.dispose();}copy(source,recursive){super.copy(source,recursive);this.distance=source.distance;this.angle=source.angle;this.penumbra=source.penumbra;this.decay=source.decay;this.target=source.target.clone();this.shadow=source.shadow.clone();return this;}}const _projScreenMatrix=/*@__PURE__*/new Matrix4$1();const _lightPositionWorld=/*@__PURE__*/new Vector3$1();const _lookTarget=/*@__PURE__*/new Vector3$1();class PointLightShadow extends LightShadow{constructor(){super(new PerspectiveCamera$1(90,1,0.5,500));this.isPointLightShadow=true;this._frameExtents=new Vector2$1(4,2);this._viewportCount=6;this._viewports=[// These viewports map a cube-map onto a 2D texture with the
// following orientation:
//
//  xzXZ
//   y Y
//
// X - Positive x direction
// x - Negative x direction
// Y - Positive y direction
// y - Negative y direction
// Z - Positive z direction
// z - Negative z direction
// positive X
new Vector4$1(2,1,1,1),// negative X
new Vector4$1(0,1,1,1),// positive Z
new Vector4$1(3,1,1,1),// negative Z
new Vector4$1(1,1,1,1),// positive Y
new Vector4$1(3,0,1,1),// negative Y
new Vector4$1(1,0,1,1)];this._cubeDirections=[new Vector3$1(1,0,0),new Vector3$1(-1,0,0),new Vector3$1(0,0,1),new Vector3$1(0,0,-1),new Vector3$1(0,1,0),new Vector3$1(0,-1,0)];this._cubeUps=[new Vector3$1(0,1,0),new Vector3$1(0,1,0),new Vector3$1(0,1,0),new Vector3$1(0,1,0),new Vector3$1(0,0,1),new Vector3$1(0,0,-1)];}updateMatrices(light,viewportIndex=0){const camera=this.camera;const shadowMatrix=this.matrix;const far=light.distance||camera.far;if(far!==camera.far){camera.far=far;camera.updateProjectionMatrix();}_lightPositionWorld.setFromMatrixPosition(light.matrixWorld);camera.position.copy(_lightPositionWorld);_lookTarget.copy(camera.position);_lookTarget.add(this._cubeDirections[viewportIndex]);camera.up.copy(this._cubeUps[viewportIndex]);camera.lookAt(_lookTarget);camera.updateMatrixWorld();shadowMatrix.makeTranslation(-_lightPositionWorld.x,-_lightPositionWorld.y,-_lightPositionWorld.z);_projScreenMatrix.multiplyMatrices(camera.projectionMatrix,camera.matrixWorldInverse);this._frustum.setFromProjectionMatrix(_projScreenMatrix);}}class PointLight extends Light{constructor(color,intensity,distance=0,decay=2){super(color,intensity);this.isPointLight=true;this.type='PointLight';this.distance=distance;this.decay=decay;this.shadow=new PointLightShadow();}get power(){// compute the light's luminous power (in lumens) from its intensity (in candela)
// for an isotropic light source, luminous power (lm) = 4 π luminous intensity (cd)
return this.intensity*4*Math.PI;}set power(power){// set the light's intensity (in candela) from the desired luminous power (in lumens)
this.intensity=power/(4*Math.PI);}dispose(){this.shadow.dispose();}copy(source,recursive){super.copy(source,recursive);this.distance=source.distance;this.decay=source.decay;this.shadow=source.shadow.clone();return this;}}class DirectionalLightShadow extends LightShadow{constructor(){super(new OrthographicCamera$1(-5,5,5,-5,0.5,500));this.isDirectionalLightShadow=true;}}class DirectionalLight extends Light{constructor(color,intensity){super(color,intensity);this.isDirectionalLight=true;this.type='DirectionalLight';this.position.copy(Object3D$1.DEFAULT_UP);this.updateMatrix();this.target=new Object3D$1();this.shadow=new DirectionalLightShadow();}dispose(){this.shadow.dispose();}copy(source){super.copy(source);this.target=source.target.clone();this.shadow=source.shadow.clone();return this;}}class AmbientLight extends Light{constructor(color,intensity){super(color,intensity);this.isAmbientLight=true;this.type='AmbientLight';}}class LoaderUtils{static decodeText(array){// @deprecated, r165
console.warn('THREE.LoaderUtils: decodeText() has been deprecated with r165 and will be removed with r175. Use TextDecoder instead.');if(typeof TextDecoder!=='undefined'){return new TextDecoder().decode(array);}// Avoid the String.fromCharCode.apply(null, array) shortcut, which
// throws a "maximum call stack size exceeded" error for large arrays.
let s='';for(let i=0,il=array.length;i<il;i++){// Implicitly assumes little-endian.
s+=String.fromCharCode(array[i]);}try{// merges multi-byte utf-8 characters.
return decodeURIComponent(escape(s));}catch(e){// see #16358
return s;}}static extractUrlBase(url){const index=url.lastIndexOf('/');if(index===-1)return './';return url.slice(0,index+1);}static resolveURL(url,path){// Invalid URL
if(typeof url!=='string'||url==='')return '';// Host Relative URL
if(/^https?:\/\//i.test(path)&&/^\//.test(url)){path=path.replace(/(^https?:\/\/[^\/]+).*/i,'$1');}// Absolute URL http://,https://,//
if(/^(https?:)?\/\//i.test(url))return url;// Data URI
if(/^data:.*,.*$/i.test(url))return url;// Blob URL
if(/^blob:.*$/i.test(url))return url;// Relative URL
return path+url;}}class ImageBitmapLoader extends Loader{constructor(manager){super(manager);this.isImageBitmapLoader=true;if(typeof createImageBitmap==='undefined'){console.warn('THREE.ImageBitmapLoader: createImageBitmap() not supported.');}if(typeof fetch==='undefined'){console.warn('THREE.ImageBitmapLoader: fetch() not supported.');}this.options={premultiplyAlpha:'none'};}setOptions(options){this.options=options;return this;}load(url,onLoad,onProgress,onError){if(url===undefined)url='';if(this.path!==undefined)url=this.path+url;url=this.manager.resolveURL(url);const scope=this;const cached=Cache.get(url);if(cached!==undefined){scope.manager.itemStart(url);// If cached is a promise, wait for it to resolve
if(cached.then){cached.then(imageBitmap=>{if(onLoad)onLoad(imageBitmap);scope.manager.itemEnd(url);}).catch(e=>{if(onError)onError(e);});return;}// If cached is not a promise (i.e., it's already an imageBitmap)
setTimeout(function(){if(onLoad)onLoad(cached);scope.manager.itemEnd(url);},0);return cached;}const fetchOptions={};fetchOptions.credentials=this.crossOrigin==='anonymous'?'same-origin':'include';fetchOptions.headers=this.requestHeader;const promise=fetch(url,fetchOptions).then(function(res){return res.blob();}).then(function(blob){return createImageBitmap(blob,Object.assign(scope.options,{colorSpaceConversion:'none'}));}).then(function(imageBitmap){Cache.add(url,imageBitmap);if(onLoad)onLoad(imageBitmap);scope.manager.itemEnd(url);return imageBitmap;}).catch(function(e){if(onError)onError(e);Cache.remove(url);scope.manager.itemError(url);scope.manager.itemEnd(url);});Cache.add(url,promise);scope.manager.itemStart(url);}}const _RESERVED_CHARS_RE='\\[\\]\\.:\\/';const _reservedRe=new RegExp('['+_RESERVED_CHARS_RE+']','g');// Attempts to allow node names from any language. ES5's `\w` regexp matches
// only latin characters, and the unicode \p{L} is not yet supported. So
// instead, we exclude reserved characters and match everything else.
const _wordChar='[^'+_RESERVED_CHARS_RE+']';const _wordCharOrDot='[^'+_RESERVED_CHARS_RE.replace('\\.','')+']';// Parent directories, delimited by '/' or ':'. Currently unused, but must
// be matched to parse the rest of the track name.
const _directoryRe=/*@__PURE__*/ /((?:WC+[\/:])*)/.source.replace('WC',_wordChar);// Target node. May contain word characters (a-zA-Z0-9_) and '.' or '-'.
const _nodeRe=/*@__PURE__*/ /(WCOD+)?/.source.replace('WCOD',_wordCharOrDot);// Object on target node, and accessor. May not contain reserved
// characters. Accessor may contain any character except closing bracket.
const _objectRe=/*@__PURE__*/ /(?:\.(WC+)(?:\[(.+)\])?)?/.source.replace('WC',_wordChar);// Property and accessor. May not contain reserved characters. Accessor may
// contain any non-bracket characters.
const _propertyRe=/*@__PURE__*/ /\.(WC+)(?:\[(.+)\])?/.source.replace('WC',_wordChar);const _trackRe=new RegExp(''+'^'+_directoryRe+_nodeRe+_objectRe+_propertyRe+'$');const _supportedObjectNames=['material','materials','bones','map'];class Composite{constructor(targetGroup,path,optionalParsedPath){const parsedPath=optionalParsedPath||PropertyBinding.parseTrackName(path);this._targetGroup=targetGroup;this._bindings=targetGroup.subscribe_(path,parsedPath);}getValue(array,offset){this.bind();// bind all binding
const firstValidIndex=this._targetGroup.nCachedObjects_,binding=this._bindings[firstValidIndex];// and only call .getValue on the first
if(binding!==undefined)binding.getValue(array,offset);}setValue(array,offset){const bindings=this._bindings;for(let i=this._targetGroup.nCachedObjects_,n=bindings.length;i!==n;++i){bindings[i].setValue(array,offset);}}bind(){const bindings=this._bindings;for(let i=this._targetGroup.nCachedObjects_,n=bindings.length;i!==n;++i){bindings[i].bind();}}unbind(){const bindings=this._bindings;for(let i=this._targetGroup.nCachedObjects_,n=bindings.length;i!==n;++i){bindings[i].unbind();}}}// Note: This class uses a State pattern on a per-method basis:
// 'bind' sets 'this.getValue' / 'setValue' and shadows the
// prototype version of these methods with one that represents
// the bound state. When the property is not found, the methods
// become no-ops.
class PropertyBinding{constructor(rootNode,path,parsedPath){this.path=path;this.parsedPath=parsedPath||PropertyBinding.parseTrackName(path);this.node=PropertyBinding.findNode(rootNode,this.parsedPath.nodeName);this.rootNode=rootNode;// initial state of these methods that calls 'bind'
this.getValue=this._getValue_unbound;this.setValue=this._setValue_unbound;}static create(root,path,parsedPath){if(!(root&&root.isAnimationObjectGroup)){return new PropertyBinding(root,path,parsedPath);}else {return new PropertyBinding.Composite(root,path,parsedPath);}}/**
	 * Replaces spaces with underscores and removes unsupported characters from
	 * node names, to ensure compatibility with parseTrackName().
	 *
	 * @param {string} name Node name to be sanitized.
	 * @return {string}
	 */static sanitizeNodeName(name){return name.replace(/\s/g,'_').replace(_reservedRe,'');}static parseTrackName(trackName){const matches=_trackRe.exec(trackName);if(matches===null){throw new Error('PropertyBinding: Cannot parse trackName: '+trackName);}const results={// directoryName: matches[ 1 ], // (tschw) currently unused
nodeName:matches[2],objectName:matches[3],objectIndex:matches[4],propertyName:matches[5],// required
propertyIndex:matches[6]};const lastDot=results.nodeName&&results.nodeName.lastIndexOf('.');if(lastDot!==undefined&&lastDot!==-1){const objectName=results.nodeName.substring(lastDot+1);// Object names must be checked against an allowlist. Otherwise, there
// is no way to parse 'foo.bar.baz': 'baz' must be a property, but
// 'bar' could be the objectName, or part of a nodeName (which can
// include '.' characters).
if(_supportedObjectNames.indexOf(objectName)!==-1){results.nodeName=results.nodeName.substring(0,lastDot);results.objectName=objectName;}}if(results.propertyName===null||results.propertyName.length===0){throw new Error('PropertyBinding: can not parse propertyName from trackName: '+trackName);}return results;}static findNode(root,nodeName){if(nodeName===undefined||nodeName===''||nodeName==='.'||nodeName===-1||nodeName===root.name||nodeName===root.uuid){return root;}// search into skeleton bones.
if(root.skeleton){const bone=root.skeleton.getBoneByName(nodeName);if(bone!==undefined){return bone;}}// search into node subtree.
if(root.children){const searchNodeSubtree=function(children){for(let i=0;i<children.length;i++){const childNode=children[i];if(childNode.name===nodeName||childNode.uuid===nodeName){return childNode;}const result=searchNodeSubtree(childNode.children);if(result)return result;}return null;};const subTreeNode=searchNodeSubtree(root.children);if(subTreeNode){return subTreeNode;}}return null;}// these are used to "bind" a nonexistent property
_getValue_unavailable(){}_setValue_unavailable(){}// Getters
_getValue_direct(buffer,offset){buffer[offset]=this.targetObject[this.propertyName];}_getValue_array(buffer,offset){const source=this.resolvedProperty;for(let i=0,n=source.length;i!==n;++i){buffer[offset++]=source[i];}}_getValue_arrayElement(buffer,offset){buffer[offset]=this.resolvedProperty[this.propertyIndex];}_getValue_toArray(buffer,offset){this.resolvedProperty.toArray(buffer,offset);}// Direct
_setValue_direct(buffer,offset){this.targetObject[this.propertyName]=buffer[offset];}_setValue_direct_setNeedsUpdate(buffer,offset){this.targetObject[this.propertyName]=buffer[offset];this.targetObject.needsUpdate=true;}_setValue_direct_setMatrixWorldNeedsUpdate(buffer,offset){this.targetObject[this.propertyName]=buffer[offset];this.targetObject.matrixWorldNeedsUpdate=true;}// EntireArray
_setValue_array(buffer,offset){const dest=this.resolvedProperty;for(let i=0,n=dest.length;i!==n;++i){dest[i]=buffer[offset++];}}_setValue_array_setNeedsUpdate(buffer,offset){const dest=this.resolvedProperty;for(let i=0,n=dest.length;i!==n;++i){dest[i]=buffer[offset++];}this.targetObject.needsUpdate=true;}_setValue_array_setMatrixWorldNeedsUpdate(buffer,offset){const dest=this.resolvedProperty;for(let i=0,n=dest.length;i!==n;++i){dest[i]=buffer[offset++];}this.targetObject.matrixWorldNeedsUpdate=true;}// ArrayElement
_setValue_arrayElement(buffer,offset){this.resolvedProperty[this.propertyIndex]=buffer[offset];}_setValue_arrayElement_setNeedsUpdate(buffer,offset){this.resolvedProperty[this.propertyIndex]=buffer[offset];this.targetObject.needsUpdate=true;}_setValue_arrayElement_setMatrixWorldNeedsUpdate(buffer,offset){this.resolvedProperty[this.propertyIndex]=buffer[offset];this.targetObject.matrixWorldNeedsUpdate=true;}// HasToFromArray
_setValue_fromArray(buffer,offset){this.resolvedProperty.fromArray(buffer,offset);}_setValue_fromArray_setNeedsUpdate(buffer,offset){this.resolvedProperty.fromArray(buffer,offset);this.targetObject.needsUpdate=true;}_setValue_fromArray_setMatrixWorldNeedsUpdate(buffer,offset){this.resolvedProperty.fromArray(buffer,offset);this.targetObject.matrixWorldNeedsUpdate=true;}_getValue_unbound(targetArray,offset){this.bind();this.getValue(targetArray,offset);}_setValue_unbound(sourceArray,offset){this.bind();this.setValue(sourceArray,offset);}// create getter / setter pair for a property in the scene graph
bind(){let targetObject=this.node;const parsedPath=this.parsedPath;const objectName=parsedPath.objectName;const propertyName=parsedPath.propertyName;let propertyIndex=parsedPath.propertyIndex;if(!targetObject){targetObject=PropertyBinding.findNode(this.rootNode,parsedPath.nodeName);this.node=targetObject;}// set fail state so we can just 'return' on error
this.getValue=this._getValue_unavailable;this.setValue=this._setValue_unavailable;// ensure there is a value node
if(!targetObject){console.warn('THREE.PropertyBinding: No target node found for track: '+this.path+'.');return;}if(objectName){let objectIndex=parsedPath.objectIndex;// special cases were we need to reach deeper into the hierarchy to get the face materials....
switch(objectName){case'materials':if(!targetObject.material){console.error('THREE.PropertyBinding: Can not bind to material as node does not have a material.',this);return;}if(!targetObject.material.materials){console.error('THREE.PropertyBinding: Can not bind to material.materials as node.material does not have a materials array.',this);return;}targetObject=targetObject.material.materials;break;case'bones':if(!targetObject.skeleton){console.error('THREE.PropertyBinding: Can not bind to bones as node does not have a skeleton.',this);return;}// potential future optimization: skip this if propertyIndex is already an integer
// and convert the integer string to a true integer.
targetObject=targetObject.skeleton.bones;// support resolving morphTarget names into indices.
for(let i=0;i<targetObject.length;i++){if(targetObject[i].name===objectIndex){objectIndex=i;break;}}break;case'map':if('map'in targetObject){targetObject=targetObject.map;break;}if(!targetObject.material){console.error('THREE.PropertyBinding: Can not bind to material as node does not have a material.',this);return;}if(!targetObject.material.map){console.error('THREE.PropertyBinding: Can not bind to material.map as node.material does not have a map.',this);return;}targetObject=targetObject.material.map;break;default:if(targetObject[objectName]===undefined){console.error('THREE.PropertyBinding: Can not bind to objectName of node undefined.',this);return;}targetObject=targetObject[objectName];}if(objectIndex!==undefined){if(targetObject[objectIndex]===undefined){console.error('THREE.PropertyBinding: Trying to bind to objectIndex of objectName, but is undefined.',this,targetObject);return;}targetObject=targetObject[objectIndex];}}// resolve property
const nodeProperty=targetObject[propertyName];if(nodeProperty===undefined){const nodeName=parsedPath.nodeName;console.error('THREE.PropertyBinding: Trying to update property for track: '+nodeName+'.'+propertyName+' but it wasn\'t found.',targetObject);return;}// determine versioning scheme
let versioning=this.Versioning.None;this.targetObject=targetObject;if(targetObject.needsUpdate!==undefined){// material
versioning=this.Versioning.NeedsUpdate;}else if(targetObject.matrixWorldNeedsUpdate!==undefined){// node transform
versioning=this.Versioning.MatrixWorldNeedsUpdate;}// determine how the property gets bound
let bindingType=this.BindingType.Direct;if(propertyIndex!==undefined){// access a sub element of the property array (only primitives are supported right now)
if(propertyName==='morphTargetInfluences'){// potential optimization, skip this if propertyIndex is already an integer, and convert the integer string to a true integer.
// support resolving morphTarget names into indices.
if(!targetObject.geometry){console.error('THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.',this);return;}if(!targetObject.geometry.morphAttributes){console.error('THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.morphAttributes.',this);return;}if(targetObject.morphTargetDictionary[propertyIndex]!==undefined){propertyIndex=targetObject.morphTargetDictionary[propertyIndex];}}bindingType=this.BindingType.ArrayElement;this.resolvedProperty=nodeProperty;this.propertyIndex=propertyIndex;}else if(nodeProperty.fromArray!==undefined&&nodeProperty.toArray!==undefined){// must use copy for Object3D.Euler/Quaternion
bindingType=this.BindingType.HasFromToArray;this.resolvedProperty=nodeProperty;}else if(Array.isArray(nodeProperty)){bindingType=this.BindingType.EntireArray;this.resolvedProperty=nodeProperty;}else {this.propertyName=propertyName;}// select getter / setter
this.getValue=this.GetterByBindingType[bindingType];this.setValue=this.SetterByBindingTypeAndVersioning[bindingType][versioning];}unbind(){this.node=null;// back to the prototype version of getValue / setValue
// note: avoiding to mutate the shape of 'this' via 'delete'
this.getValue=this._getValue_unbound;this.setValue=this._setValue_unbound;}}PropertyBinding.Composite=Composite;PropertyBinding.prototype.BindingType={Direct:0,EntireArray:1,ArrayElement:2,HasFromToArray:3};PropertyBinding.prototype.Versioning={None:0,NeedsUpdate:1,MatrixWorldNeedsUpdate:2};PropertyBinding.prototype.GetterByBindingType=[PropertyBinding.prototype._getValue_direct,PropertyBinding.prototype._getValue_array,PropertyBinding.prototype._getValue_arrayElement,PropertyBinding.prototype._getValue_toArray];PropertyBinding.prototype.SetterByBindingTypeAndVersioning=[[// Direct
PropertyBinding.prototype._setValue_direct,PropertyBinding.prototype._setValue_direct_setNeedsUpdate,PropertyBinding.prototype._setValue_direct_setMatrixWorldNeedsUpdate],[// EntireArray
PropertyBinding.prototype._setValue_array,PropertyBinding.prototype._setValue_array_setNeedsUpdate,PropertyBinding.prototype._setValue_array_setMatrixWorldNeedsUpdate],[// ArrayElement
PropertyBinding.prototype._setValue_arrayElement,PropertyBinding.prototype._setValue_arrayElement_setNeedsUpdate,PropertyBinding.prototype._setValue_arrayElement_setMatrixWorldNeedsUpdate],[// HasToFromArray
PropertyBinding.prototype._setValue_fromArray,PropertyBinding.prototype._setValue_fromArray_setNeedsUpdate,PropertyBinding.prototype._setValue_fromArray_setMatrixWorldNeedsUpdate]];/**
 * Ref: https://en.wikipedia.org/wiki/Spherical_coordinate_system
 *
 * phi (the polar angle) is measured from the positive y-axis. The positive y-axis is up.
 * theta (the azimuthal angle) is measured from the positive z-axis.
 */class Spherical{constructor(radius=1,phi=0,theta=0){this.radius=radius;this.phi=phi;// polar angle
this.theta=theta;// azimuthal angle
return this;}set(radius,phi,theta){this.radius=radius;this.phi=phi;this.theta=theta;return this;}copy(other){this.radius=other.radius;this.phi=other.phi;this.theta=other.theta;return this;}// restrict phi to be between EPS and PI-EPS
makeSafe(){const EPS=0.000001;this.phi=Math.max(EPS,Math.min(Math.PI-EPS,this.phi));return this;}setFromVector3(v){return this.setFromCartesianCoords(v.x,v.y,v.z);}setFromCartesianCoords(x,y,z){this.radius=Math.sqrt(x*x+y*y+z*z);if(this.radius===0){this.theta=0;this.phi=0;}else {this.theta=Math.atan2(x,z);this.phi=Math.acos(clamp$2(y/this.radius,-1,1));}return this;}clone(){return new this.constructor().copy(this);}}class Controls extends EventDispatcher$1{constructor(object,domElement){super();this.object=object;this.domElement=domElement;this.enabled=true;this.state=-1;this.keys={};this.mouseButtons={LEFT:null,MIDDLE:null,RIGHT:null};this.touches={ONE:null,TWO:null};}connect(){}disconnect(){}dispose(){}update(/* delta */){}}if(typeof __THREE_DEVTOOLS__!=='undefined'){__THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('register',{detail:{revision:REVISION}}));}if(typeof window!=='undefined'){if(window.__THREE__){console.warn('WARNING: Multiple instances of Three.js being imported.');}else {window.__THREE__=REVISION;}}

/**
 * @param {BufferGeometry} geometry
 * @param {number} drawMode
 * @return {BufferGeometry}
 */
function toTrianglesDrawMode(geometry, drawMode) {
  if (drawMode === TrianglesDrawMode) {
    console.warn('THREE.BufferGeometryUtils.toTrianglesDrawMode(): Geometry already defined as triangles.');
    return geometry;
  }
  if (drawMode === TriangleFanDrawMode || drawMode === TriangleStripDrawMode) {
    let index = geometry.getIndex();

    // generate index if not present

    if (index === null) {
      const indices = [];
      const position = geometry.getAttribute('position');
      if (position !== undefined) {
        for (let i = 0; i < position.count; i++) {
          indices.push(i);
        }
        geometry.setIndex(indices);
        index = geometry.getIndex();
      } else {
        console.error('THREE.BufferGeometryUtils.toTrianglesDrawMode(): Undefined position attribute. Processing not possible.');
        return geometry;
      }
    }

    //

    const numberOfTriangles = index.count - 2;
    const newIndices = [];
    if (drawMode === TriangleFanDrawMode) {
      // gl.TRIANGLE_FAN

      for (let i = 1; i <= numberOfTriangles; i++) {
        newIndices.push(index.getX(0));
        newIndices.push(index.getX(i));
        newIndices.push(index.getX(i + 1));
      }
    } else {
      // gl.TRIANGLE_STRIP

      for (let i = 0; i < numberOfTriangles; i++) {
        if (i % 2 === 0) {
          newIndices.push(index.getX(i));
          newIndices.push(index.getX(i + 1));
          newIndices.push(index.getX(i + 2));
        } else {
          newIndices.push(index.getX(i + 2));
          newIndices.push(index.getX(i + 1));
          newIndices.push(index.getX(i));
        }
      }
    }
    if (newIndices.length / 3 !== numberOfTriangles) {
      console.error('THREE.BufferGeometryUtils.toTrianglesDrawMode(): Unable to generate correct amount of triangles.');
    }

    // build final geometry

    const newGeometry = geometry.clone();
    newGeometry.setIndex(newIndices);
    newGeometry.clearGroups();
    return newGeometry;
  } else {
    console.error('THREE.BufferGeometryUtils.toTrianglesDrawMode(): Unknown draw mode:', drawMode);
    return geometry;
  }
}

class GLTFLoader extends Loader {
  constructor(manager) {
    super(manager);
    this.dracoLoader = null;
    this.ktx2Loader = null;
    this.meshoptDecoder = null;
    this.pluginCallbacks = [];
    this.register(function (parser) {
      return new GLTFMaterialsClearcoatExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsDispersionExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFTextureBasisUExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFTextureWebPExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFTextureAVIFExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsSheenExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsTransmissionExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsVolumeExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsIorExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsEmissiveStrengthExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsSpecularExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsIridescenceExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsAnisotropyExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMaterialsBumpExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFLightsExtension(parser);
    });
    this.register(function (parser) {
      return new GLTFMeshoptCompression(parser);
    });
    this.register(function (parser) {
      return new GLTFMeshGpuInstancing(parser);
    });
  }
  load(url, onLoad, onProgress, onError) {
    const scope = this;
    let resourcePath;
    if (this.resourcePath !== '') {
      resourcePath = this.resourcePath;
    } else if (this.path !== '') {
      // If a base path is set, resources will be relative paths from that plus the relative path of the gltf file
      // Example  path = 'https://my-cnd-server.com/', url = 'assets/models/model.gltf'
      // resourcePath = 'https://my-cnd-server.com/assets/models/'
      // referenced resource 'model.bin' will be loaded from 'https://my-cnd-server.com/assets/models/model.bin'
      // referenced resource '../textures/texture.png' will be loaded from 'https://my-cnd-server.com/assets/textures/texture.png'
      const relativeUrl = LoaderUtils.extractUrlBase(url);
      resourcePath = LoaderUtils.resolveURL(relativeUrl, this.path);
    } else {
      resourcePath = LoaderUtils.extractUrlBase(url);
    }

    // Tells the LoadingManager to track an extra item, which resolves after
    // the model is fully loaded. This means the count of items loaded will
    // be incorrect, but ensures manager.onLoad() does not fire early.
    this.manager.itemStart(url);
    const _onError = function (e) {
      if (onError) {
        onError(e);
      } else {
        console.error(e);
      }
      scope.manager.itemError(url);
      scope.manager.itemEnd(url);
    };
    const loader = new FileLoader(this.manager);
    loader.setPath(this.path);
    loader.setResponseType('arraybuffer');
    loader.setRequestHeader(this.requestHeader);
    loader.setWithCredentials(this.withCredentials);
    loader.load(url, function (data) {
      try {
        scope.parse(data, resourcePath, function (gltf) {
          onLoad(gltf);
          scope.manager.itemEnd(url);
        }, _onError);
      } catch (e) {
        _onError(e);
      }
    }, onProgress, _onError);
  }
  setDRACOLoader(dracoLoader) {
    this.dracoLoader = dracoLoader;
    return this;
  }
  setDDSLoader() {
    throw new Error('THREE.GLTFLoader: "MSFT_texture_dds" no longer supported. Please update to "KHR_texture_basisu".');
  }
  setKTX2Loader(ktx2Loader) {
    this.ktx2Loader = ktx2Loader;
    return this;
  }
  setMeshoptDecoder(meshoptDecoder) {
    this.meshoptDecoder = meshoptDecoder;
    return this;
  }
  register(callback) {
    if (this.pluginCallbacks.indexOf(callback) === -1) {
      this.pluginCallbacks.push(callback);
    }
    return this;
  }
  unregister(callback) {
    if (this.pluginCallbacks.indexOf(callback) !== -1) {
      this.pluginCallbacks.splice(this.pluginCallbacks.indexOf(callback), 1);
    }
    return this;
  }
  parse(data, path, onLoad, onError) {
    let json;
    const extensions = {};
    const plugins = {};
    const textDecoder = new TextDecoder();
    if (typeof data === 'string') {
      json = JSON.parse(data);
    } else if (data instanceof ArrayBuffer) {
      const magic = textDecoder.decode(new Uint8Array(data, 0, 4));
      if (magic === BINARY_EXTENSION_HEADER_MAGIC) {
        try {
          extensions[EXTENSIONS.KHR_BINARY_GLTF] = new GLTFBinaryExtension(data);
        } catch (error) {
          if (onError) onError(error);
          return;
        }
        json = JSON.parse(extensions[EXTENSIONS.KHR_BINARY_GLTF].content);
      } else {
        json = JSON.parse(textDecoder.decode(data));
      }
    } else {
      json = data;
    }
    if (json.asset === undefined || json.asset.version[0] < 2) {
      if (onError) onError(new Error('THREE.GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported.'));
      return;
    }
    const parser = new GLTFParser(json, {
      path: path || this.resourcePath || '',
      crossOrigin: this.crossOrigin,
      requestHeader: this.requestHeader,
      manager: this.manager,
      ktx2Loader: this.ktx2Loader,
      meshoptDecoder: this.meshoptDecoder
    });
    parser.fileLoader.setRequestHeader(this.requestHeader);
    for (let i = 0; i < this.pluginCallbacks.length; i++) {
      const plugin = this.pluginCallbacks[i](parser);
      if (!plugin.name) console.error('THREE.GLTFLoader: Invalid plugin found: missing name');
      plugins[plugin.name] = plugin;

      // Workaround to avoid determining as unknown extension
      // in addUnknownExtensionsToUserData().
      // Remove this workaround if we move all the existing
      // extension handlers to plugin system
      extensions[plugin.name] = true;
    }
    if (json.extensionsUsed) {
      for (let i = 0; i < json.extensionsUsed.length; ++i) {
        const extensionName = json.extensionsUsed[i];
        const extensionsRequired = json.extensionsRequired || [];
        switch (extensionName) {
          case EXTENSIONS.KHR_MATERIALS_UNLIT:
            extensions[extensionName] = new GLTFMaterialsUnlitExtension();
            break;
          case EXTENSIONS.KHR_DRACO_MESH_COMPRESSION:
            extensions[extensionName] = new GLTFDracoMeshCompressionExtension(json, this.dracoLoader);
            break;
          case EXTENSIONS.KHR_TEXTURE_TRANSFORM:
            extensions[extensionName] = new GLTFTextureTransformExtension();
            break;
          case EXTENSIONS.KHR_MESH_QUANTIZATION:
            extensions[extensionName] = new GLTFMeshQuantizationExtension();
            break;
          default:
            if (extensionsRequired.indexOf(extensionName) >= 0 && plugins[extensionName] === undefined) {
              console.warn('THREE.GLTFLoader: Unknown extension "' + extensionName + '".');
            }
        }
      }
    }
    parser.setExtensions(extensions);
    parser.setPlugins(plugins);
    parser.parse(onLoad, onError);
  }
  parseAsync(data, path) {
    const scope = this;
    return new Promise(function (resolve, reject) {
      scope.parse(data, path, resolve, reject);
    });
  }
}

/* GLTFREGISTRY */

function GLTFRegistry() {
  let objects = {};
  return {
    get: function (key) {
      return objects[key];
    },
    add: function (key, object) {
      objects[key] = object;
    },
    remove: function (key) {
      delete objects[key];
    },
    removeAll: function () {
      objects = {};
    }
  };
}

/*********************************/
/********** EXTENSIONS ***********/
/*********************************/

const EXTENSIONS = {
  KHR_BINARY_GLTF: 'KHR_binary_glTF',
  KHR_DRACO_MESH_COMPRESSION: 'KHR_draco_mesh_compression',
  KHR_LIGHTS_PUNCTUAL: 'KHR_lights_punctual',
  KHR_MATERIALS_CLEARCOAT: 'KHR_materials_clearcoat',
  KHR_MATERIALS_DISPERSION: 'KHR_materials_dispersion',
  KHR_MATERIALS_IOR: 'KHR_materials_ior',
  KHR_MATERIALS_SHEEN: 'KHR_materials_sheen',
  KHR_MATERIALS_SPECULAR: 'KHR_materials_specular',
  KHR_MATERIALS_TRANSMISSION: 'KHR_materials_transmission',
  KHR_MATERIALS_IRIDESCENCE: 'KHR_materials_iridescence',
  KHR_MATERIALS_ANISOTROPY: 'KHR_materials_anisotropy',
  KHR_MATERIALS_UNLIT: 'KHR_materials_unlit',
  KHR_MATERIALS_VOLUME: 'KHR_materials_volume',
  KHR_TEXTURE_BASISU: 'KHR_texture_basisu',
  KHR_TEXTURE_TRANSFORM: 'KHR_texture_transform',
  KHR_MESH_QUANTIZATION: 'KHR_mesh_quantization',
  KHR_MATERIALS_EMISSIVE_STRENGTH: 'KHR_materials_emissive_strength',
  EXT_MATERIALS_BUMP: 'EXT_materials_bump',
  EXT_TEXTURE_WEBP: 'EXT_texture_webp',
  EXT_TEXTURE_AVIF: 'EXT_texture_avif',
  EXT_MESHOPT_COMPRESSION: 'EXT_meshopt_compression',
  EXT_MESH_GPU_INSTANCING: 'EXT_mesh_gpu_instancing'
};

/**
 * Punctual Lights Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_lights_punctual
 */
class GLTFLightsExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_LIGHTS_PUNCTUAL;

    // Object3D instance caches
    this.cache = {
      refs: {},
      uses: {}
    };
  }
  _markDefs() {
    const parser = this.parser;
    const nodeDefs = this.parser.json.nodes || [];
    for (let nodeIndex = 0, nodeLength = nodeDefs.length; nodeIndex < nodeLength; nodeIndex++) {
      const nodeDef = nodeDefs[nodeIndex];
      if (nodeDef.extensions && nodeDef.extensions[this.name] && nodeDef.extensions[this.name].light !== undefined) {
        parser._addNodeRef(this.cache, nodeDef.extensions[this.name].light);
      }
    }
  }
  _loadLight(lightIndex) {
    const parser = this.parser;
    const cacheKey = 'light:' + lightIndex;
    let dependency = parser.cache.get(cacheKey);
    if (dependency) return dependency;
    const json = parser.json;
    const extensions = json.extensions && json.extensions[this.name] || {};
    const lightDefs = extensions.lights || [];
    const lightDef = lightDefs[lightIndex];
    let lightNode;
    const color = new Color$1(0xffffff);
    if (lightDef.color !== undefined) color.setRGB(lightDef.color[0], lightDef.color[1], lightDef.color[2], LinearSRGBColorSpace$1);
    const range = lightDef.range !== undefined ? lightDef.range : 0;
    switch (lightDef.type) {
      case 'directional':
        lightNode = new DirectionalLight(color);
        lightNode.target.position.set(0, 0, -1);
        lightNode.add(lightNode.target);
        break;
      case 'point':
        lightNode = new PointLight(color);
        lightNode.distance = range;
        break;
      case 'spot':
        lightNode = new SpotLight(color);
        lightNode.distance = range;
        // Handle spotlight properties.
        lightDef.spot = lightDef.spot || {};
        lightDef.spot.innerConeAngle = lightDef.spot.innerConeAngle !== undefined ? lightDef.spot.innerConeAngle : 0;
        lightDef.spot.outerConeAngle = lightDef.spot.outerConeAngle !== undefined ? lightDef.spot.outerConeAngle : Math.PI / 4.0;
        lightNode.angle = lightDef.spot.outerConeAngle;
        lightNode.penumbra = 1.0 - lightDef.spot.innerConeAngle / lightDef.spot.outerConeAngle;
        lightNode.target.position.set(0, 0, -1);
        lightNode.add(lightNode.target);
        break;
      default:
        throw new Error('THREE.GLTFLoader: Unexpected light type: ' + lightDef.type);
    }

    // Some lights (e.g. spot) default to a position other than the origin. Reset the position
    // here, because node-level parsing will only override position if explicitly specified.
    lightNode.position.set(0, 0, 0);
    lightNode.decay = 2;
    assignExtrasToUserData(lightNode, lightDef);
    if (lightDef.intensity !== undefined) lightNode.intensity = lightDef.intensity;
    lightNode.name = parser.createUniqueName(lightDef.name || 'light_' + lightIndex);
    dependency = Promise.resolve(lightNode);
    parser.cache.add(cacheKey, dependency);
    return dependency;
  }
  getDependency(type, index) {
    if (type !== 'light') return;
    return this._loadLight(index);
  }
  createNodeAttachment(nodeIndex) {
    const self = this;
    const parser = this.parser;
    const json = parser.json;
    const nodeDef = json.nodes[nodeIndex];
    const lightDef = nodeDef.extensions && nodeDef.extensions[this.name] || {};
    const lightIndex = lightDef.light;
    if (lightIndex === undefined) return null;
    return this._loadLight(lightIndex).then(function (light) {
      return parser._getNodeRef(self.cache, lightIndex, light);
    });
  }
}

/**
 * Unlit Materials Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_unlit
 */
class GLTFMaterialsUnlitExtension {
  constructor() {
    this.name = EXTENSIONS.KHR_MATERIALS_UNLIT;
  }
  getMaterialType() {
    return MeshBasicMaterial$1;
  }
  extendParams(materialParams, materialDef, parser) {
    const pending = [];
    materialParams.color = new Color$1(1.0, 1.0, 1.0);
    materialParams.opacity = 1.0;
    const metallicRoughness = materialDef.pbrMetallicRoughness;
    if (metallicRoughness) {
      if (Array.isArray(metallicRoughness.baseColorFactor)) {
        const array = metallicRoughness.baseColorFactor;
        materialParams.color.setRGB(array[0], array[1], array[2], LinearSRGBColorSpace$1);
        materialParams.opacity = array[3];
      }
      if (metallicRoughness.baseColorTexture !== undefined) {
        pending.push(parser.assignTexture(materialParams, 'map', metallicRoughness.baseColorTexture, SRGBColorSpace$1));
      }
    }
    return Promise.all(pending);
  }
}

/**
 * Materials Emissive Strength Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/blob/5768b3ce0ef32bc39cdf1bef10b948586635ead3/extensions/2.0/Khronos/KHR_materials_emissive_strength/README.md
 */
class GLTFMaterialsEmissiveStrengthExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_EMISSIVE_STRENGTH;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const emissiveStrength = materialDef.extensions[this.name].emissiveStrength;
    if (emissiveStrength !== undefined) {
      materialParams.emissiveIntensity = emissiveStrength;
    }
    return Promise.resolve();
  }
}

/**
 * Clearcoat Materials Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_clearcoat
 */
class GLTFMaterialsClearcoatExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_CLEARCOAT;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    if (extension.clearcoatFactor !== undefined) {
      materialParams.clearcoat = extension.clearcoatFactor;
    }
    if (extension.clearcoatTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'clearcoatMap', extension.clearcoatTexture));
    }
    if (extension.clearcoatRoughnessFactor !== undefined) {
      materialParams.clearcoatRoughness = extension.clearcoatRoughnessFactor;
    }
    if (extension.clearcoatRoughnessTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'clearcoatRoughnessMap', extension.clearcoatRoughnessTexture));
    }
    if (extension.clearcoatNormalTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'clearcoatNormalMap', extension.clearcoatNormalTexture));
      if (extension.clearcoatNormalTexture.scale !== undefined) {
        const scale = extension.clearcoatNormalTexture.scale;
        materialParams.clearcoatNormalScale = new Vector2$1(scale, scale);
      }
    }
    return Promise.all(pending);
  }
}

/**
 * Materials dispersion Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Khronos/KHR_materials_dispersion
 */
class GLTFMaterialsDispersionExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_DISPERSION;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const extension = materialDef.extensions[this.name];
    materialParams.dispersion = extension.dispersion !== undefined ? extension.dispersion : 0;
    return Promise.resolve();
  }
}

/**
 * Iridescence Materials Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_iridescence
 */
class GLTFMaterialsIridescenceExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_IRIDESCENCE;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    if (extension.iridescenceFactor !== undefined) {
      materialParams.iridescence = extension.iridescenceFactor;
    }
    if (extension.iridescenceTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'iridescenceMap', extension.iridescenceTexture));
    }
    if (extension.iridescenceIor !== undefined) {
      materialParams.iridescenceIOR = extension.iridescenceIor;
    }
    if (materialParams.iridescenceThicknessRange === undefined) {
      materialParams.iridescenceThicknessRange = [100, 400];
    }
    if (extension.iridescenceThicknessMinimum !== undefined) {
      materialParams.iridescenceThicknessRange[0] = extension.iridescenceThicknessMinimum;
    }
    if (extension.iridescenceThicknessMaximum !== undefined) {
      materialParams.iridescenceThicknessRange[1] = extension.iridescenceThicknessMaximum;
    }
    if (extension.iridescenceThicknessTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'iridescenceThicknessMap', extension.iridescenceThicknessTexture));
    }
    return Promise.all(pending);
  }
}

/**
 * Sheen Materials Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Khronos/KHR_materials_sheen
 */
class GLTFMaterialsSheenExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_SHEEN;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    materialParams.sheenColor = new Color$1(0, 0, 0);
    materialParams.sheenRoughness = 0;
    materialParams.sheen = 1;
    const extension = materialDef.extensions[this.name];
    if (extension.sheenColorFactor !== undefined) {
      const colorFactor = extension.sheenColorFactor;
      materialParams.sheenColor.setRGB(colorFactor[0], colorFactor[1], colorFactor[2], LinearSRGBColorSpace$1);
    }
    if (extension.sheenRoughnessFactor !== undefined) {
      materialParams.sheenRoughness = extension.sheenRoughnessFactor;
    }
    if (extension.sheenColorTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'sheenColorMap', extension.sheenColorTexture, SRGBColorSpace$1));
    }
    if (extension.sheenRoughnessTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'sheenRoughnessMap', extension.sheenRoughnessTexture));
    }
    return Promise.all(pending);
  }
}

/**
 * Transmission Materials Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_transmission
 * Draft: https://github.com/KhronosGroup/glTF/pull/1698
 */
class GLTFMaterialsTransmissionExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_TRANSMISSION;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    if (extension.transmissionFactor !== undefined) {
      materialParams.transmission = extension.transmissionFactor;
    }
    if (extension.transmissionTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'transmissionMap', extension.transmissionTexture));
    }
    return Promise.all(pending);
  }
}

/**
 * Materials Volume Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_volume
 */
class GLTFMaterialsVolumeExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_VOLUME;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    materialParams.thickness = extension.thicknessFactor !== undefined ? extension.thicknessFactor : 0;
    if (extension.thicknessTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'thicknessMap', extension.thicknessTexture));
    }
    materialParams.attenuationDistance = extension.attenuationDistance || Infinity;
    const colorArray = extension.attenuationColor || [1, 1, 1];
    materialParams.attenuationColor = new Color$1().setRGB(colorArray[0], colorArray[1], colorArray[2], LinearSRGBColorSpace$1);
    return Promise.all(pending);
  }
}

/**
 * Materials ior Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_ior
 */
class GLTFMaterialsIorExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_IOR;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const extension = materialDef.extensions[this.name];
    materialParams.ior = extension.ior !== undefined ? extension.ior : 1.5;
    return Promise.resolve();
  }
}

/**
 * Materials specular Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_specular
 */
class GLTFMaterialsSpecularExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_SPECULAR;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    materialParams.specularIntensity = extension.specularFactor !== undefined ? extension.specularFactor : 1.0;
    if (extension.specularTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'specularIntensityMap', extension.specularTexture));
    }
    const colorArray = extension.specularColorFactor || [1, 1, 1];
    materialParams.specularColor = new Color$1().setRGB(colorArray[0], colorArray[1], colorArray[2], LinearSRGBColorSpace$1);
    if (extension.specularColorTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'specularColorMap', extension.specularColorTexture, SRGBColorSpace$1));
    }
    return Promise.all(pending);
  }
}

/**
 * Materials bump Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/EXT_materials_bump
 */
class GLTFMaterialsBumpExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.EXT_MATERIALS_BUMP;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    materialParams.bumpScale = extension.bumpFactor !== undefined ? extension.bumpFactor : 1.0;
    if (extension.bumpTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'bumpMap', extension.bumpTexture));
    }
    return Promise.all(pending);
  }
}

/**
 * Materials anisotropy Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_anisotropy
 */
class GLTFMaterialsAnisotropyExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_MATERIALS_ANISOTROPY;
  }
  getMaterialType(materialIndex) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null;
    return MeshPhysicalMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const pending = [];
    const extension = materialDef.extensions[this.name];
    if (extension.anisotropyStrength !== undefined) {
      materialParams.anisotropy = extension.anisotropyStrength;
    }
    if (extension.anisotropyRotation !== undefined) {
      materialParams.anisotropyRotation = extension.anisotropyRotation;
    }
    if (extension.anisotropyTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'anisotropyMap', extension.anisotropyTexture));
    }
    return Promise.all(pending);
  }
}

/**
 * BasisU Texture Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_texture_basisu
 */
class GLTFTextureBasisUExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.KHR_TEXTURE_BASISU;
  }
  loadTexture(textureIndex) {
    const parser = this.parser;
    const json = parser.json;
    const textureDef = json.textures[textureIndex];
    if (!textureDef.extensions || !textureDef.extensions[this.name]) {
      return null;
    }
    const extension = textureDef.extensions[this.name];
    const loader = parser.options.ktx2Loader;
    if (!loader) {
      if (json.extensionsRequired && json.extensionsRequired.indexOf(this.name) >= 0) {
        throw new Error('THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures');
      } else {
        // Assumes that the extension is optional and that a fallback texture is present
        return null;
      }
    }
    return parser.loadTextureImage(textureIndex, extension.source, loader);
  }
}

/**
 * WebP Texture Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Vendor/EXT_texture_webp
 */
class GLTFTextureWebPExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.EXT_TEXTURE_WEBP;
    this.isSupported = null;
  }
  loadTexture(textureIndex) {
    const name = this.name;
    const parser = this.parser;
    const json = parser.json;
    const textureDef = json.textures[textureIndex];
    if (!textureDef.extensions || !textureDef.extensions[name]) {
      return null;
    }
    const extension = textureDef.extensions[name];
    const source = json.images[extension.source];
    let loader = parser.textureLoader;
    if (source.uri) {
      const handler = parser.options.manager.getHandler(source.uri);
      if (handler !== null) loader = handler;
    }
    return this.detectSupport().then(function (isSupported) {
      if (isSupported) return parser.loadTextureImage(textureIndex, extension.source, loader);
      if (json.extensionsRequired && json.extensionsRequired.indexOf(name) >= 0) {
        throw new Error('THREE.GLTFLoader: WebP required by asset but unsupported.');
      }

      // Fall back to PNG or JPEG.
      return parser.loadTexture(textureIndex);
    });
  }
  detectSupport() {
    if (!this.isSupported) {
      this.isSupported = new Promise(function (resolve) {
        const image = new Image();

        // Lossy test image. Support for lossy images doesn't guarantee support for all
        // WebP images, unfortunately.
        image.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
        image.onload = image.onerror = function () {
          resolve(image.height === 1);
        };
      });
    }
    return this.isSupported;
  }
}

/**
 * AVIF Texture Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Vendor/EXT_texture_avif
 */
class GLTFTextureAVIFExtension {
  constructor(parser) {
    this.parser = parser;
    this.name = EXTENSIONS.EXT_TEXTURE_AVIF;
    this.isSupported = null;
  }
  loadTexture(textureIndex) {
    const name = this.name;
    const parser = this.parser;
    const json = parser.json;
    const textureDef = json.textures[textureIndex];
    if (!textureDef.extensions || !textureDef.extensions[name]) {
      return null;
    }
    const extension = textureDef.extensions[name];
    const source = json.images[extension.source];
    let loader = parser.textureLoader;
    if (source.uri) {
      const handler = parser.options.manager.getHandler(source.uri);
      if (handler !== null) loader = handler;
    }
    return this.detectSupport().then(function (isSupported) {
      if (isSupported) return parser.loadTextureImage(textureIndex, extension.source, loader);
      if (json.extensionsRequired && json.extensionsRequired.indexOf(name) >= 0) {
        throw new Error('THREE.GLTFLoader: AVIF required by asset but unsupported.');
      }

      // Fall back to PNG or JPEG.
      return parser.loadTexture(textureIndex);
    });
  }
  detectSupport() {
    if (!this.isSupported) {
      this.isSupported = new Promise(function (resolve) {
        const image = new Image();

        // Lossy test image.
        image.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
        image.onload = image.onerror = function () {
          resolve(image.height === 1);
        };
      });
    }
    return this.isSupported;
  }
}

/**
 * meshopt BufferView Compression Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Vendor/EXT_meshopt_compression
 */
class GLTFMeshoptCompression {
  constructor(parser) {
    this.name = EXTENSIONS.EXT_MESHOPT_COMPRESSION;
    this.parser = parser;
  }
  loadBufferView(index) {
    const json = this.parser.json;
    const bufferView = json.bufferViews[index];
    if (bufferView.extensions && bufferView.extensions[this.name]) {
      const extensionDef = bufferView.extensions[this.name];
      const buffer = this.parser.getDependency('buffer', extensionDef.buffer);
      const decoder = this.parser.options.meshoptDecoder;
      if (!decoder || !decoder.supported) {
        if (json.extensionsRequired && json.extensionsRequired.indexOf(this.name) >= 0) {
          throw new Error('THREE.GLTFLoader: setMeshoptDecoder must be called before loading compressed files');
        } else {
          // Assumes that the extension is optional and that fallback buffer data is present
          return null;
        }
      }
      return buffer.then(function (res) {
        const byteOffset = extensionDef.byteOffset || 0;
        const byteLength = extensionDef.byteLength || 0;
        const count = extensionDef.count;
        const stride = extensionDef.byteStride;
        const source = new Uint8Array(res, byteOffset, byteLength);
        if (decoder.decodeGltfBufferAsync) {
          return decoder.decodeGltfBufferAsync(count, stride, source, extensionDef.mode, extensionDef.filter).then(function (res) {
            return res.buffer;
          });
        } else {
          // Support for MeshoptDecoder 0.18 or earlier, without decodeGltfBufferAsync
          return decoder.ready.then(function () {
            const result = new ArrayBuffer(count * stride);
            decoder.decodeGltfBuffer(new Uint8Array(result), count, stride, source, extensionDef.mode, extensionDef.filter);
            return result;
          });
        }
      });
    } else {
      return null;
    }
  }
}

/**
 * GPU Instancing Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Vendor/EXT_mesh_gpu_instancing
 *
 */
class GLTFMeshGpuInstancing {
  constructor(parser) {
    this.name = EXTENSIONS.EXT_MESH_GPU_INSTANCING;
    this.parser = parser;
  }
  createNodeMesh(nodeIndex) {
    const json = this.parser.json;
    const nodeDef = json.nodes[nodeIndex];
    if (!nodeDef.extensions || !nodeDef.extensions[this.name] || nodeDef.mesh === undefined) {
      return null;
    }
    const meshDef = json.meshes[nodeDef.mesh];

    // No Points or Lines + Instancing support yet

    for (const primitive of meshDef.primitives) {
      if (primitive.mode !== WEBGL_CONSTANTS.TRIANGLES && primitive.mode !== WEBGL_CONSTANTS.TRIANGLE_STRIP && primitive.mode !== WEBGL_CONSTANTS.TRIANGLE_FAN && primitive.mode !== undefined) {
        return null;
      }
    }
    const extensionDef = nodeDef.extensions[this.name];
    const attributesDef = extensionDef.attributes;

    // @TODO: Can we support InstancedMesh + SkinnedMesh?

    const pending = [];
    const attributes = {};
    for (const key in attributesDef) {
      pending.push(this.parser.getDependency('accessor', attributesDef[key]).then(accessor => {
        attributes[key] = accessor;
        return attributes[key];
      }));
    }
    if (pending.length < 1) {
      return null;
    }
    pending.push(this.parser.createNodeMesh(nodeIndex));
    return Promise.all(pending).then(results => {
      const nodeObject = results.pop();
      const meshes = nodeObject.isGroup ? nodeObject.children : [nodeObject];
      const count = results[0].count; // All attribute counts should be same
      const instancedMeshes = [];
      for (const mesh of meshes) {
        // Temporal variables
        const m = new Matrix4$1();
        const p = new Vector3$1();
        const q = new Quaternion$1();
        const s = new Vector3$1(1, 1, 1);
        const instancedMesh = new InstancedMesh(mesh.geometry, mesh.material, count);
        for (let i = 0; i < count; i++) {
          if (attributes.TRANSLATION) {
            p.fromBufferAttribute(attributes.TRANSLATION, i);
          }
          if (attributes.ROTATION) {
            q.fromBufferAttribute(attributes.ROTATION, i);
          }
          if (attributes.SCALE) {
            s.fromBufferAttribute(attributes.SCALE, i);
          }
          instancedMesh.setMatrixAt(i, m.compose(p, q, s));
        }

        // Add instance attributes to the geometry, excluding TRS.
        for (const attributeName in attributes) {
          if (attributeName === '_COLOR_0') {
            const attr = attributes[attributeName];
            instancedMesh.instanceColor = new InstancedBufferAttribute$1(attr.array, attr.itemSize, attr.normalized);
          } else if (attributeName !== 'TRANSLATION' && attributeName !== 'ROTATION' && attributeName !== 'SCALE') {
            mesh.geometry.setAttribute(attributeName, attributes[attributeName]);
          }
        }

        // Just in case
        Object3D$1.prototype.copy.call(instancedMesh, mesh);
        this.parser.assignFinalMaterial(instancedMesh);
        instancedMeshes.push(instancedMesh);
      }
      if (nodeObject.isGroup) {
        nodeObject.clear();
        nodeObject.add(...instancedMeshes);
        return nodeObject;
      }
      return instancedMeshes[0];
    });
  }
}

/* BINARY EXTENSION */
const BINARY_EXTENSION_HEADER_MAGIC = 'glTF';
const BINARY_EXTENSION_HEADER_LENGTH = 12;
const BINARY_EXTENSION_CHUNK_TYPES = {
  JSON: 0x4E4F534A,
  BIN: 0x004E4942
};
class GLTFBinaryExtension {
  constructor(data) {
    this.name = EXTENSIONS.KHR_BINARY_GLTF;
    this.content = null;
    this.body = null;
    const headerView = new DataView(data, 0, BINARY_EXTENSION_HEADER_LENGTH);
    const textDecoder = new TextDecoder();
    this.header = {
      magic: textDecoder.decode(new Uint8Array(data.slice(0, 4))),
      version: headerView.getUint32(4, true),
      length: headerView.getUint32(8, true)
    };
    if (this.header.magic !== BINARY_EXTENSION_HEADER_MAGIC) {
      throw new Error('THREE.GLTFLoader: Unsupported glTF-Binary header.');
    } else if (this.header.version < 2.0) {
      throw new Error('THREE.GLTFLoader: Legacy binary file detected.');
    }
    const chunkContentsLength = this.header.length - BINARY_EXTENSION_HEADER_LENGTH;
    const chunkView = new DataView(data, BINARY_EXTENSION_HEADER_LENGTH);
    let chunkIndex = 0;
    while (chunkIndex < chunkContentsLength) {
      const chunkLength = chunkView.getUint32(chunkIndex, true);
      chunkIndex += 4;
      const chunkType = chunkView.getUint32(chunkIndex, true);
      chunkIndex += 4;
      if (chunkType === BINARY_EXTENSION_CHUNK_TYPES.JSON) {
        const contentArray = new Uint8Array(data, BINARY_EXTENSION_HEADER_LENGTH + chunkIndex, chunkLength);
        this.content = textDecoder.decode(contentArray);
      } else if (chunkType === BINARY_EXTENSION_CHUNK_TYPES.BIN) {
        const byteOffset = BINARY_EXTENSION_HEADER_LENGTH + chunkIndex;
        this.body = data.slice(byteOffset, byteOffset + chunkLength);
      }

      // Clients must ignore chunks with unknown types.

      chunkIndex += chunkLength;
    }
    if (this.content === null) {
      throw new Error('THREE.GLTFLoader: JSON content not found.');
    }
  }
}

/**
 * DRACO Mesh Compression Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_draco_mesh_compression
 */
class GLTFDracoMeshCompressionExtension {
  constructor(json, dracoLoader) {
    if (!dracoLoader) {
      throw new Error('THREE.GLTFLoader: No DRACOLoader instance provided.');
    }
    this.name = EXTENSIONS.KHR_DRACO_MESH_COMPRESSION;
    this.json = json;
    this.dracoLoader = dracoLoader;
    this.dracoLoader.preload();
  }
  decodePrimitive(primitive, parser) {
    const json = this.json;
    const dracoLoader = this.dracoLoader;
    const bufferViewIndex = primitive.extensions[this.name].bufferView;
    const gltfAttributeMap = primitive.extensions[this.name].attributes;
    const threeAttributeMap = {};
    const attributeNormalizedMap = {};
    const attributeTypeMap = {};
    for (const attributeName in gltfAttributeMap) {
      const threeAttributeName = ATTRIBUTES[attributeName] || attributeName.toLowerCase();
      threeAttributeMap[threeAttributeName] = gltfAttributeMap[attributeName];
    }
    for (const attributeName in primitive.attributes) {
      const threeAttributeName = ATTRIBUTES[attributeName] || attributeName.toLowerCase();
      if (gltfAttributeMap[attributeName] !== undefined) {
        const accessorDef = json.accessors[primitive.attributes[attributeName]];
        const componentType = WEBGL_COMPONENT_TYPES[accessorDef.componentType];
        attributeTypeMap[threeAttributeName] = componentType.name;
        attributeNormalizedMap[threeAttributeName] = accessorDef.normalized === true;
      }
    }
    return parser.getDependency('bufferView', bufferViewIndex).then(function (bufferView) {
      return new Promise(function (resolve, reject) {
        dracoLoader.decodeDracoFile(bufferView, function (geometry) {
          for (const attributeName in geometry.attributes) {
            const attribute = geometry.attributes[attributeName];
            const normalized = attributeNormalizedMap[attributeName];
            if (normalized !== undefined) attribute.normalized = normalized;
          }
          resolve(geometry);
        }, threeAttributeMap, attributeTypeMap, LinearSRGBColorSpace$1, reject);
      });
    });
  }
}

/**
 * Texture Transform Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_texture_transform
 */
class GLTFTextureTransformExtension {
  constructor() {
    this.name = EXTENSIONS.KHR_TEXTURE_TRANSFORM;
  }
  extendTexture(texture, transform) {
    if ((transform.texCoord === undefined || transform.texCoord === texture.channel) && transform.offset === undefined && transform.rotation === undefined && transform.scale === undefined) {
      // See https://github.com/mrdoob/three.js/issues/21819.
      return texture;
    }
    texture = texture.clone();
    if (transform.texCoord !== undefined) {
      texture.channel = transform.texCoord;
    }
    if (transform.offset !== undefined) {
      texture.offset.fromArray(transform.offset);
    }
    if (transform.rotation !== undefined) {
      texture.rotation = transform.rotation;
    }
    if (transform.scale !== undefined) {
      texture.repeat.fromArray(transform.scale);
    }
    texture.needsUpdate = true;
    return texture;
  }
}

/**
 * Mesh Quantization Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_mesh_quantization
 */
class GLTFMeshQuantizationExtension {
  constructor() {
    this.name = EXTENSIONS.KHR_MESH_QUANTIZATION;
  }
}

/*********************************/
/********** INTERPOLATION ********/
/*********************************/

// Spline Interpolation
// Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#appendix-c-spline-interpolation
class GLTFCubicSplineInterpolant extends Interpolant {
  constructor(parameterPositions, sampleValues, sampleSize, resultBuffer) {
    super(parameterPositions, sampleValues, sampleSize, resultBuffer);
  }
  copySampleValue_(index) {
    // Copies a sample value to the result buffer. See description of glTF
    // CUBICSPLINE values layout in interpolate_() function below.

    const result = this.resultBuffer,
      values = this.sampleValues,
      valueSize = this.valueSize,
      offset = index * valueSize * 3 + valueSize;
    for (let i = 0; i !== valueSize; i++) {
      result[i] = values[offset + i];
    }
    return result;
  }
  interpolate_(i1, t0, t, t1) {
    const result = this.resultBuffer;
    const values = this.sampleValues;
    const stride = this.valueSize;
    const stride2 = stride * 2;
    const stride3 = stride * 3;
    const td = t1 - t0;
    const p = (t - t0) / td;
    const pp = p * p;
    const ppp = pp * p;
    const offset1 = i1 * stride3;
    const offset0 = offset1 - stride3;
    const s2 = -2 * ppp + 3 * pp;
    const s3 = ppp - pp;
    const s0 = 1 - s2;
    const s1 = s3 - pp + p;

    // Layout of keyframe output values for CUBICSPLINE animations:
    //   [ inTangent_1, splineVertex_1, outTangent_1, inTangent_2, splineVertex_2, ... ]
    for (let i = 0; i !== stride; i++) {
      const p0 = values[offset0 + i + stride]; // splineVertex_k
      const m0 = values[offset0 + i + stride2] * td; // outTangent_k * (t_k+1 - t_k)
      const p1 = values[offset1 + i + stride]; // splineVertex_k+1
      const m1 = values[offset1 + i] * td; // inTangent_k+1 * (t_k+1 - t_k)

      result[i] = s0 * p0 + s1 * m0 + s2 * p1 + s3 * m1;
    }
    return result;
  }
}
const _q$1 = new Quaternion$1();
class GLTFCubicSplineQuaternionInterpolant extends GLTFCubicSplineInterpolant {
  interpolate_(i1, t0, t, t1) {
    const result = super.interpolate_(i1, t0, t, t1);
    _q$1.fromArray(result).normalize().toArray(result);
    return result;
  }
}

/*********************************/
/********** INTERNALS ************/
/*********************************/

/* CONSTANTS */

const WEBGL_CONSTANTS = {
  FLOAT: 5126,
  //FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123
};
const WEBGL_COMPONENT_TYPES = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array
};
const WEBGL_FILTERS = {
  9728: NearestFilter$1,
  9729: LinearFilter$1,
  9984: NearestMipmapNearestFilter,
  9985: LinearMipmapNearestFilter,
  9986: NearestMipmapLinearFilter,
  9987: LinearMipmapLinearFilter$1
};
const WEBGL_WRAPPINGS = {
  33071: ClampToEdgeWrapping$1,
  33648: MirroredRepeatWrapping$1,
  10497: RepeatWrapping$1
};
const WEBGL_TYPE_SIZES = {
  'SCALAR': 1,
  'VEC2': 2,
  'VEC3': 3,
  'VEC4': 4,
  'MAT2': 4,
  'MAT3': 9,
  'MAT4': 16
};
const ATTRIBUTES = {
  POSITION: 'position',
  NORMAL: 'normal',
  TANGENT: 'tangent',
  TEXCOORD_0: 'uv',
  TEXCOORD_1: 'uv1',
  TEXCOORD_2: 'uv2',
  TEXCOORD_3: 'uv3',
  COLOR_0: 'color',
  WEIGHTS_0: 'skinWeight',
  JOINTS_0: 'skinIndex'
};
const PATH_PROPERTIES = {
  scale: 'scale',
  translation: 'position',
  rotation: 'quaternion',
  weights: 'morphTargetInfluences'
};
const INTERPOLATION = {
  CUBICSPLINE: undefined,
  // We use a custom interpolant (GLTFCubicSplineInterpolation) for CUBICSPLINE tracks. Each
  // keyframe track will be initialized with a default interpolation type, then modified.
  LINEAR: InterpolateLinear,
  STEP: InterpolateDiscrete
};
const ALPHA_MODES = {
  OPAQUE: 'OPAQUE',
  MASK: 'MASK',
  BLEND: 'BLEND'
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#default-material
 */
function createDefaultMaterial(cache) {
  if (cache['DefaultMaterial'] === undefined) {
    cache['DefaultMaterial'] = new MeshStandardMaterial({
      color: 0xFFFFFF,
      emissive: 0x000000,
      metalness: 1,
      roughness: 1,
      transparent: false,
      depthTest: true,
      side: FrontSide$1
    });
  }
  return cache['DefaultMaterial'];
}
function addUnknownExtensionsToUserData(knownExtensions, object, objectDef) {
  // Add unknown glTF extensions to an object's userData.

  for (const name in objectDef.extensions) {
    if (knownExtensions[name] === undefined) {
      object.userData.gltfExtensions = object.userData.gltfExtensions || {};
      object.userData.gltfExtensions[name] = objectDef.extensions[name];
    }
  }
}

/**
 * @param {Object3D|Material|BufferGeometry} object
 * @param {GLTF.definition} gltfDef
 */
function assignExtrasToUserData(object, gltfDef) {
  if (gltfDef.extras !== undefined) {
    if (typeof gltfDef.extras === 'object') {
      Object.assign(object.userData, gltfDef.extras);
    } else {
      console.warn('THREE.GLTFLoader: Ignoring primitive type .extras, ' + gltfDef.extras);
    }
  }
}

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#morph-targets
 *
 * @param {BufferGeometry} geometry
 * @param {Array<GLTF.Target>} targets
 * @param {GLTFParser} parser
 * @return {Promise<BufferGeometry>}
 */
function addMorphTargets(geometry, targets, parser) {
  let hasMorphPosition = false;
  let hasMorphNormal = false;
  let hasMorphColor = false;
  for (let i = 0, il = targets.length; i < il; i++) {
    const target = targets[i];
    if (target.POSITION !== undefined) hasMorphPosition = true;
    if (target.NORMAL !== undefined) hasMorphNormal = true;
    if (target.COLOR_0 !== undefined) hasMorphColor = true;
    if (hasMorphPosition && hasMorphNormal && hasMorphColor) break;
  }
  if (!hasMorphPosition && !hasMorphNormal && !hasMorphColor) return Promise.resolve(geometry);
  const pendingPositionAccessors = [];
  const pendingNormalAccessors = [];
  const pendingColorAccessors = [];
  for (let i = 0, il = targets.length; i < il; i++) {
    const target = targets[i];
    if (hasMorphPosition) {
      const pendingAccessor = target.POSITION !== undefined ? parser.getDependency('accessor', target.POSITION) : geometry.attributes.position;
      pendingPositionAccessors.push(pendingAccessor);
    }
    if (hasMorphNormal) {
      const pendingAccessor = target.NORMAL !== undefined ? parser.getDependency('accessor', target.NORMAL) : geometry.attributes.normal;
      pendingNormalAccessors.push(pendingAccessor);
    }
    if (hasMorphColor) {
      const pendingAccessor = target.COLOR_0 !== undefined ? parser.getDependency('accessor', target.COLOR_0) : geometry.attributes.color;
      pendingColorAccessors.push(pendingAccessor);
    }
  }
  return Promise.all([Promise.all(pendingPositionAccessors), Promise.all(pendingNormalAccessors), Promise.all(pendingColorAccessors)]).then(function (accessors) {
    const morphPositions = accessors[0];
    const morphNormals = accessors[1];
    const morphColors = accessors[2];
    if (hasMorphPosition) geometry.morphAttributes.position = morphPositions;
    if (hasMorphNormal) geometry.morphAttributes.normal = morphNormals;
    if (hasMorphColor) geometry.morphAttributes.color = morphColors;
    geometry.morphTargetsRelative = true;
    return geometry;
  });
}

/**
 * @param {Mesh} mesh
 * @param {GLTF.Mesh} meshDef
 */
function updateMorphTargets(mesh, meshDef) {
  mesh.updateMorphTargets();
  if (meshDef.weights !== undefined) {
    for (let i = 0, il = meshDef.weights.length; i < il; i++) {
      mesh.morphTargetInfluences[i] = meshDef.weights[i];
    }
  }

  // .extras has user-defined data, so check that .extras.targetNames is an array.
  if (meshDef.extras && Array.isArray(meshDef.extras.targetNames)) {
    const targetNames = meshDef.extras.targetNames;
    if (mesh.morphTargetInfluences.length === targetNames.length) {
      mesh.morphTargetDictionary = {};
      for (let i = 0, il = targetNames.length; i < il; i++) {
        mesh.morphTargetDictionary[targetNames[i]] = i;
      }
    } else {
      console.warn('THREE.GLTFLoader: Invalid extras.targetNames length. Ignoring names.');
    }
  }
}
function createPrimitiveKey(primitiveDef) {
  let geometryKey;
  const dracoExtension = primitiveDef.extensions && primitiveDef.extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION];
  if (dracoExtension) {
    geometryKey = 'draco:' + dracoExtension.bufferView + ':' + dracoExtension.indices + ':' + createAttributesKey(dracoExtension.attributes);
  } else {
    geometryKey = primitiveDef.indices + ':' + createAttributesKey(primitiveDef.attributes) + ':' + primitiveDef.mode;
  }
  if (primitiveDef.targets !== undefined) {
    for (let i = 0, il = primitiveDef.targets.length; i < il; i++) {
      geometryKey += ':' + createAttributesKey(primitiveDef.targets[i]);
    }
  }
  return geometryKey;
}
function createAttributesKey(attributes) {
  let attributesKey = '';
  const keys = Object.keys(attributes).sort();
  for (let i = 0, il = keys.length; i < il; i++) {
    attributesKey += keys[i] + ':' + attributes[keys[i]] + ';';
  }
  return attributesKey;
}
function getNormalizedComponentScale(constructor) {
  // Reference:
  // https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_mesh_quantization#encoding-quantized-data

  switch (constructor) {
    case Int8Array:
      return 1 / 127;
    case Uint8Array:
      return 1 / 255;
    case Int16Array:
      return 1 / 32767;
    case Uint16Array:
      return 1 / 65535;
    default:
      throw new Error('THREE.GLTFLoader: Unsupported normalized accessor component type.');
  }
}
function getImageURIMimeType(uri) {
  if (uri.search(/\.jpe?g($|\?)/i) > 0 || uri.search(/^data\:image\/jpeg/) === 0) return 'image/jpeg';
  if (uri.search(/\.webp($|\?)/i) > 0 || uri.search(/^data\:image\/webp/) === 0) return 'image/webp';
  return 'image/png';
}
const _identityMatrix = new Matrix4$1();

/* GLTF PARSER */

class GLTFParser {
  constructor(json = {}, options = {}) {
    this.json = json;
    this.extensions = {};
    this.plugins = {};
    this.options = options;

    // loader object cache
    this.cache = new GLTFRegistry();

    // associations between Three.js objects and glTF elements
    this.associations = new Map();

    // BufferGeometry caching
    this.primitiveCache = {};

    // Node cache
    this.nodeCache = {};

    // Object3D instance caches
    this.meshCache = {
      refs: {},
      uses: {}
    };
    this.cameraCache = {
      refs: {},
      uses: {}
    };
    this.lightCache = {
      refs: {},
      uses: {}
    };
    this.sourceCache = {};
    this.textureCache = {};

    // Track node names, to ensure no duplicates
    this.nodeNamesUsed = {};

    // Use an ImageBitmapLoader if imageBitmaps are supported. Moves much of the
    // expensive work of uploading a texture to the GPU off the main thread.

    let isSafari = false;
    let safariVersion = -1;
    let isFirefox = false;
    let firefoxVersion = -1;
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent;
      isSafari = /^((?!chrome|android).)*safari/i.test(userAgent) === true;
      const safariMatch = userAgent.match(/Version\/(\d+)/);
      safariVersion = isSafari && safariMatch ? parseInt(safariMatch[1], 10) : -1;
      isFirefox = userAgent.indexOf('Firefox') > -1;
      firefoxVersion = isFirefox ? userAgent.match(/Firefox\/([0-9]+)\./)[1] : -1;
    }
    if (typeof createImageBitmap === 'undefined' || isSafari && safariVersion < 17 || isFirefox && firefoxVersion < 98) {
      this.textureLoader = new TextureLoader(this.options.manager);
    } else {
      this.textureLoader = new ImageBitmapLoader(this.options.manager);
    }
    this.textureLoader.setCrossOrigin(this.options.crossOrigin);
    this.textureLoader.setRequestHeader(this.options.requestHeader);
    this.fileLoader = new FileLoader(this.options.manager);
    this.fileLoader.setResponseType('arraybuffer');
    if (this.options.crossOrigin === 'use-credentials') {
      this.fileLoader.setWithCredentials(true);
    }
  }
  setExtensions(extensions) {
    this.extensions = extensions;
  }
  setPlugins(plugins) {
    this.plugins = plugins;
  }
  parse(onLoad, onError) {
    const parser = this;
    const json = this.json;
    const extensions = this.extensions;

    // Clear the loader cache
    this.cache.removeAll();
    this.nodeCache = {};

    // Mark the special nodes/meshes in json for efficient parse
    this._invokeAll(function (ext) {
      return ext._markDefs && ext._markDefs();
    });
    Promise.all(this._invokeAll(function (ext) {
      return ext.beforeRoot && ext.beforeRoot();
    })).then(function () {
      return Promise.all([parser.getDependencies('scene'), parser.getDependencies('animation'), parser.getDependencies('camera')]);
    }).then(function (dependencies) {
      const result = {
        scene: dependencies[0][json.scene || 0],
        scenes: dependencies[0],
        animations: dependencies[1],
        cameras: dependencies[2],
        asset: json.asset,
        parser: parser,
        userData: {}
      };
      addUnknownExtensionsToUserData(extensions, result, json);
      assignExtrasToUserData(result, json);
      return Promise.all(parser._invokeAll(function (ext) {
        return ext.afterRoot && ext.afterRoot(result);
      })).then(function () {
        for (const scene of result.scenes) {
          scene.updateMatrixWorld();
        }
        onLoad(result);
      });
    }).catch(onError);
  }

  /**
   * Marks the special nodes/meshes in json for efficient parse.
   */
  _markDefs() {
    const nodeDefs = this.json.nodes || [];
    const skinDefs = this.json.skins || [];
    const meshDefs = this.json.meshes || [];

    // Nothing in the node definition indicates whether it is a Bone or an
    // Object3D. Use the skins' joint references to mark bones.
    for (let skinIndex = 0, skinLength = skinDefs.length; skinIndex < skinLength; skinIndex++) {
      const joints = skinDefs[skinIndex].joints;
      for (let i = 0, il = joints.length; i < il; i++) {
        nodeDefs[joints[i]].isBone = true;
      }
    }

    // Iterate over all nodes, marking references to shared resources,
    // as well as skeleton joints.
    for (let nodeIndex = 0, nodeLength = nodeDefs.length; nodeIndex < nodeLength; nodeIndex++) {
      const nodeDef = nodeDefs[nodeIndex];
      if (nodeDef.mesh !== undefined) {
        this._addNodeRef(this.meshCache, nodeDef.mesh);

        // Nothing in the mesh definition indicates whether it is
        // a SkinnedMesh or Mesh. Use the node's mesh reference
        // to mark SkinnedMesh if node has skin.
        if (nodeDef.skin !== undefined) {
          meshDefs[nodeDef.mesh].isSkinnedMesh = true;
        }
      }
      if (nodeDef.camera !== undefined) {
        this._addNodeRef(this.cameraCache, nodeDef.camera);
      }
    }
  }

  /**
   * Counts references to shared node / Object3D resources. These resources
   * can be reused, or "instantiated", at multiple nodes in the scene
   * hierarchy. Mesh, Camera, and Light instances are instantiated and must
   * be marked. Non-scenegraph resources (like Materials, Geometries, and
   * Textures) can be reused directly and are not marked here.
   *
   * Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
   */
  _addNodeRef(cache, index) {
    if (index === undefined) return;
    if (cache.refs[index] === undefined) {
      cache.refs[index] = cache.uses[index] = 0;
    }
    cache.refs[index]++;
  }

  /** Returns a reference to a shared resource, cloning it if necessary. */
  _getNodeRef(cache, index, object) {
    if (cache.refs[index] <= 1) return object;
    const ref = object.clone();

    // Propagates mappings to the cloned object, prevents mappings on the
    // original object from being lost.
    const updateMappings = (original, clone) => {
      const mappings = this.associations.get(original);
      if (mappings != null) {
        this.associations.set(clone, mappings);
      }
      for (const [i, child] of original.children.entries()) {
        updateMappings(child, clone.children[i]);
      }
    };
    updateMappings(object, ref);
    ref.name += '_instance_' + cache.uses[index]++;
    return ref;
  }
  _invokeOne(func) {
    const extensions = Object.values(this.plugins);
    extensions.push(this);
    for (let i = 0; i < extensions.length; i++) {
      const result = func(extensions[i]);
      if (result) return result;
    }
    return null;
  }
  _invokeAll(func) {
    const extensions = Object.values(this.plugins);
    extensions.unshift(this);
    const pending = [];
    for (let i = 0; i < extensions.length; i++) {
      const result = func(extensions[i]);
      if (result) pending.push(result);
    }
    return pending;
  }

  /**
   * Requests the specified dependency asynchronously, with caching.
   * @param {string} type
   * @param {number} index
   * @return {Promise<Object3D|Material|THREE.Texture|AnimationClip|ArrayBuffer|Object>}
   */
  getDependency(type, index) {
    const cacheKey = type + ':' + index;
    let dependency = this.cache.get(cacheKey);
    if (!dependency) {
      switch (type) {
        case 'scene':
          dependency = this.loadScene(index);
          break;
        case 'node':
          dependency = this._invokeOne(function (ext) {
            return ext.loadNode && ext.loadNode(index);
          });
          break;
        case 'mesh':
          dependency = this._invokeOne(function (ext) {
            return ext.loadMesh && ext.loadMesh(index);
          });
          break;
        case 'accessor':
          dependency = this.loadAccessor(index);
          break;
        case 'bufferView':
          dependency = this._invokeOne(function (ext) {
            return ext.loadBufferView && ext.loadBufferView(index);
          });
          break;
        case 'buffer':
          dependency = this.loadBuffer(index);
          break;
        case 'material':
          dependency = this._invokeOne(function (ext) {
            return ext.loadMaterial && ext.loadMaterial(index);
          });
          break;
        case 'texture':
          dependency = this._invokeOne(function (ext) {
            return ext.loadTexture && ext.loadTexture(index);
          });
          break;
        case 'skin':
          dependency = this.loadSkin(index);
          break;
        case 'animation':
          dependency = this._invokeOne(function (ext) {
            return ext.loadAnimation && ext.loadAnimation(index);
          });
          break;
        case 'camera':
          dependency = this.loadCamera(index);
          break;
        default:
          dependency = this._invokeOne(function (ext) {
            return ext != this && ext.getDependency && ext.getDependency(type, index);
          });
          if (!dependency) {
            throw new Error('Unknown type: ' + type);
          }
          break;
      }
      this.cache.add(cacheKey, dependency);
    }
    return dependency;
  }

  /**
   * Requests all dependencies of the specified type asynchronously, with caching.
   * @param {string} type
   * @return {Promise<Array<Object>>}
   */
  getDependencies(type) {
    let dependencies = this.cache.get(type);
    if (!dependencies) {
      const parser = this;
      const defs = this.json[type + (type === 'mesh' ? 'es' : 's')] || [];
      dependencies = Promise.all(defs.map(function (def, index) {
        return parser.getDependency(type, index);
      }));
      this.cache.add(type, dependencies);
    }
    return dependencies;
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBuffer(bufferIndex) {
    const bufferDef = this.json.buffers[bufferIndex];
    const loader = this.fileLoader;
    if (bufferDef.type && bufferDef.type !== 'arraybuffer') {
      throw new Error('THREE.GLTFLoader: ' + bufferDef.type + ' buffer type is not supported.');
    }

    // If present, GLB container is required to be the first buffer.
    if (bufferDef.uri === undefined && bufferIndex === 0) {
      return Promise.resolve(this.extensions[EXTENSIONS.KHR_BINARY_GLTF].body);
    }
    const options = this.options;
    return new Promise(function (resolve, reject) {
      loader.load(LoaderUtils.resolveURL(bufferDef.uri, options.path), resolve, undefined, function () {
        reject(new Error('THREE.GLTFLoader: Failed to load buffer "' + bufferDef.uri + '".'));
      });
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   * @param {number} bufferViewIndex
   * @return {Promise<ArrayBuffer>}
   */
  loadBufferView(bufferViewIndex) {
    const bufferViewDef = this.json.bufferViews[bufferViewIndex];
    return this.getDependency('buffer', bufferViewDef.buffer).then(function (buffer) {
      const byteLength = bufferViewDef.byteLength || 0;
      const byteOffset = bufferViewDef.byteOffset || 0;
      return buffer.slice(byteOffset, byteOffset + byteLength);
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
   * @param {number} accessorIndex
   * @return {Promise<BufferAttribute|InterleavedBufferAttribute>}
   */
  loadAccessor(accessorIndex) {
    const parser = this;
    const json = this.json;
    const accessorDef = this.json.accessors[accessorIndex];
    if (accessorDef.bufferView === undefined && accessorDef.sparse === undefined) {
      const itemSize = WEBGL_TYPE_SIZES[accessorDef.type];
      const TypedArray = WEBGL_COMPONENT_TYPES[accessorDef.componentType];
      const normalized = accessorDef.normalized === true;
      const array = new TypedArray(accessorDef.count * itemSize);
      return Promise.resolve(new BufferAttribute$1(array, itemSize, normalized));
    }
    const pendingBufferViews = [];
    if (accessorDef.bufferView !== undefined) {
      pendingBufferViews.push(this.getDependency('bufferView', accessorDef.bufferView));
    } else {
      pendingBufferViews.push(null);
    }
    if (accessorDef.sparse !== undefined) {
      pendingBufferViews.push(this.getDependency('bufferView', accessorDef.sparse.indices.bufferView));
      pendingBufferViews.push(this.getDependency('bufferView', accessorDef.sparse.values.bufferView));
    }
    return Promise.all(pendingBufferViews).then(function (bufferViews) {
      const bufferView = bufferViews[0];
      const itemSize = WEBGL_TYPE_SIZES[accessorDef.type];
      const TypedArray = WEBGL_COMPONENT_TYPES[accessorDef.componentType];

      // For VEC3: itemSize is 3, elementBytes is 4, itemBytes is 12.
      const elementBytes = TypedArray.BYTES_PER_ELEMENT;
      const itemBytes = elementBytes * itemSize;
      const byteOffset = accessorDef.byteOffset || 0;
      const byteStride = accessorDef.bufferView !== undefined ? json.bufferViews[accessorDef.bufferView].byteStride : undefined;
      const normalized = accessorDef.normalized === true;
      let array, bufferAttribute;

      // The buffer is not interleaved if the stride is the item size in bytes.
      if (byteStride && byteStride !== itemBytes) {
        // Each "slice" of the buffer, as defined by 'count' elements of 'byteStride' bytes, gets its own InterleavedBuffer
        // This makes sure that IBA.count reflects accessor.count properly
        const ibSlice = Math.floor(byteOffset / byteStride);
        const ibCacheKey = 'InterleavedBuffer:' + accessorDef.bufferView + ':' + accessorDef.componentType + ':' + ibSlice + ':' + accessorDef.count;
        let ib = parser.cache.get(ibCacheKey);
        if (!ib) {
          array = new TypedArray(bufferView, ibSlice * byteStride, accessorDef.count * byteStride / elementBytes);

          // Integer parameters to IB/IBA are in array elements, not bytes.
          ib = new InterleavedBuffer$1(array, byteStride / elementBytes);
          parser.cache.add(ibCacheKey, ib);
        }
        bufferAttribute = new InterleavedBufferAttribute$1(ib, itemSize, byteOffset % byteStride / elementBytes, normalized);
      } else {
        if (bufferView === null) {
          array = new TypedArray(accessorDef.count * itemSize);
        } else {
          array = new TypedArray(bufferView, byteOffset, accessorDef.count * itemSize);
        }
        bufferAttribute = new BufferAttribute$1(array, itemSize, normalized);
      }

      // https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#sparse-accessors
      if (accessorDef.sparse !== undefined) {
        const itemSizeIndices = WEBGL_TYPE_SIZES.SCALAR;
        const TypedArrayIndices = WEBGL_COMPONENT_TYPES[accessorDef.sparse.indices.componentType];
        const byteOffsetIndices = accessorDef.sparse.indices.byteOffset || 0;
        const byteOffsetValues = accessorDef.sparse.values.byteOffset || 0;
        const sparseIndices = new TypedArrayIndices(bufferViews[1], byteOffsetIndices, accessorDef.sparse.count * itemSizeIndices);
        const sparseValues = new TypedArray(bufferViews[2], byteOffsetValues, accessorDef.sparse.count * itemSize);
        if (bufferView !== null) {
          // Avoid modifying the original ArrayBuffer, if the bufferView wasn't initialized with zeroes.
          bufferAttribute = new BufferAttribute$1(bufferAttribute.array.slice(), bufferAttribute.itemSize, bufferAttribute.normalized);
        }
        for (let i = 0, il = sparseIndices.length; i < il; i++) {
          const index = sparseIndices[i];
          bufferAttribute.setX(index, sparseValues[i * itemSize]);
          if (itemSize >= 2) bufferAttribute.setY(index, sparseValues[i * itemSize + 1]);
          if (itemSize >= 3) bufferAttribute.setZ(index, sparseValues[i * itemSize + 2]);
          if (itemSize >= 4) bufferAttribute.setW(index, sparseValues[i * itemSize + 3]);
          if (itemSize >= 5) throw new Error('THREE.GLTFLoader: Unsupported itemSize in sparse BufferAttribute.');
        }
      }
      return bufferAttribute;
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
   * @param {number} textureIndex
   * @return {Promise<THREE.Texture|null>}
   */
  loadTexture(textureIndex) {
    const json = this.json;
    const options = this.options;
    const textureDef = json.textures[textureIndex];
    const sourceIndex = textureDef.source;
    const sourceDef = json.images[sourceIndex];
    let loader = this.textureLoader;
    if (sourceDef.uri) {
      const handler = options.manager.getHandler(sourceDef.uri);
      if (handler !== null) loader = handler;
    }
    return this.loadTextureImage(textureIndex, sourceIndex, loader);
  }
  loadTextureImage(textureIndex, sourceIndex, loader) {
    const parser = this;
    const json = this.json;
    const textureDef = json.textures[textureIndex];
    const sourceDef = json.images[sourceIndex];
    const cacheKey = (sourceDef.uri || sourceDef.bufferView) + ':' + textureDef.sampler;
    if (this.textureCache[cacheKey]) {
      // See https://github.com/mrdoob/three.js/issues/21559.
      return this.textureCache[cacheKey];
    }
    const promise = this.loadImageSource(sourceIndex, loader).then(function (texture) {
      texture.flipY = false;
      texture.name = textureDef.name || sourceDef.name || '';
      if (texture.name === '' && typeof sourceDef.uri === 'string' && sourceDef.uri.startsWith('data:image/') === false) {
        texture.name = sourceDef.uri;
      }
      const samplers = json.samplers || {};
      const sampler = samplers[textureDef.sampler] || {};
      texture.magFilter = WEBGL_FILTERS[sampler.magFilter] || LinearFilter$1;
      texture.minFilter = WEBGL_FILTERS[sampler.minFilter] || LinearMipmapLinearFilter$1;
      texture.wrapS = WEBGL_WRAPPINGS[sampler.wrapS] || RepeatWrapping$1;
      texture.wrapT = WEBGL_WRAPPINGS[sampler.wrapT] || RepeatWrapping$1;
      parser.associations.set(texture, {
        textures: textureIndex
      });
      return texture;
    }).catch(function () {
      return null;
    });
    this.textureCache[cacheKey] = promise;
    return promise;
  }
  loadImageSource(sourceIndex, loader) {
    const parser = this;
    const json = this.json;
    const options = this.options;
    if (this.sourceCache[sourceIndex] !== undefined) {
      return this.sourceCache[sourceIndex].then(texture => texture.clone());
    }
    const sourceDef = json.images[sourceIndex];
    const URL = self.URL || self.webkitURL;
    let sourceURI = sourceDef.uri || '';
    let isObjectURL = false;
    if (sourceDef.bufferView !== undefined) {
      // Load binary image data from bufferView, if provided.

      sourceURI = parser.getDependency('bufferView', sourceDef.bufferView).then(function (bufferView) {
        isObjectURL = true;
        const blob = new Blob([bufferView], {
          type: sourceDef.mimeType
        });
        sourceURI = URL.createObjectURL(blob);
        return sourceURI;
      });
    } else if (sourceDef.uri === undefined) {
      throw new Error('THREE.GLTFLoader: Image ' + sourceIndex + ' is missing URI and bufferView');
    }
    const promise = Promise.resolve(sourceURI).then(function (sourceURI) {
      return new Promise(function (resolve, reject) {
        let onLoad = resolve;
        if (loader.isImageBitmapLoader === true) {
          onLoad = function (imageBitmap) {
            const texture = new Texture$1(imageBitmap);
            texture.needsUpdate = true;
            resolve(texture);
          };
        }
        loader.load(LoaderUtils.resolveURL(sourceURI, options.path), onLoad, undefined, reject);
      });
    }).then(function (texture) {
      // Clean up resources and configure Texture.

      if (isObjectURL === true) {
        URL.revokeObjectURL(sourceURI);
      }
      assignExtrasToUserData(texture, sourceDef);
      texture.userData.mimeType = sourceDef.mimeType || getImageURIMimeType(sourceDef.uri);
      return texture;
    }).catch(function (error) {
      console.error('THREE.GLTFLoader: Couldn\'t load texture', sourceURI);
      throw error;
    });
    this.sourceCache[sourceIndex] = promise;
    return promise;
  }

  /**
   * Asynchronously assigns a texture to the given material parameters.
   * @param {Object} materialParams
   * @param {string} mapName
   * @param {Object} mapDef
   * @return {Promise<Texture>}
   */
  assignTexture(materialParams, mapName, mapDef, colorSpace) {
    const parser = this;
    return this.getDependency('texture', mapDef.index).then(function (texture) {
      if (!texture) return null;
      if (mapDef.texCoord !== undefined && mapDef.texCoord > 0) {
        texture = texture.clone();
        texture.channel = mapDef.texCoord;
      }
      if (parser.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM]) {
        const transform = mapDef.extensions !== undefined ? mapDef.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM] : undefined;
        if (transform) {
          const gltfReference = parser.associations.get(texture);
          texture = parser.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM].extendTexture(texture, transform);
          parser.associations.set(texture, gltfReference);
        }
      }
      if (colorSpace !== undefined) {
        texture.colorSpace = colorSpace;
      }
      materialParams[mapName] = texture;
      return texture;
    });
  }

  /**
   * Assigns final material to a Mesh, Line, or Points instance. The instance
   * already has a material (generated from the glTF material options alone)
   * but reuse of the same glTF material may require multiple threejs materials
   * to accommodate different primitive types, defines, etc. New materials will
   * be created if necessary, and reused from a cache.
   * @param  {Object3D} mesh Mesh, Line, or Points instance.
   */
  assignFinalMaterial(mesh) {
    const geometry = mesh.geometry;
    let material = mesh.material;
    const useDerivativeTangents = geometry.attributes.tangent === undefined;
    const useVertexColors = geometry.attributes.color !== undefined;
    const useFlatShading = geometry.attributes.normal === undefined;
    if (mesh.isPoints) {
      const cacheKey = 'PointsMaterial:' + material.uuid;
      let pointsMaterial = this.cache.get(cacheKey);
      if (!pointsMaterial) {
        pointsMaterial = new PointsMaterial();
        Material$1.prototype.copy.call(pointsMaterial, material);
        pointsMaterial.color.copy(material.color);
        pointsMaterial.map = material.map;
        pointsMaterial.sizeAttenuation = false; // glTF spec says points should be 1px

        this.cache.add(cacheKey, pointsMaterial);
      }
      material = pointsMaterial;
    } else if (mesh.isLine) {
      const cacheKey = 'LineBasicMaterial:' + material.uuid;
      let lineMaterial = this.cache.get(cacheKey);
      if (!lineMaterial) {
        lineMaterial = new LineBasicMaterial();
        Material$1.prototype.copy.call(lineMaterial, material);
        lineMaterial.color.copy(material.color);
        lineMaterial.map = material.map;
        this.cache.add(cacheKey, lineMaterial);
      }
      material = lineMaterial;
    }

    // Clone the material if it will be modified
    if (useDerivativeTangents || useVertexColors || useFlatShading) {
      let cacheKey = 'ClonedMaterial:' + material.uuid + ':';
      if (useDerivativeTangents) cacheKey += 'derivative-tangents:';
      if (useVertexColors) cacheKey += 'vertex-colors:';
      if (useFlatShading) cacheKey += 'flat-shading:';
      let cachedMaterial = this.cache.get(cacheKey);
      if (!cachedMaterial) {
        cachedMaterial = material.clone();
        if (useVertexColors) cachedMaterial.vertexColors = true;
        if (useFlatShading) cachedMaterial.flatShading = true;
        if (useDerivativeTangents) {
          // https://github.com/mrdoob/three.js/issues/11438#issuecomment-507003995
          if (cachedMaterial.normalScale) cachedMaterial.normalScale.y *= -1;
          if (cachedMaterial.clearcoatNormalScale) cachedMaterial.clearcoatNormalScale.y *= -1;
        }
        this.cache.add(cacheKey, cachedMaterial);
        this.associations.set(cachedMaterial, this.associations.get(material));
      }
      material = cachedMaterial;
    }
    mesh.material = material;
  }
  getMaterialType( /* materialIndex */
  ) {
    return MeshStandardMaterial;
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
   * @param {number} materialIndex
   * @return {Promise<Material>}
   */
  loadMaterial(materialIndex) {
    const parser = this;
    const json = this.json;
    const extensions = this.extensions;
    const materialDef = json.materials[materialIndex];
    let materialType;
    const materialParams = {};
    const materialExtensions = materialDef.extensions || {};
    const pending = [];
    if (materialExtensions[EXTENSIONS.KHR_MATERIALS_UNLIT]) {
      const kmuExtension = extensions[EXTENSIONS.KHR_MATERIALS_UNLIT];
      materialType = kmuExtension.getMaterialType();
      pending.push(kmuExtension.extendParams(materialParams, materialDef, parser));
    } else {
      // Specification:
      // https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#metallic-roughness-material

      const metallicRoughness = materialDef.pbrMetallicRoughness || {};
      materialParams.color = new Color$1(1.0, 1.0, 1.0);
      materialParams.opacity = 1.0;
      if (Array.isArray(metallicRoughness.baseColorFactor)) {
        const array = metallicRoughness.baseColorFactor;
        materialParams.color.setRGB(array[0], array[1], array[2], LinearSRGBColorSpace$1);
        materialParams.opacity = array[3];
      }
      if (metallicRoughness.baseColorTexture !== undefined) {
        pending.push(parser.assignTexture(materialParams, 'map', metallicRoughness.baseColorTexture, SRGBColorSpace$1));
      }
      materialParams.metalness = metallicRoughness.metallicFactor !== undefined ? metallicRoughness.metallicFactor : 1.0;
      materialParams.roughness = metallicRoughness.roughnessFactor !== undefined ? metallicRoughness.roughnessFactor : 1.0;
      if (metallicRoughness.metallicRoughnessTexture !== undefined) {
        pending.push(parser.assignTexture(materialParams, 'metalnessMap', metallicRoughness.metallicRoughnessTexture));
        pending.push(parser.assignTexture(materialParams, 'roughnessMap', metallicRoughness.metallicRoughnessTexture));
      }
      materialType = this._invokeOne(function (ext) {
        return ext.getMaterialType && ext.getMaterialType(materialIndex);
      });
      pending.push(Promise.all(this._invokeAll(function (ext) {
        return ext.extendMaterialParams && ext.extendMaterialParams(materialIndex, materialParams);
      })));
    }
    if (materialDef.doubleSided === true) {
      materialParams.side = DoubleSide;
    }
    const alphaMode = materialDef.alphaMode || ALPHA_MODES.OPAQUE;
    if (alphaMode === ALPHA_MODES.BLEND) {
      materialParams.transparent = true;

      // See: https://github.com/mrdoob/three.js/issues/17706
      materialParams.depthWrite = false;
    } else {
      materialParams.transparent = false;
      if (alphaMode === ALPHA_MODES.MASK) {
        materialParams.alphaTest = materialDef.alphaCutoff !== undefined ? materialDef.alphaCutoff : 0.5;
      }
    }
    if (materialDef.normalTexture !== undefined && materialType !== MeshBasicMaterial$1) {
      pending.push(parser.assignTexture(materialParams, 'normalMap', materialDef.normalTexture));
      materialParams.normalScale = new Vector2$1(1, 1);
      if (materialDef.normalTexture.scale !== undefined) {
        const scale = materialDef.normalTexture.scale;
        materialParams.normalScale.set(scale, scale);
      }
    }
    if (materialDef.occlusionTexture !== undefined && materialType !== MeshBasicMaterial$1) {
      pending.push(parser.assignTexture(materialParams, 'aoMap', materialDef.occlusionTexture));
      if (materialDef.occlusionTexture.strength !== undefined) {
        materialParams.aoMapIntensity = materialDef.occlusionTexture.strength;
      }
    }
    if (materialDef.emissiveFactor !== undefined && materialType !== MeshBasicMaterial$1) {
      const emissiveFactor = materialDef.emissiveFactor;
      materialParams.emissive = new Color$1().setRGB(emissiveFactor[0], emissiveFactor[1], emissiveFactor[2], LinearSRGBColorSpace$1);
    }
    if (materialDef.emissiveTexture !== undefined && materialType !== MeshBasicMaterial$1) {
      pending.push(parser.assignTexture(materialParams, 'emissiveMap', materialDef.emissiveTexture, SRGBColorSpace$1));
    }
    return Promise.all(pending).then(function () {
      const material = new materialType(materialParams);
      if (materialDef.name) material.name = materialDef.name;
      assignExtrasToUserData(material, materialDef);
      parser.associations.set(material, {
        materials: materialIndex
      });
      if (materialDef.extensions) addUnknownExtensionsToUserData(extensions, material, materialDef);
      return material;
    });
  }

  /** When Object3D instances are targeted by animation, they need unique names. */
  createUniqueName(originalName) {
    const sanitizedName = PropertyBinding.sanitizeNodeName(originalName || '');
    if (sanitizedName in this.nodeNamesUsed) {
      return sanitizedName + '_' + ++this.nodeNamesUsed[sanitizedName];
    } else {
      this.nodeNamesUsed[sanitizedName] = 0;
      return sanitizedName;
    }
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
   *
   * Creates BufferGeometries from primitives.
   *
   * @param {Array<GLTF.Primitive>} primitives
   * @return {Promise<Array<BufferGeometry>>}
   */
  loadGeometries(primitives) {
    const parser = this;
    const extensions = this.extensions;
    const cache = this.primitiveCache;
    function createDracoPrimitive(primitive) {
      return extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION].decodePrimitive(primitive, parser).then(function (geometry) {
        return addPrimitiveAttributes(geometry, primitive, parser);
      });
    }
    const pending = [];
    for (let i = 0, il = primitives.length; i < il; i++) {
      const primitive = primitives[i];
      const cacheKey = createPrimitiveKey(primitive);

      // See if we've already created this geometry
      const cached = cache[cacheKey];
      if (cached) {
        // Use the cached geometry if it exists
        pending.push(cached.promise);
      } else {
        let geometryPromise;
        if (primitive.extensions && primitive.extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION]) {
          // Use DRACO geometry if available
          geometryPromise = createDracoPrimitive(primitive);
        } else {
          // Otherwise create a new geometry
          geometryPromise = addPrimitiveAttributes(new BufferGeometry$1(), primitive, parser);
        }

        // Cache this geometry
        cache[cacheKey] = {
          primitive: primitive,
          promise: geometryPromise
        };
        pending.push(geometryPromise);
      }
    }
    return Promise.all(pending);
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
   * @param {number} meshIndex
   * @return {Promise<Group|Mesh|SkinnedMesh>}
   */
  loadMesh(meshIndex) {
    const parser = this;
    const json = this.json;
    const extensions = this.extensions;
    const meshDef = json.meshes[meshIndex];
    const primitives = meshDef.primitives;
    const pending = [];
    for (let i = 0, il = primitives.length; i < il; i++) {
      const material = primitives[i].material === undefined ? createDefaultMaterial(this.cache) : this.getDependency('material', primitives[i].material);
      pending.push(material);
    }
    pending.push(parser.loadGeometries(primitives));
    return Promise.all(pending).then(function (results) {
      const materials = results.slice(0, results.length - 1);
      const geometries = results[results.length - 1];
      const meshes = [];
      for (let i = 0, il = geometries.length; i < il; i++) {
        const geometry = geometries[i];
        const primitive = primitives[i];

        // 1. create Mesh

        let mesh;
        const material = materials[i];
        if (primitive.mode === WEBGL_CONSTANTS.TRIANGLES || primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP || primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN || primitive.mode === undefined) {
          // .isSkinnedMesh isn't in glTF spec. See ._markDefs()
          mesh = meshDef.isSkinnedMesh === true ? new SkinnedMesh(geometry, material) : new Mesh$1(geometry, material);
          if (mesh.isSkinnedMesh === true) {
            // normalize skin weights to fix malformed assets (see #15319)
            mesh.normalizeSkinWeights();
          }
          if (primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP) {
            mesh.geometry = toTrianglesDrawMode(mesh.geometry, TriangleStripDrawMode);
          } else if (primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN) {
            mesh.geometry = toTrianglesDrawMode(mesh.geometry, TriangleFanDrawMode);
          }
        } else if (primitive.mode === WEBGL_CONSTANTS.LINES) {
          mesh = new LineSegments(geometry, material);
        } else if (primitive.mode === WEBGL_CONSTANTS.LINE_STRIP) {
          mesh = new Line(geometry, material);
        } else if (primitive.mode === WEBGL_CONSTANTS.LINE_LOOP) {
          mesh = new LineLoop(geometry, material);
        } else if (primitive.mode === WEBGL_CONSTANTS.POINTS) {
          mesh = new Points(geometry, material);
        } else {
          throw new Error('THREE.GLTFLoader: Primitive mode unsupported: ' + primitive.mode);
        }
        if (Object.keys(mesh.geometry.morphAttributes).length > 0) {
          updateMorphTargets(mesh, meshDef);
        }
        mesh.name = parser.createUniqueName(meshDef.name || 'mesh_' + meshIndex);
        assignExtrasToUserData(mesh, meshDef);
        if (primitive.extensions) addUnknownExtensionsToUserData(extensions, mesh, primitive);
        parser.assignFinalMaterial(mesh);
        meshes.push(mesh);
      }
      for (let i = 0, il = meshes.length; i < il; i++) {
        parser.associations.set(meshes[i], {
          meshes: meshIndex,
          primitives: i
        });
      }
      if (meshes.length === 1) {
        if (meshDef.extensions) addUnknownExtensionsToUserData(extensions, meshes[0], meshDef);
        return meshes[0];
      }
      const group = new Group();
      if (meshDef.extensions) addUnknownExtensionsToUserData(extensions, group, meshDef);
      parser.associations.set(group, {
        meshes: meshIndex
      });
      for (let i = 0, il = meshes.length; i < il; i++) {
        group.add(meshes[i]);
      }
      return group;
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#cameras
   * @param {number} cameraIndex
   * @return {Promise<THREE.Camera>}
   */
  loadCamera(cameraIndex) {
    let camera;
    const cameraDef = this.json.cameras[cameraIndex];
    const params = cameraDef[cameraDef.type];
    if (!params) {
      console.warn('THREE.GLTFLoader: Missing camera parameters.');
      return;
    }
    if (cameraDef.type === 'perspective') {
      camera = new PerspectiveCamera$1(MathUtils$1.radToDeg(params.yfov), params.aspectRatio || 1, params.znear || 1, params.zfar || 2e6);
    } else if (cameraDef.type === 'orthographic') {
      camera = new OrthographicCamera$1(-params.xmag, params.xmag, params.ymag, -params.ymag, params.znear, params.zfar);
    }
    if (cameraDef.name) camera.name = this.createUniqueName(cameraDef.name);
    assignExtrasToUserData(camera, cameraDef);
    return Promise.resolve(camera);
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#skins
   * @param {number} skinIndex
   * @return {Promise<Skeleton>}
   */
  loadSkin(skinIndex) {
    const skinDef = this.json.skins[skinIndex];
    const pending = [];
    for (let i = 0, il = skinDef.joints.length; i < il; i++) {
      pending.push(this._loadNodeShallow(skinDef.joints[i]));
    }
    if (skinDef.inverseBindMatrices !== undefined) {
      pending.push(this.getDependency('accessor', skinDef.inverseBindMatrices));
    } else {
      pending.push(null);
    }
    return Promise.all(pending).then(function (results) {
      const inverseBindMatrices = results.pop();
      const jointNodes = results;

      // Note that bones (joint nodes) may or may not be in the
      // scene graph at this time.

      const bones = [];
      const boneInverses = [];
      for (let i = 0, il = jointNodes.length; i < il; i++) {
        const jointNode = jointNodes[i];
        if (jointNode) {
          bones.push(jointNode);
          const mat = new Matrix4$1();
          if (inverseBindMatrices !== null) {
            mat.fromArray(inverseBindMatrices.array, i * 16);
          }
          boneInverses.push(mat);
        } else {
          console.warn('THREE.GLTFLoader: Joint "%s" could not be found.', skinDef.joints[i]);
        }
      }
      return new Skeleton(bones, boneInverses);
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#animations
   * @param {number} animationIndex
   * @return {Promise<AnimationClip>}
   */
  loadAnimation(animationIndex) {
    const json = this.json;
    const parser = this;
    const animationDef = json.animations[animationIndex];
    const animationName = animationDef.name ? animationDef.name : 'animation_' + animationIndex;
    const pendingNodes = [];
    const pendingInputAccessors = [];
    const pendingOutputAccessors = [];
    const pendingSamplers = [];
    const pendingTargets = [];
    for (let i = 0, il = animationDef.channels.length; i < il; i++) {
      const channel = animationDef.channels[i];
      const sampler = animationDef.samplers[channel.sampler];
      const target = channel.target;
      const name = target.node;
      const input = animationDef.parameters !== undefined ? animationDef.parameters[sampler.input] : sampler.input;
      const output = animationDef.parameters !== undefined ? animationDef.parameters[sampler.output] : sampler.output;
      if (target.node === undefined) continue;
      pendingNodes.push(this.getDependency('node', name));
      pendingInputAccessors.push(this.getDependency('accessor', input));
      pendingOutputAccessors.push(this.getDependency('accessor', output));
      pendingSamplers.push(sampler);
      pendingTargets.push(target);
    }
    return Promise.all([Promise.all(pendingNodes), Promise.all(pendingInputAccessors), Promise.all(pendingOutputAccessors), Promise.all(pendingSamplers), Promise.all(pendingTargets)]).then(function (dependencies) {
      const nodes = dependencies[0];
      const inputAccessors = dependencies[1];
      const outputAccessors = dependencies[2];
      const samplers = dependencies[3];
      const targets = dependencies[4];
      const tracks = [];
      for (let i = 0, il = nodes.length; i < il; i++) {
        const node = nodes[i];
        const inputAccessor = inputAccessors[i];
        const outputAccessor = outputAccessors[i];
        const sampler = samplers[i];
        const target = targets[i];
        if (node === undefined) continue;
        if (node.updateMatrix) {
          node.updateMatrix();
        }
        const createdTracks = parser._createAnimationTracks(node, inputAccessor, outputAccessor, sampler, target);
        if (createdTracks) {
          for (let k = 0; k < createdTracks.length; k++) {
            tracks.push(createdTracks[k]);
          }
        }
      }
      return new AnimationClip(animationName, undefined, tracks);
    });
  }
  createNodeMesh(nodeIndex) {
    const json = this.json;
    const parser = this;
    const nodeDef = json.nodes[nodeIndex];
    if (nodeDef.mesh === undefined) return null;
    return parser.getDependency('mesh', nodeDef.mesh).then(function (mesh) {
      const node = parser._getNodeRef(parser.meshCache, nodeDef.mesh, mesh);

      // if weights are provided on the node, override weights on the mesh.
      if (nodeDef.weights !== undefined) {
        node.traverse(function (o) {
          if (!o.isMesh) return;
          for (let i = 0, il = nodeDef.weights.length; i < il; i++) {
            o.morphTargetInfluences[i] = nodeDef.weights[i];
          }
        });
      }
      return node;
    });
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
   * @param {number} nodeIndex
   * @return {Promise<Object3D>}
   */
  loadNode(nodeIndex) {
    const json = this.json;
    const parser = this;
    const nodeDef = json.nodes[nodeIndex];
    const nodePending = parser._loadNodeShallow(nodeIndex);
    const childPending = [];
    const childrenDef = nodeDef.children || [];
    for (let i = 0, il = childrenDef.length; i < il; i++) {
      childPending.push(parser.getDependency('node', childrenDef[i]));
    }
    const skeletonPending = nodeDef.skin === undefined ? Promise.resolve(null) : parser.getDependency('skin', nodeDef.skin);
    return Promise.all([nodePending, Promise.all(childPending), skeletonPending]).then(function (results) {
      const node = results[0];
      const children = results[1];
      const skeleton = results[2];
      if (skeleton !== null) {
        // This full traverse should be fine because
        // child glTF nodes have not been added to this node yet.
        node.traverse(function (mesh) {
          if (!mesh.isSkinnedMesh) return;
          mesh.bind(skeleton, _identityMatrix);
        });
      }
      for (let i = 0, il = children.length; i < il; i++) {
        node.add(children[i]);
      }
      return node;
    });
  }

  // ._loadNodeShallow() parses a single node.
  // skin and child nodes are created and added in .loadNode() (no '_' prefix).
  _loadNodeShallow(nodeIndex) {
    const json = this.json;
    const extensions = this.extensions;
    const parser = this;

    // This method is called from .loadNode() and .loadSkin().
    // Cache a node to avoid duplication.

    if (this.nodeCache[nodeIndex] !== undefined) {
      return this.nodeCache[nodeIndex];
    }
    const nodeDef = json.nodes[nodeIndex];

    // reserve node's name before its dependencies, so the root has the intended name.
    const nodeName = nodeDef.name ? parser.createUniqueName(nodeDef.name) : '';
    const pending = [];
    const meshPromise = parser._invokeOne(function (ext) {
      return ext.createNodeMesh && ext.createNodeMesh(nodeIndex);
    });
    if (meshPromise) {
      pending.push(meshPromise);
    }
    if (nodeDef.camera !== undefined) {
      pending.push(parser.getDependency('camera', nodeDef.camera).then(function (camera) {
        return parser._getNodeRef(parser.cameraCache, nodeDef.camera, camera);
      }));
    }
    parser._invokeAll(function (ext) {
      return ext.createNodeAttachment && ext.createNodeAttachment(nodeIndex);
    }).forEach(function (promise) {
      pending.push(promise);
    });
    this.nodeCache[nodeIndex] = Promise.all(pending).then(function (objects) {
      let node;

      // .isBone isn't in glTF spec. See ._markDefs
      if (nodeDef.isBone === true) {
        node = new Bone();
      } else if (objects.length > 1) {
        node = new Group();
      } else if (objects.length === 1) {
        node = objects[0];
      } else {
        node = new Object3D$1();
      }
      if (node !== objects[0]) {
        for (let i = 0, il = objects.length; i < il; i++) {
          node.add(objects[i]);
        }
      }
      if (nodeDef.name) {
        node.userData.name = nodeDef.name;
        node.name = nodeName;
      }
      assignExtrasToUserData(node, nodeDef);
      if (nodeDef.extensions) addUnknownExtensionsToUserData(extensions, node, nodeDef);
      if (nodeDef.matrix !== undefined) {
        const matrix = new Matrix4$1();
        matrix.fromArray(nodeDef.matrix);
        node.applyMatrix4(matrix);
      } else {
        if (nodeDef.translation !== undefined) {
          node.position.fromArray(nodeDef.translation);
        }
        if (nodeDef.rotation !== undefined) {
          node.quaternion.fromArray(nodeDef.rotation);
        }
        if (nodeDef.scale !== undefined) {
          node.scale.fromArray(nodeDef.scale);
        }
      }
      if (!parser.associations.has(node)) {
        parser.associations.set(node, {});
      }
      parser.associations.get(node).nodes = nodeIndex;
      return node;
    });
    return this.nodeCache[nodeIndex];
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
   * @param {number} sceneIndex
   * @return {Promise<Group>}
   */
  loadScene(sceneIndex) {
    const extensions = this.extensions;
    const sceneDef = this.json.scenes[sceneIndex];
    const parser = this;

    // Loader returns Group, not Scene.
    // See: https://github.com/mrdoob/three.js/issues/18342#issuecomment-578981172
    const scene = new Group();
    if (sceneDef.name) scene.name = parser.createUniqueName(sceneDef.name);
    assignExtrasToUserData(scene, sceneDef);
    if (sceneDef.extensions) addUnknownExtensionsToUserData(extensions, scene, sceneDef);
    const nodeIds = sceneDef.nodes || [];
    const pending = [];
    for (let i = 0, il = nodeIds.length; i < il; i++) {
      pending.push(parser.getDependency('node', nodeIds[i]));
    }
    return Promise.all(pending).then(function (nodes) {
      for (let i = 0, il = nodes.length; i < il; i++) {
        scene.add(nodes[i]);
      }

      // Removes dangling associations, associations that reference a node that
      // didn't make it into the scene.
      const reduceAssociations = node => {
        const reducedAssociations = new Map();
        for (const [key, value] of parser.associations) {
          if (key instanceof Material$1 || key instanceof Texture$1) {
            reducedAssociations.set(key, value);
          }
        }
        node.traverse(node => {
          const mappings = parser.associations.get(node);
          if (mappings != null) {
            reducedAssociations.set(node, mappings);
          }
        });
        return reducedAssociations;
      };
      parser.associations = reduceAssociations(scene);
      return scene;
    });
  }
  _createAnimationTracks(node, inputAccessor, outputAccessor, sampler, target) {
    const tracks = [];
    const targetName = node.name ? node.name : node.uuid;
    const targetNames = [];
    if (PATH_PROPERTIES[target.path] === PATH_PROPERTIES.weights) {
      node.traverse(function (object) {
        if (object.morphTargetInfluences) {
          targetNames.push(object.name ? object.name : object.uuid);
        }
      });
    } else {
      targetNames.push(targetName);
    }
    let TypedKeyframeTrack;
    switch (PATH_PROPERTIES[target.path]) {
      case PATH_PROPERTIES.weights:
        TypedKeyframeTrack = NumberKeyframeTrack;
        break;
      case PATH_PROPERTIES.rotation:
        TypedKeyframeTrack = QuaternionKeyframeTrack;
        break;
      case PATH_PROPERTIES.position:
      case PATH_PROPERTIES.scale:
        TypedKeyframeTrack = VectorKeyframeTrack;
        break;
      default:
        switch (outputAccessor.itemSize) {
          case 1:
            TypedKeyframeTrack = NumberKeyframeTrack;
            break;
          case 2:
          case 3:
          default:
            TypedKeyframeTrack = VectorKeyframeTrack;
            break;
        }
        break;
    }
    const interpolation = sampler.interpolation !== undefined ? INTERPOLATION[sampler.interpolation] : InterpolateLinear;
    const outputArray = this._getArrayFromAccessor(outputAccessor);
    for (let j = 0, jl = targetNames.length; j < jl; j++) {
      const track = new TypedKeyframeTrack(targetNames[j] + '.' + PATH_PROPERTIES[target.path], inputAccessor.array, outputArray, interpolation);

      // Override interpolation with custom factory method.
      if (sampler.interpolation === 'CUBICSPLINE') {
        this._createCubicSplineTrackInterpolant(track);
      }
      tracks.push(track);
    }
    return tracks;
  }
  _getArrayFromAccessor(accessor) {
    let outputArray = accessor.array;
    if (accessor.normalized) {
      const scale = getNormalizedComponentScale(outputArray.constructor);
      const scaled = new Float32Array(outputArray.length);
      for (let j = 0, jl = outputArray.length; j < jl; j++) {
        scaled[j] = outputArray[j] * scale;
      }
      outputArray = scaled;
    }
    return outputArray;
  }
  _createCubicSplineTrackInterpolant(track) {
    track.createInterpolant = function InterpolantFactoryMethodGLTFCubicSpline(result) {
      // A CUBICSPLINE keyframe in glTF has three output values for each input value,
      // representing inTangent, splineVertex, and outTangent. As a result, track.getValueSize()
      // must be divided by three to get the interpolant's sampleSize argument.

      const interpolantType = this instanceof QuaternionKeyframeTrack ? GLTFCubicSplineQuaternionInterpolant : GLTFCubicSplineInterpolant;
      return new interpolantType(this.times, this.values, this.getValueSize() / 3, result);
    };

    // Mark as CUBICSPLINE. `track.getInterpolation()` doesn't support custom interpolants.
    track.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline = true;
  }
}

/**
 * @param {BufferGeometry} geometry
 * @param {GLTF.Primitive} primitiveDef
 * @param {GLTFParser} parser
 */
function computeBounds(geometry, primitiveDef, parser) {
  const attributes = primitiveDef.attributes;
  const box = new Box3$1();
  if (attributes.POSITION !== undefined) {
    const accessor = parser.json.accessors[attributes.POSITION];
    const min = accessor.min;
    const max = accessor.max;

    // glTF requires 'min' and 'max', but VRM (which extends glTF) currently ignores that requirement.

    if (min !== undefined && max !== undefined) {
      box.set(new Vector3$1(min[0], min[1], min[2]), new Vector3$1(max[0], max[1], max[2]));
      if (accessor.normalized) {
        const boxScale = getNormalizedComponentScale(WEBGL_COMPONENT_TYPES[accessor.componentType]);
        box.min.multiplyScalar(boxScale);
        box.max.multiplyScalar(boxScale);
      }
    } else {
      console.warn('THREE.GLTFLoader: Missing min/max properties for accessor POSITION.');
      return;
    }
  } else {
    return;
  }
  const targets = primitiveDef.targets;
  if (targets !== undefined) {
    const maxDisplacement = new Vector3$1();
    const vector = new Vector3$1();
    for (let i = 0, il = targets.length; i < il; i++) {
      const target = targets[i];
      if (target.POSITION !== undefined) {
        const accessor = parser.json.accessors[target.POSITION];
        const min = accessor.min;
        const max = accessor.max;

        // glTF requires 'min' and 'max', but VRM (which extends glTF) currently ignores that requirement.

        if (min !== undefined && max !== undefined) {
          // we need to get max of absolute components because target weight is [-1,1]
          vector.setX(Math.max(Math.abs(min[0]), Math.abs(max[0])));
          vector.setY(Math.max(Math.abs(min[1]), Math.abs(max[1])));
          vector.setZ(Math.max(Math.abs(min[2]), Math.abs(max[2])));
          if (accessor.normalized) {
            const boxScale = getNormalizedComponentScale(WEBGL_COMPONENT_TYPES[accessor.componentType]);
            vector.multiplyScalar(boxScale);
          }

          // Note: this assumes that the sum of all weights is at most 1. This isn't quite correct - it's more conservative
          // to assume that each target can have a max weight of 1. However, for some use cases - notably, when morph targets
          // are used to implement key-frame animations and as such only two are active at a time - this results in very large
          // boxes. So for now we make a box that's sometimes a touch too small but is hopefully mostly of reasonable size.
          maxDisplacement.max(vector);
        } else {
          console.warn('THREE.GLTFLoader: Missing min/max properties for accessor POSITION.');
        }
      }
    }

    // As per comment above this box isn't conservative, but has a reasonable size for a very large number of morph targets.
    box.expandByVector(maxDisplacement);
  }
  geometry.boundingBox = box;
  const sphere = new Sphere$1();
  box.getCenter(sphere.center);
  sphere.radius = box.min.distanceTo(box.max) / 2;
  geometry.boundingSphere = sphere;
}

/**
 * @param {BufferGeometry} geometry
 * @param {GLTF.Primitive} primitiveDef
 * @param {GLTFParser} parser
 * @return {Promise<BufferGeometry>}
 */
function addPrimitiveAttributes(geometry, primitiveDef, parser) {
  const attributes = primitiveDef.attributes;
  const pending = [];
  function assignAttributeAccessor(accessorIndex, attributeName) {
    return parser.getDependency('accessor', accessorIndex).then(function (accessor) {
      geometry.setAttribute(attributeName, accessor);
    });
  }
  for (const gltfAttributeName in attributes) {
    const threeAttributeName = ATTRIBUTES[gltfAttributeName] || gltfAttributeName.toLowerCase();

    // Skip attributes already provided by e.g. Draco extension.
    if (threeAttributeName in geometry.attributes) continue;
    pending.push(assignAttributeAccessor(attributes[gltfAttributeName], threeAttributeName));
  }
  if (primitiveDef.indices !== undefined && !geometry.index) {
    const accessor = parser.getDependency('accessor', primitiveDef.indices).then(function (accessor) {
      geometry.setIndex(accessor);
    });
    pending.push(accessor);
  }
  if (ColorManagement$1.workingColorSpace !== LinearSRGBColorSpace$1 && 'COLOR_0' in attributes) {
    console.warn(`THREE.GLTFLoader: Converting vertex colors from "srgb-linear" to "${ColorManagement$1.workingColorSpace}" not supported.`);
  }
  assignExtrasToUserData(geometry, primitiveDef);
  computeBounds(geometry, primitiveDef, parser);
  return Promise.all(pending).then(function () {
    return primitiveDef.targets !== undefined ? addMorphTargets(geometry, primitiveDef.targets, parser) : geometry;
  });
}

// OrbitControls performs orbiting, dollying (zooming), and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
//
//    Orbit - left mouse / touch: one-finger move
//    Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
//    Pan - right mouse, or left mouse + ctrl/meta/shiftKey, or arrow keys / touch: two-finger move

const _changeEvent = {
  type: 'change'
};
const _startEvent = {
  type: 'start'
};
const _endEvent = {
  type: 'end'
};
const _ray$1 = new Ray$1();
const _plane = new Plane$1();
const _TILT_LIMIT = Math.cos(70 * MathUtils$1.DEG2RAD);
const _v = new Vector3$1();
const _twoPI = 2 * Math.PI;
const _STATE = {
  NONE: -1,
  ROTATE: 0,
  DOLLY: 1,
  PAN: 2,
  TOUCH_ROTATE: 3,
  TOUCH_PAN: 4,
  TOUCH_DOLLY_PAN: 5,
  TOUCH_DOLLY_ROTATE: 6
};
const _EPS = 0.000001;
class OrbitControls extends Controls {
  constructor(object, domElement = null) {
    super(object, domElement);
    this.state = _STATE.NONE;

    // Set to false to disable this control
    this.enabled = true;

    // "target" sets the location of focus, where the object orbits around
    this.target = new Vector3$1();

    // Sets the 3D cursor (similar to Blender), from which the maxTargetRadius takes effect
    this.cursor = new Vector3$1();

    // How far you can dolly in and out ( PerspectiveCamera only )
    this.minDistance = 0;
    this.maxDistance = Infinity;

    // How far you can zoom in and out ( OrthographicCamera only )
    this.minZoom = 0;
    this.maxZoom = Infinity;

    // Limit camera target within a spherical area around the cursor
    this.minTargetRadius = 0;
    this.maxTargetRadius = Infinity;

    // How far you can orbit vertically, upper and lower limits.
    // Range is 0 to Math.PI radians.
    this.minPolarAngle = 0; // radians
    this.maxPolarAngle = Math.PI; // radians

    // How far you can orbit horizontally, upper and lower limits.
    // If set, the interval [ min, max ] must be a sub-interval of [ - 2 PI, 2 PI ], with ( max - min < 2 PI )
    this.minAzimuthAngle = -Infinity; // radians
    this.maxAzimuthAngle = Infinity; // radians

    // Set to true to enable damping (inertia)
    // If damping is enabled, you must call controls.update() in your animation loop
    this.enableDamping = false;
    this.dampingFactor = 0.05;

    // This option actually enables dollying in and out; left as "zoom" for backwards compatibility.
    // Set to false to disable zooming
    this.enableZoom = true;
    this.zoomSpeed = 1.0;

    // Set to false to disable rotating
    this.enableRotate = true;
    this.rotateSpeed = 1.0;

    // Set to false to disable panning
    this.enablePan = true;
    this.panSpeed = 1.0;
    this.screenSpacePanning = true; // if false, pan orthogonal to world-space direction camera.up
    this.keyPanSpeed = 7.0; // pixels moved per arrow key push
    this.zoomToCursor = false;

    // Set to true to automatically rotate around the target
    // If auto-rotate is enabled, you must call controls.update() in your animation loop
    this.autoRotate = false;
    this.autoRotateSpeed = 2.0; // 30 seconds per orbit when fps is 60

    // The four arrow keys
    this.keys = {
      LEFT: 'ArrowLeft',
      UP: 'ArrowUp',
      RIGHT: 'ArrowRight',
      BOTTOM: 'ArrowDown'
    };

    // Mouse buttons
    this.mouseButtons = {
      LEFT: MOUSE.ROTATE,
      MIDDLE: MOUSE.DOLLY,
      RIGHT: MOUSE.PAN
    };

    // Touch fingers
    this.touches = {
      ONE: TOUCH.ROTATE,
      TWO: TOUCH.DOLLY_PAN
    };

    // for reset
    this.target0 = this.target.clone();
    this.position0 = this.object.position.clone();
    this.zoom0 = this.object.zoom;

    // the target DOM element for key events
    this._domElementKeyEvents = null;

    // internals

    this._lastPosition = new Vector3$1();
    this._lastQuaternion = new Quaternion$1();
    this._lastTargetPosition = new Vector3$1();

    // so camera.up is the orbit axis
    this._quat = new Quaternion$1().setFromUnitVectors(object.up, new Vector3$1(0, 1, 0));
    this._quatInverse = this._quat.clone().invert();

    // current position in spherical coordinates
    this._spherical = new Spherical();
    this._sphericalDelta = new Spherical();
    this._scale = 1;
    this._panOffset = new Vector3$1();
    this._rotateStart = new Vector2$1();
    this._rotateEnd = new Vector2$1();
    this._rotateDelta = new Vector2$1();
    this._panStart = new Vector2$1();
    this._panEnd = new Vector2$1();
    this._panDelta = new Vector2$1();
    this._dollyStart = new Vector2$1();
    this._dollyEnd = new Vector2$1();
    this._dollyDelta = new Vector2$1();
    this._dollyDirection = new Vector3$1();
    this._mouse = new Vector2$1();
    this._performCursorZoom = false;
    this._pointers = [];
    this._pointerPositions = {};
    this._controlActive = false;

    // event listeners

    this._onPointerMove = onPointerMove.bind(this);
    this._onPointerDown = onPointerDown.bind(this);
    this._onPointerUp = onPointerUp.bind(this);
    this._onContextMenu = onContextMenu.bind(this);
    this._onMouseWheel = onMouseWheel.bind(this);
    this._onKeyDown = onKeyDown.bind(this);
    this._onTouchStart = onTouchStart.bind(this);
    this._onTouchMove = onTouchMove.bind(this);
    this._onMouseDown = onMouseDown.bind(this);
    this._onMouseMove = onMouseMove.bind(this);
    this._interceptControlDown = interceptControlDown.bind(this);
    this._interceptControlUp = interceptControlUp.bind(this);

    //

    if (this.domElement !== null) {
      this.connect();
    }
    this.update();
  }
  connect() {
    this.domElement.addEventListener('pointerdown', this._onPointerDown);
    this.domElement.addEventListener('pointercancel', this._onPointerUp);
    this.domElement.addEventListener('contextmenu', this._onContextMenu);
    this.domElement.addEventListener('wheel', this._onMouseWheel, {
      passive: false
    });
    const document = this.domElement.getRootNode(); // offscreen canvas compatibility
    document.addEventListener('keydown', this._interceptControlDown, {
      passive: true,
      capture: true
    });
    this.domElement.style.touchAction = 'none'; // disable touch scroll
  }
  disconnect() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);
    this.domElement.removeEventListener('pointercancel', this._onPointerUp);
    this.domElement.removeEventListener('wheel', this._onMouseWheel);
    this.domElement.removeEventListener('contextmenu', this._onContextMenu);
    this.stopListenToKeyEvents();
    const document = this.domElement.getRootNode(); // offscreen canvas compatibility
    document.removeEventListener('keydown', this._interceptControlDown, {
      capture: true
    });
    this.domElement.style.touchAction = 'auto';
  }
  dispose() {
    this.disconnect();
  }
  getPolarAngle() {
    return this._spherical.phi;
  }
  getAzimuthalAngle() {
    return this._spherical.theta;
  }
  getDistance() {
    return this.object.position.distanceTo(this.target);
  }
  listenToKeyEvents(domElement) {
    domElement.addEventListener('keydown', this._onKeyDown);
    this._domElementKeyEvents = domElement;
  }
  stopListenToKeyEvents() {
    if (this._domElementKeyEvents !== null) {
      this._domElementKeyEvents.removeEventListener('keydown', this._onKeyDown);
      this._domElementKeyEvents = null;
    }
  }
  saveState() {
    this.target0.copy(this.target);
    this.position0.copy(this.object.position);
    this.zoom0 = this.object.zoom;
  }
  reset() {
    this.target.copy(this.target0);
    this.object.position.copy(this.position0);
    this.object.zoom = this.zoom0;
    this.object.updateProjectionMatrix();
    this.dispatchEvent(_changeEvent);
    this.update();
    this.state = _STATE.NONE;
  }
  update(deltaTime = null) {
    const position = this.object.position;
    _v.copy(position).sub(this.target);

    // rotate offset to "y-axis-is-up" space
    _v.applyQuaternion(this._quat);

    // angle from z-axis around y-axis
    this._spherical.setFromVector3(_v);
    if (this.autoRotate && this.state === _STATE.NONE) {
      this._rotateLeft(this._getAutoRotationAngle(deltaTime));
    }
    if (this.enableDamping) {
      this._spherical.theta += this._sphericalDelta.theta * this.dampingFactor;
      this._spherical.phi += this._sphericalDelta.phi * this.dampingFactor;
    } else {
      this._spherical.theta += this._sphericalDelta.theta;
      this._spherical.phi += this._sphericalDelta.phi;
    }

    // restrict theta to be between desired limits

    let min = this.minAzimuthAngle;
    let max = this.maxAzimuthAngle;
    if (isFinite(min) && isFinite(max)) {
      if (min < -Math.PI) min += _twoPI;else if (min > Math.PI) min -= _twoPI;
      if (max < -Math.PI) max += _twoPI;else if (max > Math.PI) max -= _twoPI;
      if (min <= max) {
        this._spherical.theta = Math.max(min, Math.min(max, this._spherical.theta));
      } else {
        this._spherical.theta = this._spherical.theta > (min + max) / 2 ? Math.max(min, this._spherical.theta) : Math.min(max, this._spherical.theta);
      }
    }

    // restrict phi to be between desired limits
    this._spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this._spherical.phi));
    this._spherical.makeSafe();

    // move target to panned location

    if (this.enableDamping === true) {
      this.target.addScaledVector(this._panOffset, this.dampingFactor);
    } else {
      this.target.add(this._panOffset);
    }

    // Limit the target distance from the cursor to create a sphere around the center of interest
    this.target.sub(this.cursor);
    this.target.clampLength(this.minTargetRadius, this.maxTargetRadius);
    this.target.add(this.cursor);
    let zoomChanged = false;
    // adjust the camera position based on zoom only if we're not zooming to the cursor or if it's an ortho camera
    // we adjust zoom later in these cases
    if (this.zoomToCursor && this._performCursorZoom || this.object.isOrthographicCamera) {
      this._spherical.radius = this._clampDistance(this._spherical.radius);
    } else {
      const prevRadius = this._spherical.radius;
      this._spherical.radius = this._clampDistance(this._spherical.radius * this._scale);
      zoomChanged = prevRadius != this._spherical.radius;
    }
    _v.setFromSpherical(this._spherical);

    // rotate offset back to "camera-up-vector-is-up" space
    _v.applyQuaternion(this._quatInverse);
    position.copy(this.target).add(_v);
    this.object.lookAt(this.target);
    if (this.enableDamping === true) {
      this._sphericalDelta.theta *= 1 - this.dampingFactor;
      this._sphericalDelta.phi *= 1 - this.dampingFactor;
      this._panOffset.multiplyScalar(1 - this.dampingFactor);
    } else {
      this._sphericalDelta.set(0, 0, 0);
      this._panOffset.set(0, 0, 0);
    }

    // adjust camera position
    if (this.zoomToCursor && this._performCursorZoom) {
      let newRadius = null;
      if (this.object.isPerspectiveCamera) {
        // move the camera down the pointer ray
        // this method avoids floating point error
        const prevRadius = _v.length();
        newRadius = this._clampDistance(prevRadius * this._scale);
        const radiusDelta = prevRadius - newRadius;
        this.object.position.addScaledVector(this._dollyDirection, radiusDelta);
        this.object.updateMatrixWorld();
        zoomChanged = !!radiusDelta;
      } else if (this.object.isOrthographicCamera) {
        // adjust the ortho camera position based on zoom changes
        const mouseBefore = new Vector3$1(this._mouse.x, this._mouse.y, 0);
        mouseBefore.unproject(this.object);
        const prevZoom = this.object.zoom;
        this.object.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.object.zoom / this._scale));
        this.object.updateProjectionMatrix();
        zoomChanged = prevZoom !== this.object.zoom;
        const mouseAfter = new Vector3$1(this._mouse.x, this._mouse.y, 0);
        mouseAfter.unproject(this.object);
        this.object.position.sub(mouseAfter).add(mouseBefore);
        this.object.updateMatrixWorld();
        newRadius = _v.length();
      } else {
        console.warn('WARNING: OrbitControls.js encountered an unknown camera type - zoom to cursor disabled.');
        this.zoomToCursor = false;
      }

      // handle the placement of the target
      if (newRadius !== null) {
        if (this.screenSpacePanning) {
          // position the orbit target in front of the new camera position
          this.target.set(0, 0, -1).transformDirection(this.object.matrix).multiplyScalar(newRadius).add(this.object.position);
        } else {
          // get the ray and translation plane to compute target
          _ray$1.origin.copy(this.object.position);
          _ray$1.direction.set(0, 0, -1).transformDirection(this.object.matrix);

          // if the camera is 20 degrees above the horizon then don't adjust the focus target to avoid
          // extremely large values
          if (Math.abs(this.object.up.dot(_ray$1.direction)) < _TILT_LIMIT) {
            this.object.lookAt(this.target);
          } else {
            _plane.setFromNormalAndCoplanarPoint(this.object.up, this.target);
            _ray$1.intersectPlane(_plane, this.target);
          }
        }
      }
    } else if (this.object.isOrthographicCamera) {
      const prevZoom = this.object.zoom;
      this.object.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.object.zoom / this._scale));
      if (prevZoom !== this.object.zoom) {
        this.object.updateProjectionMatrix();
        zoomChanged = true;
      }
    }
    this._scale = 1;
    this._performCursorZoom = false;

    // update condition is:
    // min(camera displacement, camera rotation in radians)^2 > EPS
    // using small-angle approximation cos(x/2) = 1 - x^2 / 8

    if (zoomChanged || this._lastPosition.distanceToSquared(this.object.position) > _EPS || 8 * (1 - this._lastQuaternion.dot(this.object.quaternion)) > _EPS || this._lastTargetPosition.distanceToSquared(this.target) > _EPS) {
      this.dispatchEvent(_changeEvent);
      this._lastPosition.copy(this.object.position);
      this._lastQuaternion.copy(this.object.quaternion);
      this._lastTargetPosition.copy(this.target);
      return true;
    }
    return false;
  }
  _getAutoRotationAngle(deltaTime) {
    if (deltaTime !== null) {
      return _twoPI / 60 * this.autoRotateSpeed * deltaTime;
    } else {
      return _twoPI / 60 / 60 * this.autoRotateSpeed;
    }
  }
  _getZoomScale(delta) {
    const normalizedDelta = Math.abs(delta * 0.01);
    return Math.pow(0.95, this.zoomSpeed * normalizedDelta);
  }
  _rotateLeft(angle) {
    this._sphericalDelta.theta -= angle;
  }
  _rotateUp(angle) {
    this._sphericalDelta.phi -= angle;
  }
  _panLeft(distance, objectMatrix) {
    _v.setFromMatrixColumn(objectMatrix, 0); // get X column of objectMatrix
    _v.multiplyScalar(-distance);
    this._panOffset.add(_v);
  }
  _panUp(distance, objectMatrix) {
    if (this.screenSpacePanning === true) {
      _v.setFromMatrixColumn(objectMatrix, 1);
    } else {
      _v.setFromMatrixColumn(objectMatrix, 0);
      _v.crossVectors(this.object.up, _v);
    }
    _v.multiplyScalar(distance);
    this._panOffset.add(_v);
  }

  // deltaX and deltaY are in pixels; right and down are positive
  _pan(deltaX, deltaY) {
    const element = this.domElement;
    if (this.object.isPerspectiveCamera) {
      // perspective
      const position = this.object.position;
      _v.copy(position).sub(this.target);
      let targetDistance = _v.length();

      // half of the fov is center to top of screen
      targetDistance *= Math.tan(this.object.fov / 2 * Math.PI / 180.0);

      // we use only clientHeight here so aspect ratio does not distort speed
      this._panLeft(2 * deltaX * targetDistance / element.clientHeight, this.object.matrix);
      this._panUp(2 * deltaY * targetDistance / element.clientHeight, this.object.matrix);
    } else if (this.object.isOrthographicCamera) {
      // orthographic
      this._panLeft(deltaX * (this.object.right - this.object.left) / this.object.zoom / element.clientWidth, this.object.matrix);
      this._panUp(deltaY * (this.object.top - this.object.bottom) / this.object.zoom / element.clientHeight, this.object.matrix);
    } else {
      // camera neither orthographic nor perspective
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - pan disabled.');
      this.enablePan = false;
    }
  }
  _dollyOut(dollyScale) {
    if (this.object.isPerspectiveCamera || this.object.isOrthographicCamera) {
      this._scale /= dollyScale;
    } else {
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
      this.enableZoom = false;
    }
  }
  _dollyIn(dollyScale) {
    if (this.object.isPerspectiveCamera || this.object.isOrthographicCamera) {
      this._scale *= dollyScale;
    } else {
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
      this.enableZoom = false;
    }
  }
  _updateZoomParameters(x, y) {
    if (!this.zoomToCursor) {
      return;
    }
    this._performCursorZoom = true;
    const rect = this.domElement.getBoundingClientRect();
    const dx = x - rect.left;
    const dy = y - rect.top;
    const w = rect.width;
    const h = rect.height;
    this._mouse.x = dx / w * 2 - 1;
    this._mouse.y = -(dy / h) * 2 + 1;
    this._dollyDirection.set(this._mouse.x, this._mouse.y, 1).unproject(this.object).sub(this.object.position).normalize();
  }
  _clampDistance(dist) {
    return Math.max(this.minDistance, Math.min(this.maxDistance, dist));
  }

  //
  // event callbacks - update the object state
  //

  _handleMouseDownRotate(event) {
    this._rotateStart.set(event.clientX, event.clientY);
  }
  _handleMouseDownDolly(event) {
    this._updateZoomParameters(event.clientX, event.clientX);
    this._dollyStart.set(event.clientX, event.clientY);
  }
  _handleMouseDownPan(event) {
    this._panStart.set(event.clientX, event.clientY);
  }
  _handleMouseMoveRotate(event) {
    this._rotateEnd.set(event.clientX, event.clientY);
    this._rotateDelta.subVectors(this._rotateEnd, this._rotateStart).multiplyScalar(this.rotateSpeed);
    const element = this.domElement;
    this._rotateLeft(_twoPI * this._rotateDelta.x / element.clientHeight); // yes, height

    this._rotateUp(_twoPI * this._rotateDelta.y / element.clientHeight);
    this._rotateStart.copy(this._rotateEnd);
    this.update();
  }
  _handleMouseMoveDolly(event) {
    this._dollyEnd.set(event.clientX, event.clientY);
    this._dollyDelta.subVectors(this._dollyEnd, this._dollyStart);
    if (this._dollyDelta.y > 0) {
      this._dollyOut(this._getZoomScale(this._dollyDelta.y));
    } else if (this._dollyDelta.y < 0) {
      this._dollyIn(this._getZoomScale(this._dollyDelta.y));
    }
    this._dollyStart.copy(this._dollyEnd);
    this.update();
  }
  _handleMouseMovePan(event) {
    this._panEnd.set(event.clientX, event.clientY);
    this._panDelta.subVectors(this._panEnd, this._panStart).multiplyScalar(this.panSpeed);
    this._pan(this._panDelta.x, this._panDelta.y);
    this._panStart.copy(this._panEnd);
    this.update();
  }
  _handleMouseWheel(event) {
    this._updateZoomParameters(event.clientX, event.clientY);
    if (event.deltaY < 0) {
      this._dollyIn(this._getZoomScale(event.deltaY));
    } else if (event.deltaY > 0) {
      this._dollyOut(this._getZoomScale(event.deltaY));
    }
    this.update();
  }
  _handleKeyDown(event) {
    let needsUpdate = false;
    switch (event.code) {
      case this.keys.UP:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          this._rotateUp(_twoPI * this.rotateSpeed / this.domElement.clientHeight);
        } else {
          this._pan(0, this.keyPanSpeed);
        }
        needsUpdate = true;
        break;
      case this.keys.BOTTOM:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          this._rotateUp(-_twoPI * this.rotateSpeed / this.domElement.clientHeight);
        } else {
          this._pan(0, -this.keyPanSpeed);
        }
        needsUpdate = true;
        break;
      case this.keys.LEFT:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          this._rotateLeft(_twoPI * this.rotateSpeed / this.domElement.clientHeight);
        } else {
          this._pan(this.keyPanSpeed, 0);
        }
        needsUpdate = true;
        break;
      case this.keys.RIGHT:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          this._rotateLeft(-_twoPI * this.rotateSpeed / this.domElement.clientHeight);
        } else {
          this._pan(-this.keyPanSpeed, 0);
        }
        needsUpdate = true;
        break;
    }
    if (needsUpdate) {
      // prevent the browser from scrolling on cursor keys
      event.preventDefault();
      this.update();
    }
  }
  _handleTouchStartRotate(event) {
    if (this._pointers.length === 1) {
      this._rotateStart.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this._rotateStart.set(x, y);
    }
  }
  _handleTouchStartPan(event) {
    if (this._pointers.length === 1) {
      this._panStart.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this._panStart.set(x, y);
    }
  }
  _handleTouchStartDolly(event) {
    const position = this._getSecondPointerPosition(event);
    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._dollyStart.set(0, distance);
  }
  _handleTouchStartDollyPan(event) {
    if (this.enableZoom) this._handleTouchStartDolly(event);
    if (this.enablePan) this._handleTouchStartPan(event);
  }
  _handleTouchStartDollyRotate(event) {
    if (this.enableZoom) this._handleTouchStartDolly(event);
    if (this.enableRotate) this._handleTouchStartRotate(event);
  }
  _handleTouchMoveRotate(event) {
    if (this._pointers.length == 1) {
      this._rotateEnd.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this._rotateEnd.set(x, y);
    }
    this._rotateDelta.subVectors(this._rotateEnd, this._rotateStart).multiplyScalar(this.rotateSpeed);
    const element = this.domElement;
    this._rotateLeft(_twoPI * this._rotateDelta.x / element.clientHeight); // yes, height

    this._rotateUp(_twoPI * this._rotateDelta.y / element.clientHeight);
    this._rotateStart.copy(this._rotateEnd);
  }
  _handleTouchMovePan(event) {
    if (this._pointers.length === 1) {
      this._panEnd.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this._panEnd.set(x, y);
    }
    this._panDelta.subVectors(this._panEnd, this._panStart).multiplyScalar(this.panSpeed);
    this._pan(this._panDelta.x, this._panDelta.y);
    this._panStart.copy(this._panEnd);
  }
  _handleTouchMoveDolly(event) {
    const position = this._getSecondPointerPosition(event);
    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._dollyEnd.set(0, distance);
    this._dollyDelta.set(0, Math.pow(this._dollyEnd.y / this._dollyStart.y, this.zoomSpeed));
    this._dollyOut(this._dollyDelta.y);
    this._dollyStart.copy(this._dollyEnd);
    const centerX = (event.pageX + position.x) * 0.5;
    const centerY = (event.pageY + position.y) * 0.5;
    this._updateZoomParameters(centerX, centerY);
  }
  _handleTouchMoveDollyPan(event) {
    if (this.enableZoom) this._handleTouchMoveDolly(event);
    if (this.enablePan) this._handleTouchMovePan(event);
  }
  _handleTouchMoveDollyRotate(event) {
    if (this.enableZoom) this._handleTouchMoveDolly(event);
    if (this.enableRotate) this._handleTouchMoveRotate(event);
  }

  // pointers

  _addPointer(event) {
    this._pointers.push(event.pointerId);
  }
  _removePointer(event) {
    delete this._pointerPositions[event.pointerId];
    for (let i = 0; i < this._pointers.length; i++) {
      if (this._pointers[i] == event.pointerId) {
        this._pointers.splice(i, 1);
        return;
      }
    }
  }
  _isTrackingPointer(event) {
    for (let i = 0; i < this._pointers.length; i++) {
      if (this._pointers[i] == event.pointerId) return true;
    }
    return false;
  }
  _trackPointer(event) {
    let position = this._pointerPositions[event.pointerId];
    if (position === undefined) {
      position = new Vector2$1();
      this._pointerPositions[event.pointerId] = position;
    }
    position.set(event.pageX, event.pageY);
  }
  _getSecondPointerPosition(event) {
    const pointerId = event.pointerId === this._pointers[0] ? this._pointers[1] : this._pointers[0];
    return this._pointerPositions[pointerId];
  }

  //

  _customWheelEvent(event) {
    const mode = event.deltaMode;

    // minimal wheel event altered to meet delta-zoom demand
    const newEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
      deltaY: event.deltaY
    };
    switch (mode) {
      case 1:
        // LINE_MODE
        newEvent.deltaY *= 16;
        break;
      case 2:
        // PAGE_MODE
        newEvent.deltaY *= 100;
        break;
    }

    // detect if event was triggered by pinching
    if (event.ctrlKey && !this._controlActive) {
      newEvent.deltaY *= 10;
    }
    return newEvent;
  }
}
function onPointerDown(event) {
  if (this.enabled === false) return;
  if (this._pointers.length === 0) {
    this.domElement.setPointerCapture(event.pointerId);
    this.domElement.addEventListener('pointermove', this._onPointerMove);
    this.domElement.addEventListener('pointerup', this._onPointerUp);
  }

  //

  if (this._isTrackingPointer(event)) return;

  //

  this._addPointer(event);
  if (event.pointerType === 'touch') {
    this._onTouchStart(event);
  } else {
    this._onMouseDown(event);
  }
}
function onPointerMove(event) {
  if (this.enabled === false) return;
  if (event.pointerType === 'touch') {
    this._onTouchMove(event);
  } else {
    this._onMouseMove(event);
  }
}
function onPointerUp(event) {
  this._removePointer(event);
  switch (this._pointers.length) {
    case 0:
      this.domElement.releasePointerCapture(event.pointerId);
      this.domElement.removeEventListener('pointermove', this._onPointerMove);
      this.domElement.removeEventListener('pointerup', this._onPointerUp);
      this.dispatchEvent(_endEvent);
      this.state = _STATE.NONE;
      break;
    case 1:
      const pointerId = this._pointers[0];
      const position = this._pointerPositions[pointerId];

      // minimal placeholder event - allows state correction on pointer-up
      this._onTouchStart({
        pointerId: pointerId,
        pageX: position.x,
        pageY: position.y
      });
      break;
  }
}
function onMouseDown(event) {
  let mouseAction;
  switch (event.button) {
    case 0:
      mouseAction = this.mouseButtons.LEFT;
      break;
    case 1:
      mouseAction = this.mouseButtons.MIDDLE;
      break;
    case 2:
      mouseAction = this.mouseButtons.RIGHT;
      break;
    default:
      mouseAction = -1;
  }
  switch (mouseAction) {
    case MOUSE.DOLLY:
      if (this.enableZoom === false) return;
      this._handleMouseDownDolly(event);
      this.state = _STATE.DOLLY;
      break;
    case MOUSE.ROTATE:
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        if (this.enablePan === false) return;
        this._handleMouseDownPan(event);
        this.state = _STATE.PAN;
      } else {
        if (this.enableRotate === false) return;
        this._handleMouseDownRotate(event);
        this.state = _STATE.ROTATE;
      }
      break;
    case MOUSE.PAN:
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        if (this.enableRotate === false) return;
        this._handleMouseDownRotate(event);
        this.state = _STATE.ROTATE;
      } else {
        if (this.enablePan === false) return;
        this._handleMouseDownPan(event);
        this.state = _STATE.PAN;
      }
      break;
    default:
      this.state = _STATE.NONE;
  }
  if (this.state !== _STATE.NONE) {
    this.dispatchEvent(_startEvent);
  }
}
function onMouseMove(event) {
  switch (this.state) {
    case _STATE.ROTATE:
      if (this.enableRotate === false) return;
      this._handleMouseMoveRotate(event);
      break;
    case _STATE.DOLLY:
      if (this.enableZoom === false) return;
      this._handleMouseMoveDolly(event);
      break;
    case _STATE.PAN:
      if (this.enablePan === false) return;
      this._handleMouseMovePan(event);
      break;
  }
}
function onMouseWheel(event) {
  if (this.enabled === false || this.enableZoom === false || this.state !== _STATE.NONE) return;
  event.preventDefault();
  this.dispatchEvent(_startEvent);
  this._handleMouseWheel(this._customWheelEvent(event));
  this.dispatchEvent(_endEvent);
}
function onKeyDown(event) {
  if (this.enabled === false || this.enablePan === false) return;
  this._handleKeyDown(event);
}
function onTouchStart(event) {
  this._trackPointer(event);
  switch (this._pointers.length) {
    case 1:
      switch (this.touches.ONE) {
        case TOUCH.ROTATE:
          if (this.enableRotate === false) return;
          this._handleTouchStartRotate(event);
          this.state = _STATE.TOUCH_ROTATE;
          break;
        case TOUCH.PAN:
          if (this.enablePan === false) return;
          this._handleTouchStartPan(event);
          this.state = _STATE.TOUCH_PAN;
          break;
        default:
          this.state = _STATE.NONE;
      }
      break;
    case 2:
      switch (this.touches.TWO) {
        case TOUCH.DOLLY_PAN:
          if (this.enableZoom === false && this.enablePan === false) return;
          this._handleTouchStartDollyPan(event);
          this.state = _STATE.TOUCH_DOLLY_PAN;
          break;
        case TOUCH.DOLLY_ROTATE:
          if (this.enableZoom === false && this.enableRotate === false) return;
          this._handleTouchStartDollyRotate(event);
          this.state = _STATE.TOUCH_DOLLY_ROTATE;
          break;
        default:
          this.state = _STATE.NONE;
      }
      break;
    default:
      this.state = _STATE.NONE;
  }
  if (this.state !== _STATE.NONE) {
    this.dispatchEvent(_startEvent);
  }
}
function onTouchMove(event) {
  this._trackPointer(event);
  switch (this.state) {
    case _STATE.TOUCH_ROTATE:
      if (this.enableRotate === false) return;
      this._handleTouchMoveRotate(event);
      this.update();
      break;
    case _STATE.TOUCH_PAN:
      if (this.enablePan === false) return;
      this._handleTouchMovePan(event);
      this.update();
      break;
    case _STATE.TOUCH_DOLLY_PAN:
      if (this.enableZoom === false && this.enablePan === false) return;
      this._handleTouchMoveDollyPan(event);
      this.update();
      break;
    case _STATE.TOUCH_DOLLY_ROTATE:
      if (this.enableZoom === false && this.enableRotate === false) return;
      this._handleTouchMoveDollyRotate(event);
      this.update();
      break;
    default:
      this.state = _STATE.NONE;
  }
}
function onContextMenu(event) {
  if (this.enabled === false) return;
  event.preventDefault();
}
function interceptControlDown(event) {
  if (event.key === 'Control') {
    this._controlActive = true;
    const document = this.domElement.getRootNode(); // offscreen canvas compatibility

    document.addEventListener('keyup', this._interceptControlUp, {
      passive: true,
      capture: true
    });
  }
}
function interceptControlUp(event) {
  if (event.key === 'Control') {
    this._controlActive = false;
    const document = this.domElement.getRootNode(); // offscreen canvas compatibility

    document.removeEventListener('keyup', this._interceptControlUp, {
      passive: true,
      capture: true
    });
  }
}

class DataMap {
  constructor() {
    this.data = new WeakMap();
  }
  get(object) {
    let map = this.data.get(object);
    if (map === undefined) {
      map = {};
      this.data.set(object, map);
    }
    return map;
  }
  delete(object) {
    let map;
    if (this.data.has(object)) {
      map = this.data.get(object);
      this.data.delete(object);
    }
    return map;
  }
  has(object) {
    return this.data.has(object);
  }
  dispose() {
    this.data = new WeakMap();
  }
}
var DataMap$1 = DataMap;

const _lut = ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '0a', '0b', '0c', '0d', '0e', '0f', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '1a', '1b', '1c', '1d', '1e', '1f', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '2a', '2b', '2c', '2d', '2e', '2f', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '3a', '3b', '3c', '3d', '3e', '3f', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '4a', '4b', '4c', '4d', '4e', '4f', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '5a', '5b', '5c', '5d', '5e', '5f', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '6a', '6b', '6c', '6d', '6e', '6f', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '7a', '7b', '7c', '7d', '7e', '7f', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '8a', '8b', '8c', '8d', '8e', '8f', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '9a', '9b', '9c', '9d', '9e', '9f', 'a0', 'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'a9', 'aa', 'ab', 'ac', 'ad', 'ae', 'af', 'b0', 'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8', 'b9', 'ba', 'bb', 'bc', 'bd', 'be', 'bf', 'c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'ca', 'cb', 'cc', 'cd', 'ce', 'cf', 'd0', 'd1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9', 'da', 'db', 'dc', 'dd', 'de', 'df', 'e0', 'e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9', 'ea', 'eb', 'ec', 'ed', 'ee', 'ef', 'f0', 'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'fa', 'fb', 'fc', 'fd', 'fe', 'ff'];
let _seed = 1234567;
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

// http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
function generateUUID() {
  const d0 = Math.random() * 0xffffffff | 0;
  const d1 = Math.random() * 0xffffffff | 0;
  const d2 = Math.random() * 0xffffffff | 0;
  const d3 = Math.random() * 0xffffffff | 0;
  const uuid = _lut[d0 & 0xff] + _lut[d0 >> 8 & 0xff] + _lut[d0 >> 16 & 0xff] + _lut[d0 >> 24 & 0xff] + '-' + _lut[d1 & 0xff] + _lut[d1 >> 8 & 0xff] + '-' + _lut[d1 >> 16 & 0x0f | 0x40] + _lut[d1 >> 24 & 0xff] + '-' + _lut[d2 & 0x3f | 0x80] + _lut[d2 >> 8 & 0xff] + '-' + _lut[d2 >> 16 & 0xff] + _lut[d2 >> 24 & 0xff] + _lut[d3 & 0xff] + _lut[d3 >> 8 & 0xff] + _lut[d3 >> 16 & 0xff] + _lut[d3 >> 24 & 0xff];

  // .toLowerCase() here flattens concatenated strings to save heap memory space.
  return uuid.toLowerCase();
}
function clamp$1(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// compute euclidean modulo of m % n
// https://en.wikipedia.org/wiki/Modulo_operation
function euclideanModulo(n, m) {
  return (n % m + m) % m;
}

// Linear mapping from range <a1, a2> to range <b1, b2>
function mapLinear(x, a1, a2, b1, b2) {
  return b1 + (x - a1) * (b2 - b1) / (a2 - a1);
}

// https://www.gamedev.net/tutorials/programming/general-and-gameplay-programming/inverse-lerp-a-super-useful-yet-often-overlooked-function-r5230/
function inverseLerp(x, y, value) {
  if (x !== y) {
    return (value - x) / (y - x);
  } else {
    return 0;
  }
}

// https://en.wikipedia.org/wiki/Linear_interpolation
function lerp(x, y, t) {
  return (1 - t) * x + t * y;
}

// http://www.rorydriscoll.com/2016/03/07/frame-rate-independent-damping-using-lerp/
function damp(x, y, lambda, dt) {
  return lerp(x, y, 1 - Math.exp(-lambda * dt));
}

// https://www.desmos.com/calculator/vcsjnyz7x4
function pingpong(x, length = 1) {
  return length - Math.abs(euclideanModulo(x, length * 2) - length);
}

// http://en.wikipedia.org/wiki/Smoothstep
function smoothstep$1(x, min, max) {
  if (x <= min) return 0;
  if (x >= max) return 1;
  x = (x - min) / (max - min);
  return x * x * (3 - 2 * x);
}
function smootherstep(x, min, max) {
  if (x <= min) return 0;
  if (x >= max) return 1;
  x = (x - min) / (max - min);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

// Random integer from <low, high> interval
function randInt(low, high) {
  return low + Math.floor(Math.random() * (high - low + 1));
}

// Random float from <low, high> interval
function randFloat(low, high) {
  return low + Math.random() * (high - low);
}

// Random float from <-range/2, range/2> interval
function randFloatSpread(range) {
  return range * (0.5 - Math.random());
}

// Deterministic pseudo-random float in the interval [ 0, 1 ]
function seededRandom(s) {
  if (s !== undefined) _seed = s;

  // Mulberry32 generator

  let t = _seed += 0x6D2B79F5;
  t = Math.imul(t ^ t >>> 15, t | 1);
  t ^= t + Math.imul(t ^ t >>> 7, t | 61);
  return ((t ^ t >>> 14) >>> 0) / 4294967296;
}
function degToRad(degrees) {
  return degrees * DEG2RAD;
}
function radToDeg(radians) {
  return radians * RAD2DEG;
}
function isPowerOfTwo(value) {
  return (value & value - 1) === 0 && value !== 0;
}
function ceilPowerOfTwo(value) {
  return Math.pow(2, Math.ceil(Math.log(value) / Math.LN2));
}
function floorPowerOfTwo(value) {
  return Math.pow(2, Math.floor(Math.log(value) / Math.LN2));
}
function setQuaternionFromProperEuler(q, a, b, c, order) {
  // Intrinsic Proper Euler Angles - see https://en.wikipedia.org/wiki/Euler_angles

  // rotations are applied to the axes in the order specified by 'order'
  // rotation by angle 'a' is applied first, then by angle 'b', then by angle 'c'
  // angles are in radians

  const cos = Math.cos;
  const sin = Math.sin;
  const c2 = cos(b / 2);
  const s2 = sin(b / 2);
  const c13 = cos((a + c) / 2);
  const s13 = sin((a + c) / 2);
  const c1_3 = cos((a - c) / 2);
  const s1_3 = sin((a - c) / 2);
  const c3_1 = cos((c - a) / 2);
  const s3_1 = sin((c - a) / 2);
  switch (order) {
    case 'XYX':
      q.set(c2 * s13, s2 * c1_3, s2 * s1_3, c2 * c13);
      break;
    case 'YZY':
      q.set(s2 * s1_3, c2 * s13, s2 * c1_3, c2 * c13);
      break;
    case 'ZXZ':
      q.set(s2 * c1_3, s2 * s1_3, c2 * s13, c2 * c13);
      break;
    case 'XZX':
      q.set(c2 * s13, s2 * s3_1, s2 * c3_1, c2 * c13);
      break;
    case 'YXY':
      q.set(s2 * c3_1, c2 * s13, s2 * s3_1, c2 * c13);
      break;
    case 'ZYZ':
      q.set(s2 * s3_1, s2 * c3_1, c2 * s13, c2 * c13);
      break;
    default:
      console.warn('THREE.MathUtils: .setQuaternionFromProperEuler() encountered an unknown order: ' + order);
  }
}
function denormalize(value, array) {
  switch (array.constructor) {
    case Float32Array:
      return value;
    case Uint32Array:
      return value / 4294967295.0;
    case Uint16Array:
      return value / 65535.0;
    case Uint8Array:
      return value / 255.0;
    case Int32Array:
      return Math.max(value / 2147483647.0, -1.0);
    case Int16Array:
      return Math.max(value / 32767.0, -1.0);
    case Int8Array:
      return Math.max(value / 127.0, -1.0);
    default:
      throw new Error('Invalid component type.');
  }
}
function normalize$1(value, array) {
  switch (array.constructor) {
    case Float32Array:
      return value;
    case Uint32Array:
      return Math.round(value * 4294967295.0);
    case Uint16Array:
      return Math.round(value * 65535.0);
    case Uint8Array:
      return Math.round(value * 255.0);
    case Int32Array:
      return Math.round(value * 2147483647.0);
    case Int16Array:
      return Math.round(value * 32767.0);
    case Int8Array:
      return Math.round(value * 127.0);
    default:
      throw new Error('Invalid component type.');
  }
}
const MathUtils = {
  DEG2RAD: DEG2RAD,
  RAD2DEG: RAD2DEG,
  generateUUID: generateUUID,
  clamp: clamp$1,
  euclideanModulo: euclideanModulo,
  mapLinear: mapLinear,
  inverseLerp: inverseLerp,
  lerp: lerp,
  damp: damp,
  pingpong: pingpong,
  smoothstep: smoothstep$1,
  smootherstep: smootherstep,
  randInt: randInt,
  randFloat: randFloat,
  randFloatSpread: randFloatSpread,
  seededRandom: seededRandom,
  degToRad: degToRad,
  radToDeg: radToDeg,
  isPowerOfTwo: isPowerOfTwo,
  ceilPowerOfTwo: ceilPowerOfTwo,
  floorPowerOfTwo: floorPowerOfTwo,
  setQuaternionFromProperEuler: setQuaternionFromProperEuler,
  normalize: normalize$1,
  denormalize: denormalize
};

const FrontSide = 0;
const BackSide = 1;
const NormalBlending = 1;
const AdditiveBlending = 2;
const AddEquation = 100;
const SrcAlphaFactor = 204;
const OneMinusSrcAlphaFactor = 205;
const LessEqualDepth = 3;
const MultiplyOperation = 0;
const NoToneMapping = 0;
const UVMapping = 300;
const CubeReflectionMapping = 301;
const CubeRefractionMapping = 302;
const RepeatWrapping = 1000;
const ClampToEdgeWrapping = 1001;
const MirroredRepeatWrapping = 1002;
const NearestFilter = 1003;
const LinearFilter = 1006;
const LinearMipmapLinearFilter = 1008;
const LinearMipMapLinearFilter = 1008;
const UnsignedByteType = 1009;
const IntType = 1013;
const UnsignedIntType = 1014;
const FloatType = 1015;
const HalfFloatType = 1016;
const UnsignedInt248Type = 1020;
const RGBAFormat = 1023;
const DepthFormat = 1026;
const DepthStencilFormat = 1027;
const TangentSpaceNormalMap = 0;
const ObjectSpaceNormalMap = 1;

// Color space string identifiers, matching CSS Color Module Level 4 and WebGPU names where available.
const NoColorSpace = '';
const SRGBColorSpace = 'srgb';
const LinearSRGBColorSpace = 'srgb-linear';
const DisplayP3ColorSpace = 'display-p3';
const LinearDisplayP3ColorSpace = 'display-p3-linear';
const LinearTransfer = 'linear';
const SRGBTransfer = 'srgb';
const Rec709Primaries = 'rec709';
const P3Primaries = 'p3';
const KeepStencilOp = 7680;
const AlwaysStencilFunc = 519;
const StaticDrawUsage = 35044;
const DynamicDrawUsage = 35048;
const WebGLCoordinateSystem = 2000;
const WebGPUCoordinateSystem = 2001;

class Matrix3 {
  constructor(n11, n12, n13, n21, n22, n23, n31, n32, n33) {
    Matrix3.prototype.isMatrix3 = true;
    this.elements = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    if (n11 !== undefined) {
      this.set(n11, n12, n13, n21, n22, n23, n31, n32, n33);
    }
  }
  set(n11, n12, n13, n21, n22, n23, n31, n32, n33) {
    const te = this.elements;
    te[0] = n11;
    te[1] = n21;
    te[2] = n31;
    te[3] = n12;
    te[4] = n22;
    te[5] = n32;
    te[6] = n13;
    te[7] = n23;
    te[8] = n33;
    return this;
  }
  identity() {
    this.set(1, 0, 0, 0, 1, 0, 0, 0, 1);
    return this;
  }
  copy(m) {
    const te = this.elements;
    const me = m.elements;
    te[0] = me[0];
    te[1] = me[1];
    te[2] = me[2];
    te[3] = me[3];
    te[4] = me[4];
    te[5] = me[5];
    te[6] = me[6];
    te[7] = me[7];
    te[8] = me[8];
    return this;
  }
  extractBasis(xAxis, yAxis, zAxis) {
    xAxis.setFromMatrix3Column(this, 0);
    yAxis.setFromMatrix3Column(this, 1);
    zAxis.setFromMatrix3Column(this, 2);
    return this;
  }
  setFromMatrix4(m) {
    const me = m.elements;
    this.set(me[0], me[4], me[8], me[1], me[5], me[9], me[2], me[6], me[10]);
    return this;
  }
  multiply(m) {
    return this.multiplyMatrices(this, m);
  }
  premultiply(m) {
    return this.multiplyMatrices(m, this);
  }
  multiplyMatrices(a, b) {
    const ae = a.elements;
    const be = b.elements;
    const te = this.elements;
    const a11 = ae[0],
      a12 = ae[3],
      a13 = ae[6];
    const a21 = ae[1],
      a22 = ae[4],
      a23 = ae[7];
    const a31 = ae[2],
      a32 = ae[5],
      a33 = ae[8];
    const b11 = be[0],
      b12 = be[3],
      b13 = be[6];
    const b21 = be[1],
      b22 = be[4],
      b23 = be[7];
    const b31 = be[2],
      b32 = be[5],
      b33 = be[8];
    te[0] = a11 * b11 + a12 * b21 + a13 * b31;
    te[3] = a11 * b12 + a12 * b22 + a13 * b32;
    te[6] = a11 * b13 + a12 * b23 + a13 * b33;
    te[1] = a21 * b11 + a22 * b21 + a23 * b31;
    te[4] = a21 * b12 + a22 * b22 + a23 * b32;
    te[7] = a21 * b13 + a22 * b23 + a23 * b33;
    te[2] = a31 * b11 + a32 * b21 + a33 * b31;
    te[5] = a31 * b12 + a32 * b22 + a33 * b32;
    te[8] = a31 * b13 + a32 * b23 + a33 * b33;
    return this;
  }
  multiplyScalar(s) {
    const te = this.elements;
    te[0] *= s;
    te[3] *= s;
    te[6] *= s;
    te[1] *= s;
    te[4] *= s;
    te[7] *= s;
    te[2] *= s;
    te[5] *= s;
    te[8] *= s;
    return this;
  }
  determinant() {
    const te = this.elements;
    const a = te[0],
      b = te[1],
      c = te[2],
      d = te[3],
      e = te[4],
      f = te[5],
      g = te[6],
      h = te[7],
      i = te[8];
    return a * e * i - a * f * h - b * d * i + b * f * g + c * d * h - c * e * g;
  }
  invert() {
    const te = this.elements,
      n11 = te[0],
      n21 = te[1],
      n31 = te[2],
      n12 = te[3],
      n22 = te[4],
      n32 = te[5],
      n13 = te[6],
      n23 = te[7],
      n33 = te[8],
      t11 = n33 * n22 - n32 * n23,
      t12 = n32 * n13 - n33 * n12,
      t13 = n23 * n12 - n22 * n13,
      det = n11 * t11 + n21 * t12 + n31 * t13;
    if (det === 0) return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0);
    const detInv = 1 / det;
    te[0] = t11 * detInv;
    te[1] = (n31 * n23 - n33 * n21) * detInv;
    te[2] = (n32 * n21 - n31 * n22) * detInv;
    te[3] = t12 * detInv;
    te[4] = (n33 * n11 - n31 * n13) * detInv;
    te[5] = (n31 * n12 - n32 * n11) * detInv;
    te[6] = t13 * detInv;
    te[7] = (n21 * n13 - n23 * n11) * detInv;
    te[8] = (n22 * n11 - n21 * n12) * detInv;
    return this;
  }
  transpose() {
    let tmp;
    const m = this.elements;
    tmp = m[1];
    m[1] = m[3];
    m[3] = tmp;
    tmp = m[2];
    m[2] = m[6];
    m[6] = tmp;
    tmp = m[5];
    m[5] = m[7];
    m[7] = tmp;
    return this;
  }
  getNormalMatrix(matrix4) {
    return this.setFromMatrix4(matrix4).invert().transpose();
  }
  transposeIntoArray(r) {
    const m = this.elements;
    r[0] = m[0];
    r[1] = m[3];
    r[2] = m[6];
    r[3] = m[1];
    r[4] = m[4];
    r[5] = m[7];
    r[6] = m[2];
    r[7] = m[5];
    r[8] = m[8];
    return this;
  }
  setUvTransform(tx, ty, sx, sy, rotation, cx, cy) {
    const c = Math.cos(rotation);
    const s = Math.sin(rotation);
    this.set(sx * c, sx * s, -sx * (c * cx + s * cy) + cx + tx, -sy * s, sy * c, -sy * (-s * cx + c * cy) + cy + ty, 0, 0, 1);
    return this;
  }

  //

  scale(sx, sy) {
    this.premultiply(_m3.makeScale(sx, sy));
    return this;
  }
  rotate(theta) {
    this.premultiply(_m3.makeRotation(-theta));
    return this;
  }
  translate(tx, ty) {
    this.premultiply(_m3.makeTranslation(tx, ty));
    return this;
  }

  // for 2D Transforms

  makeTranslation(x, y) {
    if (x.isVector2) {
      this.set(1, 0, x.x, 0, 1, x.y, 0, 0, 1);
    } else {
      this.set(1, 0, x, 0, 1, y, 0, 0, 1);
    }
    return this;
  }
  makeRotation(theta) {
    // counterclockwise

    const c = Math.cos(theta);
    const s = Math.sin(theta);
    this.set(c, -s, 0, s, c, 0, 0, 0, 1);
    return this;
  }
  makeScale(x, y) {
    this.set(x, 0, 0, 0, y, 0, 0, 0, 1);
    return this;
  }

  //

  equals(matrix) {
    const te = this.elements;
    const me = matrix.elements;
    for (let i = 0; i < 9; i++) {
      if (te[i] !== me[i]) return false;
    }
    return true;
  }
  fromArray(array, offset = 0) {
    for (let i = 0; i < 9; i++) {
      this.elements[i] = array[i + offset];
    }
    return this;
  }
  toArray(array = [], offset = 0) {
    const te = this.elements;
    array[offset] = te[0];
    array[offset + 1] = te[1];
    array[offset + 2] = te[2];
    array[offset + 3] = te[3];
    array[offset + 4] = te[4];
    array[offset + 5] = te[5];
    array[offset + 6] = te[6];
    array[offset + 7] = te[7];
    array[offset + 8] = te[8];
    return array;
  }
  clone() {
    return new this.constructor().fromArray(this.elements);
  }
}
const _m3 = /*@__PURE__*/new Matrix3();

/**
 * Matrices converting P3 <-> Rec. 709 primaries, without gamut mapping
 * or clipping. Based on W3C specifications for sRGB and Display P3,
 * and ICC specifications for the D50 connection space. Values in/out
 * are _linear_ sRGB and _linear_ Display P3.
 *
 * Note that both sRGB and Display P3 use the sRGB transfer functions.
 *
 * Reference:
 * - http://www.russellcottrell.com/photo/matrixCalculator.htm
 */

const LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = /*@__PURE__*/new Matrix3().set(0.8224621, 0.177538, 0.0, 0.0331941, 0.9668058, 0.0, 0.0170827, 0.0723974, 0.9105199);
const LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = /*@__PURE__*/new Matrix3().set(1.2249401, -0.2249404, 0.0, -0.0420569, 1.0420571, 0.0, -0.0196376, -0.0786361, 1.0982735);

/**
 * Defines supported color spaces by transfer function and primaries,
 * and provides conversions to/from the Linear-sRGB reference space.
 */
const COLOR_SPACES = {
  [LinearSRGBColorSpace]: {
    transfer: LinearTransfer,
    primaries: Rec709Primaries,
    luminanceCoefficients: [0.2126, 0.7152, 0.0722],
    toReference: color => color,
    fromReference: color => color
  },
  [SRGBColorSpace]: {
    transfer: SRGBTransfer,
    primaries: Rec709Primaries,
    luminanceCoefficients: [0.2126, 0.7152, 0.0722],
    toReference: color => color.convertSRGBToLinear(),
    fromReference: color => color.convertLinearToSRGB()
  },
  [LinearDisplayP3ColorSpace]: {
    transfer: LinearTransfer,
    primaries: P3Primaries,
    luminanceCoefficients: [0.2289, 0.6917, 0.0793],
    toReference: color => color.applyMatrix3(LINEAR_DISPLAY_P3_TO_LINEAR_SRGB),
    fromReference: color => color.applyMatrix3(LINEAR_SRGB_TO_LINEAR_DISPLAY_P3)
  },
  [DisplayP3ColorSpace]: {
    transfer: SRGBTransfer,
    primaries: P3Primaries,
    luminanceCoefficients: [0.2289, 0.6917, 0.0793],
    toReference: color => color.convertSRGBToLinear().applyMatrix3(LINEAR_DISPLAY_P3_TO_LINEAR_SRGB),
    fromReference: color => color.applyMatrix3(LINEAR_SRGB_TO_LINEAR_DISPLAY_P3).convertLinearToSRGB()
  }
};
const SUPPORTED_WORKING_COLOR_SPACES = new Set([LinearSRGBColorSpace, LinearDisplayP3ColorSpace]);
const ColorManagement = {
  enabled: true,
  _workingColorSpace: LinearSRGBColorSpace,
  get workingColorSpace() {
    return this._workingColorSpace;
  },
  set workingColorSpace(colorSpace) {
    if (!SUPPORTED_WORKING_COLOR_SPACES.has(colorSpace)) {
      throw new Error(`Unsupported working color space, "${colorSpace}".`);
    }
    this._workingColorSpace = colorSpace;
  },
  convert: function (color, sourceColorSpace, targetColorSpace) {
    if (this.enabled === false || sourceColorSpace === targetColorSpace || !sourceColorSpace || !targetColorSpace) {
      return color;
    }
    const sourceToReference = COLOR_SPACES[sourceColorSpace].toReference;
    const targetFromReference = COLOR_SPACES[targetColorSpace].fromReference;
    return targetFromReference(sourceToReference(color));
  },
  fromWorkingColorSpace: function (color, targetColorSpace) {
    return this.convert(color, this._workingColorSpace, targetColorSpace);
  },
  toWorkingColorSpace: function (color, sourceColorSpace) {
    return this.convert(color, sourceColorSpace, this._workingColorSpace);
  },
  getPrimaries: function (colorSpace) {
    return COLOR_SPACES[colorSpace].primaries;
  },
  getTransfer: function (colorSpace) {
    if (colorSpace === NoColorSpace) return LinearTransfer;
    return COLOR_SPACES[colorSpace].transfer;
  },
  getLuminanceCoefficients: function (target, colorSpace = this._workingColorSpace) {
    return target.fromArray(COLOR_SPACES[colorSpace].luminanceCoefficients);
  }
};
function SRGBToLinear(c) {
  return c < 0.04045 ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
}
function LinearToSRGB(c) {
  return c < 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 0.41666) - 0.055;
}

const _colorKeywords = {
  'aliceblue': 0xF0F8FF,
  'antiquewhite': 0xFAEBD7,
  'aqua': 0x00FFFF,
  'aquamarine': 0x7FFFD4,
  'azure': 0xF0FFFF,
  'beige': 0xF5F5DC,
  'bisque': 0xFFE4C4,
  'black': 0x000000,
  'blanchedalmond': 0xFFEBCD,
  'blue': 0x0000FF,
  'blueviolet': 0x8A2BE2,
  'brown': 0xA52A2A,
  'burlywood': 0xDEB887,
  'cadetblue': 0x5F9EA0,
  'chartreuse': 0x7FFF00,
  'chocolate': 0xD2691E,
  'coral': 0xFF7F50,
  'cornflowerblue': 0x6495ED,
  'cornsilk': 0xFFF8DC,
  'crimson': 0xDC143C,
  'cyan': 0x00FFFF,
  'darkblue': 0x00008B,
  'darkcyan': 0x008B8B,
  'darkgoldenrod': 0xB8860B,
  'darkgray': 0xA9A9A9,
  'darkgreen': 0x006400,
  'darkgrey': 0xA9A9A9,
  'darkkhaki': 0xBDB76B,
  'darkmagenta': 0x8B008B,
  'darkolivegreen': 0x556B2F,
  'darkorange': 0xFF8C00,
  'darkorchid': 0x9932CC,
  'darkred': 0x8B0000,
  'darksalmon': 0xE9967A,
  'darkseagreen': 0x8FBC8F,
  'darkslateblue': 0x483D8B,
  'darkslategray': 0x2F4F4F,
  'darkslategrey': 0x2F4F4F,
  'darkturquoise': 0x00CED1,
  'darkviolet': 0x9400D3,
  'deeppink': 0xFF1493,
  'deepskyblue': 0x00BFFF,
  'dimgray': 0x696969,
  'dimgrey': 0x696969,
  'dodgerblue': 0x1E90FF,
  'firebrick': 0xB22222,
  'floralwhite': 0xFFFAF0,
  'forestgreen': 0x228B22,
  'fuchsia': 0xFF00FF,
  'gainsboro': 0xDCDCDC,
  'ghostwhite': 0xF8F8FF,
  'gold': 0xFFD700,
  'goldenrod': 0xDAA520,
  'gray': 0x808080,
  'green': 0x008000,
  'greenyellow': 0xADFF2F,
  'grey': 0x808080,
  'honeydew': 0xF0FFF0,
  'hotpink': 0xFF69B4,
  'indianred': 0xCD5C5C,
  'indigo': 0x4B0082,
  'ivory': 0xFFFFF0,
  'khaki': 0xF0E68C,
  'lavender': 0xE6E6FA,
  'lavenderblush': 0xFFF0F5,
  'lawngreen': 0x7CFC00,
  'lemonchiffon': 0xFFFACD,
  'lightblue': 0xADD8E6,
  'lightcoral': 0xF08080,
  'lightcyan': 0xE0FFFF,
  'lightgoldenrodyellow': 0xFAFAD2,
  'lightgray': 0xD3D3D3,
  'lightgreen': 0x90EE90,
  'lightgrey': 0xD3D3D3,
  'lightpink': 0xFFB6C1,
  'lightsalmon': 0xFFA07A,
  'lightseagreen': 0x20B2AA,
  'lightskyblue': 0x87CEFA,
  'lightslategray': 0x778899,
  'lightslategrey': 0x778899,
  'lightsteelblue': 0xB0C4DE,
  'lightyellow': 0xFFFFE0,
  'lime': 0x00FF00,
  'limegreen': 0x32CD32,
  'linen': 0xFAF0E6,
  'magenta': 0xFF00FF,
  'maroon': 0x800000,
  'mediumaquamarine': 0x66CDAA,
  'mediumblue': 0x0000CD,
  'mediumorchid': 0xBA55D3,
  'mediumpurple': 0x9370DB,
  'mediumseagreen': 0x3CB371,
  'mediumslateblue': 0x7B68EE,
  'mediumspringgreen': 0x00FA9A,
  'mediumturquoise': 0x48D1CC,
  'mediumvioletred': 0xC71585,
  'midnightblue': 0x191970,
  'mintcream': 0xF5FFFA,
  'mistyrose': 0xFFE4E1,
  'moccasin': 0xFFE4B5,
  'navajowhite': 0xFFDEAD,
  'navy': 0x000080,
  'oldlace': 0xFDF5E6,
  'olive': 0x808000,
  'olivedrab': 0x6B8E23,
  'orange': 0xFFA500,
  'orangered': 0xFF4500,
  'orchid': 0xDA70D6,
  'palegoldenrod': 0xEEE8AA,
  'palegreen': 0x98FB98,
  'paleturquoise': 0xAFEEEE,
  'palevioletred': 0xDB7093,
  'papayawhip': 0xFFEFD5,
  'peachpuff': 0xFFDAB9,
  'peru': 0xCD853F,
  'pink': 0xFFC0CB,
  'plum': 0xDDA0DD,
  'powderblue': 0xB0E0E6,
  'purple': 0x800080,
  'rebeccapurple': 0x663399,
  'red': 0xFF0000,
  'rosybrown': 0xBC8F8F,
  'royalblue': 0x4169E1,
  'saddlebrown': 0x8B4513,
  'salmon': 0xFA8072,
  'sandybrown': 0xF4A460,
  'seagreen': 0x2E8B57,
  'seashell': 0xFFF5EE,
  'sienna': 0xA0522D,
  'silver': 0xC0C0C0,
  'skyblue': 0x87CEEB,
  'slateblue': 0x6A5ACD,
  'slategray': 0x708090,
  'slategrey': 0x708090,
  'snow': 0xFFFAFA,
  'springgreen': 0x00FF7F,
  'steelblue': 0x4682B4,
  'tan': 0xD2B48C,
  'teal': 0x008080,
  'thistle': 0xD8BFD8,
  'tomato': 0xFF6347,
  'turquoise': 0x40E0D0,
  'violet': 0xEE82EE,
  'wheat': 0xF5DEB3,
  'white': 0xFFFFFF,
  'whitesmoke': 0xF5F5F5,
  'yellow': 0xFFFF00,
  'yellowgreen': 0x9ACD32
};
const _hslA = {
  h: 0,
  s: 0,
  l: 0
};
const _hslB = {
  h: 0,
  s: 0,
  l: 0
};
function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * 6 * (2 / 3 - t);
  return p;
}
class Color {
  constructor(r, g, b) {
    this.isColor = true;
    this.r = 1;
    this.g = 1;
    this.b = 1;
    return this.set(r, g, b);
  }
  set(r, g, b) {
    if (g === undefined && b === undefined) {
      // r is THREE.Color, hex or string

      const value = r;
      if (value && value.isColor) {
        this.copy(value);
      } else if (typeof value === 'number') {
        this.setHex(value);
      } else if (typeof value === 'string') {
        this.setStyle(value);
      }
    } else {
      this.setRGB(r, g, b);
    }
    return this;
  }
  setScalar(scalar) {
    this.r = scalar;
    this.g = scalar;
    this.b = scalar;
    return this;
  }
  setHex(hex, colorSpace = SRGBColorSpace) {
    hex = Math.floor(hex);
    this.r = (hex >> 16 & 255) / 255;
    this.g = (hex >> 8 & 255) / 255;
    this.b = (hex & 255) / 255;
    ColorManagement.toWorkingColorSpace(this, colorSpace);
    return this;
  }
  setRGB(r, g, b, colorSpace = ColorManagement.workingColorSpace) {
    this.r = r;
    this.g = g;
    this.b = b;
    ColorManagement.toWorkingColorSpace(this, colorSpace);
    return this;
  }
  setHSL(h, s, l, colorSpace = ColorManagement.workingColorSpace) {
    // h,s,l ranges are in 0.0 - 1.0
    h = euclideanModulo(h, 1);
    s = clamp$1(s, 0, 1);
    l = clamp$1(l, 0, 1);
    if (s === 0) {
      this.r = this.g = this.b = l;
    } else {
      const p = l <= 0.5 ? l * (1 + s) : l + s - l * s;
      const q = 2 * l - p;
      this.r = hue2rgb(q, p, h + 1 / 3);
      this.g = hue2rgb(q, p, h);
      this.b = hue2rgb(q, p, h - 1 / 3);
    }
    ColorManagement.toWorkingColorSpace(this, colorSpace);
    return this;
  }
  setStyle(style, colorSpace = SRGBColorSpace) {
    function handleAlpha(string) {
      if (string === undefined) return;
      if (parseFloat(string) < 1) {
        console.warn('THREE.Color: Alpha component of ' + style + ' will be ignored.');
      }
    }
    let m;
    if (m = /^(\w+)\(([^\)]*)\)/.exec(style)) {
      // rgb / hsl

      let color;
      const name = m[1];
      const components = m[2];
      switch (name) {
        case 'rgb':
        case 'rgba':
          if (color = /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)) {
            // rgb(255,0,0) rgba(255,0,0,0.5)

            handleAlpha(color[4]);
            return this.setRGB(Math.min(255, parseInt(color[1], 10)) / 255, Math.min(255, parseInt(color[2], 10)) / 255, Math.min(255, parseInt(color[3], 10)) / 255, colorSpace);
          }
          if (color = /^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)) {
            // rgb(100%,0%,0%) rgba(100%,0%,0%,0.5)

            handleAlpha(color[4]);
            return this.setRGB(Math.min(100, parseInt(color[1], 10)) / 100, Math.min(100, parseInt(color[2], 10)) / 100, Math.min(100, parseInt(color[3], 10)) / 100, colorSpace);
          }
          break;
        case 'hsl':
        case 'hsla':
          if (color = /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(components)) {
            // hsl(120,50%,50%) hsla(120,50%,50%,0.5)

            handleAlpha(color[4]);
            return this.setHSL(parseFloat(color[1]) / 360, parseFloat(color[2]) / 100, parseFloat(color[3]) / 100, colorSpace);
          }
          break;
        default:
          console.warn('THREE.Color: Unknown color model ' + style);
      }
    } else if (m = /^\#([A-Fa-f\d]+)$/.exec(style)) {
      // hex color

      const hex = m[1];
      const size = hex.length;
      if (size === 3) {
        // #ff0
        return this.setRGB(parseInt(hex.charAt(0), 16) / 15, parseInt(hex.charAt(1), 16) / 15, parseInt(hex.charAt(2), 16) / 15, colorSpace);
      } else if (size === 6) {
        // #ff0000
        return this.setHex(parseInt(hex, 16), colorSpace);
      } else {
        console.warn('THREE.Color: Invalid hex color ' + style);
      }
    } else if (style && style.length > 0) {
      return this.setColorName(style, colorSpace);
    }
    return this;
  }
  setColorName(style, colorSpace = SRGBColorSpace) {
    // color keywords
    const hex = _colorKeywords[style.toLowerCase()];
    if (hex !== undefined) {
      // red
      this.setHex(hex, colorSpace);
    } else {
      // unknown color
      console.warn('THREE.Color: Unknown color ' + style);
    }
    return this;
  }
  clone() {
    return new this.constructor(this.r, this.g, this.b);
  }
  copy(color) {
    this.r = color.r;
    this.g = color.g;
    this.b = color.b;
    return this;
  }
  copySRGBToLinear(color) {
    this.r = SRGBToLinear(color.r);
    this.g = SRGBToLinear(color.g);
    this.b = SRGBToLinear(color.b);
    return this;
  }
  copyLinearToSRGB(color) {
    this.r = LinearToSRGB(color.r);
    this.g = LinearToSRGB(color.g);
    this.b = LinearToSRGB(color.b);
    return this;
  }
  convertSRGBToLinear() {
    this.copySRGBToLinear(this);
    return this;
  }
  convertLinearToSRGB() {
    this.copyLinearToSRGB(this);
    return this;
  }
  getHex(colorSpace = SRGBColorSpace) {
    ColorManagement.fromWorkingColorSpace(_color.copy(this), colorSpace);
    return Math.round(clamp$1(_color.r * 255, 0, 255)) * 65536 + Math.round(clamp$1(_color.g * 255, 0, 255)) * 256 + Math.round(clamp$1(_color.b * 255, 0, 255));
  }
  getHexString(colorSpace = SRGBColorSpace) {
    return ('000000' + this.getHex(colorSpace).toString(16)).slice(-6);
  }
  getHSL(target, colorSpace = ColorManagement.workingColorSpace) {
    // h,s,l ranges are in 0.0 - 1.0

    ColorManagement.fromWorkingColorSpace(_color.copy(this), colorSpace);
    const r = _color.r,
      g = _color.g,
      b = _color.b;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hue, saturation;
    const lightness = (min + max) / 2.0;
    if (min === max) {
      hue = 0;
      saturation = 0;
    } else {
      const delta = max - min;
      saturation = lightness <= 0.5 ? delta / (max + min) : delta / (2 - max - min);
      switch (max) {
        case r:
          hue = (g - b) / delta + (g < b ? 6 : 0);
          break;
        case g:
          hue = (b - r) / delta + 2;
          break;
        case b:
          hue = (r - g) / delta + 4;
          break;
      }
      hue /= 6;
    }
    target.h = hue;
    target.s = saturation;
    target.l = lightness;
    return target;
  }
  getRGB(target, colorSpace = ColorManagement.workingColorSpace) {
    ColorManagement.fromWorkingColorSpace(_color.copy(this), colorSpace);
    target.r = _color.r;
    target.g = _color.g;
    target.b = _color.b;
    return target;
  }
  getStyle(colorSpace = SRGBColorSpace) {
    ColorManagement.fromWorkingColorSpace(_color.copy(this), colorSpace);
    const r = _color.r,
      g = _color.g,
      b = _color.b;
    if (colorSpace !== SRGBColorSpace) {
      // Requires CSS Color Module Level 4 (https://www.w3.org/TR/css-color-4/).
      return `color(${colorSpace} ${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)})`;
    }
    return `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`;
  }
  offsetHSL(h, s, l) {
    this.getHSL(_hslA);
    return this.setHSL(_hslA.h + h, _hslA.s + s, _hslA.l + l);
  }
  add(color) {
    this.r += color.r;
    this.g += color.g;
    this.b += color.b;
    return this;
  }
  addColors(color1, color2) {
    this.r = color1.r + color2.r;
    this.g = color1.g + color2.g;
    this.b = color1.b + color2.b;
    return this;
  }
  addScalar(s) {
    this.r += s;
    this.g += s;
    this.b += s;
    return this;
  }
  sub(color) {
    this.r = Math.max(0, this.r - color.r);
    this.g = Math.max(0, this.g - color.g);
    this.b = Math.max(0, this.b - color.b);
    return this;
  }
  multiply(color) {
    this.r *= color.r;
    this.g *= color.g;
    this.b *= color.b;
    return this;
  }
  multiplyScalar(s) {
    this.r *= s;
    this.g *= s;
    this.b *= s;
    return this;
  }
  lerp(color, alpha) {
    this.r += (color.r - this.r) * alpha;
    this.g += (color.g - this.g) * alpha;
    this.b += (color.b - this.b) * alpha;
    return this;
  }
  lerpColors(color1, color2, alpha) {
    this.r = color1.r + (color2.r - color1.r) * alpha;
    this.g = color1.g + (color2.g - color1.g) * alpha;
    this.b = color1.b + (color2.b - color1.b) * alpha;
    return this;
  }
  lerpHSL(color, alpha) {
    this.getHSL(_hslA);
    color.getHSL(_hslB);
    const h = lerp(_hslA.h, _hslB.h, alpha);
    const s = lerp(_hslA.s, _hslB.s, alpha);
    const l = lerp(_hslA.l, _hslB.l, alpha);
    this.setHSL(h, s, l);
    return this;
  }
  setFromVector3(v) {
    this.r = v.x;
    this.g = v.y;
    this.b = v.z;
    return this;
  }
  applyMatrix3(m) {
    const r = this.r,
      g = this.g,
      b = this.b;
    const e = m.elements;
    this.r = e[0] * r + e[3] * g + e[6] * b;
    this.g = e[1] * r + e[4] * g + e[7] * b;
    this.b = e[2] * r + e[5] * g + e[8] * b;
    return this;
  }
  equals(c) {
    return c.r === this.r && c.g === this.g && c.b === this.b;
  }
  fromArray(array, offset = 0) {
    this.r = array[offset];
    this.g = array[offset + 1];
    this.b = array[offset + 2];
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this.r;
    array[offset + 1] = this.g;
    array[offset + 2] = this.b;
    return array;
  }
  fromBufferAttribute(attribute, index) {
    this.r = attribute.getX(index);
    this.g = attribute.getY(index);
    this.b = attribute.getZ(index);
    return this;
  }
  toJSON() {
    return this.getHex();
  }
  *[Symbol.iterator]() {
    yield this.r;
    yield this.g;
    yield this.b;
  }
}
const _color = /*@__PURE__*/new Color();
Color.NAMES = _colorKeywords;

class Color4 extends Color {
  constructor(r, g, b, a = 1) {
    super(r, g, b);
    this.a = a;
  }
  set(r, g, b, a = 1) {
    this.a = a;
    return super.set(r, g, b);
  }
  copy(color) {
    if (color.a !== undefined) this.a = color.a;
    return super.copy(color);
  }
  clone() {
    return new this.constructor(this.r, this.g, this.b, this.a);
  }
}
var Color4$1 = Color4;

const NodeShaderStage = {
  VERTEX: 'vertex',
  FRAGMENT: 'fragment'
};
const NodeUpdateType = {
  NONE: 'none',
  FRAME: 'frame',
  RENDER: 'render',
  OBJECT: 'object'
};
const vectorComponents = ['x', 'y', 'z', 'w'];

class Quaternion {
  constructor(x = 0, y = 0, z = 0, w = 1) {
    this.isQuaternion = true;
    this._x = x;
    this._y = y;
    this._z = z;
    this._w = w;
  }
  static slerpFlat(dst, dstOffset, src0, srcOffset0, src1, srcOffset1, t) {
    // fuzz-free, array-based Quaternion SLERP operation

    let x0 = src0[srcOffset0 + 0],
      y0 = src0[srcOffset0 + 1],
      z0 = src0[srcOffset0 + 2],
      w0 = src0[srcOffset0 + 3];
    const x1 = src1[srcOffset1 + 0],
      y1 = src1[srcOffset1 + 1],
      z1 = src1[srcOffset1 + 2],
      w1 = src1[srcOffset1 + 3];
    if (t === 0) {
      dst[dstOffset + 0] = x0;
      dst[dstOffset + 1] = y0;
      dst[dstOffset + 2] = z0;
      dst[dstOffset + 3] = w0;
      return;
    }
    if (t === 1) {
      dst[dstOffset + 0] = x1;
      dst[dstOffset + 1] = y1;
      dst[dstOffset + 2] = z1;
      dst[dstOffset + 3] = w1;
      return;
    }
    if (w0 !== w1 || x0 !== x1 || y0 !== y1 || z0 !== z1) {
      let s = 1 - t;
      const cos = x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1,
        dir = cos >= 0 ? 1 : -1,
        sqrSin = 1 - cos * cos;

      // Skip the Slerp for tiny steps to avoid numeric problems:
      if (sqrSin > Number.EPSILON) {
        const sin = Math.sqrt(sqrSin),
          len = Math.atan2(sin, cos * dir);
        s = Math.sin(s * len) / sin;
        t = Math.sin(t * len) / sin;
      }
      const tDir = t * dir;
      x0 = x0 * s + x1 * tDir;
      y0 = y0 * s + y1 * tDir;
      z0 = z0 * s + z1 * tDir;
      w0 = w0 * s + w1 * tDir;

      // Normalize in case we just did a lerp:
      if (s === 1 - t) {
        const f = 1 / Math.sqrt(x0 * x0 + y0 * y0 + z0 * z0 + w0 * w0);
        x0 *= f;
        y0 *= f;
        z0 *= f;
        w0 *= f;
      }
    }
    dst[dstOffset] = x0;
    dst[dstOffset + 1] = y0;
    dst[dstOffset + 2] = z0;
    dst[dstOffset + 3] = w0;
  }
  static multiplyQuaternionsFlat(dst, dstOffset, src0, srcOffset0, src1, srcOffset1) {
    const x0 = src0[srcOffset0];
    const y0 = src0[srcOffset0 + 1];
    const z0 = src0[srcOffset0 + 2];
    const w0 = src0[srcOffset0 + 3];
    const x1 = src1[srcOffset1];
    const y1 = src1[srcOffset1 + 1];
    const z1 = src1[srcOffset1 + 2];
    const w1 = src1[srcOffset1 + 3];
    dst[dstOffset] = x0 * w1 + w0 * x1 + y0 * z1 - z0 * y1;
    dst[dstOffset + 1] = y0 * w1 + w0 * y1 + z0 * x1 - x0 * z1;
    dst[dstOffset + 2] = z0 * w1 + w0 * z1 + x0 * y1 - y0 * x1;
    dst[dstOffset + 3] = w0 * w1 - x0 * x1 - y0 * y1 - z0 * z1;
    return dst;
  }
  get x() {
    return this._x;
  }
  set x(value) {
    this._x = value;
    this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(value) {
    this._y = value;
    this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(value) {
    this._z = value;
    this._onChangeCallback();
  }
  get w() {
    return this._w;
  }
  set w(value) {
    this._w = value;
    this._onChangeCallback();
  }
  set(x, y, z, w) {
    this._x = x;
    this._y = y;
    this._z = z;
    this._w = w;
    this._onChangeCallback();
    return this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._w);
  }
  copy(quaternion) {
    this._x = quaternion.x;
    this._y = quaternion.y;
    this._z = quaternion.z;
    this._w = quaternion.w;
    this._onChangeCallback();
    return this;
  }
  setFromEuler(euler, update = true) {
    const x = euler._x,
      y = euler._y,
      z = euler._z,
      order = euler._order;

    // http://www.mathworks.com/matlabcentral/fileexchange/
    // 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
    //	content/SpinCalc.m

    const cos = Math.cos;
    const sin = Math.sin;
    const c1 = cos(x / 2);
    const c2 = cos(y / 2);
    const c3 = cos(z / 2);
    const s1 = sin(x / 2);
    const s2 = sin(y / 2);
    const s3 = sin(z / 2);
    switch (order) {
      case 'XYZ':
        this._x = s1 * c2 * c3 + c1 * s2 * s3;
        this._y = c1 * s2 * c3 - s1 * c2 * s3;
        this._z = c1 * c2 * s3 + s1 * s2 * c3;
        this._w = c1 * c2 * c3 - s1 * s2 * s3;
        break;
      case 'YXZ':
        this._x = s1 * c2 * c3 + c1 * s2 * s3;
        this._y = c1 * s2 * c3 - s1 * c2 * s3;
        this._z = c1 * c2 * s3 - s1 * s2 * c3;
        this._w = c1 * c2 * c3 + s1 * s2 * s3;
        break;
      case 'ZXY':
        this._x = s1 * c2 * c3 - c1 * s2 * s3;
        this._y = c1 * s2 * c3 + s1 * c2 * s3;
        this._z = c1 * c2 * s3 + s1 * s2 * c3;
        this._w = c1 * c2 * c3 - s1 * s2 * s3;
        break;
      case 'ZYX':
        this._x = s1 * c2 * c3 - c1 * s2 * s3;
        this._y = c1 * s2 * c3 + s1 * c2 * s3;
        this._z = c1 * c2 * s3 - s1 * s2 * c3;
        this._w = c1 * c2 * c3 + s1 * s2 * s3;
        break;
      case 'YZX':
        this._x = s1 * c2 * c3 + c1 * s2 * s3;
        this._y = c1 * s2 * c3 + s1 * c2 * s3;
        this._z = c1 * c2 * s3 - s1 * s2 * c3;
        this._w = c1 * c2 * c3 - s1 * s2 * s3;
        break;
      case 'XZY':
        this._x = s1 * c2 * c3 - c1 * s2 * s3;
        this._y = c1 * s2 * c3 - s1 * c2 * s3;
        this._z = c1 * c2 * s3 + s1 * s2 * c3;
        this._w = c1 * c2 * c3 + s1 * s2 * s3;
        break;
      default:
        console.warn('THREE.Quaternion: .setFromEuler() encountered an unknown order: ' + order);
    }
    if (update === true) this._onChangeCallback();
    return this;
  }
  setFromAxisAngle(axis, angle) {
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm

    // assumes axis is normalized

    const halfAngle = angle / 2,
      s = Math.sin(halfAngle);
    this._x = axis.x * s;
    this._y = axis.y * s;
    this._z = axis.z * s;
    this._w = Math.cos(halfAngle);
    this._onChangeCallback();
    return this;
  }
  setFromRotationMatrix(m) {
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm

    // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

    const te = m.elements,
      m11 = te[0],
      m12 = te[4],
      m13 = te[8],
      m21 = te[1],
      m22 = te[5],
      m23 = te[9],
      m31 = te[2],
      m32 = te[6],
      m33 = te[10],
      trace = m11 + m22 + m33;
    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1.0);
      this._w = 0.25 / s;
      this._x = (m32 - m23) * s;
      this._y = (m13 - m31) * s;
      this._z = (m21 - m12) * s;
    } else if (m11 > m22 && m11 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m11 - m22 - m33);
      this._w = (m32 - m23) / s;
      this._x = 0.25 * s;
      this._y = (m12 + m21) / s;
      this._z = (m13 + m31) / s;
    } else if (m22 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m22 - m11 - m33);
      this._w = (m13 - m31) / s;
      this._x = (m12 + m21) / s;
      this._y = 0.25 * s;
      this._z = (m23 + m32) / s;
    } else {
      const s = 2.0 * Math.sqrt(1.0 + m33 - m11 - m22);
      this._w = (m21 - m12) / s;
      this._x = (m13 + m31) / s;
      this._y = (m23 + m32) / s;
      this._z = 0.25 * s;
    }
    this._onChangeCallback();
    return this;
  }
  setFromUnitVectors(vFrom, vTo) {
    // assumes direction vectors vFrom and vTo are normalized

    let r = vFrom.dot(vTo) + 1;
    if (r < Number.EPSILON) {
      // vFrom and vTo point in opposite directions

      r = 0;
      if (Math.abs(vFrom.x) > Math.abs(vFrom.z)) {
        this._x = -vFrom.y;
        this._y = vFrom.x;
        this._z = 0;
        this._w = r;
      } else {
        this._x = 0;
        this._y = -vFrom.z;
        this._z = vFrom.y;
        this._w = r;
      }
    } else {
      // crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3

      this._x = vFrom.y * vTo.z - vFrom.z * vTo.y;
      this._y = vFrom.z * vTo.x - vFrom.x * vTo.z;
      this._z = vFrom.x * vTo.y - vFrom.y * vTo.x;
      this._w = r;
    }
    return this.normalize();
  }
  angleTo(q) {
    return 2 * Math.acos(Math.abs(clamp$1(this.dot(q), -1, 1)));
  }
  rotateTowards(q, step) {
    const angle = this.angleTo(q);
    if (angle === 0) return this;
    const t = Math.min(1, step / angle);
    this.slerp(q, t);
    return this;
  }
  identity() {
    return this.set(0, 0, 0, 1);
  }
  invert() {
    // quaternion is assumed to have unit length

    return this.conjugate();
  }
  conjugate() {
    this._x *= -1;
    this._y *= -1;
    this._z *= -1;
    this._onChangeCallback();
    return this;
  }
  dot(v) {
    return this._x * v._x + this._y * v._y + this._z * v._z + this._w * v._w;
  }
  lengthSq() {
    return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;
  }
  length() {
    return Math.sqrt(this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w);
  }
  normalize() {
    let l = this.length();
    if (l === 0) {
      this._x = 0;
      this._y = 0;
      this._z = 0;
      this._w = 1;
    } else {
      l = 1 / l;
      this._x = this._x * l;
      this._y = this._y * l;
      this._z = this._z * l;
      this._w = this._w * l;
    }
    this._onChangeCallback();
    return this;
  }
  multiply(q) {
    return this.multiplyQuaternions(this, q);
  }
  premultiply(q) {
    return this.multiplyQuaternions(q, this);
  }
  multiplyQuaternions(a, b) {
    // from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm

    const qax = a._x,
      qay = a._y,
      qaz = a._z,
      qaw = a._w;
    const qbx = b._x,
      qby = b._y,
      qbz = b._z,
      qbw = b._w;
    this._x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby;
    this._y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz;
    this._z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx;
    this._w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz;
    this._onChangeCallback();
    return this;
  }
  slerp(qb, t) {
    if (t === 0) return this;
    if (t === 1) return this.copy(qb);
    const x = this._x,
      y = this._y,
      z = this._z,
      w = this._w;

    // http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/

    let cosHalfTheta = w * qb._w + x * qb._x + y * qb._y + z * qb._z;
    if (cosHalfTheta < 0) {
      this._w = -qb._w;
      this._x = -qb._x;
      this._y = -qb._y;
      this._z = -qb._z;
      cosHalfTheta = -cosHalfTheta;
    } else {
      this.copy(qb);
    }
    if (cosHalfTheta >= 1.0) {
      this._w = w;
      this._x = x;
      this._y = y;
      this._z = z;
      return this;
    }
    const sqrSinHalfTheta = 1.0 - cosHalfTheta * cosHalfTheta;
    if (sqrSinHalfTheta <= Number.EPSILON) {
      const s = 1 - t;
      this._w = s * w + t * this._w;
      this._x = s * x + t * this._x;
      this._y = s * y + t * this._y;
      this._z = s * z + t * this._z;
      this.normalize(); // normalize calls _onChangeCallback()

      return this;
    }
    const sinHalfTheta = Math.sqrt(sqrSinHalfTheta);
    const halfTheta = Math.atan2(sinHalfTheta, cosHalfTheta);
    const ratioA = Math.sin((1 - t) * halfTheta) / sinHalfTheta,
      ratioB = Math.sin(t * halfTheta) / sinHalfTheta;
    this._w = w * ratioA + this._w * ratioB;
    this._x = x * ratioA + this._x * ratioB;
    this._y = y * ratioA + this._y * ratioB;
    this._z = z * ratioA + this._z * ratioB;
    this._onChangeCallback();
    return this;
  }
  slerpQuaternions(qa, qb, t) {
    return this.copy(qa).slerp(qb, t);
  }
  random() {
    // sets this quaternion to a uniform random unit quaternnion

    // Ken Shoemake
    // Uniform random rotations
    // D. Kirk, editor, Graphics Gems III, pages 124-132. Academic Press, New York, 1992.

    const theta1 = 2 * Math.PI * Math.random();
    const theta2 = 2 * Math.PI * Math.random();
    const x0 = Math.random();
    const r1 = Math.sqrt(1 - x0);
    const r2 = Math.sqrt(x0);
    return this.set(r1 * Math.sin(theta1), r1 * Math.cos(theta1), r2 * Math.sin(theta2), r2 * Math.cos(theta2));
  }
  equals(quaternion) {
    return quaternion._x === this._x && quaternion._y === this._y && quaternion._z === this._z && quaternion._w === this._w;
  }
  fromArray(array, offset = 0) {
    this._x = array[offset];
    this._y = array[offset + 1];
    this._z = array[offset + 2];
    this._w = array[offset + 3];
    this._onChangeCallback();
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this._x;
    array[offset + 1] = this._y;
    array[offset + 2] = this._z;
    array[offset + 3] = this._w;
    return array;
  }
  fromBufferAttribute(attribute, index) {
    this._x = attribute.getX(index);
    this._y = attribute.getY(index);
    this._z = attribute.getZ(index);
    this._w = attribute.getW(index);
    this._onChangeCallback();
    return this;
  }
  toJSON() {
    return this.toArray();
  }
  _onChange(callback) {
    this._onChangeCallback = callback;
    return this;
  }
  _onChangeCallback() {}
  *[Symbol.iterator]() {
    yield this._x;
    yield this._y;
    yield this._z;
    yield this._w;
  }
}

class Vector3 {
  constructor(x = 0, y = 0, z = 0) {
    Vector3.prototype.isVector3 = true;
    this.x = x;
    this.y = y;
    this.z = z;
  }
  set(x, y, z) {
    if (z === undefined) z = this.z; // sprite.scale.set(x,y)

    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  setScalar(scalar) {
    this.x = scalar;
    this.y = scalar;
    this.z = scalar;
    return this;
  }
  setX(x) {
    this.x = x;
    return this;
  }
  setY(y) {
    this.y = y;
    return this;
  }
  setZ(z) {
    this.z = z;
    return this;
  }
  setComponent(index, value) {
    switch (index) {
      case 0:
        this.x = value;
        break;
      case 1:
        this.y = value;
        break;
      case 2:
        this.z = value;
        break;
      default:
        throw new Error('index is out of range: ' + index);
    }
    return this;
  }
  getComponent(index) {
    switch (index) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      default:
        throw new Error('index is out of range: ' + index);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z);
  }
  copy(v) {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }
  add(v) {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }
  addScalar(s) {
    this.x += s;
    this.y += s;
    this.z += s;
    return this;
  }
  addVectors(a, b) {
    this.x = a.x + b.x;
    this.y = a.y + b.y;
    this.z = a.z + b.z;
    return this;
  }
  addScaledVector(v, s) {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    return this;
  }
  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }
  subScalar(s) {
    this.x -= s;
    this.y -= s;
    this.z -= s;
    return this;
  }
  subVectors(a, b) {
    this.x = a.x - b.x;
    this.y = a.y - b.y;
    this.z = a.z - b.z;
    return this;
  }
  multiply(v) {
    this.x *= v.x;
    this.y *= v.y;
    this.z *= v.z;
    return this;
  }
  multiplyScalar(scalar) {
    this.x *= scalar;
    this.y *= scalar;
    this.z *= scalar;
    return this;
  }
  multiplyVectors(a, b) {
    this.x = a.x * b.x;
    this.y = a.y * b.y;
    this.z = a.z * b.z;
    return this;
  }
  applyEuler(euler) {
    return this.applyQuaternion(_quaternion$2.setFromEuler(euler));
  }
  applyAxisAngle(axis, angle) {
    return this.applyQuaternion(_quaternion$2.setFromAxisAngle(axis, angle));
  }
  applyMatrix3(m) {
    const x = this.x,
      y = this.y,
      z = this.z;
    const e = m.elements;
    this.x = e[0] * x + e[3] * y + e[6] * z;
    this.y = e[1] * x + e[4] * y + e[7] * z;
    this.z = e[2] * x + e[5] * y + e[8] * z;
    return this;
  }
  applyNormalMatrix(m) {
    return this.applyMatrix3(m).normalize();
  }
  applyMatrix4(m) {
    const x = this.x,
      y = this.y,
      z = this.z;
    const e = m.elements;
    const w = 1 / (e[3] * x + e[7] * y + e[11] * z + e[15]);
    this.x = (e[0] * x + e[4] * y + e[8] * z + e[12]) * w;
    this.y = (e[1] * x + e[5] * y + e[9] * z + e[13]) * w;
    this.z = (e[2] * x + e[6] * y + e[10] * z + e[14]) * w;
    return this;
  }
  applyQuaternion(q) {
    // quaternion q is assumed to have unit length

    const vx = this.x,
      vy = this.y,
      vz = this.z;
    const qx = q.x,
      qy = q.y,
      qz = q.z,
      qw = q.w;

    // t = 2 * cross( q.xyz, v );
    const tx = 2 * (qy * vz - qz * vy);
    const ty = 2 * (qz * vx - qx * vz);
    const tz = 2 * (qx * vy - qy * vx);

    // v + q.w * t + cross( q.xyz, t );
    this.x = vx + qw * tx + qy * tz - qz * ty;
    this.y = vy + qw * ty + qz * tx - qx * tz;
    this.z = vz + qw * tz + qx * ty - qy * tx;
    return this;
  }
  project(camera) {
    return this.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(camera.projectionMatrix);
  }
  unproject(camera) {
    return this.applyMatrix4(camera.projectionMatrixInverse).applyMatrix4(camera.matrixWorld);
  }
  transformDirection(m) {
    // input: THREE.Matrix4 affine matrix
    // vector interpreted as a direction

    const x = this.x,
      y = this.y,
      z = this.z;
    const e = m.elements;
    this.x = e[0] * x + e[4] * y + e[8] * z;
    this.y = e[1] * x + e[5] * y + e[9] * z;
    this.z = e[2] * x + e[6] * y + e[10] * z;
    return this.normalize();
  }
  divide(v) {
    this.x /= v.x;
    this.y /= v.y;
    this.z /= v.z;
    return this;
  }
  divideScalar(scalar) {
    return this.multiplyScalar(1 / scalar);
  }
  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    this.z = Math.min(this.z, v.z);
    return this;
  }
  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    this.z = Math.max(this.z, v.z);
    return this;
  }
  clamp(min, max) {
    // assumes min < max, componentwise

    this.x = Math.max(min.x, Math.min(max.x, this.x));
    this.y = Math.max(min.y, Math.min(max.y, this.y));
    this.z = Math.max(min.z, Math.min(max.z, this.z));
    return this;
  }
  clampScalar(minVal, maxVal) {
    this.x = Math.max(minVal, Math.min(maxVal, this.x));
    this.y = Math.max(minVal, Math.min(maxVal, this.y));
    this.z = Math.max(minVal, Math.min(maxVal, this.z));
    return this;
  }
  clampLength(min, max) {
    const length = this.length();
    return this.divideScalar(length || 1).multiplyScalar(Math.max(min, Math.min(max, length)));
  }
  floor() {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);
    this.z = Math.floor(this.z);
    return this;
  }
  ceil() {
    this.x = Math.ceil(this.x);
    this.y = Math.ceil(this.y);
    this.z = Math.ceil(this.z);
    return this;
  }
  round() {
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);
    this.z = Math.round(this.z);
    return this;
  }
  roundToZero() {
    this.x = Math.trunc(this.x);
    this.y = Math.trunc(this.y);
    this.z = Math.trunc(this.z);
    return this;
  }
  negate() {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    return this;
  }
  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  // TODO lengthSquared?

  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(length) {
    return this.normalize().multiplyScalar(length);
  }
  lerp(v, alpha) {
    this.x += (v.x - this.x) * alpha;
    this.y += (v.y - this.y) * alpha;
    this.z += (v.z - this.z) * alpha;
    return this;
  }
  lerpVectors(v1, v2, alpha) {
    this.x = v1.x + (v2.x - v1.x) * alpha;
    this.y = v1.y + (v2.y - v1.y) * alpha;
    this.z = v1.z + (v2.z - v1.z) * alpha;
    return this;
  }
  cross(v) {
    return this.crossVectors(this, v);
  }
  crossVectors(a, b) {
    const ax = a.x,
      ay = a.y,
      az = a.z;
    const bx = b.x,
      by = b.y,
      bz = b.z;
    this.x = ay * bz - az * by;
    this.y = az * bx - ax * bz;
    this.z = ax * by - ay * bx;
    return this;
  }
  projectOnVector(v) {
    const denominator = v.lengthSq();
    if (denominator === 0) return this.set(0, 0, 0);
    const scalar = v.dot(this) / denominator;
    return this.copy(v).multiplyScalar(scalar);
  }
  projectOnPlane(planeNormal) {
    _vector$5.copy(this).projectOnVector(planeNormal);
    return this.sub(_vector$5);
  }
  reflect(normal) {
    // reflect incident vector off plane orthogonal to normal
    // normal is assumed to have unit length

    return this.sub(_vector$5.copy(normal).multiplyScalar(2 * this.dot(normal)));
  }
  angleTo(v) {
    const denominator = Math.sqrt(this.lengthSq() * v.lengthSq());
    if (denominator === 0) return Math.PI / 2;
    const theta = this.dot(v) / denominator;

    // clamp, to handle numerical problems

    return Math.acos(clamp$1(theta, -1, 1));
  }
  distanceTo(v) {
    return Math.sqrt(this.distanceToSquared(v));
  }
  distanceToSquared(v) {
    const dx = this.x - v.x,
      dy = this.y - v.y,
      dz = this.z - v.z;
    return dx * dx + dy * dy + dz * dz;
  }
  manhattanDistanceTo(v) {
    return Math.abs(this.x - v.x) + Math.abs(this.y - v.y) + Math.abs(this.z - v.z);
  }
  setFromSpherical(s) {
    return this.setFromSphericalCoords(s.radius, s.phi, s.theta);
  }
  setFromSphericalCoords(radius, phi, theta) {
    const sinPhiRadius = Math.sin(phi) * radius;
    this.x = sinPhiRadius * Math.sin(theta);
    this.y = Math.cos(phi) * radius;
    this.z = sinPhiRadius * Math.cos(theta);
    return this;
  }
  setFromCylindrical(c) {
    return this.setFromCylindricalCoords(c.radius, c.theta, c.y);
  }
  setFromCylindricalCoords(radius, theta, y) {
    this.x = radius * Math.sin(theta);
    this.y = y;
    this.z = radius * Math.cos(theta);
    return this;
  }
  setFromMatrixPosition(m) {
    const e = m.elements;
    this.x = e[12];
    this.y = e[13];
    this.z = e[14];
    return this;
  }
  setFromMatrixScale(m) {
    const sx = this.setFromMatrixColumn(m, 0).length();
    const sy = this.setFromMatrixColumn(m, 1).length();
    const sz = this.setFromMatrixColumn(m, 2).length();
    this.x = sx;
    this.y = sy;
    this.z = sz;
    return this;
  }
  setFromMatrixColumn(m, index) {
    return this.fromArray(m.elements, index * 4);
  }
  setFromMatrix3Column(m, index) {
    return this.fromArray(m.elements, index * 3);
  }
  setFromEuler(e) {
    this.x = e._x;
    this.y = e._y;
    this.z = e._z;
    return this;
  }
  setFromColor(c) {
    this.x = c.r;
    this.y = c.g;
    this.z = c.b;
    return this;
  }
  equals(v) {
    return v.x === this.x && v.y === this.y && v.z === this.z;
  }
  fromArray(array, offset = 0) {
    this.x = array[offset];
    this.y = array[offset + 1];
    this.z = array[offset + 2];
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this.x;
    array[offset + 1] = this.y;
    array[offset + 2] = this.z;
    return array;
  }
  fromBufferAttribute(attribute, index) {
    this.x = attribute.getX(index);
    this.y = attribute.getY(index);
    this.z = attribute.getZ(index);
    return this;
  }
  random() {
    this.x = Math.random();
    this.y = Math.random();
    this.z = Math.random();
    return this;
  }
  randomDirection() {
    // https://mathworld.wolfram.com/SpherePointPicking.html

    const theta = Math.random() * Math.PI * 2;
    const u = Math.random() * 2 - 1;
    const c = Math.sqrt(1 - u * u);
    this.x = c * Math.cos(theta);
    this.y = u;
    this.z = c * Math.sin(theta);
    return this;
  }
  *[Symbol.iterator]() {
    yield this.x;
    yield this.y;
    yield this.z;
  }
}
const _vector$5 = /*@__PURE__*/new Vector3();
const _quaternion$2 = /*@__PURE__*/new Quaternion();

class Matrix4 {
  constructor(n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44) {
    Matrix4.prototype.isMatrix4 = true;
    this.elements = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    if (n11 !== undefined) {
      this.set(n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44);
    }
  }
  set(n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44) {
    const te = this.elements;
    te[0] = n11;
    te[4] = n12;
    te[8] = n13;
    te[12] = n14;
    te[1] = n21;
    te[5] = n22;
    te[9] = n23;
    te[13] = n24;
    te[2] = n31;
    te[6] = n32;
    te[10] = n33;
    te[14] = n34;
    te[3] = n41;
    te[7] = n42;
    te[11] = n43;
    te[15] = n44;
    return this;
  }
  identity() {
    this.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
    return this;
  }
  clone() {
    return new Matrix4().fromArray(this.elements);
  }
  copy(m) {
    const te = this.elements;
    const me = m.elements;
    te[0] = me[0];
    te[1] = me[1];
    te[2] = me[2];
    te[3] = me[3];
    te[4] = me[4];
    te[5] = me[5];
    te[6] = me[6];
    te[7] = me[7];
    te[8] = me[8];
    te[9] = me[9];
    te[10] = me[10];
    te[11] = me[11];
    te[12] = me[12];
    te[13] = me[13];
    te[14] = me[14];
    te[15] = me[15];
    return this;
  }
  copyPosition(m) {
    const te = this.elements,
      me = m.elements;
    te[12] = me[12];
    te[13] = me[13];
    te[14] = me[14];
    return this;
  }
  setFromMatrix3(m) {
    const me = m.elements;
    this.set(me[0], me[3], me[6], 0, me[1], me[4], me[7], 0, me[2], me[5], me[8], 0, 0, 0, 0, 1);
    return this;
  }
  extractBasis(xAxis, yAxis, zAxis) {
    xAxis.setFromMatrixColumn(this, 0);
    yAxis.setFromMatrixColumn(this, 1);
    zAxis.setFromMatrixColumn(this, 2);
    return this;
  }
  makeBasis(xAxis, yAxis, zAxis) {
    this.set(xAxis.x, yAxis.x, zAxis.x, 0, xAxis.y, yAxis.y, zAxis.y, 0, xAxis.z, yAxis.z, zAxis.z, 0, 0, 0, 0, 1);
    return this;
  }
  extractRotation(m) {
    // this method does not support reflection matrices

    const te = this.elements;
    const me = m.elements;
    const scaleX = 1 / _v1$4.setFromMatrixColumn(m, 0).length();
    const scaleY = 1 / _v1$4.setFromMatrixColumn(m, 1).length();
    const scaleZ = 1 / _v1$4.setFromMatrixColumn(m, 2).length();
    te[0] = me[0] * scaleX;
    te[1] = me[1] * scaleX;
    te[2] = me[2] * scaleX;
    te[3] = 0;
    te[4] = me[4] * scaleY;
    te[5] = me[5] * scaleY;
    te[6] = me[6] * scaleY;
    te[7] = 0;
    te[8] = me[8] * scaleZ;
    te[9] = me[9] * scaleZ;
    te[10] = me[10] * scaleZ;
    te[11] = 0;
    te[12] = 0;
    te[13] = 0;
    te[14] = 0;
    te[15] = 1;
    return this;
  }
  makeRotationFromEuler(euler) {
    const te = this.elements;
    const x = euler.x,
      y = euler.y,
      z = euler.z;
    const a = Math.cos(x),
      b = Math.sin(x);
    const c = Math.cos(y),
      d = Math.sin(y);
    const e = Math.cos(z),
      f = Math.sin(z);
    if (euler.order === 'XYZ') {
      const ae = a * e,
        af = a * f,
        be = b * e,
        bf = b * f;
      te[0] = c * e;
      te[4] = -c * f;
      te[8] = d;
      te[1] = af + be * d;
      te[5] = ae - bf * d;
      te[9] = -b * c;
      te[2] = bf - ae * d;
      te[6] = be + af * d;
      te[10] = a * c;
    } else if (euler.order === 'YXZ') {
      const ce = c * e,
        cf = c * f,
        de = d * e,
        df = d * f;
      te[0] = ce + df * b;
      te[4] = de * b - cf;
      te[8] = a * d;
      te[1] = a * f;
      te[5] = a * e;
      te[9] = -b;
      te[2] = cf * b - de;
      te[6] = df + ce * b;
      te[10] = a * c;
    } else if (euler.order === 'ZXY') {
      const ce = c * e,
        cf = c * f,
        de = d * e,
        df = d * f;
      te[0] = ce - df * b;
      te[4] = -a * f;
      te[8] = de + cf * b;
      te[1] = cf + de * b;
      te[5] = a * e;
      te[9] = df - ce * b;
      te[2] = -a * d;
      te[6] = b;
      te[10] = a * c;
    } else if (euler.order === 'ZYX') {
      const ae = a * e,
        af = a * f,
        be = b * e,
        bf = b * f;
      te[0] = c * e;
      te[4] = be * d - af;
      te[8] = ae * d + bf;
      te[1] = c * f;
      te[5] = bf * d + ae;
      te[9] = af * d - be;
      te[2] = -d;
      te[6] = b * c;
      te[10] = a * c;
    } else if (euler.order === 'YZX') {
      const ac = a * c,
        ad = a * d,
        bc = b * c,
        bd = b * d;
      te[0] = c * e;
      te[4] = bd - ac * f;
      te[8] = bc * f + ad;
      te[1] = f;
      te[5] = a * e;
      te[9] = -b * e;
      te[2] = -d * e;
      te[6] = ad * f + bc;
      te[10] = ac - bd * f;
    } else if (euler.order === 'XZY') {
      const ac = a * c,
        ad = a * d,
        bc = b * c,
        bd = b * d;
      te[0] = c * e;
      te[4] = -f;
      te[8] = d * e;
      te[1] = ac * f + bd;
      te[5] = a * e;
      te[9] = ad * f - bc;
      te[2] = bc * f - ad;
      te[6] = b * e;
      te[10] = bd * f + ac;
    }

    // bottom row
    te[3] = 0;
    te[7] = 0;
    te[11] = 0;

    // last column
    te[12] = 0;
    te[13] = 0;
    te[14] = 0;
    te[15] = 1;
    return this;
  }
  makeRotationFromQuaternion(q) {
    return this.compose(_zero, q, _one);
  }
  lookAt(eye, target, up) {
    const te = this.elements;
    _z.subVectors(eye, target);
    if (_z.lengthSq() === 0) {
      // eye and target are in the same position

      _z.z = 1;
    }
    _z.normalize();
    _x.crossVectors(up, _z);
    if (_x.lengthSq() === 0) {
      // up and z are parallel

      if (Math.abs(up.z) === 1) {
        _z.x += 0.0001;
      } else {
        _z.z += 0.0001;
      }
      _z.normalize();
      _x.crossVectors(up, _z);
    }
    _x.normalize();
    _y.crossVectors(_z, _x);
    te[0] = _x.x;
    te[4] = _y.x;
    te[8] = _z.x;
    te[1] = _x.y;
    te[5] = _y.y;
    te[9] = _z.y;
    te[2] = _x.z;
    te[6] = _y.z;
    te[10] = _z.z;
    return this;
  }
  multiply(m) {
    return this.multiplyMatrices(this, m);
  }
  premultiply(m) {
    return this.multiplyMatrices(m, this);
  }
  multiplyMatrices(a, b) {
    const ae = a.elements;
    const be = b.elements;
    const te = this.elements;
    const a11 = ae[0],
      a12 = ae[4],
      a13 = ae[8],
      a14 = ae[12];
    const a21 = ae[1],
      a22 = ae[5],
      a23 = ae[9],
      a24 = ae[13];
    const a31 = ae[2],
      a32 = ae[6],
      a33 = ae[10],
      a34 = ae[14];
    const a41 = ae[3],
      a42 = ae[7],
      a43 = ae[11],
      a44 = ae[15];
    const b11 = be[0],
      b12 = be[4],
      b13 = be[8],
      b14 = be[12];
    const b21 = be[1],
      b22 = be[5],
      b23 = be[9],
      b24 = be[13];
    const b31 = be[2],
      b32 = be[6],
      b33 = be[10],
      b34 = be[14];
    const b41 = be[3],
      b42 = be[7],
      b43 = be[11],
      b44 = be[15];
    te[0] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
    te[4] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
    te[8] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
    te[12] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;
    te[1] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
    te[5] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
    te[9] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
    te[13] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;
    te[2] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
    te[6] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
    te[10] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
    te[14] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;
    te[3] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
    te[7] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
    te[11] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
    te[15] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;
    return this;
  }
  multiplyScalar(s) {
    const te = this.elements;
    te[0] *= s;
    te[4] *= s;
    te[8] *= s;
    te[12] *= s;
    te[1] *= s;
    te[5] *= s;
    te[9] *= s;
    te[13] *= s;
    te[2] *= s;
    te[6] *= s;
    te[10] *= s;
    te[14] *= s;
    te[3] *= s;
    te[7] *= s;
    te[11] *= s;
    te[15] *= s;
    return this;
  }
  determinant() {
    const te = this.elements;
    const n11 = te[0],
      n12 = te[4],
      n13 = te[8],
      n14 = te[12];
    const n21 = te[1],
      n22 = te[5],
      n23 = te[9],
      n24 = te[13];
    const n31 = te[2],
      n32 = te[6],
      n33 = te[10],
      n34 = te[14];
    const n41 = te[3],
      n42 = te[7],
      n43 = te[11],
      n44 = te[15];

    //TODO: make this more efficient
    //( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

    return n41 * (+n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34) + n42 * (+n11 * n23 * n34 - n11 * n24 * n33 + n14 * n21 * n33 - n13 * n21 * n34 + n13 * n24 * n31 - n14 * n23 * n31) + n43 * (+n11 * n24 * n32 - n11 * n22 * n34 - n14 * n21 * n32 + n12 * n21 * n34 + n14 * n22 * n31 - n12 * n24 * n31) + n44 * (-n13 * n22 * n31 - n11 * n23 * n32 + n11 * n22 * n33 + n13 * n21 * n32 - n12 * n21 * n33 + n12 * n23 * n31);
  }
  transpose() {
    const te = this.elements;
    let tmp;
    tmp = te[1];
    te[1] = te[4];
    te[4] = tmp;
    tmp = te[2];
    te[2] = te[8];
    te[8] = tmp;
    tmp = te[6];
    te[6] = te[9];
    te[9] = tmp;
    tmp = te[3];
    te[3] = te[12];
    te[12] = tmp;
    tmp = te[7];
    te[7] = te[13];
    te[13] = tmp;
    tmp = te[11];
    te[11] = te[14];
    te[14] = tmp;
    return this;
  }
  setPosition(x, y, z) {
    const te = this.elements;
    if (x.isVector3) {
      te[12] = x.x;
      te[13] = x.y;
      te[14] = x.z;
    } else {
      te[12] = x;
      te[13] = y;
      te[14] = z;
    }
    return this;
  }
  invert() {
    // based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
    const te = this.elements,
      n11 = te[0],
      n21 = te[1],
      n31 = te[2],
      n41 = te[3],
      n12 = te[4],
      n22 = te[5],
      n32 = te[6],
      n42 = te[7],
      n13 = te[8],
      n23 = te[9],
      n33 = te[10],
      n43 = te[11],
      n14 = te[12],
      n24 = te[13],
      n34 = te[14],
      n44 = te[15],
      t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
      t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
      t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
      t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;
    const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;
    if (det === 0) return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    const detInv = 1 / det;
    te[0] = t11 * detInv;
    te[1] = (n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44) * detInv;
    te[2] = (n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44) * detInv;
    te[3] = (n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43) * detInv;
    te[4] = t12 * detInv;
    te[5] = (n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44) * detInv;
    te[6] = (n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44) * detInv;
    te[7] = (n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43) * detInv;
    te[8] = t13 * detInv;
    te[9] = (n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44) * detInv;
    te[10] = (n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44) * detInv;
    te[11] = (n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43) * detInv;
    te[12] = t14 * detInv;
    te[13] = (n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34) * detInv;
    te[14] = (n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34) * detInv;
    te[15] = (n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33) * detInv;
    return this;
  }
  scale(v) {
    const te = this.elements;
    const x = v.x,
      y = v.y,
      z = v.z;
    te[0] *= x;
    te[4] *= y;
    te[8] *= z;
    te[1] *= x;
    te[5] *= y;
    te[9] *= z;
    te[2] *= x;
    te[6] *= y;
    te[10] *= z;
    te[3] *= x;
    te[7] *= y;
    te[11] *= z;
    return this;
  }
  getMaxScaleOnAxis() {
    const te = this.elements;
    const scaleXSq = te[0] * te[0] + te[1] * te[1] + te[2] * te[2];
    const scaleYSq = te[4] * te[4] + te[5] * te[5] + te[6] * te[6];
    const scaleZSq = te[8] * te[8] + te[9] * te[9] + te[10] * te[10];
    return Math.sqrt(Math.max(scaleXSq, scaleYSq, scaleZSq));
  }
  makeTranslation(x, y, z) {
    if (x.isVector3) {
      this.set(1, 0, 0, x.x, 0, 1, 0, x.y, 0, 0, 1, x.z, 0, 0, 0, 1);
    } else {
      this.set(1, 0, 0, x, 0, 1, 0, y, 0, 0, 1, z, 0, 0, 0, 1);
    }
    return this;
  }
  makeRotationX(theta) {
    const c = Math.cos(theta),
      s = Math.sin(theta);
    this.set(1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0, 0, 0, 0, 1);
    return this;
  }
  makeRotationY(theta) {
    const c = Math.cos(theta),
      s = Math.sin(theta);
    this.set(c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0, 0, 0, 0, 1);
    return this;
  }
  makeRotationZ(theta) {
    const c = Math.cos(theta),
      s = Math.sin(theta);
    this.set(c, -s, 0, 0, s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
    return this;
  }
  makeRotationAxis(axis, angle) {
    // Based on http://www.gamedev.net/reference/articles/article1199.asp

    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const t = 1 - c;
    const x = axis.x,
      y = axis.y,
      z = axis.z;
    const tx = t * x,
      ty = t * y;
    this.set(tx * x + c, tx * y - s * z, tx * z + s * y, 0, tx * y + s * z, ty * y + c, ty * z - s * x, 0, tx * z - s * y, ty * z + s * x, t * z * z + c, 0, 0, 0, 0, 1);
    return this;
  }
  makeScale(x, y, z) {
    this.set(x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1);
    return this;
  }
  makeShear(xy, xz, yx, yz, zx, zy) {
    this.set(1, yx, zx, 0, xy, 1, zy, 0, xz, yz, 1, 0, 0, 0, 0, 1);
    return this;
  }
  compose(position, quaternion, scale) {
    const te = this.elements;
    const x = quaternion._x,
      y = quaternion._y,
      z = quaternion._z,
      w = quaternion._w;
    const x2 = x + x,
      y2 = y + y,
      z2 = z + z;
    const xx = x * x2,
      xy = x * y2,
      xz = x * z2;
    const yy = y * y2,
      yz = y * z2,
      zz = z * z2;
    const wx = w * x2,
      wy = w * y2,
      wz = w * z2;
    const sx = scale.x,
      sy = scale.y,
      sz = scale.z;
    te[0] = (1 - (yy + zz)) * sx;
    te[1] = (xy + wz) * sx;
    te[2] = (xz - wy) * sx;
    te[3] = 0;
    te[4] = (xy - wz) * sy;
    te[5] = (1 - (xx + zz)) * sy;
    te[6] = (yz + wx) * sy;
    te[7] = 0;
    te[8] = (xz + wy) * sz;
    te[9] = (yz - wx) * sz;
    te[10] = (1 - (xx + yy)) * sz;
    te[11] = 0;
    te[12] = position.x;
    te[13] = position.y;
    te[14] = position.z;
    te[15] = 1;
    return this;
  }
  decompose(position, quaternion, scale) {
    const te = this.elements;
    let sx = _v1$4.set(te[0], te[1], te[2]).length();
    const sy = _v1$4.set(te[4], te[5], te[6]).length();
    const sz = _v1$4.set(te[8], te[9], te[10]).length();

    // if determine is negative, we need to invert one scale
    const det = this.determinant();
    if (det < 0) sx = -sx;
    position.x = te[12];
    position.y = te[13];
    position.z = te[14];

    // scale the rotation part
    _m1$2.copy(this);
    const invSX = 1 / sx;
    const invSY = 1 / sy;
    const invSZ = 1 / sz;
    _m1$2.elements[0] *= invSX;
    _m1$2.elements[1] *= invSX;
    _m1$2.elements[2] *= invSX;
    _m1$2.elements[4] *= invSY;
    _m1$2.elements[5] *= invSY;
    _m1$2.elements[6] *= invSY;
    _m1$2.elements[8] *= invSZ;
    _m1$2.elements[9] *= invSZ;
    _m1$2.elements[10] *= invSZ;
    quaternion.setFromRotationMatrix(_m1$2);
    scale.x = sx;
    scale.y = sy;
    scale.z = sz;
    return this;
  }
  makePerspective(left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem) {
    const te = this.elements;
    const x = 2 * near / (right - left);
    const y = 2 * near / (top - bottom);
    const a = (right + left) / (right - left);
    const b = (top + bottom) / (top - bottom);
    let c, d;
    if (coordinateSystem === WebGLCoordinateSystem) {
      c = -(far + near) / (far - near);
      d = -2 * far * near / (far - near);
    } else if (coordinateSystem === WebGPUCoordinateSystem) {
      c = -far / (far - near);
      d = -far * near / (far - near);
    } else {
      throw new Error('THREE.Matrix4.makePerspective(): Invalid coordinate system: ' + coordinateSystem);
    }
    te[0] = x;
    te[4] = 0;
    te[8] = a;
    te[12] = 0;
    te[1] = 0;
    te[5] = y;
    te[9] = b;
    te[13] = 0;
    te[2] = 0;
    te[6] = 0;
    te[10] = c;
    te[14] = d;
    te[3] = 0;
    te[7] = 0;
    te[11] = -1;
    te[15] = 0;
    return this;
  }
  makeOrthographic(left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem) {
    const te = this.elements;
    const w = 1.0 / (right - left);
    const h = 1.0 / (top - bottom);
    const p = 1.0 / (far - near);
    const x = (right + left) * w;
    const y = (top + bottom) * h;
    let z, zInv;
    if (coordinateSystem === WebGLCoordinateSystem) {
      z = (far + near) * p;
      zInv = -2 * p;
    } else if (coordinateSystem === WebGPUCoordinateSystem) {
      z = near * p;
      zInv = -1 * p;
    } else {
      throw new Error('THREE.Matrix4.makeOrthographic(): Invalid coordinate system: ' + coordinateSystem);
    }
    te[0] = 2 * w;
    te[4] = 0;
    te[8] = 0;
    te[12] = -x;
    te[1] = 0;
    te[5] = 2 * h;
    te[9] = 0;
    te[13] = -y;
    te[2] = 0;
    te[6] = 0;
    te[10] = zInv;
    te[14] = -z;
    te[3] = 0;
    te[7] = 0;
    te[11] = 0;
    te[15] = 1;
    return this;
  }
  equals(matrix) {
    const te = this.elements;
    const me = matrix.elements;
    for (let i = 0; i < 16; i++) {
      if (te[i] !== me[i]) return false;
    }
    return true;
  }
  fromArray(array, offset = 0) {
    for (let i = 0; i < 16; i++) {
      this.elements[i] = array[i + offset];
    }
    return this;
  }
  toArray(array = [], offset = 0) {
    const te = this.elements;
    array[offset] = te[0];
    array[offset + 1] = te[1];
    array[offset + 2] = te[2];
    array[offset + 3] = te[3];
    array[offset + 4] = te[4];
    array[offset + 5] = te[5];
    array[offset + 6] = te[6];
    array[offset + 7] = te[7];
    array[offset + 8] = te[8];
    array[offset + 9] = te[9];
    array[offset + 10] = te[10];
    array[offset + 11] = te[11];
    array[offset + 12] = te[12];
    array[offset + 13] = te[13];
    array[offset + 14] = te[14];
    array[offset + 15] = te[15];
    return array;
  }
}
const _v1$4 = /*@__PURE__*/new Vector3();
const _m1$2 = /*@__PURE__*/new Matrix4();
const _zero = /*@__PURE__*/new Vector3(0, 0, 0);
const _one = /*@__PURE__*/new Vector3(1, 1, 1);
const _x = /*@__PURE__*/new Vector3();
const _y = /*@__PURE__*/new Vector3();
const _z = /*@__PURE__*/new Vector3();

class Vector2 {
  constructor(x = 0, y = 0) {
    Vector2.prototype.isVector2 = true;
    this.x = x;
    this.y = y;
  }
  get width() {
    return this.x;
  }
  set width(value) {
    this.x = value;
  }
  get height() {
    return this.y;
  }
  set height(value) {
    this.y = value;
  }
  set(x, y) {
    this.x = x;
    this.y = y;
    return this;
  }
  setScalar(scalar) {
    this.x = scalar;
    this.y = scalar;
    return this;
  }
  setX(x) {
    this.x = x;
    return this;
  }
  setY(y) {
    this.y = y;
    return this;
  }
  setComponent(index, value) {
    switch (index) {
      case 0:
        this.x = value;
        break;
      case 1:
        this.y = value;
        break;
      default:
        throw new Error('index is out of range: ' + index);
    }
    return this;
  }
  getComponent(index) {
    switch (index) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      default:
        throw new Error('index is out of range: ' + index);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y);
  }
  copy(v) {
    this.x = v.x;
    this.y = v.y;
    return this;
  }
  add(v) {
    this.x += v.x;
    this.y += v.y;
    return this;
  }
  addScalar(s) {
    this.x += s;
    this.y += s;
    return this;
  }
  addVectors(a, b) {
    this.x = a.x + b.x;
    this.y = a.y + b.y;
    return this;
  }
  addScaledVector(v, s) {
    this.x += v.x * s;
    this.y += v.y * s;
    return this;
  }
  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    return this;
  }
  subScalar(s) {
    this.x -= s;
    this.y -= s;
    return this;
  }
  subVectors(a, b) {
    this.x = a.x - b.x;
    this.y = a.y - b.y;
    return this;
  }
  multiply(v) {
    this.x *= v.x;
    this.y *= v.y;
    return this;
  }
  multiplyScalar(scalar) {
    this.x *= scalar;
    this.y *= scalar;
    return this;
  }
  divide(v) {
    this.x /= v.x;
    this.y /= v.y;
    return this;
  }
  divideScalar(scalar) {
    return this.multiplyScalar(1 / scalar);
  }
  applyMatrix3(m) {
    const x = this.x,
      y = this.y;
    const e = m.elements;
    this.x = e[0] * x + e[3] * y + e[6];
    this.y = e[1] * x + e[4] * y + e[7];
    return this;
  }
  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    return this;
  }
  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    return this;
  }
  clamp(min, max) {
    // assumes min < max, componentwise

    this.x = Math.max(min.x, Math.min(max.x, this.x));
    this.y = Math.max(min.y, Math.min(max.y, this.y));
    return this;
  }
  clampScalar(minVal, maxVal) {
    this.x = Math.max(minVal, Math.min(maxVal, this.x));
    this.y = Math.max(minVal, Math.min(maxVal, this.y));
    return this;
  }
  clampLength(min, max) {
    const length = this.length();
    return this.divideScalar(length || 1).multiplyScalar(Math.max(min, Math.min(max, length)));
  }
  floor() {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);
    return this;
  }
  ceil() {
    this.x = Math.ceil(this.x);
    this.y = Math.ceil(this.y);
    return this;
  }
  round() {
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);
    return this;
  }
  roundToZero() {
    this.x = Math.trunc(this.x);
    this.y = Math.trunc(this.y);
    return this;
  }
  negate() {
    this.x = -this.x;
    this.y = -this.y;
    return this;
  }
  dot(v) {
    return this.x * v.x + this.y * v.y;
  }
  cross(v) {
    return this.x * v.y - this.y * v.x;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  angle() {
    // computes the angle in radians with respect to the positive x-axis

    const angle = Math.atan2(-this.y, -this.x) + Math.PI;
    return angle;
  }
  angleTo(v) {
    const denominator = Math.sqrt(this.lengthSq() * v.lengthSq());
    if (denominator === 0) return Math.PI / 2;
    const theta = this.dot(v) / denominator;

    // clamp, to handle numerical problems

    return Math.acos(clamp$1(theta, -1, 1));
  }
  distanceTo(v) {
    return Math.sqrt(this.distanceToSquared(v));
  }
  distanceToSquared(v) {
    const dx = this.x - v.x,
      dy = this.y - v.y;
    return dx * dx + dy * dy;
  }
  manhattanDistanceTo(v) {
    return Math.abs(this.x - v.x) + Math.abs(this.y - v.y);
  }
  setLength(length) {
    return this.normalize().multiplyScalar(length);
  }
  lerp(v, alpha) {
    this.x += (v.x - this.x) * alpha;
    this.y += (v.y - this.y) * alpha;
    return this;
  }
  lerpVectors(v1, v2, alpha) {
    this.x = v1.x + (v2.x - v1.x) * alpha;
    this.y = v1.y + (v2.y - v1.y) * alpha;
    return this;
  }
  equals(v) {
    return v.x === this.x && v.y === this.y;
  }
  fromArray(array, offset = 0) {
    this.x = array[offset];
    this.y = array[offset + 1];
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this.x;
    array[offset + 1] = this.y;
    return array;
  }
  fromBufferAttribute(attribute, index) {
    this.x = attribute.getX(index);
    this.y = attribute.getY(index);
    return this;
  }
  rotateAround(center, angle) {
    const c = Math.cos(angle),
      s = Math.sin(angle);
    const x = this.x - center.x;
    const y = this.y - center.y;
    this.x = x * c - y * s + center.x;
    this.y = x * s + y * c + center.y;
    return this;
  }
  random() {
    this.x = Math.random();
    this.y = Math.random();
    return this;
  }
  *[Symbol.iterator]() {
    yield this.x;
    yield this.y;
  }
}

class Vector4 {
  constructor(x = 0, y = 0, z = 0, w = 1) {
    Vector4.prototype.isVector4 = true;
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
  }
  get width() {
    return this.z;
  }
  set width(value) {
    this.z = value;
  }
  get height() {
    return this.w;
  }
  set height(value) {
    this.w = value;
  }
  set(x, y, z, w) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }
  setScalar(scalar) {
    this.x = scalar;
    this.y = scalar;
    this.z = scalar;
    this.w = scalar;
    return this;
  }
  setX(x) {
    this.x = x;
    return this;
  }
  setY(y) {
    this.y = y;
    return this;
  }
  setZ(z) {
    this.z = z;
    return this;
  }
  setW(w) {
    this.w = w;
    return this;
  }
  setComponent(index, value) {
    switch (index) {
      case 0:
        this.x = value;
        break;
      case 1:
        this.y = value;
        break;
      case 2:
        this.z = value;
        break;
      case 3:
        this.w = value;
        break;
      default:
        throw new Error('index is out of range: ' + index);
    }
    return this;
  }
  getComponent(index) {
    switch (index) {
      case 0:
        return this.x;
      case 1:
        return this.y;
      case 2:
        return this.z;
      case 3:
        return this.w;
      default:
        throw new Error('index is out of range: ' + index);
    }
  }
  clone() {
    return new this.constructor(this.x, this.y, this.z, this.w);
  }
  copy(v) {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    this.w = v.w !== undefined ? v.w : 1;
    return this;
  }
  add(v) {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    this.w += v.w;
    return this;
  }
  addScalar(s) {
    this.x += s;
    this.y += s;
    this.z += s;
    this.w += s;
    return this;
  }
  addVectors(a, b) {
    this.x = a.x + b.x;
    this.y = a.y + b.y;
    this.z = a.z + b.z;
    this.w = a.w + b.w;
    return this;
  }
  addScaledVector(v, s) {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    this.w += v.w * s;
    return this;
  }
  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    this.w -= v.w;
    return this;
  }
  subScalar(s) {
    this.x -= s;
    this.y -= s;
    this.z -= s;
    this.w -= s;
    return this;
  }
  subVectors(a, b) {
    this.x = a.x - b.x;
    this.y = a.y - b.y;
    this.z = a.z - b.z;
    this.w = a.w - b.w;
    return this;
  }
  multiply(v) {
    this.x *= v.x;
    this.y *= v.y;
    this.z *= v.z;
    this.w *= v.w;
    return this;
  }
  multiplyScalar(scalar) {
    this.x *= scalar;
    this.y *= scalar;
    this.z *= scalar;
    this.w *= scalar;
    return this;
  }
  applyMatrix4(m) {
    const x = this.x,
      y = this.y,
      z = this.z,
      w = this.w;
    const e = m.elements;
    this.x = e[0] * x + e[4] * y + e[8] * z + e[12] * w;
    this.y = e[1] * x + e[5] * y + e[9] * z + e[13] * w;
    this.z = e[2] * x + e[6] * y + e[10] * z + e[14] * w;
    this.w = e[3] * x + e[7] * y + e[11] * z + e[15] * w;
    return this;
  }
  divideScalar(scalar) {
    return this.multiplyScalar(1 / scalar);
  }
  setAxisAngleFromQuaternion(q) {
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/quaternionToAngle/index.htm

    // q is assumed to be normalized

    this.w = 2 * Math.acos(q.w);
    const s = Math.sqrt(1 - q.w * q.w);
    if (s < 0.0001) {
      this.x = 1;
      this.y = 0;
      this.z = 0;
    } else {
      this.x = q.x / s;
      this.y = q.y / s;
      this.z = q.z / s;
    }
    return this;
  }
  setAxisAngleFromRotationMatrix(m) {
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToAngle/index.htm

    // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

    let angle, x, y, z; // variables for result
    const epsilon = 0.01,
      // margin to allow for rounding errors
      epsilon2 = 0.1,
      // margin to distinguish between 0 and 180 degrees

      te = m.elements,
      m11 = te[0],
      m12 = te[4],
      m13 = te[8],
      m21 = te[1],
      m22 = te[5],
      m23 = te[9],
      m31 = te[2],
      m32 = te[6],
      m33 = te[10];
    if (Math.abs(m12 - m21) < epsilon && Math.abs(m13 - m31) < epsilon && Math.abs(m23 - m32) < epsilon) {
      // singularity found
      // first check for identity matrix which must have +1 for all terms
      // in leading diagonal and zero in other terms

      if (Math.abs(m12 + m21) < epsilon2 && Math.abs(m13 + m31) < epsilon2 && Math.abs(m23 + m32) < epsilon2 && Math.abs(m11 + m22 + m33 - 3) < epsilon2) {
        // this singularity is identity matrix so angle = 0

        this.set(1, 0, 0, 0);
        return this; // zero angle, arbitrary axis
      }

      // otherwise this singularity is angle = 180

      angle = Math.PI;
      const xx = (m11 + 1) / 2;
      const yy = (m22 + 1) / 2;
      const zz = (m33 + 1) / 2;
      const xy = (m12 + m21) / 4;
      const xz = (m13 + m31) / 4;
      const yz = (m23 + m32) / 4;
      if (xx > yy && xx > zz) {
        // m11 is the largest diagonal term

        if (xx < epsilon) {
          x = 0;
          y = 0.707106781;
          z = 0.707106781;
        } else {
          x = Math.sqrt(xx);
          y = xy / x;
          z = xz / x;
        }
      } else if (yy > zz) {
        // m22 is the largest diagonal term

        if (yy < epsilon) {
          x = 0.707106781;
          y = 0;
          z = 0.707106781;
        } else {
          y = Math.sqrt(yy);
          x = xy / y;
          z = yz / y;
        }
      } else {
        // m33 is the largest diagonal term so base result on this

        if (zz < epsilon) {
          x = 0.707106781;
          y = 0.707106781;
          z = 0;
        } else {
          z = Math.sqrt(zz);
          x = xz / z;
          y = yz / z;
        }
      }
      this.set(x, y, z, angle);
      return this; // return 180 deg rotation
    }

    // as we have reached here there are no singularities so we can handle normally

    let s = Math.sqrt((m32 - m23) * (m32 - m23) + (m13 - m31) * (m13 - m31) + (m21 - m12) * (m21 - m12)); // used to normalize

    if (Math.abs(s) < 0.001) s = 1;

    // prevent divide by zero, should not happen if matrix is orthogonal and should be
    // caught by singularity test above, but I've left it in just in case

    this.x = (m32 - m23) / s;
    this.y = (m13 - m31) / s;
    this.z = (m21 - m12) / s;
    this.w = Math.acos((m11 + m22 + m33 - 1) / 2);
    return this;
  }
  setFromMatrixPosition(m) {
    const e = m.elements;
    this.x = e[12];
    this.y = e[13];
    this.z = e[14];
    this.w = e[15];
    return this;
  }
  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    this.z = Math.min(this.z, v.z);
    this.w = Math.min(this.w, v.w);
    return this;
  }
  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    this.z = Math.max(this.z, v.z);
    this.w = Math.max(this.w, v.w);
    return this;
  }
  clamp(min, max) {
    // assumes min < max, componentwise

    this.x = Math.max(min.x, Math.min(max.x, this.x));
    this.y = Math.max(min.y, Math.min(max.y, this.y));
    this.z = Math.max(min.z, Math.min(max.z, this.z));
    this.w = Math.max(min.w, Math.min(max.w, this.w));
    return this;
  }
  clampScalar(minVal, maxVal) {
    this.x = Math.max(minVal, Math.min(maxVal, this.x));
    this.y = Math.max(minVal, Math.min(maxVal, this.y));
    this.z = Math.max(minVal, Math.min(maxVal, this.z));
    this.w = Math.max(minVal, Math.min(maxVal, this.w));
    return this;
  }
  clampLength(min, max) {
    const length = this.length();
    return this.divideScalar(length || 1).multiplyScalar(Math.max(min, Math.min(max, length)));
  }
  floor() {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);
    this.z = Math.floor(this.z);
    this.w = Math.floor(this.w);
    return this;
  }
  ceil() {
    this.x = Math.ceil(this.x);
    this.y = Math.ceil(this.y);
    this.z = Math.ceil(this.z);
    this.w = Math.ceil(this.w);
    return this;
  }
  round() {
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);
    this.z = Math.round(this.z);
    this.w = Math.round(this.w);
    return this;
  }
  roundToZero() {
    this.x = Math.trunc(this.x);
    this.y = Math.trunc(this.y);
    this.z = Math.trunc(this.z);
    this.w = Math.trunc(this.w);
    return this;
  }
  negate() {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    this.w = -this.w;
    return this;
  }
  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z + this.w * v.w;
  }
  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w;
  }
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
  }
  manhattanLength() {
    return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z) + Math.abs(this.w);
  }
  normalize() {
    return this.divideScalar(this.length() || 1);
  }
  setLength(length) {
    return this.normalize().multiplyScalar(length);
  }
  lerp(v, alpha) {
    this.x += (v.x - this.x) * alpha;
    this.y += (v.y - this.y) * alpha;
    this.z += (v.z - this.z) * alpha;
    this.w += (v.w - this.w) * alpha;
    return this;
  }
  lerpVectors(v1, v2, alpha) {
    this.x = v1.x + (v2.x - v1.x) * alpha;
    this.y = v1.y + (v2.y - v1.y) * alpha;
    this.z = v1.z + (v2.z - v1.z) * alpha;
    this.w = v1.w + (v2.w - v1.w) * alpha;
    return this;
  }
  equals(v) {
    return v.x === this.x && v.y === this.y && v.z === this.z && v.w === this.w;
  }
  fromArray(array, offset = 0) {
    this.x = array[offset];
    this.y = array[offset + 1];
    this.z = array[offset + 2];
    this.w = array[offset + 3];
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this.x;
    array[offset + 1] = this.y;
    array[offset + 2] = this.z;
    array[offset + 3] = this.w;
    return array;
  }
  fromBufferAttribute(attribute, index) {
    this.x = attribute.getX(index);
    this.y = attribute.getY(index);
    this.z = attribute.getZ(index);
    this.w = attribute.getW(index);
    return this;
  }
  random() {
    this.x = Math.random();
    this.y = Math.random();
    this.z = Math.random();
    this.w = Math.random();
    return this;
  }
  *[Symbol.iterator]() {
    yield this.x;
    yield this.y;
    yield this.z;
    yield this.w;
  }
}

function getCacheKey(object, force = false) {
  let cacheKey = '{';
  if (object.isNode === true) {
    cacheKey += object.id;
    object = object.getSelf();
  }
  for (const {
    property,
    childNode
  } of getNodeChildren(object)) {
    cacheKey += ',' + property.slice(0, -4) + ':' + childNode.getCacheKey(force);
  }
  cacheKey += '}';
  return cacheKey;
}
function* getNodeChildren(node, toJSON = false) {
  for (const property in node) {
    // Ignore private properties.
    if (property.startsWith('_') === true) continue;
    const object = node[property];
    if (Array.isArray(object) === true) {
      for (let i = 0; i < object.length; i++) {
        const child = object[i];
        if (child && (child.isNode === true || toJSON && typeof child.toJSON === 'function')) {
          yield {
            property,
            index: i,
            childNode: child
          };
        }
      }
    } else if (object && object.isNode === true) {
      yield {
        property,
        childNode: object
      };
    } else if (typeof object === 'object') {
      for (const subProperty in object) {
        const child = object[subProperty];
        if (child && (child.isNode === true || toJSON && typeof child.toJSON === 'function')) {
          yield {
            property,
            index: subProperty,
            childNode: child
          };
        }
      }
    }
  }
}
function getValueType(value) {
  if (value === undefined || value === null) return null;
  const typeOf = typeof value;
  if (value.isNode === true) {
    return 'node';
  } else if (typeOf === 'number') {
    return 'float';
  } else if (typeOf === 'boolean') {
    return 'bool';
  } else if (typeOf === 'string') {
    return 'string';
  } else if (typeOf === 'function') {
    return 'shader';
  } else if (value.isVector2 === true) {
    return 'vec2';
  } else if (value.isVector3 === true) {
    return 'vec3';
  } else if (value.isVector4 === true) {
    return 'vec4';
  } else if (value.isMatrix3 === true) {
    return 'mat3';
  } else if (value.isMatrix4 === true) {
    return 'mat4';
  } else if (value.isColor === true) {
    return 'color';
  } else if (value instanceof ArrayBuffer) {
    return 'ArrayBuffer';
  }
  return null;
}
function getValueFromType(type, ...params) {
  const last4 = type ? type.slice(-4) : undefined;
  if (params.length === 1) {
    // ensure same behaviour as in NodeBuilder.format()

    if (last4 === 'vec2') params = [params[0], params[0]];else if (last4 === 'vec3') params = [params[0], params[0], params[0]];else if (last4 === 'vec4') params = [params[0], params[0], params[0], params[0]];
  }
  if (type === 'color') {
    return new Color(...params);
  } else if (last4 === 'vec2') {
    return new Vector2(...params);
  } else if (last4 === 'vec3') {
    return new Vector3(...params);
  } else if (last4 === 'vec4') {
    return new Vector4(...params);
  } else if (last4 === 'mat3') {
    return new Matrix3(...params);
  } else if (last4 === 'mat4') {
    return new Matrix4(...params);
  } else if (type === 'bool') {
    return params[0] || false;
  } else if (type === 'float' || type === 'int' || type === 'uint') {
    return params[0] || 0;
  } else if (type === 'string') {
    return params[0] || '';
  } else if (type === 'ArrayBuffer') {
    return base64ToArrayBuffer(params[0]);
  }
  return null;
}
function arrayBufferToBase64(arrayBuffer) {
  let chars = '';
  const array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < array.length; i++) {
    chars += String.fromCharCode(array[i]);
  }
  return btoa(chars);
}
function base64ToArrayBuffer(base64) {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer;
}

/**
 * https://github.com/mrdoob/eventdispatcher.js/
 */

class EventDispatcher {
  addEventListener(type, listener) {
    if (this._listeners === undefined) this._listeners = {};
    const listeners = this._listeners;
    if (listeners[type] === undefined) {
      listeners[type] = [];
    }
    if (listeners[type].indexOf(listener) === -1) {
      listeners[type].push(listener);
    }
  }
  hasEventListener(type, listener) {
    if (this._listeners === undefined) return false;
    const listeners = this._listeners;
    return listeners[type] !== undefined && listeners[type].indexOf(listener) !== -1;
  }
  removeEventListener(type, listener) {
    if (this._listeners === undefined) return;
    const listeners = this._listeners;
    const listenerArray = listeners[type];
    if (listenerArray !== undefined) {
      const index = listenerArray.indexOf(listener);
      if (index !== -1) {
        listenerArray.splice(index, 1);
      }
    }
  }
  dispatchEvent(event) {
    if (this._listeners === undefined) return;
    const listeners = this._listeners;
    const listenerArray = listeners[event.type];
    if (listenerArray !== undefined) {
      event.target = this;

      // Make a copy, in case listeners are removed while iterating.
      const array = listenerArray.slice(0);
      for (let i = 0, l = array.length; i < l; i++) {
        array[i].call(this, event);
      }
      event.target = null;
    }
  }
}

const Nodes = new Map();
let _nodeId = 0;
class Node$1 extends EventDispatcher {
  constructor(nodeType = null) {
    super();
    this.nodeType = nodeType;
    this.updateType = NodeUpdateType.NONE;
    this.updateBeforeType = NodeUpdateType.NONE;
    this.updateAfterType = NodeUpdateType.NONE;
    this.uuid = MathUtils.generateUUID();
    this.version = 0;
    this._cacheKey = null;
    this._cacheKeyVersion = 0;
    this.global = false;
    this.isNode = true;
    Object.defineProperty(this, 'id', {
      value: _nodeId++
    });
  }
  set needsUpdate(value) {
    if (value === true) {
      this.version++;
    }
  }
  get type() {
    return this.constructor.type;
  }
  onUpdate(callback, updateType) {
    this.updateType = updateType;
    this.update = callback.bind(this.getSelf());
    return this;
  }
  onFrameUpdate(callback) {
    return this.onUpdate(callback, NodeUpdateType.FRAME);
  }
  onRenderUpdate(callback) {
    return this.onUpdate(callback, NodeUpdateType.RENDER);
  }
  onObjectUpdate(callback) {
    return this.onUpdate(callback, NodeUpdateType.OBJECT);
  }
  onReference(callback) {
    this.updateReference = callback.bind(this.getSelf());
    return this;
  }
  getSelf() {
    // Returns non-node object.

    return this.self || this;
  }
  updateReference( /*state*/
  ) {
    return this;
  }
  isGlobal( /*builder*/
  ) {
    return this.global;
  }
  *getChildren() {
    for (const {
      childNode
    } of getNodeChildren(this)) {
      yield childNode;
    }
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
  traverse(callback) {
    callback(this);
    for (const childNode of this.getChildren()) {
      childNode.traverse(callback);
    }
  }
  getCacheKey(force = false) {
    force = force || this.version !== this._cacheKeyVersion;
    if (force === true || this._cacheKey === null) {
      this._cacheKey = getCacheKey(this, force);
      this._cacheKeyVersion = this.version;
    }
    return this._cacheKey;
  }
  getScope() {
    return this;
  }
  getHash( /*builder*/
  ) {
    return this.uuid;
  }
  getUpdateType() {
    return this.updateType;
  }
  getUpdateBeforeType() {
    return this.updateBeforeType;
  }
  getUpdateAfterType() {
    return this.updateAfterType;
  }
  getElementType(builder) {
    const type = this.getNodeType(builder);
    const elementType = builder.getElementType(type);
    return elementType;
  }
  getNodeType(builder) {
    const nodeProperties = builder.getNodeProperties(this);
    if (nodeProperties.outputNode) {
      return nodeProperties.outputNode.getNodeType(builder);
    }
    return this.nodeType;
  }
  getShared(builder) {
    const hash = this.getHash(builder);
    const nodeFromHash = builder.getNodeFromHash(hash);
    return nodeFromHash || this;
  }
  setup(builder) {
    const nodeProperties = builder.getNodeProperties(this);
    let index = 0;
    for (const childNode of this.getChildren()) {
      nodeProperties['node' + index++] = childNode;
    }

    // return a outputNode if exists
    return null;
  }
  analyze(builder) {
    const usageCount = builder.increaseUsage(this);
    if (usageCount === 1) {
      // node flow children

      const nodeProperties = builder.getNodeProperties(this);
      for (const childNode of Object.values(nodeProperties)) {
        if (childNode && childNode.isNode === true) {
          childNode.build(builder);
        }
      }
    }
  }
  generate(builder, output) {
    const {
      outputNode
    } = builder.getNodeProperties(this);
    if (outputNode && outputNode.isNode === true) {
      return outputNode.build(builder, output);
    }
  }
  updateBefore( /*frame*/
  ) {
    console.warn('Abstract function.');
  }
  updateAfter( /*frame*/
  ) {
    console.warn('Abstract function.');
  }
  update( /*frame*/
  ) {
    console.warn('Abstract function.');
  }
  build(builder, output = null) {
    const refNode = this.getShared(builder);
    if (this !== refNode) {
      return refNode.build(builder, output);
    }
    builder.addNode(this);
    builder.addChain(this);

    /* Build stages expected results:
    	- "setup"		-> Node
    	- "analyze"		-> null
    	- "generate"	-> String
    */
    let result = null;
    const buildStage = builder.getBuildStage();
    if (buildStage === 'setup') {
      this.updateReference(builder);
      const properties = builder.getNodeProperties(this);
      if (properties.initialized !== true) {
        const stackNodesBeforeSetup = builder.stack.nodes.length;
        properties.initialized = true;
        properties.outputNode = this.setup(builder);
        if (properties.outputNode !== null && builder.stack.nodes.length !== stackNodesBeforeSetup) ;
        for (const childNode of Object.values(properties)) {
          if (childNode && childNode.isNode === true) {
            childNode.build(builder);
          }
        }
      }
    } else if (buildStage === 'analyze') {
      this.analyze(builder);
    } else if (buildStage === 'generate') {
      const isGenerateOnce = this.generate.length === 1;
      if (isGenerateOnce) {
        const type = this.getNodeType(builder);
        const nodeData = builder.getDataFromNode(this);
        result = nodeData.snippet;
        if (result === undefined) {
          result = this.generate(builder) || '';
          nodeData.snippet = result;
        }
        result = builder.format(result, type, output);
      } else {
        result = this.generate(builder, output) || '';
      }
    }
    builder.removeChain(this);
    return result;
  }
  getSerializeChildren() {
    return getNodeChildren(this);
  }
  serialize(json) {
    const nodeChildren = this.getSerializeChildren();
    const inputNodes = {};
    for (const {
      property,
      index,
      childNode
    } of nodeChildren) {
      if (index !== undefined) {
        if (inputNodes[property] === undefined) {
          inputNodes[property] = Number.isInteger(index) ? [] : {};
        }
        inputNodes[property][index] = childNode.toJSON(json.meta).uuid;
      } else {
        inputNodes[property] = childNode.toJSON(json.meta).uuid;
      }
    }
    if (Object.keys(inputNodes).length > 0) {
      json.inputNodes = inputNodes;
    }
  }
  deserialize(json) {
    if (json.inputNodes !== undefined) {
      const nodes = json.meta.nodes;
      for (const property in json.inputNodes) {
        if (Array.isArray(json.inputNodes[property])) {
          const inputArray = [];
          for (const uuid of json.inputNodes[property]) {
            inputArray.push(nodes[uuid]);
          }
          this[property] = inputArray;
        } else if (typeof json.inputNodes[property] === 'object') {
          const inputObject = {};
          for (const subProperty in json.inputNodes[property]) {
            const uuid = json.inputNodes[property][subProperty];
            inputObject[subProperty] = nodes[uuid];
          }
          this[property] = inputObject;
        } else {
          const uuid = json.inputNodes[property];
          this[property] = nodes[uuid];
        }
      }
    }
  }
  toJSON(meta) {
    const {
      uuid,
      type
    } = this;
    const isRoot = meta === undefined || typeof meta === 'string';
    if (isRoot) {
      meta = {
        textures: {},
        images: {},
        nodes: {}
      };
    }

    // serialize

    let data = meta.nodes[uuid];
    if (data === undefined) {
      data = {
        uuid,
        type,
        meta,
        metadata: {
          version: 4.6,
          type: 'Node',
          generator: 'Node.toJSON'
        }
      };
      if (isRoot !== true) meta.nodes[data.uuid] = data;
      this.serialize(data);
      delete data.meta;
    }

    // TODO: Copied from Object3D.toJSON

    function extractFromCache(cache) {
      const values = [];
      for (const key in cache) {
        const data = cache[key];
        delete data.metadata;
        values.push(data);
      }
      return values;
    }
    if (isRoot) {
      const textures = extractFromCache(meta.textures);
      const images = extractFromCache(meta.images);
      const nodes = extractFromCache(meta.nodes);
      if (textures.length > 0) data.textures = textures;
      if (images.length > 0) data.images = images;
      if (nodes.length > 0) data.nodes = nodes;
    }
    return data;
  }
}
Node$1.type = /*@__PURE__*/registerNode('', Node$1);
function registerNode(type, nodeClass) {
  const suffix = 'Node';
  const nodeType = type + suffix;
  if (typeof nodeClass !== 'function') throw new Error(`TSL.Node: Node class ${type} is not a class`);
  if (Nodes.has(nodeType)) {
    console.warn(`TSL.Node: Redefinition of node class ${nodeType}`);
    return;
  }
  if (type.slice(-suffix.length) === suffix) {
    console.warn(`TSL.Node: Node class ${nodeType} should not have '${suffix}' suffix.`);
    return;
  }
  Nodes.set(nodeType, nodeClass);
  nodeClass.type = nodeType;
  return nodeType;
}

class TempNode extends Node$1 {
  constructor(type) {
    super(type);
    this.isTempNode = true;
  }
  hasDependencies(builder) {
    return builder.getDataFromNode(this).usageCount > 1;
  }
  build(builder, output) {
    const buildStage = builder.getBuildStage();
    if (buildStage === 'generate') {
      const type = builder.getVectorType(this.getNodeType(builder, output));
      const nodeData = builder.getDataFromNode(this);
      if (nodeData.propertyName !== undefined) {
        return builder.format(nodeData.propertyName, type, output);
      } else if (type !== 'void' && output !== 'void' && this.hasDependencies(builder)) {
        const snippet = super.build(builder, type);
        const nodeVar = builder.getVarFromNode(this, null, type);
        const propertyName = builder.getPropertyName(nodeVar);
        builder.addLineFlowCode(`${propertyName} = ${snippet}`);
        nodeData.snippet = snippet;
        nodeData.propertyName = propertyName;
        return builder.format(nodeData.propertyName, type, output);
      }
    }
    return super.build(builder, output);
  }
}
TempNode.type = /*@__PURE__*/registerNode('Temp', TempNode);

class ArrayElementNode extends Node$1 {
  // @TODO: If extending from TempNode it breaks webgpu_compute

  constructor(node, indexNode) {
    super();
    this.node = node;
    this.indexNode = indexNode;
    this.isArrayElementNode = true;
  }
  getNodeType(builder) {
    return this.node.getElementType(builder);
  }
  generate(builder) {
    const nodeSnippet = this.node.build(builder);
    const indexSnippet = this.indexNode.build(builder, 'uint');
    return `${nodeSnippet}[ ${indexSnippet} ]`;
  }
}
ArrayElementNode.type = /*@__PURE__*/registerNode('ArrayElement', ArrayElementNode);

class ConvertNode extends Node$1 {
  constructor(node, convertTo) {
    super();
    this.node = node;
    this.convertTo = convertTo;
  }
  getNodeType(builder) {
    const requestType = this.node.getNodeType(builder);
    let convertTo = null;
    for (const overloadingType of this.convertTo.split('|')) {
      if (convertTo === null || builder.getTypeLength(requestType) === builder.getTypeLength(overloadingType)) {
        convertTo = overloadingType;
      }
    }
    return convertTo;
  }
  serialize(data) {
    super.serialize(data);
    data.convertTo = this.convertTo;
  }
  deserialize(data) {
    super.deserialize(data);
    this.convertTo = data.convertTo;
  }
  generate(builder, output) {
    const node = this.node;
    const type = this.getNodeType(builder);
    const snippet = node.build(builder, type);
    return builder.format(snippet, type, output);
  }
}
ConvertNode.type = /*@__PURE__*/registerNode('Convert', ConvertNode);

class JoinNode extends TempNode {
  constructor(nodes = [], nodeType = null) {
    super(nodeType);
    this.nodes = nodes;
  }
  getNodeType(builder) {
    if (this.nodeType !== null) {
      return builder.getVectorType(this.nodeType);
    }
    return builder.getTypeFromLength(this.nodes.reduce((count, cur) => count + builder.getTypeLength(cur.getNodeType(builder)), 0));
  }
  generate(builder, output) {
    const type = this.getNodeType(builder);
    const nodes = this.nodes;
    const primitiveType = builder.getComponentType(type);
    const snippetValues = [];
    for (const input of nodes) {
      let inputSnippet = input.build(builder);
      const inputPrimitiveType = builder.getComponentType(input.getNodeType(builder));
      if (inputPrimitiveType !== primitiveType) {
        inputSnippet = builder.format(inputSnippet, inputPrimitiveType, primitiveType);
      }
      snippetValues.push(inputSnippet);
    }
    const snippet = `${builder.getType(type)}( ${snippetValues.join(', ')} )`;
    return builder.format(snippet, type, output);
  }
}
JoinNode.type = /*@__PURE__*/registerNode('Join', JoinNode);

const stringVectorComponents = vectorComponents.join('');
class SplitNode extends Node$1 {
  constructor(node, components = 'x') {
    super();
    this.node = node;
    this.components = components;
    this.isSplitNode = true;
  }
  getVectorLength() {
    let vectorLength = this.components.length;
    for (const c of this.components) {
      vectorLength = Math.max(vectorComponents.indexOf(c) + 1, vectorLength);
    }
    return vectorLength;
  }
  getComponentType(builder) {
    return builder.getComponentType(this.node.getNodeType(builder));
  }
  getNodeType(builder) {
    return builder.getTypeFromLength(this.components.length, this.getComponentType(builder));
  }
  generate(builder, output) {
    const node = this.node;
    const nodeTypeLength = builder.getTypeLength(node.getNodeType(builder));
    let snippet = null;
    if (nodeTypeLength > 1) {
      let type = null;
      const componentsLength = this.getVectorLength();
      if (componentsLength >= nodeTypeLength) {
        // needed expand the input node

        type = builder.getTypeFromLength(this.getVectorLength(), this.getComponentType(builder));
      }
      const nodeSnippet = node.build(builder, type);
      if (this.components.length === nodeTypeLength && this.components === stringVectorComponents.slice(0, this.components.length)) {
        // unnecessary swizzle

        snippet = builder.format(nodeSnippet, type, output);
      } else {
        snippet = builder.format(`${nodeSnippet}.${this.components}`, this.getNodeType(builder), output);
      }
    } else {
      // ignore .components if .node returns float/integer

      snippet = node.build(builder, output);
    }
    return snippet;
  }
  serialize(data) {
    super.serialize(data);
    data.components = this.components;
  }
  deserialize(data) {
    super.deserialize(data);
    this.components = data.components;
  }
}
SplitNode.type = /*@__PURE__*/registerNode('Split', SplitNode);

class SetNode extends TempNode {
  constructor(sourceNode, components, targetNode) {
    super();
    this.sourceNode = sourceNode;
    this.components = components;
    this.targetNode = targetNode;
  }
  getNodeType(builder) {
    return this.sourceNode.getNodeType(builder);
  }
  generate(builder) {
    const {
      sourceNode,
      components,
      targetNode
    } = this;
    const sourceType = this.getNodeType(builder);
    const targetType = builder.getTypeFromLength(components.length);
    const targetSnippet = targetNode.build(builder, targetType);
    const sourceSnippet = sourceNode.build(builder, sourceType);
    const length = builder.getTypeLength(sourceType);
    const snippetValues = [];
    for (let i = 0; i < length; i++) {
      const component = vectorComponents[i];
      if (component === components[0]) {
        snippetValues.push(targetSnippet);
        i += components.length - 1;
      } else {
        snippetValues.push(sourceSnippet + '.' + component);
      }
    }
    return `${builder.getType(sourceType)}( ${snippetValues.join(', ')} )`;
  }
}
SetNode.type = /*@__PURE__*/registerNode('Set', SetNode);

class FlipNode extends TempNode {
  constructor(sourceNode, components) {
    super();
    this.sourceNode = sourceNode;
    this.components = components;
  }
  getNodeType(builder) {
    return this.sourceNode.getNodeType(builder);
  }
  generate(builder) {
    const {
      components,
      sourceNode
    } = this;
    const sourceType = this.getNodeType(builder);
    const sourceSnippet = sourceNode.build(builder);
    const sourceCache = builder.getVarFromNode(this);
    const sourceProperty = builder.getPropertyName(sourceCache);
    builder.addLineFlowCode(sourceProperty + ' = ' + sourceSnippet);
    const length = builder.getTypeLength(sourceType);
    const snippetValues = [];
    let componentIndex = 0;
    for (let i = 0; i < length; i++) {
      const component = vectorComponents[i];
      if (component === components[componentIndex]) {
        snippetValues.push('1.0 - ' + (sourceProperty + '.' + component));
        componentIndex++;
      } else {
        snippetValues.push(sourceProperty + '.' + component);
      }
    }
    return `${builder.getType(sourceType)}( ${snippetValues.join(', ')} )`;
  }
}
FlipNode.type = /*@__PURE__*/registerNode('Flip', FlipNode);

class InputNode extends Node$1 {
  constructor(value, nodeType = null) {
    super(nodeType);
    this.isInputNode = true;
    this.value = value;
    this.precision = null;
  }
  getNodeType( /*builder*/
  ) {
    if (this.nodeType === null) {
      return getValueType(this.value);
    }
    return this.nodeType;
  }
  getInputType(builder) {
    return this.getNodeType(builder);
  }
  setPrecision(precision) {
    this.precision = precision;
    return this;
  }
  serialize(data) {
    super.serialize(data);
    data.value = this.value;
    if (this.value && this.value.toArray) data.value = this.value.toArray();
    data.valueType = getValueType(this.value);
    data.nodeType = this.nodeType;
    if (data.valueType === 'ArrayBuffer') data.value = arrayBufferToBase64(data.value);
    data.precision = this.precision;
  }
  deserialize(data) {
    super.deserialize(data);
    this.nodeType = data.nodeType;
    this.value = Array.isArray(data.value) ? getValueFromType(data.valueType, ...data.value) : data.value;
    this.precision = data.precision || null;
    if (this.value && this.value.fromArray) this.value = this.value.fromArray(data.value);
  }
  generate( /*builder, output*/
  ) {
    console.warn('Abstract function.');
  }
}
InputNode.type = /*@__PURE__*/registerNode('Input', InputNode);

class ConstNode extends InputNode {
  constructor(value, nodeType = null) {
    super(value, nodeType);
    this.isConstNode = true;
  }
  generateConst(builder) {
    return builder.generateConst(this.getNodeType(builder), this.value);
  }
  generate(builder, output) {
    const type = this.getNodeType(builder);
    return builder.format(this.generateConst(builder), type, output);
  }
}
ConstNode.type = /*@__PURE__*/registerNode('Const', ConstNode);

//

let currentStack = null;
const NodeElements = new Map();
function addMethodChaining(name, nodeElement) {
  if (NodeElements.has(name)) {
    console.warn(`Redefinition of method chaining ${name}`);
    return;
  }
  if (typeof nodeElement !== 'function') throw new Error(`Node element ${name} is not a function`);
  NodeElements.set(name, nodeElement);
}
const parseSwizzle = props => props.replace(/r|s/g, 'x').replace(/g|t/g, 'y').replace(/b|p/g, 'z').replace(/a|q/g, 'w');
const parseSwizzleAndSort = props => parseSwizzle(props).split('').sort().join('');
const shaderNodeHandler = {
  setup(NodeClosure, params) {
    const inputs = params.shift();
    return NodeClosure(nodeObjects(inputs), ...params);
  },
  get(node, prop, nodeObj) {
    if (typeof prop === 'string' && node[prop] === undefined) {
      if (node.isStackNode !== true && prop === 'assign') {
        return (...params) => {
          currentStack.assign(nodeObj, ...params);
          return nodeObj;
        };
      } else if (NodeElements.has(prop)) {
        const nodeElement = NodeElements.get(prop);
        return node.isStackNode ? (...params) => nodeObj.add(nodeElement(...params)) : (...params) => nodeElement(nodeObj, ...params);
      } else if (prop === 'self') {
        return node;
      } else if (prop.endsWith('Assign') && NodeElements.has(prop.slice(0, prop.length - 'Assign'.length))) {
        const nodeElement = NodeElements.get(prop.slice(0, prop.length - 'Assign'.length));
        return node.isStackNode ? (...params) => nodeObj.assign(params[0], nodeElement(...params)) : (...params) => nodeObj.assign(nodeElement(nodeObj, ...params));
      } else if (/^[xyzwrgbastpq]{1,4}$/.test(prop) === true) {
        // accessing properties ( swizzle )

        prop = parseSwizzle(prop);
        return nodeObject(new SplitNode(nodeObj, prop));
      } else if (/^set[XYZWRGBASTPQ]{1,4}$/.test(prop) === true) {
        // set properties ( swizzle ) and sort to xyzw sequence

        prop = parseSwizzleAndSort(prop.slice(3).toLowerCase());
        return value => nodeObject(new SetNode(node, prop, value));
      } else if (/^flip[XYZWRGBASTPQ]{1,4}$/.test(prop) === true) {
        // set properties ( swizzle ) and sort to xyzw sequence

        prop = parseSwizzleAndSort(prop.slice(4).toLowerCase());
        return () => nodeObject(new FlipNode(nodeObject(node), prop));
      } else if (prop === 'width' || prop === 'height' || prop === 'depth') {
        // accessing property

        if (prop === 'width') prop = 'x';else if (prop === 'height') prop = 'y';else if (prop === 'depth') prop = 'z';
        return nodeObject(new SplitNode(node, prop));
      } else if (/^\d+$/.test(prop) === true) {
        // accessing array

        return nodeObject(new ArrayElementNode(nodeObj, new ConstNode(Number(prop), 'uint')));
      }
    }
    return Reflect.get(node, prop, nodeObj);
  },
  set(node, prop, value, nodeObj) {
    if (typeof prop === 'string' && node[prop] === undefined) {
      // setting properties

      if (/^[xyzwrgbastpq]{1,4}$/.test(prop) === true || prop === 'width' || prop === 'height' || prop === 'depth' || /^\d+$/.test(prop) === true) {
        nodeObj[prop].assign(value);
        return true;
      }
    }
    return Reflect.set(node, prop, value, nodeObj);
  }
};
const nodeObjectsCacheMap = new WeakMap();
const nodeBuilderFunctionsCacheMap = new WeakMap();
const ShaderNodeObject = function (obj, altType = null) {
  const type = getValueType(obj);
  if (type === 'node') {
    let nodeObject = nodeObjectsCacheMap.get(obj);
    if (nodeObject === undefined) {
      nodeObject = new Proxy(obj, shaderNodeHandler);
      nodeObjectsCacheMap.set(obj, nodeObject);
      nodeObjectsCacheMap.set(nodeObject, nodeObject);
    }
    return nodeObject;
  } else if (altType === null && (type === 'float' || type === 'boolean') || type && type !== 'shader' && type !== 'string') {
    return nodeObject(getConstNode(obj, altType));
  } else if (type === 'shader') {
    return Fn(obj);
  }
  return obj;
};
const ShaderNodeObjects = function (objects, altType = null) {
  for (const name in objects) {
    objects[name] = nodeObject(objects[name], altType);
  }
  return objects;
};
const ShaderNodeArray = function (array, altType = null) {
  const len = array.length;
  for (let i = 0; i < len; i++) {
    array[i] = nodeObject(array[i], altType);
  }
  return array;
};
const ShaderNodeProxy = function (NodeClass, scope = null, factor = null, settings = null) {
  const assignNode = node => nodeObject(settings !== null ? Object.assign(node, settings) : node);
  if (scope === null) {
    return (...params) => {
      return assignNode(new NodeClass(...nodeArray(params)));
    };
  } else if (factor !== null) {
    factor = nodeObject(factor);
    return (...params) => {
      return assignNode(new NodeClass(scope, ...nodeArray(params), factor));
    };
  } else {
    return (...params) => {
      return assignNode(new NodeClass(scope, ...nodeArray(params)));
    };
  }
};
const ShaderNodeImmutable = function (NodeClass, ...params) {
  return nodeObject(new NodeClass(...nodeArray(params)));
};
class ShaderCallNodeInternal extends Node$1 {
  constructor(shaderNode, inputNodes) {
    super();
    this.shaderNode = shaderNode;
    this.inputNodes = inputNodes;
  }
  getNodeType(builder) {
    return this.shaderNode.nodeType || this.getOutputNode(builder).getNodeType(builder);
  }
  call(builder) {
    const {
      shaderNode,
      inputNodes
    } = this;
    const properties = builder.getNodeProperties(shaderNode);
    if (properties.onceOutput) return properties.onceOutput;

    //

    let result = null;
    if (shaderNode.layout) {
      let functionNodesCacheMap = nodeBuilderFunctionsCacheMap.get(builder.constructor);
      if (functionNodesCacheMap === undefined) {
        functionNodesCacheMap = new WeakMap();
        nodeBuilderFunctionsCacheMap.set(builder.constructor, functionNodesCacheMap);
      }
      let functionNode = functionNodesCacheMap.get(shaderNode);
      if (functionNode === undefined) {
        functionNode = nodeObject(builder.buildFunctionNode(shaderNode));
        functionNodesCacheMap.set(shaderNode, functionNode);
      }
      if (builder.currentFunctionNode !== null) {
        builder.currentFunctionNode.includes.push(functionNode);
      }
      result = nodeObject(functionNode.call(inputNodes));
    } else {
      const jsFunc = shaderNode.jsFunc;
      const outputNode = inputNodes !== null ? jsFunc(inputNodes, builder) : jsFunc(builder);
      result = nodeObject(outputNode);
    }
    if (shaderNode.once) {
      properties.onceOutput = result;
    }
    return result;
  }
  getOutputNode(builder) {
    const properties = builder.getNodeProperties(this);
    if (properties.outputNode === null) {
      properties.outputNode = this.setupOutput(builder);
    }
    return properties.outputNode;
  }
  setup(builder) {
    return this.getOutputNode(builder);
  }
  setupOutput(builder) {
    builder.addStack();
    builder.stack.outputNode = this.call(builder);
    return builder.removeStack();
  }
  generate(builder, output) {
    const outputNode = this.getOutputNode(builder);
    return outputNode.build(builder, output);
  }
}
class ShaderNodeInternal extends Node$1 {
  constructor(jsFunc, nodeType) {
    super(nodeType);
    this.jsFunc = jsFunc;
    this.layout = null;
    this.global = true;
    this.once = false;
  }
  setLayout(layout) {
    this.layout = layout;
    return this;
  }
  call(inputs = null) {
    nodeObjects(inputs);
    return nodeObject(new ShaderCallNodeInternal(this, inputs));
  }
  setup() {
    return this.call();
  }
}
const bools = [false, true];
const uints = [0, 1, 2, 3];
const ints = [-1, -2];
const floats = [0.5, 1.5, 1 / 3, 1e-6, 1e6, Math.PI, Math.PI * 2, 1 / Math.PI, 2 / Math.PI, 1 / (Math.PI * 2), Math.PI / 2];
const boolsCacheMap = new Map();
for (const bool of bools) boolsCacheMap.set(bool, new ConstNode(bool));
const uintsCacheMap = new Map();
for (const uint of uints) uintsCacheMap.set(uint, new ConstNode(uint, 'uint'));
const intsCacheMap = new Map([...uintsCacheMap].map(el => new ConstNode(el.value, 'int')));
for (const int of ints) intsCacheMap.set(int, new ConstNode(int, 'int'));
const floatsCacheMap = new Map([...intsCacheMap].map(el => new ConstNode(el.value)));
for (const float of floats) floatsCacheMap.set(float, new ConstNode(float));
for (const float of floats) floatsCacheMap.set(-float, new ConstNode(-float));
const cacheMaps = {
  bool: boolsCacheMap,
  uint: uintsCacheMap,
  ints: intsCacheMap,
  float: floatsCacheMap
};
const constNodesCacheMap = new Map([...boolsCacheMap, ...floatsCacheMap]);
const getConstNode = (value, type) => {
  if (constNodesCacheMap.has(value)) {
    return constNodesCacheMap.get(value);
  } else if (value.isNode === true) {
    return value;
  } else {
    return new ConstNode(value, type);
  }
};
const safeGetNodeType = node => {
  try {
    return node.getNodeType();
  } catch (_) {
    return undefined;
  }
};
const ConvertType = function (type, cacheMap = null) {
  return (...params) => {
    if (params.length === 0 || !['bool', 'float', 'int', 'uint'].includes(type) && params.every(param => typeof param !== 'object')) {
      params = [getValueFromType(type, ...params)];
    }
    if (params.length === 1 && cacheMap !== null && cacheMap.has(params[0])) {
      return nodeObject(cacheMap.get(params[0]));
    }
    if (params.length === 1) {
      const node = getConstNode(params[0], type);
      if (safeGetNodeType(node) === type) return nodeObject(node);
      return nodeObject(new ConvertNode(node, type));
    }
    const nodes = params.map(param => getConstNode(param));
    return nodeObject(new JoinNode(nodes, type));
  };
};

// utils

const getConstNodeType = value => value !== undefined && value !== null ? value.nodeType || value.convertTo || (typeof value === 'string' ? value : null) : null;

// shader node base

function ShaderNode(jsFunc, nodeType) {
  return new Proxy(new ShaderNodeInternal(jsFunc, nodeType), shaderNodeHandler);
}
const nodeObject = (val, altType = null) => /* new */ShaderNodeObject(val, altType);
const nodeObjects = (val, altType = null) => new ShaderNodeObjects(val, altType);
const nodeArray = (val, altType = null) => new ShaderNodeArray(val, altType);
const nodeProxy = (...params) => new ShaderNodeProxy(...params);
const nodeImmutable = (...params) => new ShaderNodeImmutable(...params);
const Fn = (jsFunc, nodeType) => {
  const shaderNode = new ShaderNode(jsFunc, nodeType);
  const fn = (...params) => {
    let inputs;
    nodeObjects(params);
    if (params[0] && params[0].isNode) {
      inputs = [...params];
    } else {
      inputs = params[0];
    }
    return shaderNode.call(inputs);
  };
  fn.shaderNode = shaderNode;
  fn.setLayout = layout => {
    shaderNode.setLayout(layout);
    return fn;
  };
  fn.once = () => {
    shaderNode.once = true;
    return fn;
  };
  return fn;
};

//

addMethodChaining('toGlobal', node => {
  node.global = true;
  return node;
});

//

const setCurrentStack = stack => {
  currentStack = stack;
};
const getCurrentStack = () => currentStack;
const If = (...params) => currentStack.If(...params);
function append(node) {
  if (currentStack) currentStack.add(node);
  return node;
}
addMethodChaining('append', append);

// types

const color = new ConvertType('color');
const float = new ConvertType('float', cacheMaps.float);
const int$1 = new ConvertType('int', cacheMaps.ints);
const uint = new ConvertType('uint', cacheMaps.uint);
const bool = new ConvertType('bool', cacheMaps.bool);
const vec2 = new ConvertType('vec2');
const ivec2 = new ConvertType('ivec2');
const uvec2 = new ConvertType('uvec2');
const bvec2 = new ConvertType('bvec2');
const vec3 = new ConvertType('vec3');
const ivec3 = new ConvertType('ivec3');
const uvec3 = new ConvertType('uvec3');
const bvec3 = new ConvertType('bvec3');
const vec4 = new ConvertType('vec4');
const ivec4 = new ConvertType('ivec4');
const uvec4 = new ConvertType('uvec4');
const bvec4 = new ConvertType('bvec4');
const mat2 = new ConvertType('mat2');
const mat3 = new ConvertType('mat3');
const mat4 = new ConvertType('mat4');
addMethodChaining('toColor', color);
addMethodChaining('toFloat', float);
addMethodChaining('toInt', int$1);
addMethodChaining('toUint', uint);
addMethodChaining('toBool', bool);
addMethodChaining('toVec2', vec2);
addMethodChaining('toIVec2', ivec2);
addMethodChaining('toUVec2', uvec2);
addMethodChaining('toBVec2', bvec2);
addMethodChaining('toVec3', vec3);
addMethodChaining('toIVec3', ivec3);
addMethodChaining('toUVec3', uvec3);
addMethodChaining('toBVec3', bvec3);
addMethodChaining('toVec4', vec4);
addMethodChaining('toIVec4', ivec4);
addMethodChaining('toUVec4', uvec4);
addMethodChaining('toBVec4', bvec4);
addMethodChaining('toMat2', mat2);
addMethodChaining('toMat3', mat3);
addMethodChaining('toMat4', mat4);

// basic nodes

const element = /*@__PURE__*/nodeProxy(ArrayElementNode);
const convert = (node, types) => nodeObject(new ConvertNode(nodeObject(node), types));
addMethodChaining('element', element);
addMethodChaining('convert', convert);

class AssignNode extends TempNode {
  constructor(targetNode, sourceNode) {
    super();
    this.targetNode = targetNode;
    this.sourceNode = sourceNode;
  }
  hasDependencies() {
    return false;
  }
  getNodeType(builder, output) {
    return output !== 'void' ? this.targetNode.getNodeType(builder) : 'void';
  }
  needsSplitAssign(builder) {
    const {
      targetNode
    } = this;
    if (builder.isAvailable('swizzleAssign') === false && targetNode.isSplitNode && targetNode.components.length > 1) {
      const targetLength = builder.getTypeLength(targetNode.node.getNodeType(builder));
      const assignDiferentVector = vectorComponents.join('').slice(0, targetLength) !== targetNode.components;
      return assignDiferentVector;
    }
    return false;
  }
  generate(builder, output) {
    const {
      targetNode,
      sourceNode
    } = this;
    const needsSplitAssign = this.needsSplitAssign(builder);
    const targetType = targetNode.getNodeType(builder);
    const target = targetNode.context({
      assign: true
    }).build(builder);
    const source = sourceNode.build(builder, targetType);
    const sourceType = sourceNode.getNodeType(builder);
    const nodeData = builder.getDataFromNode(this);

    //

    let snippet;
    if (nodeData.initialized === true) {
      if (output !== 'void') {
        snippet = target;
      }
    } else if (needsSplitAssign) {
      const sourceVar = builder.getVarFromNode(this, null, targetType);
      const sourceProperty = builder.getPropertyName(sourceVar);
      builder.addLineFlowCode(`${sourceProperty} = ${source}`);
      const targetRoot = targetNode.node.context({
        assign: true
      }).build(builder);
      for (let i = 0; i < targetNode.components.length; i++) {
        const component = targetNode.components[i];
        builder.addLineFlowCode(`${targetRoot}.${component} = ${sourceProperty}[ ${i} ]`);
      }
      if (output !== 'void') {
        snippet = target;
      }
    } else {
      snippet = `${target} = ${source}`;
      if (output === 'void' || sourceType === 'void') {
        builder.addLineFlowCode(snippet);
        if (output !== 'void') {
          snippet = target;
        }
      }
    }
    nodeData.initialized = true;
    return builder.format(snippet, targetType, output);
  }
}
AssignNode.type = /*@__PURE__*/registerNode('Assign', AssignNode);
const assign = /*@__PURE__*/nodeProxy(AssignNode);
addMethodChaining('assign', assign);

class UniformGroupNode extends Node$1 {
  constructor(name, shared = false) {
    super('string');
    this.name = name;
    this.version = 0;
    this.shared = shared;
    this.isUniformGroup = true;
  }
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  serialize(data) {
    super.serialize(data);
    data.name = this.name;
    data.version = this.version;
    data.shared = this.shared;
  }
  deserialize(data) {
    super.deserialize(data);
    this.name = data.name;
    this.version = data.version;
    this.shared = data.shared;
  }
}
UniformGroupNode.type = /*@__PURE__*/registerNode('UniformGroup', UniformGroupNode);
const uniformGroup = name => new UniformGroupNode(name);
const sharedUniformGroup = name => new UniformGroupNode(name, true);
const objectGroup = /*@__PURE__*/uniformGroup('object');

class UniformNode extends InputNode {
  constructor(value, nodeType = null) {
    super(value, nodeType);
    this.isUniformNode = true;
    this.name = '';
    this.groupNode = objectGroup;
  }
  label(name) {
    this.name = name;
    return this;
  }
  setGroup(group) {
    this.groupNode = group;
    return this;
  }
  getGroup() {
    return this.groupNode;
  }
  getUniformHash(builder) {
    return this.getHash(builder);
  }
  onUpdate(callback, updateType) {
    const self = this.getSelf();
    callback = callback.bind(self);
    return super.onUpdate(frame => {
      const value = callback(frame, self);
      if (value !== undefined) {
        this.value = value;
      }
    }, updateType);
  }
  generate(builder, output) {
    const type = this.getNodeType(builder);
    const hash = this.getUniformHash(builder);
    let sharedNode = builder.getNodeFromHash(hash);
    if (sharedNode === undefined) {
      builder.setHashNode(this, hash);
      sharedNode = this;
    }
    const sharedNodeType = sharedNode.getInputType(builder);
    const nodeUniform = builder.getUniformFromNode(sharedNode, sharedNodeType, builder.shaderStage, this.name || builder.context.label);
    const propertyName = builder.getPropertyName(nodeUniform);
    if (builder.context.label !== undefined) delete builder.context.label;
    return builder.format(propertyName, type, output);
  }
}
UniformNode.type = /*@__PURE__*/registerNode('Uniform', UniformNode);
const uniform = (arg1, arg2) => {
  const nodeType = getConstNodeType(arg2 || arg1);

  // @TODO: get ConstNode from .traverse() in the future
  const value = arg1 && arg1.isNode === true ? arg1.node && arg1.node.value || arg1.value : arg1;
  return nodeObject(new UniformNode(value, nodeType));
};

class PropertyNode extends Node$1 {
  constructor(nodeType, name = null, varying = false) {
    super(nodeType);
    this.name = name;
    this.varying = varying;
    this.isPropertyNode = true;
  }
  getHash(builder) {
    return this.name || super.getHash(builder);
  }
  isGlobal( /*builder*/
  ) {
    return true;
  }
  generate(builder) {
    let nodeVar;
    if (this.varying === true) {
      nodeVar = builder.getVaryingFromNode(this, this.name);
      nodeVar.needsInterpolation = true;
    } else {
      nodeVar = builder.getVarFromNode(this, this.name);
    }
    return builder.getPropertyName(nodeVar);
  }
}
PropertyNode.type = /*@__PURE__*/registerNode('Property', PropertyNode);
const property = (type, name) => nodeObject(new PropertyNode(type, name));
const varyingProperty = (type, name) => nodeObject(new PropertyNode(type, name, true));
const diffuseColor = /*@__PURE__*/nodeImmutable(PropertyNode, 'vec4', 'DiffuseColor');
const emissive = /*@__PURE__*/nodeImmutable(PropertyNode, 'vec3', 'EmissiveColor');
const output = /*@__PURE__*/nodeImmutable(PropertyNode, 'vec4', 'Output');

class FunctionCallNode extends TempNode {
  constructor(functionNode = null, parameters = {}) {
    super();
    this.functionNode = functionNode;
    this.parameters = parameters;
  }
  setParameters(parameters) {
    this.parameters = parameters;
    return this;
  }
  getParameters() {
    return this.parameters;
  }
  getNodeType(builder) {
    return this.functionNode.getNodeType(builder);
  }
  generate(builder) {
    const params = [];
    const functionNode = this.functionNode;
    const inputs = functionNode.getInputs(builder);
    const parameters = this.parameters;
    if (Array.isArray(parameters)) {
      for (let i = 0; i < parameters.length; i++) {
        const inputNode = inputs[i];
        const node = parameters[i];
        params.push(node.build(builder, inputNode.type));
      }
    } else {
      for (const inputNode of inputs) {
        const node = parameters[inputNode.name];
        if (node !== undefined) {
          params.push(node.build(builder, inputNode.type));
        } else {
          throw new Error(`FunctionCallNode: Input '${inputNode.name}' not found in FunctionNode.`);
        }
      }
    }
    const functionName = functionNode.build(builder, 'property');
    return `${functionName}( ${params.join(', ')} )`;
  }
}
FunctionCallNode.type = /*@__PURE__*/registerNode('FunctionCall', FunctionCallNode);
const call = (func, ...params) => {
  params = params.length > 1 || params[0] && params[0].isNode === true ? nodeArray(params) : nodeObjects(params[0]);
  return nodeObject(new FunctionCallNode(nodeObject(func), params));
};
addMethodChaining('call', call);

class OperatorNode extends TempNode {
  constructor(op, aNode, bNode, ...params) {
    super();
    if (params.length > 0) {
      let finalOp = new OperatorNode(op, aNode, bNode);
      for (let i = 0; i < params.length - 1; i++) {
        finalOp = new OperatorNode(op, finalOp, params[i]);
      }
      aNode = finalOp;
      bNode = params[params.length - 1];
    }
    this.op = op;
    this.aNode = aNode;
    this.bNode = bNode;
  }
  getNodeType(builder, output) {
    const op = this.op;
    const aNode = this.aNode;
    const bNode = this.bNode;
    const typeA = aNode.getNodeType(builder);
    const typeB = typeof bNode !== 'undefined' ? bNode.getNodeType(builder) : null;
    if (typeA === 'void' || typeB === 'void') {
      return 'void';
    } else if (op === '%') {
      return typeA;
    } else if (op === '~' || op === '&' || op === '|' || op === '^' || op === '>>' || op === '<<') {
      return builder.getIntegerType(typeA);
    } else if (op === '!' || op === '==' || op === '&&' || op === '||' || op === '^^') {
      return 'bool';
    } else if (op === '<' || op === '>' || op === '<=' || op === '>=') {
      const typeLength = output ? builder.getTypeLength(output) : Math.max(builder.getTypeLength(typeA), builder.getTypeLength(typeB));
      return typeLength > 1 ? `bvec${typeLength}` : 'bool';
    } else {
      if (typeA === 'float' && builder.isMatrix(typeB)) {
        return typeB;
      } else if (builder.isMatrix(typeA) && builder.isVector(typeB)) {
        // matrix x vector

        return builder.getVectorFromMatrix(typeA);
      } else if (builder.isVector(typeA) && builder.isMatrix(typeB)) {
        // vector x matrix

        return builder.getVectorFromMatrix(typeB);
      } else if (builder.getTypeLength(typeB) > builder.getTypeLength(typeA)) {
        // anytype x anytype: use the greater length vector

        return typeB;
      }
      return typeA;
    }
  }
  generate(builder, output) {
    const op = this.op;
    const aNode = this.aNode;
    const bNode = this.bNode;
    const type = this.getNodeType(builder, output);
    let typeA = null;
    let typeB = null;
    if (type !== 'void') {
      typeA = aNode.getNodeType(builder);
      typeB = typeof bNode !== 'undefined' ? bNode.getNodeType(builder) : null;
      if (op === '<' || op === '>' || op === '<=' || op === '>=' || op === '==') {
        if (builder.isVector(typeA)) {
          typeB = typeA;
        } else if (typeA !== typeB) {
          typeA = typeB = 'float';
        }
      } else if (op === '>>' || op === '<<') {
        typeA = type;
        typeB = builder.changeComponentType(typeB, 'uint');
      } else if (builder.isMatrix(typeA) && builder.isVector(typeB)) {
        // matrix x vector

        typeB = builder.getVectorFromMatrix(typeA);
      } else if (builder.isVector(typeA) && builder.isMatrix(typeB)) {
        // vector x matrix

        typeA = builder.getVectorFromMatrix(typeB);
      } else {
        // anytype x anytype

        typeA = typeB = type;
      }
    } else {
      typeA = typeB = type;
    }
    const a = aNode.build(builder, typeA);
    const b = typeof bNode !== 'undefined' ? bNode.build(builder, typeB) : null;
    const outputLength = builder.getTypeLength(output);
    const fnOpSnippet = builder.getFunctionOperator(op);
    if (output !== 'void') {
      if (op === '<' && outputLength > 1) {
        if (builder.useComparisonMethod) {
          return builder.format(`${builder.getMethod('lessThan', output)}( ${a}, ${b} )`, type, output);
        } else {
          return builder.format(`( ${a} < ${b} )`, type, output);
        }
      } else if (op === '<=' && outputLength > 1) {
        if (builder.useComparisonMethod) {
          return builder.format(`${builder.getMethod('lessThanEqual', output)}( ${a}, ${b} )`, type, output);
        } else {
          return builder.format(`( ${a} <= ${b} )`, type, output);
        }
      } else if (op === '>' && outputLength > 1) {
        if (builder.useComparisonMethod) {
          return builder.format(`${builder.getMethod('greaterThan', output)}( ${a}, ${b} )`, type, output);
        } else {
          return builder.format(`( ${a} > ${b} )`, type, output);
        }
      } else if (op === '>=' && outputLength > 1) {
        if (builder.useComparisonMethod) {
          return builder.format(`${builder.getMethod('greaterThanEqual', output)}( ${a}, ${b} )`, type, output);
        } else {
          return builder.format(`( ${a} >= ${b} )`, type, output);
        }
      } else if (op === '!' || op === '~') {
        return builder.format(`(${op}${a})`, typeA, output);
      } else if (fnOpSnippet) {
        return builder.format(`${fnOpSnippet}( ${a}, ${b} )`, type, output);
      } else {
        return builder.format(`( ${a} ${op} ${b} )`, type, output);
      }
    } else if (typeA !== 'void') {
      if (fnOpSnippet) {
        return builder.format(`${fnOpSnippet}( ${a}, ${b} )`, type, output);
      } else {
        return builder.format(`${a} ${op} ${b}`, type, output);
      }
    }
  }
  serialize(data) {
    super.serialize(data);
    data.op = this.op;
  }
  deserialize(data) {
    super.deserialize(data);
    this.op = data.op;
  }
}
OperatorNode.type = /*@__PURE__*/registerNode('Operator', OperatorNode);
const add = /*@__PURE__*/nodeProxy(OperatorNode, '+');
const sub = /*@__PURE__*/nodeProxy(OperatorNode, '-');
const mul = /*@__PURE__*/nodeProxy(OperatorNode, '*');
const div = /*@__PURE__*/nodeProxy(OperatorNode, '/');
const modInt = /*@__PURE__*/nodeProxy(OperatorNode, '%');
const equal = /*@__PURE__*/nodeProxy(OperatorNode, '==');
const notEqual = /*@__PURE__*/nodeProxy(OperatorNode, '!=');
const lessThan = /*@__PURE__*/nodeProxy(OperatorNode, '<');
const greaterThan = /*@__PURE__*/nodeProxy(OperatorNode, '>');
const lessThanEqual = /*@__PURE__*/nodeProxy(OperatorNode, '<=');
const greaterThanEqual = /*@__PURE__*/nodeProxy(OperatorNode, '>=');
const and = /*@__PURE__*/nodeProxy(OperatorNode, '&&');
const or = /*@__PURE__*/nodeProxy(OperatorNode, '||');
const not = /*@__PURE__*/nodeProxy(OperatorNode, '!');
const xor = /*@__PURE__*/nodeProxy(OperatorNode, '^^');
const bitAnd = /*@__PURE__*/nodeProxy(OperatorNode, '&');
const bitNot = /*@__PURE__*/nodeProxy(OperatorNode, '~');
const bitOr = /*@__PURE__*/nodeProxy(OperatorNode, '|');
const bitXor = /*@__PURE__*/nodeProxy(OperatorNode, '^');
const shiftLeft = /*@__PURE__*/nodeProxy(OperatorNode, '<<');
const shiftRight = /*@__PURE__*/nodeProxy(OperatorNode, '>>');
addMethodChaining('add', add);
addMethodChaining('sub', sub);
addMethodChaining('mul', mul);
addMethodChaining('div', div);
addMethodChaining('modInt', modInt);
addMethodChaining('equal', equal);
addMethodChaining('notEqual', notEqual);
addMethodChaining('lessThan', lessThan);
addMethodChaining('greaterThan', greaterThan);
addMethodChaining('lessThanEqual', lessThanEqual);
addMethodChaining('greaterThanEqual', greaterThanEqual);
addMethodChaining('and', and);
addMethodChaining('or', or);
addMethodChaining('not', not);
addMethodChaining('xor', xor);
addMethodChaining('bitAnd', bitAnd);
addMethodChaining('bitNot', bitNot);
addMethodChaining('bitOr', bitOr);
addMethodChaining('bitXor', bitXor);
addMethodChaining('shiftLeft', shiftLeft);
addMethodChaining('shiftRight', shiftRight);
const remainder = (...params) => {
  // @deprecated, r168

  console.warn('TSL.OperatorNode: .remainder() has been renamed to .modInt().');
  return modInt(...params);
};
addMethodChaining('remainder', remainder);

class MathNode extends TempNode {
  constructor(method, aNode, bNode = null, cNode = null) {
    super();
    this.method = method;
    this.aNode = aNode;
    this.bNode = bNode;
    this.cNode = cNode;
  }
  getInputType(builder) {
    const aType = this.aNode.getNodeType(builder);
    const bType = this.bNode ? this.bNode.getNodeType(builder) : null;
    const cType = this.cNode ? this.cNode.getNodeType(builder) : null;
    const aLen = builder.isMatrix(aType) ? 0 : builder.getTypeLength(aType);
    const bLen = builder.isMatrix(bType) ? 0 : builder.getTypeLength(bType);
    const cLen = builder.isMatrix(cType) ? 0 : builder.getTypeLength(cType);
    if (aLen > bLen && aLen > cLen) {
      return aType;
    } else if (bLen > cLen) {
      return bType;
    } else if (cLen > aLen) {
      return cType;
    }
    return aType;
  }
  getNodeType(builder) {
    const method = this.method;
    if (method === MathNode.LENGTH || method === MathNode.DISTANCE || method === MathNode.DOT) {
      return 'float';
    } else if (method === MathNode.CROSS) {
      return 'vec3';
    } else if (method === MathNode.ALL) {
      return 'bool';
    } else if (method === MathNode.EQUALS) {
      return builder.changeComponentType(this.aNode.getNodeType(builder), 'bool');
    } else if (method === MathNode.MOD) {
      return this.aNode.getNodeType(builder);
    } else {
      return this.getInputType(builder);
    }
  }
  generate(builder, output) {
    const method = this.method;
    const type = this.getNodeType(builder);
    const inputType = this.getInputType(builder);
    const a = this.aNode;
    const b = this.bNode;
    const c = this.cNode;
    const isWebGL = builder.renderer.isWebGLRenderer === true;
    if (method === MathNode.TRANSFORM_DIRECTION) {
      // dir can be either a direction vector or a normal vector
      // upper-left 3x3 of matrix is assumed to be orthogonal

      let tA = a;
      let tB = b;
      if (builder.isMatrix(tA.getNodeType(builder))) {
        tB = vec4(vec3(tB), 0.0);
      } else {
        tA = vec4(vec3(tA), 0.0);
      }
      const mulNode = mul(tA, tB).xyz;
      return normalize(mulNode).build(builder, output);
    } else if (method === MathNode.NEGATE) {
      return builder.format('( - ' + a.build(builder, inputType) + ' )', type, output);
    } else if (method === MathNode.ONE_MINUS) {
      return sub(1.0, a).build(builder, output);
    } else if (method === MathNode.RECIPROCAL) {
      return div(1.0, a).build(builder, output);
    } else if (method === MathNode.DIFFERENCE) {
      return abs(sub(a, b)).build(builder, output);
    } else {
      const params = [];
      if (method === MathNode.CROSS || method === MathNode.MOD) {
        params.push(a.build(builder, type), b.build(builder, type));
      } else if (isWebGL && method === MathNode.STEP) {
        params.push(a.build(builder, builder.getTypeLength(a.getNodeType(builder)) === 1 ? 'float' : inputType), b.build(builder, inputType));
      } else if (isWebGL && (method === MathNode.MIN || method === MathNode.MAX) || method === MathNode.MOD) {
        params.push(a.build(builder, inputType), b.build(builder, builder.getTypeLength(b.getNodeType(builder)) === 1 ? 'float' : inputType));
      } else if (method === MathNode.REFRACT) {
        params.push(a.build(builder, inputType), b.build(builder, inputType), c.build(builder, 'float'));
      } else if (method === MathNode.MIX) {
        params.push(a.build(builder, inputType), b.build(builder, inputType), c.build(builder, builder.getTypeLength(c.getNodeType(builder)) === 1 ? 'float' : inputType));
      } else {
        params.push(a.build(builder, inputType));
        if (b !== null) params.push(b.build(builder, inputType));
        if (c !== null) params.push(c.build(builder, inputType));
      }
      return builder.format(`${builder.getMethod(method, type)}( ${params.join(', ')} )`, type, output);
    }
  }
  serialize(data) {
    super.serialize(data);
    data.method = this.method;
  }
  deserialize(data) {
    super.deserialize(data);
    this.method = data.method;
  }
}

// 1 input

MathNode.ALL = 'all';
MathNode.ANY = 'any';
MathNode.EQUALS = 'equals';
MathNode.RADIANS = 'radians';
MathNode.DEGREES = 'degrees';
MathNode.EXP = 'exp';
MathNode.EXP2 = 'exp2';
MathNode.LOG = 'log';
MathNode.LOG2 = 'log2';
MathNode.SQRT = 'sqrt';
MathNode.INVERSE_SQRT = 'inversesqrt';
MathNode.FLOOR = 'floor';
MathNode.CEIL = 'ceil';
MathNode.NORMALIZE = 'normalize';
MathNode.FRACT = 'fract';
MathNode.SIN = 'sin';
MathNode.COS = 'cos';
MathNode.TAN = 'tan';
MathNode.ASIN = 'asin';
MathNode.ACOS = 'acos';
MathNode.ATAN = 'atan';
MathNode.ABS = 'abs';
MathNode.SIGN = 'sign';
MathNode.LENGTH = 'length';
MathNode.NEGATE = 'negate';
MathNode.ONE_MINUS = 'oneMinus';
MathNode.DFDX = 'dFdx';
MathNode.DFDY = 'dFdy';
MathNode.ROUND = 'round';
MathNode.RECIPROCAL = 'reciprocal';
MathNode.TRUNC = 'trunc';
MathNode.FWIDTH = 'fwidth';
MathNode.BITCAST = 'bitcast';
MathNode.TRANSPOSE = 'transpose';

// 2 inputs

MathNode.ATAN2 = 'atan2';
MathNode.MIN = 'min';
MathNode.MAX = 'max';
MathNode.MOD = 'mod';
MathNode.STEP = 'step';
MathNode.REFLECT = 'reflect';
MathNode.DISTANCE = 'distance';
MathNode.DIFFERENCE = 'difference';
MathNode.DOT = 'dot';
MathNode.CROSS = 'cross';
MathNode.POW = 'pow';
MathNode.TRANSFORM_DIRECTION = 'transformDirection';

// 3 inputs

MathNode.MIX = 'mix';
MathNode.CLAMP = 'clamp';
MathNode.REFRACT = 'refract';
MathNode.SMOOTHSTEP = 'smoothstep';
MathNode.FACEFORWARD = 'faceforward';
MathNode.type = /*@__PURE__*/registerNode('Math', MathNode);
const PI = /*@__PURE__*/float(Math.PI);
const all = /*@__PURE__*/nodeProxy(MathNode, MathNode.ALL);
const any = /*@__PURE__*/nodeProxy(MathNode, MathNode.ANY);
const equals = /*@__PURE__*/nodeProxy(MathNode, MathNode.EQUALS);
const radians = /*@__PURE__*/nodeProxy(MathNode, MathNode.RADIANS);
const degrees = /*@__PURE__*/nodeProxy(MathNode, MathNode.DEGREES);
const exp = /*@__PURE__*/nodeProxy(MathNode, MathNode.EXP);
const exp2 = /*@__PURE__*/nodeProxy(MathNode, MathNode.EXP2);
const log = /*@__PURE__*/nodeProxy(MathNode, MathNode.LOG);
const log2 = /*@__PURE__*/nodeProxy(MathNode, MathNode.LOG2);
const sqrt = /*@__PURE__*/nodeProxy(MathNode, MathNode.SQRT);
const inverseSqrt = /*@__PURE__*/nodeProxy(MathNode, MathNode.INVERSE_SQRT);
const floor = /*@__PURE__*/nodeProxy(MathNode, MathNode.FLOOR);
const ceil = /*@__PURE__*/nodeProxy(MathNode, MathNode.CEIL);
const normalize = /*@__PURE__*/nodeProxy(MathNode, MathNode.NORMALIZE);
const fract = /*@__PURE__*/nodeProxy(MathNode, MathNode.FRACT);
const sin = /*@__PURE__*/nodeProxy(MathNode, MathNode.SIN);
const cos = /*@__PURE__*/nodeProxy(MathNode, MathNode.COS);
const tan = /*@__PURE__*/nodeProxy(MathNode, MathNode.TAN);
const asin = /*@__PURE__*/nodeProxy(MathNode, MathNode.ASIN);
const acos = /*@__PURE__*/nodeProxy(MathNode, MathNode.ACOS);
const atan = /*@__PURE__*/nodeProxy(MathNode, MathNode.ATAN);
const abs = /*@__PURE__*/nodeProxy(MathNode, MathNode.ABS);
const sign = /*@__PURE__*/nodeProxy(MathNode, MathNode.SIGN);
const length = /*@__PURE__*/nodeProxy(MathNode, MathNode.LENGTH);
const negate = /*@__PURE__*/nodeProxy(MathNode, MathNode.NEGATE);
const oneMinus = /*@__PURE__*/nodeProxy(MathNode, MathNode.ONE_MINUS);
const dFdx = /*@__PURE__*/nodeProxy(MathNode, MathNode.DFDX);
const dFdy = /*@__PURE__*/nodeProxy(MathNode, MathNode.DFDY);
const round = /*@__PURE__*/nodeProxy(MathNode, MathNode.ROUND);
const reciprocal = /*@__PURE__*/nodeProxy(MathNode, MathNode.RECIPROCAL);
const trunc = /*@__PURE__*/nodeProxy(MathNode, MathNode.TRUNC);
const fwidth = /*@__PURE__*/nodeProxy(MathNode, MathNode.FWIDTH);
/*@__PURE__*/nodeProxy(MathNode, MathNode.BITCAST);
const transpose = /*@__PURE__*/nodeProxy(MathNode, MathNode.TRANSPOSE);
const atan2 = /*@__PURE__*/nodeProxy(MathNode, MathNode.ATAN2);
const min$1 = /*@__PURE__*/nodeProxy(MathNode, MathNode.MIN);
const max$1 = /*@__PURE__*/nodeProxy(MathNode, MathNode.MAX);
const mod = /*@__PURE__*/nodeProxy(MathNode, MathNode.MOD);
const step = /*@__PURE__*/nodeProxy(MathNode, MathNode.STEP);
const reflect = /*@__PURE__*/nodeProxy(MathNode, MathNode.REFLECT);
const distance = /*@__PURE__*/nodeProxy(MathNode, MathNode.DISTANCE);
const difference = /*@__PURE__*/nodeProxy(MathNode, MathNode.DIFFERENCE);
const dot = /*@__PURE__*/nodeProxy(MathNode, MathNode.DOT);
const cross = /*@__PURE__*/nodeProxy(MathNode, MathNode.CROSS);
const pow = /*@__PURE__*/nodeProxy(MathNode, MathNode.POW);
const pow2 = /*@__PURE__*/nodeProxy(MathNode, MathNode.POW, 2);
const pow3 = /*@__PURE__*/nodeProxy(MathNode, MathNode.POW, 3);
const pow4 = /*@__PURE__*/nodeProxy(MathNode, MathNode.POW, 4);
const transformDirection = /*@__PURE__*/nodeProxy(MathNode, MathNode.TRANSFORM_DIRECTION);
const cbrt = a => mul(sign(a), pow(abs(a), 1.0 / 3.0));
const lengthSq = a => dot(a, a);
const mix = /*@__PURE__*/nodeProxy(MathNode, MathNode.MIX);
const clamp = (value, low = 0, high = 1) => nodeObject(new MathNode(MathNode.CLAMP, nodeObject(value), nodeObject(low), nodeObject(high)));
const saturate = value => clamp(value);
const refract = /*@__PURE__*/nodeProxy(MathNode, MathNode.REFRACT);
const smoothstep = /*@__PURE__*/nodeProxy(MathNode, MathNode.SMOOTHSTEP);
const faceForward = /*@__PURE__*/nodeProxy(MathNode, MathNode.FACEFORWARD);
const rand = /*@__PURE__*/Fn(([uv]) => {
  const a = 12.9898,
    b = 78.233,
    c = 43758.5453;
  const dt = dot(uv.xy, vec2(a, b)),
    sn = mod(dt, PI);
  return fract(sin(sn).mul(c));
});
const mixElement = (t, e1, e2) => mix(e1, e2, t);
const smoothstepElement = (x, low, high) => smoothstep(low, high, x);
addMethodChaining('all', all);
addMethodChaining('any', any);
addMethodChaining('equals', equals);
addMethodChaining('radians', radians);
addMethodChaining('degrees', degrees);
addMethodChaining('exp', exp);
addMethodChaining('exp2', exp2);
addMethodChaining('log', log);
addMethodChaining('log2', log2);
addMethodChaining('sqrt', sqrt);
addMethodChaining('inverseSqrt', inverseSqrt);
addMethodChaining('floor', floor);
addMethodChaining('ceil', ceil);
addMethodChaining('normalize', normalize);
addMethodChaining('fract', fract);
addMethodChaining('sin', sin);
addMethodChaining('cos', cos);
addMethodChaining('tan', tan);
addMethodChaining('asin', asin);
addMethodChaining('acos', acos);
addMethodChaining('atan', atan);
addMethodChaining('abs', abs);
addMethodChaining('sign', sign);
addMethodChaining('length', length);
addMethodChaining('lengthSq', lengthSq);
addMethodChaining('negate', negate);
addMethodChaining('oneMinus', oneMinus);
addMethodChaining('dFdx', dFdx);
addMethodChaining('dFdy', dFdy);
addMethodChaining('round', round);
addMethodChaining('reciprocal', reciprocal);
addMethodChaining('trunc', trunc);
addMethodChaining('fwidth', fwidth);
addMethodChaining('atan2', atan2);
addMethodChaining('min', min$1);
addMethodChaining('max', max$1);
addMethodChaining('mod', mod);
addMethodChaining('step', step);
addMethodChaining('reflect', reflect);
addMethodChaining('distance', distance);
addMethodChaining('dot', dot);
addMethodChaining('cross', cross);
addMethodChaining('pow', pow);
addMethodChaining('pow2', pow2);
addMethodChaining('pow3', pow3);
addMethodChaining('pow4', pow4);
addMethodChaining('transformDirection', transformDirection);
addMethodChaining('mix', mixElement);
addMethodChaining('clamp', clamp);
addMethodChaining('refract', refract);
addMethodChaining('smoothstep', smoothstepElement);
addMethodChaining('faceForward', faceForward);
addMethodChaining('difference', difference);
addMethodChaining('saturate', saturate);
addMethodChaining('cbrt', cbrt);
addMethodChaining('transpose', transpose);
addMethodChaining('rand', rand);

class ConditionalNode extends Node$1 {
  constructor(condNode, ifNode, elseNode = null) {
    super();
    this.condNode = condNode;
    this.ifNode = ifNode;
    this.elseNode = elseNode;
  }
  getNodeType(builder) {
    const ifType = this.ifNode.getNodeType(builder);
    if (this.elseNode !== null) {
      const elseType = this.elseNode.getNodeType(builder);
      if (builder.getTypeLength(elseType) > builder.getTypeLength(ifType)) {
        return elseType;
      }
    }
    return ifType;
  }
  setup(builder) {
    const properties = builder.getNodeProperties(this);
    properties.condNode = this.condNode.cache();
    properties.ifNode = this.ifNode.cache();
    properties.elseNode = this.elseNode ? this.elseNode.cache() : null;
  }
  generate(builder, output) {
    const type = this.getNodeType(builder);
    const nodeData = builder.getDataFromNode(this);
    if (nodeData.nodeProperty !== undefined) {
      return nodeData.nodeProperty;
    }
    const {
      condNode,
      ifNode,
      elseNode
    } = builder.getNodeProperties(this);
    const needsOutput = output !== 'void';
    const nodeProperty = needsOutput ? property(type).build(builder) : '';
    nodeData.nodeProperty = nodeProperty;
    const nodeSnippet = condNode.build(builder, 'bool');
    builder.addFlowCode(`\n${builder.tab}if ( ${nodeSnippet} ) {\n\n`).addFlowTab();
    let ifSnippet = ifNode.build(builder, type);
    if (ifSnippet) {
      if (needsOutput) {
        ifSnippet = nodeProperty + ' = ' + ifSnippet + ';';
      } else {
        ifSnippet = 'return ' + ifSnippet + ';';
      }
    }
    builder.removeFlowTab().addFlowCode(builder.tab + '\t' + ifSnippet + '\n\n' + builder.tab + '}');
    if (elseNode !== null) {
      builder.addFlowCode(' else {\n\n').addFlowTab();
      let elseSnippet = elseNode.build(builder, type);
      if (elseSnippet) {
        if (needsOutput) {
          elseSnippet = nodeProperty + ' = ' + elseSnippet + ';';
        } else {
          elseSnippet = 'return ' + elseSnippet + ';';
        }
      }
      builder.removeFlowTab().addFlowCode(builder.tab + '\t' + elseSnippet + '\n\n' + builder.tab + '}\n\n');
    } else {
      builder.addFlowCode('\n\n');
    }
    return builder.format(nodeProperty, type, output);
  }
}
ConditionalNode.type = /*@__PURE__*/registerNode('Conditional', ConditionalNode);
const select = /*@__PURE__*/nodeProxy(ConditionalNode);
addMethodChaining('select', select);

//

const cond = (...params) => {
  // @deprecated, r168

  console.warn('TSL.ConditionalNode: cond() has been renamed to select().');
  return select(...params);
};
addMethodChaining('cond', cond);

class ContextNode extends Node$1 {
  constructor(node, value = {}) {
    super();
    this.isContextNode = true;
    this.node = node;
    this.value = value;
  }
  getScope() {
    return this.node.getScope();
  }
  getNodeType(builder) {
    return this.node.getNodeType(builder);
  }
  analyze(builder) {
    this.node.build(builder);
  }
  setup(builder) {
    const previousContext = builder.getContext();
    builder.setContext({
      ...builder.context,
      ...this.value
    });
    const node = this.node.build(builder);
    builder.setContext(previousContext);
    return node;
  }
  generate(builder, output) {
    const previousContext = builder.getContext();
    builder.setContext({
      ...builder.context,
      ...this.value
    });
    const snippet = this.node.build(builder, output);
    builder.setContext(previousContext);
    return snippet;
  }
}
ContextNode.type = /*@__PURE__*/registerNode('Context', ContextNode);
const context = /*@__PURE__*/nodeProxy(ContextNode);
const label = (node, name) => context(node, {
  label: name
});
addMethodChaining('context', context);
addMethodChaining('label', label);

class VarNode extends Node$1 {
  constructor(node, name = null) {
    super();
    this.node = node;
    this.name = name;
    this.global = true;
    this.isVarNode = true;
  }
  getHash(builder) {
    return this.name || super.getHash(builder);
  }
  getNodeType(builder) {
    return this.node.getNodeType(builder);
  }
  generate(builder) {
    const {
      node,
      name
    } = this;
    const nodeVar = builder.getVarFromNode(this, name, builder.getVectorType(this.getNodeType(builder)));
    const propertyName = builder.getPropertyName(nodeVar);
    const snippet = node.build(builder, nodeVar.type);
    builder.addLineFlowCode(`${propertyName} = ${snippet}`);
    return propertyName;
  }
}
VarNode.type = /*@__PURE__*/registerNode('Var', VarNode);
const temp = /*@__PURE__*/nodeProxy(VarNode);
addMethodChaining('temp', temp); // @TODO: Will be removed in the future
addMethodChaining('toVar', (...params) => temp(...params).append());

class VaryingNode extends Node$1 {
  constructor(node, name = null) {
    super();
    this.node = node;
    this.name = name;
    this.isVaryingNode = true;
  }
  isGlobal() {
    return true;
  }
  getHash(builder) {
    return this.name || super.getHash(builder);
  }
  getNodeType(builder) {
    // VaryingNode is auto type

    return this.node.getNodeType(builder);
  }
  setupVarying(builder) {
    const properties = builder.getNodeProperties(this);
    let varying = properties.varying;
    if (varying === undefined) {
      const name = this.name;
      const type = this.getNodeType(builder);
      properties.varying = varying = builder.getVaryingFromNode(this, name, type);
      properties.node = this.node;
    }

    // this property can be used to check if the varying can be optimized for a variable
    varying.needsInterpolation || (varying.needsInterpolation = builder.shaderStage === 'fragment');
    return varying;
  }
  setup(builder) {
    this.setupVarying(builder);
  }
  analyze(builder) {
    this.setupVarying(builder);
    return this.node.analyze(builder);
  }
  generate(builder) {
    const properties = builder.getNodeProperties(this);
    const varying = this.setupVarying(builder);
    if (properties.propertyName === undefined) {
      const type = this.getNodeType(builder);
      const propertyName = builder.getPropertyName(varying, NodeShaderStage.VERTEX);

      // force node run in vertex stage
      builder.flowNodeFromShaderStage(NodeShaderStage.VERTEX, this.node, type, propertyName);
      properties.propertyName = propertyName;
    }
    return builder.getPropertyName(varying);
  }
}
VaryingNode.type = /*@__PURE__*/registerNode('Varying', VaryingNode);
const varying = /*@__PURE__*/nodeProxy(VaryingNode);
addMethodChaining('varying', varying);

const getColorSpaceName = colorSpace => {
  let method = null;
  if (colorSpace === LinearSRGBColorSpace) {
    method = 'Linear';
  } else if (colorSpace === SRGBColorSpace) {
    method = 'sRGB';
  }
  return method;
};
const getColorSpaceMethod = (source, target) => {
  return getColorSpaceName(source) + 'To' + getColorSpaceName(target);
};
class ColorSpaceNode extends TempNode {
  constructor(colorNode, target = null, source = null) {
    super('vec4');
    this.colorNode = colorNode;
    this.target = target;
    this.source = source;
  }
  setup(builder) {
    const {
      renderer,
      context
    } = builder;
    const source = this.source || context.outputColorSpace || renderer.outputColorSpace;
    const target = this.target || context.outputColorSpace || renderer.outputColorSpace;
    const colorNode = this.colorNode;
    if (source === target) return colorNode;
    const colorSpace = getColorSpaceMethod(source, target);
    let outputNode = null;
    const colorSpaceFn = renderer.nodes.library.getColorSpaceFunction(colorSpace);
    if (colorSpaceFn !== null) {
      outputNode = vec4(colorSpaceFn(colorNode.rgb), colorNode.a);
    } else {
      console.error('ColorSpaceNode: Unsupported Color Space configuration.', colorSpace);
      outputNode = colorNode;
    }
    return outputNode;
  }
}
ColorSpaceNode.type = /*@__PURE__*/registerNode('ColorSpace', ColorSpaceNode);
const toOutputColorSpace = (node, colorSpace = null) => nodeObject(new ColorSpaceNode(nodeObject(node), colorSpace, LinearSRGBColorSpace));
const toWorkingColorSpace = (node, colorSpace = null) => nodeObject(new ColorSpaceNode(nodeObject(node), LinearSRGBColorSpace, colorSpace));
addMethodChaining('toOutputColorSpace', toOutputColorSpace);
addMethodChaining('toWorkingColorSpace', toWorkingColorSpace);

class ReferenceElementNode$1 extends ArrayElementNode {
  constructor(referenceNode, indexNode) {
    super(referenceNode, indexNode);
    this.referenceNode = referenceNode;
    this.isReferenceElementNode = true;
  }
  getNodeType() {
    return this.referenceNode.uniformType;
  }
  generate(builder) {
    const snippet = super.generate(builder);
    const arrayType = this.referenceNode.getNodeType();
    const elementType = this.getNodeType();
    return builder.format(snippet, arrayType, elementType);
  }
}
class ReferenceBaseNode extends Node$1 {
  constructor(property, uniformType, object = null, count = null) {
    super();
    this.property = property;
    this.uniformType = uniformType;
    this.object = object;
    this.count = count;
    this.properties = property.split('.');
    this.reference = object;
    this.node = null;
    this.updateType = NodeUpdateType.OBJECT;
  }
  element(indexNode) {
    return nodeObject(new ReferenceElementNode$1(this, nodeObject(indexNode)));
  }
  setNodeType(uniformType) {
    this.node = uniform(null, uniformType).getSelf();
  }
  getNodeType(builder) {
    if (this.node === null) {
      this.updateValue();
    }
    return this.node.getNodeType(builder);
  }
  getValueFromReference(object = this.reference) {
    const {
      properties
    } = this;
    let value = object[properties[0]];
    for (let i = 1; i < properties.length; i++) {
      value = value[properties[i]];
    }
    return value;
  }
  updateReference(state) {
    this.reference = this.object !== null ? this.object : state.object;
    return this.reference;
  }
  setup() {
    this.updateValue();
    return this.node;
  }
  update( /*frame*/
  ) {
    this.updateValue();
  }
  updateValue() {
    if (this.node === null) this.setNodeType(this.uniformType);
    const value = this.getValueFromReference();
    if (Array.isArray(value)) {
      this.node.array = value;
    } else {
      this.node.value = value;
    }
  }
}
ReferenceBaseNode.type = /*@__PURE__*/registerNode('ReferenceBase', ReferenceBaseNode);

class RendererReferenceNode extends ReferenceBaseNode {
  constructor(property, inputType, renderer = null) {
    super(property, inputType, renderer);
    this.renderer = renderer;
  }
  updateReference(state) {
    this.reference = this.renderer !== null ? this.renderer : state.renderer;
    return this.reference;
  }
}
RendererReferenceNode.type = /*@__PURE__*/registerNode('RendererReference', RendererReferenceNode);
const rendererReference = (name, type, renderer) => nodeObject(new RendererReferenceNode(name, type, renderer));

class ToneMappingNode extends TempNode {
  constructor(toneMapping, exposureNode = toneMappingExposure, colorNode = null) {
    super('vec3');
    this.toneMapping = toneMapping;
    this.exposureNode = exposureNode;
    this.colorNode = colorNode;
  }
  getCacheKey() {
    let cacheKey = super.getCacheKey();
    cacheKey = '{toneMapping:' + this.toneMapping + ',nodes:' + cacheKey + '}';
    return cacheKey;
  }
  setup(builder) {
    const colorNode = this.colorNode || builder.context.color;
    const toneMapping = this.toneMapping;
    if (toneMapping === NoToneMapping) return colorNode;
    let outputNode = null;
    const toneMappingFn = builder.renderer.nodes.library.getToneMappingFunction(toneMapping);
    if (toneMappingFn !== null) {
      outputNode = vec4(toneMappingFn(colorNode.rgb, this.exposureNode), colorNode.a);
    } else {
      console.error('ToneMappingNode: Unsupported Tone Mapping configuration.', toneMapping);
      outputNode = colorNode;
    }
    return outputNode;
  }
}
ToneMappingNode.type = /*@__PURE__*/registerNode('ToneMapping', ToneMappingNode);
const toneMapping = (mapping, exposure, color) => nodeObject(new ToneMappingNode(mapping, nodeObject(exposure), nodeObject(color)));
const toneMappingExposure = /*@__PURE__*/rendererReference('toneMappingExposure', 'float');
addMethodChaining('toneMapping', (color, mapping, exposure) => toneMapping(mapping, exposure, color));

function arrayNeedsUint32(array) {
  // assumes larger values usually on last

  for (let i = array.length - 1; i >= 0; --i) {
    if (array[i] >= 65535) return true; // account for PRIMITIVE_RESTART_FIXED_INDEX, #24565
  }
  return false;
}
function createElementNS(name) {
  return document.createElementNS('http://www.w3.org/1999/xhtml', name);
}
const _cache$1 = {};
function warnOnce(message) {
  if (message in _cache$1) return;
  _cache$1[message] = true;
  console.warn(message);
}

const _vector$4 = /*@__PURE__*/new Vector3();
const _vector2$1 = /*@__PURE__*/new Vector2();
class BufferAttribute {
  constructor(array, itemSize, normalized = false) {
    if (Array.isArray(array)) {
      throw new TypeError('THREE.BufferAttribute: array should be a Typed Array.');
    }
    this.isBufferAttribute = true;
    this.name = '';
    this.array = array;
    this.itemSize = itemSize;
    this.count = array !== undefined ? array.length / itemSize : 0;
    this.normalized = normalized;
    this.usage = StaticDrawUsage;
    this._updateRange = {
      offset: 0,
      count: -1
    };
    this.updateRanges = [];
    this.gpuType = FloatType;
    this.version = 0;
  }
  onUploadCallback() {}
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  get updateRange() {
    warnOnce('THREE.BufferAttribute: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead.'); // @deprecated, r159
    return this._updateRange;
  }
  setUsage(value) {
    this.usage = value;
    return this;
  }
  addUpdateRange(start, count) {
    this.updateRanges.push({
      start,
      count
    });
  }
  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }
  copy(source) {
    this.name = source.name;
    this.array = new source.array.constructor(source.array);
    this.itemSize = source.itemSize;
    this.count = source.count;
    this.normalized = source.normalized;
    this.usage = source.usage;
    this.gpuType = source.gpuType;
    return this;
  }
  copyAt(index1, attribute, index2) {
    index1 *= this.itemSize;
    index2 *= attribute.itemSize;
    for (let i = 0, l = this.itemSize; i < l; i++) {
      this.array[index1 + i] = attribute.array[index2 + i];
    }
    return this;
  }
  copyArray(array) {
    this.array.set(array);
    return this;
  }
  applyMatrix3(m) {
    if (this.itemSize === 2) {
      for (let i = 0, l = this.count; i < l; i++) {
        _vector2$1.fromBufferAttribute(this, i);
        _vector2$1.applyMatrix3(m);
        this.setXY(i, _vector2$1.x, _vector2$1.y);
      }
    } else if (this.itemSize === 3) {
      for (let i = 0, l = this.count; i < l; i++) {
        _vector$4.fromBufferAttribute(this, i);
        _vector$4.applyMatrix3(m);
        this.setXYZ(i, _vector$4.x, _vector$4.y, _vector$4.z);
      }
    }
    return this;
  }
  applyMatrix4(m) {
    for (let i = 0, l = this.count; i < l; i++) {
      _vector$4.fromBufferAttribute(this, i);
      _vector$4.applyMatrix4(m);
      this.setXYZ(i, _vector$4.x, _vector$4.y, _vector$4.z);
    }
    return this;
  }
  applyNormalMatrix(m) {
    for (let i = 0, l = this.count; i < l; i++) {
      _vector$4.fromBufferAttribute(this, i);
      _vector$4.applyNormalMatrix(m);
      this.setXYZ(i, _vector$4.x, _vector$4.y, _vector$4.z);
    }
    return this;
  }
  transformDirection(m) {
    for (let i = 0, l = this.count; i < l; i++) {
      _vector$4.fromBufferAttribute(this, i);
      _vector$4.transformDirection(m);
      this.setXYZ(i, _vector$4.x, _vector$4.y, _vector$4.z);
    }
    return this;
  }
  set(value, offset = 0) {
    // Matching BufferAttribute constructor, do not normalize the array.
    this.array.set(value, offset);
    return this;
  }
  getComponent(index, component) {
    let value = this.array[index * this.itemSize + component];
    if (this.normalized) value = denormalize(value, this.array);
    return value;
  }
  setComponent(index, component, value) {
    if (this.normalized) value = normalize$1(value, this.array);
    this.array[index * this.itemSize + component] = value;
    return this;
  }
  getX(index) {
    let x = this.array[index * this.itemSize];
    if (this.normalized) x = denormalize(x, this.array);
    return x;
  }
  setX(index, x) {
    if (this.normalized) x = normalize$1(x, this.array);
    this.array[index * this.itemSize] = x;
    return this;
  }
  getY(index) {
    let y = this.array[index * this.itemSize + 1];
    if (this.normalized) y = denormalize(y, this.array);
    return y;
  }
  setY(index, y) {
    if (this.normalized) y = normalize$1(y, this.array);
    this.array[index * this.itemSize + 1] = y;
    return this;
  }
  getZ(index) {
    let z = this.array[index * this.itemSize + 2];
    if (this.normalized) z = denormalize(z, this.array);
    return z;
  }
  setZ(index, z) {
    if (this.normalized) z = normalize$1(z, this.array);
    this.array[index * this.itemSize + 2] = z;
    return this;
  }
  getW(index) {
    let w = this.array[index * this.itemSize + 3];
    if (this.normalized) w = denormalize(w, this.array);
    return w;
  }
  setW(index, w) {
    if (this.normalized) w = normalize$1(w, this.array);
    this.array[index * this.itemSize + 3] = w;
    return this;
  }
  setXY(index, x, y) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    return this;
  }
  setXYZ(index, x, y, z) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
      z = normalize$1(z, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    this.array[index + 2] = z;
    return this;
  }
  setXYZW(index, x, y, z, w) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
      z = normalize$1(z, this.array);
      w = normalize$1(w, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    this.array[index + 2] = z;
    this.array[index + 3] = w;
    return this;
  }
  onUpload(callback) {
    this.onUploadCallback = callback;
    return this;
  }
  clone() {
    return new this.constructor(this.array, this.itemSize).copy(this);
  }
  toJSON() {
    const data = {
      itemSize: this.itemSize,
      type: this.array.constructor.name,
      array: Array.from(this.array),
      normalized: this.normalized
    };
    if (this.name !== '') data.name = this.name;
    if (this.usage !== StaticDrawUsage) data.usage = this.usage;
    return data;
  }
}
class Uint16BufferAttribute extends BufferAttribute {
  constructor(array, itemSize, normalized) {
    super(new Uint16Array(array), itemSize, normalized);
  }
}
class Uint32BufferAttribute extends BufferAttribute {
  constructor(array, itemSize, normalized) {
    super(new Uint32Array(array), itemSize, normalized);
  }
}
class Float32BufferAttribute extends BufferAttribute {
  constructor(array, itemSize, normalized) {
    super(new Float32Array(array), itemSize, normalized);
  }
}

const _vector$3 = /*@__PURE__*/new Vector3();
class InterleavedBufferAttribute {
  constructor(interleavedBuffer, itemSize, offset, normalized = false) {
    this.isInterleavedBufferAttribute = true;
    this.name = '';
    this.data = interleavedBuffer;
    this.itemSize = itemSize;
    this.offset = offset;
    this.normalized = normalized;
  }
  get count() {
    return this.data.count;
  }
  get array() {
    return this.data.array;
  }
  set needsUpdate(value) {
    this.data.needsUpdate = value;
  }
  applyMatrix4(m) {
    for (let i = 0, l = this.data.count; i < l; i++) {
      _vector$3.fromBufferAttribute(this, i);
      _vector$3.applyMatrix4(m);
      this.setXYZ(i, _vector$3.x, _vector$3.y, _vector$3.z);
    }
    return this;
  }
  applyNormalMatrix(m) {
    for (let i = 0, l = this.count; i < l; i++) {
      _vector$3.fromBufferAttribute(this, i);
      _vector$3.applyNormalMatrix(m);
      this.setXYZ(i, _vector$3.x, _vector$3.y, _vector$3.z);
    }
    return this;
  }
  transformDirection(m) {
    for (let i = 0, l = this.count; i < l; i++) {
      _vector$3.fromBufferAttribute(this, i);
      _vector$3.transformDirection(m);
      this.setXYZ(i, _vector$3.x, _vector$3.y, _vector$3.z);
    }
    return this;
  }
  getComponent(index, component) {
    let value = this.array[index * this.data.stride + this.offset + component];
    if (this.normalized) value = denormalize(value, this.array);
    return value;
  }
  setComponent(index, component, value) {
    if (this.normalized) value = normalize$1(value, this.array);
    this.data.array[index * this.data.stride + this.offset + component] = value;
    return this;
  }
  setX(index, x) {
    if (this.normalized) x = normalize$1(x, this.array);
    this.data.array[index * this.data.stride + this.offset] = x;
    return this;
  }
  setY(index, y) {
    if (this.normalized) y = normalize$1(y, this.array);
    this.data.array[index * this.data.stride + this.offset + 1] = y;
    return this;
  }
  setZ(index, z) {
    if (this.normalized) z = normalize$1(z, this.array);
    this.data.array[index * this.data.stride + this.offset + 2] = z;
    return this;
  }
  setW(index, w) {
    if (this.normalized) w = normalize$1(w, this.array);
    this.data.array[index * this.data.stride + this.offset + 3] = w;
    return this;
  }
  getX(index) {
    let x = this.data.array[index * this.data.stride + this.offset];
    if (this.normalized) x = denormalize(x, this.array);
    return x;
  }
  getY(index) {
    let y = this.data.array[index * this.data.stride + this.offset + 1];
    if (this.normalized) y = denormalize(y, this.array);
    return y;
  }
  getZ(index) {
    let z = this.data.array[index * this.data.stride + this.offset + 2];
    if (this.normalized) z = denormalize(z, this.array);
    return z;
  }
  getW(index) {
    let w = this.data.array[index * this.data.stride + this.offset + 3];
    if (this.normalized) w = denormalize(w, this.array);
    return w;
  }
  setXY(index, x, y) {
    index = index * this.data.stride + this.offset;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
    }
    this.data.array[index + 0] = x;
    this.data.array[index + 1] = y;
    return this;
  }
  setXYZ(index, x, y, z) {
    index = index * this.data.stride + this.offset;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
      z = normalize$1(z, this.array);
    }
    this.data.array[index + 0] = x;
    this.data.array[index + 1] = y;
    this.data.array[index + 2] = z;
    return this;
  }
  setXYZW(index, x, y, z, w) {
    index = index * this.data.stride + this.offset;
    if (this.normalized) {
      x = normalize$1(x, this.array);
      y = normalize$1(y, this.array);
      z = normalize$1(z, this.array);
      w = normalize$1(w, this.array);
    }
    this.data.array[index + 0] = x;
    this.data.array[index + 1] = y;
    this.data.array[index + 2] = z;
    this.data.array[index + 3] = w;
    return this;
  }
  clone(data) {
    if (data === undefined) {
      console.log('THREE.InterleavedBufferAttribute.clone(): Cloning an interleaved buffer attribute will de-interleave buffer data.');
      const array = [];
      for (let i = 0; i < this.count; i++) {
        const index = i * this.data.stride + this.offset;
        for (let j = 0; j < this.itemSize; j++) {
          array.push(this.data.array[index + j]);
        }
      }
      return new BufferAttribute(new this.array.constructor(array), this.itemSize, this.normalized);
    } else {
      if (data.interleavedBuffers === undefined) {
        data.interleavedBuffers = {};
      }
      if (data.interleavedBuffers[this.data.uuid] === undefined) {
        data.interleavedBuffers[this.data.uuid] = this.data.clone(data);
      }
      return new InterleavedBufferAttribute(data.interleavedBuffers[this.data.uuid], this.itemSize, this.offset, this.normalized);
    }
  }
  toJSON(data) {
    if (data === undefined) {
      console.log('THREE.InterleavedBufferAttribute.toJSON(): Serializing an interleaved buffer attribute will de-interleave buffer data.');
      const array = [];
      for (let i = 0; i < this.count; i++) {
        const index = i * this.data.stride + this.offset;
        for (let j = 0; j < this.itemSize; j++) {
          array.push(this.data.array[index + j]);
        }
      }

      // de-interleave data and save it as an ordinary buffer attribute for now

      return {
        itemSize: this.itemSize,
        type: this.array.constructor.name,
        array: array,
        normalized: this.normalized
      };
    } else {
      // save as true interleaved attribute

      if (data.interleavedBuffers === undefined) {
        data.interleavedBuffers = {};
      }
      if (data.interleavedBuffers[this.data.uuid] === undefined) {
        data.interleavedBuffers[this.data.uuid] = this.data.toJSON(data);
      }
      return {
        isInterleavedBufferAttribute: true,
        itemSize: this.itemSize,
        data: this.data.uuid,
        offset: this.offset,
        normalized: this.normalized
      };
    }
  }
}

class InterleavedBuffer {
  constructor(array, stride) {
    this.isInterleavedBuffer = true;
    this.array = array;
    this.stride = stride;
    this.count = array !== undefined ? array.length / stride : 0;
    this.usage = StaticDrawUsage;
    this._updateRange = {
      offset: 0,
      count: -1
    };
    this.updateRanges = [];
    this.version = 0;
    this.uuid = generateUUID();
  }
  onUploadCallback() {}
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  get updateRange() {
    warnOnce('THREE.InterleavedBuffer: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead.'); // @deprecated, r159
    return this._updateRange;
  }
  setUsage(value) {
    this.usage = value;
    return this;
  }
  addUpdateRange(start, count) {
    this.updateRanges.push({
      start,
      count
    });
  }
  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }
  copy(source) {
    this.array = new source.array.constructor(source.array);
    this.count = source.count;
    this.stride = source.stride;
    this.usage = source.usage;
    return this;
  }
  copyAt(index1, attribute, index2) {
    index1 *= this.stride;
    index2 *= attribute.stride;
    for (let i = 0, l = this.stride; i < l; i++) {
      this.array[index1 + i] = attribute.array[index2 + i];
    }
    return this;
  }
  set(value, offset = 0) {
    this.array.set(value, offset);
    return this;
  }
  clone(data) {
    if (data.arrayBuffers === undefined) {
      data.arrayBuffers = {};
    }
    if (this.array.buffer._uuid === undefined) {
      this.array.buffer._uuid = generateUUID();
    }
    if (data.arrayBuffers[this.array.buffer._uuid] === undefined) {
      data.arrayBuffers[this.array.buffer._uuid] = this.array.slice(0).buffer;
    }
    const array = new this.array.constructor(data.arrayBuffers[this.array.buffer._uuid]);
    const ib = new this.constructor(array, this.stride);
    ib.setUsage(this.usage);
    return ib;
  }
  onUpload(callback) {
    this.onUploadCallback = callback;
    return this;
  }
  toJSON(data) {
    if (data.arrayBuffers === undefined) {
      data.arrayBuffers = {};
    }

    // generate UUID for array buffer if necessary

    if (this.array.buffer._uuid === undefined) {
      this.array.buffer._uuid = generateUUID();
    }
    if (data.arrayBuffers[this.array.buffer._uuid] === undefined) {
      data.arrayBuffers[this.array.buffer._uuid] = Array.from(new Uint32Array(this.array.buffer));
    }

    //

    return {
      uuid: this.uuid,
      buffer: this.array.buffer._uuid,
      type: this.array.constructor.name,
      stride: this.stride
    };
  }
}

class BufferAttributeNode extends InputNode {
  constructor(value, bufferType = null, bufferStride = 0, bufferOffset = 0) {
    super(value, bufferType);
    this.isBufferNode = true;
    this.bufferType = bufferType;
    this.bufferStride = bufferStride;
    this.bufferOffset = bufferOffset;
    this.usage = StaticDrawUsage;
    this.instanced = false;
    this.attribute = null;
    this.global = true;
    if (value && value.isBufferAttribute === true) {
      this.attribute = value;
      this.usage = value.usage;
      this.instanced = value.isInstancedBufferAttribute;
    }
  }
  getHash(builder) {
    if (this.bufferStride === 0 && this.bufferOffset === 0) {
      let bufferData = builder.globalCache.getData(this.value);
      if (bufferData === undefined) {
        bufferData = {
          node: this
        };
        builder.globalCache.setData(this.value, bufferData);
      }
      return bufferData.node.uuid;
    }
    return this.uuid;
  }
  getNodeType(builder) {
    if (this.bufferType === null) {
      this.bufferType = builder.getTypeFromAttribute(this.attribute);
    }
    return this.bufferType;
  }
  setup(builder) {
    if (this.attribute !== null) return;
    const type = this.getNodeType(builder);
    const array = this.value;
    const itemSize = builder.getTypeLength(type);
    const stride = this.bufferStride || itemSize;
    const offset = this.bufferOffset;
    const buffer = array.isInterleavedBuffer === true ? array : new InterleavedBuffer(array, stride);
    const bufferAttribute = new InterleavedBufferAttribute(buffer, itemSize, offset);
    buffer.setUsage(this.usage);
    this.attribute = bufferAttribute;
    this.attribute.isInstancedBufferAttribute = this.instanced; // @TODO: Add a possible: InstancedInterleavedBufferAttribute
  }
  generate(builder) {
    const nodeType = this.getNodeType(builder);
    const nodeAttribute = builder.getBufferAttributeFromNode(this, nodeType);
    const propertyName = builder.getPropertyName(nodeAttribute);
    let output = null;
    if (builder.shaderStage === 'vertex' || builder.shaderStage === 'compute') {
      this.name = propertyName;
      output = propertyName;
    } else {
      const nodeVarying = varying(this);
      output = nodeVarying.build(builder, nodeType);
    }
    return output;
  }
  getInputType( /*builder*/
  ) {
    return 'bufferAttribute';
  }
  setUsage(value) {
    this.usage = value;
    if (this.attribute && this.attribute.isBufferAttribute === true) {
      this.attribute.usage = value;
    }
    return this;
  }
  setInstanced(value) {
    this.instanced = value;
    return this;
  }
}
BufferAttributeNode.type = /*@__PURE__*/registerNode('BufferAttribute', BufferAttributeNode);
const bufferAttribute = (array, type, stride, offset) => nodeObject(new BufferAttributeNode(array, type, stride, offset));
const dynamicBufferAttribute = (array, type, stride, offset) => bufferAttribute(array, type, stride, offset).setUsage(DynamicDrawUsage);
const instancedBufferAttribute = (array, type, stride, offset) => bufferAttribute(array, type, stride, offset).setInstanced(true);
const instancedDynamicBufferAttribute = (array, type, stride, offset) => dynamicBufferAttribute(array, type, stride, offset).setInstanced(true);
addMethodChaining('toAttribute', bufferNode => bufferAttribute(bufferNode.value));

class ComputeNode extends Node$1 {
  constructor(computeNode, count, workgroupSize = [64]) {
    super('void');
    this.isComputeNode = true;
    this.computeNode = computeNode;
    this.count = count;
    this.workgroupSize = workgroupSize;
    this.dispatchCount = 0;
    this.version = 1;
    this.updateBeforeType = NodeUpdateType.OBJECT;
    this.updateDispatchCount();
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  updateDispatchCount() {
    const {
      count,
      workgroupSize
    } = this;
    let size = workgroupSize[0];
    for (let i = 1; i < workgroupSize.length; i++) size *= workgroupSize[i];
    this.dispatchCount = Math.ceil(count / size);
  }
  onInit() {}
  updateBefore({
    renderer
  }) {
    renderer.compute(this);
  }
  generate(builder) {
    const {
      shaderStage
    } = builder;
    if (shaderStage === 'compute') {
      const snippet = this.computeNode.build(builder, 'void');
      if (snippet !== '') {
        builder.addLineFlowCode(snippet);
      }
    }
  }
}
ComputeNode.type = /*@__PURE__*/registerNode('Compute', ComputeNode);
const compute = (node, count, workgroupSize) => nodeObject(new ComputeNode(nodeObject(node), count, workgroupSize));
addMethodChaining('compute', compute);

class CacheNode extends Node$1 {
  constructor(node, parent = true) {
    super();
    this.node = node;
    this.parent = parent;
    this.isCacheNode = true;
  }
  getNodeType(builder) {
    return this.node.getNodeType(builder);
  }
  build(builder, ...params) {
    const previousCache = builder.getCache();
    const cache = builder.getCacheFromNode(this, parent);
    builder.setCache(cache);
    const data = this.node.build(builder, ...params);
    builder.setCache(previousCache);
    return data;
  }
}
CacheNode.type = /*@__PURE__*/registerNode('Cache', CacheNode);
const cache = (node, ...params) => nodeObject(new CacheNode(nodeObject(node), ...params));
addMethodChaining('cache', cache);

class BypassNode extends Node$1 {
  constructor(returnNode, callNode) {
    super();
    this.isBypassNode = true;
    this.outputNode = returnNode;
    this.callNode = callNode;
  }
  getNodeType(builder) {
    return this.outputNode.getNodeType(builder);
  }
  generate(builder) {
    const snippet = this.callNode.build(builder, 'void');
    if (snippet !== '') {
      builder.addLineFlowCode(snippet);
    }
    return this.outputNode.build(builder);
  }
}
BypassNode.type = /*@__PURE__*/registerNode('Bypass', BypassNode);
const bypass = /*@__PURE__*/nodeProxy(BypassNode);
addMethodChaining('bypass', bypass);

class RemapNode extends Node$1 {
  constructor(node, inLowNode, inHighNode, outLowNode = float(0), outHighNode = float(1)) {
    super();
    this.node = node;
    this.inLowNode = inLowNode;
    this.inHighNode = inHighNode;
    this.outLowNode = outLowNode;
    this.outHighNode = outHighNode;
    this.doClamp = true;
  }
  setup() {
    const {
      node,
      inLowNode,
      inHighNode,
      outLowNode,
      outHighNode,
      doClamp
    } = this;
    let t = node.sub(inLowNode).div(inHighNode.sub(inLowNode));
    if (doClamp === true) t = t.clamp();
    return t.mul(outHighNode.sub(outLowNode)).add(outLowNode);
  }
}
RemapNode.type = /*@__PURE__*/registerNode('Remap', RemapNode);
const remap = /*@__PURE__*/nodeProxy(RemapNode, null, null, {
  doClamp: false
});
const remapClamp = /*@__PURE__*/nodeProxy(RemapNode);
addMethodChaining('remap', remap);
addMethodChaining('remapClamp', remapClamp);

class ExpressionNode extends Node$1 {
  constructor(snippet = '', nodeType = 'void') {
    super(nodeType);
    this.snippet = snippet;
  }
  generate(builder, output) {
    const type = this.getNodeType(builder);
    const snippet = this.snippet;
    if (type === 'void') {
      builder.addLineFlowCode(snippet);
    } else {
      return builder.format(`( ${snippet} )`, type, output);
    }
  }
}
ExpressionNode.type = /*@__PURE__*/registerNode('Expression', ExpressionNode);
const expression = /*@__PURE__*/nodeProxy(ExpressionNode);

const Discard = conditional => (conditional ? select(conditional, expression('discard')) : expression('discard')).append();
addMethodChaining('discard', Discard);

class RenderOutputNode extends TempNode {
  constructor(colorNode, toneMapping, outputColorSpace) {
    super('vec4');
    this.colorNode = colorNode;
    this.toneMapping = toneMapping;
    this.outputColorSpace = outputColorSpace;
    this.isRenderOutput = true;
  }
  setup({
    context
  }) {
    let outputNode = this.colorNode || context.color;

    // tone mapping

    const toneMapping = (this.toneMapping !== null ? this.toneMapping : context.toneMapping) || NoToneMapping;
    const outputColorSpace = (this.outputColorSpace !== null ? this.outputColorSpace : context.outputColorSpace) || LinearSRGBColorSpace;
    if (toneMapping !== NoToneMapping) {
      outputNode = outputNode.toneMapping(toneMapping);
    }

    // output color space

    if (outputColorSpace === SRGBColorSpace) {
      outputNode = outputNode.toOutputColorSpace(outputColorSpace);
    }
    return outputNode;
  }
}
RenderOutputNode.type = /*@__PURE__*/registerNode('RenderOutput', RenderOutputNode);
const renderOutput = (color, toneMapping = null, outputColorSpace = null) => nodeObject(new RenderOutputNode(nodeObject(color), toneMapping, outputColorSpace));
addMethodChaining('renderOutput', renderOutput);

class AttributeNode extends Node$1 {
  constructor(attributeName, nodeType = null) {
    super(nodeType);
    this.global = true;
    this._attributeName = attributeName;
  }
  getHash(builder) {
    return this.getAttributeName(builder);
  }
  getNodeType(builder) {
    let nodeType = this.nodeType;
    if (nodeType === null) {
      const attributeName = this.getAttributeName(builder);
      if (builder.hasGeometryAttribute(attributeName)) {
        const attribute = builder.geometry.getAttribute(attributeName);
        nodeType = builder.getTypeFromAttribute(attribute);
      } else {
        nodeType = 'float';
      }
    }
    return nodeType;
  }
  setAttributeName(attributeName) {
    this._attributeName = attributeName;
    return this;
  }
  getAttributeName( /*builder*/
  ) {
    return this._attributeName;
  }
  generate(builder) {
    const attributeName = this.getAttributeName(builder);
    const nodeType = this.getNodeType(builder);
    const geometryAttribute = builder.hasGeometryAttribute(attributeName);
    if (geometryAttribute === true) {
      const attribute = builder.geometry.getAttribute(attributeName);
      const attributeType = builder.getTypeFromAttribute(attribute);
      const nodeAttribute = builder.getAttribute(attributeName, attributeType);
      if (builder.shaderStage === 'vertex') {
        return builder.format(nodeAttribute.name, attributeType, nodeType);
      } else {
        const nodeVarying = varying(this);
        return nodeVarying.build(builder, nodeType);
      }
    } else {
      console.warn(`AttributeNode: Vertex attribute "${attributeName}" not found on geometry.`);
      return builder.generateConst(nodeType);
    }
  }
  serialize(data) {
    super.serialize(data);
    data.global = this.global;
    data._attributeName = this._attributeName;
  }
  deserialize(data) {
    super.deserialize(data);
    this.global = data.global;
    this._attributeName = data._attributeName;
  }
}
AttributeNode.type = /*@__PURE__*/registerNode('Attribute', AttributeNode);
const attribute = (name, nodeType) => nodeObject(new AttributeNode(name, nodeType));

class IndexNode extends Node$1 {
  constructor(scope) {
    super('uint');
    this.scope = scope;
    this.isInstanceIndexNode = true;
  }
  generate(builder) {
    const nodeType = this.getNodeType(builder);
    const scope = this.scope;
    let propertyName;
    if (scope === IndexNode.VERTEX) {
      propertyName = builder.getVertexIndex();
    } else if (scope === IndexNode.INSTANCE) {
      propertyName = builder.getInstanceIndex();
    } else if (scope === IndexNode.DRAW) {
      propertyName = builder.getDrawIndex();
    } else if (scope === IndexNode.INVOCATION_LOCAL) {
      propertyName = builder.getInvocationLocalIndex();
    } else {
      throw new Error('THREE.IndexNode: Unknown scope: ' + scope);
    }
    let output;
    if (builder.shaderStage === 'vertex' || builder.shaderStage === 'compute') {
      output = propertyName;
    } else {
      const nodeVarying = varying(this);
      output = nodeVarying.build(builder, nodeType);
    }
    return output;
  }
}
IndexNode.VERTEX = 'vertex';
IndexNode.INSTANCE = 'instance';
IndexNode.INVOCATION_LOCAL = 'invocationLocal';
IndexNode.DRAW = 'draw';
IndexNode.type = /*@__PURE__*/registerNode('Index', IndexNode);
const vertexIndex = /*@__PURE__*/nodeImmutable(IndexNode, IndexNode.VERTEX);
const instanceIndex = /*@__PURE__*/nodeImmutable(IndexNode, IndexNode.INSTANCE);
/*@__PURE__*/nodeImmutable(IndexNode, IndexNode.INVOCATION_LOCAL);
const drawIndex = /*@__PURE__*/nodeImmutable(IndexNode, IndexNode.DRAW);

class ParameterNode extends PropertyNode {
  constructor(nodeType, name = null) {
    super(nodeType, name);
    this.isParameterNode = true;
  }
  getHash() {
    return this.uuid;
  }
  generate() {
    return this.name;
  }
}
ParameterNode.type = /*@__PURE__*/registerNode('Parameter', ParameterNode);

class StackNode extends Node$1 {
  constructor(parent = null) {
    super();
    this.nodes = [];
    this.outputNode = null;
    this.parent = parent;
    this._currentCond = null;
    this.isStackNode = true;
  }
  getNodeType(builder) {
    return this.outputNode ? this.outputNode.getNodeType(builder) : 'void';
  }
  add(node) {
    this.nodes.push(node);
    return this;
  }
  If(boolNode, method) {
    const methodNode = new ShaderNode(method);
    this._currentCond = select(boolNode, methodNode);
    return this.add(this._currentCond);
  }
  ElseIf(boolNode, method) {
    const methodNode = new ShaderNode(method);
    const ifNode = select(boolNode, methodNode);
    this._currentCond.elseNode = ifNode;
    this._currentCond = ifNode;
    return this;
  }
  Else(method) {
    this._currentCond.elseNode = new ShaderNode(method);
    return this;
  }
  build(builder, ...params) {
    const previousStack = getCurrentStack();
    setCurrentStack(this);
    for (const node of this.nodes) {
      node.build(builder, 'void');
    }
    setCurrentStack(previousStack);
    return this.outputNode ? this.outputNode.build(builder, ...params) : super.build(builder, ...params);
  }

  //

  else(...params) {
    // @deprecated, r168

    console.warn('TSL.StackNode: .else() has been renamed to .Else().');
    return this.Else(...params);
  }
  elseif(...params) {
    // @deprecated, r168

    console.warn('TSL.StackNode: .elseif() has been renamed to .ElseIf().');
    return this.ElseIf(...params);
  }
}
StackNode.type = /*@__PURE__*/registerNode('Stack', StackNode);

class StructTypeNode extends Node$1 {
  constructor(types) {
    super();
    this.types = types;
    this.isStructTypeNode = true;
  }
  getMemberTypes() {
    return this.types;
  }
}
StructTypeNode.type = /*@__PURE__*/registerNode('StructType', StructTypeNode);

class OutputStructNode extends Node$1 {
  constructor(...members) {
    super();
    this.members = members;
    this.isOutputStructNode = true;
  }
  setup(builder) {
    super.setup(builder);
    const members = this.members;
    const types = [];
    for (let i = 0; i < members.length; i++) {
      types.push(members[i].getNodeType(builder));
    }
    this.nodeType = builder.getStructTypeFromNode(new StructTypeNode(types)).name;
  }
  generate(builder, output) {
    const propertyName = builder.getOutputStructName();
    const members = this.members;
    const structPrefix = propertyName !== '' ? propertyName + '.' : '';
    for (let i = 0; i < members.length; i++) {
      const snippet = members[i].build(builder, output);
      builder.addLineFlowCode(`${structPrefix}m${i} = ${snippet}`);
    }
    return propertyName;
  }
}
OutputStructNode.type = /*@__PURE__*/registerNode('OutputStruct', OutputStructNode);

function getTextureIndex(textures, name) {
  for (let i = 0; i < textures.length; i++) {
    if (textures[i].name === name) {
      return i;
    }
  }
  return -1;
}
class MRTNode extends OutputStructNode {
  constructor(outputNodes) {
    super();
    this.outputNodes = outputNodes;
    this.isMRTNode = true;
  }
  has(name) {
    return this.outputNodes[name] !== undefined;
  }
  get(name) {
    return this.outputNodes[name];
  }
  merge(mrtNode) {
    const outputs = {
      ...this.outputNodes,
      ...mrtNode.outputNodes
    };
    return mrt(outputs);
  }
  setup(builder) {
    const outputNodes = this.outputNodes;
    const mrt = builder.renderer.getRenderTarget();
    const members = [];
    const textures = mrt.textures;
    for (const name in outputNodes) {
      const index = getTextureIndex(textures, name);
      members[index] = vec4(outputNodes[name]);
    }
    this.members = members;
    return super.setup(builder);
  }
}
MRTNode.type = /*@__PURE__*/registerNode('MRT', MRTNode);
const mrt = /*@__PURE__*/nodeProxy(MRTNode);

class LoopNode extends Node$1 {
  constructor(params = []) {
    super();
    this.params = params;
  }
  getVarName(index) {
    return String.fromCharCode('i'.charCodeAt() + index);
  }
  getProperties(builder) {
    const properties = builder.getNodeProperties(this);
    if (properties.stackNode !== undefined) return properties;

    //

    const inputs = {};
    for (let i = 0, l = this.params.length - 1; i < l; i++) {
      const param = this.params[i];
      const name = param.isNode !== true && param.name || this.getVarName(i);
      const type = param.isNode !== true && param.type || 'int';
      inputs[name] = expression(name, type);
    }
    const stack = builder.addStack(); // TODO: cache() it

    properties.returnsNode = this.params[this.params.length - 1](inputs, stack, builder);
    properties.stackNode = stack;
    builder.removeStack();
    return properties;
  }
  getNodeType(builder) {
    const {
      returnsNode
    } = this.getProperties(builder);
    return returnsNode ? returnsNode.getNodeType(builder) : 'void';
  }
  setup(builder) {
    // setup properties

    this.getProperties(builder);
  }
  generate(builder) {
    const properties = this.getProperties(builder);
    const params = this.params;
    const stackNode = properties.stackNode;
    for (let i = 0, l = params.length - 1; i < l; i++) {
      const param = params[i];
      let start = null,
        end = null,
        name = null,
        type = null,
        condition = null,
        update = null;
      if (param.isNode) {
        type = 'int';
        name = this.getVarName(i);
        start = '0';
        end = param.build(builder, type);
        condition = '<';
      } else {
        type = param.type || 'int';
        name = param.name || this.getVarName(i);
        start = param.start;
        end = param.end;
        condition = param.condition;
        update = param.update;
        if (typeof start === 'number') start = start.toString();else if (start && start.isNode) start = start.build(builder, type);
        if (typeof end === 'number') end = end.toString();else if (end && end.isNode) end = end.build(builder, type);
        if (start !== undefined && end === undefined) {
          start = start + ' - 1';
          end = '0';
          condition = '>=';
        } else if (end !== undefined && start === undefined) {
          start = '0';
          condition = '<';
        }
        if (condition === undefined) {
          if (Number(start) > Number(end)) {
            condition = '>=';
          } else {
            condition = '<';
          }
        }
      }
      const internalParam = {
        start,
        end,
        condition
      };

      //

      const startSnippet = internalParam.start;
      const endSnippet = internalParam.end;
      let declarationSnippet = '';
      let conditionalSnippet = '';
      let updateSnippet = '';
      if (!update) {
        if (type === 'int' || type === 'uint') {
          if (condition.includes('<')) update = '++';else update = '--';
        } else {
          if (condition.includes('<')) update = '+= 1.';else update = '-= 1.';
        }
      }
      declarationSnippet += builder.getVar(type, name) + ' = ' + startSnippet;
      conditionalSnippet += name + ' ' + condition + ' ' + endSnippet;
      updateSnippet += name + ' ' + update;
      const forSnippet = `for ( ${declarationSnippet}; ${conditionalSnippet}; ${updateSnippet} )`;
      builder.addFlowCode((i === 0 ? '\n' : '') + builder.tab + forSnippet + ' {\n\n').addFlowTab();
    }
    const stackSnippet = stackNode.build(builder, 'void');
    const returnsSnippet = properties.returnsNode ? properties.returnsNode.build(builder) : '';
    builder.removeFlowTab().addFlowCode('\n' + builder.tab + stackSnippet);
    for (let i = 0, l = this.params.length - 1; i < l; i++) {
      builder.addFlowCode((i === 0 ? '' : builder.tab) + '}\n\n').removeFlowTab();
    }
    builder.addFlowTab();
    return returnsSnippet;
  }
}
LoopNode.type = /*@__PURE__*/registerNode('Loop', LoopNode);
const Loop = (...params) => nodeObject(new LoopNode(nodeArray(params, 'int'))).append();
const Break = () => expression('break').append();

class Object3DNode extends Node$1 {
  constructor(scope = Object3DNode.VIEW_MATRIX, object3d = null) {
    super();
    this.scope = scope;
    this.object3d = object3d;
    this.updateType = NodeUpdateType.OBJECT;
    this._uniformNode = new UniformNode(null);
  }
  getNodeType() {
    const scope = this.scope;
    if (scope === Object3DNode.WORLD_MATRIX || scope === Object3DNode.VIEW_MATRIX) {
      return 'mat4';
    } else if (scope === Object3DNode.NORMAL_MATRIX) {
      return 'mat3';
    } else if (scope === Object3DNode.POSITION || scope === Object3DNode.VIEW_POSITION || scope === Object3DNode.DIRECTION || scope === Object3DNode.SCALE) {
      return 'vec3';
    }
  }
  update(frame) {
    const object = this.object3d;
    const uniformNode = this._uniformNode;
    const scope = this.scope;
    if (scope === Object3DNode.VIEW_MATRIX) {
      uniformNode.value = object.modelViewMatrix;
    } else if (scope === Object3DNode.NORMAL_MATRIX) {
      uniformNode.value = object.normalMatrix;
    } else if (scope === Object3DNode.WORLD_MATRIX) {
      uniformNode.value = object.matrixWorld;
    } else if (scope === Object3DNode.POSITION) {
      uniformNode.value = uniformNode.value || new Vector3();
      uniformNode.value.setFromMatrixPosition(object.matrixWorld);
    } else if (scope === Object3DNode.SCALE) {
      uniformNode.value = uniformNode.value || new Vector3();
      uniformNode.value.setFromMatrixScale(object.matrixWorld);
    } else if (scope === Object3DNode.DIRECTION) {
      uniformNode.value = uniformNode.value || new Vector3();
      object.getWorldDirection(uniformNode.value);
    } else if (scope === Object3DNode.VIEW_POSITION) {
      const camera = frame.camera;
      uniformNode.value = uniformNode.value || new Vector3();
      uniformNode.value.setFromMatrixPosition(object.matrixWorld);
      uniformNode.value.applyMatrix4(camera.matrixWorldInverse);
    }
  }
  generate(builder) {
    const scope = this.scope;
    if (scope === Object3DNode.WORLD_MATRIX || scope === Object3DNode.VIEW_MATRIX) {
      this._uniformNode.nodeType = 'mat4';
    } else if (scope === Object3DNode.NORMAL_MATRIX) {
      this._uniformNode.nodeType = 'mat3';
    } else if (scope === Object3DNode.POSITION || scope === Object3DNode.VIEW_POSITION || scope === Object3DNode.DIRECTION || scope === Object3DNode.SCALE) {
      this._uniformNode.nodeType = 'vec3';
    }
    return this._uniformNode.build(builder);
  }
  serialize(data) {
    super.serialize(data);
    data.scope = this.scope;
  }
  deserialize(data) {
    super.deserialize(data);
    this.scope = data.scope;
  }
}
Object3DNode.VIEW_MATRIX = 'viewMatrix';
Object3DNode.NORMAL_MATRIX = 'normalMatrix';
Object3DNode.WORLD_MATRIX = 'worldMatrix';
Object3DNode.POSITION = 'position';
Object3DNode.SCALE = 'scale';
Object3DNode.VIEW_POSITION = 'viewPosition';
Object3DNode.DIRECTION = 'direction';
Object3DNode.type = /*@__PURE__*/registerNode('Object3D', Object3DNode);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.DIRECTION);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.VIEW_MATRIX);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.NORMAL_MATRIX);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.WORLD_MATRIX);
const objectPosition = /*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.POSITION);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.SCALE);
/*@__PURE__*/nodeProxy(Object3DNode, Object3DNode.VIEW_POSITION);

class ModelNode extends Object3DNode {
  constructor(scope = ModelNode.VIEW_MATRIX) {
    super(scope);
  }
  update(frame) {
    this.object3d = frame.object;
    super.update(frame);
  }
}
ModelNode.type = /*@__PURE__*/registerNode('Model', ModelNode);
/*@__PURE__*/nodeImmutable(ModelNode, ModelNode.DIRECTION);
const modelViewMatrix = /*@__PURE__*/nodeImmutable(ModelNode, ModelNode.VIEW_MATRIX).label('modelViewMatrix').toVar('ModelViewMatrix');
const modelNormalMatrix = /*@__PURE__*/nodeImmutable(ModelNode, ModelNode.NORMAL_MATRIX);
const modelWorldMatrix = /*@__PURE__*/nodeImmutable(ModelNode, ModelNode.WORLD_MATRIX);
/*@__PURE__*/nodeImmutable(ModelNode, ModelNode.POSITION);
/*@__PURE__*/nodeImmutable(ModelNode, ModelNode.SCALE);
/*@__PURE__*/nodeImmutable(ModelNode, ModelNode.VIEW_POSITION);

const positionGeometry = /*@__PURE__*/attribute('position', 'vec3');
const positionLocal = /*@__PURE__*/positionGeometry.varying('positionLocal');
const positionPrevious = /*@__PURE__*/positionGeometry.varying('positionPrevious');
const positionWorldDirection = /*@__PURE__*/positionLocal.transformDirection(modelWorldMatrix).varying('v_positionWorldDirection').normalize().toVar('positionWorldDirection');
const positionView = /*@__PURE__*/modelViewMatrix.mul(positionLocal).xyz.varying('v_positionView');
const positionViewDirection = /*@__PURE__*/positionView.negate().varying('v_positionViewDirection').normalize().toVar('positionViewDirection');

class EquirectUVNode extends TempNode {
  constructor(dirNode = positionWorldDirection) {
    super('vec2');
    this.dirNode = dirNode;
  }
  setup() {
    const dir = this.dirNode;
    const u = dir.z.atan2(dir.x).mul(1 / (Math.PI * 2)).add(0.5);
    const v = dir.y.clamp(-1.0, 1.0).asin().mul(1 / Math.PI).add(0.5);
    return vec2(u, v);
  }
}
EquirectUVNode.type = /*@__PURE__*/registerNode('EquirectUV', EquirectUVNode);

class FunctionOverloadingNode extends Node$1 {
  constructor(functionNodes = [], ...parametersNodes) {
    super();
    this.functionNodes = functionNodes;
    this.parametersNodes = parametersNodes;
    this._candidateFnCall = null;
    this.global = true;
  }
  getNodeType() {
    return this.functionNodes[0].shaderNode.layout.type;
  }
  setup(builder) {
    const params = this.parametersNodes;
    let candidateFnCall = this._candidateFnCall;
    if (candidateFnCall === null) {
      let candidateFn = null;
      let candidateScore = -1;
      for (const functionNode of this.functionNodes) {
        const shaderNode = functionNode.shaderNode;
        const layout = shaderNode.layout;
        if (layout === null) {
          throw new Error('FunctionOverloadingNode: FunctionNode must be a layout.');
        }
        const inputs = layout.inputs;
        if (params.length === inputs.length) {
          let score = 0;
          for (let i = 0; i < params.length; i++) {
            const param = params[i];
            const input = inputs[i];
            if (param.getNodeType(builder) === input.type) {
              score++;
            } else {
              score = 0;
            }
          }
          if (score > candidateScore) {
            candidateFn = functionNode;
            candidateScore = score;
          }
        }
      }
      this._candidateFnCall = candidateFnCall = candidateFn(...params);
    }
    return candidateFnCall;
  }
}
FunctionOverloadingNode.type = /*@__PURE__*/registerNode('FunctionOverloading', FunctionOverloadingNode);

const cameraGroup = /*@__PURE__*/sharedUniformGroup('camera').onRenderUpdate(() => {
  cameraGroup.needsUpdate = true;
});
const cameraNear = /*@__PURE__*/uniform('float').label('cameraNear').setGroup(cameraGroup).onRenderUpdate(({
  camera
}) => camera.near);
const cameraFar = /*@__PURE__*/uniform('float').label('cameraFar').setGroup(cameraGroup).onRenderUpdate(({
  camera
}) => camera.far);
const cameraLogDepth = /*@__PURE__*/uniform('float').label('cameraLogDepth').setGroup(cameraGroup).onRenderUpdate(({
  camera
}) => 2.0 / (Math.log(camera.far + 1.0) / Math.LN2));
const cameraProjectionMatrix = /*@__PURE__*/uniform('mat4').label('cameraProjectionMatrix').setGroup(cameraGroup).onRenderUpdate(({
  camera
}) => camera.projectionMatrix);
const cameraViewMatrix = /*@__PURE__*/uniform('mat4').label('cameraViewMatrix').setGroup(cameraGroup).onRenderUpdate(({
  camera
}) => camera.matrixWorldInverse);

class FrontFacingNode extends Node$1 {
  constructor() {
    super('bool');
    this.isFrontFacingNode = true;
  }
  generate(builder) {
    const {
      renderer,
      material
    } = builder;
    if (renderer.coordinateSystem === WebGLCoordinateSystem) {
      if (material.side === BackSide) {
        return 'false';
      }
    }
    return builder.getFrontFacing();
  }
}
FrontFacingNode.type = /*@__PURE__*/registerNode('FrontFacing', FrontFacingNode);
const frontFacing = /*@__PURE__*/nodeImmutable(FrontFacingNode);
const faceDirection = /*@__PURE__*/float(frontFacing).mul(2.0).sub(1.0);

const normalGeometry = /*@__PURE__*/attribute('normal', 'vec3');
const normalLocal = /*@__PURE__*/Fn(builder => {
  if (builder.geometry.hasAttribute('normal') === false) {
    console.warn('TSL.NormalNode: Vertex attribute "normal" not found on geometry.');
    return vec3(0, 1, 0);
  }
  return normalGeometry;
}, 'vec3').once()().toVar('normalLocal');
const normalFlat = /*@__PURE__*/positionView.dFdx().cross(positionView.dFdy()).normalize().toVar('normalFlat');
let normalViewVarying = null;
const normalView = /*@__PURE__*/Fn(builder => {
  let node;
  if (builder.material.flatShading === true) {
    node = normalFlat;
  } else {
    node = normalViewVarying || (normalViewVarying = varying(modelNormalMatrix.mul(normalLocal), 'v_normalView').normalize());
  }
  return node;
}, 'vec3').once()().toVar('normalView');
const normalWorld = /*@__PURE__*/varying(normalView.transformDirection(cameraViewMatrix), 'v_normalWorld').normalize().toVar('normalWorld');
const transformedNormalView = /*@__PURE__*/Fn(builder => {
  return builder.context.setupNormal();
}, 'vec3').once()().mul(faceDirection).toVar('transformedNormalView');

class MatcapUVNode extends TempNode {
  constructor() {
    super('vec2');
  }
  setup() {
    const x = vec3(positionViewDirection.z, 0, positionViewDirection.x.negate()).normalize();
    const y = positionViewDirection.cross(x);
    return vec2(x.dot(transformedNormalView), y.dot(transformedNormalView)).mul(0.495).add(0.5); // 0.495 to remove artifacts caused by undersized matcap disks
  }
}
MatcapUVNode.type = /*@__PURE__*/registerNode('MatcapUV', MatcapUVNode);

class MaxMipLevelNode extends UniformNode {
  constructor(textureNode) {
    super(0);
    this._textureNode = textureNode;
    this.updateType = NodeUpdateType.FRAME;
  }
  get textureNode() {
    return this._textureNode;
  }
  get texture() {
    return this._textureNode.value;
  }
  update() {
    const texture = this.texture;
    const images = texture.images;
    const image = images && images.length > 0 ? images[0] && images[0].image || images[0] : texture.image;
    if (image && image.width !== undefined) {
      const {
        width,
        height
      } = image;
      this.value = Math.log2(Math.max(width, height));
    }
  }
}
MaxMipLevelNode.type = /*@__PURE__*/registerNode('MaxMipLevel', MaxMipLevelNode);
const maxMipLevel = /*@__PURE__*/nodeProxy(MaxMipLevelNode);

class TimerNode extends UniformNode {
  constructor(scope = TimerNode.LOCAL, scale = 1, value = 0) {
    super(value);
    this.scope = scope;
    this.scale = scale;
    this.updateType = NodeUpdateType.FRAME;
  }
  /*
  @TODO:
  getNodeType( builder ) {
  		const scope = this.scope;
  		if ( scope === TimerNode.FRAME ) {
  			return 'uint';
  		}
  		return 'float';
  	}
  */
  update(frame) {
    const scope = this.scope;
    const scale = this.scale;
    if (scope === TimerNode.LOCAL) {
      this.value += frame.deltaTime * scale;
    } else if (scope === TimerNode.DELTA) {
      this.value = frame.deltaTime * scale;
    } else if (scope === TimerNode.FRAME) {
      this.value = frame.frameId;
    } else {
      // global

      this.value = frame.time * scale;
    }
  }
  serialize(data) {
    super.serialize(data);
    data.scope = this.scope;
    data.scale = this.scale;
  }
  deserialize(data) {
    super.deserialize(data);
    this.scope = data.scope;
    this.scale = data.scale;
  }
}
TimerNode.LOCAL = 'local';
TimerNode.GLOBAL = 'global';
TimerNode.DELTA = 'delta';
TimerNode.FRAME = 'frame';
TimerNode.type = /*@__PURE__*/registerNode('Timer', TimerNode);

// @TODO: add support to use node in timeScale
const timerLocal = (timeScale, value = 0) => nodeObject(new TimerNode(TimerNode.LOCAL, timeScale, value));

class OscNode extends Node$1 {
  constructor(method = OscNode.SINE, timeNode = timerLocal()) {
    super();
    this.method = method;
    this.timeNode = timeNode;
  }
  getNodeType(builder) {
    return this.timeNode.getNodeType(builder);
  }
  setup() {
    const method = this.method;
    const timeNode = nodeObject(this.timeNode);
    let outputNode = null;
    if (method === OscNode.SINE) {
      outputNode = timeNode.add(0.75).mul(Math.PI * 2).sin().mul(0.5).add(0.5);
    } else if (method === OscNode.SQUARE) {
      outputNode = timeNode.fract().round();
    } else if (method === OscNode.TRIANGLE) {
      outputNode = timeNode.add(0.5).fract().mul(2).sub(1).abs();
    } else if (method === OscNode.SAWTOOTH) {
      outputNode = timeNode.fract();
    }
    return outputNode;
  }
  serialize(data) {
    super.serialize(data);
    data.method = this.method;
  }
  deserialize(data) {
    super.deserialize(data);
    this.method = data.method;
  }
}
OscNode.SINE = 'sine';
OscNode.SQUARE = 'square';
OscNode.TRIANGLE = 'triangle';
OscNode.SAWTOOTH = 'sawtooth';
OscNode.type = /*@__PURE__*/registerNode('Osc', OscNode);
/*@__PURE__*/nodeProxy(OscNode, OscNode.SINE);
/*@__PURE__*/nodeProxy(OscNode, OscNode.SQUARE);
/*@__PURE__*/nodeProxy(OscNode, OscNode.TRIANGLE);
/*@__PURE__*/nodeProxy(OscNode, OscNode.SAWTOOTH);

class RotateNode extends TempNode {
  constructor(positionNode, rotationNode) {
    super();
    this.positionNode = positionNode;
    this.rotationNode = rotationNode;
  }
  getNodeType(builder) {
    return this.positionNode.getNodeType(builder);
  }
  setup(builder) {
    const {
      rotationNode,
      positionNode
    } = this;
    const nodeType = this.getNodeType(builder);
    if (nodeType === 'vec2') {
      const cosAngle = rotationNode.cos();
      const sinAngle = rotationNode.sin();
      const rotationMatrix = mat2(cosAngle, sinAngle, sinAngle.negate(), cosAngle);
      return rotationMatrix.mul(positionNode);
    } else {
      const rotation = rotationNode;
      const rotationXMatrix = mat4(vec4(1.0, 0.0, 0.0, 0.0), vec4(0.0, cos(rotation.x), sin(rotation.x).negate(), 0.0), vec4(0.0, sin(rotation.x), cos(rotation.x), 0.0), vec4(0.0, 0.0, 0.0, 1.0));
      const rotationYMatrix = mat4(vec4(cos(rotation.y), 0.0, sin(rotation.y), 0.0), vec4(0.0, 1.0, 0.0, 0.0), vec4(sin(rotation.y).negate(), 0.0, cos(rotation.y), 0.0), vec4(0.0, 0.0, 0.0, 1.0));
      const rotationZMatrix = mat4(vec4(cos(rotation.z), sin(rotation.z).negate(), 0.0, 0.0), vec4(sin(rotation.z), cos(rotation.z), 0.0, 0.0), vec4(0.0, 0.0, 1.0, 0.0), vec4(0.0, 0.0, 0.0, 1.0));
      return rotationXMatrix.mul(rotationYMatrix).mul(rotationZMatrix).mul(vec4(positionNode, 1.0)).xyz;
    }
  }
}
RotateNode.type = /*@__PURE__*/registerNode('Rotate', RotateNode);

let resolution, viewportResult;
class ViewportNode extends Node$1 {
  constructor(scope) {
    super();
    this.scope = scope;
    this.isViewportNode = true;
  }
  getNodeType() {
    if (this.scope === ViewportNode.VIEWPORT) return 'vec4';else return 'vec2';
  }
  getUpdateType() {
    let updateType = NodeUpdateType.NONE;
    if (this.scope === ViewportNode.RESOLUTION || this.scope === ViewportNode.VIEWPORT) {
      updateType = NodeUpdateType.RENDER;
    }
    this.updateType = updateType;
    return updateType;
  }
  update({
    renderer
  }) {
    if (this.scope === ViewportNode.VIEWPORT) {
      renderer.getViewport(viewportResult);
    } else {
      renderer.getDrawingBufferSize(resolution);
    }
  }
  setup( /*builder*/
  ) {
    const scope = this.scope;
    let output = null;
    if (scope === ViewportNode.RESOLUTION) {
      output = uniform(resolution || (resolution = new Vector2()));
    } else if (scope === ViewportNode.VIEWPORT) {
      output = uniform(viewportResult || (viewportResult = new Vector4()));
    } else {
      output = vec2(viewportCoordinate.div(viewportResolution));
    }
    return output;
  }
  generate(builder) {
    if (this.scope === ViewportNode.COORDINATE) {
      let coord = builder.getFragCoord();
      if (builder.isFlipY()) {
        // follow webgpu standards

        const resolution = builder.getNodeProperties(viewportResolution).outputNode.build(builder);
        coord = `${builder.getType('vec2')}( ${coord}.x, ${resolution}.y - ${coord}.y )`;
      }
      return coord;
    }
    return super.generate(builder);
  }
}
ViewportNode.COORDINATE = 'coordinate';
ViewportNode.RESOLUTION = 'resolution';
ViewportNode.VIEWPORT = 'viewport';
ViewportNode.UV = 'uv';
ViewportNode.type = /*@__PURE__*/registerNode('Viewport', ViewportNode);
const viewportCoordinate = /*@__PURE__*/nodeImmutable(ViewportNode, ViewportNode.COORDINATE);
const viewportResolution = /*@__PURE__*/nodeImmutable(ViewportNode, ViewportNode.RESOLUTION);
/*@__PURE__*/nodeImmutable(ViewportNode, ViewportNode.VIEWPORT);
const viewportUV = /*@__PURE__*/nodeImmutable(ViewportNode, ViewportNode.UV);

const uv = index => attribute('uv' + (index > 0 ? index : ''), 'vec2');

class TextureSizeNode extends Node$1 {
  constructor(textureNode, levelNode = null) {
    super('uvec2');
    this.isTextureSizeNode = true;
    this.textureNode = textureNode;
    this.levelNode = levelNode;
  }
  generate(builder, output) {
    const textureProperty = this.textureNode.build(builder, 'property');
    const levelNode = this.levelNode.build(builder, 'int');
    return builder.format(`${builder.getMethod('textureDimensions')}( ${textureProperty}, ${levelNode} )`, this.getNodeType(builder), output);
  }
}
TextureSizeNode.type = /*@__PURE__*/registerNode('TextureSize', TextureSizeNode);
const textureSize = /*@__PURE__*/nodeProxy(TextureSizeNode);

class TextureNode extends UniformNode {
  constructor(value, uvNode = null, levelNode = null, biasNode = null) {
    super(value);
    this.isTextureNode = true;
    this.uvNode = uvNode;
    this.levelNode = levelNode;
    this.biasNode = biasNode;
    this.compareNode = null;
    this.depthNode = null;
    this.gradNode = null;
    this.sampler = true;
    this.updateMatrix = false;
    this.updateType = NodeUpdateType.NONE;
    this.referenceNode = null;
    this._value = value;
    this._matrixUniform = null;
    this.setUpdateMatrix(uvNode === null);
  }
  set value(value) {
    if (this.referenceNode) {
      this.referenceNode.value = value;
    } else {
      this._value = value;
    }
  }
  get value() {
    return this.referenceNode ? this.referenceNode.value : this._value;
  }
  getUniformHash( /*builder*/
  ) {
    return this.value.uuid;
  }
  getNodeType( /*builder*/
  ) {
    if (this.value.isDepthTexture === true) return 'float';
    if (this.value.type === UnsignedIntType) {
      return 'uvec4';
    } else if (this.value.type === IntType) {
      return 'ivec4';
    }
    return 'vec4';
  }
  getInputType( /*builder*/
  ) {
    return 'texture';
  }
  getDefaultUV() {
    return uv(this.value.channel);
  }
  updateReference( /*state*/
  ) {
    return this.value;
  }
  getTransformedUV(uvNode) {
    if (this._matrixUniform === null) this._matrixUniform = uniform(this.value.matrix);
    return this._matrixUniform.mul(vec3(uvNode, 1)).xy;
  }
  setUpdateMatrix(value) {
    this.updateMatrix = value;
    this.updateType = value ? NodeUpdateType.FRAME : NodeUpdateType.NONE;
    return this;
  }
  setupUV(builder, uvNode) {
    const texture = this.value;
    if (builder.isFlipY() && (texture.isRenderTargetTexture === true || texture.isFramebufferTexture === true || texture.isDepthTexture === true)) {
      uvNode = uvNode.setY(uvNode.y.oneMinus());
    }
    return uvNode;
  }
  setup(builder) {
    const properties = builder.getNodeProperties(this);
    properties.referenceNode = this.referenceNode;

    //

    let uvNode = this.uvNode;
    if ((uvNode === null || builder.context.forceUVContext === true) && builder.context.getUV) {
      uvNode = builder.context.getUV(this);
    }
    if (!uvNode) uvNode = this.getDefaultUV();
    if (this.updateMatrix === true) {
      uvNode = this.getTransformedUV(uvNode);
    }
    uvNode = this.setupUV(builder, uvNode);

    //

    let levelNode = this.levelNode;
    if (levelNode === null && builder.context.getTextureLevel) {
      levelNode = builder.context.getTextureLevel(this);
    }

    //

    properties.uvNode = uvNode;
    properties.levelNode = levelNode;
    properties.biasNode = this.biasNode;
    properties.compareNode = this.compareNode;
    properties.gradNode = this.gradNode;
    properties.depthNode = this.depthNode;
  }
  generateUV(builder, uvNode) {
    return uvNode.build(builder, this.sampler === true ? 'vec2' : 'ivec2');
  }
  generateSnippet(builder, textureProperty, uvSnippet, levelSnippet, biasSnippet, depthSnippet, compareSnippet, gradSnippet) {
    const texture = this.value;
    let snippet;
    if (levelSnippet) {
      snippet = builder.generateTextureLevel(texture, textureProperty, uvSnippet, levelSnippet, depthSnippet);
    } else if (biasSnippet) {
      snippet = builder.generateTextureBias(texture, textureProperty, uvSnippet, biasSnippet, depthSnippet);
    } else if (gradSnippet) {
      snippet = builder.generateTextureGrad(texture, textureProperty, uvSnippet, gradSnippet, depthSnippet);
    } else if (compareSnippet) {
      snippet = builder.generateTextureCompare(texture, textureProperty, uvSnippet, compareSnippet, depthSnippet);
    } else if (this.sampler === false) {
      snippet = builder.generateTextureLoad(texture, textureProperty, uvSnippet, depthSnippet);
    } else {
      snippet = builder.generateTexture(texture, textureProperty, uvSnippet, depthSnippet);
    }
    return snippet;
  }
  generate(builder, output) {
    const properties = builder.getNodeProperties(this);
    const texture = this.value;
    if (!texture || texture.isTexture !== true) {
      throw new Error('TextureNode: Need a three.js texture.');
    }
    const textureProperty = super.generate(builder, 'property');
    if (output === 'sampler') {
      return textureProperty + '_sampler';
    } else if (builder.isReference(output)) {
      return textureProperty;
    } else {
      const nodeData = builder.getDataFromNode(this);
      let propertyName = nodeData.propertyName;
      if (propertyName === undefined) {
        const {
          uvNode,
          levelNode,
          biasNode,
          compareNode,
          depthNode,
          gradNode
        } = properties;
        const uvSnippet = this.generateUV(builder, uvNode);
        const levelSnippet = levelNode ? levelNode.build(builder, 'float') : null;
        const biasSnippet = biasNode ? biasNode.build(builder, 'float') : null;
        const depthSnippet = depthNode ? depthNode.build(builder, 'int') : null;
        const compareSnippet = compareNode ? compareNode.build(builder, 'float') : null;
        const gradSnippet = gradNode ? [gradNode[0].build(builder, 'vec2'), gradNode[1].build(builder, 'vec2')] : null;
        const nodeVar = builder.getVarFromNode(this);
        propertyName = builder.getPropertyName(nodeVar);
        const snippet = this.generateSnippet(builder, textureProperty, uvSnippet, levelSnippet, biasSnippet, depthSnippet, compareSnippet, gradSnippet);
        builder.addLineFlowCode(`${propertyName} = ${snippet}`);
        nodeData.snippet = snippet;
        nodeData.propertyName = propertyName;
      }
      let snippet = propertyName;
      const nodeType = this.getNodeType(builder);
      if (builder.needsToWorkingColorSpace(texture)) {
        snippet = toWorkingColorSpace(expression(snippet, nodeType), texture.colorSpace).setup(builder).build(builder, nodeType);
      }
      return builder.format(snippet, nodeType, output);
    }
  }
  setSampler(value) {
    this.sampler = value;
    return this;
  }
  getSampler() {
    return this.sampler;
  }

  // @TODO: Move to TSL

  uv(uvNode) {
    const textureNode = this.clone();
    textureNode.uvNode = nodeObject(uvNode);
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  blur(amountNode) {
    const textureNode = this.clone();
    textureNode.biasNode = nodeObject(amountNode).mul(maxMipLevel(textureNode));
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  level(levelNode) {
    const textureNode = this.clone();
    textureNode.levelNode = nodeObject(levelNode);
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  size(levelNode) {
    return textureSize(this, levelNode);
  }
  bias(biasNode) {
    const textureNode = this.clone();
    textureNode.biasNode = nodeObject(biasNode);
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  compare(compareNode) {
    const textureNode = this.clone();
    textureNode.compareNode = nodeObject(compareNode);
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  grad(gradNodeX, gradNodeY) {
    const textureNode = this.clone();
    textureNode.gradNode = [nodeObject(gradNodeX), nodeObject(gradNodeY)];
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }
  depth(depthNode) {
    const textureNode = this.clone();
    textureNode.depthNode = nodeObject(depthNode);
    textureNode.referenceNode = this.getSelf();
    return nodeObject(textureNode);
  }

  // --

  serialize(data) {
    super.serialize(data);
    data.value = this.value.toJSON(data.meta).uuid;
    data.sampler = this.sampler;
    data.updateMatrix = this.updateMatrix;
    data.updateType = this.updateType;
  }
  deserialize(data) {
    super.deserialize(data);
    this.value = data.meta.textures[data.value];
    this.sampler = data.sampler;
    this.updateMatrix = data.updateMatrix;
    this.updateType = data.updateType;
  }
  update() {
    const texture = this.value;
    const matrixUniform = this._matrixUniform;
    if (matrixUniform !== null) matrixUniform.value = texture.matrix;
    if (texture.matrixAutoUpdate === true) {
      texture.updateMatrix();
    }
  }
  clone() {
    const newNode = new this.constructor(this.value, this.uvNode, this.levelNode, this.biasNode);
    newNode.sampler = this.sampler;
    return newNode;
  }
}
TextureNode.type = /*@__PURE__*/registerNode('Texture', TextureNode);
const texture = /*@__PURE__*/nodeProxy(TextureNode);
const textureLoad = (...params) => texture(...params).setSampler(false);

let _canvas;
class ImageUtils {
  static getDataURL(image) {
    if (/^data:/i.test(image.src)) {
      return image.src;
    }
    if (typeof HTMLCanvasElement === 'undefined') {
      return image.src;
    }
    let canvas;
    if (image instanceof HTMLCanvasElement) {
      canvas = image;
    } else {
      if (_canvas === undefined) _canvas = createElementNS('canvas');
      _canvas.width = image.width;
      _canvas.height = image.height;
      const context = _canvas.getContext('2d');
      if (image instanceof ImageData) {
        context.putImageData(image, 0, 0);
      } else {
        context.drawImage(image, 0, 0, image.width, image.height);
      }
      canvas = _canvas;
    }
    if (canvas.width > 2048 || canvas.height > 2048) {
      console.warn('THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons', image);
      return canvas.toDataURL('image/jpeg', 0.6);
    } else {
      return canvas.toDataURL('image/png');
    }
  }
  static sRGBToLinear(image) {
    if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement || typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement || typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
      const canvas = createElementNS('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, image.width, image.height);
      const imageData = context.getImageData(0, 0, image.width, image.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i++) {
        data[i] = SRGBToLinear(data[i] / 255) * 255;
      }
      context.putImageData(imageData, 0, 0);
      return canvas;
    } else if (image.data) {
      const data = image.data.slice(0);
      for (let i = 0; i < data.length; i++) {
        if (data instanceof Uint8Array || data instanceof Uint8ClampedArray) {
          data[i] = Math.floor(SRGBToLinear(data[i] / 255) * 255);
        } else {
          // assuming float

          data[i] = SRGBToLinear(data[i]);
        }
      }
      return {
        data: data,
        width: image.width,
        height: image.height
      };
    } else {
      console.warn('THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied.');
      return image;
    }
  }
}

let _sourceId = 0;
class Source {
  constructor(data = null) {
    this.isSource = true;
    Object.defineProperty(this, 'id', {
      value: _sourceId++
    });
    this.uuid = generateUUID();
    this.data = data;
    this.dataReady = true;
    this.version = 0;
  }
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  toJSON(meta) {
    const isRootObject = meta === undefined || typeof meta === 'string';
    if (!isRootObject && meta.images[this.uuid] !== undefined) {
      return meta.images[this.uuid];
    }
    const output = {
      uuid: this.uuid,
      url: ''
    };
    const data = this.data;
    if (data !== null) {
      let url;
      if (Array.isArray(data)) {
        // cube texture

        url = [];
        for (let i = 0, l = data.length; i < l; i++) {
          if (data[i].isDataTexture) {
            url.push(serializeImage(data[i].image));
          } else {
            url.push(serializeImage(data[i]));
          }
        }
      } else {
        // texture

        url = serializeImage(data);
      }
      output.url = url;
    }
    if (!isRootObject) {
      meta.images[this.uuid] = output;
    }
    return output;
  }
}
function serializeImage(image) {
  if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement || typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement || typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    // default images

    return ImageUtils.getDataURL(image);
  } else {
    if (image.data) {
      // images of DataTexture

      return {
        data: Array.from(image.data),
        width: image.width,
        height: image.height,
        type: image.data.constructor.name
      };
    } else {
      console.warn('THREE.Texture: Unable to serialize Texture.');
      return {};
    }
  }
}

let _textureId = 0;
class Texture extends EventDispatcher {
  constructor(image = Texture.DEFAULT_IMAGE, mapping = Texture.DEFAULT_MAPPING, wrapS = ClampToEdgeWrapping, wrapT = ClampToEdgeWrapping, magFilter = LinearFilter, minFilter = LinearMipmapLinearFilter, format = RGBAFormat, type = UnsignedByteType, anisotropy = Texture.DEFAULT_ANISOTROPY, colorSpace = NoColorSpace) {
    super();
    this.isTexture = true;
    Object.defineProperty(this, 'id', {
      value: _textureId++
    });
    this.uuid = generateUUID();
    this.name = '';
    this.source = new Source(image);
    this.mipmaps = [];
    this.mapping = mapping;
    this.channel = 0;
    this.wrapS = wrapS;
    this.wrapT = wrapT;
    this.magFilter = magFilter;
    this.minFilter = minFilter;
    this.anisotropy = anisotropy;
    this.format = format;
    this.internalFormat = null;
    this.type = type;
    this.offset = new Vector2(0, 0);
    this.repeat = new Vector2(1, 1);
    this.center = new Vector2(0, 0);
    this.rotation = 0;
    this.matrixAutoUpdate = true;
    this.matrix = new Matrix3();
    this.generateMipmaps = true;
    this.premultiplyAlpha = false;
    this.flipY = true;
    this.unpackAlignment = 4; // valid values: 1, 2, 4, 8 (see http://www.khronos.org/opengles/sdk/docs/man/xhtml/glPixelStorei.xml)

    this.colorSpace = colorSpace;
    this.userData = {};
    this.version = 0;
    this.onUpdate = null;
    this.isRenderTargetTexture = false; // indicates whether a texture belongs to a render target or not
    this.pmremVersion = 0; // indicates whether this texture should be processed by PMREMGenerator or not (only relevant for render target textures)
  }
  get image() {
    return this.source.data;
  }
  set image(value = null) {
    this.source.data = value;
  }
  updateMatrix() {
    this.matrix.setUvTransform(this.offset.x, this.offset.y, this.repeat.x, this.repeat.y, this.rotation, this.center.x, this.center.y);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(source) {
    this.name = source.name;
    this.source = source.source;
    this.mipmaps = source.mipmaps.slice(0);
    this.mapping = source.mapping;
    this.channel = source.channel;
    this.wrapS = source.wrapS;
    this.wrapT = source.wrapT;
    this.magFilter = source.magFilter;
    this.minFilter = source.minFilter;
    this.anisotropy = source.anisotropy;
    this.format = source.format;
    this.internalFormat = source.internalFormat;
    this.type = source.type;
    this.offset.copy(source.offset);
    this.repeat.copy(source.repeat);
    this.center.copy(source.center);
    this.rotation = source.rotation;
    this.matrixAutoUpdate = source.matrixAutoUpdate;
    this.matrix.copy(source.matrix);
    this.generateMipmaps = source.generateMipmaps;
    this.premultiplyAlpha = source.premultiplyAlpha;
    this.flipY = source.flipY;
    this.unpackAlignment = source.unpackAlignment;
    this.colorSpace = source.colorSpace;
    this.userData = JSON.parse(JSON.stringify(source.userData));
    this.needsUpdate = true;
    return this;
  }
  toJSON(meta) {
    const isRootObject = meta === undefined || typeof meta === 'string';
    if (!isRootObject && meta.textures[this.uuid] !== undefined) {
      return meta.textures[this.uuid];
    }
    const output = {
      metadata: {
        version: 4.6,
        type: 'Texture',
        generator: 'Texture.toJSON'
      },
      uuid: this.uuid,
      name: this.name,
      image: this.source.toJSON(meta).uuid,
      mapping: this.mapping,
      channel: this.channel,
      repeat: [this.repeat.x, this.repeat.y],
      offset: [this.offset.x, this.offset.y],
      center: [this.center.x, this.center.y],
      rotation: this.rotation,
      wrap: [this.wrapS, this.wrapT],
      format: this.format,
      internalFormat: this.internalFormat,
      type: this.type,
      colorSpace: this.colorSpace,
      minFilter: this.minFilter,
      magFilter: this.magFilter,
      anisotropy: this.anisotropy,
      flipY: this.flipY,
      generateMipmaps: this.generateMipmaps,
      premultiplyAlpha: this.premultiplyAlpha,
      unpackAlignment: this.unpackAlignment
    };
    if (Object.keys(this.userData).length > 0) output.userData = this.userData;
    if (!isRootObject) {
      meta.textures[this.uuid] = output;
    }
    return output;
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
  transformUv(uv) {
    if (this.mapping !== UVMapping) return uv;
    uv.applyMatrix3(this.matrix);
    if (uv.x < 0 || uv.x > 1) {
      switch (this.wrapS) {
        case RepeatWrapping:
          uv.x = uv.x - Math.floor(uv.x);
          break;
        case ClampToEdgeWrapping:
          uv.x = uv.x < 0 ? 0 : 1;
          break;
        case MirroredRepeatWrapping:
          if (Math.abs(Math.floor(uv.x) % 2) === 1) {
            uv.x = Math.ceil(uv.x) - uv.x;
          } else {
            uv.x = uv.x - Math.floor(uv.x);
          }
          break;
      }
    }
    if (uv.y < 0 || uv.y > 1) {
      switch (this.wrapT) {
        case RepeatWrapping:
          uv.y = uv.y - Math.floor(uv.y);
          break;
        case ClampToEdgeWrapping:
          uv.y = uv.y < 0 ? 0 : 1;
          break;
        case MirroredRepeatWrapping:
          if (Math.abs(Math.floor(uv.y) % 2) === 1) {
            uv.y = Math.ceil(uv.y) - uv.y;
          } else {
            uv.y = uv.y - Math.floor(uv.y);
          }
          break;
      }
    }
    if (this.flipY) {
      uv.y = 1 - uv.y;
    }
    return uv;
  }
  set needsUpdate(value) {
    if (value === true) {
      this.version++;
      this.source.needsUpdate = true;
    }
  }
  set needsPMREMUpdate(value) {
    if (value === true) {
      this.pmremVersion++;
    }
  }
}
Texture.DEFAULT_IMAGE = null;
Texture.DEFAULT_MAPPING = UVMapping;
Texture.DEFAULT_ANISOTROPY = 1;

class FramebufferTexture extends Texture {
  constructor(width, height) {
    super({
      width,
      height
    });
    this.isFramebufferTexture = true;
    this.magFilter = NearestFilter;
    this.minFilter = NearestFilter;
    this.generateMipmaps = false;
    this.needsUpdate = true;
  }
}

const _size$7 = /*@__PURE__*/new Vector2();
class ViewportTextureNode extends TextureNode {
  constructor(uvNode = viewportUV, levelNode = null, framebufferTexture = null) {
    if (framebufferTexture === null) {
      framebufferTexture = new FramebufferTexture();
      framebufferTexture.minFilter = LinearMipmapLinearFilter;
    }
    super(framebufferTexture, uvNode, levelNode);
    this.generateMipmaps = false;
    this.isOutputTextureNode = true;
    this.updateBeforeType = NodeUpdateType.FRAME;
  }
  updateBefore(frame) {
    const renderer = frame.renderer;
    renderer.getDrawingBufferSize(_size$7);

    //

    const framebufferTexture = this.value;
    if (framebufferTexture.image.width !== _size$7.width || framebufferTexture.image.height !== _size$7.height) {
      framebufferTexture.image.width = _size$7.width;
      framebufferTexture.image.height = _size$7.height;
      framebufferTexture.needsUpdate = true;
    }

    //

    const currentGenerateMipmaps = framebufferTexture.generateMipmaps;
    framebufferTexture.generateMipmaps = this.generateMipmaps;
    renderer.copyFramebufferToTexture(framebufferTexture);
    framebufferTexture.generateMipmaps = currentGenerateMipmaps;
  }
  clone() {
    const viewportTextureNode = new this.constructor(this.uvNode, this.levelNode, this.value);
    viewportTextureNode.generateMipmaps = this.generateMipmaps;
    return viewportTextureNode;
  }
}
ViewportTextureNode.type = /*@__PURE__*/registerNode('ViewportTexture', ViewportTextureNode);

class DepthTexture extends Texture {
  constructor(width, height, type, mapping, wrapS, wrapT, magFilter, minFilter, anisotropy, format = DepthFormat) {
    if (format !== DepthFormat && format !== DepthStencilFormat) {
      throw new Error('DepthTexture format must be either THREE.DepthFormat or THREE.DepthStencilFormat');
    }
    if (type === undefined && format === DepthFormat) type = UnsignedIntType;
    if (type === undefined && format === DepthStencilFormat) type = UnsignedInt248Type;
    super(null, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy);
    this.isDepthTexture = true;
    this.image = {
      width: width,
      height: height
    };
    this.magFilter = magFilter !== undefined ? magFilter : NearestFilter;
    this.minFilter = minFilter !== undefined ? minFilter : NearestFilter;
    this.flipY = false;
    this.generateMipmaps = false;
    this.compareFunction = null;
  }
  copy(source) {
    super.copy(source);
    this.compareFunction = source.compareFunction;
    return this;
  }
  toJSON(meta) {
    const data = super.toJSON(meta);
    if (this.compareFunction !== null) data.compareFunction = this.compareFunction;
    return data;
  }
}

let sharedDepthbuffer = null;
class ViewportDepthTextureNode extends ViewportTextureNode {
  constructor(uvNode = viewportUV, levelNode = null) {
    if (sharedDepthbuffer === null) {
      sharedDepthbuffer = new DepthTexture();
    }
    super(uvNode, levelNode, sharedDepthbuffer);
  }
}
ViewportDepthTextureNode.type = /*@__PURE__*/registerNode('ViewportDepthTexture', ViewportDepthTextureNode);
const viewportDepthTexture = /*@__PURE__*/nodeProxy(ViewportDepthTextureNode);

class ViewportDepthNode extends Node$1 {
  constructor(scope, valueNode = null) {
    super('float');
    this.scope = scope;
    this.valueNode = valueNode;
    this.isViewportDepthNode = true;
  }
  generate(builder) {
    const {
      scope
    } = this;
    if (scope === ViewportDepthNode.DEPTH_BASE) {
      return builder.getFragDepth();
    }
    return super.generate(builder);
  }
  setup({
    camera
  }) {
    const {
      scope
    } = this;
    const value = this.valueNode;
    let node = null;
    if (scope === ViewportDepthNode.DEPTH_BASE) {
      if (value !== null) {
        node = depthBase().assign(value);
      }
    } else if (scope === ViewportDepthNode.DEPTH) {
      if (camera.isPerspectiveCamera) {
        node = viewZToPerspectiveDepth(positionView.z, cameraNear, cameraFar);
      } else {
        node = viewZToOrthographicDepth(positionView.z, cameraNear, cameraFar);
      }
    } else if (scope === ViewportDepthNode.LINEAR_DEPTH) {
      if (value !== null) {
        if (camera.isPerspectiveCamera) {
          const viewZ = perspectiveDepthToViewZ(value, cameraNear, cameraFar);
          node = viewZToOrthographicDepth(viewZ, cameraNear, cameraFar);
        } else {
          node = value;
        }
      } else {
        node = viewZToOrthographicDepth(positionView.z, cameraNear, cameraFar);
      }
    }
    return node;
  }
}
ViewportDepthNode.DEPTH_BASE = 'depthBase';
ViewportDepthNode.DEPTH = 'depth';
ViewportDepthNode.LINEAR_DEPTH = 'linearDepth';
ViewportDepthNode.type = /*@__PURE__*/registerNode('ViewportDepth', ViewportDepthNode);

// NOTE: viewZ, the z-coordinate in camera space, is negative for points in front of the camera

// -near maps to 0; -far maps to 1
const viewZToOrthographicDepth = (viewZ, near, far) => viewZ.add(near).div(near.sub(far));

// NOTE: https://twitter.com/gonnavis/status/1377183786949959682

// -near maps to 0; -far maps to 1
const viewZToPerspectiveDepth = (viewZ, near, far) => near.add(viewZ).mul(far).div(far.sub(near).mul(viewZ));

// maps perspective depth in [ 0, 1 ] to viewZ
const perspectiveDepthToViewZ = (depth, near, far) => near.mul(far).div(far.sub(near).mul(depth).sub(far));
const depthBase = /*@__PURE__*/nodeProxy(ViewportDepthNode, ViewportDepthNode.DEPTH_BASE);
const depth = /*@__PURE__*/nodeImmutable(ViewportDepthNode, ViewportDepthNode.DEPTH);
const linearDepth = /*@__PURE__*/nodeProxy(ViewportDepthNode, ViewportDepthNode.LINEAR_DEPTH);
/*@__PURE__*/linearDepth(viewportDepthTexture());
depth.assign = value => depthBase(value);

class SpriteSheetUVNode extends Node$1 {
  constructor(countNode, uvNode = uv(), frameNode = float(0)) {
    super('vec2');
    this.countNode = countNode;
    this.uvNode = uvNode;
    this.frameNode = frameNode;
  }
  setup() {
    const {
      frameNode,
      uvNode,
      countNode
    } = this;
    const {
      width,
      height
    } = countNode;
    const frameNum = frameNode.mod(width.mul(height)).floor();
    const column = frameNum.mod(width);
    const row = height.sub(frameNum.add(1).div(width).ceil());
    const scale = countNode.reciprocal();
    const uvFrameOffset = vec2(column, row);
    return uvNode.add(uvFrameOffset).mul(scale);
  }
}
SpriteSheetUVNode.type = /*@__PURE__*/registerNode('SpriteSheetUV', SpriteSheetUVNode);

class TriplanarTexturesNode extends Node$1 {
  constructor(textureXNode, textureYNode = null, textureZNode = null, scaleNode = float(1), positionNode = positionLocal, normalNode = normalLocal) {
    super('vec4');
    this.textureXNode = textureXNode;
    this.textureYNode = textureYNode;
    this.textureZNode = textureZNode;
    this.scaleNode = scaleNode;
    this.positionNode = positionNode;
    this.normalNode = normalNode;
  }
  setup() {
    const {
      textureXNode,
      textureYNode,
      textureZNode,
      scaleNode,
      positionNode,
      normalNode
    } = this;

    // Ref: https://github.com/keijiro/StandardTriplanar

    // Blending factor of triplanar mapping
    let bf = normalNode.abs().normalize();
    bf = bf.div(bf.dot(vec3(1.0)));

    // Triplanar mapping
    const tx = positionNode.yz.mul(scaleNode);
    const ty = positionNode.zx.mul(scaleNode);
    const tz = positionNode.xy.mul(scaleNode);

    // Base color
    const textureX = textureXNode.value;
    const textureY = textureYNode !== null ? textureYNode.value : textureX;
    const textureZ = textureZNode !== null ? textureZNode.value : textureX;
    const cx = texture(textureX, tx).mul(bf.x);
    const cy = texture(textureY, ty).mul(bf.y);
    const cz = texture(textureZ, tz).mul(bf.z);
    return add(cx, cy, cz);
  }
}
TriplanarTexturesNode.type = /*@__PURE__*/registerNode('TriplanarTextures', TriplanarTexturesNode);

const _vector1 = /*@__PURE__*/new Vector3();
const _vector2 = /*@__PURE__*/new Vector3();
const _normalMatrix = /*@__PURE__*/new Matrix3();
class Plane {
  constructor(normal = new Vector3(1, 0, 0), constant = 0) {
    this.isPlane = true;

    // normal is assumed to be normalized

    this.normal = normal;
    this.constant = constant;
  }
  set(normal, constant) {
    this.normal.copy(normal);
    this.constant = constant;
    return this;
  }
  setComponents(x, y, z, w) {
    this.normal.set(x, y, z);
    this.constant = w;
    return this;
  }
  setFromNormalAndCoplanarPoint(normal, point) {
    this.normal.copy(normal);
    this.constant = -point.dot(this.normal);
    return this;
  }
  setFromCoplanarPoints(a, b, c) {
    const normal = _vector1.subVectors(c, b).cross(_vector2.subVectors(a, b)).normalize();

    // Q: should an error be thrown if normal is zero (e.g. degenerate plane)?

    this.setFromNormalAndCoplanarPoint(normal, a);
    return this;
  }
  copy(plane) {
    this.normal.copy(plane.normal);
    this.constant = plane.constant;
    return this;
  }
  normalize() {
    // Note: will lead to a divide by zero if the plane is invalid.

    const inverseNormalLength = 1.0 / this.normal.length();
    this.normal.multiplyScalar(inverseNormalLength);
    this.constant *= inverseNormalLength;
    return this;
  }
  negate() {
    this.constant *= -1;
    this.normal.negate();
    return this;
  }
  distanceToPoint(point) {
    return this.normal.dot(point) + this.constant;
  }
  distanceToSphere(sphere) {
    return this.distanceToPoint(sphere.center) - sphere.radius;
  }
  projectPoint(point, target) {
    return target.copy(point).addScaledVector(this.normal, -this.distanceToPoint(point));
  }
  intersectLine(line, target) {
    const direction = line.delta(_vector1);
    const denominator = this.normal.dot(direction);
    if (denominator === 0) {
      // line is coplanar, return origin
      if (this.distanceToPoint(line.start) === 0) {
        return target.copy(line.start);
      }

      // Unsure if this is the correct method to handle this case.
      return null;
    }
    const t = -(line.start.dot(this.normal) + this.constant) / denominator;
    if (t < 0 || t > 1) {
      return null;
    }
    return target.copy(line.start).addScaledVector(direction, t);
  }
  intersectsLine(line) {
    // Note: this tests if a line intersects the plane, not whether it (or its end-points) are coplanar with it.

    const startSign = this.distanceToPoint(line.start);
    const endSign = this.distanceToPoint(line.end);
    return startSign < 0 && endSign > 0 || endSign < 0 && startSign > 0;
  }
  intersectsBox(box) {
    return box.intersectsPlane(this);
  }
  intersectsSphere(sphere) {
    return sphere.intersectsPlane(this);
  }
  coplanarPoint(target) {
    return target.copy(this.normal).multiplyScalar(-this.constant);
  }
  applyMatrix4(matrix, optionalNormalMatrix) {
    const normalMatrix = optionalNormalMatrix || _normalMatrix.getNormalMatrix(matrix);
    const referencePoint = this.coplanarPoint(_vector1).applyMatrix4(matrix);
    const normal = this.normal.applyMatrix3(normalMatrix).normalize();
    this.constant = -referencePoint.dot(normal);
    return this;
  }
  translate(offset) {
    this.constant -= offset.dot(this.normal);
    return this;
  }
  equals(plane) {
    return plane.normal.equals(this.normal) && plane.constant === this.constant;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}

const _matrix = /*@__PURE__*/new Matrix4();
const _quaternion$1 = /*@__PURE__*/new Quaternion();
class Euler {
  constructor(x = 0, y = 0, z = 0, order = Euler.DEFAULT_ORDER) {
    this.isEuler = true;
    this._x = x;
    this._y = y;
    this._z = z;
    this._order = order;
  }
  get x() {
    return this._x;
  }
  set x(value) {
    this._x = value;
    this._onChangeCallback();
  }
  get y() {
    return this._y;
  }
  set y(value) {
    this._y = value;
    this._onChangeCallback();
  }
  get z() {
    return this._z;
  }
  set z(value) {
    this._z = value;
    this._onChangeCallback();
  }
  get order() {
    return this._order;
  }
  set order(value) {
    this._order = value;
    this._onChangeCallback();
  }
  set(x, y, z, order = this._order) {
    this._x = x;
    this._y = y;
    this._z = z;
    this._order = order;
    this._onChangeCallback();
    return this;
  }
  clone() {
    return new this.constructor(this._x, this._y, this._z, this._order);
  }
  copy(euler) {
    this._x = euler._x;
    this._y = euler._y;
    this._z = euler._z;
    this._order = euler._order;
    this._onChangeCallback();
    return this;
  }
  setFromRotationMatrix(m, order = this._order, update = true) {
    // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

    const te = m.elements;
    const m11 = te[0],
      m12 = te[4],
      m13 = te[8];
    const m21 = te[1],
      m22 = te[5],
      m23 = te[9];
    const m31 = te[2],
      m32 = te[6],
      m33 = te[10];
    switch (order) {
      case 'XYZ':
        this._y = Math.asin(clamp$1(m13, -1, 1));
        if (Math.abs(m13) < 0.9999999) {
          this._x = Math.atan2(-m23, m33);
          this._z = Math.atan2(-m12, m11);
        } else {
          this._x = Math.atan2(m32, m22);
          this._z = 0;
        }
        break;
      case 'YXZ':
        this._x = Math.asin(-clamp$1(m23, -1, 1));
        if (Math.abs(m23) < 0.9999999) {
          this._y = Math.atan2(m13, m33);
          this._z = Math.atan2(m21, m22);
        } else {
          this._y = Math.atan2(-m31, m11);
          this._z = 0;
        }
        break;
      case 'ZXY':
        this._x = Math.asin(clamp$1(m32, -1, 1));
        if (Math.abs(m32) < 0.9999999) {
          this._y = Math.atan2(-m31, m33);
          this._z = Math.atan2(-m12, m22);
        } else {
          this._y = 0;
          this._z = Math.atan2(m21, m11);
        }
        break;
      case 'ZYX':
        this._y = Math.asin(-clamp$1(m31, -1, 1));
        if (Math.abs(m31) < 0.9999999) {
          this._x = Math.atan2(m32, m33);
          this._z = Math.atan2(m21, m11);
        } else {
          this._x = 0;
          this._z = Math.atan2(-m12, m22);
        }
        break;
      case 'YZX':
        this._z = Math.asin(clamp$1(m21, -1, 1));
        if (Math.abs(m21) < 0.9999999) {
          this._x = Math.atan2(-m23, m22);
          this._y = Math.atan2(-m31, m11);
        } else {
          this._x = 0;
          this._y = Math.atan2(m13, m33);
        }
        break;
      case 'XZY':
        this._z = Math.asin(-clamp$1(m12, -1, 1));
        if (Math.abs(m12) < 0.9999999) {
          this._x = Math.atan2(m32, m22);
          this._y = Math.atan2(m13, m11);
        } else {
          this._x = Math.atan2(-m23, m33);
          this._y = 0;
        }
        break;
      default:
        console.warn('THREE.Euler: .setFromRotationMatrix() encountered an unknown order: ' + order);
    }
    this._order = order;
    if (update === true) this._onChangeCallback();
    return this;
  }
  setFromQuaternion(q, order, update) {
    _matrix.makeRotationFromQuaternion(q);
    return this.setFromRotationMatrix(_matrix, order, update);
  }
  setFromVector3(v, order = this._order) {
    return this.set(v.x, v.y, v.z, order);
  }
  reorder(newOrder) {
    // WARNING: this discards revolution information -bhouston

    _quaternion$1.setFromEuler(this);
    return this.setFromQuaternion(_quaternion$1, newOrder);
  }
  equals(euler) {
    return euler._x === this._x && euler._y === this._y && euler._z === this._z && euler._order === this._order;
  }
  fromArray(array) {
    this._x = array[0];
    this._y = array[1];
    this._z = array[2];
    if (array[3] !== undefined) this._order = array[3];
    this._onChangeCallback();
    return this;
  }
  toArray(array = [], offset = 0) {
    array[offset] = this._x;
    array[offset + 1] = this._y;
    array[offset + 2] = this._z;
    array[offset + 3] = this._order;
    return array;
  }
  _onChange(callback) {
    this._onChangeCallback = callback;
    return this;
  }
  _onChangeCallback() {}
  *[Symbol.iterator]() {
    yield this._x;
    yield this._y;
    yield this._z;
    yield this._order;
  }
}
Euler.DEFAULT_ORDER = 'XYZ';

class Layers {
  constructor() {
    this.mask = 1 | 0;
  }
  set(channel) {
    this.mask = (1 << channel | 0) >>> 0;
  }
  enable(channel) {
    this.mask |= 1 << channel | 0;
  }
  enableAll() {
    this.mask = 0xffffffff | 0;
  }
  toggle(channel) {
    this.mask ^= 1 << channel | 0;
  }
  disable(channel) {
    this.mask &= ~(1 << channel | 0);
  }
  disableAll() {
    this.mask = 0;
  }
  test(layers) {
    return (this.mask & layers.mask) !== 0;
  }
  isEnabled(channel) {
    return (this.mask & (1 << channel | 0)) !== 0;
  }
}

let _object3DId = 0;
const _v1$3 = /*@__PURE__*/new Vector3();
const _q1 = /*@__PURE__*/new Quaternion();
const _m1$1 = /*@__PURE__*/new Matrix4();
const _target$1 = /*@__PURE__*/new Vector3();
const _position = /*@__PURE__*/new Vector3();
const _scale = /*@__PURE__*/new Vector3();
const _quaternion = /*@__PURE__*/new Quaternion();
const _xAxis = /*@__PURE__*/new Vector3(1, 0, 0);
const _yAxis = /*@__PURE__*/new Vector3(0, 1, 0);
const _zAxis = /*@__PURE__*/new Vector3(0, 0, 1);
const _addedEvent = {
  type: 'added'
};
const _removedEvent = {
  type: 'removed'
};
const _childaddedEvent = {
  type: 'childadded',
  child: null
};
const _childremovedEvent = {
  type: 'childremoved',
  child: null
};
class Object3D extends EventDispatcher {
  constructor() {
    super();
    this.isObject3D = true;
    Object.defineProperty(this, 'id', {
      value: _object3DId++
    });
    this.uuid = generateUUID();
    this.name = '';
    this.type = 'Object3D';
    this.parent = null;
    this.children = [];
    this.up = Object3D.DEFAULT_UP.clone();
    const position = new Vector3();
    const rotation = new Euler();
    const quaternion = new Quaternion();
    const scale = new Vector3(1, 1, 1);
    function onRotationChange() {
      quaternion.setFromEuler(rotation, false);
    }
    function onQuaternionChange() {
      rotation.setFromQuaternion(quaternion, undefined, false);
    }
    rotation._onChange(onRotationChange);
    quaternion._onChange(onQuaternionChange);
    Object.defineProperties(this, {
      position: {
        configurable: true,
        enumerable: true,
        value: position
      },
      rotation: {
        configurable: true,
        enumerable: true,
        value: rotation
      },
      quaternion: {
        configurable: true,
        enumerable: true,
        value: quaternion
      },
      scale: {
        configurable: true,
        enumerable: true,
        value: scale
      },
      modelViewMatrix: {
        value: new Matrix4()
      },
      normalMatrix: {
        value: new Matrix3()
      }
    });
    this.matrix = new Matrix4();
    this.matrixWorld = new Matrix4();
    this.matrixAutoUpdate = Object3D.DEFAULT_MATRIX_AUTO_UPDATE;
    this.matrixWorldAutoUpdate = Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE; // checked by the renderer
    this.matrixWorldNeedsUpdate = false;
    this.layers = new Layers();
    this.visible = true;
    this.castShadow = false;
    this.receiveShadow = false;
    this.frustumCulled = true;
    this.renderOrder = 0;
    this.animations = [];
    this.userData = {};
  }
  onBeforeShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */) {}
  onAfterShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */) {}
  onBeforeRender( /* renderer, scene, camera, geometry, material, group */) {}
  onAfterRender( /* renderer, scene, camera, geometry, material, group */) {}
  applyMatrix4(matrix) {
    if (this.matrixAutoUpdate) this.updateMatrix();
    this.matrix.premultiply(matrix);
    this.matrix.decompose(this.position, this.quaternion, this.scale);
  }
  applyQuaternion(q) {
    this.quaternion.premultiply(q);
    return this;
  }
  setRotationFromAxisAngle(axis, angle) {
    // assumes axis is normalized

    this.quaternion.setFromAxisAngle(axis, angle);
  }
  setRotationFromEuler(euler) {
    this.quaternion.setFromEuler(euler, true);
  }
  setRotationFromMatrix(m) {
    // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

    this.quaternion.setFromRotationMatrix(m);
  }
  setRotationFromQuaternion(q) {
    // assumes q is normalized

    this.quaternion.copy(q);
  }
  rotateOnAxis(axis, angle) {
    // rotate object on axis in object space
    // axis is assumed to be normalized

    _q1.setFromAxisAngle(axis, angle);
    this.quaternion.multiply(_q1);
    return this;
  }
  rotateOnWorldAxis(axis, angle) {
    // rotate object on axis in world space
    // axis is assumed to be normalized
    // method assumes no rotated parent

    _q1.setFromAxisAngle(axis, angle);
    this.quaternion.premultiply(_q1);
    return this;
  }
  rotateX(angle) {
    return this.rotateOnAxis(_xAxis, angle);
  }
  rotateY(angle) {
    return this.rotateOnAxis(_yAxis, angle);
  }
  rotateZ(angle) {
    return this.rotateOnAxis(_zAxis, angle);
  }
  translateOnAxis(axis, distance) {
    // translate object by distance along axis in object space
    // axis is assumed to be normalized

    _v1$3.copy(axis).applyQuaternion(this.quaternion);
    this.position.add(_v1$3.multiplyScalar(distance));
    return this;
  }
  translateX(distance) {
    return this.translateOnAxis(_xAxis, distance);
  }
  translateY(distance) {
    return this.translateOnAxis(_yAxis, distance);
  }
  translateZ(distance) {
    return this.translateOnAxis(_zAxis, distance);
  }
  localToWorld(vector) {
    this.updateWorldMatrix(true, false);
    return vector.applyMatrix4(this.matrixWorld);
  }
  worldToLocal(vector) {
    this.updateWorldMatrix(true, false);
    return vector.applyMatrix4(_m1$1.copy(this.matrixWorld).invert());
  }
  lookAt(x, y, z) {
    // This method does not support objects having non-uniformly-scaled parent(s)

    if (x.isVector3) {
      _target$1.copy(x);
    } else {
      _target$1.set(x, y, z);
    }
    const parent = this.parent;
    this.updateWorldMatrix(true, false);
    _position.setFromMatrixPosition(this.matrixWorld);
    if (this.isCamera || this.isLight) {
      _m1$1.lookAt(_position, _target$1, this.up);
    } else {
      _m1$1.lookAt(_target$1, _position, this.up);
    }
    this.quaternion.setFromRotationMatrix(_m1$1);
    if (parent) {
      _m1$1.extractRotation(parent.matrixWorld);
      _q1.setFromRotationMatrix(_m1$1);
      this.quaternion.premultiply(_q1.invert());
    }
  }
  add(object) {
    if (arguments.length > 1) {
      for (let i = 0; i < arguments.length; i++) {
        this.add(arguments[i]);
      }
      return this;
    }
    if (object === this) {
      console.error('THREE.Object3D.add: object can\'t be added as a child of itself.', object);
      return this;
    }
    if (object && object.isObject3D) {
      object.removeFromParent();
      object.parent = this;
      this.children.push(object);
      object.dispatchEvent(_addedEvent);
      _childaddedEvent.child = object;
      this.dispatchEvent(_childaddedEvent);
      _childaddedEvent.child = null;
    } else {
      console.error('THREE.Object3D.add: object not an instance of THREE.Object3D.', object);
    }
    return this;
  }
  remove(object) {
    if (arguments.length > 1) {
      for (let i = 0; i < arguments.length; i++) {
        this.remove(arguments[i]);
      }
      return this;
    }
    const index = this.children.indexOf(object);
    if (index !== -1) {
      object.parent = null;
      this.children.splice(index, 1);
      object.dispatchEvent(_removedEvent);
      _childremovedEvent.child = object;
      this.dispatchEvent(_childremovedEvent);
      _childremovedEvent.child = null;
    }
    return this;
  }
  removeFromParent() {
    const parent = this.parent;
    if (parent !== null) {
      parent.remove(this);
    }
    return this;
  }
  clear() {
    return this.remove(...this.children);
  }
  attach(object) {
    // adds object as a child of this, while maintaining the object's world transform

    // Note: This method does not support scene graphs having non-uniformly-scaled nodes(s)

    this.updateWorldMatrix(true, false);
    _m1$1.copy(this.matrixWorld).invert();
    if (object.parent !== null) {
      object.parent.updateWorldMatrix(true, false);
      _m1$1.multiply(object.parent.matrixWorld);
    }
    object.applyMatrix4(_m1$1);
    object.removeFromParent();
    object.parent = this;
    this.children.push(object);
    object.updateWorldMatrix(false, true);
    object.dispatchEvent(_addedEvent);
    _childaddedEvent.child = object;
    this.dispatchEvent(_childaddedEvent);
    _childaddedEvent.child = null;
    return this;
  }
  getObjectById(id) {
    return this.getObjectByProperty('id', id);
  }
  getObjectByName(name) {
    return this.getObjectByProperty('name', name);
  }
  getObjectByProperty(name, value) {
    if (this[name] === value) return this;
    for (let i = 0, l = this.children.length; i < l; i++) {
      const child = this.children[i];
      const object = child.getObjectByProperty(name, value);
      if (object !== undefined) {
        return object;
      }
    }
    return undefined;
  }
  getObjectsByProperty(name, value, result = []) {
    if (this[name] === value) result.push(this);
    const children = this.children;
    for (let i = 0, l = children.length; i < l; i++) {
      children[i].getObjectsByProperty(name, value, result);
    }
    return result;
  }
  getWorldPosition(target) {
    this.updateWorldMatrix(true, false);
    return target.setFromMatrixPosition(this.matrixWorld);
  }
  getWorldQuaternion(target) {
    this.updateWorldMatrix(true, false);
    this.matrixWorld.decompose(_position, target, _scale);
    return target;
  }
  getWorldScale(target) {
    this.updateWorldMatrix(true, false);
    this.matrixWorld.decompose(_position, _quaternion, target);
    return target;
  }
  getWorldDirection(target) {
    this.updateWorldMatrix(true, false);
    const e = this.matrixWorld.elements;
    return target.set(e[8], e[9], e[10]).normalize();
  }
  raycast( /* raycaster, intersects */) {}
  traverse(callback) {
    callback(this);
    const children = this.children;
    for (let i = 0, l = children.length; i < l; i++) {
      children[i].traverse(callback);
    }
  }
  traverseVisible(callback) {
    if (this.visible === false) return;
    callback(this);
    const children = this.children;
    for (let i = 0, l = children.length; i < l; i++) {
      children[i].traverseVisible(callback);
    }
  }
  traverseAncestors(callback) {
    const parent = this.parent;
    if (parent !== null) {
      callback(parent);
      parent.traverseAncestors(callback);
    }
  }
  updateMatrix() {
    this.matrix.compose(this.position, this.quaternion, this.scale);
    this.matrixWorldNeedsUpdate = true;
  }
  updateMatrixWorld(force) {
    if (this.matrixAutoUpdate) this.updateMatrix();
    if (this.matrixWorldNeedsUpdate || force) {
      if (this.matrixWorldAutoUpdate === true) {
        if (this.parent === null) {
          this.matrixWorld.copy(this.matrix);
        } else {
          this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
        }
      }
      this.matrixWorldNeedsUpdate = false;
      force = true;
    }

    // make sure descendants are updated if required

    const children = this.children;
    for (let i = 0, l = children.length; i < l; i++) {
      const child = children[i];
      child.updateMatrixWorld(force);
    }
  }
  updateWorldMatrix(updateParents, updateChildren) {
    const parent = this.parent;
    if (updateParents === true && parent !== null) {
      parent.updateWorldMatrix(true, false);
    }
    if (this.matrixAutoUpdate) this.updateMatrix();
    if (this.matrixWorldAutoUpdate === true) {
      if (this.parent === null) {
        this.matrixWorld.copy(this.matrix);
      } else {
        this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
      }
    }

    // make sure descendants are updated

    if (updateChildren === true) {
      const children = this.children;
      for (let i = 0, l = children.length; i < l; i++) {
        const child = children[i];
        child.updateWorldMatrix(false, true);
      }
    }
  }
  toJSON(meta) {
    // meta is a string when called from JSON.stringify
    const isRootObject = meta === undefined || typeof meta === 'string';
    const output = {};

    // meta is a hash used to collect geometries, materials.
    // not providing it implies that this is the root object
    // being serialized.
    if (isRootObject) {
      // initialize meta obj
      meta = {
        geometries: {},
        materials: {},
        textures: {},
        images: {},
        shapes: {},
        skeletons: {},
        animations: {},
        nodes: {}
      };
      output.metadata = {
        version: 4.6,
        type: 'Object',
        generator: 'Object3D.toJSON'
      };
    }

    // standard Object3D serialization

    const object = {};
    object.uuid = this.uuid;
    object.type = this.type;
    if (this.name !== '') object.name = this.name;
    if (this.castShadow === true) object.castShadow = true;
    if (this.receiveShadow === true) object.receiveShadow = true;
    if (this.visible === false) object.visible = false;
    if (this.frustumCulled === false) object.frustumCulled = false;
    if (this.renderOrder !== 0) object.renderOrder = this.renderOrder;
    if (Object.keys(this.userData).length > 0) object.userData = this.userData;
    object.layers = this.layers.mask;
    object.matrix = this.matrix.toArray();
    object.up = this.up.toArray();
    if (this.matrixAutoUpdate === false) object.matrixAutoUpdate = false;

    // object specific properties

    if (this.isInstancedMesh) {
      object.type = 'InstancedMesh';
      object.count = this.count;
      object.instanceMatrix = this.instanceMatrix.toJSON();
      if (this.instanceColor !== null) object.instanceColor = this.instanceColor.toJSON();
    }
    if (this.isBatchedMesh) {
      object.type = 'BatchedMesh';
      object.perObjectFrustumCulled = this.perObjectFrustumCulled;
      object.sortObjects = this.sortObjects;
      object.drawRanges = this._drawRanges;
      object.reservedRanges = this._reservedRanges;
      object.visibility = this._visibility;
      object.active = this._active;
      object.bounds = this._bounds.map(bound => ({
        boxInitialized: bound.boxInitialized,
        boxMin: bound.box.min.toArray(),
        boxMax: bound.box.max.toArray(),
        sphereInitialized: bound.sphereInitialized,
        sphereRadius: bound.sphere.radius,
        sphereCenter: bound.sphere.center.toArray()
      }));
      object.maxInstanceCount = this._maxInstanceCount;
      object.maxVertexCount = this._maxVertexCount;
      object.maxIndexCount = this._maxIndexCount;
      object.geometryInitialized = this._geometryInitialized;
      object.geometryCount = this._geometryCount;
      object.matricesTexture = this._matricesTexture.toJSON(meta);
      if (this._colorsTexture !== null) object.colorsTexture = this._colorsTexture.toJSON(meta);
      if (this.boundingSphere !== null) {
        object.boundingSphere = {
          center: object.boundingSphere.center.toArray(),
          radius: object.boundingSphere.radius
        };
      }
      if (this.boundingBox !== null) {
        object.boundingBox = {
          min: object.boundingBox.min.toArray(),
          max: object.boundingBox.max.toArray()
        };
      }
    }

    //

    function serialize(library, element) {
      if (library[element.uuid] === undefined) {
        library[element.uuid] = element.toJSON(meta);
      }
      return element.uuid;
    }
    if (this.isScene) {
      if (this.background) {
        if (this.background.isColor) {
          object.background = this.background.toJSON();
        } else if (this.background.isTexture) {
          object.background = this.background.toJSON(meta).uuid;
        }
      }
      if (this.environment && this.environment.isTexture && this.environment.isRenderTargetTexture !== true) {
        object.environment = this.environment.toJSON(meta).uuid;
      }
    } else if (this.isMesh || this.isLine || this.isPoints) {
      object.geometry = serialize(meta.geometries, this.geometry);
      const parameters = this.geometry.parameters;
      if (parameters !== undefined && parameters.shapes !== undefined) {
        const shapes = parameters.shapes;
        if (Array.isArray(shapes)) {
          for (let i = 0, l = shapes.length; i < l; i++) {
            const shape = shapes[i];
            serialize(meta.shapes, shape);
          }
        } else {
          serialize(meta.shapes, shapes);
        }
      }
    }
    if (this.isSkinnedMesh) {
      object.bindMode = this.bindMode;
      object.bindMatrix = this.bindMatrix.toArray();
      if (this.skeleton !== undefined) {
        serialize(meta.skeletons, this.skeleton);
        object.skeleton = this.skeleton.uuid;
      }
    }
    if (this.material !== undefined) {
      if (Array.isArray(this.material)) {
        const uuids = [];
        for (let i = 0, l = this.material.length; i < l; i++) {
          uuids.push(serialize(meta.materials, this.material[i]));
        }
        object.material = uuids;
      } else {
        object.material = serialize(meta.materials, this.material);
      }
    }

    //

    if (this.children.length > 0) {
      object.children = [];
      for (let i = 0; i < this.children.length; i++) {
        object.children.push(this.children[i].toJSON(meta).object);
      }
    }

    //

    if (this.animations.length > 0) {
      object.animations = [];
      for (let i = 0; i < this.animations.length; i++) {
        const animation = this.animations[i];
        object.animations.push(serialize(meta.animations, animation));
      }
    }
    if (isRootObject) {
      const geometries = extractFromCache(meta.geometries);
      const materials = extractFromCache(meta.materials);
      const textures = extractFromCache(meta.textures);
      const images = extractFromCache(meta.images);
      const shapes = extractFromCache(meta.shapes);
      const skeletons = extractFromCache(meta.skeletons);
      const animations = extractFromCache(meta.animations);
      const nodes = extractFromCache(meta.nodes);
      if (geometries.length > 0) output.geometries = geometries;
      if (materials.length > 0) output.materials = materials;
      if (textures.length > 0) output.textures = textures;
      if (images.length > 0) output.images = images;
      if (shapes.length > 0) output.shapes = shapes;
      if (skeletons.length > 0) output.skeletons = skeletons;
      if (animations.length > 0) output.animations = animations;
      if (nodes.length > 0) output.nodes = nodes;
    }
    output.object = object;
    return output;

    // extract data from the cache hash
    // remove metadata on each item
    // and return as array
    function extractFromCache(cache) {
      const values = [];
      for (const key in cache) {
        const data = cache[key];
        delete data.metadata;
        values.push(data);
      }
      return values;
    }
  }
  clone(recursive) {
    return new this.constructor().copy(this, recursive);
  }
  copy(source, recursive = true) {
    this.name = source.name;
    this.up.copy(source.up);
    this.position.copy(source.position);
    this.rotation.order = source.rotation.order;
    this.quaternion.copy(source.quaternion);
    this.scale.copy(source.scale);
    this.matrix.copy(source.matrix);
    this.matrixWorld.copy(source.matrixWorld);
    this.matrixAutoUpdate = source.matrixAutoUpdate;
    this.matrixWorldAutoUpdate = source.matrixWorldAutoUpdate;
    this.matrixWorldNeedsUpdate = source.matrixWorldNeedsUpdate;
    this.layers.mask = source.layers.mask;
    this.visible = source.visible;
    this.castShadow = source.castShadow;
    this.receiveShadow = source.receiveShadow;
    this.frustumCulled = source.frustumCulled;
    this.renderOrder = source.renderOrder;
    this.animations = source.animations.slice();
    this.userData = JSON.parse(JSON.stringify(source.userData));
    if (recursive === true) {
      for (let i = 0; i < source.children.length; i++) {
        const child = source.children[i];
        this.add(child.clone());
      }
    }
    return this;
  }
}
Object3D.DEFAULT_UP = /*@__PURE__*/new Vector3(0, 1, 0);
Object3D.DEFAULT_MATRIX_AUTO_UPDATE = true;
Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE = true;

/*
 In options, we can specify:
 * Texture parameters for an auto-generated target texture
 * depthBuffer/stencilBuffer: Booleans to indicate if we should generate these buffers
*/
class RenderTarget extends EventDispatcher {
  constructor(width = 1, height = 1, options = {}) {
    super();
    this.isRenderTarget = true;
    this.width = width;
    this.height = height;
    this.depth = 1;
    this.scissor = new Vector4(0, 0, width, height);
    this.scissorTest = false;
    this.viewport = new Vector4(0, 0, width, height);
    const image = {
      width: width,
      height: height,
      depth: 1
    };
    options = Object.assign({
      generateMipmaps: false,
      internalFormat: null,
      minFilter: LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
      resolveDepthBuffer: true,
      resolveStencilBuffer: true,
      depthTexture: null,
      samples: 0,
      count: 1
    }, options);
    const texture = new Texture(image, options.mapping, options.wrapS, options.wrapT, options.magFilter, options.minFilter, options.format, options.type, options.anisotropy, options.colorSpace);
    texture.flipY = false;
    texture.generateMipmaps = options.generateMipmaps;
    texture.internalFormat = options.internalFormat;
    this.textures = [];
    const count = options.count;
    for (let i = 0; i < count; i++) {
      this.textures[i] = texture.clone();
      this.textures[i].isRenderTargetTexture = true;
    }
    this.depthBuffer = options.depthBuffer;
    this.stencilBuffer = options.stencilBuffer;
    this.resolveDepthBuffer = options.resolveDepthBuffer;
    this.resolveStencilBuffer = options.resolveStencilBuffer;
    this.depthTexture = options.depthTexture;
    this.samples = options.samples;
  }
  get texture() {
    return this.textures[0];
  }
  set texture(value) {
    this.textures[0] = value;
  }
  setSize(width, height, depth = 1) {
    if (this.width !== width || this.height !== height || this.depth !== depth) {
      this.width = width;
      this.height = height;
      this.depth = depth;
      for (let i = 0, il = this.textures.length; i < il; i++) {
        this.textures[i].image.width = width;
        this.textures[i].image.height = height;
        this.textures[i].image.depth = depth;
      }
      this.dispose();
    }
    this.viewport.set(0, 0, width, height);
    this.scissor.set(0, 0, width, height);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(source) {
    this.width = source.width;
    this.height = source.height;
    this.depth = source.depth;
    this.scissor.copy(source.scissor);
    this.scissorTest = source.scissorTest;
    this.viewport.copy(source.viewport);
    this.textures.length = 0;
    for (let i = 0, il = source.textures.length; i < il; i++) {
      this.textures[i] = source.textures[i].clone();
      this.textures[i].isRenderTargetTexture = true;
    }

    // ensure image object is not shared, see #20328

    const image = Object.assign({}, source.texture.image);
    this.texture.source = new Source(image);
    this.depthBuffer = source.depthBuffer;
    this.stencilBuffer = source.stencilBuffer;
    this.resolveDepthBuffer = source.resolveDepthBuffer;
    this.resolveStencilBuffer = source.resolveStencilBuffer;
    if (source.depthTexture !== null) this.depthTexture = source.depthTexture.clone();
    this.samples = source.samples;
    return this;
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
}

const _reflectorPlane = new Plane();
const _normal$1 = new Vector3();
const _reflectorWorldPosition = new Vector3();
const _cameraWorldPosition = new Vector3();
const _rotationMatrix = new Matrix4();
const _lookAtPosition = new Vector3(0, 0, -1);
const clipPlane = new Vector4();
const _view = new Vector3();
const _target = new Vector3();
const _q = new Vector4();
const _size$6 = new Vector2();
const _defaultRT = new RenderTarget();
const _defaultUV = viewportUV.flipX();
let _inReflector = false;
class ReflectorNode extends TextureNode {
  constructor(parameters = {}) {
    super(_defaultRT.texture, _defaultUV);
    const {
      target = new Object3D(),
      resolution = 1,
      generateMipmaps = false,
      bounces = true
    } = parameters;

    //

    this.target = target;
    this.resolution = resolution;
    this.generateMipmaps = generateMipmaps;
    this.bounces = bounces;
    this.updateBeforeType = bounces ? NodeUpdateType.RENDER : NodeUpdateType.FRAME;
    this.virtualCameras = new WeakMap();
    this.renderTargets = new WeakMap();
  }
  _updateResolution(renderTarget, renderer) {
    const resolution = this.resolution;
    renderer.getDrawingBufferSize(_size$6);
    renderTarget.setSize(Math.round(_size$6.width * resolution), Math.round(_size$6.height * resolution));
  }
  setup(builder) {
    this._updateResolution(_defaultRT, builder.renderer);
    return super.setup(builder);
  }
  getTextureNode() {
    return this.textureNode;
  }
  getVirtualCamera(camera) {
    let virtualCamera = this.virtualCameras.get(camera);
    if (virtualCamera === undefined) {
      virtualCamera = camera.clone();
      this.virtualCameras.set(camera, virtualCamera);
    }
    return virtualCamera;
  }
  getRenderTarget(camera) {
    let renderTarget = this.renderTargets.get(camera);
    if (renderTarget === undefined) {
      renderTarget = new RenderTarget(0, 0, {
        type: HalfFloatType
      });
      if (this.generateMipmaps === true) {
        renderTarget.texture.minFilter = LinearMipMapLinearFilter;
        renderTarget.texture.generateMipmaps = true;
      }
      this.renderTargets.set(camera, renderTarget);
    }
    return renderTarget;
  }
  updateBefore(frame) {
    if (this.bounces === false && _inReflector) return false;
    _inReflector = true;
    const {
      scene,
      camera,
      renderer,
      material
    } = frame;
    const {
      target
    } = this;
    const virtualCamera = this.getVirtualCamera(camera);
    const renderTarget = this.getRenderTarget(virtualCamera);
    renderer.getDrawingBufferSize(_size$6);
    this._updateResolution(renderTarget, renderer);

    //

    _reflectorWorldPosition.setFromMatrixPosition(target.matrixWorld);
    _cameraWorldPosition.setFromMatrixPosition(camera.matrixWorld);
    _rotationMatrix.extractRotation(target.matrixWorld);
    _normal$1.set(0, 0, 1);
    _normal$1.applyMatrix4(_rotationMatrix);
    _view.subVectors(_reflectorWorldPosition, _cameraWorldPosition);

    // Avoid rendering when reflector is facing away

    if (_view.dot(_normal$1) > 0) return;
    _view.reflect(_normal$1).negate();
    _view.add(_reflectorWorldPosition);
    _rotationMatrix.extractRotation(camera.matrixWorld);
    _lookAtPosition.set(0, 0, -1);
    _lookAtPosition.applyMatrix4(_rotationMatrix);
    _lookAtPosition.add(_cameraWorldPosition);
    _target.subVectors(_reflectorWorldPosition, _lookAtPosition);
    _target.reflect(_normal$1).negate();
    _target.add(_reflectorWorldPosition);

    //

    virtualCamera.coordinateSystem = camera.coordinateSystem;
    virtualCamera.position.copy(_view);
    virtualCamera.up.set(0, 1, 0);
    virtualCamera.up.applyMatrix4(_rotationMatrix);
    virtualCamera.up.reflect(_normal$1);
    virtualCamera.lookAt(_target);
    virtualCamera.near = camera.near;
    virtualCamera.far = camera.far;
    virtualCamera.updateMatrixWorld();
    virtualCamera.projectionMatrix.copy(camera.projectionMatrix);

    // Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
    // Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
    _reflectorPlane.setFromNormalAndCoplanarPoint(_normal$1, _reflectorWorldPosition);
    _reflectorPlane.applyMatrix4(virtualCamera.matrixWorldInverse);
    clipPlane.set(_reflectorPlane.normal.x, _reflectorPlane.normal.y, _reflectorPlane.normal.z, _reflectorPlane.constant);
    const projectionMatrix = virtualCamera.projectionMatrix;
    _q.x = (Math.sign(clipPlane.x) + projectionMatrix.elements[8]) / projectionMatrix.elements[0];
    _q.y = (Math.sign(clipPlane.y) + projectionMatrix.elements[9]) / projectionMatrix.elements[5];
    _q.z = -1.0;
    _q.w = (1.0 + projectionMatrix.elements[10]) / projectionMatrix.elements[14];

    // Calculate the scaled plane vector
    clipPlane.multiplyScalar(1.0 / clipPlane.dot(_q));
    const clipBias = 0;

    // Replacing the third row of the projection matrix
    projectionMatrix.elements[2] = clipPlane.x;
    projectionMatrix.elements[6] = clipPlane.y;
    projectionMatrix.elements[10] = clipPlane.z - clipBias;
    projectionMatrix.elements[14] = clipPlane.w;

    //

    this.value = renderTarget.texture;
    material.visible = false;
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    renderer.setMRT(null);
    renderer.setRenderTarget(renderTarget);
    renderer.render(scene, virtualCamera);
    renderer.setMRT(currentMRT);
    renderer.setRenderTarget(currentRenderTarget);
    material.visible = true;
    _inReflector = false;
  }
}
ReflectorNode.type = /*@__PURE__*/registerNode('Reflector', ReflectorNode);

let _materialId = 0;
class Material extends EventDispatcher {
  constructor() {
    super();
    this.isMaterial = true;
    Object.defineProperty(this, 'id', {
      value: _materialId++
    });
    this.uuid = generateUUID();
    this.name = '';
    this.type = 'Material';
    this.blending = NormalBlending;
    this.side = FrontSide;
    this.vertexColors = false;
    this.opacity = 1;
    this.transparent = false;
    this.alphaHash = false;
    this.blendSrc = SrcAlphaFactor;
    this.blendDst = OneMinusSrcAlphaFactor;
    this.blendEquation = AddEquation;
    this.blendSrcAlpha = null;
    this.blendDstAlpha = null;
    this.blendEquationAlpha = null;
    this.blendColor = new Color(0, 0, 0);
    this.blendAlpha = 0;
    this.depthFunc = LessEqualDepth;
    this.depthTest = true;
    this.depthWrite = true;
    this.stencilWriteMask = 0xff;
    this.stencilFunc = AlwaysStencilFunc;
    this.stencilRef = 0;
    this.stencilFuncMask = 0xff;
    this.stencilFail = KeepStencilOp;
    this.stencilZFail = KeepStencilOp;
    this.stencilZPass = KeepStencilOp;
    this.stencilWrite = false;
    this.clippingPlanes = null;
    this.clipIntersection = false;
    this.clipShadows = false;
    this.shadowSide = null;
    this.colorWrite = true;
    this.precision = null; // override the renderer's default precision for this material

    this.polygonOffset = false;
    this.polygonOffsetFactor = 0;
    this.polygonOffsetUnits = 0;
    this.dithering = false;
    this.alphaToCoverage = false;
    this.premultipliedAlpha = false;
    this.forceSinglePass = false;
    this.visible = true;
    this.toneMapped = true;
    this.userData = {};
    this.version = 0;
    this._alphaTest = 0;
  }
  get alphaTest() {
    return this._alphaTest;
  }
  set alphaTest(value) {
    if (this._alphaTest > 0 !== value > 0) {
      this.version++;
    }
    this._alphaTest = value;
  }

  // onBeforeRender and onBeforeCompile only supported in WebGLRenderer

  onBeforeRender( /* renderer, scene, camera, geometry, object, group */) {}
  onBeforeCompile( /* shaderobject, renderer */) {}
  customProgramCacheKey() {
    return this.onBeforeCompile.toString();
  }
  setValues(values) {
    if (values === undefined) return;
    for (const key in values) {
      const newValue = values[key];
      if (newValue === undefined) {
        console.warn(`THREE.Material: parameter '${key}' has value of undefined.`);
        continue;
      }
      const currentValue = this[key];
      if (currentValue === undefined) {
        console.warn(`THREE.Material: '${key}' is not a property of THREE.${this.type}.`);
        continue;
      }
      if (currentValue && currentValue.isColor) {
        currentValue.set(newValue);
      } else if (currentValue && currentValue.isVector3 && newValue && newValue.isVector3) {
        currentValue.copy(newValue);
      } else {
        this[key] = newValue;
      }
    }
  }
  toJSON(meta) {
    const isRootObject = meta === undefined || typeof meta === 'string';
    if (isRootObject) {
      meta = {
        textures: {},
        images: {}
      };
    }
    const data = {
      metadata: {
        version: 4.6,
        type: 'Material',
        generator: 'Material.toJSON'
      }
    };

    // standard Material serialization
    data.uuid = this.uuid;
    data.type = this.type;
    if (this.name !== '') data.name = this.name;
    if (this.color && this.color.isColor) data.color = this.color.getHex();
    if (this.roughness !== undefined) data.roughness = this.roughness;
    if (this.metalness !== undefined) data.metalness = this.metalness;
    if (this.sheen !== undefined) data.sheen = this.sheen;
    if (this.sheenColor && this.sheenColor.isColor) data.sheenColor = this.sheenColor.getHex();
    if (this.sheenRoughness !== undefined) data.sheenRoughness = this.sheenRoughness;
    if (this.emissive && this.emissive.isColor) data.emissive = this.emissive.getHex();
    if (this.emissiveIntensity !== undefined && this.emissiveIntensity !== 1) data.emissiveIntensity = this.emissiveIntensity;
    if (this.specular && this.specular.isColor) data.specular = this.specular.getHex();
    if (this.specularIntensity !== undefined) data.specularIntensity = this.specularIntensity;
    if (this.specularColor && this.specularColor.isColor) data.specularColor = this.specularColor.getHex();
    if (this.shininess !== undefined) data.shininess = this.shininess;
    if (this.clearcoat !== undefined) data.clearcoat = this.clearcoat;
    if (this.clearcoatRoughness !== undefined) data.clearcoatRoughness = this.clearcoatRoughness;
    if (this.clearcoatMap && this.clearcoatMap.isTexture) {
      data.clearcoatMap = this.clearcoatMap.toJSON(meta).uuid;
    }
    if (this.clearcoatRoughnessMap && this.clearcoatRoughnessMap.isTexture) {
      data.clearcoatRoughnessMap = this.clearcoatRoughnessMap.toJSON(meta).uuid;
    }
    if (this.clearcoatNormalMap && this.clearcoatNormalMap.isTexture) {
      data.clearcoatNormalMap = this.clearcoatNormalMap.toJSON(meta).uuid;
      data.clearcoatNormalScale = this.clearcoatNormalScale.toArray();
    }
    if (this.dispersion !== undefined) data.dispersion = this.dispersion;
    if (this.iridescence !== undefined) data.iridescence = this.iridescence;
    if (this.iridescenceIOR !== undefined) data.iridescenceIOR = this.iridescenceIOR;
    if (this.iridescenceThicknessRange !== undefined) data.iridescenceThicknessRange = this.iridescenceThicknessRange;
    if (this.iridescenceMap && this.iridescenceMap.isTexture) {
      data.iridescenceMap = this.iridescenceMap.toJSON(meta).uuid;
    }
    if (this.iridescenceThicknessMap && this.iridescenceThicknessMap.isTexture) {
      data.iridescenceThicknessMap = this.iridescenceThicknessMap.toJSON(meta).uuid;
    }
    if (this.anisotropy !== undefined) data.anisotropy = this.anisotropy;
    if (this.anisotropyRotation !== undefined) data.anisotropyRotation = this.anisotropyRotation;
    if (this.anisotropyMap && this.anisotropyMap.isTexture) {
      data.anisotropyMap = this.anisotropyMap.toJSON(meta).uuid;
    }
    if (this.map && this.map.isTexture) data.map = this.map.toJSON(meta).uuid;
    if (this.matcap && this.matcap.isTexture) data.matcap = this.matcap.toJSON(meta).uuid;
    if (this.alphaMap && this.alphaMap.isTexture) data.alphaMap = this.alphaMap.toJSON(meta).uuid;
    if (this.lightMap && this.lightMap.isTexture) {
      data.lightMap = this.lightMap.toJSON(meta).uuid;
      data.lightMapIntensity = this.lightMapIntensity;
    }
    if (this.aoMap && this.aoMap.isTexture) {
      data.aoMap = this.aoMap.toJSON(meta).uuid;
      data.aoMapIntensity = this.aoMapIntensity;
    }
    if (this.bumpMap && this.bumpMap.isTexture) {
      data.bumpMap = this.bumpMap.toJSON(meta).uuid;
      data.bumpScale = this.bumpScale;
    }
    if (this.normalMap && this.normalMap.isTexture) {
      data.normalMap = this.normalMap.toJSON(meta).uuid;
      data.normalMapType = this.normalMapType;
      data.normalScale = this.normalScale.toArray();
    }
    if (this.displacementMap && this.displacementMap.isTexture) {
      data.displacementMap = this.displacementMap.toJSON(meta).uuid;
      data.displacementScale = this.displacementScale;
      data.displacementBias = this.displacementBias;
    }
    if (this.roughnessMap && this.roughnessMap.isTexture) data.roughnessMap = this.roughnessMap.toJSON(meta).uuid;
    if (this.metalnessMap && this.metalnessMap.isTexture) data.metalnessMap = this.metalnessMap.toJSON(meta).uuid;
    if (this.emissiveMap && this.emissiveMap.isTexture) data.emissiveMap = this.emissiveMap.toJSON(meta).uuid;
    if (this.specularMap && this.specularMap.isTexture) data.specularMap = this.specularMap.toJSON(meta).uuid;
    if (this.specularIntensityMap && this.specularIntensityMap.isTexture) data.specularIntensityMap = this.specularIntensityMap.toJSON(meta).uuid;
    if (this.specularColorMap && this.specularColorMap.isTexture) data.specularColorMap = this.specularColorMap.toJSON(meta).uuid;
    if (this.envMap && this.envMap.isTexture) {
      data.envMap = this.envMap.toJSON(meta).uuid;
      if (this.combine !== undefined) data.combine = this.combine;
    }
    if (this.envMapRotation !== undefined) data.envMapRotation = this.envMapRotation.toArray();
    if (this.envMapIntensity !== undefined) data.envMapIntensity = this.envMapIntensity;
    if (this.reflectivity !== undefined) data.reflectivity = this.reflectivity;
    if (this.refractionRatio !== undefined) data.refractionRatio = this.refractionRatio;
    if (this.gradientMap && this.gradientMap.isTexture) {
      data.gradientMap = this.gradientMap.toJSON(meta).uuid;
    }
    if (this.transmission !== undefined) data.transmission = this.transmission;
    if (this.transmissionMap && this.transmissionMap.isTexture) data.transmissionMap = this.transmissionMap.toJSON(meta).uuid;
    if (this.thickness !== undefined) data.thickness = this.thickness;
    if (this.thicknessMap && this.thicknessMap.isTexture) data.thicknessMap = this.thicknessMap.toJSON(meta).uuid;
    if (this.attenuationDistance !== undefined && this.attenuationDistance !== Infinity) data.attenuationDistance = this.attenuationDistance;
    if (this.attenuationColor !== undefined) data.attenuationColor = this.attenuationColor.getHex();
    if (this.size !== undefined) data.size = this.size;
    if (this.shadowSide !== null) data.shadowSide = this.shadowSide;
    if (this.sizeAttenuation !== undefined) data.sizeAttenuation = this.sizeAttenuation;
    if (this.blending !== NormalBlending) data.blending = this.blending;
    if (this.side !== FrontSide) data.side = this.side;
    if (this.vertexColors === true) data.vertexColors = true;
    if (this.opacity < 1) data.opacity = this.opacity;
    if (this.transparent === true) data.transparent = true;
    if (this.blendSrc !== SrcAlphaFactor) data.blendSrc = this.blendSrc;
    if (this.blendDst !== OneMinusSrcAlphaFactor) data.blendDst = this.blendDst;
    if (this.blendEquation !== AddEquation) data.blendEquation = this.blendEquation;
    if (this.blendSrcAlpha !== null) data.blendSrcAlpha = this.blendSrcAlpha;
    if (this.blendDstAlpha !== null) data.blendDstAlpha = this.blendDstAlpha;
    if (this.blendEquationAlpha !== null) data.blendEquationAlpha = this.blendEquationAlpha;
    if (this.blendColor && this.blendColor.isColor) data.blendColor = this.blendColor.getHex();
    if (this.blendAlpha !== 0) data.blendAlpha = this.blendAlpha;
    if (this.depthFunc !== LessEqualDepth) data.depthFunc = this.depthFunc;
    if (this.depthTest === false) data.depthTest = this.depthTest;
    if (this.depthWrite === false) data.depthWrite = this.depthWrite;
    if (this.colorWrite === false) data.colorWrite = this.colorWrite;
    if (this.stencilWriteMask !== 0xff) data.stencilWriteMask = this.stencilWriteMask;
    if (this.stencilFunc !== AlwaysStencilFunc) data.stencilFunc = this.stencilFunc;
    if (this.stencilRef !== 0) data.stencilRef = this.stencilRef;
    if (this.stencilFuncMask !== 0xff) data.stencilFuncMask = this.stencilFuncMask;
    if (this.stencilFail !== KeepStencilOp) data.stencilFail = this.stencilFail;
    if (this.stencilZFail !== KeepStencilOp) data.stencilZFail = this.stencilZFail;
    if (this.stencilZPass !== KeepStencilOp) data.stencilZPass = this.stencilZPass;
    if (this.stencilWrite === true) data.stencilWrite = this.stencilWrite;

    // rotation (SpriteMaterial)
    if (this.rotation !== undefined && this.rotation !== 0) data.rotation = this.rotation;
    if (this.polygonOffset === true) data.polygonOffset = true;
    if (this.polygonOffsetFactor !== 0) data.polygonOffsetFactor = this.polygonOffsetFactor;
    if (this.polygonOffsetUnits !== 0) data.polygonOffsetUnits = this.polygonOffsetUnits;
    if (this.linewidth !== undefined && this.linewidth !== 1) data.linewidth = this.linewidth;
    if (this.dashSize !== undefined) data.dashSize = this.dashSize;
    if (this.gapSize !== undefined) data.gapSize = this.gapSize;
    if (this.scale !== undefined) data.scale = this.scale;
    if (this.dithering === true) data.dithering = true;
    if (this.alphaTest > 0) data.alphaTest = this.alphaTest;
    if (this.alphaHash === true) data.alphaHash = true;
    if (this.alphaToCoverage === true) data.alphaToCoverage = true;
    if (this.premultipliedAlpha === true) data.premultipliedAlpha = true;
    if (this.forceSinglePass === true) data.forceSinglePass = true;
    if (this.wireframe === true) data.wireframe = true;
    if (this.wireframeLinewidth > 1) data.wireframeLinewidth = this.wireframeLinewidth;
    if (this.wireframeLinecap !== 'round') data.wireframeLinecap = this.wireframeLinecap;
    if (this.wireframeLinejoin !== 'round') data.wireframeLinejoin = this.wireframeLinejoin;
    if (this.flatShading === true) data.flatShading = true;
    if (this.visible === false) data.visible = false;
    if (this.toneMapped === false) data.toneMapped = false;
    if (this.fog === false) data.fog = false;
    if (Object.keys(this.userData).length > 0) data.userData = this.userData;

    // TODO: Copied from Object3D.toJSON

    function extractFromCache(cache) {
      const values = [];
      for (const key in cache) {
        const data = cache[key];
        delete data.metadata;
        values.push(data);
      }
      return values;
    }
    if (isRootObject) {
      const textures = extractFromCache(meta.textures);
      const images = extractFromCache(meta.images);
      if (textures.length > 0) data.textures = textures;
      if (images.length > 0) data.images = images;
    }
    return data;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(source) {
    this.name = source.name;
    this.blending = source.blending;
    this.side = source.side;
    this.vertexColors = source.vertexColors;
    this.opacity = source.opacity;
    this.transparent = source.transparent;
    this.blendSrc = source.blendSrc;
    this.blendDst = source.blendDst;
    this.blendEquation = source.blendEquation;
    this.blendSrcAlpha = source.blendSrcAlpha;
    this.blendDstAlpha = source.blendDstAlpha;
    this.blendEquationAlpha = source.blendEquationAlpha;
    this.blendColor.copy(source.blendColor);
    this.blendAlpha = source.blendAlpha;
    this.depthFunc = source.depthFunc;
    this.depthTest = source.depthTest;
    this.depthWrite = source.depthWrite;
    this.stencilWriteMask = source.stencilWriteMask;
    this.stencilFunc = source.stencilFunc;
    this.stencilRef = source.stencilRef;
    this.stencilFuncMask = source.stencilFuncMask;
    this.stencilFail = source.stencilFail;
    this.stencilZFail = source.stencilZFail;
    this.stencilZPass = source.stencilZPass;
    this.stencilWrite = source.stencilWrite;
    const srcPlanes = source.clippingPlanes;
    let dstPlanes = null;
    if (srcPlanes !== null) {
      const n = srcPlanes.length;
      dstPlanes = new Array(n);
      for (let i = 0; i !== n; ++i) {
        dstPlanes[i] = srcPlanes[i].clone();
      }
    }
    this.clippingPlanes = dstPlanes;
    this.clipIntersection = source.clipIntersection;
    this.clipShadows = source.clipShadows;
    this.shadowSide = source.shadowSide;
    this.colorWrite = source.colorWrite;
    this.precision = source.precision;
    this.polygonOffset = source.polygonOffset;
    this.polygonOffsetFactor = source.polygonOffsetFactor;
    this.polygonOffsetUnits = source.polygonOffsetUnits;
    this.dithering = source.dithering;
    this.alphaTest = source.alphaTest;
    this.alphaHash = source.alphaHash;
    this.alphaToCoverage = source.alphaToCoverage;
    this.premultipliedAlpha = source.premultipliedAlpha;
    this.forceSinglePass = source.forceSinglePass;
    this.visible = source.visible;
    this.toneMapped = source.toneMapped;
    this.userData = JSON.parse(JSON.stringify(source.userData));
    return this;
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
  set needsUpdate(value) {
    if (value === true) this.version++;
  }
  onBuild( /* shaderobject, renderer */
  ) {
    console.warn('Material: onBuild() has been removed.'); // @deprecated, r166
  }
}

const materialRefractionRatio = /*@__PURE__*/uniform(0).onReference(({
  material
}) => material).onRenderUpdate(({
  material
}) => material.refractionRatio);

const reflectView = /*@__PURE__*/positionViewDirection.negate().reflect(transformedNormalView);
const refractView = /*@__PURE__*/positionViewDirection.negate().refract(transformedNormalView, materialRefractionRatio);
const reflectVector = /*@__PURE__*/reflectView.transformDirection(cameraViewMatrix).toVar('reflectVector');
const refractVector = /*@__PURE__*/refractView.transformDirection(cameraViewMatrix).toVar('reflectVector');

class CubeTextureNode extends TextureNode {
  constructor(value, uvNode = null, levelNode = null, biasNode = null) {
    super(value, uvNode, levelNode, biasNode);
    this.isCubeTextureNode = true;
  }
  getInputType( /*builder*/
  ) {
    return 'cubeTexture';
  }
  getDefaultUV() {
    const texture = this.value;
    if (texture.mapping === CubeReflectionMapping) {
      return reflectVector;
    } else if (texture.mapping === CubeRefractionMapping) {
      return refractVector;
    } else {
      console.error('THREE.CubeTextureNode: Mapping "%s" not supported.', texture.mapping);
      return vec3(0, 0, 0);
    }
  }
  setUpdateMatrix( /*updateMatrix*/) {} // Ignore .updateMatrix for CubeTextureNode

  setupUV(builder, uvNode) {
    const texture = this.value;
    if (builder.renderer.coordinateSystem === WebGPUCoordinateSystem || !texture.isRenderTargetTexture) {
      return vec3(uvNode.x.negate(), uvNode.yz);
    } else {
      return uvNode;
    }
  }
  generateUV(builder, cubeUV) {
    return cubeUV.build(builder, 'vec3');
  }
}
CubeTextureNode.type = /*@__PURE__*/registerNode('CubeTexture', CubeTextureNode);
const cubeTexture = /*@__PURE__*/nodeProxy(CubeTextureNode);

class BufferNode extends UniformNode {
  constructor(value, bufferType, bufferCount = 0) {
    super(value, bufferType);
    this.isBufferNode = true;
    this.bufferType = bufferType;
    this.bufferCount = bufferCount;
  }
  getElementType(builder) {
    return this.getNodeType(builder);
  }
  getInputType( /*builder*/
  ) {
    return 'buffer';
  }
}
BufferNode.type = /*@__PURE__*/registerNode('Buffer', BufferNode);
const buffer = (value, type, count) => nodeObject(new BufferNode(value, type, count));

class UniformArrayElementNode extends ArrayElementNode {
  constructor(arrayBuffer, indexNode) {
    super(arrayBuffer, indexNode);
    this.isArrayBufferElementNode = true;
  }
  getNodeType(builder) {
    return this.node.getElementType(builder);
  }
  generate(builder) {
    const snippet = super.generate(builder);
    const type = this.getNodeType();
    return builder.format(snippet, 'vec4', type);
  }
}
class UniformArrayNode extends BufferNode {
  constructor(value, elementType = null) {
    super(null, 'vec4');
    this.array = value;
    this.elementType = elementType;
    this._elementType = null;
    this._elementLength = 0;
    this.updateType = NodeUpdateType.RENDER;
    this.isArrayBufferNode = true;
  }
  getElementType() {
    return this.elementType || this._elementType;
  }
  getElementLength() {
    return this._elementLength;
  }
  update( /*frame*/
  ) {
    const {
      array,
      value
    } = this;
    const elementLength = this.getElementLength();
    const elementType = this.getElementType();
    if (elementLength === 1) {
      for (let i = 0; i < array.length; i++) {
        const index = i * 4;
        value[index] = array[i];
      }
    } else if (elementType === 'color') {
      for (let i = 0; i < array.length; i++) {
        const index = i * 4;
        const vector = array[i];
        value[index] = vector.r;
        value[index + 1] = vector.g;
        value[index + 2] = vector.b || 0;
        //value[ index + 3 ] = vector.a || 0;
      }
    } else {
      for (let i = 0; i < array.length; i++) {
        const index = i * 4;
        const vector = array[i];
        value[index] = vector.x;
        value[index + 1] = vector.y;
        value[index + 2] = vector.z || 0;
        value[index + 3] = vector.w || 0;
      }
    }
  }
  setup(builder) {
    const length = this.array.length;
    this._elementType = this.elementType === null ? getValueType(this.array[0]) : this.elementType;
    this._elementLength = builder.getTypeLength(this._elementType);
    let arrayType = Float32Array;
    if (this._elementType.charAt(0) === 'i') arrayType = Int32Array;else if (this._elementType.charAt(0) === 'u') arrayType = Uint32Array;
    this.value = new arrayType(length * 4);
    this.bufferCount = length;
    this.bufferType = builder.changeComponentType('vec4', builder.getComponentType(this._elementType));
    return super.setup(builder);
  }
  element(indexNode) {
    return nodeObject(new UniformArrayElementNode(this, nodeObject(indexNode)));
  }
}
UniformArrayNode.type = /*@__PURE__*/registerNode('UniformArray', UniformArrayNode);
const uniformArray = (values, nodeType) => nodeObject(new UniformArrayNode(values, nodeType));

class ReferenceElementNode extends ArrayElementNode {
  constructor(referenceNode, indexNode) {
    super(referenceNode, indexNode);
    this.referenceNode = referenceNode;
    this.isReferenceElementNode = true;
  }
  getNodeType() {
    return this.referenceNode.uniformType;
  }
  generate(builder) {
    const snippet = super.generate(builder);
    const arrayType = this.referenceNode.getNodeType();
    const elementType = this.getNodeType();
    return builder.format(snippet, arrayType, elementType);
  }
}

// TODO: Extends this from ReferenceBaseNode
class ReferenceNode extends Node$1 {
  constructor(property, uniformType, object = null, count = null) {
    super();
    this.property = property;
    this.uniformType = uniformType;
    this.object = object;
    this.count = count;
    this.properties = property.split('.');
    this.reference = object;
    this.node = null;
    this.updateType = NodeUpdateType.OBJECT;
  }
  element(indexNode) {
    return nodeObject(new ReferenceElementNode(this, nodeObject(indexNode)));
  }
  setNodeType(uniformType) {
    let node = null;
    if (this.count !== null) {
      node = buffer(null, uniformType, this.count);
    } else if (Array.isArray(this.getValueFromReference())) {
      node = uniformArray(null, uniformType);
    } else if (uniformType === 'texture') {
      node = texture(null);
    } else if (uniformType === 'cubeTexture') {
      node = cubeTexture(null);
    } else {
      node = uniform(null, uniformType);
    }
    this.node = node.getSelf();
  }
  getNodeType(builder) {
    if (this.node === null) {
      this.updateValue();
    }
    return this.node.getNodeType(builder);
  }
  getValueFromReference(object = this.reference) {
    const {
      properties
    } = this;
    let value = object[properties[0]];
    for (let i = 1; i < properties.length; i++) {
      value = value[properties[i]];
    }
    return value;
  }
  updateReference(state) {
    this.reference = this.object !== null ? this.object : state.object;
    return this.reference;
  }
  setup() {
    this.updateValue();
    return this.node;
  }
  update( /*frame*/
  ) {
    this.updateValue();
  }
  updateValue() {
    if (this.node === null) this.setNodeType(this.uniformType);
    const value = this.getValueFromReference();
    if (Array.isArray(value)) {
      this.node.array = value;
    } else {
      this.node.value = value;
    }
  }
}
ReferenceNode.type = /*@__PURE__*/registerNode('Reference', ReferenceNode);
const reference = (name, type, object) => nodeObject(new ReferenceNode(name, type, object));
const referenceBuffer = (name, type, count, object) => nodeObject(new ReferenceNode(name, type, object, count));

class MaterialReferenceNode extends ReferenceNode {
  constructor(property, inputType, material = null) {
    super(property, inputType, material);
    this.material = material;

    //this.updateType = NodeUpdateType.RENDER;

    this.isMaterialReferenceNode = true;
  }

  /*setNodeType( node ) {
  		super.setNodeType( node );
  		this.node.groupNode = renderGroup;
  	}*/

  updateReference(state) {
    this.reference = this.material !== null ? this.material : state.material;
    return this.reference;
  }
}
MaterialReferenceNode.type = /*@__PURE__*/registerNode('MaterialReference', MaterialReferenceNode);
const materialReference = (name, type, material) => nodeObject(new MaterialReferenceNode(name, type, material));

const tangentGeometry = /*@__PURE__*/Fn(builder => {
  if (builder.geometry.hasAttribute('tangent') === false) {
    builder.geometry.computeTangents();
  }
  return attribute('tangent', 'vec4');
})();
const tangentLocal = /*@__PURE__*/tangentGeometry.xyz.toVar('tangentLocal');
const tangentView = /*@__PURE__*/modelViewMatrix.mul(vec4(tangentLocal, 0)).xyz.varying('v_tangentView').normalize().toVar('tangentView');

const getBitangent = crossNormalTangent => crossNormalTangent.mul(tangentGeometry.w).xyz;
const bitangentView = /*@__PURE__*/varying(getBitangent(normalView.cross(tangentView)), 'v_bitangentView').normalize().toVar('bitangentView');

const TBNViewMatrix = /*@__PURE__*/mat3(tangentView, bitangentView, normalView);

// Normal Mapping Without Precomputed Tangents
// http://www.thetenthplanet.de/archives/1180

const perturbNormal2Arb = /*@__PURE__*/Fn(inputs => {
  const {
    eye_pos,
    surf_norm,
    mapN,
    uv
  } = inputs;
  const q0 = eye_pos.dFdx();
  const q1 = eye_pos.dFdy();
  const st0 = uv.dFdx();
  const st1 = uv.dFdy();
  const N = surf_norm; // normalized

  const q1perp = q1.cross(N);
  const q0perp = N.cross(q0);
  const T = q1perp.mul(st0.x).add(q0perp.mul(st1.x));
  const B = q1perp.mul(st0.y).add(q0perp.mul(st1.y));
  const det = T.dot(T).max(B.dot(B));
  const scale = faceDirection.mul(det.inverseSqrt());
  return add(T.mul(mapN.x, scale), B.mul(mapN.y, scale), N.mul(mapN.z)).normalize();
});
class NormalMapNode extends TempNode {
  constructor(node, scaleNode = null) {
    super('vec3');
    this.node = node;
    this.scaleNode = scaleNode;
    this.normalMapType = TangentSpaceNormalMap;
  }
  setup(builder) {
    const {
      normalMapType,
      scaleNode
    } = this;
    let normalMap = this.node.mul(2.0).sub(1.0);
    if (scaleNode !== null) {
      normalMap = vec3(normalMap.xy.mul(scaleNode), normalMap.z);
    }
    let outputNode = null;
    if (normalMapType === ObjectSpaceNormalMap) {
      outputNode = modelNormalMatrix.mul(normalMap).normalize();
    } else if (normalMapType === TangentSpaceNormalMap) {
      const tangent = builder.hasGeometryAttribute('tangent');
      if (tangent === true) {
        outputNode = TBNViewMatrix.mul(normalMap).normalize();
      } else {
        outputNode = perturbNormal2Arb({
          eye_pos: positionView,
          surf_norm: normalView,
          mapN: normalMap,
          uv: uv()
        });
      }
    }
    return outputNode;
  }
}
NormalMapNode.type = /*@__PURE__*/registerNode('NormalMap', NormalMapNode);
const normalMap = /*@__PURE__*/nodeProxy(NormalMapNode);

// Bump Mapping Unparametrized Surfaces on the GPU by Morten S. Mikkelsen
// https://mmikk.github.io/papers3d/mm_sfgrad_bump.pdf

const dHdxy_fwd = Fn(({
  textureNode,
  bumpScale
}) => {
  // It's used to preserve the same TextureNode instance
  const sampleTexture = callback => textureNode.cache().context({
    getUV: texNode => callback(texNode.uvNode || uv()),
    forceUVContext: true
  });
  const Hll = float(sampleTexture(uvNode => uvNode));
  return vec2(float(sampleTexture(uvNode => uvNode.add(uvNode.dFdx()))).sub(Hll), float(sampleTexture(uvNode => uvNode.add(uvNode.dFdy()))).sub(Hll)).mul(bumpScale);
});

// Evaluate the derivative of the height w.r.t. screen-space using forward differencing (listing 2)

const perturbNormalArb = Fn(inputs => {
  const {
    surf_pos,
    surf_norm,
    dHdxy
  } = inputs;

  // normalize is done to ensure that the bump map looks the same regardless of the texture's scale
  const vSigmaX = surf_pos.dFdx().normalize();
  const vSigmaY = surf_pos.dFdy().normalize();
  const vN = surf_norm; // normalized

  const R1 = vSigmaY.cross(vN);
  const R2 = vN.cross(vSigmaX);
  const fDet = vSigmaX.dot(R1).mul(faceDirection);
  const vGrad = fDet.sign().mul(dHdxy.x.mul(R1).add(dHdxy.y.mul(R2)));
  return fDet.abs().mul(surf_norm).sub(vGrad).normalize();
});
class BumpMapNode extends TempNode {
  constructor(textureNode, scaleNode = null) {
    super('vec3');
    this.textureNode = textureNode;
    this.scaleNode = scaleNode;
  }
  setup() {
    const bumpScale = this.scaleNode !== null ? this.scaleNode : 1;
    const dHdxy = dHdxy_fwd({
      textureNode: this.textureNode,
      bumpScale
    });
    return perturbNormalArb({
      surf_pos: positionView,
      surf_norm: normalView,
      dHdxy
    });
  }
}
const bumpMap = /*@__PURE__*/nodeProxy(BumpMapNode);

const _propertyCache = new Map();
class MaterialNode extends Node$1 {
  constructor(scope) {
    super();
    this.scope = scope;
  }
  getCache(property, type) {
    let node = _propertyCache.get(property);
    if (node === undefined) {
      node = materialReference(property, type);
      _propertyCache.set(property, node);
    }
    return node;
  }
  getFloat(property) {
    return this.getCache(property, 'float');
  }
  getColor(property) {
    return this.getCache(property, 'color');
  }
  getTexture(property) {
    return this.getCache(property === 'map' ? 'map' : property + 'Map', 'texture');
  }
  setup(builder) {
    const material = builder.context.material;
    const scope = this.scope;
    let node = null;
    if (scope === MaterialNode.COLOR) {
      const colorNode = material.color !== undefined ? this.getColor(scope) : vec3();
      if (material.map && material.map.isTexture === true) {
        node = colorNode.mul(this.getTexture('map'));
      } else {
        node = colorNode;
      }
    } else if (scope === MaterialNode.OPACITY) {
      const opacityNode = this.getFloat(scope);
      if (material.alphaMap && material.alphaMap.isTexture === true) {
        node = opacityNode.mul(this.getTexture('alpha'));
      } else {
        node = opacityNode;
      }
    } else if (scope === MaterialNode.SPECULAR_STRENGTH) {
      if (material.specularMap && material.specularMap.isTexture === true) {
        node = this.getTexture('specular').r;
      } else {
        node = float(1);
      }
    } else if (scope === MaterialNode.SPECULAR_INTENSITY) {
      const specularIntensity = this.getFloat(scope);
      if (material.specularMap) {
        node = specularIntensity.mul(this.getTexture(scope).a);
      } else {
        node = specularIntensity;
      }
    } else if (scope === MaterialNode.SPECULAR_COLOR) {
      const specularColorNode = this.getColor(scope);
      if (material.specularColorMap && material.specularColorMap.isTexture === true) {
        node = specularColorNode.mul(this.getTexture(scope).rgb);
      } else {
        node = specularColorNode;
      }
    } else if (scope === MaterialNode.ROUGHNESS) {
      // TODO: cleanup similar branches

      const roughnessNode = this.getFloat(scope);
      if (material.roughnessMap && material.roughnessMap.isTexture === true) {
        node = roughnessNode.mul(this.getTexture(scope).g);
      } else {
        node = roughnessNode;
      }
    } else if (scope === MaterialNode.METALNESS) {
      const metalnessNode = this.getFloat(scope);
      if (material.metalnessMap && material.metalnessMap.isTexture === true) {
        node = metalnessNode.mul(this.getTexture(scope).b);
      } else {
        node = metalnessNode;
      }
    } else if (scope === MaterialNode.EMISSIVE) {
      const emissiveIntensityNode = this.getFloat('emissiveIntensity');
      const emissiveNode = this.getColor(scope).mul(emissiveIntensityNode);
      if (material.emissiveMap && material.emissiveMap.isTexture === true) {
        node = emissiveNode.mul(this.getTexture(scope));
      } else {
        node = emissiveNode;
      }
    } else if (scope === MaterialNode.NORMAL) {
      if (material.normalMap) {
        node = normalMap(this.getTexture('normal'), this.getCache('normalScale', 'vec2'));
      } else if (material.bumpMap) {
        node = bumpMap(this.getTexture('bump').r, this.getFloat('bumpScale'));
      } else {
        node = normalView;
      }
    } else if (scope === MaterialNode.CLEARCOAT) {
      const clearcoatNode = this.getFloat(scope);
      if (material.clearcoatMap && material.clearcoatMap.isTexture === true) {
        node = clearcoatNode.mul(this.getTexture(scope).r);
      } else {
        node = clearcoatNode;
      }
    } else if (scope === MaterialNode.CLEARCOAT_ROUGHNESS) {
      const clearcoatRoughnessNode = this.getFloat(scope);
      if (material.clearcoatRoughnessMap && material.clearcoatRoughnessMap.isTexture === true) {
        node = clearcoatRoughnessNode.mul(this.getTexture(scope).r);
      } else {
        node = clearcoatRoughnessNode;
      }
    } else if (scope === MaterialNode.CLEARCOAT_NORMAL) {
      if (material.clearcoatNormalMap) {
        node = normalMap(this.getTexture(scope), this.getCache(scope + 'Scale', 'vec2'));
      } else {
        node = normalView;
      }
    } else if (scope === MaterialNode.SHEEN) {
      const sheenNode = this.getColor('sheenColor').mul(this.getFloat('sheen')); // Move this mul() to CPU

      if (material.sheenColorMap && material.sheenColorMap.isTexture === true) {
        node = sheenNode.mul(this.getTexture('sheenColor').rgb);
      } else {
        node = sheenNode;
      }
    } else if (scope === MaterialNode.SHEEN_ROUGHNESS) {
      const sheenRoughnessNode = this.getFloat(scope);
      if (material.sheenRoughnessMap && material.sheenRoughnessMap.isTexture === true) {
        node = sheenRoughnessNode.mul(this.getTexture(scope).a);
      } else {
        node = sheenRoughnessNode;
      }
      node = node.clamp(0.07, 1.0);
    } else if (scope === MaterialNode.ANISOTROPY) {
      if (material.anisotropyMap && material.anisotropyMap.isTexture === true) {
        const anisotropyPolar = this.getTexture(scope);
        const anisotropyMat = mat2(materialAnisotropyVector.x, materialAnisotropyVector.y, materialAnisotropyVector.y.negate(), materialAnisotropyVector.x);
        node = anisotropyMat.mul(anisotropyPolar.rg.mul(2.0).sub(vec2(1.0)).normalize().mul(anisotropyPolar.b));
      } else {
        node = materialAnisotropyVector;
      }
    } else if (scope === MaterialNode.IRIDESCENCE_THICKNESS) {
      const iridescenceThicknessMaximum = reference('1', 'float', material.iridescenceThicknessRange);
      if (material.iridescenceThicknessMap) {
        const iridescenceThicknessMinimum = reference('0', 'float', material.iridescenceThicknessRange);
        node = iridescenceThicknessMaximum.sub(iridescenceThicknessMinimum).mul(this.getTexture(scope).g).add(iridescenceThicknessMinimum);
      } else {
        node = iridescenceThicknessMaximum;
      }
    } else if (scope === MaterialNode.TRANSMISSION) {
      const transmissionNode = this.getFloat(scope);
      if (material.transmissionMap) {
        node = transmissionNode.mul(this.getTexture(scope).r);
      } else {
        node = transmissionNode;
      }
    } else if (scope === MaterialNode.THICKNESS) {
      const thicknessNode = this.getFloat(scope);
      if (material.thicknessMap) {
        node = thicknessNode.mul(this.getTexture(scope).g);
      } else {
        node = thicknessNode;
      }
    } else if (scope === MaterialNode.IOR) {
      node = this.getFloat(scope);
    } else if (scope === MaterialNode.LIGHT_MAP) {
      node = this.getTexture(scope).rgb.mul(this.getFloat('lightMapIntensity'));
    } else if (scope === MaterialNode.AO_MAP) {
      node = this.getTexture(scope).r.sub(1.0).mul(this.getFloat('aoMapIntensity')).add(1.0);
    } else {
      const outputType = this.getNodeType(builder);
      node = this.getCache(scope, outputType);
    }
    return node;
  }
}
MaterialNode.ALPHA_TEST = 'alphaTest';
MaterialNode.COLOR = 'color';
MaterialNode.OPACITY = 'opacity';
MaterialNode.SHININESS = 'shininess';
MaterialNode.SPECULAR = 'specular';
MaterialNode.SPECULAR_STRENGTH = 'specularStrength';
MaterialNode.SPECULAR_INTENSITY = 'specularIntensity';
MaterialNode.SPECULAR_COLOR = 'specularColor';
MaterialNode.REFLECTIVITY = 'reflectivity';
MaterialNode.ROUGHNESS = 'roughness';
MaterialNode.METALNESS = 'metalness';
MaterialNode.NORMAL = 'normal';
MaterialNode.CLEARCOAT = 'clearcoat';
MaterialNode.CLEARCOAT_ROUGHNESS = 'clearcoatRoughness';
MaterialNode.CLEARCOAT_NORMAL = 'clearcoatNormal';
MaterialNode.EMISSIVE = 'emissive';
MaterialNode.ROTATION = 'rotation';
MaterialNode.SHEEN = 'sheen';
MaterialNode.SHEEN_ROUGHNESS = 'sheenRoughness';
MaterialNode.ANISOTROPY = 'anisotropy';
MaterialNode.IRIDESCENCE = 'iridescence';
MaterialNode.IRIDESCENCE_IOR = 'iridescenceIOR';
MaterialNode.IRIDESCENCE_THICKNESS = 'iridescenceThickness';
MaterialNode.IOR = 'ior';
MaterialNode.TRANSMISSION = 'transmission';
MaterialNode.THICKNESS = 'thickness';
MaterialNode.ATTENUATION_DISTANCE = 'attenuationDistance';
MaterialNode.ATTENUATION_COLOR = 'attenuationColor';
MaterialNode.LINE_SCALE = 'scale';
MaterialNode.LINE_DASH_SIZE = 'dashSize';
MaterialNode.LINE_GAP_SIZE = 'gapSize';
MaterialNode.LINE_WIDTH = 'linewidth';
MaterialNode.LINE_DASH_OFFSET = 'dashOffset';
MaterialNode.POINT_WIDTH = 'pointWidth';
MaterialNode.DISPERSION = 'dispersion';
MaterialNode.LIGHT_MAP = 'light';
MaterialNode.AO_MAP = 'ao';
MaterialNode.type = /*@__PURE__*/registerNode('Material', MaterialNode);
const materialAlphaTest = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ALPHA_TEST);
const materialColor = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.COLOR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SHININESS);
const materialEmissive = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.EMISSIVE);
const materialOpacity = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.OPACITY);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SPECULAR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SPECULAR_INTENSITY);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SPECULAR_COLOR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SPECULAR_STRENGTH);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.REFLECTIVITY);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ROUGHNESS);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.METALNESS);
const materialNormal = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.NORMAL).context({
  getUV: null
});
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.CLEARCOAT);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.CLEARCOAT_ROUGHNESS);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ROTATION);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SHEEN);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.SHEEN_ROUGHNESS);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ANISOTROPY);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.IRIDESCENCE);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.IRIDESCENCE_IOR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.IRIDESCENCE_THICKNESS);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.TRANSMISSION);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.THICKNESS);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.IOR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ATTENUATION_DISTANCE);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.ATTENUATION_COLOR);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LINE_SCALE);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LINE_DASH_SIZE);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LINE_GAP_SIZE);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LINE_WIDTH);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LINE_DASH_OFFSET);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.POINT_WIDTH);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.DISPERSION);
const materialLightMap = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.LIGHT_MAP);
const materialAOMap = /*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.AO_MAP);
/*@__PURE__*/nodeImmutable(MaterialNode, MaterialNode.REFRACTION_RATIO);
const materialAnisotropyVector = /*@__PURE__*/uniform(new Vector2()).onReference(function (frame) {
  return frame.material;
}).onRenderUpdate(function ({
  material
}) {
  this.value.set(material.anisotropy * Math.cos(material.anisotropyRotation), material.anisotropy * Math.sin(material.anisotropyRotation));
});

class ModelViewProjectionNode extends TempNode {
  constructor(positionNode = null) {
    super('vec4');
    this.positionNode = positionNode;
  }
  setup(builder) {
    if (builder.shaderStage === 'fragment') {
      return varying(builder.context.mvp);
    }
    const position = this.positionNode || positionLocal;
    return cameraProjectionMatrix.mul(modelViewMatrix).mul(position);
  }
}
ModelViewProjectionNode.type = /*@__PURE__*/registerNode('ModelViewProjection', ModelViewProjectionNode);
const modelViewProjection = /*@__PURE__*/nodeProxy(ModelViewProjectionNode);

class InstancedInterleavedBuffer extends InterleavedBuffer {
  constructor(array, stride, meshPerAttribute = 1) {
    super(array, stride);
    this.isInstancedInterleavedBuffer = true;
    this.meshPerAttribute = meshPerAttribute;
  }
  copy(source) {
    super.copy(source);
    this.meshPerAttribute = source.meshPerAttribute;
    return this;
  }
  clone(data) {
    const ib = super.clone(data);
    ib.meshPerAttribute = this.meshPerAttribute;
    return ib;
  }
  toJSON(data) {
    const json = super.toJSON(data);
    json.isInstancedInterleavedBuffer = true;
    json.meshPerAttribute = this.meshPerAttribute;
    return json;
  }
}

class InstancedBufferAttribute extends BufferAttribute {
  constructor(array, itemSize, normalized, meshPerAttribute = 1) {
    super(array, itemSize, normalized);
    this.isInstancedBufferAttribute = true;
    this.meshPerAttribute = meshPerAttribute;
  }
  copy(source) {
    super.copy(source);
    this.meshPerAttribute = source.meshPerAttribute;
    return this;
  }
  toJSON() {
    const data = super.toJSON();
    data.meshPerAttribute = this.meshPerAttribute;
    data.isInstancedBufferAttribute = true;
    return data;
  }
}

class InstanceNode extends Node$1 {
  constructor(instanceMesh) {
    super('void');
    this.instanceMesh = instanceMesh;
    this.instanceMatrixNode = null;
    this.instanceColorNode = null;
    this.updateType = NodeUpdateType.FRAME;
    this.buffer = null;
    this.bufferColor = null;
  }
  setup(builder) {
    let instanceMatrixNode = this.instanceMatrixNode;
    let instanceColorNode = this.instanceColorNode;
    const instanceMesh = this.instanceMesh;
    if (instanceMatrixNode === null) {
      const instanceAttribute = instanceMesh.instanceMatrix;

      // Both WebGPU and WebGL backends have UBO max limited to 64kb. Matrix count number bigger than 1000 ( 16 * 4 * 1000 = 64kb ) will fallback to attribute.

      if (instanceMesh.count <= 1000) {
        instanceMatrixNode = buffer(instanceAttribute.array, 'mat4', instanceMesh.count).element(instanceIndex);
      } else {
        const buffer = new InstancedInterleavedBuffer(instanceAttribute.array, 16, 1);
        this.buffer = buffer;
        const bufferFn = instanceAttribute.usage === DynamicDrawUsage ? instancedDynamicBufferAttribute : instancedBufferAttribute;
        const instanceBuffers = [
        // F.Signature -> bufferAttribute( array, type, stride, offset )
        bufferFn(buffer, 'vec4', 16, 0), bufferFn(buffer, 'vec4', 16, 4), bufferFn(buffer, 'vec4', 16, 8), bufferFn(buffer, 'vec4', 16, 12)];
        instanceMatrixNode = mat4(...instanceBuffers);
      }
      this.instanceMatrixNode = instanceMatrixNode;
    }
    const instanceColorAttribute = instanceMesh.instanceColor;
    if (instanceColorAttribute && instanceColorNode === null) {
      const buffer = new InstancedBufferAttribute(instanceColorAttribute.array, 3);
      const bufferFn = instanceColorAttribute.usage === DynamicDrawUsage ? instancedDynamicBufferAttribute : instancedBufferAttribute;
      this.bufferColor = buffer;
      instanceColorNode = vec3(bufferFn(buffer, 'vec3', 3, 0));
      this.instanceColorNode = instanceColorNode;
    }

    // POSITION

    const instancePosition = instanceMatrixNode.mul(positionLocal).xyz;
    positionLocal.assign(instancePosition);

    // NORMAL

    if (builder.hasGeometryAttribute('normal')) {
      const m = mat3(instanceMatrixNode);
      const transformedNormal = normalLocal.div(vec3(m[0].dot(m[0]), m[1].dot(m[1]), m[2].dot(m[2])));
      const instanceNormal = m.mul(transformedNormal).xyz;

      // ASSIGNS

      normalLocal.assign(instanceNormal);
    }

    // COLOR

    if (this.instanceColorNode !== null) {
      varyingProperty('vec3', 'vInstanceColor').assign(this.instanceColorNode);
    }
  }
  update( /*frame*/
  ) {
    if (this.instanceMesh.instanceMatrix.usage !== DynamicDrawUsage && this.buffer != null && this.instanceMesh.instanceMatrix.version !== this.buffer.version) {
      this.buffer.version = this.instanceMesh.instanceMatrix.version;
    }
    if (this.instanceMesh.instanceColor && this.instanceMesh.instanceColor.usage !== DynamicDrawUsage && this.bufferColor != null && this.instanceMesh.instanceColor.version !== this.bufferColor.version) {
      this.bufferColor.version = this.instanceMesh.instanceColor.version;
    }
  }
}
InstanceNode.type = /*@__PURE__*/registerNode('Instance', InstanceNode);
const instance$1 = /*@__PURE__*/nodeProxy(InstanceNode);

class BatchNode extends Node$1 {
  constructor(batchMesh) {
    super('void');
    this.batchMesh = batchMesh;
    this.batchingIdNode = null;
  }
  setup(builder) {
    // POSITION

    if (this.batchingIdNode === null) {
      if (builder.getDrawIndex() === null) {
        this.batchingIdNode = instanceIndex;
      } else {
        this.batchingIdNode = drawIndex;
      }
    }
    const getIndirectIndex = Fn(([id]) => {
      const size = textureSize(textureLoad(this.batchMesh._indirectTexture), 0);
      const x = int$1(id).modInt(int$1(size));
      const y = int$1(id).div(int$1(size));
      return textureLoad(this.batchMesh._indirectTexture, ivec2(x, y)).x;
    }).setLayout({
      name: 'getIndirectIndex',
      type: 'uint',
      inputs: [{
        name: 'id',
        type: 'int'
      }]
    });
    const indirectId = getIndirectIndex(int$1(this.batchingIdNode));
    const matricesTexture = this.batchMesh._matricesTexture;
    const size = textureSize(textureLoad(matricesTexture), 0);
    const j = float(indirectId).mul(4).toInt().toVar();
    const x = j.modInt(size);
    const y = j.div(int$1(size));
    const batchingMatrix = mat4(textureLoad(matricesTexture, ivec2(x, y)), textureLoad(matricesTexture, ivec2(x.add(1), y)), textureLoad(matricesTexture, ivec2(x.add(2), y)), textureLoad(matricesTexture, ivec2(x.add(3), y)));
    const colorsTexture = this.batchMesh._colorsTexture;
    if (colorsTexture !== null) {
      const getBatchingColor = Fn(([id]) => {
        const size = textureSize(textureLoad(colorsTexture), 0).x;
        const j = id;
        const x = j.modInt(size);
        const y = j.div(size);
        return textureLoad(colorsTexture, ivec2(x, y)).rgb;
      }).setLayout({
        name: 'getBatchingColor',
        type: 'vec3',
        inputs: [{
          name: 'id',
          type: 'int'
        }]
      });
      const color = getBatchingColor(indirectId);
      varyingProperty('vec3', 'vBatchColor').assign(color);
    }
    const bm = mat3(batchingMatrix);
    positionLocal.assign(batchingMatrix.mul(positionLocal));
    const transformedNormal = normalLocal.div(vec3(bm[0].dot(bm[0]), bm[1].dot(bm[1]), bm[2].dot(bm[2])));
    const batchingNormal = bm.mul(transformedNormal).xyz;
    normalLocal.assign(batchingNormal);
    if (builder.hasGeometryAttribute('tangent')) {
      tangentLocal.mulAssign(bm);
    }
  }
}
BatchNode.type = /*@__PURE__*/registerNode('Batch', BatchNode);
const batch = /*@__PURE__*/nodeProxy(BatchNode);

const _frameId = new WeakMap();
class SkinningNode extends Node$1 {
  constructor(skinnedMesh, useReference = false) {
    super('void');
    this.skinnedMesh = skinnedMesh;
    this.useReference = useReference;
    this.updateType = NodeUpdateType.OBJECT;

    //

    this.skinIndexNode = attribute('skinIndex', 'uvec4');
    this.skinWeightNode = attribute('skinWeight', 'vec4');
    let bindMatrixNode, bindMatrixInverseNode, boneMatricesNode;
    if (useReference) {
      bindMatrixNode = reference('bindMatrix', 'mat4');
      bindMatrixInverseNode = reference('bindMatrixInverse', 'mat4');
      boneMatricesNode = referenceBuffer('skeleton.boneMatrices', 'mat4', skinnedMesh.skeleton.bones.length);
    } else {
      bindMatrixNode = uniform(skinnedMesh.bindMatrix, 'mat4');
      bindMatrixInverseNode = uniform(skinnedMesh.bindMatrixInverse, 'mat4');
      boneMatricesNode = buffer(skinnedMesh.skeleton.boneMatrices, 'mat4', skinnedMesh.skeleton.bones.length);
    }
    this.bindMatrixNode = bindMatrixNode;
    this.bindMatrixInverseNode = bindMatrixInverseNode;
    this.boneMatricesNode = boneMatricesNode;
    this.previousBoneMatricesNode = null;
  }
  getSkinnedPosition(boneMatrices = this.boneMatricesNode, position = positionLocal) {
    const {
      skinIndexNode,
      skinWeightNode,
      bindMatrixNode,
      bindMatrixInverseNode
    } = this;
    const boneMatX = boneMatrices.element(skinIndexNode.x);
    const boneMatY = boneMatrices.element(skinIndexNode.y);
    const boneMatZ = boneMatrices.element(skinIndexNode.z);
    const boneMatW = boneMatrices.element(skinIndexNode.w);

    // POSITION

    const skinVertex = bindMatrixNode.mul(position);
    const skinned = add(boneMatX.mul(skinWeightNode.x).mul(skinVertex), boneMatY.mul(skinWeightNode.y).mul(skinVertex), boneMatZ.mul(skinWeightNode.z).mul(skinVertex), boneMatW.mul(skinWeightNode.w).mul(skinVertex));
    return bindMatrixInverseNode.mul(skinned).xyz;
  }
  getSkinnedNormal(boneMatrices = this.boneMatricesNode, normal = normalLocal) {
    const {
      skinIndexNode,
      skinWeightNode,
      bindMatrixNode,
      bindMatrixInverseNode
    } = this;
    const boneMatX = boneMatrices.element(skinIndexNode.x);
    const boneMatY = boneMatrices.element(skinIndexNode.y);
    const boneMatZ = boneMatrices.element(skinIndexNode.z);
    const boneMatW = boneMatrices.element(skinIndexNode.w);

    // NORMAL

    let skinMatrix = add(skinWeightNode.x.mul(boneMatX), skinWeightNode.y.mul(boneMatY), skinWeightNode.z.mul(boneMatZ), skinWeightNode.w.mul(boneMatW));
    skinMatrix = bindMatrixInverseNode.mul(skinMatrix).mul(bindMatrixNode);
    return skinMatrix.transformDirection(normal).xyz;
  }
  getPreviousSkinnedPosition(builder) {
    const skinnedMesh = builder.object;
    if (this.previousBoneMatricesNode === null) {
      skinnedMesh.skeleton.previousBoneMatrices = new Float32Array(skinnedMesh.skeleton.boneMatrices);
      this.previousBoneMatricesNode = referenceBuffer('skeleton.previousBoneMatrices', 'mat4', skinnedMesh.skeleton.bones.length);
    }
    return this.getSkinnedPosition(this.previousBoneMatricesNode, positionPrevious);
  }
  needsPreviousBoneMatrices(builder) {
    const mrt = builder.renderer.getMRT();
    return mrt && mrt.has('velocity');
  }
  setup(builder) {
    if (this.needsPreviousBoneMatrices(builder)) {
      positionPrevious.assign(this.getPreviousSkinnedPosition(builder));
    }
    const skinPosition = this.getSkinnedPosition();
    positionLocal.assign(skinPosition);
    if (builder.hasGeometryAttribute('normal')) {
      const skinNormal = this.getSkinnedNormal();
      normalLocal.assign(skinNormal);
      if (builder.hasGeometryAttribute('tangent')) {
        tangentLocal.assign(skinNormal);
      }
    }
  }
  generate(builder, output) {
    if (output !== 'void') {
      return positionLocal.build(builder, output);
    }
  }
  update(frame) {
    const object = this.useReference ? frame.object : this.skinnedMesh;
    const skeleton = object.skeleton;
    if (_frameId.get(skeleton) === frame.frameId) return;
    _frameId.set(skeleton, frame.frameId);
    if (this.previousBoneMatricesNode !== null) skeleton.previousBoneMatrices.set(skeleton.boneMatrices);
    skeleton.update();
  }
}
SkinningNode.type = /*@__PURE__*/registerNode('Skinning', SkinningNode);
const skinningReference = skinnedMesh => nodeObject(new SkinningNode(skinnedMesh, true));

class DataArrayTexture extends Texture {
  constructor(data = null, width = 1, height = 1, depth = 1) {
    super(null);
    this.isDataArrayTexture = true;
    this.image = {
      data,
      width,
      height,
      depth
    };
    this.magFilter = NearestFilter;
    this.minFilter = NearestFilter;
    this.wrapR = ClampToEdgeWrapping;
    this.generateMipmaps = false;
    this.flipY = false;
    this.unpackAlignment = 1;
    this.layerUpdates = new Set();
  }
  addLayerUpdate(layerIndex) {
    this.layerUpdates.add(layerIndex);
  }
  clearLayerUpdates() {
    this.layerUpdates.clear();
  }
}

const _morphTextures = /*@__PURE__*/new WeakMap();
const _morphVec4 = /*@__PURE__*/new Vector4();
const getMorph = /*@__PURE__*/Fn(({
  bufferMap,
  influence,
  stride,
  width,
  depth,
  offset
}) => {
  const texelIndex = int$1(vertexIndex).mul(stride).add(offset);
  const y = texelIndex.div(width);
  const x = texelIndex.sub(y.mul(width));
  const bufferAttrib = textureLoad(bufferMap, ivec2(x, y)).depth(depth);
  return bufferAttrib.mul(influence);
});
function getEntry(geometry) {
  const hasMorphPosition = geometry.morphAttributes.position !== undefined;
  const hasMorphNormals = geometry.morphAttributes.normal !== undefined;
  const hasMorphColors = geometry.morphAttributes.color !== undefined;

  // instead of using attributes, the WebGL 2 code path encodes morph targets
  // into an array of data textures. Each layer represents a single morph target.

  const morphAttribute = geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color;
  const morphTargetsCount = morphAttribute !== undefined ? morphAttribute.length : 0;
  let entry = _morphTextures.get(geometry);
  if (entry === undefined || entry.count !== morphTargetsCount) {
    if (entry !== undefined) entry.texture.dispose();
    const morphTargets = geometry.morphAttributes.position || [];
    const morphNormals = geometry.morphAttributes.normal || [];
    const morphColors = geometry.morphAttributes.color || [];
    let vertexDataCount = 0;
    if (hasMorphPosition === true) vertexDataCount = 1;
    if (hasMorphNormals === true) vertexDataCount = 2;
    if (hasMorphColors === true) vertexDataCount = 3;
    let width = geometry.attributes.position.count * vertexDataCount;
    let height = 1;
    const maxTextureSize = 4096; // @TODO: Use 'capabilities.maxTextureSize'

    if (width > maxTextureSize) {
      height = Math.ceil(width / maxTextureSize);
      width = maxTextureSize;
    }
    const buffer = new Float32Array(width * height * 4 * morphTargetsCount);
    const bufferTexture = new DataArrayTexture(buffer, width, height, morphTargetsCount);
    bufferTexture.type = FloatType;
    bufferTexture.needsUpdate = true;

    // fill buffer

    const vertexDataStride = vertexDataCount * 4;
    for (let i = 0; i < morphTargetsCount; i++) {
      const morphTarget = morphTargets[i];
      const morphNormal = morphNormals[i];
      const morphColor = morphColors[i];
      const offset = width * height * 4 * i;
      for (let j = 0; j < morphTarget.count; j++) {
        const stride = j * vertexDataStride;
        if (hasMorphPosition === true) {
          _morphVec4.fromBufferAttribute(morphTarget, j);
          buffer[offset + stride + 0] = _morphVec4.x;
          buffer[offset + stride + 1] = _morphVec4.y;
          buffer[offset + stride + 2] = _morphVec4.z;
          buffer[offset + stride + 3] = 0;
        }
        if (hasMorphNormals === true) {
          _morphVec4.fromBufferAttribute(morphNormal, j);
          buffer[offset + stride + 4] = _morphVec4.x;
          buffer[offset + stride + 5] = _morphVec4.y;
          buffer[offset + stride + 6] = _morphVec4.z;
          buffer[offset + stride + 7] = 0;
        }
        if (hasMorphColors === true) {
          _morphVec4.fromBufferAttribute(morphColor, j);
          buffer[offset + stride + 8] = _morphVec4.x;
          buffer[offset + stride + 9] = _morphVec4.y;
          buffer[offset + stride + 10] = _morphVec4.z;
          buffer[offset + stride + 11] = morphColor.itemSize === 4 ? _morphVec4.w : 1;
        }
      }
    }
    entry = {
      count: morphTargetsCount,
      texture: bufferTexture,
      stride: vertexDataCount,
      size: new Vector2(width, height)
    };
    _morphTextures.set(geometry, entry);
    function disposeTexture() {
      bufferTexture.dispose();
      _morphTextures.delete(geometry);
      geometry.removeEventListener('dispose', disposeTexture);
    }
    geometry.addEventListener('dispose', disposeTexture);
  }
  return entry;
}
class MorphNode extends Node$1 {
  constructor(mesh) {
    super('void');
    this.mesh = mesh;
    this.morphBaseInfluence = uniform(1);
    this.updateType = NodeUpdateType.OBJECT;
  }
  setup(builder) {
    const {
      geometry
    } = builder;
    const hasMorphPosition = geometry.morphAttributes.position !== undefined;
    const hasMorphNormals = geometry.hasAttribute('normal') && geometry.morphAttributes.normal !== undefined;
    const morphAttribute = geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color;
    const morphTargetsCount = morphAttribute !== undefined ? morphAttribute.length : 0;

    // nodes

    const {
      texture: bufferMap,
      stride,
      size
    } = getEntry(geometry);
    if (hasMorphPosition === true) positionLocal.mulAssign(this.morphBaseInfluence);
    if (hasMorphNormals === true) normalLocal.mulAssign(this.morphBaseInfluence);
    const width = int$1(size.width);
    Loop(morphTargetsCount, ({
      i
    }) => {
      const influence = float(0).toVar();
      if (this.mesh.count > 1 && this.mesh.morphTexture !== null && this.mesh.morphTexture !== undefined) {
        influence.assign(textureLoad(this.mesh.morphTexture, ivec2(int$1(i).add(1), int$1(instanceIndex))).r);
      } else {
        influence.assign(reference('morphTargetInfluences', 'float').element(i).toVar());
      }
      if (hasMorphPosition === true) {
        positionLocal.addAssign(getMorph({
          bufferMap,
          influence,
          stride,
          width,
          depth: i,
          offset: int$1(0)
        }));
      }
      if (hasMorphNormals === true) {
        normalLocal.addAssign(getMorph({
          bufferMap,
          influence,
          stride,
          width,
          depth: i,
          offset: int$1(1)
        }));
      }
    });
  }
  update() {
    const morphBaseInfluence = this.morphBaseInfluence;
    if (this.mesh.geometry.morphTargetsRelative) {
      morphBaseInfluence.value = 1;
    } else {
      morphBaseInfluence.value = 1 - this.mesh.morphTargetInfluences.reduce((a, b) => a + b, 0);
    }
  }
}
MorphNode.type = /*@__PURE__*/registerNode('Morph', MorphNode);
const morphReference = /*@__PURE__*/nodeProxy(MorphNode);

const sortLights = lights => {
  return lights.sort((a, b) => a.id - b.id);
};
const getLightNodeById = (id, lightNodes) => {
  for (const lightNode of lightNodes) {
    if (lightNode.isAnalyticLightNode && lightNode.light.id === id) {
      return lightNode;
    }
  }
  return null;
};
class LightsNode extends Node$1 {
  constructor(lights = []) {
    super('vec3');
    this.totalDiffuseNode = vec3().toVar('totalDiffuse');
    this.totalSpecularNode = vec3().toVar('totalSpecular');
    this.outgoingLightNode = vec3().toVar('outgoingLight');
    this._lights = lights;
    this._lightNodes = null;
    this._lightNodesHash = null;
    this.global = true;
  }
  getHash(builder) {
    if (this._lightNodesHash === null) {
      if (this._lightNodes === null) this.setupLightsNode(builder);
      const hash = [];
      for (const lightNode of this._lightNodes) {
        hash.push(lightNode.getHash());
      }
      this._lightNodesHash = 'lights-' + hash.join(',');
    }
    return this._lightNodesHash;
  }
  analyze(builder) {
    const properties = builder.getDataFromNode(this);
    for (const node of properties.nodes) {
      node.build(builder);
    }
  }
  setupLightsNode(builder) {
    const lightNodes = [];
    const previousLightNodes = this._lightNodes;
    const lights = sortLights(this._lights);
    const nodeLibrary = builder.renderer.nodes.library;
    for (const light of lights) {
      if (light.isNode) {
        lightNodes.push(nodeObject(light));
      } else {
        let lightNode = null;
        if (previousLightNodes !== null) {
          lightNode = getLightNodeById(light.id, previousLightNodes); // resuse existing light node
        }
        if (lightNode === null) {
          const lightNodeClass = nodeLibrary.getLightNodeClass(light.constructor);
          if (lightNodeClass === undefined) {
            console.warn(`LightsNode.setupNodeLights: Light node not found for ${light.constructor.name}`);
            continue;
          }
          lightNodes.push(nodeObject(new lightNodeClass(light)));
        }
      }
    }
    this._lightNodes = lightNodes;
  }
  setup(builder) {
    if (this._lightNodes === null) this.setupLightsNode(builder);
    const context = builder.context;
    const lightingModel = context.lightingModel;
    let outgoingLightNode = this.outgoingLightNode;
    if (lightingModel) {
      const {
        _lightNodes,
        totalDiffuseNode,
        totalSpecularNode
      } = this;
      context.outgoingLight = outgoingLightNode;
      const stack = builder.addStack();

      //

      const properties = builder.getDataFromNode(this);
      properties.nodes = stack.nodes;

      //

      lightingModel.start(context, stack, builder);

      // lights

      for (const lightNode of _lightNodes) {
        lightNode.build(builder);
      }

      //

      lightingModel.indirect(context, stack, builder);

      //

      const {
        backdrop,
        backdropAlpha
      } = context;
      const {
        directDiffuse,
        directSpecular,
        indirectDiffuse,
        indirectSpecular
      } = context.reflectedLight;
      let totalDiffuse = directDiffuse.add(indirectDiffuse);
      if (backdrop !== null) {
        if (backdropAlpha !== null) {
          totalDiffuse = vec3(backdropAlpha.mix(totalDiffuse, backdrop));
        } else {
          totalDiffuse = vec3(backdrop);
        }
        context.material.transparent = true;
      }
      totalDiffuseNode.assign(totalDiffuse);
      totalSpecularNode.assign(directSpecular.add(indirectSpecular));
      outgoingLightNode.assign(totalDiffuseNode.add(totalSpecularNode));

      //

      lightingModel.finish(context, stack, builder);

      //

      outgoingLightNode = outgoingLightNode.bypass(builder.removeStack());
    }
    return outgoingLightNode;
  }
  setLights(lights) {
    this._lights = lights;
    this._lightNodes = null;
    this._lightNodesHash = null;
    return this;
  }
  getLights() {
    return this._lights;
  }
}
LightsNode.type = /*@__PURE__*/registerNode('Lights', LightsNode);
const lights = /*@__PURE__*/nodeProxy(LightsNode);

class LightingNode extends Node$1 {
  constructor() {
    super('vec3');
    this.isLightingNode = true;
  }
  generate( /*builder*/
  ) {
    console.warn('Abstract function.');
  }
}
LightingNode.type = /*@__PURE__*/registerNode('Lighting', LightingNode);

class AONode extends LightingNode {
  constructor(aoNode = null) {
    super();
    this.aoNode = aoNode;
  }
  setup(builder) {
    builder.context.ambientOcclusion.mulAssign(this.aoNode);
  }
}
AONode.type = /*@__PURE__*/registerNode('AO', AONode);

class LightingContextNode extends ContextNode {
  constructor(node, lightingModel = null, backdropNode = null, backdropAlphaNode = null) {
    super(node);
    this.lightingModel = lightingModel;
    this.backdropNode = backdropNode;
    this.backdropAlphaNode = backdropAlphaNode;
    this._value = null;
  }
  getContext() {
    const {
      backdropNode,
      backdropAlphaNode
    } = this;
    const directDiffuse = vec3().toVar('directDiffuse'),
      directSpecular = vec3().toVar('directSpecular'),
      indirectDiffuse = vec3().toVar('indirectDiffuse'),
      indirectSpecular = vec3().toVar('indirectSpecular');
    const reflectedLight = {
      directDiffuse,
      directSpecular,
      indirectDiffuse,
      indirectSpecular
    };
    const context = {
      radiance: vec3().toVar('radiance'),
      irradiance: vec3().toVar('irradiance'),
      iblIrradiance: vec3().toVar('iblIrradiance'),
      ambientOcclusion: float(1).toVar('ambientOcclusion'),
      reflectedLight,
      backdrop: backdropNode,
      backdropAlpha: backdropAlphaNode
    };
    return context;
  }
  setup(builder) {
    this.value = this._value || (this._value = this.getContext());
    this.value.lightingModel = this.lightingModel || builder.context.lightingModel;
    return super.setup(builder);
  }
}
LightingContextNode.type = /*@__PURE__*/registerNode('LightingContext', LightingContextNode);
const lightingContext = /*@__PURE__*/nodeProxy(LightingContextNode);

class IrradianceNode extends LightingNode {
  constructor(node) {
    super();
    this.node = node;
  }
  setup(builder) {
    builder.context.irradiance.addAssign(this.node);
  }
}
IrradianceNode.type = /*@__PURE__*/registerNode('Irradiance', IrradianceNode);

class ClippingNode extends Node$1 {
  constructor(scope = ClippingNode.DEFAULT) {
    super();
    this.scope = scope;
  }
  setup(builder) {
    super.setup(builder);
    const clippingContext = builder.clippingContext;
    const {
      localClipIntersection,
      localClippingCount,
      globalClippingCount
    } = clippingContext;
    const numClippingPlanes = globalClippingCount + localClippingCount;
    const numUnionClippingPlanes = localClipIntersection ? numClippingPlanes - localClippingCount : numClippingPlanes;
    if (this.scope === ClippingNode.ALPHA_TO_COVERAGE) {
      return this.setupAlphaToCoverage(clippingContext.planes, numClippingPlanes, numUnionClippingPlanes);
    } else {
      return this.setupDefault(clippingContext.planes, numClippingPlanes, numUnionClippingPlanes);
    }
  }
  setupAlphaToCoverage(planes, numClippingPlanes, numUnionClippingPlanes) {
    return Fn(() => {
      const clippingPlanes = uniformArray(planes);
      const distanceToPlane = property('float', 'distanceToPlane');
      const distanceGradient = property('float', 'distanceToGradient');
      const clipOpacity = property('float', 'clipOpacity');
      clipOpacity.assign(1);
      let plane;
      Loop(numUnionClippingPlanes, ({
        i
      }) => {
        plane = clippingPlanes.element(i);
        distanceToPlane.assign(positionView.dot(plane.xyz).negate().add(plane.w));
        distanceGradient.assign(distanceToPlane.fwidth().div(2.0));
        clipOpacity.mulAssign(smoothstep(distanceGradient.negate(), distanceGradient, distanceToPlane));
        clipOpacity.equal(0.0).discard();
      });
      if (numUnionClippingPlanes < numClippingPlanes) {
        const unionClipOpacity = property('float', 'unionclipOpacity');
        unionClipOpacity.assign(1);
        Loop({
          start: numUnionClippingPlanes,
          end: numClippingPlanes
        }, ({
          i
        }) => {
          plane = clippingPlanes.element(i);
          distanceToPlane.assign(positionView.dot(plane.xyz).negate().add(plane.w));
          distanceGradient.assign(distanceToPlane.fwidth().div(2.0));
          unionClipOpacity.mulAssign(smoothstep(distanceGradient.negate(), distanceGradient, distanceToPlane).oneMinus());
        });
        clipOpacity.mulAssign(unionClipOpacity.oneMinus());
      }
      diffuseColor.a.mulAssign(clipOpacity);
      diffuseColor.a.equal(0.0).discard();
    })();
  }
  setupDefault(planes, numClippingPlanes, numUnionClippingPlanes) {
    return Fn(() => {
      const clippingPlanes = uniformArray(planes);
      let plane;
      Loop(numUnionClippingPlanes, ({
        i
      }) => {
        plane = clippingPlanes.element(i);
        positionView.dot(plane.xyz).greaterThan(plane.w).discard();
      });
      if (numUnionClippingPlanes < numClippingPlanes) {
        const clipped = property('bool', 'clipped');
        clipped.assign(true);
        Loop({
          start: numUnionClippingPlanes,
          end: numClippingPlanes
        }, ({
          i
        }) => {
          plane = clippingPlanes.element(i);
          clipped.assign(positionView.dot(plane.xyz).greaterThan(plane.w).and(clipped));
        });
        clipped.discard();
      }
    })();
  }
}
ClippingNode.ALPHA_TO_COVERAGE = 'alphaToCoverage';
ClippingNode.DEFAULT = 'default';
ClippingNode.type = /*@__PURE__*/registerNode('Clipping', ClippingNode);
const clipping = () => nodeObject(new ClippingNode());
const clippingAlpha = () => nodeObject(new ClippingNode(ClippingNode.ALPHA_TO_COVERAGE));

const NodeMaterials = new Map();
class NodeMaterial extends Material {
  constructor() {
    super();
    this.isNodeMaterial = true;
    this.type = this.constructor.type;
    this.forceSinglePass = false;
    this.fog = true;
    this.lights = false;
    this.lightsNode = null;
    this.envNode = null;
    this.aoNode = null;
    this.colorNode = null;
    this.normalNode = null;
    this.opacityNode = null;
    this.backdropNode = null;
    this.backdropAlphaNode = null;
    this.alphaTestNode = null;
    this.positionNode = null;
    this.depthNode = null;
    this.shadowNode = null;
    this.shadowPositionNode = null;
    this.outputNode = null;
    this.mrtNode = null;
    this.fragmentNode = null;
    this.vertexNode = null;
  }
  customProgramCacheKey() {
    return this.type + getCacheKey(this);
  }
  build(builder) {
    this.setup(builder);
  }
  setup(builder) {
    builder.context.setupNormal = () => this.setupNormal(builder);

    // < VERTEX STAGE >

    builder.addStack();
    builder.stack.outputNode = this.vertexNode || this.setupPosition(builder);
    builder.addFlow('vertex', builder.removeStack());

    // < FRAGMENT STAGE >

    builder.addStack();
    let resultNode;
    const clippingNode = this.setupClipping(builder);
    if (this.depthWrite === true) this.setupDepth(builder);
    if (this.fragmentNode === null) {
      this.setupDiffuseColor(builder);
      this.setupVariants(builder);
      const outgoingLightNode = this.setupLighting(builder);
      if (clippingNode !== null) builder.stack.add(clippingNode);

      // force unsigned floats - useful for RenderTargets

      const basicOutput = vec4(outgoingLightNode, diffuseColor.a).max(0);
      resultNode = this.setupOutput(builder, basicOutput);

      // OUTPUT NODE

      output.assign(resultNode);

      //

      if (this.outputNode !== null) resultNode = this.outputNode;

      // MRT

      const renderTarget = builder.renderer.getRenderTarget();
      if (renderTarget !== null) {
        const mrt = builder.renderer.getMRT();
        const materialMRT = this.mrtNode;
        if (mrt !== null) {
          resultNode = mrt;
          if (materialMRT !== null) {
            resultNode = mrt.merge(materialMRT);
          }
        } else if (materialMRT !== null) {
          resultNode = materialMRT;
        }
      }
    } else {
      let fragmentNode = this.fragmentNode;
      if (fragmentNode.isOutputStructNode !== true) {
        fragmentNode = vec4(fragmentNode);
      }
      resultNode = this.setupOutput(builder, fragmentNode);
    }
    builder.stack.outputNode = resultNode;
    builder.addFlow('fragment', builder.removeStack());
  }
  setupClipping(builder) {
    if (builder.clippingContext === null) return null;
    const {
      globalClippingCount,
      localClippingCount
    } = builder.clippingContext;
    let result = null;
    if (globalClippingCount || localClippingCount) {
      if (this.alphaToCoverage) {
        // to be added to flow when the color/alpha value has been determined
        result = clippingAlpha();
      } else {
        builder.stack.add(clipping());
      }
    }
    return result;
  }
  setupDepth(builder) {
    const {
      renderer
    } = builder;

    // Depth

    let depthNode = this.depthNode;
    if (depthNode === null) {
      const mrt = renderer.getMRT();
      if (mrt && mrt.has('depth')) {
        depthNode = mrt.get('depth');
      } else if (renderer.logarithmicDepthBuffer === true) {
        const fragDepth = modelViewProjection().w.add(1);
        depthNode = fragDepth.log2().mul(cameraLogDepth).mul(0.5);
      }
    }
    if (depthNode !== null) {
      depth.assign(depthNode).append();
    }
  }
  setupPosition(builder) {
    const {
      object
    } = builder;
    const geometry = object.geometry;
    builder.addStack();

    // Vertex

    if (geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color) {
      morphReference(object).append();
    }
    if (object.isSkinnedMesh === true) {
      skinningReference(object).append();
    }
    if (this.displacementMap) {
      const displacementMap = materialReference('displacementMap', 'texture');
      const displacementScale = materialReference('displacementScale', 'float');
      const displacementBias = materialReference('displacementBias', 'float');
      positionLocal.addAssign(normalLocal.normalize().mul(displacementMap.x.mul(displacementScale).add(displacementBias)));
    }
    if (object.isBatchedMesh) {
      batch(object).append();
    }
    if (object.instanceMatrix && object.instanceMatrix.isInstancedBufferAttribute === true) {
      instance$1(object).append();
    }
    if (this.positionNode !== null) {
      positionLocal.assign(this.positionNode);
    }
    const mvp = modelViewProjection();
    builder.context.vertex = builder.removeStack();
    builder.context.mvp = mvp;
    return mvp;
  }
  setupDiffuseColor({
    object,
    geometry
  }) {
    let colorNode = this.colorNode ? vec4(this.colorNode) : materialColor;

    // VERTEX COLORS

    if (this.vertexColors === true && geometry.hasAttribute('color')) {
      colorNode = vec4(colorNode.xyz.mul(attribute('color', 'vec3')), colorNode.a);
    }

    // Instanced colors

    if (object.instanceColor) {
      const instanceColor = varyingProperty('vec3', 'vInstanceColor');
      colorNode = instanceColor.mul(colorNode);
    }
    if (object.isBatchedMesh && object._colorsTexture) {
      const batchColor = varyingProperty('vec3', 'vBatchColor');
      colorNode = batchColor.mul(colorNode);
    }

    // COLOR

    diffuseColor.assign(colorNode);

    // OPACITY

    const opacityNode = this.opacityNode ? float(this.opacityNode) : materialOpacity;
    diffuseColor.a.assign(diffuseColor.a.mul(opacityNode));

    // ALPHA TEST

    if (this.alphaTestNode !== null || this.alphaTest > 0) {
      const alphaTestNode = this.alphaTestNode !== null ? float(this.alphaTestNode) : materialAlphaTest;
      diffuseColor.a.lessThanEqual(alphaTestNode).discard();
    }
    if (this.transparent === false && this.blending === NormalBlending && this.alphaToCoverage === false) {
      diffuseColor.a.assign(1.0);
    }
  }
  setupVariants( /*builder*/
  ) {

    // Interface function.
  }
  setupOutgoingLight() {
    return this.lights === true ? vec3(0) : diffuseColor.rgb;
  }
  setupNormal() {
    return this.normalNode ? vec3(this.normalNode) : materialNormal;
  }
  setupEnvironment( /*builder*/
  ) {
    let node = null;
    if (this.envNode) {
      node = this.envNode;
    } else if (this.envMap) {
      node = this.envMap.isCubeTexture ? materialReference('envMap', 'cubeTexture') : materialReference('envMap', 'texture');
    }
    return node;
  }
  setupLightMap(builder) {
    let node = null;
    if (builder.material.lightMap) {
      node = new IrradianceNode(materialLightMap);
    }
    return node;
  }
  setupLights(builder) {
    const materialLightsNode = [];

    //

    const envNode = this.setupEnvironment(builder);
    if (envNode && envNode.isLightingNode) {
      materialLightsNode.push(envNode);
    }
    const lightMapNode = this.setupLightMap(builder);
    if (lightMapNode && lightMapNode.isLightingNode) {
      materialLightsNode.push(lightMapNode);
    }
    if (this.aoNode !== null || builder.material.aoMap) {
      const aoNode = this.aoNode !== null ? this.aoNode : materialAOMap;
      materialLightsNode.push(new AONode(aoNode));
    }
    let lightsN = this.lightsNode || builder.lightsNode;
    if (materialLightsNode.length > 0) {
      lightsN = lights([...lightsN.getLights(), ...materialLightsNode]);
    }
    return lightsN;
  }
  setupLightingModel( /*builder*/
  ) {

    // Interface function.
  }
  setupLighting(builder) {
    const {
      material
    } = builder;
    const {
      backdropNode,
      backdropAlphaNode,
      emissiveNode
    } = this;

    // OUTGOING LIGHT

    const lights = this.lights === true || this.lightsNode !== null;
    const lightsNode = lights ? this.setupLights(builder) : null;
    let outgoingLightNode = this.setupOutgoingLight(builder);
    if (lightsNode && lightsNode.getScope().getLights().length > 0) {
      const lightingModel = this.setupLightingModel(builder);
      outgoingLightNode = lightingContext(lightsNode, lightingModel, backdropNode, backdropAlphaNode);
    } else if (backdropNode !== null) {
      outgoingLightNode = vec3(backdropAlphaNode !== null ? mix(outgoingLightNode, backdropNode, backdropAlphaNode) : backdropNode);
    }

    // EMISSIVE

    if (emissiveNode && emissiveNode.isNode === true || material.emissive && material.emissive.isColor === true) {
      emissive.assign(vec3(emissiveNode ? emissiveNode : materialEmissive));
      outgoingLightNode = outgoingLightNode.add(emissive);
    }
    return outgoingLightNode;
  }
  setupOutput(builder, outputNode) {
    // FOG

    if (this.fog === true) {
      const fogNode = builder.fogNode;
      if (fogNode) outputNode = vec4(fogNode.mix(outputNode.rgb, fogNode.colorNode), outputNode.a);
    }
    return outputNode;
  }
  setDefaultValues(material) {
    // This approach is to reuse the native refreshUniforms*
    // and turn available the use of features like transmission and environment in core

    for (const property in material) {
      const value = material[property];
      if (this[property] === undefined) {
        this[property] = value;
        if (value && value.clone) this[property] = value.clone();
      }
    }
    const descriptors = Object.getOwnPropertyDescriptors(material.constructor.prototype);
    for (const key in descriptors) {
      if (Object.getOwnPropertyDescriptor(this.constructor.prototype, key) === undefined && descriptors[key].get !== undefined) {
        Object.defineProperty(this.constructor.prototype, key, descriptors[key]);
      }
    }
  }
  toJSON(meta) {
    const isRoot = meta === undefined || typeof meta === 'string';
    if (isRoot) {
      meta = {
        textures: {},
        images: {},
        nodes: {}
      };
    }
    const data = Material.prototype.toJSON.call(this, meta);
    const nodeChildren = getNodeChildren(this);
    data.inputNodes = {};
    for (const {
      property,
      childNode
    } of nodeChildren) {
      data.inputNodes[property] = childNode.toJSON(meta).uuid;
    }

    // TODO: Copied from Object3D.toJSON

    function extractFromCache(cache) {
      const values = [];
      for (const key in cache) {
        const data = cache[key];
        delete data.metadata;
        values.push(data);
      }
      return values;
    }
    if (isRoot) {
      const textures = extractFromCache(meta.textures);
      const images = extractFromCache(meta.images);
      const nodes = extractFromCache(meta.nodes);
      if (textures.length > 0) data.textures = textures;
      if (images.length > 0) data.images = images;
      if (nodes.length > 0) data.nodes = nodes;
    }
    return data;
  }
  copy(source) {
    this.lightsNode = source.lightsNode;
    this.envNode = source.envNode;
    this.colorNode = source.colorNode;
    this.normalNode = source.normalNode;
    this.opacityNode = source.opacityNode;
    this.backdropNode = source.backdropNode;
    this.backdropAlphaNode = source.backdropAlphaNode;
    this.alphaTestNode = source.alphaTestNode;
    this.positionNode = source.positionNode;
    this.depthNode = source.depthNode;
    this.shadowNode = source.shadowNode;
    this.shadowPositionNode = source.shadowPositionNode;
    this.outputNode = source.outputNode;
    this.mrtNode = source.mrtNode;
    this.fragmentNode = source.fragmentNode;
    this.vertexNode = source.vertexNode;
    return super.copy(source);
  }
}
var NodeMaterial$1 = NodeMaterial;
NodeMaterial.type = /*@__PURE__*/registerNodeMaterial('', NodeMaterial);
function registerNodeMaterial(type, nodeMaterialClass) {
  const suffix = 'NodeMaterial';
  const nodeMaterialType = type + suffix;
  if (typeof nodeMaterialClass !== 'function') throw new Error(`THREE.Node: NodeMaterial class "${type}" is not a class.`);
  if (NodeMaterials.has(nodeMaterialType)) {
    console.warn(`THREE.Node: Redefinition of NodeMaterial class "${nodeMaterialType}".`);
    return;
  }
  if (type.slice(-suffix.length) === suffix) {
    console.warn(`THREE.NodeMaterial: NodeMaterial class ${nodeMaterialType} should not have '${suffix}' suffix.`);
    return;
  }
  NodeMaterials.set(nodeMaterialType, nodeMaterialClass);
  nodeMaterialClass.type = nodeMaterialType;
  return nodeMaterialType;
}

class Box3 {
  constructor(min = new Vector3(+Infinity, +Infinity, +Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity)) {
    this.isBox3 = true;
    this.min = min;
    this.max = max;
  }
  set(min, max) {
    this.min.copy(min);
    this.max.copy(max);
    return this;
  }
  setFromArray(array) {
    this.makeEmpty();
    for (let i = 0, il = array.length; i < il; i += 3) {
      this.expandByPoint(_vector$2.fromArray(array, i));
    }
    return this;
  }
  setFromBufferAttribute(attribute) {
    this.makeEmpty();
    for (let i = 0, il = attribute.count; i < il; i++) {
      this.expandByPoint(_vector$2.fromBufferAttribute(attribute, i));
    }
    return this;
  }
  setFromPoints(points) {
    this.makeEmpty();
    for (let i = 0, il = points.length; i < il; i++) {
      this.expandByPoint(points[i]);
    }
    return this;
  }
  setFromCenterAndSize(center, size) {
    const halfSize = _vector$2.copy(size).multiplyScalar(0.5);
    this.min.copy(center).sub(halfSize);
    this.max.copy(center).add(halfSize);
    return this;
  }
  setFromObject(object, precise = false) {
    this.makeEmpty();
    return this.expandByObject(object, precise);
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(box) {
    this.min.copy(box.min);
    this.max.copy(box.max);
    return this;
  }
  makeEmpty() {
    this.min.x = this.min.y = this.min.z = +Infinity;
    this.max.x = this.max.y = this.max.z = -Infinity;
    return this;
  }
  isEmpty() {
    // this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

    return this.max.x < this.min.x || this.max.y < this.min.y || this.max.z < this.min.z;
  }
  getCenter(target) {
    return this.isEmpty() ? target.set(0, 0, 0) : target.addVectors(this.min, this.max).multiplyScalar(0.5);
  }
  getSize(target) {
    return this.isEmpty() ? target.set(0, 0, 0) : target.subVectors(this.max, this.min);
  }
  expandByPoint(point) {
    this.min.min(point);
    this.max.max(point);
    return this;
  }
  expandByVector(vector) {
    this.min.sub(vector);
    this.max.add(vector);
    return this;
  }
  expandByScalar(scalar) {
    this.min.addScalar(-scalar);
    this.max.addScalar(scalar);
    return this;
  }
  expandByObject(object, precise = false) {
    // Computes the world-axis-aligned bounding box of an object (including its children),
    // accounting for both the object's, and children's, world transforms

    object.updateWorldMatrix(false, false);
    const geometry = object.geometry;
    if (geometry !== undefined) {
      const positionAttribute = geometry.getAttribute('position');

      // precise AABB computation based on vertex data requires at least a position attribute.
      // instancing isn't supported so far and uses the normal (conservative) code path.

      if (precise === true && positionAttribute !== undefined && object.isInstancedMesh !== true) {
        for (let i = 0, l = positionAttribute.count; i < l; i++) {
          if (object.isMesh === true) {
            object.getVertexPosition(i, _vector$2);
          } else {
            _vector$2.fromBufferAttribute(positionAttribute, i);
          }
          _vector$2.applyMatrix4(object.matrixWorld);
          this.expandByPoint(_vector$2);
        }
      } else {
        if (object.boundingBox !== undefined) {
          // object-level bounding box

          if (object.boundingBox === null) {
            object.computeBoundingBox();
          }
          _box$2.copy(object.boundingBox);
        } else {
          // geometry-level bounding box

          if (geometry.boundingBox === null) {
            geometry.computeBoundingBox();
          }
          _box$2.copy(geometry.boundingBox);
        }
        _box$2.applyMatrix4(object.matrixWorld);
        this.union(_box$2);
      }
    }
    const children = object.children;
    for (let i = 0, l = children.length; i < l; i++) {
      this.expandByObject(children[i], precise);
    }
    return this;
  }
  containsPoint(point) {
    return point.x >= this.min.x && point.x <= this.max.x && point.y >= this.min.y && point.y <= this.max.y && point.z >= this.min.z && point.z <= this.max.z;
  }
  containsBox(box) {
    return this.min.x <= box.min.x && box.max.x <= this.max.x && this.min.y <= box.min.y && box.max.y <= this.max.y && this.min.z <= box.min.z && box.max.z <= this.max.z;
  }
  getParameter(point, target) {
    // This can potentially have a divide by zero if the box
    // has a size dimension of 0.

    return target.set((point.x - this.min.x) / (this.max.x - this.min.x), (point.y - this.min.y) / (this.max.y - this.min.y), (point.z - this.min.z) / (this.max.z - this.min.z));
  }
  intersectsBox(box) {
    // using 6 splitting planes to rule out intersections.
    return box.max.x >= this.min.x && box.min.x <= this.max.x && box.max.y >= this.min.y && box.min.y <= this.max.y && box.max.z >= this.min.z && box.min.z <= this.max.z;
  }
  intersectsSphere(sphere) {
    // Find the point on the AABB closest to the sphere center.
    this.clampPoint(sphere.center, _vector$2);

    // If that point is inside the sphere, the AABB and sphere intersect.
    return _vector$2.distanceToSquared(sphere.center) <= sphere.radius * sphere.radius;
  }
  intersectsPlane(plane) {
    // We compute the minimum and maximum dot product values. If those values
    // are on the same side (back or front) of the plane, then there is no intersection.

    let min, max;
    if (plane.normal.x > 0) {
      min = plane.normal.x * this.min.x;
      max = plane.normal.x * this.max.x;
    } else {
      min = plane.normal.x * this.max.x;
      max = plane.normal.x * this.min.x;
    }
    if (plane.normal.y > 0) {
      min += plane.normal.y * this.min.y;
      max += plane.normal.y * this.max.y;
    } else {
      min += plane.normal.y * this.max.y;
      max += plane.normal.y * this.min.y;
    }
    if (plane.normal.z > 0) {
      min += plane.normal.z * this.min.z;
      max += plane.normal.z * this.max.z;
    } else {
      min += plane.normal.z * this.max.z;
      max += plane.normal.z * this.min.z;
    }
    return min <= -plane.constant && max >= -plane.constant;
  }
  intersectsTriangle(triangle) {
    if (this.isEmpty()) {
      return false;
    }

    // compute box center and extents
    this.getCenter(_center);
    _extents.subVectors(this.max, _center);

    // translate triangle to aabb origin
    _v0$1.subVectors(triangle.a, _center);
    _v1$2.subVectors(triangle.b, _center);
    _v2$2.subVectors(triangle.c, _center);

    // compute edge vectors for triangle
    _f0.subVectors(_v1$2, _v0$1);
    _f1.subVectors(_v2$2, _v1$2);
    _f2.subVectors(_v0$1, _v2$2);

    // test against axes that are given by cross product combinations of the edges of the triangle and the edges of the aabb
    // make an axis testing of each of the 3 sides of the aabb against each of the 3 sides of the triangle = 9 axis of separation
    // axis_ij = u_i x f_j (u0, u1, u2 = face normals of aabb = x,y,z axes vectors since aabb is axis aligned)
    let axes = [0, -_f0.z, _f0.y, 0, -_f1.z, _f1.y, 0, -_f2.z, _f2.y, _f0.z, 0, -_f0.x, _f1.z, 0, -_f1.x, _f2.z, 0, -_f2.x, -_f0.y, _f0.x, 0, -_f1.y, _f1.x, 0, -_f2.y, _f2.x, 0];
    if (!satForAxes(axes, _v0$1, _v1$2, _v2$2, _extents)) {
      return false;
    }

    // test 3 face normals from the aabb
    axes = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    if (!satForAxes(axes, _v0$1, _v1$2, _v2$2, _extents)) {
      return false;
    }

    // finally testing the face normal of the triangle
    // use already existing triangle edge vectors here
    _triangleNormal.crossVectors(_f0, _f1);
    axes = [_triangleNormal.x, _triangleNormal.y, _triangleNormal.z];
    return satForAxes(axes, _v0$1, _v1$2, _v2$2, _extents);
  }
  clampPoint(point, target) {
    return target.copy(point).clamp(this.min, this.max);
  }
  distanceToPoint(point) {
    return this.clampPoint(point, _vector$2).distanceTo(point);
  }
  getBoundingSphere(target) {
    if (this.isEmpty()) {
      target.makeEmpty();
    } else {
      this.getCenter(target.center);
      target.radius = this.getSize(_vector$2).length() * 0.5;
    }
    return target;
  }
  intersect(box) {
    this.min.max(box.min);
    this.max.min(box.max);

    // ensure that if there is no overlap, the result is fully empty, not slightly empty with non-inf/+inf values that will cause subsequence intersects to erroneously return valid values.
    if (this.isEmpty()) this.makeEmpty();
    return this;
  }
  union(box) {
    this.min.min(box.min);
    this.max.max(box.max);
    return this;
  }
  applyMatrix4(matrix) {
    // transform of empty box is an empty box.
    if (this.isEmpty()) return this;

    // NOTE: I am using a binary pattern to specify all 2^3 combinations below
    _points[0].set(this.min.x, this.min.y, this.min.z).applyMatrix4(matrix); // 000
    _points[1].set(this.min.x, this.min.y, this.max.z).applyMatrix4(matrix); // 001
    _points[2].set(this.min.x, this.max.y, this.min.z).applyMatrix4(matrix); // 010
    _points[3].set(this.min.x, this.max.y, this.max.z).applyMatrix4(matrix); // 011
    _points[4].set(this.max.x, this.min.y, this.min.z).applyMatrix4(matrix); // 100
    _points[5].set(this.max.x, this.min.y, this.max.z).applyMatrix4(matrix); // 101
    _points[6].set(this.max.x, this.max.y, this.min.z).applyMatrix4(matrix); // 110
    _points[7].set(this.max.x, this.max.y, this.max.z).applyMatrix4(matrix); // 111

    this.setFromPoints(_points);
    return this;
  }
  translate(offset) {
    this.min.add(offset);
    this.max.add(offset);
    return this;
  }
  equals(box) {
    return box.min.equals(this.min) && box.max.equals(this.max);
  }
}
const _points = [/*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3(), /*@__PURE__*/new Vector3()];
const _vector$2 = /*@__PURE__*/new Vector3();
const _box$2 = /*@__PURE__*/new Box3();

// triangle centered vertices

const _v0$1 = /*@__PURE__*/new Vector3();
const _v1$2 = /*@__PURE__*/new Vector3();
const _v2$2 = /*@__PURE__*/new Vector3();

// triangle edge vectors

const _f0 = /*@__PURE__*/new Vector3();
const _f1 = /*@__PURE__*/new Vector3();
const _f2 = /*@__PURE__*/new Vector3();
const _center = /*@__PURE__*/new Vector3();
const _extents = /*@__PURE__*/new Vector3();
const _triangleNormal = /*@__PURE__*/new Vector3();
const _testAxis = /*@__PURE__*/new Vector3();
function satForAxes(axes, v0, v1, v2, extents) {
  for (let i = 0, j = axes.length - 3; i <= j; i += 3) {
    _testAxis.fromArray(axes, i);
    // project the aabb onto the separating axis
    const r = extents.x * Math.abs(_testAxis.x) + extents.y * Math.abs(_testAxis.y) + extents.z * Math.abs(_testAxis.z);
    // project all 3 vertices of the triangle onto the separating axis
    const p0 = v0.dot(_testAxis);
    const p1 = v1.dot(_testAxis);
    const p2 = v2.dot(_testAxis);
    // actual test, basically see if either of the most extreme of the triangle points intersects r
    if (Math.max(-Math.max(p0, p1, p2), Math.min(p0, p1, p2)) > r) {
      // points of the projected triangle are outside the projected half-length of the aabb
      // the axis is separating and we can exit
      return false;
    }
  }
  return true;
}

const _box$1 = /*@__PURE__*/new Box3();
const _v1$1 = /*@__PURE__*/new Vector3();
const _v2$1 = /*@__PURE__*/new Vector3();
class Sphere {
  constructor(center = new Vector3(), radius = -1) {
    this.isSphere = true;
    this.center = center;
    this.radius = radius;
  }
  set(center, radius) {
    this.center.copy(center);
    this.radius = radius;
    return this;
  }
  setFromPoints(points, optionalCenter) {
    const center = this.center;
    if (optionalCenter !== undefined) {
      center.copy(optionalCenter);
    } else {
      _box$1.setFromPoints(points).getCenter(center);
    }
    let maxRadiusSq = 0;
    for (let i = 0, il = points.length; i < il; i++) {
      maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(points[i]));
    }
    this.radius = Math.sqrt(maxRadiusSq);
    return this;
  }
  copy(sphere) {
    this.center.copy(sphere.center);
    this.radius = sphere.radius;
    return this;
  }
  isEmpty() {
    return this.radius < 0;
  }
  makeEmpty() {
    this.center.set(0, 0, 0);
    this.radius = -1;
    return this;
  }
  containsPoint(point) {
    return point.distanceToSquared(this.center) <= this.radius * this.radius;
  }
  distanceToPoint(point) {
    return point.distanceTo(this.center) - this.radius;
  }
  intersectsSphere(sphere) {
    const radiusSum = this.radius + sphere.radius;
    return sphere.center.distanceToSquared(this.center) <= radiusSum * radiusSum;
  }
  intersectsBox(box) {
    return box.intersectsSphere(this);
  }
  intersectsPlane(plane) {
    return Math.abs(plane.distanceToPoint(this.center)) <= this.radius;
  }
  clampPoint(point, target) {
    const deltaLengthSq = this.center.distanceToSquared(point);
    target.copy(point);
    if (deltaLengthSq > this.radius * this.radius) {
      target.sub(this.center).normalize();
      target.multiplyScalar(this.radius).add(this.center);
    }
    return target;
  }
  getBoundingBox(target) {
    if (this.isEmpty()) {
      // Empty sphere produces empty bounding box
      target.makeEmpty();
      return target;
    }
    target.set(this.center, this.center);
    target.expandByScalar(this.radius);
    return target;
  }
  applyMatrix4(matrix) {
    this.center.applyMatrix4(matrix);
    this.radius = this.radius * matrix.getMaxScaleOnAxis();
    return this;
  }
  translate(offset) {
    this.center.add(offset);
    return this;
  }
  expandByPoint(point) {
    if (this.isEmpty()) {
      this.center.copy(point);
      this.radius = 0;
      return this;
    }
    _v1$1.subVectors(point, this.center);
    const lengthSq = _v1$1.lengthSq();
    if (lengthSq > this.radius * this.radius) {
      // calculate the minimal sphere

      const length = Math.sqrt(lengthSq);
      const delta = (length - this.radius) * 0.5;
      this.center.addScaledVector(_v1$1, delta / length);
      this.radius += delta;
    }
    return this;
  }
  union(sphere) {
    if (sphere.isEmpty()) {
      return this;
    }
    if (this.isEmpty()) {
      this.copy(sphere);
      return this;
    }
    if (this.center.equals(sphere.center) === true) {
      this.radius = Math.max(this.radius, sphere.radius);
    } else {
      _v2$1.subVectors(sphere.center, this.center).setLength(sphere.radius);
      this.expandByPoint(_v1$1.copy(sphere.center).add(_v2$1));
      this.expandByPoint(_v1$1.copy(sphere.center).sub(_v2$1));
    }
    return this;
  }
  equals(sphere) {
    return sphere.center.equals(this.center) && sphere.radius === this.radius;
  }
  clone() {
    return new this.constructor().copy(this);
  }
}

let _id = 0;
const _m1 = /*@__PURE__*/new Matrix4();
const _obj = /*@__PURE__*/new Object3D();
const _offset = /*@__PURE__*/new Vector3();
const _box = /*@__PURE__*/new Box3();
const _boxMorphTargets = /*@__PURE__*/new Box3();
const _vector$1 = /*@__PURE__*/new Vector3();
class BufferGeometry extends EventDispatcher {
  constructor() {
    super();
    this.isBufferGeometry = true;
    Object.defineProperty(this, 'id', {
      value: _id++
    });
    this.uuid = generateUUID();
    this.name = '';
    this.type = 'BufferGeometry';
    this.index = null;
    this.attributes = {};
    this.morphAttributes = {};
    this.morphTargetsRelative = false;
    this.groups = [];
    this.boundingBox = null;
    this.boundingSphere = null;
    this.drawRange = {
      start: 0,
      count: Infinity
    };
    this.userData = {};
  }
  getIndex() {
    return this.index;
  }
  setIndex(index) {
    if (Array.isArray(index)) {
      this.index = new (arrayNeedsUint32(index) ? Uint32BufferAttribute : Uint16BufferAttribute)(index, 1);
    } else {
      this.index = index;
    }
    return this;
  }
  getAttribute(name) {
    return this.attributes[name];
  }
  setAttribute(name, attribute) {
    this.attributes[name] = attribute;
    return this;
  }
  deleteAttribute(name) {
    delete this.attributes[name];
    return this;
  }
  hasAttribute(name) {
    return this.attributes[name] !== undefined;
  }
  addGroup(start, count, materialIndex = 0) {
    this.groups.push({
      start: start,
      count: count,
      materialIndex: materialIndex
    });
  }
  clearGroups() {
    this.groups = [];
  }
  setDrawRange(start, count) {
    this.drawRange.start = start;
    this.drawRange.count = count;
  }
  applyMatrix4(matrix) {
    const position = this.attributes.position;
    if (position !== undefined) {
      position.applyMatrix4(matrix);
      position.needsUpdate = true;
    }
    const normal = this.attributes.normal;
    if (normal !== undefined) {
      const normalMatrix = new Matrix3().getNormalMatrix(matrix);
      normal.applyNormalMatrix(normalMatrix);
      normal.needsUpdate = true;
    }
    const tangent = this.attributes.tangent;
    if (tangent !== undefined) {
      tangent.transformDirection(matrix);
      tangent.needsUpdate = true;
    }
    if (this.boundingBox !== null) {
      this.computeBoundingBox();
    }
    if (this.boundingSphere !== null) {
      this.computeBoundingSphere();
    }
    return this;
  }
  applyQuaternion(q) {
    _m1.makeRotationFromQuaternion(q);
    this.applyMatrix4(_m1);
    return this;
  }
  rotateX(angle) {
    // rotate geometry around world x-axis

    _m1.makeRotationX(angle);
    this.applyMatrix4(_m1);
    return this;
  }
  rotateY(angle) {
    // rotate geometry around world y-axis

    _m1.makeRotationY(angle);
    this.applyMatrix4(_m1);
    return this;
  }
  rotateZ(angle) {
    // rotate geometry around world z-axis

    _m1.makeRotationZ(angle);
    this.applyMatrix4(_m1);
    return this;
  }
  translate(x, y, z) {
    // translate geometry

    _m1.makeTranslation(x, y, z);
    this.applyMatrix4(_m1);
    return this;
  }
  scale(x, y, z) {
    // scale geometry

    _m1.makeScale(x, y, z);
    this.applyMatrix4(_m1);
    return this;
  }
  lookAt(vector) {
    _obj.lookAt(vector);
    _obj.updateMatrix();
    this.applyMatrix4(_obj.matrix);
    return this;
  }
  center() {
    this.computeBoundingBox();
    this.boundingBox.getCenter(_offset).negate();
    this.translate(_offset.x, _offset.y, _offset.z);
    return this;
  }
  setFromPoints(points) {
    const position = [];
    for (let i = 0, l = points.length; i < l; i++) {
      const point = points[i];
      position.push(point.x, point.y, point.z || 0);
    }
    this.setAttribute('position', new Float32BufferAttribute(position, 3));
    return this;
  }
  computeBoundingBox() {
    if (this.boundingBox === null) {
      this.boundingBox = new Box3();
    }
    const position = this.attributes.position;
    const morphAttributesPosition = this.morphAttributes.position;
    if (position && position.isGLBufferAttribute) {
      console.error('THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.', this);
      this.boundingBox.set(new Vector3(-Infinity, -Infinity, -Infinity), new Vector3(+Infinity, +Infinity, +Infinity));
      return;
    }
    if (position !== undefined) {
      this.boundingBox.setFromBufferAttribute(position);

      // process morph attributes if present

      if (morphAttributesPosition) {
        for (let i = 0, il = morphAttributesPosition.length; i < il; i++) {
          const morphAttribute = morphAttributesPosition[i];
          _box.setFromBufferAttribute(morphAttribute);
          if (this.morphTargetsRelative) {
            _vector$1.addVectors(this.boundingBox.min, _box.min);
            this.boundingBox.expandByPoint(_vector$1);
            _vector$1.addVectors(this.boundingBox.max, _box.max);
            this.boundingBox.expandByPoint(_vector$1);
          } else {
            this.boundingBox.expandByPoint(_box.min);
            this.boundingBox.expandByPoint(_box.max);
          }
        }
      }
    } else {
      this.boundingBox.makeEmpty();
    }
    if (isNaN(this.boundingBox.min.x) || isNaN(this.boundingBox.min.y) || isNaN(this.boundingBox.min.z)) {
      console.error('THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.', this);
    }
  }
  computeBoundingSphere() {
    if (this.boundingSphere === null) {
      this.boundingSphere = new Sphere();
    }
    const position = this.attributes.position;
    const morphAttributesPosition = this.morphAttributes.position;
    if (position && position.isGLBufferAttribute) {
      console.error('THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.', this);
      this.boundingSphere.set(new Vector3(), Infinity);
      return;
    }
    if (position) {
      // first, find the center of the bounding sphere

      const center = this.boundingSphere.center;
      _box.setFromBufferAttribute(position);

      // process morph attributes if present

      if (morphAttributesPosition) {
        for (let i = 0, il = morphAttributesPosition.length; i < il; i++) {
          const morphAttribute = morphAttributesPosition[i];
          _boxMorphTargets.setFromBufferAttribute(morphAttribute);
          if (this.morphTargetsRelative) {
            _vector$1.addVectors(_box.min, _boxMorphTargets.min);
            _box.expandByPoint(_vector$1);
            _vector$1.addVectors(_box.max, _boxMorphTargets.max);
            _box.expandByPoint(_vector$1);
          } else {
            _box.expandByPoint(_boxMorphTargets.min);
            _box.expandByPoint(_boxMorphTargets.max);
          }
        }
      }
      _box.getCenter(center);

      // second, try to find a boundingSphere with a radius smaller than the
      // boundingSphere of the boundingBox: sqrt(3) smaller in the best case

      let maxRadiusSq = 0;
      for (let i = 0, il = position.count; i < il; i++) {
        _vector$1.fromBufferAttribute(position, i);
        maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(_vector$1));
      }

      // process morph attributes if present

      if (morphAttributesPosition) {
        for (let i = 0, il = morphAttributesPosition.length; i < il; i++) {
          const morphAttribute = morphAttributesPosition[i];
          const morphTargetsRelative = this.morphTargetsRelative;
          for (let j = 0, jl = morphAttribute.count; j < jl; j++) {
            _vector$1.fromBufferAttribute(morphAttribute, j);
            if (morphTargetsRelative) {
              _offset.fromBufferAttribute(position, j);
              _vector$1.add(_offset);
            }
            maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(_vector$1));
          }
        }
      }
      this.boundingSphere.radius = Math.sqrt(maxRadiusSq);
      if (isNaN(this.boundingSphere.radius)) {
        console.error('THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.', this);
      }
    }
  }
  computeTangents() {
    const index = this.index;
    const attributes = this.attributes;

    // based on http://www.terathon.com/code/tangent.html
    // (per vertex tangents)

    if (index === null || attributes.position === undefined || attributes.normal === undefined || attributes.uv === undefined) {
      console.error('THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)');
      return;
    }
    const positionAttribute = attributes.position;
    const normalAttribute = attributes.normal;
    const uvAttribute = attributes.uv;
    if (this.hasAttribute('tangent') === false) {
      this.setAttribute('tangent', new BufferAttribute(new Float32Array(4 * positionAttribute.count), 4));
    }
    const tangentAttribute = this.getAttribute('tangent');
    const tan1 = [],
      tan2 = [];
    for (let i = 0; i < positionAttribute.count; i++) {
      tan1[i] = new Vector3();
      tan2[i] = new Vector3();
    }
    const vA = new Vector3(),
      vB = new Vector3(),
      vC = new Vector3(),
      uvA = new Vector2(),
      uvB = new Vector2(),
      uvC = new Vector2(),
      sdir = new Vector3(),
      tdir = new Vector3();
    function handleTriangle(a, b, c) {
      vA.fromBufferAttribute(positionAttribute, a);
      vB.fromBufferAttribute(positionAttribute, b);
      vC.fromBufferAttribute(positionAttribute, c);
      uvA.fromBufferAttribute(uvAttribute, a);
      uvB.fromBufferAttribute(uvAttribute, b);
      uvC.fromBufferAttribute(uvAttribute, c);
      vB.sub(vA);
      vC.sub(vA);
      uvB.sub(uvA);
      uvC.sub(uvA);
      const r = 1.0 / (uvB.x * uvC.y - uvC.x * uvB.y);

      // silently ignore degenerate uv triangles having coincident or colinear vertices

      if (!isFinite(r)) return;
      sdir.copy(vB).multiplyScalar(uvC.y).addScaledVector(vC, -uvB.y).multiplyScalar(r);
      tdir.copy(vC).multiplyScalar(uvB.x).addScaledVector(vB, -uvC.x).multiplyScalar(r);
      tan1[a].add(sdir);
      tan1[b].add(sdir);
      tan1[c].add(sdir);
      tan2[a].add(tdir);
      tan2[b].add(tdir);
      tan2[c].add(tdir);
    }
    let groups = this.groups;
    if (groups.length === 0) {
      groups = [{
        start: 0,
        count: index.count
      }];
    }
    for (let i = 0, il = groups.length; i < il; ++i) {
      const group = groups[i];
      const start = group.start;
      const count = group.count;
      for (let j = start, jl = start + count; j < jl; j += 3) {
        handleTriangle(index.getX(j + 0), index.getX(j + 1), index.getX(j + 2));
      }
    }
    const tmp = new Vector3(),
      tmp2 = new Vector3();
    const n = new Vector3(),
      n2 = new Vector3();
    function handleVertex(v) {
      n.fromBufferAttribute(normalAttribute, v);
      n2.copy(n);
      const t = tan1[v];

      // Gram-Schmidt orthogonalize

      tmp.copy(t);
      tmp.sub(n.multiplyScalar(n.dot(t))).normalize();

      // Calculate handedness

      tmp2.crossVectors(n2, t);
      const test = tmp2.dot(tan2[v]);
      const w = test < 0.0 ? -1.0 : 1.0;
      tangentAttribute.setXYZW(v, tmp.x, tmp.y, tmp.z, w);
    }
    for (let i = 0, il = groups.length; i < il; ++i) {
      const group = groups[i];
      const start = group.start;
      const count = group.count;
      for (let j = start, jl = start + count; j < jl; j += 3) {
        handleVertex(index.getX(j + 0));
        handleVertex(index.getX(j + 1));
        handleVertex(index.getX(j + 2));
      }
    }
  }
  computeVertexNormals() {
    const index = this.index;
    const positionAttribute = this.getAttribute('position');
    if (positionAttribute !== undefined) {
      let normalAttribute = this.getAttribute('normal');
      if (normalAttribute === undefined) {
        normalAttribute = new BufferAttribute(new Float32Array(positionAttribute.count * 3), 3);
        this.setAttribute('normal', normalAttribute);
      } else {
        // reset existing normals to zero

        for (let i = 0, il = normalAttribute.count; i < il; i++) {
          normalAttribute.setXYZ(i, 0, 0, 0);
        }
      }
      const pA = new Vector3(),
        pB = new Vector3(),
        pC = new Vector3();
      const nA = new Vector3(),
        nB = new Vector3(),
        nC = new Vector3();
      const cb = new Vector3(),
        ab = new Vector3();

      // indexed elements

      if (index) {
        for (let i = 0, il = index.count; i < il; i += 3) {
          const vA = index.getX(i + 0);
          const vB = index.getX(i + 1);
          const vC = index.getX(i + 2);
          pA.fromBufferAttribute(positionAttribute, vA);
          pB.fromBufferAttribute(positionAttribute, vB);
          pC.fromBufferAttribute(positionAttribute, vC);
          cb.subVectors(pC, pB);
          ab.subVectors(pA, pB);
          cb.cross(ab);
          nA.fromBufferAttribute(normalAttribute, vA);
          nB.fromBufferAttribute(normalAttribute, vB);
          nC.fromBufferAttribute(normalAttribute, vC);
          nA.add(cb);
          nB.add(cb);
          nC.add(cb);
          normalAttribute.setXYZ(vA, nA.x, nA.y, nA.z);
          normalAttribute.setXYZ(vB, nB.x, nB.y, nB.z);
          normalAttribute.setXYZ(vC, nC.x, nC.y, nC.z);
        }
      } else {
        // non-indexed elements (unconnected triangle soup)

        for (let i = 0, il = positionAttribute.count; i < il; i += 3) {
          pA.fromBufferAttribute(positionAttribute, i + 0);
          pB.fromBufferAttribute(positionAttribute, i + 1);
          pC.fromBufferAttribute(positionAttribute, i + 2);
          cb.subVectors(pC, pB);
          ab.subVectors(pA, pB);
          cb.cross(ab);
          normalAttribute.setXYZ(i + 0, cb.x, cb.y, cb.z);
          normalAttribute.setXYZ(i + 1, cb.x, cb.y, cb.z);
          normalAttribute.setXYZ(i + 2, cb.x, cb.y, cb.z);
        }
      }
      this.normalizeNormals();
      normalAttribute.needsUpdate = true;
    }
  }
  normalizeNormals() {
    const normals = this.attributes.normal;
    for (let i = 0, il = normals.count; i < il; i++) {
      _vector$1.fromBufferAttribute(normals, i);
      _vector$1.normalize();
      normals.setXYZ(i, _vector$1.x, _vector$1.y, _vector$1.z);
    }
  }
  toNonIndexed() {
    function convertBufferAttribute(attribute, indices) {
      const array = attribute.array;
      const itemSize = attribute.itemSize;
      const normalized = attribute.normalized;
      const array2 = new array.constructor(indices.length * itemSize);
      let index = 0,
        index2 = 0;
      for (let i = 0, l = indices.length; i < l; i++) {
        if (attribute.isInterleavedBufferAttribute) {
          index = indices[i] * attribute.data.stride + attribute.offset;
        } else {
          index = indices[i] * itemSize;
        }
        for (let j = 0; j < itemSize; j++) {
          array2[index2++] = array[index++];
        }
      }
      return new BufferAttribute(array2, itemSize, normalized);
    }

    //

    if (this.index === null) {
      console.warn('THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed.');
      return this;
    }
    const geometry2 = new BufferGeometry();
    const indices = this.index.array;
    const attributes = this.attributes;

    // attributes

    for (const name in attributes) {
      const attribute = attributes[name];
      const newAttribute = convertBufferAttribute(attribute, indices);
      geometry2.setAttribute(name, newAttribute);
    }

    // morph attributes

    const morphAttributes = this.morphAttributes;
    for (const name in morphAttributes) {
      const morphArray = [];
      const morphAttribute = morphAttributes[name]; // morphAttribute: array of Float32BufferAttributes

      for (let i = 0, il = morphAttribute.length; i < il; i++) {
        const attribute = morphAttribute[i];
        const newAttribute = convertBufferAttribute(attribute, indices);
        morphArray.push(newAttribute);
      }
      geometry2.morphAttributes[name] = morphArray;
    }
    geometry2.morphTargetsRelative = this.morphTargetsRelative;

    // groups

    const groups = this.groups;
    for (let i = 0, l = groups.length; i < l; i++) {
      const group = groups[i];
      geometry2.addGroup(group.start, group.count, group.materialIndex);
    }
    return geometry2;
  }
  toJSON() {
    const data = {
      metadata: {
        version: 4.6,
        type: 'BufferGeometry',
        generator: 'BufferGeometry.toJSON'
      }
    };

    // standard BufferGeometry serialization

    data.uuid = this.uuid;
    data.type = this.type;
    if (this.name !== '') data.name = this.name;
    if (Object.keys(this.userData).length > 0) data.userData = this.userData;
    if (this.parameters !== undefined) {
      const parameters = this.parameters;
      for (const key in parameters) {
        if (parameters[key] !== undefined) data[key] = parameters[key];
      }
      return data;
    }

    // for simplicity the code assumes attributes are not shared across geometries, see #15811

    data.data = {
      attributes: {}
    };
    const index = this.index;
    if (index !== null) {
      data.data.index = {
        type: index.array.constructor.name,
        array: Array.prototype.slice.call(index.array)
      };
    }
    const attributes = this.attributes;
    for (const key in attributes) {
      const attribute = attributes[key];
      data.data.attributes[key] = attribute.toJSON(data.data);
    }
    const morphAttributes = {};
    let hasMorphAttributes = false;
    for (const key in this.morphAttributes) {
      const attributeArray = this.morphAttributes[key];
      const array = [];
      for (let i = 0, il = attributeArray.length; i < il; i++) {
        const attribute = attributeArray[i];
        array.push(attribute.toJSON(data.data));
      }
      if (array.length > 0) {
        morphAttributes[key] = array;
        hasMorphAttributes = true;
      }
    }
    if (hasMorphAttributes) {
      data.data.morphAttributes = morphAttributes;
      data.data.morphTargetsRelative = this.morphTargetsRelative;
    }
    const groups = this.groups;
    if (groups.length > 0) {
      data.data.groups = JSON.parse(JSON.stringify(groups));
    }
    const boundingSphere = this.boundingSphere;
    if (boundingSphere !== null) {
      data.data.boundingSphere = {
        center: boundingSphere.center.toArray(),
        radius: boundingSphere.radius
      };
    }
    return data;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(source) {
    // reset

    this.index = null;
    this.attributes = {};
    this.morphAttributes = {};
    this.groups = [];
    this.boundingBox = null;
    this.boundingSphere = null;

    // used for storing cloned, shared data

    const data = {};

    // name

    this.name = source.name;

    // index

    const index = source.index;
    if (index !== null) {
      this.setIndex(index.clone(data));
    }

    // attributes

    const attributes = source.attributes;
    for (const name in attributes) {
      const attribute = attributes[name];
      this.setAttribute(name, attribute.clone(data));
    }

    // morph attributes

    const morphAttributes = source.morphAttributes;
    for (const name in morphAttributes) {
      const array = [];
      const morphAttribute = morphAttributes[name]; // morphAttribute: array of Float32BufferAttributes

      for (let i = 0, l = morphAttribute.length; i < l; i++) {
        array.push(morphAttribute[i].clone(data));
      }
      this.morphAttributes[name] = array;
    }
    this.morphTargetsRelative = source.morphTargetsRelative;

    // groups

    const groups = source.groups;
    for (let i = 0, l = groups.length; i < l; i++) {
      const group = groups[i];
      this.addGroup(group.start, group.count, group.materialIndex);
    }

    // bounding box

    const boundingBox = source.boundingBox;
    if (boundingBox !== null) {
      this.boundingBox = boundingBox.clone();
    }

    // bounding sphere

    const boundingSphere = source.boundingSphere;
    if (boundingSphere !== null) {
      this.boundingSphere = boundingSphere.clone();
    }

    // draw range

    this.drawRange.start = source.drawRange.start;
    this.drawRange.count = source.drawRange.count;

    // user data

    this.userData = source.userData;
    return this;
  }
  dispose() {
    this.dispatchEvent({
      type: 'dispose'
    });
  }
}

const _vector = /*@__PURE__*/new Vector3();
const _segCenter = /*@__PURE__*/new Vector3();
const _segDir = /*@__PURE__*/new Vector3();
const _diff = /*@__PURE__*/new Vector3();
const _edge1 = /*@__PURE__*/new Vector3();
const _edge2 = /*@__PURE__*/new Vector3();
const _normal = /*@__PURE__*/new Vector3();
class Ray {
  constructor(origin = new Vector3(), direction = new Vector3(0, 0, -1)) {
    this.origin = origin;
    this.direction = direction;
  }
  set(origin, direction) {
    this.origin.copy(origin);
    this.direction.copy(direction);
    return this;
  }
  copy(ray) {
    this.origin.copy(ray.origin);
    this.direction.copy(ray.direction);
    return this;
  }
  at(t, target) {
    return target.copy(this.origin).addScaledVector(this.direction, t);
  }
  lookAt(v) {
    this.direction.copy(v).sub(this.origin).normalize();
    return this;
  }
  recast(t) {
    this.origin.copy(this.at(t, _vector));
    return this;
  }
  closestPointToPoint(point, target) {
    target.subVectors(point, this.origin);
    const directionDistance = target.dot(this.direction);
    if (directionDistance < 0) {
      return target.copy(this.origin);
    }
    return target.copy(this.origin).addScaledVector(this.direction, directionDistance);
  }
  distanceToPoint(point) {
    return Math.sqrt(this.distanceSqToPoint(point));
  }
  distanceSqToPoint(point) {
    const directionDistance = _vector.subVectors(point, this.origin).dot(this.direction);

    // point behind the ray

    if (directionDistance < 0) {
      return this.origin.distanceToSquared(point);
    }
    _vector.copy(this.origin).addScaledVector(this.direction, directionDistance);
    return _vector.distanceToSquared(point);
  }
  distanceSqToSegment(v0, v1, optionalPointOnRay, optionalPointOnSegment) {
    // from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteDistRaySegment.h
    // It returns the min distance between the ray and the segment
    // defined by v0 and v1
    // It can also set two optional targets :
    // - The closest point on the ray
    // - The closest point on the segment

    _segCenter.copy(v0).add(v1).multiplyScalar(0.5);
    _segDir.copy(v1).sub(v0).normalize();
    _diff.copy(this.origin).sub(_segCenter);
    const segExtent = v0.distanceTo(v1) * 0.5;
    const a01 = -this.direction.dot(_segDir);
    const b0 = _diff.dot(this.direction);
    const b1 = -_diff.dot(_segDir);
    const c = _diff.lengthSq();
    const det = Math.abs(1 - a01 * a01);
    let s0, s1, sqrDist, extDet;
    if (det > 0) {
      // The ray and segment are not parallel.

      s0 = a01 * b1 - b0;
      s1 = a01 * b0 - b1;
      extDet = segExtent * det;
      if (s0 >= 0) {
        if (s1 >= -extDet) {
          if (s1 <= extDet) {
            // region 0
            // Minimum at interior points of ray and segment.

            const invDet = 1 / det;
            s0 *= invDet;
            s1 *= invDet;
            sqrDist = s0 * (s0 + a01 * s1 + 2 * b0) + s1 * (a01 * s0 + s1 + 2 * b1) + c;
          } else {
            // region 1

            s1 = segExtent;
            s0 = Math.max(0, -(a01 * s1 + b0));
            sqrDist = -s0 * s0 + s1 * (s1 + 2 * b1) + c;
          }
        } else {
          // region 5

          s1 = -segExtent;
          s0 = Math.max(0, -(a01 * s1 + b0));
          sqrDist = -s0 * s0 + s1 * (s1 + 2 * b1) + c;
        }
      } else {
        if (s1 <= -extDet) {
          // region 4

          s0 = Math.max(0, -(-a01 * segExtent + b0));
          s1 = s0 > 0 ? -segExtent : Math.min(Math.max(-segExtent, -b1), segExtent);
          sqrDist = -s0 * s0 + s1 * (s1 + 2 * b1) + c;
        } else if (s1 <= extDet) {
          // region 3

          s0 = 0;
          s1 = Math.min(Math.max(-segExtent, -b1), segExtent);
          sqrDist = s1 * (s1 + 2 * b1) + c;
        } else {
          // region 2

          s0 = Math.max(0, -(a01 * segExtent + b0));
          s1 = s0 > 0 ? segExtent : Math.min(Math.max(-segExtent, -b1), segExtent);
          sqrDist = -s0 * s0 + s1 * (s1 + 2 * b1) + c;
        }
      }
    } else {
      // Ray and segment are parallel.

      s1 = a01 > 0 ? -segExtent : segExtent;
      s0 = Math.max(0, -(a01 * s1 + b0));
      sqrDist = -s0 * s0 + s1 * (s1 + 2 * b1) + c;
    }
    if (optionalPointOnRay) {
      optionalPointOnRay.copy(this.origin).addScaledVector(this.direction, s0);
    }
    if (optionalPointOnSegment) {
      optionalPointOnSegment.copy(_segCenter).addScaledVector(_segDir, s1);
    }
    return sqrDist;
  }
  intersectSphere(sphere, target) {
    _vector.subVectors(sphere.center, this.origin);
    const tca = _vector.dot(this.direction);
    const d2 = _vector.dot(_vector) - tca * tca;
    const radius2 = sphere.radius * sphere.radius;
    if (d2 > radius2) return null;
    const thc = Math.sqrt(radius2 - d2);

    // t0 = first intersect point - entrance on front of sphere
    const t0 = tca - thc;

    // t1 = second intersect point - exit point on back of sphere
    const t1 = tca + thc;

    // test to see if t1 is behind the ray - if so, return null
    if (t1 < 0) return null;

    // test to see if t0 is behind the ray:
    // if it is, the ray is inside the sphere, so return the second exit point scaled by t1,
    // in order to always return an intersect point that is in front of the ray.
    if (t0 < 0) return this.at(t1, target);

    // else t0 is in front of the ray, so return the first collision point scaled by t0
    return this.at(t0, target);
  }
  intersectsSphere(sphere) {
    return this.distanceSqToPoint(sphere.center) <= sphere.radius * sphere.radius;
  }
  distanceToPlane(plane) {
    const denominator = plane.normal.dot(this.direction);
    if (denominator === 0) {
      // line is coplanar, return origin
      if (plane.distanceToPoint(this.origin) === 0) {
        return 0;
      }

      // Null is preferable to undefined since undefined means.... it is undefined

      return null;
    }
    const t = -(this.origin.dot(plane.normal) + plane.constant) / denominator;

    // Return if the ray never intersects the plane

    return t >= 0 ? t : null;
  }
  intersectPlane(plane, target) {
    const t = this.distanceToPlane(plane);
    if (t === null) {
      return null;
    }
    return this.at(t, target);
  }
  intersectsPlane(plane) {
    // check if the ray lies on the plane first

    const distToPoint = plane.distanceToPoint(this.origin);
    if (distToPoint === 0) {
      return true;
    }
    const denominator = plane.normal.dot(this.direction);
    if (denominator * distToPoint < 0) {
      return true;
    }

    // ray origin is behind the plane (and is pointing behind it)

    return false;
  }
  intersectBox(box, target) {
    let tmin, tmax, tymin, tymax, tzmin, tzmax;
    const invdirx = 1 / this.direction.x,
      invdiry = 1 / this.direction.y,
      invdirz = 1 / this.direction.z;
    const origin = this.origin;
    if (invdirx >= 0) {
      tmin = (box.min.x - origin.x) * invdirx;
      tmax = (box.max.x - origin.x) * invdirx;
    } else {
      tmin = (box.max.x - origin.x) * invdirx;
      tmax = (box.min.x - origin.x) * invdirx;
    }
    if (invdiry >= 0) {
      tymin = (box.min.y - origin.y) * invdiry;
      tymax = (box.max.y - origin.y) * invdiry;
    } else {
      tymin = (box.max.y - origin.y) * invdiry;
      tymax = (box.min.y - origin.y) * invdiry;
    }
    if (tmin > tymax || tymin > tmax) return null;
    if (tymin > tmin || isNaN(tmin)) tmin = tymin;
    if (tymax < tmax || isNaN(tmax)) tmax = tymax;
    if (invdirz >= 0) {
      tzmin = (box.min.z - origin.z) * invdirz;
      tzmax = (box.max.z - origin.z) * invdirz;
    } else {
      tzmin = (box.max.z - origin.z) * invdirz;
      tzmax = (box.min.z - origin.z) * invdirz;
    }
    if (tmin > tzmax || tzmin > tmax) return null;
    if (tzmin > tmin || tmin !== tmin) tmin = tzmin;
    if (tzmax < tmax || tmax !== tmax) tmax = tzmax;

    //return point closest to the ray (positive side)

    if (tmax < 0) return null;
    return this.at(tmin >= 0 ? tmin : tmax, target);
  }
  intersectsBox(box) {
    return this.intersectBox(box, _vector) !== null;
  }
  intersectTriangle(a, b, c, backfaceCulling, target) {
    // Compute the offset origin, edges, and normal.

    // from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteIntrRay3Triangle3.h

    _edge1.subVectors(b, a);
    _edge2.subVectors(c, a);
    _normal.crossVectors(_edge1, _edge2);

    // Solve Q + t*D = b1*E1 + b2*E2 (Q = kDiff, D = ray direction,
    // E1 = kEdge1, E2 = kEdge2, N = Cross(E1,E2)) by
    //   |Dot(D,N)|*b1 = sign(Dot(D,N))*Dot(D,Cross(Q,E2))
    //   |Dot(D,N)|*b2 = sign(Dot(D,N))*Dot(D,Cross(E1,Q))
    //   |Dot(D,N)|*t = -sign(Dot(D,N))*Dot(Q,N)
    let DdN = this.direction.dot(_normal);
    let sign;
    if (DdN > 0) {
      if (backfaceCulling) return null;
      sign = 1;
    } else if (DdN < 0) {
      sign = -1;
      DdN = -DdN;
    } else {
      return null;
    }
    _diff.subVectors(this.origin, a);
    const DdQxE2 = sign * this.direction.dot(_edge2.crossVectors(_diff, _edge2));

    // b1 < 0, no intersection
    if (DdQxE2 < 0) {
      return null;
    }
    const DdE1xQ = sign * this.direction.dot(_edge1.cross(_diff));

    // b2 < 0, no intersection
    if (DdE1xQ < 0) {
      return null;
    }

    // b1+b2 > 1, no intersection
    if (DdQxE2 + DdE1xQ > DdN) {
      return null;
    }

    // Line intersects triangle, check if ray does.
    const QdN = -sign * _diff.dot(_normal);

    // t < 0, no intersection
    if (QdN < 0) {
      return null;
    }

    // Ray intersects triangle.
    return this.at(QdN / DdN, target);
  }
  applyMatrix4(matrix4) {
    this.origin.applyMatrix4(matrix4);
    this.direction.transformDirection(matrix4);
    return this;
  }
  equals(ray) {
    return ray.origin.equals(this.origin) && ray.direction.equals(this.direction);
  }
  clone() {
    return new this.constructor().copy(this);
  }
}

const _v0 = /*@__PURE__*/new Vector3();
const _v1 = /*@__PURE__*/new Vector3();
const _v2 = /*@__PURE__*/new Vector3();
const _v3$1 = /*@__PURE__*/new Vector3();
const _vab = /*@__PURE__*/new Vector3();
const _vac = /*@__PURE__*/new Vector3();
const _vbc = /*@__PURE__*/new Vector3();
const _vap = /*@__PURE__*/new Vector3();
const _vbp = /*@__PURE__*/new Vector3();
const _vcp = /*@__PURE__*/new Vector3();
class Triangle {
  constructor(a = new Vector3(), b = new Vector3(), c = new Vector3()) {
    this.a = a;
    this.b = b;
    this.c = c;
  }
  static getNormal(a, b, c, target) {
    target.subVectors(c, b);
    _v0.subVectors(a, b);
    target.cross(_v0);
    const targetLengthSq = target.lengthSq();
    if (targetLengthSq > 0) {
      return target.multiplyScalar(1 / Math.sqrt(targetLengthSq));
    }
    return target.set(0, 0, 0);
  }

  // static/instance method to calculate barycentric coordinates
  // based on: http://www.blackpawn.com/texts/pointinpoly/default.html
  static getBarycoord(point, a, b, c, target) {
    _v0.subVectors(c, a);
    _v1.subVectors(b, a);
    _v2.subVectors(point, a);
    const dot00 = _v0.dot(_v0);
    const dot01 = _v0.dot(_v1);
    const dot02 = _v0.dot(_v2);
    const dot11 = _v1.dot(_v1);
    const dot12 = _v1.dot(_v2);
    const denom = dot00 * dot11 - dot01 * dot01;

    // collinear or singular triangle
    if (denom === 0) {
      target.set(0, 0, 0);
      return null;
    }
    const invDenom = 1 / denom;
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    // barycentric coordinates must always sum to 1
    return target.set(1 - u - v, v, u);
  }
  static containsPoint(point, a, b, c) {
    // if the triangle is degenerate then we can't contain a point
    if (this.getBarycoord(point, a, b, c, _v3$1) === null) {
      return false;
    }
    return _v3$1.x >= 0 && _v3$1.y >= 0 && _v3$1.x + _v3$1.y <= 1;
  }
  static getInterpolation(point, p1, p2, p3, v1, v2, v3, target) {
    if (this.getBarycoord(point, p1, p2, p3, _v3$1) === null) {
      target.x = 0;
      target.y = 0;
      if ('z' in target) target.z = 0;
      if ('w' in target) target.w = 0;
      return null;
    }
    target.setScalar(0);
    target.addScaledVector(v1, _v3$1.x);
    target.addScaledVector(v2, _v3$1.y);
    target.addScaledVector(v3, _v3$1.z);
    return target;
  }
  static isFrontFacing(a, b, c, direction) {
    _v0.subVectors(c, b);
    _v1.subVectors(a, b);

    // strictly front facing
    return _v0.cross(_v1).dot(direction) < 0 ? true : false;
  }
  set(a, b, c) {
    this.a.copy(a);
    this.b.copy(b);
    this.c.copy(c);
    return this;
  }
  setFromPointsAndIndices(points, i0, i1, i2) {
    this.a.copy(points[i0]);
    this.b.copy(points[i1]);
    this.c.copy(points[i2]);
    return this;
  }
  setFromAttributeAndIndices(attribute, i0, i1, i2) {
    this.a.fromBufferAttribute(attribute, i0);
    this.b.fromBufferAttribute(attribute, i1);
    this.c.fromBufferAttribute(attribute, i2);
    return this;
  }
  clone() {
    return new this.constructor().copy(this);
  }
  copy(triangle) {
    this.a.copy(triangle.a);
    this.b.copy(triangle.b);
    this.c.copy(triangle.c);
    return this;
  }
  getArea() {
    _v0.subVectors(this.c, this.b);
    _v1.subVectors(this.a, this.b);
    return _v0.cross(_v1).length() * 0.5;
  }
  getMidpoint(target) {
    return target.addVectors(this.a, this.b).add(this.c).multiplyScalar(1 / 3);
  }
  getNormal(target) {
    return Triangle.getNormal(this.a, this.b, this.c, target);
  }
  getPlane(target) {
    return target.setFromCoplanarPoints(this.a, this.b, this.c);
  }
  getBarycoord(point, target) {
    return Triangle.getBarycoord(point, this.a, this.b, this.c, target);
  }
  getInterpolation(point, v1, v2, v3, target) {
    return Triangle.getInterpolation(point, this.a, this.b, this.c, v1, v2, v3, target);
  }
  containsPoint(point) {
    return Triangle.containsPoint(point, this.a, this.b, this.c);
  }
  isFrontFacing(direction) {
    return Triangle.isFrontFacing(this.a, this.b, this.c, direction);
  }
  intersectsBox(box) {
    return box.intersectsTriangle(this);
  }
  closestPointToPoint(p, target) {
    const a = this.a,
      b = this.b,
      c = this.c;
    let v, w;

    // algorithm thanks to Real-Time Collision Detection by Christer Ericson,
    // published by Morgan Kaufmann Publishers, (c) 2005 Elsevier Inc.,
    // under the accompanying license; see chapter 5.1.5 for detailed explanation.
    // basically, we're distinguishing which of the voronoi regions of the triangle
    // the point lies in with the minimum amount of redundant computation.

    _vab.subVectors(b, a);
    _vac.subVectors(c, a);
    _vap.subVectors(p, a);
    const d1 = _vab.dot(_vap);
    const d2 = _vac.dot(_vap);
    if (d1 <= 0 && d2 <= 0) {
      // vertex region of A; barycentric coords (1, 0, 0)
      return target.copy(a);
    }
    _vbp.subVectors(p, b);
    const d3 = _vab.dot(_vbp);
    const d4 = _vac.dot(_vbp);
    if (d3 >= 0 && d4 <= d3) {
      // vertex region of B; barycentric coords (0, 1, 0)
      return target.copy(b);
    }
    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) {
      v = d1 / (d1 - d3);
      // edge region of AB; barycentric coords (1-v, v, 0)
      return target.copy(a).addScaledVector(_vab, v);
    }
    _vcp.subVectors(p, c);
    const d5 = _vab.dot(_vcp);
    const d6 = _vac.dot(_vcp);
    if (d6 >= 0 && d5 <= d6) {
      // vertex region of C; barycentric coords (0, 0, 1)
      return target.copy(c);
    }
    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) {
      w = d2 / (d2 - d6);
      // edge region of AC; barycentric coords (1-w, 0, w)
      return target.copy(a).addScaledVector(_vac, w);
    }
    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
      _vbc.subVectors(c, b);
      w = (d4 - d3) / (d4 - d3 + (d5 - d6));
      // edge region of BC; barycentric coords (0, 1-w, w)
      return target.copy(b).addScaledVector(_vbc, w); // edge region of BC
    }

    // face region
    const denom = 1 / (va + vb + vc);
    // u = va * denom
    v = vb * denom;
    w = vc * denom;
    return target.copy(a).addScaledVector(_vab, v).addScaledVector(_vac, w);
  }
  equals(triangle) {
    return triangle.a.equals(this.a) && triangle.b.equals(this.b) && triangle.c.equals(this.c);
  }
}

class MeshBasicMaterial extends Material {
  constructor(parameters) {
    super();
    this.isMeshBasicMaterial = true;
    this.type = 'MeshBasicMaterial';
    this.color = new Color(0xffffff); // emissive

    this.map = null;
    this.lightMap = null;
    this.lightMapIntensity = 1.0;
    this.aoMap = null;
    this.aoMapIntensity = 1.0;
    this.specularMap = null;
    this.alphaMap = null;
    this.envMap = null;
    this.envMapRotation = new Euler();
    this.combine = MultiplyOperation;
    this.reflectivity = 1;
    this.refractionRatio = 0.98;
    this.wireframe = false;
    this.wireframeLinewidth = 1;
    this.wireframeLinecap = 'round';
    this.wireframeLinejoin = 'round';
    this.fog = true;
    this.setValues(parameters);
  }
  copy(source) {
    super.copy(source);
    this.color.copy(source.color);
    this.map = source.map;
    this.lightMap = source.lightMap;
    this.lightMapIntensity = source.lightMapIntensity;
    this.aoMap = source.aoMap;
    this.aoMapIntensity = source.aoMapIntensity;
    this.specularMap = source.specularMap;
    this.alphaMap = source.alphaMap;
    this.envMap = source.envMap;
    this.envMapRotation.copy(source.envMapRotation);
    this.combine = source.combine;
    this.reflectivity = source.reflectivity;
    this.refractionRatio = source.refractionRatio;
    this.wireframe = source.wireframe;
    this.wireframeLinewidth = source.wireframeLinewidth;
    this.wireframeLinecap = source.wireframeLinecap;
    this.wireframeLinejoin = source.wireframeLinejoin;
    this.fog = source.fog;
    return this;
  }
}

const _inverseMatrix = /*@__PURE__*/new Matrix4();
const _ray = /*@__PURE__*/new Ray();
const _sphere = /*@__PURE__*/new Sphere();
const _sphereHitAt = /*@__PURE__*/new Vector3();
const _vA = /*@__PURE__*/new Vector3();
const _vB = /*@__PURE__*/new Vector3();
const _vC = /*@__PURE__*/new Vector3();
const _tempA = /*@__PURE__*/new Vector3();
const _morphA = /*@__PURE__*/new Vector3();
const _uvA = /*@__PURE__*/new Vector2();
const _uvB = /*@__PURE__*/new Vector2();
const _uvC = /*@__PURE__*/new Vector2();
const _normalA = /*@__PURE__*/new Vector3();
const _normalB = /*@__PURE__*/new Vector3();
const _normalC = /*@__PURE__*/new Vector3();
const _intersectionPoint = /*@__PURE__*/new Vector3();
const _intersectionPointWorld = /*@__PURE__*/new Vector3();
class Mesh extends Object3D {
  constructor(geometry = new BufferGeometry(), material = new MeshBasicMaterial()) {
    super();
    this.isMesh = true;
    this.type = 'Mesh';
    this.geometry = geometry;
    this.material = material;
    this.updateMorphTargets();
  }
  copy(source, recursive) {
    super.copy(source, recursive);
    if (source.morphTargetInfluences !== undefined) {
      this.morphTargetInfluences = source.morphTargetInfluences.slice();
    }
    if (source.morphTargetDictionary !== undefined) {
      this.morphTargetDictionary = Object.assign({}, source.morphTargetDictionary);
    }
    this.material = Array.isArray(source.material) ? source.material.slice() : source.material;
    this.geometry = source.geometry;
    return this;
  }
  updateMorphTargets() {
    const geometry = this.geometry;
    const morphAttributes = geometry.morphAttributes;
    const keys = Object.keys(morphAttributes);
    if (keys.length > 0) {
      const morphAttribute = morphAttributes[keys[0]];
      if (morphAttribute !== undefined) {
        this.morphTargetInfluences = [];
        this.morphTargetDictionary = {};
        for (let m = 0, ml = morphAttribute.length; m < ml; m++) {
          const name = morphAttribute[m].name || String(m);
          this.morphTargetInfluences.push(0);
          this.morphTargetDictionary[name] = m;
        }
      }
    }
  }
  getVertexPosition(index, target) {
    const geometry = this.geometry;
    const position = geometry.attributes.position;
    const morphPosition = geometry.morphAttributes.position;
    const morphTargetsRelative = geometry.morphTargetsRelative;
    target.fromBufferAttribute(position, index);
    const morphInfluences = this.morphTargetInfluences;
    if (morphPosition && morphInfluences) {
      _morphA.set(0, 0, 0);
      for (let i = 0, il = morphPosition.length; i < il; i++) {
        const influence = morphInfluences[i];
        const morphAttribute = morphPosition[i];
        if (influence === 0) continue;
        _tempA.fromBufferAttribute(morphAttribute, index);
        if (morphTargetsRelative) {
          _morphA.addScaledVector(_tempA, influence);
        } else {
          _morphA.addScaledVector(_tempA.sub(target), influence);
        }
      }
      target.add(_morphA);
    }
    return target;
  }
  raycast(raycaster, intersects) {
    const geometry = this.geometry;
    const material = this.material;
    const matrixWorld = this.matrixWorld;
    if (material === undefined) return;

    // test with bounding sphere in world space

    if (geometry.boundingSphere === null) geometry.computeBoundingSphere();
    _sphere.copy(geometry.boundingSphere);
    _sphere.applyMatrix4(matrixWorld);

    // check distance from ray origin to bounding sphere

    _ray.copy(raycaster.ray).recast(raycaster.near);
    if (_sphere.containsPoint(_ray.origin) === false) {
      if (_ray.intersectSphere(_sphere, _sphereHitAt) === null) return;
      if (_ray.origin.distanceToSquared(_sphereHitAt) > (raycaster.far - raycaster.near) ** 2) return;
    }

    // convert ray to local space of mesh

    _inverseMatrix.copy(matrixWorld).invert();
    _ray.copy(raycaster.ray).applyMatrix4(_inverseMatrix);

    // test with bounding box in local space

    if (geometry.boundingBox !== null) {
      if (_ray.intersectsBox(geometry.boundingBox) === false) return;
    }

    // test for intersections with geometry

    this._computeIntersections(raycaster, intersects, _ray);
  }
  _computeIntersections(raycaster, intersects, rayLocalSpace) {
    let intersection;
    const geometry = this.geometry;
    const material = this.material;
    const index = geometry.index;
    const position = geometry.attributes.position;
    const uv = geometry.attributes.uv;
    const uv1 = geometry.attributes.uv1;
    const normal = geometry.attributes.normal;
    const groups = geometry.groups;
    const drawRange = geometry.drawRange;
    if (index !== null) {
      // indexed buffer geometry

      if (Array.isArray(material)) {
        for (let i = 0, il = groups.length; i < il; i++) {
          const group = groups[i];
          const groupMaterial = material[group.materialIndex];
          const start = Math.max(group.start, drawRange.start);
          const end = Math.min(index.count, Math.min(group.start + group.count, drawRange.start + drawRange.count));
          for (let j = start, jl = end; j < jl; j += 3) {
            const a = index.getX(j);
            const b = index.getX(j + 1);
            const c = index.getX(j + 2);
            intersection = checkGeometryIntersection(this, groupMaterial, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c);
            if (intersection) {
              intersection.faceIndex = Math.floor(j / 3); // triangle number in indexed buffer semantics
              intersection.face.materialIndex = group.materialIndex;
              intersects.push(intersection);
            }
          }
        }
      } else {
        const start = Math.max(0, drawRange.start);
        const end = Math.min(index.count, drawRange.start + drawRange.count);
        for (let i = start, il = end; i < il; i += 3) {
          const a = index.getX(i);
          const b = index.getX(i + 1);
          const c = index.getX(i + 2);
          intersection = checkGeometryIntersection(this, material, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c);
          if (intersection) {
            intersection.faceIndex = Math.floor(i / 3); // triangle number in indexed buffer semantics
            intersects.push(intersection);
          }
        }
      }
    } else if (position !== undefined) {
      // non-indexed buffer geometry

      if (Array.isArray(material)) {
        for (let i = 0, il = groups.length; i < il; i++) {
          const group = groups[i];
          const groupMaterial = material[group.materialIndex];
          const start = Math.max(group.start, drawRange.start);
          const end = Math.min(position.count, Math.min(group.start + group.count, drawRange.start + drawRange.count));
          for (let j = start, jl = end; j < jl; j += 3) {
            const a = j;
            const b = j + 1;
            const c = j + 2;
            intersection = checkGeometryIntersection(this, groupMaterial, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c);
            if (intersection) {
              intersection.faceIndex = Math.floor(j / 3); // triangle number in non-indexed buffer semantics
              intersection.face.materialIndex = group.materialIndex;
              intersects.push(intersection);
            }
          }
        }
      } else {
        const start = Math.max(0, drawRange.start);
        const end = Math.min(position.count, drawRange.start + drawRange.count);
        for (let i = start, il = end; i < il; i += 3) {
          const a = i;
          const b = i + 1;
          const c = i + 2;
          intersection = checkGeometryIntersection(this, material, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c);
          if (intersection) {
            intersection.faceIndex = Math.floor(i / 3); // triangle number in non-indexed buffer semantics
            intersects.push(intersection);
          }
        }
      }
    }
  }
}
function checkIntersection(object, material, raycaster, ray, pA, pB, pC, point) {
  let intersect;
  if (material.side === BackSide) {
    intersect = ray.intersectTriangle(pC, pB, pA, true, point);
  } else {
    intersect = ray.intersectTriangle(pA, pB, pC, material.side === FrontSide, point);
  }
  if (intersect === null) return null;
  _intersectionPointWorld.copy(point);
  _intersectionPointWorld.applyMatrix4(object.matrixWorld);
  const distance = raycaster.ray.origin.distanceTo(_intersectionPointWorld);
  if (distance < raycaster.near || distance > raycaster.far) return null;
  return {
    distance: distance,
    point: _intersectionPointWorld.clone(),
    object: object
  };
}
function checkGeometryIntersection(object, material, raycaster, ray, uv, uv1, normal, a, b, c) {
  object.getVertexPosition(a, _vA);
  object.getVertexPosition(b, _vB);
  object.getVertexPosition(c, _vC);
  const intersection = checkIntersection(object, material, raycaster, ray, _vA, _vB, _vC, _intersectionPoint);
  if (intersection) {
    if (uv) {
      _uvA.fromBufferAttribute(uv, a);
      _uvB.fromBufferAttribute(uv, b);
      _uvC.fromBufferAttribute(uv, c);
      intersection.uv = Triangle.getInterpolation(_intersectionPoint, _vA, _vB, _vC, _uvA, _uvB, _uvC, new Vector2());
    }
    if (uv1) {
      _uvA.fromBufferAttribute(uv1, a);
      _uvB.fromBufferAttribute(uv1, b);
      _uvC.fromBufferAttribute(uv1, c);
      intersection.uv1 = Triangle.getInterpolation(_intersectionPoint, _vA, _vB, _vC, _uvA, _uvB, _uvC, new Vector2());
    }
    if (normal) {
      _normalA.fromBufferAttribute(normal, a);
      _normalB.fromBufferAttribute(normal, b);
      _normalC.fromBufferAttribute(normal, c);
      intersection.normal = Triangle.getInterpolation(_intersectionPoint, _vA, _vB, _vC, _normalA, _normalB, _normalC, new Vector3());
      if (intersection.normal.dot(ray.direction) > 0) {
        intersection.normal.multiplyScalar(-1);
      }
    }
    const face = {
      a: a,
      b: b,
      c: c,
      normal: new Vector3(),
      materialIndex: 0
    };
    Triangle.getNormal(_vA, _vB, _vC, face.normal);
    intersection.face = face;
  }
  return intersection;
}

class Camera extends Object3D {
  constructor() {
    super();
    this.isCamera = true;
    this.type = 'Camera';
    this.matrixWorldInverse = new Matrix4();
    this.projectionMatrix = new Matrix4();
    this.projectionMatrixInverse = new Matrix4();
    this.coordinateSystem = WebGLCoordinateSystem;
  }
  copy(source, recursive) {
    super.copy(source, recursive);
    this.matrixWorldInverse.copy(source.matrixWorldInverse);
    this.projectionMatrix.copy(source.projectionMatrix);
    this.projectionMatrixInverse.copy(source.projectionMatrixInverse);
    this.coordinateSystem = source.coordinateSystem;
    return this;
  }
  getWorldDirection(target) {
    return super.getWorldDirection(target).negate();
  }
  updateMatrixWorld(force) {
    super.updateMatrixWorld(force);
    this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  updateWorldMatrix(updateParents, updateChildren) {
    super.updateWorldMatrix(updateParents, updateChildren);
    this.matrixWorldInverse.copy(this.matrixWorld).invert();
  }
  clone() {
    return new this.constructor().copy(this);
  }
}

class OrthographicCamera extends Camera {
  constructor(left = -1, right = 1, top = 1, bottom = -1, near = 0.1, far = 2000) {
    super();
    this.isOrthographicCamera = true;
    this.type = 'OrthographicCamera';
    this.zoom = 1;
    this.view = null;
    this.left = left;
    this.right = right;
    this.top = top;
    this.bottom = bottom;
    this.near = near;
    this.far = far;
    this.updateProjectionMatrix();
  }
  copy(source, recursive) {
    super.copy(source, recursive);
    this.left = source.left;
    this.right = source.right;
    this.top = source.top;
    this.bottom = source.bottom;
    this.near = source.near;
    this.far = source.far;
    this.zoom = source.zoom;
    this.view = source.view === null ? null : Object.assign({}, source.view);
    return this;
  }
  setViewOffset(fullWidth, fullHeight, x, y, width, height) {
    if (this.view === null) {
      this.view = {
        enabled: true,
        fullWidth: 1,
        fullHeight: 1,
        offsetX: 0,
        offsetY: 0,
        width: 1,
        height: 1
      };
    }
    this.view.enabled = true;
    this.view.fullWidth = fullWidth;
    this.view.fullHeight = fullHeight;
    this.view.offsetX = x;
    this.view.offsetY = y;
    this.view.width = width;
    this.view.height = height;
    this.updateProjectionMatrix();
  }
  clearViewOffset() {
    if (this.view !== null) {
      this.view.enabled = false;
    }
    this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const dx = (this.right - this.left) / (2 * this.zoom);
    const dy = (this.top - this.bottom) / (2 * this.zoom);
    const cx = (this.right + this.left) / 2;
    const cy = (this.top + this.bottom) / 2;
    let left = cx - dx;
    let right = cx + dx;
    let top = cy + dy;
    let bottom = cy - dy;
    if (this.view !== null && this.view.enabled) {
      const scaleW = (this.right - this.left) / this.view.fullWidth / this.zoom;
      const scaleH = (this.top - this.bottom) / this.view.fullHeight / this.zoom;
      left += scaleW * this.view.offsetX;
      right = left + scaleW * this.view.width;
      top -= scaleH * this.view.offsetY;
      bottom = top - scaleH * this.view.height;
    }
    this.projectionMatrix.makeOrthographic(left, right, top, bottom, this.near, this.far, this.coordinateSystem);
    this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(meta) {
    const data = super.toJSON(meta);
    data.object.zoom = this.zoom;
    data.object.left = this.left;
    data.object.right = this.right;
    data.object.top = this.top;
    data.object.bottom = this.bottom;
    data.object.near = this.near;
    data.object.far = this.far;
    if (this.view !== null) data.object.view = Object.assign({}, this.view);
    return data;
  }
}

// Helper for passes that need to fill the viewport with a single quad.

const _camera = /*@__PURE__*/new OrthographicCamera(-1, 1, 1, -1, 0, 1);

// https://github.com/mrdoob/three.js/pull/21358

class QuadGeometry extends BufferGeometry {
  constructor(flipY = false) {
    super();
    const uv = flipY === false ? [0, -1, 0, 1, 2, 1] : [0, 2, 0, 0, 2, 0];
    this.setAttribute('position', new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
    this.setAttribute('uv', new Float32BufferAttribute(uv, 2));
  }
}
const _geometry = /*@__PURE__*/new QuadGeometry();
class QuadMesh extends Mesh {
  constructor(material = null) {
    super(_geometry, material);
    this.camera = _camera;
    this.isQuadMesh = true;
  }
  renderAsync(renderer) {
    return renderer.renderAsync(this, _camera);
  }
  render(renderer) {
    renderer.render(this, _camera);
  }
}
var QuadMesh$1 = QuadMesh;

const _size$5 = /*@__PURE__*/new Vector2();
class RTTNode extends TextureNode {
  constructor(node, width = null, height = null, options = {
    type: HalfFloatType
  }) {
    const renderTarget = new RenderTarget(width, height, options);
    super(renderTarget.texture, uv());
    this.node = node;
    this.width = width;
    this.height = height;
    this.renderTarget = renderTarget;
    this.textureNeedsUpdate = true;
    this.autoUpdate = true;
    this.updateMap = new WeakMap();
    this._rttNode = null;
    this._quadMesh = new QuadMesh$1(new NodeMaterial$1());
    this.updateBeforeType = NodeUpdateType.RENDER;
  }
  get autoSize() {
    return this.width === null;
  }
  setup(builder) {
    this._rttNode = this.node.context(builder.getSharedContext());
    this._quadMesh.material.name = 'RTT';
    this._quadMesh.material.needsUpdate = true;
    return super.setup(builder);
  }
  setSize(width, height) {
    this.width = width;
    this.height = height;
    const effectiveWidth = width * this.pixelRatio;
    const effectiveHeight = height * this.pixelRatio;
    this.renderTarget.setSize(effectiveWidth, effectiveHeight);
    this.textureNeedsUpdate = true;
  }
  setPixelRatio(pixelRatio) {
    this.pixelRatio = pixelRatio;
    this.setSize(this.width, this.height);
  }
  updateBefore({
    renderer
  }) {
    if (this.textureNeedsUpdate === false && this.autoUpdate === false) return;
    this.textureNeedsUpdate = false;

    //

    if (this.autoSize === true) {
      this.pixelRatio = renderer.getPixelRatio();
      const size = renderer.getSize(_size$5);
      this.setSize(size.width, size.height);
    }

    //

    this._quadMesh.material.fragmentNode = this._rttNode;

    //

    const currentRenderTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(this.renderTarget);
    this._quadMesh.render(renderer);
    renderer.setRenderTarget(currentRenderTarget);
  }
  clone() {
    const newNode = new TextureNode(this.value, this.uvNode, this.levelNode);
    newNode.sampler = this.sampler;
    newNode.referenceNode = this;
    return newNode;
  }
}
RTTNode.type = /*@__PURE__*/registerNode('RTT', RTTNode);
const rtt = (node, ...params) => nodeObject(new RTTNode(nodeObject(node), ...params));
const convertToTexture = (node, ...params) => node.isTextureNode ? node : rtt(node, ...params);

class VertexColorNode extends AttributeNode {
  constructor(index = 0) {
    super(null, 'vec4');
    this.isVertexColorNode = true;
    this.index = index;
  }
  getAttributeName( /*builder*/
  ) {
    const index = this.index;
    return 'color' + (index > 0 ? index : '');
  }
  generate(builder) {
    const attributeName = this.getAttributeName(builder);
    const geometryAttribute = builder.hasGeometryAttribute(attributeName);
    let result;
    if (geometryAttribute === true) {
      result = super.generate(builder);
    } else {
      // Vertex color fallback should be white
      result = builder.generateConst(this.nodeType, new Vector4(1, 1, 1, 1));
    }
    return result;
  }
  serialize(data) {
    super.serialize(data);
    data.index = this.index;
  }
  deserialize(data) {
    super.deserialize(data);
    this.index = data.index;
  }
}
VertexColorNode.type = /*@__PURE__*/registerNode('VertexColor', VertexColorNode);

class PointUVNode extends Node$1 {
  constructor() {
    super('vec2');
    this.isPointUVNode = true;
  }
  generate( /*builder*/
  ) {
    return 'vec2( gl_PointCoord.x, 1.0 - gl_PointCoord.y )';
  }
}
PointUVNode.type = /*@__PURE__*/registerNode('PointUV', PointUVNode);

class SceneNode extends Node$1 {
  constructor(scope = SceneNode.BACKGROUND_BLURRINESS, scene = null) {
    super();
    this.scope = scope;
    this.scene = scene;
  }
  setup(builder) {
    const scope = this.scope;
    const scene = this.scene !== null ? this.scene : builder.scene;
    let output;
    if (scope === SceneNode.BACKGROUND_BLURRINESS) {
      output = reference('backgroundBlurriness', 'float', scene);
    } else if (scope === SceneNode.BACKGROUND_INTENSITY) {
      output = reference('backgroundIntensity', 'float', scene);
    } else {
      console.error('THREE.SceneNode: Unknown scope:', scope);
    }
    return output;
  }
}
SceneNode.BACKGROUND_BLURRINESS = 'backgroundBlurriness';
SceneNode.BACKGROUND_INTENSITY = 'backgroundIntensity';
SceneNode.type = /*@__PURE__*/registerNode('Scene', SceneNode);
const backgroundBlurriness = /*@__PURE__*/nodeImmutable(SceneNode, SceneNode.BACKGROUND_BLURRINESS);
const backgroundIntensity = /*@__PURE__*/nodeImmutable(SceneNode, SceneNode.BACKGROUND_INTENSITY);

class StorageArrayElementNode extends ArrayElementNode {
  constructor(storageBufferNode, indexNode) {
    super(storageBufferNode, indexNode);
    this.isStorageArrayElementNode = true;
  }
  set storageBufferNode(value) {
    this.node = value;
  }
  get storageBufferNode() {
    return this.node;
  }
  setup(builder) {
    if (builder.isAvailable('storageBuffer') === false) {
      if (!this.node.instanceIndex && this.node.bufferObject === true) {
        builder.setupPBO(this.node);
      }
    }
    return super.setup(builder);
  }
  generate(builder, output) {
    let snippet;
    const isAssignContext = builder.context.assign;

    //

    if (builder.isAvailable('storageBuffer') === false) {
      const {
        node
      } = this;
      if (!node.instanceIndex && this.node.bufferObject === true && isAssignContext !== true) {
        snippet = builder.generatePBO(this);
      } else {
        snippet = node.build(builder);
      }
    } else {
      snippet = super.generate(builder);
    }
    if (isAssignContext !== true) {
      const type = this.getNodeType(builder);
      snippet = builder.format(snippet, type, output);
    }
    return snippet;
  }
}
StorageArrayElementNode.type = /*@__PURE__*/registerNode('StorageArrayElement', StorageArrayElementNode);
const storageElement = /*@__PURE__*/nodeProxy(StorageArrayElementNode);

const GPUBufferBindingType = {
  Uniform: 'uniform',
  Storage: 'storage',
  ReadOnlyStorage: 'read-only-storage'
};
const GPUStorageTextureAccess = {
  WriteOnly: 'write-only',
  ReadOnly: 'read-only',
  ReadWrite: 'read-write'
};

class StorageBufferNode extends BufferNode {
  constructor(value, bufferType, bufferCount = 0) {
    super(value, bufferType, bufferCount);
    this.isStorageBufferNode = true;
    this.access = GPUBufferBindingType.Storage;
    this.bufferObject = false;
    this.bufferCount = bufferCount;
    this._attribute = null;
    this._varying = null;
    this.global = true;
    if (value.isStorageBufferAttribute !== true && value.isStorageInstancedBufferAttribute !== true) {
      // TOOD: Improve it, possibly adding a new property to the BufferAttribute to identify it as a storage buffer read-only attribute in Renderer

      if (value.isInstancedBufferAttribute) value.isStorageInstancedBufferAttribute = true;else value.isStorageBufferAttribute = true;
    }
  }
  getHash(builder) {
    if (this.bufferCount === 0) {
      let bufferData = builder.globalCache.getData(this.value);
      if (bufferData === undefined) {
        bufferData = {
          node: this
        };
        builder.globalCache.setData(this.value, bufferData);
      }
      return bufferData.node.uuid;
    }
    return this.uuid;
  }
  getInputType( /*builder*/
  ) {
    return 'storageBuffer';
  }
  element(indexNode) {
    return storageElement(this, indexNode);
  }
  setBufferObject(value) {
    this.bufferObject = value;
    return this;
  }
  setAccess(value) {
    this.access = value;
    return this;
  }
  toReadOnly() {
    return this.setAccess(GPUBufferBindingType.ReadOnlyStorage);
  }
  generate(builder) {
    if (builder.isAvailable('storageBuffer')) {
      return super.generate(builder);
    }
    const nodeType = this.getNodeType(builder);
    if (this._attribute === null) {
      this._attribute = bufferAttribute(this.value);
      this._varying = varying(this._attribute);
    }
    const output = this._varying.build(builder, nodeType);
    builder.registerTransform(output, this._attribute);
    return output;
  }
}
StorageBufferNode.type = /*@__PURE__*/registerNode('StorageBuffer', StorageBufferNode);

class StorageTextureNode extends TextureNode {
  constructor(value, uvNode, storeNode = null) {
    super(value, uvNode);
    this.storeNode = storeNode;
    this.isStorageTextureNode = true;
    this.access = GPUStorageTextureAccess.WriteOnly;
  }
  getInputType( /*builder*/
  ) {
    return 'storageTexture';
  }
  setup(builder) {
    super.setup(builder);
    const properties = builder.getNodeProperties(this);
    properties.storeNode = this.storeNode;
  }
  setAccess(value) {
    this.access = value;
    return this;
  }
  generate(builder, output) {
    let snippet;
    if (this.storeNode !== null) {
      snippet = this.generateStore(builder);
    } else {
      snippet = super.generate(builder, output);
    }
    return snippet;
  }
  toReadOnly() {
    return this.setAccess(GPUStorageTextureAccess.ReadOnly);
  }
  toWriteOnly() {
    return this.setAccess(GPUStorageTextureAccess.WriteOnly);
  }
  generateStore(builder) {
    const properties = builder.getNodeProperties(this);
    const {
      uvNode,
      storeNode
    } = properties;
    const textureProperty = super.generate(builder, 'property');
    const uvSnippet = uvNode.build(builder, 'uvec2');
    const storeSnippet = storeNode.build(builder, 'vec4');
    const snippet = builder.generateTextureStore(builder, textureProperty, uvSnippet, storeSnippet);
    builder.addLineFlowCode(snippet);
  }
}
StorageTextureNode.type = /*@__PURE__*/registerNode('StorageTexture', StorageTextureNode);

const normal = Fn(({
  texture,
  uv
}) => {
  const epsilon = 0.0001;
  const ret = vec3().temp();
  If(uv.x.lessThan(epsilon), () => {
    ret.assign(vec3(1, 0, 0));
  }).ElseIf(uv.y.lessThan(epsilon), () => {
    ret.assign(vec3(0, 1, 0));
  }).ElseIf(uv.z.lessThan(epsilon), () => {
    ret.assign(vec3(0, 0, 1));
  }).ElseIf(uv.x.greaterThan(1 - epsilon), () => {
    ret.assign(vec3(-1, 0, 0));
  }).ElseIf(uv.y.greaterThan(1 - epsilon), () => {
    ret.assign(vec3(0, -1, 0));
  }).ElseIf(uv.z.greaterThan(1 - epsilon), () => {
    ret.assign(vec3(0, 0, -1));
  }).Else(() => {
    const step = 0.01;
    const x = texture.uv(uv.add(vec3(-step, 0.0, 0.0))).r.sub(texture.uv(uv.add(vec3(step, 0.0, 0.0))).r);
    const y = texture.uv(uv.add(vec3(0.0, -step, 0.0))).r.sub(texture.uv(uv.add(vec3(0.0, step, 0.0))).r);
    const z = texture.uv(uv.add(vec3(0.0, 0.0, -step))).r.sub(texture.uv(uv.add(vec3(0.0, 0.0, step))).r);
    ret.assign(vec3(x, y, z));
  });
  return ret.normalize();
});
class Texture3DNode extends TextureNode {
  constructor(value, uvNode = null, levelNode = null) {
    super(value, uvNode, levelNode);
    this.isTexture3DNode = true;
  }
  getInputType( /*builder*/
  ) {
    return 'texture3D';
  }
  getDefaultUV() {
    return vec3(0.5, 0.5, 0.5);
  }
  setUpdateMatrix( /*updateMatrix*/) {} // Ignore .updateMatrix for 3d TextureNode

  setupUV(builder, uvNode) {
    return uvNode;
  }
  generateUV(builder, uvNode) {
    return uvNode.build(builder, 'vec3');
  }
  normal(uvNode) {
    return normal({
      texture: this,
      uv: uvNode
    });
  }
}
Texture3DNode.type = /*@__PURE__*/registerNode('Texture3D', Texture3DNode);

class UserDataNode extends ReferenceNode {
  constructor(property, inputType, userData = null) {
    super(property, inputType, userData);
    this.userData = userData;
  }
  update(frame) {
    this.reference = this.userData !== null ? this.userData : frame.object.userData;
    super.update(frame);
  }
}
UserDataNode.type = /*@__PURE__*/registerNode('UserData', UserDataNode);

const _matrixCache = new WeakMap();
class VelocityNode extends TempNode {
  constructor() {
    super('vec2');
    this.updateType = NodeUpdateType.OBJECT;
    this.updateAfterType = NodeUpdateType.OBJECT;
    this.previousProjectionMatrix = uniform(new Matrix4());
    this.previousModelViewMatrix = uniform(new Matrix4());
  }
  update({
    camera,
    object
  }) {
    const previousModelMatrix = getPreviousMatrix(object);
    const previousCameraMatrix = getPreviousMatrix(camera);
    this.previousModelViewMatrix.value.copy(previousModelMatrix);
    this.previousProjectionMatrix.value.copy(previousCameraMatrix);
  }
  updateAfter({
    camera,
    object
  }) {
    const previousModelMatrix = getPreviousMatrix(object);
    const previousCameraMatrix = getPreviousMatrix(camera);
    previousModelMatrix.copy(object.modelViewMatrix);
    previousCameraMatrix.copy(camera.projectionMatrix);
  }
  setup( /*builder*/
  ) {
    const clipPositionCurrent = cameraProjectionMatrix.mul(modelViewMatrix).mul(positionLocal);
    const clipPositionPrevious = this.previousProjectionMatrix.mul(this.previousModelViewMatrix).mul(positionPrevious);
    const ndcPositionCurrent = clipPositionCurrent.xy.div(clipPositionCurrent.w);
    const ndcPositionPrevious = clipPositionPrevious.xy.div(clipPositionPrevious.w);
    const velocity = sub(ndcPositionCurrent, ndcPositionPrevious);
    return velocity;
  }
}
function getPreviousMatrix(object) {
  let previousMatrix = _matrixCache.get(object);
  if (previousMatrix === undefined) {
    previousMatrix = new Matrix4();
    _matrixCache.set(object, previousMatrix);
  }
  return previousMatrix;
}
VelocityNode.type = /*@__PURE__*/registerNode('Velocity', VelocityNode);

const _luminanceCoefficients = /*@__PURE__*/new Vector3();
const luminance = (color, luminanceCoefficients = vec3(...ColorManagement.getLuminanceCoefficients(_luminanceCoefficients))) => dot(color, luminanceCoefficients);

class PosterizeNode extends TempNode {
  constructor(sourceNode, stepsNode) {
    super();
    this.sourceNode = sourceNode;
    this.stepsNode = stepsNode;
  }
  setup() {
    const {
      sourceNode,
      stepsNode
    } = this;
    return sourceNode.mul(stepsNode).floor().div(stepsNode);
  }
}
PosterizeNode.type = /*@__PURE__*/registerNode('Posterize', PosterizeNode);

let _sharedFramebuffer = null;
class ViewportSharedTextureNode extends ViewportTextureNode {
  constructor(uvNode = viewportUV, levelNode = null) {
    if (_sharedFramebuffer === null) {
      _sharedFramebuffer = new FramebufferTexture();
    }
    super(uvNode, levelNode, _sharedFramebuffer);
  }
  updateReference() {
    return this;
  }
}
ViewportSharedTextureNode.type = /*@__PURE__*/registerNode('ViewportSharedTexture', ViewportSharedTextureNode);

const _size$4 = /*@__PURE__*/new Vector2();
class PassTextureNode extends TextureNode {
  constructor(passNode, texture) {
    super(texture);
    this.passNode = passNode;
    this.setUpdateMatrix(false);
  }
  setup(builder) {
    if (builder.object.isQuadMesh) this.passNode.build(builder);
    return super.setup(builder);
  }
  clone() {
    return new this.constructor(this.passNode, this.value);
  }
}
PassTextureNode.type = /*@__PURE__*/registerNode('PassTexture', PassTextureNode);
class PassMultipleTextureNode extends PassTextureNode {
  constructor(passNode, textureName, previousTexture = false) {
    super(passNode, null);
    this.textureName = textureName;
    this.previousTexture = previousTexture;
  }
  updateTexture() {
    this.value = this.previousTexture ? this.passNode.getPreviousTexture(this.textureName) : this.passNode.getTexture(this.textureName);
  }
  setup(builder) {
    this.updateTexture();
    return super.setup(builder);
  }
  clone() {
    return new this.constructor(this.passNode, this.textureName, this.previousTexture);
  }
}
PassMultipleTextureNode.type = /*@__PURE__*/registerNode('PassMultipleTexture', PassMultipleTextureNode);
class PassNode extends TempNode {
  constructor(scope, scene, camera, options = {}) {
    super('vec4');
    this.scope = scope;
    this.scene = scene;
    this.camera = camera;
    this.options = options;
    this._pixelRatio = 1;
    this._width = 1;
    this._height = 1;
    const depthTexture = new DepthTexture();
    depthTexture.isRenderTargetTexture = true;
    //depthTexture.type = FloatType;
    depthTexture.name = 'depth';
    const renderTarget = new RenderTarget(this._width * this._pixelRatio, this._height * this._pixelRatio, {
      type: HalfFloatType,
      ...options
    });
    renderTarget.texture.name = 'output';
    renderTarget.depthTexture = depthTexture;
    this.renderTarget = renderTarget;
    this.updateBeforeType = NodeUpdateType.FRAME;
    this._textures = {
      output: renderTarget.texture,
      depth: depthTexture
    };
    this._textureNodes = {};
    this._linearDepthNodes = {};
    this._viewZNodes = {};
    this._previousTextures = {};
    this._previousTextureNodes = {};
    this._cameraNear = uniform(0);
    this._cameraFar = uniform(0);
    this._mrt = null;
    this.isPassNode = true;
  }
  setMRT(mrt) {
    this._mrt = mrt;
    return this;
  }
  getMRT() {
    return this._mrt;
  }
  isGlobal() {
    return true;
  }
  getTexture(name) {
    let texture = this._textures[name];
    if (texture === undefined) {
      const refTexture = this.renderTarget.texture;
      texture = refTexture.clone();
      texture.isRenderTargetTexture = true;
      texture.name = name;
      this._textures[name] = texture;
      this.renderTarget.textures.push(texture);
    }
    return texture;
  }
  getPreviousTexture(name) {
    let texture = this._previousTextures[name];
    if (texture === undefined) {
      texture = this.getTexture(name).clone();
      texture.isRenderTargetTexture = true;
      this._previousTextures[name] = texture;
    }
    return texture;
  }
  toggleTexture(name) {
    const prevTexture = this._previousTextures[name];
    if (prevTexture !== undefined) {
      const texture = this._textures[name];
      const index = this.renderTarget.textures.indexOf(texture);
      this.renderTarget.textures[index] = prevTexture;
      this._textures[name] = prevTexture;
      this._previousTextures[name] = texture;
      this._textureNodes[name].updateTexture();
      this._previousTextureNodes[name].updateTexture();
    }
  }
  getTextureNode(name = 'output') {
    let textureNode = this._textureNodes[name];
    if (textureNode === undefined) {
      this._textureNodes[name] = textureNode = nodeObject(new PassMultipleTextureNode(this, name));
      this._textureNodes[name].updateTexture();
    }
    return textureNode;
  }
  getPreviousTextureNode(name = 'output') {
    let textureNode = this._previousTextureNodes[name];
    if (textureNode === undefined) {
      if (this._textureNodes[name] === undefined) this.getTextureNode(name);
      this._previousTextureNodes[name] = textureNode = nodeObject(new PassMultipleTextureNode(this, name, true));
      this._previousTextureNodes[name].updateTexture();
    }
    return textureNode;
  }
  getViewZNode(name = 'depth') {
    let viewZNode = this._viewZNodes[name];
    if (viewZNode === undefined) {
      const cameraNear = this._cameraNear;
      const cameraFar = this._cameraFar;
      this._viewZNodes[name] = viewZNode = perspectiveDepthToViewZ(this.getTextureNode(name), cameraNear, cameraFar);
    }
    return viewZNode;
  }
  getLinearDepthNode(name = 'depth') {
    let linearDepthNode = this._linearDepthNodes[name];
    if (linearDepthNode === undefined) {
      const cameraNear = this._cameraNear;
      const cameraFar = this._cameraFar;
      const viewZNode = this.getViewZNode(name);

      // TODO: just if ( builder.camera.isPerspectiveCamera )

      this._linearDepthNodes[name] = linearDepthNode = viewZToOrthographicDepth(viewZNode, cameraNear, cameraFar);
    }
    return linearDepthNode;
  }
  setup({
    renderer
  }) {
    this.renderTarget.samples = this.options.samples === undefined ? renderer.samples : this.options.samples;

    // Disable MSAA for WebGL backend for now
    if (renderer.backend.isWebGLBackend === true) {
      this.renderTarget.samples = 0;
    }
    this.renderTarget.depthTexture.isMultisampleRenderTargetTexture = this.renderTarget.samples > 1;
    return this.scope === PassNode.COLOR ? this.getTextureNode() : this.getLinearDepthNode();
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const {
      scene,
      camera
    } = this;
    this._pixelRatio = renderer.getPixelRatio();
    const size = renderer.getSize(_size$4);
    this.setSize(size.width, size.height);
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    this._cameraNear.value = camera.near;
    this._cameraFar.value = camera.far;
    for (const name in this._previousTextures) {
      this.toggleTexture(name);
    }
    renderer.setRenderTarget(this.renderTarget);
    renderer.setMRT(this._mrt);
    renderer.render(scene, camera);
    renderer.setRenderTarget(currentRenderTarget);
    renderer.setMRT(currentMRT);
  }
  setSize(width, height) {
    this._width = width;
    this._height = height;
    const effectiveWidth = this._width * this._pixelRatio;
    const effectiveHeight = this._height * this._pixelRatio;
    this.renderTarget.setSize(effectiveWidth, effectiveHeight);
  }
  setPixelRatio(pixelRatio) {
    this._pixelRatio = pixelRatio;
    this.setSize(this._width, this._height);
  }
  dispose() {
    this.renderTarget.dispose();
  }
}
PassNode.COLOR = 'color';
PassNode.DEPTH = 'depth';
PassNode.type = /*@__PURE__*/registerNode('Pass', PassNode);
const passTexture = (pass, texture) => nodeObject(new PassTextureNode(pass, texture));

// WebGPU: The use of a single QuadMesh for both gaussian blur passes results in a single RenderObject with a SampledTexture binding that
// alternates between source textures and triggers creation of new BindGroups and BindGroupLayouts every frame.

const _quadMesh1 = /*@__PURE__*/new QuadMesh$1();
const _quadMesh2 = /*@__PURE__*/new QuadMesh$1();
class GaussianBlurNode extends TempNode {
  constructor(textureNode, directionNode = null, sigma = 2) {
    super('vec4');
    this.textureNode = textureNode;
    this.directionNode = directionNode;
    this.sigma = sigma;
    this._invSize = uniform(new Vector2());
    this._passDirection = uniform(new Vector2());
    this._horizontalRT = new RenderTarget();
    this._horizontalRT.texture.name = 'GaussianBlurNode.horizontal';
    this._verticalRT = new RenderTarget();
    this._verticalRT.texture.name = 'GaussianBlurNode.vertical';
    this._textureNode = passTexture(this, this._verticalRT.texture);
    this.updateBeforeType = NodeUpdateType.RENDER;
    this.resolution = new Vector2(1, 1);
  }
  setSize(width, height) {
    width = Math.max(Math.round(width * this.resolution.x), 1);
    height = Math.max(Math.round(height * this.resolution.y), 1);
    this._invSize.value.set(1 / width, 1 / height);
    this._horizontalRT.setSize(width, height);
    this._verticalRT.setSize(width, height);
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const textureNode = this.textureNode;
    const map = textureNode.value;
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    const currentTexture = textureNode.value;
    _quadMesh1.material = this._material;
    _quadMesh2.material = this._material;
    this.setSize(map.image.width, map.image.height);
    const textureType = map.type;
    this._horizontalRT.texture.type = textureType;
    this._verticalRT.texture.type = textureType;

    // clear

    renderer.setMRT(null);

    // horizontal

    renderer.setRenderTarget(this._horizontalRT);
    this._passDirection.value.set(1, 0);
    _quadMesh1.render(renderer);

    // vertical

    textureNode.value = this._horizontalRT.texture;
    renderer.setRenderTarget(this._verticalRT);
    this._passDirection.value.set(0, 1);
    _quadMesh2.render(renderer);

    // restore

    renderer.setRenderTarget(currentRenderTarget);
    renderer.setMRT(currentMRT);
    textureNode.value = currentTexture;
  }
  getTextureNode() {
    return this._textureNode;
  }
  setup(builder) {
    const textureNode = this.textureNode;
    if (textureNode.isTextureNode !== true) {
      console.error('GaussianBlurNode requires a TextureNode.');
      return vec4();
    }

    //

    const uvNode = textureNode.uvNode || uv();
    const directionNode = vec2(this.directionNode || 1);
    const sampleTexture = uv => textureNode.uv(uv);
    const blur = Fn(() => {
      const kernelSize = 3 + 2 * this.sigma;
      const gaussianCoefficients = this._getCoefficients(kernelSize);
      const invSize = this._invSize;
      const direction = directionNode.mul(this._passDirection);
      const weightSum = float(gaussianCoefficients[0]).toVar();
      const diffuseSum = vec4(sampleTexture(uvNode).mul(weightSum)).toVar();
      for (let i = 1; i < kernelSize; i++) {
        const x = float(i);
        const w = float(gaussianCoefficients[i]);
        const uvOffset = vec2(direction.mul(invSize.mul(x))).toVar();
        const sample1 = vec4(sampleTexture(uvNode.add(uvOffset)));
        const sample2 = vec4(sampleTexture(uvNode.sub(uvOffset)));
        diffuseSum.addAssign(sample1.add(sample2).mul(w));
        weightSum.addAssign(mul(2.0, w));
      }
      return diffuseSum.div(weightSum);
    });

    //

    const material = this._material || (this._material = new NodeMaterial$1());
    material.fragmentNode = blur().context(builder.getSharedContext());
    material.name = 'Gaussian_blur';
    material.needsUpdate = true;

    //

    const properties = builder.getNodeProperties(this);
    properties.textureNode = textureNode;

    //

    return this._textureNode;
  }
  dispose() {
    this._horizontalRT.dispose();
    this._verticalRT.dispose();
  }
  _getCoefficients(kernelRadius) {
    const coefficients = [];
    for (let i = 0; i < kernelRadius; i++) {
      coefficients.push(0.39894 * Math.exp(-0.5 * i * i / (kernelRadius * kernelRadius)) / kernelRadius);
    }
    return coefficients;
  }
}
GaussianBlurNode.type = /*@__PURE__*/registerNode('GaussianBlur', GaussianBlurNode);

class SobelOperatorNode extends TempNode {
  constructor(textureNode) {
    super();
    this.textureNode = textureNode;
    this.updateBeforeType = NodeUpdateType.RENDER;
    this._invSize = uniform(new Vector2());
  }
  updateBefore() {
    const map = this.textureNode.value;
    this._invSize.value.set(1 / map.image.width, 1 / map.image.height);
  }
  setup() {
    const {
      textureNode
    } = this;
    const uvNode = textureNode.uvNode || uv();
    const sampleTexture = uv => textureNode.uv(uv);
    const sobel = Fn(() => {
      // Sobel Edge Detection (see https://youtu.be/uihBwtPIBxM)

      const texel = this._invSize;

      // kernel definition (in glsl matrices are filled in column-major order)

      const Gx = mat3(-1, -2, -1, 0, 0, 0, 1, 2, 1); // x direction kernel
      const Gy = mat3(-1, 0, 1, -2, 0, 2, -1, 0, 1); // y direction kernel

      // fetch the 3x3 neighbourhood of a fragment

      // first column

      const tx0y0 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(-1, -1)))).xyz);
      const tx0y1 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(-1, 0)))).xyz);
      const tx0y2 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(-1, 1)))).xyz);

      // second column

      const tx1y0 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(0, -1)))).xyz);
      const tx1y1 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(0, 0)))).xyz);
      const tx1y2 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(0, 1)))).xyz);

      // third column

      const tx2y0 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(1, -1)))).xyz);
      const tx2y1 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(1, 0)))).xyz);
      const tx2y2 = luminance(sampleTexture(uvNode.add(texel.mul(vec2(1, 1)))).xyz);

      // gradient value in x direction

      const valueGx = add(Gx[0][0].mul(tx0y0), Gx[1][0].mul(tx1y0), Gx[2][0].mul(tx2y0), Gx[0][1].mul(tx0y1), Gx[1][1].mul(tx1y1), Gx[2][1].mul(tx2y1), Gx[0][2].mul(tx0y2), Gx[1][2].mul(tx1y2), Gx[2][2].mul(tx2y2));

      // gradient value in y direction

      const valueGy = add(Gy[0][0].mul(tx0y0), Gy[1][0].mul(tx1y0), Gy[2][0].mul(tx2y0), Gy[0][1].mul(tx0y1), Gy[1][1].mul(tx1y1), Gy[2][1].mul(tx2y1), Gy[0][2].mul(tx0y2), Gy[1][2].mul(tx1y2), Gy[2][2].mul(tx2y2));

      // magnitute of the total gradient

      const G = valueGx.mul(valueGx).add(valueGy.mul(valueGy)).sqrt();
      return vec4(vec3(G), 1);
    });
    const outputNode = sobel();
    return outputNode;
  }
}
SobelOperatorNode.type = /*@__PURE__*/registerNode('SobelOperator', SobelOperatorNode);

class DepthOfFieldNode extends TempNode {
  constructor(textureNode, viewZNode, focusNode, apertureNode, maxblurNode) {
    super();
    this.textureNode = textureNode;
    this.viewZNode = viewZNode;
    this.focusNode = focusNode;
    this.apertureNode = apertureNode;
    this.maxblurNode = maxblurNode;
    this._aspect = uniform(0);
    this.updateBeforeType = NodeUpdateType.RENDER;
  }
  updateBefore() {
    const map = this.textureNode.value;
    this._aspect.value = map.image.width / map.image.height;
  }
  setup() {
    const textureNode = this.textureNode;
    const uvNode = textureNode.uvNode || uv();
    const sampleTexture = uv => textureNode.uv(uv);
    const dof = Fn(() => {
      const aspectcorrect = vec2(1.0, this._aspect);
      const factor = this.focusNode.add(this.viewZNode);
      const dofblur = vec2(clamp(factor.mul(this.apertureNode), this.maxblurNode.negate(), this.maxblurNode));
      const dofblur9 = dofblur.mul(0.9);
      const dofblur7 = dofblur.mul(0.7);
      const dofblur4 = dofblur.mul(0.4);
      let col = vec4(0.0);
      col = col.add(sampleTexture(uvNode));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, 0.4).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.15, 0.37).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, 0.29).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.37, 0.15).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.40, 0.0).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.37, -0.15).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, -0.29).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.15, -0.37).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, -0.4).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.15, 0.37).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, 0.29).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.37, 0.15).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.4, 0.0).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.37, -0.15).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, -0.29).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.15, -0.37).mul(aspectcorrect).mul(dofblur))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.15, 0.37).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.37, 0.15).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.37, -0.15).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.15, -0.37).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.15, 0.37).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.37, 0.15).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.37, -0.15).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.15, -0.37).mul(aspectcorrect).mul(dofblur9))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, 0.29).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.40, 0.0).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, -0.29).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, -0.4).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, 0.29).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.4, 0.0).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, -0.29).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, 0.4).mul(aspectcorrect).mul(dofblur7))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, 0.29).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.4, 0.0).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.29, -0.29).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, -0.4).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, 0.29).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.4, 0.0).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(-0.29, -0.29).mul(aspectcorrect).mul(dofblur4))));
      col = col.add(sampleTexture(uvNode.add(vec2(0.0, 0.4).mul(aspectcorrect).mul(dofblur4))));
      col = col.div(41);
      col.a = 1;
      return vec4(col);
    });
    const outputNode = dof();
    return outputNode;
  }
}
DepthOfFieldNode.type = /*@__PURE__*/registerNode('DepthOfField', DepthOfFieldNode);

class DotScreenNode extends TempNode {
  constructor(inputNode, center = new Vector2(0.5, 0.5), angle = 1.57, scale = 1) {
    super('vec4');
    this.inputNode = inputNode;
    this.center = uniform(center);
    this.angle = uniform(angle);
    this.scale = uniform(scale);
  }
  setup() {
    const inputNode = this.inputNode;
    const pattern = Fn(() => {
      const s = sin(this.angle);
      const c = cos(this.angle);
      const tex = uv().mul(viewportResolution).sub(this.center);
      const point = vec2(c.mul(tex.x).sub(s.mul(tex.y)), s.mul(tex.x).add(c.mul(tex.y))).mul(this.scale);
      return sin(point.x).mul(sin(point.y)).mul(4);
    });
    const dotScreen = Fn(() => {
      const color = inputNode;
      const average = add(color.r, color.g, color.b).div(3);
      return vec4(vec3(average.mul(10).sub(5).add(pattern())), color.a);
    });
    const outputNode = dotScreen();
    return outputNode;
  }
}
DotScreenNode.type = /*@__PURE__*/registerNode('DotScreen', DotScreenNode);

class RGBShiftNode extends TempNode {
  constructor(textureNode, amount = 0.005, angle = 0) {
    super('vec4');
    this.textureNode = textureNode;
    this.amount = uniform(amount);
    this.angle = uniform(angle);
  }
  setup() {
    const {
      textureNode
    } = this;
    const uvNode = textureNode.uvNode || uv();
    const sampleTexture = uv => textureNode.uv(uv);
    const rgbShift = Fn(() => {
      const offset = vec2(cos(this.angle), sin(this.angle)).mul(this.amount);
      const cr = sampleTexture(uvNode.add(offset));
      const cga = sampleTexture(uvNode);
      const cb = sampleTexture(uvNode.sub(offset));
      return vec4(cr.r, cga.g, cb.b, cga.a);
    });
    return rgbShift();
  }
}
RGBShiftNode.type = /*@__PURE__*/registerNode('RGBShift', RGBShiftNode);

class FilmNode extends TempNode {
  constructor(inputNode, intensityNode = null, uvNode = null) {
    super();
    this.inputNode = inputNode;
    this.intensityNode = intensityNode;
    this.uvNode = uvNode;
  }
  setup() {
    const uvNode = this.uvNode || uv();
    const film = Fn(() => {
      const base = this.inputNode.rgb;
      const noise = rand(fract(uvNode.add(timerLocal())));
      let color = base.add(base.mul(clamp(noise.add(0.1), 0, 1)));
      if (this.intensityNode !== null) {
        color = mix(base, color, this.intensityNode);
      }
      return vec4(color, this.inputNode.a);
    });
    const outputNode = film();
    return outputNode;
  }
}
FilmNode.type = /*@__PURE__*/registerNode('Film', FilmNode);

class Lut3DNode extends TempNode {
  constructor(inputNode, lutNode, size, intensityNode) {
    super();
    this.inputNode = inputNode;
    this.lutNode = lutNode;
    this.size = uniform(size);
    this.intensityNode = intensityNode;
  }
  setup() {
    const {
      inputNode,
      lutNode
    } = this;
    const sampleLut = uv => lutNode.uv(uv);
    const lut3D = Fn(() => {
      const base = inputNode;

      // pull the sample in by half a pixel so the sample begins at the center of the edge pixels.

      const pixelWidth = float(1.0).div(this.size);
      const halfPixelWidth = float(0.5).div(this.size);
      const uvw = vec3(halfPixelWidth).add(base.rgb.mul(float(1.0).sub(pixelWidth)));
      const lutValue = vec4(sampleLut(uvw).rgb, base.a);
      return vec4(mix(base, lutValue, this.intensityNode));
    });
    const outputNode = lut3D();
    return outputNode;
  }
}
Lut3DNode.type = /*@__PURE__*/registerNode('Lut3D', Lut3DNode);

class DataTexture extends Texture {
  constructor(data = null, width = 1, height = 1, format, type, mapping, wrapS, wrapT, magFilter = NearestFilter, minFilter = NearestFilter, anisotropy, colorSpace) {
    super(null, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy, colorSpace);
    this.isDataTexture = true;
    this.image = {
      data: data,
      width: width,
      height: height
    };
    this.generateMipmaps = false;
    this.flipY = false;
    this.unpackAlignment = 1;
  }
}

const _quadMesh$1 = /*@__PURE__*/new QuadMesh$1();
const _currentClearColor = /*@__PURE__*/new Color();
const _size$3 = /*@__PURE__*/new Vector2();
class GTAONode extends TempNode {
  constructor(depthNode, normalNode, camera) {
    super();
    this.depthNode = depthNode;
    this.normalNode = normalNode;
    this.radius = uniform(0.25);
    this.resolution = uniform(new Vector2());
    this.thickness = uniform(1);
    this.distanceExponent = uniform(1);
    this.distanceFallOff = uniform(1);
    this.scale = uniform(1);
    this.noiseNode = texture(generateMagicSquareNoise());
    this.cameraProjectionMatrix = uniform(camera.projectionMatrix);
    this.cameraProjectionMatrixInverse = uniform(camera.projectionMatrixInverse);
    this.SAMPLES = uniform(16);
    this._aoRenderTarget = new RenderTarget();
    this._aoRenderTarget.texture.name = 'GTAONode.AO';
    this._material = null;
    this._textureNode = passTexture(this, this._aoRenderTarget.texture);
    this.updateBeforeType = NodeUpdateType.FRAME;
  }
  getTextureNode() {
    return this._textureNode;
  }
  setSize(width, height) {
    this.resolution.value.set(width, height);
    this._aoRenderTarget.setSize(width, height);
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const size = renderer.getDrawingBufferSize(_size$3);
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    renderer.getClearColor(_currentClearColor);
    const currentClearAlpha = renderer.getClearAlpha();
    _quadMesh$1.material = this._material;
    this.setSize(size.width, size.height);

    // clear

    renderer.setMRT(null);
    renderer.setClearColor(0xffffff, 1);

    // ao

    renderer.setRenderTarget(this._aoRenderTarget);
    _quadMesh$1.render(renderer);

    // restore

    renderer.setRenderTarget(currentRenderTarget);
    renderer.setMRT(currentMRT);
    renderer.setClearColor(_currentClearColor, currentClearAlpha);
  }
  setup(builder) {
    const uvNode = uv();
    const sampleDepth = uv => this.depthNode.uv(uv).x;
    const sampleNoise = uv => this.noiseNode.uv(uv);
    const getSceneUvAndDepth = Fn(([sampleViewPos]) => {
      const sampleClipPos = this.cameraProjectionMatrix.mul(vec4(sampleViewPos, 1.0));
      let sampleUv = sampleClipPos.xy.div(sampleClipPos.w).mul(0.5).add(0.5).toVar();
      sampleUv = vec2(sampleUv.x, sampleUv.y.oneMinus());
      const sampleSceneDepth = sampleDepth(sampleUv);
      return vec3(sampleUv, sampleSceneDepth);
    });
    const getViewPosition = Fn(([screenPosition, depth]) => {
      screenPosition = vec2(screenPosition.x, screenPosition.y.oneMinus()).mul(2.0).sub(1.0);
      const clipSpacePosition = vec4(vec3(screenPosition, depth), 1.0);
      const viewSpacePosition = vec4(this.cameraProjectionMatrixInverse.mul(clipSpacePosition));
      return viewSpacePosition.xyz.div(viewSpacePosition.w);
    });
    const ao = Fn(() => {
      const depth = sampleDepth(uvNode);
      depth.greaterThanEqual(1.0).discard();
      const viewPosition = getViewPosition(uvNode, depth);
      const viewNormal = this.normalNode.rgb.normalize();
      const radiusToUse = this.radius;
      const noiseResolution = textureSize(this.noiseNode, 0);
      let noiseUv = vec2(uvNode.x, uvNode.y.oneMinus());
      noiseUv = noiseUv.mul(this.resolution.div(noiseResolution));
      const noiseTexel = sampleNoise(noiseUv);
      const randomVec = noiseTexel.xyz.mul(2.0).sub(1.0);
      const tangent = vec3(randomVec.xy, 0.0).normalize();
      const bitangent = vec3(tangent.y.mul(-1.0), tangent.x, 0.0);
      const kernelMatrix = mat3(tangent, bitangent, vec3(0.0, 0.0, 1.0));
      const DIRECTIONS = this.SAMPLES.lessThan(30).select(3, 5);
      const STEPS = add(this.SAMPLES, DIRECTIONS.sub(1)).div(DIRECTIONS);
      const ao = float(0).toVar();
      Loop({
        start: int$1(0),
        end: DIRECTIONS,
        type: 'int',
        condition: '<'
      }, ({
        i
      }) => {
        const angle = float(i).div(float(DIRECTIONS)).mul(PI);
        const sampleDir = vec4(cos(angle), sin(angle), 0., add(0.5, mul(0.5, noiseTexel.w)));
        sampleDir.xyz = normalize(kernelMatrix.mul(sampleDir.xyz));
        const viewDir = normalize(viewPosition.xyz.negate());
        const sliceBitangent = normalize(cross(sampleDir.xyz, viewDir));
        const sliceTangent = cross(sliceBitangent, viewDir);
        const normalInSlice = normalize(viewNormal.sub(sliceBitangent.mul(dot(viewNormal, sliceBitangent))));
        const tangentToNormalInSlice = cross(normalInSlice, sliceBitangent);
        const cosHorizons = vec2(dot(viewDir, tangentToNormalInSlice), dot(viewDir, tangentToNormalInSlice.negate())).toVar();
        Loop({
          end: STEPS,
          type: 'int',
          name: 'j',
          condition: '<'
        }, ({
          j
        }) => {
          const sampleViewOffset = sampleDir.xyz.mul(radiusToUse).mul(sampleDir.w).mul(pow(div(float(j).add(1.0), float(STEPS)), this.distanceExponent));

          // x

          const sampleSceneUvDepthX = getSceneUvAndDepth(viewPosition.add(sampleViewOffset));
          const sampleSceneViewPositionX = getViewPosition(sampleSceneUvDepthX.xy, sampleSceneUvDepthX.z);
          const viewDeltaX = sampleSceneViewPositionX.sub(viewPosition);
          If(abs(viewDeltaX.z).lessThan(this.thickness), () => {
            const sampleCosHorizon = dot(viewDir, normalize(viewDeltaX));
            cosHorizons.x.addAssign(max$1(0, mul(sampleCosHorizon.sub(cosHorizons.x), mix(1.0, float(2.0).div(float(j).add(2)), this.distanceFallOff))));
          });

          // y

          const sampleSceneUvDepthY = getSceneUvAndDepth(viewPosition.sub(sampleViewOffset));
          const sampleSceneViewPositionY = getViewPosition(sampleSceneUvDepthY.xy, sampleSceneUvDepthY.z);
          const viewDeltaY = sampleSceneViewPositionY.sub(viewPosition);
          If(abs(viewDeltaY.z).lessThan(this.thickness), () => {
            const sampleCosHorizon = dot(viewDir, normalize(viewDeltaY));
            cosHorizons.y.addAssign(max$1(0, mul(sampleCosHorizon.sub(cosHorizons.y), mix(1.0, float(2.0).div(float(j).add(2)), this.distanceFallOff))));
          });
        });
        const sinHorizons = sqrt(sub(1.0, cosHorizons.mul(cosHorizons)));
        const nx = dot(normalInSlice, sliceTangent);
        const ny = dot(normalInSlice, viewDir);
        const nxb = mul(0.5, acos(cosHorizons.y).sub(acos(cosHorizons.x)).add(sinHorizons.x.mul(cosHorizons.x).sub(sinHorizons.y.mul(cosHorizons.y))));
        const nyb = mul(0.5, sub(2.0, cosHorizons.x.mul(cosHorizons.x)).sub(cosHorizons.y.mul(cosHorizons.y)));
        const occlusion = nx.mul(nxb).add(ny.mul(nyb));
        ao.addAssign(occlusion);
      });
      ao.assign(clamp(ao.div(DIRECTIONS), 0, 1));
      ao.assign(pow(ao, this.scale));
      return vec4(vec3(ao), 1.0);
    });
    const material = this._material || (this._material = new NodeMaterial$1());
    material.fragmentNode = ao().context(builder.getSharedContext());
    material.name = 'GTAO';
    material.needsUpdate = true;

    //

    return this._textureNode;
  }
  dispose() {
    this._aoRenderTarget.dispose();
  }
}
GTAONode.type = /*@__PURE__*/registerNode('GTAO', GTAONode);
function generateMagicSquareNoise(size = 5) {
  const noiseSize = Math.floor(size) % 2 === 0 ? Math.floor(size) + 1 : Math.floor(size);
  const magicSquare = generateMagicSquare(noiseSize);
  const noiseSquareSize = magicSquare.length;
  const data = new Uint8Array(noiseSquareSize * 4);
  for (let inx = 0; inx < noiseSquareSize; ++inx) {
    const iAng = magicSquare[inx];
    const angle = 2 * Math.PI * iAng / noiseSquareSize;
    const randomVec = new Vector3(Math.cos(angle), Math.sin(angle), 0).normalize();
    data[inx * 4] = (randomVec.x * 0.5 + 0.5) * 255;
    data[inx * 4 + 1] = (randomVec.y * 0.5 + 0.5) * 255;
    data[inx * 4 + 2] = 127;
    data[inx * 4 + 3] = 255;
  }
  const noiseTexture = new DataTexture(data, noiseSize, noiseSize);
  noiseTexture.wrapS = RepeatWrapping;
  noiseTexture.wrapT = RepeatWrapping;
  noiseTexture.needsUpdate = true;
  return noiseTexture;
}
function generateMagicSquare(size) {
  const noiseSize = Math.floor(size) % 2 === 0 ? Math.floor(size) + 1 : Math.floor(size);
  const noiseSquareSize = noiseSize * noiseSize;
  const magicSquare = Array(noiseSquareSize).fill(0);
  let i = Math.floor(noiseSize / 2);
  let j = noiseSize - 1;
  for (let num = 1; num <= noiseSquareSize;) {
    if (i === -1 && j === noiseSize) {
      j = noiseSize - 2;
      i = 0;
    } else {
      if (j === noiseSize) {
        j = 0;
      }
      if (i < 0) {
        i = noiseSize - 1;
      }
    }
    if (magicSquare[i * noiseSize + j] !== 0) {
      j -= 2;
      i++;
      continue;
    } else {
      magicSquare[i * noiseSize + j] = num++;
    }
    j++;
    i--;
  }
  return magicSquare;
}

class DenoiseNode extends TempNode {
  constructor(textureNode, depthNode, normalNode, noiseNode, camera) {
    super();
    this.textureNode = textureNode;
    this.depthNode = depthNode;
    this.normalNode = normalNode;
    this.noiseNode = noiseNode;
    this.cameraProjectionMatrixInverse = uniform(camera.projectionMatrixInverse);
    this.lumaPhi = uniform(5);
    this.depthPhi = uniform(5);
    this.normalPhi = uniform(5);
    this.radius = uniform(5);
    this.index = uniform(0);
    this._resolution = uniform(new Vector2());
    this._sampleVectors = uniformArray(generatePdSamplePointInitializer(16, 2, 1));
    this.updateBeforeType = NodeUpdateType.RENDER;
  }
  updateBefore() {
    const map = this.textureNode.value;
    this._resolution.value.set(map.image.width, map.image.height);
  }
  setup() {
    const uvNode = uv();
    const sampleTexture = uv => this.textureNode.uv(uv);
    const sampleDepth = uv => this.depthNode.uv(uv).x;
    const sampleNormal = uv => this.normalNode.uv(uv);
    const sampleNoise = uv => this.noiseNode.uv(uv);
    const getViewPosition = Fn(([screenPosition, depth]) => {
      screenPosition = vec2(screenPosition.x, screenPosition.y.oneMinus()).mul(2.0).sub(1.0);
      const clipSpacePosition = vec4(vec3(screenPosition, depth), 1.0);
      const viewSpacePosition = vec4(this.cameraProjectionMatrixInverse.mul(clipSpacePosition));
      return viewSpacePosition.xyz.div(viewSpacePosition.w);
    });
    const denoiseSample = Fn(([center, viewNormal, viewPosition, sampleUv]) => {
      const texel = sampleTexture(sampleUv);
      const depth = sampleDepth(sampleUv);
      const normal = sampleNormal(sampleUv).rgb.normalize();
      const neighborColor = texel.rgb;
      const viewPos = getViewPosition(sampleUv, depth);
      const normalDiff = dot(viewNormal, normal).toVar();
      const normalSimilarity = pow(max$1(normalDiff, 0), this.normalPhi).toVar();
      const lumaDiff = abs(luminance(neighborColor).sub(luminance(center))).toVar();
      const lumaSimilarity = max$1(float(1.0).sub(lumaDiff.div(this.lumaPhi)), 0).toVar();
      const depthDiff = abs(dot(viewPosition.sub(viewPos), viewNormal)).toVar();
      const depthSimilarity = max$1(float(1.0).sub(depthDiff.div(this.depthPhi)), 0);
      const w = lumaSimilarity.mul(depthSimilarity).mul(normalSimilarity);
      return vec4(neighborColor.mul(w), w);
    });
    const denoise = Fn(([uvNode]) => {
      const depth = sampleDepth(uvNode);
      const viewNormal = sampleNormal(uvNode).rgb.normalize();
      const texel = sampleTexture(uvNode);
      If(depth.greaterThanEqual(1.0).or(dot(viewNormal, viewNormal).equal(0.0)), () => {
        return texel;
      });
      const center = vec3(texel.rgb);
      const viewPosition = getViewPosition(uvNode, depth);
      const noiseResolution = textureSize(this.noiseNode, 0);
      let noiseUv = vec2(uvNode.x, uvNode.y.oneMinus());
      noiseUv = noiseUv.mul(this._resolution.div(noiseResolution));
      const noiseTexel = sampleNoise(noiseUv);
      const x = sin(noiseTexel.element(this.index.mod(4).mul(2).mul(PI)));
      const y = cos(noiseTexel.element(this.index.mod(4).mul(2).mul(PI)));
      const noiseVec = vec2(x, y);
      const rotationMatrix = mat2(noiseVec.x, noiseVec.y.negate(), noiseVec.x, noiseVec.y);
      const totalWeight = float(1.0).toVar();
      const denoised = vec3(texel.rgb).toVar();
      Loop({
        start: int$1(0),
        end: int$1(16),
        type: 'int',
        condition: '<'
      }, ({
        i
      }) => {
        const sampleDir = this._sampleVectors.element(i).toVar();
        const offset = rotationMatrix.mul(sampleDir.xy.mul(float(1.0).add(sampleDir.z.mul(this.radius.sub(1))))).div(this._resolution).toVar();
        const sampleUv = uvNode.add(offset).toVar();
        const result = denoiseSample(center, viewNormal, viewPosition, sampleUv);
        denoised.addAssign(result.xyz);
        totalWeight.addAssign(result.w);
      });
      If(totalWeight.greaterThan(float(0)), () => {
        denoised.divAssign(totalWeight);
      });
      return vec4(denoised, texel.a);
    }).setLayout({
      name: 'denoise',
      type: 'vec4',
      inputs: [{
        name: 'uv',
        type: 'vec2'
      }]
    });
    const output = Fn(() => {
      return denoise(uvNode);
    });
    const outputNode = output();
    return outputNode;
  }
}
DenoiseNode.type = /*@__PURE__*/registerNode('Denoise', DenoiseNode);
function generatePdSamplePointInitializer(samples, rings, radiusExponent) {
  const poissonDisk = generateDenoiseSamples(samples, rings, radiusExponent);
  const array = [];
  for (let i = 0; i < samples; i++) {
    const sample = poissonDisk[i];
    array.push(sample);
  }
  return array;
}
function generateDenoiseSamples(numSamples, numRings, radiusExponent) {
  const samples = [];
  for (let i = 0; i < numSamples; i++) {
    const angle = 2 * Math.PI * numRings * i / numSamples;
    const radius = Math.pow(i / (numSamples - 1), radiusExponent);
    samples.push(new Vector3(Math.cos(angle), Math.sin(angle), radius));
  }
  return samples;
}

class FXAANode extends TempNode {
  constructor(textureNode) {
    super();
    this.textureNode = textureNode;
    this.updateBeforeType = NodeUpdateType.RENDER;
    this._invSize = uniform(new Vector2());
  }
  updateBefore() {
    const map = this.textureNode.value;
    this._invSize.value.set(1 / map.image.width, 1 / map.image.height);
  }
  setup() {
    const textureNode = this.textureNode.bias(-100);
    const uvNode = textureNode.uvNode || uv();

    // FXAA 3.11 implementation by NVIDIA, ported to WebGL by Agost Biro (biro@archilogic.com)

    //----------------------------------------------------------------------------------
    // File:        es3-kepler\FXAA\assets\shaders/FXAA_DefaultES.frag
    // SDK Version: v3.00
    // Email:       gameworks@nvidia.com
    // Site:        http://developer.nvidia.com/
    //
    // Copyright (c) 2014-2015, NVIDIA CORPORATION. All rights reserved.
    //
    // Redistribution and use in source and binary forms, with or without
    // modification, are permitted provided that the following conditions
    // are met:
    //  * Redistributions of source code must retain the above copyright
    //    notice, this list of conditions and the following disclaimer.
    //  * Redistributions in binary form must reproduce the above copyright
    //    notice, this list of conditions and the following disclaimer in the
    //    documentation and/or other materials provided with the distribution.
    //  * Neither the name of NVIDIA CORPORATION nor the names of its
    //    contributors may be used to endorse or promote products derived
    //    from this software without specific prior written permission.
    //
    // THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS ''AS IS'' AND ANY
    // EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
    // IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
    // PURPOSE ARE DISCLAIMED.  IN NO EVENT SHALL THE COPYRIGHT OWNER OR
    // CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
    // EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
    // PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
    // PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY
    // OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
    // (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
    // OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
    //
    //----------------------------------------------------------------------------------

    const FxaaTexTop = p => textureNode.uv(p);
    const FxaaTexOff = (p, o, r) => textureNode.uv(p.add(o.mul(r)));
    const NUM_SAMPLES = int$1(5);
    const contrast = Fn(([a_immutable, b_immutable]) => {
      // assumes colors have premultipliedAlpha, so that the calculated color contrast is scaled by alpha

      const b = vec4(b_immutable).toVar();
      const a = vec4(a_immutable).toVar();
      const diff = vec4(abs(a.sub(b))).toVar();
      return max$1(max$1(max$1(diff.r, diff.g), diff.b), diff.a);
    });

    // FXAA3 QUALITY - PC

    const FxaaPixelShader = Fn(([uv, fxaaQualityRcpFrame, fxaaQualityEdgeThreshold, fxaaQualityinvEdgeThreshold]) => {
      const rgbaM = FxaaTexTop(uv).toVar();
      const rgbaS = FxaaTexOff(uv, vec2(0.0, -1.0), fxaaQualityRcpFrame.xy).toVar();
      const rgbaE = FxaaTexOff(uv, vec2(1.0, 0.0), fxaaQualityRcpFrame.xy).toVar();
      const rgbaN = FxaaTexOff(uv, vec2(0.0, 1.0), fxaaQualityRcpFrame.xy).toVar();
      const rgbaW = FxaaTexOff(uv, vec2(-1.0, 0.0), fxaaQualityRcpFrame.xy).toVar();
      // . S .
      // W M E
      // . N .

      const contrastN = contrast(rgbaM, rgbaN).toVar();
      const contrastS = contrast(rgbaM, rgbaS).toVar();
      const contrastE = contrast(rgbaM, rgbaE).toVar();
      const contrastW = contrast(rgbaM, rgbaW).toVar();
      const maxValue = max$1(contrastN, max$1(contrastS, max$1(contrastE, contrastW))).toVar();

      // . 0 .
      // 0 0 0
      // . 0 .

      If(maxValue.lessThan(fxaaQualityEdgeThreshold), () => {
        return rgbaM; // assuming define FXAA_DISCARD is always 0
      });

      //

      const relativeVContrast = sub(contrastN.add(contrastS), contrastE.add(contrastW)).toVar();
      relativeVContrast.mulAssign(fxaaQualityinvEdgeThreshold);

      // 45 deg edge detection and corners of objects, aka V/H contrast is too similar

      If(abs(relativeVContrast).lessThan(0.3), () => {
        // locate the edge

        const x = contrastE.greaterThan(contrastW).select(1, -1).toVar();
        const y = contrastS.greaterThan(contrastN).select(1, -1).toVar();
        const dirToEdge = vec2(x, y).toVar();
        // . 2 .      . 1 .
        // 1 0 2  ~=  0 0 1
        // . 1 .      . 0 .

        // tap 2 pixels and see which ones are "outside" the edge, to
        // determine if the edge is vertical or horizontal

        const rgbaAlongH = FxaaTexOff(uv, vec2(dirToEdge.x, dirToEdge.y), fxaaQualityRcpFrame.xy);
        const matchAlongH = contrast(rgbaM, rgbaAlongH).toVar();
        // . 1 .
        // 0 0 1
        // . 0 H

        const rgbaAlongV = FxaaTexOff(uv, vec2(dirToEdge.x.negate(), dirToEdge.y.negate()), fxaaQualityRcpFrame.xy);
        const matchAlongV = contrast(rgbaM, rgbaAlongV).toVar();
        // V 1 .
        // 0 0 1
        // . 0 .

        relativeVContrast.assign(matchAlongV.sub(matchAlongH));
        relativeVContrast.mulAssign(fxaaQualityinvEdgeThreshold);
        If(abs(relativeVContrast).lessThan(0.3), () => {
          // 45 deg edge

          // 1 1 .
          // 0 0 1
          // . 0 1

          // do a simple blur
          const sum = rgbaN.add(rgbaS).add(rgbaE).add(rgbaW);
          return mix(rgbaM, sum.mul(0.25), 0.4);
        });
      });
      const offNP = vec2().toVar();
      If(relativeVContrast.lessThanEqual(0), () => {
        rgbaN.assign(rgbaW);
        rgbaS.assign(rgbaE);

        // . 0 .      1
        // 1 0 1  ->  0
        // . 0 .      1

        offNP.x.assign(0);
        offNP.y.assign(fxaaQualityRcpFrame.y);
      }).Else(() => {
        offNP.x.assign(fxaaQualityRcpFrame.x);
        offNP.y.assign(0);
      });
      const mn = contrast(rgbaM, rgbaN).toVar();
      const ms = contrast(rgbaM, rgbaS).toVar();
      If(mn.lessThanEqual(ms), () => {
        rgbaN.assign(rgbaS);
      });
      const doneN = int$1(0).toVar();
      const doneP = int$1(0).toVar();
      const nDist = float(0).toVar();
      const pDist = float(0).toVar();
      const posN = vec2(uv).toVar();
      const posP = vec2(uv).toVar();
      const iterationsUsedN = int$1(0).toVar();
      const iterationsUsedP = int$1(0).toVar();
      Loop(NUM_SAMPLES, ({
        i
      }) => {
        const increment = i.add(1).toVar();
        If(doneN.equal(0), () => {
          nDist.addAssign(increment);
          posN.assign(uv.add(offNP.mul(nDist)));
          const rgbaEndN = FxaaTexTop(posN.xy);
          const nm = contrast(rgbaEndN, rgbaM).toVar();
          const nn = contrast(rgbaEndN, rgbaN).toVar();
          If(nm.greaterThan(nn), () => {
            doneN.assign(1);
          });
          iterationsUsedN.assign(i);
        });
        If(doneP.equal(0), () => {
          pDist.addAssign(increment);
          posP.assign(uv.sub(offNP.mul(pDist)));
          const rgbaEndP = FxaaTexTop(posP.xy);
          const pm = contrast(rgbaEndP, rgbaM).toVar();
          const pn = contrast(rgbaEndP, rgbaN).toVar();
          If(pm.greaterThan(pn), () => {
            doneP.assign(1);
          });
          iterationsUsedP.assign(i);
        });
        If(doneN.equal(1).or(doneP.equal(1)), () => {
          Break();
        });
      });
      If(doneN.equal(0).and(doneP.equal(0)), () => {
        return rgbaM; // failed to find end of edge
      });
      const distN = float(1).toVar();
      const distP = float(1).toVar();
      If(doneN.equal(1), () => {
        distN.assign(float(iterationsUsedN).div(float(NUM_SAMPLES.sub(1))));
      });
      If(doneP.equal(1), () => {
        distP.assign(float(iterationsUsedP).div(float(NUM_SAMPLES.sub(1))));
      });
      const dist = min$1(distN, distP);

      // hacky way of reduces blurriness of mostly diagonal edges
      // but reduces AA quality
      dist.assign(pow(dist, 0.5));
      dist.assign(float(1).sub(dist));
      return mix(rgbaM, rgbaN, dist.mul(0.5));
    }).setLayout({
      name: 'FxaaPixelShader',
      type: 'vec4',
      inputs: [{
        name: 'uv',
        type: 'vec2'
      }, {
        name: 'fxaaQualityRcpFrame',
        type: 'vec2'
      }, {
        name: 'fxaaQualityEdgeThreshold',
        type: 'float'
      }, {
        name: 'fxaaQualityinvEdgeThreshold',
        type: 'float'
      }]
    });
    const fxaa = Fn(() => {
      const edgeDetectionQuality = float(0.2);
      const invEdgeDetectionQuality = float(1).div(edgeDetectionQuality);
      return FxaaPixelShader(uvNode, this._invSize, edgeDetectionQuality, invEdgeDetectionQuality);
    });
    const outputNode = fxaa();
    return outputNode;
  }
}
FXAANode.type = /*@__PURE__*/registerNode('FXAA', FXAANode);

class TransitionNode extends TempNode {
  constructor(textureNodeA, textureNodeB, mixTextureNode, mixRatioNode, thresholdNode, useTextureNode) {
    super();

    // Input textures

    this.textureNodeA = textureNodeA;
    this.textureNodeB = textureNodeB;
    this.mixTextureNode = mixTextureNode;

    // Uniforms

    this.mixRatioNode = mixRatioNode;
    this.thresholdNode = thresholdNode;
    this.useTextureNode = useTextureNode;
  }
  setup() {
    const {
      textureNodeA,
      textureNodeB,
      mixTextureNode,
      mixRatioNode,
      thresholdNode,
      useTextureNode
    } = this;
    const sampleTexture = textureNode => {
      const uvNodeTexture = textureNode.uvNode || uv();
      return textureNode.uv(uvNodeTexture);
    };
    const transition = Fn(() => {
      const texelOne = sampleTexture(textureNodeA);
      const texelTwo = sampleTexture(textureNodeB);
      const color = vec4().toVar();
      If(useTextureNode.equal(int$1(1)), () => {
        const transitionTexel = sampleTexture(mixTextureNode);
        const r = mixRatioNode.mul(thresholdNode.mul(2.0).add(1.0)).sub(thresholdNode);
        const mixf = clamp(sub(transitionTexel.r, r).mul(float(1.0).div(thresholdNode)), 0.0, 1.0);
        color.assign(mix(texelOne, texelTwo, mixf));
      }).Else(() => {
        color.assign(mix(texelTwo, texelOne, mixRatioNode));
      });
      return color;
    });
    const outputNode = transition();
    return outputNode;
  }
}
TransitionNode.type = /*@__PURE__*/registerNode('Transition', TransitionNode);

class PixelationNode extends TempNode {
  constructor(textureNode, depthNode, normalNode, pixelSize, normalEdgeStrength, depthEdgeStrength) {
    super();

    // Input textures

    this.textureNode = textureNode;
    this.depthNode = depthNode;
    this.normalNode = normalNode;

    // Input uniforms

    this.pixelSize = pixelSize;
    this.normalEdgeStrength = normalEdgeStrength;
    this.depthEdgeStrength = depthEdgeStrength;

    // Private uniforms

    this._resolution = uniform(new Vector4());
    this.updateBeforeType = NodeUpdateType.RENDER;
  }
  updateBefore() {
    const map = this.textureNode.value;
    const width = map.image.width;
    const height = map.image.height;
    this._resolution.value.set(width, height, 1 / width, 1 / height);
  }
  setup() {
    const {
      textureNode,
      depthNode,
      normalNode
    } = this;
    const uvNodeTexture = textureNode.uvNode || uv();
    const uvNodeDepth = depthNode.uvNode || uv();
    const uvNodeNormal = normalNode.uvNode || uv();
    const sampleTexture = () => textureNode.uv(uvNodeTexture);
    const sampleDepth = (x, y) => depthNode.uv(uvNodeDepth.add(vec2(x, y).mul(this._resolution.zw))).r;
    const sampleNormal = (x, y) => normalNode.uv(uvNodeNormal.add(vec2(x, y).mul(this._resolution.zw))).rgb.normalize();
    const depthEdgeIndicator = depth => {
      const diff = property('float', 'diff');
      diff.addAssign(clamp(sampleDepth(1, 0).sub(depth)));
      diff.addAssign(clamp(sampleDepth(-1, 0).sub(depth)));
      diff.addAssign(clamp(sampleDepth(0, 1).sub(depth)));
      diff.addAssign(clamp(sampleDepth(0, -1).sub(depth)));
      return floor(smoothstep(0.01, 0.02, diff).mul(2)).div(2);
    };
    const neighborNormalEdgeIndicator = (x, y, depth, normal) => {
      const depthDiff = sampleDepth(x, y).sub(depth);
      const neighborNormal = sampleNormal(x, y);

      // Edge pixels should yield to faces who's normals are closer to the bias normal.

      const normalEdgeBias = vec3(1, 1, 1); // This should probably be a parameter.
      const normalDiff = dot(normal.sub(neighborNormal), normalEdgeBias);
      const normalIndicator = clamp(smoothstep(-0.01, 0.01, normalDiff), 0.0, 1.0);

      // Only the shallower pixel should detect the normal edge.

      const depthIndicator = clamp(sign(depthDiff.mul(.25).add(.0025)), 0.0, 1.0);
      return float(1.0).sub(dot(normal, neighborNormal)).mul(depthIndicator).mul(normalIndicator);
    };
    const normalEdgeIndicator = (depth, normal) => {
      const indicator = property('float', 'indicator');
      indicator.addAssign(neighborNormalEdgeIndicator(0, -1, depth, normal));
      indicator.addAssign(neighborNormalEdgeIndicator(0, 1, depth, normal));
      indicator.addAssign(neighborNormalEdgeIndicator(-1, 0, depth, normal));
      indicator.addAssign(neighborNormalEdgeIndicator(1, 0, depth, normal));
      return step(0.1, indicator);
    };
    const pixelation = Fn(() => {
      const texel = sampleTexture();
      const depth = property('float', 'depth');
      const normal = property('vec3', 'normal');
      If(this.depthEdgeStrength.greaterThan(0.0).or(this.normalEdgeStrength.greaterThan(0.0)), () => {
        depth.assign(sampleDepth(0, 0));
        normal.assign(sampleNormal(0, 0));
      });
      const dei = property('float', 'dei');
      If(this.depthEdgeStrength.greaterThan(0.0), () => {
        dei.assign(depthEdgeIndicator(depth));
      });
      const nei = property('float', 'nei');
      If(this.normalEdgeStrength.greaterThan(0.0), () => {
        nei.assign(normalEdgeIndicator(depth, normal));
      });
      const strength = dei.greaterThan(0).select(float(1.0).sub(dei.mul(this.depthEdgeStrength)), nei.mul(this.normalEdgeStrength).add(1));
      return texel.mul(strength);
    });
    const outputNode = pixelation();
    return outputNode;
  }
}
PixelationNode.type = /*@__PURE__*/registerNode('Pixelation', PixelationNode);
const pixelation = (node, depthNode, normalNode, pixelSize = 6, normalEdgeStrength = 0.3, depthEdgeStrength = 0.4) => nodeObject(new PixelationNode(convertToTexture(node), convertToTexture(depthNode), convertToTexture(normalNode), nodeObject(pixelSize), nodeObject(normalEdgeStrength), nodeObject(depthEdgeStrength)));
class PixelationPassNode extends PassNode {
  constructor(scene, camera, pixelSize = 6, normalEdgeStrength = 0.3, depthEdgeStrength = 0.4) {
    super('color', scene, camera, {
      minFilter: NearestFilter,
      magFilter: NearestFilter
    });
    this.pixelSize = pixelSize;
    this.normalEdgeStrength = normalEdgeStrength;
    this.depthEdgeStrength = depthEdgeStrength;
    this.isPixelationPassNode = true;
    this._mrt = mrt({
      output: output,
      normal: normalView
    });
  }
  setSize(width, height) {
    const pixelSize = this.pixelSize.value ? this.pixelSize.value : this.pixelSize;
    const adjustedWidth = Math.floor(width / pixelSize);
    const adjustedHeight = Math.floor(height / pixelSize);
    super.setSize(adjustedWidth, adjustedHeight);
  }
  setup() {
    const color = super.getTextureNode('output');
    const depth = super.getTextureNode('depth');
    const normal = super.getTextureNode('normal');
    return pixelation(color, depth, normal, this.pixelSize, this.normalEdgeStrength, this.depthEdgeStrength);
  }
}
PixelationPassNode.type = /*@__PURE__*/registerNode('PixelationPass', PixelationPassNode);

const _size$2 = /*@__PURE__*/new Vector2();

/**
*
* Supersample Anti-Aliasing Render Pass
*
* This manual approach to SSAA re-renders the scene ones for each sample with camera jitter and accumulates the results.
*
* References: https://en.wikipedia.org/wiki/Supersampling
*
*/

class SSAAPassNode extends PassNode {
  constructor(scene, camera) {
    super(PassNode.COLOR, scene, camera);
    this.isSSAAPassNode = true;
    this.sampleLevel = 4; // specified as n, where the number of samples is 2^n, so sampleLevel = 4, is 2^4 samples, 16.
    this.unbiased = true;
    this.clearColor = new Color(0x000000);
    this.clearAlpha = 0;
    this._currentClearColor = new Color();
    this.sampleWeight = uniform(1);
    this.sampleRenderTarget = null;
    this._quadMesh = new QuadMesh$1();
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const {
      scene,
      camera
    } = this;
    this._pixelRatio = renderer.getPixelRatio();
    const size = renderer.getSize(_size$2);
    this.setSize(size.width, size.height);
    this.sampleRenderTarget.setSize(this.renderTarget.width, this.renderTarget.height);

    // save current renderer settings

    renderer.getClearColor(this._currentClearColor);
    const currentClearAlpha = renderer.getClearAlpha();
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    const currentAutoClear = renderer.autoClear;

    //

    this._cameraNear.value = camera.near;
    this._cameraFar.value = camera.far;
    renderer.setMRT(this.getMRT());
    renderer.autoClear = false;
    const jitterOffsets = _JitterVectors[Math.max(0, Math.min(this.sampleLevel, 5))];
    const baseSampleWeight = 1.0 / jitterOffsets.length;
    const roundingRange = 1 / 32;
    const viewOffset = {
      fullWidth: this.renderTarget.width,
      fullHeight: this.renderTarget.height,
      offsetX: 0,
      offsetY: 0,
      width: this.renderTarget.width,
      height: this.renderTarget.height
    };
    const originalViewOffset = Object.assign({}, camera.view);
    if (originalViewOffset.enabled) Object.assign(viewOffset, originalViewOffset);

    // render the scene multiple times, each slightly jitter offset from the last and accumulate the results.

    for (let i = 0; i < jitterOffsets.length; i++) {
      const jitterOffset = jitterOffsets[i];
      if (camera.setViewOffset) {
        camera.setViewOffset(viewOffset.fullWidth, viewOffset.fullHeight, viewOffset.offsetX + jitterOffset[0] * 0.0625, viewOffset.offsetY + jitterOffset[1] * 0.0625,
        // 0.0625 = 1 / 16

        viewOffset.width, viewOffset.height);
      }
      this.sampleWeight.value = baseSampleWeight;
      if (this.unbiased) {
        // the theory is that equal weights for each sample lead to an accumulation of rounding errors.
        // The following equation varies the sampleWeight per sample so that it is uniformly distributed
        // across a range of values whose rounding errors cancel each other out.

        const uniformCenteredDistribution = -0.5 + (i + 0.5) / jitterOffsets.length;
        this.sampleWeight.value += roundingRange * uniformCenteredDistribution;
      }
      renderer.setClearColor(this.clearColor, this.clearAlpha);
      renderer.setRenderTarget(this.sampleRenderTarget);
      renderer.clear();
      renderer.render(scene, camera);

      // accumulation

      renderer.setRenderTarget(this.renderTarget);
      if (i === 0) {
        renderer.setClearColor(0x000000, 0.0);
        renderer.clear();
      }
      this._quadMesh.render(renderer);
    }
    renderer.copyTextureToTexture(this.sampleRenderTarget.depthTexture, this.renderTarget.depthTexture);

    // restore

    if (camera.setViewOffset && originalViewOffset.enabled) {
      camera.setViewOffset(originalViewOffset.fullWidth, originalViewOffset.fullHeight, originalViewOffset.offsetX, originalViewOffset.offsetY, originalViewOffset.width, originalViewOffset.height);
    } else if (camera.clearViewOffset) {
      camera.clearViewOffset();
    }
    renderer.setRenderTarget(currentRenderTarget);
    renderer.setMRT(currentMRT);
    renderer.autoClear = currentAutoClear;
    renderer.setClearColor(this._currentClearColor, currentClearAlpha);
  }
  setup(builder) {
    if (this.sampleRenderTarget === null) {
      this.sampleRenderTarget = this.renderTarget.clone();
    }
    let sampleTexture;
    const passMRT = this.getMRT();
    if (passMRT !== null) {
      const outputs = {};
      for (const name in passMRT.outputNodes) {
        const index = getTextureIndex(this.sampleRenderTarget.textures, name);
        if (index >= 0) {
          outputs[name] = texture(this.sampleRenderTarget.textures[index]).mul(this.sampleWeight);
        }
      }
      sampleTexture = mrt(outputs);
    } else {
      sampleTexture = texture(this.sampleRenderTarget.texture).mul(this.sampleWeight);
    }
    this._quadMesh.material = new NodeMaterial$1();
    this._quadMesh.material.fragmentNode = sampleTexture;
    this._quadMesh.material.transparent = true;
    this._quadMesh.material.depthTest = false;
    this._quadMesh.material.depthWrite = false;
    this._quadMesh.material.premultipliedAlpha = true;
    this._quadMesh.material.blending = AdditiveBlending;
    this._quadMesh.material.normals = false;
    this._quadMesh.material.name = 'SSAA';
    return super.setup(builder);
  }
  dispose() {
    super.dispose();
    if (this.sampleRenderTarget !== null) {
      this.sampleRenderTarget.dispose();
    }
  }
}
SSAAPassNode.type = /*@__PURE__*/registerNode('SSAAPass', SSAAPassNode);

// These jitter vectors are specified in integers because it is easier.
// I am assuming a [-8,8) integer grid, but it needs to be mapped onto [-0.5,0.5)
// before being used, thus these integers need to be scaled by 1/16.
//
// Sample patterns reference: https://msdn.microsoft.com/en-us/library/windows/desktop/ff476218%28v=vs.85%29.aspx?f=255&MSPPError=-2147217396
const _JitterVectors = [[[0, 0]], [[4, 4], [-4, -4]], [[-2, -6], [6, -2], [-6, 2], [2, 6]], [[1, -3], [-1, 3], [5, 1], [-3, -5], [-5, 5], [-7, -1], [3, 7], [7, -7]], [[1, 1], [-1, -3], [-3, 2], [4, -1], [-5, -2], [2, 5], [5, 3], [3, -5], [-2, 6], [0, -7], [-4, -6], [-6, 4], [-8, 0], [7, -4], [6, 7], [-7, -8]], [[-4, -7], [-7, -5], [-3, -5], [-5, -4], [-1, -4], [-2, -2], [-6, -1], [-4, 0], [-7, 1], [-1, 2], [-6, 3], [-3, 3], [-7, 6], [-3, 6], [-5, 7], [-1, 7], [5, -7], [1, -6], [6, -5], [4, -4], [2, -3], [7, -2], [1, -1], [4, -1], [2, 1], [6, 2], [0, 4], [4, 4], [2, 5], [7, 5], [5, 6], [3, 7]]];

const _v3 = /*@__PURE__*/new Vector3();
const _minTarget = /*@__PURE__*/new Vector2();
const _maxTarget = /*@__PURE__*/new Vector2();
class PerspectiveCamera extends Camera {
  constructor(fov = 50, aspect = 1, near = 0.1, far = 2000) {
    super();
    this.isPerspectiveCamera = true;
    this.type = 'PerspectiveCamera';
    this.fov = fov;
    this.zoom = 1;
    this.near = near;
    this.far = far;
    this.focus = 10;
    this.aspect = aspect;
    this.view = null;
    this.filmGauge = 35; // width of the film (default in millimeters)
    this.filmOffset = 0; // horizontal film offset (same unit as gauge)

    this.updateProjectionMatrix();
  }
  copy(source, recursive) {
    super.copy(source, recursive);
    this.fov = source.fov;
    this.zoom = source.zoom;
    this.near = source.near;
    this.far = source.far;
    this.focus = source.focus;
    this.aspect = source.aspect;
    this.view = source.view === null ? null : Object.assign({}, source.view);
    this.filmGauge = source.filmGauge;
    this.filmOffset = source.filmOffset;
    return this;
  }

  /**
   * Sets the FOV by focal length in respect to the current .filmGauge.
   *
   * The default film gauge is 35, so that the focal length can be specified for
   * a 35mm (full frame) camera.
   *
   * Values for focal length and film gauge must have the same unit.
   */
  setFocalLength(focalLength) {
    /** see {@link http://www.bobatkins.com/photography/technical/field_of_view.html} */
    const vExtentSlope = 0.5 * this.getFilmHeight() / focalLength;
    this.fov = RAD2DEG * 2 * Math.atan(vExtentSlope);
    this.updateProjectionMatrix();
  }

  /**
   * Calculates the focal length from the current .fov and .filmGauge.
   */
  getFocalLength() {
    const vExtentSlope = Math.tan(DEG2RAD * 0.5 * this.fov);
    return 0.5 * this.getFilmHeight() / vExtentSlope;
  }
  getEffectiveFOV() {
    return RAD2DEG * 2 * Math.atan(Math.tan(DEG2RAD * 0.5 * this.fov) / this.zoom);
  }
  getFilmWidth() {
    // film not completely covered in portrait format (aspect < 1)
    return this.filmGauge * Math.min(this.aspect, 1);
  }
  getFilmHeight() {
    // film not completely covered in landscape format (aspect > 1)
    return this.filmGauge / Math.max(this.aspect, 1);
  }

  /**
   * Computes the 2D bounds of the camera's viewable rectangle at a given distance along the viewing direction.
   * Sets minTarget and maxTarget to the coordinates of the lower-left and upper-right corners of the view rectangle.
   */
  getViewBounds(distance, minTarget, maxTarget) {
    _v3.set(-1, -1, 0.5).applyMatrix4(this.projectionMatrixInverse);
    minTarget.set(_v3.x, _v3.y).multiplyScalar(-distance / _v3.z);
    _v3.set(1, 1, 0.5).applyMatrix4(this.projectionMatrixInverse);
    maxTarget.set(_v3.x, _v3.y).multiplyScalar(-distance / _v3.z);
  }

  /**
   * Computes the width and height of the camera's viewable rectangle at a given distance along the viewing direction.
   * Copies the result into the target Vector2, where x is width and y is height.
   */
  getViewSize(distance, target) {
    this.getViewBounds(distance, _minTarget, _maxTarget);
    return target.subVectors(_maxTarget, _minTarget);
  }

  /**
   * Sets an offset in a larger frustum. This is useful for multi-window or
   * multi-monitor/multi-machine setups.
   *
   * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
   * the monitors are in grid like this
   *
   *   +---+---+---+
   *   | A | B | C |
   *   +---+---+---+
   *   | D | E | F |
   *   +---+---+---+
   *
   * then for each monitor you would call it like this
   *
   *   const w = 1920;
   *   const h = 1080;
   *   const fullWidth = w * 3;
   *   const fullHeight = h * 2;
   *
   *   --A--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
   *   --B--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
   *   --C--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
   *   --D--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
   *   --E--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
   *   --F--
   *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
   *
   *   Note there is no reason monitors have to be the same size or in a grid.
   */
  setViewOffset(fullWidth, fullHeight, x, y, width, height) {
    this.aspect = fullWidth / fullHeight;
    if (this.view === null) {
      this.view = {
        enabled: true,
        fullWidth: 1,
        fullHeight: 1,
        offsetX: 0,
        offsetY: 0,
        width: 1,
        height: 1
      };
    }
    this.view.enabled = true;
    this.view.fullWidth = fullWidth;
    this.view.fullHeight = fullHeight;
    this.view.offsetX = x;
    this.view.offsetY = y;
    this.view.width = width;
    this.view.height = height;
    this.updateProjectionMatrix();
  }
  clearViewOffset() {
    if (this.view !== null) {
      this.view.enabled = false;
    }
    this.updateProjectionMatrix();
  }
  updateProjectionMatrix() {
    const near = this.near;
    let top = near * Math.tan(DEG2RAD * 0.5 * this.fov) / this.zoom;
    let height = 2 * top;
    let width = this.aspect * height;
    let left = -0.5 * width;
    const view = this.view;
    if (this.view !== null && this.view.enabled) {
      const fullWidth = view.fullWidth,
        fullHeight = view.fullHeight;
      left += view.offsetX * width / fullWidth;
      top -= view.offsetY * height / fullHeight;
      width *= view.width / fullWidth;
      height *= view.height / fullHeight;
    }
    const skew = this.filmOffset;
    if (skew !== 0) left += near * skew / this.getFilmWidth();
    this.projectionMatrix.makePerspective(left, left + width, top, top - height, near, this.far, this.coordinateSystem);
    this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
  toJSON(meta) {
    const data = super.toJSON(meta);
    data.object.fov = this.fov;
    data.object.zoom = this.zoom;
    data.object.near = this.near;
    data.object.far = this.far;
    data.object.focus = this.focus;
    data.object.aspect = this.aspect;
    if (this.view !== null) data.object.view = Object.assign({}, this.view);
    data.object.filmGauge = this.filmGauge;
    data.object.filmOffset = this.filmOffset;
    return data;
  }
}

const _eyeRight = /*@__PURE__*/new Matrix4();
const _eyeLeft = /*@__PURE__*/new Matrix4();
const _projectionMatrix = /*@__PURE__*/new Matrix4();
class StereoCamera {
  constructor() {
    this.type = 'StereoCamera';
    this.aspect = 1;
    this.eyeSep = 0.064;
    this.cameraL = new PerspectiveCamera();
    this.cameraL.layers.enable(1);
    this.cameraL.matrixAutoUpdate = false;
    this.cameraR = new PerspectiveCamera();
    this.cameraR.layers.enable(2);
    this.cameraR.matrixAutoUpdate = false;
    this._cache = {
      focus: null,
      fov: null,
      aspect: null,
      near: null,
      far: null,
      zoom: null,
      eyeSep: null
    };
  }
  update(camera) {
    const cache = this._cache;
    const needsUpdate = cache.focus !== camera.focus || cache.fov !== camera.fov || cache.aspect !== camera.aspect * this.aspect || cache.near !== camera.near || cache.far !== camera.far || cache.zoom !== camera.zoom || cache.eyeSep !== this.eyeSep;
    if (needsUpdate) {
      cache.focus = camera.focus;
      cache.fov = camera.fov;
      cache.aspect = camera.aspect * this.aspect;
      cache.near = camera.near;
      cache.far = camera.far;
      cache.zoom = camera.zoom;
      cache.eyeSep = this.eyeSep;

      // Off-axis stereoscopic effect based on
      // http://paulbourke.net/stereographics/stereorender/

      _projectionMatrix.copy(camera.projectionMatrix);
      const eyeSepHalf = cache.eyeSep / 2;
      const eyeSepOnProjection = eyeSepHalf * cache.near / cache.focus;
      const ymax = cache.near * Math.tan(DEG2RAD * cache.fov * 0.5) / cache.zoom;
      let xmin, xmax;

      // translate xOffset

      _eyeLeft.elements[12] = -eyeSepHalf;
      _eyeRight.elements[12] = eyeSepHalf;

      // for left eye

      xmin = -ymax * cache.aspect + eyeSepOnProjection;
      xmax = ymax * cache.aspect + eyeSepOnProjection;
      _projectionMatrix.elements[0] = 2 * cache.near / (xmax - xmin);
      _projectionMatrix.elements[8] = (xmax + xmin) / (xmax - xmin);
      this.cameraL.projectionMatrix.copy(_projectionMatrix);

      // for right eye

      xmin = -ymax * cache.aspect - eyeSepOnProjection;
      xmax = ymax * cache.aspect - eyeSepOnProjection;
      _projectionMatrix.elements[0] = 2 * cache.near / (xmax - xmin);
      _projectionMatrix.elements[8] = (xmax + xmin) / (xmax - xmin);
      this.cameraR.projectionMatrix.copy(_projectionMatrix);
    }
    this.cameraL.matrixWorld.copy(camera.matrixWorld).multiply(_eyeLeft);
    this.cameraR.matrixWorld.copy(camera.matrixWorld).multiply(_eyeRight);
  }
}

const _size$1 = /*@__PURE__*/new Vector2();
class StereoPassNode extends PassNode {
  constructor(scene, camera) {
    super(PassNode.COLOR, scene, camera);
    this.isStereoPassNode = true;
    this.stereo = new StereoCamera();
    this.stereo.aspect = 0.5;
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const {
      scene,
      camera,
      stereo,
      renderTarget
    } = this;
    this._pixelRatio = renderer.getPixelRatio();
    stereo.cameraL.coordinateSystem = renderer.coordinateSystem;
    stereo.cameraR.coordinateSystem = renderer.coordinateSystem;
    stereo.update(camera);
    const size = renderer.getSize(_size$1);
    this.setSize(size.width, size.height);
    const currentAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    const currentRenderTarget = renderer.getRenderTarget();
    const currentMRT = renderer.getMRT();
    this._cameraNear.value = camera.near;
    this._cameraFar.value = camera.far;
    for (const name in this._previousTextures) {
      this.toggleTexture(name);
    }
    renderer.setRenderTarget(renderTarget);
    renderer.setMRT(this._mrt);
    renderer.clear();
    renderTarget.scissorTest = true;
    renderTarget.scissor.set(0, 0, renderTarget.width / 2, renderTarget.height);
    renderTarget.viewport.set(0, 0, renderTarget.width / 2, renderTarget.height);
    renderer.render(scene, stereo.cameraL);
    renderTarget.scissor.set(renderTarget.width / 2, 0, renderTarget.width / 2, renderTarget.height);
    renderTarget.viewport.set(renderTarget.width / 2, 0, renderTarget.width / 2, renderTarget.height);
    renderer.render(scene, stereo.cameraR);
    renderTarget.scissorTest = false;
    renderer.setRenderTarget(currentRenderTarget);
    renderer.setMRT(currentMRT);
    renderer.autoClear = currentAutoClear;
  }
}
StereoPassNode.type = /*@__PURE__*/registerNode('StereoPass', StereoPassNode);

const _size = /*@__PURE__*/new Vector2();
const _quadMesh = /*@__PURE__*/new QuadMesh$1();
class StereoCompositePassNode extends PassNode {
  constructor(scene, camera) {
    super(PassNode.COLOR, scene, camera);
    this.isStereoCompositePassNode = true;
    this.stereo = new StereoCamera();
    const _params = {
      minFilter: LinearFilter,
      magFilter: NearestFilter,
      type: HalfFloatType
    };
    this._renderTargetL = new RenderTarget(1, 1, _params);
    this._renderTargetR = new RenderTarget(1, 1, _params);
    this._mapLeft = texture(this._renderTargetL.texture);
    this._mapRight = texture(this._renderTargetR.texture);
    this._material = null;
  }
  updateStereoCamera(coordinateSystem) {
    this.stereo.cameraL.coordinateSystem = coordinateSystem;
    this.stereo.cameraR.coordinateSystem = coordinateSystem;
    this.stereo.update(this.camera);
  }
  setSize(width, height) {
    super.setSize(width, height);
    this._renderTargetL.setSize(this.renderTarget.width, this.renderTarget.height);
    this._renderTargetR.setSize(this.renderTarget.width, this.renderTarget.height);
  }
  updateBefore(frame) {
    const {
      renderer
    } = frame;
    const {
      scene,
      stereo,
      renderTarget
    } = this;
    this._pixelRatio = renderer.getPixelRatio();
    this.updateStereoCamera(renderer.coordinateSystem);
    const size = renderer.getSize(_size);
    this.setSize(size.width, size.height);
    const currentRenderTarget = renderer.getRenderTarget();

    // left

    renderer.setRenderTarget(this._renderTargetL);
    renderer.render(scene, stereo.cameraL);

    // right

    renderer.setRenderTarget(this._renderTargetR);
    renderer.render(scene, stereo.cameraR);

    // composite

    renderer.setRenderTarget(renderTarget);
    _quadMesh.material = this._material;
    _quadMesh.render(renderer);

    // restore

    renderer.setRenderTarget(currentRenderTarget);
  }
  dispose() {
    super.dispose();
    this._renderTargetL.dispose();
    this._renderTargetR.dispose();
    if (this._material !== null) {
      this._material.dispose();
    }
  }
}
StereoCompositePassNode.type = /*@__PURE__*/registerNode('StereoCompositePass', StereoCompositePassNode);

class ParallaxBarrierPassNode extends StereoCompositePassNode {
  constructor(scene, camera) {
    super(scene, camera);
    this.isParallaxBarrierPassNode = true;
  }
  setup(builder) {
    const uvNode = uv();
    const parallaxBarrier = Fn(() => {
      const color = vec4().toVar();
      If(mod(viewportCoordinate.y, 2).greaterThan(1), () => {
        color.assign(this._mapLeft.uv(uvNode));
      }).Else(() => {
        color.assign(this._mapRight.uv(uvNode));
      });
      return color;
    });
    const material = this._material || (this._material = new NodeMaterial$1());
    material.fragmentNode = parallaxBarrier().context(builder.getSharedContext());
    material.needsUpdate = true;
    return super.setup(builder);
  }
}
ParallaxBarrierPassNode.type = /*@__PURE__*/registerNode('ParallaxBarrierPass', ParallaxBarrierPassNode);

/*@__PURE__*/mat3(vec3(1.6605, -0.1246, -0.0182), vec3(-0.5876, 1.1329, -0.1006), vec3(-0.0728, -0.0083, 1.1187));
/*@__PURE__*/mat3(vec3(0.6274, 0.0691, 0.0164), vec3(0.3293, 0.9195, 0.0880), vec3(0.0433, 0.0113, 0.8956));

class CodeNode extends Node$1 {
  constructor(code = '', includes = [], language = '') {
    super('code');
    this.isCodeNode = true;
    this.code = code;
    this.language = language;
    this.includes = includes;
  }
  isGlobal() {
    return true;
  }
  setIncludes(includes) {
    this.includes = includes;
    return this;
  }
  getIncludes( /*builder*/
  ) {
    return this.includes;
  }
  generate(builder) {
    const includes = this.getIncludes(builder);
    for (const include of includes) {
      include.build(builder);
    }
    const nodeCode = builder.getCodeFromNode(this, this.getNodeType(builder));
    nodeCode.code = this.code;
    return nodeCode.code;
  }
  serialize(data) {
    super.serialize(data);
    data.code = this.code;
    data.language = this.language;
  }
  deserialize(data) {
    super.deserialize(data);
    this.code = data.code;
    this.language = data.language;
  }
}
CodeNode.type = /*@__PURE__*/registerNode('Code', CodeNode);

class FunctionNode extends CodeNode {
  constructor(code = '', includes = [], language = '') {
    super(code, includes, language);
  }
  getNodeType(builder) {
    return this.getNodeFunction(builder).type;
  }
  getInputs(builder) {
    return this.getNodeFunction(builder).inputs;
  }
  getNodeFunction(builder) {
    const nodeData = builder.getDataFromNode(this);
    let nodeFunction = nodeData.nodeFunction;
    if (nodeFunction === undefined) {
      nodeFunction = builder.parser.parseFunction(this.code);
      nodeData.nodeFunction = nodeFunction;
    }
    return nodeFunction;
  }
  generate(builder, output) {
    super.generate(builder);
    const nodeFunction = this.getNodeFunction(builder);
    const name = nodeFunction.name;
    const type = nodeFunction.type;
    const nodeCode = builder.getCodeFromNode(this, type);
    if (name !== '') {
      // use a custom property name

      nodeCode.name = name;
    }
    const propertyName = builder.getPropertyName(nodeCode);
    const code = this.getNodeFunction(builder).getCode(propertyName);
    nodeCode.code = code + '\n';
    if (output === 'property') {
      return propertyName;
    } else {
      return builder.format(`${propertyName}()`, type, output);
    }
  }
}
FunctionNode.type = /*@__PURE__*/registerNode('Function', FunctionNode);

class ScriptableValueNode extends Node$1 {
  constructor(value = null) {
    super();
    this._value = value;
    this._cache = null;
    this.inputType = null;
    this.outpuType = null;
    this.events = new EventDispatcher();
    this.isScriptableValueNode = true;
  }
  get isScriptableOutputNode() {
    return this.outputType !== null;
  }
  set value(val) {
    if (this._value === val) return;
    if (this._cache && this.inputType === 'URL' && this.value.value instanceof ArrayBuffer) {
      URL.revokeObjectURL(this._cache);
      this._cache = null;
    }
    this._value = val;
    this.events.dispatchEvent({
      type: 'change'
    });
    this.refresh();
  }
  get value() {
    return this._value;
  }
  refresh() {
    this.events.dispatchEvent({
      type: 'refresh'
    });
  }
  getValue() {
    const value = this.value;
    if (value && this._cache === null && this.inputType === 'URL' && value.value instanceof ArrayBuffer) {
      this._cache = URL.createObjectURL(new Blob([value.value]));
    } else if (value && value.value !== null && value.value !== undefined && ((this.inputType === 'URL' || this.inputType === 'String') && typeof value.value === 'string' || this.inputType === 'Number' && typeof value.value === 'number' || this.inputType === 'Vector2' && value.value.isVector2 || this.inputType === 'Vector3' && value.value.isVector3 || this.inputType === 'Vector4' && value.value.isVector4 || this.inputType === 'Color' && value.value.isColor || this.inputType === 'Matrix3' && value.value.isMatrix3 || this.inputType === 'Matrix4' && value.value.isMatrix4)) {
      return value.value;
    }
    return this._cache || value;
  }
  getNodeType(builder) {
    return this.value && this.value.isNode ? this.value.getNodeType(builder) : 'float';
  }
  setup() {
    return this.value && this.value.isNode ? this.value : float();
  }
  serialize(data) {
    super.serialize(data);
    if (this.value !== null) {
      if (this.inputType === 'ArrayBuffer') {
        data.value = arrayBufferToBase64(this.value);
      } else {
        data.value = this.value ? this.value.toJSON(data.meta).uuid : null;
      }
    } else {
      data.value = null;
    }
    data.inputType = this.inputType;
    data.outputType = this.outputType;
  }
  deserialize(data) {
    super.deserialize(data);
    let value = null;
    if (data.value !== null) {
      if (data.inputType === 'ArrayBuffer') {
        value = base64ToArrayBuffer(data.value);
      } else if (data.inputType === 'Texture') {
        value = data.meta.textures[data.value];
      } else {
        value = data.meta.nodes[data.value] || null;
      }
    }
    this.value = value;
    this.inputType = data.inputType;
    this.outputType = data.outputType;
  }
}
ScriptableValueNode.type = /*@__PURE__*/registerNode('ScriptableValue', ScriptableValueNode);
const scriptableValue = /*@__PURE__*/nodeProxy(ScriptableValueNode);

class Resources extends Map {
  get(key, callback = null, ...params) {
    if (this.has(key)) return super.get(key);
    if (callback !== null) {
      const value = callback(...params);
      this.set(key, value);
      return value;
    }
  }
}
class Parameters {
  constructor(scriptableNode) {
    this.scriptableNode = scriptableNode;
  }
  get parameters() {
    return this.scriptableNode.parameters;
  }
  get layout() {
    return this.scriptableNode.getLayout();
  }
  getInputLayout(id) {
    return this.scriptableNode.getInputLayout(id);
  }
  get(name) {
    const param = this.parameters[name];
    const value = param ? param.getValue() : null;
    return value;
  }
}
const global$1 = new Resources();
class ScriptableNode extends Node$1 {
  constructor(codeNode = null, parameters = {}) {
    super();
    this.codeNode = codeNode;
    this.parameters = parameters;
    this._local = new Resources();
    this._output = scriptableValue();
    this._outputs = {};
    this._source = this.source;
    this._method = null;
    this._object = null;
    this._value = null;
    this._needsOutputUpdate = true;
    this.onRefresh = this.onRefresh.bind(this);
    this.isScriptableNode = true;
  }
  get source() {
    return this.codeNode ? this.codeNode.code : '';
  }
  setLocal(name, value) {
    return this._local.set(name, value);
  }
  getLocal(name) {
    return this._local.get(name);
  }
  onRefresh() {
    this._refresh();
  }
  getInputLayout(id) {
    for (const element of this.getLayout()) {
      if (element.inputType && (element.id === id || element.name === id)) {
        return element;
      }
    }
  }
  getOutputLayout(id) {
    for (const element of this.getLayout()) {
      if (element.outputType && (element.id === id || element.name === id)) {
        return element;
      }
    }
  }
  setOutput(name, value) {
    const outputs = this._outputs;
    if (outputs[name] === undefined) {
      outputs[name] = scriptableValue(value);
    } else {
      outputs[name].value = value;
    }
    return this;
  }
  getOutput(name) {
    return this._outputs[name];
  }
  getParameter(name) {
    return this.parameters[name];
  }
  setParameter(name, value) {
    const parameters = this.parameters;
    if (value && value.isScriptableNode) {
      this.deleteParameter(name);
      parameters[name] = value;
      parameters[name].getDefaultOutput().events.addEventListener('refresh', this.onRefresh);
    } else if (value && value.isScriptableValueNode) {
      this.deleteParameter(name);
      parameters[name] = value;
      parameters[name].events.addEventListener('refresh', this.onRefresh);
    } else if (parameters[name] === undefined) {
      parameters[name] = scriptableValue(value);
      parameters[name].events.addEventListener('refresh', this.onRefresh);
    } else {
      parameters[name].value = value;
    }
    return this;
  }
  getValue() {
    return this.getDefaultOutput().getValue();
  }
  deleteParameter(name) {
    let valueNode = this.parameters[name];
    if (valueNode) {
      if (valueNode.isScriptableNode) valueNode = valueNode.getDefaultOutput();
      valueNode.events.removeEventListener('refresh', this.onRefresh);
    }
    return this;
  }
  clearParameters() {
    for (const name of Object.keys(this.parameters)) {
      this.deleteParameter(name);
    }
    this.needsUpdate = true;
    return this;
  }
  call(name, ...params) {
    const object = this.getObject();
    const method = object[name];
    if (typeof method === 'function') {
      return method(...params);
    }
  }
  async callAsync(name, ...params) {
    const object = this.getObject();
    const method = object[name];
    if (typeof method === 'function') {
      return method.constructor.name === 'AsyncFunction' ? await method(...params) : method(...params);
    }
  }
  getNodeType(builder) {
    return this.getDefaultOutputNode().getNodeType(builder);
  }
  refresh(output = null) {
    if (output !== null) {
      this.getOutput(output).refresh();
    } else {
      this._refresh();
    }
  }
  getObject() {
    if (this.needsUpdate) this.dispose();
    if (this._object !== null) return this._object;

    //

    const refresh = () => this.refresh();
    const setOutput = (id, value) => this.setOutput(id, value);
    const parameters = new Parameters(this);
    const THREE = global$1.get('THREE');
    const TSL = global$1.get('TSL');
    const method = this.getMethod(this.codeNode);
    const params = [parameters, this._local, global$1, refresh, setOutput, THREE, TSL];
    this._object = method(...params);
    const layout = this._object.layout;
    if (layout) {
      if (layout.cache === false) {
        this._local.clear();
      }

      // default output
      this._output.outputType = layout.outputType || null;
      if (Array.isArray(layout.elements)) {
        for (const element of layout.elements) {
          const id = element.id || element.name;
          if (element.inputType) {
            if (this.getParameter(id) === undefined) this.setParameter(id, null);
            this.getParameter(id).inputType = element.inputType;
          }
          if (element.outputType) {
            if (this.getOutput(id) === undefined) this.setOutput(id, null);
            this.getOutput(id).outputType = element.outputType;
          }
        }
      }
    }
    return this._object;
  }
  deserialize(data) {
    super.deserialize(data);
    for (const name in this.parameters) {
      let valueNode = this.parameters[name];
      if (valueNode.isScriptableNode) valueNode = valueNode.getDefaultOutput();
      valueNode.events.addEventListener('refresh', this.onRefresh);
    }
  }
  getLayout() {
    return this.getObject().layout;
  }
  getDefaultOutputNode() {
    const output = this.getDefaultOutput().value;
    if (output && output.isNode) {
      return output;
    }
    return float();
  }
  getDefaultOutput() {
    return this._exec()._output;
  }
  getMethod() {
    if (this.needsUpdate) this.dispose();
    if (this._method !== null) return this._method;

    //

    const parametersProps = ['parameters', 'local', 'global', 'refresh', 'setOutput', 'THREE', 'TSL'];
    const interfaceProps = ['layout', 'init', 'main', 'dispose'];
    const properties = interfaceProps.join(', ');
    const declarations = 'var ' + properties + '; var output = {};\n';
    const returns = '\nreturn { ...output, ' + properties + ' };';
    const code = declarations + this.codeNode.code + returns;

    //

    this._method = new Function(...parametersProps, code);
    return this._method;
  }
  dispose() {
    if (this._method === null) return;
    if (this._object && typeof this._object.dispose === 'function') {
      this._object.dispose();
    }
    this._method = null;
    this._object = null;
    this._source = null;
    this._value = null;
    this._needsOutputUpdate = true;
    this._output.value = null;
    this._outputs = {};
  }
  setup() {
    return this.getDefaultOutputNode();
  }
  getCacheKey(force) {
    const cacheKey = [this.source, this.getDefaultOutputNode().getCacheKey(force)];
    for (const param in this.parameters) {
      cacheKey.push(this.parameters[param].getCacheKey(force));
    }
    return cacheKey.join(',');
  }
  set needsUpdate(value) {
    if (value === true) this.dispose();
  }
  get needsUpdate() {
    return this.source !== this._source;
  }
  _exec() {
    if (this.codeNode === null) return this;
    if (this._needsOutputUpdate === true) {
      this._value = this.call('main');
      this._needsOutputUpdate = false;
    }
    this._output.value = this._value;
    return this;
  }
  _refresh() {
    this.needsUpdate = true;
    this._exec();
    this._output.refresh();
  }
}
ScriptableNode.type = /*@__PURE__*/registerNode('Scriptable', ScriptableNode);

class FogNode extends Node$1 {
  constructor(colorNode, factorNode) {
    super('float');
    this.isFogNode = true;
    this.colorNode = colorNode;
    this.factorNode = factorNode;
  }
  getViewZNode(builder) {
    let viewZ;
    const getViewZ = builder.context.getViewZ;
    if (getViewZ !== undefined) {
      viewZ = getViewZ(this);
    }
    return (viewZ || positionView.z).negate();
  }
  setup() {
    return this.factorNode;
  }
}
FogNode.type = /*@__PURE__*/registerNode('Fog', FogNode);

class FogRangeNode extends FogNode {
  constructor(colorNode, nearNode, farNode) {
    super(colorNode);
    this.isFogRangeNode = true;
    this.nearNode = nearNode;
    this.farNode = farNode;
  }
  setup(builder) {
    const viewZ = this.getViewZNode(builder);
    return smoothstep(this.nearNode, this.farNode, viewZ);
  }
}
FogRangeNode.type = /*@__PURE__*/registerNode('FogRange', FogRangeNode);

class FogExp2Node extends FogNode {
  constructor(colorNode, densityNode) {
    super(colorNode);
    this.isFogExp2Node = true;
    this.densityNode = densityNode;
  }
  setup(builder) {
    const viewZ = this.getViewZNode(builder);
    const density = this.densityNode;
    return density.mul(density, viewZ, viewZ).negate().exp().oneMinus();
  }
}
FogExp2Node.type = /*@__PURE__*/registerNode('FogExp2', FogExp2Node);

let min = null;
let max = null;
class RangeNode extends Node$1 {
  constructor(minNode = float(), maxNode = float()) {
    super();
    this.minNode = minNode;
    this.maxNode = maxNode;
  }
  getVectorLength(builder) {
    const minLength = builder.getTypeLength(getValueType(this.minNode.value));
    const maxLength = builder.getTypeLength(getValueType(this.maxNode.value));
    return minLength > maxLength ? minLength : maxLength;
  }
  getNodeType(builder) {
    return builder.object.count > 1 ? builder.getTypeFromLength(this.getVectorLength(builder)) : 'float';
  }
  setup(builder) {
    const object = builder.object;
    let output = null;
    if (object.count > 1) {
      const minValue = this.minNode.value;
      const maxValue = this.maxNode.value;
      const minLength = builder.getTypeLength(getValueType(minValue));
      const maxLength = builder.getTypeLength(getValueType(maxValue));
      min = min || new Vector4();
      max = max || new Vector4();
      min.setScalar(0);
      max.setScalar(0);
      if (minLength === 1) min.setScalar(minValue);else if (minValue.isColor) min.set(minValue.r, minValue.g, minValue.b);else min.set(minValue.x, minValue.y, minValue.z || 0, minValue.w || 0);
      if (maxLength === 1) max.setScalar(maxValue);else if (maxValue.isColor) max.set(maxValue.r, maxValue.g, maxValue.b);else max.set(maxValue.x, maxValue.y, maxValue.z || 0, maxValue.w || 0);
      const stride = 4;
      const length = stride * object.count;
      const array = new Float32Array(length);
      for (let i = 0; i < length; i++) {
        const index = i % stride;
        const minElementValue = min.getComponent(index);
        const maxElementValue = max.getComponent(index);
        array[i] = MathUtils.lerp(minElementValue, maxElementValue, Math.random());
      }
      const nodeType = this.getNodeType(builder);
      if (object.count <= 4096) {
        output = buffer(array, 'vec4', object.count).element(instanceIndex).convert(nodeType);
      } else {
        // TODO: Improve anonymous buffer attribute creation removing this part
        const bufferAttribute = new InstancedBufferAttribute(array, 4);
        builder.geometry.setAttribute('__range' + this.id, bufferAttribute);
        output = instancedBufferAttribute(bufferAttribute).convert(nodeType);
      }
    } else {
      output = float(0);
    }
    return output;
  }
}
RangeNode.type = /*@__PURE__*/registerNode('Range', RangeNode);

class LightNode extends Node$1 {
  constructor(scope = LightNode.TARGET_DIRECTION, light = null) {
    super();
    this.scope = scope;
    this.light = light;
  }
  setup() {
    const {
      scope,
      light
    } = this;
    let output = null;
    if (scope === LightNode.TARGET_DIRECTION) {
      output = cameraViewMatrix.transformDirection(objectPosition(light).sub(objectPosition(light.target)));
    }
    return output;
  }
  serialize(data) {
    super.serialize(data);
    data.scope = this.scope;
  }
  deserialize(data) {
    super.deserialize(data);
    this.scope = data.scope;
  }
}
LightNode.TARGET_DIRECTION = 'targetDirection';
LightNode.type = /*@__PURE__*/registerNode('Light', LightNode);
/*@__PURE__*/nodeProxy(LightNode, LightNode.TARGET_DIRECTION);

// These defines must match with PMREMGenerator

const cubeUV_r0 = /*@__PURE__*/float(1.0);
const cubeUV_m0 = /*@__PURE__*/float(-2.0);
const cubeUV_r1 = /*@__PURE__*/float(0.8);
const cubeUV_m1 = /*@__PURE__*/float(-1.0);
const cubeUV_r4 = /*@__PURE__*/float(0.4);
const cubeUV_m4 = /*@__PURE__*/float(2.0);
const cubeUV_r5 = /*@__PURE__*/float(0.305);
const cubeUV_m5 = /*@__PURE__*/float(3.0);
const cubeUV_r6 = /*@__PURE__*/float(0.21);
const cubeUV_m6 = /*@__PURE__*/float(4.0);
const cubeUV_minMipLevel = /*@__PURE__*/float(4.0);
const cubeUV_minTileSize = /*@__PURE__*/float(16.0);

// These shader functions convert between the UV coordinates of a single face of
// a cubemap, the 0-5 integer index of a cube face, and the direction vector for
// sampling a textureCube (not generally normalized ).

const getFace = /*@__PURE__*/Fn(([direction]) => {
  const absDirection = vec3(abs(direction)).toVar();
  const face = float(-1.0).toVar();
  If(absDirection.x.greaterThan(absDirection.z), () => {
    If(absDirection.x.greaterThan(absDirection.y), () => {
      face.assign(select(direction.x.greaterThan(0.0), 0.0, 3.0));
    }).Else(() => {
      face.assign(select(direction.y.greaterThan(0.0), 1.0, 4.0));
    });
  }).Else(() => {
    If(absDirection.z.greaterThan(absDirection.y), () => {
      face.assign(select(direction.z.greaterThan(0.0), 2.0, 5.0));
    }).Else(() => {
      face.assign(select(direction.y.greaterThan(0.0), 1.0, 4.0));
    });
  });
  return face;
}).setLayout({
  name: 'getFace',
  type: 'float',
  inputs: [{
    name: 'direction',
    type: 'vec3'
  }]
});

// RH coordinate system; PMREM face-indexing convention
const getUV = /*@__PURE__*/Fn(([direction, face]) => {
  const uv = vec2().toVar();
  If(face.equal(0.0), () => {
    uv.assign(vec2(direction.z, direction.y).div(abs(direction.x))); // pos x
  }).ElseIf(face.equal(1.0), () => {
    uv.assign(vec2(direction.x.negate(), direction.z.negate()).div(abs(direction.y))); // pos y
  }).ElseIf(face.equal(2.0), () => {
    uv.assign(vec2(direction.x.negate(), direction.y).div(abs(direction.z))); // pos z
  }).ElseIf(face.equal(3.0), () => {
    uv.assign(vec2(direction.z.negate(), direction.y).div(abs(direction.x))); // neg x
  }).ElseIf(face.equal(4.0), () => {
    uv.assign(vec2(direction.x.negate(), direction.z).div(abs(direction.y))); // neg y
  }).Else(() => {
    uv.assign(vec2(direction.x, direction.y).div(abs(direction.z))); // neg z
  });
  return mul(0.5, uv.add(1.0));
}).setLayout({
  name: 'getUV',
  type: 'vec2',
  inputs: [{
    name: 'direction',
    type: 'vec3'
  }, {
    name: 'face',
    type: 'float'
  }]
});
const roughnessToMip = /*@__PURE__*/Fn(([roughness]) => {
  const mip = float(0.0).toVar();
  If(roughness.greaterThanEqual(cubeUV_r1), () => {
    mip.assign(cubeUV_r0.sub(roughness).mul(cubeUV_m1.sub(cubeUV_m0)).div(cubeUV_r0.sub(cubeUV_r1)).add(cubeUV_m0));
  }).ElseIf(roughness.greaterThanEqual(cubeUV_r4), () => {
    mip.assign(cubeUV_r1.sub(roughness).mul(cubeUV_m4.sub(cubeUV_m1)).div(cubeUV_r1.sub(cubeUV_r4)).add(cubeUV_m1));
  }).ElseIf(roughness.greaterThanEqual(cubeUV_r5), () => {
    mip.assign(cubeUV_r4.sub(roughness).mul(cubeUV_m5.sub(cubeUV_m4)).div(cubeUV_r4.sub(cubeUV_r5)).add(cubeUV_m4));
  }).ElseIf(roughness.greaterThanEqual(cubeUV_r6), () => {
    mip.assign(cubeUV_r5.sub(roughness).mul(cubeUV_m6.sub(cubeUV_m5)).div(cubeUV_r5.sub(cubeUV_r6)).add(cubeUV_m5));
  }).Else(() => {
    mip.assign(float(-2.0).mul(log2(mul(1.16, roughness)))); // 1.16 = 1.79^0.25
  });
  return mip;
}).setLayout({
  name: 'roughnessToMip',
  type: 'float',
  inputs: [{
    name: 'roughness',
    type: 'float'
  }]
});

//

const textureCubeUV = /*@__PURE__*/Fn(([envMap, sampleDir_immutable, roughness_immutable, CUBEUV_TEXEL_WIDTH, CUBEUV_TEXEL_HEIGHT, CUBEUV_MAX_MIP]) => {
  const roughness = float(roughness_immutable);
  const sampleDir = vec3(sampleDir_immutable);
  const mip = clamp(roughnessToMip(roughness), cubeUV_m0, CUBEUV_MAX_MIP);
  const mipF = fract(mip);
  const mipInt = floor(mip);
  const color0 = vec3(bilinearCubeUV(envMap, sampleDir, mipInt, CUBEUV_TEXEL_WIDTH, CUBEUV_TEXEL_HEIGHT, CUBEUV_MAX_MIP)).toVar();
  If(mipF.notEqual(0.0), () => {
    const color1 = vec3(bilinearCubeUV(envMap, sampleDir, mipInt.add(1.0), CUBEUV_TEXEL_WIDTH, CUBEUV_TEXEL_HEIGHT, CUBEUV_MAX_MIP)).toVar();
    color0.assign(mix(color0, color1, mipF));
  });
  return color0;
});
const bilinearCubeUV = /*@__PURE__*/Fn(([envMap, direction_immutable, mipInt_immutable, CUBEUV_TEXEL_WIDTH, CUBEUV_TEXEL_HEIGHT, CUBEUV_MAX_MIP]) => {
  const mipInt = float(mipInt_immutable).toVar();
  const direction = vec3(direction_immutable);
  const face = float(getFace(direction)).toVar();
  const filterInt = float(max$1(cubeUV_minMipLevel.sub(mipInt), 0.0)).toVar();
  mipInt.assign(max$1(mipInt, cubeUV_minMipLevel));
  const faceSize = float(exp2(mipInt)).toVar();
  const uv = vec2(getUV(direction, face).mul(faceSize.sub(2.0)).add(1.0)).toVar();
  If(face.greaterThan(2.0), () => {
    uv.y.addAssign(faceSize);
    face.subAssign(3.0);
  });
  uv.x.addAssign(face.mul(faceSize));
  uv.x.addAssign(filterInt.mul(mul(3.0, cubeUV_minTileSize)));
  uv.y.addAssign(mul(4.0, exp2(CUBEUV_MAX_MIP).sub(faceSize)));
  uv.x.mulAssign(CUBEUV_TEXEL_WIDTH);
  uv.y.mulAssign(CUBEUV_TEXEL_HEIGHT);
  return envMap.uv(uv).grad(vec2(), vec2()); // disable anisotropic filtering
});

let _generator = null;
const _cache = new WeakMap();
function _generateCubeUVSize(imageHeight) {
  const maxMip = Math.log2(imageHeight) - 2;
  const texelHeight = 1.0 / imageHeight;
  const texelWidth = 1.0 / (3 * Math.max(Math.pow(2, maxMip), 7 * 16));
  return {
    texelWidth,
    texelHeight,
    maxMip
  };
}
function _getPMREMFromTexture(texture) {
  let cacheTexture = _cache.get(texture);
  const pmremVersion = cacheTexture !== undefined ? cacheTexture.pmremVersion : -1;
  if (pmremVersion !== texture.pmremVersion) {
    const image = texture.image;
    if (texture.isCubeTexture) {
      if (isCubeMapReady(image)) {
        cacheTexture = _generator.fromCubemap(texture, cacheTexture);
      } else {
        return null;
      }
    } else {
      if (isEquirectangularMapReady(image)) {
        cacheTexture = _generator.fromEquirectangular(texture, cacheTexture);
      } else {
        return null;
      }
    }
    cacheTexture.pmremVersion = texture.pmremVersion;
    _cache.set(texture, cacheTexture);
  }
  return cacheTexture.texture;
}
class PMREMNode extends TempNode {
  constructor(value, uvNode = null, levelNode = null) {
    super('vec3');
    this._value = value;
    this._pmrem = null;
    this.uvNode = uvNode;
    this.levelNode = levelNode;
    this._generator = null;
    const defaultTexture = new Texture();
    defaultTexture.isRenderTargetTexture = true;
    this._texture = texture(defaultTexture);
    this._width = uniform(0);
    this._height = uniform(0);
    this._maxMip = uniform(0);
    this.updateBeforeType = NodeUpdateType.RENDER;
  }
  set value(value) {
    this._value = value;
    this._pmrem = null;
  }
  get value() {
    return this._value;
  }
  updateFromTexture(texture) {
    const cubeUVSize = _generateCubeUVSize(texture.image.height);
    this._texture.value = texture;
    this._width.value = cubeUVSize.texelWidth;
    this._height.value = cubeUVSize.texelHeight;
    this._maxMip.value = cubeUVSize.maxMip;
  }
  updateBefore() {
    let pmrem = this._pmrem;
    const pmremVersion = pmrem ? pmrem.pmremVersion : -1;
    const texture = this._value;
    if (pmremVersion !== texture.pmremVersion) {
      if (texture.isPMREMTexture === true) {
        pmrem = texture;
      } else {
        pmrem = _getPMREMFromTexture(texture);
      }
      if (pmrem !== null) {
        this._pmrem = pmrem;
        this.updateFromTexture(pmrem);
      }
    }
  }
  setup(builder) {
    if (_generator === null) {
      _generator = builder.createPMREMGenerator();
    }

    //

    this.updateBefore(builder);

    //

    let uvNode = this.uvNode;
    if (uvNode === null && builder.context.getUV) {
      uvNode = builder.context.getUV(this);
    }

    //

    const texture = this.value;
    if (builder.renderer.coordinateSystem === WebGLCoordinateSystem && texture.isPMREMTexture !== true && texture.isRenderTargetTexture === true) {
      uvNode = vec3(uvNode.x.negate(), uvNode.yz);
    }

    //

    let levelNode = this.levelNode;
    if (levelNode === null && builder.context.getTextureLevel) {
      levelNode = builder.context.getTextureLevel(this);
    }

    //

    return textureCubeUV(this._texture, uvNode, levelNode, this._width, this._height, this._maxMip);
  }
}
PMREMNode.type = /*@__PURE__*/registerNode('PMREM', PMREMNode);
function isCubeMapReady(image) {
  if (image === null || image === undefined) return false;
  let count = 0;
  const length = 6;
  for (let i = 0; i < length; i++) {
    if (image[i] !== undefined) count++;
  }
  return count === length;
}
function isEquirectangularMapReady(image) {
  if (image === null || image === undefined) return false;
  return image.height > 0;
}

class SphereGeometry extends BufferGeometry {
  constructor(radius = 1, widthSegments = 32, heightSegments = 16, phiStart = 0, phiLength = Math.PI * 2, thetaStart = 0, thetaLength = Math.PI) {
    super();
    this.type = 'SphereGeometry';
    this.parameters = {
      radius: radius,
      widthSegments: widthSegments,
      heightSegments: heightSegments,
      phiStart: phiStart,
      phiLength: phiLength,
      thetaStart: thetaStart,
      thetaLength: thetaLength
    };
    widthSegments = Math.max(3, Math.floor(widthSegments));
    heightSegments = Math.max(2, Math.floor(heightSegments));
    const thetaEnd = Math.min(thetaStart + thetaLength, Math.PI);
    let index = 0;
    const grid = [];
    const vertex = new Vector3();
    const normal = new Vector3();

    // buffers

    const indices = [];
    const vertices = [];
    const normals = [];
    const uvs = [];

    // generate vertices, normals and uvs

    for (let iy = 0; iy <= heightSegments; iy++) {
      const verticesRow = [];
      const v = iy / heightSegments;

      // special case for the poles

      let uOffset = 0;
      if (iy === 0 && thetaStart === 0) {
        uOffset = 0.5 / widthSegments;
      } else if (iy === heightSegments && thetaEnd === Math.PI) {
        uOffset = -0.5 / widthSegments;
      }
      for (let ix = 0; ix <= widthSegments; ix++) {
        const u = ix / widthSegments;

        // vertex

        vertex.x = -radius * Math.cos(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength);
        vertex.y = radius * Math.cos(thetaStart + v * thetaLength);
        vertex.z = radius * Math.sin(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength);
        vertices.push(vertex.x, vertex.y, vertex.z);

        // normal

        normal.copy(vertex).normalize();
        normals.push(normal.x, normal.y, normal.z);

        // uv

        uvs.push(u + uOffset, 1 - v);
        verticesRow.push(index++);
      }
      grid.push(verticesRow);
    }

    // indices

    for (let iy = 0; iy < heightSegments; iy++) {
      for (let ix = 0; ix < widthSegments; ix++) {
        const a = grid[iy][ix + 1];
        const b = grid[iy][ix];
        const c = grid[iy + 1][ix];
        const d = grid[iy + 1][ix + 1];
        if (iy !== 0 || thetaStart > 0) indices.push(a, b, d);
        if (iy !== heightSegments - 1 || thetaEnd < Math.PI) indices.push(b, c, d);
      }
    }

    // build geometry

    this.setIndex(indices);
    this.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    this.setAttribute('normal', new Float32BufferAttribute(normals, 3));
    this.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  }
  copy(source) {
    super.copy(source);
    this.parameters = Object.assign({}, source.parameters);
    return this;
  }
  static fromJSON(data) {
    return new SphereGeometry(data.radius, data.widthSegments, data.heightSegments, data.phiStart, data.phiLength, data.thetaStart, data.thetaLength);
  }
}

const _clearColor = /*@__PURE__*/new Color4$1();
class Background extends DataMap$1 {
  constructor(renderer, nodes) {
    super();
    this.renderer = renderer;
    this.nodes = nodes;
  }
  update(scene, renderList, renderContext) {
    const renderer = this.renderer;
    const background = this.nodes.getBackgroundNode(scene) || scene.background;
    let forceClear = false;
    if (background === null) {
      // no background settings, use clear color configuration from the renderer

      renderer._clearColor.getRGB(_clearColor, LinearSRGBColorSpace);
      _clearColor.a = renderer._clearColor.a;
    } else if (background.isColor === true) {
      // background is an opaque color

      background.getRGB(_clearColor, LinearSRGBColorSpace);
      _clearColor.a = 1;
      forceClear = true;
    } else if (background.isNode === true) {
      const sceneData = this.get(scene);
      const backgroundNode = background;
      _clearColor.copy(renderer._clearColor);
      let backgroundMesh = sceneData.backgroundMesh;
      if (backgroundMesh === undefined) {
        const backgroundMeshNode = context(vec4(backgroundNode).mul(backgroundIntensity), {
          // @TODO: Add Texture2D support using node context
          getUV: () => normalWorld,
          getTextureLevel: () => backgroundBlurriness
        });
        let viewProj = modelViewProjection();
        viewProj = viewProj.setZ(viewProj.w);
        const nodeMaterial = new NodeMaterial$1();
        nodeMaterial.name = 'Background.material';
        nodeMaterial.side = BackSide;
        nodeMaterial.depthTest = false;
        nodeMaterial.depthWrite = false;
        nodeMaterial.fog = false;
        nodeMaterial.lights = false;
        nodeMaterial.vertexNode = viewProj;
        nodeMaterial.colorNode = backgroundMeshNode;
        sceneData.backgroundMeshNode = backgroundMeshNode;
        sceneData.backgroundMesh = backgroundMesh = new Mesh(new SphereGeometry(1, 32, 32), nodeMaterial);
        backgroundMesh.frustumCulled = false;
        backgroundMesh.name = 'Background.mesh';
        backgroundMesh.onBeforeRender = function (renderer, scene, camera) {
          this.matrixWorld.copyPosition(camera.matrixWorld);
        };
      }
      const backgroundCacheKey = backgroundNode.getCacheKey();
      if (sceneData.backgroundCacheKey !== backgroundCacheKey) {
        sceneData.backgroundMeshNode.node = vec4(backgroundNode).mul(backgroundIntensity);
        sceneData.backgroundMeshNode.needsUpdate = true;
        backgroundMesh.material.needsUpdate = true;
        sceneData.backgroundCacheKey = backgroundCacheKey;
      }
      renderList.unshift(backgroundMesh, backgroundMesh.geometry, backgroundMesh.material, 0, 0, null);
    } else {
      console.error('THREE.Renderer: Unsupported background configuration.', background);
    }

    //

    if (renderer.autoClear === true || forceClear === true) {
      _clearColor.multiplyScalar(_clearColor.a);
      const clearColorValue = renderContext.clearColorValue;
      clearColorValue.r = _clearColor.r;
      clearColorValue.g = _clearColor.g;
      clearColorValue.b = _clearColor.b;
      clearColorValue.a = _clearColor.a;
      renderContext.depthClearValue = renderer._clearDepth;
      renderContext.stencilClearValue = renderer._clearStencil;
      renderContext.clearColor = renderer.autoClearColor === true;
      renderContext.clearDepth = renderer.autoClearDepth === true;
      renderContext.clearStencil = renderer.autoClearStencil === true;
    } else {
      renderContext.clearColor = false;
      renderContext.clearDepth = false;
      renderContext.clearStencil = false;
    }
  }
}
var background = Background;

//
var script$3 = {
  name: 'XmlObjectModal',
  components: {},
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      //属性组件名
      propertyComponentsName: 'xml-object-modal-property',
      dialogShow: false,
      curVideoUrl: '',
      curVideoTitle: '',
      curVideoContent: '',
      curVideoPoster: '',
      currentPlayer: {},
      initialTime: 0,
      modalPlayerKey: 'mpk' + new Date().getTime(),
      isLoad: false
    };
  },
  computed: {
    background() {
      return background;
    },
    isEmpty() {
      return !this.pBlockData.title;
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    },
    'blockData.loadingType': {
      handler(newValue) {
        if (this.renderer) {
          this.renderer.dispose();
          this.renderer.forceContextLoss();
          this.renderer.context = null;
          this.renderer.domElement = null;
        }
        if (newValue == 'click') {
          let div = this.$refs.threejsContainer;
          div.innerHTML = '';
          this.isLoad = false;
        } else {
          this.isLoad = true;
          this.$nextTick(() => {
            let div = this.$refs.threejsContainer;
            div.innerHTML = '';
            this.initThree();
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.blockData = this.pBlockData;
    if (this.blockData.loadingType == 'default') {
      this.isLoad = true;
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        this.renderer.context = null;
        this.renderer.domElement = null;
      }
      this.$nextTick(() => {
        this.initThree();
      });
    }
  },
  methods: {
    load3D() {
      console.log(123);
      this.isLoad = true;
      this.$nextTick(() => {
        this.initThree();
      });
    },
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    initThree() {
      this.scene = new Scene();
      this.scene.background = new Color$1(0xffffff); //场景背景色
      // this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      this.camera = new PerspectiveCamera$1(75, this.$refs.threejsContainer.clientWidth / this.$refs.threejsContainer.clientHeight, 0.1, 1000);
      this.camera.position.set(0, 0, 5);
      this.renderer = new WebGLRenderer({
        antialias: false
      });
      this.renderer.setSize(this.$refs.threejsContainer.clientWidth, this.$refs.threejsContainer.clientHeight);
      // this.renderer.setPixelRatio(window.devicePixelRatio / 1.1);
      this.$refs.threejsContainer.appendChild(this.renderer.domElement);
      this.gltfLoader = new GLTFLoader();
      // 添加光源
      const ambientLight = new AmbientLight(0xcccccc, 0.4);
      this.scene.add(ambientLight);
      const directionalLight = new DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(1, 1, 0).normalize();
      this.scene.add(directionalLight);
      this.controls = new OrbitControls(this.camera, this.$refs.threejsContainer);
      this.controls.zoomSpeed = 5;
      this.controls.rotateSpeed = 1;
      // this.controls.addEventListener('change', this.animate); // 监听控制器变化事件
      this.controls.enablePan = true; // 启用平移
      this.controls.enableRotate = true; // 启用旋转
      this.controls.enableZoom = true; // 启用缩放
      this.controls.target.set(0, 0, 0); // 设置控制器的目标点
      // this.camera.add(this.controls); // 将控制器添加到相机实例上
      this.loadModel();
    },
    loadModel() {
      console.log(this.blockData.Modal3DUrl);
      this.gltfLoader.load(this.blockData.Modal3DUrl, gltf => {
        const box = new Box3$1().setFromObject(gltf.scene);
        const size = box.getSize(new Vector3$1());
        const maxDimension = 4;
        const maxModelSize = Math.max(size.x, size.y, size.z);
        const scale = maxDimension / maxModelSize;
        const model = gltf.scene;
        model.scale.set(scale, scale, scale);
        this.loadedModel = model;
        this.scene.background = new Color$1(0xe6f0ff);
        this.scene.add(gltf.scene);
        this.animate();
      }, undefined, error => {
        console.error('An error happened1', error);
      });
    },
    animate() {
      requestAnimationFrame(this.animate);
      this.renderer.render(this.scene, this.camera);
    }
  }
};

/* script */
const __vue_script__$3 = script$3;
/* template */
var __vue_render__$3 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        "xml-object-modal-container",
        "object-model-content",
        "xml-object-modal-container-" + _vm.pageType,
      ],
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    [
      _c(
        "div",
        {
          style: {
            width: _vm.blockData.width ? _vm.blockData.width + "%" : "100%",
          },
        },
        [
          _vm.blockData.loadingType == "click" && _vm.isLoad == false
            ? _c(
                "div",
                {
                  staticClass: "object3d-btn",
                  style: {
                    background:
                      "url(" +
                      _vm.blockData.imgUrl +
                      ") no-repeat center center",
                    backgroundSize: "contain",
                  },
                },
                [
                  _c("span", { on: { click: _vm.load3D } }, [
                    _vm._v("加载模型"),
                  ]),
                ]
              )
            : _c("div", {
                ref: "threejsContainer",
                staticClass: "threejs-container",
              }),
          _vm._v(" "),
          _vm.blockData.showTitle == "1" && _vm.blockData.title
            ? _c(
                "p",
                {
                  staticClass: "object3d-title",
                  style: {
                    marginTop:
                      _vm.blockData.titlePosition == "below" ? "0" : "-32px",
                    textAlign: _vm.blockData.alignment,
                  },
                },
                [_vm._v(_vm._s(_vm.blockData.title))]
              )
            : _vm._e(),
        ]
      ),
    ]
  )
};
var __vue_staticRenderFns__$3 = [];
__vue_render__$3._withStripped = true;

  /* style */
  const __vue_inject_styles__$3 = undefined;
  /* scoped */
  const __vue_scope_id__$3 = "data-v-55979142";
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$3,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3,
    __vue_module_identifier__$3,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$3.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1(__vue_component__$3.name, {
    prefix: options.prefix
  }), __vue_component__$3);
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2 = {
  name: 'XmlTextareaRender',
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //属性中传递过来的块儿数据
    pBlockData: {},
    //块儿数据名
    blockDataName: String,
    //页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    officePreviewPath: {
      type: String,
      default: ''
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isReader: {
      // 是否阅读器
      type: Boolean,
      default: false
    }
  },
  watch: {
    pBlockData: {
      handler(newValue) {
        this.$nextTick(() => {
          this.blockData = Object.assign({}, this.blockData, newValue);
        });
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      ouchnOss: null,
      propertyComponentsName: 'xml-textarea-property',
      blockId: 'blockId' + Math.random(),
      //当前块儿数据，一般用不到，因为一般的块儿中不会在块儿内更改数据
      blockData: {},
      innerText: '',
      textareaWidth: 0,
      pageWidth: 0,
      placeholder: 0,
      fontWidth: 0,
      rows: 5
    };
  },
  computed: {},
  methods: {
    // 此方法必须要，用户注册块儿点击事件
    blockClick() {
      this.$emit('blockclick', {
        blockData: this.blockData,
        dataName: this.blockDataName,
        propertyComponentsName: this.propertyComponentsName,
        blockId: this.blockId,
        ruleIndex: this.ruleIndex
      });
    },
    handleInput(e) {
      this.innerText = e.target.innerText;
    },
    textareaClick(index) {
      setTimeout(() => {
        this.$refs['xmlTextarea' + index][0].focus();
      }, 200);
    }
  },
  created() {},
  mounted() {}
};

/* script */
const __vue_script__$2 = script$2;
/* template */
var __vue_render__$2 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "xml-textarea",
      class: "xml-textarea-container-" + _vm.pageType,
      attrs: { id: _vm.blockId },
      on: { click: _vm.blockClick },
    },
    _vm._l(_vm.blockData.textareaList, function (item, index) {
      return _c(
        "div",
        {
          key: index,
          staticClass: "wrapper",
          style: { height: item.rows * 30 + "px" },
        },
        [
          _c(
            "div",
            {
              staticClass: "box",
              on: {
                click: function ($event) {
                  return _vm.textareaClick(index)
                },
              },
            },
            [
              _c(
                "div",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: item.isShowImg == "2",
                      expression: "item.isShowImg == '2'",
                    },
                  ],
                  staticClass: "float",
                },
                [
                  _c("img", {
                    staticClass: "img",
                    attrs: { src: item.imageUrl },
                  }),
                ]
              ),
              _vm._v(" "),
              _c("span", { staticClass: "title" }, [
                _vm._v(_vm._s(item.title)),
              ]),
              _vm._v(" "),
              _c("span", {
                ref: "xmlTextarea" + index,
                refInFor: true,
                staticClass: "content textarea",
                attrs: { contenteditable: "true" },
                on: { input: _vm.handleInput },
              }),
            ]
          ),
        ]
      )
    }),
    0
  )
};
var __vue_staticRenderFns__$2 = [];
__vue_render__$2._withStripped = true;

  /* style */
  const __vue_inject_styles__$2 = undefined;
  /* scoped */
  const __vue_scope_id__$2 = "data-v-81cd5934";
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$2,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$2.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('resource-render', {
    prefix: options.prefix
  }), __vue_component__$2);
};

//
var script$1 = {
  name: "XmlSingleObjectModalGroupRender",
  components: {
    XmlObjectModal: __vue_component__$3
  },
  mixins: [singleGroupMixin],
  // components: {  },
  data() {
    return {
      typeText: "三维模型",
      object3DDataName: "Object3DData",
      groupData: {}
    };
  },
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return "editor";
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: "h5"
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  created() {
    this.groupData = this.pGroupData;
  },
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit("blockclick", {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    }
  }
};

/* script */
const __vue_script__$1 = script$1;
/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-object-modal-group",
        "xml-single-object-modal-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            "page-type": _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-object-modal", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.object3DDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.object3DDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              mode: _vm.mode,
            },
            on: { blockclick: _vm.handleBlockClick },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__$1 = [];
__vue_render__$1._withStripped = true;

  /* style */
  const __vue_inject_styles__$1 = undefined;
  /* scoped */
  const __vue_scope_id__$1 = "data-v-c111bfc4";
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__$1.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1(__vue_component__$1.name, {
    prefix: options.prefix
  }), __vue_component__$1);
};

//

/**
 * 单图片组合
 */
var script = {
  name: 'XmlSingleTextareaGroupRender',
  mixins: [singleGroupMixin],
  props: {
    //模式：preview 预览 (默认)，editor 编辑
    mode: {
      type: String,
      default: function () {
        return 'editor';
      }
    },
    //必须有此属性，接收初始化值
    pGroupData: {
      type: Object,
      default: function () {
        return {};
      }
    },
    //当前所处组的规则
    currentRule: {
      type: Object,
      default: function () {
        return {};
      }
    },
    ruleIndex: Number,
    //必须有此属性，页面类型，取值h5、pc、pad
    pageType: {
      type: String,
      default: 'h5'
    },
    resourceBasisPath: {
      type: String,
      default: ''
    },
    extendParams: {
      type: Object,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      typeText: '图片',
      //定义此变量，是为了解决页面中有多个块儿的情况。
      textareaBlockDataName: 'textareaData',
      groupData: {}
    };
  },
  created() {
    this.groupData = this.pGroupData;
  },
  mounted() {},
  methods: {
    //
    /***
     * 固定方法。接收固定参数
     * 此方法接收块儿点击事件，事件传递块相关参数
     * @param blockData 块中的data属性数据
     * @param dataName 块data数据在组中时，属性key
     * @param propertyComponentsName 块儿对应的属性组件名
     */
    handleBlockClick({
      blockData,
      dataName,
      propertyComponentsName,
      blockId,
      ruleIndex
    }) {
      this.$emit('blockclick', {
        groupData: this.groupData,
        blockData,
        dataName,
        propertyComponentsName,
        currentRule: this.currentRule,
        blockId,
        ruleIndex
      });
    },
    clickImg() {
      this.$emit('clickImg');
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "parent-group parent-group-mark",
      class: [
        "xml-single-textarea-group",
        "xml-single-textarea-group-" + _vm.pageType,
        _vm.groupIsActive ? "active" : "",
      ],
      on: { click: _vm.emitClick },
    },
    [
      _c(
        "xml-group-render",
        {
          ref: "xmlGroup",
          attrs: {
            pageType: _vm.pageType,
            mode: _vm.mode,
            "type-text": _vm.typeText,
            "rule-index": _vm.ruleIndex,
          },
          scopedSlots: _vm._u(
            [
              {
                key: "action-buttons",
                fn: function () {
                  return [_vm._t("default")]
                },
                proxy: true,
              },
            ],
            null,
            true
          ),
        },
        [
          _vm._v(" "),
          _c("xml-textarea-render", {
            attrs: {
              "p-block-data": _vm.groupData[_vm.textareaBlockDataName],
              "current-rule": _vm.currentRule,
              "block-data-name": _vm.textareaBlockDataName,
              "extend-params": _vm.extendParams,
              "rule-index": _vm.ruleIndex,
              "show-title": true,
              pageType: _vm.pageType,
              mode: _vm.mode,
              resourceBasisPath: _vm.resourceBasisPath,
            },
            on: { blockclick: _vm.handleBlockClick, clickImg: _vm.clickImg },
          }),
        ],
        1
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

__vue_component__.install = (Vue, options = {}) => {
  Vue.component(createNamespace$1('single-textarea-group-render', {
    prefix: options.prefix
  }), __vue_component__);
};

vhCheck();
const publicLibComponents = [Popup, Icon$3, Tab, Tabs, NavBar];
const components = [__vue_component__$5G, __vue_component__$5D, __vue_component__$5C, __vue_component__$5B, __vue_component__$5w, __vue_component__$5k, __vue_component__$5j, __vue_component__$54, __vue_component__$5h,
// XmlVideo,
__vue_component__$53,
// XmlHotzone,
// XmlLayout,
__vue_component__$2_, __vue_component__$2Z, __vue_component__$2Y, __vue_component__$2X, __vue_component__$2W, __vue_component__$2V, __vue_component__$2U, __vue_component__$2T, __vue_component__$2S, __vue_component__$2R, __vue_component__$2Q, __vue_component__$2P, __vue_component__$2O, __vue_component__$2N, __vue_component__$2M, __vue_component__$2L, __vue_component__$5E, __vue_component__$51, __vue_component__$4, __vue_component__$3, __vue_component__$1, __vue_component__$2, __vue_component__];
const styledComponents = [];
// 多个导出的组件使用AddComponent方法将全部导出添加到components
AddComponent(XmlMusic);
AddComponent(XmlTitle, true);
AddComponent(ImageText, true);
AddComponent(paragraph, true);
AddComponent(splitline, true);
AddComponent(XmlReader);
AddComponent(XmlTitleLibrary, true);
AddComponent(xmlParagraphLibrary, true);
AddComponent(xmlImageTextLibrary, true);
AddComponent(xmlSplitlineLibrary, true);
AddComponent(XmlText, true);
AddComponent(XmlHotzone);
AddComponent(XmlLayout);
AddComponent(XmlVideo, true);
AddComponent(xmlView$1);
const EventBus = new Vue();
let imgApp;
let instance;
let hevueImgPreviewConfig;
const install = async (Vue, options = {}) => {
  // 判断是否安装
  if (install.installed) return;
  install.installed = true;
  components.forEach(component => {
    Vue.component(createNamespace$1(component.name, {
      prefix: options.prefix
    }), component);
    // console.log('xml普通组件注册', component, options, createNamespace(component.name, { prefix: options.prefix }))
  });
  styledComponents.forEach(component => {
    Vue.component(createNamespace$1(component.name, {
      prefix: options.prefix,
      isUnPrefix: true
    }), component);
    // console.log(
    //   'xml样式库组件注册',
    //   component,
    //   options,
    //   createNamespace(component.name, { prefix: options.prefix, isUnPrefix: true })
    // )
  });
  publicLibComponents.forEach(component => {
    Vue.use(component);
  });

  // console.log({ instance: Vue })

  Vue.prototype.$EventBus = EventBus;
  Vue.prototype.$iconfont = options.iconfont || 'iconfont';
  Vue.prototype.$useNamespace = useNamespace;
  hevueImgPreviewConfig = options;
  let _vue = await (await import('vue')).default;
  imgApp = _vue.extend(__vue_component__$4);
  _vue.prototype.$xmlImgPreview = ImgPreview;
};

// 判断调用vue加载器，实现全局加载
if (typeof window !== 'undefined' && window.Vue) {
  install(window.Vue);
}

// 将组件添加到components进行注册
function AddComponent(data, isStyled) {
  Object.keys(data).forEach(key => {
    isStyled ? styledComponents.push(data[key]) : components.push(data[key]);
  });
}
const ImgPreview = (options = {}) => {
  if (typeof options === 'string') {
    options = {
      url: options
    };
  }

  // 优先采取局部配置，其次采取全局配置
  Object.keys(hevueImgPreviewConfig).map(name => {
    if (options[name] == undefined) {
      options[name] = hevueImgPreviewConfig[name];
    }
  });
  instance = new imgApp({
    data: options
  });
  instance.$mount();
  let dom = instance.$el;
  document.body.appendChild(dom);
  instance.show();
  return instance;
};
// Vue.use 和按需加载都可以
var index = {
  install,
  updateElementTheme,
  updateUITheme,
  formatTextBook,
  ...components
};

export { index as default };
